/*!
 * jQuery
 * https://jquery.com/
 *
 * Copyright OpenJS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2023-05-11T18:29Z
 */
(function (global, factory) {

	"use strict";

	if (typeof module === "object" && typeof module.exports === "object") {

		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket trac-14549 for more info.
		module.exports = global.document ?
			factory(global, true) :
			function (w) {
				if (!w.document) {
					throw new Error("jQuery requires a window with a document");
				}
				return factory(w);
			};
	} else {
		factory(global);
	}

	// Pass this if window is not defined yet
})(typeof window !== "undefined" ? window : this, function (window, noGlobal) {

	// Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
	// throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
	// arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
	// enough that all such attempts are guarded in a try block.
	"use strict";

	var arr = [];

	var getProto = Object.getPrototypeOf;

	var slice = arr.slice;

	var flat = arr.flat ? function (array) {
		return arr.flat.call(array);
	} : function (array) {
		return arr.concat.apply([], array);
	};


	var push = arr.push;

	var indexOf = arr.indexOf;

	var class2type = {};

	var toString = class2type.toString;

	var hasOwn = class2type.hasOwnProperty;

	var fnToString = hasOwn.toString;

	var ObjectFunctionString = fnToString.call(Object);

	var support = {};

	var isFunction = function isFunction(obj) {

		// Support: Chrome <=57, Firefox <=52
		// In some browsers, typeof returns "function" for HTML <object> elements
		// (i.e., `typeof document.createElement( "object" ) === "function"`).
		// We don't want to classify *any* DOM node as a function.
		// Support: QtWeb <=3.8.5, WebKit <=534.34, wkhtmltopdf tool <=0.12.5
		// Plus for old WebKit, typeof returns "function" for HTML collections
		// (e.g., `typeof document.getElementsByTagName("div") === "function"`). (gh-4756)
		return typeof obj === "function" && typeof obj.nodeType !== "number" &&
			typeof obj.item !== "function";
	};


	var isWindow = function isWindow(obj) {
		return obj != null && obj === obj.window;
	};


	var document = window.document;



	var preservedScriptAttributes = {
		type: true,
		src: true,
		nonce: true,
		noModule: true
	};

	function DOMEval(code, node, doc) {
		doc = doc || document;

		var i, val,
			script = doc.createElement("script");

		script.text = code;
		if (node) {
			for (i in preservedScriptAttributes) {

				// Support: Firefox 64+, Edge 18+
				// Some browsers don't support the "nonce" property on scripts.
				// On the other hand, just using `getAttribute` is not enough as
				// the `nonce` attribute is reset to an empty string whenever it
				// becomes browsing-context connected.
				// See https://github.com/whatwg/html/issues/2369
				// See https://html.spec.whatwg.org/#nonce-attributes
				// The `node.getAttribute` check was added for the sake of
				// `jQuery.globalEval` so that it can fake a nonce-containing node
				// via an object.
				val = node[i] || node.getAttribute && node.getAttribute(i);
				if (val) {
					script.setAttribute(i, val);
				}
			}
		}
		doc.head.appendChild(script).parentNode.removeChild(script);
	}


	function toType(obj) {
		if (obj == null) {
			return obj + "";
		}

		// Support: Android <=2.3 only (functionish RegExp)
		return typeof obj === "object" || typeof obj === "function" ?
			class2type[toString.call(obj)] || "object" :
			typeof obj;
	}
	/* global Symbol */
	// Defining this global in .eslintrc.json would create a danger of using the global
	// unguarded in another place, it seems safer to define global only for this module



	var version = "3.7.0",

		rhtmlSuffix = /HTML$/i,

		// Define a local copy of jQuery
		jQuery = function (selector, context) {

			// The jQuery object is actually just the init constructor 'enhanced'
			// Need init if jQuery is called (just allow error to be thrown if not included)
			return new jQuery.fn.init(selector, context);
		};

	jQuery.fn = jQuery.prototype = {

		// The current version of jQuery being used
		jquery: version,

		constructor: jQuery,

		// The default length of a jQuery object is 0
		length: 0,

		toArray: function () {
			return slice.call(this);
		},

		// Get the Nth element in the matched element set OR
		// Get the whole matched element set as a clean array
		get: function (num) {

			// Return all the elements in a clean array
			if (num == null) {
				return slice.call(this);
			}

			// Return just the one element from the set
			return num < 0 ? this[num + this.length] : this[num];
		},

		// Take an array of elements and push it onto the stack
		// (returning the new matched element set)
		pushStack: function (elems) {

			// Build a new jQuery matched element set
			var ret = jQuery.merge(this.constructor(), elems);

			// Add the old object onto the stack (as a reference)
			ret.prevObject = this;

			// Return the newly-formed element set
			return ret;
		},

		// Execute a callback for every element in the matched set.
		each: function (callback) {
			return jQuery.each(this, callback);
		},

		map: function (callback) {
			return this.pushStack(jQuery.map(this, function (elem, i) {
				return callback.call(elem, i, elem);
			}));
		},

		slice: function () {
			return this.pushStack(slice.apply(this, arguments));
		},

		first: function () {
			return this.eq(0);
		},

		last: function () {
			return this.eq(-1);
		},

		even: function () {
			return this.pushStack(jQuery.grep(this, function (_elem, i) {
				return (i + 1) % 2;
			}));
		},

		odd: function () {
			return this.pushStack(jQuery.grep(this, function (_elem, i) {
				return i % 2;
			}));
		},

		eq: function (i) {
			var len = this.length,
				j = +i + (i < 0 ? len : 0);
			return this.pushStack(j >= 0 && j < len ? [this[j]] : []);
		},

		end: function () {
			return this.prevObject || this.constructor();
		},

		// For internal use only.
		// Behaves like an Array's method, not like a jQuery method.
		push: push,
		sort: arr.sort,
		splice: arr.splice
	};

	jQuery.extend = jQuery.fn.extend = function () {
		var options, name, src, copy, copyIsArray, clone,
			target = arguments[0] || {},
			i = 1,
			length = arguments.length,
			deep = false;

		// Handle a deep copy situation
		if (typeof target === "boolean") {
			deep = target;

			// Skip the boolean and the target
			target = arguments[i] || {};
			i++;
		}

		// Handle case when target is a string or something (possible in deep copy)
		if (typeof target !== "object" && !isFunction(target)) {
			target = {};
		}

		// Extend jQuery itself if only one argument is passed
		if (i === length) {
			target = this;
			i--;
		}

		for (; i < length; i++) {

			// Only deal with non-null/undefined values
			if ((options = arguments[i]) != null) {

				// Extend the base object
				for (name in options) {
					copy = options[name];

					// Prevent Object.prototype pollution
					// Prevent never-ending loop
					if (name === "__proto__" || target === copy) {
						continue;
					}

					// Recurse if we're merging plain objects or arrays
					if (deep && copy && (jQuery.isPlainObject(copy) ||
						(copyIsArray = Array.isArray(copy)))) {
						src = target[name];

						// Ensure proper type for the source value
						if (copyIsArray && !Array.isArray(src)) {
							clone = [];
						} else if (!copyIsArray && !jQuery.isPlainObject(src)) {
							clone = {};
						} else {
							clone = src;
						}
						copyIsArray = false;

						// Never move original objects, clone them
						target[name] = jQuery.extend(deep, clone, copy);

						// Don't bring in undefined values
					} else if (copy !== undefined) {
						target[name] = copy;
					}
				}
			}
		}

		// Return the modified object
		return target;
	};

	jQuery.extend({

		// Unique for each copy of jQuery on the page
		expando: "jQuery" + (version + Math.random()).replace(/\D/g, ""),

		// Assume jQuery is ready without the ready module
		isReady: true,

		error: function (msg) {
			throw new Error(msg);
		},

		noop: function () { },

		isPlainObject: function (obj) {
			var proto, Ctor;

			// Detect obvious negatives
			// Use toString instead of jQuery.type to catch host objects
			if (!obj || toString.call(obj) !== "[object Object]") {
				return false;
			}

			proto = getProto(obj);

			// Objects with no prototype (e.g., `Object.create( null )`) are plain
			if (!proto) {
				return true;
			}

			// Objects with prototype are plain iff they were constructed by a global Object function
			Ctor = hasOwn.call(proto, "constructor") && proto.constructor;
			return typeof Ctor === "function" && fnToString.call(Ctor) === ObjectFunctionString;
		},

		isEmptyObject: function (obj) {
			var name;

			for (name in obj) {
				return false;
			}
			return true;
		},

		// Evaluates a script in a provided context; falls back to the global one
		// if not specified.
		globalEval: function (code, options, doc) {
			DOMEval(code, { nonce: options && options.nonce }, doc);
		},

		each: function (obj, callback) {
			var length, i = 0;

			if (isArrayLike(obj)) {
				length = obj.length;
				for (; i < length; i++) {
					if (callback.call(obj[i], i, obj[i]) === false) {
						break;
					}
				}
			} else {
				for (i in obj) {
					if (callback.call(obj[i], i, obj[i]) === false) {
						break;
					}
				}
			}

			return obj;
		},


		// Retrieve the text value of an array of DOM nodes
		text: function (elem) {
			var node,
				ret = "",
				i = 0,
				nodeType = elem.nodeType;

			if (!nodeType) {

				// If no nodeType, this is expected to be an array
				while ((node = elem[i++])) {

					// Do not traverse comment nodes
					ret += jQuery.text(node);
				}
			} else if (nodeType === 1 || nodeType === 9 || nodeType === 11) {
				return elem.textContent;
			} else if (nodeType === 3 || nodeType === 4) {
				return elem.nodeValue;
			}

			// Do not include comment or processing instruction nodes

			return ret;
		},

		// results is for internal usage only
		makeArray: function (arr, results) {
			var ret = results || [];

			if (arr != null) {
				if (isArrayLike(Object(arr))) {
					jQuery.merge(ret,
						typeof arr === "string" ?
							[arr] : arr
					);
				} else {
					push.call(ret, arr);
				}
			}

			return ret;
		},

		inArray: function (elem, arr, i) {
			return arr == null ? -1 : indexOf.call(arr, elem, i);
		},

		isXMLDoc: function (elem) {
			var namespace = elem && elem.namespaceURI,
				docElem = elem && (elem.ownerDocument || elem).documentElement;

			// Assume HTML when documentElement doesn't yet exist, such as inside
			// document fragments.
			return !rhtmlSuffix.test(namespace || docElem && docElem.nodeName || "HTML");
		},

		// Support: Android <=4.0 only, PhantomJS 1 only
		// push.apply(_, arraylike) throws on ancient WebKit
		merge: function (first, second) {
			var len = +second.length,
				j = 0,
				i = first.length;

			for (; j < len; j++) {
				first[i++] = second[j];
			}

			first.length = i;

			return first;
		},

		grep: function (elems, callback, invert) {
			var callbackInverse,
				matches = [],
				i = 0,
				length = elems.length,
				callbackExpect = !invert;

			// Go through the array, only saving the items
			// that pass the validator function
			for (; i < length; i++) {
				callbackInverse = !callback(elems[i], i);
				if (callbackInverse !== callbackExpect) {
					matches.push(elems[i]);
				}
			}

			return matches;
		},

		// arg is for internal usage only
		map: function (elems, callback, arg) {
			var length, value,
				i = 0,
				ret = [];

			// Go through the array, translating each of the items to their new values
			if (isArrayLike(elems)) {
				length = elems.length;
				for (; i < length; i++) {
					value = callback(elems[i], i, arg);

					if (value != null) {
						ret.push(value);
					}
				}

				// Go through every key on the object,
			} else {
				for (i in elems) {
					value = callback(elems[i], i, arg);

					if (value != null) {
						ret.push(value);
					}
				}
			}

			// Flatten any nested arrays
			return flat(ret);
		},

		// A global GUID counter for objects
		guid: 1,

		// jQuery.support is not used in Core but other projects attach their
		// properties to it so it needs to exist.
		support: support
	});

	if (typeof Symbol === "function") {
		jQuery.fn[Symbol.iterator] = arr[Symbol.iterator];
	}

	// Populate the class2type map
	jQuery.each("Boolean Number String Function Array Date RegExp Object Error Symbol".split(" "),
		function (_i, name) {
			class2type["[object " + name + "]"] = name.toLowerCase();
		});

	function isArrayLike(obj) {

		// Support: real iOS 8.2 only (not reproducible in simulator)
		// `in` check used to prevent JIT error (gh-2145)
		// hasOwn isn't used here due to false negatives
		// regarding Nodelist length in IE
		var length = !!obj && "length" in obj && obj.length,
			type = toType(obj);

		if (isFunction(obj) || isWindow(obj)) {
			return false;
		}

		return type === "array" || length === 0 ||
			typeof length === "number" && length > 0 && (length - 1) in obj;
	}


	function nodeName(elem, name) {

		return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();

	}
	var pop = arr.pop;


	var sort = arr.sort;


	var splice = arr.splice;


	var whitespace = "[\\x20\\t\\r\\n\\f]";


	var rtrimCSS = new RegExp(
		"^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" + whitespace + "+$",
		"g"
	);




	// Note: an element does not contain itself
	jQuery.contains = function (a, b) {
		var bup = b && b.parentNode;

		return a === bup || !!(bup && bup.nodeType === 1 && (

			// Support: IE 9 - 11+
			// IE doesn't have `contains` on SVG.
			a.contains ?
				a.contains(bup) :
				a.compareDocumentPosition && a.compareDocumentPosition(bup) & 16
		));
	};




	// CSS string/identifier serialization
	// https://drafts.csswg.org/cssom/#common-serializing-idioms
	var rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\x80-\uFFFF\w-]/g;

	function fcssescape(ch, asCodePoint) {
		if (asCodePoint) {

			// U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
			if (ch === "\0") {
				return "\uFFFD";
			}

			// Control characters and (dependent upon position) numbers get escaped as code points
			return ch.slice(0, -1) + "\\" + ch.charCodeAt(ch.length - 1).toString(16) + " ";
		}

		// Other potentially-special ASCII characters get backslash-escaped
		return "\\" + ch;
	}

	jQuery.escapeSelector = function (sel) {
		return (sel + "").replace(rcssescape, fcssescape);
	};




	var preferredDoc = document,
		pushNative = push;

	(function () {

		var i,
			Expr,
			outermostContext,
			sortInput,
			hasDuplicate,
			push = pushNative,

			// Local document vars
			document,
			documentElement,
			documentIsHTML,
			rbuggyQSA,
			matches,

			// Instance-specific data
			expando = jQuery.expando,
			dirruns = 0,
			done = 0,
			classCache = createCache(),
			tokenCache = createCache(),
			compilerCache = createCache(),
			nonnativeSelectorCache = createCache(),
			sortOrder = function (a, b) {
				if (a === b) {
					hasDuplicate = true;
				}
				return 0;
			},

			booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|ismap|" +
				"loop|multiple|open|readonly|required|scoped",

			// Regular expressions

			// https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
			identifier = "(?:\\\\[\\da-fA-F]{1,6}" + whitespace +
				"?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+",

			// Attribute selectors: https://www.w3.org/TR/selectors/#attribute-selectors
			attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +

				// Operator (capture 2)
				"*([*^$|!~]?=)" + whitespace +

				// "Attribute values must be CSS identifiers [capture 5] or strings [capture 3 or capture 4]"
				"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" +
				whitespace + "*\\]",

			pseudos = ":(" + identifier + ")(?:\\((" +

				// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
				// 1. quoted (capture 3; capture 4 or capture 5)
				"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +

				// 2. simple (capture 6)
				"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +

				// 3. anything else (capture 2)
				".*" +
				")\\)|)",

			// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
			rwhitespace = new RegExp(whitespace + "+", "g"),

			rcomma = new RegExp("^" + whitespace + "*," + whitespace + "*"),
			rleadingCombinator = new RegExp("^" + whitespace + "*([>+~]|" + whitespace + ")" +
				whitespace + "*"),
			rdescend = new RegExp(whitespace + "|>"),

			rpseudo = new RegExp(pseudos),
			ridentifier = new RegExp("^" + identifier + "$"),

			matchExpr = {
				ID: new RegExp("^#(" + identifier + ")"),
				CLASS: new RegExp("^\\.(" + identifier + ")"),
				TAG: new RegExp("^(" + identifier + "|[*])"),
				ATTR: new RegExp("^" + attributes),
				PSEUDO: new RegExp("^" + pseudos),
				CHILD: new RegExp(
					"^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" +
					whitespace + "*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" +
					whitespace + "*(\\d+)|))" + whitespace + "*\\)|)", "i"),
				bool: new RegExp("^(?:" + booleans + ")$", "i"),

				// For use in libraries implementing .is()
				// We use this for POS matching in `select`
				needsContext: new RegExp("^" + whitespace +
					"*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" + whitespace +
					"*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i")
			},

			rinputs = /^(?:input|select|textarea|button)$/i,
			rheader = /^h\d$/i,

			// Easily-parseable/retrievable ID or TAG or CLASS selectors
			rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

			rsibling = /[+~]/,

			// CSS escapes
			// https://www.w3.org/TR/CSS21/syndata.html#escaped-characters
			runescape = new RegExp("\\\\[\\da-fA-F]{1,6}" + whitespace +
				"?|\\\\([^\\r\\n\\f])", "g"),
			funescape = function (escape, nonHex) {
				var high = "0x" + escape.slice(1) - 0x10000;

				if (nonHex) {

					// Strip the backslash prefix from a non-hex escape sequence
					return nonHex;
				}

				// Replace a hexadecimal escape sequence with the encoded Unicode code point
				// Support: IE <=11+
				// For values outside the Basic Multilingual Plane (BMP), manually construct a
				// surrogate pair
				return high < 0 ?
					String.fromCharCode(high + 0x10000) :
					String.fromCharCode(high >> 10 | 0xD800, high & 0x3FF | 0xDC00);
			},

			// Used for iframes; see `setDocument`.
			// Support: IE 9 - 11+, Edge 12 - 18+
			// Removing the function wrapper causes a "Permission Denied"
			// error in IE/Edge.
			unloadHandler = function () {
				setDocument();
			},

			inDisabledFieldset = addCombinator(
				function (elem) {
					return elem.disabled === true && nodeName(elem, "fieldset");
				},
				{ dir: "parentNode", next: "legend" }
			);

		// Support: IE <=9 only
		// Accessing document.activeElement can throw unexpectedly
		// https://bugs.jquery.com/ticket/13393
		function safeActiveElement() {
			try {
				return document.activeElement;
			} catch (err) { }
		}

		// Optimize for push.apply( _, NodeList )
		try {
			push.apply(
				(arr = slice.call(preferredDoc.childNodes)),
				preferredDoc.childNodes
			);

			// Support: Android <=4.0
			// Detect silently failing push.apply
			// eslint-disable-next-line no-unused-expressions
			arr[preferredDoc.childNodes.length].nodeType;
		} catch (e) {
			push = {
				apply: function (target, els) {
					pushNative.apply(target, slice.call(els));
				},
				call: function (target) {
					pushNative.apply(target, slice.call(arguments, 1));
				}
			};
		}

		function find(selector, context, results, seed) {
			var m, i, elem, nid, match, groups, newSelector,
				newContext = context && context.ownerDocument,

				// nodeType defaults to 9, since context defaults to document
				nodeType = context ? context.nodeType : 9;

			results = results || [];

			// Return early from calls with invalid selector or context
			if (typeof selector !== "string" || !selector ||
				nodeType !== 1 && nodeType !== 9 && nodeType !== 11) {

				return results;
			}

			// Try to shortcut find operations (as opposed to filters) in HTML documents
			if (!seed) {
				setDocument(context);
				context = context || document;

				if (documentIsHTML) {

					// If the selector is sufficiently simple, try using a "get*By*" DOM method
					// (excepting DocumentFragment context, where the methods don't exist)
					if (nodeType !== 11 && (match = rquickExpr.exec(selector))) {

						// ID selector
						if ((m = match[1])) {

							// Document context
							if (nodeType === 9) {
								if ((elem = context.getElementById(m))) {

									// Support: IE 9 only
									// getElementById can match elements by name instead of ID
									if (elem.id === m) {
										push.call(results, elem);
										return results;
									}
								} else {
									return results;
								}

								// Element context
							} else {

								// Support: IE 9 only
								// getElementById can match elements by name instead of ID
								if (newContext && (elem = newContext.getElementById(m)) &&
									find.contains(context, elem) &&
									elem.id === m) {

									push.call(results, elem);
									return results;
								}
							}

							// Type selector
						} else if (match[2]) {
							push.apply(results, context.getElementsByTagName(selector));
							return results;

							// Class selector
						} else if ((m = match[3]) && context.getElementsByClassName) {
							push.apply(results, context.getElementsByClassName(m));
							return results;
						}
					}

					// Take advantage of querySelectorAll
					if (!nonnativeSelectorCache[selector + " "] &&
						(!rbuggyQSA || !rbuggyQSA.test(selector))) {

						newSelector = selector;
						newContext = context;

						// qSA considers elements outside a scoping root when evaluating child or
						// descendant combinators, which is not what we want.
						// In such cases, we work around the behavior by prefixing every selector in the
						// list with an ID selector referencing the scope context.
						// The technique has to be used as well when a leading combinator is used
						// as such selectors are not recognized by querySelectorAll.
						// Thanks to Andrew Dupont for this technique.
						if (nodeType === 1 &&
							(rdescend.test(selector) || rleadingCombinator.test(selector))) {

							// Expand context for sibling selectors
							newContext = rsibling.test(selector) && testContext(context.parentNode) ||
								context;

							// We can use :scope instead of the ID hack if the browser
							// supports it & if we're not changing the context.
							// Support: IE 11+, Edge 17 - 18+
							// IE/Edge sometimes throw a "Permission denied" error when
							// strict-comparing two documents; shallow comparisons work.
							// eslint-disable-next-line eqeqeq
							if (newContext != context || !support.scope) {

								// Capture the context ID, setting it first if necessary
								if ((nid = context.getAttribute("id"))) {
									nid = jQuery.escapeSelector(nid);
								} else {
									context.setAttribute("id", (nid = expando));
								}
							}

							// Prefix every selector in the list
							groups = tokenize(selector);
							i = groups.length;
							while (i--) {
								groups[i] = (nid ? "#" + nid : ":scope") + " " +
									toSelector(groups[i]);
							}
							newSelector = groups.join(",");
						}

						try {
							push.apply(results,
								newContext.querySelectorAll(newSelector)
							);
							return results;
						} catch (qsaError) {
							nonnativeSelectorCache(selector, true);
						} finally {
							if (nid === expando) {
								context.removeAttribute("id");
							}
						}
					}
				}
			}

			// All others
			return select(selector.replace(rtrimCSS, "$1"), context, results, seed);
		}

		/**
		 * Create key-value caches of limited size
		 * @returns {function(string, object)} Returns the Object data after storing it on itself with
		 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
		 *	deleting the oldest entry
		 */
		function createCache() {
			var keys = [];

			function cache(key, value) {

				// Use (key + " ") to avoid collision with native prototype properties
				// (see https://github.com/jquery/sizzle/issues/157)
				if (keys.push(key + " ") > Expr.cacheLength) {

					// Only keep the most recent entries
					delete cache[keys.shift()];
				}
				return (cache[key + " "] = value);
			}
			return cache;
		}

		/**
		 * Mark a function for special use by jQuery selector module
		 * @param {Function} fn The function to mark
		 */
		function markFunction(fn) {
			fn[expando] = true;
			return fn;
		}

		/**
		 * Support testing using an element
		 * @param {Function} fn Passed the created element and returns a boolean result
		 */
		function assert(fn) {
			var el = document.createElement("fieldset");

			try {
				return !!fn(el);
			} catch (e) {
				return false;
			} finally {

				// Remove from its parent by default
				if (el.parentNode) {
					el.parentNode.removeChild(el);
				}

				// release memory in IE
				el = null;
			}
		}

		/**
		 * Returns a function to use in pseudos for input types
		 * @param {String} type
		 */
		function createInputPseudo(type) {
			return function (elem) {
				return nodeName(elem, "input") && elem.type === type;
			};
		}

		/**
		 * Returns a function to use in pseudos for buttons
		 * @param {String} type
		 */
		function createButtonPseudo(type) {
			return function (elem) {
				return (nodeName(elem, "input") || nodeName(elem, "button")) &&
					elem.type === type;
			};
		}

		/**
		 * Returns a function to use in pseudos for :enabled/:disabled
		 * @param {Boolean} disabled true for :disabled; false for :enabled
		 */
		function createDisabledPseudo(disabled) {

			// Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
			return function (elem) {

				// Only certain elements can match :enabled or :disabled
				// https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
				// https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
				if ("form" in elem) {

					// Check for inherited disabledness on relevant non-disabled elements:
					// * listed form-associated elements in a disabled fieldset
					//   https://html.spec.whatwg.org/multipage/forms.html#category-listed
					//   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
					// * option elements in a disabled optgroup
					//   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
					// All such elements have a "form" property.
					if (elem.parentNode && elem.disabled === false) {

						// Option elements defer to a parent optgroup if present
						if ("label" in elem) {
							if ("label" in elem.parentNode) {
								return elem.parentNode.disabled === disabled;
							} else {
								return elem.disabled === disabled;
							}
						}

						// Support: IE 6 - 11+
						// Use the isDisabled shortcut property to check for disabled fieldset ancestors
						return elem.isDisabled === disabled ||

							// Where there is no isDisabled, check manually
							elem.isDisabled !== !disabled &&
							inDisabledFieldset(elem) === disabled;
					}

					return elem.disabled === disabled;

					// Try to winnow out elements that can't be disabled before trusting the disabled property.
					// Some victims get caught in our net (label, legend, menu, track), but it shouldn't
					// even exist on them, let alone have a boolean value.
				} else if ("label" in elem) {
					return elem.disabled === disabled;
				}

				// Remaining elements are neither :enabled nor :disabled
				return false;
			};
		}

		/**
		 * Returns a function to use in pseudos for positionals
		 * @param {Function} fn
		 */
		function createPositionalPseudo(fn) {
			return markFunction(function (argument) {
				argument = +argument;
				return markFunction(function (seed, matches) {
					var j,
						matchIndexes = fn([], seed.length, argument),
						i = matchIndexes.length;

					// Match elements found at the specified indexes
					while (i--) {
						if (seed[(j = matchIndexes[i])]) {
							seed[j] = !(matches[j] = seed[j]);
						}
					}
				});
			});
		}

		/**
		 * Checks a node for validity as a jQuery selector context
		 * @param {Element|Object=} context
		 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
		 */
		function testContext(context) {
			return context && typeof context.getElementsByTagName !== "undefined" && context;
		}

		/**
		 * Sets document-related variables once based on the current document
		 * @param {Element|Object} [node] An element or document object to use to set the document
		 * @returns {Object} Returns the current document
		 */
		function setDocument(node) {
			var subWindow,
				doc = node ? node.ownerDocument || node : preferredDoc;

			// Return early if doc is invalid or already selected
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if (doc == document || doc.nodeType !== 9 || !doc.documentElement) {
				return document;
			}

			// Update global variables
			document = doc;
			documentElement = document.documentElement;
			documentIsHTML = !jQuery.isXMLDoc(document);

			// Support: iOS 7 only, IE 9 - 11+
			// Older browsers didn't support unprefixed `matches`.
			matches = documentElement.matches ||
				documentElement.webkitMatchesSelector ||
				documentElement.msMatchesSelector;

			// Support: IE 9 - 11+, Edge 12 - 18+
			// Accessing iframe documents after unload throws "permission denied" errors (see trac-13936)
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if (preferredDoc != document &&
				(subWindow = document.defaultView) && subWindow.top !== subWindow) {

				// Support: IE 9 - 11+, Edge 12 - 18+
				subWindow.addEventListener("unload", unloadHandler);
			}

			// Support: IE <10
			// Check if getElementById returns elements by name
			// The broken getElementById methods don't pick up programmatically-set names,
			// so use a roundabout getElementsByName test
			support.getById = assert(function (el) {
				documentElement.appendChild(el).id = jQuery.expando;
				return !document.getElementsByName ||
					!document.getElementsByName(jQuery.expando).length;
			});

			// Support: IE 9 only
			// Check to see if it's possible to do matchesSelector
			// on a disconnected node.
			support.disconnectedMatch = assert(function (el) {
				return matches.call(el, "*");
			});

			// Support: IE 9 - 11+, Edge 12 - 18+
			// IE/Edge don't support the :scope pseudo-class.
			support.scope = assert(function () {
				return document.querySelectorAll(":scope");
			});

			// Support: Chrome 105 - 111 only, Safari 15.4 - 16.3 only
			// Make sure the `:has()` argument is parsed unforgivingly.
			// We include `*` in the test to detect buggy implementations that are
			// _selectively_ forgiving (specifically when the list includes at least
			// one valid selector).
			// Note that we treat complete lack of support for `:has()` as if it were
			// spec-compliant support, which is fine because use of `:has()` in such
			// environments will fail in the qSA path and fall back to jQuery traversal
			// anyway.
			support.cssHas = assert(function () {
				try {
					document.querySelector(":has(*,:jqfake)");
					return false;
				} catch (e) {
					return true;
				}
			});

			// ID filter and find
			if (support.getById) {
				Expr.filter.ID = function (id) {
					var attrId = id.replace(runescape, funescape);
					return function (elem) {
						return elem.getAttribute("id") === attrId;
					};
				};
				Expr.find.ID = function (id, context) {
					if (typeof context.getElementById !== "undefined" && documentIsHTML) {
						var elem = context.getElementById(id);
						return elem ? [elem] : [];
					}
				};
			} else {
				Expr.filter.ID = function (id) {
					var attrId = id.replace(runescape, funescape);
					return function (elem) {
						var node = typeof elem.getAttributeNode !== "undefined" &&
							elem.getAttributeNode("id");
						return node && node.value === attrId;
					};
				};

				// Support: IE 6 - 7 only
				// getElementById is not reliable as a find shortcut
				Expr.find.ID = function (id, context) {
					if (typeof context.getElementById !== "undefined" && documentIsHTML) {
						var node, i, elems,
							elem = context.getElementById(id);

						if (elem) {

							// Verify the id attribute
							node = elem.getAttributeNode("id");
							if (node && node.value === id) {
								return [elem];
							}

							// Fall back on getElementsByName
							elems = context.getElementsByName(id);
							i = 0;
							while ((elem = elems[i++])) {
								node = elem.getAttributeNode("id");
								if (node && node.value === id) {
									return [elem];
								}
							}
						}

						return [];
					}
				};
			}

			// Tag
			Expr.find.TAG = function (tag, context) {
				if (typeof context.getElementsByTagName !== "undefined") {
					return context.getElementsByTagName(tag);

					// DocumentFragment nodes don't have gEBTN
				} else {
					return context.querySelectorAll(tag);
				}
			};

			// Class
			Expr.find.CLASS = function (className, context) {
				if (typeof context.getElementsByClassName !== "undefined" && documentIsHTML) {
					return context.getElementsByClassName(className);
				}
			};

			/* QSA/matchesSelector
			---------------------------------------------------------------------- */

			// QSA and matchesSelector support

			rbuggyQSA = [];

			// Build QSA regex
			// Regex strategy adopted from Diego Perini
			assert(function (el) {

				var input;

				documentElement.appendChild(el).innerHTML =
					"<a id='" + expando + "' href='' disabled='disabled'></a>" +
					"<select id='" + expando + "-\r\\' disabled='disabled'>" +
					"<option selected=''></option></select>";

				// Support: iOS <=7 - 8 only
				// Boolean attributes and "value" are not treated correctly in some XML documents
				if (!el.querySelectorAll("[selected]").length) {
					rbuggyQSA.push("\\[" + whitespace + "*(?:value|" + booleans + ")");
				}

				// Support: iOS <=7 - 8 only
				if (!el.querySelectorAll("[id~=" + expando + "-]").length) {
					rbuggyQSA.push("~=");
				}

				// Support: iOS 8 only
				// https://bugs.webkit.org/show_bug.cgi?id=136851
				// In-page `selector#id sibling-combinator selector` fails
				if (!el.querySelectorAll("a#" + expando + "+*").length) {
					rbuggyQSA.push(".#.+[+~]");
				}

				// Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
				// In some of the document kinds, these selectors wouldn't work natively.
				// This is probably OK but for backwards compatibility we want to maintain
				// handling them through jQuery traversal in jQuery 3.x.
				if (!el.querySelectorAll(":checked").length) {
					rbuggyQSA.push(":checked");
				}

				// Support: Windows 8 Native Apps
				// The type and name attributes are restricted during .innerHTML assignment
				input = document.createElement("input");
				input.setAttribute("type", "hidden");
				el.appendChild(input).setAttribute("name", "D");

				// Support: IE 9 - 11+
				// IE's :disabled selector does not pick up the children of disabled fieldsets
				// Support: Chrome <=105+, Firefox <=104+, Safari <=15.4+
				// In some of the document kinds, these selectors wouldn't work natively.
				// This is probably OK but for backwards compatibility we want to maintain
				// handling them through jQuery traversal in jQuery 3.x.
				documentElement.appendChild(el).disabled = true;
				if (el.querySelectorAll(":disabled").length !== 2) {
					rbuggyQSA.push(":enabled", ":disabled");
				}

				// Support: IE 11+, Edge 15 - 18+
				// IE 11/Edge don't find elements on a `[name='']` query in some cases.
				// Adding a temporary attribute to the document before the selection works
				// around the issue.
				// Interestingly, IE 10 & older don't seem to have the issue.
				input = document.createElement("input");
				input.setAttribute("name", "");
				el.appendChild(input);
				if (!el.querySelectorAll("[name='']").length) {
					rbuggyQSA.push("\\[" + whitespace + "*name" + whitespace + "*=" +
						whitespace + "*(?:''|\"\")");
				}
			});

			if (!support.cssHas) {

				// Support: Chrome 105 - 110+, Safari 15.4 - 16.3+
				// Our regular `try-catch` mechanism fails to detect natively-unsupported
				// pseudo-classes inside `:has()` (such as `:has(:contains("Foo"))`)
				// in browsers that parse the `:has()` argument as a forgiving selector list.
				// https://drafts.csswg.org/selectors/#relational now requires the argument
				// to be parsed unforgivingly, but browsers have not yet fully adjusted.
				rbuggyQSA.push(":has");
			}

			rbuggyQSA = rbuggyQSA.length && new RegExp(rbuggyQSA.join("|"));

			/* Sorting
			---------------------------------------------------------------------- */

			// Document order sorting
			sortOrder = function (a, b) {

				// Flag for duplicate removal
				if (a === b) {
					hasDuplicate = true;
					return 0;
				}

				// Sort on method existence if only one input has compareDocumentPosition
				var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
				if (compare) {
					return compare;
				}

				// Calculate position if both inputs belong to the same document
				// Support: IE 11+, Edge 17 - 18+
				// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
				// two documents; shallow comparisons work.
				// eslint-disable-next-line eqeqeq
				compare = (a.ownerDocument || a) == (b.ownerDocument || b) ?
					a.compareDocumentPosition(b) :

					// Otherwise we know they are disconnected
					1;

				// Disconnected nodes
				if (compare & 1 ||
					(!support.sortDetached && b.compareDocumentPosition(a) === compare)) {

					// Choose the first element that is related to our preferred document
					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
					// two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					if (a === document || a.ownerDocument == preferredDoc &&
						find.contains(preferredDoc, a)) {
						return -1;
					}

					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
					// two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					if (b === document || b.ownerDocument == preferredDoc &&
						find.contains(preferredDoc, b)) {
						return 1;
					}

					// Maintain original order
					return sortInput ?
						(indexOf.call(sortInput, a) - indexOf.call(sortInput, b)) :
						0;
				}

				return compare & 4 ? -1 : 1;
			};

			return document;
		}

		find.matches = function (expr, elements) {
			return find(expr, null, null, elements);
		};

		find.matchesSelector = function (elem, expr) {
			setDocument(elem);

			if (documentIsHTML &&
				!nonnativeSelectorCache[expr + " "] &&
				(!rbuggyQSA || !rbuggyQSA.test(expr))) {

				try {
					var ret = matches.call(elem, expr);

					// IE 9's matchesSelector returns false on disconnected nodes
					if (ret || support.disconnectedMatch ||

						// As well, disconnected nodes are said to be in a document
						// fragment in IE 9
						elem.document && elem.document.nodeType !== 11) {
						return ret;
					}
				} catch (e) {
					nonnativeSelectorCache(expr, true);
				}
			}

			return find(expr, document, null, [elem]).length > 0;
		};

		find.contains = function (context, elem) {

			// Set document vars if needed
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ((context.ownerDocument || context) != document) {
				setDocument(context);
			}
			return jQuery.contains(context, elem);
		};


		find.attr = function (elem, name) {

			// Set document vars if needed
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ((elem.ownerDocument || elem) != document) {
				setDocument(elem);
			}

			var fn = Expr.attrHandle[name.toLowerCase()],

				// Don't get fooled by Object.prototype properties (see trac-13807)
				val = fn && hasOwn.call(Expr.attrHandle, name.toLowerCase()) ?
					fn(elem, name, !documentIsHTML) :
					undefined;

			if (val !== undefined) {
				return val;
			}

			return elem.getAttribute(name);
		};

		find.error = function (msg) {
			throw new Error("Syntax error, unrecognized expression: " + msg);
		};

		/**
		 * Document sorting and removing duplicates
		 * @param {ArrayLike} results
		 */
		jQuery.uniqueSort = function (results) {
			var elem,
				duplicates = [],
				j = 0,
				i = 0;

			// Unless we *know* we can detect duplicates, assume their presence
			//
			// Support: Android <=4.0+
			// Testing for detecting duplicates is unpredictable so instead assume we can't
			// depend on duplicate detection in all browsers without a stable sort.
			hasDuplicate = !support.sortStable;
			sortInput = !support.sortStable && slice.call(results, 0);
			sort.call(results, sortOrder);

			if (hasDuplicate) {
				while ((elem = results[i++])) {
					if (elem === results[i]) {
						j = duplicates.push(i);
					}
				}
				while (j--) {
					splice.call(results, duplicates[j], 1);
				}
			}

			// Clear input after sorting to release objects
			// See https://github.com/jquery/sizzle/pull/225
			sortInput = null;

			return results;
		};

		jQuery.fn.uniqueSort = function () {
			return this.pushStack(jQuery.uniqueSort(slice.apply(this)));
		};

		Expr = jQuery.expr = {

			// Can be adjusted by the user
			cacheLength: 50,

			createPseudo: markFunction,

			match: matchExpr,

			attrHandle: {},

			find: {},

			relative: {
				">": { dir: "parentNode", first: true },
				" ": { dir: "parentNode" },
				"+": { dir: "previousSibling", first: true },
				"~": { dir: "previousSibling" }
			},

			preFilter: {
				ATTR: function (match) {
					match[1] = match[1].replace(runescape, funescape);

					// Move the given value to match[3] whether quoted or unquoted
					match[3] = (match[3] || match[4] || match[5] || "")
						.replace(runescape, funescape);

					if (match[2] === "~=") {
						match[3] = " " + match[3] + " ";
					}

					return match.slice(0, 4);
				},

				CHILD: function (match) {

					/* matches from matchExpr["CHILD"]
						1 type (only|nth|...)
						2 what (child|of-type)
						3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
						4 xn-component of xn+y argument ([+-]?\d*n|)
						5 sign of xn-component
						6 x of xn-component
						7 sign of y-component
						8 y of y-component
					*/
					match[1] = match[1].toLowerCase();

					if (match[1].slice(0, 3) === "nth") {

						// nth-* requires argument
						if (!match[3]) {
							find.error(match[0]);
						}

						// numeric x and y parameters for Expr.filter.CHILD
						// remember that false/true cast respectively to 0/1
						match[4] = +(match[4] ?
							match[5] + (match[6] || 1) :
							2 * (match[3] === "even" || match[3] === "odd")
						);
						match[5] = +((match[7] + match[8]) || match[3] === "odd");

						// other types prohibit arguments
					} else if (match[3]) {
						find.error(match[0]);
					}

					return match;
				},

				PSEUDO: function (match) {
					var excess,
						unquoted = !match[6] && match[2];

					if (matchExpr.CHILD.test(match[0])) {
						return null;
					}

					// Accept quoted arguments as-is
					if (match[3]) {
						match[2] = match[4] || match[5] || "";

						// Strip excess characters from unquoted arguments
					} else if (unquoted && rpseudo.test(unquoted) &&

						// Get excess from tokenize (recursively)
						(excess = tokenize(unquoted, true)) &&

						// advance to the next closing parenthesis
						(excess = unquoted.indexOf(")", unquoted.length - excess) - unquoted.length)) {

						// excess is a negative index
						match[0] = match[0].slice(0, excess);
						match[2] = unquoted.slice(0, excess);
					}

					// Return only captures needed by the pseudo filter method (type and argument)
					return match.slice(0, 3);
				}
			},

			filter: {

				TAG: function (nodeNameSelector) {
					var expectedNodeName = nodeNameSelector.replace(runescape, funescape).toLowerCase();
					return nodeNameSelector === "*" ?
						function () {
							return true;
						} :
						function (elem) {
							return nodeName(elem, expectedNodeName);
						};
				},

				CLASS: function (className) {
					var pattern = classCache[className + " "];

					return pattern ||
						(pattern = new RegExp("(^|" + whitespace + ")" + className +
							"(" + whitespace + "|$)")) &&
						classCache(className, function (elem) {
							return pattern.test(
								typeof elem.className === "string" && elem.className ||
								typeof elem.getAttribute !== "undefined" &&
								elem.getAttribute("class") ||
								""
							);
						});
				},

				ATTR: function (name, operator, check) {
					return function (elem) {
						var result = find.attr(elem, name);

						if (result == null) {
							return operator === "!=";
						}
						if (!operator) {
							return true;
						}

						result += "";

						if (operator === "=") {
							return result === check;
						}
						if (operator === "!=") {
							return result !== check;
						}
						if (operator === "^=") {
							return check && result.indexOf(check) === 0;
						}
						if (operator === "*=") {
							return check && result.indexOf(check) > -1;
						}
						if (operator === "$=") {
							return check && result.slice(-check.length) === check;
						}
						if (operator === "~=") {
							return (" " + result.replace(rwhitespace, " ") + " ")
								.indexOf(check) > -1;
						}
						if (operator === "|=") {
							return result === check || result.slice(0, check.length + 1) === check + "-";
						}

						return false;
					};
				},

				CHILD: function (type, what, _argument, first, last) {
					var simple = type.slice(0, 3) !== "nth",
						forward = type.slice(-4) !== "last",
						ofType = what === "of-type";

					return first === 1 && last === 0 ?

						// Shortcut for :nth-*(n)
						function (elem) {
							return !!elem.parentNode;
						} :

						function (elem, _context, xml) {
							var cache, outerCache, node, nodeIndex, start,
								dir = simple !== forward ? "nextSibling" : "previousSibling",
								parent = elem.parentNode,
								name = ofType && elem.nodeName.toLowerCase(),
								useCache = !xml && !ofType,
								diff = false;

							if (parent) {

								// :(first|last|only)-(child|of-type)
								if (simple) {
									while (dir) {
										node = elem;
										while ((node = node[dir])) {
											if (ofType ?
												nodeName(node, name) :
												node.nodeType === 1) {

												return false;
											}
										}

										// Reverse direction for :only-* (if we haven't yet done so)
										start = dir = type === "only" && !start && "nextSibling";
									}
									return true;
								}

								start = [forward ? parent.firstChild : parent.lastChild];

								// non-xml :nth-child(...) stores cache data on `parent`
								if (forward && useCache) {

									// Seek `elem` from a previously-cached index
									outerCache = parent[expando] || (parent[expando] = {});
									cache = outerCache[type] || [];
									nodeIndex = cache[0] === dirruns && cache[1];
									diff = nodeIndex && cache[2];
									node = nodeIndex && parent.childNodes[nodeIndex];

									while ((node = ++nodeIndex && node && node[dir] ||

										// Fallback to seeking `elem` from the start
										(diff = nodeIndex = 0) || start.pop())) {

										// When found, cache indexes on `parent` and break
										if (node.nodeType === 1 && ++diff && node === elem) {
											outerCache[type] = [dirruns, nodeIndex, diff];
											break;
										}
									}

								} else {

									// Use previously-cached element index if available
									if (useCache) {
										outerCache = elem[expando] || (elem[expando] = {});
										cache = outerCache[type] || [];
										nodeIndex = cache[0] === dirruns && cache[1];
										diff = nodeIndex;
									}

									// xml :nth-child(...)
									// or :nth-last-child(...) or :nth(-last)?-of-type(...)
									if (diff === false) {

										// Use the same loop as above to seek `elem` from the start
										while ((node = ++nodeIndex && node && node[dir] ||
											(diff = nodeIndex = 0) || start.pop())) {

											if ((ofType ?
												nodeName(node, name) :
												node.nodeType === 1) &&
												++diff) {

												// Cache the index of each encountered element
												if (useCache) {
													outerCache = node[expando] ||
														(node[expando] = {});
													outerCache[type] = [dirruns, diff];
												}

												if (node === elem) {
													break;
												}
											}
										}
									}
								}

								// Incorporate the offset, then check against cycle size
								diff -= last;
								return diff === first || (diff % first === 0 && diff / first >= 0);
							}
						};
				},

				PSEUDO: function (pseudo, argument) {

					// pseudo-class names are case-insensitive
					// https://www.w3.org/TR/selectors/#pseudo-classes
					// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
					// Remember that setFilters inherits from pseudos
					var args,
						fn = Expr.pseudos[pseudo] || Expr.setFilters[pseudo.toLowerCase()] ||
							find.error("unsupported pseudo: " + pseudo);

					// The user may use createPseudo to indicate that
					// arguments are needed to create the filter function
					// just as jQuery does
					if (fn[expando]) {
						return fn(argument);
					}

					// But maintain support for old signatures
					if (fn.length > 1) {
						args = [pseudo, pseudo, "", argument];
						return Expr.setFilters.hasOwnProperty(pseudo.toLowerCase()) ?
							markFunction(function (seed, matches) {
								var idx,
									matched = fn(seed, argument),
									i = matched.length;
								while (i--) {
									idx = indexOf.call(seed, matched[i]);
									seed[idx] = !(matches[idx] = matched[i]);
								}
							}) :
							function (elem) {
								return fn(elem, 0, args);
							};
					}

					return fn;
				}
			},

			pseudos: {

				// Potentially complex pseudos
				not: markFunction(function (selector) {

					// Trim the selector passed to compile
					// to avoid treating leading and trailing
					// spaces as combinators
					var input = [],
						results = [],
						matcher = compile(selector.replace(rtrimCSS, "$1"));

					return matcher[expando] ?
						markFunction(function (seed, matches, _context, xml) {
							var elem,
								unmatched = matcher(seed, null, xml, []),
								i = seed.length;

							// Match elements unmatched by `matcher`
							while (i--) {
								if ((elem = unmatched[i])) {
									seed[i] = !(matches[i] = elem);
								}
							}
						}) :
						function (elem, _context, xml) {
							input[0] = elem;
							matcher(input, null, xml, results);

							// Don't keep the element
							// (see https://github.com/jquery/sizzle/issues/299)
							input[0] = null;
							return !results.pop();
						};
				}),

				has: markFunction(function (selector) {
					return function (elem) {
						return find(selector, elem).length > 0;
					};
				}),

				contains: markFunction(function (text) {
					text = text.replace(runescape, funescape);
					return function (elem) {
						return (elem.textContent || jQuery.text(elem)).indexOf(text) > -1;
					};
				}),

				// "Whether an element is represented by a :lang() selector
				// is based solely on the element's language value
				// being equal to the identifier C,
				// or beginning with the identifier C immediately followed by "-".
				// The matching of C against the element's language value is performed case-insensitively.
				// The identifier C does not have to be a valid language name."
				// https://www.w3.org/TR/selectors/#lang-pseudo
				lang: markFunction(function (lang) {

					// lang value must be a valid identifier
					if (!ridentifier.test(lang || "")) {
						find.error("unsupported lang: " + lang);
					}
					lang = lang.replace(runescape, funescape).toLowerCase();
					return function (elem) {
						var elemLang;
						do {
							if ((elemLang = documentIsHTML ?
								elem.lang :
								elem.getAttribute("xml:lang") || elem.getAttribute("lang"))) {

								elemLang = elemLang.toLowerCase();
								return elemLang === lang || elemLang.indexOf(lang + "-") === 0;
							}
						} while ((elem = elem.parentNode) && elem.nodeType === 1);
						return false;
					};
				}),

				// Miscellaneous
				target: function (elem) {
					var hash = window.location && window.location.hash;
					return hash && hash.slice(1) === elem.id;
				},

				root: function (elem) {
					return elem === documentElement;
				},

				focus: function (elem) {
					return elem === safeActiveElement() &&
						document.hasFocus() &&
						!!(elem.type || elem.href || ~elem.tabIndex);
				},

				// Boolean properties
				enabled: createDisabledPseudo(false),
				disabled: createDisabledPseudo(true),

				checked: function (elem) {

					// In CSS3, :checked should return both checked and selected elements
					// https://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
					return (nodeName(elem, "input") && !!elem.checked) ||
						(nodeName(elem, "option") && !!elem.selected);
				},

				selected: function (elem) {

					// Support: IE <=11+
					// Accessing the selectedIndex property
					// forces the browser to treat the default option as
					// selected when in an optgroup.
					if (elem.parentNode) {
						// eslint-disable-next-line no-unused-expressions
						elem.parentNode.selectedIndex;
					}

					return elem.selected === true;
				},

				// Contents
				empty: function (elem) {

					// https://www.w3.org/TR/selectors/#empty-pseudo
					// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
					//   but not by others (comment: 8; processing instruction: 7; etc.)
					// nodeType < 6 works because attributes (2) do not appear as children
					for (elem = elem.firstChild; elem; elem = elem.nextSibling) {
						if (elem.nodeType < 6) {
							return false;
						}
					}
					return true;
				},

				parent: function (elem) {
					return !Expr.pseudos.empty(elem);
				},

				// Element/input types
				header: function (elem) {
					return rheader.test(elem.nodeName);
				},

				input: function (elem) {
					return rinputs.test(elem.nodeName);
				},

				button: function (elem) {
					return nodeName(elem, "input") && elem.type === "button" ||
						nodeName(elem, "button");
				},

				text: function (elem) {
					var attr;
					return nodeName(elem, "input") && elem.type === "text" &&

						// Support: IE <10 only
						// New HTML5 attribute values (e.g., "search") appear
						// with elem.type === "text"
						((attr = elem.getAttribute("type")) == null ||
							attr.toLowerCase() === "text");
				},

				// Position-in-collection
				first: createPositionalPseudo(function () {
					return [0];
				}),

				last: createPositionalPseudo(function (_matchIndexes, length) {
					return [length - 1];
				}),

				eq: createPositionalPseudo(function (_matchIndexes, length, argument) {
					return [argument < 0 ? argument + length : argument];
				}),

				even: createPositionalPseudo(function (matchIndexes, length) {
					var i = 0;
					for (; i < length; i += 2) {
						matchIndexes.push(i);
					}
					return matchIndexes;
				}),

				odd: createPositionalPseudo(function (matchIndexes, length) {
					var i = 1;
					for (; i < length; i += 2) {
						matchIndexes.push(i);
					}
					return matchIndexes;
				}),

				lt: createPositionalPseudo(function (matchIndexes, length, argument) {
					var i;

					if (argument < 0) {
						i = argument + length;
					} else if (argument > length) {
						i = length;
					} else {
						i = argument;
					}

					for (; --i >= 0;) {
						matchIndexes.push(i);
					}
					return matchIndexes;
				}),

				gt: createPositionalPseudo(function (matchIndexes, length, argument) {
					var i = argument < 0 ? argument + length : argument;
					for (; ++i < length;) {
						matchIndexes.push(i);
					}
					return matchIndexes;
				})
			}
		};

		Expr.pseudos.nth = Expr.pseudos.eq;

		// Add button/input type pseudos
		for (i in { radio: true, checkbox: true, file: true, password: true, image: true }) {
			Expr.pseudos[i] = createInputPseudo(i);
		}
		for (i in { submit: true, reset: true }) {
			Expr.pseudos[i] = createButtonPseudo(i);
		}

		// Easy API for creating new setFilters
		function setFilters() { }
		setFilters.prototype = Expr.filters = Expr.pseudos;
		Expr.setFilters = new setFilters();

		function tokenize(selector, parseOnly) {
			var matched, match, tokens, type,
				soFar, groups, preFilters,
				cached = tokenCache[selector + " "];

			if (cached) {
				return parseOnly ? 0 : cached.slice(0);
			}

			soFar = selector;
			groups = [];
			preFilters = Expr.preFilter;

			while (soFar) {

				// Comma and first run
				if (!matched || (match = rcomma.exec(soFar))) {
					if (match) {

						// Don't consume trailing commas as valid
						soFar = soFar.slice(match[0].length) || soFar;
					}
					groups.push((tokens = []));
				}

				matched = false;

				// Combinators
				if ((match = rleadingCombinator.exec(soFar))) {
					matched = match.shift();
					tokens.push({
						value: matched,

						// Cast descendant combinators to space
						type: match[0].replace(rtrimCSS, " ")
					});
					soFar = soFar.slice(matched.length);
				}

				// Filters
				for (type in Expr.filter) {
					if ((match = matchExpr[type].exec(soFar)) && (!preFilters[type] ||
						(match = preFilters[type](match)))) {
						matched = match.shift();
						tokens.push({
							value: matched,
							type: type,
							matches: match
						});
						soFar = soFar.slice(matched.length);
					}
				}

				if (!matched) {
					break;
				}
			}

			// Return the length of the invalid excess
			// if we're just parsing
			// Otherwise, throw an error or return tokens
			if (parseOnly) {
				return soFar.length;
			}

			return soFar ?
				find.error(selector) :

				// Cache the tokens
				tokenCache(selector, groups).slice(0);
		}

		function toSelector(tokens) {
			var i = 0,
				len = tokens.length,
				selector = "";
			for (; i < len; i++) {
				selector += tokens[i].value;
			}
			return selector;
		}

		function addCombinator(matcher, combinator, base) {
			var dir = combinator.dir,
				skip = combinator.next,
				key = skip || dir,
				checkNonElements = base && key === "parentNode",
				doneName = done++;

			return combinator.first ?

				// Check against closest ancestor/preceding element
				function (elem, context, xml) {
					while ((elem = elem[dir])) {
						if (elem.nodeType === 1 || checkNonElements) {
							return matcher(elem, context, xml);
						}
					}
					return false;
				} :

				// Check against all ancestor/preceding elements
				function (elem, context, xml) {
					var oldCache, outerCache,
						newCache = [dirruns, doneName];

					// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
					if (xml) {
						while ((elem = elem[dir])) {
							if (elem.nodeType === 1 || checkNonElements) {
								if (matcher(elem, context, xml)) {
									return true;
								}
							}
						}
					} else {
						while ((elem = elem[dir])) {
							if (elem.nodeType === 1 || checkNonElements) {
								outerCache = elem[expando] || (elem[expando] = {});

								if (skip && nodeName(elem, skip)) {
									elem = elem[dir] || elem;
								} else if ((oldCache = outerCache[key]) &&
									oldCache[0] === dirruns && oldCache[1] === doneName) {

									// Assign to newCache so results back-propagate to previous elements
									return (newCache[2] = oldCache[2]);
								} else {

									// Reuse newcache so results back-propagate to previous elements
									outerCache[key] = newCache;

									// A match means we're done; a fail means we have to keep checking
									if ((newCache[2] = matcher(elem, context, xml))) {
										return true;
									}
								}
							}
						}
					}
					return false;
				};
		}

		function elementMatcher(matchers) {
			return matchers.length > 1 ?
				function (elem, context, xml) {
					var i = matchers.length;
					while (i--) {
						if (!matchers[i](elem, context, xml)) {
							return false;
						}
					}
					return true;
				} :
				matchers[0];
		}

		function multipleContexts(selector, contexts, results) {
			var i = 0,
				len = contexts.length;
			for (; i < len; i++) {
				find(selector, contexts[i], results);
			}
			return results;
		}

		function condense(unmatched, map, filter, context, xml) {
			var elem,
				newUnmatched = [],
				i = 0,
				len = unmatched.length,
				mapped = map != null;

			for (; i < len; i++) {
				if ((elem = unmatched[i])) {
					if (!filter || filter(elem, context, xml)) {
						newUnmatched.push(elem);
						if (mapped) {
							map.push(i);
						}
					}
				}
			}

			return newUnmatched;
		}

		function setMatcher(preFilter, selector, matcher, postFilter, postFinder, postSelector) {
			if (postFilter && !postFilter[expando]) {
				postFilter = setMatcher(postFilter);
			}
			if (postFinder && !postFinder[expando]) {
				postFinder = setMatcher(postFinder, postSelector);
			}
			return markFunction(function (seed, results, context, xml) {
				var temp, i, elem, matcherOut,
					preMap = [],
					postMap = [],
					preexisting = results.length,

					// Get initial elements from seed or context
					elems = seed ||
						multipleContexts(selector || "*",
							context.nodeType ? [context] : context, []),

					// Prefilter to get matcher input, preserving a map for seed-results synchronization
					matcherIn = preFilter && (seed || !selector) ?
						condense(elems, preMap, preFilter, context, xml) :
						elems;

				if (matcher) {

					// If we have a postFinder, or filtered seed, or non-seed postFilter
					// or preexisting results,
					matcherOut = postFinder || (seed ? preFilter : preexisting || postFilter) ?

						// ...intermediate processing is necessary
						[] :

						// ...otherwise use results directly
						results;

					// Find primary matches
					matcher(matcherIn, matcherOut, context, xml);
				} else {
					matcherOut = matcherIn;
				}

				// Apply postFilter
				if (postFilter) {
					temp = condense(matcherOut, postMap);
					postFilter(temp, [], context, xml);

					// Un-match failing elements by moving them back to matcherIn
					i = temp.length;
					while (i--) {
						if ((elem = temp[i])) {
							matcherOut[postMap[i]] = !(matcherIn[postMap[i]] = elem);
						}
					}
				}

				if (seed) {
					if (postFinder || preFilter) {
						if (postFinder) {

							// Get the final matcherOut by condensing this intermediate into postFinder contexts
							temp = [];
							i = matcherOut.length;
							while (i--) {
								if ((elem = matcherOut[i])) {

									// Restore matcherIn since elem is not yet a final match
									temp.push((matcherIn[i] = elem));
								}
							}
							postFinder(null, (matcherOut = []), temp, xml);
						}

						// Move matched elements from seed to results to keep them synchronized
						i = matcherOut.length;
						while (i--) {
							if ((elem = matcherOut[i]) &&
								(temp = postFinder ? indexOf.call(seed, elem) : preMap[i]) > -1) {

								seed[temp] = !(results[temp] = elem);
							}
						}
					}

					// Add elements to results, through postFinder if defined
				} else {
					matcherOut = condense(
						matcherOut === results ?
							matcherOut.splice(preexisting, matcherOut.length) :
							matcherOut
					);
					if (postFinder) {
						postFinder(null, results, matcherOut, xml);
					} else {
						push.apply(results, matcherOut);
					}
				}
			});
		}

		function matcherFromTokens(tokens) {
			var checkContext, matcher, j,
				len = tokens.length,
				leadingRelative = Expr.relative[tokens[0].type],
				implicitRelative = leadingRelative || Expr.relative[" "],
				i = leadingRelative ? 1 : 0,

				// The foundational matcher ensures that elements are reachable from top-level context(s)
				matchContext = addCombinator(function (elem) {
					return elem === checkContext;
				}, implicitRelative, true),
				matchAnyContext = addCombinator(function (elem) {
					return indexOf.call(checkContext, elem) > -1;
				}, implicitRelative, true),
				matchers = [function (elem, context, xml) {

					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
					// two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					var ret = (!leadingRelative && (xml || context != outermostContext)) || (
						(checkContext = context).nodeType ?
							matchContext(elem, context, xml) :
							matchAnyContext(elem, context, xml));

					// Avoid hanging onto element
					// (see https://github.com/jquery/sizzle/issues/299)
					checkContext = null;
					return ret;
				}];

			for (; i < len; i++) {
				if ((matcher = Expr.relative[tokens[i].type])) {
					matchers = [addCombinator(elementMatcher(matchers), matcher)];
				} else {
					matcher = Expr.filter[tokens[i].type].apply(null, tokens[i].matches);

					// Return special upon seeing a positional matcher
					if (matcher[expando]) {

						// Find the next relative operator (if any) for proper handling
						j = ++i;
						for (; j < len; j++) {
							if (Expr.relative[tokens[j].type]) {
								break;
							}
						}
						return setMatcher(
							i > 1 && elementMatcher(matchers),
							i > 1 && toSelector(

								// If the preceding token was a descendant combinator, insert an implicit any-element `*`
								tokens.slice(0, i - 1)
									.concat({ value: tokens[i - 2].type === " " ? "*" : "" })
							).replace(rtrimCSS, "$1"),
							matcher,
							i < j && matcherFromTokens(tokens.slice(i, j)),
							j < len && matcherFromTokens((tokens = tokens.slice(j))),
							j < len && toSelector(tokens)
						);
					}
					matchers.push(matcher);
				}
			}

			return elementMatcher(matchers);
		}

		function matcherFromGroupMatchers(elementMatchers, setMatchers) {
			var bySet = setMatchers.length > 0,
				byElement = elementMatchers.length > 0,
				superMatcher = function (seed, context, xml, results, outermost) {
					var elem, j, matcher,
						matchedCount = 0,
						i = "0",
						unmatched = seed && [],
						setMatched = [],
						contextBackup = outermostContext,

						// We must always have either seed elements or outermost context
						elems = seed || byElement && Expr.find.TAG("*", outermost),

						// Use integer dirruns iff this is the outermost matcher
						dirrunsUnique = (dirruns += contextBackup == null ? 1 : Math.random() || 0.1),
						len = elems.length;

					if (outermost) {

						// Support: IE 11+, Edge 17 - 18+
						// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
						// two documents; shallow comparisons work.
						// eslint-disable-next-line eqeqeq
						outermostContext = context == document || context || outermost;
					}

					// Add elements passing elementMatchers directly to results
					// Support: iOS <=7 - 9 only
					// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching
					// elements by id. (see trac-14142)
					for (; i !== len && (elem = elems[i]) != null; i++) {
						if (byElement && elem) {
							j = 0;

							// Support: IE 11+, Edge 17 - 18+
							// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
							// two documents; shallow comparisons work.
							// eslint-disable-next-line eqeqeq
							if (!context && elem.ownerDocument != document) {
								setDocument(elem);
								xml = !documentIsHTML;
							}
							while ((matcher = elementMatchers[j++])) {
								if (matcher(elem, context || document, xml)) {
									push.call(results, elem);
									break;
								}
							}
							if (outermost) {
								dirruns = dirrunsUnique;
							}
						}

						// Track unmatched elements for set filters
						if (bySet) {

							// They will have gone through all possible matchers
							if ((elem = !matcher && elem)) {
								matchedCount--;
							}

							// Lengthen the array for every element, matched or not
							if (seed) {
								unmatched.push(elem);
							}
						}
					}

					// `i` is now the count of elements visited above, and adding it to `matchedCount`
					// makes the latter nonnegative.
					matchedCount += i;

					// Apply set filters to unmatched elements
					// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
					// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
					// no element matchers and no seed.
					// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
					// case, which will result in a "00" `matchedCount` that differs from `i` but is also
					// numerically zero.
					if (bySet && i !== matchedCount) {
						j = 0;
						while ((matcher = setMatchers[j++])) {
							matcher(unmatched, setMatched, context, xml);
						}

						if (seed) {

							// Reintegrate element matches to eliminate the need for sorting
							if (matchedCount > 0) {
								while (i--) {
									if (!(unmatched[i] || setMatched[i])) {
										setMatched[i] = pop.call(results);
									}
								}
							}

							// Discard index placeholder values to get only actual matches
							setMatched = condense(setMatched);
						}

						// Add matches to results
						push.apply(results, setMatched);

						// Seedless set matches succeeding multiple successful matchers stipulate sorting
						if (outermost && !seed && setMatched.length > 0 &&
							(matchedCount + setMatchers.length) > 1) {

							jQuery.uniqueSort(results);
						}
					}

					// Override manipulation of globals by nested matchers
					if (outermost) {
						dirruns = dirrunsUnique;
						outermostContext = contextBackup;
					}

					return unmatched;
				};

			return bySet ?
				markFunction(superMatcher) :
				superMatcher;
		}

		function compile(selector, match /* Internal Use Only */) {
			var i,
				setMatchers = [],
				elementMatchers = [],
				cached = compilerCache[selector + " "];

			if (!cached) {

				// Generate a function of recursive functions that can be used to check each element
				if (!match) {
					match = tokenize(selector);
				}
				i = match.length;
				while (i--) {
					cached = matcherFromTokens(match[i]);
					if (cached[expando]) {
						setMatchers.push(cached);
					} else {
						elementMatchers.push(cached);
					}
				}

				// Cache the compiled function
				cached = compilerCache(selector,
					matcherFromGroupMatchers(elementMatchers, setMatchers));

				// Save selector and tokenization
				cached.selector = selector;
			}
			return cached;
		}

		/**
		 * A low-level selection function that works with jQuery's compiled
		 *  selector functions
		 * @param {String|Function} selector A selector or a pre-compiled
		 *  selector function built with jQuery selector compile
		 * @param {Element} context
		 * @param {Array} [results]
		 * @param {Array} [seed] A set of elements to match against
		 */
		function select(selector, context, results, seed) {
			var i, tokens, token, type, find,
				compiled = typeof selector === "function" && selector,
				match = !seed && tokenize((selector = compiled.selector || selector));

			results = results || [];

			// Try to minimize operations if there is only one selector in the list and no seed
			// (the latter of which guarantees us context)
			if (match.length === 1) {

				// Reduce context if the leading compound selector is an ID
				tokens = match[0] = match[0].slice(0);
				if (tokens.length > 2 && (token = tokens[0]).type === "ID" &&
					context.nodeType === 9 && documentIsHTML && Expr.relative[tokens[1].type]) {

					context = (Expr.find.ID(
						token.matches[0].replace(runescape, funescape),
						context
					) || [])[0];
					if (!context) {
						return results;

						// Precompiled matchers will still verify ancestry, so step up a level
					} else if (compiled) {
						context = context.parentNode;
					}

					selector = selector.slice(tokens.shift().value.length);
				}

				// Fetch a seed set for right-to-left matching
				i = matchExpr.needsContext.test(selector) ? 0 : tokens.length;
				while (i--) {
					token = tokens[i];

					// Abort if we hit a combinator
					if (Expr.relative[(type = token.type)]) {
						break;
					}
					if ((find = Expr.find[type])) {

						// Search, expanding context for leading sibling combinators
						if ((seed = find(
							token.matches[0].replace(runescape, funescape),
							rsibling.test(tokens[0].type) &&
							testContext(context.parentNode) || context
						))) {

							// If seed is empty or no tokens remain, we can return early
							tokens.splice(i, 1);
							selector = seed.length && toSelector(tokens);
							if (!selector) {
								push.apply(results, seed);
								return results;
							}

							break;
						}
					}
				}
			}

			// Compile and execute a filtering function if one is not provided
			// Provide `match` to avoid retokenization if we modified the selector above
			(compiled || compile(selector, match))(
				seed,
				context,
				!documentIsHTML,
				results,
				!context || rsibling.test(selector) && testContext(context.parentNode) || context
			);
			return results;
		}

		// One-time assignments

		// Support: Android <=4.0 - 4.1+
		// Sort stability
		support.sortStable = expando.split("").sort(sortOrder).join("") === expando;

		// Initialize against the default document
		setDocument();

		// Support: Android <=4.0 - 4.1+
		// Detached nodes confoundingly follow *each other*
		support.sortDetached = assert(function (el) {

			// Should return 1, but returns 4 (following)
			return el.compareDocumentPosition(document.createElement("fieldset")) & 1;
		});

		jQuery.find = find;

		// Deprecated
		jQuery.expr[":"] = jQuery.expr.pseudos;
		jQuery.unique = jQuery.uniqueSort;

		// These have always been private, but they used to be documented
		// as part of Sizzle so let's maintain them in the 3.x line
		// for backwards compatibility purposes.
		find.compile = compile;
		find.select = select;
		find.setDocument = setDocument;

		find.escape = jQuery.escapeSelector;
		find.getText = jQuery.text;
		find.isXML = jQuery.isXMLDoc;
		find.selectors = jQuery.expr;
		find.support = jQuery.support;
		find.uniqueSort = jQuery.uniqueSort;

		/* eslint-enable */

	})();


	var dir = function (elem, dir, until) {
		var matched = [],
			truncate = until !== undefined;

		while ((elem = elem[dir]) && elem.nodeType !== 9) {
			if (elem.nodeType === 1) {
				if (truncate && jQuery(elem).is(until)) {
					break;
				}
				matched.push(elem);
			}
		}
		return matched;
	};


	var siblings = function (n, elem) {
		var matched = [];

		for (; n; n = n.nextSibling) {
			if (n.nodeType === 1 && n !== elem) {
				matched.push(n);
			}
		}

		return matched;
	};


	var rneedsContext = jQuery.expr.match.needsContext;

	var rsingleTag = (/^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i);



	// Implement the identical functionality for filter and not
	function winnow(elements, qualifier, not) {
		if (isFunction(qualifier)) {
			return jQuery.grep(elements, function (elem, i) {
				return !!qualifier.call(elem, i, elem) !== not;
			});
		}

		// Single element
		if (qualifier.nodeType) {
			return jQuery.grep(elements, function (elem) {
				return (elem === qualifier) !== not;
			});
		}

		// Arraylike of elements (jQuery, arguments, Array)
		if (typeof qualifier !== "string") {
			return jQuery.grep(elements, function (elem) {
				return (indexOf.call(qualifier, elem) > -1) !== not;
			});
		}

		// Filtered directly for both simple and complex selectors
		return jQuery.filter(qualifier, elements, not);
	}

	jQuery.filter = function (expr, elems, not) {
		var elem = elems[0];

		if (not) {
			expr = ":not(" + expr + ")";
		}

		if (elems.length === 1 && elem.nodeType === 1) {
			return jQuery.find.matchesSelector(elem, expr) ? [elem] : [];
		}

		return jQuery.find.matches(expr, jQuery.grep(elems, function (elem) {
			return elem.nodeType === 1;
		}));
	};

	jQuery.fn.extend({
		find: function (selector) {
			var i, ret,
				len = this.length,
				self = this;

			if (typeof selector !== "string") {
				return this.pushStack(jQuery(selector).filter(function () {
					for (i = 0; i < len; i++) {
						if (jQuery.contains(self[i], this)) {
							return true;
						}
					}
				}));
			}

			ret = this.pushStack([]);

			for (i = 0; i < len; i++) {
				jQuery.find(selector, self[i], ret);
			}

			return len > 1 ? jQuery.uniqueSort(ret) : ret;
		},
		filter: function (selector) {
			return this.pushStack(winnow(this, selector || [], false));
		},
		not: function (selector) {
			return this.pushStack(winnow(this, selector || [], true));
		},
		is: function (selector) {
			return !!winnow(
				this,

				// If this is a positional/relative selector, check membership in the returned set
				// so $("p:first").is("p:last") won't return true for a doc with two "p".
				typeof selector === "string" && rneedsContext.test(selector) ?
					jQuery(selector) :
					selector || [],
				false
			).length;
		}
	});


	// Initialize a jQuery object


	// A central reference to the root jQuery(document)
	var rootjQuery,

		// A simple way to check for HTML strings
		// Prioritize #id over <tag> to avoid XSS via location.hash (trac-9521)
		// Strict HTML recognition (trac-11290: must start with <)
		// Shortcut simple #id case for speed
		rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,

		init = jQuery.fn.init = function (selector, context, root) {
			var match, elem;

			// HANDLE: $(""), $(null), $(undefined), $(false)
			if (!selector) {
				return this;
			}

			// Method init() accepts an alternate rootjQuery
			// so migrate can support jQuery.sub (gh-2101)
			root = root || rootjQuery;

			// Handle HTML strings
			if (typeof selector === "string") {
				if (selector[0] === "<" &&
					selector[selector.length - 1] === ">" &&
					selector.length >= 3) {

					// Assume that strings that start and end with <> are HTML and skip the regex check
					match = [null, selector, null];

				} else {
					match = rquickExpr.exec(selector);
				}

				// Match html or make sure no context is specified for #id
				if (match && (match[1] || !context)) {

					// HANDLE: $(html) -> $(array)
					if (match[1]) {
						context = context instanceof jQuery ? context[0] : context;

						// Option to run scripts is true for back-compat
						// Intentionally let the error be thrown if parseHTML is not present
						jQuery.merge(this, jQuery.parseHTML(
							match[1],
							context && context.nodeType ? context.ownerDocument || context : document,
							true
						));

						// HANDLE: $(html, props)
						if (rsingleTag.test(match[1]) && jQuery.isPlainObject(context)) {
							for (match in context) {

								// Properties of context are called as methods if possible
								if (isFunction(this[match])) {
									this[match](context[match]);

									// ...and otherwise set as attributes
								} else {
									this.attr(match, context[match]);
								}
							}
						}

						return this;

						// HANDLE: $(#id)
					} else {
						elem = document.getElementById(match[2]);

						if (elem) {

							// Inject the element directly into the jQuery object
							this[0] = elem;
							this.length = 1;
						}
						return this;
					}

					// HANDLE: $(expr, $(...))
				} else if (!context || context.jquery) {
					return (context || root).find(selector);

					// HANDLE: $(expr, context)
					// (which is just equivalent to: $(context).find(expr)
				} else {
					return this.constructor(context).find(selector);
				}

				// HANDLE: $(DOMElement)
			} else if (selector.nodeType) {
				this[0] = selector;
				this.length = 1;
				return this;

				// HANDLE: $(function)
				// Shortcut for document ready
			} else if (isFunction(selector)) {
				return root.ready !== undefined ?
					root.ready(selector) :

					// Execute immediately if ready is not present
					selector(jQuery);
			}

			return jQuery.makeArray(selector, this);
		};

	// Give the init function the jQuery prototype for later instantiation
	init.prototype = jQuery.fn;

	// Initialize central reference
	rootjQuery = jQuery(document);


	var rparentsprev = /^(?:parents|prev(?:Until|All))/,

		// Methods guaranteed to produce a unique set when starting from a unique set
		guaranteedUnique = {
			children: true,
			contents: true,
			next: true,
			prev: true
		};

	jQuery.fn.extend({
		has: function (target) {
			var targets = jQuery(target, this),
				l = targets.length;

			return this.filter(function () {
				var i = 0;
				for (; i < l; i++) {
					if (jQuery.contains(this, targets[i])) {
						return true;
					}
				}
			});
		},

		closest: function (selectors, context) {
			var cur,
				i = 0,
				l = this.length,
				matched = [],
				targets = typeof selectors !== "string" && jQuery(selectors);

			// Positional selectors never match, since there's no _selection_ context
			if (!rneedsContext.test(selectors)) {
				for (; i < l; i++) {
					for (cur = this[i]; cur && cur !== context; cur = cur.parentNode) {

						// Always skip document fragments
						if (cur.nodeType < 11 && (targets ?
							targets.index(cur) > -1 :

							// Don't pass non-elements to jQuery#find
							cur.nodeType === 1 &&
							jQuery.find.matchesSelector(cur, selectors))) {

							matched.push(cur);
							break;
						}
					}
				}
			}

			return this.pushStack(matched.length > 1 ? jQuery.uniqueSort(matched) : matched);
		},

		// Determine the position of an element within the set
		index: function (elem) {

			// No argument, return index in parent
			if (!elem) {
				return (this[0] && this[0].parentNode) ? this.first().prevAll().length : -1;
			}

			// Index in selector
			if (typeof elem === "string") {
				return indexOf.call(jQuery(elem), this[0]);
			}

			// Locate the position of the desired element
			return indexOf.call(this,

				// If it receives a jQuery object, the first element is used
				elem.jquery ? elem[0] : elem
			);
		},

		add: function (selector, context) {
			return this.pushStack(
				jQuery.uniqueSort(
					jQuery.merge(this.get(), jQuery(selector, context))
				)
			);
		},

		addBack: function (selector) {
			return this.add(selector == null ?
				this.prevObject : this.prevObject.filter(selector)
			);
		}
	});

	function sibling(cur, dir) {
		while ((cur = cur[dir]) && cur.nodeType !== 1) { }
		return cur;
	}

	jQuery.each({
		parent: function (elem) {
			var parent = elem.parentNode;
			return parent && parent.nodeType !== 11 ? parent : null;
		},
		parents: function (elem) {
			return dir(elem, "parentNode");
		},
		parentsUntil: function (elem, _i, until) {
			return dir(elem, "parentNode", until);
		},
		next: function (elem) {
			return sibling(elem, "nextSibling");
		},
		prev: function (elem) {
			return sibling(elem, "previousSibling");
		},
		nextAll: function (elem) {
			return dir(elem, "nextSibling");
		},
		prevAll: function (elem) {
			return dir(elem, "previousSibling");
		},
		nextUntil: function (elem, _i, until) {
			return dir(elem, "nextSibling", until);
		},
		prevUntil: function (elem, _i, until) {
			return dir(elem, "previousSibling", until);
		},
		siblings: function (elem) {
			return siblings((elem.parentNode || {}).firstChild, elem);
		},
		children: function (elem) {
			return siblings(elem.firstChild);
		},
		contents: function (elem) {
			if (elem.contentDocument != null &&

				// Support: IE 11+
				// <object> elements with no `data` attribute has an object
				// `contentDocument` with a `null` prototype.
				getProto(elem.contentDocument)) {

				return elem.contentDocument;
			}

			// Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
			// Treat the template element as a regular one in browsers that
			// don't support it.
			if (nodeName(elem, "template")) {
				elem = elem.content || elem;
			}

			return jQuery.merge([], elem.childNodes);
		}
	}, function (name, fn) {
		jQuery.fn[name] = function (until, selector) {
			var matched = jQuery.map(this, fn, until);

			if (name.slice(-5) !== "Until") {
				selector = until;
			}

			if (selector && typeof selector === "string") {
				matched = jQuery.filter(selector, matched);
			}

			if (this.length > 1) {

				// Remove duplicates
				if (!guaranteedUnique[name]) {
					jQuery.uniqueSort(matched);
				}

				// Reverse order for parents* and prev-derivatives
				if (rparentsprev.test(name)) {
					matched.reverse();
				}
			}

			return this.pushStack(matched);
		};
	});
	var rnothtmlwhite = (/[^\x20\t\r\n\f]+/g);



	// Convert String-formatted options into Object-formatted ones
	function createOptions(options) {
		var object = {};
		jQuery.each(options.match(rnothtmlwhite) || [], function (_, flag) {
			object[flag] = true;
		});
		return object;
	}

	/*
	 * Create a callback list using the following parameters:
	 *
	 *	options: an optional list of space-separated options that will change how
	 *			the callback list behaves or a more traditional option object
	 *
	 * By default a callback list will act like an event callback list and can be
	 * "fired" multiple times.
	 *
	 * Possible options:
	 *
	 *	once:			will ensure the callback list can only be fired once (like a Deferred)
	 *
	 *	memory:			will keep track of previous values and will call any callback added
	 *					after the list has been fired right away with the latest "memorized"
	 *					values (like a Deferred)
	 *
	 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
	 *
	 *	stopOnFalse:	interrupt callings when a callback returns false
	 *
	 */
	jQuery.Callbacks = function (options) {

		// Convert options from String-formatted to Object-formatted if needed
		// (we check in cache first)
		options = typeof options === "string" ?
			createOptions(options) :
			jQuery.extend({}, options);

		var // Flag to know if list is currently firing
			firing,

			// Last fire value for non-forgettable lists
			memory,

			// Flag to know if list was already fired
			fired,

			// Flag to prevent firing
			locked,

			// Actual callback list
			list = [],

			// Queue of execution data for repeatable lists
			queue = [],

			// Index of currently firing callback (modified by add/remove as needed)
			firingIndex = -1,

			// Fire callbacks
			fire = function () {

				// Enforce single-firing
				locked = locked || options.once;

				// Execute callbacks for all pending executions,
				// respecting firingIndex overrides and runtime changes
				fired = firing = true;
				for (; queue.length; firingIndex = -1) {
					memory = queue.shift();
					while (++firingIndex < list.length) {

						// Run callback and check for early termination
						if (list[firingIndex].apply(memory[0], memory[1]) === false &&
							options.stopOnFalse) {

							// Jump to end and forget the data so .add doesn't re-fire
							firingIndex = list.length;
							memory = false;
						}
					}
				}

				// Forget the data if we're done with it
				if (!options.memory) {
					memory = false;
				}

				firing = false;

				// Clean up if we're done firing for good
				if (locked) {

					// Keep an empty list if we have data for future add calls
					if (memory) {
						list = [];

						// Otherwise, this object is spent
					} else {
						list = "";
					}
				}
			},

			// Actual Callbacks object
			self = {

				// Add a callback or a collection of callbacks to the list
				add: function () {
					if (list) {

						// If we have memory from a past run, we should fire after adding
						if (memory && !firing) {
							firingIndex = list.length - 1;
							queue.push(memory);
						}

						(function add(args) {
							jQuery.each(args, function (_, arg) {
								if (isFunction(arg)) {
									if (!options.unique || !self.has(arg)) {
										list.push(arg);
									}
								} else if (arg && arg.length && toType(arg) !== "string") {

									// Inspect recursively
									add(arg);
								}
							});
						})(arguments);

						if (memory && !firing) {
							fire();
						}
					}
					return this;
				},

				// Remove a callback from the list
				remove: function () {
					jQuery.each(arguments, function (_, arg) {
						var index;
						while ((index = jQuery.inArray(arg, list, index)) > -1) {
							list.splice(index, 1);

							// Handle firing indexes
							if (index <= firingIndex) {
								firingIndex--;
							}
						}
					});
					return this;
				},

				// Check if a given callback is in the list.
				// If no argument is given, return whether or not list has callbacks attached.
				has: function (fn) {
					return fn ?
						jQuery.inArray(fn, list) > -1 :
						list.length > 0;
				},

				// Remove all callbacks from the list
				empty: function () {
					if (list) {
						list = [];
					}
					return this;
				},

				// Disable .fire and .add
				// Abort any current/pending executions
				// Clear all callbacks and values
				disable: function () {
					locked = queue = [];
					list = memory = "";
					return this;
				},
				disabled: function () {
					return !list;
				},

				// Disable .fire
				// Also disable .add unless we have memory (since it would have no effect)
				// Abort any pending executions
				lock: function () {
					locked = queue = [];
					if (!memory && !firing) {
						list = memory = "";
					}
					return this;
				},
				locked: function () {
					return !!locked;
				},

				// Call all callbacks with the given context and arguments
				fireWith: function (context, args) {
					if (!locked) {
						args = args || [];
						args = [context, args.slice ? args.slice() : args];
						queue.push(args);
						if (!firing) {
							fire();
						}
					}
					return this;
				},

				// Call all the callbacks with the given arguments
				fire: function () {
					self.fireWith(this, arguments);
					return this;
				},

				// To know if the callbacks have already been called at least once
				fired: function () {
					return !!fired;
				}
			};

		return self;
	};


	function Identity(v) {
		return v;
	}
	function Thrower(ex) {
		throw ex;
	}

	function adoptValue(value, resolve, reject, noValue) {
		var method;

		try {

			// Check for promise aspect first to privilege synchronous behavior
			if (value && isFunction((method = value.promise))) {
				method.call(value).done(resolve).fail(reject);

				// Other thenables
			} else if (value && isFunction((method = value.then))) {
				method.call(value, resolve, reject);

				// Other non-thenables
			} else {

				// Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
				// * false: [ value ].slice( 0 ) => resolve( value )
				// * true: [ value ].slice( 1 ) => resolve()
				resolve.apply(undefined, [value].slice(noValue));
			}

			// For Promises/A+, convert exceptions into rejections
			// Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
			// Deferred#then to conditionally suppress rejection.
		} catch (value) {

			// Support: Android 4.0 only
			// Strict mode functions invoked without .call/.apply get global-object context
			reject.apply(undefined, [value]);
		}
	}

	jQuery.extend({

		Deferred: function (func) {
			var tuples = [

				// action, add listener, callbacks,
				// ... .then handlers, argument index, [final state]
				["notify", "progress", jQuery.Callbacks("memory"),
					jQuery.Callbacks("memory"), 2],
				["resolve", "done", jQuery.Callbacks("once memory"),
					jQuery.Callbacks("once memory"), 0, "resolved"],
				["reject", "fail", jQuery.Callbacks("once memory"),
					jQuery.Callbacks("once memory"), 1, "rejected"]
			],
				state = "pending",
				promise = {
					state: function () {
						return state;
					},
					always: function () {
						deferred.done(arguments).fail(arguments);
						return this;
					},
					"catch": function (fn) {
						return promise.then(null, fn);
					},

					// Keep pipe for back-compat
					pipe: function ( /* fnDone, fnFail, fnProgress */) {
						var fns = arguments;

						return jQuery.Deferred(function (newDefer) {
							jQuery.each(tuples, function (_i, tuple) {

								// Map tuples (progress, done, fail) to arguments (done, fail, progress)
								var fn = isFunction(fns[tuple[4]]) && fns[tuple[4]];

								// deferred.progress(function() { bind to newDefer or newDefer.notify })
								// deferred.done(function() { bind to newDefer or newDefer.resolve })
								// deferred.fail(function() { bind to newDefer or newDefer.reject })
								deferred[tuple[1]](function () {
									var returned = fn && fn.apply(this, arguments);
									if (returned && isFunction(returned.promise)) {
										returned.promise()
											.progress(newDefer.notify)
											.done(newDefer.resolve)
											.fail(newDefer.reject);
									} else {
										newDefer[tuple[0] + "With"](
											this,
											fn ? [returned] : arguments
										);
									}
								});
							});
							fns = null;
						}).promise();
					},
					then: function (onFulfilled, onRejected, onProgress) {
						var maxDepth = 0;
						function resolve(depth, deferred, handler, special) {
							return function () {
								var that = this,
									args = arguments,
									mightThrow = function () {
										var returned, then;

										// Support: Promises/A+ section 2.3.3.3.3
										// https://promisesaplus.com/#point-59
										// Ignore double-resolution attempts
										if (depth < maxDepth) {
											return;
										}

										returned = handler.apply(that, args);

										// Support: Promises/A+ section 2.3.1
										// https://promisesaplus.com/#point-48
										if (returned === deferred.promise()) {
											throw new TypeError("Thenable self-resolution");
										}

										// Support: Promises/A+ sections 2.3.3.1, 3.5
										// https://promisesaplus.com/#point-54
										// https://promisesaplus.com/#point-75
										// Retrieve `then` only once
										then = returned &&

											// Support: Promises/A+ section 2.3.4
											// https://promisesaplus.com/#point-64
											// Only check objects and functions for thenability
											(typeof returned === "object" ||
												typeof returned === "function") &&
											returned.then;

										// Handle a returned thenable
										if (isFunction(then)) {

											// Special processors (notify) just wait for resolution
											if (special) {
												then.call(
													returned,
													resolve(maxDepth, deferred, Identity, special),
													resolve(maxDepth, deferred, Thrower, special)
												);

												// Normal processors (resolve) also hook into progress
											} else {

												// ...and disregard older resolution values
												maxDepth++;

												then.call(
													returned,
													resolve(maxDepth, deferred, Identity, special),
													resolve(maxDepth, deferred, Thrower, special),
													resolve(maxDepth, deferred, Identity,
														deferred.notifyWith)
												);
											}

											// Handle all other returned values
										} else {

											// Only substitute handlers pass on context
											// and multiple values (non-spec behavior)
											if (handler !== Identity) {
												that = undefined;
												args = [returned];
											}

											// Process the value(s)
											// Default process is resolve
											(special || deferred.resolveWith)(that, args);
										}
									},

									// Only normal processors (resolve) catch and reject exceptions
									process = special ?
										mightThrow :
										function () {
											try {
												mightThrow();
											} catch (e) {

												if (jQuery.Deferred.exceptionHook) {
													jQuery.Deferred.exceptionHook(e,
														process.error);
												}

												// Support: Promises/A+ section 2.3.3.3.4.1
												// https://promisesaplus.com/#point-61
												// Ignore post-resolution exceptions
												if (depth + 1 >= maxDepth) {

													// Only substitute handlers pass on context
													// and multiple values (non-spec behavior)
													if (handler !== Thrower) {
														that = undefined;
														args = [e];
													}

													deferred.rejectWith(that, args);
												}
											}
										};

								// Support: Promises/A+ section 2.3.3.3.1
								// https://promisesaplus.com/#point-57
								// Re-resolve promises immediately to dodge false rejection from
								// subsequent errors
								if (depth) {
									process();
								} else {

									// Call an optional hook to record the error, in case of exception
									// since it's otherwise lost when execution goes async
									if (jQuery.Deferred.getErrorHook) {
										process.error = jQuery.Deferred.getErrorHook();

										// The deprecated alias of the above. While the name suggests
										// returning the stack, not an error instance, jQuery just passes
										// it directly to `console.warn` so both will work; an instance
										// just better cooperates with source maps.
									} else if (jQuery.Deferred.getStackHook) {
										process.error = jQuery.Deferred.getStackHook();
									}
									window.setTimeout(process);
								}
							};
						}

						return jQuery.Deferred(function (newDefer) {

							// progress_handlers.add( ... )
							tuples[0][3].add(
								resolve(
									0,
									newDefer,
									isFunction(onProgress) ?
										onProgress :
										Identity,
									newDefer.notifyWith
								)
							);

							// fulfilled_handlers.add( ... )
							tuples[1][3].add(
								resolve(
									0,
									newDefer,
									isFunction(onFulfilled) ?
										onFulfilled :
										Identity
								)
							);

							// rejected_handlers.add( ... )
							tuples[2][3].add(
								resolve(
									0,
									newDefer,
									isFunction(onRejected) ?
										onRejected :
										Thrower
								)
							);
						}).promise();
					},

					// Get a promise for this deferred
					// If obj is provided, the promise aspect is added to the object
					promise: function (obj) {
						return obj != null ? jQuery.extend(obj, promise) : promise;
					}
				},
				deferred = {};

			// Add list-specific methods
			jQuery.each(tuples, function (i, tuple) {
				var list = tuple[2],
					stateString = tuple[5];

				// promise.progress = list.add
				// promise.done = list.add
				// promise.fail = list.add
				promise[tuple[1]] = list.add;

				// Handle state
				if (stateString) {
					list.add(
						function () {

							// state = "resolved" (i.e., fulfilled)
							// state = "rejected"
							state = stateString;
						},

						// rejected_callbacks.disable
						// fulfilled_callbacks.disable
						tuples[3 - i][2].disable,

						// rejected_handlers.disable
						// fulfilled_handlers.disable
						tuples[3 - i][3].disable,

						// progress_callbacks.lock
						tuples[0][2].lock,

						// progress_handlers.lock
						tuples[0][3].lock
					);
				}

				// progress_handlers.fire
				// fulfilled_handlers.fire
				// rejected_handlers.fire
				list.add(tuple[3].fire);

				// deferred.notify = function() { deferred.notifyWith(...) }
				// deferred.resolve = function() { deferred.resolveWith(...) }
				// deferred.reject = function() { deferred.rejectWith(...) }
				deferred[tuple[0]] = function () {
					deferred[tuple[0] + "With"](this === deferred ? undefined : this, arguments);
					return this;
				};

				// deferred.notifyWith = list.fireWith
				// deferred.resolveWith = list.fireWith
				// deferred.rejectWith = list.fireWith
				deferred[tuple[0] + "With"] = list.fireWith;
			});

			// Make the deferred a promise
			promise.promise(deferred);

			// Call given func if any
			if (func) {
				func.call(deferred, deferred);
			}

			// All done!
			return deferred;
		},

		// Deferred helper
		when: function (singleValue) {
			var

				// count of uncompleted subordinates
				remaining = arguments.length,

				// count of unprocessed arguments
				i = remaining,

				// subordinate fulfillment data
				resolveContexts = Array(i),
				resolveValues = slice.call(arguments),

				// the primary Deferred
				primary = jQuery.Deferred(),

				// subordinate callback factory
				updateFunc = function (i) {
					return function (value) {
						resolveContexts[i] = this;
						resolveValues[i] = arguments.length > 1 ? slice.call(arguments) : value;
						if (!(--remaining)) {
							primary.resolveWith(resolveContexts, resolveValues);
						}
					};
				};

			// Single- and empty arguments are adopted like Promise.resolve
			if (remaining <= 1) {
				adoptValue(singleValue, primary.done(updateFunc(i)).resolve, primary.reject,
					!remaining);

				// Use .then() to unwrap secondary thenables (cf. gh-3000)
				if (primary.state() === "pending" ||
					isFunction(resolveValues[i] && resolveValues[i].then)) {

					return primary.then();
				}
			}

			// Multiple arguments are aggregated like Promise.all array elements
			while (i--) {
				adoptValue(resolveValues[i], updateFunc(i), primary.reject);
			}

			return primary.promise();
		}
	});


	// These usually indicate a programmer mistake during development,
	// warn about them ASAP rather than swallowing them by default.
	var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

	// If `jQuery.Deferred.getErrorHook` is defined, `asyncError` is an error
	// captured before the async barrier to get the original error cause
	// which may otherwise be hidden.
	jQuery.Deferred.exceptionHook = function (error, asyncError) {

		// Support: IE 8 - 9 only
		// Console exists when dev tools are open, which can happen at any time
		if (window.console && window.console.warn && error && rerrorNames.test(error.name)) {
			window.console.warn("jQuery.Deferred exception: " + error.message,
				error.stack, asyncError);
		}
	};




	jQuery.readyException = function (error) {
		window.setTimeout(function () {
			throw error;
		});
	};




	// The deferred used on DOM ready
	var readyList = jQuery.Deferred();

	jQuery.fn.ready = function (fn) {

		readyList
			.then(fn)

			// Wrap jQuery.readyException in a function so that the lookup
			// happens at the time of error handling instead of callback
			// registration.
			.catch(function (error) {
				jQuery.readyException(error);
			});

		return this;
	};

	jQuery.extend({

		// Is the DOM ready to be used? Set to true once it occurs.
		isReady: false,

		// A counter to track how many items to wait for before
		// the ready event fires. See trac-6781
		readyWait: 1,

		// Handle when the DOM is ready
		ready: function (wait) {

			// Abort if there are pending holds or we're already ready
			if (wait === true ? --jQuery.readyWait : jQuery.isReady) {
				return;
			}

			// Remember that the DOM is ready
			jQuery.isReady = true;

			// If a normal DOM Ready event fired, decrement, and wait if need be
			if (wait !== true && --jQuery.readyWait > 0) {
				return;
			}

			// If there are functions bound, to execute
			readyList.resolveWith(document, [jQuery]);
		}
	});

	jQuery.ready.then = readyList.then;

	// The ready event handler and self cleanup method
	function completed() {
		document.removeEventListener("DOMContentLoaded", completed);
		window.removeEventListener("load", completed);
		jQuery.ready();
	}

	// Catch cases where $(document).ready() is called
	// after the browser event has already occurred.
	// Support: IE <=9 - 10 only
	// Older IE sometimes signals "interactive" too soon
	if (document.readyState === "complete" ||
		(document.readyState !== "loading" && !document.documentElement.doScroll)) {

		// Handle it asynchronously to allow scripts the opportunity to delay ready
		window.setTimeout(jQuery.ready);

	} else {

		// Use the handy event callback
		document.addEventListener("DOMContentLoaded", completed);

		// A fallback to window.onload, that will always work
		window.addEventListener("load", completed);
	}




	// Multifunctional method to get and set values of a collection
	// The value/s can optionally be executed if it's a function
	var access = function (elems, fn, key, value, chainable, emptyGet, raw) {
		var i = 0,
			len = elems.length,
			bulk = key == null;

		// Sets many values
		if (toType(key) === "object") {
			chainable = true;
			for (i in key) {
				access(elems, fn, i, key[i], true, emptyGet, raw);
			}

			// Sets one value
		} else if (value !== undefined) {
			chainable = true;

			if (!isFunction(value)) {
				raw = true;
			}

			if (bulk) {

				// Bulk operations run against the entire set
				if (raw) {
					fn.call(elems, value);
					fn = null;

					// ...except when executing function values
				} else {
					bulk = fn;
					fn = function (elem, _key, value) {
						return bulk.call(jQuery(elem), value);
					};
				}
			}

			if (fn) {
				for (; i < len; i++) {
					fn(
						elems[i], key, raw ?
						value :
						value.call(elems[i], i, fn(elems[i], key))
					);
				}
			}
		}

		if (chainable) {
			return elems;
		}

		// Gets
		if (bulk) {
			return fn.call(elems);
		}

		return len ? fn(elems[0], key) : emptyGet;
	};


	// Matches dashed string for camelizing
	var rmsPrefix = /^-ms-/,
		rdashAlpha = /-([a-z])/g;

	// Used by camelCase as callback to replace()
	function fcamelCase(_all, letter) {
		return letter.toUpperCase();
	}

	// Convert dashed to camelCase; used by the css and data modules
	// Support: IE <=9 - 11, Edge 12 - 15
	// Microsoft forgot to hump their vendor prefix (trac-9572)
	function camelCase(string) {
		return string.replace(rmsPrefix, "ms-").replace(rdashAlpha, fcamelCase);
	}
	var acceptData = function (owner) {

		// Accepts only:
		//  - Node
		//    - Node.ELEMENT_NODE
		//    - Node.DOCUMENT_NODE
		//  - Object
		//    - Any
		return owner.nodeType === 1 || owner.nodeType === 9 || !(+owner.nodeType);
	};




	function Data() {
		this.expando = jQuery.expando + Data.uid++;
	}

	Data.uid = 1;

	Data.prototype = {

		cache: function (owner) {

			// Check if the owner object already has a cache
			var value = owner[this.expando];

			// If not, create one
			if (!value) {
				value = {};

				// We can accept data for non-element nodes in modern browsers,
				// but we should not, see trac-8335.
				// Always return an empty object.
				if (acceptData(owner)) {

					// If it is a node unlikely to be stringify-ed or looped over
					// use plain assignment
					if (owner.nodeType) {
						owner[this.expando] = value;

						// Otherwise secure it in a non-enumerable property
						// configurable must be true to allow the property to be
						// deleted when data is removed
					} else {
						Object.defineProperty(owner, this.expando, {
							value: value,
							configurable: true
						});
					}
				}
			}

			return value;
		},
		set: function (owner, data, value) {
			var prop,
				cache = this.cache(owner);

			// Handle: [ owner, key, value ] args
			// Always use camelCase key (gh-2257)
			if (typeof data === "string") {
				cache[camelCase(data)] = value;

				// Handle: [ owner, { properties } ] args
			} else {

				// Copy the properties one-by-one to the cache object
				for (prop in data) {
					cache[camelCase(prop)] = data[prop];
				}
			}
			return cache;
		},
		get: function (owner, key) {
			return key === undefined ?
				this.cache(owner) :

				// Always use camelCase key (gh-2257)
				owner[this.expando] && owner[this.expando][camelCase(key)];
		},
		access: function (owner, key, value) {

			// In cases where either:
			//
			//   1. No key was specified
			//   2. A string key was specified, but no value provided
			//
			// Take the "read" path and allow the get method to determine
			// which value to return, respectively either:
			//
			//   1. The entire cache object
			//   2. The data stored at the key
			//
			if (key === undefined ||
				((key && typeof key === "string") && value === undefined)) {

				return this.get(owner, key);
			}

			// When the key is not a string, or both a key and value
			// are specified, set or extend (existing objects) with either:
			//
			//   1. An object of properties
			//   2. A key and value
			//
			this.set(owner, key, value);

			// Since the "set" path can have two possible entry points
			// return the expected data based on which path was taken[*]
			return value !== undefined ? value : key;
		},
		remove: function (owner, key) {
			var i,
				cache = owner[this.expando];

			if (cache === undefined) {
				return;
			}

			if (key !== undefined) {

				// Support array or space separated string of keys
				if (Array.isArray(key)) {

					// If key is an array of keys...
					// We always set camelCase keys, so remove that.
					key = key.map(camelCase);
				} else {
					key = camelCase(key);

					// If a key with the spaces exists, use it.
					// Otherwise, create an array by matching non-whitespace
					key = key in cache ?
						[key] :
						(key.match(rnothtmlwhite) || []);
				}

				i = key.length;

				while (i--) {
					delete cache[key[i]];
				}
			}

			// Remove the expando if there's no more data
			if (key === undefined || jQuery.isEmptyObject(cache)) {

				// Support: Chrome <=35 - 45
				// Webkit & Blink performance suffers when deleting properties
				// from DOM nodes, so set to undefined instead
				// https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
				if (owner.nodeType) {
					owner[this.expando] = undefined;
				} else {
					delete owner[this.expando];
				}
			}
		},
		hasData: function (owner) {
			var cache = owner[this.expando];
			return cache !== undefined && !jQuery.isEmptyObject(cache);
		}
	};
	var dataPriv = new Data();

	var dataUser = new Data();



	//	Implementation Summary
	//
	//	1. Enforce API surface and semantic compatibility with 1.9.x branch
	//	2. Improve the module's maintainability by reducing the storage
	//		paths to a single mechanism.
	//	3. Use the same single mechanism to support "private" and "user" data.
	//	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
	//	5. Avoid exposing implementation details on user objects (eg. expando properties)
	//	6. Provide a clear path for implementation upgrade to WeakMap in 2014

	var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
		rmultiDash = /[A-Z]/g;

	function getData(data) {
		if (data === "true") {
			return true;
		}

		if (data === "false") {
			return false;
		}

		if (data === "null") {
			return null;
		}

		// Only convert to a number if it doesn't change the string
		if (data === +data + "") {
			return +data;
		}

		if (rbrace.test(data)) {
			return JSON.parse(data);
		}

		return data;
	}

	function dataAttr(elem, key, data) {
		var name;

		// If nothing was found internally, try to fetch any
		// data from the HTML5 data-* attribute
		if (data === undefined && elem.nodeType === 1) {
			name = "data-" + key.replace(rmultiDash, "-$&").toLowerCase();
			data = elem.getAttribute(name);

			if (typeof data === "string") {
				try {
					data = getData(data);
				} catch (e) { }

				// Make sure we set the data so it isn't changed later
				dataUser.set(elem, key, data);
			} else {
				data = undefined;
			}
		}
		return data;
	}

	jQuery.extend({
		hasData: function (elem) {
			return dataUser.hasData(elem) || dataPriv.hasData(elem);
		},

		data: function (elem, name, data) {
			return dataUser.access(elem, name, data);
		},

		removeData: function (elem, name) {
			dataUser.remove(elem, name);
		},

		// TODO: Now that all calls to _data and _removeData have been replaced
		// with direct calls to dataPriv methods, these can be deprecated.
		_data: function (elem, name, data) {
			return dataPriv.access(elem, name, data);
		},

		_removeData: function (elem, name) {
			dataPriv.remove(elem, name);
		}
	});

	jQuery.fn.extend({
		data: function (key, value) {
			var i, name, data,
				elem = this[0],
				attrs = elem && elem.attributes;

			// Gets all values
			if (key === undefined) {
				if (this.length) {
					data = dataUser.get(elem);

					if (elem.nodeType === 1 && !dataPriv.get(elem, "hasDataAttrs")) {
						i = attrs.length;
						while (i--) {

							// Support: IE 11 only
							// The attrs elements can be null (trac-14894)
							if (attrs[i]) {
								name = attrs[i].name;
								if (name.indexOf("data-") === 0) {
									name = camelCase(name.slice(5));
									dataAttr(elem, name, data[name]);
								}
							}
						}
						dataPriv.set(elem, "hasDataAttrs", true);
					}
				}

				return data;
			}

			// Sets multiple values
			if (typeof key === "object") {
				return this.each(function () {
					dataUser.set(this, key);
				});
			}

			return access(this, function (value) {
				var data;

				// The calling jQuery object (element matches) is not empty
				// (and therefore has an element appears at this[ 0 ]) and the
				// `value` parameter was not undefined. An empty jQuery object
				// will result in `undefined` for elem = this[ 0 ] which will
				// throw an exception if an attempt to read a data cache is made.
				if (elem && value === undefined) {

					// Attempt to get data from the cache
					// The key will always be camelCased in Data
					data = dataUser.get(elem, key);
					if (data !== undefined) {
						return data;
					}

					// Attempt to "discover" the data in
					// HTML5 custom data-* attrs
					data = dataAttr(elem, key);
					if (data !== undefined) {
						return data;
					}

					// We tried really hard, but the data doesn't exist.
					return;
				}

				// Set the data...
				this.each(function () {

					// We always store the camelCased key
					dataUser.set(this, key, value);
				});
			}, null, value, arguments.length > 1, null, true);
		},

		removeData: function (key) {
			return this.each(function () {
				dataUser.remove(this, key);
			});
		}
	});


	jQuery.extend({
		queue: function (elem, type, data) {
			var queue;

			if (elem) {
				type = (type || "fx") + "queue";
				queue = dataPriv.get(elem, type);

				// Speed up dequeue by getting out quickly if this is just a lookup
				if (data) {
					if (!queue || Array.isArray(data)) {
						queue = dataPriv.access(elem, type, jQuery.makeArray(data));
					} else {
						queue.push(data);
					}
				}
				return queue || [];
			}
		},

		dequeue: function (elem, type) {
			type = type || "fx";

			var queue = jQuery.queue(elem, type),
				startLength = queue.length,
				fn = queue.shift(),
				hooks = jQuery._queueHooks(elem, type),
				next = function () {
					jQuery.dequeue(elem, type);
				};

			// If the fx queue is dequeued, always remove the progress sentinel
			if (fn === "inprogress") {
				fn = queue.shift();
				startLength--;
			}

			if (fn) {

				// Add a progress sentinel to prevent the fx queue from being
				// automatically dequeued
				if (type === "fx") {
					queue.unshift("inprogress");
				}

				// Clear up the last queue stop function
				delete hooks.stop;
				fn.call(elem, next, hooks);
			}

			if (!startLength && hooks) {
				hooks.empty.fire();
			}
		},

		// Not public - generate a queueHooks object, or return the current one
		_queueHooks: function (elem, type) {
			var key = type + "queueHooks";
			return dataPriv.get(elem, key) || dataPriv.access(elem, key, {
				empty: jQuery.Callbacks("once memory").add(function () {
					dataPriv.remove(elem, [type + "queue", key]);
				})
			});
		}
	});

	jQuery.fn.extend({
		queue: function (type, data) {
			var setter = 2;

			if (typeof type !== "string") {
				data = type;
				type = "fx";
				setter--;
			}

			if (arguments.length < setter) {
				return jQuery.queue(this[0], type);
			}

			return data === undefined ?
				this :
				this.each(function () {
					var queue = jQuery.queue(this, type, data);

					// Ensure a hooks for this queue
					jQuery._queueHooks(this, type);

					if (type === "fx" && queue[0] !== "inprogress") {
						jQuery.dequeue(this, type);
					}
				});
		},
		dequeue: function (type) {
			return this.each(function () {
				jQuery.dequeue(this, type);
			});
		},
		clearQueue: function (type) {
			return this.queue(type || "fx", []);
		},

		// Get a promise resolved when queues of a certain type
		// are emptied (fx is the type by default)
		promise: function (type, obj) {
			var tmp,
				count = 1,
				defer = jQuery.Deferred(),
				elements = this,
				i = this.length,
				resolve = function () {
					if (!(--count)) {
						defer.resolveWith(elements, [elements]);
					}
				};

			if (typeof type !== "string") {
				obj = type;
				type = undefined;
			}
			type = type || "fx";

			while (i--) {
				tmp = dataPriv.get(elements[i], type + "queueHooks");
				if (tmp && tmp.empty) {
					count++;
					tmp.empty.add(resolve);
				}
			}
			resolve();
			return defer.promise(obj);
		}
	});
	var pnum = (/[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/).source;

	var rcssNum = new RegExp("^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i");


	var cssExpand = ["Top", "Right", "Bottom", "Left"];

	var documentElement = document.documentElement;



	var isAttached = function (elem) {
		return jQuery.contains(elem.ownerDocument, elem);
	},
		composed = { composed: true };

	// Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
	// Check attachment across shadow DOM boundaries when possible (gh-3504)
	// Support: iOS 10.0-10.2 only
	// Early iOS 10 versions support `attachShadow` but not `getRootNode`,
	// leading to errors. We need to check for `getRootNode`.
	if (documentElement.getRootNode) {
		isAttached = function (elem) {
			return jQuery.contains(elem.ownerDocument, elem) ||
				elem.getRootNode(composed) === elem.ownerDocument;
		};
	}
	var isHiddenWithinTree = function (elem, el) {

		// isHiddenWithinTree might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;

		// Inline style trumps all
		return elem.style.display === "none" ||
			elem.style.display === "" &&

			// Otherwise, check computed style
			// Support: Firefox <=43 - 45
			// Disconnected elements can have computed display: none, so first confirm that elem is
			// in the document.
			isAttached(elem) &&

			jQuery.css(elem, "display") === "none";
	};



	function adjustCSS(elem, prop, valueParts, tween) {
		var adjusted, scale,
			maxIterations = 20,
			currentValue = tween ?
				function () {
					return tween.cur();
				} :
				function () {
					return jQuery.css(elem, prop, "");
				},
			initial = currentValue(),
			unit = valueParts && valueParts[3] || (jQuery.cssNumber[prop] ? "" : "px"),

			// Starting value computation is required for potential unit mismatches
			initialInUnit = elem.nodeType &&
				(jQuery.cssNumber[prop] || unit !== "px" && +initial) &&
				rcssNum.exec(jQuery.css(elem, prop));

		if (initialInUnit && initialInUnit[3] !== unit) {

			// Support: Firefox <=54
			// Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
			initial = initial / 2;

			// Trust units reported by jQuery.css
			unit = unit || initialInUnit[3];

			// Iteratively approximate from a nonzero starting point
			initialInUnit = +initial || 1;

			while (maxIterations--) {

				// Evaluate and update our best guess (doubling guesses that zero out).
				// Finish if the scale equals or crosses 1 (making the old*new product non-positive).
				jQuery.style(elem, prop, initialInUnit + unit);
				if ((1 - scale) * (1 - (scale = currentValue() / initial || 0.5)) <= 0) {
					maxIterations = 0;
				}
				initialInUnit = initialInUnit / scale;

			}

			initialInUnit = initialInUnit * 2;
			jQuery.style(elem, prop, initialInUnit + unit);

			// Make sure we update the tween properties later on
			valueParts = valueParts || [];
		}

		if (valueParts) {
			initialInUnit = +initialInUnit || +initial || 0;

			// Apply relative offset (+=/-=) if specified
			adjusted = valueParts[1] ?
				initialInUnit + (valueParts[1] + 1) * valueParts[2] :
				+valueParts[2];
			if (tween) {
				tween.unit = unit;
				tween.start = initialInUnit;
				tween.end = adjusted;
			}
		}
		return adjusted;
	}


	var defaultDisplayMap = {};

	function getDefaultDisplay(elem) {
		var temp,
			doc = elem.ownerDocument,
			nodeName = elem.nodeName,
			display = defaultDisplayMap[nodeName];

		if (display) {
			return display;
		}

		temp = doc.body.appendChild(doc.createElement(nodeName));
		display = jQuery.css(temp, "display");

		temp.parentNode.removeChild(temp);

		if (display === "none") {
			display = "block";
		}
		defaultDisplayMap[nodeName] = display;

		return display;
	}

	function showHide(elements, show) {
		var display, elem,
			values = [],
			index = 0,
			length = elements.length;

		// Determine new display value for elements that need to change
		for (; index < length; index++) {
			elem = elements[index];
			if (!elem.style) {
				continue;
			}

			display = elem.style.display;
			if (show) {

				// Since we force visibility upon cascade-hidden elements, an immediate (and slow)
				// check is required in this first loop unless we have a nonempty display value (either
				// inline or about-to-be-restored)
				if (display === "none") {
					values[index] = dataPriv.get(elem, "display") || null;
					if (!values[index]) {
						elem.style.display = "";
					}
				}
				if (elem.style.display === "" && isHiddenWithinTree(elem)) {
					values[index] = getDefaultDisplay(elem);
				}
			} else {
				if (display !== "none") {
					values[index] = "none";

					// Remember what we're overwriting
					dataPriv.set(elem, "display", display);
				}
			}
		}

		// Set the display of the elements in a second loop to avoid constant reflow
		for (index = 0; index < length; index++) {
			if (values[index] != null) {
				elements[index].style.display = values[index];
			}
		}

		return elements;
	}

	jQuery.fn.extend({
		show: function () {
			return showHide(this, true);
		},
		hide: function () {
			return showHide(this);
		},
		toggle: function (state) {
			if (typeof state === "boolean") {
				return state ? this.show() : this.hide();
			}

			return this.each(function () {
				if (isHiddenWithinTree(this)) {
					jQuery(this).show();
				} else {
					jQuery(this).hide();
				}
			});
		}
	});
	var rcheckableType = (/^(?:checkbox|radio)$/i);

	var rtagName = (/<([a-z][^\/\0>\x20\t\r\n\f]*)/i);

	var rscriptType = (/^$|^module$|\/(?:java|ecma)script/i);



	(function () {
		var fragment = document.createDocumentFragment(),
			div = fragment.appendChild(document.createElement("div")),
			input = document.createElement("input");

		// Support: Android 4.0 - 4.3 only
		// Check state lost if the name is set (trac-11217)
		// Support: Windows Web Apps (WWA)
		// `name` and `type` must use .setAttribute for WWA (trac-14901)
		input.setAttribute("type", "radio");
		input.setAttribute("checked", "checked");
		input.setAttribute("name", "t");

		div.appendChild(input);

		// Support: Android <=4.1 only
		// Older WebKit doesn't clone checked state correctly in fragments
		support.checkClone = div.cloneNode(true).cloneNode(true).lastChild.checked;

		// Support: IE <=11 only
		// Make sure textarea (and checkbox) defaultValue is properly cloned
		div.innerHTML = "<textarea>x</textarea>";
		support.noCloneChecked = !!div.cloneNode(true).lastChild.defaultValue;

		// Support: IE <=9 only
		// IE <=9 replaces <option> tags with their contents when inserted outside of
		// the select element.
		div.innerHTML = "<option></option>";
		support.option = !!div.lastChild;
	})();


	// We have to close these tags to support XHTML (trac-13200)
	var wrapMap = {

		// XHTML parsers do not magically insert elements in the
		// same way that tag soup parsers do. So we cannot shorten
		// this by omitting <tbody> or other required elements.
		thead: [1, "<table>", "</table>"],
		col: [2, "<table><colgroup>", "</colgroup></table>"],
		tr: [2, "<table><tbody>", "</tbody></table>"],
		td: [3, "<table><tbody><tr>", "</tr></tbody></table>"],

		_default: [0, "", ""]
	};

	wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
	wrapMap.th = wrapMap.td;

	// Support: IE <=9 only
	if (!support.option) {
		wrapMap.optgroup = wrapMap.option = [1, "<select multiple='multiple'>", "</select>"];
	}


	function getAll(context, tag) {

		// Support: IE <=9 - 11 only
		// Use typeof to avoid zero-argument method invocation on host objects (trac-15151)
		var ret;

		if (typeof context.getElementsByTagName !== "undefined") {
			ret = context.getElementsByTagName(tag || "*");

		} else if (typeof context.querySelectorAll !== "undefined") {
			ret = context.querySelectorAll(tag || "*");

		} else {
			ret = [];
		}

		if (tag === undefined || tag && nodeName(context, tag)) {
			return jQuery.merge([context], ret);
		}

		return ret;
	}


	// Mark scripts as having already been evaluated
	function setGlobalEval(elems, refElements) {
		var i = 0,
			l = elems.length;

		for (; i < l; i++) {
			dataPriv.set(
				elems[i],
				"globalEval",
				!refElements || dataPriv.get(refElements[i], "globalEval")
			);
		}
	}


	var rhtml = /<|&#?\w+;/;

	function buildFragment(elems, context, scripts, selection, ignored) {
		var elem, tmp, tag, wrap, attached, j,
			fragment = context.createDocumentFragment(),
			nodes = [],
			i = 0,
			l = elems.length;

		for (; i < l; i++) {
			elem = elems[i];

			if (elem || elem === 0) {

				// Add nodes directly
				if (toType(elem) === "object") {

					// Support: Android <=4.0 only, PhantomJS 1 only
					// push.apply(_, arraylike) throws on ancient WebKit
					jQuery.merge(nodes, elem.nodeType ? [elem] : elem);

					// Convert non-html into a text node
				} else if (!rhtml.test(elem)) {
					nodes.push(context.createTextNode(elem));

					// Convert html into DOM nodes
				} else {
					tmp = tmp || fragment.appendChild(context.createElement("div"));

					// Deserialize a standard representation
					tag = (rtagName.exec(elem) || ["", ""])[1].toLowerCase();
					wrap = wrapMap[tag] || wrapMap._default;
					tmp.innerHTML = wrap[1] + jQuery.htmlPrefilter(elem) + wrap[2];

					// Descend through wrappers to the right content
					j = wrap[0];
					while (j--) {
						tmp = tmp.lastChild;
					}

					// Support: Android <=4.0 only, PhantomJS 1 only
					// push.apply(_, arraylike) throws on ancient WebKit
					jQuery.merge(nodes, tmp.childNodes);

					// Remember the top-level container
					tmp = fragment.firstChild;

					// Ensure the created nodes are orphaned (trac-12392)
					tmp.textContent = "";
				}
			}
		}

		// Remove wrapper from fragment
		fragment.textContent = "";

		i = 0;
		while ((elem = nodes[i++])) {

			// Skip elements already in the context collection (trac-4087)
			if (selection && jQuery.inArray(elem, selection) > -1) {
				if (ignored) {
					ignored.push(elem);
				}
				continue;
			}

			attached = isAttached(elem);

			// Append to fragment
			tmp = getAll(fragment.appendChild(elem), "script");

			// Preserve script evaluation history
			if (attached) {
				setGlobalEval(tmp);
			}

			// Capture executables
			if (scripts) {
				j = 0;
				while ((elem = tmp[j++])) {
					if (rscriptType.test(elem.type || "")) {
						scripts.push(elem);
					}
				}
			}
		}

		return fragment;
	}


	var rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

	function returnTrue() {
		return true;
	}

	function returnFalse() {
		return false;
	}

	function on(elem, types, selector, data, fn, one) {
		var origFn, type;

		// Types can be a map of types/handlers
		if (typeof types === "object") {

			// ( types-Object, selector, data )
			if (typeof selector !== "string") {

				// ( types-Object, data )
				data = data || selector;
				selector = undefined;
			}
			for (type in types) {
				on(elem, type, selector, data, types[type], one);
			}
			return elem;
		}

		if (data == null && fn == null) {

			// ( types, fn )
			fn = selector;
			data = selector = undefined;
		} else if (fn == null) {
			if (typeof selector === "string") {

				// ( types, selector, fn )
				fn = data;
				data = undefined;
			} else {

				// ( types, data, fn )
				fn = data;
				data = selector;
				selector = undefined;
			}
		}
		if (fn === false) {
			fn = returnFalse;
		} else if (!fn) {
			return elem;
		}

		if (one === 1) {
			origFn = fn;
			fn = function (event) {

				// Can use an empty set, since event contains the info
				jQuery().off(event);
				return origFn.apply(this, arguments);
			};

			// Use same guid so caller can remove using origFn
			fn.guid = origFn.guid || (origFn.guid = jQuery.guid++);
		}
		return elem.each(function () {
			jQuery.event.add(this, types, fn, data, selector);
		});
	}

	/*
	 * Helper functions for managing events -- not part of the public interface.
	 * Props to Dean Edwards' addEvent library for many of the ideas.
	 */
	jQuery.event = {

		global: {},

		add: function (elem, types, handler, data, selector) {

			var handleObjIn, eventHandle, tmp,
				events, t, handleObj,
				special, handlers, type, namespaces, origType,
				elemData = dataPriv.get(elem);

			// Only attach events to objects that accept data
			if (!acceptData(elem)) {
				return;
			}

			// Caller can pass in an object of custom data in lieu of the handler
			if (handler.handler) {
				handleObjIn = handler;
				handler = handleObjIn.handler;
				selector = handleObjIn.selector;
			}

			// Ensure that invalid selectors throw exceptions at attach time
			// Evaluate against documentElement in case elem is a non-element node (e.g., document)
			if (selector) {
				jQuery.find.matchesSelector(documentElement, selector);
			}

			// Make sure that the handler has a unique ID, used to find/remove it later
			if (!handler.guid) {
				handler.guid = jQuery.guid++;
			}

			// Init the element's event structure and main handler, if this is the first
			if (!(events = elemData.events)) {
				events = elemData.events = Object.create(null);
			}
			if (!(eventHandle = elemData.handle)) {
				eventHandle = elemData.handle = function (e) {

					// Discard the second event of a jQuery.event.trigger() and
					// when an event is called after a page has unloaded
					return typeof jQuery !== "undefined" && jQuery.event.triggered !== e.type ?
						jQuery.event.dispatch.apply(elem, arguments) : undefined;
				};
			}

			// Handle multiple events separated by a space
			types = (types || "").match(rnothtmlwhite) || [""];
			t = types.length;
			while (t--) {
				tmp = rtypenamespace.exec(types[t]) || [];
				type = origType = tmp[1];
				namespaces = (tmp[2] || "").split(".").sort();

				// There *must* be a type, no attaching namespace-only handlers
				if (!type) {
					continue;
				}

				// If event changes its type, use the special event handlers for the changed type
				special = jQuery.event.special[type] || {};

				// If selector defined, determine special event api type, otherwise given type
				type = (selector ? special.delegateType : special.bindType) || type;

				// Update special based on newly reset type
				special = jQuery.event.special[type] || {};

				// handleObj is passed to all event handlers
				handleObj = jQuery.extend({
					type: type,
					origType: origType,
					data: data,
					handler: handler,
					guid: handler.guid,
					selector: selector,
					needsContext: selector && jQuery.expr.match.needsContext.test(selector),
					namespace: namespaces.join(".")
				}, handleObjIn);

				// Init the event handler queue if we're the first
				if (!(handlers = events[type])) {
					handlers = events[type] = [];
					handlers.delegateCount = 0;

					// Only use addEventListener if the special events handler returns false
					if (!special.setup ||
						special.setup.call(elem, data, namespaces, eventHandle) === false) {

						if (elem.addEventListener) {
							elem.addEventListener(type, eventHandle);
						}
					}
				}

				if (special.add) {
					special.add.call(elem, handleObj);

					if (!handleObj.handler.guid) {
						handleObj.handler.guid = handler.guid;
					}
				}

				// Add to the element's handler list, delegates in front
				if (selector) {
					handlers.splice(handlers.delegateCount++, 0, handleObj);
				} else {
					handlers.push(handleObj);
				}

				// Keep track of which events have ever been used, for event optimization
				jQuery.event.global[type] = true;
			}

		},

		// Detach an event or set of events from an element
		remove: function (elem, types, handler, selector, mappedTypes) {

			var j, origCount, tmp,
				events, t, handleObj,
				special, handlers, type, namespaces, origType,
				elemData = dataPriv.hasData(elem) && dataPriv.get(elem);

			if (!elemData || !(events = elemData.events)) {
				return;
			}

			// Once for each type.namespace in types; type may be omitted
			types = (types || "").match(rnothtmlwhite) || [""];
			t = types.length;
			while (t--) {
				tmp = rtypenamespace.exec(types[t]) || [];
				type = origType = tmp[1];
				namespaces = (tmp[2] || "").split(".").sort();

				// Unbind all events (on this namespace, if provided) for the element
				if (!type) {
					for (type in events) {
						jQuery.event.remove(elem, type + types[t], handler, selector, true);
					}
					continue;
				}

				special = jQuery.event.special[type] || {};
				type = (selector ? special.delegateType : special.bindType) || type;
				handlers = events[type] || [];
				tmp = tmp[2] &&
					new RegExp("(^|\\.)" + namespaces.join("\\.(?:.*\\.|)") + "(\\.|$)");

				// Remove matching events
				origCount = j = handlers.length;
				while (j--) {
					handleObj = handlers[j];

					if ((mappedTypes || origType === handleObj.origType) &&
						(!handler || handler.guid === handleObj.guid) &&
						(!tmp || tmp.test(handleObj.namespace)) &&
						(!selector || selector === handleObj.selector ||
							selector === "**" && handleObj.selector)) {
						handlers.splice(j, 1);

						if (handleObj.selector) {
							handlers.delegateCount--;
						}
						if (special.remove) {
							special.remove.call(elem, handleObj);
						}
					}
				}

				// Remove generic event handler if we removed something and no more handlers exist
				// (avoids potential for endless recursion during removal of special event handlers)
				if (origCount && !handlers.length) {
					if (!special.teardown ||
						special.teardown.call(elem, namespaces, elemData.handle) === false) {

						jQuery.removeEvent(elem, type, elemData.handle);
					}

					delete events[type];
				}
			}

			// Remove data and the expando if it's no longer used
			if (jQuery.isEmptyObject(events)) {
				dataPriv.remove(elem, "handle events");
			}
		},

		dispatch: function (nativeEvent) {

			var i, j, ret, matched, handleObj, handlerQueue,
				args = new Array(arguments.length),

				// Make a writable jQuery.Event from the native event object
				event = jQuery.event.fix(nativeEvent),

				handlers = (
					dataPriv.get(this, "events") || Object.create(null)
				)[event.type] || [],
				special = jQuery.event.special[event.type] || {};

			// Use the fix-ed jQuery.Event rather than the (read-only) native event
			args[0] = event;

			for (i = 1; i < arguments.length; i++) {
				args[i] = arguments[i];
			}

			event.delegateTarget = this;

			// Call the preDispatch hook for the mapped type, and let it bail if desired
			if (special.preDispatch && special.preDispatch.call(this, event) === false) {
				return;
			}

			// Determine handlers
			handlerQueue = jQuery.event.handlers.call(this, event, handlers);

			// Run delegates first; they may want to stop propagation beneath us
			i = 0;
			while ((matched = handlerQueue[i++]) && !event.isPropagationStopped()) {
				event.currentTarget = matched.elem;

				j = 0;
				while ((handleObj = matched.handlers[j++]) &&
					!event.isImmediatePropagationStopped()) {

					// If the event is namespaced, then each handler is only invoked if it is
					// specially universal or its namespaces are a superset of the event's.
					if (!event.rnamespace || handleObj.namespace === false ||
						event.rnamespace.test(handleObj.namespace)) {

						event.handleObj = handleObj;
						event.data = handleObj.data;

						ret = ((jQuery.event.special[handleObj.origType] || {}).handle ||
							handleObj.handler).apply(matched.elem, args);

						if (ret !== undefined) {
							if ((event.result = ret) === false) {
								event.preventDefault();
								event.stopPropagation();
							}
						}
					}
				}
			}

			// Call the postDispatch hook for the mapped type
			if (special.postDispatch) {
				special.postDispatch.call(this, event);
			}

			return event.result;
		},

		handlers: function (event, handlers) {
			var i, handleObj, sel, matchedHandlers, matchedSelectors,
				handlerQueue = [],
				delegateCount = handlers.delegateCount,
				cur = event.target;

			// Find delegate handlers
			if (delegateCount &&

				// Support: IE <=9
				// Black-hole SVG <use> instance trees (trac-13180)
				cur.nodeType &&

				// Support: Firefox <=42
				// Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
				// https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
				// Support: IE 11 only
				// ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
				!(event.type === "click" && event.button >= 1)) {

				for (; cur !== this; cur = cur.parentNode || this) {

					// Don't check non-elements (trac-13208)
					// Don't process clicks on disabled elements (trac-6911, trac-8165, trac-11382, trac-11764)
					if (cur.nodeType === 1 && !(event.type === "click" && cur.disabled === true)) {
						matchedHandlers = [];
						matchedSelectors = {};
						for (i = 0; i < delegateCount; i++) {
							handleObj = handlers[i];

							// Don't conflict with Object.prototype properties (trac-13203)
							sel = handleObj.selector + " ";

							if (matchedSelectors[sel] === undefined) {
								matchedSelectors[sel] = handleObj.needsContext ?
									jQuery(sel, this).index(cur) > -1 :
									jQuery.find(sel, this, null, [cur]).length;
							}
							if (matchedSelectors[sel]) {
								matchedHandlers.push(handleObj);
							}
						}
						if (matchedHandlers.length) {
							handlerQueue.push({ elem: cur, handlers: matchedHandlers });
						}
					}
				}
			}

			// Add the remaining (directly-bound) handlers
			cur = this;
			if (delegateCount < handlers.length) {
				handlerQueue.push({ elem: cur, handlers: handlers.slice(delegateCount) });
			}

			return handlerQueue;
		},

		addProp: function (name, hook) {
			Object.defineProperty(jQuery.Event.prototype, name, {
				enumerable: true,
				configurable: true,

				get: isFunction(hook) ?
					function () {
						if (this.originalEvent) {
							return hook(this.originalEvent);
						}
					} :
					function () {
						if (this.originalEvent) {
							return this.originalEvent[name];
						}
					},

				set: function (value) {
					Object.defineProperty(this, name, {
						enumerable: true,
						configurable: true,
						writable: true,
						value: value
					});
				}
			});
		},

		fix: function (originalEvent) {
			return originalEvent[jQuery.expando] ?
				originalEvent :
				new jQuery.Event(originalEvent);
		},

		special: {
			load: {

				// Prevent triggered image.load events from bubbling to window.load
				noBubble: true
			},
			click: {

				// Utilize native event to ensure correct state for checkable inputs
				setup: function (data) {

					// For mutual compressibility with _default, replace `this` access with a local var.
					// `|| data` is dead code meant only to preserve the variable through minification.
					var el = this || data;

					// Claim the first handler
					if (rcheckableType.test(el.type) &&
						el.click && nodeName(el, "input")) {

						// dataPriv.set( el, "click", ... )
						leverageNative(el, "click", true);
					}

					// Return false to allow normal processing in the caller
					return false;
				},
				trigger: function (data) {

					// For mutual compressibility with _default, replace `this` access with a local var.
					// `|| data` is dead code meant only to preserve the variable through minification.
					var el = this || data;

					// Force setup before triggering a click
					if (rcheckableType.test(el.type) &&
						el.click && nodeName(el, "input")) {

						leverageNative(el, "click");
					}

					// Return non-false to allow normal event-path propagation
					return true;
				},

				// For cross-browser consistency, suppress native .click() on links
				// Also prevent it if we're currently inside a leveraged native-event stack
				_default: function (event) {
					var target = event.target;
					return rcheckableType.test(target.type) &&
						target.click && nodeName(target, "input") &&
						dataPriv.get(target, "click") ||
						nodeName(target, "a");
				}
			},

			beforeunload: {
				postDispatch: function (event) {

					// Support: Firefox 20+
					// Firefox doesn't alert if the returnValue field is not set.
					if (event.result !== undefined && event.originalEvent) {
						event.originalEvent.returnValue = event.result;
					}
				}
			}
		}
	};

	// Ensure the presence of an event listener that handles manually-triggered
	// synthetic events by interrupting progress until reinvoked in response to
	// *native* events that it fires directly, ensuring that state changes have
	// already occurred before other listeners are invoked.
	function leverageNative(el, type, isSetup) {

		// Missing `isSetup` indicates a trigger call, which must force setup through jQuery.event.add
		if (!isSetup) {
			if (dataPriv.get(el, type) === undefined) {
				jQuery.event.add(el, type, returnTrue);
			}
			return;
		}

		// Register the controller as a special universal handler for all event namespaces
		dataPriv.set(el, type, false);
		jQuery.event.add(el, type, {
			namespace: false,
			handler: function (event) {
				var result,
					saved = dataPriv.get(this, type);

				if ((event.isTrigger & 1) && this[type]) {

					// Interrupt processing of the outer synthetic .trigger()ed event
					if (!saved) {

						// Store arguments for use when handling the inner native event
						// There will always be at least one argument (an event object), so this array
						// will not be confused with a leftover capture object.
						saved = slice.call(arguments);
						dataPriv.set(this, type, saved);

						// Trigger the native event and capture its result
						this[type]();
						result = dataPriv.get(this, type);
						dataPriv.set(this, type, false);

						if (saved !== result) {

							// Cancel the outer synthetic event
							event.stopImmediatePropagation();
							event.preventDefault();

							return result;
						}

						// If this is an inner synthetic event for an event with a bubbling surrogate
						// (focus or blur), assume that the surrogate already propagated from triggering
						// the native event and prevent that from happening again here.
						// This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
						// bubbling surrogate propagates *after* the non-bubbling base), but that seems
						// less bad than duplication.
					} else if ((jQuery.event.special[type] || {}).delegateType) {
						event.stopPropagation();
					}

					// If this is a native event triggered above, everything is now in order
					// Fire an inner synthetic event with the original arguments
				} else if (saved) {

					// ...and capture the result
					dataPriv.set(this, type, jQuery.event.trigger(
						saved[0],
						saved.slice(1),
						this
					));

					// Abort handling of the native event by all jQuery handlers while allowing
					// native handlers on the same element to run. On target, this is achieved
					// by stopping immediate propagation just on the jQuery event. However,
					// the native event is re-wrapped by a jQuery one on each level of the
					// propagation so the only way to stop it for jQuery is to stop it for
					// everyone via native `stopPropagation()`. This is not a problem for
					// focus/blur which don't bubble, but it does also stop click on checkboxes
					// and radios. We accept this limitation.
					event.stopPropagation();
					event.isImmediatePropagationStopped = returnTrue;
				}
			}
		});
	}

	jQuery.removeEvent = function (elem, type, handle) {

		// This "if" is needed for plain objects
		if (elem.removeEventListener) {
			elem.removeEventListener(type, handle);
		}
	};

	jQuery.Event = function (src, props) {

		// Allow instantiation without the 'new' keyword
		if (!(this instanceof jQuery.Event)) {
			return new jQuery.Event(src, props);
		}

		// Event object
		if (src && src.type) {
			this.originalEvent = src;
			this.type = src.type;

			// Events bubbling up the document may have been marked as prevented
			// by a handler lower down the tree; reflect the correct value.
			this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: Android <=2.3 only
				src.returnValue === false ?
				returnTrue :
				returnFalse;

			// Create target properties
			// Support: Safari <=6 - 7 only
			// Target should not be a text node (trac-504, trac-13143)
			this.target = (src.target && src.target.nodeType === 3) ?
				src.target.parentNode :
				src.target;

			this.currentTarget = src.currentTarget;
			this.relatedTarget = src.relatedTarget;

			// Event type
		} else {
			this.type = src;
		}

		// Put explicitly provided properties onto the event object
		if (props) {
			jQuery.extend(this, props);
		}

		// Create a timestamp if incoming event doesn't have one
		this.timeStamp = src && src.timeStamp || Date.now();

		// Mark it as fixed
		this[jQuery.expando] = true;
	};

	// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
	// https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
	jQuery.Event.prototype = {
		constructor: jQuery.Event,
		isDefaultPrevented: returnFalse,
		isPropagationStopped: returnFalse,
		isImmediatePropagationStopped: returnFalse,
		isSimulated: false,

		preventDefault: function () {
			var e = this.originalEvent;

			this.isDefaultPrevented = returnTrue;

			if (e && !this.isSimulated) {
				e.preventDefault();
			}
		},
		stopPropagation: function () {
			var e = this.originalEvent;

			this.isPropagationStopped = returnTrue;

			if (e && !this.isSimulated) {
				e.stopPropagation();
			}
		},
		stopImmediatePropagation: function () {
			var e = this.originalEvent;

			this.isImmediatePropagationStopped = returnTrue;

			if (e && !this.isSimulated) {
				e.stopImmediatePropagation();
			}

			this.stopPropagation();
		}
	};

	// Includes all common event props including KeyEvent and MouseEvent specific props
	jQuery.each({
		altKey: true,
		bubbles: true,
		cancelable: true,
		changedTouches: true,
		ctrlKey: true,
		detail: true,
		eventPhase: true,
		metaKey: true,
		pageX: true,
		pageY: true,
		shiftKey: true,
		view: true,
		"char": true,
		code: true,
		charCode: true,
		key: true,
		keyCode: true,
		button: true,
		buttons: true,
		clientX: true,
		clientY: true,
		offsetX: true,
		offsetY: true,
		pointerId: true,
		pointerType: true,
		screenX: true,
		screenY: true,
		targetTouches: true,
		toElement: true,
		touches: true,
		which: true
	}, jQuery.event.addProp);

	jQuery.each({ focus: "focusin", blur: "focusout" }, function (type, delegateType) {

		function focusMappedHandler(nativeEvent) {
			if (document.documentMode) {

				// Support: IE 11+
				// Attach a single focusin/focusout handler on the document while someone wants
				// focus/blur. This is because the former are synchronous in IE while the latter
				// are async. In other browsers, all those handlers are invoked synchronously.

				// `handle` from private data would already wrap the event, but we need
				// to change the `type` here.
				var handle = dataPriv.get(this, "handle"),
					event = jQuery.event.fix(nativeEvent);
				event.type = nativeEvent.type === "focusin" ? "focus" : "blur";
				event.isSimulated = true;

				// First, handle focusin/focusout
				handle(nativeEvent);

				// ...then, handle focus/blur
				//
				// focus/blur don't bubble while focusin/focusout do; simulate the former by only
				// invoking the handler at the lower level.
				if (event.target === event.currentTarget) {

					// The setup part calls `leverageNative`, which, in turn, calls
					// `jQuery.event.add`, so event handle will already have been set
					// by this point.
					handle(event);
				}
			} else {

				// For non-IE browsers, attach a single capturing handler on the document
				// while someone wants focusin/focusout.
				jQuery.event.simulate(delegateType, nativeEvent.target,
					jQuery.event.fix(nativeEvent));
			}
		}

		jQuery.event.special[type] = {

			// Utilize native event if possible so blur/focus sequence is correct
			setup: function () {

				var attaches;

				// Claim the first handler
				// dataPriv.set( this, "focus", ... )
				// dataPriv.set( this, "blur", ... )
				leverageNative(this, type, true);

				if (document.documentMode) {

					// Support: IE 9 - 11+
					// We use the same native handler for focusin & focus (and focusout & blur)
					// so we need to coordinate setup & teardown parts between those events.
					// Use `delegateType` as the key as `type` is already used by `leverageNative`.
					attaches = dataPriv.get(this, delegateType);
					if (!attaches) {
						this.addEventListener(delegateType, focusMappedHandler);
					}
					dataPriv.set(this, delegateType, (attaches || 0) + 1);
				} else {

					// Return false to allow normal processing in the caller
					return false;
				}
			},
			trigger: function () {

				// Force setup before trigger
				leverageNative(this, type);

				// Return non-false to allow normal event-path propagation
				return true;
			},

			teardown: function () {
				var attaches;

				if (document.documentMode) {
					attaches = dataPriv.get(this, delegateType) - 1;
					if (!attaches) {
						this.removeEventListener(delegateType, focusMappedHandler);
						dataPriv.remove(this, delegateType);
					} else {
						dataPriv.set(this, delegateType, attaches);
					}
				} else {

					// Return false to indicate standard teardown should be applied
					return false;
				}
			},

			// Suppress native focus or blur if we're currently inside
			// a leveraged native-event stack
			_default: function (event) {
				return dataPriv.get(event.target, type);
			},

			delegateType: delegateType
		};

		// Support: Firefox <=44
		// Firefox doesn't have focus(in | out) events
		// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
		//
		// Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
		// focus(in | out) events fire after focus & blur events,
		// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
		// Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
		//
		// Support: IE 9 - 11+
		// To preserve relative focusin/focus & focusout/blur event order guaranteed on the 3.x branch,
		// attach a single handler for both events in IE.
		jQuery.event.special[delegateType] = {
			setup: function () {

				// Handle: regular nodes (via `this.ownerDocument`), window
				// (via `this.document`) & document (via `this`).
				var doc = this.ownerDocument || this.document || this,
					dataHolder = document.documentMode ? this : doc,
					attaches = dataPriv.get(dataHolder, delegateType);

				// Support: IE 9 - 11+
				// We use the same native handler for focusin & focus (and focusout & blur)
				// so we need to coordinate setup & teardown parts between those events.
				// Use `delegateType` as the key as `type` is already used by `leverageNative`.
				if (!attaches) {
					if (document.documentMode) {
						this.addEventListener(delegateType, focusMappedHandler);
					} else {
						doc.addEventListener(type, focusMappedHandler, true);
					}
				}
				dataPriv.set(dataHolder, delegateType, (attaches || 0) + 1);
			},
			teardown: function () {
				var doc = this.ownerDocument || this.document || this,
					dataHolder = document.documentMode ? this : doc,
					attaches = dataPriv.get(dataHolder, delegateType) - 1;

				if (!attaches) {
					if (document.documentMode) {
						this.removeEventListener(delegateType, focusMappedHandler);
					} else {
						doc.removeEventListener(type, focusMappedHandler, true);
					}
					dataPriv.remove(dataHolder, delegateType);
				} else {
					dataPriv.set(dataHolder, delegateType, attaches);
				}
			}
		};
	});

	// Create mouseenter/leave events using mouseover/out and event-time checks
	// so that event delegation works in jQuery.
	// Do the same for pointerenter/pointerleave and pointerover/pointerout
	//
	// Support: Safari 7 only
	// Safari sends mouseenter too often; see:
	// https://bugs.chromium.org/p/chromium/issues/detail?id=470258
	// for the description of the bug (it existed in older Chrome versions as well).
	jQuery.each({
		mouseenter: "mouseover",
		mouseleave: "mouseout",
		pointerenter: "pointerover",
		pointerleave: "pointerout"
	}, function (orig, fix) {
		jQuery.event.special[orig] = {
			delegateType: fix,
			bindType: fix,

			handle: function (event) {
				var ret,
					target = this,
					related = event.relatedTarget,
					handleObj = event.handleObj;

				// For mouseenter/leave call the handler if related is outside the target.
				// NB: No relatedTarget if the mouse left/entered the browser window
				if (!related || (related !== target && !jQuery.contains(target, related))) {
					event.type = handleObj.origType;
					ret = handleObj.handler.apply(this, arguments);
					event.type = fix;
				}
				return ret;
			}
		};
	});

	jQuery.fn.extend({

		on: function (types, selector, data, fn) {
			return on(this, types, selector, data, fn);
		},
		one: function (types, selector, data, fn) {
			return on(this, types, selector, data, fn, 1);
		},
		off: function (types, selector, fn) {
			var handleObj, type;
			if (types && types.preventDefault && types.handleObj) {

				// ( event )  dispatched jQuery.Event
				handleObj = types.handleObj;
				jQuery(types.delegateTarget).off(
					handleObj.namespace ?
						handleObj.origType + "." + handleObj.namespace :
						handleObj.origType,
					handleObj.selector,
					handleObj.handler
				);
				return this;
			}
			if (typeof types === "object") {

				// ( types-object [, selector] )
				for (type in types) {
					this.off(type, selector, types[type]);
				}
				return this;
			}
			if (selector === false || typeof selector === "function") {

				// ( types [, fn] )
				fn = selector;
				selector = undefined;
			}
			if (fn === false) {
				fn = returnFalse;
			}
			return this.each(function () {
				jQuery.event.remove(this, types, fn, selector);
			});
		}
	});


	var

		// Support: IE <=10 - 11, Edge 12 - 13 only
		// In IE/Edge using regex groups here causes severe slowdowns.
		// See https://connect.microsoft.com/IE/feedback/details/1736512/
		rnoInnerhtml = /<script|<style|<link/i,

		// checked="checked" or checked
		rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,

		rcleanScript = /^\s*<!\[CDATA\[|\]\]>\s*$/g;

	// Prefer a tbody over its parent table for containing new rows
	function manipulationTarget(elem, content) {
		if (nodeName(elem, "table") &&
			nodeName(content.nodeType !== 11 ? content : content.firstChild, "tr")) {

			return jQuery(elem).children("tbody")[0] || elem;
		}

		return elem;
	}

	// Replace/restore the type attribute of script elements for safe DOM manipulation
	function disableScript(elem) {
		elem.type = (elem.getAttribute("type") !== null) + "/" + elem.type;
		return elem;
	}
	function restoreScript(elem) {
		if ((elem.type || "").slice(0, 5) === "true/") {
			elem.type = elem.type.slice(5);
		} else {
			elem.removeAttribute("type");
		}

		return elem;
	}

	function cloneCopyEvent(src, dest) {
		var i, l, type, pdataOld, udataOld, udataCur, events;

		if (dest.nodeType !== 1) {
			return;
		}

		// 1. Copy private data: events, handlers, etc.
		if (dataPriv.hasData(src)) {
			pdataOld = dataPriv.get(src);
			events = pdataOld.events;

			if (events) {
				dataPriv.remove(dest, "handle events");

				for (type in events) {
					for (i = 0, l = events[type].length; i < l; i++) {
						jQuery.event.add(dest, type, events[type][i]);
					}
				}
			}
		}

		// 2. Copy user data
		if (dataUser.hasData(src)) {
			udataOld = dataUser.access(src);
			udataCur = jQuery.extend({}, udataOld);

			dataUser.set(dest, udataCur);
		}
	}

	// Fix IE bugs, see support tests
	function fixInput(src, dest) {
		var nodeName = dest.nodeName.toLowerCase();

		// Fails to persist the checked state of a cloned checkbox or radio button.
		if (nodeName === "input" && rcheckableType.test(src.type)) {
			dest.checked = src.checked;

			// Fails to return the selected option to the default selected state when cloning options
		} else if (nodeName === "input" || nodeName === "textarea") {
			dest.defaultValue = src.defaultValue;
		}
	}

	function domManip(collection, args, callback, ignored) {

		// Flatten any nested arrays
		args = flat(args);

		var fragment, first, scripts, hasScripts, node, doc,
			i = 0,
			l = collection.length,
			iNoClone = l - 1,
			value = args[0],
			valueIsFunction = isFunction(value);

		// We can't cloneNode fragments that contain checked, in WebKit
		if (valueIsFunction ||
			(l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test(value))) {
			return collection.each(function (index) {
				var self = collection.eq(index);
				if (valueIsFunction) {
					args[0] = value.call(this, index, self.html());
				}
				domManip(self, args, callback, ignored);
			});
		}

		if (l) {
			fragment = buildFragment(args, collection[0].ownerDocument, false, collection, ignored);
			first = fragment.firstChild;

			if (fragment.childNodes.length === 1) {
				fragment = first;
			}

			// Require either new content or an interest in ignored elements to invoke the callback
			if (first || ignored) {
				scripts = jQuery.map(getAll(fragment, "script"), disableScript);
				hasScripts = scripts.length;

				// Use the original fragment for the last item
				// instead of the first because it can end up
				// being emptied incorrectly in certain situations (trac-8070).
				for (; i < l; i++) {
					node = fragment;

					if (i !== iNoClone) {
						node = jQuery.clone(node, true, true);

						// Keep references to cloned scripts for later restoration
						if (hasScripts) {

							// Support: Android <=4.0 only, PhantomJS 1 only
							// push.apply(_, arraylike) throws on ancient WebKit
							jQuery.merge(scripts, getAll(node, "script"));
						}
					}

					callback.call(collection[i], node, i);
				}

				if (hasScripts) {
					doc = scripts[scripts.length - 1].ownerDocument;

					// Reenable scripts
					jQuery.map(scripts, restoreScript);

					// Evaluate executable scripts on first document insertion
					for (i = 0; i < hasScripts; i++) {
						node = scripts[i];
						if (rscriptType.test(node.type || "") &&
							!dataPriv.access(node, "globalEval") &&
							jQuery.contains(doc, node)) {

							if (node.src && (node.type || "").toLowerCase() !== "module") {

								// Optional AJAX dependency, but won't run scripts if not present
								if (jQuery._evalUrl && !node.noModule) {
									jQuery._evalUrl(node.src, {
										nonce: node.nonce || node.getAttribute("nonce")
									}, doc);
								}
							} else {

								// Unwrap a CDATA section containing script contents. This shouldn't be
								// needed as in XML documents they're already not visible when
								// inspecting element contents and in HTML documents they have no
								// meaning but we're preserving that logic for backwards compatibility.
								// This will be removed completely in 4.0. See gh-4904.
								DOMEval(node.textContent.replace(rcleanScript, ""), node, doc);
							}
						}
					}
				}
			}
		}

		return collection;
	}

	function remove(elem, selector, keepData) {
		var node,
			nodes = selector ? jQuery.filter(selector, elem) : elem,
			i = 0;

		for (; (node = nodes[i]) != null; i++) {
			if (!keepData && node.nodeType === 1) {
				jQuery.cleanData(getAll(node));
			}

			if (node.parentNode) {
				if (keepData && isAttached(node)) {
					setGlobalEval(getAll(node, "script"));
				}
				node.parentNode.removeChild(node);
			}
		}

		return elem;
	}

	jQuery.extend({
		htmlPrefilter: function (html) {
			return html;
		},

		clone: function (elem, dataAndEvents, deepDataAndEvents) {
			var i, l, srcElements, destElements,
				clone = elem.cloneNode(true),
				inPage = isAttached(elem);

			// Fix IE cloning issues
			if (!support.noCloneChecked && (elem.nodeType === 1 || elem.nodeType === 11) &&
				!jQuery.isXMLDoc(elem)) {

				// We eschew jQuery#find here for performance reasons:
				// https://jsperf.com/getall-vs-sizzle/2
				destElements = getAll(clone);
				srcElements = getAll(elem);

				for (i = 0, l = srcElements.length; i < l; i++) {
					fixInput(srcElements[i], destElements[i]);
				}
			}

			// Copy the events from the original to the clone
			if (dataAndEvents) {
				if (deepDataAndEvents) {
					srcElements = srcElements || getAll(elem);
					destElements = destElements || getAll(clone);

					for (i = 0, l = srcElements.length; i < l; i++) {
						cloneCopyEvent(srcElements[i], destElements[i]);
					}
				} else {
					cloneCopyEvent(elem, clone);
				}
			}

			// Preserve script evaluation history
			destElements = getAll(clone, "script");
			if (destElements.length > 0) {
				setGlobalEval(destElements, !inPage && getAll(elem, "script"));
			}

			// Return the cloned set
			return clone;
		},

		cleanData: function (elems) {
			var data, elem, type,
				special = jQuery.event.special,
				i = 0;

			for (; (elem = elems[i]) !== undefined; i++) {
				if (acceptData(elem)) {
					if ((data = elem[dataPriv.expando])) {
						if (data.events) {
							for (type in data.events) {
								if (special[type]) {
									jQuery.event.remove(elem, type);

									// This is a shortcut to avoid jQuery.event.remove's overhead
								} else {
									jQuery.removeEvent(elem, type, data.handle);
								}
							}
						}

						// Support: Chrome <=35 - 45+
						// Assign undefined instead of using delete, see Data#remove
						elem[dataPriv.expando] = undefined;
					}
					if (elem[dataUser.expando]) {

						// Support: Chrome <=35 - 45+
						// Assign undefined instead of using delete, see Data#remove
						elem[dataUser.expando] = undefined;
					}
				}
			}
		}
	});

	jQuery.fn.extend({
		detach: function (selector) {
			return remove(this, selector, true);
		},

		remove: function (selector) {
			return remove(this, selector);
		},

		text: function (value) {
			return access(this, function (value) {
				return value === undefined ?
					jQuery.text(this) :
					this.empty().each(function () {
						if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
							this.textContent = value;
						}
					});
			}, null, value, arguments.length);
		},

		append: function () {
			return domManip(this, arguments, function (elem) {
				if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
					var target = manipulationTarget(this, elem);
					target.appendChild(elem);
				}
			});
		},

		prepend: function () {
			return domManip(this, arguments, function (elem) {
				if (this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9) {
					var target = manipulationTarget(this, elem);
					target.insertBefore(elem, target.firstChild);
				}
			});
		},

		before: function () {
			return domManip(this, arguments, function (elem) {
				if (this.parentNode) {
					this.parentNode.insertBefore(elem, this);
				}
			});
		},

		after: function () {
			return domManip(this, arguments, function (elem) {
				if (this.parentNode) {
					this.parentNode.insertBefore(elem, this.nextSibling);
				}
			});
		},

		empty: function () {
			var elem,
				i = 0;

			for (; (elem = this[i]) != null; i++) {
				if (elem.nodeType === 1) {

					// Prevent memory leaks
					jQuery.cleanData(getAll(elem, false));

					// Remove any remaining nodes
					elem.textContent = "";
				}
			}

			return this;
		},

		clone: function (dataAndEvents, deepDataAndEvents) {
			dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
			deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

			return this.map(function () {
				return jQuery.clone(this, dataAndEvents, deepDataAndEvents);
			});
		},

		html: function (value) {
			return access(this, function (value) {
				var elem = this[0] || {},
					i = 0,
					l = this.length;

				if (value === undefined && elem.nodeType === 1) {
					return elem.innerHTML;
				}

				// See if we can take a shortcut and just use innerHTML
				if (typeof value === "string" && !rnoInnerhtml.test(value) &&
					!wrapMap[(rtagName.exec(value) || ["", ""])[1].toLowerCase()]) {

					value = jQuery.htmlPrefilter(value);

					try {
						for (; i < l; i++) {
							elem = this[i] || {};

							// Remove element nodes and prevent memory leaks
							if (elem.nodeType === 1) {
								jQuery.cleanData(getAll(elem, false));
								elem.innerHTML = value;
							}
						}

						elem = 0;

						// If using innerHTML throws an exception, use the fallback method
					} catch (e) { }
				}

				if (elem) {
					this.empty().append(value);
				}
			}, null, value, arguments.length);
		},

		replaceWith: function () {
			var ignored = [];

			// Make the changes, replacing each non-ignored context element with the new content
			return domManip(this, arguments, function (elem) {
				var parent = this.parentNode;

				if (jQuery.inArray(this, ignored) < 0) {
					jQuery.cleanData(getAll(this));
					if (parent) {
						parent.replaceChild(elem, this);
					}
				}

				// Force callback invocation
			}, ignored);
		}
	});

	jQuery.each({
		appendTo: "append",
		prependTo: "prepend",
		insertBefore: "before",
		insertAfter: "after",
		replaceAll: "replaceWith"
	}, function (name, original) {
		jQuery.fn[name] = function (selector) {
			var elems,
				ret = [],
				insert = jQuery(selector),
				last = insert.length - 1,
				i = 0;

			for (; i <= last; i++) {
				elems = i === last ? this : this.clone(true);
				jQuery(insert[i])[original](elems);

				// Support: Android <=4.0 only, PhantomJS 1 only
				// .get() because push.apply(_, arraylike) throws on ancient WebKit
				push.apply(ret, elems.get());
			}

			return this.pushStack(ret);
		};
	});
	var rnumnonpx = new RegExp("^(" + pnum + ")(?!px)[a-z%]+$", "i");

	var rcustomProp = /^--/;


	var getStyles = function (elem) {

		// Support: IE <=11 only, Firefox <=30 (trac-15098, trac-14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if (!view || !view.opener) {
			view = window;
		}

		return view.getComputedStyle(elem);
	};

	var swap = function (elem, options, callback) {
		var ret, name,
			old = {};

		// Remember the old values, and insert the new ones
		for (name in options) {
			old[name] = elem.style[name];
			elem.style[name] = options[name];
		}

		ret = callback.call(elem);

		// Revert the old values
		for (name in options) {
			elem.style[name] = old[name];
		}

		return ret;
	};


	var rboxStyle = new RegExp(cssExpand.join("|"), "i");



	(function () {

		// Executing both pixelPosition & boxSizingReliable tests require only one layout
		// so they're executed at the same time to save the second computation.
		function computeStyleTests() {

			// This is a singleton, we need to execute it only once
			if (!div) {
				return;
			}

			container.style.cssText = "position:absolute;left:-11111px;width:60px;" +
				"margin-top:1px;padding:0;border:0";
			div.style.cssText =
				"position:relative;display:block;box-sizing:border-box;overflow:scroll;" +
				"margin:auto;border:1px;padding:1px;" +
				"width:60%;top:1%";
			documentElement.appendChild(container).appendChild(div);

			var divStyle = window.getComputedStyle(div);
			pixelPositionVal = divStyle.top !== "1%";

			// Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
			reliableMarginLeftVal = roundPixelMeasures(divStyle.marginLeft) === 12;

			// Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
			// Some styles come back with percentage values, even though they shouldn't
			div.style.right = "60%";
			pixelBoxStylesVal = roundPixelMeasures(divStyle.right) === 36;

			// Support: IE 9 - 11 only
			// Detect misreporting of content dimensions for box-sizing:border-box elements
			boxSizingReliableVal = roundPixelMeasures(divStyle.width) === 36;

			// Support: IE 9 only
			// Detect overflow:scroll screwiness (gh-3699)
			// Support: Chrome <=64
			// Don't get tricked when zoom affects offsetWidth (gh-4029)
			div.style.position = "absolute";
			scrollboxSizeVal = roundPixelMeasures(div.offsetWidth / 3) === 12;

			documentElement.removeChild(container);

			// Nullify the div so it wouldn't be stored in the memory and
			// it will also be a sign that checks already performed
			div = null;
		}

		function roundPixelMeasures(measure) {
			return Math.round(parseFloat(measure));
		}

		var pixelPositionVal, boxSizingReliableVal, scrollboxSizeVal, pixelBoxStylesVal,
			reliableTrDimensionsVal, reliableMarginLeftVal,
			container = document.createElement("div"),
			div = document.createElement("div");

		// Finish early in limited (non-browser) environments
		if (!div.style) {
			return;
		}

		// Support: IE <=9 - 11 only
		// Style of cloned element affects source element cloned (trac-8908)
		div.style.backgroundClip = "content-box";
		div.cloneNode(true).style.backgroundClip = "";
		support.clearCloneStyle = div.style.backgroundClip === "content-box";

		jQuery.extend(support, {
			boxSizingReliable: function () {
				computeStyleTests();
				return boxSizingReliableVal;
			},
			pixelBoxStyles: function () {
				computeStyleTests();
				return pixelBoxStylesVal;
			},
			pixelPosition: function () {
				computeStyleTests();
				return pixelPositionVal;
			},
			reliableMarginLeft: function () {
				computeStyleTests();
				return reliableMarginLeftVal;
			},
			scrollboxSize: function () {
				computeStyleTests();
				return scrollboxSizeVal;
			},

			// Support: IE 9 - 11+, Edge 15 - 18+
			// IE/Edge misreport `getComputedStyle` of table rows with width/height
			// set in CSS while `offset*` properties report correct values.
			// Behavior in IE 9 is more subtle than in newer versions & it passes
			// some versions of this test; make sure not to make it pass there!
			//
			// Support: Firefox 70+
			// Only Firefox includes border widths
			// in computed dimensions. (gh-4529)
			reliableTrDimensions: function () {
				var table, tr, trChild, trStyle;
				if (reliableTrDimensionsVal == null) {
					table = document.createElement("table");
					tr = document.createElement("tr");
					trChild = document.createElement("div");

					table.style.cssText = "position:absolute;left:-11111px;border-collapse:separate";
					tr.style.cssText = "border:1px solid";

					// Support: Chrome 86+
					// Height set through cssText does not get applied.
					// Computed height then comes back as 0.
					tr.style.height = "1px";
					trChild.style.height = "9px";

					// Support: Android 8 Chrome 86+
					// In our bodyBackground.html iframe,
					// display for all div elements is set to "inline",
					// which causes a problem only in Android 8 Chrome 86.
					// Ensuring the div is display: block
					// gets around this issue.
					trChild.style.display = "block";

					documentElement
						.appendChild(table)
						.appendChild(tr)
						.appendChild(trChild);

					trStyle = window.getComputedStyle(tr);
					reliableTrDimensionsVal = (parseInt(trStyle.height, 10) +
						parseInt(trStyle.borderTopWidth, 10) +
						parseInt(trStyle.borderBottomWidth, 10)) === tr.offsetHeight;

					documentElement.removeChild(table);
				}
				return reliableTrDimensionsVal;
			}
		});
	})();


	function curCSS(elem, name, computed) {
		var width, minWidth, maxWidth, ret,
			isCustomProp = rcustomProp.test(name),

			// Support: Firefox 51+
			// Retrieving style before computed somehow
			// fixes an issue with getting wrong values
			// on detached elements
			style = elem.style;

		computed = computed || getStyles(elem);

		// getPropertyValue is needed for:
		//   .css('filter') (IE 9 only, trac-12537)
		//   .css('--customProperty) (gh-3144)
		if (computed) {

			// Support: IE <=9 - 11+
			// IE only supports `"float"` in `getPropertyValue`; in computed styles
			// it's only available as `"cssFloat"`. We no longer modify properties
			// sent to `.css()` apart from camelCasing, so we need to check both.
			// Normally, this would create difference in behavior: if
			// `getPropertyValue` returns an empty string, the value returned
			// by `.css()` would be `undefined`. This is usually the case for
			// disconnected elements. However, in IE even disconnected elements
			// with no styles return `"none"` for `getPropertyValue( "float" )`
			ret = computed.getPropertyValue(name) || computed[name];

			if (isCustomProp && ret) {

				// Support: Firefox 105+, Chrome <=105+
				// Spec requires trimming whitespace for custom properties (gh-4926).
				// Firefox only trims leading whitespace. Chrome just collapses
				// both leading & trailing whitespace to a single space.
				//
				// Fall back to `undefined` if empty string returned.
				// This collapses a missing definition with property defined
				// and set to an empty string but there's no standard API
				// allowing us to differentiate them without a performance penalty
				// and returning `undefined` aligns with older jQuery.
				//
				// rtrimCSS treats U+000D CARRIAGE RETURN and U+000C FORM FEED
				// as whitespace while CSS does not, but this is not a problem
				// because CSS preprocessing replaces them with U+000A LINE FEED
				// (which *is* CSS whitespace)
				// https://www.w3.org/TR/css-syntax-3/#input-preprocessing
				ret = ret.replace(rtrimCSS, "$1") || undefined;
			}

			if (ret === "" && !isAttached(elem)) {
				ret = jQuery.style(elem, name);
			}

			// A tribute to the "awesome hack by Dean Edwards"
			// Android Browser returns percentage for some values,
			// but width seems to be reliably pixels.
			// This is against the CSSOM draft spec:
			// https://drafts.csswg.org/cssom/#resolved-values
			if (!support.pixelBoxStyles() && rnumnonpx.test(ret) && rboxStyle.test(name)) {

				// Remember the original values
				width = style.width;
				minWidth = style.minWidth;
				maxWidth = style.maxWidth;

				// Put in the new values to get a computed value out
				style.minWidth = style.maxWidth = style.width = ret;
				ret = computed.width;

				// Revert the changed values
				style.width = width;
				style.minWidth = minWidth;
				style.maxWidth = maxWidth;
			}
		}

		return ret !== undefined ?

			// Support: IE <=9 - 11 only
			// IE returns zIndex value as an integer.
			ret + "" :
			ret;
	}


	function addGetHookIf(conditionFn, hookFn) {

		// Define the hook, we'll check on the first run if it's really needed.
		return {
			get: function () {
				if (conditionFn()) {

					// Hook not needed (or it's not possible to use it due
					// to missing dependency), remove it.
					delete this.get;
					return;
				}

				// Hook needed; redefine it so that the support test is not executed again.
				return (this.get = hookFn).apply(this, arguments);
			}
		};
	}


	var cssPrefixes = ["Webkit", "Moz", "ms"],
		emptyStyle = document.createElement("div").style,
		vendorProps = {};

	// Return a vendor-prefixed property or undefined
	function vendorPropName(name) {

		// Check for vendor prefixed names
		var capName = name[0].toUpperCase() + name.slice(1),
			i = cssPrefixes.length;

		while (i--) {
			name = cssPrefixes[i] + capName;
			if (name in emptyStyle) {
				return name;
			}
		}
	}

	// Return a potentially-mapped jQuery.cssProps or vendor prefixed property
	function finalPropName(name) {
		var final = jQuery.cssProps[name] || vendorProps[name];

		if (final) {
			return final;
		}
		if (name in emptyStyle) {
			return name;
		}
		return vendorProps[name] = vendorPropName(name) || name;
	}


	var

		// Swappable if display is none or starts with table
		// except "table", "table-cell", or "table-caption"
		// See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
		rdisplayswap = /^(none|table(?!-c[ea]).+)/,
		cssShow = { position: "absolute", visibility: "hidden", display: "block" },
		cssNormalTransform = {
			letterSpacing: "0",
			fontWeight: "400"
		};

	function setPositiveNumber(_elem, value, subtract) {

		// Any relative (+/-) values have already been
		// normalized at this point
		var matches = rcssNum.exec(value);
		return matches ?

			// Guard against undefined "subtract", e.g., when used as in cssHooks
			Math.max(0, matches[2] - (subtract || 0)) + (matches[3] || "px") :
			value;
	}

	function boxModelAdjustment(elem, dimension, box, isBorderBox, styles, computedVal) {
		var i = dimension === "width" ? 1 : 0,
			extra = 0,
			delta = 0,
			marginDelta = 0;

		// Adjustment may not be necessary
		if (box === (isBorderBox ? "border" : "content")) {
			return 0;
		}

		for (; i < 4; i += 2) {

			// Both box models exclude margin
			// Count margin delta separately to only add it after scroll gutter adjustment.
			// This is needed to make negative margins work with `outerHeight( true )` (gh-3982).
			if (box === "margin") {
				marginDelta += jQuery.css(elem, box + cssExpand[i], true, styles);
			}

			// If we get here with a content-box, we're seeking "padding" or "border" or "margin"
			if (!isBorderBox) {

				// Add padding
				delta += jQuery.css(elem, "padding" + cssExpand[i], true, styles);

				// For "border" or "margin", add border
				if (box !== "padding") {
					delta += jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);

					// But still keep track of it otherwise
				} else {
					extra += jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);
				}

				// If we get here with a border-box (content + padding + border), we're seeking "content" or
				// "padding" or "margin"
			} else {

				// For "content", subtract padding
				if (box === "content") {
					delta -= jQuery.css(elem, "padding" + cssExpand[i], true, styles);
				}

				// For "content" or "padding", subtract border
				if (box !== "margin") {
					delta -= jQuery.css(elem, "border" + cssExpand[i] + "Width", true, styles);
				}
			}
		}

		// Account for positive content-box scroll gutter when requested by providing computedVal
		if (!isBorderBox && computedVal >= 0) {

			// offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
			// Assuming integer scroll gutter, subtract the rest and round down
			delta += Math.max(0, Math.ceil(
				elem["offset" + dimension[0].toUpperCase() + dimension.slice(1)] -
				computedVal -
				delta -
				extra -
				0.5

				// If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
				// Use an explicit zero to avoid NaN (gh-3964)
			)) || 0;
		}

		return delta + marginDelta;
	}

	function getWidthOrHeight(elem, dimension, extra) {

		// Start with computed style
		var styles = getStyles(elem),

			// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
			// Fake content-box until we know it's needed to know the true value.
			boxSizingNeeded = !support.boxSizingReliable() || extra,
			isBorderBox = boxSizingNeeded &&
				jQuery.css(elem, "boxSizing", false, styles) === "border-box",
			valueIsBorderBox = isBorderBox,

			val = curCSS(elem, dimension, styles),
			offsetProp = "offset" + dimension[0].toUpperCase() + dimension.slice(1);

		// Support: Firefox <=54
		// Return a confounding non-pixel value or feign ignorance, as appropriate.
		if (rnumnonpx.test(val)) {
			if (!extra) {
				return val;
			}
			val = "auto";
		}


		// Support: IE 9 - 11 only
		// Use offsetWidth/offsetHeight for when box sizing is unreliable.
		// In those cases, the computed value can be trusted to be border-box.
		if ((!support.boxSizingReliable() && isBorderBox ||

			// Support: IE 10 - 11+, Edge 15 - 18+
			// IE/Edge misreport `getComputedStyle` of table rows with width/height
			// set in CSS while `offset*` properties report correct values.
			// Interestingly, in some cases IE 9 doesn't suffer from this issue.
			!support.reliableTrDimensions() && nodeName(elem, "tr") ||

			// Fall back to offsetWidth/offsetHeight when value is "auto"
			// This happens for inline elements with no explicit setting (gh-3571)
			val === "auto" ||

			// Support: Android <=4.1 - 4.3 only
			// Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
			!parseFloat(val) && jQuery.css(elem, "display", false, styles) === "inline") &&

			// Make sure the element is visible & connected
			elem.getClientRects().length) {

			isBorderBox = jQuery.css(elem, "boxSizing", false, styles) === "border-box";

			// Where available, offsetWidth/offsetHeight approximate border box dimensions.
			// Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
			// retrieved value as a content box dimension.
			valueIsBorderBox = offsetProp in elem;
			if (valueIsBorderBox) {
				val = elem[offsetProp];
			}
		}

		// Normalize "" and auto
		val = parseFloat(val) || 0;

		// Adjust for the element's box model
		return (val +
			boxModelAdjustment(
				elem,
				dimension,
				extra || (isBorderBox ? "border" : "content"),
				valueIsBorderBox,
				styles,

				// Provide the current computed size to request scroll gutter calculation (gh-3589)
				val
			)
		) + "px";
	}

	jQuery.extend({

		// Add in style property hooks for overriding the default
		// behavior of getting and setting a style property
		cssHooks: {
			opacity: {
				get: function (elem, computed) {
					if (computed) {

						// We should always get a number back from opacity
						var ret = curCSS(elem, "opacity");
						return ret === "" ? "1" : ret;
					}
				}
			}
		},

		// Don't automatically add "px" to these possibly-unitless properties
		cssNumber: {
			animationIterationCount: true,
			aspectRatio: true,
			borderImageSlice: true,
			columnCount: true,
			flexGrow: true,
			flexShrink: true,
			fontWeight: true,
			gridArea: true,
			gridColumn: true,
			gridColumnEnd: true,
			gridColumnStart: true,
			gridRow: true,
			gridRowEnd: true,
			gridRowStart: true,
			lineHeight: true,
			opacity: true,
			order: true,
			orphans: true,
			scale: true,
			widows: true,
			zIndex: true,
			zoom: true,

			// SVG-related
			fillOpacity: true,
			floodOpacity: true,
			stopOpacity: true,
			strokeMiterlimit: true,
			strokeOpacity: true
		},

		// Add in properties whose names you wish to fix before
		// setting or getting the value
		cssProps: {},

		// Get and set the style property on a DOM Node
		style: function (elem, name, value, extra) {

			// Don't set styles on text and comment nodes
			if (!elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style) {
				return;
			}

			// Make sure that we're working with the right name
			var ret, type, hooks,
				origName = camelCase(name),
				isCustomProp = rcustomProp.test(name),
				style = elem.style;

			// Make sure that we're working with the right name. We don't
			// want to query the value if it is a CSS custom property
			// since they are user-defined.
			if (!isCustomProp) {
				name = finalPropName(origName);
			}

			// Gets hook for the prefixed version, then unprefixed version
			hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

			// Check if we're setting a value
			if (value !== undefined) {
				type = typeof value;

				// Convert "+=" or "-=" to relative numbers (trac-7345)
				if (type === "string" && (ret = rcssNum.exec(value)) && ret[1]) {
					value = adjustCSS(elem, name, ret);

					// Fixes bug trac-9237
					type = "number";
				}

				// Make sure that null and NaN values aren't set (trac-7116)
				if (value == null || value !== value) {
					return;
				}

				// If a number was passed in, add the unit (except for certain CSS properties)
				// The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
				// "px" to a few hardcoded values.
				if (type === "number" && !isCustomProp) {
					value += ret && ret[3] || (jQuery.cssNumber[origName] ? "" : "px");
				}

				// background-* props affect original clone's values
				if (!support.clearCloneStyle && value === "" && name.indexOf("background") === 0) {
					style[name] = "inherit";
				}

				// If a hook was provided, use that value, otherwise just set the specified value
				if (!hooks || !("set" in hooks) ||
					(value = hooks.set(elem, value, extra)) !== undefined) {

					if (isCustomProp) {
						style.setProperty(name, value);
					} else {
						style[name] = value;
					}
				}

			} else {

				// If a hook was provided get the non-computed value from there
				if (hooks && "get" in hooks &&
					(ret = hooks.get(elem, false, extra)) !== undefined) {

					return ret;
				}

				// Otherwise just get the value from the style object
				return style[name];
			}
		},

		css: function (elem, name, extra, styles) {
			var val, num, hooks,
				origName = camelCase(name),
				isCustomProp = rcustomProp.test(name);

			// Make sure that we're working with the right name. We don't
			// want to modify the value if it is a CSS custom property
			// since they are user-defined.
			if (!isCustomProp) {
				name = finalPropName(origName);
			}

			// Try prefixed name followed by the unprefixed name
			hooks = jQuery.cssHooks[name] || jQuery.cssHooks[origName];

			// If a hook was provided get the computed value from there
			if (hooks && "get" in hooks) {
				val = hooks.get(elem, true, extra);
			}

			// Otherwise, if a way to get the computed value exists, use that
			if (val === undefined) {
				val = curCSS(elem, name, styles);
			}

			// Convert "normal" to computed value
			if (val === "normal" && name in cssNormalTransform) {
				val = cssNormalTransform[name];
			}

			// Make numeric if forced or a qualifier was provided and val looks numeric
			if (extra === "" || extra) {
				num = parseFloat(val);
				return extra === true || isFinite(num) ? num || 0 : val;
			}

			return val;
		}
	});

	jQuery.each(["height", "width"], function (_i, dimension) {
		jQuery.cssHooks[dimension] = {
			get: function (elem, computed, extra) {
				if (computed) {

					// Certain elements can have dimension info if we invisibly show them
					// but it must have a current display style that would benefit
					return rdisplayswap.test(jQuery.css(elem, "display")) &&

						// Support: Safari 8+
						// Table columns in Safari have non-zero offsetWidth & zero
						// getBoundingClientRect().width unless display is changed.
						// Support: IE <=11 only
						// Running getBoundingClientRect on a disconnected node
						// in IE throws an error.
						(!elem.getClientRects().length || !elem.getBoundingClientRect().width) ?
						swap(elem, cssShow, function () {
							return getWidthOrHeight(elem, dimension, extra);
						}) :
						getWidthOrHeight(elem, dimension, extra);
				}
			},

			set: function (elem, value, extra) {
				var matches,
					styles = getStyles(elem),

					// Only read styles.position if the test has a chance to fail
					// to avoid forcing a reflow.
					scrollboxSizeBuggy = !support.scrollboxSize() &&
						styles.position === "absolute",

					// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
					boxSizingNeeded = scrollboxSizeBuggy || extra,
					isBorderBox = boxSizingNeeded &&
						jQuery.css(elem, "boxSizing", false, styles) === "border-box",
					subtract = extra ?
						boxModelAdjustment(
							elem,
							dimension,
							extra,
							isBorderBox,
							styles
						) :
						0;

				// Account for unreliable border-box dimensions by comparing offset* to computed and
				// faking a content-box to get border and padding (gh-3699)
				if (isBorderBox && scrollboxSizeBuggy) {
					subtract -= Math.ceil(
						elem["offset" + dimension[0].toUpperCase() + dimension.slice(1)] -
						parseFloat(styles[dimension]) -
						boxModelAdjustment(elem, dimension, "border", false, styles) -
						0.5
					);
				}

				// Convert to pixels if value adjustment is needed
				if (subtract && (matches = rcssNum.exec(value)) &&
					(matches[3] || "px") !== "px") {

					elem.style[dimension] = value;
					value = jQuery.css(elem, dimension);
				}

				return setPositiveNumber(elem, value, subtract);
			}
		};
	});

	jQuery.cssHooks.marginLeft = addGetHookIf(support.reliableMarginLeft,
		function (elem, computed) {
			if (computed) {
				return (parseFloat(curCSS(elem, "marginLeft")) ||
					elem.getBoundingClientRect().left -
					swap(elem, { marginLeft: 0 }, function () {
						return elem.getBoundingClientRect().left;
					})
				) + "px";
			}
		}
	);

	// These hooks are used by animate to expand properties
	jQuery.each({
		margin: "",
		padding: "",
		border: "Width"
	}, function (prefix, suffix) {
		jQuery.cssHooks[prefix + suffix] = {
			expand: function (value) {
				var i = 0,
					expanded = {},

					// Assumes a single number if not a string
					parts = typeof value === "string" ? value.split(" ") : [value];

				for (; i < 4; i++) {
					expanded[prefix + cssExpand[i] + suffix] =
						parts[i] || parts[i - 2] || parts[0];
				}

				return expanded;
			}
		};

		if (prefix !== "margin") {
			jQuery.cssHooks[prefix + suffix].set = setPositiveNumber;
		}
	});

	jQuery.fn.extend({
		css: function (name, value) {
			return access(this, function (elem, name, value) {
				var styles, len,
					map = {},
					i = 0;

				if (Array.isArray(name)) {
					styles = getStyles(elem);
					len = name.length;

					for (; i < len; i++) {
						map[name[i]] = jQuery.css(elem, name[i], false, styles);
					}

					return map;
				}

				return value !== undefined ?
					jQuery.style(elem, name, value) :
					jQuery.css(elem, name);
			}, name, value, arguments.length > 1);
		}
	});


	function Tween(elem, options, prop, end, easing) {
		return new Tween.prototype.init(elem, options, prop, end, easing);
	}
	jQuery.Tween = Tween;

	Tween.prototype = {
		constructor: Tween,
		init: function (elem, options, prop, end, easing, unit) {
			this.elem = elem;
			this.prop = prop;
			this.easing = easing || jQuery.easing._default;
			this.options = options;
			this.start = this.now = this.cur();
			this.end = end;
			this.unit = unit || (jQuery.cssNumber[prop] ? "" : "px");
		},
		cur: function () {
			var hooks = Tween.propHooks[this.prop];

			return hooks && hooks.get ?
				hooks.get(this) :
				Tween.propHooks._default.get(this);
		},
		run: function (percent) {
			var eased,
				hooks = Tween.propHooks[this.prop];

			if (this.options.duration) {
				this.pos = eased = jQuery.easing[this.easing](
					percent, this.options.duration * percent, 0, 1, this.options.duration
				);
			} else {
				this.pos = eased = percent;
			}
			this.now = (this.end - this.start) * eased + this.start;

			if (this.options.step) {
				this.options.step.call(this.elem, this.now, this);
			}

			if (hooks && hooks.set) {
				hooks.set(this);
			} else {
				Tween.propHooks._default.set(this);
			}
			return this;
		}
	};

	Tween.prototype.init.prototype = Tween.prototype;

	Tween.propHooks = {
		_default: {
			get: function (tween) {
				var result;

				// Use a property on the element directly when it is not a DOM element,
				// or when there is no matching style property that exists.
				if (tween.elem.nodeType !== 1 ||
					tween.elem[tween.prop] != null && tween.elem.style[tween.prop] == null) {
					return tween.elem[tween.prop];
				}

				// Passing an empty string as a 3rd parameter to .css will automatically
				// attempt a parseFloat and fallback to a string if the parse fails.
				// Simple values such as "10px" are parsed to Float;
				// complex values such as "rotate(1rad)" are returned as-is.
				result = jQuery.css(tween.elem, tween.prop, "");

				// Empty strings, null, undefined and "auto" are converted to 0.
				return !result || result === "auto" ? 0 : result;
			},
			set: function (tween) {

				// Use step hook for back compat.
				// Use cssHook if its there.
				// Use .style if available and use plain properties where available.
				if (jQuery.fx.step[tween.prop]) {
					jQuery.fx.step[tween.prop](tween);
				} else if (tween.elem.nodeType === 1 && (
					jQuery.cssHooks[tween.prop] ||
					tween.elem.style[finalPropName(tween.prop)] != null)) {
					jQuery.style(tween.elem, tween.prop, tween.now + tween.unit);
				} else {
					tween.elem[tween.prop] = tween.now;
				}
			}
		}
	};

	// Support: IE <=9 only
	// Panic based approach to setting things on disconnected nodes
	Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
		set: function (tween) {
			if (tween.elem.nodeType && tween.elem.parentNode) {
				tween.elem[tween.prop] = tween.now;
			}
		}
	};

	jQuery.easing = {
		linear: function (p) {
			return p;
		},
		swing: function (p) {
			return 0.5 - Math.cos(p * Math.PI) / 2;
		},
		_default: "swing"
	};

	jQuery.fx = Tween.prototype.init;

	// Back compat <1.8 extension point
	jQuery.fx.step = {};




	var
		fxNow, inProgress,
		rfxtypes = /^(?:toggle|show|hide)$/,
		rrun = /queueHooks$/;

	function schedule() {
		if (inProgress) {
			if (document.hidden === false && window.requestAnimationFrame) {
				window.requestAnimationFrame(schedule);
			} else {
				window.setTimeout(schedule, jQuery.fx.interval);
			}

			jQuery.fx.tick();
		}
	}

	// Animations created synchronously will run synchronously
	function createFxNow() {
		window.setTimeout(function () {
			fxNow = undefined;
		});
		return (fxNow = Date.now());
	}

	// Generate parameters to create a standard animation
	function genFx(type, includeWidth) {
		var which,
			i = 0,
			attrs = { height: type };

		// If we include width, step value is 1 to do all cssExpand values,
		// otherwise step value is 2 to skip over Left and Right
		includeWidth = includeWidth ? 1 : 0;
		for (; i < 4; i += 2 - includeWidth) {
			which = cssExpand[i];
			attrs["margin" + which] = attrs["padding" + which] = type;
		}

		if (includeWidth) {
			attrs.opacity = attrs.width = type;
		}

		return attrs;
	}

	function createTween(value, prop, animation) {
		var tween,
			collection = (Animation.tweeners[prop] || []).concat(Animation.tweeners["*"]),
			index = 0,
			length = collection.length;
		for (; index < length; index++) {
			if ((tween = collection[index].call(animation, prop, value))) {

				// We're done with this property
				return tween;
			}
		}
	}

	function defaultPrefilter(elem, props, opts) {
		var prop, value, toggle, hooks, oldfire, propTween, restoreDisplay, display,
			isBox = "width" in props || "height" in props,
			anim = this,
			orig = {},
			style = elem.style,
			hidden = elem.nodeType && isHiddenWithinTree(elem),
			dataShow = dataPriv.get(elem, "fxshow");

		// Queue-skipping animations hijack the fx hooks
		if (!opts.queue) {
			hooks = jQuery._queueHooks(elem, "fx");
			if (hooks.unqueued == null) {
				hooks.unqueued = 0;
				oldfire = hooks.empty.fire;
				hooks.empty.fire = function () {
					if (!hooks.unqueued) {
						oldfire();
					}
				};
			}
			hooks.unqueued++;

			anim.always(function () {

				// Ensure the complete handler is called before this completes
				anim.always(function () {
					hooks.unqueued--;
					if (!jQuery.queue(elem, "fx").length) {
						hooks.empty.fire();
					}
				});
			});
		}

		// Detect show/hide animations
		for (prop in props) {
			value = props[prop];
			if (rfxtypes.test(value)) {
				delete props[prop];
				toggle = toggle || value === "toggle";
				if (value === (hidden ? "hide" : "show")) {

					// Pretend to be hidden if this is a "show" and
					// there is still data from a stopped show/hide
					if (value === "show" && dataShow && dataShow[prop] !== undefined) {
						hidden = true;

						// Ignore all other no-op show/hide data
					} else {
						continue;
					}
				}
				orig[prop] = dataShow && dataShow[prop] || jQuery.style(elem, prop);
			}
		}

		// Bail out if this is a no-op like .hide().hide()
		propTween = !jQuery.isEmptyObject(props);
		if (!propTween && jQuery.isEmptyObject(orig)) {
			return;
		}

		// Restrict "overflow" and "display" styles during box animations
		if (isBox && elem.nodeType === 1) {

			// Support: IE <=9 - 11, Edge 12 - 15
			// Record all 3 overflow attributes because IE does not infer the shorthand
			// from identically-valued overflowX and overflowY and Edge just mirrors
			// the overflowX value there.
			opts.overflow = [style.overflow, style.overflowX, style.overflowY];

			// Identify a display type, preferring old show/hide data over the CSS cascade
			restoreDisplay = dataShow && dataShow.display;
			if (restoreDisplay == null) {
				restoreDisplay = dataPriv.get(elem, "display");
			}
			display = jQuery.css(elem, "display");
			if (display === "none") {
				if (restoreDisplay) {
					display = restoreDisplay;
				} else {

					// Get nonempty value(s) by temporarily forcing visibility
					showHide([elem], true);
					restoreDisplay = elem.style.display || restoreDisplay;
					display = jQuery.css(elem, "display");
					showHide([elem]);
				}
			}

			// Animate inline elements as inline-block
			if (display === "inline" || display === "inline-block" && restoreDisplay != null) {
				if (jQuery.css(elem, "float") === "none") {

					// Restore the original display value at the end of pure show/hide animations
					if (!propTween) {
						anim.done(function () {
							style.display = restoreDisplay;
						});
						if (restoreDisplay == null) {
							display = style.display;
							restoreDisplay = display === "none" ? "" : display;
						}
					}
					style.display = "inline-block";
				}
			}
		}

		if (opts.overflow) {
			style.overflow = "hidden";
			anim.always(function () {
				style.overflow = opts.overflow[0];
				style.overflowX = opts.overflow[1];
				style.overflowY = opts.overflow[2];
			});
		}

		// Implement show/hide animations
		propTween = false;
		for (prop in orig) {

			// General show/hide setup for this element animation
			if (!propTween) {
				if (dataShow) {
					if ("hidden" in dataShow) {
						hidden = dataShow.hidden;
					}
				} else {
					dataShow = dataPriv.access(elem, "fxshow", { display: restoreDisplay });
				}

				// Store hidden/visible for toggle so `.stop().toggle()` "reverses"
				if (toggle) {
					dataShow.hidden = !hidden;
				}

				// Show elements before animating them
				if (hidden) {
					showHide([elem], true);
				}

				/* eslint-disable no-loop-func */

				anim.done(function () {

					/* eslint-enable no-loop-func */

					// The final step of a "hide" animation is actually hiding the element
					if (!hidden) {
						showHide([elem]);
					}
					dataPriv.remove(elem, "fxshow");
					for (prop in orig) {
						jQuery.style(elem, prop, orig[prop]);
					}
				});
			}

			// Per-property setup
			propTween = createTween(hidden ? dataShow[prop] : 0, prop, anim);
			if (!(prop in dataShow)) {
				dataShow[prop] = propTween.start;
				if (hidden) {
					propTween.end = propTween.start;
					propTween.start = 0;
				}
			}
		}
	}

	function propFilter(props, specialEasing) {
		var index, name, easing, value, hooks;

		// camelCase, specialEasing and expand cssHook pass
		for (index in props) {
			name = camelCase(index);
			easing = specialEasing[name];
			value = props[index];
			if (Array.isArray(value)) {
				easing = value[1];
				value = props[index] = value[0];
			}

			if (index !== name) {
				props[name] = value;
				delete props[index];
			}

			hooks = jQuery.cssHooks[name];
			if (hooks && "expand" in hooks) {
				value = hooks.expand(value);
				delete props[name];

				// Not quite $.extend, this won't overwrite existing keys.
				// Reusing 'index' because we have the correct "name"
				for (index in value) {
					if (!(index in props)) {
						props[index] = value[index];
						specialEasing[index] = easing;
					}
				}
			} else {
				specialEasing[name] = easing;
			}
		}
	}

	function Animation(elem, properties, options) {
		var result,
			stopped,
			index = 0,
			length = Animation.prefilters.length,
			deferred = jQuery.Deferred().always(function () {

				// Don't match elem in the :animated selector
				delete tick.elem;
			}),
			tick = function () {
				if (stopped) {
					return false;
				}
				var currentTime = fxNow || createFxNow(),
					remaining = Math.max(0, animation.startTime + animation.duration - currentTime),

					// Support: Android 2.3 only
					// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (trac-12497)
					temp = remaining / animation.duration || 0,
					percent = 1 - temp,
					index = 0,
					length = animation.tweens.length;

				for (; index < length; index++) {
					animation.tweens[index].run(percent);
				}

				deferred.notifyWith(elem, [animation, percent, remaining]);

				// If there's more to do, yield
				if (percent < 1 && length) {
					return remaining;
				}

				// If this was an empty animation, synthesize a final progress notification
				if (!length) {
					deferred.notifyWith(elem, [animation, 1, 0]);
				}

				// Resolve the animation and report its conclusion
				deferred.resolveWith(elem, [animation]);
				return false;
			},
			animation = deferred.promise({
				elem: elem,
				props: jQuery.extend({}, properties),
				opts: jQuery.extend(true, {
					specialEasing: {},
					easing: jQuery.easing._default
				}, options),
				originalProperties: properties,
				originalOptions: options,
				startTime: fxNow || createFxNow(),
				duration: options.duration,
				tweens: [],
				createTween: function (prop, end) {
					var tween = jQuery.Tween(elem, animation.opts, prop, end,
						animation.opts.specialEasing[prop] || animation.opts.easing);
					animation.tweens.push(tween);
					return tween;
				},
				stop: function (gotoEnd) {
					var index = 0,

						// If we are going to the end, we want to run all the tweens
						// otherwise we skip this part
						length = gotoEnd ? animation.tweens.length : 0;
					if (stopped) {
						return this;
					}
					stopped = true;
					for (; index < length; index++) {
						animation.tweens[index].run(1);
					}

					// Resolve when we played the last frame; otherwise, reject
					if (gotoEnd) {
						deferred.notifyWith(elem, [animation, 1, 0]);
						deferred.resolveWith(elem, [animation, gotoEnd]);
					} else {
						deferred.rejectWith(elem, [animation, gotoEnd]);
					}
					return this;
				}
			}),
			props = animation.props;

		propFilter(props, animation.opts.specialEasing);

		for (; index < length; index++) {
			result = Animation.prefilters[index].call(animation, elem, props, animation.opts);
			if (result) {
				if (isFunction(result.stop)) {
					jQuery._queueHooks(animation.elem, animation.opts.queue).stop =
						result.stop.bind(result);
				}
				return result;
			}
		}

		jQuery.map(props, createTween, animation);

		if (isFunction(animation.opts.start)) {
			animation.opts.start.call(elem, animation);
		}

		// Attach callbacks from options
		animation
			.progress(animation.opts.progress)
			.done(animation.opts.done, animation.opts.complete)
			.fail(animation.opts.fail)
			.always(animation.opts.always);

		jQuery.fx.timer(
			jQuery.extend(tick, {
				elem: elem,
				anim: animation,
				queue: animation.opts.queue
			})
		);

		return animation;
	}

	jQuery.Animation = jQuery.extend(Animation, {

		tweeners: {
			"*": [function (prop, value) {
				var tween = this.createTween(prop, value);
				adjustCSS(tween.elem, prop, rcssNum.exec(value), tween);
				return tween;
			}]
		},

		tweener: function (props, callback) {
			if (isFunction(props)) {
				callback = props;
				props = ["*"];
			} else {
				props = props.match(rnothtmlwhite);
			}

			var prop,
				index = 0,
				length = props.length;

			for (; index < length; index++) {
				prop = props[index];
				Animation.tweeners[prop] = Animation.tweeners[prop] || [];
				Animation.tweeners[prop].unshift(callback);
			}
		},

		prefilters: [defaultPrefilter],

		prefilter: function (callback, prepend) {
			if (prepend) {
				Animation.prefilters.unshift(callback);
			} else {
				Animation.prefilters.push(callback);
			}
		}
	});

	jQuery.speed = function (speed, easing, fn) {
		var opt = speed && typeof speed === "object" ? jQuery.extend({}, speed) : {
			complete: fn || !fn && easing ||
				isFunction(speed) && speed,
			duration: speed,
			easing: fn && easing || easing && !isFunction(easing) && easing
		};

		// Go to the end state if fx are off
		if (jQuery.fx.off) {
			opt.duration = 0;

		} else {
			if (typeof opt.duration !== "number") {
				if (opt.duration in jQuery.fx.speeds) {
					opt.duration = jQuery.fx.speeds[opt.duration];

				} else {
					opt.duration = jQuery.fx.speeds._default;
				}
			}
		}

		// Normalize opt.queue - true/undefined/null -> "fx"
		if (opt.queue == null || opt.queue === true) {
			opt.queue = "fx";
		}

		// Queueing
		opt.old = opt.complete;

		opt.complete = function () {
			if (isFunction(opt.old)) {
				opt.old.call(this);
			}

			if (opt.queue) {
				jQuery.dequeue(this, opt.queue);
			}
		};

		return opt;
	};

	jQuery.fn.extend({
		fadeTo: function (speed, to, easing, callback) {

			// Show any hidden elements after setting opacity to 0
			return this.filter(isHiddenWithinTree).css("opacity", 0).show()

				// Animate to the value specified
				.end().animate({ opacity: to }, speed, easing, callback);
		},
		animate: function (prop, speed, easing, callback) {
			var empty = jQuery.isEmptyObject(prop),
				optall = jQuery.speed(speed, easing, callback),
				doAnimation = function () {

					// Operate on a copy of prop so per-property easing won't be lost
					var anim = Animation(this, jQuery.extend({}, prop), optall);

					// Empty animations, or finishing resolves immediately
					if (empty || dataPriv.get(this, "finish")) {
						anim.stop(true);
					}
				};

			doAnimation.finish = doAnimation;

			return empty || optall.queue === false ?
				this.each(doAnimation) :
				this.queue(optall.queue, doAnimation);
		},
		stop: function (type, clearQueue, gotoEnd) {
			var stopQueue = function (hooks) {
				var stop = hooks.stop;
				delete hooks.stop;
				stop(gotoEnd);
			};

			if (typeof type !== "string") {
				gotoEnd = clearQueue;
				clearQueue = type;
				type = undefined;
			}
			if (clearQueue) {
				this.queue(type || "fx", []);
			}

			return this.each(function () {
				var dequeue = true,
					index = type != null && type + "queueHooks",
					timers = jQuery.timers,
					data = dataPriv.get(this);

				if (index) {
					if (data[index] && data[index].stop) {
						stopQueue(data[index]);
					}
				} else {
					for (index in data) {
						if (data[index] && data[index].stop && rrun.test(index)) {
							stopQueue(data[index]);
						}
					}
				}

				for (index = timers.length; index--;) {
					if (timers[index].elem === this &&
						(type == null || timers[index].queue === type)) {

						timers[index].anim.stop(gotoEnd);
						dequeue = false;
						timers.splice(index, 1);
					}
				}

				// Start the next in the queue if the last step wasn't forced.
				// Timers currently will call their complete callbacks, which
				// will dequeue but only if they were gotoEnd.
				if (dequeue || !gotoEnd) {
					jQuery.dequeue(this, type);
				}
			});
		},
		finish: function (type) {
			if (type !== false) {
				type = type || "fx";
			}
			return this.each(function () {
				var index,
					data = dataPriv.get(this),
					queue = data[type + "queue"],
					hooks = data[type + "queueHooks"],
					timers = jQuery.timers,
					length = queue ? queue.length : 0;

				// Enable finishing flag on private data
				data.finish = true;

				// Empty the queue first
				jQuery.queue(this, type, []);

				if (hooks && hooks.stop) {
					hooks.stop.call(this, true);
				}

				// Look for any active animations, and finish them
				for (index = timers.length; index--;) {
					if (timers[index].elem === this && timers[index].queue === type) {
						timers[index].anim.stop(true);
						timers.splice(index, 1);
					}
				}

				// Look for any animations in the old queue and finish them
				for (index = 0; index < length; index++) {
					if (queue[index] && queue[index].finish) {
						queue[index].finish.call(this);
					}
				}

				// Turn off finishing flag
				delete data.finish;
			});
		}
	});

	jQuery.each(["toggle", "show", "hide"], function (_i, name) {
		var cssFn = jQuery.fn[name];
		jQuery.fn[name] = function (speed, easing, callback) {
			return speed == null || typeof speed === "boolean" ?
				cssFn.apply(this, arguments) :
				this.animate(genFx(name, true), speed, easing, callback);
		};
	});

	// Generate shortcuts for custom animations
	jQuery.each({
		slideDown: genFx("show"),
		slideUp: genFx("hide"),
		slideToggle: genFx("toggle"),
		fadeIn: { opacity: "show" },
		fadeOut: { opacity: "hide" },
		fadeToggle: { opacity: "toggle" }
	}, function (name, props) {
		jQuery.fn[name] = function (speed, easing, callback) {
			return this.animate(props, speed, easing, callback);
		};
	});

	jQuery.timers = [];
	jQuery.fx.tick = function () {
		var timer,
			i = 0,
			timers = jQuery.timers;

		fxNow = Date.now();

		for (; i < timers.length; i++) {
			timer = timers[i];

			// Run the timer and safely remove it when done (allowing for external removal)
			if (!timer() && timers[i] === timer) {
				timers.splice(i--, 1);
			}
		}

		if (!timers.length) {
			jQuery.fx.stop();
		}
		fxNow = undefined;
	};

	jQuery.fx.timer = function (timer) {
		jQuery.timers.push(timer);
		jQuery.fx.start();
	};

	jQuery.fx.interval = 13;
	jQuery.fx.start = function () {
		if (inProgress) {
			return;
		}

		inProgress = true;
		schedule();
	};

	jQuery.fx.stop = function () {
		inProgress = null;
	};

	jQuery.fx.speeds = {
		slow: 600,
		fast: 200,

		// Default speed
		_default: 400
	};


	// Based off of the plugin by Clint Helfers, with permission.
	jQuery.fn.delay = function (time, type) {
		time = jQuery.fx ? jQuery.fx.speeds[time] || time : time;
		type = type || "fx";

		return this.queue(type, function (next, hooks) {
			var timeout = window.setTimeout(next, time);
			hooks.stop = function () {
				window.clearTimeout(timeout);
			};
		});
	};


	(function () {
		var input = document.createElement("input"),
			select = document.createElement("select"),
			opt = select.appendChild(document.createElement("option"));

		input.type = "checkbox";

		// Support: Android <=4.3 only
		// Default value for a checkbox should be "on"
		support.checkOn = input.value !== "";

		// Support: IE <=11 only
		// Must access selectedIndex to make default options select
		support.optSelected = opt.selected;

		// Support: IE <=11 only
		// An input loses its value after becoming a radio
		input = document.createElement("input");
		input.value = "t";
		input.type = "radio";
		support.radioValue = input.value === "t";
	})();


	var boolHook,
		attrHandle = jQuery.expr.attrHandle;

	jQuery.fn.extend({
		attr: function (name, value) {
			return access(this, jQuery.attr, name, value, arguments.length > 1);
		},

		removeAttr: function (name) {
			return this.each(function () {
				jQuery.removeAttr(this, name);
			});
		}
	});

	jQuery.extend({
		attr: function (elem, name, value) {
			var ret, hooks,
				nType = elem.nodeType;

			// Don't get/set attributes on text, comment and attribute nodes
			if (nType === 3 || nType === 8 || nType === 2) {
				return;
			}

			// Fallback to prop when attributes are not supported
			if (typeof elem.getAttribute === "undefined") {
				return jQuery.prop(elem, name, value);
			}

			// Attribute hooks are determined by the lowercase version
			// Grab necessary hook if one is defined
			if (nType !== 1 || !jQuery.isXMLDoc(elem)) {
				hooks = jQuery.attrHooks[name.toLowerCase()] ||
					(jQuery.expr.match.bool.test(name) ? boolHook : undefined);
			}

			if (value !== undefined) {
				if (value === null) {
					jQuery.removeAttr(elem, name);
					return;
				}

				if (hooks && "set" in hooks &&
					(ret = hooks.set(elem, value, name)) !== undefined) {
					return ret;
				}

				elem.setAttribute(name, value + "");
				return value;
			}

			if (hooks && "get" in hooks && (ret = hooks.get(elem, name)) !== null) {
				return ret;
			}

			ret = jQuery.find.attr(elem, name);

			// Non-existent attributes return null, we normalize to undefined
			return ret == null ? undefined : ret;
		},

		attrHooks: {
			type: {
				set: function (elem, value) {
					if (!support.radioValue && value === "radio" &&
						nodeName(elem, "input")) {
						var val = elem.value;
						elem.setAttribute("type", value);
						if (val) {
							elem.value = val;
						}
						return value;
					}
				}
			}
		},

		removeAttr: function (elem, value) {
			var name,
				i = 0,

				// Attribute names can contain non-HTML whitespace characters
				// https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
				attrNames = value && value.match(rnothtmlwhite);

			if (attrNames && elem.nodeType === 1) {
				while ((name = attrNames[i++])) {
					elem.removeAttribute(name);
				}
			}
		}
	});

	// Hooks for boolean attributes
	boolHook = {
		set: function (elem, value, name) {
			if (value === false) {

				// Remove boolean attributes when set to false
				jQuery.removeAttr(elem, name);
			} else {
				elem.setAttribute(name, name);
			}
			return name;
		}
	};

	jQuery.each(jQuery.expr.match.bool.source.match(/\w+/g), function (_i, name) {
		var getter = attrHandle[name] || jQuery.find.attr;

		attrHandle[name] = function (elem, name, isXML) {
			var ret, handle,
				lowercaseName = name.toLowerCase();

			if (!isXML) {

				// Avoid an infinite loop by temporarily removing this function from the getter
				handle = attrHandle[lowercaseName];
				attrHandle[lowercaseName] = ret;
				ret = getter(elem, name, isXML) != null ?
					lowercaseName :
					null;
				attrHandle[lowercaseName] = handle;
			}
			return ret;
		};
	});




	var rfocusable = /^(?:input|select|textarea|button)$/i,
		rclickable = /^(?:a|area)$/i;

	jQuery.fn.extend({
		prop: function (name, value) {
			return access(this, jQuery.prop, name, value, arguments.length > 1);
		},

		removeProp: function (name) {
			return this.each(function () {
				delete this[jQuery.propFix[name] || name];
			});
		}
	});

	jQuery.extend({
		prop: function (elem, name, value) {
			var ret, hooks,
				nType = elem.nodeType;

			// Don't get/set properties on text, comment and attribute nodes
			if (nType === 3 || nType === 8 || nType === 2) {
				return;
			}

			if (nType !== 1 || !jQuery.isXMLDoc(elem)) {

				// Fix name and attach hooks
				name = jQuery.propFix[name] || name;
				hooks = jQuery.propHooks[name];
			}

			if (value !== undefined) {
				if (hooks && "set" in hooks &&
					(ret = hooks.set(elem, value, name)) !== undefined) {
					return ret;
				}

				return (elem[name] = value);
			}

			if (hooks && "get" in hooks && (ret = hooks.get(elem, name)) !== null) {
				return ret;
			}

			return elem[name];
		},

		propHooks: {
			tabIndex: {
				get: function (elem) {

					// Support: IE <=9 - 11 only
					// elem.tabIndex doesn't always return the
					// correct value when it hasn't been explicitly set
					// Use proper attribute retrieval (trac-12072)
					var tabindex = jQuery.find.attr(elem, "tabindex");

					if (tabindex) {
						return parseInt(tabindex, 10);
					}

					if (
						rfocusable.test(elem.nodeName) ||
						rclickable.test(elem.nodeName) &&
						elem.href
					) {
						return 0;
					}

					return -1;
				}
			}
		},

		propFix: {
			"for": "htmlFor",
			"class": "className"
		}
	});

	// Support: IE <=11 only
	// Accessing the selectedIndex property
	// forces the browser to respect setting selected
	// on the option
	// The getter ensures a default option is selected
	// when in an optgroup
	// eslint rule "no-unused-expressions" is disabled for this code
	// since it considers such accessions noop
	if (!support.optSelected) {
		jQuery.propHooks.selected = {
			get: function (elem) {

				/* eslint no-unused-expressions: "off" */

				var parent = elem.parentNode;
				if (parent && parent.parentNode) {
					parent.parentNode.selectedIndex;
				}
				return null;
			},
			set: function (elem) {

				/* eslint no-unused-expressions: "off" */

				var parent = elem.parentNode;
				if (parent) {
					parent.selectedIndex;

					if (parent.parentNode) {
						parent.parentNode.selectedIndex;
					}
				}
			}
		};
	}

	jQuery.each([
		"tabIndex",
		"readOnly",
		"maxLength",
		"cellSpacing",
		"cellPadding",
		"rowSpan",
		"colSpan",
		"useMap",
		"frameBorder",
		"contentEditable"
	], function () {
		jQuery.propFix[this.toLowerCase()] = this;
	});




	// Strip and collapse whitespace according to HTML spec
	// https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
	function stripAndCollapse(value) {
		var tokens = value.match(rnothtmlwhite) || [];
		return tokens.join(" ");
	}


	function getClass(elem) {
		return elem.getAttribute && elem.getAttribute("class") || "";
	}

	function classesToArray(value) {
		if (Array.isArray(value)) {
			return value;
		}
		if (typeof value === "string") {
			return value.match(rnothtmlwhite) || [];
		}
		return [];
	}

	jQuery.fn.extend({
		addClass: function (value) {
			var classNames, cur, curValue, className, i, finalValue;

			if (isFunction(value)) {
				return this.each(function (j) {
					jQuery(this).addClass(value.call(this, j, getClass(this)));
				});
			}

			classNames = classesToArray(value);

			if (classNames.length) {
				return this.each(function () {
					curValue = getClass(this);
					cur = this.nodeType === 1 && (" " + stripAndCollapse(curValue) + " ");

					if (cur) {
						for (i = 0; i < classNames.length; i++) {
							className = classNames[i];
							if (cur.indexOf(" " + className + " ") < 0) {
								cur += className + " ";
							}
						}

						// Only assign if different to avoid unneeded rendering.
						finalValue = stripAndCollapse(cur);
						if (curValue !== finalValue) {
							this.setAttribute("class", finalValue);
						}
					}
				});
			}

			return this;
		},

		removeClass: function (value) {
			var classNames, cur, curValue, className, i, finalValue;

			if (isFunction(value)) {
				return this.each(function (j) {
					jQuery(this).removeClass(value.call(this, j, getClass(this)));
				});
			}

			if (!arguments.length) {
				return this.attr("class", "");
			}

			classNames = classesToArray(value);

			if (classNames.length) {
				return this.each(function () {
					curValue = getClass(this);

					// This expression is here for better compressibility (see addClass)
					cur = this.nodeType === 1 && (" " + stripAndCollapse(curValue) + " ");

					if (cur) {
						for (i = 0; i < classNames.length; i++) {
							className = classNames[i];

							// Remove *all* instances
							while (cur.indexOf(" " + className + " ") > -1) {
								cur = cur.replace(" " + className + " ", " ");
							}
						}

						// Only assign if different to avoid unneeded rendering.
						finalValue = stripAndCollapse(cur);
						if (curValue !== finalValue) {
							this.setAttribute("class", finalValue);
						}
					}
				});
			}

			return this;
		},

		toggleClass: function (value, stateVal) {
			var classNames, className, i, self,
				type = typeof value,
				isValidValue = type === "string" || Array.isArray(value);

			if (isFunction(value)) {
				return this.each(function (i) {
					jQuery(this).toggleClass(
						value.call(this, i, getClass(this), stateVal),
						stateVal
					);
				});
			}

			if (typeof stateVal === "boolean" && isValidValue) {
				return stateVal ? this.addClass(value) : this.removeClass(value);
			}

			classNames = classesToArray(value);

			return this.each(function () {
				if (isValidValue) {

					// Toggle individual class names
					self = jQuery(this);

					for (i = 0; i < classNames.length; i++) {
						className = classNames[i];

						// Check each className given, space separated list
						if (self.hasClass(className)) {
							self.removeClass(className);
						} else {
							self.addClass(className);
						}
					}

					// Toggle whole class name
				} else if (value === undefined || type === "boolean") {
					className = getClass(this);
					if (className) {

						// Store className if set
						dataPriv.set(this, "__className__", className);
					}

					// If the element has a class name or if we're passed `false`,
					// then remove the whole classname (if there was one, the above saved it).
					// Otherwise bring back whatever was previously saved (if anything),
					// falling back to the empty string if nothing was stored.
					if (this.setAttribute) {
						this.setAttribute("class",
							className || value === false ?
								"" :
								dataPriv.get(this, "__className__") || ""
						);
					}
				}
			});
		},

		hasClass: function (selector) {
			var className, elem,
				i = 0;

			className = " " + selector + " ";
			while ((elem = this[i++])) {
				if (elem.nodeType === 1 &&
					(" " + stripAndCollapse(getClass(elem)) + " ").indexOf(className) > -1) {
					return true;
				}
			}

			return false;
		}
	});




	var rreturn = /\r/g;

	jQuery.fn.extend({
		val: function (value) {
			var hooks, ret, valueIsFunction,
				elem = this[0];

			if (!arguments.length) {
				if (elem) {
					hooks = jQuery.valHooks[elem.type] ||
						jQuery.valHooks[elem.nodeName.toLowerCase()];

					if (hooks &&
						"get" in hooks &&
						(ret = hooks.get(elem, "value")) !== undefined
					) {
						return ret;
					}

					ret = elem.value;

					// Handle most common string cases
					if (typeof ret === "string") {
						return ret.replace(rreturn, "");
					}

					// Handle cases where value is null/undef or number
					return ret == null ? "" : ret;
				}

				return;
			}

			valueIsFunction = isFunction(value);

			return this.each(function (i) {
				var val;

				if (this.nodeType !== 1) {
					return;
				}

				if (valueIsFunction) {
					val = value.call(this, i, jQuery(this).val());
				} else {
					val = value;
				}

				// Treat null/undefined as ""; convert numbers to string
				if (val == null) {
					val = "";

				} else if (typeof val === "number") {
					val += "";

				} else if (Array.isArray(val)) {
					val = jQuery.map(val, function (value) {
						return value == null ? "" : value + "";
					});
				}

				hooks = jQuery.valHooks[this.type] || jQuery.valHooks[this.nodeName.toLowerCase()];

				// If set returns undefined, fall back to normal setting
				if (!hooks || !("set" in hooks) || hooks.set(this, val, "value") === undefined) {
					this.value = val;
				}
			});
		}
	});

	jQuery.extend({
		valHooks: {
			option: {
				get: function (elem) {

					var val = jQuery.find.attr(elem, "value");
					return val != null ?
						val :

						// Support: IE <=10 - 11 only
						// option.text throws exceptions (trac-14686, trac-14858)
						// Strip and collapse whitespace
						// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
						stripAndCollapse(jQuery.text(elem));
				}
			},
			select: {
				get: function (elem) {
					var value, option, i,
						options = elem.options,
						index = elem.selectedIndex,
						one = elem.type === "select-one",
						values = one ? null : [],
						max = one ? index + 1 : options.length;

					if (index < 0) {
						i = max;

					} else {
						i = one ? index : 0;
					}

					// Loop through all the selected options
					for (; i < max; i++) {
						option = options[i];

						// Support: IE <=9 only
						// IE8-9 doesn't update selected after form reset (trac-2551)
						if ((option.selected || i === index) &&

							// Don't return options that are disabled or in a disabled optgroup
							!option.disabled &&
							(!option.parentNode.disabled ||
								!nodeName(option.parentNode, "optgroup"))) {

							// Get the specific value for the option
							value = jQuery(option).val();

							// We don't need an array for one selects
							if (one) {
								return value;
							}

							// Multi-Selects return an array
							values.push(value);
						}
					}

					return values;
				},

				set: function (elem, value) {
					var optionSet, option,
						options = elem.options,
						values = jQuery.makeArray(value),
						i = options.length;

					while (i--) {
						option = options[i];

						/* eslint-disable no-cond-assign */

						if (option.selected =
							jQuery.inArray(jQuery.valHooks.option.get(option), values) > -1
						) {
							optionSet = true;
						}

						/* eslint-enable no-cond-assign */
					}

					// Force browsers to behave consistently when non-matching value is set
					if (!optionSet) {
						elem.selectedIndex = -1;
					}
					return values;
				}
			}
		}
	});

	// Radios and checkboxes getter/setter
	jQuery.each(["radio", "checkbox"], function () {
		jQuery.valHooks[this] = {
			set: function (elem, value) {
				if (Array.isArray(value)) {
					return (elem.checked = jQuery.inArray(jQuery(elem).val(), value) > -1);
				}
			}
		};
		if (!support.checkOn) {
			jQuery.valHooks[this].get = function (elem) {
				return elem.getAttribute("value") === null ? "on" : elem.value;
			};
		}
	});




	// Return jQuery for attributes-only inclusion
	var location = window.location;

	var nonce = { guid: Date.now() };

	var rquery = (/\?/);



	// Cross-browser xml parsing
	jQuery.parseXML = function (data) {
		var xml, parserErrorElem;
		if (!data || typeof data !== "string") {
			return null;
		}

		// Support: IE 9 - 11 only
		// IE throws on parseFromString with invalid input.
		try {
			xml = (new window.DOMParser()).parseFromString(data, "text/xml");
		} catch (e) { }

		parserErrorElem = xml && xml.getElementsByTagName("parsererror")[0];
		if (!xml || parserErrorElem) {
			jQuery.error("Invalid XML: " + (
				parserErrorElem ?
					jQuery.map(parserErrorElem.childNodes, function (el) {
						return el.textContent;
					}).join("\n") :
					data
			));
		}
		return xml;
	};


	var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
		stopPropagationCallback = function (e) {
			e.stopPropagation();
		};

	jQuery.extend(jQuery.event, {

		trigger: function (event, data, elem, onlyHandlers) {

			var i, cur, tmp, bubbleType, ontype, handle, special, lastElement,
				eventPath = [elem || document],
				type = hasOwn.call(event, "type") ? event.type : event,
				namespaces = hasOwn.call(event, "namespace") ? event.namespace.split(".") : [];

			cur = lastElement = tmp = elem = elem || document;

			// Don't do events on text and comment nodes
			if (elem.nodeType === 3 || elem.nodeType === 8) {
				return;
			}

			// focus/blur morphs to focusin/out; ensure we're not firing them right now
			if (rfocusMorph.test(type + jQuery.event.triggered)) {
				return;
			}

			if (type.indexOf(".") > -1) {

				// Namespaced trigger; create a regexp to match event type in handle()
				namespaces = type.split(".");
				type = namespaces.shift();
				namespaces.sort();
			}
			ontype = type.indexOf(":") < 0 && "on" + type;

			// Caller can pass in a jQuery.Event object, Object, or just an event type string
			event = event[jQuery.expando] ?
				event :
				new jQuery.Event(type, typeof event === "object" && event);

			// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
			event.isTrigger = onlyHandlers ? 2 : 3;
			event.namespace = namespaces.join(".");
			event.rnamespace = event.namespace ?
				new RegExp("(^|\\.)" + namespaces.join("\\.(?:.*\\.|)") + "(\\.|$)") :
				null;

			// Clean up the event in case it is being reused
			event.result = undefined;
			if (!event.target) {
				event.target = elem;
			}

			// Clone any incoming data and prepend the event, creating the handler arg list
			data = data == null ?
				[event] :
				jQuery.makeArray(data, [event]);

			// Allow special events to draw outside the lines
			special = jQuery.event.special[type] || {};
			if (!onlyHandlers && special.trigger && special.trigger.apply(elem, data) === false) {
				return;
			}

			// Determine event propagation path in advance, per W3C events spec (trac-9951)
			// Bubble up to document, then to window; watch for a global ownerDocument var (trac-9724)
			if (!onlyHandlers && !special.noBubble && !isWindow(elem)) {

				bubbleType = special.delegateType || type;
				if (!rfocusMorph.test(bubbleType + type)) {
					cur = cur.parentNode;
				}
				for (; cur; cur = cur.parentNode) {
					eventPath.push(cur);
					tmp = cur;
				}

				// Only add window if we got to document (e.g., not plain obj or detached DOM)
				if (tmp === (elem.ownerDocument || document)) {
					eventPath.push(tmp.defaultView || tmp.parentWindow || window);
				}
			}

			// Fire handlers on the event path
			i = 0;
			while ((cur = eventPath[i++]) && !event.isPropagationStopped()) {
				lastElement = cur;
				event.type = i > 1 ?
					bubbleType :
					special.bindType || type;

				// jQuery handler
				handle = (dataPriv.get(cur, "events") || Object.create(null))[event.type] &&
					dataPriv.get(cur, "handle");
				if (handle) {
					handle.apply(cur, data);
				}

				// Native handler
				handle = ontype && cur[ontype];
				if (handle && handle.apply && acceptData(cur)) {
					event.result = handle.apply(cur, data);
					if (event.result === false) {
						event.preventDefault();
					}
				}
			}
			event.type = type;

			// If nobody prevented the default action, do it now
			if (!onlyHandlers && !event.isDefaultPrevented()) {

				if ((!special._default ||
					special._default.apply(eventPath.pop(), data) === false) &&
					acceptData(elem)) {

					// Call a native DOM method on the target with the same name as the event.
					// Don't do default actions on window, that's where global variables be (trac-6170)
					if (ontype && isFunction(elem[type]) && !isWindow(elem)) {

						// Don't re-trigger an onFOO event when we call its FOO() method
						tmp = elem[ontype];

						if (tmp) {
							elem[ontype] = null;
						}

						// Prevent re-triggering of the same event, since we already bubbled it above
						jQuery.event.triggered = type;

						if (event.isPropagationStopped()) {
							lastElement.addEventListener(type, stopPropagationCallback);
						}

						elem[type]();

						if (event.isPropagationStopped()) {
							lastElement.removeEventListener(type, stopPropagationCallback);
						}

						jQuery.event.triggered = undefined;

						if (tmp) {
							elem[ontype] = tmp;
						}
					}
				}
			}

			return event.result;
		},

		// Piggyback on a donor event to simulate a different one
		// Used only for `focus(in | out)` events
		simulate: function (type, elem, event) {
			var e = jQuery.extend(
				new jQuery.Event(),
				event,
				{
					type: type,
					isSimulated: true
				}
			);

			jQuery.event.trigger(e, null, elem);
		}

	});

	jQuery.fn.extend({

		trigger: function (type, data) {
			return this.each(function () {
				jQuery.event.trigger(type, data, this);
			});
		},
		triggerHandler: function (type, data) {
			var elem = this[0];
			if (elem) {
				return jQuery.event.trigger(type, data, elem, true);
			}
		}
	});


	var
		rbracket = /\[\]$/,
		rCRLF = /\r?\n/g,
		rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
		rsubmittable = /^(?:input|select|textarea|keygen)/i;

	function buildParams(prefix, obj, traditional, add) {
		var name;

		if (Array.isArray(obj)) {

			// Serialize array item.
			jQuery.each(obj, function (i, v) {
				if (traditional || rbracket.test(prefix)) {

					// Treat each array item as a scalar.
					add(prefix, v);

				} else {

					// Item is non-scalar (array or object), encode its numeric index.
					buildParams(
						prefix + "[" + (typeof v === "object" && v != null ? i : "") + "]",
						v,
						traditional,
						add
					);
				}
			});

		} else if (!traditional && toType(obj) === "object") {

			// Serialize object item.
			for (name in obj) {
				buildParams(prefix + "[" + name + "]", obj[name], traditional, add);
			}

		} else {

			// Serialize scalar item.
			add(prefix, obj);
		}
	}

	// Serialize an array of form elements or a set of
	// key/values into a query string
	jQuery.param = function (a, traditional) {
		var prefix,
			s = [],
			add = function (key, valueOrFunction) {

				// If value is a function, invoke it and use its return value
				var value = isFunction(valueOrFunction) ?
					valueOrFunction() :
					valueOrFunction;

				s[s.length] = encodeURIComponent(key) + "=" +
					encodeURIComponent(value == null ? "" : value);
			};

		if (a == null) {
			return "";
		}

		// If an array was passed in, assume that it is an array of form elements.
		if (Array.isArray(a) || (a.jquery && !jQuery.isPlainObject(a))) {

			// Serialize the form elements
			jQuery.each(a, function () {
				add(this.name, this.value);
			});

		} else {

			// If traditional, encode the "old" way (the way 1.3.2 or older
			// did it), otherwise encode params recursively.
			for (prefix in a) {
				buildParams(prefix, a[prefix], traditional, add);
			}
		}

		// Return the resulting serialization
		return s.join("&");
	};

	jQuery.fn.extend({
		serialize: function () {
			return jQuery.param(this.serializeArray());
		},
		serializeArray: function () {
			return this.map(function () {

				// Can add propHook for "elements" to filter or add form elements
				var elements = jQuery.prop(this, "elements");
				return elements ? jQuery.makeArray(elements) : this;
			}).filter(function () {
				var type = this.type;

				// Use .is( ":disabled" ) so that fieldset[disabled] works
				return this.name && !jQuery(this).is(":disabled") &&
					rsubmittable.test(this.nodeName) && !rsubmitterTypes.test(type) &&
					(this.checked || !rcheckableType.test(type));
			}).map(function (_i, elem) {
				var val = jQuery(this).val();

				if (val == null) {
					return null;
				}

				if (Array.isArray(val)) {
					return jQuery.map(val, function (val) {
						return { name: elem.name, value: val.replace(rCRLF, "\r\n") };
					});
				}

				return { name: elem.name, value: val.replace(rCRLF, "\r\n") };
			}).get();
		}
	});


	var
		r20 = /%20/g,
		rhash = /#.*$/,
		rantiCache = /([?&])_=[^&]*/,
		rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg,

		// trac-7653, trac-8125, trac-8152: local protocol detection
		rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
		rnoContent = /^(?:GET|HEAD)$/,
		rprotocol = /^\/\//,

		/* Prefilters
		 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
		 * 2) These are called:
		 *    - BEFORE asking for a transport
		 *    - AFTER param serialization (s.data is a string if s.processData is true)
		 * 3) key is the dataType
		 * 4) the catchall symbol "*" can be used
		 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
		 */
		prefilters = {},

		/* Transports bindings
		 * 1) key is the dataType
		 * 2) the catchall symbol "*" can be used
		 * 3) selection will start with transport dataType and THEN go to "*" if needed
		 */
		transports = {},

		// Avoid comment-prolog char sequence (trac-10098); must appease lint and evade compression
		allTypes = "*/".concat("*"),

		// Anchor tag for parsing the document origin
		originAnchor = document.createElement("a");

	originAnchor.href = location.href;

	// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
	function addToPrefiltersOrTransports(structure) {

		// dataTypeExpression is optional and defaults to "*"
		return function (dataTypeExpression, func) {

			if (typeof dataTypeExpression !== "string") {
				func = dataTypeExpression;
				dataTypeExpression = "*";
			}

			var dataType,
				i = 0,
				dataTypes = dataTypeExpression.toLowerCase().match(rnothtmlwhite) || [];

			if (isFunction(func)) {

				// For each dataType in the dataTypeExpression
				while ((dataType = dataTypes[i++])) {

					// Prepend if requested
					if (dataType[0] === "+") {
						dataType = dataType.slice(1) || "*";
						(structure[dataType] = structure[dataType] || []).unshift(func);

						// Otherwise append
					} else {
						(structure[dataType] = structure[dataType] || []).push(func);
					}
				}
			}
		};
	}

	// Base inspection function for prefilters and transports
	function inspectPrefiltersOrTransports(structure, options, originalOptions, jqXHR) {

		var inspected = {},
			seekingTransport = (structure === transports);

		function inspect(dataType) {
			var selected;
			inspected[dataType] = true;
			jQuery.each(structure[dataType] || [], function (_, prefilterOrFactory) {
				var dataTypeOrTransport = prefilterOrFactory(options, originalOptions, jqXHR);
				if (typeof dataTypeOrTransport === "string" &&
					!seekingTransport && !inspected[dataTypeOrTransport]) {

					options.dataTypes.unshift(dataTypeOrTransport);
					inspect(dataTypeOrTransport);
					return false;
				} else if (seekingTransport) {
					return !(selected = dataTypeOrTransport);
				}
			});
			return selected;
		}

		return inspect(options.dataTypes[0]) || !inspected["*"] && inspect("*");
	}

	// A special extend for ajax options
	// that takes "flat" options (not to be deep extended)
	// Fixes trac-9887
	function ajaxExtend(target, src) {
		var key, deep,
			flatOptions = jQuery.ajaxSettings.flatOptions || {};

		for (key in src) {
			if (src[key] !== undefined) {
				(flatOptions[key] ? target : (deep || (deep = {})))[key] = src[key];
			}
		}
		if (deep) {
			jQuery.extend(true, target, deep);
		}

		return target;
	}

	/* Handles responses to an ajax request:
	 * - finds the right dataType (mediates between content-type and expected dataType)
	 * - returns the corresponding response
	 */
	function ajaxHandleResponses(s, jqXHR, responses) {

		var ct, type, finalDataType, firstDataType,
			contents = s.contents,
			dataTypes = s.dataTypes;

		// Remove auto dataType and get content-type in the process
		while (dataTypes[0] === "*") {
			dataTypes.shift();
			if (ct === undefined) {
				ct = s.mimeType || jqXHR.getResponseHeader("Content-Type");
			}
		}

		// Check if we're dealing with a known content-type
		if (ct) {
			for (type in contents) {
				if (contents[type] && contents[type].test(ct)) {
					dataTypes.unshift(type);
					break;
				}
			}
		}

		// Check to see if we have a response for the expected dataType
		if (dataTypes[0] in responses) {
			finalDataType = dataTypes[0];
		} else {

			// Try convertible dataTypes
			for (type in responses) {
				if (!dataTypes[0] || s.converters[type + " " + dataTypes[0]]) {
					finalDataType = type;
					break;
				}
				if (!firstDataType) {
					firstDataType = type;
				}
			}

			// Or just use first one
			finalDataType = finalDataType || firstDataType;
		}

		// If we found a dataType
		// We add the dataType to the list if needed
		// and return the corresponding response
		if (finalDataType) {
			if (finalDataType !== dataTypes[0]) {
				dataTypes.unshift(finalDataType);
			}
			return responses[finalDataType];
		}
	}

	/* Chain conversions given the request and the original response
	 * Also sets the responseXXX fields on the jqXHR instance
	 */
	function ajaxConvert(s, response, jqXHR, isSuccess) {
		var conv2, current, conv, tmp, prev,
			converters = {},

			// Work with a copy of dataTypes in case we need to modify it for conversion
			dataTypes = s.dataTypes.slice();

		// Create converters map with lowercased keys
		if (dataTypes[1]) {
			for (conv in s.converters) {
				converters[conv.toLowerCase()] = s.converters[conv];
			}
		}

		current = dataTypes.shift();

		// Convert to each sequential dataType
		while (current) {

			if (s.responseFields[current]) {
				jqXHR[s.responseFields[current]] = response;
			}

			// Apply the dataFilter if provided
			if (!prev && isSuccess && s.dataFilter) {
				response = s.dataFilter(response, s.dataType);
			}

			prev = current;
			current = dataTypes.shift();

			if (current) {

				// There's only work to do if current dataType is non-auto
				if (current === "*") {

					current = prev;

					// Convert response if prev dataType is non-auto and differs from current
				} else if (prev !== "*" && prev !== current) {

					// Seek a direct converter
					conv = converters[prev + " " + current] || converters["* " + current];

					// If none found, seek a pair
					if (!conv) {
						for (conv2 in converters) {

							// If conv2 outputs current
							tmp = conv2.split(" ");
							if (tmp[1] === current) {

								// If prev can be converted to accepted input
								conv = converters[prev + " " + tmp[0]] ||
									converters["* " + tmp[0]];
								if (conv) {

									// Condense equivalence converters
									if (conv === true) {
										conv = converters[conv2];

										// Otherwise, insert the intermediate dataType
									} else if (converters[conv2] !== true) {
										current = tmp[0];
										dataTypes.unshift(tmp[1]);
									}
									break;
								}
							}
						}
					}

					// Apply converter (if not an equivalence)
					if (conv !== true) {

						// Unless errors are allowed to bubble, catch and return them
						if (conv && s.throws) {
							response = conv(response);
						} else {
							try {
								response = conv(response);
							} catch (e) {
								return {
									state: "parsererror",
									error: conv ? e : "No conversion from " + prev + " to " + current
								};
							}
						}
					}
				}
			}
		}

		return { state: "success", data: response };
	}

	jQuery.extend({

		// Counter for holding the number of active queries
		active: 0,

		// Last-Modified header cache for next request
		lastModified: {},
		etag: {},

		ajaxSettings: {
			url: location.href,
			type: "GET",
			isLocal: rlocalProtocol.test(location.protocol),
			global: true,
			processData: true,
			async: true,
			contentType: "application/x-www-form-urlencoded; charset=UTF-8",

			/*
			timeout: 0,
			data: null,
			dataType: null,
			username: null,
			password: null,
			cache: null,
			throws: false,
			traditional: false,
			headers: {},
			*/

			accepts: {
				"*": allTypes,
				text: "text/plain",
				html: "text/html",
				xml: "application/xml, text/xml",
				json: "application/json, text/javascript"
			},

			contents: {
				xml: /\bxml\b/,
				html: /\bhtml/,
				json: /\bjson\b/
			},

			responseFields: {
				xml: "responseXML",
				text: "responseText",
				json: "responseJSON"
			},

			// Data converters
			// Keys separate source (or catchall "*") and destination types with a single space
			converters: {

				// Convert anything to text
				"* text": String,

				// Text to html (true = no transformation)
				"text html": true,

				// Evaluate text as a json expression
				"text json": JSON.parse,

				// Parse text as xml
				"text xml": jQuery.parseXML
			},

			// For options that shouldn't be deep extended:
			// you can add your own custom options here if
			// and when you create one that shouldn't be
			// deep extended (see ajaxExtend)
			flatOptions: {
				url: true,
				context: true
			}
		},

		// Creates a full fledged settings object into target
		// with both ajaxSettings and settings fields.
		// If target is omitted, writes into ajaxSettings.
		ajaxSetup: function (target, settings) {
			return settings ?

				// Building a settings object
				ajaxExtend(ajaxExtend(target, jQuery.ajaxSettings), settings) :

				// Extending ajaxSettings
				ajaxExtend(jQuery.ajaxSettings, target);
		},

		ajaxPrefilter: addToPrefiltersOrTransports(prefilters),
		ajaxTransport: addToPrefiltersOrTransports(transports),

		// Main method
		ajax: function (url, options) {

			// If url is an object, simulate pre-1.5 signature
			if (typeof url === "object") {
				options = url;
				url = undefined;
			}

			// Force options to be an object
			options = options || {};

			var transport,

				// URL without anti-cache param
				cacheURL,

				// Response headers
				responseHeadersString,
				responseHeaders,

				// timeout handle
				timeoutTimer,

				// Url cleanup var
				urlAnchor,

				// Request state (becomes false upon send and true upon completion)
				completed,

				// To know if global events are to be dispatched
				fireGlobals,

				// Loop variable
				i,

				// uncached part of the url
				uncached,

				// Create the final options object
				s = jQuery.ajaxSetup({}, options),

				// Callbacks context
				callbackContext = s.context || s,

				// Context for global events is callbackContext if it is a DOM node or jQuery collection
				globalEventContext = s.context &&
					(callbackContext.nodeType || callbackContext.jquery) ?
					jQuery(callbackContext) :
					jQuery.event,

				// Deferreds
				deferred = jQuery.Deferred(),
				completeDeferred = jQuery.Callbacks("once memory"),

				// Status-dependent callbacks
				statusCode = s.statusCode || {},

				// Headers (they are sent all at once)
				requestHeaders = {},
				requestHeadersNames = {},

				// Default abort message
				strAbort = "canceled",

				// Fake xhr
				jqXHR = {
					readyState: 0,

					// Builds headers hashtable if needed
					getResponseHeader: function (key) {
						var match;
						if (completed) {
							if (!responseHeaders) {
								responseHeaders = {};
								while ((match = rheaders.exec(responseHeadersString))) {
									responseHeaders[match[1].toLowerCase() + " "] =
										(responseHeaders[match[1].toLowerCase() + " "] || [])
											.concat(match[2]);
								}
							}
							match = responseHeaders[key.toLowerCase() + " "];
						}
						return match == null ? null : match.join(", ");
					},

					// Raw string
					getAllResponseHeaders: function () {
						return completed ? responseHeadersString : null;
					},

					// Caches the header
					setRequestHeader: function (name, value) {
						if (completed == null) {
							name = requestHeadersNames[name.toLowerCase()] =
								requestHeadersNames[name.toLowerCase()] || name;
							requestHeaders[name] = value;
						}
						return this;
					},

					// Overrides response content-type header
					overrideMimeType: function (type) {
						if (completed == null) {
							s.mimeType = type;
						}
						return this;
					},

					// Status-dependent callbacks
					statusCode: function (map) {
						var code;
						if (map) {
							if (completed) {

								// Execute the appropriate callbacks
								jqXHR.always(map[jqXHR.status]);
							} else {

								// Lazy-add the new callbacks in a way that preserves old ones
								for (code in map) {
									statusCode[code] = [statusCode[code], map[code]];
								}
							}
						}
						return this;
					},

					// Cancel the request
					abort: function (statusText) {
						var finalText = statusText || strAbort;
						if (transport) {
							transport.abort(finalText);
						}
						done(0, finalText);
						return this;
					}
				};

			// Attach deferreds
			deferred.promise(jqXHR);

			// Add protocol if not provided (prefilters might expect it)
			// Handle falsy url in the settings object (trac-10093: consistency with old signature)
			// We also use the url parameter if available
			s.url = ((url || s.url || location.href) + "")
				.replace(rprotocol, location.protocol + "//");

			// Alias method option to type as per ticket trac-12004
			s.type = options.method || options.type || s.method || s.type;

			// Extract dataTypes list
			s.dataTypes = (s.dataType || "*").toLowerCase().match(rnothtmlwhite) || [""];

			// A cross-domain request is in order when the origin doesn't match the current origin.
			if (s.crossDomain == null) {
				urlAnchor = document.createElement("a");

				// Support: IE <=8 - 11, Edge 12 - 15
				// IE throws exception on accessing the href property if url is malformed,
				// e.g. http://example.com:80x/
				try {
					urlAnchor.href = s.url;

					// Support: IE <=8 - 11 only
					// Anchor's host property isn't correctly set when s.url is relative
					urlAnchor.href = urlAnchor.href;
					s.crossDomain = originAnchor.protocol + "//" + originAnchor.host !==
						urlAnchor.protocol + "//" + urlAnchor.host;
				} catch (e) {

					// If there is an error parsing the URL, assume it is crossDomain,
					// it can be rejected by the transport if it is invalid
					s.crossDomain = true;
				}
			}

			// Convert data if not already a string
			if (s.data && s.processData && typeof s.data !== "string") {
				s.data = jQuery.param(s.data, s.traditional);
			}

			// Apply prefilters
			inspectPrefiltersOrTransports(prefilters, s, options, jqXHR);

			// If request was aborted inside a prefilter, stop there
			if (completed) {
				return jqXHR;
			}

			// We can fire global events as of now if asked to
			// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (trac-15118)
			fireGlobals = jQuery.event && s.global;

			// Watch for a new set of requests
			if (fireGlobals && jQuery.active++ === 0) {
				jQuery.event.trigger("ajaxStart");
			}

			// Uppercase the type
			s.type = s.type.toUpperCase();

			// Determine if request has content
			s.hasContent = !rnoContent.test(s.type);

			// Save the URL in case we're toying with the If-Modified-Since
			// and/or If-None-Match header later on
			// Remove hash to simplify url manipulation
			cacheURL = s.url.replace(rhash, "");

			// More options handling for requests with no content
			if (!s.hasContent) {

				// Remember the hash so we can put it back
				uncached = s.url.slice(cacheURL.length);

				// If data is available and should be processed, append data to url
				if (s.data && (s.processData || typeof s.data === "string")) {
					cacheURL += (rquery.test(cacheURL) ? "&" : "?") + s.data;

					// trac-9682: remove data so that it's not used in an eventual retry
					delete s.data;
				}

				// Add or update anti-cache param if needed
				if (s.cache === false) {
					cacheURL = cacheURL.replace(rantiCache, "$1");
					uncached = (rquery.test(cacheURL) ? "&" : "?") + "_=" + (nonce.guid++) +
						uncached;
				}

				// Put hash and anti-cache on the URL that will be requested (gh-1732)
				s.url = cacheURL + uncached;

				// Change '%20' to '+' if this is encoded form body content (gh-2658)
			} else if (s.data && s.processData &&
				(s.contentType || "").indexOf("application/x-www-form-urlencoded") === 0) {
				s.data = s.data.replace(r20, "+");
			}

			// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
			if (s.ifModified) {
				if (jQuery.lastModified[cacheURL]) {
					jqXHR.setRequestHeader("If-Modified-Since", jQuery.lastModified[cacheURL]);
				}
				if (jQuery.etag[cacheURL]) {
					jqXHR.setRequestHeader("If-None-Match", jQuery.etag[cacheURL]);
				}
			}

			// Set the correct header, if data is being sent
			if (s.data && s.hasContent && s.contentType !== false || options.contentType) {
				jqXHR.setRequestHeader("Content-Type", s.contentType);
			}

			// Set the Accepts header for the server, depending on the dataType
			jqXHR.setRequestHeader(
				"Accept",
				s.dataTypes[0] && s.accepts[s.dataTypes[0]] ?
					s.accepts[s.dataTypes[0]] +
					(s.dataTypes[0] !== "*" ? ", " + allTypes + "; q=0.01" : "") :
					s.accepts["*"]
			);

			// Check for headers option
			for (i in s.headers) {
				jqXHR.setRequestHeader(i, s.headers[i]);
			}

			// Allow custom headers/mimetypes and early abort
			if (s.beforeSend &&
				(s.beforeSend.call(callbackContext, jqXHR, s) === false || completed)) {

				// Abort if not done already and return
				return jqXHR.abort();
			}

			// Aborting is no longer a cancellation
			strAbort = "abort";

			// Install callbacks on deferreds
			completeDeferred.add(s.complete);
			jqXHR.done(s.success);
			jqXHR.fail(s.error);

			// Get transport
			transport = inspectPrefiltersOrTransports(transports, s, options, jqXHR);

			// If no transport, we auto-abort
			if (!transport) {
				done(-1, "No Transport");
			} else {
				jqXHR.readyState = 1;

				// Send global event
				if (fireGlobals) {
					globalEventContext.trigger("ajaxSend", [jqXHR, s]);
				}

				// If request was aborted inside ajaxSend, stop there
				if (completed) {
					return jqXHR;
				}

				// Timeout
				if (s.async && s.timeout > 0) {
					timeoutTimer = window.setTimeout(function () {
						jqXHR.abort("timeout");
					}, s.timeout);
				}

				try {
					completed = false;
					transport.send(requestHeaders, done);
				} catch (e) {

					// Rethrow post-completion exceptions
					if (completed) {
						throw e;
					}

					// Propagate others as results
					done(-1, e);
				}
			}

			// Callback for when everything is done
			function done(status, nativeStatusText, responses, headers) {
				var isSuccess, success, error, response, modified,
					statusText = nativeStatusText;

				// Ignore repeat invocations
				if (completed) {
					return;
				}

				completed = true;

				// Clear timeout if it exists
				if (timeoutTimer) {
					window.clearTimeout(timeoutTimer);
				}

				// Dereference transport for early garbage collection
				// (no matter how long the jqXHR object will be used)
				transport = undefined;

				// Cache response headers
				responseHeadersString = headers || "";

				// Set readyState
				jqXHR.readyState = status > 0 ? 4 : 0;

				// Determine if successful
				isSuccess = status >= 200 && status < 300 || status === 304;

				// Get response data
				if (responses) {
					response = ajaxHandleResponses(s, jqXHR, responses);
				}

				// Use a noop converter for missing script but not if jsonp
				if (!isSuccess &&
					jQuery.inArray("script", s.dataTypes) > -1 &&
					jQuery.inArray("json", s.dataTypes) < 0) {
					s.converters["text script"] = function () { };
				}

				// Convert no matter what (that way responseXXX fields are always set)
				response = ajaxConvert(s, response, jqXHR, isSuccess);

				// If successful, handle type chaining
				if (isSuccess) {

					// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
					if (s.ifModified) {
						modified = jqXHR.getResponseHeader("Last-Modified");
						if (modified) {
							jQuery.lastModified[cacheURL] = modified;
						}
						modified = jqXHR.getResponseHeader("etag");
						if (modified) {
							jQuery.etag[cacheURL] = modified;
						}
					}

					// if no content
					if (status === 204 || s.type === "HEAD") {
						statusText = "nocontent";

						// if not modified
					} else if (status === 304) {
						statusText = "notmodified";

						// If we have data, let's convert it
					} else {
						statusText = response.state;
						success = response.data;
						error = response.error;
						isSuccess = !error;
					}
				} else {

					// Extract error from statusText and normalize for non-aborts
					error = statusText;
					if (status || !statusText) {
						statusText = "error";
						if (status < 0) {
							status = 0;
						}
					}
				}

				// Set data for the fake xhr object
				jqXHR.status = status;
				jqXHR.statusText = (nativeStatusText || statusText) + "";

				// Success/Error
				if (isSuccess) {
					deferred.resolveWith(callbackContext, [success, statusText, jqXHR]);
				} else {
					deferred.rejectWith(callbackContext, [jqXHR, statusText, error]);
				}

				// Status-dependent callbacks
				jqXHR.statusCode(statusCode);
				statusCode = undefined;

				if (fireGlobals) {
					globalEventContext.trigger(isSuccess ? "ajaxSuccess" : "ajaxError",
						[jqXHR, s, isSuccess ? success : error]);
				}

				// Complete
				completeDeferred.fireWith(callbackContext, [jqXHR, statusText]);

				if (fireGlobals) {
					globalEventContext.trigger("ajaxComplete", [jqXHR, s]);

					// Handle the global AJAX counter
					if (!(--jQuery.active)) {
						jQuery.event.trigger("ajaxStop");
					}
				}
			}

			return jqXHR;
		},

		getJSON: function (url, data, callback) {
			return jQuery.get(url, data, callback, "json");
		},

		getScript: function (url, callback) {
			return jQuery.get(url, undefined, callback, "script");
		}
	});

	jQuery.each(["get", "post"], function (_i, method) {
		jQuery[method] = function (url, data, callback, type) {

			// Shift arguments if data argument was omitted
			if (isFunction(data)) {
				type = type || callback;
				callback = data;
				data = undefined;
			}

			// The url can be an options object (which then must have .url)
			return jQuery.ajax(jQuery.extend({
				url: url,
				type: method,
				dataType: type,
				data: data,
				success: callback
			}, jQuery.isPlainObject(url) && url));
		};
	});

	jQuery.ajaxPrefilter(function (s) {
		var i;
		for (i in s.headers) {
			if (i.toLowerCase() === "content-type") {
				s.contentType = s.headers[i] || "";
			}
		}
	});


	jQuery._evalUrl = function (url, options, doc) {
		return jQuery.ajax({
			url: url,

			// Make this explicit, since user can override this through ajaxSetup (trac-11264)
			type: "GET",
			dataType: "script",
			cache: true,
			async: false,
			global: false,

			// Only evaluate the response if it is successful (gh-4126)
			// dataFilter is not invoked for failure responses, so using it instead
			// of the default converter is kludgy but it works.
			converters: {
				"text script": function () { }
			},
			dataFilter: function (response) {
				jQuery.globalEval(response, options, doc);
			}
		});
	};


	jQuery.fn.extend({
		wrapAll: function (html) {
			var wrap;

			if (this[0]) {
				if (isFunction(html)) {
					html = html.call(this[0]);
				}

				// The elements to wrap the target around
				wrap = jQuery(html, this[0].ownerDocument).eq(0).clone(true);

				if (this[0].parentNode) {
					wrap.insertBefore(this[0]);
				}

				wrap.map(function () {
					var elem = this;

					while (elem.firstElementChild) {
						elem = elem.firstElementChild;
					}

					return elem;
				}).append(this);
			}

			return this;
		},

		wrapInner: function (html) {
			if (isFunction(html)) {
				return this.each(function (i) {
					jQuery(this).wrapInner(html.call(this, i));
				});
			}

			return this.each(function () {
				var self = jQuery(this),
					contents = self.contents();

				if (contents.length) {
					contents.wrapAll(html);

				} else {
					self.append(html);
				}
			});
		},

		wrap: function (html) {
			var htmlIsFunction = isFunction(html);

			return this.each(function (i) {
				jQuery(this).wrapAll(htmlIsFunction ? html.call(this, i) : html);
			});
		},

		unwrap: function (selector) {
			this.parent(selector).not("body").each(function () {
				jQuery(this).replaceWith(this.childNodes);
			});
			return this;
		}
	});


	jQuery.expr.pseudos.hidden = function (elem) {
		return !jQuery.expr.pseudos.visible(elem);
	};
	jQuery.expr.pseudos.visible = function (elem) {
		return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
	};




	jQuery.ajaxSettings.xhr = function () {
		try {
			return new window.XMLHttpRequest();
		} catch (e) { }
	};

	var xhrSuccessStatus = {

		// File protocol always yields status code 0, assume 200
		0: 200,

		// Support: IE <=9 only
		// trac-1450: sometimes IE returns 1223 when it should be 204
		1223: 204
	},
		xhrSupported = jQuery.ajaxSettings.xhr();

	support.cors = !!xhrSupported && ("withCredentials" in xhrSupported);
	support.ajax = xhrSupported = !!xhrSupported;

	jQuery.ajaxTransport(function (options) {
		var callback, errorCallback;

		// Cross domain only allowed if supported through XMLHttpRequest
		if (support.cors || xhrSupported && !options.crossDomain) {
			return {
				send: function (headers, complete) {
					var i,
						xhr = options.xhr();

					xhr.open(
						options.type,
						options.url,
						options.async,
						options.username,
						options.password
					);

					// Apply custom fields if provided
					if (options.xhrFields) {
						for (i in options.xhrFields) {
							xhr[i] = options.xhrFields[i];
						}
					}

					// Override mime type if needed
					if (options.mimeType && xhr.overrideMimeType) {
						xhr.overrideMimeType(options.mimeType);
					}

					// X-Requested-With header
					// For cross-domain requests, seeing as conditions for a preflight are
					// akin to a jigsaw puzzle, we simply never set it to be sure.
					// (it can always be set on a per-request basis or even using ajaxSetup)
					// For same-domain requests, won't change header if already provided.
					if (!options.crossDomain && !headers["X-Requested-With"]) {
						headers["X-Requested-With"] = "XMLHttpRequest";
					}

					// Set headers
					for (i in headers) {
						xhr.setRequestHeader(i, headers[i]);
					}

					// Callback
					callback = function (type) {
						return function () {
							if (callback) {
								callback = errorCallback = xhr.onload =
									xhr.onerror = xhr.onabort = xhr.ontimeout =
									xhr.onreadystatechange = null;

								if (type === "abort") {
									xhr.abort();
								} else if (type === "error") {

									// Support: IE <=9 only
									// On a manual native abort, IE9 throws
									// errors on any property access that is not readyState
									if (typeof xhr.status !== "number") {
										complete(0, "error");
									} else {
										complete(

											// File: protocol always yields status 0; see trac-8605, trac-14207
											xhr.status,
											xhr.statusText
										);
									}
								} else {
									complete(
										xhrSuccessStatus[xhr.status] || xhr.status,
										xhr.statusText,

										// Support: IE <=9 only
										// IE9 has no XHR2 but throws on binary (trac-11426)
										// For XHR2 non-text, let the caller handle it (gh-2498)
										(xhr.responseType || "text") !== "text" ||
											typeof xhr.responseText !== "string" ?
											{ binary: xhr.response } :
											{ text: xhr.responseText },
										xhr.getAllResponseHeaders()
									);
								}
							}
						};
					};

					// Listen to events
					xhr.onload = callback();
					errorCallback = xhr.onerror = xhr.ontimeout = callback("error");

					// Support: IE 9 only
					// Use onreadystatechange to replace onabort
					// to handle uncaught aborts
					if (xhr.onabort !== undefined) {
						xhr.onabort = errorCallback;
					} else {
						xhr.onreadystatechange = function () {

							// Check readyState before timeout as it changes
							if (xhr.readyState === 4) {

								// Allow onerror to be called first,
								// but that will not handle a native abort
								// Also, save errorCallback to a variable
								// as xhr.onerror cannot be accessed
								window.setTimeout(function () {
									if (callback) {
										errorCallback();
									}
								});
							}
						};
					}

					// Create the abort callback
					callback = callback("abort");

					try {

						// Do send the request (this may raise an exception)
						xhr.send(options.hasContent && options.data || null);
					} catch (e) {

						// trac-14683: Only rethrow if this hasn't been notified as an error yet
						if (callback) {
							throw e;
						}
					}
				},

				abort: function () {
					if (callback) {
						callback();
					}
				}
			};
		}
	});




	// Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
	jQuery.ajaxPrefilter(function (s) {
		if (s.crossDomain) {
			s.contents.script = false;
		}
	});

	// Install script dataType
	jQuery.ajaxSetup({
		accepts: {
			script: "text/javascript, application/javascript, " +
				"application/ecmascript, application/x-ecmascript"
		},
		contents: {
			script: /\b(?:java|ecma)script\b/
		},
		converters: {
			"text script": function (text) {
				jQuery.globalEval(text);
				return text;
			}
		}
	});

	// Handle cache's special case and crossDomain
	jQuery.ajaxPrefilter("script", function (s) {
		if (s.cache === undefined) {
			s.cache = false;
		}
		if (s.crossDomain) {
			s.type = "GET";
		}
	});

	// Bind script tag hack transport
	jQuery.ajaxTransport("script", function (s) {

		// This transport only deals with cross domain or forced-by-attrs requests
		if (s.crossDomain || s.scriptAttrs) {
			var script, callback;
			return {
				send: function (_, complete) {
					script = jQuery("<script>")
						.attr(s.scriptAttrs || {})
						.prop({ charset: s.scriptCharset, src: s.url })
						.on("load error", callback = function (evt) {
							script.remove();
							callback = null;
							if (evt) {
								complete(evt.type === "error" ? 404 : 200, evt.type);
							}
						});

					// Use native DOM manipulation to avoid our domManip AJAX trickery
					document.head.appendChild(script[0]);
				},
				abort: function () {
					if (callback) {
						callback();
					}
				}
			};
		}
	});




	var oldCallbacks = [],
		rjsonp = /(=)\?(?=&|$)|\?\?/;

	// Default jsonp settings
	jQuery.ajaxSetup({
		jsonp: "callback",
		jsonpCallback: function () {
			var callback = oldCallbacks.pop() || (jQuery.expando + "_" + (nonce.guid++));
			this[callback] = true;
			return callback;
		}
	});

	// Detect, normalize options and install callbacks for jsonp requests
	jQuery.ajaxPrefilter("json jsonp", function (s, originalSettings, jqXHR) {

		var callbackName, overwritten, responseContainer,
			jsonProp = s.jsonp !== false && (rjsonp.test(s.url) ?
				"url" :
				typeof s.data === "string" &&
				(s.contentType || "")
					.indexOf("application/x-www-form-urlencoded") === 0 &&
				rjsonp.test(s.data) && "data"
			);

		// Handle iff the expected data type is "jsonp" or we have a parameter to set
		if (jsonProp || s.dataTypes[0] === "jsonp") {

			// Get callback name, remembering preexisting value associated with it
			callbackName = s.jsonpCallback = isFunction(s.jsonpCallback) ?
				s.jsonpCallback() :
				s.jsonpCallback;

			// Insert callback into url or form data
			if (jsonProp) {
				s[jsonProp] = s[jsonProp].replace(rjsonp, "$1" + callbackName);
			} else if (s.jsonp !== false) {
				s.url += (rquery.test(s.url) ? "&" : "?") + s.jsonp + "=" + callbackName;
			}

			// Use data converter to retrieve json after script execution
			s.converters["script json"] = function () {
				if (!responseContainer) {
					jQuery.error(callbackName + " was not called");
				}
				return responseContainer[0];
			};

			// Force json dataType
			s.dataTypes[0] = "json";

			// Install callback
			overwritten = window[callbackName];
			window[callbackName] = function () {
				responseContainer = arguments;
			};

			// Clean-up function (fires after converters)
			jqXHR.always(function () {

				// If previous value didn't exist - remove it
				if (overwritten === undefined) {
					jQuery(window).removeProp(callbackName);

					// Otherwise restore preexisting value
				} else {
					window[callbackName] = overwritten;
				}

				// Save back as free
				if (s[callbackName]) {

					// Make sure that re-using the options doesn't screw things around
					s.jsonpCallback = originalSettings.jsonpCallback;

					// Save the callback name for future use
					oldCallbacks.push(callbackName);
				}

				// Call if it was a function and we have a response
				if (responseContainer && isFunction(overwritten)) {
					overwritten(responseContainer[0]);
				}

				responseContainer = overwritten = undefined;
			});

			// Delegate to script
			return "script";
		}
	});




	// Support: Safari 8 only
	// In Safari 8 documents created via document.implementation.createHTMLDocument
	// collapse sibling forms: the second one becomes a child of the first one.
	// Because of that, this security measure has to be disabled in Safari 8.
	// https://bugs.webkit.org/show_bug.cgi?id=137337
	support.createHTMLDocument = (function () {
		var body = document.implementation.createHTMLDocument("").body;
		body.innerHTML = "<form></form><form></form>";
		return body.childNodes.length === 2;
	})();


	// Argument "data" should be string of html
	// context (optional): If specified, the fragment will be created in this context,
	// defaults to document
	// keepScripts (optional): If true, will include scripts passed in the html string
	jQuery.parseHTML = function (data, context, keepScripts) {
		if (typeof data !== "string") {
			return [];
		}
		if (typeof context === "boolean") {
			keepScripts = context;
			context = false;
		}

		var base, parsed, scripts;

		if (!context) {

			// Stop scripts or inline event handlers from being executed immediately
			// by using document.implementation
			if (support.createHTMLDocument) {
				context = document.implementation.createHTMLDocument("");

				// Set the base href for the created document
				// so any parsed elements with URLs
				// are based on the document's URL (gh-2965)
				base = context.createElement("base");
				base.href = document.location.href;
				context.head.appendChild(base);
			} else {
				context = document;
			}
		}

		parsed = rsingleTag.exec(data);
		scripts = !keepScripts && [];

		// Single tag
		if (parsed) {
			return [context.createElement(parsed[1])];
		}

		parsed = buildFragment([data], context, scripts);

		if (scripts && scripts.length) {
			jQuery(scripts).remove();
		}

		return jQuery.merge([], parsed.childNodes);
	};


	/**
	 * Load a url into a page
	 */
	jQuery.fn.load = function (url, params, callback) {
		var selector, type, response,
			self = this,
			off = url.indexOf(" ");

		if (off > -1) {
			selector = stripAndCollapse(url.slice(off));
			url = url.slice(0, off);
		}

		// If it's a function
		if (isFunction(params)) {

			// We assume that it's the callback
			callback = params;
			params = undefined;

			// Otherwise, build a param string
		} else if (params && typeof params === "object") {
			type = "POST";
		}

		// If we have elements to modify, make the request
		if (self.length > 0) {
			jQuery.ajax({
				url: url,

				// If "type" variable is undefined, then "GET" method will be used.
				// Make value of this field explicit since
				// user can override it through ajaxSetup method
				type: type || "GET",
				dataType: "html",
				data: params
			}).done(function (responseText) {

				// Save response for use in complete callback
				response = arguments;

				self.html(selector ?

					// If a selector was specified, locate the right elements in a dummy div
					// Exclude scripts to avoid IE 'Permission Denied' errors
					jQuery("<div>").append(jQuery.parseHTML(responseText)).find(selector) :

					// Otherwise use the full result
					responseText);

				// If the request succeeds, this function gets "data", "status", "jqXHR"
				// but they are ignored because response was set above.
				// If it fails, this function gets "jqXHR", "status", "error"
			}).always(callback && function (jqXHR, status) {
				self.each(function () {
					callback.apply(this, response || [jqXHR.responseText, status, jqXHR]);
				});
			});
		}

		return this;
	};




	jQuery.expr.pseudos.animated = function (elem) {
		return jQuery.grep(jQuery.timers, function (fn) {
			return elem === fn.elem;
		}).length;
	};




	jQuery.offset = {
		setOffset: function (elem, options, i) {
			var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
				position = jQuery.css(elem, "position"),
				curElem = jQuery(elem),
				props = {};

			// Set position first, in-case top/left are set even on static elem
			if (position === "static") {
				elem.style.position = "relative";
			}

			curOffset = curElem.offset();
			curCSSTop = jQuery.css(elem, "top");
			curCSSLeft = jQuery.css(elem, "left");
			calculatePosition = (position === "absolute" || position === "fixed") &&
				(curCSSTop + curCSSLeft).indexOf("auto") > -1;

			// Need to be able to calculate position if either
			// top or left is auto and position is either absolute or fixed
			if (calculatePosition) {
				curPosition = curElem.position();
				curTop = curPosition.top;
				curLeft = curPosition.left;

			} else {
				curTop = parseFloat(curCSSTop) || 0;
				curLeft = parseFloat(curCSSLeft) || 0;
			}

			if (isFunction(options)) {

				// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
				options = options.call(elem, i, jQuery.extend({}, curOffset));
			}

			if (options.top != null) {
				props.top = (options.top - curOffset.top) + curTop;
			}
			if (options.left != null) {
				props.left = (options.left - curOffset.left) + curLeft;
			}

			if ("using" in options) {
				options.using.call(elem, props);

			} else {
				curElem.css(props);
			}
		}
	};

	jQuery.fn.extend({

		// offset() relates an element's border box to the document origin
		offset: function (options) {

			// Preserve chaining for setter
			if (arguments.length) {
				return options === undefined ?
					this :
					this.each(function (i) {
						jQuery.offset.setOffset(this, options, i);
					});
			}

			var rect, win,
				elem = this[0];

			if (!elem) {
				return;
			}

			// Return zeros for disconnected and hidden (display: none) elements (gh-2310)
			// Support: IE <=11 only
			// Running getBoundingClientRect on a
			// disconnected node in IE throws an error
			if (!elem.getClientRects().length) {
				return { top: 0, left: 0 };
			}

			// Get document-relative position by adding viewport scroll to viewport-relative gBCR
			rect = elem.getBoundingClientRect();
			win = elem.ownerDocument.defaultView;
			return {
				top: rect.top + win.pageYOffset,
				left: rect.left + win.pageXOffset
			};
		},

		// position() relates an element's margin box to its offset parent's padding box
		// This corresponds to the behavior of CSS absolute positioning
		position: function () {
			if (!this[0]) {
				return;
			}

			var offsetParent, offset, doc,
				elem = this[0],
				parentOffset = { top: 0, left: 0 };

			// position:fixed elements are offset from the viewport, which itself always has zero offset
			if (jQuery.css(elem, "position") === "fixed") {

				// Assume position:fixed implies availability of getBoundingClientRect
				offset = elem.getBoundingClientRect();

			} else {
				offset = this.offset();

				// Account for the *real* offset parent, which can be the document or its root element
				// when a statically positioned element is identified
				doc = elem.ownerDocument;
				offsetParent = elem.offsetParent || doc.documentElement;
				while (offsetParent &&
					(offsetParent === doc.body || offsetParent === doc.documentElement) &&
					jQuery.css(offsetParent, "position") === "static") {

					offsetParent = offsetParent.parentNode;
				}
				if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {

					// Incorporate borders into its offset, since they are outside its content origin
					parentOffset = jQuery(offsetParent).offset();
					parentOffset.top += jQuery.css(offsetParent, "borderTopWidth", true);
					parentOffset.left += jQuery.css(offsetParent, "borderLeftWidth", true);
				}
			}

			// Subtract parent offsets and element margins
			return {
				top: offset.top - parentOffset.top - jQuery.css(elem, "marginTop", true),
				left: offset.left - parentOffset.left - jQuery.css(elem, "marginLeft", true)
			};
		},

		// This method will return documentElement in the following cases:
		// 1) For the element inside the iframe without offsetParent, this method will return
		//    documentElement of the parent window
		// 2) For the hidden or detached element
		// 3) For body or html element, i.e. in case of the html node - it will return itself
		//
		// but those exceptions were never presented as a real life use-cases
		// and might be considered as more preferable results.
		//
		// This logic, however, is not guaranteed and can change at any point in the future
		offsetParent: function () {
			return this.map(function () {
				var offsetParent = this.offsetParent;

				while (offsetParent && jQuery.css(offsetParent, "position") === "static") {
					offsetParent = offsetParent.offsetParent;
				}

				return offsetParent || documentElement;
			});
		}
	});

	// Create scrollLeft and scrollTop methods
	jQuery.each({ scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function (method, prop) {
		var top = "pageYOffset" === prop;

		jQuery.fn[method] = function (val) {
			return access(this, function (elem, method, val) {

				// Coalesce documents and windows
				var win;
				if (isWindow(elem)) {
					win = elem;
				} else if (elem.nodeType === 9) {
					win = elem.defaultView;
				}

				if (val === undefined) {
					return win ? win[prop] : elem[method];
				}

				if (win) {
					win.scrollTo(
						!top ? val : win.pageXOffset,
						top ? val : win.pageYOffset
					);

				} else {
					elem[method] = val;
				}
			}, method, val, arguments.length);
		};
	});

	// Support: Safari <=7 - 9.1, Chrome <=37 - 49
	// Add the top/left cssHooks using jQuery.fn.position
	// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
	// Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
	// getComputedStyle returns percent when specified for top/left/bottom/right;
	// rather than make the css module depend on the offset module, just check for it here
	jQuery.each(["top", "left"], function (_i, prop) {
		jQuery.cssHooks[prop] = addGetHookIf(support.pixelPosition,
			function (elem, computed) {
				if (computed) {
					computed = curCSS(elem, prop);

					// If curCSS returns percentage, fallback to offset
					return rnumnonpx.test(computed) ?
						jQuery(elem).position()[prop] + "px" :
						computed;
				}
			}
		);
	});


	// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
	jQuery.each({ Height: "height", Width: "width" }, function (name, type) {
		jQuery.each({
			padding: "inner" + name,
			content: type,
			"": "outer" + name
		}, function (defaultExtra, funcName) {

			// Margin is only for outerHeight, outerWidth
			jQuery.fn[funcName] = function (margin, value) {
				var chainable = arguments.length && (defaultExtra || typeof margin !== "boolean"),
					extra = defaultExtra || (margin === true || value === true ? "margin" : "border");

				return access(this, function (elem, type, value) {
					var doc;

					if (isWindow(elem)) {

						// $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
						return funcName.indexOf("outer") === 0 ?
							elem["inner" + name] :
							elem.document.documentElement["client" + name];
					}

					// Get document width or height
					if (elem.nodeType === 9) {
						doc = elem.documentElement;

						// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
						// whichever is greatest
						return Math.max(
							elem.body["scroll" + name], doc["scroll" + name],
							elem.body["offset" + name], doc["offset" + name],
							doc["client" + name]
						);
					}

					return value === undefined ?

						// Get width or height on the element, requesting but not forcing parseFloat
						jQuery.css(elem, type, extra) :

						// Set width or height on the element
						jQuery.style(elem, type, value, extra);
				}, type, chainable ? margin : undefined, chainable);
			};
		});
	});


	jQuery.each([
		"ajaxStart",
		"ajaxStop",
		"ajaxComplete",
		"ajaxError",
		"ajaxSuccess",
		"ajaxSend"
	], function (_i, type) {
		jQuery.fn[type] = function (fn) {
			return this.on(type, fn);
		};
	});




	jQuery.fn.extend({

		bind: function (types, data, fn) {
			return this.on(types, null, data, fn);
		},
		unbind: function (types, fn) {
			return this.off(types, null, fn);
		},

		delegate: function (selector, types, data, fn) {
			return this.on(types, selector, data, fn);
		},
		undelegate: function (selector, types, fn) {

			// ( namespace ) or ( selector, types [, fn] )
			return arguments.length === 1 ?
				this.off(selector, "**") :
				this.off(types, selector || "**", fn);
		},

		hover: function (fnOver, fnOut) {
			return this.mouseenter(fnOver).mouseleave(fnOut || fnOver);
		}
	});

	jQuery.each(
		("blur focus focusin focusout resize scroll click dblclick " +
			"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
			"change select submit keydown keypress keyup contextmenu").split(" "),
		function (_i, name) {

			// Handle event binding
			jQuery.fn[name] = function (data, fn) {
				return arguments.length > 0 ?
					this.on(name, null, data, fn) :
					this.trigger(name);
			};
		}
	);




	// Support: Android <=4.0 only
	// Make sure we trim BOM and NBSP
	// Require that the "whitespace run" starts from a non-whitespace
	// to avoid O(N^2) behavior when the engine would try matching "\s+$" at each space position.
	var rtrim = /^[\s\uFEFF\xA0]+|([^\s\uFEFF\xA0])[\s\uFEFF\xA0]+$/g;

	// Bind a function to a context, optionally partially applying any
	// arguments.
	// jQuery.proxy is deprecated to promote standards (specifically Function#bind)
	// However, it is not slated for removal any time soon
	jQuery.proxy = function (fn, context) {
		var tmp, args, proxy;

		if (typeof context === "string") {
			tmp = fn[context];
			context = fn;
			fn = tmp;
		}

		// Quick check to determine if target is callable, in the spec
		// this throws a TypeError, but we will just return undefined.
		if (!isFunction(fn)) {
			return undefined;
		}

		// Simulated bind
		args = slice.call(arguments, 2);
		proxy = function () {
			return fn.apply(context || this, args.concat(slice.call(arguments)));
		};

		// Set the guid of unique handler to the same of original handler, so it can be removed
		proxy.guid = fn.guid = fn.guid || jQuery.guid++;

		return proxy;
	};

	jQuery.holdReady = function (hold) {
		if (hold) {
			jQuery.readyWait++;
		} else {
			jQuery.ready(true);
		}
	};
	jQuery.isArray = Array.isArray;
	jQuery.parseJSON = JSON.parse;
	jQuery.nodeName = nodeName;
	jQuery.isFunction = isFunction;
	jQuery.isWindow = isWindow;
	jQuery.camelCase = camelCase;
	jQuery.type = toType;

	jQuery.now = Date.now;

	jQuery.isNumeric = function (obj) {

		// As of jQuery 3.0, isNumeric is limited to
		// strings and numbers (primitives or objects)
		// that can be coerced to finite numbers (gh-2662)
		var type = jQuery.type(obj);
		return (type === "number" || type === "string") &&

			// parseFloat NaNs numeric-cast false positives ("")
			// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
			// subtraction forces infinities to NaN
			!isNaN(obj - parseFloat(obj));
	};

	jQuery.trim = function (text) {
		return text == null ?
			"" :
			(text + "").replace(rtrim, "$1");
	};



	// Register as a named AMD module, since jQuery can be concatenated with other
	// files that may use define, but not via a proper concatenation script that
	// understands anonymous AMD modules. A named AMD is safest and most robust
	// way to register. Lowercase jquery is used because AMD module names are
	// derived from file names, and jQuery is normally delivered in a lowercase
	// file name. Do this after creating the global so that if an AMD module wants
	// to call noConflict to hide this version of jQuery, it will work.

	// Note that for maximum portability, libraries that are not jQuery should
	// declare themselves as anonymous modules, and avoid setting a global if an
	// AMD loader is present. jQuery is a special case. For more information, see
	// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

	if (typeof define === "function" && define.amd) {
		define("jquery", [], function () {
			return jQuery;
		});
	}




	var

		// Map over jQuery in case of overwrite
		_jQuery = window.jQuery,

		// Map over the $ in case of overwrite
		_$ = window.$;

	jQuery.noConflict = function (deep) {
		if (window.$ === jQuery) {
			window.$ = _$;
		}

		if (deep && window.jQuery === jQuery) {
			window.jQuery = _jQuery;
		}

		return jQuery;
	};

	// Expose jQuery and $ identifiers, even in AMD
	// (trac-7102#comment:10, https://github.com/jquery/jquery/pull/557)
	// and CommonJS for browser emulators (trac-13566)
	if (typeof noGlobal === "undefined") {
		window.jQuery = window.$ = jQuery;
	}




	return jQuery;
});


/**
 *******************************
 **      SEMANTIC-UI          **
 *******************************
 */


; (function ($, window, document, undefined) {

	$.site = $.fn.site = function (parameters) {
		var
			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			settings = ($.isPlainObject(parameters))
				? $.extend(true, {}, $.site.settings, parameters)
				: $.extend({}, $.site.settings),

			namespace = settings.namespace,
			error = settings.error,

			eventNamespace = '.' + namespace,
			moduleNamespace = 'module-' + namespace,

			$document = $(document),
			$module = $document,
			element = this,
			instance = $module.data(moduleNamespace),

			module,
			returnedValue
			;
		module = {

			initialize: function () {
				module.instantiate();
			},

			instantiate: function () {
				module.verbose('Storing instance of site', module);
				instance = module;
				$module
					.data(moduleNamespace, module)
					;
			},

			normalize: function () {
				module.fix.console();
				module.fix.requestAnimationFrame();
			},

			fix: {
				console: function () {
					module.debug('Normalizing window.console');
					if (console === undefined || console.log === undefined) {
						module.verbose('Console not available, normalizing events');
						module.disable.console();
					}
					if (typeof console.group == 'undefined' || typeof console.groupEnd == 'undefined' || typeof console.groupCollapsed == 'undefined') {
						module.verbose('Console group not available, normalizing events');
						window.console.group = function () { };
						window.console.groupEnd = function () { };
						window.console.groupCollapsed = function () { };
					}
					if (typeof console.markTimeline == 'undefined') {
						module.verbose('Mark timeline not available, normalizing events');
						window.console.markTimeline = function () { };
					}
				},
				consoleClear: function () {
					module.debug('Disabling programmatic console clearing');
					window.console.clear = function () { };
				},
				requestAnimationFrame: function () {
					module.debug('Normalizing requestAnimationFrame');
					if (window.requestAnimationFrame === undefined) {
						module.debug('RequestAnimationFrame not available, normalizing event');
						window.requestAnimationFrame = window.requestAnimationFrame
							|| window.mozRequestAnimationFrame
							|| window.webkitRequestAnimationFrame
							|| window.msRequestAnimationFrame
							|| function (callback) { setTimeout(callback, 0); }
							;
					}
				}
			},

			moduleExists: function (name) {
				return ($.fn[name] !== undefined && $.fn[name].settings !== undefined);
			},

			enabled: {
				modules: function (modules) {
					var
						enabledModules = []
						;
					modules = modules || settings.modules;
					$.each(modules, function (index, name) {
						if (module.moduleExists(name)) {
							enabledModules.push(name);
						}
					});
					return enabledModules;
				}
			},

			disabled: {
				modules: function (modules) {
					var
						disabledModules = []
						;
					modules = modules || settings.modules;
					$.each(modules, function (index, name) {
						if (!module.moduleExists(name)) {
							disabledModules.push(name);
						}
					});
					return disabledModules;
				}
			},

			change: {
				setting: function (setting, value, modules, modifyExisting) {
					modules = (typeof modules === 'string')
						? (modules === 'all')
							? settings.modules
							: [modules]
						: modules || settings.modules
						;
					modifyExisting = (modifyExisting !== undefined)
						? modifyExisting
						: true
						;
					$.each(modules, function (index, name) {
						var
							namespace = (module.moduleExists(name))
								? $.fn[name].settings.namespace || false
								: true,
							$existingModules
							;
						if (module.moduleExists(name)) {
							module.verbose('Changing default setting', setting, value, name);
							$.fn[name].settings[setting] = value;
							if (modifyExisting && namespace) {
								$existingModules = $(':data(module-' + namespace + ')');
								if ($existingModules.length > 0) {
									module.verbose('Modifying existing settings', $existingModules);
									$existingModules[name]('setting', setting, value);
								}
							}
						}
					});
				},
				settings: function (newSettings, modules, modifyExisting) {
					modules = (typeof modules === 'string')
						? [modules]
						: modules || settings.modules
						;
					modifyExisting = (modifyExisting !== undefined)
						? modifyExisting
						: true
						;
					$.each(modules, function (index, name) {
						var
							$existingModules
							;
						if (module.moduleExists(name)) {
							module.verbose('Changing default setting', newSettings, name);
							$.extend(true, $.fn[name].settings, newSettings);
							if (modifyExisting && namespace) {
								$existingModules = $(':data(module-' + namespace + ')');
								if ($existingModules.length > 0) {
									module.verbose('Modifying existing settings', $existingModules);
									$existingModules[name]('setting', newSettings);
								}
							}
						}
					});
				}
			},

			enable: {
				console: function () {
					module.console(true);
				},
				debug: function (modules, modifyExisting) {
					modules = modules || settings.modules;
					module.debug('Enabling debug for modules', modules);
					module.change.setting('debug', true, modules, modifyExisting);
				},
				verbose: function (modules, modifyExisting) {
					modules = modules || settings.modules;
					module.debug('Enabling verbose debug for modules', modules);
					module.change.setting('verbose', true, modules, modifyExisting);
				}
			},
			disable: {
				console: function () {
					module.console(false);
				},
				debug: function (modules, modifyExisting) {
					modules = modules || settings.modules;
					module.debug('Disabling debug for modules', modules);
					module.change.setting('debug', false, modules, modifyExisting);
				},
				verbose: function (modules, modifyExisting) {
					modules = modules || settings.modules;
					module.debug('Disabling verbose debug for modules', modules);
					module.change.setting('verbose', false, modules, modifyExisting);
				}
			},

			console: function (enable) {
				if (enable) {
					if (instance.cache.console === undefined) {
						module.error(error.console);
						return;
					}
					module.debug('Restoring console function');
					window.console = instance.cache.console;
				}
				else {
					module.debug('Disabling console function');
					instance.cache.console = window.console;
					window.console = {
						clear: function () { },
						error: function () { },
						group: function () { },
						groupCollapsed: function () { },
						groupEnd: function () { },
						info: function () { },
						log: function () { },
						markTimeline: function () { },
						warn: function () { }
					};
				}
			},

			destroy: function () {
				module.verbose('Destroying previous site for', $module);
				$module
					.removeData(moduleNamespace)
					;
			},

			cache: {},

			setting: function (name, value) {
				if ($.isPlainObject(name)) {
					$.extend(true, settings, name);
				}
				else if (value !== undefined) {
					settings[name] = value;
				}
				else {
					return settings[name];
				}
			},
			internal: function (name, value) {
				if ($.isPlainObject(name)) {
					$.extend(true, module, name);
				}
				else if (value !== undefined) {
					module[name] = value;
				}
				else {
					return module[name];
				}
			},
			debug: function () {
				if (settings.debug) {
					if (settings.performance) {
						module.performance.log(arguments);
					}
					else {
						module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
						module.debug.apply(console, arguments);
					}
				}
			},
			verbose: function () {
				if (settings.verbose && settings.debug) {
					if (settings.performance) {
						module.performance.log(arguments);
					}
					else {
						module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
						module.verbose.apply(console, arguments);
					}
				}
			},
			error: function () {
				module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
				module.error.apply(console, arguments);
			},
			performance: {
				log: function (message) {
					var
						currentTime,
						executionTime,
						previousTime
						;
					if (settings.performance) {
						currentTime = new Date().getTime();
						previousTime = time || currentTime;
						executionTime = currentTime - previousTime;
						time = currentTime;
						performance.push({
							'Element': element,
							'Name': message[0],
							'Arguments': [].slice.call(message, 1) || '',
							'Execution Time': executionTime
						});
					}
					clearTimeout(module.performance.timer);
					module.performance.timer = setTimeout(module.performance.display, 500);
				},
				display: function () {
					var
						title = settings.name + ':',
						totalTime = 0
						;
					time = false;
					clearTimeout(module.performance.timer);
					$.each(performance, function (index, data) {
						totalTime += data['Execution Time'];
					});
					title += ' ' + totalTime + 'ms';
					if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
						console.groupCollapsed(title);
						if (console.table) {
							console.table(performance);
						}
						else {
							$.each(performance, function (index, data) {
								console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
							});
						}
						console.groupEnd();
					}
					performance = [];
				}
			},
			invoke: function (query, passedArguments, context) {
				var
					object = instance,
					maxDepth,
					found,
					response
					;
				passedArguments = passedArguments || queryArguments;
				context = element || context;
				if (typeof query == 'string' && object !== undefined) {
					query = query.split(/[\. ]/);
					maxDepth = query.length - 1;
					$.each(query, function (depth, value) {
						var camelCaseValue = (depth != maxDepth)
							? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
							: query
							;
						if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
							object = object[camelCaseValue];
						}
						else if (object[camelCaseValue] !== undefined) {
							found = object[camelCaseValue];
							return false;
						}
						else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
							object = object[value];
						}
						else if (object[value] !== undefined) {
							found = object[value];
							return false;
						}
						else {
							module.error(error.method, query);
							return false;
						}
					});
				}
				if ($.isFunction(found)) {
					response = found.apply(context, passedArguments);
				}
				else if (found !== undefined) {
					response = found;
				}
				if ($.isArray(returnedValue)) {
					returnedValue.push(response);
				}
				else if (returnedValue !== undefined) {
					returnedValue = [returnedValue, response];
				}
				else if (response !== undefined) {
					returnedValue = response;
				}
				return found;
			}
		};

		if (methodInvoked) {
			if (instance === undefined) {
				module.initialize();
			}
			module.invoke(query);
		}
		else {
			if (instance !== undefined) {
				module.destroy();
			}
			module.initialize();
		}
		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.site.settings = {

		name: 'Site',
		namespace: 'site',

		error: {
			console: 'Console cannot be restored, most likely it was overwritten outside of module',
			method: 'The method you called is not defined.'
		},

		debug: false,
		verbose: false,
		performance: true,

		modules: [
			'accordion',
			'api',
			'checkbox',
			'dimmer',
			'dropdown',
			'embed',
			'form',
			'modal',
			'nag',
			'popup',
			'rating',
			'shape',
			'sidebar',
			'state',
			'sticky',
			'tab',
			'transition',
			'visit',
			'visibility'
		],

		siteNamespace: 'site',
		namespaceStub: {
			cache: {},
			config: {},
			sections: {},
			section: {},
			utilities: {}
		}

	};

	// allows for selection of elements with data attributes
	$.extend($.expr[":"], {
		data: ($.expr.createPseudo)
			? $.expr.createPseudo(function (dataName) {
				return function (elem) {
					return !!$.data(elem, dataName);
				};
			})
			: function (elem, i, match) {
				// support: jQuery < 1.8
				return !!$.data(elem, match[3]);
			}
	});


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Form Validation
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.form = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			legacyParameters = arguments[1],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;
		$allModules
			.each(function () {
				var
					$module = $(this),
					element = this,

					formErrors = [],
					keyHeldDown = false,

					// set at run-time
					$field,
					$group,
					$message,
					$prompt,
					$submit,
					$clear,
					$reset,

					settings,
					validation,

					metadata,
					selector,
					className,
					regExp,
					error,

					namespace,
					moduleNamespace,
					eventNamespace,

					instance,
					module
					;

				module = {

					initialize: function () {

						// settings grabbed at run time
						module.get.settings();
						if (methodInvoked) {
							if (instance === undefined) {
								module.instantiate();
							}
							module.invoke(query);
						}
						else {
							if (instance !== undefined) {
								instance.invoke('destroy');
							}
							module.verbose('Initializing form validation', $module, settings);
							module.bindEvents();
							module.set.defaults();
							module.instantiate();
						}
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous module', instance);
						module.removeEvents();
						$module
							.removeData(moduleNamespace)
							;
					},

					refresh: function () {
						module.verbose('Refreshing selector cache');
						$field = $module.find(selector.field);
						$group = $module.find(selector.group);
						$message = $module.find(selector.message);
						$prompt = $module.find(selector.prompt);

						$submit = $module.find(selector.submit);
						$clear = $module.find(selector.clear);
						$reset = $module.find(selector.reset);
					},

					submit: function () {
						module.verbose('Submitting form', $module);
						$module
							.submit()
							;
					},

					attachEvents: function (selector, action) {
						action = action || 'submit';
						$(selector)
							.on('click' + eventNamespace, function (event) {
								module[action]();
								event.preventDefault();
							})
							;
					},

					bindEvents: function () {
						module.verbose('Attaching form events');
						$module
							.on('submit' + eventNamespace, module.validate.form)
							.on('blur' + eventNamespace, selector.field, module.event.field.blur)
							.on('click' + eventNamespace, selector.submit, module.submit)
							.on('click' + eventNamespace, selector.reset, module.reset)
							.on('click' + eventNamespace, selector.clear, module.clear)
							;
						if (settings.keyboardShortcuts) {
							$module
								.on('keydown' + eventNamespace, selector.field, module.event.field.keydown)
								;
						}
						$field
							.each(function () {
								var
									$input = $(this),
									type = $input.prop('type'),
									inputEvent = module.get.changeEvent(type, $input)
									;
								$(this)
									.on(inputEvent + eventNamespace, module.event.field.change)
									;
							})
							;
					},

					clear: function () {
						$field
							.each(function () {
								var
									$field = $(this),
									$element = $field.parent(),
									$fieldGroup = $field.closest($group),
									$prompt = $fieldGroup.find(selector.prompt),
									defaultValue = $field.data(metadata.defaultValue) || '',
									isCheckbox = $element.is(selector.uiCheckbox),
									isDropdown = $element.is(selector.uiDropdown),
									isErrored = $fieldGroup.hasClass(className.error)
									;
								if (isErrored) {
									module.verbose('Resetting error on field', $fieldGroup);
									$fieldGroup.removeClass(className.error);
									$prompt.remove();
								}
								if (isDropdown) {
									module.verbose('Resetting dropdown value', $element, defaultValue);
									$element.dropdown('clear');
								}
								else if (isCheckbox) {
									$field.prop('checked', false);
								}
								else {
									module.verbose('Resetting field value', $field, defaultValue);
									$field.val('');
								}
							})
							;
					},

					reset: function () {
						$field
							.each(function () {
								var
									$field = $(this),
									$element = $field.parent(),
									$fieldGroup = $field.closest($group),
									$prompt = $fieldGroup.find(selector.prompt),
									defaultValue = $field.data(metadata.defaultValue),
									isCheckbox = $element.is(selector.uiCheckbox),
									isDropdown = $element.is(selector.uiDropdown),
									isErrored = $fieldGroup.hasClass(className.error)
									;
								if (defaultValue === undefined) {
									return;
								}
								if (isErrored) {
									module.verbose('Resetting error on field', $fieldGroup);
									$fieldGroup.removeClass(className.error);
									$prompt.remove();
								}
								if (isDropdown) {
									module.verbose('Resetting dropdown value', $element, defaultValue);
									$element.dropdown('restore defaults');
								}
								else if (isCheckbox) {
									module.verbose('Resetting checkbox value', $element, defaultValue);
									$field.prop('checked', defaultValue);
								}
								else {
									module.verbose('Resetting field value', $field, defaultValue);
									$field.val(defaultValue);
								}
							})
							;
					},

					determine: {
						isValid: function () {
							var
								allValid = true
								;
							$.each(validation, function (fieldName, field) {
								if (!(module.validate.field(field, fieldName, true))) {
									allValid = false;
								}
							});
							return allValid;
						}
					},

					is: {
						bracketedRule: function (rule) {
							return (rule.type && rule.type.match(settings.regExp.bracket));
						},
						shorthandFields: function (fields) {
							var
								fieldKeys = Object.keys(fields),
								firstRule = fields[fieldKeys[0]]
								;
							return module.is.shorthandRules(firstRule);
						},
						// duck type rule test
						shorthandRules: function (rules) {
							return (typeof rules == 'string' || $.isArray(rules));
						},
						empty: function ($field) {
							if (!$field || $field.length === 0) {
								return true;
							}
							else if ($field.is('input[type="checkbox"]')) {
								return !$field.is(':checked');
							}
							else {
								return module.is.blank($field);
							}
						},
						blank: function ($field) {
							return $.trim($field.val()) === '';
						},
						valid: function (field) {
							var
								allValid = true
								;
							if (field) {
								module.verbose('Checking if field is valid', field);
								return module.validate.field(validation[field], field, false);
							}
							else {
								module.verbose('Checking if form is valid');
								$.each(validation, function (fieldName, field) {
									if (!module.is.valid(fieldName)) {
										allValid = false;
									}
								});
								return allValid;
							}
						}
					},

					removeEvents: function () {
						$module
							.off(eventNamespace)
							;
						$field
							.off(eventNamespace)
							;
						$submit
							.off(eventNamespace)
							;
						$field
							.off(eventNamespace)
							;
					},

					event: {
						field: {
							keydown: function (event) {
								var
									$field = $(this),
									key = event.which,
									isInput = $field.is(selector.input),
									isCheckbox = $field.is(selector.checkbox),
									isInDropdown = ($field.closest(selector.uiDropdown).length > 0),
									keyCode = {
										enter: 13,
										escape: 27
									}
									;
								if (key == keyCode.escape) {
									module.verbose('Escape key pressed blurring field');
									$field
										.blur()
										;
								}
								if (!event.ctrlKey && key == keyCode.enter && isInput && !isInDropdown && !isCheckbox) {
									if (!keyHeldDown) {
										$field
											.one('keyup' + eventNamespace, module.event.field.keyup)
											;
										module.submit();
										module.debug('Enter pressed on input submitting form');
									}
									keyHeldDown = true;
								}
							},
							keyup: function () {
								keyHeldDown = false;
							},
							blur: function (event) {
								var
									$field = $(this),
									$fieldGroup = $field.closest($group),
									validationRules = module.get.validation($field)
									;
								if ($fieldGroup.hasClass(className.error)) {
									module.debug('Revalidating field', $field, validationRules);
									if (validationRules) {
										module.validate.field(validationRules);
									}
								}
								else if (settings.on == 'blur') {
									if (validationRules) {
										module.validate.field(validationRules);
									}
								}
							},
							change: function (event) {
								var
									$field = $(this),
									$fieldGroup = $field.closest($group),
									validationRules = module.get.validation($field)
									;
								if (validationRules && (settings.on == 'change' || ($fieldGroup.hasClass(className.error) && settings.revalidate))) {
									clearTimeout(module.timer);
									module.timer = setTimeout(function () {
										module.debug('Revalidating field', $field, module.get.validation($field));
										module.validate.field(validationRules);
									}, settings.delay);
								}
							}
						}

					},

					get: {
						ancillaryValue: function (rule) {
							if (!rule.type || (!rule.value && !module.is.bracketedRule(rule))) {
								return false;
							}
							return (rule.value !== undefined)
								? rule.value
								: rule.type.match(settings.regExp.bracket)[1] + ''
								;
						},
						ruleName: function (rule) {
							if (module.is.bracketedRule(rule)) {
								return rule.type.replace(rule.type.match(settings.regExp.bracket)[0], '');
							}
							return rule.type;
						},
						changeEvent: function (type, $input) {
							if (type == 'checkbox' || type == 'radio' || type == 'hidden' || $input.is('select')) {
								return 'change';
							}
							else {
								return module.get.inputEvent();
							}
						},
						inputEvent: function () {
							return (document.createElement('input').oninput !== undefined)
								? 'input'
								: (document.createElement('input').onpropertychange !== undefined)
									? 'propertychange'
									: 'keyup'
								;
						},
						fieldsFromShorthand: function (fields) {
							var
								fullFields = {}
								;
							$.each(fields, function (name, rules) {
								if (typeof rules == 'string') {
									rules = [rules];
								}
								fullFields[name] = {
									rules: []
								};
								$.each(rules, function (index, rule) {
									fullFields[name].rules.push({ type: rule });
								});
							});
							return fullFields;
						},
						prompt: function (rule, field) {
							var
								ruleName = module.get.ruleName(rule),
								ancillary = module.get.ancillaryValue(rule),
								$field = module.get.field(field.identifier),
								value = $field.val(),
								prompt = $.isFunction(rule.prompt)
									? rule.prompt(value)
									: rule.prompt || settings.prompt[ruleName] || settings.text.unspecifiedRule,
								requiresValue = (prompt.search('{value}') !== -1),
								requiresName = (prompt.search('{name}') !== -1),
								$label,
								name
								;
							if (requiresValue) {
								prompt = prompt.replace('{value}', $field.val());
							}
							if (requiresName) {
								$label = $field.closest(selector.group).find('label').eq(0);
								name = ($label.length == 1)
									? $label.text()
									: $field.prop('placeholder') || settings.text.unspecifiedField
									;
								prompt = prompt.replace('{name}', name);
							}
							prompt = prompt.replace('{identifier}', field.identifier);
							prompt = prompt.replace('{ruleValue}', ancillary);
							if (!rule.prompt) {
								module.verbose('Using default validation prompt for type', prompt, ruleName);
							}
							return prompt;
						},
						settings: function () {
							if ($.isPlainObject(parameters)) {
								var
									keys = Object.keys(parameters),
									isLegacySettings = (keys.length > 0)
										? (parameters[keys[0]].identifier !== undefined && parameters[keys[0]].rules !== undefined)
										: false,
									ruleKeys
									;
								if (isLegacySettings) {
									// 1.x (ducktyped)
									settings = $.extend(true, {}, $.fn.form.settings, legacyParameters);
									validation = $.extend({}, $.fn.form.settings.defaults, parameters);
									module.error(settings.error.oldSyntax, element);
									module.verbose('Extending settings from legacy parameters', validation, settings);
								}
								else {
									// 2.x
									if (parameters.fields && module.is.shorthandFields(parameters.fields)) {
										parameters.fields = module.get.fieldsFromShorthand(parameters.fields);
									}
									settings = $.extend(true, {}, $.fn.form.settings, parameters);
									validation = $.extend({}, $.fn.form.settings.defaults, settings.fields);
									module.verbose('Extending settings', validation, settings);
								}
							}
							else {
								settings = $.fn.form.settings;
								validation = $.fn.form.settings.defaults;
								module.verbose('Using default form validation', validation, settings);
							}

							// shorthand
							namespace = settings.namespace;
							metadata = settings.metadata;
							selector = settings.selector;
							className = settings.className;
							regExp = settings.regExp;
							error = settings.error;
							moduleNamespace = 'module-' + namespace;
							eventNamespace = '.' + namespace;

							// grab instance
							instance = $module.data(moduleNamespace);

							// refresh selector cache
							module.refresh();
						},
						field: function (identifier) {
							module.verbose('Finding field with identifier', identifier);
							identifier = module.escape.string(identifier);
							if ($field.filter('#' + identifier).length > 0) {
								return $field.filter('#' + identifier);
							}
							else if ($field.filter('[name="' + identifier + '"]').length > 0) {
								return $field.filter('[name="' + identifier + '"]');
							}
							else if ($field.filter('[name="' + identifier + '[]"]').length > 0) {
								return $field.filter('[name="' + identifier + '[]"]');
							}
							else if ($field.filter('[data-' + metadata.validate + '="' + identifier + '"]').length > 0) {
								return $field.filter('[data-' + metadata.validate + '="' + identifier + '"]');
							}
							return $('<input/>');
						},
						fields: function (fields) {
							var
								$fields = $()
								;
							$.each(fields, function (index, name) {
								$fields = $fields.add(module.get.field(name));
							});
							return $fields;
						},
						validation: function ($field) {
							var
								fieldValidation,
								identifier
								;
							if (!validation) {
								return false;
							}
							$.each(validation, function (fieldName, field) {
								identifier = field.identifier || fieldName;
								if (module.get.field(identifier)[0] == $field[0]) {
									field.identifier = identifier;
									fieldValidation = field;
								}
							});
							return fieldValidation || false;
						},
						value: function (field) {
							var
								fields = [],
								results
								;
							fields.push(field);
							results = module.get.values.call(element, fields);
							return results[field];
						},
						values: function (fields) {
							var
								$fields = $.isArray(fields)
									? module.get.fields(fields)
									: $field,
								values = {}
								;
							$fields.each(function (index, field) {
								var
									$field = $(field),
									type = $field.prop('type'),
									name = $field.prop('name'),
									value = $field.val(),
									isCheckbox = $field.is(selector.checkbox),
									isRadio = $field.is(selector.radio),
									isMultiple = (name.indexOf('[]') !== -1),
									isChecked = (isCheckbox)
										? $field.is(':checked')
										: false
									;
								if (name) {
									if (isMultiple) {
										name = name.replace('[]', '');
										if (!values[name]) {
											values[name] = [];
										}
										if (isCheckbox) {
											if (isChecked) {
												values[name].push(value || true);
											}
											else {
												values[name].push(false);
											}
										}
										else {
											values[name].push(value);
										}
									}
									else {
										if (isRadio) {
											if (values[name] === undefined || values[name] == false) {
												values[name] = (isChecked)
													? value || true
													: false
													;
											}
										}
										else if (isCheckbox) {
											if (isChecked) {
												values[name] = value || true;
											}
											else {
												values[name] = false;
											}
										}
										else {
											values[name] = value;
										}
									}
								}
							});
							return values;
						}
					},

					has: {

						field: function (identifier) {
							module.verbose('Checking for existence of a field with identifier', identifier);
							identifier = module.escape.string(identifier);
							if (typeof identifier !== 'string') {
								module.error(error.identifier, identifier);
							}
							if ($field.filter('#' + identifier).length > 0) {
								return true;
							}
							else if ($field.filter('[name="' + identifier + '"]').length > 0) {
								return true;
							}
							else if ($field.filter('[data-' + metadata.validate + '="' + identifier + '"]').length > 0) {
								return true;
							}
							return false;
						}

					},

					escape: {
						string: function (text) {
							text = String(text);
							return text.replace(regExp.escape, '\\$&');
						}
					},

					add: {
						// alias
						rule: function (name, rules) {
							module.add.field(name, rules);
						},
						field: function (name, rules) {
							var
								newValidation = {}
								;
							if (module.is.shorthandRules(rules)) {
								rules = $.isArray(rules)
									? rules
									: [rules]
									;
								newValidation[name] = {
									rules: []
								};
								$.each(rules, function (index, rule) {
									newValidation[name].rules.push({ type: rule });
								});
							}
							else {
								newValidation[name] = rules;
							}
							validation = $.extend({}, validation, newValidation);
							module.debug('Adding rules', newValidation, validation);
						},
						fields: function (fields) {
							var
								newValidation
								;
							if (fields && module.is.shorthandFields(fields)) {
								newValidation = module.get.fieldsFromShorthand(fields);
							}
							else {
								newValidation = fields;
							}
							validation = $.extend({}, validation, newValidation);
						},
						prompt: function (identifier, errors) {
							var
								$field = module.get.field(identifier),
								$fieldGroup = $field.closest($group),
								$prompt = $fieldGroup.children(selector.prompt),
								promptExists = ($prompt.length !== 0)
								;
							errors = (typeof errors == 'string')
								? [errors]
								: errors
								;
							module.verbose('Adding field error state', identifier);
							$fieldGroup
								.addClass(className.error)
								;
							if (settings.inline) {
								if (!promptExists) {
									$prompt = settings.templates.prompt(errors);
									$prompt
										.appendTo($fieldGroup)
										;
								}
								$prompt
									.html(errors[0])
									;
								if (!promptExists) {
									if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
										module.verbose('Displaying error with css transition', settings.transition);
										$prompt.transition(settings.transition + ' in', settings.duration);
									}
									else {
										module.verbose('Displaying error with fallback javascript animation');
										$prompt
											.fadeIn(settings.duration)
											;
									}
								}
								else {
									module.verbose('Inline errors are disabled, no inline error added', identifier);
								}
							}
						},
						errors: function (errors) {
							module.debug('Adding form error messages', errors);
							module.set.error();
							$message
								.html(settings.templates.error(errors))
								;
						}
					},

					remove: {
						rule: function (field, rule) {
							var
								rules = $.isArray(rule)
									? rule
									: [rule]
								;
							if (rule == undefined) {
								module.debug('Removed all rules');
								validation[field].rules = [];
								return;
							}
							if (validation[field] == undefined || !$.isArray(validation[field].rules)) {
								return;
							}
							$.each(validation[field].rules, function (index, rule) {
								if (rules.indexOf(rule.type) !== -1) {
									module.debug('Removed rule', rule.type);
									validation[field].rules.splice(index, 1);
								}
							});
						},
						field: function (field) {
							var
								fields = $.isArray(field)
									? field
									: [field]
								;
							$.each(fields, function (index, field) {
								module.remove.rule(field);
							});
						},
						// alias
						rules: function (field, rules) {
							if ($.isArray(field)) {
								$.each(fields, function (index, field) {
									module.remove.rule(field, rules);
								});
							}
							else {
								module.remove.rule(field, rules);
							}
						},
						fields: function (fields) {
							module.remove.field(fields);
						},
						prompt: function (identifier) {
							var
								$field = module.get.field(identifier),
								$fieldGroup = $field.closest($group),
								$prompt = $fieldGroup.children(selector.prompt)
								;
							$fieldGroup
								.removeClass(className.error)
								;
							if (settings.inline && $prompt.is(':visible')) {
								module.verbose('Removing prompt for field', identifier);
								if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
									$prompt.transition(settings.transition + ' out', settings.duration, function () {
										$prompt.remove();
									});
								}
								else {
									$prompt
										.fadeOut(settings.duration, function () {
											$prompt.remove();
										})
										;
								}
							}
						}
					},

					set: {
						success: function () {
							$module
								.removeClass(className.error)
								.addClass(className.success)
								;
						},
						defaults: function () {
							$field
								.each(function () {
									var
										$field = $(this),
										isCheckbox = ($field.filter(selector.checkbox).length > 0),
										value = (isCheckbox)
											? $field.is(':checked')
											: $field.val()
										;
									$field.data(metadata.defaultValue, value);
								})
								;
						},
						error: function () {
							$module
								.removeClass(className.success)
								.addClass(className.error)
								;
						},
						value: function (field, value) {
							var
								fields = {}
								;
							fields[field] = value;
							return module.set.values.call(element, fields);
						},
						values: function (fields) {
							if ($.isEmptyObject(fields)) {
								return;
							}
							$.each(fields, function (key, value) {
								var
									$field = module.get.field(key),
									$element = $field.parent(),
									isMultiple = $.isArray(value),
									isCheckbox = $element.is(selector.uiCheckbox),
									isDropdown = $element.is(selector.uiDropdown),
									isRadio = ($field.is(selector.radio) && isCheckbox),
									fieldExists = ($field.length > 0),
									$multipleField
									;
								if (fieldExists) {
									if (isMultiple && isCheckbox) {
										module.verbose('Selecting multiple', value, $field);
										$element.checkbox('uncheck');
										$.each(value, function (index, value) {
											$multipleField = $field.filter('[value="' + value + '"]');
											$element = $multipleField.parent();
											if ($multipleField.length > 0) {
												$element.checkbox('check');
											}
										});
									}
									else if (isRadio) {
										module.verbose('Selecting radio value', value, $field);
										$field.filter('[value="' + value + '"]')
											.parent(selector.uiCheckbox)
											.checkbox('check')
											;
									}
									else if (isCheckbox) {
										module.verbose('Setting checkbox value', value, $element);
										if (value === true) {
											$element.checkbox('check');
										}
										else {
											$element.checkbox('uncheck');
										}
									}
									else if (isDropdown) {
										module.verbose('Setting dropdown value', value, $element);
										$element.dropdown('set selected', value);
									}
									else {
										module.verbose('Setting field value', value, $field);
										$field.val(value);
									}
								}
							});
						}
					},

					validate: {

						form: function (event, ignoreCallbacks) {
							var
								values = module.get.values(),
								apiRequest
								;

							// input keydown event will fire submit repeatedly by browser default
							if (keyHeldDown) {
								return false;
							}

							// reset errors
							formErrors = [];
							if (module.determine.isValid()) {
								module.debug('Form has no validation errors, submitting');
								module.set.success();
								if (ignoreCallbacks !== true) {
									return settings.onSuccess.call(element, event, values);
								}
							}
							else {
								module.debug('Form has errors');
								module.set.error();
								if (!settings.inline) {
									module.add.errors(formErrors);
								}
								// prevent ajax submit
								if ($module.data('moduleApi') !== undefined) {
									event.stopImmediatePropagation();
								}
								if (ignoreCallbacks !== true) {
									return settings.onFailure.call(element, formErrors, values);
								}
							}
						},

						// takes a validation object and returns whether field passes validation
						field: function (field, fieldName, showErrors) {
							showErrors = (showErrors !== undefined)
								? showErrors
								: true
								;
							if (typeof field == 'string') {
								module.verbose('Validating field', field);
								fieldName = field;
								field = validation[field];
							}
							var
								identifier = field.identifier || fieldName,
								$field = module.get.field(identifier),
								$dependsField = (field.depends)
									? module.get.field(field.depends)
									: false,
								fieldValid = true,
								fieldErrors = []
								;
							if (!field.identifier) {
								module.debug('Using field name as identifier', identifier);
								field.identifier = identifier;
							}
							if ($field.prop('disabled')) {
								module.debug('Field is disabled. Skipping', identifier);
								fieldValid = true;
							}
							else if (field.optional && module.is.blank($field)) {
								module.debug('Field is optional and blank. Skipping', identifier);
								fieldValid = true;
							}
							else if (field.depends && module.is.empty($dependsField)) {
								module.debug('Field depends on another value that is not present or empty. Skipping', $dependsField);
								fieldValid = true;
							}
							else if (field.rules !== undefined) {
								$.each(field.rules, function (index, rule) {
									if (module.has.field(identifier) && !(module.validate.rule(field, rule))) {
										module.debug('Field is invalid', identifier, rule.type);
										fieldErrors.push(module.get.prompt(rule, field));
										fieldValid = false;
									}
								});
							}
							if (fieldValid) {
								if (showErrors) {
									module.remove.prompt(identifier, fieldErrors);
									settings.onValid.call($field);
								}
							}
							else {
								if (showErrors) {
									formErrors = formErrors.concat(fieldErrors);
									module.add.prompt(identifier, fieldErrors);
									settings.onInvalid.call($field, fieldErrors);
								}
								return false;
							}
							return true;
						},

						// takes validation rule and returns whether field passes rule
						rule: function (field, rule) {
							var
								$field = module.get.field(field.identifier),
								type = rule.type,
								value = $field.val(),
								isValid = true,
								ancillary = module.get.ancillaryValue(rule),
								ruleName = module.get.ruleName(rule),
								ruleFunction = settings.rules[ruleName]
								;
							if (!$.isFunction(ruleFunction)) {
								module.error(error.noRule, ruleName);
								return;
							}
							// cast to string avoiding encoding special values
							value = (value === undefined || value === '' || value === null)
								? ''
								: $.trim(value + '')
								;
							return ruleFunction.call($field, value, ancillary);
						}
					},

					setting: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							settings[name] = value;
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};
				module.initialize();
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.form.settings = {

		name: 'Form',
		namespace: 'form',

		debug: false,
		verbose: false,
		performance: true,

		fields: false,

		keyboardShortcuts: true,
		on: 'submit',
		inline: false,

		delay: 200,
		revalidate: true,

		transition: 'scale',
		duration: 200,

		onValid: function () { },
		onInvalid: function () { },
		onSuccess: function () { return true; },
		onFailure: function () { return false; },

		metadata: {
			defaultValue: 'default',
			validate: 'validate'
		},

		regExp: {
			htmlID: /^[a-zA-Z][\w:.-]*$/g,
			bracket: /\[(.*)\]/i,
			decimal: /^\d+\.?\d*$/,
			email: /^[a-z0-9!#$%&'*+\/=?^_`{|}~.-]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i,
			escape: /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
			flags: /^\/(.*)\/(.*)?/,
			integer: /^\-?\d+$/,
			number: /^\-?\d*(\.\d+)?$/,
			url: /(https?:\/\/(?:www\.|(?!www))[^\s\.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})/i
		},

		text: {
			unspecifiedRule: 'Please enter a valid value',
			unspecifiedField: 'This field'
		},

		prompt: {
			empty: '{name} must have a value',
			checked: '{name} must be checked',
			email: '{name} must be a valid e-mail',
			url: '{name} must be a valid url',
			regExp: '{name} is not formatted correctly',
			integer: '{name} must be an integer',
			decimal: '{name} must be a decimal number',
			number: '{name} must be set to a number',
			is: '{name} must be "{ruleValue}"',
			isExactly: '{name} must be exactly "{ruleValue}"',
			not: '{name} cannot be set to "{ruleValue}"',
			notExactly: '{name} cannot be set to exactly "{ruleValue}"',
			contain: '{name} must contain "{ruleValue}"',
			containExactly: '{name} must contain exactly "{ruleValue}"',
			doesntContain: '{name} cannot contain  "{ruleValue}"',
			doesntContainExactly: '{name} cannot contain exactly "{ruleValue}"',
			minLength: '{name} must be at least {ruleValue} characters',
			length: '{name} must be at least {ruleValue} characters',
			exactLength: '{name} must be exactly {ruleValue} characters',
			maxLength: '{name} cannot be longer than {ruleValue} characters',
			match: '{name} must match {ruleValue} field',
			different: '{name} must have a different value than {ruleValue} field',
			creditCard: '{name} must be a valid credit card number',
			minCount: '{name} must have at least {ruleValue} choices',
			exactCount: '{name} must have exactly {ruleValue} choices',
			maxCount: '{name} must have {ruleValue} or less choices'
		},

		selector: {
			checkbox: 'input[type="checkbox"], input[type="radio"]',
			clear: '.clear',
			field: 'input, textarea, select',
			group: '.field',
			input: 'input',
			message: '.error.message',
			prompt: '.prompt.label',
			radio: 'input[type="radio"]',
			reset: '.reset:not([type="reset"])',
			submit: '.submit:not([type="submit"])',
			uiCheckbox: '.ui.checkbox',
			uiDropdown: '.ui.dropdown'
		},

		className: {
			error: 'error',
			label: 'ui prompt label',
			pressed: 'down',
			success: 'success'
		},

		error: {
			identifier: 'You must specify a string identifier for each field',
			method: 'The method you called is not defined.',
			noRule: 'There is no rule matching the one you specified',
			oldSyntax: 'Starting in 2.0 forms now only take a single settings object. Validation settings converted to new syntax automatically.'
		},

		templates: {

			// template that produces error message
			error: function (errors) {
				var
					html = '<ul class="list">'
					;
				$.each(errors, function (index, value) {
					html += '<li>' + value + '</li>';
				});
				html += '</ul>';
				return $(html);
			},

			// template that produces label
			prompt: function (errors) {
				return $('<div/>')
					.addClass('ui basic red pointing prompt label')
					.html(errors[0])
					;
			}
		},

		rules: {

			// is not empty or blank string
			empty: function (value) {
				return !(value === undefined || '' === value || $.isArray(value) && value.length === 0);
			},

			// checkbox checked
			checked: function () {
				return ($(this).filter(':checked').length > 0);
			},

			// is most likely an email
			email: function (value) {
				return $.fn.form.settings.regExp.email.test(value);
			},

			// value is most likely url
			url: function (value) {
				return $.fn.form.settings.regExp.url.test(value);
			},

			// matches specified regExp
			regExp: function (value, regExp) {
				if (regExp instanceof RegExp) {
					return value.match(regExp);
				}
				var
					regExpParts = regExp.match($.fn.form.settings.regExp.flags),
					flags
					;
				// regular expression specified as /baz/gi (flags)
				if (regExpParts) {
					regExp = (regExpParts.length >= 2)
						? regExpParts[1]
						: regExp
						;
					flags = (regExpParts.length >= 3)
						? regExpParts[2]
						: ''
						;
				}
				return value.match(new RegExp(regExp, flags));
			},

			// is valid integer or matches range
			integer: function (value, range) {
				var
					intRegExp = $.fn.form.settings.regExp.integer,
					min,
					max,
					parts
					;
				if (!range || ['', '..'].indexOf(range) !== -1) {
					// do nothing
				}
				else if (range.indexOf('..') == -1) {
					if (intRegExp.test(range)) {
						min = max = range - 0;
					}
				}
				else {
					parts = range.split('..', 2);
					if (intRegExp.test(parts[0])) {
						min = parts[0] - 0;
					}
					if (intRegExp.test(parts[1])) {
						max = parts[1] - 0;
					}
				}
				return (
					intRegExp.test(value) &&
					(min === undefined || value >= min) &&
					(max === undefined || value <= max)
				);
			},

			// is valid number (with decimal)
			decimal: function (value) {
				return $.fn.form.settings.regExp.decimal.test(value);
			},

			// is valid number
			number: function (value) {
				return $.fn.form.settings.regExp.number.test(value);
			},

			// is value (case insensitive)
			is: function (value, text) {
				text = (typeof text == 'string')
					? text.toLowerCase()
					: text
					;
				value = (typeof value == 'string')
					? value.toLowerCase()
					: value
					;
				return (value == text);
			},

			// is value
			isExactly: function (value, text) {
				return (value == text);
			},

			// value is not another value (case insensitive)
			not: function (value, notValue) {
				value = (typeof value == 'string')
					? value.toLowerCase()
					: value
					;
				notValue = (typeof notValue == 'string')
					? notValue.toLowerCase()
					: notValue
					;
				return (value != notValue);
			},

			// value is not another value (case sensitive)
			notExactly: function (value, notValue) {
				return (value != notValue);
			},

			// value contains text (insensitive)
			contains: function (value, text) {
				// escape regex characters
				text = text.replace($.fn.form.settings.regExp.escape, "\\$&");
				return (value.search(new RegExp(text, 'i')) !== -1);
			},

			// value contains text (case sensitive)
			containsExactly: function (value, text) {
				// escape regex characters
				text = text.replace($.fn.form.settings.regExp.escape, "\\$&");
				return (value.search(new RegExp(text)) !== -1);
			},

			// value contains text (insensitive)
			doesntContain: function (value, text) {
				// escape regex characters
				text = text.replace($.fn.form.settings.regExp.escape, "\\$&");
				return (value.search(new RegExp(text, 'i')) === -1);
			},

			// value contains text (case sensitive)
			doesntContainExactly: function (value, text) {
				// escape regex characters
				text = text.replace($.fn.form.settings.regExp.escape, "\\$&");
				return (value.search(new RegExp(text)) === -1);
			},

			// is at least string length
			minLength: function (value, requiredLength) {
				return (value !== undefined)
					? (value.length >= requiredLength)
					: false
					;
			},

			// see rls notes for 2.0.6 (this is a duplicate of minLength)
			length: function (value, requiredLength) {
				return (value !== undefined)
					? (value.length >= requiredLength)
					: false
					;
			},

			// is exactly length
			exactLength: function (value, requiredLength) {
				return (value !== undefined)
					? (value.length == requiredLength)
					: false
					;
			},

			// is less than length
			maxLength: function (value, maxLength) {
				return (value !== undefined)
					? (value.length <= maxLength)
					: false
					;
			},

			// matches another field
			match: function (value, identifier) {
				var
					$form = $(this),
					matchingValue
					;
				if ($('[data-validate="' + identifier + '"]').length > 0) {
					matchingValue = $('[data-validate="' + identifier + '"]').val();
				}
				else if ($('#' + identifier).length > 0) {
					matchingValue = $('#' + identifier).val();
				}
				else if ($('[name="' + identifier + '"]').length > 0) {
					matchingValue = $('[name="' + identifier + '"]').val();
				}
				else if ($('[name="' + identifier + '[]"]').length > 0) {
					matchingValue = $('[name="' + identifier + '[]"]');
				}
				return (matchingValue !== undefined)
					? (value.toString() == matchingValue.toString())
					: false
					;
			},

			// different than another field
			different: function (value, identifier) {
				// use either id or name of field
				var
					$form = $(this),
					matchingValue
					;
				if ($('[data-validate="' + identifier + '"]').length > 0) {
					matchingValue = $('[data-validate="' + identifier + '"]').val();
				}
				else if ($('#' + identifier).length > 0) {
					matchingValue = $('#' + identifier).val();
				}
				else if ($('[name="' + identifier + '"]').length > 0) {
					matchingValue = $('[name="' + identifier + '"]').val();
				}
				else if ($('[name="' + identifier + '[]"]').length > 0) {
					matchingValue = $('[name="' + identifier + '[]"]');
				}
				return (matchingValue !== undefined)
					? (value.toString() !== matchingValue.toString())
					: false
					;
			},

			creditCard: function (cardNumber, cardTypes) {
				var
					cards = {
						visa: {
							pattern: /^4/,
							length: [16]
						},
						amex: {
							pattern: /^3[47]/,
							length: [15]
						},
						mastercard: {
							pattern: /^5[1-5]/,
							length: [16]
						},
						discover: {
							pattern: /^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)/,
							length: [16]
						},
						unionPay: {
							pattern: /^(62|88)/,
							length: [16, 17, 18, 19]
						},
						jcb: {
							pattern: /^35(2[89]|[3-8][0-9])/,
							length: [16]
						},
						maestro: {
							pattern: /^(5018|5020|5038|6304|6759|676[1-3])/,
							length: [12, 13, 14, 15, 16, 17, 18, 19]
						},
						dinersClub: {
							pattern: /^(30[0-5]|^36)/,
							length: [14]
						},
						laser: {
							pattern: /^(6304|670[69]|6771)/,
							length: [16, 17, 18, 19]
						},
						visaElectron: {
							pattern: /^(4026|417500|4508|4844|491(3|7))/,
							length: [16]
						}
					},
					valid = {},
					validCard = false,
					requiredTypes = (typeof cardTypes == 'string')
						? cardTypes.split(',')
						: false,
					unionPay,
					validation
					;

				if (typeof cardNumber !== 'string' || cardNumber.length === 0) {
					return;
				}

				// allow dashes in card
				cardNumber = cardNumber.replace(/[\-]/g, '');

				// verify card types
				if (requiredTypes) {
					$.each(requiredTypes, function (index, type) {
						// verify each card type
						validation = cards[type];
						if (validation) {
							valid = {
								length: ($.inArray(cardNumber.length, validation.length) !== -1),
								pattern: (cardNumber.search(validation.pattern) !== -1)
							};
							if (valid.length && valid.pattern) {
								validCard = true;
							}
						}
					});

					if (!validCard) {
						return false;
					}
				}

				// skip luhn for UnionPay
				unionPay = {
					number: ($.inArray(cardNumber.length, cards.unionPay.length) !== -1),
					pattern: (cardNumber.search(cards.unionPay.pattern) !== -1)
				};
				if (unionPay.number && unionPay.pattern) {
					return true;
				}

				// verify luhn, adapted from  <https://gist.github.com/2134376>
				var
					length = cardNumber.length,
					multiple = 0,
					producedValue = [
						[0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
						[0, 2, 4, 6, 8, 1, 3, 5, 7, 9]
					],
					sum = 0
					;
				while (length--) {
					sum += producedValue[multiple][parseInt(cardNumber.charAt(length), 10)];
					multiple ^= 1;
				}
				return (sum % 10 === 0 && sum > 0);
			},

			minCount: function (value, minCount) {
				if (minCount == 0) {
					return true;
				}
				if (minCount == 1) {
					return (value !== '');
				}
				return (value.split(',').length >= minCount);
			},

			exactCount: function (value, exactCount) {
				if (exactCount == 0) {
					return (value === '');
				}
				if (exactCount == 1) {
					return (value !== '' && value.search(',') === -1);
				}
				return (value.split(',').length == exactCount);
			},

			maxCount: function (value, maxCount) {
				if (maxCount == 0) {
					return false;
				}
				if (maxCount == 1) {
					return (value.search(',') === -1);
				}
				return (value.split(',').length <= maxCount);
			}
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Accordion
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.accordion = function (parameters) {
		var
			$allModules = $(this),

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			requestAnimationFrame = window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.msRequestAnimationFrame
				|| function (callback) { setTimeout(callback, 0); },

			returnedValue
			;
		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.accordion.settings, parameters)
						: $.extend({}, $.fn.accordion.settings),

					className = settings.className,
					namespace = settings.namespace,
					selector = settings.selector,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,
					moduleSelector = $allModules.selector || '',

					$module = $(this),
					$title = $module.find(selector.title),
					$content = $module.find(selector.content),

					element = this,
					instance = $module.data(moduleNamespace),
					observer,
					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing', $module);
						module.bind.events();
						if (settings.observeChanges) {
							module.observeChanges();
						}
						module.instantiate();
					},

					instantiate: function () {
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.debug('Destroying previous instance', $module);
						$module
							.off(eventNamespace)
							.removeData(moduleNamespace)
							;
					},

					refresh: function () {
						$title = $module.find(selector.title);
						$content = $module.find(selector.content);
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							observer = new MutationObserver(function (mutations) {
								module.debug('DOM tree modified, updating selector cache');
								module.refresh();
							});
							observer.observe(element, {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', observer);
						}
					},

					bind: {
						events: function () {
							module.debug('Binding delegated events');
							$module
								.on(settings.on + eventNamespace, selector.trigger, module.event.click)
								;
						}
					},

					event: {
						click: function () {
							module.toggle.call(this);
						}
					},

					toggle: function (query) {
						var
							$activeTitle = (query !== undefined)
								? (typeof query === 'number')
									? $title.eq(query)
									: $(query).closest(selector.title)
								: $(this).closest(selector.title),
							$activeContent = $activeTitle.next($content),
							isAnimating = $activeContent.hasClass(className.animating),
							isActive = $activeContent.hasClass(className.active),
							isOpen = (isActive && !isAnimating),
							isOpening = (!isActive && isAnimating)
							;
						module.debug('Toggling visibility of content', $activeTitle);
						if (isOpen || isOpening) {
							if (settings.collapsible) {
								module.close.call($activeTitle);
							}
							else {
								module.debug('Cannot close accordion content collapsing is disabled');
							}
						}
						else {
							module.open.call($activeTitle);
						}
					},

					open: function (query) {
						var
							$activeTitle = (query !== undefined)
								? (typeof query === 'number')
									? $title.eq(query)
									: $(query).closest(selector.title)
								: $(this).closest(selector.title),
							$activeContent = $activeTitle.next($content),
							isAnimating = $activeContent.hasClass(className.animating),
							isActive = $activeContent.hasClass(className.active),
							isOpen = (isActive || isAnimating)
							;
						if (isOpen) {
							module.debug('Accordion already open, skipping', $activeContent);
							return;
						}
						module.debug('Opening accordion content', $activeTitle);
						settings.onOpening.call($activeContent);
						settings.onChanging.call($activeContent);
						if (settings.exclusive) {
							module.closeOthers.call($activeTitle);
						}
						$activeTitle
							.addClass(className.active)
							;
						$activeContent
							.stop(true, true)
							.addClass(className.animating)
							;
						if (settings.animateChildren) {
							if ($.fn.transition !== undefined && $module.transition('is supported')) {
								$activeContent
									.children()
									.transition({
										animation: 'fade in',
										queue: false,
										useFailSafe: true,
										debug: settings.debug,
										verbose: settings.verbose,
										duration: settings.duration
									})
									;
							}
							else {
								$activeContent
									.children()
									.stop(true, true)
									.animate({
										opacity: 1
									}, settings.duration, module.resetOpacity)
									;
							}
						}
						$activeContent
							.slideDown(settings.duration, settings.easing, function () {
								$activeContent
									.removeClass(className.animating)
									.addClass(className.active)
									;
								module.reset.display.call(this);
								settings.onOpen.call(this);
								settings.onChange.call(this);
							})
							;
					},

					close: function (query) {
						var
							$activeTitle = (query !== undefined)
								? (typeof query === 'number')
									? $title.eq(query)
									: $(query).closest(selector.title)
								: $(this).closest(selector.title),
							$activeContent = $activeTitle.next($content),
							isAnimating = $activeContent.hasClass(className.animating),
							isActive = $activeContent.hasClass(className.active),
							isOpening = (!isActive && isAnimating),
							isClosing = (isActive && isAnimating)
							;
						if ((isActive || isOpening) && !isClosing) {
							module.debug('Closing accordion content', $activeContent);
							settings.onClosing.call($activeContent);
							settings.onChanging.call($activeContent);
							$activeTitle
								.removeClass(className.active)
								;
							$activeContent
								.stop(true, true)
								.addClass(className.animating)
								;
							if (settings.animateChildren) {
								if ($.fn.transition !== undefined && $module.transition('is supported')) {
									$activeContent
										.children()
										.transition({
											animation: 'fade out',
											queue: false,
											useFailSafe: true,
											debug: settings.debug,
											verbose: settings.verbose,
											duration: settings.duration
										})
										;
								}
								else {
									$activeContent
										.children()
										.stop(true, true)
										.animate({
											opacity: 0
										}, settings.duration, module.resetOpacity)
										;
								}
							}
							$activeContent
								.slideUp(settings.duration, settings.easing, function () {
									$activeContent
										.removeClass(className.animating)
										.removeClass(className.active)
										;
									module.reset.display.call(this);
									settings.onClose.call(this);
									settings.onChange.call(this);
								})
								;
						}
					},

					closeOthers: function (index) {
						var
							$activeTitle = (index !== undefined)
								? $title.eq(index)
								: $(this).closest(selector.title),
							$parentTitles = $activeTitle.parents(selector.content).prev(selector.title),
							$activeAccordion = $activeTitle.closest(selector.accordion),
							activeSelector = selector.title + '.' + className.active + ':visible',
							activeContent = selector.content + '.' + className.active + ':visible',
							$openTitles,
							$nestedTitles,
							$openContents
							;
						if (settings.closeNested) {
							$openTitles = $activeAccordion.find(activeSelector).not($parentTitles);
							$openContents = $openTitles.next($content);
						}
						else {
							$openTitles = $activeAccordion.find(activeSelector).not($parentTitles);
							$nestedTitles = $activeAccordion.find(activeContent).find(activeSelector).not($parentTitles);
							$openTitles = $openTitles.not($nestedTitles);
							$openContents = $openTitles.next($content);
						}
						if (($openTitles.length > 0)) {
							module.debug('Exclusive enabled, closing other content', $openTitles);
							$openTitles
								.removeClass(className.active)
								;
							$openContents
								.removeClass(className.animating)
								.stop(true, true)
								;
							if (settings.animateChildren) {
								if ($.fn.transition !== undefined && $module.transition('is supported')) {
									$openContents
										.children()
										.transition({
											animation: 'fade out',
											useFailSafe: true,
											debug: settings.debug,
											verbose: settings.verbose,
											duration: settings.duration
										})
										;
								}
								else {
									$openContents
										.children()
										.stop(true, true)
										.animate({
											opacity: 0
										}, settings.duration, module.resetOpacity)
										;
								}
							}
							$openContents
								.slideUp(settings.duration, settings.easing, function () {
									$(this).removeClass(className.active);
									module.reset.display.call(this);
								})
								;
						}
					},

					reset: {

						display: function () {
							module.verbose('Removing inline display from element', this);
							$(this).css('display', '');
							if ($(this).attr('style') === '') {
								$(this)
									.attr('style', '')
									.removeAttr('style')
									;
							}
						},

						opacity: function () {
							module.verbose('Removing inline opacity from element', this);
							$(this).css('opacity', '');
							if ($(this).attr('style') === '') {
								$(this)
									.attr('style', '')
									.removeAttr('style')
									;
							}
						},

					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						module.debug('Changing internal', name, value);
						if (value !== undefined) {
							if ($.isPlainObject(name)) {
								$.extend(true, module, name);
							}
							else {
								module[name] = value;
							}
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};
				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;
		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.accordion.settings = {

		name: 'Accordion',
		namespace: 'accordion',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		on: 'click', // event on title that opens accordion

		observeChanges: true,  // whether accordion should automatically refresh on DOM insertion

		exclusive: true,  // whether a single accordion content panel should be open at once
		collapsible: true,  // whether accordion content can be closed
		closeNested: false, // whether nested content should be closed when a panel is closed
		animateChildren: true,  // whether children opacity should be animated

		duration: 350, // duration of animation
		easing: 'easeOutQuad', // easing equation for animation

		onOpening: function () { }, // callback before open animation
		onClosing: function () { }, // callback before closing animation
		onChanging: function () { }, // callback before closing or opening animation

		onOpen: function () { }, // callback after open animation
		onClose: function () { }, // callback after closing animation
		onChange: function () { }, // callback after closing or opening animation

		error: {
			method: 'The method you called is not defined'
		},

		className: {
			active: 'active',
			animating: 'animating'
		},

		selector: {
			accordion: '.accordion',
			title: '.title',
			trigger: '.title',
			content: '.content'
		}

	};

	// Adds easing
	$.extend($.easing, {
		easeOutQuad: function (x, t, b, c, d) {
			return -c * (t /= d) * (t - 2) + b;
		}
	});

})(jQuery, window, document);


/*!
 * # Semantic UI 2.5.0 - Checkbox
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.checkbox = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = $.extend(true, {}, $.fn.checkbox.settings, parameters),

					className = settings.className,
					namespace = settings.namespace,
					selector = settings.selector,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$label = $(this).children(selector.label),
					$input = $(this).children(selector.input),
					input = $input[0],

					initialLoad = false,
					shortcutPressed = false,
					instance = $module.data(moduleNamespace),

					observer,
					element = this,
					module
					;

				module = {

					initialize: function () {
						module.verbose('Initializing checkbox', settings);

						module.create.label();
						module.bind.events();

						module.set.tabbable();
						module.hide.input();

						module.observeChanges();
						module.instantiate();
						module.setup();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying module');
						module.unbind.events();
						module.show.input();
						$module.removeData(moduleNamespace);
					},

					fix: {
						reference: function () {
							if ($module.is(selector.input)) {
								module.debug('Behavior called on <input> adjusting invoked element');
								$module = $module.closest(selector.checkbox);
								module.refresh();
							}
						}
					},

					setup: function () {
						module.set.initialLoad();
						if (module.is.indeterminate()) {
							module.debug('Initial value is indeterminate');
							module.indeterminate();
						}
						else if (module.is.checked()) {
							module.debug('Initial value is checked');
							module.check();
						}
						else {
							module.debug('Initial value is unchecked');
							module.uncheck();
						}
						module.remove.initialLoad();
					},

					refresh: function () {
						$label = $module.children(selector.label);
						$input = $module.children(selector.input);
						input = $input[0];
					},

					hide: {
						input: function () {
							module.verbose('Modifying <input> z-index to be unselectable');
							$input.addClass(className.hidden);
						}
					},
					show: {
						input: function () {
							module.verbose('Modifying <input> z-index to be selectable');
							$input.removeClass(className.hidden);
						}
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							observer = new MutationObserver(function (mutations) {
								module.debug('DOM tree modified, updating selector cache');
								module.refresh();
							});
							observer.observe(element, {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', observer);
						}
					},

					attachEvents: function (selector, event) {
						var
							$element = $(selector)
							;
						event = $.isFunction(module[event])
							? module[event]
							: module.toggle
							;
						if ($element.length > 0) {
							module.debug('Attaching checkbox events to element', selector, event);
							$element
								.on('click' + eventNamespace, event)
								;
						}
						else {
							module.error(error.notFound);
						}
					},

					event: {
						click: function (event) {
							var
								$target = $(event.target)
								;
							if ($target.is(selector.input)) {
								module.verbose('Using default check action on initialized checkbox');
								return;
							}
							if ($target.is(selector.link)) {
								module.debug('Clicking link inside checkbox, skipping toggle');
								return;
							}
							module.toggle();
							$input.focus();
							event.preventDefault();
						},
						keydown: function (event) {
							var
								key = event.which,
								keyCode = {
									enter: 13,
									space: 32,
									escape: 27
								}
								;
							if (key == keyCode.escape) {
								module.verbose('Escape key pressed blurring field');
								$input.blur();
								shortcutPressed = true;
							}
							else if (!event.ctrlKey && (key == keyCode.space || key == keyCode.enter)) {
								module.verbose('Enter/space key pressed, toggling checkbox');
								module.toggle();
								shortcutPressed = true;
							}
							else {
								shortcutPressed = false;
							}
						},
						keyup: function (event) {
							if (shortcutPressed) {
								event.preventDefault();
							}
						}
					},

					check: function () {
						if (!module.should.allowCheck()) {
							return;
						}
						module.debug('Checking checkbox', $input);
						module.set.checked();
						if (!module.should.ignoreCallbacks()) {
							settings.onChecked.call(input);
							settings.onChange.call(input);
						}
					},

					uncheck: function () {
						if (!module.should.allowUncheck()) {
							return;
						}
						module.debug('Unchecking checkbox');
						module.set.unchecked();
						if (!module.should.ignoreCallbacks()) {
							settings.onUnchecked.call(input);
							settings.onChange.call(input);
						}
					},

					indeterminate: function () {
						if (module.should.allowIndeterminate()) {
							module.debug('Checkbox is already indeterminate');
							return;
						}
						module.debug('Making checkbox indeterminate');
						module.set.indeterminate();
						if (!module.should.ignoreCallbacks()) {
							settings.onIndeterminate.call(input);
							settings.onChange.call(input);
						}
					},

					determinate: function () {
						if (module.should.allowDeterminate()) {
							module.debug('Checkbox is already determinate');
							return;
						}
						module.debug('Making checkbox determinate');
						module.set.determinate();
						if (!module.should.ignoreCallbacks()) {
							settings.onDeterminate.call(input);
							settings.onChange.call(input);
						}
					},

					enable: function () {
						if (module.is.enabled()) {
							module.debug('Checkbox is already enabled');
							return;
						}
						module.debug('Enabling checkbox');
						module.set.enabled();
						settings.onEnable.call(input);
						// preserve legacy callbacks
						settings.onEnabled.call(input);
					},

					disable: function () {
						if (module.is.disabled()) {
							module.debug('Checkbox is already disabled');
							return;
						}
						module.debug('Disabling checkbox');
						module.set.disabled();
						settings.onDisable.call(input);
						// preserve legacy callbacks
						settings.onDisabled.call(input);
					},

					get: {
						radios: function () {
							var
								name = module.get.name()
								;
							return $('input[name="' + name + '"]').closest(selector.checkbox);
						},
						otherRadios: function () {
							return module.get.radios().not($module);
						},
						name: function () {
							return $input.attr('name');
						}
					},

					is: {
						initialLoad: function () {
							return initialLoad;
						},
						radio: function () {
							return ($input.hasClass(className.radio) || $input.attr('type') == 'radio');
						},
						indeterminate: function () {
							return $input.prop('indeterminate') !== undefined && $input.prop('indeterminate');
						},
						checked: function () {
							return $input.prop('checked') !== undefined && $input.prop('checked');
						},
						disabled: function () {
							return $input.prop('disabled') !== undefined && $input.prop('disabled');
						},
						enabled: function () {
							return !module.is.disabled();
						},
						determinate: function () {
							return !module.is.indeterminate();
						},
						unchecked: function () {
							return !module.is.checked();
						}
					},

					should: {
						allowCheck: function () {
							if (module.is.determinate() && module.is.checked() && !module.should.forceCallbacks()) {
								module.debug('Should not allow check, checkbox is already checked');
								return false;
							}
							if (settings.beforeChecked.apply(input) === false) {
								module.debug('Should not allow check, beforeChecked cancelled');
								return false;
							}
							return true;
						},
						allowUncheck: function () {
							if (module.is.determinate() && module.is.unchecked() && !module.should.forceCallbacks()) {
								module.debug('Should not allow uncheck, checkbox is already unchecked');
								return false;
							}
							if (settings.beforeUnchecked.apply(input) === false) {
								module.debug('Should not allow uncheck, beforeUnchecked cancelled');
								return false;
							}
							return true;
						},
						allowIndeterminate: function () {
							if (module.is.indeterminate() && !module.should.forceCallbacks()) {
								module.debug('Should not allow indeterminate, checkbox is already indeterminate');
								return false;
							}
							if (settings.beforeIndeterminate.apply(input) === false) {
								module.debug('Should not allow indeterminate, beforeIndeterminate cancelled');
								return false;
							}
							return true;
						},
						allowDeterminate: function () {
							if (module.is.determinate() && !module.should.forceCallbacks()) {
								module.debug('Should not allow determinate, checkbox is already determinate');
								return false;
							}
							if (settings.beforeDeterminate.apply(input) === false) {
								module.debug('Should not allow determinate, beforeDeterminate cancelled');
								return false;
							}
							return true;
						},
						forceCallbacks: function () {
							return (module.is.initialLoad() && settings.fireOnInit);
						},
						ignoreCallbacks: function () {
							return (initialLoad && !settings.fireOnInit);
						}
					},

					can: {
						change: function () {
							return !($module.hasClass(className.disabled) || $module.hasClass(className.readOnly) || $input.prop('disabled') || $input.prop('readonly'));
						},
						uncheck: function () {
							return (typeof settings.uncheckable === 'boolean')
								? settings.uncheckable
								: !module.is.radio()
								;
						}
					},

					set: {
						initialLoad: function () {
							initialLoad = true;
						},
						checked: function () {
							module.verbose('Setting class to checked');
							$module
								.removeClass(className.indeterminate)
								.addClass(className.checked)
								;
							if (module.is.radio()) {
								module.uncheckOthers();
							}
							if (!module.is.indeterminate() && module.is.checked()) {
								module.debug('Input is already checked, skipping input property change');
								return;
							}
							module.verbose('Setting state to checked', input);
							$input
								.prop('indeterminate', false)
								.prop('checked', true)
								;
							module.trigger.change();
						},
						unchecked: function () {
							module.verbose('Removing checked class');
							$module
								.removeClass(className.indeterminate)
								.removeClass(className.checked)
								;
							if (!module.is.indeterminate() && module.is.unchecked()) {
								module.debug('Input is already unchecked');
								return;
							}
							module.debug('Setting state to unchecked');
							$input
								.prop('indeterminate', false)
								.prop('checked', false)
								;
							module.trigger.change();
						},
						indeterminate: function () {
							module.verbose('Setting class to indeterminate');
							$module
								.addClass(className.indeterminate)
								;
							if (module.is.indeterminate()) {
								module.debug('Input is already indeterminate, skipping input property change');
								return;
							}
							module.debug('Setting state to indeterminate');
							$input
								.prop('indeterminate', true)
								;
							module.trigger.change();
						},
						determinate: function () {
							module.verbose('Removing indeterminate class');
							$module
								.removeClass(className.indeterminate)
								;
							if (module.is.determinate()) {
								module.debug('Input is already determinate, skipping input property change');
								return;
							}
							module.debug('Setting state to determinate');
							$input
								.prop('indeterminate', false)
								;
						},
						disabled: function () {
							module.verbose('Setting class to disabled');
							$module
								.addClass(className.disabled)
								;
							if (module.is.disabled()) {
								module.debug('Input is already disabled, skipping input property change');
								return;
							}
							module.debug('Setting state to disabled');
							$input
								.prop('disabled', 'disabled')
								;
							module.trigger.change();
						},
						enabled: function () {
							module.verbose('Removing disabled class');
							$module.removeClass(className.disabled);
							if (module.is.enabled()) {
								module.debug('Input is already enabled, skipping input property change');
								return;
							}
							module.debug('Setting state to enabled');
							$input
								.prop('disabled', false)
								;
							module.trigger.change();
						},
						tabbable: function () {
							module.verbose('Adding tabindex to checkbox');
							if ($input.attr('tabindex') === undefined) {
								$input.attr('tabindex', 0);
							}
						}
					},

					remove: {
						initialLoad: function () {
							initialLoad = false;
						}
					},

					trigger: {
						change: function () {
							var
								events = document.createEvent('HTMLEvents'),
								inputElement = $input[0]
								;
							if (inputElement) {
								module.verbose('Triggering native change event');
								events.initEvent('change', true, false);
								inputElement.dispatchEvent(events);
							}
						}
					},


					create: {
						label: function () {
							if ($input.prevAll(selector.label).length > 0) {
								$input.prev(selector.label).detach().insertAfter($input);
								module.debug('Moving existing label', $label);
							}
							else if (!module.has.label()) {
								$label = $('<label>').insertAfter($input);
								module.debug('Creating label', $label);
							}
						}
					},

					has: {
						label: function () {
							return ($label.length > 0);
						}
					},

					bind: {
						events: function () {
							module.verbose('Attaching checkbox events');
							$module
								.on('click' + eventNamespace, module.event.click)
								.on('keydown' + eventNamespace, selector.input, module.event.keydown)
								.on('keyup' + eventNamespace, selector.input, module.event.keyup)
								;
						}
					},

					unbind: {
						events: function () {
							module.debug('Removing events');
							$module
								.off(eventNamespace)
								;
						}
					},

					uncheckOthers: function () {
						var
							$radios = module.get.otherRadios()
							;
						module.debug('Unchecking other radios', $radios);
						$radios.removeClass(className.checked);
					},

					toggle: function () {
						if (!module.can.change()) {
							if (!module.is.radio()) {
								module.debug('Checkbox is read-only or disabled, ignoring toggle');
							}
							return;
						}
						if (module.is.indeterminate() || module.is.unchecked()) {
							module.debug('Currently unchecked');
							module.check();
						}
						else if (module.is.checked() && module.can.uncheck()) {
							module.debug('Currently checked');
							module.uncheck();
						}
					},
					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.checkbox.settings = {

		name: 'Checkbox',
		namespace: 'checkbox',

		silent: false,
		debug: false,
		verbose: true,
		performance: true,

		// delegated event context
		uncheckable: 'auto',
		fireOnInit: false,

		onChange: function () { },

		beforeChecked: function () { },
		beforeUnchecked: function () { },
		beforeDeterminate: function () { },
		beforeIndeterminate: function () { },

		onChecked: function () { },
		onUnchecked: function () { },

		onDeterminate: function () { },
		onIndeterminate: function () { },

		onEnable: function () { },
		onDisable: function () { },

		// preserve misspelled callbacks (will be removed in 3.0)
		onEnabled: function () { },
		onDisabled: function () { },

		className: {
			checked: 'checked',
			indeterminate: 'indeterminate',
			disabled: 'disabled',
			hidden: 'hidden',
			radio: 'radio',
			readOnly: 'read-only'
		},

		error: {
			method: 'The method you called is not defined'
		},

		selector: {
			checkbox: '.ui.checkbox',
			label: 'label, .box',
			input: 'input[type="checkbox"], input[type="radio"]',
			link: 'a[href]'
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Dimmer
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.dimmer = function (parameters) {
		var
			$allModules = $(this),

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.dimmer.settings, parameters)
						: $.extend({}, $.fn.dimmer.settings),

					selector = settings.selector,
					namespace = settings.namespace,
					className = settings.className,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,
					moduleSelector = $allModules.selector || '',

					clickEvent = ('ontouchstart' in document.documentElement)
						? 'touchstart'
						: 'click',

					$module = $(this),
					$dimmer,
					$dimmable,

					element = this,
					instance = $module.data(moduleNamespace),
					module
					;

				module = {

					preinitialize: function () {
						if (module.is.dimmer()) {

							$dimmable = $module.parent();
							$dimmer = $module;
						}
						else {
							$dimmable = $module;
							if (module.has.dimmer()) {
								if (settings.dimmerName) {
									$dimmer = $dimmable.find(selector.dimmer).filter('.' + settings.dimmerName);
								}
								else {
									$dimmer = $dimmable.find(selector.dimmer);
								}
							}
							else {
								$dimmer = module.create();
							}
						}
					},

					initialize: function () {
						module.debug('Initializing dimmer', settings);

						module.bind.events();
						module.set.dimmable();
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous module', $dimmer);
						module.unbind.events();
						module.remove.variation();
						$dimmable
							.off(eventNamespace)
							;
					},

					bind: {
						events: function () {
							if (settings.on == 'hover') {
								$dimmable
									.on('mouseenter' + eventNamespace, module.show)
									.on('mouseleave' + eventNamespace, module.hide)
									;
							}
							else if (settings.on == 'click') {
								$dimmable
									.on(clickEvent + eventNamespace, module.toggle)
									;
							}
							if (module.is.page()) {
								module.debug('Setting as a page dimmer', $dimmable);
								module.set.pageDimmer();
							}

							if (module.is.closable()) {
								module.verbose('Adding dimmer close event', $dimmer);
								$dimmable
									.on(clickEvent + eventNamespace, selector.dimmer, module.event.click)
									;
							}
						}
					},

					unbind: {
						events: function () {
							$module
								.removeData(moduleNamespace)
								;
							$dimmable
								.off(eventNamespace)
								;
						}
					},

					event: {
						click: function (event) {
							module.verbose('Determining if event occurred on dimmer', event);
							if ($dimmer.find(event.target).length === 0 || $(event.target).is(selector.content)) {
								module.hide();
								event.stopImmediatePropagation();
							}
						},
					},

					addContent: function (element) {
						var
							$content = $(element)
							;
						module.debug('Add content to dimmer', $content);
						if ($content.parent()[0] !== $dimmer[0]) {
							$content.detach().appendTo($dimmer);
						}
					},

					create: function () {
						var
							$element = $(settings.template.dimmer())
							;
						if (settings.dimmerName) {
							module.debug('Creating named dimmer', settings.dimmerName);
							$element.addClass(settings.dimmerName);
						}
						$element
							.appendTo($dimmable)
							;
						return $element;
					},

					show: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						module.debug('Showing dimmer', $dimmer, settings);
						module.set.variation();
						if ((!module.is.dimmed() || module.is.animating()) && module.is.enabled()) {
							module.animate.show(callback);
							settings.onShow.call(element);
							settings.onChange.call(element);
						}
						else {
							module.debug('Dimmer is already shown or disabled');
						}
					},

					hide: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.dimmed() || module.is.animating()) {
							module.debug('Hiding dimmer', $dimmer);
							module.animate.hide(callback);
							settings.onHide.call(element);
							settings.onChange.call(element);
						}
						else {
							module.debug('Dimmer is not visible');
						}
					},

					toggle: function () {
						module.verbose('Toggling dimmer visibility', $dimmer);
						if (!module.is.dimmed()) {
							module.show();
						}
						else {
							module.hide();
						}
					},

					animate: {
						show: function (callback) {
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if (settings.useCSS && $.fn.transition !== undefined && $dimmer.transition('is supported')) {
								if (settings.useFlex) {
									module.debug('Using flex dimmer');
									module.remove.legacy();
								}
								else {
									module.debug('Using legacy non-flex dimmer');
									module.set.legacy();
								}
								if (settings.opacity !== 'auto') {
									module.set.opacity();
								}
								$dimmer
									.transition({
										displayType: settings.useFlex
											? 'flex'
											: 'block',
										animation: settings.transition + ' in',
										queue: false,
										duration: module.get.duration(),
										useFailSafe: true,
										onStart: function () {
											module.set.dimmed();
										},
										onComplete: function () {
											module.set.active();
											callback();
										}
									})
									;
							}
							else {
								module.verbose('Showing dimmer animation with javascript');
								module.set.dimmed();
								if (settings.opacity == 'auto') {
									settings.opacity = 0.8;
								}
								$dimmer
									.stop()
									.css({
										opacity: 0,
										width: '100%',
										height: '100%'
									})
									.fadeTo(module.get.duration(), settings.opacity, function () {
										$dimmer.removeAttr('style');
										module.set.active();
										callback();
									})
									;
							}
						},
						hide: function (callback) {
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if (settings.useCSS && $.fn.transition !== undefined && $dimmer.transition('is supported')) {
								module.verbose('Hiding dimmer with css');
								$dimmer
									.transition({
										displayType: settings.useFlex
											? 'flex'
											: 'block',
										animation: settings.transition + ' out',
										queue: false,
										duration: module.get.duration(),
										useFailSafe: true,
										onStart: function () {
											module.remove.dimmed();
										},
										onComplete: function () {
											module.remove.variation();
											module.remove.active();
											callback();
										}
									})
									;
							}
							else {
								module.verbose('Hiding dimmer with javascript');
								module.remove.dimmed();
								$dimmer
									.stop()
									.fadeOut(module.get.duration(), function () {
										module.remove.active();
										$dimmer.removeAttr('style');
										callback();
									})
									;
							}
						}
					},

					get: {
						dimmer: function () {
							return $dimmer;
						},
						duration: function () {
							if (typeof settings.duration == 'object') {
								if (module.is.active()) {
									return settings.duration.hide;
								}
								else {
									return settings.duration.show;
								}
							}
							return settings.duration;
						}
					},

					has: {
						dimmer: function () {
							if (settings.dimmerName) {
								return ($module.find(selector.dimmer).filter('.' + settings.dimmerName).length > 0);
							}
							else {
								return ($module.find(selector.dimmer).length > 0);
							}
						}
					},

					is: {
						active: function () {
							return $dimmer.hasClass(className.active);
						},
						animating: function () {
							return ($dimmer.is(':animated') || $dimmer.hasClass(className.animating));
						},
						closable: function () {
							if (settings.closable == 'auto') {
								if (settings.on == 'hover') {
									return false;
								}
								return true;
							}
							return settings.closable;
						},
						dimmer: function () {
							return $module.hasClass(className.dimmer);
						},
						dimmable: function () {
							return $module.hasClass(className.dimmable);
						},
						dimmed: function () {
							return $dimmable.hasClass(className.dimmed);
						},
						disabled: function () {
							return $dimmable.hasClass(className.disabled);
						},
						enabled: function () {
							return !module.is.disabled();
						},
						page: function () {
							return $dimmable.is('body');
						},
						pageDimmer: function () {
							return $dimmer.hasClass(className.pageDimmer);
						}
					},

					can: {
						show: function () {
							return !$dimmer.hasClass(className.disabled);
						}
					},

					set: {
						opacity: function (opacity) {
							var
								color = $dimmer.css('background-color'),
								colorArray = color.split(','),
								isRGB = (colorArray && colorArray.length == 3),
								isRGBA = (colorArray && colorArray.length == 4)
								;
							opacity = settings.opacity === 0 ? 0 : settings.opacity || opacity;
							if (isRGB || isRGBA) {
								colorArray[3] = opacity + ')';
								color = colorArray.join(',');
							}
							else {
								color = 'rgba(0, 0, 0, ' + opacity + ')';
							}
							module.debug('Setting opacity to', opacity);
							$dimmer.css('background-color', color);
						},
						legacy: function () {
							$dimmer.addClass(className.legacy);
						},
						active: function () {
							$dimmer.addClass(className.active);
						},
						dimmable: function () {
							$dimmable.addClass(className.dimmable);
						},
						dimmed: function () {
							$dimmable.addClass(className.dimmed);
						},
						pageDimmer: function () {
							$dimmer.addClass(className.pageDimmer);
						},
						disabled: function () {
							$dimmer.addClass(className.disabled);
						},
						variation: function (variation) {
							variation = variation || settings.variation;
							if (variation) {
								$dimmer.addClass(variation);
							}
						}
					},

					remove: {
						active: function () {
							$dimmer
								.removeClass(className.active)
								;
						},
						legacy: function () {
							$dimmer.removeClass(className.legacy);
						},
						dimmed: function () {
							$dimmable.removeClass(className.dimmed);
						},
						disabled: function () {
							$dimmer.removeClass(className.disabled);
						},
						variation: function (variation) {
							variation = variation || settings.variation;
							if (variation) {
								$dimmer.removeClass(variation);
							}
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				module.preinitialize();

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.dimmer.settings = {

		name: 'Dimmer',
		namespace: 'dimmer',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		// whether should use flex layout
		useFlex: true,

		// name to distinguish between multiple dimmers in context
		dimmerName: false,

		// whether to add a variation type
		variation: false,

		// whether to bind close events
		closable: 'auto',

		// whether to use css animations
		useCSS: true,

		// css animation to use
		transition: 'fade',

		// event to bind to
		on: false,

		// overriding opacity value
		opacity: 'auto',

		// transition durations
		duration: {
			show: 500,
			hide: 500
		},

		onChange: function () { },
		onShow: function () { },
		onHide: function () { },

		error: {
			method: 'The method you called is not defined.'
		},

		className: {
			active: 'active',
			animating: 'animating',
			dimmable: 'dimmable',
			dimmed: 'dimmed',
			dimmer: 'dimmer',
			disabled: 'disabled',
			hide: 'hide',
			legacy: 'legacy',
			pageDimmer: 'page',
			show: 'show'
		},

		selector: {
			dimmer: '> .ui.dimmer',
			content: '.ui.dimmer > .content, .ui.dimmer > .content > .center'
		},

		template: {
			dimmer: function () {
				return $('<div />').attr('class', 'ui dimmer');
			}
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Dropdown
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.dropdown = function (parameters) {
		var
			$allModules = $(this),
			$document = $(document),

			moduleSelector = $allModules.selector || '',

			hasTouch = ('ontouchstart' in document.documentElement),
			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;

		$allModules
			.each(function (elementIndex) {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.dropdown.settings, parameters)
						: $.extend({}, $.fn.dropdown.settings),

					className = settings.className,
					message = settings.message,
					fields = settings.fields,
					keys = settings.keys,
					metadata = settings.metadata,
					namespace = settings.namespace,
					regExp = settings.regExp,
					selector = settings.selector,
					error = settings.error,
					templates = settings.templates,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$context = $(settings.context),
					$text = $module.find(selector.text),
					$search = $module.find(selector.search),
					$sizer = $module.find(selector.sizer),
					$input = $module.find(selector.input),
					$icon = $module.find(selector.icon),

					$combo = ($module.prev().find(selector.text).length > 0)
						? $module.prev().find(selector.text)
						: $module.prev(),

					$menu = $module.children(selector.menu),
					$item = $menu.find(selector.item),

					activated = false,
					itemActivated = false,
					internalChange = false,
					element = this,
					instance = $module.data(moduleNamespace),

					initialLoad,
					pageLostFocus,
					willRefocus,
					elementNamespace,
					id,
					selectObserver,
					menuObserver,
					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing dropdown', settings);

						if (module.is.alreadySetup()) {
							module.setup.reference();
						}
						else {

							module.setup.layout();

							if (settings.values) {
								module.change.values(settings.values);
							}

							module.refreshData();

							module.save.defaults();
							module.restore.selected();

							module.create.id();
							module.bind.events();

							module.observeChanges();
							module.instantiate();
						}

					},

					instantiate: function () {
						module.verbose('Storing instance of dropdown', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous dropdown', $module);
						module.remove.tabbable();
						$module
							.off(eventNamespace)
							.removeData(moduleNamespace)
							;
						$menu
							.off(eventNamespace)
							;
						$document
							.off(elementNamespace)
							;
						module.disconnect.menuObserver();
						module.disconnect.selectObserver();
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							selectObserver = new MutationObserver(module.event.select.mutation);
							menuObserver = new MutationObserver(module.event.menu.mutation);
							module.debug('Setting up mutation observer', selectObserver, menuObserver);
							module.observe.select();
							module.observe.menu();
						}
					},

					disconnect: {
						menuObserver: function () {
							if (menuObserver) {
								menuObserver.disconnect();
							}
						},
						selectObserver: function () {
							if (selectObserver) {
								selectObserver.disconnect();
							}
						}
					},
					observe: {
						select: function () {
							if (module.has.input()) {
								selectObserver.observe($module[0], {
									childList: true,
									subtree: true
								});
							}
						},
						menu: function () {
							if (module.has.menu()) {
								menuObserver.observe($menu[0], {
									childList: true,
									subtree: true
								});
							}
						}
					},

					create: {
						id: function () {
							id = (Math.random().toString(16) + '000000000').substr(2, 8);
							elementNamespace = '.' + id;
							module.verbose('Creating unique id for element', id);
						},
						userChoice: function (values) {
							var
								$userChoices,
								$userChoice,
								isUserValue,
								html
								;
							values = values || module.get.userValues();
							if (!values) {
								return false;
							}
							values = $.isArray(values)
								? values
								: [values]
								;
							$.each(values, function (index, value) {
								if (module.get.item(value) === false) {
									html = settings.templates.addition(module.add.variables(message.addResult, value));
									$userChoice = $('<div />')
										.html(html)
										.attr('data-' + metadata.value, value)
										.attr('data-' + metadata.text, value)
										.addClass(className.addition)
										.addClass(className.item)
										;
									if (settings.hideAdditions) {
										$userChoice.addClass(className.hidden);
									}
									$userChoices = ($userChoices === undefined)
										? $userChoice
										: $userChoices.add($userChoice)
										;
									module.verbose('Creating user choices for value', value, $userChoice);
								}
							});
							return $userChoices;
						},
						userLabels: function (value) {
							var
								userValues = module.get.userValues()
								;
							if (userValues) {
								module.debug('Adding user labels', userValues);
								$.each(userValues, function (index, value) {
									module.verbose('Adding custom user value');
									module.add.label(value, value);
								});
							}
						},
						menu: function () {
							$menu = $('<div />')
								.addClass(className.menu)
								.appendTo($module)
								;
						},
						sizer: function () {
							$sizer = $('<span />')
								.addClass(className.sizer)
								.insertAfter($search)
								;
						}
					},

					search: function (query) {
						query = (query !== undefined)
							? query
							: module.get.query()
							;
						module.verbose('Searching for query', query);
						if (module.has.minCharacters(query)) {
							module.filter(query);
						}
						else {
							module.hide();
						}
					},

					select: {
						firstUnfiltered: function () {
							module.verbose('Selecting first non-filtered element');
							module.remove.selectedItem();
							$item
								.not(selector.unselectable)
								.not(selector.addition + selector.hidden)
								.eq(0)
								.addClass(className.selected)
								;
						},
						nextAvailable: function ($selected) {
							$selected = $selected.eq(0);
							var
								$nextAvailable = $selected.nextAll(selector.item).not(selector.unselectable).eq(0),
								$prevAvailable = $selected.prevAll(selector.item).not(selector.unselectable).eq(0),
								hasNext = ($nextAvailable.length > 0)
								;
							if (hasNext) {
								module.verbose('Moving selection to', $nextAvailable);
								$nextAvailable.addClass(className.selected);
							}
							else {
								module.verbose('Moving selection to', $prevAvailable);
								$prevAvailable.addClass(className.selected);
							}
						}
					},

					setup: {
						api: function () {
							var
								apiSettings = {
									debug: settings.debug,
									urlData: {
										value: module.get.value(),
										query: module.get.query()
									},
									on: false
								}
								;
							module.verbose('First request, initializing API');
							$module
								.api(apiSettings)
								;
						},
						layout: function () {
							if ($module.is('select')) {
								module.setup.select();
								module.setup.returnedObject();
							}
							if (!module.has.menu()) {
								module.create.menu();
							}
							if (module.is.search() && !module.has.search()) {
								module.verbose('Adding search input');
								$search = $('<input />')
									.addClass(className.search)
									.prop('autocomplete', 'off')
									.insertBefore($text)
									;
							}
							if (module.is.multiple() && module.is.searchSelection() && !module.has.sizer()) {
								module.create.sizer();
							}
							if (settings.allowTab) {
								module.set.tabbable();
							}
						},
						select: function () {
							var
								selectValues = module.get.selectValues()
								;
							module.debug('Dropdown initialized on a select', selectValues);
							if ($module.is('select')) {
								$input = $module;
							}
							// see if select is placed correctly already
							if ($input.parent(selector.dropdown).length > 0) {
								module.debug('UI dropdown already exists. Creating dropdown menu only');
								$module = $input.closest(selector.dropdown);
								if (!module.has.menu()) {
									module.create.menu();
								}
								$menu = $module.children(selector.menu);
								module.setup.menu(selectValues);
							}
							else {
								module.debug('Creating entire dropdown from select');
								$module = $('<div />')
									.attr('class', $input.attr('class'))
									.addClass(className.selection)
									.addClass(className.dropdown)
									.html(templates.dropdown(selectValues))
									.insertBefore($input)
									;
								if ($input.hasClass(className.multiple) && $input.prop('multiple') === false) {
									module.error(error.missingMultiple);
									$input.prop('multiple', true);
								}
								if ($input.is('[multiple]')) {
									module.set.multiple();
								}
								if ($input.prop('disabled')) {
									module.debug('Disabling dropdown');
									$module.addClass(className.disabled);
								}
								$input
									.removeAttr('class')
									.detach()
									.prependTo($module)
									;
							}
							module.refresh();
						},
						menu: function (values) {
							$menu.html(templates.menu(values, fields));
							$item = $menu.find(selector.item);
						},
						reference: function () {
							module.debug('Dropdown behavior was called on select, replacing with closest dropdown');
							// replace module reference
							$module = $module.parent(selector.dropdown);
							instance = $module.data(moduleNamespace);
							element = $module.get(0);
							module.refresh();
							module.setup.returnedObject();
						},
						returnedObject: function () {
							var
								$firstModules = $allModules.slice(0, elementIndex),
								$lastModules = $allModules.slice(elementIndex + 1)
								;
							// adjust all modules to use correct reference
							$allModules = $firstModules.add($module).add($lastModules);
						}
					},

					refresh: function () {
						module.refreshSelectors();
						module.refreshData();
					},

					refreshItems: function () {
						$item = $menu.find(selector.item);
					},

					refreshSelectors: function () {
						module.verbose('Refreshing selector cache');
						$text = $module.find(selector.text);
						$search = $module.find(selector.search);
						$input = $module.find(selector.input);
						$icon = $module.find(selector.icon);
						$combo = ($module.prev().find(selector.text).length > 0)
							? $module.prev().find(selector.text)
							: $module.prev()
							;
						$menu = $module.children(selector.menu);
						$item = $menu.find(selector.item);
					},

					refreshData: function () {
						module.verbose('Refreshing cached metadata');
						$item
							.removeData(metadata.text)
							.removeData(metadata.value)
							;
					},

					clearData: function () {
						module.verbose('Clearing metadata');
						$item
							.removeData(metadata.text)
							.removeData(metadata.value)
							;
						$module
							.removeData(metadata.defaultText)
							.removeData(metadata.defaultValue)
							.removeData(metadata.placeholderText)
							;
					},

					toggle: function () {
						module.verbose('Toggling menu visibility');
						if (!module.is.active()) {
							module.show();
						}
						else {
							module.hide();
						}
					},

					show: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (!module.can.show() && module.is.remote()) {
							module.debug('No API results retrieved, searching before show');
							module.queryRemote(module.get.query(), module.show);
						}
						if (module.can.show() && !module.is.active()) {
							module.debug('Showing dropdown');
							if (module.has.message() && !(module.has.maxSelections() || module.has.allResultsFiltered())) {
								module.remove.message();
							}
							if (module.is.allFiltered()) {
								return true;
							}
							if (settings.onShow.call(element) !== false) {
								module.animate.show(function () {
									if (module.can.click()) {
										module.bind.intent();
									}
									if (module.has.menuSearch()) {
										module.focusSearch();
									}
									module.set.visible();
									callback.call(element);
								});
							}
						}
					},

					hide: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.active() && !module.is.animatingOutward()) {
							module.debug('Hiding dropdown');
							if (settings.onHide.call(element) !== false) {
								module.animate.hide(function () {
									module.remove.visible();
									callback.call(element);
								});
							}
						}
					},

					hideOthers: function () {
						module.verbose('Finding other dropdowns to hide');
						$allModules
							.not($module)
							.has(selector.menu + '.' + className.visible)
							.dropdown('hide')
							;
					},

					hideMenu: function () {
						module.verbose('Hiding menu  instantaneously');
						module.remove.active();
						module.remove.visible();
						$menu.transition('hide');
					},

					hideSubMenus: function () {
						var
							$subMenus = $menu.children(selector.item).find(selector.menu)
							;
						module.verbose('Hiding sub menus', $subMenus);
						$subMenus.transition('hide');
					},

					bind: {
						events: function () {
							if (hasTouch) {
								module.bind.touchEvents();
							}
							module.bind.keyboardEvents();
							module.bind.inputEvents();
							module.bind.mouseEvents();
						},
						touchEvents: function () {
							module.debug('Touch device detected binding additional touch events');
							if (module.is.searchSelection()) {
								// do nothing special yet
							}
							else if (module.is.single()) {
								$module
									.on('touchstart' + eventNamespace, module.event.test.toggle)
									;
							}
							$menu
								.on('touchstart' + eventNamespace, selector.item, module.event.item.mouseenter)
								;
						},
						keyboardEvents: function () {
							module.verbose('Binding keyboard events');
							$module
								.on('keydown' + eventNamespace, module.event.keydown)
								;
							if (module.has.search()) {
								$module
									.on(module.get.inputEvent() + eventNamespace, selector.search, module.event.input)
									;
							}
							if (module.is.multiple()) {
								$document
									.on('keydown' + elementNamespace, module.event.document.keydown)
									;
							}
						},
						inputEvents: function () {
							module.verbose('Binding input change events');
							$module
								.on('change' + eventNamespace, selector.input, module.event.change)
								;
						},
						mouseEvents: function () {
							module.verbose('Binding mouse events');
							if (module.is.multiple()) {
								$module
									.on('click' + eventNamespace, selector.label, module.event.label.click)
									.on('click' + eventNamespace, selector.remove, module.event.remove.click)
									;
							}
							if (module.is.searchSelection()) {
								$module
									.on('mousedown' + eventNamespace, module.event.mousedown)
									.on('mouseup' + eventNamespace, module.event.mouseup)
									.on('mousedown' + eventNamespace, selector.menu, module.event.menu.mousedown)
									.on('mouseup' + eventNamespace, selector.menu, module.event.menu.mouseup)
									.on('click' + eventNamespace, selector.icon, module.event.icon.click)
									.on('focus' + eventNamespace, selector.search, module.event.search.focus)
									.on('click' + eventNamespace, selector.search, module.event.search.focus)
									.on('blur' + eventNamespace, selector.search, module.event.search.blur)
									.on('click' + eventNamespace, selector.text, module.event.text.focus)
									;
								if (module.is.multiple()) {
									$module
										.on('click' + eventNamespace, module.event.click)
										;
								}
							}
							else {
								if (settings.on == 'click') {
									$module
										.on('click' + eventNamespace, module.event.test.toggle)
										;
								}
								else if (settings.on == 'hover') {
									$module
										.on('mouseenter' + eventNamespace, module.delay.show)
										.on('mouseleave' + eventNamespace, module.delay.hide)
										;
								}
								else {
									$module
										.on(settings.on + eventNamespace, module.toggle)
										;
								}
								$module
									.on('click' + eventNamespace, selector.icon, module.event.icon.click)
									.on('mousedown' + eventNamespace, module.event.mousedown)
									.on('mouseup' + eventNamespace, module.event.mouseup)
									.on('focus' + eventNamespace, module.event.focus)
									;
								if (module.has.menuSearch()) {
									$module
										.on('blur' + eventNamespace, selector.search, module.event.search.blur)
										;
								}
								else {
									$module
										.on('blur' + eventNamespace, module.event.blur)
										;
								}
							}
							$menu
								.on('mouseenter' + eventNamespace, selector.item, module.event.item.mouseenter)
								.on('mouseleave' + eventNamespace, selector.item, module.event.item.mouseleave)
								.on('click' + eventNamespace, selector.item, module.event.item.click)
								;
						},
						intent: function () {
							module.verbose('Binding hide intent event to document');
							if (hasTouch) {
								$document
									.on('touchstart' + elementNamespace, module.event.test.touch)
									.on('touchmove' + elementNamespace, module.event.test.touch)
									;
							}
							$document
								.on('click' + elementNamespace, module.event.test.hide)
								;
						}
					},

					unbind: {
						intent: function () {
							module.verbose('Removing hide intent event from document');
							if (hasTouch) {
								$document
									.off('touchstart' + elementNamespace)
									.off('touchmove' + elementNamespace)
									;
							}
							$document
								.off('click' + elementNamespace)
								;
						}
					},

					filter: function (query) {
						var
							searchTerm = (query !== undefined)
								? query
								: module.get.query(),
							afterFiltered = function () {
								if (module.is.multiple()) {
									module.filterActive();
								}
								if (query || (!query && module.get.activeItem().length == 0)) {
									module.select.firstUnfiltered();
								}
								if (module.has.allResultsFiltered()) {
									if (settings.onNoResults.call(element, searchTerm)) {
										if (settings.allowAdditions) {
											if (settings.hideAdditions) {
												module.verbose('User addition with no menu, setting empty style');
												module.set.empty();
												module.hideMenu();
											}
										}
										else {
											module.verbose('All items filtered, showing message', searchTerm);
											module.add.message(message.noResults);
										}
									}
									else {
										module.verbose('All items filtered, hiding dropdown', searchTerm);
										module.hideMenu();
									}
								}
								else {
									module.remove.empty();
									module.remove.message();
								}
								if (settings.allowAdditions) {
									module.add.userSuggestion(query);
								}
								if (module.is.searchSelection() && module.can.show() && module.is.focusedOnSearch()) {
									module.show();
								}
							}
							;
						if (settings.useLabels && module.has.maxSelections()) {
							return;
						}
						if (settings.apiSettings) {
							if (module.can.useAPI()) {
								module.queryRemote(searchTerm, function () {
									if (settings.filterRemoteData) {
										module.filterItems(searchTerm);
									}
									afterFiltered();
								});
							}
							else {
								module.error(error.noAPI);
							}
						}
						else {
							module.filterItems(searchTerm);
							afterFiltered();
						}
					},

					queryRemote: function (query, callback) {
						var
							apiSettings = {
								errorDuration: false,
								cache: 'local',
								throttle: settings.throttle,
								urlData: {
									query: query
								},
								onError: function () {
									module.add.message(message.serverError);
									callback();
								},
								onFailure: function () {
									module.add.message(message.serverError);
									callback();
								},
								onSuccess: function (response) {
									var
										values = response[fields.remoteValues],
										hasRemoteValues = ($.isArray(values) && values.length > 0)
										;
									if (hasRemoteValues) {
										module.remove.message();
										module.setup.menu({
											values: response[fields.remoteValues]
										});
									}
									else {
										module.add.message(message.noResults);
									}
									callback();
								}
							}
							;
						if (!$module.api('get request')) {
							module.setup.api();
						}
						apiSettings = $.extend(true, {}, apiSettings, settings.apiSettings);
						$module
							.api('setting', apiSettings)
							.api('query')
							;
					},

					filterItems: function (query) {
						var
							searchTerm = (query !== undefined)
								? query
								: module.get.query(),
							results = null,
							escapedTerm = module.escape.string(searchTerm),
							beginsWithRegExp = new RegExp('^' + escapedTerm, 'igm')
							;
						// avoid loop if we're matching nothing
						if (module.has.query()) {
							results = [];

							module.verbose('Searching for matching values', searchTerm);
							$item
								.each(function () {
									var
										$choice = $(this),
										text,
										value
										;
									if (settings.match == 'both' || settings.match == 'text') {
										text = String(module.get.choiceText($choice, false));
										if (text.search(beginsWithRegExp) !== -1) {
											results.push(this);
											return true;
										}
										else if (settings.fullTextSearch === 'exact' && module.exactSearch(searchTerm, text)) {
											results.push(this);
											return true;
										}
										else if (settings.fullTextSearch === true && module.fuzzySearch(searchTerm, text)) {
											results.push(this);
											return true;
										}
									}
									if (settings.match == 'both' || settings.match == 'value') {
										value = String(module.get.choiceValue($choice, text));
										if (value.search(beginsWithRegExp) !== -1) {
											results.push(this);
											return true;
										}
										else if (settings.fullTextSearch === 'exact' && module.exactSearch(searchTerm, value)) {
											results.push(this);
											return true;
										}
										else if (settings.fullTextSearch === true && module.fuzzySearch(searchTerm, value)) {
											results.push(this);
											return true;
										}
									}
								})
								;
						}
						module.debug('Showing only matched items', searchTerm);
						module.remove.filteredItem();
						if (results) {
							$item
								.not(results)
								.addClass(className.filtered)
								;
						}
					},

					fuzzySearch: function (query, term) {
						var
							termLength = term.length,
							queryLength = query.length
							;
						query = query.toLowerCase();
						term = term.toLowerCase();
						if (queryLength > termLength) {
							return false;
						}
						if (queryLength === termLength) {
							return (query === term);
						}
						search: for (var characterIndex = 0, nextCharacterIndex = 0; characterIndex < queryLength; characterIndex++) {
							var
								queryCharacter = query.charCodeAt(characterIndex)
								;
							while (nextCharacterIndex < termLength) {
								if (term.charCodeAt(nextCharacterIndex++) === queryCharacter) {
									continue search;
								}
							}
							return false;
						}
						return true;
					},
					exactSearch: function (query, term) {
						query = query.toLowerCase();
						term = term.toLowerCase();
						if (term.indexOf(query) > -1) {
							return true;
						}
						return false;
					},
					filterActive: function () {
						if (settings.useLabels) {
							$item.filter('.' + className.active)
								.addClass(className.filtered)
								;
						}
					},

					focusSearch: function (skipHandler) {
						if (module.has.search() && !module.is.focusedOnSearch()) {
							if (skipHandler) {
								$module.off('focus' + eventNamespace, selector.search);
								$search.focus();
								$module.on('focus' + eventNamespace, selector.search, module.event.search.focus);
							}
							else {
								$search.focus();
							}
						}
					},

					forceSelection: function () {
						var
							$currentlySelected = $item.not(className.filtered).filter('.' + className.selected).eq(0),
							$activeItem = $item.not(className.filtered).filter('.' + className.active).eq(0),
							$selectedItem = ($currentlySelected.length > 0)
								? $currentlySelected
								: $activeItem,
							hasSelected = ($selectedItem.length > 0)
							;
						if (hasSelected && !module.is.multiple()) {
							module.debug('Forcing partial selection to selected item', $selectedItem);
							module.event.item.click.call($selectedItem, {}, true);
							return;
						}
						else {
							if (settings.allowAdditions) {
								module.set.selected(module.get.query());
								module.remove.searchTerm();
							}
							else {
								module.remove.searchTerm();
							}
						}
					},

					change: {
						values: function (values) {
							if (!settings.allowAdditions) {
								module.clear();
							}
							module.debug('Creating dropdown with specified values', values);
							module.setup.menu({ values: values });
							$.each(values, function (index, item) {
								if (item.selected == true) {
									module.debug('Setting initial selection to', item.value);
									module.set.selected(item.value);
									return true;
								}
							});
						}
					},

					event: {
						change: function () {
							if (!internalChange) {
								module.debug('Input changed, updating selection');
								module.set.selected();
							}
						},
						focus: function () {
							if (settings.showOnFocus && !activated && module.is.hidden() && !pageLostFocus) {
								module.show();
							}
						},
						blur: function (event) {
							pageLostFocus = (document.activeElement === this);
							if (!activated && !pageLostFocus) {
								module.remove.activeLabel();
								module.hide();
							}
						},
						mousedown: function () {
							if (module.is.searchSelection()) {
								// prevent menu hiding on immediate re-focus
								willRefocus = true;
							}
							else {
								// prevents focus callback from occurring on mousedown
								activated = true;
							}
						},
						mouseup: function () {
							if (module.is.searchSelection()) {
								// prevent menu hiding on immediate re-focus
								willRefocus = false;
							}
							else {
								activated = false;
							}
						},
						click: function (event) {
							var
								$target = $(event.target)
								;
							// focus search
							if ($target.is($module)) {
								if (!module.is.focusedOnSearch()) {
									module.focusSearch();
								}
								else {
									module.show();
								}
							}
						},
						search: {
							focus: function () {
								activated = true;
								if (module.is.multiple()) {
									module.remove.activeLabel();
								}
								if (settings.showOnFocus) {
									module.search();
								}
							},
							blur: function (event) {
								pageLostFocus = (document.activeElement === this);
								if (module.is.searchSelection() && !willRefocus) {
									if (!itemActivated && !pageLostFocus) {
										if (settings.forceSelection) {
											module.forceSelection();
										}
										module.hide();
									}
								}
								willRefocus = false;
							}
						},
						icon: {
							click: function (event) {
								if ($icon.hasClass(className.clear)) {
									module.clear();
								}
								else if (module.can.click()) {
									module.toggle();
								}
							}
						},
						text: {
							focus: function (event) {
								activated = true;
								module.focusSearch();
							}
						},
						input: function (event) {
							if (module.is.multiple() || module.is.searchSelection()) {
								module.set.filtered();
							}
							clearTimeout(module.timer);
							module.timer = setTimeout(module.search, settings.delay.search);
						},
						label: {
							click: function (event) {
								var
									$label = $(this),
									$labels = $module.find(selector.label),
									$activeLabels = $labels.filter('.' + className.active),
									$nextActive = $label.nextAll('.' + className.active),
									$prevActive = $label.prevAll('.' + className.active),
									$range = ($nextActive.length > 0)
										? $label.nextUntil($nextActive).add($activeLabels).add($label)
										: $label.prevUntil($prevActive).add($activeLabels).add($label)
									;
								if (event.shiftKey) {
									$activeLabels.removeClass(className.active);
									$range.addClass(className.active);
								}
								else if (event.ctrlKey) {
									$label.toggleClass(className.active);
								}
								else {
									$activeLabels.removeClass(className.active);
									$label.addClass(className.active);
								}
								settings.onLabelSelect.apply(this, $labels.filter('.' + className.active));
							}
						},
						remove: {
							click: function () {
								var
									$label = $(this).parent()
									;
								if ($label.hasClass(className.active)) {
									// remove all selected labels
									module.remove.activeLabels();
								}
								else {
									// remove this label only
									module.remove.activeLabels($label);
								}
							}
						},
						test: {
							toggle: function (event) {
								var
									toggleBehavior = (module.is.multiple())
										? module.show
										: module.toggle
									;
								if (module.is.bubbledLabelClick(event) || module.is.bubbledIconClick(event)) {
									return;
								}
								if (module.determine.eventOnElement(event, toggleBehavior)) {
									event.preventDefault();
								}
							},
							touch: function (event) {
								module.determine.eventOnElement(event, function () {
									if (event.type == 'touchstart') {
										module.timer = setTimeout(function () {
											module.hide();
										}, settings.delay.touch);
									}
									else if (event.type == 'touchmove') {
										clearTimeout(module.timer);
									}
								});
								event.stopPropagation();
							},
							hide: function (event) {
								module.determine.eventInModule(event, module.hide);
							}
						},
						select: {
							mutation: function (mutations) {
								module.debug('<select> modified, recreating menu');
								var
									isSelectMutation = false
									;
								$.each(mutations, function (index, mutation) {
									if ($(mutation.target).is('select') || $(mutation.addedNodes).is('select')) {
										isSelectMutation = true;
										return true;
									}
								});
								if (isSelectMutation) {
									module.disconnect.selectObserver();
									module.refresh();
									module.setup.select();
									module.set.selected();
									module.observe.select();
								}
							}
						},
						menu: {
							mutation: function (mutations) {
								var
									mutation = mutations[0],
									$addedNode = mutation.addedNodes
										? $(mutation.addedNodes[0])
										: $(false),
									$removedNode = mutation.removedNodes
										? $(mutation.removedNodes[0])
										: $(false),
									$changedNodes = $addedNode.add($removedNode),
									isUserAddition = $changedNodes.is(selector.addition) || $changedNodes.closest(selector.addition).length > 0,
									isMessage = $changedNodes.is(selector.message) || $changedNodes.closest(selector.message).length > 0
									;
								if (isUserAddition || isMessage) {
									module.debug('Updating item selector cache');
									module.refreshItems();
								}
								else {
									module.debug('Menu modified, updating selector cache');
									module.refresh();
								}
							},
							mousedown: function () {
								itemActivated = true;
							},
							mouseup: function () {
								itemActivated = false;
							}
						},
						item: {
							mouseenter: function (event) {
								var
									$target = $(event.target),
									$item = $(this),
									$subMenu = $item.children(selector.menu),
									$otherMenus = $item.siblings(selector.item).children(selector.menu),
									hasSubMenu = ($subMenu.length > 0),
									isBubbledEvent = ($subMenu.find($target).length > 0)
									;
								if (!isBubbledEvent && hasSubMenu) {
									clearTimeout(module.itemTimer);
									module.itemTimer = setTimeout(function () {
										module.verbose('Showing sub-menu', $subMenu);
										$.each($otherMenus, function () {
											module.animate.hide(false, $(this));
										});
										module.animate.show(false, $subMenu);
									}, settings.delay.show);
									event.preventDefault();
								}
							},
							mouseleave: function (event) {
								var
									$subMenu = $(this).children(selector.menu)
									;
								if ($subMenu.length > 0) {
									clearTimeout(module.itemTimer);
									module.itemTimer = setTimeout(function () {
										module.verbose('Hiding sub-menu', $subMenu);
										module.animate.hide(false, $subMenu);
									}, settings.delay.hide);
								}
							},
							click: function (event, skipRefocus) {
								var
									$choice = $(this),
									$target = (event)
										? $(event.target)
										: $(''),
									$subMenu = $choice.find(selector.menu),
									text = module.get.choiceText($choice),
									value = module.get.choiceValue($choice, text),
									hasSubMenu = ($subMenu.length > 0),
									isBubbledEvent = ($subMenu.find($target).length > 0)
									;
								// prevents IE11 bug where menu receives focus even though `tabindex=-1`
								if (module.has.menuSearch()) {
									$(document.activeElement).blur();
								}
								if (!isBubbledEvent && (!hasSubMenu || settings.allowCategorySelection)) {
									if (module.is.searchSelection()) {
										if (settings.allowAdditions) {
											module.remove.userAddition();
										}
										module.remove.searchTerm();
										if (!module.is.focusedOnSearch() && !(skipRefocus == true)) {
											module.focusSearch(true);
										}
									}
									if (!settings.useLabels) {
										module.remove.filteredItem();
										module.set.scrollPosition($choice);
									}
									module.determine.selectAction.call(this, text, value);
								}
							}
						},

						document: {
							// label selection should occur even when element has no focus
							keydown: function (event) {
								var
									pressedKey = event.which,
									isShortcutKey = module.is.inObject(pressedKey, keys)
									;
								if (isShortcutKey) {
									var
										$label = $module.find(selector.label),
										$activeLabel = $label.filter('.' + className.active),
										activeValue = $activeLabel.data(metadata.value),
										labelIndex = $label.index($activeLabel),
										labelCount = $label.length,
										hasActiveLabel = ($activeLabel.length > 0),
										hasMultipleActive = ($activeLabel.length > 1),
										isFirstLabel = (labelIndex === 0),
										isLastLabel = (labelIndex + 1 == labelCount),
										isSearch = module.is.searchSelection(),
										isFocusedOnSearch = module.is.focusedOnSearch(),
										isFocused = module.is.focused(),
										caretAtStart = (isFocusedOnSearch && module.get.caretPosition() === 0),
										$nextLabel
										;
									if (isSearch && !hasActiveLabel && !isFocusedOnSearch) {
										return;
									}

									if (pressedKey == keys.leftArrow) {
										// activate previous label
										if ((isFocused || caretAtStart) && !hasActiveLabel) {
											module.verbose('Selecting previous label');
											$label.last().addClass(className.active);
										}
										else if (hasActiveLabel) {
											if (!event.shiftKey) {
												module.verbose('Selecting previous label');
												$label.removeClass(className.active);
											}
											else {
												module.verbose('Adding previous label to selection');
											}
											if (isFirstLabel && !hasMultipleActive) {
												$activeLabel.addClass(className.active);
											}
											else {
												$activeLabel.prev(selector.siblingLabel)
													.addClass(className.active)
													.end()
													;
											}
											event.preventDefault();
										}
									}
									else if (pressedKey == keys.rightArrow) {
										// activate first label
										if (isFocused && !hasActiveLabel) {
											$label.first().addClass(className.active);
										}
										// activate next label
										if (hasActiveLabel) {
											if (!event.shiftKey) {
												module.verbose('Selecting next label');
												$label.removeClass(className.active);
											}
											else {
												module.verbose('Adding next label to selection');
											}
											if (isLastLabel) {
												if (isSearch) {
													if (!isFocusedOnSearch) {
														module.focusSearch();
													}
													else {
														$label.removeClass(className.active);
													}
												}
												else if (hasMultipleActive) {
													$activeLabel.next(selector.siblingLabel).addClass(className.active);
												}
												else {
													$activeLabel.addClass(className.active);
												}
											}
											else {
												$activeLabel.next(selector.siblingLabel).addClass(className.active);
											}
											event.preventDefault();
										}
									}
									else if (pressedKey == keys.deleteKey || pressedKey == keys.backspace) {
										if (hasActiveLabel) {
											module.verbose('Removing active labels');
											if (isLastLabel) {
												if (isSearch && !isFocusedOnSearch) {
													module.focusSearch();
												}
											}
											$activeLabel.last().next(selector.siblingLabel).addClass(className.active);
											module.remove.activeLabels($activeLabel);
											event.preventDefault();
										}
										else if (caretAtStart && !hasActiveLabel && pressedKey == keys.backspace) {
											module.verbose('Removing last label on input backspace');
											$activeLabel = $label.last().addClass(className.active);
											module.remove.activeLabels($activeLabel);
										}
									}
									else {
										$activeLabel.removeClass(className.active);
									}
								}
							}
						},

						keydown: function (event) {
							var
								pressedKey = event.which,
								isShortcutKey = module.is.inObject(pressedKey, keys)
								;
							if (isShortcutKey) {
								var
									$currentlySelected = $item.not(selector.unselectable).filter('.' + className.selected).eq(0),
									$activeItem = $menu.children('.' + className.active).eq(0),
									$selectedItem = ($currentlySelected.length > 0)
										? $currentlySelected
										: $activeItem,
									$visibleItems = ($selectedItem.length > 0)
										? $selectedItem.siblings(':not(.' + className.filtered + ')').addBack()
										: $menu.children(':not(.' + className.filtered + ')'),
									$subMenu = $selectedItem.children(selector.menu),
									$parentMenu = $selectedItem.closest(selector.menu),
									inVisibleMenu = ($parentMenu.hasClass(className.visible) || $parentMenu.hasClass(className.animating) || $parentMenu.parent(selector.menu).length > 0),
									hasSubMenu = ($subMenu.length > 0),
									hasSelectedItem = ($selectedItem.length > 0),
									selectedIsSelectable = ($selectedItem.not(selector.unselectable).length > 0),
									delimiterPressed = (pressedKey == keys.delimiter && settings.allowAdditions && module.is.multiple()),
									isAdditionWithoutMenu = (settings.allowAdditions && settings.hideAdditions && (pressedKey == keys.enter || delimiterPressed) && selectedIsSelectable),
									$nextItem,
									isSubMenuItem,
									newIndex
									;
								// allow selection with menu closed
								if (isAdditionWithoutMenu) {
									module.verbose('Selecting item from keyboard shortcut', $selectedItem);
									module.event.item.click.call($selectedItem, event);
									if (module.is.searchSelection()) {
										module.remove.searchTerm();
									}
								}

								// visible menu keyboard shortcuts
								if (module.is.visible()) {

									// enter (select or open sub-menu)
									if (pressedKey == keys.enter || delimiterPressed) {
										if (pressedKey == keys.enter && hasSelectedItem && hasSubMenu && !settings.allowCategorySelection) {
											module.verbose('Pressed enter on unselectable category, opening sub menu');
											pressedKey = keys.rightArrow;
										}
										else if (selectedIsSelectable) {
											module.verbose('Selecting item from keyboard shortcut', $selectedItem);
											module.event.item.click.call($selectedItem, event);
											if (module.is.searchSelection()) {
												module.remove.searchTerm();
											}
										}
										event.preventDefault();
									}

									// sub-menu actions
									if (hasSelectedItem) {

										if (pressedKey == keys.leftArrow) {

											isSubMenuItem = ($parentMenu[0] !== $menu[0]);

											if (isSubMenuItem) {
												module.verbose('Left key pressed, closing sub-menu');
												module.animate.hide(false, $parentMenu);
												$selectedItem
													.removeClass(className.selected)
													;
												$parentMenu
													.closest(selector.item)
													.addClass(className.selected)
													;
												event.preventDefault();
											}
										}

										// right arrow (show sub-menu)
										if (pressedKey == keys.rightArrow) {
											if (hasSubMenu) {
												module.verbose('Right key pressed, opening sub-menu');
												module.animate.show(false, $subMenu);
												$selectedItem
													.removeClass(className.selected)
													;
												$subMenu
													.find(selector.item).eq(0)
													.addClass(className.selected)
													;
												event.preventDefault();
											}
										}
									}

									// up arrow (traverse menu up)
									if (pressedKey == keys.upArrow) {
										$nextItem = (hasSelectedItem && inVisibleMenu)
											? $selectedItem.prevAll(selector.item + ':not(' + selector.unselectable + ')').eq(0)
											: $item.eq(0)
											;
										if ($visibleItems.index($nextItem) < 0) {
											module.verbose('Up key pressed but reached top of current menu');
											event.preventDefault();
											return;
										}
										else {
											module.verbose('Up key pressed, changing active item');
											$selectedItem
												.removeClass(className.selected)
												;
											$nextItem
												.addClass(className.selected)
												;
											module.set.scrollPosition($nextItem);
											if (settings.selectOnKeydown && module.is.single()) {
												module.set.selectedItem($nextItem);
											}
										}
										event.preventDefault();
									}

									// down arrow (traverse menu down)
									if (pressedKey == keys.downArrow) {
										$nextItem = (hasSelectedItem && inVisibleMenu)
											? $nextItem = $selectedItem.nextAll(selector.item + ':not(' + selector.unselectable + ')').eq(0)
											: $item.eq(0)
											;
										if ($nextItem.length === 0) {
											module.verbose('Down key pressed but reached bottom of current menu');
											event.preventDefault();
											return;
										}
										else {
											module.verbose('Down key pressed, changing active item');
											$item
												.removeClass(className.selected)
												;
											$nextItem
												.addClass(className.selected)
												;
											module.set.scrollPosition($nextItem);
											if (settings.selectOnKeydown && module.is.single()) {
												module.set.selectedItem($nextItem);
											}
										}
										event.preventDefault();
									}

									// page down (show next page)
									if (pressedKey == keys.pageUp) {
										module.scrollPage('up');
										event.preventDefault();
									}
									if (pressedKey == keys.pageDown) {
										module.scrollPage('down');
										event.preventDefault();
									}

									// escape (close menu)
									if (pressedKey == keys.escape) {
										module.verbose('Escape key pressed, closing dropdown');
										module.hide();
									}

								}
								else {
									// delimiter key
									if (delimiterPressed) {
										event.preventDefault();
									}
									// down arrow (open menu)
									if (pressedKey == keys.downArrow && !module.is.visible()) {
										module.verbose('Down key pressed, showing dropdown');
										module.show();
										event.preventDefault();
									}
								}
							}
							else {
								if (!module.has.search()) {
									module.set.selectedLetter(String.fromCharCode(pressedKey));
								}
							}
						}
					},

					trigger: {
						change: function () {
							var
								events = document.createEvent('HTMLEvents'),
								inputElement = $input[0]
								;
							if (inputElement) {
								module.verbose('Triggering native change event');
								events.initEvent('change', true, false);
								inputElement.dispatchEvent(events);
							}
						}
					},

					determine: {
						selectAction: function (text, value) {
							module.verbose('Determining action', settings.action);
							if ($.isFunction(module.action[settings.action])) {
								module.verbose('Triggering preset action', settings.action, text, value);
								module.action[settings.action].call(element, text, value, this);
							}
							else if ($.isFunction(settings.action)) {
								module.verbose('Triggering user action', settings.action, text, value);
								settings.action.call(element, text, value, this);
							}
							else {
								module.error(error.action, settings.action);
							}
						},
						eventInModule: function (event, callback) {
							var
								$target = $(event.target),
								inDocument = ($target.closest(document.documentElement).length > 0),
								inModule = ($target.closest($module).length > 0)
								;
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if (inDocument && !inModule) {
								module.verbose('Triggering event', callback);
								callback();
								return true;
							}
							else {
								module.verbose('Event occurred in dropdown, canceling callback');
								return false;
							}
						},
						eventOnElement: function (event, callback) {
							var
								$target = $(event.target),
								$label = $target.closest(selector.siblingLabel),
								inVisibleDOM = document.body.contains(event.target),
								notOnLabel = ($module.find($label).length === 0),
								notInMenu = ($target.closest($menu).length === 0)
								;
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if (inVisibleDOM && notOnLabel && notInMenu) {
								module.verbose('Triggering event', callback);
								callback();
								return true;
							}
							else {
								module.verbose('Event occurred in dropdown menu, canceling callback');
								return false;
							}
						}
					},

					action: {

						nothing: function () { },

						activate: function (text, value, element) {
							value = (value !== undefined)
								? value
								: text
								;
							if (module.can.activate($(element))) {
								module.set.selected(value, $(element));
								if (module.is.multiple() && !module.is.allFiltered()) {
									return;
								}
								else {
									module.hideAndClear();
								}
							}
						},

						select: function (text, value, element) {
							value = (value !== undefined)
								? value
								: text
								;
							if (module.can.activate($(element))) {
								module.set.value(value, text, $(element));
								if (module.is.multiple() && !module.is.allFiltered()) {
									return;
								}
								else {
									module.hideAndClear();
								}
							}
						},

						combo: function (text, value, element) {
							value = (value !== undefined)
								? value
								: text
								;
							module.set.selected(value, $(element));
							module.hideAndClear();
						},

						hide: function (text, value, element) {
							module.set.value(value, text, $(element));
							module.hideAndClear();
						}

					},

					get: {
						id: function () {
							return id;
						},
						defaultText: function () {
							return $module.data(metadata.defaultText);
						},
						defaultValue: function () {
							return $module.data(metadata.defaultValue);
						},
						placeholderText: function () {
							if (settings.placeholder != 'auto' && typeof settings.placeholder == 'string') {
								return settings.placeholder;
							}
							return $module.data(metadata.placeholderText) || '';
						},
						text: function () {
							return $text.text();
						},
						query: function () {
							return $.trim($search.val());
						},
						searchWidth: function (value) {
							value = (value !== undefined)
								? value
								: $search.val()
								;
							$sizer.text(value);
							// prevent rounding issues
							return Math.ceil($sizer.width() + 1);
						},
						selectionCount: function () {
							var
								values = module.get.values(),
								count
								;
							count = (module.is.multiple())
								? $.isArray(values)
									? values.length
									: 0
								: (module.get.value() !== '')
									? 1
									: 0
								;
							return count;
						},
						transition: function ($subMenu) {
							return (settings.transition == 'auto')
								? module.is.upward($subMenu)
									? 'slide up'
									: 'slide down'
								: settings.transition
								;
						},
						userValues: function () {
							var
								values = module.get.values()
								;
							if (!values) {
								return false;
							}
							values = $.isArray(values)
								? values
								: [values]
								;
							return $.grep(values, function (value) {
								return (module.get.item(value) === false);
							});
						},
						uniqueArray: function (array) {
							return $.grep(array, function (value, index) {
								return $.inArray(value, array) === index;
							});
						},
						caretPosition: function () {
							var
								input = $search.get(0),
								range,
								rangeLength
								;
							if ('selectionStart' in input) {
								return input.selectionStart;
							}
							else if (document.selection) {
								input.focus();
								range = document.selection.createRange();
								rangeLength = range.text.length;
								range.moveStart('character', -input.value.length);
								return range.text.length - rangeLength;
							}
						},
						value: function () {
							var
								value = ($input.length > 0)
									? $input.val()
									: $module.data(metadata.value),
								isEmptyMultiselect = ($.isArray(value) && value.length === 1 && value[0] === '')
								;
							// prevents placeholder element from being selected when multiple
							return (value === undefined || isEmptyMultiselect)
								? ''
								: value
								;
						},
						values: function () {
							var
								value = module.get.value()
								;
							if (value === '') {
								return '';
							}
							return (!module.has.selectInput() && module.is.multiple())
								? (typeof value == 'string') // delimited string
									? value.split(settings.delimiter)
									: ''
								: value
								;
						},
						remoteValues: function () {
							var
								values = module.get.values(),
								remoteValues = false
								;
							if (values) {
								if (typeof values == 'string') {
									values = [values];
								}
								$.each(values, function (index, value) {
									var
										name = module.read.remoteData(value)
										;
									module.verbose('Restoring value from session data', name, value);
									if (name) {
										if (!remoteValues) {
											remoteValues = {};
										}
										remoteValues[value] = name;
									}
								});
							}
							return remoteValues;
						},
						choiceText: function ($choice, preserveHTML) {
							preserveHTML = (preserveHTML !== undefined)
								? preserveHTML
								: settings.preserveHTML
								;
							if ($choice) {
								if ($choice.find(selector.menu).length > 0) {
									module.verbose('Retrieving text of element with sub-menu');
									$choice = $choice.clone();
									$choice.find(selector.menu).remove();
									$choice.find(selector.menuIcon).remove();
								}
								return ($choice.data(metadata.text) !== undefined)
									? $choice.data(metadata.text)
									: (preserveHTML)
										? $.trim($choice.html())
										: $.trim($choice.text())
									;
							}
						},
						choiceValue: function ($choice, choiceText) {
							choiceText = choiceText || module.get.choiceText($choice);
							if (!$choice) {
								return false;
							}
							return ($choice.data(metadata.value) !== undefined)
								? String($choice.data(metadata.value))
								: (typeof choiceText === 'string')
									? $.trim(choiceText.toLowerCase())
									: String(choiceText)
								;
						},
						inputEvent: function () {
							var
								input = $search[0]
								;
							if (input) {
								return (input.oninput !== undefined)
									? 'input'
									: (input.onpropertychange !== undefined)
										? 'propertychange'
										: 'keyup'
									;
							}
							return false;
						},
						selectValues: function () {
							var
								select = {}
								;
							select.values = [];
							$module
								.find('option')
								.each(function () {
									var
										$option = $(this),
										name = $option.html(),
										disabled = $option.attr('disabled'),
										value = ($option.attr('value') !== undefined)
											? $option.attr('value')
											: name
										;
									if (settings.placeholder === 'auto' && value === '') {
										select.placeholder = name;
									}
									else {
										select.values.push({
											name: name,
											value: value,
											disabled: disabled
										});
									}
								})
								;
							if (settings.placeholder && settings.placeholder !== 'auto') {
								module.debug('Setting placeholder value to', settings.placeholder);
								select.placeholder = settings.placeholder;
							}
							if (settings.sortSelect) {
								select.values.sort(function (a, b) {
									return (a.name > b.name)
										? 1
										: -1
										;
								});
								module.debug('Retrieved and sorted values from select', select);
							}
							else {
								module.debug('Retrieved values from select', select);
							}
							return select;
						},
						activeItem: function () {
							return $item.filter('.' + className.active);
						},
						selectedItem: function () {
							var
								$selectedItem = $item.not(selector.unselectable).filter('.' + className.selected)
								;
							return ($selectedItem.length > 0)
								? $selectedItem
								: $item.eq(0)
								;
						},
						itemWithAdditions: function (value) {
							var
								$items = module.get.item(value),
								$userItems = module.create.userChoice(value),
								hasUserItems = ($userItems && $userItems.length > 0)
								;
							if (hasUserItems) {
								$items = ($items.length > 0)
									? $items.add($userItems)
									: $userItems
									;
							}
							return $items;
						},
						item: function (value, strict) {
							var
								$selectedItem = false,
								shouldSearch,
								isMultiple
								;
							value = (value !== undefined)
								? value
								: (module.get.values() !== undefined)
									? module.get.values()
									: module.get.text()
								;
							shouldSearch = (isMultiple)
								? (value.length > 0)
								: (value !== undefined && value !== null)
								;
							isMultiple = (module.is.multiple() && $.isArray(value));
							strict = (value === '' || value === 0)
								? true
								: strict || false
								;
							if (shouldSearch) {
								$item
									.each(function () {
										var
											$choice = $(this),
											optionText = module.get.choiceText($choice),
											optionValue = module.get.choiceValue($choice, optionText)
											;
										// safe early exit
										if (optionValue === null || optionValue === undefined) {
											return;
										}
										if (isMultiple) {
											if ($.inArray(String(optionValue), value) !== -1 || $.inArray(optionText, value) !== -1) {
												$selectedItem = ($selectedItem)
													? $selectedItem.add($choice)
													: $choice
													;
											}
										}
										else if (strict) {
											module.verbose('Ambiguous dropdown value using strict type check', $choice, value);
											if (optionValue === value || optionText === value) {
												$selectedItem = $choice;
												return true;
											}
										}
										else {
											if (String(optionValue) == String(value) || optionText == value) {
												module.verbose('Found select item by value', optionValue, value);
												$selectedItem = $choice;
												return true;
											}
										}
									})
									;
							}
							return $selectedItem;
						}
					},

					check: {
						maxSelections: function (selectionCount) {
							if (settings.maxSelections) {
								selectionCount = (selectionCount !== undefined)
									? selectionCount
									: module.get.selectionCount()
									;
								if (selectionCount >= settings.maxSelections) {
									module.debug('Maximum selection count reached');
									if (settings.useLabels) {
										$item.addClass(className.filtered);
										module.add.message(message.maxSelections);
									}
									return true;
								}
								else {
									module.verbose('No longer at maximum selection count');
									module.remove.message();
									module.remove.filteredItem();
									if (module.is.searchSelection()) {
										module.filterItems();
									}
									return false;
								}
							}
							return true;
						}
					},

					restore: {
						defaults: function () {
							module.clear();
							module.restore.defaultText();
							module.restore.defaultValue();
						},
						defaultText: function () {
							var
								defaultText = module.get.defaultText(),
								placeholderText = module.get.placeholderText
								;
							if (defaultText === placeholderText) {
								module.debug('Restoring default placeholder text', defaultText);
								module.set.placeholderText(defaultText);
							}
							else {
								module.debug('Restoring default text', defaultText);
								module.set.text(defaultText);
							}
						},
						placeholderText: function () {
							module.set.placeholderText();
						},
						defaultValue: function () {
							var
								defaultValue = module.get.defaultValue()
								;
							if (defaultValue !== undefined) {
								module.debug('Restoring default value', defaultValue);
								if (defaultValue !== '') {
									module.set.value(defaultValue);
									module.set.selected();
								}
								else {
									module.remove.activeItem();
									module.remove.selectedItem();
								}
							}
						},
						labels: function () {
							if (settings.allowAdditions) {
								if (!settings.useLabels) {
									module.error(error.labels);
									settings.useLabels = true;
								}
								module.debug('Restoring selected values');
								module.create.userLabels();
							}
							module.check.maxSelections();
						},
						selected: function () {
							module.restore.values();
							if (module.is.multiple()) {
								module.debug('Restoring previously selected values and labels');
								module.restore.labels();
							}
							else {
								module.debug('Restoring previously selected values');
							}
						},
						values: function () {
							// prevents callbacks from occurring on initial load
							module.set.initialLoad();
							if (settings.apiSettings && settings.saveRemoteData && module.get.remoteValues()) {
								module.restore.remoteValues();
							}
							else {
								module.set.selected();
							}
							module.remove.initialLoad();
						},
						remoteValues: function () {
							var
								values = module.get.remoteValues()
								;
							module.debug('Recreating selected from session data', values);
							if (values) {
								if (module.is.single()) {
									$.each(values, function (value, name) {
										module.set.text(name);
									});
								}
								else {
									$.each(values, function (value, name) {
										module.add.label(value, name);
									});
								}
							}
						}
					},

					read: {
						remoteData: function (value) {
							var
								name
								;
							if (window.Storage === undefined) {
								module.error(error.noStorage);
								return;
							}
							name = sessionStorage.getItem(value);
							return (name !== undefined)
								? name
								: false
								;
						}
					},

					save: {
						defaults: function () {
							module.save.defaultText();
							module.save.placeholderText();
							module.save.defaultValue();
						},
						defaultValue: function () {
							var
								value = module.get.value()
								;
							module.verbose('Saving default value as', value);
							$module.data(metadata.defaultValue, value);
						},
						defaultText: function () {
							var
								text = module.get.text()
								;
							module.verbose('Saving default text as', text);
							$module.data(metadata.defaultText, text);
						},
						placeholderText: function () {
							var
								text
								;
							if (settings.placeholder !== false && $text.hasClass(className.placeholder)) {
								text = module.get.text();
								module.verbose('Saving placeholder text as', text);
								$module.data(metadata.placeholderText, text);
							}
						},
						remoteData: function (name, value) {
							if (window.Storage === undefined) {
								module.error(error.noStorage);
								return;
							}
							module.verbose('Saving remote data to session storage', value, name);
							sessionStorage.setItem(value, name);
						}
					},

					clear: function () {
						if (module.is.multiple() && settings.useLabels) {
							module.remove.labels();
						}
						else {
							module.remove.activeItem();
							module.remove.selectedItem();
						}
						module.set.placeholderText();
						module.clearValue();
					},

					clearValue: function () {
						module.set.value('');
					},

					scrollPage: function (direction, $selectedItem) {
						var
							$currentItem = $selectedItem || module.get.selectedItem(),
							$menu = $currentItem.closest(selector.menu),
							menuHeight = $menu.outerHeight(),
							currentScroll = $menu.scrollTop(),
							itemHeight = $item.eq(0).outerHeight(),
							itemsPerPage = Math.floor(menuHeight / itemHeight),
							maxScroll = $menu.prop('scrollHeight'),
							newScroll = (direction == 'up')
								? currentScroll - (itemHeight * itemsPerPage)
								: currentScroll + (itemHeight * itemsPerPage),
							$selectableItem = $item.not(selector.unselectable),
							isWithinRange,
							$nextSelectedItem,
							elementIndex
							;
						elementIndex = (direction == 'up')
							? $selectableItem.index($currentItem) - itemsPerPage
							: $selectableItem.index($currentItem) + itemsPerPage
							;
						isWithinRange = (direction == 'up')
							? (elementIndex >= 0)
							: (elementIndex < $selectableItem.length)
							;
						$nextSelectedItem = (isWithinRange)
							? $selectableItem.eq(elementIndex)
							: (direction == 'up')
								? $selectableItem.first()
								: $selectableItem.last()
							;
						if ($nextSelectedItem.length > 0) {
							module.debug('Scrolling page', direction, $nextSelectedItem);
							$currentItem
								.removeClass(className.selected)
								;
							$nextSelectedItem
								.addClass(className.selected)
								;
							if (settings.selectOnKeydown && module.is.single()) {
								module.set.selectedItem($nextSelectedItem);
							}
							$menu
								.scrollTop(newScroll)
								;
						}
					},

					set: {
						filtered: function () {
							var
								isMultiple = module.is.multiple(),
								isSearch = module.is.searchSelection(),
								isSearchMultiple = (isMultiple && isSearch),
								searchValue = (isSearch)
									? module.get.query()
									: '',
								hasSearchValue = (typeof searchValue === 'string' && searchValue.length > 0),
								searchWidth = module.get.searchWidth(),
								valueIsSet = searchValue !== ''
								;
							if (isMultiple && hasSearchValue) {
								module.verbose('Adjusting input width', searchWidth, settings.glyphWidth);
								$search.css('width', searchWidth);
							}
							if (hasSearchValue || (isSearchMultiple && valueIsSet)) {
								module.verbose('Hiding placeholder text');
								$text.addClass(className.filtered);
							}
							else if (!isMultiple || (isSearchMultiple && !valueIsSet)) {
								module.verbose('Showing placeholder text');
								$text.removeClass(className.filtered);
							}
						},
						empty: function () {
							$module.addClass(className.empty);
						},
						loading: function () {
							$module.addClass(className.loading);
						},
						placeholderText: function (text) {
							text = text || module.get.placeholderText();
							module.debug('Setting placeholder text', text);
							module.set.text(text);
							$text.addClass(className.placeholder);
						},
						tabbable: function () {
							if (module.is.searchSelection()) {
								module.debug('Added tabindex to searchable dropdown');
								$search
									.val('')
									.attr('tabindex', 0)
									;
								$menu
									.attr('tabindex', -1)
									;
							}
							else {
								module.debug('Added tabindex to dropdown');
								if ($module.attr('tabindex') === undefined) {
									$module
										.attr('tabindex', 0)
										;
									$menu
										.attr('tabindex', -1)
										;
								}
							}
						},
						initialLoad: function () {
							module.verbose('Setting initial load');
							initialLoad = true;
						},
						activeItem: function ($item) {
							if (settings.allowAdditions && $item.filter(selector.addition).length > 0) {
								$item.addClass(className.filtered);
							}
							else {
								$item.addClass(className.active);
							}
						},
						partialSearch: function (text) {
							var
								length = module.get.query().length
								;
							$search.val(text.substr(0, length));
						},
						scrollPosition: function ($item, forceScroll) {
							var
								edgeTolerance = 5,
								$menu,
								hasActive,
								offset,
								itemHeight,
								itemOffset,
								menuOffset,
								menuScroll,
								menuHeight,
								abovePage,
								belowPage
								;

							$item = $item || module.get.selectedItem();
							$menu = $item.closest(selector.menu);
							hasActive = ($item && $item.length > 0);
							forceScroll = (forceScroll !== undefined)
								? forceScroll
								: false
								;
							if ($item && $menu.length > 0 && hasActive) {
								itemOffset = $item.position().top;

								$menu.addClass(className.loading);
								menuScroll = $menu.scrollTop();
								menuOffset = $menu.offset().top;
								itemOffset = $item.offset().top;
								offset = menuScroll - menuOffset + itemOffset;
								if (!forceScroll) {
									menuHeight = $menu.height();
									belowPage = menuScroll + menuHeight < (offset + edgeTolerance);
									abovePage = ((offset - edgeTolerance) < menuScroll);
								}
								module.debug('Scrolling to active item', offset);
								if (forceScroll || abovePage || belowPage) {
									$menu.scrollTop(offset);
								}
								$menu.removeClass(className.loading);
							}
						},
						text: function (text) {
							if (settings.action !== 'select') {
								if (settings.action == 'combo') {
									module.debug('Changing combo button text', text, $combo);
									if (settings.preserveHTML) {
										$combo.html(text);
									}
									else {
										$combo.text(text);
									}
								}
								else {
									if (text !== module.get.placeholderText()) {
										$text.removeClass(className.placeholder);
									}
									module.debug('Changing text', text, $text);
									$text
										.removeClass(className.filtered)
										;
									if (settings.preserveHTML) {
										$text.html(text);
									}
									else {
										$text.text(text);
									}
								}
							}
						},
						selectedItem: function ($item) {
							var
								value = module.get.choiceValue($item),
								searchText = module.get.choiceText($item, false),
								text = module.get.choiceText($item, true)
								;
							module.debug('Setting user selection to item', $item);
							module.remove.activeItem();
							module.set.partialSearch(searchText);
							module.set.activeItem($item);
							module.set.selected(value, $item);
							module.set.text(text);
						},
						selectedLetter: function (letter) {
							var
								$selectedItem = $item.filter('.' + className.selected),
								alreadySelectedLetter = $selectedItem.length > 0 && module.has.firstLetter($selectedItem, letter),
								$nextValue = false,
								$nextItem
								;
							// check next of same letter
							if (alreadySelectedLetter) {
								$nextItem = $selectedItem.nextAll($item).eq(0);
								if (module.has.firstLetter($nextItem, letter)) {
									$nextValue = $nextItem;
								}
							}
							// check all values
							if (!$nextValue) {
								$item
									.each(function () {
										if (module.has.firstLetter($(this), letter)) {
											$nextValue = $(this);
											return false;
										}
									})
									;
							}
							// set next value
							if ($nextValue) {
								module.verbose('Scrolling to next value with letter', letter);
								module.set.scrollPosition($nextValue);
								$selectedItem.removeClass(className.selected);
								$nextValue.addClass(className.selected);
								if (settings.selectOnKeydown && module.is.single()) {
									module.set.selectedItem($nextValue);
								}
							}
						},
						direction: function ($menu) {
							if (settings.direction == 'auto') {
								// reset position
								module.remove.upward();

								if (module.can.openDownward($menu)) {
									module.remove.upward($menu);
								}
								else {
									module.set.upward($menu);
								}
								if (!module.is.leftward($menu) && !module.can.openRightward($menu)) {
									module.set.leftward($menu);
								}
							}
							else if (settings.direction == 'upward') {
								module.set.upward($menu);
							}
						},
						upward: function ($currentMenu) {
							var $element = $currentMenu || $module;
							$element.addClass(className.upward);
						},
						leftward: function ($currentMenu) {
							var $element = $currentMenu || $menu;
							$element.addClass(className.leftward);
						},
						value: function (value, text, $selected) {
							var
								escapedValue = module.escape.value(value),
								hasInput = ($input.length > 0),
								currentValue = module.get.values(),
								stringValue = (value !== undefined)
									? String(value)
									: value,
								newValue
								;
							if (hasInput) {
								if (!settings.allowReselection && stringValue == currentValue) {
									module.verbose('Skipping value update already same value', value, currentValue);
									if (!module.is.initialLoad()) {
										return;
									}
								}

								if (module.is.single() && module.has.selectInput() && module.can.extendSelect()) {
									module.debug('Adding user option', value);
									module.add.optionValue(value);
								}
								module.debug('Updating input value', escapedValue, currentValue);
								internalChange = true;
								$input
									.val(escapedValue)
									;
								if (settings.fireOnInit === false && module.is.initialLoad()) {
									module.debug('Input native change event ignored on initial load');
								}
								else {
									module.trigger.change();
								}
								internalChange = false;
							}
							else {
								module.verbose('Storing value in metadata', escapedValue, $input);
								if (escapedValue !== currentValue) {
									$module.data(metadata.value, stringValue);
								}
							}
							if (module.is.single() && settings.clearable) {
								// treat undefined or '' as empty
								if (!escapedValue) {
									module.remove.clearable();
								}
								else {
									module.set.clearable();
								}
							}
							if (settings.fireOnInit === false && module.is.initialLoad()) {
								module.verbose('No callback on initial load', settings.onChange);
							}
							else {
								settings.onChange.call(element, value, text, $selected);
							}
						},
						active: function () {
							$module
								.addClass(className.active)
								;
						},
						multiple: function () {
							$module.addClass(className.multiple);
						},
						visible: function () {
							$module.addClass(className.visible);
						},
						exactly: function (value, $selectedItem) {
							module.debug('Setting selected to exact values');
							module.clear();
							module.set.selected(value, $selectedItem);
						},
						selected: function (value, $selectedItem) {
							var
								isMultiple = module.is.multiple(),
								$userSelectedItem
								;
							$selectedItem = (settings.allowAdditions)
								? $selectedItem || module.get.itemWithAdditions(value)
								: $selectedItem || module.get.item(value)
								;
							if (!$selectedItem) {
								return;
							}
							module.debug('Setting selected menu item to', $selectedItem);
							if (module.is.multiple()) {
								module.remove.searchWidth();
							}
							if (module.is.single()) {
								module.remove.activeItem();
								module.remove.selectedItem();
							}
							else if (settings.useLabels) {
								module.remove.selectedItem();
							}
							// select each item
							$selectedItem
								.each(function () {
									var
										$selected = $(this),
										selectedText = module.get.choiceText($selected),
										selectedValue = module.get.choiceValue($selected, selectedText),

										isFiltered = $selected.hasClass(className.filtered),
										isActive = $selected.hasClass(className.active),
										isUserValue = $selected.hasClass(className.addition),
										shouldAnimate = (isMultiple && $selectedItem.length == 1)
										;
									if (isMultiple) {
										if (!isActive || isUserValue) {
											if (settings.apiSettings && settings.saveRemoteData) {
												module.save.remoteData(selectedText, selectedValue);
											}
											if (settings.useLabels) {
												module.add.label(selectedValue, selectedText, shouldAnimate);
												module.add.value(selectedValue, selectedText, $selected);
												module.set.activeItem($selected);
												module.filterActive();
												module.select.nextAvailable($selectedItem);
											}
											else {
												module.add.value(selectedValue, selectedText, $selected);
												module.set.text(module.add.variables(message.count));
												module.set.activeItem($selected);
											}
										}
										else if (!isFiltered) {
											module.debug('Selected active value, removing label');
											module.remove.selected(selectedValue);
										}
									}
									else {
										if (settings.apiSettings && settings.saveRemoteData) {
											module.save.remoteData(selectedText, selectedValue);
										}
										module.set.text(selectedText);
										module.set.value(selectedValue, selectedText, $selected);
										$selected
											.addClass(className.active)
											.addClass(className.selected)
											;
									}
								})
								;
						},
						clearable: function () {
							$icon.addClass(className.clear);
						},
					},

					add: {
						label: function (value, text, shouldAnimate) {
							var
								$next = module.is.searchSelection()
									? $search
									: $text,
								escapedValue = module.escape.value(value),
								$label
								;
							if (settings.ignoreCase) {
								escapedValue = escapedValue.toLowerCase();
							}
							$label = $('<a />')
								.addClass(className.label)
								.attr('data-' + metadata.value, escapedValue)
								.html(templates.label(escapedValue, text))
								;
							$label = settings.onLabelCreate.call($label, escapedValue, text);

							if (module.has.label(value)) {
								module.debug('User selection already exists, skipping', escapedValue);
								return;
							}
							if (settings.label.variation) {
								$label.addClass(settings.label.variation);
							}
							if (shouldAnimate === true) {
								module.debug('Animating in label', $label);
								$label
									.addClass(className.hidden)
									.insertBefore($next)
									.transition(settings.label.transition, settings.label.duration)
									;
							}
							else {
								module.debug('Adding selection label', $label);
								$label
									.insertBefore($next)
									;
							}
						},
						message: function (message) {
							var
								$message = $menu.children(selector.message),
								html = settings.templates.message(module.add.variables(message))
								;
							if ($message.length > 0) {
								$message
									.html(html)
									;
							}
							else {
								$message = $('<div/>')
									.html(html)
									.addClass(className.message)
									.appendTo($menu)
									;
							}
						},
						optionValue: function (value) {
							var
								escapedValue = module.escape.value(value),
								$option = $input.find('option[value="' + module.escape.string(escapedValue) + '"]'),
								hasOption = ($option.length > 0)
								;
							if (hasOption) {
								return;
							}
							// temporarily disconnect observer
							module.disconnect.selectObserver();
							if (module.is.single()) {
								module.verbose('Removing previous user addition');
								$input.find('option.' + className.addition).remove();
							}
							$('<option/>')
								.prop('value', escapedValue)
								.addClass(className.addition)
								.html(value)
								.appendTo($input)
								;
							module.verbose('Adding user addition as an <option>', value);
							module.observe.select();
						},
						userSuggestion: function (value) {
							var
								$addition = $menu.children(selector.addition),
								$existingItem = module.get.item(value),
								alreadyHasValue = $existingItem && $existingItem.not(selector.addition).length,
								hasUserSuggestion = $addition.length > 0,
								html
								;
							if (settings.useLabels && module.has.maxSelections()) {
								return;
							}
							if (value === '' || alreadyHasValue) {
								$addition.remove();
								return;
							}
							if (hasUserSuggestion) {
								$addition
									.data(metadata.value, value)
									.data(metadata.text, value)
									.attr('data-' + metadata.value, value)
									.attr('data-' + metadata.text, value)
									.removeClass(className.filtered)
									;
								if (!settings.hideAdditions) {
									html = settings.templates.addition(module.add.variables(message.addResult, value));
									$addition
										.html(html)
										;
								}
								module.verbose('Replacing user suggestion with new value', $addition);
							}
							else {
								$addition = module.create.userChoice(value);
								$addition
									.prependTo($menu)
									;
								module.verbose('Adding item choice to menu corresponding with user choice addition', $addition);
							}
							if (!settings.hideAdditions || module.is.allFiltered()) {
								$addition
									.addClass(className.selected)
									.siblings()
									.removeClass(className.selected)
									;
							}
							module.refreshItems();
						},
						variables: function (message, term) {
							var
								hasCount = (message.search('{count}') !== -1),
								hasMaxCount = (message.search('{maxCount}') !== -1),
								hasTerm = (message.search('{term}') !== -1),
								values,
								count,
								query
								;
							module.verbose('Adding templated variables to message', message);
							if (hasCount) {
								count = module.get.selectionCount();
								message = message.replace('{count}', count);
							}
							if (hasMaxCount) {
								count = module.get.selectionCount();
								message = message.replace('{maxCount}', settings.maxSelections);
							}
							if (hasTerm) {
								query = term || module.get.query();
								message = message.replace('{term}', query);
							}
							return message;
						},
						value: function (addedValue, addedText, $selectedItem) {
							var
								currentValue = module.get.values(),
								newValue
								;
							if (module.has.value(addedValue)) {
								module.debug('Value already selected');
								return;
							}
							if (addedValue === '') {
								module.debug('Cannot select blank values from multiselect');
								return;
							}
							// extend current array
							if ($.isArray(currentValue)) {
								newValue = currentValue.concat([addedValue]);
								newValue = module.get.uniqueArray(newValue);
							}
							else {
								newValue = [addedValue];
							}
							// add values
							if (module.has.selectInput()) {
								if (module.can.extendSelect()) {
									module.debug('Adding value to select', addedValue, newValue, $input);
									module.add.optionValue(addedValue);
								}
							}
							else {
								newValue = newValue.join(settings.delimiter);
								module.debug('Setting hidden input to delimited value', newValue, $input);
							}

							if (settings.fireOnInit === false && module.is.initialLoad()) {
								module.verbose('Skipping onadd callback on initial load', settings.onAdd);
							}
							else {
								settings.onAdd.call(element, addedValue, addedText, $selectedItem);
							}
							module.set.value(newValue, addedValue, addedText, $selectedItem);
							module.check.maxSelections();
						},
					},

					remove: {
						active: function () {
							$module.removeClass(className.active);
						},
						activeLabel: function () {
							$module.find(selector.label).removeClass(className.active);
						},
						empty: function () {
							$module.removeClass(className.empty);
						},
						loading: function () {
							$module.removeClass(className.loading);
						},
						initialLoad: function () {
							initialLoad = false;
						},
						upward: function ($currentMenu) {
							var $element = $currentMenu || $module;
							$element.removeClass(className.upward);
						},
						leftward: function ($currentMenu) {
							var $element = $currentMenu || $menu;
							$element.removeClass(className.leftward);
						},
						visible: function () {
							$module.removeClass(className.visible);
						},
						activeItem: function () {
							$item.removeClass(className.active);
						},
						filteredItem: function () {
							if (settings.useLabels && module.has.maxSelections()) {
								return;
							}
							if (settings.useLabels && module.is.multiple()) {
								$item.not('.' + className.active).removeClass(className.filtered);
							}
							else {
								$item.removeClass(className.filtered);
							}
							module.remove.empty();
						},
						optionValue: function (value) {
							var
								escapedValue = module.escape.value(value),
								$option = $input.find('option[value="' + module.escape.string(escapedValue) + '"]'),
								hasOption = ($option.length > 0)
								;
							if (!hasOption || !$option.hasClass(className.addition)) {
								return;
							}
							// temporarily disconnect observer
							if (selectObserver) {
								selectObserver.disconnect();
								module.verbose('Temporarily disconnecting mutation observer');
							}
							$option.remove();
							module.verbose('Removing user addition as an <option>', escapedValue);
							if (selectObserver) {
								selectObserver.observe($input[0], {
									childList: true,
									subtree: true
								});
							}
						},
						message: function () {
							$menu.children(selector.message).remove();
						},
						searchWidth: function () {
							$search.css('width', '');
						},
						searchTerm: function () {
							module.verbose('Cleared search term');
							$search.val('');
							module.set.filtered();
						},
						userAddition: function () {
							$item.filter(selector.addition).remove();
						},
						selected: function (value, $selectedItem) {
							$selectedItem = (settings.allowAdditions)
								? $selectedItem || module.get.itemWithAdditions(value)
								: $selectedItem || module.get.item(value)
								;

							if (!$selectedItem) {
								return false;
							}

							$selectedItem
								.each(function () {
									var
										$selected = $(this),
										selectedText = module.get.choiceText($selected),
										selectedValue = module.get.choiceValue($selected, selectedText)
										;
									if (module.is.multiple()) {
										if (settings.useLabels) {
											module.remove.value(selectedValue, selectedText, $selected);
											module.remove.label(selectedValue);
										}
										else {
											module.remove.value(selectedValue, selectedText, $selected);
											if (module.get.selectionCount() === 0) {
												module.set.placeholderText();
											}
											else {
												module.set.text(module.add.variables(message.count));
											}
										}
									}
									else {
										module.remove.value(selectedValue, selectedText, $selected);
									}
									$selected
										.removeClass(className.filtered)
										.removeClass(className.active)
										;
									if (settings.useLabels) {
										$selected.removeClass(className.selected);
									}
								})
								;
						},
						selectedItem: function () {
							$item.removeClass(className.selected);
						},
						value: function (removedValue, removedText, $removedItem) {
							var
								values = module.get.values(),
								newValue
								;
							if (module.has.selectInput()) {
								module.verbose('Input is <select> removing selected option', removedValue);
								newValue = module.remove.arrayValue(removedValue, values);
								module.remove.optionValue(removedValue);
							}
							else {
								module.verbose('Removing from delimited values', removedValue);
								newValue = module.remove.arrayValue(removedValue, values);
								newValue = newValue.join(settings.delimiter);
							}
							if (settings.fireOnInit === false && module.is.initialLoad()) {
								module.verbose('No callback on initial load', settings.onRemove);
							}
							else {
								settings.onRemove.call(element, removedValue, removedText, $removedItem);
							}
							module.set.value(newValue, removedText, $removedItem);
							module.check.maxSelections();
						},
						arrayValue: function (removedValue, values) {
							if (!$.isArray(values)) {
								values = [values];
							}
							values = $.grep(values, function (value) {
								return (removedValue != value);
							});
							module.verbose('Removed value from delimited string', removedValue, values);
							return values;
						},
						label: function (value, shouldAnimate) {
							var
								$labels = $module.find(selector.label),
								$removedLabel = $labels.filter('[data-' + metadata.value + '="' + module.escape.string(value) + '"]')
								;
							module.verbose('Removing label', $removedLabel);
							$removedLabel.remove();
						},
						activeLabels: function ($activeLabels) {
							$activeLabels = $activeLabels || $module.find(selector.label).filter('.' + className.active);
							module.verbose('Removing active label selections', $activeLabels);
							module.remove.labels($activeLabels);
						},
						labels: function ($labels) {
							$labels = $labels || $module.find(selector.label);
							module.verbose('Removing labels', $labels);
							$labels
								.each(function () {
									var
										$label = $(this),
										value = $label.data(metadata.value),
										stringValue = (value !== undefined)
											? String(value)
											: value,
										isUserValue = module.is.userValue(stringValue)
										;
									if (settings.onLabelRemove.call($label, value) === false) {
										module.debug('Label remove callback cancelled removal');
										return;
									}
									module.remove.message();
									if (isUserValue) {
										module.remove.value(stringValue);
										module.remove.label(stringValue);
									}
									else {
										// selected will also remove label
										module.remove.selected(stringValue);
									}
								})
								;
						},
						tabbable: function () {
							if (module.is.searchSelection()) {
								module.debug('Searchable dropdown initialized');
								$search
									.removeAttr('tabindex')
									;
								$menu
									.removeAttr('tabindex')
									;
							}
							else {
								module.debug('Simple selection dropdown initialized');
								$module
									.removeAttr('tabindex')
									;
								$menu
									.removeAttr('tabindex')
									;
							}
						},
						clearable: function () {
							$icon.removeClass(className.clear);
						}
					},

					has: {
						menuSearch: function () {
							return (module.has.search() && $search.closest($menu).length > 0);
						},
						search: function () {
							return ($search.length > 0);
						},
						sizer: function () {
							return ($sizer.length > 0);
						},
						selectInput: function () {
							return ($input.is('select'));
						},
						minCharacters: function (searchTerm) {
							if (settings.minCharacters) {
								searchTerm = (searchTerm !== undefined)
									? String(searchTerm)
									: String(module.get.query())
									;
								return (searchTerm.length >= settings.minCharacters);
							}
							return true;
						},
						firstLetter: function ($item, letter) {
							var
								text,
								firstLetter
								;
							if (!$item || $item.length === 0 || typeof letter !== 'string') {
								return false;
							}
							text = module.get.choiceText($item, false);
							letter = letter.toLowerCase();
							firstLetter = String(text).charAt(0).toLowerCase();
							return (letter == firstLetter);
						},
						input: function () {
							return ($input.length > 0);
						},
						items: function () {
							return ($item.length > 0);
						},
						menu: function () {
							return ($menu.length > 0);
						},
						message: function () {
							return ($menu.children(selector.message).length !== 0);
						},
						label: function (value) {
							var
								escapedValue = module.escape.value(value),
								$labels = $module.find(selector.label)
								;
							if (settings.ignoreCase) {
								escapedValue = escapedValue.toLowerCase();
							}
							return ($labels.filter('[data-' + metadata.value + '="' + module.escape.string(escapedValue) + '"]').length > 0);
						},
						maxSelections: function () {
							return (settings.maxSelections && module.get.selectionCount() >= settings.maxSelections);
						},
						allResultsFiltered: function () {
							var
								$normalResults = $item.not(selector.addition)
								;
							return ($normalResults.filter(selector.unselectable).length === $normalResults.length);
						},
						userSuggestion: function () {
							return ($menu.children(selector.addition).length > 0);
						},
						query: function () {
							return (module.get.query() !== '');
						},
						value: function (value) {
							return (settings.ignoreCase)
								? module.has.valueIgnoringCase(value)
								: module.has.valueMatchingCase(value)
								;
						},
						valueMatchingCase: function (value) {
							var
								values = module.get.values(),
								hasValue = $.isArray(values)
									? values && ($.inArray(value, values) !== -1)
									: (values == value)
								;
							return (hasValue)
								? true
								: false
								;
						},
						valueIgnoringCase: function (value) {
							var
								values = module.get.values(),
								hasValue = false
								;
							if (!$.isArray(values)) {
								values = [values];
							}
							$.each(values, function (index, existingValue) {
								if (String(value).toLowerCase() == String(existingValue).toLowerCase()) {
									hasValue = true;
									return false;
								}
							});
							return hasValue;
						}
					},

					is: {
						active: function () {
							return $module.hasClass(className.active);
						},
						animatingInward: function () {
							return $menu.transition('is inward');
						},
						animatingOutward: function () {
							return $menu.transition('is outward');
						},
						bubbledLabelClick: function (event) {
							return $(event.target).is('select, input') && $module.closest('label').length > 0;
						},
						bubbledIconClick: function (event) {
							return $(event.target).closest($icon).length > 0;
						},
						alreadySetup: function () {
							return ($module.is('select') && $module.parent(selector.dropdown).data(moduleNamespace) !== undefined && $module.prev().length === 0);
						},
						animating: function ($subMenu) {
							return ($subMenu)
								? $subMenu.transition && $subMenu.transition('is animating')
								: $menu.transition && $menu.transition('is animating')
								;
						},
						leftward: function ($subMenu) {
							var $selectedMenu = $subMenu || $menu;
							return $selectedMenu.hasClass(className.leftward);
						},
						disabled: function () {
							return $module.hasClass(className.disabled);
						},
						focused: function () {
							return (document.activeElement === $module[0]);
						},
						focusedOnSearch: function () {
							return (document.activeElement === $search[0]);
						},
						allFiltered: function () {
							return ((module.is.multiple() || module.has.search()) && !(settings.hideAdditions == false && module.has.userSuggestion()) && !module.has.message() && module.has.allResultsFiltered());
						},
						hidden: function ($subMenu) {
							return !module.is.visible($subMenu);
						},
						initialLoad: function () {
							return initialLoad;
						},
						inObject: function (needle, object) {
							var
								found = false
								;
							$.each(object, function (index, property) {
								if (property == needle) {
									found = true;
									return true;
								}
							});
							return found;
						},
						multiple: function () {
							return $module.hasClass(className.multiple);
						},
						remote: function () {
							return settings.apiSettings && module.can.useAPI();
						},
						single: function () {
							return !module.is.multiple();
						},
						selectMutation: function (mutations) {
							var
								selectChanged = false
								;
							$.each(mutations, function (index, mutation) {
								if (mutation.target && $(mutation.target).is('select')) {
									selectChanged = true;
									return true;
								}
							});
							return selectChanged;
						},
						search: function () {
							return $module.hasClass(className.search);
						},
						searchSelection: function () {
							return (module.has.search() && $search.parent(selector.dropdown).length === 1);
						},
						selection: function () {
							return $module.hasClass(className.selection);
						},
						userValue: function (value) {
							return ($.inArray(value, module.get.userValues()) !== -1);
						},
						upward: function ($menu) {
							var $element = $menu || $module;
							return $element.hasClass(className.upward);
						},
						visible: function ($subMenu) {
							return ($subMenu)
								? $subMenu.hasClass(className.visible)
								: $menu.hasClass(className.visible)
								;
						},
						verticallyScrollableContext: function () {
							var
								overflowY = ($context.get(0) !== window)
									? $context.css('overflow-y')
									: false
								;
							return (overflowY == 'auto' || overflowY == 'scroll');
						},
						horizontallyScrollableContext: function () {
							var
								overflowX = ($context.get(0) !== window)
									? $context.css('overflow-X')
									: false
								;
							return (overflowX == 'auto' || overflowX == 'scroll');
						}
					},

					can: {
						activate: function ($item) {
							if (settings.useLabels) {
								return true;
							}
							if (!module.has.maxSelections()) {
								return true;
							}
							if (module.has.maxSelections() && $item.hasClass(className.active)) {
								return true;
							}
							return false;
						},
						openDownward: function ($subMenu) {
							var
								$currentMenu = $subMenu || $menu,
								canOpenDownward = true,
								onScreen = {},
								calculations
								;
							$currentMenu
								.addClass(className.loading)
								;
							calculations = {
								context: {
									offset: ($context.get(0) === window)
										? { top: 0, left: 0 }
										: $context.offset(),
									scrollTop: $context.scrollTop(),
									height: $context.outerHeight()
								},
								menu: {
									offset: $currentMenu.offset(),
									height: $currentMenu.outerHeight()
								}
							};
							if (module.is.verticallyScrollableContext()) {
								calculations.menu.offset.top += calculations.context.scrollTop;
							}
							onScreen = {
								above: (calculations.context.scrollTop) <= calculations.menu.offset.top - calculations.context.offset.top - calculations.menu.height,
								below: (calculations.context.scrollTop + calculations.context.height) >= calculations.menu.offset.top - calculations.context.offset.top + calculations.menu.height
							};
							if (onScreen.below) {
								module.verbose('Dropdown can fit in context downward', onScreen);
								canOpenDownward = true;
							}
							else if (!onScreen.below && !onScreen.above) {
								module.verbose('Dropdown cannot fit in either direction, favoring downward', onScreen);
								canOpenDownward = true;
							}
							else {
								module.verbose('Dropdown cannot fit below, opening upward', onScreen);
								canOpenDownward = false;
							}
							$currentMenu.removeClass(className.loading);
							return canOpenDownward;
						},
						openRightward: function ($subMenu) {
							var
								$currentMenu = $subMenu || $menu,
								canOpenRightward = true,
								isOffscreenRight = false,
								calculations
								;
							$currentMenu
								.addClass(className.loading)
								;
							calculations = {
								context: {
									offset: ($context.get(0) === window)
										? { top: 0, left: 0 }
										: $context.offset(),
									scrollLeft: $context.scrollLeft(),
									width: $context.outerWidth()
								},
								menu: {
									offset: $currentMenu.offset(),
									width: $currentMenu.outerWidth()
								}
							};
							if (module.is.horizontallyScrollableContext()) {
								calculations.menu.offset.left += calculations.context.scrollLeft;
							}
							isOffscreenRight = (calculations.menu.offset.left - calculations.context.offset.left + calculations.menu.width >= calculations.context.scrollLeft + calculations.context.width);
							if (isOffscreenRight) {
								module.verbose('Dropdown cannot fit in context rightward', isOffscreenRight);
								canOpenRightward = false;
							}
							$currentMenu.removeClass(className.loading);
							return canOpenRightward;
						},
						click: function () {
							return (hasTouch || settings.on == 'click');
						},
						extendSelect: function () {
							return settings.allowAdditions || settings.apiSettings;
						},
						show: function () {
							return !module.is.disabled() && (module.has.items() || module.has.message());
						},
						useAPI: function () {
							return $.fn.api !== undefined;
						}
					},

					animate: {
						show: function (callback, $subMenu) {
							var
								$currentMenu = $subMenu || $menu,
								start = ($subMenu)
									? function () { }
									: function () {
										module.hideSubMenus();
										module.hideOthers();
										module.set.active();
									},
								transition
								;
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							module.verbose('Doing menu show animation', $currentMenu);
							module.set.direction($subMenu);
							transition = module.get.transition($subMenu);
							if (module.is.selection()) {
								module.set.scrollPosition(module.get.selectedItem(), true);
							}
							if (module.is.hidden($currentMenu) || module.is.animating($currentMenu)) {
								if (transition == 'none') {
									start();
									$currentMenu.transition('show');
									callback.call(element);
								}
								else if ($.fn.transition !== undefined && $module.transition('is supported')) {
									$currentMenu
										.transition({
											animation: transition + ' in',
											debug: settings.debug,
											verbose: settings.verbose,
											duration: settings.duration,
											queue: true,
											onStart: start,
											onComplete: function () {
												callback.call(element);
											}
										})
										;
								}
								else {
									module.error(error.noTransition, transition);
								}
							}
						},
						hide: function (callback, $subMenu) {
							var
								$currentMenu = $subMenu || $menu,
								duration = ($subMenu)
									? (settings.duration * 0.9)
									: settings.duration,
								start = ($subMenu)
									? function () { }
									: function () {
										if (module.can.click()) {
											module.unbind.intent();
										}
										module.remove.active();
									},
								transition = module.get.transition($subMenu)
								;
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if (module.is.visible($currentMenu) || module.is.animating($currentMenu)) {
								module.verbose('Doing menu hide animation', $currentMenu);

								if (transition == 'none') {
									start();
									$currentMenu.transition('hide');
									callback.call(element);
								}
								else if ($.fn.transition !== undefined && $module.transition('is supported')) {
									$currentMenu
										.transition({
											animation: transition + ' out',
											duration: settings.duration,
											debug: settings.debug,
											verbose: settings.verbose,
											queue: false,
											onStart: start,
											onComplete: function () {
												callback.call(element);
											}
										})
										;
								}
								else {
									module.error(error.transition);
								}
							}
						}
					},

					hideAndClear: function () {
						module.remove.searchTerm();
						if (module.has.maxSelections()) {
							return;
						}
						if (module.has.search()) {
							module.hide(function () {
								module.remove.filteredItem();
							});
						}
						else {
							module.hide();
						}
					},

					delay: {
						show: function () {
							module.verbose('Delaying show event to ensure user intent');
							clearTimeout(module.timer);
							module.timer = setTimeout(module.show, settings.delay.show);
						},
						hide: function () {
							module.verbose('Delaying hide event to ensure user intent');
							clearTimeout(module.timer);
							module.timer = setTimeout(module.hide, settings.delay.hide);
						}
					},

					escape: {
						value: function (value) {
							var
								multipleValues = $.isArray(value),
								stringValue = (typeof value === 'string'),
								isUnparsable = (!stringValue && !multipleValues),
								hasQuotes = (stringValue && value.search(regExp.quote) !== -1),
								values = []
								;
							if (isUnparsable || !hasQuotes) {
								return value;
							}
							module.debug('Encoding quote values for use in select', value);
							if (multipleValues) {
								$.each(value, function (index, value) {
									values.push(value.replace(regExp.quote, '&quot;'));
								});
								return values;
							}
							return value.replace(regExp.quote, '&quot;');
						},
						string: function (text) {
							text = String(text);
							return text.replace(regExp.escape, '\\$&');
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;
		return (returnedValue !== undefined)
			? returnedValue
			: $allModules
			;
	};

	$.fn.dropdown.settings = {

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		on: 'click',    // what event should show menu action on item selection
		action: 'activate', // action on item selection (nothing, activate, select, combo, hide, function(){})

		values: false,      // specify values to use for dropdown

		clearable: false,      // whether the value of the dropdown can be cleared

		apiSettings: false,
		selectOnKeydown: true,       // Whether selection should occur automatically when keyboard shortcuts used
		minCharacters: 0,          // Minimum characters required to trigger API call

		filterRemoteData: false,      // Whether API results should be filtered after being returned for query term
		saveRemoteData: true,       // Whether remote name/value pairs should be stored in sessionStorage to allow remote data to be restored on page refresh

		throttle: 200,        // How long to wait after last user input to search remotely

		context: window,     // Context to use when determining if on screen
		direction: 'auto',     // Whether dropdown should always open in one direction
		keepOnScreen: true,       // Whether dropdown should check whether it is on screen before showing

		match: 'both',     // what to match against with search selection (both, text, or label)
		fullTextSearch: false,      // search anywhere in value (set to 'exact' to require exact matches)

		placeholder: 'auto',     // whether to convert blank <select> values to placeholder text
		preserveHTML: true,       // preserve html when selecting value
		sortSelect: false,      // sort selection on init

		forceSelection: true,       // force a choice on blur with search selection

		allowAdditions: false,      // whether multiple select should allow user added values
		ignoreCase: false,       // whether to consider values not matching in case to be the same
		hideAdditions: true,       // whether or not to hide special message prompting a user they can enter a value

		maxSelections: false,      // When set to a number limits the number of selections to this count
		useLabels: true,       // whether multiple select should filter currently active selections from choices
		delimiter: ',',        // when multiselect uses normal <input> the values will be delimited with this character

		showOnFocus: true,       // show menu on focus
		allowReselection: false,      // whether current value should trigger callbacks when reselected
		allowTab: true,       // add tabindex to element
		allowCategorySelection: false,      // allow elements with sub-menus to be selected

		fireOnInit: false,      // Whether callbacks should fire when initializing dropdown values

		transition: 'auto',     // auto transition will slide down or up based on direction
		duration: 200,        // duration of transition

		glyphWidth: 1.037,      // widest glyph width in em (W is 1.037 em) used to calculate multiselect input width

		// label settings on multi-select
		label: {
			transition: 'scale',
			duration: 200,
			variation: false
		},

		// delay before event
		delay: {
			hide: 300,
			show: 200,
			search: 20,
			touch: 50
		},

		/* Callbacks */
		onChange: function (value, text, $selected) { },
		onAdd: function (value, text, $selected) { },
		onRemove: function (value, text, $selected) { },

		onLabelSelect: function ($selectedLabels) { },
		onLabelCreate: function (value, text) { return $(this); },
		onLabelRemove: function (value) { return true; },
		onNoResults: function (searchTerm) { return true; },
		onShow: function () { },
		onHide: function () { },

		/* Component */
		name: 'Dropdown',
		namespace: 'dropdown',

		message: {
			addResult: 'Add <b>{term}</b>',
			count: '{count} selected',
			maxSelections: 'Max {maxCount} selections',
			noResults: 'No results found.',
			serverError: 'There was an error contacting the server'
		},

		error: {
			action: 'You called a dropdown action that was not defined',
			alreadySetup: 'Once a select has been initialized behaviors must be called on the created ui dropdown',
			labels: 'Allowing user additions currently requires the use of labels.',
			missingMultiple: '<select> requires multiple property to be set to correctly preserve multiple values',
			method: 'The method you called is not defined.',
			noAPI: 'The API module is required to load resources remotely',
			noStorage: 'Saving remote data requires session storage',
			noTransition: 'This module requires ui transitions <https://github.com/Semantic-Org/UI-Transition>'
		},

		regExp: {
			escape: /[-[\]{}()*+?.,\\^$|#\s]/g,
			quote: /"/g
		},

		metadata: {
			defaultText: 'defaultText',
			defaultValue: 'defaultValue',
			placeholderText: 'placeholder',
			text: 'text',
			value: 'value'
		},

		// property names for remote query
		fields: {
			remoteValues: 'results',  // grouping for api results
			values: 'values',   // grouping for all dropdown values
			disabled: 'disabled', // whether value should be disabled
			name: 'name',     // displayed dropdown text
			value: 'value',    // actual dropdown value
			text: 'text'      // displayed text when selected
		},

		keys: {
			backspace: 8,
			delimiter: 188, // comma
			deleteKey: 46,
			enter: 13,
			escape: 27,
			pageUp: 33,
			pageDown: 34,
			leftArrow: 37,
			upArrow: 38,
			rightArrow: 39,
			downArrow: 40
		},

		selector: {
			addition: '.addition',
			dropdown: '.ui.dropdown',
			hidden: '.hidden',
			icon: '> .dropdown.icon',
			input: '> input[type="hidden"], > select',
			item: '.item',
			label: '> .label',
			remove: '> .label > .delete.icon',
			siblingLabel: '.label',
			menu: '.menu',
			message: '.message',
			menuIcon: '.dropdown.icon',
			search: 'input.search, .menu > .search > input, .menu input.search',
			sizer: '> input.sizer',
			text: '> .text:not(.icon)',
			unselectable: '.disabled, .filtered'
		},

		className: {
			active: 'active',
			addition: 'addition',
			animating: 'animating',
			clear: 'clear',
			disabled: 'disabled',
			empty: 'empty',
			dropdown: 'ui dropdown',
			filtered: 'filtered',
			hidden: 'hidden transition',
			item: 'item',
			label: 'ui label',
			loading: 'loading',
			menu: 'menu',
			message: 'message',
			multiple: 'multiple',
			placeholder: 'default',
			sizer: 'sizer',
			search: 'search',
			selected: 'selected',
			selection: 'selection',
			upward: 'upward',
			leftward: 'left',
			visible: 'visible'
		}

	};

	/* Templates */
	$.fn.dropdown.settings.templates = {

		// generates dropdown from select values
		dropdown: function (select) {
			var
				placeholder = select.placeholder || false,
				values = select.values || {},
				html = ''
				;
			html += '<i class="dropdown icon"></i>';
			if (select.placeholder) {
				html += '<div class="default text">' + placeholder + '</div>';
			}
			else {
				html += '<div class="text"></div>';
			}
			html += '<div class="menu">';
			$.each(select.values, function (index, option) {
				html += (option.disabled)
					? '<div class="disabled item" data-value="' + option.value + '">' + option.name + '</div>'
					: '<div class="item" data-value="' + option.value + '">' + option.name + '</div>'
					;
			});
			html += '</div>';
			return html;
		},

		// generates just menu from select
		menu: function (response, fields) {
			var
				values = response[fields.values] || {},
				html = ''
				;
			$.each(values, function (index, option) {
				var
					maybeText = (option[fields.text])
						? 'data-text="' + option[fields.text] + '"'
						: '',
					maybeDisabled = (option[fields.disabled])
						? 'disabled '
						: ''
					;
				html += '<div class="' + maybeDisabled + 'item" data-value="' + option[fields.value] + '"' + maybeText + '>';
				html += option[fields.name];
				html += '</div>';
			});
			return html;
		},

		// generates label for multiselect
		label: function (value, text) {
			return text + '<i class="delete icon"></i>';
		},


		// generates messages like "No results"
		message: function (message) {
			return message;
		},

		// generates user addition to selection menu
		addition: function (choice) {
			return choice;
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Embed
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	"use strict";

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.embed = function (parameters) {

		var
			$allModules = $(this),

			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.embed.settings, parameters)
						: $.extend({}, $.fn.embed.settings),

					selector = settings.selector,
					className = settings.className,
					sources = settings.sources,
					error = settings.error,
					metadata = settings.metadata,
					namespace = settings.namespace,
					templates = settings.templates,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$window = $(window),
					$module = $(this),
					$placeholder = $module.find(selector.placeholder),
					$icon = $module.find(selector.icon),
					$embed = $module.find(selector.embed),

					element = this,
					instance = $module.data(moduleNamespace),
					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing embed');
						module.determine.autoplay();
						module.create();
						module.bind.events();
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous instance of embed');
						module.reset();
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
					},

					refresh: function () {
						module.verbose('Refreshing selector cache');
						$placeholder = $module.find(selector.placeholder);
						$icon = $module.find(selector.icon);
						$embed = $module.find(selector.embed);
					},

					bind: {
						events: function () {
							if (module.has.placeholder()) {
								module.debug('Adding placeholder events');
								$module
									.on('click' + eventNamespace, selector.placeholder, module.createAndShow)
									.on('click' + eventNamespace, selector.icon, module.createAndShow)
									;
							}
						}
					},

					create: function () {
						var
							placeholder = module.get.placeholder()
							;
						if (placeholder) {
							module.createPlaceholder();
						}
						else {
							module.createAndShow();
						}
					},

					createPlaceholder: function (placeholder) {
						var
							icon = module.get.icon(),
							url = module.get.url(),
							embed = module.generate.embed(url)
							;
						placeholder = placeholder || module.get.placeholder();
						$module.html(templates.placeholder(placeholder, icon));
						module.debug('Creating placeholder for embed', placeholder, icon);
					},

					createEmbed: function (url) {
						module.refresh();
						url = url || module.get.url();
						$embed = $('<div/>')
							.addClass(className.embed)
							.html(module.generate.embed(url))
							.appendTo($module)
							;
						settings.onCreate.call(element, url);
						module.debug('Creating embed object', $embed);
					},

					changeEmbed: function (url) {
						$embed
							.html(module.generate.embed(url))
							;
					},

					createAndShow: function () {
						module.createEmbed();
						module.show();
					},

					// sets new embed
					change: function (source, id, url) {
						module.debug('Changing video to ', source, id, url);
						$module
							.data(metadata.source, source)
							.data(metadata.id, id)
							;
						if (url) {
							$module.data(metadata.url, url);
						}
						else {
							$module.removeData(metadata.url);
						}
						if (module.has.embed()) {
							module.changeEmbed();
						}
						else {
							module.create();
						}
					},

					// clears embed
					reset: function () {
						module.debug('Clearing embed and showing placeholder');
						module.remove.data();
						module.remove.active();
						module.remove.embed();
						module.showPlaceholder();
						settings.onReset.call(element);
					},

					// shows current embed
					show: function () {
						module.debug('Showing embed');
						module.set.active();
						settings.onDisplay.call(element);
					},

					hide: function () {
						module.debug('Hiding embed');
						module.showPlaceholder();
					},

					showPlaceholder: function () {
						module.debug('Showing placeholder image');
						module.remove.active();
						settings.onPlaceholderDisplay.call(element);
					},

					get: {
						id: function () {
							return settings.id || $module.data(metadata.id);
						},
						placeholder: function () {
							return settings.placeholder || $module.data(metadata.placeholder);
						},
						icon: function () {
							return (settings.icon)
								? settings.icon
								: ($module.data(metadata.icon) !== undefined)
									? $module.data(metadata.icon)
									: module.determine.icon()
								;
						},
						source: function (url) {
							return (settings.source)
								? settings.source
								: ($module.data(metadata.source) !== undefined)
									? $module.data(metadata.source)
									: module.determine.source()
								;
						},
						type: function () {
							var source = module.get.source();
							return (sources[source] !== undefined)
								? sources[source].type
								: false
								;
						},
						url: function () {
							return (settings.url)
								? settings.url
								: ($module.data(metadata.url) !== undefined)
									? $module.data(metadata.url)
									: module.determine.url()
								;
						}
					},

					determine: {
						autoplay: function () {
							if (module.should.autoplay()) {
								settings.autoplay = true;
							}
						},
						source: function (url) {
							var
								matchedSource = false
								;
							url = url || module.get.url();
							if (url) {
								$.each(sources, function (name, source) {
									if (url.search(source.domain) !== -1) {
										matchedSource = name;
										return false;
									}
								});
							}
							return matchedSource;
						},
						icon: function () {
							var
								source = module.get.source()
								;
							return (sources[source] !== undefined)
								? sources[source].icon
								: false
								;
						},
						url: function () {
							var
								id = settings.id || $module.data(metadata.id),
								source = settings.source || $module.data(metadata.source),
								url
								;
							url = (sources[source] !== undefined)
								? sources[source].url.replace('{id}', id)
								: false
								;
							if (url) {
								$module.data(metadata.url, url);
							}
							return url;
						}
					},


					set: {
						active: function () {
							$module.addClass(className.active);
						}
					},

					remove: {
						data: function () {
							$module
								.removeData(metadata.id)
								.removeData(metadata.icon)
								.removeData(metadata.placeholder)
								.removeData(metadata.source)
								.removeData(metadata.url)
								;
						},
						active: function () {
							$module.removeClass(className.active);
						},
						embed: function () {
							$embed.empty();
						}
					},

					encode: {
						parameters: function (parameters) {
							var
								urlString = [],
								index
								;
							for (index in parameters) {
								urlString.push(encodeURIComponent(index) + '=' + encodeURIComponent(parameters[index]));
							}
							return urlString.join('&amp;');
						}
					},

					generate: {
						embed: function (url) {
							module.debug('Generating embed html');
							var
								source = module.get.source(),
								html,
								parameters
								;
							url = module.get.url(url);
							if (url) {
								parameters = module.generate.parameters(source);
								html = templates.iframe(url, parameters);
							}
							else {
								module.error(error.noURL, $module);
							}
							return html;
						},
						parameters: function (source, extraParameters) {
							var
								parameters = (sources[source] && sources[source].parameters !== undefined)
									? sources[source].parameters(settings)
									: {}
								;
							extraParameters = extraParameters || settings.parameters;
							if (extraParameters) {
								parameters = $.extend({}, parameters, extraParameters);
							}
							parameters = settings.onEmbed(parameters);
							return module.encode.parameters(parameters);
						}
					},

					has: {
						embed: function () {
							return ($embed.length > 0);
						},
						placeholder: function () {
							return settings.placeholder || $module.data(metadata.placeholder);
						}
					},

					should: {
						autoplay: function () {
							return (settings.autoplay === 'auto')
								? (settings.placeholder || $module.data(metadata.placeholder) !== undefined)
								: settings.autoplay
								;
						}
					},

					is: {
						video: function () {
							return module.get.type() == 'video';
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;
		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.embed.settings = {

		name: 'Embed',
		namespace: 'embed',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		icon: false,
		source: false,
		url: false,
		id: false,

		// standard video settings
		autoplay: 'auto',
		color: '#444444',
		hd: true,
		brandedUI: false,

		// additional parameters to include with the embed
		parameters: false,

		onDisplay: function () { },
		onPlaceholderDisplay: function () { },
		onReset: function () { },
		onCreate: function (url) { },
		onEmbed: function (parameters) {
			return parameters;
		},

		metadata: {
			id: 'id',
			icon: 'icon',
			placeholder: 'placeholder',
			source: 'source',
			url: 'url'
		},

		error: {
			noURL: 'No URL specified',
			method: 'The method you called is not defined'
		},

		className: {
			active: 'active',
			embed: 'embed'
		},

		selector: {
			embed: '.embed',
			placeholder: '.placeholder',
			icon: '.icon'
		},

		sources: {
			youtube: {
				name: 'youtube',
				type: 'video',
				icon: 'video play',
				domain: 'youtube.com',
				url: '//www.youtube.com/embed/{id}',
				parameters: function (settings) {
					return {
						autohide: !settings.brandedUI,
						autoplay: settings.autoplay,
						color: settings.color || undefined,
						hq: settings.hd,
						jsapi: settings.api,
						modestbranding: !settings.brandedUI
					};
				}
			},
			vimeo: {
				name: 'vimeo',
				type: 'video',
				icon: 'video play',
				domain: 'vimeo.com',
				url: '//player.vimeo.com/video/{id}',
				parameters: function (settings) {
					return {
						api: settings.api,
						autoplay: settings.autoplay,
						byline: settings.brandedUI,
						color: settings.color || undefined,
						portrait: settings.brandedUI,
						title: settings.brandedUI
					};
				}
			}
		},

		templates: {
			iframe: function (url, parameters) {
				var src = url;
				if (parameters) {
					src += '?' + parameters;
				}
				return ''
					+ '<iframe src="' + src + '"'
					+ ' width="100%" height="100%"'
					+ ' frameborder="0" scrolling="no" webkitAllowFullScreen mozallowfullscreen allowFullScreen></iframe>'
					;
			},
			placeholder: function (image, icon) {
				var
					html = ''
					;
				if (icon) {
					html += '<i class="' + icon + ' icon"></i>';
				}
				if (image) {
					html += '<img class="placeholder" src="' + image + '">';
				}
				return html;
			}
		},

		// NOT YET IMPLEMENTED
		api: false,
		onPause: function () { },
		onPlay: function () { },
		onStop: function () { }

	};



})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Modal
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.modal = function (parameters) {
		var
			$allModules = $(this),
			$window = $(window),
			$document = $(document),
			$body = $('body'),

			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			requestAnimationFrame = window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.msRequestAnimationFrame
				|| function (callback) { setTimeout(callback, 0); },

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.modal.settings, parameters)
						: $.extend({}, $.fn.modal.settings),

					selector = settings.selector,
					className = settings.className,
					namespace = settings.namespace,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$context = $(settings.context),
					$close = $module.find(selector.close),

					$allModals,
					$otherModals,
					$focusedElement,
					$dimmable,
					$dimmer,

					element = this,
					instance = $module.data(moduleNamespace),

					ignoreRepeatedEvents = false,

					elementEventNamespace,
					id,
					observer,
					module
					;
				module = {

					initialize: function () {
						module.verbose('Initializing dimmer', $context);

						module.create.id();
						module.create.dimmer();
						module.refreshModals();

						module.bind.events();
						if (settings.observeChanges) {
							module.observeChanges();
						}
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of modal');
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					create: {
						dimmer: function () {
							var
								defaultSettings = {
									debug: settings.debug,
									variation: settings.centered
										? false
										: 'top aligned',
									dimmerName: 'modals'
								},
								dimmerSettings = $.extend(true, defaultSettings, settings.dimmerSettings)
								;
							if ($.fn.dimmer === undefined) {
								module.error(error.dimmer);
								return;
							}
							module.debug('Creating dimmer');
							$dimmable = $context.dimmer(dimmerSettings);
							if (settings.detachable) {
								module.verbose('Modal is detachable, moving content into dimmer');
								$dimmable.dimmer('add content', $module);
							}
							else {
								module.set.undetached();
							}
							$dimmer = $dimmable.dimmer('get dimmer');
						},
						id: function () {
							id = (Math.random().toString(16) + '000000000').substr(2, 8);
							elementEventNamespace = '.' + id;
							module.verbose('Creating unique id for element', id);
						}
					},

					destroy: function () {
						if (observer) {
							observer.disconnect();
						}
						module.verbose('Destroying previous modal');
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
						$window.off(elementEventNamespace);
						$dimmer.off(elementEventNamespace);
						$close.off(eventNamespace);
						$context.dimmer('destroy');
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							observer = new MutationObserver(function (mutations) {
								module.debug('DOM tree modified, refreshing');
								module.refresh();
							});
							observer.observe(element, {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', observer);
						}
					},

					refresh: function () {
						module.remove.scrolling();
						module.cacheSizes();
						if (!module.can.useFlex()) {
							module.set.modalOffset();
						}
						module.set.screenHeight();
						module.set.type();
					},

					refreshModals: function () {
						$otherModals = $module.siblings(selector.modal);
						$allModals = $otherModals.add($module);
					},

					attachEvents: function (selector, event) {
						var
							$toggle = $(selector)
							;
						event = $.isFunction(module[event])
							? module[event]
							: module.toggle
							;
						if ($toggle.length > 0) {
							module.debug('Attaching modal events to element', selector, event);
							$toggle
								.off(eventNamespace)
								.on('click' + eventNamespace, event)
								;
						}
						else {
							module.error(error.notFound, selector);
						}
					},

					bind: {
						events: function () {
							module.verbose('Attaching events');
							$module
								.on('click' + eventNamespace, selector.close, module.event.close)
								.on('click' + eventNamespace, selector.approve, module.event.approve)
								.on('click' + eventNamespace, selector.deny, module.event.deny)
								;
							$window
								.on('resize' + elementEventNamespace, module.event.resize)
								;
						},
						scrollLock: function () {
							// touch events default to passive, due to changes in chrome to optimize mobile perf
							$dimmable.get(0).addEventListener('touchmove', module.event.preventScroll, { passive: false });
						}
					},

					unbind: {
						scrollLock: function () {
							$dimmable.get(0).removeEventListener('touchmove', module.event.preventScroll, { passive: false });
						}
					},

					get: {
						id: function () {
							return (Math.random().toString(16) + '000000000').substr(2, 8);
						}
					},

					event: {
						approve: function () {
							if (ignoreRepeatedEvents || settings.onApprove.call(element, $(this)) === false) {
								module.verbose('Approve callback returned false cancelling hide');
								return;
							}
							ignoreRepeatedEvents = true;
							module.hide(function () {
								ignoreRepeatedEvents = false;
							});
						},
						preventScroll: function (event) {
							event.preventDefault();
						},
						deny: function () {
							if (ignoreRepeatedEvents || settings.onDeny.call(element, $(this)) === false) {
								module.verbose('Deny callback returned false cancelling hide');
								return;
							}
							ignoreRepeatedEvents = true;
							module.hide(function () {
								ignoreRepeatedEvents = false;
							});
						},
						close: function () {
							module.hide();
						},
						click: function (event) {
							if (!settings.closable) {
								module.verbose('Dimmer clicked but closable setting is disabled');
								return;
							}
							var
								$target = $(event.target),
								isInModal = ($target.closest(selector.modal).length > 0),
								isInDOM = $.contains(document.documentElement, event.target)
								;
							if (!isInModal && isInDOM && module.is.active()) {
								module.debug('Dimmer clicked, hiding all modals');
								module.remove.clickaway();
								if (settings.allowMultiple) {
									module.hide();
								}
								else {
									module.hideAll();
								}
							}
						},
						debounce: function (method, delay) {
							clearTimeout(module.timer);
							module.timer = setTimeout(method, delay);
						},
						keyboard: function (event) {
							var
								keyCode = event.which,
								escapeKey = 27
								;
							if (keyCode == escapeKey) {
								if (settings.closable) {
									module.debug('Escape key pressed hiding modal');
									module.hide();
								}
								else {
									module.debug('Escape key pressed, but closable is set to false');
								}
								event.preventDefault();
							}
						},
						resize: function () {
							if ($dimmable.dimmer('is active') && (module.is.animating() || module.is.active())) {
								requestAnimationFrame(module.refresh);
							}
						}
					},

					toggle: function () {
						if (module.is.active() || module.is.animating()) {
							module.hide();
						}
						else {
							module.show();
						}
					},

					show: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						module.refreshModals();
						module.set.dimmerSettings();
						module.set.dimmerStyles();

						module.showModal(callback);
					},

					hide: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						module.refreshModals();
						module.hideModal(callback);
					},

					showModal: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.animating() || !module.is.active()) {
							module.showDimmer();
							module.cacheSizes();
							if (module.can.useFlex()) {
								module.remove.legacy();
							}
							else {
								module.set.legacy();
								module.set.modalOffset();
								module.debug('Using non-flex legacy modal positioning.');
							}
							module.set.screenHeight();
							module.set.type();
							module.set.clickaway();

							if (!settings.allowMultiple && module.others.active()) {
								module.hideOthers(module.showModal);
							}
							else {
								if (settings.allowMultiple && settings.detachable) {
									$module.detach().appendTo($dimmer);
								}
								settings.onShow.call(element);
								if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
									module.debug('Showing modal with css animations');
									$module
										.transition({
											debug: settings.debug,
											animation: settings.transition + ' in',
											queue: settings.queue,
											duration: settings.duration,
											useFailSafe: true,
											onComplete: function () {
												settings.onVisible.apply(element);
												if (settings.keyboardShortcuts) {
													module.add.keyboardShortcuts();
												}
												module.save.focus();
												module.set.active();
												if (settings.autofocus) {
													module.set.autofocus();
												}
												callback();
											}
										})
										;
								}
								else {
									module.error(error.noTransition);
								}
							}
						}
						else {
							module.debug('Modal is already visible');
						}
					},

					hideModal: function (callback, keepDimmed) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						module.debug('Hiding modal');
						if (settings.onHide.call(element, $(this)) === false) {
							module.verbose('Hide callback returned false cancelling hide');
							return;
						}

						if (module.is.animating() || module.is.active()) {
							if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
								module.remove.active();
								$module
									.transition({
										debug: settings.debug,
										animation: settings.transition + ' out',
										queue: settings.queue,
										duration: settings.duration,
										useFailSafe: true,
										onStart: function () {
											if (!module.others.active() && !keepDimmed) {
												module.hideDimmer();
											}
											if (settings.keyboardShortcuts) {
												module.remove.keyboardShortcuts();
											}
										},
										onComplete: function () {
											settings.onHidden.call(element);
											module.remove.dimmerStyles();
											module.restore.focus();
											callback();
										}
									})
									;
							}
							else {
								module.error(error.noTransition);
							}
						}
					},

					showDimmer: function () {
						if ($dimmable.dimmer('is animating') || !$dimmable.dimmer('is active')) {
							module.debug('Showing dimmer');
							$dimmable.dimmer('show');
						}
						else {
							module.debug('Dimmer already visible');
						}
					},

					hideDimmer: function () {
						if ($dimmable.dimmer('is animating') || ($dimmable.dimmer('is active'))) {
							module.unbind.scrollLock();
							$dimmable.dimmer('hide', function () {
								module.remove.clickaway();
								module.remove.screenHeight();
							});
						}
						else {
							module.debug('Dimmer is not visible cannot hide');
							return;
						}
					},

					hideAll: function (callback) {
						var
							$visibleModals = $allModals.filter('.' + className.active + ', .' + className.animating)
							;
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if ($visibleModals.length > 0) {
							module.debug('Hiding all visible modals');
							module.hideDimmer();
							$visibleModals
								.modal('hide modal', callback)
								;
						}
					},

					hideOthers: function (callback) {
						var
							$visibleModals = $otherModals.filter('.' + className.active + ', .' + className.animating)
							;
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if ($visibleModals.length > 0) {
							module.debug('Hiding other modals', $otherModals);
							$visibleModals
								.modal('hide modal', callback, true)
								;
						}
					},

					others: {
						active: function () {
							return ($otherModals.filter('.' + className.active).length > 0);
						},
						animating: function () {
							return ($otherModals.filter('.' + className.animating).length > 0);
						}
					},


					add: {
						keyboardShortcuts: function () {
							module.verbose('Adding keyboard shortcuts');
							$document
								.on('keyup' + eventNamespace, module.event.keyboard)
								;
						}
					},

					save: {
						focus: function () {
							var
								$activeElement = $(document.activeElement),
								inCurrentModal = $activeElement.closest($module).length > 0
								;
							if (!inCurrentModal) {
								$focusedElement = $(document.activeElement).blur();
							}
						}
					},

					restore: {
						focus: function () {
							if ($focusedElement && $focusedElement.length > 0) {
								$focusedElement.focus();
							}
						}
					},

					remove: {
						active: function () {
							$module.removeClass(className.active);
						},
						legacy: function () {
							$module.removeClass(className.legacy);
						},
						clickaway: function () {
							$dimmer
								.off('click' + elementEventNamespace)
								;
						},
						dimmerStyles: function () {
							$dimmer.removeClass(className.inverted);
							$dimmable.removeClass(className.blurring);
						},
						bodyStyle: function () {
							if ($body.attr('style') === '') {
								module.verbose('Removing style attribute');
								$body.removeAttr('style');
							}
						},
						screenHeight: function () {
							module.debug('Removing page height');
							$body
								.css('height', '')
								;
						},
						keyboardShortcuts: function () {
							module.verbose('Removing keyboard shortcuts');
							$document
								.off('keyup' + eventNamespace)
								;
						},
						scrolling: function () {
							$dimmable.removeClass(className.scrolling);
							$module.removeClass(className.scrolling);
						}
					},

					cacheSizes: function () {
						$module.addClass(className.loading);
						var
							scrollHeight = $module.prop('scrollHeight'),
							modalWidth = $module.outerWidth(),
							modalHeight = $module.outerHeight()
							;
						if (module.cache === undefined || modalHeight !== 0) {
							module.cache = {
								pageHeight: $(document).outerHeight(),
								width: modalWidth,
								height: modalHeight + settings.offset,
								scrollHeight: scrollHeight + settings.offset,
								contextHeight: (settings.context == 'body')
									? $(window).height()
									: $dimmable.height(),
							};
							module.cache.topOffset = -(module.cache.height / 2);
						}
						$module.removeClass(className.loading);
						module.debug('Caching modal and container sizes', module.cache);
					},

					can: {
						useFlex: function () {
							return (settings.useFlex == 'auto')
								? settings.detachable && !module.is.ie()
								: settings.useFlex
								;
						},
						fit: function () {
							var
								contextHeight = module.cache.contextHeight,
								verticalCenter = module.cache.contextHeight / 2,
								topOffset = module.cache.topOffset,
								scrollHeight = module.cache.scrollHeight,
								height = module.cache.height,
								paddingHeight = settings.padding,
								startPosition = (verticalCenter + topOffset)
								;
							return (scrollHeight > height)
								? (startPosition + scrollHeight + paddingHeight < contextHeight)
								: (height + (paddingHeight * 2) < contextHeight)
								;
						}
					},

					is: {
						active: function () {
							return $module.hasClass(className.active);
						},
						ie: function () {
							var
								isIE11 = (!(window.ActiveXObject) && 'ActiveXObject' in window),
								isIE = ('ActiveXObject' in window)
								;
							return (isIE11 || isIE);
						},
						animating: function () {
							return $module.transition('is supported')
								? $module.transition('is animating')
								: $module.is(':visible')
								;
						},
						scrolling: function () {
							return $dimmable.hasClass(className.scrolling);
						},
						modernBrowser: function () {
							// appName for IE11 reports 'Netscape' can no longer use
							return !(window.ActiveXObject || 'ActiveXObject' in window);
						}
					},

					set: {
						autofocus: function () {
							var
								$inputs = $module.find('[tabindex], :input').filter(':visible'),
								$autofocus = $inputs.filter('[autofocus]'),
								$input = ($autofocus.length > 0)
									? $autofocus.first()
									: $inputs.first()
								;
							if ($input.length > 0) {
								$input.focus();
							}
						},
						clickaway: function () {
							$dimmer
								.on('click' + elementEventNamespace, module.event.click)
								;
						},
						dimmerSettings: function () {
							if ($.fn.dimmer === undefined) {
								module.error(error.dimmer);
								return;
							}
							var
								defaultSettings = {
									debug: settings.debug,
									dimmerName: 'modals',
									closable: 'auto',
									useFlex: module.can.useFlex(),
									variation: settings.centered
										? false
										: 'top aligned',
									duration: {
										show: settings.duration,
										hide: settings.duration
									}
								},
								dimmerSettings = $.extend(true, defaultSettings, settings.dimmerSettings)
								;
							if (settings.inverted) {
								dimmerSettings.variation = (dimmerSettings.variation !== undefined)
									? dimmerSettings.variation + ' inverted'
									: 'inverted'
									;
							}
							$context.dimmer('setting', dimmerSettings);
						},
						dimmerStyles: function () {
							if (settings.inverted) {
								$dimmer.addClass(className.inverted);
							}
							else {
								$dimmer.removeClass(className.inverted);
							}
							if (settings.blurring) {
								$dimmable.addClass(className.blurring);
							}
							else {
								$dimmable.removeClass(className.blurring);
							}
						},
						modalOffset: function () {
							var
								width = module.cache.width,
								height = module.cache.height
								;
							$module
								.css({
									marginTop: (settings.centered && module.can.fit())
										? -(height / 2)
										: 0,
									marginLeft: -(width / 2)
								})
								;
							module.verbose('Setting modal offset for legacy mode');
						},
						screenHeight: function () {
							if (module.can.fit()) {
								$body.css('height', '');
							}
							else {
								module.debug('Modal is taller than page content, resizing page height');
								$body
									.css('height', module.cache.height + (settings.padding * 2))
									;
							}
						},
						active: function () {
							$module.addClass(className.active);
						},
						scrolling: function () {
							$dimmable.addClass(className.scrolling);
							$module.addClass(className.scrolling);
							module.unbind.scrollLock();
						},
						legacy: function () {
							$module.addClass(className.legacy);
						},
						type: function () {
							if (module.can.fit()) {
								module.verbose('Modal fits on screen');
								if (!module.others.active() && !module.others.animating()) {
									module.remove.scrolling();
									module.bind.scrollLock();
								}
							}
							else {
								module.verbose('Modal cannot fit on screen setting to scrolling');
								module.set.scrolling();
							}
						},
						undetached: function () {
							$dimmable.addClass(className.undetached);
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.modal.settings = {

		name: 'Modal',
		namespace: 'modal',

		useFlex: 'auto',
		offset: 0,

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		observeChanges: false,

		allowMultiple: false,
		detachable: true,
		closable: true,
		autofocus: true,

		inverted: false,
		blurring: false,

		centered: true,

		dimmerSettings: {
			closable: false,
			useCSS: true
		},

		// whether to use keyboard shortcuts
		keyboardShortcuts: true,

		context: 'body',

		queue: false,
		duration: 500,
		transition: 'scale',

		// padding with edge of page
		padding: 50,

		// called before show animation
		onShow: function () { },

		// called after show animation
		onVisible: function () { },

		// called before hide animation
		onHide: function () { return true; },

		// called after hide animation
		onHidden: function () { },

		// called after approve selector match
		onApprove: function () { return true; },

		// called after deny selector match
		onDeny: function () { return true; },

		selector: {
			close: '> .close',
			approve: '.actions .positive, .actions .approve, .actions .ok',
			deny: '.actions .negative, .actions .deny, .actions .cancel',
			modal: '.ui.modal'
		},
		error: {
			dimmer: 'UI Dimmer, a required component is not included in this page',
			method: 'The method you called is not defined.',
			notFound: 'The element you specified could not be found'
		},
		className: {
			active: 'active',
			animating: 'animating',
			blurring: 'blurring',
			inverted: 'inverted',
			legacy: 'legacy',
			loading: 'loading',
			scrolling: 'scrolling',
			undetached: 'undetached'
		}
	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Nag
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.nag = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;
		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.nag.settings, parameters)
						: $.extend({}, $.fn.nag.settings),

					className = settings.className,
					selector = settings.selector,
					error = settings.error,
					namespace = settings.namespace,

					eventNamespace = '.' + namespace,
					moduleNamespace = namespace + '-module',

					$module = $(this),

					$close = $module.find(selector.close),
					$context = (settings.context)
						? $(settings.context)
						: $('body'),

					element = this,
					instance = $module.data(moduleNamespace),

					moduleOffset,
					moduleHeight,

					contextWidth,
					contextHeight,
					contextOffset,

					yOffset,
					yPosition,

					timer,
					module,

					requestAnimationFrame = window.requestAnimationFrame
						|| window.mozRequestAnimationFrame
						|| window.webkitRequestAnimationFrame
						|| window.msRequestAnimationFrame
						|| function (callback) { setTimeout(callback, 0); }
					;
				module = {

					initialize: function () {
						module.verbose('Initializing element');

						$module
							.on('click' + eventNamespace, selector.close, module.dismiss)
							.data(moduleNamespace, module)
							;

						if (settings.detachable && $module.parent()[0] !== $context[0]) {
							$module
								.detach()
								.prependTo($context)
								;
						}

						if (settings.displayTime > 0) {
							setTimeout(module.hide, settings.displayTime);
						}
						module.show();
					},

					destroy: function () {
						module.verbose('Destroying instance');
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
					},

					show: function () {
						if (module.should.show() && !$module.is(':visible')) {
							module.debug('Showing nag', settings.animation.show);
							if (settings.animation.show == 'fade') {
								$module
									.fadeIn(settings.duration, settings.easing)
									;
							}
							else {
								$module
									.slideDown(settings.duration, settings.easing)
									;
							}
						}
					},

					hide: function () {
						module.debug('Showing nag', settings.animation.hide);
						if (settings.animation.show == 'fade') {
							$module
								.fadeIn(settings.duration, settings.easing)
								;
						}
						else {
							$module
								.slideUp(settings.duration, settings.easing)
								;
						}
					},

					onHide: function () {
						module.debug('Removing nag', settings.animation.hide);
						$module.remove();
						if (settings.onHide) {
							settings.onHide();
						}
					},

					dismiss: function (event) {
						if (settings.storageMethod) {
							module.storage.set(settings.key, settings.value);
						}
						module.hide();
						event.stopImmediatePropagation();
						event.preventDefault();
					},

					should: {
						show: function () {
							if (settings.persist) {
								module.debug('Persistent nag is set, can show nag');
								return true;
							}
							if (module.storage.get(settings.key) != settings.value.toString()) {
								module.debug('Stored value is not set, can show nag', module.storage.get(settings.key));
								return true;
							}
							module.debug('Stored value is set, cannot show nag', module.storage.get(settings.key));
							return false;
						}
					},

					get: {
						storageOptions: function () {
							var
								options = {}
								;
							if (settings.expires) {
								options.expires = settings.expires;
							}
							if (settings.domain) {
								options.domain = settings.domain;
							}
							if (settings.path) {
								options.path = settings.path;
							}
							return options;
						}
					},

					clear: function () {
						module.storage.remove(settings.key);
					},

					storage: {
						set: function (key, value) {
							var
								options = module.get.storageOptions()
								;
							if (settings.storageMethod == 'localstorage' && window.localStorage !== undefined) {
								window.localStorage.setItem(key, value);
								module.debug('Value stored using local storage', key, value);
							}
							else if (settings.storageMethod == 'sessionstorage' && window.sessionStorage !== undefined) {
								window.sessionStorage.setItem(key, value);
								module.debug('Value stored using session storage', key, value);
							}
							else if ($.cookie !== undefined) {
								$.cookie(key, value, options);
								module.debug('Value stored using cookie', key, value, options);
							}
							else {
								module.error(error.noCookieStorage);
								return;
							}
						},
						get: function (key, value) {
							var
								storedValue
								;
							if (settings.storageMethod == 'localstorage' && window.localStorage !== undefined) {
								storedValue = window.localStorage.getItem(key);
							}
							else if (settings.storageMethod == 'sessionstorage' && window.sessionStorage !== undefined) {
								storedValue = window.sessionStorage.getItem(key);
							}
							// get by cookie
							else if ($.cookie !== undefined) {
								storedValue = $.cookie(key);
							}
							else {
								module.error(error.noCookieStorage);
							}
							if (storedValue == 'undefined' || storedValue == 'null' || storedValue === undefined || storedValue === null) {
								storedValue = undefined;
							}
							return storedValue;
						},
						remove: function (key) {
							var
								options = module.get.storageOptions()
								;
							if (settings.storageMethod == 'localstorage' && window.localStorage !== undefined) {
								window.localStorage.removeItem(key);
							}
							else if (settings.storageMethod == 'sessionstorage' && window.sessionStorage !== undefined) {
								window.sessionStorage.removeItem(key);
							}
							// store by cookie
							else if ($.cookie !== undefined) {
								$.removeCookie(key, options);
							}
							else {
								module.error(error.noStorage);
							}
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.nag.settings = {

		name: 'Nag',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		namespace: 'Nag',

		// allows cookie to be overridden
		persist: false,

		// set to zero to require manually dismissal, otherwise hides on its own
		displayTime: 0,

		animation: {
			show: 'slide',
			hide: 'slide'
		},

		context: false,
		detachable: false,

		expires: 30,
		domain: false,
		path: '/',

		// type of storage to use
		storageMethod: 'cookie',

		// value to store in dismissed localstorage/cookie
		key: 'nag',
		value: 'dismiss',

		error: {
			noCookieStorage: '$.cookie is not included. A storage solution is required.',
			noStorage: 'Neither $.cookie or store is defined. A storage solution is required for storing state',
			method: 'The method you called is not defined.'
		},

		className: {
			bottom: 'bottom',
			fixed: 'fixed'
		},

		selector: {
			close: '.close.icon'
		},

		speed: 500,
		easing: 'easeOutQuad',

		onHide: function () { }

	};

	// Adds easing
	$.extend($.easing, {
		easeOutQuad: function (x, t, b, c, d) {
			return -c * (t /= d) * (t - 2) + b;
		}
	});

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Popup
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.popup = function (parameters) {
		var
			$allModules = $(this),
			$document = $(document),
			$window = $(window),
			$body = $('body'),

			moduleSelector = $allModules.selector || '',

			hasTouch = (true),
			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			returnedValue
			;
		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.popup.settings, parameters)
						: $.extend({}, $.fn.popup.settings),

					selector = settings.selector,
					className = settings.className,
					error = settings.error,
					metadata = settings.metadata,
					namespace = settings.namespace,

					eventNamespace = '.' + settings.namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$context = $(settings.context),
					$scrollContext = $(settings.scrollContext),
					$boundary = $(settings.boundary),
					$target = (settings.target)
						? $(settings.target)
						: $module,

					$popup,
					$offsetParent,

					searchDepth = 0,
					triedPositions = false,
					openedWithTouch = false,

					element = this,
					instance = $module.data(moduleNamespace),

					documentObserver,
					elementNamespace,
					id,
					module
					;

				module = {

					// binds events
					initialize: function () {
						module.debug('Initializing', $module);
						module.createID();
						module.bind.events();
						if (!module.exists() && settings.preserve) {
							module.create();
						}
						if (settings.observeChanges) {
							module.observeChanges();
						}
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance', module);
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							documentObserver = new MutationObserver(module.event.documentChanged);
							documentObserver.observe(document, {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', documentObserver);
						}
					},

					refresh: function () {
						if (settings.popup) {
							$popup = $(settings.popup).eq(0);
						}
						else {
							if (settings.inline) {
								$popup = $target.nextAll(selector.popup).eq(0);
								settings.popup = $popup;
							}
						}
						if (settings.popup) {
							$popup.addClass(className.loading);
							$offsetParent = module.get.offsetParent();
							$popup.removeClass(className.loading);
							if (settings.movePopup && module.has.popup() && module.get.offsetParent($popup)[0] !== $offsetParent[0]) {
								module.debug('Moving popup to the same offset parent as target');
								$popup
									.detach()
									.appendTo($offsetParent)
									;
							}
						}
						else {
							$offsetParent = (settings.inline)
								? module.get.offsetParent($target)
								: module.has.popup()
									? module.get.offsetParent($popup)
									: $body
								;
						}
						if ($offsetParent.is('html') && $offsetParent[0] !== $body[0]) {
							module.debug('Setting page as offset parent');
							$offsetParent = $body;
						}
						if (module.get.variation()) {
							module.set.variation();
						}
					},

					reposition: function () {
						module.refresh();
						module.set.position();
					},

					destroy: function () {
						module.debug('Destroying previous module');
						if (documentObserver) {
							documentObserver.disconnect();
						}
						// remove element only if was created dynamically
						if ($popup && !settings.preserve) {
							module.removePopup();
						}
						// clear all timeouts
						clearTimeout(module.hideTimer);
						clearTimeout(module.showTimer);
						// remove events
						module.unbind.close();
						module.unbind.events();
						$module
							.removeData(moduleNamespace)
							;
					},

					event: {
						start: function (event) {
							var
								delay = ($.isPlainObject(settings.delay))
									? settings.delay.show
									: settings.delay
								;
							clearTimeout(module.hideTimer);
							if (!openedWithTouch) {
								module.showTimer = setTimeout(module.show, delay);
							}
						},
						end: function () {
							var
								delay = ($.isPlainObject(settings.delay))
									? settings.delay.hide
									: settings.delay
								;
							clearTimeout(module.showTimer);
							module.hideTimer = setTimeout(module.hide, delay);
						},
						touchstart: function (event) {
							openedWithTouch = true;
							module.show();
						},
						resize: function () {
							if (module.is.visible()) {
								module.set.position();
							}
						},
						documentChanged: function (mutations) {
							[].forEach.call(mutations, function (mutation) {
								if (mutation.removedNodes) {
									[].forEach.call(mutation.removedNodes, function (node) {
										if (node == element || $(node).find(element).length > 0) {
											module.debug('Element removed from DOM, tearing down events');
											module.destroy();
										}
									});
								}
							});
						},
						hideGracefully: function (event) {
							var
								$target = $(event.target),
								isInDOM = $.contains(document.documentElement, event.target),
								inPopup = ($target.closest(selector.popup).length > 0)
								;
							// don't close on clicks inside popup
							if (event && !inPopup && isInDOM) {
								module.debug('Click occurred outside popup hiding popup');
								module.hide();
							}
							else {
								module.debug('Click was inside popup, keeping popup open');
							}
						}
					},

					// generates popup html from metadata
					create: function () {
						var
							html = module.get.html(),
							title = module.get.title(),
							content = module.get.content()
							;

						if (html || content || title) {
							module.debug('Creating pop-up html');
							if (!html) {
								html = settings.templates.popup({
									title: title,
									content: content
								});
							}
							$popup = $('<div/>')
								.addClass(className.popup)
								.data(metadata.activator, $module)
								.html(html)
								;
							if (settings.inline) {
								module.verbose('Inserting popup element inline', $popup);
								$popup
									.insertAfter($module)
									;
							}
							else {
								module.verbose('Appending popup element to body', $popup);
								$popup
									.appendTo($context)
									;
							}
							module.refresh();
							module.set.variation();

							if (settings.hoverable) {
								module.bind.popup();
							}
							settings.onCreate.call($popup, element);
						}
						else if ($target.next(selector.popup).length !== 0) {
							module.verbose('Pre-existing popup found');
							settings.inline = true;
							settings.popup = $target.next(selector.popup).data(metadata.activator, $module);
							module.refresh();
							if (settings.hoverable) {
								module.bind.popup();
							}
						}
						else if (settings.popup) {
							$(settings.popup).data(metadata.activator, $module);
							module.verbose('Used popup specified in settings');
							module.refresh();
							if (settings.hoverable) {
								module.bind.popup();
							}
						}
						else {
							module.debug('No content specified skipping display', element);
						}
					},

					createID: function () {
						id = (Math.random().toString(16) + '000000000').substr(2, 8);
						elementNamespace = '.' + id;
						module.verbose('Creating unique id for element', id);
					},

					// determines popup state
					toggle: function () {
						module.debug('Toggling pop-up');
						if (module.is.hidden()) {
							module.debug('Popup is hidden, showing pop-up');
							module.unbind.close();
							module.show();
						}
						else {
							module.debug('Popup is visible, hiding pop-up');
							module.hide();
						}
					},

					show: function (callback) {
						callback = callback || function () { };
						module.debug('Showing pop-up', settings.transition);
						if (module.is.hidden() && !(module.is.active() && module.is.dropdown())) {
							if (!module.exists()) {
								module.create();
							}
							if (settings.onShow.call($popup, element) === false) {
								module.debug('onShow callback returned false, cancelling popup animation');
								return;
							}
							else if (!settings.preserve && !settings.popup) {
								module.refresh();
							}
							if ($popup && module.set.position()) {
								module.save.conditions();
								if (settings.exclusive) {
									module.hideAll();
								}
								module.animate.show(callback);
							}
						}
					},


					hide: function (callback) {
						callback = callback || function () { };
						if (module.is.visible() || module.is.animating()) {
							if (settings.onHide.call($popup, element) === false) {
								module.debug('onHide callback returned false, cancelling popup animation');
								return;
							}
							module.remove.visible();
							module.unbind.close();
							module.restore.conditions();
							module.animate.hide(callback);
						}
					},

					hideAll: function () {
						$(selector.popup)
							.filter('.' + className.popupVisible)
							.each(function () {
								$(this)
									.data(metadata.activator)
									.popup('hide')
									;
							})
							;
					},
					exists: function () {
						if (!$popup) {
							return false;
						}
						if (settings.inline || settings.popup) {
							return (module.has.popup());
						}
						else {
							return ($popup.closest($context).length >= 1)
								? true
								: false
								;
						}
					},

					removePopup: function () {
						if (module.has.popup() && !settings.popup) {
							module.debug('Removing popup', $popup);
							$popup.remove();
							$popup = undefined;
							settings.onRemove.call($popup, element);
						}
					},

					save: {
						conditions: function () {
							module.cache = {
								title: $module.attr('title')
							};
							if (module.cache.title) {
								$module.removeAttr('title');
							}
							module.verbose('Saving original attributes', module.cache.title);
						}
					},
					restore: {
						conditions: function () {
							if (module.cache && module.cache.title) {
								$module.attr('title', module.cache.title);
								module.verbose('Restoring original attributes', module.cache.title);
							}
							return true;
						}
					},
					supports: {
						svg: function () {
							return (typeof SVGGraphicsElement === 'undefined');
						}
					},
					animate: {
						show: function (callback) {
							callback = $.isFunction(callback) ? callback : function () { };
							if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
								module.set.visible();
								$popup
									.transition({
										animation: settings.transition + ' in',
										queue: false,
										debug: settings.debug,
										verbose: settings.verbose,
										duration: settings.duration,
										onComplete: function () {
											module.bind.close();
											callback.call($popup, element);
											settings.onVisible.call($popup, element);
										}
									})
									;
							}
							else {
								module.error(error.noTransition);
							}
						},
						hide: function (callback) {
							callback = $.isFunction(callback) ? callback : function () { };
							module.debug('Hiding pop-up');
							if (settings.onHide.call($popup, element) === false) {
								module.debug('onHide callback returned false, cancelling popup animation');
								return;
							}
							if (settings.transition && $.fn.transition !== undefined && $module.transition('is supported')) {
								$popup
									.transition({
										animation: settings.transition + ' out',
										queue: false,
										duration: settings.duration,
										debug: settings.debug,
										verbose: settings.verbose,
										onComplete: function () {
											module.reset();
											callback.call($popup, element);
											settings.onHidden.call($popup, element);
										}
									})
									;
							}
							else {
								module.error(error.noTransition);
							}
						}
					},

					change: {
						content: function (html) {
							$popup.html(html);
						}
					},

					get: {
						html: function () {
							$module.removeData(metadata.html);
							return $module.data(metadata.html) || settings.html;
						},
						title: function () {
							$module.removeData(metadata.title);
							return $module.data(metadata.title) || settings.title;
						},
						content: function () {
							$module.removeData(metadata.content);
							return $module.data(metadata.content) || settings.content || $module.attr('title');
						},
						variation: function () {
							$module.removeData(metadata.variation);
							return $module.data(metadata.variation) || settings.variation;
						},
						popup: function () {
							return $popup;
						},
						popupOffset: function () {
							return $popup.offset();
						},
						calculations: function () {
							var
								$popupOffsetParent = module.get.offsetParent($popup),
								targetElement = $target[0],
								isWindow = ($boundary[0] == window),
								targetPosition = (settings.inline || (settings.popup && settings.movePopup))
									? $target.position()
									: $target.offset(),
								screenPosition = (isWindow)
									? { top: 0, left: 0 }
									: $boundary.offset(),
								calculations = {},
								scroll = (isWindow)
									? { top: $window.scrollTop(), left: $window.scrollLeft() }
									: { top: 0, left: 0 },
								screen
								;
							calculations = {
								// element which is launching popup
								target: {
									element: $target[0],
									width: $target.outerWidth(),
									height: $target.outerHeight(),
									top: targetPosition.top,
									left: targetPosition.left,
									margin: {}
								},
								// popup itself
								popup: {
									width: $popup.outerWidth(),
									height: $popup.outerHeight()
								},
								// offset container (or 3d context)
								parent: {
									width: $offsetParent.outerWidth(),
									height: $offsetParent.outerHeight()
								},
								// screen boundaries
								screen: {
									top: screenPosition.top,
									left: screenPosition.left,
									scroll: {
										top: scroll.top,
										left: scroll.left
									},
									width: $boundary.width(),
									height: $boundary.height()
								}
							};

							// if popup offset context is not same as target, then adjust calculations
							if ($popupOffsetParent.get(0) !== $offsetParent.get(0)) {
								var
									popupOffset = $popupOffsetParent.offset()
									;
								calculations.target.top -= popupOffset.top;
								calculations.target.left -= popupOffset.left;
								calculations.parent.width = $popupOffsetParent.outerWidth();
								calculations.parent.height = $popupOffsetParent.outerHeight();
							}

							// add in container calcs if fluid
							if (settings.setFluidWidth && module.is.fluid()) {
								calculations.container = {
									width: $popup.parent().outerWidth()
								};
								calculations.popup.width = calculations.container.width;
							}

							// add in margins if inline
							calculations.target.margin.top = (settings.inline)
								? parseInt(window.getComputedStyle(targetElement).getPropertyValue('margin-top'), 10)
								: 0
								;
							calculations.target.margin.left = (settings.inline)
								? module.is.rtl()
									? parseInt(window.getComputedStyle(targetElement).getPropertyValue('margin-right'), 10)
									: parseInt(window.getComputedStyle(targetElement).getPropertyValue('margin-left'), 10)
								: 0
								;
							// calculate screen boundaries
							screen = calculations.screen;
							calculations.boundary = {
								top: screen.top + screen.scroll.top,
								bottom: screen.top + screen.scroll.top + screen.height,
								left: screen.left + screen.scroll.left,
								right: screen.left + screen.scroll.left + screen.width
							};
							return calculations;
						},
						id: function () {
							return id;
						},
						startEvent: function () {
							if (settings.on == 'hover') {
								return 'mouseenter';
							}
							else if (settings.on == 'focus') {
								return 'focus';
							}
							return false;
						},
						scrollEvent: function () {
							return 'scroll';
						},
						endEvent: function () {
							if (settings.on == 'hover') {
								return 'mouseleave';
							}
							else if (settings.on == 'focus') {
								return 'blur';
							}
							return false;
						},
						distanceFromBoundary: function (offset, calculations) {
							var
								distanceFromBoundary = {},
								popup,
								boundary
								;
							calculations = calculations || module.get.calculations();

							// shorthand
							popup = calculations.popup;
							boundary = calculations.boundary;

							if (offset) {
								distanceFromBoundary = {
									top: (offset.top - boundary.top),
									left: (offset.left - boundary.left),
									right: (boundary.right - (offset.left + popup.width)),
									bottom: (boundary.bottom - (offset.top + popup.height))
								};
								module.verbose('Distance from boundaries determined', offset, distanceFromBoundary);
							}
							return distanceFromBoundary;
						},
						offsetParent: function ($element) {
							var
								element = ($element !== undefined)
									? $element[0]
									: $target[0],
								parentNode = element.parentNode,
								$node = $(parentNode)
								;
							if (parentNode) {
								var
									is2D = ($node.css('transform') === 'none'),
									isStatic = ($node.css('position') === 'static'),
									isBody = $node.is('body')
									;
								while (parentNode && !isBody && isStatic && is2D) {
									parentNode = parentNode.parentNode;
									$node = $(parentNode);
									is2D = ($node.css('transform') === 'none');
									isStatic = ($node.css('position') === 'static');
									isBody = $node.is('body');
								}
							}
							return ($node && $node.length > 0)
								? $node
								: $()
								;
						},
						positions: function () {
							return {
								'top left': false,
								'top center': false,
								'top right': false,
								'bottom left': false,
								'bottom center': false,
								'bottom right': false,
								'left center': false,
								'right center': false
							};
						},
						nextPosition: function (position) {
							var
								positions = position.split(' '),
								verticalPosition = positions[0],
								horizontalPosition = positions[1],
								opposite = {
									top: 'bottom',
									bottom: 'top',
									left: 'right',
									right: 'left'
								},
								adjacent = {
									left: 'center',
									center: 'right',
									right: 'left'
								},
								backup = {
									'top left': 'top center',
									'top center': 'top right',
									'top right': 'right center',
									'right center': 'bottom right',
									'bottom right': 'bottom center',
									'bottom center': 'bottom left',
									'bottom left': 'left center',
									'left center': 'top left'
								},
								adjacentsAvailable = (verticalPosition == 'top' || verticalPosition == 'bottom'),
								oppositeTried = false,
								adjacentTried = false,
								nextPosition = false
								;
							if (!triedPositions) {
								module.verbose('All available positions available');
								triedPositions = module.get.positions();
							}

							module.debug('Recording last position tried', position);
							triedPositions[position] = true;

							if (settings.prefer === 'opposite') {
								nextPosition = [opposite[verticalPosition], horizontalPosition];
								nextPosition = nextPosition.join(' ');
								oppositeTried = (triedPositions[nextPosition] === true);
								module.debug('Trying opposite strategy', nextPosition);
							}
							if ((settings.prefer === 'adjacent') && adjacentsAvailable) {
								nextPosition = [verticalPosition, adjacent[horizontalPosition]];
								nextPosition = nextPosition.join(' ');
								adjacentTried = (triedPositions[nextPosition] === true);
								module.debug('Trying adjacent strategy', nextPosition);
							}
							if (adjacentTried || oppositeTried) {
								module.debug('Using backup position', nextPosition);
								nextPosition = backup[position];
							}
							return nextPosition;
						}
					},

					set: {
						position: function (position, calculations) {

							// exit conditions
							if ($target.length === 0 || $popup.length === 0) {
								module.error(error.notFound);
								return;
							}
							var
								offset,
								distanceAway,
								target,
								popup,
								parent,
								positioning,
								popupOffset,
								distanceFromBoundary
								;

							calculations = calculations || module.get.calculations();
							position = position || $module.data(metadata.position) || settings.position;

							offset = $module.data(metadata.offset) || settings.offset;
							distanceAway = settings.distanceAway;

							// shorthand
							target = calculations.target;
							popup = calculations.popup;
							parent = calculations.parent;

							if (module.should.centerArrow(calculations)) {
								module.verbose('Adjusting offset to center arrow on small target element');
								if (position == 'top left' || position == 'bottom left') {
									offset += (target.width / 2)
									offset -= settings.arrowPixelsFromEdge;
								}
								if (position == 'top right' || position == 'bottom right') {
									offset -= (target.width / 2)
									offset += settings.arrowPixelsFromEdge;
								}
							}

							if (target.width === 0 && target.height === 0 && !module.is.svg(target.element)) {
								module.debug('Popup target is hidden, no action taken');
								return false;
							}

							if (settings.inline) {
								module.debug('Adding margin to calculation', target.margin);
								if (position == 'left center' || position == 'right center') {
									offset += target.margin.top;
									distanceAway += -target.margin.left;
								}
								else if (position == 'top left' || position == 'top center' || position == 'top right') {
									offset += target.margin.left;
									distanceAway -= target.margin.top;
								}
								else {
									offset += target.margin.left;
									distanceAway += target.margin.top;
								}
							}

							module.debug('Determining popup position from calculations', position, calculations);

							if (module.is.rtl()) {
								position = position.replace(/left|right/g, function (match) {
									return (match == 'left')
										? 'right'
										: 'left'
										;
								});
								module.debug('RTL: Popup position updated', position);
							}

							// if last attempt use specified last resort position
							if (searchDepth == settings.maxSearchDepth && typeof settings.lastResort === 'string') {
								position = settings.lastResort;
							}

							switch (position) {
								case 'top left':
									positioning = {
										top: 'auto',
										bottom: parent.height - target.top + distanceAway,
										left: target.left + offset,
										right: 'auto'
									};
									break;
								case 'top center':
									positioning = {
										bottom: parent.height - target.top + distanceAway,
										left: target.left + (target.width / 2) - (popup.width / 2) + offset,
										top: 'auto',
										right: 'auto'
									};
									break;
								case 'top right':
									positioning = {
										bottom: parent.height - target.top + distanceAway,
										right: parent.width - target.left - target.width - offset,
										top: 'auto',
										left: 'auto'
									};
									break;
								case 'left center':
									positioning = {
										top: target.top + (target.height / 2) - (popup.height / 2) + offset,
										right: parent.width - target.left + distanceAway,
										left: 'auto',
										bottom: 'auto'
									};
									break;
								case 'right center':
									positioning = {
										top: target.top + (target.height / 2) - (popup.height / 2) + offset,
										left: target.left + target.width + distanceAway,
										bottom: 'auto',
										right: 'auto'
									};
									break;
								case 'bottom left':
									positioning = {
										top: target.top + target.height + distanceAway,
										left: target.left + offset,
										bottom: 'auto',
										right: 'auto'
									};
									break;
								case 'bottom center':
									positioning = {
										top: target.top + target.height + distanceAway,
										left: target.left + (target.width / 2) - (popup.width / 2) + offset,
										bottom: 'auto',
										right: 'auto'
									};
									break;
								case 'bottom right':
									positioning = {
										top: target.top + target.height + distanceAway,
										right: parent.width - target.left - target.width - offset,
										left: 'auto',
										bottom: 'auto'
									};
									break;
							}
							if (positioning === undefined) {
								module.error(error.invalidPosition, position);
							}

							module.debug('Calculated popup positioning values', positioning);

							// tentatively place on stage
							$popup
								.css(positioning)
								.removeClass(className.position)
								.addClass(position)
								.addClass(className.loading)
								;

							popupOffset = module.get.popupOffset();

							// see if any boundaries are surpassed with this tentative position
							distanceFromBoundary = module.get.distanceFromBoundary(popupOffset, calculations);

							if (module.is.offstage(distanceFromBoundary, position)) {
								module.debug('Position is outside viewport', position);
								if (searchDepth < settings.maxSearchDepth) {
									searchDepth++;
									position = module.get.nextPosition(position);
									module.debug('Trying new position', position);
									return ($popup)
										? module.set.position(position, calculations)
										: false
										;
								}
								else {
									if (settings.lastResort) {
										module.debug('No position found, showing with last position');
									}
									else {
										module.debug('Popup could not find a position to display', $popup);
										module.error(error.cannotPlace, element);
										module.remove.attempts();
										module.remove.loading();
										module.reset();
										settings.onUnplaceable.call($popup, element);
										return false;
									}
								}
							}
							module.debug('Position is on stage', position);
							module.remove.attempts();
							module.remove.loading();
							if (settings.setFluidWidth && module.is.fluid()) {
								module.set.fluidWidth(calculations);
							}
							return true;
						},

						fluidWidth: function (calculations) {
							calculations = calculations || module.get.calculations();
							module.debug('Automatically setting element width to parent width', calculations.parent.width);
							$popup.css('width', calculations.container.width);
						},

						variation: function (variation) {
							variation = variation || module.get.variation();
							if (variation && module.has.popup()) {
								module.verbose('Adding variation to popup', variation);
								$popup.addClass(variation);
							}
						},

						visible: function () {
							$module.addClass(className.visible);
						}
					},

					remove: {
						loading: function () {
							$popup.removeClass(className.loading);
						},
						variation: function (variation) {
							variation = variation || module.get.variation();
							if (variation) {
								module.verbose('Removing variation', variation);
								$popup.removeClass(variation);
							}
						},
						visible: function () {
							$module.removeClass(className.visible);
						},
						attempts: function () {
							module.verbose('Resetting all searched positions');
							searchDepth = 0;
							triedPositions = false;
						}
					},

					bind: {
						events: function () {
							module.debug('Binding popup events to module');
							if (settings.on == 'click') {
								$module
									.on('click' + eventNamespace, module.toggle)
									;
							}
							if (settings.on == 'hover' && hasTouch) {
								$module
									.on('touchstart' + eventNamespace, module.event.touchstart)
									;
							}
							if (module.get.startEvent()) {
								$module
									.on(module.get.startEvent() + eventNamespace, module.event.start)
									.on(module.get.endEvent() + eventNamespace, module.event.end)
									;
							}
							if (settings.target) {
								module.debug('Target set to element', $target);
							}
							$window.on('resize' + elementNamespace, module.event.resize);
						},
						popup: function () {
							module.verbose('Allowing hover events on popup to prevent closing');
							if ($popup && module.has.popup()) {
								$popup
									.on('mouseenter' + eventNamespace, module.event.start)
									.on('mouseleave' + eventNamespace, module.event.end)
									;
							}
						},
						close: function () {
							if (settings.hideOnScroll === true || (settings.hideOnScroll == 'auto' && settings.on != 'click')) {
								module.bind.closeOnScroll();
							}
							if (module.is.closable()) {
								module.bind.clickaway();
							}
							else if (settings.on == 'hover' && openedWithTouch) {
								module.bind.touchClose();
							}
						},
						closeOnScroll: function () {
							module.verbose('Binding scroll close event to document');
							$scrollContext
								.one(module.get.scrollEvent() + elementNamespace, module.event.hideGracefully)
								;
						},
						touchClose: function () {
							module.verbose('Binding popup touchclose event to document');
							$document
								.on('touchstart' + elementNamespace, function (event) {
									module.verbose('Touched away from popup');
									module.event.hideGracefully.call(element, event);
								})
								;
						},
						clickaway: function () {
							module.verbose('Binding popup close event to document');
							$document
								.on('click' + elementNamespace, function (event) {
									module.verbose('Clicked away from popup');
									module.event.hideGracefully.call(element, event);
								})
								;
						}
					},

					unbind: {
						events: function () {
							$window
								.off(elementNamespace)
								;
							$module
								.off(eventNamespace)
								;
						},
						close: function () {
							$document
								.off(elementNamespace)
								;
							$scrollContext
								.off(elementNamespace)
								;
						},
					},

					has: {
						popup: function () {
							return ($popup && $popup.length > 0);
						}
					},

					should: {
						centerArrow: function (calculations) {
							return !module.is.basic() && calculations.target.width <= (settings.arrowPixelsFromEdge * 2);
						},
					},

					is: {
						closable: function () {
							if (settings.closable == 'auto') {
								if (settings.on == 'hover') {
									return false;
								}
								return true;
							}
							return settings.closable;
						},
						offstage: function (distanceFromBoundary, position) {
							var
								offstage = []
								;
							// return boundaries that have been surpassed
							$.each(distanceFromBoundary, function (direction, distance) {
								if (distance < -settings.jitter) {
									module.debug('Position exceeds allowable distance from edge', direction, distance, position);
									offstage.push(direction);
								}
							});
							if (offstage.length > 0) {
								return true;
							}
							else {
								return false;
							}
						},
						svg: function (element) {
							return module.supports.svg() && (element instanceof SVGGraphicsElement);
						},
						basic: function () {
							return $module.hasClass(className.basic);
						},
						active: function () {
							return $module.hasClass(className.active);
						},
						animating: function () {
							return ($popup !== undefined && $popup.hasClass(className.animating));
						},
						fluid: function () {
							return ($popup !== undefined && $popup.hasClass(className.fluid));
						},
						visible: function () {
							return ($popup !== undefined && $popup.hasClass(className.popupVisible));
						},
						dropdown: function () {
							return $module.hasClass(className.dropdown);
						},
						hidden: function () {
							return !module.is.visible();
						},
						rtl: function () {
							return $module.css('direction') == 'rtl';
						}
					},

					reset: function () {
						module.remove.visible();
						if (settings.preserve) {
							if ($.fn.transition !== undefined) {
								$popup
									.transition('remove transition')
									;
							}
						}
						else {
							module.removePopup();
						}
					},

					setting: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							settings[name] = value;
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.popup.settings = {

		name: 'Popup',

		// module settings
		silent: false,
		debug: false,
		verbose: false,
		performance: true,
		namespace: 'popup',

		// whether it should use dom mutation observers
		observeChanges: true,

		// callback only when element added to dom
		onCreate: function () { },

		// callback before element removed from dom
		onRemove: function () { },

		// callback before show animation
		onShow: function () { },

		// callback after show animation
		onVisible: function () { },

		// callback before hide animation
		onHide: function () { },

		// callback when popup cannot be positioned in visible screen
		onUnplaceable: function () { },

		// callback after hide animation
		onHidden: function () { },

		// when to show popup
		on: 'hover',

		// element to use to determine if popup is out of boundary
		boundary: window,

		// whether to add touchstart events when using hover
		addTouchEvents: true,

		// default position relative to element
		position: 'top left',

		// name of variation to use
		variation: '',

		// whether popup should be moved to context
		movePopup: true,

		// element which popup should be relative to
		target: false,

		// jq selector or element that should be used as popup
		popup: false,

		// popup should remain inline next to activator
		inline: false,

		// popup should be removed from page on hide
		preserve: false,

		// popup should not close when being hovered on
		hoverable: false,

		// explicitly set content
		content: false,

		// explicitly set html
		html: false,

		// explicitly set title
		title: false,

		// whether automatically close on clickaway when on click
		closable: true,

		// automatically hide on scroll
		hideOnScroll: 'auto',

		// hide other popups on show
		exclusive: false,

		// context to attach popups
		context: 'body',

		// context for binding scroll events
		scrollContext: window,

		// position to prefer when calculating new position
		prefer: 'opposite',

		// specify position to appear even if it doesn't fit
		lastResort: false,

		// number of pixels from edge of popup to pointing arrow center (used from centering)
		arrowPixelsFromEdge: 20,

		// delay used to prevent accidental refiring of animations due to user error
		delay: {
			show: 50,
			hide: 70
		},

		// whether fluid variation should assign width explicitly
		setFluidWidth: true,

		// transition settings
		duration: 200,
		transition: 'scale',

		// distance away from activating element in px
		distanceAway: 0,

		// number of pixels an element is allowed to be "offstage" for a position to be chosen (allows for rounding)
		jitter: 2,

		// offset on aligning axis from calculated position
		offset: 0,

		// maximum times to look for a position before failing (9 positions total)
		maxSearchDepth: 15,

		error: {
			invalidPosition: 'The position you specified is not a valid position',
			cannotPlace: 'Popup does not fit within the boundaries of the viewport',
			method: 'The method you called is not defined.',
			noTransition: 'This module requires ui transitions <https://github.com/Semantic-Org/UI-Transition>',
			notFound: 'The target or popup you specified does not exist on the page'
		},

		metadata: {
			activator: 'activator',
			content: 'content',
			html: 'html',
			offset: 'offset',
			position: 'position',
			title: 'title',
			variation: 'variation'
		},

		className: {
			active: 'active',
			basic: 'basic',
			animating: 'animating',
			dropdown: 'dropdown',
			fluid: 'fluid',
			loading: 'loading',
			popup: 'ui popup',
			position: 'top left center bottom right',
			visible: 'visible',
			popupVisible: 'visible'
		},

		selector: {
			popup: '.ui.popup'
		},

		templates: {
			escape: function (string) {
				var
					badChars = /[&<>"'`]/g,
					shouldEscape = /[&<>"'`]/,
					escape = {
						"&": "&amp;",
						"<": "&lt;",
						">": "&gt;",
						'"': "&quot;",
						"'": "&#x27;",
						"`": "&#x60;"
					},
					escapedChar = function (chr) {
						return escape[chr];
					}
					;
				if (shouldEscape.test(string)) {
					return string.replace(badChars, escapedChar);
				}
				return string;
			},
			popup: function (text) {
				var
					html = '',
					escape = $.fn.popup.settings.templates.escape
					;
				if (typeof text !== undefined) {
					if (typeof text.title !== undefined && text.title) {
						text.title = escape(text.title);
						html += '<div class="header">' + text.title + '</div>';
					}
					if (typeof text.content !== undefined && text.content) {
						text.content = escape(text.content);
						html += '<div class="content">' + text.content + '</div>';
					}
				}
				return html;
			}
		}

	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Progress
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	var
		global = (typeof window != 'undefined' && window.Math == Math)
			? window
			: (typeof self != 'undefined' && self.Math == Math)
				? self
				: Function('return this')()
		;

	$.fn.progress = function (parameters) {
		var
			$allModules = $(this),

			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.progress.settings, parameters)
						: $.extend({}, $.fn.progress.settings),

					className = settings.className,
					metadata = settings.metadata,
					namespace = settings.namespace,
					selector = settings.selector,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$bar = $(this).find(selector.bar),
					$progress = $(this).find(selector.progress),
					$label = $(this).find(selector.label),

					element = this,
					instance = $module.data(moduleNamespace),

					animating = false,
					transitionEnd,
					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing progress bar', settings);

						module.set.duration();
						module.set.transitionEvent();

						module.read.metadata();
						module.read.settings();

						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of progress', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},
					destroy: function () {
						module.verbose('Destroying previous progress for', $module);
						clearInterval(instance.interval);
						module.remove.state();
						$module.removeData(moduleNamespace);
						instance = undefined;
					},

					reset: function () {
						module.remove.nextValue();
						module.update.progress(0);
					},

					complete: function () {
						if (module.percent === undefined || module.percent < 100) {
							module.remove.progressPoll();
							module.set.percent(100);
						}
					},

					read: {
						metadata: function () {
							var
								data = {
									percent: $module.data(metadata.percent),
									total: $module.data(metadata.total),
									value: $module.data(metadata.value)
								}
								;
							if (data.percent) {
								module.debug('Current percent value set from metadata', data.percent);
								module.set.percent(data.percent);
							}
							if (data.total) {
								module.debug('Total value set from metadata', data.total);
								module.set.total(data.total);
							}
							if (data.value) {
								module.debug('Current value set from metadata', data.value);
								module.set.value(data.value);
								module.set.progress(data.value);
							}
						},
						settings: function () {
							if (settings.total !== false) {
								module.debug('Current total set in settings', settings.total);
								module.set.total(settings.total);
							}
							if (settings.value !== false) {
								module.debug('Current value set in settings', settings.value);
								module.set.value(settings.value);
								module.set.progress(module.value);
							}
							if (settings.percent !== false) {
								module.debug('Current percent set in settings', settings.percent);
								module.set.percent(settings.percent);
							}
						}
					},

					bind: {
						transitionEnd: function (callback) {
							var
								transitionEnd = module.get.transitionEnd()
								;
							$bar
								.one(transitionEnd + eventNamespace, function (event) {
									clearTimeout(module.failSafeTimer);
									callback.call(this, event);
								})
								;
							module.failSafeTimer = setTimeout(function () {
								$bar.triggerHandler(transitionEnd);
							}, settings.duration + settings.failSafeDelay);
							module.verbose('Adding fail safe timer', module.timer);
						}
					},

					increment: function (incrementValue) {
						var
							maxValue,
							startValue,
							newValue
							;
						if (module.has.total()) {
							startValue = module.get.value();
							incrementValue = incrementValue || 1;
							newValue = startValue + incrementValue;
						}
						else {
							startValue = module.get.percent();
							incrementValue = incrementValue || module.get.randomValue();

							newValue = startValue + incrementValue;
							maxValue = 100;
							module.debug('Incrementing percentage by', startValue, newValue);
						}
						newValue = module.get.normalizedValue(newValue);
						module.set.progress(newValue);
					},
					decrement: function (decrementValue) {
						var
							total = module.get.total(),
							startValue,
							newValue
							;
						if (total) {
							startValue = module.get.value();
							decrementValue = decrementValue || 1;
							newValue = startValue - decrementValue;
							module.debug('Decrementing value by', decrementValue, startValue);
						}
						else {
							startValue = module.get.percent();
							decrementValue = decrementValue || module.get.randomValue();
							newValue = startValue - decrementValue;
							module.debug('Decrementing percentage by', decrementValue, startValue);
						}
						newValue = module.get.normalizedValue(newValue);
						module.set.progress(newValue);
					},

					has: {
						progressPoll: function () {
							return module.progressPoll;
						},
						total: function () {
							return (module.get.total() !== false);
						}
					},

					get: {
						text: function (templateText) {
							var
								value = module.value || 0,
								total = module.total || 0,
								percent = (animating)
									? module.get.displayPercent()
									: module.percent || 0,
								left = (module.total > 0)
									? (total - value)
									: (100 - percent)
								;
							templateText = templateText || '';
							templateText = templateText
								.replace('{value}', value)
								.replace('{total}', total)
								.replace('{left}', left)
								.replace('{percent}', percent)
								;
							module.verbose('Adding variables to progress bar text', templateText);
							return templateText;
						},

						normalizedValue: function (value) {
							if (value < 0) {
								module.debug('Value cannot decrement below 0');
								return 0;
							}
							if (module.has.total()) {
								if (value > module.total) {
									module.debug('Value cannot increment above total', module.total);
									return module.total;
								}
							}
							else if (value > 100) {
								module.debug('Value cannot increment above 100 percent');
								return 100;
							}
							return value;
						},

						updateInterval: function () {
							if (settings.updateInterval == 'auto') {
								return settings.duration;
							}
							return settings.updateInterval;
						},

						randomValue: function () {
							module.debug('Generating random increment percentage');
							return Math.floor((Math.random() * settings.random.max) + settings.random.min);
						},

						numericValue: function (value) {
							return (typeof value === 'string')
								? (value.replace(/[^\d.]/g, '') !== '')
									? +(value.replace(/[^\d.]/g, ''))
									: false
								: value
								;
						},

						transitionEnd: function () {
							var
								element = document.createElement('element'),
								transitions = {
									'transition': 'transitionend',
									'OTransition': 'oTransitionEnd',
									'MozTransition': 'transitionend',
									'WebkitTransition': 'webkitTransitionEnd'
								},
								transition
								;
							for (transition in transitions) {
								if (element.style[transition] !== undefined) {
									return transitions[transition];
								}
							}
						},

						// gets current displayed percentage (if animating values this is the intermediary value)
						displayPercent: function () {
							var
								barWidth = $bar.width(),
								totalWidth = $module.width(),
								minDisplay = parseInt($bar.css('min-width'), 10),
								displayPercent = (barWidth > minDisplay)
									? (barWidth / totalWidth * 100)
									: module.percent
								;
							return (settings.precision > 0)
								? Math.round(displayPercent * (10 * settings.precision)) / (10 * settings.precision)
								: Math.round(displayPercent)
								;
						},

						percent: function () {
							return module.percent || 0;
						},
						value: function () {
							return module.nextValue || module.value || 0;
						},
						total: function () {
							return module.total || false;
						}
					},

					create: {
						progressPoll: function () {
							module.progressPoll = setTimeout(function () {
								module.update.toNextValue();
								module.remove.progressPoll();
							}, module.get.updateInterval());
						},
					},

					is: {
						complete: function () {
							return module.is.success() || module.is.warning() || module.is.error();
						},
						success: function () {
							return $module.hasClass(className.success);
						},
						warning: function () {
							return $module.hasClass(className.warning);
						},
						error: function () {
							return $module.hasClass(className.error);
						},
						active: function () {
							return $module.hasClass(className.active);
						},
						visible: function () {
							return $module.is(':visible');
						}
					},

					remove: {
						progressPoll: function () {
							module.verbose('Removing progress poll timer');
							if (module.progressPoll) {
								clearTimeout(module.progressPoll);
								delete module.progressPoll;
							}
						},
						nextValue: function () {
							module.verbose('Removing progress value stored for next update');
							delete module.nextValue;
						},
						state: function () {
							module.verbose('Removing stored state');
							delete module.total;
							delete module.percent;
							delete module.value;
						},
						active: function () {
							module.verbose('Removing active state');
							$module.removeClass(className.active);
						},
						success: function () {
							module.verbose('Removing success state');
							$module.removeClass(className.success);
						},
						warning: function () {
							module.verbose('Removing warning state');
							$module.removeClass(className.warning);
						},
						error: function () {
							module.verbose('Removing error state');
							$module.removeClass(className.error);
						}
					},

					set: {
						barWidth: function (value) {
							if (value > 100) {
								module.error(error.tooHigh, value);
							}
							else if (value < 0) {
								module.error(error.tooLow, value);
							}
							else {
								$bar
									.css('width', value + '%')
									;
								$module
									.attr('data-percent', parseInt(value, 10))
									;
							}
						},
						duration: function (duration) {
							duration = duration || settings.duration;
							duration = (typeof duration == 'number')
								? duration + 'ms'
								: duration
								;
							module.verbose('Setting progress bar transition duration', duration);
							$bar
								.css({
									'transition-duration': duration
								})
								;
						},
						percent: function (percent) {
							percent = (typeof percent == 'string')
								? +(percent.replace('%', ''))
								: percent
								;
							// round display percentage
							percent = (settings.precision > 0)
								? Math.round(percent * (10 * settings.precision)) / (10 * settings.precision)
								: Math.round(percent)
								;
							module.percent = percent;
							if (!module.has.total()) {
								module.value = (settings.precision > 0)
									? Math.round((percent / 100) * module.total * (10 * settings.precision)) / (10 * settings.precision)
									: Math.round((percent / 100) * module.total * 10) / 10
									;
								if (settings.limitValues) {
									module.value = (module.value > 100)
										? 100
										: (module.value < 0)
											? 0
											: module.value
										;
								}
							}
							module.set.barWidth(percent);
							module.set.labelInterval();
							module.set.labels();
							settings.onChange.call(element, percent, module.value, module.total);
						},
						labelInterval: function () {
							var
								animationCallback = function () {
									module.verbose('Bar finished animating, removing continuous label updates');
									clearInterval(module.interval);
									animating = false;
									module.set.labels();
								}
								;
							clearInterval(module.interval);
							module.bind.transitionEnd(animationCallback);
							animating = true;
							module.interval = setInterval(function () {
								var
									isInDOM = $.contains(document.documentElement, element)
									;
								if (!isInDOM) {
									clearInterval(module.interval);
									animating = false;
								}
								module.set.labels();
							}, settings.framerate);
						},
						labels: function () {
							module.verbose('Setting both bar progress and outer label text');
							module.set.barLabel();
							module.set.state();
						},
						label: function (text) {
							text = text || '';
							if (text) {
								text = module.get.text(text);
								module.verbose('Setting label to text', text);
								$label.text(text);
							}
						},
						state: function (percent) {
							percent = (percent !== undefined)
								? percent
								: module.percent
								;
							if (percent === 100) {
								if (settings.autoSuccess && !(module.is.warning() || module.is.error() || module.is.success())) {
									module.set.success();
									module.debug('Automatically triggering success at 100%');
								}
								else {
									module.verbose('Reached 100% removing active state');
									module.remove.active();
									module.remove.progressPoll();
								}
							}
							else if (percent > 0) {
								module.verbose('Adjusting active progress bar label', percent);
								module.set.active();
							}
							else {
								module.remove.active();
								module.set.label(settings.text.active);
							}
						},
						barLabel: function (text) {
							if (text !== undefined) {
								$progress.text(module.get.text(text));
							}
							else if (settings.label == 'ratio' && module.total) {
								module.verbose('Adding ratio to bar label');
								$progress.text(module.get.text(settings.text.ratio));
							}
							else if (settings.label == 'percent') {
								module.verbose('Adding percentage to bar label');
								$progress.text(module.get.text(settings.text.percent));
							}
						},
						active: function (text) {
							text = text || settings.text.active;
							module.debug('Setting active state');
							if (settings.showActivity && !module.is.active()) {
								$module.addClass(className.active);
							}
							module.remove.warning();
							module.remove.error();
							module.remove.success();
							text = settings.onLabelUpdate('active', text, module.value, module.total);
							if (text) {
								module.set.label(text);
							}
							module.bind.transitionEnd(function () {
								settings.onActive.call(element, module.value, module.total);
							});
						},
						success: function (text) {
							text = text || settings.text.success || settings.text.active;
							module.debug('Setting success state');
							$module.addClass(className.success);
							module.remove.active();
							module.remove.warning();
							module.remove.error();
							module.complete();
							if (settings.text.success) {
								text = settings.onLabelUpdate('success', text, module.value, module.total);
								module.set.label(text);
							}
							else {
								text = settings.onLabelUpdate('active', text, module.value, module.total);
								module.set.label(text);
							}
							module.bind.transitionEnd(function () {
								settings.onSuccess.call(element, module.total);
							});
						},
						warning: function (text) {
							text = text || settings.text.warning;
							module.debug('Setting warning state');
							$module.addClass(className.warning);
							module.remove.active();
							module.remove.success();
							module.remove.error();
							module.complete();
							text = settings.onLabelUpdate('warning', text, module.value, module.total);
							if (text) {
								module.set.label(text);
							}
							module.bind.transitionEnd(function () {
								settings.onWarning.call(element, module.value, module.total);
							});
						},
						error: function (text) {
							text = text || settings.text.error;
							module.debug('Setting error state');
							$module.addClass(className.error);
							module.remove.active();
							module.remove.success();
							module.remove.warning();
							module.complete();
							text = settings.onLabelUpdate('error', text, module.value, module.total);
							if (text) {
								module.set.label(text);
							}
							module.bind.transitionEnd(function () {
								settings.onError.call(element, module.value, module.total);
							});
						},
						transitionEvent: function () {
							transitionEnd = module.get.transitionEnd();
						},
						total: function (totalValue) {
							module.total = totalValue;
						},
						value: function (value) {
							module.value = value;
						},
						progress: function (value) {
							if (!module.has.progressPoll()) {
								module.debug('First update in progress update interval, immediately updating', value);
								module.update.progress(value);
								module.create.progressPoll();
							}
							else {
								module.debug('Updated within interval, setting next update to use new value', value);
								module.set.nextValue(value);
							}
						},
						nextValue: function (value) {
							module.nextValue = value;
						}
					},

					update: {
						toNextValue: function () {
							var
								nextValue = module.nextValue
								;
							if (nextValue) {
								module.debug('Update interval complete using last updated value', nextValue);
								module.update.progress(nextValue);
								module.remove.nextValue();
							}
						},
						progress: function (value) {
							var
								percentComplete
								;
							value = module.get.numericValue(value);
							if (value === false) {
								module.error(error.nonNumeric, value);
							}
							value = module.get.normalizedValue(value);
							if (module.has.total()) {
								module.set.value(value);
								percentComplete = (value / module.total) * 100;
								module.debug('Calculating percent complete from total', percentComplete);
								module.set.percent(percentComplete);
							}
							else {
								percentComplete = value;
								module.debug('Setting value to exact percentage value', percentComplete);
								module.set.percent(percentComplete);
							}
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.progress.settings = {

		name: 'Progress',
		namespace: 'progress',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		random: {
			min: 2,
			max: 5
		},

		duration: 300,

		updateInterval: 'auto',

		autoSuccess: true,
		showActivity: true,
		limitValues: true,

		label: 'percent',
		precision: 0,
		framerate: (1000 / 30), /// 30 fps

		percent: false,
		total: false,
		value: false,

		// delay in ms for fail safe animation callback
		failSafeDelay: 100,

		onLabelUpdate: function (state, text, value, total) {
			return text;
		},
		onChange: function (percent, value, total) { },
		onSuccess: function (total) { },
		onActive: function (value, total) { },
		onError: function (value, total) { },
		onWarning: function (value, total) { },

		error: {
			method: 'The method you called is not defined.',
			nonNumeric: 'Progress value is non numeric',
			tooHigh: 'Value specified is above 100%',
			tooLow: 'Value specified is below 0%'
		},

		regExp: {
			variable: /\{\$*[A-z0-9]+\}/g
		},

		metadata: {
			percent: 'percent',
			total: 'total',
			value: 'value'
		},

		selector: {
			bar: '> .bar',
			label: '> .label',
			progress: '.bar > .progress'
		},

		text: {
			active: false,
			error: false,
			success: false,
			warning: false,
			percent: '{percent}%',
			ratio: '{value} of {total}'
		},

		className: {
			active: 'active',
			error: 'error',
			success: 'success',
			warning: 'warning'
		}

	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Rating
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.rating = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;
		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.rating.settings, parameters)
						: $.extend({}, $.fn.rating.settings),

					namespace = settings.namespace,
					className = settings.className,
					metadata = settings.metadata,
					selector = settings.selector,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					element = this,
					instance = $(this).data(moduleNamespace),

					$module = $(this),
					$icon = $module.find(selector.icon),

					initialLoad,
					module
					;

				module = {

					initialize: function () {
						module.verbose('Initializing rating module', settings);

						if ($icon.length === 0) {
							module.setup.layout();
						}

						if (settings.interactive) {
							module.enable();
						}
						else {
							module.disable();
						}
						module.set.initialLoad();
						module.set.rating(module.get.initialRating());
						module.remove.initialLoad();
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Instantiating module', settings);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous instance', instance);
						module.remove.events();
						$module
							.removeData(moduleNamespace)
							;
					},

					refresh: function () {
						$icon = $module.find(selector.icon);
					},

					setup: {
						layout: function () {
							var
								maxRating = module.get.maxRating(),
								html = $.fn.rating.settings.templates.icon(maxRating)
								;
							module.debug('Generating icon html dynamically');
							$module
								.html(html)
								;
							module.refresh();
						}
					},

					event: {
						mouseenter: function () {
							var
								$activeIcon = $(this)
								;
							$activeIcon
								.nextAll()
								.removeClass(className.selected)
								;
							$module
								.addClass(className.selected)
								;
							$activeIcon
								.addClass(className.selected)
								.prevAll()
								.addClass(className.selected)
								;
						},
						mouseleave: function () {
							$module
								.removeClass(className.selected)
								;
							$icon
								.removeClass(className.selected)
								;
						},
						click: function () {
							var
								$activeIcon = $(this),
								currentRating = module.get.rating(),
								rating = $icon.index($activeIcon) + 1,
								canClear = (settings.clearable == 'auto')
									? ($icon.length === 1)
									: settings.clearable
								;
							if (canClear && currentRating == rating) {
								module.clearRating();
							}
							else {
								module.set.rating(rating);
							}
						}
					},

					clearRating: function () {
						module.debug('Clearing current rating');
						module.set.rating(0);
					},

					bind: {
						events: function () {
							module.verbose('Binding events');
							$module
								.on('mouseenter' + eventNamespace, selector.icon, module.event.mouseenter)
								.on('mouseleave' + eventNamespace, selector.icon, module.event.mouseleave)
								.on('click' + eventNamespace, selector.icon, module.event.click)
								;
						}
					},

					remove: {
						events: function () {
							module.verbose('Removing events');
							$module
								.off(eventNamespace)
								;
						},
						initialLoad: function () {
							initialLoad = false;
						}
					},

					enable: function () {
						module.debug('Setting rating to interactive mode');
						module.bind.events();
						$module
							.removeClass(className.disabled)
							;
					},

					disable: function () {
						module.debug('Setting rating to read-only mode');
						module.remove.events();
						$module
							.addClass(className.disabled)
							;
					},

					is: {
						initialLoad: function () {
							return initialLoad;
						}
					},

					get: {
						initialRating: function () {
							if ($module.data(metadata.rating) !== undefined) {
								$module.removeData(metadata.rating);
								return $module.data(metadata.rating);
							}
							return settings.initialRating;
						},
						maxRating: function () {
							if ($module.data(metadata.maxRating) !== undefined) {
								$module.removeData(metadata.maxRating);
								return $module.data(metadata.maxRating);
							}
							return settings.maxRating;
						},
						rating: function () {
							var
								currentRating = $icon.filter('.' + className.active).length
								;
							module.verbose('Current rating retrieved', currentRating);
							return currentRating;
						}
					},

					set: {
						rating: function (rating) {
							var
								ratingIndex = (rating - 1 >= 0)
									? (rating - 1)
									: 0,
								$activeIcon = $icon.eq(ratingIndex)
								;
							$module
								.removeClass(className.selected)
								;
							$icon
								.removeClass(className.selected)
								.removeClass(className.active)
								;
							if (rating > 0) {
								module.verbose('Setting current rating to', rating);
								$activeIcon
									.prevAll()
									.addBack()
									.addClass(className.active)
									;
							}
							if (!module.is.initialLoad()) {
								settings.onRate.call(element, rating);
							}
						},
						initialLoad: function () {
							initialLoad = true;
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};
				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.rating.settings = {

		name: 'Rating',
		namespace: 'rating',

		slent: false,
		debug: false,
		verbose: false,
		performance: true,

		initialRating: 0,
		interactive: true,
		maxRating: 4,
		clearable: 'auto',

		fireOnInit: false,

		onRate: function (rating) { },

		error: {
			method: 'The method you called is not defined',
			noMaximum: 'No maximum rating specified. Cannot generate HTML automatically'
		},


		metadata: {
			rating: 'rating',
			maxRating: 'maxRating'
		},

		className: {
			active: 'active',
			disabled: 'disabled',
			selected: 'selected',
			loading: 'loading'
		},

		selector: {
			icon: '.icon'
		},

		templates: {
			icon: function (maxRating) {
				var
					icon = 1,
					html = ''
					;
				while (icon <= maxRating) {
					html += '<i class="icon"></i>';
					icon++;
				}
				return html;
			}
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Search
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.search = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;
		$(this)
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.search.settings, parameters)
						: $.extend({}, $.fn.search.settings),

					className = settings.className,
					metadata = settings.metadata,
					regExp = settings.regExp,
					fields = settings.fields,
					selector = settings.selector,
					error = settings.error,
					namespace = settings.namespace,

					eventNamespace = '.' + namespace,
					moduleNamespace = namespace + '-module',

					$module = $(this),
					$prompt = $module.find(selector.prompt),
					$searchButton = $module.find(selector.searchButton),
					$results = $module.find(selector.results),
					$result = $module.find(selector.result),
					$category = $module.find(selector.category),

					element = this,
					instance = $module.data(moduleNamespace),

					disabledBubbled = false,
					resultsDismissed = false,

					module
					;

				module = {

					initialize: function () {
						module.verbose('Initializing module');
						module.get.settings();
						module.determine.searchFields();
						module.bind.events();
						module.set.type();
						module.create.results();
						module.instantiate();
					},
					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},
					destroy: function () {
						module.verbose('Destroying instance');
						$module
							.off(eventNamespace)
							.removeData(moduleNamespace)
							;
					},

					refresh: function () {
						module.debug('Refreshing selector cache');
						$prompt = $module.find(selector.prompt);
						$searchButton = $module.find(selector.searchButton);
						$category = $module.find(selector.category);
						$results = $module.find(selector.results);
						$result = $module.find(selector.result);
					},

					refreshResults: function () {
						$results = $module.find(selector.results);
						$result = $module.find(selector.result);
					},

					bind: {
						events: function () {
							module.verbose('Binding events to search');
							if (settings.automatic) {
								$module
									.on(module.get.inputEvent() + eventNamespace, selector.prompt, module.event.input)
									;
								$prompt
									.attr('autocomplete', 'off')
									;
							}
							$module
								// prompt
								.on('focus' + eventNamespace, selector.prompt, module.event.focus)
								.on('blur' + eventNamespace, selector.prompt, module.event.blur)
								.on('keydown' + eventNamespace, selector.prompt, module.handleKeyboard)
								// search button
								.on('click' + eventNamespace, selector.searchButton, module.query)
								// results
								.on('mousedown' + eventNamespace, selector.results, module.event.result.mousedown)
								.on('mouseup' + eventNamespace, selector.results, module.event.result.mouseup)
								.on('click' + eventNamespace, selector.result, module.event.result.click)
								;
						}
					},

					determine: {
						searchFields: function () {
							// this makes sure $.extend does not add specified search fields to default fields
							// this is the only setting which should not extend defaults
							if (parameters && parameters.searchFields !== undefined) {
								settings.searchFields = parameters.searchFields;
							}
						}
					},

					event: {
						input: function () {
							if (settings.searchDelay) {
								clearTimeout(module.timer);
								module.timer = setTimeout(function () {
									if (module.is.focused()) {
										module.query();
									}
								}, settings.searchDelay);
							}
							else {
								module.query();
							}
						},
						focus: function () {
							module.set.focus();
							if (settings.searchOnFocus && module.has.minimumCharacters()) {
								module.query(function () {
									if (module.can.show()) {
										module.showResults();
									}
								});
							}
						},
						blur: function (event) {
							var
								pageLostFocus = (document.activeElement === this),
								callback = function () {
									module.cancel.query();
									module.remove.focus();
									module.timer = setTimeout(module.hideResults, settings.hideDelay);
								}
								;
							if (pageLostFocus) {
								return;
							}
							resultsDismissed = false;
							if (module.resultsClicked) {
								module.debug('Determining if user action caused search to close');
								$module
									.one('click.close' + eventNamespace, selector.results, function (event) {
										if (module.is.inMessage(event) || disabledBubbled) {
											$prompt.focus();
											return;
										}
										disabledBubbled = false;
										if (!module.is.animating() && !module.is.hidden()) {
											callback();
										}
									})
									;
							}
							else {
								module.debug('Input blurred without user action, closing results');
								callback();
							}
						},
						result: {
							mousedown: function () {
								module.resultsClicked = true;
							},
							mouseup: function () {
								module.resultsClicked = false;
							},
							click: function (event) {
								module.debug('Search result selected');
								var
									$result = $(this),
									$title = $result.find(selector.title).eq(0),
									$link = $result.is('a[href]')
										? $result
										: $result.find('a[href]').eq(0),
									href = $link.attr('href') || false,
									target = $link.attr('target') || false,
									title = $title.html(),
									// title is used for result lookup
									value = ($title.length > 0)
										? $title.text()
										: false,
									results = module.get.results(),
									result = $result.data(metadata.result) || module.get.result(value, results),
									returnedValue
									;
								if ($.isFunction(settings.onSelect)) {
									if (settings.onSelect.call(element, result, results) === false) {
										module.debug('Custom onSelect callback cancelled default select action');
										disabledBubbled = true;
										return;
									}
								}
								module.hideResults();
								if (value) {
									module.set.value(value);
								}
								if (href) {
									module.verbose('Opening search link found in result', $link);
									if (target == '_blank' || event.ctrlKey) {
										window.open(href);
									}
									else {
										window.location.href = (href);
									}
								}
							}
						}
					},
					handleKeyboard: function (event) {
						var
							// force selector refresh
							$result = $module.find(selector.result),
							$category = $module.find(selector.category),
							$activeResult = $result.filter('.' + className.active),
							currentIndex = $result.index($activeResult),
							resultSize = $result.length,
							hasActiveResult = $activeResult.length > 0,

							keyCode = event.which,
							keys = {
								backspace: 8,
								enter: 13,
								escape: 27,
								upArrow: 38,
								downArrow: 40
							},
							newIndex
							;
						// search shortcuts
						if (keyCode == keys.escape) {
							module.verbose('Escape key pressed, blurring search field');
							module.hideResults();
							resultsDismissed = true;
						}
						if (module.is.visible()) {
							if (keyCode == keys.enter) {
								module.verbose('Enter key pressed, selecting active result');
								if ($result.filter('.' + className.active).length > 0) {
									module.event.result.click.call($result.filter('.' + className.active), event);
									event.preventDefault();
									return false;
								}
							}
							else if (keyCode == keys.upArrow && hasActiveResult) {
								module.verbose('Up key pressed, changing active result');
								newIndex = (currentIndex - 1 < 0)
									? currentIndex
									: currentIndex - 1
									;
								$category
									.removeClass(className.active)
									;
								$result
									.removeClass(className.active)
									.eq(newIndex)
									.addClass(className.active)
									.closest($category)
									.addClass(className.active)
									;
								event.preventDefault();
							}
							else if (keyCode == keys.downArrow) {
								module.verbose('Down key pressed, changing active result');
								newIndex = (currentIndex + 1 >= resultSize)
									? currentIndex
									: currentIndex + 1
									;
								$category
									.removeClass(className.active)
									;
								$result
									.removeClass(className.active)
									.eq(newIndex)
									.addClass(className.active)
									.closest($category)
									.addClass(className.active)
									;
								event.preventDefault();
							}
						}
						else {
							// query shortcuts
							if (keyCode == keys.enter) {
								module.verbose('Enter key pressed, executing query');
								module.query();
								module.set.buttonPressed();
								$prompt.one('keyup', module.remove.buttonFocus);
							}
						}
					},

					setup: {
						api: function (searchTerm, callback) {
							var
								apiSettings = {
									debug: settings.debug,
									on: false,
									cache: settings.cache,
									action: 'search',
									urlData: {
										query: searchTerm
									},
									onSuccess: function (response) {
										module.parse.response.call(element, response, searchTerm);
										callback();
									},
									onFailure: function () {
										module.displayMessage(error.serverError);
										callback();
									},
									onAbort: function (response) {
									},
									onError: module.error
								},
								searchHTML
								;
							$.extend(true, apiSettings, settings.apiSettings);
							module.verbose('Setting up API request', apiSettings);
							$module.api(apiSettings);
						}
					},

					can: {
						useAPI: function () {
							return $.fn.api !== undefined;
						},
						show: function () {
							return module.is.focused() && !module.is.visible() && !module.is.empty();
						},
						transition: function () {
							return settings.transition && $.fn.transition !== undefined && $module.transition('is supported');
						}
					},

					is: {
						animating: function () {
							return $results.hasClass(className.animating);
						},
						hidden: function () {
							return $results.hasClass(className.hidden);
						},
						inMessage: function (event) {
							if (!event.target) {
								return;
							}
							var
								$target = $(event.target),
								isInDOM = $.contains(document.documentElement, event.target)
								;
							return (isInDOM && $target.closest(selector.message).length > 0);
						},
						empty: function () {
							return ($results.html() === '');
						},
						visible: function () {
							return ($results.filter(':visible').length > 0);
						},
						focused: function () {
							return ($prompt.filter(':focus').length > 0);
						}
					},

					get: {
						settings: function () {
							if ($.isPlainObject(parameters) && parameters.searchFullText) {
								settings.fullTextSearch = parameters.searchFullText;
								module.error(settings.error.oldSearchSyntax, element);
							}
						},
						inputEvent: function () {
							var
								prompt = $prompt[0],
								inputEvent = (prompt !== undefined && prompt.oninput !== undefined)
									? 'input'
									: (prompt !== undefined && prompt.onpropertychange !== undefined)
										? 'propertychange'
										: 'keyup'
								;
							return inputEvent;
						},
						value: function () {
							return $prompt.val();
						},
						results: function () {
							var
								results = $module.data(metadata.results)
								;
							return results;
						},
						result: function (value, results) {
							var
								lookupFields = ['title', 'id'],
								result = false
								;
							value = (value !== undefined)
								? value
								: module.get.value()
								;
							results = (results !== undefined)
								? results
								: module.get.results()
								;
							if (settings.type === 'category') {
								module.debug('Finding result that matches', value);
								$.each(results, function (index, category) {
									if ($.isArray(category.results)) {
										result = module.search.object(value, category.results, lookupFields)[0];
										// don't continue searching if a result is found
										if (result) {
											return false;
										}
									}
								});
							}
							else {
								module.debug('Finding result in results object', value);
								result = module.search.object(value, results, lookupFields)[0];
							}
							return result || false;
						},
					},

					select: {
						firstResult: function () {
							module.verbose('Selecting first result');
							$result.first().addClass(className.active);
						}
					},

					set: {
						focus: function () {
							$module.addClass(className.focus);
						},
						loading: function () {
							$module.addClass(className.loading);
						},
						value: function (value) {
							module.verbose('Setting search input value', value);
							$prompt
								.val(value)
								;
						},
						type: function (type) {
							type = type || settings.type;
							if (settings.type == 'category') {
								$module.addClass(settings.type);
							}
						},
						buttonPressed: function () {
							$searchButton.addClass(className.pressed);
						}
					},

					remove: {
						loading: function () {
							$module.removeClass(className.loading);
						},
						focus: function () {
							$module.removeClass(className.focus);
						},
						buttonPressed: function () {
							$searchButton.removeClass(className.pressed);
						}
					},

					query: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						var
							searchTerm = module.get.value(),
							cache = module.read.cache(searchTerm)
							;
						callback = callback || function () { };
						if (module.has.minimumCharacters()) {
							if (cache) {
								module.debug('Reading result from cache', searchTerm);
								module.save.results(cache.results);
								module.addResults(cache.html);
								module.inject.id(cache.results);
								callback();
							}
							else {
								module.debug('Querying for', searchTerm);
								if ($.isPlainObject(settings.source) || $.isArray(settings.source)) {
									module.search.local(searchTerm);
									callback();
								}
								else if (module.can.useAPI()) {
									module.search.remote(searchTerm, callback);
								}
								else {
									module.error(error.source);
									callback();
								}
							}
							settings.onSearchQuery.call(element, searchTerm);
						}
						else {
							module.hideResults();
						}
					},

					search: {
						local: function (searchTerm) {
							var
								results = module.search.object(searchTerm, settings.content),
								searchHTML
								;
							module.set.loading();
							module.save.results(results);
							module.debug('Returned full local search results', results);
							if (settings.maxResults > 0) {
								module.debug('Using specified max results', results);
								results = results.slice(0, settings.maxResults);
							}
							if (settings.type == 'category') {
								results = module.create.categoryResults(results);
							}
							searchHTML = module.generateResults({
								results: results
							});
							module.remove.loading();
							module.addResults(searchHTML);
							module.inject.id(results);
							module.write.cache(searchTerm, {
								html: searchHTML,
								results: results
							});
						},
						remote: function (searchTerm, callback) {
							callback = $.isFunction(callback)
								? callback
								: function () { }
								;
							if ($module.api('is loading')) {
								$module.api('abort');
							}
							module.setup.api(searchTerm, callback);
							$module
								.api('query')
								;
						},
						object: function (searchTerm, source, searchFields) {
							var
								results = [],
								exactResults = [],
								fuzzyResults = [],
								searchExp = searchTerm.toString().replace(regExp.escape, '\\$&'),
								matchRegExp = new RegExp(regExp.beginsWith + searchExp, 'i'),

								// avoid duplicates when pushing results
								addResult = function (array, result) {
									var
										notResult = ($.inArray(result, results) == -1),
										notFuzzyResult = ($.inArray(result, fuzzyResults) == -1),
										notExactResults = ($.inArray(result, exactResults) == -1)
										;
									if (notResult && notFuzzyResult && notExactResults) {
										array.push(result);
									}
								}
								;
							source = source || settings.source;
							searchFields = (searchFields !== undefined)
								? searchFields
								: settings.searchFields
								;

							// search fields should be array to loop correctly
							if (!$.isArray(searchFields)) {
								searchFields = [searchFields];
							}

							// exit conditions if no source
							if (source === undefined || source === false) {
								module.error(error.source);
								return [];
							}
							// iterate through search fields looking for matches
							$.each(searchFields, function (index, field) {
								$.each(source, function (label, content) {
									var
										fieldExists = (typeof content[field] == 'string')
										;
									if (fieldExists) {
										if (content[field].search(matchRegExp) !== -1) {
											// content starts with value (first in results)
											addResult(results, content);
										}
										else if (settings.fullTextSearch === 'exact' && module.exactSearch(searchTerm, content[field])) {
											// content fuzzy matches (last in results)
											addResult(exactResults, content);
										}
										else if (settings.fullTextSearch == true && module.fuzzySearch(searchTerm, content[field])) {
											// content fuzzy matches (last in results)
											addResult(fuzzyResults, content);
										}
									}
								});
							});
							$.merge(exactResults, fuzzyResults)
							$.merge(results, exactResults);
							return results;
						}
					},
					exactSearch: function (query, term) {
						query = query.toLowerCase();
						term = term.toLowerCase();
						if (term.indexOf(query) > -1) {
							return true;
						}
						return false;
					},
					fuzzySearch: function (query, term) {
						var
							termLength = term.length,
							queryLength = query.length
							;
						if (typeof query !== 'string') {
							return false;
						}
						query = query.toLowerCase();
						term = term.toLowerCase();
						if (queryLength > termLength) {
							return false;
						}
						if (queryLength === termLength) {
							return (query === term);
						}
						search: for (var characterIndex = 0, nextCharacterIndex = 0; characterIndex < queryLength; characterIndex++) {
							var
								queryCharacter = query.charCodeAt(characterIndex)
								;
							while (nextCharacterIndex < termLength) {
								if (term.charCodeAt(nextCharacterIndex++) === queryCharacter) {
									continue search;
								}
							}
							return false;
						}
						return true;
					},

					parse: {
						response: function (response, searchTerm) {
							var
								searchHTML = module.generateResults(response)
								;
							module.verbose('Parsing server response', response);
							if (response !== undefined) {
								if (searchTerm !== undefined && response[fields.results] !== undefined) {
									module.addResults(searchHTML);
									module.inject.id(response[fields.results]);
									module.write.cache(searchTerm, {
										html: searchHTML,
										results: response[fields.results]
									});
									module.save.results(response[fields.results]);
								}
							}
						}
					},

					cancel: {
						query: function () {
							if (module.can.useAPI()) {
								$module.api('abort');
							}
						}
					},

					has: {
						minimumCharacters: function () {
							var
								searchTerm = module.get.value(),
								numCharacters = searchTerm.length
								;
							return (numCharacters >= settings.minCharacters);
						},
						results: function () {
							if ($results.length === 0) {
								return false;
							}
							var
								html = $results.html()
								;
							return html != '';
						}
					},

					clear: {
						cache: function (value) {
							var
								cache = $module.data(metadata.cache)
								;
							if (!value) {
								module.debug('Clearing cache', value);
								$module.removeData(metadata.cache);
							}
							else if (value && cache && cache[value]) {
								module.debug('Removing value from cache', value);
								delete cache[value];
								$module.data(metadata.cache, cache);
							}
						}
					},

					read: {
						cache: function (name) {
							var
								cache = $module.data(metadata.cache)
								;
							if (settings.cache) {
								module.verbose('Checking cache for generated html for query', name);
								return (typeof cache == 'object') && (cache[name] !== undefined)
									? cache[name]
									: false
									;
							}
							return false;
						}
					},

					create: {
						categoryResults: function (results) {
							var
								categoryResults = {}
								;
							$.each(results, function (index, result) {
								if (!result.category) {
									return;
								}
								if (categoryResults[result.category] === undefined) {
									module.verbose('Creating new category of results', result.category);
									categoryResults[result.category] = {
										name: result.category,
										results: [result]
									}
								}
								else {
									categoryResults[result.category].results.push(result);
								}
							});
							return categoryResults;
						},
						id: function (resultIndex, categoryIndex) {
							var
								resultID = (resultIndex + 1), // not zero indexed
								categoryID = (categoryIndex + 1),
								firstCharCode,
								letterID,
								id
								;
							if (categoryIndex !== undefined) {
								// start char code for "A"
								letterID = String.fromCharCode(97 + categoryIndex);
								id = letterID + resultID;
								module.verbose('Creating category result id', id);
							}
							else {
								id = resultID;
								module.verbose('Creating result id', id);
							}
							return id;
						},
						results: function () {
							if ($results.length === 0) {
								$results = $('<div />')
									.addClass(className.results)
									.appendTo($module)
									;
							}
						}
					},

					inject: {
						result: function (result, resultIndex, categoryIndex) {
							module.verbose('Injecting result into results');
							var
								$selectedResult = (categoryIndex !== undefined)
									? $results
										.children().eq(categoryIndex)
										.children(selector.results)
										.first()
										.children(selector.result)
										.eq(resultIndex)
									: $results
										.children(selector.result).eq(resultIndex)
								;
							module.verbose('Injecting results metadata', $selectedResult);
							$selectedResult
								.data(metadata.result, result)
								;
						},
						id: function (results) {
							module.debug('Injecting unique ids into results');
							var
								// since results may be object, we must use counters
								categoryIndex = 0,
								resultIndex = 0
								;
							if (settings.type === 'category') {
								// iterate through each category result
								$.each(results, function (index, category) {
									resultIndex = 0;
									$.each(category.results, function (index, value) {
										var
											result = category.results[index]
											;
										if (result.id === undefined) {
											result.id = module.create.id(resultIndex, categoryIndex);
										}
										module.inject.result(result, resultIndex, categoryIndex);
										resultIndex++;
									});
									categoryIndex++;
								});
							}
							else {
								// top level
								$.each(results, function (index, value) {
									var
										result = results[index]
										;
									if (result.id === undefined) {
										result.id = module.create.id(resultIndex);
									}
									module.inject.result(result, resultIndex);
									resultIndex++;
								});
							}
							return results;
						}
					},

					save: {
						results: function (results) {
							module.verbose('Saving current search results to metadata', results);
							$module.data(metadata.results, results);
						}
					},

					write: {
						cache: function (name, value) {
							var
								cache = ($module.data(metadata.cache) !== undefined)
									? $module.data(metadata.cache)
									: {}
								;
							if (settings.cache) {
								module.verbose('Writing generated html to cache', name, value);
								cache[name] = value;
								$module
									.data(metadata.cache, cache)
									;
							}
						}
					},

					addResults: function (html) {
						if ($.isFunction(settings.onResultsAdd)) {
							if (settings.onResultsAdd.call($results, html) === false) {
								module.debug('onResultsAdd callback cancelled default action');
								return false;
							}
						}
						if (html) {
							$results
								.html(html)
								;
							module.refreshResults();
							if (settings.selectFirstResult) {
								module.select.firstResult();
							}
							module.showResults();
						}
						else {
							module.hideResults(function () {
								$results.empty();
							});
						}
					},

					showResults: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (resultsDismissed) {
							return;
						}
						if (!module.is.visible() && module.has.results()) {
							if (module.can.transition()) {
								module.debug('Showing results with css animations');
								$results
									.transition({
										animation: settings.transition + ' in',
										debug: settings.debug,
										verbose: settings.verbose,
										duration: settings.duration,
										onComplete: function () {
											callback();
										},
										queue: true
									})
									;
							}
							else {
								module.debug('Showing results with javascript');
								$results
									.stop()
									.fadeIn(settings.duration, settings.easing)
									;
							}
							settings.onResultsOpen.call($results);
						}
					},
					hideResults: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.visible()) {
							if (module.can.transition()) {
								module.debug('Hiding results with css animations');
								$results
									.transition({
										animation: settings.transition + ' out',
										debug: settings.debug,
										verbose: settings.verbose,
										duration: settings.duration,
										onComplete: function () {
											callback();
										},
										queue: true
									})
									;
							}
							else {
								module.debug('Hiding results with javascript');
								$results
									.stop()
									.fadeOut(settings.duration, settings.easing)
									;
							}
							settings.onResultsClose.call($results);
						}
					},

					generateResults: function (response) {
						module.debug('Generating html from response', response);
						var
							template = settings.templates[settings.type],
							isProperObject = ($.isPlainObject(response[fields.results]) && !$.isEmptyObject(response[fields.results])),
							isProperArray = ($.isArray(response[fields.results]) && response[fields.results].length > 0),
							html = ''
							;
						if (isProperObject || isProperArray) {
							if (settings.maxResults > 0) {
								if (isProperObject) {
									if (settings.type == 'standard') {
										module.error(error.maxResults);
									}
								}
								else {
									response[fields.results] = response[fields.results].slice(0, settings.maxResults);
								}
							}
							if ($.isFunction(template)) {
								html = template(response, fields);
							}
							else {
								module.error(error.noTemplate, false);
							}
						}
						else if (settings.showNoResults) {
							html = module.displayMessage(error.noResults, 'empty');
						}
						settings.onResults.call(element, response);
						return html;
					},

					displayMessage: function (text, type) {
						type = type || 'standard';
						module.debug('Displaying message', text, type);
						module.addResults(settings.templates.message(text, type));
						return settings.templates.message(text, type);
					},

					setting: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							settings[name] = value;
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};
				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}

			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.search.settings = {

		name: 'Search',
		namespace: 'search',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		// template to use (specified in settings.templates)
		type: 'standard',

		// minimum characters required to search
		minCharacters: 1,

		// whether to select first result after searching automatically
		selectFirstResult: false,

		// API config
		apiSettings: false,

		// object to search
		source: false,

		// Whether search should query current term on focus
		searchOnFocus: true,

		// fields to search
		searchFields: [
			'title',
			'description'
		],

		// field to display in standard results template
		displayField: '',

		// search anywhere in value (set to 'exact' to require exact matches
		fullTextSearch: 'exact',

		// whether to add events to prompt automatically
		automatic: true,

		// delay before hiding menu after blur
		hideDelay: 0,

		// delay before searching
		searchDelay: 200,

		// maximum results returned from search
		maxResults: 7,

		// whether to store lookups in local cache
		cache: true,

		// whether no results errors should be shown
		showNoResults: true,

		// transition settings
		transition: 'scale',
		duration: 200,
		easing: 'easeOutExpo',

		// callbacks
		onSelect: false,
		onResultsAdd: false,

		onSearchQuery: function (query) { },
		onResults: function (response) { },

		onResultsOpen: function () { },
		onResultsClose: function () { },

		className: {
			animating: 'animating',
			active: 'active',
			empty: 'empty',
			focus: 'focus',
			hidden: 'hidden',
			loading: 'loading',
			results: 'results',
			pressed: 'down'
		},

		error: {
			source: 'Cannot search. No source used, and Semantic API module was not included',
			noResults: 'Your search returned no results',
			logging: 'Error in debug logging, exiting.',
			noEndpoint: 'No search endpoint was specified',
			noTemplate: 'A valid template name was not specified.',
			oldSearchSyntax: 'searchFullText setting has been renamed fullTextSearch for consistency, please adjust your settings.',
			serverError: 'There was an issue querying the server.',
			maxResults: 'Results must be an array to use maxResults setting',
			method: 'The method you called is not defined.'
		},

		metadata: {
			cache: 'cache',
			results: 'results',
			result: 'result'
		},

		regExp: {
			escape: /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
			beginsWith: '(?:\s|^)'
		},

		// maps api response attributes to internal representation
		fields: {
			categories: 'results',     // array of categories (category view)
			categoryName: 'name',        // name of category (category view)
			categoryResults: 'results',     // array of results (category view)
			description: 'description', // result description
			image: 'image',       // result image
			price: 'price',       // result price
			results: 'results',     // array of results (standard)
			title: 'title',       // result title
			url: 'url',         // result url
			action: 'action',      // "view more" object name
			actionText: 'text',        // "view more" text
			actionURL: 'url'          // "view more" url
		},

		selector: {
			prompt: '.prompt',
			searchButton: '.search.button',
			results: '.results',
			message: '.results > .message',
			category: '.category',
			result: '.result',
			title: '.title, .name'
		},

		templates: {
			escape: function (string) {
				var
					badChars = /[&<>"'`]/g,
					shouldEscape = /[&<>"'`]/,
					escape = {
						"&": "&amp;",
						"<": "&lt;",
						">": "&gt;",
						'"': "&quot;",
						"'": "&#x27;",
						"`": "&#x60;"
					},
					escapedChar = function (chr) {
						return escape[chr];
					}
					;
				if (shouldEscape.test(string)) {
					return string.replace(badChars, escapedChar);
				}
				return string;
			},
			message: function (message, type) {
				var
					html = ''
					;
				if (message !== undefined && type !== undefined) {
					html += ''
						+ '<div class="message ' + type + '">'
						;
					// message type
					if (type == 'empty') {
						html += ''
							+ '<div class="header">No Results</div class="header">'
							+ '<div class="description">' + message + '</div class="description">'
							;
					}
					else {
						html += ' <div class="description">' + message + '</div>';
					}
					html += '</div>';
				}
				return html;
			},
			category: function (response, fields) {
				var
					html = '',
					escape = $.fn.search.settings.templates.escape
					;
				if (response[fields.categoryResults] !== undefined) {

					// each category
					$.each(response[fields.categoryResults], function (index, category) {
						if (category[fields.results] !== undefined && category.results.length > 0) {

							html += '<div class="category">';

							if (category[fields.categoryName] !== undefined) {
								html += '<div class="name">' + category[fields.categoryName] + '</div>';
							}

							// each item inside category
							html += '<div class="results">';
							$.each(category.results, function (index, result) {
								if (result[fields.url]) {
									html += '<a class="result" href="' + result[fields.url] + '">';
								}
								else {
									html += '<a class="result">';
								}
								if (result[fields.image] !== undefined) {
									html += ''
										+ '<div class="image">'
										+ ' <img src="' + result[fields.image] + '">'
										+ '</div>'
										;
								}
								html += '<div class="content">';
								if (result[fields.price] !== undefined) {
									html += '<div class="price">' + result[fields.price] + '</div>';
								}
								if (result[fields.title] !== undefined) {
									html += '<div class="title">' + result[fields.title] + '</div>';
								}
								if (result[fields.description] !== undefined) {
									html += '<div class="description">' + result[fields.description] + '</div>';
								}
								html += ''
									+ '</div>'
									;
								html += '</a>';
							});
							html += '</div>';
							html += ''
								+ '</div>'
								;
						}
					});
					if (response[fields.action]) {
						html += ''
							+ '<a href="' + response[fields.action][fields.actionURL] + '" class="action">'
							+ response[fields.action][fields.actionText]
							+ '</a>';
					}
					return html;
				}
				return false;
			},
			standard: function (response, fields) {
				var
					html = ''
					;
				if (response[fields.results] !== undefined) {

					// each result
					$.each(response[fields.results], function (index, result) {
						if (result[fields.url]) {
							html += '<a class="result" href="' + result[fields.url] + '">';
						}
						else {
							html += '<a class="result">';
						}
						if (result[fields.image] !== undefined) {
							html += ''
								+ '<div class="image">'
								+ ' <img src="' + result[fields.image] + '">'
								+ '</div>'
								;
						}
						html += '<div class="content">';
						if (result[fields.price] !== undefined) {
							html += '<div class="price">' + result[fields.price] + '</div>';
						}
						if (result[fields.title] !== undefined) {
							html += '<div class="title">' + result[fields.title] + '</div>';
						}
						if (result[fields.description] !== undefined) {
							html += '<div class="description">' + result[fields.description] + '</div>';
						}
						html += ''
							+ '</div>'
							;
						html += '</a>';
					});

					if (response[fields.action]) {
						html += ''
							+ '<a href="' + response[fields.action][fields.actionURL] + '" class="action">'
							+ response[fields.action][fields.actionText]
							+ '</a>';
					}
					return html;
				}
				return false;
			}
		}
	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Shape
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.shape = function (parameters) {
		var
			$allModules = $(this),
			$body = $('body'),

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			requestAnimationFrame = window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.msRequestAnimationFrame
				|| function (callback) { setTimeout(callback, 0); },

			returnedValue
			;

		$allModules
			.each(function () {
				var
					moduleSelector = $allModules.selector || '',
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.shape.settings, parameters)
						: $.extend({}, $.fn.shape.settings),

					// internal aliases
					namespace = settings.namespace,
					selector = settings.selector,
					error = settings.error,
					className = settings.className,

					// define namespaces for modules
					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					// selector cache
					$module = $(this),
					$sides = $module.find(selector.sides),
					$side = $module.find(selector.side),

					// private variables
					nextIndex = false,
					$activeSide,
					$nextSide,

					// standard module
					element = this,
					instance = $module.data(moduleNamespace),
					module
					;

				module = {

					initialize: function () {
						module.verbose('Initializing module for', element);
						module.set.defaultSide();
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous module for', element);
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
					},

					refresh: function () {
						module.verbose('Refreshing selector cache for', element);
						$module = $(element);
						$sides = $(this).find(selector.shape);
						$side = $(this).find(selector.side);
					},

					repaint: function () {
						module.verbose('Forcing repaint event');
						var
							shape = $sides[0] || document.createElement('div'),
							fakeAssignment = shape.offsetWidth
							;
					},

					animate: function (propertyObject, callback) {
						module.verbose('Animating box with properties', propertyObject);
						callback = callback || function (event) {
							module.verbose('Executing animation callback');
							if (event !== undefined) {
								event.stopPropagation();
							}
							module.reset();
							module.set.active();
						};
						settings.beforeChange.call($nextSide[0]);
						if (module.get.transitionEvent()) {
							module.verbose('Starting CSS animation');
							$module
								.addClass(className.animating)
								;
							$sides
								.css(propertyObject)
								.one(module.get.transitionEvent(), callback)
								;
							module.set.duration(settings.duration);
							requestAnimationFrame(function () {
								$module
									.addClass(className.animating)
									;
								$activeSide
									.addClass(className.hidden)
									;
							});
						}
						else {
							callback();
						}
					},

					queue: function (method) {
						module.debug('Queueing animation of', method);
						$sides
							.one(module.get.transitionEvent(), function () {
								module.debug('Executing queued animation');
								setTimeout(function () {
									$module.shape(method);
								}, 0);
							})
							;
					},

					reset: function () {
						module.verbose('Animating states reset');
						$module
							.removeClass(className.animating)
							.attr('style', '')
							.removeAttr('style')
							;
						// removeAttr style does not consistently work in safari
						$sides
							.attr('style', '')
							.removeAttr('style')
							;
						$side
							.attr('style', '')
							.removeAttr('style')
							.removeClass(className.hidden)
							;
						$nextSide
							.removeClass(className.animating)
							.attr('style', '')
							.removeAttr('style')
							;
					},

					is: {
						complete: function () {
							return ($side.filter('.' + className.active)[0] == $nextSide[0]);
						},
						animating: function () {
							return $module.hasClass(className.animating);
						}
					},

					set: {

						defaultSide: function () {
							$activeSide = $module.find('.' + settings.className.active);
							$nextSide = ($activeSide.next(selector.side).length > 0)
								? $activeSide.next(selector.side)
								: $module.find(selector.side).first()
								;
							nextIndex = false;
							module.verbose('Active side set to', $activeSide);
							module.verbose('Next side set to', $nextSide);
						},

						duration: function (duration) {
							duration = duration || settings.duration;
							duration = (typeof duration == 'number')
								? duration + 'ms'
								: duration
								;
							module.verbose('Setting animation duration', duration);
							if (settings.duration || settings.duration === 0) {
								$sides.add($side)
									.css({
										'-webkit-transition-duration': duration,
										'-moz-transition-duration': duration,
										'-ms-transition-duration': duration,
										'-o-transition-duration': duration,
										'transition-duration': duration
									})
									;
							}
						},

						currentStageSize: function () {
							var
								$activeSide = $module.find('.' + settings.className.active),
								width = $activeSide.outerWidth(true),
								height = $activeSide.outerHeight(true)
								;
							$module
								.css({
									width: width,
									height: height
								})
								;
						},

						stageSize: function () {
							var
								$clone = $module.clone().addClass(className.loading),
								$activeSide = $clone.find('.' + settings.className.active),
								$nextSide = (nextIndex)
									? $clone.find(selector.side).eq(nextIndex)
									: ($activeSide.next(selector.side).length > 0)
										? $activeSide.next(selector.side)
										: $clone.find(selector.side).first(),
								newWidth = (settings.width == 'next')
									? $nextSide.outerWidth(true)
									: (settings.width == 'initial')
										? $module.width()
										: settings.width,
								newHeight = (settings.height == 'next')
									? $nextSide.outerHeight(true)
									: (settings.height == 'initial')
										? $module.height()
										: settings.height
								;
							$activeSide.removeClass(className.active);
							$nextSide.addClass(className.active);
							$clone.insertAfter($module);
							$clone.remove();
							if (settings.width != 'auto') {
								$module.css('width', newWidth + settings.jitter);
								module.verbose('Specifying width during animation', newWidth);
							}
							if (settings.height != 'auto') {
								$module.css('height', newHeight + settings.jitter);
								module.verbose('Specifying height during animation', newHeight);
							}
						},

						nextSide: function (selector) {
							nextIndex = selector;
							$nextSide = $side.filter(selector);
							nextIndex = $side.index($nextSide);
							if ($nextSide.length === 0) {
								module.set.defaultSide();
								module.error(error.side);
							}
							module.verbose('Next side manually set to', $nextSide);
						},

						active: function () {
							module.verbose('Setting new side to active', $nextSide);
							$side
								.removeClass(className.active)
								;
							$nextSide
								.addClass(className.active)
								;
							settings.onChange.call($nextSide[0]);
							module.set.defaultSide();
						}
					},

					flip: {

						up: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping up', $nextSide);
								var
									transform = module.get.transform.up()
									;
								module.set.stageSize();
								module.stage.above();
								module.animate(transform);
							}
							else {
								module.queue('flip up');
							}
						},

						down: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping down', $nextSide);
								var
									transform = module.get.transform.down()
									;
								module.set.stageSize();
								module.stage.below();
								module.animate(transform);
							}
							else {
								module.queue('flip down');
							}
						},

						left: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping left', $nextSide);
								var
									transform = module.get.transform.left()
									;
								module.set.stageSize();
								module.stage.left();
								module.animate(transform);
							}
							else {
								module.queue('flip left');
							}
						},

						right: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping right', $nextSide);
								var
									transform = module.get.transform.right()
									;
								module.set.stageSize();
								module.stage.right();
								module.animate(transform);
							}
							else {
								module.queue('flip right');
							}
						},

						over: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping over', $nextSide);
								module.set.stageSize();
								module.stage.behind();
								module.animate(module.get.transform.over());
							}
							else {
								module.queue('flip over');
							}
						},

						back: function () {
							if (module.is.complete() && !module.is.animating() && !settings.allowRepeats) {
								module.debug('Side already visible', $nextSide);
								return;
							}
							if (!module.is.animating()) {
								module.debug('Flipping back', $nextSide);
								module.set.stageSize();
								module.stage.behind();
								module.animate(module.get.transform.back());
							}
							else {
								module.queue('flip back');
							}
						}

					},

					get: {

						transform: {
							up: function () {
								var
									translate = {
										y: -(($activeSide.outerHeight(true) - $nextSide.outerHeight(true)) / 2),
										z: -($activeSide.outerHeight(true) / 2)
									}
									;
								return {
									transform: 'translateY(' + translate.y + 'px) translateZ(' + translate.z + 'px) rotateX(-90deg)'
								};
							},

							down: function () {
								var
									translate = {
										y: -(($activeSide.outerHeight(true) - $nextSide.outerHeight(true)) / 2),
										z: -($activeSide.outerHeight(true) / 2)
									}
									;
								return {
									transform: 'translateY(' + translate.y + 'px) translateZ(' + translate.z + 'px) rotateX(90deg)'
								};
							},

							left: function () {
								var
									translate = {
										x: -(($activeSide.outerWidth(true) - $nextSide.outerWidth(true)) / 2),
										z: -($activeSide.outerWidth(true) / 2)
									}
									;
								return {
									transform: 'translateX(' + translate.x + 'px) translateZ(' + translate.z + 'px) rotateY(90deg)'
								};
							},

							right: function () {
								var
									translate = {
										x: -(($activeSide.outerWidth(true) - $nextSide.outerWidth(true)) / 2),
										z: -($activeSide.outerWidth(true) / 2)
									}
									;
								return {
									transform: 'translateX(' + translate.x + 'px) translateZ(' + translate.z + 'px) rotateY(-90deg)'
								};
							},

							over: function () {
								var
									translate = {
										x: -(($activeSide.outerWidth(true) - $nextSide.outerWidth(true)) / 2)
									}
									;
								return {
									transform: 'translateX(' + translate.x + 'px) rotateY(180deg)'
								};
							},

							back: function () {
								var
									translate = {
										x: -(($activeSide.outerWidth(true) - $nextSide.outerWidth(true)) / 2)
									}
									;
								return {
									transform: 'translateX(' + translate.x + 'px) rotateY(-180deg)'
								};
							}
						},

						transitionEvent: function () {
							var
								element = document.createElement('element'),
								transitions = {
									'transition': 'transitionend',
									'OTransition': 'oTransitionEnd',
									'MozTransition': 'transitionend',
									'WebkitTransition': 'webkitTransitionEnd'
								},
								transition
								;
							for (transition in transitions) {
								if (element.style[transition] !== undefined) {
									return transitions[transition];
								}
							}
						},

						nextSide: function () {
							return ($activeSide.next(selector.side).length > 0)
								? $activeSide.next(selector.side)
								: $module.find(selector.side).first()
								;
						}

					},

					stage: {

						above: function () {
							var
								box = {
									origin: (($activeSide.outerHeight(true) - $nextSide.outerHeight(true)) / 2),
									depth: {
										active: ($nextSide.outerHeight(true) / 2),
										next: ($activeSide.outerHeight(true) / 2)
									}
								}
								;
							module.verbose('Setting the initial animation position as above', $nextSide, box);
							$sides
								.css({
									'transform': 'translateZ(-' + box.depth.active + 'px)'
								})
								;
							$activeSide
								.css({
									'transform': 'rotateY(0deg) translateZ(' + box.depth.active + 'px)'
								})
								;
							$nextSide
								.addClass(className.animating)
								.css({
									'top': box.origin + 'px',
									'transform': 'rotateX(90deg) translateZ(' + box.depth.next + 'px)'
								})
								;
						},

						below: function () {
							var
								box = {
									origin: (($activeSide.outerHeight(true) - $nextSide.outerHeight(true)) / 2),
									depth: {
										active: ($nextSide.outerHeight(true) / 2),
										next: ($activeSide.outerHeight(true) / 2)
									}
								}
								;
							module.verbose('Setting the initial animation position as below', $nextSide, box);
							$sides
								.css({
									'transform': 'translateZ(-' + box.depth.active + 'px)'
								})
								;
							$activeSide
								.css({
									'transform': 'rotateY(0deg) translateZ(' + box.depth.active + 'px)'
								})
								;
							$nextSide
								.addClass(className.animating)
								.css({
									'top': box.origin + 'px',
									'transform': 'rotateX(-90deg) translateZ(' + box.depth.next + 'px)'
								})
								;
						},

						left: function () {
							var
								height = {
									active: $activeSide.outerWidth(true),
									next: $nextSide.outerWidth(true)
								},
								box = {
									origin: ((height.active - height.next) / 2),
									depth: {
										active: (height.next / 2),
										next: (height.active / 2)
									}
								}
								;
							module.verbose('Setting the initial animation position as left', $nextSide, box);
							$sides
								.css({
									'transform': 'translateZ(-' + box.depth.active + 'px)'
								})
								;
							$activeSide
								.css({
									'transform': 'rotateY(0deg) translateZ(' + box.depth.active + 'px)'
								})
								;
							$nextSide
								.addClass(className.animating)
								.css({
									'left': box.origin + 'px',
									'transform': 'rotateY(-90deg) translateZ(' + box.depth.next + 'px)'
								})
								;
						},

						right: function () {
							var
								height = {
									active: $activeSide.outerWidth(true),
									next: $nextSide.outerWidth(true)
								},
								box = {
									origin: ((height.active - height.next) / 2),
									depth: {
										active: (height.next / 2),
										next: (height.active / 2)
									}
								}
								;
							module.verbose('Setting the initial animation position as left', $nextSide, box);
							$sides
								.css({
									'transform': 'translateZ(-' + box.depth.active + 'px)'
								})
								;
							$activeSide
								.css({
									'transform': 'rotateY(0deg) translateZ(' + box.depth.active + 'px)'
								})
								;
							$nextSide
								.addClass(className.animating)
								.css({
									'left': box.origin + 'px',
									'transform': 'rotateY(90deg) translateZ(' + box.depth.next + 'px)'
								})
								;
						},

						behind: function () {
							var
								height = {
									active: $activeSide.outerWidth(true),
									next: $nextSide.outerWidth(true)
								},
								box = {
									origin: ((height.active - height.next) / 2),
									depth: {
										active: (height.next / 2),
										next: (height.active / 2)
									}
								}
								;
							module.verbose('Setting the initial animation position as behind', $nextSide, box);
							$activeSide
								.css({
									'transform': 'rotateY(0deg)'
								})
								;
							$nextSide
								.addClass(className.animating)
								.css({
									'left': box.origin + 'px',
									'transform': 'rotateY(-180deg)'
								})
								;
						}
					},
					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.shape.settings = {

		// module info
		name: 'Shape',

		// hide all debug content
		silent: false,

		// debug content outputted to console
		debug: false,

		// verbose debug output
		verbose: false,

		// fudge factor in pixels when swapping from 2d to 3d (can be useful to correct rounding errors)
		jitter: 0,

		// performance data output
		performance: true,

		// event namespace
		namespace: 'shape',

		// width during animation, can be set to 'auto', initial', 'next' or pixel amount
		width: 'initial',

		// height during animation, can be set to 'auto', 'initial', 'next' or pixel amount
		height: 'initial',

		// callback occurs on side change
		beforeChange: function () { },
		onChange: function () { },

		// allow animation to same side
		allowRepeats: false,

		// animation duration
		duration: false,

		// possible errors
		error: {
			side: 'You tried to switch to a side that does not exist.',
			method: 'The method you called is not defined'
		},

		// classnames used
		className: {
			animating: 'animating',
			hidden: 'hidden',
			loading: 'loading',
			active: 'active'
		},

		// selectors used
		selector: {
			sides: '.sides',
			side: '.side'
		}

	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Sidebar
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.sidebar = function (parameters) {
		var
			$allModules = $(this),
			$window = $(window),
			$document = $(document),
			$html = $('html'),
			$head = $('head'),

			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			requestAnimationFrame = window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.msRequestAnimationFrame
				|| function (callback) { setTimeout(callback, 0); },

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.sidebar.settings, parameters)
						: $.extend({}, $.fn.sidebar.settings),

					selector = settings.selector,
					className = settings.className,
					namespace = settings.namespace,
					regExp = settings.regExp,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$context = $(settings.context),

					$sidebars = $module.children(selector.sidebar),
					$fixed = $context.children(selector.fixed),
					$pusher = $context.children(selector.pusher),
					$style,

					element = this,
					instance = $module.data(moduleNamespace),

					elementNamespace,
					id,
					currentScroll,
					transitionEvent,

					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing sidebar', parameters);

						module.create.id();

						transitionEvent = module.get.transitionEvent();

						// avoids locking rendering if initialized in onReady
						if (settings.delaySetup) {
							requestAnimationFrame(module.setup.layout);
						}
						else {
							module.setup.layout();
						}

						requestAnimationFrame(function () {
							module.setup.cache();
						});

						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					create: {
						id: function () {
							id = (Math.random().toString(16) + '000000000').substr(2, 8);
							elementNamespace = '.' + id;
							module.verbose('Creating unique id for element', id);
						}
					},

					destroy: function () {
						module.verbose('Destroying previous module for', $module);
						$module
							.off(eventNamespace)
							.removeData(moduleNamespace)
							;
						if (module.is.ios()) {
							module.remove.ios();
						}
						// bound by uuid
						$context.off(elementNamespace);
						$window.off(elementNamespace);
						$document.off(elementNamespace);
					},

					event: {
						clickaway: function (event) {
							var
								clickedInPusher = ($pusher.find(event.target).length > 0 || $pusher.is(event.target)),
								clickedContext = ($context.is(event.target))
								;
							if (clickedInPusher) {
								module.verbose('User clicked on dimmed page');
								module.hide();
							}
							if (clickedContext) {
								module.verbose('User clicked on dimmable context (scaled out page)');
								module.hide();
							}
						},
						touch: function (event) {
							//event.stopPropagation();
						},
						containScroll: function (event) {
							if (element.scrollTop <= 0) {
								element.scrollTop = 1;
							}
							if ((element.scrollTop + element.offsetHeight) >= element.scrollHeight) {
								element.scrollTop = element.scrollHeight - element.offsetHeight - 1;
							}
						},
						scroll: function (event) {
							if ($(event.target).closest(selector.sidebar).length === 0) {
								event.preventDefault();
							}
						}
					},

					bind: {
						clickaway: function () {
							module.verbose('Adding clickaway events to context', $context);
							if (settings.closable) {
								$context
									.on('click' + elementNamespace, module.event.clickaway)
									.on('touchend' + elementNamespace, module.event.clickaway)
									;
							}
						},
						scrollLock: function () {
							if (settings.scrollLock) {
								module.debug('Disabling page scroll');
								$window
									.on('DOMMouseScroll' + elementNamespace, module.event.scroll)
									;
							}
							module.verbose('Adding events to contain sidebar scroll');
							$document
								.on('touchmove' + elementNamespace, module.event.touch)
								;
							$module
								.on('scroll' + eventNamespace, module.event.containScroll)
								;
						}
					},
					unbind: {
						clickaway: function () {
							module.verbose('Removing clickaway events from context', $context);
							$context.off(elementNamespace);
						},
						scrollLock: function () {
							module.verbose('Removing scroll lock from page');
							$document.off(elementNamespace);
							$window.off(elementNamespace);
							$module.off('scroll' + eventNamespace);
						}
					},

					add: {
						inlineCSS: function () {
							var
								width = module.cache.width || $module.outerWidth(),
								height = module.cache.height || $module.outerHeight(),
								isRTL = module.is.rtl(),
								direction = module.get.direction(),
								distance = {
									left: width,
									right: -width,
									top: height,
									bottom: -height
								},
								style
								;

							if (isRTL) {
								module.verbose('RTL detected, flipping widths');
								distance.left = -width;
								distance.right = width;
							}

							style = '<style>';

							if (direction === 'left' || direction === 'right') {
								module.debug('Adding CSS rules for animation distance', width);
								style += ''
									+ ' .ui.visible.' + direction + '.sidebar ~ .fixed,'
									+ ' .ui.visible.' + direction + '.sidebar ~ .pusher {'
									+ '   -webkit-transform: translate3d(' + distance[direction] + 'px, 0, 0);'
									+ '           transform: translate3d(' + distance[direction] + 'px, 0, 0);'
									+ ' }'
									;
							}
							else if (direction === 'top' || direction == 'bottom') {
								style += ''
									+ ' .ui.visible.' + direction + '.sidebar ~ .fixed,'
									+ ' .ui.visible.' + direction + '.sidebar ~ .pusher {'
									+ '   -webkit-transform: translate3d(0, ' + distance[direction] + 'px, 0);'
									+ '           transform: translate3d(0, ' + distance[direction] + 'px, 0);'
									+ ' }'
									;
							}

							/* IE is only browser not to create context with transforms */
							/* https://www.w3.org/Bugs/Public/show_bug.cgi?id=16328 */
							if (module.is.ie()) {
								if (direction === 'left' || direction === 'right') {
									module.debug('Adding CSS rules for animation distance', width);
									style += ''
										+ ' body.pushable > .ui.visible.' + direction + '.sidebar ~ .pusher:after {'
										+ '   -webkit-transform: translate3d(' + distance[direction] + 'px, 0, 0);'
										+ '           transform: translate3d(' + distance[direction] + 'px, 0, 0);'
										+ ' }'
										;
								}
								else if (direction === 'top' || direction == 'bottom') {
									style += ''
										+ ' body.pushable > .ui.visible.' + direction + '.sidebar ~ .pusher:after {'
										+ '   -webkit-transform: translate3d(0, ' + distance[direction] + 'px, 0);'
										+ '           transform: translate3d(0, ' + distance[direction] + 'px, 0);'
										+ ' }'
										;
								}
								/* opposite sides visible forces content overlay */
								style += ''
									+ ' body.pushable > .ui.visible.left.sidebar ~ .ui.visible.right.sidebar ~ .pusher:after,'
									+ ' body.pushable > .ui.visible.right.sidebar ~ .ui.visible.left.sidebar ~ .pusher:after {'
									+ '   -webkit-transform: translate3d(0px, 0, 0);'
									+ '           transform: translate3d(0px, 0, 0);'
									+ ' }'
									;
							}
							style += '</style>';
							$style = $(style)
								.appendTo($head)
								;
							module.debug('Adding sizing css to head', $style);
						}
					},

					refresh: function () {
						module.verbose('Refreshing selector cache');
						$context = $(settings.context);
						$sidebars = $context.children(selector.sidebar);
						$pusher = $context.children(selector.pusher);
						$fixed = $context.children(selector.fixed);
						module.clear.cache();
					},

					refreshSidebars: function () {
						module.verbose('Refreshing other sidebars');
						$sidebars = $context.children(selector.sidebar);
					},

					repaint: function () {
						module.verbose('Forcing repaint event');
						element.style.display = 'none';
						var ignored = element.offsetHeight;
						element.scrollTop = element.scrollTop;
						element.style.display = '';
					},

					setup: {
						cache: function () {
							module.cache = {
								width: $module.outerWidth(),
								height: $module.outerHeight(),
								rtl: ($module.css('direction') == 'rtl')
							};
						},
						layout: function () {
							if ($context.children(selector.pusher).length === 0) {
								module.debug('Adding wrapper element for sidebar');
								module.error(error.pusher);
								$pusher = $('<div class="pusher" />');
								$context
									.children()
									.not(selector.omitted)
									.not($sidebars)
									.wrapAll($pusher)
									;
								module.refresh();
							}
							if ($module.nextAll(selector.pusher).length === 0 || $module.nextAll(selector.pusher)[0] !== $pusher[0]) {
								module.debug('Moved sidebar to correct parent element');
								module.error(error.movedSidebar, element);
								$module.detach().prependTo($context);
								module.refresh();
							}
							module.clear.cache();
							module.set.pushable();
							module.set.direction();
						}
					},

					attachEvents: function (selector, event) {
						var
							$toggle = $(selector)
							;
						event = $.isFunction(module[event])
							? module[event]
							: module.toggle
							;
						if ($toggle.length > 0) {
							module.debug('Attaching sidebar events to element', selector, event);
							$toggle
								.on('click' + eventNamespace, event)
								;
						}
						else {
							module.error(error.notFound, selector);
						}
					},

					show: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.hidden()) {
							module.refreshSidebars();
							if (settings.overlay) {
								module.error(error.overlay);
								settings.transition = 'overlay';
							}
							module.refresh();
							if (module.othersActive()) {
								module.debug('Other sidebars currently visible');
								if (settings.exclusive) {
									// if not overlay queue animation after hide
									if (settings.transition != 'overlay') {
										module.hideOthers(module.show);
										return;
									}
									else {
										module.hideOthers();
									}
								}
								else {
									settings.transition = 'overlay';
								}
							}
							module.pushPage(function () {
								callback.call(element);
								settings.onShow.call(element);
							});
							settings.onChange.call(element);
							settings.onVisible.call(element);
						}
						else {
							module.debug('Sidebar is already visible');
						}
					},

					hide: function (callback) {
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (module.is.visible() || module.is.animating()) {
							module.debug('Hiding sidebar', callback);
							module.refreshSidebars();
							module.pullPage(function () {
								callback.call(element);
								settings.onHidden.call(element);
							});
							settings.onChange.call(element);
							settings.onHide.call(element);
						}
					},

					othersAnimating: function () {
						return ($sidebars.not($module).filter('.' + className.animating).length > 0);
					},
					othersVisible: function () {
						return ($sidebars.not($module).filter('.' + className.visible).length > 0);
					},
					othersActive: function () {
						return (module.othersVisible() || module.othersAnimating());
					},

					hideOthers: function (callback) {
						var
							$otherSidebars = $sidebars.not($module).filter('.' + className.visible),
							sidebarCount = $otherSidebars.length,
							callbackCount = 0
							;
						callback = callback || function () { };
						$otherSidebars
							.sidebar('hide', function () {
								callbackCount++;
								if (callbackCount == sidebarCount) {
									callback();
								}
							})
							;
					},

					toggle: function () {
						module.verbose('Determining toggled direction');
						if (module.is.hidden()) {
							module.show();
						}
						else {
							module.hide();
						}
					},

					pushPage: function (callback) {
						var
							transition = module.get.transition(),
							$transition = (transition === 'overlay' || module.othersActive())
								? $module
								: $pusher,
							animate,
							dim,
							transitionEnd
							;
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						if (settings.transition == 'scale down') {
							module.scrollToTop();
						}
						module.set.transition(transition);
						module.repaint();
						animate = function () {
							module.bind.clickaway();
							module.add.inlineCSS();
							module.set.animating();
							module.set.visible();
						};
						dim = function () {
							module.set.dimmed();
						};
						transitionEnd = function (event) {
							if (event.target == $transition[0]) {
								$transition.off(transitionEvent + elementNamespace, transitionEnd);
								module.remove.animating();
								module.bind.scrollLock();
								callback.call(element);
							}
						};
						$transition.off(transitionEvent + elementNamespace);
						$transition.on(transitionEvent + elementNamespace, transitionEnd);
						requestAnimationFrame(animate);
						if (settings.dimPage && !module.othersVisible()) {
							requestAnimationFrame(dim);
						}
					},

					pullPage: function (callback) {
						var
							transition = module.get.transition(),
							$transition = (transition == 'overlay' || module.othersActive())
								? $module
								: $pusher,
							animate,
							transitionEnd
							;
						callback = $.isFunction(callback)
							? callback
							: function () { }
							;
						module.verbose('Removing context push state', module.get.direction());

						module.unbind.clickaway();
						module.unbind.scrollLock();

						animate = function () {
							module.set.transition(transition);
							module.set.animating();
							module.remove.visible();
							if (settings.dimPage && !module.othersVisible()) {
								$pusher.removeClass(className.dimmed);
							}
						};
						transitionEnd = function (event) {
							if (event.target == $transition[0]) {
								$transition.off(transitionEvent + elementNamespace, transitionEnd);
								module.remove.animating();
								module.remove.transition();
								module.remove.inlineCSS();
								if (transition == 'scale down' || (settings.returnScroll && module.is.mobile())) {
									module.scrollBack();
								}
								callback.call(element);
							}
						};
						$transition.off(transitionEvent + elementNamespace);
						$transition.on(transitionEvent + elementNamespace, transitionEnd);
						requestAnimationFrame(animate);
					},

					scrollToTop: function () {
						module.verbose('Scrolling to top of page to avoid animation issues');
						currentScroll = $(window).scrollTop();
						$module.scrollTop(0);
						window.scrollTo(0, 0);
					},

					scrollBack: function () {
						module.verbose('Scrolling back to original page position');
						window.scrollTo(0, currentScroll);
					},

					clear: {
						cache: function () {
							module.verbose('Clearing cached dimensions');
							module.cache = {};
						}
					},

					set: {

						// ios only (scroll on html not document). This prevent auto-resize canvas/scroll in ios
						// (This is no longer necessary in latest iOS)
						ios: function () {
							$html.addClass(className.ios);
						},

						// container
						pushed: function () {
							$context.addClass(className.pushed);
						},
						pushable: function () {
							$context.addClass(className.pushable);
						},

						// pusher
						dimmed: function () {
							$pusher.addClass(className.dimmed);
						},

						// sidebar
						active: function () {
							$module.addClass(className.active);
						},
						animating: function () {
							$module.addClass(className.animating);
						},
						transition: function (transition) {
							transition = transition || module.get.transition();
							$module.addClass(transition);
						},
						direction: function (direction) {
							direction = direction || module.get.direction();
							$module.addClass(className[direction]);
						},
						visible: function () {
							$module.addClass(className.visible);
						},
						overlay: function () {
							$module.addClass(className.overlay);
						}
					},
					remove: {

						inlineCSS: function () {
							module.debug('Removing inline css styles', $style);
							if ($style && $style.length > 0) {
								$style.remove();
							}
						},

						// ios scroll on html not document
						ios: function () {
							$html.removeClass(className.ios);
						},

						// context
						pushed: function () {
							$context.removeClass(className.pushed);
						},
						pushable: function () {
							$context.removeClass(className.pushable);
						},

						// sidebar
						active: function () {
							$module.removeClass(className.active);
						},
						animating: function () {
							$module.removeClass(className.animating);
						},
						transition: function (transition) {
							transition = transition || module.get.transition();
							$module.removeClass(transition);
						},
						direction: function (direction) {
							direction = direction || module.get.direction();
							$module.removeClass(className[direction]);
						},
						visible: function () {
							$module.removeClass(className.visible);
						},
						overlay: function () {
							$module.removeClass(className.overlay);
						}
					},

					get: {
						direction: function () {
							if ($module.hasClass(className.top)) {
								return className.top;
							}
							else if ($module.hasClass(className.right)) {
								return className.right;
							}
							else if ($module.hasClass(className.bottom)) {
								return className.bottom;
							}
							return className.left;
						},
						transition: function () {
							var
								direction = module.get.direction(),
								transition
								;
							transition = (module.is.mobile())
								? (settings.mobileTransition == 'auto')
									? settings.defaultTransition.mobile[direction]
									: settings.mobileTransition
								: (settings.transition == 'auto')
									? settings.defaultTransition.computer[direction]
									: settings.transition
								;
							module.verbose('Determined transition', transition);
							return transition;
						},
						transitionEvent: function () {
							var
								element = document.createElement('element'),
								transitions = {
									'transition': 'transitionend',
									'OTransition': 'oTransitionEnd',
									'MozTransition': 'transitionend',
									'WebkitTransition': 'webkitTransitionEnd'
								},
								transition
								;
							for (transition in transitions) {
								if (element.style[transition] !== undefined) {
									return transitions[transition];
								}
							}
						}
					},

					is: {

						ie: function () {
							var
								isIE11 = (!(window.ActiveXObject) && 'ActiveXObject' in window),
								isIE = ('ActiveXObject' in window)
								;
							return (isIE11 || isIE);
						},

						ios: function () {
							var
								userAgent = navigator.userAgent,
								isIOS = userAgent.match(regExp.ios),
								isMobileChrome = userAgent.match(regExp.mobileChrome)
								;
							if (isIOS && !isMobileChrome) {
								module.verbose('Browser was found to be iOS', userAgent);
								return true;
							}
							else {
								return false;
							}
						},
						mobile: function () {
							var
								userAgent = navigator.userAgent,
								isMobile = userAgent.match(regExp.mobile)
								;
							if (isMobile) {
								module.verbose('Browser was found to be mobile', userAgent);
								return true;
							}
							else {
								module.verbose('Browser is not mobile, using regular transition', userAgent);
								return false;
							}
						},
						hidden: function () {
							return !module.is.visible();
						},
						visible: function () {
							return $module.hasClass(className.visible);
						},
						// alias
						open: function () {
							return module.is.visible();
						},
						closed: function () {
							return module.is.hidden();
						},
						vertical: function () {
							return $module.hasClass(className.top);
						},
						animating: function () {
							return $context.hasClass(className.animating);
						},
						rtl: function () {
							if (module.cache.rtl === undefined) {
								module.cache.rtl = ($module.css('direction') == 'rtl');
							}
							return module.cache.rtl;
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				}
					;

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						module.invoke('destroy');
					}
					module.initialize();
				}
			});

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.sidebar.settings = {

		name: 'Sidebar',
		namespace: 'sidebar',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		transition: 'auto',
		mobileTransition: 'auto',

		defaultTransition: {
			computer: {
				left: 'uncover',
				right: 'uncover',
				top: 'overlay',
				bottom: 'overlay'
			},
			mobile: {
				left: 'uncover',
				right: 'uncover',
				top: 'overlay',
				bottom: 'overlay'
			}
		},

		context: 'body',
		exclusive: false,
		closable: true,
		dimPage: true,
		scrollLock: false,
		returnScroll: false,
		delaySetup: false,

		duration: 500,

		onChange: function () { },
		onShow: function () { },
		onHide: function () { },

		onHidden: function () { },
		onVisible: function () { },

		className: {
			active: 'active',
			animating: 'animating',
			dimmed: 'dimmed',
			ios: 'ios',
			pushable: 'pushable',
			pushed: 'pushed',
			right: 'right',
			top: 'top',
			left: 'left',
			bottom: 'bottom',
			visible: 'visible'
		},

		selector: {
			fixed: '.fixed',
			omitted: 'script, link, style, .ui.modal, .ui.dimmer, .ui.nag, .ui.fixed',
			pusher: '.pusher',
			sidebar: '.ui.sidebar'
		},

		regExp: {
			ios: /(iPad|iPhone|iPod)/g,
			mobileChrome: /(CriOS)/g,
			mobile: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/g
		},

		error: {
			method: 'The method you called is not defined.',
			pusher: 'Had to add pusher element. For optimal performance make sure body content is inside a pusher element',
			movedSidebar: 'Had to move sidebar. For optimal performance make sure sidebar and pusher are direct children of your body tag',
			overlay: 'The overlay setting is no longer supported, use animation: overlay',
			notFound: 'There were no elements that matched the specified selector'
		}

	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Sticky
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.sticky = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.sticky.settings, parameters)
						: $.extend({}, $.fn.sticky.settings),

					className = settings.className,
					namespace = settings.namespace,
					error = settings.error,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$module = $(this),
					$window = $(window),
					$scroll = $(settings.scrollContext),
					$container,
					$context,

					selector = $module.selector || '',
					instance = $module.data(moduleNamespace),

					requestAnimationFrame = window.requestAnimationFrame
						|| window.mozRequestAnimationFrame
						|| window.webkitRequestAnimationFrame
						|| window.msRequestAnimationFrame
						|| function (callback) { setTimeout(callback, 0); },

					element = this,

					documentObserver,
					observer,
					module
					;

				module = {

					initialize: function () {

						module.determineContainer();
						module.determineContext();
						module.verbose('Initializing sticky', settings, $container);

						module.save.positions();
						module.checkErrors();
						module.bind.events();

						if (settings.observeChanges) {
							module.observeChanges();
						}
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous instance');
						module.reset();
						if (documentObserver) {
							documentObserver.disconnect();
						}
						if (observer) {
							observer.disconnect();
						}
						$window
							.off('load' + eventNamespace, module.event.load)
							.off('resize' + eventNamespace, module.event.resize)
							;
						$scroll
							.off('scrollchange' + eventNamespace, module.event.scrollchange)
							;
						$module.removeData(moduleNamespace);
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							documentObserver = new MutationObserver(module.event.documentChanged);
							observer = new MutationObserver(module.event.changed);
							documentObserver.observe(document, {
								childList: true,
								subtree: true
							});
							observer.observe(element, {
								childList: true,
								subtree: true
							});
							observer.observe($context[0], {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', observer);
						}
					},

					determineContainer: function () {
						if (settings.container) {
							$container = $(settings.container);
						}
						else {
							$container = $module.offsetParent();
						}
					},

					determineContext: function () {
						if (settings.context) {
							$context = $(settings.context);
						}
						else {
							$context = $container;
						}
						if ($context.length === 0) {
							module.error(error.invalidContext, settings.context, $module);
							return;
						}
					},

					checkErrors: function () {
						if (module.is.hidden()) {
							module.error(error.visible, $module);
						}
						if (module.cache.element.height > module.cache.context.height) {
							module.reset();
							module.error(error.elementSize, $module);
							return;
						}
					},

					bind: {
						events: function () {
							$window
								.on('load' + eventNamespace, module.event.load)
								.on('resize' + eventNamespace, module.event.resize)
								;
							// pub/sub pattern
							$scroll
								.off('scroll' + eventNamespace)
								.on('scroll' + eventNamespace, module.event.scroll)
								.on('scrollchange' + eventNamespace, module.event.scrollchange)
								;
						}
					},

					event: {
						changed: function (mutations) {
							clearTimeout(module.timer);
							module.timer = setTimeout(function () {
								module.verbose('DOM tree modified, updating sticky menu', mutations);
								module.refresh();
							}, 100);
						},
						documentChanged: function (mutations) {
							[].forEach.call(mutations, function (mutation) {
								if (mutation.removedNodes) {
									[].forEach.call(mutation.removedNodes, function (node) {
										if (node == element || $(node).find(element).length > 0) {
											module.debug('Element removed from DOM, tearing down events');
											module.destroy();
										}
									});
								}
							});
						},
						load: function () {
							module.verbose('Page contents finished loading');
							requestAnimationFrame(module.refresh);
						},
						resize: function () {
							module.verbose('Window resized');
							requestAnimationFrame(module.refresh);
						},
						scroll: function () {
							requestAnimationFrame(function () {
								$scroll.triggerHandler('scrollchange' + eventNamespace, $scroll.scrollTop());
							});
						},
						scrollchange: function (event, scrollPosition) {
							module.stick(scrollPosition);
							settings.onScroll.call(element);
						}
					},

					refresh: function (hardRefresh) {
						module.reset();
						if (!settings.context) {
							module.determineContext();
						}
						if (hardRefresh) {
							module.determineContainer();
						}
						module.save.positions();
						module.stick();
						settings.onReposition.call(element);
					},

					supports: {
						sticky: function () {
							var
								$element = $('<div/>'),
								element = $element[0]
								;
							$element.addClass(className.supported);
							return ($element.css('position').match('sticky'));
						}
					},

					save: {
						lastScroll: function (scroll) {
							module.lastScroll = scroll;
						},
						elementScroll: function (scroll) {
							module.elementScroll = scroll;
						},
						positions: function () {
							var
								scrollContext = {
									height: $scroll.height()
								},
								element = {
									margin: {
										top: parseInt($module.css('margin-top'), 10),
										bottom: parseInt($module.css('margin-bottom'), 10),
									},
									offset: $module.offset(),
									width: $module.outerWidth(),
									height: $module.outerHeight()
								},
								context = {
									offset: $context.offset(),
									height: $context.outerHeight()
								},
								container = {
									height: $container.outerHeight()
								}
								;
							if (!module.is.standardScroll()) {
								module.debug('Non-standard scroll. Removing scroll offset from element offset');

								scrollContext.top = $scroll.scrollTop();
								scrollContext.left = $scroll.scrollLeft();

								element.offset.top += scrollContext.top;
								context.offset.top += scrollContext.top;
								element.offset.left += scrollContext.left;
								context.offset.left += scrollContext.left;
							}
							module.cache = {
								fits: ((element.height + settings.offset) <= scrollContext.height),
								sameHeight: (element.height == context.height),
								scrollContext: {
									height: scrollContext.height
								},
								element: {
									margin: element.margin,
									top: element.offset.top - element.margin.top,
									left: element.offset.left,
									width: element.width,
									height: element.height,
									bottom: element.offset.top + element.height
								},
								context: {
									top: context.offset.top,
									height: context.height,
									bottom: context.offset.top + context.height
								}
							};
							module.set.containerSize();

							module.stick();
							module.debug('Caching element positions', module.cache);
						}
					},

					get: {
						direction: function (scroll) {
							var
								direction = 'down'
								;
							scroll = scroll || $scroll.scrollTop();
							if (module.lastScroll !== undefined) {
								if (module.lastScroll < scroll) {
									direction = 'down';
								}
								else if (module.lastScroll > scroll) {
									direction = 'up';
								}
							}
							return direction;
						},
						scrollChange: function (scroll) {
							scroll = scroll || $scroll.scrollTop();
							return (module.lastScroll)
								? (scroll - module.lastScroll)
								: 0
								;
						},
						currentElementScroll: function () {
							if (module.elementScroll) {
								return module.elementScroll;
							}
							return (module.is.top())
								? Math.abs(parseInt($module.css('top'), 10)) || 0
								: Math.abs(parseInt($module.css('bottom'), 10)) || 0
								;
						},

						elementScroll: function (scroll) {
							scroll = scroll || $scroll.scrollTop();
							var
								element = module.cache.element,
								scrollContext = module.cache.scrollContext,
								delta = module.get.scrollChange(scroll),
								maxScroll = (element.height - scrollContext.height + settings.offset),
								elementScroll = module.get.currentElementScroll(),
								possibleScroll = (elementScroll + delta)
								;
							if (module.cache.fits || possibleScroll < 0) {
								elementScroll = 0;
							}
							else if (possibleScroll > maxScroll) {
								elementScroll = maxScroll;
							}
							else {
								elementScroll = possibleScroll;
							}
							return elementScroll;
						}
					},

					remove: {
						lastScroll: function () {
							delete module.lastScroll;
						},
						elementScroll: function (scroll) {
							delete module.elementScroll;
						},
						minimumSize: function () {
							$container
								.css('min-height', '')
								;
						},
						offset: function () {
							$module.css('margin-top', '');
						}
					},

					set: {
						offset: function () {
							module.verbose('Setting offset on element', settings.offset);
							$module
								.css('margin-top', settings.offset)
								;
						},
						containerSize: function () {
							var
								tagName = $container.get(0).tagName
								;
							if (tagName === 'HTML' || tagName == 'body') {
								// this can trigger for too many reasons
								//module.error(error.container, tagName, $module);
								module.determineContainer();
							}
							else {
								var tallestHeight = Math.max(module.cache.context.height, module.cache.element.height);
								if (tallestHeight - $container.outerHeight() > settings.jitter) {
									module.debug('Context is taller than container. Specifying exact height for container', module.cache.context.height);
									$container.css({
										height: tallestHeight,
									});
								}
								else {
									$container.css({
										height: '',
									});
								}
								if (Math.abs($container.outerHeight() - module.cache.context.height) > settings.jitter) {
									module.debug('Context has padding, specifying exact height for container', module.cache.context.height);
									$container.css({
										height: module.cache.context.height
									});
								}
							}
						},
						minimumSize: function () {
							var
								element = module.cache.element
								;
							$container
								.css('min-height', element.height)
								;
						},
						scroll: function (scroll) {
							module.debug('Setting scroll on element', scroll);
							if (module.elementScroll == scroll) {
								return;
							}
							if (module.is.top()) {
								$module
									.css('bottom', '')
									.css('top', -scroll)
									;
							}
							if (module.is.bottom()) {
								$module
									.css('top', '')
									.css('bottom', scroll)
									;
							}
						},
						size: function () {
							if (module.cache.element.height !== 0 && module.cache.element.width !== 0) {
								element.style.setProperty('width', module.cache.element.width + 'px', 'important');
								element.style.setProperty('height', module.cache.element.height + 'px', 'important');
							}
						}
					},

					is: {
						standardScroll: function () {
							return ($scroll[0] == window);
						},
						top: function () {
							return $module.hasClass(className.top);
						},
						bottom: function () {
							return $module.hasClass(className.bottom);
						},
						initialPosition: function () {
							return (!module.is.fixed() && !module.is.bound());
						},
						hidden: function () {
							return (!$module.is(':visible'));
						},
						bound: function () {
							return $module.hasClass(className.bound);
						},
						fixed: function () {
							return $module.hasClass(className.fixed);
						}
					},

					stick: function (scroll) {
						var
							cachedPosition = scroll || $scroll.scrollTop(),
							cache = module.cache,
							fits = cache.fits,
							sameHeight = cache.sameHeight,
							element = cache.element,
							scrollContext = cache.scrollContext,
							context = cache.context,
							offset = (module.is.bottom() && settings.pushing)
								? settings.bottomOffset
								: settings.offset,
							scroll = {
								top: cachedPosition + offset,
								bottom: cachedPosition + offset + scrollContext.height
							},
							direction = module.get.direction(scroll.top),
							elementScroll = (fits)
								? 0
								: module.get.elementScroll(scroll.top),

							// shorthand
							doesntFit = !fits,
							elementVisible = (element.height !== 0)
							;
						if (elementVisible && !sameHeight) {

							if (module.is.initialPosition()) {
								if (scroll.top >= context.bottom) {
									module.debug('Initial element position is bottom of container');
									module.bindBottom();
								}
								else if (scroll.top > element.top) {
									if ((element.height + scroll.top - elementScroll) >= context.bottom && element.height < context.height) {
										module.debug('Initial element position is bottom of container');
										module.bindBottom();
									}
									else {
										module.debug('Initial element position is fixed');
										module.fixTop();
									}
								}

							}
							else if (module.is.fixed()) {

								// currently fixed top
								if (module.is.top()) {
									if (scroll.top <= element.top) {
										module.debug('Fixed element reached top of container');
										module.setInitialPosition();
									}
									else if ((element.height + scroll.top - elementScroll) >= context.bottom) {
										module.debug('Fixed element reached bottom of container');
										module.bindBottom();
									}
									// scroll element if larger than screen
									else if (doesntFit) {
										module.set.scroll(elementScroll);
										module.save.lastScroll(scroll.top);
										module.save.elementScroll(elementScroll);
									}
								}

								// currently fixed bottom
								else if (module.is.bottom()) {

									// top edge
									if ((scroll.bottom - element.height) <= element.top) {
										module.debug('Bottom fixed rail has reached top of container');
										module.setInitialPosition();
									}
									// bottom edge
									else if (scroll.bottom >= context.bottom) {
										module.debug('Bottom fixed rail has reached bottom of container');
										module.bindBottom();
									}
									// scroll element if larger than screen
									else if (doesntFit) {
										module.set.scroll(elementScroll);
										module.save.lastScroll(scroll.top);
										module.save.elementScroll(elementScroll);
									}

								}
							}
							else if (module.is.bottom()) {
								if (scroll.top <= element.top) {
									module.debug('Jumped from bottom fixed to top fixed, most likely used home/end button');
									module.setInitialPosition();
								}
								else {
									if (settings.pushing) {
										if (module.is.bound() && scroll.bottom <= context.bottom) {
											module.debug('Fixing bottom attached element to bottom of browser.');
											module.fixBottom();
										}
									}
									else {
										if (module.is.bound() && (scroll.top <= context.bottom - element.height)) {
											module.debug('Fixing bottom attached element to top of browser.');
											module.fixTop();
										}
									}
								}
							}
						}
					},

					bindTop: function () {
						module.debug('Binding element to top of parent container');
						module.remove.offset();
						if (settings.setSize) {
							module.set.size();
						}
						$module
							.css({
								left: '',
								top: '',
								marginBottom: ''
							})
							.removeClass(className.fixed)
							.removeClass(className.bottom)
							.addClass(className.bound)
							.addClass(className.top)
							;
						settings.onTop.call(element);
						settings.onUnstick.call(element);
					},
					bindBottom: function () {
						module.debug('Binding element to bottom of parent container');
						module.remove.offset();
						if (settings.setSize) {
							module.set.size();
						}
						$module
							.css({
								left: '',
								top: ''
							})
							.removeClass(className.fixed)
							.removeClass(className.top)
							.addClass(className.bound)
							.addClass(className.bottom)
							;
						settings.onBottom.call(element);
						settings.onUnstick.call(element);
					},

					setInitialPosition: function () {
						module.debug('Returning to initial position');
						module.unfix();
						module.unbind();
					},


					fixTop: function () {
						module.debug('Fixing element to top of page');
						if (settings.setSize) {
							module.set.size();
						}
						module.set.minimumSize();
						module.set.offset();
						$module
							.css({
								left: module.cache.element.left,
								bottom: '',
								marginBottom: ''
							})
							.removeClass(className.bound)
							.removeClass(className.bottom)
							.addClass(className.fixed)
							.addClass(className.top)
							;
						settings.onStick.call(element);
					},

					fixBottom: function () {
						module.debug('Sticking element to bottom of page');
						if (settings.setSize) {
							module.set.size();
						}
						module.set.minimumSize();
						module.set.offset();
						$module
							.css({
								left: module.cache.element.left,
								bottom: '',
								marginBottom: ''
							})
							.removeClass(className.bound)
							.removeClass(className.top)
							.addClass(className.fixed)
							.addClass(className.bottom)
							;
						settings.onStick.call(element);
					},

					unbind: function () {
						if (module.is.bound()) {
							module.debug('Removing container bound position on element');
							module.remove.offset();
							$module
								.removeClass(className.bound)
								.removeClass(className.top)
								.removeClass(className.bottom)
								;
						}
					},

					unfix: function () {
						if (module.is.fixed()) {
							module.debug('Removing fixed position on element');
							module.remove.minimumSize();
							module.remove.offset();
							$module
								.removeClass(className.fixed)
								.removeClass(className.top)
								.removeClass(className.bottom)
								;
							settings.onUnstick.call(element);
						}
					},

					reset: function () {
						module.debug('Resetting elements position');
						module.unbind();
						module.unfix();
						module.resetCSS();
						module.remove.offset();
						module.remove.lastScroll();
					},

					resetCSS: function () {
						$module
							.css({
								width: '',
								height: ''
							})
							;
						$container
							.css({
								height: ''
							})
							;
					},

					setting: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							settings[name] = value;
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 0);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.sticky.settings = {

		name: 'Sticky',
		namespace: 'sticky',

		silent: false,
		debug: false,
		verbose: true,
		performance: true,

		// whether to stick in the opposite direction on scroll up
		pushing: false,

		context: false,
		container: false,

		// Context to watch scroll events
		scrollContext: window,

		// Offset to adjust scroll
		offset: 0,

		// Offset to adjust scroll when attached to bottom of screen
		bottomOffset: 0,

		// will only set container height if difference between context and container is larger than this number
		jitter: 5,

		// set width of sticky element when it is fixed to page (used to make sure 100% width is maintained if no fixed size set)
		setSize: true,

		// Whether to automatically observe changes with Mutation Observers
		observeChanges: false,

		// Called when position is recalculated
		onReposition: function () { },

		// Called on each scroll
		onScroll: function () { },

		// Called when element is stuck to viewport
		onStick: function () { },

		// Called when element is unstuck from viewport
		onUnstick: function () { },

		// Called when element reaches top of context
		onTop: function () { },

		// Called when element reaches bottom of context
		onBottom: function () { },

		error: {
			container: 'Sticky element must be inside a relative container',
			visible: 'Element is hidden, you must call refresh after element becomes visible. Use silent setting to surpress this warning in production.',
			method: 'The method you called is not defined.',
			invalidContext: 'Context specified does not exist',
			elementSize: 'Sticky element is larger than its container, cannot create sticky.'
		},

		className: {
			bound: 'bound',
			fixed: 'fixed',
			supported: 'native',
			top: 'top',
			bottom: 'bottom'
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Tab
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.tab = function (parameters) {

		var
			// use window context if none specified
			$allModules = $.isFunction(this)
				? $(window)
				: $(this),

			moduleSelector = $allModules.selector || '',
			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			initializedHistory = false,
			returnedValue
			;

		$allModules
			.each(function () {
				var

					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.tab.settings, parameters)
						: $.extend({}, $.fn.tab.settings),

					className = settings.className,
					metadata = settings.metadata,
					selector = settings.selector,
					error = settings.error,

					eventNamespace = '.' + settings.namespace,
					moduleNamespace = 'module-' + settings.namespace,

					$module = $(this),
					$context,
					$tabs,

					cache = {},
					firstLoad = true,
					recursionDepth = 0,
					element = this,
					instance = $module.data(moduleNamespace),

					activeTabPath,
					parameterArray,
					module,

					historyEvent

					;

				module = {

					initialize: function () {
						module.debug('Initializing tab menu item', $module);
						module.fix.callbacks();
						module.determineTabs();

						module.debug('Determining tabs', settings.context, $tabs);
						// set up automatic routing
						if (settings.auto) {
							module.set.auto();
						}
						module.bind.events();

						if (settings.history && !initializedHistory) {
							module.initializeHistory();
							initializedHistory = true;
						}

						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, module)
							;
					},

					destroy: function () {
						module.debug('Destroying tabs', $module);
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
					},

					bind: {
						events: function () {
							// if using $.tab don't add events
							if (!$.isWindow(element)) {
								module.debug('Attaching tab activation events to element', $module);
								$module
									.on('click' + eventNamespace, module.event.click)
									;
							}
						}
					},

					determineTabs: function () {
						var
							$reference
							;

						// determine tab context
						if (settings.context === 'parent') {
							if ($module.closest(selector.ui).length > 0) {
								$reference = $module.closest(selector.ui);
								module.verbose('Using closest UI element as parent', $reference);
							}
							else {
								$reference = $module;
							}
							$context = $reference.parent();
							module.verbose('Determined parent element for creating context', $context);
						}
						else if (settings.context) {
							$context = $(settings.context);
							module.verbose('Using selector for tab context', settings.context, $context);
						}
						else {
							$context = $('body');
						}
						// find tabs
						if (settings.childrenOnly) {
							$tabs = $context.children(selector.tabs);
							module.debug('Searching tab context children for tabs', $context, $tabs);
						}
						else {
							$tabs = $context.find(selector.tabs);
							module.debug('Searching tab context for tabs', $context, $tabs);
						}
					},

					fix: {
						callbacks: function () {
							if ($.isPlainObject(parameters) && (parameters.onTabLoad || parameters.onTabInit)) {
								if (parameters.onTabLoad) {
									parameters.onLoad = parameters.onTabLoad;
									delete parameters.onTabLoad;
									module.error(error.legacyLoad, parameters.onLoad);
								}
								if (parameters.onTabInit) {
									parameters.onFirstLoad = parameters.onTabInit;
									delete parameters.onTabInit;
									module.error(error.legacyInit, parameters.onFirstLoad);
								}
								settings = $.extend(true, {}, $.fn.tab.settings, parameters);
							}
						}
					},

					initializeHistory: function () {
						module.debug('Initializing page state');
						if ($.address === undefined) {
							module.error(error.state);
							return false;
						}
						else {
							if (settings.historyType == 'state') {
								module.debug('Using HTML5 to manage state');
								if (settings.path !== false) {
									$.address
										.history(true)
										.state(settings.path)
										;
								}
								else {
									module.error(error.path);
									return false;
								}
							}
							$.address
								.bind('change', module.event.history.change)
								;
						}
					},

					event: {
						click: function (event) {
							var
								tabPath = $(this).data(metadata.tab)
								;
							if (tabPath !== undefined) {
								if (settings.history) {
									module.verbose('Updating page state', event);
									$.address.value(tabPath);
								}
								else {
									module.verbose('Changing tab', event);
									module.changeTab(tabPath);
								}
								event.preventDefault();
							}
							else {
								module.debug('No tab specified');
							}
						},
						history: {
							change: function (event) {
								var
									tabPath = event.pathNames.join('/') || module.get.initialPath(),
									pageTitle = settings.templates.determineTitle(tabPath) || false
									;
								module.performance.display();
								module.debug('History change event', tabPath, event);
								historyEvent = event;
								if (tabPath !== undefined) {
									module.changeTab(tabPath);
								}
								if (pageTitle) {
									$.address.title(pageTitle);
								}
							}
						}
					},

					refresh: function () {
						if (activeTabPath) {
							module.debug('Refreshing tab', activeTabPath);
							module.changeTab(activeTabPath);
						}
					},

					cache: {

						read: function (cacheKey) {
							return (cacheKey !== undefined)
								? cache[cacheKey]
								: false
								;
						},
						add: function (cacheKey, content) {
							cacheKey = cacheKey || activeTabPath;
							module.debug('Adding cached content for', cacheKey);
							cache[cacheKey] = content;
						},
						remove: function (cacheKey) {
							cacheKey = cacheKey || activeTabPath;
							module.debug('Removing cached content for', cacheKey);
							delete cache[cacheKey];
						}
					},

					set: {
						auto: function () {
							var
								url = (typeof settings.path == 'string')
									? settings.path.replace(/\/$/, '') + '/{$tab}'
									: '/{$tab}'
								;
							module.verbose('Setting up automatic tab retrieval from server', url);
							if ($.isPlainObject(settings.apiSettings)) {
								settings.apiSettings.url = url;
							}
							else {
								settings.apiSettings = {
									url: url
								};
							}
						},
						loading: function (tabPath) {
							var
								$tab = module.get.tabElement(tabPath),
								isLoading = $tab.hasClass(className.loading)
								;
							if (!isLoading) {
								module.verbose('Setting loading state for', $tab);
								$tab
									.addClass(className.loading)
									.siblings($tabs)
									.removeClass(className.active + ' ' + className.loading)
									;
								if ($tab.length > 0) {
									settings.onRequest.call($tab[0], tabPath);
								}
							}
						},
						state: function (state) {
							$.address.value(state);
						}
					},

					changeTab: function (tabPath) {
						var
							pushStateAvailable = (window.history && window.history.pushState),
							shouldIgnoreLoad = (pushStateAvailable && settings.ignoreFirstLoad && firstLoad),
							remoteContent = (settings.auto || $.isPlainObject(settings.apiSettings)),
							// only add default path if not remote content
							pathArray = (remoteContent && !shouldIgnoreLoad)
								? module.utilities.pathToArray(tabPath)
								: module.get.defaultPathArray(tabPath)
							;
						tabPath = module.utilities.arrayToPath(pathArray);
						$.each(pathArray, function (index, tab) {
							var
								currentPathArray = pathArray.slice(0, index + 1),
								currentPath = module.utilities.arrayToPath(currentPathArray),

								isTab = module.is.tab(currentPath),
								isLastIndex = (index + 1 == pathArray.length),

								$tab = module.get.tabElement(currentPath),
								$anchor,
								nextPathArray,
								nextPath,
								isLastTab
								;
							module.verbose('Looking for tab', tab);
							if (isTab) {
								module.verbose('Tab was found', tab);
								// scope up
								activeTabPath = currentPath;
								parameterArray = module.utilities.filterArray(pathArray, currentPathArray);

								if (isLastIndex) {
									isLastTab = true;
								}
								else {
									nextPathArray = pathArray.slice(0, index + 2);
									nextPath = module.utilities.arrayToPath(nextPathArray);
									isLastTab = (!module.is.tab(nextPath));
									if (isLastTab) {
										module.verbose('Tab parameters found', nextPathArray);
									}
								}
								if (isLastTab && remoteContent) {
									if (!shouldIgnoreLoad) {
										module.activate.navigation(currentPath);
										module.fetch.content(currentPath, tabPath);
									}
									else {
										module.debug('Ignoring remote content on first tab load', currentPath);
										firstLoad = false;
										module.cache.add(tabPath, $tab.html());
										module.activate.all(currentPath);
										settings.onFirstLoad.call($tab[0], currentPath, parameterArray, historyEvent);
										settings.onLoad.call($tab[0], currentPath, parameterArray, historyEvent);
									}
									return false;
								}
								else {
									module.debug('Opened local tab', currentPath);
									module.activate.all(currentPath);
									if (!module.cache.read(currentPath)) {
										module.cache.add(currentPath, true);
										module.debug('First time tab loaded calling tab init');
										settings.onFirstLoad.call($tab[0], currentPath, parameterArray, historyEvent);
									}
									settings.onLoad.call($tab[0], currentPath, parameterArray, historyEvent);
								}

							}
							else if (tabPath.search('/') == -1 && tabPath !== '') {
								// look for in page anchor
								$anchor = $('#' + tabPath + ', a[name="' + tabPath + '"]');
								currentPath = $anchor.closest('[data-tab]').data(metadata.tab);
								$tab = module.get.tabElement(currentPath);
								// if anchor exists use parent tab
								if ($anchor && $anchor.length > 0 && currentPath) {
									module.debug('Anchor link used, opening parent tab', $tab, $anchor);
									if (!$tab.hasClass(className.active)) {
										setTimeout(function () {
											module.scrollTo($anchor);
										}, 0);
									}
									module.activate.all(currentPath);
									if (!module.cache.read(currentPath)) {
										module.cache.add(currentPath, true);
										module.debug('First time tab loaded calling tab init');
										settings.onFirstLoad.call($tab[0], currentPath, parameterArray, historyEvent);
									}
									settings.onLoad.call($tab[0], currentPath, parameterArray, historyEvent);
									return false;
								}
							}
							else {
								module.error(error.missingTab, $module, $context, currentPath);
								return false;
							}
						});
					},

					scrollTo: function ($element) {
						var
							scrollOffset = ($element && $element.length > 0)
								? $element.offset().top
								: false
							;
						if (scrollOffset !== false) {
							module.debug('Forcing scroll to an in-page link in a hidden tab', scrollOffset, $element);
							$(document).scrollTop(scrollOffset);
						}
					},

					update: {
						content: function (tabPath, html, evaluateScripts) {
							var
								$tab = module.get.tabElement(tabPath),
								tab = $tab[0]
								;
							evaluateScripts = (evaluateScripts !== undefined)
								? evaluateScripts
								: settings.evaluateScripts
								;
							if (typeof settings.cacheType == 'string' && settings.cacheType.toLowerCase() == 'dom' && typeof html !== 'string') {
								$tab
									.empty()
									.append($(html).clone(true))
									;
							}
							else {
								if (evaluateScripts) {
									module.debug('Updating HTML and evaluating inline scripts', tabPath, html);
									$tab.html(html);
								}
								else {
									module.debug('Updating HTML', tabPath, html);
									tab.innerHTML = html;
								}
							}
						}
					},

					fetch: {

						content: function (tabPath, fullTabPath) {
							var
								$tab = module.get.tabElement(tabPath),
								apiSettings = {
									dataType: 'html',
									encodeParameters: false,
									on: 'now',
									cache: settings.alwaysRefresh,
									headers: {
										'X-Remote': true
									},
									onSuccess: function (response) {
										if (settings.cacheType == 'response') {
											module.cache.add(fullTabPath, response);
										}
										module.update.content(tabPath, response);
										if (tabPath == activeTabPath) {
											module.debug('Content loaded', tabPath);
											module.activate.tab(tabPath);
										}
										else {
											module.debug('Content loaded in background', tabPath);
										}
										settings.onFirstLoad.call($tab[0], tabPath, parameterArray, historyEvent);
										settings.onLoad.call($tab[0], tabPath, parameterArray, historyEvent);

										if (settings.loadOnce) {
											module.cache.add(fullTabPath, true);
										}
										else if (typeof settings.cacheType == 'string' && settings.cacheType.toLowerCase() == 'dom' && $tab.children().length > 0) {
											setTimeout(function () {
												var
													$clone = $tab.children().clone(true)
													;
												$clone = $clone.not('script');
												module.cache.add(fullTabPath, $clone);
											}, 0);
										}
										else {
											module.cache.add(fullTabPath, $tab.html());
										}
									},
									urlData: {
										tab: fullTabPath
									}
								},
								request = $tab.api('get request') || false,
								existingRequest = (request && request.state() === 'pending'),
								requestSettings,
								cachedContent
								;

							fullTabPath = fullTabPath || tabPath;
							cachedContent = module.cache.read(fullTabPath);


							if (settings.cache && cachedContent) {
								module.activate.tab(tabPath);
								module.debug('Adding cached content', fullTabPath);
								if (!settings.loadOnce) {
									if (settings.evaluateScripts == 'once') {
										module.update.content(tabPath, cachedContent, false);
									}
									else {
										module.update.content(tabPath, cachedContent);
									}
								}
								settings.onLoad.call($tab[0], tabPath, parameterArray, historyEvent);
							}
							else if (existingRequest) {
								module.set.loading(tabPath);
								module.debug('Content is already loading', fullTabPath);
							}
							else if ($.api !== undefined) {
								requestSettings = $.extend(true, {}, settings.apiSettings, apiSettings);
								module.debug('Retrieving remote content', fullTabPath, requestSettings);
								module.set.loading(tabPath);
								$tab.api(requestSettings);
							}
							else {
								module.error(error.api);
							}
						}
					},

					activate: {
						all: function (tabPath) {
							module.activate.tab(tabPath);
							module.activate.navigation(tabPath);
						},
						tab: function (tabPath) {
							var
								$tab = module.get.tabElement(tabPath),
								$deactiveTabs = (settings.deactivate == 'siblings')
									? $tab.siblings($tabs)
									: $tabs.not($tab),
								isActive = $tab.hasClass(className.active)
								;
							module.verbose('Showing tab content for', $tab);
							if (!isActive) {
								$tab
									.addClass(className.active)
									;
								$deactiveTabs
									.removeClass(className.active + ' ' + className.loading)
									;
								if ($tab.length > 0) {
									settings.onVisible.call($tab[0], tabPath);
								}
							}
						},
						navigation: function (tabPath) {
							var
								$navigation = module.get.navElement(tabPath),
								$deactiveNavigation = (settings.deactivate == 'siblings')
									? $navigation.siblings($allModules)
									: $allModules.not($navigation),
								isActive = $navigation.hasClass(className.active)
								;
							module.verbose('Activating tab navigation for', $navigation, tabPath);
							if (!isActive) {
								$navigation
									.addClass(className.active)
									;
								$deactiveNavigation
									.removeClass(className.active + ' ' + className.loading)
									;
							}
						}
					},

					deactivate: {
						all: function () {
							module.deactivate.navigation();
							module.deactivate.tabs();
						},
						navigation: function () {
							$allModules
								.removeClass(className.active)
								;
						},
						tabs: function () {
							$tabs
								.removeClass(className.active + ' ' + className.loading)
								;
						}
					},

					is: {
						tab: function (tabName) {
							return (tabName !== undefined)
								? (module.get.tabElement(tabName).length > 0)
								: false
								;
						}
					},

					get: {
						initialPath: function () {
							return $allModules.eq(0).data(metadata.tab) || $tabs.eq(0).data(metadata.tab);
						},
						path: function () {
							return $.address.value();
						},
						// adds default tabs to tab path
						defaultPathArray: function (tabPath) {
							return module.utilities.pathToArray(module.get.defaultPath(tabPath));
						},
						defaultPath: function (tabPath) {
							var
								$defaultNav = $allModules.filter('[data-' + metadata.tab + '^="' + tabPath + '/"]').eq(0),
								defaultTab = $defaultNav.data(metadata.tab) || false
								;
							if (defaultTab) {
								module.debug('Found default tab', defaultTab);
								if (recursionDepth < settings.maxDepth) {
									recursionDepth++;
									return module.get.defaultPath(defaultTab);
								}
								module.error(error.recursion);
							}
							else {
								module.debug('No default tabs found for', tabPath, $tabs);
							}
							recursionDepth = 0;
							return tabPath;
						},
						navElement: function (tabPath) {
							tabPath = tabPath || activeTabPath;
							return $allModules.filter('[data-' + metadata.tab + '="' + tabPath + '"]');
						},
						tabElement: function (tabPath) {
							var
								$fullPathTab,
								$simplePathTab,
								tabPathArray,
								lastTab
								;
							tabPath = tabPath || activeTabPath;
							tabPathArray = module.utilities.pathToArray(tabPath);
							lastTab = module.utilities.last(tabPathArray);
							$fullPathTab = $tabs.filter('[data-' + metadata.tab + '="' + tabPath + '"]');
							$simplePathTab = $tabs.filter('[data-' + metadata.tab + '="' + lastTab + '"]');
							return ($fullPathTab.length > 0)
								? $fullPathTab
								: $simplePathTab
								;
						},
						tab: function () {
							return activeTabPath;
						}
					},

					utilities: {
						filterArray: function (keepArray, removeArray) {
							return $.grep(keepArray, function (keepValue) {
								return ($.inArray(keepValue, removeArray) == -1);
							});
						},
						last: function (array) {
							return $.isArray(array)
								? array[array.length - 1]
								: false
								;
						},
						pathToArray: function (pathName) {
							if (pathName === undefined) {
								pathName = activeTabPath;
							}
							return typeof pathName == 'string'
								? pathName.split('/')
								: [pathName]
								;
						},
						arrayToPath: function (pathArray) {
							return $.isArray(pathArray)
								? pathArray.join('/')
								: false
								;
						}
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};
				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;
		return (returnedValue !== undefined)
			? returnedValue
			: this
			;

	};

	// shortcut for tabbed content with no defined navigation
	$.tab = function () {
		$(window).tab.apply(this, arguments);
	};

	$.fn.tab.settings = {

		name: 'Tab',
		namespace: 'tab',

		silent: false,
		debug: false,
		verbose: false,
		performance: true,

		auto: false,      // uses pjax style endpoints fetching content from same url with remote-content headers
		history: false,      // use browser history
		historyType: 'hash',     // #/ or html5 state
		path: false,      // base path of url

		context: false,      // specify a context that tabs must appear inside
		childrenOnly: false,      // use only tabs that are children of context
		maxDepth: 25,         // max depth a tab can be nested

		deactivate: 'siblings', // whether tabs should deactivate sibling menu elements or all elements initialized together

		alwaysRefresh: false,      // load tab content new every tab click
		cache: true,       // cache the content requests to pull locally
		loadOnce: false,      // Whether tab data should only be loaded once when using remote content
		cacheType: 'response', // Whether to cache exact response, or to html cache contents after scripts execute
		ignoreFirstLoad: false,      // don't load remote content on first load

		apiSettings: false,      // settings for api call
		evaluateScripts: 'once',     // whether inline scripts should be parsed (true/false/once). Once will not re-evaluate on cached content

		onFirstLoad: function (tabPath, parameterArray, historyEvent) { }, // called first time loaded
		onLoad: function (tabPath, parameterArray, historyEvent) { }, // called on every load
		onVisible: function (tabPath, parameterArray, historyEvent) { }, // called every time tab visible
		onRequest: function (tabPath, parameterArray, historyEvent) { }, // called ever time a tab beings loading remote content

		templates: {
			determineTitle: function (tabArray) { } // returns page title for path
		},

		error: {
			api: 'You attempted to load content without API module',
			method: 'The method you called is not defined',
			missingTab: 'Activated tab cannot be found. Tabs are case-sensitive.',
			noContent: 'The tab you specified is missing a content url.',
			path: 'History enabled, but no path was specified',
			recursion: 'Max recursive depth reached',
			legacyInit: 'onTabInit has been renamed to onFirstLoad in 2.0, please adjust your code.',
			legacyLoad: 'onTabLoad has been renamed to onLoad in 2.0. Please adjust your code',
			state: 'History requires Asual\'s Address library <https://github.com/asual/jquery-address>'
		},

		metadata: {
			tab: 'tab',
			loaded: 'loaded',
			promise: 'promise'
		},

		className: {
			loading: 'loading',
			active: 'active'
		},

		selector: {
			tabs: '.ui.tab',
			ui: '.ui'
		}

	};

})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Transition
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.transition = function () {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			moduleArguments = arguments,
			query = moduleArguments[0],
			queryArguments = [].slice.call(arguments, 1),
			methodInvoked = (typeof query === 'string'),

			requestAnimationFrame = window.requestAnimationFrame
				|| window.mozRequestAnimationFrame
				|| window.webkitRequestAnimationFrame
				|| window.msRequestAnimationFrame
				|| function (callback) { setTimeout(callback, 0); },

			returnedValue
			;
		$allModules
			.each(function (index) {
				var
					$module = $(this),
					element = this,

					// set at run time
					settings,
					instance,

					error,
					className,
					metadata,
					animationEnd,
					animationName,

					namespace,
					moduleNamespace,
					eventNamespace,
					module
					;

				module = {

					initialize: function () {

						// get full settings
						settings = module.get.settings.apply(element, moduleArguments);

						// shorthand
						className = settings.className;
						error = settings.error;
						metadata = settings.metadata;

						// define namespace
						eventNamespace = '.' + settings.namespace;
						moduleNamespace = 'module-' + settings.namespace;
						instance = $module.data(moduleNamespace) || module;

						// get vendor specific events
						animationEnd = module.get.animationEndEvent();

						if (methodInvoked) {
							methodInvoked = module.invoke(query);
						}

						// method not invoked, lets run an animation
						if (methodInvoked === false) {
							module.verbose('Converted arguments into settings object', settings);
							if (settings.interval) {
								module.delay(settings.animate);
							}
							else {
								module.animate();
							}
							module.instantiate();
						}
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous module for', element);
						$module
							.removeData(moduleNamespace)
							;
					},

					refresh: function () {
						module.verbose('Refreshing display type on next animation');
						delete module.displayType;
					},

					forceRepaint: function () {
						module.verbose('Forcing element repaint');
						var
							$parentElement = $module.parent(),
							$nextElement = $module.next()
							;
						if ($nextElement.length === 0) {
							$module.detach().appendTo($parentElement);
						}
						else {
							$module.detach().insertBefore($nextElement);
						}
					},

					repaint: function () {
						module.verbose('Repainting element');
						var
							fakeAssignment = element.offsetWidth
							;
					},

					delay: function (interval) {
						var
							direction = module.get.animationDirection(),
							shouldReverse,
							delay
							;
						if (!direction) {
							direction = module.can.transition()
								? module.get.direction()
								: 'static'
								;
						}
						interval = (interval !== undefined)
							? interval
							: settings.interval
							;
						shouldReverse = (settings.reverse == 'auto' && direction == className.outward);
						delay = (shouldReverse || settings.reverse == true)
							? ($allModules.length - index) * settings.interval
							: index * settings.interval
							;
						module.debug('Delaying animation by', delay);
						setTimeout(module.animate, delay);
					},

					animate: function (overrideSettings) {
						settings = overrideSettings || settings;
						if (!module.is.supported()) {
							module.error(error.support);
							return false;
						}
						module.debug('Preparing animation', settings.animation);
						if (module.is.animating()) {
							if (settings.queue) {
								if (!settings.allowRepeats && module.has.direction() && module.is.occurring() && module.queuing !== true) {
									module.debug('Animation is currently occurring, preventing queueing same animation', settings.animation);
								}
								else {
									module.queue(settings.animation);
								}
								return false;
							}
							else if (!settings.allowRepeats && module.is.occurring()) {
								module.debug('Animation is already occurring, will not execute repeated animation', settings.animation);
								return false;
							}
							else {
								module.debug('New animation started, completing previous early', settings.animation);
								instance.complete();
							}
						}
						if (module.can.animate()) {
							module.set.animating(settings.animation);
						}
						else {
							module.error(error.noAnimation, settings.animation, element);
						}
					},

					reset: function () {
						module.debug('Resetting animation to beginning conditions');
						module.remove.animationCallbacks();
						module.restore.conditions();
						module.remove.animating();
					},

					queue: function (animation) {
						module.debug('Queueing animation of', animation);
						module.queuing = true;
						$module
							.one(animationEnd + '.queue' + eventNamespace, function () {
								module.queuing = false;
								module.repaint();
								module.animate.apply(this, settings);
							})
							;
					},

					complete: function (event) {
						module.debug('Animation complete', settings.animation);
						module.remove.completeCallback();
						module.remove.failSafe();
						if (!module.is.looping()) {
							if (module.is.outward()) {
								module.verbose('Animation is outward, hiding element');
								module.restore.conditions();
								module.hide();
							}
							else if (module.is.inward()) {
								module.verbose('Animation is outward, showing element');
								module.restore.conditions();
								module.show();
							}
							else {
								module.verbose('Static animation completed');
								module.restore.conditions();
								settings.onComplete.call(element);
							}
						}
					},

					force: {
						visible: function () {
							var
								style = $module.attr('style'),
								userStyle = module.get.userStyle(),
								displayType = module.get.displayType(),
								overrideStyle = userStyle + 'display: ' + displayType + ' !important;',
								currentDisplay = $module.css('display'),
								emptyStyle = (style === undefined || style === '')
								;
							if (currentDisplay !== displayType) {
								module.verbose('Overriding default display to show element', displayType);
								$module
									.attr('style', overrideStyle)
									;
							}
							else if (emptyStyle) {
								$module.removeAttr('style');
							}
						},
						hidden: function () {
							var
								style = $module.attr('style'),
								currentDisplay = $module.css('display'),
								emptyStyle = (style === undefined || style === '')
								;
							if (currentDisplay !== 'none' && !module.is.hidden()) {
								module.verbose('Overriding default display to hide element');
								$module
									.css('display', 'none')
									;
							}
							else if (emptyStyle) {
								$module
									.removeAttr('style')
									;
							}
						}
					},

					has: {
						direction: function (animation) {
							var
								hasDirection = false
								;
							animation = animation || settings.animation;
							if (typeof animation === 'string') {
								animation = animation.split(' ');
								$.each(animation, function (index, word) {
									if (word === className.inward || word === className.outward) {
										hasDirection = true;
									}
								});
							}
							return hasDirection;
						},
						inlineDisplay: function () {
							var
								style = $module.attr('style') || ''
								;
							return $.isArray(style.match(/display.*?;/, ''));
						}
					},

					set: {
						animating: function (animation) {
							var
								animationClass,
								direction
								;
							// remove previous callbacks
							module.remove.completeCallback();

							// determine exact animation
							animation = animation || settings.animation;
							animationClass = module.get.animationClass(animation);

							// save animation class in cache to restore class names
							module.save.animation(animationClass);

							// override display if necessary so animation appears visibly
							module.force.visible();

							module.remove.hidden();
							module.remove.direction();

							module.start.animation(animationClass);

						},
						duration: function (animationName, duration) {
							duration = duration || settings.duration;
							duration = (typeof duration == 'number')
								? duration + 'ms'
								: duration
								;
							if (duration || duration === 0) {
								module.verbose('Setting animation duration', duration);
								$module
									.css({
										'animation-duration': duration
									})
									;
							}
						},
						direction: function (direction) {
							direction = direction || module.get.direction();
							if (direction == className.inward) {
								module.set.inward();
							}
							else {
								module.set.outward();
							}
						},
						looping: function () {
							module.debug('Transition set to loop');
							$module
								.addClass(className.looping)
								;
						},
						hidden: function () {
							$module
								.addClass(className.transition)
								.addClass(className.hidden)
								;
						},
						inward: function () {
							module.debug('Setting direction to inward');
							$module
								.removeClass(className.outward)
								.addClass(className.inward)
								;
						},
						outward: function () {
							module.debug('Setting direction to outward');
							$module
								.removeClass(className.inward)
								.addClass(className.outward)
								;
						},
						visible: function () {
							$module
								.addClass(className.transition)
								.addClass(className.visible)
								;
						}
					},

					start: {
						animation: function (animationClass) {
							animationClass = animationClass || module.get.animationClass();
							module.debug('Starting tween', animationClass);
							$module
								.addClass(animationClass)
								.one(animationEnd + '.complete' + eventNamespace, module.complete)
								;
							if (settings.useFailSafe) {
								module.add.failSafe();
							}
							module.set.duration(settings.duration);
							settings.onStart.call(element);
						}
					},

					save: {
						animation: function (animation) {
							if (!module.cache) {
								module.cache = {};
							}
							module.cache.animation = animation;
						},
						displayType: function (displayType) {
							if (displayType !== 'none') {
								$module.data(metadata.displayType, displayType);
							}
						},
						transitionExists: function (animation, exists) {
							$.fn.transition.exists[animation] = exists;
							module.verbose('Saving existence of transition', animation, exists);
						}
					},

					restore: {
						conditions: function () {
							var
								animation = module.get.currentAnimation()
								;
							if (animation) {
								$module
									.removeClass(animation)
									;
								module.verbose('Removing animation class', module.cache);
							}
							module.remove.duration();
						}
					},

					add: {
						failSafe: function () {
							var
								duration = module.get.duration()
								;
							module.timer = setTimeout(function () {
								$module.triggerHandler(animationEnd);
							}, duration + settings.failSafeDelay);
							module.verbose('Adding fail safe timer', module.timer);
						}
					},

					remove: {
						animating: function () {
							$module.removeClass(className.animating);
						},
						animationCallbacks: function () {
							module.remove.queueCallback();
							module.remove.completeCallback();
						},
						queueCallback: function () {
							$module.off('.queue' + eventNamespace);
						},
						completeCallback: function () {
							$module.off('.complete' + eventNamespace);
						},
						display: function () {
							$module.css('display', '');
						},
						direction: function () {
							$module
								.removeClass(className.inward)
								.removeClass(className.outward)
								;
						},
						duration: function () {
							$module
								.css('animation-duration', '')
								;
						},
						failSafe: function () {
							module.verbose('Removing fail safe timer', module.timer);
							if (module.timer) {
								clearTimeout(module.timer);
							}
						},
						hidden: function () {
							$module.removeClass(className.hidden);
						},
						visible: function () {
							$module.removeClass(className.visible);
						},
						looping: function () {
							module.debug('Transitions are no longer looping');
							if (module.is.looping()) {
								module.reset();
								$module
									.removeClass(className.looping)
									;
							}
						},
						transition: function () {
							$module
								.removeClass(className.visible)
								.removeClass(className.hidden)
								;
						}
					},
					get: {
						settings: function (animation, duration, onComplete) {
							// single settings object
							if (typeof animation == 'object') {
								return $.extend(true, {}, $.fn.transition.settings, animation);
							}
							// all arguments provided
							else if (typeof onComplete == 'function') {
								return $.extend({}, $.fn.transition.settings, {
									animation: animation,
									onComplete: onComplete,
									duration: duration
								});
							}
							// only duration provided
							else if (typeof duration == 'string' || typeof duration == 'number') {
								return $.extend({}, $.fn.transition.settings, {
									animation: animation,
									duration: duration
								});
							}
							// duration is actually settings object
							else if (typeof duration == 'object') {
								return $.extend({}, $.fn.transition.settings, duration, {
									animation: animation
								});
							}
							// duration is actually callback
							else if (typeof duration == 'function') {
								return $.extend({}, $.fn.transition.settings, {
									animation: animation,
									onComplete: duration
								});
							}
							// only animation provided
							else {
								return $.extend({}, $.fn.transition.settings, {
									animation: animation
								});
							}
						},
						animationClass: function (animation) {
							var
								animationClass = animation || settings.animation,
								directionClass = (module.can.transition() && !module.has.direction())
									? module.get.direction() + ' '
									: ''
								;
							return className.animating + ' '
								+ className.transition + ' '
								+ directionClass
								+ animationClass
								;
						},
						currentAnimation: function () {
							return (module.cache && module.cache.animation !== undefined)
								? module.cache.animation
								: false
								;
						},
						currentDirection: function () {
							return module.is.inward()
								? className.inward
								: className.outward
								;
						},
						direction: function () {
							return module.is.hidden() || !module.is.visible()
								? className.inward
								: className.outward
								;
						},
						animationDirection: function (animation) {
							var
								direction
								;
							animation = animation || settings.animation;
							if (typeof animation === 'string') {
								animation = animation.split(' ');
								// search animation name for out/in class
								$.each(animation, function (index, word) {
									if (word === className.inward) {
										direction = className.inward;
									}
									else if (word === className.outward) {
										direction = className.outward;
									}
								});
							}
							// return found direction
							if (direction) {
								return direction;
							}
							return false;
						},
						duration: function (duration) {
							duration = duration || settings.duration;
							if (duration === false) {
								duration = $module.css('animation-duration') || 0;
							}
							return (typeof duration === 'string')
								? (duration.indexOf('ms') > -1)
									? parseFloat(duration)
									: parseFloat(duration) * 1000
								: duration
								;
						},
						displayType: function (shouldDetermine) {
							shouldDetermine = (shouldDetermine !== undefined)
								? shouldDetermine
								: true
								;
							if (settings.displayType) {
								return settings.displayType;
							}
							if (shouldDetermine && $module.data(metadata.displayType) === undefined) {
								// create fake element to determine display state
								module.can.transition(true);
							}
							return $module.data(metadata.displayType);
						},
						userStyle: function (style) {
							style = style || $module.attr('style') || '';
							return style.replace(/display.*?;/, '');
						},
						transitionExists: function (animation) {
							return $.fn.transition.exists[animation];
						},
						animationStartEvent: function () {
							var
								element = document.createElement('div'),
								animations = {
									'animation': 'animationstart',
									'OAnimation': 'oAnimationStart',
									'MozAnimation': 'mozAnimationStart',
									'WebkitAnimation': 'webkitAnimationStart'
								},
								animation
								;
							for (animation in animations) {
								if (element.style[animation] !== undefined) {
									return animations[animation];
								}
							}
							return false;
						},
						animationEndEvent: function () {
							var
								element = document.createElement('div'),
								animations = {
									'animation': 'animationend',
									'OAnimation': 'oAnimationEnd',
									'MozAnimation': 'mozAnimationEnd',
									'WebkitAnimation': 'webkitAnimationEnd'
								},
								animation
								;
							for (animation in animations) {
								if (element.style[animation] !== undefined) {
									return animations[animation];
								}
							}
							return false;
						}

					},

					can: {
						transition: function (forced) {
							var
								animation = settings.animation,
								transitionExists = module.get.transitionExists(animation),
								displayType = module.get.displayType(false),
								elementClass,
								tagName,
								$clone,
								currentAnimation,
								inAnimation,
								directionExists
								;
							if (transitionExists === undefined || forced) {
								module.verbose('Determining whether animation exists');
								elementClass = $module.attr('class');
								tagName = $module.prop('tagName');

								$clone = $('<' + tagName + ' />').addClass(elementClass).insertAfter($module);
								currentAnimation = $clone
									.addClass(animation)
									.removeClass(className.inward)
									.removeClass(className.outward)
									.addClass(className.animating)
									.addClass(className.transition)
									.css('animationName')
									;
								inAnimation = $clone
									.addClass(className.inward)
									.css('animationName')
									;
								if (!displayType) {
									displayType = $clone
										.attr('class', elementClass)
										.removeAttr('style')
										.removeClass(className.hidden)
										.removeClass(className.visible)
										.show()
										.css('display')
										;
									module.verbose('Determining final display state', displayType);
									module.save.displayType(displayType);
								}

								$clone.remove();
								if (currentAnimation != inAnimation) {
									module.debug('Direction exists for animation', animation);
									directionExists = true;
								}
								else if (currentAnimation == 'none' || !currentAnimation) {
									module.debug('No animation defined in css', animation);
									return;
								}
								else {
									module.debug('Static animation found', animation, displayType);
									directionExists = false;
								}
								module.save.transitionExists(animation, directionExists);
							}
							return (transitionExists !== undefined)
								? transitionExists
								: directionExists
								;
						},
						animate: function () {
							// can transition does not return a value if animation does not exist
							return (module.can.transition() !== undefined);
						}
					},

					is: {
						animating: function () {
							return $module.hasClass(className.animating);
						},
						inward: function () {
							return $module.hasClass(className.inward);
						},
						outward: function () {
							return $module.hasClass(className.outward);
						},
						looping: function () {
							return $module.hasClass(className.looping);
						},
						occurring: function (animation) {
							animation = animation || settings.animation;
							animation = '.' + animation.replace(' ', '.');
							return ($module.filter(animation).length > 0);
						},
						visible: function () {
							return $module.is(':visible');
						},
						hidden: function () {
							return $module.css('visibility') === 'hidden';
						},
						supported: function () {
							return (animationEnd !== false);
						}
					},

					hide: function () {
						module.verbose('Hiding element');
						if (module.is.animating()) {
							module.reset();
						}
						element.blur(); // IE will trigger focus change if element is not blurred before hiding
						module.remove.display();
						module.remove.visible();
						module.set.hidden();
						module.force.hidden();
						settings.onHide.call(element);
						settings.onComplete.call(element);
						// module.repaint();
					},

					show: function (display) {
						module.verbose('Showing element', display);
						module.remove.hidden();
						module.set.visible();
						module.force.visible();
						settings.onShow.call(element);
						settings.onComplete.call(element);
						// module.repaint();
					},

					toggle: function () {
						if (module.is.visible()) {
							module.hide();
						}
						else {
							module.show();
						}
					},

					stop: function () {
						module.debug('Stopping current animation');
						$module.triggerHandler(animationEnd);
					},

					stopAll: function () {
						module.debug('Stopping all animation');
						module.remove.queueCallback();
						$module.triggerHandler(animationEnd);
					},

					clear: {
						queue: function () {
							module.debug('Clearing animation queue');
							module.remove.queueCallback();
						}
					},

					enable: function () {
						module.verbose('Starting animation');
						$module.removeClass(className.disabled);
					},

					disable: function () {
						module.debug('Stopping animation');
						$module.addClass(className.disabled);
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ($allModules.length > 1) {
								title += ' ' + '(' + $allModules.length + ')';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					// modified for transition to return invoke success
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}

						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return (found !== undefined)
							? found
							: false
							;
					}
				};
				module.initialize();
			})
			;
		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	// Records if CSS transition is available
	$.fn.transition.exists = {};

	$.fn.transition.settings = {

		// module info
		name: 'Transition',

		// hide all output from this component regardless of other settings
		silent: false,

		// debug content outputted to console
		debug: false,

		// verbose debug output
		verbose: false,

		// performance data output
		performance: true,

		// event namespace
		namespace: 'transition',

		// delay between animations in group
		interval: 0,

		// whether group animations should be reversed
		reverse: 'auto',

		// animation callback event
		onStart: function () { },
		onComplete: function () { },
		onShow: function () { },
		onHide: function () { },

		// whether timeout should be used to ensure callback fires in cases animationend does not
		useFailSafe: true,

		// delay in ms for fail safe
		failSafeDelay: 100,

		// whether EXACT animation can occur twice in a row
		allowRepeats: false,

		// Override final display type on visible
		displayType: false,

		// animation duration
		animation: 'fade',
		duration: false,

		// new animations will occur after previous ones
		queue: true,

		metadata: {
			displayType: 'display'
		},

		className: {
			animating: 'animating',
			disabled: 'disabled',
			hidden: 'hidden',
			inward: 'in',
			loading: 'loading',
			looping: 'looping',
			outward: 'out',
			transition: 'transition',
			visible: 'visible'
		},

		// possible errors
		error: {
			noAnimation: 'Element is no longer attached to DOM. Unable to animate.  Use silent setting to surpress this warning in production.',
			repeated: 'That animation is already occurring, cancelling repeated animation',
			method: 'The method you called is not defined',
			support: 'This browser does not support CSS animations'
		}

	};


})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - API
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	var
		window = (typeof window != 'undefined' && window.Math == Math)
			? window
			: (typeof self != 'undefined' && self.Math == Math)
				? self
				: Function('return this')()
		;

	$.api = $.fn.api = function (parameters) {

		var
			// use window context if none specified
			$allModules = $.isFunction(this)
				? $(window)
				: $(this),
			moduleSelector = $allModules.selector || '',
			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),

			returnedValue
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.api.settings, parameters)
						: $.extend({}, $.fn.api.settings),

					// internal aliases
					namespace = settings.namespace,
					metadata = settings.metadata,
					selector = settings.selector,
					error = settings.error,
					className = settings.className,

					// define namespaces for modules
					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					// element that creates request
					$module = $(this),
					$form = $module.closest(selector.form),

					// context used for state
					$context = (settings.stateContext)
						? $(settings.stateContext)
						: $module,

					// request details
					ajaxSettings,
					requestSettings,
					url,
					data,
					requestStartTime,

					// standard module
					element = this,
					context = $context[0],
					instance = $module.data(moduleNamespace),
					module
					;

				module = {

					initialize: function () {
						if (!methodInvoked) {
							module.bind.events();
						}
						module.instantiate();
					},

					instantiate: function () {
						module.verbose('Storing instance of module', module);
						instance = module;
						$module
							.data(moduleNamespace, instance)
							;
					},

					destroy: function () {
						module.verbose('Destroying previous module for', element);
						$module
							.removeData(moduleNamespace)
							.off(eventNamespace)
							;
					},

					bind: {
						events: function () {
							var
								triggerEvent = module.get.event()
								;
							if (triggerEvent) {
								module.verbose('Attaching API events to element', triggerEvent);
								$module
									.on(triggerEvent + eventNamespace, module.event.trigger)
									;
							}
							else if (settings.on == 'now') {
								module.debug('Querying API endpoint immediately');
								module.query();
							}
						}
					},

					decode: {
						json: function (response) {
							if (response !== undefined && typeof response == 'string') {
								try {
									response = JSON.parse(response);
								}
								catch (e) {
									// isnt json string
								}
							}
							return response;
						}
					},

					read: {
						cachedResponse: function (url) {
							var
								response
								;
							if (window.Storage === undefined) {
								module.error(error.noStorage);
								return;
							}
							response = sessionStorage.getItem(url);
							module.debug('Using cached response', url, response);
							response = module.decode.json(response);
							return response;
						}
					},
					write: {
						cachedResponse: function (url, response) {
							if (response && response === '') {
								module.debug('Response empty, not caching', response);
								return;
							}
							if (window.Storage === undefined) {
								module.error(error.noStorage);
								return;
							}
							if ($.isPlainObject(response)) {
								response = JSON.stringify(response);
							}
							sessionStorage.setItem(url, response);
							module.verbose('Storing cached response for url', url, response);
						}
					},

					query: function () {

						if (module.is.disabled()) {
							module.debug('Element is disabled API request aborted');
							return;
						}

						if (module.is.loading()) {
							if (settings.interruptRequests) {
								module.debug('Interrupting previous request');
								module.abort();
							}
							else {
								module.debug('Cancelling request, previous request is still pending');
								return;
							}
						}

						// pass element metadata to url (value, text)
						if (settings.defaultData) {
							$.extend(true, settings.urlData, module.get.defaultData());
						}

						// Add form content
						if (settings.serializeForm) {
							settings.data = module.add.formData(settings.data);
						}

						// call beforesend and get any settings changes
						requestSettings = module.get.settings();

						// check if before send cancelled request
						if (requestSettings === false) {
							module.cancelled = true;
							module.error(error.beforeSend);
							return;
						}
						else {
							module.cancelled = false;
						}

						// get url
						url = module.get.templatedURL();

						if (!url && !module.is.mocked()) {
							module.error(error.missingURL);
							return;
						}

						// replace variables
						url = module.add.urlData(url);
						// missing url parameters
						if (!url && !module.is.mocked()) {
							return;
						}

						requestSettings.url = settings.base + url;

						// look for jQuery ajax parameters in settings
						ajaxSettings = $.extend(true, {}, settings, {
							type: settings.method || settings.type,
							data: data,
							url: settings.base + url,
							beforeSend: settings.beforeXHR,
							success: function () { },
							failure: function () { },
							complete: function () { }
						});

						module.debug('Querying URL', ajaxSettings.url);
						module.verbose('Using AJAX settings', ajaxSettings);
						if (settings.cache === 'local' && module.read.cachedResponse(url)) {
							module.debug('Response returned from local cache');
							module.request = module.create.request();
							module.request.resolveWith(context, [module.read.cachedResponse(url)]);
							return;
						}

						if (!settings.throttle) {
							module.debug('Sending request', data, ajaxSettings.method);
							module.send.request();
						}
						else {
							if (!settings.throttleFirstRequest && !module.timer) {
								module.debug('Sending request', data, ajaxSettings.method);
								module.send.request();
								module.timer = setTimeout(function () { }, settings.throttle);
							}
							else {
								module.debug('Throttling request', settings.throttle);
								clearTimeout(module.timer);
								module.timer = setTimeout(function () {
									if (module.timer) {
										delete module.timer;
									}
									module.debug('Sending throttled request', data, ajaxSettings.method);
									module.send.request();
								}, settings.throttle);
							}
						}

					},

					should: {
						removeError: function () {
							return (settings.hideError === true || (settings.hideError === 'auto' && !module.is.form()));
						}
					},

					is: {
						disabled: function () {
							return ($module.filter(selector.disabled).length > 0);
						},
						expectingJSON: function () {
							return settings.dataType === 'json' || settings.dataType === 'jsonp';
						},
						form: function () {
							return $module.is('form') || $context.is('form');
						},
						mocked: function () {
							return (settings.mockResponse || settings.mockResponseAsync || settings.response || settings.responseAsync);
						},
						input: function () {
							return $module.is('input');
						},
						loading: function () {
							return (module.request)
								? (module.request.state() == 'pending')
								: false
								;
						},
						abortedRequest: function (xhr) {
							if (xhr && xhr.readyState !== undefined && xhr.readyState === 0) {
								module.verbose('XHR request determined to be aborted');
								return true;
							}
							else {
								module.verbose('XHR request was not aborted');
								return false;
							}
						},
						validResponse: function (response) {
							if ((!module.is.expectingJSON()) || !$.isFunction(settings.successTest)) {
								module.verbose('Response is not JSON, skipping validation', settings.successTest, response);
								return true;
							}
							module.debug('Checking JSON returned success', settings.successTest, response);
							if (settings.successTest(response)) {
								module.debug('Response passed success test', response);
								return true;
							}
							else {
								module.debug('Response failed success test', response);
								return false;
							}
						}
					},

					was: {
						cancelled: function () {
							return (module.cancelled || false);
						},
						succesful: function () {
							return (module.request && module.request.state() == 'resolved');
						},
						failure: function () {
							return (module.request && module.request.state() == 'rejected');
						},
						complete: function () {
							return (module.request && (module.request.state() == 'resolved' || module.request.state() == 'rejected'));
						}
					},

					add: {
						urlData: function (url, urlData) {
							var
								requiredVariables,
								optionalVariables
								;
							if (url) {
								requiredVariables = url.match(settings.regExp.required);
								optionalVariables = url.match(settings.regExp.optional);
								urlData = urlData || settings.urlData;
								if (requiredVariables) {
									module.debug('Looking for required URL variables', requiredVariables);
									$.each(requiredVariables, function (index, templatedString) {
										var
											// allow legacy {$var} style
											variable = (templatedString.indexOf('$') !== -1)
												? templatedString.substr(2, templatedString.length - 3)
												: templatedString.substr(1, templatedString.length - 2),
											value = ($.isPlainObject(urlData) && urlData[variable] !== undefined)
												? urlData[variable]
												: ($module.data(variable) !== undefined)
													? $module.data(variable)
													: ($context.data(variable) !== undefined)
														? $context.data(variable)
														: urlData[variable]
											;
										// remove value
										if (value === undefined) {
											module.error(error.requiredParameter, variable, url);
											url = false;
											return false;
										}
										else {
											module.verbose('Found required variable', variable, value);
											value = (settings.encodeParameters)
												? module.get.urlEncodedValue(value)
												: value
												;
											url = url.replace(templatedString, value);
										}
									});
								}
								if (optionalVariables) {
									module.debug('Looking for optional URL variables', requiredVariables);
									$.each(optionalVariables, function (index, templatedString) {
										var
											// allow legacy {/$var} style
											variable = (templatedString.indexOf('$') !== -1)
												? templatedString.substr(3, templatedString.length - 4)
												: templatedString.substr(2, templatedString.length - 3),
											value = ($.isPlainObject(urlData) && urlData[variable] !== undefined)
												? urlData[variable]
												: ($module.data(variable) !== undefined)
													? $module.data(variable)
													: ($context.data(variable) !== undefined)
														? $context.data(variable)
														: urlData[variable]
											;
										// optional replacement
										if (value !== undefined) {
											module.verbose('Optional variable Found', variable, value);
											url = url.replace(templatedString, value);
										}
										else {
											module.verbose('Optional variable not found', variable);
											// remove preceding slash if set
											if (url.indexOf('/' + templatedString) !== -1) {
												url = url.replace('/' + templatedString, '');
											}
											else {
												url = url.replace(templatedString, '');
											}
										}
									});
								}
							}
							return url;
						},
						formData: function (data) {
							var
								canSerialize = ($.fn.serializeObject !== undefined),
								formData = (canSerialize)
									? $form.serializeObject()
									: $form.serialize(),
								hasOtherData
								;
							data = data || settings.data;
							hasOtherData = $.isPlainObject(data);

							if (hasOtherData) {
								if (canSerialize) {
									module.debug('Extending existing data with form data', data, formData);
									data = $.extend(true, {}, data, formData);
								}
								else {
									module.error(error.missingSerialize);
									module.debug('Cant extend data. Replacing data with form data', data, formData);
									data = formData;
								}
							}
							else {
								module.debug('Adding form data', formData);
								data = formData;
							}
							return data;
						}
					},

					send: {
						request: function () {
							module.set.loading();
							module.request = module.create.request();
							if (module.is.mocked()) {
								module.mockedXHR = module.create.mockedXHR();
							}
							else {
								module.xhr = module.create.xhr();
							}
							settings.onRequest.call(context, module.request, module.xhr);
						}
					},

					event: {
						trigger: function (event) {
							module.query();
							if (event.type == 'submit' || event.type == 'click') {
								event.preventDefault();
							}
						},
						xhr: {
							always: function () {
								// nothing special
							},
							done: function (response, textStatus, xhr) {
								var
									context = this,
									elapsedTime = (new Date().getTime() - requestStartTime),
									timeLeft = (settings.loadingDuration - elapsedTime),
									translatedResponse = ($.isFunction(settings.onResponse))
										? module.is.expectingJSON()
											? settings.onResponse.call(context, $.extend(true, {}, response))
											: settings.onResponse.call(context, response)
										: false
									;
								timeLeft = (timeLeft > 0)
									? timeLeft
									: 0
									;
								if (translatedResponse) {
									module.debug('Modified API response in onResponse callback', settings.onResponse, translatedResponse, response);
									response = translatedResponse;
								}
								if (timeLeft > 0) {
									module.debug('Response completed early delaying state change by', timeLeft);
								}
								setTimeout(function () {
									if (module.is.validResponse(response)) {
										module.request.resolveWith(context, [response, xhr]);
									}
									else {
										module.request.rejectWith(context, [xhr, 'invalid']);
									}
								}, timeLeft);
							},
							fail: function (xhr, status, httpMessage) {
								var
									context = this,
									elapsedTime = (new Date().getTime() - requestStartTime),
									timeLeft = (settings.loadingDuration - elapsedTime)
									;
								timeLeft = (timeLeft > 0)
									? timeLeft
									: 0
									;
								if (timeLeft > 0) {
									module.debug('Response completed early delaying state change by', timeLeft);
								}
								setTimeout(function () {
									if (module.is.abortedRequest(xhr)) {
										module.request.rejectWith(context, [xhr, 'aborted', httpMessage]);
									}
									else {
										module.request.rejectWith(context, [xhr, 'error', status, httpMessage]);
									}
								}, timeLeft);
							}
						},
						request: {
							done: function (response, xhr) {
								module.debug('Successful API Response', response);
								if (settings.cache === 'local' && url) {
									module.write.cachedResponse(url, response);
									module.debug('Saving server response locally', module.cache);
								}
								settings.onSuccess.call(context, response, $module, xhr);
							},
							complete: function (firstParameter, secondParameter) {
								var
									xhr,
									response
									;
								// have to guess callback parameters based on request success
								if (module.was.succesful()) {
									response = firstParameter;
									xhr = secondParameter;
								}
								else {
									xhr = firstParameter;
									response = module.get.responseFromXHR(xhr);
								}
								module.remove.loading();
								settings.onComplete.call(context, response, $module, xhr);
							},
							fail: function (xhr, status, httpMessage) {
								var
									// pull response from xhr if available
									response = module.get.responseFromXHR(xhr),
									errorMessage = module.get.errorFromRequest(response, status, httpMessage)
									;
								if (status == 'aborted') {
									module.debug('XHR Aborted (Most likely caused by page navigation or CORS Policy)', status, httpMessage);
									settings.onAbort.call(context, status, $module, xhr);
									return true;
								}
								else if (status == 'invalid') {
									module.debug('JSON did not pass success test. A server-side error has most likely occurred', response);
								}
								else if (status == 'error') {
									if (xhr !== undefined) {
										module.debug('XHR produced a server error', status, httpMessage);
										// make sure we have an error to display to console
										if (xhr.status != 200 && httpMessage !== undefined && httpMessage !== '') {
											module.error(error.statusMessage + httpMessage, ajaxSettings.url);
										}
										settings.onError.call(context, errorMessage, $module, xhr);
									}
								}

								if (settings.errorDuration && status !== 'aborted') {
									module.debug('Adding error state');
									module.set.error();
									if (module.should.removeError()) {
										setTimeout(module.remove.error, settings.errorDuration);
									}
								}
								module.debug('API Request failed', errorMessage, xhr);
								settings.onFailure.call(context, response, $module, xhr);
							}
						}
					},

					create: {

						request: function () {
							// api request promise
							return $.Deferred()
								.always(module.event.request.complete)
								.done(module.event.request.done)
								.fail(module.event.request.fail)
								;
						},

						mockedXHR: function () {
							var
								// xhr does not simulate these properties of xhr but must return them
								textStatus = false,
								status = false,
								httpMessage = false,
								responder = settings.mockResponse || settings.response,
								asyncResponder = settings.mockResponseAsync || settings.responseAsync,
								asyncCallback,
								response,
								mockedXHR
								;

							mockedXHR = $.Deferred()
								.always(module.event.xhr.complete)
								.done(module.event.xhr.done)
								.fail(module.event.xhr.fail)
								;

							if (responder) {
								if ($.isFunction(responder)) {
									module.debug('Using specified synchronous callback', responder);
									response = responder.call(context, requestSettings);
								}
								else {
									module.debug('Using settings specified response', responder);
									response = responder;
								}
								// simulating response
								mockedXHR.resolveWith(context, [response, textStatus, { responseText: response }]);
							}
							else if ($.isFunction(asyncResponder)) {
								asyncCallback = function (response) {
									module.debug('Async callback returned response', response);

									if (response) {
										mockedXHR.resolveWith(context, [response, textStatus, { responseText: response }]);
									}
									else {
										mockedXHR.rejectWith(context, [{ responseText: response }, status, httpMessage]);
									}
								};
								module.debug('Using specified async response callback', asyncResponder);
								asyncResponder.call(context, requestSettings, asyncCallback);
							}
							return mockedXHR;
						},

						xhr: function () {
							var
								xhr
								;
							// ajax request promise
							xhr = $.ajax(ajaxSettings)
								.always(module.event.xhr.always)
								.done(module.event.xhr.done)
								.fail(module.event.xhr.fail)
								;
							module.verbose('Created server request', xhr, ajaxSettings);
							return xhr;
						}
					},

					set: {
						error: function () {
							module.verbose('Adding error state to element', $context);
							$context.addClass(className.error);
						},
						loading: function () {
							module.verbose('Adding loading state to element', $context);
							$context.addClass(className.loading);
							requestStartTime = new Date().getTime();
						}
					},

					remove: {
						error: function () {
							module.verbose('Removing error state from element', $context);
							$context.removeClass(className.error);
						},
						loading: function () {
							module.verbose('Removing loading state from element', $context);
							$context.removeClass(className.loading);
						}
					},

					get: {
						responseFromXHR: function (xhr) {
							return $.isPlainObject(xhr)
								? (module.is.expectingJSON())
									? module.decode.json(xhr.responseText)
									: xhr.responseText
								: false
								;
						},
						errorFromRequest: function (response, status, httpMessage) {
							return ($.isPlainObject(response) && response.error !== undefined)
								? response.error // use json error message
								: (settings.error[status] !== undefined) // use server error message
									? settings.error[status]
									: httpMessage
								;
						},
						request: function () {
							return module.request || false;
						},
						xhr: function () {
							return module.xhr || false;
						},
						settings: function () {
							var
								runSettings
								;
							runSettings = settings.beforeSend.call(context, settings);
							if (runSettings) {
								if (runSettings.success !== undefined) {
									module.debug('Legacy success callback detected', runSettings);
									module.error(error.legacyParameters, runSettings.success);
									runSettings.onSuccess = runSettings.success;
								}
								if (runSettings.failure !== undefined) {
									module.debug('Legacy failure callback detected', runSettings);
									module.error(error.legacyParameters, runSettings.failure);
									runSettings.onFailure = runSettings.failure;
								}
								if (runSettings.complete !== undefined) {
									module.debug('Legacy complete callback detected', runSettings);
									module.error(error.legacyParameters, runSettings.complete);
									runSettings.onComplete = runSettings.complete;
								}
							}
							if (runSettings === undefined) {
								module.error(error.noReturnedValue);
							}
							if (runSettings === false) {
								return runSettings;
							}
							return (runSettings !== undefined)
								? $.extend(true, {}, runSettings)
								: $.extend(true, {}, settings)
								;
						},
						urlEncodedValue: function (value) {
							var
								decodedValue = window.decodeURIComponent(value),
								encodedValue = window.encodeURIComponent(value),
								alreadyEncoded = (decodedValue !== value)
								;
							if (alreadyEncoded) {
								module.debug('URL value is already encoded, avoiding double encoding', value);
								return value;
							}
							module.verbose('Encoding value using encodeURIComponent', value, encodedValue);
							return encodedValue;
						},
						defaultData: function () {
							var
								data = {}
								;
							if (!$.isWindow(element)) {
								if (module.is.input()) {
									data.value = $module.val();
								}
								else if (module.is.form()) {

								}
								else {
									data.text = $module.text();
								}
							}
							return data;
						},
						event: function () {
							if ($.isWindow(element) || settings.on == 'now') {
								module.debug('API called without element, no events attached');
								return false;
							}
							else if (settings.on == 'auto') {
								if ($module.is('input')) {
									return (element.oninput !== undefined)
										? 'input'
										: (element.onpropertychange !== undefined)
											? 'propertychange'
											: 'keyup'
										;
								}
								else if ($module.is('form')) {
									return 'submit';
								}
								else {
									return 'click';
								}
							}
							else {
								return settings.on;
							}
						},
						templatedURL: function (action) {
							action = action || $module.data(metadata.action) || settings.action || false;
							url = $module.data(metadata.url) || settings.url || false;
							if (url) {
								module.debug('Using specified url', url);
								return url;
							}
							if (action) {
								module.debug('Looking up url for action', action, settings.api);
								if (settings.api[action] === undefined && !module.is.mocked()) {
									module.error(error.missingAction, settings.action, settings.api);
									return;
								}
								url = settings.api[action];
							}
							else if (module.is.form()) {
								url = $module.attr('action') || $context.attr('action') || false;
								module.debug('No url or action specified, defaulting to form action', url);
							}
							return url;
						}
					},

					abort: function () {
						var
							xhr = module.get.xhr()
							;
						if (xhr && xhr.state() !== 'resolved') {
							module.debug('Cancelling API request');
							xhr.abort();
						}
					},

					// reset state
					reset: function () {
						module.remove.error();
						module.remove.loading();
					},

					setting: function (name, value) {
						module.debug('Changing setting', name, value);
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							if ($.isPlainObject(settings[name])) {
								$.extend(true, settings[name], value);
							}
							else {
								settings[name] = value;
							}
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									//'Element'        : element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.api.settings = {

		name: 'API',
		namespace: 'api',

		debug: false,
		verbose: false,
		performance: true,

		// object containing all templates endpoints
		api: {},

		// whether to cache responses
		cache: true,

		// whether new requests should abort previous requests
		interruptRequests: true,

		// event binding
		on: 'auto',

		// context for applying state classes
		stateContext: false,

		// duration for loading state
		loadingDuration: 0,

		// whether to hide errors after a period of time
		hideError: 'auto',

		// duration for error state
		errorDuration: 2000,

		// whether parameters should be encoded with encodeURIComponent
		encodeParameters: true,

		// API action to use
		action: false,

		// templated URL to use
		url: false,

		// base URL to apply to all endpoints
		base: '',

		// data that will
		urlData: {},

		// whether to add default data to url data
		defaultData: true,

		// whether to serialize closest form
		serializeForm: false,

		// how long to wait before request should occur
		throttle: 0,

		// whether to throttle first request or only repeated
		throttleFirstRequest: true,

		// standard ajax settings
		method: 'get',
		data: {},
		dataType: 'json',

		// mock response
		mockResponse: false,
		mockResponseAsync: false,

		// aliases for mock
		response: false,
		responseAsync: false,

		// callbacks before request
		beforeSend: function (settings) { return settings; },
		beforeXHR: function (xhr) { },
		onRequest: function (promise, xhr) { },

		// after request
		onResponse: false, // function(response) { },

		// response was successful, if JSON passed validation
		onSuccess: function (response, $module) { },

		// request finished without aborting
		onComplete: function (response, $module) { },

		// failed JSON success test
		onFailure: function (response, $module) { },

		// server error
		onError: function (errorMessage, $module) { },

		// request aborted
		onAbort: function (errorMessage, $module) { },

		successTest: false,

		// errors
		error: {
			beforeSend: 'The before send function has aborted the request',
			error: 'There was an error with your request',
			exitConditions: 'API Request Aborted. Exit conditions met',
			JSONParse: 'JSON could not be parsed during error handling',
			legacyParameters: 'You are using legacy API success callback names',
			method: 'The method you called is not defined',
			missingAction: 'API action used but no url was defined',
			missingSerialize: 'jquery-serialize-object is required to add form data to an existing data object',
			missingURL: 'No URL specified for api event',
			noReturnedValue: 'The beforeSend callback must return a settings object, beforeSend ignored.',
			noStorage: 'Caching responses locally requires session storage',
			parseError: 'There was an error parsing your request',
			requiredParameter: 'Missing a required URL parameter: ',
			statusMessage: 'Server gave an error: ',
			timeout: 'Your request timed out'
		},

		regExp: {
			required: /\{\$*[A-z0-9]+\}/g,
			optional: /\{\/\$*[A-z0-9]+\}/g,
		},

		className: {
			loading: 'loading',
			error: 'error'
		},

		selector: {
			disabled: '.disabled',
			form: 'form'
		},

		metadata: {
			action: 'action',
			url: 'url'
		}
	};



})(jQuery, window, document);

/*!
 * # Semantic UI 2.5.0 - Visibility
 * http://github.com/semantic-org/semantic-ui/
 *
 *
 * Released under the MIT license
 * http://opensource.org/licenses/MIT
 *
 */

; (function ($, window, document, undefined) {

	'use strict';

	window = (typeof window != 'undefined' && window.Math == Math)
		? window
		: (typeof self != 'undefined' && self.Math == Math)
			? self
			: Function('return this')()
		;

	$.fn.visibility = function (parameters) {
		var
			$allModules = $(this),
			moduleSelector = $allModules.selector || '',

			time = new Date().getTime(),
			performance = [],

			query = arguments[0],
			methodInvoked = (typeof query == 'string'),
			queryArguments = [].slice.call(arguments, 1),
			returnedValue,

			moduleCount = $allModules.length,
			loadedCount = 0
			;

		$allModules
			.each(function () {
				var
					settings = ($.isPlainObject(parameters))
						? $.extend(true, {}, $.fn.visibility.settings, parameters)
						: $.extend({}, $.fn.visibility.settings),

					className = settings.className,
					namespace = settings.namespace,
					error = settings.error,
					metadata = settings.metadata,

					eventNamespace = '.' + namespace,
					moduleNamespace = 'module-' + namespace,

					$window = $(window),

					$module = $(this),
					$context = $(settings.context),

					$placeholder,

					selector = $module.selector || '',
					instance = $module.data(moduleNamespace),

					requestAnimationFrame = window.requestAnimationFrame
						|| window.mozRequestAnimationFrame
						|| window.webkitRequestAnimationFrame
						|| window.msRequestAnimationFrame
						|| function (callback) { setTimeout(callback, 0); },

					element = this,
					disabled = false,

					contextObserver,
					observer,
					module
					;

				module = {

					initialize: function () {
						module.debug('Initializing', settings);

						module.setup.cache();

						if (module.should.trackChanges()) {

							if (settings.type == 'image') {
								module.setup.image();
							}
							if (settings.type == 'fixed') {
								module.setup.fixed();
							}

							if (settings.observeChanges) {
								module.observeChanges();
							}
							module.bind.events();
						}

						module.save.position();
						if (!module.is.visible()) {
							module.error(error.visible, $module);
						}

						if (settings.initialCheck) {
							module.checkVisibility();
						}
						module.instantiate();
					},

					instantiate: function () {
						module.debug('Storing instance', module);
						$module
							.data(moduleNamespace, module)
							;
						instance = module;
					},

					destroy: function () {
						module.verbose('Destroying previous module');
						if (observer) {
							observer.disconnect();
						}
						if (contextObserver) {
							contextObserver.disconnect();
						}
						$window
							.off('load' + eventNamespace, module.event.load)
							.off('resize' + eventNamespace, module.event.resize)
							;
						$context
							.off('scroll' + eventNamespace, module.event.scroll)
							.off('scrollchange' + eventNamespace, module.event.scrollchange)
							;
						if (settings.type == 'fixed') {
							module.resetFixed();
							module.remove.placeholder();
						}
						$module
							.off(eventNamespace)
							.removeData(moduleNamespace)
							;
					},

					observeChanges: function () {
						if ('MutationObserver' in window) {
							contextObserver = new MutationObserver(module.event.contextChanged);
							observer = new MutationObserver(module.event.changed);
							contextObserver.observe(document, {
								childList: true,
								subtree: true
							});
							observer.observe(element, {
								childList: true,
								subtree: true
							});
							module.debug('Setting up mutation observer', observer);
						}
					},

					bind: {
						events: function () {
							module.verbose('Binding visibility events to scroll and resize');
							if (settings.refreshOnLoad) {
								$window
									.on('load' + eventNamespace, module.event.load)
									;
							}
							$window
								.on('resize' + eventNamespace, module.event.resize)
								;
							// pub/sub pattern
							$context
								.off('scroll' + eventNamespace)
								.on('scroll' + eventNamespace, module.event.scroll)
								.on('scrollchange' + eventNamespace, module.event.scrollchange)
								;
						}
					},

					event: {
						changed: function (mutations) {
							module.verbose('DOM tree modified, updating visibility calculations');
							module.timer = setTimeout(function () {
								module.verbose('DOM tree modified, updating sticky menu');
								module.refresh();
							}, 100);
						},
						contextChanged: function (mutations) {
							[].forEach.call(mutations, function (mutation) {
								if (mutation.removedNodes) {
									[].forEach.call(mutation.removedNodes, function (node) {
										if (node == element || $(node).find(element).length > 0) {
											module.debug('Element removed from DOM, tearing down events');
											module.destroy();
										}
									});
								}
							});
						},
						resize: function () {
							module.debug('Window resized');
							if (settings.refreshOnResize) {
								requestAnimationFrame(module.refresh);
							}
						},
						load: function () {
							module.debug('Page finished loading');
							requestAnimationFrame(module.refresh);
						},
						// publishes scrollchange event on one scroll
						scroll: function () {
							if (settings.throttle) {
								clearTimeout(module.timer);
								module.timer = setTimeout(function () {
									$context.triggerHandler('scrollchange' + eventNamespace, [$context.scrollTop()]);
								}, settings.throttle);
							}
							else {
								requestAnimationFrame(function () {
									$context.triggerHandler('scrollchange' + eventNamespace, [$context.scrollTop()]);
								});
							}
						},
						// subscribes to scrollchange
						scrollchange: function (event, scrollPosition) {
							module.checkVisibility(scrollPosition);
						},
					},

					precache: function (images, callback) {
						if (!(images instanceof Array)) {
							images = [images];
						}
						var
							imagesLength = images.length,
							loadedCounter = 0,
							cache = [],
							cacheImage = document.createElement('img'),
							handleLoad = function () {
								loadedCounter++;
								if (loadedCounter >= images.length) {
									if ($.isFunction(callback)) {
										callback();
									}
								}
							}
							;
						while (imagesLength--) {
							cacheImage = document.createElement('img');
							cacheImage.onload = handleLoad;
							cacheImage.onerror = handleLoad;
							cacheImage.src = images[imagesLength];
							cache.push(cacheImage);
						}
					},

					enableCallbacks: function () {
						module.debug('Allowing callbacks to occur');
						disabled = false;
					},

					disableCallbacks: function () {
						module.debug('Disabling all callbacks temporarily');
						disabled = true;
					},

					should: {
						trackChanges: function () {
							if (methodInvoked) {
								module.debug('One time query, no need to bind events');
								return false;
							}
							module.debug('Callbacks being attached');
							return true;
						}
					},

					setup: {
						cache: function () {
							module.cache = {
								occurred: {},
								screen: {},
								element: {},
							};
						},
						image: function () {
							var
								src = $module.data(metadata.src)
								;
							if (src) {
								module.verbose('Lazy loading image', src);
								settings.once = true;
								settings.observeChanges = false;

								// show when top visible
								settings.onOnScreen = function () {
									module.debug('Image on screen', element);
									module.precache(src, function () {
										module.set.image(src, function () {
											loadedCount++;
											if (loadedCount == moduleCount) {
												settings.onAllLoaded.call(this);
											}
											settings.onLoad.call(this);
										});
									});
								};
							}
						},
						fixed: function () {
							module.debug('Setting up fixed');
							settings.once = false;
							settings.observeChanges = false;
							settings.initialCheck = true;
							settings.refreshOnLoad = true;
							if (!parameters.transition) {
								settings.transition = false;
							}
							module.create.placeholder();
							module.debug('Added placeholder', $placeholder);
							settings.onTopPassed = function () {
								module.debug('Element passed, adding fixed position', $module);
								module.show.placeholder();
								module.set.fixed();
								if (settings.transition) {
									if ($.fn.transition !== undefined) {
										$module.transition(settings.transition, settings.duration);
									}
								}
							};
							settings.onTopPassedReverse = function () {
								module.debug('Element returned to position, removing fixed', $module);
								module.hide.placeholder();
								module.remove.fixed();
							};
						}
					},

					create: {
						placeholder: function () {
							module.verbose('Creating fixed position placeholder');
							$placeholder = $module
								.clone(false)
								.css('display', 'none')
								.addClass(className.placeholder)
								.insertAfter($module)
								;
						}
					},

					show: {
						placeholder: function () {
							module.verbose('Showing placeholder');
							$placeholder
								.css('display', 'block')
								.css('visibility', 'hidden')
								;
						}
					},
					hide: {
						placeholder: function () {
							module.verbose('Hiding placeholder');
							$placeholder
								.css('display', 'none')
								.css('visibility', '')
								;
						}
					},

					set: {
						fixed: function () {
							module.verbose('Setting element to fixed position');
							$module
								.addClass(className.fixed)
								.css({
									position: 'fixed',
									top: settings.offset + 'px',
									left: 'auto',
									zIndex: settings.zIndex
								})
								;
							settings.onFixed.call(element);
						},
						image: function (src, callback) {
							$module
								.attr('src', src)
								;
							if (settings.transition) {
								if ($.fn.transition !== undefined) {
									if ($module.hasClass(className.visible)) {
										module.debug('Transition already occurred on this image, skipping animation');
										return;
									}
									$module.transition(settings.transition, settings.duration, callback);
								}
								else {
									$module.fadeIn(settings.duration, callback);
								}
							}
							else {
								$module.show();
							}
						}
					},

					is: {
						onScreen: function () {
							var
								calculations = module.get.elementCalculations()
								;
							return calculations.onScreen;
						},
						offScreen: function () {
							var
								calculations = module.get.elementCalculations()
								;
							return calculations.offScreen;
						},
						visible: function () {
							if (module.cache && module.cache.element) {
								return !(module.cache.element.width === 0 && module.cache.element.offset.top === 0);
							}
							return false;
						},
						verticallyScrollableContext: function () {
							var
								overflowY = ($context.get(0) !== window)
									? $context.css('overflow-y')
									: false
								;
							return (overflowY == 'auto' || overflowY == 'scroll');
						},
						horizontallyScrollableContext: function () {
							var
								overflowX = ($context.get(0) !== window)
									? $context.css('overflow-x')
									: false
								;
							return (overflowX == 'auto' || overflowX == 'scroll');
						}
					},

					refresh: function () {
						module.debug('Refreshing constants (width/height)');
						if (settings.type == 'fixed') {
							module.resetFixed();
						}
						module.reset();
						module.save.position();
						if (settings.checkOnRefresh) {
							module.checkVisibility();
						}
						settings.onRefresh.call(element);
					},

					resetFixed: function () {
						module.remove.fixed();
						module.remove.occurred();
					},

					reset: function () {
						module.verbose('Resetting all cached values');
						if ($.isPlainObject(module.cache)) {
							module.cache.screen = {};
							module.cache.element = {};
						}
					},

					checkVisibility: function (scroll) {
						module.verbose('Checking visibility of element', module.cache.element);

						if (!disabled && module.is.visible()) {

							// save scroll position
							module.save.scroll(scroll);

							// update calculations derived from scroll
							module.save.calculations();

							// percentage
							module.passed();

							// reverse (must be first)
							module.passingReverse();
							module.topVisibleReverse();
							module.bottomVisibleReverse();
							module.topPassedReverse();
							module.bottomPassedReverse();

							// one time
							module.onScreen();
							module.offScreen();
							module.passing();
							module.topVisible();
							module.bottomVisible();
							module.topPassed();
							module.bottomPassed();

							// on update callback
							if (settings.onUpdate) {
								settings.onUpdate.call(element, module.get.elementCalculations());
							}
						}
					},

					passed: function (amount, newCallback) {
						var
							calculations = module.get.elementCalculations(),
							amountInPixels
							;
						// assign callback
						if (amount && newCallback) {
							settings.onPassed[amount] = newCallback;
						}
						else if (amount !== undefined) {
							return (module.get.pixelsPassed(amount) > calculations.pixelsPassed);
						}
						else if (calculations.passing) {
							$.each(settings.onPassed, function (amount, callback) {
								if (calculations.bottomVisible || calculations.pixelsPassed > module.get.pixelsPassed(amount)) {
									module.execute(callback, amount);
								}
								else if (!settings.once) {
									module.remove.occurred(callback);
								}
							});
						}
					},

					onScreen: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onOnScreen,
							callbackName = 'onScreen'
							;
						if (newCallback) {
							module.debug('Adding callback for onScreen', newCallback);
							settings.onOnScreen = newCallback;
						}
						if (calculations.onScreen) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback !== undefined) {
							return calculations.onOnScreen;
						}
					},

					offScreen: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onOffScreen,
							callbackName = 'offScreen'
							;
						if (newCallback) {
							module.debug('Adding callback for offScreen', newCallback);
							settings.onOffScreen = newCallback;
						}
						if (calculations.offScreen) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback !== undefined) {
							return calculations.onOffScreen;
						}
					},

					passing: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onPassing,
							callbackName = 'passing'
							;
						if (newCallback) {
							module.debug('Adding callback for passing', newCallback);
							settings.onPassing = newCallback;
						}
						if (calculations.passing) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback !== undefined) {
							return calculations.passing;
						}
					},


					topVisible: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onTopVisible,
							callbackName = 'topVisible'
							;
						if (newCallback) {
							module.debug('Adding callback for top visible', newCallback);
							settings.onTopVisible = newCallback;
						}
						if (calculations.topVisible) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return calculations.topVisible;
						}
					},

					bottomVisible: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onBottomVisible,
							callbackName = 'bottomVisible'
							;
						if (newCallback) {
							module.debug('Adding callback for bottom visible', newCallback);
							settings.onBottomVisible = newCallback;
						}
						if (calculations.bottomVisible) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return calculations.bottomVisible;
						}
					},

					topPassed: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onTopPassed,
							callbackName = 'topPassed'
							;
						if (newCallback) {
							module.debug('Adding callback for top passed', newCallback);
							settings.onTopPassed = newCallback;
						}
						if (calculations.topPassed) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return calculations.topPassed;
						}
					},

					bottomPassed: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onBottomPassed,
							callbackName = 'bottomPassed'
							;
						if (newCallback) {
							module.debug('Adding callback for bottom passed', newCallback);
							settings.onBottomPassed = newCallback;
						}
						if (calculations.bottomPassed) {
							module.execute(callback, callbackName);
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return calculations.bottomPassed;
						}
					},

					passingReverse: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onPassingReverse,
							callbackName = 'passingReverse'
							;
						if (newCallback) {
							module.debug('Adding callback for passing reverse', newCallback);
							settings.onPassingReverse = newCallback;
						}
						if (!calculations.passing) {
							if (module.get.occurred('passing')) {
								module.execute(callback, callbackName);
							}
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback !== undefined) {
							return !calculations.passing;
						}
					},


					topVisibleReverse: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onTopVisibleReverse,
							callbackName = 'topVisibleReverse'
							;
						if (newCallback) {
							module.debug('Adding callback for top visible reverse', newCallback);
							settings.onTopVisibleReverse = newCallback;
						}
						if (!calculations.topVisible) {
							if (module.get.occurred('topVisible')) {
								module.execute(callback, callbackName);
							}
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return !calculations.topVisible;
						}
					},

					bottomVisibleReverse: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onBottomVisibleReverse,
							callbackName = 'bottomVisibleReverse'
							;
						if (newCallback) {
							module.debug('Adding callback for bottom visible reverse', newCallback);
							settings.onBottomVisibleReverse = newCallback;
						}
						if (!calculations.bottomVisible) {
							if (module.get.occurred('bottomVisible')) {
								module.execute(callback, callbackName);
							}
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return !calculations.bottomVisible;
						}
					},

					topPassedReverse: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onTopPassedReverse,
							callbackName = 'topPassedReverse'
							;
						if (newCallback) {
							module.debug('Adding callback for top passed reverse', newCallback);
							settings.onTopPassedReverse = newCallback;
						}
						if (!calculations.topPassed) {
							if (module.get.occurred('topPassed')) {
								module.execute(callback, callbackName);
							}
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return !calculations.onTopPassed;
						}
					},

					bottomPassedReverse: function (newCallback) {
						var
							calculations = module.get.elementCalculations(),
							callback = newCallback || settings.onBottomPassedReverse,
							callbackName = 'bottomPassedReverse'
							;
						if (newCallback) {
							module.debug('Adding callback for bottom passed reverse', newCallback);
							settings.onBottomPassedReverse = newCallback;
						}
						if (!calculations.bottomPassed) {
							if (module.get.occurred('bottomPassed')) {
								module.execute(callback, callbackName);
							}
						}
						else if (!settings.once) {
							module.remove.occurred(callbackName);
						}
						if (newCallback === undefined) {
							return !calculations.bottomPassed;
						}
					},

					execute: function (callback, callbackName) {
						var
							calculations = module.get.elementCalculations(),
							screen = module.get.screenCalculations()
							;
						callback = callback || false;
						if (callback) {
							if (settings.continuous) {
								module.debug('Callback being called continuously', callbackName, calculations);
								callback.call(element, calculations, screen);
							}
							else if (!module.get.occurred(callbackName)) {
								module.debug('Conditions met', callbackName, calculations);
								callback.call(element, calculations, screen);
							}
						}
						module.save.occurred(callbackName);
					},

					remove: {
						fixed: function () {
							module.debug('Removing fixed position');
							$module
								.removeClass(className.fixed)
								.css({
									position: '',
									top: '',
									left: '',
									zIndex: ''
								})
								;
							settings.onUnfixed.call(element);
						},
						placeholder: function () {
							module.debug('Removing placeholder content');
							if ($placeholder) {
								$placeholder.remove();
							}
						},
						occurred: function (callback) {
							if (callback) {
								var
									occurred = module.cache.occurred
									;
								if (occurred[callback] !== undefined && occurred[callback] === true) {
									module.debug('Callback can now be called again', callback);
									module.cache.occurred[callback] = false;
								}
							}
							else {
								module.cache.occurred = {};
							}
						}
					},

					save: {
						calculations: function () {
							module.verbose('Saving all calculations necessary to determine positioning');
							module.save.direction();
							module.save.screenCalculations();
							module.save.elementCalculations();
						},
						occurred: function (callback) {
							if (callback) {
								if (module.cache.occurred[callback] === undefined || (module.cache.occurred[callback] !== true)) {
									module.verbose('Saving callback occurred', callback);
									module.cache.occurred[callback] = true;
								}
							}
						},
						scroll: function (scrollPosition) {
							scrollPosition = scrollPosition + settings.offset || $context.scrollTop() + settings.offset;
							module.cache.scroll = scrollPosition;
						},
						direction: function () {
							var
								scroll = module.get.scroll(),
								lastScroll = module.get.lastScroll(),
								direction
								;
							if (scroll > lastScroll && lastScroll) {
								direction = 'down';
							}
							else if (scroll < lastScroll && lastScroll) {
								direction = 'up';
							}
							else {
								direction = 'static';
							}
							module.cache.direction = direction;
							return module.cache.direction;
						},
						elementPosition: function () {
							var
								element = module.cache.element,
								screen = module.get.screenSize()
								;
							module.verbose('Saving element position');
							// (quicker than $.extend)
							element.fits = (element.height < screen.height);
							element.offset = $module.offset();
							element.width = $module.outerWidth();
							element.height = $module.outerHeight();
							// compensate for scroll in context
							if (module.is.verticallyScrollableContext()) {
								element.offset.top += $context.scrollTop() - $context.offset().top;
							}
							if (module.is.horizontallyScrollableContext()) {
								element.offset.left += $context.scrollLeft - $context.offset().left;
							}
							// store
							module.cache.element = element;
							return element;
						},
						elementCalculations: function () {
							var
								screen = module.get.screenCalculations(),
								element = module.get.elementPosition()
								;
							// offset
							if (settings.includeMargin) {
								element.margin = {};
								element.margin.top = parseInt($module.css('margin-top'), 10);
								element.margin.bottom = parseInt($module.css('margin-bottom'), 10);
								element.top = element.offset.top - element.margin.top;
								element.bottom = element.offset.top + element.height + element.margin.bottom;
							}
							else {
								element.top = element.offset.top;
								element.bottom = element.offset.top + element.height;
							}

							// visibility
							element.topPassed = (screen.top >= element.top);
							element.bottomPassed = (screen.top >= element.bottom);
							element.topVisible = (screen.bottom >= element.top) && !element.topPassed;
							element.bottomVisible = (screen.bottom >= element.bottom) && !element.bottomPassed;
							element.pixelsPassed = 0;
							element.percentagePassed = 0;

							// meta calculations
							element.onScreen = ((element.topVisible || element.passing) && !element.bottomPassed);
							element.passing = (element.topPassed && !element.bottomPassed);
							element.offScreen = (!element.onScreen);

							// passing calculations
							if (element.passing) {
								element.pixelsPassed = (screen.top - element.top);
								element.percentagePassed = (screen.top - element.top) / element.height;
							}
							module.cache.element = element;
							module.verbose('Updated element calculations', element);
							return element;
						},
						screenCalculations: function () {
							var
								scroll = module.get.scroll()
								;
							module.save.direction();
							module.cache.screen.top = scroll;
							module.cache.screen.bottom = scroll + module.cache.screen.height;
							return module.cache.screen;
						},
						screenSize: function () {
							module.verbose('Saving window position');
							module.cache.screen = {
								height: $context.height()
							};
						},
						position: function () {
							module.save.screenSize();
							module.save.elementPosition();
						}
					},

					get: {
						pixelsPassed: function (amount) {
							var
								element = module.get.elementCalculations()
								;
							if (amount.search('%') > -1) {
								return (element.height * (parseInt(amount, 10) / 100));
							}
							return parseInt(amount, 10);
						},
						occurred: function (callback) {
							return (module.cache.occurred !== undefined)
								? module.cache.occurred[callback] || false
								: false
								;
						},
						direction: function () {
							if (module.cache.direction === undefined) {
								module.save.direction();
							}
							return module.cache.direction;
						},
						elementPosition: function () {
							if (module.cache.element === undefined) {
								module.save.elementPosition();
							}
							return module.cache.element;
						},
						elementCalculations: function () {
							if (module.cache.element === undefined) {
								module.save.elementCalculations();
							}
							return module.cache.element;
						},
						screenCalculations: function () {
							if (module.cache.screen === undefined) {
								module.save.screenCalculations();
							}
							return module.cache.screen;
						},
						screenSize: function () {
							if (module.cache.screen === undefined) {
								module.save.screenSize();
							}
							return module.cache.screen;
						},
						scroll: function () {
							if (module.cache.scroll === undefined) {
								module.save.scroll();
							}
							return module.cache.scroll;
						},
						lastScroll: function () {
							if (module.cache.screen === undefined) {
								module.debug('First scroll event, no last scroll could be found');
								return false;
							}
							return module.cache.screen.top;
						}
					},

					setting: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, settings, name);
						}
						else if (value !== undefined) {
							settings[name] = value;
						}
						else {
							return settings[name];
						}
					},
					internal: function (name, value) {
						if ($.isPlainObject(name)) {
							$.extend(true, module, name);
						}
						else if (value !== undefined) {
							module[name] = value;
						}
						else {
							return module[name];
						}
					},
					debug: function () {
						if (!settings.silent && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.debug = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.debug.apply(console, arguments);
							}
						}
					},
					verbose: function () {
						if (!settings.silent && settings.verbose && settings.debug) {
							if (settings.performance) {
								module.performance.log(arguments);
							}
							else {
								module.verbose = Function.prototype.bind.call(console.info, console, settings.name + ':');
								module.verbose.apply(console, arguments);
							}
						}
					},
					error: function () {
						if (!settings.silent) {
							module.error = Function.prototype.bind.call(console.error, console, settings.name + ':');
							module.error.apply(console, arguments);
						}
					},
					performance: {
						log: function (message) {
							var
								currentTime,
								executionTime,
								previousTime
								;
							if (settings.performance) {
								currentTime = new Date().getTime();
								previousTime = time || currentTime;
								executionTime = currentTime - previousTime;
								time = currentTime;
								performance.push({
									'Name': message[0],
									'Arguments': [].slice.call(message, 1) || '',
									'Element': element,
									'Execution Time': executionTime
								});
							}
							clearTimeout(module.performance.timer);
							module.performance.timer = setTimeout(module.performance.display, 500);
						},
						display: function () {
							var
								title = settings.name + ':',
								totalTime = 0
								;
							time = false;
							clearTimeout(module.performance.timer);
							$.each(performance, function (index, data) {
								totalTime += data['Execution Time'];
							});
							title += ' ' + totalTime + 'ms';
							if (moduleSelector) {
								title += ' \'' + moduleSelector + '\'';
							}
							if ((console.group !== undefined || console.table !== undefined) && performance.length > 0) {
								console.groupCollapsed(title);
								if (console.table) {
									console.table(performance);
								}
								else {
									$.each(performance, function (index, data) {
										console.log(data['Name'] + ': ' + data['Execution Time'] + 'ms');
									});
								}
								console.groupEnd();
							}
							performance = [];
						}
					},
					invoke: function (query, passedArguments, context) {
						var
							object = instance,
							maxDepth,
							found,
							response
							;
						passedArguments = passedArguments || queryArguments;
						context = element || context;
						if (typeof query == 'string' && object !== undefined) {
							query = query.split(/[\. ]/);
							maxDepth = query.length - 1;
							$.each(query, function (depth, value) {
								var camelCaseValue = (depth != maxDepth)
									? value + query[depth + 1].charAt(0).toUpperCase() + query[depth + 1].slice(1)
									: query
									;
								if ($.isPlainObject(object[camelCaseValue]) && (depth != maxDepth)) {
									object = object[camelCaseValue];
								}
								else if (object[camelCaseValue] !== undefined) {
									found = object[camelCaseValue];
									return false;
								}
								else if ($.isPlainObject(object[value]) && (depth != maxDepth)) {
									object = object[value];
								}
								else if (object[value] !== undefined) {
									found = object[value];
									return false;
								}
								else {
									module.error(error.method, query);
									return false;
								}
							});
						}
						if ($.isFunction(found)) {
							response = found.apply(context, passedArguments);
						}
						else if (found !== undefined) {
							response = found;
						}
						if ($.isArray(returnedValue)) {
							returnedValue.push(response);
						}
						else if (returnedValue !== undefined) {
							returnedValue = [returnedValue, response];
						}
						else if (response !== undefined) {
							returnedValue = response;
						}
						return found;
					}
				};

				if (methodInvoked) {
					if (instance === undefined) {
						module.initialize();
					}
					instance.save.scroll();
					instance.save.calculations();
					module.invoke(query);
				}
				else {
					if (instance !== undefined) {
						instance.invoke('destroy');
					}
					module.initialize();
				}
			})
			;

		return (returnedValue !== undefined)
			? returnedValue
			: this
			;
	};

	$.fn.visibility.settings = {

		name: 'Visibility',
		namespace: 'visibility',

		debug: false,
		verbose: false,
		performance: true,

		// whether to use mutation observers to follow changes
		observeChanges: true,

		// check position immediately on init
		initialCheck: true,

		// whether to refresh calculations after all page images load
		refreshOnLoad: true,

		// whether to refresh calculations after page resize event
		refreshOnResize: true,

		// should call callbacks on refresh event (resize, etc)
		checkOnRefresh: true,

		// callback should only occur one time
		once: true,

		// callback should fire continuously whe evaluates to true
		continuous: false,

		// offset to use with scroll top
		offset: 0,

		// whether to include margin in elements position
		includeMargin: false,

		// scroll context for visibility checks
		context: window,

		// visibility check delay in ms (defaults to animationFrame)
		throttle: false,

		// special visibility type (image, fixed)
		type: false,

		// z-index to use with visibility 'fixed'
		zIndex: '10',

		// image only animation settings
		transition: 'fade in',
		duration: 1000,

		// array of callbacks for percentage
		onPassed: {},

		// standard callbacks
		onOnScreen: false,
		onOffScreen: false,
		onPassing: false,
		onTopVisible: false,
		onBottomVisible: false,
		onTopPassed: false,
		onBottomPassed: false,

		// reverse callbacks
		onPassingReverse: false,
		onTopVisibleReverse: false,
		onBottomVisibleReverse: false,
		onTopPassedReverse: false,
		onBottomPassedReverse: false,

		// special callbacks for image
		onLoad: function () { },
		onAllLoaded: function () { },

		// special callbacks for fixed position
		onFixed: function () { },
		onUnfixed: function () { },

		// utility callbacks
		onUpdate: false, // disabled by default for performance
		onRefresh: function () { },

		metadata: {
			src: 'src'
		},

		className: {
			fixed: 'fixed',
			placeholder: 'placeholder',
			visible: 'visible'
		},

		error: {
			method: 'The method you called is not defined.',
			visible: 'Element is hidden, you must call refresh after element becomes visible'
		}

	};

})(jQuery, window, document);

/**
 ********************************
 **        ANGULAR             **
 ********************************
 */
/**
/**
 * 
 * 
 * 
 */
(function (window) {
	'use strict';

	/* exported
	  minErrConfig,
	  errorHandlingConfig,
	  isValidObjectMaxDepth
	*/

	var minErrConfig = {
		objectMaxDepth: 5,
		urlErrorParamsEnabled: true
	};

	/**
	 * @ngdoc function
	 * @name angular.errorHandlingConfig
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Configure several aspects of error handling in AngularJS if used as a setter or return the
	 * current configuration if used as a getter. The following options are supported:
	 *
	 * - **objectMaxDepth**: The maximum depth to which objects are traversed when stringified for error messages.
	 *
	 * Omitted or undefined options will leave the corresponding configuration values unchanged.
	 *
	 * @param {Object=} config - The configuration object. May only contain the options that need to be
	 *     updated. Supported keys:
	 *
	 * * `objectMaxDepth`  **{Number}** - The max depth for stringifying objects. Setting to a
	 *   non-positive or non-numeric value, removes the max depth limit.
	 *   Default: 5
	 *
	 * * `urlErrorParamsEnabled`  **{Boolean}** - Specifies whether the generated error url will
	 *   contain the parameters of the thrown error. Disabling the parameters can be useful if the
	 *   generated error url is very long.
	 *
	 *   Default: true. When used without argument, it returns the current value.
	 */
	function errorHandlingConfig(config) {
		if (isObject(config)) {
			if (isDefined(config.objectMaxDepth)) {
				minErrConfig.objectMaxDepth = isValidObjectMaxDepth(config.objectMaxDepth) ? config.objectMaxDepth : NaN;
			}
			if (isDefined(config.urlErrorParamsEnabled) && isBoolean(config.urlErrorParamsEnabled)) {
				minErrConfig.urlErrorParamsEnabled = config.urlErrorParamsEnabled;
			}
		} else {
			return minErrConfig;
		}
	}

	/**
	 * @private
	 * @param {Number} maxDepth
	 * @return {boolean}
	 */
	function isValidObjectMaxDepth(maxDepth) {
		return isNumber(maxDepth) && maxDepth > 0;
	}


	/**
	 * @description
	 *
	 * This object provides a utility for producing rich Error messages within
	 * AngularJS. It can be called as follows:
	 *
	 * var exampleMinErr = minErr('example');
	 * throw exampleMinErr('one', 'This {0} is {1}', foo, bar);
	 *
	 * The above creates an instance of minErr in the example namespace. The
	 * resulting error will have a namespaced error code of example.one.  The
	 * resulting error will replace {0} with the value of foo, and {1} with the
	 * value of bar. The object is not restricted in the number of arguments it can
	 * take.
	 *
	 * If fewer arguments are specified than necessary for interpolation, the extra
	 * interpolation markers will be preserved in the final string.
	 *
	 * Since data will be parsed statically during a build step, some restrictions
	 * are applied with respect to how minErr instances are created and called.
	 * Instances should have names of the form namespaceMinErr for a minErr created
	 * using minErr('namespace'). Error codes, namespaces and template strings
	 * should all be static strings, not variables or general expressions.
	 *
	 * @param {string} module The namespace to use for the new minErr instance.
	 * @param {function} ErrorConstructor Custom error constructor to be instantiated when returning
	 *   error from returned function, for cases when a particular type of error is useful.
	 * @returns {function(code:string, template:string, ...templateArgs): Error} minErr instance
	 */

	function minErr(module, ErrorConstructor) {
		ErrorConstructor = ErrorConstructor || Error;

		var url = 'https://errors.angularjs.org/1.8.3-build.3784+sha.05c65be8a/';
		var regex = url.replace('.', '\\.') + '[\\s\\S]*';
		var errRegExp = new RegExp(regex, 'g');

		return function () {
			var code = arguments[0],
				template = arguments[1],
				message = '[' + (module ? module + ':' : '') + code + '] ',
				templateArgs = sliceArgs(arguments, 2).map(function (arg) {
					return toDebugString(arg, minErrConfig.objectMaxDepth);
				}),
				paramPrefix, i;

			// A minErr message has two parts: the message itself and the url that contains the
			// encoded message.
			// The message's parameters can contain other error messages which also include error urls.
			// To prevent the messages from getting too long, we strip the error urls from the parameters.

			message += template.replace(/\{\d+\}/g, function (match) {
				var index = +match.slice(1, -1);

				if (index < templateArgs.length) {
					return templateArgs[index].replace(errRegExp, '');
				}

				return match;
			});

			message += '\n' + url + (module ? module + '/' : '') + code;

			if (minErrConfig.urlErrorParamsEnabled) {
				for (i = 0, paramPrefix = '?'; i < templateArgs.length; i++, paramPrefix = '&') {
					message += paramPrefix + 'p' + i + '=' + encodeURIComponent(templateArgs[i]);
				}
			}

			return new ErrorConstructor(message);
		};
	}

	/* We need to tell ESLint what variables are being exported */
	/* exported
	  angular,
	  msie,
	  jqLite,
	  jQuery,
	  slice,
	  splice,
	  push,
	  toString,
	  minErrConfig,
	  errorHandlingConfig,
	  isValidObjectMaxDepth,
	  ngMinErr,
	  angularModule,
	  uid,
	  REGEX_STRING_REGEXP,
	  VALIDITY_STATE_PROPERTY,
	
	  lowercase,
	  uppercase,
	  nodeName_,
	  isArrayLike,
	  forEach,
	  forEachSorted,
	  reverseParams,
	  nextUid,
	  setHashKey,
	  extend,
	  toInt,
	  inherit,
	  merge,
	  noop,
	  identity,
	  valueFn,
	  isUndefined,
	  isDefined,
	  isObject,
	  isBlankObject,
	  isString,
	  isNumber,
	  isNumberNaN,
	  isDate,
	  isError,
	  isArray,
	  isFunction,
	  isRegExp,
	  isWindow,
	  isScope,
	  isFile,
	  isFormData,
	  isBlob,
	  isBoolean,
	  isPromiseLike,
	  trim,
	  escapeForRegexp,
	  isElement,
	  makeMap,
	  includes,
	  arrayRemove,
	  copy,
	  simpleCompare,
	  equals,
	  csp,
	  jq,
	  concat,
	  sliceArgs,
	  bind,
	  toJsonReplacer,
	  toJson,
	  fromJson,
	  convertTimezoneToLocal,
	  timezoneToOffset,
	  addDateMinutes,
	  startingTag,
	  tryDecodeURIComponent,
	  parseKeyValue,
	  toKeyValue,
	  encodeUriSegment,
	  encodeUriQuery,
	  angularInit,
	  bootstrap,
	  getTestability,
	  snake_case,
	  bindJQuery,
	  assertArg,
	  assertArgFn,
	  assertNotHasOwnProperty,
	  getter,
	  getBlockNodes,
	  hasOwnProperty,
	  createMap,
	  stringify,
	  UNSAFE_restoreLegacyJqLiteXHTMLReplacement,
	
	  NODE_TYPE_ELEMENT,
	  NODE_TYPE_ATTRIBUTE,
	  NODE_TYPE_TEXT,
	  NODE_TYPE_COMMENT,
	  NODE_TYPE_DOCUMENT,
	  NODE_TYPE_DOCUMENT_FRAGMENT
	*/

	////////////////////////////////////

	/**
	 * @ngdoc module
	 * @name ng
	 * @module ng
	 * @installation
	 * @description
	 *
	 * The ng module is loaded by default when an AngularJS application is started. The module itself
	 * contains the essential components for an AngularJS application to function. The table below
	 * lists a high level breakdown of each of the services/factories, filters, directives and testing
	 * components available within this core module.
	 *
	 */

	var REGEX_STRING_REGEXP = /^\/(.+)\/([a-z]*)$/;

	// The name of a form control's ValidityState property.
	// This is used so that it's possible for internal tests to create mock ValidityStates.
	var VALIDITY_STATE_PROPERTY = 'validity';


	var hasOwnProperty = Object.prototype.hasOwnProperty;

	/**
	 * @private
	 *
	 * @description Converts the specified string to lowercase.
	 * @param {string} string String to be converted to lowercase.
	 * @returns {string} Lowercased string.
	 */
	var lowercase = function (string) { return isString(string) ? string.toLowerCase() : string; };

	/**
	 * @private
	 *
	 * @description Converts the specified string to uppercase.
	 * @param {string} string String to be converted to uppercase.
	 * @returns {string} Uppercased string.
	 */
	var uppercase = function (string) { return isString(string) ? string.toUpperCase() : string; };


	var
		msie,             // holds major version number for IE, or NaN if UA is not IE.
		jqLite,           // delay binding since jQuery could be loaded after us.
		jQuery,           // delay binding
		slice = [].slice,
		splice = [].splice,
		push = [].push,
		toString = Object.prototype.toString,
		getPrototypeOf = Object.getPrototypeOf,
		ngMinErr = minErr('ng'),

		/** @name angular */
		angular = window.angular || (window.angular = {}),
		angularModule,
		uid = 0;

	// Support: IE 9-11 only
	/**
	 * documentMode is an IE-only property
	 * http://msdn.microsoft.com/en-us/library/ie/cc196988(v=vs.85).aspx
	 */
	msie = window.document.documentMode;


	/**
	 * @private
	 * @param {*} obj
	 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
	 *                   String ...)
	 */
	function isArrayLike(obj) {

		// `null`, `undefined` and `window` are not array-like
		if (obj == null || isWindow(obj)) return false;

		// arrays, strings and jQuery/jqLite objects are array like
		// * jqLite is either the jQuery or jqLite constructor function
		// * we have to check the existence of jqLite first as this method is called
		//   via the forEach method when constructing the jqLite object in the first place
		if (isArray(obj) || isString(obj) || (jqLite && obj instanceof jqLite)) return true;

		// Support: iOS 8.2 (not reproducible in simulator)
		// "length" in obj used to prevent JIT error (gh-11508)
		var length = 'length' in Object(obj) && obj.length;

		// NodeList objects (with `item` method) and
		// other objects with suitable length characteristics are array-like
		return isNumber(length) && (length >= 0 && (length - 1) in obj || typeof obj.item === 'function');

	}

	/**
	 * @ngdoc function
	 * @name angular.forEach
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Invokes the `iterator` function once for each item in `obj` collection, which can be either an
	 * object or an array. The `iterator` function is invoked with `iterator(value, key, obj)`, where `value`
	 * is the value of an object property or an array element, `key` is the object property key or
	 * array element index and obj is the `obj` itself. Specifying a `context` for the function is optional.
	 *
	 * It is worth noting that `.forEach` does not iterate over inherited properties because it filters
	 * using the `hasOwnProperty` method.
	 *
	 * Unlike ES262's
	 * [Array.prototype.forEach](http://www.ecma-international.org/ecma-262/5.1/#sec-15.4.4.18),
	 * providing 'undefined' or 'null' values for `obj` will not throw a TypeError, but rather just
	 * return the value provided.
	 *
	   ```js
		 var values = {name: 'misko', gender: 'male'};
		 var log = [];
		 angular.forEach(values, function(value, key) {
		   this.push(key + ': ' + value);
		 }, log);
		 expect(log).toEqual(['name: misko', 'gender: male']);
	   ```
	 *
	 * @param {Object|Array} obj Object to iterate over.
	 * @param {Function} iterator Iterator function.
	 * @param {Object=} context Object to become context (`this`) for the iterator function.
	 * @returns {Object|Array} Reference to `obj`.
	 */

	function forEach(obj, iterator, context) {
		var key, length;
		if (obj) {
			if (isFunction(obj)) {
				for (key in obj) {
					if (key !== 'prototype' && key !== 'length' && key !== 'name' && obj.hasOwnProperty(key)) {
						iterator.call(context, obj[key], key, obj);
					}
				}
			} else if (isArray(obj) || isArrayLike(obj)) {
				var isPrimitive = typeof obj !== 'object';
				for (key = 0, length = obj.length; key < length; key++) {
					if (isPrimitive || key in obj) {
						iterator.call(context, obj[key], key, obj);
					}
				}
			} else if (obj.forEach && obj.forEach !== forEach) {
				obj.forEach(iterator, context, obj);
			} else if (isBlankObject(obj)) {
				// createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
				for (key in obj) {
					iterator.call(context, obj[key], key, obj);
				}
			} else if (typeof obj.hasOwnProperty === 'function') {
				// Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
				for (key in obj) {
					if (obj.hasOwnProperty(key)) {
						iterator.call(context, obj[key], key, obj);
					}
				}
			} else {
				// Slow path for objects which do not have a method `hasOwnProperty`
				for (key in obj) {
					if (hasOwnProperty.call(obj, key)) {
						iterator.call(context, obj[key], key, obj);
					}
				}
			}
		}
		return obj;
	}

	function forEachSorted(obj, iterator, context) {
		var keys = Object.keys(obj).sort();
		for (var i = 0; i < keys.length; i++) {
			iterator.call(context, obj[keys[i]], keys[i]);
		}
		return keys;
	}


	/**
	 * when using forEach the params are value, key, but it is often useful to have key, value.
	 * @param {function(string, *)} iteratorFn
	 * @returns {function(*, string)}
	 */
	function reverseParams(iteratorFn) {
		return function (value, key) { iteratorFn(key, value); };
	}

	/**
	 * A consistent way of creating unique IDs in angular.
	 *
	 * Using simple numbers allows us to generate 28.6 million unique ids per second for 10 years before
	 * we hit number precision issues in JavaScript.
	 *
	 * Math.pow(2,53) / 60 / 60 / 24 / 365 / 10 = 28.6M
	 *
	 * @returns {number} an unique alpha-numeric string
	 */
	function nextUid() {
		return ++uid;
	}


	/**
	 * Set or clear the hashkey for an object.
	 * @param obj object
	 * @param h the hashkey (!truthy to delete the hashkey)
	 */
	function setHashKey(obj, h) {
		if (h) {
			obj.$$hashKey = h;
		} else {
			delete obj.$$hashKey;
		}
	}


	function baseExtend(dst, objs, deep) {
		var h = dst.$$hashKey;

		for (var i = 0, ii = objs.length; i < ii; ++i) {
			var obj = objs[i];
			if (!isObject(obj) && !isFunction(obj)) continue;
			var keys = Object.keys(obj);
			for (var j = 0, jj = keys.length; j < jj; j++) {
				var key = keys[j];
				var src = obj[key];

				if (deep && isObject(src)) {
					if (isDate(src)) {
						dst[key] = new Date(src.valueOf());
					} else if (isRegExp(src)) {
						dst[key] = new RegExp(src);
					} else if (src.nodeName) {
						dst[key] = src.cloneNode(true);
					} else if (isElement(src)) {
						dst[key] = src.clone();
					} else {
						if (key !== '__proto__') {
							if (!isObject(dst[key])) dst[key] = isArray(src) ? [] : {};
							baseExtend(dst[key], [src], true);
						}
					}
				} else {
					dst[key] = src;
				}
			}
		}

		setHashKey(dst, h);
		return dst;
	}

	/**
	 * @ngdoc function
	 * @name angular.extend
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
	 * to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
	 * by passing an empty object as the target: `var object = angular.extend({}, object1, object2)`.
	 *
	 * **Note:** Keep in mind that `angular.extend` does not support recursive merge (deep copy). Use
	 * {@link angular.merge} for this.
	 *
	 * @param {Object} dst Destination object.
	 * @param {...Object} src Source object(s).
	 * @returns {Object} Reference to `dst`.
	 */
	function extend(dst) {
		return baseExtend(dst, slice.call(arguments, 1), false);
	}


	/**
	* @ngdoc function
	* @name angular.merge
	* @module ng
	* @kind function
	*
	* @description
	* Deeply extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
	* to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
	* by passing an empty object as the target: `var object = angular.merge({}, object1, object2)`.
	*
	* Unlike {@link angular.extend extend()}, `merge()` recursively descends into object properties of source
	* objects, performing a deep copy.
	*
	* @deprecated
	* sinceVersion="1.6.5"
	* This function is deprecated, but will not be removed in the 1.x lifecycle.
	* There are edge cases (see {@link angular.merge#known-issues known issues}) that are not
	* supported by this function. We suggest using another, similar library for all-purpose merging,
	* such as [lodash's merge()](https://lodash.com/docs/4.17.4#merge).
	*
	* @knownIssue
	* This is a list of (known) object types that are not handled correctly by this function:
	* - [`Blob`](https://developer.mozilla.org/docs/Web/API/Blob)
	* - [`MediaStream`](https://developer.mozilla.org/docs/Web/API/MediaStream)
	* - [`CanvasGradient`](https://developer.mozilla.org/docs/Web/API/CanvasGradient)
	* - AngularJS {@link $rootScope.Scope scopes};
	*
	* `angular.merge` also does not support merging objects with circular references.
	*
	* @param {Object} dst Destination object.
	* @param {...Object} src Source object(s).
	* @returns {Object} Reference to `dst`.
	*/
	function merge(dst) {
		return baseExtend(dst, slice.call(arguments, 1), true);
	}



	function toInt(str) {
		return parseInt(str, 10);
	}

	var isNumberNaN = Number.isNaN || function isNumberNaN(num) {
		// eslint-disable-next-line no-self-compare
		return num !== num;
	};


	function inherit(parent, extra) {
		return extend(Object.create(parent), extra);
	}

	/**
	 * @ngdoc function
	 * @name angular.noop
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * A function that performs no operations. This function can be useful when writing code in the
	 * functional style.
	   ```js
		 function foo(callback) {
		   var result = calculateResult();
		   (callback || angular.noop)(result);
		 }
	   ```
	 */
	function noop() { }
	noop.$inject = [];


	/**
	 * @ngdoc function
	 * @name angular.identity
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * A function that returns its first argument. This function is useful when writing code in the
	 * functional style.
	 *
	   ```js
	   function transformer(transformationFn, value) {
		 return (transformationFn || angular.identity)(value);
	   };
	
	   // E.g.
	   function getResult(fn, input) {
		 return (fn || angular.identity)(input);
	   };
	
	   getResult(function(n) { return n * 2; }, 21);   // returns 42
	   getResult(null, 21);                            // returns 21
	   getResult(undefined, 21);                       // returns 21
	   ```
	 *
	 * @param {*} value to be returned.
	 * @returns {*} the value passed in.
	 */
	function identity($) { return $; }
	identity.$inject = [];


	function valueFn(value) { return function valueRef() { return value; }; }

	function hasCustomToString(obj) {
		return isFunction(obj.toString) && obj.toString !== toString;
	}


	/**
	 * @ngdoc function
	 * @name angular.isUndefined
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is undefined.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is undefined.
	 */
	function isUndefined(value) { return typeof value === 'undefined'; }


	/**
	 * @ngdoc function
	 * @name angular.isDefined
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is defined.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is defined.
	 */
	function isDefined(value) { return typeof value !== 'undefined'; }


	/**
	 * @ngdoc function
	 * @name angular.isObject
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
	 * considered to be objects. Note that JavaScript arrays are objects.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is an `Object` but not `null`.
	 */
	function isObject(value) {
		// http://jsperf.com/isobject4
		return value !== null && typeof value === 'object';
	}


	/**
	 * Determine if a value is an object with a null prototype
	 *
	 * @returns {boolean} True if `value` is an `Object` with a null prototype
	 */
	function isBlankObject(value) {
		return value !== null && typeof value === 'object' && !getPrototypeOf(value);
	}


	/**
	 * @ngdoc function
	 * @name angular.isString
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is a `String`.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a `String`.
	 */
	function isString(value) { return typeof value === 'string'; }


	/**
	 * @ngdoc function
	 * @name angular.isNumber
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is a `Number`.
	 *
	 * This includes the "special" numbers `NaN`, `+Infinity` and `-Infinity`.
	 *
	 * If you wish to exclude these then you can use the native
	 * [`isFinite'](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isFinite)
	 * method.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a `Number`.
	 */
	function isNumber(value) { return typeof value === 'number'; }


	/**
	 * @ngdoc function
	 * @name angular.isDate
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a value is a date.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a `Date`.
	 */
	function isDate(value) {
		return toString.call(value) === '[object Date]';
	}


	/**
	 * @ngdoc function
	 * @name angular.isArray
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is an `Array`.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is an `Array`.
	 */
	function isArray(arr) {
		return Array.isArray(arr) || arr instanceof Array;
	}

	/**
	 * @description
	 * Determines if a reference is an `Error`.
	 * Loosely based on https://www.npmjs.com/package/iserror
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is an `Error`.
	 */
	function isError(value) {
		var tag = toString.call(value);
		switch (tag) {
			case '[object Error]': return true;
			case '[object Exception]': return true;
			case '[object DOMException]': return true;
			default: return value instanceof Error;
		}
	}

	/**
	 * @ngdoc function
	 * @name angular.isFunction
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is a `Function`.
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a `Function`.
	 */
	function isFunction(value) { return typeof value === 'function'; }


	/**
	 * Determines if a value is a regular expression object.
	 *
	 * @private
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a `RegExp`.
	 */
	function isRegExp(value) {
		return toString.call(value) === '[object RegExp]';
	}


	/**
	 * Checks if `obj` is a window object.
	 *
	 * @private
	 * @param {*} obj Object to check
	 * @returns {boolean} True if `obj` is a window obj.
	 */
	function isWindow(obj) {
		return obj && obj.window === obj;
	}


	function isScope(obj) {
		return obj && obj.$evalAsync && obj.$watch;
	}


	function isFile(obj) {
		return toString.call(obj) === '[object File]';
	}


	function isFormData(obj) {
		return toString.call(obj) === '[object FormData]';
	}


	function isBlob(obj) {
		return toString.call(obj) === '[object Blob]';
	}


	function isBoolean(value) {
		return typeof value === 'boolean';
	}


	function isPromiseLike(obj) {
		return obj && isFunction(obj.then);
	}


	var TYPED_ARRAY_REGEXP = /^\[object (?:Uint8|Uint8Clamped|Uint16|Uint32|Int8|Int16|Int32|Float32|Float64)Array]$/;
	function isTypedArray(value) {
		return value && isNumber(value.length) && TYPED_ARRAY_REGEXP.test(toString.call(value));
	}

	function isArrayBuffer(obj) {
		return toString.call(obj) === '[object ArrayBuffer]';
	}


	var trim = function (value) {
		return isString(value) ? value.trim() : value;
	};

	// Copied from:
	// http://docs.closure-library.googlecode.com/git/local_closure_goog_string_string.js.source.html#line1021
	// Prereq: s is a string.
	var escapeForRegexp = function (s) {
		return s
			.replace(/([-()[\]{}+?*.$^|,:#<!\\])/g, '\\$1')
			// eslint-disable-next-line no-control-regex
			.replace(/\x08/g, '\\x08');
	};


	/**
	 * @ngdoc function
	 * @name angular.isElement
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if a reference is a DOM element (or wrapped jQuery element).
	 *
	 * @param {*} value Reference to check.
	 * @returns {boolean} True if `value` is a DOM element (or wrapped jQuery element).
	 */
	function isElement(node) {
		return !!(node &&
			(node.nodeName  // We are a direct element.
				|| (node.prop && node.attr && node.find)));  // We have an on and find method part of jQuery API.
	}

	/**
	 * @param str 'key1,key2,...'
	 * @returns {object} in the form of {key1:true, key2:true, ...}
	 */
	function makeMap(str) {
		var obj = {}, items = str.split(','), i;
		for (i = 0; i < items.length; i++) {
			obj[items[i]] = true;
		}
		return obj;
	}


	function nodeName_(element) {
		return lowercase(element.nodeName || (element[0] && element[0].nodeName));
	}

	function includes(array, obj) {
		return Array.prototype.indexOf.call(array, obj) !== -1;
	}

	function arrayRemove(array, value) {
		var index = array.indexOf(value);
		if (index >= 0) {
			array.splice(index, 1);
		}
		return index;
	}

	/**
	 * @ngdoc function
	 * @name angular.copy
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Creates a deep copy of `source`, which should be an object or an array. This functions is used
	 * internally, mostly in the change-detection code. It is not intended as an all-purpose copy
	 * function, and has several limitations (see below).
	 *
	 * * If no destination is supplied, a copy of the object or array is created.
	 * * If a destination is provided, all of its elements (for arrays) or properties (for objects)
	 *   are deleted and then all elements/properties from the source are copied to it.
	 * * If `source` is not an object or array (inc. `null` and `undefined`), `source` is returned.
	 * * If `source` is identical to `destination` an exception will be thrown.
	 *
	 * <br />
	 *
	 * <div class="alert alert-warning">
	 *   Only enumerable properties are taken into account. Non-enumerable properties (both on `source`
	 *   and on `destination`) will be ignored.
	 * </div>
	 *
	 * <div class="alert alert-warning">
	 *   `angular.copy` does not check if destination and source are of the same type. It's the
	 *   developer's responsibility to make sure they are compatible.
	 * </div>
	 *
	 * @knownIssue
	 * This is a non-exhaustive list of object types / features that are not handled correctly by
	 * `angular.copy`. Note that since this functions is used by the change detection code, this
	 * means binding or watching objects of these types (or that include these types) might not work
	 * correctly.
	 * - [`File`](https://developer.mozilla.org/docs/Web/API/File)
	 * - [`Map`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Map)
	 * - [`ImageData`](https://developer.mozilla.org/docs/Web/API/ImageData)
	 * - [`MediaStream`](https://developer.mozilla.org/docs/Web/API/MediaStream)
	 * - [`Set`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Set)
	 * - [`WeakMap`](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/WeakMap)
	 * - [`getter`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/get)/
	 *   [`setter`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/set)
	 *
	 * @param {*} source The source that will be used to make a copy. Can be any type, including
	 *     primitives, `null`, and `undefined`.
	 * @param {(Object|Array)=} destination Destination into which the source is copied. If provided,
	 *     must be of the same type as `source`.
	 * @returns {*} The copy or updated `destination`, if `destination` was specified.
	 *
	 * @example
	  <example module="copyExample" name="angular-copy">
		<file name="index.html">
		  <div ng-controller="ExampleController">
			<form novalidate class="simple-form">
			  <label>Name: <input type="text" ng-model="user.name" /></label><br />
			  <label>Age:  <input type="number" ng-model="user.age" /></label><br />
			  Gender: <label><input type="radio" ng-model="user.gender" value="male" />male</label>
					  <label><input type="radio" ng-model="user.gender" value="female" />female</label><br />
			  <button ng-click="reset()">RESET</button>
			  <button ng-click="update(user)">SAVE</button>
			</form>
			<pre>form = {{user | json}}</pre>
			<pre>leader = {{leader | json}}</pre>
		  </div>
		</file>
		<file name="script.js">
		  // Module: copyExample
		  angular.
			module('copyExample', []).
			controller('ExampleController', ['$scope', function($scope) {
			  $scope.leader = {};
	
			  $scope.reset = function() {
				// Example with 1 argument
				$scope.user = angular.copy($scope.leader);
			  };
	
			  $scope.update = function(user) {
				// Example with 2 arguments
				angular.copy(user, $scope.leader);
			  };
	
			  $scope.reset();
			}]);
		</file>
	  </example>
	 */
	function copy(source, destination, maxDepth) {
		var stackSource = [];
		var stackDest = [];
		maxDepth = isValidObjectMaxDepth(maxDepth) ? maxDepth : NaN;

		if (destination) {
			if (isTypedArray(destination) || isArrayBuffer(destination)) {
				throw ngMinErr('cpta', 'Can\'t copy! TypedArray destination cannot be mutated.');
			}
			if (source === destination) {
				throw ngMinErr('cpi', 'Can\'t copy! Source and destination are identical.');
			}

			// Empty the destination object
			if (isArray(destination)) {
				destination.length = 0;
			} else {
				forEach(destination, function (value, key) {
					if (key !== '$$hashKey') {
						delete destination[key];
					}
				});
			}

			stackSource.push(source);
			stackDest.push(destination);
			return copyRecurse(source, destination, maxDepth);
		}

		return copyElement(source, maxDepth);

		function copyRecurse(source, destination, maxDepth) {
			maxDepth--;
			if (maxDepth < 0) {
				return '...';
			}
			var h = destination.$$hashKey;
			var key;
			if (isArray(source)) {
				for (var i = 0, ii = source.length; i < ii; i++) {
					destination.push(copyElement(source[i], maxDepth));
				}
			} else if (isBlankObject(source)) {
				// createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
				for (key in source) {
					destination[key] = copyElement(source[key], maxDepth);
				}
			} else if (source && typeof source.hasOwnProperty === 'function') {
				// Slow path, which must rely on hasOwnProperty
				for (key in source) {
					if (source.hasOwnProperty(key)) {
						destination[key] = copyElement(source[key], maxDepth);
					}
				}
			} else {
				// Slowest path --- hasOwnProperty can't be called as a method
				for (key in source) {
					if (hasOwnProperty.call(source, key)) {
						destination[key] = copyElement(source[key], maxDepth);
					}
				}
			}
			setHashKey(destination, h);
			return destination;
		}

		function copyElement(source, maxDepth) {
			// Simple values
			if (!isObject(source)) {
				return source;
			}

			// Already copied values
			var index = stackSource.indexOf(source);
			if (index !== -1) {
				return stackDest[index];
			}

			if (isWindow(source) || isScope(source)) {
				throw ngMinErr('cpws',
					'Can\'t copy! Making copies of Window or Scope instances is not supported.');
			}

			var needsRecurse = false;
			var destination = copyType(source);

			if (destination === undefined) {
				destination = isArray(source) ? [] : Object.create(getPrototypeOf(source));
				needsRecurse = true;
			}

			stackSource.push(source);
			stackDest.push(destination);

			return needsRecurse
				? copyRecurse(source, destination, maxDepth)
				: destination;
		}

		function copyType(source) {
			switch (toString.call(source)) {
				case '[object Int8Array]':
				case '[object Int16Array]':
				case '[object Int32Array]':
				case '[object Float32Array]':
				case '[object Float64Array]':
				case '[object Uint8Array]':
				case '[object Uint8ClampedArray]':
				case '[object Uint16Array]':
				case '[object Uint32Array]':
					return new source.constructor(copyElement(source.buffer), source.byteOffset, source.length);

				case '[object ArrayBuffer]':
					// Support: IE10
					if (!source.slice) {
						// If we're in this case we know the environment supports ArrayBuffer
						/* eslint-disable no-undef */
						var copied = new ArrayBuffer(source.byteLength);
						new Uint8Array(copied).set(new Uint8Array(source));
						/* eslint-enable */
						return copied;
					}
					return source.slice(0);

				case '[object Boolean]':
				case '[object Number]':
				case '[object String]':
				case '[object Date]':
					return new source.constructor(source.valueOf());

				case '[object RegExp]':
					var re = new RegExp(source.source, source.toString().match(/[^/]*$/)[0]);
					re.lastIndex = source.lastIndex;
					return re;

				case '[object Blob]':
					return new source.constructor([source], { type: source.type });
			}

			if (isFunction(source.cloneNode)) {
				return source.cloneNode(true);
			}
		}
	}


	// eslint-disable-next-line no-self-compare
	function simpleCompare(a, b) { return a === b || (a !== a && b !== b); }


	/**
	 * @ngdoc function
	 * @name angular.equals
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Determines if two objects or two values are equivalent. Supports value types, regular
	 * expressions, arrays and objects.
	 *
	 * Two objects or values are considered equivalent if at least one of the following is true:
	 *
	 * * Both objects or values pass `===` comparison.
	 * * Both objects or values are of the same type and all of their properties are equal by
	 *   comparing them with `angular.equals`.
	 * * Both values are NaN. (In JavaScript, NaN == NaN => false. But we consider two NaN as equal)
	 * * Both values represent the same regular expression (In JavaScript,
	 *   /abc/ == /abc/ => false. But we consider two regular expressions as equal when their textual
	 *   representation matches).
	 *
	 * During a property comparison, properties of `function` type and properties with names
	 * that begin with `$` are ignored.
	 *
	 * Scope and DOMWindow objects are being compared only by identify (`===`).
	 *
	 * @param {*} o1 Object or value to compare.
	 * @param {*} o2 Object or value to compare.
	 * @returns {boolean} True if arguments are equal.
	 *
	 * @example
	   <example module="equalsExample" name="equalsExample">
		 <file name="index.html">
		  <div ng-controller="ExampleController">
			<form novalidate>
			  <h3>User 1</h3>
			  Name: <input type="text" ng-model="user1.name">
			  Age: <input type="number" ng-model="user1.age">
	
			  <h3>User 2</h3>
			  Name: <input type="text" ng-model="user2.name">
			  Age: <input type="number" ng-model="user2.age">
	
			  <div>
				<br/>
				<input type="button" value="Compare" ng-click="compare()">
			  </div>
			  User 1: <pre>{{user1 | json}}</pre>
			  User 2: <pre>{{user2 | json}}</pre>
			  Equal: <pre>{{result}}</pre>
			</form>
		  </div>
		</file>
		<file name="script.js">
			angular.module('equalsExample', []).controller('ExampleController', ['$scope', function($scope) {
			  $scope.user1 = {};
			  $scope.user2 = {};
			  $scope.compare = function() {
				$scope.result = angular.equals($scope.user1, $scope.user2);
			  };
			}]);
		</file>
	  </example>
	 */
	function equals(o1, o2) {
		if (o1 === o2) return true;
		if (o1 === null || o2 === null) return false;
		// eslint-disable-next-line no-self-compare
		if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
		var t1 = typeof o1, t2 = typeof o2, length, key, keySet;
		if (t1 === t2 && t1 === 'object') {
			if (isArray(o1)) {
				if (!isArray(o2)) return false;
				if ((length = o1.length) === o2.length) {
					for (key = 0; key < length; key++) {
						if (!equals(o1[key], o2[key])) return false;
					}
					return true;
				}
			} else if (isDate(o1)) {
				if (!isDate(o2)) return false;
				return simpleCompare(o1.getTime(), o2.getTime());
			} else if (isRegExp(o1)) {
				if (!isRegExp(o2)) return false;
				return o1.toString() === o2.toString();
			} else {
				if (isScope(o1) || isScope(o2) || isWindow(o1) || isWindow(o2) ||
					isArray(o2) || isDate(o2) || isRegExp(o2)) return false;
				keySet = createMap();
				for (key in o1) {
					if (key.charAt(0) === '$' || isFunction(o1[key])) continue;
					if (!equals(o1[key], o2[key])) return false;
					keySet[key] = true;
				}
				for (key in o2) {
					if (!(key in keySet) &&
						key.charAt(0) !== '$' &&
						isDefined(o2[key]) &&
						!isFunction(o2[key])) return false;
				}
				return true;
			}
		}
		return false;
	}

	var csp = function () {
		if (!isDefined(csp.rules)) {


			var ngCspElement = (window.document.querySelector('[ng-csp]') ||
				window.document.querySelector('[data-ng-csp]'));

			if (ngCspElement) {
				var ngCspAttribute = ngCspElement.getAttribute('ng-csp') ||
					ngCspElement.getAttribute('data-ng-csp');
				csp.rules = {
					noUnsafeEval: !ngCspAttribute || (ngCspAttribute.indexOf('no-unsafe-eval') !== -1),
					noInlineStyle: !ngCspAttribute || (ngCspAttribute.indexOf('no-inline-style') !== -1)
				};
			} else {
				csp.rules = {
					noUnsafeEval: noUnsafeEval(),
					noInlineStyle: false
				};
			}
		}

		return csp.rules;

		function noUnsafeEval() {
			try {
				// eslint-disable-next-line no-new, no-new-func
				new Function('');
				return false;
			} catch (e) {
				return true;
			}
		}
	};

	/**
	 * @ngdoc directive
	 * @module ng
	 * @name ngJq
	 *
	 * @element ANY
	 * @param {string=} ngJq the name of the library available under `window`
	 * to be used for angular.element
	 * @description
	 * Use this directive to force the angular.element library.  This should be
	 * used to force either jqLite by leaving ng-jq blank or setting the name of
	 * the jquery variable under window (eg. jQuery).
	 *
	 * Since AngularJS looks for this directive when it is loaded (doesn't wait for the
	 * DOMContentLoaded event), it must be placed on an element that comes before the script
	 * which loads angular. Also, only the first instance of `ng-jq` will be used and all
	 * others ignored.
	 *
	 * @example
	 * This example shows how to force jqLite using the `ngJq` directive to the `html` tag.
	 ```html
	 <!doctype html>
	 <html ng-app ng-jq>
	 ...
	 ...
	 </html>
	 ```
	 * @example
	 * This example shows how to use a jQuery based library of a different name.
	 * The library name must be available at the top most 'window'.
	 ```html
	 <!doctype html>
	 <html ng-app ng-jq="jQueryLib">
	 ...
	 ...
	 </html>
	 ```
	 */
	var jq = function () {
		if (isDefined(jq.name_)) return jq.name_;
		var el;
		var i, ii = ngAttrPrefixes.length, prefix, name;
		for (i = 0; i < ii; ++i) {
			prefix = ngAttrPrefixes[i];
			el = window.document.querySelector('[' + prefix.replace(':', '\\:') + 'jq]');
			if (el) {
				name = el.getAttribute(prefix + 'jq');
				break;
			}
		}

		return (jq.name_ = name);
	};

	function concat(array1, array2, index) {
		return array1.concat(slice.call(array2, index));
	}

	function sliceArgs(args, startIndex) {
		return slice.call(args, startIndex || 0);
	}


	/**
	 * @ngdoc function
	 * @name angular.bind
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Returns a function which calls function `fn` bound to `self` (`self` becomes the `this` for
	 * `fn`). You can supply optional `args` that are prebound to the function. This feature is also
	 * known as [partial application](http://en.wikipedia.org/wiki/Partial_application), as
	 * distinguished from [function currying](http://en.wikipedia.org/wiki/Currying#Contrast_with_partial_function_application).
	 *
	 * @param {Object} self Context which `fn` should be evaluated in.
	 * @param {function()} fn Function to be bound.
	 * @param {...*} args Optional arguments to be prebound to the `fn` function call.
	 * @returns {function()} Function that wraps the `fn` with all the specified bindings.
	 */
	function bind(self, fn) {
		var curryArgs = arguments.length > 2 ? sliceArgs(arguments, 2) : [];
		if (isFunction(fn) && !(fn instanceof RegExp)) {
			return curryArgs.length
				? function () {
					return arguments.length
						? fn.apply(self, concat(curryArgs, arguments, 0))
						: fn.apply(self, curryArgs);
				}
				: function () {
					return arguments.length
						? fn.apply(self, arguments)
						: fn.call(self);
				};
		} else {
			// In IE, native methods are not functions so they cannot be bound (note: they don't need to be).
			return fn;
		}
	}


	function toJsonReplacer(key, value) {
		var val = value;

		if (typeof key === 'string' && key.charAt(0) === '$' && key.charAt(1) === '$') {
			val = undefined;
		} else if (isWindow(value)) {
			val = '$WINDOW';
		} else if (value && window.document === value) {
			val = '$DOCUMENT';
		} else if (isScope(value)) {
			val = '$SCOPE';
		}

		return val;
	}


	/**
	 * @ngdoc function
	 * @name angular.toJson
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Serializes input into a JSON-formatted string. Properties with leading $$ characters will be
	 * stripped since AngularJS uses this notation internally.
	 *
	 * @param {Object|Array|Date|string|number|boolean} obj Input to be serialized into JSON.
	 * @param {boolean|number} [pretty=2] If set to true, the JSON output will contain newlines and whitespace.
	 *    If set to an integer, the JSON output will contain that many spaces per indentation.
	 * @returns {string|undefined} JSON-ified string representing `obj`.
	 * @knownIssue
	 *
	 * The Safari browser throws a `RangeError` instead of returning `null` when it tries to stringify a `Date`
	 * object with an invalid date value. The only reliable way to prevent this is to monkeypatch the
	 * `Date.prototype.toJSON` method as follows:
	 *
	 * ```
	 * var _DatetoJSON = Date.prototype.toJSON;
	 * Date.prototype.toJSON = function() {
	 *   try {
	 *     return _DatetoJSON.call(this);
	 *   } catch(e) {
	 *     if (e instanceof RangeError) {
	 *       return null;
	 *     }
	 *     throw e;
	 *   }
	 * };
	 * ```
	 *
	 * See https://github.com/angular/angular.js/pull/14221 for more information.
	 */
	function toJson(obj, pretty) {
		if (isUndefined(obj)) return undefined;
		if (!isNumber(pretty)) {
			pretty = pretty ? 2 : null;
		}
		return JSON.stringify(obj, toJsonReplacer, pretty);
	}


	/**
	 * @ngdoc function
	 * @name angular.fromJson
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Deserializes a JSON string.
	 *
	 * @param {string} json JSON string to deserialize.
	 * @returns {Object|Array|string|number} Deserialized JSON string.
	 */
	function fromJson(json) {
		return isString(json)
			? JSON.parse(json)
			: json;
	}


	var ALL_COLONS = /:/g;
	function timezoneToOffset(timezone, fallback) {
		// Support: IE 9-11 only, Edge 13-15+
		// IE/Edge do not "understand" colon (`:`) in timezone
		timezone = timezone.replace(ALL_COLONS, '');
		var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
		return isNumberNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
	}


	function addDateMinutes(date, minutes) {
		date = new Date(date.getTime());
		date.setMinutes(date.getMinutes() + minutes);
		return date;
	}


	function convertTimezoneToLocal(date, timezone, reverse) {
		reverse = reverse ? -1 : 1;
		var dateTimezoneOffset = date.getTimezoneOffset();
		var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
		return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
	}


	/**
	 * @returns {string} Returns the string representation of the element.
	 */
	function startingTag(element) {
		element = jqLite(element).clone().empty();
		var elemHtml = jqLite('<div></div>').append(element).html();
		try {
			return element[0].nodeType === NODE_TYPE_TEXT ? lowercase(elemHtml) :
				elemHtml.
					match(/^(<[^>]+>)/)[1].
					replace(/^<([\w-]+)/, function (match, nodeName) { return '<' + lowercase(nodeName); });
		} catch (e) {
			return lowercase(elemHtml);
		}

	}


	/////////////////////////////////////////////////

	/**
	 * Tries to decode the URI component without throwing an exception.
	 *
	 * @private
	 * @param str value potential URI component to check.
	 * @returns {boolean} True if `value` can be decoded
	 * with the decodeURIComponent function.
	 */
	function tryDecodeURIComponent(value) {
		try {
			return decodeURIComponent(value);
		} catch (e) {
			// Ignore any invalid uri component.
		}
	}


	/**
	 * Parses an escaped url query string into key-value pairs.
	 * @returns {Object.<string,boolean|Array>}
	 */
	function parseKeyValue(/**string*/keyValue) {
		var obj = {};
		forEach((keyValue || '').split('&'), function (keyValue) {
			var splitPoint, key, val;
			if (keyValue) {
				key = keyValue = keyValue.replace(/\+/g, '%20');
				splitPoint = keyValue.indexOf('=');
				if (splitPoint !== -1) {
					key = keyValue.substring(0, splitPoint);
					val = keyValue.substring(splitPoint + 1);
				}
				key = tryDecodeURIComponent(key);
				if (isDefined(key)) {
					val = isDefined(val) ? tryDecodeURIComponent(val) : true;
					if (!hasOwnProperty.call(obj, key)) {
						obj[key] = val;
					} else if (isArray(obj[key])) {
						obj[key].push(val);
					} else {
						obj[key] = [obj[key], val];
					}
				}
			}
		});
		return obj;
	}

	function toKeyValue(obj) {
		var parts = [];
		forEach(obj, function (value, key) {
			if (isArray(value)) {
				forEach(value, function (arrayValue) {
					parts.push(encodeUriQuery(key, true) +
						(arrayValue === true ? '' : '=' + encodeUriQuery(arrayValue, true)));
				});
			} else {
				parts.push(encodeUriQuery(key, true) +
					(value === true ? '' : '=' + encodeUriQuery(value, true)));
			}
		});
		return parts.length ? parts.join('&') : '';
	}


	/**
	 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
	 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
	 * segments:
	 *    segment       = *pchar
	 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
	 *    pct-encoded   = "%" HEXDIG HEXDIG
	 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
	 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
	 *                     / "*" / "+" / "," / ";" / "="
	 */
	function encodeUriSegment(val) {
		return encodeUriQuery(val, true).
			replace(/%26/gi, '&').
			replace(/%3D/gi, '=').
			replace(/%2B/gi, '+');
	}


	/**
	 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
	 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
	 * encoded per http://tools.ietf.org/html/rfc3986:
	 *    query         = *( pchar / "/" / "?" )
	 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
	 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
	 *    pct-encoded   = "%" HEXDIG HEXDIG
	 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
	 *                     / "*" / "+" / "," / ";" / "="
	 */
	function encodeUriQuery(val, pctEncodeSpaces) {
		return encodeURIComponent(val).
			replace(/%40/gi, '@').
			replace(/%3A/gi, ':').
			replace(/%24/g, '$').
			replace(/%2C/gi, ',').
			replace(/%3B/gi, ';').
			replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
	}

	var ngAttrPrefixes = ['ng-', 'data-ng-', 'ng:', 'x-ng-'];

	function getNgAttribute(element, ngAttr) {
		var attr, i, ii = ngAttrPrefixes.length;
		for (i = 0; i < ii; ++i) {
			attr = ngAttrPrefixes[i] + ngAttr;
			if (isString(attr = element.getAttribute(attr))) {
				return attr;
			}
		}
		return null;
	}

	function allowAutoBootstrap(document) {
		var script = document.currentScript;

		if (!script) {
			// Support: IE 9-11 only
			// IE does not have `document.currentScript`
			return true;
		}

		// If the `currentScript` property has been clobbered just return false, since this indicates a probable attack
		if (!(script instanceof window.HTMLScriptElement || script instanceof window.SVGScriptElement)) {
			return false;
		}

		var attributes = script.attributes;
		var srcs = [attributes.getNamedItem('src'), attributes.getNamedItem('href'), attributes.getNamedItem('xlink:href')];

		return srcs.every(function (src) {
			if (!src) {
				return true;
			}
			if (!src.value) {
				return false;
			}

			var link = document.createElement('a');
			link.href = src.value;

			if (document.location.origin === link.origin) {
				// Same-origin resources are always allowed, even for banned URL schemes.
				return true;
			}
			// Disabled bootstrapping unless angular.js was loaded from a known scheme used on the web.
			// This is to prevent angular.js bundled with browser extensions from being used to bypass the
			// content security policy in web pages and other browser extensions.
			switch (link.protocol) {
				case 'http:':
				case 'https:':
				case 'ftp:':
				case 'blob:':
				case 'file:':
				case 'data:':
					return true;
				default:
					return false;
			}
		});
	}

	// Cached as it has to run during loading so that document.currentScript is available.
	var isAutoBootstrapAllowed = allowAutoBootstrap(window.document);

	/**
	 * @ngdoc directive
	 * @name ngApp
	 * @module ng
	 *
	 * @element ANY
	 * @param {angular.Module} ngApp an optional application
	 *   {@link angular.module module} name to load.
	 * @param {boolean=} ngStrictDi if this attribute is present on the app element, the injector will be
	 *   created in "strict-di" mode. This means that the application will fail to invoke functions which
	 *   do not use explicit function annotation (and are thus unsuitable for minification), as described
	 *   in {@link guide/di the Dependency Injection guide}, and useful debugging info will assist in
	 *   tracking down the root of these bugs.
	 *
	 * @description
	 *
	 * Use this directive to **auto-bootstrap** an AngularJS application. The `ngApp` directive
	 * designates the **root element** of the application and is typically placed near the root element
	 * of the page - e.g. on the `<body>` or `<html>` tags.
	 *
	 * There are a few things to keep in mind when using `ngApp`:
	 * - only one AngularJS application can be auto-bootstrapped per HTML document. The first `ngApp`
	 *   found in the document will be used to define the root element to auto-bootstrap as an
	 *   application. To run multiple applications in an HTML document you must manually bootstrap them using
	 *   {@link angular.bootstrap} instead.
	 * - AngularJS applications cannot be nested within each other.
	 * - Do not use a directive that uses {@link ng.$compile#transclusion transclusion} on the same element as `ngApp`.
	 *   This includes directives such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and
	 *   {@link ngRoute.ngView `ngView`}.
	 *   Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
	 *   causing animations to stop working and making the injector inaccessible from outside the app.
	 *
	 * You can specify an **AngularJS module** to be used as the root module for the application.  This
	 * module will be loaded into the {@link auto.$injector} when the application is bootstrapped. It
	 * should contain the application code needed or have dependencies on other modules that will
	 * contain the code. See {@link angular.module} for more information.
	 *
	 * In the example below if the `ngApp` directive were not placed on the `html` element then the
	 * document would not be compiled, the `AppController` would not be instantiated and the `{{ a+b }}`
	 * would not be resolved to `3`.
	 *
	 * @example
	 *
	 * ### Simple Usage
	 *
	 * `ngApp` is the easiest, and most common way to bootstrap an application.
	 *
	 <example module="ngAppDemo" name="ng-app">
	   <file name="index.html">
	   <div ng-controller="ngAppDemoController">
		 I can add: {{a}} + {{b}} =  {{ a+b }}
	   </div>
	   </file>
	   <file name="script.js">
	   angular.module('ngAppDemo', []).controller('ngAppDemoController', function($scope) {
		 $scope.a = 1;
		 $scope.b = 2;
	   });
	   </file>
	 </example>
	 *
	 * @example
	 *
	 * ### With `ngStrictDi`
	 *
	 * Using `ngStrictDi`, you would see something like this:
	 *
	 <example ng-app-included="true" name="strict-di">
	   <file name="index.html">
	   <div ng-app="ngAppStrictDemo" ng-strict-di>
		   <div ng-controller="GoodController1">
			   I can add: {{a}} + {{b}} =  {{ a+b }}
	
			   <p>This renders because the controller does not fail to
				  instantiate, by using explicit annotation style (see
				  script.js for details)
			   </p>
		   </div>
	
		   <div ng-controller="GoodController2">
			   Name: <input ng-model="name"><br />
			   Hello, {{name}}!
	
			   <p>This renders because the controller does not fail to
				  instantiate, by using explicit annotation style
				  (see script.js for details)
			   </p>
		   </div>
	
		   <div ng-controller="BadController">
			   I can add: {{a}} + {{b}} =  {{ a+b }}
	
			   <p>The controller could not be instantiated, due to relying
				  on automatic function annotations (which are disabled in
				  strict mode). As such, the content of this section is not
				  interpolated, and there should be an error in your web console.
			   </p>
		   </div>
	   </div>
	   </file>
	   <file name="script.js">
	   angular.module('ngAppStrictDemo', [])
		 // BadController will fail to instantiate, due to relying on automatic function annotation,
		 // rather than an explicit annotation
		 .controller('BadController', function($scope) {
		   $scope.a = 1;
		   $scope.b = 2;
		 })
		 // Unlike BadController, GoodController1 and GoodController2 will not fail to be instantiated,
		 // due to using explicit annotations using the array style and $inject property, respectively.
		 .controller('GoodController1', ['$scope', function($scope) {
		   $scope.a = 1;
		   $scope.b = 2;
		 }])
		 .controller('GoodController2', GoodController2);
		 function GoodController2($scope) {
		   $scope.name = 'World';
		 }
		 GoodController2.$inject = ['$scope'];
	   </file>
	   <file name="style.css">
	   div[ng-controller] {
		   margin-bottom: 1em;
		   -webkit-border-radius: 4px;
		   border-radius: 4px;
		   border: 1px solid;
		   padding: .5em;
	   }
	   div[ng-controller^=Good] {
		   border-color: #d6e9c6;
		   background-color: #dff0d8;
		   color: #3c763d;
	   }
	   div[ng-controller^=Bad] {
		   border-color: #ebccd1;
		   background-color: #f2dede;
		   color: #a94442;
		   margin-bottom: 0;
	   }
	   </file>
	 </example>
	 */
	function angularInit(element, bootstrap) {
		var appElement,
			module,
			config = {};

		// The element `element` has priority over any other element.
		forEach(ngAttrPrefixes, function (prefix) {
			var name = prefix + 'app';

			if (!appElement && element.hasAttribute && element.hasAttribute(name)) {
				appElement = element;
				module = element.getAttribute(name);
			}
		});
		forEach(ngAttrPrefixes, function (prefix) {
			var name = prefix + 'app';
			var candidate;

			if (!appElement && (candidate = element.querySelector('[' + name.replace(':', '\\:') + ']'))) {
				appElement = candidate;
				module = candidate.getAttribute(name);
			}
		});
		if (appElement) {
			if (!isAutoBootstrapAllowed) {
				window.console.error('AngularJS: disabling automatic bootstrap. <script> protocol indicates ' +
					'an extension, document.location.href does not match.');
				return;
			}
			config.strictDi = getNgAttribute(appElement, 'strict-di') !== null;
			bootstrap(appElement, module ? [module] : [], config);
		}
	}

	/**
	 * @ngdoc function
	 * @name angular.bootstrap
	 * @module ng
	 * @description
	 * Use this function to manually start up AngularJS application.
	 *
	 * For more information, see the {@link guide/bootstrap Bootstrap guide}.
	 *
	 * AngularJS will detect if it has been loaded into the browser more than once and only allow the
	 * first loaded script to be bootstrapped and will report a warning to the browser console for
	 * each of the subsequent scripts. This prevents strange results in applications, where otherwise
	 * multiple instances of AngularJS try to work on the DOM.
	 *
	 * <div class="alert alert-warning">
	 * **Note:** Protractor based end-to-end tests cannot use this function to bootstrap manually.
	 * They must use {@link ng.directive:ngApp ngApp}.
	 * </div>
	 *
	 * <div class="alert alert-warning">
	 * **Note:** Do not bootstrap the app on an element with a directive that uses {@link ng.$compile#transclusion transclusion},
	 * such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and {@link ngRoute.ngView `ngView`}.
	 * Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
	 * causing animations to stop working and making the injector inaccessible from outside the app.
	 * </div>
	 *
	 * ```html
	 * <!doctype html>
	 * <html>
	 * <body>
	 * <div ng-controller="WelcomeController">
	 *   {{greeting}}
	 * </div>
	 *
	 * <script src="angular.js"></script>
	 * <script>
	 *   var app = angular.module('demo', [])
	 *   .controller('WelcomeController', function($scope) {
	 *       $scope.greeting = 'Welcome!';
	 *   });
	 *   angular.bootstrap(document, ['demo']);
	 * </script>
	 * </body>
	 * </html>
	 * ```
	 *
	 * @param {DOMElement} element DOM element which is the root of AngularJS application.
	 * @param {Array<String|Function|Array>=} modules an array of modules to load into the application.
	 *     Each item in the array should be the name of a predefined module or a (DI annotated)
	 *     function that will be invoked by the injector as a `config` block.
	 *     See: {@link angular.module modules}
	 * @param {Object=} config an object for defining configuration options for the application. The
	 *     following keys are supported:
	 *
	 * * `strictDi` - disable automatic function annotation for the application. This is meant to
	 *   assist in finding bugs which break minified code. Defaults to `false`.
	 *
	 * @returns {auto.$injector} Returns the newly created injector for this app.
	 */
	function bootstrap(element, modules, config) {
		if (!isObject(config)) config = {};
		var defaultConfig = {
			strictDi: false
		};
		config = extend(defaultConfig, config);
		var doBootstrap = function () {
			element = jqLite(element);

			if (element.injector()) {
				var tag = (element[0] === window.document) ? 'document' : startingTag(element);
				// Encode angle brackets to prevent input from being sanitized to empty string #8683.
				throw ngMinErr(
					'btstrpd',
					'App already bootstrapped with this element \'{0}\'',
					tag.replace(/</, '&lt;').replace(/>/, '&gt;'));
			}

			modules = modules || [];
			modules.unshift(['$provide', function ($provide) {
				$provide.value('$rootElement', element);
			}]);

			if (config.debugInfoEnabled) {
				// Pushing so that this overrides `debugInfoEnabled` setting defined in user's `modules`.
				modules.push(['$compileProvider', function ($compileProvider) {
					$compileProvider.debugInfoEnabled(true);
				}]);
			}

			modules.unshift('ng');
			var injector = createInjector(modules, config.strictDi);
			injector.invoke(['$rootScope', '$rootElement', '$compile', '$injector',
				function bootstrapApply(scope, element, compile, injector) {
					scope.$apply(function () {
						element.data('$injector', injector);
						compile(element)(scope);
					});
				}]
			);
			return injector;
		};

		var NG_ENABLE_DEBUG_INFO = /^NG_ENABLE_DEBUG_INFO!/;
		var NG_DEFER_BOOTSTRAP = /^NG_DEFER_BOOTSTRAP!/;

		if (window && NG_ENABLE_DEBUG_INFO.test(window.name)) {
			config.debugInfoEnabled = true;
			window.name = window.name.replace(NG_ENABLE_DEBUG_INFO, '');
		}

		if (window && !NG_DEFER_BOOTSTRAP.test(window.name)) {
			return doBootstrap();
		}

		window.name = window.name.replace(NG_DEFER_BOOTSTRAP, '');
		angular.resumeBootstrap = function (extraModules) {
			forEach(extraModules, function (module) {
				modules.push(module);
			});
			return doBootstrap();
		};

		if (isFunction(angular.resumeDeferredBootstrap)) {
			angular.resumeDeferredBootstrap();
		}
	}

	/**
	 * @ngdoc function
	 * @name angular.reloadWithDebugInfo
	 * @module ng
	 * @description
	 * Use this function to reload the current application with debug information turned on.
	 * This takes precedence over a call to `$compileProvider.debugInfoEnabled(false)`.
	 *
	 * See {@link ng.$compileProvider#debugInfoEnabled} for more.
	 */
	function reloadWithDebugInfo() {
		window.name = 'NG_ENABLE_DEBUG_INFO!' + window.name;
		window.location.reload();
	}

	/**
	 * @name angular.getTestability
	 * @module ng
	 * @description
	 * Get the testability service for the instance of AngularJS on the given
	 * element.
	 * @param {DOMElement} element DOM element which is the root of AngularJS application.
	 */
	function getTestability(rootElement) {
		var injector = angular.element(rootElement).injector();
		if (!injector) {
			throw ngMinErr('test',
				'no injector found for element argument to getTestability');
		}
		return injector.get('$$testability');
	}

	var SNAKE_CASE_REGEXP = /[A-Z]/g;
	function snake_case(name, separator) {
		separator = separator || '_';
		return name.replace(SNAKE_CASE_REGEXP, function (letter, pos) {
			return (pos ? separator : '') + letter.toLowerCase();
		});
	}

	var bindJQueryFired = false;
	function bindJQuery() {
		var originalCleanData;

		if (bindJQueryFired) {
			return;
		}

		// bind to jQuery if present;
		var jqName = jq();
		jQuery = isUndefined(jqName) ? window.jQuery :   // use jQuery (if present)
			!jqName ? undefined :   // use jqLite
				window[jqName];   // use jQuery specified by `ngJq`

		// Use jQuery if it exists with proper functionality, otherwise default to us.
		// AngularJS 1.2+ requires jQuery 1.7+ for on()/off() support.
		// AngularJS 1.3+ technically requires at least jQuery 2.1+ but it may work with older
		// versions. It will not work for sure with jQuery <1.7, though.
		if (jQuery && jQuery.fn.on) {
			jqLite = jQuery;
			extend(jQuery.fn, {
				scope: JQLitePrototype.scope,
				isolateScope: JQLitePrototype.isolateScope,
				controller: /** @type {?} */ (JQLitePrototype).controller,
				injector: JQLitePrototype.injector,
				inheritedData: JQLitePrototype.inheritedData
			});
		} else {
			jqLite = JQLite;
		}

		// All nodes removed from the DOM via various jqLite/jQuery APIs like .remove()
		// are passed through jqLite/jQuery.cleanData. Monkey-patch this method to fire
		// the $destroy event on all removed nodes.
		originalCleanData = jqLite.cleanData;
		jqLite.cleanData = function (elems) {
			var events;
			for (var i = 0, elem; (elem = elems[i]) != null; i++) {
				events = (jqLite._data(elem) || {}).events;
				if (events && events.$destroy) {
					jqLite(elem).triggerHandler('$destroy');
				}
			}
			originalCleanData(elems);
		};

		angular.element = jqLite;

		// Prevent double-proxying.
		bindJQueryFired = true;
	}

	/**
	 * @ngdoc function
	 * @name angular.UNSAFE_restoreLegacyJqLiteXHTMLReplacement
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Restores the pre-1.8 behavior of jqLite that turns XHTML-like strings like
	 * `<div /><span />` to `<div></div><span></span>` instead of `<div><span></span></div>`.
	 * The new behavior is a security fix. Thus, if you need to call this function, please try to adjust
	 * your code for this change and remove your use of this function as soon as possible.
	
	 * Note that this only patches jqLite. If you use jQuery 3.5.0 or newer, please read the
	 * [jQuery 3.5 upgrade guide](https://jquery.com/upgrade-guide/3.5/) for more details
	 * about the workarounds.
	 */
	function UNSAFE_restoreLegacyJqLiteXHTMLReplacement() {
		JQLite.legacyXHTMLReplacement = true;
	}

	/**
	 * throw error if the argument is falsy.
	 */
	function assertArg(arg, name, reason) {
		if (!arg) {
			throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
		}
		return arg;
	}

	function assertArgFn(arg, name, acceptArrayAnnotation) {
		if (acceptArrayAnnotation && isArray(arg)) {
			arg = arg[arg.length - 1];
		}

		assertArg(isFunction(arg), name, 'not a function, got ' +
			(arg && typeof arg === 'object' ? arg.constructor.name || 'Object' : typeof arg));
		return arg;
	}

	/**
	 * throw error if the name given is hasOwnProperty
	 * @param  {String} name    the name to test
	 * @param  {String} context the context in which the name is used, such as module or directive
	 */
	function assertNotHasOwnProperty(name, context) {
		if (name === 'hasOwnProperty') {
			throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
		}
	}

	/**
	 * Return the value accessible from the object by path. Any undefined traversals are ignored
	 * @param {Object} obj starting object
	 * @param {String} path path to traverse
	 * @param {boolean} [bindFnToScope=true]
	 * @returns {Object} value as accessible by path
	 */
	//TODO(misko): this function needs to be removed
	function getter(obj, path, bindFnToScope) {
		if (!path) return obj;
		var keys = path.split('.');
		var key;
		var lastInstance = obj;
		var len = keys.length;

		for (var i = 0; i < len; i++) {
			key = keys[i];
			if (obj) {
				obj = (lastInstance = obj)[key];
			}
		}
		if (!bindFnToScope && isFunction(obj)) {
			return bind(lastInstance, obj);
		}
		return obj;
	}

	/**
	 * Return the DOM siblings between the first and last node in the given array.
	 * @param {Array} array like object
	 * @returns {Array} the inputted object or a jqLite collection containing the nodes
	 */
	function getBlockNodes(nodes) {
		// TODO(perf): update `nodes` instead of creating a new object?
		var node = nodes[0];
		var endNode = nodes[nodes.length - 1];
		var blockNodes;

		for (var i = 1; node !== endNode && (node = node.nextSibling); i++) {
			if (blockNodes || nodes[i] !== node) {
				if (!blockNodes) {
					blockNodes = jqLite(slice.call(nodes, 0, i));
				}
				blockNodes.push(node);
			}
		}

		return blockNodes || nodes;
	}


	/**
	 * Creates a new object without a prototype. This object is useful for lookup without having to
	 * guard against prototypically inherited properties via hasOwnProperty.
	 *
	 * Related micro-benchmarks:
	 * - http://jsperf.com/object-create2
	 * - http://jsperf.com/proto-map-lookup/2
	 * - http://jsperf.com/for-in-vs-object-keys2
	 *
	 * @returns {Object}
	 */
	function createMap() {
		return Object.create(null);
	}

	function stringify(value) {
		if (value == null) { // null || undefined
			return '';
		}
		switch (typeof value) {
			case 'string':
				break;
			case 'number':
				value = '' + value;
				break;
			default:
				if (hasCustomToString(value) && !isArray(value) && !isDate(value)) {
					value = value.toString();
				} else {
					value = toJson(value);
				}
		}

		return value;
	}

	var NODE_TYPE_ELEMENT = 1;
	var NODE_TYPE_ATTRIBUTE = 2;
	var NODE_TYPE_TEXT = 3;
	var NODE_TYPE_COMMENT = 8;
	var NODE_TYPE_DOCUMENT = 9;
	var NODE_TYPE_DOCUMENT_FRAGMENT = 11;

	/**
	 * @ngdoc type
	 * @name angular.Module
	 * @module ng
	 * @description
	 *
	 * Interface for configuring AngularJS {@link angular.module modules}.
	 */

	function setupModuleLoader(window) {

		var $injectorMinErr = minErr('$injector');
		var ngMinErr = minErr('ng');

		function ensure(obj, name, factory) {
			return obj[name] || (obj[name] = factory());
		}

		var angular = ensure(window, 'angular', Object);

		// We need to expose `angular.$$minErr` to modules such as `ngResource` that reference it during bootstrap
		angular.$$minErr = angular.$$minErr || minErr;

		return ensure(angular, 'module', function () {
			/** @type {Object.<string, angular.Module>} */
			var modules = {};

			/**
			 * @ngdoc function
			 * @name angular.module
			 * @module ng
			 * @description
			 *
			 * The `angular.module` is a global place for creating, registering and retrieving AngularJS
			 * modules.
			 * All modules (AngularJS core or 3rd party) that should be available to an application must be
			 * registered using this mechanism.
			 *
			 * Passing one argument retrieves an existing {@link angular.Module},
			 * whereas passing more than one argument creates a new {@link angular.Module}
			 *
			 *
			 * # Module
			 *
			 * A module is a collection of services, directives, controllers, filters, and configuration information.
			 * `angular.module` is used to configure the {@link auto.$injector $injector}.
			 *
			 * ```js
			 * // Create a new module
			 * var myModule = angular.module('myModule', []);
			 *
			 * // register a new service
			 * myModule.value('appName', 'MyCoolApp');
			 *
			 * // configure existing services inside initialization blocks.
			 * myModule.config(['$locationProvider', function($locationProvider) {
			 *   // Configure existing providers
			 *   $locationProvider.hashPrefix('!');
			 * }]);
			 * ```
			 *
			 * Then you can create an injector and load your modules like this:
			 *
			 * ```js
			 * var injector = angular.injector(['ng', 'myModule'])
			 * ```
			 *
			 * However it's more likely that you'll just use
			 * {@link ng.directive:ngApp ngApp} or
			 * {@link angular.bootstrap} to simplify this process for you.
			 *
			 * @param {!string} name The name of the module to create or retrieve.
			 * @param {!Array.<string>=} requires If specified then new module is being created. If
			 *        unspecified then the module is being retrieved for further configuration.
			 * @param {Function=} configFn Optional configuration function for the module. Same as
			 *        {@link angular.Module#config Module#config()}.
			 * @returns {angular.Module} new module with the {@link angular.Module} api.
			 */
			return function module(name, requires, configFn) {

				var info = {};

				var assertNotHasOwnProperty = function (name, context) {
					if (name === 'hasOwnProperty') {
						throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
					}
				};

				assertNotHasOwnProperty(name, 'module');
				if (requires && modules.hasOwnProperty(name)) {
					modules[name] = null;
				}
				return ensure(modules, name, function () {
					if (!requires) {
						throw $injectorMinErr('nomod', 'Module \'{0}\' is not available! You either misspelled ' +
							'the module name or forgot to load it. If registering a module ensure that you ' +
							'specify the dependencies as the second argument.', name);
					}

					/** @type {!Array.<Array.<*>>} */
					var invokeQueue = [];

					/** @type {!Array.<Function>} */
					var configBlocks = [];

					/** @type {!Array.<Function>} */
					var runBlocks = [];

					var config = invokeLater('$injector', 'invoke', 'push', configBlocks);

					/** @type {angular.Module} */
					var moduleInstance = {
						// Private state
						_invokeQueue: invokeQueue,
						_configBlocks: configBlocks,
						_runBlocks: runBlocks,

						/**
						 * @ngdoc method
						 * @name angular.Module#info
						 * @module ng
						 *
						 * @param {Object=} info Information about the module
						 * @returns {Object|Module} The current info object for this module if called as a getter,
						 *                          or `this` if called as a setter.
						 *
						 * @description
						 * Read and write custom information about this module.
						 * For example you could put the version of the module in here.
						 *
						 * ```js
						 * angular.module('myModule', []).info({ version: '1.0.0' });
						 * ```
						 *
						 * The version could then be read back out by accessing the module elsewhere:
						 *
						 * ```
						 * var version = angular.module('myModule').info().version;
						 * ```
						 *
						 * You can also retrieve this information during runtime via the
						 * {@link $injector#modules `$injector.modules`} property:
						 *
						 * ```js
						 * var version = $injector.modules['myModule'].info().version;
						 * ```
						 */
						info: function (value) {
							if (isDefined(value)) {
								if (!isObject(value)) throw ngMinErr('aobj', 'Argument \'{0}\' must be an object', 'value');
								info = value;
								return this;
							}
							return info;
						},

						/**
						 * @ngdoc property
						 * @name angular.Module#requires
						 * @module ng
						 *
						 * @description
						 * Holds the list of modules which the injector will load before the current module is
						 * loaded.
						 */
						requires: requires,

						/**
						 * @ngdoc property
						 * @name angular.Module#name
						 * @module ng
						 *
						 * @description
						 * Name of the module.
						 */
						name: name,


						/**
						 * @ngdoc method
						 * @name angular.Module#provider
						 * @module ng
						 * @param {string} name service name
						 * @param {Function} providerType Construction function for creating new instance of the
						 *                                service.
						 * @description
						 * See {@link auto.$provide#provider $provide.provider()}.
						 */
						provider: invokeLaterAndSetModuleName('$provide', 'provider'),

						/**
						 * @ngdoc method
						 * @name angular.Module#factory
						 * @module ng
						 * @param {string} name service name
						 * @param {Function} providerFunction Function for creating new instance of the service.
						 * @description
						 * See {@link auto.$provide#factory $provide.factory()}.
						 */
						factory: invokeLaterAndSetModuleName('$provide', 'factory'),

						/**
						 * @ngdoc method
						 * @name angular.Module#service
						 * @module ng
						 * @param {string} name service name
						 * @param {Function} constructor A constructor function that will be instantiated.
						 * @description
						 * See {@link auto.$provide#service $provide.service()}.
						 */
						service: invokeLaterAndSetModuleName('$provide', 'service'),

						/**
						 * @ngdoc method
						 * @name angular.Module#value
						 * @module ng
						 * @param {string} name service name
						 * @param {*} object Service instance object.
						 * @description
						 * See {@link auto.$provide#value $provide.value()}.
						 */
						value: invokeLater('$provide', 'value'),

						/**
						 * @ngdoc method
						 * @name angular.Module#constant
						 * @module ng
						 * @param {string} name constant name
						 * @param {*} object Constant value.
						 * @description
						 * Because the constants are fixed, they get applied before other provide methods.
						 * See {@link auto.$provide#constant $provide.constant()}.
						 */
						constant: invokeLater('$provide', 'constant', 'unshift'),

						/**
						* @ngdoc method
						* @name angular.Module#decorator
						* @module ng
						* @param {string} name The name of the service to decorate.
						* @param {Function} decorFn This function will be invoked when the service needs to be
						*                           instantiated and should return the decorated service instance.
						* @description
						* See {@link auto.$provide#decorator $provide.decorator()}.
						*/
						decorator: invokeLaterAndSetModuleName('$provide', 'decorator', configBlocks),

						/**
						 * @ngdoc method
						 * @name angular.Module#animation
						 * @module ng
						 * @param {string} name animation name
						 * @param {Function} animationFactory Factory function for creating new instance of an
						 *                                    animation.
						 * @description
						 *
						 * **NOTE**: animations take effect only if the **ngAnimate** module is loaded.
						 *
						 *
						 * Defines an animation hook that can be later used with
						 * {@link $animate $animate} service and directives that use this service.
						 *
						 * ```js
						 * module.animation('.animation-name', function($inject1, $inject2) {
						 *   return {
						 *     eventName : function(element, done) {
						 *       //code to run the animation
						 *       //once complete, then run done()
						 *       return function cancellationFunction(element) {
						 *         //code to cancel the animation
						 *       }
						 *     }
						 *   }
						 * })
						 * ```
						 *
						 * See {@link ng.$animateProvider#register $animateProvider.register()} and
						 * {@link ngAnimate ngAnimate module} for more information.
						 */
						animation: invokeLaterAndSetModuleName('$animateProvider', 'register'),

						/**
						 * @ngdoc method
						 * @name angular.Module#filter
						 * @module ng
						 * @param {string} name Filter name - this must be a valid AngularJS expression identifier
						 * @param {Function} filterFactory Factory function for creating new instance of filter.
						 * @description
						 * See {@link ng.$filterProvider#register $filterProvider.register()}.
						 *
						 * <div class="alert alert-warning">
						 * **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
						 * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
						 * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
						 * (`myapp_subsection_filterx`).
						 * </div>
						 */
						filter: invokeLaterAndSetModuleName('$filterProvider', 'register'),

						/**
						 * @ngdoc method
						 * @name angular.Module#controller
						 * @module ng
						 * @param {string|Object} name Controller name, or an object map of controllers where the
						 *    keys are the names and the values are the constructors.
						 * @param {Function} constructor Controller constructor function.
						 * @description
						 * See {@link ng.$controllerProvider#register $controllerProvider.register()}.
						 */
						controller: invokeLaterAndSetModuleName('$controllerProvider', 'register'),

						/**
						 * @ngdoc method
						 * @name angular.Module#directive
						 * @module ng
						 * @param {string|Object} name Directive name, or an object map of directives where the
						 *    keys are the names and the values are the factories.
						 * @param {Function} directiveFactory Factory function for creating new instance of
						 * directives.
						 * @description
						 * See {@link ng.$compileProvider#directive $compileProvider.directive()}.
						 */
						directive: invokeLaterAndSetModuleName('$compileProvider', 'directive'),

						/**
						 * @ngdoc method
						 * @name angular.Module#component
						 * @module ng
						 * @param {string|Object} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`),
						 *    or an object map of components where the keys are the names and the values are the component definition objects.
						 * @param {Object} options Component definition object (a simplified
						 *    {@link ng.$compile#directive-definition-object directive definition object})
						 *
						 * @description
						 * See {@link ng.$compileProvider#component $compileProvider.component()}.
						 */
						component: invokeLaterAndSetModuleName('$compileProvider', 'component'),

						/**
						 * @ngdoc method
						 * @name angular.Module#config
						 * @module ng
						 * @param {Function} configFn Execute this function on module load. Useful for service
						 *    configuration.
						 * @description
						 * Use this method to configure services by injecting their
						 * {@link angular.Module#provider `providers`}, e.g. for adding routes to the
						 * {@link ngRoute.$routeProvider $routeProvider}.
						 *
						 * Note that you can only inject {@link angular.Module#provider `providers`} and
						 * {@link angular.Module#constant `constants`} into this function.
						 *
						 * For more about how to configure services, see
						 * {@link providers#provider-recipe Provider Recipe}.
						 */
						config: config,

						/**
						 * @ngdoc method
						 * @name angular.Module#run
						 * @module ng
						 * @param {Function} initializationFn Execute this function after injector creation.
						 *    Useful for application initialization.
						 * @description
						 * Use this method to register work which should be performed when the injector is done
						 * loading all modules.
						 */
						run: function (block) {
							runBlocks.push(block);
							return this;
						}
					};

					if (configFn) {
						config(configFn);
					}

					return moduleInstance;

					/**
					 * @param {string} provider
					 * @param {string} method
					 * @param {String=} insertMethod
					 * @returns {angular.Module}
					 */
					function invokeLater(provider, method, insertMethod, queue) {
						if (!queue) queue = invokeQueue;
						return function () {
							queue[insertMethod || 'push']([provider, method, arguments]);
							return moduleInstance;
						};
					}

					/**
					 * @param {string} provider
					 * @param {string} method
					 * @returns {angular.Module}
					 */
					function invokeLaterAndSetModuleName(provider, method, queue) {
						if (!queue) queue = invokeQueue;
						return function (recipeName, factoryFunction) {
							if (factoryFunction && isFunction(factoryFunction)) factoryFunction.$$moduleName = name;
							queue.push([provider, method, arguments]);
							return moduleInstance;
						};
					}
				});
			};
		});

	}

	/* global shallowCopy: true */

	/**
	 * Creates a shallow copy of an object, an array or a primitive.
	 *
	 * Assumes that there are no proto properties for objects.
	 */
	function shallowCopy(src, dst) {
		if (isArray(src)) {
			dst = dst || [];

			for (var i = 0, ii = src.length; i < ii; i++) {
				dst[i] = src[i];
			}
		} else if (isObject(src)) {
			dst = dst || {};

			for (var key in src) {
				if (!(key.charAt(0) === '$' && key.charAt(1) === '$')) {
					dst[key] = src[key];
				}
			}
		}

		return dst || src;
	}

	/* exported toDebugString */

	function serializeObject(obj, maxDepth) {
		var seen = [];

		// There is no direct way to stringify object until reaching a specific depth
		// and a very deep object can cause a performance issue, so we copy the object
		// based on this specific depth and then stringify it.
		if (isValidObjectMaxDepth(maxDepth)) {
			// This file is also included in `angular-loader`, so `copy()` might not always be available in
			// the closure. Therefore, it is lazily retrieved as `angular.copy()` when needed.
			obj = angular.copy(obj, null, maxDepth);
		}
		return JSON.stringify(obj, function (key, val) {
			val = toJsonReplacer(key, val);
			if (isObject(val)) {

				if (seen.indexOf(val) >= 0) return '...';

				seen.push(val);
			}
			return val;
		});
	}

	function toDebugString(obj, maxDepth) {
		if (typeof obj === 'function') {
			return obj.toString().replace(/ \{[\s\S]*$/, '');
		} else if (isUndefined(obj)) {
			return 'undefined';
		} else if (typeof obj !== 'string') {
			return serializeObject(obj, maxDepth);
		}
		return obj;
	}

	/* global angularModule: true,
	  version: true,
	
	  $CompileProvider,
	
	  htmlAnchorDirective,
	  inputDirective,
	  hiddenInputBrowserCacheDirective,
	  formDirective,
	  scriptDirective,
	  selectDirective,
	  optionDirective,
	  ngBindDirective,
	  ngBindHtmlDirective,
	  ngBindTemplateDirective,
	  ngClassDirective,
	  ngClassEvenDirective,
	  ngClassOddDirective,
	  ngCloakDirective,
	  ngControllerDirective,
	  ngFormDirective,
	  ngHideDirective,
	  ngIfDirective,
	  ngIncludeDirective,
	  ngIncludeFillContentDirective,
	  ngInitDirective,
	  ngNonBindableDirective,
	  ngPluralizeDirective,
	  ngRefDirective,
	  ngRepeatDirective,
	  ngShowDirective,
	  ngStyleDirective,
	  ngSwitchDirective,
	  ngSwitchWhenDirective,
	  ngSwitchDefaultDirective,
	  ngOptionsDirective,
	  ngTranscludeDirective,
	  ngModelDirective,
	  ngListDirective,
	  ngChangeDirective,
	  patternDirective,
	  patternDirective,
	  requiredDirective,
	  requiredDirective,
	  minlengthDirective,
	  minlengthDirective,
	  maxlengthDirective,
	  maxlengthDirective,
	  ngValueDirective,
	  ngModelOptionsDirective,
	  ngAttributeAliasDirectives,
	  ngEventDirectives,
	
	  $AnchorScrollProvider,
	  $AnimateProvider,
	  $CoreAnimateCssProvider,
	  $$CoreAnimateJsProvider,
	  $$CoreAnimateQueueProvider,
	  $$AnimateRunnerFactoryProvider,
	  $$AnimateAsyncRunFactoryProvider,
	  $BrowserProvider,
	  $CacheFactoryProvider,
	  $ControllerProvider,
	  $DateProvider,
	  $DocumentProvider,
	  $$IsDocumentHiddenProvider,
	  $ExceptionHandlerProvider,
	  $FilterProvider,
	  $$ForceReflowProvider,
	  $InterpolateProvider,
	  $$IntervalFactoryProvider,
	  $IntervalProvider,
	  $HttpProvider,
	  $HttpParamSerializerProvider,
	  $HttpParamSerializerJQLikeProvider,
	  $HttpBackendProvider,
	  $xhrFactoryProvider,
	  $jsonpCallbacksProvider,
	  $LocationProvider,
	  $LogProvider,
	  $$MapProvider,
	  $ParseProvider,
	  $RootScopeProvider,
	  $QProvider,
	  $$QProvider,
	  $$SanitizeUriProvider,
	  $SceProvider,
	  $SceDelegateProvider,
	  $SnifferProvider,
	  $$TaskTrackerFactoryProvider,
	  $TemplateCacheProvider,
	  $TemplateRequestProvider,
	  $$TestabilityProvider,
	  $TimeoutProvider,
	  $$RAFProvider,
	  $WindowProvider,
	  $$jqLiteProvider,
	  $$CookieReaderProvider
	*/


	/**
	 * @ngdoc object
	 * @name angular.version
	 * @module ng
	 * @description
	 * An object that contains information about the current AngularJS version.
	 *
	 * This object has the following properties:
	 *
	 * - `full` – `{string}` – Full version string, such as "0.9.18".
	 * - `major` – `{number}` – Major version number, such as "0".
	 * - `minor` – `{number}` – Minor version number, such as "9".
	 * - `dot` – `{number}` – Dot version number, such as "18".
	 * - `codeName` – `{string}` – Code name of the release, such as "jiggling-armfat".
	 */
	var version = {
		// These placeholder strings will be replaced by grunt's `build` task.
		// They need to be double- or single-quoted.
		full: '1.8.3-build.3784+sha.05c65be8a',
		major: 1,
		minor: 8,
		dot: 3,
		codeName: 'snapshot'
	};


	function publishExternalAPI(angular) {
		extend(angular, {
			'errorHandlingConfig': errorHandlingConfig,
			'bootstrap': bootstrap,
			'copy': copy,
			'extend': extend,
			'merge': merge,
			'equals': equals,
			'element': jqLite,
			'forEach': forEach,
			'injector': createInjector,
			'noop': noop,
			'bind': bind,
			'toJson': toJson,
			'fromJson': fromJson,
			'identity': identity,
			'isUndefined': isUndefined,
			'isDefined': isDefined,
			'isString': isString,
			'isFunction': isFunction,
			'isObject': isObject,
			'isNumber': isNumber,
			'isElement': isElement,
			'isArray': isArray,
			'version': version,
			'isDate': isDate,
			'callbacks': { $$counter: 0 },
			'getTestability': getTestability,
			'reloadWithDebugInfo': reloadWithDebugInfo,
			'UNSAFE_restoreLegacyJqLiteXHTMLReplacement': UNSAFE_restoreLegacyJqLiteXHTMLReplacement,
			'$$minErr': minErr,
			'$$csp': csp,
			'$$encodeUriSegment': encodeUriSegment,
			'$$encodeUriQuery': encodeUriQuery,
			'$$lowercase': lowercase,
			'$$stringify': stringify,
			'$$uppercase': uppercase
		});

		angularModule = setupModuleLoader(window);

		angularModule('ng', ['ngLocale'], ['$provide',
			function ngModule($provide) {
				// $$sanitizeUriProvider needs to be before $compileProvider as it is used by it.
				$provide.provider({
					$$sanitizeUri: $$SanitizeUriProvider
				});
				$provide.provider('$compile', $CompileProvider).
					directive({
						a: htmlAnchorDirective,
						input: inputDirective,
						textarea: inputDirective,
						form: formDirective,
						script: scriptDirective,
						select: selectDirective,
						option: optionDirective,
						ngBind: ngBindDirective,
						ngBindHtml: ngBindHtmlDirective,
						ngBindTemplate: ngBindTemplateDirective,
						ngClass: ngClassDirective,
						ngClassEven: ngClassEvenDirective,
						ngClassOdd: ngClassOddDirective,
						ngCloak: ngCloakDirective,
						ngController: ngControllerDirective,
						ngForm: ngFormDirective,
						ngHide: ngHideDirective,
						ngIf: ngIfDirective,
						ngInclude: ngIncludeDirective,
						ngInit: ngInitDirective,
						ngNonBindable: ngNonBindableDirective,
						ngPluralize: ngPluralizeDirective,
						ngRef: ngRefDirective,
						ngRepeat: ngRepeatDirective,
						ngShow: ngShowDirective,
						ngStyle: ngStyleDirective,
						ngSwitch: ngSwitchDirective,
						ngSwitchWhen: ngSwitchWhenDirective,
						ngSwitchDefault: ngSwitchDefaultDirective,
						ngOptions: ngOptionsDirective,
						ngTransclude: ngTranscludeDirective,
						ngModel: ngModelDirective,
						ngList: ngListDirective,
						ngChange: ngChangeDirective,
						pattern: patternDirective,
						ngPattern: patternDirective,
						required: requiredDirective,
						ngRequired: requiredDirective,
						minlength: minlengthDirective,
						ngMinlength: minlengthDirective,
						maxlength: maxlengthDirective,
						ngMaxlength: maxlengthDirective,
						ngValue: ngValueDirective,
						ngModelOptions: ngModelOptionsDirective
					}).
					directive({
						ngInclude: ngIncludeFillContentDirective,
						input: hiddenInputBrowserCacheDirective
					}).
					directive(ngAttributeAliasDirectives).
					directive(ngEventDirectives);
				$provide.provider({
					$anchorScroll: $AnchorScrollProvider,
					$animate: $AnimateProvider,
					$animateCss: $CoreAnimateCssProvider,
					$$animateJs: $$CoreAnimateJsProvider,
					$$animateQueue: $$CoreAnimateQueueProvider,
					$$AnimateRunner: $$AnimateRunnerFactoryProvider,
					$$animateAsyncRun: $$AnimateAsyncRunFactoryProvider,
					$browser: $BrowserProvider,
					$cacheFactory: $CacheFactoryProvider,
					$controller: $ControllerProvider,
					$document: $DocumentProvider,
					$$isDocumentHidden: $$IsDocumentHiddenProvider,
					$exceptionHandler: $ExceptionHandlerProvider,
					$filter: $FilterProvider,
					$$forceReflow: $$ForceReflowProvider,
					$interpolate: $InterpolateProvider,
					$interval: $IntervalProvider,
					$$intervalFactory: $$IntervalFactoryProvider,
					$http: $HttpProvider,
					$httpParamSerializer: $HttpParamSerializerProvider,
					$httpParamSerializerJQLike: $HttpParamSerializerJQLikeProvider,
					$httpBackend: $HttpBackendProvider,
					$xhrFactory: $xhrFactoryProvider,
					$jsonpCallbacks: $jsonpCallbacksProvider,
					$location: $LocationProvider,
					$log: $LogProvider,
					$parse: $ParseProvider,
					$rootScope: $RootScopeProvider,
					$q: $QProvider,
					$$q: $$QProvider,
					$sce: $SceProvider,
					$sceDelegate: $SceDelegateProvider,
					$sniffer: $SnifferProvider,
					$$taskTrackerFactory: $$TaskTrackerFactoryProvider,
					$templateCache: $TemplateCacheProvider,
					$templateRequest: $TemplateRequestProvider,
					$$testability: $$TestabilityProvider,
					$timeout: $TimeoutProvider,
					$window: $WindowProvider,
					$$rAF: $$RAFProvider,
					$$jqLite: $$jqLiteProvider,
					$$Map: $$MapProvider,
					$$cookieReader: $$CookieReaderProvider
				});
			}
		])
			.info({ angularVersion: '1.8.3-build.3784+sha.05c65be8a' });
	}

	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 *     Any commits to this file should be reviewed with security in mind.  *
	 *   Changes to this file can potentially create security vulnerabilities. *
	 *          An approval from 2 Core members with history of modifying      *
	 *                         this file is required.                          *
	 *                                                                         *
	 *  Does the change somehow allow for arbitrary javascript to be executed? *
	 *    Or allows for someone to change the prototype of built-in objects?   *
	 *     Or gives undesired access to variables likes document or window?    *
	 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

	/* global
	  JQLitePrototype: true,
	  BOOLEAN_ATTR: true,
	  ALIASED_ATTR: true
	*/

	//////////////////////////////////
	//JQLite
	//////////////////////////////////

	/**
	 * @ngdoc function
	 * @name angular.element
	 * @module ng
	 * @kind function
	 *
	 * @description
	 * Wraps a raw DOM element or HTML string as a [jQuery](http://jquery.com) element.
	 *
	 * If jQuery is available, `angular.element` is an alias for the
	 * [jQuery](http://api.jquery.com/jQuery/) function. If jQuery is not available, `angular.element`
	 * delegates to AngularJS's built-in subset of jQuery, called "jQuery lite" or **jqLite**.
	 *
	 * jqLite is a tiny, API-compatible subset of jQuery that allows
	 * AngularJS to manipulate the DOM in a cross-browser compatible way. jqLite implements only the most
	 * commonly needed functionality with the goal of having a very small footprint.
	 *
	 * To use `jQuery`, simply ensure it is loaded before the `angular.js` file. You can also use the
	 * {@link ngJq `ngJq`} directive to specify that jqlite should be used over jQuery, or to use a
	 * specific version of jQuery if multiple versions exist on the page.
	 *
	 * <div class="alert alert-info">**Note:** All element references in AngularJS are always wrapped with jQuery or
	 * jqLite (such as the element argument in a directive's compile / link function). They are never raw DOM references.</div>
	 *
	 * <div class="alert alert-warning">**Note:** Keep in mind that this function will not find elements
	 * by tag name / CSS selector. For lookups by tag name, try instead `angular.element(document).find(...)`
	 * or `$document.find()`, or use the standard DOM APIs, e.g. `document.querySelectorAll()`.</div>
	 *
	 * ## AngularJS's jqLite
	 * jqLite provides only the following jQuery methods:
	 *
	 * - [`addClass()`](http://api.jquery.com/addClass/) - Does not support a function as first argument
	 * - [`after()`](http://api.jquery.com/after/)
	 * - [`append()`](http://api.jquery.com/append/) - Contrary to jQuery, this doesn't clone elements
	 *   so will not work correctly when invoked on a jqLite object containing more than one DOM node
	 * - [`attr()`](http://api.jquery.com/attr/) - Does not support functions as parameters
	 * - [`bind()`](http://api.jquery.com/bind/) (_deprecated_, use [`on()`](http://api.jquery.com/on/)) - Does not support namespaces, selectors or eventData
	 * - [`children()`](http://api.jquery.com/children/) - Does not support selectors
	 * - [`clone()`](http://api.jquery.com/clone/)
	 * - [`contents()`](http://api.jquery.com/contents/)
	 * - [`css()`](http://api.jquery.com/css/) - Only retrieves inline-styles, does not call `getComputedStyle()`.
	 *   As a setter, does not convert numbers to strings or append 'px', and also does not have automatic property prefixing.
	 * - [`data()`](http://api.jquery.com/data/)
	 * - [`detach()`](http://api.jquery.com/detach/)
	 * - [`empty()`](http://api.jquery.com/empty/)
	 * - [`eq()`](http://api.jquery.com/eq/)
	 * - [`find()`](http://api.jquery.com/find/) - Limited to lookups by tag name
	 * - [`hasClass()`](http://api.jquery.com/hasClass/)
	 * - [`html()`](http://api.jquery.com/html/)
	 * - [`next()`](http://api.jquery.com/next/) - Does not support selectors
	 * - [`on()`](http://api.jquery.com/on/) - Does not support namespaces, selectors or eventData
	 * - [`off()`](http://api.jquery.com/off/) - Does not support namespaces, selectors or event object as parameter
	 * - [`one()`](http://api.jquery.com/one/) - Does not support namespaces or selectors
	 * - [`parent()`](http://api.jquery.com/parent/) - Does not support selectors
	 * - [`prepend()`](http://api.jquery.com/prepend/)
	 * - [`prop()`](http://api.jquery.com/prop/)
	 * - [`ready()`](http://api.jquery.com/ready/) (_deprecated_, use `angular.element(callback)` instead of `angular.element(document).ready(callback)`)
	 * - [`remove()`](http://api.jquery.com/remove/)
	 * - [`removeAttr()`](http://api.jquery.com/removeAttr/) - Does not support multiple attributes
	 * - [`removeClass()`](http://api.jquery.com/removeClass/) - Does not support a function as first argument
	 * - [`removeData()`](http://api.jquery.com/removeData/)
	 * - [`replaceWith()`](http://api.jquery.com/replaceWith/)
	 * - [`text()`](http://api.jquery.com/text/)
	 * - [`toggleClass()`](http://api.jquery.com/toggleClass/) - Does not support a function as first argument
	 * - [`triggerHandler()`](http://api.jquery.com/triggerHandler/) - Passes a dummy event object to handlers
	 * - [`unbind()`](http://api.jquery.com/unbind/) (_deprecated_, use [`off()`](http://api.jquery.com/off/)) - Does not support namespaces or event object as parameter
	 * - [`val()`](http://api.jquery.com/val/)
	 * - [`wrap()`](http://api.jquery.com/wrap/)
	 *
	 * jqLite also provides a method restoring pre-1.8 insecure treatment of XHTML-like tags.
	 * This legacy behavior turns input like `<div /><span />` to `<div></div><span></span>`
	 * instead of `<div><span></span></div>` like version 1.8 & newer do. To restore it, invoke:
	 * ```js
	 * angular.UNSAFE_restoreLegacyJqLiteXHTMLReplacement();
	 * ```
	 * Note that this only patches jqLite. If you use jQuery 3.5.0 or newer, please read the
	 * [jQuery 3.5 upgrade guide](https://jquery.com/upgrade-guide/3.5/) for more details
	 * about the workarounds.
	 *
	 * ## jQuery/jqLite Extras
	 * AngularJS also provides the following additional methods and events to both jQuery and jqLite:
	 *
	 * ### Events
	 * - `$destroy` - AngularJS intercepts all jqLite/jQuery's DOM destruction apis and fires this event
	 *    on all DOM nodes being removed.  This can be used to clean up any 3rd party bindings to the DOM
	 *    element before it is removed.
	 *
	 * ### Methods
	 * - `controller(name)` - retrieves the controller of the current element or its parent. By default
	 *   retrieves controller associated with the `ngController` directive. If `name` is provided as
	 *   camelCase directive name, then the controller for this directive will be retrieved (e.g.
	 *   `'ngModel'`).
	 * - `injector()` - retrieves the injector of the current element or its parent.
	 * - `scope()` - retrieves the {@link ng.$rootScope.Scope scope} of the current
	 *   element or its parent. Requires {@link guide/production#disabling-debug-data Debug Data} to
	 *   be enabled.
	 * - `isolateScope()` - retrieves an isolate {@link ng.$rootScope.Scope scope} if one is attached directly to the
	 *   current element. This getter should be used only on elements that contain a directive which starts a new isolate
	 *   scope. Calling `scope()` on this element always returns the original non-isolate scope.
	 *   Requires {@link guide/production#disabling-debug-data Debug Data} to be enabled.
	 * - `inheritedData()` - same as `data()`, but walks up the DOM until a value is found or the top
	 *   parent element is reached.
	 *
	 * @knownIssue You cannot spy on `angular.element` if you are using Jasmine version 1.x. See
	 * https://github.com/angular/angular.js/issues/14251 for more information.
	 *
	 * @param {string|DOMElement} element HTML string or DOMElement to be wrapped into jQuery.
	 * @returns {Object} jQuery object.
	 */

	JQLite.expando = 'ng339';

	var jqCache = JQLite.cache = {},
		jqId = 1;

	/*
	 * !!! This is an undocumented "private" function !!!
	 */
	JQLite._data = function (node) {
		//jQuery always returns an object on cache miss
		return this.cache[node[this.expando]] || {};
	};

	function jqNextId() { return ++jqId; }


	var DASH_LOWERCASE_REGEXP = /-([a-z])/g;
	var MS_HACK_REGEXP = /^-ms-/;
	var MOUSE_EVENT_MAP = { mouseleave: 'mouseout', mouseenter: 'mouseover' };
	var jqLiteMinErr = minErr('jqLite');

	/**
	 * Converts kebab-case to camelCase.
	 * There is also a special case for the ms prefix starting with a lowercase letter.
	 * @param name Name to normalize
	 */
	function cssKebabToCamel(name) {
		return kebabToCamel(name.replace(MS_HACK_REGEXP, 'ms-'));
	}

	function fnCamelCaseReplace(all, letter) {
		return letter.toUpperCase();
	}

	/**
	 * Converts kebab-case to camelCase.
	 * @param name Name to normalize
	 */
	function kebabToCamel(name) {
		return name
			.replace(DASH_LOWERCASE_REGEXP, fnCamelCaseReplace);
	}

	var SINGLE_TAG_REGEXP = /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/;
	var HTML_REGEXP = /<|&#?\w+;/;
	var TAG_NAME_REGEXP = /<([\w:-]+)/;
	var XHTML_TAG_REGEXP = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi;

	// Table parts need to be wrapped with `<table>` or they're
	// stripped to their contents when put in a div.
	// XHTML parsers do not magically insert elements in the
	// same way that tag soup parsers do, so we cannot shorten
	// this by omitting <tbody> or other required elements.
	var wrapMap = {
		thead: ['table'],
		col: ['colgroup', 'table'],
		tr: ['tbody', 'table'],
		td: ['tr', 'tbody', 'table']
	};

	wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
	wrapMap.th = wrapMap.td;

	// Support: IE <10 only
	// IE 9 requires an option wrapper & it needs to have the whole table structure
	// set up in advance; assigning `"<td></td>"` to `tr.innerHTML` doesn't work, etc.
	var wrapMapIE9 = {
		option: [1, '<select multiple="multiple">', '</select>'],
		_default: [0, '', '']
	};

	for (var key in wrapMap) {
		var wrapMapValueClosing = wrapMap[key];
		var wrapMapValue = wrapMapValueClosing.slice().reverse();
		wrapMapIE9[key] = [wrapMapValue.length, '<' + wrapMapValue.join('><') + '>', '</' + wrapMapValueClosing.join('></') + '>'];
	}

	wrapMapIE9.optgroup = wrapMapIE9.option;

	function jqLiteIsTextNode(html) {
		return !HTML_REGEXP.test(html);
	}

	function jqLiteAcceptsData(node) {
		// The window object can accept data but has no nodeType
		// Otherwise we are only interested in elements (1) and documents (9)
		var nodeType = node.nodeType;
		return nodeType === NODE_TYPE_ELEMENT || !nodeType || nodeType === NODE_TYPE_DOCUMENT;
	}

	function jqLiteHasData(node) {
		for (var key in jqCache[node.ng339]) {
			return true;
		}
		return false;
	}

	function jqLiteBuildFragment(html, context) {
		var tmp, tag, wrap, finalHtml,
			fragment = context.createDocumentFragment(),
			nodes = [], i;

		if (jqLiteIsTextNode(html)) {
			// Convert non-html into a text node
			nodes.push(context.createTextNode(html));
		} else {
			// Convert html into DOM nodes
			tmp = fragment.appendChild(context.createElement('div'));
			tag = (TAG_NAME_REGEXP.exec(html) || ['', ''])[1].toLowerCase();
			finalHtml = JQLite.legacyXHTMLReplacement ?
				html.replace(XHTML_TAG_REGEXP, '<$1></$2>') :
				html;

			if (msie < 10) {
				wrap = wrapMapIE9[tag] || wrapMapIE9._default;
				tmp.innerHTML = wrap[1] + finalHtml + wrap[2];

				// Descend through wrappers to the right content
				i = wrap[0];
				while (i--) {
					tmp = tmp.firstChild;
				}
			} else {
				wrap = wrapMap[tag] || [];

				// Create wrappers & descend into them
				i = wrap.length;
				while (--i > -1) {
					tmp.appendChild(window.document.createElement(wrap[i]));
					tmp = tmp.firstChild;
				}

				tmp.innerHTML = finalHtml;
			}

			nodes = concat(nodes, tmp.childNodes);

			tmp = fragment.firstChild;
			tmp.textContent = '';
		}

		// Remove wrapper from fragment
		fragment.textContent = '';
		fragment.innerHTML = ''; // Clear inner HTML
		forEach(nodes, function (node) {
			fragment.appendChild(node);
		});

		return fragment;
	}

	function jqLiteParseHTML(html, context) {
		context = context || window.document;
		var parsed;

		if ((parsed = SINGLE_TAG_REGEXP.exec(html))) {
			return [context.createElement(parsed[1])];
		}

		if ((parsed = jqLiteBuildFragment(html, context))) {
			return parsed.childNodes;
		}

		return [];
	}

	function jqLiteWrapNode(node, wrapper) {
		var parent = node.parentNode;

		if (parent) {
			parent.replaceChild(wrapper, node);
		}

		wrapper.appendChild(node);
	}


	// IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
	var jqLiteContains = window.Node.prototype.contains || /** @this */ function (arg) {
		// eslint-disable-next-line no-bitwise
		return !!(this.compareDocumentPosition(arg) & 16);
	};

	/////////////////////////////////////////////
	function JQLite(element) {
		if (element instanceof JQLite) {
			return element;
		}

		var argIsString;

		if (isString(element)) {
			element = trim(element);
			argIsString = true;
		}
		if (!(this instanceof JQLite)) {
			if (argIsString && element.charAt(0) !== '<') {
				throw jqLiteMinErr('nosel', 'Looking up elements via selectors is not supported by jqLite! See: http://docs.angularjs.org/api/angular.element');
			}
			return new JQLite(element);
		}

		if (argIsString) {
			jqLiteAddNodes(this, jqLiteParseHTML(element));
		} else if (isFunction(element)) {
			jqLiteReady(element);
		} else {
			jqLiteAddNodes(this, element);
		}
	}

	function jqLiteClone(element) {
		return element.cloneNode(true);
	}

	function jqLiteDealoc(element, onlyDescendants) {
		if (!onlyDescendants && jqLiteAcceptsData(element)) jqLite.cleanData([element]);

		if (element.querySelectorAll) {
			jqLite.cleanData(element.querySelectorAll('*'));
		}
	}

	function isEmptyObject(obj) {
		var name;

		for (name in obj) {
			return false;
		}
		return true;
	}

	function removeIfEmptyData(element) {
		var expandoId = element.ng339;
		var expandoStore = expandoId && jqCache[expandoId];

		var events = expandoStore && expandoStore.events;
		var data = expandoStore && expandoStore.data;

		if ((!data || isEmptyObject(data)) && (!events || isEmptyObject(events))) {
			delete jqCache[expandoId];
			element.ng339 = undefined; // don't delete DOM expandos. IE and Chrome don't like it
		}
	}

	function jqLiteOff(element, type, fn, unsupported) {
		if (isDefined(unsupported)) throw jqLiteMinErr('offargs', 'jqLite#off() does not support the `selector` argument');

		var expandoStore = jqLiteExpandoStore(element);
		var events = expandoStore && expandoStore.events;
		var handle = expandoStore && expandoStore.handle;

		if (!handle) return; //no listeners registered

		if (!type) {
			for (type in events) {
				if (type !== '$destroy') {
					element.removeEventListener(type, handle);
				}
				delete events[type];
			}
		} else {

			var removeHandler = function (type) {
				var listenerFns = events[type];
				if (isDefined(fn)) {
					arrayRemove(listenerFns || [], fn);
				}
				if (!(isDefined(fn) && listenerFns && listenerFns.length > 0)) {
					element.removeEventListener(type, handle);
					delete events[type];
				}
			};

			forEach(type.split(' '), function (type) {
				removeHandler(type);
				if (MOUSE_EVENT_MAP[type]) {
					removeHandler(MOUSE_EVENT_MAP[type]);
				}
			});
		}

		removeIfEmptyData(element);
	}

	function jqLiteRemoveData(element, name) {
		var expandoId = element.ng339;
		var expandoStore = expandoId && jqCache[expandoId];

		if (expandoStore) {
			if (name) {
				delete expandoStore.data[name];
			} else {
				expandoStore.data = {};
			}

			removeIfEmptyData(element);
		}
	}


	function jqLiteExpandoStore(element, createIfNecessary) {
		var expandoId = element.ng339,
			expandoStore = expandoId && jqCache[expandoId];

		if (createIfNecessary && !expandoStore) {
			element.ng339 = expandoId = jqNextId();
			expandoStore = jqCache[expandoId] = { events: {}, data: {}, handle: undefined };
		}

		return expandoStore;
	}


	function jqLiteData(element, key, value) {
		if (jqLiteAcceptsData(element)) {
			var prop;

			var isSimpleSetter = isDefined(value);
			var isSimpleGetter = !isSimpleSetter && key && !isObject(key);
			var massGetter = !key;
			var expandoStore = jqLiteExpandoStore(element, !isSimpleGetter);
			var data = expandoStore && expandoStore.data;

			if (isSimpleSetter) { // data('key', value)
				data[kebabToCamel(key)] = value;
			} else {
				if (massGetter) {  // data()
					return data;
				} else {
					if (isSimpleGetter) { // data('key')
						// don't force creation of expandoStore if it doesn't exist yet
						return data && data[kebabToCamel(key)];
					} else { // mass-setter: data({key1: val1, key2: val2})
						for (prop in key) {
							data[kebabToCamel(prop)] = key[prop];
						}
					}
				}
			}
		}
	}

	function jqLiteHasClass(element, selector) {
		if (!element.getAttribute) return false;
		return ((' ' + (element.getAttribute('class') || '') + ' ').replace(/[\n\t]/g, ' ').
			indexOf(' ' + selector + ' ') > -1);
	}

	function jqLiteRemoveClass(element, cssClasses) {
		if (cssClasses && element.setAttribute) {
			var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
				.replace(/[\n\t]/g, ' ');
			var newClasses = existingClasses;

			forEach(cssClasses.split(' '), function (cssClass) {
				cssClass = trim(cssClass);
				newClasses = newClasses.replace(' ' + cssClass + ' ', ' ');
			});

			if (newClasses !== existingClasses) {
				element.setAttribute('class', trim(newClasses));
			}
		}
	}

	function jqLiteAddClass(element, cssClasses) {
		if (cssClasses && element.setAttribute) {
			var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
				.replace(/[\n\t]/g, ' ');
			var newClasses = existingClasses;

			forEach(cssClasses.split(' '), function (cssClass) {
				cssClass = trim(cssClass);
				if (newClasses.indexOf(' ' + cssClass + ' ') === -1) {
					newClasses += cssClass + ' ';
				}
			});

			if (newClasses !== existingClasses) {
				element.setAttribute('class', trim(newClasses));
			}
		}
	}


	function jqLiteAddNodes(root, elements) {
		// THIS CODE IS VERY HOT. Don't make changes without benchmarking.

		if (elements) {

			// if a Node (the most common case)
			if (elements.nodeType) {
				root[root.length++] = elements;
			} else {
				var length = elements.length;

				// if an Array or NodeList and not a Window
				if (typeof length === 'number' && elements.window !== elements) {
					if (length) {
						for (var i = 0; i < length; i++) {
							root[root.length++] = elements[i];
						}
					}
				} else {
					root[root.length++] = elements;
				}
			}
		}
	}


	function jqLiteController(element, name) {
		return jqLiteInheritedData(element, '$' + (name || 'ngController') + 'Controller');
	}

	function jqLiteInheritedData(element, name, value) {
		// if element is the document object work with the html element instead
		// this makes $(document).scope() possible
		if (element.nodeType === NODE_TYPE_DOCUMENT) {
			element = element.documentElement;
		}
		var names = isArray(name) ? name : [name];

		while (element) {
			for (var i = 0, ii = names.length; i < ii; i++) {
				if (isDefined(value = jqLite.data(element, names[i]))) return value;
			}

			// If dealing with a document fragment node with a host element, and no parent, use the host
			// element as the parent. This enables directives within a Shadow DOM or polyfilled Shadow DOM
			// to lookup parent controllers.
			element = element.parentNode || (element.nodeType === NODE_TYPE_DOCUMENT_FRAGMENT && element.host);
		}
	}

	function jqLiteEmpty(element) {
		jqLiteDealoc(element, true);
		while (element.firstChild) {
			element.removeChild(element.firstChild);
		}
	}

	function jqLiteRemove(element, keepData) {
		if (!keepData) jqLiteDealoc(element);
		var parent = element.parentNode;
		if (parent) parent.removeChild(element);
	}


	function jqLiteDocumentLoaded(action, win) {
		win = win || window;
		if (win.document.readyState === 'complete') {
			// Force the action to be run async for consistent behavior
			// from the action's point of view
			// i.e. it will definitely not be in a $apply
			win.setTimeout(action);
		} else {
			// No need to unbind this handler as load is only ever called once
			jqLite(win).on('load', action);
		}
	}

	function jqLiteReady(fn) {
		function trigger() {
			window.document.removeEventListener('DOMContentLoaded', trigger);
			window.removeEventListener('load', trigger);
			fn();
		}

		// check if document is already loaded
		if (window.document.readyState === 'complete') {
			window.setTimeout(fn);
		} else {
			// We can not use jqLite since we are not done loading and jQuery could be loaded later.

			// Works for modern browsers and IE9
			window.document.addEventListener('DOMContentLoaded', trigger);

			// Fallback to window.onload for others
			window.addEventListener('load', trigger);
		}
	}

	//////////////////////////////////////////
	// Functions which are declared directly.
	//////////////////////////////////////////
	var JQLitePrototype = JQLite.prototype = {
		ready: jqLiteReady,
		toString: function () {
			var value = [];
			forEach(this, function (e) { value.push('' + e); });
			return '[' + value.join(', ') + ']';
		},

		eq: function (index) {
			return (index >= 0) ? jqLite(this[index]) : jqLite(this[this.length + index]);
		},

		length: 0,
		push: push,
		sort: [].sort,
		splice: [].splice
	};

	//////////////////////////////////////////
	// Functions iterating getter/setters.
	// these functions return self on setter and
	// value on get.
	//////////////////////////////////////////
	var BOOLEAN_ATTR = {};
	forEach('multiple,selected,checked,disabled,readOnly,required,open'.split(','), function (value) {
		BOOLEAN_ATTR[lowercase(value)] = value;
	});
	var BOOLEAN_ELEMENTS = {};
	forEach('input,select,option,textarea,button,form,details'.split(','), function (value) {
		BOOLEAN_ELEMENTS[value] = true;
	});
	var ALIASED_ATTR = {
		'ngMinlength': 'minlength',
		'ngMaxlength': 'maxlength',
		'ngMin': 'min',
		'ngMax': 'max',
		'ngPattern': 'pattern',
		'ngStep': 'step'
	};

	function getBooleanAttrName(element, name) {
		// check dom last since we will most likely fail on name
		var booleanAttr = BOOLEAN_ATTR[name.toLowerCase()];

		// booleanAttr is here twice to minimize DOM access
		return booleanAttr && BOOLEAN_ELEMENTS[nodeName_(element)] && booleanAttr;
	}

	function getAliasedAttrName(name) {
		return ALIASED_ATTR[name];
	}

	forEach({
		data: jqLiteData,
		removeData: jqLiteRemoveData,
		hasData: jqLiteHasData,
		cleanData: function jqLiteCleanData(nodes) {
			for (var i = 0, ii = nodes.length; i < ii; i++) {
				jqLiteRemoveData(nodes[i]);
				jqLiteOff(nodes[i]);
			}
		}
	}, function (fn, name) {
		JQLite[name] = fn;
	});

	forEach({
		data: jqLiteData,
		inheritedData: jqLiteInheritedData,

		scope: function (element) {
			// Can't use jqLiteData here directly so we stay compatible with jQuery!
			return jqLite.data(element, '$scope') || jqLiteInheritedData(element.parentNode || element, ['$isolateScope', '$scope']);
		},

		isolateScope: function (element) {
			// Can't use jqLiteData here directly so we stay compatible with jQuery!
			return jqLite.data(element, '$isolateScope') || jqLite.data(element, '$isolateScopeNoTemplate');
		},

		controller: jqLiteController,

		injector: function (element) {
			return jqLiteInheritedData(element, '$injector');
		},

		removeAttr: function (element, name) {
			element.removeAttribute(name);
		},

		hasClass: jqLiteHasClass,

		css: function (element, name, value) {
			name = cssKebabToCamel(name);

			if (isDefined(value)) {
				element.style[name] = value;
			} else {
				return element.style[name];
			}
		},

		attr: function (element, name, value) {
			var ret;
			var nodeType = element.nodeType;
			if (nodeType === NODE_TYPE_TEXT || nodeType === NODE_TYPE_ATTRIBUTE || nodeType === NODE_TYPE_COMMENT ||
				!element.getAttribute) {
				return;
			}

			var lowercasedName = lowercase(name);
			var isBooleanAttr = BOOLEAN_ATTR[lowercasedName];

			if (isDefined(value)) {
				// setter

				if (value === null || (value === false && isBooleanAttr)) {
					element.removeAttribute(name);
				} else {
					element.setAttribute(name, isBooleanAttr ? lowercasedName : value);
				}
			} else {
				// getter

				ret = element.getAttribute(name);

				if (isBooleanAttr && ret !== null) {
					ret = lowercasedName;
				}
				// Normalize non-existing attributes to undefined (as jQuery).
				return ret === null ? undefined : ret;
			}
		},

		prop: function (element, name, value) {
			if (isDefined(value)) {
				element[name] = value;
			} else {
				return element[name];
			}
		},

		text: (function () {
			getText.$dv = '';
			return getText;

			function getText(element, value) {
				if (isUndefined(value)) {
					var nodeType = element.nodeType;
					return (nodeType === NODE_TYPE_ELEMENT || nodeType === NODE_TYPE_TEXT) ? element.textContent : '';
				}
				element.textContent = value;
			}
		})(),

		val: function (element, value) {
			if (isUndefined(value)) {
				if (element.multiple && nodeName_(element) === 'select') {
					var result = [];
					forEach(element.options, function (option) {
						if (option.selected) {
							result.push(option.value || option.text);
						}
					});
					return result;
				}
				return element.value;
			}
			element.value = value;
		},

		html: function (element, value) {
			if (isUndefined(value)) {
				return element.innerHTML;
			}
			jqLiteDealoc(element, true);
			element.innerHTML = value;
		},

		empty: jqLiteEmpty
	}, function (fn, name) {
		/**
		 * Properties: writes return selection, reads return first value
		 */
		JQLite.prototype[name] = function (arg1, arg2) {
			var i, key;
			var nodeCount = this.length;

			// jqLiteHasClass has only two arguments, but is a getter-only fn, so we need to special-case it
			// in a way that survives minification.
			// jqLiteEmpty takes no arguments but is a setter.
			if (fn !== jqLiteEmpty &&
				(isUndefined((fn.length === 2 && (fn !== jqLiteHasClass && fn !== jqLiteController)) ? arg1 : arg2))) {
				if (isObject(arg1)) {

					// we are a write, but the object properties are the key/values
					for (i = 0; i < nodeCount; i++) {
						if (fn === jqLiteData) {
							// data() takes the whole object in jQuery
							fn(this[i], arg1);
						} else {
							for (key in arg1) {
								fn(this[i], key, arg1[key]);
							}
						}
					}
					// return self for chaining
					return this;
				} else {
					// we are a read, so read the first child.
					// TODO: do we still need this?
					var value = fn.$dv;
					// Only if we have $dv do we iterate over all, otherwise it is just the first element.
					var jj = (isUndefined(value)) ? Math.min(nodeCount, 1) : nodeCount;
					for (var j = 0; j < jj; j++) {
						var nodeValue = fn(this[j], arg1, arg2);
						value = value ? value + nodeValue : nodeValue;
					}
					return value;
				}
			} else {
				// we are a write, so apply to all children
				for (i = 0; i < nodeCount; i++) {
					fn(this[i], arg1, arg2);
				}
				// return self for chaining
				return this;
			}
		};
	});

	function createEventHandler(element, events) {
		var eventHandler = function (event, type) {
			// jQuery specific api
			event.isDefaultPrevented = function () {
				return event.defaultPrevented;
			};

			var eventFns = events[type || event.type];
			var eventFnsLength = eventFns ? eventFns.length : 0;

			if (!eventFnsLength) return;

			if (isUndefined(event.immediatePropagationStopped)) {
				var originalStopImmediatePropagation = event.stopImmediatePropagation;
				event.stopImmediatePropagation = function () {
					event.immediatePropagationStopped = true;

					if (event.stopPropagation) {
						event.stopPropagation();
					}

					if (originalStopImmediatePropagation) {
						originalStopImmediatePropagation.call(event);
					}
				};
			}

			event.isImmediatePropagationStopped = function () {
				return event.immediatePropagationStopped === true;
			};

			// Some events have special handlers that wrap the real handler
			var handlerWrapper = eventFns.specialHandlerWrapper || defaultHandlerWrapper;

			// Copy event handlers in case event handlers array is modified during execution.
			if ((eventFnsLength > 1)) {
				eventFns = shallowCopy(eventFns);
			}

			for (var i = 0; i < eventFnsLength; i++) {
				if (!event.isImmediatePropagationStopped()) {
					handlerWrapper(element, event, eventFns[i]);
				}
			}
		};

		// TODO: this is a hack for angularMocks/clearDataCache that makes it possible to deregister all
		//       events on `element`
		eventHandler.elem = element;
		return eventHandler;
	}

	function defaultHandlerWrapper(element, event, handler) {
		handler.call(element, event);
	}

	function specialMouseHandlerWrapper(target, event, handler) {
		// Refer to jQuery's implementation of mouseenter & mouseleave
		// Read about mouseenter and mouseleave:
		// http://www.quirksmode.org/js/events_mouse.html#link8
		var related = event.relatedTarget;
		// For mousenter/leave call the handler if related is outside the target.
		// NB: No relatedTarget if the mouse left/entered the browser window
		if (!related || (related !== target && !jqLiteContains.call(target, related))) {
			handler.call(target, event);
		}
	}

	//////////////////////////////////////////
	// Functions iterating traversal.
	// These functions chain results into a single
	// selector.
	//////////////////////////////////////////
	forEach({
		removeData: jqLiteRemoveData,

		on: function jqLiteOn(element, type, fn, unsupported) {
			if (isDefined(unsupported)) throw jqLiteMinErr('onargs', 'jqLite#on() does not support the `selector` or `eventData` parameters');

			// Do not add event handlers to non-elements because they will not be cleaned up.
			if (!jqLiteAcceptsData(element)) {
				return;
			}

			var expandoStore = jqLiteExpandoStore(element, true);
			var events = expandoStore.events;
			var handle = expandoStore.handle;

			if (!handle) {
				handle = expandoStore.handle = createEventHandler(element, events);
			}

			// http://jsperf.com/string-indexof-vs-split
			var types = type.indexOf(' ') >= 0 ? type.split(' ') : [type];
			var i = types.length;

			var addHandler = function (type, specialHandlerWrapper, noEventListener) {
				var eventFns = events[type];

				if (!eventFns) {
					eventFns = events[type] = [];
					eventFns.specialHandlerWrapper = specialHandlerWrapper;
					if (type !== '$destroy' && !noEventListener) {
						element.addEventListener(type, handle);
					}
				}

				eventFns.push(fn);
			};

			while (i--) {
				type = types[i];
				if (MOUSE_EVENT_MAP[type]) {
					addHandler(MOUSE_EVENT_MAP[type], specialMouseHandlerWrapper);
					addHandler(type, undefined, true);
				} else {
					addHandler(type);
				}
			}
		},

		off: jqLiteOff,

		one: function (element, type, fn) {
			element = jqLite(element);

			//add the listener twice so that when it is called
			//you can remove the original function and still be
			//able to call element.off(ev, fn) normally
			element.on(type, function onFn() {
				element.off(type, fn);
				element.off(type, onFn);
			});
			element.on(type, fn);
		},

		replaceWith: function (element, replaceNode) {
			var index, parent = element.parentNode;
			jqLiteDealoc(element);
			forEach(new JQLite(replaceNode), function (node) {
				if (index) {
					parent.insertBefore(node, index.nextSibling);
				} else {
					parent.replaceChild(node, element);
				}
				index = node;
			});
		},

		children: function (element) {
			var children = [];
			forEach(element.childNodes, function (element) {
				if (element.nodeType === NODE_TYPE_ELEMENT) {
					children.push(element);
				}
			});
			return children;
		},

		contents: function (element) {
			return element.contentDocument || element.childNodes || [];
		},

		append: function (element, node) {
			var nodeType = element.nodeType;
			if (nodeType !== NODE_TYPE_ELEMENT && nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT) return;

			node = new JQLite(node);

			for (var i = 0, ii = node.length; i < ii; i++) {
				var child = node[i];
				element.appendChild(child);
			}
		},

		prepend: function (element, node) {
			if (element.nodeType === NODE_TYPE_ELEMENT) {
				var index = element.firstChild;
				forEach(new JQLite(node), function (child) {
					element.insertBefore(child, index);
				});
			}
		},

		wrap: function (element, wrapNode) {
			jqLiteWrapNode(element, jqLite(wrapNode).eq(0).clone()[0]);
		},

		remove: jqLiteRemove,

		detach: function (element) {
			jqLiteRemove(element, true);
		},

		after: function (element, newElement) {
			var index = element, parent = element.parentNode;

			if (parent) {
				newElement = new JQLite(newElement);

				for (var i = 0, ii = newElement.length; i < ii; i++) {
					var node = newElement[i];
					parent.insertBefore(node, index.nextSibling);
					index = node;
				}
			}
		},

		addClass: jqLiteAddClass,
		removeClass: jqLiteRemoveClass,

		toggleClass: function (element, selector, condition) {
			if (selector) {
				forEach(selector.split(' '), function (className) {
					var classCondition = condition;
					if (isUndefined(classCondition)) {
						classCondition = !jqLiteHasClass(element, className);
					}
					(classCondition ? jqLiteAddClass : jqLiteRemoveClass)(element, className);
				});
			}
		},

		parent: function (element) {
			var parent = element.parentNode;
			return parent && parent.nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT ? parent : null;
		},

		next: function (element) {
			return element.nextElementSibling;
		},

		find: function (element, selector) {
			if (element.getElementsByTagName) {
				return element.getElementsByTagName(selector);
			} else {
				return [];
			}
		},

		clone: jqLiteClone,

		triggerHandler: function (element, event, extraParameters) {

			var dummyEvent, eventFnsCopy, handlerArgs;
			var eventName = event.type || event;
			var expandoStore = jqLiteExpandoStore(element);
			var events = expandoStore && expandoStore.events;
			var eventFns = events && events[eventName];

			if (eventFns) {
				// Create a dummy event to pass to the handlers
				dummyEvent = {
					preventDefault: function () { this.defaultPrevented = true; },
					isDefaultPrevented: function () { return this.defaultPrevented === true; },
					stopImmediatePropagation: function () { this.immediatePropagationStopped = true; },
					isImmediatePropagationStopped: function () { return this.immediatePropagationStopped === true; },
					stopPropagation: noop,
					type: eventName,
					target: element
				};

				// If a custom event was provided then extend our dummy event with it
				if (event.type) {
					dummyEvent = extend(dummyEvent, event);
				}

				// Copy event handlers in case event handlers array is modified during execution.
				eventFnsCopy = shallowCopy(eventFns);
				handlerArgs = extraParameters ? [dummyEvent].concat(extraParameters) : [dummyEvent];

				forEach(eventFnsCopy, function (fn) {
					if (!dummyEvent.isImmediatePropagationStopped()) {
						fn.apply(element, handlerArgs);
					}
				});
			}
		}
	}, function (fn, name) {
		/**
		 * chaining functions
		 */
		JQLite.prototype[name] = function (arg1, arg2, arg3) {
			var value;

			for (var i = 0, ii = this.length; i < ii; i++) {
				if (isUndefined(value)) {
					value = fn(this[i], arg1, arg2, arg3);
					if (isDefined(value)) {
						// any function which returns a value needs to be wrapped
						value = jqLite(value);
					}
				} else {
					jqLiteAddNodes(value, fn(this[i], arg1, arg2, arg3));
				}
			}
			return isDefined(value) ? value : this;
		};
	});

	// bind legacy bind/unbind to on/off
	JQLite.prototype.bind = JQLite.prototype.on;
	JQLite.prototype.unbind = JQLite.prototype.off;


	// Provider for private $$jqLite service
	/** @this */
	function $$jqLiteProvider() {
		this.$get = function $$jqLite() {
			return extend(JQLite, {
				hasClass: function (node, classes) {
					if (node.attr) node = node[0];
					return jqLiteHasClass(node, classes);
				},
				addClass: function (node, classes) {
					if (node.attr) node = node[0];
					return jqLiteAddClass(node, classes);
				},
				removeClass: function (node, classes) {
					if (node.attr) node = node[0];
					return jqLiteRemoveClass(node, classes);
				}
			});
		};
	}

	/**
	 * Computes a hash of an 'obj'.
	 * Hash of a:
	 *  string is string
	 *  number is number as string
	 *  object is either result of calling $$hashKey function on the object or uniquely generated id,
	 *         that is also assigned to the $$hashKey property of the object.
	 *
	 * @param obj
	 * @returns {string} hash string such that the same input will have the same hash string.
	 *         The resulting string key is in 'type:hashKey' format.
	 */
	function hashKey(obj, nextUidFn) {
		var key = obj && obj.$$hashKey;

		if (key) {
			if (typeof key === 'function') {
				key = obj.$$hashKey();
			}
			return key;
		}

		var objType = typeof obj;
		if (objType === 'function' || (objType === 'object' && obj !== null)) {
			key = obj.$$hashKey = objType + ':' + (nextUidFn || nextUid)();
		} else {
			key = objType + ':' + obj;
		}

		return key;
	}

	// A minimal ES2015 Map implementation.
	// Should be bug/feature equivalent to the native implementations of supported browsers
	// (for the features required in Angular).
	// See https://kangax.github.io/compat-table/es6/#test-Map
	var nanKey = Object.create(null);
	function NgMapShim() {
		this._keys = [];
		this._values = [];
		this._lastKey = NaN;
		this._lastIndex = -1;
	}
	NgMapShim.prototype = {
		_idx: function (key) {
			if (key !== this._lastKey) {
				this._lastKey = key;
				this._lastIndex = this._keys.indexOf(key);
			}
			return this._lastIndex;
		},
		_transformKey: function (key) {
			return isNumberNaN(key) ? nanKey : key;
		},
		get: function (key) {
			key = this._transformKey(key);
			var idx = this._idx(key);
			if (idx !== -1) {
				return this._values[idx];
			}
		},
		has: function (key) {
			key = this._transformKey(key);
			var idx = this._idx(key);
			return idx !== -1;
		},
		set: function (key, value) {
			key = this._transformKey(key);
			var idx = this._idx(key);
			if (idx === -1) {
				idx = this._lastIndex = this._keys.length;
			}
			this._keys[idx] = key;
			this._values[idx] = value;

			// Support: IE11
			// Do not `return this` to simulate the partial IE11 implementation
		},
		delete: function (key) {
			key = this._transformKey(key);
			var idx = this._idx(key);
			if (idx === -1) {
				return false;
			}
			this._keys.splice(idx, 1);
			this._values.splice(idx, 1);
			this._lastKey = NaN;
			this._lastIndex = -1;
			return true;
		}
	};

	// For now, always use `NgMapShim`, even if `window.Map` is available. Some native implementations
	// are still buggy (often in subtle ways) and can cause hard-to-debug failures. When native `Map`
	// implementations get more stable, we can reconsider switching to `window.Map` (when available).
	var NgMap = NgMapShim;

	var $$MapProvider = [/** @this */function () {
		this.$get = [function () {
			return NgMap;
		}];
	}];

	/**
	 * @ngdoc function
	 * @module ng
	 * @name angular.injector
	 * @kind function
	 *
	 * @description
	 * Creates an injector object that can be used for retrieving services as well as for
	 * dependency injection (see {@link guide/di dependency injection}).
	 *
	 * @param {Array.<string|Function>} modules A list of module functions or their aliases. See
	 *     {@link angular.module}. The `ng` module must be explicitly added.
	 * @param {boolean=} [strictDi=false] Whether the injector should be in strict mode, which
	 *     disallows argument name annotation inference.
	 * @returns {injector} Injector object. See {@link auto.$injector $injector}.
	 *
	 * @example
	 * Typical usage
	 * ```js
	 *   // create an injector
	 *   var $injector = angular.injector(['ng']);
	 *
	 *   // use the injector to kick off your application
	 *   // use the type inference to auto inject arguments, or use implicit injection
	 *   $injector.invoke(function($rootScope, $compile, $document) {
	 *     $compile($document)($rootScope);
	 *     $rootScope.$digest();
	 *   });
	 * ```
	 *
	 * Sometimes you want to get access to the injector of a currently running AngularJS app
	 * from outside AngularJS. Perhaps, you want to inject and compile some markup after the
	 * application has been bootstrapped. You can do this using the extra `injector()` added
	 * to JQuery/jqLite elements. See {@link angular.element}.
	 *
	 * *This is fairly rare but could be the case if a third party library is injecting the
	 * markup.*
	 *
	 * In the following example a new block of HTML containing a `ng-controller`
	 * directive is added to the end of the document body by JQuery. We then compile and link
	 * it into the current AngularJS scope.
	 *
	 * ```js
	 * var $div = $('<div ng-controller="MyCtrl">{{content.label}}</div>');
	 * $(document.body).append($div);
	 *
	 * angular.element(document).injector().invoke(function($compile) {
	 *   var scope = angular.element($div).scope();
	 *   $compile($div)(scope);
	 * });
	 * ```
	 */


	/**
	 * @ngdoc module
	 * @name auto
	 * @installation
	 * @description
	 *
	 * Implicit module which gets automatically added to each {@link auto.$injector $injector}.
	 */

	var ARROW_ARG = /^([^(]+?)=>/;
	var FN_ARGS = /^[^(]*\(\s*([^)]*)\)/m;
	var FN_ARG_SPLIT = /,/;
	var FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
	var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
	var $injectorMinErr = minErr('$injector');

	function stringifyFn(fn) {
		return Function.prototype.toString.call(fn);
	}

	function extractArgs(fn) {
		var fnText = stringifyFn(fn).replace(STRIP_COMMENTS, ''),
			args = fnText.match(ARROW_ARG) || fnText.match(FN_ARGS);
		return args;
	}

	function anonFn(fn) {
		// For anonymous functions, showing at the very least the function signature can help in
		// debugging.
		var args = extractArgs(fn);
		if (args) {
			return 'function(' + (args[1] || '').replace(/[\s\r\n]+/, ' ') + ')';
		}
		return 'fn';
	}

	function annotate(fn, strictDi, name) {
		var $inject,
			argDecl,
			last;

		if (typeof fn === 'function') {
			if (!($inject = fn.$inject)) {
				$inject = [];
				if (fn.length) {
					if (strictDi) {
						if (!isString(name) || !name) {
							name = fn.name || anonFn(fn);
						}
						throw $injectorMinErr('strictdi',
							'{0} is not using explicit annotation and cannot be invoked in strict mode', name);
					}
					argDecl = extractArgs(fn);
					forEach(argDecl[1].split(FN_ARG_SPLIT), function (arg) {
						arg.replace(FN_ARG, function (all, underscore, name) {
							$inject.push(name);
						});
					});
				}
				fn.$inject = $inject;
			}
		} else if (isArray(fn)) {
			last = fn.length - 1;
			assertArgFn(fn[last], 'fn');
			$inject = fn.slice(0, last);
		} else {
			assertArgFn(fn, 'fn', true);
		}
		return $inject;
	}

	///////////////////////////////////////

	/**
	 * @ngdoc service
	 * @name $injector
	 *
	 * @description
	 *
	 * `$injector` is used to retrieve object instances as defined by
	 * {@link auto.$provide provider}, instantiate types, invoke methods,
	 * and load modules.
	 *
	 * The following always holds true:
	 *
	 * ```js
	 *   var $injector = angular.injector();
	 *   expect($injector.get('$injector')).toBe($injector);
	 *   expect($injector.invoke(function($injector) {
	 *     return $injector;
	 *   })).toBe($injector);
	 * ```
	 *
	 * ## Injection Function Annotation
	 *
	 * JavaScript does not have annotations, and annotations are needed for dependency injection. The
	 * following are all valid ways of annotating function with injection arguments and are equivalent.
	 *
	 * ```js
	 *   // inferred (only works if code not minified/obfuscated)
	 *   $injector.invoke(function(serviceA){});
	 *
	 *   // annotated
	 *   function explicit(serviceA) {};
	 *   explicit.$inject = ['serviceA'];
	 *   $injector.invoke(explicit);
	 *
	 *   // inline
	 *   $injector.invoke(['serviceA', function(serviceA){}]);
	 * ```
	 *
	 * ### Inference
	 *
	 * In JavaScript calling `toString()` on a function returns the function definition. The definition
	 * can then be parsed and the function arguments can be extracted. This method of discovering
	 * annotations is disallowed when the injector is in strict mode.
	 * *NOTE:* This does not work with minification, and obfuscation tools since these tools change the
	 * argument names.
	 *
	 * ### `$inject` Annotation
	 * By adding an `$inject` property onto a function the injection parameters can be specified.
	 *
	 * ### Inline
	 * As an array of injection names, where the last item in the array is the function to call.
	 */

	/**
	 * @ngdoc property
	 * @name $injector#modules
	 * @type {Object}
	 * @description
	 * A hash containing all the modules that have been loaded into the
	 * $injector.
	 *
	 * You can use this property to find out information about a module via the
	 * {@link angular.Module#info `myModule.info(...)`} method.
	 *
	 * For example:
	 *
	 * ```
	 * var info = $injector.modules['ngAnimate'].info();
	 * ```
	 *
	 * **Do not use this property to attempt to modify the modules after the application
	 * has been bootstrapped.**
	 */


	/**
	 * @ngdoc method
	 * @name $injector#get
	 *
	 * @description
	 * Return an instance of the service.
	 *
	 * @param {string} name The name of the instance to retrieve.
	 * @param {string=} caller An optional string to provide the origin of the function call for error messages.
	 * @return {*} The instance.
	 */

	/**
	 * @ngdoc method
	 * @name $injector#invoke
	 *
	 * @description
	 * Invoke the method and supply the method arguments from the `$injector`.
	 *
	 * @param {Function|Array.<string|Function>} fn The injectable function to invoke. Function parameters are
	 *   injected according to the {@link guide/di $inject Annotation} rules.
	 * @param {Object=} self The `this` for the invoked method.
	 * @param {Object=} locals Optional object. If preset then any argument names are read from this
	 *                         object first, before the `$injector` is consulted.
	 * @returns {*} the value returned by the invoked `fn` function.
	 */

	/**
	 * @ngdoc method
	 * @name $injector#has
	 *
	 * @description
	 * Allows the user to query if the particular service exists.
	 *
	 * @param {string} name Name of the service to query.
	 * @returns {boolean} `true` if injector has given service.
	 */

	/**
	 * @ngdoc method
	 * @name $injector#instantiate
	 * @description
	 * Create a new instance of JS type. The method takes a constructor function, invokes the new
	 * operator, and supplies all of the arguments to the constructor function as specified by the
	 * constructor annotation.
	 *
	 * @param {Function} Type Annotated constructor function.
	 * @param {Object=} locals Optional object. If preset then any argument names are read from this
	 * object first, before the `$injector` is consulted.
	 * @returns {Object} new instance of `Type`.
	 */

	/**
	 * @ngdoc method
	 * @name $injector#annotate
	 *
	 * @description
	 * Returns an array of service names which the function is requesting for injection. This API is
	 * used by the injector to determine which services need to be injected into the function when the
	 * function is invoked. There are three ways in which the function can be annotated with the needed
	 * dependencies.
	 *
	 * #### Argument names
	 *
	 * The simplest form is to extract the dependencies from the arguments of the function. This is done
	 * by converting the function into a string using `toString()` method and extracting the argument
	 * names.
	 * ```js
	 *   // Given
	 *   function MyController($scope, $route) {
	 *     // ...
	 *   }
	 *
	 *   // Then
	 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
	 * ```
	 *
	 * You can disallow this method by using strict injection mode.
	 *
	 * This method does not work with code minification / obfuscation. For this reason the following
	 * annotation strategies are supported.
	 *
	 * #### The `$inject` property
	 *
	 * If a function has an `$inject` property and its value is an array of strings, then the strings
	 * represent names of services to be injected into the function.
	 * ```js
	 *   // Given
	 *   var MyController = function(obfuscatedScope, obfuscatedRoute) {
	 *     // ...
	 *   }
	 *   // Define function dependencies
	 *   MyController['$inject'] = ['$scope', '$route'];
	 *
	 *   // Then
	 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
	 * ```
	 *
	 * #### The array notation
	 *
	 * It is often desirable to inline Injected functions and that's when setting the `$inject` property
	 * is very inconvenient. In these situations using the array notation to specify the dependencies in
	 * a way that survives minification is a better choice:
	 *
	 * ```js
	 *   // We wish to write this (not minification / obfuscation safe)
	 *   injector.invoke(function($compile, $rootScope) {
	 *     // ...
	 *   });
	 *
	 *   // We are forced to write break inlining
	 *   var tmpFn = function(obfuscatedCompile, obfuscatedRootScope) {
	 *     // ...
	 *   };
	 *   tmpFn.$inject = ['$compile', '$rootScope'];
	 *   injector.invoke(tmpFn);
	 *
	 *   // To better support inline function the inline annotation is supported
	 *   injector.invoke(['$compile', '$rootScope', function(obfCompile, obfRootScope) {
	 *     // ...
	 *   }]);
	 *
	 *   // Therefore
	 *   expect(injector.annotate(
	 *      ['$compile', '$rootScope', function(obfus_$compile, obfus_$rootScope) {}])
	 *    ).toEqual(['$compile', '$rootScope']);
	 * ```
	 *
	 * @param {Function|Array.<string|Function>} fn Function for which dependent service names need to
	 * be retrieved as described above.
	 *
	 * @param {boolean=} [strictDi=false] Disallow argument name annotation inference.
	 *
	 * @returns {Array.<string>} The names of the services which the function requires.
	 */
	/**
	 * @ngdoc method
	 * @name $injector#loadNewModules
	 *
	 * @description
	 *
	 * **This is a dangerous API, which you use at your own risk!**
	 *
	 * Add the specified modules to the current injector.
	 *
	 * This method will add each of the injectables to the injector and execute all of the config and run
	 * blocks for each module passed to the method.
	 *
	 * If a module has already been loaded into the injector then it will not be loaded again.
	 *
	 * * The application developer is responsible for loading the code containing the modules; and for
	 * ensuring that lazy scripts are not downloaded and executed more often that desired.
	 * * Previously compiled HTML will not be affected by newly loaded directives, filters and components.
	 * * Modules cannot be unloaded.
	 *
	 * You can use {@link $injector#modules `$injector.modules`} to check whether a module has been loaded
	 * into the injector, which may indicate whether the script has been executed already.
	 *
	 * @example
	 * Here is an example of loading a bundle of modules, with a utility method called `getScript`:
	 *
	 * ```javascript
	 * app.factory('loadModule', function($injector) {
	 *   return function loadModule(moduleName, bundleUrl) {
	 *     return getScript(bundleUrl).then(function() { $injector.loadNewModules([moduleName]); });
	 *   };
	 * })
	 * ```
	 *
	 * @param {Array<String|Function|Array>=} mods an array of modules to load into the application.
	 *     Each item in the array should be the name of a predefined module or a (DI annotated)
	 *     function that will be invoked by the injector as a `config` block.
	 *     See: {@link angular.module modules}
	 */


	/**
	 * @ngdoc service
	 * @name $provide
	 *
	 * @description
	 *
	 * The {@link auto.$provide $provide} service has a number of methods for registering components
	 * with the {@link auto.$injector $injector}. Many of these functions are also exposed on
	 * {@link angular.Module}.
	 *
	 * An AngularJS **service** is a singleton object created by a **service factory**.  These **service
	 * factories** are functions which, in turn, are created by a **service provider**.
	 * The **service providers** are constructor functions. When instantiated they must contain a
	 * property called `$get`, which holds the **service factory** function.
	 *
	 * When you request a service, the {@link auto.$injector $injector} is responsible for finding the
	 * correct **service provider**, instantiating it and then calling its `$get` **service factory**
	 * function to get the instance of the **service**.
	 *
	 * Often services have no configuration options and there is no need to add methods to the service
	 * provider.  The provider will be no more than a constructor function with a `$get` property. For
	 * these cases the {@link auto.$provide $provide} service has additional helper methods to register
	 * services without specifying a provider.
	 *
	 * * {@link auto.$provide#provider provider(name, provider)} - registers a **service provider** with the
	 *     {@link auto.$injector $injector}
	 * * {@link auto.$provide#constant constant(name, obj)} - registers a value/object that can be accessed by
	 *     providers and services.
	 * * {@link auto.$provide#value value(name, obj)} - registers a value/object that can only be accessed by
	 *     services, not providers.
	 * * {@link auto.$provide#factory factory(name, fn)} - registers a service **factory function**
	 *     that will be wrapped in a **service provider** object, whose `$get` property will contain the
	 *     given factory function.
	 * * {@link auto.$provide#service service(name, Fn)} - registers a **constructor function**
	 *     that will be wrapped in a **service provider** object, whose `$get` property will instantiate
	 *      a new object using the given constructor function.
	 * * {@link auto.$provide#decorator decorator(name, decorFn)} - registers a **decorator function** that
	 *      will be able to modify or replace the implementation of another service.
	 *
	 * See the individual methods for more information and examples.
	 */

	/**
	 * @ngdoc method
	 * @name $provide#provider
	 * @description
	 *
	 * Register a **provider function** with the {@link auto.$injector $injector}. Provider functions
	 * are constructor functions, whose instances are responsible for "providing" a factory for a
	 * service.
	 *
	 * Service provider names start with the name of the service they provide followed by `Provider`.
	 * For example, the {@link ng.$log $log} service has a provider called
	 * {@link ng.$logProvider $logProvider}.
	 *
	 * Service provider objects can have additional methods which allow configuration of the provider
	 * and its service. Importantly, you can configure what kind of service is created by the `$get`
	 * method, or how that service will act. For example, the {@link ng.$logProvider $logProvider} has a
	 * method {@link ng.$logProvider#debugEnabled debugEnabled}
	 * which lets you specify whether the {@link ng.$log $log} service will log debug messages to the
	 * console or not.
	 *
	 * It is possible to inject other providers into the provider function,
	 * but the injected provider must have been defined before the one that requires it.
	 *
	 * @param {string} name The name of the instance. NOTE: the provider will be available under `name +
							'Provider'` key.
	 * @param {(Object|function())} provider If the provider is:
	 *
	 *   - `Object`: then it should have a `$get` method. The `$get` method will be invoked using
	 *     {@link auto.$injector#invoke $injector.invoke()} when an instance needs to be created.
	 *   - `Constructor`: a new instance of the provider will be created using
	 *     {@link auto.$injector#instantiate $injector.instantiate()}, then treated as `object`.
	 *
	 * @returns {Object} registered provider instance
	
	 * @example
	 *
	 * The following example shows how to create a simple event tracking service and register it using
	 * {@link auto.$provide#provider $provide.provider()}.
	 *
	 * ```js
	 *  // Define the eventTracker provider
	 *  function EventTrackerProvider() {
	 *    var trackingUrl = '/track';
	 *
	 *    // A provider method for configuring where the tracked events should been saved
	 *    this.setTrackingUrl = function(url) {
	 *      trackingUrl = url;
	 *    };
	 *
	 *    // The service factory function
	 *    this.$get = ['$http', function($http) {
	 *      var trackedEvents = {};
	 *      return {
	 *        // Call this to track an event
	 *        event: function(event) {
	 *          var count = trackedEvents[event] || 0;
	 *          count += 1;
	 *          trackedEvents[event] = count;
	 *          return count;
	 *        },
	 *        // Call this to save the tracked events to the trackingUrl
	 *        save: function() {
	 *          $http.post(trackingUrl, trackedEvents);
	 *        }
	 *      };
	 *    }];
	 *  }
	 *
	 *  describe('eventTracker', function() {
	 *    var postSpy;
	 *
	 *    beforeEach(module(function($provide) {
	 *      // Register the eventTracker provider
	 *      $provide.provider('eventTracker', EventTrackerProvider);
	 *    }));
	 *
	 *    beforeEach(module(function(eventTrackerProvider) {
	 *      // Configure eventTracker provider
	 *      eventTrackerProvider.setTrackingUrl('/custom-track');
	 *    }));
	 *
	 *    it('tracks events', inject(function(eventTracker) {
	 *      expect(eventTracker.event('login')).toEqual(1);
	 *      expect(eventTracker.event('login')).toEqual(2);
	 *    }));
	 *
	 *    it('saves to the tracking url', inject(function(eventTracker, $http) {
	 *      postSpy = spyOn($http, 'post');
	 *      eventTracker.event('login');
	 *      eventTracker.save();
	 *      expect(postSpy).toHaveBeenCalled();
	 *      expect(postSpy.mostRecentCall.args[0]).not.toEqual('/track');
	 *      expect(postSpy.mostRecentCall.args[0]).toEqual('/custom-track');
	 *      expect(postSpy.mostRecentCall.args[1]).toEqual({ 'login': 1 });
	 *    }));
	 *  });
	 * ```
	 */

	/**
	 * @ngdoc method
	 * @name $provide#factory
	 * @description
	 *
	 * Register a **service factory**, which will be called to return the service instance.
	 * This is short for registering a service where its provider consists of only a `$get` property,
	 * which is the given service factory function.
	 * You should use {@link auto.$provide#factory $provide.factory(getFn)} if you do not need to
	 * configure your service in a provider.
	 *
	 * @param {string} name The name of the instance.
	 * @param {Function|Array.<string|Function>} $getFn The injectable $getFn for the instance creation.
	 *                      Internally this is a short hand for `$provide.provider(name, {$get: $getFn})`.
	 * @returns {Object} registered provider instance
	 *
	 * @example
	 * Here is an example of registering a service
	 * ```js
	 *   $provide.factory('ping', ['$http', function($http) {
	 *     return function ping() {
	 *       return $http.send('/ping');
	 *     };
	 *   }]);
	 * ```
	 * You would then inject and use this service like this:
	 * ```js
	 *   someModule.controller('Ctrl', ['ping', function(ping) {
	 *     ping();
	 *   }]);
	 * ```
	 */


	/**
	 * @ngdoc method
	 * @name $provide#service
	 * @description
	 *
	 * Register a **service constructor**, which will be invoked with `new` to create the service
	 * instance.
	 * This is short for registering a service where its provider's `$get` property is a factory
	 * function that returns an instance instantiated by the injector from the service constructor
	 * function.
	 *
	 * Internally it looks a bit like this:
	 *
	 * ```
	 * {
	 *   $get: function() {
	 *     return $injector.instantiate(constructor);
	 *   }
	 * }
	 * ```
	 *
	 *
	 * You should use {@link auto.$provide#service $provide.service(class)} if you define your service
	 * as a type/class.
	 *
	 * @param {string} name The name of the instance.
	 * @param {Function|Array.<string|Function>} constructor An injectable class (constructor function)
	 *     that will be instantiated.
	 * @returns {Object} registered provider instance
	 *
	 * @example
	 * Here is an example of registering a service using
	 * {@link auto.$provide#service $provide.service(class)}.
	 * ```js
	 *   var Ping = function($http) {
	 *     this.$http = $http;
	 *   };
	 *
	 *   Ping.$inject = ['$http'];
	 *
	 *   Ping.prototype.send = function() {
	 *     return this.$http.get('/ping');
	 *   };
	 *   $provide.service('ping', Ping);
	 * ```
	 * You would then inject and use this service like this:
	 * ```js
	 *   someModule.controller('Ctrl', ['ping', function(ping) {
	 *     ping.send();
	 *   }]);
	 * ```
	 */


	/**
	 * @ngdoc method
	 * @name $provide#value
	 * @description
	 *
	 * Register a **value service** with the {@link auto.$injector $injector}, such as a string, a
	 * number, an array, an object or a function. This is short for registering a service where its
	 * provider's `$get` property is a factory function that takes no arguments and returns the **value
	 * service**. That also means it is not possible to inject other services into a value service.
	 *
	 * Value services are similar to constant services, except that they cannot be injected into a
	 * module configuration function (see {@link angular.Module#config}) but they can be overridden by
	 * an AngularJS {@link auto.$provide#decorator decorator}.
	 *
	 * @param {string} name The name of the instance.
	 * @param {*} value The value.
	 * @returns {Object} registered provider instance
	 *
	 * @example
	 * Here are some examples of creating value services.
	 * ```js
	 *   $provide.value('ADMIN_USER', 'admin');
	 *
	 *   $provide.value('RoleLookup', { admin: 0, writer: 1, reader: 2 });
	 *
	 *   $provide.value('halfOf', function(value) {
	 *     return value / 2;
	 *   });
	 * ```
	 */


	/**
	 * @ngdoc method
	 * @name $provide#constant
	 * @description
	 *
	 * Register a **constant service** with the {@link auto.$injector $injector}, such as a string,
	 * a number, an array, an object or a function. Like the {@link auto.$provide#value value}, it is not
	 * possible to inject other services into a constant.
	 *
	 * But unlike {@link auto.$provide#value value}, a constant can be
	 * injected into a module configuration function (see {@link angular.Module#config}) and it cannot
	 * be overridden by an AngularJS {@link auto.$provide#decorator decorator}.
	 *
	 * @param {string} name The name of the constant.
	 * @param {*} value The constant value.
	 * @returns {Object} registered instance
	 *
	 * @example
	 * Here a some examples of creating constants:
	 * ```js
	 *   $provide.constant('SHARD_HEIGHT', 306);
	 *
	 *   $provide.constant('MY_COLOURS', ['red', 'blue', 'grey']);
	 *
	 *   $provide.constant('double', function(value) {
	 *     return value * 2;
	 *   });
	 * ```
	 */


	/**
	 * @ngdoc method
	 * @name $provide#decorator
	 * @description
	 *
	 * Register a **decorator function** with the {@link auto.$injector $injector}. A decorator function
	 * intercepts the creation of a service, allowing it to override or modify the behavior of the
	 * service. The return value of the decorator function may be the original service, or a new service
	 * that replaces (or wraps and delegates to) the original service.
	 *
	 * You can find out more about using decorators in the {@link guide/decorators} guide.
	 *
	 * @param {string} name The name of the service to decorate.
	 * @param {Function|Array.<string|Function>} decorator This function will be invoked when the service needs to be
	 *    provided and should return the decorated service instance. The function is called using
	 *    the {@link auto.$injector#invoke injector.invoke} method and is therefore fully injectable.
	 *    Local injection arguments:
	 *
	 *    * `$delegate` - The original service instance, which can be replaced, monkey patched, configured,
	 *      decorated or delegated to.
	 *
	 * @example
	 * Here we decorate the {@link ng.$log $log} service to convert warnings to errors by intercepting
	 * calls to {@link ng.$log#error $log.warn()}.
	 * ```js
	 *   $provide.decorator('$log', ['$delegate', function($delegate) {
	 *     $delegate.warn = $delegate.error;
	 *     return $delegate;
	 *   }]);
	 * ```
	 */


	function createInjector(modulesToLoad, strictDi) {
		strictDi = (strictDi === true);
		var INSTANTIATING = {},
			providerSuffix = 'Provider',
			path = [],
			loadedModules = new NgMap(),
			providerCache = {
				$provide: {
					provider: supportObject(provider),
					factory: supportObject(factory),
					service: supportObject(service),
					value: supportObject(value),
					constant: supportObject(constant),
					decorator: decorator
				}
			},
			providerInjector = (providerCache.$injector =
				createInternalInjector(providerCache, function (serviceName, caller) {
					if (angular.isString(caller)) {
						path.push(caller);
					}
					throw $injectorMinErr('unpr', 'Unknown provider: {0}', path.join(' <- '));
				})),
			instanceCache = {},
			protoInstanceInjector =
				createInternalInjector(instanceCache, function (serviceName, caller) {
					var provider = providerInjector.get(serviceName + providerSuffix, caller);
					return instanceInjector.invoke(
						provider.$get, provider, undefined, serviceName);
				}),
			instanceInjector = protoInstanceInjector;

		providerCache['$injector' + providerSuffix] = { $get: valueFn(protoInstanceInjector) };
		instanceInjector.modules = providerInjector.modules = createMap();
		var runBlocks = loadModules(modulesToLoad);
		instanceInjector = protoInstanceInjector.get('$injector');
		instanceInjector.strictDi = strictDi;
		forEach(runBlocks, function (fn) { if (fn) instanceInjector.invoke(fn); });

		instanceInjector.loadNewModules = function (mods) {
			forEach(loadModules(mods), function (fn) { if (fn) instanceInjector.invoke(fn); });
		};


		return instanceInjector;

		////////////////////////////////////
		// $provider
		////////////////////////////////////

		function supportObject(delegate) {
			return function (key, value) {
				if (isObject(key)) {
					forEach(key, reverseParams(delegate));
				} else {
					return delegate(key, value);
				}
			};
		}

		function provider(name, provider_) {
			assertNotHasOwnProperty(name, 'service');
			if (isFunction(provider_) || isArray(provider_)) {
				provider_ = providerInjector.instantiate(provider_);
			}
			if (!provider_.$get) {
				throw $injectorMinErr('pget', 'Provider \'{0}\' must define $get factory method.', name);
			}
			return (providerCache[name + providerSuffix] = provider_);
		}

		function enforceReturnValue(name, factory) {
			return /** @this */ function enforcedReturnValue() {
				var result = instanceInjector.invoke(factory, this);
				if (isUndefined(result)) {
					throw $injectorMinErr('undef', 'Provider \'{0}\' must return a value from $get factory method.', name);
				}
				return result;
			};
		}

		function factory(name, factoryFn, enforce) {
			return provider(name, {
				$get: enforce !== false ? enforceReturnValue(name, factoryFn) : factoryFn
			});
		}

		function service(name, constructor) {
			return factory(name, ['$injector', function ($injector) {
				return $injector.instantiate(constructor);
			}]);
		}

		function value(name, val) { return factory(name, valueFn(val), false); }

		function constant(name, value) {
			assertNotHasOwnProperty(name, 'constant');
			providerCache[name] = value;
			instanceCache[name] = value;
		}

		function decorator(serviceName, decorFn) {
			var origProvider = providerInjector.get(serviceName + providerSuffix),
				orig$get = origProvider.$get;

			origProvider.$get = function () {
				var origInstance = instanceInjector.invoke(orig$get, origProvider);
				return instanceInjector.invoke(decorFn, null, { $delegate: origInstance });
			};
		}

		////////////////////////////////////
		// Module Loading
		////////////////////////////////////
		function loadModules(modulesToLoad) {
			assertArg(isUndefined(modulesToLoad) || isArray(modulesToLoad), 'modulesToLoad', 'not an array');
			var runBlocks = [], moduleFn;
			forEach(modulesToLoad, function (module) {
				if (loadedModules.get(module)) return;
				loadedModules.set(module, true);

				function runInvokeQueue(queue) {
					var i, ii;
					for (i = 0, ii = queue.length; i < ii; i++) {
						var invokeArgs = queue[i],
							provider = providerInjector.get(invokeArgs[0]);

						provider[invokeArgs[1]].apply(provider, invokeArgs[2]);
					}
				}

				try {
					if (isString(module)) {
						moduleFn = angularModule(module);
						instanceInjector.modules[module] = moduleFn;
						runBlocks = runBlocks.concat(loadModules(moduleFn.requires)).concat(moduleFn._runBlocks);
						runInvokeQueue(moduleFn._invokeQueue);
						runInvokeQueue(moduleFn._configBlocks);
					} else if (isFunction(module)) {
						runBlocks.push(providerInjector.invoke(module));
					} else if (isArray(module)) {
						runBlocks.push(providerInjector.invoke(module));
					} else {
						assertArgFn(module, 'module');
					}
				} catch (e) {
					if (isArray(module)) {
						module = module[module.length - 1];
					}
					if (e.message && e.stack && e.stack.indexOf(e.message) === -1) {
						// Safari & FF's stack traces don't contain error.message content
						// unlike those of Chrome and IE
						// So if stack doesn't contain message, we create a new string that contains both.
						// Since error.stack is read-only in Safari, I'm overriding e and not e.stack here.
						// eslint-disable-next-line no-ex-assign
						e = e.message + '\n' + e.stack;
					}
					throw $injectorMinErr('modulerr', 'Failed to instantiate module {0} due to:\n{1}',
						module, e.stack || e.message || e);
				}
			});
			return runBlocks;
		}

		////////////////////////////////////
		// internal Injector
		////////////////////////////////////

		function createInternalInjector(cache, factory) {

			function getService(serviceName, caller) {
				if (cache.hasOwnProperty(serviceName)) {
					if (cache[serviceName] === INSTANTIATING) {
						throw $injectorMinErr('cdep', 'Circular dependency found: {0}',
							serviceName + ' <- ' + path.join(' <- '));
					}
					return cache[serviceName];
				} else {
					try {
						path.unshift(serviceName);
						cache[serviceName] = INSTANTIATING;
						cache[serviceName] = factory(serviceName, caller);
						return cache[serviceName];
					} catch (err) {
						if (cache[serviceName] === INSTANTIATING) {
							delete cache[serviceName];
						}
						throw err;
					} finally {
						path.shift();
					}
				}
			}


			function injectionArgs(fn, locals, serviceName) {
				var args = [],
					$inject = createInjector.$$annotate(fn, strictDi, serviceName);

				for (var i = 0, length = $inject.length; i < length; i++) {
					var key = $inject[i];
					if (typeof key !== 'string') {
						throw $injectorMinErr('itkn',
							'Incorrect injection token! Expected service name as string, got {0}', key);
					}
					args.push(locals && locals.hasOwnProperty(key) ? locals[key] :
						getService(key, serviceName));
				}
				return args;
			}

			function isClass(func) {
				// Support: IE 9-11 only
				// IE 9-11 do not support classes and IE9 leaks with the code below.
				if (msie || typeof func !== 'function') {
					return false;
				}
				var result = func.$$ngIsClass;
				if (!isBoolean(result)) {
					result = func.$$ngIsClass = /^class\b/.test(stringifyFn(func));
				}
				return result;
			}

			function invoke(fn, self, locals, serviceName) {
				if (typeof locals === 'string') {
					serviceName = locals;
					locals = null;
				}

				var args = injectionArgs(fn, locals, serviceName);
				if (isArray(fn)) {
					fn = fn[fn.length - 1];
				}

				if (!isClass(fn)) {
					// http://jsperf.com/angularjs-invoke-apply-vs-switch
					// #5388
					return fn.apply(self, args);
				} else {
					args.unshift(null);
					return new (Function.prototype.bind.apply(fn, args))();
				}
			}


			function instantiate(Type, locals, serviceName) {
				// Check if Type is annotated and use just the given function at n-1 as parameter
				// e.g. someModule.factory('greeter', ['$window', function(renamed$window) {}]);
				var ctor = (isArray(Type) ? Type[Type.length - 1] : Type);
				var args = injectionArgs(Type, locals, serviceName);
				// Empty object at position 0 is ignored for invocation with `new`, but required.
				args.unshift(null);
				return new (Function.prototype.bind.apply(ctor, args))();
			}


			return {
				invoke: invoke,
				instantiate: instantiate,
				get: getService,
				annotate: createInjector.$$annotate,
				has: function (name) {
					return providerCache.hasOwnProperty(name + providerSuffix) || cache.hasOwnProperty(name);
				}
			};
		}
	}

	createInjector.$$annotate = annotate;

	/**
	 * @ngdoc provider
	 * @name $anchorScrollProvider
	 * @this
	 *
	 * @description
	 * Use `$anchorScrollProvider` to disable automatic scrolling whenever
	 * {@link ng.$location#hash $location.hash()} changes.
	 */
	function $AnchorScrollProvider() {

		var autoScrollingEnabled = true;

		/**
		 * @ngdoc method
		 * @name $anchorScrollProvider#disableAutoScrolling
		 *
		 * @description
		 * By default, {@link ng.$anchorScroll $anchorScroll()} will automatically detect changes to
		 * {@link ng.$location#hash $location.hash()} and scroll to the element matching the new hash.<br />
		 * Use this method to disable automatic scrolling.
		 *
		 * If automatic scrolling is disabled, one must explicitly call
		 * {@link ng.$anchorScroll $anchorScroll()} in order to scroll to the element related to the
		 * current hash.
		 */
		this.disableAutoScrolling = function () {
			autoScrollingEnabled = false;
		};

		/**
		 * @ngdoc service
		 * @name $anchorScroll
		 * @kind function
		 * @requires $window
		 * @requires $location
		 * @requires $rootScope
		 *
		 * @description
		 * When called, it scrolls to the element related to the specified `hash` or (if omitted) to the
		 * current value of {@link ng.$location#hash $location.hash()}, according to the rules specified
		 * in the
		 * [HTML5 spec](http://www.w3.org/html/wg/drafts/html/master/browsers.html#an-indicated-part-of-the-document).
		 *
		 * It also watches the {@link ng.$location#hash $location.hash()} and automatically scrolls to
		 * match any anchor whenever it changes. This can be disabled by calling
		 * {@link ng.$anchorScrollProvider#disableAutoScrolling $anchorScrollProvider.disableAutoScrolling()}.
		 *
		 * Additionally, you can use its {@link ng.$anchorScroll#yOffset yOffset} property to specify a
		 * vertical scroll-offset (either fixed or dynamic).
		 *
		 * @param {string=} hash The hash specifying the element to scroll to. If omitted, the value of
		 *                       {@link ng.$location#hash $location.hash()} will be used.
		 *
		 * @property {(number|function|jqLite)} yOffset
		 * If set, specifies a vertical scroll-offset. This is often useful when there are fixed
		 * positioned elements at the top of the page, such as navbars, headers etc.
		 *
		 * `yOffset` can be specified in various ways:
		 * - **number**: A fixed number of pixels to be used as offset.<br /><br />
		 * - **function**: A getter function called everytime `$anchorScroll()` is executed. Must return
		 *   a number representing the offset (in pixels).<br /><br />
		 * - **jqLite**: A jqLite/jQuery element to be used for specifying the offset. The distance from
		 *   the top of the page to the element's bottom will be used as offset.<br />
		 *   **Note**: The element will be taken into account only as long as its `position` is set to
		 *   `fixed`. This option is useful, when dealing with responsive navbars/headers that adjust
		 *   their height and/or positioning according to the viewport's size.
		 *
		 * <br />
		 * <div class="alert alert-warning">
		 * In order for `yOffset` to work properly, scrolling should take place on the document's root and
		 * not some child element.
		 * </div>
		 *
		 * @example
		   <example module="anchorScrollExample" name="anchor-scroll">
			 <file name="index.html">
			   <div id="scrollArea" ng-controller="ScrollController">
				 <a ng-click="gotoBottom()">Go to bottom</a>
				 <a id="bottom"></a> You're at the bottom!
			   </div>
			 </file>
			 <file name="script.js">
			   angular.module('anchorScrollExample', [])
				 .controller('ScrollController', ['$scope', '$location', '$anchorScroll',
				   function($scope, $location, $anchorScroll) {
					 $scope.gotoBottom = function() {
					   // set the location.hash to the id of
					   // the element you wish to scroll to.
					   $location.hash('bottom');
	  
					   // call $anchorScroll()
					   $anchorScroll();
					 };
				   }]);
			 </file>
			 <file name="style.css">
			   #scrollArea {
				 height: 280px;
				 overflow: auto;
			   }
	  
			   #bottom {
				 display: block;
				 margin-top: 2000px;
			   }
			 </file>
		   </example>
		 *
		 * <hr />
		 * The example below illustrates the use of a vertical scroll-offset (specified as a fixed value).
		 * See {@link ng.$anchorScroll#yOffset $anchorScroll.yOffset} for more details.
		 *
		 * @example
		   <example module="anchorScrollOffsetExample" name="anchor-scroll-offset">
			 <file name="index.html">
			   <div class="fixed-header" ng-controller="headerCtrl">
				 <a href="" ng-click="gotoAnchor(x)" ng-repeat="x in [1,2,3,4,5]">
				   Go to anchor {{x}}
				 </a>
			   </div>
			   <div id="anchor{{x}}" class="anchor" ng-repeat="x in [1,2,3,4,5]">
				 Anchor {{x}} of 5
			   </div>
			 </file>
			 <file name="script.js">
			   angular.module('anchorScrollOffsetExample', [])
				 .run(['$anchorScroll', function($anchorScroll) {
				   $anchorScroll.yOffset = 50;   // always scroll by 50 extra pixels
				 }])
				 .controller('headerCtrl', ['$anchorScroll', '$location', '$scope',
				   function($anchorScroll, $location, $scope) {
					 $scope.gotoAnchor = function(x) {
					   var newHash = 'anchor' + x;
					   if ($location.hash() !== newHash) {
						 // set the $location.hash to `newHash` and
						 // $anchorScroll will automatically scroll to it
						 $location.hash('anchor' + x);
					   } else {
						 // call $anchorScroll() explicitly,
						 // since $location.hash hasn't changed
						 $anchorScroll();
					   }
					 };
				   }
				 ]);
			 </file>
			 <file name="style.css">
			   body {
				 padding-top: 50px;
			   }
	  
			   .anchor {
				 border: 2px dashed DarkOrchid;
				 padding: 10px 10px 200px 10px;
			   }
	  
			   .fixed-header {
				 background-color: rgba(0, 0, 0, 0.2);
				 height: 50px;
				 position: fixed;
				 top: 0; left: 0; right: 0;
			   }
	  
			   .fixed-header > a {
				 display: inline-block;
				 margin: 5px 15px;
			   }
			 </file>
		   </example>
		 */
		this.$get = ['$window', '$location', '$rootScope', function ($window, $location, $rootScope) {
			var document = $window.document;

			// Helper function to get first anchor from a NodeList
			// (using `Array#some()` instead of `angular#forEach()` since it's more performant
			//  and working in all supported browsers.)
			function getFirstAnchor(list) {
				var result = null;
				Array.prototype.some.call(list, function (element) {
					if (nodeName_(element) === 'a') {
						result = element;
						return true;
					}
				});
				return result;
			}

			function getYOffset() {

				var offset = scroll.yOffset;

				if (isFunction(offset)) {
					offset = offset();
				} else if (isElement(offset)) {
					var elem = offset[0];
					var style = $window.getComputedStyle(elem);
					if (style.position !== 'fixed') {
						offset = 0;
					} else {
						offset = elem.getBoundingClientRect().bottom;
					}
				} else if (!isNumber(offset)) {
					offset = 0;
				}

				return offset;
			}

			function scrollTo(elem) {
				if (elem) {
					elem.scrollIntoView();

					var offset = getYOffset();

					if (offset) {
						// `offset` is the number of pixels we should scroll UP in order to align `elem` properly.
						// This is true ONLY if the call to `elem.scrollIntoView()` initially aligns `elem` at the
						// top of the viewport.
						//
						// IF the number of pixels from the top of `elem` to the end of the page's content is less
						// than the height of the viewport, then `elem.scrollIntoView()` will align the `elem` some
						// way down the page.
						//
						// This is often the case for elements near the bottom of the page.
						//
						// In such cases we do not need to scroll the whole `offset` up, just the difference between
						// the top of the element and the offset, which is enough to align the top of `elem` at the
						// desired position.
						var elemTop = elem.getBoundingClientRect().top;
						$window.scrollBy(0, elemTop - offset);
					}
				} else {
					$window.scrollTo(0, 0);
				}
			}

			function scroll(hash) {
				// Allow numeric hashes
				hash = isString(hash) ? hash : isNumber(hash) ? hash.toString() : $location.hash();
				var elm;

				// empty hash, scroll to the top of the page
				if (!hash) scrollTo(null);

				// element with given id
				else if ((elm = document.getElementById(hash))) scrollTo(elm);

				// first anchor with given name :-D
				else if ((elm = getFirstAnchor(document.getElementsByName(hash)))) scrollTo(elm);

				// no element and hash === 'top', scroll to the top of the page
				else if (hash === 'top') scrollTo(null);
			}

			// does not scroll when user clicks on anchor link that is currently on
			// (no url change, no $location.hash() change), browser native does scroll
			if (autoScrollingEnabled) {
				$rootScope.$watch(function autoScrollWatch() { return $location.hash(); },
					function autoScrollWatchAction(newVal, oldVal) {
						// skip the initial scroll if $location.hash is empty
						if (newVal === oldVal && newVal === '') return;

						jqLiteDocumentLoaded(function () {
							$rootScope.$evalAsync(scroll);
						});
					});
			}

			return scroll;
		}];
	}

	var $animateMinErr = minErr('$animate');
	var ELEMENT_NODE = 1;
	var NG_ANIMATE_CLASSNAME = 'ng-animate';

	function mergeClasses(a, b) {
		if (!a && !b) return '';
		if (!a) return b;
		if (!b) return a;
		if (isArray(a)) a = a.join(' ');
		if (isArray(b)) b = b.join(' ');
		return a + ' ' + b;
	}

	function extractElementNode(element) {
		for (var i = 0; i < element.length; i++) {
			var elm = element[i];
			if (elm.nodeType === ELEMENT_NODE) {
				return elm;
			}
		}
	}

	function splitClasses(classes) {
		if (isString(classes)) {
			classes = classes.split(' ');
		}

		// Use createMap() to prevent class assumptions involving property names in
		// Object.prototype
		var obj = createMap();
		forEach(classes, function (klass) {
			// sometimes the split leaves empty string values
			// incase extra spaces were applied to the options
			if (klass.length) {
				obj[klass] = true;
			}
		});
		return obj;
	}

	// if any other type of options value besides an Object value is
	// passed into the $animate.method() animation then this helper code
	// will be run which will ignore it. While this patch is not the
	// greatest solution to this, a lot of existing plugins depend on
	// $animate to either call the callback (< 1.2) or return a promise
	// that can be changed. This helper function ensures that the options
	// are wiped clean incase a callback function is provided.
	function prepareAnimateOptions(options) {
		return isObject(options)
			? options
			: {};
	}

	var $$CoreAnimateJsProvider = /** @this */ function () {
		this.$get = noop;
	};

	// this is prefixed with Core since it conflicts with
	// the animateQueueProvider defined in ngAnimate/animateQueue.js
	var $$CoreAnimateQueueProvider = /** @this */ function () {
		var postDigestQueue = new NgMap();
		var postDigestElements = [];

		this.$get = ['$$AnimateRunner', '$rootScope',
			function ($$AnimateRunner, $rootScope) {
				return {
					enabled: noop,
					on: noop,
					off: noop,
					pin: noop,

					push: function (element, event, options, domOperation) {
						if (domOperation) {
							domOperation();
						}

						options = options || {};
						if (options.from) {
							element.css(options.from);
						}
						if (options.to) {
							element.css(options.to);
						}

						if (options.addClass || options.removeClass) {
							addRemoveClassesPostDigest(element, options.addClass, options.removeClass);
						}

						var runner = new $$AnimateRunner();

						// since there are no animations to run the runner needs to be
						// notified that the animation call is complete.
						runner.complete();
						return runner;
					}
				};


				function updateData(data, classes, value) {
					var changed = false;
					if (classes) {
						classes = isString(classes) ? classes.split(' ') :
							isArray(classes) ? classes : [];
						forEach(classes, function (className) {
							if (className) {
								changed = true;
								data[className] = value;
							}
						});
					}
					return changed;
				}

				function handleCSSClassChanges() {
					forEach(postDigestElements, function (element) {
						var data = postDigestQueue.get(element);
						if (data) {
							var existing = splitClasses(element.attr('class'));
							var toAdd = '';
							var toRemove = '';
							forEach(data, function (status, className) {
								var hasClass = !!existing[className];
								if (status !== hasClass) {
									if (status) {
										toAdd += (toAdd.length ? ' ' : '') + className;
									} else {
										toRemove += (toRemove.length ? ' ' : '') + className;
									}
								}
							});

							forEach(element, function (elm) {
								if (toAdd) {
									jqLiteAddClass(elm, toAdd);
								}
								if (toRemove) {
									jqLiteRemoveClass(elm, toRemove);
								}
							});
							postDigestQueue.delete(element);
						}
					});
					postDigestElements.length = 0;
				}


				function addRemoveClassesPostDigest(element, add, remove) {
					var data = postDigestQueue.get(element) || {};

					var classesAdded = updateData(data, add, true);
					var classesRemoved = updateData(data, remove, false);

					if (classesAdded || classesRemoved) {

						postDigestQueue.set(element, data);
						postDigestElements.push(element);

						if (postDigestElements.length === 1) {
							$rootScope.$$postDigest(handleCSSClassChanges);
						}
					}
				}
			}];
	};

	/**
	 * @ngdoc provider
	 * @name $animateProvider
	 *
	 * @description
	 * Default implementation of $animate that doesn't perform any animations, instead just
	 * synchronously performs DOM updates and resolves the returned runner promise.
	 *
	 * In order to enable animations the `ngAnimate` module has to be loaded.
	 *
	 * To see the functional implementation check out `src/ngAnimate/animate.js`.
	 */
	var $AnimateProvider = ['$provide', /** @this */ function ($provide) {
		var provider = this;
		var classNameFilter = null;
		var customFilter = null;

		this.$$registeredAnimations = Object.create(null);

		/**
		* @ngdoc method
		* @name $animateProvider#register
		*
		* @description
		* Registers a new injectable animation factory function. The factory function produces the
		* animation object which contains callback functions for each event that is expected to be
		* animated.
		*
		*   * `eventFn`: `function(element, ... , doneFunction, options)`
		*   The element to animate, the `doneFunction` and the options fed into the animation. Depending
		*   on the type of animation additional arguments will be injected into the animation function. The
		*   list below explains the function signatures for the different animation methods:
		*
		*   - setClass: function(element, addedClasses, removedClasses, doneFunction, options)
		*   - addClass: function(element, addedClasses, doneFunction, options)
		*   - removeClass: function(element, removedClasses, doneFunction, options)
		*   - enter, leave, move: function(element, doneFunction, options)
		*   - animate: function(element, fromStyles, toStyles, doneFunction, options)
		*
		*   Make sure to trigger the `doneFunction` once the animation is fully complete.
		*
		* ```js
		*   return {
		*     //enter, leave, move signature
		*     eventFn : function(element, done, options) {
		*       //code to run the animation
		*       //once complete, then run done()
		*       return function endFunction(wasCancelled) {
		*         //code to cancel the animation
		*       }
		*     }
		*   }
		* ```
		*
		* @param {string} name The name of the animation (this is what the class-based CSS value will be compared to).
		* @param {Function} factory The factory function that will be executed to return the animation
		*                           object.
		*/
		this.register = function (name, factory) {
			if (name && name.charAt(0) !== '.') {
				throw $animateMinErr('notcsel', 'Expecting class selector starting with \'.\' got \'{0}\'.', name);
			}

			var key = name + '-animation';
			provider.$$registeredAnimations[name.substr(1)] = key;
			$provide.factory(key, factory);
		};

		/**
		 * @ngdoc method
		 * @name $animateProvider#customFilter
		 *
		 * @description
		 * Sets and/or returns the custom filter function that is used to "filter" animations, i.e.
		 * determine if an animation is allowed or not. When no filter is specified (the default), no
		 * animation will be blocked. Setting the `customFilter` value will only allow animations for
		 * which the filter function's return value is truthy.
		 *
		 * This allows to easily create arbitrarily complex rules for filtering animations, such as
		 * allowing specific events only, or enabling animations on specific subtrees of the DOM, etc.
		 * Filtering animations can also boost performance for low-powered devices, as well as
		 * applications containing a lot of structural operations.
		 *
		 * <div class="alert alert-success">
		 *   **Best Practice:**
		 *   Keep the filtering function as lean as possible, because it will be called for each DOM
		 *   action (e.g. insertion, removal, class change) performed by "animation-aware" directives.
		 *   See {@link guide/animations#which-directives-support-animations- here} for a list of built-in
		 *   directives that support animations.
		 *   Performing computationally expensive or time-consuming operations on each call of the
		 *   filtering function can make your animations sluggish.
		 * </div>
		 *
		 * **Note:** If present, `customFilter` will be checked before
		 * {@link $animateProvider#classNameFilter classNameFilter}.
		 *
		 * @param {Function=} filterFn - The filter function which will be used to filter all animations.
		 *   If a falsy value is returned, no animation will be performed. The function will be called
		 *   with the following arguments:
		 *   - **node** `{DOMElement}` - The DOM element to be animated.
		 *   - **event** `{String}` - The name of the animation event (e.g. `enter`, `leave`, `addClass`
		 *     etc).
		 *   - **options** `{Object}` - A collection of options/styles used for the animation.
		 * @return {Function} The current filter function or `null` if there is none set.
		 */
		this.customFilter = function (filterFn) {
			if (arguments.length === 1) {
				customFilter = isFunction(filterFn) ? filterFn : null;
			}

			return customFilter;
		};

		/**
		 * @ngdoc method
		 * @name $animateProvider#classNameFilter
		 *
		 * @description
		 * Sets and/or returns the CSS class regular expression that is checked when performing
		 * an animation. Upon bootstrap the classNameFilter value is not set at all and will
		 * therefore enable $animate to attempt to perform an animation on any element that is triggered.
		 * When setting the `classNameFilter` value, animations will only be performed on elements
		 * that successfully match the filter expression. This in turn can boost performance
		 * for low-powered devices as well as applications containing a lot of structural operations.
		 *
		 * **Note:** If present, `classNameFilter` will be checked after
		 * {@link $animateProvider#customFilter customFilter}. If `customFilter` is present and returns
		 * false, `classNameFilter` will not be checked.
		 *
		 * @param {RegExp=} expression The className expression which will be checked against all animations
		 * @return {RegExp} The current CSS className expression value. If null then there is no expression value
		 */
		this.classNameFilter = function (expression) {
			if (arguments.length === 1) {
				classNameFilter = (expression instanceof RegExp) ? expression : null;
				if (classNameFilter) {
					var reservedRegex = new RegExp('[(\\s|\\/)]' + NG_ANIMATE_CLASSNAME + '[(\\s|\\/)]');
					if (reservedRegex.test(classNameFilter.toString())) {
						classNameFilter = null;
						throw $animateMinErr('nongcls', '$animateProvider.classNameFilter(regex) prohibits accepting a regex value which matches/contains the "{0}" CSS class.', NG_ANIMATE_CLASSNAME);
					}
				}
			}
			return classNameFilter;
		};

		this.$get = ['$$animateQueue', function ($$animateQueue) {
			function domInsert(element, parentElement, afterElement) {
				// if for some reason the previous element was removed
				// from the dom sometime before this code runs then let's
				// just stick to using the parent element as the anchor
				if (afterElement) {
					var afterNode = extractElementNode(afterElement);
					if (afterNode && !afterNode.parentNode && !afterNode.previousElementSibling) {
						afterElement = null;
					}
				}
				if (afterElement) {
					afterElement.after(element);
				} else {
					parentElement.prepend(element);
				}
			}

			/**
			 * @ngdoc service
			 * @name $animate
			 * @description The $animate service exposes a series of DOM utility methods that provide support
			 * for animation hooks. The default behavior is the application of DOM operations, however,
			 * when an animation is detected (and animations are enabled), $animate will do the heavy lifting
			 * to ensure that animation runs with the triggered DOM operation.
			 *
			 * By default $animate doesn't trigger any animations. This is because the `ngAnimate` module isn't
			 * included and only when it is active then the animation hooks that `$animate` triggers will be
			 * functional. Once active then all structural `ng-` directives will trigger animations as they perform
			 * their DOM-related operations (enter, leave and move). Other directives such as `ngClass`,
			 * `ngShow`, `ngHide` and `ngMessages` also provide support for animations.
			 *
			 * It is recommended that the`$animate` service is always used when executing DOM-related procedures within directives.
			 *
			 * To learn more about enabling animation support, click here to visit the
			 * {@link ngAnimate ngAnimate module page}.
			 */
			return {
				// we don't call it directly since non-existant arguments may
				// be interpreted as null within the sub enabled function

				/**
				 *
				 * @ngdoc method
				 * @name $animate#on
				 * @kind function
				 * @description Sets up an event listener to fire whenever the animation event (enter, leave, move, etc...)
				 *    has fired on the given element or among any of its children. Once the listener is fired, the provided callback
				 *    is fired with the following params:
				 *
				 * ```js
				 * $animate.on('enter', container,
				 *    function callback(element, phase) {
				 *      // cool we detected an enter animation within the container
				 *    }
				 * );
				 * ```
				 *
				 * <div class="alert alert-warning">
				 * **Note**: Generally, the events that are fired correspond 1:1 to `$animate` method names,
				 * e.g. {@link ng.$animate#addClass addClass()} will fire `addClass`, and {@link ng.ngClass}
				 * will fire `addClass` if classes are added, and `removeClass` if classes are removed.
				 * However, there are two exceptions:
				 *
				 * <ul>
				 *   <li>if both an {@link ng.$animate#addClass addClass()} and a
				 *   {@link ng.$animate#removeClass removeClass()} action are performed during the same
				 *   animation, the event fired will be `setClass`. This is true even for `ngClass`.</li>
				 *   <li>an {@link ng.$animate#animate animate()} call that adds and removes classes will fire
				 *   the `setClass` event, but if it either removes or adds classes,
				 *   it will fire `animate` instead.</li>
				 * </ul>
				 *
				 * </div>
				 *
				 * @param {string} event the animation event that will be captured (e.g. enter, leave, move, addClass, removeClass, etc...)
				 * @param {DOMElement} container the container element that will capture each of the animation events that are fired on itself
				 *     as well as among its children
				 * @param {Function} callback the callback function that will be fired when the listener is triggered.
				 *
				 * The arguments present in the callback function are:
				 * * `element` - The captured DOM element that the animation was fired on.
				 * * `phase` - The phase of the animation. The two possible phases are **start** (when the animation starts) and **close** (when it ends).
				 * * `data` - an object with these properties:
				 *     * addClass - `{string|null}` - space-separated CSS classes to add to the element
				 *     * removeClass - `{string|null}` - space-separated CSS classes to remove from the element
				 *     * from - `{Object|null}` - CSS properties & values at the beginning of the animation
				 *     * to - `{Object|null}` - CSS properties & values at the end of the animation
				 *
				 * Note that the callback does not trigger a scope digest. Wrap your call into a
				 * {@link $rootScope.Scope#$apply scope.$apply} to propagate changes to the scope.
				 */
				on: $$animateQueue.on,

				/**
				 *
				 * @ngdoc method
				 * @name $animate#off
				 * @kind function
				 * @description Deregisters an event listener based on the event which has been associated with the provided element. This method
				 * can be used in three different ways depending on the arguments:
				 *
				 * ```js
				 * // remove all the animation event listeners listening for `enter`
				 * $animate.off('enter');
				 *
				 * // remove listeners for all animation events from the container element
				 * $animate.off(container);
				 *
				 * // remove all the animation event listeners listening for `enter` on the given element and its children
				 * $animate.off('enter', container);
				 *
				 * // remove the event listener function provided by `callback` that is set
				 * // to listen for `enter` on the given `container` as well as its children
				 * $animate.off('enter', container, callback);
				 * ```
				 *
				 * @param {string|DOMElement} event|container the animation event (e.g. enter, leave, move,
				 * addClass, removeClass, etc...), or the container element. If it is the element, all other
				 * arguments are ignored.
				 * @param {DOMElement=} container the container element the event listener was placed on
				 * @param {Function=} callback the callback function that was registered as the listener
				 */
				off: $$animateQueue.off,

				/**
				 * @ngdoc method
				 * @name $animate#pin
				 * @kind function
				 * @description Associates the provided element with a host parent element to allow the element to be animated even if it exists
				 *    outside of the DOM structure of the AngularJS application. By doing so, any animation triggered via `$animate` can be issued on the
				 *    element despite being outside the realm of the application or within another application. Say for example if the application
				 *    was bootstrapped on an element that is somewhere inside of the `<body>` tag, but we wanted to allow for an element to be situated
				 *    as a direct child of `document.body`, then this can be achieved by pinning the element via `$animate.pin(element)`. Keep in mind
				 *    that calling `$animate.pin(element, parentElement)` will not actually insert into the DOM anywhere; it will just create the association.
				 *
				 *    Note that this feature is only active when the `ngAnimate` module is used.
				 *
				 * @param {DOMElement} element the external element that will be pinned
				 * @param {DOMElement} parentElement the host parent element that will be associated with the external element
				 */
				pin: $$animateQueue.pin,

				/**
				 *
				 * @ngdoc method
				 * @name $animate#enabled
				 * @kind function
				 * @description Used to get and set whether animations are enabled or not on the entire application or on an element and its children. This
				 * function can be called in four ways:
				 *
				 * ```js
				 * // returns true or false
				 * $animate.enabled();
				 *
				 * // changes the enabled state for all animations
				 * $animate.enabled(false);
				 * $animate.enabled(true);
				 *
				 * // returns true or false if animations are enabled for an element
				 * $animate.enabled(element);
				 *
				 * // changes the enabled state for an element and its children
				 * $animate.enabled(element, true);
				 * $animate.enabled(element, false);
				 * ```
				 *
				 * @param {DOMElement=} element the element that will be considered for checking/setting the enabled state
				 * @param {boolean=} enabled whether or not the animations will be enabled for the element
				 *
				 * @return {boolean} whether or not animations are enabled
				 */
				enabled: $$animateQueue.enabled,

				/**
				 * @ngdoc method
				 * @name $animate#cancel
				 * @kind function
				 * @description Cancels the provided animation and applies the end state of the animation.
				 * Note that this does not cancel the underlying operation, e.g. the setting of classes or
				 * adding the element to the DOM.
				 *
				 * @param {animationRunner} animationRunner An animation runner returned by an $animate function.
				 *
				 * @example
				  <example module="animationExample" deps="angular-animate.js" animations="true" name="animate-cancel">
					<file name="app.js">
					  angular.module('animationExample', ['ngAnimate']).component('cancelExample', {
						templateUrl: 'template.html',
						controller: function($element, $animate) {
						  this.runner = null;
		  
						  this.addClass = function() {
							this.runner = $animate.addClass($element.find('div'), 'red');
							var ctrl = this;
							this.runner.finally(function() {
							  ctrl.runner = null;
							});
						  };
		  
						  this.removeClass = function() {
							this.runner = $animate.removeClass($element.find('div'), 'red');
							var ctrl = this;
							this.runner.finally(function() {
							  ctrl.runner = null;
							});
						  };
		  
						  this.cancel = function() {
							$animate.cancel(this.runner);
						  };
						}
					  });
					</file>
					<file name="template.html">
					  <p>
						<button id="add" ng-click="$ctrl.addClass()">Add</button>
						<button ng-click="$ctrl.removeClass()">Remove</button>
						<br>
						<button id="cancel" ng-click="$ctrl.cancel()" ng-disabled="!$ctrl.runner">Cancel</button>
						<br>
						<div id="target">CSS-Animated Text</div>
					  </p>
					</file>
					<file name="index.html">
					  <cancel-example></cancel-example>
					</file>
					<file name="style.css">
					  .red-add, .red-remove {
						transition: all 4s cubic-bezier(0.250, 0.460, 0.450, 0.940);
					  }
		  
					  .red,
					  .red-add.red-add-active {
						color: #FF0000;
						font-size: 40px;
					  }
		  
					  .red-remove.red-remove-active {
						font-size: 10px;
						color: black;
					  }
		  
					</file>
				  </example>
				 */
				cancel: function (runner) {
					if (runner.cancel) {
						runner.cancel();
					}
				},

				/**
				 *
				 * @ngdoc method
				 * @name $animate#enter
				 * @kind function
				 * @description Inserts the element into the DOM either after the `after` element (if provided) or
				 *   as the first child within the `parent` element and then triggers an animation.
				 *   A promise is returned that will be resolved during the next digest once the animation
				 *   has completed.
				 *
				 * @param {DOMElement} element the element which will be inserted into the DOM
				 * @param {DOMElement} parent the parent element which will append the element as
				 *   a child (so long as the after element is not present)
				 * @param {DOMElement=} after the sibling element after which the element will be appended
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				enter: function (element, parent, after, options) {
					parent = parent && jqLite(parent);
					after = after && jqLite(after);
					parent = parent || after.parent();
					domInsert(element, parent, after);
					return $$animateQueue.push(element, 'enter', prepareAnimateOptions(options));
				},

				/**
				 *
				 * @ngdoc method
				 * @name $animate#move
				 * @kind function
				 * @description Inserts (moves) the element into its new position in the DOM either after
				 *   the `after` element (if provided) or as the first child within the `parent` element
				 *   and then triggers an animation. A promise is returned that will be resolved
				 *   during the next digest once the animation has completed.
				 *
				 * @param {DOMElement} element the element which will be moved into the new DOM position
				 * @param {DOMElement} parent the parent element which will append the element as
				 *   a child (so long as the after element is not present)
				 * @param {DOMElement=} after the sibling element after which the element will be appended
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				move: function (element, parent, after, options) {
					parent = parent && jqLite(parent);
					after = after && jqLite(after);
					parent = parent || after.parent();
					domInsert(element, parent, after);
					return $$animateQueue.push(element, 'move', prepareAnimateOptions(options));
				},

				/**
				 * @ngdoc method
				 * @name $animate#leave
				 * @kind function
				 * @description Triggers an animation and then removes the element from the DOM.
				 * When the function is called a promise is returned that will be resolved during the next
				 * digest once the animation has completed.
				 *
				 * @param {DOMElement} element the element which will be removed from the DOM
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				leave: function (element, options) {
					return $$animateQueue.push(element, 'leave', prepareAnimateOptions(options), function () {
						element.remove();
					});
				},

				/**
				 * @ngdoc method
				 * @name $animate#addClass
				 * @kind function
				 *
				 * @description Triggers an addClass animation surrounding the addition of the provided CSS class(es). Upon
				 *   execution, the addClass operation will only be handled after the next digest and it will not trigger an
				 *   animation if element already contains the CSS class or if the class is removed at a later step.
				 *   Note that class-based animations are treated differently compared to structural animations
				 *   (like enter, move and leave) since the CSS classes may be added/removed at different points
				 *   depending if CSS or JavaScript animations are used.
				 *
				 * @param {DOMElement} element the element which the CSS classes will be applied to
				 * @param {string} className the CSS class(es) that will be added (multiple classes are separated via spaces)
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} animationRunner the animation runner
				 */
				addClass: function (element, className, options) {
					options = prepareAnimateOptions(options);
					options.addClass = mergeClasses(options.addclass, className);
					return $$animateQueue.push(element, 'addClass', options);
				},

				/**
				 * @ngdoc method
				 * @name $animate#removeClass
				 * @kind function
				 *
				 * @description Triggers a removeClass animation surrounding the removal of the provided CSS class(es). Upon
				 *   execution, the removeClass operation will only be handled after the next digest and it will not trigger an
				 *   animation if element does not contain the CSS class or if the class is added at a later step.
				 *   Note that class-based animations are treated differently compared to structural animations
				 *   (like enter, move and leave) since the CSS classes may be added/removed at different points
				 *   depending if CSS or JavaScript animations are used.
				 *
				 * @param {DOMElement} element the element which the CSS classes will be applied to
				 * @param {string} className the CSS class(es) that will be removed (multiple classes are separated via spaces)
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				removeClass: function (element, className, options) {
					options = prepareAnimateOptions(options);
					options.removeClass = mergeClasses(options.removeClass, className);
					return $$animateQueue.push(element, 'removeClass', options);
				},

				/**
				 * @ngdoc method
				 * @name $animate#setClass
				 * @kind function
				 *
				 * @description Performs both the addition and removal of a CSS classes on an element and (during the process)
				 *    triggers an animation surrounding the class addition/removal. Much like `$animate.addClass` and
				 *    `$animate.removeClass`, `setClass` will only evaluate the classes being added/removed once a digest has
				 *    passed. Note that class-based animations are treated differently compared to structural animations
				 *    (like enter, move and leave) since the CSS classes may be added/removed at different points
				 *    depending if CSS or JavaScript animations are used.
				 *
				 * @param {DOMElement} element the element which the CSS classes will be applied to
				 * @param {string} add the CSS class(es) that will be added (multiple classes are separated via spaces)
				 * @param {string} remove the CSS class(es) that will be removed (multiple classes are separated via spaces)
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				setClass: function (element, add, remove, options) {
					options = prepareAnimateOptions(options);
					options.addClass = mergeClasses(options.addClass, add);
					options.removeClass = mergeClasses(options.removeClass, remove);
					return $$animateQueue.push(element, 'setClass', options);
				},

				/**
				 * @ngdoc method
				 * @name $animate#animate
				 * @kind function
				 *
				 * @description Performs an inline animation on the element which applies the provided to and from CSS styles to the element.
				 * If any detected CSS transition, keyframe or JavaScript matches the provided className value, then the animation will take
				 * on the provided styles. For example, if a transition animation is set for the given className, then the provided `from` and
				 * `to` styles will be applied alongside the given transition. If the CSS style provided in `from` does not have a corresponding
				 * style in `to`, the style in `from` is applied immediately, and no animation is run.
				 * If a JavaScript animation is detected then the provided styles will be given in as function parameters into the `animate`
				 * method (or as part of the `options` parameter):
				 *
				 * ```js
				 * ngModule.animation('.my-inline-animation', function() {
				 *   return {
				 *     animate : function(element, from, to, done, options) {
				 *       //animation
				 *       done();
				 *     }
				 *   }
				 * });
				 * ```
				 *
				 * @param {DOMElement} element the element which the CSS styles will be applied to
				 * @param {object} from the from (starting) CSS styles that will be applied to the element and across the animation.
				 * @param {object} to the to (destination) CSS styles that will be applied to the element and across the animation.
				 * @param {string=} className an optional CSS class that will be applied to the element for the duration of the animation. If
				 *    this value is left as empty then a CSS class of `ng-inline-animate` will be applied to the element.
				 *    (Note that if no animation is detected then this value will not be applied to the element.)
				 * @param {object=} options an optional collection of options/styles that will be applied to the element.
				 *   The object can have the following properties:
				 *
				 *   - **addClass** - `{string}` - space-separated CSS classes to add to element
				 *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
				 *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
				 *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
				 *
				 * @return {Runner} the animation runner
				 */
				animate: function (element, from, to, className, options) {
					options = prepareAnimateOptions(options);
					options.from = options.from ? extend(options.from, from) : from;
					options.to = options.to ? extend(options.to, to) : to;

					className = className || 'ng-inline-animate';
					options.tempClasses = mergeClasses(options.tempClasses, className);
					return $$animateQueue.push(element, 'animate', options);
				}
			};
		}];
	}];

	var $$AnimateAsyncRunFactoryProvider = /** @this */ function () {
		this.$get = ['$$rAF', function ($$rAF) {
			var waitQueue = [];

			function waitForTick(fn) {
				waitQueue.push(fn);
				if (waitQueue.length > 1) return;
				$$rAF(function () {
					for (var i = 0; i < waitQueue.length; i++) {
						waitQueue[i]();
					}
					waitQueue = [];
				});
			}

			return function () {
				var passed = false;
				waitForTick(function () {
					passed = true;
				});
				return function (callback) {
					if (passed) {
						callback();
					} else {
						waitForTick(callback);
					}
				};
			};
		}];
	};

	var $$AnimateRunnerFactoryProvider = /** @this */ function () {
		this.$get = ['$q', '$sniffer', '$$animateAsyncRun', '$$isDocumentHidden', '$timeout',
			function ($q, $sniffer, $$animateAsyncRun, $$isDocumentHidden, $timeout) {

				var INITIAL_STATE = 0;
				var DONE_PENDING_STATE = 1;
				var DONE_COMPLETE_STATE = 2;

				AnimateRunner.chain = function (chain, callback) {
					var index = 0;

					next();
					function next() {
						if (index === chain.length) {
							callback(true);
							return;
						}

						chain[index](function (response) {
							if (response === false) {
								callback(false);
								return;
							}
							index++;
							next();
						});
					}
				};

				AnimateRunner.all = function (runners, callback) {
					var count = 0;
					var status = true;
					forEach(runners, function (runner) {
						runner.done(onProgress);
					});

					function onProgress(response) {
						status = status && response;
						if (++count === runners.length) {
							callback(status);
						}
					}
				};

				function AnimateRunner(host) {
					this.setHost(host);

					var rafTick = $$animateAsyncRun();
					var timeoutTick = function (fn) {
						$timeout(fn, 0, false);
					};

					this._doneCallbacks = [];
					this._tick = function (fn) {
						if ($$isDocumentHidden()) {
							timeoutTick(fn);
						} else {
							rafTick(fn);
						}
					};
					this._state = 0;
				}

				AnimateRunner.prototype = {
					setHost: function (host) {
						this.host = host || {};
					},

					done: function (fn) {
						if (this._state === DONE_COMPLETE_STATE) {
							fn();
						} else {
							this._doneCallbacks.push(fn);
						}
					},

					progress: noop,

					getPromise: function () {
						if (!this.promise) {
							var self = this;
							this.promise = $q(function (resolve, reject) {
								self.done(function (status) {
									if (status === false) {
										reject();
									} else {
										resolve();
									}
								});
							});
						}
						return this.promise;
					},

					then: function (resolveHandler, rejectHandler) {
						return this.getPromise().then(resolveHandler, rejectHandler);
					},

					'catch': function (handler) {
						return this.getPromise()['catch'](handler);
					},

					'finally': function (handler) {
						return this.getPromise()['finally'](handler);
					},

					pause: function () {
						if (this.host.pause) {
							this.host.pause();
						}
					},

					resume: function () {
						if (this.host.resume) {
							this.host.resume();
						}
					},

					end: function () {
						if (this.host.end) {
							this.host.end();
						}
						this._resolve(true);
					},

					cancel: function () {
						if (this.host.cancel) {
							this.host.cancel();
						}
						this._resolve(false);
					},

					complete: function (response) {
						var self = this;
						if (self._state === INITIAL_STATE) {
							self._state = DONE_PENDING_STATE;
							self._tick(function () {
								self._resolve(response);
							});
						}
					},

					_resolve: function (response) {
						if (this._state !== DONE_COMPLETE_STATE) {
							forEach(this._doneCallbacks, function (fn) {
								fn(response);
							});
							this._doneCallbacks.length = 0;
							this._state = DONE_COMPLETE_STATE;
						}
					}
				};

				return AnimateRunner;
			}];
	};

	/* exported $CoreAnimateCssProvider */

	/**
	 * @ngdoc service
	 * @name $animateCss
	 * @kind object
	 * @this
	 *
	 * @description
	 * This is the core version of `$animateCss`. By default, only when the `ngAnimate` is included,
	 * then the `$animateCss` service will actually perform animations.
	 *
	 * Click here {@link ngAnimate.$animateCss to read the documentation for $animateCss}.
	 */
	var $CoreAnimateCssProvider = function () {
		this.$get = ['$$rAF', '$q', '$$AnimateRunner', function ($$rAF, $q, $$AnimateRunner) {

			return function (element, initialOptions) {
				// all of the animation functions should create
				// a copy of the options data, however, if a
				// parent service has already created a copy then
				// we should stick to using that
				var options = initialOptions || {};
				if (!options.$$prepared) {
					options = copy(options);
				}

				// there is no point in applying the styles since
				// there is no animation that goes on at all in
				// this version of $animateCss.
				if (options.cleanupStyles) {
					options.from = options.to = null;
				}

				if (options.from) {
					element.css(options.from);
					options.from = null;
				}

				var closed, runner = new $$AnimateRunner();
				return {
					start: run,
					end: run
				};

				function run() {
					$$rAF(function () {
						applyAnimationContents();
						if (!closed) {
							runner.complete();
						}
						closed = true;
					});
					return runner;
				}

				function applyAnimationContents() {
					if (options.addClass) {
						element.addClass(options.addClass);
						options.addClass = null;
					}
					if (options.removeClass) {
						element.removeClass(options.removeClass);
						options.removeClass = null;
					}
					if (options.to) {
						element.css(options.to);
						options.to = null;
					}
				}
			};
		}];
	};

	/* global getHash: true, stripHash: false */

	function getHash(url) {
		var index = url.indexOf('#');
		return index === -1 ? '' : url.substr(index);
	}

	function trimEmptyHash(url) {
		return url.replace(/#$/, '');
	}

	/**
	 * ! This is a private undocumented service !
	 *
	 * @name $browser
	 * @requires $log
	 * @description
	 * This object has two goals:
	 *
	 * - hide all the global state in the browser caused by the window object
	 * - abstract away all the browser specific features and inconsistencies
	 *
	 * For tests we provide {@link ngMock.$browser mock implementation} of the `$browser`
	 * service, which can be used for convenient testing of the application without the interaction with
	 * the real browser apis.
	 */
	/**
	 * @param {object} window The global window object.
	 * @param {object} document jQuery wrapped document.
	 * @param {object} $log window.console or an object with the same interface.
	 * @param {object} $sniffer $sniffer service
	 */
	function Browser(window, document, $log, $sniffer, $$taskTrackerFactory) {
		var self = this,
			location = window.location,
			history = window.history,
			setTimeout = window.setTimeout,
			clearTimeout = window.clearTimeout,
			pendingDeferIds = {},
			taskTracker = $$taskTrackerFactory($log);

		self.isMock = false;

		//////////////////////////////////////////////////////////////
		// Task-tracking API
		//////////////////////////////////////////////////////////////

		// TODO(vojta): remove this temporary api
		self.$$completeOutstandingRequest = taskTracker.completeTask;
		self.$$incOutstandingRequestCount = taskTracker.incTaskCount;

		// TODO(vojta): prefix this method with $$ ?
		self.notifyWhenNoOutstandingRequests = taskTracker.notifyWhenNoPendingTasks;

		//////////////////////////////////////////////////////////////
		// URL API
		//////////////////////////////////////////////////////////////

		var cachedState, lastHistoryState,
			lastBrowserUrl = location.href,
			baseElement = document.find('base'),
			pendingLocation = null,
			getCurrentState = !$sniffer.history ? noop : function getCurrentState() {
				try {
					return history.state;
				} catch (e) {
					// MSIE can reportedly throw when there is no state (UNCONFIRMED).
				}
			};

		cacheState();

		/**
		 * @name $browser#url
		 *
		 * @description
		 * GETTER:
		 * Without any argument, this method just returns current value of `location.href` (with a
		 * trailing `#` stripped of if the hash is empty).
		 *
		 * SETTER:
		 * With at least one argument, this method sets url to new value.
		 * If html5 history api supported, `pushState`/`replaceState` is used, otherwise
		 * `location.href`/`location.replace` is used.
		 * Returns its own instance to allow chaining.
		 *
		 * NOTE: this api is intended for use only by the `$location` service. Please use the
		 * {@link ng.$location $location service} to change url.
		 *
		 * @param {string} url New url (when used as setter)
		 * @param {boolean=} replace Should new url replace current history record?
		 * @param {object=} state State object to use with `pushState`/`replaceState`
		 */
		self.url = function (url, replace, state) {
			// In modern browsers `history.state` is `null` by default; treating it separately
			// from `undefined` would cause `$browser.url('/foo')` to change `history.state`
			// to undefined via `pushState`. Instead, let's change `undefined` to `null` here.
			if (isUndefined(state)) {
				state = null;
			}

			// Android Browser BFCache causes location, history reference to become stale.
			if (location !== window.location) location = window.location;
			if (history !== window.history) history = window.history;

			// setter
			if (url) {
				var sameState = lastHistoryState === state;

				// Normalize the inputted URL
				url = urlResolve(url).href;

				// Don't change anything if previous and current URLs and states match. This also prevents
				// IE<10 from getting into redirect loop when in LocationHashbangInHtml5Url mode.
				// See https://github.com/angular/angular.js/commit/ffb2701
				if (lastBrowserUrl === url && (!$sniffer.history || sameState)) {
					return self;
				}
				var sameBase = lastBrowserUrl && stripHash(lastBrowserUrl) === stripHash(url);
				lastBrowserUrl = url;
				lastHistoryState = state;
				// Don't use history API if only the hash changed
				// due to a bug in IE10/IE11 which leads
				// to not firing a `hashchange` nor `popstate` event
				// in some cases (see #9143).
				if ($sniffer.history && (!sameBase || !sameState)) {
					history[replace ? 'replaceState' : 'pushState'](state, '', url);
					cacheState();
				} else {
					if (!sameBase) {
						pendingLocation = url;
					}
					if (replace) {
						location.replace(url);
					} else if (!sameBase) {
						location.href = url;
					} else {
						location.hash = getHash(url);
					}
					if (location.href !== url) {
						pendingLocation = url;
					}
				}
				if (pendingLocation) {
					pendingLocation = url;
				}
				return self;
				// getter
			} else {
				// - pendingLocation is needed as browsers don't allow to read out
				//   the new location.href if a reload happened or if there is a bug like in iOS 9 (see
				//   https://openradar.appspot.com/22186109).
				return trimEmptyHash(pendingLocation || location.href);
			}
		};

		/**
		 * @name $browser#state
		 *
		 * @description
		 * This method is a getter.
		 *
		 * Return history.state or null if history.state is undefined.
		 *
		 * @returns {object} state
		 */
		self.state = function () {
			return cachedState;
		};

		var urlChangeListeners = [],
			urlChangeInit = false;

		function cacheStateAndFireUrlChange() {
			pendingLocation = null;
			fireStateOrUrlChange();
		}

		// This variable should be used *only* inside the cacheState function.
		var lastCachedState = null;
		function cacheState() {
			// This should be the only place in $browser where `history.state` is read.
			cachedState = getCurrentState();
			cachedState = isUndefined(cachedState) ? null : cachedState;

			// Prevent callbacks fo fire twice if both hashchange & popstate were fired.
			if (equals(cachedState, lastCachedState)) {
				cachedState = lastCachedState;
			}

			lastCachedState = cachedState;
			lastHistoryState = cachedState;
		}

		function fireStateOrUrlChange() {
			var prevLastHistoryState = lastHistoryState;
			cacheState();

			if (lastBrowserUrl === self.url() && prevLastHistoryState === cachedState) {
				return;
			}

			lastBrowserUrl = self.url();
			lastHistoryState = cachedState;
			forEach(urlChangeListeners, function (listener) {
				listener(self.url(), cachedState);
			});
		}

		/**
		 * @name $browser#onUrlChange
		 *
		 * @description
		 * Register callback function that will be called, when url changes.
		 *
		 * It's only called when the url is changed from outside of AngularJS:
		 * - user types different url into address bar
		 * - user clicks on history (forward/back) button
		 * - user clicks on a link
		 *
		 * It's not called when url is changed by $browser.url() method
		 *
		 * The listener gets called with new url as parameter.
		 *
		 * NOTE: this api is intended for use only by the $location service. Please use the
		 * {@link ng.$location $location service} to monitor url changes in AngularJS apps.
		 *
		 * @param {function(string)} listener Listener function to be called when url changes.
		 * @return {function(string)} Returns the registered listener fn - handy if the fn is anonymous.
		 */
		self.onUrlChange = function (callback) {
			// TODO(vojta): refactor to use node's syntax for events
			if (!urlChangeInit) {
				// We listen on both (hashchange/popstate) when available, as some browsers don't
				// fire popstate when user changes the address bar and don't fire hashchange when url
				// changed by push/replaceState

				// html5 history api - popstate event
				if ($sniffer.history) jqLite(window).on('popstate', cacheStateAndFireUrlChange);
				// hashchange event
				jqLite(window).on('hashchange', cacheStateAndFireUrlChange);

				urlChangeInit = true;
			}

			urlChangeListeners.push(callback);
			return callback;
		};

		/**
		 * @private
		 * Remove popstate and hashchange handler from window.
		 *
		 * NOTE: this api is intended for use only by $rootScope.
		 */
		self.$$applicationDestroyed = function () {
			jqLite(window).off('hashchange popstate', cacheStateAndFireUrlChange);
		};

		/**
		 * Checks whether the url has changed outside of AngularJS.
		 * Needs to be exported to be able to check for changes that have been done in sync,
		 * as hashchange/popstate events fire in async.
		 */
		self.$$checkUrlChange = fireStateOrUrlChange;

		//////////////////////////////////////////////////////////////
		// Misc API
		//////////////////////////////////////////////////////////////

		/**
		 * @name $browser#baseHref
		 *
		 * @description
		 * Returns current <base href>
		 * (always relative - without domain)
		 *
		 * @returns {string} The current base href
		 */
		self.baseHref = function () {
			var href = baseElement.attr('href');
			return href ? href.replace(/^(https?:)?\/\/[^/]*/, '') : '';
		};

		/**
		 * @name $browser#defer
		 * @param {function()} fn A function, who's execution should be deferred.
		 * @param {number=} [delay=0] Number of milliseconds to defer the function execution.
		 * @param {string=} [taskType=DEFAULT_TASK_TYPE] The type of task that is deferred.
		 * @returns {*} DeferId that can be used to cancel the task via `$browser.defer.cancel()`.
		 *
		 * @description
		 * Executes a fn asynchronously via `setTimeout(fn, delay)`.
		 *
		 * Unlike when calling `setTimeout` directly, in test this function is mocked and instead of using
		 * `setTimeout` in tests, the fns are queued in an array, which can be programmatically flushed
		 * via `$browser.defer.flush()`.
		 *
		 */
		self.defer = function (fn, delay, taskType) {
			var timeoutId;

			delay = delay || 0;
			taskType = taskType || taskTracker.DEFAULT_TASK_TYPE;

			taskTracker.incTaskCount(taskType);
			timeoutId = setTimeout(function () {
				delete pendingDeferIds[timeoutId];
				taskTracker.completeTask(fn, taskType);
			}, delay);
			pendingDeferIds[timeoutId] = taskType;

			return timeoutId;
		};


		/**
		 * @name $browser#defer.cancel
		 *
		 * @description
		 * Cancels a deferred task identified with `deferId`.
		 *
		 * @param {*} deferId Token returned by the `$browser.defer` function.
		 * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
		 *                    canceled.
		 */
		self.defer.cancel = function (deferId) {
			if (pendingDeferIds.hasOwnProperty(deferId)) {
				var taskType = pendingDeferIds[deferId];
				delete pendingDeferIds[deferId];
				clearTimeout(deferId);
				taskTracker.completeTask(noop, taskType);
				return true;
			}
			return false;
		};

	}

	/** @this */
	function $BrowserProvider() {
		this.$get = ['$window', '$log', '$sniffer', '$document', '$$taskTrackerFactory',
			function ($window, $log, $sniffer, $document, $$taskTrackerFactory) {
				return new Browser($window, $document, $log, $sniffer, $$taskTrackerFactory);
			}];
	}

	/**
	 * @ngdoc service
	 * @name $cacheFactory
	 * @this
	 *
	 * @description
	 * Factory that constructs {@link $cacheFactory.Cache Cache} objects and gives access to
	 * them.
	 *
	 * ```js
	 *
	 *  var cache = $cacheFactory('cacheId');
	 *  expect($cacheFactory.get('cacheId')).toBe(cache);
	 *  expect($cacheFactory.get('noSuchCacheId')).not.toBeDefined();
	 *
	 *  cache.put("key", "value");
	 *  cache.put("another key", "another value");
	 *
	 *  // We've specified no options on creation
	 *  expect(cache.info()).toEqual({id: 'cacheId', size: 2});
	 *
	 * ```
	 *
	 *
	 * @param {string} cacheId Name or id of the newly created cache.
	 * @param {object=} options Options object that specifies the cache behavior. Properties:
	 *
	 *   - `{number=}` `capacity` — turns the cache into LRU cache.
	 *
	 * @returns {object} Newly created cache object with the following set of methods:
	 *
	 * - `{object}` `info()` — Returns id, size, and options of cache.
	 * - `{{*}}` `put({string} key, {*} value)` — Puts a new key-value pair into the cache and returns
	 *   it.
	 * - `{{*}}` `get({string} key)` — Returns cached value for `key` or undefined for cache miss.
	 * - `{void}` `remove({string} key)` — Removes a key-value pair from the cache.
	 * - `{void}` `removeAll()` — Removes all cached values.
	 * - `{void}` `destroy()` — Removes references to this cache from $cacheFactory.
	 *
	 * @example
	   <example module="cacheExampleApp" name="cache-factory">
		 <file name="index.html">
		   <div ng-controller="CacheController">
			 <input ng-model="newCacheKey" placeholder="Key">
			 <input ng-model="newCacheValue" placeholder="Value">
			 <button ng-click="put(newCacheKey, newCacheValue)">Cache</button>
	
			 <p ng-if="keys.length">Cached Values</p>
			 <div ng-repeat="key in keys">
			   <span ng-bind="key"></span>
			   <span>: </span>
			   <b ng-bind="cache.get(key)"></b>
			 </div>
	
			 <p>Cache Info</p>
			 <div ng-repeat="(key, value) in cache.info()">
			   <span ng-bind="key"></span>
			   <span>: </span>
			   <b ng-bind="value"></b>
			 </div>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('cacheExampleApp', []).
			 controller('CacheController', ['$scope', '$cacheFactory', function($scope, $cacheFactory) {
			   $scope.keys = [];
			   $scope.cache = $cacheFactory('cacheId');
			   $scope.put = function(key, value) {
				 if (angular.isUndefined($scope.cache.get(key))) {
				   $scope.keys.push(key);
				 }
				 $scope.cache.put(key, angular.isUndefined(value) ? null : value);
			   };
			 }]);
		 </file>
		 <file name="style.css">
		   p {
			 margin: 10px 0 3px;
		   }
		 </file>
	   </example>
	 */
	function $CacheFactoryProvider() {

		this.$get = function () {
			var caches = {};

			function cacheFactory(cacheId, options) {
				if (cacheId in caches) {
					throw minErr('$cacheFactory')('iid', 'CacheId \'{0}\' is already taken!', cacheId);
				}

				var size = 0,
					stats = extend({}, options, { id: cacheId }),
					data = createMap(),
					capacity = (options && options.capacity) || Number.MAX_VALUE,
					lruHash = createMap(),
					freshEnd = null,
					staleEnd = null;

				/**
				 * @ngdoc type
				 * @name $cacheFactory.Cache
				 *
				 * @description
				 * A cache object used to store and retrieve data, primarily used by
				 * {@link $templateRequest $templateRequest} and the {@link ng.directive:script script}
				 * directive to cache templates and other data.
				 *
				 * ```js
				 *  angular.module('superCache')
				 *    .factory('superCache', ['$cacheFactory', function($cacheFactory) {
				 *      return $cacheFactory('super-cache');
				 *    }]);
				 * ```
				 *
				 * Example test:
				 *
				 * ```js
				 *  it('should behave like a cache', inject(function(superCache) {
				 *    superCache.put('key', 'value');
				 *    superCache.put('another key', 'another value');
				 *
				 *    expect(superCache.info()).toEqual({
				 *      id: 'super-cache',
				 *      size: 2
				 *    });
				 *
				 *    superCache.remove('another key');
				 *    expect(superCache.get('another key')).toBeUndefined();
				 *
				 *    superCache.removeAll();
				 *    expect(superCache.info()).toEqual({
				 *      id: 'super-cache',
				 *      size: 0
				 *    });
				 *  }));
				 * ```
				 */
				return (caches[cacheId] = {

					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#put
					 * @kind function
					 *
					 * @description
					 * Inserts a named entry into the {@link $cacheFactory.Cache Cache} object to be
					 * retrieved later, and incrementing the size of the cache if the key was not already
					 * present in the cache. If behaving like an LRU cache, it will also remove stale
					 * entries from the set.
					 *
					 * It will not insert undefined values into the cache.
					 *
					 * @param {string} key the key under which the cached data is stored.
					 * @param {*} value the value to store alongside the key. If it is undefined, the key
					 *    will not be stored.
					 * @returns {*} the value stored.
					 */
					put: function (key, value) {
						if (isUndefined(value)) return;
						if (capacity < Number.MAX_VALUE) {
							var lruEntry = lruHash[key] || (lruHash[key] = { key: key });

							refresh(lruEntry);
						}

						if (!(key in data)) size++;
						data[key] = value;

						if (size > capacity) {
							this.remove(staleEnd.key);
						}

						return value;
					},

					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#get
					 * @kind function
					 *
					 * @description
					 * Retrieves named data stored in the {@link $cacheFactory.Cache Cache} object.
					 *
					 * @param {string} key the key of the data to be retrieved
					 * @returns {*} the value stored.
					 */
					get: function (key) {
						if (capacity < Number.MAX_VALUE) {
							var lruEntry = lruHash[key];

							if (!lruEntry) return;

							refresh(lruEntry);
						}

						return data[key];
					},


					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#remove
					 * @kind function
					 *
					 * @description
					 * Removes an entry from the {@link $cacheFactory.Cache Cache} object.
					 *
					 * @param {string} key the key of the entry to be removed
					 */
					remove: function (key) {
						if (capacity < Number.MAX_VALUE) {
							var lruEntry = lruHash[key];

							if (!lruEntry) return;

							if (lruEntry === freshEnd) freshEnd = lruEntry.p;
							if (lruEntry === staleEnd) staleEnd = lruEntry.n;
							link(lruEntry.n, lruEntry.p);

							delete lruHash[key];
						}

						if (!(key in data)) return;

						delete data[key];
						size--;
					},


					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#removeAll
					 * @kind function
					 *
					 * @description
					 * Clears the cache object of any entries.
					 */
					removeAll: function () {
						data = createMap();
						size = 0;
						lruHash = createMap();
						freshEnd = staleEnd = null;
					},


					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#destroy
					 * @kind function
					 *
					 * @description
					 * Destroys the {@link $cacheFactory.Cache Cache} object entirely,
					 * removing it from the {@link $cacheFactory $cacheFactory} set.
					 */
					destroy: function () {
						data = null;
						stats = null;
						lruHash = null;
						delete caches[cacheId];
					},


					/**
					 * @ngdoc method
					 * @name $cacheFactory.Cache#info
					 * @kind function
					 *
					 * @description
					 * Retrieve information regarding a particular {@link $cacheFactory.Cache Cache}.
					 *
					 * @returns {object} an object with the following properties:
					 *   <ul>
					 *     <li>**id**: the id of the cache instance</li>
					 *     <li>**size**: the number of entries kept in the cache instance</li>
					 *     <li>**...**: any additional properties from the options object when creating the
					 *       cache.</li>
					 *   </ul>
					 */
					info: function () {
						return extend({}, stats, { size: size });
					}
				});


				/**
				 * makes the `entry` the freshEnd of the LRU linked list
				 */
				function refresh(entry) {
					if (entry !== freshEnd) {
						if (!staleEnd) {
							staleEnd = entry;
						} else if (staleEnd === entry) {
							staleEnd = entry.n;
						}

						link(entry.n, entry.p);
						link(entry, freshEnd);
						freshEnd = entry;
						freshEnd.n = null;
					}
				}


				/**
				 * bidirectionally links two entries of the LRU linked list
				 */
				function link(nextEntry, prevEntry) {
					if (nextEntry !== prevEntry) {
						if (nextEntry) nextEntry.p = prevEntry; //p stands for previous, 'prev' didn't minify
						if (prevEntry) prevEntry.n = nextEntry; //n stands for next, 'next' didn't minify
					}
				}
			}


			/**
			 * @ngdoc method
			 * @name $cacheFactory#info
			 *
			 * @description
			 * Get information about all the caches that have been created
			 *
			 * @returns {Object} - key-value map of `cacheId` to the result of calling `cache#info`
			 */
			cacheFactory.info = function () {
				var info = {};
				forEach(caches, function (cache, cacheId) {
					info[cacheId] = cache.info();
				});
				return info;
			};


			/**
			 * @ngdoc method
			 * @name $cacheFactory#get
			 *
			 * @description
			 * Get access to a cache object by the `cacheId` used when it was created.
			 *
			 * @param {string} cacheId Name or id of a cache to access.
			 * @returns {object} Cache object identified by the cacheId or undefined if no such cache.
			 */
			cacheFactory.get = function (cacheId) {
				return caches[cacheId];
			};


			return cacheFactory;
		};
	}

	/**
	 * @ngdoc service
	 * @name $templateCache
	 * @this
	 *
	 * @description
	 * `$templateCache` is a {@link $cacheFactory.Cache Cache object} created by the
	 * {@link ng.$cacheFactory $cacheFactory}.
	 *
	 * The first time a template is used, it is loaded in the template cache for quick retrieval. You
	 * can load templates directly into the cache in a `script` tag, by using {@link $templateRequest},
	 * or by consuming the `$templateCache` service directly.
	 *
	 * Adding via the `script` tag:
	 *
	 * ```html
	 *   <script type="text/ng-template" id="templateId.html">
	 *     <p>This is the content of the template</p>
	 *   </script>
	 * ```
	 *
	 * **Note:** the `script` tag containing the template does not need to be included in the `head` of
	 * the document, but it must be a descendent of the {@link ng.$rootElement $rootElement} (e.g.
	 * element with {@link ngApp} attribute), otherwise the template will be ignored.
	 *
	 * Adding via the `$templateCache` service:
	 *
	 * ```js
	 * var myApp = angular.module('myApp', []);
	 * myApp.run(function($templateCache) {
	 *   $templateCache.put('templateId.html', 'This is the content of the template');
	 * });
	 * ```
	 *
	 * To retrieve the template later, simply use it in your component:
	 * ```js
	 * myApp.component('myComponent', {
	 *    templateUrl: 'templateId.html'
	 * });
	 * ```
	 *
	 * or get it via the `$templateCache` service:
	 * ```js
	 * $templateCache.get('templateId.html')
	 * ```
	 *
	 */
	function $TemplateCacheProvider() {
		this.$get = ['$cacheFactory', function ($cacheFactory) {
			return $cacheFactory('templates');
		}];
	}

	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 *     Any commits to this file should be reviewed with security in mind.  *
	 *   Changes to this file can potentially create security vulnerabilities. *
	 *          An approval from 2 Core members with history of modifying      *
	 *                         this file is required.                          *
	 *                                                                         *
	 *  Does the change somehow allow for arbitrary javascript to be executed? *
	 *    Or allows for someone to change the prototype of built-in objects?   *
	 *     Or gives undesired access to variables like document or window?    *
	 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

	/* ! VARIABLE/FUNCTION NAMING CONVENTIONS THAT APPLY TO THIS FILE!
	 *
	 * DOM-related variables:
	 *
	 * - "node" - DOM Node
	 * - "element" - DOM Element or Node
	 * - "$node" or "$element" - jqLite-wrapped node or element
	 *
	 *
	 * Compiler related stuff:
	 *
	 * - "linkFn" - linking fn of a single directive
	 * - "nodeLinkFn" - function that aggregates all linking fns for a particular node
	 * - "childLinkFn" -  function that aggregates all linking fns for child nodes of a particular node
	 * - "compositeLinkFn" - function that aggregates all linking fns for a compilation root (nodeList)
	 */


	/**
	 * @ngdoc service
	 * @name $compile
	 * @kind function
	 *
	 * @description
	 * Compiles an HTML string or DOM into a template and produces a template function, which
	 * can then be used to link {@link ng.$rootScope.Scope `scope`} and the template together.
	 *
	 * The compilation is a process of walking the DOM tree and matching DOM elements to
	 * {@link ng.$compileProvider#directive directives}.
	 *
	 * <div class="alert alert-warning">
	 * **Note:** This document is an in-depth reference of all directive options.
	 * For a gentle introduction to directives with examples of common use cases,
	 * see the {@link guide/directive directive guide}.
	 * </div>
	 *
	 * ## Comprehensive Directive API
	 *
	 * There are many different options for a directive.
	 *
	 * The difference resides in the return value of the factory function.
	 * You can either return a {@link $compile#directive-definition-object Directive Definition Object (see below)}
	 * that defines the directive properties, or just the `postLink` function (all other properties will have
	 * the default values).
	 *
	 * <div class="alert alert-success">
	 * **Best Practice:** It's recommended to use the "directive definition object" form.
	 * </div>
	 *
	 * Here's an example directive declared with a Directive Definition Object:
	 *
	 * ```js
	 *   var myModule = angular.module(...);
	 *
	 *   myModule.directive('directiveName', function factory(injectables) {
	 *     var directiveDefinitionObject = {
	 *       {@link $compile#-priority- priority}: 0,
	 *       {@link $compile#-template- template}: '<div></div>', // or // function(tElement, tAttrs) { ... },
	 *       // or
	 *       // {@link $compile#-templateurl- templateUrl}: 'directive.html', // or // function(tElement, tAttrs) { ... },
	 *       {@link $compile#-transclude- transclude}: false,
	 *       {@link $compile#-restrict- restrict}: 'A',
	 *       {@link $compile#-templatenamespace- templateNamespace}: 'html',
	 *       {@link $compile#-scope- scope}: false,
	 *       {@link $compile#-controller- controller}: function($scope, $element, $attrs, $transclude, otherInjectables) { ... },
	 *       {@link $compile#-controlleras- controllerAs}: 'stringIdentifier',
	 *       {@link $compile#-bindtocontroller- bindToController}: false,
	 *       {@link $compile#-require- require}: 'siblingDirectiveName', // or // ['^parentDirectiveName', '?optionalDirectiveName', '?^optionalParent'],
	 *       {@link $compile#-multielement- multiElement}: false,
	 *       {@link $compile#-compile- compile}: function compile(tElement, tAttrs, transclude) {
	 *         return {
	 *            {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
	 *            {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
	 *         }
	 *         // or
	 *         // return function postLink( ... ) { ... }
	 *       },
	 *       // or
	 *       // {@link $compile#-link- link}: {
	 *       //  {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
	 *       //  {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
	 *       // }
	 *       // or
	 *       // {@link $compile#-link- link}: function postLink( ... ) { ... }
	 *     };
	 *     return directiveDefinitionObject;
	 *   });
	 * ```
	 *
	 * <div class="alert alert-warning">
	 * **Note:** Any unspecified options will use the default value. You can see the default values below.
	 * </div>
	 *
	 * Therefore the above can be simplified as:
	 *
	 * ```js
	 *   var myModule = angular.module(...);
	 *
	 *   myModule.directive('directiveName', function factory(injectables) {
	 *     var directiveDefinitionObject = {
	 *       link: function postLink(scope, iElement, iAttrs) { ... }
	 *     };
	 *     return directiveDefinitionObject;
	 *     // or
	 *     // return function postLink(scope, iElement, iAttrs) { ... }
	 *   });
	 * ```
	 *
	 * ### Life-cycle hooks
	 * Directive controllers can provide the following methods that are called by AngularJS at points in the life-cycle of the
	 * directive:
	 * * `$onInit()` - Called on each controller after all the controllers on an element have been constructed and
	 *   had their bindings initialized (and before the pre &amp; post linking functions for the directives on
	 *   this element). This is a good place to put initialization code for your controller.
	 * * `$onChanges(changesObj)` - Called whenever one-way (`<`) or interpolation (`@`) bindings are updated. The
	 *   `changesObj` is a hash whose keys are the names of the bound properties that have changed, and the values are an
	 *   object of the form `{ currentValue, previousValue, isFirstChange() }`. Use this hook to trigger updates within a
	 *   component such as cloning the bound value to prevent accidental mutation of the outer value. Note that this will
	 *   also be called when your bindings are initialized.
	 * * `$doCheck()` - Called on each turn of the digest cycle. Provides an opportunity to detect and act on
	 *   changes. Any actions that you wish to take in response to the changes that you detect must be
	 *   invoked from this hook; implementing this has no effect on when `$onChanges` is called. For example, this hook
	 *   could be useful if you wish to perform a deep equality check, or to check a Date object, changes to which would not
	 *   be detected by AngularJS's change detector and thus not trigger `$onChanges`. This hook is invoked with no arguments;
	 *   if detecting changes, you must store the previous value(s) for comparison to the current values.
	 * * `$onDestroy()` - Called on a controller when its containing scope is destroyed. Use this hook for releasing
	 *   external resources, watches and event handlers. Note that components have their `$onDestroy()` hooks called in
	 *   the same order as the `$scope.$broadcast` events are triggered, which is top down. This means that parent
	 *   components will have their `$onDestroy()` hook called before child components.
	 * * `$postLink()` - Called after this controller's element and its children have been linked. Similar to the post-link
	 *   function this hook can be used to set up DOM event handlers and do direct DOM manipulation.
	 *   Note that child elements that contain `templateUrl` directives will not have been compiled and linked since
	 *   they are waiting for their template to load asynchronously and their own compilation and linking has been
	 *   suspended until that occurs.
	 *
	 * #### Comparison with life-cycle hooks in the new Angular
	 * The new Angular also uses life-cycle hooks for its components. While the AngularJS life-cycle hooks are similar there are
	 * some differences that you should be aware of, especially when it comes to moving your code from AngularJS to Angular:
	 *
	 * * AngularJS hooks are prefixed with `$`, such as `$onInit`. Angular hooks are prefixed with `ng`, such as `ngOnInit`.
	 * * AngularJS hooks can be defined on the controller prototype or added to the controller inside its constructor.
	 *   In Angular you can only define hooks on the prototype of the Component class.
	 * * Due to the differences in change-detection, you may get many more calls to `$doCheck` in AngularJS than you would to
	 *   `ngDoCheck` in Angular.
	 * * Changes to the model inside `$doCheck` will trigger new turns of the digest loop, which will cause the changes to be
	 *   propagated throughout the application.
	 *   Angular does not allow the `ngDoCheck` hook to trigger a change outside of the component. It will either throw an
	 *   error or do nothing depending upon the state of `enableProdMode()`.
	 *
	 * #### Life-cycle hook examples
	 *
	 * This example shows how you can check for mutations to a Date object even though the identity of the object
	 * has not changed.
	 *
	 * <example name="doCheckDateExample" module="do-check-module">
	 *   <file name="app.js">
	 *     angular.module('do-check-module', [])
	 *       .component('app', {
	 *         template:
	 *           'Month: <input ng-model="$ctrl.month" ng-change="$ctrl.updateDate()">' +
	 *           'Date: {{ $ctrl.date }}' +
	 *           '<test date="$ctrl.date"></test>',
	 *         controller: function() {
	 *           this.date = new Date();
	 *           this.month = this.date.getMonth();
	 *           this.updateDate = function() {
	 *             this.date.setMonth(this.month);
	 *           };
	 *         }
	 *       })
	 *       .component('test', {
	 *         bindings: { date: '<' },
	 *         template:
	 *           '<pre>{{ $ctrl.log | json }}</pre>',
	 *         controller: function() {
	 *           var previousValue;
	 *           this.log = [];
	 *           this.$doCheck = function() {
	 *             var currentValue = this.date && this.date.valueOf();
	 *             if (previousValue !== currentValue) {
	 *               this.log.push('doCheck: date mutated: ' + this.date);
	 *               previousValue = currentValue;
	 *             }
	 *           };
	 *         }
	 *       });
	 *   </file>
	 *   <file name="index.html">
	 *     <app></app>
	 *   </file>
	 * </example>
	 *
	 * This example show how you might use `$doCheck` to trigger changes in your component's inputs even if the
	 * actual identity of the component doesn't change. (Be aware that cloning and deep equality checks on large
	 * arrays or objects can have a negative impact on your application performance.)
	 *
	 * <example name="doCheckArrayExample" module="do-check-module">
	 *   <file name="index.html">
	 *     <div ng-init="items = []">
	 *       <button ng-click="items.push(items.length)">Add Item</button>
	 *       <button ng-click="items = []">Reset Items</button>
	 *       <pre>{{ items }}</pre>
	 *       <test items="items"></test>
	 *     </div>
	 *   </file>
	 *   <file name="app.js">
	 *      angular.module('do-check-module', [])
	 *        .component('test', {
	 *          bindings: { items: '<' },
	 *          template:
	 *            '<pre>{{ $ctrl.log | json }}</pre>',
	 *          controller: function() {
	 *            this.log = [];
	 *
	 *            this.$doCheck = function() {
	 *              if (this.items_ref !== this.items) {
	 *                this.log.push('doCheck: items changed');
	 *                this.items_ref = this.items;
	 *              }
	 *              if (!angular.equals(this.items_clone, this.items)) {
	 *                this.log.push('doCheck: items mutated');
	 *                this.items_clone = angular.copy(this.items);
	 *              }
	 *            };
	 *          }
	 *        });
	 *   </file>
	 * </example>
	 *
	 *
	 * ### Directive Definition Object
	 *
	 * The directive definition object provides instructions to the {@link ng.$compile
	 * compiler}. The attributes are:
	 *
	 * #### `multiElement`
	 * When this property is set to true (default is `false`), the HTML compiler will collect DOM nodes between
	 * nodes with the attributes `directive-name-start` and `directive-name-end`, and group them
	 * together as the directive elements. It is recommended that this feature be used on directives
	 * which are not strictly behavioral (such as {@link ngClick}), and which
	 * do not manipulate or replace child nodes (such as {@link ngInclude}).
	 *
	 * #### `priority`
	 * When there are multiple directives defined on a single DOM element, sometimes it
	 * is necessary to specify the order in which the directives are applied. The `priority` is used
	 * to sort the directives before their `compile` functions get called. Priority is defined as a
	 * number. Directives with greater numerical `priority` are compiled first. Pre-link functions
	 * are also run in priority order, but post-link functions are run in reverse order. The order
	 * of directives with the same priority is undefined. The default priority is `0`.
	 *
	 * #### `terminal`
	 * If set to true then the current `priority` will be the last set of directives
	 * which will execute (any directives at the current priority will still execute
	 * as the order of execution on same `priority` is undefined). Note that expressions
	 * and other directives used in the directive's template will also be excluded from execution.
	 *
	 * #### `scope`
	 * The scope property can be `false`, `true`, or an object:
	 *
	 * * **`false` (default):** No scope will be created for the directive. The directive will use its
	 * parent's scope.
	 *
	 * * **`true`:** A new child scope that prototypically inherits from its parent will be created for
	 * the directive's element. If multiple directives on the same element request a new scope,
	 * only one new scope is created.
	 *
	 * * **`{...}` (an object hash):** A new "isolate" scope is created for the directive's template.
	 * The 'isolate' scope differs from normal scope in that it does not prototypically
	 * inherit from its parent scope. This is useful when creating reusable components, which should not
	 * accidentally read or modify data in the parent scope. Note that an isolate scope
	 * directive without a `template` or `templateUrl` will not apply the isolate scope
	 * to its children elements.
	 *
	 * The 'isolate' scope object hash defines a set of local scope properties derived from attributes on the
	 * directive's element. These local properties are useful for aliasing values for templates. The keys in
	 * the object hash map to the name of the property on the isolate scope; the values define how the property
	 * is bound to the parent scope, via matching attributes on the directive's element:
	 *
	 * * `@` or `@attr` - bind a local scope property to the value of DOM attribute. The result is
	 *   always a string since DOM attributes are strings. If no `attr` name is specified then the
	 *   attribute name is assumed to be the same as the local name. Given `<my-component
	 *   my-attr="hello {{name}}">` and the isolate scope definition `scope: { localName:'@myAttr' }`,
	 *   the directive's scope property `localName` will reflect the interpolated value of `hello
	 *   {{name}}`. As the `name` attribute changes so will the `localName` property on the directive's
	 *   scope. The `name` is read from the parent scope (not the directive's scope).
	 *
	 * * `=` or `=attr` - set up a bidirectional binding between a local scope property and an expression
	 *   passed via the attribute `attr`. The expression is evaluated in the context of the parent scope.
	 *   If no `attr` name is specified then the attribute name is assumed to be the same as the local
	 *   name. Given `<my-component my-attr="parentModel">` and the isolate scope definition `scope: {
	 *   localModel: '=myAttr' }`, the property `localModel` on the directive's scope will reflect the
	 *   value of `parentModel` on the parent scope. Changes to `parentModel` will be reflected in
	 *   `localModel` and vice versa. If the binding expression is non-assignable, or if the attribute
	 *   isn't  optional and doesn't exist, an exception
	 *   ({@link error/$compile/nonassign `$compile:nonassign`}) will be thrown upon discovering changes
	 *   to the local value, since it will be impossible to sync them back to the parent scope.
	 *
	 *   By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
	 *   method is used for tracking changes, and the equality check is based on object identity.
	 *   However, if an object literal or an array literal is passed as the binding expression, the
	 *   equality check is done by value (using the {@link angular.equals} function). It's also possible
	 *   to watch the evaluated value shallowly with {@link ng.$rootScope.Scope#$watchCollection
	 *   `$watchCollection`}: use `=*` or `=*attr`
	 *
	  * * `<` or `<attr` - set up a one-way (one-directional) binding between a local scope property and an
	 *   expression passed via the attribute `attr`. The expression is evaluated in the context of the
	 *   parent scope. If no `attr` name is specified then the attribute name is assumed to be the same as the
	 *   local name.
	 *
	 *   For example, given `<my-component my-attr="parentModel">` and directive definition of
	 *   `scope: { localModel:'<myAttr' }`, then the isolated scope property `localModel` will reflect the
	 *   value of `parentModel` on the parent scope. Any changes to `parentModel` will be reflected
	 *   in `localModel`, but changes in `localModel` will not reflect in `parentModel`. There are however
	 *   two caveats:
	 *     1. one-way binding does not copy the value from the parent to the isolate scope, it simply
	 *     sets the same value. That means if your bound value is an object, changes to its properties
	 *     in the isolated scope will be reflected in the parent scope (because both reference the same object).
	 *     2. one-way binding watches changes to the **identity** of the parent value. That means the
	 *     {@link ng.$rootScope.Scope#$watch `$watch`} on the parent value only fires if the reference
	 *     to the value has changed. In most cases, this should not be of concern, but can be important
	 *     to know if you one-way bind to an object, and then replace that object in the isolated scope.
	 *     If you now change a property of the object in your parent scope, the change will not be
	 *     propagated to the isolated scope, because the identity of the object on the parent scope
	 *     has not changed. Instead you must assign a new object.
	 *
	 *   One-way binding is useful if you do not plan to propagate changes to your isolated scope bindings
	 *   back to the parent. However, it does not make this completely impossible.
	 *
	 *   By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
	 *   method is used for tracking changes, and the equality check is based on object identity.
	 *   It's also possible to watch the evaluated value shallowly with
	 *   {@link ng.$rootScope.Scope#$watchCollection `$watchCollection`}: use `<*` or `<*attr`
	 *
	 * * `&` or `&attr` - provides a way to execute an expression in the context of the parent scope. If
	 *   no `attr` name is specified then the attribute name is assumed to be the same as the local name.
	 *   Given `<my-component my-attr="count = count + value">` and the isolate scope definition `scope: {
	 *   localFn:'&myAttr' }`, the isolate scope property `localFn` will point to a function wrapper for
	 *   the `count = count + value` expression. Often it's desirable to pass data from the isolated scope
	 *   via an expression to the parent scope. This can be done by passing a map of local variable names
	 *   and values into the expression wrapper fn. For example, if the expression is `increment(amount)`
	 *   then we can specify the amount value by calling the `localFn` as `localFn({amount: 22})`.
	 *
	 * All 4 kinds of bindings (`@`, `=`, `<`, and `&`) can be made optional by adding `?` to the expression.
	 * The marker must come after the mode and before the attribute name.
	 * See the {@link error/$compile/iscp Invalid Isolate Scope Definition error} for definition examples.
	 * This is useful to refine the interface directives provide.
	 * One subtle difference between optional and non-optional happens **when the binding attribute is not
	 * set**:
	 * - the binding is optional: the property will not be defined
	 * - the binding is not optional: the property is defined
	 *
	 * ```js
	 *app.directive('testDir', function() {
		return {
		  scope: {
			notoptional: '=',
			optional: '=?',
		  },
		  bindToController: true,
		  controller: function() {
			this.$onInit = function() {
			  console.log(this.hasOwnProperty('notoptional')) // true
			  console.log(this.hasOwnProperty('optional')) // false
			}
		  }
		}
	  })
	 *```
	 *
	 *
	 * ##### Combining directives with different scope defintions
	 *
	 * In general it's possible to apply more than one directive to one element, but there might be limitations
	 * depending on the type of scope required by the directives. The following points will help explain these limitations.
	 * For simplicity only two directives are taken into account, but it is also applicable for several directives:
	 *
	 * * **no scope** + **no scope** => Two directives which don't require their own scope will use their parent's scope
	 * * **child scope** + **no scope** =>  Both directives will share one single child scope
	 * * **child scope** + **child scope** =>  Both directives will share one single child scope
	 * * **isolated scope** + **no scope** =>  The isolated directive will use it's own created isolated scope. The other directive will use
	 * its parent's scope
	 * * **isolated scope** + **child scope** =>  **Won't work!** Only one scope can be related to one element. Therefore these directives cannot
	 * be applied to the same element.
	 * * **isolated scope** + **isolated scope**  =>  **Won't work!** Only one scope can be related to one element. Therefore these directives
	 * cannot be applied to the same element.
	 *
	 *
	 * #### `bindToController`
	 * This property is used to bind scope properties directly to the controller. It can be either
	 * `true` or an object hash with the same format as the `scope` property.
	 *
	 * When an isolate scope is used for a directive (see above), `bindToController: true` will
	 * allow a component to have its properties bound to the controller, rather than to scope.
	 *
	 * After the controller is instantiated, the initial values of the isolate scope bindings will be bound to the controller
	 * properties. You can access these bindings once they have been initialized by providing a controller method called
	 * `$onInit`, which is called after all the controllers on an element have been constructed and had their bindings
	 * initialized.
	 *
	 * It is also possible to set `bindToController` to an object hash with the same format as the `scope` property.
	 * This will set up the scope bindings to the controller directly. Note that `scope` can still be used
	 * to define which kind of scope is created. By default, no scope is created. Use `scope: {}` to create an isolate
	 * scope (useful for component directives).
	 *
	 * If both `bindToController` and `scope` are defined and have object hashes, `bindToController` overrides `scope`.
	 *
	 *
	 * #### `controller`
	 * Controller constructor function. The controller is instantiated before the
	 * pre-linking phase and can be accessed by other directives (see
	 * `require` attribute). This allows the directives to communicate with each other and augment
	 * each other's behavior. The controller is injectable (and supports bracket notation) with the following locals:
	 *
	 * * `$scope` - Current scope associated with the element
	 * * `$element` - Current element
	 * * `$attrs` - Current attributes object for the element
	 * * `$transclude` - A transclude linking function pre-bound to the correct transclusion scope:
	 *   `function([scope], cloneLinkingFn, futureParentElement, slotName)`:
	 *    * `scope`: (optional) override the scope.
	 *    * `cloneLinkingFn`: (optional) argument to create clones of the original transcluded content.
	 *    * `futureParentElement` (optional):
	 *        * defines the parent to which the `cloneLinkingFn` will add the cloned elements.
	 *        * default: `$element.parent()` resp. `$element` for `transclude:'element'` resp. `transclude:true`.
	 *        * only needed for transcludes that are allowed to contain non html elements (e.g. SVG elements)
	 *          and when the `cloneLinkingFn` is passed,
	 *          as those elements need to created and cloned in a special way when they are defined outside their
	 *          usual containers (e.g. like `<svg>`).
	 *        * See also the `directive.templateNamespace` property.
	 *    * `slotName`: (optional) the name of the slot to transclude. If falsy (e.g. `null`, `undefined` or `''`)
	 *      then the default transclusion is provided.
	 *    The `$transclude` function also has a method on it, `$transclude.isSlotFilled(slotName)`, which returns
	 *    `true` if the specified slot contains content (i.e. one or more DOM nodes).
	 *
	 * #### `require`
	 * Require another directive and inject its controller as the fourth argument to the linking function. The
	 * `require` property can be a string, an array or an object:
	 * * a **string** containing the name of the directive to pass to the linking function
	 * * an **array** containing the names of directives to pass to the linking function. The argument passed to the
	 * linking function will be an array of controllers in the same order as the names in the `require` property
	 * * an **object** whose property values are the names of the directives to pass to the linking function. The argument
	 * passed to the linking function will also be an object with matching keys, whose values will hold the corresponding
	 * controllers.
	 *
	 * If the `require` property is an object and `bindToController` is truthy, then the required controllers are
	 * bound to the controller using the keys of the `require` property. This binding occurs after all the controllers
	 * have been constructed but before `$onInit` is called.
	 * If the name of the required controller is the same as the local name (the key), the name can be
	 * omitted. For example, `{parentDir: '^^'}` is equivalent to `{parentDir: '^^parentDir'}`.
	 * See the {@link $compileProvider#component} helper for an example of how this can be used.
	 * If no such required directive(s) can be found, or if the directive does not have a controller, then an error is
	 * raised (unless no link function is specified and the required controllers are not being bound to the directive
	 * controller, in which case error checking is skipped). The name can be prefixed with:
	 *
	 * * (no prefix) - Locate the required controller on the current element. Throw an error if not found.
	 * * `?` - Attempt to locate the required controller or pass `null` to the `link` fn if not found.
	 * * `^` - Locate the required controller by searching the element and its parents. Throw an error if not found.
	 * * `^^` - Locate the required controller by searching the element's parents. Throw an error if not found.
	 * * `?^` - Attempt to locate the required controller by searching the element and its parents or pass
	 *   `null` to the `link` fn if not found.
	 * * `?^^` - Attempt to locate the required controller by searching the element's parents, or pass
	 *   `null` to the `link` fn if not found.
	 *
	 *
	 * #### `controllerAs`
	 * Identifier name for a reference to the controller in the directive's scope.
	 * This allows the controller to be referenced from the directive template. This is especially
	 * useful when a directive is used as component, i.e. with an `isolate` scope. It's also possible
	 * to use it in a directive without an `isolate` / `new` scope, but you need to be aware that the
	 * `controllerAs` reference might overwrite a property that already exists on the parent scope.
	 *
	 *
	 * #### `restrict`
	 * String of subset of `EACM` which restricts the directive to a specific directive
	 * declaration style. If omitted, the defaults (elements and attributes) are used.
	 *
	 * * `E` - Element name (default): `<my-directive></my-directive>`
	 * * `A` - Attribute (default): `<div my-directive="exp"></div>`
	 * * `C` - Class: `<div class="my-directive: exp;"></div>`
	 * * `M` - Comment: `<!-- directive: my-directive exp -->`
	 *
	 *
	 * #### `templateNamespace`
	 * String representing the document type used by the markup in the template.
	 * AngularJS needs this information as those elements need to be created and cloned
	 * in a special way when they are defined outside their usual containers like `<svg>` and `<math>`.
	 *
	 * * `html` - All root nodes in the template are HTML. Root nodes may also be
	 *   top-level elements such as `<svg>` or `<math>`.
	 * * `svg` - The root nodes in the template are SVG elements (excluding `<math>`).
	 * * `math` - The root nodes in the template are MathML elements (excluding `<svg>`).
	 *
	 * If no `templateNamespace` is specified, then the namespace is considered to be `html`.
	 *
	 * #### `template`
	 * HTML markup that may:
	 * * Replace the contents of the directive's element (default).
	 * * Replace the directive's element itself (if `replace` is true - DEPRECATED).
	 * * Wrap the contents of the directive's element (if `transclude` is true).
	 *
	 * Value may be:
	 *
	 * * A string. For example `<div red-on-hover>{{delete_str}}</div>`.
	 * * A function which takes two arguments `tElement` and `tAttrs` (described in the `compile`
	 *   function api below) and returns a string value.
	 *
	 *
	 * #### `templateUrl`
	 * This is similar to `template` but the template is loaded from the specified URL, asynchronously.
	 *
	 * Because template loading is asynchronous the compiler will suspend compilation of directives on that element
	 * for later when the template has been resolved.  In the meantime it will continue to compile and link
	 * sibling and parent elements as though this element had not contained any directives.
	 *
	 * The compiler does not suspend the entire compilation to wait for templates to be loaded because this
	 * would result in the whole app "stalling" until all templates are loaded asynchronously - even in the
	 * case when only one deeply nested directive has `templateUrl`.
	 *
	 * Template loading is asynchronous even if the template has been preloaded into the {@link $templateCache}.
	 *
	 * You can specify `templateUrl` as a string representing the URL or as a function which takes two
	 * arguments `tElement` and `tAttrs` (described in the `compile` function api below) and returns
	 * a string value representing the url.  In either case, the template URL is passed through {@link
	 * $sce#getTrustedResourceUrl $sce.getTrustedResourceUrl}.
	 *
	 *
	 * #### `replace`
	 * <div class="alert alert-danger">
	 * **Note:** `replace` is deprecated in AngularJS and has been removed in the new Angular (v2+).
	 * </div>
	 *
	 * Specifies what the template should replace. Defaults to `false`.
	 *
	 * * `true` - the template will replace the directive's element.
	 * * `false` - the template will replace the contents of the directive's element.
	 *
	 * The replacement process migrates all of the attributes / classes from the old element to the new
	 * one. See the {@link guide/directive#template-expanding-directive
	 * Directives Guide} for an example.
	 *
	 * There are very few scenarios where element replacement is required for the application function,
	 * the main one being reusable custom components that are used within SVG contexts
	 * (because SVG doesn't work with custom elements in the DOM tree).
	 *
	 * #### `transclude`
	 * Extract the contents of the element where the directive appears and make it available to the directive.
	 * The contents are compiled and provided to the directive as a **transclusion function**. See the
	 * {@link $compile#transclusion Transclusion} section below.
	 *
	 *
	 * #### `compile`
	 *
	 * ```js
	 *   function compile(tElement, tAttrs, transclude) { ... }
	 * ```
	 *
	 * The compile function deals with transforming the template DOM. Since most directives do not do
	 * template transformation, it is not used often. The compile function takes the following arguments:
	 *
	 *   * `tElement` - template element - The element where the directive has been declared. It is
	 *     safe to do template transformation on the element and child elements only.
	 *
	 *   * `tAttrs` - template attributes - Normalized list of attributes declared on this element shared
	 *     between all directive compile functions.
	 *
	 *   * `transclude` -  [*DEPRECATED*!] A transclude linking function: `function(scope, cloneLinkingFn)`
	 *
	 * <div class="alert alert-warning">
	 * **Note:** The template instance and the link instance may be different objects if the template has
	 * been cloned. For this reason it is **not** safe to do anything other than DOM transformations that
	 * apply to all cloned DOM nodes within the compile function. Specifically, DOM listener registration
	 * should be done in a linking function rather than in a compile function.
	 * </div>
	
	 * <div class="alert alert-warning">
	 * **Note:** The compile function cannot handle directives that recursively use themselves in their
	 * own templates or compile functions. Compiling these directives results in an infinite loop and
	 * stack overflow errors.
	 *
	 * This can be avoided by manually using `$compile` in the postLink function to imperatively compile
	 * a directive's template instead of relying on automatic template compilation via `template` or
	 * `templateUrl` declaration or manual compilation inside the compile function.
	 * </div>
	 *
	 * <div class="alert alert-danger">
	 * **Note:** The `transclude` function that is passed to the compile function is deprecated, as it
	 *   e.g. does not know about the right outer scope. Please use the transclude function that is passed
	 *   to the link function instead.
	 * </div>
	
	 * A compile function can have a return value which can be either a function or an object.
	 *
	 * * returning a (post-link) function - is equivalent to registering the linking function via the
	 *   `link` property of the config object when the compile function is empty.
	 *
	 * * returning an object with function(s) registered via `pre` and `post` properties - allows you to
	 *   control when a linking function should be called during the linking phase. See info about
	 *   pre-linking and post-linking functions below.
	 *
	 *
	 * #### `link`
	 * This property is used only if the `compile` property is not defined.
	 *
	 * ```js
	 *   function link(scope, iElement, iAttrs, controller, transcludeFn) { ... }
	 * ```
	 *
	 * The link function is responsible for registering DOM listeners as well as updating the DOM. It is
	 * executed after the template has been cloned. This is where most of the directive logic will be
	 * put.
	 *
	 *   * `scope` - {@link ng.$rootScope.Scope Scope} - The scope to be used by the
	 *     directive for registering {@link ng.$rootScope.Scope#$watch watches}.
	 *
	 *   * `iElement` - instance element - The element where the directive is to be used. It is safe to
	 *     manipulate the children of the element only in `postLink` function since the children have
	 *     already been linked.
	 *
	 *   * `iAttrs` - instance attributes - Normalized list of attributes declared on this element shared
	 *     between all directive linking functions.
	 *
	 *   * `controller` - the directive's required controller instance(s) - Instances are shared
	 *     among all directives, which allows the directives to use the controllers as a communication
	 *     channel. The exact value depends on the directive's `require` property:
	 *       * no controller(s) required: the directive's own controller, or `undefined` if it doesn't have one
	 *       * `string`: the controller instance
	 *       * `array`: array of controller instances
	 *
	 *     If a required controller cannot be found, and it is optional, the instance is `null`,
	 *     otherwise the {@link error:$compile:ctreq Missing Required Controller} error is thrown.
	 *
	 *     Note that you can also require the directive's own controller - it will be made available like
	 *     any other controller.
	 *
	 *   * `transcludeFn` - A transclude linking function pre-bound to the correct transclusion scope.
	 *     This is the same as the `$transclude` parameter of directive controllers,
	 *     see {@link ng.$compile#-controller- the controller section for details}.
	 *     `function([scope], cloneLinkingFn, futureParentElement)`.
	 *
	 * #### Pre-linking function
	 *
	 * Executed before the child elements are linked. Not safe to do DOM transformation since the
	 * compiler linking function will fail to locate the correct elements for linking.
	 *
	 * #### Post-linking function
	 *
	 * Executed after the child elements are linked.
	 *
	 * Note that child elements that contain `templateUrl` directives will not have been compiled
	 * and linked since they are waiting for their template to load asynchronously and their own
	 * compilation and linking has been suspended until that occurs.
	 *
	 * It is safe to do DOM transformation in the post-linking function on elements that are not waiting
	 * for their async templates to be resolved.
	 *
	 *
	 * ### Transclusion
	 *
	 * Transclusion is the process of extracting a collection of DOM elements from one part of the DOM and
	 * copying them to another part of the DOM, while maintaining their connection to the original AngularJS
	 * scope from where they were taken.
	 *
	 * Transclusion is used (often with {@link ngTransclude}) to insert the
	 * original contents of a directive's element into a specified place in the template of the directive.
	 * The benefit of transclusion, over simply moving the DOM elements manually, is that the transcluded
	 * content has access to the properties on the scope from which it was taken, even if the directive
	 * has isolated scope.
	 * See the {@link guide/directive#creating-a-directive-that-wraps-other-elements Directives Guide}.
	 *
	 * This makes it possible for the widget to have private state for its template, while the transcluded
	 * content has access to its originating scope.
	 *
	 * <div class="alert alert-warning">
	 * **Note:** When testing an element transclude directive you must not place the directive at the root of the
	 * DOM fragment that is being compiled. See {@link guide/unit-testing#testing-transclusion-directives
	 * Testing Transclusion Directives}.
	 * </div>
	 *
	 * There are three kinds of transclusion depending upon whether you want to transclude just the contents of the
	 * directive's element, the entire element or multiple parts of the element contents:
	 *
	 * * `true` - transclude the content (i.e. the child nodes) of the directive's element.
	 * * `'element'` - transclude the whole of the directive's element including any directives on this
	 *   element that are defined at a lower priority than this directive. When used, the `template`
	 *   property is ignored.
	 * * **`{...}` (an object hash):** - map elements of the content onto transclusion "slots" in the template.
	 *
	 * **Multi-slot transclusion** is declared by providing an object for the `transclude` property.
	 *
	 * This object is a map where the keys are the name of the slot to fill and the value is an element selector
	 * used to match the HTML to the slot. The element selector should be in normalized form (e.g. `myElement`)
	 * and will match the standard element variants (e.g. `my-element`, `my:element`, `data-my-element`, etc).
	 *
	 * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}.
	 *
	 * If the element selector is prefixed with a `?` then that slot is optional.
	 *
	 * For example, the transclude object `{ slotA: '?myCustomElement' }` maps `<my-custom-element>` elements to
	 * the `slotA` slot, which can be accessed via the `$transclude` function or via the {@link ngTransclude} directive.
	 *
	 * Slots that are not marked as optional (`?`) will trigger a compile time error if there are no matching elements
	 * in the transclude content. If you wish to know if an optional slot was filled with content, then you can call
	 * `$transclude.isSlotFilled(slotName)` on the transclude function passed to the directive's link function and
	 * injectable into the directive's controller.
	 *
	 *
	 * #### Transclusion Functions
	 *
	 * When a directive requests transclusion, the compiler extracts its contents and provides a **transclusion
	 * function** to the directive's `link` function and `controller`. This transclusion function is a special
	 * **linking function** that will return the compiled contents linked to a new transclusion scope.
	 *
	 * <div class="alert alert-info">
	 * If you are just using {@link ngTransclude} then you don't need to worry about this function, since
	 * ngTransclude will deal with it for us.
	 * </div>
	 *
	 * If you want to manually control the insertion and removal of the transcluded content in your directive
	 * then you must use this transclude function. When you call a transclude function it returns a jqLite/JQuery
	 * object that contains the compiled DOM, which is linked to the correct transclusion scope.
	 *
	 * When you call a transclusion function you can pass in a **clone attach function**. This function accepts
	 * two parameters, `function(clone, scope) { ... }`, where the `clone` is a fresh compiled copy of your transcluded
	 * content and the `scope` is the newly created transclusion scope, which the clone will be linked to.
	 *
	 * <div class="alert alert-info">
	 * **Best Practice**: Always provide a `cloneFn` (clone attach function) when you call a transclude function
	 * since you then get a fresh clone of the original DOM and also have access to the new transclusion scope.
	 * </div>
	 *
	 * It is normal practice to attach your transcluded content (`clone`) to the DOM inside your **clone
	 * attach function**:
	 *
	 * ```js
	 * var transcludedContent, transclusionScope;
	 *
	 * $transclude(function(clone, scope) {
	 *   element.append(clone);
	 *   transcludedContent = clone;
	 *   transclusionScope = scope;
	 * });
	 * ```
	 *
	 * Later, if you want to remove the transcluded content from your DOM then you should also destroy the
	 * associated transclusion scope:
	 *
	 * ```js
	 * transcludedContent.remove();
	 * transclusionScope.$destroy();
	 * ```
	 *
	 * <div class="alert alert-info">
	 * **Best Practice**: if you intend to add and remove transcluded content manually in your directive
	 * (by calling the transclude function to get the DOM and calling `element.remove()` to remove it),
	 * then you are also responsible for calling `$destroy` on the transclusion scope.
	 * </div>
	 *
	 * The built-in DOM manipulation directives, such as {@link ngIf}, {@link ngSwitch} and {@link ngRepeat}
	 * automatically destroy their transcluded clones as necessary so you do not need to worry about this if
	 * you are simply using {@link ngTransclude} to inject the transclusion into your directive.
	 *
	 *
	 * #### Transclusion Scopes
	 *
	 * When you call a transclude function it returns a DOM fragment that is pre-bound to a **transclusion
	 * scope**. This scope is special, in that it is a child of the directive's scope (and so gets destroyed
	 * when the directive's scope gets destroyed) but it inherits the properties of the scope from which it
	 * was taken.
	 *
	 * For example consider a directive that uses transclusion and isolated scope. The DOM hierarchy might look
	 * like this:
	 *
	 * ```html
	 * <div ng-app>
	 *   <div isolate>
	 *     <div transclusion>
	 *     </div>
	 *   </div>
	 * </div>
	 * ```
	 *
	 * The `$parent` scope hierarchy will look like this:
	 *
	   ```
	   - $rootScope
		 - isolate
		   - transclusion
	   ```
	 *
	 * but the scopes will inherit prototypically from different scopes to their `$parent`.
	 *
	   ```
	   - $rootScope
		 - transclusion
	   - isolate
	   ```
	 *
	 *
	 * ### Attributes
	 *
	 * The {@link ng.$compile.directive.Attributes Attributes} object - passed as a parameter in the
	 * `link()` or `compile()` functions. It has a variety of uses.
	 *
	 * * *Accessing normalized attribute names:* Directives like `ngBind` can be expressed in many ways:
	 *   `ng:bind`, `data-ng-bind`, or `x-ng-bind`. The attributes object allows for normalized access
	 *   to the attributes.
	 *
	 * * *Directive inter-communication:* All directives share the same instance of the attributes
	 *   object which allows the directives to use the attributes object as inter directive
	 *   communication.
	 *
	 * * *Supports interpolation:* Interpolation attributes are assigned to the attribute object
	 *   allowing other directives to read the interpolated value.
	 *
	 * * *Observing interpolated attributes:* Use `$observe` to observe the value changes of attributes
	 *   that contain interpolation (e.g. `src="{{bar}}"`). Not only is this very efficient but it's also
	 *   the only way to easily get the actual value because during the linking phase the interpolation
	 *   hasn't been evaluated yet and so the value is at this time set to `undefined`.
	 *
	 * ```js
	 * function linkingFn(scope, elm, attrs, ctrl) {
	 *   // get the attribute value
	 *   console.log(attrs.ngModel);
	 *
	 *   // change the attribute
	 *   attrs.$set('ngModel', 'new value');
	 *
	 *   // observe changes to interpolated attribute
	 *   attrs.$observe('ngModel', function(value) {
	 *     console.log('ngModel has changed value to ' + value);
	 *   });
	 * }
	 * ```
	 *
	 * ## Example
	 *
	 * <div class="alert alert-warning">
	 * **Note**: Typically directives are registered with `module.directive`. The example below is
	 * to illustrate how `$compile` works.
	 * </div>
	 *
	 <example module="compileExample" name="compile">
	   <file name="index.html">
		<script>
		  angular.module('compileExample', [], function($compileProvider) {
			// Configure new 'compile' directive by passing a directive
			// factory function. The factory function injects '$compile'.
			$compileProvider.directive('compile', function($compile) {
			  // The directive factory creates a link function.
			  return function(scope, element, attrs) {
				scope.$watch(
				  function(scope) {
					// Watch the 'compile' expression for changes.
					return scope.$eval(attrs.compile);
				  },
				  function(value) {
					// When the 'compile' expression changes
					// assign it into the current DOM.
					element.html(value);
	
					// Compile the new DOM and link it to the current scope.
					// NOTE: we only compile '.childNodes' so that we
					// don't get into an infinite loop compiling ourselves.
					$compile(element.contents())(scope);
				  }
				);
			  };
			});
		  })
		  .controller('GreeterController', ['$scope', function($scope) {
			$scope.name = 'AngularJS';
			$scope.html = 'Hello {{name}}';
		  }]);
		</script>
		<div ng-controller="GreeterController">
		  <input ng-model="name"> <br/>
		  <textarea ng-model="html"></textarea> <br/>
		  <div compile="html"></div>
		</div>
	   </file>
	   <file name="protractor.js" type="protractor">
		 it('should auto compile', function() {
		   var textarea = $('textarea');
		   var output = $('div[compile]');
		   // The initial state reads 'Hello AngularJS'.
		   expect(output.getText()).toBe('Hello AngularJS');
		   textarea.clear();
		   textarea.sendKeys('{{name}}!');
		   expect(output.getText()).toBe('AngularJS!');
		 });
	   </file>
	 </example>
	
	 *
	 *
	 * @param {string|DOMElement} element Element or HTML string to compile into a template function.
	 * @param {function(angular.Scope, cloneAttachFn=)} transclude function available to directives - DEPRECATED.
	 *
	 * <div class="alert alert-danger">
	 * **Note:** Passing a `transclude` function to the $compile function is deprecated, as it
	 *   e.g. will not use the right outer scope. Please pass the transclude function as a
	 *   `parentBoundTranscludeFn` to the link function instead.
	 * </div>
	 *
	 * @param {number} maxPriority only apply directives lower than given priority (Only effects the
	 *                 root element(s), not their children)
	 * @returns {function(scope, cloneAttachFn=, options=)} a link function which is used to bind template
	 * (a DOM element/tree) to a scope. Where:
	 *
	 *  * `scope` - A {@link ng.$rootScope.Scope Scope} to bind to.
	 *  * `cloneAttachFn` - If `cloneAttachFn` is provided, then the link function will clone the
	 *  `template` and call the `cloneAttachFn` function allowing the caller to attach the
	 *  cloned elements to the DOM document at the appropriate place. The `cloneAttachFn` is
	 *  called as: <br/> `cloneAttachFn(clonedElement, scope)` where:
	 *
	 *      * `clonedElement` - is a clone of the original `element` passed into the compiler.
	 *      * `scope` - is the current scope with which the linking function is working with.
	 *
	 *  * `options` - An optional object hash with linking options. If `options` is provided, then the following
	 *  keys may be used to control linking behavior:
	 *
	 *      * `parentBoundTranscludeFn` - the transclude function made available to
	 *        directives; if given, it will be passed through to the link functions of
	 *        directives found in `element` during compilation.
	 *      * `transcludeControllers` - an object hash with keys that map controller names
	 *        to a hash with the key `instance`, which maps to the controller instance;
	 *        if given, it will make the controllers available to directives on the compileNode:
	 *        ```
	 *        {
	 *          parent: {
	 *            instance: parentControllerInstance
	 *          }
	 *        }
	 *        ```
	 *      * `futureParentElement` - defines the parent to which the `cloneAttachFn` will add
	 *        the cloned elements; only needed for transcludes that are allowed to contain non HTML
	 *        elements (e.g. SVG elements). See also the `directive.controller` property.
	 *
	 * Calling the linking function returns the element of the template. It is either the original
	 * element passed in, or the clone of the element if the `cloneAttachFn` is provided.
	 *
	 * After linking the view is not updated until after a call to `$digest`, which typically is done by
	 * AngularJS automatically.
	 *
	 * If you need access to the bound view, there are two ways to do it:
	 *
	 * - If you are not asking the linking function to clone the template, create the DOM element(s)
	 *   before you send them to the compiler and keep this reference around.
	 *   ```js
	 *     var element = angular.element('<p>{{total}}</p>');
	 *     $compile(element)(scope);
	 *   ```
	 *
	 * - if on the other hand, you need the element to be cloned, the view reference from the original
	 *   example would not point to the clone, but rather to the original template that was cloned. In
	 *   this case, you can access the clone either via the `cloneAttachFn` or the value returned by the
	 *   linking function:
	 *   ```js
	 *     var templateElement = angular.element('<p>{{total}}</p>');
	 *     var clonedElement = $compile(templateElement)(scope, function(clonedElement, scope) {
	 *       // Attach the clone to DOM document at the right place.
	 *     });
	 *
	 *     // Now we have reference to the cloned DOM via `clonedElement`.
	 *     // NOTE: The `clonedElement` returned by the linking function is the same as the
	 *     //       `clonedElement` passed to `cloneAttachFn`.
	 *   ```
	 *
	 *
	 * For information on how the compiler works, see the
	 * {@link guide/compiler AngularJS HTML Compiler} section of the Developer Guide.
	 *
	 * @knownIssue
	 *
	 * ### Double Compilation
	 *
	   Double compilation occurs when an already compiled part of the DOM gets
	   compiled again. This is an undesired effect and can lead to misbehaving directives, performance issues,
	   and memory leaks. Refer to the Compiler Guide {@link guide/compiler#double-compilation-and-how-to-avoid-it
	   section on double compilation} for an in-depth explanation and ways to avoid it.
	
	 * @knownIssue
	
	   ### Issues with `replace: true`
	 *
	 * <div class="alert alert-danger">
	 *   **Note**: {@link $compile#-replace- `replace: true`} is deprecated and not recommended to use,
	 *   mainly due to the issues listed here. It has been completely removed in the new Angular.
	 * </div>
	 *
	 * #### Attribute values are not merged
	 *
	 * When a `replace` directive encounters the same attribute on the original and the replace node,
	 * it will simply deduplicate the attribute and join the values with a space or with a `;` in case of
	 * the `style` attribute.
	 * ```html
	 * Original Node: <span class="original" style="color: red;"></span>
	 * Replace Template: <span class="replaced" style="background: blue;"></span>
	 * Result: <span class="original replaced" style="color: red; background: blue;"></span>
	 * ```
	 *
	 * That means attributes that contain AngularJS expressions will not be merged correctly, e.g.
	 * {@link ngShow} or {@link ngClass} will cause a {@link $parse} error:
	 *
	 * ```html
	 * Original Node: <span ng-class="{'something': something}" ng-show="!condition"></span>
	 * Replace Template: <span ng-class="{'else': else}" ng-show="otherCondition"></span>
	 * Result: <span ng-class="{'something': something} {'else': else}" ng-show="!condition otherCondition"></span>
	 * ```
	 *
	 * See issue [#5695](https://github.com/angular/angular.js/issues/5695).
	 *
	 * #### Directives are not deduplicated before compilation
	 *
	 * When the original node and the replace template declare the same directive(s), they will be
	 * {@link guide/compiler#double-compilation-and-how-to-avoid-it compiled twice} because the compiler
	 * does not deduplicate them. In many cases, this is not noticeable, but e.g. {@link ngModel} will
	 * attach `$formatters` and `$parsers` twice.
	 *
	 * See issue [#2573](https://github.com/angular/angular.js/issues/2573).
	 *
	 * #### `transclude: element` in the replace template root can have unexpected effects
	 *
	 * When the replace template has a directive at the root node that uses
	 * {@link $compile#-transclude- `transclude: element`}, e.g.
	 * {@link ngIf} or {@link ngRepeat}, the DOM structure or scope inheritance can be incorrect.
	 * See the following issues:
	 *
	 * - Incorrect scope on replaced element:
	 * [#9837](https://github.com/angular/angular.js/issues/9837)
	 * - Different DOM between `template` and `templateUrl`:
	 * [#10612](https://github.com/angular/angular.js/issues/14326)
	 *
	 */

	/**
	 * @ngdoc directive
	 * @name ngProp
	 * @restrict A
	 * @element ANY
	 *
	 * @usage
	 *
	 * ```html
	 * <ANY ng-prop-propname="expression">
	 * </ANY>
	 * ```
	 *
	 * or with uppercase letters in property (e.g. "propName"):
	 *
	 *
	 * ```html
	 * <ANY ng-prop-prop_name="expression">
	 * </ANY>
	 * ```
	 *
	 *
	 * @description
	 * The `ngProp` directive binds an expression to a DOM element property.
	 * `ngProp` allows writing to arbitrary properties by including
	 * the property name in the attribute, e.g. `ng-prop-value="'my value'"` binds 'my value' to
	 * the `value` property.
	 *
	 * Usually, it's not necessary to write to properties in AngularJS, as the built-in directives
	 * handle the most common use cases (instead of the above example, you would use {@link ngValue}).
	 *
	 * However, [custom elements](https://developer.mozilla.org/docs/Web/Web_Components/Using_custom_elements)
	 * often use custom properties to hold data, and `ngProp` can be used to provide input to these
	 * custom elements.
	 *
	 * ## Binding to camelCase properties
	 *
	 * Since HTML attributes are case-insensitive, camelCase properties like `innerHTML` must be escaped.
	 * AngularJS uses the underscore (_) in front of a character to indicate that it is uppercase, so
	 * `innerHTML`  must be written as `ng-prop-inner_h_t_m_l="expression"` (Note that this is just an
	 * example, and for binding HTML {@link ngBindHtml} should be used.
	 *
	 * ## Security
	 *
	 * Binding expressions to arbitrary properties poses a security risk, as  properties like `innerHTML`
	 * can insert potentially dangerous HTML into the application, e.g. script tags that execute
	 * malicious code.
	 * For this reason, `ngProp` applies Strict Contextual Escaping with the {@link ng.$sce $sce service}.
	 * This means vulnerable properties require their content to be "trusted", based on the
	 * context of the property. For example, the `innerHTML` is in the `HTML` context, and the
	 * `iframe.src` property is in the `RESOURCE_URL` context, which requires that values written to
	 * this property are trusted as a `RESOURCE_URL`.
	 *
	 * This can be set explicitly by calling $sce.trustAs(type, value) on the value that is
	 * trusted before passing it to the `ng-prop-*` directive. There are exist shorthand methods for
	 * each context type in the form of {@link ng.$sce#trustAsResourceUrl $sce.trustAsResourceUrl()} et al.
	 *
	 * In some cases you can also rely upon automatic sanitization of untrusted values - see below.
	 *
	 * Based on the context, other options may exist to mark a value as trusted / configure the behavior
	 * of {@link ng.$sce}. For example, to restrict the `RESOURCE_URL` context to specific origins, use
	 * the {@link $sceDelegateProvider#trustedResourceUrlList trustedResourceUrlList()}
	 * and {@link $sceDelegateProvider#bannedResourceUrlList bannedResourceUrlList()}.
	 *
	 * {@link ng.$sce#what-trusted-context-types-are-supported- Find out more about the different context types}.
	 *
	 * ### HTML Sanitization
	 *
	 * By default, `$sce` will throw an error if it detects untrusted HTML content, and will not bind the
	 * content.
	 * However, if you include the {@link ngSanitize ngSanitize module}, it will try to sanitize the
	 * potentially dangerous HTML, e.g. strip non-trusted tags and attributes when binding to
	 * `innerHTML`.
	 *
	 * @example
	 * ### Binding to different contexts
	 *
	 * <example name="ngProp" module="exampleNgProp">
	 *   <file name="app.js">
	 *     angular.module('exampleNgProp', [])
	 *       .component('main', {
	 *         templateUrl: 'main.html',
	 *         controller: function($sce) {
	 *           this.safeContent = '<strong>Safe content</strong>';
	 *           this.unsafeContent = '<button onclick="alert(\'Hello XSS!\')">Click for XSS</button>';
	 *           this.trustedUnsafeContent = $sce.trustAsHtml(this.unsafeContent);
	 *         }
	 *       });
	 *   </file>
	 *   <file name="main.html">
	 *     <div>
	 *       <div class="prop-unit">
	 *         Binding to a property without security context:
	 *         <div class="prop-binding" ng-prop-inner_text="$ctrl.safeContent"></div>
	 *         <span class="prop-note">innerText</span> (safeContent)
	 *       </div>
	 *
	 *       <div class="prop-unit">
	 *         "Safe" content that requires a security context will throw because the contents could potentially be dangerous ...
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.safeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (safeContent)
	 *       </div>
	 *
	 *       <div class="prop-unit">
	 *         ... so that actually dangerous content cannot be executed:
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.unsafeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (unsafeContent)
	 *       </div>
	 *
	 *       <div class="prop-unit">
	 *         ... but unsafe Content that has been trusted explicitly works - only do this if you are 100% sure!
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.trustedUnsafeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (trustedUnsafeContent)
	 *       </div>
	 *     </div>
	 *   </file>
	 *   <file name="index.html">
	 *     <main></main>
	 *   </file>
	 *   <file name="styles.css">
	 *     .prop-unit {
	 *       margin-bottom: 10px;
	 *     }
	 *
	 *     .prop-binding {
	 *       min-height: 30px;
	 *       border: 1px solid blue;
	 *     }
	 *
	 *     .prop-note {
	 *       font-family: Monospace;
	 *     }
	 *   </file>
	 * </example>
	 *
	 *
	 * @example
	 * ### Binding to innerHTML with ngSanitize
	 *
	 * <example name="ngProp" module="exampleNgProp" deps="angular-sanitize.js">
	 *   <file name="app.js">
	 *     angular.module('exampleNgProp', ['ngSanitize'])
	 *       .component('main', {
	 *         templateUrl: 'main.html',
	 *         controller: function($sce) {
	 *           this.safeContent = '<strong>Safe content</strong>';
	 *           this.unsafeContent = '<button onclick="alert(\'Hello XSS!\')">Click for XSS</button>';
	 *           this.trustedUnsafeContent = $sce.trustAsHtml(this.unsafeContent);
	 *         }
	 *       });
	 *   </file>
	 *   <file name="main.html">
	 *     <div>
	 *       <div class="prop-unit">
	 *         "Safe" content will be sanitized ...
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.safeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (safeContent)
	 *       </div>
	 *
	 *       <div class="prop-unit">
	 *         ... as will dangerous content:
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.unsafeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (unsafeContent)
	 *       </div>
	 *
	 *       <div class="prop-unit">
	 *         ... and content that has been trusted explicitly works the same as without ngSanitize:
	 *         <div class="prop-binding" ng-prop-inner_h_t_m_l="$ctrl.trustedUnsafeContent"></div>
	 *         <span class="prop-note">innerHTML</span> (trustedUnsafeContent)
	 *       </div>
	 *     </div>
	 *   </file>
	 *   <file name="index.html">
	 *     <main></main>
	 *   </file>
	 *   <file name="styles.css">
	 *     .prop-unit {
	 *       margin-bottom: 10px;
	 *     }
	 *
	 *     .prop-binding {
	 *       min-height: 30px;
	 *       border: 1px solid blue;
	 *     }
	 *
	 *     .prop-note {
	 *       font-family: Monospace;
	 *     }
	 *   </file>
	 * </example>
	 *
	 */

	/** @ngdoc directive
	 * @name ngOn
	 * @restrict A
	 * @element ANY
	 *
	 * @usage
	 *
	 * ```html
	 * <ANY ng-on-eventname="expression">
	 * </ANY>
	 * ```
	 *
	 * or with uppercase letters in property (e.g. "eventName"):
	 *
	 *
	 * ```html
	 * <ANY ng-on-event_name="expression">
	 * </ANY>
	 * ```
	 *
	 * @description
	 * The `ngOn` directive adds an event listener to a DOM element via
	 * {@link angular.element angular.element().on()}, and evaluates an expression when the event is
	 * fired.
	 * `ngOn` allows adding listeners for arbitrary events by including
	 * the event name in the attribute, e.g. `ng-on-drop="onDrop()"` executes the 'onDrop()' expression
	 * when the `drop` event is fired.
	 *
	 * AngularJS provides specific directives for many events, such as {@link ngClick}, so in most
	 * cases it is not necessary to use `ngOn`. However, AngularJS does not support all events
	 * (e.g. the `drop` event in the example above), and new events might be introduced in later DOM
	 * standards.
	 *
	 * Another use-case for `ngOn` is listening to
	 * [custom events](https://developer.mozilla.org/docs/Web/Guide/Events/Creating_and_triggering_events)
	 * fired by
	 * [custom elements](https://developer.mozilla.org/docs/Web/Web_Components/Using_custom_elements).
	 *
	 * ## Binding to camelCase properties
	 *
	 * Since HTML attributes are case-insensitive, camelCase properties like `myEvent` must be escaped.
	 * AngularJS uses the underscore (_) in front of a character to indicate that it is uppercase, so
	 * `myEvent` must be written as `ng-on-my_event="expression"`.
	 *
	 * @example
	 * ### Bind to built-in DOM events
	 *
	 * <example name="ngOn" module="exampleNgOn">
	 *   <file name="app.js">
	 *     angular.module('exampleNgOn', [])
	 *       .component('main', {
	 *         templateUrl: 'main.html',
	 *         controller: function() {
	 *           this.clickCount = 0;
	 *           this.mouseoverCount = 0;
	 *
	 *           this.loadingState = 0;
	 *         }
	 *       });
	 *   </file>
	 *   <file name="main.html">
	 *     <div>
	 *       This is equivalent to `ngClick` and `ngMouseover`:<br>
	 *       <button
	 *         ng-on-click="$ctrl.clickCount = $ctrl.clickCount + 1"
	 *         ng-on-mouseover="$ctrl.mouseoverCount = $ctrl.mouseoverCount + 1">Click or mouseover</button><br>
	 *       clickCount: {{$ctrl.clickCount}}<br>
	 *       mouseover: {{$ctrl.mouseoverCount}}
	 *
	 *       <hr>
	 *
	 *       For the `error` and `load` event on images no built-in AngularJS directives exist:<br>
	 *       <img src="thisimagedoesnotexist.png" ng-on-error="$ctrl.loadingState = -1" ng-on-load="$ctrl.loadingState = 1"><br>
	 *       <div ng-switch="$ctrl.loadingState">
	 *         <span ng-switch-when="0">Image is loading</span>
	 *         <span ng-switch-when="-1">Image load error</span>
	 *         <span ng-switch-when="1">Image loaded successfully</span>
	 *       </div>
	 *     </div>
	 *   </file>
	 *   <file name="index.html">
	 *     <main></main>
	 *   </file>
	 * </example>
	 *
	 *
	 * @example
	 * ### Bind to custom DOM events
	 *
	 * <example name="ngOnCustom" module="exampleNgOn">
	 *   <file name="app.js">
	 *     angular.module('exampleNgOn', [])
	 *       .component('main', {
	 *         templateUrl: 'main.html',
	 *         controller: function() {
	 *           this.eventLog = '';
	 *
	 *           this.listener = function($event) {
	 *             this.eventLog = 'Event with type "' + $event.type + '" fired at ' + $event.detail;
	 *           };
	 *         }
	 *       })
	 *       .component('childComponent', {
	 *         templateUrl: 'child.html',
	 *         controller: function($element) {
	 *           this.fireEvent = function() {
	 *             var event = new CustomEvent('customtype', { detail: new Date()});
	 *
	 *             $element[0].dispatchEvent(event);
	 *           };
	 *         }
	 *       });
	 *   </file>
	 *   <file name="main.html">
	 *     <child-component ng-on-customtype="$ctrl.listener($event)"></child-component><br>
	 *     <span>Event log: {{$ctrl.eventLog}}</span>
	 *   </file>
	 *   <file name="child.html">
		  <button ng-click="$ctrl.fireEvent()">Fire custom event</button>
	 *   </file>
	 *   <file name="index.html">
	 *     <main></main>
	 *   </file>
	 * </example>
	 */

	var $compileMinErr = minErr('$compile');

	function UNINITIALIZED_VALUE() { }
	var _UNINITIALIZED_VALUE = new UNINITIALIZED_VALUE();

	/**
	 * @ngdoc provider
	 * @name $compileProvider
	 *
	 * @description
	 */
	$CompileProvider.$inject = ['$provide', '$$sanitizeUriProvider'];
	/** @this */
	function $CompileProvider($provide, $$sanitizeUriProvider) {
		var hasDirectives = {},
			Suffix = 'Directive',
			COMMENT_DIRECTIVE_REGEXP = /^\s*directive:\s*([\w-]+)\s+(.*)$/,
			CLASS_DIRECTIVE_REGEXP = /(([\w-]+)(?::([^;]+))?;?)/,
			ALL_OR_NOTHING_ATTRS = makeMap('ngSrc,ngSrcset,src,srcset'),
			REQUIRE_PREFIX_REGEXP = /^(?:(\^\^?)?(\?)?(\^\^?)?)?/;

		// Ref: http://developers.whatwg.org/webappapis.html#event-handler-idl-attributes
		// The assumption is that future DOM event attribute names will begin with
		// 'on' and be composed of only English letters.
		var EVENT_HANDLER_ATTR_REGEXP = /^(on[a-z]+|formaction)$/;
		var bindingCache = createMap();

		function parseIsolateBindings(scope, directiveName, isController) {
			var LOCAL_REGEXP = /^([@&]|[=<](\*?))(\??)\s*([\w$]*)$/;

			var bindings = createMap();

			forEach(scope, function (definition, scopeName) {
				definition = definition.trim();

				if (definition in bindingCache) {
					bindings[scopeName] = bindingCache[definition];
					return;
				}
				var match = definition.match(LOCAL_REGEXP);

				if (!match) {
					throw $compileMinErr('iscp',
						'Invalid {3} for directive \'{0}\'.' +
						' Definition: {... {1}: \'{2}\' ...}',
						directiveName, scopeName, definition,
						(isController ? 'controller bindings definition' :
							'isolate scope definition'));
				}

				bindings[scopeName] = {
					mode: match[1][0],
					collection: match[2] === '*',
					optional: match[3] === '?',
					attrName: match[4] || scopeName
				};
				if (match[4]) {
					bindingCache[definition] = bindings[scopeName];
				}
			});

			return bindings;
		}

		function parseDirectiveBindings(directive, directiveName) {
			var bindings = {
				isolateScope: null,
				bindToController: null
			};
			if (isObject(directive.scope)) {
				if (directive.bindToController === true) {
					bindings.bindToController = parseIsolateBindings(directive.scope,
						directiveName, true);
					bindings.isolateScope = {};
				} else {
					bindings.isolateScope = parseIsolateBindings(directive.scope,
						directiveName, false);
				}
			}
			if (isObject(directive.bindToController)) {
				bindings.bindToController =
					parseIsolateBindings(directive.bindToController, directiveName, true);
			}
			if (bindings.bindToController && !directive.controller) {
				// There is no controller
				throw $compileMinErr('noctrl',
					'Cannot bind to controller without directive \'{0}\'s controller.',
					directiveName);
			}
			return bindings;
		}

		function assertValidDirectiveName(name) {
			var letter = name.charAt(0);
			if (!letter || letter !== lowercase(letter)) {
				throw $compileMinErr('baddir', 'Directive/Component name \'{0}\' is invalid. The first character must be a lowercase letter', name);
			}
			if (name !== name.trim()) {
				throw $compileMinErr('baddir',
					'Directive/Component name \'{0}\' is invalid. The name should not contain leading or trailing whitespaces',
					name);
			}
		}

		function getDirectiveRequire(directive) {
			var require = directive.require || (directive.controller && directive.name);

			if (!isArray(require) && isObject(require)) {
				forEach(require, function (value, key) {
					var match = value.match(REQUIRE_PREFIX_REGEXP);
					var name = value.substring(match[0].length);
					if (!name) require[key] = match[0] + key;
				});
			}

			return require;
		}

		function getDirectiveRestrict(restrict, name) {
			if (restrict && !(isString(restrict) && /[EACM]/.test(restrict))) {
				throw $compileMinErr('badrestrict',
					'Restrict property \'{0}\' of directive \'{1}\' is invalid',
					restrict,
					name);
			}

			return restrict || 'EA';
		}

		/**
		 * @ngdoc method
		 * @name $compileProvider#directive
		 * @kind function
		 *
		 * @description
		 * Register a new directive with the compiler.
		 *
		 * @param {string|Object} name Name of the directive in camel-case (i.e. `ngBind` which will match
		 *    as `ng-bind`), or an object map of directives where the keys are the names and the values
		 *    are the factories.
		 * @param {Function|Array} directiveFactory An injectable directive factory function. See the
		 *    {@link guide/directive directive guide} and the {@link $compile compile API} for more info.
		 * @returns {ng.$compileProvider} Self for chaining.
		 */
		this.directive = function registerDirective(name, directiveFactory) {
			assertArg(name, 'name');
			assertNotHasOwnProperty(name, 'directive');
			if (isString(name)) {
				assertValidDirectiveName(name);
				assertArg(directiveFactory, 'directiveFactory');
				if (!hasDirectives.hasOwnProperty(name)) {
					hasDirectives[name] = [];
					$provide.factory(name + Suffix, ['$injector', '$exceptionHandler',
						function ($injector, $exceptionHandler) {
							var directives = [];
							forEach(hasDirectives[name], function (directiveFactory, index) {
								try {
									var directive = $injector.invoke(directiveFactory);
									if (isFunction(directive)) {
										directive = { compile: valueFn(directive) };
									} else if (!directive.compile && directive.link) {
										directive.compile = valueFn(directive.link);
									}
									directive.priority = directive.priority || 0;
									directive.index = index;
									directive.name = directive.name || name;
									directive.require = getDirectiveRequire(directive);
									directive.restrict = getDirectiveRestrict(directive.restrict, name);
									directive.$$moduleName = directiveFactory.$$moduleName;
									directives.push(directive);
								} catch (e) {
									$exceptionHandler(e);
								}
							});
							return directives;
						}]);
				}
				hasDirectives[name].push(directiveFactory);
			} else {
				forEach(name, reverseParams(registerDirective));
			}
			return this;
		};

		/**
		 * @ngdoc method
		 * @name $compileProvider#component
		 * @module ng
		 * @param {string|Object} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`),
		 *    or an object map of components where the keys are the names and the values are the component definition objects.
		 * @param {Object} options Component definition object (a simplified
		 *    {@link ng.$compile#directive-definition-object directive definition object}),
		 *    with the following properties (all optional):
		 *
		 *    - `controller` – `{(string|function()=}` – controller constructor function that should be
		 *      associated with newly created scope or the name of a {@link ng.$compile#-controller-
		 *      registered controller} if passed as a string. An empty `noop` function by default.
		 *    - `controllerAs` – `{string=}` – identifier name for to reference the controller in the component's scope.
		 *      If present, the controller will be published to scope under the `controllerAs` name.
		 *      If not present, this will default to be `$ctrl`.
		 *    - `template` – `{string=|function()=}` – html template as a string or a function that
		 *      returns an html template as a string which should be used as the contents of this component.
		 *      Empty string by default.
		 *
		 *      If `template` is a function, then it is {@link auto.$injector#invoke injected} with
		 *      the following locals:
		 *
		 *      - `$element` - Current element
		 *      - `$attrs` - Current attributes object for the element
		 *
		 *    - `templateUrl` – `{string=|function()=}` – path or function that returns a path to an html
		 *      template that should be used  as the contents of this component.
		 *
		 *      If `templateUrl` is a function, then it is {@link auto.$injector#invoke injected} with
		 *      the following locals:
		 *
		 *      - `$element` - Current element
		 *      - `$attrs` - Current attributes object for the element
		 *
		 *    - `bindings` – `{object=}` – defines bindings between DOM attributes and component properties.
		 *      Component properties are always bound to the component controller and not to the scope.
		 *      See {@link ng.$compile#-bindtocontroller- `bindToController`}.
		 *    - `transclude` – `{boolean=}` – whether {@link $compile#transclusion content transclusion} is enabled.
		 *      Disabled by default.
		 *    - `require` - `{Object<string, string>=}` - requires the controllers of other directives and binds them to
		 *      this component's controller. The object keys specify the property names under which the required
		 *      controllers (object values) will be bound. See {@link ng.$compile#-require- `require`}.
		 *    - `$...` – additional properties to attach to the directive factory function and the controller
		 *      constructor function. (This is used by the component router to annotate)
		 *
		 * @returns {ng.$compileProvider} the compile provider itself, for chaining of function calls.
		 * @description
		 * Register a **component definition** with the compiler. This is a shorthand for registering a special
		 * type of directive, which represents a self-contained UI component in your application. Such components
		 * are always isolated (i.e. `scope: {}`) and are always restricted to elements (i.e. `restrict: 'E'`).
		 *
		 * Component definitions are very simple and do not require as much configuration as defining general
		 * directives. Component definitions usually consist only of a template and a controller backing it.
		 *
		 * In order to make the definition easier, components enforce best practices like use of `controllerAs`,
		 * `bindToController`. They always have **isolate scope** and are restricted to elements.
		 *
		 * Here are a few examples of how you would usually define components:
		 *
		 * ```js
		 *   var myMod = angular.module(...);
		 *   myMod.component('myComp', {
		 *     template: '<div>My name is {{$ctrl.name}}</div>',
		 *     controller: function() {
		 *       this.name = 'shahar';
		 *     }
		 *   });
		 *
		 *   myMod.component('myComp', {
		 *     template: '<div>My name is {{$ctrl.name}}</div>',
		 *     bindings: {name: '@'}
		 *   });
		 *
		 *   myMod.component('myComp', {
		 *     templateUrl: 'views/my-comp.html',
		 *     controller: 'MyCtrl',
		 *     controllerAs: 'ctrl',
		 *     bindings: {name: '@'}
		 *   });
		 *
		 * ```
		 * For more examples, and an in-depth guide, see the {@link guide/component component guide}.
		 *
		 * <br />
		 * See also {@link ng.$compileProvider#directive $compileProvider.directive()}.
		 */
		this.component = function registerComponent(name, options) {
			if (!isString(name)) {
				forEach(name, reverseParams(bind(this, registerComponent)));
				return this;
			}

			var controller = options.controller || function () { };

			function factory($injector) {
				function makeInjectable(fn) {
					if (isFunction(fn) || isArray(fn)) {
						return /** @this */ function (tElement, tAttrs) {
							return $injector.invoke(fn, this, { $element: tElement, $attrs: tAttrs });
						};
					} else {
						return fn;
					}
				}

				var template = (!options.template && !options.templateUrl ? '' : options.template);
				var ddo = {
					controller: controller,
					controllerAs: identifierForController(options.controller) || options.controllerAs || '$ctrl',
					template: makeInjectable(template),
					templateUrl: makeInjectable(options.templateUrl),
					transclude: options.transclude,
					scope: {},
					bindToController: options.bindings || {},
					restrict: 'E',
					require: options.require
				};

				// Copy annotations (starting with $) over to the DDO
				forEach(options, function (val, key) {
					if (key.charAt(0) === '$') ddo[key] = val;
				});

				return ddo;
			}

			// TODO(pete) remove the following `forEach` before we release 1.6.0
			// The component-router@0.2.0 looks for the annotations on the controller constructor
			// Nothing in AngularJS looks for annotations on the factory function but we can't remove
			// it from 1.5.x yet.

			// Copy any annotation properties (starting with $) over to the factory and controller constructor functions
			// These could be used by libraries such as the new component router
			forEach(options, function (val, key) {
				if (key.charAt(0) === '$') {
					factory[key] = val;
					// Don't try to copy over annotations to named controller
					if (isFunction(controller)) controller[key] = val;
				}
			});

			factory.$inject = ['$injector'];

			return this.directive(name, factory);
		};


		/**
		 * @ngdoc method
		 * @name $compileProvider#aHrefSanitizationTrustedUrlList
		 * @kind function
		 *
		 * @description
		 * Retrieves or overrides the default regular expression that is used for determining trusted safe
		 * urls during a[href] sanitization.
		 *
		 * The sanitization is a security measure aimed at preventing XSS attacks via html links.
		 *
		 * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
		 * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationTrustedUrlList`
		 * regular expression. If a match is found, the original url is written into the dom. Otherwise,
		 * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
		 *
		 * @param {RegExp=} regexp New regexp to trust urls with.
		 * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
		 *    chaining otherwise.
		 */
		this.aHrefSanitizationTrustedUrlList = function (regexp) {
			if (isDefined(regexp)) {
				$$sanitizeUriProvider.aHrefSanitizationTrustedUrlList(regexp);
				return this;
			} else {
				return $$sanitizeUriProvider.aHrefSanitizationTrustedUrlList();
			}
		};


		/**
		 * @ngdoc method
		 * @name $compileProvider#aHrefSanitizationWhitelist
		 * @kind function
		 *
		 * @deprecated
		 * sinceVersion="1.8.1"
		 *
		 * This method is deprecated. Use {@link $compileProvider#aHrefSanitizationTrustedUrlList
		 * aHrefSanitizationTrustedUrlList} instead.
		 */
		Object.defineProperty(this, 'aHrefSanitizationWhitelist', {
			get: function () {
				return this.aHrefSanitizationTrustedUrlList;
			},
			set: function (value) {
				this.aHrefSanitizationTrustedUrlList = value;
			}
		});


		/**
		 * @ngdoc method
		 * @name $compileProvider#imgSrcSanitizationTrustedUrlList
		 * @kind function
		 *
		 * @description
		 * Retrieves or overrides the default regular expression that is used for determining trusted safe
		 * urls during img[src] sanitization.
		 *
		 * The sanitization is a security measure aimed at prevent XSS attacks via html links.
		 *
		 * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
		 * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationTrustedUrlList`
		 * regular expression. If a match is found, the original url is written into the dom. Otherwise,
		 * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
		 *
		 * @param {RegExp=} regexp New regexp to trust urls with.
		 * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
		 *    chaining otherwise.
		 */
		this.imgSrcSanitizationTrustedUrlList = function (regexp) {
			if (isDefined(regexp)) {
				$$sanitizeUriProvider.imgSrcSanitizationTrustedUrlList(regexp);
				return this;
			} else {
				return $$sanitizeUriProvider.imgSrcSanitizationTrustedUrlList();
			}
		};


		/**
		 * @ngdoc method
		 * @name $compileProvider#imgSrcSanitizationWhitelist
		 * @kind function
		 *
		 * @deprecated
		 * sinceVersion="1.8.1"
		 *
		 * This method is deprecated. Use {@link $compileProvider#imgSrcSanitizationTrustedUrlList
		 * imgSrcSanitizationTrustedUrlList} instead.
		 */
		Object.defineProperty(this, 'imgSrcSanitizationWhitelist', {
			get: function () {
				return this.imgSrcSanitizationTrustedUrlList;
			},
			set: function (value) {
				this.imgSrcSanitizationTrustedUrlList = value;
			}
		});

		/**
		 * @ngdoc method
		 * @name  $compileProvider#debugInfoEnabled
		 *
		 * @param {boolean=} enabled update the debugInfoEnabled state if provided, otherwise just return the
		 * current debugInfoEnabled state
		 * @returns {*} current value if used as getter or itself (chaining) if used as setter
		 *
		 * @kind function
		 *
		 * @description
		 * Call this method to enable/disable various debug runtime information in the compiler such as adding
		 * binding information and a reference to the current scope on to DOM elements.
		 * If enabled, the compiler will add the following to DOM elements that have been bound to the scope
		 * * `ng-binding` CSS class
		 * * `ng-scope` and `ng-isolated-scope` CSS classes
		 * * `$binding` data property containing an array of the binding expressions
		 * * Data properties used by the {@link angular.element#methods `scope()`/`isolateScope()` methods} to return
		 *   the element's scope.
		 * * Placeholder comments will contain information about what directive and binding caused the placeholder.
		 *   E.g. `<!-- ngIf: shouldShow() -->`.
		 *
		 * You may want to disable this in production for a significant performance boost. See
		 * {@link guide/production#disabling-debug-data Disabling Debug Data} for more.
		 *
		 * The default value is true.
		 */
		var debugInfoEnabled = true;
		this.debugInfoEnabled = function (enabled) {
			if (isDefined(enabled)) {
				debugInfoEnabled = enabled;
				return this;
			}
			return debugInfoEnabled;
		};

		/**
		 * @ngdoc method
		 * @name  $compileProvider#strictComponentBindingsEnabled
		 *
		 * @param {boolean=} enabled update the strictComponentBindingsEnabled state if provided,
		 * otherwise return the current strictComponentBindingsEnabled state.
		 * @returns {*} current value if used as getter or itself (chaining) if used as setter
		 *
		 * @kind function
		 *
		 * @description
		 * Call this method to enable / disable the strict component bindings check. If enabled, the
		 * compiler will enforce that all scope / controller bindings of a
		 * {@link $compileProvider#directive directive} / {@link $compileProvider#component component}
		 * that are not set as optional with `?`, must be provided when the directive is instantiated.
		 * If not provided, the compiler will throw the
		 * {@link error/$compile/missingattr $compile:missingattr error}.
		 *
		 * The default value is false.
		 */
		var strictComponentBindingsEnabled = false;
		this.strictComponentBindingsEnabled = function (enabled) {
			if (isDefined(enabled)) {
				strictComponentBindingsEnabled = enabled;
				return this;
			}
			return strictComponentBindingsEnabled;
		};

		var TTL = 10;
		/**
		 * @ngdoc method
		 * @name $compileProvider#onChangesTtl
		 * @description
		 *
		 * Sets the number of times `$onChanges` hooks can trigger new changes before giving up and
		 * assuming that the model is unstable.
		 *
		 * The current default is 10 iterations.
		 *
		 * In complex applications it's possible that dependencies between `$onChanges` hooks and bindings will result
		 * in several iterations of calls to these hooks. However if an application needs more than the default 10
		 * iterations to stabilize then you should investigate what is causing the model to continuously change during
		 * the `$onChanges` hook execution.
		 *
		 * Increasing the TTL could have performance implications, so you should not change it without proper justification.
		 *
		 * @param {number} limit The number of `$onChanges` hook iterations.
		 * @returns {number|object} the current limit (or `this` if called as a setter for chaining)
		 */
		this.onChangesTtl = function (value) {
			if (arguments.length) {
				TTL = value;
				return this;
			}
			return TTL;
		};

		var commentDirectivesEnabledConfig = true;
		/**
		 * @ngdoc method
		 * @name $compileProvider#commentDirectivesEnabled
		 * @description
		 *
		 * It indicates to the compiler
		 * whether or not directives on comments should be compiled.
		 * Defaults to `true`.
		 *
		 * Calling this function with false disables the compilation of directives
		 * on comments for the whole application.
		 * This results in a compilation performance gain,
		 * as the compiler doesn't have to check comments when looking for directives.
		 * This should however only be used if you are sure that no comment directives are used in
		 * the application (including any 3rd party directives).
		 *
		 * @param {boolean} enabled `false` if the compiler may ignore directives on comments
		 * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
		 */
		this.commentDirectivesEnabled = function (value) {
			if (arguments.length) {
				commentDirectivesEnabledConfig = value;
				return this;
			}
			return commentDirectivesEnabledConfig;
		};


		var cssClassDirectivesEnabledConfig = true;
		/**
		 * @ngdoc method
		 * @name $compileProvider#cssClassDirectivesEnabled
		 * @description
		 *
		 * It indicates to the compiler
		 * whether or not directives on element classes should be compiled.
		 * Defaults to `true`.
		 *
		 * Calling this function with false disables the compilation of directives
		 * on element classes for the whole application.
		 * This results in a compilation performance gain,
		 * as the compiler doesn't have to check element classes when looking for directives.
		 * This should however only be used if you are sure that no class directives are used in
		 * the application (including any 3rd party directives).
		 *
		 * @param {boolean} enabled `false` if the compiler may ignore directives on element classes
		 * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
		 */
		this.cssClassDirectivesEnabled = function (value) {
			if (arguments.length) {
				cssClassDirectivesEnabledConfig = value;
				return this;
			}
			return cssClassDirectivesEnabledConfig;
		};


		/**
		 * The security context of DOM Properties.
		 * @private
		 */
		var PROP_CONTEXTS = createMap();

		/**
		 * @ngdoc method
		 * @name $compileProvider#addPropertySecurityContext
		 * @description
		 *
		 * Defines the security context for DOM properties bound by ng-prop-*.
		 *
		 * @param {string} elementName The element name or '*' to match any element.
		 * @param {string} propertyName The DOM property name.
		 * @param {string} ctx The {@link $sce} security context in which this value is safe for use, e.g. `$sce.URL`
		 * @returns {object} `this` for chaining
		 */
		this.addPropertySecurityContext = function (elementName, propertyName, ctx) {
			var key = (elementName.toLowerCase() + '|' + propertyName.toLowerCase());

			if (key in PROP_CONTEXTS && PROP_CONTEXTS[key] !== ctx) {
				throw $compileMinErr('ctxoverride', 'Property context \'{0}.{1}\' already set to \'{2}\', cannot override to \'{3}\'.', elementName, propertyName, PROP_CONTEXTS[key], ctx);
			}

			PROP_CONTEXTS[key] = ctx;
			return this;
		};

		/* Default property contexts.
		 *
		 * Copy of https://github.com/angular/angular/blob/6.0.6/packages/compiler/src/schema/dom_security_schema.ts#L31-L58
		 * Changing:
		 * - SecurityContext.* => SCE_CONTEXTS/$sce.*
		 * - STYLE => CSS
		 * - various URL => MEDIA_URL
		 * - *|formAction, form|action URL => RESOURCE_URL (like the attribute)
		 */
		(function registerNativePropertyContexts() {
			function registerContext(ctx, values) {
				forEach(values, function (v) { PROP_CONTEXTS[v.toLowerCase()] = ctx; });
			}

			registerContext(SCE_CONTEXTS.HTML, [
				'iframe|srcdoc',
				'*|innerHTML',
				'*|outerHTML'
			]);
			registerContext(SCE_CONTEXTS.CSS, ['*|style']);
			registerContext(SCE_CONTEXTS.URL, [
				'area|href', 'area|ping',
				'a|href', 'a|ping',
				'blockquote|cite',
				'body|background',
				'del|cite',
				'input|src',
				'ins|cite',
				'q|cite'
			]);
			registerContext(SCE_CONTEXTS.MEDIA_URL, [
				'audio|src',
				'img|src', 'img|srcset',
				'source|src', 'source|srcset',
				'track|src',
				'video|src', 'video|poster'
			]);
			registerContext(SCE_CONTEXTS.RESOURCE_URL, [
				'*|formAction',
				'applet|code', 'applet|codebase',
				'base|href',
				'embed|src',
				'frame|src',
				'form|action',
				'head|profile',
				'html|manifest',
				'iframe|src',
				'link|href',
				'media|src',
				'object|codebase', 'object|data',
				'script|src'
			]);
		})();


		this.$get = [
			'$injector', '$interpolate', '$exceptionHandler', '$templateRequest', '$parse',
			'$controller', '$rootScope', '$sce', '$animate',
			function ($injector, $interpolate, $exceptionHandler, $templateRequest, $parse,
				$controller, $rootScope, $sce, $animate) {

				var SIMPLE_ATTR_NAME = /^\w/;
				var specialAttrHolder = window.document.createElement('div');


				var commentDirectivesEnabled = commentDirectivesEnabledConfig;
				var cssClassDirectivesEnabled = cssClassDirectivesEnabledConfig;


				var onChangesTtl = TTL;
				// The onChanges hooks should all be run together in a single digest
				// When changes occur, the call to trigger their hooks will be added to this queue
				var onChangesQueue;

				// This function is called in a $$postDigest to trigger all the onChanges hooks in a single digest
				function flushOnChangesQueue() {
					try {
						if (!(--onChangesTtl)) {
							// We have hit the TTL limit so reset everything
							onChangesQueue = undefined;
							throw $compileMinErr('infchng', '{0} $onChanges() iterations reached. Aborting!\n', TTL);
						}
						// We must run this hook in an apply since the $$postDigest runs outside apply
						$rootScope.$apply(function () {
							for (var i = 0, ii = onChangesQueue.length; i < ii; ++i) {
								try {
									onChangesQueue[i]();
								} catch (e) {
									$exceptionHandler(e);
								}
							}
							// Reset the queue to trigger a new schedule next time there is a change
							onChangesQueue = undefined;
						});
					} finally {
						onChangesTtl++;
					}
				}


				function sanitizeSrcset(value, invokeType) {
					if (!value) {
						return value;
					}
					if (!isString(value)) {
						throw $compileMinErr('srcset', 'Can\'t pass trusted values to `{0}`: "{1}"', invokeType, value.toString());
					}

					// Such values are a bit too complex to handle automatically inside $sce.
					// Instead, we sanitize each of the URIs individually, which works, even dynamically.

					// It's not possible to work around this using `$sce.trustAsMediaUrl`.
					// If you want to programmatically set explicitly trusted unsafe URLs, you should use
					// `$sce.trustAsHtml` on the whole `img` tag and inject it into the DOM using the
					// `ng-bind-html` directive.

					var result = '';

					// first check if there are spaces because it's not the same pattern
					var trimmedSrcset = trim(value);
					//                (   999x   ,|   999w   ,|   ,|,   )
					var srcPattern = /(\s+\d+x\s*,|\s+\d+w\s*,|\s+,|,\s+)/;
					var pattern = /\s/.test(trimmedSrcset) ? srcPattern : /(,)/;

					// split srcset into tuple of uri and descriptor except for the last item
					var rawUris = trimmedSrcset.split(pattern);

					// for each tuples
					var nbrUrisWith2parts = Math.floor(rawUris.length / 2);
					for (var i = 0; i < nbrUrisWith2parts; i++) {
						var innerIdx = i * 2;
						// sanitize the uri
						result += $sce.getTrustedMediaUrl(trim(rawUris[innerIdx]));
						// add the descriptor
						result += ' ' + trim(rawUris[innerIdx + 1]);
					}

					// split the last item into uri and descriptor
					var lastTuple = trim(rawUris[i * 2]).split(/\s/);

					// sanitize the last uri
					result += $sce.getTrustedMediaUrl(trim(lastTuple[0]));

					// and add the last descriptor if any
					if (lastTuple.length === 2) {
						result += (' ' + trim(lastTuple[1]));
					}
					return result;
				}


				function Attributes(element, attributesToCopy) {
					if (attributesToCopy) {
						var keys = Object.keys(attributesToCopy);
						var i, l, key;

						for (i = 0, l = keys.length; i < l; i++) {
							key = keys[i];
							this[key] = attributesToCopy[key];
						}
					} else {
						this.$attr = {};
					}

					this.$$element = element;
				}

				Attributes.prototype = {
					/**
					 * @ngdoc method
					 * @name $compile.directive.Attributes#$normalize
					 * @kind function
					 *
					 * @description
					 * Converts an attribute name (e.g. dash/colon/underscore-delimited string, optionally prefixed with `x-` or
					 * `data-`) to its normalized, camelCase form.
					 *
					 * Also there is special case for Moz prefix starting with upper case letter.
					 *
					 * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
					 *
					 * @param {string} name Name to normalize
					 */
					$normalize: directiveNormalize,


					/**
					 * @ngdoc method
					 * @name $compile.directive.Attributes#$addClass
					 * @kind function
					 *
					 * @description
					 * Adds the CSS class value specified by the classVal parameter to the element. If animations
					 * are enabled then an animation will be triggered for the class addition.
					 *
					 * @param {string} classVal The className value that will be added to the element
					 */
					$addClass: function (classVal) {
						if (classVal && classVal.length > 0) {
							$animate.addClass(this.$$element, classVal);
						}
					},

					/**
					 * @ngdoc method
					 * @name $compile.directive.Attributes#$removeClass
					 * @kind function
					 *
					 * @description
					 * Removes the CSS class value specified by the classVal parameter from the element. If
					 * animations are enabled then an animation will be triggered for the class removal.
					 *
					 * @param {string} classVal The className value that will be removed from the element
					 */
					$removeClass: function (classVal) {
						if (classVal && classVal.length > 0) {
							$animate.removeClass(this.$$element, classVal);
						}
					},

					/**
					 * @ngdoc method
					 * @name $compile.directive.Attributes#$updateClass
					 * @kind function
					 *
					 * @description
					 * Adds and removes the appropriate CSS class values to the element based on the difference
					 * between the new and old CSS class values (specified as newClasses and oldClasses).
					 *
					 * @param {string} newClasses The current CSS className value
					 * @param {string} oldClasses The former CSS className value
					 */
					$updateClass: function (newClasses, oldClasses) {
						var toAdd = tokenDifference(newClasses, oldClasses);
						if (toAdd && toAdd.length) {
							$animate.addClass(this.$$element, toAdd);
						}

						var toRemove = tokenDifference(oldClasses, newClasses);
						if (toRemove && toRemove.length) {
							$animate.removeClass(this.$$element, toRemove);
						}
					},

					/**
					 * Set a normalized attribute on the element in a way such that all directives
					 * can share the attribute. This function properly handles boolean attributes.
					 * @param {string} key Normalized key. (ie ngAttribute)
					 * @param {string|boolean} value The value to set. If `null` attribute will be deleted.
					 * @param {boolean=} writeAttr If false, does not write the value to DOM element attribute.
					 *     Defaults to true.
					 * @param {string=} attrName Optional none normalized name. Defaults to key.
					 */
					$set: function (key, value, writeAttr, attrName) {
						// TODO: decide whether or not to throw an error if "class"
						// is set through this function since it may cause $updateClass to
						// become unstable.

						var node = this.$$element[0],
							booleanKey = getBooleanAttrName(node, key),
							aliasedKey = getAliasedAttrName(key),
							observer = key,
							nodeName;

						if (booleanKey) {
							this.$$element.prop(key, value);
							attrName = booleanKey;
						} else if (aliasedKey) {
							this[aliasedKey] = value;
							observer = aliasedKey;
						}

						this[key] = value;

						// translate normalized key to actual key
						if (attrName) {
							this.$attr[key] = attrName;
						} else {
							attrName = this.$attr[key];
							if (!attrName) {
								this.$attr[key] = attrName = snake_case(key, '-');
							}
						}

						nodeName = nodeName_(this.$$element);

						// Sanitize img[srcset] values.
						if (nodeName === 'img' && key === 'srcset') {
							this[key] = value = sanitizeSrcset(value, '$set(\'srcset\', value)');
						}

						if (writeAttr !== false) {
							if (value === null || isUndefined(value)) {
								this.$$element.removeAttr(attrName);
							} else {
								if (SIMPLE_ATTR_NAME.test(attrName)) {
									// jQuery skips special boolean attrs treatment in XML nodes for
									// historical reasons and hence AngularJS cannot freely call
									// `.attr(attrName, false) with such attributes. To avoid issues
									// in XHTML, call `removeAttr` in such cases instead.
									// See https://github.com/jquery/jquery/issues/4249
									if (booleanKey && value === false) {
										this.$$element.removeAttr(attrName);
									} else {
										this.$$element.attr(attrName, value);
									}
								} else {
									setSpecialAttr(this.$$element[0], attrName, value);
								}
							}
						}

						// fire observers
						var $$observers = this.$$observers;
						if ($$observers) {
							forEach($$observers[observer], function (fn) {
								try {
									fn(value);
								} catch (e) {
									$exceptionHandler(e);
								}
							});
						}
					},


					/**
					 * @ngdoc method
					 * @name $compile.directive.Attributes#$observe
					 * @kind function
					 *
					 * @description
					 * Observes an interpolated attribute.
					 *
					 * The observer function will be invoked once during the next `$digest` following
					 * compilation. The observer is then invoked whenever the interpolated value
					 * changes.
					 *
					 * @param {string} key Normalized key. (ie ngAttribute) .
					 * @param {function(interpolatedValue)} fn Function that will be called whenever
							  the interpolated value of the attribute changes.
					 *        See the {@link guide/interpolation#how-text-and-attribute-bindings-work Interpolation
					 *        guide} for more info.
					 * @returns {function()} Returns a deregistration function for this observer.
					 */
					$observe: function (key, fn) {
						var attrs = this,
							$$observers = (attrs.$$observers || (attrs.$$observers = createMap())),
							listeners = ($$observers[key] || ($$observers[key] = []));

						listeners.push(fn);
						$rootScope.$evalAsync(function () {
							if (!listeners.$$inter && attrs.hasOwnProperty(key) && !isUndefined(attrs[key])) {
								// no one registered attribute interpolation function, so lets call it manually
								fn(attrs[key]);
							}
						});

						return function () {
							arrayRemove(listeners, fn);
						};
					}
				};

				function setSpecialAttr(element, attrName, value) {
					// Attributes names that do not start with letters (such as `(click)`) cannot be set using `setAttribute`
					// so we have to jump through some hoops to get such an attribute
					// https://github.com/angular/angular.js/pull/13318
					specialAttrHolder.innerHTML = '<span ' + attrName + '>';
					var attributes = specialAttrHolder.firstChild.attributes;
					var attribute = attributes[0];
					// We have to remove the attribute from its container element before we can add it to the destination element
					attributes.removeNamedItem(attribute.name);
					attribute.value = value;
					element.attributes.setNamedItem(attribute);
				}

				function safeAddClass($element, className) {
					try {
						$element.addClass(className);
					} catch (e) {
						// ignore, since it means that we are trying to set class on
						// SVG element, where class name is read-only.
					}
				}


				var startSymbol = $interpolate.startSymbol(),
					endSymbol = $interpolate.endSymbol(),
					denormalizeTemplate = (startSymbol === '{{' && endSymbol === '}}')
						? identity
						: function denormalizeTemplate(template) {
							return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
						},
					NG_PREFIX_BINDING = /^ng(Attr|Prop|On)([A-Z].*)$/;
				var MULTI_ELEMENT_DIR_RE = /^(.+)Start$/;

				compile.$$addBindingInfo = debugInfoEnabled ? function $$addBindingInfo($element, binding) {
					var bindings = $element.data('$binding') || [];

					if (isArray(binding)) {
						bindings = bindings.concat(binding);
					} else {
						bindings.push(binding);
					}

					$element.data('$binding', bindings);
				} : noop;

				compile.$$addBindingClass = debugInfoEnabled ? function $$addBindingClass($element) {
					safeAddClass($element, 'ng-binding');
				} : noop;

				compile.$$addScopeInfo = debugInfoEnabled ? function $$addScopeInfo($element, scope, isolated, noTemplate) {
					var dataName = isolated ? (noTemplate ? '$isolateScopeNoTemplate' : '$isolateScope') : '$scope';
					$element.data(dataName, scope);
				} : noop;

				compile.$$addScopeClass = debugInfoEnabled ? function $$addScopeClass($element, isolated) {
					safeAddClass($element, isolated ? 'ng-isolate-scope' : 'ng-scope');
				} : noop;

				compile.$$createComment = function (directiveName, comment) {
					var content = '';
					if (debugInfoEnabled) {
						content = ' ' + (directiveName || '') + ': ';
						if (comment) content += comment + ' ';
					}
					return window.document.createComment(content);
				};

				return compile;

				//================================

				function compile($compileNodes, transcludeFn, maxPriority, ignoreDirective,
					previousCompileContext) {
					if (!($compileNodes instanceof jqLite)) {
						// jquery always rewraps, whereas we need to preserve the original selector so that we can
						// modify it.
						$compileNodes = jqLite($compileNodes);
					}
					var compositeLinkFn =
						compileNodes($compileNodes, transcludeFn, $compileNodes,
							maxPriority, ignoreDirective, previousCompileContext);
					compile.$$addScopeClass($compileNodes);
					var namespace = null;
					return function publicLinkFn(scope, cloneConnectFn, options) {
						if (!$compileNodes) {
							throw $compileMinErr('multilink', 'This element has already been linked.');
						}
						assertArg(scope, 'scope');

						if (previousCompileContext && previousCompileContext.needsNewScope) {
							// A parent directive did a replace and a directive on this element asked
							// for transclusion, which caused us to lose a layer of element on which
							// we could hold the new transclusion scope, so we will create it manually
							// here.
							scope = scope.$parent.$new();
						}

						options = options || {};
						var parentBoundTranscludeFn = options.parentBoundTranscludeFn,
							transcludeControllers = options.transcludeControllers,
							futureParentElement = options.futureParentElement;

						// When `parentBoundTranscludeFn` is passed, it is a
						// `controllersBoundTransclude` function (it was previously passed
						// as `transclude` to directive.link) so we must unwrap it to get
						// its `boundTranscludeFn`
						if (parentBoundTranscludeFn && parentBoundTranscludeFn.$$boundTransclude) {
							parentBoundTranscludeFn = parentBoundTranscludeFn.$$boundTransclude;
						}

						if (!namespace) {
							namespace = detectNamespaceForChildElements(futureParentElement);
						}
						var $linkNode;
						if (namespace !== 'html') {
							// When using a directive with replace:true and templateUrl the $compileNodes
							// (or a child element inside of them)
							// might change, so we need to recreate the namespace adapted compileNodes
							// for call to the link function.
							// Note: This will already clone the nodes...
							$linkNode = jqLite(
								wrapTemplate(namespace, jqLite('<div></div>').append($compileNodes).html())
							);
						} else if (cloneConnectFn) {
							// important!!: we must call our jqLite.clone() since the jQuery one is trying to be smart
							// and sometimes changes the structure of the DOM.
							$linkNode = JQLitePrototype.clone.call($compileNodes);
						} else {
							$linkNode = $compileNodes;
						}

						if (transcludeControllers) {
							for (var controllerName in transcludeControllers) {
								$linkNode.data('$' + controllerName + 'Controller', transcludeControllers[controllerName].instance);
							}
						}

						compile.$$addScopeInfo($linkNode, scope);

						if (cloneConnectFn) cloneConnectFn($linkNode, scope);
						if (compositeLinkFn) compositeLinkFn(scope, $linkNode, $linkNode, parentBoundTranscludeFn);

						if (!cloneConnectFn) {
							$compileNodes = compositeLinkFn = null;
						}
						return $linkNode;
					};
				}

				function detectNamespaceForChildElements(parentElement) {
					// TODO: Make this detect MathML as well...
					var node = parentElement && parentElement[0];
					if (!node) {
						return 'html';
					} else {
						return nodeName_(node) !== 'foreignobject' && toString.call(node).match(/SVG/) ? 'svg' : 'html';
					}
				}

				/**
				 * Compile function matches each node in nodeList against the directives. Once all directives
				 * for a particular node are collected their compile functions are executed. The compile
				 * functions return values - the linking functions - are combined into a composite linking
				 * function, which is the a linking function for the node.
				 *
				 * @param {NodeList} nodeList an array of nodes or NodeList to compile
				 * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
				 *        scope argument is auto-generated to the new child of the transcluded parent scope.
				 * @param {DOMElement=} $rootElement If the nodeList is the root of the compilation tree then
				 *        the rootElement must be set the jqLite collection of the compile root. This is
				 *        needed so that the jqLite collection items can be replaced with widgets.
				 * @param {number=} maxPriority Max directive priority.
				 * @returns {Function} A composite linking function of all of the matched directives or null.
				 */
				function compileNodes(nodeList, transcludeFn, $rootElement, maxPriority, ignoreDirective,
					previousCompileContext) {
					var linkFns = [],
						// `nodeList` can be either an element's `.childNodes` (live NodeList)
						// or a jqLite/jQuery collection or an array
						notLiveList = isArray(nodeList) || (nodeList instanceof jqLite),
						attrs, directives, nodeLinkFn, childNodes, childLinkFn, linkFnFound, nodeLinkFnFound;


					for (var i = 0; i < nodeList.length; i++) {
						attrs = new Attributes();

						// Support: IE 11 only
						// Workaround for #11781 and #14924
						if (msie === 11) {
							mergeConsecutiveTextNodes(nodeList, i, notLiveList);
						}

						// We must always refer to `nodeList[i]` hereafter,
						// since the nodes can be replaced underneath us.
						directives = collectDirectives(nodeList[i], [], attrs, i === 0 ? maxPriority : undefined,
							ignoreDirective);

						nodeLinkFn = (directives.length)
							? applyDirectivesToNode(directives, nodeList[i], attrs, transcludeFn, $rootElement,
								null, [], [], previousCompileContext)
							: null;

						if (nodeLinkFn && nodeLinkFn.scope) {
							compile.$$addScopeClass(attrs.$$element);
						}

						childLinkFn = (nodeLinkFn && nodeLinkFn.terminal ||
							!(childNodes = nodeList[i].childNodes) ||
							!childNodes.length)
							? null
							: compileNodes(childNodes,
								nodeLinkFn ? (
									(nodeLinkFn.transcludeOnThisElement || !nodeLinkFn.templateOnThisElement)
									&& nodeLinkFn.transclude) : transcludeFn);

						if (nodeLinkFn || childLinkFn) {
							linkFns.push(i, nodeLinkFn, childLinkFn);
							linkFnFound = true;
							nodeLinkFnFound = nodeLinkFnFound || nodeLinkFn;
						}

						//use the previous context only for the first element in the virtual group
						previousCompileContext = null;
					}

					// return a linking function if we have found anything, null otherwise
					return linkFnFound ? compositeLinkFn : null;

					function compositeLinkFn(scope, nodeList, $rootElement, parentBoundTranscludeFn) {
						var nodeLinkFn, childLinkFn, node, childScope, i, ii, idx, childBoundTranscludeFn;
						var stableNodeList;


						if (nodeLinkFnFound) {
							// copy nodeList so that if a nodeLinkFn removes or adds an element at this DOM level our
							// offsets don't get screwed up
							var nodeListLength = nodeList.length;
							stableNodeList = new Array(nodeListLength);

							// create a sparse array by only copying the elements which have a linkFn
							for (i = 0; i < linkFns.length; i += 3) {
								idx = linkFns[i];
								stableNodeList[idx] = nodeList[idx];
							}
						} else {
							stableNodeList = nodeList;
						}

						for (i = 0, ii = linkFns.length; i < ii;) {
							node = stableNodeList[linkFns[i++]];
							nodeLinkFn = linkFns[i++];
							childLinkFn = linkFns[i++];

							if (nodeLinkFn) {
								if (nodeLinkFn.scope) {
									childScope = scope.$new();
									compile.$$addScopeInfo(jqLite(node), childScope);
								} else {
									childScope = scope;
								}

								if (nodeLinkFn.transcludeOnThisElement) {
									childBoundTranscludeFn = createBoundTranscludeFn(
										scope, nodeLinkFn.transclude, parentBoundTranscludeFn);

								} else if (!nodeLinkFn.templateOnThisElement && parentBoundTranscludeFn) {
									childBoundTranscludeFn = parentBoundTranscludeFn;

								} else if (!parentBoundTranscludeFn && transcludeFn) {
									childBoundTranscludeFn = createBoundTranscludeFn(scope, transcludeFn);

								} else {
									childBoundTranscludeFn = null;
								}

								nodeLinkFn(childLinkFn, childScope, node, $rootElement, childBoundTranscludeFn);

							} else if (childLinkFn) {
								childLinkFn(scope, node.childNodes, undefined, parentBoundTranscludeFn);
							}
						}
					}
				}

				function mergeConsecutiveTextNodes(nodeList, idx, notLiveList) {
					var node = nodeList[idx];
					var parent = node.parentNode;
					var sibling;

					if (node.nodeType !== NODE_TYPE_TEXT) {
						return;
					}

					while (true) {
						sibling = parent ? node.nextSibling : nodeList[idx + 1];
						if (!sibling || sibling.nodeType !== NODE_TYPE_TEXT) {
							break;
						}

						node.nodeValue = node.nodeValue + sibling.nodeValue;

						if (sibling.parentNode) {
							sibling.parentNode.removeChild(sibling);
						}
						if (notLiveList && sibling === nodeList[idx + 1]) {
							nodeList.splice(idx + 1, 1);
						}
					}
				}

				function createBoundTranscludeFn(scope, transcludeFn, previousBoundTranscludeFn) {
					function boundTranscludeFn(transcludedScope, cloneFn, controllers, futureParentElement, containingScope) {

						if (!transcludedScope) {
							transcludedScope = scope.$new(false, containingScope);
							transcludedScope.$$transcluded = true;
						}

						return transcludeFn(transcludedScope, cloneFn, {
							parentBoundTranscludeFn: previousBoundTranscludeFn,
							transcludeControllers: controllers,
							futureParentElement: futureParentElement
						});
					}

					// We need  to attach the transclusion slots onto the `boundTranscludeFn`
					// so that they are available inside the `controllersBoundTransclude` function
					var boundSlots = boundTranscludeFn.$$slots = createMap();
					for (var slotName in transcludeFn.$$slots) {
						if (transcludeFn.$$slots[slotName]) {
							boundSlots[slotName] = createBoundTranscludeFn(scope, transcludeFn.$$slots[slotName], previousBoundTranscludeFn);
						} else {
							boundSlots[slotName] = null;
						}
					}

					return boundTranscludeFn;
				}

				/**
				 * Looks for directives on the given node and adds them to the directive collection which is
				 * sorted.
				 *
				 * @param node Node to search.
				 * @param directives An array to which the directives are added to. This array is sorted before
				 *        the function returns.
				 * @param attrs The shared attrs object which is used to populate the normalized attributes.
				 * @param {number=} maxPriority Max directive priority.
				 */
				function collectDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
					var nodeType = node.nodeType,
						attrsMap = attrs.$attr,
						match,
						nodeName,
						className;

					switch (nodeType) {
						case NODE_TYPE_ELEMENT: /* Element */

							nodeName = nodeName_(node);

							// use the node name: <directive>
							addDirective(directives,
								directiveNormalize(nodeName), 'E', maxPriority, ignoreDirective);

							// iterate over the attributes
							for (var attr, name, nName, value, ngPrefixMatch, nAttrs = node.attributes,
								j = 0, jj = nAttrs && nAttrs.length; j < jj; j++) {
								var attrStartName = false;
								var attrEndName = false;

								var isNgAttr = false, isNgProp = false, isNgEvent = false;
								var multiElementMatch;

								attr = nAttrs[j];
								name = attr.name;
								value = attr.value;

								nName = directiveNormalize(name.toLowerCase());

								// Support ng-attr-*, ng-prop-* and ng-on-*
								if ((ngPrefixMatch = nName.match(NG_PREFIX_BINDING))) {
									isNgAttr = ngPrefixMatch[1] === 'Attr';
									isNgProp = ngPrefixMatch[1] === 'Prop';
									isNgEvent = ngPrefixMatch[1] === 'On';

									// Normalize the non-prefixed name
									name = name.replace(PREFIX_REGEXP, '')
										.toLowerCase()
										.substr(4 + ngPrefixMatch[1].length).replace(/_(.)/g, function (match, letter) {
											return letter.toUpperCase();
										});

									// Support *-start / *-end multi element directives
								} else if ((multiElementMatch = nName.match(MULTI_ELEMENT_DIR_RE)) && directiveIsMultiElement(multiElementMatch[1])) {
									attrStartName = name;
									attrEndName = name.substr(0, name.length - 5) + 'end';
									name = name.substr(0, name.length - 6);
								}

								if (isNgProp || isNgEvent) {
									attrs[nName] = value;
									attrsMap[nName] = attr.name;

									if (isNgProp) {
										addPropertyDirective(node, directives, nName, name);
									} else {
										addEventDirective(directives, nName, name);
									}
								} else {
									// Update nName for cases where a prefix was removed
									// NOTE: the .toLowerCase() is unnecessary and causes https://github.com/angular/angular.js/issues/16624 for ng-attr-*
									nName = directiveNormalize(name.toLowerCase());
									attrsMap[nName] = name;

									if (isNgAttr || !attrs.hasOwnProperty(nName)) {
										attrs[nName] = value;
										if (getBooleanAttrName(node, nName)) {
											attrs[nName] = true; // presence means true
										}
									}

									addAttrInterpolateDirective(node, directives, value, nName, isNgAttr);
									addDirective(directives, nName, 'A', maxPriority, ignoreDirective, attrStartName,
										attrEndName);
								}
							}

							if (nodeName === 'input' && node.getAttribute('type') === 'hidden') {
								// Hidden input elements can have strange behaviour when navigating back to the page
								// This tells the browser not to try to cache and reinstate previous values
								node.setAttribute('autocomplete', 'off');
							}

							// use class as directive
							if (!cssClassDirectivesEnabled) break;
							className = node.className;
							if (isObject(className)) {
								// Maybe SVGAnimatedString
								className = className.animVal;
							}
							if (isString(className) && className !== '') {
								while ((match = CLASS_DIRECTIVE_REGEXP.exec(className))) {
									nName = directiveNormalize(match[2]);
									if (addDirective(directives, nName, 'C', maxPriority, ignoreDirective)) {
										attrs[nName] = trim(match[3]);
									}
									className = className.substr(match.index + match[0].length);
								}
							}
							break;
						case NODE_TYPE_TEXT: /* Text Node */
							addTextInterpolateDirective(directives, node.nodeValue);
							break;
						case NODE_TYPE_COMMENT: /* Comment */
							if (!commentDirectivesEnabled) break;
							collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective);
							break;
					}

					directives.sort(byPriority);
					return directives;
				}

				function collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
					// function created because of performance, try/catch disables
					// the optimization of the whole function #14848
					try {
						var match = COMMENT_DIRECTIVE_REGEXP.exec(node.nodeValue);
						if (match) {
							var nName = directiveNormalize(match[1]);
							if (addDirective(directives, nName, 'M', maxPriority, ignoreDirective)) {
								attrs[nName] = trim(match[2]);
							}
						}
					} catch (e) {
						// turns out that under some circumstances IE9 throws errors when one attempts to read
						// comment's node value.
						// Just ignore it and continue. (Can't seem to reproduce in test case.)
					}
				}

				/**
				 * Given a node with a directive-start it collects all of the siblings until it finds
				 * directive-end.
				 * @param node
				 * @param attrStart
				 * @param attrEnd
				 * @returns {*}
				 */
				function groupScan(node, attrStart, attrEnd) {
					var nodes = [];
					var depth = 0;
					if (attrStart && node.hasAttribute && node.hasAttribute(attrStart)) {
						do {
							if (!node) {
								throw $compileMinErr('uterdir',
									'Unterminated attribute, found \'{0}\' but no matching \'{1}\' found.',
									attrStart, attrEnd);
							}
							if (node.nodeType === NODE_TYPE_ELEMENT) {
								if (node.hasAttribute(attrStart)) depth++;
								if (node.hasAttribute(attrEnd)) depth--;
							}
							nodes.push(node);
							node = node.nextSibling;
						} while (depth > 0);
					} else {
						nodes.push(node);
					}

					return jqLite(nodes);
				}

				/**
				 * Wrapper for linking function which converts normal linking function into a grouped
				 * linking function.
				 * @param linkFn
				 * @param attrStart
				 * @param attrEnd
				 * @returns {Function}
				 */
				function groupElementsLinkFnWrapper(linkFn, attrStart, attrEnd) {
					return function groupedElementsLink(scope, element, attrs, controllers, transcludeFn) {
						element = groupScan(element[0], attrStart, attrEnd);
						return linkFn(scope, element, attrs, controllers, transcludeFn);
					};
				}

				/**
				 * A function generator that is used to support both eager and lazy compilation
				 * linking function.
				 * @param eager
				 * @param $compileNodes
				 * @param transcludeFn
				 * @param maxPriority
				 * @param ignoreDirective
				 * @param previousCompileContext
				 * @returns {Function}
				 */
				function compilationGenerator(eager, $compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext) {
					var compiled;

					if (eager) {
						return compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);
					}
					return /** @this */ function lazyCompilation() {
						if (!compiled) {
							compiled = compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);

							// Null out all of these references in order to make them eligible for garbage collection
							// since this is a potentially long lived closure
							$compileNodes = transcludeFn = previousCompileContext = null;
						}
						return compiled.apply(this, arguments);
					};
				}

				/**
				 * Once the directives have been collected, their compile functions are executed. This method
				 * is responsible for inlining directive templates as well as terminating the application
				 * of the directives if the terminal directive has been reached.
				 *
				 * @param {Array} directives Array of collected directives to execute their compile function.
				 *        this needs to be pre-sorted by priority order.
				 * @param {Node} compileNode The raw DOM node to apply the compile functions to
				 * @param {Object} templateAttrs The shared attribute function
				 * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
				 *                                                  scope argument is auto-generated to the new
				 *                                                  child of the transcluded parent scope.
				 * @param {JQLite} jqCollection If we are working on the root of the compile tree then this
				 *                              argument has the root jqLite array so that we can replace nodes
				 *                              on it.
				 * @param {Object=} originalReplaceDirective An optional directive that will be ignored when
				 *                                           compiling the transclusion.
				 * @param {Array.<Function>} preLinkFns
				 * @param {Array.<Function>} postLinkFns
				 * @param {Object} previousCompileContext Context used for previous compilation of the current
				 *                                        node
				 * @returns {Function} linkFn
				 */
				function applyDirectivesToNode(directives, compileNode, templateAttrs, transcludeFn,
					jqCollection, originalReplaceDirective, preLinkFns, postLinkFns,
					previousCompileContext) {
					previousCompileContext = previousCompileContext || {};

					var terminalPriority = -Number.MAX_VALUE,
						newScopeDirective = previousCompileContext.newScopeDirective,
						controllerDirectives = previousCompileContext.controllerDirectives,
						newIsolateScopeDirective = previousCompileContext.newIsolateScopeDirective,
						templateDirective = previousCompileContext.templateDirective,
						nonTlbTranscludeDirective = previousCompileContext.nonTlbTranscludeDirective,
						hasTranscludeDirective = false,
						hasTemplate = false,
						hasElementTranscludeDirective = previousCompileContext.hasElementTranscludeDirective,
						$compileNode = templateAttrs.$$element = jqLite(compileNode),
						directive,
						directiveName,
						$template,
						replaceDirective = originalReplaceDirective,
						childTranscludeFn = transcludeFn,
						linkFn,
						didScanForMultipleTransclusion = false,
						mightHaveMultipleTransclusionError = false,
						directiveValue;

					// executes all directives on the current element
					for (var i = 0, ii = directives.length; i < ii; i++) {
						directive = directives[i];
						var attrStart = directive.$$start;
						var attrEnd = directive.$$end;

						// collect multiblock sections
						if (attrStart) {
							$compileNode = groupScan(compileNode, attrStart, attrEnd);
						}
						$template = undefined;

						if (terminalPriority > directive.priority) {
							break; // prevent further processing of directives
						}

						directiveValue = directive.scope;

						if (directiveValue) {

							// skip the check for directives with async templates, we'll check the derived sync
							// directive when the template arrives
							if (!directive.templateUrl) {
								if (isObject(directiveValue)) {
									// This directive is trying to add an isolated scope.
									// Check that there is no scope of any kind already
									assertNoDuplicate('new/isolated scope', newIsolateScopeDirective || newScopeDirective,
										directive, $compileNode);
									newIsolateScopeDirective = directive;
								} else {
									// This directive is trying to add a child scope.
									// Check that there is no isolated scope already
									assertNoDuplicate('new/isolated scope', newIsolateScopeDirective, directive,
										$compileNode);
								}
							}

							newScopeDirective = newScopeDirective || directive;
						}

						directiveName = directive.name;

						// If we encounter a condition that can result in transclusion on the directive,
						// then scan ahead in the remaining directives for others that may cause a multiple
						// transclusion error to be thrown during the compilation process.  If a matching directive
						// is found, then we know that when we encounter a transcluded directive, we need to eagerly
						// compile the `transclude` function rather than doing it lazily in order to throw
						// exceptions at the correct time
						if (!didScanForMultipleTransclusion && ((directive.replace && (directive.templateUrl || directive.template))
							|| (directive.transclude && !directive.$$tlb))) {
							var candidateDirective;

							for (var scanningIndex = i + 1; (candidateDirective = directives[scanningIndex++]);) {
								if ((candidateDirective.transclude && !candidateDirective.$$tlb)
									|| (candidateDirective.replace && (candidateDirective.templateUrl || candidateDirective.template))) {
									mightHaveMultipleTransclusionError = true;
									break;
								}
							}

							didScanForMultipleTransclusion = true;
						}

						if (!directive.templateUrl && directive.controller) {
							controllerDirectives = controllerDirectives || createMap();
							assertNoDuplicate('\'' + directiveName + '\' controller',
								controllerDirectives[directiveName], directive, $compileNode);
							controllerDirectives[directiveName] = directive;
						}

						directiveValue = directive.transclude;

						if (directiveValue) {
							hasTranscludeDirective = true;

							// Special case ngIf and ngRepeat so that we don't complain about duplicate transclusion.
							// This option should only be used by directives that know how to safely handle element transclusion,
							// where the transcluded nodes are added or replaced after linking.
							if (!directive.$$tlb) {
								assertNoDuplicate('transclusion', nonTlbTranscludeDirective, directive, $compileNode);
								nonTlbTranscludeDirective = directive;
							}

							if (directiveValue === 'element') {
								hasElementTranscludeDirective = true;
								terminalPriority = directive.priority;
								$template = $compileNode;
								$compileNode = templateAttrs.$$element =
									jqLite(compile.$$createComment(directiveName, templateAttrs[directiveName]));
								compileNode = $compileNode[0];
								replaceWith(jqCollection, sliceArgs($template), compileNode);

								childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, terminalPriority,
									replaceDirective && replaceDirective.name, {
									// Don't pass in:
									// - controllerDirectives - otherwise we'll create duplicates controllers
									// - newIsolateScopeDirective or templateDirective - combining templates with
									//   element transclusion doesn't make sense.
									//
									// We need only nonTlbTranscludeDirective so that we prevent putting transclusion
									// on the same element more than once.
									nonTlbTranscludeDirective: nonTlbTranscludeDirective
								});
							} else {

								var slots = createMap();

								if (!isObject(directiveValue)) {
									$template = jqLite(jqLiteClone(compileNode)).contents();
								} else {

									// We have transclusion slots,
									// collect them up, compile them and store their transclusion functions
									$template = window.document.createDocumentFragment();

									var slotMap = createMap();
									var filledSlots = createMap();

									// Parse the element selectors
									forEach(directiveValue, function (elementSelector, slotName) {
										// If an element selector starts with a ? then it is optional
										var optional = (elementSelector.charAt(0) === '?');
										elementSelector = optional ? elementSelector.substring(1) : elementSelector;

										slotMap[elementSelector] = slotName;

										// We explicitly assign `null` since this implies that a slot was defined but not filled.
										// Later when calling boundTransclusion functions with a slot name we only error if the
										// slot is `undefined`
										slots[slotName] = null;

										// filledSlots contains `true` for all slots that are either optional or have been
										// filled. This is used to check that we have not missed any required slots
										filledSlots[slotName] = optional;
									});

									// Add the matching elements into their slot
									forEach($compileNode.contents(), function (node) {
										var slotName = slotMap[directiveNormalize(nodeName_(node))];
										if (slotName) {
											filledSlots[slotName] = true;
											slots[slotName] = slots[slotName] || window.document.createDocumentFragment();
											slots[slotName].appendChild(node);
										} else {
											$template.appendChild(node);
										}
									});

									// Check for required slots that were not filled
									forEach(filledSlots, function (filled, slotName) {
										if (!filled) {
											throw $compileMinErr('reqslot', 'Required transclusion slot `{0}` was not filled.', slotName);
										}
									});

									for (var slotName in slots) {
										if (slots[slotName]) {
											// Only define a transclusion function if the slot was filled
											var slotCompileNodes = jqLite(slots[slotName].childNodes);
											slots[slotName] = compilationGenerator(mightHaveMultipleTransclusionError, slotCompileNodes, transcludeFn);
										}
									}

									$template = jqLite($template.childNodes);
								}

								$compileNode.empty(); // clear contents
								childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, undefined,
									undefined, { needsNewScope: directive.$$isolateScope || directive.$$newScope });
								childTranscludeFn.$$slots = slots;
							}
						}

						if (directive.template) {
							hasTemplate = true;
							assertNoDuplicate('template', templateDirective, directive, $compileNode);
							templateDirective = directive;

							directiveValue = (isFunction(directive.template))
								? directive.template($compileNode, templateAttrs)
								: directive.template;

							directiveValue = denormalizeTemplate(directiveValue);

							if (directive.replace) {
								replaceDirective = directive;
								if (jqLiteIsTextNode(directiveValue)) {
									$template = [];
								} else {
									$template = removeComments(wrapTemplate(directive.templateNamespace, trim(directiveValue)));
								}
								compileNode = $template[0];

								if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
									throw $compileMinErr('tplrt',
										'Template for directive \'{0}\' must have exactly one root element. {1}',
										directiveName, '');
								}

								replaceWith(jqCollection, $compileNode, compileNode);

								var newTemplateAttrs = { $attr: {} };

								// combine directives from the original node and from the template:
								// - take the array of directives for this element
								// - split it into two parts, those that already applied (processed) and those that weren't (unprocessed)
								// - collect directives from the template and sort them by priority
								// - combine directives as: processed + template + unprocessed
								var templateDirectives = collectDirectives(compileNode, [], newTemplateAttrs);
								var unprocessedDirectives = directives.splice(i + 1, directives.length - (i + 1));

								if (newIsolateScopeDirective || newScopeDirective) {
									// The original directive caused the current element to be replaced but this element
									// also needs to have a new scope, so we need to tell the template directives
									// that they would need to get their scope from further up, if they require transclusion
									markDirectiveScope(templateDirectives, newIsolateScopeDirective, newScopeDirective);
								}
								directives = directives.concat(templateDirectives).concat(unprocessedDirectives);
								mergeTemplateAttributes(templateAttrs, newTemplateAttrs);

								ii = directives.length;
							} else {
								$compileNode.html(directiveValue);
							}
						}

						if (directive.templateUrl) {
							hasTemplate = true;
							assertNoDuplicate('template', templateDirective, directive, $compileNode);
							templateDirective = directive;

							if (directive.replace) {
								replaceDirective = directive;
							}

							// eslint-disable-next-line no-func-assign
							nodeLinkFn = compileTemplateUrl(directives.splice(i, directives.length - i), $compileNode,
								templateAttrs, jqCollection, hasTranscludeDirective && childTranscludeFn, preLinkFns, postLinkFns, {
								controllerDirectives: controllerDirectives,
								newScopeDirective: (newScopeDirective !== directive) && newScopeDirective,
								newIsolateScopeDirective: newIsolateScopeDirective,
								templateDirective: templateDirective,
								nonTlbTranscludeDirective: nonTlbTranscludeDirective
							});
							ii = directives.length;
						} else if (directive.compile) {
							try {
								linkFn = directive.compile($compileNode, templateAttrs, childTranscludeFn);
								var context = directive.$$originalDirective || directive;
								if (isFunction(linkFn)) {
									addLinkFns(null, bind(context, linkFn), attrStart, attrEnd);
								} else if (linkFn) {
									addLinkFns(bind(context, linkFn.pre), bind(context, linkFn.post), attrStart, attrEnd);
								}
							} catch (e) {
								$exceptionHandler(e, startingTag($compileNode));
							}
						}

						if (directive.terminal) {
							nodeLinkFn.terminal = true;
							terminalPriority = Math.max(terminalPriority, directive.priority);
						}

					}

					nodeLinkFn.scope = newScopeDirective && newScopeDirective.scope === true;
					nodeLinkFn.transcludeOnThisElement = hasTranscludeDirective;
					nodeLinkFn.templateOnThisElement = hasTemplate;
					nodeLinkFn.transclude = childTranscludeFn;

					previousCompileContext.hasElementTranscludeDirective = hasElementTranscludeDirective;

					// might be normal or delayed nodeLinkFn depending on if templateUrl is present
					return nodeLinkFn;

					////////////////////

					function addLinkFns(pre, post, attrStart, attrEnd) {
						if (pre) {
							if (attrStart) pre = groupElementsLinkFnWrapper(pre, attrStart, attrEnd);
							pre.require = directive.require;
							pre.directiveName = directiveName;
							if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
								pre = cloneAndAnnotateFn(pre, { isolateScope: true });
							}
							preLinkFns.push(pre);
						}
						if (post) {
							if (attrStart) post = groupElementsLinkFnWrapper(post, attrStart, attrEnd);
							post.require = directive.require;
							post.directiveName = directiveName;
							if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
								post = cloneAndAnnotateFn(post, { isolateScope: true });
							}
							postLinkFns.push(post);
						}
					}

					function nodeLinkFn(childLinkFn, scope, linkNode, $rootElement, boundTranscludeFn) {
						var i, ii, linkFn, isolateScope, controllerScope, elementControllers, transcludeFn, $element,
							attrs, scopeBindingInfo;

						if (compileNode === linkNode) {
							attrs = templateAttrs;
							$element = templateAttrs.$$element;
						} else {
							$element = jqLite(linkNode);
							attrs = new Attributes($element, templateAttrs);
						}

						controllerScope = scope;
						if (newIsolateScopeDirective) {
							isolateScope = scope.$new(true);
						} else if (newScopeDirective) {
							controllerScope = scope.$parent;
						}

						if (boundTranscludeFn) {
							// track `boundTranscludeFn` so it can be unwrapped if `transcludeFn`
							// is later passed as `parentBoundTranscludeFn` to `publicLinkFn`
							transcludeFn = controllersBoundTransclude;
							transcludeFn.$$boundTransclude = boundTranscludeFn;
							// expose the slots on the `$transclude` function
							transcludeFn.isSlotFilled = function (slotName) {
								return !!boundTranscludeFn.$$slots[slotName];
							};
						}

						if (controllerDirectives) {
							elementControllers = setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective);
						}

						if (newIsolateScopeDirective) {
							// Initialize isolate scope bindings for new isolate scope directive.
							compile.$$addScopeInfo($element, isolateScope, true, !(templateDirective && (templateDirective === newIsolateScopeDirective ||
								templateDirective === newIsolateScopeDirective.$$originalDirective)));
							compile.$$addScopeClass($element, true);
							isolateScope.$$isolateBindings =
								newIsolateScopeDirective.$$isolateBindings;
							scopeBindingInfo = initializeDirectiveBindings(scope, attrs, isolateScope,
								isolateScope.$$isolateBindings,
								newIsolateScopeDirective);
							if (scopeBindingInfo.removeWatches) {
								isolateScope.$on('$destroy', scopeBindingInfo.removeWatches);
							}
						}

						// Initialize bindToController bindings
						for (var name in elementControllers) {
							var controllerDirective = controllerDirectives[name];
							var controller = elementControllers[name];
							var bindings = controllerDirective.$$bindings.bindToController;

							controller.instance = controller();
							$element.data('$' + controllerDirective.name + 'Controller', controller.instance);
							controller.bindingInfo =
								initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
						}

						// Bind the required controllers to the controller, if `require` is an object and `bindToController` is truthy
						forEach(controllerDirectives, function (controllerDirective, name) {
							var require = controllerDirective.require;
							if (controllerDirective.bindToController && !isArray(require) && isObject(require)) {
								extend(elementControllers[name].instance, getControllers(name, require, $element, elementControllers));
							}
						});

						// Handle the init and destroy lifecycle hooks on all controllers that have them
						forEach(elementControllers, function (controller) {
							var controllerInstance = controller.instance;
							if (isFunction(controllerInstance.$onChanges)) {
								try {
									controllerInstance.$onChanges(controller.bindingInfo.initialChanges);
								} catch (e) {
									$exceptionHandler(e);
								}
							}
							if (isFunction(controllerInstance.$onInit)) {
								try {
									controllerInstance.$onInit();
								} catch (e) {
									$exceptionHandler(e);
								}
							}
							if (isFunction(controllerInstance.$doCheck)) {
								controllerScope.$watch(function () { controllerInstance.$doCheck(); });
								controllerInstance.$doCheck();
							}
							if (isFunction(controllerInstance.$onDestroy)) {
								controllerScope.$on('$destroy', function callOnDestroyHook() {
									controllerInstance.$onDestroy();
								});
							}
						});

						// PRELINKING
						for (i = 0, ii = preLinkFns.length; i < ii; i++) {
							linkFn = preLinkFns[i];
							invokeLinkFn(linkFn,
								linkFn.isolateScope ? isolateScope : scope,
								$element,
								attrs,
								linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
								transcludeFn
							);
						}

						// RECURSION
						// We only pass the isolate scope, if the isolate directive has a template,
						// otherwise the child elements do not belong to the isolate directive.
						var scopeToChild = scope;
						if (newIsolateScopeDirective && (newIsolateScopeDirective.template || newIsolateScopeDirective.templateUrl === null)) {
							scopeToChild = isolateScope;
						}
						if (childLinkFn) {
							childLinkFn(scopeToChild, linkNode.childNodes, undefined, boundTranscludeFn);
						}

						// POSTLINKING
						for (i = postLinkFns.length - 1; i >= 0; i--) {
							linkFn = postLinkFns[i];
							invokeLinkFn(linkFn,
								linkFn.isolateScope ? isolateScope : scope,
								$element,
								attrs,
								linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
								transcludeFn
							);
						}

						// Trigger $postLink lifecycle hooks
						forEach(elementControllers, function (controller) {
							var controllerInstance = controller.instance;
							if (isFunction(controllerInstance.$postLink)) {
								controllerInstance.$postLink();
							}
						});

						// This is the function that is injected as `$transclude`.
						// Note: all arguments are optional!
						function controllersBoundTransclude(scope, cloneAttachFn, futureParentElement, slotName) {
							var transcludeControllers;
							// No scope passed in:
							if (!isScope(scope)) {
								slotName = futureParentElement;
								futureParentElement = cloneAttachFn;
								cloneAttachFn = scope;
								scope = undefined;
							}

							if (hasElementTranscludeDirective) {
								transcludeControllers = elementControllers;
							}
							if (!futureParentElement) {
								futureParentElement = hasElementTranscludeDirective ? $element.parent() : $element;
							}
							if (slotName) {
								// slotTranscludeFn can be one of three things:
								//  * a transclude function - a filled slot
								//  * `null` - an optional slot that was not filled
								//  * `undefined` - a slot that was not declared (i.e. invalid)
								var slotTranscludeFn = boundTranscludeFn.$$slots[slotName];
								if (slotTranscludeFn) {
									return slotTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
								} else if (isUndefined(slotTranscludeFn)) {
									throw $compileMinErr('noslot',
										'No parent directive that requires a transclusion with slot name "{0}". ' +
										'Element: {1}',
										slotName, startingTag($element));
								}
							} else {
								return boundTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
							}
						}
					}
				}

				function getControllers(directiveName, require, $element, elementControllers) {
					var value;

					if (isString(require)) {
						var match = require.match(REQUIRE_PREFIX_REGEXP);
						var name = require.substring(match[0].length);
						var inheritType = match[1] || match[3];
						var optional = match[2] === '?';

						//If only parents then start at the parent element
						if (inheritType === '^^') {
							$element = $element.parent();
							//Otherwise attempt getting the controller from elementControllers in case
							//the element is transcluded (and has no data) and to avoid .data if possible
						} else {
							value = elementControllers && elementControllers[name];
							value = value && value.instance;
						}

						if (!value) {
							var dataName = '$' + name + 'Controller';

							if (inheritType === '^^' && $element[0] && $element[0].nodeType === NODE_TYPE_DOCUMENT) {
								// inheritedData() uses the documentElement when it finds the document, so we would
								// require from the element itself.
								value = null;
							} else {
								value = inheritType ? $element.inheritedData(dataName) : $element.data(dataName);
							}
						}

						if (!value && !optional) {
							throw $compileMinErr('ctreq',
								'Controller \'{0}\', required by directive \'{1}\', can\'t be found!',
								name, directiveName);
						}
					} else if (isArray(require)) {
						value = [];
						for (var i = 0, ii = require.length; i < ii; i++) {
							value[i] = getControllers(directiveName, require[i], $element, elementControllers);
						}
					} else if (isObject(require)) {
						value = {};
						forEach(require, function (controller, property) {
							value[property] = getControllers(directiveName, controller, $element, elementControllers);
						});
					}

					return value || null;
				}

				function setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective) {
					var elementControllers = createMap();
					for (var controllerKey in controllerDirectives) {
						var directive = controllerDirectives[controllerKey];
						var locals = {
							$scope: directive === newIsolateScopeDirective || directive.$$isolateScope ? isolateScope : scope,
							$element: $element,
							$attrs: attrs,
							$transclude: transcludeFn
						};

						var controller = directive.controller;
						if (controller === '@') {
							controller = attrs[directive.name];
						}

						var controllerInstance = $controller(controller, locals, true, directive.controllerAs);

						// For directives with element transclusion the element is a comment.
						// In this case .data will not attach any data.
						// Instead, we save the controllers for the element in a local hash and attach to .data
						// later, once we have the actual element.
						elementControllers[directive.name] = controllerInstance;
						$element.data('$' + directive.name + 'Controller', controllerInstance.instance);
					}
					return elementControllers;
				}

				// Depending upon the context in which a directive finds itself it might need to have a new isolated
				// or child scope created. For instance:
				// * if the directive has been pulled into a template because another directive with a higher priority
				// asked for element transclusion
				// * if the directive itself asks for transclusion but it is at the root of a template and the original
				// element was replaced. See https://github.com/angular/angular.js/issues/12936
				function markDirectiveScope(directives, isolateScope, newScope) {
					for (var j = 0, jj = directives.length; j < jj; j++) {
						directives[j] = inherit(directives[j], { $$isolateScope: isolateScope, $$newScope: newScope });
					}
				}

				/**
				 * looks up the directive and decorates it with exception handling and proper parameters. We
				 * call this the boundDirective.
				 *
				 * @param {string} name name of the directive to look up.
				 * @param {string} location The directive must be found in specific format.
				 *   String containing any of theses characters:
				 *
				 *   * `E`: element name
				 *   * `A': attribute
				 *   * `C`: class
				 *   * `M`: comment
				 * @returns {boolean} true if directive was added.
				 */
				function addDirective(tDirectives, name, location, maxPriority, ignoreDirective, startAttrName,
					endAttrName) {
					if (name === ignoreDirective) return null;
					var match = null;
					if (hasDirectives.hasOwnProperty(name)) {
						for (var directive, directives = $injector.get(name + Suffix),
							i = 0, ii = directives.length; i < ii; i++) {
							directive = directives[i];
							if ((isUndefined(maxPriority) || maxPriority > directive.priority) &&
								directive.restrict.indexOf(location) !== -1) {
								if (startAttrName) {
									directive = inherit(directive, { $$start: startAttrName, $$end: endAttrName });
								}
								if (!directive.$$bindings) {
									var bindings = directive.$$bindings =
										parseDirectiveBindings(directive, directive.name);
									if (isObject(bindings.isolateScope)) {
										directive.$$isolateBindings = bindings.isolateScope;
									}
								}
								tDirectives.push(directive);
								match = directive;
							}
						}
					}
					return match;
				}


				/**
				 * looks up the directive and returns true if it is a multi-element directive,
				 * and therefore requires DOM nodes between -start and -end markers to be grouped
				 * together.
				 *
				 * @param {string} name name of the directive to look up.
				 * @returns true if directive was registered as multi-element.
				 */
				function directiveIsMultiElement(name) {
					if (hasDirectives.hasOwnProperty(name)) {
						for (var directive, directives = $injector.get(name + Suffix),
							i = 0, ii = directives.length; i < ii; i++) {
							directive = directives[i];
							if (directive.multiElement) {
								return true;
							}
						}
					}
					return false;
				}

				/**
				 * When the element is replaced with HTML template then the new attributes
				 * on the template need to be merged with the existing attributes in the DOM.
				 * The desired effect is to have both of the attributes present.
				 *
				 * @param {object} dst destination attributes (original DOM)
				 * @param {object} src source attributes (from the directive template)
				 */
				function mergeTemplateAttributes(dst, src) {
					var srcAttr = src.$attr,
						dstAttr = dst.$attr;

					// reapply the old attributes to the new element
					forEach(dst, function (value, key) {
						if (key.charAt(0) !== '$') {
							if (src[key] && src[key] !== value) {
								if (value.length) {
									value += (key === 'style' ? ';' : ' ') + src[key];
								} else {
									value = src[key];
								}
							}
							dst.$set(key, value, true, srcAttr[key]);
						}
					});

					// copy the new attributes on the old attrs object
					forEach(src, function (value, key) {
						// Check if we already set this attribute in the loop above.
						// `dst` will never contain hasOwnProperty as DOM parser won't let it.
						// You will get an "InvalidCharacterError: DOM Exception 5" error if you
						// have an attribute like "has-own-property" or "data-has-own-property", etc.
						if (!dst.hasOwnProperty(key) && key.charAt(0) !== '$') {
							dst[key] = value;

							if (key !== 'class' && key !== 'style') {
								dstAttr[key] = srcAttr[key];
							}
						}
					});
				}


				function compileTemplateUrl(directives, $compileNode, tAttrs,
					$rootElement, childTranscludeFn, preLinkFns, postLinkFns, previousCompileContext) {
					var linkQueue = [],
						afterTemplateNodeLinkFn,
						afterTemplateChildLinkFn,
						beforeTemplateCompileNode = $compileNode[0],
						origAsyncDirective = directives.shift(),
						derivedSyncDirective = inherit(origAsyncDirective, {
							templateUrl: null, transclude: null, replace: null, $$originalDirective: origAsyncDirective
						}),
						templateUrl = (isFunction(origAsyncDirective.templateUrl))
							? origAsyncDirective.templateUrl($compileNode, tAttrs)
							: origAsyncDirective.templateUrl,
						templateNamespace = origAsyncDirective.templateNamespace;

					$compileNode.empty();

					$templateRequest(templateUrl)
						.then(function (content) {
							var compileNode, tempTemplateAttrs, $template, childBoundTranscludeFn;

							content = denormalizeTemplate(content);

							if (origAsyncDirective.replace) {
								if (jqLiteIsTextNode(content)) {
									$template = [];
								} else {
									$template = removeComments(wrapTemplate(templateNamespace, trim(content)));
								}
								compileNode = $template[0];

								if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
									throw $compileMinErr('tplrt',
										'Template for directive \'{0}\' must have exactly one root element. {1}',
										origAsyncDirective.name, templateUrl);
								}

								tempTemplateAttrs = { $attr: {} };
								replaceWith($rootElement, $compileNode, compileNode);
								var templateDirectives = collectDirectives(compileNode, [], tempTemplateAttrs);

								if (isObject(origAsyncDirective.scope)) {
									// the original directive that caused the template to be loaded async required
									// an isolate scope
									markDirectiveScope(templateDirectives, true);
								}
								directives = templateDirectives.concat(directives);
								mergeTemplateAttributes(tAttrs, tempTemplateAttrs);
							} else {
								compileNode = beforeTemplateCompileNode;
								$compileNode.html(content);
							}

							directives.unshift(derivedSyncDirective);

							afterTemplateNodeLinkFn = applyDirectivesToNode(directives, compileNode, tAttrs,
								childTranscludeFn, $compileNode, origAsyncDirective, preLinkFns, postLinkFns,
								previousCompileContext);
							forEach($rootElement, function (node, i) {
								if (node === compileNode) {
									$rootElement[i] = $compileNode[0];
								}
							});
							afterTemplateChildLinkFn = compileNodes($compileNode[0].childNodes, childTranscludeFn);

							while (linkQueue.length) {
								var scope = linkQueue.shift(),
									beforeTemplateLinkNode = linkQueue.shift(),
									linkRootElement = linkQueue.shift(),
									boundTranscludeFn = linkQueue.shift(),
									linkNode = $compileNode[0];

								if (scope.$$destroyed) continue;

								if (beforeTemplateLinkNode !== beforeTemplateCompileNode) {
									var oldClasses = beforeTemplateLinkNode.className;

									if (!(previousCompileContext.hasElementTranscludeDirective &&
										origAsyncDirective.replace)) {
										// it was cloned therefore we have to clone as well.
										linkNode = jqLiteClone(compileNode);
									}
									replaceWith(linkRootElement, jqLite(beforeTemplateLinkNode), linkNode);

									// Copy in CSS classes from original node
									safeAddClass(jqLite(linkNode), oldClasses);
								}
								if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
									childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
								} else {
									childBoundTranscludeFn = boundTranscludeFn;
								}
								afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, linkNode, $rootElement,
									childBoundTranscludeFn);
							}
							linkQueue = null;
						}).catch(function (error) {
							if (isError(error)) {
								$exceptionHandler(error);
							}
						});

					return function delayedNodeLinkFn(ignoreChildLinkFn, scope, node, rootElement, boundTranscludeFn) {
						var childBoundTranscludeFn = boundTranscludeFn;
						if (scope.$$destroyed) return;
						if (linkQueue) {
							linkQueue.push(scope,
								node,
								rootElement,
								childBoundTranscludeFn);
						} else {
							if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
								childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
							}
							afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, node, rootElement, childBoundTranscludeFn);
						}
					};
				}


				/**
				 * Sorting function for bound directives.
				 */
				function byPriority(a, b) {
					var diff = b.priority - a.priority;
					if (diff !== 0) return diff;
					if (a.name !== b.name) return (a.name < b.name) ? -1 : 1;
					return a.index - b.index;
				}

				function assertNoDuplicate(what, previousDirective, directive, element) {

					function wrapModuleNameIfDefined(moduleName) {
						return moduleName ?
							(' (module: ' + moduleName + ')') :
							'';
					}

					if (previousDirective) {
						throw $compileMinErr('multidir', 'Multiple directives [{0}{1}, {2}{3}] asking for {4} on: {5}',
							previousDirective.name, wrapModuleNameIfDefined(previousDirective.$$moduleName),
							directive.name, wrapModuleNameIfDefined(directive.$$moduleName), what, startingTag(element));
					}
				}


				function addTextInterpolateDirective(directives, text) {
					var interpolateFn = $interpolate(text, true);
					if (interpolateFn) {
						directives.push({
							priority: 0,
							compile: function textInterpolateCompileFn(templateNode) {
								var templateNodeParent = templateNode.parent(),
									hasCompileParent = !!templateNodeParent.length;

								// When transcluding a template that has bindings in the root
								// we don't have a parent and thus need to add the class during linking fn.
								if (hasCompileParent) compile.$$addBindingClass(templateNodeParent);

								return function textInterpolateLinkFn(scope, node) {
									var parent = node.parent();
									if (!hasCompileParent) compile.$$addBindingClass(parent);
									compile.$$addBindingInfo(parent, interpolateFn.expressions);
									scope.$watch(interpolateFn, function interpolateFnWatchAction(value) {
										node[0].nodeValue = value;
									});
								};
							}
						});
					}
				}


				function wrapTemplate(type, template) {
					type = lowercase(type || 'html');
					switch (type) {
						case 'svg':
						case 'math':
							var wrapper = window.document.createElement('div');
							wrapper.innerHTML = '<' + type + '>' + template + '</' + type + '>';
							return wrapper.childNodes[0].childNodes;
						default:
							return template;
					}
				}


				function getTrustedAttrContext(nodeName, attrNormalizedName) {
					if (attrNormalizedName === 'srcdoc') {
						return $sce.HTML;
					}
					// All nodes with src attributes require a RESOURCE_URL value, except for
					// img and various html5 media nodes, which require the MEDIA_URL context.
					if (attrNormalizedName === 'src' || attrNormalizedName === 'ngSrc') {
						if (['img', 'video', 'audio', 'source', 'track'].indexOf(nodeName) === -1) {
							return $sce.RESOURCE_URL;
						}
						return $sce.MEDIA_URL;
					} else if (attrNormalizedName === 'xlinkHref') {
						// Some xlink:href are okay, most aren't
						if (nodeName === 'image') return $sce.MEDIA_URL;
						if (nodeName === 'a') return $sce.URL;
						return $sce.RESOURCE_URL;
					} else if (
						// Formaction
						(nodeName === 'form' && attrNormalizedName === 'action') ||
						// If relative URLs can go where they are not expected to, then
						// all sorts of trust issues can arise.
						(nodeName === 'base' && attrNormalizedName === 'href') ||
						// links can be stylesheets or imports, which can run script in the current origin
						(nodeName === 'link' && attrNormalizedName === 'href')
					) {
						return $sce.RESOURCE_URL;
					} else if (nodeName === 'a' && (attrNormalizedName === 'href' ||
						attrNormalizedName === 'ngHref')) {
						return $sce.URL;
					}
				}

				function getTrustedPropContext(nodeName, propNormalizedName) {
					var prop = propNormalizedName.toLowerCase();
					return PROP_CONTEXTS[nodeName + '|' + prop] || PROP_CONTEXTS['*|' + prop];
				}

				function sanitizeSrcsetPropertyValue(value) {
					return sanitizeSrcset($sce.valueOf(value), 'ng-prop-srcset');
				}
				function addPropertyDirective(node, directives, attrName, propName) {
					if (EVENT_HANDLER_ATTR_REGEXP.test(propName)) {
						throw $compileMinErr('nodomevents', 'Property bindings for HTML DOM event properties are disallowed');
					}

					var nodeName = nodeName_(node);
					var trustedContext = getTrustedPropContext(nodeName, propName);

					var sanitizer = identity;
					// Sanitize img[srcset] + source[srcset] values.
					if (propName === 'srcset' && (nodeName === 'img' || nodeName === 'source')) {
						sanitizer = sanitizeSrcsetPropertyValue;
					} else if (trustedContext) {
						sanitizer = $sce.getTrusted.bind($sce, trustedContext);
					}

					directives.push({
						priority: 100,
						compile: function ngPropCompileFn(_, attr) {
							var ngPropGetter = $parse(attr[attrName]);
							var ngPropWatch = $parse(attr[attrName], function sceValueOf(val) {
								// Unwrap the value to compare the actual inner safe value, not the wrapper object.
								return $sce.valueOf(val);
							});

							return {
								pre: function ngPropPreLinkFn(scope, $element) {
									function applyPropValue() {
										var propValue = ngPropGetter(scope);
										$element[0][propName] = sanitizer(propValue);
									}

									applyPropValue();
									scope.$watch(ngPropWatch, applyPropValue);
								}
							};
						}
					});
				}

				function addEventDirective(directives, attrName, eventName) {
					directives.push(
						createEventDirective($parse, $rootScope, $exceptionHandler, attrName, eventName, /*forceAsync=*/false)
					);
				}

				function addAttrInterpolateDirective(node, directives, value, name, isNgAttr) {
					var nodeName = nodeName_(node);
					var trustedContext = getTrustedAttrContext(nodeName, name);
					var mustHaveExpression = !isNgAttr;
					var allOrNothing = ALL_OR_NOTHING_ATTRS[name] || isNgAttr;

					var interpolateFn = $interpolate(value, mustHaveExpression, trustedContext, allOrNothing);

					// no interpolation found -> ignore
					if (!interpolateFn) return;

					if (name === 'multiple' && nodeName === 'select') {
						throw $compileMinErr('selmulti',
							'Binding to the \'multiple\' attribute is not supported. Element: {0}',
							startingTag(node));
					}

					if (EVENT_HANDLER_ATTR_REGEXP.test(name)) {
						throw $compileMinErr('nodomevents', 'Interpolations for HTML DOM event attributes are disallowed');
					}

					directives.push({
						priority: 100,
						compile: function () {
							return {
								pre: function attrInterpolatePreLinkFn(scope, element, attr) {
									var $$observers = (attr.$$observers || (attr.$$observers = createMap()));

									// If the attribute has changed since last $interpolate()ed
									var newValue = attr[name];
									if (newValue !== value) {
										// we need to interpolate again since the attribute value has been updated
										// (e.g. by another directive's compile function)
										// ensure unset/empty values make interpolateFn falsy
										interpolateFn = newValue && $interpolate(newValue, true, trustedContext, allOrNothing);
										value = newValue;
									}

									// if attribute was updated so that there is no interpolation going on we don't want to
									// register any observers
									if (!interpolateFn) return;

									// initialize attr object so that it's ready in case we need the value for isolate
									// scope initialization, otherwise the value would not be available from isolate
									// directive's linking fn during linking phase
									attr[name] = interpolateFn(scope);

									($$observers[name] || ($$observers[name] = [])).$$inter = true;
									(attr.$$observers && attr.$$observers[name].$$scope || scope).
										$watch(interpolateFn, function interpolateFnWatchAction(newValue, oldValue) {
											//special case for class attribute addition + removal
											//so that class changes can tap into the animation
											//hooks provided by the $animate service. Be sure to
											//skip animations when the first digest occurs (when
											//both the new and the old values are the same) since
											//the CSS classes are the non-interpolated values
											if (name === 'class' && newValue !== oldValue) {
												attr.$updateClass(newValue, oldValue);
											} else {
												attr.$set(name, newValue);
											}
										});
								}
							};
						}
					});
				}


				/**
				 * This is a special jqLite.replaceWith, which can replace items which
				 * have no parents, provided that the containing jqLite collection is provided.
				 *
				 * @param {JqLite=} $rootElement The root of the compile tree. Used so that we can replace nodes
				 *                               in the root of the tree.
				 * @param {JqLite} elementsToRemove The jqLite element which we are going to replace. We keep
				 *                                  the shell, but replace its DOM node reference.
				 * @param {Node} newNode The new DOM node.
				 */
				function replaceWith($rootElement, elementsToRemove, newNode) {
					var firstElementToRemove = elementsToRemove[0],
						removeCount = elementsToRemove.length,
						parent = firstElementToRemove.parentNode,
						i, ii;

					if ($rootElement) {
						for (i = 0, ii = $rootElement.length; i < ii; i++) {
							if ($rootElement[i] === firstElementToRemove) {
								$rootElement[i++] = newNode;
								for (var j = i, j2 = j + removeCount - 1,
									jj = $rootElement.length;
									j < jj; j++, j2++) {
									if (j2 < jj) {
										$rootElement[j] = $rootElement[j2];
									} else {
										delete $rootElement[j];
									}
								}
								$rootElement.length -= removeCount - 1;

								// If the replaced element is also the jQuery .context then replace it
								// .context is a deprecated jQuery api, so we should set it only when jQuery set it
								// http://api.jquery.com/context/
								if ($rootElement.context === firstElementToRemove) {
									$rootElement.context = newNode;
								}
								break;
							}
						}
					}

					if (parent) {
						parent.replaceChild(newNode, firstElementToRemove);
					}

					// Append all the `elementsToRemove` to a fragment. This will...
					// - remove them from the DOM
					// - allow them to still be traversed with .nextSibling
					// - allow a single fragment.qSA to fetch all elements being removed
					var fragment = window.document.createDocumentFragment();
					for (i = 0; i < removeCount; i++) {
						fragment.appendChild(elementsToRemove[i]);
					}

					if (jqLite.hasData(firstElementToRemove)) {
						// Copy over user data (that includes AngularJS's $scope etc.). Don't copy private
						// data here because there's no public interface in jQuery to do that and copying over
						// event listeners (which is the main use of private data) wouldn't work anyway.
						jqLite.data(newNode, jqLite.data(firstElementToRemove));

						// Remove $destroy event listeners from `firstElementToRemove`
						jqLite(firstElementToRemove).off('$destroy');
					}

					// Cleanup any data/listeners on the elements and children.
					// This includes invoking the $destroy event on any elements with listeners.
					jqLite.cleanData(fragment.querySelectorAll('*'));

					// Update the jqLite collection to only contain the `newNode`
					for (i = 1; i < removeCount; i++) {
						delete elementsToRemove[i];
					}
					elementsToRemove[0] = newNode;
					elementsToRemove.length = 1;
				}


				function cloneAndAnnotateFn(fn, annotation) {
					return extend(function () { return fn.apply(null, arguments); }, fn, annotation);
				}


				function invokeLinkFn(linkFn, scope, $element, attrs, controllers, transcludeFn) {
					try {
						linkFn(scope, $element, attrs, controllers, transcludeFn);
					} catch (e) {
						$exceptionHandler(e, startingTag($element));
					}
				}

				function strictBindingsCheck(attrName, directiveName) {
					if (strictComponentBindingsEnabled) {
						throw $compileMinErr('missingattr',
							'Attribute \'{0}\' of \'{1}\' is non-optional and must be set!',
							attrName, directiveName);
					}
				}

				// Set up $watches for isolate scope and controller bindings.
				function initializeDirectiveBindings(scope, attrs, destination, bindings, directive) {
					var removeWatchCollection = [];
					var initialChanges = {};
					var changes;

					forEach(bindings, function initializeBinding(definition, scopeName) {
						var attrName = definition.attrName,
							optional = definition.optional,
							mode = definition.mode, // @, =, <, or &
							lastValue,
							parentGet, parentSet, compare, removeWatch;

						switch (mode) {

							case '@':
								if (!optional && !hasOwnProperty.call(attrs, attrName)) {
									strictBindingsCheck(attrName, directive.name);
									destination[scopeName] = attrs[attrName] = undefined;

								}
								removeWatch = attrs.$observe(attrName, function (value) {
									if (isString(value) || isBoolean(value)) {
										var oldValue = destination[scopeName];
										recordChanges(scopeName, value, oldValue);
										destination[scopeName] = value;
									}
								});
								attrs.$$observers[attrName].$$scope = scope;
								lastValue = attrs[attrName];
								if (isString(lastValue)) {
									// If the attribute has been provided then we trigger an interpolation to ensure
									// the value is there for use in the link fn
									destination[scopeName] = $interpolate(lastValue)(scope);
								} else if (isBoolean(lastValue)) {
									// If the attributes is one of the BOOLEAN_ATTR then AngularJS will have converted
									// the value to boolean rather than a string, so we special case this situation
									destination[scopeName] = lastValue;
								}
								initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);
								removeWatchCollection.push(removeWatch);
								break;

							case '=':
								if (!hasOwnProperty.call(attrs, attrName)) {
									if (optional) break;
									strictBindingsCheck(attrName, directive.name);
									attrs[attrName] = undefined;
								}
								if (optional && !attrs[attrName]) break;

								parentGet = $parse(attrs[attrName]);
								if (parentGet.literal) {
									compare = equals;
								} else {
									compare = simpleCompare;
								}
								parentSet = parentGet.assign || function () {
									// reset the change, or we will throw this exception on every $digest
									lastValue = destination[scopeName] = parentGet(scope);
									throw $compileMinErr('nonassign',
										'Expression \'{0}\' in attribute \'{1}\' used with directive \'{2}\' is non-assignable!',
										attrs[attrName], attrName, directive.name);
								};
								lastValue = destination[scopeName] = parentGet(scope);
								var parentValueWatch = function parentValueWatch(parentValue) {
									if (!compare(parentValue, destination[scopeName])) {
										// we are out of sync and need to copy
										if (!compare(parentValue, lastValue)) {
											// parent changed and it has precedence
											destination[scopeName] = parentValue;
										} else {
											// if the parent can be assigned then do so
											parentSet(scope, parentValue = destination[scopeName]);
										}
									}
									lastValue = parentValue;
									return lastValue;
								};
								parentValueWatch.$stateful = true;
								if (definition.collection) {
									removeWatch = scope.$watchCollection(attrs[attrName], parentValueWatch);
								} else {
									removeWatch = scope.$watch($parse(attrs[attrName], parentValueWatch), null, parentGet.literal);
								}
								removeWatchCollection.push(removeWatch);
								break;

							case '<':
								if (!hasOwnProperty.call(attrs, attrName)) {
									if (optional) break;
									strictBindingsCheck(attrName, directive.name);
									attrs[attrName] = undefined;
								}
								if (optional && !attrs[attrName]) break;

								parentGet = $parse(attrs[attrName]);
								var isLiteral = parentGet.literal;

								var initialValue = destination[scopeName] = parentGet(scope);
								initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);

								removeWatch = scope[definition.collection ? '$watchCollection' : '$watch'](parentGet, function parentValueWatchAction(newValue, oldValue) {
									if (oldValue === newValue) {
										if (oldValue === initialValue || (isLiteral && equals(oldValue, initialValue))) {
											return;
										}
										oldValue = initialValue;
									}
									recordChanges(scopeName, newValue, oldValue);
									destination[scopeName] = newValue;
								});

								removeWatchCollection.push(removeWatch);
								break;

							case '&':
								if (!optional && !hasOwnProperty.call(attrs, attrName)) {
									strictBindingsCheck(attrName, directive.name);
								}
								// Don't assign Object.prototype method to scope
								parentGet = attrs.hasOwnProperty(attrName) ? $parse(attrs[attrName]) : noop;

								// Don't assign noop to destination if expression is not valid
								if (parentGet === noop && optional) break;

								destination[scopeName] = function (locals) {
									return parentGet(scope, locals);
								};
								break;
						}
					});

					function recordChanges(key, currentValue, previousValue) {
						if (isFunction(destination.$onChanges) && !simpleCompare(currentValue, previousValue)) {
							// If we have not already scheduled the top level onChangesQueue handler then do so now
							if (!onChangesQueue) {
								scope.$$postDigest(flushOnChangesQueue);
								onChangesQueue = [];
							}
							// If we have not already queued a trigger of onChanges for this controller then do so now
							if (!changes) {
								changes = {};
								onChangesQueue.push(triggerOnChangesHook);
							}
							// If the has been a change on this property already then we need to reuse the previous value
							if (changes[key]) {
								previousValue = changes[key].previousValue;
							}
							// Store this change
							changes[key] = new SimpleChange(previousValue, currentValue);
						}
					}

					function triggerOnChangesHook() {
						destination.$onChanges(changes);
						// Now clear the changes so that we schedule onChanges when more changes arrive
						changes = undefined;
					}

					return {
						initialChanges: initialChanges,
						removeWatches: removeWatchCollection.length && function removeWatches() {
							for (var i = 0, ii = removeWatchCollection.length; i < ii; ++i) {
								removeWatchCollection[i]();
							}
						}
					};
				}
			}];
	}

	function SimpleChange(previous, current) {
		this.previousValue = previous;
		this.currentValue = current;
	}
	SimpleChange.prototype.isFirstChange = function () { return this.previousValue === _UNINITIALIZED_VALUE; };


	var PREFIX_REGEXP = /^((?:x|data)[:\-_])/i;
	var SPECIAL_CHARS_REGEXP = /[:\-_]+(.)/g;

	/**
	 * Converts all accepted directives format into proper directive name.
	 * @param name Name to normalize
	 */
	function directiveNormalize(name) {
		return name
			.replace(PREFIX_REGEXP, '')
			.replace(SPECIAL_CHARS_REGEXP, function (_, letter, offset) {
				return offset ? letter.toUpperCase() : letter;
			});
	}

	/**
	 * @ngdoc type
	 * @name $compile.directive.Attributes
	 *
	 * @description
	 * A shared object between directive compile / linking functions which contains normalized DOM
	 * element attributes. The values reflect current binding state `{{ }}`. The normalization is
	 * needed since all of these are treated as equivalent in AngularJS:
	 *
	 * ```
	 *    <span ng:bind="a" ng-bind="a" data-ng-bind="a" x-ng-bind="a">
	 * ```
	 */

	/**
	 * @ngdoc property
	 * @name $compile.directive.Attributes#$attr
	 *
	 * @description
	 * A map of DOM element attribute names to the normalized name. This is
	 * needed to do reverse lookup from normalized name back to actual name.
	 */


	/**
	 * @ngdoc method
	 * @name $compile.directive.Attributes#$set
	 * @kind function
	 *
	 * @description
	 * Set DOM element attribute value.
	 *
	 *
	 * @param {string} name Normalized element attribute name of the property to modify. The name is
	 *          reverse-translated using the {@link ng.$compile.directive.Attributes#$attr $attr}
	 *          property to the original name.
	 * @param {string} value Value to set the attribute to. The value can be an interpolated string.
	 */



	/**
	 * Closure compiler type information
	 */

	function nodesetLinkingFn(
  /* angular.Scope */ scope,
  /* NodeList */ nodeList,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
	) { }

	function directiveLinkingFn(
  /* nodesetLinkingFn */ nodesetLinkingFn,
  /* angular.Scope */ scope,
  /* Node */ node,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
	) { }

	function tokenDifference(str1, str2) {
		var values = '',
			tokens1 = str1.split(/\s+/),
			tokens2 = str2.split(/\s+/);

		outer:
		for (var i = 0; i < tokens1.length; i++) {
			var token = tokens1[i];
			for (var j = 0; j < tokens2.length; j++) {
				if (token === tokens2[j]) continue outer;
			}
			values += (values.length > 0 ? ' ' : '') + token;
		}
		return values;
	}

	function removeComments(jqNodes) {
		jqNodes = jqLite(jqNodes);
		var i = jqNodes.length;

		if (i <= 1) {
			return jqNodes;
		}

		while (i--) {
			var node = jqNodes[i];
			if (node.nodeType === NODE_TYPE_COMMENT ||
				(node.nodeType === NODE_TYPE_TEXT && node.nodeValue.trim() === '')) {
				splice.call(jqNodes, i, 1);
			}
		}
		return jqNodes;
	}

	var $controllerMinErr = minErr('$controller');


	var CNTRL_REG = /^(\S+)(\s+as\s+([\w$]+))?$/;
	function identifierForController(controller, ident) {
		if (ident && isString(ident)) return ident;
		if (isString(controller)) {
			var match = CNTRL_REG.exec(controller);
			if (match) return match[3];
		}
	}


	/**
	 * @ngdoc provider
	 * @name $controllerProvider
	 * @this
	 *
	 * @description
	 * The {@link ng.$controller $controller service} is used by AngularJS to create new
	 * controllers.
	 *
	 * This provider allows controller registration via the
	 * {@link ng.$controllerProvider#register register} method.
	 */
	function $ControllerProvider() {
		var controllers = {};

		/**
		 * @ngdoc method
		 * @name $controllerProvider#has
		 * @param {string} name Controller name to check.
		 */
		this.has = function (name) {
			return controllers.hasOwnProperty(name);
		};

		/**
		 * @ngdoc method
		 * @name $controllerProvider#register
		 * @param {string|Object} name Controller name, or an object map of controllers where the keys are
		 *    the names and the values are the constructors.
		 * @param {Function|Array} constructor Controller constructor fn (optionally decorated with DI
		 *    annotations in the array notation).
		 */
		this.register = function (name, constructor) {
			assertNotHasOwnProperty(name, 'controller');
			if (isObject(name)) {
				extend(controllers, name);
			} else {
				controllers[name] = constructor;
			}
		};

		this.$get = ['$injector', function ($injector) {

			/**
			 * @ngdoc service
			 * @name $controller
			 * @requires $injector
			 *
			 * @param {Function|string} constructor If called with a function then it's considered to be the
			 *    controller constructor function. Otherwise it's considered to be a string which is used
			 *    to retrieve the controller constructor using the following steps:
			 *
			 *    * check if a controller with given name is registered via `$controllerProvider`
			 *    * check if evaluating the string on the current scope returns a constructor
			 *
			 *    The string can use the `controller as property` syntax, where the controller instance is published
			 *    as the specified property on the `scope`; the `scope` must be injected into `locals` param for this
			 *    to work correctly.
			 *
			 * @param {Object} locals Injection locals for Controller.
			 * @return {Object} Instance of given controller.
			 *
			 * @description
			 * `$controller` service is responsible for instantiating controllers.
			 *
			 * It's just a simple call to {@link auto.$injector $injector}, but extracted into
			 * a service, so that one can override this service with [BC version](https://gist.github.com/1649788).
			 */
			return function $controller(expression, locals, later, ident) {
				// PRIVATE API:
				//   param `later` --- indicates that the controller's constructor is invoked at a later time.
				//                     If true, $controller will allocate the object with the correct
				//                     prototype chain, but will not invoke the controller until a returned
				//                     callback is invoked.
				//   param `ident` --- An optional label which overrides the label parsed from the controller
				//                     expression, if any.
				var instance, match, constructor, identifier;
				later = later === true;
				if (ident && isString(ident)) {
					identifier = ident;
				}

				if (isString(expression)) {
					match = expression.match(CNTRL_REG);
					if (!match) {
						throw $controllerMinErr('ctrlfmt',
							'Badly formed controller string \'{0}\'. ' +
							'Must match `__name__ as __id__` or `__name__`.', expression);
					}
					constructor = match[1];
					identifier = identifier || match[3];
					expression = controllers.hasOwnProperty(constructor)
						? controllers[constructor]
						: getter(locals.$scope, constructor, true);

					if (!expression) {
						throw $controllerMinErr('ctrlreg',
							'The controller with the name \'{0}\' is not registered.', constructor);
					}

					assertArgFn(expression, constructor, true);
				}

				if (later) {
					// Instantiate controller later:
					// This machinery is used to create an instance of the object before calling the
					// controller's constructor itself.
					//
					// This allows properties to be added to the controller before the constructor is
					// invoked. Primarily, this is used for isolate scope bindings in $compile.
					//
					// This feature is not intended for use by applications, and is thus not documented
					// publicly.
					// Object creation: http://jsperf.com/create-constructor/2
					var controllerPrototype = (isArray(expression) ?
						expression[expression.length - 1] : expression).prototype;
					instance = Object.create(controllerPrototype || null);

					if (identifier) {
						addIdentifier(locals, identifier, instance, constructor || expression.name);
					}

					return extend(function $controllerInit() {
						var result = $injector.invoke(expression, instance, locals, constructor);
						if (result !== instance && (isObject(result) || isFunction(result))) {
							instance = result;
							if (identifier) {
								// If result changed, re-assign controllerAs value to scope.
								addIdentifier(locals, identifier, instance, constructor || expression.name);
							}
						}
						return instance;
					}, {
						instance: instance,
						identifier: identifier
					});
				}

				instance = $injector.instantiate(expression, locals, constructor);

				if (identifier) {
					addIdentifier(locals, identifier, instance, constructor || expression.name);
				}

				return instance;
			};

			function addIdentifier(locals, identifier, instance, name) {
				if (!(locals && isObject(locals.$scope))) {
					throw minErr('$controller')('noscp',
						'Cannot export controller \'{0}\' as \'{1}\'! No $scope object provided via `locals`.',
						name, identifier);
				}

				locals.$scope[identifier] = instance;
			}
		}];
	}

	/**
	 * @ngdoc service
	 * @name $document
	 * @requires $window
	 * @this
	 *
	 * @description
	 * A {@link angular.element jQuery or jqLite} wrapper for the browser's `window.document` object.
	 *
	 * @example
	   <example module="documentExample" name="document">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <p>$document title: <b ng-bind="title"></b></p>
			 <p>window.document title: <b ng-bind="windowTitle"></b></p>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('documentExample', [])
			 .controller('ExampleController', ['$scope', '$document', function($scope, $document) {
			   $scope.title = $document[0].title;
			   $scope.windowTitle = angular.element(window.document)[0].title;
			 }]);
		 </file>
	   </example>
	 */
	function $DocumentProvider() {
		this.$get = ['$window', function (window) {
			return jqLite(window.document);
		}];
	}


	/**
	 * @private
	 * @this
	 * Listens for document visibility change and makes the current status accessible.
	 */
	function $$IsDocumentHiddenProvider() {
		this.$get = ['$document', '$rootScope', function ($document, $rootScope) {
			var doc = $document[0];
			var hidden = doc && doc.hidden;

			$document.on('visibilitychange', changeListener);

			$rootScope.$on('$destroy', function () {
				$document.off('visibilitychange', changeListener);
			});

			function changeListener() {
				hidden = doc.hidden;
			}

			return function () {
				return hidden;
			};
		}];
	}

	/**
	 * @ngdoc service
	 * @name $exceptionHandler
	 * @requires ng.$log
	 * @this
	 *
	 * @description
	 * Any uncaught exception in AngularJS expressions is delegated to this service.
	 * The default implementation simply delegates to `$log.error` which logs it into
	 * the browser console.
	 *
	 * In unit tests, if `angular-mocks.js` is loaded, this service is overridden by
	 * {@link ngMock.$exceptionHandler mock $exceptionHandler} which aids in testing.
	 *
	 * ## Example:
	 *
	 * The example below will overwrite the default `$exceptionHandler` in order to (a) log uncaught
	 * errors to the backend for later inspection by the developers and (b) to use `$log.warn()` instead
	 * of `$log.error()`.
	 *
	 * ```js
	 *   angular.
	 *     module('exceptionOverwrite', []).
	 *     factory('$exceptionHandler', ['$log', 'logErrorsToBackend', function($log, logErrorsToBackend) {
	 *       return function myExceptionHandler(exception, cause) {
	 *         logErrorsToBackend(exception, cause);
	 *         $log.warn(exception, cause);
	 *       };
	 *     }]);
	 * ```
	 *
	 * <hr />
	 * Note, that code executed in event-listeners (even those registered using jqLite's `on`/`bind`
	 * methods) does not delegate exceptions to the {@link ng.$exceptionHandler $exceptionHandler}
	 * (unless executed during a digest).
	 *
	 * If you wish, you can manually delegate exceptions, e.g.
	 * `try { ... } catch(e) { $exceptionHandler(e); }`
	 *
	 * @param {Error} exception Exception associated with the error.
	 * @param {string=} cause Optional information about the context in which
	 *       the error was thrown.
	 *
	 */
	function $ExceptionHandlerProvider() {
		this.$get = ['$log', function ($log) {
			return function (exception, cause) {
				$log.error.apply($log, arguments);
			};
		}];
	}

	var $$ForceReflowProvider = /** @this */ function () {
		this.$get = ['$document', function ($document) {
			return function (domNode) {
				//the line below will force the browser to perform a repaint so
				//that all the animated elements within the animation frame will
				//be properly updated and drawn on screen. This is required to
				//ensure that the preparation animation is properly flushed so that
				//the active state picks up from there. DO NOT REMOVE THIS LINE.
				//DO NOT OPTIMIZE THIS LINE. THE MINIFIER WILL REMOVE IT OTHERWISE WHICH
				//WILL RESULT IN AN UNPREDICTABLE BUG THAT IS VERY HARD TO TRACK DOWN AND
				//WILL TAKE YEARS AWAY FROM YOUR LIFE.
				if (domNode) {
					if (!domNode.nodeType && domNode instanceof jqLite) {
						domNode = domNode[0];
					}
				} else {
					domNode = $document[0].body;
				}
				return domNode.offsetWidth + 1;
			};
		}];
	};

	var APPLICATION_JSON = 'application/json';
	var CONTENT_TYPE_APPLICATION_JSON = { 'Content-Type': APPLICATION_JSON + ';charset=utf-8' };
	var JSON_START = /^\[|^\{(?!\{)/;
	var JSON_ENDS = {
		'[': /]$/,
		'{': /}$/
	};
	var JSON_PROTECTION_PREFIX = /^\)]\}',?\n/;
	var $httpMinErr = minErr('$http');

	function serializeValue(v) {
		if (isObject(v)) {
			return isDate(v) ? v.toISOString() : toJson(v);
		}
		return v;
	}


	/** @this */
	function $HttpParamSerializerProvider() {
		/**
		 * @ngdoc service
		 * @name $httpParamSerializer
		 * @description
		 *
		 * Default {@link $http `$http`} params serializer that converts objects to strings
		 * according to the following rules:
		 *
		 * * `{'foo': 'bar'}` results in `foo=bar`
		 * * `{'foo': Date.now()}` results in `foo=2015-04-01T09%3A50%3A49.262Z` (`toISOString()` and encoded representation of a Date object)
		 * * `{'foo': ['bar', 'baz']}` results in `foo=bar&foo=baz` (repeated key for each array element)
		 * * `{'foo': {'bar':'baz'}}` results in `foo=%7B%22bar%22%3A%22baz%22%7D` (stringified and encoded representation of an object)
		 *
		 * Note that serializer will sort the request parameters alphabetically.
		 */

		this.$get = function () {
			return function ngParamSerializer(params) {
				if (!params) return '';
				var parts = [];
				forEachSorted(params, function (value, key) {
					if (value === null || isUndefined(value) || isFunction(value)) return;
					if (isArray(value)) {
						forEach(value, function (v) {
							parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(serializeValue(v)));
						});
					} else {
						parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(serializeValue(value)));
					}
				});

				return parts.join('&');
			};
		};
	}

	/** @this */
	function $HttpParamSerializerJQLikeProvider() {
		/**
		 * @ngdoc service
		 * @name $httpParamSerializerJQLike
		 *
		 * @description
		 *
		 * Alternative {@link $http `$http`} params serializer that follows
		 * jQuery's [`param()`](http://api.jquery.com/jquery.param/) method logic.
		 * The serializer will also sort the params alphabetically.
		 *
		 * To use it for serializing `$http` request parameters, set it as the `paramSerializer` property:
		 *
		 * ```js
		 * $http({
		 *   url: myUrl,
		 *   method: 'GET',
		 *   params: myParams,
		 *   paramSerializer: '$httpParamSerializerJQLike'
		 * });
		 * ```
		 *
		 * It is also possible to set it as the default `paramSerializer` in the
		 * {@link $httpProvider#defaults `$httpProvider`}.
		 *
		 * Additionally, you can inject the serializer and use it explicitly, for example to serialize
		 * form data for submission:
		 *
		 * ```js
		 * .controller(function($http, $httpParamSerializerJQLike) {
		 *   //...
		 *
		 *   $http({
		 *     url: myUrl,
		 *     method: 'POST',
		 *     data: $httpParamSerializerJQLike(myData),
		 *     headers: {
		 *       'Content-Type': 'application/x-www-form-urlencoded'
		 *     }
		 *   });
		 *
		 * });
		 * ```
		 *
		 */
		this.$get = function () {
			return function jQueryLikeParamSerializer(params) {
				if (!params) return '';
				var parts = [];
				serialize(params, '', true);
				return parts.join('&');

				function serialize(toSerialize, prefix, topLevel) {
					if (isArray(toSerialize)) {
						forEach(toSerialize, function (value, index) {
							serialize(value, prefix + '[' + (isObject(value) ? index : '') + ']');
						});
					} else if (isObject(toSerialize) && !isDate(toSerialize)) {
						forEachSorted(toSerialize, function (value, key) {
							serialize(value, prefix +
								(topLevel ? '' : '[') +
								key +
								(topLevel ? '' : ']'));
						});
					} else {
						if (isFunction(toSerialize)) {
							toSerialize = toSerialize();
						}
						parts.push(encodeUriQuery(prefix) + '=' +
							(toSerialize == null ? '' : encodeUriQuery(serializeValue(toSerialize))));
					}
				}
			};
		};
	}

	function defaultHttpResponseTransform(data, headers) {
		if (isString(data)) {
			// Strip json vulnerability protection prefix and trim whitespace
			var tempData = data.replace(JSON_PROTECTION_PREFIX, '').trim();

			if (tempData) {
				var contentType = headers('Content-Type');
				var hasJsonContentType = contentType && (contentType.indexOf(APPLICATION_JSON) === 0);

				if (hasJsonContentType || isJsonLike(tempData)) {
					try {
						data = fromJson(tempData);
					} catch (e) {
						if (!hasJsonContentType) {
							return data;
						}
						throw $httpMinErr('baddata', 'Data must be a valid JSON object. Received: "{0}". ' +
							'Parse error: "{1}"', data, e);
					}
				}
			}
		}

		return data;
	}

	function isJsonLike(str) {
		var jsonStart = str.match(JSON_START);
		return jsonStart && JSON_ENDS[jsonStart[0]].test(str);
	}

	/**
	 * Parse headers into key value object
	 *
	 * @param {string} headers Raw headers as a string
	 * @returns {Object} Parsed headers as key value object
	 */
	function parseHeaders(headers) {
		var parsed = createMap(), i;

		function fillInParsed(key, val) {
			if (key) {
				parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
			}
		}

		if (isString(headers)) {
			forEach(headers.split('\n'), function (line) {
				i = line.indexOf(':');
				fillInParsed(lowercase(trim(line.substr(0, i))), trim(line.substr(i + 1)));
			});
		} else if (isObject(headers)) {
			forEach(headers, function (headerVal, headerKey) {
				fillInParsed(lowercase(headerKey), trim(headerVal));
			});
		}

		return parsed;
	}


	/**
	 * Returns a function that provides access to parsed headers.
	 *
	 * Headers are lazy parsed when first requested.
	 * @see parseHeaders
	 *
	 * @param {(string|Object)} headers Headers to provide access to.
	 * @returns {function(string=)} Returns a getter function which if called with:
	 *
	 *   - if called with an argument returns a single header value or null
	 *   - if called with no arguments returns an object containing all headers.
	 */
	function headersGetter(headers) {
		var headersObj;

		return function (name) {
			if (!headersObj) headersObj = parseHeaders(headers);

			if (name) {
				var value = headersObj[lowercase(name)];
				if (value === undefined) {
					value = null;
				}
				return value;
			}

			return headersObj;
		};
	}


	/**
	 * Chain all given functions
	 *
	 * This function is used for both request and response transforming
	 *
	 * @param {*} data Data to transform.
	 * @param {function(string=)} headers HTTP headers getter fn.
	 * @param {number} status HTTP status code of the response.
	 * @param {(Function|Array.<Function>)} fns Function or an array of functions.
	 * @returns {*} Transformed data.
	 */
	function transformData(data, headers, status, fns) {
		if (isFunction(fns)) {
			return fns(data, headers, status);
		}

		forEach(fns, function (fn) {
			data = fn(data, headers, status);
		});

		return data;
	}


	function isSuccess(status) {
		return 200 <= status && status < 300;
	}


	/**
	 * @ngdoc provider
	 * @name $httpProvider
	 * @this
	 *
	 * @description
	 * Use `$httpProvider` to change the default behavior of the {@link ng.$http $http} service.
	 */
	function $HttpProvider() {
		/**
		 * @ngdoc property
		 * @name $httpProvider#defaults
		 * @description
		 *
		 * Object containing default values for all {@link ng.$http $http} requests.
		 *
		 * - **`defaults.cache`** - {boolean|Object} - A boolean value or object created with
		 * {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of HTTP responses
		 * by default. See {@link $http#caching $http Caching} for more information.
		 *
		 * - **`defaults.headers`** - {Object} - Default headers for all $http requests.
		 * Refer to {@link ng.$http#setting-http-headers $http} for documentation on
		 * setting default headers.
		 *     - **`defaults.headers.common`**
		 *     - **`defaults.headers.post`**
		 *     - **`defaults.headers.put`**
		 *     - **`defaults.headers.patch`**
		 *
		 * - **`defaults.jsonpCallbackParam`** - `{string}` - the name of the query parameter that passes the name of the
		 * callback in a JSONP request. The value of this parameter will be replaced with the expression generated by the
		 * {@link $jsonpCallbacks} service. Defaults to `'callback'`.
		 *
		 * - **`defaults.paramSerializer`** - `{string|function(Object<string,string>):string}` - A function
		 *  used to the prepare string representation of request parameters (specified as an object).
		 *  If specified as string, it is interpreted as a function registered with the {@link auto.$injector $injector}.
		 *  Defaults to {@link ng.$httpParamSerializer $httpParamSerializer}.
		 *
		 * - **`defaults.transformRequest`** -
		 * `{Array<function(data, headersGetter)>|function(data, headersGetter)}` -
		 * An array of functions (or a single function) which are applied to the request data.
		 * By default, this is an array with one request transformation function:
		 *
		 *   - If the `data` property of the request configuration object contains an object, serialize it
		 *     into JSON format.
		 *
		 * - **`defaults.transformResponse`** -
		 * `{Array<function(data, headersGetter, status)>|function(data, headersGetter, status)}` -
		 * An array of functions (or a single function) which are applied to the response data. By default,
		 * this is an array which applies one response transformation function that does two things:
		 *
		 *  - If XSRF prefix is detected, strip it
		 *    (see {@link ng.$http#security-considerations Security Considerations in the $http docs}).
		 *  - If the `Content-Type` is `application/json` or the response looks like JSON,
		 *    deserialize it using a JSON parser.
		 *
		 * - **`defaults.xsrfCookieName`** - {string} - Name of cookie containing the XSRF token.
		 * Defaults value is `'XSRF-TOKEN'`.
		 *
		 * - **`defaults.xsrfHeaderName`** - {string} - Name of HTTP header to populate with the
		 * XSRF token. Defaults value is `'X-XSRF-TOKEN'`.
		 *
		 */
		var defaults = this.defaults = {
			// transform incoming response data
			transformResponse: [defaultHttpResponseTransform],

			// transform outgoing request data
			transformRequest: [function (d) {
				return isObject(d) && !isFile(d) && !isBlob(d) && !isFormData(d) ? toJson(d) : d;
			}],

			// default headers
			headers: {
				common: {
					'Accept': 'application/json, text/plain, */*'
				},
				post: shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
				put: shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
				patch: shallowCopy(CONTENT_TYPE_APPLICATION_JSON)
			},

			xsrfCookieName: 'XSRF-TOKEN',
			xsrfHeaderName: 'X-XSRF-TOKEN',

			paramSerializer: '$httpParamSerializer',

			jsonpCallbackParam: 'callback'
		};

		var useApplyAsync = false;
		/**
		 * @ngdoc method
		 * @name $httpProvider#useApplyAsync
		 * @description
		 *
		 * Configure $http service to combine processing of multiple http responses received at around
		 * the same time via {@link ng.$rootScope.Scope#$applyAsync $rootScope.$applyAsync}. This can result in
		 * significant performance improvement for bigger applications that make many HTTP requests
		 * concurrently (common during application bootstrap).
		 *
		 * Defaults to false. If no value is specified, returns the current configured value.
		 *
		 * @param {boolean=} value If true, when requests are loaded, they will schedule a deferred
		 *    "apply" on the next tick, giving time for subsequent requests in a roughly ~10ms window
		 *    to load and share the same digest cycle.
		 *
		 * @returns {boolean|Object} If a value is specified, returns the $httpProvider for chaining.
		 *    otherwise, returns the current configured value.
		 */
		this.useApplyAsync = function (value) {
			if (isDefined(value)) {
				useApplyAsync = !!value;
				return this;
			}
			return useApplyAsync;
		};

		/**
		 * @ngdoc property
		 * @name $httpProvider#interceptors
		 * @description
		 *
		 * Array containing service factories for all synchronous or asynchronous {@link ng.$http $http}
		 * pre-processing of request or postprocessing of responses.
		 *
		 * These service factories are ordered by request, i.e. they are applied in the same order as the
		 * array, on request, but reverse order, on response.
		 *
		 * {@link ng.$http#interceptors Interceptors detailed info}
		 */
		var interceptorFactories = this.interceptors = [];

		/**
		 * @ngdoc property
		 * @name $httpProvider#xsrfTrustedOrigins
		 * @description
		 *
		 * Array containing URLs whose origins are trusted to receive the XSRF token. See the
		 * {@link ng.$http#security-considerations Security Considerations} sections for more details on
		 * XSRF.
		 *
		 * **Note:** An "origin" consists of the [URI scheme](https://en.wikipedia.org/wiki/URI_scheme),
		 * the [hostname](https://en.wikipedia.org/wiki/Hostname) and the
		 * [port number](https://en.wikipedia.org/wiki/Port_(computer_networking). For `http:` and
		 * `https:`, the port number can be omitted if using th default ports (80 and 443 respectively).
		 * Examples: `http://example.com`, `https://api.example.com:9876`
		 *
		 * <div class="alert alert-warning">
		 *   It is not possible to trust specific URLs/paths. The `path`, `query` and `fragment` parts
		 *   of a URL will be ignored. For example, `https://foo.com/path/bar?query=baz#fragment` will be
		 *   treated as `https://foo.com`, meaning that **all** requests to URLs starting with
		 *   `https://foo.com/` will include the XSRF token.
		 * </div>
		 *
		 * @example
		 *
		 * ```js
		 * // App served from `https://example.com/`.
		 * angular.
		 *   module('xsrfTrustedOriginsExample', []).
		 *   config(['$httpProvider', function($httpProvider) {
		 *     $httpProvider.xsrfTrustedOrigins.push('https://api.example.com');
		 *   }]).
		 *   run(['$http', function($http) {
		 *     // The XSRF token will be sent.
		 *     $http.get('https://api.example.com/preferences').then(...);
		 *
		 *     // The XSRF token will NOT be sent.
		 *     $http.get('https://stats.example.com/activity').then(...);
		 *   }]);
		 * ```
		 */
		var xsrfTrustedOrigins = this.xsrfTrustedOrigins = [];

		/**
		 * @ngdoc property
		 * @name $httpProvider#xsrfWhitelistedOrigins
		 * @description
		 *
		 * @deprecated
		 * sinceVersion="1.8.1"
		 *
		 * This property is deprecated. Use {@link $httpProvider#xsrfTrustedOrigins xsrfTrustedOrigins}
		 * instead.
		 */
		Object.defineProperty(this, 'xsrfWhitelistedOrigins', {
			get: function () {
				return this.xsrfTrustedOrigins;
			},
			set: function (origins) {
				this.xsrfTrustedOrigins = origins;
			}
		});

		this.$get = ['$browser', '$httpBackend', '$$cookieReader', '$cacheFactory', '$rootScope', '$q', '$injector', '$sce',
			function ($browser, $httpBackend, $$cookieReader, $cacheFactory, $rootScope, $q, $injector, $sce) {

				var defaultCache = $cacheFactory('$http');

				/**
				 * Make sure that default param serializer is exposed as a function
				 */
				defaults.paramSerializer = isString(defaults.paramSerializer) ?
					$injector.get(defaults.paramSerializer) : defaults.paramSerializer;

				/**
				 * Interceptors stored in reverse order. Inner interceptors before outer interceptors.
				 * The reversal is needed so that we can build up the interception chain around the
				 * server request.
				 */
				var reversedInterceptors = [];

				forEach(interceptorFactories, function (interceptorFactory) {
					reversedInterceptors.unshift(isString(interceptorFactory)
						? $injector.get(interceptorFactory) : $injector.invoke(interceptorFactory));
				});

				/**
				 * A function to check request URLs against a list of allowed origins.
				 */
				var urlIsAllowedOrigin = urlIsAllowedOriginFactory(xsrfTrustedOrigins);

				/**
				 * @ngdoc service
				 * @kind function
				 * @name $http
				 * @requires ng.$httpBackend
				 * @requires $cacheFactory
				 * @requires $rootScope
				 * @requires $q
				 * @requires $injector
				 *
				 * @description
				 * The `$http` service is a core AngularJS service that facilitates communication with the remote
				 * HTTP servers via the browser's [XMLHttpRequest](https://developer.mozilla.org/en/xmlhttprequest)
				 * object or via [JSONP](http://en.wikipedia.org/wiki/JSONP).
				 *
				 * For unit testing applications that use `$http` service, see
				 * {@link ngMock.$httpBackend $httpBackend mock}.
				 *
				 * For a higher level of abstraction, please check out the {@link ngResource.$resource
				 * $resource} service.
				 *
				 * The $http API is based on the {@link ng.$q deferred/promise APIs} exposed by
				 * the $q service. While for simple usage patterns this doesn't matter much, for advanced usage
				 * it is important to familiarize yourself with these APIs and the guarantees they provide.
				 *
				 *
				 * ## General usage
				 * The `$http` service is a function which takes a single argument — a {@link $http#usage configuration object} —
				 * that is used to generate an HTTP request and returns  a {@link ng.$q promise} that is
				 * resolved (request success) or rejected (request failure) with a
				 * {@link ng.$http#$http-returns response} object.
				 *
				 * ```js
				 *   // Simple GET request example:
				 *   $http({
				 *     method: 'GET',
				 *     url: '/someUrl'
				 *   }).then(function successCallback(response) {
				 *       // this callback will be called asynchronously
				 *       // when the response is available
				 *     }, function errorCallback(response) {
				 *       // called asynchronously if an error occurs
				 *       // or server returns response with an error status.
				 *     });
				 * ```
				 *
				 *
				 * ## Shortcut methods
				 *
				 * Shortcut methods are also available. All shortcut methods require passing in the URL, and
				 * request data must be passed in for POST/PUT requests. An optional config can be passed as the
				 * last argument.
				 *
				 * ```js
				 *   $http.get('/someUrl', config).then(successCallback, errorCallback);
				 *   $http.post('/someUrl', data, config).then(successCallback, errorCallback);
				 * ```
				 *
				 * Complete list of shortcut methods:
				 *
				 * - {@link ng.$http#get $http.get}
				 * - {@link ng.$http#head $http.head}
				 * - {@link ng.$http#post $http.post}
				 * - {@link ng.$http#put $http.put}
				 * - {@link ng.$http#delete $http.delete}
				 * - {@link ng.$http#jsonp $http.jsonp}
				 * - {@link ng.$http#patch $http.patch}
				 *
				 *
				 * ## Writing Unit Tests that use $http
				 * When unit testing (using {@link ngMock ngMock}), it is necessary to call
				 * {@link ngMock.$httpBackend#flush $httpBackend.flush()} to flush each pending
				 * request using trained responses.
				 *
				 * ```
				 * $httpBackend.expectGET(...);
				 * $http.get(...);
				 * $httpBackend.flush();
				 * ```
				 *
				 * ## Setting HTTP Headers
				 *
				 * The $http service will automatically add certain HTTP headers to all requests. These defaults
				 * can be fully configured by accessing the `$httpProvider.defaults.headers` configuration
				 * object, which currently contains this default configuration:
				 *
				 * - `$httpProvider.defaults.headers.common` (headers that are common for all requests):
				 *   - <code>Accept: application/json, text/plain, \*&#65279;/&#65279;\*</code>
				 * - `$httpProvider.defaults.headers.post`: (header defaults for POST requests)
				 *   - `Content-Type: application/json`
				 * - `$httpProvider.defaults.headers.put` (header defaults for PUT requests)
				 *   - `Content-Type: application/json`
				 *
				 * To add or overwrite these defaults, simply add or remove a property from these configuration
				 * objects. To add headers for an HTTP method other than POST or PUT, simply add a new object
				 * with the lowercased HTTP method name as the key, e.g.
				 * `$httpProvider.defaults.headers.get = { 'My-Header' : 'value' }`.
				 *
				 * The defaults can also be set at runtime via the `$http.defaults` object in the same
				 * fashion. For example:
				 *
				 * ```
				 * module.run(function($http) {
				 *   $http.defaults.headers.common.Authorization = 'Basic YmVlcDpib29w';
				 * });
				 * ```
				 *
				 * In addition, you can supply a `headers` property in the config object passed when
				 * calling `$http(config)`, which overrides the defaults without changing them globally.
				 *
				 * To explicitly remove a header automatically added via $httpProvider.defaults.headers on a per request basis,
				 * Use the `headers` property, setting the desired header to `undefined`. For example:
				 *
				 * ```js
				 * var req = {
				 *  method: 'POST',
				 *  url: 'http://example.com',
				 *  headers: {
				 *    'Content-Type': undefined
				 *  },
				 *  data: { test: 'test' }
				 * }
				 *
				 * $http(req).then(function(){...}, function(){...});
				 * ```
				 *
				 * ## Transforming Requests and Responses
				 *
				 * Both requests and responses can be transformed using transformation functions: `transformRequest`
				 * and `transformResponse`. These properties can be a single function that returns
				 * the transformed value (`function(data, headersGetter, status)`) or an array of such transformation functions,
				 * which allows you to `push` or `unshift` a new transformation function into the transformation chain.
				 *
				 * <div class="alert alert-warning">
				 * **Note:** AngularJS does not make a copy of the `data` parameter before it is passed into the `transformRequest` pipeline.
				 * That means changes to the properties of `data` are not local to the transform function (since Javascript passes objects by reference).
				 * For example, when calling `$http.get(url, $scope.myObject)`, modifications to the object's properties in a transformRequest
				 * function will be reflected on the scope and in any templates where the object is data-bound.
				 * To prevent this, transform functions should have no side-effects.
				 * If you need to modify properties, it is recommended to make a copy of the data, or create new object to return.
				 * </div>
				 *
				 * ### Default Transformations
				 *
				 * The `$httpProvider` provider and `$http` service expose `defaults.transformRequest` and
				 * `defaults.transformResponse` properties. If a request does not provide its own transformations
				 * then these will be applied.
				 *
				 * You can augment or replace the default transformations by modifying these properties by adding to or
				 * replacing the array.
				 *
				 * AngularJS provides the following default transformations:
				 *
				 * Request transformations (`$httpProvider.defaults.transformRequest` and `$http.defaults.transformRequest`) is
				 * an array with one function that does the following:
				 *
				 * - If the `data` property of the request configuration object contains an object, serialize it
				 *   into JSON format.
				 *
				 * Response transformations (`$httpProvider.defaults.transformResponse` and `$http.defaults.transformResponse`) is
				 * an array with one function that does the following:
				 *
				 *  - If XSRF prefix is detected, strip it (see Security Considerations section below).
				 *  - If the `Content-Type` is `application/json` or the response looks like JSON,
			   *      deserialize it using a JSON parser.
				 *
				 *
				 * ### Overriding the Default Transformations Per Request
				 *
				 * If you wish to override the request/response transformations only for a single request then provide
				 * `transformRequest` and/or `transformResponse` properties on the configuration object passed
				 * into `$http`.
				 *
				 * Note that if you provide these properties on the config object the default transformations will be
				 * overwritten. If you wish to augment the default transformations then you must include them in your
				 * local transformation array.
				 *
				 * The following code demonstrates adding a new response transformation to be run after the default response
				 * transformations have been run.
				 *
				 * ```js
				 * function appendTransform(defaults, transform) {
				 *
				 *   // We can't guarantee that the default transformation is an array
				 *   defaults = angular.isArray(defaults) ? defaults : [defaults];
				 *
				 *   // Append the new transformation to the defaults
				 *   return defaults.concat(transform);
				 * }
				 *
				 * $http({
				 *   url: '...',
				 *   method: 'GET',
				 *   transformResponse: appendTransform($http.defaults.transformResponse, function(value) {
				 *     return doTransform(value);
				 *   })
				 * });
				 * ```
				 *
				 *
				 * ## Caching
				 *
				 * {@link ng.$http `$http`} responses are not cached by default. To enable caching, you must
				 * set the config.cache value or the default cache value to TRUE or to a cache object (created
				 * with {@link ng.$cacheFactory `$cacheFactory`}). If defined, the value of config.cache takes
				 * precedence over the default cache value.
				 *
				 * In order to:
				 *   * cache all responses - set the default cache value to TRUE or to a cache object
				 *   * cache a specific response - set config.cache value to TRUE or to a cache object
				 *
				 * If caching is enabled, but neither the default cache nor config.cache are set to a cache object,
				 * then the default `$cacheFactory("$http")` object is used.
				 *
				 * The default cache value can be set by updating the
				 * {@link ng.$http#defaults `$http.defaults.cache`} property or the
				 * {@link $httpProvider#defaults `$httpProvider.defaults.cache`} property.
				 *
				 * When caching is enabled, {@link ng.$http `$http`} stores the response from the server using
				 * the relevant cache object. The next time the same request is made, the response is returned
				 * from the cache without sending a request to the server.
				 *
				 * Take note that:
				 *
				 *   * Only GET and JSONP requests are cached.
				 *   * The cache key is the request URL including search parameters; headers are not considered.
				 *   * Cached responses are returned asynchronously, in the same way as responses from the server.
				 *   * If multiple identical requests are made using the same cache, which is not yet populated,
				 *     one request will be made to the server and remaining requests will return the same response.
				 *   * A cache-control header on the response does not affect if or how responses are cached.
				 *
				 *
				 * ## Interceptors
				 *
				 * Before you start creating interceptors, be sure to understand the
				 * {@link ng.$q $q and deferred/promise APIs}.
				 *
				 * For purposes of global error handling, authentication, or any kind of synchronous or
				 * asynchronous pre-processing of request or postprocessing of responses, it is desirable to be
				 * able to intercept requests before they are handed to the server and
				 * responses before they are handed over to the application code that
				 * initiated these requests. The interceptors leverage the {@link ng.$q
				 * promise APIs} to fulfill this need for both synchronous and asynchronous pre-processing.
				 *
				 * The interceptors are service factories that are registered with the `$httpProvider` by
				 * adding them to the `$httpProvider.interceptors` array. The factory is called and
				 * injected with dependencies (if specified) and returns the interceptor.
				 *
				 * There are two kinds of interceptors (and two kinds of rejection interceptors):
				 *
				 *   * `request`: interceptors get called with a http {@link $http#usage config} object. The function is free to
				 *     modify the `config` object or create a new one. The function needs to return the `config`
				 *     object directly, or a promise containing the `config` or a new `config` object.
				 *   * `requestError`: interceptor gets called when a previous interceptor threw an error or
				 *     resolved with a rejection.
				 *   * `response`: interceptors get called with http `response` object. The function is free to
				 *     modify the `response` object or create a new one. The function needs to return the `response`
				 *     object directly, or as a promise containing the `response` or a new `response` object.
				 *   * `responseError`: interceptor gets called when a previous interceptor threw an error or
				 *     resolved with a rejection.
				 *
				 *
				 * ```js
				 *   // register the interceptor as a service
				 *   $provide.factory('myHttpInterceptor', function($q, dependency1, dependency2) {
				 *     return {
				 *       // optional method
				 *       'request': function(config) {
				 *         // do something on success
				 *         return config;
				 *       },
				 *
				 *       // optional method
				 *      'requestError': function(rejection) {
				 *         // do something on error
				 *         if (canRecover(rejection)) {
				 *           return responseOrNewPromise
				 *         }
				 *         return $q.reject(rejection);
				 *       },
				 *
				 *
				 *
				 *       // optional method
				 *       'response': function(response) {
				 *         // do something on success
				 *         return response;
				 *       },
				 *
				 *       // optional method
				 *      'responseError': function(rejection) {
				 *         // do something on error
				 *         if (canRecover(rejection)) {
				 *           return responseOrNewPromise
				 *         }
				 *         return $q.reject(rejection);
				 *       }
				 *     };
				 *   });
				 *
				 *   $httpProvider.interceptors.push('myHttpInterceptor');
				 *
				 *
				 *   // alternatively, register the interceptor via an anonymous factory
				 *   $httpProvider.interceptors.push(function($q, dependency1, dependency2) {
				 *     return {
				 *      'request': function(config) {
				 *          // same as above
				 *       },
				 *
				 *       'response': function(response) {
				 *          // same as above
				 *       }
				 *     };
				 *   });
				 * ```
				 *
				 * ## Security Considerations
				 *
				 * When designing web applications, consider security threats from:
				 *
				 * - [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
				 * - [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery)
				 *
				 * Both server and the client must cooperate in order to eliminate these threats. AngularJS comes
				 * pre-configured with strategies that address these issues, but for this to work backend server
				 * cooperation is required.
				 *
				 * ### JSON Vulnerability Protection
				 *
				 * A [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
				 * allows third party website to turn your JSON resource URL into
				 * [JSONP](http://en.wikipedia.org/wiki/JSONP) request under some conditions. To
				 * counter this your server can prefix all JSON requests with following string `")]}',\n"`.
				 * AngularJS will automatically strip the prefix before processing it as JSON.
				 *
				 * For example if your server needs to return:
				 * ```js
				 * ['one','two']
				 * ```
				 *
				 * which is vulnerable to attack, your server can return:
				 * ```js
				 * )]}',
				 * ['one','two']
				 * ```
				 *
				 * AngularJS will strip the prefix, before processing the JSON.
				 *
				 *
				 * ### Cross Site Request Forgery (XSRF) Protection
				 *
				 * [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery) is an attack technique by
				 * which the attacker can trick an authenticated user into unknowingly executing actions on your
				 * website. AngularJS provides a mechanism to counter XSRF. When performing XHR requests, the
				 * $http service reads a token from a cookie (by default, `XSRF-TOKEN`) and sets it as an HTTP
				 * header (by default `X-XSRF-TOKEN`). Since only JavaScript that runs on your domain could read
				 * the cookie, your server can be assured that the XHR came from JavaScript running on your
				 * domain.
				 *
				 * To take advantage of this, your server needs to set a token in a JavaScript readable session
				 * cookie called `XSRF-TOKEN` on the first HTTP GET request. On subsequent XHR requests the
				 * server can verify that the cookie matches the `X-XSRF-TOKEN` HTTP header, and therefore be
				 * sure that only JavaScript running on your domain could have sent the request. The token must
				 * be unique for each user and must be verifiable by the server (to prevent the JavaScript from
				 * making up its own tokens). We recommend that the token is a digest of your site's
				 * authentication cookie with a [salt](https://en.wikipedia.org/wiki/Salt_(cryptography&#41;)
				 * for added security.
				 *
				 * The header will &mdash; by default &mdash; **not** be set for cross-domain requests. This
				 * prevents unauthorized servers (e.g. malicious or compromised 3rd-party APIs) from gaining
				 * access to your users' XSRF tokens and exposing them to Cross Site Request Forgery. If you
				 * want to, you can trust additional origins to also receive the XSRF token, by adding them
				 * to {@link ng.$httpProvider#xsrfTrustedOrigins xsrfTrustedOrigins}. This might be
				 * useful, for example, if your application, served from `example.com`, needs to access your API
				 * at `api.example.com`.
				 * See {@link ng.$httpProvider#xsrfTrustedOrigins $httpProvider.xsrfTrustedOrigins} for
				 * more details.
				 *
				 * <div class="alert alert-danger">
				 *   **Warning**<br />
				 *   Only trusted origins that you have control over and make sure you understand the
				 *   implications of doing so.
				 * </div>
				 *
				 * The name of the cookie and the header can be specified using the `xsrfCookieName` and
				 * `xsrfHeaderName` properties of either `$httpProvider.defaults` at config-time,
				 * `$http.defaults` at run-time, or the per-request config object.
				 *
				 * In order to prevent collisions in environments where multiple AngularJS apps share the
				 * same domain or subdomain, we recommend that each application uses a unique cookie name.
				 *
				 *
				 * @param {object} config Object describing the request to be made and how it should be
				 *    processed. The object has following properties:
				 *
				 *    - **method** – `{string}` – HTTP method (e.g. 'GET', 'POST', etc)
				 *    - **url** – `{string|TrustedObject}` – Absolute or relative URL of the resource that is being requested;
				 *      or an object created by a call to `$sce.trustAsResourceUrl(url)`.
				 *    - **params** – `{Object.<string|Object>}` – Map of strings or objects which will be serialized
				 *      with the `paramSerializer` and appended as GET parameters.
				 *    - **data** – `{string|Object}` – Data to be sent as the request message data.
				 *    - **headers** – `{Object}` – Map of strings or functions which return strings representing
				 *      HTTP headers to send to the server. If the return value of a function is null, the
				 *      header will not be sent. Functions accept a config object as an argument.
				 *    - **eventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest object.
				 *      To bind events to the XMLHttpRequest upload object, use `uploadEventHandlers`.
				 *      The handler will be called in the context of a `$apply` block.
				 *    - **uploadEventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest upload
				 *      object. To bind events to the XMLHttpRequest object, use `eventHandlers`.
				 *      The handler will be called in the context of a `$apply` block.
				 *    - **xsrfHeaderName** – `{string}` – Name of HTTP header to populate with the XSRF token.
				 *    - **xsrfCookieName** – `{string}` – Name of cookie containing the XSRF token.
				 *    - **transformRequest** –
				 *      `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
				 *      transform function or an array of such functions. The transform function takes the http
				 *      request body and headers and returns its transformed (typically serialized) version.
				 *      See {@link ng.$http#overriding-the-default-transformations-per-request
				 *      Overriding the Default Transformations}
				 *    - **transformResponse** –
				 *      `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
				 *      transform function or an array of such functions. The transform function takes the http
				 *      response body, headers and status and returns its transformed (typically deserialized) version.
				 *      See {@link ng.$http#overriding-the-default-transformations-per-request
				 *      Overriding the Default Transformations}
				 *    - **paramSerializer** - `{string|function(Object<string,string>):string}` - A function used to
				 *      prepare the string representation of request parameters (specified as an object).
				 *      If specified as string, it is interpreted as function registered with the
				 *      {@link $injector $injector}, which means you can create your own serializer
				 *      by registering it as a {@link auto.$provide#service service}.
				 *      The default serializer is the {@link $httpParamSerializer $httpParamSerializer};
				 *      alternatively, you can use the {@link $httpParamSerializerJQLike $httpParamSerializerJQLike}
				 *    - **cache** – `{boolean|Object}` – A boolean value or object created with
				 *      {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
				 *      See {@link $http#caching $http Caching} for more information.
				 *    - **timeout** – `{number|Promise}` – timeout in milliseconds, or {@link ng.$q promise}
				 *      that should abort the request when resolved.
				 *
				 *      A numerical timeout or a promise returned from {@link ng.$timeout $timeout}, will set
				 *      the `xhrStatus` in the {@link $http#$http-returns response} to "timeout", and any other
				 *      resolved promise will set it to "abort", following standard XMLHttpRequest behavior.
				 *
				 *    - **withCredentials** - `{boolean}` - whether to set the `withCredentials` flag on the
				 *      XHR object. See [requests with credentials](https://developer.mozilla.org/docs/Web/HTTP/Access_control_CORS#Requests_with_credentials)
				 *      for more information.
				 *    - **responseType** - `{string}` - see
				 *      [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest#xmlhttprequest-responsetype).
				 *
				 * @returns {HttpPromise} A {@link ng.$q `Promise}` that will be resolved (request success)
				 *   or rejected (request failure) with a response object.
				 *
				 *   The response object has these properties:
				 *
				 *   - **data** – `{string|Object}` – The response body transformed with
				 *     the transform functions.
				 *   - **status** – `{number}` – HTTP status code of the response.
				 *   - **headers** – `{function([headerName])}` – Header getter function.
				 *   - **config** – `{Object}` – The configuration object that was used
				 *     to generate the request.
				 *   - **statusText** – `{string}` – HTTP status text of the response.
				 *   - **xhrStatus** – `{string}` – Status of the XMLHttpRequest
				 *     (`complete`, `error`, `timeout` or `abort`).
				 *
				 *
				 *   A response status code between 200 and 299 is considered a success status
				 *   and will result in the success callback being called. Any response status
				 *   code outside of that range is considered an error status and will result
				 *   in the error callback being called.
				 *   Also, status codes less than -1 are normalized to zero. -1 usually means
				 *   the request was aborted, e.g. using a `config.timeout`. More information
				 *   about the status might be available in the `xhrStatus` property.
				 *
				 *   Note that if the response is a redirect, XMLHttpRequest will transparently
				 *   follow it, meaning that the outcome (success or error) will be determined
				 *   by the final response status code.
				 *
				 *
				 * @property {Array.<Object>} pendingRequests Array of config objects for currently pending
				 *   requests. This is primarily meant to be used for debugging purposes.
				 *
				 *
				 * @example
			<example module="httpExample" name="http-service">
			<file name="index.html">
			  <div ng-controller="FetchController">
				<select ng-model="method" aria-label="Request method">
				  <option>GET</option>
				  <option>JSONP</option>
				</select>
				<input type="text" ng-model="url" size="80" aria-label="URL" />
				<button id="fetchbtn" ng-click="fetch()">fetch</button><br>
				<button id="samplegetbtn" ng-click="updateModel('GET', 'http-hello.html')">Sample GET</button>
				<button id="samplejsonpbtn"
				  ng-click="updateModel('JSONP',
								'https://angularjs.org/greet.php?name=Super%20Hero')">
				  Sample JSONP
				</button>
				<button id="invalidjsonpbtn"
				  ng-click="updateModel('JSONP', 'https://angularjs.org/doesntexist')">
					Invalid JSONP
				  </button>
				<pre>http status code: {{status}}</pre>
				<pre>http response data: {{data}}</pre>
			  </div>
			</file>
			<file name="script.js">
			  angular.module('httpExample', [])
				.config(['$sceDelegateProvider', function($sceDelegateProvider) {
				  // We must add the JSONP endpoint that we are using to the trusted list to show that we trust it
				  $sceDelegateProvider.trustedResourceUrlList([
					'self',
					'https://angularjs.org/**'
				  ]);
				}])
				.controller('FetchController', ['$scope', '$http', '$templateCache',
				  function($scope, $http, $templateCache) {
					$scope.method = 'GET';
					$scope.url = 'http-hello.html';
			
					$scope.fetch = function() {
					  $scope.code = null;
					  $scope.response = null;
			
					  $http({method: $scope.method, url: $scope.url, cache: $templateCache}).
						then(function(response) {
						  $scope.status = response.status;
						  $scope.data = response.data;
						}, function(response) {
						  $scope.data = response.data || 'Request failed';
						  $scope.status = response.status;
					  });
					};
			
					$scope.updateModel = function(method, url) {
					  $scope.method = method;
					  $scope.url = url;
					};
				  }]);
			</file>
			<file name="http-hello.html">
			  Hello, $http!
			</file>
			<file name="protractor.js" type="protractor">
			  var status = element(by.binding('status'));
			  var data = element(by.binding('data'));
			  var fetchBtn = element(by.id('fetchbtn'));
			  var sampleGetBtn = element(by.id('samplegetbtn'));
			  var invalidJsonpBtn = element(by.id('invalidjsonpbtn'));
			
			  it('should make an xhr GET request', function() {
				sampleGetBtn.click();
				fetchBtn.click();
				expect(status.getText()).toMatch('200');
				expect(data.getText()).toMatch(/Hello, \$http!/);
			  });
			
			// Commented out due to flakes. See https://github.com/angular/angular.js/issues/9185
			// it('should make a JSONP request to angularjs.org', function() {
			//   var sampleJsonpBtn = element(by.id('samplejsonpbtn'));
			//   sampleJsonpBtn.click();
			//   fetchBtn.click();
			//   expect(status.getText()).toMatch('200');
			//   expect(data.getText()).toMatch(/Super Hero!/);
			// });
			
			  it('should make JSONP request to invalid URL and invoke the error handler',
				  function() {
				invalidJsonpBtn.click();
				fetchBtn.click();
				expect(status.getText()).toMatch('0');
				expect(data.getText()).toMatch('Request failed');
			  });
			</file>
			</example>
				 */
				function $http(requestConfig) {

					if (!isObject(requestConfig)) {
						throw minErr('$http')('badreq', 'Http request configuration must be an object.  Received: {0}', requestConfig);
					}

					if (!isString($sce.valueOf(requestConfig.url))) {
						throw minErr('$http')('badreq', 'Http request configuration url must be a string or a $sce trusted object.  Received: {0}', requestConfig.url);
					}

					var config = extend({
						method: 'get',
						transformRequest: defaults.transformRequest,
						transformResponse: defaults.transformResponse,
						paramSerializer: defaults.paramSerializer,
						jsonpCallbackParam: defaults.jsonpCallbackParam
					}, requestConfig);

					config.headers = mergeHeaders(requestConfig);
					config.method = uppercase(config.method);
					config.paramSerializer = isString(config.paramSerializer) ?
						$injector.get(config.paramSerializer) : config.paramSerializer;

					$browser.$$incOutstandingRequestCount('$http');

					var requestInterceptors = [];
					var responseInterceptors = [];
					var promise = $q.resolve(config);

					// apply interceptors
					forEach(reversedInterceptors, function (interceptor) {
						if (interceptor.request || interceptor.requestError) {
							requestInterceptors.unshift(interceptor.request, interceptor.requestError);
						}
						if (interceptor.response || interceptor.responseError) {
							responseInterceptors.push(interceptor.response, interceptor.responseError);
						}
					});

					promise = chainInterceptors(promise, requestInterceptors);
					promise = promise.then(serverRequest);
					promise = chainInterceptors(promise, responseInterceptors);
					promise = promise.finally(completeOutstandingRequest);

					return promise;


					function chainInterceptors(promise, interceptors) {
						for (var i = 0, ii = interceptors.length; i < ii;) {
							var thenFn = interceptors[i++];
							var rejectFn = interceptors[i++];

							promise = promise.then(thenFn, rejectFn);
						}

						interceptors.length = 0;

						return promise;
					}

					function completeOutstandingRequest() {
						$browser.$$completeOutstandingRequest(noop, '$http');
					}

					function executeHeaderFns(headers, config) {
						var headerContent, processedHeaders = {};

						forEach(headers, function (headerFn, header) {
							if (isFunction(headerFn)) {
								headerContent = headerFn(config);
								if (headerContent != null) {
									processedHeaders[header] = headerContent;
								}
							} else {
								processedHeaders[header] = headerFn;
							}
						});

						return processedHeaders;
					}

					function mergeHeaders(config) {
						var defHeaders = defaults.headers,
							reqHeaders = extend({}, config.headers),
							defHeaderName, lowercaseDefHeaderName, reqHeaderName;

						defHeaders = extend({}, defHeaders.common, defHeaders[lowercase(config.method)]);

						// using for-in instead of forEach to avoid unnecessary iteration after header has been found
						defaultHeadersIteration:
						for (defHeaderName in defHeaders) {
							lowercaseDefHeaderName = lowercase(defHeaderName);

							for (reqHeaderName in reqHeaders) {
								if (lowercase(reqHeaderName) === lowercaseDefHeaderName) {
									continue defaultHeadersIteration;
								}
							}

							reqHeaders[defHeaderName] = defHeaders[defHeaderName];
						}

						// execute if header value is a function for merged headers
						return executeHeaderFns(reqHeaders, shallowCopy(config));
					}

					function serverRequest(config) {
						var headers = config.headers;
						var reqData = transformData(config.data, headersGetter(headers), undefined, config.transformRequest);

						// strip content-type if data is undefined
						if (isUndefined(reqData)) {
							forEach(headers, function (value, header) {
								if (lowercase(header) === 'content-type') {
									delete headers[header];
								}
							});
						}

						if (isUndefined(config.withCredentials) && !isUndefined(defaults.withCredentials)) {
							config.withCredentials = defaults.withCredentials;
						}

						// send request
						return sendReq(config, reqData).then(transformResponse, transformResponse);
					}

					function transformResponse(response) {
						// make a copy since the response must be cacheable
						var resp = extend({}, response);
						resp.data = transformData(response.data, response.headers, response.status,
							config.transformResponse);
						return (isSuccess(response.status))
							? resp
							: $q.reject(resp);
					}
				}

				$http.pendingRequests = [];

				/**
				 * @ngdoc method
				 * @name $http#get
				 *
				 * @description
				 * Shortcut method to perform `GET` request.
				 *
				 * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
				 *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */

				/**
				 * @ngdoc method
				 * @name $http#delete
				 *
				 * @description
				 * Shortcut method to perform `DELETE` request.
				 *
				 * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
				 *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */

				/**
				 * @ngdoc method
				 * @name $http#head
				 *
				 * @description
				 * Shortcut method to perform `HEAD` request.
				 *
				 * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
				 *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */

				/**
				 * @ngdoc method
				 * @name $http#jsonp
				 *
				 * @description
				 * Shortcut method to perform `JSONP` request.
				 *
				 * Note that, since JSONP requests are sensitive because the response is given full access to the browser,
				 * the url must be declared, via {@link $sce} as a trusted resource URL.
				 * You can trust a URL by adding it to the trusted resource URL list via
				 * {@link $sceDelegateProvider#trustedResourceUrlList  `$sceDelegateProvider.trustedResourceUrlList`} or
				 * by explicitly trusting the URL via {@link $sce#trustAsResourceUrl `$sce.trustAsResourceUrl(url)`}.
				 *
				 * You should avoid generating the URL for the JSONP request from user provided data.
				 * Provide additional query parameters via `params` property of the `config` parameter, rather than
				 * modifying the URL itself.
				 *
				 * JSONP requests must specify a callback to be used in the response from the server. This callback
				 * is passed as a query parameter in the request. You must specify the name of this parameter by
				 * setting the `jsonpCallbackParam` property on the request config object.
				 *
				 * ```
				 * $http.jsonp('some/trusted/url', {jsonpCallbackParam: 'callback'})
				 * ```
				 *
				 * You can also specify a default callback parameter name in `$http.defaults.jsonpCallbackParam`.
				 * Initially this is set to `'callback'`.
				 *
				 * <div class="alert alert-danger">
				 * You can no longer use the `JSON_CALLBACK` string as a placeholder for specifying where the callback
				 * parameter value should go.
				 * </div>
				 *
				 * If you would like to customise where and how the callbacks are stored then try overriding
				 * or decorating the {@link $jsonpCallbacks} service.
				 *
				 * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
				 *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */
				createShortMethods('get', 'delete', 'head', 'jsonp');

				/**
				 * @ngdoc method
				 * @name $http#post
				 *
				 * @description
				 * Shortcut method to perform `POST` request.
				 *
				 * @param {string} url Relative or absolute URL specifying the destination of the request
				 * @param {*} data Request content
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */

				/**
				 * @ngdoc method
				 * @name $http#put
				 *
				 * @description
				 * Shortcut method to perform `PUT` request.
				 *
				 * @param {string} url Relative or absolute URL specifying the destination of the request
				 * @param {*} data Request content
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */

				/**
				 * @ngdoc method
				 * @name $http#patch
				 *
				 * @description
				 * Shortcut method to perform `PATCH` request.
				 *
				 * @param {string} url Relative or absolute URL specifying the destination of the request
				 * @param {*} data Request content
				 * @param {Object=} config Optional configuration object. See {@link ng.$http#$http-arguments `$http()` arguments}.
				 * @returns {HttpPromise}  A Promise that will be resolved or rejected with a response object.
				 * See {@link ng.$http#$http-returns `$http()` return value}.
				 */
				createShortMethodsWithData('post', 'put', 'patch');

				/**
				 * @ngdoc property
				 * @name $http#defaults
				 *
				 * @description
				 * Runtime equivalent of the `$httpProvider.defaults` property. Allows configuration of
				 * default headers, withCredentials as well as request and response transformations.
				 *
				 * See "Setting HTTP Headers" and "Transforming Requests and Responses" sections above.
				 */
				$http.defaults = defaults;


				return $http;


				function createShortMethods(names) {
					forEach(arguments, function (name) {
						$http[name] = function (url, config) {
							return $http(extend({}, config || {}, {
								method: name,
								url: url
							}));
						};
					});
				}


				function createShortMethodsWithData(name) {
					forEach(arguments, function (name) {
						$http[name] = function (url, data, config) {
							return $http(extend({}, config || {}, {
								method: name,
								url: url,
								data: data
							}));
						};
					});
				}


				/**
				 * Makes the request.
				 *
				 * !!! ACCESSES CLOSURE VARS:
				 * $httpBackend, defaults, $log, $rootScope, defaultCache, $http.pendingRequests
				 */
				function sendReq(config, reqData) {
					var deferred = $q.defer(),
						promise = deferred.promise,
						cache,
						cachedResp,
						reqHeaders = config.headers,
						isJsonp = lowercase(config.method) === 'jsonp',
						url = config.url;

					if (isJsonp) {
						// JSONP is a pretty sensitive operation where we're allowing a script to have full access to
						// our DOM and JS space.  So we require that the URL satisfies SCE.RESOURCE_URL.
						url = $sce.getTrustedResourceUrl(url);
					} else if (!isString(url)) {
						// If it is not a string then the URL must be a $sce trusted object
						url = $sce.valueOf(url);
					}

					url = buildUrl(url, config.paramSerializer(config.params));

					if (isJsonp) {
						// Check the url and add the JSONP callback placeholder
						url = sanitizeJsonpCallbackParam(url, config.jsonpCallbackParam);
					}

					$http.pendingRequests.push(config);
					promise.then(removePendingReq, removePendingReq);

					if ((config.cache || defaults.cache) && config.cache !== false &&
						(config.method === 'GET' || config.method === 'JSONP')) {
						cache = isObject(config.cache) ? config.cache
							: isObject(/** @type {?} */(defaults).cache)
								? /** @type {?} */ (defaults).cache
								: defaultCache;
					}

					if (cache) {
						cachedResp = cache.get(url);
						if (isDefined(cachedResp)) {
							if (isPromiseLike(cachedResp)) {
								// cached request has already been sent, but there is no response yet
								cachedResp.then(resolvePromiseWithResult, resolvePromiseWithResult);
							} else {
								// serving from cache
								if (isArray(cachedResp)) {
									resolvePromise(cachedResp[1], cachedResp[0], shallowCopy(cachedResp[2]), cachedResp[3], cachedResp[4]);
								} else {
									resolvePromise(cachedResp, 200, {}, 'OK', 'complete');
								}
							}
						} else {
							// put the promise for the non-transformed response into cache as a placeholder
							cache.put(url, promise);
						}
					}


					// if we won't have the response in cache, set the xsrf headers and
					// send the request to the backend
					if (isUndefined(cachedResp)) {
						var xsrfValue = urlIsAllowedOrigin(config.url)
							? $$cookieReader()[config.xsrfCookieName || defaults.xsrfCookieName]
							: undefined;
						if (xsrfValue) {
							reqHeaders[(config.xsrfHeaderName || defaults.xsrfHeaderName)] = xsrfValue;
						}

						$httpBackend(config.method, url, reqData, done, reqHeaders, config.timeout,
							config.withCredentials, config.responseType,
							createApplyHandlers(config.eventHandlers),
							createApplyHandlers(config.uploadEventHandlers));
					}

					return promise;

					function createApplyHandlers(eventHandlers) {
						if (eventHandlers) {
							var applyHandlers = {};
							forEach(eventHandlers, function (eventHandler, key) {
								applyHandlers[key] = function (event) {
									if (useApplyAsync) {
										$rootScope.$applyAsync(callEventHandler);
									} else if ($rootScope.$$phase) {
										callEventHandler();
									} else {
										$rootScope.$apply(callEventHandler);
									}

									function callEventHandler() {
										eventHandler(event);
									}
								};
							});
							return applyHandlers;
						}
					}


					/**
					 * Callback registered to $httpBackend():
					 *  - caches the response if desired
					 *  - resolves the raw $http promise
					 *  - calls $apply
					 */
					function done(status, response, headersString, statusText, xhrStatus) {
						if (cache) {
							if (isSuccess(status)) {
								cache.put(url, [status, response, parseHeaders(headersString), statusText, xhrStatus]);
							} else {
								// remove promise from the cache
								cache.remove(url);
							}
						}

						function resolveHttpPromise() {
							resolvePromise(response, status, headersString, statusText, xhrStatus);
						}

						if (useApplyAsync) {
							$rootScope.$applyAsync(resolveHttpPromise);
						} else {
							resolveHttpPromise();
							if (!$rootScope.$$phase) $rootScope.$apply();
						}
					}


					/**
					 * Resolves the raw $http promise.
					 */
					function resolvePromise(response, status, headers, statusText, xhrStatus) {
						//status: HTTP response status code, 0, -1 (aborted by timeout / promise)
						status = status >= -1 ? status : 0;

						(isSuccess(status) ? deferred.resolve : deferred.reject)({
							data: response,
							status: status,
							headers: headersGetter(headers),
							config: config,
							statusText: statusText,
							xhrStatus: xhrStatus
						});
					}

					function resolvePromiseWithResult(result) {
						resolvePromise(result.data, result.status, shallowCopy(result.headers()), result.statusText, result.xhrStatus);
					}

					function removePendingReq() {
						var idx = $http.pendingRequests.indexOf(config);
						if (idx !== -1) $http.pendingRequests.splice(idx, 1);
					}
				}


				function buildUrl(url, serializedParams) {
					if (serializedParams.length > 0) {
						url += ((url.indexOf('?') === -1) ? '?' : '&') + serializedParams;
					}
					return url;
				}

				function sanitizeJsonpCallbackParam(url, cbKey) {
					var parts = url.split('?');
					if (parts.length > 2) {
						// Throw if the url contains more than one `?` query indicator
						throw $httpMinErr('badjsonp', 'Illegal use more than one "?", in url, "{1}"', url);
					}
					var params = parseKeyValue(parts[1]);
					forEach(params, function (value, key) {
						if (value === 'JSON_CALLBACK') {
							// Throw if the url already contains a reference to JSON_CALLBACK
							throw $httpMinErr('badjsonp', 'Illegal use of JSON_CALLBACK in url, "{0}"', url);
						}
						if (key === cbKey) {
							// Throw if the callback param was already provided
							throw $httpMinErr('badjsonp', 'Illegal use of callback param, "{0}", in url, "{1}"', cbKey, url);
						}
					});

					// Add in the JSON_CALLBACK callback param value
					url += ((url.indexOf('?') === -1) ? '?' : '&') + cbKey + '=JSON_CALLBACK';

					return url;
				}
			}];
	}

	/**
	 * @ngdoc service
	 * @name $xhrFactory
	 * @this
	 *
	 * @description
	 * Factory function used to create XMLHttpRequest objects.
	 *
	 * Replace or decorate this service to create your own custom XMLHttpRequest objects.
	 *
	 * ```
	 * angular.module('myApp', [])
	 * .factory('$xhrFactory', function() {
	 *   return function createXhr(method, url) {
	 *     return new window.XMLHttpRequest({mozSystem: true});
	 *   };
	 * });
	 * ```
	 *
	 * @param {string} method HTTP method of the request (GET, POST, PUT, ..)
	 * @param {string} url URL of the request.
	 */
	function $xhrFactoryProvider() {
		this.$get = function () {
			return function createXhr() {
				return new window.XMLHttpRequest();
			};
		};
	}

	/**
	 * @ngdoc service
	 * @name $httpBackend
	 * @requires $jsonpCallbacks
	 * @requires $document
	 * @requires $xhrFactory
	 * @this
	 *
	 * @description
	 * HTTP backend used by the {@link ng.$http service} that delegates to
	 * XMLHttpRequest object or JSONP and deals with browser incompatibilities.
	 *
	 * You should never need to use this service directly, instead use the higher-level abstractions:
	 * {@link ng.$http $http} or {@link ngResource.$resource $resource}.
	 *
	 * During testing this implementation is swapped with {@link ngMock.$httpBackend mock
	 * $httpBackend} which can be trained with responses.
	 */
	function $HttpBackendProvider() {
		this.$get = ['$browser', '$jsonpCallbacks', '$document', '$xhrFactory', function ($browser, $jsonpCallbacks, $document, $xhrFactory) {
			return createHttpBackend($browser, $xhrFactory, $browser.defer, $jsonpCallbacks, $document[0]);
		}];
	}

	function createHttpBackend($browser, createXhr, $browserDefer, callbacks, rawDocument) {
		// TODO(vojta): fix the signature
		return function (method, url, post, callback, headers, timeout, withCredentials, responseType, eventHandlers, uploadEventHandlers) {
			url = url || $browser.url();

			if (lowercase(method) === 'jsonp') {
				var callbackPath = callbacks.createCallback(url);
				var jsonpDone = jsonpReq(url, callbackPath, function (status, text) {
					// jsonpReq only ever sets status to 200 (OK), 404 (ERROR) or -1 (WAITING)
					var response = (status === 200) && callbacks.getResponse(callbackPath);
					completeRequest(callback, status, response, '', text, 'complete');
					callbacks.removeCallback(callbackPath);
				});
			} else {

				var xhr = createXhr(method, url);
				var abortedByTimeout = false;

				xhr.open(method, url, true);
				forEach(headers, function (value, key) {
					if (isDefined(value)) {
						xhr.setRequestHeader(key, value);
					}
				});

				xhr.onload = function requestLoaded() {
					var statusText = xhr.statusText || '';

					// responseText is the old-school way of retrieving response (supported by IE9)
					// response/responseType properties were introduced in XHR Level2 spec (supported by IE10)
					var response = ('response' in xhr) ? xhr.response : xhr.responseText;

					// normalize IE9 bug (http://bugs.jquery.com/ticket/1450)
					var status = xhr.status === 1223 ? 204 : xhr.status;

					// fix status code when it is 0 (0 status is undocumented).
					// Occurs when accessing file resources or on Android 4.1 stock browser
					// while retrieving files from application cache.
					if (status === 0) {
						status = response ? 200 : urlResolve(url).protocol === 'file' ? 404 : 0;
					}

					completeRequest(callback,
						status,
						response,
						xhr.getAllResponseHeaders(),
						statusText,
						'complete');
				};

				var requestError = function () {
					// The response is always empty
					// See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
					completeRequest(callback, -1, null, null, '', 'error');
				};

				var requestAborted = function () {
					completeRequest(callback, -1, null, null, '', abortedByTimeout ? 'timeout' : 'abort');
				};

				var requestTimeout = function () {
					// The response is always empty
					// See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
					completeRequest(callback, -1, null, null, '', 'timeout');
				};

				xhr.onerror = requestError;
				xhr.ontimeout = requestTimeout;
				xhr.onabort = requestAborted;

				forEach(eventHandlers, function (value, key) {
					xhr.addEventListener(key, value);
				});

				forEach(uploadEventHandlers, function (value, key) {
					xhr.upload.addEventListener(key, value);
				});

				if (withCredentials) {
					xhr.withCredentials = true;
				}

				if (responseType) {
					try {
						xhr.responseType = responseType;
					} catch (e) {
						// WebKit added support for the json responseType value on 09/03/2013
						// https://bugs.webkit.org/show_bug.cgi?id=73648. Versions of Safari prior to 7 are
						// known to throw when setting the value "json" as the response type. Other older
						// browsers implementing the responseType
						//
						// The json response type can be ignored if not supported, because JSON payloads are
						// parsed on the client-side regardless.
						if (responseType !== 'json') {
							throw e;
						}
					}
				}

				xhr.send(isUndefined(post) ? null : post);
			}

			// Since we are using xhr.abort() when a request times out, we have to set a flag that
			// indicates to requestAborted if the request timed out or was aborted.
			//
			// http.timeout = numerical timeout   timeout
			// http.timeout = $timeout            timeout
			// http.timeout = promise             abort
			// xhr.abort()                        abort (The xhr object is normally inaccessible, but
			//                                    can be exposed with the xhrFactory)
			if (timeout > 0) {
				var timeoutId = $browserDefer(function () {
					timeoutRequest('timeout');
				}, timeout);
			} else if (isPromiseLike(timeout)) {
				timeout.then(function () {
					timeoutRequest(isDefined(timeout.$$timeoutId) ? 'timeout' : 'abort');
				});
			}

			function timeoutRequest(reason) {
				abortedByTimeout = reason === 'timeout';
				if (jsonpDone) {
					jsonpDone();
				}
				if (xhr) {
					xhr.abort();
				}
			}

			function completeRequest(callback, status, response, headersString, statusText, xhrStatus) {
				// cancel timeout and subsequent timeout promise resolution
				if (isDefined(timeoutId)) {
					$browserDefer.cancel(timeoutId);
				}
				jsonpDone = xhr = null;

				callback(status, response, headersString, statusText, xhrStatus);
			}
		};

		function jsonpReq(url, callbackPath, done) {
			url = url.replace('JSON_CALLBACK', callbackPath);
			// we can't use jQuery/jqLite here because jQuery does crazy stuff with script elements, e.g.:
			// - fetches local scripts via XHR and evals them
			// - adds and immediately removes script elements from the document
			var script = rawDocument.createElement('script'), callback = null;
			script.type = 'text/javascript';
			script.src = url;
			script.async = true;

			callback = function (event) {
				script.removeEventListener('load', callback);
				script.removeEventListener('error', callback);
				rawDocument.body.removeChild(script);
				script = null;
				var status = -1;
				var text = 'unknown';

				if (event) {
					if (event.type === 'load' && !callbacks.wasCalled(callbackPath)) {
						event = { type: 'error' };
					}
					text = event.type;
					status = event.type === 'error' ? 404 : 200;
				}

				if (done) {
					done(status, text);
				}
			};

			script.addEventListener('load', callback);
			script.addEventListener('error', callback);
			rawDocument.body.appendChild(script);
			return callback;
		}
	}

	var $interpolateMinErr = angular.$interpolateMinErr = minErr('$interpolate');
	$interpolateMinErr.throwNoconcat = function (text) {
		throw $interpolateMinErr('noconcat',
			'Error while interpolating: {0}\nStrict Contextual Escaping disallows ' +
			'interpolations that concatenate multiple expressions when a trusted value is ' +
			'required.  See http://docs.angularjs.org/api/ng.$sce', text);
	};

	$interpolateMinErr.interr = function (text, err) {
		return $interpolateMinErr('interr', 'Can\'t interpolate: {0}\n{1}', text, err.toString());
	};

	/**
	 * @ngdoc provider
	 * @name $interpolateProvider
	 * @this
	 *
	 * @description
	 *
	 * Used for configuring the interpolation markup. Defaults to `{{` and `}}`.
	 *
	 * <div class="alert alert-danger">
	 * This feature is sometimes used to mix different markup languages, e.g. to wrap an AngularJS
	 * template within a Python Jinja template (or any other template language). Mixing templating
	 * languages is **very dangerous**. The embedding template language will not safely escape AngularJS
	 * expressions, so any user-controlled values in the template will cause Cross Site Scripting (XSS)
	 * security bugs!
	 * </div>
	 *
	 * @example
	<example name="custom-interpolation-markup" module="customInterpolationApp">
	<file name="index.html">
	<script>
	  var customInterpolationApp = angular.module('customInterpolationApp', []);
	
	  customInterpolationApp.config(function($interpolateProvider) {
		$interpolateProvider.startSymbol('//');
		$interpolateProvider.endSymbol('//');
	  });
	
	
	  customInterpolationApp.controller('DemoController', function() {
		  this.label = "This binding is brought you by // interpolation symbols.";
	  });
	</script>
	<div ng-controller="DemoController as demo">
		//demo.label//
	</div>
	</file>
	<file name="protractor.js" type="protractor">
	  it('should interpolate binding with custom symbols', function() {
		expect(element(by.binding('demo.label')).getText()).toBe('This binding is brought you by // interpolation symbols.');
	  });
	</file>
	</example>
	 */
	function $InterpolateProvider() {
		var startSymbol = '{{';
		var endSymbol = '}}';

		/**
		 * @ngdoc method
		 * @name $interpolateProvider#startSymbol
		 * @description
		 * Symbol to denote start of expression in the interpolated string. Defaults to `{{`.
		 *
		 * @param {string=} value new value to set the starting symbol to.
		 * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
		 */
		this.startSymbol = function (value) {
			if (value) {
				startSymbol = value;
				return this;
			}
			return startSymbol;
		};

		/**
		 * @ngdoc method
		 * @name $interpolateProvider#endSymbol
		 * @description
		 * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
		 *
		 * @param {string=} value new value to set the ending symbol to.
		 * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
		 */
		this.endSymbol = function (value) {
			if (value) {
				endSymbol = value;
				return this;
			}
			return endSymbol;
		};


		this.$get = ['$parse', '$exceptionHandler', '$sce', function ($parse, $exceptionHandler, $sce) {
			var startSymbolLength = startSymbol.length,
				endSymbolLength = endSymbol.length,
				escapedStartRegexp = new RegExp(startSymbol.replace(/./g, escape), 'g'),
				escapedEndRegexp = new RegExp(endSymbol.replace(/./g, escape), 'g');

			function escape(ch) {
				return '\\\\\\' + ch;
			}

			function unescapeText(text) {
				return text.replace(escapedStartRegexp, startSymbol).
					replace(escapedEndRegexp, endSymbol);
			}

			// TODO: this is the same as the constantWatchDelegate in parse.js
			function constantWatchDelegate(scope, listener, objectEquality, constantInterp) {
				var unwatch = scope.$watch(function constantInterpolateWatch(scope) {
					unwatch();
					return constantInterp(scope);
				}, listener, objectEquality);
				return unwatch;
			}

			/**
			 * @ngdoc service
			 * @name $interpolate
			 * @kind function
			 *
			 * @requires $parse
			 * @requires $sce
			 *
			 * @description
			 *
			 * Compiles a string with markup into an interpolation function. This service is used by the
			 * HTML {@link ng.$compile $compile} service for data binding. See
			 * {@link ng.$interpolateProvider $interpolateProvider} for configuring the
			 * interpolation markup.
			 *
			 *
			 * ```js
			 *   var $interpolate = ...; // injected
			 *   var exp = $interpolate('Hello {{name | uppercase}}!');
			 *   expect(exp({name:'AngularJS'})).toEqual('Hello ANGULARJS!');
			 * ```
			 *
			 * `$interpolate` takes an optional fourth argument, `allOrNothing`. If `allOrNothing` is
			 * `true`, the interpolation function will return `undefined` unless all embedded expressions
			 * evaluate to a value other than `undefined`.
			 *
			 * ```js
			 *   var $interpolate = ...; // injected
			 *   var context = {greeting: 'Hello', name: undefined };
			 *
			 *   // default "forgiving" mode
			 *   var exp = $interpolate('{{greeting}} {{name}}!');
			 *   expect(exp(context)).toEqual('Hello !');
			 *
			 *   // "allOrNothing" mode
			 *   exp = $interpolate('{{greeting}} {{name}}!', false, null, true);
			 *   expect(exp(context)).toBeUndefined();
			 *   context.name = 'AngularJS';
			 *   expect(exp(context)).toEqual('Hello AngularJS!');
			 * ```
			 *
			 * `allOrNothing` is useful for interpolating URLs. `ngSrc` and `ngSrcset` use this behavior.
			 *
			 * #### Escaped Interpolation
			 * $interpolate provides a mechanism for escaping interpolation markers. Start and end markers
			 * can be escaped by preceding each of their characters with a REVERSE SOLIDUS U+005C (backslash).
			 * It will be rendered as a regular start/end marker, and will not be interpreted as an expression
			 * or binding.
			 *
			 * This enables web-servers to prevent script injection attacks and defacing attacks, to some
			 * degree, while also enabling code examples to work without relying on the
			 * {@link ng.directive:ngNonBindable ngNonBindable} directive.
			 *
			 * **For security purposes, it is strongly encouraged that web servers escape user-supplied data,
			 * replacing angle brackets (&lt;, &gt;) with &amp;lt; and &amp;gt; respectively, and replacing all
			 * interpolation start/end markers with their escaped counterparts.**
			 *
			 * Escaped interpolation markers are only replaced with the actual interpolation markers in rendered
			 * output when the $interpolate service processes the text. So, for HTML elements interpolated
			 * by {@link ng.$compile $compile}, or otherwise interpolated with the `mustHaveExpression` parameter
			 * set to `true`, the interpolated text must contain an unescaped interpolation expression. As such,
			 * this is typically useful only when user-data is used in rendering a template from the server, or
			 * when otherwise untrusted data is used by a directive.
			 *
			 * <example name="interpolation">
			 *  <file name="index.html">
			 *    <div ng-init="username='A user'">
			 *      <p ng-init="apptitle='Escaping demo'">{{apptitle}}: \{\{ username = "defaced value"; \}\}
			 *        </p>
			 *      <p><strong>{{username}}</strong> attempts to inject code which will deface the
			 *        application, but fails to accomplish their task, because the server has correctly
			 *        escaped the interpolation start/end markers with REVERSE SOLIDUS U+005C (backslash)
			 *        characters.</p>
			 *      <p>Instead, the result of the attempted script injection is visible, and can be removed
			 *        from the database by an administrator.</p>
			 *    </div>
			 *  </file>
			 * </example>
			 *
			 * @knownIssue
			 * It is currently not possible for an interpolated expression to contain the interpolation end
			 * symbol. For example, `{{ '}}' }}` will be incorrectly interpreted as `{{ ' }}` + `' }}`, i.e.
			 * an interpolated expression consisting of a single-quote (`'`) and the `' }}` string.
			 *
			 * @knownIssue
			 * All directives and components must use the standard `{{` `}}` interpolation symbols
			 * in their templates. If you change the application interpolation symbols the {@link $compile}
			 * service will attempt to denormalize the standard symbols to the custom symbols.
			 * The denormalization process is not clever enough to know not to replace instances of the standard
			 * symbols where they would not normally be treated as interpolation symbols. For example in the following
			 * code snippet the closing braces of the literal object will get incorrectly denormalized:
			 *
			 * ```
			 * <div data-context='{"context":{"id":3,"type":"page"}}">
			 * ```
			 *
			 * The workaround is to ensure that such instances are separated by whitespace:
			 * ```
			 * <div data-context='{"context":{"id":3,"type":"page"} }">
			 * ```
			 *
			 * See https://github.com/angular/angular.js/pull/14610#issuecomment-219401099 for more information.
			 *
			 * @param {string} text The text with markup to interpolate.
			 * @param {boolean=} mustHaveExpression if set to true then the interpolation string must have
			 *    embedded expression in order to return an interpolation function. Strings with no
			 *    embedded expression will return null for the interpolation function.
			 * @param {string=} trustedContext when provided, the returned function passes the interpolated
			 *    result through {@link ng.$sce#getTrusted $sce.getTrusted(interpolatedResult,
			 *    trustedContext)} before returning it.  Refer to the {@link ng.$sce $sce} service that
			 *    provides Strict Contextual Escaping for details.
			 * @param {boolean=} allOrNothing if `true`, then the returned function returns undefined
			 *    unless all embedded expressions evaluate to a value other than `undefined`.
			 * @returns {function(context)} an interpolation function which is used to compute the
			 *    interpolated string. The function has these parameters:
			 *
			 * - `context`: evaluation context for all expressions embedded in the interpolated text
			 */
			function $interpolate(text, mustHaveExpression, trustedContext, allOrNothing) {
				var contextAllowsConcatenation = trustedContext === $sce.URL || trustedContext === $sce.MEDIA_URL;

				// Provide a quick exit and simplified result function for text with no interpolation
				if (!text.length || text.indexOf(startSymbol) === -1) {
					if (mustHaveExpression) return;

					var unescapedText = unescapeText(text);
					if (contextAllowsConcatenation) {
						unescapedText = $sce.getTrusted(trustedContext, unescapedText);
					}
					var constantInterp = valueFn(unescapedText);
					constantInterp.exp = text;
					constantInterp.expressions = [];
					constantInterp.$$watchDelegate = constantWatchDelegate;

					return constantInterp;
				}

				allOrNothing = !!allOrNothing;
				var startIndex,
					endIndex,
					index = 0,
					expressions = [],
					parseFns,
					textLength = text.length,
					exp,
					concat = [],
					expressionPositions = [],
					singleExpression;


				while (index < textLength) {
					if (((startIndex = text.indexOf(startSymbol, index)) !== -1) &&
						((endIndex = text.indexOf(endSymbol, startIndex + startSymbolLength)) !== -1)) {
						if (index !== startIndex) {
							concat.push(unescapeText(text.substring(index, startIndex)));
						}
						exp = text.substring(startIndex + startSymbolLength, endIndex);
						expressions.push(exp);
						index = endIndex + endSymbolLength;
						expressionPositions.push(concat.length);
						concat.push(''); // Placeholder that will get replaced with the evaluated expression.
					} else {
						// we did not find an interpolation, so we have to add the remainder to the separators array
						if (index !== textLength) {
							concat.push(unescapeText(text.substring(index)));
						}
						break;
					}
				}

				singleExpression = concat.length === 1 && expressionPositions.length === 1;
				// Intercept expression if we need to stringify concatenated inputs, which may be SCE trusted
				// objects rather than simple strings
				// (we don't modify the expression if the input consists of only a single trusted input)
				var interceptor = contextAllowsConcatenation && singleExpression ? undefined : parseStringifyInterceptor;
				parseFns = expressions.map(function (exp) { return $parse(exp, interceptor); });

				// Concatenating expressions makes it hard to reason about whether some combination of
				// concatenated values are unsafe to use and could easily lead to XSS.  By requiring that a
				// single expression be used for some $sce-managed secure contexts (RESOURCE_URLs mostly),
				// we ensure that the value that's used is assigned or constructed by some JS code somewhere
				// that is more testable or make it obvious that you bound the value to some user controlled
				// value.  This helps reduce the load when auditing for XSS issues.

				// Note that URL and MEDIA_URL $sce contexts do not need this, since `$sce` can sanitize the values
				// passed to it. In that case, `$sce.getTrusted` will be called on either the single expression
				// or on the overall concatenated string (losing trusted types used in the mix, by design).
				// Both these methods will sanitize plain strings. Also, HTML could be included, but since it's
				// only used in srcdoc attributes, this would not be very useful.

				if (!mustHaveExpression || expressions.length) {
					var compute = function (values) {
						for (var i = 0, ii = expressions.length; i < ii; i++) {
							if (allOrNothing && isUndefined(values[i])) return;
							concat[expressionPositions[i]] = values[i];
						}

						if (contextAllowsConcatenation) {
							// If `singleExpression` then `concat[0]` might be a "trusted" value or `null`, rather than a string
							return $sce.getTrusted(trustedContext, singleExpression ? concat[0] : concat.join(''));
						} else if (trustedContext && concat.length > 1) {
							// This context does not allow more than one part, e.g. expr + string or exp + exp.
							$interpolateMinErr.throwNoconcat(text);
						}
						// In an unprivileged context or only one part: just concatenate and return.
						return concat.join('');
					};

					return extend(function interpolationFn(context) {
						var i = 0;
						var ii = expressions.length;
						var values = new Array(ii);

						try {
							for (; i < ii; i++) {
								values[i] = parseFns[i](context);
							}

							return compute(values);
						} catch (err) {
							$exceptionHandler($interpolateMinErr.interr(text, err));
						}

					}, {
						// all of these properties are undocumented for now
						exp: text, //just for compatibility with regular watchers created via $watch
						expressions: expressions,
						$$watchDelegate: function (scope, listener) {
							var lastValue;
							return scope.$watchGroup(parseFns, /** @this */ function interpolateFnWatcher(values, oldValues) {
								var currValue = compute(values);
								listener.call(this, currValue, values !== oldValues ? lastValue : currValue, scope);
								lastValue = currValue;
							});
						}
					});
				}

				function parseStringifyInterceptor(value) {
					try {
						// In concatenable contexts, getTrusted comes at the end, to avoid sanitizing individual
						// parts of a full URL. We don't care about losing the trustedness here.
						// In non-concatenable contexts, where there is only one expression, this interceptor is
						// not applied to the expression.
						value = (trustedContext && !contextAllowsConcatenation) ?
							$sce.getTrusted(trustedContext, value) :
							$sce.valueOf(value);
						return allOrNothing && !isDefined(value) ? value : stringify(value);
					} catch (err) {
						$exceptionHandler($interpolateMinErr.interr(text, err));
					}
				}
			}


			/**
			 * @ngdoc method
			 * @name $interpolate#startSymbol
			 * @description
			 * Symbol to denote the start of expression in the interpolated string. Defaults to `{{`.
			 *
			 * Use {@link ng.$interpolateProvider#startSymbol `$interpolateProvider.startSymbol`} to change
			 * the symbol.
			 *
			 * @returns {string} start symbol.
			 */
			$interpolate.startSymbol = function () {
				return startSymbol;
			};


			/**
			 * @ngdoc method
			 * @name $interpolate#endSymbol
			 * @description
			 * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
			 *
			 * Use {@link ng.$interpolateProvider#endSymbol `$interpolateProvider.endSymbol`} to change
			 * the symbol.
			 *
			 * @returns {string} end symbol.
			 */
			$interpolate.endSymbol = function () {
				return endSymbol;
			};

			return $interpolate;
		}];
	}

	var $intervalMinErr = minErr('$interval');

	/** @this */
	function $IntervalProvider() {
		this.$get = ['$$intervalFactory', '$window',
			function ($$intervalFactory, $window) {
				var intervals = {};
				var setIntervalFn = function (tick, delay, deferred) {
					var id = $window.setInterval(tick, delay);
					intervals[id] = deferred;
					return id;
				};
				var clearIntervalFn = function (id) {
					$window.clearInterval(id);
					delete intervals[id];
				};

				/**
				 * @ngdoc service
				 * @name $interval
				 *
				 * @description
				 * AngularJS's wrapper for `window.setInterval`. The `fn` function is executed every `delay`
				 * milliseconds.
				 *
				 * The return value of registering an interval function is a promise. This promise will be
				 * notified upon each tick of the interval, and will be resolved after `count` iterations, or
				 * run indefinitely if `count` is not defined. The value of the notification will be the
				 * number of iterations that have run.
				 * To cancel an interval, call `$interval.cancel(promise)`.
				 *
				 * In tests you can use {@link ngMock.$interval#flush `$interval.flush(millis)`} to
				 * move forward by `millis` milliseconds and trigger any functions scheduled to run in that
				 * time.
				 *
				 * <div class="alert alert-warning">
				 * **Note**: Intervals created by this service must be explicitly destroyed when you are finished
				 * with them.  In particular they are not automatically destroyed when a controller's scope or a
				 * directive's element are destroyed.
				 * You should take this into consideration and make sure to always cancel the interval at the
				 * appropriate moment.  See the example below for more details on how and when to do this.
				 * </div>
				 *
				 * @param {function()} fn A function that should be called repeatedly. If no additional arguments
				 *   are passed (see below), the function is called with the current iteration count.
				 * @param {number} delay Number of milliseconds between each function call.
				 * @param {number=} [count=0] Number of times to repeat. If not set, or 0, will repeat
				 *   indefinitely.
				 * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
				 *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
				 * @param {...*=} Pass additional parameters to the executed function.
				 * @returns {promise} A promise which will be notified on each iteration. It will resolve once all iterations of the interval complete.
				 *
				 * @example
				 * <example module="intervalExample" name="interval-service">
				 * <file name="index.html">
				 *   <script>
				 *     angular.module('intervalExample', [])
				 *       .controller('ExampleController', ['$scope', '$interval',
				 *         function($scope, $interval) {
				 *           $scope.format = 'M/d/yy h:mm:ss a';
				 *           $scope.blood_1 = 100;
				 *           $scope.blood_2 = 120;
				 *
				 *           var stop;
				 *           $scope.fight = function() {
				 *             // Don't start a new fight if we are already fighting
				 *             if ( angular.isDefined(stop) ) return;
				 *
				 *             stop = $interval(function() {
				 *               if ($scope.blood_1 > 0 && $scope.blood_2 > 0) {
				 *                 $scope.blood_1 = $scope.blood_1 - 3;
				 *                 $scope.blood_2 = $scope.blood_2 - 4;
				 *               } else {
				 *                 $scope.stopFight();
				 *               }
				 *             }, 100);
				 *           };
				 *
				 *           $scope.stopFight = function() {
				 *             if (angular.isDefined(stop)) {
				 *               $interval.cancel(stop);
				 *               stop = undefined;
				 *             }
				 *           };
				 *
				 *           $scope.resetFight = function() {
				 *             $scope.blood_1 = 100;
				 *             $scope.blood_2 = 120;
				 *           };
				 *
				 *           $scope.$on('$destroy', function() {
				 *             // Make sure that the interval is destroyed too
				 *             $scope.stopFight();
				 *           });
				 *         }])
				 *       // Register the 'myCurrentTime' directive factory method.
				 *       // We inject $interval and dateFilter service since the factory method is DI.
				 *       .directive('myCurrentTime', ['$interval', 'dateFilter',
				 *         function($interval, dateFilter) {
				 *           // return the directive link function. (compile function not needed)
				 *           return function(scope, element, attrs) {
				 *             var format,  // date format
				 *                 stopTime; // so that we can cancel the time updates
				 *
				 *             // used to update the UI
				 *             function updateTime() {
				 *               element.text(dateFilter(new Date(), format));
				 *             }
				 *
				 *             // watch the expression, and update the UI on change.
				 *             scope.$watch(attrs.myCurrentTime, function(value) {
				 *               format = value;
				 *               updateTime();
				 *             });
				 *
				 *             stopTime = $interval(updateTime, 1000);
				 *
				 *             // listen on DOM destroy (removal) event, and cancel the next UI update
				 *             // to prevent updating time after the DOM element was removed.
				 *             element.on('$destroy', function() {
				 *               $interval.cancel(stopTime);
				 *             });
				 *           }
				 *         }]);
				 *   </script>
				 *
				 *   <div>
				 *     <div ng-controller="ExampleController">
				 *       <label>Date format: <input ng-model="format"></label> <hr/>
				 *       Current time is: <span my-current-time="format"></span>
				 *       <hr/>
				 *       Blood 1 : <font color='red'>{{blood_1}}</font>
				 *       Blood 2 : <font color='red'>{{blood_2}}</font>
				 *       <button type="button" data-ng-click="fight()">Fight</button>
				 *       <button type="button" data-ng-click="stopFight()">StopFight</button>
				 *       <button type="button" data-ng-click="resetFight()">resetFight</button>
				 *     </div>
				 *   </div>
				 *
				 * </file>
				 * </example>
				 */
				var interval = $$intervalFactory(setIntervalFn, clearIntervalFn);

				/**
				 * @ngdoc method
				 * @name $interval#cancel
				 *
				 * @description
				 * Cancels a task associated with the `promise`.
				 *
				 * @param {Promise=} promise returned by the `$interval` function.
				 * @returns {boolean} Returns `true` if the task was successfully canceled.
				 */
				interval.cancel = function (promise) {
					if (!promise) return false;

					if (!promise.hasOwnProperty('$$intervalId')) {
						throw $intervalMinErr('badprom',
							'`$interval.cancel()` called with a promise that was not generated by `$interval()`.');
					}

					if (!intervals.hasOwnProperty(promise.$$intervalId)) return false;

					var id = promise.$$intervalId;
					var deferred = intervals[id];

					// Interval cancels should not report an unhandled promise.
					markQExceptionHandled(deferred.promise);
					deferred.reject('canceled');
					clearIntervalFn(id);

					return true;
				};

				return interval;
			}];
	}

	/** @this */
	function $$IntervalFactoryProvider() {
		this.$get = ['$browser', '$q', '$$q', '$rootScope',
			function ($browser, $q, $$q, $rootScope) {
				return function intervalFactory(setIntervalFn, clearIntervalFn) {
					return function intervalFn(fn, delay, count, invokeApply) {
						var hasParams = arguments.length > 4,
							args = hasParams ? sliceArgs(arguments, 4) : [],
							iteration = 0,
							skipApply = isDefined(invokeApply) && !invokeApply,
							deferred = (skipApply ? $$q : $q).defer(),
							promise = deferred.promise;

						count = isDefined(count) ? count : 0;

						function callback() {
							if (!hasParams) {
								fn(iteration);
							} else {
								fn.apply(null, args);
							}
						}

						function tick() {
							if (skipApply) {
								$browser.defer(callback);
							} else {
								$rootScope.$evalAsync(callback);
							}
							deferred.notify(iteration++);

							if (count > 0 && iteration >= count) {
								deferred.resolve(iteration);
								clearIntervalFn(promise.$$intervalId);
							}

							if (!skipApply) $rootScope.$apply();
						}

						promise.$$intervalId = setIntervalFn(tick, delay, deferred, skipApply);

						return promise;
					};
				};
			}];
	}

	/**
	 * @ngdoc service
	 * @name $jsonpCallbacks
	 * @requires $window
	 * @description
	 * This service handles the lifecycle of callbacks to handle JSONP requests.
	 * Override this service if you wish to customise where the callbacks are stored and
	 * how they vary compared to the requested url.
	 */
	var $jsonpCallbacksProvider = /** @this */ function () {
		this.$get = function () {
			var callbacks = angular.callbacks;
			var callbackMap = {};

			function createCallback(callbackId) {
				var callback = function (data) {
					callback.data = data;
					callback.called = true;
				};
				callback.id = callbackId;
				return callback;
			}

			return {
				/**
				 * @ngdoc method
				 * @name $jsonpCallbacks#createCallback
				 * @param {string} url the url of the JSONP request
				 * @returns {string} the callback path to send to the server as part of the JSONP request
				 * @description
				 * {@link $httpBackend} calls this method to create a callback and get hold of the path to the callback
				 * to pass to the server, which will be used to call the callback with its payload in the JSONP response.
				 */
				createCallback: function (url) {
					var callbackId = '_' + (callbacks.$$counter++).toString(36);
					var callbackPath = 'angular.callbacks.' + callbackId;
					var callback = createCallback(callbackId);
					callbackMap[callbackPath] = callbacks[callbackId] = callback;
					return callbackPath;
				},
				/**
				 * @ngdoc method
				 * @name $jsonpCallbacks#wasCalled
				 * @param {string} callbackPath the path to the callback that was sent in the JSONP request
				 * @returns {boolean} whether the callback has been called, as a result of the JSONP response
				 * @description
				 * {@link $httpBackend} calls this method to find out whether the JSONP response actually called the
				 * callback that was passed in the request.
				 */
				wasCalled: function (callbackPath) {
					return callbackMap[callbackPath].called;
				},
				/**
				 * @ngdoc method
				 * @name $jsonpCallbacks#getResponse
				 * @param {string} callbackPath the path to the callback that was sent in the JSONP request
				 * @returns {*} the data received from the response via the registered callback
				 * @description
				 * {@link $httpBackend} calls this method to get hold of the data that was provided to the callback
				 * in the JSONP response.
				 */
				getResponse: function (callbackPath) {
					return callbackMap[callbackPath].data;
				},
				/**
				 * @ngdoc method
				 * @name $jsonpCallbacks#removeCallback
				 * @param {string} callbackPath the path to the callback that was sent in the JSONP request
				 * @description
				 * {@link $httpBackend} calls this method to remove the callback after the JSONP request has
				 * completed or timed-out.
				 */
				removeCallback: function (callbackPath) {
					var callback = callbackMap[callbackPath];
					delete callbacks[callback.id];
					delete callbackMap[callbackPath];
				}
			};
		};
	};

	/**
	 * @ngdoc service
	 * @name $locale
	 *
	 * @description
	 * $locale service provides localization rules for various AngularJS components. As of right now the
	 * only public api is:
	 *
	 * * `id` – `{string}` – locale id formatted as `languageId-countryId` (e.g. `en-us`)
	 */

	/* global stripHash: true */

	var PATH_MATCH = /^([^?#]*)(\?([^#]*))?(#(.*))?$/,
		DEFAULT_PORTS = { 'http': 80, 'https': 443, 'ftp': 21 };
	var $locationMinErr = minErr('$location');


	/**
	 * Encode path using encodeUriSegment, ignoring forward slashes
	 *
	 * @param {string} path Path to encode
	 * @returns {string}
	 */
	function encodePath(path) {
		var segments = path.split('/'),
			i = segments.length;

		while (i--) {
			// decode forward slashes to prevent them from being double encoded
			segments[i] = encodeUriSegment(segments[i].replace(/%2F/g, '/'));
		}

		return segments.join('/');
	}

	function decodePath(path, html5Mode) {
		var segments = path.split('/'),
			i = segments.length;

		while (i--) {
			segments[i] = decodeURIComponent(segments[i]);
			if (html5Mode) {
				// encode forward slashes to prevent them from being mistaken for path separators
				segments[i] = segments[i].replace(/\//g, '%2F');
			}
		}

		return segments.join('/');
	}

	function normalizePath(pathValue, searchValue, hashValue) {
		var search = toKeyValue(searchValue),
			hash = hashValue ? '#' + encodeUriSegment(hashValue) : '',
			path = encodePath(pathValue);

		return path + (search ? '?' + search : '') + hash;
	}

	function parseAbsoluteUrl(absoluteUrl, locationObj) {
		var parsedUrl = urlResolve(absoluteUrl);

		locationObj.$$protocol = parsedUrl.protocol;
		locationObj.$$host = parsedUrl.hostname;
		locationObj.$$port = toInt(parsedUrl.port) || DEFAULT_PORTS[parsedUrl.protocol] || null;
	}

	var DOUBLE_SLASH_REGEX = /^\s*[\\/]{2,}/;
	function parseAppUrl(url, locationObj, html5Mode) {

		if (DOUBLE_SLASH_REGEX.test(url)) {
			throw $locationMinErr('badpath', 'Invalid url "{0}".', url);
		}

		var prefixed = (url.charAt(0) !== '/');
		if (prefixed) {
			url = '/' + url;
		}
		var match = urlResolve(url);
		var path = prefixed && match.pathname.charAt(0) === '/' ? match.pathname.substring(1) : match.pathname;
		locationObj.$$path = decodePath(path, html5Mode);
		locationObj.$$search = parseKeyValue(match.search);
		locationObj.$$hash = decodeURIComponent(match.hash);

		// make sure path starts with '/';
		if (locationObj.$$path && locationObj.$$path.charAt(0) !== '/') {
			locationObj.$$path = '/' + locationObj.$$path;
		}
	}

	function startsWith(str, search) {
		return str.slice(0, search.length) === search;
	}

	/**
	 *
	 * @param {string} base
	 * @param {string} url
	 * @returns {string} returns text from `url` after `base` or `undefined` if it does not begin with
	 *                   the expected string.
	 */
	function stripBaseUrl(base, url) {
		if (startsWith(url, base)) {
			return url.substr(base.length);
		}
	}

	function stripHash(url) {
		var index = url.indexOf('#');
		return index === -1 ? url : url.substr(0, index);
	}

	function stripFile(url) {
		return url.substr(0, stripHash(url).lastIndexOf('/') + 1);
	}

	/* return the server only (scheme://host:port) */
	function serverBase(url) {
		return url.substring(0, url.indexOf('/', url.indexOf('//') + 2));
	}


	/**
	 * LocationHtml5Url represents a URL
	 * This object is exposed as $location service when HTML5 mode is enabled and supported
	 *
	 * @constructor
	 * @param {string} appBase application base URL
	 * @param {string} appBaseNoFile application base URL stripped of any filename
	 * @param {string} basePrefix URL path prefix
	 */
	function LocationHtml5Url(appBase, appBaseNoFile, basePrefix) {
		this.$$html5 = true;
		basePrefix = basePrefix || '';
		parseAbsoluteUrl(appBase, this);


		/**
		 * Parse given HTML5 (regular) URL string into properties
		 * @param {string} url HTML5 URL
		 * @private
		 */
		this.$$parse = function (url) {
			var pathUrl = stripBaseUrl(appBaseNoFile, url);
			if (!isString(pathUrl)) {
				throw $locationMinErr('ipthprfx', 'Invalid url "{0}", missing path prefix "{1}".', url,
					appBaseNoFile);
			}

			parseAppUrl(pathUrl, this, true);

			if (!this.$$path) {
				this.$$path = '/';
			}

			this.$$compose();
		};

		this.$$normalizeUrl = function (url) {
			return appBaseNoFile + url.substr(1); // first char is always '/'
		};

		this.$$parseLinkUrl = function (url, relHref) {
			if (relHref && relHref[0] === '#') {
				// special case for links to hash fragments:
				// keep the old url and only replace the hash fragment
				this.hash(relHref.slice(1));
				return true;
			}
			var appUrl, prevAppUrl;
			var rewrittenUrl;


			if (isDefined(appUrl = stripBaseUrl(appBase, url))) {
				prevAppUrl = appUrl;
				if (basePrefix && isDefined(appUrl = stripBaseUrl(basePrefix, appUrl))) {
					rewrittenUrl = appBaseNoFile + (stripBaseUrl('/', appUrl) || appUrl);
				} else {
					rewrittenUrl = appBase + prevAppUrl;
				}
			} else if (isDefined(appUrl = stripBaseUrl(appBaseNoFile, url))) {
				rewrittenUrl = appBaseNoFile + appUrl;
			} else if (appBaseNoFile === url + '/') {
				rewrittenUrl = appBaseNoFile;
			}
			if (rewrittenUrl) {
				this.$$parse(rewrittenUrl);
			}
			return !!rewrittenUrl;
		};
	}


	/**
	 * LocationHashbangUrl represents URL
	 * This object is exposed as $location service when developer doesn't opt into html5 mode.
	 * It also serves as the base class for html5 mode fallback on legacy browsers.
	 *
	 * @constructor
	 * @param {string} appBase application base URL
	 * @param {string} appBaseNoFile application base URL stripped of any filename
	 * @param {string} hashPrefix hashbang prefix
	 */
	function LocationHashbangUrl(appBase, appBaseNoFile, hashPrefix) {

		parseAbsoluteUrl(appBase, this);


		/**
		 * Parse given hashbang URL into properties
		 * @param {string} url Hashbang URL
		 * @private
		 */
		this.$$parse = function (url) {
			var withoutBaseUrl = stripBaseUrl(appBase, url) || stripBaseUrl(appBaseNoFile, url);
			var withoutHashUrl;

			if (!isUndefined(withoutBaseUrl) && withoutBaseUrl.charAt(0) === '#') {

				// The rest of the URL starts with a hash so we have
				// got either a hashbang path or a plain hash fragment
				withoutHashUrl = stripBaseUrl(hashPrefix, withoutBaseUrl);
				if (isUndefined(withoutHashUrl)) {
					// There was no hashbang prefix so we just have a hash fragment
					withoutHashUrl = withoutBaseUrl;
				}

			} else {
				// There was no hashbang path nor hash fragment:
				// If we are in HTML5 mode we use what is left as the path;
				// Otherwise we ignore what is left
				if (this.$$html5) {
					withoutHashUrl = withoutBaseUrl;
				} else {
					withoutHashUrl = '';
					if (isUndefined(withoutBaseUrl)) {
						appBase = url;
          /** @type {?} */ (this).replace();
					}
				}
			}

			parseAppUrl(withoutHashUrl, this, false);

			this.$$path = removeWindowsDriveName(this.$$path, withoutHashUrl, appBase);

			this.$$compose();

			/*
			 * In Windows, on an anchor node on documents loaded from
			 * the filesystem, the browser will return a pathname
			 * prefixed with the drive name ('/C:/path') when a
			 * pathname without a drive is set:
			 *  * a.setAttribute('href', '/foo')
			 *   * a.pathname === '/C:/foo' //true
			 *
			 * Inside of AngularJS, we're always using pathnames that
			 * do not include drive names for routing.
			 */
			function removeWindowsDriveName(path, url, base) {
				/*
				Matches paths for file protocol on windows,
				such as /C:/foo/bar, and captures only /foo/bar.
				*/
				var windowsFilePathExp = /^\/[A-Z]:(\/.*)/;

				var firstPathSegmentMatch;

				//Get the relative path from the input URL.
				if (startsWith(url, base)) {
					url = url.replace(base, '');
				}

				// The input URL intentionally contains a first path segment that ends with a colon.
				if (windowsFilePathExp.exec(url)) {
					return path;
				}

				firstPathSegmentMatch = windowsFilePathExp.exec(path);
				return firstPathSegmentMatch ? firstPathSegmentMatch[1] : path;
			}
		};

		this.$$normalizeUrl = function (url) {
			return appBase + (url ? hashPrefix + url : '');
		};

		this.$$parseLinkUrl = function (url, relHref) {
			if (stripHash(appBase) === stripHash(url)) {
				this.$$parse(url);
				return true;
			}
			return false;
		};
	}


	/**
	 * LocationHashbangUrl represents URL
	 * This object is exposed as $location service when html5 history api is enabled but the browser
	 * does not support it.
	 *
	 * @constructor
	 * @param {string} appBase application base URL
	 * @param {string} appBaseNoFile application base URL stripped of any filename
	 * @param {string} hashPrefix hashbang prefix
	 */
	function LocationHashbangInHtml5Url(appBase, appBaseNoFile, hashPrefix) {
		this.$$html5 = true;
		LocationHashbangUrl.apply(this, arguments);

		this.$$parseLinkUrl = function (url, relHref) {
			if (relHref && relHref[0] === '#') {
				// special case for links to hash fragments:
				// keep the old url and only replace the hash fragment
				this.hash(relHref.slice(1));
				return true;
			}

			var rewrittenUrl;
			var appUrl;

			if (appBase === stripHash(url)) {
				rewrittenUrl = url;
			} else if ((appUrl = stripBaseUrl(appBaseNoFile, url))) {
				rewrittenUrl = appBase + hashPrefix + appUrl;
			} else if (appBaseNoFile === url + '/') {
				rewrittenUrl = appBaseNoFile;
			}
			if (rewrittenUrl) {
				this.$$parse(rewrittenUrl);
			}
			return !!rewrittenUrl;
		};

		this.$$normalizeUrl = function (url) {
			// include hashPrefix in $$absUrl when $$url is empty so IE9 does not reload page because of removal of '#'
			return appBase + hashPrefix + url;
		};
	}


	var locationPrototype = {

		/**
		 * Ensure absolute URL is initialized.
		 * @private
		 */
		$$absUrl: '',

		/**
		 * Are we in html5 mode?
		 * @private
		 */
		$$html5: false,

		/**
		 * Has any change been replacing?
		 * @private
		 */
		$$replace: false,

		/**
		 * Compose url and update `url` and `absUrl` property
		 * @private
		 */
		$$compose: function () {
			this.$$url = normalizePath(this.$$path, this.$$search, this.$$hash);
			this.$$absUrl = this.$$normalizeUrl(this.$$url);
			this.$$urlUpdatedByLocation = true;
		},

		/**
		 * @ngdoc method
		 * @name $location#absUrl
		 *
		 * @description
		 * This method is getter only.
		 *
		 * Return full URL representation with all segments encoded according to rules specified in
		 * [RFC 3986](http://www.ietf.org/rfc/rfc3986.txt).
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var absUrl = $location.absUrl();
		 * // => "http://example.com/#/some/path?foo=bar&baz=xoxo"
		 * ```
		 *
		 * @return {string} full URL
		 */
		absUrl: locationGetter('$$absUrl'),

		/**
		 * @ngdoc method
		 * @name $location#url
		 *
		 * @description
		 * This method is getter / setter.
		 *
		 * Return URL (e.g. `/path?a=b#hash`) when called without any parameter.
		 *
		 * Change path, search and hash, when called with parameter and return `$location`.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var url = $location.url();
		 * // => "/some/path?foo=bar&baz=xoxo"
		 * ```
		 *
		 * @param {string=} url New URL without base prefix (e.g. `/path?a=b#hash`)
		 * @return {string} url
		 */
		url: function (url) {
			if (isUndefined(url)) {
				return this.$$url;
			}

			var match = PATH_MATCH.exec(url);
			if (match[1] || url === '') this.path(decodeURIComponent(match[1]));
			if (match[2] || match[1] || url === '') this.search(match[3] || '');
			this.hash(match[5] || '');

			return this;
		},

		/**
		 * @ngdoc method
		 * @name $location#protocol
		 *
		 * @description
		 * This method is getter only.
		 *
		 * Return protocol of current URL.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var protocol = $location.protocol();
		 * // => "http"
		 * ```
		 *
		 * @return {string} protocol of current URL
		 */
		protocol: locationGetter('$$protocol'),

		/**
		 * @ngdoc method
		 * @name $location#host
		 *
		 * @description
		 * This method is getter only.
		 *
		 * Return host of current URL.
		 *
		 * Note: compared to the non-AngularJS version `location.host` which returns `hostname:port`, this returns the `hostname` portion only.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var host = $location.host();
		 * // => "example.com"
		 *
		 * // given URL http://user:password@example.com:8080/#/some/path?foo=bar&baz=xoxo
		 * host = $location.host();
		 * // => "example.com"
		 * host = location.host;
		 * // => "example.com:8080"
		 * ```
		 *
		 * @return {string} host of current URL.
		 */
		host: locationGetter('$$host'),

		/**
		 * @ngdoc method
		 * @name $location#port
		 *
		 * @description
		 * This method is getter only.
		 *
		 * Return port of current URL.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var port = $location.port();
		 * // => 80
		 * ```
		 *
		 * @return {Number} port
		 */
		port: locationGetter('$$port'),

		/**
		 * @ngdoc method
		 * @name $location#path
		 *
		 * @description
		 * This method is getter / setter.
		 *
		 * Return path of current URL when called without any parameter.
		 *
		 * Change path when called with parameter and return `$location`.
		 *
		 * Note: Path should always begin with forward slash (/), this method will add the forward slash
		 * if it is missing.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var path = $location.path();
		 * // => "/some/path"
		 * ```
		 *
		 * @param {(string|number)=} path New path
		 * @return {(string|object)} path if called with no parameters, or `$location` if called with a parameter
		 */
		path: locationGetterSetter('$$path', function (path) {
			path = path !== null ? path.toString() : '';
			return path.charAt(0) === '/' ? path : '/' + path;
		}),

		/**
		 * @ngdoc method
		 * @name $location#search
		 *
		 * @description
		 * This method is getter / setter.
		 *
		 * Return search part (as object) of current URL when called without any parameter.
		 *
		 * Change search part when called with parameter and return `$location`.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
		 * var searchObject = $location.search();
		 * // => {foo: 'bar', baz: 'xoxo'}
		 *
		 * // set foo to 'yipee'
		 * $location.search('foo', 'yipee');
		 * // $location.search() => {foo: 'yipee', baz: 'xoxo'}
		 * ```
		 *
		 * @param {string|Object.<string>|Object.<Array.<string>>} search New search params - string or
		 * hash object.
		 *
		 * When called with a single argument the method acts as a setter, setting the `search` component
		 * of `$location` to the specified value.
		 *
		 * If the argument is a hash object containing an array of values, these values will be encoded
		 * as duplicate search parameters in the URL.
		 *
		 * @param {(string|Number|Array<string>|boolean)=} paramValue If `search` is a string or number, then `paramValue`
		 * will override only a single search property.
		 *
		 * If `paramValue` is an array, it will override the property of the `search` component of
		 * `$location` specified via the first argument.
		 *
		 * If `paramValue` is `null`, the property specified via the first argument will be deleted.
		 *
		 * If `paramValue` is `true`, the property specified via the first argument will be added with no
		 * value nor trailing equal sign.
		 *
		 * @return {Object} If called with no arguments returns the parsed `search` object. If called with
		 * one or more arguments returns `$location` object itself.
		 */
		search: function (search, paramValue) {
			switch (arguments.length) {
				case 0:
					return this.$$search;
				case 1:
					if (isString(search) || isNumber(search)) {
						search = search.toString();
						this.$$search = parseKeyValue(search);
					} else if (isObject(search)) {
						search = copy(search, {});
						// remove object undefined or null properties
						forEach(search, function (value, key) {
							if (value == null) delete search[key];
						});

						this.$$search = search;
					} else {
						throw $locationMinErr('isrcharg',
							'The first argument of the `$location#search()` call must be a string or an object.');
					}
					break;
				default:
					if (isUndefined(paramValue) || paramValue === null) {
						delete this.$$search[search];
					} else {
						this.$$search[search] = paramValue;
					}
			}

			this.$$compose();
			return this;
		},

		/**
		 * @ngdoc method
		 * @name $location#hash
		 *
		 * @description
		 * This method is getter / setter.
		 *
		 * Returns the hash fragment when called without any parameters.
		 *
		 * Changes the hash fragment when called with a parameter and returns `$location`.
		 *
		 *
		 * ```js
		 * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo#hashValue
		 * var hash = $location.hash();
		 * // => "hashValue"
		 * ```
		 *
		 * @param {(string|number)=} hash New hash fragment
		 * @return {string} hash
		 */
		hash: locationGetterSetter('$$hash', function (hash) {
			return hash !== null ? hash.toString() : '';
		}),

		/**
		 * @ngdoc method
		 * @name $location#replace
		 *
		 * @description
		 * If called, all changes to $location during the current `$digest` will replace the current history
		 * record, instead of adding a new one.
		 */
		replace: function () {
			this.$$replace = true;
			return this;
		}
	};

	forEach([LocationHashbangInHtml5Url, LocationHashbangUrl, LocationHtml5Url], function (Location) {
		Location.prototype = Object.create(locationPrototype);

		/**
		 * @ngdoc method
		 * @name $location#state
		 *
		 * @description
		 * This method is getter / setter.
		 *
		 * Return the history state object when called without any parameter.
		 *
		 * Change the history state object when called with one parameter and return `$location`.
		 * The state object is later passed to `pushState` or `replaceState`.
		 *
		 * NOTE: This method is supported only in HTML5 mode and only in browsers supporting
		 * the HTML5 History API (i.e. methods `pushState` and `replaceState`). If you need to support
		 * older browsers (like IE9 or Android < 4.0), don't use this method.
		 *
		 * @param {object=} state State object for pushState or replaceState
		 * @return {object} state
		 */
		Location.prototype.state = function (state) {
			if (!arguments.length) {
				return this.$$state;
			}

			if (Location !== LocationHtml5Url || !this.$$html5) {
				throw $locationMinErr('nostate', 'History API state support is available only ' +
					'in HTML5 mode and only in browsers supporting HTML5 History API');
			}
			// The user might modify `stateObject` after invoking `$location.state(stateObject)`
			// but we're changing the $$state reference to $browser.state() during the $digest
			// so the modification window is narrow.
			this.$$state = isUndefined(state) ? null : state;
			this.$$urlUpdatedByLocation = true;

			return this;
		};
	});


	function locationGetter(property) {
		return /** @this */ function () {
			return this[property];
		};
	}


	function locationGetterSetter(property, preprocess) {
		return /** @this */ function (value) {
			if (isUndefined(value)) {
				return this[property];
			}

			this[property] = preprocess(value);
			this.$$compose();

			return this;
		};
	}


	/**
	 * @ngdoc service
	 * @name $location
	 *
	 * @requires $rootElement
	 *
	 * @description
	 * The $location service parses the URL in the browser address bar (based on the
	 * [window.location](https://developer.mozilla.org/en/window.location)) and makes the URL
	 * available to your application. Changes to the URL in the address bar are reflected into
	 * $location service and changes to $location are reflected into the browser address bar.
	 *
	 * **The $location service:**
	 *
	 * - Exposes the current URL in the browser address bar, so you can
	 *   - Watch and observe the URL.
	 *   - Change the URL.
	 * - Synchronizes the URL with the browser when the user
	 *   - Changes the address bar.
	 *   - Clicks the back or forward button (or clicks a History link).
	 *   - Clicks on a link.
	 * - Represents the URL object as a set of methods (protocol, host, port, path, search, hash).
	 *
	 * For more information see {@link guide/$location Developer Guide: Using $location}
	 */

	/**
	 * @ngdoc provider
	 * @name $locationProvider
	 * @this
	 *
	 * @description
	 * Use the `$locationProvider` to configure how the application deep linking paths are stored.
	 */
	function $LocationProvider() {
		var hashPrefix = '!',
			html5Mode = {
				enabled: false,
				requireBase: true,
				rewriteLinks: true
			};

		/**
		 * @ngdoc method
		 * @name $locationProvider#hashPrefix
		 * @description
		 * The default value for the prefix is `'!'`.
		 * @param {string=} prefix Prefix for hash part (containing path and search)
		 * @returns {*} current value if used as getter or itself (chaining) if used as setter
		 */
		this.hashPrefix = function (prefix) {
			if (isDefined(prefix)) {
				hashPrefix = prefix;
				return this;
			} else {
				return hashPrefix;
			}
		};

		/**
		 * @ngdoc method
		 * @name $locationProvider#html5Mode
		 * @description
		 * @param {(boolean|Object)=} mode If boolean, sets `html5Mode.enabled` to value.
		 *   If object, sets `enabled`, `requireBase` and `rewriteLinks` to respective values. Supported
		 *   properties:
		 *   - **enabled** – `{boolean}` – (default: false) If true, will rely on `history.pushState` to
		 *     change urls where supported. Will fall back to hash-prefixed paths in browsers that do not
		 *     support `pushState`.
		 *   - **requireBase** - `{boolean}` - (default: `true`) When html5Mode is enabled, specifies
		 *     whether or not a <base> tag is required to be present. If `enabled` and `requireBase` are
		 *     true, and a base tag is not present, an error will be thrown when `$location` is injected.
		 *     See the {@link guide/$location $location guide for more information}
		 *   - **rewriteLinks** - `{boolean|string}` - (default: `true`) When html5Mode is enabled,
		 *     enables/disables URL rewriting for relative links. If set to a string, URL rewriting will
		 *     only happen on links with an attribute that matches the given string. For example, if set
		 *     to `'internal-link'`, then the URL will only be rewritten for `<a internal-link>` links.
		 *     Note that [attribute name normalization](guide/directive#normalization) does not apply
		 *     here, so `'internalLink'` will **not** match `'internal-link'`.
		 *
		 * @returns {Object} html5Mode object if used as getter or itself (chaining) if used as setter
		 */
		this.html5Mode = function (mode) {
			if (isBoolean(mode)) {
				html5Mode.enabled = mode;
				return this;
			} else if (isObject(mode)) {

				if (isBoolean(mode.enabled)) {
					html5Mode.enabled = mode.enabled;
				}

				if (isBoolean(mode.requireBase)) {
					html5Mode.requireBase = mode.requireBase;
				}

				if (isBoolean(mode.rewriteLinks) || isString(mode.rewriteLinks)) {
					html5Mode.rewriteLinks = mode.rewriteLinks;
				}

				return this;
			} else {
				return html5Mode;
			}
		};

		/**
		 * @ngdoc event
		 * @name $location#$locationChangeStart
		 * @eventType broadcast on root scope
		 * @description
		 * Broadcasted before a URL will change.
		 *
		 * This change can be prevented by calling
		 * `preventDefault` method of the event. See {@link ng.$rootScope.Scope#$on} for more
		 * details about event object. Upon successful change
		 * {@link ng.$location#$locationChangeSuccess $locationChangeSuccess} is fired.
		 *
		 * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
		 * the browser supports the HTML5 History API.
		 *
		 * @param {Object} angularEvent Synthetic event object.
		 * @param {string} newUrl New URL
		 * @param {string=} oldUrl URL that was before it was changed.
		 * @param {string=} newState New history state object
		 * @param {string=} oldState History state object that was before it was changed.
		 */

		/**
		 * @ngdoc event
		 * @name $location#$locationChangeSuccess
		 * @eventType broadcast on root scope
		 * @description
		 * Broadcasted after a URL was changed.
		 *
		 * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
		 * the browser supports the HTML5 History API.
		 *
		 * @param {Object} angularEvent Synthetic event object.
		 * @param {string} newUrl New URL
		 * @param {string=} oldUrl URL that was before it was changed.
		 * @param {string=} newState New history state object
		 * @param {string=} oldState History state object that was before it was changed.
		 */

		this.$get = ['$rootScope', '$browser', '$sniffer', '$rootElement', '$window',
			function ($rootScope, $browser, $sniffer, $rootElement, $window) {
				var $location,
					LocationMode,
					baseHref = $browser.baseHref(), // if base[href] is undefined, it defaults to ''
					initialUrl = $browser.url(),
					appBase;

				if (html5Mode.enabled) {
					if (!baseHref && html5Mode.requireBase) {
						throw $locationMinErr('nobase',
							'$location in HTML5 mode requires a <base> tag to be present!');
					}
					appBase = serverBase(initialUrl) + (baseHref || '/');
					LocationMode = $sniffer.history ? LocationHtml5Url : LocationHashbangInHtml5Url;
				} else {
					appBase = stripHash(initialUrl);
					LocationMode = LocationHashbangUrl;
				}
				var appBaseNoFile = stripFile(appBase);

				$location = new LocationMode(appBase, appBaseNoFile, '#' + hashPrefix);
				$location.$$parseLinkUrl(initialUrl, initialUrl);

				$location.$$state = $browser.state();

				var IGNORE_URI_REGEXP = /^\s*(javascript|mailto):/i;

				// Determine if two URLs are equal despite potentially having different encoding/normalizing
				//  such as $location.absUrl() vs $browser.url()
				// See https://github.com/angular/angular.js/issues/16592
				function urlsEqual(a, b) {
					return a === b || urlResolve(a).href === urlResolve(b).href;
				}

				function setBrowserUrlWithFallback(url, replace, state) {
					var oldUrl = $location.url();
					var oldState = $location.$$state;
					try {
						$browser.url(url, replace, state);

						// Make sure $location.state() returns referentially identical (not just deeply equal)
						// state object; this makes possible quick checking if the state changed in the digest
						// loop. Checking deep equality would be too expensive.
						$location.$$state = $browser.state();
					} catch (e) {
						// Restore old values if pushState fails
						$location.url(oldUrl);
						$location.$$state = oldState;

						throw e;
					}
				}

				$rootElement.on('click', function (event) {
					var rewriteLinks = html5Mode.rewriteLinks;
					// TODO(vojta): rewrite link when opening in new tab/window (in legacy browser)
					// currently we open nice url link and redirect then

					if (!rewriteLinks || event.ctrlKey || event.metaKey || event.shiftKey || event.which === 2 || event.button === 2) return;

					var elm = jqLite(event.target);

					// traverse the DOM up to find first A tag
					while (nodeName_(elm[0]) !== 'a') {
						// ignore rewriting if no A tag (reached root element, or no parent - removed from document)
						if (elm[0] === $rootElement[0] || !(elm = elm.parent())[0]) return;
					}

					if (isString(rewriteLinks) && isUndefined(elm.attr(rewriteLinks))) return;

					var absHref = elm.prop('href');
					// get the actual href attribute - see
					// http://msdn.microsoft.com/en-us/library/ie/dd347148(v=vs.85).aspx
					var relHref = elm.attr('href') || elm.attr('xlink:href');

					if (isObject(absHref) && absHref.toString() === '[object SVGAnimatedString]') {
						// SVGAnimatedString.animVal should be identical to SVGAnimatedString.baseVal, unless during
						// an animation.
						absHref = urlResolve(absHref.animVal).href;
					}

					// Ignore when url is started with javascript: or mailto:
					if (IGNORE_URI_REGEXP.test(absHref)) return;

					if (absHref && !elm.attr('target') && !event.isDefaultPrevented()) {
						if ($location.$$parseLinkUrl(absHref, relHref)) {
							// We do a preventDefault for all urls that are part of the AngularJS application,
							// in html5mode and also without, so that we are able to abort navigation without
							// getting double entries in the location history.
							event.preventDefault();
							// update location manually
							if ($location.absUrl() !== $browser.url()) {
								$rootScope.$apply();
							}
						}
					}
				});


				// rewrite hashbang url <> html5 url
				if ($location.absUrl() !== initialUrl) {
					$browser.url($location.absUrl(), true);
				}

				var initializing = true;

				// update $location when $browser url changes
				$browser.onUrlChange(function (newUrl, newState) {

					if (!startsWith(newUrl, appBaseNoFile)) {
						// If we are navigating outside of the app then force a reload
						$window.location.href = newUrl;
						return;
					}

					$rootScope.$evalAsync(function () {
						var oldUrl = $location.absUrl();
						var oldState = $location.$$state;
						var defaultPrevented;
						$location.$$parse(newUrl);
						$location.$$state = newState;

						defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
							newState, oldState).defaultPrevented;

						// if the location was changed by a `$locationChangeStart` handler then stop
						// processing this location change
						if ($location.absUrl() !== newUrl) return;

						if (defaultPrevented) {
							$location.$$parse(oldUrl);
							$location.$$state = oldState;
							setBrowserUrlWithFallback(oldUrl, false, oldState);
						} else {
							initializing = false;
							afterLocationChange(oldUrl, oldState);
						}
					});
					if (!$rootScope.$$phase) $rootScope.$digest();
				});

				// update browser
				$rootScope.$watch(function $locationWatch() {
					if (initializing || $location.$$urlUpdatedByLocation) {
						$location.$$urlUpdatedByLocation = false;

						var oldUrl = $browser.url();
						var newUrl = $location.absUrl();
						var oldState = $browser.state();
						var currentReplace = $location.$$replace;
						var urlOrStateChanged = !urlsEqual(oldUrl, newUrl) ||
							($location.$$html5 && $sniffer.history && oldState !== $location.$$state);

						if (initializing || urlOrStateChanged) {
							initializing = false;

							$rootScope.$evalAsync(function () {
								var newUrl = $location.absUrl();
								var defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
									$location.$$state, oldState).defaultPrevented;

								// if the location was changed by a `$locationChangeStart` handler then stop
								// processing this location change
								if ($location.absUrl() !== newUrl) return;

								if (defaultPrevented) {
									$location.$$parse(oldUrl);
									$location.$$state = oldState;
								} else {
									if (urlOrStateChanged) {
										setBrowserUrlWithFallback(newUrl, currentReplace,
											oldState === $location.$$state ? null : $location.$$state);
									}
									afterLocationChange(oldUrl, oldState);
								}
							});
						}
					}

					$location.$$replace = false;

					// we don't need to return anything because $evalAsync will make the digest loop dirty when
					// there is a change
				});

				return $location;

				function afterLocationChange(oldUrl, oldState) {
					$rootScope.$broadcast('$locationChangeSuccess', $location.absUrl(), oldUrl,
						$location.$$state, oldState);
				}
			}];
	}

	/**
	 * @ngdoc service
	 * @name $log
	 * @requires $window
	 *
	 * @description
	 * Simple service for logging. Default implementation safely writes the message
	 * into the browser's console (if present).
	 *
	 * The main purpose of this service is to simplify debugging and troubleshooting.
	 *
	 * To reveal the location of the calls to `$log` in the JavaScript console,
	 * you can "blackbox" the AngularJS source in your browser:
	 *
	 * [Mozilla description of blackboxing](https://developer.mozilla.org/en-US/docs/Tools/Debugger/How_to/Black_box_a_source).
	 * [Chrome description of blackboxing](https://developer.chrome.com/devtools/docs/blackboxing).
	 *
	 * Note: Not all browsers support blackboxing.
	 *
	 * The default is to log `debug` messages. You can use
	 * {@link ng.$logProvider ng.$logProvider#debugEnabled} to change this.
	 *
	 * @example
	   <example module="logExample" name="log-service">
		 <file name="script.js">
		   angular.module('logExample', [])
			 .controller('LogController', ['$scope', '$log', function($scope, $log) {
			   $scope.$log = $log;
			   $scope.message = 'Hello World!';
			 }]);
		 </file>
		 <file name="index.html">
		   <div ng-controller="LogController">
			 <p>Reload this page with open console, enter text and hit the log button...</p>
			 <label>Message:
			 <input type="text" ng-model="message" /></label>
			 <button ng-click="$log.log(message)">log</button>
			 <button ng-click="$log.warn(message)">warn</button>
			 <button ng-click="$log.info(message)">info</button>
			 <button ng-click="$log.error(message)">error</button>
			 <button ng-click="$log.debug(message)">debug</button>
		   </div>
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc provider
	 * @name $logProvider
	 * @this
	 *
	 * @description
	 * Use the `$logProvider` to configure how the application logs messages
	 */
	function $LogProvider() {
		var debug = true,
			self = this;

		/**
		 * @ngdoc method
		 * @name $logProvider#debugEnabled
		 * @description
		 * @param {boolean=} flag enable or disable debug level messages
		 * @returns {*} current value if used as getter or itself (chaining) if used as setter
		 */
		this.debugEnabled = function (flag) {
			if (isDefined(flag)) {
				debug = flag;
				return this;
			} else {
				return debug;
			}
		};

		this.$get = ['$window', function ($window) {
			// Support: IE 9-11, Edge 12-14+
			// IE/Edge display errors in such a way that it requires the user to click in 4 places
			// to see the stack trace. There is no way to feature-detect it so there's a chance
			// of the user agent sniffing to go wrong but since it's only about logging, this shouldn't
			// break apps. Other browsers display errors in a sensible way and some of them map stack
			// traces along source maps if available so it makes sense to let browsers display it
			// as they want.
			var formatStackTrace = msie || /\bEdge\//.test($window.navigator && $window.navigator.userAgent);

			return {
				/**
				 * @ngdoc method
				 * @name $log#log
				 *
				 * @description
				 * Write a log message
				 */
				log: consoleLog('log'),

				/**
				 * @ngdoc method
				 * @name $log#info
				 *
				 * @description
				 * Write an information message
				 */
				info: consoleLog('info'),

				/**
				 * @ngdoc method
				 * @name $log#warn
				 *
				 * @description
				 * Write a warning message
				 */
				warn: consoleLog('warn'),

				/**
				 * @ngdoc method
				 * @name $log#error
				 *
				 * @description
				 * Write an error message
				 */
				error: consoleLog('error'),

				/**
				 * @ngdoc method
				 * @name $log#debug
				 *
				 * @description
				 * Write a debug message
				 */
				debug: (function () {
					var fn = consoleLog('debug');

					return function () {
						if (debug) {
							fn.apply(self, arguments);
						}
					};
				})()
			};

			function formatError(arg) {
				if (isError(arg)) {
					if (arg.stack && formatStackTrace) {
						arg = (arg.message && arg.stack.indexOf(arg.message) === -1)
							? 'Error: ' + arg.message + '\n' + arg.stack
							: arg.stack;
					} else if (arg.sourceURL) {
						arg = arg.message + '\n' + arg.sourceURL + ':' + arg.line;
					}
				}
				return arg;
			}

			function consoleLog(type) {
				var console = $window.console || {},
					logFn = console[type] || console.log || noop;

				return function () {
					var args = [];
					forEach(arguments, function (arg) {
						args.push(formatError(arg));
					});
					// Support: IE 9 only
					// console methods don't inherit from Function.prototype in IE 9 so we can't
					// call `logFn.apply(console, args)` directly.
					return Function.prototype.apply.call(logFn, console, args);
				};
			}
		}];
	}

	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 *     Any commits to this file should be reviewed with security in mind.  *
	 *   Changes to this file can potentially create security vulnerabilities. *
	 *          An approval from 2 Core members with history of modifying      *
	 *                         this file is required.                          *
	 *                                                                         *
	 *  Does the change somehow allow for arbitrary javascript to be executed? *
	 *    Or allows for someone to change the prototype of built-in objects?   *
	 *     Or gives undesired access to variables likes document or window?    *
	 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

	var $parseMinErr = minErr('$parse');

	var objectValueOf = {}.constructor.prototype.valueOf;

	// Sandboxing AngularJS Expressions
	// ------------------------------
	// AngularJS expressions are no longer sandboxed. So it is now even easier to access arbitrary JS code by
	// various means such as obtaining a reference to native JS functions like the Function constructor.
	//
	// As an example, consider the following AngularJS expression:
	//
	//   {}.toString.constructor('alert("evil JS code")')
	//
	// It is important to realize that if you create an expression from a string that contains user provided
	// content then it is possible that your application contains a security vulnerability to an XSS style attack.
	//
	// See https://docs.angularjs.org/guide/security


	function getStringValue(name) {
		// Property names must be strings. This means that non-string objects cannot be used
		// as keys in an object. Any non-string object, including a number, is typecasted
		// into a string via the toString method.
		// -- MDN, https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Operators/Property_accessors#Property_names
		//
		// So, to ensure that we are checking the same `name` that JavaScript would use, we cast it
		// to a string. It's not always possible. If `name` is an object and its `toString` method is
		// 'broken' (doesn't return a string, isn't a function, etc.), an error will be thrown:
		//
		// TypeError: Cannot convert object to primitive value
		//
		// For performance reasons, we don't catch this error here and allow it to propagate up the call
		// stack. Note that you'll get the same error in JavaScript if you try to access a property using
		// such a 'broken' object as a key.
		return name + '';
	}


	var OPERATORS = createMap();
	forEach('+ - * / % === !== == != < > <= >= && || ! = |'.split(' '), function (operator) { OPERATORS[operator] = true; });
	var ESCAPE = { 'n': '\n', 'f': '\f', 'r': '\r', 't': '\t', 'v': '\v', '\'': '\'', '"': '"' };


	/////////////////////////////////////////


	/**
	 * @constructor
	 */
	var Lexer = function Lexer(options) {
		this.options = options;
	};

	Lexer.prototype = {
		constructor: Lexer,

		lex: function (text) {
			this.text = text;
			this.index = 0;
			this.tokens = [];

			while (this.index < this.text.length) {
				var ch = this.text.charAt(this.index);
				if (ch === '"' || ch === '\'') {
					this.readString(ch);
				} else if (this.isNumber(ch) || ch === '.' && this.isNumber(this.peek())) {
					this.readNumber();
				} else if (this.isIdentifierStart(this.peekMultichar())) {
					this.readIdent();
				} else if (this.is(ch, '(){}[].,;:?')) {
					this.tokens.push({ index: this.index, text: ch });
					this.index++;
				} else if (this.isWhitespace(ch)) {
					this.index++;
				} else {
					var ch2 = ch + this.peek();
					var ch3 = ch2 + this.peek(2);
					var op1 = OPERATORS[ch];
					var op2 = OPERATORS[ch2];
					var op3 = OPERATORS[ch3];
					if (op1 || op2 || op3) {
						var token = op3 ? ch3 : (op2 ? ch2 : ch);
						this.tokens.push({ index: this.index, text: token, operator: true });
						this.index += token.length;
					} else {
						this.throwError('Unexpected next character ', this.index, this.index + 1);
					}
				}
			}
			return this.tokens;
		},

		is: function (ch, chars) {
			return chars.indexOf(ch) !== -1;
		},

		peek: function (i) {
			var num = i || 1;
			return (this.index + num < this.text.length) ? this.text.charAt(this.index + num) : false;
		},

		isNumber: function (ch) {
			return ('0' <= ch && ch <= '9') && typeof ch === 'string';
		},

		isWhitespace: function (ch) {
			// IE treats non-breaking space as \u00A0
			return (ch === ' ' || ch === '\r' || ch === '\t' ||
				ch === '\n' || ch === '\v' || ch === '\u00A0');
		},

		isIdentifierStart: function (ch) {
			return this.options.isIdentifierStart ?
				this.options.isIdentifierStart(ch, this.codePointAt(ch)) :
				this.isValidIdentifierStart(ch);
		},

		isValidIdentifierStart: function (ch) {
			return ('a' <= ch && ch <= 'z' ||
				'A' <= ch && ch <= 'Z' ||
				'_' === ch || ch === '$');
		},

		isIdentifierContinue: function (ch) {
			return this.options.isIdentifierContinue ?
				this.options.isIdentifierContinue(ch, this.codePointAt(ch)) :
				this.isValidIdentifierContinue(ch);
		},

		isValidIdentifierContinue: function (ch, cp) {
			return this.isValidIdentifierStart(ch, cp) || this.isNumber(ch);
		},

		codePointAt: function (ch) {
			if (ch.length === 1) return ch.charCodeAt(0);
			// eslint-disable-next-line no-bitwise
			return (ch.charCodeAt(0) << 10) + ch.charCodeAt(1) - 0x35FDC00;
		},

		peekMultichar: function () {
			var ch = this.text.charAt(this.index);
			var peek = this.peek();
			if (!peek) {
				return ch;
			}
			var cp1 = ch.charCodeAt(0);
			var cp2 = peek.charCodeAt(0);
			if (cp1 >= 0xD800 && cp1 <= 0xDBFF && cp2 >= 0xDC00 && cp2 <= 0xDFFF) {
				return ch + peek;
			}
			return ch;
		},

		isExpOperator: function (ch) {
			return (ch === '-' || ch === '+' || this.isNumber(ch));
		},

		throwError: function (error, start, end) {
			end = end || this.index;
			var colStr = (isDefined(start)
				? 's ' + start + '-' + this.index + ' [' + this.text.substring(start, end) + ']'
				: ' ' + end);
			throw $parseMinErr('lexerr', 'Lexer Error: {0} at column{1} in expression [{2}].',
				error, colStr, this.text);
		},

		readNumber: function () {
			var number = '';
			var start = this.index;
			while (this.index < this.text.length) {
				var ch = lowercase(this.text.charAt(this.index));
				if (ch === '.' || this.isNumber(ch)) {
					number += ch;
				} else {
					var peekCh = this.peek();
					if (ch === 'e' && this.isExpOperator(peekCh)) {
						number += ch;
					} else if (this.isExpOperator(ch) &&
						peekCh && this.isNumber(peekCh) &&
						number.charAt(number.length - 1) === 'e') {
						number += ch;
					} else if (this.isExpOperator(ch) &&
						(!peekCh || !this.isNumber(peekCh)) &&
						number.charAt(number.length - 1) === 'e') {
						this.throwError('Invalid exponent');
					} else {
						break;
					}
				}
				this.index++;
			}
			this.tokens.push({
				index: start,
				text: number,
				constant: true,
				value: Number(number)
			});
		},

		readIdent: function () {
			var start = this.index;
			this.index += this.peekMultichar().length;
			while (this.index < this.text.length) {
				var ch = this.peekMultichar();
				if (!this.isIdentifierContinue(ch)) {
					break;
				}
				this.index += ch.length;
			}
			this.tokens.push({
				index: start,
				text: this.text.slice(start, this.index),
				identifier: true
			});
		},

		readString: function (quote) {
			var start = this.index;
			this.index++;
			var string = '';
			var rawString = quote;
			var escape = false;
			while (this.index < this.text.length) {
				var ch = this.text.charAt(this.index);
				rawString += ch;
				if (escape) {
					if (ch === 'u') {
						var hex = this.text.substring(this.index + 1, this.index + 5);
						if (!hex.match(/[\da-f]{4}/i)) {
							this.throwError('Invalid unicode escape [\\u' + hex + ']');
						}
						this.index += 4;
						string += String.fromCharCode(parseInt(hex, 16));
					} else {
						var rep = ESCAPE[ch];
						string = string + (rep || ch);
					}
					escape = false;
				} else if (ch === '\\') {
					escape = true;
				} else if (ch === quote) {
					this.index++;
					this.tokens.push({
						index: start,
						text: rawString,
						constant: true,
						value: string
					});
					return;
				} else {
					string += ch;
				}
				this.index++;
			}
			this.throwError('Unterminated quote', start);
		}
	};

	var AST = function AST(lexer, options) {
		this.lexer = lexer;
		this.options = options;
	};

	AST.Program = 'Program';
	AST.ExpressionStatement = 'ExpressionStatement';
	AST.AssignmentExpression = 'AssignmentExpression';
	AST.ConditionalExpression = 'ConditionalExpression';
	AST.LogicalExpression = 'LogicalExpression';
	AST.BinaryExpression = 'BinaryExpression';
	AST.UnaryExpression = 'UnaryExpression';
	AST.CallExpression = 'CallExpression';
	AST.MemberExpression = 'MemberExpression';
	AST.Identifier = 'Identifier';
	AST.Literal = 'Literal';
	AST.ArrayExpression = 'ArrayExpression';
	AST.Property = 'Property';
	AST.ObjectExpression = 'ObjectExpression';
	AST.ThisExpression = 'ThisExpression';
	AST.LocalsExpression = 'LocalsExpression';

	// Internal use only
	AST.NGValueParameter = 'NGValueParameter';

	AST.prototype = {
		ast: function (text) {
			this.text = text;
			this.tokens = this.lexer.lex(text);

			var value = this.program();

			if (this.tokens.length !== 0) {
				this.throwError('is an unexpected token', this.tokens[0]);
			}

			return value;
		},

		program: function () {
			var body = [];
			while (true) {
				if (this.tokens.length > 0 && !this.peek('}', ')', ';', ']'))
					body.push(this.expressionStatement());
				if (!this.expect(';')) {
					return { type: AST.Program, body: body };
				}
			}
		},

		expressionStatement: function () {
			return { type: AST.ExpressionStatement, expression: this.filterChain() };
		},

		filterChain: function () {
			var left = this.expression();
			while (this.expect('|')) {
				left = this.filter(left);
			}
			return left;
		},

		expression: function () {
			return this.assignment();
		},

		assignment: function () {
			var result = this.ternary();
			if (this.expect('=')) {
				if (!isAssignable(result)) {
					throw $parseMinErr('lval', 'Trying to assign a value to a non l-value');
				}

				result = { type: AST.AssignmentExpression, left: result, right: this.assignment(), operator: '=' };
			}
			return result;
		},

		ternary: function () {
			var test = this.logicalOR();
			var alternate;
			var consequent;
			if (this.expect('?')) {
				alternate = this.expression();
				if (this.consume(':')) {
					consequent = this.expression();
					return { type: AST.ConditionalExpression, test: test, alternate: alternate, consequent: consequent };
				}
			}
			return test;
		},

		logicalOR: function () {
			var left = this.logicalAND();
			while (this.expect('||')) {
				left = { type: AST.LogicalExpression, operator: '||', left: left, right: this.logicalAND() };
			}
			return left;
		},

		logicalAND: function () {
			var left = this.equality();
			while (this.expect('&&')) {
				left = { type: AST.LogicalExpression, operator: '&&', left: left, right: this.equality() };
			}
			return left;
		},

		equality: function () {
			var left = this.relational();
			var token;
			while ((token = this.expect('==', '!=', '===', '!=='))) {
				left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.relational() };
			}
			return left;
		},

		relational: function () {
			var left = this.additive();
			var token;
			while ((token = this.expect('<', '>', '<=', '>='))) {
				left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.additive() };
			}
			return left;
		},

		additive: function () {
			var left = this.multiplicative();
			var token;
			while ((token = this.expect('+', '-'))) {
				left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.multiplicative() };
			}
			return left;
		},

		multiplicative: function () {
			var left = this.unary();
			var token;
			while ((token = this.expect('*', '/', '%'))) {
				left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.unary() };
			}
			return left;
		},

		unary: function () {
			var token;
			if ((token = this.expect('+', '-', '!'))) {
				return { type: AST.UnaryExpression, operator: token.text, prefix: true, argument: this.unary() };
			} else {
				return this.primary();
			}
		},

		primary: function () {
			var primary;
			if (this.expect('(')) {
				primary = this.filterChain();
				this.consume(')');
			} else if (this.expect('[')) {
				primary = this.arrayDeclaration();
			} else if (this.expect('{')) {
				primary = this.object();
			} else if (this.selfReferential.hasOwnProperty(this.peek().text)) {
				primary = copy(this.selfReferential[this.consume().text]);
			} else if (this.options.literals.hasOwnProperty(this.peek().text)) {
				primary = { type: AST.Literal, value: this.options.literals[this.consume().text] };
			} else if (this.peek().identifier) {
				primary = this.identifier();
			} else if (this.peek().constant) {
				primary = this.constant();
			} else {
				this.throwError('not a primary expression', this.peek());
			}

			var next;
			while ((next = this.expect('(', '[', '.'))) {
				if (next.text === '(') {
					primary = { type: AST.CallExpression, callee: primary, arguments: this.parseArguments() };
					this.consume(')');
				} else if (next.text === '[') {
					primary = { type: AST.MemberExpression, object: primary, property: this.expression(), computed: true };
					this.consume(']');
				} else if (next.text === '.') {
					primary = { type: AST.MemberExpression, object: primary, property: this.identifier(), computed: false };
				} else {
					this.throwError('IMPOSSIBLE');
				}
			}
			return primary;
		},

		filter: function (baseExpression) {
			var args = [baseExpression];
			var result = { type: AST.CallExpression, callee: this.identifier(), arguments: args, filter: true };

			while (this.expect(':')) {
				args.push(this.expression());
			}

			return result;
		},

		parseArguments: function () {
			var args = [];
			if (this.peekToken().text !== ')') {
				do {
					args.push(this.filterChain());
				} while (this.expect(','));
			}
			return args;
		},

		identifier: function () {
			var token = this.consume();
			if (!token.identifier) {
				this.throwError('is not a valid identifier', token);
			}
			return { type: AST.Identifier, name: token.text };
		},

		constant: function () {
			// TODO check that it is a constant
			return { type: AST.Literal, value: this.consume().value };
		},

		arrayDeclaration: function () {
			var elements = [];
			if (this.peekToken().text !== ']') {
				do {
					if (this.peek(']')) {
						// Support trailing commas per ES5.1.
						break;
					}
					elements.push(this.expression());
				} while (this.expect(','));
			}
			this.consume(']');

			return { type: AST.ArrayExpression, elements: elements };
		},

		object: function () {
			var properties = [], property;
			if (this.peekToken().text !== '}') {
				do {
					if (this.peek('}')) {
						// Support trailing commas per ES5.1.
						break;
					}
					property = { type: AST.Property, kind: 'init' };
					if (this.peek().constant) {
						property.key = this.constant();
						property.computed = false;
						this.consume(':');
						property.value = this.expression();
					} else if (this.peek().identifier) {
						property.key = this.identifier();
						property.computed = false;
						if (this.peek(':')) {
							this.consume(':');
							property.value = this.expression();
						} else {
							property.value = property.key;
						}
					} else if (this.peek('[')) {
						this.consume('[');
						property.key = this.expression();
						this.consume(']');
						property.computed = true;
						this.consume(':');
						property.value = this.expression();
					} else {
						this.throwError('invalid key', this.peek());
					}
					properties.push(property);
				} while (this.expect(','));
			}
			this.consume('}');

			return { type: AST.ObjectExpression, properties: properties };
		},

		throwError: function (msg, token) {
			throw $parseMinErr('syntax',
				'Syntax Error: Token \'{0}\' {1} at column {2} of the expression [{3}] starting at [{4}].',
				token.text, msg, (token.index + 1), this.text, this.text.substring(token.index));
		},

		consume: function (e1) {
			if (this.tokens.length === 0) {
				throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
			}

			var token = this.expect(e1);
			if (!token) {
				this.throwError('is unexpected, expecting [' + e1 + ']', this.peek());
			}
			return token;
		},

		peekToken: function () {
			if (this.tokens.length === 0) {
				throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
			}
			return this.tokens[0];
		},

		peek: function (e1, e2, e3, e4) {
			return this.peekAhead(0, e1, e2, e3, e4);
		},

		peekAhead: function (i, e1, e2, e3, e4) {
			if (this.tokens.length > i) {
				var token = this.tokens[i];
				var t = token.text;
				if (t === e1 || t === e2 || t === e3 || t === e4 ||
					(!e1 && !e2 && !e3 && !e4)) {
					return token;
				}
			}
			return false;
		},

		expect: function (e1, e2, e3, e4) {
			var token = this.peek(e1, e2, e3, e4);
			if (token) {
				this.tokens.shift();
				return token;
			}
			return false;
		},

		selfReferential: {
			'this': { type: AST.ThisExpression },
			'$locals': { type: AST.LocalsExpression }
		}
	};

	function ifDefined(v, d) {
		return typeof v !== 'undefined' ? v : d;
	}

	function plusFn(l, r) {
		if (typeof l === 'undefined') return r;
		if (typeof r === 'undefined') return l;
		return l + r;
	}

	function isStateless($filter, filterName) {
		var fn = $filter(filterName);
		return !fn.$stateful;
	}

	var PURITY_ABSOLUTE = 1;
	var PURITY_RELATIVE = 2;

	// Detect nodes which could depend on non-shallow state of objects
	function isPure(node, parentIsPure) {
		switch (node.type) {
			// Computed members might invoke a stateful toString()
			case AST.MemberExpression:
				if (node.computed) {
					return false;
				}
				break;

			// Unary always convert to primative
			case AST.UnaryExpression:
				return PURITY_ABSOLUTE;

			// The binary + operator can invoke a stateful toString().
			case AST.BinaryExpression:
				return node.operator !== '+' ? PURITY_ABSOLUTE : false;

			// Functions / filters probably read state from within objects
			case AST.CallExpression:
				return false;
		}

		return (undefined === parentIsPure) ? PURITY_RELATIVE : parentIsPure;
	}

	function findConstantAndWatchExpressions(ast, $filter, parentIsPure) {
		var allConstants;
		var argsToWatch;
		var isStatelessFilter;

		var astIsPure = ast.isPure = isPure(ast, parentIsPure);

		switch (ast.type) {
			case AST.Program:
				allConstants = true;
				forEach(ast.body, function (expr) {
					findConstantAndWatchExpressions(expr.expression, $filter, astIsPure);
					allConstants = allConstants && expr.expression.constant;
				});
				ast.constant = allConstants;
				break;
			case AST.Literal:
				ast.constant = true;
				ast.toWatch = [];
				break;
			case AST.UnaryExpression:
				findConstantAndWatchExpressions(ast.argument, $filter, astIsPure);
				ast.constant = ast.argument.constant;
				ast.toWatch = ast.argument.toWatch;
				break;
			case AST.BinaryExpression:
				findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
				findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
				ast.constant = ast.left.constant && ast.right.constant;
				ast.toWatch = ast.left.toWatch.concat(ast.right.toWatch);
				break;
			case AST.LogicalExpression:
				findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
				findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
				ast.constant = ast.left.constant && ast.right.constant;
				ast.toWatch = ast.constant ? [] : [ast];
				break;
			case AST.ConditionalExpression:
				findConstantAndWatchExpressions(ast.test, $filter, astIsPure);
				findConstantAndWatchExpressions(ast.alternate, $filter, astIsPure);
				findConstantAndWatchExpressions(ast.consequent, $filter, astIsPure);
				ast.constant = ast.test.constant && ast.alternate.constant && ast.consequent.constant;
				ast.toWatch = ast.constant ? [] : [ast];
				break;
			case AST.Identifier:
				ast.constant = false;
				ast.toWatch = [ast];
				break;
			case AST.MemberExpression:
				findConstantAndWatchExpressions(ast.object, $filter, astIsPure);
				if (ast.computed) {
					findConstantAndWatchExpressions(ast.property, $filter, astIsPure);
				}
				ast.constant = ast.object.constant && (!ast.computed || ast.property.constant);
				ast.toWatch = ast.constant ? [] : [ast];
				break;
			case AST.CallExpression:
				isStatelessFilter = ast.filter ? isStateless($filter, ast.callee.name) : false;
				allConstants = isStatelessFilter;
				argsToWatch = [];
				forEach(ast.arguments, function (expr) {
					findConstantAndWatchExpressions(expr, $filter, astIsPure);
					allConstants = allConstants && expr.constant;
					argsToWatch.push.apply(argsToWatch, expr.toWatch);
				});
				ast.constant = allConstants;
				ast.toWatch = isStatelessFilter ? argsToWatch : [ast];
				break;
			case AST.AssignmentExpression:
				findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
				findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
				ast.constant = ast.left.constant && ast.right.constant;
				ast.toWatch = [ast];
				break;
			case AST.ArrayExpression:
				allConstants = true;
				argsToWatch = [];
				forEach(ast.elements, function (expr) {
					findConstantAndWatchExpressions(expr, $filter, astIsPure);
					allConstants = allConstants && expr.constant;
					argsToWatch.push.apply(argsToWatch, expr.toWatch);
				});
				ast.constant = allConstants;
				ast.toWatch = argsToWatch;
				break;
			case AST.ObjectExpression:
				allConstants = true;
				argsToWatch = [];
				forEach(ast.properties, function (property) {
					findConstantAndWatchExpressions(property.value, $filter, astIsPure);
					allConstants = allConstants && property.value.constant;
					argsToWatch.push.apply(argsToWatch, property.value.toWatch);
					if (property.computed) {
						//`{[key]: value}` implicitly does `key.toString()` which may be non-pure
						findConstantAndWatchExpressions(property.key, $filter, /*parentIsPure=*/false);
						allConstants = allConstants && property.key.constant;
						argsToWatch.push.apply(argsToWatch, property.key.toWatch);
					}
				});
				ast.constant = allConstants;
				ast.toWatch = argsToWatch;
				break;
			case AST.ThisExpression:
				ast.constant = false;
				ast.toWatch = [];
				break;
			case AST.LocalsExpression:
				ast.constant = false;
				ast.toWatch = [];
				break;
		}
	}

	function getInputs(body) {
		if (body.length !== 1) return;
		var lastExpression = body[0].expression;
		var candidate = lastExpression.toWatch;
		if (candidate.length !== 1) return candidate;
		return candidate[0] !== lastExpression ? candidate : undefined;
	}

	function isAssignable(ast) {
		return ast.type === AST.Identifier || ast.type === AST.MemberExpression;
	}

	function assignableAST(ast) {
		if (ast.body.length === 1 && isAssignable(ast.body[0].expression)) {
			return { type: AST.AssignmentExpression, left: ast.body[0].expression, right: { type: AST.NGValueParameter }, operator: '=' };
		}
	}

	function isLiteral(ast) {
		return ast.body.length === 0 ||
			ast.body.length === 1 && (
				ast.body[0].expression.type === AST.Literal ||
				ast.body[0].expression.type === AST.ArrayExpression ||
				ast.body[0].expression.type === AST.ObjectExpression);
	}

	function isConstant(ast) {
		return ast.constant;
	}

	function ASTCompiler($filter) {
		this.$filter = $filter;
	}

	ASTCompiler.prototype = {
		compile: function (ast) {
			var self = this;
			this.state = {
				nextId: 0,
				filters: {},
				fn: { vars: [], body: [], own: {} },
				assign: { vars: [], body: [], own: {} },
				inputs: []
			};
			findConstantAndWatchExpressions(ast, self.$filter);
			var extra = '';
			var assignable;
			this.stage = 'assign';
			if ((assignable = assignableAST(ast))) {
				this.state.computing = 'assign';
				var result = this.nextId();
				this.recurse(assignable, result);
				this.return_(result);
				extra = 'fn.assign=' + this.generateFunction('assign', 's,v,l');
			}
			var toWatch = getInputs(ast.body);
			self.stage = 'inputs';
			forEach(toWatch, function (watch, key) {
				var fnKey = 'fn' + key;
				self.state[fnKey] = { vars: [], body: [], own: {} };
				self.state.computing = fnKey;
				var intoId = self.nextId();
				self.recurse(watch, intoId);
				self.return_(intoId);
				self.state.inputs.push({ name: fnKey, isPure: watch.isPure });
				watch.watchId = key;
			});
			this.state.computing = 'fn';
			this.stage = 'main';
			this.recurse(ast);
			var fnString =
				// The build and minification steps remove the string "use strict" from the code, but this is done using a regex.
				// This is a workaround for this until we do a better job at only removing the prefix only when we should.
				'"' + this.USE + ' ' + this.STRICT + '";\n' +
				this.filterPrefix() +
				'var fn=' + this.generateFunction('fn', 's,l,a,i') +
				extra +
				this.watchFns() +
				'return fn;';

			// eslint-disable-next-line no-new-func
			var fn = (new Function('$filter',
				'getStringValue',
				'ifDefined',
				'plus',
				fnString))(
					this.$filter,
					getStringValue,
					ifDefined,
					plusFn);
			this.state = this.stage = undefined;
			return fn;
		},

		USE: 'use',

		STRICT: 'strict',

		watchFns: function () {
			var result = [];
			var inputs = this.state.inputs;
			var self = this;
			forEach(inputs, function (input) {
				result.push('var ' + input.name + '=' + self.generateFunction(input.name, 's'));
				if (input.isPure) {
					result.push(input.name, '.isPure=' + JSON.stringify(input.isPure) + ';');
				}
			});
			if (inputs.length) {
				result.push('fn.inputs=[' + inputs.map(function (i) { return i.name; }).join(',') + '];');
			}
			return result.join('');
		},

		generateFunction: function (name, params) {
			return 'function(' + params + '){' +
				this.varsPrefix(name) +
				this.body(name) +
				'};';
		},

		filterPrefix: function () {
			var parts = [];
			var self = this;
			forEach(this.state.filters, function (id, filter) {
				parts.push(id + '=$filter(' + self.escape(filter) + ')');
			});
			if (parts.length) return 'var ' + parts.join(',') + ';';
			return '';
		},

		varsPrefix: function (section) {
			return this.state[section].vars.length ? 'var ' + this.state[section].vars.join(',') + ';' : '';
		},

		body: function (section) {
			return this.state[section].body.join('');
		},

		recurse: function (ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
			var left, right, self = this, args, expression, computed;
			recursionFn = recursionFn || noop;
			if (!skipWatchIdCheck && isDefined(ast.watchId)) {
				intoId = intoId || this.nextId();
				this.if_('i',
					this.lazyAssign(intoId, this.computedMember('i', ast.watchId)),
					this.lazyRecurse(ast, intoId, nameId, recursionFn, create, true)
				);
				return;
			}
			switch (ast.type) {
				case AST.Program:
					forEach(ast.body, function (expression, pos) {
						self.recurse(expression.expression, undefined, undefined, function (expr) { right = expr; });
						if (pos !== ast.body.length - 1) {
							self.current().body.push(right, ';');
						} else {
							self.return_(right);
						}
					});
					break;
				case AST.Literal:
					expression = this.escape(ast.value);
					this.assign(intoId, expression);
					recursionFn(intoId || expression);
					break;
				case AST.UnaryExpression:
					this.recurse(ast.argument, undefined, undefined, function (expr) { right = expr; });
					expression = ast.operator + '(' + this.ifDefined(right, 0) + ')';
					this.assign(intoId, expression);
					recursionFn(expression);
					break;
				case AST.BinaryExpression:
					this.recurse(ast.left, undefined, undefined, function (expr) { left = expr; });
					this.recurse(ast.right, undefined, undefined, function (expr) { right = expr; });
					if (ast.operator === '+') {
						expression = this.plus(left, right);
					} else if (ast.operator === '-') {
						expression = this.ifDefined(left, 0) + ast.operator + this.ifDefined(right, 0);
					} else {
						expression = '(' + left + ')' + ast.operator + '(' + right + ')';
					}
					this.assign(intoId, expression);
					recursionFn(expression);
					break;
				case AST.LogicalExpression:
					intoId = intoId || this.nextId();
					self.recurse(ast.left, intoId);
					self.if_(ast.operator === '&&' ? intoId : self.not(intoId), self.lazyRecurse(ast.right, intoId));
					recursionFn(intoId);
					break;
				case AST.ConditionalExpression:
					intoId = intoId || this.nextId();
					self.recurse(ast.test, intoId);
					self.if_(intoId, self.lazyRecurse(ast.alternate, intoId), self.lazyRecurse(ast.consequent, intoId));
					recursionFn(intoId);
					break;
				case AST.Identifier:
					intoId = intoId || this.nextId();
					if (nameId) {
						nameId.context = self.stage === 'inputs' ? 's' : this.assign(this.nextId(), this.getHasOwnProperty('l', ast.name) + '?l:s');
						nameId.computed = false;
						nameId.name = ast.name;
					}
					self.if_(self.stage === 'inputs' || self.not(self.getHasOwnProperty('l', ast.name)),
						function () {
							self.if_(self.stage === 'inputs' || 's', function () {
								if (create && create !== 1) {
									self.if_(
										self.isNull(self.nonComputedMember('s', ast.name)),
										self.lazyAssign(self.nonComputedMember('s', ast.name), '{}'));
								}
								self.assign(intoId, self.nonComputedMember('s', ast.name));
							});
						}, intoId && self.lazyAssign(intoId, self.nonComputedMember('l', ast.name))
					);
					recursionFn(intoId);
					break;
				case AST.MemberExpression:
					left = nameId && (nameId.context = this.nextId()) || this.nextId();
					intoId = intoId || this.nextId();
					self.recurse(ast.object, left, undefined, function () {
						self.if_(self.notNull(left), function () {
							if (ast.computed) {
								right = self.nextId();
								self.recurse(ast.property, right);
								self.getStringValue(right);
								if (create && create !== 1) {
									self.if_(self.not(self.computedMember(left, right)), self.lazyAssign(self.computedMember(left, right), '{}'));
								}
								expression = self.computedMember(left, right);
								self.assign(intoId, expression);
								if (nameId) {
									nameId.computed = true;
									nameId.name = right;
								}
							} else {
								if (create && create !== 1) {
									self.if_(self.isNull(self.nonComputedMember(left, ast.property.name)), self.lazyAssign(self.nonComputedMember(left, ast.property.name), '{}'));
								}
								expression = self.nonComputedMember(left, ast.property.name);
								self.assign(intoId, expression);
								if (nameId) {
									nameId.computed = false;
									nameId.name = ast.property.name;
								}
							}
						}, function () {
							self.assign(intoId, 'undefined');
						});
						recursionFn(intoId);
					}, !!create);
					break;
				case AST.CallExpression:
					intoId = intoId || this.nextId();
					if (ast.filter) {
						right = self.filter(ast.callee.name);
						args = [];
						forEach(ast.arguments, function (expr) {
							var argument = self.nextId();
							self.recurse(expr, argument);
							args.push(argument);
						});
						expression = right + '(' + args.join(',') + ')';
						self.assign(intoId, expression);
						recursionFn(intoId);
					} else {
						right = self.nextId();
						left = {};
						args = [];
						self.recurse(ast.callee, right, left, function () {
							self.if_(self.notNull(right), function () {
								forEach(ast.arguments, function (expr) {
									self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function (argument) {
										args.push(argument);
									});
								});
								if (left.name) {
									expression = self.member(left.context, left.name, left.computed) + '(' + args.join(',') + ')';
								} else {
									expression = right + '(' + args.join(',') + ')';
								}
								self.assign(intoId, expression);
							}, function () {
								self.assign(intoId, 'undefined');
							});
							recursionFn(intoId);
						});
					}
					break;
				case AST.AssignmentExpression:
					right = this.nextId();
					left = {};
					this.recurse(ast.left, undefined, left, function () {
						self.if_(self.notNull(left.context), function () {
							self.recurse(ast.right, right);
							expression = self.member(left.context, left.name, left.computed) + ast.operator + right;
							self.assign(intoId, expression);
							recursionFn(intoId || expression);
						});
					}, 1);
					break;
				case AST.ArrayExpression:
					args = [];
					forEach(ast.elements, function (expr) {
						self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function (argument) {
							args.push(argument);
						});
					});
					expression = '[' + args.join(',') + ']';
					this.assign(intoId, expression);
					recursionFn(intoId || expression);
					break;
				case AST.ObjectExpression:
					args = [];
					computed = false;
					forEach(ast.properties, function (property) {
						if (property.computed) {
							computed = true;
						}
					});
					if (computed) {
						intoId = intoId || this.nextId();
						this.assign(intoId, '{}');
						forEach(ast.properties, function (property) {
							if (property.computed) {
								left = self.nextId();
								self.recurse(property.key, left);
							} else {
								left = property.key.type === AST.Identifier ?
									property.key.name :
									('' + property.key.value);
							}
							right = self.nextId();
							self.recurse(property.value, right);
							self.assign(self.member(intoId, left, property.computed), right);
						});
					} else {
						forEach(ast.properties, function (property) {
							self.recurse(property.value, ast.constant ? undefined : self.nextId(), undefined, function (expr) {
								args.push(self.escape(
									property.key.type === AST.Identifier ? property.key.name :
										('' + property.key.value)) +
									':' + expr);
							});
						});
						expression = '{' + args.join(',') + '}';
						this.assign(intoId, expression);
					}
					recursionFn(intoId || expression);
					break;
				case AST.ThisExpression:
					this.assign(intoId, 's');
					recursionFn(intoId || 's');
					break;
				case AST.LocalsExpression:
					this.assign(intoId, 'l');
					recursionFn(intoId || 'l');
					break;
				case AST.NGValueParameter:
					this.assign(intoId, 'v');
					recursionFn(intoId || 'v');
					break;
			}
		},

		getHasOwnProperty: function (element, property) {
			var key = element + '.' + property;
			var own = this.current().own;
			if (!own.hasOwnProperty(key)) {
				own[key] = this.nextId(false, element + '&&(' + this.escape(property) + ' in ' + element + ')');
			}
			return own[key];
		},

		assign: function (id, value) {
			if (!id) return;
			this.current().body.push(id, '=', value, ';');
			return id;
		},

		filter: function (filterName) {
			if (!this.state.filters.hasOwnProperty(filterName)) {
				this.state.filters[filterName] = this.nextId(true);
			}
			return this.state.filters[filterName];
		},

		ifDefined: function (id, defaultValue) {
			return 'ifDefined(' + id + ',' + this.escape(defaultValue) + ')';
		},

		plus: function (left, right) {
			return 'plus(' + left + ',' + right + ')';
		},

		return_: function (id) {
			this.current().body.push('return ', id, ';');
		},

		if_: function (test, alternate, consequent) {
			if (test === true) {
				alternate();
			} else {
				var body = this.current().body;
				body.push('if(', test, '){');
				alternate();
				body.push('}');
				if (consequent) {
					body.push('else{');
					consequent();
					body.push('}');
				}
			}
		},

		not: function (expression) {
			return '!(' + expression + ')';
		},

		isNull: function (expression) {
			return expression + '==null';
		},

		notNull: function (expression) {
			return expression + '!=null';
		},

		nonComputedMember: function (left, right) {
			var SAFE_IDENTIFIER = /^[$_a-zA-Z][$_a-zA-Z0-9]*$/;
			var UNSAFE_CHARACTERS = /[^$_a-zA-Z0-9]/g;
			if (SAFE_IDENTIFIER.test(right)) {
				return left + '.' + right;
			} else {
				return left + '["' + right.replace(UNSAFE_CHARACTERS, this.stringEscapeFn) + '"]';
			}
		},

		computedMember: function (left, right) {
			return left + '[' + right + ']';
		},

		member: function (left, right, computed) {
			if (computed) return this.computedMember(left, right);
			return this.nonComputedMember(left, right);
		},

		getStringValue: function (item) {
			this.assign(item, 'getStringValue(' + item + ')');
		},

		lazyRecurse: function (ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
			var self = this;
			return function () {
				self.recurse(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck);
			};
		},

		lazyAssign: function (id, value) {
			var self = this;
			return function () {
				self.assign(id, value);
			};
		},

		stringEscapeRegex: /[^ a-zA-Z0-9]/g,

		stringEscapeFn: function (c) {
			return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
		},

		escape: function (value) {
			if (isString(value)) return '\'' + value.replace(this.stringEscapeRegex, this.stringEscapeFn) + '\'';
			if (isNumber(value)) return value.toString();
			if (value === true) return 'true';
			if (value === false) return 'false';
			if (value === null) return 'null';
			if (typeof value === 'undefined') return 'undefined';

			throw $parseMinErr('esc', 'IMPOSSIBLE');
		},

		nextId: function (skip, init) {
			var id = 'v' + (this.state.nextId++);
			if (!skip) {
				this.current().vars.push(id + (init ? '=' + init : ''));
			}
			return id;
		},

		current: function () {
			return this.state[this.state.computing];
		}
	};


	function ASTInterpreter($filter) {
		this.$filter = $filter;
	}

	ASTInterpreter.prototype = {
		compile: function (ast) {
			var self = this;
			findConstantAndWatchExpressions(ast, self.$filter);
			var assignable;
			var assign;
			if ((assignable = assignableAST(ast))) {
				assign = this.recurse(assignable);
			}
			var toWatch = getInputs(ast.body);
			var inputs;
			if (toWatch) {
				inputs = [];
				forEach(toWatch, function (watch, key) {
					var input = self.recurse(watch);
					input.isPure = watch.isPure;
					watch.input = input;
					inputs.push(input);
					watch.watchId = key;
				});
			}
			var expressions = [];
			forEach(ast.body, function (expression) {
				expressions.push(self.recurse(expression.expression));
			});
			var fn = ast.body.length === 0 ? noop :
				ast.body.length === 1 ? expressions[0] :
					function (scope, locals) {
						var lastValue;
						forEach(expressions, function (exp) {
							lastValue = exp(scope, locals);
						});
						return lastValue;
					};
			if (assign) {
				fn.assign = function (scope, value, locals) {
					return assign(scope, locals, value);
				};
			}
			if (inputs) {
				fn.inputs = inputs;
			}
			return fn;
		},

		recurse: function (ast, context, create) {
			var left, right, self = this, args;
			if (ast.input) {
				return this.inputs(ast.input, ast.watchId);
			}
			switch (ast.type) {
				case AST.Literal:
					return this.value(ast.value, context);
				case AST.UnaryExpression:
					right = this.recurse(ast.argument);
					return this['unary' + ast.operator](right, context);
				case AST.BinaryExpression:
					left = this.recurse(ast.left);
					right = this.recurse(ast.right);
					return this['binary' + ast.operator](left, right, context);
				case AST.LogicalExpression:
					left = this.recurse(ast.left);
					right = this.recurse(ast.right);
					return this['binary' + ast.operator](left, right, context);
				case AST.ConditionalExpression:
					return this['ternary?:'](
						this.recurse(ast.test),
						this.recurse(ast.alternate),
						this.recurse(ast.consequent),
						context
					);
				case AST.Identifier:
					return self.identifier(ast.name, context, create);
				case AST.MemberExpression:
					left = this.recurse(ast.object, false, !!create);
					if (!ast.computed) {
						right = ast.property.name;
					}
					if (ast.computed) right = this.recurse(ast.property);
					return ast.computed ?
						this.computedMember(left, right, context, create) :
						this.nonComputedMember(left, right, context, create);
				case AST.CallExpression:
					args = [];
					forEach(ast.arguments, function (expr) {
						args.push(self.recurse(expr));
					});
					if (ast.filter) right = this.$filter(ast.callee.name);
					if (!ast.filter) right = this.recurse(ast.callee, true);
					return ast.filter ?
						function (scope, locals, assign, inputs) {
							var values = [];
							for (var i = 0; i < args.length; ++i) {
								values.push(args[i](scope, locals, assign, inputs));
							}
							var value = right.apply(undefined, values, inputs);
							return context ? { context: undefined, name: undefined, value: value } : value;
						} :
						function (scope, locals, assign, inputs) {
							var rhs = right(scope, locals, assign, inputs);
							var value;
							if (rhs.value != null) {
								var values = [];
								for (var i = 0; i < args.length; ++i) {
									values.push(args[i](scope, locals, assign, inputs));
								}
								value = rhs.value.apply(rhs.context, values);
							}
							return context ? { value: value } : value;
						};
				case AST.AssignmentExpression:
					left = this.recurse(ast.left, true, 1);
					right = this.recurse(ast.right);
					return function (scope, locals, assign, inputs) {
						var lhs = left(scope, locals, assign, inputs);
						var rhs = right(scope, locals, assign, inputs);
						lhs.context[lhs.name] = rhs;
						return context ? { value: rhs } : rhs;
					};
				case AST.ArrayExpression:
					args = [];
					forEach(ast.elements, function (expr) {
						args.push(self.recurse(expr));
					});
					return function (scope, locals, assign, inputs) {
						var value = [];
						for (var i = 0; i < args.length; ++i) {
							value.push(args[i](scope, locals, assign, inputs));
						}
						return context ? { value: value } : value;
					};
				case AST.ObjectExpression:
					args = [];
					forEach(ast.properties, function (property) {
						if (property.computed) {
							args.push({
								key: self.recurse(property.key),
								computed: true,
								value: self.recurse(property.value)
							});
						} else {
							args.push({
								key: property.key.type === AST.Identifier ?
									property.key.name :
									('' + property.key.value),
								computed: false,
								value: self.recurse(property.value)
							});
						}
					});
					return function (scope, locals, assign, inputs) {
						var value = {};
						for (var i = 0; i < args.length; ++i) {
							if (args[i].computed) {
								value[args[i].key(scope, locals, assign, inputs)] = args[i].value(scope, locals, assign, inputs);
							} else {
								value[args[i].key] = args[i].value(scope, locals, assign, inputs);
							}
						}
						return context ? { value: value } : value;
					};
				case AST.ThisExpression:
					return function (scope) {
						return context ? { value: scope } : scope;
					};
				case AST.LocalsExpression:
					return function (scope, locals) {
						return context ? { value: locals } : locals;
					};
				case AST.NGValueParameter:
					return function (scope, locals, assign) {
						return context ? { value: assign } : assign;
					};
			}
		},

		'unary+': function (argument, context) {
			return function (scope, locals, assign, inputs) {
				var arg = argument(scope, locals, assign, inputs);
				if (isDefined(arg)) {
					arg = +arg;
				} else {
					arg = 0;
				}
				return context ? { value: arg } : arg;
			};
		},
		'unary-': function (argument, context) {
			return function (scope, locals, assign, inputs) {
				var arg = argument(scope, locals, assign, inputs);
				if (isDefined(arg)) {
					arg = -arg;
				} else {
					arg = -0;
				}
				return context ? { value: arg } : arg;
			};
		},
		'unary!': function (argument, context) {
			return function (scope, locals, assign, inputs) {
				var arg = !argument(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary+': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var lhs = left(scope, locals, assign, inputs);
				var rhs = right(scope, locals, assign, inputs);
				var arg = plusFn(lhs, rhs);
				return context ? { value: arg } : arg;
			};
		},
		'binary-': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var lhs = left(scope, locals, assign, inputs);
				var rhs = right(scope, locals, assign, inputs);
				var arg = (isDefined(lhs) ? lhs : 0) - (isDefined(rhs) ? rhs : 0);
				return context ? { value: arg } : arg;
			};
		},
		'binary*': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) * right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary/': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) / right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary%': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) % right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary===': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) === right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary!==': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) !== right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary==': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				// eslint-disable-next-line eqeqeq
				var arg = left(scope, locals, assign, inputs) == right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary!=': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				// eslint-disable-next-line eqeqeq
				var arg = left(scope, locals, assign, inputs) != right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary<': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) < right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary>': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) > right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary<=': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) <= right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary>=': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) >= right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary&&': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) && right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'binary||': function (left, right, context) {
			return function (scope, locals, assign, inputs) {
				var arg = left(scope, locals, assign, inputs) || right(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		'ternary?:': function (test, alternate, consequent, context) {
			return function (scope, locals, assign, inputs) {
				var arg = test(scope, locals, assign, inputs) ? alternate(scope, locals, assign, inputs) : consequent(scope, locals, assign, inputs);
				return context ? { value: arg } : arg;
			};
		},
		value: function (value, context) {
			return function () { return context ? { context: undefined, name: undefined, value: value } : value; };
		},
		identifier: function (name, context, create) {
			return function (scope, locals, assign, inputs) {
				var base = locals && (name in locals) ? locals : scope;
				if (create && create !== 1 && base && base[name] == null) {
					base[name] = {};
				}
				var value = base ? base[name] : undefined;
				if (context) {
					return { context: base, name: name, value: value };
				} else {
					return value;
				}
			};
		},
		computedMember: function (left, right, context, create) {
			return function (scope, locals, assign, inputs) {
				var lhs = left(scope, locals, assign, inputs);
				var rhs;
				var value;
				if (lhs != null) {
					rhs = right(scope, locals, assign, inputs);
					rhs = getStringValue(rhs);
					if (create && create !== 1) {
						if (lhs && !(lhs[rhs])) {
							lhs[rhs] = {};
						}
					}
					value = lhs[rhs];
				}
				if (context) {
					return { context: lhs, name: rhs, value: value };
				} else {
					return value;
				}
			};
		},
		nonComputedMember: function (left, right, context, create) {
			return function (scope, locals, assign, inputs) {
				var lhs = left(scope, locals, assign, inputs);
				if (create && create !== 1) {
					if (lhs && lhs[right] == null) {
						lhs[right] = {};
					}
				}
				var value = lhs != null ? lhs[right] : undefined;
				if (context) {
					return { context: lhs, name: right, value: value };
				} else {
					return value;
				}
			};
		},
		inputs: function (input, watchId) {
			return function (scope, value, locals, inputs) {
				if (inputs) return inputs[watchId];
				return input(scope, value, locals);
			};
		}
	};

	/**
	 * @constructor
	 */
	function Parser(lexer, $filter, options) {
		this.ast = new AST(lexer, options);
		this.astCompiler = options.csp ? new ASTInterpreter($filter) :
			new ASTCompiler($filter);
	}

	Parser.prototype = {
		constructor: Parser,

		parse: function (text) {
			var ast = this.getAst(text);
			var fn = this.astCompiler.compile(ast.ast);
			fn.literal = isLiteral(ast.ast);
			fn.constant = isConstant(ast.ast);
			fn.oneTime = ast.oneTime;
			return fn;
		},

		getAst: function (exp) {
			var oneTime = false;
			exp = exp.trim();

			if (exp.charAt(0) === ':' && exp.charAt(1) === ':') {
				oneTime = true;
				exp = exp.substring(2);
			}
			return {
				ast: this.ast.ast(exp),
				oneTime: oneTime
			};
		}
	};

	function getValueOf(value) {
		return isFunction(value.valueOf) ? value.valueOf() : objectValueOf.call(value);
	}

	///////////////////////////////////

	/**
	 * @ngdoc service
	 * @name $parse
	 * @kind function
	 *
	 * @description
	 *
	 * Converts AngularJS {@link guide/expression expression} into a function.
	 *
	 * ```js
	 *   var getter = $parse('user.name');
	 *   var setter = getter.assign;
	 *   var context = {user:{name:'AngularJS'}};
	 *   var locals = {user:{name:'local'}};
	 *
	 *   expect(getter(context)).toEqual('AngularJS');
	 *   setter(context, 'newValue');
	 *   expect(context.user.name).toEqual('newValue');
	 *   expect(getter(context, locals)).toEqual('local');
	 * ```
	 *
	 *
	 * @param {string} expression String expression to compile.
	 * @returns {function(context, locals)} a function which represents the compiled expression:
	 *
	 *    * `context` – `{object}` – an object against which any expressions embedded in the strings
	 *      are evaluated against (typically a scope object).
	 *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
	 *      `context`.
	 *
	 *    The returned function also has the following properties:
	 *      * `literal` – `{boolean}` – whether the expression's top-level node is a JavaScript
	 *        literal.
	 *      * `constant` – `{boolean}` – whether the expression is made entirely of JavaScript
	 *        constant literals.
	 *      * `assign` – `{?function(context, value)}` – if the expression is assignable, this will be
	 *        set to a function to change its value on the given context.
	 *
	 */


	/**
	 * @ngdoc provider
	 * @name $parseProvider
	 * @this
	 *
	 * @description
	 * `$parseProvider` can be used for configuring the default behavior of the {@link ng.$parse $parse}
	 *  service.
	 */
	function $ParseProvider() {
		var cache = createMap();
		var literals = {
			'true': true,
			'false': false,
			'null': null,
			'undefined': undefined
		};
		var identStart, identContinue;

		/**
		 * @ngdoc method
		 * @name $parseProvider#addLiteral
		 * @description
		 *
		 * Configure $parse service to add literal values that will be present as literal at expressions.
		 *
		 * @param {string} literalName Token for the literal value. The literal name value must be a valid literal name.
		 * @param {*} literalValue Value for this literal. All literal values must be primitives or `undefined`.
		 *
		 **/
		this.addLiteral = function (literalName, literalValue) {
			literals[literalName] = literalValue;
		};

		/**
		 * @ngdoc method
		 * @name $parseProvider#setIdentifierFns
		 *
		 * @description
		 *
		 * Allows defining the set of characters that are allowed in AngularJS expressions. The function
		 * `identifierStart` will get called to know if a given character is a valid character to be the
		 * first character for an identifier. The function `identifierContinue` will get called to know if
		 * a given character is a valid character to be a follow-up identifier character. The functions
		 * `identifierStart` and `identifierContinue` will receive as arguments the single character to be
		 * identifier and the character code point. These arguments will be `string` and `numeric`. Keep in
		 * mind that the `string` parameter can be two characters long depending on the character
		 * representation. It is expected for the function to return `true` or `false`, whether that
		 * character is allowed or not.
		 *
		 * Since this function will be called extensively, keep the implementation of these functions fast,
		 * as the performance of these functions have a direct impact on the expressions parsing speed.
		 *
		 * @param {function=} identifierStart The function that will decide whether the given character is
		 *   a valid identifier start character.
		 * @param {function=} identifierContinue The function that will decide whether the given character is
		 *   a valid identifier continue character.
		 */
		this.setIdentifierFns = function (identifierStart, identifierContinue) {
			identStart = identifierStart;
			identContinue = identifierContinue;
			return this;
		};

		this.$get = ['$filter', function ($filter) {
			var noUnsafeEval = csp().noUnsafeEval;
			var $parseOptions = {
				csp: noUnsafeEval,
				literals: copy(literals),
				isIdentifierStart: isFunction(identStart) && identStart,
				isIdentifierContinue: isFunction(identContinue) && identContinue
			};
			$parse.$$getAst = $$getAst;
			return $parse;

			function $parse(exp, interceptorFn) {
				var parsedExpression, cacheKey;

				switch (typeof exp) {
					case 'string':
						exp = exp.trim();
						cacheKey = exp;

						parsedExpression = cache[cacheKey];

						if (!parsedExpression) {
							var lexer = new Lexer($parseOptions);
							var parser = new Parser(lexer, $filter, $parseOptions);
							parsedExpression = parser.parse(exp);

							cache[cacheKey] = addWatchDelegate(parsedExpression);
						}
						return addInterceptor(parsedExpression, interceptorFn);

					case 'function':
						return addInterceptor(exp, interceptorFn);

					default:
						return addInterceptor(noop, interceptorFn);
				}
			}

			function $$getAst(exp) {
				var lexer = new Lexer($parseOptions);
				var parser = new Parser(lexer, $filter, $parseOptions);
				return parser.getAst(exp).ast;
			}

			function expressionInputDirtyCheck(newValue, oldValueOfValue, compareObjectIdentity) {

				if (newValue == null || oldValueOfValue == null) { // null/undefined
					return newValue === oldValueOfValue;
				}

				if (typeof newValue === 'object') {

					// attempt to convert the value to a primitive type
					// TODO(docs): add a note to docs that by implementing valueOf even objects and arrays can
					//             be cheaply dirty-checked
					newValue = getValueOf(newValue);

					if (typeof newValue === 'object' && !compareObjectIdentity) {
						// objects/arrays are not supported - deep-watching them would be too expensive
						return false;
					}

					// fall-through to the primitive equality check
				}

				//Primitive or NaN
				// eslint-disable-next-line no-self-compare
				return newValue === oldValueOfValue || (newValue !== newValue && oldValueOfValue !== oldValueOfValue);
			}

			function inputsWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
				var inputExpressions = parsedExpression.inputs;
				var lastResult;

				if (inputExpressions.length === 1) {
					var oldInputValueOf = expressionInputDirtyCheck; // init to something unique so that equals check fails
					inputExpressions = inputExpressions[0];
					return scope.$watch(function expressionInputWatch(scope) {
						var newInputValue = inputExpressions(scope);
						if (!expressionInputDirtyCheck(newInputValue, oldInputValueOf, inputExpressions.isPure)) {
							lastResult = parsedExpression(scope, undefined, undefined, [newInputValue]);
							oldInputValueOf = newInputValue && getValueOf(newInputValue);
						}
						return lastResult;
					}, listener, objectEquality, prettyPrintExpression);
				}

				var oldInputValueOfValues = [];
				var oldInputValues = [];
				for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
					oldInputValueOfValues[i] = expressionInputDirtyCheck; // init to something unique so that equals check fails
					oldInputValues[i] = null;
				}

				return scope.$watch(function expressionInputsWatch(scope) {
					var changed = false;

					for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
						var newInputValue = inputExpressions[i](scope);
						if (changed || (changed = !expressionInputDirtyCheck(newInputValue, oldInputValueOfValues[i], inputExpressions[i].isPure))) {
							oldInputValues[i] = newInputValue;
							oldInputValueOfValues[i] = newInputValue && getValueOf(newInputValue);
						}
					}

					if (changed) {
						lastResult = parsedExpression(scope, undefined, undefined, oldInputValues);
					}

					return lastResult;
				}, listener, objectEquality, prettyPrintExpression);
			}

			function oneTimeWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
				var isDone = parsedExpression.literal ? isAllDefined : isDefined;
				var unwatch, lastValue;

				var exp = parsedExpression.$$intercepted || parsedExpression;
				var post = parsedExpression.$$interceptor || identity;

				var useInputs = parsedExpression.inputs && !exp.inputs;

				// Propagate the literal/inputs/constant attributes
				// ... but not oneTime since we are handling it
				oneTimeWatch.literal = parsedExpression.literal;
				oneTimeWatch.constant = parsedExpression.constant;
				oneTimeWatch.inputs = parsedExpression.inputs;

				// Allow other delegates to run on this wrapped expression
				addWatchDelegate(oneTimeWatch);

				unwatch = scope.$watch(oneTimeWatch, listener, objectEquality, prettyPrintExpression);

				return unwatch;

				function unwatchIfDone() {
					if (isDone(lastValue)) {
						unwatch();
					}
				}

				function oneTimeWatch(scope, locals, assign, inputs) {
					lastValue = useInputs && inputs ? inputs[0] : exp(scope, locals, assign, inputs);
					if (isDone(lastValue)) {
						scope.$$postDigest(unwatchIfDone);
					}
					return post(lastValue);
				}
			}

			function isAllDefined(value) {
				var allDefined = true;
				forEach(value, function (val) {
					if (!isDefined(val)) allDefined = false;
				});
				return allDefined;
			}

			function constantWatchDelegate(scope, listener, objectEquality, parsedExpression) {
				var unwatch = scope.$watch(function constantWatch(scope) {
					unwatch();
					return parsedExpression(scope);
				}, listener, objectEquality);
				return unwatch;
			}

			function addWatchDelegate(parsedExpression) {
				if (parsedExpression.constant) {
					parsedExpression.$$watchDelegate = constantWatchDelegate;
				} else if (parsedExpression.oneTime) {
					parsedExpression.$$watchDelegate = oneTimeWatchDelegate;
				} else if (parsedExpression.inputs) {
					parsedExpression.$$watchDelegate = inputsWatchDelegate;
				}

				return parsedExpression;
			}

			function chainInterceptors(first, second) {
				function chainedInterceptor(value) {
					return second(first(value));
				}
				chainedInterceptor.$stateful = first.$stateful || second.$stateful;
				chainedInterceptor.$$pure = first.$$pure && second.$$pure;

				return chainedInterceptor;
			}

			function addInterceptor(parsedExpression, interceptorFn) {
				if (!interceptorFn) return parsedExpression;

				// Extract any existing interceptors out of the parsedExpression
				// to ensure the original parsedExpression is always the $$intercepted
				if (parsedExpression.$$interceptor) {
					interceptorFn = chainInterceptors(parsedExpression.$$interceptor, interceptorFn);
					parsedExpression = parsedExpression.$$intercepted;
				}

				var useInputs = false;

				var fn = function interceptedExpression(scope, locals, assign, inputs) {
					var value = useInputs && inputs ? inputs[0] : parsedExpression(scope, locals, assign, inputs);
					return interceptorFn(value);
				};

				// Maintain references to the interceptor/intercepted
				fn.$$intercepted = parsedExpression;
				fn.$$interceptor = interceptorFn;

				// Propagate the literal/oneTime/constant attributes
				fn.literal = parsedExpression.literal;
				fn.oneTime = parsedExpression.oneTime;
				fn.constant = parsedExpression.constant;

				// Treat the interceptor like filters.
				// If it is not $stateful then only watch its inputs.
				// If the expression itself has no inputs then use the full expression as an input.
				if (!interceptorFn.$stateful) {
					useInputs = !parsedExpression.inputs;
					fn.inputs = parsedExpression.inputs ? parsedExpression.inputs : [parsedExpression];

					if (!interceptorFn.$$pure) {
						fn.inputs = fn.inputs.map(function (e) {
							// Remove the isPure flag of inputs when it is not absolute because they are now wrapped in a
							// non-pure interceptor function.
							if (e.isPure === PURITY_RELATIVE) {
								return function depurifier(s) { return e(s); };
							}
							return e;
						});
					}
				}

				return addWatchDelegate(fn);
			}
		}];
	}

	/**
	 * @ngdoc service
	 * @name $q
	 * @requires $rootScope
	 *
	 * @description
	 * A service that helps you run functions asynchronously, and use their return values (or exceptions)
	 * when they are done processing.
	 *
	 * This is a [Promises/A+](https://promisesaplus.com/)-compliant implementation of promises/deferred
	 * objects inspired by [Kris Kowal's Q](https://github.com/kriskowal/q).
	 *
	 * $q can be used in two fashions --- one which is more similar to Kris Kowal's Q or jQuery's Deferred
	 * implementations, and the other which resembles ES6 (ES2015) promises to some degree.
	 *
	 * ## $q constructor
	 *
	 * The streamlined ES6 style promise is essentially just using $q as a constructor which takes a `resolver`
	 * function as the first argument. This is similar to the native Promise implementation from ES6,
	 * see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
	 *
	 * While the constructor-style use is supported, not all of the supporting methods from ES6 promises are
	 * available yet.
	 *
	 * It can be used like so:
	 *
	 * ```js
	 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
	 *   // are available in the current lexical scope (they could have been injected or passed in).
	 *
	 *   function asyncGreet(name) {
	 *     // perform some asynchronous operation, resolve or reject the promise when appropriate.
	 *     return $q(function(resolve, reject) {
	 *       setTimeout(function() {
	 *         if (okToGreet(name)) {
	 *           resolve('Hello, ' + name + '!');
	 *         } else {
	 *           reject('Greeting ' + name + ' is not allowed.');
	 *         }
	 *       }, 1000);
	 *     });
	 *   }
	 *
	 *   var promise = asyncGreet('Robin Hood');
	 *   promise.then(function(greeting) {
	 *     alert('Success: ' + greeting);
	 *   }, function(reason) {
	 *     alert('Failed: ' + reason);
	 *   });
	 * ```
	 *
	 * Note: progress/notify callbacks are not currently supported via the ES6-style interface.
	 *
	 * Note: unlike ES6 behavior, an exception thrown in the constructor function will NOT implicitly reject the promise.
	 *
	 * However, the more traditional CommonJS-style usage is still available, and documented below.
	 *
	 * [The CommonJS Promise proposal](http://wiki.commonjs.org/wiki/Promises) describes a promise as an
	 * interface for interacting with an object that represents the result of an action that is
	 * performed asynchronously, and may or may not be finished at any given point in time.
	 *
	 * From the perspective of dealing with error handling, deferred and promise APIs are to
	 * asynchronous programming what `try`, `catch` and `throw` keywords are to synchronous programming.
	 *
	 * ```js
	 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
	 *   // are available in the current lexical scope (they could have been injected or passed in).
	 *
	 *   function asyncGreet(name) {
	 *     var deferred = $q.defer();
	 *
	 *     setTimeout(function() {
	 *       deferred.notify('About to greet ' + name + '.');
	 *
	 *       if (okToGreet(name)) {
	 *         deferred.resolve('Hello, ' + name + '!');
	 *       } else {
	 *         deferred.reject('Greeting ' + name + ' is not allowed.');
	 *       }
	 *     }, 1000);
	 *
	 *     return deferred.promise;
	 *   }
	 *
	 *   var promise = asyncGreet('Robin Hood');
	 *   promise.then(function(greeting) {
	 *     alert('Success: ' + greeting);
	 *   }, function(reason) {
	 *     alert('Failed: ' + reason);
	 *   }, function(update) {
	 *     alert('Got notification: ' + update);
	 *   });
	 * ```
	 *
	 * At first it might not be obvious why this extra complexity is worth the trouble. The payoff
	 * comes in the way of guarantees that promise and deferred APIs make, see
	 * https://github.com/kriskowal/uncommonjs/blob/master/promises/specification.md.
	 *
	 * Additionally the promise api allows for composition that is very hard to do with the
	 * traditional callback ([CPS](http://en.wikipedia.org/wiki/Continuation-passing_style)) approach.
	 * For more on this please see the [Q documentation](https://github.com/kriskowal/q) especially the
	 * section on serial or parallel joining of promises.
	 *
	 * ## The Deferred API
	 *
	 * A new instance of deferred is constructed by calling `$q.defer()`.
	 *
	 * The purpose of the deferred object is to expose the associated Promise instance as well as APIs
	 * that can be used for signaling the successful or unsuccessful completion, as well as the status
	 * of the task.
	 *
	 * **Methods**
	 *
	 * - `resolve(value)` – resolves the derived promise with the `value`. If the value is a rejection
	 *   constructed via `$q.reject`, the promise will be rejected instead.
	 * - `reject(reason)` – rejects the derived promise with the `reason`. This is equivalent to
	 *   resolving it with a rejection constructed via `$q.reject`.
	 * - `notify(value)` - provides updates on the status of the promise's execution. This may be called
	 *   multiple times before the promise is either resolved or rejected.
	 *
	 * **Properties**
	 *
	 * - promise – `{Promise}` – promise object associated with this deferred.
	 *
	 *
	 * ## The Promise API
	 *
	 * A new promise instance is created when a deferred instance is created and can be retrieved by
	 * calling `deferred.promise`.
	 *
	 * The purpose of the promise object is to allow for interested parties to get access to the result
	 * of the deferred task when it completes.
	 *
	 * **Methods**
	 *
	 * - `then(successCallback, [errorCallback], [notifyCallback])` – regardless of when the promise was or
	 *   will be resolved or rejected, `then` calls one of the success or error callbacks asynchronously
	 *   as soon as the result is available. The callbacks are called with a single argument: the result
	 *   or rejection reason. Additionally, the notify callback may be called zero or more times to
	 *   provide a progress indication, before the promise is resolved or rejected.
	 *
	 *   This method *returns a new promise* which is resolved or rejected via the return value of the
	 *   `successCallback`, `errorCallback` (unless that value is a promise, in which case it is resolved
	 *   with the value which is resolved in that promise using
	 *   [promise chaining](http://www.html5rocks.com/en/tutorials/es6/promises/#toc-promises-queues)).
	 *   It also notifies via the return value of the `notifyCallback` method. The promise cannot be
	 *   resolved or rejected from the notifyCallback method. The errorCallback and notifyCallback
	 *   arguments are optional.
	 *
	 * - `catch(errorCallback)` – shorthand for `promise.then(null, errorCallback)`
	 *
	 * - `finally(callback, notifyCallback)` – allows you to observe either the fulfillment or rejection of a promise,
	 *   but to do so without modifying the final value. This is useful to release resources or do some
	 *   clean-up that needs to be done whether the promise was rejected or resolved. See the [full
	 *   specification](https://github.com/kriskowal/q/wiki/API-Reference#promisefinallycallback) for
	 *   more information.
	 *
	 * ## Chaining promises
	 *
	 * Because calling the `then` method of a promise returns a new derived promise, it is easily
	 * possible to create a chain of promises:
	 *
	 * ```js
	 *   promiseB = promiseA.then(function(result) {
	 *     return result + 1;
	 *   });
	 *
	 *   // promiseB will be resolved immediately after promiseA is resolved and its value
	 *   // will be the result of promiseA incremented by 1
	 * ```
	 *
	 * It is possible to create chains of any length and since a promise can be resolved with another
	 * promise (which will defer its resolution further), it is possible to pause/defer resolution of
	 * the promises at any point in the chain. This makes it possible to implement powerful APIs like
	 * $http's response interceptors.
	 *
	 *
	 * ## Differences between Kris Kowal's Q and $q
	 *
	 *  There are two main differences:
	 *
	 * - $q is integrated with the {@link ng.$rootScope.Scope} Scope model observation
	 *   mechanism in AngularJS, which means faster propagation of resolution or rejection into your
	 *   models and avoiding unnecessary browser repaints, which would result in flickering UI.
	 * - Q has many more features than $q, but that comes at a cost of bytes. $q is tiny, but contains
	 *   all the important functionality needed for common async tasks.
	 *
	 * ## Testing
	 *
	 *  ```js
	 *    it('should simulate promise', inject(function($q, $rootScope) {
	 *      var deferred = $q.defer();
	 *      var promise = deferred.promise;
	 *      var resolvedValue;
	 *
	 *      promise.then(function(value) { resolvedValue = value; });
	 *      expect(resolvedValue).toBeUndefined();
	 *
	 *      // Simulate resolving of promise
	 *      deferred.resolve(123);
	 *      // Note that the 'then' function does not get called synchronously.
	 *      // This is because we want the promise API to always be async, whether or not
	 *      // it got called synchronously or asynchronously.
	 *      expect(resolvedValue).toBeUndefined();
	 *
	 *      // Propagate promise resolution to 'then' functions using $apply().
	 *      $rootScope.$apply();
	 *      expect(resolvedValue).toEqual(123);
	 *    }));
	 *  ```
	 *
	 * @param {function(function, function)} resolver Function which is responsible for resolving or
	 *   rejecting the newly created promise. The first parameter is a function which resolves the
	 *   promise, the second parameter is a function which rejects the promise.
	 *
	 * @returns {Promise} The newly created promise.
	 */
	/**
	 * @ngdoc provider
	 * @name $qProvider
	 * @this
	 *
	 * @description
	 */
	function $QProvider() {
		var errorOnUnhandledRejections = true;
		this.$get = ['$rootScope', '$exceptionHandler', function ($rootScope, $exceptionHandler) {
			return qFactory(function (callback) {
				$rootScope.$evalAsync(callback);
			}, $exceptionHandler, errorOnUnhandledRejections);
		}];

		/**
		 * @ngdoc method
		 * @name $qProvider#errorOnUnhandledRejections
		 * @kind function
		 *
		 * @description
		 * Retrieves or overrides whether to generate an error when a rejected promise is not handled.
		 * This feature is enabled by default.
		 *
		 * @param {boolean=} value Whether to generate an error when a rejected promise is not handled.
		 * @returns {boolean|ng.$qProvider} Current value when called without a new value or self for
		 *    chaining otherwise.
		 */
		this.errorOnUnhandledRejections = function (value) {
			if (isDefined(value)) {
				errorOnUnhandledRejections = value;
				return this;
			} else {
				return errorOnUnhandledRejections;
			}
		};
	}

	/** @this */
	function $$QProvider() {
		var errorOnUnhandledRejections = true;
		this.$get = ['$browser', '$exceptionHandler', function ($browser, $exceptionHandler) {
			return qFactory(function (callback) {
				$browser.defer(callback);
			}, $exceptionHandler, errorOnUnhandledRejections);
		}];

		this.errorOnUnhandledRejections = function (value) {
			if (isDefined(value)) {
				errorOnUnhandledRejections = value;
				return this;
			} else {
				return errorOnUnhandledRejections;
			}
		};
	}

	/**
	 * Constructs a promise manager.
	 *
	 * @param {function(function)} nextTick Function for executing functions in the next turn.
	 * @param {function(...*)} exceptionHandler Function into which unexpected exceptions are passed for
	 *     debugging purposes.
	 * @param {boolean=} errorOnUnhandledRejections Whether an error should be generated on unhandled
	 *     promises rejections.
	 * @returns {object} Promise manager.
	 */
	function qFactory(nextTick, exceptionHandler, errorOnUnhandledRejections) {
		var $qMinErr = minErr('$q', TypeError);
		var queueSize = 0;
		var checkQueue = [];

		/**
		 * @ngdoc method
		 * @name ng.$q#defer
		 * @kind function
		 *
		 * @description
		 * Creates a `Deferred` object which represents a task which will finish in the future.
		 *
		 * @returns {Deferred} Returns a new instance of deferred.
		 */
		function defer() {
			return new Deferred();
		}

		function Deferred() {
			var promise = this.promise = new Promise();
			//Non prototype methods necessary to support unbound execution :/
			this.resolve = function (val) { resolvePromise(promise, val); };
			this.reject = function (reason) { rejectPromise(promise, reason); };
			this.notify = function (progress) { notifyPromise(promise, progress); };
		}


		function Promise() {
			this.$$state = { status: 0 };
		}

		extend(Promise.prototype, {
			then: function (onFulfilled, onRejected, progressBack) {
				if (isUndefined(onFulfilled) && isUndefined(onRejected) && isUndefined(progressBack)) {
					return this;
				}
				var result = new Promise();

				this.$$state.pending = this.$$state.pending || [];
				this.$$state.pending.push([result, onFulfilled, onRejected, progressBack]);
				if (this.$$state.status > 0) scheduleProcessQueue(this.$$state);

				return result;
			},

			'catch': function (callback) {
				return this.then(null, callback);
			},

			'finally': function (callback, progressBack) {
				return this.then(function (value) {
					return handleCallback(value, resolve, callback);
				}, function (error) {
					return handleCallback(error, reject, callback);
				}, progressBack);
			}
		});

		function processQueue(state) {
			var fn, promise, pending;

			pending = state.pending;
			state.processScheduled = false;
			state.pending = undefined;
			try {
				for (var i = 0, ii = pending.length; i < ii; ++i) {
					markQStateExceptionHandled(state);
					promise = pending[i][0];
					fn = pending[i][state.status];
					try {
						if (isFunction(fn)) {
							resolvePromise(promise, fn(state.value));
						} else if (state.status === 1) {
							resolvePromise(promise, state.value);
						} else {
							rejectPromise(promise, state.value);
						}
					} catch (e) {
						rejectPromise(promise, e);
						// This error is explicitly marked for being passed to the $exceptionHandler
						if (e && e.$$passToExceptionHandler === true) {
							exceptionHandler(e);
						}
					}
				}
			} finally {
				--queueSize;
				if (errorOnUnhandledRejections && queueSize === 0) {
					nextTick(processChecks);
				}
			}
		}

		function processChecks() {
			// eslint-disable-next-line no-unmodified-loop-condition
			while (!queueSize && checkQueue.length) {
				var toCheck = checkQueue.shift();
				if (!isStateExceptionHandled(toCheck)) {
					markQStateExceptionHandled(toCheck);
					var errorMessage = 'Possibly unhandled rejection: ' + toDebugString(toCheck.value);
					if (isError(toCheck.value)) {
						exceptionHandler(toCheck.value, errorMessage);
					} else {
						exceptionHandler(errorMessage);
					}
				}
			}
		}

		function scheduleProcessQueue(state) {
			if (errorOnUnhandledRejections && !state.pending && state.status === 2 && !isStateExceptionHandled(state)) {
				if (queueSize === 0 && checkQueue.length === 0) {
					nextTick(processChecks);
				}
				checkQueue.push(state);
			}
			if (state.processScheduled || !state.pending) return;
			state.processScheduled = true;
			++queueSize;
			nextTick(function () { processQueue(state); });
		}

		function resolvePromise(promise, val) {
			if (promise.$$state.status) return;
			if (val === promise) {
				$$reject(promise, $qMinErr(
					'qcycle',
					'Expected promise to be resolved with value other than itself \'{0}\'',
					val));
			} else {
				$$resolve(promise, val);
			}

		}

		function $$resolve(promise, val) {
			var then;
			var done = false;
			try {
				if (isObject(val) || isFunction(val)) then = val.then;
				if (isFunction(then)) {
					promise.$$state.status = -1;
					then.call(val, doResolve, doReject, doNotify);
				} else {
					promise.$$state.value = val;
					promise.$$state.status = 1;
					scheduleProcessQueue(promise.$$state);
				}
			} catch (e) {
				doReject(e);
			}

			function doResolve(val) {
				if (done) return;
				done = true;
				$$resolve(promise, val);
			}
			function doReject(val) {
				if (done) return;
				done = true;
				$$reject(promise, val);
			}
			function doNotify(progress) {
				notifyPromise(promise, progress);
			}
		}

		function rejectPromise(promise, reason) {
			if (promise.$$state.status) return;
			$$reject(promise, reason);
		}

		function $$reject(promise, reason) {
			promise.$$state.value = reason;
			promise.$$state.status = 2;
			scheduleProcessQueue(promise.$$state);
		}

		function notifyPromise(promise, progress) {
			var callbacks = promise.$$state.pending;

			if ((promise.$$state.status <= 0) && callbacks && callbacks.length) {
				nextTick(function () {
					var callback, result;
					for (var i = 0, ii = callbacks.length; i < ii; i++) {
						result = callbacks[i][0];
						callback = callbacks[i][3];
						try {
							notifyPromise(result, isFunction(callback) ? callback(progress) : progress);
						} catch (e) {
							exceptionHandler(e);
						}
					}
				});
			}
		}

		/**
		 * @ngdoc method
		 * @name $q#reject
		 * @kind function
		 *
		 * @description
		 * Creates a promise that is resolved as rejected with the specified `reason`. This api should be
		 * used to forward rejection in a chain of promises. If you are dealing with the last promise in
		 * a promise chain, you don't need to worry about it.
		 *
		 * When comparing deferreds/promises to the familiar behavior of try/catch/throw, think of
		 * `reject` as the `throw` keyword in JavaScript. This also means that if you "catch" an error via
		 * a promise error callback and you want to forward the error to the promise derived from the
		 * current promise, you have to "rethrow" the error by returning a rejection constructed via
		 * `reject`.
		 *
		 * ```js
		 *   promiseB = promiseA.then(function(result) {
		 *     // success: do something and resolve promiseB
		 *     //          with the old or a new result
		 *     return result;
		 *   }, function(reason) {
		 *     // error: handle the error if possible and
		 *     //        resolve promiseB with newPromiseOrValue,
		 *     //        otherwise forward the rejection to promiseB
		 *     if (canHandle(reason)) {
		 *      // handle the error and recover
		 *      return newPromiseOrValue;
		 *     }
		 *     return $q.reject(reason);
		 *   });
		 * ```
		 *
		 * @param {*} reason Constant, message, exception or an object representing the rejection reason.
		 * @returns {Promise} Returns a promise that was already resolved as rejected with the `reason`.
		 */
		function reject(reason) {
			var result = new Promise();
			rejectPromise(result, reason);
			return result;
		}

		function handleCallback(value, resolver, callback) {
			var callbackOutput = null;
			try {
				if (isFunction(callback)) callbackOutput = callback();
			} catch (e) {
				return reject(e);
			}
			if (isPromiseLike(callbackOutput)) {
				return callbackOutput.then(function () {
					return resolver(value);
				}, reject);
			} else {
				return resolver(value);
			}
		}

		/**
		 * @ngdoc method
		 * @name $q#when
		 * @kind function
		 *
		 * @description
		 * Wraps an object that might be a value or a (3rd party) then-able promise into a $q promise.
		 * This is useful when you are dealing with an object that might or might not be a promise, or if
		 * the promise comes from a source that can't be trusted.
		 *
		 * @param {*} value Value or a promise
		 * @param {Function=} successCallback
		 * @param {Function=} errorCallback
		 * @param {Function=} progressCallback
		 * @returns {Promise} Returns a promise of the passed value or promise
		 */


		function when(value, callback, errback, progressBack) {
			var result = new Promise();
			resolvePromise(result, value);
			return result.then(callback, errback, progressBack);
		}

		/**
		 * @ngdoc method
		 * @name $q#resolve
		 * @kind function
		 *
		 * @description
		 * Alias of {@link ng.$q#when when} to maintain naming consistency with ES6.
		 *
		 * @param {*} value Value or a promise
		 * @param {Function=} successCallback
		 * @param {Function=} errorCallback
		 * @param {Function=} progressCallback
		 * @returns {Promise} Returns a promise of the passed value or promise
		 */
		var resolve = when;

		/**
		 * @ngdoc method
		 * @name $q#all
		 * @kind function
		 *
		 * @description
		 * Combines multiple promises into a single promise that is resolved when all of the input
		 * promises are resolved.
		 *
		 * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
		 * @returns {Promise} Returns a single promise that will be resolved with an array/hash of values,
		 *   each value corresponding to the promise at the same index/key in the `promises` array/hash.
		 *   If any of the promises is resolved with a rejection, this resulting promise will be rejected
		 *   with the same rejection value.
		 */

		function all(promises) {
			var result = new Promise(),
				counter = 0,
				results = isArray(promises) ? [] : {};

			forEach(promises, function (promise, key) {
				counter++;
				when(promise).then(function (value) {
					results[key] = value;
					if (!(--counter)) resolvePromise(result, results);
				}, function (reason) {
					rejectPromise(result, reason);
				});
			});

			if (counter === 0) {
				resolvePromise(result, results);
			}

			return result;
		}

		/**
		 * @ngdoc method
		 * @name $q#race
		 * @kind function
		 *
		 * @description
		 * Returns a promise that resolves or rejects as soon as one of those promises
		 * resolves or rejects, with the value or reason from that promise.
		 *
		 * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
		 * @returns {Promise} a promise that resolves or rejects as soon as one of the `promises`
		 * resolves or rejects, with the value or reason from that promise.
		 */

		function race(promises) {
			var deferred = defer();

			forEach(promises, function (promise) {
				when(promise).then(deferred.resolve, deferred.reject);
			});

			return deferred.promise;
		}

		function $Q(resolver) {
			if (!isFunction(resolver)) {
				throw $qMinErr('norslvr', 'Expected resolverFn, got \'{0}\'', resolver);
			}

			var promise = new Promise();

			function resolveFn(value) {
				resolvePromise(promise, value);
			}

			function rejectFn(reason) {
				rejectPromise(promise, reason);
			}

			resolver(resolveFn, rejectFn);

			return promise;
		}

		// Let's make the instanceof operator work for promises, so that
		// `new $q(fn) instanceof $q` would evaluate to true.
		$Q.prototype = Promise.prototype;

		$Q.defer = defer;
		$Q.reject = reject;
		$Q.when = when;
		$Q.resolve = resolve;
		$Q.all = all;
		$Q.race = race;

		return $Q;
	}

	function isStateExceptionHandled(state) {
		return !!state.pur;
	}
	function markQStateExceptionHandled(state) {
		state.pur = true;
	}
	function markQExceptionHandled(q) {
		// Built-in `$q` promises will always have a `$$state` property. This check is to allow
		// overwriting `$q` with a different promise library (e.g. Bluebird + angular-bluebird-promises).
		// (Currently, this is the only method that might be called with a promise, even if it is not
		// created by the built-in `$q`.)
		if (q.$$state) {
			markQStateExceptionHandled(q.$$state);
		}
	}

	/** @this */
	function $$RAFProvider() { //rAF
		this.$get = ['$window', '$timeout', function ($window, $timeout) {
			var requestAnimationFrame = $window.requestAnimationFrame ||
				$window.webkitRequestAnimationFrame;

			var cancelAnimationFrame = $window.cancelAnimationFrame ||
				$window.webkitCancelAnimationFrame ||
				$window.webkitCancelRequestAnimationFrame;

			var rafSupported = !!requestAnimationFrame;
			var raf = rafSupported
				? function (fn) {
					var id = requestAnimationFrame(fn);
					return function () {
						cancelAnimationFrame(id);
					};
				}
				: function (fn) {
					var timer = $timeout(fn, 16.66, false); // 1000 / 60 = 16.666
					return function () {
						$timeout.cancel(timer);
					};
				};

			raf.supported = rafSupported;

			return raf;
		}];
	}

	/**
	 * DESIGN NOTES
	 *
	 * The design decisions behind the scope are heavily favored for speed and memory consumption.
	 *
	 * The typical use of scope is to watch the expressions, which most of the time return the same
	 * value as last time so we optimize the operation.
	 *
	 * Closures construction is expensive in terms of speed as well as memory:
	 *   - No closures, instead use prototypical inheritance for API
	 *   - Internal state needs to be stored on scope directly, which means that private state is
	 *     exposed as $$____ properties
	 *
	 * Loop operations are optimized by using while(count--) { ... }
	 *   - This means that in order to keep the same order of execution as addition we have to add
	 *     items to the array at the beginning (unshift) instead of at the end (push)
	 *
	 * Child scopes are created and removed often
	 *   - Using an array would be slow since inserts in the middle are expensive; so we use linked lists
	 *
	 * There are fewer watches than observers. This is why you don't want the observer to be implemented
	 * in the same way as watch. Watch requires return of the initialization function which is expensive
	 * to construct.
	 */


	/**
	 * @ngdoc provider
	 * @name $rootScopeProvider
	 * @description
	 *
	 * Provider for the $rootScope service.
	 */

	/**
	 * @ngdoc method
	 * @name $rootScopeProvider#digestTtl
	 * @description
	 *
	 * Sets the number of `$digest` iterations the scope should attempt to execute before giving up and
	 * assuming that the model is unstable.
	 *
	 * The current default is 10 iterations.
	 *
	 * In complex applications it's possible that the dependencies between `$watch`s will result in
	 * several digest iterations. However if an application needs more than the default 10 digest
	 * iterations for its model to stabilize then you should investigate what is causing the model to
	 * continuously change during the digest.
	 *
	 * Increasing the TTL could have performance implications, so you should not change it without
	 * proper justification.
	 *
	 * @param {number} limit The number of digest iterations.
	 */


	/**
	 * @ngdoc service
	 * @name $rootScope
	 * @this
	 *
	 * @description
	 *
	 * Every application has a single root {@link ng.$rootScope.Scope scope}.
	 * All other scopes are descendant scopes of the root scope. Scopes provide separation
	 * between the model and the view, via a mechanism for watching the model for changes.
	 * They also provide event emission/broadcast and subscription facility. See the
	 * {@link guide/scope developer guide on scopes}.
	 */
	function $RootScopeProvider() {
		var TTL = 10;
		var $rootScopeMinErr = minErr('$rootScope');
		var lastDirtyWatch = null;
		var applyAsyncId = null;

		this.digestTtl = function (value) {
			if (arguments.length) {
				TTL = value;
			}
			return TTL;
		};

		function createChildScopeClass(parent) {
			function ChildScope() {
				this.$$watchers = this.$$nextSibling =
					this.$$childHead = this.$$childTail = null;
				this.$$listeners = {};
				this.$$listenerCount = {};
				this.$$watchersCount = 0;
				this.$id = nextUid();
				this.$$ChildScope = null;
				this.$$suspended = false;
			}
			ChildScope.prototype = parent;
			return ChildScope;
		}

		this.$get = ['$exceptionHandler', '$parse', '$browser',
			function ($exceptionHandler, $parse, $browser) {

				function destroyChildScope($event) {
					$event.currentScope.$$destroyed = true;
				}

				function cleanUpScope($scope) {

					// Support: IE 9 only
					if (msie === 9) {
						// There is a memory leak in IE9 if all child scopes are not disconnected
						// completely when a scope is destroyed. So this code will recurse up through
						// all this scopes children
						//
						// See issue https://github.com/angular/angular.js/issues/10706
						if ($scope.$$childHead) {
							cleanUpScope($scope.$$childHead);
						}
						if ($scope.$$nextSibling) {
							cleanUpScope($scope.$$nextSibling);
						}
					}

					// The code below works around IE9 and V8's memory leaks
					//
					// See:
					// - https://code.google.com/p/v8/issues/detail?id=2073#c26
					// - https://github.com/angular/angular.js/issues/6794#issuecomment-38648909
					// - https://github.com/angular/angular.js/issues/1313#issuecomment-10378451

					$scope.$parent = $scope.$$nextSibling = $scope.$$prevSibling = $scope.$$childHead =
						$scope.$$childTail = $scope.$root = $scope.$$watchers = null;
				}

				/**
				 * @ngdoc type
				 * @name $rootScope.Scope
				 *
				 * @description
				 * A root scope can be retrieved using the {@link ng.$rootScope $rootScope} key from the
				 * {@link auto.$injector $injector}. Child scopes are created using the
				 * {@link ng.$rootScope.Scope#$new $new()} method. (Most scopes are created automatically when
				 * compiled HTML template is executed.) See also the {@link guide/scope Scopes guide} for
				 * an in-depth introduction and usage examples.
				 *
				 *
				 * ## Inheritance
				 * A scope can inherit from a parent scope, as in this example:
				 * ```js
					 var parent = $rootScope;
					 var child = parent.$new();
			
					 parent.salutation = "Hello";
					 expect(child.salutation).toEqual('Hello');
			
					 child.salutation = "Welcome";
					 expect(child.salutation).toEqual('Welcome');
					 expect(parent.salutation).toEqual('Hello');
				 * ```
				 *
				 * When interacting with `Scope` in tests, additional helper methods are available on the
				 * instances of `Scope` type. See {@link ngMock.$rootScope.Scope ngMock Scope} for additional
				 * details.
				 *
				 *
				 * @param {Object.<string, function()>=} providers Map of service factory which need to be
				 *                                       provided for the current scope. Defaults to {@link ng}.
				 * @param {Object.<string, *>=} instanceCache Provides pre-instantiated services which should
				 *                              append/override services provided by `providers`. This is handy
				 *                              when unit-testing and having the need to override a default
				 *                              service.
				 * @returns {Object} Newly created scope.
				 *
				 */
				function Scope() {
					this.$id = nextUid();
					this.$$phase = this.$parent = this.$$watchers =
						this.$$nextSibling = this.$$prevSibling =
						this.$$childHead = this.$$childTail = null;
					this.$root = this;
					this.$$destroyed = false;
					this.$$suspended = false;
					this.$$listeners = {};
					this.$$listenerCount = {};
					this.$$watchersCount = 0;
					this.$$isolateBindings = null;
				}

				/**
				 * @ngdoc property
				 * @name $rootScope.Scope#$id
				 *
				 * @description
				 * Unique scope ID (monotonically increasing) useful for debugging.
				 */

				/**
				 * @ngdoc property
				 * @name $rootScope.Scope#$parent
				 *
				 * @description
				 * Reference to the parent scope.
				 */

				/**
				 * @ngdoc property
				 * @name $rootScope.Scope#$root
				 *
				 * @description
				 * Reference to the root scope.
				 */

				Scope.prototype = {
					constructor: Scope,
					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$new
					 * @kind function
					 *
					 * @description
					 * Creates a new child {@link ng.$rootScope.Scope scope}.
					 *
					 * The parent scope will propagate the {@link ng.$rootScope.Scope#$digest $digest()} event.
					 * The scope can be removed from the scope hierarchy using {@link ng.$rootScope.Scope#$destroy $destroy()}.
					 *
					 * {@link ng.$rootScope.Scope#$destroy $destroy()} must be called on a scope when it is
					 * desired for the scope and its child scopes to be permanently detached from the parent and
					 * thus stop participating in model change detection and listener notification by invoking.
					 *
					 * @param {boolean} isolate If true, then the scope does not prototypically inherit from the
					 *         parent scope. The scope is isolated, as it can not see parent scope properties.
					 *         When creating widgets, it is useful for the widget to not accidentally read parent
					 *         state.
					 *
					 * @param {Scope} [parent=this] The {@link ng.$rootScope.Scope `Scope`} that will be the `$parent`
					 *                              of the newly created scope. Defaults to `this` scope if not provided.
					 *                              This is used when creating a transclude scope to correctly place it
					 *                              in the scope hierarchy while maintaining the correct prototypical
					 *                              inheritance.
					 *
					 * @returns {Object} The newly created child scope.
					 *
					 */
					$new: function (isolate, parent) {
						var child;

						parent = parent || this;

						if (isolate) {
							child = new Scope();
							child.$root = this.$root;
						} else {
							// Only create a child scope class if somebody asks for one,
							// but cache it to allow the VM to optimize lookups.
							if (!this.$$ChildScope) {
								this.$$ChildScope = createChildScopeClass(this);
							}
							child = new this.$$ChildScope();
						}
						child.$parent = parent;
						child.$$prevSibling = parent.$$childTail;
						if (parent.$$childHead) {
							parent.$$childTail.$$nextSibling = child;
							parent.$$childTail = child;
						} else {
							parent.$$childHead = parent.$$childTail = child;
						}

						// When the new scope is not isolated or we inherit from `this`, and
						// the parent scope is destroyed, the property `$$destroyed` is inherited
						// prototypically. In all other cases, this property needs to be set
						// when the parent scope is destroyed.
						// The listener needs to be added after the parent is set
						if (isolate || parent !== this) child.$on('$destroy', destroyChildScope);

						return child;
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$watch
					 * @kind function
					 *
					 * @description
					 * Registers a `listener` callback to be executed whenever the `watchExpression` changes.
					 *
					 * - The `watchExpression` is called on every call to {@link ng.$rootScope.Scope#$digest
					 *   $digest()} and should return the value that will be watched. (`watchExpression` should not change
					 *   its value when executed multiple times with the same input because it may be executed multiple
					 *   times by {@link ng.$rootScope.Scope#$digest $digest()}. That is, `watchExpression` should be
					 *   [idempotent](http://en.wikipedia.org/wiki/Idempotence).)
					 * - The `listener` is called only when the value from the current `watchExpression` and the
					 *   previous call to `watchExpression` are not equal (with the exception of the initial run,
					 *   see below). Inequality is determined according to reference inequality,
					 *   [strict comparison](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Comparison_Operators)
					 *    via the `!==` Javascript operator, unless `objectEquality == true`
					 *   (see next point)
					 * - When `objectEquality == true`, inequality of the `watchExpression` is determined
					 *   according to the {@link angular.equals} function. To save the value of the object for
					 *   later comparison, the {@link angular.copy} function is used. This therefore means that
					 *   watching complex objects will have adverse memory and performance implications.
					 * - This should not be used to watch for changes in objects that are (or contain)
					 *   [File](https://developer.mozilla.org/docs/Web/API/File) objects due to limitations with {@link angular.copy `angular.copy`}.
					 * - The watch `listener` may change the model, which may trigger other `listener`s to fire.
					 *   This is achieved by rerunning the watchers until no changes are detected. The rerun
					 *   iteration limit is 10 to prevent an infinite loop deadlock.
					 *
					 *
					 * If you want to be notified whenever {@link ng.$rootScope.Scope#$digest $digest} is called,
					 * you can register a `watchExpression` function with no `listener`. (Be prepared for
					 * multiple calls to your `watchExpression` because it will execute multiple times in a
					 * single {@link ng.$rootScope.Scope#$digest $digest} cycle if a change is detected.)
					 *
					 * After a watcher is registered with the scope, the `listener` fn is called asynchronously
					 * (via {@link ng.$rootScope.Scope#$evalAsync $evalAsync}) to initialize the
					 * watcher. In rare cases, this is undesirable because the listener is called when the result
					 * of `watchExpression` didn't change. To detect this scenario within the `listener` fn, you
					 * can compare the `newVal` and `oldVal`. If these two values are identical (`===`) then the
					 * listener was called due to initialization.
					 *
					 *
					 *
					 * @example
					 * ```js
						 // let's assume that scope was dependency injected as the $rootScope
						 var scope = $rootScope;
						 scope.name = 'misko';
						 scope.counter = 0;
			  
						 expect(scope.counter).toEqual(0);
						 scope.$watch('name', function(newValue, oldValue) {
						   scope.counter = scope.counter + 1;
						 });
						 expect(scope.counter).toEqual(0);
			  
						 scope.$digest();
						 // the listener is always called during the first $digest loop after it was registered
						 expect(scope.counter).toEqual(1);
			  
						 scope.$digest();
						 // but now it will not be called unless the value changes
						 expect(scope.counter).toEqual(1);
			  
						 scope.name = 'adam';
						 scope.$digest();
						 expect(scope.counter).toEqual(2);
			  
			  
			  
						 // Using a function as a watchExpression
						 var food;
						 scope.foodCounter = 0;
						 expect(scope.foodCounter).toEqual(0);
						 scope.$watch(
						   // This function returns the value being watched. It is called for each turn of the $digest loop
						   function() { return food; },
						   // This is the change listener, called when the value returned from the above function changes
						   function(newValue, oldValue) {
							 if ( newValue !== oldValue ) {
							   // Only increment the counter if the value changed
							   scope.foodCounter = scope.foodCounter + 1;
							 }
						   }
						 );
						 // No digest has been run so the counter will be zero
						 expect(scope.foodCounter).toEqual(0);
			  
						 // Run the digest but since food has not changed count will still be zero
						 scope.$digest();
						 expect(scope.foodCounter).toEqual(0);
			  
						 // Update food and run digest.  Now the counter will increment
						 food = 'cheeseburger';
						 scope.$digest();
						 expect(scope.foodCounter).toEqual(1);
			  
					 * ```
					 *
					 *
					 *
					 * @param {(function()|string)} watchExpression Expression that is evaluated on each
					 *    {@link ng.$rootScope.Scope#$digest $digest} cycle. A change in the return value triggers
					 *    a call to the `listener`.
					 *
					 *    - `string`: Evaluated as {@link guide/expression expression}
					 *    - `function(scope)`: called with current `scope` as a parameter.
					 * @param {function(newVal, oldVal, scope)} listener Callback called whenever the value
					 *    of `watchExpression` changes.
					 *
					 *    - `newVal` contains the current value of the `watchExpression`
					 *    - `oldVal` contains the previous value of the `watchExpression`
					 *    - `scope` refers to the current scope
					 * @param {boolean=} [objectEquality=false] Compare for object equality using {@link angular.equals} instead of
					 *     comparing for reference equality.
					 * @returns {function()} Returns a deregistration function for this listener.
					 */
					$watch: function (watchExp, listener, objectEquality, prettyPrintExpression) {
						var get = $parse(watchExp);
						var fn = isFunction(listener) ? listener : noop;

						if (get.$$watchDelegate) {
							return get.$$watchDelegate(this, fn, objectEquality, get, watchExp);
						}
						var scope = this,
							array = scope.$$watchers,
							watcher = {
								fn: fn,
								last: initWatchVal,
								get: get,
								exp: prettyPrintExpression || watchExp,
								eq: !!objectEquality
							};

						lastDirtyWatch = null;

						if (!array) {
							array = scope.$$watchers = [];
							array.$$digestWatchIndex = -1;
						}
						// we use unshift since we use a while loop in $digest for speed.
						// the while loop reads in reverse order.
						array.unshift(watcher);
						array.$$digestWatchIndex++;
						incrementWatchersCount(this, 1);

						return function deregisterWatch() {
							var index = arrayRemove(array, watcher);
							if (index >= 0) {
								incrementWatchersCount(scope, -1);
								if (index < array.$$digestWatchIndex) {
									array.$$digestWatchIndex--;
								}
							}
							lastDirtyWatch = null;
						};
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$watchGroup
					 * @kind function
					 *
					 * @description
					 * A variant of {@link ng.$rootScope.Scope#$watch $watch()} where it watches an array of `watchExpressions`.
					 * If any one expression in the collection changes the `listener` is executed.
					 *
					 * - The items in the `watchExpressions` array are observed via the standard `$watch` operation. Their return
					 *   values are examined for changes on every call to `$digest`.
					 * - The `listener` is called whenever any expression in the `watchExpressions` array changes.
					 *
					 * @param {Array.<string|Function(scope)>} watchExpressions Array of expressions that will be individually
					 * watched using {@link ng.$rootScope.Scope#$watch $watch()}
					 *
					 * @param {function(newValues, oldValues, scope)} listener Callback called whenever the return value of any
					 *    expression in `watchExpressions` changes
					 *    The `newValues` array contains the current values of the `watchExpressions`, with the indexes matching
					 *    those of `watchExpression`
					 *    and the `oldValues` array contains the previous values of the `watchExpressions`, with the indexes matching
					 *    those of `watchExpression`
					 *    The `scope` refers to the current scope.
					 * @returns {function()} Returns a de-registration function for all listeners.
					 */
					$watchGroup: function (watchExpressions, listener) {
						var oldValues = new Array(watchExpressions.length);
						var newValues = new Array(watchExpressions.length);
						var deregisterFns = [];
						var self = this;
						var changeReactionScheduled = false;
						var firstRun = true;

						if (!watchExpressions.length) {
							// No expressions means we call the listener ASAP
							var shouldCall = true;
							self.$evalAsync(function () {
								if (shouldCall) listener(newValues, newValues, self);
							});
							return function deregisterWatchGroup() {
								shouldCall = false;
							};
						}

						if (watchExpressions.length === 1) {
							// Special case size of one
							return this.$watch(watchExpressions[0], function watchGroupAction(value, oldValue, scope) {
								newValues[0] = value;
								oldValues[0] = oldValue;
								listener(newValues, (value === oldValue) ? newValues : oldValues, scope);
							});
						}

						forEach(watchExpressions, function (expr, i) {
							var unwatchFn = self.$watch(expr, function watchGroupSubAction(value) {
								newValues[i] = value;
								if (!changeReactionScheduled) {
									changeReactionScheduled = true;
									self.$evalAsync(watchGroupAction);
								}
							});
							deregisterFns.push(unwatchFn);
						});

						function watchGroupAction() {
							changeReactionScheduled = false;

							try {
								if (firstRun) {
									firstRun = false;
									listener(newValues, newValues, self);
								} else {
									listener(newValues, oldValues, self);
								}
							} finally {
								for (var i = 0; i < watchExpressions.length; i++) {
									oldValues[i] = newValues[i];
								}
							}
						}

						return function deregisterWatchGroup() {
							while (deregisterFns.length) {
								deregisterFns.shift()();
							}
						};
					},


					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$watchCollection
					 * @kind function
					 *
					 * @description
					 * Shallow watches the properties of an object and fires whenever any of the properties change
					 * (for arrays, this implies watching the array items; for object maps, this implies watching
					 * the properties). If a change is detected, the `listener` callback is fired.
					 *
					 * - The `obj` collection is observed via standard $watch operation and is examined on every
					 *   call to $digest() to see if any items have been added, removed, or moved.
					 * - The `listener` is called whenever anything within the `obj` has changed. Examples include
					 *   adding, removing, and moving items belonging to an object or array.
					 *
					 *
					 * @example
					 * ```js
						$scope.names = ['igor', 'matias', 'misko', 'james'];
						$scope.dataCount = 4;
			  
						$scope.$watchCollection('names', function(newNames, oldNames) {
						  $scope.dataCount = newNames.length;
						});
			  
						expect($scope.dataCount).toEqual(4);
						$scope.$digest();
			  
						//still at 4 ... no changes
						expect($scope.dataCount).toEqual(4);
			  
						$scope.names.pop();
						$scope.$digest();
			  
						//now there's been a change
						expect($scope.dataCount).toEqual(3);
					 * ```
					 *
					 *
					 * @param {string|function(scope)} obj Evaluated as {@link guide/expression expression}. The
					 *    expression value should evaluate to an object or an array which is observed on each
					 *    {@link ng.$rootScope.Scope#$digest $digest} cycle. Any shallow change within the
					 *    collection will trigger a call to the `listener`.
					 *
					 * @param {function(newCollection, oldCollection, scope)} listener a callback function called
					 *    when a change is detected.
					 *    - The `newCollection` object is the newly modified data obtained from the `obj` expression
					 *    - The `oldCollection` object is a copy of the former collection data.
					 *      Due to performance considerations, the`oldCollection` value is computed only if the
					 *      `listener` function declares two or more arguments.
					 *    - The `scope` argument refers to the current scope.
					 *
					 * @returns {function()} Returns a de-registration function for this listener. When the
					 *    de-registration function is executed, the internal watch operation is terminated.
					 */
					$watchCollection: function (obj, listener) {
						// Mark the interceptor as
						// ... $$pure when literal since the instance will change when any input changes
						$watchCollectionInterceptor.$$pure = $parse(obj).literal;
						// ... $stateful when non-literal since we must read the state of the collection
						$watchCollectionInterceptor.$stateful = !$watchCollectionInterceptor.$$pure;

						var self = this;
						// the current value, updated on each dirty-check run
						var newValue;
						// a shallow copy of the newValue from the last dirty-check run,
						// updated to match newValue during dirty-check run
						var oldValue;
						// a shallow copy of the newValue from when the last change happened
						var veryOldValue;
						// only track veryOldValue if the listener is asking for it
						var trackVeryOldValue = (listener.length > 1);
						var changeDetected = 0;
						var changeDetector = $parse(obj, $watchCollectionInterceptor);
						var internalArray = [];
						var internalObject = {};
						var initRun = true;
						var oldLength = 0;

						function $watchCollectionInterceptor(_value) {
							newValue = _value;
							var newLength, key, bothNaN, newItem, oldItem;

							// If the new value is undefined, then return undefined as the watch may be a one-time watch
							if (isUndefined(newValue)) return;

							if (!isObject(newValue)) { // if primitive
								if (oldValue !== newValue) {
									oldValue = newValue;
									changeDetected++;
								}
							} else if (isArrayLike(newValue)) {
								if (oldValue !== internalArray) {
									// we are transitioning from something which was not an array into array.
									oldValue = internalArray;
									oldLength = oldValue.length = 0;
									changeDetected++;
								}

								newLength = newValue.length;

								if (oldLength !== newLength) {
									// if lengths do not match we need to trigger change notification
									changeDetected++;
									oldValue.length = oldLength = newLength;
								}
								// copy the items to oldValue and look for changes.
								for (var i = 0; i < newLength; i++) {
									oldItem = oldValue[i];
									newItem = newValue[i];

									// eslint-disable-next-line no-self-compare
									bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
									if (!bothNaN && (oldItem !== newItem)) {
										changeDetected++;
										oldValue[i] = newItem;
									}
								}
							} else {
								if (oldValue !== internalObject) {
									// we are transitioning from something which was not an object into object.
									oldValue = internalObject = {};
									oldLength = 0;
									changeDetected++;
								}
								// copy the items to oldValue and look for changes.
								newLength = 0;
								for (key in newValue) {
									if (hasOwnProperty.call(newValue, key)) {
										newLength++;
										newItem = newValue[key];
										oldItem = oldValue[key];

										if (key in oldValue) {
											// eslint-disable-next-line no-self-compare
											bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
											if (!bothNaN && (oldItem !== newItem)) {
												changeDetected++;
												oldValue[key] = newItem;
											}
										} else {
											oldLength++;
											oldValue[key] = newItem;
											changeDetected++;
										}
									}
								}
								if (oldLength > newLength) {
									// we used to have more keys, need to find them and destroy them.
									changeDetected++;
									for (key in oldValue) {
										if (!hasOwnProperty.call(newValue, key)) {
											oldLength--;
											delete oldValue[key];
										}
									}
								}
							}
							return changeDetected;
						}

						function $watchCollectionAction() {
							if (initRun) {
								initRun = false;
								listener(newValue, newValue, self);
							} else {
								listener(newValue, veryOldValue, self);
							}

							// make a copy for the next time a collection is changed
							if (trackVeryOldValue) {
								if (!isObject(newValue)) {
									//primitive
									veryOldValue = newValue;
								} else if (isArrayLike(newValue)) {
									veryOldValue = new Array(newValue.length);
									for (var i = 0; i < newValue.length; i++) {
										veryOldValue[i] = newValue[i];
									}
								} else { // if object
									veryOldValue = {};
									for (var key in newValue) {
										if (hasOwnProperty.call(newValue, key)) {
											veryOldValue[key] = newValue[key];
										}
									}
								}
							}
						}

						return this.$watch(changeDetector, $watchCollectionAction);
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$digest
					 * @kind function
					 *
					 * @description
					 * Processes all of the {@link ng.$rootScope.Scope#$watch watchers} of the current scope and
					 * its children. Because a {@link ng.$rootScope.Scope#$watch watcher}'s listener can change
					 * the model, the `$digest()` keeps calling the {@link ng.$rootScope.Scope#$watch watchers}
					 * until no more listeners are firing. This means that it is possible to get into an infinite
					 * loop. This function will throw `'Maximum iteration limit exceeded.'` if the number of
					 * iterations exceeds 10.
					 *
					 * Usually, you don't call `$digest()` directly in
					 * {@link ng.directive:ngController controllers} or in
					 * {@link ng.$compileProvider#directive directives}.
					 * Instead, you should call {@link ng.$rootScope.Scope#$apply $apply()} (typically from within
					 * a {@link ng.$compileProvider#directive directive}), which will force a `$digest()`.
					 *
					 * If you want to be notified whenever `$digest()` is called,
					 * you can register a `watchExpression` function with
					 * {@link ng.$rootScope.Scope#$watch $watch()} with no `listener`.
					 *
					 * In unit tests, you may need to call `$digest()` to simulate the scope life cycle.
					 *
					 * @example
					 * ```js
						 var scope = ...;
						 scope.name = 'misko';
						 scope.counter = 0;
			  
						 expect(scope.counter).toEqual(0);
						 scope.$watch('name', function(newValue, oldValue) {
						   scope.counter = scope.counter + 1;
						 });
						 expect(scope.counter).toEqual(0);
			  
						 scope.$digest();
						 // the listener is always called during the first $digest loop after it was registered
						 expect(scope.counter).toEqual(1);
			  
						 scope.$digest();
						 // but now it will not be called unless the value changes
						 expect(scope.counter).toEqual(1);
			  
						 scope.name = 'adam';
						 scope.$digest();
						 expect(scope.counter).toEqual(2);
					 * ```
					 *
					 */
					$digest: function () {
						var watch, value, last, fn, get,
							watchers,
							dirty, ttl = TTL,
							next, current, target = asyncQueue.length ? $rootScope : this,
							watchLog = [],
							logIdx, asyncTask;

						beginPhase('$digest');
						// Check for changes to browser url that happened in sync before the call to $digest
						$browser.$$checkUrlChange();

						if (this === $rootScope && applyAsyncId !== null) {
							// If this is the root scope, and $applyAsync has scheduled a deferred $apply(), then
							// cancel the scheduled $apply and flush the queue of expressions to be evaluated.
							$browser.defer.cancel(applyAsyncId);
							flushApplyAsync();
						}

						lastDirtyWatch = null;

						do { // "while dirty" loop
							dirty = false;
							current = target;

							// It's safe for asyncQueuePosition to be a local variable here because this loop can't
							// be reentered recursively. Calling $digest from a function passed to $evalAsync would
							// lead to a '$digest already in progress' error.
							for (var asyncQueuePosition = 0; asyncQueuePosition < asyncQueue.length; asyncQueuePosition++) {
								try {
									asyncTask = asyncQueue[asyncQueuePosition];
									fn = asyncTask.fn;
									fn(asyncTask.scope, asyncTask.locals);
								} catch (e) {
									$exceptionHandler(e);
								}
								lastDirtyWatch = null;
							}
							asyncQueue.length = 0;

							traverseScopesLoop:
							do { // "traverse the scopes" loop
								if ((watchers = !current.$$suspended && current.$$watchers)) {
									// process our watches
									watchers.$$digestWatchIndex = watchers.length;
									while (watchers.$$digestWatchIndex--) {
										try {
											watch = watchers[watchers.$$digestWatchIndex];
											// Most common watches are on primitives, in which case we can short
											// circuit it with === operator, only when === fails do we use .equals
											if (watch) {
												get = watch.get;
												if ((value = get(current)) !== (last = watch.last) &&
													!(watch.eq
														? equals(value, last)
														: (isNumberNaN(value) && isNumberNaN(last)))) {
													dirty = true;
													lastDirtyWatch = watch;
													watch.last = watch.eq ? copy(value, null) : value;
													fn = watch.fn;
													fn(value, ((last === initWatchVal) ? value : last), current);
													if (ttl < 5) {
														logIdx = 4 - ttl;
														if (!watchLog[logIdx]) watchLog[logIdx] = [];
														watchLog[logIdx].push({
															msg: isFunction(watch.exp) ? 'fn: ' + (watch.exp.name || watch.exp.toString()) : watch.exp,
															newVal: value,
															oldVal: last
														});
													}
												} else if (watch === lastDirtyWatch) {
													// If the most recently dirty watcher is now clean, short circuit since the remaining watchers
													// have already been tested.
													dirty = false;
													break traverseScopesLoop;
												}
											}
										} catch (e) {
											$exceptionHandler(e);
										}
									}
								}

								// Insanity Warning: scope depth-first traversal
								// yes, this code is a bit crazy, but it works and we have tests to prove it!
								// this piece should be kept in sync with the traversal in $broadcast
								// (though it differs due to having the extra check for $$suspended and does not
								// check $$listenerCount)
								if (!(next = ((!current.$$suspended && current.$$watchersCount && current.$$childHead) ||
									(current !== target && current.$$nextSibling)))) {
									while (current !== target && !(next = current.$$nextSibling)) {
										current = current.$parent;
									}
								}
							} while ((current = next));

							// `break traverseScopesLoop;` takes us to here

							if ((dirty || asyncQueue.length) && !(ttl--)) {
								clearPhase();
								throw $rootScopeMinErr('infdig',
									'{0} $digest() iterations reached. Aborting!\n' +
									'Watchers fired in the last 5 iterations: {1}',
									TTL, watchLog);
							}

						} while (dirty || asyncQueue.length);

						clearPhase();

						// postDigestQueuePosition isn't local here because this loop can be reentered recursively.
						while (postDigestQueuePosition < postDigestQueue.length) {
							try {
								postDigestQueue[postDigestQueuePosition++]();
							} catch (e) {
								$exceptionHandler(e);
							}
						}
						postDigestQueue.length = postDigestQueuePosition = 0;

						// Check for changes to browser url that happened during the $digest
						// (for which no event is fired; e.g. via `history.pushState()`)
						$browser.$$checkUrlChange();
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$suspend
					 * @kind function
					 *
					 * @description
					 * Suspend watchers of this scope subtree so that they will not be invoked during digest.
					 *
					 * This can be used to optimize your application when you know that running those watchers
					 * is redundant.
					 *
					 * **Warning**
					 *
					 * Suspending scopes from the digest cycle can have unwanted and difficult to debug results.
					 * Only use this approach if you are confident that you know what you are doing and have
					 * ample tests to ensure that bindings get updated as you expect.
					 *
					 * Some of the things to consider are:
					 *
					 * * Any external event on a directive/component will not trigger a digest while the hosting
					 *   scope is suspended - even if the event handler calls `$apply()` or `$rootScope.$digest()`.
					 * * Transcluded content exists on a scope that inherits from outside a directive but exists
					 *   as a child of the directive's containing scope. If the containing scope is suspended the
					 *   transcluded scope will also be suspended, even if the scope from which the transcluded
					 *   scope inherits is not suspended.
					 * * Multiple directives trying to manage the suspended status of a scope can confuse each other:
					 *    * A call to `$suspend()` on an already suspended scope is a no-op.
					 *    * A call to `$resume()` on a non-suspended scope is a no-op.
					 *    * If two directives suspend a scope, then one of them resumes the scope, the scope will no
					 *      longer be suspended. This could result in the other directive believing a scope to be
					 *      suspended when it is not.
					 * * If a parent scope is suspended then all its descendants will be also excluded from future
					 *   digests whether or not they have been suspended themselves. Note that this also applies to
					 *   isolate child scopes.
					 * * Calling `$digest()` directly on a descendant of a suspended scope will still run the watchers
					 *   for that scope and its descendants. When digesting we only check whether the current scope is
					 *   locally suspended, rather than checking whether it has a suspended ancestor.
					 * * Calling `$resume()` on a scope that has a suspended ancestor will not cause the scope to be
					 *   included in future digests until all its ancestors have been resumed.
					 * * Resolved promises, e.g. from explicit `$q` deferreds and `$http` calls, trigger `$apply()`
					 *   against the `$rootScope` and so will still trigger a global digest even if the promise was
					 *   initiated by a component that lives on a suspended scope.
					 */
					$suspend: function () {
						this.$$suspended = true;
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$isSuspended
					 * @kind function
					 *
					 * @description
					 * Call this method to determine if this scope has been explicitly suspended. It will not
					 * tell you whether an ancestor has been suspended.
					 * To determine if this scope will be excluded from a digest triggered at the $rootScope,
					 * for example, you must check all its ancestors:
					 *
					 * ```
					 * function isExcludedFromDigest(scope) {
					 *   while(scope) {
					 *     if (scope.$isSuspended()) return true;
					 *     scope = scope.$parent;
					 *   }
					 *   return false;
					 * ```
					 *
					 * Be aware that a scope may not be included in digests if it has a suspended ancestor,
					 * even if `$isSuspended()` returns false.
					 *
					 * @returns true if the current scope has been suspended.
					 */
					$isSuspended: function () {
						return this.$$suspended;
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$resume
					 * @kind function
					 *
					 * @description
					 * Resume watchers of this scope subtree in case it was suspended.
					 *
					 * See {@link $rootScope.Scope#$suspend} for information about the dangers of using this approach.
					 */
					$resume: function () {
						this.$$suspended = false;
					},

					/**
					 * @ngdoc event
					 * @name $rootScope.Scope#$destroy
					 * @eventType broadcast on scope being destroyed
					 *
					 * @description
					 * Broadcasted when a scope and its children are being destroyed.
					 *
					 * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
					 * clean up DOM bindings before an element is removed from the DOM.
					 */

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$destroy
					 * @kind function
					 *
					 * @description
					 * Removes the current scope (and all of its children) from the parent scope. Removal implies
					 * that calls to {@link ng.$rootScope.Scope#$digest $digest()} will no longer
					 * propagate to the current scope and its children. Removal also implies that the current
					 * scope is eligible for garbage collection.
					 *
					 * The `$destroy()` is usually used by directives such as
					 * {@link ng.directive:ngRepeat ngRepeat} for managing the
					 * unrolling of the loop.
					 *
					 * Just before a scope is destroyed, a `$destroy` event is broadcasted on this scope.
					 * Application code can register a `$destroy` event handler that will give it a chance to
					 * perform any necessary cleanup.
					 *
					 * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
					 * clean up DOM bindings before an element is removed from the DOM.
					 */
					$destroy: function () {
						// We can't destroy a scope that has been already destroyed.
						if (this.$$destroyed) return;
						var parent = this.$parent;

						this.$broadcast('$destroy');
						this.$$destroyed = true;

						if (this === $rootScope) {
							//Remove handlers attached to window when $rootScope is removed
							$browser.$$applicationDestroyed();
						}

						incrementWatchersCount(this, -this.$$watchersCount);
						for (var eventName in this.$$listenerCount) {
							decrementListenerCount(this, this.$$listenerCount[eventName], eventName);
						}

						// sever all the references to parent scopes (after this cleanup, the current scope should
						// not be retained by any of our references and should be eligible for garbage collection)
						if (parent && parent.$$childHead === this) parent.$$childHead = this.$$nextSibling;
						if (parent && parent.$$childTail === this) parent.$$childTail = this.$$prevSibling;
						if (this.$$prevSibling) this.$$prevSibling.$$nextSibling = this.$$nextSibling;
						if (this.$$nextSibling) this.$$nextSibling.$$prevSibling = this.$$prevSibling;

						// Disable listeners, watchers and apply/digest methods
						this.$destroy = this.$digest = this.$apply = this.$evalAsync = this.$applyAsync = noop;
						this.$on = this.$watch = this.$watchGroup = function () { return noop; };
						this.$$listeners = {};

						// Disconnect the next sibling to prevent `cleanUpScope` destroying those too
						this.$$nextSibling = null;
						cleanUpScope(this);
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$eval
					 * @kind function
					 *
					 * @description
					 * Executes the `expression` on the current scope and returns the result. Any exceptions in
					 * the expression are propagated (uncaught). This is useful when evaluating AngularJS
					 * expressions.
					 *
					 * @example
					 * ```js
						 var scope = ng.$rootScope.Scope();
						 scope.a = 1;
						 scope.b = 2;
			  
						 expect(scope.$eval('a+b')).toEqual(3);
						 expect(scope.$eval(function(scope){ return scope.a + scope.b; })).toEqual(3);
					 * ```
					 *
					 * @param {(string|function())=} expression An AngularJS expression to be executed.
					 *
					 *    - `string`: execute using the rules as defined in  {@link guide/expression expression}.
					 *    - `function(scope)`: execute the function with the current `scope` parameter.
					 *
					 * @param {(object)=} locals Local variables object, useful for overriding values in scope.
					 * @returns {*} The result of evaluating the expression.
					 */
					$eval: function (expr, locals) {
						return $parse(expr)(this, locals);
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$evalAsync
					 * @kind function
					 *
					 * @description
					 * Executes the expression on the current scope at a later point in time.
					 *
					 * The `$evalAsync` makes no guarantees as to when the `expression` will be executed, only
					 * that:
					 *
					 *   - it will execute after the function that scheduled the evaluation (preferably before DOM
					 *     rendering).
					 *   - at least one {@link ng.$rootScope.Scope#$digest $digest cycle} will be performed after
					 *     `expression` execution.
					 *
					 * Any exceptions from the execution of the expression are forwarded to the
					 * {@link ng.$exceptionHandler $exceptionHandler} service.
					 *
					 * __Note:__ if this function is called outside of a `$digest` cycle, a new `$digest` cycle
					 * will be scheduled. However, it is encouraged to always call code that changes the model
					 * from within an `$apply` call. That includes code evaluated via `$evalAsync`.
					 *
					 * @param {(string|function())=} expression An AngularJS expression to be executed.
					 *
					 *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
					 *    - `function(scope)`: execute the function with the current `scope` parameter.
					 *
					 * @param {(object)=} locals Local variables object, useful for overriding values in scope.
					 */
					$evalAsync: function (expr, locals) {
						// if we are outside of an $digest loop and this is the first time we are scheduling async
						// task also schedule async auto-flush
						if (!$rootScope.$$phase && !asyncQueue.length) {
							$browser.defer(function () {
								if (asyncQueue.length) {
									$rootScope.$digest();
								}
							}, null, '$evalAsync');
						}

						asyncQueue.push({ scope: this, fn: $parse(expr), locals: locals });
					},

					$$postDigest: function (fn) {
						postDigestQueue.push(fn);
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$apply
					 * @kind function
					 *
					 * @description
					 * `$apply()` is used to execute an expression in AngularJS from outside of the AngularJS
					 * framework. (For example from browser DOM events, setTimeout, XHR or third party libraries).
					 * Because we are calling into the AngularJS framework we need to perform proper scope life
					 * cycle of {@link ng.$exceptionHandler exception handling},
					 * {@link ng.$rootScope.Scope#$digest executing watches}.
					 *
					 * **Life cycle: Pseudo-Code of `$apply()`**
					 *
					 * ```js
						 function $apply(expr) {
						   try {
							 return $eval(expr);
						   } catch (e) {
							 $exceptionHandler(e);
						   } finally {
							 $root.$digest();
						   }
						 }
					 * ```
					 *
					 *
					 * Scope's `$apply()` method transitions through the following stages:
					 *
					 * 1. The {@link guide/expression expression} is executed using the
					 *    {@link ng.$rootScope.Scope#$eval $eval()} method.
					 * 2. Any exceptions from the execution of the expression are forwarded to the
					 *    {@link ng.$exceptionHandler $exceptionHandler} service.
					 * 3. The {@link ng.$rootScope.Scope#$watch watch} listeners are fired immediately after the
					 *    expression was executed using the {@link ng.$rootScope.Scope#$digest $digest()} method.
					 *
					 *
					 * @param {(string|function())=} exp An AngularJS expression to be executed.
					 *
					 *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
					 *    - `function(scope)`: execute the function with current `scope` parameter.
					 *
					 * @returns {*} The result of evaluating the expression.
					 */
					$apply: function (expr) {
						try {
							beginPhase('$apply');
							try {
								return this.$eval(expr);
							} finally {
								clearPhase();
							}
						} catch (e) {
							$exceptionHandler(e);
						} finally {
							try {
								$rootScope.$digest();
							} catch (e) {
								$exceptionHandler(e);
								// eslint-disable-next-line no-unsafe-finally
								throw e;
							}
						}
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$applyAsync
					 * @kind function
					 *
					 * @description
					 * Schedule the invocation of $apply to occur at a later time. The actual time difference
					 * varies across browsers, but is typically around ~10 milliseconds.
					 *
					 * This can be used to queue up multiple expressions which need to be evaluated in the same
					 * digest.
					 *
					 * @param {(string|function())=} exp An AngularJS expression to be executed.
					 *
					 *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
					 *    - `function(scope)`: execute the function with current `scope` parameter.
					 */
					$applyAsync: function (expr) {
						var scope = this;
						if (expr) {
							applyAsyncQueue.push($applyAsyncExpression);
						}
						expr = $parse(expr);
						scheduleApplyAsync();

						function $applyAsyncExpression() {
							scope.$eval(expr);
						}
					},

					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$on
					 * @kind function
					 *
					 * @description
					 * Listens on events of a given type. See {@link ng.$rootScope.Scope#$emit $emit} for
					 * discussion of event life cycle.
					 *
					 * The event listener function format is: `function(event, args...)`. The `event` object
					 * passed into the listener has the following attributes:
					 *
					 *   - `targetScope` - `{Scope}`: the scope on which the event was `$emit`-ed or
					 *     `$broadcast`-ed.
					 *   - `currentScope` - `{Scope}`: the scope that is currently handling the event. Once the
					 *     event propagates through the scope hierarchy, this property is set to null.
					 *   - `name` - `{string}`: name of the event.
					 *   - `stopPropagation` - `{function=}`: calling `stopPropagation` function will cancel
					 *     further event propagation (available only for events that were `$emit`-ed).
					 *   - `preventDefault` - `{function}`: calling `preventDefault` sets `defaultPrevented` flag
					 *     to true.
					 *   - `defaultPrevented` - `{boolean}`: true if `preventDefault` was called.
					 *
					 * @param {string} name Event name to listen on.
					 * @param {function(event, ...args)} listener Function to call when the event is emitted.
					 * @returns {function()} Returns a deregistration function for this listener.
					 */
					$on: function (name, listener) {
						var namedListeners = this.$$listeners[name];
						if (!namedListeners) {
							this.$$listeners[name] = namedListeners = [];
						}
						namedListeners.push(listener);

						var current = this;
						do {
							if (!current.$$listenerCount[name]) {
								current.$$listenerCount[name] = 0;
							}
							current.$$listenerCount[name]++;
						} while ((current = current.$parent));

						var self = this;
						return function () {
							var indexOfListener = namedListeners.indexOf(listener);
							if (indexOfListener !== -1) {
								// Use delete in the hope of the browser deallocating the memory for the array entry,
								// while not shifting the array indexes of other listeners.
								// See issue https://github.com/angular/angular.js/issues/16135
								delete namedListeners[indexOfListener];
								decrementListenerCount(self, 1, name);
							}
						};
					},


					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$emit
					 * @kind function
					 *
					 * @description
					 * Dispatches an event `name` upwards through the scope hierarchy notifying the
					 * registered {@link ng.$rootScope.Scope#$on} listeners.
					 *
					 * The event life cycle starts at the scope on which `$emit` was called. All
					 * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
					 * notified. Afterwards, the event traverses upwards toward the root scope and calls all
					 * registered listeners along the way. The event will stop propagating if one of the listeners
					 * cancels it.
					 *
					 * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
					 * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
					 *
					 * @param {string} name Event name to emit.
					 * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
					 * @return {Object} Event object (see {@link ng.$rootScope.Scope#$on}).
					 */
					$emit: function (name, args) {
						var empty = [],
							namedListeners,
							scope = this,
							stopPropagation = false,
							event = {
								name: name,
								targetScope: scope,
								stopPropagation: function () { stopPropagation = true; },
								preventDefault: function () {
									event.defaultPrevented = true;
								},
								defaultPrevented: false
							},
							listenerArgs = concat([event], arguments, 1),
							i, length;

						do {
							namedListeners = scope.$$listeners[name] || empty;
							event.currentScope = scope;
							for (i = 0, length = namedListeners.length; i < length; i++) {

								// if listeners were deregistered, defragment the array
								if (!namedListeners[i]) {
									namedListeners.splice(i, 1);
									i--;
									length--;
									continue;
								}
								try {
									//allow all listeners attached to the current scope to run
									namedListeners[i].apply(null, listenerArgs);
								} catch (e) {
									$exceptionHandler(e);
								}
							}
							//if any listener on the current scope stops propagation, prevent bubbling
							if (stopPropagation) {
								break;
							}
							//traverse upwards
							scope = scope.$parent;
						} while (scope);

						event.currentScope = null;

						return event;
					},


					/**
					 * @ngdoc method
					 * @name $rootScope.Scope#$broadcast
					 * @kind function
					 *
					 * @description
					 * Dispatches an event `name` downwards to all child scopes (and their children) notifying the
					 * registered {@link ng.$rootScope.Scope#$on} listeners.
					 *
					 * The event life cycle starts at the scope on which `$broadcast` was called. All
					 * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
					 * notified. Afterwards, the event propagates to all direct and indirect scopes of the current
					 * scope and calls all registered listeners along the way. The event cannot be canceled.
					 *
					 * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
					 * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
					 *
					 * @param {string} name Event name to broadcast.
					 * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
					 * @return {Object} Event object, see {@link ng.$rootScope.Scope#$on}
					 */
					$broadcast: function (name, args) {
						var target = this,
							current = target,
							next = target,
							event = {
								name: name,
								targetScope: target,
								preventDefault: function () {
									event.defaultPrevented = true;
								},
								defaultPrevented: false
							};

						if (!target.$$listenerCount[name]) return event;

						var listenerArgs = concat([event], arguments, 1),
							listeners, i, length;

						//down while you can, then up and next sibling or up and next sibling until back at root
						while ((current = next)) {
							event.currentScope = current;
							listeners = current.$$listeners[name] || [];
							for (i = 0, length = listeners.length; i < length; i++) {
								// if listeners were deregistered, defragment the array
								if (!listeners[i]) {
									listeners.splice(i, 1);
									i--;
									length--;
									continue;
								}

								try {
									listeners[i].apply(null, listenerArgs);
								} catch (e) {
									$exceptionHandler(e);
								}
							}

							// Insanity Warning: scope depth-first traversal
							// yes, this code is a bit crazy, but it works and we have tests to prove it!
							// this piece should be kept in sync with the traversal in $digest
							// (though it differs due to having the extra check for $$listenerCount and
							// does not check $$suspended)
							if (!(next = ((current.$$listenerCount[name] && current.$$childHead) ||
								(current !== target && current.$$nextSibling)))) {
								while (current !== target && !(next = current.$$nextSibling)) {
									current = current.$parent;
								}
							}
						}

						event.currentScope = null;
						return event;
					}
				};

				var $rootScope = new Scope();

				//The internal queues. Expose them on the $rootScope for debugging/testing purposes.
				var asyncQueue = $rootScope.$$asyncQueue = [];
				var postDigestQueue = $rootScope.$$postDigestQueue = [];
				var applyAsyncQueue = $rootScope.$$applyAsyncQueue = [];

				var postDigestQueuePosition = 0;

				return $rootScope;


				function beginPhase(phase) {
					if ($rootScope.$$phase) {
						throw $rootScopeMinErr('inprog', '{0} already in progress', $rootScope.$$phase);
					}

					$rootScope.$$phase = phase;
				}

				function clearPhase() {
					$rootScope.$$phase = null;
				}

				function incrementWatchersCount(current, count) {
					do {
						current.$$watchersCount += count;
					} while ((current = current.$parent));
				}

				function decrementListenerCount(current, count, name) {
					do {
						current.$$listenerCount[name] -= count;

						if (current.$$listenerCount[name] === 0) {
							delete current.$$listenerCount[name];
						}
					} while ((current = current.$parent));
				}

				/**
				 * function used as an initial value for watchers.
				 * because it's unique we can easily tell it apart from other values
				 */
				function initWatchVal() { }

				function flushApplyAsync() {
					while (applyAsyncQueue.length) {
						try {
							applyAsyncQueue.shift()();
						} catch (e) {
							$exceptionHandler(e);
						}
					}
					applyAsyncId = null;
				}

				function scheduleApplyAsync() {
					if (applyAsyncId === null) {
						applyAsyncId = $browser.defer(function () {
							$rootScope.$apply(flushApplyAsync);
						}, null, '$applyAsync');
					}
				}
			}];
	}

	/**
	 * @ngdoc service
	 * @name $rootElement
	 *
	 * @description
	 * The root element of AngularJS application. This is either the element where {@link
	 * ng.directive:ngApp ngApp} was declared or the element passed into
	 * {@link angular.bootstrap}. The element represents the root element of application. It is also the
	 * location where the application's {@link auto.$injector $injector} service gets
	 * published, and can be retrieved using `$rootElement.injector()`.
	 */


	// the implementation is in angular.bootstrap

	/**
	 * @this
	 * @description
	 * Private service to sanitize uris for links and images. Used by $compile and $sanitize.
	 */
	function $$SanitizeUriProvider() {

		var aHrefSanitizationTrustedUrlList = /^\s*(https?|s?ftp|mailto|tel|file):/,
			imgSrcSanitizationTrustedUrlList = /^\s*((https?|ftp|file|blob):|data:image\/)/;

		/**
		 * @description
		 * Retrieves or overrides the default regular expression that is used for determining trusted safe
		 * urls during a[href] sanitization.
		 *
		 * The sanitization is a security measure aimed at prevent XSS attacks via HTML anchor links.
		 *
		 * Any url due to be assigned to an `a[href]` attribute via interpolation is marked as requiring
		 * the $sce.URL security context. When interpolation occurs a call is made to `$sce.trustAsUrl(url)`
		 * which in turn may call `$$sanitizeUri(url, isMedia)` to sanitize the potentially malicious URL.
		 *
		 * If the URL matches the `aHrefSanitizationTrustedUrlList` regular expression, it is returned unchanged.
		 *
		 * If there is no match the URL is returned prefixed with `'unsafe:'` to ensure that when it is written
		 * to the DOM it is inactive and potentially malicious code will not be executed.
		 *
		 * @param {RegExp=} regexp New regexp to trust urls with.
		 * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
		 *    chaining otherwise.
		 */
		this.aHrefSanitizationTrustedUrlList = function (regexp) {
			if (isDefined(regexp)) {
				aHrefSanitizationTrustedUrlList = regexp;
				return this;
			}
			return aHrefSanitizationTrustedUrlList;
		};


		/**
		 * @description
		 * Retrieves or overrides the default regular expression that is used for determining trusted safe
		 * urls during img[src] sanitization.
		 *
		 * The sanitization is a security measure aimed at prevent XSS attacks via HTML image src links.
		 *
		 * Any URL due to be assigned to an `img[src]` attribute via interpolation is marked as requiring
		 * the $sce.MEDIA_URL security context. When interpolation occurs a call is made to
		 * `$sce.trustAsMediaUrl(url)` which in turn may call `$$sanitizeUri(url, isMedia)` to sanitize
		 * the potentially malicious URL.
		 *
		 * If the URL matches the `imgSrcSanitizationTrustedUrlList` regular expression, it is returned
		 * unchanged.
		 *
		 * If there is no match the URL is returned prefixed with `'unsafe:'` to ensure that when it is written
		 * to the DOM it is inactive and potentially malicious code will not be executed.
		 *
		 * @param {RegExp=} regexp New regexp to trust urls with.
		 * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
		 *    chaining otherwise.
		 */
		this.imgSrcSanitizationTrustedUrlList = function (regexp) {
			if (isDefined(regexp)) {
				imgSrcSanitizationTrustedUrlList = regexp;
				return this;
			}
			return imgSrcSanitizationTrustedUrlList;
		};

		this.$get = function () {
			return function sanitizeUri(uri, isMediaUrl) {
				// if (!uri) return uri;
				var regex = isMediaUrl ? imgSrcSanitizationTrustedUrlList : aHrefSanitizationTrustedUrlList;
				var normalizedVal = urlResolve(uri && uri.trim()).href;
				if (normalizedVal !== '' && !normalizedVal.match(regex)) {
					return 'unsafe:' + normalizedVal;
				}
				return uri;
			};
		};
	}

	/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
	 *     Any commits to this file should be reviewed with security in mind.  *
	 *   Changes to this file can potentially create security vulnerabilities. *
	 *          An approval from 2 Core members with history of modifying      *
	 *                         this file is required.                          *
	 *                                                                         *
	 *  Does the change somehow allow for arbitrary javascript to be executed? *
	 *    Or allows for someone to change the prototype of built-in objects?   *
	 *     Or gives undesired access to variables likes document or window?    *
	 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

	/* exported $SceProvider, $SceDelegateProvider */

	var $sceMinErr = minErr('$sce');

	var SCE_CONTEXTS = {
		// HTML is used when there's HTML rendered (e.g. ng-bind-html, iframe srcdoc binding).
		HTML: 'html',

		// Style statements or stylesheets. Currently unused in AngularJS.
		CSS: 'css',

		// An URL used in a context where it refers to the source of media, which are not expected to be run
		// as scripts, such as an image, audio, video, etc.
		MEDIA_URL: 'mediaUrl',

		// An URL used in a context where it does not refer to a resource that loads code.
		// A value that can be trusted as a URL can also trusted as a MEDIA_URL.
		URL: 'url',

		// RESOURCE_URL is a subtype of URL used where the referred-to resource could be interpreted as
		// code. (e.g. ng-include, script src binding, templateUrl)
		// A value that can be trusted as a RESOURCE_URL, can also trusted as a URL and a MEDIA_URL.
		RESOURCE_URL: 'resourceUrl',

		// Script. Currently unused in AngularJS.
		JS: 'js'
	};

	// Helper functions follow.

	var UNDERSCORE_LOWERCASE_REGEXP = /_([a-z])/g;

	function snakeToCamel(name) {
		return name
			.replace(UNDERSCORE_LOWERCASE_REGEXP, fnCamelCaseReplace);
	}

	function adjustMatcher(matcher) {
		if (matcher === 'self') {
			return matcher;
		} else if (isString(matcher)) {
			// Strings match exactly except for 2 wildcards - '*' and '**'.
			// '*' matches any character except those from the set ':/.?&'.
			// '**' matches any character (like .* in a RegExp).
			// More than 2 *'s raises an error as it's ill defined.
			if (matcher.indexOf('***') > -1) {
				throw $sceMinErr('iwcard',
					'Illegal sequence *** in string matcher.  String: {0}', matcher);
			}
			matcher = escapeForRegexp(matcher).
				replace(/\\\*\\\*/g, '.*').
				replace(/\\\*/g, '[^:/.?&;]*');
			return new RegExp('^' + matcher + '$');
		} else if (isRegExp(matcher)) {
			// The only other type of matcher allowed is a Regexp.
			// Match entire URL / disallow partial matches.
			// Flags are reset (i.e. no global, ignoreCase or multiline)
			return new RegExp('^' + matcher.source + '$');
		} else {
			throw $sceMinErr('imatcher',
				'Matchers may only be "self", string patterns or RegExp objects');
		}
	}


	function adjustMatchers(matchers) {
		var adjustedMatchers = [];
		if (isDefined(matchers)) {
			forEach(matchers, function (matcher) {
				adjustedMatchers.push(adjustMatcher(matcher));
			});
		}
		return adjustedMatchers;
	}


	/**
	 * @ngdoc service
	 * @name $sceDelegate
	 * @kind function
	 *
	 * @description
	 *
	 * `$sceDelegate` is a service that is used by the `$sce` service to provide {@link ng.$sce Strict
	 * Contextual Escaping (SCE)} services to AngularJS.
	 *
	 * For an overview of this service and the functionnality it provides in AngularJS, see the main
	 * page for {@link ng.$sce SCE}. The current page is targeted for developers who need to alter how
	 * SCE works in their application, which shouldn't be needed in most cases.
	 *
	 * <div class="alert alert-danger">
	 * AngularJS strongly relies on contextual escaping for the security of bindings: disabling or
	 * modifying this might cause cross site scripting (XSS) vulnerabilities. For libraries owners,
	 * changes to this service will also influence users, so be extra careful and document your changes.
	 * </div>
	 *
	 * Typically, you would configure or override the {@link ng.$sceDelegate $sceDelegate} instead of
	 * the `$sce` service to customize the way Strict Contextual Escaping works in AngularJS.  This is
	 * because, while the `$sce` provides numerous shorthand methods, etc., you really only need to
	 * override 3 core functions (`trustAs`, `getTrusted` and `valueOf`) to replace the way things
	 * work because `$sce` delegates to `$sceDelegate` for these operations.
	 *
	 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} to configure this service.
	 *
	 * The default instance of `$sceDelegate` should work out of the box with little pain.  While you
	 * can override it completely to change the behavior of `$sce`, the common case would
	 * involve configuring the {@link ng.$sceDelegateProvider $sceDelegateProvider} instead by setting
	 * your own trusted and banned resource lists for trusting URLs used for loading AngularJS resources
	 * such as templates.  Refer {@link ng.$sceDelegateProvider#trustedResourceUrlList
	 * $sceDelegateProvider.trustedResourceUrlList} and {@link
	 * ng.$sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider.bannedResourceUrlList}
	 */

	/**
	 * @ngdoc provider
	 * @name $sceDelegateProvider
	 * @this
	 *
	 * @description
	 *
	 * The `$sceDelegateProvider` provider allows developers to configure the {@link ng.$sceDelegate
	 * $sceDelegate service}, used as a delegate for {@link ng.$sce Strict Contextual Escaping (SCE)}.
	 *
	 * The `$sceDelegateProvider` allows one to get/set the `trustedResourceUrlList` and
	 * `bannedResourceUrlList` used to ensure that the URLs used for sourcing AngularJS templates and
	 * other script-running URLs are safe (all places that use the `$sce.RESOURCE_URL` context). See
	 * {@link ng.$sceDelegateProvider#trustedResourceUrlList
	 * $sceDelegateProvider.trustedResourceUrlList} and
	 * {@link ng.$sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider.bannedResourceUrlList},
	 *
	 * For the general details about this service in AngularJS, read the main page for {@link ng.$sce
	 * Strict Contextual Escaping (SCE)}.
	 *
	 * **Example**:  Consider the following case. <a name="example"></a>
	 *
	 * - your app is hosted at url `http://myapp.example.com/`
	 * - but some of your templates are hosted on other domains you control such as
	 *   `http://srv01.assets.example.com/`, `http://srv02.assets.example.com/`, etc.
	 * - and you have an open redirect at `http://myapp.example.com/clickThru?...`.
	 *
	 * Here is what a secure configuration for this scenario might look like:
	 *
	 * ```
	 *  angular.module('myApp', []).config(function($sceDelegateProvider) {
	 *    $sceDelegateProvider.trustedResourceUrlList([
	 *      // Allow same origin resource loads.
	 *      'self',
	 *      // Allow loading from our assets domain.  Notice the difference between * and **.
	 *      'http://srv*.assets.example.com/**'
	 *    ]);
	 *
	 *    // The banned resource URL list overrides the trusted resource URL list so the open redirect
	 *    // here is blocked.
	 *    $sceDelegateProvider.bannedResourceUrlList([
	 *      'http://myapp.example.com/clickThru**'
	 *    ]);
	 *  });
	 * ```
	 * Note that an empty trusted resource URL list will block every resource URL from being loaded, and will require
	 * you to manually mark each one as trusted with `$sce.trustAsResourceUrl`. However, templates
	 * requested by {@link ng.$templateRequest $templateRequest} that are present in
	 * {@link ng.$templateCache $templateCache} will not go through this check. If you have a mechanism
	 * to populate your templates in that cache at config time, then it is a good idea to remove 'self'
	 * from the trusted resource URL lsit. This helps to mitigate the security impact of certain types
	 * of issues, like for instance attacker-controlled `ng-includes`.
	 */

	function $SceDelegateProvider() {
		this.SCE_CONTEXTS = SCE_CONTEXTS;

		// Resource URLs can also be trusted by policy.
		var trustedResourceUrlList = ['self'],
			bannedResourceUrlList = [];

		/**
		 * @ngdoc method
		 * @name $sceDelegateProvider#trustedResourceUrlList
		 * @kind function
		 *
		 * @param {Array=} trustedResourceUrlList When provided, replaces the trustedResourceUrlList with
		 *     the value provided.  This must be an array or null.  A snapshot of this array is used so
		 *     further changes to the array are ignored.
		 *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
		 *     allowed in this array.
		 *
		 * @return {Array} The currently set trusted resource URL array.
		 *
		 * @description
		 * Sets/Gets the list trusted of resource URLs.
		 *
		 * The **default value** when no `trustedResourceUrlList` has been explicitly set is `['self']`
		 * allowing only same origin resource requests.
		 *
		 * <div class="alert alert-warning">
		 * **Note:** the default `trustedResourceUrlList` of 'self' is not recommended if your app shares
		 * its origin with other apps! It is a good idea to limit it to only your application's directory.
		 * </div>
		 */
		this.trustedResourceUrlList = function (value) {
			if (arguments.length) {
				trustedResourceUrlList = adjustMatchers(value);
			}
			return trustedResourceUrlList;
		};

		/**
		 * @ngdoc method
		 * @name $sceDelegateProvider#resourceUrlWhitelist
		 * @kind function
		 *
		 * @deprecated
		 * sinceVersion="1.8.1"
		 *
		 * This method is deprecated. Use {@link $sceDelegateProvider#trustedResourceUrlList
		 * trustedResourceUrlList} instead.
		 */
		Object.defineProperty(this, 'resourceUrlWhitelist', {
			get: function () {
				return this.trustedResourceUrlList;
			},
			set: function (value) {
				this.trustedResourceUrlList = value;
			}
		});

		/**
		 * @ngdoc method
		 * @name $sceDelegateProvider#bannedResourceUrlList
		 * @kind function
		 *
		 * @param {Array=} bannedResourceUrlList When provided, replaces the `bannedResourceUrlList` with
		 *     the value provided. This must be an array or null. A snapshot of this array is used so
		 *     further changes to the array are ignored.</p><p>
		 *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
		 *     allowed in this array.</p><p>
		 *     The typical usage for the `bannedResourceUrlList` is to **block
		 *     [open redirects](http://cwe.mitre.org/data/definitions/601.html)** served by your domain as
		 *     these would otherwise be trusted but actually return content from the redirected domain.
		 *     </p><p>
		 *     Finally, **the banned resource URL list overrides the trusted resource URL list** and has
		 *     the final say.
		 *
		 * @return {Array} The currently set `bannedResourceUrlList` array.
		 *
		 * @description
		 * Sets/Gets the `bannedResourceUrlList` of trusted resource URLs.
		 *
		 * The **default value** when no trusted resource URL list has been explicitly set is the empty
		 * array (i.e. there is no `bannedResourceUrlList`.)
		 */
		this.bannedResourceUrlList = function (value) {
			if (arguments.length) {
				bannedResourceUrlList = adjustMatchers(value);
			}
			return bannedResourceUrlList;
		};

		/**
		 * @ngdoc method
		 * @name $sceDelegateProvider#resourceUrlBlacklist
		 * @kind function
		 *
		 * @deprecated
		 * sinceVersion="1.8.1"
		 *
		 * This method is deprecated. Use {@link $sceDelegateProvider#bannedResourceUrlList
		 * bannedResourceUrlList} instead.
		 */
		Object.defineProperty(this, 'resourceUrlBlacklist', {
			get: function () {
				return this.bannedResourceUrlList;
			},
			set: function (value) {
				this.bannedResourceUrlList = value;
			}
		});

		this.$get = ['$injector', '$$sanitizeUri', function ($injector, $$sanitizeUri) {

			var htmlSanitizer = function htmlSanitizer(html) {
				throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
			};

			if ($injector.has('$sanitize')) {
				htmlSanitizer = $injector.get('$sanitize');
			}


			function matchUrl(matcher, parsedUrl) {
				if (matcher === 'self') {
					return urlIsSameOrigin(parsedUrl) || urlIsSameOriginAsBaseUrl(parsedUrl);
				} else {
					// definitely a regex.  See adjustMatchers()
					return !!matcher.exec(parsedUrl.href);
				}
			}

			function isResourceUrlAllowedByPolicy(url) {
				var parsedUrl = urlResolve(url.toString());
				var i, n, allowed = false;
				// Ensure that at least one item from the trusted resource URL list allows this url.
				for (i = 0, n = trustedResourceUrlList.length; i < n; i++) {
					if (matchUrl(trustedResourceUrlList[i], parsedUrl)) {
						allowed = true;
						break;
					}
				}
				if (allowed) {
					// Ensure that no item from the banned resource URL list has blocked this url.
					for (i = 0, n = bannedResourceUrlList.length; i < n; i++) {
						if (matchUrl(bannedResourceUrlList[i], parsedUrl)) {
							allowed = false;
							break;
						}
					}
				}
				return allowed;
			}

			function generateHolderType(Base) {
				var holderType = function TrustedValueHolderType(trustedValue) {
					this.$$unwrapTrustedValue = function () {
						return trustedValue;
					};
				};
				if (Base) {
					holderType.prototype = new Base();
				}
				holderType.prototype.valueOf = function sceValueOf() {
					return this.$$unwrapTrustedValue();
				};
				holderType.prototype.toString = function sceToString() {
					return this.$$unwrapTrustedValue().toString();
				};
				return holderType;
			}

			var trustedValueHolderBase = generateHolderType(),
				byType = {};

			byType[SCE_CONTEXTS.HTML] = generateHolderType(trustedValueHolderBase);
			byType[SCE_CONTEXTS.CSS] = generateHolderType(trustedValueHolderBase);
			byType[SCE_CONTEXTS.MEDIA_URL] = generateHolderType(trustedValueHolderBase);
			byType[SCE_CONTEXTS.URL] = generateHolderType(byType[SCE_CONTEXTS.MEDIA_URL]);
			byType[SCE_CONTEXTS.JS] = generateHolderType(trustedValueHolderBase);
			byType[SCE_CONTEXTS.RESOURCE_URL] = generateHolderType(byType[SCE_CONTEXTS.URL]);

			/**
			 * @ngdoc method
			 * @name $sceDelegate#trustAs
			 *
			 * @description
			 * Returns a trusted representation of the parameter for the specified context. This trusted
			 * object will later on be used as-is, without any security check, by bindings or directives
			 * that require this security context.
			 * For instance, marking a string as trusted for the `$sce.HTML` context will entirely bypass
			 * the potential `$sanitize` call in corresponding `$sce.HTML` bindings or directives, such as
			 * `ng-bind-html`. Note that in most cases you won't need to call this function: if you have the
			 * sanitizer loaded, passing the value itself will render all the HTML that does not pose a
			 * security risk.
			 *
			 * See {@link ng.$sceDelegate#getTrusted getTrusted} for the function that will consume those
			 * trusted values, and {@link ng.$sce $sce} for general documentation about strict contextual
			 * escaping.
			 *
			 * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
			 *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
			 *
			 * @param {*} value The value that should be considered trusted.
			 * @return {*} A trusted representation of value, that can be used in the given context.
			 */
			function trustAs(type, trustedValue) {
				var Constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
				if (!Constructor) {
					throw $sceMinErr('icontext',
						'Attempted to trust a value in invalid context. Context: {0}; Value: {1}',
						type, trustedValue);
				}
				if (trustedValue === null || isUndefined(trustedValue) || trustedValue === '') {
					return trustedValue;
				}
				// All the current contexts in SCE_CONTEXTS happen to be strings.  In order to avoid trusting
				// mutable objects, we ensure here that the value passed in is actually a string.
				if (typeof trustedValue !== 'string') {
					throw $sceMinErr('itype',
						'Attempted to trust a non-string value in a content requiring a string: Context: {0}',
						type);
				}
				return new Constructor(trustedValue);
			}

			/**
			 * @ngdoc method
			 * @name $sceDelegate#valueOf
			 *
			 * @description
			 * If the passed parameter had been returned by a prior call to {@link ng.$sceDelegate#trustAs
			 * `$sceDelegate.trustAs`}, returns the value that had been passed to {@link
			 * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}.
			 *
			 * If the passed parameter is not a value that had been returned by {@link
			 * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}, it must be returned as-is.
			 *
			 * @param {*} value The result of a prior {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}
			 *     call or anything else.
			 * @return {*} The `value` that was originally provided to {@link ng.$sceDelegate#trustAs
			 *     `$sceDelegate.trustAs`} if `value` is the result of such a call.  Otherwise, returns
			 *     `value` unchanged.
			 */
			function valueOf(maybeTrusted) {
				if (maybeTrusted instanceof trustedValueHolderBase) {
					return maybeTrusted.$$unwrapTrustedValue();
				} else {
					return maybeTrusted;
				}
			}

			/**
			 * @ngdoc method
			 * @name $sceDelegate#getTrusted
			 *
			 * @description
			 * Given an object and a security context in which to assign it, returns a value that's safe to
			 * use in this context, which was represented by the parameter. To do so, this function either
			 * unwraps the safe type it has been given (for instance, a {@link ng.$sceDelegate#trustAs
			 * `$sceDelegate.trustAs`} result), or it might try to sanitize the value given, depending on
			 * the context and sanitizer availablility.
			 *
			 * The contexts that can be sanitized are $sce.MEDIA_URL, $sce.URL and $sce.HTML. The first two are available
			 * by default, and the third one relies on the `$sanitize` service (which may be loaded through
			 * the `ngSanitize` module). Furthermore, for $sce.RESOURCE_URL context, a plain string may be
			 * accepted if the resource url policy defined by {@link ng.$sceDelegateProvider#trustedResourceUrlList
			 * `$sceDelegateProvider.trustedResourceUrlList`} and {@link ng.$sceDelegateProvider#bannedResourceUrlList
			 * `$sceDelegateProvider.bannedResourceUrlList`} accepts that resource.
			 *
			 * This function will throw if the safe type isn't appropriate for this context, or if the
			 * value given cannot be accepted in the context (which might be caused by sanitization not
			 * being available, or the value not being recognized as safe).
			 *
			 * <div class="alert alert-danger">
			 * Disabling auto-escaping is extremely dangerous, it usually creates a Cross Site Scripting
			 * (XSS) vulnerability in your application.
			 * </div>
			 *
			 * @param {string} type The context in which this value is to be used (such as `$sce.HTML`).
			 * @param {*} maybeTrusted The result of a prior {@link ng.$sceDelegate#trustAs
			 *     `$sceDelegate.trustAs`} call, or anything else (which will not be considered trusted.)
			 * @return {*} A version of the value that's safe to use in the given context, or throws an
			 *     exception if this is impossible.
			 */
			function getTrusted(type, maybeTrusted) {
				if (maybeTrusted === null || isUndefined(maybeTrusted) || maybeTrusted === '') {
					return maybeTrusted;
				}
				var constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
				// If maybeTrusted is a trusted class instance or subclass instance, then unwrap and return
				// as-is.
				if (constructor && maybeTrusted instanceof constructor) {
					return maybeTrusted.$$unwrapTrustedValue();
				}

				// If maybeTrusted is a trusted class instance but not of the correct trusted type
				// then unwrap it and allow it to pass through to the rest of the checks
				if (isFunction(maybeTrusted.$$unwrapTrustedValue)) {
					maybeTrusted = maybeTrusted.$$unwrapTrustedValue();
				}

				// If we get here, then we will either sanitize the value or throw an exception.
				if (type === SCE_CONTEXTS.MEDIA_URL || type === SCE_CONTEXTS.URL) {
					// we attempt to sanitize non-resource URLs
					return $$sanitizeUri(maybeTrusted.toString(), type === SCE_CONTEXTS.MEDIA_URL);
				} else if (type === SCE_CONTEXTS.RESOURCE_URL) {
					if (isResourceUrlAllowedByPolicy(maybeTrusted)) {
						return maybeTrusted;
					} else {
						throw $sceMinErr('insecurl',
							'Blocked loading resource from url not allowed by $sceDelegate policy.  URL: {0}',
							maybeTrusted.toString());
					}
				} else if (type === SCE_CONTEXTS.HTML) {
					// htmlSanitizer throws its own error when no sanitizer is available.
					return htmlSanitizer(maybeTrusted);
				}
				// Default error when the $sce service has no way to make the input safe.
				throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
			}

			return {
				trustAs: trustAs,
				getTrusted: getTrusted,
				valueOf: valueOf
			};
		}];
	}


	/**
	 * @ngdoc provider
	 * @name $sceProvider
	 * @this
	 *
	 * @description
	 *
	 * The $sceProvider provider allows developers to configure the {@link ng.$sce $sce} service.
	 * -   enable/disable Strict Contextual Escaping (SCE) in a module
	 * -   override the default implementation with a custom delegate
	 *
	 * Read more about {@link ng.$sce Strict Contextual Escaping (SCE)}.
	 */

	/**
	 * @ngdoc service
	 * @name $sce
	 * @kind function
	 *
	 * @description
	 *
	 * `$sce` is a service that provides Strict Contextual Escaping services to AngularJS.
	 *
	 * ## Strict Contextual Escaping
	 *
	 * Strict Contextual Escaping (SCE) is a mode in which AngularJS constrains bindings to only render
	 * trusted values. Its goal is to assist in writing code in a way that (a) is secure by default, and
	 * (b) makes auditing for security vulnerabilities such as XSS, clickjacking, etc. a lot easier.
	 *
	 * ### Overview
	 *
	 * To systematically block XSS security bugs, AngularJS treats all values as untrusted by default in
	 * HTML or sensitive URL bindings. When binding untrusted values, AngularJS will automatically
	 * run security checks on them (sanitizations, trusted URL resource, depending on context), or throw
	 * when it cannot guarantee the security of the result. That behavior depends strongly on contexts:
	 * HTML can be sanitized, but template URLs cannot, for instance.
	 *
	 * To illustrate this, consider the `ng-bind-html` directive. It renders its value directly as HTML:
	 * we call that the *context*. When given an untrusted input, AngularJS will attempt to sanitize it
	 * before rendering if a sanitizer is available, and throw otherwise. To bypass sanitization and
	 * render the input as-is, you will need to mark it as trusted for that context before attempting
	 * to bind it.
	 *
	 * As of version 1.2, AngularJS ships with SCE enabled by default.
	 *
	 * ### In practice
	 *
	 * Here's an example of a binding in a privileged context:
	 *
	 * ```
	 * <input ng-model="userHtml" aria-label="User input">
	 * <div ng-bind-html="userHtml"></div>
	 * ```
	 *
	 * Notice that `ng-bind-html` is bound to `userHtml` controlled by the user.  With SCE
	 * disabled, this application allows the user to render arbitrary HTML into the DIV, which would
	 * be an XSS security bug. In a more realistic example, one may be rendering user comments, blog
	 * articles, etc. via bindings. (HTML is just one example of a context where rendering user
	 * controlled input creates security vulnerabilities.)
	 *
	 * For the case of HTML, you might use a library, either on the client side, or on the server side,
	 * to sanitize unsafe HTML before binding to the value and rendering it in the document.
	 *
	 * How would you ensure that every place that used these types of bindings was bound to a value that
	 * was sanitized by your library (or returned as safe for rendering by your server?)  How can you
	 * ensure that you didn't accidentally delete the line that sanitized the value, or renamed some
	 * properties/fields and forgot to update the binding to the sanitized value?
	 *
	 * To be secure by default, AngularJS makes sure bindings go through that sanitization, or
	 * any similar validation process, unless there's a good reason to trust the given value in this
	 * context.  That trust is formalized with a function call. This means that as a developer, you
	 * can assume all untrusted bindings are safe. Then, to audit your code for binding security issues,
	 * you just need to ensure the values you mark as trusted indeed are safe - because they were
	 * received from your server, sanitized by your library, etc. You can organize your codebase to
	 * help with this - perhaps allowing only the files in a specific directory to do this.
	 * Ensuring that the internal API exposed by that code doesn't markup arbitrary values as safe then
	 * becomes a more manageable task.
	 *
	 * In the case of AngularJS' SCE service, one uses {@link ng.$sce#trustAs $sce.trustAs}
	 * (and shorthand methods such as {@link ng.$sce#trustAsHtml $sce.trustAsHtml}, etc.) to
	 * build the trusted versions of your values.
	 *
	 * ### How does it work?
	 *
	 * In privileged contexts, directives and code will bind to the result of {@link ng.$sce#getTrusted
	 * $sce.getTrusted(context, value)} rather than to the value directly.  Think of this function as
	 * a way to enforce the required security context in your data sink. Directives use {@link
	 * ng.$sce#parseAs $sce.parseAs} rather than `$parse` to watch attribute bindings, which performs
	 * the {@link ng.$sce#getTrusted $sce.getTrusted} behind the scenes on non-constant literals. Also,
	 * when binding without directives, AngularJS will understand the context of your bindings
	 * automatically.
	 *
	 * As an example, {@link ng.directive:ngBindHtml ngBindHtml} uses {@link
	 * ng.$sce#parseAsHtml $sce.parseAsHtml(binding expression)}.  Here's the actual code (slightly
	 * simplified):
	 *
	 * ```
	 * var ngBindHtmlDirective = ['$sce', function($sce) {
	 *   return function(scope, element, attr) {
	 *     scope.$watch($sce.parseAsHtml(attr.ngBindHtml), function(value) {
	 *       element.html(value || '');
	 *     });
	 *   };
	 * }];
	 * ```
	 *
	 * ### Impact on loading templates
	 *
	 * This applies both to the {@link ng.directive:ngInclude `ng-include`} directive as well as
	 * `templateUrl`'s specified by {@link guide/directive directives}.
	 *
	 * By default, AngularJS only loads templates from the same domain and protocol as the application
	 * document.  This is done by calling {@link ng.$sce#getTrustedResourceUrl
	 * $sce.getTrustedResourceUrl} on the template URL.  To load templates from other domains and/or
	 * protocols, you may either add them to the {@link ng.$sceDelegateProvider#trustedResourceUrlList
	 * trustedResourceUrlList} or {@link ng.$sce#trustAsResourceUrl wrap them} into trusted values.
	 *
	 * *Please note*:
	 * The browser's
	 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
	 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
	 * policy apply in addition to this and may further restrict whether the template is successfully
	 * loaded.  This means that without the right CORS policy, loading templates from a different domain
	 * won't work on all browsers.  Also, loading templates from `file://` URL does not work on some
	 * browsers.
	 *
	 * ### This feels like too much overhead
	 *
	 * It's important to remember that SCE only applies to interpolation expressions.
	 *
	 * If your expressions are constant literals, they're automatically trusted and you don't need to
	 * call `$sce.trustAs` on them (e.g.
	 * `<div ng-bind-html="'<b>implicitly trusted</b>'"></div>`) just works (remember to include the
	 * `ngSanitize` module). The `$sceDelegate` will also use the `$sanitize` service if it is available
	 * when binding untrusted values to `$sce.HTML` context.
	 * AngularJS provides an implementation in `angular-sanitize.js`, and if you
	 * wish to use it, you will also need to depend on the {@link ngSanitize `ngSanitize`} module in
	 * your application.
	 *
	 * The included {@link ng.$sceDelegate $sceDelegate} comes with sane defaults to allow you to load
	 * templates in `ng-include` from your application's domain without having to even know about SCE.
	 * It blocks loading templates from other domains or loading templates over http from an https
	 * served document.  You can change these by setting your own custom {@link
	 * ng.$sceDelegateProvider#trustedResourceUrlList trusted resource URL list} and {@link
	 * ng.$sceDelegateProvider#bannedResourceUrlList banned resource URL list} for matching such URLs.
	 *
	 * This significantly reduces the overhead.  It is far easier to pay the small overhead and have an
	 * application that's secure and can be audited to verify that with much more ease than bolting
	 * security onto an application later.
	 *
	 * <a name="contexts"></a>
	 * ### What trusted context types are supported?
	 *
	 * | Context             | Notes          |
	 * |---------------------|----------------|
	 * | `$sce.HTML`         | For HTML that's safe to source into the application.  The {@link ng.directive:ngBindHtml ngBindHtml} directive uses this context for bindings. If an unsafe value is encountered and the {@link ngSanitize $sanitize} module is present this will sanitize the value instead of throwing an error. |
	 * | `$sce.CSS`          | For CSS that's safe to source into the application.  Currently unused.  Feel free to use it in your own directives. |
	 * | `$sce.MEDIA_URL`    | For URLs that are safe to render as media. Is automatically converted from string by sanitizing when needed. |
	 * | `$sce.URL`          | For URLs that are safe to follow as links. Is automatically converted from string by sanitizing when needed. Note that `$sce.URL` makes a stronger statement about the URL than `$sce.MEDIA_URL` does and therefore contexts requiring values trusted for `$sce.URL` can be used anywhere that values trusted for `$sce.MEDIA_URL` are required.|
	 * | `$sce.RESOURCE_URL` | For URLs that are not only safe to follow as links, but whose contents are also safe to include in your application.  Examples include `ng-include`, `src` / `ngSrc` bindings for tags other than `IMG` (e.g. `IFRAME`, `OBJECT`, etc.)  <br><br>Note that `$sce.RESOURCE_URL` makes a stronger statement about the URL than `$sce.URL` or `$sce.MEDIA_URL` do and therefore contexts requiring values trusted for `$sce.RESOURCE_URL` can be used anywhere that values trusted for `$sce.URL` or `$sce.MEDIA_URL` are required. <br><br> The {@link $sceDelegateProvider#trustedResourceUrlList $sceDelegateProvider#trustedResourceUrlList()} and {@link $sceDelegateProvider#bannedResourceUrlList $sceDelegateProvider#bannedResourceUrlList()} can be used to restrict trusted origins for `RESOURCE_URL` |
	 * | `$sce.JS`           | For JavaScript that is safe to execute in your application's context.  Currently unused.  Feel free to use it in your own directives. |
	 *
	 *
	 * <div class="alert alert-warning">
	 * Be aware that, before AngularJS 1.7.0, `a[href]` and `img[src]` used to sanitize their
	 * interpolated values directly rather than rely upon {@link ng.$sce#getTrusted `$sce.getTrusted`}.
	 *
	 * **As of 1.7.0, this is no longer the case.**
	 *
	 * Now such interpolations are marked as requiring `$sce.URL` (for `a[href]`) or `$sce.MEDIA_URL`
	 * (for `img[src]`), so that the sanitization happens (via `$sce.getTrusted...`) when the `$interpolate`
	 * service evaluates the expressions.
	 * </div>
	 *
	 * There are no CSS or JS context bindings in AngularJS currently, so their corresponding `$sce.trustAs`
	 * functions aren't useful yet. This might evolve.
	 *
	 * ### Format of items in {@link ng.$sceDelegateProvider#trustedResourceUrlList trustedResourceUrlList}/{@link ng.$sceDelegateProvider#bannedResourceUrlList bannedResourceUrlList} <a name="resourceUrlPatternItem"></a>
	 *
	 *  Each element in these arrays must be one of the following:
	 *
	 *  - **'self'**
	 *    - The special **string**, `'self'`, can be used to match against all URLs of the **same
	 *      domain** as the application document using the **same protocol**.
	 *  - **String** (except the special value `'self'`)
	 *    - The string is matched against the full *normalized / absolute URL* of the resource
	 *      being tested (substring matches are not good enough.)
	 *    - There are exactly **two wildcard sequences** - `*` and `**`.  All other characters
	 *      match themselves.
	 *    - `*`: matches zero or more occurrences of any character other than one of the following 6
	 *      characters: '`:`', '`/`', '`.`', '`?`', '`&`' and '`;`'.  It's a useful wildcard for use
	 *      for matching resource URL lists.
	 *    - `**`: matches zero or more occurrences of *any* character.  As such, it's not
	 *      appropriate for use in a scheme, domain, etc. as it would match too much.  (e.g.
	 *      http://**.example.com/ would match http://evil.com/?ignore=.example.com/ and that might
	 *      not have been the intention.)  Its usage at the very end of the path is ok.  (e.g.
	 *      http://foo.example.com/templates/**).
	 *  - **RegExp** (*see caveat below*)
	 *    - *Caveat*:  While regular expressions are powerful and offer great flexibility,  their syntax
	 *      (and all the inevitable escaping) makes them *harder to maintain*.  It's easy to
	 *      accidentally introduce a bug when one updates a complex expression (imho, all regexes should
	 *      have good test coverage).  For instance, the use of `.` in the regex is correct only in a
	 *      small number of cases.  A `.` character in the regex used when matching the scheme or a
	 *      subdomain could be matched against a `:` or literal `.` that was likely not intended.   It
	 *      is highly recommended to use the string patterns and only fall back to regular expressions
	 *      as a last resort.
	 *    - The regular expression must be an instance of RegExp (i.e. not a string.)  It is
	 *      matched against the **entire** *normalized / absolute URL* of the resource being tested
	 *      (even when the RegExp did not have the `^` and `$` codes.)  In addition, any flags
	 *      present on the RegExp (such as multiline, global, ignoreCase) are ignored.
	 *    - If you are generating your JavaScript from some other templating engine (not
	 *      recommended, e.g. in issue [#4006](https://github.com/angular/angular.js/issues/4006)),
	 *      remember to escape your regular expression (and be aware that you might need more than
	 *      one level of escaping depending on your templating engine and the way you interpolated
	 *      the value.)  Do make use of your platform's escaping mechanism as it might be good
	 *      enough before coding your own.  E.g. Ruby has
	 *      [Regexp.escape(str)](http://www.ruby-doc.org/core-2.0.0/Regexp.html#method-c-escape)
	 *      and Python has [re.escape](http://docs.python.org/library/re.html#re.escape).
	 *      Javascript lacks a similar built in function for escaping.  Take a look at Google
	 *      Closure library's [goog.string.regExpEscape(s)](
	 *      http://docs.closure-library.googlecode.com/git/closure_goog_string_string.js.source.html#line962).
	 *
	 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} for an example.
	 *
	 * ### Show me an example using SCE.
	 *
	 * <example module="mySceApp" deps="angular-sanitize.js" name="sce-service">
	 * <file name="index.html">
	 *   <div ng-controller="AppController as myCtrl">
	 *     <i ng-bind-html="myCtrl.explicitlyTrustedHtml" id="explicitlyTrustedHtml"></i><br><br>
	 *     <b>User comments</b><br>
	 *     By default, HTML that isn't explicitly trusted (e.g. Alice's comment) is sanitized when
	 *     $sanitize is available.  If $sanitize isn't available, this results in an error instead of an
	 *     exploit.
	 *     <div class="well">
	 *       <div ng-repeat="userComment in myCtrl.userComments">
	 *         <b>{{userComment.name}}</b>:
	 *         <span ng-bind-html="userComment.htmlComment" class="htmlComment"></span>
	 *         <br>
	 *       </div>
	 *     </div>
	 *   </div>
	 * </file>
	 *
	 * <file name="script.js">
	 *   angular.module('mySceApp', ['ngSanitize'])
	 *     .controller('AppController', ['$http', '$templateCache', '$sce',
	 *       function AppController($http, $templateCache, $sce) {
	 *         var self = this;
	 *         $http.get('test_data.json', {cache: $templateCache}).then(function(response) {
	 *           self.userComments = response.data;
	 *         });
	 *         self.explicitlyTrustedHtml = $sce.trustAsHtml(
	 *             '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
	 *             'sanitization.&quot;">Hover over this text.</span>');
	 *       }]);
	 * </file>
	 *
	 * <file name="test_data.json">
	 * [
	 *   { "name": "Alice",
	 *     "htmlComment":
	 *         "<span onmouseover='this.textContent=\"PWN3D!\"'>Is <i>anyone</i> reading this?</span>"
	 *   },
	 *   { "name": "Bob",
	 *     "htmlComment": "<i>Yes!</i>  Am I the only other one?"
	 *   }
	 * ]
	 * </file>
	 *
	 * <file name="protractor.js" type="protractor">
	 *   describe('SCE doc demo', function() {
	 *     it('should sanitize untrusted values', function() {
	 *       expect(element.all(by.css('.htmlComment')).first().getAttribute('innerHTML'))
	 *           .toBe('<span>Is <i>anyone</i> reading this?</span>');
	 *     });
	 *
	 *     it('should NOT sanitize explicitly trusted values', function() {
	 *       expect(element(by.id('explicitlyTrustedHtml')).getAttribute('innerHTML')).toBe(
	 *           '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
	 *           'sanitization.&quot;">Hover over this text.</span>');
	 *     });
	 *   });
	 * </file>
	 * </example>
	 *
	 *
	 *
	 * ## Can I disable SCE completely?
	 *
	 * Yes, you can.  However, this is strongly discouraged.  SCE gives you a lot of security benefits
	 * for little coding overhead.  It will be much harder to take an SCE disabled application and
	 * either secure it on your own or enable SCE at a later stage.  It might make sense to disable SCE
	 * for cases where you have a lot of existing code that was written before SCE was introduced and
	 * you're migrating them a module at a time. Also do note that this is an app-wide setting, so if
	 * you are writing a library, you will cause security bugs applications using it.
	 *
	 * That said, here's how you can completely disable SCE:
	 *
	 * ```
	 * angular.module('myAppWithSceDisabledmyApp', []).config(function($sceProvider) {
	 *   // Completely disable SCE.  For demonstration purposes only!
	 *   // Do not use in new projects or libraries.
	 *   $sceProvider.enabled(false);
	 * });
	 * ```
	 *
	 */

	function $SceProvider() {
		var enabled = true;

		/**
		 * @ngdoc method
		 * @name $sceProvider#enabled
		 * @kind function
		 *
		 * @param {boolean=} value If provided, then enables/disables SCE application-wide.
		 * @return {boolean} True if SCE is enabled, false otherwise.
		 *
		 * @description
		 * Enables/disables SCE and returns the current value.
		 */
		this.enabled = function (value) {
			if (arguments.length) {
				enabled = !!value;
			}
			return enabled;
		};


		/* Design notes on the default implementation for SCE.
		 *
		 * The API contract for the SCE delegate
		 * -------------------------------------
		 * The SCE delegate object must provide the following 3 methods:
		 *
		 * - trustAs(contextEnum, value)
		 *     This method is used to tell the SCE service that the provided value is OK to use in the
		 *     contexts specified by contextEnum.  It must return an object that will be accepted by
		 *     getTrusted() for a compatible contextEnum and return this value.
		 *
		 * - valueOf(value)
		 *     For values that were not produced by trustAs(), return them as is.  For values that were
		 *     produced by trustAs(), return the corresponding input value to trustAs.  Basically, if
		 *     trustAs is wrapping the given values into some type, this operation unwraps it when given
		 *     such a value.
		 *
		 * - getTrusted(contextEnum, value)
		 *     This function should return the value that is safe to use in the context specified by
		 *     contextEnum or throw and exception otherwise.
		 *
		 * NOTE: This contract deliberately does NOT state that values returned by trustAs() must be
		 * opaque or wrapped in some holder object.  That happens to be an implementation detail.  For
		 * instance, an implementation could maintain a registry of all trusted objects by context.  In
		 * such a case, trustAs() would return the same object that was passed in.  getTrusted() would
		 * return the same object passed in if it was found in the registry under a compatible context or
		 * throw an exception otherwise.  An implementation might only wrap values some of the time based
		 * on some criteria.  getTrusted() might return a value and not throw an exception for special
		 * constants or objects even if not wrapped.  All such implementations fulfill this contract.
		 *
		 *
		 * A note on the inheritance model for SCE contexts
		 * ------------------------------------------------
		 * I've used inheritance and made RESOURCE_URL wrapped types a subtype of URL wrapped types.  This
		 * is purely an implementation details.
		 *
		 * The contract is simply this:
		 *
		 *     getTrusted($sce.RESOURCE_URL, value) succeeding implies that getTrusted($sce.URL, value)
		 *     will also succeed.
		 *
		 * Inheritance happens to capture this in a natural way. In some future, we may not use
		 * inheritance anymore. That is OK because no code outside of sce.js and sceSpecs.js would need to
		 * be aware of this detail.
		 */

		this.$get = ['$parse', '$sceDelegate', function (
			$parse, $sceDelegate) {
			// Support: IE 9-11 only
			// Prereq: Ensure that we're not running in IE<11 quirks mode.  In that mode, IE < 11 allow
			// the "expression(javascript expression)" syntax which is insecure.
			if (enabled && msie < 8) {
				throw $sceMinErr('iequirks',
					'Strict Contextual Escaping does not support Internet Explorer version < 11 in quirks ' +
					'mode.  You can fix this by adding the text <!doctype html> to the top of your HTML ' +
					'document.  See http://docs.angularjs.org/api/ng.$sce for more information.');
			}

			var sce = shallowCopy(SCE_CONTEXTS);

			/**
			 * @ngdoc method
			 * @name $sce#isEnabled
			 * @kind function
			 *
			 * @return {Boolean} True if SCE is enabled, false otherwise.  If you want to set the value, you
			 *     have to do it at module config time on {@link ng.$sceProvider $sceProvider}.
			 *
			 * @description
			 * Returns a boolean indicating if SCE is enabled.
			 */
			sce.isEnabled = function () {
				return enabled;
			};
			sce.trustAs = $sceDelegate.trustAs;
			sce.getTrusted = $sceDelegate.getTrusted;
			sce.valueOf = $sceDelegate.valueOf;

			if (!enabled) {
				sce.trustAs = sce.getTrusted = function (type, value) { return value; };
				sce.valueOf = identity;
			}

			/**
			 * @ngdoc method
			 * @name $sce#parseAs
			 *
			 * @description
			 * Converts AngularJS {@link guide/expression expression} into a function.  This is like {@link
			 * ng.$parse $parse} and is identical when the expression is a literal constant.  Otherwise, it
			 * wraps the expression in a call to {@link ng.$sce#getTrusted $sce.getTrusted(*type*,
			 * *result*)}
			 *
			 * @param {string} type The SCE context in which this result will be used.
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */
			sce.parseAs = function sceParseAs(type, expr) {
				var parsed = $parse(expr);
				if (parsed.literal && parsed.constant) {
					return parsed;
				} else {
					return $parse(expr, function (value) {
						return sce.getTrusted(type, value);
					});
				}
			};

			/**
			 * @ngdoc method
			 * @name $sce#trustAs
			 *
			 * @description
			 * Delegates to {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}. As such, returns a
			 * wrapped object that represents your value, and the trust you have in its safety for the given
			 * context. AngularJS can then use that value as-is in bindings of the specified secure context.
			 * This is used in bindings for `ng-bind-html`, `ng-include`, and most `src` attribute
			 * interpolations. See {@link ng.$sce $sce} for strict contextual escaping.
			 *
			 * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
			 *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
			 *
			 * @param {*} value The value that that should be considered trusted.
			 * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
			 *     in the context you specified.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#trustAsHtml
			 *
			 * @description
			 * Shorthand method.  `$sce.trustAsHtml(value)` →
			 *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.HTML, value)`}
			 *
			 * @param {*} value The value to mark as trusted for `$sce.HTML` context.
			 * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
			 *     in `$sce.HTML` context (like `ng-bind-html`).
			 */

			/**
			 * @ngdoc method
			 * @name $sce#trustAsCss
			 *
			 * @description
			 * Shorthand method.  `$sce.trustAsCss(value)` →
			 *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.CSS, value)`}
			 *
			 * @param {*} value The value to mark as trusted for `$sce.CSS` context.
			 * @return {*} A wrapped version of value that can be used as a trusted variant
			 *     of your `value` in `$sce.CSS` context. This context is currently unused, so there are
			 *     almost no reasons to use this function so far.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#trustAsUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.trustAsUrl(value)` →
			 *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.URL, value)`}
			 *
			 * @param {*} value The value to mark as trusted for `$sce.URL` context.
			 * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
			 *     in `$sce.URL` context. That context is currently unused, so there are almost no reasons
			 *     to use this function so far.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#trustAsResourceUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.trustAsResourceUrl(value)` →
			 *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.RESOURCE_URL, value)`}
			 *
			 * @param {*} value The value to mark as trusted for `$sce.RESOURCE_URL` context.
			 * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
			 *     in `$sce.RESOURCE_URL` context (template URLs in `ng-include`, most `src` attribute
			 *     bindings, ...)
			 */

			/**
			 * @ngdoc method
			 * @name $sce#trustAsJs
			 *
			 * @description
			 * Shorthand method.  `$sce.trustAsJs(value)` →
			 *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.JS, value)`}
			 *
			 * @param {*} value The value to mark as trusted for `$sce.JS` context.
			 * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
			 *     in `$sce.JS` context. That context is currently unused, so there are almost no reasons to
			 *     use this function so far.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrusted
			 *
			 * @description
			 * Delegates to {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted`}.  As such,
			 * takes any input, and either returns a value that's safe to use in the specified context,
			 * or throws an exception. This function is aware of trusted values created by the `trustAs`
			 * function and its shorthands, and when contexts are appropriate, returns the unwrapped value
			 * as-is. Finally, this function can also throw when there is no way to turn `maybeTrusted` in a
			 * safe value (e.g., no sanitization is available or possible.)
			 *
			 * @param {string} type The context in which this value is to be used.
			 * @param {*} maybeTrusted The result of a prior {@link ng.$sce#trustAs
			 *     `$sce.trustAs`} call, or anything else (which will not be considered trusted.)
			 * @return {*} A version of the value that's safe to use in the given context, or throws an
			 *     exception if this is impossible.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrustedHtml
			 *
			 * @description
			 * Shorthand method.  `$sce.getTrustedHtml(value)` →
			 *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.HTML, value)`}
			 *
			 * @param {*} value The value to pass to `$sce.getTrusted`.
			 * @return {*} The return value of `$sce.getTrusted($sce.HTML, value)`
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrustedCss
			 *
			 * @description
			 * Shorthand method.  `$sce.getTrustedCss(value)` →
			 *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.CSS, value)`}
			 *
			 * @param {*} value The value to pass to `$sce.getTrusted`.
			 * @return {*} The return value of `$sce.getTrusted($sce.CSS, value)`
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrustedUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.getTrustedUrl(value)` →
			 *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.URL, value)`}
			 *
			 * @param {*} value The value to pass to `$sce.getTrusted`.
			 * @return {*} The return value of `$sce.getTrusted($sce.URL, value)`
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrustedResourceUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.getTrustedResourceUrl(value)` →
			 *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.RESOURCE_URL, value)`}
			 *
			 * @param {*} value The value to pass to `$sceDelegate.getTrusted`.
			 * @return {*} The return value of `$sce.getTrusted($sce.RESOURCE_URL, value)`
			 */

			/**
			 * @ngdoc method
			 * @name $sce#getTrustedJs
			 *
			 * @description
			 * Shorthand method.  `$sce.getTrustedJs(value)` →
			 *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.JS, value)`}
			 *
			 * @param {*} value The value to pass to `$sce.getTrusted`.
			 * @return {*} The return value of `$sce.getTrusted($sce.JS, value)`
			 */

			/**
			 * @ngdoc method
			 * @name $sce#parseAsHtml
			 *
			 * @description
			 * Shorthand method.  `$sce.parseAsHtml(expression string)` →
			 *     {@link ng.$sce#parseAs `$sce.parseAs($sce.HTML, value)`}
			 *
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#parseAsCss
			 *
			 * @description
			 * Shorthand method.  `$sce.parseAsCss(value)` →
			 *     {@link ng.$sce#parseAs `$sce.parseAs($sce.CSS, value)`}
			 *
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#parseAsUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.parseAsUrl(value)` →
			 *     {@link ng.$sce#parseAs `$sce.parseAs($sce.URL, value)`}
			 *
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#parseAsResourceUrl
			 *
			 * @description
			 * Shorthand method.  `$sce.parseAsResourceUrl(value)` →
			 *     {@link ng.$sce#parseAs `$sce.parseAs($sce.RESOURCE_URL, value)`}
			 *
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */

			/**
			 * @ngdoc method
			 * @name $sce#parseAsJs
			 *
			 * @description
			 * Shorthand method.  `$sce.parseAsJs(value)` →
			 *     {@link ng.$sce#parseAs `$sce.parseAs($sce.JS, value)`}
			 *
			 * @param {string} expression String expression to compile.
			 * @return {function(context, locals)} A function which represents the compiled expression:
			 *
			 *    * `context` – `{object}` – an object against which any expressions embedded in the
			 *      strings are evaluated against (typically a scope object).
			 *    * `locals` – `{object=}` – local variables context object, useful for overriding values
			 *      in `context`.
			 */

			// Shorthand delegations.
			var parse = sce.parseAs,
				getTrusted = sce.getTrusted,
				trustAs = sce.trustAs;

			forEach(SCE_CONTEXTS, function (enumValue, name) {
				var lName = lowercase(name);
				sce[snakeToCamel('parse_as_' + lName)] = function (expr) {
					return parse(enumValue, expr);
				};
				sce[snakeToCamel('get_trusted_' + lName)] = function (value) {
					return getTrusted(enumValue, value);
				};
				sce[snakeToCamel('trust_as_' + lName)] = function (value) {
					return trustAs(enumValue, value);
				};
			});

			return sce;
		}];
	}

	/* exported $SnifferProvider */

	/**
	 * !!! This is an undocumented "private" service !!!
	 *
	 * @name $sniffer
	 * @requires $window
	 * @requires $document
	 * @this
	 *
	 * @property {boolean} history Does the browser support html5 history api ?
	 * @property {boolean} transitions Does the browser support CSS transition events ?
	 * @property {boolean} animations Does the browser support CSS animation events ?
	 *
	 * @description
	 * This is very simple implementation of testing browser's features.
	 */
	function $SnifferProvider() {
		this.$get = ['$window', '$document', function ($window, $document) {
			var eventSupport = {},
				// Chrome Packaged Apps are not allowed to access `history.pushState`.
				// If not sandboxed, they can be detected by the presence of `chrome.app.runtime`
				// (see https://developer.chrome.com/apps/api_index). If sandboxed, they can be detected by
				// the presence of an extension runtime ID and the absence of other Chrome runtime APIs
				// (see https://developer.chrome.com/apps/manifest/sandbox).
				// (NW.js apps have access to Chrome APIs, but do support `history`.)
				isNw = $window.nw && $window.nw.process,
				isChromePackagedApp =
					!isNw &&
					$window.chrome &&
					($window.chrome.app && $window.chrome.app.runtime ||
						!$window.chrome.app && $window.chrome.runtime && $window.chrome.runtime.id),
				hasHistoryPushState = !isChromePackagedApp && $window.history && $window.history.pushState,
				android =
					toInt((/android (\d+)/.exec(lowercase(($window.navigator || {}).userAgent)) || [])[1]),
				boxee = /Boxee/i.test(($window.navigator || {}).userAgent),
				document = $document[0] || {},
				bodyStyle = document.body && document.body.style,
				transitions = false,
				animations = false;

			if (bodyStyle) {
				// Support: Android <5, Blackberry Browser 10, default Chrome in Android 4.4.x
				// Mentioned browsers need a -webkit- prefix for transitions & animations.
				transitions = !!('transition' in bodyStyle || 'webkitTransition' in bodyStyle);
				animations = !!('animation' in bodyStyle || 'webkitAnimation' in bodyStyle);
			}


			return {
				// Android has history.pushState, but it does not update location correctly
				// so let's not use the history API at all.
				// http://code.google.com/p/android/issues/detail?id=17471
				// https://github.com/angular/angular.js/issues/904

				// older webkit browser (533.9) on Boxee box has exactly the same problem as Android has
				// so let's not use the history API also
				// We are purposefully using `!(android < 4)` to cover the case when `android` is undefined
				history: !!(hasHistoryPushState && !(android < 4) && !boxee),
				hasEvent: function (event) {
					// Support: IE 9-11 only
					// IE9 implements 'input' event it's so fubared that we rather pretend that it doesn't have
					// it. In particular the event is not fired when backspace or delete key are pressed or
					// when cut operation is performed.
					// IE10+ implements 'input' event but it erroneously fires under various situations,
					// e.g. when placeholder changes, or a form is focused.
					if (event === 'input' && msie) return false;

					if (isUndefined(eventSupport[event])) {
						var divElm = document.createElement('div');
						eventSupport[event] = 'on' + event in divElm;
					}

					return eventSupport[event];
				},
				csp: csp(),
				transitions: transitions,
				animations: animations,
				android: android
			};
		}];
	}

	/**
	 * ! This is a private undocumented service !
	 *
	 * @name $$taskTrackerFactory
	 * @description
	 * A function to create `TaskTracker` instances.
	 *
	 * A `TaskTracker` can keep track of pending tasks (grouped by type) and can notify interested
	 * parties when all pending tasks (or tasks of a specific type) have been completed.
	 *
	 * @param {$log} log - A logger instance (such as `$log`). Used to log error during callback
	 *     execution.
	 *
	 * @this
	 */
	function $$TaskTrackerFactoryProvider() {
		this.$get = valueFn(function (log) { return new TaskTracker(log); });
	}

	function TaskTracker(log) {
		var self = this;
		var taskCounts = {};
		var taskCallbacks = [];

		var ALL_TASKS_TYPE = self.ALL_TASKS_TYPE = '$$all$$';
		var DEFAULT_TASK_TYPE = self.DEFAULT_TASK_TYPE = '$$default$$';

		/**
		 * Execute the specified function and decrement the appropriate `taskCounts` counter.
		 * If the counter reaches 0, all corresponding `taskCallbacks` are executed.
		 *
		 * @param {Function} fn - The function to execute.
		 * @param {string=} [taskType=DEFAULT_TASK_TYPE] - The type of task that is being completed.
		 */
		self.completeTask = completeTask;

		/**
		 * Increase the task count for the specified task type (or the default task type if non is
		 * specified).
		 *
		 * @param {string=} [taskType=DEFAULT_TASK_TYPE] - The type of task whose count will be increased.
		 */
		self.incTaskCount = incTaskCount;

		/**
		 * Execute the specified callback when all pending tasks have been completed.
		 *
		 * If there are no pending tasks, the callback is executed immediately. You can optionally limit
		 * the tasks that will be waited for to a specific type, by passing a `taskType`.
		 *
		 * @param {function} callback - The function to call when there are no pending tasks.
		 * @param {string=} [taskType=ALL_TASKS_TYPE] - The type of tasks that will be waited for.
		 */
		self.notifyWhenNoPendingTasks = notifyWhenNoPendingTasks;

		function completeTask(fn, taskType) {
			taskType = taskType || DEFAULT_TASK_TYPE;

			try {
				fn();
			} finally {
				decTaskCount(taskType);

				var countForType = taskCounts[taskType];
				var countForAll = taskCounts[ALL_TASKS_TYPE];

				// If at least one of the queues (`ALL_TASKS_TYPE` or `taskType`) is empty, run callbacks.
				if (!countForAll || !countForType) {
					var getNextCallback = !countForAll ? getLastCallback : getLastCallbackForType;
					var nextCb;

					while ((nextCb = getNextCallback(taskType))) {
						try {
							nextCb();
						} catch (e) {
							log.error(e);
						}
					}
				}
			}
		}

		function decTaskCount(taskType) {
			taskType = taskType || DEFAULT_TASK_TYPE;
			if (taskCounts[taskType]) {
				taskCounts[taskType]--;
				taskCounts[ALL_TASKS_TYPE]--;
			}
		}

		function getLastCallback() {
			var cbInfo = taskCallbacks.pop();
			return cbInfo && cbInfo.cb;
		}

		function getLastCallbackForType(taskType) {
			for (var i = taskCallbacks.length - 1; i >= 0; --i) {
				var cbInfo = taskCallbacks[i];
				if (cbInfo.type === taskType) {
					taskCallbacks.splice(i, 1);
					return cbInfo.cb;
				}
			}
		}

		function incTaskCount(taskType) {
			taskType = taskType || DEFAULT_TASK_TYPE;
			taskCounts[taskType] = (taskCounts[taskType] || 0) + 1;
			taskCounts[ALL_TASKS_TYPE] = (taskCounts[ALL_TASKS_TYPE] || 0) + 1;
		}

		function notifyWhenNoPendingTasks(callback, taskType) {
			taskType = taskType || ALL_TASKS_TYPE;
			if (!taskCounts[taskType]) {
				callback();
			} else {
				taskCallbacks.push({ type: taskType, cb: callback });
			}
		}
	}

	var $templateRequestMinErr = minErr('$templateRequest');

	/**
	 * @ngdoc provider
	 * @name $templateRequestProvider
	 * @this
	 *
	 * @description
	 * Used to configure the options passed to the {@link $http} service when making a template request.
	 *
	 * For example, it can be used for specifying the "Accept" header that is sent to the server, when
	 * requesting a template.
	 */
	function $TemplateRequestProvider() {

		var httpOptions;

		/**
		 * @ngdoc method
		 * @name $templateRequestProvider#httpOptions
		 * @description
		 * The options to be passed to the {@link $http} service when making the request.
		 * You can use this to override options such as the "Accept" header for template requests.
		 *
		 * The {@link $templateRequest} will set the `cache` and the `transformResponse` properties of the
		 * options if not overridden here.
		 *
		 * @param {string=} value new value for the {@link $http} options.
		 * @returns {string|self} Returns the {@link $http} options when used as getter and self if used as setter.
		 */
		this.httpOptions = function (val) {
			if (val) {
				httpOptions = val;
				return this;
			}
			return httpOptions;
		};

		/**
		 * @ngdoc service
		 * @name $templateRequest
		 *
		 * @description
		 * The `$templateRequest` service runs security checks then downloads the provided template using
		 * `$http` and, upon success, stores the contents inside of `$templateCache`. If the HTTP request
		 * fails or the response data of the HTTP request is empty, a `$compile` error will be thrown (the
		 * exception can be thwarted by setting the 2nd parameter of the function to true). Note that the
		 * contents of `$templateCache` are trusted, so the call to `$sce.getTrustedUrl(tpl)` is omitted
		 * when `tpl` is of type string and `$templateCache` has the matching entry.
		 *
		 * If you want to pass custom options to the `$http` service, such as setting the Accept header you
		 * can configure this via {@link $templateRequestProvider#httpOptions}.
		 *
		 * `$templateRequest` is used internally by {@link $compile}, {@link ngRoute.$route}, and directives such
		 * as {@link ngInclude} to download and cache templates.
		 *
		 * 3rd party modules should use `$templateRequest` if their services or directives are loading
		 * templates.
		 *
		 * @param {string|TrustedResourceUrl} tpl The HTTP request template URL
		 * @param {boolean=} ignoreRequestError Whether or not to ignore the exception when the request fails or the template is empty
		 *
		 * @return {Promise} a promise for the HTTP response data of the given URL.
		 *
		 * @property {number} totalPendingRequests total amount of pending template requests being downloaded.
		 */
		this.$get = ['$exceptionHandler', '$templateCache', '$http', '$q', '$sce',
			function ($exceptionHandler, $templateCache, $http, $q, $sce) {

				function handleRequestFn(tpl, ignoreRequestError) {
					handleRequestFn.totalPendingRequests++;

					// We consider the template cache holds only trusted templates, so
					// there's no need to go through adding the template again to the trusted
					// resources for keys that already are included in there. This also makes
					// AngularJS accept any script directive, no matter its name. However, we
					// still need to unwrap trusted types.
					if (!isString(tpl) || isUndefined($templateCache.get(tpl))) {
						tpl = $sce.getTrustedResourceUrl(tpl);
					}

					var transformResponse = $http.defaults && $http.defaults.transformResponse;

					if (isArray(transformResponse)) {
						transformResponse = transformResponse.filter(function (transformer) {
							return transformer !== defaultHttpResponseTransform;
						});
					} else if (transformResponse === defaultHttpResponseTransform) {
						transformResponse = null;
					}

					return $http.get(tpl, extend({
						cache: $templateCache,
						transformResponse: transformResponse
					}, httpOptions))
						.finally(function () {
							handleRequestFn.totalPendingRequests--;
						})
						.then(function (response) {
							return $templateCache.put(tpl, response.data);
						}, handleError);

					function handleError(resp) {
						if (!ignoreRequestError) {
							resp = $templateRequestMinErr('tpload',
								'Failed to load template: {0} (HTTP status: {1} {2})',
								tpl, resp.status, resp.statusText);

							$exceptionHandler(resp);
						}

						return $q.reject(resp);
					}
				}

				handleRequestFn.totalPendingRequests = 0;

				return handleRequestFn;
			}
		];
	}

	/** @this */
	function $$TestabilityProvider() {
		this.$get = ['$rootScope', '$browser', '$location',
			function ($rootScope, $browser, $location) {

				/**
				 * @name $testability
				 *
				 * @description
				 * The private $$testability service provides a collection of methods for use when debugging
				 * or by automated test and debugging tools.
				 */
				var testability = {};

				/**
				 * @name $$testability#findBindings
				 *
				 * @description
				 * Returns an array of elements that are bound (via ng-bind or {{}})
				 * to expressions matching the input.
				 *
				 * @param {Element} element The element root to search from.
				 * @param {string} expression The binding expression to match.
				 * @param {boolean} opt_exactMatch If true, only returns exact matches
				 *     for the expression. Filters and whitespace are ignored.
				 */
				testability.findBindings = function (element, expression, opt_exactMatch) {
					var bindings = element.getElementsByClassName('ng-binding');
					var matches = [];
					forEach(bindings, function (binding) {
						var dataBinding = angular.element(binding).data('$binding');
						if (dataBinding) {
							forEach(dataBinding, function (bindingName) {
								if (opt_exactMatch) {
									var matcher = new RegExp('(^|\\s)' + escapeForRegexp(expression) + '(\\s|\\||$)');
									if (matcher.test(bindingName)) {
										matches.push(binding);
									}
								} else {
									if (bindingName.indexOf(expression) !== -1) {
										matches.push(binding);
									}
								}
							});
						}
					});
					return matches;
				};

				/**
				 * @name $$testability#findModels
				 *
				 * @description
				 * Returns an array of elements that are two-way found via ng-model to
				 * expressions matching the input.
				 *
				 * @param {Element} element The element root to search from.
				 * @param {string} expression The model expression to match.
				 * @param {boolean} opt_exactMatch If true, only returns exact matches
				 *     for the expression.
				 */
				testability.findModels = function (element, expression, opt_exactMatch) {
					var prefixes = ['ng-', 'data-ng-', 'ng\\:'];
					for (var p = 0; p < prefixes.length; ++p) {
						var attributeEquals = opt_exactMatch ? '=' : '*=';
						var selector = '[' + prefixes[p] + 'model' + attributeEquals + '"' + expression + '"]';
						var elements = element.querySelectorAll(selector);
						if (elements.length) {
							return elements;
						}
					}
				};

				/**
				 * @name $$testability#getLocation
				 *
				 * @description
				 * Shortcut for getting the location in a browser agnostic way. Returns
				 *     the path, search, and hash. (e.g. /path?a=b#hash)
				 */
				testability.getLocation = function () {
					return $location.url();
				};

				/**
				 * @name $$testability#setLocation
				 *
				 * @description
				 * Shortcut for navigating to a location without doing a full page reload.
				 *
				 * @param {string} url The location url (path, search and hash,
				 *     e.g. /path?a=b#hash) to go to.
				 */
				testability.setLocation = function (url) {
					if (url !== $location.url()) {
						$location.url(url);
						$rootScope.$digest();
					}
				};

				/**
				 * @name $$testability#whenStable
				 *
				 * @description
				 * Calls the callback when all pending tasks are completed.
				 *
				 * Types of tasks waited for include:
				 * - Pending timeouts (via {@link $timeout}).
				 * - Pending HTTP requests (via {@link $http}).
				 * - In-progress route transitions (via {@link $route}).
				 * - Pending tasks scheduled via {@link $rootScope#$applyAsync}.
				 * - Pending tasks scheduled via {@link $rootScope#$evalAsync}.
				 *   These include tasks scheduled via `$evalAsync()` indirectly (such as {@link $q} promises).
				 *
				 * @param {function} callback
				 */
				testability.whenStable = function (callback) {
					$browser.notifyWhenNoOutstandingRequests(callback);
				};

				return testability;
			}];
	}

	var $timeoutMinErr = minErr('$timeout');

	/** @this */
	function $TimeoutProvider() {
		this.$get = ['$rootScope', '$browser', '$q', '$$q', '$exceptionHandler',
			function ($rootScope, $browser, $q, $$q, $exceptionHandler) {

				var deferreds = {};


				/**
				 * @ngdoc service
				 * @name $timeout
				 *
				 * @description
				 * AngularJS's wrapper for `window.setTimeout`. The `fn` function is wrapped into a try/catch
				 * block and delegates any exceptions to
				 * {@link ng.$exceptionHandler $exceptionHandler} service.
				 *
				 * The return value of calling `$timeout` is a promise, which will be resolved when
				 * the delay has passed and the timeout function, if provided, is executed.
				 *
				 * To cancel a timeout request, call `$timeout.cancel(promise)`.
				 *
				 * In tests you can use {@link ngMock.$timeout `$timeout.flush()`} to
				 * synchronously flush the queue of deferred functions.
				 *
				 * If you only want a promise that will be resolved after some specified delay
				 * then you can call `$timeout` without the `fn` function.
				 *
				 * @param {function()=} fn A function, whose execution should be delayed.
				 * @param {number=} [delay=0] Delay in milliseconds.
				 * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
				 *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
				 * @param {...*=} Pass additional parameters to the executed function.
				 * @returns {Promise} Promise that will be resolved when the timeout is reached. The promise
				 *   will be resolved with the return value of the `fn` function.
				 *
				 */
				function timeout(fn, delay, invokeApply) {
					if (!isFunction(fn)) {
						invokeApply = delay;
						delay = fn;
						fn = noop;
					}

					var args = sliceArgs(arguments, 3),
						skipApply = (isDefined(invokeApply) && !invokeApply),
						deferred = (skipApply ? $$q : $q).defer(),
						promise = deferred.promise,
						timeoutId;

					timeoutId = $browser.defer(function () {
						try {
							deferred.resolve(fn.apply(null, args));
						} catch (e) {
							deferred.reject(e);
							$exceptionHandler(e);
						} finally {
							delete deferreds[promise.$$timeoutId];
						}

						if (!skipApply) $rootScope.$apply();
					}, delay, '$timeout');

					promise.$$timeoutId = timeoutId;
					deferreds[timeoutId] = deferred;

					return promise;
				}


				/**
				 * @ngdoc method
				 * @name $timeout#cancel
				 *
				 * @description
				 * Cancels a task associated with the `promise`. As a result of this, the promise will be
				 * resolved with a rejection.
				 *
				 * @param {Promise=} promise Promise returned by the `$timeout` function.
				 * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
				 *   canceled.
				 */
				timeout.cancel = function (promise) {
					if (!promise) return false;

					if (!promise.hasOwnProperty('$$timeoutId')) {
						throw $timeoutMinErr('badprom',
							'`$timeout.cancel()` called with a promise that was not generated by `$timeout()`.');
					}

					if (!deferreds.hasOwnProperty(promise.$$timeoutId)) return false;

					var id = promise.$$timeoutId;
					var deferred = deferreds[id];

					// Timeout cancels should not report an unhandled promise.
					markQExceptionHandled(deferred.promise);
					deferred.reject('canceled');
					delete deferreds[id];

					return $browser.defer.cancel(id);
				};

				return timeout;
			}];
	}

	// NOTE:  The usage of window and document instead of $window and $document here is
	// deliberate.  This service depends on the specific behavior of anchor nodes created by the
	// browser (resolving and parsing URLs) that is unlikely to be provided by mock objects and
	// cause us to break tests.  In addition, when the browser resolves a URL for XHR, it
	// doesn't know about mocked locations and resolves URLs to the real document - which is
	// exactly the behavior needed here.  There is little value is mocking these out for this
	// service.
	var urlParsingNode = window.document.createElement('a');
	var originUrl = urlResolve(window.location.href);
	var baseUrlParsingNode;

	urlParsingNode.href = 'http://[::1]';

	// Support: IE 9-11 only, Edge 16-17 only (fixed in 18 Preview)
	// IE/Edge don't wrap IPv6 addresses' hostnames in square brackets
	// when parsed out of an anchor element.
	var ipv6InBrackets = urlParsingNode.hostname === '[::1]';

	/**
	 *
	 * Implementation Notes for non-IE browsers
	 * ----------------------------------------
	 * Assigning a URL to the href property of an anchor DOM node, even one attached to the DOM,
	 * results both in the normalizing and parsing of the URL.  Normalizing means that a relative
	 * URL will be resolved into an absolute URL in the context of the application document.
	 * Parsing means that the anchor node's host, hostname, protocol, port, pathname and related
	 * properties are all populated to reflect the normalized URL.  This approach has wide
	 * compatibility - Safari 1+, Mozilla 1+ etc.  See
	 * http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
	 *
	 * Implementation Notes for IE
	 * ---------------------------
	 * IE <= 10 normalizes the URL when assigned to the anchor node similar to the other
	 * browsers.  However, the parsed components will not be set if the URL assigned did not specify
	 * them.  (e.g. if you assign a.href = "foo", then a.protocol, a.host, etc. will be empty.)  We
	 * work around that by performing the parsing in a 2nd step by taking a previously normalized
	 * URL (e.g. by assigning to a.href) and assigning it a.href again.  This correctly populates the
	 * properties such as protocol, hostname, port, etc.
	 *
	 * References:
	 *   http://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement
	 *   http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
	 *   http://url.spec.whatwg.org/#urlutils
	 *   https://github.com/angular/angular.js/pull/2902
	 *   http://james.padolsey.com/javascript/parsing-urls-with-the-dom/
	 *
	 * @kind function
	 * @param {string|object} url The URL to be parsed. If `url` is not a string, it will be returned
	 *     unchanged.
	 * @description Normalizes and parses a URL.
	 * @returns {object} Returns the normalized URL as a dictionary.
	 *
	 *   | member name   | Description                                                            |
	 *   |---------------|------------------------------------------------------------------------|
	 *   | href          | A normalized version of the provided URL if it was not an absolute URL |
	 *   | protocol      | The protocol without the trailing colon                                |
	 *   | host          | The host and port (if the port is non-default) of the normalizedUrl    |
	 *   | search        | The search params, minus the question mark                             |
	 *   | hash          | The hash string, minus the hash symbol                                 |
	 *   | hostname      | The hostname                                                           |
	 *   | port          | The port, without ":"                                                  |
	 *   | pathname      | The pathname, beginning with "/"                                       |
	 *
	 */
	function urlResolve(url) {
		if (!isString(url)) return url;

		var href = url;

		// Support: IE 9-11 only
		if (msie) {
			// Normalize before parse.  Refer Implementation Notes on why this is
			// done in two steps on IE.
			urlParsingNode.setAttribute('href', href);
			href = urlParsingNode.href;
		}

		urlParsingNode.setAttribute('href', href);

		var hostname = urlParsingNode.hostname;

		if (!ipv6InBrackets && hostname.indexOf(':') > -1) {
			hostname = '[' + hostname + ']';
		}

		return {
			href: urlParsingNode.href,
			protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
			host: urlParsingNode.host,
			search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
			hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
			hostname: hostname,
			port: urlParsingNode.port,
			pathname: (urlParsingNode.pathname.charAt(0) === '/')
				? urlParsingNode.pathname
				: '/' + urlParsingNode.pathname
		};
	}

	/**
	 * Parse a request URL and determine whether this is a same-origin request as the application
	 * document.
	 *
	 * @param {string|object} requestUrl The url of the request as a string that will be resolved
	 * or a parsed URL object.
	 * @returns {boolean} Whether the request is for the same origin as the application document.
	 */
	function urlIsSameOrigin(requestUrl) {
		return urlsAreSameOrigin(requestUrl, originUrl);
	}

	/**
	 * Parse a request URL and determine whether it is same-origin as the current document base URL.
	 *
	 * Note: The base URL is usually the same as the document location (`location.href`) but can
	 * be overriden by using the `<base>` tag.
	 *
	 * @param {string|object} requestUrl The url of the request as a string that will be resolved
	 * or a parsed URL object.
	 * @returns {boolean} Whether the URL is same-origin as the document base URL.
	 */
	function urlIsSameOriginAsBaseUrl(requestUrl) {
		return urlsAreSameOrigin(requestUrl, getBaseUrl());
	}

	/**
	 * Create a function that can check a URL's origin against a list of allowed/trusted origins.
	 * The current location's origin is implicitly trusted.
	 *
	 * @param {string[]} trustedOriginUrls - A list of URLs (strings), whose origins are trusted.
	 *
	 * @returns {Function} - A function that receives a URL (string or parsed URL object) and returns
	 *     whether it is of an allowed origin.
	 */
	function urlIsAllowedOriginFactory(trustedOriginUrls) {
		var parsedAllowedOriginUrls = [originUrl].concat(trustedOriginUrls.map(urlResolve));

		/**
		 * Check whether the specified URL (string or parsed URL object) has an origin that is allowed
		 * based on a list of trusted-origin URLs. The current location's origin is implicitly
		 * trusted.
		 *
		 * @param {string|Object} requestUrl - The URL to be checked (provided as a string that will be
		 *     resolved or a parsed URL object).
		 *
		 * @returns {boolean} - Whether the specified URL is of an allowed origin.
		 */
		return function urlIsAllowedOrigin(requestUrl) {
			var parsedUrl = urlResolve(requestUrl);
			return parsedAllowedOriginUrls.some(urlsAreSameOrigin.bind(null, parsedUrl));
		};
	}

	/**
	 * Determine if two URLs share the same origin.
	 *
	 * @param {string|Object} url1 - First URL to compare as a string or a normalized URL in the form of
	 *     a dictionary object returned by `urlResolve()`.
	 * @param {string|object} url2 - Second URL to compare as a string or a normalized URL in the form
	 *     of a dictionary object returned by `urlResolve()`.
	 *
	 * @returns {boolean} - True if both URLs have the same origin, and false otherwise.
	 */
	function urlsAreSameOrigin(url1, url2) {
		url1 = urlResolve(url1);
		url2 = urlResolve(url2);

		return (url1.protocol === url2.protocol &&
			url1.host === url2.host);
	}

	/**
	 * Returns the current document base URL.
	 * @returns {string}
	 */
	function getBaseUrl() {
		if (window.document.baseURI) {
			return window.document.baseURI;
		}

		// `document.baseURI` is available everywhere except IE
		if (!baseUrlParsingNode) {
			baseUrlParsingNode = window.document.createElement('a');
			baseUrlParsingNode.href = '.';

			// Work-around for IE bug described in Implementation Notes. The fix in `urlResolve()` is not
			// suitable here because we need to track changes to the base URL.
			baseUrlParsingNode = baseUrlParsingNode.cloneNode(false);
		}
		return baseUrlParsingNode.href;
	}

	/**
	 * @ngdoc service
	 * @name $window
	 * @this
	 *
	 * @description
	 * A reference to the browser's `window` object. While `window`
	 * is globally available in JavaScript, it causes testability problems, because
	 * it is a global variable. In AngularJS we always refer to it through the
	 * `$window` service, so it may be overridden, removed or mocked for testing.
	 *
	 * Expressions, like the one defined for the `ngClick` directive in the example
	 * below, are evaluated with respect to the current scope.  Therefore, there is
	 * no risk of inadvertently coding in a dependency on a global value in such an
	 * expression.
	 *
	 * @example
	   <example module="windowExample" name="window-service">
		 <file name="index.html">
		   <script>
			 angular.module('windowExample', [])
			   .controller('ExampleController', ['$scope', '$window', function($scope, $window) {
				 $scope.greeting = 'Hello, World!';
				 $scope.doGreeting = function(greeting) {
				   $window.alert(greeting);
				 };
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <input type="text" ng-model="greeting" aria-label="greeting" />
			 <button ng-click="doGreeting(greeting)">ALERT</button>
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		  it('should display the greeting in the input box', function() {
		   element(by.model('greeting')).sendKeys('Hello, E2E Tests');
		   // If we click the button it will block the test runner
		   // element(':button').click();
		  });
		 </file>
	   </example>
	 */
	function $WindowProvider() {
		this.$get = valueFn(window);
	}

	/**
	 * @name $$cookieReader
	 * @requires $document
	 *
	 * @description
	 * This is a private service for reading cookies used by $http and ngCookies
	 *
	 * @return {Object} a key/value map of the current cookies
	 */
	function $$CookieReader($document) {
		var rawDocument = $document[0] || {};
		var lastCookies = {};
		var lastCookieString = '';

		function safeGetCookie(rawDocument) {
			try {
				return rawDocument.cookie || '';
			} catch (e) {
				return '';
			}
		}

		function safeDecodeURIComponent(str) {
			try {
				return decodeURIComponent(str);
			} catch (e) {
				return str;
			}
		}

		return function () {
			var cookieArray, cookie, i, index, name;
			var currentCookieString = safeGetCookie(rawDocument);

			if (currentCookieString !== lastCookieString) {
				lastCookieString = currentCookieString;
				cookieArray = lastCookieString.split('; ');
				lastCookies = {};

				for (i = 0; i < cookieArray.length; i++) {
					cookie = cookieArray[i];
					index = cookie.indexOf('=');
					if (index > 0) { //ignore nameless cookies
						name = safeDecodeURIComponent(cookie.substring(0, index));
						// the first value that is seen for a cookie is the most
						// specific one.  values for the same cookie name that
						// follow are for less specific paths.
						if (isUndefined(lastCookies[name])) {
							lastCookies[name] = safeDecodeURIComponent(cookie.substring(index + 1));
						}
					}
				}
			}
			return lastCookies;
		};
	}

	$$CookieReader.$inject = ['$document'];

	/** @this */
	function $$CookieReaderProvider() {
		this.$get = $$CookieReader;
	}

	/* global currencyFilter: true,
	 dateFilter: true,
	 filterFilter: true,
	 jsonFilter: true,
	 limitToFilter: true,
	 lowercaseFilter: true,
	 numberFilter: true,
	 orderByFilter: true,
	 uppercaseFilter: true,
	 */

	/**
	 * @ngdoc provider
	 * @name $filterProvider
	 * @description
	 *
	 * Filters are just functions which transform input to an output. However filters need to be
	 * Dependency Injected. To achieve this a filter definition consists of a factory function which is
	 * annotated with dependencies and is responsible for creating a filter function.
	 *
	 * <div class="alert alert-warning">
	 * **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
	 * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
	 * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
	 * (`myapp_subsection_filterx`).
	 * </div>
	 *
	 * ```js
	 *   // Filter registration
	 *   function MyModule($provide, $filterProvider) {
	 *     // create a service to demonstrate injection (not always needed)
	 *     $provide.value('greet', function(name){
	 *       return 'Hello ' + name + '!';
	 *     });
	 *
	 *     // register a filter factory which uses the
	 *     // greet service to demonstrate DI.
	 *     $filterProvider.register('greet', function(greet){
	 *       // return the filter function which uses the greet service
	 *       // to generate salutation
	 *       return function(text) {
	 *         // filters need to be forgiving so check input validity
	 *         return text && greet(text) || text;
	 *       };
	 *     });
	 *   }
	 * ```
	 *
	 * The filter function is registered with the `$injector` under the filter name suffix with
	 * `Filter`.
	 *
	 * ```js
	 *   it('should be the same instance', inject(
	 *     function($filterProvider) {
	 *       $filterProvider.register('reverse', function(){
	 *         return ...;
	 *       });
	 *     },
	 *     function($filter, reverseFilter) {
	 *       expect($filter('reverse')).toBe(reverseFilter);
	 *     });
	 * ```
	 *
	 *
	 * For more information about how AngularJS filters work, and how to create your own filters, see
	 * {@link guide/filter Filters} in the AngularJS Developer Guide.
	 */

	/**
	 * @ngdoc service
	 * @name $filter
	 * @kind function
	 * @description
	 * Filters are used for formatting data displayed to the user.
	 *
	 * They can be used in view templates, controllers or services. AngularJS comes
	 * with a collection of [built-in filters](api/ng/filter), but it is easy to
	 * define your own as well.
	 *
	 * The general syntax in templates is as follows:
	 *
	 * ```html
	 * {{ expression [| filter_name[:parameter_value] ... ] }}
	 * ```
	 *
	 * @param {String} name Name of the filter function to retrieve
	 * @return {Function} the filter function
	 * @example
	   <example name="$filter" module="filterExample">
		 <file name="index.html">
		   <div ng-controller="MainCtrl">
			<h3>{{ originalText }}</h3>
			<h3>{{ filteredText }}</h3>
		   </div>
		 </file>
	
		 <file name="script.js">
		  angular.module('filterExample', [])
		  .controller('MainCtrl', function($scope, $filter) {
			$scope.originalText = 'hello';
			$scope.filteredText = $filter('uppercase')($scope.originalText);
		  });
		 </file>
	   </example>
	  */
	$FilterProvider.$inject = ['$provide'];
	/** @this */
	function $FilterProvider($provide) {
		var suffix = 'Filter';

		/**
		 * @ngdoc method
		 * @name $filterProvider#register
		 * @param {string|Object} name Name of the filter function, or an object map of filters where
		 *    the keys are the filter names and the values are the filter factories.
		 *
		 *    <div class="alert alert-warning">
		 *    **Note:** Filter names must be valid AngularJS {@link expression} identifiers, such as `uppercase` or `orderBy`.
		 *    Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
		 *    your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
		 *    (`myapp_subsection_filterx`).
		 *    </div>
		  * @param {Function} factory If the first argument was a string, a factory function for the filter to be registered.
		 * @returns {Object} Registered filter instance, or if a map of filters was provided then a map
		 *    of the registered filter instances.
		 */
		function register(name, factory) {
			if (isObject(name)) {
				var filters = {};
				forEach(name, function (filter, key) {
					filters[key] = register(key, filter);
				});
				return filters;
			} else {
				return $provide.factory(name + suffix, factory);
			}
		}
		this.register = register;

		this.$get = ['$injector', function ($injector) {
			return function (name) {
				return $injector.get(name + suffix);
			};
		}];

		////////////////////////////////////////

		/* global
		  currencyFilter: false,
		  dateFilter: false,
		  filterFilter: false,
		  jsonFilter: false,
		  limitToFilter: false,
		  lowercaseFilter: false,
		  numberFilter: false,
		  orderByFilter: false,
		  uppercaseFilter: false
		*/

		register('currency', currencyFilter);
		register('date', dateFilter);
		register('filter', filterFilter);
		register('json', jsonFilter);
		register('limitTo', limitToFilter);
		register('lowercase', lowercaseFilter);
		register('number', numberFilter);
		register('orderBy', orderByFilter);
		register('uppercase', uppercaseFilter);
	}

	/**
	 * @ngdoc filter
	 * @name filter
	 * @kind function
	 *
	 * @description
	 * Selects a subset of items from `array` and returns it as a new array.
	 *
	 * @param {Array} array The source array.
	 * <div class="alert alert-info">
	 *   **Note**: If the array contains objects that reference themselves, filtering is not possible.
	 * </div>
	 * @param {string|Object|function()} expression The predicate to be used for selecting items from
	 *   `array`.
	 *
	 *   Can be one of:
	 *
	 *   - `string`: The string is used for matching against the contents of the `array`. All strings or
	 *     objects with string properties in `array` that match this string will be returned. This also
	 *     applies to nested object properties.
	 *     The predicate can be negated by prefixing the string with `!`.
	 *
	 *   - `Object`: A pattern object can be used to filter specific properties on objects contained
	 *     by `array`. For example `{name:"M", phone:"1"}` predicate will return an array of items
	 *     which have property `name` containing "M" and property `phone` containing "1". A special
	 *     property name (`$` by default) can be used (e.g. as in `{$: "text"}`) to accept a match
	 *     against any property of the object or its nested object properties. That's equivalent to the
	 *     simple substring match with a `string` as described above. The special property name can be
	 *     overwritten, using the `anyPropertyKey` parameter.
	 *     The predicate can be negated by prefixing the string with `!`.
	 *     For example `{name: "!M"}` predicate will return an array of items which have property `name`
	 *     not containing "M".
	 *
	 *     Note that a named property will match properties on the same level only, while the special
	 *     `$` property will match properties on the same level or deeper. E.g. an array item like
	 *     `{name: {first: 'John', last: 'Doe'}}` will **not** be matched by `{name: 'John'}`, but
	 *     **will** be matched by `{$: 'John'}`.
	 *
	 *   - `function(value, index, array)`: A predicate function can be used to write arbitrary filters.
	 *     The function is called for each element of the array, with the element, its index, and
	 *     the entire array itself as arguments.
	 *
	 *     The final result is an array of those elements that the predicate returned true for.
	 *
	 * @param {function(actual, expected)|true|false} [comparator] Comparator which is used in
	 *     determining if values retrieved using `expression` (when it is not a function) should be
	 *     considered a match based on the expected value (from the filter expression) and actual
	 *     value (from the object in the array).
	 *
	 *   Can be one of:
	 *
	 *   - `function(actual, expected)`:
	 *     The function will be given the object value and the predicate value to compare and
	 *     should return true if both values should be considered equal.
	 *
	 *   - `true`: A shorthand for `function(actual, expected) { return angular.equals(actual, expected)}`.
	 *     This is essentially strict comparison of expected and actual.
	 *
	 *   - `false`: A short hand for a function which will look for a substring match in a case
	 *     insensitive way. Primitive values are converted to strings. Objects are not compared against
	 *     primitives, unless they have a custom `toString` method (e.g. `Date` objects).
	 *
	 *
	 *   Defaults to `false`.
	 *
	 * @param {string} [anyPropertyKey] The special property name that matches against any property.
	 *     By default `$`.
	 *
	 * @example
	   <example name="filter-filter">
		 <file name="index.html">
		   <div ng-init="friends = [{name:'John', phone:'555-1276'},
									{name:'Mary', phone:'800-BIG-MARY'},
									{name:'Mike', phone:'555-4321'},
									{name:'Adam', phone:'555-5678'},
									{name:'Julie', phone:'555-8765'},
									{name:'Juliette', phone:'555-5678'}]"></div>
	
		   <label>Search: <input ng-model="searchText"></label>
		   <table id="searchTextResults">
			 <tr><th>Name</th><th>Phone</th></tr>
			 <tr ng-repeat="friend in friends | filter:searchText">
			   <td>{{friend.name}}</td>
			   <td>{{friend.phone}}</td>
			 </tr>
		   </table>
		   <hr>
		   <label>Any: <input ng-model="search.$"></label> <br>
		   <label>Name only <input ng-model="search.name"></label><br>
		   <label>Phone only <input ng-model="search.phone"></label><br>
		   <label>Equality <input type="checkbox" ng-model="strict"></label><br>
		   <table id="searchObjResults">
			 <tr><th>Name</th><th>Phone</th></tr>
			 <tr ng-repeat="friendObj in friends | filter:search:strict">
			   <td>{{friendObj.name}}</td>
			   <td>{{friendObj.phone}}</td>
			 </tr>
		   </table>
		 </file>
		 <file name="protractor.js" type="protractor">
		   var expectFriendNames = function(expectedNames, key) {
			 element.all(by.repeater(key + ' in friends').column(key + '.name')).then(function(arr) {
			   arr.forEach(function(wd, i) {
				 expect(wd.getText()).toMatch(expectedNames[i]);
			   });
			 });
		   };
	
		   it('should search across all fields when filtering with a string', function() {
			 var searchText = element(by.model('searchText'));
			 searchText.clear();
			 searchText.sendKeys('m');
			 expectFriendNames(['Mary', 'Mike', 'Adam'], 'friend');
	
			 searchText.clear();
			 searchText.sendKeys('76');
			 expectFriendNames(['John', 'Julie'], 'friend');
		   });
	
		   it('should search in specific fields when filtering with a predicate object', function() {
			 var searchAny = element(by.model('search.$'));
			 searchAny.clear();
			 searchAny.sendKeys('i');
			 expectFriendNames(['Mary', 'Mike', 'Julie', 'Juliette'], 'friendObj');
		   });
		   it('should use a equal comparison when comparator is true', function() {
			 var searchName = element(by.model('search.name'));
			 var strict = element(by.model('strict'));
			 searchName.clear();
			 searchName.sendKeys('Julie');
			 strict.click();
			 expectFriendNames(['Julie'], 'friendObj');
		   });
		 </file>
	   </example>
	 */

	function filterFilter() {
		return function (array, expression, comparator, anyPropertyKey) {
			if (!isArrayLike(array)) {
				if (array == null) {
					return array;
				} else {
					throw minErr('filter')('notarray', 'Expected array but received: {0}', array);
				}
			}

			anyPropertyKey = anyPropertyKey || '$';
			var expressionType = getTypeForFilter(expression);
			var predicateFn;
			var matchAgainstAnyProp;

			switch (expressionType) {
				case 'function':
					predicateFn = expression;
					break;
				case 'boolean':
				case 'null':
				case 'number':
				case 'string':
					matchAgainstAnyProp = true;
				// falls through
				case 'object':
					predicateFn = createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp);
					break;
				default:
					return array;
			}

			return Array.prototype.filter.call(array, predicateFn);
		};
	}

	// Helper functions for `filterFilter`
	function createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp) {
		var shouldMatchPrimitives = isObject(expression) && (anyPropertyKey in expression);
		var predicateFn;

		if (comparator === true) {
			comparator = equals;
		} else if (!isFunction(comparator)) {
			comparator = function (actual, expected) {
				if (isUndefined(actual)) {
					// No substring matching against `undefined`
					return false;
				}
				if ((actual === null) || (expected === null)) {
					// No substring matching against `null`; only match against `null`
					return actual === expected;
				}
				if (isObject(expected) || (isObject(actual) && !hasCustomToString(actual))) {
					// Should not compare primitives against objects, unless they have custom `toString` method
					return false;
				}

				actual = lowercase('' + actual);
				expected = lowercase('' + expected);
				return actual.indexOf(expected) !== -1;
			};
		}

		predicateFn = function (item) {
			if (shouldMatchPrimitives && !isObject(item)) {
				return deepCompare(item, expression[anyPropertyKey], comparator, anyPropertyKey, false);
			}
			return deepCompare(item, expression, comparator, anyPropertyKey, matchAgainstAnyProp);
		};

		return predicateFn;
	}

	function deepCompare(actual, expected, comparator, anyPropertyKey, matchAgainstAnyProp, dontMatchWholeObject) {
		var actualType = getTypeForFilter(actual);
		var expectedType = getTypeForFilter(expected);

		if ((expectedType === 'string') && (expected.charAt(0) === '!')) {
			return !deepCompare(actual, expected.substring(1), comparator, anyPropertyKey, matchAgainstAnyProp);
		} else if (isArray(actual)) {
			// In case `actual` is an array, consider it a match
			// if ANY of it's items matches `expected`
			return actual.some(function (item) {
				return deepCompare(item, expected, comparator, anyPropertyKey, matchAgainstAnyProp);
			});
		}

		switch (actualType) {
			case 'object':
				var key;
				if (matchAgainstAnyProp) {
					for (key in actual) {
						// Under certain, rare, circumstances, key may not be a string and `charAt` will be undefined
						// See: https://github.com/angular/angular.js/issues/15644
						if (key.charAt && (key.charAt(0) !== '$') &&
							deepCompare(actual[key], expected, comparator, anyPropertyKey, true)) {
							return true;
						}
					}
					return dontMatchWholeObject ? false : deepCompare(actual, expected, comparator, anyPropertyKey, false);
				} else if (expectedType === 'object') {
					for (key in expected) {
						var expectedVal = expected[key];
						if (isFunction(expectedVal) || isUndefined(expectedVal)) {
							continue;
						}

						var matchAnyProperty = key === anyPropertyKey;
						var actualVal = matchAnyProperty ? actual : actual[key];
						if (!deepCompare(actualVal, expectedVal, comparator, anyPropertyKey, matchAnyProperty, matchAnyProperty)) {
							return false;
						}
					}
					return true;
				} else {
					return comparator(actual, expected);
				}
			case 'function':
				return false;
			default:
				return comparator(actual, expected);
		}
	}

	// Used for easily differentiating between `null` and actual `object`
	function getTypeForFilter(val) {
		return (val === null) ? 'null' : typeof val;
	}

	var MAX_DIGITS = 22;
	var DECIMAL_SEP = '.';
	var ZERO_CHAR = '0';

	/**
	 * @ngdoc filter
	 * @name currency
	 * @kind function
	 *
	 * @description
	 * Formats a number as a currency (ie $1,234.56). When no currency symbol is provided, default
	 * symbol for current locale is used.
	 *
	 * @param {number} amount Input to filter.
	 * @param {string=} symbol Currency symbol or identifier to be displayed.
	 * @param {number=} fractionSize Number of decimal places to round the amount to, defaults to default max fraction size for current locale
	 * @returns {string} Formatted number.
	 *
	 *
	 * @example
	   <example module="currencyExample" name="currency-filter">
		 <file name="index.html">
		   <script>
			 angular.module('currencyExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.amount = 1234.56;
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <input type="number" ng-model="amount" aria-label="amount"> <br>
			 default currency symbol ($): <span id="currency-default">{{amount | currency}}</span><br>
			 custom currency identifier (USD$): <span id="currency-custom">{{amount | currency:"USD$"}}</span><br>
			 no fractions (0): <span id="currency-no-fractions">{{amount | currency:"USD$":0}}</span>
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should init with 1234.56', function() {
			 expect(element(by.id('currency-default')).getText()).toBe('$1,234.56');
			 expect(element(by.id('currency-custom')).getText()).toBe('USD$1,234.56');
			 expect(element(by.id('currency-no-fractions')).getText()).toBe('USD$1,235');
		   });
		   it('should update', function() {
			 if (browser.params.browser === 'safari') {
			   // Safari does not understand the minus key. See
			   // https://github.com/angular/protractor/issues/481
			   return;
			 }
			 element(by.model('amount')).clear();
			 element(by.model('amount')).sendKeys('-1234');
			 expect(element(by.id('currency-default')).getText()).toBe('-$1,234.00');
			 expect(element(by.id('currency-custom')).getText()).toBe('-USD$1,234.00');
			 expect(element(by.id('currency-no-fractions')).getText()).toBe('-USD$1,234');
		   });
		 </file>
	   </example>
	 */
	currencyFilter.$inject = ['$locale'];
	function currencyFilter($locale) {
		var formats = $locale.NUMBER_FORMATS;
		return function (amount, currencySymbol, fractionSize) {
			if (isUndefined(currencySymbol)) {
				currencySymbol = formats.CURRENCY_SYM;
			}

			if (isUndefined(fractionSize)) {
				fractionSize = formats.PATTERNS[1].maxFrac;
			}

			// If the currency symbol is empty, trim whitespace around the symbol
			var currencySymbolRe = !currencySymbol ? /\s*\u00A4\s*/g : /\u00A4/g;

			// if null or undefined pass it through
			return (amount == null)
				? amount
				: formatNumber(amount, formats.PATTERNS[1], formats.GROUP_SEP, formats.DECIMAL_SEP, fractionSize).
					replace(currencySymbolRe, currencySymbol);
		};
	}

	/**
	 * @ngdoc filter
	 * @name number
	 * @kind function
	 *
	 * @description
	 * Formats a number as text.
	 *
	 * If the input is null or undefined, it will just be returned.
	 * If the input is infinite (Infinity or -Infinity), the Infinity symbol '∞' or '-∞' is returned, respectively.
	 * If the input is not a number an empty string is returned.
	 *
	 *
	 * @param {number|string} number Number to format.
	 * @param {(number|string)=} fractionSize Number of decimal places to round the number to.
	 * If this is not provided then the fraction size is computed from the current locale's number
	 * formatting pattern. In the case of the default locale, it will be 3.
	 * @returns {string} Number rounded to `fractionSize` appropriately formatted based on the current
	 *                   locale (e.g., in the en_US locale it will have "." as the decimal separator and
	 *                   include "," group separators after each third digit).
	 *
	 * @example
	   <example module="numberFilterExample" name="number-filter">
		 <file name="index.html">
		   <script>
			 angular.module('numberFilterExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.val = 1234.56789;
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <label>Enter number: <input ng-model='val'></label><br>
			 Default formatting: <span id='number-default'>{{val | number}}</span><br>
			 No fractions: <span>{{val | number:0}}</span><br>
			 Negative number: <span>{{-val | number:4}}</span>
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should format numbers', function() {
			 expect(element(by.id('number-default')).getText()).toBe('1,234.568');
			 expect(element(by.binding('val | number:0')).getText()).toBe('1,235');
			 expect(element(by.binding('-val | number:4')).getText()).toBe('-1,234.5679');
		   });
	
		   it('should update', function() {
			 element(by.model('val')).clear();
			 element(by.model('val')).sendKeys('3374.333');
			 expect(element(by.id('number-default')).getText()).toBe('3,374.333');
			 expect(element(by.binding('val | number:0')).getText()).toBe('3,374');
			 expect(element(by.binding('-val | number:4')).getText()).toBe('-3,374.3330');
		  });
		 </file>
	   </example>
	 */
	numberFilter.$inject = ['$locale'];
	function numberFilter($locale) {
		var formats = $locale.NUMBER_FORMATS;
		return function (number, fractionSize) {

			// if null or undefined pass it through
			return (number == null)
				? number
				: formatNumber(number, formats.PATTERNS[0], formats.GROUP_SEP, formats.DECIMAL_SEP,
					fractionSize);
		};
	}

	/**
	 * Parse a number (as a string) into three components that can be used
	 * for formatting the number.
	 *
	 * (Significant bits of this parse algorithm came from https://github.com/MikeMcl/big.js/)
	 *
	 * @param  {string} numStr The number to parse
	 * @return {object} An object describing this number, containing the following keys:
	 *  - d : an array of digits containing leading zeros as necessary
	 *  - i : the number of the digits in `d` that are to the left of the decimal point
	 *  - e : the exponent for numbers that would need more than `MAX_DIGITS` digits in `d`
	 *
	 */
	function parse(numStr) {
		var exponent = 0, digits, numberOfIntegerDigits;
		var i, j, zeros;

		// Decimal point?
		if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
			numStr = numStr.replace(DECIMAL_SEP, '');
		}

		// Exponential form?
		if ((i = numStr.search(/e/i)) > 0) {
			// Work out the exponent.
			if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
			numberOfIntegerDigits += +numStr.slice(i + 1);
			numStr = numStr.substring(0, i);
		} else if (numberOfIntegerDigits < 0) {
			// There was no decimal point or exponent so it is an integer.
			numberOfIntegerDigits = numStr.length;
		}

		// Count the number of leading zeros.
		for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) { /* empty */ }

		if (i === (zeros = numStr.length)) {
			// The digits are all zero.
			digits = [0];
			numberOfIntegerDigits = 1;
		} else {
			// Count the number of trailing zeros
			zeros--;
			while (numStr.charAt(zeros) === ZERO_CHAR) zeros--;

			// Trailing zeros are insignificant so ignore them
			numberOfIntegerDigits -= i;
			digits = [];
			// Convert string to array of digits without leading/trailing zeros.
			for (j = 0; i <= zeros; i++, j++) {
				digits[j] = +numStr.charAt(i);
			}
		}

		// If the number overflows the maximum allowed digits then use an exponent.
		if (numberOfIntegerDigits > MAX_DIGITS) {
			digits = digits.splice(0, MAX_DIGITS - 1);
			exponent = numberOfIntegerDigits - 1;
			numberOfIntegerDigits = 1;
		}

		return { d: digits, e: exponent, i: numberOfIntegerDigits };
	}

	/**
	 * Round the parsed number to the specified number of decimal places
	 * This function changed the parsedNumber in-place
	 */
	function roundNumber(parsedNumber, fractionSize, minFrac, maxFrac) {
		var digits = parsedNumber.d;
		var fractionLen = digits.length - parsedNumber.i;

		// determine fractionSize if it is not specified; `+fractionSize` converts it to a number
		fractionSize = (isUndefined(fractionSize)) ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

		// The index of the digit to where rounding is to occur
		var roundAt = fractionSize + parsedNumber.i;
		var digit = digits[roundAt];

		if (roundAt > 0) {
			// Drop fractional digits beyond `roundAt`
			digits.splice(Math.max(parsedNumber.i, roundAt));

			// Set non-fractional digits beyond `roundAt` to 0
			for (var j = roundAt; j < digits.length; j++) {
				digits[j] = 0;
			}
		} else {
			// We rounded to zero so reset the parsedNumber
			fractionLen = Math.max(0, fractionLen);
			parsedNumber.i = 1;
			digits.length = Math.max(1, roundAt = fractionSize + 1);
			digits[0] = 0;
			for (var i = 1; i < roundAt; i++) digits[i] = 0;
		}

		if (digit >= 5) {
			if (roundAt - 1 < 0) {
				for (var k = 0; k > roundAt; k--) {
					digits.unshift(0);
					parsedNumber.i++;
				}
				digits.unshift(1);
				parsedNumber.i++;
			} else {
				digits[roundAt - 1]++;
			}
		}

		// Pad out with zeros to get the required fraction length
		for (; fractionLen < Math.max(0, fractionSize); fractionLen++) digits.push(0);


		// Do any carrying, e.g. a digit was rounded up to 10
		var carry = digits.reduceRight(function (carry, d, i, digits) {
			d = d + carry;
			digits[i] = d % 10;
			return Math.floor(d / 10);
		}, 0);
		if (carry) {
			digits.unshift(carry);
			parsedNumber.i++;
		}
	}

	/**
	 * Format a number into a string
	 * @param  {number} number       The number to format
	 * @param  {{
	 *           minFrac, // the minimum number of digits required in the fraction part of the number
	 *           maxFrac, // the maximum number of digits required in the fraction part of the number
	 *           gSize,   // number of digits in each group of separated digits
	 *           lgSize,  // number of digits in the last group of digits before the decimal separator
	 *           negPre,  // the string to go in front of a negative number (e.g. `-` or `(`))
	 *           posPre,  // the string to go in front of a positive number
	 *           negSuf,  // the string to go after a negative number (e.g. `)`)
	 *           posSuf   // the string to go after a positive number
	 *         }} pattern
	 * @param  {string} groupSep     The string to separate groups of number (e.g. `,`)
	 * @param  {string} decimalSep   The string to act as the decimal separator (e.g. `.`)
	 * @param  {[type]} fractionSize The size of the fractional part of the number
	 * @return {string}              The number formatted as a string
	 */
	function formatNumber(number, pattern, groupSep, decimalSep, fractionSize) {

		if (!(isString(number) || isNumber(number)) || isNaN(number)) return '';

		var isInfinity = !isFinite(number);
		var isZero = false;
		var numStr = Math.abs(number) + '',
			formattedText = '',
			parsedNumber;

		if (isInfinity) {
			formattedText = '\u221e';
		} else {
			parsedNumber = parse(numStr);

			roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

			var digits = parsedNumber.d;
			var integerLen = parsedNumber.i;
			var exponent = parsedNumber.e;
			var decimals = [];
			isZero = digits.reduce(function (isZero, d) { return isZero && !d; }, true);

			// pad zeros for small numbers
			while (integerLen < 0) {
				digits.unshift(0);
				integerLen++;
			}

			// extract decimals digits
			if (integerLen > 0) {
				decimals = digits.splice(integerLen, digits.length);
			} else {
				decimals = digits;
				digits = [0];
			}

			// format the integer digits with grouping separators
			var groups = [];
			if (digits.length >= pattern.lgSize) {
				groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(''));
			}
			while (digits.length > pattern.gSize) {
				groups.unshift(digits.splice(-pattern.gSize, digits.length).join(''));
			}
			if (digits.length) {
				groups.unshift(digits.join(''));
			}
			formattedText = groups.join(groupSep);

			// append the decimal digits
			if (decimals.length) {
				formattedText += decimalSep + decimals.join('');
			}

			if (exponent) {
				formattedText += 'e+' + exponent;
			}
		}
		if (number < 0 && !isZero) {
			return pattern.negPre + formattedText + pattern.negSuf;
		} else {
			return pattern.posPre + formattedText + pattern.posSuf;
		}
	}

	function padNumber(num, digits, trim, negWrap) {
		var neg = '';
		if (num < 0 || (negWrap && num <= 0)) {
			if (negWrap) {
				num = -num + 1;
			} else {
				num = -num;
				neg = '-';
			}
		}
		num = '' + num;
		while (num.length < digits) num = ZERO_CHAR + num;
		if (trim) {
			num = num.substr(num.length - digits);
		}
		return neg + num;
	}


	function dateGetter(name, size, offset, trim, negWrap) {
		offset = offset || 0;
		return function (date) {
			var value = date['get' + name]();
			if (offset > 0 || value > -offset) {
				value += offset;
			}
			if (value === 0 && offset === -12) value = 12;
			return padNumber(value, size, trim, negWrap);
		};
	}

	function dateStrGetter(name, shortForm, standAlone) {
		return function (date, formats) {
			var value = date['get' + name]();
			var propPrefix = (standAlone ? 'STANDALONE' : '') + (shortForm ? 'SHORT' : '');
			var get = uppercase(propPrefix + name);

			return formats[get][value];
		};
	}

	function timeZoneGetter(date, formats, offset) {
		var zone = -1 * offset;
		var paddedZone = (zone >= 0) ? '+' : '';

		paddedZone += padNumber(Math[zone > 0 ? 'floor' : 'ceil'](zone / 60), 2) +
			padNumber(Math.abs(zone % 60), 2);

		return paddedZone;
	}

	function getFirstThursdayOfYear(year) {
		// 0 = index of January
		var dayOfWeekOnFirst = (new Date(year, 0, 1)).getDay();
		// 4 = index of Thursday (+1 to account for 1st = 5)
		// 11 = index of *next* Thursday (+1 account for 1st = 12)
		return new Date(year, 0, ((dayOfWeekOnFirst <= 4) ? 5 : 12) - dayOfWeekOnFirst);
	}

	function getThursdayThisWeek(datetime) {
		return new Date(datetime.getFullYear(), datetime.getMonth(),
			// 4 = index of Thursday
			datetime.getDate() + (4 - datetime.getDay()));
	}

	function weekGetter(size) {
		return function (date) {
			var firstThurs = getFirstThursdayOfYear(date.getFullYear()),
				thisThurs = getThursdayThisWeek(date);

			var diff = +thisThurs - +firstThurs,
				result = 1 + Math.round(diff / 6.048e8); // 6.048e8 ms per week

			return padNumber(result, size);
		};
	}

	function ampmGetter(date, formats) {
		return date.getHours() < 12 ? formats.AMPMS[0] : formats.AMPMS[1];
	}

	function eraGetter(date, formats) {
		return date.getFullYear() <= 0 ? formats.ERAS[0] : formats.ERAS[1];
	}

	function longEraGetter(date, formats) {
		return date.getFullYear() <= 0 ? formats.ERANAMES[0] : formats.ERANAMES[1];
	}

	var DATE_FORMATS = {
		yyyy: dateGetter('FullYear', 4, 0, false, true),
		yy: dateGetter('FullYear', 2, 0, true, true),
		y: dateGetter('FullYear', 1, 0, false, true),
		MMMM: dateStrGetter('Month'),
		MMM: dateStrGetter('Month', true),
		MM: dateGetter('Month', 2, 1),
		M: dateGetter('Month', 1, 1),
		LLLL: dateStrGetter('Month', false, true),
		dd: dateGetter('Date', 2),
		d: dateGetter('Date', 1),
		HH: dateGetter('Hours', 2),
		H: dateGetter('Hours', 1),
		hh: dateGetter('Hours', 2, -12),
		h: dateGetter('Hours', 1, -12),
		mm: dateGetter('Minutes', 2),
		m: dateGetter('Minutes', 1),
		ss: dateGetter('Seconds', 2),
		s: dateGetter('Seconds', 1),
		// while ISO 8601 requires fractions to be prefixed with `.` or `,`
		// we can be just safely rely on using `sss` since we currently don't support single or two digit fractions
		sss: dateGetter('Milliseconds', 3),
		EEEE: dateStrGetter('Day'),
		EEE: dateStrGetter('Day', true),
		a: ampmGetter,
		Z: timeZoneGetter,
		ww: weekGetter(2),
		w: weekGetter(1),
		G: eraGetter,
		GG: eraGetter,
		GGG: eraGetter,
		GGGG: longEraGetter
	};

	var DATE_FORMATS_SPLIT = /((?:[^yMLdHhmsaZEwG']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|L+|d+|H+|h+|m+|s+|a|Z|G+|w+))([\s\S]*)/,
		NUMBER_STRING = /^-?\d+$/;

	/**
	 * @ngdoc filter
	 * @name date
	 * @kind function
	 *
	 * @description
	 *   Formats `date` to a string based on the requested `format`.
	 *
	 *   `format` string can be composed of the following elements:
	 *
	 *   * `'yyyy'`: 4 digit representation of year (e.g. AD 1 => 0001, AD 2010 => 2010)
	 *   * `'yy'`: 2 digit representation of year, padded (00-99). (e.g. AD 2001 => 01, AD 2010 => 10)
	 *   * `'y'`: 1 digit representation of year, e.g. (AD 1 => 1, AD 199 => 199)
	 *   * `'MMMM'`: Month in year (January-December)
	 *   * `'MMM'`: Month in year (Jan-Dec)
	 *   * `'MM'`: Month in year, padded (01-12)
	 *   * `'M'`: Month in year (1-12)
	 *   * `'LLLL'`: Stand-alone month in year (January-December)
	 *   * `'dd'`: Day in month, padded (01-31)
	 *   * `'d'`: Day in month (1-31)
	 *   * `'EEEE'`: Day in Week,(Sunday-Saturday)
	 *   * `'EEE'`: Day in Week, (Sun-Sat)
	 *   * `'HH'`: Hour in day, padded (00-23)
	 *   * `'H'`: Hour in day (0-23)
	 *   * `'hh'`: Hour in AM/PM, padded (01-12)
	 *   * `'h'`: Hour in AM/PM, (1-12)
	 *   * `'mm'`: Minute in hour, padded (00-59)
	 *   * `'m'`: Minute in hour (0-59)
	 *   * `'ss'`: Second in minute, padded (00-59)
	 *   * `'s'`: Second in minute (0-59)
	 *   * `'sss'`: Millisecond in second, padded (000-999)
	 *   * `'a'`: AM/PM marker
	 *   * `'Z'`: 4 digit (+sign) representation of the timezone offset (-1200-+1200)
	 *   * `'ww'`: Week of year, padded (00-53). Week 01 is the week with the first Thursday of the year
	 *   * `'w'`: Week of year (0-53). Week 1 is the week with the first Thursday of the year
	 *   * `'G'`, `'GG'`, `'GGG'`: The abbreviated form of the era string (e.g. 'AD')
	 *   * `'GGGG'`: The long form of the era string (e.g. 'Anno Domini')
	 *
	 *   `format` string can also be one of the following predefined
	 *   {@link guide/i18n localizable formats}:
	 *
	 *   * `'medium'`: equivalent to `'MMM d, y h:mm:ss a'` for en_US locale
	 *     (e.g. Sep 3, 2010 12:05:08 PM)
	 *   * `'short'`: equivalent to `'M/d/yy h:mm a'` for en_US  locale (e.g. 9/3/10 12:05 PM)
	 *   * `'fullDate'`: equivalent to `'EEEE, MMMM d, y'` for en_US  locale
	 *     (e.g. Friday, September 3, 2010)
	 *   * `'longDate'`: equivalent to `'MMMM d, y'` for en_US  locale (e.g. September 3, 2010)
	 *   * `'mediumDate'`: equivalent to `'MMM d, y'` for en_US  locale (e.g. Sep 3, 2010)
	 *   * `'shortDate'`: equivalent to `'M/d/yy'` for en_US locale (e.g. 9/3/10)
	 *   * `'mediumTime'`: equivalent to `'h:mm:ss a'` for en_US locale (e.g. 12:05:08 PM)
	 *   * `'shortTime'`: equivalent to `'h:mm a'` for en_US locale (e.g. 12:05 PM)
	 *
	 *   `format` string can contain literal values. These need to be escaped by surrounding with single quotes (e.g.
	 *   `"h 'in the morning'"`). In order to output a single quote, escape it - i.e., two single quotes in a sequence
	 *   (e.g. `"h 'o''clock'"`).
	 *
	 *   Any other characters in the `format` string will be output as-is.
	 *
	 * @param {(Date|number|string)} date Date to format either as Date object, milliseconds (string or
	 *    number) or various ISO 8601 datetime string formats (e.g. yyyy-MM-ddTHH:mm:ss.sssZ and its
	 *    shorter versions like yyyy-MM-ddTHH:mmZ, yyyy-MM-dd or yyyyMMddTHHmmssZ). If no timezone is
	 *    specified in the string input, the time is considered to be in the local timezone.
	 * @param {string=} format Formatting rules (see Description). If not specified,
	 *    `mediumDate` is used.
	 * @param {string=} timezone Timezone to be used for formatting. It understands UTC/GMT and the
	 *    continental US time zone abbreviations, but for general use, use a time zone offset, for
	 *    example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
	 *    If not specified, the timezone of the browser will be used.
	 * @returns {string} Formatted string or the input if input is not recognized as date/millis.
	 *
	 * @example
	   <example name="filter-date">
		 <file name="index.html">
		   <span ng-non-bindable>{{1288323623006 | date:'medium'}}</span>:
			   <span>{{1288323623006 | date:'medium'}}</span><br>
		   <span ng-non-bindable>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span>:
			  <span>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span><br>
		   <span ng-non-bindable>{{1288323623006 | date:'MM/dd/yyyy @ h:mma'}}</span>:
			  <span>{{'1288323623006' | date:'MM/dd/yyyy @ h:mma'}}</span><br>
		   <span ng-non-bindable>{{1288323623006 | date:"MM/dd/yyyy 'at' h:mma"}}</span>:
			  <span>{{'1288323623006' | date:"MM/dd/yyyy 'at' h:mma"}}</span><br>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should format date', function() {
			 expect(element(by.binding("1288323623006 | date:'medium'")).getText()).
				toMatch(/Oct 2\d, 2010 \d{1,2}:\d{2}:\d{2} (AM|PM)/);
			 expect(element(by.binding("1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'")).getText()).
				toMatch(/2010-10-2\d \d{2}:\d{2}:\d{2} (-|\+)?\d{4}/);
			 expect(element(by.binding("'1288323623006' | date:'MM/dd/yyyy @ h:mma'")).getText()).
				toMatch(/10\/2\d\/2010 @ \d{1,2}:\d{2}(AM|PM)/);
			 expect(element(by.binding("'1288323623006' | date:\"MM/dd/yyyy 'at' h:mma\"")).getText()).
				toMatch(/10\/2\d\/2010 at \d{1,2}:\d{2}(AM|PM)/);
		   });
		 </file>
	   </example>
	 */
	dateFilter.$inject = ['$locale'];
	function dateFilter($locale) {


		var R_ISO8601_STR = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
		// 1        2       3         4          5          6          7          8  9     10      11
		function jsonStringToDate(string) {
			var match;
			if ((match = string.match(R_ISO8601_STR))) {
				var date = new Date(0),
					tzHour = 0,
					tzMin = 0,
					dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear,
					timeSetter = match[8] ? date.setUTCHours : date.setHours;

				if (match[9]) {
					tzHour = toInt(match[9] + match[10]);
					tzMin = toInt(match[9] + match[11]);
				}
				dateSetter.call(date, toInt(match[1]), toInt(match[2]) - 1, toInt(match[3]));
				var h = toInt(match[4] || 0) - tzHour;
				var m = toInt(match[5] || 0) - tzMin;
				var s = toInt(match[6] || 0);
				var ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
				timeSetter.call(date, h, m, s, ms);
				return date;
			}
			return string;
		}


		return function (date, format, timezone) {
			var text = '',
				parts = [],
				fn, match;

			format = format || 'mediumDate';
			format = $locale.DATETIME_FORMATS[format] || format;
			if (isString(date)) {
				date = NUMBER_STRING.test(date) ? toInt(date) : jsonStringToDate(date);
			}

			if (isNumber(date)) {
				date = new Date(date);
			}

			if (!isDate(date) || !isFinite(date.getTime())) {
				return date;
			}

			while (format) {
				match = DATE_FORMATS_SPLIT.exec(format);
				if (match) {
					parts = concat(parts, match, 1);
					format = parts.pop();
				} else {
					parts.push(format);
					format = null;
				}
			}

			var dateTimezoneOffset = date.getTimezoneOffset();
			if (timezone) {
				dateTimezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
				date = convertTimezoneToLocal(date, timezone, true);
			}
			forEach(parts, function (value) {
				fn = DATE_FORMATS[value];
				text += fn ? fn(date, $locale.DATETIME_FORMATS, dateTimezoneOffset)
					: value === '\'\'' ? '\'' : value.replace(/(^'|'$)/g, '').replace(/''/g, '\'');
			});

			return text;
		};
	}


	/**
	 * @ngdoc filter
	 * @name json
	 * @kind function
	 *
	 * @description
	 *   Allows you to convert a JavaScript object into JSON string.
	 *
	 *   This filter is mostly useful for debugging. When using the double curly {{value}} notation
	 *   the binding is automatically converted to JSON.
	 *
	 * @param {*} object Any JavaScript object (including arrays and primitive types) to filter.
	 * @param {number=} spacing The number of spaces to use per indentation, defaults to 2.
	 * @returns {string} JSON string.
	 *
	 *
	 * @example
	   <example name="filter-json">
		 <file name="index.html">
		   <pre id="default-spacing">{{ {'name':'value'} | json }}</pre>
		   <pre id="custom-spacing">{{ {'name':'value'} | json:4 }}</pre>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should jsonify filtered objects', function() {
			 expect(element(by.id('default-spacing')).getText()).toMatch(/\{\n {2}"name": ?"value"\n}/);
			 expect(element(by.id('custom-spacing')).getText()).toMatch(/\{\n {4}"name": ?"value"\n}/);
		   });
		 </file>
	   </example>
	 *
	 */
	function jsonFilter() {
		return function (object, spacing) {
			if (isUndefined(spacing)) {
				spacing = 2;
			}
			return toJson(object, spacing);
		};
	}


	/**
	 * @ngdoc filter
	 * @name lowercase
	 * @kind function
	 * @description
	 * Converts string to lowercase.
	 *
	 * See the {@link ng.uppercase uppercase filter documentation} for a functionally identical example.
	 *
	 * @see angular.lowercase
	 */
	var lowercaseFilter = valueFn(lowercase);


	/**
	 * @ngdoc filter
	 * @name uppercase
	 * @kind function
	 * @description
	 * Converts string to uppercase.
	 * @example
	   <example module="uppercaseFilterExample" name="filter-uppercase">
		 <file name="index.html">
		   <script>
			 angular.module('uppercaseFilterExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.title = 'This is a title';
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <!-- This title should be formatted normally -->
			 <h1>{{title}}</h1>
			 <!-- This title should be capitalized -->
			 <h1>{{title | uppercase}}</h1>
		   </div>
		 </file>
	   </example>
	 */
	var uppercaseFilter = valueFn(uppercase);

	/**
	 * @ngdoc filter
	 * @name limitTo
	 * @kind function
	 *
	 * @description
	 * Creates a new array or string containing only a specified number of elements. The elements are
	 * taken from either the beginning or the end of the source array, string or number, as specified by
	 * the value and sign (positive or negative) of `limit`. Other array-like objects are also supported
	 * (e.g. array subclasses, NodeLists, jqLite/jQuery collections etc). If a number is used as input,
	 * it is converted to a string.
	 *
	 * @param {Array|ArrayLike|string|number} input - Array/array-like, string or number to be limited.
	 * @param {string|number} limit - The length of the returned array or string. If the `limit` number
	 *     is positive, `limit` number of items from the beginning of the source array/string are copied.
	 *     If the number is negative, `limit` number  of items from the end of the source array/string
	 *     are copied. The `limit` will be trimmed if it exceeds `array.length`. If `limit` is undefined,
	 *     the input will be returned unchanged.
	 * @param {(string|number)=} begin - Index at which to begin limitation. As a negative index,
	 *     `begin` indicates an offset from the end of `input`. Defaults to `0`.
	 * @returns {Array|string} A new sub-array or substring of length `limit` or less if the input had
	 *     less than `limit` elements.
	 *
	 * @example
	   <example module="limitToExample" name="limit-to-filter">
		 <file name="index.html">
		   <script>
			 angular.module('limitToExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.numbers = [1,2,3,4,5,6,7,8,9];
				 $scope.letters = "abcdefghi";
				 $scope.longNumber = 2345432342;
				 $scope.numLimit = 3;
				 $scope.letterLimit = 3;
				 $scope.longNumberLimit = 3;
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <label>
				Limit {{numbers}} to:
				<input type="number" step="1" ng-model="numLimit">
			 </label>
			 <p>Output numbers: {{ numbers | limitTo:numLimit }}</p>
			 <label>
				Limit {{letters}} to:
				<input type="number" step="1" ng-model="letterLimit">
			 </label>
			 <p>Output letters: {{ letters | limitTo:letterLimit }}</p>
			 <label>
				Limit {{longNumber}} to:
				<input type="number" step="1" ng-model="longNumberLimit">
			 </label>
			 <p>Output long number: {{ longNumber | limitTo:longNumberLimit }}</p>
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   var numLimitInput = element(by.model('numLimit'));
		   var letterLimitInput = element(by.model('letterLimit'));
		   var longNumberLimitInput = element(by.model('longNumberLimit'));
		   var limitedNumbers = element(by.binding('numbers | limitTo:numLimit'));
		   var limitedLetters = element(by.binding('letters | limitTo:letterLimit'));
		   var limitedLongNumber = element(by.binding('longNumber | limitTo:longNumberLimit'));
	
		   it('should limit the number array to first three items', function() {
			 expect(numLimitInput.getAttribute('value')).toBe('3');
			 expect(letterLimitInput.getAttribute('value')).toBe('3');
			 expect(longNumberLimitInput.getAttribute('value')).toBe('3');
			 expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3]');
			 expect(limitedLetters.getText()).toEqual('Output letters: abc');
			 expect(limitedLongNumber.getText()).toEqual('Output long number: 234');
		   });
	
		   // There is a bug in safari and protractor that doesn't like the minus key
		   // it('should update the output when -3 is entered', function() {
		   //   numLimitInput.clear();
		   //   numLimitInput.sendKeys('-3');
		   //   letterLimitInput.clear();
		   //   letterLimitInput.sendKeys('-3');
		   //   longNumberLimitInput.clear();
		   //   longNumberLimitInput.sendKeys('-3');
		   //   expect(limitedNumbers.getText()).toEqual('Output numbers: [7,8,9]');
		   //   expect(limitedLetters.getText()).toEqual('Output letters: ghi');
		   //   expect(limitedLongNumber.getText()).toEqual('Output long number: 342');
		   // });
	
		   it('should not exceed the maximum size of input array', function() {
			 numLimitInput.clear();
			 numLimitInput.sendKeys('100');
			 letterLimitInput.clear();
			 letterLimitInput.sendKeys('100');
			 longNumberLimitInput.clear();
			 longNumberLimitInput.sendKeys('100');
			 expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3,4,5,6,7,8,9]');
			 expect(limitedLetters.getText()).toEqual('Output letters: abcdefghi');
			 expect(limitedLongNumber.getText()).toEqual('Output long number: 2345432342');
		   });
		 </file>
	   </example>
	*/
	function limitToFilter() {
		return function (input, limit, begin) {
			if (Math.abs(Number(limit)) === Infinity) {
				limit = Number(limit);
			} else {
				limit = toInt(limit);
			}
			if (isNumberNaN(limit)) return input;

			if (isNumber(input)) input = input.toString();
			if (!isArrayLike(input)) return input;

			begin = (!begin || isNaN(begin)) ? 0 : toInt(begin);
			begin = (begin < 0) ? Math.max(0, input.length + begin) : begin;

			if (limit >= 0) {
				return sliceFn(input, begin, begin + limit);
			} else {
				if (begin === 0) {
					return sliceFn(input, limit, input.length);
				} else {
					return sliceFn(input, Math.max(0, begin + limit), begin);
				}
			}
		};
	}

	function sliceFn(input, begin, end) {
		if (isString(input)) return input.slice(begin, end);

		return slice.call(input, begin, end);
	}

	/**
	 * @ngdoc filter
	 * @name orderBy
	 * @kind function
	 *
	 * @description
	 * Returns an array containing the items from the specified `collection`, ordered by a `comparator`
	 * function based on the values computed using the `expression` predicate.
	 *
	 * For example, `[{id: 'foo'}, {id: 'bar'}] | orderBy:'id'` would result in
	 * `[{id: 'bar'}, {id: 'foo'}]`.
	 *
	 * The `collection` can be an Array or array-like object (e.g. NodeList, jQuery object, TypedArray,
	 * String, etc).
	 *
	 * The `expression` can be a single predicate, or a list of predicates each serving as a tie-breaker
	 * for the preceding one. The `expression` is evaluated against each item and the output is used
	 * for comparing with other items.
	 *
	 * You can change the sorting order by setting `reverse` to `true`. By default, items are sorted in
	 * ascending order.
	 *
	 * The comparison is done using the `comparator` function. If none is specified, a default, built-in
	 * comparator is used (see below for details - in a nutshell, it compares numbers numerically and
	 * strings alphabetically).
	 *
	 * ### Under the hood
	 *
	 * Ordering the specified `collection` happens in two phases:
	 *
	 * 1. All items are passed through the predicate (or predicates), and the returned values are saved
	 *    along with their type (`string`, `number` etc). For example, an item `{label: 'foo'}`, passed
	 *    through a predicate that extracts the value of the `label` property, would be transformed to:
	 *    ```
	 *    {
	 *      value: 'foo',
	 *      type: 'string',
	 *      index: ...
	 *    }
	 *    ```
	 *    **Note:** `null` values use `'null'` as their type.
	 * 2. The comparator function is used to sort the items, based on the derived values, types and
	 *    indices.
	 *
	 * If you use a custom comparator, it will be called with pairs of objects of the form
	 * `{value: ..., type: '...', index: ...}` and is expected to return `0` if the objects are equal
	 * (as far as the comparator is concerned), `-1` if the 1st one should be ranked higher than the
	 * second, or `1` otherwise.
	 *
	 * In order to ensure that the sorting will be deterministic across platforms, if none of the
	 * specified predicates can distinguish between two items, `orderBy` will automatically introduce a
	 * dummy predicate that returns the item's index as `value`.
	 * (If you are using a custom comparator, make sure it can handle this predicate as well.)
	 *
	 * If a custom comparator still can't distinguish between two items, then they will be sorted based
	 * on their index using the built-in comparator.
	 *
	 * Finally, in an attempt to simplify things, if a predicate returns an object as the extracted
	 * value for an item, `orderBy` will try to convert that object to a primitive value, before passing
	 * it to the comparator. The following rules govern the conversion:
	 *
	 * 1. If the object has a `valueOf()` method that returns a primitive, its return value will be
	 *    used instead.<br />
	 *    (If the object has a `valueOf()` method that returns another object, then the returned object
	 *    will be used in subsequent steps.)
	 * 2. If the object has a custom `toString()` method (i.e. not the one inherited from `Object`) that
	 *    returns a primitive, its return value will be used instead.<br />
	 *    (If the object has a `toString()` method that returns another object, then the returned object
	 *    will be used in subsequent steps.)
	 * 3. No conversion; the object itself is used.
	 *
	 * ### The default comparator
	 *
	 * The default, built-in comparator should be sufficient for most usecases. In short, it compares
	 * numbers numerically, strings alphabetically (and case-insensitively), for objects falls back to
	 * using their index in the original collection, sorts values of different types by type and puts
	 * `undefined` and `null` values at the end of the sorted list.
	 *
	 * More specifically, it follows these steps to determine the relative order of items:
	 *
	 * 1. If the compared values are of different types:
	 *    - If one of the values is undefined, consider it "greater than" the other.
	 *    - Else if one of the values is null, consider it "greater than" the other.
	 *    - Else compare the types themselves alphabetically.
	 * 2. If both values are of type `string`, compare them alphabetically in a case- and
	 *    locale-insensitive way.
	 * 3. If both values are objects, compare their indices instead.
	 * 4. Otherwise, return:
	 *    -  `0`, if the values are equal (by strict equality comparison, i.e. using `===`).
	 *    - `-1`, if the 1st value is "less than" the 2nd value (compared using the `<` operator).
	 *    -  `1`, otherwise.
	 *
	 * **Note:** If you notice numbers not being sorted as expected, make sure they are actually being
	 *           saved as numbers and not strings.
	 * **Note:** For the purpose of sorting, `null` and `undefined` are considered "greater than"
	 *           any other value (with undefined "greater than" null). This effectively means that `null`
	 *           and `undefined` values end up at the end of a list sorted in ascending order.
	 * **Note:** `null` values use `'null'` as their type to be able to distinguish them from objects.
	 *
	 * @param {Array|ArrayLike} collection - The collection (array or array-like object) to sort.
	 * @param {(Function|string|Array.<Function|string>)=} expression - A predicate (or list of
	 *    predicates) to be used by the comparator to determine the order of elements.
	 *
	 *    Can be one of:
	 *
	 *    - `Function`: A getter function. This function will be called with each item as argument and
	 *      the return value will be used for sorting.
	 *    - `string`: An AngularJS expression. This expression will be evaluated against each item and the
	 *      result will be used for sorting. For example, use `'label'` to sort by a property called
	 *      `label` or `'label.substring(0, 3)'` to sort by the first 3 characters of the `label`
	 *      property.<br />
	 *      (The result of a constant expression is interpreted as a property name to be used for
	 *      comparison. For example, use `'"special name"'` (note the extra pair of quotes) to sort by a
	 *      property called `special name`.)<br />
	 *      An expression can be optionally prefixed with `+` or `-` to control the sorting direction,
	 *      ascending or descending. For example, `'+label'` or `'-label'`. If no property is provided,
	 *      (e.g. `'+'` or `'-'`), the collection element itself is used in comparisons.
	 *    - `Array`: An array of function and/or string predicates. If a predicate cannot determine the
	 *      relative order of two items, the next predicate is used as a tie-breaker.
	 *
	 * **Note:** If the predicate is missing or empty then it defaults to `'+'`.
	 *
	 * @param {boolean=} reverse - If `true`, reverse the sorting order.
	 * @param {(Function)=} comparator - The comparator function used to determine the relative order of
	 *    value pairs. If omitted, the built-in comparator will be used.
	 *
	 * @returns {Array} - The sorted array.
	 *
	 *
	 * @example
	 * ### Ordering a table with `ngRepeat`
	 *
	 * The example below demonstrates a simple {@link ngRepeat ngRepeat}, where the data is sorted by
	 * age in descending order (expression is set to `'-age'`). The `comparator` is not set, which means
	 * it defaults to the built-in comparator.
	 *
	   <example name="orderBy-static" module="orderByExample1">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <table class="friends">
			   <tr>
				 <th>Name</th>
				 <th>Phone Number</th>
				 <th>Age</th>
			   </tr>
			   <tr ng-repeat="friend in friends | orderBy:'-age'">
				 <td>{{friend.name}}</td>
				 <td>{{friend.phone}}</td>
				 <td>{{friend.age}}</td>
			   </tr>
			 </table>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('orderByExample1', [])
			 .controller('ExampleController', ['$scope', function($scope) {
			   $scope.friends = [
				 {name: 'John',   phone: '555-1212',  age: 10},
				 {name: 'Mary',   phone: '555-9876',  age: 19},
				 {name: 'Mike',   phone: '555-4321',  age: 21},
				 {name: 'Adam',   phone: '555-5678',  age: 35},
				 {name: 'Julie',  phone: '555-8765',  age: 29}
			   ];
			 }]);
		 </file>
		 <file name="style.css">
		   .friends {
			 border-collapse: collapse;
		   }
	
		   .friends th {
			 border-bottom: 1px solid;
		   }
		   .friends td, .friends th {
			 border-left: 1px solid;
			 padding: 5px 10px;
		   }
		   .friends td:first-child, .friends th:first-child {
			 border-left: none;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   // Element locators
		   var names = element.all(by.repeater('friends').column('friend.name'));
	
		   it('should sort friends by age in reverse order', function() {
			 expect(names.get(0).getText()).toBe('Adam');
			 expect(names.get(1).getText()).toBe('Julie');
			 expect(names.get(2).getText()).toBe('Mike');
			 expect(names.get(3).getText()).toBe('Mary');
			 expect(names.get(4).getText()).toBe('John');
		   });
		 </file>
	   </example>
	 * <hr />
	 *
	 * @example
	 * ### Changing parameters dynamically
	 *
	 * All parameters can be changed dynamically. The next example shows how you can make the columns of
	 * a table sortable, by binding the `expression` and `reverse` parameters to scope properties.
	 *
	   <example name="orderBy-dynamic" module="orderByExample2">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
			 <hr/>
			 <button ng-click="propertyName = null; reverse = false">Set to unsorted</button>
			 <hr/>
			 <table class="friends">
			   <tr>
				 <th>
				   <button ng-click="sortBy('name')">Name</button>
				   <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
				 </th>
				 <th>
				   <button ng-click="sortBy('phone')">Phone Number</button>
				   <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
				 </th>
				 <th>
				   <button ng-click="sortBy('age')">Age</button>
				   <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
				 </th>
			   </tr>
			   <tr ng-repeat="friend in friends | orderBy:propertyName:reverse">
				 <td>{{friend.name}}</td>
				 <td>{{friend.phone}}</td>
				 <td>{{friend.age}}</td>
			   </tr>
			 </table>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('orderByExample2', [])
			 .controller('ExampleController', ['$scope', function($scope) {
			   var friends = [
				 {name: 'John',   phone: '555-1212',  age: 10},
				 {name: 'Mary',   phone: '555-9876',  age: 19},
				 {name: 'Mike',   phone: '555-4321',  age: 21},
				 {name: 'Adam',   phone: '555-5678',  age: 35},
				 {name: 'Julie',  phone: '555-8765',  age: 29}
			   ];
	
			   $scope.propertyName = 'age';
			   $scope.reverse = true;
			   $scope.friends = friends;
	
			   $scope.sortBy = function(propertyName) {
				 $scope.reverse = ($scope.propertyName === propertyName) ? !$scope.reverse : false;
				 $scope.propertyName = propertyName;
			   };
			 }]);
		 </file>
		 <file name="style.css">
		   .friends {
			 border-collapse: collapse;
		   }
	
		   .friends th {
			 border-bottom: 1px solid;
		   }
		   .friends td, .friends th {
			 border-left: 1px solid;
			 padding: 5px 10px;
		   }
		   .friends td:first-child, .friends th:first-child {
			 border-left: none;
		   }
	
		   .sortorder:after {
			 content: '\25b2';   // BLACK UP-POINTING TRIANGLE
		   }
		   .sortorder.reverse:after {
			 content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   // Element locators
		   var unsortButton = element(by.partialButtonText('unsorted'));
		   var nameHeader = element(by.partialButtonText('Name'));
		   var phoneHeader = element(by.partialButtonText('Phone'));
		   var ageHeader = element(by.partialButtonText('Age'));
		   var firstName = element(by.repeater('friends').column('friend.name').row(0));
		   var lastName = element(by.repeater('friends').column('friend.name').row(4));
	
		   it('should sort friends by some property, when clicking on the column header', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 phoneHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Mary');
	
			 nameHeader.click();
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('Mike');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Adam');
		   });
	
		   it('should sort friends in reverse order, when clicking on the same column', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Adam');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
		   });
	
		   it('should restore the original order, when clicking "Set to unsorted"', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 unsortButton.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Julie');
		   });
		 </file>
	   </example>
	 * <hr />
	 *
	 * @example
	 * ### Using `orderBy` inside a controller
	 *
	 * It is also possible to call the `orderBy` filter manually, by injecting `orderByFilter`, and
	 * calling it with the desired parameters. (Alternatively, you could inject the `$filter` factory
	 * and retrieve the `orderBy` filter with `$filter('orderBy')`.)
	 *
	   <example name="orderBy-call-manually" module="orderByExample3">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
			 <hr/>
			 <button ng-click="sortBy(null)">Set to unsorted</button>
			 <hr/>
			 <table class="friends">
			   <tr>
				 <th>
				   <button ng-click="sortBy('name')">Name</button>
				   <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
				 </th>
				 <th>
				   <button ng-click="sortBy('phone')">Phone Number</button>
				   <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
				 </th>
				 <th>
				   <button ng-click="sortBy('age')">Age</button>
				   <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
				 </th>
			   </tr>
			   <tr ng-repeat="friend in friends">
				 <td>{{friend.name}}</td>
				 <td>{{friend.phone}}</td>
				 <td>{{friend.age}}</td>
			   </tr>
			 </table>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('orderByExample3', [])
			 .controller('ExampleController', ['$scope', 'orderByFilter', function($scope, orderBy) {
			   var friends = [
				 {name: 'John',   phone: '555-1212',  age: 10},
				 {name: 'Mary',   phone: '555-9876',  age: 19},
				 {name: 'Mike',   phone: '555-4321',  age: 21},
				 {name: 'Adam',   phone: '555-5678',  age: 35},
				 {name: 'Julie',  phone: '555-8765',  age: 29}
			   ];
	
			   $scope.propertyName = 'age';
			   $scope.reverse = true;
			   $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);
	
			   $scope.sortBy = function(propertyName) {
				 $scope.reverse = (propertyName !== null && $scope.propertyName === propertyName)
					 ? !$scope.reverse : false;
				 $scope.propertyName = propertyName;
				 $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);
			   };
			 }]);
		 </file>
		 <file name="style.css">
		   .friends {
			 border-collapse: collapse;
		   }
	
		   .friends th {
			 border-bottom: 1px solid;
		   }
		   .friends td, .friends th {
			 border-left: 1px solid;
			 padding: 5px 10px;
		   }
		   .friends td:first-child, .friends th:first-child {
			 border-left: none;
		   }
	
		   .sortorder:after {
			 content: '\25b2';   // BLACK UP-POINTING TRIANGLE
		   }
		   .sortorder.reverse:after {
			 content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   // Element locators
		   var unsortButton = element(by.partialButtonText('unsorted'));
		   var nameHeader = element(by.partialButtonText('Name'));
		   var phoneHeader = element(by.partialButtonText('Phone'));
		   var ageHeader = element(by.partialButtonText('Age'));
		   var firstName = element(by.repeater('friends').column('friend.name').row(0));
		   var lastName = element(by.repeater('friends').column('friend.name').row(4));
	
		   it('should sort friends by some property, when clicking on the column header', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 phoneHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Mary');
	
			 nameHeader.click();
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('Mike');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Adam');
		   });
	
		   it('should sort friends in reverse order, when clicking on the same column', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Adam');
	
			 ageHeader.click();
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
		   });
	
		   it('should restore the original order, when clicking "Set to unsorted"', function() {
			 expect(firstName.getText()).toBe('Adam');
			 expect(lastName.getText()).toBe('John');
	
			 unsortButton.click();
			 expect(firstName.getText()).toBe('John');
			 expect(lastName.getText()).toBe('Julie');
		   });
		 </file>
	   </example>
	 * <hr />
	 *
	 * @example
	 * ### Using a custom comparator
	 *
	 * If you have very specific requirements about the way items are sorted, you can pass your own
	 * comparator function. For example, you might need to compare some strings in a locale-sensitive
	 * way. (When specifying a custom comparator, you also need to pass a value for the `reverse`
	 * argument - passing `false` retains the default sorting order, i.e. ascending.)
	 *
	   <example name="orderBy-custom-comparator" module="orderByExample4">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <div class="friends-container custom-comparator">
			   <h3>Locale-sensitive Comparator</h3>
			   <table class="friends">
				 <tr>
				   <th>Name</th>
				   <th>Favorite Letter</th>
				 </tr>
				 <tr ng-repeat="friend in friends | orderBy:'favoriteLetter':false:localeSensitiveComparator">
				   <td>{{friend.name}}</td>
				   <td>{{friend.favoriteLetter}}</td>
				 </tr>
			   </table>
			 </div>
			 <div class="friends-container default-comparator">
			   <h3>Default Comparator</h3>
			   <table class="friends">
				 <tr>
				   <th>Name</th>
				   <th>Favorite Letter</th>
				 </tr>
				 <tr ng-repeat="friend in friends | orderBy:'favoriteLetter'">
				   <td>{{friend.name}}</td>
				   <td>{{friend.favoriteLetter}}</td>
				 </tr>
			   </table>
			 </div>
		   </div>
		 </file>
		 <file name="script.js">
		   angular.module('orderByExample4', [])
			 .controller('ExampleController', ['$scope', function($scope) {
			   $scope.friends = [
				 {name: 'John',   favoriteLetter: 'Ä'},
				 {name: 'Mary',   favoriteLetter: 'Ü'},
				 {name: 'Mike',   favoriteLetter: 'Ö'},
				 {name: 'Adam',   favoriteLetter: 'H'},
				 {name: 'Julie',  favoriteLetter: 'Z'}
			   ];
	
			   $scope.localeSensitiveComparator = function(v1, v2) {
				 // If we don't get strings, just compare by index
				 if (v1.type !== 'string' || v2.type !== 'string') {
				   return (v1.index < v2.index) ? -1 : 1;
				 }
	
				 // Compare strings alphabetically, taking locale into account
				 return v1.value.localeCompare(v2.value);
			   };
			 }]);
		 </file>
		 <file name="style.css">
		   .friends-container {
			 display: inline-block;
			 margin: 0 30px;
		   }
	
		   .friends {
			 border-collapse: collapse;
		   }
	
		   .friends th {
			 border-bottom: 1px solid;
		   }
		   .friends td, .friends th {
			 border-left: 1px solid;
			 padding: 5px 10px;
		   }
		   .friends td:first-child, .friends th:first-child {
			 border-left: none;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   // Element locators
		   var container = element(by.css('.custom-comparator'));
		   var names = container.all(by.repeater('friends').column('friend.name'));
	
		   it('should sort friends by favorite letter (in correct alphabetical order)', function() {
			 expect(names.get(0).getText()).toBe('John');
			 expect(names.get(1).getText()).toBe('Adam');
			 expect(names.get(2).getText()).toBe('Mike');
			 expect(names.get(3).getText()).toBe('Mary');
			 expect(names.get(4).getText()).toBe('Julie');
		   });
		 </file>
	   </example>
	 *
	 */
	orderByFilter.$inject = ['$parse'];
	function orderByFilter($parse) {
		return function (array, sortPredicate, reverseOrder, compareFn) {

			if (array == null) return array;
			if (!isArrayLike(array)) {
				throw minErr('orderBy')('notarray', 'Expected array but received: {0}', array);
			}

			if (!isArray(sortPredicate)) { sortPredicate = [sortPredicate]; }
			if (sortPredicate.length === 0) { sortPredicate = ['+']; }

			var predicates = processPredicates(sortPredicate);

			var descending = reverseOrder ? -1 : 1;

			// Define the `compare()` function. Use a default comparator if none is specified.
			var compare = isFunction(compareFn) ? compareFn : defaultCompare;

			// The next three lines are a version of a Swartzian Transform idiom from Perl
			// (sometimes called the Decorate-Sort-Undecorate idiom)
			// See https://en.wikipedia.org/wiki/Schwartzian_transform
			var compareValues = Array.prototype.map.call(array, getComparisonObject);
			compareValues.sort(doComparison);
			array = compareValues.map(function (item) { return item.value; });

			return array;

			function getComparisonObject(value, index) {
				// NOTE: We are adding an extra `tieBreaker` value based on the element's index.
				// This will be used to keep the sort stable when none of the input predicates can
				// distinguish between two elements.
				return {
					value: value,
					tieBreaker: { value: index, type: 'number', index: index },
					predicateValues: predicates.map(function (predicate) {
						return getPredicateValue(predicate.get(value), index);
					})
				};
			}

			function doComparison(v1, v2) {
				for (var i = 0, ii = predicates.length; i < ii; i++) {
					var result = compare(v1.predicateValues[i], v2.predicateValues[i]);
					if (result) {
						return result * predicates[i].descending * descending;
					}
				}

				return (compare(v1.tieBreaker, v2.tieBreaker) || defaultCompare(v1.tieBreaker, v2.tieBreaker)) * descending;
			}
		};

		function processPredicates(sortPredicates) {
			return sortPredicates.map(function (predicate) {
				var descending = 1, get = identity;

				if (isFunction(predicate)) {
					get = predicate;
				} else if (isString(predicate)) {
					if ((predicate.charAt(0) === '+' || predicate.charAt(0) === '-')) {
						descending = predicate.charAt(0) === '-' ? -1 : 1;
						predicate = predicate.substring(1);
					}
					if (predicate !== '') {
						get = $parse(predicate);
						if (get.constant) {
							var key = get();
							get = function (value) { return value[key]; };
						}
					}
				}
				return { get: get, descending: descending };
			});
		}

		function isPrimitive(value) {
			switch (typeof value) {
				case 'number': /* falls through */
				case 'boolean': /* falls through */
				case 'string':
					return true;
				default:
					return false;
			}
		}

		function objectValue(value) {
			// If `valueOf` is a valid function use that
			if (isFunction(value.valueOf)) {
				value = value.valueOf();
				if (isPrimitive(value)) return value;
			}
			// If `toString` is a valid function and not the one from `Object.prototype` use that
			if (hasCustomToString(value)) {
				value = value.toString();
				if (isPrimitive(value)) return value;
			}

			return value;
		}

		function getPredicateValue(value, index) {
			var type = typeof value;
			if (value === null) {
				type = 'null';
			} else if (type === 'object') {
				value = objectValue(value);
			}
			return { value: value, type: type, index: index };
		}

		function defaultCompare(v1, v2) {
			var result = 0;
			var type1 = v1.type;
			var type2 = v2.type;

			if (type1 === type2) {
				var value1 = v1.value;
				var value2 = v2.value;

				if (type1 === 'string') {
					// Compare strings case-insensitively
					value1 = value1.toLowerCase();
					value2 = value2.toLowerCase();
				} else if (type1 === 'object') {
					// For basic objects, use the position of the object
					// in the collection instead of the value
					if (isObject(value1)) value1 = v1.index;
					if (isObject(value2)) value2 = v2.index;
				}

				if (value1 !== value2) {
					result = value1 < value2 ? -1 : 1;
				}
			} else {
				result = (type1 === 'undefined') ? 1 :
					(type2 === 'undefined') ? -1 :
						(type1 === 'null') ? 1 :
							(type2 === 'null') ? -1 :
								(type1 < type2) ? -1 : 1;
			}

			return result;
		}
	}

	function ngDirective(directive) {
		if (isFunction(directive)) {
			directive = {
				link: directive
			};
		}
		directive.restrict = directive.restrict || 'AC';
		return valueFn(directive);
	}

	/**
	 * @ngdoc directive
	 * @name a
	 * @restrict E
	 *
	 * @description
	 * Modifies the default behavior of the html a tag so that the default action is prevented when
	 * the href attribute is empty.
	 *
	 * For dynamically creating `href` attributes for a tags, see the {@link ng.ngHref `ngHref`} directive.
	 */
	var htmlAnchorDirective = valueFn({
		restrict: 'E',
		compile: function (element, attr) {
			if (!attr.href && !attr.xlinkHref) {
				return function (scope, element) {
					// If the linked element is not an anchor tag anymore, do nothing
					if (element[0].nodeName.toLowerCase() !== 'a') return;

					// SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
					var href = toString.call(element.prop('href')) === '[object SVGAnimatedString]' ?
						'xlink:href' : 'href';
					element.on('click', function (event) {
						// if we have no href url, then don't navigate anywhere.
						if (!element.attr(href)) {
							event.preventDefault();
						}
					});
				};
			}
		}
	});

	/**
	 * @ngdoc directive
	 * @name ngHref
	 * @restrict A
	 * @priority 99
	 *
	 * @description
	 * Using AngularJS markup like `{{hash}}` in an href attribute will
	 * make the link go to the wrong URL if the user clicks it before
	 * AngularJS has a chance to replace the `{{hash}}` markup with its
	 * value. Until AngularJS replaces the markup the link will be broken
	 * and will most likely return a 404 error. The `ngHref` directive
	 * solves this problem.
	 *
	 * The wrong way to write it:
	 * ```html
	 * <a href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
	 * ```
	 *
	 * The correct way to write it:
	 * ```html
	 * <a ng-href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
	 * ```
	 *
	 * @element A
	 * @param {template} ngHref any string which can contain `{{}}` markup.
	 *
	 * @example
	 * This example shows various combinations of `href`, `ng-href` and `ng-click` attributes
	 * in links and their different behaviors:
		<example name="ng-href">
		  <file name="index.html">
			<input ng-model="value" /><br />
			<a id="link-1" href ng-click="value = 1">link 1</a> (link, don't reload)<br />
			<a id="link-2" href="" ng-click="value = 2">link 2</a> (link, don't reload)<br />
			<a id="link-3" ng-href="/{{'123'}}">link 3</a> (link, reload!)<br />
			<a id="link-4" href="" name="xx" ng-click="value = 4">anchor</a> (link, don't reload)<br />
			<a id="link-5" name="xxx" ng-click="value = 5">anchor</a> (no link)<br />
			<a id="link-6" ng-href="{{value}}">link</a> (link, change location)
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should execute ng-click but not reload when href without value', function() {
			  element(by.id('link-1')).click();
			  expect(element(by.model('value')).getAttribute('value')).toEqual('1');
			  expect(element(by.id('link-1')).getAttribute('href')).toBe('');
			});
	
			it('should execute ng-click but not reload when href empty string', function() {
			  element(by.id('link-2')).click();
			  expect(element(by.model('value')).getAttribute('value')).toEqual('2');
			  expect(element(by.id('link-2')).getAttribute('href')).toBe('');
			});
	
			it('should execute ng-click and change url when ng-href specified', function() {
			  expect(element(by.id('link-3')).getAttribute('href')).toMatch(/\/123$/);
	
			  element(by.id('link-3')).click();
	
			  // At this point, we navigate away from an AngularJS page, so we need
			  // to use browser.driver to get the base webdriver.
	
			  browser.wait(function() {
				return browser.driver.getCurrentUrl().then(function(url) {
				  return url.match(/\/123$/);
				});
			  }, 5000, 'page should navigate to /123');
			});
	
			it('should execute ng-click but not reload when href empty string and name specified', function() {
			  element(by.id('link-4')).click();
			  expect(element(by.model('value')).getAttribute('value')).toEqual('4');
			  expect(element(by.id('link-4')).getAttribute('href')).toBe('');
			});
	
			it('should execute ng-click but not reload when no href but name specified', function() {
			  element(by.id('link-5')).click();
			  expect(element(by.model('value')).getAttribute('value')).toEqual('5');
			  expect(element(by.id('link-5')).getAttribute('href')).toBe(null);
			});
	
			it('should only change url when only ng-href', function() {
			  element(by.model('value')).clear();
			  element(by.model('value')).sendKeys('6');
			  expect(element(by.id('link-6')).getAttribute('href')).toMatch(/\/6$/);
	
			  element(by.id('link-6')).click();
	
			  // At this point, we navigate away from an AngularJS page, so we need
			  // to use browser.driver to get the base webdriver.
			  browser.wait(function() {
				return browser.driver.getCurrentUrl().then(function(url) {
				  return url.match(/\/6$/);
				});
			  }, 5000, 'page should navigate to /6');
			});
		  </file>
		</example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngSrc
	 * @restrict A
	 * @priority 99
	 *
	 * @description
	 * Using AngularJS markup like `{{hash}}` in a `src` attribute doesn't
	 * work right: The browser will fetch from the URL with the literal
	 * text `{{hash}}` until AngularJS replaces the expression inside
	 * `{{hash}}`. The `ngSrc` directive solves this problem.
	 *
	 * The buggy way to write it:
	 * ```html
	 * <img src="http://www.gravatar.com/avatar/{{hash}}" alt="Description"/>
	 * ```
	 *
	 * The correct way to write it:
	 * ```html
	 * <img ng-src="http://www.gravatar.com/avatar/{{hash}}" alt="Description" />
	 * ```
	 *
	 * @element IMG
	 * @param {template} ngSrc any string which can contain `{{}}` markup.
	 */

	/**
	 * @ngdoc directive
	 * @name ngSrcset
	 * @restrict A
	 * @priority 99
	 *
	 * @description
	 * Using AngularJS markup like `{{hash}}` in a `srcset` attribute doesn't
	 * work right: The browser will fetch from the URL with the literal
	 * text `{{hash}}` until AngularJS replaces the expression inside
	 * `{{hash}}`. The `ngSrcset` directive solves this problem.
	 *
	 * The buggy way to write it:
	 * ```html
	 * <img srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description"/>
	 * ```
	 *
	 * The correct way to write it:
	 * ```html
	 * <img ng-srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description" />
	 * ```
	 *
	 * @element IMG
	 * @param {template} ngSrcset any string which can contain `{{}}` markup.
	 */

	/**
	 * @ngdoc directive
	 * @name ngDisabled
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 *
	 * This directive sets the `disabled` attribute on the element (typically a form control,
	 * e.g. `input`, `button`, `select` etc.) if the
	 * {@link guide/expression expression} inside `ngDisabled` evaluates to truthy.
	 *
	 * A special directive is necessary because we cannot use interpolation inside the `disabled`
	 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
	 *
	 * @example
		<example name="ng-disabled">
		  <file name="index.html">
			<label>Click me to toggle: <input type="checkbox" ng-model="checked"></label><br/>
			<button ng-model="button" ng-disabled="checked">Button</button>
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should toggle button', function() {
			  expect(element(by.css('button')).getAttribute('disabled')).toBeFalsy();
			  element(by.model('checked')).click();
			  expect(element(by.css('button')).getAttribute('disabled')).toBeTruthy();
			});
		  </file>
		</example>
	 *
	 * @param {expression} ngDisabled If the {@link guide/expression expression} is truthy,
	 *     then the `disabled` attribute will be set on the element
	 */


	/**
	 * @ngdoc directive
	 * @name ngChecked
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 * Sets the `checked` attribute on the element, if the expression inside `ngChecked` is truthy.
	 *
	 * Note that this directive should not be used together with {@link ngModel `ngModel`},
	 * as this can lead to unexpected behavior.
	 *
	 * A special directive is necessary because we cannot use interpolation inside the `checked`
	 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
	 *
	 * @example
		<example name="ng-checked">
		  <file name="index.html">
			<label>Check me to check both: <input type="checkbox" ng-model="leader"></label><br/>
			<input id="checkFollower" type="checkbox" ng-checked="leader" aria-label="Follower input">
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should check both checkBoxes', function() {
			  expect(element(by.id('checkFollower')).getAttribute('checked')).toBeFalsy();
			  element(by.model('leader')).click();
			  expect(element(by.id('checkFollower')).getAttribute('checked')).toBeTruthy();
			});
		  </file>
		</example>
	 *
	 * @element INPUT
	 * @param {expression} ngChecked If the {@link guide/expression expression} is truthy,
	 *     then the `checked` attribute will be set on the element
	 */


	/**
	 * @ngdoc directive
	 * @name ngReadonly
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 *
	 * Sets the `readonly` attribute on the element, if the expression inside `ngReadonly` is truthy.
	 * Note that `readonly` applies only to `input` elements with specific types. [See the input docs on
	 * MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly) for more information.
	 *
	 * A special directive is necessary because we cannot use interpolation inside the `readonly`
	 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
	 *
	 * @example
		<example name="ng-readonly">
		  <file name="index.html">
			<label>Check me to make text readonly: <input type="checkbox" ng-model="checked"></label><br/>
			<input type="text" ng-readonly="checked" value="I'm AngularJS" aria-label="Readonly field" />
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should toggle readonly attr', function() {
			  expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeFalsy();
			  element(by.model('checked')).click();
			  expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeTruthy();
			});
		  </file>
		</example>
	 *
	 * @element INPUT
	 * @param {expression} ngReadonly If the {@link guide/expression expression} is truthy,
	 *     then special attribute "readonly" will be set on the element
	 */


	/**
	 * @ngdoc directive
	 * @name ngSelected
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 *
	 * Sets the `selected` attribute on the element, if the expression inside `ngSelected` is truthy.
	 *
	 * A special directive is necessary because we cannot use interpolation inside the `selected`
	 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
	 *
	 * <div class="alert alert-warning">
	 *   **Note:** `ngSelected` does not interact with the `select` and `ngModel` directives, it only
	 *   sets the `selected` attribute on the element. If you are using `ngModel` on the select, you
	 *   should not use `ngSelected` on the options, as `ngModel` will set the select value and
	 *   selected options.
	 * </div>
	 *
	 * @example
		<example name="ng-selected">
		  <file name="index.html">
			<label>Check me to select: <input type="checkbox" ng-model="selected"></label><br/>
			<select aria-label="ngSelected demo">
			  <option>Hello!</option>
			  <option id="greet" ng-selected="selected">Greetings!</option>
			</select>
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should select Greetings!', function() {
			  expect(element(by.id('greet')).getAttribute('selected')).toBeFalsy();
			  element(by.model('selected')).click();
			  expect(element(by.id('greet')).getAttribute('selected')).toBeTruthy();
			});
		  </file>
		</example>
	 *
	 * @element OPTION
	 * @param {expression} ngSelected If the {@link guide/expression expression} is truthy,
	 *     then special attribute "selected" will be set on the element
	 */

	/**
	 * @ngdoc directive
	 * @name ngOpen
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 *
	 * Sets the `open` attribute on the element, if the expression inside `ngOpen` is truthy.
	 *
	 * A special directive is necessary because we cannot use interpolation inside the `open`
	 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
	 *
	 * ## A note about browser compatibility
	 *
	 * Internet Explorer and Edge do not support the `details` element, it is
	 * recommended to use {@link ng.ngShow} and {@link ng.ngHide} instead.
	 *
	 * @example
		 <example name="ng-open">
		   <file name="index.html">
			 <label>Toggle details: <input type="checkbox" ng-model="open"></label><br/>
			 <details id="details" ng-open="open">
				<summary>List</summary>
				<ul>
				  <li>Apple</li>
				  <li>Orange</li>
				  <li>Durian</li>
				</ul>
			 </details>
		   </file>
		   <file name="protractor.js" type="protractor">
			 it('should toggle open', function() {
			   expect(element(by.id('details')).getAttribute('open')).toBeFalsy();
			   element(by.model('open')).click();
			   expect(element(by.id('details')).getAttribute('open')).toBeTruthy();
			 });
		   </file>
		 </example>
	 *
	 * @element DETAILS
	 * @param {expression} ngOpen If the {@link guide/expression expression} is truthy,
	 *     then special attribute "open" will be set on the element
	 */

	var ngAttributeAliasDirectives = {};

	// boolean attrs are evaluated
	forEach(BOOLEAN_ATTR, function (propName, attrName) {
		// binding to multiple is not supported
		if (propName === 'multiple') return;

		function defaultLinkFn(scope, element, attr) {
			scope.$watch(attr[normalized], function ngBooleanAttrWatchAction(value) {
				attr.$set(attrName, !!value);
			});
		}

		var normalized = directiveNormalize('ng-' + attrName);
		var linkFn = defaultLinkFn;

		if (propName === 'checked') {
			linkFn = function (scope, element, attr) {
				// ensuring ngChecked doesn't interfere with ngModel when both are set on the same input
				if (attr.ngModel !== attr[normalized]) {
					defaultLinkFn(scope, element, attr);
				}
			};
		}

		ngAttributeAliasDirectives[normalized] = function () {
			return {
				restrict: 'A',
				priority: 100,
				link: linkFn
			};
		};
	});

	// aliased input attrs are evaluated
	forEach(ALIASED_ATTR, function (htmlAttr, ngAttr) {
		ngAttributeAliasDirectives[ngAttr] = function () {
			return {
				priority: 100,
				link: function (scope, element, attr) {
					//special case ngPattern when a literal regular expression value
					//is used as the expression (this way we don't have to watch anything).
					if (ngAttr === 'ngPattern' && attr.ngPattern.charAt(0) === '/') {
						var match = attr.ngPattern.match(REGEX_STRING_REGEXP);
						if (match) {
							attr.$set('ngPattern', new RegExp(match[1], match[2]));
							return;
						}
					}

					scope.$watch(attr[ngAttr], function ngAttrAliasWatchAction(value) {
						attr.$set(ngAttr, value);
					});
				}
			};
		};
	});

	// ng-src, ng-srcset, ng-href are interpolated
	forEach(['src', 'srcset', 'href'], function (attrName) {
		var normalized = directiveNormalize('ng-' + attrName);
		ngAttributeAliasDirectives[normalized] = ['$sce', function ($sce) {
			return {
				priority: 99, // it needs to run after the attributes are interpolated
				link: function (scope, element, attr) {
					var propName = attrName,
						name = attrName;

					if (attrName === 'href' &&
						toString.call(element.prop('href')) === '[object SVGAnimatedString]') {
						name = 'xlinkHref';
						attr.$attr[name] = 'xlink:href';
						propName = null;
					}

					// We need to sanitize the url at least once, in case it is a constant
					// non-interpolated attribute.
					attr.$set(normalized, $sce.getTrustedMediaUrl(attr[normalized]));

					attr.$observe(normalized, function (value) {
						if (!value) {
							if (attrName === 'href') {
								attr.$set(name, null);
							}
							return;
						}

						attr.$set(name, value);

						// Support: IE 9-11 only
						// On IE, if "ng:src" directive declaration is used and "src" attribute doesn't exist
						// then calling element.setAttribute('src', 'foo') doesn't do anything, so we need
						// to set the property as well to achieve the desired effect.
						// We use attr[attrName] value since $set might have sanitized the url.
						if (msie && propName) element.prop(propName, attr[name]);
					});
				}
			};
		}];
	});

	/* global -nullFormCtrl, -PENDING_CLASS, -SUBMITTED_CLASS
	 */
	var nullFormCtrl = {
		$addControl: noop,
		$getControls: valueFn([]),
		$$renameControl: nullFormRenameControl,
		$removeControl: noop,
		$setValidity: noop,
		$setDirty: noop,
		$setPristine: noop,
		$setSubmitted: noop,
		$$setSubmitted: noop
	},
		PENDING_CLASS = 'ng-pending',
		SUBMITTED_CLASS = 'ng-submitted';

	function nullFormRenameControl(control, name) {
		control.$name = name;
	}

	/**
	 * @ngdoc type
	 * @name form.FormController
	 *
	 * @property {boolean} $pristine True if user has not interacted with the form yet.
	 * @property {boolean} $dirty True if user has already interacted with the form.
	 * @property {boolean} $valid True if all of the containing forms and controls are valid.
	 * @property {boolean} $invalid True if at least one containing control or form is invalid.
	 * @property {boolean} $submitted True if user has submitted the form even if its invalid.
	 *
	 * @property {Object} $pending An object hash, containing references to controls or forms with
	 *  pending validators, where:
	 *
	 *  - keys are validations tokens (error names).
	 *  - values are arrays of controls or forms that have a pending validator for the given error name.
	 *
	 * See {@link form.FormController#$error $error} for a list of built-in validation tokens.
	 *
	 * @property {Object} $error An object hash, containing references to controls or forms with failing
	 *  validators, where:
	 *
	 *  - keys are validation tokens (error names),
	 *  - values are arrays of controls or forms that have a failing validator for the given error name.
	 *
	 *  Built-in validation tokens:
	 *  - `email`
	 *  - `max`
	 *  - `maxlength`
	 *  - `min`
	 *  - `minlength`
	 *  - `number`
	 *  - `pattern`
	 *  - `required`
	 *  - `url`
	 *  - `date`
	 *  - `datetimelocal`
	 *  - `time`
	 *  - `week`
	 *  - `month`
	 *
	 * @description
	 * `FormController` keeps track of all its controls and nested forms as well as the state of them,
	 * such as being valid/invalid or dirty/pristine.
	 *
	 * Each {@link ng.directive:form form} directive creates an instance
	 * of `FormController`.
	 *
	 */
	//asks for $scope to fool the BC controller module
	FormController.$inject = ['$element', '$attrs', '$scope', '$animate', '$interpolate'];
	function FormController($element, $attrs, $scope, $animate, $interpolate) {
		this.$$controls = [];

		// init state
		this.$error = {};
		this.$$success = {};
		this.$pending = undefined;
		this.$name = $interpolate($attrs.name || $attrs.ngForm || '')($scope);
		this.$dirty = false;
		this.$pristine = true;
		this.$valid = true;
		this.$invalid = false;
		this.$submitted = false;
		this.$$parentForm = nullFormCtrl;

		this.$$element = $element;
		this.$$animate = $animate;

		setupValidity(this);
	}

	FormController.prototype = {
		/**
		 * @ngdoc method
		 * @name form.FormController#$rollbackViewValue
		 *
		 * @description
		 * Rollback all form controls pending updates to the `$modelValue`.
		 *
		 * Updates may be pending by a debounced event or because the input is waiting for a some future
		 * event defined in `ng-model-options`. This method is typically needed by the reset button of
		 * a form that uses `ng-model-options` to pend updates.
		 */
		$rollbackViewValue: function () {
			forEach(this.$$controls, function (control) {
				control.$rollbackViewValue();
			});
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$commitViewValue
		 *
		 * @description
		 * Commit all form controls pending updates to the `$modelValue`.
		 *
		 * Updates may be pending by a debounced event or because the input is waiting for a some future
		 * event defined in `ng-model-options`. This method is rarely needed as `NgModelController`
		 * usually handles calling this in response to input events.
		 */
		$commitViewValue: function () {
			forEach(this.$$controls, function (control) {
				control.$commitViewValue();
			});
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$addControl
		 * @param {object} control control object, either a {@link form.FormController} or an
		 * {@link ngModel.NgModelController}
		 *
		 * @description
		 * Register a control with the form. Input elements using ngModelController do this automatically
		 * when they are linked.
		 *
		 * Note that the current state of the control will not be reflected on the new parent form. This
		 * is not an issue with normal use, as freshly compiled and linked controls are in a `$pristine`
		 * state.
		 *
		 * However, if the method is used programmatically, for example by adding dynamically created controls,
		 * or controls that have been previously removed without destroying their corresponding DOM element,
		 * it's the developers responsibility to make sure the current state propagates to the parent form.
		 *
		 * For example, if an input control is added that is already `$dirty` and has `$error` properties,
		 * calling `$setDirty()` and `$validate()` afterwards will propagate the state to the parent form.
		 */
		$addControl: function (control) {
			// Breaking change - before, inputs whose name was "hasOwnProperty" were quietly ignored
			// and not added to the scope.  Now we throw an error.
			assertNotHasOwnProperty(control.$name, 'input');
			this.$$controls.push(control);

			if (control.$name) {
				this[control.$name] = control;
			}

			control.$$parentForm = this;
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$getControls
		 * @returns {Array} the controls that are currently part of this form
		 *
		 * @description
		 * This method returns a **shallow copy** of the controls that are currently part of this form.
		 * The controls can be instances of {@link form.FormController `FormController`}
		 * ({@link ngForm "child-forms"}) and of {@link ngModel.NgModelController `NgModelController`}.
		 * If you need access to the controls of child-forms, you have to call `$getControls()`
		 * recursively on them.
		 * This can be used for example to iterate over all controls to validate them.
		 *
		 * The controls can be accessed normally, but adding to, or removing controls from the array has
		 * no effect on the form. Instead, use {@link form.FormController#$addControl `$addControl()`} and
		 * {@link form.FormController#$removeControl `$removeControl()`} for this use-case.
		 * Likewise, adding a control to, or removing a control from the form is not reflected
		 * in the shallow copy. That means you should get a fresh copy from `$getControls()` every time
		 * you need access to the controls.
		 */
		$getControls: function () {
			return shallowCopy(this.$$controls);
		},

		// Private API: rename a form control
		$$renameControl: function (control, newName) {
			var oldName = control.$name;

			if (this[oldName] === control) {
				delete this[oldName];
			}
			this[newName] = control;
			control.$name = newName;
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$removeControl
		 * @param {object} control control object, either a {@link form.FormController} or an
		 * {@link ngModel.NgModelController}
		 *
		 * @description
		 * Deregister a control from the form.
		 *
		 * Input elements using ngModelController do this automatically when they are destroyed.
		 *
		 * Note that only the removed control's validation state (`$errors`etc.) will be removed from the
		 * form. `$dirty`, `$submitted` states will not be changed, because the expected behavior can be
		 * different from case to case. For example, removing the only `$dirty` control from a form may or
		 * may not mean that the form is still `$dirty`.
		 */
		$removeControl: function (control) {
			if (control.$name && this[control.$name] === control) {
				delete this[control.$name];
			}
			forEach(this.$pending, function (value, name) {
				// eslint-disable-next-line no-invalid-this
				this.$setValidity(name, null, control);
			}, this);
			forEach(this.$error, function (value, name) {
				// eslint-disable-next-line no-invalid-this
				this.$setValidity(name, null, control);
			}, this);
			forEach(this.$$success, function (value, name) {
				// eslint-disable-next-line no-invalid-this
				this.$setValidity(name, null, control);
			}, this);

			arrayRemove(this.$$controls, control);
			control.$$parentForm = nullFormCtrl;
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$setDirty
		 *
		 * @description
		 * Sets the form to a dirty state.
		 *
		 * This method can be called to add the 'ng-dirty' class and set the form to a dirty
		 * state (ng-dirty class). This method will also propagate to parent forms.
		 */
		$setDirty: function () {
			this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
			this.$$animate.addClass(this.$$element, DIRTY_CLASS);
			this.$dirty = true;
			this.$pristine = false;
			this.$$parentForm.$setDirty();
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$setPristine
		 *
		 * @description
		 * Sets the form to its pristine state.
		 *
		 * This method sets the form's `$pristine` state to true, the `$dirty` state to false, removes
		 * the `ng-dirty` class and adds the `ng-pristine` class. Additionally, it sets the `$submitted`
		 * state to false.
		 *
		 * This method will also propagate to all the controls contained in this form.
		 *
		 * Setting a form back to a pristine state is often useful when we want to 'reuse' a form after
		 * saving or resetting it.
		 */
		$setPristine: function () {
			this.$$animate.setClass(this.$$element, PRISTINE_CLASS, DIRTY_CLASS + ' ' + SUBMITTED_CLASS);
			this.$dirty = false;
			this.$pristine = true;
			this.$submitted = false;
			forEach(this.$$controls, function (control) {
				control.$setPristine();
			});
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$setUntouched
		 *
		 * @description
		 * Sets the form to its untouched state.
		 *
		 * This method can be called to remove the 'ng-touched' class and set the form controls to their
		 * untouched state (ng-untouched class).
		 *
		 * Setting a form controls back to their untouched state is often useful when setting the form
		 * back to its pristine state.
		 */
		$setUntouched: function () {
			forEach(this.$$controls, function (control) {
				control.$setUntouched();
			});
		},

		/**
		 * @ngdoc method
		 * @name form.FormController#$setSubmitted
		 *
		 * @description
		 * Sets the form to its `$submitted` state. This will also set `$submitted` on all child and
		 * parent forms of the form.
		 */
		$setSubmitted: function () {
			var rootForm = this;
			while (rootForm.$$parentForm && (rootForm.$$parentForm !== nullFormCtrl)) {
				rootForm = rootForm.$$parentForm;
			}
			rootForm.$$setSubmitted();
		},

		$$setSubmitted: function () {
			this.$$animate.addClass(this.$$element, SUBMITTED_CLASS);
			this.$submitted = true;
			forEach(this.$$controls, function (control) {
				if (control.$$setSubmitted) {
					control.$$setSubmitted();
				}
			});
		}
	};

	/**
	 * @ngdoc method
	 * @name form.FormController#$setValidity
	 *
	 * @description
	 * Change the validity state of the form, and notify the parent form (if any).
	 *
	 * Application developers will rarely need to call this method directly. It is used internally, by
	 * {@link ngModel.NgModelController#$setValidity NgModelController.$setValidity()}, to propagate a
	 * control's validity state to the parent `FormController`.
	 *
	 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be
	 *        assigned to either `$error[validationErrorKey]` or `$pending[validationErrorKey]` (for
	 *        unfulfilled `$asyncValidators`), so that it is available for data-binding. The
	 *        `validationErrorKey` should be in camelCase and will get converted into dash-case for
	 *        class name. Example: `myError` will result in `ng-valid-my-error` and
	 *        `ng-invalid-my-error` classes and can be bound to as `{{ someForm.$error.myError }}`.
	 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending
	 *        (undefined),  or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
	 *        Skipped is used by AngularJS when validators do not run because of parse errors and when
	 *        `$asyncValidators` do not run because any of the `$validators` failed.
	 * @param {NgModelController | FormController} controller - The controller whose validity state is
	 *        triggering the change.
	 */
	addSetValidityMethod({
		clazz: FormController,
		set: function (object, property, controller) {
			var list = object[property];
			if (!list) {
				object[property] = [controller];
			} else {
				var index = list.indexOf(controller);
				if (index === -1) {
					list.push(controller);
				}
			}
		},
		unset: function (object, property, controller) {
			var list = object[property];
			if (!list) {
				return;
			}
			arrayRemove(list, controller);
			if (list.length === 0) {
				delete object[property];
			}
		}
	});

	/**
	 * @ngdoc directive
	 * @name ngForm
	 * @restrict EAC
	 *
	 * @description
	 * Helper directive that makes it possible to create control groups inside a
	 * {@link ng.directive:form `form`} directive.
	 * These "child forms" can be used, for example, to determine the validity of a sub-group of
	 * controls.
	 *
	 * <div class="alert alert-danger">
	 * **Note**: `ngForm` cannot be used as a replacement for `<form>`, because it lacks its
	 * [built-in HTML functionality](https://html.spec.whatwg.org/#the-form-element).
	 * Specifically, you cannot submit `ngForm` like a `<form>` tag. That means,
	 * you cannot send data to the server with `ngForm`, or integrate it with
	 * {@link ng.directive:ngSubmit `ngSubmit`}.
	 * </div>
	 *
	 * @param {string=} ngForm|name Name of the form. If specified, the form controller will
	 *                              be published into the related scope, under this name.
	 *
	 */

	/**
	* @ngdoc directive
	* @name form
	* @restrict E
	*
	* @description
	* Directive that instantiates
	* {@link form.FormController FormController}.
	*
	* If the `name` attribute is specified, the form controller is published onto the current scope under
	* this name.
	*
	* ## Alias: {@link ng.directive:ngForm `ngForm`}
	*
	* In AngularJS, forms can be nested. This means that the outer form is valid when all of the child
	* forms are valid as well. However, browsers do not allow nesting of `<form>` elements, so
	* AngularJS provides the {@link ng.directive:ngForm `ngForm`} directive, which behaves identically to
	* `form` but can be nested. Nested forms can be useful, for example, if the validity of a sub-group
	* of controls needs to be determined.
	*
	* ## CSS classes
	*  - `ng-valid` is set if the form is valid.
	*  - `ng-invalid` is set if the form is invalid.
	*  - `ng-pending` is set if the form is pending.
	*  - `ng-pristine` is set if the form is pristine.
	*  - `ng-dirty` is set if the form is dirty.
	*  - `ng-submitted` is set if the form was submitted.
	*
	* Keep in mind that ngAnimate can detect each of these classes when added and removed.
	*
	*
	* ## Submitting a form and preventing the default action
	*
	* Since the role of forms in client-side AngularJS applications is different than in classical
	* roundtrip apps, it is desirable for the browser not to translate the form submission into a full
	* page reload that sends the data to the server. Instead some javascript logic should be triggered
	* to handle the form submission in an application-specific way.
	*
	* For this reason, AngularJS prevents the default action (form submission to the server) unless the
	* `<form>` element has an `action` attribute specified.
	*
	* You can use one of the following two ways to specify what javascript method should be called when
	* a form is submitted:
	*
	* - {@link ng.directive:ngSubmit ngSubmit} directive on the form element
	* - {@link ng.directive:ngClick ngClick} directive on the first
	 *  button or input field of type submit (input[type=submit])
	*
	* To prevent double execution of the handler, use only one of the {@link ng.directive:ngSubmit ngSubmit}
	* or {@link ng.directive:ngClick ngClick} directives.
	* This is because of the following form submission rules in the HTML specification:
	*
	* - If a form has only one input field then hitting enter in this field triggers form submit
	* (`ngSubmit`)
	* - if a form has 2+ input fields and no buttons or input[type=submit] then hitting enter
	* doesn't trigger submit
	* - if a form has one or more input fields and one or more buttons or input[type=submit] then
	* hitting enter in any of the input fields will trigger the click handler on the *first* button or
	* input[type=submit] (`ngClick`) *and* a submit handler on the enclosing form (`ngSubmit`)
	*
	* Any pending `ngModelOptions` changes will take place immediately when an enclosing form is
	* submitted. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
	* to have access to the updated model.
	*
	* @animations
	* Animations in ngForm are triggered when any of the associated CSS classes are added and removed.
	* These classes are: `.ng-pristine`, `.ng-dirty`, `.ng-invalid` and `.ng-valid` as well as any
	* other validations that are performed within the form. Animations in ngForm are similar to how
	* they work in ngClass and animations can be hooked into using CSS transitions, keyframes as well
	* as JS animations.
	*
	* The following example shows a simple way to utilize CSS transitions to style a form element
	* that has been rendered as invalid after it has been validated:
	*
	* <pre>
	* //be sure to include ngAnimate as a module to hook into more
	* //advanced animations
	* .my-form {
	*   transition:0.5s linear all;
	*   background: white;
	* }
	* .my-form.ng-invalid {
	*   background: red;
	*   color:white;
	* }
	* </pre>
	*
	* @example
	   <example name="ng-form" deps="angular-animate.js" animations="true" fixBase="true" module="formExample">
		 <file name="index.html">
		  <script>
			angular.module('formExample', [])
			  .controller('FormController', ['$scope', function($scope) {
				$scope.userType = 'guest';
			  }]);
		  </script>
		  <style>
		   .my-form {
			 transition:all linear 0.5s;
			 background: transparent;
		   }
		   .my-form.ng-invalid {
			 background: red;
		   }
		  </style>
		  <form name="myForm" ng-controller="FormController" class="my-form">
			userType: <input name="input" ng-model="userType" required>
			<span class="error" ng-show="myForm.input.$error.required">Required!</span><br>
			<code>userType = {{userType}}</code><br>
			<code>myForm.input.$valid = {{myForm.input.$valid}}</code><br>
			<code>myForm.input.$error = {{myForm.input.$error}}</code><br>
			<code>myForm.$valid = {{myForm.$valid}}</code><br>
			<code>myForm.$error.required = {{!!myForm.$error.required}}</code><br>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should initialize to model', function() {
			 var userType = element(by.binding('userType'));
			 var valid = element(by.binding('myForm.input.$valid'));
   
			 expect(userType.getText()).toContain('guest');
			 expect(valid.getText()).toContain('true');
		   });
   
		   it('should be invalid if empty', function() {
			 var userType = element(by.binding('userType'));
			 var valid = element(by.binding('myForm.input.$valid'));
			 var userInput = element(by.model('userType'));
   
			 userInput.clear();
			 userInput.sendKeys('');
   
			 expect(userType.getText()).toEqual('userType =');
			 expect(valid.getText()).toContain('false');
		   });
		 </file>
	   </example>
	*
	* @param {string=} name Name of the form. If specified, the form controller will be published into
	*                       related scope, under this name.
	*/
	var formDirectiveFactory = function (isNgForm) {
		return ['$timeout', '$parse', function ($timeout, $parse) {
			var formDirective = {
				name: 'form',
				restrict: isNgForm ? 'EAC' : 'E',
				require: ['form', '^^?form'], //first is the form's own ctrl, second is an optional parent form
				controller: FormController,
				compile: function ngFormCompile(formElement, attr) {
					// Setup initial state of the control
					formElement.addClass(PRISTINE_CLASS).addClass(VALID_CLASS);

					var nameAttr = attr.name ? 'name' : (isNgForm && attr.ngForm ? 'ngForm' : false);

					return {
						pre: function ngFormPreLink(scope, formElement, attr, ctrls) {
							var controller = ctrls[0];

							// if `action` attr is not present on the form, prevent the default action (submission)
							if (!('action' in attr)) {
								// we can't use jq events because if a form is destroyed during submission the default
								// action is not prevented. see #1238
								//
								// IE 9 is not affected because it doesn't fire a submit event and try to do a full
								// page reload if the form was destroyed by submission of the form via a click handler
								// on a button in the form. Looks like an IE9 specific bug.
								var handleFormSubmission = function (event) {
									scope.$apply(function () {
										controller.$commitViewValue();
										controller.$setSubmitted();
									});

									event.preventDefault();
								};

								formElement[0].addEventListener('submit', handleFormSubmission);

								// unregister the preventDefault listener so that we don't not leak memory but in a
								// way that will achieve the prevention of the default action.
								formElement.on('$destroy', function () {
									$timeout(function () {
										formElement[0].removeEventListener('submit', handleFormSubmission);
									}, 0, false);
								});
							}

							var parentFormCtrl = ctrls[1] || controller.$$parentForm;
							parentFormCtrl.$addControl(controller);

							var setter = nameAttr ? getSetter(controller.$name) : noop;

							if (nameAttr) {
								setter(scope, controller);
								attr.$observe(nameAttr, function (newValue) {
									if (controller.$name === newValue) return;
									setter(scope, undefined);
									controller.$$parentForm.$$renameControl(controller, newValue);
									setter = getSetter(controller.$name);
									setter(scope, controller);
								});
							}
							formElement.on('$destroy', function () {
								controller.$$parentForm.$removeControl(controller);
								setter(scope, undefined);
								extend(controller, nullFormCtrl); //stop propagating child destruction handlers upwards
							});
						}
					};
				}
			};

			return formDirective;

			function getSetter(expression) {
				if (expression === '') {
					//create an assignable expression, so forms with an empty name can be renamed later
					return $parse('this[""]').assign;
				}
				return $parse(expression).assign || noop;
			}
		}];
	};

	var formDirective = formDirectiveFactory();
	var ngFormDirective = formDirectiveFactory(true);



	// helper methods
	function setupValidity(instance) {
		instance.$$classCache = {};
		instance.$$classCache[INVALID_CLASS] = !(instance.$$classCache[VALID_CLASS] = instance.$$element.hasClass(VALID_CLASS));
	}
	function addSetValidityMethod(context) {
		var clazz = context.clazz,
			set = context.set,
			unset = context.unset;

		clazz.prototype.$setValidity = function (validationErrorKey, state, controller) {
			if (isUndefined(state)) {
				createAndSet(this, '$pending', validationErrorKey, controller);
			} else {
				unsetAndCleanup(this, '$pending', validationErrorKey, controller);
			}
			if (!isBoolean(state)) {
				unset(this.$error, validationErrorKey, controller);
				unset(this.$$success, validationErrorKey, controller);
			} else {
				if (state) {
					unset(this.$error, validationErrorKey, controller);
					set(this.$$success, validationErrorKey, controller);
				} else {
					set(this.$error, validationErrorKey, controller);
					unset(this.$$success, validationErrorKey, controller);
				}
			}
			if (this.$pending) {
				cachedToggleClass(this, PENDING_CLASS, true);
				this.$valid = this.$invalid = undefined;
				toggleValidationCss(this, '', null);
			} else {
				cachedToggleClass(this, PENDING_CLASS, false);
				this.$valid = isObjectEmpty(this.$error);
				this.$invalid = !this.$valid;
				toggleValidationCss(this, '', this.$valid);
			}

			// re-read the state as the set/unset methods could have
			// combined state in this.$error[validationError] (used for forms),
			// where setting/unsetting only increments/decrements the value,
			// and does not replace it.
			var combinedState;
			if (this.$pending && this.$pending[validationErrorKey]) {
				combinedState = undefined;
			} else if (this.$error[validationErrorKey]) {
				combinedState = false;
			} else if (this.$$success[validationErrorKey]) {
				combinedState = true;
			} else {
				combinedState = null;
			}

			toggleValidationCss(this, validationErrorKey, combinedState);
			this.$$parentForm.$setValidity(validationErrorKey, combinedState, this);
		};

		function createAndSet(ctrl, name, value, controller) {
			if (!ctrl[name]) {
				ctrl[name] = {};
			}
			set(ctrl[name], value, controller);
		}

		function unsetAndCleanup(ctrl, name, value, controller) {
			if (ctrl[name]) {
				unset(ctrl[name], value, controller);
			}
			if (isObjectEmpty(ctrl[name])) {
				ctrl[name] = undefined;
			}
		}

		function cachedToggleClass(ctrl, className, switchValue) {
			if (switchValue && !ctrl.$$classCache[className]) {
				ctrl.$$animate.addClass(ctrl.$$element, className);
				ctrl.$$classCache[className] = true;
			} else if (!switchValue && ctrl.$$classCache[className]) {
				ctrl.$$animate.removeClass(ctrl.$$element, className);
				ctrl.$$classCache[className] = false;
			}
		}

		function toggleValidationCss(ctrl, validationErrorKey, isValid) {
			validationErrorKey = validationErrorKey ? '-' + snake_case(validationErrorKey, '-') : '';

			cachedToggleClass(ctrl, VALID_CLASS + validationErrorKey, isValid === true);
			cachedToggleClass(ctrl, INVALID_CLASS + validationErrorKey, isValid === false);
		}
	}

	function isObjectEmpty(obj) {
		if (obj) {
			for (var prop in obj) {
				if (obj.hasOwnProperty(prop)) {
					return false;
				}
			}
		}
		return true;
	}

	/* global
	  VALID_CLASS: false,
	  INVALID_CLASS: false,
	  PRISTINE_CLASS: false,
	  DIRTY_CLASS: false,
	  ngModelMinErr: false
	*/

	// Regex code was initially obtained from SO prior to modification: https://stackoverflow.com/questions/3143070/javascript-regex-iso-datetime#answer-3143231
	var ISO_DATE_REGEXP = /^\d{4,}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+(?:[+-][0-2]\d:[0-5]\d|Z)$/;
	// See valid URLs in RFC3987 (http://tools.ietf.org/html/rfc3987)
	// Note: We are being more lenient, because browsers are too.
	//   1. Scheme
	//   2. Slashes
	//   3. Username
	//   4. Password
	//   5. Hostname
	//   6. Port
	//   7. Path
	//   8. Query
	//   9. Fragment
	//                 1111111111111111 222   333333    44444        55555555555555555555555     666     77777777     8888888     999
	var URL_REGEXP = /^[a-z][a-z\d.+-]*:\/*(?:[^:@]+(?::[^@]+)?@)?(?:[^\s:/?#]+|\[[a-f\d:]+])(?::\d+)?(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i;
	// eslint-disable-next-line max-len
	var EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
	var NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))([eE][+-]?\d+)?\s*$/;
	var DATE_REGEXP = /^(\d{4,})-(\d{2})-(\d{2})$/;
	var DATETIMELOCAL_REGEXP = /^(\d{4,})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;
	var WEEK_REGEXP = /^(\d{4,})-W(\d\d)$/;
	var MONTH_REGEXP = /^(\d{4,})-(\d\d)$/;
	var TIME_REGEXP = /^(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;

	var PARTIAL_VALIDATION_EVENTS = 'keydown wheel mousedown';
	var PARTIAL_VALIDATION_TYPES = createMap();
	forEach('date,datetime-local,month,time,week'.split(','), function (type) {
		PARTIAL_VALIDATION_TYPES[type] = true;
	});

	var inputType = {

		/**
		 * @ngdoc input
		 * @name input[text]
		 *
		 * @description
		 * Standard HTML text input with AngularJS data binding, inherited by most of the `input` elements.
		 *
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} required Adds `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
		 *    minlength.
		 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
		 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
		 *    any length.
		 * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
		 *    that contains the regular expression body that will be converted to a regular expression
		 *    as in the ngPattern directive.
		 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
		 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
		 *    If the expression evaluates to a RegExp object, then this is used directly.
		 *    If the expression evaluates to a string, then it will be converted to a RegExp
		 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
		 *    `new RegExp('^abc$')`.<br />
		 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
		 *    start at the index of the last search's match, thus not taking the whole input value into
		 *    account.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
		 *    This parameter is ignored for input[type=password] controls, which will never trim the
		 *    input.
		 *
		 * @example
			<example name="text-input-directive" module="textInputExample">
			  <file name="index.html">
			   <script>
				 angular.module('textInputExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.example = {
					   text: 'guest',
					   word: /^\s*\w*\s*$/
					 };
				   }]);
			   </script>
			   <form name="myForm" ng-controller="ExampleController">
				 <label>Single word:
				   <input type="text" name="input" ng-model="example.text"
						  ng-pattern="example.word" required ng-trim="false">
				 </label>
				 <div role="alert">
				   <span class="error" ng-show="myForm.input.$error.required">
					 Required!</span>
				   <span class="error" ng-show="myForm.input.$error.pattern">
					 Single word only!</span>
				 </div>
				 <code>text = {{example.text}}</code><br/>
				 <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br/>
				 <code>myForm.input.$error = {{myForm.input.$error}}</code><br/>
				 <code>myForm.$valid = {{myForm.$valid}}</code><br/>
				 <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br/>
				</form>
			  </file>
			  <file name="protractor.js" type="protractor">
				var text = element(by.binding('example.text'));
				var valid = element(by.binding('myForm.input.$valid'));
				var input = element(by.model('example.text'));
	  
				it('should initialize to model', function() {
				  expect(text.getText()).toContain('guest');
				  expect(valid.getText()).toContain('true');
				});
	  
				it('should be invalid if empty', function() {
				  input.clear();
				  input.sendKeys('');
	  
				  expect(text.getText()).toEqual('text =');
				  expect(valid.getText()).toContain('false');
				});
	  
				it('should be invalid if multi word', function() {
				  input.clear();
				  input.sendKeys('hello world');
	  
				  expect(valid.getText()).toContain('false');
				});
			  </file>
			</example>
		 */
		'text': textInputType,

		/**
		 * @ngdoc input
		 * @name input[date]
		 *
		 * @description
		 * Input with date validation and transformation. In browsers that do not yet support
		 * the HTML5 date input, a text element will be used. In that case, text must be entered in a valid ISO-8601
		 * date format (yyyy-MM-dd), for example: `2009-01-06`. Since many
		 * modern browsers do not yet support this input type, it is important to provide cues to users on the
		 * expected input format via a placeholder or label.
		 *
		 * The model must always be a Date object, otherwise AngularJS will throw an error.
		 * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
		 *
		 * The timezone to be used to read/write the `Date` instance in the model can be defined using
		 * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`. This must be a
		 *   valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
		 *   (e.g. `min="{{minDate | date:'yyyy-MM-dd'}}"`). Note that `min` will also add native HTML5
		 *   constraint validation.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`. This must be
		 *   a valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
		 *   (e.g. `max="{{maxDate | date:'yyyy-MM-dd'}}"`). Note that `max` will also add native HTML5
		 *   constraint validation.
		 * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO date string
		 *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
		 * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO date string
		 *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
		 <example name="date-input-directive" module="dateInputExample">
		 <file name="index.html">
		   <script>
			  angular.module('dateInputExample', [])
				.controller('DateController', ['$scope', function($scope) {
				  $scope.example = {
					value: new Date(2013, 9, 22)
				  };
				}]);
		   </script>
		   <form name="myForm" ng-controller="DateController as dateCtrl">
			  <label for="exampleInput">Pick a date in 2013:</label>
			  <input type="date" id="exampleInput" name="input" ng-model="example.value"
				  placeholder="yyyy-MM-dd" min="2013-01-01" max="2013-12-31" required />
			  <div role="alert">
				<span class="error" ng-show="myForm.input.$error.required">
					Required!</span>
				<span class="error" ng-show="myForm.input.$error.date">
					Not a valid date!</span>
			   </div>
			   <tt>value = {{example.value | date: "yyyy-MM-dd"}}</tt><br/>
			   <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
			   <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
			   <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			   <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
			var value = element(by.binding('example.value | date: "yyyy-MM-dd"'));
			var valid = element(by.binding('myForm.input.$valid'));
	
			// currently protractor/webdriver does not support
			// sending keys to all known HTML5 input controls
			// for various browsers (see https://github.com/angular/protractor/issues/562).
			function setInput(val) {
			  // set the value of the element and force validation.
			  var scr = "var ipt = document.getElementById('exampleInput'); " +
			  "ipt.value = '" + val + "';" +
			  "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
			  browser.executeScript(scr);
			}
	
			it('should initialize to model', function() {
			  expect(value.getText()).toContain('2013-10-22');
			  expect(valid.getText()).toContain('myForm.input.$valid = true');
			});
	
			it('should be invalid if empty', function() {
			  setInput('');
			  expect(value.getText()).toEqual('value =');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
	
			it('should be invalid if over max', function() {
			  setInput('2015-01-01');
			  expect(value.getText()).toContain('');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
		 </file>
		 </example>
		 */
		'date': createDateInputType('date', DATE_REGEXP,
			createDateParser(DATE_REGEXP, ['yyyy', 'MM', 'dd']),
			'yyyy-MM-dd'),

		/**
		 * @ngdoc input
		 * @name input[datetime-local]
		 *
		 * @description
		 * Input with datetime validation and transformation. In browsers that do not yet support
		 * the HTML5 date input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
		 * local datetime format (yyyy-MM-ddTHH:mm:ss), for example: `2010-12-28T14:57:00`.
		 *
		 * The model must always be a Date object, otherwise AngularJS will throw an error.
		 * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
		 *
		 * The timezone to be used to read/write the `Date` instance in the model can be defined using
		 * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
		 *
		 * The format of the displayed time can be adjusted with the
		 * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions} `timeSecondsFormat`
		 * and `timeStripZeroSeconds`.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
		 *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
		 *   inside this attribute (e.g. `min="{{minDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
		 *   Note that `min` will also add native HTML5 constraint validation.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
		 *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
		 *   inside this attribute (e.g. `max="{{maxDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
		 *   Note that `max` will also add native HTML5 constraint validation.
		 * @param {(date|string)=} ngMin Sets the `min` validation error key to the Date / ISO datetime string
		 *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
		 * @param {(date|string)=} ngMax Sets the `max` validation error key to the Date / ISO datetime string
		 *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
		 <example name="datetimelocal-input-directive" module="dateExample">
		 <file name="index.html">
		   <script>
			 angular.module('dateExample', [])
			   .controller('DateController', ['$scope', function($scope) {
				 $scope.example = {
				   value: new Date(2010, 11, 28, 14, 57)
				 };
			   }]);
		   </script>
		   <form name="myForm" ng-controller="DateController as dateCtrl">
			 <label for="exampleInput">Pick a date between in 2013:</label>
			 <input type="datetime-local" id="exampleInput" name="input" ng-model="example.value"
				 placeholder="yyyy-MM-ddTHH:mm:ss" min="2001-01-01T00:00:00" max="2013-12-31T00:00:00" required />
			 <div role="alert">
			   <span class="error" ng-show="myForm.input.$error.required">
				   Required!</span>
			   <span class="error" ng-show="myForm.input.$error.datetimelocal">
				   Not a valid date!</span>
			 </div>
			 <tt>value = {{example.value | date: "yyyy-MM-ddTHH:mm:ss"}}</tt><br/>
			 <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
			 <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
			 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
		   var value = element(by.binding('example.value | date: "yyyy-MM-ddTHH:mm:ss"'));
		   var valid = element(by.binding('myForm.input.$valid'));
	 
		   // currently protractor/webdriver does not support
		   // sending keys to all known HTML5 input controls
		   // for various browsers (https://github.com/angular/protractor/issues/562).
		   function setInput(val) {
			 // set the value of the element and force validation.
			 var scr = "var ipt = document.getElementById('exampleInput'); " +
			 "ipt.value = '" + val + "';" +
			 "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
			 browser.executeScript(scr);
		   }
	 
		   it('should initialize to model', function() {
			 expect(value.getText()).toContain('2010-12-28T14:57:00');
			 expect(valid.getText()).toContain('myForm.input.$valid = true');
		   });
	 
		   it('should be invalid if empty', function() {
			 setInput('');
			 expect(value.getText()).toEqual('value =');
			 expect(valid.getText()).toContain('myForm.input.$valid = false');
		   });
	 
		   it('should be invalid if over max', function() {
			 setInput('2015-01-01T23:59:00');
			 expect(value.getText()).toContain('');
			 expect(valid.getText()).toContain('myForm.input.$valid = false');
		   });
		 </file>
		 </example>
		 */
		'datetime-local': createDateInputType('datetimelocal', DATETIMELOCAL_REGEXP,
			createDateParser(DATETIMELOCAL_REGEXP, ['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'sss']),
			'yyyy-MM-ddTHH:mm:ss.sss'),

		/**
		 * @ngdoc input
		 * @name input[time]
		 *
		 * @description
		 * Input with time validation and transformation. In browsers that do not yet support
		 * the HTML5 time input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
		 * local time format (HH:mm:ss), for example: `14:57:00`. Model must be a Date object. This binding will always output a
		 * Date object to the model of January 1, 1970, or local date `new Date(1970, 0, 1, HH, mm, ss)`.
		 *
		 * The model must always be a Date object, otherwise AngularJS will throw an error.
		 * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
		 *
		 * The timezone to be used to read/write the `Date` instance in the model can be defined using
		 * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions}. By default,
		 * this is the timezone of the browser.
		 *
		 * The format of the displayed time can be adjusted with the
		 * {@link ng.directive:ngModelOptions#ngModelOptions-arguments ngModelOptions} `timeSecondsFormat`
		 * and `timeStripZeroSeconds`.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
		 *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
		 *   attribute (e.g. `min="{{minTime | date:'HH:mm:ss'}}"`). Note that `min` will also add
		 *   native HTML5 constraint validation.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
		 *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
		 *   attribute (e.g. `max="{{maxTime | date:'HH:mm:ss'}}"`). Note that `max` will also add
		 *   native HTML5 constraint validation.
		 * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO time string the
		 *   `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
		 * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO time string the
		 *   `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
		 <example name="time-input-directive" module="timeExample">
		 <file name="index.html">
		   <script>
			angular.module('timeExample', [])
			  .controller('DateController', ['$scope', function($scope) {
				$scope.example = {
				  value: new Date(1970, 0, 1, 14, 57, 0)
				};
			  }]);
		   </script>
		   <form name="myForm" ng-controller="DateController as dateCtrl">
			  <label for="exampleInput">Pick a time between 8am and 5pm:</label>
			  <input type="time" id="exampleInput" name="input" ng-model="example.value"
				  placeholder="HH:mm:ss" min="08:00:00" max="17:00:00" required />
			  <div role="alert">
				<span class="error" ng-show="myForm.input.$error.required">
					Required!</span>
				<span class="error" ng-show="myForm.input.$error.time">
					Not a valid date!</span>
			  </div>
			  <tt>value = {{example.value | date: "HH:mm:ss"}}</tt><br/>
			  <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
			  <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
			  <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			  <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
			var value = element(by.binding('example.value | date: "HH:mm:ss"'));
			var valid = element(by.binding('myForm.input.$valid'));
	  
			// currently protractor/webdriver does not support
			// sending keys to all known HTML5 input controls
			// for various browsers (https://github.com/angular/protractor/issues/562).
			function setInput(val) {
			  // set the value of the element and force validation.
			  var scr = "var ipt = document.getElementById('exampleInput'); " +
			  "ipt.value = '" + val + "';" +
			  "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
			  browser.executeScript(scr);
			}
	  
			it('should initialize to model', function() {
			  expect(value.getText()).toContain('14:57:00');
			  expect(valid.getText()).toContain('myForm.input.$valid = true');
			});
	  
			it('should be invalid if empty', function() {
			  setInput('');
			  expect(value.getText()).toEqual('value =');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
	  
			it('should be invalid if over max', function() {
			  setInput('23:59:00');
			  expect(value.getText()).toContain('');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
		 </file>
		 </example>
		 */
		'time': createDateInputType('time', TIME_REGEXP,
			createDateParser(TIME_REGEXP, ['HH', 'mm', 'ss', 'sss']),
			'HH:mm:ss.sss'),

		/**
		 * @ngdoc input
		 * @name input[week]
		 *
		 * @description
		 * Input with week-of-the-year validation and transformation to Date. In browsers that do not yet support
		 * the HTML5 week input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
		 * week format (yyyy-W##), for example: `2013-W02`.
		 *
		 * The model must always be a Date object, otherwise AngularJS will throw an error.
		 * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
		 *
		 * The value of the resulting Date object will be set to Thursday at 00:00:00 of the requested week,
		 * due to ISO-8601 week numbering standards. Information on ISO's system for numbering the weeks of the
		 * year can be found at: https://en.wikipedia.org/wiki/ISO_8601#Week_dates
		 *
		 * The timezone to be used to read/write the `Date` instance in the model can be defined using
		 * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
		 *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
		 *   attribute (e.g. `min="{{minWeek | date:'yyyy-Www'}}"`). Note that `min` will also add
		 *   native HTML5 constraint validation.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
		 *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
		 *   attribute (e.g. `max="{{maxWeek | date:'yyyy-Www'}}"`). Note that `max` will also add
		 *   native HTML5 constraint validation.
		 * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
		 *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
		 * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
		 *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
		 <example name="week-input-directive" module="weekExample">
		 <file name="index.html">
		   <script>
		   angular.module('weekExample', [])
			 .controller('DateController', ['$scope', function($scope) {
			   $scope.example = {
				 value: new Date(2013, 0, 3)
			   };
			 }]);
		   </script>
		   <form name="myForm" ng-controller="DateController as dateCtrl">
			 <label>Pick a date between in 2013:
			   <input id="exampleInput" type="week" name="input" ng-model="example.value"
					  placeholder="YYYY-W##" min="2012-W32"
					  max="2013-W52" required />
			 </label>
			 <div role="alert">
			   <span class="error" ng-show="myForm.input.$error.required">
				   Required!</span>
			   <span class="error" ng-show="myForm.input.$error.week">
				   Not a valid date!</span>
			 </div>
			 <tt>value = {{example.value | date: "yyyy-Www"}}</tt><br/>
			 <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
			 <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
			 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
		   var value = element(by.binding('example.value | date: "yyyy-Www"'));
		   var valid = element(by.binding('myForm.input.$valid'));
	 
		   // currently protractor/webdriver does not support
		   // sending keys to all known HTML5 input controls
		   // for various browsers (https://github.com/angular/protractor/issues/562).
		   function setInput(val) {
			 // set the value of the element and force validation.
			 var scr = "var ipt = document.getElementById('exampleInput'); " +
			 "ipt.value = '" + val + "';" +
			 "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
			 browser.executeScript(scr);
		   }
	 
		   it('should initialize to model', function() {
			 expect(value.getText()).toContain('2013-W01');
			 expect(valid.getText()).toContain('myForm.input.$valid = true');
		   });
	 
		   it('should be invalid if empty', function() {
			 setInput('');
			 expect(value.getText()).toEqual('value =');
			 expect(valid.getText()).toContain('myForm.input.$valid = false');
		   });
	 
		   it('should be invalid if over max', function() {
			 setInput('2015-W01');
			 expect(value.getText()).toContain('');
			 expect(valid.getText()).toContain('myForm.input.$valid = false');
		   });
		 </file>
		 </example>
		 */
		'week': createDateInputType('week', WEEK_REGEXP, weekParser, 'yyyy-Www'),

		/**
		 * @ngdoc input
		 * @name input[month]
		 *
		 * @description
		 * Input with month validation and transformation. In browsers that do not yet support
		 * the HTML5 month input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
		 * month format (yyyy-MM), for example: `2009-01`.
		 *
		 * The model must always be a Date object, otherwise AngularJS will throw an error.
		 * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
		 * If the model is not set to the first of the month, the next view to model update will set it
		 * to the first of the month.
		 *
		 * The timezone to be used to read/write the `Date` instance in the model can be defined using
		 * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
		 *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
		 *   attribute (e.g. `min="{{minMonth | date:'yyyy-MM'}}"`). Note that `min` will also add
		 *   native HTML5 constraint validation.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
		 *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
		 *   attribute (e.g. `max="{{maxMonth | date:'yyyy-MM'}}"`). Note that `max` will also add
		 *   native HTML5 constraint validation.
		 * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
		 *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
		 * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
		 *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
	  
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
		 <example name="month-input-directive" module="monthExample">
		 <file name="index.html">
		   <script>
			angular.module('monthExample', [])
			  .controller('DateController', ['$scope', function($scope) {
				$scope.example = {
				  value: new Date(2013, 9, 1)
				};
			  }]);
		   </script>
		   <form name="myForm" ng-controller="DateController as dateCtrl">
			 <label for="exampleInput">Pick a month in 2013:</label>
			 <input id="exampleInput" type="month" name="input" ng-model="example.value"
				placeholder="yyyy-MM" min="2013-01" max="2013-12" required />
			 <div role="alert">
			   <span class="error" ng-show="myForm.input.$error.required">
				  Required!</span>
			   <span class="error" ng-show="myForm.input.$error.month">
				  Not a valid month!</span>
			 </div>
			 <tt>value = {{example.value | date: "yyyy-MM"}}</tt><br/>
			 <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
			 <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
			 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
		   </form>
		 </file>
		 <file name="protractor.js" type="protractor">
			var value = element(by.binding('example.value | date: "yyyy-MM"'));
			var valid = element(by.binding('myForm.input.$valid'));
	  
			// currently protractor/webdriver does not support
			// sending keys to all known HTML5 input controls
			// for various browsers (https://github.com/angular/protractor/issues/562).
			function setInput(val) {
			  // set the value of the element and force validation.
			  var scr = "var ipt = document.getElementById('exampleInput'); " +
			  "ipt.value = '" + val + "';" +
			  "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
			  browser.executeScript(scr);
			}
	  
			it('should initialize to model', function() {
			  expect(value.getText()).toContain('2013-10');
			  expect(valid.getText()).toContain('myForm.input.$valid = true');
			});
	  
			it('should be invalid if empty', function() {
			  setInput('');
			  expect(value.getText()).toEqual('value =');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
	  
			it('should be invalid if over max', function() {
			  setInput('2015-01');
			  expect(value.getText()).toContain('');
			  expect(valid.getText()).toContain('myForm.input.$valid = false');
			});
		 </file>
		 </example>
		 */
		'month': createDateInputType('month', MONTH_REGEXP,
			createDateParser(MONTH_REGEXP, ['yyyy', 'MM']),
			'yyyy-MM'),

		/**
		 * @ngdoc input
		 * @name input[number]
		 *
		 * @description
		 * Text input with number validation and transformation. Sets the `number` validation
		 * error if not a valid number.
		 *
		 * <div class="alert alert-warning">
		 * The model must always be of type `number` otherwise AngularJS will throw an error.
		 * Be aware that a string containing a number is not enough. See the {@link ngModel:numfmt}
		 * error docs for more information and an example of how to convert your model if necessary.
		 * </div>
		 *
		 *
		 *
		 * @knownIssue
		 *
		 * ### HTML5 constraint validation and `allowInvalid`
		 *
		 * In browsers that follow the
		 * [HTML5 specification](https://html.spec.whatwg.org/multipage/forms.html#number-state-%28type=number%29),
		 * `input[number]` does not work as expected with {@link ngModelOptions `ngModelOptions.allowInvalid`}.
		 * If a non-number is entered in the input, the browser will report the value as an empty string,
		 * which means the view / model values in `ngModel` and subsequently the scope value
		 * will also be an empty string.
		 *
		 * @knownIssue
		 *
		 * ### Large numbers and `step` validation
		 *
		 * The `step` validation will not work correctly for very large numbers (e.g. 9999999999) due to
		 * Javascript's arithmetic limitations. If you need to handle large numbers, purpose-built
		 * libraries (e.g. https://github.com/MikeMcl/big.js/), can be included into AngularJS by
		 * {@link guide/forms#modifying-built-in-validators overwriting the validators}
		 * for `number` and / or `step`, or by {@link guide/forms#custom-validation applying custom validators}
		 * to an `input[text]` element. The source for `input[number]` type can be used as a starting
		 * point for both implementations.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
		 *    Can be interpolated.
		 * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
		 *    Can be interpolated.
		 * @param {string=} ngMin Like `min`, sets the `min` validation error key if the value entered is less than `ngMin`,
		 *    but does not trigger HTML5 native validation. Takes an expression.
		 * @param {string=} ngMax Like `max`, sets the `max` validation error key if the value entered is greater than `ngMax`,
		 *    but does not trigger HTML5 native validation. Takes an expression.
		 * @param {string=} step Sets the `step` validation error key if the value entered does not fit the `step` constraint.
		 *    Can be interpolated.
		 * @param {string=} ngStep Like `step`, sets the `step` validation error key if the value entered does not fit the `ngStep` constraint,
		 *    but does not trigger HTML5 native validation. Takes an expression.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
		 *    minlength.
		 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
		 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
		 *    any length.
		 * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
		 *    that contains the regular expression body that will be converted to a regular expression
		 *    as in the ngPattern directive.
		 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
		 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
		 *    If the expression evaluates to a RegExp object, then this is used directly.
		 *    If the expression evaluates to a string, then it will be converted to a RegExp
		 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
		 *    `new RegExp('^abc$')`.<br />
		 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
		 *    start at the index of the last search's match, thus not taking the whole input value into
		 *    account.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
			<example name="number-input-directive" module="numberExample">
			  <file name="index.html">
			   <script>
				 angular.module('numberExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.example = {
					   value: 12
					 };
				   }]);
			   </script>
			   <form name="myForm" ng-controller="ExampleController">
				 <label>Number:
				   <input type="number" name="input" ng-model="example.value"
						  min="0" max="99" required>
				</label>
				 <div role="alert">
				   <span class="error" ng-show="myForm.input.$error.required">
					 Required!</span>
				   <span class="error" ng-show="myForm.input.$error.number">
					 Not valid number!</span>
				 </div>
				 <tt>value = {{example.value}}</tt><br/>
				 <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
				 <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
				 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
				 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
				</form>
			  </file>
			  <file name="protractor.js" type="protractor">
				var value = element(by.binding('example.value'));
				var valid = element(by.binding('myForm.input.$valid'));
				var input = element(by.model('example.value'));
	  
				it('should initialize to model', function() {
				  expect(value.getText()).toContain('12');
				  expect(valid.getText()).toContain('true');
				});
	  
				it('should be invalid if empty', function() {
				  input.clear();
				  input.sendKeys('');
				  expect(value.getText()).toEqual('value =');
				  expect(valid.getText()).toContain('false');
				});
	  
				it('should be invalid if over max', function() {
				  input.clear();
				  input.sendKeys('123');
				  expect(value.getText()).toEqual('value =');
				  expect(valid.getText()).toContain('false');
				});
			  </file>
			</example>
		 */
		'number': numberInputType,


		/**
		 * @ngdoc input
		 * @name input[url]
		 *
		 * @description
		 * Text input with URL validation. Sets the `url` validation error key if the content is not a
		 * valid URL.
		 *
		 * <div class="alert alert-warning">
		 * **Note:** `input[url]` uses a regex to validate urls that is derived from the regex
		 * used in Chromium. If you need stricter validation, you can use `ng-pattern` or modify
		 * the built-in validators (see the {@link guide/forms Forms guide})
		 * </div>
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
		 *    minlength.
		 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
		 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
		 *    any length.
		 * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
		 *    that contains the regular expression body that will be converted to a regular expression
		 *    as in the ngPattern directive.
		 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
		 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
		 *    If the expression evaluates to a RegExp object, then this is used directly.
		 *    If the expression evaluates to a string, then it will be converted to a RegExp
		 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
		 *    `new RegExp('^abc$')`.<br />
		 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
		 *    start at the index of the last search's match, thus not taking the whole input value into
		 *    account.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
			<example name="url-input-directive" module="urlExample">
			  <file name="index.html">
			   <script>
				 angular.module('urlExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.url = {
					   text: 'http://google.com'
					 };
				   }]);
			   </script>
			   <form name="myForm" ng-controller="ExampleController">
				 <label>URL:
				   <input type="url" name="input" ng-model="url.text" required>
				 <label>
				 <div role="alert">
				   <span class="error" ng-show="myForm.input.$error.required">
					 Required!</span>
				   <span class="error" ng-show="myForm.input.$error.url">
					 Not valid url!</span>
				 </div>
				 <tt>text = {{url.text}}</tt><br/>
				 <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
				 <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
				 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
				 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
				 <tt>myForm.$error.url = {{!!myForm.$error.url}}</tt><br/>
				</form>
			  </file>
			  <file name="protractor.js" type="protractor">
				var text = element(by.binding('url.text'));
				var valid = element(by.binding('myForm.input.$valid'));
				var input = element(by.model('url.text'));
	  
				it('should initialize to model', function() {
				  expect(text.getText()).toContain('http://google.com');
				  expect(valid.getText()).toContain('true');
				});
	  
				it('should be invalid if empty', function() {
				  input.clear();
				  input.sendKeys('');
	  
				  expect(text.getText()).toEqual('text =');
				  expect(valid.getText()).toContain('false');
				});
	  
				it('should be invalid if not url', function() {
				  input.clear();
				  input.sendKeys('box');
	  
				  expect(valid.getText()).toContain('false');
				});
			  </file>
			</example>
		 */
		'url': urlInputType,


		/**
		 * @ngdoc input
		 * @name input[email]
		 *
		 * @description
		 * Text input with email validation. Sets the `email` validation error key if not a valid email
		 * address.
		 *
		 * <div class="alert alert-warning">
		 * **Note:** `input[email]` uses a regex to validate email addresses that is derived from the regex
		 * used in Chromium, which may not fulfill your app's requirements.
		 * If you need stricter (e.g. requiring a top-level domain), or more relaxed validation
		 * (e.g. allowing IPv6 address literals) you can use `ng-pattern` or
		 * modify the built-in validators (see the {@link guide/forms Forms guide}).
		 * </div>
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} required Sets `required` validation error key if the value is not entered.
		 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
		 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
		 *    `required` when you want to data-bind to the `required` attribute.
		 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
		 *    minlength.
		 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
		 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
		 *    any length.
		 * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
		 *    that contains the regular expression body that will be converted to a regular expression
		 *    as in the ngPattern directive.
		 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
		 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
		 *    If the expression evaluates to a RegExp object, then this is used directly.
		 *    If the expression evaluates to a string, then it will be converted to a RegExp
		 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
		 *    `new RegExp('^abc$')`.<br />
		 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
		 *    start at the index of the last search's match, thus not taking the whole input value into
		 *    account.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
			<example name="email-input-directive" module="emailExample">
			  <file name="index.html">
			   <script>
				 angular.module('emailExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.email = {
					   text: 'me@example.com'
					 };
				   }]);
			   </script>
				 <form name="myForm" ng-controller="ExampleController">
				   <label>Email:
					 <input type="email" name="input" ng-model="email.text" required>
				   </label>
				   <div role="alert">
					 <span class="error" ng-show="myForm.input.$error.required">
					   Required!</span>
					 <span class="error" ng-show="myForm.input.$error.email">
					   Not valid email!</span>
				   </div>
				   <tt>text = {{email.text}}</tt><br/>
				   <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
				   <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
				   <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
				   <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
				   <tt>myForm.$error.email = {{!!myForm.$error.email}}</tt><br/>
				 </form>
			   </file>
			  <file name="protractor.js" type="protractor">
				var text = element(by.binding('email.text'));
				var valid = element(by.binding('myForm.input.$valid'));
				var input = element(by.model('email.text'));
	  
				it('should initialize to model', function() {
				  expect(text.getText()).toContain('me@example.com');
				  expect(valid.getText()).toContain('true');
				});
	  
				it('should be invalid if empty', function() {
				  input.clear();
				  input.sendKeys('');
				  expect(text.getText()).toEqual('text =');
				  expect(valid.getText()).toContain('false');
				});
	  
				it('should be invalid if not email', function() {
				  input.clear();
				  input.sendKeys('xxx');
	  
				  expect(valid.getText()).toContain('false');
				});
			  </file>
			</example>
		 */
		'email': emailInputType,


		/**
		 * @ngdoc input
		 * @name input[radio]
		 *
		 * @description
		 * HTML radio button.
		 *
		 * **Note:**<br>
		 * All inputs controlled by {@link ngModel ngModel} (including those of type `radio`) will use the
		 * value of their `name` attribute to determine the property under which their
		 * {@link ngModel.NgModelController NgModelController} will be published on the parent
		 * {@link form.FormController FormController}. Thus, if you use the same `name` for multiple
		 * inputs of a form (e.g. a group of radio inputs), only _one_ `NgModelController` will be
		 * published on the parent `FormController` under that name. The rest of the controllers will
		 * continue to work as expected, but you won't be able to access them as properties on the parent
		 * `FormController`.
		 *
		 * <div class="alert alert-info">
		 *   <p>
		 *     In plain HTML forms, the `name` attribute is used to identify groups of radio inputs, so
		 *     that the browser can manage their state (checked/unchecked) based on the state of other
		 *     inputs in the same group.
		 *   </p>
		 *   <p>
		 *     In AngularJS forms, this is not necessary. The input's state will be updated based on the
		 *     value of the underlying model data.
		 *   </p>
		 * </div>
		 *
		 * <div class="alert alert-success">
		 *   If you omit the `name` attribute on a radio input, `ngModel` will automatically assign it a
		 *   unique name.
		 * </div>
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string} value The value to which the `ngModel` expression should be set when selected.
		 *    Note that `value` only supports `string` values, i.e. the scope model needs to be a string,
		 *    too. Use `ngValue` if you need complex models (`number`, `object`, ...).
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 * @param {string} ngValue AngularJS expression to which `ngModel` will be be set when the radio
		 *    is selected. Should be used instead of the `value` attribute if you need
		 *    a non-string `ngModel` (`boolean`, `array`, ...).
		 *
		 * @example
			<example name="radio-input-directive" module="radioExample">
			  <file name="index.html">
			   <script>
				 angular.module('radioExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.color = {
					   name: 'blue'
					 };
					 $scope.specialValue = {
					   "id": "12345",
					   "value": "green"
					 };
				   }]);
			   </script>
			   <form name="myForm" ng-controller="ExampleController">
				 <label>
				   <input type="radio" ng-model="color.name" value="red">
				   Red
				 </label><br/>
				 <label>
				   <input type="radio" ng-model="color.name" ng-value="specialValue">
				   Green
				 </label><br/>
				 <label>
				   <input type="radio" ng-model="color.name" value="blue">
				   Blue
				 </label><br/>
				 <tt>color = {{color.name | json}}</tt><br/>
				</form>
				Note that `ng-value="specialValue"` sets radio item's value to be the value of `$scope.specialValue`.
			  </file>
			  <file name="protractor.js" type="protractor">
				it('should change state', function() {
				  var inputs = element.all(by.model('color.name'));
				  var color = element(by.binding('color.name'));
	  
				  expect(color.getText()).toContain('blue');
	  
				  inputs.get(0).click();
				  expect(color.getText()).toContain('red');
	  
				  inputs.get(1).click();
				  expect(color.getText()).toContain('green');
				});
			  </file>
			</example>
		 */
		'radio': radioInputType,

		/**
		 * @ngdoc input
		 * @name input[range]
		 *
		 * @description
		 * Native range input with validation and transformation.
		 *
		 * The model for the range input must always be a `Number`.
		 *
		 * IE9 and other browsers that do not support the `range` type fall back
		 * to a text input without any default values for `min`, `max` and `step`. Model binding,
		 * validation and number parsing are nevertheless supported.
		 *
		 * Browsers that support range (latest Chrome, Safari, Firefox, Edge) treat `input[range]`
		 * in a way that never allows the input to hold an invalid value. That means:
		 * - any non-numerical value is set to `(max + min) / 2`.
		 * - any numerical value that is less than the current min val, or greater than the current max val
		 * is set to the min / max val respectively.
		 * - additionally, the current `step` is respected, so the nearest value that satisfies a step
		 * is used.
		 *
		 * See the [HTML Spec on input[type=range]](https://www.w3.org/TR/html5/forms.html#range-state-(type=range))
		 * for more info.
		 *
		 * This has the following consequences for AngularJS:
		 *
		 * Since the element value should always reflect the current model value, a range input
		 * will set the bound ngModel expression to the value that the browser has set for the
		 * input element. For example, in the following input `<input type="range" ng-model="model.value">`,
		 * if the application sets `model.value = null`, the browser will set the input to `'50'`.
		 * AngularJS will then set the model to `50`, to prevent input and model value being out of sync.
		 *
		 * That means the model for range will immediately be set to `50` after `ngModel` has been
		 * initialized. It also means a range input can never have the required error.
		 *
		 * This does not only affect changes to the model value, but also to the values of the `min`,
		 * `max`, and `step` attributes. When these change in a way that will cause the browser to modify
		 * the input value, AngularJS will also update the model value.
		 *
		 * Automatic value adjustment also means that a range input element can never have the `required`,
		 * `min`, or `max` errors.
		 *
		 * However, `step` is currently only fully implemented by Firefox. Other browsers have problems
		 * when the step value changes dynamically - they do not adjust the element value correctly, but
		 * instead may set the `stepMismatch` error. If that's the case, the AngularJS will set the `step`
		 * error on the input, and set the model to `undefined`.
		 *
		 * Note that `input[range]` is not compatible with`ngMax`, `ngMin`, and `ngStep`, because they do
		 * not set the `min` and `max` attributes, which means that the browser won't automatically adjust
		 * the input value based on their values, and will always assume min = 0, max = 100, and step = 1.
		 *
		 * @param {string}  ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {string=} min Sets the `min` validation to ensure that the value entered is greater
		 *                  than `min`. Can be interpolated.
		 * @param {string=} max Sets the `max` validation to ensure that the value entered is less than `max`.
		 *                  Can be interpolated.
		 * @param {string=} step Sets the `step` validation to ensure that the value entered matches the `step`
		 *                  Can be interpolated.
		 * @param {expression=} ngChange AngularJS expression to be executed when the ngModel value changes due
		 *                      to user interaction with the input element.
		 * @param {expression=} ngChecked If the expression is truthy, then the `checked` attribute will be set on the
		 *                      element. **Note** : `ngChecked` should not be used alongside `ngModel`.
		 *                      Checkout {@link ng.directive:ngChecked ngChecked} for usage.
		 *
		 * @example
			<example name="range-input-directive" module="rangeExample">
			  <file name="index.html">
				<script>
				  angular.module('rangeExample', [])
					.controller('ExampleController', ['$scope', function($scope) {
					  $scope.value = 75;
					  $scope.min = 10;
					  $scope.max = 90;
					}]);
				</script>
				<form name="myForm" ng-controller="ExampleController">
	  
				  Model as range: <input type="range" name="range" ng-model="value" min="{{min}}"  max="{{max}}">
				  <hr>
				  Model as number: <input type="number" ng-model="value"><br>
				  Min: <input type="number" ng-model="min"><br>
				  Max: <input type="number" ng-model="max"><br>
				  value = <code>{{value}}</code><br/>
				  myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
				  myForm.range.$error = <code>{{myForm.range.$error}}</code>
				</form>
			  </file>
			</example>
	  
		 * ## Range Input with ngMin & ngMax attributes
	  
		 * @example
			<example name="range-input-directive-ng" module="rangeExample">
			  <file name="index.html">
				<script>
				  angular.module('rangeExample', [])
					.controller('ExampleController', ['$scope', function($scope) {
					  $scope.value = 75;
					  $scope.min = 10;
					  $scope.max = 90;
					}]);
				</script>
				<form name="myForm" ng-controller="ExampleController">
				  Model as range: <input type="range" name="range" ng-model="value" ng-min="min" ng-max="max">
				  <hr>
				  Model as number: <input type="number" ng-model="value"><br>
				  Min: <input type="number" ng-model="min"><br>
				  Max: <input type="number" ng-model="max"><br>
				  value = <code>{{value}}</code><br/>
				  myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
				  myForm.range.$error = <code>{{myForm.range.$error}}</code>
				</form>
			  </file>
			</example>
	  
		 */
		'range': rangeInputType,

		/**
		 * @ngdoc input
		 * @name input[checkbox]
		 *
		 * @description
		 * HTML checkbox.
		 *
		 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
		 * @param {string=} name Property name of the form under which the control is published.
		 * @param {expression=} ngTrueValue The value to which the expression should be set when selected.
		 * @param {expression=} ngFalseValue The value to which the expression should be set when not selected.
		 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
		 *    interaction with the input element.
		 *
		 * @example
			<example name="checkbox-input-directive" module="checkboxExample">
			  <file name="index.html">
			   <script>
				 angular.module('checkboxExample', [])
				   .controller('ExampleController', ['$scope', function($scope) {
					 $scope.checkboxModel = {
					  value1 : true,
					  value2 : 'YES'
					};
				   }]);
			   </script>
			   <form name="myForm" ng-controller="ExampleController">
				 <label>Value1:
				   <input type="checkbox" ng-model="checkboxModel.value1">
				 </label><br/>
				 <label>Value2:
				   <input type="checkbox" ng-model="checkboxModel.value2"
						  ng-true-value="'YES'" ng-false-value="'NO'">
				  </label><br/>
				 <tt>value1 = {{checkboxModel.value1}}</tt><br/>
				 <tt>value2 = {{checkboxModel.value2}}</tt><br/>
				</form>
			  </file>
			  <file name="protractor.js" type="protractor">
				it('should change state', function() {
				  var value1 = element(by.binding('checkboxModel.value1'));
				  var value2 = element(by.binding('checkboxModel.value2'));
	  
				  expect(value1.getText()).toContain('true');
				  expect(value2.getText()).toContain('YES');
	  
				  element(by.model('checkboxModel.value1')).click();
				  element(by.model('checkboxModel.value2')).click();
	  
				  expect(value1.getText()).toContain('false');
				  expect(value2.getText()).toContain('NO');
				});
			  </file>
			</example>
		 */
		'checkbox': checkboxInputType,

		'hidden': noop,
		'button': noop,
		'submit': noop,
		'reset': noop,
		'file': noop
	};

	function stringBasedInputType(ctrl) {
		ctrl.$formatters.push(function (value) {
			return ctrl.$isEmpty(value) ? value : value.toString();
		});
	}

	function textInputType(scope, element, attr, ctrl, $sniffer, $browser) {
		baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
		stringBasedInputType(ctrl);
	}

	function baseInputType(scope, element, attr, ctrl, $sniffer, $browser) {
		var type = lowercase(element[0].type);

		// In composition mode, users are still inputting intermediate text buffer,
		// hold the listener until composition is done.
		// More about composition events: https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
		if (!$sniffer.android) {
			var composing = false;

			element.on('compositionstart', function () {
				composing = true;
			});

			// Support: IE9+
			element.on('compositionupdate', function (ev) {
				// End composition when ev.data is empty string on 'compositionupdate' event.
				// When the input de-focusses (e.g. by clicking away), IE triggers 'compositionupdate'
				// instead of 'compositionend'.
				if (isUndefined(ev.data) || ev.data === '') {
					composing = false;
				}
			});

			element.on('compositionend', function () {
				composing = false;
				listener();
			});
		}

		var timeout;

		var listener = function (ev) {
			if (timeout) {
				$browser.defer.cancel(timeout);
				timeout = null;
			}
			if (composing) return;
			var value = element.val(),
				event = ev && ev.type;

			// By default we will trim the value
			// If the attribute ng-trim exists we will avoid trimming
			// If input type is 'password', the value is never trimmed
			if (type !== 'password' && (!attr.ngTrim || attr.ngTrim !== 'false')) {
				value = trim(value);
			}

			// If a control is suffering from bad input (due to native validators), browsers discard its
			// value, so it may be necessary to revalidate (by calling $setViewValue again) even if the
			// control's value is the same empty value twice in a row.
			if (ctrl.$viewValue !== value || (value === '' && ctrl.$$hasNativeValidators)) {
				ctrl.$setViewValue(value, event);
			}
		};

		// if the browser does support "input" event, we are fine - except on IE9 which doesn't fire the
		// input event on backspace, delete or cut
		if ($sniffer.hasEvent('input')) {
			element.on('input', listener);
		} else {
			var deferListener = function (ev, input, origValue) {
				if (!timeout) {
					timeout = $browser.defer(function () {
						timeout = null;
						if (!input || input.value !== origValue) {
							listener(ev);
						}
					});
				}
			};

			element.on('keydown', /** @this */ function (event) {
				var key = event.keyCode;

				// ignore
				//    command            modifiers                   arrows
				if (key === 91 || (15 < key && key < 19) || (37 <= key && key <= 40)) return;

				deferListener(event, this, this.value);
			});

			// if user modifies input value using context menu in IE, we need "paste", "cut" and "drop" events to catch it
			if ($sniffer.hasEvent('paste')) {
				element.on('paste cut drop', deferListener);
			}
		}

		// if user paste into input using mouse on older browser
		// or form autocomplete on newer browser, we need "change" event to catch it
		element.on('change', listener);

		// Some native input types (date-family) have the ability to change validity without
		// firing any input/change events.
		// For these event types, when native validators are present and the browser supports the type,
		// check for validity changes on various DOM events.
		if (PARTIAL_VALIDATION_TYPES[type] && ctrl.$$hasNativeValidators && type === attr.type) {
			element.on(PARTIAL_VALIDATION_EVENTS, /** @this */ function (ev) {
				if (!timeout) {
					var validity = this[VALIDITY_STATE_PROPERTY];
					var origBadInput = validity.badInput;
					var origTypeMismatch = validity.typeMismatch;
					timeout = $browser.defer(function () {
						timeout = null;
						if (validity.badInput !== origBadInput || validity.typeMismatch !== origTypeMismatch) {
							listener(ev);
						}
					});
				}
			});
		}

		ctrl.$render = function () {
			// Workaround for Firefox validation #12102.
			var value = ctrl.$isEmpty(ctrl.$viewValue) ? '' : ctrl.$viewValue;
			if (element.val() !== value) {
				element.val(value);
			}
		};
	}

	function weekParser(isoWeek, existingDate) {
		if (isDate(isoWeek)) {
			return isoWeek;
		}

		if (isString(isoWeek)) {
			WEEK_REGEXP.lastIndex = 0;
			var parts = WEEK_REGEXP.exec(isoWeek);
			if (parts) {
				var year = +parts[1],
					week = +parts[2],
					hours = 0,
					minutes = 0,
					seconds = 0,
					milliseconds = 0,
					firstThurs = getFirstThursdayOfYear(year),
					addDays = (week - 1) * 7;

				if (existingDate) {
					hours = existingDate.getHours();
					minutes = existingDate.getMinutes();
					seconds = existingDate.getSeconds();
					milliseconds = existingDate.getMilliseconds();
				}

				return new Date(year, 0, firstThurs.getDate() + addDays, hours, minutes, seconds, milliseconds);
			}
		}

		return NaN;
	}

	function createDateParser(regexp, mapping) {
		return function (iso, previousDate) {
			var parts, map;

			if (isDate(iso)) {
				return iso;
			}

			if (isString(iso)) {
				// When a date is JSON'ified to wraps itself inside of an extra
				// set of double quotes. This makes the date parsing code unable
				// to match the date string and parse it as a date.
				if (iso.charAt(0) === '"' && iso.charAt(iso.length - 1) === '"') {
					iso = iso.substring(1, iso.length - 1);
				}
				if (ISO_DATE_REGEXP.test(iso)) {
					return new Date(iso);
				}
				regexp.lastIndex = 0;
				parts = regexp.exec(iso);

				if (parts) {
					parts.shift();
					if (previousDate) {
						map = {
							yyyy: previousDate.getFullYear(),
							MM: previousDate.getMonth() + 1,
							dd: previousDate.getDate(),
							HH: previousDate.getHours(),
							mm: previousDate.getMinutes(),
							ss: previousDate.getSeconds(),
							sss: previousDate.getMilliseconds() / 1000
						};
					} else {
						map = { yyyy: 1970, MM: 1, dd: 1, HH: 0, mm: 0, ss: 0, sss: 0 };
					}

					forEach(parts, function (part, index) {
						if (index < mapping.length) {
							map[mapping[index]] = +part;
						}
					});

					var date = new Date(map.yyyy, map.MM - 1, map.dd, map.HH, map.mm, map.ss || 0, map.sss * 1000 || 0);
					if (map.yyyy < 100) {
						// In the constructor, 2-digit years map to 1900-1999.
						// Use `setFullYear()` to set the correct year.
						date.setFullYear(map.yyyy);
					}

					return date;
				}
			}

			return NaN;
		};
	}

	function createDateInputType(type, regexp, parseDate, format) {
		return function dynamicDateInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
			badInputChecker(scope, element, attr, ctrl, type);
			baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

			var isTimeType = type === 'time' || type === 'datetimelocal';
			var previousDate;
			var previousTimezone;

			ctrl.$parsers.push(function (value) {
				if (ctrl.$isEmpty(value)) return null;

				if (regexp.test(value)) {
					// Note: We cannot read ctrl.$modelValue, as there might be a different
					// parser/formatter in the processing chain so that the model
					// contains some different data format!
					return parseDateAndConvertTimeZoneToLocal(value, previousDate);
				}
				ctrl.$$parserName = type;
				return undefined;
			});

			ctrl.$formatters.push(function (value) {
				if (value && !isDate(value)) {
					throw ngModelMinErr('datefmt', 'Expected `{0}` to be a date', value);
				}
				if (isValidDate(value)) {
					previousDate = value;
					var timezone = ctrl.$options.getOption('timezone');

					if (timezone) {
						previousTimezone = timezone;
						previousDate = convertTimezoneToLocal(previousDate, timezone, true);
					}

					return formatter(value, timezone);
				} else {
					previousDate = null;
					previousTimezone = null;
					return '';
				}
			});

			if (isDefined(attr.min) || attr.ngMin) {
				var minVal = attr.min || $parse(attr.ngMin)(scope);
				var parsedMinVal = parseObservedDateValue(minVal);

				ctrl.$validators.min = function (value) {
					return !isValidDate(value) || isUndefined(parsedMinVal) || parseDate(value) >= parsedMinVal;
				};
				attr.$observe('min', function (val) {
					if (val !== minVal) {
						parsedMinVal = parseObservedDateValue(val);
						minVal = val;
						ctrl.$validate();
					}
				});
			}

			if (isDefined(attr.max) || attr.ngMax) {
				var maxVal = attr.max || $parse(attr.ngMax)(scope);
				var parsedMaxVal = parseObservedDateValue(maxVal);

				ctrl.$validators.max = function (value) {
					return !isValidDate(value) || isUndefined(parsedMaxVal) || parseDate(value) <= parsedMaxVal;
				};
				attr.$observe('max', function (val) {
					if (val !== maxVal) {
						parsedMaxVal = parseObservedDateValue(val);
						maxVal = val;
						ctrl.$validate();
					}
				});
			}

			function isValidDate(value) {
				// Invalid Date: getTime() returns NaN
				return value && !(value.getTime && value.getTime() !== value.getTime());
			}

			function parseObservedDateValue(val) {
				return isDefined(val) && !isDate(val) ? parseDateAndConvertTimeZoneToLocal(val) || undefined : val;
			}

			function parseDateAndConvertTimeZoneToLocal(value, previousDate) {
				var timezone = ctrl.$options.getOption('timezone');

				if (previousTimezone && previousTimezone !== timezone) {
					// If the timezone has changed, adjust the previousDate to the default timezone
					// so that the new date is converted with the correct timezone offset
					previousDate = addDateMinutes(previousDate, timezoneToOffset(previousTimezone));
				}

				var parsedDate = parseDate(value, previousDate);

				if (!isNaN(parsedDate) && timezone) {
					parsedDate = convertTimezoneToLocal(parsedDate, timezone);
				}
				return parsedDate;
			}

			function formatter(value, timezone) {
				var targetFormat = format;

				if (isTimeType && isString(ctrl.$options.getOption('timeSecondsFormat'))) {
					targetFormat = format
						.replace('ss.sss', ctrl.$options.getOption('timeSecondsFormat'))
						.replace(/:$/, '');
				}

				var formatted = $filter('date')(value, targetFormat, timezone);

				if (isTimeType && ctrl.$options.getOption('timeStripZeroSeconds')) {
					formatted = formatted.replace(/(?::00)?(?:\.000)?$/, '');
				}

				return formatted;
			}
		};
	}

	function badInputChecker(scope, element, attr, ctrl, parserName) {
		var node = element[0];
		var nativeValidation = ctrl.$$hasNativeValidators = isObject(node.validity);
		if (nativeValidation) {
			ctrl.$parsers.push(function (value) {
				var validity = element.prop(VALIDITY_STATE_PROPERTY) || {};
				if (validity.badInput || validity.typeMismatch) {
					ctrl.$$parserName = parserName;
					return undefined;
				}

				return value;
			});
		}
	}

	function numberFormatterParser(ctrl) {
		ctrl.$parsers.push(function (value) {
			if (ctrl.$isEmpty(value)) return null;
			if (NUMBER_REGEXP.test(value)) return parseFloat(value);

			ctrl.$$parserName = 'number';
			return undefined;
		});

		ctrl.$formatters.push(function (value) {
			if (!ctrl.$isEmpty(value)) {
				if (!isNumber(value)) {
					throw ngModelMinErr('numfmt', 'Expected `{0}` to be a number', value);
				}
				value = value.toString();
			}
			return value;
		});
	}

	function parseNumberAttrVal(val) {
		if (isDefined(val) && !isNumber(val)) {
			val = parseFloat(val);
		}
		return !isNumberNaN(val) ? val : undefined;
	}

	function isNumberInteger(num) {
		// See http://stackoverflow.com/questions/14636536/how-to-check-if-a-variable-is-an-integer-in-javascript#14794066
		// (minus the assumption that `num` is a number)

		// eslint-disable-next-line no-bitwise
		return (num | 0) === num;
	}

	function countDecimals(num) {
		var numString = num.toString();
		var decimalSymbolIndex = numString.indexOf('.');

		if (decimalSymbolIndex === -1) {
			if (-1 < num && num < 1) {
				// It may be in the exponential notation format (`1e-X`)
				var match = /e-(\d+)$/.exec(numString);

				if (match) {
					return Number(match[1]);
				}
			}

			return 0;
		}

		return numString.length - decimalSymbolIndex - 1;
	}

	function isValidForStep(viewValue, stepBase, step) {
		// At this point `stepBase` and `step` are expected to be non-NaN values
		// and `viewValue` is expected to be a valid stringified number.
		var value = Number(viewValue);

		var isNonIntegerValue = !isNumberInteger(value);
		var isNonIntegerStepBase = !isNumberInteger(stepBase);
		var isNonIntegerStep = !isNumberInteger(step);

		// Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
		// `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
		if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
			var valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
			var stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
			var stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

			var decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
			var multiplier = Math.pow(10, decimalCount);

			value = value * multiplier;
			stepBase = stepBase * multiplier;
			step = step * multiplier;

			if (isNonIntegerValue) value = Math.round(value);
			if (isNonIntegerStepBase) stepBase = Math.round(stepBase);
			if (isNonIntegerStep) step = Math.round(step);
		}

		return (value - stepBase) % step === 0;
	}

	function numberInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
		badInputChecker(scope, element, attr, ctrl, 'number');
		numberFormatterParser(ctrl);
		baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

		var parsedMinVal;

		if (isDefined(attr.min) || attr.ngMin) {
			var minVal = attr.min || $parse(attr.ngMin)(scope);
			parsedMinVal = parseNumberAttrVal(minVal);

			ctrl.$validators.min = function (modelValue, viewValue) {
				return ctrl.$isEmpty(viewValue) || isUndefined(parsedMinVal) || viewValue >= parsedMinVal;
			};

			attr.$observe('min', function (val) {
				if (val !== minVal) {
					parsedMinVal = parseNumberAttrVal(val);
					minVal = val;
					// TODO(matsko): implement validateLater to reduce number of validations
					ctrl.$validate();
				}
			});
		}

		if (isDefined(attr.max) || attr.ngMax) {
			var maxVal = attr.max || $parse(attr.ngMax)(scope);
			var parsedMaxVal = parseNumberAttrVal(maxVal);

			ctrl.$validators.max = function (modelValue, viewValue) {
				return ctrl.$isEmpty(viewValue) || isUndefined(parsedMaxVal) || viewValue <= parsedMaxVal;
			};

			attr.$observe('max', function (val) {
				if (val !== maxVal) {
					parsedMaxVal = parseNumberAttrVal(val);
					maxVal = val;
					// TODO(matsko): implement validateLater to reduce number of validations
					ctrl.$validate();
				}
			});
		}

		if (isDefined(attr.step) || attr.ngStep) {
			var stepVal = attr.step || $parse(attr.ngStep)(scope);
			var parsedStepVal = parseNumberAttrVal(stepVal);

			ctrl.$validators.step = function (modelValue, viewValue) {
				return ctrl.$isEmpty(viewValue) || isUndefined(parsedStepVal) ||
					isValidForStep(viewValue, parsedMinVal || 0, parsedStepVal);
			};

			attr.$observe('step', function (val) {
				// TODO(matsko): implement validateLater to reduce number of validations
				if (val !== stepVal) {
					parsedStepVal = parseNumberAttrVal(val);
					stepVal = val;
					ctrl.$validate();
				}

			});

		}
	}

	function rangeInputType(scope, element, attr, ctrl, $sniffer, $browser) {
		badInputChecker(scope, element, attr, ctrl, 'range');
		numberFormatterParser(ctrl);
		baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

		var supportsRange = ctrl.$$hasNativeValidators && element[0].type === 'range',
			minVal = supportsRange ? 0 : undefined,
			maxVal = supportsRange ? 100 : undefined,
			stepVal = supportsRange ? 1 : undefined,
			validity = element[0].validity,
			hasMinAttr = isDefined(attr.min),
			hasMaxAttr = isDefined(attr.max),
			hasStepAttr = isDefined(attr.step);

		var originalRender = ctrl.$render;

		ctrl.$render = supportsRange && isDefined(validity.rangeUnderflow) && isDefined(validity.rangeOverflow) ?
			//Browsers that implement range will set these values automatically, but reading the adjusted values after
			//$render would cause the min / max validators to be applied with the wrong value
			function rangeRender() {
				originalRender();
				ctrl.$setViewValue(element.val());
			} :
			originalRender;

		if (hasMinAttr) {
			minVal = parseNumberAttrVal(attr.min);

			ctrl.$validators.min = supportsRange ?
				// Since all browsers set the input to a valid value, we don't need to check validity
				function noopMinValidator() { return true; } :
				// non-support browsers validate the min val
				function minValidator(modelValue, viewValue) {
					return ctrl.$isEmpty(viewValue) || isUndefined(minVal) || viewValue >= minVal;
				};

			setInitialValueAndObserver('min', minChange);
		}

		if (hasMaxAttr) {
			maxVal = parseNumberAttrVal(attr.max);

			ctrl.$validators.max = supportsRange ?
				// Since all browsers set the input to a valid value, we don't need to check validity
				function noopMaxValidator() { return true; } :
				// non-support browsers validate the max val
				function maxValidator(modelValue, viewValue) {
					return ctrl.$isEmpty(viewValue) || isUndefined(maxVal) || viewValue <= maxVal;
				};

			setInitialValueAndObserver('max', maxChange);
		}

		if (hasStepAttr) {
			stepVal = parseNumberAttrVal(attr.step);

			ctrl.$validators.step = supportsRange ?
				function nativeStepValidator() {
					// Currently, only FF implements the spec on step change correctly (i.e. adjusting the
					// input element value to a valid value). It's possible that other browsers set the stepMismatch
					// validity error instead, so we can at least report an error in that case.
					return !validity.stepMismatch;
				} :
				// ngStep doesn't set the setp attr, so the browser doesn't adjust the input value as setting step would
				function stepValidator(modelValue, viewValue) {
					return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
						isValidForStep(viewValue, minVal || 0, stepVal);
				};

			setInitialValueAndObserver('step', stepChange);
		}

		function setInitialValueAndObserver(htmlAttrName, changeFn) {
			// interpolated attributes set the attribute value only after a digest, but we need the
			// attribute value when the input is first rendered, so that the browser can adjust the
			// input value based on the min/max value
			element.attr(htmlAttrName, attr[htmlAttrName]);
			var oldVal = attr[htmlAttrName];
			attr.$observe(htmlAttrName, function wrappedObserver(val) {
				if (val !== oldVal) {
					oldVal = val;
					changeFn(val);
				}
			});
		}

		function minChange(val) {
			minVal = parseNumberAttrVal(val);
			// ignore changes before model is initialized
			if (isNumberNaN(ctrl.$modelValue)) {
				return;
			}

			if (supportsRange) {
				var elVal = element.val();
				// IE11 doesn't set the el val correctly if the minVal is greater than the element value
				if (minVal > elVal) {
					elVal = minVal;
					element.val(elVal);
				}
				ctrl.$setViewValue(elVal);
			} else {
				// TODO(matsko): implement validateLater to reduce number of validations
				ctrl.$validate();
			}
		}

		function maxChange(val) {
			maxVal = parseNumberAttrVal(val);
			// ignore changes before model is initialized
			if (isNumberNaN(ctrl.$modelValue)) {
				return;
			}

			if (supportsRange) {
				var elVal = element.val();
				// IE11 doesn't set the el val correctly if the maxVal is less than the element value
				if (maxVal < elVal) {
					element.val(maxVal);
					// IE11 and Chrome don't set the value to the minVal when max < min
					elVal = maxVal < minVal ? minVal : maxVal;
				}
				ctrl.$setViewValue(elVal);
			} else {
				// TODO(matsko): implement validateLater to reduce number of validations
				ctrl.$validate();
			}
		}

		function stepChange(val) {
			stepVal = parseNumberAttrVal(val);
			// ignore changes before model is initialized
			if (isNumberNaN(ctrl.$modelValue)) {
				return;
			}

			// Some browsers don't adjust the input value correctly, but set the stepMismatch error
			if (!supportsRange) {
				// TODO(matsko): implement validateLater to reduce number of validations
				ctrl.$validate();
			} else if (ctrl.$viewValue !== element.val()) {
				ctrl.$setViewValue(element.val());
			}
		}
	}

	function urlInputType(scope, element, attr, ctrl, $sniffer, $browser) {
		// Note: no badInputChecker here by purpose as `url` is only a validation
		// in browsers, i.e. we can always read out input.value even if it is not valid!
		baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
		stringBasedInputType(ctrl);

		ctrl.$validators.url = function (modelValue, viewValue) {
			var value = modelValue || viewValue;
			return ctrl.$isEmpty(value) || URL_REGEXP.test(value);
		};
	}

	function emailInputType(scope, element, attr, ctrl, $sniffer, $browser) {
		// Note: no badInputChecker here by purpose as `url` is only a validation
		// in browsers, i.e. we can always read out input.value even if it is not valid!
		baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
		stringBasedInputType(ctrl);

		ctrl.$validators.email = function (modelValue, viewValue) {
			var value = modelValue || viewValue;
			return ctrl.$isEmpty(value) || EMAIL_REGEXP.test(value);
		};
	}

	function radioInputType(scope, element, attr, ctrl) {
		var doTrim = !attr.ngTrim || trim(attr.ngTrim) !== 'false';
		// make the name unique, if not defined
		if (isUndefined(attr.name)) {
			element.attr('name', nextUid());
		}

		var listener = function (ev) {
			var value;
			if (element[0].checked) {
				value = attr.value;
				if (doTrim) {
					value = trim(value);
				}
				ctrl.$setViewValue(value, ev && ev.type);
			}
		};

		element.on('change', listener);

		ctrl.$render = function () {
			var value = attr.value;
			if (doTrim) {
				value = trim(value);
			}
			element[0].checked = (value === ctrl.$viewValue);
		};

		attr.$observe('value', ctrl.$render);
	}

	function parseConstantExpr($parse, context, name, expression, fallback) {
		var parseFn;
		if (isDefined(expression)) {
			parseFn = $parse(expression);
			if (!parseFn.constant) {
				throw ngModelMinErr('constexpr', 'Expected constant expression for `{0}`, but saw ' +
					'`{1}`.', name, expression);
			}
			return parseFn(context);
		}
		return fallback;
	}

	function checkboxInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
		var trueValue = parseConstantExpr($parse, scope, 'ngTrueValue', attr.ngTrueValue, true);
		var falseValue = parseConstantExpr($parse, scope, 'ngFalseValue', attr.ngFalseValue, false);

		var listener = function (ev) {
			ctrl.$setViewValue(element[0].checked, ev && ev.type);
		};

		element.on('change', listener);

		ctrl.$render = function () {
			element[0].checked = ctrl.$viewValue;
		};

		// Override the standard `$isEmpty` because the $viewValue of an empty checkbox is always set to `false`
		// This is because of the parser below, which compares the `$modelValue` with `trueValue` to convert
		// it to a boolean.
		ctrl.$isEmpty = function (value) {
			return value === false;
		};

		ctrl.$formatters.push(function (value) {
			return equals(value, trueValue);
		});

		ctrl.$parsers.push(function (value) {
			return value ? trueValue : falseValue;
		});
	}


	/**
	 * @ngdoc directive
	 * @name textarea
	 * @restrict E
	 *
	 * @description
	 * HTML textarea element control with AngularJS data-binding. The data-binding and validation
	 * properties of this element are exactly the same as those of the
	 * {@link ng.directive:input input element}.
	 *
	 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
	 * @param {string=} name Property name of the form under which the control is published.
	 * @param {string=} required Sets `required` validation error key if the value is not entered.
	 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
	 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
	 *    `required` when you want to data-bind to the `required` attribute.
	 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
	 *    minlength.
	 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
	 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
	 *    length.
	 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
	 *    does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
	 *    If the expression evaluates to a RegExp object, then this is used directly.
	 *    If the expression evaluates to a string, then it will be converted to a RegExp
	 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
	 *    `new RegExp('^abc$')`.<br />
	 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
	 *    start at the index of the last search's match, thus not taking the whole input value into
	 *    account.
	 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
	 *    interaction with the input element.
	 * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
	 *
	 * @knownIssue
	 *
	 * When specifying the `placeholder` attribute of `<textarea>`, Internet Explorer will temporarily
	 * insert the placeholder value as the textarea's content. If the placeholder value contains
	 * interpolation (`{{ ... }}`), an error will be logged in the console when AngularJS tries to update
	 * the value of the by-then-removed text node. This doesn't affect the functionality of the
	 * textarea, but can be undesirable.
	 *
	 * You can work around this Internet Explorer issue by using `ng-attr-placeholder` instead of
	 * `placeholder` on textareas, whenever you need interpolation in the placeholder value. You can
	 * find more details on `ngAttr` in the
	 * [Interpolation](guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes) section of the
	 * Developer Guide.
	 */


	/**
	 * @ngdoc directive
	 * @name input
	 * @restrict E
	 *
	 * @description
	 * HTML input element control. When used together with {@link ngModel `ngModel`}, it provides data-binding,
	 * input state control, and validation.
	 * Input control follows HTML5 input types and polyfills the HTML5 validation behavior for older browsers.
	 *
	 * <div class="alert alert-warning">
	 * **Note:** Not every feature offered is available for all input types.
	 * Specifically, data binding and event handling via `ng-model` is unsupported for `input[file]`.
	 * </div>
	 *
	 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
	 * @param {string=} name Property name of the form under which the control is published.
	 * @param {string=} required Sets `required` validation error key if the value is not entered.
	 * @param {boolean=} ngRequired Sets `required` attribute if set to true
	 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
	 *    minlength.
	 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
	 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
	 *    length.
	 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
	 *    value does not match a RegExp found by evaluating the AngularJS expression given in the attribute value.
	 *    If the expression evaluates to a RegExp object, then this is used directly.
	 *    If the expression evaluates to a string, then it will be converted to a RegExp
	 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
	 *    `new RegExp('^abc$')`.<br />
	 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
	 *    start at the index of the last search's match, thus not taking the whole input value into
	 *    account.
	 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
	 *    interaction with the input element.
	 * @param {boolean=} [ngTrim=true] If set to false AngularJS will not automatically trim the input.
	 *    This parameter is ignored for input[type=password] controls, which will never trim the
	 *    input.
	 *
	 * @example
		<example name="input-directive" module="inputExample">
		  <file name="index.html">
		   <script>
			  angular.module('inputExample', [])
				.controller('ExampleController', ['$scope', function($scope) {
				  $scope.user = {name: 'guest', last: 'visitor'};
				}]);
		   </script>
		   <div ng-controller="ExampleController">
			 <form name="myForm">
			   <label>
				  User name:
				  <input type="text" name="userName" ng-model="user.name" required>
			   </label>
			   <div role="alert">
				 <span class="error" ng-show="myForm.userName.$error.required">
				  Required!</span>
			   </div>
			   <label>
				  Last name:
				  <input type="text" name="lastName" ng-model="user.last"
				  ng-minlength="3" ng-maxlength="10">
			   </label>
			   <div role="alert">
				 <span class="error" ng-show="myForm.lastName.$error.minlength">
				   Too short!</span>
				 <span class="error" ng-show="myForm.lastName.$error.maxlength">
				   Too long!</span>
			   </div>
			 </form>
			 <hr>
			 <tt>user = {{user}}</tt><br/>
			 <tt>myForm.userName.$valid = {{myForm.userName.$valid}}</tt><br/>
			 <tt>myForm.userName.$error = {{myForm.userName.$error}}</tt><br/>
			 <tt>myForm.lastName.$valid = {{myForm.lastName.$valid}}</tt><br/>
			 <tt>myForm.lastName.$error = {{myForm.lastName.$error}}</tt><br/>
			 <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
			 <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
			 <tt>myForm.$error.minlength = {{!!myForm.$error.minlength}}</tt><br/>
			 <tt>myForm.$error.maxlength = {{!!myForm.$error.maxlength}}</tt><br/>
		   </div>
		  </file>
		  <file name="protractor.js" type="protractor">
			var user = element(by.exactBinding('user'));
			var userNameValid = element(by.binding('myForm.userName.$valid'));
			var lastNameValid = element(by.binding('myForm.lastName.$valid'));
			var lastNameError = element(by.binding('myForm.lastName.$error'));
			var formValid = element(by.binding('myForm.$valid'));
			var userNameInput = element(by.model('user.name'));
			var userLastInput = element(by.model('user.last'));
	
			it('should initialize to model', function() {
			  expect(user.getText()).toContain('{"name":"guest","last":"visitor"}');
			  expect(userNameValid.getText()).toContain('true');
			  expect(formValid.getText()).toContain('true');
			});
	
			it('should be invalid if empty when required', function() {
			  userNameInput.clear();
			  userNameInput.sendKeys('');
	
			  expect(user.getText()).toContain('{"last":"visitor"}');
			  expect(userNameValid.getText()).toContain('false');
			  expect(formValid.getText()).toContain('false');
			});
	
			it('should be valid if empty when min length is set', function() {
			  userLastInput.clear();
			  userLastInput.sendKeys('');
	
			  expect(user.getText()).toContain('{"name":"guest","last":""}');
			  expect(lastNameValid.getText()).toContain('true');
			  expect(formValid.getText()).toContain('true');
			});
	
			it('should be invalid if less than required min length', function() {
			  userLastInput.clear();
			  userLastInput.sendKeys('xx');
	
			  expect(user.getText()).toContain('{"name":"guest"}');
			  expect(lastNameValid.getText()).toContain('false');
			  expect(lastNameError.getText()).toContain('minlength');
			  expect(formValid.getText()).toContain('false');
			});
	
			it('should be invalid if longer than max length', function() {
			  userLastInput.clear();
			  userLastInput.sendKeys('some ridiculously long name');
	
			  expect(user.getText()).toContain('{"name":"guest"}');
			  expect(lastNameValid.getText()).toContain('false');
			  expect(lastNameError.getText()).toContain('maxlength');
			  expect(formValid.getText()).toContain('false');
			});
		  </file>
		</example>
	 */
	var inputDirective = ['$browser', '$sniffer', '$filter', '$parse',
		function ($browser, $sniffer, $filter, $parse) {
			return {
				restrict: 'E',
				require: ['?ngModel'],
				link: {
					pre: function (scope, element, attr, ctrls) {
						if (ctrls[0]) {
							(inputType[lowercase(attr.type)] || inputType.text)(scope, element, attr, ctrls[0], $sniffer,
								$browser, $filter, $parse);
						}
					}
				}
			};
		}];


	var hiddenInputBrowserCacheDirective = function () {
		var valueProperty = {
			configurable: true,
			enumerable: false,
			get: function () {
				return this.getAttribute('value') || '';
			},
			set: function (val) {
				this.setAttribute('value', val);
			}
		};

		return {
			restrict: 'E',
			priority: 200,
			compile: function (_, attr) {
				if (lowercase(attr.type) !== 'hidden') {
					return;
				}

				return {
					pre: function (scope, element, attr, ctrls) {
						var node = element[0];

						// Support: Edge
						// Moving the DOM around prevents autofillling
						if (node.parentNode) {
							node.parentNode.insertBefore(node, node.nextSibling);
						}

						// Support: FF, IE
						// Avoiding direct assignment to .value prevents autofillling
						if (Object.defineProperty) {
							Object.defineProperty(node, 'value', valueProperty);
						}
					}
				};
			}
		};
	};



	var CONSTANT_VALUE_REGEXP = /^(true|false|\d+)$/;
	/**
	 * @ngdoc directive
	 * @name ngValue
	 * @restrict A
	 * @priority 100
	 *
	 * @description
	 * Binds the given expression to the value of the element.
	 *
	 * It is mainly used on {@link input[radio] `input[radio]`} and option elements,
	 * so that when the element is selected, the {@link ngModel `ngModel`} of that element (or its
	 * {@link select `select`} parent element) is set to the bound value. It is especially useful
	 * for dynamically generated lists using {@link ngRepeat `ngRepeat`}, as shown below.
	 *
	 * It can also be used to achieve one-way binding of a given expression to an input element
	 * such as an `input[text]` or a `textarea`, when that element does not use ngModel.
	 *
	 * @element ANY
	 * @param {string=} ngValue AngularJS expression, whose value will be bound to the `value` attribute
	 * and `value` property of the element.
	 *
	 * @example
		<example name="ngValue-directive" module="valueExample">
		  <file name="index.html">
		   <script>
			  angular.module('valueExample', [])
				.controller('ExampleController', ['$scope', function($scope) {
				  $scope.names = ['pizza', 'unicorns', 'robots'];
				  $scope.my = { favorite: 'unicorns' };
				}]);
		   </script>
			<form ng-controller="ExampleController">
			  <h2>Which is your favorite?</h2>
				<label ng-repeat="name in names" for="{{name}}">
				  {{name}}
				  <input type="radio"
						 ng-model="my.favorite"
						 ng-value="name"
						 id="{{name}}"
						 name="favorite">
				</label>
			  <div>You chose {{my.favorite}}</div>
			</form>
		  </file>
		  <file name="protractor.js" type="protractor">
			var favorite = element(by.binding('my.favorite'));
	
			it('should initialize to model', function() {
			  expect(favorite.getText()).toContain('unicorns');
			});
			it('should bind the values to the inputs', function() {
			  element.all(by.model('my.favorite')).get(0).click();
			  expect(favorite.getText()).toContain('pizza');
			});
		  </file>
		</example>
	 */
	var ngValueDirective = function () {
		/**
		 *  inputs use the value attribute as their default value if the value property is not set.
		 *  Once the value property has been set (by adding input), it will not react to changes to
		 *  the value attribute anymore. Setting both attribute and property fixes this behavior, and
		 *  makes it possible to use ngValue as a sort of one-way bind.
		 */
		function updateElementValue(element, attr, value) {
			// Support: IE9 only
			// In IE9 values are converted to string (e.g. `input.value = null` results in `input.value === 'null'`).
			var propValue = isDefined(value) ? value : (msie === 9) ? '' : null;
			element.prop('value', propValue);
			attr.$set('value', value);
		}

		return {
			restrict: 'A',
			priority: 100,
			compile: function (tpl, tplAttr) {
				if (CONSTANT_VALUE_REGEXP.test(tplAttr.ngValue)) {
					return function ngValueConstantLink(scope, elm, attr) {
						var value = scope.$eval(attr.ngValue);
						updateElementValue(elm, attr, value);
					};
				} else {
					return function ngValueLink(scope, elm, attr) {
						scope.$watch(attr.ngValue, function valueWatchAction(value) {
							updateElementValue(elm, attr, value);
						});
					};
				}
			}
		};
	};

	/**
	 * @ngdoc directive
	 * @name ngBind
	 * @restrict AC
	 *
	 * @description
	 * The `ngBind` attribute tells AngularJS to replace the text content of the specified HTML element
	 * with the value of a given expression, and to update the text content when the value of that
	 * expression changes.
	 *
	 * Typically, you don't use `ngBind` directly, but instead you use the double curly markup like
	 * `{{ expression }}` which is similar but less verbose.
	 *
	 * It is preferable to use `ngBind` instead of `{{ expression }}` if a template is momentarily
	 * displayed by the browser in its raw state before AngularJS compiles it. Since `ngBind` is an
	 * element attribute, it makes the bindings invisible to the user while the page is loading.
	 *
	 * An alternative solution to this problem would be using the
	 * {@link ng.directive:ngCloak ngCloak} directive.
	 *
	 *
	 * @element ANY
	 * @param {expression} ngBind {@link guide/expression Expression} to evaluate.
	 *
	 * @example
	 * Enter a name in the Live Preview text box; the greeting below the text box changes instantly.
	   <example module="bindExample" name="ng-bind">
		 <file name="index.html">
		   <script>
			 angular.module('bindExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.name = 'Whirled';
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			 <label>Enter name: <input type="text" ng-model="name"></label><br>
			 Hello <span ng-bind="name"></span>!
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-bind', function() {
			 var nameInput = element(by.model('name'));
	
			 expect(element(by.binding('name')).getText()).toBe('Whirled');
			 nameInput.clear();
			 nameInput.sendKeys('world');
			 expect(element(by.binding('name')).getText()).toBe('world');
		   });
		 </file>
	   </example>
	 */
	var ngBindDirective = ['$compile', function ($compile) {
		return {
			restrict: 'AC',
			compile: function ngBindCompile(templateElement) {
				$compile.$$addBindingClass(templateElement);
				return function ngBindLink(scope, element, attr) {
					$compile.$$addBindingInfo(element, attr.ngBind);
					element = element[0];
					scope.$watch(attr.ngBind, function ngBindWatchAction(value) {
						element.textContent = stringify(value);
					});
				};
			}
		};
	}];


	/**
	 * @ngdoc directive
	 * @name ngBindTemplate
	 *
	 * @description
	 * The `ngBindTemplate` directive specifies that the element
	 * text content should be replaced with the interpolation of the template
	 * in the `ngBindTemplate` attribute.
	 * Unlike `ngBind`, the `ngBindTemplate` can contain multiple `{{` `}}`
	 * expressions. This directive is needed since some HTML elements
	 * (such as TITLE and OPTION) cannot contain SPAN elements.
	 *
	 * @element ANY
	 * @param {string} ngBindTemplate template of form
	 *   <tt>{{</tt> <tt>expression</tt> <tt>}}</tt> to eval.
	 *
	 * @example
	 * Try it here: enter text in text box and watch the greeting change.
	   <example module="bindExample" name="ng-bind-template">
		 <file name="index.html">
		   <script>
			 angular.module('bindExample', [])
			   .controller('ExampleController', ['$scope', function($scope) {
				 $scope.salutation = 'Hello';
				 $scope.name = 'World';
			   }]);
		   </script>
		   <div ng-controller="ExampleController">
			<label>Salutation: <input type="text" ng-model="salutation"></label><br>
			<label>Name: <input type="text" ng-model="name"></label><br>
			<pre ng-bind-template="{{salutation}} {{name}}!"></pre>
		   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-bind', function() {
			 var salutationElem = element(by.binding('salutation'));
			 var salutationInput = element(by.model('salutation'));
			 var nameInput = element(by.model('name'));
	
			 expect(salutationElem.getText()).toBe('Hello World!');
	
			 salutationInput.clear();
			 salutationInput.sendKeys('Greetings');
			 nameInput.clear();
			 nameInput.sendKeys('user');
	
			 expect(salutationElem.getText()).toBe('Greetings user!');
		   });
		 </file>
	   </example>
	 */
	var ngBindTemplateDirective = ['$interpolate', '$compile', function ($interpolate, $compile) {
		return {
			compile: function ngBindTemplateCompile(templateElement) {
				$compile.$$addBindingClass(templateElement);
				return function ngBindTemplateLink(scope, element, attr) {
					var interpolateFn = $interpolate(element.attr(attr.$attr.ngBindTemplate));
					$compile.$$addBindingInfo(element, interpolateFn.expressions);
					element = element[0];
					attr.$observe('ngBindTemplate', function (value) {
						element.textContent = isUndefined(value) ? '' : value;
					});
				};
			}
		};
	}];


	/**
	 * @ngdoc directive
	 * @name ngBindHtml
	 *
	 * @description
	 * Evaluates the expression and inserts the resulting HTML into the element in a secure way. By default,
	 * the resulting HTML content will be sanitized using the {@link ngSanitize.$sanitize $sanitize} service.
	 * To utilize this functionality, ensure that `$sanitize` is available, for example, by including {@link
	 * ngSanitize} in your module's dependencies (not in core AngularJS). In order to use {@link ngSanitize}
	 * in your module's dependencies, you need to include "angular-sanitize.js" in your application.
	 *
	 * You may also bypass sanitization for values you know are safe. To do so, bind to
	 * an explicitly trusted value via {@link ng.$sce#trustAsHtml $sce.trustAsHtml}.  See the example
	 * under {@link ng.$sce#show-me-an-example-using-sce- Strict Contextual Escaping (SCE)}.
	 *
	 * Note: If a `$sanitize` service is unavailable and the bound value isn't explicitly trusted, you
	 * will have an exception (instead of an exploit.)
	 *
	 * @element ANY
	 * @param {expression} ngBindHtml {@link guide/expression Expression} to evaluate.
	 *
	 * @example
	
	   <example module="bindHtmlExample" deps="angular-sanitize.js" name="ng-bind-html">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			<p ng-bind-html="myHTML"></p>
		   </div>
		 </file>
	
		 <file name="script.js">
		   angular.module('bindHtmlExample', ['ngSanitize'])
			 .controller('ExampleController', ['$scope', function($scope) {
			   $scope.myHTML =
				  'I am an <code>HTML</code>string with ' +
				  '<a href="#">links!</a> and other <em>stuff</em>';
			 }]);
		 </file>
	
		 <file name="protractor.js" type="protractor">
		   it('should check ng-bind-html', function() {
			 expect(element(by.binding('myHTML')).getText()).toBe(
				 'I am an HTMLstring with links! and other stuff');
		   });
		 </file>
	   </example>
	 */
	var ngBindHtmlDirective = ['$sce', '$parse', '$compile', function ($sce, $parse, $compile) {
		return {
			restrict: 'A',
			compile: function ngBindHtmlCompile(tElement, tAttrs) {
				var ngBindHtmlGetter = $parse(tAttrs.ngBindHtml);
				var ngBindHtmlWatch = $parse(tAttrs.ngBindHtml, function sceValueOf(val) {
					// Unwrap the value to compare the actual inner safe value, not the wrapper object.
					return $sce.valueOf(val);
				});
				$compile.$$addBindingClass(tElement);

				return function ngBindHtmlLink(scope, element, attr) {
					$compile.$$addBindingInfo(element, attr.ngBindHtml);

					scope.$watch(ngBindHtmlWatch, function ngBindHtmlWatchAction() {
						// The watched value is the unwrapped value. To avoid re-escaping, use the direct getter.
						var value = ngBindHtmlGetter(scope);
						element.html($sce.getTrustedHtml(value) || '');
					});
				};
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngChange
	 * @restrict A
	 *
	 * @description
	 * Evaluate the given expression when the user changes the input.
	 * The expression is evaluated immediately, unlike the JavaScript onchange event
	 * which only triggers at the end of a change (usually, when the user leaves the
	 * form element or presses the return key).
	 *
	 * The `ngChange` expression is only evaluated when a change in the input value causes
	 * a new value to be committed to the model.
	 *
	 * It will not be evaluated:
	 * * if the value returned from the `$parsers` transformation pipeline has not changed
	 * * if the input has continued to be invalid since the model will stay `null`
	 * * if the model is changed programmatically and not by a change to the input value
	 *
	 *
	 * Note, this directive requires `ngModel` to be present.
	 *
	 * @element ANY
	 * @param {expression} ngChange {@link guide/expression Expression} to evaluate upon change
	 * in input value.
	 *
	 * @example
	 * <example name="ngChange-directive" module="changeExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('changeExample', [])
	 *         .controller('ExampleController', ['$scope', function($scope) {
	 *           $scope.counter = 0;
	 *           $scope.change = function() {
	 *             $scope.counter++;
	 *           };
	 *         }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <input type="checkbox" ng-model="confirmed" ng-change="change()" id="ng-change-example1" />
	 *       <input type="checkbox" ng-model="confirmed" id="ng-change-example2" />
	 *       <label for="ng-change-example2">Confirmed</label><br />
	 *       <tt>debug = {{confirmed}}</tt><br/>
	 *       <tt>counter = {{counter}}</tt><br/>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     var counter = element(by.binding('counter'));
	 *     var debug = element(by.binding('confirmed'));
	 *
	 *     it('should evaluate the expression if changing from view', function() {
	 *       expect(counter.getText()).toContain('0');
	 *
	 *       element(by.id('ng-change-example1')).click();
	 *
	 *       expect(counter.getText()).toContain('1');
	 *       expect(debug.getText()).toContain('true');
	 *     });
	 *
	 *     it('should not evaluate the expression if changing from model', function() {
	 *       element(by.id('ng-change-example2')).click();
	
	 *       expect(counter.getText()).toContain('0');
	 *       expect(debug.getText()).toContain('true');
	 *     });
	 *   </file>
	 * </example>
	 */
	var ngChangeDirective = valueFn({
		restrict: 'A',
		require: 'ngModel',
		link: function (scope, element, attr, ctrl) {
			ctrl.$viewChangeListeners.push(function () {
				scope.$eval(attr.ngChange);
			});
		}
	});

	/* exported
	  ngClassDirective,
	  ngClassEvenDirective,
	  ngClassOddDirective
	*/

	function classDirective(name, selector) {
		name = 'ngClass' + name;
		var indexWatchExpression;

		return ['$parse', function ($parse) {
			return {
				restrict: 'AC',
				link: function (scope, element, attr) {
					var classCounts = element.data('$classCounts');
					var oldModulo = true;
					var oldClassString;

					if (!classCounts) {
						// Use createMap() to prevent class assumptions involving property
						// names in Object.prototype
						classCounts = createMap();
						element.data('$classCounts', classCounts);
					}

					if (name !== 'ngClass') {
						if (!indexWatchExpression) {
							indexWatchExpression = $parse('$index', function moduloTwo($index) {
								// eslint-disable-next-line no-bitwise
								return $index & 1;
							});
						}

						scope.$watch(indexWatchExpression, ngClassIndexWatchAction);
					}

					scope.$watch($parse(attr[name], toClassString), ngClassWatchAction);

					function addClasses(classString) {
						classString = digestClassCounts(split(classString), 1);
						attr.$addClass(classString);
					}

					function removeClasses(classString) {
						classString = digestClassCounts(split(classString), -1);
						attr.$removeClass(classString);
					}

					function updateClasses(oldClassString, newClassString) {
						var oldClassArray = split(oldClassString);
						var newClassArray = split(newClassString);

						var toRemoveArray = arrayDifference(oldClassArray, newClassArray);
						var toAddArray = arrayDifference(newClassArray, oldClassArray);

						var toRemoveString = digestClassCounts(toRemoveArray, -1);
						var toAddString = digestClassCounts(toAddArray, 1);

						attr.$addClass(toAddString);
						attr.$removeClass(toRemoveString);
					}

					function digestClassCounts(classArray, count) {
						var classesToUpdate = [];

						forEach(classArray, function (className) {
							if (count > 0 || classCounts[className]) {
								classCounts[className] = (classCounts[className] || 0) + count;
								if (classCounts[className] === +(count > 0)) {
									classesToUpdate.push(className);
								}
							}
						});

						return classesToUpdate.join(' ');
					}

					function ngClassIndexWatchAction(newModulo) {
						// This watch-action should run before the `ngClassWatchAction()`, thus it
						// adds/removes `oldClassString`. If the `ngClass` expression has changed as well, the
						// `ngClassWatchAction()` will update the classes.
						if (newModulo === selector) {
							addClasses(oldClassString);
						} else {
							removeClasses(oldClassString);
						}

						oldModulo = newModulo;
					}

					function ngClassWatchAction(newClassString) {
						if (oldModulo === selector) {
							updateClasses(oldClassString, newClassString);
						}

						oldClassString = newClassString;
					}
				}
			};
		}];

		// Helpers
		function arrayDifference(tokens1, tokens2) {
			if (!tokens1 || !tokens1.length) return [];
			if (!tokens2 || !tokens2.length) return tokens1;

			var values = [];

			outer:
			for (var i = 0; i < tokens1.length; i++) {
				var token = tokens1[i];
				for (var j = 0; j < tokens2.length; j++) {
					if (token === tokens2[j]) continue outer;
				}
				values.push(token);
			}

			return values;
		}

		function split(classString) {
			return classString && classString.split(' ');
		}

		function toClassString(classValue) {
			if (!classValue) return classValue;

			var classString = classValue;

			if (isArray(classValue)) {
				classString = classValue.map(toClassString).join(' ');
			} else if (isObject(classValue)) {
				classString = Object.keys(classValue).
					filter(function (key) { return classValue[key]; }).
					join(' ');
			} else if (!isString(classValue)) {
				classString = classValue + '';
			}

			return classString;
		}
	}

	/**
	 * @ngdoc directive
	 * @name ngClass
	 * @restrict AC
	 * @element ANY
	 *
	 * @description
	 * The `ngClass` directive allows you to dynamically set CSS classes on an HTML element by databinding
	 * an expression that represents all classes to be added.
	 *
	 * The directive operates in three different ways, depending on which of three types the expression
	 * evaluates to:
	 *
	 * 1. If the expression evaluates to a string, the string should be one or more space-delimited class
	 * names.
	 *
	 * 2. If the expression evaluates to an object, then for each key-value pair of the
	 * object with a truthy value the corresponding key is used as a class name.
	 *
	 * 3. If the expression evaluates to an array, each element of the array should either be a string as in
	 * type 1 or an object as in type 2. This means that you can mix strings and objects together in an array
	 * to give you more control over what CSS classes appear. See the code below for an example of this.
	 *
	 *
	 * The directive won't add duplicate classes if a particular class was already set.
	 *
	 * When the expression changes, the previously added classes are removed and only then are the
	 * new classes added.
	 *
	 * @knownIssue
	 * You should not use {@link guide/interpolation interpolation} in the value of the `class`
	 * attribute, when using the `ngClass` directive on the same element.
	 * See {@link guide/interpolation#known-issues here} for more info.
	 *
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
	 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
	 * | {@link ng.$animate#setClass setClass} | just before classes are added and classes are removed from the element at the same time |
	 *
	 * ### ngClass and pre-existing CSS3 Transitions/Animations
	   The ngClass directive still supports CSS3 Transitions/Animations even if they do not follow the ngAnimate CSS naming structure.
	   Upon animation ngAnimate will apply supplementary CSS classes to track the start and end of an animation, but this will not hinder
	   any pre-existing CSS transitions already on the element. To get an idea of what happens during a class-based animation, be sure
	   to view the step by step details of {@link $animate#addClass $animate.addClass} and
	   {@link $animate#removeClass $animate.removeClass}.
	 *
	 * @param {expression} ngClass {@link guide/expression Expression} to eval. The result
	 *   of the evaluation can be a string representing space delimited class
	 *   names, an array, or a map of class names to boolean values. In the case of a map, the
	 *   names of the properties whose values are truthy will be added as css classes to the
	 *   element.
	 *
	 * @example
	 * ### Basic
	   <example name="ng-class">
		 <file name="index.html">
		   <p ng-class="{strike: deleted, bold: important, 'has-error': error}">Map Syntax Example</p>
		   <label>
			  <input type="checkbox" ng-model="deleted">
			  deleted (apply "strike" class)
		   </label><br>
		   <label>
			  <input type="checkbox" ng-model="important">
			  important (apply "bold" class)
		   </label><br>
		   <label>
			  <input type="checkbox" ng-model="error">
			  error (apply "has-error" class)
		   </label>
		   <hr>
		   <p ng-class="style">Using String Syntax</p>
		   <input type="text" ng-model="style"
				  placeholder="Type: bold strike red" aria-label="Type: bold strike red">
		   <hr>
		   <p ng-class="[style1, style2, style3]">Using Array Syntax</p>
		   <input ng-model="style1"
				  placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red"><br>
		   <input ng-model="style2"
				  placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 2"><br>
		   <input ng-model="style3"
				  placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 3"><br>
		   <hr>
		   <p ng-class="[style4, {orange: warning}]">Using Array and Map Syntax</p>
		   <input ng-model="style4" placeholder="Type: bold, strike" aria-label="Type: bold, strike"><br>
		   <label><input type="checkbox" ng-model="warning"> warning (apply "orange" class)</label>
		 </file>
		 <file name="style.css">
		   .strike {
			   text-decoration: line-through;
		   }
		   .bold {
			   font-weight: bold;
		   }
		   .red {
			   color: red;
		   }
		   .has-error {
			   color: red;
			   background-color: yellow;
		   }
		   .orange {
			   color: orange;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   var ps = element.all(by.css('p'));
	
		   it('should let you toggle the class', function() {
	
			 expect(ps.first().getAttribute('class')).not.toMatch(/bold/);
			 expect(ps.first().getAttribute('class')).not.toMatch(/has-error/);
	
			 element(by.model('important')).click();
			 expect(ps.first().getAttribute('class')).toMatch(/bold/);
	
			 element(by.model('error')).click();
			 expect(ps.first().getAttribute('class')).toMatch(/has-error/);
		   });
	
		   it('should let you toggle string example', function() {
			 expect(ps.get(1).getAttribute('class')).toBe('');
			 element(by.model('style')).clear();
			 element(by.model('style')).sendKeys('red');
			 expect(ps.get(1).getAttribute('class')).toBe('red');
		   });
	
		   it('array example should have 3 classes', function() {
			 expect(ps.get(2).getAttribute('class')).toBe('');
			 element(by.model('style1')).sendKeys('bold');
			 element(by.model('style2')).sendKeys('strike');
			 element(by.model('style3')).sendKeys('red');
			 expect(ps.get(2).getAttribute('class')).toBe('bold strike red');
		   });
	
		   it('array with map example should have 2 classes', function() {
			 expect(ps.last().getAttribute('class')).toBe('');
			 element(by.model('style4')).sendKeys('bold');
			 element(by.model('warning')).click();
			 expect(ps.last().getAttribute('class')).toBe('bold orange');
		   });
		 </file>
	   </example>
	
	   @example
	   ### Animations
	
	   The example below demonstrates how to perform animations using ngClass.
	
	   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class">
		 <file name="index.html">
		  <input id="setbtn" type="button" value="set" ng-click="myVar='my-class'">
		  <input id="clearbtn" type="button" value="clear" ng-click="myVar=''">
		  <br>
		  <span class="base-class" ng-class="myVar">Sample Text</span>
		 </file>
		 <file name="style.css">
		   .base-class {
			 transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
		   }
	
		   .base-class.my-class {
			 color: red;
			 font-size:3em;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-class', function() {
			 expect(element(by.css('.base-class')).getAttribute('class')).not.
			   toMatch(/my-class/);
	
			 element(by.id('setbtn')).click();
	
			 expect(element(by.css('.base-class')).getAttribute('class')).
			   toMatch(/my-class/);
	
			 element(by.id('clearbtn')).click();
	
			 expect(element(by.css('.base-class')).getAttribute('class')).not.
			   toMatch(/my-class/);
		   });
		 </file>
	   </example>
	 */
	var ngClassDirective = classDirective('', true);

	/**
	 * @ngdoc directive
	 * @name ngClassOdd
	 * @restrict AC
	 *
	 * @description
	 * The `ngClassOdd` and `ngClassEven` directives work exactly as
	 * {@link ng.directive:ngClass ngClass}, except they work in
	 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
	 *
	 * This directive can be applied only within the scope of an
	 * {@link ng.directive:ngRepeat ngRepeat}.
	 *
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
	 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
	 *
	 * @element ANY
	 * @param {expression} ngClassOdd {@link guide/expression Expression} to eval. The result
	 *   of the evaluation can be a string representing space delimited class names or an array.
	 *
	 * @example
	   <example name="ng-class-odd">
		 <file name="index.html">
			<ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
			  <li ng-repeat="name in names">
			   <span ng-class-odd="'odd'" ng-class-even="'even'">
				 {{name}}
			   </span>
			  </li>
			</ol>
		 </file>
		 <file name="style.css">
		   .odd {
			 color: red;
		   }
		   .even {
			 color: blue;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-class-odd and ng-class-even', function() {
			 expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
			   toMatch(/odd/);
			 expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
			   toMatch(/even/);
		   });
		 </file>
	   </example>
	 *
	 * <hr />
	 * @example
	 * An example on how to implement animations using `ngClassOdd`:
	 *
	   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class-odd-animate">
		 <file name="index.html">
		   <div ng-init="items=['Item 3', 'Item 2', 'Item 1', 'Item 0']">
			 <button ng-click="items.unshift('Item ' + items.length)">Add item</button>
			 <hr />
			 <table>
			   <tr ng-repeat="item in items" ng-class-odd="'odd'">
				 <td>{{ item }}</td>
			   </tr>
			 </table>
		   </div>
		 </file>
		 <file name="style.css">
		   .odd {
			 background: rgba(255, 255, 0, 0.25);
		   }
	
		   .odd-add, .odd-remove {
			 transition: 1.5s;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should add new entries to the beginning of the list', function() {
			 var button = element(by.buttonText('Add item'));
			 var rows = element.all(by.repeater('item in items'));
	
			 expect(rows.count()).toBe(4);
			 expect(rows.get(0).getText()).toBe('Item 3');
			 expect(rows.get(1).getText()).toBe('Item 2');
	
			 button.click();
	
			 expect(rows.count()).toBe(5);
			 expect(rows.get(0).getText()).toBe('Item 4');
			 expect(rows.get(1).getText()).toBe('Item 3');
		   });
	
		   it('should add odd class to odd entries', function() {
			 var button = element(by.buttonText('Add item'));
			 var rows = element.all(by.repeater('item in items'));
	
			 expect(rows.get(0).getAttribute('class')).toMatch(/odd/);
			 expect(rows.get(1).getAttribute('class')).not.toMatch(/odd/);
	
			 button.click();
	
			 expect(rows.get(0).getAttribute('class')).toMatch(/odd/);
			 expect(rows.get(1).getAttribute('class')).not.toMatch(/odd/);
		   });
		 </file>
	   </example>
	 */
	var ngClassOddDirective = classDirective('Odd', 0);

	/**
	 * @ngdoc directive
	 * @name ngClassEven
	 * @restrict AC
	 *
	 * @description
	 * The `ngClassOdd` and `ngClassEven` directives work exactly as
	 * {@link ng.directive:ngClass ngClass}, except they work in
	 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
	 *
	 * This directive can be applied only within the scope of an
	 * {@link ng.directive:ngRepeat ngRepeat}.
	 *
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
	 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
	 *
	 * @element ANY
	 * @param {expression} ngClassEven {@link guide/expression Expression} to eval. The
	 *   result of the evaluation can be a string representing space delimited class names or an array.
	 *
	 * @example
	   <example name="ng-class-even">
		 <file name="index.html">
			<ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
			  <li ng-repeat="name in names">
			   <span ng-class-odd="'odd'" ng-class-even="'even'">
				 {{name}} &nbsp; &nbsp; &nbsp;
			   </span>
			  </li>
			</ol>
		 </file>
		 <file name="style.css">
		   .odd {
			 color: red;
		   }
		   .even {
			 color: blue;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-class-odd and ng-class-even', function() {
			 expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
			   toMatch(/odd/);
			 expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
			   toMatch(/even/);
		   });
		 </file>
	   </example>
	 *
	 * <hr />
	 * @example
	 * An example on how to implement animations using `ngClassEven`:
	 *
	   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class-even-animate">
		 <file name="index.html">
		   <div ng-init="items=['Item 3', 'Item 2', 'Item 1', 'Item 0']">
			 <button ng-click="items.unshift('Item ' + items.length)">Add item</button>
			 <hr />
			 <table>
			   <tr ng-repeat="item in items" ng-class-even="'even'">
				 <td>{{ item }}</td>
			   </tr>
			 </table>
		   </div>
		 </file>
		 <file name="style.css">
		   .even {
			 background: rgba(255, 255, 0, 0.25);
		   }
	
		   .even-add, .even-remove {
			 transition: 1.5s;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should add new entries to the beginning of the list', function() {
			 var button = element(by.buttonText('Add item'));
			 var rows = element.all(by.repeater('item in items'));
	
			 expect(rows.count()).toBe(4);
			 expect(rows.get(0).getText()).toBe('Item 3');
			 expect(rows.get(1).getText()).toBe('Item 2');
	
			 button.click();
	
			 expect(rows.count()).toBe(5);
			 expect(rows.get(0).getText()).toBe('Item 4');
			 expect(rows.get(1).getText()).toBe('Item 3');
		   });
	
		   it('should add even class to even entries', function() {
			 var button = element(by.buttonText('Add item'));
			 var rows = element.all(by.repeater('item in items'));
	
			 expect(rows.get(0).getAttribute('class')).not.toMatch(/even/);
			 expect(rows.get(1).getAttribute('class')).toMatch(/even/);
	
			 button.click();
	
			 expect(rows.get(0).getAttribute('class')).not.toMatch(/even/);
			 expect(rows.get(1).getAttribute('class')).toMatch(/even/);
		   });
		 </file>
	   </example>
	 */
	var ngClassEvenDirective = classDirective('Even', 1);

	/**
	 * @ngdoc directive
	 * @name ngCloak
	 * @restrict AC
	 *
	 * @description
	 * The `ngCloak` directive is used to prevent the AngularJS html template from being briefly
	 * displayed by the browser in its raw (uncompiled) form while your application is loading. Use this
	 * directive to avoid the undesirable flicker effect caused by the html template display.
	 *
	 * The directive can be applied to the `<body>` element, but the preferred usage is to apply
	 * multiple `ngCloak` directives to small portions of the page to permit progressive rendering
	 * of the browser view.
	 *
	 * `ngCloak` works in cooperation with the following css rule embedded within `angular.js` and
	 * `angular.min.js`.
	 * For CSP mode please add `angular-csp.css` to your html file (see {@link ng.directive:ngCsp ngCsp}).
	 *
	 * ```css
	 * [ng\:cloak], [ng-cloak], [data-ng-cloak], [x-ng-cloak], .ng-cloak, .x-ng-cloak {
	 *   display: none !important;
	 * }
	 * ```
	 *
	 * When this css rule is loaded by the browser, all html elements (including their children) that
	 * are tagged with the `ngCloak` directive are hidden. When AngularJS encounters this directive
	 * during the compilation of the template it deletes the `ngCloak` element attribute, making
	 * the compiled element visible.
	 *
	 * For the best result, the `angular.js` script must be loaded in the head section of the html
	 * document; alternatively, the css rule above must be included in the external stylesheet of the
	 * application.
	 *
	 * @element ANY
	 *
	 * @example
	   <example name="ng-cloak">
		 <file name="index.html">
			<div id="template1" ng-cloak>{{ 'hello' }}</div>
			<div id="template2" class="ng-cloak">{{ 'world' }}</div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should remove the template directive and css class', function() {
			 expect($('#template1').getAttribute('ng-cloak')).
			   toBeNull();
			 expect($('#template2').getAttribute('ng-cloak')).
			   toBeNull();
		   });
		 </file>
	   </example>
	 *
	 */
	var ngCloakDirective = ngDirective({
		compile: function (element, attr) {
			attr.$set('ngCloak', undefined);
			element.removeClass('ng-cloak');
		}
	});

	/**
	 * @ngdoc directive
	 * @name ngController
	 *
	 * @description
	 * The `ngController` directive attaches a controller class to the view. This is a key aspect of how angular
	 * supports the principles behind the Model-View-Controller design pattern.
	 *
	 * MVC components in angular:
	 *
	 * * Model — Models are the properties of a scope; scopes are attached to the DOM where scope properties
	 *   are accessed through bindings.
	 * * View — The template (HTML with data bindings) that is rendered into the View.
	 * * Controller — The `ngController` directive specifies a Controller class; the class contains business
	 *   logic behind the application to decorate the scope with functions and values
	 *
	 * Note that you can also attach controllers to the DOM by declaring it in a route definition
	 * via the {@link ngRoute.$route $route} service. A common mistake is to declare the controller
	 * again using `ng-controller` in the template itself.  This will cause the controller to be attached
	 * and executed twice.
	 *
	 * @element ANY
	 * @scope
	 * @priority 500
	 * @param {expression} ngController Name of a constructor function registered with the current
	 * {@link ng.$controllerProvider $controllerProvider} or an {@link guide/expression expression}
	 * that on the current scope evaluates to a constructor function.
	 *
	 * The controller instance can be published into a scope property by specifying
	 * `ng-controller="as propertyName"`.
	 *
	 * @example
	 * Here is a simple form for editing user contact information. Adding, removing, clearing, and
	 * greeting are methods declared on the controller (see source tab). These methods can
	 * easily be called from the AngularJS markup. Any changes to the data are automatically reflected
	 * in the View without the need for a manual update.
	 *
	 * Two different declaration styles are included below:
	 *
	 * * one binds methods and properties directly onto the controller using `this`:
	 * `ng-controller="SettingsController1 as settings"`
	 * * one injects `$scope` into the controller:
	 * `ng-controller="SettingsController2"`
	 *
	 * The second option is more common in the AngularJS community, and is generally used in boilerplates
	 * and in this guide. However, there are advantages to binding properties directly to the controller
	 * and avoiding scope.
	 *
	 * * Using `controller as` makes it obvious which controller you are accessing in the template when
	 * multiple controllers apply to an element.
	 * * If you are writing your controllers as classes you have easier access to the properties and
	 * methods, which will appear on the scope, from inside the controller code.
	 * * Since there is always a `.` in the bindings, you don't have to worry about prototypal
	 * inheritance masking primitives.
	 *
	 * This example demonstrates the `controller as` syntax.
	 *
	 * <example name="ngControllerAs" module="controllerAsExample">
	 *   <file name="index.html">
	 *    <div id="ctrl-as-exmpl" ng-controller="SettingsController1 as settings">
	 *      <label>Name: <input type="text" ng-model="settings.name"/></label>
	 *      <button ng-click="settings.greet()">greet</button><br/>
	 *      Contact:
	 *      <ul>
	 *        <li ng-repeat="contact in settings.contacts">
	 *          <select ng-model="contact.type" aria-label="Contact method" id="select_{{$index}}">
	 *             <option>phone</option>
	 *             <option>email</option>
	 *          </select>
	 *          <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
	 *          <button ng-click="settings.clearContact(contact)">clear</button>
	 *          <button ng-click="settings.removeContact(contact)" aria-label="Remove">X</button>
	 *        </li>
	 *        <li><button ng-click="settings.addContact()">add</button></li>
	 *     </ul>
	 *    </div>
	 *   </file>
	 *   <file name="app.js">
	 *    angular.module('controllerAsExample', [])
	 *      .controller('SettingsController1', SettingsController1);
	 *
	 *    function SettingsController1() {
	 *      this.name = 'John Smith';
	 *      this.contacts = [
	 *        {type: 'phone', value: '408 555 1212'},
	 *        {type: 'email', value: 'john.smith@example.org'}
	 *      ];
	 *    }
	 *
	 *    SettingsController1.prototype.greet = function() {
	 *      alert(this.name);
	 *    };
	 *
	 *    SettingsController1.prototype.addContact = function() {
	 *      this.contacts.push({type: 'email', value: 'yourname@example.org'});
	 *    };
	 *
	 *    SettingsController1.prototype.removeContact = function(contactToRemove) {
	 *     var index = this.contacts.indexOf(contactToRemove);
	 *      this.contacts.splice(index, 1);
	 *    };
	 *
	 *    SettingsController1.prototype.clearContact = function(contact) {
	 *      contact.type = 'phone';
	 *      contact.value = '';
	 *    };
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     it('should check controller as', function() {
	 *       var container = element(by.id('ctrl-as-exmpl'));
	 *         expect(container.element(by.model('settings.name'))
	 *           .getAttribute('value')).toBe('John Smith');
	 *
	 *       var firstRepeat =
	 *           container.element(by.repeater('contact in settings.contacts').row(0));
	 *       var secondRepeat =
	 *           container.element(by.repeater('contact in settings.contacts').row(1));
	 *
	 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *           .toBe('408 555 1212');
	 *
	 *       expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *           .toBe('john.smith@example.org');
	 *
	 *       firstRepeat.element(by.buttonText('clear')).click();
	 *
	 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *           .toBe('');
	 *
	 *       container.element(by.buttonText('add')).click();
	 *
	 *       expect(container.element(by.repeater('contact in settings.contacts').row(2))
	 *           .element(by.model('contact.value'))
	 *           .getAttribute('value'))
	 *           .toBe('yourname@example.org');
	 *     });
	 *   </file>
	 * </example>
	 *
	 * This example demonstrates the "attach to `$scope`" style of controller.
	 *
	 * <example name="ngController" module="controllerExample">
	 *  <file name="index.html">
	 *   <div id="ctrl-exmpl" ng-controller="SettingsController2">
	 *     <label>Name: <input type="text" ng-model="name"/></label>
	 *     <button ng-click="greet()">greet</button><br/>
	 *     Contact:
	 *     <ul>
	 *       <li ng-repeat="contact in contacts">
	 *         <select ng-model="contact.type" id="select_{{$index}}">
	 *            <option>phone</option>
	 *            <option>email</option>
	 *         </select>
	 *         <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
	 *         <button ng-click="clearContact(contact)">clear</button>
	 *         <button ng-click="removeContact(contact)">X</button>
	 *       </li>
	 *       <li>[ <button ng-click="addContact()">add</button> ]</li>
	 *    </ul>
	 *   </div>
	 *  </file>
	 *  <file name="app.js">
	 *   angular.module('controllerExample', [])
	 *     .controller('SettingsController2', ['$scope', SettingsController2]);
	 *
	 *   function SettingsController2($scope) {
	 *     $scope.name = 'John Smith';
	 *     $scope.contacts = [
	 *       {type:'phone', value:'408 555 1212'},
	 *       {type:'email', value:'john.smith@example.org'}
	 *     ];
	 *
	 *     $scope.greet = function() {
	 *       alert($scope.name);
	 *     };
	 *
	 *     $scope.addContact = function() {
	 *       $scope.contacts.push({type:'email', value:'yourname@example.org'});
	 *     };
	 *
	 *     $scope.removeContact = function(contactToRemove) {
	 *       var index = $scope.contacts.indexOf(contactToRemove);
	 *       $scope.contacts.splice(index, 1);
	 *     };
	 *
	 *     $scope.clearContact = function(contact) {
	 *       contact.type = 'phone';
	 *       contact.value = '';
	 *     };
	 *   }
	 *  </file>
	 *  <file name="protractor.js" type="protractor">
	 *    it('should check controller', function() {
	 *      var container = element(by.id('ctrl-exmpl'));
	 *
	 *      expect(container.element(by.model('name'))
	 *          .getAttribute('value')).toBe('John Smith');
	 *
	 *      var firstRepeat =
	 *          container.element(by.repeater('contact in contacts').row(0));
	 *      var secondRepeat =
	 *          container.element(by.repeater('contact in contacts').row(1));
	 *
	 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *          .toBe('408 555 1212');
	 *      expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *          .toBe('john.smith@example.org');
	 *
	 *      firstRepeat.element(by.buttonText('clear')).click();
	 *
	 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
	 *          .toBe('');
	 *
	 *      container.element(by.buttonText('add')).click();
	 *
	 *      expect(container.element(by.repeater('contact in contacts').row(2))
	 *          .element(by.model('contact.value'))
	 *          .getAttribute('value'))
	 *          .toBe('yourname@example.org');
	 *    });
	 *  </file>
	 *</example>
	
	 */
	var ngControllerDirective = [function () {
		return {
			restrict: 'A',
			scope: true,
			controller: '@',
			priority: 500
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngCsp
	 *
	 * @restrict A
	 * @element ANY
	 * @description
	 *
	 * AngularJS has some features that can conflict with certain restrictions that are applied when using
	 * [CSP (Content Security Policy)](https://developer.mozilla.org/en/Security/CSP) rules.
	 *
	 * If you intend to implement CSP with these rules then you must tell AngularJS not to use these
	 * features.
	 *
	 * This is necessary when developing things like Google Chrome Extensions or Universal Windows Apps.
	 *
	 *
	 * The following default rules in CSP affect AngularJS:
	 *
	 * * The use of `eval()`, `Function(string)` and similar functions to dynamically create and execute
	 * code from strings is forbidden. AngularJS makes use of this in the {@link $parse} service to
	 * provide a 30% increase in the speed of evaluating AngularJS expressions. (This CSP rule can be
	 * disabled with the CSP keyword `unsafe-eval`, but it is generally not recommended as it would
	 * weaken the protections offered by CSP.)
	 *
	 * * The use of inline resources, such as inline `<script>` and `<style>` elements, are forbidden.
	 * This prevents apps from injecting custom styles directly into the document. AngularJS makes use of
	 * this to include some CSS rules (e.g. {@link ngCloak} and {@link ngHide}). To make these
	 * directives work when a CSP rule is blocking inline styles, you must link to the `angular-csp.css`
	 * in your HTML manually. (This CSP rule can be disabled with the CSP keyword `unsafe-inline`, but
	 * it is generally not recommended as it would weaken the protections offered by CSP.)
	 *
	 * If you do not provide `ngCsp` then AngularJS tries to autodetect if CSP is blocking dynamic code
	 * creation from strings (e.g., `unsafe-eval` not specified in CSP header) and automatically
	 * deactivates this feature in the {@link $parse} service. This autodetection, however, triggers a
	 * CSP error to be logged in the console:
	 *
	 * ```
	 * Refused to evaluate a string as JavaScript because 'unsafe-eval' is not an allowed source of
	 * script in the following Content Security Policy directive: "default-src 'self'". Note that
	 * 'script-src' was not explicitly set, so 'default-src' is used as a fallback.
	 * ```
	 *
	 * This error is harmless but annoying. To prevent the error from showing up, put the `ngCsp`
	 * directive on an element of the HTML document that appears before the `<script>` tag that loads
	 * the `angular.js` file.
	 *
	 * *Note: This directive is only available in the `ng-csp` and `data-ng-csp` attribute form.*
	 *
	 * You can specify which of the CSP related AngularJS features should be deactivated by providing
	 * a value for the `ng-csp` attribute. The options are as follows:
	 *
	 * * no-inline-style: this stops AngularJS from injecting CSS styles into the DOM
	 *
	 * * no-unsafe-eval: this stops AngularJS from optimizing $parse with unsafe eval of strings
	 *
	 * You can use these values in the following combinations:
	 *
	 *
	 * * No declaration means that AngularJS will assume that you can do inline styles, but it will do
	 * a runtime check for unsafe-eval. E.g. `<body>`. This is backwardly compatible with previous
	 * versions of AngularJS.
	 *
	 * * A simple `ng-csp` (or `data-ng-csp`) attribute will tell AngularJS to deactivate both inline
	 * styles and unsafe eval. E.g. `<body ng-csp>`. This is backwardly compatible with previous
	 * versions of AngularJS.
	 *
	 * * Specifying only `no-unsafe-eval` tells AngularJS that we must not use eval, but that we can
	 * inject inline styles. E.g. `<body ng-csp="no-unsafe-eval">`.
	 *
	 * * Specifying only `no-inline-style` tells AngularJS that we must not inject styles, but that we can
	 * run eval - no automatic check for unsafe eval will occur. E.g. `<body ng-csp="no-inline-style">`
	 *
	 * * Specifying both `no-unsafe-eval` and `no-inline-style` tells AngularJS that we must not inject
	 * styles nor use eval, which is the same as an empty: ng-csp.
	 * E.g.`<body ng-csp="no-inline-style;no-unsafe-eval">`
	 *
	 * @example
	 *
	 * This example shows how to apply the `ngCsp` directive to the `html` tag.
	   ```html
		 <!doctype html>
		 <html ng-app ng-csp>
		 ...
		 ...
		 </html>
	   ```
	
	  <!-- Note: the `.csp` suffix in the example name triggers CSP mode in our http server! -->
	  <example name="example.csp" module="cspExample" ng-csp="true">
		<file name="index.html">
		  <div ng-controller="MainController as ctrl">
			<div>
			  <button ng-click="ctrl.inc()" id="inc">Increment</button>
			  <span id="counter">
				{{ctrl.counter}}
			  </span>
			</div>
	
			<div>
			  <button ng-click="ctrl.evil()" id="evil">Evil</button>
			  <span id="evilError">
				{{ctrl.evilError}}
			  </span>
			</div>
		  </div>
		</file>
		<file name="script.js">
		   angular.module('cspExample', [])
			 .controller('MainController', function MainController() {
				this.counter = 0;
				this.inc = function() {
				  this.counter++;
				};
				this.evil = function() {
				  try {
					eval('1+2'); // eslint-disable-line no-eval
				  } catch (e) {
					this.evilError = e.message;
				  }
				};
			  });
		</file>
		<file name="protractor.js" type="protractor">
		  var util, webdriver;
	
		  var incBtn = element(by.id('inc'));
		  var counter = element(by.id('counter'));
		  var evilBtn = element(by.id('evil'));
		  var evilError = element(by.id('evilError'));
	
		  function getAndClearSevereErrors() {
			return browser.manage().logs().get('browser').then(function(browserLog) {
			  return browserLog.filter(function(logEntry) {
				return logEntry.level.value > webdriver.logging.Level.WARNING.value;
			  });
			});
		  }
	
		  function clearErrors() {
			getAndClearSevereErrors();
		  }
	
		  function expectNoErrors() {
			getAndClearSevereErrors().then(function(filteredLog) {
			  expect(filteredLog.length).toEqual(0);
			  if (filteredLog.length) {
				console.log('browser console errors: ' + util.inspect(filteredLog));
			  }
			});
		  }
	
		  function expectError(regex) {
			getAndClearSevereErrors().then(function(filteredLog) {
			  var found = false;
			  filteredLog.forEach(function(log) {
				if (log.message.match(regex)) {
				  found = true;
				}
			  });
			  if (!found) {
				throw new Error('expected an error that matches ' + regex);
			  }
			});
		  }
	
		  beforeEach(function() {
			util = require('util');
			webdriver = require('selenium-webdriver');
		  });
	
		  // For now, we only test on Chrome,
		  // as Safari does not load the page with Protractor's injected scripts,
		  // and Firefox webdriver always disables content security policy (#6358)
		  if (browser.params.browser !== 'chrome') {
			return;
		  }
	
		  it('should not report errors when the page is loaded', function() {
			// clear errors so we are not dependent on previous tests
			clearErrors();
			// Need to reload the page as the page is already loaded when
			// we come here
			browser.driver.getCurrentUrl().then(function(url) {
			  browser.get(url);
			});
			expectNoErrors();
		  });
	
		  it('should evaluate expressions', function() {
			expect(counter.getText()).toEqual('0');
			incBtn.click();
			expect(counter.getText()).toEqual('1');
			expectNoErrors();
		  });
	
		  it('should throw and report an error when using "eval"', function() {
			evilBtn.click();
			expect(evilError.getText()).toMatch(/Content Security Policy/);
			expectError(/Content Security Policy/);
		  });
		</file>
	  </example>
	  */

	// `ngCsp` is not implemented as a proper directive any more, because we need it be processed while
	// we bootstrap the app (before `$parse` is instantiated). For this reason, we just have the `csp()`
	// fn that looks for the `ng-csp` attribute anywhere in the current doc.

	/**
	 * @ngdoc directive
	 * @name ngClick
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * The ngClick directive allows you to specify custom behavior when
	 * an element is clicked.
	 *
	 * @param {expression} ngClick {@link guide/expression Expression} to evaluate upon
	 * click. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-click">
		 <file name="index.html">
		  <button ng-click="count = count + 1" ng-init="count=0">
			Increment
		  </button>
		  <span>
			count: {{count}}
		  </span>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-click', function() {
			 expect(element(by.binding('count')).getText()).toMatch('0');
			 element(by.css('button')).click();
			 expect(element(by.binding('count')).getText()).toMatch('1');
		   });
		 </file>
	   </example>
	 */
	/*
	 * A collection of directives that allows creation of custom event handlers that are defined as
	 * AngularJS expressions and are compiled and executed within the current scope.
	 */
	var ngEventDirectives = {};

	// For events that might fire synchronously during DOM manipulation
	// we need to execute their event handlers asynchronously using $evalAsync,
	// so that they are not executed in an inconsistent state.
	var forceAsyncEvents = {
		'blur': true,
		'focus': true
	};
	forEach(
		'click dblclick mousedown mouseup mouseover mouseout mousemove mouseenter mouseleave keydown keyup keypress submit focus blur copy cut paste'.split(' '),
		function (eventName) {
			var directiveName = directiveNormalize('ng-' + eventName);
			ngEventDirectives[directiveName] = ['$parse', '$rootScope', '$exceptionHandler', function ($parse, $rootScope, $exceptionHandler) {
				return createEventDirective($parse, $rootScope, $exceptionHandler, directiveName, eventName, forceAsyncEvents[eventName]);
			}];
		}
	);

	function createEventDirective($parse, $rootScope, $exceptionHandler, directiveName, eventName, forceAsync) {
		return {
			restrict: 'A',
			compile: function ($element, attr) {
				// NOTE:
				// We expose the powerful `$event` object on the scope that provides access to the Window,
				// etc. This is OK, because expressions are not sandboxed any more (and the expression
				// sandbox was never meant to be a security feature anyway).
				var fn = $parse(attr[directiveName]);
				return function ngEventHandler(scope, element) {
					element.on(eventName, function (event) {
						var callback = function () {
							fn(scope, { $event: event });
						};

						if (!$rootScope.$$phase) {
							scope.$apply(callback);
						} else if (forceAsync) {
							scope.$evalAsync(callback);
						} else {
							try {
								callback();
							} catch (error) {
								$exceptionHandler(error);
							}
						}
					});
				};
			}
		};
	}

	/**
	 * @ngdoc directive
	 * @name ngDblclick
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * The `ngDblclick` directive allows you to specify custom behavior on a dblclick event.
	 *
	 * @param {expression} ngDblclick {@link guide/expression Expression} to evaluate upon
	 * a dblclick. (The Event object is available as `$event`)
	 *
	 * @example
	   <example name="ng-dblclick">
		 <file name="index.html">
		  <button ng-dblclick="count = count + 1" ng-init="count=0">
			Increment (on double click)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngMousedown
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * The ngMousedown directive allows you to specify custom behavior on mousedown event.
	 *
	 * @param {expression} ngMousedown {@link guide/expression Expression} to evaluate upon
	 * mousedown. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mousedown">
		 <file name="index.html">
		  <button ng-mousedown="count = count + 1" ng-init="count=0">
			Increment (on mouse down)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngMouseup
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on mouseup event.
	 *
	 * @param {expression} ngMouseup {@link guide/expression Expression} to evaluate upon
	 * mouseup. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mouseup">
		 <file name="index.html">
		  <button ng-mouseup="count = count + 1" ng-init="count=0">
			Increment (on mouse up)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngMouseover
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on mouseover event.
	 *
	 * @param {expression} ngMouseover {@link guide/expression Expression} to evaluate upon
	 * mouseover. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mouseover">
		 <file name="index.html">
		  <button ng-mouseover="count = count + 1" ng-init="count=0">
			Increment (when mouse is over)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngMouseenter
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on mouseenter event.
	 *
	 * @param {expression} ngMouseenter {@link guide/expression Expression} to evaluate upon
	 * mouseenter. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mouseenter">
		 <file name="index.html">
		  <button ng-mouseenter="count = count + 1" ng-init="count=0">
			Increment (when mouse enters)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngMouseleave
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on mouseleave event.
	 *
	 * @param {expression} ngMouseleave {@link guide/expression Expression} to evaluate upon
	 * mouseleave. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mouseleave">
		 <file name="index.html">
		  <button ng-mouseleave="count = count + 1" ng-init="count=0">
			Increment (when mouse leaves)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngMousemove
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on mousemove event.
	 *
	 * @param {expression} ngMousemove {@link guide/expression Expression} to evaluate upon
	 * mousemove. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-mousemove">
		 <file name="index.html">
		  <button ng-mousemove="count = count + 1" ng-init="count=0">
			Increment (when mouse moves)
		  </button>
		  count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngKeydown
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on keydown event.
	 *
	 * @param {expression} ngKeydown {@link guide/expression Expression} to evaluate upon
	 * keydown. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
	 *
	 * @example
	   <example name="ng-keydown">
		 <file name="index.html">
		  <input ng-keydown="count = count + 1" ng-init="count=0">
		  key down count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngKeyup
	 * @restrict A
	 * @element ANY
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on keyup event.
	 *
	 * @param {expression} ngKeyup {@link guide/expression Expression} to evaluate upon
	 * keyup. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
	 *
	 * @example
	   <example name="ng-keyup">
		 <file name="index.html">
		   <p>Typing in the input box below updates the key count</p>
		   <input ng-keyup="count = count + 1" ng-init="count=0"> key up count: {{count}}
	
		   <p>Typing in the input box below updates the keycode</p>
		   <input ng-keyup="event=$event">
		   <p>event keyCode: {{ event.keyCode }}</p>
		   <p>event altKey: {{ event.altKey }}</p>
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngKeypress
	 * @restrict A
	 * @element ANY
	 *
	 * @description
	 * Specify custom behavior on keypress event.
	 *
	 * @param {expression} ngKeypress {@link guide/expression Expression} to evaluate upon
	 * keypress. ({@link guide/expression#-event- Event object is available as `$event`}
	 * and can be interrogated for keyCode, altKey, etc.)
	 *
	 * @example
	   <example name="ng-keypress">
		 <file name="index.html">
		  <input ng-keypress="count = count + 1" ng-init="count=0">
		  key press count: {{count}}
		 </file>
	   </example>
	 */


	/**
	 * @ngdoc directive
	 * @name ngSubmit
	 * @restrict A
	 * @element form
	 * @priority 0
	 *
	 * @description
	 * Enables binding AngularJS expressions to onsubmit events.
	 *
	 * Additionally it prevents the default action (which for form means sending the request to the
	 * server and reloading the current page), but only if the form does not contain `action`,
	 * `data-action`, or `x-action` attributes.
	 *
	 * <div class="alert alert-warning">
	 * **Warning:** Be careful not to cause "double-submission" by using both the `ngClick` and
	 * `ngSubmit` handlers together. See the
	 * {@link form#submitting-a-form-and-preventing-the-default-action `form` directive documentation}
	 * for a detailed discussion of when `ngSubmit` may be triggered.
	 * </div>
	 *
	 * @param {expression} ngSubmit {@link guide/expression Expression} to eval.
	 * ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example module="submitExample" name="ng-submit">
		 <file name="index.html">
		  <script>
			angular.module('submitExample', [])
			  .controller('ExampleController', ['$scope', function($scope) {
				$scope.list = [];
				$scope.text = 'hello';
				$scope.submit = function() {
				  if ($scope.text) {
					$scope.list.push(this.text);
					$scope.text = '';
				  }
				};
			  }]);
		  </script>
		  <form ng-submit="submit()" ng-controller="ExampleController">
			Enter text and hit enter:
			<input type="text" ng-model="text" name="text" />
			<input type="submit" id="submit" value="Submit" />
			<pre>list={{list}}</pre>
		  </form>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should check ng-submit', function() {
			 expect(element(by.binding('list')).getText()).toBe('list=[]');
			 element(by.css('#submit')).click();
			 expect(element(by.binding('list')).getText()).toContain('hello');
			 expect(element(by.model('text')).getAttribute('value')).toBe('');
		   });
		   it('should ignore empty strings', function() {
			 expect(element(by.binding('list')).getText()).toBe('list=[]');
			 element(by.css('#submit')).click();
			 element(by.css('#submit')).click();
			 expect(element(by.binding('list')).getText()).toContain('hello');
			});
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngFocus
	 * @restrict A
	 * @element window, input, select, textarea, a
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on focus event.
	 *
	 * Note: As the `focus` event is executed synchronously when calling `input.focus()`
	 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
	 * during an `$apply` to ensure a consistent state.
	 *
	 * @param {expression} ngFocus {@link guide/expression Expression} to evaluate upon
	 * focus. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	 * See {@link ng.directive:ngClick ngClick}
	 */

	/**
	 * @ngdoc directive
	 * @name ngBlur
	 * @restrict A
	 * @element window, input, select, textarea, a
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on blur event.
	 *
	 * A [blur event](https://developer.mozilla.org/en-US/docs/Web/Events/blur) fires when
	 * an element has lost focus.
	 *
	 * Note: As the `blur` event is executed synchronously also during DOM manipulations
	 * (e.g. removing a focussed input),
	 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
	 * during an `$apply` to ensure a consistent state.
	 *
	 * @param {expression} ngBlur {@link guide/expression Expression} to evaluate upon
	 * blur. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	 * See {@link ng.directive:ngClick ngClick}
	 */

	/**
	 * @ngdoc directive
	 * @name ngCopy
	 * @restrict A
	 * @element window, input, select, textarea, a
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on copy event.
	 *
	 * @param {expression} ngCopy {@link guide/expression Expression} to evaluate upon
	 * copy. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-copy">
		 <file name="index.html">
		  <input ng-copy="copied=true" ng-init="copied=false; value='copy me'" ng-model="value">
		  copied: {{copied}}
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngCut
	 * @restrict A
	 * @element window, input, select, textarea, a
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on cut event.
	 *
	 * @param {expression} ngCut {@link guide/expression Expression} to evaluate upon
	 * cut. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-cut">
		 <file name="index.html">
		  <input ng-cut="cut=true" ng-init="cut=false; value='cut me'" ng-model="value">
		  cut: {{cut}}
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngPaste
	 * @restrict A
	 * @element window, input, select, textarea, a
	 * @priority 0
	 *
	 * @description
	 * Specify custom behavior on paste event.
	 *
	 * @param {expression} ngPaste {@link guide/expression Expression} to evaluate upon
	 * paste. ({@link guide/expression#-event- Event object is available as `$event`})
	 *
	 * @example
	   <example name="ng-paste">
		 <file name="index.html">
		  <input ng-paste="paste=true" ng-init="paste=false" placeholder='paste here'>
		  pasted: {{paste}}
		 </file>
	   </example>
	 */

	/**
	 * @ngdoc directive
	 * @name ngIf
	 * @restrict A
	 * @multiElement
	 *
	 * @description
	 * The `ngIf` directive removes or recreates a portion of the DOM tree based on an
	 * {expression}. If the expression assigned to `ngIf` evaluates to a false
	 * value then the element is removed from the DOM, otherwise a clone of the
	 * element is reinserted into the DOM.
	 *
	 * `ngIf` differs from `ngShow` and `ngHide` in that `ngIf` completely removes and recreates the
	 * element in the DOM rather than changing its visibility via the `display` css property.  A common
	 * case when this difference is significant is when using css selectors that rely on an element's
	 * position within the DOM, such as the `:first-child` or `:last-child` pseudo-classes.
	 *
	 * Note that when an element is removed using `ngIf` its scope is destroyed and a new scope
	 * is created when the element is restored.  The scope created within `ngIf` inherits from
	 * its parent scope using
	 * [prototypal inheritance](https://github.com/angular/angular.js/wiki/Understanding-Scopes#javascript-prototypal-inheritance).
	 * An important implication of this is if `ngModel` is used within `ngIf` to bind to
	 * a javascript primitive defined in the parent scope. In this case any modifications made to the
	 * variable within the child scope will override (hide) the value in the parent scope.
	 *
	 * Also, `ngIf` recreates elements using their compiled state. An example of this behavior
	 * is if an element's class attribute is directly modified after it's compiled, using something like
	 * jQuery's `.addClass()` method, and the element is later removed. When `ngIf` recreates the element
	 * the added class will be lost because the original compiled state is used to regenerate the element.
	 *
	 * Additionally, you can provide animations via the `ngAnimate` module to animate the `enter`
	 * and `leave` effects.
	 *
	 * @animations
	 * | Animation                        | Occurs                               |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#enter enter}  | just after the `ngIf` contents change and a new DOM element is created and injected into the `ngIf` container |
	 * | {@link ng.$animate#leave leave}  | just before the `ngIf` contents are removed from the DOM |
	 *
	 * @element ANY
	 * @scope
	 * @priority 600
	 * @param {expression} ngIf If the {@link guide/expression expression} is falsy then
	 *     the element is removed from the DOM tree. If it is truthy a copy of the compiled
	 *     element is added to the DOM tree.
	 *
	 * @example
	  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-if">
		<file name="index.html">
		  <label>Click me: <input type="checkbox" ng-model="checked" ng-init="checked=true" /></label><br/>
		  Show when checked:
		  <span ng-if="checked" class="animate-if">
			This is removed when the checkbox is unchecked.
		  </span>
		</file>
		<file name="animations.css">
		  .animate-if {
			background:white;
			border:1px solid black;
			padding:10px;
		  }
	
		  .animate-if.ng-enter, .animate-if.ng-leave {
			transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
		  }
	
		  .animate-if.ng-enter,
		  .animate-if.ng-leave.ng-leave-active {
			opacity:0;
		  }
	
		  .animate-if.ng-leave,
		  .animate-if.ng-enter.ng-enter-active {
			opacity:1;
		  }
		</file>
	  </example>
	 */
	var ngIfDirective = ['$animate', '$compile', function ($animate, $compile) {
		return {
			multiElement: true,
			transclude: 'element',
			priority: 600,
			terminal: true,
			restrict: 'A',
			$$tlb: true,
			link: function ($scope, $element, $attr, ctrl, $transclude) {
				var block, childScope, previousElements;
				$scope.$watch($attr.ngIf, function ngIfWatchAction(value) {

					if (value) {
						if (!childScope) {
							$transclude(function (clone, newScope) {
								childScope = newScope;
								clone[clone.length++] = $compile.$$createComment('end ngIf', $attr.ngIf);
								// Note: We only need the first/last node of the cloned nodes.
								// However, we need to keep the reference to the jqlite wrapper as it might be changed later
								// by a directive with templateUrl when its template arrives.
								block = {
									clone: clone
								};
								$animate.enter(clone, $element.parent(), $element);
							});
						}
					} else {
						if (previousElements) {
							previousElements.remove();
							previousElements = null;
						}
						if (childScope) {
							childScope.$destroy();
							childScope = null;
						}
						if (block) {
							previousElements = getBlockNodes(block.clone);
							$animate.leave(previousElements).done(function (response) {
								if (response !== false) previousElements = null;
							});
							block = null;
						}
					}
				});
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngInclude
	 * @restrict ECA
	 * @scope
	 * @priority -400
	 *
	 * @description
	 * Fetches, compiles and includes an external HTML fragment.
	 *
	 * By default, the template URL is restricted to the same domain and protocol as the
	 * application document. This is done by calling {@link $sce#getTrustedResourceUrl
	 * $sce.getTrustedResourceUrl} on it. To load templates from other domains or protocols
	 * you may either add them to your {@link ng.$sceDelegateProvider#trustedResourceUrlList trusted
	 * resource URL list} or {@link $sce#trustAsResourceUrl wrap them} as trusted values. Refer to
	 * AngularJS's {@link ng.$sce Strict Contextual Escaping}.
	 *
	 * In addition, the browser's
	 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
	 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
	 * policy may further restrict whether the template is successfully loaded.
	 * For example, `ngInclude` won't work for cross-domain requests on all browsers and for `file://`
	 * access on some browsers.
	 *
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#enter enter}  | when the expression changes, on the new include |
	 * | {@link ng.$animate#leave leave}  | when the expression changes, on the old include |
	 *
	 * The enter and leave animation occur concurrently.
	 *
	 * @param {string} ngInclude|src AngularJS expression evaluating to URL. If the source is a string constant,
	 *                 make sure you wrap it in **single** quotes, e.g. `src="'myPartialTemplate.html'"`.
	 * @param {string=} onload Expression to evaluate when a new partial is loaded.
	 *                  <div class="alert alert-warning">
	 *                  **Note:** When using onload on SVG elements in IE11, the browser will try to call
	 *                  a function with the name on the window element, which will usually throw a
	 *                  "function is undefined" error. To fix this, you can instead use `data-onload` or a
	 *                  different form that {@link guide/directive#normalization matches} `onload`.
	 *                  </div>
	   *
	 * @param {string=} autoscroll Whether `ngInclude` should call {@link ng.$anchorScroll
	 *                  $anchorScroll} to scroll the viewport after the content is loaded.
	 *
	 *                  - If the attribute is not set, disable scrolling.
	 *                  - If the attribute is set without value, enable scrolling.
	 *                  - Otherwise enable scrolling only if the expression evaluates to truthy value.
	 *
	 * @example
	  <example module="includeExample" deps="angular-animate.js" animations="true" name="ng-include">
		<file name="index.html">
		 <div ng-controller="ExampleController">
		   <select ng-model="template" ng-options="t.name for t in templates">
			<option value="">(blank)</option>
		   </select>
		   url of the template: <code>{{template.url}}</code>
		   <hr/>
		   <div class="slide-animate-container">
			 <div class="slide-animate" ng-include="template.url"></div>
		   </div>
		 </div>
		</file>
		<file name="script.js">
		  angular.module('includeExample', ['ngAnimate'])
			.controller('ExampleController', ['$scope', function($scope) {
			  $scope.templates =
				[{ name: 'template1.html', url: 'template1.html'},
				 { name: 'template2.html', url: 'template2.html'}];
			  $scope.template = $scope.templates[0];
			}]);
		 </file>
		<file name="template1.html">
		  Content of template1.html
		</file>
		<file name="template2.html">
		  Content of template2.html
		</file>
		<file name="animations.css">
		  .slide-animate-container {
			position:relative;
			background:white;
			border:1px solid black;
			height:40px;
			overflow:hidden;
		  }
	
		  .slide-animate {
			padding:10px;
		  }
	
		  .slide-animate.ng-enter, .slide-animate.ng-leave {
			transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
	
			position:absolute;
			top:0;
			left:0;
			right:0;
			bottom:0;
			display:block;
			padding:10px;
		  }
	
		  .slide-animate.ng-enter {
			top:-50px;
		  }
		  .slide-animate.ng-enter.ng-enter-active {
			top:0;
		  }
	
		  .slide-animate.ng-leave {
			top:0;
		  }
		  .slide-animate.ng-leave.ng-leave-active {
			top:50px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  var templateSelect = element(by.model('template'));
		  var includeElem = element(by.css('[ng-include]'));
	
		  it('should load template1.html', function() {
			expect(includeElem.getText()).toMatch(/Content of template1.html/);
		  });
	
		  it('should load template2.html', function() {
			if (browser.params.browser === 'firefox') {
			  // Firefox can't handle using selects
			  // See https://github.com/angular/protractor/issues/480
			  return;
			}
			templateSelect.click();
			templateSelect.all(by.css('option')).get(2).click();
			expect(includeElem.getText()).toMatch(/Content of template2.html/);
		  });
	
		  it('should change to blank', function() {
			if (browser.params.browser === 'firefox') {
			  // Firefox can't handle using selects
			  return;
			}
			templateSelect.click();
			templateSelect.all(by.css('option')).get(0).click();
			expect(includeElem.isPresent()).toBe(false);
		  });
		</file>
	  </example>
	 */


	/**
	 * @ngdoc event
	 * @name ngInclude#$includeContentRequested
	 * @eventType emit on the scope ngInclude was declared in
	 * @description
	 * Emitted every time the ngInclude content is requested.
	 *
	 * @param {Object} angularEvent Synthetic event object.
	 * @param {String} src URL of content to load.
	 */


	/**
	 * @ngdoc event
	 * @name ngInclude#$includeContentLoaded
	 * @eventType emit on the current ngInclude scope
	 * @description
	 * Emitted every time the ngInclude content is reloaded.
	 *
	 * @param {Object} angularEvent Synthetic event object.
	 * @param {String} src URL of content to load.
	 */


	/**
	 * @ngdoc event
	 * @name ngInclude#$includeContentError
	 * @eventType emit on the scope ngInclude was declared in
	 * @description
	 * Emitted when a template HTTP request yields an erroneous response (status < 200 || status > 299)
	 *
	 * @param {Object} angularEvent Synthetic event object.
	 * @param {String} src URL of content to load.
	 */
	var ngIncludeDirective = ['$templateRequest', '$anchorScroll', '$animate',
		function ($templateRequest, $anchorScroll, $animate) {
			return {
				restrict: 'ECA',
				priority: 400,
				terminal: true,
				transclude: 'element',
				controller: angular.noop,
				compile: function (element, attr) {
					var srcExp = attr.ngInclude || attr.src,
						onloadExp = attr.onload || '',
						autoScrollExp = attr.autoscroll;

					return function (scope, $element, $attr, ctrl, $transclude) {
						var changeCounter = 0,
							currentScope,
							previousElement,
							currentElement;

						var cleanupLastIncludeContent = function () {
							if (previousElement) {
								previousElement.remove();
								previousElement = null;
							}
							if (currentScope) {
								currentScope.$destroy();
								currentScope = null;
							}
							if (currentElement) {
								$animate.leave(currentElement).done(function (response) {
									if (response !== false) previousElement = null;
								});
								previousElement = currentElement;
								currentElement = null;
							}
						};

						scope.$watch(srcExp, function ngIncludeWatchAction(src) {
							var afterAnimation = function (response) {
								if (response !== false && isDefined(autoScrollExp) &&
									(!autoScrollExp || scope.$eval(autoScrollExp))) {
									$anchorScroll();
								}
							};
							var thisChangeId = ++changeCounter;

							if (src) {
								//set the 2nd param to true to ignore the template request error so that the inner
								//contents and scope can be cleaned up.
								$templateRequest(src, true).then(function (response) {
									if (scope.$$destroyed) return;

									if (thisChangeId !== changeCounter) return;
									var newScope = scope.$new();
									ctrl.template = response;

									// Note: This will also link all children of ng-include that were contained in the original
									// html. If that content contains controllers, ... they could pollute/change the scope.
									// However, using ng-include on an element with additional content does not make sense...
									// Note: We can't remove them in the cloneAttchFn of $transclude as that
									// function is called before linking the content, which would apply child
									// directives to non existing elements.
									var clone = $transclude(newScope, function (clone) {
										cleanupLastIncludeContent();
										$animate.enter(clone, null, $element).done(afterAnimation);
									});

									currentScope = newScope;
									currentElement = clone;

									currentScope.$emit('$includeContentLoaded', src);
									scope.$eval(onloadExp);
								}, function () {
									if (scope.$$destroyed) return;

									if (thisChangeId === changeCounter) {
										cleanupLastIncludeContent();
										scope.$emit('$includeContentError', src);
									}
								});
								scope.$emit('$includeContentRequested', src);
							} else {
								cleanupLastIncludeContent();
								ctrl.template = null;
							}
						});
					};
				}
			};
		}];

	// This directive is called during the $transclude call of the first `ngInclude` directive.
	// It will replace and compile the content of the element with the loaded template.
	// We need this directive so that the element content is already filled when
	// the link function of another directive on the same element as ngInclude
	// is called.
	var ngIncludeFillContentDirective = ['$compile',
		function ($compile) {
			return {
				restrict: 'ECA',
				priority: -400,
				require: 'ngInclude',
				link: function (scope, $element, $attr, ctrl) {
					if (toString.call($element[0]).match(/SVG/)) {
						// WebKit: https://bugs.webkit.org/show_bug.cgi?id=135698 --- SVG elements do not
						// support innerHTML, so detect this here and try to generate the contents
						// specially.
						$element.empty();
						$compile(jqLiteBuildFragment(ctrl.template, window.document).childNodes)(scope,
							function namespaceAdaptedClone(clone) {
								$element.append(clone);
							}, { futureParentElement: $element });
						return;
					}

					$element.html(ctrl.template);
					$compile($element.contents())(scope);
				}
			};
		}];

	/**
	 * @ngdoc directive
	 * @name ngInit
	 * @restrict AC
	 * @priority 450
	 * @element ANY
	 *
	 * @param {expression} ngInit {@link guide/expression Expression} to eval.
	 *
	 * @description
	 * The `ngInit` directive allows you to evaluate an expression in the
	 * current scope.
	 *
	 * <div class="alert alert-danger">
	 * This directive can be abused to add unnecessary amounts of logic into your templates.
	 * There are only a few appropriate uses of `ngInit`:
	 * <ul>
	 *   <li>aliasing special properties of {@link ng.directive:ngRepeat `ngRepeat`},
	 *     as seen in the demo below.</li>
	 *   <li>initializing data during development, or for examples, as seen throughout these docs.</li>
	 *   <li>injecting data via server side scripting.</li>
	 * </ul>
	 *
	 * Besides these few cases, you should use {@link guide/component Components} or
	 * {@link guide/controller Controllers} rather than `ngInit` to initialize values on a scope.
	 * </div>
	 *
	 * <div class="alert alert-warning">
	 * **Note**: If you have assignment in `ngInit` along with a {@link ng.$filter `filter`}, make
	 * sure you have parentheses to ensure correct operator precedence:
	 * <pre class="prettyprint">
	 * `<div ng-init="test1 = ($index | toString)"></div>`
	 * </pre>
	 * </div>
	 *
	 * @example
	   <example module="initExample" name="ng-init">
		 <file name="index.html">
	   <script>
		 angular.module('initExample', [])
		   .controller('ExampleController', ['$scope', function($scope) {
			 $scope.list = [['a', 'b'], ['c', 'd']];
		   }]);
	   </script>
	   <div ng-controller="ExampleController">
		 <div ng-repeat="innerList in list" ng-init="outerIndex = $index">
		   <div ng-repeat="value in innerList" ng-init="innerIndex = $index">
			  <span class="example-init">list[ {{outerIndex}} ][ {{innerIndex}} ] = {{value}};</span>
		   </div>
		 </div>
	   </div>
		 </file>
		 <file name="protractor.js" type="protractor">
		   it('should alias index positions', function() {
			 var elements = element.all(by.css('.example-init'));
			 expect(elements.get(0).getText()).toBe('list[ 0 ][ 0 ] = a;');
			 expect(elements.get(1).getText()).toBe('list[ 0 ][ 1 ] = b;');
			 expect(elements.get(2).getText()).toBe('list[ 1 ][ 0 ] = c;');
			 expect(elements.get(3).getText()).toBe('list[ 1 ][ 1 ] = d;');
		   });
		 </file>
	   </example>
	 */
	var ngInitDirective = ngDirective({
		priority: 450,
		compile: function () {
			return {
				pre: function (scope, element, attrs) {
					scope.$eval(attrs.ngInit);
				}
			};
		}
	});

	/**
	 * @ngdoc directive
	 * @name ngList
	 * @restrict A
	 * @priority 100
	 *
	 * @param {string=} ngList optional delimiter that should be used to split the value.
	 *
	 * @description
	 * Text input that converts between a delimited string and an array of strings. The default
	 * delimiter is a comma followed by a space - equivalent to `ng-list=", "`. You can specify a custom
	 * delimiter as the value of the `ngList` attribute - for example, `ng-list=" | "`.
	 *
	 * The behaviour of the directive is affected by the use of the `ngTrim` attribute.
	 * * If `ngTrim` is set to `"false"` then whitespace around both the separator and each
	 *   list item is respected. This implies that the user of the directive is responsible for
	 *   dealing with whitespace but also allows you to use whitespace as a delimiter, such as a
	 *   tab or newline character.
	 * * Otherwise whitespace around the delimiter is ignored when splitting (although it is respected
	 *   when joining the list items back together) and whitespace around each list item is stripped
	 *   before it is added to the model.
	 *
	 * @example
	 * ### Validation
	 *
	 * <example name="ngList-directive" module="listExample">
	 *   <file name="app.js">
	 *      angular.module('listExample', [])
	 *        .controller('ExampleController', ['$scope', function($scope) {
	 *          $scope.names = ['morpheus', 'neo', 'trinity'];
	 *        }]);
	 *   </file>
	 *   <file name="index.html">
	 *    <form name="myForm" ng-controller="ExampleController">
	 *      <label>List: <input name="namesInput" ng-model="names" ng-list required></label>
	 *      <span role="alert">
	 *        <span class="error" ng-show="myForm.namesInput.$error.required">
	 *        Required!</span>
	 *      </span>
	 *      <br>
	 *      <tt>names = {{names}}</tt><br/>
	 *      <tt>myForm.namesInput.$valid = {{myForm.namesInput.$valid}}</tt><br/>
	 *      <tt>myForm.namesInput.$error = {{myForm.namesInput.$error}}</tt><br/>
	 *      <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
	 *      <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
	 *     </form>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     var listInput = element(by.model('names'));
	 *     var names = element(by.exactBinding('names'));
	 *     var valid = element(by.binding('myForm.namesInput.$valid'));
	 *     var error = element(by.css('span.error'));
	 *
	 *     it('should initialize to model', function() {
	 *       expect(names.getText()).toContain('["morpheus","neo","trinity"]');
	 *       expect(valid.getText()).toContain('true');
	 *       expect(error.getCssValue('display')).toBe('none');
	 *     });
	 *
	 *     it('should be invalid if empty', function() {
	 *       listInput.clear();
	 *       listInput.sendKeys('');
	 *
	 *       expect(names.getText()).toContain('');
	 *       expect(valid.getText()).toContain('false');
	 *       expect(error.getCssValue('display')).not.toBe('none');
	 *     });
	 *   </file>
	 * </example>
	 *
	 * @example
	 * ### Splitting on newline
	 *
	 * <example name="ngList-directive-newlines">
	 *   <file name="index.html">
	 *    <textarea ng-model="list" ng-list="&#10;" ng-trim="false"></textarea>
	 *    <pre>{{ list | json }}</pre>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     it("should split the text by newlines", function() {
	 *       var listInput = element(by.model('list'));
	 *       var output = element(by.binding('list | json'));
	 *       listInput.sendKeys('abc\ndef\nghi');
	 *       expect(output.getText()).toContain('[\n  "abc",\n  "def",\n  "ghi"\n]');
	 *     });
	 *   </file>
	 * </example>
	 *
	 */
	var ngListDirective = function () {
		return {
			restrict: 'A',
			priority: 100,
			require: 'ngModel',
			link: function (scope, element, attr, ctrl) {
				var ngList = attr.ngList || ', ';
				var trimValues = attr.ngTrim !== 'false';
				var separator = trimValues ? trim(ngList) : ngList;

				var parse = function (viewValue) {
					// If the viewValue is invalid (say required but empty) it will be `undefined`
					if (isUndefined(viewValue)) return;

					var list = [];

					if (viewValue) {
						forEach(viewValue.split(separator), function (value) {
							if (value) list.push(trimValues ? trim(value) : value);
						});
					}

					return list;
				};

				ctrl.$parsers.push(parse);
				ctrl.$formatters.push(function (value) {
					if (isArray(value)) {
						return value.join(ngList);
					}

					return undefined;
				});

				// Override the standard $isEmpty because an empty array means the input is empty.
				ctrl.$isEmpty = function (value) {
					return !value || !value.length;
				};
			}
		};
	};

	/* global VALID_CLASS: true,
	  INVALID_CLASS: true,
	  PRISTINE_CLASS: true,
	  DIRTY_CLASS: true,
	  UNTOUCHED_CLASS: true,
	  TOUCHED_CLASS: true,
	  PENDING_CLASS: true,
	  addSetValidityMethod: true,
	  setupValidity: true,
	  defaultModelOptions: false
	*/


	var VALID_CLASS = 'ng-valid',
		INVALID_CLASS = 'ng-invalid',
		PRISTINE_CLASS = 'ng-pristine',
		DIRTY_CLASS = 'ng-dirty',
		UNTOUCHED_CLASS = 'ng-untouched',
		TOUCHED_CLASS = 'ng-touched',
		EMPTY_CLASS = 'ng-empty',
		NOT_EMPTY_CLASS = 'ng-not-empty';

	var ngModelMinErr = minErr('ngModel');

	/**
	 * @ngdoc type
	 * @name ngModel.NgModelController
	 * @property {*} $viewValue The actual value from the control's view. For `input` elements, this is a
	 * String. See {@link ngModel.NgModelController#$setViewValue} for information about when the $viewValue
	 * is set.
	 *
	 * @property {*} $modelValue The value in the model that the control is bound to.
	 *
	 * @property {Array.<Function>} $parsers Array of functions to execute, as a pipeline, whenever
	 *  the control updates the ngModelController with a new {@link ngModel.NgModelController#$viewValue
		`$viewValue`} from the DOM, usually via user input.
		See {@link ngModel.NgModelController#$setViewValue `$setViewValue()`} for a detailed lifecycle explanation.
		Note that the `$parsers` are not called when the bound ngModel expression changes programmatically.
	
	  The functions are called in array order, each passing
		its return value through to the next. The last return value is forwarded to the
		{@link ngModel.NgModelController#$validators `$validators`} collection.
	
	  Parsers are used to sanitize / convert the {@link ngModel.NgModelController#$viewValue
		`$viewValue`}.
	
	  Returning `undefined` from a parser means a parse error occurred. In that case,
		no {@link ngModel.NgModelController#$validators `$validators`} will run and the `ngModel`
		will be set to `undefined` unless {@link ngModelOptions `ngModelOptions.allowInvalid`}
		is set to `true`. The parse error is stored in `ngModel.$error.parse`.
	
	  This simple example shows a parser that would convert text input value to lowercase:
	 * ```js
	 * function parse(value) {
	 *   if (value) {
	 *     return value.toLowerCase();
	 *   }
	 * }
	 * ngModelController.$parsers.push(parse);
	 * ```
	
	 *
	 * @property {Array.<Function>} $formatters Array of functions to execute, as a pipeline, whenever
		the bound ngModel expression changes programmatically. The `$formatters` are not called when the
		value of the control is changed by user interaction.
	
	  Formatters are used to format / convert the {@link ngModel.NgModelController#$modelValue
		`$modelValue`} for display in the control.
	
	  The functions are called in reverse array order, each passing the value through to the
		next. The last return value is used as the actual DOM value.
	
	  This simple example shows a formatter that would convert the model value to uppercase:
	
	 * ```js
	 * function format(value) {
	 *   if (value) {
	 *     return value.toUpperCase();
	 *   }
	 * }
	 * ngModel.$formatters.push(format);
	 * ```
	 *
	 * @property {Object.<string, function>} $validators A collection of validators that are applied
	 *      whenever the model value changes. The key value within the object refers to the name of the
	 *      validator while the function refers to the validation operation. The validation operation is
	 *      provided with the model value as an argument and must return a true or false value depending
	 *      on the response of that validation.
	 *
	 * ```js
	 * ngModel.$validators.validCharacters = function(modelValue, viewValue) {
	 *   var value = modelValue || viewValue;
	 *   return /[0-9]+/.test(value) &&
	 *          /[a-z]+/.test(value) &&
	 *          /[A-Z]+/.test(value) &&
	 *          /\W+/.test(value);
	 * };
	 * ```
	 *
	 * @property {Object.<string, function>} $asyncValidators A collection of validations that are expected to
	 *      perform an asynchronous validation (e.g. a HTTP request). The validation function that is provided
	 *      is expected to return a promise when it is run during the model validation process. Once the promise
	 *      is delivered then the validation status will be set to true when fulfilled and false when rejected.
	 *      When the asynchronous validators are triggered, each of the validators will run in parallel and the model
	 *      value will only be updated once all validators have been fulfilled. As long as an asynchronous validator
	 *      is unfulfilled, its key will be added to the controllers `$pending` property. Also, all asynchronous validators
	 *      will only run once all synchronous validators have passed.
	 *
	 * Please note that if $http is used then it is important that the server returns a success HTTP response code
	 * in order to fulfill the validation and a status level of `4xx` in order to reject the validation.
	 *
	 * ```js
	 * ngModel.$asyncValidators.uniqueUsername = function(modelValue, viewValue) {
	 *   var value = modelValue || viewValue;
	 *
	 *   // Lookup user by username
	 *   return $http.get('/api/users/' + value).
	 *      then(function resolved() {
	 *        //username exists, this means validation fails
	 *        return $q.reject('exists');
	 *      }, function rejected() {
	 *        //username does not exist, therefore this validation passes
	 *        return true;
	 *      });
	 * };
	 * ```
	 *
	 * @property {Array.<Function>} $viewChangeListeners Array of functions to execute whenever
	 *     a change to {@link ngModel.NgModelController#$viewValue `$viewValue`} has caused a change
	 *     to {@link ngModel.NgModelController#$modelValue `$modelValue`}.
	 *     It is called with no arguments, and its return value is ignored.
	 *     This can be used in place of additional $watches against the model value.
	 *
	 * @property {Object} $error An object hash with all failing validator ids as keys.
	 * @property {Object} $pending An object hash with all pending validator ids as keys.
	 *
	 * @property {boolean} $untouched True if control has not lost focus yet.
	 * @property {boolean} $touched True if control has lost focus.
	 * @property {boolean} $pristine True if user has not interacted with the control yet.
	 * @property {boolean} $dirty True if user has already interacted with the control.
	 * @property {boolean} $valid True if there is no error.
	 * @property {boolean} $invalid True if at least one error on the control.
	 * @property {string} $name The name attribute of the control.
	 *
	 * @description
	 *
	 * `NgModelController` provides API for the {@link ngModel `ngModel`} directive.
	 * The controller contains services for data-binding, validation, CSS updates, and value formatting
	 * and parsing. It purposefully does not contain any logic which deals with DOM rendering or
	 * listening to DOM events.
	 * Such DOM related logic should be provided by other directives which make use of
	 * `NgModelController` for data-binding to control elements.
	 * AngularJS provides this DOM logic for most {@link input `input`} elements.
	 * At the end of this page you can find a {@link ngModel.NgModelController#custom-control-example
	 * custom control example} that uses `ngModelController` to bind to `contenteditable` elements.
	 *
	 * @example
	 * ### Custom Control Example
	 * This example shows how to use `NgModelController` with a custom control to achieve
	 * data-binding. Notice how different directives (`contenteditable`, `ng-model`, and `required`)
	 * collaborate together to achieve the desired result.
	 *
	 * `contenteditable` is an HTML5 attribute, which tells the browser to let the element
	 * contents be edited in place by the user.
	 *
	 * We are using the {@link ng.service:$sce $sce} service here and include the {@link ngSanitize $sanitize}
	 * module to automatically remove "bad" content like inline event listener (e.g. `<span onclick="...">`).
	 * However, as we are using `$sce` the model can still decide to provide unsafe content if it marks
	 * that content using the `$sce` service.
	 *
	 * <example name="NgModelController" module="customControl" deps="angular-sanitize.js">
		<file name="style.css">
		  [contenteditable] {
			border: 1px solid black;
			background-color: white;
			min-height: 20px;
		  }
	
		  .ng-invalid {
			border: 1px solid red;
		  }
	
		</file>
		<file name="script.js">
		  angular.module('customControl', ['ngSanitize']).
			directive('contenteditable', ['$sce', function($sce) {
			  return {
				restrict: 'A', // only activate on element attribute
				require: '?ngModel', // get a hold of NgModelController
				link: function(scope, element, attrs, ngModel) {
				  if (!ngModel) return; // do nothing if no ng-model
	
				  // Specify how UI should be updated
				  ngModel.$render = function() {
					element.html($sce.getTrustedHtml(ngModel.$viewValue || ''));
				  };
	
				  // Listen for change events to enable binding
				  element.on('blur keyup change', function() {
					scope.$evalAsync(read);
				  });
				  read(); // initialize
	
				  // Write data to the model
				  function read() {
					var html = element.html();
					// When we clear the content editable the browser leaves a <br> behind
					// If strip-br attribute is provided then we strip this out
					if (attrs.stripBr && html === '<br>') {
					  html = '';
					}
					ngModel.$setViewValue(html);
				  }
				}
			  };
			}]);
		</file>
		<file name="index.html">
		  <form name="myForm">
		   <div contenteditable
				name="myWidget" ng-model="userContent"
				strip-br="true"
				required>Change me!</div>
			<span ng-show="myForm.myWidget.$error.required">Required!</span>
		   <hr>
		   <textarea ng-model="userContent" aria-label="Dynamic textarea"></textarea>
		  </form>
		</file>
		<file name="protractor.js" type="protractor">
		it('should data-bind and become invalid', function() {
		  if (browser.params.browser === 'safari' || browser.params.browser === 'firefox') {
			// SafariDriver can't handle contenteditable
			// and Firefox driver can't clear contenteditables very well
			return;
		  }
		  var contentEditable = element(by.css('[contenteditable]'));
		  var content = 'Change me!';
	
		  expect(contentEditable.getText()).toEqual(content);
	
		  contentEditable.clear();
		  contentEditable.sendKeys(protractor.Key.BACK_SPACE);
		  expect(contentEditable.getText()).toEqual('');
		  expect(contentEditable.getAttribute('class')).toMatch(/ng-invalid-required/);
		});
		</file>
	 * </example>
	 *
	 *
	 */
	NgModelController.$inject = ['$scope', '$exceptionHandler', '$attrs', '$element', '$parse', '$animate', '$timeout', '$q', '$interpolate'];
	function NgModelController($scope, $exceptionHandler, $attr, $element, $parse, $animate, $timeout, $q, $interpolate) {
		this.$viewValue = Number.NaN;
		this.$modelValue = Number.NaN;
		this.$$rawModelValue = undefined; // stores the parsed modelValue / model set from scope regardless of validity.
		this.$validators = {};
		this.$asyncValidators = {};
		this.$parsers = [];
		this.$formatters = [];
		this.$viewChangeListeners = [];
		this.$untouched = true;
		this.$touched = false;
		this.$pristine = true;
		this.$dirty = false;
		this.$valid = true;
		this.$invalid = false;
		this.$error = {}; // keep invalid keys here
		this.$$success = {}; // keep valid keys here
		this.$pending = undefined; // keep pending keys here
		this.$name = $interpolate($attr.name || '', false)($scope);
		this.$$parentForm = nullFormCtrl;
		this.$options = defaultModelOptions;
		this.$$updateEvents = '';
		// Attach the correct context to the event handler function for updateOn
		this.$$updateEventHandler = this.$$updateEventHandler.bind(this);

		this.$$parsedNgModel = $parse($attr.ngModel);
		this.$$parsedNgModelAssign = this.$$parsedNgModel.assign;
		this.$$ngModelGet = this.$$parsedNgModel;
		this.$$ngModelSet = this.$$parsedNgModelAssign;
		this.$$pendingDebounce = null;
		this.$$parserValid = undefined;
		this.$$parserName = 'parse';

		this.$$currentValidationRunId = 0;

		this.$$scope = $scope;
		this.$$rootScope = $scope.$root;
		this.$$attr = $attr;
		this.$$element = $element;
		this.$$animate = $animate;
		this.$$timeout = $timeout;
		this.$$parse = $parse;
		this.$$q = $q;
		this.$$exceptionHandler = $exceptionHandler;

		setupValidity(this);
		setupModelWatcher(this);
	}

	NgModelController.prototype = {
		$$initGetterSetters: function () {
			if (this.$options.getOption('getterSetter')) {
				var invokeModelGetter = this.$$parse(this.$$attr.ngModel + '()'),
					invokeModelSetter = this.$$parse(this.$$attr.ngModel + '($$$p)');

				this.$$ngModelGet = function ($scope) {
					var modelValue = this.$$parsedNgModel($scope);
					if (isFunction(modelValue)) {
						modelValue = invokeModelGetter($scope);
					}
					return modelValue;
				};
				this.$$ngModelSet = function ($scope, newValue) {
					if (isFunction(this.$$parsedNgModel($scope))) {
						invokeModelSetter($scope, { $$$p: newValue });
					} else {
						this.$$parsedNgModelAssign($scope, newValue);
					}
				};
			} else if (!this.$$parsedNgModel.assign) {
				throw ngModelMinErr('nonassign', 'Expression \'{0}\' is non-assignable. Element: {1}',
					this.$$attr.ngModel, startingTag(this.$$element));
			}
		},


		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$render
		 *
		 * @description
		 * Called when the view needs to be updated. It is expected that the user of the ng-model
		 * directive will implement this method.
		 *
		 * The `$render()` method is invoked in the following situations:
		 *
		 * * `$rollbackViewValue()` is called.  If we are rolling back the view value to the last
		 *   committed value then `$render()` is called to update the input control.
		 * * The value referenced by `ng-model` is changed programmatically and both the `$modelValue` and
		 *   the `$viewValue` are different from last time.
		 *
		 * Since `ng-model` does not do a deep watch, `$render()` is only invoked if the values of
		 * `$modelValue` and `$viewValue` are actually different from their previous values. If `$modelValue`
		 * or `$viewValue` are objects (rather than a string or number) then `$render()` will not be
		 * invoked if you only change a property on the objects.
		 */
		$render: noop,

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$isEmpty
		 *
		 * @description
		 * This is called when we need to determine if the value of an input is empty.
		 *
		 * For instance, the required directive does this to work out if the input has data or not.
		 *
		 * The default `$isEmpty` function checks whether the value is `undefined`, `''`, `null` or `NaN`.
		 *
		 * You can override this for input directives whose concept of being empty is different from the
		 * default. The `checkboxInputType` directive does this because in its case a value of `false`
		 * implies empty.
		 *
		 * @param {*} value The value of the input to check for emptiness.
		 * @returns {boolean} True if `value` is "empty".
		 */
		$isEmpty: function (value) {
			// eslint-disable-next-line no-self-compare
			return isUndefined(value) || value === '' || value === null || value !== value;
		},

		$$updateEmptyClasses: function (value) {
			if (this.$isEmpty(value)) {
				this.$$animate.removeClass(this.$$element, NOT_EMPTY_CLASS);
				this.$$animate.addClass(this.$$element, EMPTY_CLASS);
			} else {
				this.$$animate.removeClass(this.$$element, EMPTY_CLASS);
				this.$$animate.addClass(this.$$element, NOT_EMPTY_CLASS);
			}
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$setPristine
		 *
		 * @description
		 * Sets the control to its pristine state.
		 *
		 * This method can be called to remove the `ng-dirty` class and set the control to its pristine
		 * state (`ng-pristine` class). A model is considered to be pristine when the control
		 * has not been changed from when first compiled.
		 */
		$setPristine: function () {
			this.$dirty = false;
			this.$pristine = true;
			this.$$animate.removeClass(this.$$element, DIRTY_CLASS);
			this.$$animate.addClass(this.$$element, PRISTINE_CLASS);
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$setDirty
		 *
		 * @description
		 * Sets the control to its dirty state.
		 *
		 * This method can be called to remove the `ng-pristine` class and set the control to its dirty
		 * state (`ng-dirty` class). A model is considered to be dirty when the control has been changed
		 * from when first compiled.
		 */
		$setDirty: function () {
			this.$dirty = true;
			this.$pristine = false;
			this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
			this.$$animate.addClass(this.$$element, DIRTY_CLASS);
			this.$$parentForm.$setDirty();
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$setUntouched
		 *
		 * @description
		 * Sets the control to its untouched state.
		 *
		 * This method can be called to remove the `ng-touched` class and set the control to its
		 * untouched state (`ng-untouched` class). Upon compilation, a model is set as untouched
		 * by default, however this function can be used to restore that state if the model has
		 * already been touched by the user.
		 */
		$setUntouched: function () {
			this.$touched = false;
			this.$untouched = true;
			this.$$animate.setClass(this.$$element, UNTOUCHED_CLASS, TOUCHED_CLASS);
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$setTouched
		 *
		 * @description
		 * Sets the control to its touched state.
		 *
		 * This method can be called to remove the `ng-untouched` class and set the control to its
		 * touched state (`ng-touched` class). A model is considered to be touched when the user has
		 * first focused the control element and then shifted focus away from the control (blur event).
		 */
		$setTouched: function () {
			this.$touched = true;
			this.$untouched = false;
			this.$$animate.setClass(this.$$element, TOUCHED_CLASS, UNTOUCHED_CLASS);
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$rollbackViewValue
		 *
		 * @description
		 * Cancel an update and reset the input element's value to prevent an update to the `$modelValue`,
		 * which may be caused by a pending debounced event or because the input is waiting for some
		 * future event.
		 *
		 * If you have an input that uses `ng-model-options` to set up debounced updates or updates that
		 * depend on special events such as `blur`, there can be a period when the `$viewValue` is out of
		 * sync with the ngModel's `$modelValue`.
		 *
		 * In this case, you can use `$rollbackViewValue()` to manually cancel the debounced / future update
		 * and reset the input to the last committed view value.
		 *
		 * It is also possible that you run into difficulties if you try to update the ngModel's `$modelValue`
		 * programmatically before these debounced/future events have resolved/occurred, because AngularJS's
		 * dirty checking mechanism is not able to tell whether the model has actually changed or not.
		 *
		 * The `$rollbackViewValue()` method should be called before programmatically changing the model of an
		 * input which may have such events pending. This is important in order to make sure that the
		 * input field will be updated with the new model value and any pending operations are cancelled.
		 *
		 * @example
		 * <example name="ng-model-cancel-update" module="cancel-update-example">
		 *   <file name="app.js">
		 *     angular.module('cancel-update-example', [])
		 *
		 *     .controller('CancelUpdateController', ['$scope', function($scope) {
		 *       $scope.model = {value1: '', value2: ''};
		 *
		 *       $scope.setEmpty = function(e, value, rollback) {
		 *         if (e.keyCode === 27) {
		 *           e.preventDefault();
		 *           if (rollback) {
		 *             $scope.myForm[value].$rollbackViewValue();
		 *           }
		 *           $scope.model[value] = '';
		 *         }
		 *       };
		 *     }]);
		 *   </file>
		 *   <file name="index.html">
		 *     <div ng-controller="CancelUpdateController">
		 *       <p>Both of these inputs are only updated if they are blurred. Hitting escape should
		 *       empty them. Follow these steps and observe the difference:</p>
		 *       <ol>
		 *         <li>Type something in the input. You will see that the model is not yet updated</li>
		 *         <li>Press the Escape key.
		 *           <ol>
		 *             <li> In the first example, nothing happens, because the model is already '', and no
		 *             update is detected. If you blur the input, the model will be set to the current view.
		 *             </li>
		 *             <li> In the second example, the pending update is cancelled, and the input is set back
		 *             to the last committed view value (''). Blurring the input does nothing.
		 *             </li>
		 *           </ol>
		 *         </li>
		 *       </ol>
		 *
		 *       <form name="myForm" ng-model-options="{ updateOn: 'blur' }">
		 *         <div>
		 *           <p id="inputDescription1">Without $rollbackViewValue():</p>
		 *           <input name="value1" aria-describedby="inputDescription1" ng-model="model.value1"
		 *                  ng-keydown="setEmpty($event, 'value1')">
		 *           value1: "{{ model.value1 }}"
		 *         </div>
		 *
		 *         <div>
		 *           <p id="inputDescription2">With $rollbackViewValue():</p>
		 *           <input name="value2" aria-describedby="inputDescription2" ng-model="model.value2"
		 *                  ng-keydown="setEmpty($event, 'value2', true)">
		 *           value2: "{{ model.value2 }}"
		 *         </div>
		 *       </form>
		 *     </div>
		 *   </file>
			 <file name="style.css">
				div {
				  display: table-cell;
				}
				div:nth-child(1) {
				  padding-right: 30px;
				}
	  
			  </file>
		 * </example>
		 */
		$rollbackViewValue: function () {
			this.$$timeout.cancel(this.$$pendingDebounce);
			this.$viewValue = this.$$lastCommittedViewValue;
			this.$render();
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$validate
		 *
		 * @description
		 * Runs each of the registered validators (first synchronous validators and then
		 * asynchronous validators).
		 * If the validity changes to invalid, the model will be set to `undefined`,
		 * unless {@link ngModelOptions `ngModelOptions.allowInvalid`} is `true`.
		 * If the validity changes to valid, it will set the model to the last available valid
		 * `$modelValue`, i.e. either the last parsed value or the last value set from the scope.
		 */
		$validate: function () {

			// ignore $validate before model is initialized
			if (isNumberNaN(this.$modelValue)) {
				return;
			}

			var viewValue = this.$$lastCommittedViewValue;
			// Note: we use the $$rawModelValue as $modelValue might have been
			// set to undefined during a view -> model update that found validation
			// errors. We can't parse the view here, since that could change
			// the model although neither viewValue nor the model on the scope changed
			var modelValue = this.$$rawModelValue;

			var prevValid = this.$valid;
			var prevModelValue = this.$modelValue;

			var allowInvalid = this.$options.getOption('allowInvalid');

			var that = this;
			this.$$runValidators(modelValue, viewValue, function (allValid) {
				// If there was no change in validity, don't update the model
				// This prevents changing an invalid modelValue to undefined
				if (!allowInvalid && prevValid !== allValid) {
					// Note: Don't check this.$valid here, as we could have
					// external validators (e.g. calculated on the server),
					// that just call $setValidity and need the model value
					// to calculate their validity.
					that.$modelValue = allValid ? modelValue : undefined;

					if (that.$modelValue !== prevModelValue) {
						that.$$writeModelToScope();
					}
				}
			});
		},

		$$runValidators: function (modelValue, viewValue, doneCallback) {
			this.$$currentValidationRunId++;
			var localValidationRunId = this.$$currentValidationRunId;
			var that = this;

			// check parser error
			if (!processParseErrors()) {
				validationDone(false);
				return;
			}
			if (!processSyncValidators()) {
				validationDone(false);
				return;
			}
			processAsyncValidators();

			function processParseErrors() {
				var errorKey = that.$$parserName;

				if (isUndefined(that.$$parserValid)) {
					setValidity(errorKey, null);
				} else {
					if (!that.$$parserValid) {
						forEach(that.$validators, function (v, name) {
							setValidity(name, null);
						});
						forEach(that.$asyncValidators, function (v, name) {
							setValidity(name, null);
						});
					}

					// Set the parse error last, to prevent unsetting it, should a $validators key == parserName
					setValidity(errorKey, that.$$parserValid);
					return that.$$parserValid;
				}
				return true;
			}

			function processSyncValidators() {
				var syncValidatorsValid = true;
				forEach(that.$validators, function (validator, name) {
					var result = Boolean(validator(modelValue, viewValue));
					syncValidatorsValid = syncValidatorsValid && result;
					setValidity(name, result);
				});
				if (!syncValidatorsValid) {
					forEach(that.$asyncValidators, function (v, name) {
						setValidity(name, null);
					});
					return false;
				}
				return true;
			}

			function processAsyncValidators() {
				var validatorPromises = [];
				var allValid = true;
				forEach(that.$asyncValidators, function (validator, name) {
					var promise = validator(modelValue, viewValue);
					if (!isPromiseLike(promise)) {
						throw ngModelMinErr('nopromise',
							'Expected asynchronous validator to return a promise but got \'{0}\' instead.', promise);
					}
					setValidity(name, undefined);
					validatorPromises.push(promise.then(function () {
						setValidity(name, true);
					}, function () {
						allValid = false;
						setValidity(name, false);
					}));
				});
				if (!validatorPromises.length) {
					validationDone(true);
				} else {
					that.$$q.all(validatorPromises).then(function () {
						validationDone(allValid);
					}, noop);
				}
			}

			function setValidity(name, isValid) {
				if (localValidationRunId === that.$$currentValidationRunId) {
					that.$setValidity(name, isValid);
				}
			}

			function validationDone(allValid) {
				if (localValidationRunId === that.$$currentValidationRunId) {

					doneCallback(allValid);
				}
			}
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$commitViewValue
		 *
		 * @description
		 * Commit a pending update to the `$modelValue`.
		 *
		 * Updates may be pending by a debounced event or because the input is waiting for a some future
		 * event defined in `ng-model-options`. this method is rarely needed as `NgModelController`
		 * usually handles calling this in response to input events.
		 */
		$commitViewValue: function () {
			var viewValue = this.$viewValue;

			this.$$timeout.cancel(this.$$pendingDebounce);

			// If the view value has not changed then we should just exit, except in the case where there is
			// a native validator on the element. In this case the validation state may have changed even though
			// the viewValue has stayed empty.
			if (this.$$lastCommittedViewValue === viewValue && (viewValue !== '' || !this.$$hasNativeValidators)) {
				return;
			}
			this.$$updateEmptyClasses(viewValue);
			this.$$lastCommittedViewValue = viewValue;

			// change to dirty
			if (this.$pristine) {
				this.$setDirty();
			}
			this.$$parseAndValidate();
		},

		$$parseAndValidate: function () {
			var viewValue = this.$$lastCommittedViewValue;
			var modelValue = viewValue;
			var that = this;

			this.$$parserValid = isUndefined(modelValue) ? undefined : true;

			// Reset any previous parse error
			this.$setValidity(this.$$parserName, null);
			this.$$parserName = 'parse';

			if (this.$$parserValid) {
				for (var i = 0; i < this.$parsers.length; i++) {
					modelValue = this.$parsers[i](modelValue);
					if (isUndefined(modelValue)) {
						this.$$parserValid = false;
						break;
					}
				}
			}
			if (isNumberNaN(this.$modelValue)) {
				// this.$modelValue has not been touched yet...
				this.$modelValue = this.$$ngModelGet(this.$$scope);
			}
			var prevModelValue = this.$modelValue;
			var allowInvalid = this.$options.getOption('allowInvalid');
			this.$$rawModelValue = modelValue;

			if (allowInvalid) {
				this.$modelValue = modelValue;
				writeToModelIfNeeded();
			}

			// Pass the $$lastCommittedViewValue here, because the cached viewValue might be out of date.
			// This can happen if e.g. $setViewValue is called from inside a parser
			this.$$runValidators(modelValue, this.$$lastCommittedViewValue, function (allValid) {
				if (!allowInvalid) {
					// Note: Don't check this.$valid here, as we could have
					// external validators (e.g. calculated on the server),
					// that just call $setValidity and need the model value
					// to calculate their validity.
					that.$modelValue = allValid ? modelValue : undefined;
					writeToModelIfNeeded();
				}
			});

			function writeToModelIfNeeded() {
				if (that.$modelValue !== prevModelValue) {
					that.$$writeModelToScope();
				}
			}
		},

		$$writeModelToScope: function () {
			this.$$ngModelSet(this.$$scope, this.$modelValue);
			forEach(this.$viewChangeListeners, function (listener) {
				try {
					listener();
				} catch (e) {
					// eslint-disable-next-line no-invalid-this
					this.$$exceptionHandler(e);
				}
			}, this);
		},

		/**
		 * @ngdoc method
		 * @name ngModel.NgModelController#$setViewValue
		 *
		 * @description
		 * Update the view value.
		 *
		 * This method should be called when a control wants to change the view value; typically,
		 * this is done from within a DOM event handler. For example, the {@link ng.directive:input input}
		 * directive calls it when the value of the input changes and {@link ng.directive:select select}
		 * calls it when an option is selected.
		 *
		 * When `$setViewValue` is called, the new `value` will be staged for committing through the `$parsers`
		 * and `$validators` pipelines. If there are no special {@link ngModelOptions} specified then the staged
		 * value is sent directly for processing through the `$parsers` pipeline. After this, the `$validators` and
		 * `$asyncValidators` are called and the value is applied to `$modelValue`.
		 * Finally, the value is set to the **expression** specified in the `ng-model` attribute and
		 * all the registered change listeners, in the `$viewChangeListeners` list are called.
		 *
		 * In case the {@link ng.directive:ngModelOptions ngModelOptions} directive is used with `updateOn`
		 * and the `default` trigger is not listed, all those actions will remain pending until one of the
		 * `updateOn` events is triggered on the DOM element.
		 * All these actions will be debounced if the {@link ng.directive:ngModelOptions ngModelOptions}
		 * directive is used with a custom debounce for this particular event.
		 * Note that a `$digest` is only triggered once the `updateOn` events are fired, or if `debounce`
		 * is specified, once the timer runs out.
		 *
		 * When used with standard inputs, the view value will always be a string (which is in some cases
		 * parsed into another type, such as a `Date` object for `input[date]`.)
		 * However, custom controls might also pass objects to this method. In this case, we should make
		 * a copy of the object before passing it to `$setViewValue`. This is because `ngModel` does not
		 * perform a deep watch of objects, it only looks for a change of identity. If you only change
		 * the property of the object then ngModel will not realize that the object has changed and
		 * will not invoke the `$parsers` and `$validators` pipelines. For this reason, you should
		 * not change properties of the copy once it has been passed to `$setViewValue`.
		 * Otherwise you may cause the model value on the scope to change incorrectly.
		 *
		 * <div class="alert alert-info">
		 * In any case, the value passed to the method should always reflect the current value
		 * of the control. For example, if you are calling `$setViewValue` for an input element,
		 * you should pass the input DOM value. Otherwise, the control and the scope model become
		 * out of sync. It's also important to note that `$setViewValue` does not call `$render` or change
		 * the control's DOM value in any way. If we want to change the control's DOM value
		 * programmatically, we should update the `ngModel` scope expression. Its new value will be
		 * picked up by the model controller, which will run it through the `$formatters`, `$render` it
		 * to update the DOM, and finally call `$validate` on it.
		 * </div>
		 *
		 * @param {*} value value from the view.
		 * @param {string} trigger Event that triggered the update.
		 */
		$setViewValue: function (value, trigger) {
			this.$viewValue = value;
			if (this.$options.getOption('updateOnDefault')) {
				this.$$debounceViewValueCommit(trigger);
			}
		},

		$$debounceViewValueCommit: function (trigger) {
			var debounceDelay = this.$options.getOption('debounce');

			if (isNumber(debounceDelay[trigger])) {
				debounceDelay = debounceDelay[trigger];
			} else if (isNumber(debounceDelay['default']) &&
				this.$options.getOption('updateOn').indexOf(trigger) === -1
			) {
				debounceDelay = debounceDelay['default'];
			} else if (isNumber(debounceDelay['*'])) {
				debounceDelay = debounceDelay['*'];
			}

			this.$$timeout.cancel(this.$$pendingDebounce);
			var that = this;
			if (debounceDelay > 0) { // this fails if debounceDelay is an object
				this.$$pendingDebounce = this.$$timeout(function () {
					that.$commitViewValue();
				}, debounceDelay);
			} else if (this.$$rootScope.$$phase) {
				this.$commitViewValue();
			} else {
				this.$$scope.$apply(function () {
					that.$commitViewValue();
				});
			}
		},

		/**
		 * @ngdoc method
		 *
		 * @name ngModel.NgModelController#$overrideModelOptions
		 *
		 * @description
		 *
		 * Override the current model options settings programmatically.
		 *
		 * The previous `ModelOptions` value will not be modified. Instead, a
		 * new `ModelOptions` object will inherit from the previous one overriding
		 * or inheriting settings that are defined in the given parameter.
		 *
		 * See {@link ngModelOptions} for information about what options can be specified
		 * and how model option inheritance works.
		 *
		 * <div class="alert alert-warning">
		 * **Note:** this function only affects the options set on the `ngModelController`,
		 * and not the options on the {@link ngModelOptions} directive from which they might have been
		 * obtained initially.
		 * </div>
		 *
		 * <div class="alert alert-danger">
		 * **Note:** it is not possible to override the `getterSetter` option.
		 * </div>
		 *
		 * @param {Object} options a hash of settings to override the previous options
		 *
		 */
		$overrideModelOptions: function (options) {
			this.$options = this.$options.createChild(options);
			this.$$setUpdateOnEvents();
		},

		/**
		 * @ngdoc method
		 *
		 * @name  ngModel.NgModelController#$processModelValue
	  
		 * @description
		 *
		 * Runs the model -> view pipeline on the current
		 * {@link ngModel.NgModelController#$modelValue $modelValue}.
		 *
		 * The following actions are performed by this method:
		 *
		 * - the `$modelValue` is run through the {@link ngModel.NgModelController#$formatters $formatters}
		 * and the result is set to the {@link ngModel.NgModelController#$viewValue $viewValue}
		 * - the `ng-empty` or `ng-not-empty` class is set on the element
		 * - if the `$viewValue` has changed:
		 *   - {@link ngModel.NgModelController#$render $render} is called on the control
		 *   - the {@link ngModel.NgModelController#$validators $validators} are run and
		 *   the validation status is set.
		 *
		 * This method is called by ngModel internally when the bound scope value changes.
		 * Application developers usually do not have to call this function themselves.
		 *
		 * This function can be used when the `$viewValue` or the rendered DOM value are not correctly
		 * formatted and the `$modelValue` must be run through the `$formatters` again.
		 *
		 * @example
		 * Consider a text input with an autocomplete list (for fruit), where the items are
		 * objects with a name and an id.
		 * A user enters `ap` and then selects `Apricot` from the list.
		 * Based on this, the autocomplete widget will call `$setViewValue({name: 'Apricot', id: 443})`,
		 * but the rendered value will still be `ap`.
		 * The widget can then call `ctrl.$processModelValue()` to run the model -> view
		 * pipeline again, which formats the object to the string `Apricot`,
		 * then updates the `$viewValue`, and finally renders it in the DOM.
		 *
		 * <example module="inputExample" name="ng-model-process">
		   <file name="index.html">
			<div ng-controller="inputController" style="display: flex;">
			  <div style="margin-right: 30px;">
				Search Fruit:
				<basic-autocomplete items="items" on-select="selectedFruit = item"></basic-autocomplete>
			  </div>
			  <div>
				Model:<br>
				<pre>{{selectedFruit | json}}</pre>
			  </div>
			</div>
		   </file>
		   <file name="app.js">
			angular.module('inputExample', [])
			  .controller('inputController', function($scope) {
				$scope.items = [
				  {name: 'Apricot', id: 443},
				  {name: 'Clementine', id: 972},
				  {name: 'Durian', id: 169},
				  {name: 'Jackfruit', id: 982},
				  {name: 'Strawberry', id: 863}
				];
			  })
			  .component('basicAutocomplete', {
				bindings: {
				  items: '<',
				  onSelect: '&'
				},
				templateUrl: 'autocomplete.html',
				controller: function($element, $scope) {
				  var that = this;
				  var ngModel;
	  
				  that.$postLink = function() {
					ngModel = $element.find('input').controller('ngModel');
	  
					ngModel.$formatters.push(function(value) {
					  return (value && value.name) || value;
					});
	  
					ngModel.$parsers.push(function(value) {
					  var match = value;
					  for (var i = 0; i < that.items.length; i++) {
						if (that.items[i].name === value) {
						  match = that.items[i];
						  break;
						}
					  }
	  
					  return match;
					});
				  };
	  
				  that.selectItem = function(item) {
					ngModel.$setViewValue(item);
					ngModel.$processModelValue();
					that.onSelect({item: item});
				  };
				}
			  });
		   </file>
		   <file name="autocomplete.html">
			 <div>
			   <input type="search" ng-model="$ctrl.searchTerm" />
			   <ul>
				 <li ng-repeat="item in $ctrl.items | filter:$ctrl.searchTerm">
				   <button ng-click="$ctrl.selectItem(item)">{{ item.name }}</button>
				 </li>
			   </ul>
			 </div>
		   </file>
		 * </example>
		 *
		 */
		$processModelValue: function () {
			var viewValue = this.$$format();

			if (this.$viewValue !== viewValue) {
				this.$$updateEmptyClasses(viewValue);
				this.$viewValue = this.$$lastCommittedViewValue = viewValue;
				this.$render();
				// It is possible that model and view value have been updated during render
				this.$$runValidators(this.$modelValue, this.$viewValue, noop);
			}
		},

		/**
		 * This method is called internally to run the $formatters on the $modelValue
		 */
		$$format: function () {
			var formatters = this.$formatters,
				idx = formatters.length;

			var viewValue = this.$modelValue;
			while (idx--) {
				viewValue = formatters[idx](viewValue);
			}

			return viewValue;
		},

		/**
		 * This method is called internally when the bound scope value changes.
		 */
		$$setModelValue: function (modelValue) {
			this.$modelValue = this.$$rawModelValue = modelValue;
			this.$$parserValid = undefined;
			this.$processModelValue();
		},

		$$setUpdateOnEvents: function () {
			if (this.$$updateEvents) {
				this.$$element.off(this.$$updateEvents, this.$$updateEventHandler);
			}

			this.$$updateEvents = this.$options.getOption('updateOn');
			if (this.$$updateEvents) {
				this.$$element.on(this.$$updateEvents, this.$$updateEventHandler);
			}
		},

		$$updateEventHandler: function (ev) {
			this.$$debounceViewValueCommit(ev && ev.type);
		}
	};

	function setupModelWatcher(ctrl) {
		// model -> value
		// Note: we cannot use a normal scope.$watch as we want to detect the following:
		// 1. scope value is 'a'
		// 2. user enters 'b'
		// 3. ng-change kicks in and reverts scope value to 'a'
		//    -> scope value did not change since the last digest as
		//       ng-change executes in apply phase
		// 4. view should be changed back to 'a'
		ctrl.$$scope.$watch(function ngModelWatch(scope) {
			var modelValue = ctrl.$$ngModelGet(scope);

			// if scope model value and ngModel value are out of sync
			// This cannot be moved to the action function, because it would not catch the
			// case where the model is changed in the ngChange function or the model setter
			if (modelValue !== ctrl.$modelValue &&
				// checks for NaN is needed to allow setting the model to NaN when there's an asyncValidator
				// eslint-disable-next-line no-self-compare
				(ctrl.$modelValue === ctrl.$modelValue || modelValue === modelValue)
			) {
				ctrl.$$setModelValue(modelValue);
			}

			return modelValue;
		});
	}

	/**
	 * @ngdoc method
	 * @name ngModel.NgModelController#$setValidity
	 *
	 * @description
	 * Change the validity state, and notify the form.
	 *
	 * This method can be called within $parsers/$formatters or a custom validation implementation.
	 * However, in most cases it should be sufficient to use the `ngModel.$validators` and
	 * `ngModel.$asyncValidators` collections which will call `$setValidity` automatically.
	 *
	 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be assigned
	 *        to either `$error[validationErrorKey]` or `$pending[validationErrorKey]`
	 *        (for unfulfilled `$asyncValidators`), so that it is available for data-binding.
	 *        The `validationErrorKey` should be in camelCase and will get converted into dash-case
	 *        for class name. Example: `myError` will result in `ng-valid-my-error` and `ng-invalid-my-error`
	 *        classes and can be bound to as `{{ someForm.someControl.$error.myError }}`.
	 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending (undefined),
	 *                          or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
	 *                          Skipped is used by AngularJS when validators do not run because of parse errors and
	 *                          when `$asyncValidators` do not run because any of the `$validators` failed.
	 */
	addSetValidityMethod({
		clazz: NgModelController,
		set: function (object, property) {
			object[property] = true;
		},
		unset: function (object, property) {
			delete object[property];
		}
	});


	/**
	 * @ngdoc directive
	 * @name ngModel
	 * @restrict A
	 * @priority 1
	 * @param {expression} ngModel assignable {@link guide/expression Expression} to bind to.
	 *
	 * @description
	 * The `ngModel` directive binds an `input`,`select`, `textarea` (or custom form control) to a
	 * property on the scope using {@link ngModel.NgModelController NgModelController},
	 * which is created and exposed by this directive.
	 *
	 * `ngModel` is responsible for:
	 *
	 * - Binding the view into the model, which other directives such as `input`, `textarea` or `select`
	 *   require.
	 * - Providing validation behavior (i.e. required, number, email, url).
	 * - Keeping the state of the control (valid/invalid, dirty/pristine, touched/untouched, validation errors).
	 * - Setting related css classes on the element (`ng-valid`, `ng-invalid`, `ng-dirty`, `ng-pristine`, `ng-touched`,
	 *   `ng-untouched`, `ng-empty`, `ng-not-empty`) including animations.
	 * - Registering the control with its parent {@link ng.directive:form form}.
	 *
	 * Note: `ngModel` will try to bind to the property given by evaluating the expression on the
	 * current scope. If the property doesn't already exist on this scope, it will be created
	 * implicitly and added to the scope.
	 *
	 * For best practices on using `ngModel`, see:
	 *
	 *  - [Understanding Scopes](https://github.com/angular/angular.js/wiki/Understanding-Scopes)
	 *
	 * For basic examples, how to use `ngModel`, see:
	 *
	 *  - {@link ng.directive:input input}
	 *    - {@link input[text] text}
	 *    - {@link input[checkbox] checkbox}
	 *    - {@link input[radio] radio}
	 *    - {@link input[number] number}
	 *    - {@link input[email] email}
	 *    - {@link input[url] url}
	 *    - {@link input[date] date}
	 *    - {@link input[datetime-local] datetime-local}
	 *    - {@link input[time] time}
	 *    - {@link input[month] month}
	 *    - {@link input[week] week}
	 *  - {@link ng.directive:select select}
	 *  - {@link ng.directive:textarea textarea}
	 *
	 * ## Complex Models (objects or collections)
	 *
	 * By default, `ngModel` watches the model by reference, not value. This is important to know when
	 * binding inputs to models that are objects (e.g. `Date`) or collections (e.g. arrays). If only properties of the
	 * object or collection change, `ngModel` will not be notified and so the input will not be  re-rendered.
	 *
	 * The model must be assigned an entirely new object or collection before a re-rendering will occur.
	 *
	 * Some directives have options that will cause them to use a custom `$watchCollection` on the model expression
	 * - for example, `ngOptions` will do so when a `track by` clause is included in the comprehension expression or
	 * if the select is given the `multiple` attribute.
	 *
	 * The `$watchCollection()` method only does a shallow comparison, meaning that changing properties deeper than the
	 * first level of the object (or only changing the properties of an item in the collection if it's an array) will still
	 * not trigger a re-rendering of the model.
	 *
	 * ## CSS classes
	 * The following CSS classes are added and removed on the associated input/select/textarea element
	 * depending on the validity of the model.
	 *
	 *  - `ng-valid`: the model is valid
	 *  - `ng-invalid`: the model is invalid
	 *  - `ng-valid-[key]`: for each valid key added by `$setValidity`
	 *  - `ng-invalid-[key]`: for each invalid key added by `$setValidity`
	 *  - `ng-pristine`: the control hasn't been interacted with yet
	 *  - `ng-dirty`: the control has been interacted with
	 *  - `ng-touched`: the control has been blurred
	 *  - `ng-untouched`: the control hasn't been blurred
	 *  - `ng-pending`: any `$asyncValidators` are unfulfilled
	 *  - `ng-empty`: the view does not contain a value or the value is deemed "empty", as defined
	 *     by the {@link ngModel.NgModelController#$isEmpty} method
	 *  - `ng-not-empty`: the view contains a non-empty value
	 *
	 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
	 *
	 * @animations
	 * Animations within models are triggered when any of the associated CSS classes are added and removed
	 * on the input element which is attached to the model. These classes include: `.ng-pristine`, `.ng-dirty`,
	 * `.ng-invalid` and `.ng-valid` as well as any other validations that are performed on the model itself.
	 * The animations that are triggered within ngModel are similar to how they work in ngClass and
	 * animations can be hooked into using CSS transitions, keyframes as well as JS animations.
	 *
	 * The following example shows a simple way to utilize CSS transitions to style an input element
	 * that has been rendered as invalid after it has been validated:
	 *
	 * <pre>
	 * //be sure to include ngAnimate as a module to hook into more
	 * //advanced animations
	 * .my-input {
	 *   transition:0.5s linear all;
	 *   background: white;
	 * }
	 * .my-input.ng-invalid {
	 *   background: red;
	 *   color:white;
	 * }
	 * </pre>
	 *
	 * @example
	 * ### Basic Usage
	 * <example deps="angular-animate.js" animations="true" fixBase="true" module="inputExample" name="ng-model">
		 <file name="index.html">
		   <script>
			angular.module('inputExample', [])
			  .controller('ExampleController', ['$scope', function($scope) {
				$scope.val = '1';
			  }]);
		   </script>
		   <style>
			 .my-input {
			   transition:all linear 0.5s;
			   background: transparent;
			 }
			 .my-input.ng-invalid {
			   color:white;
			   background: red;
			 }
		   </style>
		   <p id="inputDescription">
			Update input to see transitions when valid/invalid.
			Integer is a valid value.
		   </p>
		   <form name="testForm" ng-controller="ExampleController">
			 <input ng-model="val" ng-pattern="/^\d+$/" name="anim" class="my-input"
					aria-describedby="inputDescription" />
		   </form>
		 </file>
	 * </example>
	 *
	 * @example
	 * ### Binding to a getter/setter
	 *
	 * Sometimes it's helpful to bind `ngModel` to a getter/setter function.  A getter/setter is a
	 * function that returns a representation of the model when called with zero arguments, and sets
	 * the internal state of a model when called with an argument. It's sometimes useful to use this
	 * for models that have an internal representation that's different from what the model exposes
	 * to the view.
	 *
	 * <div class="alert alert-success">
	 * **Best Practice:** It's best to keep getters fast because AngularJS is likely to call them more
	 * frequently than other parts of your code.
	 * </div>
	 *
	 * You use this behavior by adding `ng-model-options="{ getterSetter: true }"` to an element that
	 * has `ng-model` attached to it. You can also add `ng-model-options="{ getterSetter: true }"` to
	 * a `<form>`, which will enable this behavior for all `<input>`s within it. See
	 * {@link ng.directive:ngModelOptions `ngModelOptions`} for more.
	 *
	 * The following example shows how to use `ngModel` with a getter/setter:
	 *
	 * @example
	 * <example name="ngModel-getter-setter" module="getterSetterExample">
		 <file name="index.html">
		   <div ng-controller="ExampleController">
			 <form name="userForm">
			   <label>Name:
				 <input type="text" name="userName"
						ng-model="user.name"
						ng-model-options="{ getterSetter: true }" />
			   </label>
			 </form>
			 <pre>user.name = <span ng-bind="user.name()"></span></pre>
		   </div>
		 </file>
		 <file name="app.js">
		   angular.module('getterSetterExample', [])
			 .controller('ExampleController', ['$scope', function($scope) {
			   var _name = 'Brian';
			   $scope.user = {
				 name: function(newName) {
				  // Note that newName can be undefined for two reasons:
				  // 1. Because it is called as a getter and thus called with no arguments
				  // 2. Because the property should actually be set to undefined. This happens e.g. if the
				  //    input is invalid
				  return arguments.length ? (_name = newName) : _name;
				 }
			   };
			 }]);
		 </file>
	 * </example>
	 */
	var ngModelDirective = ['$rootScope', function ($rootScope) {
		return {
			restrict: 'A',
			require: ['ngModel', '^?form', '^?ngModelOptions'],
			controller: NgModelController,
			// Prelink needs to run before any input directive
			// so that we can set the NgModelOptions in NgModelController
			// before anyone else uses it.
			priority: 1,
			compile: function ngModelCompile(element) {
				// Setup initial state of the control
				element.addClass(PRISTINE_CLASS).addClass(UNTOUCHED_CLASS).addClass(VALID_CLASS);

				return {
					pre: function ngModelPreLink(scope, element, attr, ctrls) {
						var modelCtrl = ctrls[0],
							formCtrl = ctrls[1] || modelCtrl.$$parentForm,
							optionsCtrl = ctrls[2];

						if (optionsCtrl) {
							modelCtrl.$options = optionsCtrl.$options;
						}

						modelCtrl.$$initGetterSetters();

						// notify others, especially parent forms
						formCtrl.$addControl(modelCtrl);

						attr.$observe('name', function (newValue) {
							if (modelCtrl.$name !== newValue) {
								modelCtrl.$$parentForm.$$renameControl(modelCtrl, newValue);
							}
						});

						scope.$on('$destroy', function () {
							modelCtrl.$$parentForm.$removeControl(modelCtrl);
						});
					},
					post: function ngModelPostLink(scope, element, attr, ctrls) {
						var modelCtrl = ctrls[0];
						modelCtrl.$$setUpdateOnEvents();

						function setTouched() {
							modelCtrl.$setTouched();
						}

						element.on('blur', function () {
							if (modelCtrl.$touched) return;

							if ($rootScope.$$phase) {
								scope.$evalAsync(setTouched);
							} else {
								scope.$apply(setTouched);
							}
						});
					}
				};
			}
		};
	}];

	/* exported defaultModelOptions */
	var defaultModelOptions;
	var DEFAULT_REGEXP = /(\s+|^)default(\s+|$)/;

	/**
	 * @ngdoc type
	 * @name ModelOptions
	 * @description
	 * A container for the options set by the {@link ngModelOptions} directive
	 */
	function ModelOptions(options) {
		this.$$options = options;
	}

	ModelOptions.prototype = {

		/**
		 * @ngdoc method
		 * @name ModelOptions#getOption
		 * @param {string} name the name of the option to retrieve
		 * @returns {*} the value of the option
		 * @description
		 * Returns the value of the given option
		 */
		getOption: function (name) {
			return this.$$options[name];
		},

		/**
		 * @ngdoc method
		 * @name ModelOptions#createChild
		 * @param {Object} options a hash of options for the new child that will override the parent's options
		 * @return {ModelOptions} a new `ModelOptions` object initialized with the given options.
		 */
		createChild: function (options) {
			var inheritAll = false;

			// make a shallow copy
			options = extend({}, options);

			// Inherit options from the parent if specified by the value `"$inherit"`
			forEach(options, /** @this */ function (option, key) {
				if (option === '$inherit') {
					if (key === '*') {
						inheritAll = true;
					} else {
						options[key] = this.$$options[key];
						// `updateOn` is special so we must also inherit the `updateOnDefault` option
						if (key === 'updateOn') {
							options.updateOnDefault = this.$$options.updateOnDefault;
						}
					}
				} else {
					if (key === 'updateOn') {
						// If the `updateOn` property contains the `default` event then we have to remove
						// it from the event list and set the `updateOnDefault` flag.
						options.updateOnDefault = false;
						options[key] = trim(option.replace(DEFAULT_REGEXP, function () {
							options.updateOnDefault = true;
							return ' ';
						}));
					}
				}
			}, this);

			if (inheritAll) {
				// We have a property of the form: `"*": "$inherit"`
				delete options['*'];
				defaults(options, this.$$options);
			}

			// Finally add in any missing defaults
			defaults(options, defaultModelOptions.$$options);

			return new ModelOptions(options);
		}
	};


	defaultModelOptions = new ModelOptions({
		updateOn: '',
		updateOnDefault: true,
		debounce: 0,
		getterSetter: false,
		allowInvalid: false,
		timezone: null
	});


	/**
	 * @ngdoc directive
	 * @name ngModelOptions
	 * @restrict A
	 * @priority 10
	 *
	 * @description
	 * This directive allows you to modify the behaviour of {@link ngModel} directives within your
	 * application. You can specify an `ngModelOptions` directive on any element. All {@link ngModel}
	 * directives will use the options of their nearest `ngModelOptions` ancestor.
	 *
	 * The `ngModelOptions` settings are found by evaluating the value of the attribute directive as
	 * an AngularJS expression. This expression should evaluate to an object, whose properties contain
	 * the settings. For example: `<div ng-model-options="{ debounce: 100 }"`.
	 *
	 * ## Inheriting Options
	 *
	 * You can specify that an `ngModelOptions` setting should be inherited from a parent `ngModelOptions`
	 * directive by giving it the value of `"$inherit"`.
	 * Then it will inherit that setting from the first `ngModelOptions` directive found by traversing up the
	 * DOM tree. If there is no ancestor element containing an `ngModelOptions` directive then default settings
	 * will be used.
	 *
	 * For example given the following fragment of HTML
	 *
	 *
	 * ```html
	 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
	 *   <form ng-model-options="{ updateOn: 'blur', allowInvalid: '$inherit' }">
	 *     <input ng-model-options="{ updateOn: 'default', allowInvalid: '$inherit' }" />
	 *   </form>
	 * </div>
	 * ```
	 *
	 * the `input` element will have the following settings
	 *
	 * ```js
	 * { allowInvalid: true, updateOn: 'default', debounce: 0 }
	 * ```
	 *
	 * Notice that the `debounce` setting was not inherited and used the default value instead.
	 *
	 * You can specify that all undefined settings are automatically inherited from an ancestor by
	 * including a property with key of `"*"` and value of `"$inherit"`.
	 *
	 * For example given the following fragment of HTML
	 *
	 *
	 * ```html
	 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
	 *   <form ng-model-options="{ updateOn: 'blur', "*": '$inherit' }">
	 *     <input ng-model-options="{ updateOn: 'default', "*": '$inherit' }" />
	 *   </form>
	 * </div>
	 * ```
	 *
	 * the `input` element will have the following settings
	 *
	 * ```js
	 * { allowInvalid: true, updateOn: 'default', debounce: 200 }
	 * ```
	 *
	 * Notice that the `debounce` setting now inherits the value from the outer `<div>` element.
	 *
	 * If you are creating a reusable component then you should be careful when using `"*": "$inherit"`
	 * since you may inadvertently inherit a setting in the future that changes the behavior of your component.
	 *
	 *
	 * ## Triggering and debouncing model updates
	 *
	 * The `updateOn` and `debounce` properties allow you to specify a custom list of events that will
	 * trigger a model update and/or a debouncing delay so that the actual update only takes place when
	 * a timer expires; this timer will be reset after another change takes place.
	 *
	 * Given the nature of `ngModelOptions`, the value displayed inside input fields in the view might
	 * be different from the value in the actual model. This means that if you update the model you
	 * should also invoke {@link ngModel.NgModelController#$rollbackViewValue} on the relevant input field in
	 * order to make sure it is synchronized with the model and that any debounced action is canceled.
	 *
	 * The easiest way to reference the control's {@link ngModel.NgModelController#$rollbackViewValue}
	 * method is by making sure the input is placed inside a form that has a `name` attribute. This is
	 * important because `form` controllers are published to the related scope under the name in their
	 * `name` attribute.
	 *
	 * Any pending changes will take place immediately when an enclosing form is submitted via the
	 * `submit` event. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
	 * to have access to the updated model.
	 *
	 * ### Overriding immediate updates
	 *
	 * The following example shows how to override immediate updates. Changes on the inputs within the
	 * form will update the model only when the control loses focus (blur event). If `escape` key is
	 * pressed while the input field is focused, the value is reset to the value in the current model.
	 *
	 * <example name="ngModelOptions-directive-blur" module="optionsExample">
	 *   <file name="index.html">
	 *     <div ng-controller="ExampleController">
	 *       <form name="userForm">
	 *         <label>
	 *           Name:
	 *           <input type="text" name="userName"
	 *                  ng-model="user.name"
	 *                  ng-model-options="{ updateOn: 'blur' }"
	 *                  ng-keyup="cancel($event)" />
	 *         </label><br />
	 *         <label>
	 *           Other data:
	 *           <input type="text" ng-model="user.data" />
	 *         </label><br />
	 *       </form>
	 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
	 *     </div>
	 *   </file>
	 *   <file name="app.js">
	 *     angular.module('optionsExample', [])
	 *       .controller('ExampleController', ['$scope', function($scope) {
	 *         $scope.user = { name: 'say', data: '' };
	 *
	 *         $scope.cancel = function(e) {
	 *           if (e.keyCode === 27) {
	 *             $scope.userForm.userName.$rollbackViewValue();
	 *           }
	 *         };
	 *       }]);
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     var model = element(by.binding('user.name'));
	 *     var input = element(by.model('user.name'));
	 *     var other = element(by.model('user.data'));
	 *
	 *     it('should allow custom events', function() {
	 *       input.sendKeys(' hello');
	 *       input.click();
	 *       expect(model.getText()).toEqual('say');
	 *       other.click();
	 *       expect(model.getText()).toEqual('say hello');
	 *     });
	 *
	 *     it('should $rollbackViewValue when model changes', function() {
	 *       input.sendKeys(' hello');
	 *       expect(input.getAttribute('value')).toEqual('say hello');
	 *       input.sendKeys(protractor.Key.ESCAPE);
	 *       expect(input.getAttribute('value')).toEqual('say');
	 *       other.click();
	 *       expect(model.getText()).toEqual('say');
	 *     });
	 *   </file>
	 * </example>
	 *
	 * ### Debouncing updates
	 *
	 * The next example shows how to debounce model changes. Model will be updated only 1 sec after last change.
	 * If the `Clear` button is pressed, any debounced action is canceled and the value becomes empty.
	 *
	 * <example name="ngModelOptions-directive-debounce" module="optionsExample">
	 *   <file name="index.html">
	 *     <div ng-controller="ExampleController">
	 *       <form name="userForm">
	 *         Name:
	 *         <input type="text" name="userName"
	 *                ng-model="user.name"
	 *                ng-model-options="{ debounce: 1000 }" />
	 *         <button ng-click="userForm.userName.$rollbackViewValue(); user.name=''">Clear</button><br />
	 *       </form>
	 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
	 *     </div>
	 *   </file>
	 *   <file name="app.js">
	 *     angular.module('optionsExample', [])
	 *       .controller('ExampleController', ['$scope', function($scope) {
	 *         $scope.user = { name: 'say' };
	 *       }]);
	 *   </file>
	 * </example>
	 *
	 * ### Default events, extra triggers, and catch-all debounce values
	 *
	 * This example shows the relationship between "default" update events and
	 * additional `updateOn` triggers.
	 *
	 * `default` events are those that are bound to the control, and when fired, update the `$viewValue`
	 * via {@link ngModel.NgModelController#$setViewValue $setViewValue}. Every event that is not listed
	 * in `updateOn` is considered a "default" event, since different control types have different
	 * default events.
	 *
	 * The control in this example updates by "default", "click", and "blur", with different `debounce`
	 * values. You can see that "click" doesn't have an individual `debounce` value -
	 * therefore it uses the `*` debounce value.
	 *
	 * There is also a button that calls {@link ngModel.NgModelController#$setViewValue $setViewValue}
	 * directly with a "custom" event. Since "custom" is not defined in the `updateOn` list,
	 * it is considered a "default" event and will update the
	 * control if "default" is defined in `updateOn`, and will receive the "default" debounce value.
	 * Note that this is just to illustrate how custom controls would possibly call `$setViewValue`.
	 *
	 * You can change the `updateOn` and `debounce` configuration to test different scenarios. This
	 * is done with {@link ngModel.NgModelController#$overrideModelOptions $overrideModelOptions}.
	 *
	  <example name="ngModelOptions-advanced" module="optionsExample">
		<file name="index.html">
		   <model-update-demo></model-update-demo>
		</file>
		<file name="app.js">
		  angular.module('optionsExample', [])
			.component('modelUpdateDemo', {
			  templateUrl: 'template.html',
			  controller: function() {
				this.name = 'Chinua';
	
				this.options = {
				  updateOn: 'default blur click',
				  debounce: {
					default: 2000,
					blur: 0,
					'*': 1000
				  }
				};
	
				this.updateEvents = function() {
				  var eventList = this.options.updateOn.split(' ');
				  eventList.push('*');
				  var events = {};
	
				  for (var i = 0; i < eventList.length; i++) {
					events[eventList[i]] = this.options.debounce[eventList[i]];
				  }
	
				  this.events = events;
				};
	
				this.updateOptions = function() {
				  var options = angular.extend(this.options, {
					updateOn: Object.keys(this.events).join(' ').replace('*', ''),
					debounce: this.events
				  });
	
				  this.form.input.$overrideModelOptions(options);
				};
	
				// Initialize the event form
				this.updateEvents();
			  }
			});
		</file>
		<file name="template.html">
		  <form name="$ctrl.form">
			Input: <input type="text" name="input" ng-model="$ctrl.name" ng-model-options="$ctrl.options" />
		  </form>
		  Model: <tt>{{$ctrl.name}}</tt>
		  <hr>
		  <button ng-click="$ctrl.form.input.$setViewValue('some value', 'custom')">Trigger setViewValue with 'some value' and 'custom' event</button>
	
		  <hr>
		  <form ng-submit="$ctrl.updateOptions()">
			<b>updateOn</b><br>
			<input type="text" ng-model="$ctrl.options.updateOn" ng-change="$ctrl.updateEvents()" ng-model-options="{debounce: 500}">
	
			<table>
			  <tr>
				<th>Option</th>
				<th>Debounce value</th>
			  </tr>
			  <tr ng-repeat="(key, value) in $ctrl.events">
				<td>{{key}}</td>
				<td><input type="number" ng-model="$ctrl.events[key]" /></td>
			  </tr>
			</table>
	
			<br>
			<input type="submit" value="Update options">
		  </form>
		</file>
	  </example>
	 *
	 *
	 * ## Model updates and validation
	 *
	 * The default behaviour in `ngModel` is that the model value is set to `undefined` when the
	 * validation determines that the value is invalid. By setting the `allowInvalid` property to true,
	 * the model will still be updated even if the value is invalid.
	 *
	 *
	 * ## Connecting to the scope
	 *
	 * By setting the `getterSetter` property to true you are telling ngModel that the `ngModel` expression
	 * on the scope refers to a "getter/setter" function rather than the value itself.
	 *
	 * The following example shows how to bind to getter/setters:
	 *
	 * <example name="ngModelOptions-directive-getter-setter" module="getterSetterExample">
	 *   <file name="index.html">
	 *     <div ng-controller="ExampleController">
	 *       <form name="userForm">
	 *         <label>
	 *           Name:
	 *           <input type="text" name="userName"
	 *                  ng-model="user.name"
	 *                  ng-model-options="{ getterSetter: true }" />
	 *         </label>
	 *       </form>
	 *       <pre>user.name = <span ng-bind="user.name()"></span></pre>
	 *     </div>
	 *   </file>
	 *   <file name="app.js">
	 *     angular.module('getterSetterExample', [])
	 *       .controller('ExampleController', ['$scope', function($scope) {
	 *         var _name = 'Brian';
	 *         $scope.user = {
	 *           name: function(newName) {
	 *             return angular.isDefined(newName) ? (_name = newName) : _name;
	 *           }
	 *         };
	 *       }]);
	 *   </file>
	 * </example>
	 *
	 *
	 * ## Programmatically changing options
	 *
	 * The `ngModelOptions` expression is only evaluated once when the directive is linked; it is not
	 * watched for changes. However, it is possible to override the options on a single
	 * {@link ngModel.NgModelController} instance with
	 * {@link ngModel.NgModelController#$overrideModelOptions `NgModelController#$overrideModelOptions()`}.
	 * See also the example for
	 * {@link ngModelOptions#default-events-extra-triggers-and-catch-all-debounce-values
	 * Default events, extra triggers, and catch-all debounce values}.
	 *
	 *
	 * ## Specifying timezones
	 *
	 * You can specify the timezone that date/time input directives expect by providing its name in the
	 * `timezone` property.
	 *
	 *
	 * ## Formatting the value of `time` and `datetime-local`
	 *
	 * With the options `timeSecondsFormat` and `timeStripZeroSeconds` it is possible to adjust the value
	 * that is displayed in the control. Note that browsers may apply their own formatting
	 * in the user interface.
	 *
	   <example name="ngModelOptions-time-format" module="timeExample">
		 <file name="index.html">
		   <time-example></time-example>
		 </file>
		 <file name="script.js">
			angular.module('timeExample', [])
			  .component('timeExample', {
				templateUrl: 'timeExample.html',
				controller: function() {
				  this.time = new Date(1970, 0, 1, 14, 57, 0);
	
				  this.options = {
					timeSecondsFormat: 'ss',
					timeStripZeroSeconds: true
				  };
	
				  this.optionChange = function() {
					this.timeForm.timeFormatted.$overrideModelOptions(this.options);
					this.time = new Date(this.time);
				  };
				}
			  });
		 </file>
		 <file name="timeExample.html">
		   <form name="$ctrl.timeForm">
			 <strong>Default</strong>:
			 <input type="time" ng-model="$ctrl.time" step="any" /><br>
			 <strong>With options</strong>:
			 <input type="time" name="timeFormatted" ng-model="$ctrl.time" step="any" ng-model-options="$ctrl.options" />
			 <br>
	
			 Options:<br>
			 <code>timeSecondsFormat</code>:
			 <input
			   type="text"
			   ng-model="$ctrl.options.timeSecondsFormat"
			   ng-change="$ctrl.optionChange()">
			 <br>
			 <code>timeStripZeroSeconds</code>:
			 <input
			   type="checkbox"
			   ng-model="$ctrl.options.timeStripZeroSeconds"
			   ng-change="$ctrl.optionChange()">
			</form>
		  </file>
	 *  </example>
	 *
	 * @param {Object} ngModelOptions options to apply to {@link ngModel} directives on this element and
	 *   and its descendents.
	 *
	 * **General options**:
	 *
	 *   - `updateOn`: string specifying which event should the input be bound to. You can set several
	 *     events using an space delimited list. There is a special event called `default` that
	 *     matches the default events belonging to the control. These are the events that are bound to
	 *     the control, and when fired, update the `$viewValue` via `$setViewValue`.
	 *
	 *     `ngModelOptions` considers every event that is not listed in `updateOn` a "default" event,
	 *     since different control types use different default events.
	 *
	 *     See also the section {@link ngModelOptions#triggering-and-debouncing-model-updates
	 *     Triggering and debouncing model updates}.
	 *
	 *   - `debounce`: integer value which contains the debounce model update value in milliseconds. A
	 *     value of 0 triggers an immediate update. If an object is supplied instead, you can specify a
	 *     custom value for each event. For example:
	 *     ```
	 *     ng-model-options="{
	 *       updateOn: 'default blur',
	 *       debounce: { 'default': 500, 'blur': 0 }
	 *     }"
	 *     ```
	 *     You can use the `*` key to specify a debounce value that applies to all events that are not
	 *     specifically listed. In the following example, `mouseup` would have a debounce delay of 1000:
	 *     ```
	 *     ng-model-options="{
	 *       updateOn: 'default blur mouseup',
	 *       debounce: { 'default': 500, 'blur': 0, '*': 1000 }
	 *     }"
	 *     ```
	 *   - `allowInvalid`: boolean value which indicates that the model can be set with values that did
	 *     not validate correctly instead of the default behavior of setting the model to undefined.
	 *   - `getterSetter`: boolean value which determines whether or not to treat functions bound to
	 *     `ngModel` as getters/setters.
	 *
	 *
	 *  **Input-type specific options**:
	 *
	 *   - `timezone`: Defines the timezone to be used to read/write the `Date` instance in the model for
	 *     `<input type="date" />`, `<input type="time" />`, ... . It understands UTC/GMT and the
	 *     continental US time zone abbreviations, but for general use, use a time zone offset, for
	 *     example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
	 *     If not specified, the timezone of the browser will be used.
	 *     Note that changing the timezone will have no effect on the current date, and is only applied after
	 *     the next input / model change.
	 *
	 *   - `timeSecondsFormat`: Defines if the `time` and `datetime-local` types should show seconds and
	 *     milliseconds. The option follows the format string of {@link date date filter}.
	 *     By default, the options is `undefined` which is equal to `'ss.sss'` (seconds and milliseconds).
	 *     The other options are `'ss'` (strips milliseconds), and `''` (empty string), which strips both
	 *     seconds and milliseconds.
	 *     Note that browsers that support `time` and `datetime-local` require the hour and minutes
	 *     part of the time string, and may show the value differently in the user interface.
	 *     {@link ngModelOptions#formatting-the-value-of-time-and-datetime-local- See the example}.
	 *
	 *   - `timeStripZeroSeconds`: Defines if the `time` and `datetime-local` types should strip the
	 *     seconds and milliseconds from the formatted value if they are zero. This option is applied
	 *     after `timeSecondsFormat`.
	 *     This option can be used to make the formatting consistent over different browsers, as some
	 *     browsers with support for `time` will natively hide the milliseconds and
	 *     seconds if they are zero, but others won't, and browsers that don't implement these input
	 *     types will always show the full string.
	 *     {@link ngModelOptions#formatting-the-value-of-time-and-datetime-local- See the example}.
	 *
	 */
	var ngModelOptionsDirective = function () {
		NgModelOptionsController.$inject = ['$attrs', '$scope'];
		function NgModelOptionsController($attrs, $scope) {
			this.$$attrs = $attrs;
			this.$$scope = $scope;
		}
		NgModelOptionsController.prototype = {
			$onInit: function () {
				var parentOptions = this.parentCtrl ? this.parentCtrl.$options : defaultModelOptions;
				var modelOptionsDefinition = this.$$scope.$eval(this.$$attrs.ngModelOptions);

				this.$options = parentOptions.createChild(modelOptionsDefinition);
			}
		};

		return {
			restrict: 'A',
			// ngModelOptions needs to run before ngModel and input directives
			priority: 10,
			require: { parentCtrl: '?^^ngModelOptions' },
			bindToController: true,
			controller: NgModelOptionsController
		};
	};


	// shallow copy over values from `src` that are not already specified on `dst`
	function defaults(dst, src) {
		forEach(src, function (value, key) {
			if (!isDefined(dst[key])) {
				dst[key] = value;
			}
		});
	}

	/**
	 * @ngdoc directive
	 * @name ngNonBindable
	 * @restrict AC
	 * @priority 1000
	 * @element ANY
	 *
	 * @description
	 * The `ngNonBindable` directive tells AngularJS not to compile or bind the contents of the current
	 * DOM element, including directives on the element itself that have a lower priority than
	 * `ngNonBindable`. This is useful if the element contains what appears to be AngularJS directives
	 * and bindings but which should be ignored by AngularJS. This could be the case if you have a site
	 * that displays snippets of code, for instance.
	 *
	 * @example
	 * In this example there are two locations where a simple interpolation binding (`{{}}`) is present,
	 * but the one wrapped in `ngNonBindable` is left alone.
	 *
	  <example name="ng-non-bindable">
		<file name="index.html">
		  <div>Normal: {{1 + 2}}</div>
		  <div ng-non-bindable>Ignored: {{1 + 2}}</div>
		</file>
		<file name="protractor.js" type="protractor">
		 it('should check ng-non-bindable', function() {
		   expect(element(by.binding('1 + 2')).getText()).toContain('3');
		   expect(element.all(by.css('div')).last().getText()).toMatch(/1 \+ 2/);
		 });
		</file>
	  </example>
	 */
	var ngNonBindableDirective = ngDirective({ terminal: true, priority: 1000 });

	/* exported ngOptionsDirective */

	/* global jqLiteRemove */

	var ngOptionsMinErr = minErr('ngOptions');

	/**
	 * @ngdoc directive
	 * @name ngOptions
	 * @restrict A
	 *
	 * @description
	 *
	 * The `ngOptions` attribute can be used to dynamically generate a list of `<option>`
	 * elements for the `<select>` element using the array or object obtained by evaluating the
	 * `ngOptions` comprehension expression.
	 *
	 * In many cases, {@link ng.directive:ngRepeat ngRepeat} can be used on `<option>` elements instead of
	 * `ngOptions` to achieve a similar result. However, `ngOptions` provides some benefits:
	 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
	 * comprehension expression
	 * - reduced memory consumption by not creating a new scope for each repeated instance
	 * - increased render speed by creating the options in a documentFragment instead of individually
	 *
	 * When an item in the `<select>` menu is selected, the array element or object property
	 * represented by the selected option will be bound to the model identified by the `ngModel`
	 * directive.
	 *
	 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
	 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
	 * option. See example below for demonstration.
	 *
	 * ## Complex Models (objects or collections)
	 *
	 * By default, `ngModel` watches the model by reference, not value. This is important to know when
	 * binding the select to a model that is an object or a collection.
	 *
	 * One issue occurs if you want to preselect an option. For example, if you set
	 * the model to an object that is equal to an object in your collection, `ngOptions` won't be able to set the selection,
	 * because the objects are not identical. So by default, you should always reference the item in your collection
	 * for preselections, e.g.: `$scope.selected = $scope.collection[3]`.
	 *
	 * Another solution is to use a `track by` clause, because then `ngOptions` will track the identity
	 * of the item not by reference, but by the result of the `track by` expression. For example, if your
	 * collection items have an id property, you would `track by item.id`.
	 *
	 * A different issue with objects or collections is that ngModel won't detect if an object property or
	 * a collection item changes. For that reason, `ngOptions` additionally watches the model using
	 * `$watchCollection`, when the expression contains a `track by` clause or the the select has the `multiple` attribute.
	 * This allows ngOptions to trigger a re-rendering of the options even if the actual object/collection
	 * has not changed identity, but only a property on the object or an item in the collection changes.
	 *
	 * Note that `$watchCollection` does a shallow comparison of the properties of the object (or the items in the collection
	 * if the model is an array). This means that changing a property deeper than the first level inside the
	 * object/collection will not trigger a re-rendering.
	 *
	 * ## `select` **`as`**
	 *
	 * Using `select` **`as`** will bind the result of the `select` expression to the model, but
	 * the value of the `<select>` and `<option>` html elements will be either the index (for array data sources)
	 * or property name (for object data sources) of the value within the collection. If a **`track by`** expression
	 * is used, the result of that expression will be set as the value of the `option` and `select` elements.
	 *
	 *
	 * ### `select` **`as`** and **`track by`**
	 *
	 * <div class="alert alert-warning">
	 * Be careful when using `select` **`as`** and **`track by`** in the same expression.
	 * </div>
	 *
	 * Given this array of items on the $scope:
	 *
	 * ```js
	 * $scope.items = [{
	 *   id: 1,
	 *   label: 'aLabel',
	 *   subItem: { name: 'aSubItem' }
	 * }, {
	 *   id: 2,
	 *   label: 'bLabel',
	 *   subItem: { name: 'bSubItem' }
	 * }];
	 * ```
	 *
	 * This will work:
	 *
	 * ```html
	 * <select ng-options="item as item.label for item in items track by item.id" ng-model="selected"></select>
	 * ```
	 * ```js
	 * $scope.selected = $scope.items[0];
	 * ```
	 *
	 * but this will not work:
	 *
	 * ```html
	 * <select ng-options="item.subItem as item.label for item in items track by item.id" ng-model="selected"></select>
	 * ```
	 * ```js
	 * $scope.selected = $scope.items[0].subItem;
	 * ```
	 *
	 * In both examples, the **`track by`** expression is applied successfully to each `item` in the
	 * `items` array. Because the selected option has been set programmatically in the controller, the
	 * **`track by`** expression is also applied to the `ngModel` value. In the first example, the
	 * `ngModel` value is `items[0]` and the **`track by`** expression evaluates to `items[0].id` with
	 * no issue. In the second example, the `ngModel` value is `items[0].subItem` and the **`track by`**
	 * expression evaluates to `items[0].subItem.id` (which is undefined). As a result, the model value
	 * is not matched against any `<option>` and the `<select>` appears as having no selected value.
	 *
	 *
	 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
	 * @param {comprehension_expression} ngOptions in one of the following forms:
	 *
	 *   * for array data sources:
	 *     * `label` **`for`** `value` **`in`** `array`
	 *     * `select` **`as`** `label` **`for`** `value` **`in`** `array`
	 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array`
	 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array`
	 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
	 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
	 *     * `label` **`for`** `value` **`in`** `array` | orderBy:`orderexpr` **`track by`** `trackexpr`
	 *        (for including a filter with `track by`)
	 *   * for object data sources:
	 *     * `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
	 *     * `select` **`as`** `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
	 *     * `label` **`group by`** `group` **`for (`**`key`**`,`** `value`**`) in`** `object`
	 *     * `label` **`disable when`** `disable` **`for (`**`key`**`,`** `value`**`) in`** `object`
	 *     * `select` **`as`** `label` **`group by`** `group`
	 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
	 *     * `select` **`as`** `label` **`disable when`** `disable`
	 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
	 *
	 * Where:
	 *
	 *   * `array` / `object`: an expression which evaluates to an array / object to iterate over.
	 *   * `value`: local variable which will refer to each item in the `array` or each property value
	 *      of `object` during iteration.
	 *   * `key`: local variable which will refer to a property name in `object` during iteration.
	 *   * `label`: The result of this expression will be the label for `<option>` element. The
	 *     `expression` will most likely refer to the `value` variable (e.g. `value.propertyName`).
	 *   * `select`: The result of this expression will be bound to the model of the parent `<select>`
	 *      element. If not specified, `select` expression will default to `value`.
	 *   * `group`: The result of this expression will be used to group options using the `<optgroup>`
	 *      DOM element.
	 *   * `disable`: The result of this expression will be used to disable the rendered `<option>`
	 *      element. Return `true` to disable.
	 *   * `trackexpr`: Used when working with an array of objects. The result of this expression will be
	 *      used to identify the objects in the array. The `trackexpr` will most likely refer to the
	 *     `value` variable (e.g. `value.propertyName`). With this the selection is preserved
	 *      even when the options are recreated (e.g. reloaded from the server).
	 * @param {string=} name Property name of the form under which the control is published.
	 * @param {string=} required The control is considered valid only if value is entered.
	 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
	 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
	 *    `required` when you want to data-bind to the `required` attribute.
	 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
	 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
	 *
	 * @example
		<example module="selectExample" name="select">
		  <file name="index.html">
			<script>
			angular.module('selectExample', [])
			  .controller('ExampleController', ['$scope', function($scope) {
				$scope.colors = [
				  {name:'black', shade:'dark'},
				  {name:'white', shade:'light', notAnOption: true},
				  {name:'red', shade:'dark'},
				  {name:'blue', shade:'dark', notAnOption: true},
				  {name:'yellow', shade:'light', notAnOption: false}
				];
				$scope.myColor = $scope.colors[2]; // red
			  }]);
			</script>
			<div ng-controller="ExampleController">
			  <ul>
				<li ng-repeat="color in colors">
				  <label>Name: <input ng-model="color.name"></label>
				  <label><input type="checkbox" ng-model="color.notAnOption"> Disabled?</label>
				  <button ng-click="colors.splice($index, 1)" aria-label="Remove">X</button>
				</li>
				<li>
				  <button ng-click="colors.push({})">add</button>
				</li>
			  </ul>
			  <hr/>
			  <label>Color (null not allowed):
				<select ng-model="myColor" ng-options="color.name for color in colors"></select>
			  </label><br/>
			  <label>Color (null allowed):
			  <span  class="nullable">
				<select ng-model="myColor" ng-options="color.name for color in colors">
				  <option value="">-- choose color --</option>
				</select>
			  </span></label><br/>
	
			  <label>Color grouped by shade:
				<select ng-model="myColor" ng-options="color.name group by color.shade for color in colors">
				</select>
			  </label><br/>
	
			  <label>Color grouped by shade, with some disabled:
				<select ng-model="myColor"
					  ng-options="color.name group by color.shade disable when color.notAnOption for color in colors">
				</select>
			  </label><br/>
	
	
	
			  Select <button ng-click="myColor = { name:'not in list', shade: 'other' }">bogus</button>.
			  <br/>
			  <hr/>
			  Currently selected: {{ {selected_color:myColor} }}
			  <div style="border:solid 1px black; height:20px"
				   ng-style="{'background-color':myColor.name}">
			  </div>
			</div>
		  </file>
		  <file name="protractor.js" type="protractor">
			 it('should check ng-options', function() {
			   expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('red');
			   element.all(by.model('myColor')).first().click();
			   element.all(by.css('select[ng-model="myColor"] option')).first().click();
			   expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('black');
			   element(by.css('.nullable select[ng-model="myColor"]')).click();
			   element.all(by.css('.nullable select[ng-model="myColor"] option')).first().click();
			   expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('null');
			 });
		  </file>
		</example>
	 */

	/* eslint-disable max-len */
	//                     //00001111111111000000000002222222222000000000000000000000333333333300000000000000000000000004444444444400000000000005555555555555000000000666666666666600000007777777777777000000000000000888888888800000000000000000009999999999
	var NG_OPTIONS_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+group\s+by\s+([\s\S]+?))?(?:\s+disable\s+when\s+([\s\S]+?))?\s+for\s+(?:([$\w][$\w]*)|(?:\(\s*([$\w][$\w]*)\s*,\s*([$\w][$\w]*)\s*\)))\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?$/;
	// 1: value expression (valueFn)
	// 2: label expression (displayFn)
	// 3: group by expression (groupByFn)
	// 4: disable when expression (disableWhenFn)
	// 5: array item variable name
	// 6: object item key variable name
	// 7: object item value variable name
	// 8: collection expression
	// 9: track by expression
	/* eslint-enable */


	var ngOptionsDirective = ['$compile', '$document', '$parse', function ($compile, $document, $parse) {

		function parseOptionsExpression(optionsExp, selectElement, scope) {

			var match = optionsExp.match(NG_OPTIONS_REGEXP);
			if (!(match)) {
				throw ngOptionsMinErr('iexp',
					'Expected expression in form of ' +
					'\'_select_ (as _label_)? for (_key_,)?_value_ in _collection_\'' +
					' but got \'{0}\'. Element: {1}',
					optionsExp, startingTag(selectElement));
			}

			// Extract the parts from the ngOptions expression

			// The variable name for the value of the item in the collection
			var valueName = match[5] || match[7];
			// The variable name for the key of the item in the collection
			var keyName = match[6];

			// An expression that generates the viewValue for an option if there is a label expression
			var selectAs = / as /.test(match[0]) && match[1];
			// An expression that is used to track the id of each object in the options collection
			var trackBy = match[9];
			// An expression that generates the viewValue for an option if there is no label expression
			var valueFn = $parse(match[2] ? match[1] : valueName);
			var selectAsFn = selectAs && $parse(selectAs);
			var viewValueFn = selectAsFn || valueFn;
			var trackByFn = trackBy && $parse(trackBy);

			// Get the value by which we are going to track the option
			// if we have a trackFn then use that (passing scope and locals)
			// otherwise just hash the given viewValue
			var getTrackByValueFn = trackBy ?
				function (value, locals) { return trackByFn(scope, locals); } :
				function getHashOfValue(value) { return hashKey(value); };
			var getTrackByValue = function (value, key) {
				return getTrackByValueFn(value, getLocals(value, key));
			};

			var displayFn = $parse(match[2] || match[1]);
			var groupByFn = $parse(match[3] || '');
			var disableWhenFn = $parse(match[4] || '');
			var valuesFn = $parse(match[8]);

			var locals = {};
			var getLocals = keyName ? function (value, key) {
				locals[keyName] = key;
				locals[valueName] = value;
				return locals;
			} : function (value) {
				locals[valueName] = value;
				return locals;
			};


			function Option(selectValue, viewValue, label, group, disabled) {
				this.selectValue = selectValue;
				this.viewValue = viewValue;
				this.label = label;
				this.group = group;
				this.disabled = disabled;
			}

			function getOptionValuesKeys(optionValues) {
				var optionValuesKeys;

				if (!keyName && isArrayLike(optionValues)) {
					optionValuesKeys = optionValues;
				} else {
					// if object, extract keys, in enumeration order, unsorted
					optionValuesKeys = [];
					for (var itemKey in optionValues) {
						if (optionValues.hasOwnProperty(itemKey) && itemKey.charAt(0) !== '$') {
							optionValuesKeys.push(itemKey);
						}
					}
				}
				return optionValuesKeys;
			}

			return {
				trackBy: trackBy,
				getTrackByValue: getTrackByValue,
				getWatchables: $parse(valuesFn, function (optionValues) {
					// Create a collection of things that we would like to watch (watchedArray)
					// so that they can all be watched using a single $watchCollection
					// that only runs the handler once if anything changes
					var watchedArray = [];
					optionValues = optionValues || [];

					var optionValuesKeys = getOptionValuesKeys(optionValues);
					var optionValuesLength = optionValuesKeys.length;
					for (var index = 0; index < optionValuesLength; index++) {
						var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
						var value = optionValues[key];

						var locals = getLocals(value, key);
						var selectValue = getTrackByValueFn(value, locals);
						watchedArray.push(selectValue);

						// Only need to watch the displayFn if there is a specific label expression
						if (match[2] || match[1]) {
							var label = displayFn(scope, locals);
							watchedArray.push(label);
						}

						// Only need to watch the disableWhenFn if there is a specific disable expression
						if (match[4]) {
							var disableWhen = disableWhenFn(scope, locals);
							watchedArray.push(disableWhen);
						}
					}
					return watchedArray;
				}),

				getOptions: function () {

					var optionItems = [];
					var selectValueMap = {};

					// The option values were already computed in the `getWatchables` fn,
					// which must have been called to trigger `getOptions`
					var optionValues = valuesFn(scope) || [];
					var optionValuesKeys = getOptionValuesKeys(optionValues);
					var optionValuesLength = optionValuesKeys.length;

					for (var index = 0; index < optionValuesLength; index++) {
						var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
						var value = optionValues[key];
						var locals = getLocals(value, key);
						var viewValue = viewValueFn(scope, locals);
						var selectValue = getTrackByValueFn(viewValue, locals);
						var label = displayFn(scope, locals);
						var group = groupByFn(scope, locals);
						var disabled = disableWhenFn(scope, locals);
						var optionItem = new Option(selectValue, viewValue, label, group, disabled);

						optionItems.push(optionItem);
						selectValueMap[selectValue] = optionItem;
					}

					return {
						items: optionItems,
						selectValueMap: selectValueMap,
						getOptionFromViewValue: function (value) {
							return selectValueMap[getTrackByValue(value)];
						},
						getViewValueFromOption: function (option) {
							// If the viewValue could be an object that may be mutated by the application,
							// we need to make a copy and not return the reference to the value on the option.
							return trackBy ? copy(option.viewValue) : option.viewValue;
						}
					};
				}
			};
		}


		// Support: IE 9 only
		// We can't just jqLite('<option>') since jqLite is not smart enough
		// to create it in <select> and IE barfs otherwise.
		var optionTemplate = window.document.createElement('option'),
			optGroupTemplate = window.document.createElement('optgroup');

		function ngOptionsPostLink(scope, selectElement, attr, ctrls) {

			var selectCtrl = ctrls[0];
			var ngModelCtrl = ctrls[1];
			var multiple = attr.multiple;

			// The emptyOption allows the application developer to provide their own custom "empty"
			// option when the viewValue does not match any of the option values.
			for (var i = 0, children = selectElement.children(), ii = children.length; i < ii; i++) {
				if (children[i].value === '') {
					selectCtrl.hasEmptyOption = true;
					selectCtrl.emptyOption = children.eq(i);
					break;
				}
			}

			// The empty option will be compiled and rendered before we first generate the options
			selectElement.empty();

			var providedEmptyOption = !!selectCtrl.emptyOption;

			var unknownOption = jqLite(optionTemplate.cloneNode(false));
			unknownOption.val('?');

			var options;
			var ngOptions = parseOptionsExpression(attr.ngOptions, selectElement, scope);
			// This stores the newly created options before they are appended to the select.
			// Since the contents are removed from the fragment when it is appended,
			// we only need to create it once.
			var listFragment = $document[0].createDocumentFragment();

			// Overwrite the implementation. ngOptions doesn't use hashes
			selectCtrl.generateUnknownOptionValue = function (val) {
				return '?';
			};

			// Update the controller methods for multiple selectable options
			if (!multiple) {

				selectCtrl.writeValue = function writeNgOptionsValue(value) {
					// The options might not be defined yet when ngModel tries to render
					if (!options) return;

					var selectedOption = selectElement[0].options[selectElement[0].selectedIndex];
					var option = options.getOptionFromViewValue(value);

					// Make sure to remove the selected attribute from the previously selected option
					// Otherwise, screen readers might get confused
					if (selectedOption) selectedOption.removeAttribute('selected');

					if (option) {
						// Don't update the option when it is already selected.
						// For example, the browser will select the first option by default. In that case,
						// most properties are set automatically - except the `selected` attribute, which we
						// set always

						if (selectElement[0].value !== option.selectValue) {
							selectCtrl.removeUnknownOption();

							selectElement[0].value = option.selectValue;
							option.element.selected = true;
						}

						option.element.setAttribute('selected', 'selected');
					} else {
						selectCtrl.selectUnknownOrEmptyOption(value);
					}
				};

				selectCtrl.readValue = function readNgOptionsValue() {

					var selectedOption = options.selectValueMap[selectElement.val()];

					if (selectedOption && !selectedOption.disabled) {
						selectCtrl.unselectEmptyOption();
						selectCtrl.removeUnknownOption();
						return options.getViewValueFromOption(selectedOption);
					}
					return null;
				};

				// If we are using `track by` then we must watch the tracked value on the model
				// since ngModel only watches for object identity change
				// FIXME: When a user selects an option, this watch will fire needlessly
				if (ngOptions.trackBy) {
					scope.$watch(
						function () { return ngOptions.getTrackByValue(ngModelCtrl.$viewValue); },
						function () { ngModelCtrl.$render(); }
					);
				}

			} else {

				selectCtrl.writeValue = function writeNgOptionsMultiple(values) {
					// The options might not be defined yet when ngModel tries to render
					if (!options) return;

					// Only set `<option>.selected` if necessary, in order to prevent some browsers from
					// scrolling to `<option>` elements that are outside the `<select>` element's viewport.
					var selectedOptions = values && values.map(getAndUpdateSelectedOption) || [];

					options.items.forEach(function (option) {
						if (option.element.selected && !includes(selectedOptions, option)) {
							option.element.selected = false;
						}
					});
				};


				selectCtrl.readValue = function readNgOptionsMultiple() {
					var selectedValues = selectElement.val() || [],
						selections = [];

					forEach(selectedValues, function (value) {
						var option = options.selectValueMap[value];
						if (option && !option.disabled) selections.push(options.getViewValueFromOption(option));
					});

					return selections;
				};

				// If we are using `track by` then we must watch these tracked values on the model
				// since ngModel only watches for object identity change
				if (ngOptions.trackBy) {

					scope.$watchCollection(function () {
						if (isArray(ngModelCtrl.$viewValue)) {
							return ngModelCtrl.$viewValue.map(function (value) {
								return ngOptions.getTrackByValue(value);
							});
						}
					}, function () {
						ngModelCtrl.$render();
					});

				}
			}

			if (providedEmptyOption) {

				// compile the element since there might be bindings in it
				$compile(selectCtrl.emptyOption)(scope);

				selectElement.prepend(selectCtrl.emptyOption);

				if (selectCtrl.emptyOption[0].nodeType === NODE_TYPE_COMMENT) {
					// This means the empty option has currently no actual DOM node, probably because
					// it has been modified by a transclusion directive.
					selectCtrl.hasEmptyOption = false;

					// Redefine the registerOption function, which will catch
					// options that are added by ngIf etc. (rendering of the node is async because of
					// lazy transclusion)
					selectCtrl.registerOption = function (optionScope, optionEl) {
						if (optionEl.val() === '') {
							selectCtrl.hasEmptyOption = true;
							selectCtrl.emptyOption = optionEl;
							selectCtrl.emptyOption.removeClass('ng-scope');
							// This ensures the new empty option is selected if previously no option was selected
							ngModelCtrl.$render();

							optionEl.on('$destroy', function () {
								var needsRerender = selectCtrl.$isEmptyOptionSelected();

								selectCtrl.hasEmptyOption = false;
								selectCtrl.emptyOption = undefined;

								if (needsRerender) ngModelCtrl.$render();
							});
						}
					};

				} else {
					// remove the class, which is added automatically because we recompile the element and it
					// becomes the compilation root
					selectCtrl.emptyOption.removeClass('ng-scope');
				}

			}

			// We will re-render the option elements if the option values or labels change
			scope.$watchCollection(ngOptions.getWatchables, updateOptions);

			// ------------------------------------------------------------------ //

			function addOptionElement(option, parent) {
				var optionElement = optionTemplate.cloneNode(false);
				parent.appendChild(optionElement);
				updateOptionElement(option, optionElement);
			}

			function getAndUpdateSelectedOption(viewValue) {
				var option = options.getOptionFromViewValue(viewValue);
				var element = option && option.element;

				if (element && !element.selected) element.selected = true;

				return option;
			}

			function updateOptionElement(option, element) {
				option.element = element;
				element.disabled = option.disabled;
				// Support: IE 11 only, Edge 12-13 only
				// NOTE: The label must be set before the value, otherwise IE 11 & Edge create unresponsive
				// selects in certain circumstances when multiple selects are next to each other and display
				// the option list in listbox style, i.e. the select is [multiple], or specifies a [size].
				// See https://github.com/angular/angular.js/issues/11314 for more info.
				// This is unfortunately untestable with unit / e2e tests
				if (option.label !== element.label) {
					element.label = option.label;
					element.textContent = option.label;
				}
				element.value = option.selectValue;
			}

			function updateOptions() {
				var previousValue = options && selectCtrl.readValue();

				// We must remove all current options, but cannot simply set innerHTML = null
				// since the providedEmptyOption might have an ngIf on it that inserts comments which we
				// must preserve.
				// Instead, iterate over the current option elements and remove them or their optgroup
				// parents
				if (options) {

					for (var i = options.items.length - 1; i >= 0; i--) {
						var option = options.items[i];
						if (isDefined(option.group)) {
							jqLiteRemove(option.element.parentNode);
						} else {
							jqLiteRemove(option.element);
						}
					}
				}

				options = ngOptions.getOptions();

				var groupElementMap = {};

				options.items.forEach(function addOption(option) {
					var groupElement;

					if (isDefined(option.group)) {

						// This option is to live in a group
						// See if we have already created this group
						groupElement = groupElementMap[option.group];

						if (!groupElement) {

							groupElement = optGroupTemplate.cloneNode(false);
							listFragment.appendChild(groupElement);

							// Update the label on the group element
							// "null" is special cased because of Safari
							groupElement.label = option.group === null ? 'null' : option.group;

							// Store it for use later
							groupElementMap[option.group] = groupElement;
						}

						addOptionElement(option, groupElement);

					} else {

						// This option is not in a group
						addOptionElement(option, listFragment);
					}
				});

				selectElement[0].appendChild(listFragment);

				ngModelCtrl.$render();

				// Check to see if the value has changed due to the update to the options
				if (!ngModelCtrl.$isEmpty(previousValue)) {
					var nextValue = selectCtrl.readValue();
					var isNotPrimitive = ngOptions.trackBy || multiple;
					if (isNotPrimitive ? !equals(previousValue, nextValue) : previousValue !== nextValue) {
						ngModelCtrl.$setViewValue(nextValue);
						ngModelCtrl.$render();
					}
				}
			}
		}

		return {
			restrict: 'A',
			terminal: true,
			require: ['select', 'ngModel'],
			link: {
				pre: function ngOptionsPreLink(scope, selectElement, attr, ctrls) {
					// Deactivate the SelectController.register method to prevent
					// option directives from accidentally registering themselves
					// (and unwanted $destroy handlers etc.)
					ctrls[0].registerOption = noop;
				},
				post: ngOptionsPostLink
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngPluralize
	 * @restrict EA
	 *
	 * @description
	 * `ngPluralize` is a directive that displays messages according to en-US localization rules.
	 * These rules are bundled with angular.js, but can be overridden
	 * (see {@link guide/i18n AngularJS i18n} dev guide). You configure ngPluralize directive
	 * by specifying the mappings between
	 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
	 * and the strings to be displayed.
	 *
	 * ## Plural categories and explicit number rules
	 * There are two
	 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
	 * in AngularJS's default en-US locale: "one" and "other".
	 *
	 * While a plural category may match many numbers (for example, in en-US locale, "other" can match
	 * any number that is not 1), an explicit number rule can only match one number. For example, the
	 * explicit number rule for "3" matches the number 3. There are examples of plural categories
	 * and explicit number rules throughout the rest of this documentation.
	 *
	 * ## Configuring ngPluralize
	 * You configure ngPluralize by providing 2 attributes: `count` and `when`.
	 * You can also provide an optional attribute, `offset`.
	 *
	 * The value of the `count` attribute can be either a string or an {@link guide/expression
	 * AngularJS expression}; these are evaluated on the current scope for its bound value.
	 *
	 * The `when` attribute specifies the mappings between plural categories and the actual
	 * string to be displayed. The value of the attribute should be a JSON object.
	 *
	 * The following example shows how to configure ngPluralize:
	 *
	 * ```html
	 * <ng-pluralize count="personCount"
					 when="{'0': 'Nobody is viewing.',
	 *                      'one': '1 person is viewing.',
	 *                      'other': '{} people are viewing.'}">
	 * </ng-pluralize>
	 *```
	 *
	 * In the example, `"0: Nobody is viewing."` is an explicit number rule. If you did not
	 * specify this rule, 0 would be matched to the "other" category and "0 people are viewing"
	 * would be shown instead of "Nobody is viewing". You can specify an explicit number rule for
	 * other numbers, for example 12, so that instead of showing "12 people are viewing", you can
	 * show "a dozen people are viewing".
	 *
	 * You can use a set of closed braces (`{}`) as a placeholder for the number that you want substituted
	 * into pluralized strings. In the previous example, AngularJS will replace `{}` with
	 * <span ng-non-bindable>`{{personCount}}`</span>. The closed braces `{}` is a placeholder
	 * for <span ng-non-bindable>{{numberExpression}}</span>.
	 *
	 * If no rule is defined for a category, then an empty string is displayed and a warning is generated.
	 * Note that some locales define more categories than `one` and `other`. For example, fr-fr defines `few` and `many`.
	 *
	 * ## Configuring ngPluralize with offset
	 * The `offset` attribute allows further customization of pluralized text, which can result in
	 * a better user experience. For example, instead of the message "4 people are viewing this document",
	 * you might display "John, Kate and 2 others are viewing this document".
	 * The offset attribute allows you to offset a number by any desired value.
	 * Let's take a look at an example:
	 *
	 * ```html
	 * <ng-pluralize count="personCount" offset=2
	 *               when="{'0': 'Nobody is viewing.',
	 *                      '1': '{{person1}} is viewing.',
	 *                      '2': '{{person1}} and {{person2}} are viewing.',
	 *                      'one': '{{person1}}, {{person2}} and one other person are viewing.',
	 *                      'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
	 * </ng-pluralize>
	 * ```
	 *
	 * Notice that we are still using two plural categories(one, other), but we added
	 * three explicit number rules 0, 1 and 2.
	 * When one person, perhaps John, views the document, "John is viewing" will be shown.
	 * When three people view the document, no explicit number rule is found, so
	 * an offset of 2 is taken off 3, and AngularJS uses 1 to decide the plural category.
	 * In this case, plural category 'one' is matched and "John, Mary and one other person are viewing"
	 * is shown.
	 *
	 * Note that when you specify offsets, you must provide explicit number rules for
	 * numbers from 0 up to and including the offset. If you use an offset of 3, for example,
	 * you must provide explicit number rules for 0, 1, 2 and 3. You must also provide plural strings for
	 * plural categories "one" and "other".
	 *
	 * @param {string|expression} count The variable to be bound to.
	 * @param {string} when The mapping between plural category to its corresponding strings.
	 * @param {number=} offset Offset to deduct from the total number.
	 *
	 * @example
		<example module="pluralizeExample" name="ng-pluralize">
		  <file name="index.html">
			<script>
			  angular.module('pluralizeExample', [])
				.controller('ExampleController', ['$scope', function($scope) {
				  $scope.person1 = 'Igor';
				  $scope.person2 = 'Misko';
				  $scope.personCount = 1;
				}]);
			</script>
			<div ng-controller="ExampleController">
			  <label>Person 1:<input type="text" ng-model="person1" value="Igor" /></label><br/>
			  <label>Person 2:<input type="text" ng-model="person2" value="Misko" /></label><br/>
			  <label>Number of People:<input type="text" ng-model="personCount" value="1" /></label><br/>
	
			  <!--- Example with simple pluralization rules for en locale --->
			  Without Offset:
			  <ng-pluralize count="personCount"
							when="{'0': 'Nobody is viewing.',
								   'one': '1 person is viewing.',
								   'other': '{} people are viewing.'}">
			  </ng-pluralize><br>
	
			  <!--- Example with offset --->
			  With Offset(2):
			  <ng-pluralize count="personCount" offset=2
							when="{'0': 'Nobody is viewing.',
								   '1': '{{person1}} is viewing.',
								   '2': '{{person1}} and {{person2}} are viewing.',
								   'one': '{{person1}}, {{person2}} and one other person are viewing.',
								   'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
			  </ng-pluralize>
			</div>
		  </file>
		  <file name="protractor.js" type="protractor">
			it('should show correct pluralized string', function() {
			  var withoutOffset = element.all(by.css('ng-pluralize')).get(0);
			  var withOffset = element.all(by.css('ng-pluralize')).get(1);
			  var countInput = element(by.model('personCount'));
	
			  expect(withoutOffset.getText()).toEqual('1 person is viewing.');
			  expect(withOffset.getText()).toEqual('Igor is viewing.');
	
			  countInput.clear();
			  countInput.sendKeys('0');
	
			  expect(withoutOffset.getText()).toEqual('Nobody is viewing.');
			  expect(withOffset.getText()).toEqual('Nobody is viewing.');
	
			  countInput.clear();
			  countInput.sendKeys('2');
	
			  expect(withoutOffset.getText()).toEqual('2 people are viewing.');
			  expect(withOffset.getText()).toEqual('Igor and Misko are viewing.');
	
			  countInput.clear();
			  countInput.sendKeys('3');
	
			  expect(withoutOffset.getText()).toEqual('3 people are viewing.');
			  expect(withOffset.getText()).toEqual('Igor, Misko and one other person are viewing.');
	
			  countInput.clear();
			  countInput.sendKeys('4');
	
			  expect(withoutOffset.getText()).toEqual('4 people are viewing.');
			  expect(withOffset.getText()).toEqual('Igor, Misko and 2 other people are viewing.');
			});
			it('should show data-bound names', function() {
			  var withOffset = element.all(by.css('ng-pluralize')).get(1);
			  var personCount = element(by.model('personCount'));
			  var person1 = element(by.model('person1'));
			  var person2 = element(by.model('person2'));
			  personCount.clear();
			  personCount.sendKeys('4');
			  person1.clear();
			  person1.sendKeys('Di');
			  person2.clear();
			  person2.sendKeys('Vojta');
			  expect(withOffset.getText()).toEqual('Di, Vojta and 2 other people are viewing.');
			});
		  </file>
		</example>
	 */
	var ngPluralizeDirective = ['$locale', '$interpolate', '$log', function ($locale, $interpolate, $log) {
		var BRACE = /{}/g,
			IS_WHEN = /^when(Minus)?(.+)$/;

		return {
			link: function (scope, element, attr) {
				var numberExp = attr.count,
					whenExp = attr.$attr.when && element.attr(attr.$attr.when), // we have {{}} in attrs
					offset = attr.offset || 0,
					whens = scope.$eval(whenExp) || {},
					whensExpFns = {},
					startSymbol = $interpolate.startSymbol(),
					endSymbol = $interpolate.endSymbol(),
					braceReplacement = startSymbol + numberExp + '-' + offset + endSymbol,
					watchRemover = angular.noop,
					lastCount;

				forEach(attr, function (expression, attributeName) {
					var tmpMatch = IS_WHEN.exec(attributeName);
					if (tmpMatch) {
						var whenKey = (tmpMatch[1] ? '-' : '') + lowercase(tmpMatch[2]);
						whens[whenKey] = element.attr(attr.$attr[attributeName]);
					}
				});
				forEach(whens, function (expression, key) {
					whensExpFns[key] = $interpolate(expression.replace(BRACE, braceReplacement));

				});

				scope.$watch(numberExp, function ngPluralizeWatchAction(newVal) {
					var count = parseFloat(newVal);
					var countIsNaN = isNumberNaN(count);

					if (!countIsNaN && !(count in whens)) {
						// If an explicit number rule such as 1, 2, 3... is defined, just use it.
						// Otherwise, check it against pluralization rules in $locale service.
						count = $locale.pluralCat(count - offset);
					}

					// If both `count` and `lastCount` are NaN, we don't need to re-register a watch.
					// In JS `NaN !== NaN`, so we have to explicitly check.
					if ((count !== lastCount) && !(countIsNaN && isNumberNaN(lastCount))) {
						watchRemover();
						var whenExpFn = whensExpFns[count];
						if (isUndefined(whenExpFn)) {
							if (newVal != null) {
								$log.debug('ngPluralize: no rule defined for \'' + count + '\' in ' + whenExp);
							}
							watchRemover = noop;
							updateElementText();
						} else {
							watchRemover = scope.$watch(whenExpFn, updateElementText);
						}
						lastCount = count;
					}
				});

				function updateElementText(newText) {
					element.text(newText || '');
				}
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngRef
	 * @restrict A
	 *
	 * @description
	 * The `ngRef` attribute tells AngularJS to assign the controller of a component (or a directive)
	 * to the given property in the current scope. It is also possible to add the jqlite-wrapped DOM
	 * element to the scope.
	 *
	 * If the element with `ngRef` is destroyed `null` is assigned to the property.
	 *
	 * Note that if you want to assign from a child into the parent scope, you must initialize the
	 * target property on the parent scope, otherwise `ngRef` will assign on the child scope.
	 * This commonly happens when assigning elements or components wrapped in {@link ngIf} or
	 * {@link ngRepeat}. See the second example below.
	 *
	 *
	 * @element ANY
	 * @param {string} ngRef property name - A valid AngularJS expression identifier to which the
	 *                       controller or jqlite-wrapped DOM element will be bound.
	 * @param {string=} ngRefRead read value - The name of a directive (or component) on this element,
	 *                            or the special string `$element`. If a name is provided, `ngRef` will
	 *                            assign the matching controller. If `$element` is provided, the element
	 *                            itself is assigned (even if a controller is available).
	 *
	 *
	 * @example
	 * ### Simple toggle
	 * This example shows how the controller of the component toggle
	 * is reused in the template through the scope to use its logic.
	 * <example name="ng-ref-component" module="myApp">
	 *   <file name="index.html">
	 *     <my-toggle ng-ref="myToggle"></my-toggle>
	 *     <button ng-click="myToggle.toggle()">Toggle</button>
	 *     <div ng-show="myToggle.isOpen()">
	 *       You are using a component in the same template to show it.
	 *     </div>
	 *   </file>
	 *   <file name="index.js">
	 *     angular.module('myApp', [])
	 *     .component('myToggle', {
	 *       controller: function ToggleController() {
	 *         var opened = false;
	 *         this.isOpen = function() { return opened; };
	 *         this.toggle = function() { opened = !opened; };
	 *       }
	 *     });
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *      it('should publish the toggle into the scope', function() {
	 *        var toggle = element(by.buttonText('Toggle'));
	 *        expect(toggle.evaluate('myToggle.isOpen()')).toEqual(false);
	 *        toggle.click();
	 *        expect(toggle.evaluate('myToggle.isOpen()')).toEqual(true);
	 *      });
	 *   </file>
	 * </example>
	 *
	 * @example
	 * ### ngRef inside scopes
	 * This example shows how `ngRef` works with child scopes. The `ngRepeat`-ed `myWrapper` components
	 * are assigned to the scope of `myRoot`, because the `toggles` property has been initialized.
	 * The repeated `myToggle` components are published to the child scopes created by `ngRepeat`.
	 * `ngIf` behaves similarly - the assignment of `myToggle` happens in the `ngIf` child scope,
	 * because the target property has not been initialized on the `myRoot` component controller.
	 *
	 * <example name="ng-ref-scopes" module="myApp">
	 *   <file name="index.html">
	 *     <my-root></my-root>
	 *   </file>
	 *   <file name="index.js">
	 *     angular.module('myApp', [])
	 *     .component('myRoot', {
	 *       templateUrl: 'root.html',
	 *       controller: function() {
	 *         this.wrappers = []; // initialize the array so that the wrappers are assigned into the parent scope
	 *       }
	 *     })
	 *     .component('myToggle', {
	 *       template: '<strong>myToggle</strong><button ng-click="$ctrl.toggle()" ng-transclude></button>',
	 *       transclude: true,
	 *       controller: function ToggleController() {
	 *         var opened = false;
	 *         this.isOpen = function() { return opened; };
	 *         this.toggle = function() { opened = !opened; };
	 *       }
	 *     })
	 *     .component('myWrapper', {
	 *       transclude: true,
	 *       template: '<strong>myWrapper</strong>' +
	 *         '<div>ngRepeatToggle.isOpen(): {{$ctrl.ngRepeatToggle.isOpen() | json}}</div>' +
	 *         '<my-toggle ng-ref="$ctrl.ngRepeatToggle"><ng-transclude></ng-transclude></my-toggle>'
	 *     });
	 *   </file>
	 *   <file name="root.html">
	 *     <strong>myRoot</strong>
	 *     <my-toggle ng-ref="$ctrl.outerToggle">Outer Toggle</my-toggle>
	 *     <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
	 *     <div><em>wrappers assigned to root</em><br>
	 *     <div ng-repeat="wrapper in $ctrl.wrappers">
	 *       wrapper.ngRepeatToggle.isOpen(): {{wrapper.ngRepeatToggle.isOpen() | json}}
	 *     </div>
	 *
	 *     <ul>
	 *       <li ng-repeat="(index, value) in [1,2,3]">
	 *         <strong>ngRepeat</strong>
	 *         <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
	 *         <my-wrapper ng-ref="$ctrl.wrappers[index]">ngRepeat Toggle {{$index + 1}}</my-wrapper>
	 *       </li>
	 *     </ul>
	 *
	 *     <div>ngIfToggle.isOpen(): {{ngIfToggle.isOpen()}} // This is always undefined because it's
	 *       assigned to the child scope created by ngIf.
	 *     </div>
	 *     <div ng-if="true">
			  <strong>ngIf</strong>
	 *        <my-toggle ng-ref="ngIfToggle">ngIf Toggle</my-toggle>
	 *        <div>ngIfToggle.isOpen(): {{ngIfToggle.isOpen() | json}}</div>
	 *        <div>outerToggle.isOpen(): {{$ctrl.outerToggle.isOpen() | json}}</div>
	 *     </div>
	 *   </file>
	 *   <file name="styles.css">
	 *     ul {
	 *       list-style: none;
	 *       padding-left: 0;
	 *     }
	 *
	 *     li[ng-repeat] {
	 *       background: lightgreen;
	 *       padding: 8px;
	 *       margin: 8px;
	 *     }
	 *
	 *     [ng-if] {
	 *       background: lightgrey;
	 *       padding: 8px;
	 *     }
	 *
	 *     my-root {
	 *       background: lightgoldenrodyellow;
	 *       padding: 8px;
	 *       display: block;
	 *     }
	 *
	 *     my-wrapper {
	 *       background: lightsalmon;
	 *       padding: 8px;
	 *       display: block;
	 *     }
	 *
	 *     my-toggle {
	 *       background: lightblue;
	 *       padding: 8px;
	 *       display: block;
	 *     }
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *      var OuterToggle = function() {
	 *        this.toggle = function() {
	 *          element(by.buttonText('Outer Toggle')).click();
	 *        };
	 *        this.isOpen = function() {
	 *          return element.all(by.binding('outerToggle.isOpen()')).first().getText();
	 *        };
	 *      };
	 *      var NgRepeatToggle = function(i) {
	 *        var parent = element.all(by.repeater('(index, value) in [1,2,3]')).get(i - 1);
	 *        this.toggle = function() {
	 *          element(by.buttonText('ngRepeat Toggle ' + i)).click();
	 *        };
	 *        this.isOpen = function() {
	 *          return parent.element(by.binding('ngRepeatToggle.isOpen() | json')).getText();
	 *        };
	 *        this.isOuterOpen = function() {
	 *          return parent.element(by.binding('outerToggle.isOpen() | json')).getText();
	 *        };
	 *      };
	 *      var NgRepeatToggles = function() {
	 *        var toggles = [1,2,3].map(function(i) { return new NgRepeatToggle(i); });
	 *        this.forEach = function(fn) {
	 *          toggles.forEach(fn);
	 *        };
	 *        this.isOuterOpen = function(i) {
	 *          return toggles[i - 1].isOuterOpen();
	 *        };
	 *      };
	 *      var NgIfToggle = function() {
	 *        var parent = element(by.css('[ng-if]'));
	 *        this.toggle = function() {
	 *          element(by.buttonText('ngIf Toggle')).click();
	 *        };
	 *        this.isOpen = function() {
	 *          return by.binding('ngIfToggle.isOpen() | json').getText();
	 *        };
	 *        this.isOuterOpen = function() {
	 *          return parent.element(by.binding('outerToggle.isOpen() | json')).getText();
	 *        };
	 *      };
	 *
	 *      it('should toggle the outer toggle', function() {
	 *        var outerToggle = new OuterToggle();
	 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): false');
	 *        outerToggle.toggle();
	 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): true');
	 *      });
	 *
	 *      it('should toggle all outer toggles', function() {
	 *        var outerToggle = new OuterToggle();
	 *        var repeatToggles = new NgRepeatToggles();
	 *        var ifToggle = new NgIfToggle();
	 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): false');
	 *        expect(repeatToggles.isOuterOpen(1)).toEqual('outerToggle.isOpen(): false');
	 *        expect(repeatToggles.isOuterOpen(2)).toEqual('outerToggle.isOpen(): false');
	 *        expect(repeatToggles.isOuterOpen(3)).toEqual('outerToggle.isOpen(): false');
	 *        expect(ifToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
	 *        outerToggle.toggle();
	 *        expect(outerToggle.isOpen()).toEqual('outerToggle.isOpen(): true');
	 *        expect(repeatToggles.isOuterOpen(1)).toEqual('outerToggle.isOpen(): true');
	 *        expect(repeatToggles.isOuterOpen(2)).toEqual('outerToggle.isOpen(): true');
	 *        expect(repeatToggles.isOuterOpen(3)).toEqual('outerToggle.isOpen(): true');
	 *        expect(ifToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): true');
	 *      });
	 *
	 *      it('should toggle each repeat iteration separately', function() {
	 *        var repeatToggles = new NgRepeatToggles();
	 *
	 *        repeatToggles.forEach(function(repeatToggle) {
	 *          expect(repeatToggle.isOpen()).toEqual('ngRepeatToggle.isOpen(): false');
	 *          expect(repeatToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
	 *          repeatToggle.toggle();
	 *          expect(repeatToggle.isOpen()).toEqual('ngRepeatToggle.isOpen(): true');
	 *          expect(repeatToggle.isOuterOpen()).toEqual('outerToggle.isOpen(): false');
	 *        });
	 *      });
	 *   </file>
	 * </example>
	 *
	 */

	var ngRefMinErr = minErr('ngRef');

	var ngRefDirective = ['$parse', function ($parse) {
		return {
			priority: -1, // Needed for compatibility with element transclusion on the same element
			restrict: 'A',
			compile: function (tElement, tAttrs) {
				// Get the expected controller name, converts <data-some-thing> into "someThing"
				var controllerName = directiveNormalize(nodeName_(tElement));

				// Get the expression for value binding
				var getter = $parse(tAttrs.ngRef);
				var setter = getter.assign || function () {
					throw ngRefMinErr('nonassign', 'Expression in ngRef="{0}" is non-assignable!', tAttrs.ngRef);
				};

				return function (scope, element, attrs) {
					var refValue;

					if (attrs.hasOwnProperty('ngRefRead')) {
						if (attrs.ngRefRead === '$element') {
							refValue = element;
						} else {
							refValue = element.data('$' + attrs.ngRefRead + 'Controller');

							if (!refValue) {
								throw ngRefMinErr(
									'noctrl',
									'The controller for ngRefRead="{0}" could not be found on ngRef="{1}"',
									attrs.ngRefRead,
									tAttrs.ngRef
								);
							}
						}
					} else {
						refValue = element.data('$' + controllerName + 'Controller');
					}

					refValue = refValue || element;

					setter(scope, refValue);

					// when the element is removed, remove it (nullify it)
					element.on('$destroy', function () {
						// only remove it if value has not changed,
						// because animations (and other procedures) may duplicate elements
						if (getter(scope) === refValue) {
							setter(scope, null);
						}
					});
				};
			}
		};
	}];

	/* exported ngRepeatDirective */

	/**
	 * @ngdoc directive
	 * @name ngRepeat
	 * @multiElement
	 * @restrict A
	 *
	 * @description
	 * The `ngRepeat` directive instantiates a template once per item from a collection. Each template
	 * instance gets its own scope, where the given loop variable is set to the current collection item,
	 * and `$index` is set to the item index or key.
	 *
	 * Special properties are exposed on the local scope of each template instance, including:
	 *
	 * | Variable  | Type            | Details                                                                     |
	 * |-----------|-----------------|-----------------------------------------------------------------------------|
	 * | `$index`  | {@type number}  | iterator offset of the repeated element (0..length-1)                       |
	 * | `$first`  | {@type boolean} | true if the repeated element is first in the iterator.                      |
	 * | `$middle` | {@type boolean} | true if the repeated element is between the first and last in the iterator. |
	 * | `$last`   | {@type boolean} | true if the repeated element is last in the iterator.                       |
	 * | `$even`   | {@type boolean} | true if the iterator position `$index` is even (otherwise false).           |
	 * | `$odd`    | {@type boolean} | true if the iterator position `$index` is odd (otherwise false).            |
	 *
	 * <div class="alert alert-info">
	 *   Creating aliases for these properties is possible with {@link ng.directive:ngInit `ngInit`}.
	 *   This may be useful when, for instance, nesting ngRepeats.
	 * </div>
	 *
	 *
	 * ## Iterating over object properties
	 *
	 * It is possible to get `ngRepeat` to iterate over the properties of an object using the following
	 * syntax:
	 *
	 * ```js
	 * <div ng-repeat="(key, value) in myObj"> ... </div>
	 * ```
	 *
	 * However, there are a few limitations compared to array iteration:
	 *
	 * - The JavaScript specification does not define the order of keys
	 *   returned for an object, so AngularJS relies on the order returned by the browser
	 *   when running `for key in myObj`. Browsers generally follow the strategy of providing
	 *   keys in the order in which they were defined, although there are exceptions when keys are deleted
	 *   and reinstated. See the
	 *   [MDN page on `delete` for more info](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/delete#Cross-browser_notes).
	 *
	 * - `ngRepeat` will silently *ignore* object keys starting with `$`, because
	 *   it's a prefix used by AngularJS for public (`$`) and private (`$$`) properties.
	 *
	 * - The built-in filters {@link ng.orderBy orderBy} and {@link ng.filter filter} do not work with
	 *   objects, and will throw an error if used with one.
	 *
	 * If you are hitting any of these limitations, the recommended workaround is to convert your object into an array
	 * that is sorted into the order that you prefer before providing it to `ngRepeat`. You could
	 * do this with a filter such as [toArrayFilter](http://ngmodules.org/modules/angular-toArrayFilter)
	 * or implement a `$watch` on the object yourself.
	 *
	 *
	 * ## Tracking and Duplicates
	 *
	 * `ngRepeat` uses {@link $rootScope.Scope#$watchCollection $watchCollection} to detect changes in
	 * the collection. When a change happens, `ngRepeat` then makes the corresponding changes to the DOM:
	 *
	 * * When an item is added, a new instance of the template is added to the DOM.
	 * * When an item is removed, its template instance is removed from the DOM.
	 * * When items are reordered, their respective templates are reordered in the DOM.
	 *
	 * To minimize creation of DOM elements, `ngRepeat` uses a function
	 * to "keep track" of all items in the collection and their corresponding DOM elements.
	 * For example, if an item is added to the collection, `ngRepeat` will know that all other items
	 * already have DOM elements, and will not re-render them.
	 *
	 * All different types of tracking functions, their syntax, and their support for duplicate
	 * items in collections can be found in the
	 * {@link ngRepeat#ngRepeat-arguments ngRepeat expression description}.
	 *
	 * <div class="alert alert-success">
	 * **Best Practice:** If you are working with objects that have a unique identifier property, you
	 * should track by this identifier instead of the object instance,
	 * e.g. `item in items track by item.id`.
	 * Should you reload your data later, `ngRepeat` will not have to rebuild the DOM elements for items
	 * it has already rendered, even if the JavaScript objects in the collection have been substituted
	 * for new ones. For large collections, this significantly improves rendering performance.
	 * </div>
	 *
	 * ### Effects of DOM Element re-use
	 *
	 * When DOM elements are re-used, ngRepeat updates the scope for the element, which will
	 * automatically update any active bindings on the template. However, other
	 * functionality will not be updated, because the element is not re-created:
	 *
	 * - Directives are not re-compiled
	 * - {@link guide/expression#one-time-binding one-time expressions} on the repeated template are not
	 * updated if they have stabilized.
	 *
	 * The above affects all kinds of element re-use due to tracking, but may be especially visible
	 * when tracking by `$index` due to the way ngRepeat re-uses elements.
	 *
	 * The following example shows the effects of different actions with tracking:
	
	  <example module="ngRepeat" name="ngRepeat-tracking" deps="angular-animate.js" animations="true">
		<file name="script.js">
		  angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
			var friends = [
			  {name:'John', age:25},
			  {name:'Mary', age:40},
			  {name:'Peter', age:85}
			];
	
			$scope.removeFirst = function() {
			  $scope.friends.shift();
			};
	
			$scope.updateAge = function() {
			  $scope.friends.forEach(function(el) {
				el.age = el.age + 5;
			  });
			};
	
			$scope.copy = function() {
			  $scope.friends = angular.copy($scope.friends);
			};
	
			$scope.reset = function() {
			  $scope.friends = angular.copy(friends);
			};
	
			$scope.reset();
		  });
		</file>
		<file name="index.html">
		  <div ng-controller="repeatController">
			<ol>
			  <li>When you click "Update Age", only the first list updates the age, because all others have
			  a one-time binding on the age property. If you then click "Copy", the current friend list
			  is copied, and now the second list updates the age, because the identity of the collection items
			  has changed and the list must be re-rendered. The 3rd and 4th list stay the same, because all the
			  items are already known according to their tracking functions.
			  </li>
			  <li>When you click "Remove First", the 4th list has the wrong age on both remaining items. This is
			  due to tracking by $index: when the first collection item is removed, ngRepeat reuses the first
			  DOM element for the new first collection item, and so on. Since the age property is one-time
			  bound, the value remains from the collection item which was previously at this index.
			  </li>
			</ol>
	
			<button ng-click="removeFirst()">Remove First</button>
			<button ng-click="updateAge()">Update Age</button>
			<button ng-click="copy()">Copy</button>
			<br><button ng-click="reset()">Reset List</button>
			<br>
			<code>track by $id(friend)</code> (default):
			<ul class="example-animate-container">
			  <li class="animate-repeat" ng-repeat="friend in friends">
				{{friend.name}} is {{friend.age}} years old.
			  </li>
			</ul>
			<code>track by $id(friend)</code> (default), with age one-time binding:
			<ul class="example-animate-container">
			  <li class="animate-repeat" ng-repeat="friend in friends">
				{{friend.name}} is {{::friend.age}} years old.
			  </li>
			</ul>
			<code>track by friend.name</code>, with age one-time binding:
			<ul class="example-animate-container">
			  <li class="animate-repeat" ng-repeat="friend in friends track by friend.name">
				{{friend.name}}  is {{::friend.age}} years old.
			  </li>
			</ul>
			<code>track by $index</code>, with age one-time binding:
			<ul class="example-animate-container">
			  <li class="animate-repeat" ng-repeat="friend in friends track by $index">
				{{friend.name}} is {{::friend.age}} years old.
			  </li>
			</ul>
		  </div>
		</file>
		<file name="animations.css">
		  .example-animate-container {
			background:white;
			border:1px solid black;
			list-style:none;
			margin:0;
			padding:0 10px;
		  }
	
		  .animate-repeat {
			line-height:30px;
			list-style:none;
			box-sizing:border-box;
		  }
	
		  .animate-repeat.ng-move,
		  .animate-repeat.ng-enter,
		  .animate-repeat.ng-leave {
			transition:all linear 0.5s;
		  }
	
		  .animate-repeat.ng-leave.ng-leave-active,
		  .animate-repeat.ng-move,
		  .animate-repeat.ng-enter {
			opacity:0;
			max-height:0;
		  }
	
		  .animate-repeat.ng-leave,
		  .animate-repeat.ng-move.ng-move-active,
		  .animate-repeat.ng-enter.ng-enter-active {
			opacity:1;
			max-height:30px;
		  }
		</file>
	  </example>
	
	 *
	 * ## Special repeat start and end points
	 * To repeat a series of elements instead of just one parent element, ngRepeat (as well as other ng directives) supports extending
	 * the range of the repeater by defining explicit start and end points by using **ng-repeat-start** and **ng-repeat-end** respectively.
	 * The **ng-repeat-start** directive works the same as **ng-repeat**, but will repeat all the HTML code (including the tag it's defined on)
	 * up to and including the ending HTML tag where **ng-repeat-end** is placed.
	 *
	 * The example below makes use of this feature:
	 * ```html
	 *   <header ng-repeat-start="item in items">
	 *     Header {{ item }}
	 *   </header>
	 *   <div class="body">
	 *     Body {{ item }}
	 *   </div>
	 *   <footer ng-repeat-end>
	 *     Footer {{ item }}
	 *   </footer>
	 * ```
	 *
	 * And with an input of {@type ['A','B']} for the items variable in the example above, the output will evaluate to:
	 * ```html
	 *   <header>
	 *     Header A
	 *   </header>
	 *   <div class="body">
	 *     Body A
	 *   </div>
	 *   <footer>
	 *     Footer A
	 *   </footer>
	 *   <header>
	 *     Header B
	 *   </header>
	 *   <div class="body">
	 *     Body B
	 *   </div>
	 *   <footer>
	 *     Footer B
	 *   </footer>
	 * ```
	 *
	 * The custom start and end points for ngRepeat also support all other HTML directive syntax flavors provided in AngularJS (such
	 * as **data-ng-repeat-start**, **x-ng-repeat-start** and **ng:repeat-start**).
	 *
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#enter enter} | when a new item is added to the list or when an item is revealed after a filter |
	 * | {@link ng.$animate#leave leave} | when an item is removed from the list or when an item is filtered out |
	 * | {@link ng.$animate#move move } | when an adjacent item is filtered out causing a reorder or when the item contents are reordered |
	 *
	 * See the example below for defining CSS animations with ngRepeat.
	 *
	 * @element ANY
	 * @scope
	 * @priority 1000
	 * @param {repeat_expression} ngRepeat The expression indicating how to enumerate a collection. These
	 *   formats are currently supported:
	 *
	 *   * `variable in expression` – where variable is the user defined loop variable and `expression`
	 *     is a scope expression giving the collection to enumerate.
	 *
	 *     For example: `album in artist.albums`.
	 *
	 *   * `(key, value) in expression` – where `key` and `value` can be any user defined identifiers,
	 *     and `expression` is the scope expression giving the collection to enumerate.
	 *
	 *     For example: `(name, age) in {'adam':10, 'amalie':12}`.
	 *
	 *   * `variable in expression track by tracking_expression` – You can also provide an optional tracking expression
	 *     which can be used to associate the objects in the collection with the DOM elements. If no tracking expression
	 *     is specified, ng-repeat associates elements by identity. It is an error to have
	 *     more than one tracking expression value resolve to the same key. (This would mean that two distinct objects are
	 *     mapped to the same DOM element, which is not possible.)
	 *
	 *     *Default tracking: $id()*: `item in items` is equivalent to `item in items track by $id(item)`.
	 *     This implies that the DOM elements will be associated by item identity in the collection.
	 *
	 *     The built-in `$id()` function can be used to assign a unique
	 *     `$$hashKey` property to each item in the collection. This property is then used as a key to associated DOM elements
	 *     with the corresponding item in the collection by identity. Moving the same object would move
	 *     the DOM element in the same way in the DOM.
	 *     Note that the default id function does not support duplicate primitive values (`number`, `string`),
	 *     but supports duplictae non-primitive values (`object`) that are *equal* in shape.
	 *
	 *     *Custom Expression*: It is possible to use any AngularJS expression to compute the tracking
	 *     id, for example with a function, or using a property on the collection items.
	 *     `item in items track by item.id` is a typical pattern when the items have a unique identifier,
	 *     e.g. database id. In this case the object identity does not matter. Two objects are considered
	 *     equivalent as long as their `id` property is same.
	 *     Tracking by unique identifier is the most performant way and should be used whenever possible.
	 *
	 *     *$index*: This special property tracks the collection items by their index, and
	 *     re-uses the DOM elements that match that index, e.g. `item in items track by $index`. This can
	 *     be used for a performance improvement if no unique identfier is available and the identity of
	 *     the collection items cannot be easily computed. It also allows duplicates.
	 *
	 *     <div class="alert alert-warning">
	 *       <strong>Note:</strong> Re-using DOM elements can have unforeseen effects. Read the
	 *       {@link ngRepeat#tracking-and-duplicates section on tracking and duplicates} for
	 *       more info.
	 *     </div>
	 *
	 *     <div class="alert alert-warning">
	 *       <strong>Note:</strong> the `track by` expression must come last - after any filters, and the alias expression:
	 *       `item in items | filter:searchText as results  track by item.id`
	 *     </div>
	 *
	 *   * `variable in expression as alias_expression` – You can also provide an optional alias expression which will then store the
	 *     intermediate results of the repeater after the filters have been applied. Typically this is used to render a special message
	 *     when a filter is active on the repeater, but the filtered result set is empty.
	 *
	 *     For example: `item in items | filter:x as results` will store the fragment of the repeated items as `results`, but only after
	 *     the items have been processed through the filter.
	 *
	 *     Please note that `as [variable name]` is not an operator but rather a part of ngRepeat
	 *     micro-syntax so it can be used only after all filters (and not as operator, inside an expression).
	 *
	 *     For example: `item in items | filter : x | orderBy : order | limitTo : limit as results track by item.id` .
	 *
	 * @example
	 * This example uses `ngRepeat` to display a list of people. A filter is used to restrict the displayed
	 * results by name or by age. New (entering) and removed (leaving) items are animated.
	  <example module="ngRepeat" name="ngRepeat" deps="angular-animate.js" animations="true">
		<file name="index.html">
		  <div ng-controller="repeatController">
			I have {{friends.length}} friends. They are:
			<input type="search" ng-model="q" placeholder="filter friends..." aria-label="filter friends" />
			<ul class="example-animate-container">
			  <li class="animate-repeat" ng-repeat="friend in friends | filter:q as results track by friend.name">
				[{{$index + 1}}] {{friend.name}} who is {{friend.age}} years old.
			  </li>
			  <li class="animate-repeat" ng-if="results.length === 0">
				<strong>No results found...</strong>
			  </li>
			</ul>
		  </div>
		</file>
		<file name="script.js">
		  angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
			$scope.friends = [
			  {name:'John', age:25, gender:'boy'},
			  {name:'Jessie', age:30, gender:'girl'},
			  {name:'Johanna', age:28, gender:'girl'},
			  {name:'Joy', age:15, gender:'girl'},
			  {name:'Mary', age:28, gender:'girl'},
			  {name:'Peter', age:95, gender:'boy'},
			  {name:'Sebastian', age:50, gender:'boy'},
			  {name:'Erika', age:27, gender:'girl'},
			  {name:'Patrick', age:40, gender:'boy'},
			  {name:'Samantha', age:60, gender:'girl'}
			];
		  });
		</file>
		<file name="animations.css">
		  .example-animate-container {
			background:white;
			border:1px solid black;
			list-style:none;
			margin:0;
			padding:0 10px;
		  }
	
		  .animate-repeat {
			line-height:30px;
			list-style:none;
			box-sizing:border-box;
		  }
	
		  .animate-repeat.ng-move,
		  .animate-repeat.ng-enter,
		  .animate-repeat.ng-leave {
			transition:all linear 0.5s;
		  }
	
		  .animate-repeat.ng-leave.ng-leave-active,
		  .animate-repeat.ng-move,
		  .animate-repeat.ng-enter {
			opacity:0;
			max-height:0;
		  }
	
		  .animate-repeat.ng-leave,
		  .animate-repeat.ng-move.ng-move-active,
		  .animate-repeat.ng-enter.ng-enter-active {
			opacity:1;
			max-height:30px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  var friends = element.all(by.repeater('friend in friends'));
	
		  it('should render initial data set', function() {
			expect(friends.count()).toBe(10);
			expect(friends.get(0).getText()).toEqual('[1] John who is 25 years old.');
			expect(friends.get(1).getText()).toEqual('[2] Jessie who is 30 years old.');
			expect(friends.last().getText()).toEqual('[10] Samantha who is 60 years old.');
			expect(element(by.binding('friends.length')).getText())
				.toMatch("I have 10 friends. They are:");
		  });
	
		   it('should update repeater when filter predicate changes', function() {
			 expect(friends.count()).toBe(10);
	
			 element(by.model('q')).sendKeys('ma');
	
			 expect(friends.count()).toBe(2);
			 expect(friends.get(0).getText()).toEqual('[1] Mary who is 28 years old.');
			 expect(friends.last().getText()).toEqual('[2] Samantha who is 60 years old.');
		   });
		  </file>
		</example>
	 */
	var ngRepeatDirective = ['$parse', '$animate', '$compile', function ($parse, $animate, $compile) {
		var NG_REMOVED = '$$NG_REMOVED';
		var ngRepeatMinErr = minErr('ngRepeat');

		var updateScope = function (scope, index, valueIdentifier, value, keyIdentifier, key, arrayLength) {
			// TODO(perf): generate setters to shave off ~40ms or 1-1.5%
			scope[valueIdentifier] = value;
			if (keyIdentifier) scope[keyIdentifier] = key;
			scope.$index = index;
			scope.$first = (index === 0);
			scope.$last = (index === (arrayLength - 1));
			scope.$middle = !(scope.$first || scope.$last);
			// eslint-disable-next-line no-bitwise
			scope.$odd = !(scope.$even = (index & 1) === 0);
		};

		var getBlockStart = function (block) {
			return block.clone[0];
		};

		var getBlockEnd = function (block) {
			return block.clone[block.clone.length - 1];
		};

		var trackByIdArrayFn = function ($scope, key, value) {
			return hashKey(value);
		};

		var trackByIdObjFn = function ($scope, key) {
			return key;
		};

		return {
			restrict: 'A',
			multiElement: true,
			transclude: 'element',
			priority: 1000,
			terminal: true,
			$$tlb: true,
			compile: function ngRepeatCompile($element, $attr) {
				var expression = $attr.ngRepeat;
				var ngRepeatEndComment = $compile.$$createComment('end ngRepeat', expression);

				var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);

				if (!match) {
					throw ngRepeatMinErr('iexp', 'Expected expression in form of \'_item_ in _collection_[ track by _id_]\' but got \'{0}\'.',
						expression);
				}

				var lhs = match[1];
				var rhs = match[2];
				var aliasAs = match[3];
				var trackByExp = match[4];

				match = lhs.match(/^(?:(\s*[$\w]+)|\(\s*([$\w]+)\s*,\s*([$\w]+)\s*\))$/);

				if (!match) {
					throw ngRepeatMinErr('iidexp', '\'_item_\' in \'_item_ in _collection_\' should be an identifier or \'(_key_, _value_)\' expression, but got \'{0}\'.',
						lhs);
				}
				var valueIdentifier = match[3] || match[1];
				var keyIdentifier = match[2];

				if (aliasAs && (!/^[$a-zA-Z_][$a-zA-Z0-9_]*$/.test(aliasAs) ||
					/^(null|undefined|this|\$index|\$first|\$middle|\$last|\$even|\$odd|\$parent|\$root|\$id)$/.test(aliasAs))) {
					throw ngRepeatMinErr('badident', 'alias \'{0}\' is invalid --- must be a valid JS identifier which is not a reserved name.',
						aliasAs);
				}

				var trackByIdExpFn;

				if (trackByExp) {
					var hashFnLocals = { $id: hashKey };
					var trackByExpGetter = $parse(trackByExp);

					trackByIdExpFn = function ($scope, key, value, index) {
						// assign key, value, and $index to the locals so that they can be used in hash functions
						if (keyIdentifier) hashFnLocals[keyIdentifier] = key;
						hashFnLocals[valueIdentifier] = value;
						hashFnLocals.$index = index;
						return trackByExpGetter($scope, hashFnLocals);
					};
				}

				return function ngRepeatLink($scope, $element, $attr, ctrl, $transclude) {

					// Store a list of elements from previous run. This is a hash where key is the item from the
					// iterator, and the value is objects with following properties.
					//   - scope: bound scope
					//   - clone: previous element.
					//   - index: position
					//
					// We are using no-proto object so that we don't need to guard against inherited props via
					// hasOwnProperty.
					var lastBlockMap = createMap();

					//watch props
					$scope.$watchCollection(rhs, function ngRepeatAction(collection) {
						var index, length,
							previousNode = $element[0],     // node that cloned nodes should be inserted after
							// initialized to the comment node anchor
							nextNode,
							// Same as lastBlockMap but it has the current state. It will become the
							// lastBlockMap on the next iteration.
							nextBlockMap = createMap(),
							collectionLength,
							key, value, // key/value of iteration
							trackById,
							trackByIdFn,
							collectionKeys,
							block,       // last object information {scope, element, id}
							nextBlockOrder,
							elementsToRemove;

						if (aliasAs) {
							$scope[aliasAs] = collection;
						}

						if (isArrayLike(collection)) {
							collectionKeys = collection;
							trackByIdFn = trackByIdExpFn || trackByIdArrayFn;
						} else {
							trackByIdFn = trackByIdExpFn || trackByIdObjFn;
							// if object, extract keys, in enumeration order, unsorted
							collectionKeys = [];
							for (var itemKey in collection) {
								if (hasOwnProperty.call(collection, itemKey) && itemKey.charAt(0) !== '$') {
									collectionKeys.push(itemKey);
								}
							}
						}

						collectionLength = collectionKeys.length;
						nextBlockOrder = new Array(collectionLength);

						// locate existing items
						for (index = 0; index < collectionLength; index++) {
							key = (collection === collectionKeys) ? index : collectionKeys[index];
							value = collection[key];
							trackById = trackByIdFn($scope, key, value, index);
							if (lastBlockMap[trackById]) {
								// found previously seen block
								block = lastBlockMap[trackById];
								delete lastBlockMap[trackById];
								nextBlockMap[trackById] = block;
								nextBlockOrder[index] = block;
							} else if (nextBlockMap[trackById]) {
								// if collision detected. restore lastBlockMap and throw an error
								forEach(nextBlockOrder, function (block) {
									if (block && block.scope) lastBlockMap[block.id] = block;
								});
								throw ngRepeatMinErr('dupes',
									'Duplicates in a repeater are not allowed. Use \'track by\' expression to specify unique keys. Repeater: {0}, Duplicate key: {1}, Duplicate value: {2}',
									expression, trackById, value);
							} else {
								// new never before seen block
								nextBlockOrder[index] = { id: trackById, scope: undefined, clone: undefined };
								nextBlockMap[trackById] = true;
							}
						}

						// Clear the value property from the hashFnLocals object to prevent a reference to the last value
						// being leaked into the ngRepeatCompile function scope
						if (hashFnLocals) {
							hashFnLocals[valueIdentifier] = undefined;
						}

						// remove leftover items
						for (var blockKey in lastBlockMap) {
							block = lastBlockMap[blockKey];
							elementsToRemove = getBlockNodes(block.clone);
							$animate.leave(elementsToRemove);
							if (elementsToRemove[0].parentNode) {
								// if the element was not removed yet because of pending animation, mark it as deleted
								// so that we can ignore it later
								for (index = 0, length = elementsToRemove.length; index < length; index++) {
									elementsToRemove[index][NG_REMOVED] = true;
								}
							}
							block.scope.$destroy();
						}

						// we are not using forEach for perf reasons (trying to avoid #call)
						for (index = 0; index < collectionLength; index++) {
							key = (collection === collectionKeys) ? index : collectionKeys[index];
							value = collection[key];
							block = nextBlockOrder[index];

							if (block.scope) {
								// if we have already seen this object, then we need to reuse the
								// associated scope/element

								nextNode = previousNode;

								// skip nodes that are already pending removal via leave animation
								do {
									nextNode = nextNode.nextSibling;
								} while (nextNode && nextNode[NG_REMOVED]);

								if (getBlockStart(block) !== nextNode) {
									// existing item which got moved
									$animate.move(getBlockNodes(block.clone), null, previousNode);
								}
								previousNode = getBlockEnd(block);
								updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
							} else {
								// new item which we don't know about
								$transclude(function ngRepeatTransclude(clone, scope) {
									block.scope = scope;
									// http://jsperf.com/clone-vs-createcomment
									var endNode = ngRepeatEndComment.cloneNode(false);
									clone[clone.length++] = endNode;

									$animate.enter(clone, null, previousNode);
									previousNode = endNode;
									// Note: We only need the first/last node of the cloned nodes.
									// However, we need to keep the reference to the jqlite wrapper as it might be changed later
									// by a directive with templateUrl when its template arrives.
									block.clone = clone;
									nextBlockMap[block.id] = block;
									updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
								});
							}
						}
						lastBlockMap = nextBlockMap;
					});
				};
			}
		};
	}];

	var NG_HIDE_CLASS = 'ng-hide';
	var NG_HIDE_IN_PROGRESS_CLASS = 'ng-hide-animate';
	/**
	 * @ngdoc directive
	 * @name ngShow
	 * @multiElement
	 *
	 * @description
	 * The `ngShow` directive shows or hides the given HTML element based on the expression provided to
	 * the `ngShow` attribute.
	 *
	 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
	 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
	 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
	 * {@link ng.directive:ngCsp ngCsp}).
	 *
	 * ```html
	 * <!-- when $scope.myValue is truthy (element is visible) -->
	 * <div ng-show="myValue"></div>
	 *
	 * <!-- when $scope.myValue is falsy (element is hidden) -->
	 * <div ng-show="myValue" class="ng-hide"></div>
	 * ```
	 *
	 * When the `ngShow` expression evaluates to a falsy value then the `.ng-hide` CSS class is added
	 * to the class attribute on the element causing it to become hidden. When truthy, the `.ng-hide`
	 * CSS class is removed from the element causing the element not to appear hidden.
	 *
	 * ## Why is `!important` used?
	 *
	 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
	 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
	 * simple as changing the display style on a HTML list item would make hidden elements appear
	 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
	 *
	 * By using `!important`, the show and hide behavior will work as expected despite any clash between
	 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
	 * developer chooses to override the styling to change how to hide an element then it is just a
	 * matter of using `!important` in their own CSS code.
	 *
	 * ### Overriding `.ng-hide`
	 *
	 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
	 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
	 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
	 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
	 *
	 * ```css
	 * .ng-hide:not(.ng-hide-animate) {
	 *   /&#42; These are just alternative ways of hiding an element &#42;/
	 *   display: block!important;
	 *   position: absolute;
	 *   top: -9999px;
	 *   left: -9999px;
	 * }
	 * ```
	 *
	 * By default you don't need to override anything in CSS and the animations will work around the
	 * display style.
	 *
	 * @animations
	 * | Animation                                           | Occurs                                                                                                        |
	 * |-----------------------------------------------------|---------------------------------------------------------------------------------------------------------------|
	 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngShow` expression evaluates to a non truthy value and just before the contents are set to hidden. |
	 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngShow` expression evaluates to a truthy value and just before contents are set to visible.        |
	 *
	 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
	 * directive expression is true and false. This system works like the animation system present with
	 * `ngClass` except that you must also include the `!important` flag to override the display
	 * property so that the elements are not actually hidden during the animation.
	 *
	 * ```css
	 * /&#42; A working example can be found at the bottom of this page. &#42;/
	 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
	 *   transition: all 0.5s linear;
	 * }
	 *
	 * .my-element.ng-hide-add { ... }
	 * .my-element.ng-hide-add.ng-hide-add-active { ... }
	 * .my-element.ng-hide-remove { ... }
	 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
	 * ```
	 *
	 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
	 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
	 *
	 * @element ANY
	 * @param {expression} ngShow If the {@link guide/expression expression} is truthy/falsy then the
	 *                            element is shown/hidden respectively.
	 *
	 * @example
	 * A simple example, animating the element's opacity:
	 *
	  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-simple">
		<file name="index.html">
		  Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
		  <div class="check-element animate-show-hide" ng-show="checked">
			I show up when your checkbox is checked.
		  </div>
		</file>
		<file name="animations.css">
		  .animate-show-hide.ng-hide {
			opacity: 0;
		  }
	
		  .animate-show-hide.ng-hide-add,
		  .animate-show-hide.ng-hide-remove {
			transition: all linear 0.5s;
		  }
	
		  .check-element {
			border: 1px solid black;
			opacity: 1;
			padding: 10px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  it('should check ngShow', function() {
			var checkbox = element(by.model('checked'));
			var checkElem = element(by.css('.check-element'));
	
			expect(checkElem.isDisplayed()).toBe(false);
			checkbox.click();
			expect(checkElem.isDisplayed()).toBe(true);
		  });
		</file>
	  </example>
	 *
	 * <hr />
	 * @example
	 * A more complex example, featuring different show/hide animations:
	 *
	  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-complex">
		<file name="index.html">
		  Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
		  <div class="check-element funky-show-hide" ng-show="checked">
			I show up when your checkbox is checked.
		  </div>
		</file>
		<file name="animations.css">
		  body {
			overflow: hidden;
			perspective: 1000px;
		  }
	
		  .funky-show-hide.ng-hide-add {
			transform: rotateZ(0);
			transform-origin: right;
			transition: all 0.5s ease-in-out;
		  }
	
		  .funky-show-hide.ng-hide-add.ng-hide-add-active {
			transform: rotateZ(-135deg);
		  }
	
		  .funky-show-hide.ng-hide-remove {
			transform: rotateY(90deg);
			transform-origin: left;
			transition: all 0.5s ease;
		  }
	
		  .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
			transform: rotateY(0);
		  }
	
		  .check-element {
			border: 1px solid black;
			opacity: 1;
			padding: 10px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  it('should check ngShow', function() {
			var checkbox = element(by.model('checked'));
			var checkElem = element(by.css('.check-element'));
	
			expect(checkElem.isDisplayed()).toBe(false);
			checkbox.click();
			expect(checkElem.isDisplayed()).toBe(true);
		  });
		</file>
	  </example>
	 *
	 * @knownIssue
	 *
	 * ### Flickering when using ngShow to toggle between elements
	 *
	 * When using {@link ngShow} and / or {@link ngHide} to toggle between elements, it can
	 * happen that both the element to show and the element to hide are visible for a very short time.
	 *
	 * This usually happens when the {@link ngAnimate ngAnimate module} is included, but no actual animations
	 * are defined for {@link ngShow} / {@link ngHide}. Internet Explorer is affected more often than
	 * other browsers.
	 *
	 * There are several way to mitigate this problem:
	 *
	 * - {@link guide/animations#how-to-selectively-enable-disable-and-skip-animations Disable animations on the affected elements}.
	 * - Use {@link ngIf} or {@link ngSwitch} instead of {@link ngShow} / {@link ngHide}.
	 * - Use the special CSS selector `ng-hide.ng-hide-animate` to set `{display: none}` or similar on the affected elements.
	 * - Use `ng-class="{'ng-hide': expression}` instead of instead of {@link ngShow} / {@link ngHide}.
	 * - Define an animation on the affected elements.
	 */
	var ngShowDirective = ['$animate', function ($animate) {
		return {
			restrict: 'A',
			multiElement: true,
			link: function (scope, element, attr) {
				scope.$watch(attr.ngShow, function ngShowWatchAction(value) {
					// we're adding a temporary, animation-specific class for ng-hide since this way
					// we can control when the element is actually displayed on screen without having
					// to have a global/greedy CSS selector that breaks when other animations are run.
					// Read: https://github.com/angular/angular.js/issues/9103#issuecomment-58335845
					$animate[value ? 'removeClass' : 'addClass'](element, NG_HIDE_CLASS, {
						tempClasses: NG_HIDE_IN_PROGRESS_CLASS
					});
				});
			}
		};
	}];


	/**
	 * @ngdoc directive
	 * @name ngHide
	 * @multiElement
	 *
	 * @description
	 * The `ngHide` directive shows or hides the given HTML element based on the expression provided to
	 * the `ngHide` attribute.
	 *
	 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
	 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
	 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
	 * {@link ng.directive:ngCsp ngCsp}).
	 *
	 * ```html
	 * <!-- when $scope.myValue is truthy (element is hidden) -->
	 * <div ng-hide="myValue" class="ng-hide"></div>
	 *
	 * <!-- when $scope.myValue is falsy (element is visible) -->
	 * <div ng-hide="myValue"></div>
	 * ```
	 *
	 * When the `ngHide` expression evaluates to a truthy value then the `.ng-hide` CSS class is added
	 * to the class attribute on the element causing it to become hidden. When falsy, the `.ng-hide`
	 * CSS class is removed from the element causing the element not to appear hidden.
	 *
	 * ## Why is `!important` used?
	 *
	 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
	 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
	 * simple as changing the display style on a HTML list item would make hidden elements appear
	 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
	 *
	 * By using `!important`, the show and hide behavior will work as expected despite any clash between
	 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
	 * developer chooses to override the styling to change how to hide an element then it is just a
	 * matter of using `!important` in their own CSS code.
	 *
	 * ### Overriding `.ng-hide`
	 *
	 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
	 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
	 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
	 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
	 *
	 * ```css
	 * .ng-hide:not(.ng-hide-animate) {
	 *   /&#42; These are just alternative ways of hiding an element &#42;/
	 *   display: block!important;
	 *   position: absolute;
	 *   top: -9999px;
	 *   left: -9999px;
	 * }
	 * ```
	 *
	 * By default you don't need to override in CSS anything and the animations will work around the
	 * display style.
	 *
	 * @animations
	 * | Animation                                           | Occurs                                                                                                     |
	 * |-----------------------------------------------------|------------------------------------------------------------------------------------------------------------|
	 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngHide` expression evaluates to a truthy value and just before the contents are set to hidden.  |
	 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngHide` expression evaluates to a non truthy value and just before contents are set to visible. |
	 *
	 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
	 * directive expression is true and false. This system works like the animation system present with
	 * `ngClass` except that you must also include the `!important` flag to override the display
	 * property so that the elements are not actually hidden during the animation.
	 *
	 * ```css
	 * /&#42; A working example can be found at the bottom of this page. &#42;/
	 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
	 *   transition: all 0.5s linear;
	 * }
	 *
	 * .my-element.ng-hide-add { ... }
	 * .my-element.ng-hide-add.ng-hide-add-active { ... }
	 * .my-element.ng-hide-remove { ... }
	 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
	 * ```
	 *
	 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
	 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
	 *
	 * @element ANY
	 * @param {expression} ngHide If the {@link guide/expression expression} is truthy/falsy then the
	 *                            element is hidden/shown respectively.
	 *
	 * @example
	 * A simple example, animating the element's opacity:
	 *
	  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-simple">
		<file name="index.html">
		  Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
		  <div class="check-element animate-show-hide" ng-hide="checked">
			I hide when your checkbox is checked.
		  </div>
		</file>
		<file name="animations.css">
		  .animate-show-hide.ng-hide {
			opacity: 0;
		  }
	
		  .animate-show-hide.ng-hide-add,
		  .animate-show-hide.ng-hide-remove {
			transition: all linear 0.5s;
		  }
	
		  .check-element {
			border: 1px solid black;
			opacity: 1;
			padding: 10px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  it('should check ngHide', function() {
			var checkbox = element(by.model('checked'));
			var checkElem = element(by.css('.check-element'));
	
			expect(checkElem.isDisplayed()).toBe(true);
			checkbox.click();
			expect(checkElem.isDisplayed()).toBe(false);
		  });
		</file>
	  </example>
	 *
	 * <hr />
	 * @example
	 * A more complex example, featuring different show/hide animations:
	 *
	  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-complex">
		<file name="index.html">
		  Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
		  <div class="check-element funky-show-hide" ng-hide="checked">
			I hide when your checkbox is checked.
		  </div>
		</file>
		<file name="animations.css">
		  body {
			overflow: hidden;
			perspective: 1000px;
		  }
	
		  .funky-show-hide.ng-hide-add {
			transform: rotateZ(0);
			transform-origin: right;
			transition: all 0.5s ease-in-out;
		  }
	
		  .funky-show-hide.ng-hide-add.ng-hide-add-active {
			transform: rotateZ(-135deg);
		  }
	
		  .funky-show-hide.ng-hide-remove {
			transform: rotateY(90deg);
			transform-origin: left;
			transition: all 0.5s ease;
		  }
	
		  .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
			transform: rotateY(0);
		  }
	
		  .check-element {
			border: 1px solid black;
			opacity: 1;
			padding: 10px;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  it('should check ngHide', function() {
			var checkbox = element(by.model('checked'));
			var checkElem = element(by.css('.check-element'));
	
			expect(checkElem.isDisplayed()).toBe(true);
			checkbox.click();
			expect(checkElem.isDisplayed()).toBe(false);
		  });
		</file>
	  </example>
	 *
	 * @knownIssue
	 *
	 * ### Flickering when using ngHide to toggle between elements
	 *
	 * When using {@link ngShow} and / or {@link ngHide} to toggle between elements, it can
	 * happen that both the element to show and the element to hide are visible for a very short time.
	 *
	 * This usually happens when the {@link ngAnimate ngAnimate module} is included, but no actual animations
	 * are defined for {@link ngShow} / {@link ngHide}. Internet Explorer is affected more often than
	 * other browsers.
	 *
	 * There are several way to mitigate this problem:
	 *
	 * - {@link guide/animations#how-to-selectively-enable-disable-and-skip-animations Disable animations on the affected elements}.
	 * - Use {@link ngIf} or {@link ngSwitch} instead of {@link ngShow} / {@link ngHide}.
	 * - Use the special CSS selector `ng-hide.ng-hide-animate` to set `{display: none}` or similar on the affected elements.
	 * - Use `ng-class="{'ng-hide': expression}` instead of instead of {@link ngShow} / {@link ngHide}.
	 * - Define an animation on the affected elements.
	 */
	var ngHideDirective = ['$animate', function ($animate) {
		return {
			restrict: 'A',
			multiElement: true,
			link: function (scope, element, attr) {
				scope.$watch(attr.ngHide, function ngHideWatchAction(value) {
					// The comment inside of the ngShowDirective explains why we add and
					// remove a temporary class for the show/hide animation
					$animate[value ? 'addClass' : 'removeClass'](element, NG_HIDE_CLASS, {
						tempClasses: NG_HIDE_IN_PROGRESS_CLASS
					});
				});
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngStyle
	 * @restrict AC
	 *
	 * @description
	 * The `ngStyle` directive allows you to set CSS style on an HTML element conditionally.
	 *
	 * @knownIssue
	 * You should not use {@link guide/interpolation interpolation} in the value of the `style`
	 * attribute, when using the `ngStyle` directive on the same element.
	 * See {@link guide/interpolation#known-issues here} for more info.
	 *
	 * @element ANY
	 * @param {expression} ngStyle
	 *
	 * {@link guide/expression Expression} which evals to an
	 * object whose keys are CSS style names and values are corresponding values for those CSS
	 * keys.
	 *
	 * Since some CSS style names are not valid keys for an object, they must be quoted.
	 * See the 'background-color' style in the example below.
	 *
	 * @example
	   <example name="ng-style">
		 <file name="index.html">
			<input type="button" value="set color" ng-click="myStyle={color:'red'}">
			<input type="button" value="set background" ng-click="myStyle={'background-color':'blue'}">
			<input type="button" value="clear" ng-click="myStyle={}">
			<br/>
			<span ng-style="myStyle">Sample Text</span>
			<pre>myStyle={{myStyle}}</pre>
		 </file>
		 <file name="style.css">
		   span {
			 color: black;
		   }
		 </file>
		 <file name="protractor.js" type="protractor">
		   var colorSpan = element(by.css('span'));
	
		   it('should check ng-style', function() {
			 expect(colorSpan.getCssValue('color')).toMatch(/rgba\(0, 0, 0, 1\)|rgb\(0, 0, 0\)/);
			 element(by.css('input[value=\'set color\']')).click();
			 expect(colorSpan.getCssValue('color')).toMatch(/rgba\(255, 0, 0, 1\)|rgb\(255, 0, 0\)/);
			 element(by.css('input[value=clear]')).click();
			 expect(colorSpan.getCssValue('color')).toMatch(/rgba\(0, 0, 0, 1\)|rgb\(0, 0, 0\)/);
		   });
		 </file>
	   </example>
	 */
	var ngStyleDirective = ngDirective(function (scope, element, attr) {
		scope.$watchCollection(attr.ngStyle, function ngStyleWatchAction(newStyles, oldStyles) {
			if (oldStyles && (newStyles !== oldStyles)) {
				forEach(oldStyles, function (val, style) { element.css(style, ''); });
			}
			if (newStyles) element.css(newStyles);
		});
	});

	/**
	 * @ngdoc directive
	 * @name ngSwitch
	 * @restrict EA
	 *
	 * @description
	 * The `ngSwitch` directive is used to conditionally swap DOM structure on your template based on a scope expression.
	 * Elements within `ngSwitch` but without `ngSwitchWhen` or `ngSwitchDefault` directives will be preserved at the location
	 * as specified in the template.
	 *
	 * The directive itself works similar to ngInclude, however, instead of downloading template code (or loading it
	 * from the template cache), `ngSwitch` simply chooses one of the nested elements and makes it visible based on which element
	 * matches the value obtained from the evaluated expression. In other words, you define a container element
	 * (where you place the directive), place an expression on the **`on="..."` attribute**
	 * (or the **`ng-switch="..."` attribute**), define any inner elements inside of the directive and place
	 * a when attribute per element. The when attribute is used to inform ngSwitch which element to display when the on
	 * expression is evaluated. If a matching expression is not found via a when attribute then an element with the default
	 * attribute is displayed.
	 *
	 * <div class="alert alert-info">
	 * Be aware that the attribute values to match against cannot be expressions. They are interpreted
	 * as literal string values to match against.
	 * For example, **`ng-switch-when="someVal"`** will match against the string `"someVal"` not against the
	 * value of the expression `$scope.someVal`.
	 * </div>
	
	 * @animations
	 * | Animation                        | Occurs                              |
	 * |----------------------------------|-------------------------------------|
	 * | {@link ng.$animate#enter enter}  | after the ngSwitch contents change and the matched child element is placed inside the container |
	 * | {@link ng.$animate#leave leave}  | after the ngSwitch contents change and just before the former contents are removed from the DOM |
	 *
	 * @usage
	 *
	 * ```
	 * <ANY ng-switch="expression">
	 *   <ANY ng-switch-when="matchValue1">...</ANY>
	 *   <ANY ng-switch-when="matchValue2">...</ANY>
	 *   <ANY ng-switch-default>...</ANY>
	 * </ANY>
	 * ```
	 *
	 *
	 * @scope
	 * @priority 1200
	 * @param {*} ngSwitch|on expression to match against <code>ng-switch-when</code>.
	 * On child elements add:
	 *
	 * * `ngSwitchWhen`: the case statement to match against. If match then this
	 *   case will be displayed. If the same match appears multiple times, all the
	 *   elements will be displayed. It is possible to associate multiple values to
	 *   the same `ngSwitchWhen` by defining the optional attribute
	 *   `ngSwitchWhenSeparator`. The separator will be used to split the value of
	 *   the `ngSwitchWhen` attribute into multiple tokens, and the element will show
	 *   if any of the `ngSwitch` evaluates to any of these tokens.
	 * * `ngSwitchDefault`: the default case when no other case match. If there
	 *   are multiple default cases, all of them will be displayed when no other
	 *   case match.
	 *
	 *
	 * @example
	  <example module="switchExample" deps="angular-animate.js" animations="true" name="ng-switch">
		<file name="index.html">
		  <div ng-controller="ExampleController">
			<select ng-model="selection" ng-options="item for item in items">
			</select>
			<code>selection={{selection}}</code>
			<hr/>
			<div class="animate-switch-container"
			  ng-switch on="selection">
				<div class="animate-switch" ng-switch-when="settings|options" ng-switch-when-separator="|">Settings Div</div>
				<div class="animate-switch" ng-switch-when="home">Home Span</div>
				<div class="animate-switch" ng-switch-default>default</div>
			</div>
		  </div>
		</file>
		<file name="script.js">
		  angular.module('switchExample', ['ngAnimate'])
			.controller('ExampleController', ['$scope', function($scope) {
			  $scope.items = ['settings', 'home', 'options', 'other'];
			  $scope.selection = $scope.items[0];
			}]);
		</file>
		<file name="animations.css">
		  .animate-switch-container {
			position:relative;
			background:white;
			border:1px solid black;
			height:40px;
			overflow:hidden;
		  }
	
		  .animate-switch {
			padding:10px;
		  }
	
		  .animate-switch.ng-animate {
			transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
	
			position:absolute;
			top:0;
			left:0;
			right:0;
			bottom:0;
		  }
	
		  .animate-switch.ng-leave.ng-leave-active,
		  .animate-switch.ng-enter {
			top:-50px;
		  }
		  .animate-switch.ng-leave,
		  .animate-switch.ng-enter.ng-enter-active {
			top:0;
		  }
		</file>
		<file name="protractor.js" type="protractor">
		  var switchElem = element(by.css('[ng-switch]'));
		  var select = element(by.model('selection'));
	
		  it('should start in settings', function() {
			expect(switchElem.getText()).toMatch(/Settings Div/);
		  });
		  it('should change to home', function() {
			select.all(by.css('option')).get(1).click();
			expect(switchElem.getText()).toMatch(/Home Span/);
		  });
		  it('should change to settings via "options"', function() {
			select.all(by.css('option')).get(2).click();
			expect(switchElem.getText()).toMatch(/Settings Div/);
		  });
		  it('should select default', function() {
			select.all(by.css('option')).get(3).click();
			expect(switchElem.getText()).toMatch(/default/);
		  });
		</file>
	  </example>
	 */
	var ngSwitchDirective = ['$animate', '$compile', function ($animate, $compile) {
		return {
			require: 'ngSwitch',

			// asks for $scope to fool the BC controller module
			controller: ['$scope', function NgSwitchController() {
				this.cases = {};
			}],
			link: function (scope, element, attr, ngSwitchController) {
				var watchExpr = attr.ngSwitch || attr.on,
					selectedTranscludes = [],
					selectedElements = [],
					previousLeaveAnimations = [],
					selectedScopes = [];

				var spliceFactory = function (array, index) {
					return function (response) {
						if (response !== false) array.splice(index, 1);
					};
				};

				scope.$watch(watchExpr, function ngSwitchWatchAction(value) {
					var i, ii;

					// Start with the last, in case the array is modified during the loop
					while (previousLeaveAnimations.length) {
						$animate.cancel(previousLeaveAnimations.pop());
					}

					for (i = 0, ii = selectedScopes.length; i < ii; ++i) {
						var selected = getBlockNodes(selectedElements[i].clone);
						selectedScopes[i].$destroy();
						var runner = previousLeaveAnimations[i] = $animate.leave(selected);
						runner.done(spliceFactory(previousLeaveAnimations, i));
					}

					selectedElements.length = 0;
					selectedScopes.length = 0;

					if ((selectedTranscludes = ngSwitchController.cases['!' + value] || ngSwitchController.cases['?'])) {
						forEach(selectedTranscludes, function (selectedTransclude) {
							selectedTransclude.transclude(function (caseElement, selectedScope) {
								selectedScopes.push(selectedScope);
								var anchor = selectedTransclude.element;
								caseElement[caseElement.length++] = $compile.$$createComment('end ngSwitchWhen');
								var block = { clone: caseElement };

								selectedElements.push(block);
								$animate.enter(caseElement, anchor.parent(), anchor);
							});
						});
					}
				});
			}
		};
	}];

	var ngSwitchWhenDirective = ngDirective({
		transclude: 'element',
		priority: 1200,
		require: '^ngSwitch',
		multiElement: true,
		link: function (scope, element, attrs, ctrl, $transclude) {

			var cases = attrs.ngSwitchWhen.split(attrs.ngSwitchWhenSeparator).sort().filter(
				// Filter duplicate cases
				function (element, index, array) { return array[index - 1] !== element; }
			);

			forEach(cases, function (whenCase) {
				ctrl.cases['!' + whenCase] = (ctrl.cases['!' + whenCase] || []);
				ctrl.cases['!' + whenCase].push({ transclude: $transclude, element: element });
			});
		}
	});

	var ngSwitchDefaultDirective = ngDirective({
		transclude: 'element',
		priority: 1200,
		require: '^ngSwitch',
		multiElement: true,
		link: function (scope, element, attr, ctrl, $transclude) {
			ctrl.cases['?'] = (ctrl.cases['?'] || []);
			ctrl.cases['?'].push({ transclude: $transclude, element: element });
		}
	});

	/**
	 * @ngdoc directive
	 * @name ngTransclude
	 * @restrict EAC
	 *
	 * @description
	 * Directive that marks the insertion point for the transcluded DOM of the nearest parent directive that uses transclusion.
	 *
	 * You can specify that you want to insert a named transclusion slot, instead of the default slot, by providing the slot name
	 * as the value of the `ng-transclude` or `ng-transclude-slot` attribute.
	 *
	 * If the transcluded content is not empty (i.e. contains one or more DOM nodes, including whitespace text nodes), any existing
	 * content of this element will be removed before the transcluded content is inserted.
	 * If the transcluded content is empty (or only whitespace), the existing content is left intact. This lets you provide fallback
	 * content in the case that no transcluded content is provided.
	 *
	 * @element ANY
	 *
	 * @param {string} ngTransclude|ngTranscludeSlot the name of the slot to insert at this point. If this is not provided, is empty
	 *                                               or its value is the same as the name of the attribute then the default slot is used.
	 *
	 * @example
	 * ### Basic transclusion
	 * This example demonstrates basic transclusion of content into a component directive.
	 * <example name="simpleTranscludeExample" module="transcludeExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('transcludeExample', [])
	 *        .directive('pane', function(){
	 *           return {
	 *             restrict: 'E',
	 *             transclude: true,
	 *             scope: { title:'@' },
	 *             template: '<div style="border: 1px solid black;">' +
	 *                         '<div style="background-color: gray">{{title}}</div>' +
	 *                         '<ng-transclude></ng-transclude>' +
	 *                       '</div>'
	 *           };
	 *       })
	 *       .controller('ExampleController', ['$scope', function($scope) {
	 *         $scope.title = 'Lorem Ipsum';
	 *         $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
	 *       }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <input ng-model="title" aria-label="title"> <br/>
	 *       <textarea ng-model="text" aria-label="text"></textarea> <br/>
	 *       <pane title="{{title}}"><span>{{text}}</span></pane>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *      it('should have transcluded', function() {
	 *        var titleElement = element(by.model('title'));
	 *        titleElement.clear();
	 *        titleElement.sendKeys('TITLE');
	 *        var textElement = element(by.model('text'));
	 *        textElement.clear();
	 *        textElement.sendKeys('TEXT');
	 *        expect(element(by.binding('title')).getText()).toEqual('TITLE');
	 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
	 *      });
	 *   </file>
	 * </example>
	 *
	 * @example
	 * ### Transclude fallback content
	 * This example shows how to use `NgTransclude` with fallback content, that
	 * is displayed if no transcluded content is provided.
	 *
	 * <example module="transcludeFallbackContentExample" name="ng-transclude">
	 * <file name="index.html">
	 * <script>
	 * angular.module('transcludeFallbackContentExample', [])
	 * .directive('myButton', function(){
	 *             return {
	 *               restrict: 'E',
	 *               transclude: true,
	 *               scope: true,
	 *               template: '<button style="cursor: pointer;">' +
	 *                           '<ng-transclude>' +
	 *                             '<b style="color: red;">Button1</b>' +
	 *                           '</ng-transclude>' +
	 *                         '</button>'
	 *             };
	 *         });
	 * </script>
	 * <!-- fallback button content -->
	 * <my-button id="fallback"></my-button>
	 * <!-- modified button content -->
	 * <my-button id="modified">
	 *   <i style="color: green;">Button2</i>
	 * </my-button>
	 * </file>
	 * <file name="protractor.js" type="protractor">
	 * it('should have different transclude element content', function() {
	 *          expect(element(by.id('fallback')).getText()).toBe('Button1');
	 *          expect(element(by.id('modified')).getText()).toBe('Button2');
	 *        });
	 * </file>
	 * </example>
	 *
	 * @example
	 * ### Multi-slot transclusion
	 * This example demonstrates using multi-slot transclusion in a component directive.
	 * <example name="multiSlotTranscludeExample" module="multiSlotTranscludeExample">
	 *   <file name="index.html">
	 *    <style>
	 *      .title, .footer {
	 *        background-color: gray
	 *      }
	 *    </style>
	 *    <div ng-controller="ExampleController">
	 *      <input ng-model="title" aria-label="title"> <br/>
	 *      <textarea ng-model="text" aria-label="text"></textarea> <br/>
	 *      <pane>
	 *        <pane-title><a ng-href="{{link}}">{{title}}</a></pane-title>
	 *        <pane-body><p>{{text}}</p></pane-body>
	 *      </pane>
	 *    </div>
	 *   </file>
	 *   <file name="app.js">
	 *    angular.module('multiSlotTranscludeExample', [])
	 *     .directive('pane', function() {
	 *        return {
	 *          restrict: 'E',
	 *          transclude: {
	 *            'title': '?paneTitle',
	 *            'body': 'paneBody',
	 *            'footer': '?paneFooter'
	 *          },
	 *          template: '<div style="border: 1px solid black;">' +
	 *                      '<div class="title" ng-transclude="title">Fallback Title</div>' +
	 *                      '<div ng-transclude="body"></div>' +
	 *                      '<div class="footer" ng-transclude="footer">Fallback Footer</div>' +
	 *                    '</div>'
	 *        };
	 *    })
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.title = 'Lorem Ipsum';
	 *      $scope.link = 'https://google.com';
	 *      $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
	 *    }]);
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *      it('should have transcluded the title and the body', function() {
	 *        var titleElement = element(by.model('title'));
	 *        titleElement.clear();
	 *        titleElement.sendKeys('TITLE');
	 *        var textElement = element(by.model('text'));
	 *        textElement.clear();
	 *        textElement.sendKeys('TEXT');
	 *        expect(element(by.css('.title')).getText()).toEqual('TITLE');
	 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
	 *        expect(element(by.css('.footer')).getText()).toEqual('Fallback Footer');
	 *      });
	 *   </file>
	 * </example>
	 */
	var ngTranscludeMinErr = minErr('ngTransclude');
	var ngTranscludeDirective = ['$compile', function ($compile) {
		return {
			restrict: 'EAC',
			compile: function ngTranscludeCompile(tElement) {

				// Remove and cache any original content to act as a fallback
				var fallbackLinkFn = $compile(tElement.contents());
				tElement.empty();

				return function ngTranscludePostLink($scope, $element, $attrs, controller, $transclude) {

					if (!$transclude) {
						throw ngTranscludeMinErr('orphan',
							'Illegal use of ngTransclude directive in the template! ' +
							'No parent directive that requires a transclusion found. ' +
							'Element: {0}',
							startingTag($element));
					}


					// If the attribute is of the form: `ng-transclude="ng-transclude"` then treat it like the default
					if ($attrs.ngTransclude === $attrs.$attr.ngTransclude) {
						$attrs.ngTransclude = '';
					}
					var slotName = $attrs.ngTransclude || $attrs.ngTranscludeSlot;

					// If the slot is required and no transclusion content is provided then this call will throw an error
					$transclude(ngTranscludeCloneAttachFn, null, slotName);

					// If the slot is optional and no transclusion content is provided then use the fallback content
					if (slotName && !$transclude.isSlotFilled(slotName)) {
						useFallbackContent();
					}

					function ngTranscludeCloneAttachFn(clone, transcludedScope) {
						if (clone.length && notWhitespace(clone)) {
							$element.append(clone);
						} else {
							useFallbackContent();
							// There is nothing linked against the transcluded scope since no content was available,
							// so it should be safe to clean up the generated scope.
							transcludedScope.$destroy();
						}
					}

					function useFallbackContent() {
						// Since this is the fallback content rather than the transcluded content,
						// we link against the scope of this directive rather than the transcluded scope
						fallbackLinkFn($scope, function (clone) {
							$element.append(clone);
						});
					}

					function notWhitespace(nodes) {
						for (var i = 0, ii = nodes.length; i < ii; i++) {
							var node = nodes[i];
							if (node.nodeType !== NODE_TYPE_TEXT || node.nodeValue.trim()) {
								return true;
							}
						}
					}
				};
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name script
	 * @restrict E
	 *
	 * @description
	 * Load the content of a `<script>` element into {@link ng.$templateCache `$templateCache`}, so that the
	 * template can be used by {@link ng.directive:ngInclude `ngInclude`},
	 * {@link ngRoute.directive:ngView `ngView`}, or {@link guide/directive directives}. The type of the
	 * `<script>` element must be specified as `text/ng-template`, and a cache name for the template must be
	 * assigned through the element's `id`, which can then be used as a directive's `templateUrl`.
	 *
	 * @param {string} type Must be set to `'text/ng-template'`.
	 * @param {string} id Cache name of the template.
	 *
	 * @example
	  <example  name="script-tag">
		<file name="index.html">
		  <script type="text/ng-template" id="/tpl.html">
			Content of the template.
		  </script>
	
		  <a ng-click="currentTpl='/tpl.html'" id="tpl-link">Load inlined template</a>
		  <div id="tpl-content" ng-include src="currentTpl"></div>
		</file>
		<file name="protractor.js" type="protractor">
		  it('should load template defined inside script tag', function() {
			element(by.css('#tpl-link')).click();
			expect(element(by.css('#tpl-content')).getText()).toMatch(/Content of the template/);
		  });
		</file>
	  </example>
	 */
	var scriptDirective = ['$templateCache', function ($templateCache) {
		return {
			restrict: 'E',
			terminal: true,
			compile: function (element, attr) {
				if (attr.type === 'text/ng-template') {
					var templateUrl = attr.id,
						text = element[0].text;

					$templateCache.put(templateUrl, text);
				}
			}
		};
	}];

	/* exported selectDirective, optionDirective */

	var noopNgModelController = { $setViewValue: noop, $render: noop };

	function setOptionSelectedStatus(optionEl, value) {
		optionEl.prop('selected', value);
		/**
		 * When unselecting an option, setting the property to null / false should be enough
		 * However, screenreaders might react to the selected attribute instead, see
		 * https://github.com/angular/angular.js/issues/14419
		 * Note: "selected" is a boolean attr and will be removed when the "value" arg in attr() is false
		 * or null
		 */
		optionEl.attr('selected', value);
	}

	/**
	 * @ngdoc type
	 * @name  select.SelectController
	 *
	 * @description
	 * The controller for the {@link ng.select select} directive. The controller exposes
	 * a few utility methods that can be used to augment the behavior of a regular or an
	 * {@link ng.ngOptions ngOptions} select element.
	 *
	 * @example
	 * ### Set a custom error when the unknown option is selected
	 *
	 * This example sets a custom error "unknownValue" on the ngModelController
	 * when the select element's unknown option is selected, i.e. when the model is set to a value
	 * that is not matched by any option.
	 *
	 * <example name="select-unknown-value-error" module="staticSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="testSelect"> Single select: </label><br>
	 *     <select name="testSelect" ng-model="selected" unknown-value-error>
	 *       <option value="option-1">Option 1</option>
	 *       <option value="option-2">Option 2</option>
	 *     </select><br>
	 *     <span class="error" ng-if="myForm.testSelect.$error.unknownValue">
	 *       Error: The current model doesn't match any option</span><br>
	 *
	 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
	 *   </form>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('staticSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.selected = null;
	 *
	 *      $scope.forceUnknownOption = function() {
	 *        $scope.selected = 'nonsense';
	 *      };
	 *   }])
	 *   .directive('unknownValueError', function() {
	 *     return {
	 *       require: ['ngModel', 'select'],
	 *       link: function(scope, element, attrs, ctrls) {
	 *         var ngModelCtrl = ctrls[0];
	 *         var selectCtrl = ctrls[1];
	 *
	 *         ngModelCtrl.$validators.unknownValue = function(modelValue, viewValue) {
	 *           if (selectCtrl.$isUnknownOptionSelected()) {
	 *             return false;
	 *           }
	 *
	 *           return true;
	 *         };
	 *       }
	 *
	 *     };
	 *   });
	 * </file>
	 *</example>
	 *
	 *
	 * @example
	 * ### Set the "required" error when the unknown option is selected.
	 *
	 * By default, the "required" error on the ngModelController is only set on a required select
	 * when the empty option is selected. This example adds a custom directive that also sets the
	 * error when the unknown option is selected.
	 *
	 * <example name="select-unknown-value-required" module="staticSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="testSelect"> Select: </label><br>
	 *     <select name="testSelect" ng-model="selected" required unknown-value-required>
	 *       <option value="option-1">Option 1</option>
	 *       <option value="option-2">Option 2</option>
	 *     </select><br>
	 *     <span class="error" ng-if="myForm.testSelect.$error.required">Error: Please select a value</span><br>
	 *
	 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
	 *   </form>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('staticSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.selected = null;
	 *
	 *      $scope.forceUnknownOption = function() {
	 *        $scope.selected = 'nonsense';
	 *      };
	 *   }])
	 *   .directive('unknownValueRequired', function() {
	 *     return {
	 *       priority: 1, // This directive must run after the required directive has added its validator
	 *       require: ['ngModel', 'select'],
	 *       link: function(scope, element, attrs, ctrls) {
	 *         var ngModelCtrl = ctrls[0];
	 *         var selectCtrl = ctrls[1];
	 *
	 *         var originalRequiredValidator = ngModelCtrl.$validators.required;
	 *
	 *         ngModelCtrl.$validators.required = function() {
	 *           if (attrs.required && selectCtrl.$isUnknownOptionSelected()) {
	 *             return false;
	 *           }
	 *
	 *           return originalRequiredValidator.apply(this, arguments);
	 *         };
	 *       }
	 *     };
	 *   });
	 * </file>
	 * <file name="protractor.js" type="protractor">
	 *  it('should show the error message when the unknown option is selected', function() {
	
		  var error = element(by.className('error'));
	
		  expect(error.getText()).toBe('Error: Please select a value');
	
		  element(by.cssContainingText('option', 'Option 1')).click();
	
		  expect(error.isPresent()).toBe(false);
	
		  element(by.tagName('button')).click();
	
		  expect(error.getText()).toBe('Error: Please select a value');
		});
	 * </file>
	 *</example>
	 *
	 *
	 */
	var SelectController =
		['$element', '$scope', /** @this */ function ($element, $scope) {

			var self = this,
				optionsMap = new NgMap();

			self.selectValueMap = {}; // Keys are the hashed values, values the original values

			// If the ngModel doesn't get provided then provide a dummy noop version to prevent errors
			self.ngModelCtrl = noopNgModelController;
			self.multiple = false;

			// The "unknown" option is one that is prepended to the list if the viewValue
			// does not match any of the options. When it is rendered the value of the unknown
			// option is '? XXX ?' where XXX is the hashKey of the value that is not known.
			//
			// Support: IE 9 only
			// We can't just jqLite('<option>') since jqLite is not smart enough
			// to create it in <select> and IE barfs otherwise.
			self.unknownOption = jqLite(window.document.createElement('option'));

			// The empty option is an option with the value '' that the application developer can
			// provide inside the select. It is always selectable and indicates that a "null" selection has
			// been made by the user.
			// If the select has an empty option, and the model of the select is set to "undefined" or "null",
			// the empty option is selected.
			// If the model is set to a different unmatched value, the unknown option is rendered and
			// selected, i.e both are present, because a "null" selection and an unknown value are different.
			self.hasEmptyOption = false;
			self.emptyOption = undefined;

			self.renderUnknownOption = function (val) {
				var unknownVal = self.generateUnknownOptionValue(val);
				self.unknownOption.val(unknownVal);
				$element.prepend(self.unknownOption);
				setOptionSelectedStatus(self.unknownOption, true);
				$element.val(unknownVal);
			};

			self.updateUnknownOption = function (val) {
				var unknownVal = self.generateUnknownOptionValue(val);
				self.unknownOption.val(unknownVal);
				setOptionSelectedStatus(self.unknownOption, true);
				$element.val(unknownVal);
			};

			self.generateUnknownOptionValue = function (val) {
				return '? ' + hashKey(val) + ' ?';
			};

			self.removeUnknownOption = function () {
				if (self.unknownOption.parent()) self.unknownOption.remove();
			};

			self.selectEmptyOption = function () {
				if (self.emptyOption) {
					$element.val('');
					setOptionSelectedStatus(self.emptyOption, true);
				}
			};

			self.unselectEmptyOption = function () {
				if (self.hasEmptyOption) {
					setOptionSelectedStatus(self.emptyOption, false);
				}
			};

			$scope.$on('$destroy', function () {
				// disable unknown option so that we don't do work when the whole select is being destroyed
				self.renderUnknownOption = noop;
			});

			// Read the value of the select control, the implementation of this changes depending
			// upon whether the select can have multiple values and whether ngOptions is at work.
			self.readValue = function readSingleValue() {
				var val = $element.val();
				// ngValue added option values are stored in the selectValueMap, normal interpolations are not
				var realVal = val in self.selectValueMap ? self.selectValueMap[val] : val;

				if (self.hasOption(realVal)) {
					return realVal;
				}

				return null;
			};


			// Write the value to the select control, the implementation of this changes depending
			// upon whether the select can have multiple values and whether ngOptions is at work.
			self.writeValue = function writeSingleValue(value) {
				// Make sure to remove the selected attribute from the previously selected option
				// Otherwise, screen readers might get confused
				var currentlySelectedOption = $element[0].options[$element[0].selectedIndex];
				if (currentlySelectedOption) setOptionSelectedStatus(jqLite(currentlySelectedOption), false);

				if (self.hasOption(value)) {
					self.removeUnknownOption();

					var hashedVal = hashKey(value);
					$element.val(hashedVal in self.selectValueMap ? hashedVal : value);

					// Set selected attribute and property on selected option for screen readers
					var selectedOption = $element[0].options[$element[0].selectedIndex];
					setOptionSelectedStatus(jqLite(selectedOption), true);
				} else {
					self.selectUnknownOrEmptyOption(value);
				}
			};


			// Tell the select control that an option, with the given value, has been added
			self.addOption = function (value, element) {
				// Skip comment nodes, as they only pollute the `optionsMap`
				if (element[0].nodeType === NODE_TYPE_COMMENT) return;

				assertNotHasOwnProperty(value, '"option value"');
				if (value === '') {
					self.hasEmptyOption = true;
					self.emptyOption = element;
				}
				var count = optionsMap.get(value) || 0;
				optionsMap.set(value, count + 1);
				// Only render at the end of a digest. This improves render performance when many options
				// are added during a digest and ensures all relevant options are correctly marked as selected
				scheduleRender();
			};

			// Tell the select control that an option, with the given value, has been removed
			self.removeOption = function (value) {
				var count = optionsMap.get(value);
				if (count) {
					if (count === 1) {
						optionsMap.delete(value);
						if (value === '') {
							self.hasEmptyOption = false;
							self.emptyOption = undefined;
						}
					} else {
						optionsMap.set(value, count - 1);
					}
				}
			};

			// Check whether the select control has an option matching the given value
			self.hasOption = function (value) {
				return !!optionsMap.get(value);
			};

			/**
			 * @ngdoc method
			 * @name select.SelectController#$hasEmptyOption
			 *
			 * @description
			 *
			 * Returns `true` if the select element currently has an empty option
			 * element, i.e. an option that signifies that the select is empty / the selection is null.
			 *
			 */
			self.$hasEmptyOption = function () {
				return self.hasEmptyOption;
			};

			/**
			 * @ngdoc method
			 * @name select.SelectController#$isUnknownOptionSelected
			 *
			 * @description
			 *
			 * Returns `true` if the select element's unknown option is selected. The unknown option is added
			 * and automatically selected whenever the select model doesn't match any option.
			 *
			 */
			self.$isUnknownOptionSelected = function () {
				// Presence of the unknown option means it is selected
				return $element[0].options[0] === self.unknownOption[0];
			};

			/**
			 * @ngdoc method
			 * @name select.SelectController#$isEmptyOptionSelected
			 *
			 * @description
			 *
			 * Returns `true` if the select element has an empty option and this empty option is currently
			 * selected. Returns `false` if the select element has no empty option or it is not selected.
			 *
			 */
			self.$isEmptyOptionSelected = function () {
				return self.hasEmptyOption && $element[0].options[$element[0].selectedIndex] === self.emptyOption[0];
			};

			self.selectUnknownOrEmptyOption = function (value) {
				if (value == null && self.emptyOption) {
					self.removeUnknownOption();
					self.selectEmptyOption();
				} else if (self.unknownOption.parent().length) {
					self.updateUnknownOption(value);
				} else {
					self.renderUnknownOption(value);
				}
			};

			var renderScheduled = false;
			function scheduleRender() {
				if (renderScheduled) return;
				renderScheduled = true;
				$scope.$$postDigest(function () {
					renderScheduled = false;
					self.ngModelCtrl.$render();
				});
			}

			var updateScheduled = false;
			function scheduleViewValueUpdate(renderAfter) {
				if (updateScheduled) return;

				updateScheduled = true;

				$scope.$$postDigest(function () {
					if ($scope.$$destroyed) return;

					updateScheduled = false;
					self.ngModelCtrl.$setViewValue(self.readValue());
					if (renderAfter) self.ngModelCtrl.$render();
				});
			}


			self.registerOption = function (optionScope, optionElement, optionAttrs, interpolateValueFn, interpolateTextFn) {

				if (optionAttrs.$attr.ngValue) {
					// The value attribute is set by ngValue
					var oldVal, hashedVal;
					optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {

						var removal;
						var previouslySelected = optionElement.prop('selected');

						if (isDefined(hashedVal)) {
							self.removeOption(oldVal);
							delete self.selectValueMap[hashedVal];
							removal = true;
						}

						hashedVal = hashKey(newVal);
						oldVal = newVal;
						self.selectValueMap[hashedVal] = newVal;
						self.addOption(newVal, optionElement);
						// Set the attribute directly instead of using optionAttrs.$set - this stops the observer
						// from firing a second time. Other $observers on value will also get the result of the
						// ngValue expression, not the hashed value
						optionElement.attr('value', hashedVal);

						if (removal && previouslySelected) {
							scheduleViewValueUpdate();
						}

					});
				} else if (interpolateValueFn) {
					// The value attribute is interpolated
					optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {
						// This method is overwritten in ngOptions and has side-effects!
						self.readValue();

						var removal;
						var previouslySelected = optionElement.prop('selected');

						if (isDefined(oldVal)) {
							self.removeOption(oldVal);
							removal = true;
						}
						oldVal = newVal;
						self.addOption(newVal, optionElement);

						if (removal && previouslySelected) {
							scheduleViewValueUpdate();
						}
					});
				} else if (interpolateTextFn) {
					// The text content is interpolated
					optionScope.$watch(interpolateTextFn, function interpolateWatchAction(newVal, oldVal) {
						optionAttrs.$set('value', newVal);
						var previouslySelected = optionElement.prop('selected');
						if (oldVal !== newVal) {
							self.removeOption(oldVal);
						}
						self.addOption(newVal, optionElement);

						if (oldVal && previouslySelected) {
							scheduleViewValueUpdate();
						}
					});
				} else {
					// The value attribute is static
					self.addOption(optionAttrs.value, optionElement);
				}


				optionAttrs.$observe('disabled', function (newVal) {

					// Since model updates will also select disabled options (like ngOptions),
					// we only have to handle options becoming disabled, not enabled

					if (newVal === 'true' || newVal && optionElement.prop('selected')) {
						if (self.multiple) {
							scheduleViewValueUpdate(true);
						} else {
							self.ngModelCtrl.$setViewValue(null);
							self.ngModelCtrl.$render();
						}
					}
				});

				optionElement.on('$destroy', function () {
					var currentValue = self.readValue();
					var removeValue = optionAttrs.value;

					self.removeOption(removeValue);
					scheduleRender();

					if (self.multiple && currentValue && currentValue.indexOf(removeValue) !== -1 ||
						currentValue === removeValue
					) {
						// When multiple (selected) options are destroyed at the same time, we don't want
						// to run a model update for each of them. Instead, run a single update in the $$postDigest
						scheduleViewValueUpdate(true);
					}
				});
			};
		}];

	/**
	 * @ngdoc directive
	 * @name select
	 * @restrict E
	 *
	 * @description
	 * HTML `select` element with AngularJS data-binding.
	 *
	 * The `select` directive is used together with {@link ngModel `ngModel`} to provide data-binding
	 * between the scope and the `<select>` control (including setting default values).
	 * It also handles dynamic `<option>` elements, which can be added using the {@link ngRepeat `ngRepeat}` or
	 * {@link ngOptions `ngOptions`} directives.
	 *
	 * When an item in the `<select>` menu is selected, the value of the selected option will be bound
	 * to the model identified by the `ngModel` directive. With static or repeated options, this is
	 * the content of the `value` attribute or the textContent of the `<option>`, if the value attribute is missing.
	 * Value and textContent can be interpolated.
	 *
	 * The {@link select.SelectController select controller} exposes utility functions that can be used
	 * to manipulate the select's behavior.
	 *
	 * ## Matching model and option values
	 *
	 * In general, the match between the model and an option is evaluated by strictly comparing the model
	 * value against the value of the available options.
	 *
	 * If you are setting the option value with the option's `value` attribute, or textContent, the
	 * value will always be a `string` which means that the model value must also be a string.
	 * Otherwise the `select` directive cannot match them correctly.
	 *
	 * To bind the model to a non-string value, you can use one of the following strategies:
	 * - the {@link ng.ngOptions `ngOptions`} directive
	 *   ({@link ng.select#using-select-with-ngoptions-and-setting-a-default-value})
	 * - the {@link ng.ngValue `ngValue`} directive, which allows arbitrary expressions to be
	 *   option values ({@link ng.select#using-ngvalue-to-bind-the-model-to-an-array-of-objects Example})
	 * - model $parsers / $formatters to convert the string value
	 *   ({@link ng.select#binding-select-to-a-non-string-value-via-ngmodel-parsing-formatting Example})
	 *
	 * If the viewValue of `ngModel` does not match any of the options, then the control
	 * will automatically add an "unknown" option, which it then removes when the mismatch is resolved.
	 *
	 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
	 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
	 * option. See example below for demonstration.
	 *
	 * ## Choosing between `ngRepeat` and `ngOptions`
	 *
	 * In many cases, `ngRepeat` can be used on `<option>` elements instead of {@link ng.directive:ngOptions
	 * ngOptions} to achieve a similar result. However, `ngOptions` provides some benefits:
	 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
	 * comprehension expression
	 * - reduced memory consumption by not creating a new scope for each repeated instance
	 * - increased render speed by creating the options in a documentFragment instead of individually
	 *
	 * Specifically, select with repeated options slows down significantly starting at 2000 options in
	 * Chrome and Internet Explorer / Edge.
	 *
	 *
	 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
	 * @param {string=} name Property name of the form under which the control is published.
	 * @param {string=} multiple Allows multiple options to be selected. The selected values will be
	 *     bound to the model as an array.
	 * @param {string=} required Sets `required` validation error key if the value is not entered.
	 * @param {string=} ngRequired Adds required attribute and required validation constraint to
	 * the element when the ngRequired expression evaluates to true. Use ngRequired instead of required
	 * when you want to data-bind to the required attribute.
	 * @param {string=} ngChange AngularJS expression to be executed when selected option(s) changes due to user
	 *    interaction with the select element.
	 * @param {string=} ngOptions sets the options that the select is populated with and defines what is
	 * set on the model on selection. See {@link ngOptions `ngOptions`}.
	 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
	 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
	 *
	 *
	 * @example
	 * ### Simple `select` elements with static options
	 *
	 * <example name="static-select" module="staticSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="singleSelect"> Single select: </label><br>
	 *     <select name="singleSelect" ng-model="data.singleSelect">
	 *       <option value="option-1">Option 1</option>
	 *       <option value="option-2">Option 2</option>
	 *     </select><br>
	 *
	 *     <label for="singleSelect"> Single select with "not selected" option and dynamic option values: </label><br>
	 *     <select name="singleSelect" id="singleSelect" ng-model="data.singleSelect">
	 *       <option value="">---Please select---</option> <!-- not selected / blank option -->
	 *       <option value="{{data.option1}}">Option 1</option> <!-- interpolation -->
	 *       <option value="option-2">Option 2</option>
	 *     </select><br>
	 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
	 *     <tt>singleSelect = {{data.singleSelect}}</tt>
	 *
	 *     <hr>
	 *     <label for="multipleSelect"> Multiple select: </label><br>
	 *     <select name="multipleSelect" id="multipleSelect" ng-model="data.multipleSelect" multiple>
	 *       <option value="option-1">Option 1</option>
	 *       <option value="option-2">Option 2</option>
	 *       <option value="option-3">Option 3</option>
	 *     </select><br>
	 *     <tt>multipleSelect = {{data.multipleSelect}}</tt><br/>
	 *   </form>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('staticSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.data = {
	 *       singleSelect: null,
	 *       multipleSelect: [],
	 *       option1: 'option-1'
	 *      };
	 *
	 *      $scope.forceUnknownOption = function() {
	 *        $scope.data.singleSelect = 'nonsense';
	 *      };
	 *   }]);
	 * </file>
	 *</example>
	 *
	 * @example
	 * ### Using `ngRepeat` to generate `select` options
	 * <example name="select-ngrepeat" module="ngrepeatSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="repeatSelect"> Repeat select: </label>
	 *     <select name="repeatSelect" id="repeatSelect" ng-model="data.model">
	 *       <option ng-repeat="option in data.availableOptions" value="{{option.id}}">{{option.name}}</option>
	 *     </select>
	 *   </form>
	 *   <hr>
	 *   <tt>model = {{data.model}}</tt><br/>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('ngrepeatSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.data = {
	 *       model: null,
	 *       availableOptions: [
	 *         {id: '1', name: 'Option A'},
	 *         {id: '2', name: 'Option B'},
	 *         {id: '3', name: 'Option C'}
	 *       ]
	 *      };
	 *   }]);
	 * </file>
	 *</example>
	 *
	 * @example
	 * ### Using `ngValue` to bind the model to an array of objects
	 * <example name="select-ngvalue" module="ngvalueSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="ngvalueselect"> ngvalue select: </label>
	 *     <select size="6" name="ngvalueselect" ng-model="data.model" multiple>
	 *       <option ng-repeat="option in data.availableOptions" ng-value="option.value">{{option.name}}</option>
	 *     </select>
	 *   </form>
	 *   <hr>
	 *   <pre>model = {{data.model | json}}</pre><br/>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('ngvalueSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.data = {
	 *       model: null,
	 *       availableOptions: [
			   {value: 'myString', name: 'string'},
			   {value: 1, name: 'integer'},
			   {value: true, name: 'boolean'},
			   {value: null, name: 'null'},
			   {value: {prop: 'value'}, name: 'object'},
			   {value: ['a'], name: 'array'}
	 *       ]
	 *      };
	 *   }]);
	 * </file>
	 *</example>
	 *
	 * @example
	 * ### Using `select` with `ngOptions` and setting a default value
	 * See the {@link ngOptions ngOptions documentation} for more `ngOptions` usage examples.
	 *
	 * <example name="select-with-default-values" module="defaultValueSelect">
	 * <file name="index.html">
	 * <div ng-controller="ExampleController">
	 *   <form name="myForm">
	 *     <label for="mySelect">Make a choice:</label>
	 *     <select name="mySelect" id="mySelect"
	 *       ng-options="option.name for option in data.availableOptions track by option.id"
	 *       ng-model="data.selectedOption"></select>
	 *   </form>
	 *   <hr>
	 *   <tt>option = {{data.selectedOption}}</tt><br/>
	 * </div>
	 * </file>
	 * <file name="app.js">
	 *  angular.module('defaultValueSelect', [])
	 *    .controller('ExampleController', ['$scope', function($scope) {
	 *      $scope.data = {
	 *       availableOptions: [
	 *         {id: '1', name: 'Option A'},
	 *         {id: '2', name: 'Option B'},
	 *         {id: '3', name: 'Option C'}
	 *       ],
	 *       selectedOption: {id: '3', name: 'Option C'} //This sets the default value of the select in the ui
	 *       };
	 *   }]);
	 * </file>
	 *</example>
	 *
	 * @example
	 * ### Binding `select` to a non-string value via `ngModel` parsing / formatting
	 *
	 * <example name="select-with-non-string-options" module="nonStringSelect">
	 *   <file name="index.html">
	 *     <select ng-model="model.id" convert-to-number>
	 *       <option value="0">Zero</option>
	 *       <option value="1">One</option>
	 *       <option value="2">Two</option>
	 *     </select>
	 *     {{ model }}
	 *   </file>
	 *   <file name="app.js">
	 *     angular.module('nonStringSelect', [])
	 *       .run(function($rootScope) {
	 *         $rootScope.model = { id: 2 };
	 *       })
	 *       .directive('convertToNumber', function() {
	 *         return {
	 *           require: 'ngModel',
	 *           link: function(scope, element, attrs, ngModel) {
	 *             ngModel.$parsers.push(function(val) {
	 *               return parseInt(val, 10);
	 *             });
	 *             ngModel.$formatters.push(function(val) {
	 *               return '' + val;
	 *             });
	 *           }
	 *         };
	 *       });
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
	 *     it('should initialize to model', function() {
	 *       expect(element(by.model('model.id')).$('option:checked').getText()).toEqual('Two');
	 *     });
	 *   </file>
	 * </example>
	 *
	 */
	var selectDirective = function () {

		return {
			restrict: 'E',
			require: ['select', '?ngModel'],
			controller: SelectController,
			priority: 1,
			link: {
				pre: selectPreLink,
				post: selectPostLink
			}
		};

		function selectPreLink(scope, element, attr, ctrls) {

			var selectCtrl = ctrls[0];
			var ngModelCtrl = ctrls[1];

			// if ngModel is not defined, we don't need to do anything but set the registerOption
			// function to noop, so options don't get added internally
			if (!ngModelCtrl) {
				selectCtrl.registerOption = noop;
				return;
			}


			selectCtrl.ngModelCtrl = ngModelCtrl;

			// When the selected item(s) changes we delegate getting the value of the select control
			// to the `readValue` method, which can be changed if the select can have multiple
			// selected values or if the options are being generated by `ngOptions`
			element.on('change', function () {
				selectCtrl.removeUnknownOption();
				scope.$apply(function () {
					ngModelCtrl.$setViewValue(selectCtrl.readValue());
				});
			});

			// If the select allows multiple values then we need to modify how we read and write
			// values from and to the control; also what it means for the value to be empty and
			// we have to add an extra watch since ngModel doesn't work well with arrays - it
			// doesn't trigger rendering if only an item in the array changes.
			if (attr.multiple) {
				selectCtrl.multiple = true;

				// Read value now needs to check each option to see if it is selected
				selectCtrl.readValue = function readMultipleValue() {
					var array = [];
					forEach(element.find('option'), function (option) {
						if (option.selected && !option.disabled) {
							var val = option.value;
							array.push(val in selectCtrl.selectValueMap ? selectCtrl.selectValueMap[val] : val);
						}
					});
					return array;
				};

				// Write value now needs to set the selected property of each matching option
				selectCtrl.writeValue = function writeMultipleValue(value) {
					forEach(element.find('option'), function (option) {
						var shouldBeSelected = !!value && (includes(value, option.value) ||
							includes(value, selectCtrl.selectValueMap[option.value]));
						var currentlySelected = option.selected;

						// Support: IE 9-11 only, Edge 12-15+
						// In IE and Edge adding options to the selection via shift+click/UP/DOWN
						// will de-select already selected options if "selected" on those options was set
						// more than once (i.e. when the options were already selected)
						// So we only modify the selected property if necessary.
						// Note: this behavior cannot be replicated via unit tests because it only shows in the
						// actual user interface.
						if (shouldBeSelected !== currentlySelected) {
							setOptionSelectedStatus(jqLite(option), shouldBeSelected);
						}

					});
				};

				// we have to do it on each watch since ngModel watches reference, but
				// we need to work of an array, so we need to see if anything was inserted/removed
				var lastView, lastViewRef = NaN;
				scope.$watch(function selectMultipleWatch() {
					if (lastViewRef === ngModelCtrl.$viewValue && !equals(lastView, ngModelCtrl.$viewValue)) {
						lastView = shallowCopy(ngModelCtrl.$viewValue);
						ngModelCtrl.$render();
					}
					lastViewRef = ngModelCtrl.$viewValue;
				});

				// If we are a multiple select then value is now a collection
				// so the meaning of $isEmpty changes
				ngModelCtrl.$isEmpty = function (value) {
					return !value || value.length === 0;
				};

			}
		}

		function selectPostLink(scope, element, attrs, ctrls) {
			// if ngModel is not defined, we don't need to do anything
			var ngModelCtrl = ctrls[1];
			if (!ngModelCtrl) return;

			var selectCtrl = ctrls[0];

			// We delegate rendering to the `writeValue` method, which can be changed
			// if the select can have multiple selected values or if the options are being
			// generated by `ngOptions`.
			// This must be done in the postLink fn to prevent $render to be called before
			// all nodes have been linked correctly.
			ngModelCtrl.$render = function () {
				selectCtrl.writeValue(ngModelCtrl.$viewValue);
			};
		}
	};


	// The option directive is purely designed to communicate the existence (or lack of)
	// of dynamically created (and destroyed) option elements to their containing select
	// directive via its controller.
	var optionDirective = ['$interpolate', function ($interpolate) {
		return {
			restrict: 'E',
			priority: 100,
			compile: function (element, attr) {
				var interpolateValueFn, interpolateTextFn;

				if (isDefined(attr.ngValue)) {
					// Will be handled by registerOption
				} else if (isDefined(attr.value)) {
					// If the value attribute is defined, check if it contains an interpolation
					interpolateValueFn = $interpolate(attr.value, true);
				} else {
					// If the value attribute is not defined then we fall back to the
					// text content of the option element, which may be interpolated
					interpolateTextFn = $interpolate(element.text(), true);
					if (!interpolateTextFn) {
						attr.$set('value', element.text());
					}
				}

				return function (scope, element, attr) {
					// This is an optimization over using ^^ since we don't want to have to search
					// all the way to the root of the DOM for every single option element
					var selectCtrlName = '$selectController',
						parent = element.parent(),
						selectCtrl = parent.data(selectCtrlName) ||
							parent.parent().data(selectCtrlName); // in case we are in optgroup

					if (selectCtrl) {
						selectCtrl.registerOption(scope, element, attr, interpolateValueFn, interpolateTextFn);
					}
				};
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngRequired
	 * @restrict A
	 *
	 * @param {expression} ngRequired AngularJS expression. If it evaluates to `true`, it sets the
	 *                                `required` attribute to the element and adds the `required`
	 *                                {@link ngModel.NgModelController#$validators `validator`}.
	 *
	 * @description
	 *
	 * ngRequired adds the required {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
	 * It is most often used for {@link input `input`} and {@link select `select`} controls, but can also be
	 * applied to custom controls.
	 *
	 * The directive sets the `required` attribute on the element if the AngularJS expression inside
	 * `ngRequired` evaluates to true. A special directive for setting `required` is necessary because we
	 * cannot use interpolation inside `required`. See the {@link guide/interpolation interpolation guide}
	 * for more info.
	 *
	 * The validator will set the `required` error key to true if the `required` attribute is set and
	 * calling {@link ngModel.NgModelController#$isEmpty `NgModelController.$isEmpty`} with the
	 * {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`} returns `true`. For example, the
	 * `$isEmpty()` implementation for `input[text]` checks the length of the `$viewValue`. When developing
	 * custom controls, `$isEmpty()` can be overwritten to account for a $viewValue that is not string-based.
	 *
	 * @example
	 * <example name="ngRequiredDirective" module="ngRequiredExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('ngRequiredExample', [])
	 *         .controller('ExampleController', ['$scope', function($scope) {
	 *           $scope.required = true;
	 *         }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <form name="form">
	 *         <label for="required">Toggle required: </label>
	 *         <input type="checkbox" ng-model="required" id="required" />
	 *         <br>
	 *         <label for="input">This input must be filled if `required` is true: </label>
	 *         <input type="text" ng-model="model" id="input" name="input" ng-required="required" /><br>
	 *         <hr>
	 *         required error set? = <code>{{form.input.$error.required}}</code><br>
	 *         model = <code>{{model}}</code>
	 *       </form>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
		   var required = element(by.binding('form.input.$error.required'));
		   var model = element(by.binding('model'));
		   var input = element(by.id('input'));
	
		   it('should set the required error', function() {
			 expect(required.getText()).toContain('true');
	
			 input.sendKeys('123');
			 expect(required.getText()).not.toContain('true');
			 expect(model.getText()).toContain('123');
		   });
	 *   </file>
	 * </example>
	 */
	var requiredDirective = ['$parse', function ($parse) {
		return {
			restrict: 'A',
			require: '?ngModel',
			link: function (scope, elm, attr, ctrl) {
				if (!ctrl) return;
				// For boolean attributes like required, presence means true
				var value = attr.hasOwnProperty('required') || $parse(attr.ngRequired)(scope);

				if (!attr.ngRequired) {
					// force truthy in case we are on non input element
					// (input elements do this automatically for boolean attributes like required)
					attr.required = true;
				}

				ctrl.$validators.required = function (modelValue, viewValue) {
					return !value || !ctrl.$isEmpty(viewValue);
				};

				attr.$observe('required', function (newVal) {

					if (value !== newVal) {
						value = newVal;
						ctrl.$validate();
					}
				});
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngPattern
	 * @restrict A
	 *
	 * @param {expression|RegExp} ngPattern AngularJS expression that must evaluate to a `RegExp` or a `String`
	 *                                      parsable into a `RegExp`, or a `RegExp` literal. See above for
	 *                                      more details.
	 *
	 * @description
	 *
	 * ngPattern adds the pattern {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
	 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
	 *
	 * The validator sets the `pattern` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
	 * does not match a RegExp which is obtained from the `ngPattern` attribute value:
	 * - the value is an AngularJS expression:
	 *   - If the expression evaluates to a RegExp object, then this is used directly.
	 *   - If the expression evaluates to a string, then it will be converted to a RegExp after wrapping it
	 *     in `^` and `$` characters. For instance, `"abc"` will be converted to `new RegExp('^abc$')`.
	 * - If the value is a RegExp literal, e.g. `ngPattern="/^\d+$/"`, it is used directly.
	 *
	 * <div class="alert alert-info">
	 * **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
	 * start at the index of the last search's match, thus not taking the whole input value into
	 * account.
	 * </div>
	 *
	 * <div class="alert alert-info">
	 * **Note:** This directive is also added when the plain `pattern` attribute is used, with two
	 * differences:
	 * <ol>
	 *   <li>
	 *     `ngPattern` does not set the `pattern` attribute and therefore HTML5 constraint validation is
	 *     not available.
	 *   </li>
	 *   <li>
	 *     The `ngPattern` attribute must be an expression, while the `pattern` value must be
	 *     interpolated.
	 *   </li>
	 * </ol>
	 * </div>
	 *
	 * @example
	 * <example name="ngPatternDirective" module="ngPatternExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('ngPatternExample', [])
	 *         .controller('ExampleController', ['$scope', function($scope) {
	 *           $scope.regex = '\\d+';
	 *         }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <form name="form">
	 *         <label for="regex">Set a pattern (regex string): </label>
	 *         <input type="text" ng-model="regex" id="regex" />
	 *         <br>
	 *         <label for="input">This input is restricted by the current pattern: </label>
	 *         <input type="text" ng-model="model" id="input" name="input" ng-pattern="regex" /><br>
	 *         <hr>
	 *         input valid? = <code>{{form.input.$valid}}</code><br>
	 *         model = <code>{{model}}</code>
	 *       </form>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
		   var model = element(by.binding('model'));
		   var input = element(by.id('input'));
	
		   it('should validate the input with the default pattern', function() {
			 input.sendKeys('aaa');
			 expect(model.getText()).not.toContain('aaa');
	
			 input.clear().then(function() {
			   input.sendKeys('123');
			   expect(model.getText()).toContain('123');
			 });
		   });
	 *   </file>
	 * </example>
	 */
	var patternDirective = ['$parse', function ($parse) {
		return {
			restrict: 'A',
			require: '?ngModel',
			compile: function (tElm, tAttr) {
				var patternExp;
				var parseFn;

				if (tAttr.ngPattern) {
					patternExp = tAttr.ngPattern;

					// ngPattern might be a scope expression, or an inlined regex, which is not parsable.
					// We get value of the attribute here, so we can compare the old and the new value
					// in the observer to avoid unnecessary validations
					if (tAttr.ngPattern.charAt(0) === '/' && REGEX_STRING_REGEXP.test(tAttr.ngPattern)) {
						parseFn = function () { return tAttr.ngPattern; };
					} else {
						parseFn = $parse(tAttr.ngPattern);
					}
				}

				return function (scope, elm, attr, ctrl) {
					if (!ctrl) return;

					var attrVal = attr.pattern;

					if (attr.ngPattern) {
						attrVal = parseFn(scope);
					} else {
						patternExp = attr.pattern;
					}

					var regexp = parsePatternAttr(attrVal, patternExp, elm);

					attr.$observe('pattern', function (newVal) {
						var oldRegexp = regexp;

						regexp = parsePatternAttr(newVal, patternExp, elm);

						if ((oldRegexp && oldRegexp.toString()) !== (regexp && regexp.toString())) {
							ctrl.$validate();
						}
					});

					ctrl.$validators.pattern = function (modelValue, viewValue) {
						// HTML5 pattern constraint validates the input value, so we validate the viewValue
						return ctrl.$isEmpty(viewValue) || isUndefined(regexp) || regexp.test(viewValue);
					};
				};
			}

		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngMaxlength
	 * @restrict A
	 *
	 * @param {expression} ngMaxlength AngularJS expression that must evaluate to a `Number` or `String`
	 *                                 parsable into a `Number`. Used as value for the `maxlength`
	 *                                 {@link ngModel.NgModelController#$validators validator}.
	 *
	 * @description
	 *
	 * ngMaxlength adds the maxlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
	 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
	 *
	 * The validator sets the `maxlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
	 * is longer than the integer obtained by evaluating the AngularJS expression given in the
	 * `ngMaxlength` attribute value.
	 *
	 * <div class="alert alert-info">
	 * **Note:** This directive is also added when the plain `maxlength` attribute is used, with two
	 * differences:
	 * <ol>
	 *   <li>
	 *     `ngMaxlength` does not set the `maxlength` attribute and therefore HTML5 constraint
	 *     validation is not available.
	 *   </li>
	 *   <li>
	 *     The `ngMaxlength` attribute must be an expression, while the `maxlength` value must be
	 *     interpolated.
	 *   </li>
	 * </ol>
	 * </div>
	 *
	 * @example
	 * <example name="ngMaxlengthDirective" module="ngMaxlengthExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('ngMaxlengthExample', [])
	 *         .controller('ExampleController', ['$scope', function($scope) {
	 *           $scope.maxlength = 5;
	 *         }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <form name="form">
	 *         <label for="maxlength">Set a maxlength: </label>
	 *         <input type="number" ng-model="maxlength" id="maxlength" />
	 *         <br>
	 *         <label for="input">This input is restricted by the current maxlength: </label>
	 *         <input type="text" ng-model="model" id="input" name="input" ng-maxlength="maxlength" /><br>
	 *         <hr>
	 *         input valid? = <code>{{form.input.$valid}}</code><br>
	 *         model = <code>{{model}}</code>
	 *       </form>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
		   var model = element(by.binding('model'));
		   var input = element(by.id('input'));
	
		   it('should validate the input with the default maxlength', function() {
			 input.sendKeys('abcdef');
			 expect(model.getText()).not.toContain('abcdef');
	
			 input.clear().then(function() {
			   input.sendKeys('abcde');
			   expect(model.getText()).toContain('abcde');
			 });
		   });
	 *   </file>
	 * </example>
	 */
	var maxlengthDirective = ['$parse', function ($parse) {
		return {
			restrict: 'A',
			require: '?ngModel',
			link: function (scope, elm, attr, ctrl) {
				if (!ctrl) return;

				var maxlength = attr.maxlength || $parse(attr.ngMaxlength)(scope);
				var maxlengthParsed = parseLength(maxlength);

				attr.$observe('maxlength', function (value) {
					if (maxlength !== value) {
						maxlengthParsed = parseLength(value);
						maxlength = value;
						ctrl.$validate();
					}
				});
				ctrl.$validators.maxlength = function (modelValue, viewValue) {
					return (maxlengthParsed < 0) || ctrl.$isEmpty(viewValue) || (viewValue.length <= maxlengthParsed);
				};
			}
		};
	}];

	/**
	 * @ngdoc directive
	 * @name ngMinlength
	 * @restrict A
	 *
	 * @param {expression} ngMinlength AngularJS expression that must evaluate to a `Number` or `String`
	 *                                 parsable into a `Number`. Used as value for the `minlength`
	 *                                 {@link ngModel.NgModelController#$validators validator}.
	 *
	 * @description
	 *
	 * ngMinlength adds the minlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
	 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
	 *
	 * The validator sets the `minlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
	 * is shorter than the integer obtained by evaluating the AngularJS expression given in the
	 * `ngMinlength` attribute value.
	 *
	 * <div class="alert alert-info">
	 * **Note:** This directive is also added when the plain `minlength` attribute is used, with two
	 * differences:
	 * <ol>
	 *   <li>
	 *     `ngMinlength` does not set the `minlength` attribute and therefore HTML5 constraint
	 *     validation is not available.
	 *   </li>
	 *   <li>
	 *     The `ngMinlength` value must be an expression, while the `minlength` value must be
	 *     interpolated.
	 *   </li>
	 * </ol>
	 * </div>
	 *
	 * @example
	 * <example name="ngMinlengthDirective" module="ngMinlengthExample">
	 *   <file name="index.html">
	 *     <script>
	 *       angular.module('ngMinlengthExample', [])
	 *         .controller('ExampleController', ['$scope', function($scope) {
	 *           $scope.minlength = 3;
	 *         }]);
	 *     </script>
	 *     <div ng-controller="ExampleController">
	 *       <form name="form">
	 *         <label for="minlength">Set a minlength: </label>
	 *         <input type="number" ng-model="minlength" id="minlength" />
	 *         <br>
	 *         <label for="input">This input is restricted by the current minlength: </label>
	 *         <input type="text" ng-model="model" id="input" name="input" ng-minlength="minlength" /><br>
	 *         <hr>
	 *         input valid? = <code>{{form.input.$valid}}</code><br>
	 *         model = <code>{{model}}</code>
	 *       </form>
	 *     </div>
	 *   </file>
	 *   <file name="protractor.js" type="protractor">
		   var model = element(by.binding('model'));
		   var input = element(by.id('input'));
	
		   it('should validate the input with the default minlength', function() {
			 input.sendKeys('ab');
			 expect(model.getText()).not.toContain('ab');
	
			 input.sendKeys('abc');
			 expect(model.getText()).toContain('abc');
		   });
	 *   </file>
	 * </example>
	 */
	var minlengthDirective = ['$parse', function ($parse) {
		return {
			restrict: 'A',
			require: '?ngModel',
			link: function (scope, elm, attr, ctrl) {
				if (!ctrl) return;

				var minlength = attr.minlength || $parse(attr.ngMinlength)(scope);
				var minlengthParsed = parseLength(minlength) || -1;

				attr.$observe('minlength', function (value) {
					if (minlength !== value) {
						minlengthParsed = parseLength(value) || -1;
						minlength = value;
						ctrl.$validate();
					}

				});
				ctrl.$validators.minlength = function (modelValue, viewValue) {
					return ctrl.$isEmpty(viewValue) || viewValue.length >= minlengthParsed;
				};
			}
		};
	}];


	function parsePatternAttr(regex, patternExp, elm) {
		if (!regex) return undefined;

		if (isString(regex)) {
			regex = new RegExp('^' + regex + '$');
		}

		if (!regex.test) {
			throw minErr('ngPattern')('noregexp',
				'Expected {0} to be a RegExp but was {1}. Element: {2}', patternExp,
				regex, startingTag(elm));
		}

		return regex;
	}

	function parseLength(val) {
		var intVal = toInt(val);
		return isNumberNaN(intVal) ? -1 : intVal;
	}

	if (window.angular.bootstrap) {
		// AngularJS is already loaded, so we can return here...
		if (window.console) {
			console.log('WARNING: Tried to load AngularJS more than once.');
		}
		return;
	}

	// try to bind to jquery now so that one can write jqLite(fn)
	// but we will rebind on bootstrap again.
	bindJQuery();

	publishExternalAPI(angular);

	angular.module("ngLocale", [], ["$provide", function ($provide) {
		var PLURAL_CATEGORY = { ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other" };
		function getDecimals(n) {
			n = n + '';
			var i = n.indexOf('.');
			return (i == -1) ? 0 : n.length - i - 1;
		}

		function getVF(n, opt_precision) {
			var v = opt_precision;

			if (undefined === v) {
				v = Math.min(getDecimals(n), 3);
			}

			var base = Math.pow(10, v);
			var f = ((n * base) | 0) % base;
			return { v: v, f: f };
		}

		$provide.value("$locale", {
			"DATETIME_FORMATS": {
				"AMPMS": [
					"AM",
					"PM"
				],
				"DAY": [
					"Sunday",
					"Monday",
					"Tuesday",
					"Wednesday",
					"Thursday",
					"Friday",
					"Saturday"
				],
				"ERANAMES": [
					"Before Christ",
					"Anno Domini"
				],
				"ERAS": [
					"BC",
					"AD"
				],
				"FIRSTDAYOFWEEK": 6,
				"MONTH": [
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December"
				],
				"SHORTDAY": [
					"Sun",
					"Mon",
					"Tue",
					"Wed",
					"Thu",
					"Fri",
					"Sat"
				],
				"SHORTMONTH": [
					"Jan",
					"Feb",
					"Mar",
					"Apr",
					"May",
					"Jun",
					"Jul",
					"Aug",
					"Sep",
					"Oct",
					"Nov",
					"Dec"
				],
				"STANDALONEMONTH": [
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December"
				],
				"WEEKENDRANGE": [
					5,
					6
				],
				"fullDate": "EEEE, MMMM d, y",
				"longDate": "MMMM d, y",
				"medium": "MMM d, y h:mm:ss a",
				"mediumDate": "MMM d, y",
				"mediumTime": "h:mm:ss a",
				"short": "M/d/yy h:mm a",
				"shortDate": "M/d/yy",
				"shortTime": "h:mm a"
			},
			"NUMBER_FORMATS": {
				"CURRENCY_SYM": "$",
				"DECIMAL_SEP": ".",
				"GROUP_SEP": ",",
				"PATTERNS": [
					{
						"gSize": 3,
						"lgSize": 3,
						"maxFrac": 3,
						"minFrac": 0,
						"minInt": 1,
						"negPre": "-",
						"negSuf": "",
						"posPre": "",
						"posSuf": ""
					},
					{
						"gSize": 3,
						"lgSize": 3,
						"maxFrac": 2,
						"minFrac": 2,
						"minInt": 1,
						"negPre": "-\u00a4",
						"negSuf": "",
						"posPre": "\u00a4",
						"posSuf": ""
					}
				]
			},
			"id": "en-us",
			"localeID": "en_US",
			"pluralCat": function (n, opt_precision) { var i = n | 0; var vf = getVF(n, opt_precision); if (i == 1 && vf.v == 0) { return PLURAL_CATEGORY.ONE; } return PLURAL_CATEGORY.OTHER; }
		});
	}]);

	jqLite(function () {
		angularInit(window.document, bootstrap);
	});

})(window);

!window.angular.$$csp().noInlineStyle && window.angular.element(document.head).prepend(window.angular.element('<style>').text('@charset "UTF-8";[ng\\:cloak],[ng-cloak],[data-ng-cloak],[x-ng-cloak],.ng-cloak,.x-ng-cloak,.ng-hide:not(.ng-hide-animate){display:none !important;}ng\\:form{display:block;}.ng-animate-shim{visibility:hidden;}.ng-anchor{position:absolute;}'));
/**
 ********************************
 **            POPPER          **
 ********************************
 */
/**!
 * @fileOverview Kickass library to create and place poppers near their reference elements.
 * @license
 * Copyright (c) 2016 Federico Zivolo and contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
		typeof define === 'function' && define.amd ? define(factory) :
			(global.Popper = factory());
}(this, (function () {
	'use strict';

	var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
	var longerTimeoutBrowsers = ['Edge', 'Trident', 'Firefox'];
	var timeoutDuration = 0;
	for (var i = 0; i < longerTimeoutBrowsers.length; i += 1) {
		if (isBrowser && navigator.userAgent.indexOf(longerTimeoutBrowsers[i]) >= 0) {
			timeoutDuration = 1;
			break;
		}
	}

	function microtaskDebounce(fn) {
		var called = false;
		return function () {
			if (called) {
				return;
			}
			called = true;
			window.Promise.resolve().then(function () {
				called = false;
				fn();
			});
		};
	}

	function taskDebounce(fn) {
		var scheduled = false;
		return function () {
			if (!scheduled) {
				scheduled = true;
				setTimeout(function () {
					scheduled = false;
					fn();
				}, timeoutDuration);
			}
		};
	}

	var supportsMicroTasks = isBrowser && window.Promise;

	/**
	* Create a debounced version of a method, that's asynchronously deferred
	* but called in the minimum time possible.
	*
	* @method
	* @memberof Popper.Utils
	* @argument {Function} fn
	* @returns {Function}
	*/
	var debounce = supportsMicroTasks ? microtaskDebounce : taskDebounce;

	/**
	 * Check if the given variable is a function
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Any} functionToCheck - variable to check
	 * @returns {Boolean} answer to: is a function?
	 */
	function isFunction(functionToCheck) {
		var getType = {};
		return functionToCheck && getType.toString.call(functionToCheck) === '[object Function]';
	}

	/**
	 * Get CSS computed property of the given element
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Eement} element
	 * @argument {String} property
	 */
	function getStyleComputedProperty(element, property) {
		if (element.nodeType !== 1) {
			return [];
		}
		// NOTE: 1 DOM access here
		var css = getComputedStyle(element, null);
		return property ? css[property] : css;
	}

	/**
	 * Returns the parentNode or the host of the element
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @returns {Element} parent
	 */
	function getParentNode(element) {
		if (element.nodeName === 'HTML') {
			return element;
		}
		return element.parentNode || element.host;
	}

	/**
	 * Returns the scrolling parent of the given element
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @returns {Element} scroll parent
	 */
	function getScrollParent(element) {
		// Return body, `getScroll` will take care to get the correct `scrollTop` from it
		if (!element) {
			return document.body;
		}

		switch (element.nodeName) {
			case 'HTML':
			case 'BODY':
				return element.ownerDocument.body;
			case '#document':
				return element.body;
		}

		// Firefox want us to check `-x` and `-y` variations as well

		var _getStyleComputedProp = getStyleComputedProperty(element),
			overflow = _getStyleComputedProp.overflow,
			overflowX = _getStyleComputedProp.overflowX,
			overflowY = _getStyleComputedProp.overflowY;

		if (/(auto|scroll)/.test(overflow + overflowY + overflowX)) {
			return element;
		}

		return getScrollParent(getParentNode(element));
	}

	/**
	 * Returns the offset parent of the given element
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @returns {Element} offset parent
	 */
	function getOffsetParent(element) {
		// NOTE: 1 DOM access here
		var offsetParent = element && element.offsetParent;
		var nodeName = offsetParent && offsetParent.nodeName;

		if (!nodeName || nodeName === 'BODY' || nodeName === 'HTML') {
			if (element) {
				return element.ownerDocument.documentElement;
			}

			return document.documentElement;
		}

		// .offsetParent will return the closest TD or TABLE in case
		// no offsetParent is present, I hate this job...
		if (['TD', 'TABLE'].indexOf(offsetParent.nodeName) !== -1 && getStyleComputedProperty(offsetParent, 'position') === 'static') {
			return getOffsetParent(offsetParent);
		}

		return offsetParent;
	}

	function isOffsetContainer(element) {
		var nodeName = element.nodeName;

		if (nodeName === 'BODY') {
			return false;
		}
		return nodeName === 'HTML' || getOffsetParent(element.firstElementChild) === element;
	}

	/**
	 * Finds the root node (document, shadowDOM root) of the given element
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} node
	 * @returns {Element} root node
	 */
	function getRoot(node) {
		if (node.parentNode !== null) {
			return getRoot(node.parentNode);
		}

		return node;
	}

	/**
	 * Finds the offset parent common to the two provided nodes
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element1
	 * @argument {Element} element2
	 * @returns {Element} common offset parent
	 */
	function findCommonOffsetParent(element1, element2) {
		// This check is needed to avoid errors in case one of the elements isn't defined for any reason
		if (!element1 || !element1.nodeType || !element2 || !element2.nodeType) {
			return document.documentElement;
		}

		// Here we make sure to give as "start" the element that comes first in the DOM
		var order = element1.compareDocumentPosition(element2) & Node.DOCUMENT_POSITION_FOLLOWING;
		var start = order ? element1 : element2;
		var end = order ? element2 : element1;

		// Get common ancestor container
		var range = document.createRange();
		range.setStart(start, 0);
		range.setEnd(end, 0);
		var commonAncestorContainer = range.commonAncestorContainer;

		// Both nodes are inside #document

		if (element1 !== commonAncestorContainer && element2 !== commonAncestorContainer || start.contains(end)) {
			if (isOffsetContainer(commonAncestorContainer)) {
				return commonAncestorContainer;
			}

			return getOffsetParent(commonAncestorContainer);
		}

		// one of the nodes is inside shadowDOM, find which one
		var element1root = getRoot(element1);
		if (element1root.host) {
			return findCommonOffsetParent(element1root.host, element2);
		} else {
			return findCommonOffsetParent(element1, getRoot(element2).host);
		}
	}

	/**
	 * Gets the scroll value of the given element in the given side (top and left)
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @argument {String} side `top` or `left`
	 * @returns {number} amount of scrolled pixels
	 */
	function getScroll(element) {
		var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'top';

		var upperSide = side === 'top' ? 'scrollTop' : 'scrollLeft';
		var nodeName = element.nodeName;

		if (nodeName === 'BODY' || nodeName === 'HTML') {
			var html = element.ownerDocument.documentElement;
			var scrollingElement = element.ownerDocument.scrollingElement || html;
			return scrollingElement[upperSide];
		}

		return element[upperSide];
	}

	/*
	 * Sum or subtract the element scroll values (left and top) from a given rect object
	 * @method
	 * @memberof Popper.Utils
	 * @param {Object} rect - Rect object you want to change
	 * @param {HTMLElement} element - The element from the function reads the scroll values
	 * @param {Boolean} subtract - set to true if you want to subtract the scroll values
	 * @return {Object} rect - The modifier rect object
	 */
	function includeScroll(rect, element) {
		var subtract = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

		var scrollTop = getScroll(element, 'top');
		var scrollLeft = getScroll(element, 'left');
		var modifier = subtract ? -1 : 1;
		rect.top += scrollTop * modifier;
		rect.bottom += scrollTop * modifier;
		rect.left += scrollLeft * modifier;
		rect.right += scrollLeft * modifier;
		return rect;
	}

	/*
	 * Helper to detect borders of a given element
	 * @method
	 * @memberof Popper.Utils
	 * @param {CSSStyleDeclaration} styles
	 * Result of `getStyleComputedProperty` on the given element
	 * @param {String} axis - `x` or `y`
	 * @return {number} borders - The borders size of the given axis
	 */

	function getBordersSize(styles, axis) {
		var sideA = axis === 'x' ? 'Left' : 'Top';
		var sideB = sideA === 'Left' ? 'Right' : 'Bottom';

		return parseFloat(styles['border' + sideA + 'Width'], 10) + parseFloat(styles['border' + sideB + 'Width'], 10);
	}

	/**
	 * Tells if you are running Internet Explorer 10
	 * @method
	 * @memberof Popper.Utils
	 * @returns {Boolean} isIE10
	 */
	var isIE10 = undefined;

	var isIE10$1 = function () {
		if (isIE10 === undefined) {
			isIE10 = navigator.appVersion.indexOf('MSIE 10') !== -1;
		}
		return isIE10;
	};

	function getSize(axis, body, html, computedStyle) {
		return Math.max(body['offset' + axis], body['scroll' + axis], html['client' + axis], html['offset' + axis], html['scroll' + axis], isIE10$1() ? html['offset' + axis] + computedStyle['margin' + (axis === 'Height' ? 'Top' : 'Left')] + computedStyle['margin' + (axis === 'Height' ? 'Bottom' : 'Right')] : 0);
	}

	function getWindowSizes() {
		var body = document.body;
		var html = document.documentElement;
		var computedStyle = isIE10$1() && getComputedStyle(html);

		return {
			height: getSize('Height', body, html, computedStyle),
			width: getSize('Width', body, html, computedStyle)
		};
	}

	var classCallCheck = function (instance, Constructor) {
		if (!(instance instanceof Constructor)) {
			throw new TypeError("Cannot call a class as a function");
		}
	};

	var createClass = function () {
		function defineProperties(target, props) {
			for (var i = 0; i < props.length; i++) {
				var descriptor = props[i];
				descriptor.enumerable = descriptor.enumerable || false;
				descriptor.configurable = true;
				if ("value" in descriptor) descriptor.writable = true;
				Object.defineProperty(target, descriptor.key, descriptor);
			}
		}

		return function (Constructor, protoProps, staticProps) {
			if (protoProps) defineProperties(Constructor.prototype, protoProps);
			if (staticProps) defineProperties(Constructor, staticProps);
			return Constructor;
		};
	}();





	var defineProperty = function (obj, key, value) {
		if (key in obj) {
			Object.defineProperty(obj, key, {
				value: value,
				enumerable: true,
				configurable: true,
				writable: true
			});
		} else {
			obj[key] = value;
		}

		return obj;
	};

	var _extends = Object.assign || function (target) {
		for (var i = 1; i < arguments.length; i++) {
			var source = arguments[i];

			for (var key in source) {
				if (Object.prototype.hasOwnProperty.call(source, key)) {
					target[key] = source[key];
				}
			}
		}

		return target;
	};

	/**
	 * Given element offsets, generate an output similar to getBoundingClientRect
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Object} offsets
	 * @returns {Object} ClientRect like output
	 */
	function getClientRect(offsets) {
		return _extends({}, offsets, {
			right: offsets.left + offsets.width,
			bottom: offsets.top + offsets.height
		});
	}

	/**
	 * Get bounding client rect of given element
	 * @method
	 * @memberof Popper.Utils
	 * @param {HTMLElement} element
	 * @return {Object} client rect
	 */
	function getBoundingClientRect(element) {
		var rect = {};

		// IE10 10 FIX: Please, don't ask, the element isn't
		// considered in DOM in some circumstances...
		// This isn't reproducible in IE10 compatibility mode of IE11
		if (isIE10$1()) {
			try {
				rect = element.getBoundingClientRect();
				var scrollTop = getScroll(element, 'top');
				var scrollLeft = getScroll(element, 'left');
				rect.top += scrollTop;
				rect.left += scrollLeft;
				rect.bottom += scrollTop;
				rect.right += scrollLeft;
			} catch (err) { }
		} else {
			rect = element.getBoundingClientRect();
		}

		var result = {
			left: rect.left,
			top: rect.top,
			width: rect.right - rect.left,
			height: rect.bottom - rect.top
		};

		// subtract scrollbar size from sizes
		var sizes = element.nodeName === 'HTML' ? getWindowSizes() : {};
		var width = sizes.width || element.clientWidth || result.right - result.left;
		var height = sizes.height || element.clientHeight || result.bottom - result.top;

		var horizScrollbar = element.offsetWidth - width;
		var vertScrollbar = element.offsetHeight - height;

		// if an hypothetical scrollbar is detected, we must be sure it's not a `border`
		// we make this check conditional for performance reasons
		if (horizScrollbar || vertScrollbar) {
			var styles = getStyleComputedProperty(element);
			horizScrollbar -= getBordersSize(styles, 'x');
			vertScrollbar -= getBordersSize(styles, 'y');

			result.width -= horizScrollbar;
			result.height -= vertScrollbar;
		}

		return getClientRect(result);
	}

	function getOffsetRectRelativeToArbitraryNode(children, parent) {
		var isIE10 = isIE10$1();
		var isHTML = parent.nodeName === 'HTML';
		var childrenRect = getBoundingClientRect(children);
		var parentRect = getBoundingClientRect(parent);
		var scrollParent = getScrollParent(children);

		var styles = getStyleComputedProperty(parent);
		var borderTopWidth = parseFloat(styles.borderTopWidth, 10);
		var borderLeftWidth = parseFloat(styles.borderLeftWidth, 10);

		var offsets = getClientRect({
			top: childrenRect.top - parentRect.top - borderTopWidth,
			left: childrenRect.left - parentRect.left - borderLeftWidth,
			width: childrenRect.width,
			height: childrenRect.height
		});
		offsets.marginTop = 0;
		offsets.marginLeft = 0;

		// Subtract margins of documentElement in case it's being used as parent
		// we do this only on HTML because it's the only element that behaves
		// differently when margins are applied to it. The margins are included in
		// the box of the documentElement, in the other cases not.
		if (!isIE10 && isHTML) {
			var marginTop = parseFloat(styles.marginTop, 10);
			var marginLeft = parseFloat(styles.marginLeft, 10);

			offsets.top -= borderTopWidth - marginTop;
			offsets.bottom -= borderTopWidth - marginTop;
			offsets.left -= borderLeftWidth - marginLeft;
			offsets.right -= borderLeftWidth - marginLeft;

			// Attach marginTop and marginLeft because in some circumstances we may need them
			offsets.marginTop = marginTop;
			offsets.marginLeft = marginLeft;
		}

		if (isIE10 ? parent.contains(scrollParent) : parent === scrollParent && scrollParent.nodeName !== 'BODY') {
			offsets = includeScroll(offsets, parent);
		}

		return offsets;
	}

	function getViewportOffsetRectRelativeToArtbitraryNode(element) {
		var html = element.ownerDocument.documentElement;
		var relativeOffset = getOffsetRectRelativeToArbitraryNode(element, html);
		var width = Math.max(html.clientWidth, window.innerWidth || 0);
		var height = Math.max(html.clientHeight, window.innerHeight || 0);

		var scrollTop = getScroll(html);
		var scrollLeft = getScroll(html, 'left');

		var offset = {
			top: scrollTop - relativeOffset.top + relativeOffset.marginTop,
			left: scrollLeft - relativeOffset.left + relativeOffset.marginLeft,
			width: width,
			height: height
		};

		return getClientRect(offset);
	}

	/**
	 * Check if the given element is fixed or is inside a fixed parent
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @argument {Element} customContainer
	 * @returns {Boolean} answer to "isFixed?"
	 */
	function isFixed(element) {
		var nodeName = element.nodeName;
		if (nodeName === 'BODY' || nodeName === 'HTML') {
			return false;
		}
		if (getStyleComputedProperty(element, 'position') === 'fixed') {
			return true;
		}
		return isFixed(getParentNode(element));
	}

	/**
	 * Computed the boundaries limits and return them
	 * @method
	 * @memberof Popper.Utils
	 * @param {HTMLElement} popper
	 * @param {HTMLElement} reference
	 * @param {number} padding
	 * @param {HTMLElement} boundariesElement - Element used to define the boundaries
	 * @returns {Object} Coordinates of the boundaries
	 */
	function getBoundaries(popper, reference, padding, boundariesElement) {
		// NOTE: 1 DOM access here
		var boundaries = { top: 0, left: 0 };
		var offsetParent = findCommonOffsetParent(popper, reference);

		// Handle viewport case
		if (boundariesElement === 'viewport') {
			boundaries = getViewportOffsetRectRelativeToArtbitraryNode(offsetParent);
		} else {
			// Handle other cases based on DOM element used as boundaries
			var boundariesNode = void 0;
			if (boundariesElement === 'scrollParent') {
				boundariesNode = getScrollParent(getParentNode(reference));
				if (boundariesNode.nodeName === 'BODY') {
					boundariesNode = popper.ownerDocument.documentElement;
				}
			} else if (boundariesElement === 'window') {
				boundariesNode = popper.ownerDocument.documentElement;
			} else {
				boundariesNode = boundariesElement;
			}

			var offsets = getOffsetRectRelativeToArbitraryNode(boundariesNode, offsetParent);

			// In case of HTML, we need a different computation
			if (boundariesNode.nodeName === 'HTML' && !isFixed(offsetParent)) {
				var _getWindowSizes = getWindowSizes(),
					height = _getWindowSizes.height,
					width = _getWindowSizes.width;

				boundaries.top += offsets.top - offsets.marginTop;
				boundaries.bottom = height + offsets.top;
				boundaries.left += offsets.left - offsets.marginLeft;
				boundaries.right = width + offsets.left;
			} else {
				// for all the other DOM elements, this one is good
				boundaries = offsets;
			}
		}

		// Add paddings
		boundaries.left += padding;
		boundaries.top += padding;
		boundaries.right -= padding;
		boundaries.bottom -= padding;

		return boundaries;
	}

	function getArea(_ref) {
		var width = _ref.width,
			height = _ref.height;

		return width * height;
	}

	/**
	 * Utility used to transform the `auto` placement to the placement with more
	 * available space.
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function computeAutoPlacement(placement, refRect, popper, reference, boundariesElement) {
		var padding = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 0;

		if (placement.indexOf('auto') === -1) {
			return placement;
		}

		var boundaries = getBoundaries(popper, reference, padding, boundariesElement);

		var rects = {
			top: {
				width: boundaries.width,
				height: refRect.top - boundaries.top
			},
			right: {
				width: boundaries.right - refRect.right,
				height: boundaries.height
			},
			bottom: {
				width: boundaries.width,
				height: boundaries.bottom - refRect.bottom
			},
			left: {
				width: refRect.left - boundaries.left,
				height: boundaries.height
			}
		};

		var sortedAreas = Object.keys(rects).map(function (key) {
			return _extends({
				key: key
			}, rects[key], {
				area: getArea(rects[key])
			});
		}).sort(function (a, b) {
			return b.area - a.area;
		});

		var filteredAreas = sortedAreas.filter(function (_ref2) {
			var width = _ref2.width,
				height = _ref2.height;
			return width >= popper.clientWidth && height >= popper.clientHeight;
		});

		var computedPlacement = filteredAreas.length > 0 ? filteredAreas[0].key : sortedAreas[0].key;

		var variation = placement.split('-')[1];

		return computedPlacement + (variation ? '-' + variation : '');
	}

	/**
	 * Get offsets to the reference element
	 * @method
	 * @memberof Popper.Utils
	 * @param {Object} state
	 * @param {Element} popper - the popper element
	 * @param {Element} reference - the reference element (the popper will be relative to this)
	 * @returns {Object} An object containing the offsets which will be applied to the popper
	 */
	function getReferenceOffsets(state, popper, reference) {
		var commonOffsetParent = findCommonOffsetParent(popper, reference);
		return getOffsetRectRelativeToArbitraryNode(reference, commonOffsetParent);
	}

	/**
	 * Get the outer sizes of the given element (offset size + margins)
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element
	 * @returns {Object} object containing width and height properties
	 */
	function getOuterSizes(element) {
		var styles = getComputedStyle(element);
		var x = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);
		var y = parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
		var result = {
			width: element.offsetWidth + y,
			height: element.offsetHeight + x
		};
		return result;
	}

	/**
	 * Get the opposite placement of the given one
	 * @method
	 * @memberof Popper.Utils
	 * @argument {String} placement
	 * @returns {String} flipped placement
	 */
	function getOppositePlacement(placement) {
		var hash = { left: 'right', right: 'left', bottom: 'top', top: 'bottom' };
		return placement.replace(/left|right|bottom|top/g, function (matched) {
			return hash[matched];
		});
	}

	/**
	 * Get offsets to the popper
	 * @method
	 * @memberof Popper.Utils
	 * @param {Object} position - CSS position the Popper will get applied
	 * @param {HTMLElement} popper - the popper element
	 * @param {Object} referenceOffsets - the reference offsets (the popper will be relative to this)
	 * @param {String} placement - one of the valid placement options
	 * @returns {Object} popperOffsets - An object containing the offsets which will be applied to the popper
	 */
	function getPopperOffsets(popper, referenceOffsets, placement) {
		placement = placement.split('-')[0];

		// Get popper node sizes
		var popperRect = getOuterSizes(popper);

		// Add position, width and height to our offsets object
		var popperOffsets = {
			width: popperRect.width,
			height: popperRect.height
		};

		// depending by the popper placement we have to compute its offsets slightly differently
		var isHoriz = ['right', 'left'].indexOf(placement) !== -1;
		var mainSide = isHoriz ? 'top' : 'left';
		var secondarySide = isHoriz ? 'left' : 'top';
		var measurement = isHoriz ? 'height' : 'width';
		var secondaryMeasurement = !isHoriz ? 'height' : 'width';

		popperOffsets[mainSide] = referenceOffsets[mainSide] + referenceOffsets[measurement] / 2 - popperRect[measurement] / 2;
		if (placement === secondarySide) {
			popperOffsets[secondarySide] = referenceOffsets[secondarySide] - popperRect[secondaryMeasurement];
		} else {
			popperOffsets[secondarySide] = referenceOffsets[getOppositePlacement(secondarySide)];
		}

		return popperOffsets;
	}

	/**
	 * Mimics the `find` method of Array
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Array} arr
	 * @argument prop
	 * @argument value
	 * @returns index or -1
	 */
	function find(arr, check) {
		// use native find if supported
		if (Array.prototype.find) {
			return arr.find(check);
		}

		// use `filter` to obtain the same behavior of `find`
		return arr.filter(check)[0];
	}

	/**
	 * Return the index of the matching object
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Array} arr
	 * @argument prop
	 * @argument value
	 * @returns index or -1
	 */
	function findIndex(arr, prop, value) {
		// use native findIndex if supported
		if (Array.prototype.findIndex) {
			return arr.findIndex(function (cur) {
				return cur[prop] === value;
			});
		}

		// use `find` + `indexOf` if `findIndex` isn't supported
		var match = find(arr, function (obj) {
			return obj[prop] === value;
		});
		return arr.indexOf(match);
	}

	/**
	 * Loop trough the list of modifiers and run them in order,
	 * each of them will then edit the data object.
	 * @method
	 * @memberof Popper.Utils
	 * @param {dataObject} data
	 * @param {Array} modifiers
	 * @param {String} ends - Optional modifier name used as stopper
	 * @returns {dataObject}
	 */
	function runModifiers(modifiers, data, ends) {
		var modifiersToRun = ends === undefined ? modifiers : modifiers.slice(0, findIndex(modifiers, 'name', ends));

		modifiersToRun.forEach(function (modifier) {
			if (modifier['function']) {
				// eslint-disable-line dot-notation
				console.warn('`modifier.function` is deprecated, use `modifier.fn`!');
			}
			var fn = modifier['function'] || modifier.fn; // eslint-disable-line dot-notation
			if (modifier.enabled && isFunction(fn)) {
				// Add properties to offsets to make them a complete clientRect object
				// we do this before each modifier to make sure the previous one doesn't
				// mess with these values
				data.offsets.popper = getClientRect(data.offsets.popper);
				data.offsets.reference = getClientRect(data.offsets.reference);

				data = fn(data, modifier);
			}
		});

		return data;
	}

	/**
	 * Updates the position of the popper, computing the new offsets and applying
	 * the new style.<br />
	 * Prefer `scheduleUpdate` over `update` because of performance reasons.
	 * @method
	 * @memberof Popper
	 */
	function update() {
		// if popper is destroyed, don't perform any further update
		if (this.state.isDestroyed) {
			return;
		}

		var data = {
			instance: this,
			styles: {},
			arrowStyles: {},
			attributes: {},
			flipped: false,
			offsets: {}
		};

		// compute reference element offsets
		data.offsets.reference = getReferenceOffsets(this.state, this.popper, this.reference);

		// compute auto placement, store placement inside the data object,
		// modifiers will be able to edit `placement` if needed
		// and refer to originalPlacement to know the original value
		data.placement = computeAutoPlacement(this.options.placement, data.offsets.reference, this.popper, this.reference, this.options.modifiers.flip.boundariesElement, this.options.modifiers.flip.padding);

		// store the computed placement inside `originalPlacement`
		data.originalPlacement = data.placement;

		// compute the popper offsets
		data.offsets.popper = getPopperOffsets(this.popper, data.offsets.reference, data.placement);
		data.offsets.popper.position = 'absolute';

		// run the modifiers
		data = runModifiers(this.modifiers, data);

		// the first `update` will call `onCreate` callback
		// the other ones will call `onUpdate` callback
		if (!this.state.isCreated) {
			this.state.isCreated = true;
			this.options.onCreate(data);
		} else {
			this.options.onUpdate(data);
		}
	}

	/**
	 * Helper used to know if the given modifier is enabled.
	 * @method
	 * @memberof Popper.Utils
	 * @returns {Boolean}
	 */
	function isModifierEnabled(modifiers, modifierName) {
		return modifiers.some(function (_ref) {
			var name = _ref.name,
				enabled = _ref.enabled;
			return enabled && name === modifierName;
		});
	}

	/**
	 * Get the prefixed supported property name
	 * @method
	 * @memberof Popper.Utils
	 * @argument {String} property (camelCase)
	 * @returns {String} prefixed property (camelCase or PascalCase, depending on the vendor prefix)
	 */
	function getSupportedPropertyName(property) {
		var prefixes = [false, 'ms', 'Webkit', 'Moz', 'O'];
		var upperProp = property.charAt(0).toUpperCase() + property.slice(1);

		for (var i = 0; i < prefixes.length - 1; i++) {
			var prefix = prefixes[i];
			var toCheck = prefix ? '' + prefix + upperProp : property;
			if (typeof document.body.style[toCheck] !== 'undefined') {
				return toCheck;
			}
		}
		return null;
	}

	/**
	 * Destroy the popper
	 * @method
	 * @memberof Popper
	 */
	function destroy() {
		this.state.isDestroyed = true;

		// touch DOM only if `applyStyle` modifier is enabled
		if (isModifierEnabled(this.modifiers, 'applyStyle')) {
			this.popper.removeAttribute('x-placement');
			this.popper.style.left = '';
			this.popper.style.position = '';
			this.popper.style.top = '';
			this.popper.style[getSupportedPropertyName('transform')] = '';
		}

		this.disableEventListeners();

		// remove the popper if user explicity asked for the deletion on destroy
		// do not use `remove` because IE11 doesn't support it
		if (this.options.removeOnDestroy) {
			this.popper.parentNode.removeChild(this.popper);
		}
		return this;
	}

	/**
	 * Get the window associated with the element
	 * @argument {Element} element
	 * @returns {Window}
	 */
	function getWindow(element) {
		var ownerDocument = element.ownerDocument;
		return ownerDocument ? ownerDocument.defaultView : window;
	}

	function attachToScrollParents(scrollParent, event, callback, scrollParents) {
		var isBody = scrollParent.nodeName === 'BODY';
		var target = isBody ? scrollParent.ownerDocument.defaultView : scrollParent;
		target.addEventListener(event, callback, { passive: true });

		if (!isBody) {
			attachToScrollParents(getScrollParent(target.parentNode), event, callback, scrollParents);
		}
		scrollParents.push(target);
	}

	/**
	 * Setup needed event listeners used to update the popper position
	 * @method
	 * @memberof Popper.Utils
	 * @private
	 */
	function setupEventListeners(reference, options, state, updateBound) {
		// Resize event listener on window
		state.updateBound = updateBound;
		getWindow(reference).addEventListener('resize', state.updateBound, { passive: true });

		// Scroll event listener on scroll parents
		var scrollElement = getScrollParent(reference);
		attachToScrollParents(scrollElement, 'scroll', state.updateBound, state.scrollParents);
		state.scrollElement = scrollElement;
		state.eventsEnabled = true;

		return state;
	}

	/**
	 * It will add resize/scroll events and start recalculating
	 * position of the popper element when they are triggered.
	 * @method
	 * @memberof Popper
	 */
	function enableEventListeners() {
		if (!this.state.eventsEnabled) {
			this.state = setupEventListeners(this.reference, this.options, this.state, this.scheduleUpdate);
		}
	}

	/**
	 * Remove event listeners used to update the popper position
	 * @method
	 * @memberof Popper.Utils
	 * @private
	 */
	function removeEventListeners(reference, state) {
		// Remove resize event listener on window
		getWindow(reference).removeEventListener('resize', state.updateBound);

		// Remove scroll event listener on scroll parents
		state.scrollParents.forEach(function (target) {
			target.removeEventListener('scroll', state.updateBound);
		});

		// Reset state
		state.updateBound = null;
		state.scrollParents = [];
		state.scrollElement = null;
		state.eventsEnabled = false;
		return state;
	}

	/**
	 * It will remove resize/scroll events and won't recalculate popper position
	 * when they are triggered. It also won't trigger onUpdate callback anymore,
	 * unless you call `update` method manually.
	 * @method
	 * @memberof Popper
	 */
	function disableEventListeners() {
		if (this.state.eventsEnabled) {
			cancelAnimationFrame(this.scheduleUpdate);
			this.state = removeEventListeners(this.reference, this.state);
		}
	}

	/**
	 * Tells if a given input is a number
	 * @method
	 * @memberof Popper.Utils
	 * @param {*} input to check
	 * @return {Boolean}
	 */
	function isNumeric(n) {
		return n !== '' && !isNaN(parseFloat(n)) && isFinite(n);
	}

	/**
	 * Set the style to the given popper
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element - Element to apply the style to
	 * @argument {Object} styles
	 * Object with a list of properties and values which will be applied to the element
	 */
	function setStyles(element, styles) {
		Object.keys(styles).forEach(function (prop) {
			var unit = '';
			// add unit if the value is numeric and is one of the following
			if (['width', 'height', 'top', 'right', 'bottom', 'left'].indexOf(prop) !== -1 && isNumeric(styles[prop])) {
				unit = 'px';
			}
			element.style[prop] = styles[prop] + unit;
		});
	}

	/**
	 * Set the attributes to the given popper
	 * @method
	 * @memberof Popper.Utils
	 * @argument {Element} element - Element to apply the attributes to
	 * @argument {Object} styles
	 * Object with a list of properties and values which will be applied to the element
	 */
	function setAttributes(element, attributes) {
		Object.keys(attributes).forEach(function (prop) {
			var value = attributes[prop];
			if (value !== false) {
				element.setAttribute(prop, attributes[prop]);
			} else {
				element.removeAttribute(prop);
			}
		});
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by `update` method
	 * @argument {Object} data.styles - List of style properties - values to apply to popper element
	 * @argument {Object} data.attributes - List of attribute properties - values to apply to popper element
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The same data object
	 */
	function applyStyle(data) {
		// any property present in `data.styles` will be applied to the popper,
		// in this way we can make the 3rd party modifiers add custom styles to it
		// Be aware, modifiers could override the properties defined in the previous
		// lines of this modifier!
		setStyles(data.instance.popper, data.styles);

		// any property present in `data.attributes` will be applied to the popper,
		// they will be set as HTML attributes of the element
		setAttributes(data.instance.popper, data.attributes);

		// if arrowElement is defined and arrowStyles has some properties
		if (data.arrowElement && Object.keys(data.arrowStyles).length) {
			setStyles(data.arrowElement, data.arrowStyles);
		}

		return data;
	}

	/**
	 * Set the x-placement attribute before everything else because it could be used
	 * to add margins to the popper margins needs to be calculated to get the
	 * correct popper offsets.
	 * @method
	 * @memberof Popper.modifiers
	 * @param {HTMLElement} reference - The reference element used to position the popper
	 * @param {HTMLElement} popper - The HTML element used as popper.
	 * @param {Object} options - Popper.js options
	 */
	function applyStyleOnLoad(reference, popper, options, modifierOptions, state) {
		// compute reference element offsets
		var referenceOffsets = getReferenceOffsets(state, popper, reference);

		// compute auto placement, store placement inside the data object,
		// modifiers will be able to edit `placement` if needed
		// and refer to originalPlacement to know the original value
		var placement = computeAutoPlacement(options.placement, referenceOffsets, popper, reference, options.modifiers.flip.boundariesElement, options.modifiers.flip.padding);

		popper.setAttribute('x-placement', placement);

		// Apply `position` to popper before anything else because
		// without the position applied we can't guarantee correct computations
		setStyles(popper, { position: 'absolute' });

		return options;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by `update` method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function computeStyle(data, options) {
		var x = options.x,
			y = options.y;
		var popper = data.offsets.popper;

		// Remove this legacy support in Popper.js v2

		var legacyGpuAccelerationOption = find(data.instance.modifiers, function (modifier) {
			return modifier.name === 'applyStyle';
		}).gpuAcceleration;
		if (legacyGpuAccelerationOption !== undefined) {
			console.warn('WARNING: `gpuAcceleration` option moved to `computeStyle` modifier and will not be supported in future versions of Popper.js!');
		}
		var gpuAcceleration = legacyGpuAccelerationOption !== undefined ? legacyGpuAccelerationOption : options.gpuAcceleration;

		var offsetParent = getOffsetParent(data.instance.popper);
		var offsetParentRect = getBoundingClientRect(offsetParent);

		// Styles
		var styles = {
			position: popper.position
		};

		// floor sides to avoid blurry text
		var offsets = {
			left: Math.floor(popper.left),
			top: Math.floor(popper.top),
			bottom: Math.floor(popper.bottom),
			right: Math.floor(popper.right)
		};

		var sideA = x === 'bottom' ? 'top' : 'bottom';
		var sideB = y === 'right' ? 'left' : 'right';

		// if gpuAcceleration is set to `true` and transform is supported,
		//  we use `translate3d` to apply the position to the popper we
		// automatically use the supported prefixed version if needed
		var prefixedProperty = getSupportedPropertyName('transform');

		// now, let's make a step back and look at this code closely (wtf?)
		// If the content of the popper grows once it's been positioned, it
		// may happen that the popper gets misplaced because of the new content
		// overflowing its reference element
		// To avoid this problem, we provide two options (x and y), which allow
		// the consumer to define the offset origin.
		// If we position a popper on top of a reference element, we can set
		// `x` to `top` to make the popper grow towards its top instead of
		// its bottom.
		var left = void 0,
			top = void 0;
		if (sideA === 'bottom') {
			top = -offsetParentRect.height + offsets.bottom;
		} else {
			top = offsets.top;
		}
		if (sideB === 'right') {
			left = -offsetParentRect.width + offsets.right;
		} else {
			left = offsets.left;
		}
		if (gpuAcceleration && prefixedProperty) {
			styles[prefixedProperty] = 'translate3d(' + left + 'px, ' + top + 'px, 0)';
			styles[sideA] = 0;
			styles[sideB] = 0;
			styles.willChange = 'transform';
		} else {
			// othwerise, we use the standard `top`, `left`, `bottom` and `right` properties
			var invertTop = sideA === 'bottom' ? -1 : 1;
			var invertLeft = sideB === 'right' ? -1 : 1;
			styles[sideA] = top * invertTop;
			styles[sideB] = left * invertLeft;
			styles.willChange = sideA + ', ' + sideB;
		}

		// Attributes
		var attributes = {
			'x-placement': data.placement
		};

		// Update `data` attributes, styles and arrowStyles
		data.attributes = _extends({}, attributes, data.attributes);
		data.styles = _extends({}, styles, data.styles);
		data.arrowStyles = _extends({}, data.offsets.arrow, data.arrowStyles);

		return data;
	}

	/**
	 * Helper used to know if the given modifier depends from another one.<br />
	 * It checks if the needed modifier is listed and enabled.
	 * @method
	 * @memberof Popper.Utils
	 * @param {Array} modifiers - list of modifiers
	 * @param {String} requestingName - name of requesting modifier
	 * @param {String} requestedName - name of requested modifier
	 * @returns {Boolean}
	 */
	function isModifierRequired(modifiers, requestingName, requestedName) {
		var requesting = find(modifiers, function (_ref) {
			var name = _ref.name;
			return name === requestingName;
		});

		var isRequired = !!requesting && modifiers.some(function (modifier) {
			return modifier.name === requestedName && modifier.enabled && modifier.order < requesting.order;
		});

		if (!isRequired) {
			var _requesting = '`' + requestingName + '`';
			var requested = '`' + requestedName + '`';
			console.warn(requested + ' modifier is required by ' + _requesting + ' modifier in order to work, be sure to include it before ' + _requesting + '!');
		}
		return isRequired;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function arrow(data, options) {
		var _data$offsets$arrow;

		// arrow depends on keepTogether in order to work
		if (!isModifierRequired(data.instance.modifiers, 'arrow', 'keepTogether')) {
			return data;
		}

		var arrowElement = options.element;

		// if arrowElement is a string, suppose it's a CSS selector
		if (typeof arrowElement === 'string') {
			arrowElement = data.instance.popper.querySelector(arrowElement);

			// if arrowElement is not found, don't run the modifier
			if (!arrowElement) {
				return data;
			}
		} else {
			// if the arrowElement isn't a query selector we must check that the
			// provided DOM node is child of its popper node
			if (!data.instance.popper.contains(arrowElement)) {
				console.warn('WARNING: `arrow.element` must be child of its popper element!');
				return data;
			}
		}

		var placement = data.placement.split('-')[0];
		var _data$offsets = data.offsets,
			popper = _data$offsets.popper,
			reference = _data$offsets.reference;

		var isVertical = ['left', 'right'].indexOf(placement) !== -1;

		var len = isVertical ? 'height' : 'width';
		var sideCapitalized = isVertical ? 'Top' : 'Left';
		var side = sideCapitalized.toLowerCase();
		var altSide = isVertical ? 'left' : 'top';
		var opSide = isVertical ? 'bottom' : 'right';
		var arrowElementSize = getOuterSizes(arrowElement)[len];

		//
		// extends keepTogether behavior making sure the popper and its
		// reference have enough pixels in conjuction
		//

		// top/left side
		if (reference[opSide] - arrowElementSize < popper[side]) {
			data.offsets.popper[side] -= popper[side] - (reference[opSide] - arrowElementSize);
		}
		// bottom/right side
		if (reference[side] + arrowElementSize > popper[opSide]) {
			data.offsets.popper[side] += reference[side] + arrowElementSize - popper[opSide];
		}
		data.offsets.popper = getClientRect(data.offsets.popper);

		// compute center of the popper
		var center = reference[side] + reference[len] / 2 - arrowElementSize / 2;

		// Compute the sideValue using the updated popper offsets
		// take popper margin in account because we don't have this info available
		var css = getStyleComputedProperty(data.instance.popper);
		var popperMarginSide = parseFloat(css['margin' + sideCapitalized], 10);
		var popperBorderSide = parseFloat(css['border' + sideCapitalized + 'Width'], 10);
		var sideValue = center - data.offsets.popper[side] - popperMarginSide - popperBorderSide;

		// prevent arrowElement from being placed not contiguously to its popper
		sideValue = Math.max(Math.min(popper[len] - arrowElementSize, sideValue), 0);

		data.arrowElement = arrowElement;
		data.offsets.arrow = (_data$offsets$arrow = {}, defineProperty(_data$offsets$arrow, side, Math.round(sideValue)), defineProperty(_data$offsets$arrow, altSide, ''), _data$offsets$arrow);

		return data;
	}

	/**
	 * Get the opposite placement variation of the given one
	 * @method
	 * @memberof Popper.Utils
	 * @argument {String} placement variation
	 * @returns {String} flipped placement variation
	 */
	function getOppositeVariation(variation) {
		if (variation === 'end') {
			return 'start';
		} else if (variation === 'start') {
			return 'end';
		}
		return variation;
	}

	/**
	 * List of accepted placements to use as values of the `placement` option.<br />
	 * Valid placements are:
	 * - `auto`
	 * - `top`
	 * - `right`
	 * - `bottom`
	 * - `left`
	 *
	 * Each placement can have a variation from this list:
	 * - `-start`
	 * - `-end`
	 *
	 * Variations are interpreted easily if you think of them as the left to right
	 * written languages. Horizontally (`top` and `bottom`), `start` is left and `end`
	 * is right.<br />
	 * Vertically (`left` and `right`), `start` is top and `end` is bottom.
	 *
	 * Some valid examples are:
	 * - `top-end` (on top of reference, right aligned)
	 * - `right-start` (on right of reference, top aligned)
	 * - `bottom` (on bottom, centered)
	 * - `auto-right` (on the side with more space available, alignment depends by placement)
	 *
	 * @static
	 * @type {Array}
	 * @enum {String}
	 * @readonly
	 * @method placements
	 * @memberof Popper
	 */
	var placements = ['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start'];

	// Get rid of `auto` `auto-start` and `auto-end`
	var validPlacements = placements.slice(3);

	/**
	 * Given an initial placement, returns all the subsequent placements
	 * clockwise (or counter-clockwise).
	 *
	 * @method
	 * @memberof Popper.Utils
	 * @argument {String} placement - A valid placement (it accepts variations)
	 * @argument {Boolean} counter - Set to true to walk the placements counterclockwise
	 * @returns {Array} placements including their variations
	 */
	function clockwise(placement) {
		var counter = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

		var index = validPlacements.indexOf(placement);
		var arr = validPlacements.slice(index + 1).concat(validPlacements.slice(0, index));
		return counter ? arr.reverse() : arr;
	}

	var BEHAVIORS = {
		FLIP: 'flip',
		CLOCKWISE: 'clockwise',
		COUNTERCLOCKWISE: 'counterclockwise'
	};

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function flip(data, options) {
		// if `inner` modifier is enabled, we can't use the `flip` modifier
		if (isModifierEnabled(data.instance.modifiers, 'inner')) {
			return data;
		}

		if (data.flipped && data.placement === data.originalPlacement) {
			// seems like flip is trying to loop, probably there's not enough space on any of the flippable sides
			return data;
		}

		var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, options.boundariesElement);

		var placement = data.placement.split('-')[0];
		var placementOpposite = getOppositePlacement(placement);
		var variation = data.placement.split('-')[1] || '';

		var flipOrder = [];

		switch (options.behavior) {
			case BEHAVIORS.FLIP:
				flipOrder = [placement, placementOpposite];
				break;
			case BEHAVIORS.CLOCKWISE:
				flipOrder = clockwise(placement);
				break;
			case BEHAVIORS.COUNTERCLOCKWISE:
				flipOrder = clockwise(placement, true);
				break;
			default:
				flipOrder = options.behavior;
		}

		flipOrder.forEach(function (step, index) {
			if (placement !== step || flipOrder.length === index + 1) {
				return data;
			}

			placement = data.placement.split('-')[0];
			placementOpposite = getOppositePlacement(placement);

			var popperOffsets = data.offsets.popper;
			var refOffsets = data.offsets.reference;

			// using floor because the reference offsets may contain decimals we are not going to consider here
			var floor = Math.floor;
			var overlapsRef = placement === 'left' && floor(popperOffsets.right) > floor(refOffsets.left) || placement === 'right' && floor(popperOffsets.left) < floor(refOffsets.right) || placement === 'top' && floor(popperOffsets.bottom) > floor(refOffsets.top) || placement === 'bottom' && floor(popperOffsets.top) < floor(refOffsets.bottom);

			var overflowsLeft = floor(popperOffsets.left) < floor(boundaries.left);
			var overflowsRight = floor(popperOffsets.right) > floor(boundaries.right);
			var overflowsTop = floor(popperOffsets.top) < floor(boundaries.top);
			var overflowsBottom = floor(popperOffsets.bottom) > floor(boundaries.bottom);

			var overflowsBoundaries = placement === 'left' && overflowsLeft || placement === 'right' && overflowsRight || placement === 'top' && overflowsTop || placement === 'bottom' && overflowsBottom;

			// flip the variation if required
			var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;
			var flippedVariation = !!options.flipVariations && (isVertical && variation === 'start' && overflowsLeft || isVertical && variation === 'end' && overflowsRight || !isVertical && variation === 'start' && overflowsTop || !isVertical && variation === 'end' && overflowsBottom);

			if (overlapsRef || overflowsBoundaries || flippedVariation) {
				// this boolean to detect any flip loop
				data.flipped = true;

				if (overlapsRef || overflowsBoundaries) {
					placement = flipOrder[index + 1];
				}

				if (flippedVariation) {
					variation = getOppositeVariation(variation);
				}

				data.placement = placement + (variation ? '-' + variation : '');

				// this object contains `position`, we want to preserve it along with
				// any additional property we may add in the future
				data.offsets.popper = _extends({}, data.offsets.popper, getPopperOffsets(data.instance.popper, data.offsets.reference, data.placement));

				data = runModifiers(data.instance.modifiers, data, 'flip');
			}
		});
		return data;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function keepTogether(data) {
		var _data$offsets = data.offsets,
			popper = _data$offsets.popper,
			reference = _data$offsets.reference;

		var placement = data.placement.split('-')[0];
		var floor = Math.floor;
		var isVertical = ['top', 'bottom'].indexOf(placement) !== -1;
		var side = isVertical ? 'right' : 'bottom';
		var opSide = isVertical ? 'left' : 'top';
		var measurement = isVertical ? 'width' : 'height';

		if (popper[side] < floor(reference[opSide])) {
			data.offsets.popper[opSide] = floor(reference[opSide]) - popper[measurement];
		}
		if (popper[opSide] > floor(reference[side])) {
			data.offsets.popper[opSide] = floor(reference[side]);
		}

		return data;
	}

	/**
	 * Converts a string containing value + unit into a px value number
	 * @function
	 * @memberof {modifiers~offset}
	 * @private
	 * @argument {String} str - Value + unit string
	 * @argument {String} measurement - `height` or `width`
	 * @argument {Object} popperOffsets
	 * @argument {Object} referenceOffsets
	 * @returns {Number|String}
	 * Value in pixels, or original string if no values were extracted
	 */
	function toValue(str, measurement, popperOffsets, referenceOffsets) {
		// separate value from unit
		var split = str.match(/((?:\-|\+)?\d*\.?\d*)(.*)/);
		var value = +split[1];
		var unit = split[2];

		// If it's not a number it's an operator, I guess
		if (!value) {
			return str;
		}

		if (unit.indexOf('%') === 0) {
			var element = void 0;
			switch (unit) {
				case '%p':
					element = popperOffsets;
					break;
				case '%':
				case '%r':
				default:
					element = referenceOffsets;
			}

			var rect = getClientRect(element);
			return rect[measurement] / 100 * value;
		} else if (unit === 'vh' || unit === 'vw') {
			// if is a vh or vw, we calculate the size based on the viewport
			var size = void 0;
			if (unit === 'vh') {
				size = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
			} else {
				size = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
			}
			return size / 100 * value;
		} else {
			// if is an explicit pixel unit, we get rid of the unit and keep the value
			// if is an implicit unit, it's px, and we return just the value
			return value;
		}
	}

	/**
	 * Parse an `offset` string to extrapolate `x` and `y` numeric offsets.
	 * @function
	 * @memberof {modifiers~offset}
	 * @private
	 * @argument {String} offset
	 * @argument {Object} popperOffsets
	 * @argument {Object} referenceOffsets
	 * @argument {String} basePlacement
	 * @returns {Array} a two cells array with x and y offsets in numbers
	 */
	function parseOffset(offset, popperOffsets, referenceOffsets, basePlacement) {
		var offsets = [0, 0];

		// Use height if placement is left or right and index is 0 otherwise use width
		// in this way the first offset will use an axis and the second one
		// will use the other one
		var useHeight = ['right', 'left'].indexOf(basePlacement) !== -1;

		// Split the offset string to obtain a list of values and operands
		// The regex addresses values with the plus or minus sign in front (+10, -20, etc)
		var fragments = offset.split(/(\+|\-)/).map(function (frag) {
			return frag.trim();
		});

		// Detect if the offset string contains a pair of values or a single one
		// they could be separated by comma or space
		var divider = fragments.indexOf(find(fragments, function (frag) {
			return frag.search(/,|\s/) !== -1;
		}));

		if (fragments[divider] && fragments[divider].indexOf(',') === -1) {
			console.warn('Offsets separated by white space(s) are deprecated, use a comma (,) instead.');
		}

		// If divider is found, we divide the list of values and operands to divide
		// them by ofset X and Y.
		var splitRegex = /\s*,\s*|\s+/;
		var ops = divider !== -1 ? [fragments.slice(0, divider).concat([fragments[divider].split(splitRegex)[0]]), [fragments[divider].split(splitRegex)[1]].concat(fragments.slice(divider + 1))] : [fragments];

		// Convert the values with units to absolute pixels to allow our computations
		ops = ops.map(function (op, index) {
			// Most of the units rely on the orientation of the popper
			var measurement = (index === 1 ? !useHeight : useHeight) ? 'height' : 'width';
			var mergeWithPrevious = false;
			return op
				// This aggregates any `+` or `-` sign that aren't considered operators
				// e.g.: 10 + +5 => [10, +, +5]
				.reduce(function (a, b) {
					if (a[a.length - 1] === '' && ['+', '-'].indexOf(b) !== -1) {
						a[a.length - 1] = b;
						mergeWithPrevious = true;
						return a;
					} else if (mergeWithPrevious) {
						a[a.length - 1] += b;
						mergeWithPrevious = false;
						return a;
					} else {
						return a.concat(b);
					}
				}, [])
				// Here we convert the string values into number values (in px)
				.map(function (str) {
					return toValue(str, measurement, popperOffsets, referenceOffsets);
				});
		});

		// Loop trough the offsets arrays and execute the operations
		ops.forEach(function (op, index) {
			op.forEach(function (frag, index2) {
				if (isNumeric(frag)) {
					offsets[index] += frag * (op[index2 - 1] === '-' ? -1 : 1);
				}
			});
		});
		return offsets;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @argument {Number|String} options.offset=0
	 * The offset value as described in the modifier description
	 * @returns {Object} The data object, properly modified
	 */
	function offset(data, _ref) {
		var offset = _ref.offset;
		var placement = data.placement,
			_data$offsets = data.offsets,
			popper = _data$offsets.popper,
			reference = _data$offsets.reference;

		var basePlacement = placement.split('-')[0];

		var offsets = void 0;
		if (isNumeric(+offset)) {
			offsets = [+offset, 0];
		} else {
			offsets = parseOffset(offset, popper, reference, basePlacement);
		}

		if (basePlacement === 'left') {
			popper.top += offsets[0];
			popper.left -= offsets[1];
		} else if (basePlacement === 'right') {
			popper.top += offsets[0];
			popper.left += offsets[1];
		} else if (basePlacement === 'top') {
			popper.left += offsets[0];
			popper.top -= offsets[1];
		} else if (basePlacement === 'bottom') {
			popper.left += offsets[0];
			popper.top += offsets[1];
		}

		data.popper = popper;
		return data;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by `update` method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function preventOverflow(data, options) {
		var boundariesElement = options.boundariesElement || getOffsetParent(data.instance.popper);

		// If offsetParent is the reference element, we really want to
		// go one step up and use the next offsetParent as reference to
		// avoid to make this modifier completely useless and look like broken
		if (data.instance.reference === boundariesElement) {
			boundariesElement = getOffsetParent(boundariesElement);
		}

		var boundaries = getBoundaries(data.instance.popper, data.instance.reference, options.padding, boundariesElement);
		options.boundaries = boundaries;

		var order = options.priority;
		var popper = data.offsets.popper;

		var check = {
			primary: function primary(placement) {
				var value = popper[placement];
				if (popper[placement] < boundaries[placement] && !options.escapeWithReference) {
					value = Math.max(popper[placement], boundaries[placement]);
				}
				return defineProperty({}, placement, value);
			},
			secondary: function secondary(placement) {
				var mainSide = placement === 'right' ? 'left' : 'top';
				var value = popper[mainSide];
				if (popper[placement] > boundaries[placement] && !options.escapeWithReference) {
					value = Math.min(popper[mainSide], boundaries[placement] - (placement === 'right' ? popper.width : popper.height));
				}
				return defineProperty({}, mainSide, value);
			}
		};

		order.forEach(function (placement) {
			var side = ['left', 'top'].indexOf(placement) !== -1 ? 'primary' : 'secondary';
			popper = _extends({}, popper, check[side](placement));
		});

		data.offsets.popper = popper;

		return data;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by `update` method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function shift(data) {
		var placement = data.placement;
		var basePlacement = placement.split('-')[0];
		var shiftvariation = placement.split('-')[1];

		// if shift shiftvariation is specified, run the modifier
		if (shiftvariation) {
			var _data$offsets = data.offsets,
				reference = _data$offsets.reference,
				popper = _data$offsets.popper;

			var isVertical = ['bottom', 'top'].indexOf(basePlacement) !== -1;
			var side = isVertical ? 'left' : 'top';
			var measurement = isVertical ? 'width' : 'height';

			var shiftOffsets = {
				start: defineProperty({}, side, reference[side]),
				end: defineProperty({}, side, reference[side] + reference[measurement] - popper[measurement])
			};

			data.offsets.popper = _extends({}, popper, shiftOffsets[shiftvariation]);
		}

		return data;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by update method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function hide(data) {
		if (!isModifierRequired(data.instance.modifiers, 'hide', 'preventOverflow')) {
			return data;
		}

		var refRect = data.offsets.reference;
		var bound = find(data.instance.modifiers, function (modifier) {
			return modifier.name === 'preventOverflow';
		}).boundaries;

		if (refRect.bottom < bound.top || refRect.left > bound.right || refRect.top > bound.bottom || refRect.right < bound.left) {
			// Avoid unnecessary DOM access if visibility hasn't changed
			if (data.hide === true) {
				return data;
			}

			data.hide = true;
			data.attributes['x-out-of-boundaries'] = '';
		} else {
			// Avoid unnecessary DOM access if visibility hasn't changed
			if (data.hide === false) {
				return data;
			}

			data.hide = false;
			data.attributes['x-out-of-boundaries'] = false;
		}

		return data;
	}

	/**
	 * @function
	 * @memberof Modifiers
	 * @argument {Object} data - The data object generated by `update` method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {Object} The data object, properly modified
	 */
	function inner(data) {
		var placement = data.placement;
		var basePlacement = placement.split('-')[0];
		var _data$offsets = data.offsets,
			popper = _data$offsets.popper,
			reference = _data$offsets.reference;

		var isHoriz = ['left', 'right'].indexOf(basePlacement) !== -1;

		var subtractLength = ['top', 'left'].indexOf(basePlacement) === -1;

		popper[isHoriz ? 'left' : 'top'] = reference[basePlacement] - (subtractLength ? popper[isHoriz ? 'width' : 'height'] : 0);

		data.placement = getOppositePlacement(placement);
		data.offsets.popper = getClientRect(popper);

		return data;
	}

	/**
	 * Modifier function, each modifier can have a function of this type assigned
	 * to its `fn` property.<br />
	 * These functions will be called on each update, this means that you must
	 * make sure they are performant enough to avoid performance bottlenecks.
	 *
	 * @function ModifierFn
	 * @argument {dataObject} data - The data object generated by `update` method
	 * @argument {Object} options - Modifiers configuration and options
	 * @returns {dataObject} The data object, properly modified
	 */

	/**
	 * Modifiers are plugins used to alter the behavior of your poppers.<br />
	 * Popper.js uses a set of 9 modifiers to provide all the basic functionalities
	 * needed by the library.
	 *
	 * Usually you don't want to override the `order`, `fn` and `onLoad` props.
	 * All the other properties are configurations that could be tweaked.
	 * @namespace modifiers
	 */
	var modifiers = {
		/**
		 * Modifier used to shift the popper on the start or end of its reference
		 * element.<br />
		 * It will read the variation of the `placement` property.<br />
		 * It can be one either `-end` or `-start`.
		 * @memberof modifiers
		 * @inner
		 */
		shift: {
			/** @prop {number} order=100 - Index used to define the order of execution */
			order: 100,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: shift
		},

		/**
		 * The `offset` modifier can shift your popper on both its axis.
		 *
		 * It accepts the following units:
		 * - `px` or unitless, interpreted as pixels
		 * - `%` or `%r`, percentage relative to the length of the reference element
		 * - `%p`, percentage relative to the length of the popper element
		 * - `vw`, CSS viewport width unit
		 * - `vh`, CSS viewport height unit
		 *
		 * For length is intended the main axis relative to the placement of the popper.<br />
		 * This means that if the placement is `top` or `bottom`, the length will be the
		 * `width`. In case of `left` or `right`, it will be the height.
		 *
		 * You can provide a single value (as `Number` or `String`), or a pair of values
		 * as `String` divided by a comma or one (or more) white spaces.<br />
		 * The latter is a deprecated method because it leads to confusion and will be
		 * removed in v2.<br />
		 * Additionally, it accepts additions and subtractions between different units.
		 * Note that multiplications and divisions aren't supported.
		 *
		 * Valid examples are:
		 * ```
		 * 10
		 * '10%'
		 * '10, 10'
		 * '10%, 10'
		 * '10 + 10%'
		 * '10 - 5vh + 3%'
		 * '-10px + 5vh, 5px - 6%'
		 * ```
		 * > **NB**: If you desire to apply offsets to your poppers in a way that may make them overlap
		 * > with their reference element, unfortunately, you will have to disable the `flip` modifier.
		 * > More on this [reading this issue](https://github.com/FezVrasta/popper.js/issues/373)
		 *
		 * @memberof modifiers
		 * @inner
		 */
		offset: {
			/** @prop {number} order=200 - Index used to define the order of execution */
			order: 200,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: offset,
			/** @prop {Number|String} offset=0
			 * The offset value as described in the modifier description
			 */
			offset: 0
		},

		/**
		 * Modifier used to prevent the popper from being positioned outside the boundary.
		 *
		 * An scenario exists where the reference itself is not within the boundaries.<br />
		 * We can say it has "escaped the boundaries" — or just "escaped".<br />
		 * In this case we need to decide whether the popper should either:
		 *
		 * - detach from the reference and remain "trapped" in the boundaries, or
		 * - if it should ignore the boundary and "escape with its reference"
		 *
		 * When `escapeWithReference` is set to`true` and reference is completely
		 * outside its boundaries, the popper will overflow (or completely leave)
		 * the boundaries in order to remain attached to the edge of the reference.
		 *
		 * @memberof modifiers
		 * @inner
		 */
		preventOverflow: {
			/** @prop {number} order=300 - Index used to define the order of execution */
			order: 300,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: preventOverflow,
			/**
			 * @prop {Array} [priority=['left','right','top','bottom']]
			 * Popper will try to prevent overflow following these priorities by default,
			 * then, it could overflow on the left and on top of the `boundariesElement`
			 */
			priority: ['left', 'right', 'top', 'bottom'],
			/**
			 * @prop {number} padding=5
			 * Amount of pixel used to define a minimum distance between the boundaries
			 * and the popper this makes sure the popper has always a little padding
			 * between the edges of its container
			 */
			padding: 5,
			/**
			 * @prop {String|HTMLElement} boundariesElement='scrollParent'
			 * Boundaries used by the modifier, can be `scrollParent`, `window`,
			 * `viewport` or any DOM element.
			 */
			boundariesElement: 'scrollParent'
		},

		/**
		 * Modifier used to make sure the reference and its popper stay near eachothers
		 * without leaving any gap between the two. Expecially useful when the arrow is
		 * enabled and you want to assure it to point to its reference element.
		 * It cares only about the first axis, you can still have poppers with margin
		 * between the popper and its reference element.
		 * @memberof modifiers
		 * @inner
		 */
		keepTogether: {
			/** @prop {number} order=400 - Index used to define the order of execution */
			order: 400,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: keepTogether
		},

		/**
		 * This modifier is used to move the `arrowElement` of the popper to make
		 * sure it is positioned between the reference element and its popper element.
		 * It will read the outer size of the `arrowElement` node to detect how many
		 * pixels of conjuction are needed.
		 *
		 * It has no effect if no `arrowElement` is provided.
		 * @memberof modifiers
		 * @inner
		 */
		arrow: {
			/** @prop {number} order=500 - Index used to define the order of execution */
			order: 500,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: arrow,
			/** @prop {String|HTMLElement} element='[x-arrow]' - Selector or node used as arrow */
			element: '[x-arrow]'
		},

		/**
		 * Modifier used to flip the popper's placement when it starts to overlap its
		 * reference element.
		 *
		 * Requires the `preventOverflow` modifier before it in order to work.
		 *
		 * **NOTE:** this modifier will interrupt the current update cycle and will
		 * restart it if it detects the need to flip the placement.
		 * @memberof modifiers
		 * @inner
		 */
		flip: {
			/** @prop {number} order=600 - Index used to define the order of execution */
			order: 600,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: flip,
			/**
			 * @prop {String|Array} behavior='flip'
			 * The behavior used to change the popper's placement. It can be one of
			 * `flip`, `clockwise`, `counterclockwise` or an array with a list of valid
			 * placements (with optional variations).
			 */
			behavior: 'flip',
			/**
			 * @prop {number} padding=5
			 * The popper will flip if it hits the edges of the `boundariesElement`
			 */
			padding: 5,
			/**
			 * @prop {String|HTMLElement} boundariesElement='viewport'
			 * The element which will define the boundaries of the popper position,
			 * the popper will never be placed outside of the defined boundaries
			 * (except if keepTogether is enabled)
			 */
			boundariesElement: 'viewport'
		},

		/**
		 * Modifier used to make the popper flow toward the inner of the reference element.
		 * By default, when this modifier is disabled, the popper will be placed outside
		 * the reference element.
		 * @memberof modifiers
		 * @inner
		 */
		inner: {
			/** @prop {number} order=700 - Index used to define the order of execution */
			order: 700,
			/** @prop {Boolean} enabled=false - Whether the modifier is enabled or not */
			enabled: false,
			/** @prop {ModifierFn} */
			fn: inner
		},

		/**
		 * Modifier used to hide the popper when its reference element is outside of the
		 * popper boundaries. It will set a `x-out-of-boundaries` attribute which can
		 * be used to hide with a CSS selector the popper when its reference is
		 * out of boundaries.
		 *
		 * Requires the `preventOverflow` modifier before it in order to work.
		 * @memberof modifiers
		 * @inner
		 */
		hide: {
			/** @prop {number} order=800 - Index used to define the order of execution */
			order: 800,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: hide
		},

		/**
		 * Computes the style that will be applied to the popper element to gets
		 * properly positioned.
		 *
		 * Note that this modifier will not touch the DOM, it just prepares the styles
		 * so that `applyStyle` modifier can apply it. This separation is useful
		 * in case you need to replace `applyStyle` with a custom implementation.
		 *
		 * This modifier has `850` as `order` value to maintain backward compatibility
		 * with previous versions of Popper.js. Expect the modifiers ordering method
		 * to change in future major versions of the library.
		 *
		 * @memberof modifiers
		 * @inner
		 */
		computeStyle: {
			/** @prop {number} order=850 - Index used to define the order of execution */
			order: 850,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: computeStyle,
			/**
			 * @prop {Boolean} gpuAcceleration=true
			 * If true, it uses the CSS 3d transformation to position the popper.
			 * Otherwise, it will use the `top` and `left` properties.
			 */
			gpuAcceleration: true,
			/**
			 * @prop {string} [x='bottom']
			 * Where to anchor the X axis (`bottom` or `top`). AKA X offset origin.
			 * Change this if your popper should grow in a direction different from `bottom`
			 */
			x: 'bottom',
			/**
			 * @prop {string} [x='left']
			 * Where to anchor the Y axis (`left` or `right`). AKA Y offset origin.
			 * Change this if your popper should grow in a direction different from `right`
			 */
			y: 'right'
		},

		/**
		 * Applies the computed styles to the popper element.
		 *
		 * All the DOM manipulations are limited to this modifier. This is useful in case
		 * you want to integrate Popper.js inside a framework or view library and you
		 * want to delegate all the DOM manipulations to it.
		 *
		 * Note that if you disable this modifier, you must make sure the popper element
		 * has its position set to `absolute` before Popper.js can do its work!
		 *
		 * Just disable this modifier and define you own to achieve the desired effect.
		 *
		 * @memberof modifiers
		 * @inner
		 */
		applyStyle: {
			/** @prop {number} order=900 - Index used to define the order of execution */
			order: 900,
			/** @prop {Boolean} enabled=true - Whether the modifier is enabled or not */
			enabled: true,
			/** @prop {ModifierFn} */
			fn: applyStyle,
			/** @prop {Function} */
			onLoad: applyStyleOnLoad,
			/**
			 * @deprecated since version 1.10.0, the property moved to `computeStyle` modifier
			 * @prop {Boolean} gpuAcceleration=true
			 * If true, it uses the CSS 3d transformation to position the popper.
			 * Otherwise, it will use the `top` and `left` properties.
			 */
			gpuAcceleration: undefined
		}
	};

	/**
	 * The `dataObject` is an object containing all the informations used by Popper.js
	 * this object get passed to modifiers and to the `onCreate` and `onUpdate` callbacks.
	 * @name dataObject
	 * @property {Object} data.instance The Popper.js instance
	 * @property {String} data.placement Placement applied to popper
	 * @property {String} data.originalPlacement Placement originally defined on init
	 * @property {Boolean} data.flipped True if popper has been flipped by flip modifier
	 * @property {Boolean} data.hide True if the reference element is out of boundaries, useful to know when to hide the popper.
	 * @property {HTMLElement} data.arrowElement Node used as arrow by arrow modifier
	 * @property {Object} data.styles Any CSS property defined here will be applied to the popper, it expects the JavaScript nomenclature (eg. `marginBottom`)
	 * @property {Object} data.arrowStyles Any CSS property defined here will be applied to the popper arrow, it expects the JavaScript nomenclature (eg. `marginBottom`)
	 * @property {Object} data.boundaries Offsets of the popper boundaries
	 * @property {Object} data.offsets The measurements of popper, reference and arrow elements.
	 * @property {Object} data.offsets.popper `top`, `left`, `width`, `height` values
	 * @property {Object} data.offsets.reference `top`, `left`, `width`, `height` values
	 * @property {Object} data.offsets.arrow] `top` and `left` offsets, only one of them will be different from 0
	 */

	/**
	 * Default options provided to Popper.js constructor.<br />
	 * These can be overriden using the `options` argument of Popper.js.<br />
	 * To override an option, simply pass as 3rd argument an object with the same
	 * structure of this object, example:
	 * ```
	 * new Popper(ref, pop, {
	 *   modifiers: {
	 *     preventOverflow: { enabled: false }
	 *   }
	 * })
	 * ```
	 * @type {Object}
	 * @static
	 * @memberof Popper
	 */
	var Defaults = {
		/**
		 * Popper's placement
		 * @prop {Popper.placements} placement='bottom'
		 */
		placement: 'bottom',

		/**
		 * Whether events (resize, scroll) are initially enabled
		 * @prop {Boolean} eventsEnabled=true
		 */
		eventsEnabled: true,

		/**
		 * Set to true if you want to automatically remove the popper when
		 * you call the `destroy` method.
		 * @prop {Boolean} removeOnDestroy=false
		 */
		removeOnDestroy: false,

		/**
		 * Callback called when the popper is created.<br />
		 * By default, is set to no-op.<br />
		 * Access Popper.js instance with `data.instance`.
		 * @prop {onCreate}
		 */
		onCreate: function onCreate() { },

		/**
		 * Callback called when the popper is updated, this callback is not called
		 * on the initialization/creation of the popper, but only on subsequent
		 * updates.<br />
		 * By default, is set to no-op.<br />
		 * Access Popper.js instance with `data.instance`.
		 * @prop {onUpdate}
		 */
		onUpdate: function onUpdate() { },

		/**
		 * List of modifiers used to modify the offsets before they are applied to the popper.
		 * They provide most of the functionalities of Popper.js
		 * @prop {modifiers}
		 */
		modifiers: modifiers
	};

	/**
	 * @callback onCreate
	 * @param {dataObject} data
	 */

	/**
	 * @callback onUpdate
	 * @param {dataObject} data
	 */

	// Utils
	// Methods
	var Popper = function () {
		/**
		 * Create a new Popper.js instance
		 * @class Popper
		 * @param {HTMLElement|referenceObject} reference - The reference element used to position the popper
		 * @param {HTMLElement} popper - The HTML element used as popper.
		 * @param {Object} options - Your custom options to override the ones defined in [Defaults](#defaults)
		 * @return {Object} instance - The generated Popper.js instance
		 */
		function Popper(reference, popper) {
			var _this = this;

			var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
			classCallCheck(this, Popper);

			this.scheduleUpdate = function () {
				return requestAnimationFrame(_this.update);
			};

			// make update() debounced, so that it only runs at most once-per-tick
			this.update = debounce(this.update.bind(this));

			// with {} we create a new object with the options inside it
			this.options = _extends({}, Popper.Defaults, options);

			// init state
			this.state = {
				isDestroyed: false,
				isCreated: false,
				scrollParents: []
			};

			// get reference and popper elements (allow jQuery wrappers)
			this.reference = reference && reference.jquery ? reference[0] : reference;
			this.popper = popper && popper.jquery ? popper[0] : popper;

			// Deep merge modifiers options
			this.options.modifiers = {};
			Object.keys(_extends({}, Popper.Defaults.modifiers, options.modifiers)).forEach(function (name) {
				_this.options.modifiers[name] = _extends({}, Popper.Defaults.modifiers[name] || {}, options.modifiers ? options.modifiers[name] : {});
			});

			// Refactoring modifiers' list (Object => Array)
			this.modifiers = Object.keys(this.options.modifiers).map(function (name) {
				return _extends({
					name: name
				}, _this.options.modifiers[name]);
			})
				// sort the modifiers by order
				.sort(function (a, b) {
					return a.order - b.order;
				});

			// modifiers have the ability to execute arbitrary code when Popper.js get inited
			// such code is executed in the same order of its modifier
			// they could add new properties to their options configuration
			// BE AWARE: don't add options to `options.modifiers.name` but to `modifierOptions`!
			this.modifiers.forEach(function (modifierOptions) {
				if (modifierOptions.enabled && isFunction(modifierOptions.onLoad)) {
					modifierOptions.onLoad(_this.reference, _this.popper, _this.options, modifierOptions, _this.state);
				}
			});

			// fire the first update to position the popper in the right place
			this.update();

			var eventsEnabled = this.options.eventsEnabled;
			if (eventsEnabled) {
				// setup event listeners, they will take care of update the position in specific situations
				this.enableEventListeners();
			}

			this.state.eventsEnabled = eventsEnabled;
		}

		// We can't use class properties because they don't get listed in the
		// class prototype and break stuff like Sinon stubs


		createClass(Popper, [{
			key: 'update',
			value: function update$$1() {
				return update.call(this);
			}
		}, {
			key: 'destroy',
			value: function destroy$$1() {
				return destroy.call(this);
			}
		}, {
			key: 'enableEventListeners',
			value: function enableEventListeners$$1() {
				return enableEventListeners.call(this);
			}
		}, {
			key: 'disableEventListeners',
			value: function disableEventListeners$$1() {
				return disableEventListeners.call(this);
			}

			/**
			 * Schedule an update, it will run on the next UI update available
			 * @method scheduleUpdate
			 * @memberof Popper
			 */


			/**
			 * Collection of utilities useful when writing custom modifiers.
			 * Starting from version 1.7, this method is available only if you
			 * include `popper-utils.js` before `popper.js`.
			 *
			 * **DEPRECATION**: This way to access PopperUtils is deprecated
			 * and will be removed in v2! Use the PopperUtils module directly instead.
			 * Due to the high instability of the methods contained in Utils, we can't
			 * guarantee them to follow semver. Use them at your own risk!
			 * @static
			 * @private
			 * @type {Object}
			 * @deprecated since version 1.8
			 * @member Utils
			 * @memberof Popper
			 */

		}]);
		return Popper;
	}();

	/**
	 * The `referenceObject` is an object that provides an interface compatible with Popper.js
	 * and lets you use it as replacement of a real DOM node.<br />
	 * You can use this method to position a popper relatively to a set of coordinates
	 * in case you don't have a DOM node to use as reference.
	 *
	 * ```
	 * new Popper(referenceObject, popperNode);
	 * ```
	 *
	 * NB: This feature isn't supported in Internet Explorer 10
	 * @name referenceObject
	 * @property {Function} data.getBoundingClientRect
	 * A function that returns a set of coordinates compatible with the native `getBoundingClientRect` method.
	 * @property {number} data.clientWidth
	 * An ES6 getter that will return the width of the virtual reference element.
	 * @property {number} data.clientHeight
	 * An ES6 getter that will return the height of the virtual reference element.
	 */


	Popper.Utils = (typeof window !== 'undefined' ? window : global).PopperUtils;
	Popper.placements = placements;
	Popper.Defaults = Defaults;

	return Popper;

})));
//# sourceMappingURL=popper.js.map


/**
 ********************************
 **          BOOTSTRAP         **
 ********************************
 */

(function (global, factory) {
	typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('jquery'), require('popper.js')) :
		typeof define === 'function' && define.amd ? define(['exports', 'jquery', 'popper.js'], factory) :
			(global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.bootstrap = {}, global.jQuery, global.Popper));
})(this, (function (exports, $, Popper) {
	'use strict';

	function _interopDefaultLegacy(e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

	var $__default = /*#__PURE__*/_interopDefaultLegacy($);
	var Popper__default = /*#__PURE__*/_interopDefaultLegacy(Popper);

	function _defineProperties(target, props) {
		for (var i = 0; i < props.length; i++) {
			var descriptor = props[i];
			descriptor.enumerable = descriptor.enumerable || false;
			descriptor.configurable = true;
			if ("value" in descriptor) descriptor.writable = true;
			Object.defineProperty(target, descriptor.key, descriptor);
		}
	}

	function _createClass(Constructor, protoProps, staticProps) {
		if (protoProps) _defineProperties(Constructor.prototype, protoProps);
		if (staticProps) _defineProperties(Constructor, staticProps);
		Object.defineProperty(Constructor, "prototype", {
			writable: false
		});
		return Constructor;
	}

	function _extends() {
		_extends = Object.assign ? Object.assign.bind() : function (target) {
			for (var i = 1; i < arguments.length; i++) {
				var source = arguments[i];

				for (var key in source) {
					if (Object.prototype.hasOwnProperty.call(source, key)) {
						target[key] = source[key];
					}
				}
			}

			return target;
		};
		return _extends.apply(this, arguments);
	}

	function _inheritsLoose(subClass, superClass) {
		subClass.prototype = Object.create(superClass.prototype);
		subClass.prototype.constructor = subClass;

		_setPrototypeOf(subClass, superClass);
	}

	function _setPrototypeOf(o, p) {
		_setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
			o.__proto__ = p;
			return o;
		};
		return _setPrototypeOf(o, p);
	}

	/**
	 * --------------------------------------------------------------------------
	 * Bootstrap (v4.6.2): util.js
	 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
	 * --------------------------------------------------------------------------
	 */
	/**
	 * Private TransitionEnd Helpers
	 */

	var TRANSITION_END = 'transitionend';
	var MAX_UID = 1000000;
	var MILLISECONDS_MULTIPLIER = 1000; // Shoutout AngusCroll (https://goo.gl/pxwQGp)

	function toType(obj) {
		if (obj === null || typeof obj === 'undefined') {
			return "" + obj;
		}

		return {}.toString.call(obj).match(/\s([a-z]+)/i)[1].toLowerCase();
	}

	function getSpecialTransitionEndEvent() {
		return {
			bindType: TRANSITION_END,
			delegateType: TRANSITION_END,
			handle: function handle(event) {
				if ($__default["default"](event.target).is(this)) {
					return event.handleObj.handler.apply(this, arguments); // eslint-disable-line prefer-rest-params
				}

				return undefined;
			}
		};
	}

	function transitionEndEmulator(duration) {
		var _this = this;

		var called = false;
		$__default["default"](this).one(Util.TRANSITION_END, function () {
			called = true;
		});
		setTimeout(function () {
			if (!called) {
				Util.triggerTransitionEnd(_this);
			}
		}, duration);
		return this;
	}

	function setTransitionEndSupport() {
		$__default["default"].fn.emulateTransitionEnd = transitionEndEmulator;
		$__default["default"].event.special[Util.TRANSITION_END] = getSpecialTransitionEndEvent();
	}
	/**
	 * Public Util API
	 */


	var Util = {
		TRANSITION_END: 'bsTransitionEnd',
		getUID: function getUID(prefix) {
			do {
				// eslint-disable-next-line no-bitwise
				prefix += ~~(Math.random() * MAX_UID); // "~~" acts like a faster Math.floor() here
			} while (document.getElementById(prefix));

			return prefix;
		},
		getSelectorFromElement: function getSelectorFromElement(element) {
			var selector = element.getAttribute('data-target');

			if (!selector || selector === '#') {
				var hrefAttr = element.getAttribute('href');
				selector = hrefAttr && hrefAttr !== '#' ? hrefAttr.trim() : '';
			}

			try {
				return document.querySelector(selector) ? selector : null;
			} catch (_) {
				return null;
			}
		},
		getTransitionDurationFromElement: function getTransitionDurationFromElement(element) {
			if (!element) {
				return 0;
			} // Get transition-duration of the element


			var transitionDuration = $__default["default"](element).css('transition-duration');
			var transitionDelay = $__default["default"](element).css('transition-delay');
			var floatTransitionDuration = parseFloat(transitionDuration);
			var floatTransitionDelay = parseFloat(transitionDelay); // Return 0 if element or transition duration is not found

			if (!floatTransitionDuration && !floatTransitionDelay) {
				return 0;
			} // If multiple durations are defined, take the first


			transitionDuration = transitionDuration.split(',')[0];
			transitionDelay = transitionDelay.split(',')[0];
			return (parseFloat(transitionDuration) + parseFloat(transitionDelay)) * MILLISECONDS_MULTIPLIER;
		},
		reflow: function reflow(element) {
			return element.offsetHeight;
		},
		triggerTransitionEnd: function triggerTransitionEnd(element) {
			$__default["default"](element).trigger(TRANSITION_END);
		},
		supportsTransitionEnd: function supportsTransitionEnd() {
			return Boolean(TRANSITION_END);
		},
		isElement: function isElement(obj) {
			return (obj[0] || obj).nodeType;
		},
		typeCheckConfig: function typeCheckConfig(componentName, config, configTypes) {
			for (var property in configTypes) {
				if (Object.prototype.hasOwnProperty.call(configTypes, property)) {
					var expectedTypes = configTypes[property];
					var value = config[property];
					var valueType = value && Util.isElement(value) ? 'element' : toType(value);

					if (!new RegExp(expectedTypes).test(valueType)) {
						throw new Error(componentName.toUpperCase() + ": " + ("Option \"" + property + "\" provided type \"" + valueType + "\" ") + ("but expected type \"" + expectedTypes + "\"."));
					}
				}
			}
		},
		findShadowRoot: function findShadowRoot(element) {
			if (!document.documentElement.attachShadow) {
				return null;
			} // Can find the shadow root otherwise it'll return the document


			if (typeof element.getRootNode === 'function') {
				var root = element.getRootNode();
				return root instanceof ShadowRoot ? root : null;
			}

			if (element instanceof ShadowRoot) {
				return element;
			} // when we don't find a shadow root


			if (!element.parentNode) {
				return null;
			}

			return Util.findShadowRoot(element.parentNode);
		},
		jQueryDetection: function jQueryDetection() {
			if (typeof $__default["default"] === 'undefined') {
				throw new TypeError('Bootstrap\'s JavaScript requires jQuery. jQuery must be included before Bootstrap\'s JavaScript.');
			}

			var version = $__default["default"].fn.jquery.split(' ')[0].split('.');
			var minMajor = 1;
			var ltMajor = 2;
			var minMinor = 9;
			var minPatch = 1;
			var maxMajor = 4;

			if (version[0] < ltMajor && version[1] < minMinor || version[0] === minMajor && version[1] === minMinor && version[2] < minPatch || version[0] >= maxMajor) {
				throw new Error('Bootstrap\'s JavaScript requires at least jQuery v1.9.1 but less than v4.0.0');
			}
		}
	};
	Util.jQueryDetection();
	setTransitionEndSupport();

	/**
	 * Constants
	 */

	var NAME$a = 'alert';
	var VERSION$a = '4.6.2';
	var DATA_KEY$a = 'bs.alert';
	var EVENT_KEY$a = "." + DATA_KEY$a;
	var DATA_API_KEY$7 = '.data-api';
	var JQUERY_NO_CONFLICT$a = $__default["default"].fn[NAME$a];
	var CLASS_NAME_ALERT = 'alert';
	var CLASS_NAME_FADE$5 = 'fade';
	var CLASS_NAME_SHOW$7 = 'show';
	var EVENT_CLOSE = "close" + EVENT_KEY$a;
	var EVENT_CLOSED = "closed" + EVENT_KEY$a;
	var EVENT_CLICK_DATA_API$6 = "click" + EVENT_KEY$a + DATA_API_KEY$7;
	var SELECTOR_DISMISS = '[data-dismiss="alert"]';
	/**
	 * Class definition
	 */

	var Alert = /*#__PURE__*/function () {
		function Alert(element) {
			this._element = element;
		} // Getters


		var _proto = Alert.prototype;

		// Public
		_proto.close = function close(element) {
			var rootElement = this._element;

			if (element) {
				rootElement = this._getRootElement(element);
			}

			var customEvent = this._triggerCloseEvent(rootElement);

			if (customEvent.isDefaultPrevented()) {
				return;
			}

			this._removeElement(rootElement);
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$a);
			this._element = null;
		} // Private
			;

		_proto._getRootElement = function _getRootElement(element) {
			var selector = Util.getSelectorFromElement(element);
			var parent = false;

			if (selector) {
				parent = document.querySelector(selector);
			}

			if (!parent) {
				parent = $__default["default"](element).closest("." + CLASS_NAME_ALERT)[0];
			}

			return parent;
		};

		_proto._triggerCloseEvent = function _triggerCloseEvent(element) {
			var closeEvent = $__default["default"].Event(EVENT_CLOSE);
			$__default["default"](element).trigger(closeEvent);
			return closeEvent;
		};

		_proto._removeElement = function _removeElement(element) {
			var _this = this;

			$__default["default"](element).removeClass(CLASS_NAME_SHOW$7);

			if (!$__default["default"](element).hasClass(CLASS_NAME_FADE$5)) {
				this._destroyElement(element);

				return;
			}

			var transitionDuration = Util.getTransitionDurationFromElement(element);
			$__default["default"](element).one(Util.TRANSITION_END, function (event) {
				return _this._destroyElement(element, event);
			}).emulateTransitionEnd(transitionDuration);
		};

		_proto._destroyElement = function _destroyElement(element) {
			$__default["default"](element).detach().trigger(EVENT_CLOSED).remove();
		} // Static
			;

		Alert._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var $element = $__default["default"](this);
				var data = $element.data(DATA_KEY$a);

				if (!data) {
					data = new Alert(this);
					$element.data(DATA_KEY$a, data);
				}

				if (config === 'close') {
					data[config](this);
				}
			});
		};

		Alert._handleDismiss = function _handleDismiss(alertInstance) {
			return function (event) {
				if (event) {
					event.preventDefault();
				}

				alertInstance.close(this);
			};
		};

		_createClass(Alert, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$a;
			}
		}]);

		return Alert;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API$6, SELECTOR_DISMISS, Alert._handleDismiss(new Alert()));
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$a] = Alert._jQueryInterface;
	$__default["default"].fn[NAME$a].Constructor = Alert;

	$__default["default"].fn[NAME$a].noConflict = function () {
		$__default["default"].fn[NAME$a] = JQUERY_NO_CONFLICT$a;
		return Alert._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$9 = 'button';
	var VERSION$9 = '4.6.2';
	var DATA_KEY$9 = 'bs.button';
	var EVENT_KEY$9 = "." + DATA_KEY$9;
	var DATA_API_KEY$6 = '.data-api';
	var JQUERY_NO_CONFLICT$9 = $__default["default"].fn[NAME$9];
	var CLASS_NAME_ACTIVE$3 = 'active';
	var CLASS_NAME_BUTTON = 'btn';
	var CLASS_NAME_FOCUS = 'focus';
	var EVENT_CLICK_DATA_API$5 = "click" + EVENT_KEY$9 + DATA_API_KEY$6;
	var EVENT_FOCUS_BLUR_DATA_API = "focus" + EVENT_KEY$9 + DATA_API_KEY$6 + " " + ("blur" + EVENT_KEY$9 + DATA_API_KEY$6);
	var EVENT_LOAD_DATA_API$2 = "load" + EVENT_KEY$9 + DATA_API_KEY$6;
	var SELECTOR_DATA_TOGGLE_CARROT = '[data-toggle^="button"]';
	var SELECTOR_DATA_TOGGLES = '[data-toggle="buttons"]';
	var SELECTOR_DATA_TOGGLE$4 = '[data-toggle="button"]';
	var SELECTOR_DATA_TOGGLES_BUTTONS = '[data-toggle="buttons"] .btn';
	var SELECTOR_INPUT = 'input:not([type="hidden"])';
	var SELECTOR_ACTIVE$2 = '.active';
	var SELECTOR_BUTTON = '.btn';
	/**
	 * Class definition
	 */

	var Button = /*#__PURE__*/function () {
		function Button(element) {
			this._element = element;
			this.shouldAvoidTriggerChange = false;
		} // Getters


		var _proto = Button.prototype;

		// Public
		_proto.toggle = function toggle() {
			var triggerChangeEvent = true;
			var addAriaPressed = true;
			var rootElement = $__default["default"](this._element).closest(SELECTOR_DATA_TOGGLES)[0];

			if (rootElement) {
				var input = this._element.querySelector(SELECTOR_INPUT);

				if (input) {
					if (input.type === 'radio') {
						if (input.checked && this._element.classList.contains(CLASS_NAME_ACTIVE$3)) {
							triggerChangeEvent = false;
						} else {
							var activeElement = rootElement.querySelector(SELECTOR_ACTIVE$2);

							if (activeElement) {
								$__default["default"](activeElement).removeClass(CLASS_NAME_ACTIVE$3);
							}
						}
					}

					if (triggerChangeEvent) {
						// if it's not a radio button or checkbox don't add a pointless/invalid checked property to the input
						if (input.type === 'checkbox' || input.type === 'radio') {
							input.checked = !this._element.classList.contains(CLASS_NAME_ACTIVE$3);
						}

						if (!this.shouldAvoidTriggerChange) {
							$__default["default"](input).trigger('change');
						}
					}

					input.focus();
					addAriaPressed = false;
				}
			}

			if (!(this._element.hasAttribute('disabled') || this._element.classList.contains('disabled'))) {
				if (addAriaPressed) {
					this._element.setAttribute('aria-pressed', !this._element.classList.contains(CLASS_NAME_ACTIVE$3));
				}

				if (triggerChangeEvent) {
					$__default["default"](this._element).toggleClass(CLASS_NAME_ACTIVE$3);
				}
			}
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$9);
			this._element = null;
		} // Static
			;

		Button._jQueryInterface = function _jQueryInterface(config, avoidTriggerChange) {
			return this.each(function () {
				var $element = $__default["default"](this);
				var data = $element.data(DATA_KEY$9);

				if (!data) {
					data = new Button(this);
					$element.data(DATA_KEY$9, data);
				}

				data.shouldAvoidTriggerChange = avoidTriggerChange;

				if (config === 'toggle') {
					data[config]();
				}
			});
		};

		_createClass(Button, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$9;
			}
		}]);

		return Button;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API$5, SELECTOR_DATA_TOGGLE_CARROT, function (event) {
		var button = event.target;
		var initialButton = button;

		if (!$__default["default"](button).hasClass(CLASS_NAME_BUTTON)) {
			button = $__default["default"](button).closest(SELECTOR_BUTTON)[0];
		}

		if (!button || button.hasAttribute('disabled') || button.classList.contains('disabled')) {
			event.preventDefault(); // work around Firefox bug #1540995
		} else {
			var inputBtn = button.querySelector(SELECTOR_INPUT);

			if (inputBtn && (inputBtn.hasAttribute('disabled') || inputBtn.classList.contains('disabled'))) {
				event.preventDefault(); // work around Firefox bug #1540995

				return;
			}

			if (initialButton.tagName === 'INPUT' || button.tagName !== 'LABEL') {
				Button._jQueryInterface.call($__default["default"](button), 'toggle', initialButton.tagName === 'INPUT');
			}
		}
	}).on(EVENT_FOCUS_BLUR_DATA_API, SELECTOR_DATA_TOGGLE_CARROT, function (event) {
		var button = $__default["default"](event.target).closest(SELECTOR_BUTTON)[0];
		$__default["default"](button).toggleClass(CLASS_NAME_FOCUS, /^focus(in)?$/.test(event.type));
	});
	$__default["default"](window).on(EVENT_LOAD_DATA_API$2, function () {
		// ensure correct active class is set to match the controls' actual values/states
		// find all checkboxes/readio buttons inside data-toggle groups
		var buttons = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLES_BUTTONS));

		for (var i = 0, len = buttons.length; i < len; i++) {
			var button = buttons[i];
			var input = button.querySelector(SELECTOR_INPUT);

			if (input.checked || input.hasAttribute('checked')) {
				button.classList.add(CLASS_NAME_ACTIVE$3);
			} else {
				button.classList.remove(CLASS_NAME_ACTIVE$3);
			}
		} // find all button toggles


		buttons = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLE$4));

		for (var _i = 0, _len = buttons.length; _i < _len; _i++) {
			var _button = buttons[_i];

			if (_button.getAttribute('aria-pressed') === 'true') {
				_button.classList.add(CLASS_NAME_ACTIVE$3);
			} else {
				_button.classList.remove(CLASS_NAME_ACTIVE$3);
			}
		}
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$9] = Button._jQueryInterface;
	$__default["default"].fn[NAME$9].Constructor = Button;

	$__default["default"].fn[NAME$9].noConflict = function () {
		$__default["default"].fn[NAME$9] = JQUERY_NO_CONFLICT$9;
		return Button._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$8 = 'carousel';
	var VERSION$8 = '4.6.2';
	var DATA_KEY$8 = 'bs.carousel';
	var EVENT_KEY$8 = "." + DATA_KEY$8;
	var DATA_API_KEY$5 = '.data-api';
	var JQUERY_NO_CONFLICT$8 = $__default["default"].fn[NAME$8];
	var ARROW_LEFT_KEYCODE = 37; // KeyboardEvent.which value for left arrow key

	var ARROW_RIGHT_KEYCODE = 39; // KeyboardEvent.which value for right arrow key

	var TOUCHEVENT_COMPAT_WAIT = 500; // Time for mouse compat events to fire after touch

	var SWIPE_THRESHOLD = 40;
	var CLASS_NAME_CAROUSEL = 'carousel';
	var CLASS_NAME_ACTIVE$2 = 'active';
	var CLASS_NAME_SLIDE = 'slide';
	var CLASS_NAME_RIGHT = 'carousel-item-right';
	var CLASS_NAME_LEFT = 'carousel-item-left';
	var CLASS_NAME_NEXT = 'carousel-item-next';
	var CLASS_NAME_PREV = 'carousel-item-prev';
	var CLASS_NAME_POINTER_EVENT = 'pointer-event';
	var DIRECTION_NEXT = 'next';
	var DIRECTION_PREV = 'prev';
	var DIRECTION_LEFT = 'left';
	var DIRECTION_RIGHT = 'right';
	var EVENT_SLIDE = "slide" + EVENT_KEY$8;
	var EVENT_SLID = "slid" + EVENT_KEY$8;
	var EVENT_KEYDOWN = "keydown" + EVENT_KEY$8;
	var EVENT_MOUSEENTER = "mouseenter" + EVENT_KEY$8;
	var EVENT_MOUSELEAVE = "mouseleave" + EVENT_KEY$8;
	var EVENT_TOUCHSTART = "touchstart" + EVENT_KEY$8;
	var EVENT_TOUCHMOVE = "touchmove" + EVENT_KEY$8;
	var EVENT_TOUCHEND = "touchend" + EVENT_KEY$8;
	var EVENT_POINTERDOWN = "pointerdown" + EVENT_KEY$8;
	var EVENT_POINTERUP = "pointerup" + EVENT_KEY$8;
	var EVENT_DRAG_START = "dragstart" + EVENT_KEY$8;
	var EVENT_LOAD_DATA_API$1 = "load" + EVENT_KEY$8 + DATA_API_KEY$5;
	var EVENT_CLICK_DATA_API$4 = "click" + EVENT_KEY$8 + DATA_API_KEY$5;
	var SELECTOR_ACTIVE$1 = '.active';
	var SELECTOR_ACTIVE_ITEM = '.active.carousel-item';
	var SELECTOR_ITEM = '.carousel-item';
	var SELECTOR_ITEM_IMG = '.carousel-item img';
	var SELECTOR_NEXT_PREV = '.carousel-item-next, .carousel-item-prev';
	var SELECTOR_INDICATORS = '.carousel-indicators';
	var SELECTOR_DATA_SLIDE = '[data-slide], [data-slide-to]';
	var SELECTOR_DATA_RIDE = '[data-ride="carousel"]';
	var Default$7 = {
		interval: 5000,
		keyboard: true,
		slide: false,
		pause: 'hover',
		wrap: true,
		touch: true
	};
	var DefaultType$7 = {
		interval: '(number|boolean)',
		keyboard: 'boolean',
		slide: '(boolean|string)',
		pause: '(string|boolean)',
		wrap: 'boolean',
		touch: 'boolean'
	};
	var PointerType = {
		TOUCH: 'touch',
		PEN: 'pen'
	};
	/**
	 * Class definition
	 */

	var Carousel = /*#__PURE__*/function () {
		function Carousel(element, config) {
			this._items = null;
			this._interval = null;
			this._activeElement = null;
			this._isPaused = false;
			this._isSliding = false;
			this.touchTimeout = null;
			this.touchStartX = 0;
			this.touchDeltaX = 0;
			this._config = this._getConfig(config);
			this._element = element;
			this._indicatorsElement = this._element.querySelector(SELECTOR_INDICATORS);
			this._touchSupported = 'ontouchstart' in document.documentElement || navigator.maxTouchPoints > 0;
			this._pointerEvent = Boolean(window.PointerEvent || window.MSPointerEvent);

			this._addEventListeners();
		} // Getters


		var _proto = Carousel.prototype;

		// Public
		_proto.next = function next() {
			if (!this._isSliding) {
				this._slide(DIRECTION_NEXT);
			}
		};

		_proto.nextWhenVisible = function nextWhenVisible() {
			var $element = $__default["default"](this._element); // Don't call next when the page isn't visible
			// or the carousel or its parent isn't visible

			if (!document.hidden && $element.is(':visible') && $element.css('visibility') !== 'hidden') {
				this.next();
			}
		};

		_proto.prev = function prev() {
			if (!this._isSliding) {
				this._slide(DIRECTION_PREV);
			}
		};

		_proto.pause = function pause(event) {
			if (!event) {
				this._isPaused = true;
			}

			if (this._element.querySelector(SELECTOR_NEXT_PREV)) {
				Util.triggerTransitionEnd(this._element);
				this.cycle(true);
			}

			clearInterval(this._interval);
			this._interval = null;
		};

		_proto.cycle = function cycle(event) {
			if (!event) {
				this._isPaused = false;
			}

			if (this._interval) {
				clearInterval(this._interval);
				this._interval = null;
			}

			if (this._config.interval && !this._isPaused) {
				this._updateInterval();

				this._interval = setInterval((document.visibilityState ? this.nextWhenVisible : this.next).bind(this), this._config.interval);
			}
		};

		_proto.to = function to(index) {
			var _this = this;

			this._activeElement = this._element.querySelector(SELECTOR_ACTIVE_ITEM);

			var activeIndex = this._getItemIndex(this._activeElement);

			if (index > this._items.length - 1 || index < 0) {
				return;
			}

			if (this._isSliding) {
				$__default["default"](this._element).one(EVENT_SLID, function () {
					return _this.to(index);
				});
				return;
			}

			if (activeIndex === index) {
				this.pause();
				this.cycle();
				return;
			}

			var direction = index > activeIndex ? DIRECTION_NEXT : DIRECTION_PREV;

			this._slide(direction, this._items[index]);
		};

		_proto.dispose = function dispose() {
			$__default["default"](this._element).off(EVENT_KEY$8);
			$__default["default"].removeData(this._element, DATA_KEY$8);
			this._items = null;
			this._config = null;
			this._element = null;
			this._interval = null;
			this._isPaused = null;
			this._isSliding = null;
			this._activeElement = null;
			this._indicatorsElement = null;
		} // Private
			;

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, Default$7, config);
			Util.typeCheckConfig(NAME$8, config, DefaultType$7);
			return config;
		};

		_proto._handleSwipe = function _handleSwipe() {
			var absDeltax = Math.abs(this.touchDeltaX);

			if (absDeltax <= SWIPE_THRESHOLD) {
				return;
			}

			var direction = absDeltax / this.touchDeltaX;
			this.touchDeltaX = 0; // swipe left

			if (direction > 0) {
				this.prev();
			} // swipe right


			if (direction < 0) {
				this.next();
			}
		};

		_proto._addEventListeners = function _addEventListeners() {
			var _this2 = this;

			if (this._config.keyboard) {
				$__default["default"](this._element).on(EVENT_KEYDOWN, function (event) {
					return _this2._keydown(event);
				});
			}

			if (this._config.pause === 'hover') {
				$__default["default"](this._element).on(EVENT_MOUSEENTER, function (event) {
					return _this2.pause(event);
				}).on(EVENT_MOUSELEAVE, function (event) {
					return _this2.cycle(event);
				});
			}

			if (this._config.touch) {
				this._addTouchEventListeners();
			}
		};

		_proto._addTouchEventListeners = function _addTouchEventListeners() {
			var _this3 = this;

			if (!this._touchSupported) {
				return;
			}

			var start = function start(event) {
				if (_this3._pointerEvent && PointerType[event.originalEvent.pointerType.toUpperCase()]) {
					_this3.touchStartX = event.originalEvent.clientX;
				} else if (!_this3._pointerEvent) {
					_this3.touchStartX = event.originalEvent.touches[0].clientX;
				}
			};

			var move = function move(event) {
				// ensure swiping with one touch and not pinching
				_this3.touchDeltaX = event.originalEvent.touches && event.originalEvent.touches.length > 1 ? 0 : event.originalEvent.touches[0].clientX - _this3.touchStartX;
			};

			var end = function end(event) {
				if (_this3._pointerEvent && PointerType[event.originalEvent.pointerType.toUpperCase()]) {
					_this3.touchDeltaX = event.originalEvent.clientX - _this3.touchStartX;
				}

				_this3._handleSwipe();

				if (_this3._config.pause === 'hover') {
					// If it's a touch-enabled device, mouseenter/leave are fired as
					// part of the mouse compatibility events on first tap - the carousel
					// would stop cycling until user tapped out of it;
					// here, we listen for touchend, explicitly pause the carousel
					// (as if it's the second time we tap on it, mouseenter compat event
					// is NOT fired) and after a timeout (to allow for mouse compatibility
					// events to fire) we explicitly restart cycling
					_this3.pause();

					if (_this3.touchTimeout) {
						clearTimeout(_this3.touchTimeout);
					}

					_this3.touchTimeout = setTimeout(function (event) {
						return _this3.cycle(event);
					}, TOUCHEVENT_COMPAT_WAIT + _this3._config.interval);
				}
			};

			$__default["default"](this._element.querySelectorAll(SELECTOR_ITEM_IMG)).on(EVENT_DRAG_START, function (e) {
				return e.preventDefault();
			});

			if (this._pointerEvent) {
				$__default["default"](this._element).on(EVENT_POINTERDOWN, function (event) {
					return start(event);
				});
				$__default["default"](this._element).on(EVENT_POINTERUP, function (event) {
					return end(event);
				});

				this._element.classList.add(CLASS_NAME_POINTER_EVENT);
			} else {
				$__default["default"](this._element).on(EVENT_TOUCHSTART, function (event) {
					return start(event);
				});
				$__default["default"](this._element).on(EVENT_TOUCHMOVE, function (event) {
					return move(event);
				});
				$__default["default"](this._element).on(EVENT_TOUCHEND, function (event) {
					return end(event);
				});
			}
		};

		_proto._keydown = function _keydown(event) {
			if (/input|textarea/i.test(event.target.tagName)) {
				return;
			}

			switch (event.which) {
				case ARROW_LEFT_KEYCODE:
					event.preventDefault();
					this.prev();
					break;

				case ARROW_RIGHT_KEYCODE:
					event.preventDefault();
					this.next();
					break;
			}
		};

		_proto._getItemIndex = function _getItemIndex(element) {
			this._items = element && element.parentNode ? [].slice.call(element.parentNode.querySelectorAll(SELECTOR_ITEM)) : [];
			return this._items.indexOf(element);
		};

		_proto._getItemByDirection = function _getItemByDirection(direction, activeElement) {
			var isNextDirection = direction === DIRECTION_NEXT;
			var isPrevDirection = direction === DIRECTION_PREV;

			var activeIndex = this._getItemIndex(activeElement);

			var lastItemIndex = this._items.length - 1;
			var isGoingToWrap = isPrevDirection && activeIndex === 0 || isNextDirection && activeIndex === lastItemIndex;

			if (isGoingToWrap && !this._config.wrap) {
				return activeElement;
			}

			var delta = direction === DIRECTION_PREV ? -1 : 1;
			var itemIndex = (activeIndex + delta) % this._items.length;
			return itemIndex === -1 ? this._items[this._items.length - 1] : this._items[itemIndex];
		};

		_proto._triggerSlideEvent = function _triggerSlideEvent(relatedTarget, eventDirectionName) {
			var targetIndex = this._getItemIndex(relatedTarget);

			var fromIndex = this._getItemIndex(this._element.querySelector(SELECTOR_ACTIVE_ITEM));

			var slideEvent = $__default["default"].Event(EVENT_SLIDE, {
				relatedTarget: relatedTarget,
				direction: eventDirectionName,
				from: fromIndex,
				to: targetIndex
			});
			$__default["default"](this._element).trigger(slideEvent);
			return slideEvent;
		};

		_proto._setActiveIndicatorElement = function _setActiveIndicatorElement(element) {
			if (this._indicatorsElement) {
				var indicators = [].slice.call(this._indicatorsElement.querySelectorAll(SELECTOR_ACTIVE$1));
				$__default["default"](indicators).removeClass(CLASS_NAME_ACTIVE$2);

				var nextIndicator = this._indicatorsElement.children[this._getItemIndex(element)];

				if (nextIndicator) {
					$__default["default"](nextIndicator).addClass(CLASS_NAME_ACTIVE$2);
				}
			}
		};

		_proto._updateInterval = function _updateInterval() {
			var element = this._activeElement || this._element.querySelector(SELECTOR_ACTIVE_ITEM);

			if (!element) {
				return;
			}

			var elementInterval = parseInt(element.getAttribute('data-interval'), 10);

			if (elementInterval) {
				this._config.defaultInterval = this._config.defaultInterval || this._config.interval;
				this._config.interval = elementInterval;
			} else {
				this._config.interval = this._config.defaultInterval || this._config.interval;
			}
		};

		_proto._slide = function _slide(direction, element) {
			var _this4 = this;

			var activeElement = this._element.querySelector(SELECTOR_ACTIVE_ITEM);

			var activeElementIndex = this._getItemIndex(activeElement);

			var nextElement = element || activeElement && this._getItemByDirection(direction, activeElement);

			var nextElementIndex = this._getItemIndex(nextElement);

			var isCycling = Boolean(this._interval);
			var directionalClassName;
			var orderClassName;
			var eventDirectionName;

			if (direction === DIRECTION_NEXT) {
				directionalClassName = CLASS_NAME_LEFT;
				orderClassName = CLASS_NAME_NEXT;
				eventDirectionName = DIRECTION_LEFT;
			} else {
				directionalClassName = CLASS_NAME_RIGHT;
				orderClassName = CLASS_NAME_PREV;
				eventDirectionName = DIRECTION_RIGHT;
			}

			if (nextElement && $__default["default"](nextElement).hasClass(CLASS_NAME_ACTIVE$2)) {
				this._isSliding = false;
				return;
			}

			var slideEvent = this._triggerSlideEvent(nextElement, eventDirectionName);

			if (slideEvent.isDefaultPrevented()) {
				return;
			}

			if (!activeElement || !nextElement) {
				// Some weirdness is happening, so we bail
				return;
			}

			this._isSliding = true;

			if (isCycling) {
				this.pause();
			}

			this._setActiveIndicatorElement(nextElement);

			this._activeElement = nextElement;
			var slidEvent = $__default["default"].Event(EVENT_SLID, {
				relatedTarget: nextElement,
				direction: eventDirectionName,
				from: activeElementIndex,
				to: nextElementIndex
			});

			if ($__default["default"](this._element).hasClass(CLASS_NAME_SLIDE)) {
				$__default["default"](nextElement).addClass(orderClassName);
				Util.reflow(nextElement);
				$__default["default"](activeElement).addClass(directionalClassName);
				$__default["default"](nextElement).addClass(directionalClassName);
				var transitionDuration = Util.getTransitionDurationFromElement(activeElement);
				$__default["default"](activeElement).one(Util.TRANSITION_END, function () {
					$__default["default"](nextElement).removeClass(directionalClassName + " " + orderClassName).addClass(CLASS_NAME_ACTIVE$2);
					$__default["default"](activeElement).removeClass(CLASS_NAME_ACTIVE$2 + " " + orderClassName + " " + directionalClassName);
					_this4._isSliding = false;
					setTimeout(function () {
						return $__default["default"](_this4._element).trigger(slidEvent);
					}, 0);
				}).emulateTransitionEnd(transitionDuration);
			} else {
				$__default["default"](activeElement).removeClass(CLASS_NAME_ACTIVE$2);
				$__default["default"](nextElement).addClass(CLASS_NAME_ACTIVE$2);
				this._isSliding = false;
				$__default["default"](this._element).trigger(slidEvent);
			}

			if (isCycling) {
				this.cycle();
			}
		} // Static
			;

		Carousel._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var data = $__default["default"](this).data(DATA_KEY$8);

				var _config = _extends({}, Default$7, $__default["default"](this).data());

				if (typeof config === 'object') {
					_config = _extends({}, _config, config);
				}

				var action = typeof config === 'string' ? config : _config.slide;

				if (!data) {
					data = new Carousel(this, _config);
					$__default["default"](this).data(DATA_KEY$8, data);
				}

				if (typeof config === 'number') {
					data.to(config);
				} else if (typeof action === 'string') {
					if (typeof data[action] === 'undefined') {
						throw new TypeError("No method named \"" + action + "\"");
					}

					data[action]();
				} else if (_config.interval && _config.ride) {
					data.pause();
					data.cycle();
				}
			});
		};

		Carousel._dataApiClickHandler = function _dataApiClickHandler(event) {
			var selector = Util.getSelectorFromElement(this);

			if (!selector) {
				return;
			}

			var target = $__default["default"](selector)[0];

			if (!target || !$__default["default"](target).hasClass(CLASS_NAME_CAROUSEL)) {
				return;
			}

			var config = _extends({}, $__default["default"](target).data(), $__default["default"](this).data());

			var slideIndex = this.getAttribute('data-slide-to');

			if (slideIndex) {
				config.interval = false;
			}

			Carousel._jQueryInterface.call($__default["default"](target), config);

			if (slideIndex) {
				$__default["default"](target).data(DATA_KEY$8).to(slideIndex);
			}

			event.preventDefault();
		};

		_createClass(Carousel, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$8;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$7;
			}
		}]);

		return Carousel;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API$4, SELECTOR_DATA_SLIDE, Carousel._dataApiClickHandler);
	$__default["default"](window).on(EVENT_LOAD_DATA_API$1, function () {
		var carousels = [].slice.call(document.querySelectorAll(SELECTOR_DATA_RIDE));

		for (var i = 0, len = carousels.length; i < len; i++) {
			var $carousel = $__default["default"](carousels[i]);

			Carousel._jQueryInterface.call($carousel, $carousel.data());
		}
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$8] = Carousel._jQueryInterface;
	$__default["default"].fn[NAME$8].Constructor = Carousel;

	$__default["default"].fn[NAME$8].noConflict = function () {
		$__default["default"].fn[NAME$8] = JQUERY_NO_CONFLICT$8;
		return Carousel._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$7 = 'collapse';
	var VERSION$7 = '4.6.2';
	var DATA_KEY$7 = 'bs.collapse';
	var EVENT_KEY$7 = "." + DATA_KEY$7;
	var DATA_API_KEY$4 = '.data-api';
	var JQUERY_NO_CONFLICT$7 = $__default["default"].fn[NAME$7];
	var CLASS_NAME_SHOW$6 = 'show';
	var CLASS_NAME_COLLAPSE = 'collapse';
	var CLASS_NAME_COLLAPSING = 'collapsing';
	var CLASS_NAME_COLLAPSED = 'collapsed';
	var DIMENSION_WIDTH = 'width';
	var DIMENSION_HEIGHT = 'height';
	var EVENT_SHOW$4 = "show" + EVENT_KEY$7;
	var EVENT_SHOWN$4 = "shown" + EVENT_KEY$7;
	var EVENT_HIDE$4 = "hide" + EVENT_KEY$7;
	var EVENT_HIDDEN$4 = "hidden" + EVENT_KEY$7;
	var EVENT_CLICK_DATA_API$3 = "click" + EVENT_KEY$7 + DATA_API_KEY$4;
	var SELECTOR_ACTIVES = '.show, .collapsing';
	var SELECTOR_DATA_TOGGLE$3 = '[data-toggle="collapse"]';
	var Default$6 = {
		toggle: true,
		parent: ''
	};
	var DefaultType$6 = {
		toggle: 'boolean',
		parent: '(string|element)'
	};
	/**
	 * Class definition
	 */

	var Collapse = /*#__PURE__*/function () {
		function Collapse(element, config) {
			this._isTransitioning = false;
			this._element = element;
			this._config = this._getConfig(config);
			this._triggerArray = [].slice.call(document.querySelectorAll("[data-toggle=\"collapse\"][href=\"#" + element.id + "\"]," + ("[data-toggle=\"collapse\"][data-target=\"#" + element.id + "\"]")));
			var toggleList = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLE$3));

			for (var i = 0, len = toggleList.length; i < len; i++) {
				var elem = toggleList[i];
				var selector = Util.getSelectorFromElement(elem);
				var filterElement = [].slice.call(document.querySelectorAll(selector)).filter(function (foundElem) {
					return foundElem === element;
				});

				if (selector !== null && filterElement.length > 0) {
					this._selector = selector;

					this._triggerArray.push(elem);
				}
			}

			this._parent = this._config.parent ? this._getParent() : null;

			if (!this._config.parent) {
				this._addAriaAndCollapsedClass(this._element, this._triggerArray);
			}

			if (this._config.toggle) {
				this.toggle();
			}
		} // Getters


		var _proto = Collapse.prototype;

		// Public
		_proto.toggle = function toggle() {
			if ($__default["default"](this._element).hasClass(CLASS_NAME_SHOW$6)) {
				this.hide();
			} else {
				this.show();
			}
		};

		_proto.show = function show() {
			var _this = this;

			if (this._isTransitioning || $__default["default"](this._element).hasClass(CLASS_NAME_SHOW$6)) {
				return;
			}

			var actives;
			var activesData;

			if (this._parent) {
				actives = [].slice.call(this._parent.querySelectorAll(SELECTOR_ACTIVES)).filter(function (elem) {
					if (typeof _this._config.parent === 'string') {
						return elem.getAttribute('data-parent') === _this._config.parent;
					}

					return elem.classList.contains(CLASS_NAME_COLLAPSE);
				});

				if (actives.length === 0) {
					actives = null;
				}
			}

			if (actives) {
				activesData = $__default["default"](actives).not(this._selector).data(DATA_KEY$7);

				if (activesData && activesData._isTransitioning) {
					return;
				}
			}

			var startEvent = $__default["default"].Event(EVENT_SHOW$4);
			$__default["default"](this._element).trigger(startEvent);

			if (startEvent.isDefaultPrevented()) {
				return;
			}

			if (actives) {
				Collapse._jQueryInterface.call($__default["default"](actives).not(this._selector), 'hide');

				if (!activesData) {
					$__default["default"](actives).data(DATA_KEY$7, null);
				}
			}

			var dimension = this._getDimension();

			$__default["default"](this._element).removeClass(CLASS_NAME_COLLAPSE).addClass(CLASS_NAME_COLLAPSING);
			this._element.style[dimension] = 0;

			if (this._triggerArray.length) {
				$__default["default"](this._triggerArray).removeClass(CLASS_NAME_COLLAPSED).attr('aria-expanded', true);
			}

			this.setTransitioning(true);

			var complete = function complete() {
				$__default["default"](_this._element).removeClass(CLASS_NAME_COLLAPSING).addClass(CLASS_NAME_COLLAPSE + " " + CLASS_NAME_SHOW$6);
				_this._element.style[dimension] = '';

				_this.setTransitioning(false);

				$__default["default"](_this._element).trigger(EVENT_SHOWN$4);
			};

			var capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
			var scrollSize = "scroll" + capitalizedDimension;
			var transitionDuration = Util.getTransitionDurationFromElement(this._element);
			$__default["default"](this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
			this._element.style[dimension] = this._element[scrollSize] + "px";
		};

		_proto.hide = function hide() {
			var _this2 = this;

			if (this._isTransitioning || !$__default["default"](this._element).hasClass(CLASS_NAME_SHOW$6)) {
				return;
			}

			var startEvent = $__default["default"].Event(EVENT_HIDE$4);
			$__default["default"](this._element).trigger(startEvent);

			if (startEvent.isDefaultPrevented()) {
				return;
			}

			var dimension = this._getDimension();

			this._element.style[dimension] = this._element.getBoundingClientRect()[dimension] + "px";
			Util.reflow(this._element);
			$__default["default"](this._element).addClass(CLASS_NAME_COLLAPSING).removeClass(CLASS_NAME_COLLAPSE + " " + CLASS_NAME_SHOW$6);
			var triggerArrayLength = this._triggerArray.length;

			if (triggerArrayLength > 0) {
				for (var i = 0; i < triggerArrayLength; i++) {
					var trigger = this._triggerArray[i];
					var selector = Util.getSelectorFromElement(trigger);

					if (selector !== null) {
						var $elem = $__default["default"]([].slice.call(document.querySelectorAll(selector)));

						if (!$elem.hasClass(CLASS_NAME_SHOW$6)) {
							$__default["default"](trigger).addClass(CLASS_NAME_COLLAPSED).attr('aria-expanded', false);
						}
					}
				}
			}

			this.setTransitioning(true);

			var complete = function complete() {
				_this2.setTransitioning(false);

				$__default["default"](_this2._element).removeClass(CLASS_NAME_COLLAPSING).addClass(CLASS_NAME_COLLAPSE).trigger(EVENT_HIDDEN$4);
			};

			this._element.style[dimension] = '';
			var transitionDuration = Util.getTransitionDurationFromElement(this._element);
			$__default["default"](this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
		};

		_proto.setTransitioning = function setTransitioning(isTransitioning) {
			this._isTransitioning = isTransitioning;
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$7);
			this._config = null;
			this._parent = null;
			this._element = null;
			this._triggerArray = null;
			this._isTransitioning = null;
		} // Private
			;

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, Default$6, config);
			config.toggle = Boolean(config.toggle); // Coerce string values

			Util.typeCheckConfig(NAME$7, config, DefaultType$6);
			return config;
		};

		_proto._getDimension = function _getDimension() {
			var hasWidth = $__default["default"](this._element).hasClass(DIMENSION_WIDTH);
			return hasWidth ? DIMENSION_WIDTH : DIMENSION_HEIGHT;
		};

		_proto._getParent = function _getParent() {
			var _this3 = this;

			var parent;

			if (Util.isElement(this._config.parent)) {
				parent = this._config.parent; // It's a jQuery object

				if (typeof this._config.parent.jquery !== 'undefined') {
					parent = this._config.parent[0];
				}
			} else {
				parent = document.querySelector(this._config.parent);
			}

			var selector = "[data-toggle=\"collapse\"][data-parent=\"" + this._config.parent + "\"]";
			var children = [].slice.call(parent.querySelectorAll(selector));
			$__default["default"](children).each(function (i, element) {
				_this3._addAriaAndCollapsedClass(Collapse._getTargetFromElement(element), [element]);
			});
			return parent;
		};

		_proto._addAriaAndCollapsedClass = function _addAriaAndCollapsedClass(element, triggerArray) {
			var isOpen = $__default["default"](element).hasClass(CLASS_NAME_SHOW$6);

			if (triggerArray.length) {
				$__default["default"](triggerArray).toggleClass(CLASS_NAME_COLLAPSED, !isOpen).attr('aria-expanded', isOpen);
			}
		} // Static
			;

		Collapse._getTargetFromElement = function _getTargetFromElement(element) {
			var selector = Util.getSelectorFromElement(element);
			return selector ? document.querySelector(selector) : null;
		};

		Collapse._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var $element = $__default["default"](this);
				var data = $element.data(DATA_KEY$7);

				var _config = _extends({}, Default$6, $element.data(), typeof config === 'object' && config ? config : {});

				if (!data && _config.toggle && typeof config === 'string' && /show|hide/.test(config)) {
					_config.toggle = false;
				}

				if (!data) {
					data = new Collapse(this, _config);
					$element.data(DATA_KEY$7, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		_createClass(Collapse, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$7;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$6;
			}
		}]);

		return Collapse;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API$3, SELECTOR_DATA_TOGGLE$3, function (event) {
		// preventDefault only for <a> elements (which change the URL) not inside the collapsible element
		if (event.currentTarget.tagName === 'A') {
			event.preventDefault();
		}

		var $trigger = $__default["default"](this);
		var selector = Util.getSelectorFromElement(this);
		var selectors = [].slice.call(document.querySelectorAll(selector));
		$__default["default"](selectors).each(function () {
			var $target = $__default["default"](this);
			var data = $target.data(DATA_KEY$7);
			var config = data ? 'toggle' : $trigger.data();

			Collapse._jQueryInterface.call($target, config);
		});
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$7] = Collapse._jQueryInterface;
	$__default["default"].fn[NAME$7].Constructor = Collapse;

	$__default["default"].fn[NAME$7].noConflict = function () {
		$__default["default"].fn[NAME$7] = JQUERY_NO_CONFLICT$7;
		return Collapse._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$6 = 'dropdown';
	var VERSION$6 = '4.6.2';
	var DATA_KEY$6 = 'bs.dropdown';
	var EVENT_KEY$6 = "." + DATA_KEY$6;
	var DATA_API_KEY$3 = '.data-api';
	var JQUERY_NO_CONFLICT$6 = $__default["default"].fn[NAME$6];
	var ESCAPE_KEYCODE$1 = 27; // KeyboardEvent.which value for Escape (Esc) key

	var SPACE_KEYCODE = 32; // KeyboardEvent.which value for space key

	var TAB_KEYCODE = 9; // KeyboardEvent.which value for tab key

	var ARROW_UP_KEYCODE = 38; // KeyboardEvent.which value for up arrow key

	var ARROW_DOWN_KEYCODE = 40; // KeyboardEvent.which value for down arrow key

	var RIGHT_MOUSE_BUTTON_WHICH = 3; // MouseEvent.which value for the right button (assuming a right-handed mouse)

	var REGEXP_KEYDOWN = new RegExp(ARROW_UP_KEYCODE + "|" + ARROW_DOWN_KEYCODE + "|" + ESCAPE_KEYCODE$1);
	var CLASS_NAME_DISABLED$1 = 'disabled';
	var CLASS_NAME_SHOW$5 = 'show';
	var CLASS_NAME_DROPUP = 'dropup';
	var CLASS_NAME_DROPRIGHT = 'dropright';
	var CLASS_NAME_DROPLEFT = 'dropleft';
	var CLASS_NAME_MENURIGHT = 'dropdown-menu-right';
	var CLASS_NAME_POSITION_STATIC = 'position-static';
	var EVENT_HIDE$3 = "hide" + EVENT_KEY$6;
	var EVENT_HIDDEN$3 = "hidden" + EVENT_KEY$6;
	var EVENT_SHOW$3 = "show" + EVENT_KEY$6;
	var EVENT_SHOWN$3 = "shown" + EVENT_KEY$6;
	var EVENT_CLICK = "click" + EVENT_KEY$6;
	var EVENT_CLICK_DATA_API$2 = "click" + EVENT_KEY$6 + DATA_API_KEY$3;
	var EVENT_KEYDOWN_DATA_API = "keydown" + EVENT_KEY$6 + DATA_API_KEY$3;
	var EVENT_KEYUP_DATA_API = "keyup" + EVENT_KEY$6 + DATA_API_KEY$3;
	var SELECTOR_DATA_TOGGLE$2 = '[data-toggle="dropdown"]';
	var SELECTOR_FORM_CHILD = '.dropdown form';
	var SELECTOR_MENU = '.dropdown-menu';
	var SELECTOR_NAVBAR_NAV = '.navbar-nav';
	var SELECTOR_VISIBLE_ITEMS = '.dropdown-menu .dropdown-item:not(.disabled):not(:disabled)';
	var PLACEMENT_TOP = 'top-start';
	var PLACEMENT_TOPEND = 'top-end';
	var PLACEMENT_BOTTOM = 'bottom-start';
	var PLACEMENT_BOTTOMEND = 'bottom-end';
	var PLACEMENT_RIGHT = 'right-start';
	var PLACEMENT_LEFT = 'left-start';
	var Default$5 = {
		offset: 0,
		flip: true,
		boundary: 'scrollParent',
		reference: 'toggle',
		display: 'dynamic',
		popperConfig: null
	};
	var DefaultType$5 = {
		offset: '(number|string|function)',
		flip: 'boolean',
		boundary: '(string|element)',
		reference: '(string|element)',
		display: 'string',
		popperConfig: '(null|object)'
	};
	/**
	 * Class definition
	 */

	var Dropdown = /*#__PURE__*/function () {
		function Dropdown(element, config) {
			this._element = element;
			this._popper = null;
			this._config = this._getConfig(config);
			this._menu = this._getMenuElement();
			this._inNavbar = this._detectNavbar();

			this._addEventListeners();
		} // Getters


		var _proto = Dropdown.prototype;

		// Public
		_proto.toggle = function toggle() {
			if (this._element.disabled || $__default["default"](this._element).hasClass(CLASS_NAME_DISABLED$1)) {
				return;
			}

			var isActive = $__default["default"](this._menu).hasClass(CLASS_NAME_SHOW$5);

			Dropdown._clearMenus();

			if (isActive) {
				return;
			}

			this.show(true);
		};

		_proto.show = function show(usePopper) {
			if (usePopper === void 0) {
				usePopper = false;
			}

			if (this._element.disabled || $__default["default"](this._element).hasClass(CLASS_NAME_DISABLED$1) || $__default["default"](this._menu).hasClass(CLASS_NAME_SHOW$5)) {
				return;
			}

			var relatedTarget = {
				relatedTarget: this._element
			};
			var showEvent = $__default["default"].Event(EVENT_SHOW$3, relatedTarget);

			var parent = Dropdown._getParentFromElement(this._element);

			$__default["default"](parent).trigger(showEvent);

			if (showEvent.isDefaultPrevented()) {
				return;
			} // Totally disable Popper for Dropdowns in Navbar


			if (!this._inNavbar && usePopper) {
				// Check for Popper dependency
				if (typeof Popper__default["default"] === 'undefined') {
					throw new TypeError('Bootstrap\'s dropdowns require Popper (https://popper.js.org)');
				}

				var referenceElement = this._element;

				if (this._config.reference === 'parent') {
					referenceElement = parent;
				} else if (Util.isElement(this._config.reference)) {
					referenceElement = this._config.reference; // Check if it's jQuery element

					if (typeof this._config.reference.jquery !== 'undefined') {
						referenceElement = this._config.reference[0];
					}
				} // If boundary is not `scrollParent`, then set position to `static`
				// to allow the menu to "escape" the scroll parent's boundaries
				// https://github.com/twbs/bootstrap/issues/24251


				if (this._config.boundary !== 'scrollParent') {
					$__default["default"](parent).addClass(CLASS_NAME_POSITION_STATIC);
				}

				this._popper = new Popper__default["default"](referenceElement, this._menu, this._getPopperConfig());
			} // If this is a touch-enabled device we add extra
			// empty mouseover listeners to the body's immediate children;
			// only needed because of broken event delegation on iOS
			// https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html


			if ('ontouchstart' in document.documentElement && $__default["default"](parent).closest(SELECTOR_NAVBAR_NAV).length === 0) {
				$__default["default"](document.body).children().on('mouseover', null, $__default["default"].noop);
			}

			this._element.focus();

			this._element.setAttribute('aria-expanded', true);

			$__default["default"](this._menu).toggleClass(CLASS_NAME_SHOW$5);
			$__default["default"](parent).toggleClass(CLASS_NAME_SHOW$5).trigger($__default["default"].Event(EVENT_SHOWN$3, relatedTarget));
		};

		_proto.hide = function hide() {
			if (this._element.disabled || $__default["default"](this._element).hasClass(CLASS_NAME_DISABLED$1) || !$__default["default"](this._menu).hasClass(CLASS_NAME_SHOW$5)) {
				return;
			}

			var relatedTarget = {
				relatedTarget: this._element
			};
			var hideEvent = $__default["default"].Event(EVENT_HIDE$3, relatedTarget);

			var parent = Dropdown._getParentFromElement(this._element);

			$__default["default"](parent).trigger(hideEvent);

			if (hideEvent.isDefaultPrevented()) {
				return;
			}

			if (this._popper) {
				this._popper.destroy();
			}

			$__default["default"](this._menu).toggleClass(CLASS_NAME_SHOW$5);
			$__default["default"](parent).toggleClass(CLASS_NAME_SHOW$5).trigger($__default["default"].Event(EVENT_HIDDEN$3, relatedTarget));
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$6);
			$__default["default"](this._element).off(EVENT_KEY$6);
			this._element = null;
			this._menu = null;

			if (this._popper !== null) {
				this._popper.destroy();

				this._popper = null;
			}
		};

		_proto.update = function update() {
			this._inNavbar = this._detectNavbar();

			if (this._popper !== null) {
				this._popper.scheduleUpdate();
			}
		} // Private
			;

		_proto._addEventListeners = function _addEventListeners() {
			var _this = this;

			$__default["default"](this._element).on(EVENT_CLICK, function (event) {
				event.preventDefault();
				event.stopPropagation();

				_this.toggle();
			});
		};

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, this.constructor.Default, $__default["default"](this._element).data(), config);
			Util.typeCheckConfig(NAME$6, config, this.constructor.DefaultType);
			return config;
		};

		_proto._getMenuElement = function _getMenuElement() {
			if (!this._menu) {
				var parent = Dropdown._getParentFromElement(this._element);

				if (parent) {
					this._menu = parent.querySelector(SELECTOR_MENU);
				}
			}

			return this._menu;
		};

		_proto._getPlacement = function _getPlacement() {
			var $parentDropdown = $__default["default"](this._element.parentNode);
			var placement = PLACEMENT_BOTTOM; // Handle dropup

			if ($parentDropdown.hasClass(CLASS_NAME_DROPUP)) {
				placement = $__default["default"](this._menu).hasClass(CLASS_NAME_MENURIGHT) ? PLACEMENT_TOPEND : PLACEMENT_TOP;
			} else if ($parentDropdown.hasClass(CLASS_NAME_DROPRIGHT)) {
				placement = PLACEMENT_RIGHT;
			} else if ($parentDropdown.hasClass(CLASS_NAME_DROPLEFT)) {
				placement = PLACEMENT_LEFT;
			} else if ($__default["default"](this._menu).hasClass(CLASS_NAME_MENURIGHT)) {
				placement = PLACEMENT_BOTTOMEND;
			}

			return placement;
		};

		_proto._detectNavbar = function _detectNavbar() {
			return $__default["default"](this._element).closest('.navbar').length > 0;
		};

		_proto._getOffset = function _getOffset() {
			var _this2 = this;

			var offset = {};

			if (typeof this._config.offset === 'function') {
				offset.fn = function (data) {
					data.offsets = _extends({}, data.offsets, _this2._config.offset(data.offsets, _this2._element));
					return data;
				};
			} else {
				offset.offset = this._config.offset;
			}

			return offset;
		};

		_proto._getPopperConfig = function _getPopperConfig() {
			var popperConfig = {
				placement: this._getPlacement(),
				modifiers: {
					offset: this._getOffset(),
					flip: {
						enabled: this._config.flip
					},
					preventOverflow: {
						boundariesElement: this._config.boundary
					}
				}
			}; // Disable Popper if we have a static display

			if (this._config.display === 'static') {
				popperConfig.modifiers.applyStyle = {
					enabled: false
				};
			}

			return _extends({}, popperConfig, this._config.popperConfig);
		} // Static
			;

		Dropdown._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var data = $__default["default"](this).data(DATA_KEY$6);

				var _config = typeof config === 'object' ? config : null;

				if (!data) {
					data = new Dropdown(this, _config);
					$__default["default"](this).data(DATA_KEY$6, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		Dropdown._clearMenus = function _clearMenus(event) {
			if (event && (event.which === RIGHT_MOUSE_BUTTON_WHICH || event.type === 'keyup' && event.which !== TAB_KEYCODE)) {
				return;
			}

			var toggles = [].slice.call(document.querySelectorAll(SELECTOR_DATA_TOGGLE$2));

			for (var i = 0, len = toggles.length; i < len; i++) {
				var parent = Dropdown._getParentFromElement(toggles[i]);

				var context = $__default["default"](toggles[i]).data(DATA_KEY$6);
				var relatedTarget = {
					relatedTarget: toggles[i]
				};

				if (event && event.type === 'click') {
					relatedTarget.clickEvent = event;
				}

				if (!context) {
					continue;
				}

				var dropdownMenu = context._menu;

				if (!$__default["default"](parent).hasClass(CLASS_NAME_SHOW$5)) {
					continue;
				}

				if (event && (event.type === 'click' && /input|textarea/i.test(event.target.tagName) || event.type === 'keyup' && event.which === TAB_KEYCODE) && $__default["default"].contains(parent, event.target)) {
					continue;
				}

				var hideEvent = $__default["default"].Event(EVENT_HIDE$3, relatedTarget);
				$__default["default"](parent).trigger(hideEvent);

				if (hideEvent.isDefaultPrevented()) {
					continue;
				} // If this is a touch-enabled device we remove the extra
				// empty mouseover listeners we added for iOS support


				if ('ontouchstart' in document.documentElement) {
					$__default["default"](document.body).children().off('mouseover', null, $__default["default"].noop);
				}

				toggles[i].setAttribute('aria-expanded', 'false');

				if (context._popper) {
					context._popper.destroy();
				}

				$__default["default"](dropdownMenu).removeClass(CLASS_NAME_SHOW$5);
				$__default["default"](parent).removeClass(CLASS_NAME_SHOW$5).trigger($__default["default"].Event(EVENT_HIDDEN$3, relatedTarget));
			}
		};

		Dropdown._getParentFromElement = function _getParentFromElement(element) {
			var parent;
			var selector = Util.getSelectorFromElement(element);

			if (selector) {
				parent = document.querySelector(selector);
			}

			return parent || element.parentNode;
		} // eslint-disable-next-line complexity
			;

		Dropdown._dataApiKeydownHandler = function _dataApiKeydownHandler(event) {
			// If not input/textarea:
			//  - And not a key in REGEXP_KEYDOWN => not a dropdown command
			// If input/textarea:
			//  - If space key => not a dropdown command
			//  - If key is other than escape
			//    - If key is not up or down => not a dropdown command
			//    - If trigger inside the menu => not a dropdown command
			if (/input|textarea/i.test(event.target.tagName) ? event.which === SPACE_KEYCODE || event.which !== ESCAPE_KEYCODE$1 && (event.which !== ARROW_DOWN_KEYCODE && event.which !== ARROW_UP_KEYCODE || $__default["default"](event.target).closest(SELECTOR_MENU).length) : !REGEXP_KEYDOWN.test(event.which)) {
				return;
			}

			if (this.disabled || $__default["default"](this).hasClass(CLASS_NAME_DISABLED$1)) {
				return;
			}

			var parent = Dropdown._getParentFromElement(this);

			var isActive = $__default["default"](parent).hasClass(CLASS_NAME_SHOW$5);

			if (!isActive && event.which === ESCAPE_KEYCODE$1) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			if (!isActive || event.which === ESCAPE_KEYCODE$1 || event.which === SPACE_KEYCODE) {
				if (event.which === ESCAPE_KEYCODE$1) {
					$__default["default"](parent.querySelector(SELECTOR_DATA_TOGGLE$2)).trigger('focus');
				}

				$__default["default"](this).trigger('click');
				return;
			}

			var items = [].slice.call(parent.querySelectorAll(SELECTOR_VISIBLE_ITEMS)).filter(function (item) {
				return $__default["default"](item).is(':visible');
			});

			if (items.length === 0) {
				return;
			}

			var index = items.indexOf(event.target);

			if (event.which === ARROW_UP_KEYCODE && index > 0) {
				// Up
				index--;
			}

			if (event.which === ARROW_DOWN_KEYCODE && index < items.length - 1) {
				// Down
				index++;
			}

			if (index < 0) {
				index = 0;
			}

			items[index].focus();
		};

		_createClass(Dropdown, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$6;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$5;
			}
		}, {
			key: "DefaultType",
			get: function get() {
				return DefaultType$5;
			}
		}]);

		return Dropdown;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_KEYDOWN_DATA_API, SELECTOR_DATA_TOGGLE$2, Dropdown._dataApiKeydownHandler).on(EVENT_KEYDOWN_DATA_API, SELECTOR_MENU, Dropdown._dataApiKeydownHandler).on(EVENT_CLICK_DATA_API$2 + " " + EVENT_KEYUP_DATA_API, Dropdown._clearMenus).on(EVENT_CLICK_DATA_API$2, SELECTOR_DATA_TOGGLE$2, function (event) {
		event.preventDefault();
		event.stopPropagation();

		Dropdown._jQueryInterface.call($__default["default"](this), 'toggle');
	}).on(EVENT_CLICK_DATA_API$2, SELECTOR_FORM_CHILD, function (e) {
		e.stopPropagation();
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$6] = Dropdown._jQueryInterface;
	$__default["default"].fn[NAME$6].Constructor = Dropdown;

	$__default["default"].fn[NAME$6].noConflict = function () {
		$__default["default"].fn[NAME$6] = JQUERY_NO_CONFLICT$6;
		return Dropdown._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$5 = 'modal';
	var VERSION$5 = '4.6.2';
	var DATA_KEY$5 = 'bs.modal';
	var EVENT_KEY$5 = "." + DATA_KEY$5;
	var DATA_API_KEY$2 = '.data-api';
	var JQUERY_NO_CONFLICT$5 = $__default["default"].fn[NAME$5];
	var ESCAPE_KEYCODE = 27; // KeyboardEvent.which value for Escape (Esc) key

	var CLASS_NAME_SCROLLABLE = 'modal-dialog-scrollable';
	var CLASS_NAME_SCROLLBAR_MEASURER = 'modal-scrollbar-measure';
	var CLASS_NAME_BACKDROP = 'modal-backdrop';
	var CLASS_NAME_OPEN = 'modal-open';
	var CLASS_NAME_FADE$4 = 'fade';
	var CLASS_NAME_SHOW$4 = 'show';
	var CLASS_NAME_STATIC = 'modal-static';
	var EVENT_HIDE$2 = "hide" + EVENT_KEY$5;
	var EVENT_HIDE_PREVENTED = "hidePrevented" + EVENT_KEY$5;
	var EVENT_HIDDEN$2 = "hidden" + EVENT_KEY$5;
	var EVENT_SHOW$2 = "show" + EVENT_KEY$5;
	var EVENT_SHOWN$2 = "shown" + EVENT_KEY$5;
	var EVENT_FOCUSIN = "focusin" + EVENT_KEY$5;
	var EVENT_RESIZE = "resize" + EVENT_KEY$5;
	var EVENT_CLICK_DISMISS$1 = "click.dismiss" + EVENT_KEY$5;
	var EVENT_KEYDOWN_DISMISS = "keydown.dismiss" + EVENT_KEY$5;
	var EVENT_MOUSEUP_DISMISS = "mouseup.dismiss" + EVENT_KEY$5;
	var EVENT_MOUSEDOWN_DISMISS = "mousedown.dismiss" + EVENT_KEY$5;
	var EVENT_CLICK_DATA_API$1 = "click" + EVENT_KEY$5 + DATA_API_KEY$2;
	var SELECTOR_DIALOG = '.modal-dialog';
	var SELECTOR_MODAL_BODY = '.modal-body';
	var SELECTOR_DATA_TOGGLE$1 = '[data-toggle="modal"]';
	var SELECTOR_DATA_DISMISS$1 = '[data-dismiss="modal"]';
	var SELECTOR_FIXED_CONTENT = '.fixed-top, .fixed-bottom, .is-fixed, .sticky-top';
	var SELECTOR_STICKY_CONTENT = '.sticky-top';
	var Default$4 = {
		backdrop: true,
		keyboard: true,
		focus: true,
		show: true
	};
	var DefaultType$4 = {
		backdrop: '(boolean|string)',
		keyboard: 'boolean',
		focus: 'boolean',
		show: 'boolean'
	};
	/**
	 * Class definition
	 */

	var Modal = /*#__PURE__*/function () {
		function Modal(element, config) {
			this._config = this._getConfig(config);
			this._element = element;
			this._dialog = element.querySelector(SELECTOR_DIALOG);
			this._backdrop = null;
			this._isShown = false;
			this._isBodyOverflowing = false;
			this._ignoreBackdropClick = false;
			this._isTransitioning = false;
			this._scrollbarWidth = 0;
		} // Getters


		var _proto = Modal.prototype;

		// Public
		_proto.toggle = function toggle(relatedTarget) {
			return this._isShown ? this.hide() : this.show(relatedTarget);
		};

		_proto.show = function show(relatedTarget) {
			var _this = this;

			if (this._isShown || this._isTransitioning) {
				return;
			}

			var showEvent = $__default["default"].Event(EVENT_SHOW$2, {
				relatedTarget: relatedTarget
			});
			$__default["default"](this._element).trigger(showEvent);

			if (showEvent.isDefaultPrevented()) {
				return;
			}

			this._isShown = true;

			if ($__default["default"](this._element).hasClass(CLASS_NAME_FADE$4)) {
				this._isTransitioning = true;
			}

			this._checkScrollbar();

			this._setScrollbar();

			this._adjustDialog();

			this._setEscapeEvent();

			this._setResizeEvent();

			$__default["default"](this._element).on(EVENT_CLICK_DISMISS$1, SELECTOR_DATA_DISMISS$1, function (event) {
				return _this.hide(event);
			});
			$__default["default"](this._dialog).on(EVENT_MOUSEDOWN_DISMISS, function () {
				$__default["default"](_this._element).one(EVENT_MOUSEUP_DISMISS, function (event) {
					if ($__default["default"](event.target).is(_this._element)) {
						_this._ignoreBackdropClick = true;
					}
				});
			});

			this._showBackdrop(function () {
				return _this._showElement(relatedTarget);
			});
		};

		_proto.hide = function hide(event) {
			var _this2 = this;

			if (event) {
				event.preventDefault();
			}

			if (!this._isShown || this._isTransitioning) {
				return;
			}

			var hideEvent = $__default["default"].Event(EVENT_HIDE$2);
			$__default["default"](this._element).trigger(hideEvent);

			if (!this._isShown || hideEvent.isDefaultPrevented()) {
				return;
			}

			this._isShown = false;
			var transition = $__default["default"](this._element).hasClass(CLASS_NAME_FADE$4);

			if (transition) {
				this._isTransitioning = true;
			}

			this._setEscapeEvent();

			this._setResizeEvent();

			$__default["default"](document).off(EVENT_FOCUSIN);
			$__default["default"](this._element).removeClass(CLASS_NAME_SHOW$4);
			$__default["default"](this._element).off(EVENT_CLICK_DISMISS$1);
			$__default["default"](this._dialog).off(EVENT_MOUSEDOWN_DISMISS);

			if (transition) {
				var transitionDuration = Util.getTransitionDurationFromElement(this._element);
				$__default["default"](this._element).one(Util.TRANSITION_END, function (event) {
					return _this2._hideModal(event);
				}).emulateTransitionEnd(transitionDuration);
			} else {
				this._hideModal();
			}
		};

		_proto.dispose = function dispose() {
			[window, this._element, this._dialog].forEach(function (htmlElement) {
				return $__default["default"](htmlElement).off(EVENT_KEY$5);
			});
			/**
			 * `document` has 2 events `EVENT_FOCUSIN` and `EVENT_CLICK_DATA_API`
			 * Do not move `document` in `htmlElements` array
			 * It will remove `EVENT_CLICK_DATA_API` event that should remain
			 */

			$__default["default"](document).off(EVENT_FOCUSIN);
			$__default["default"].removeData(this._element, DATA_KEY$5);
			this._config = null;
			this._element = null;
			this._dialog = null;
			this._backdrop = null;
			this._isShown = null;
			this._isBodyOverflowing = null;
			this._ignoreBackdropClick = null;
			this._isTransitioning = null;
			this._scrollbarWidth = null;
		};

		_proto.handleUpdate = function handleUpdate() {
			this._adjustDialog();
		} // Private
			;

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, Default$4, config);
			Util.typeCheckConfig(NAME$5, config, DefaultType$4);
			return config;
		};

		_proto._triggerBackdropTransition = function _triggerBackdropTransition() {
			var _this3 = this;

			var hideEventPrevented = $__default["default"].Event(EVENT_HIDE_PREVENTED);
			$__default["default"](this._element).trigger(hideEventPrevented);

			if (hideEventPrevented.isDefaultPrevented()) {
				return;
			}

			var isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;

			if (!isModalOverflowing) {
				this._element.style.overflowY = 'hidden';
			}

			this._element.classList.add(CLASS_NAME_STATIC);

			var modalTransitionDuration = Util.getTransitionDurationFromElement(this._dialog);
			$__default["default"](this._element).off(Util.TRANSITION_END);
			$__default["default"](this._element).one(Util.TRANSITION_END, function () {
				_this3._element.classList.remove(CLASS_NAME_STATIC);

				if (!isModalOverflowing) {
					$__default["default"](_this3._element).one(Util.TRANSITION_END, function () {
						_this3._element.style.overflowY = '';
					}).emulateTransitionEnd(_this3._element, modalTransitionDuration);
				}
			}).emulateTransitionEnd(modalTransitionDuration);

			this._element.focus();
		};

		_proto._showElement = function _showElement(relatedTarget) {
			var _this4 = this;

			var transition = $__default["default"](this._element).hasClass(CLASS_NAME_FADE$4);
			var modalBody = this._dialog ? this._dialog.querySelector(SELECTOR_MODAL_BODY) : null;

			if (!this._element.parentNode || this._element.parentNode.nodeType !== Node.ELEMENT_NODE) {
				// Don't move modal's DOM position
				document.body.appendChild(this._element);
			}

			this._element.style.display = 'block';

			this._element.removeAttribute('aria-hidden');

			this._element.setAttribute('aria-modal', true);

			this._element.setAttribute('role', 'dialog');

			if ($__default["default"](this._dialog).hasClass(CLASS_NAME_SCROLLABLE) && modalBody) {
				modalBody.scrollTop = 0;
			} else {
				this._element.scrollTop = 0;
			}

			if (transition) {
				Util.reflow(this._element);
			}

			$__default["default"](this._element).addClass(CLASS_NAME_SHOW$4);

			if (this._config.focus) {
				this._enforceFocus();
			}

			var shownEvent = $__default["default"].Event(EVENT_SHOWN$2, {
				relatedTarget: relatedTarget
			});

			var transitionComplete = function transitionComplete() {
				if (_this4._config.focus) {
					_this4._element.focus();
				}

				_this4._isTransitioning = false;
				$__default["default"](_this4._element).trigger(shownEvent);
			};

			if (transition) {
				var transitionDuration = Util.getTransitionDurationFromElement(this._dialog);
				$__default["default"](this._dialog).one(Util.TRANSITION_END, transitionComplete).emulateTransitionEnd(transitionDuration);
			} else {
				transitionComplete();
			}
		};

		_proto._enforceFocus = function _enforceFocus() {
			var _this5 = this;

			$__default["default"](document).off(EVENT_FOCUSIN) // Guard against infinite focus loop
				.on(EVENT_FOCUSIN, function (event) {
					if (document !== event.target && _this5._element !== event.target && $__default["default"](_this5._element).has(event.target).length === 0) {
						_this5._element.focus();
					}
				});
		};

		_proto._setEscapeEvent = function _setEscapeEvent() {
			var _this6 = this;

			if (this._isShown) {
				$__default["default"](this._element).on(EVENT_KEYDOWN_DISMISS, function (event) {
					if (_this6._config.keyboard && event.which === ESCAPE_KEYCODE) {
						event.preventDefault();

						_this6.hide();
					} else if (!_this6._config.keyboard && event.which === ESCAPE_KEYCODE) {
						_this6._triggerBackdropTransition();
					}
				});
			} else if (!this._isShown) {
				$__default["default"](this._element).off(EVENT_KEYDOWN_DISMISS);
			}
		};

		_proto._setResizeEvent = function _setResizeEvent() {
			var _this7 = this;

			if (this._isShown) {
				$__default["default"](window).on(EVENT_RESIZE, function (event) {
					return _this7.handleUpdate(event);
				});
			} else {
				$__default["default"](window).off(EVENT_RESIZE);
			}
		};

		_proto._hideModal = function _hideModal() {
			var _this8 = this;

			this._element.style.display = 'none';

			this._element.setAttribute('aria-hidden', true);

			this._element.removeAttribute('aria-modal');

			this._element.removeAttribute('role');

			this._isTransitioning = false;

			this._showBackdrop(function () {
				$__default["default"](document.body).removeClass(CLASS_NAME_OPEN);

				_this8._resetAdjustments();

				_this8._resetScrollbar();

				$__default["default"](_this8._element).trigger(EVENT_HIDDEN$2);
			});
		};

		_proto._removeBackdrop = function _removeBackdrop() {
			if (this._backdrop) {
				$__default["default"](this._backdrop).remove();
				this._backdrop = null;
			}
		};

		_proto._showBackdrop = function _showBackdrop(callback) {
			var _this9 = this;

			var animate = $__default["default"](this._element).hasClass(CLASS_NAME_FADE$4) ? CLASS_NAME_FADE$4 : '';

			if (this._isShown && this._config.backdrop) {
				this._backdrop = document.createElement('div');
				this._backdrop.className = CLASS_NAME_BACKDROP;

				if (animate) {
					this._backdrop.classList.add(animate);
				}

				$__default["default"](this._backdrop).appendTo(document.body);
				$__default["default"](this._element).on(EVENT_CLICK_DISMISS$1, function (event) {
					if (_this9._ignoreBackdropClick) {
						_this9._ignoreBackdropClick = false;
						return;
					}

					if (event.target !== event.currentTarget) {
						return;
					}

					if (_this9._config.backdrop === 'static') {
						_this9._triggerBackdropTransition();
					} else {
						_this9.hide();
					}
				});

				if (animate) {
					Util.reflow(this._backdrop);
				}

				$__default["default"](this._backdrop).addClass(CLASS_NAME_SHOW$4);

				if (!callback) {
					return;
				}

				if (!animate) {
					callback();
					return;
				}

				var backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop);
				$__default["default"](this._backdrop).one(Util.TRANSITION_END, callback).emulateTransitionEnd(backdropTransitionDuration);
			} else if (!this._isShown && this._backdrop) {
				$__default["default"](this._backdrop).removeClass(CLASS_NAME_SHOW$4);

				var callbackRemove = function callbackRemove() {
					_this9._removeBackdrop();

					if (callback) {
						callback();
					}
				};

				if ($__default["default"](this._element).hasClass(CLASS_NAME_FADE$4)) {
					var _backdropTransitionDuration = Util.getTransitionDurationFromElement(this._backdrop);

					$__default["default"](this._backdrop).one(Util.TRANSITION_END, callbackRemove).emulateTransitionEnd(_backdropTransitionDuration);
				} else {
					callbackRemove();
				}
			} else if (callback) {
				callback();
			}
		} // ----------------------------------------------------------------------
			// the following methods are used to handle overflowing modals
			// todo (fat): these should probably be refactored out of modal.js
			// ----------------------------------------------------------------------
			;

		_proto._adjustDialog = function _adjustDialog() {
			var isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;

			if (!this._isBodyOverflowing && isModalOverflowing) {
				this._element.style.paddingLeft = this._scrollbarWidth + "px";
			}

			if (this._isBodyOverflowing && !isModalOverflowing) {
				this._element.style.paddingRight = this._scrollbarWidth + "px";
			}
		};

		_proto._resetAdjustments = function _resetAdjustments() {
			this._element.style.paddingLeft = '';
			this._element.style.paddingRight = '';
		};

		_proto._checkScrollbar = function _checkScrollbar() {
			var rect = document.body.getBoundingClientRect();
			this._isBodyOverflowing = Math.round(rect.left + rect.right) < window.innerWidth;
			this._scrollbarWidth = this._getScrollbarWidth();
		};

		_proto._setScrollbar = function _setScrollbar() {
			var _this10 = this;

			if (this._isBodyOverflowing) {
				// Note: DOMNode.style.paddingRight returns the actual value or '' if not set
				//   while $(DOMNode).css('padding-right') returns the calculated value or 0 if not set
				var fixedContent = [].slice.call(document.querySelectorAll(SELECTOR_FIXED_CONTENT));
				var stickyContent = [].slice.call(document.querySelectorAll(SELECTOR_STICKY_CONTENT)); // Adjust fixed content padding

				$__default["default"](fixedContent).each(function (index, element) {
					var actualPadding = element.style.paddingRight;
					var calculatedPadding = $__default["default"](element).css('padding-right');
					$__default["default"](element).data('padding-right', actualPadding).css('padding-right', parseFloat(calculatedPadding) + _this10._scrollbarWidth + "px");
				}); // Adjust sticky content margin

				$__default["default"](stickyContent).each(function (index, element) {
					var actualMargin = element.style.marginRight;
					var calculatedMargin = $__default["default"](element).css('margin-right');
					$__default["default"](element).data('margin-right', actualMargin).css('margin-right', parseFloat(calculatedMargin) - _this10._scrollbarWidth + "px");
				}); // Adjust body padding

				var actualPadding = document.body.style.paddingRight;
				var calculatedPadding = $__default["default"](document.body).css('padding-right');
				$__default["default"](document.body).data('padding-right', actualPadding).css('padding-right', parseFloat(calculatedPadding) + this._scrollbarWidth + "px");
			}

			$__default["default"](document.body).addClass(CLASS_NAME_OPEN);
		};

		_proto._resetScrollbar = function _resetScrollbar() {
			// Restore fixed content padding
			var fixedContent = [].slice.call(document.querySelectorAll(SELECTOR_FIXED_CONTENT));
			$__default["default"](fixedContent).each(function (index, element) {
				var padding = $__default["default"](element).data('padding-right');
				$__default["default"](element).removeData('padding-right');
				element.style.paddingRight = padding ? padding : '';
			}); // Restore sticky content

			var elements = [].slice.call(document.querySelectorAll("" + SELECTOR_STICKY_CONTENT));
			$__default["default"](elements).each(function (index, element) {
				var margin = $__default["default"](element).data('margin-right');

				if (typeof margin !== 'undefined') {
					$__default["default"](element).css('margin-right', margin).removeData('margin-right');
				}
			}); // Restore body padding

			var padding = $__default["default"](document.body).data('padding-right');
			$__default["default"](document.body).removeData('padding-right');
			document.body.style.paddingRight = padding ? padding : '';
		};

		_proto._getScrollbarWidth = function _getScrollbarWidth() {
			// thx d.walsh
			var scrollDiv = document.createElement('div');
			scrollDiv.className = CLASS_NAME_SCROLLBAR_MEASURER;
			document.body.appendChild(scrollDiv);
			var scrollbarWidth = scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth;
			document.body.removeChild(scrollDiv);
			return scrollbarWidth;
		} // Static
			;

		Modal._jQueryInterface = function _jQueryInterface(config, relatedTarget) {
			return this.each(function () {
				var data = $__default["default"](this).data(DATA_KEY$5);

				var _config = _extends({}, Default$4, $__default["default"](this).data(), typeof config === 'object' && config ? config : {});

				if (!data) {
					data = new Modal(this, _config);
					$__default["default"](this).data(DATA_KEY$5, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config](relatedTarget);
				} else if (_config.show) {
					data.show(relatedTarget);
				}
			});
		};

		_createClass(Modal, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$5;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$4;
			}
		}]);

		return Modal;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API$1, SELECTOR_DATA_TOGGLE$1, function (event) {
		var _this11 = this;

		var target;
		var selector = Util.getSelectorFromElement(this);

		if (selector) {
			target = document.querySelector(selector);
		}

		var config = $__default["default"](target).data(DATA_KEY$5) ? 'toggle' : _extends({}, $__default["default"](target).data(), $__default["default"](this).data());

		if (this.tagName === 'A' || this.tagName === 'AREA') {
			event.preventDefault();
		}

		var $target = $__default["default"](target).one(EVENT_SHOW$2, function (showEvent) {
			if (showEvent.isDefaultPrevented()) {
				// Only register focus restorer if modal will actually get shown
				return;
			}

			$target.one(EVENT_HIDDEN$2, function () {
				if ($__default["default"](_this11).is(':visible')) {
					_this11.focus();
				}
			});
		});

		Modal._jQueryInterface.call($__default["default"](target), config, this);
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$5] = Modal._jQueryInterface;
	$__default["default"].fn[NAME$5].Constructor = Modal;

	$__default["default"].fn[NAME$5].noConflict = function () {
		$__default["default"].fn[NAME$5] = JQUERY_NO_CONFLICT$5;
		return Modal._jQueryInterface;
	};

	/**
	 * --------------------------------------------------------------------------
	 * Bootstrap (v4.6.2): tools/sanitizer.js
	 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
	 * --------------------------------------------------------------------------
	 */
	var uriAttrs = ['background', 'cite', 'href', 'itemtype', 'longdesc', 'poster', 'src', 'xlink:href'];
	var ARIA_ATTRIBUTE_PATTERN = /^aria-[\w-]*$/i;
	var DefaultWhitelist = {
		// Global attributes allowed on any supplied element below.
		'*': ['class', 'dir', 'id', 'lang', 'role', ARIA_ATTRIBUTE_PATTERN],
		a: ['target', 'href', 'title', 'rel'],
		area: [],
		b: [],
		br: [],
		col: [],
		code: [],
		div: [],
		em: [],
		hr: [],
		h1: [],
		h2: [],
		h3: [],
		h4: [],
		h5: [],
		h6: [],
		i: [],
		img: ['src', 'srcset', 'alt', 'title', 'width', 'height'],
		li: [],
		ol: [],
		p: [],
		pre: [],
		s: [],
		small: [],
		span: [],
		sub: [],
		sup: [],
		strong: [],
		u: [],
		ul: []
	};
	/**
	 * A pattern that recognizes a commonly useful subset of URLs that are safe.
	 *
	 * Shoutout to Angular https://github.com/angular/angular/blob/12.2.x/packages/core/src/sanitization/url_sanitizer.ts
	 */

	var SAFE_URL_PATTERN = /^(?:(?:https?|mailto|ftp|tel|file|sms):|[^#&/:?]*(?:[#/?]|$))/i;
	/**
	 * A pattern that matches safe data URLs. Only matches image, video and audio types.
	 *
	 * Shoutout to Angular https://github.com/angular/angular/blob/12.2.x/packages/core/src/sanitization/url_sanitizer.ts
	 */

	var DATA_URL_PATTERN = /^data:(?:image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp)|video\/(?:mpeg|mp4|ogg|webm)|audio\/(?:mp3|oga|ogg|opus));base64,[\d+/a-z]+=*$/i;

	function allowedAttribute(attr, allowedAttributeList) {
		var attrName = attr.nodeName.toLowerCase();

		if (allowedAttributeList.indexOf(attrName) !== -1) {
			if (uriAttrs.indexOf(attrName) !== -1) {
				return Boolean(SAFE_URL_PATTERN.test(attr.nodeValue) || DATA_URL_PATTERN.test(attr.nodeValue));
			}

			return true;
		}

		var regExp = allowedAttributeList.filter(function (attrRegex) {
			return attrRegex instanceof RegExp;
		}); // Check if a regular expression validates the attribute.

		for (var i = 0, len = regExp.length; i < len; i++) {
			if (regExp[i].test(attrName)) {
				return true;
			}
		}

		return false;
	}

	function sanitizeHtml(unsafeHtml, whiteList, sanitizeFn) {
		if (unsafeHtml.length === 0) {
			return unsafeHtml;
		}

		if (sanitizeFn && typeof sanitizeFn === 'function') {
			return sanitizeFn(unsafeHtml);
		}

		var domParser = new window.DOMParser();
		var createdDocument = domParser.parseFromString(unsafeHtml, 'text/html');
		var whitelistKeys = Object.keys(whiteList);
		var elements = [].slice.call(createdDocument.body.querySelectorAll('*'));

		var _loop = function _loop(i, len) {
			var el = elements[i];
			var elName = el.nodeName.toLowerCase();

			if (whitelistKeys.indexOf(el.nodeName.toLowerCase()) === -1) {
				el.parentNode.removeChild(el);
				return "continue";
			}

			var attributeList = [].slice.call(el.attributes); // eslint-disable-next-line unicorn/prefer-spread

			var whitelistedAttributes = [].concat(whiteList['*'] || [], whiteList[elName] || []);
			attributeList.forEach(function (attr) {
				if (!allowedAttribute(attr, whitelistedAttributes)) {
					el.removeAttribute(attr.nodeName);
				}
			});
		};

		for (var i = 0, len = elements.length; i < len; i++) {
			var _ret = _loop(i);

			if (_ret === "continue") continue;
		}

		return createdDocument.body.innerHTML;
	}

	/**
	 * Constants
	 */

	var NAME$4 = 'tooltip';
	var VERSION$4 = '4.6.2';
	var DATA_KEY$4 = 'bs.tooltip';
	var EVENT_KEY$4 = "." + DATA_KEY$4;
	var JQUERY_NO_CONFLICT$4 = $__default["default"].fn[NAME$4];
	var CLASS_PREFIX$1 = 'bs-tooltip';
	var BSCLS_PREFIX_REGEX$1 = new RegExp("(^|\\s)" + CLASS_PREFIX$1 + "\\S+", 'g');
	var DISALLOWED_ATTRIBUTES = ['sanitize', 'whiteList', 'sanitizeFn'];
	var CLASS_NAME_FADE$3 = 'fade';
	var CLASS_NAME_SHOW$3 = 'show';
	var HOVER_STATE_SHOW = 'show';
	var HOVER_STATE_OUT = 'out';
	var SELECTOR_TOOLTIP_INNER = '.tooltip-inner';
	var SELECTOR_ARROW = '.arrow';
	var TRIGGER_HOVER = 'hover';
	var TRIGGER_FOCUS = 'focus';
	var TRIGGER_CLICK = 'click';
	var TRIGGER_MANUAL = 'manual';
	var AttachmentMap = {
		AUTO: 'auto',
		TOP: 'top',
		RIGHT: 'right',
		BOTTOM: 'bottom',
		LEFT: 'left'
	};
	var Default$3 = {
		animation: true,
		template: '<div class="tooltip" role="tooltip">' + '<div class="arrow"></div>' + '<div class="tooltip-inner"></div></div>',
		trigger: 'hover focus',
		title: '',
		delay: 0,
		html: false,
		selector: false,
		placement: 'top',
		offset: 0,
		container: false,
		fallbackPlacement: 'flip',
		boundary: 'scrollParent',
		customClass: '',
		sanitize: true,
		sanitizeFn: null,
		whiteList: DefaultWhitelist,
		popperConfig: null
	};
	var DefaultType$3 = {
		animation: 'boolean',
		template: 'string',
		title: '(string|element|function)',
		trigger: 'string',
		delay: '(number|object)',
		html: 'boolean',
		selector: '(string|boolean)',
		placement: '(string|function)',
		offset: '(number|string|function)',
		container: '(string|element|boolean)',
		fallbackPlacement: '(string|array)',
		boundary: '(string|element)',
		customClass: '(string|function)',
		sanitize: 'boolean',
		sanitizeFn: '(null|function)',
		whiteList: 'object',
		popperConfig: '(null|object)'
	};
	var Event$1 = {
		HIDE: "hide" + EVENT_KEY$4,
		HIDDEN: "hidden" + EVENT_KEY$4,
		SHOW: "show" + EVENT_KEY$4,
		SHOWN: "shown" + EVENT_KEY$4,
		INSERTED: "inserted" + EVENT_KEY$4,
		CLICK: "click" + EVENT_KEY$4,
		FOCUSIN: "focusin" + EVENT_KEY$4,
		FOCUSOUT: "focusout" + EVENT_KEY$4,
		MOUSEENTER: "mouseenter" + EVENT_KEY$4,
		MOUSELEAVE: "mouseleave" + EVENT_KEY$4
	};
	/**
	 * Class definition
	 */

	var Tooltip = /*#__PURE__*/function () {
		function Tooltip(element, config) {
			if (typeof Popper__default["default"] === 'undefined') {
				throw new TypeError('Bootstrap\'s tooltips require Popper (https://popper.js.org)');
			} // Private


			this._isEnabled = true;
			this._timeout = 0;
			this._hoverState = '';
			this._activeTrigger = {};
			this._popper = null; // Protected

			this.element = element;
			this.config = this._getConfig(config);
			this.tip = null;

			this._setListeners();
		} // Getters


		var _proto = Tooltip.prototype;

		// Public
		_proto.enable = function enable() {
			this._isEnabled = true;
		};

		_proto.disable = function disable() {
			this._isEnabled = false;
		};

		_proto.toggleEnabled = function toggleEnabled() {
			this._isEnabled = !this._isEnabled;
		};

		_proto.toggle = function toggle(event) {
			if (!this._isEnabled) {
				return;
			}

			if (event) {
				var dataKey = this.constructor.DATA_KEY;
				var context = $__default["default"](event.currentTarget).data(dataKey);

				if (!context) {
					context = new this.constructor(event.currentTarget, this._getDelegateConfig());
					$__default["default"](event.currentTarget).data(dataKey, context);
				}

				context._activeTrigger.click = !context._activeTrigger.click;

				if (context._isWithActiveTrigger()) {
					context._enter(null, context);
				} else {
					context._leave(null, context);
				}
			} else {
				if ($__default["default"](this.getTipElement()).hasClass(CLASS_NAME_SHOW$3)) {
					this._leave(null, this);

					return;
				}

				this._enter(null, this);
			}
		};

		_proto.dispose = function dispose() {
			clearTimeout(this._timeout);
			$__default["default"].removeData(this.element, this.constructor.DATA_KEY);
			$__default["default"](this.element).off(this.constructor.EVENT_KEY);
			$__default["default"](this.element).closest('.modal').off('hide.bs.modal', this._hideModalHandler);

			if (this.tip) {
				$__default["default"](this.tip).remove();
			}

			this._isEnabled = null;
			this._timeout = null;
			this._hoverState = null;
			this._activeTrigger = null;

			if (this._popper) {
				this._popper.destroy();
			}

			this._popper = null;
			this.element = null;
			this.config = null;
			this.tip = null;
		};

		_proto.show = function show() {
			var _this = this;

			if ($__default["default"](this.element).css('display') === 'none') {
				throw new Error('Please use show on visible elements');
			}

			var showEvent = $__default["default"].Event(this.constructor.Event.SHOW);

			if (this.isWithContent() && this._isEnabled) {
				$__default["default"](this.element).trigger(showEvent);
				var shadowRoot = Util.findShadowRoot(this.element);
				var isInTheDom = $__default["default"].contains(shadowRoot !== null ? shadowRoot : this.element.ownerDocument.documentElement, this.element);

				if (showEvent.isDefaultPrevented() || !isInTheDom) {
					return;
				}

				var tip = this.getTipElement();
				var tipId = Util.getUID(this.constructor.NAME);
				tip.setAttribute('id', tipId);
				this.element.setAttribute('aria-describedby', tipId);
				this.setContent();

				if (this.config.animation) {
					$__default["default"](tip).addClass(CLASS_NAME_FADE$3);
				}

				var placement = typeof this.config.placement === 'function' ? this.config.placement.call(this, tip, this.element) : this.config.placement;

				var attachment = this._getAttachment(placement);

				this.addAttachmentClass(attachment);

				var container = this._getContainer();

				$__default["default"](tip).data(this.constructor.DATA_KEY, this);

				if (!$__default["default"].contains(this.element.ownerDocument.documentElement, this.tip)) {
					$__default["default"](tip).appendTo(container);
				}

				$__default["default"](this.element).trigger(this.constructor.Event.INSERTED);
				this._popper = new Popper__default["default"](this.element, tip, this._getPopperConfig(attachment));
				$__default["default"](tip).addClass(CLASS_NAME_SHOW$3);
				$__default["default"](tip).addClass(this.config.customClass); // If this is a touch-enabled device we add extra
				// empty mouseover listeners to the body's immediate children;
				// only needed because of broken event delegation on iOS
				// https://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html

				if ('ontouchstart' in document.documentElement) {
					$__default["default"](document.body).children().on('mouseover', null, $__default["default"].noop);
				}

				var complete = function complete() {
					if (_this.config.animation) {
						_this._fixTransition();
					}

					var prevHoverState = _this._hoverState;
					_this._hoverState = null;
					$__default["default"](_this.element).trigger(_this.constructor.Event.SHOWN);

					if (prevHoverState === HOVER_STATE_OUT) {
						_this._leave(null, _this);
					}
				};

				if ($__default["default"](this.tip).hasClass(CLASS_NAME_FADE$3)) {
					var transitionDuration = Util.getTransitionDurationFromElement(this.tip);
					$__default["default"](this.tip).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
				} else {
					complete();
				}
			}
		};

		_proto.hide = function hide(callback) {
			var _this2 = this;

			var tip = this.getTipElement();
			var hideEvent = $__default["default"].Event(this.constructor.Event.HIDE);

			var complete = function complete() {
				if (_this2._hoverState !== HOVER_STATE_SHOW && tip.parentNode) {
					tip.parentNode.removeChild(tip);
				}

				_this2._cleanTipClass();

				_this2.element.removeAttribute('aria-describedby');

				$__default["default"](_this2.element).trigger(_this2.constructor.Event.HIDDEN);

				if (_this2._popper !== null) {
					_this2._popper.destroy();
				}

				if (callback) {
					callback();
				}
			};

			$__default["default"](this.element).trigger(hideEvent);

			if (hideEvent.isDefaultPrevented()) {
				return;
			}

			$__default["default"](tip).removeClass(CLASS_NAME_SHOW$3); // If this is a touch-enabled device we remove the extra
			// empty mouseover listeners we added for iOS support

			if ('ontouchstart' in document.documentElement) {
				$__default["default"](document.body).children().off('mouseover', null, $__default["default"].noop);
			}

			this._activeTrigger[TRIGGER_CLICK] = false;
			this._activeTrigger[TRIGGER_FOCUS] = false;
			this._activeTrigger[TRIGGER_HOVER] = false;

			if ($__default["default"](this.tip).hasClass(CLASS_NAME_FADE$3)) {
				var transitionDuration = Util.getTransitionDurationFromElement(tip);
				$__default["default"](tip).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
			} else {
				complete();
			}

			this._hoverState = '';
		};

		_proto.update = function update() {
			if (this._popper !== null) {
				this._popper.scheduleUpdate();
			}
		} // Protected
			;

		_proto.isWithContent = function isWithContent() {
			return Boolean(this.getTitle());
		};

		_proto.addAttachmentClass = function addAttachmentClass(attachment) {
			$__default["default"](this.getTipElement()).addClass(CLASS_PREFIX$1 + "-" + attachment);
		};

		_proto.getTipElement = function getTipElement() {
			this.tip = this.tip || $__default["default"](this.config.template)[0];
			return this.tip;
		};

		_proto.setContent = function setContent() {
			var tip = this.getTipElement();
			this.setElementContent($__default["default"](tip.querySelectorAll(SELECTOR_TOOLTIP_INNER)), this.getTitle());
			$__default["default"](tip).removeClass(CLASS_NAME_FADE$3 + " " + CLASS_NAME_SHOW$3);
		};

		_proto.setElementContent = function setElementContent($element, content) {
			if (typeof content === 'object' && (content.nodeType || content.jquery)) {
				// Content is a DOM node or a jQuery
				if (this.config.html) {
					if (!$__default["default"](content).parent().is($element)) {
						$element.empty().append(content);
					}
				} else {
					$element.text($__default["default"](content).text());
				}

				return;
			}

			if (this.config.html) {
				if (this.config.sanitize) {
					content = sanitizeHtml(content, this.config.whiteList, this.config.sanitizeFn);
				}

				$element.html(content);
			} else {
				$element.text(content);
			}
		};

		_proto.getTitle = function getTitle() {
			var title = this.element.getAttribute('data-original-title');

			if (!title) {
				title = typeof this.config.title === 'function' ? this.config.title.call(this.element) : this.config.title;
			}

			return title;
		} // Private
			;

		_proto._getPopperConfig = function _getPopperConfig(attachment) {
			var _this3 = this;

			var defaultBsConfig = {
				placement: attachment,
				modifiers: {
					offset: this._getOffset(),
					flip: {
						behavior: this.config.fallbackPlacement
					},
					arrow: {
						element: SELECTOR_ARROW
					},
					preventOverflow: {
						boundariesElement: this.config.boundary
					}
				},
				onCreate: function onCreate(data) {
					if (data.originalPlacement !== data.placement) {
						_this3._handlePopperPlacementChange(data);
					}
				},
				onUpdate: function onUpdate(data) {
					return _this3._handlePopperPlacementChange(data);
				}
			};
			return _extends({}, defaultBsConfig, this.config.popperConfig);
		};

		_proto._getOffset = function _getOffset() {
			var _this4 = this;

			var offset = {};

			if (typeof this.config.offset === 'function') {
				offset.fn = function (data) {
					data.offsets = _extends({}, data.offsets, _this4.config.offset(data.offsets, _this4.element));
					return data;
				};
			} else {
				offset.offset = this.config.offset;
			}

			return offset;
		};

		_proto._getContainer = function _getContainer() {
			if (this.config.container === false) {
				return document.body;
			}

			if (Util.isElement(this.config.container)) {
				return $__default["default"](this.config.container);
			}

			return $__default["default"](document).find(this.config.container);
		};

		_proto._getAttachment = function _getAttachment(placement) {
			return AttachmentMap[placement.toUpperCase()];
		};

		_proto._setListeners = function _setListeners() {
			var _this5 = this;

			var triggers = this.config.trigger.split(' ');
			triggers.forEach(function (trigger) {
				if (trigger === 'click') {
					$__default["default"](_this5.element).on(_this5.constructor.Event.CLICK, _this5.config.selector, function (event) {
						return _this5.toggle(event);
					});
				} else if (trigger !== TRIGGER_MANUAL) {
					var eventIn = trigger === TRIGGER_HOVER ? _this5.constructor.Event.MOUSEENTER : _this5.constructor.Event.FOCUSIN;
					var eventOut = trigger === TRIGGER_HOVER ? _this5.constructor.Event.MOUSELEAVE : _this5.constructor.Event.FOCUSOUT;
					$__default["default"](_this5.element).on(eventIn, _this5.config.selector, function (event) {
						return _this5._enter(event);
					}).on(eventOut, _this5.config.selector, function (event) {
						return _this5._leave(event);
					});
				}
			});

			this._hideModalHandler = function () {
				if (_this5.element) {
					_this5.hide();
				}
			};

			$__default["default"](this.element).closest('.modal').on('hide.bs.modal', this._hideModalHandler);

			if (this.config.selector) {
				this.config = _extends({}, this.config, {
					trigger: 'manual',
					selector: ''
				});
			} else {
				this._fixTitle();
			}
		};

		_proto._fixTitle = function _fixTitle() {
			var titleType = typeof this.element.getAttribute('data-original-title');

			if (this.element.getAttribute('title') || titleType !== 'string') {
				this.element.setAttribute('data-original-title', this.element.getAttribute('title') || '');
				this.element.setAttribute('title', '');
			}
		};

		_proto._enter = function _enter(event, context) {
			var dataKey = this.constructor.DATA_KEY;
			context = context || $__default["default"](event.currentTarget).data(dataKey);

			if (!context) {
				context = new this.constructor(event.currentTarget, this._getDelegateConfig());
				$__default["default"](event.currentTarget).data(dataKey, context);
			}

			if (event) {
				context._activeTrigger[event.type === 'focusin' ? TRIGGER_FOCUS : TRIGGER_HOVER] = true;
			}

			if ($__default["default"](context.getTipElement()).hasClass(CLASS_NAME_SHOW$3) || context._hoverState === HOVER_STATE_SHOW) {
				context._hoverState = HOVER_STATE_SHOW;
				return;
			}

			clearTimeout(context._timeout);
			context._hoverState = HOVER_STATE_SHOW;

			if (!context.config.delay || !context.config.delay.show) {
				context.show();
				return;
			}

			context._timeout = setTimeout(function () {
				if (context._hoverState === HOVER_STATE_SHOW) {
					context.show();
				}
			}, context.config.delay.show);
		};

		_proto._leave = function _leave(event, context) {
			var dataKey = this.constructor.DATA_KEY;
			context = context || $__default["default"](event.currentTarget).data(dataKey);

			if (!context) {
				context = new this.constructor(event.currentTarget, this._getDelegateConfig());
				$__default["default"](event.currentTarget).data(dataKey, context);
			}

			if (event) {
				context._activeTrigger[event.type === 'focusout' ? TRIGGER_FOCUS : TRIGGER_HOVER] = false;
			}

			if (context._isWithActiveTrigger()) {
				return;
			}

			clearTimeout(context._timeout);
			context._hoverState = HOVER_STATE_OUT;

			if (!context.config.delay || !context.config.delay.hide) {
				context.hide();
				return;
			}

			context._timeout = setTimeout(function () {
				if (context._hoverState === HOVER_STATE_OUT) {
					context.hide();
				}
			}, context.config.delay.hide);
		};

		_proto._isWithActiveTrigger = function _isWithActiveTrigger() {
			for (var trigger in this._activeTrigger) {
				if (this._activeTrigger[trigger]) {
					return true;
				}
			}

			return false;
		};

		_proto._getConfig = function _getConfig(config) {
			var dataAttributes = $__default["default"](this.element).data();
			Object.keys(dataAttributes).forEach(function (dataAttr) {
				if (DISALLOWED_ATTRIBUTES.indexOf(dataAttr) !== -1) {
					delete dataAttributes[dataAttr];
				}
			});
			config = _extends({}, this.constructor.Default, dataAttributes, typeof config === 'object' && config ? config : {});

			if (typeof config.delay === 'number') {
				config.delay = {
					show: config.delay,
					hide: config.delay
				};
			}

			if (typeof config.title === 'number') {
				config.title = config.title.toString();
			}

			if (typeof config.content === 'number') {
				config.content = config.content.toString();
			}

			Util.typeCheckConfig(NAME$4, config, this.constructor.DefaultType);

			if (config.sanitize) {
				config.template = sanitizeHtml(config.template, config.whiteList, config.sanitizeFn);
			}

			return config;
		};

		_proto._getDelegateConfig = function _getDelegateConfig() {
			var config = {};

			if (this.config) {
				for (var key in this.config) {
					if (this.constructor.Default[key] !== this.config[key]) {
						config[key] = this.config[key];
					}
				}
			}

			return config;
		};

		_proto._cleanTipClass = function _cleanTipClass() {
			var $tip = $__default["default"](this.getTipElement());
			var tabClass = $tip.attr('class').match(BSCLS_PREFIX_REGEX$1);

			if (tabClass !== null && tabClass.length) {
				$tip.removeClass(tabClass.join(''));
			}
		};

		_proto._handlePopperPlacementChange = function _handlePopperPlacementChange(popperData) {
			this.tip = popperData.instance.popper;

			this._cleanTipClass();

			this.addAttachmentClass(this._getAttachment(popperData.placement));
		};

		_proto._fixTransition = function _fixTransition() {
			var tip = this.getTipElement();
			var initConfigAnimation = this.config.animation;

			if (tip.getAttribute('x-placement') !== null) {
				return;
			}

			$__default["default"](tip).removeClass(CLASS_NAME_FADE$3);
			this.config.animation = false;
			this.hide();
			this.show();
			this.config.animation = initConfigAnimation;
		} // Static
			;

		Tooltip._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var $element = $__default["default"](this);
				var data = $element.data(DATA_KEY$4);

				var _config = typeof config === 'object' && config;

				if (!data && /dispose|hide/.test(config)) {
					return;
				}

				if (!data) {
					data = new Tooltip(this, _config);
					$element.data(DATA_KEY$4, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		_createClass(Tooltip, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$4;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$3;
			}
		}, {
			key: "NAME",
			get: function get() {
				return NAME$4;
			}
		}, {
			key: "DATA_KEY",
			get: function get() {
				return DATA_KEY$4;
			}
		}, {
			key: "Event",
			get: function get() {
				return Event$1;
			}
		}, {
			key: "EVENT_KEY",
			get: function get() {
				return EVENT_KEY$4;
			}
		}, {
			key: "DefaultType",
			get: function get() {
				return DefaultType$3;
			}
		}]);

		return Tooltip;
	}();
	/**
	 * jQuery
	 */


	$__default["default"].fn[NAME$4] = Tooltip._jQueryInterface;
	$__default["default"].fn[NAME$4].Constructor = Tooltip;

	$__default["default"].fn[NAME$4].noConflict = function () {
		$__default["default"].fn[NAME$4] = JQUERY_NO_CONFLICT$4;
		return Tooltip._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$3 = 'popover';
	var VERSION$3 = '4.6.2';
	var DATA_KEY$3 = 'bs.popover';
	var EVENT_KEY$3 = "." + DATA_KEY$3;
	var JQUERY_NO_CONFLICT$3 = $__default["default"].fn[NAME$3];
	var CLASS_PREFIX = 'bs-popover';
	var BSCLS_PREFIX_REGEX = new RegExp("(^|\\s)" + CLASS_PREFIX + "\\S+", 'g');
	var CLASS_NAME_FADE$2 = 'fade';
	var CLASS_NAME_SHOW$2 = 'show';
	var SELECTOR_TITLE = '.popover-header';
	var SELECTOR_CONTENT = '.popover-body';

	var Default$2 = _extends({}, Tooltip.Default, {
		placement: 'right',
		trigger: 'click',
		content: '',
		template: '<div class="popover" role="tooltip">' + '<div class="arrow"></div>' + '<h3 class="popover-header"></h3>' + '<div class="popover-body"></div></div>'
	});

	var DefaultType$2 = _extends({}, Tooltip.DefaultType, {
		content: '(string|element|function)'
	});

	var Event = {
		HIDE: "hide" + EVENT_KEY$3,
		HIDDEN: "hidden" + EVENT_KEY$3,
		SHOW: "show" + EVENT_KEY$3,
		SHOWN: "shown" + EVENT_KEY$3,
		INSERTED: "inserted" + EVENT_KEY$3,
		CLICK: "click" + EVENT_KEY$3,
		FOCUSIN: "focusin" + EVENT_KEY$3,
		FOCUSOUT: "focusout" + EVENT_KEY$3,
		MOUSEENTER: "mouseenter" + EVENT_KEY$3,
		MOUSELEAVE: "mouseleave" + EVENT_KEY$3
	};
	/**
	 * Class definition
	 */

	var Popover = /*#__PURE__*/function (_Tooltip) {
		_inheritsLoose(Popover, _Tooltip);

		function Popover() {
			return _Tooltip.apply(this, arguments) || this;
		}

		var _proto = Popover.prototype;

		// Overrides
		_proto.isWithContent = function isWithContent() {
			return this.getTitle() || this._getContent();
		};

		_proto.addAttachmentClass = function addAttachmentClass(attachment) {
			$__default["default"](this.getTipElement()).addClass(CLASS_PREFIX + "-" + attachment);
		};

		_proto.getTipElement = function getTipElement() {
			this.tip = this.tip || $__default["default"](this.config.template)[0];
			return this.tip;
		};

		_proto.setContent = function setContent() {
			var $tip = $__default["default"](this.getTipElement()); // We use append for html objects to maintain js events

			this.setElementContent($tip.find(SELECTOR_TITLE), this.getTitle());

			var content = this._getContent();

			if (typeof content === 'function') {
				content = content.call(this.element);
			}

			this.setElementContent($tip.find(SELECTOR_CONTENT), content);
			$tip.removeClass(CLASS_NAME_FADE$2 + " " + CLASS_NAME_SHOW$2);
		} // Private
			;

		_proto._getContent = function _getContent() {
			return this.element.getAttribute('data-content') || this.config.content;
		};

		_proto._cleanTipClass = function _cleanTipClass() {
			var $tip = $__default["default"](this.getTipElement());
			var tabClass = $tip.attr('class').match(BSCLS_PREFIX_REGEX);

			if (tabClass !== null && tabClass.length > 0) {
				$tip.removeClass(tabClass.join(''));
			}
		} // Static
			;

		Popover._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var data = $__default["default"](this).data(DATA_KEY$3);

				var _config = typeof config === 'object' ? config : null;

				if (!data && /dispose|hide/.test(config)) {
					return;
				}

				if (!data) {
					data = new Popover(this, _config);
					$__default["default"](this).data(DATA_KEY$3, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		_createClass(Popover, null, [{
			key: "VERSION",
			get: // Getters
				function get() {
					return VERSION$3;
				}
		}, {
			key: "Default",
			get: function get() {
				return Default$2;
			}
		}, {
			key: "NAME",
			get: function get() {
				return NAME$3;
			}
		}, {
			key: "DATA_KEY",
			get: function get() {
				return DATA_KEY$3;
			}
		}, {
			key: "Event",
			get: function get() {
				return Event;
			}
		}, {
			key: "EVENT_KEY",
			get: function get() {
				return EVENT_KEY$3;
			}
		}, {
			key: "DefaultType",
			get: function get() {
				return DefaultType$2;
			}
		}]);

		return Popover;
	}(Tooltip);
	/**
	 * jQuery
	 */


	$__default["default"].fn[NAME$3] = Popover._jQueryInterface;
	$__default["default"].fn[NAME$3].Constructor = Popover;

	$__default["default"].fn[NAME$3].noConflict = function () {
		$__default["default"].fn[NAME$3] = JQUERY_NO_CONFLICT$3;
		return Popover._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$2 = 'scrollspy';
	var VERSION$2 = '4.6.2';
	var DATA_KEY$2 = 'bs.scrollspy';
	var EVENT_KEY$2 = "." + DATA_KEY$2;
	var DATA_API_KEY$1 = '.data-api';
	var JQUERY_NO_CONFLICT$2 = $__default["default"].fn[NAME$2];
	var CLASS_NAME_DROPDOWN_ITEM = 'dropdown-item';
	var CLASS_NAME_ACTIVE$1 = 'active';
	var EVENT_ACTIVATE = "activate" + EVENT_KEY$2;
	var EVENT_SCROLL = "scroll" + EVENT_KEY$2;
	var EVENT_LOAD_DATA_API = "load" + EVENT_KEY$2 + DATA_API_KEY$1;
	var METHOD_OFFSET = 'offset';
	var METHOD_POSITION = 'position';
	var SELECTOR_DATA_SPY = '[data-spy="scroll"]';
	var SELECTOR_NAV_LIST_GROUP$1 = '.nav, .list-group';
	var SELECTOR_NAV_LINKS = '.nav-link';
	var SELECTOR_NAV_ITEMS = '.nav-item';
	var SELECTOR_LIST_ITEMS = '.list-group-item';
	var SELECTOR_DROPDOWN$1 = '.dropdown';
	var SELECTOR_DROPDOWN_ITEMS = '.dropdown-item';
	var SELECTOR_DROPDOWN_TOGGLE$1 = '.dropdown-toggle';
	var Default$1 = {
		offset: 10,
		method: 'auto',
		target: ''
	};
	var DefaultType$1 = {
		offset: 'number',
		method: 'string',
		target: '(string|element)'
	};
	/**
	 * Class definition
	 */

	var ScrollSpy = /*#__PURE__*/function () {
		function ScrollSpy(element, config) {
			var _this = this;

			this._element = element;
			this._scrollElement = element.tagName === 'BODY' ? window : element;
			this._config = this._getConfig(config);
			this._selector = this._config.target + " " + SELECTOR_NAV_LINKS + "," + (this._config.target + " " + SELECTOR_LIST_ITEMS + ",") + (this._config.target + " " + SELECTOR_DROPDOWN_ITEMS);
			this._offsets = [];
			this._targets = [];
			this._activeTarget = null;
			this._scrollHeight = 0;
			$__default["default"](this._scrollElement).on(EVENT_SCROLL, function (event) {
				return _this._process(event);
			});
			this.refresh();

			this._process();
		} // Getters


		var _proto = ScrollSpy.prototype;

		// Public
		_proto.refresh = function refresh() {
			var _this2 = this;

			var autoMethod = this._scrollElement === this._scrollElement.window ? METHOD_OFFSET : METHOD_POSITION;
			var offsetMethod = this._config.method === 'auto' ? autoMethod : this._config.method;
			var offsetBase = offsetMethod === METHOD_POSITION ? this._getScrollTop() : 0;
			this._offsets = [];
			this._targets = [];
			this._scrollHeight = this._getScrollHeight();
			var targets = [].slice.call(document.querySelectorAll(this._selector));
			targets.map(function (element) {
				var target;
				var targetSelector = Util.getSelectorFromElement(element);

				if (targetSelector) {
					target = document.querySelector(targetSelector);
				}

				if (target) {
					var targetBCR = target.getBoundingClientRect();

					if (targetBCR.width || targetBCR.height) {
						// TODO (fat): remove sketch reliance on jQuery position/offset
						return [$__default["default"](target)[offsetMethod]().top + offsetBase, targetSelector];
					}
				}

				return null;
			}).filter(Boolean).sort(function (a, b) {
				return a[0] - b[0];
			}).forEach(function (item) {
				_this2._offsets.push(item[0]);

				_this2._targets.push(item[1]);
			});
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$2);
			$__default["default"](this._scrollElement).off(EVENT_KEY$2);
			this._element = null;
			this._scrollElement = null;
			this._config = null;
			this._selector = null;
			this._offsets = null;
			this._targets = null;
			this._activeTarget = null;
			this._scrollHeight = null;
		} // Private
			;

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, Default$1, typeof config === 'object' && config ? config : {});

			if (typeof config.target !== 'string' && Util.isElement(config.target)) {
				var id = $__default["default"](config.target).attr('id');

				if (!id) {
					id = Util.getUID(NAME$2);
					$__default["default"](config.target).attr('id', id);
				}

				config.target = "#" + id;
			}

			Util.typeCheckConfig(NAME$2, config, DefaultType$1);
			return config;
		};

		_proto._getScrollTop = function _getScrollTop() {
			return this._scrollElement === window ? this._scrollElement.pageYOffset : this._scrollElement.scrollTop;
		};

		_proto._getScrollHeight = function _getScrollHeight() {
			return this._scrollElement.scrollHeight || Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
		};

		_proto._getOffsetHeight = function _getOffsetHeight() {
			return this._scrollElement === window ? window.innerHeight : this._scrollElement.getBoundingClientRect().height;
		};

		_proto._process = function _process() {
			var scrollTop = this._getScrollTop() + this._config.offset;

			var scrollHeight = this._getScrollHeight();

			var maxScroll = this._config.offset + scrollHeight - this._getOffsetHeight();

			if (this._scrollHeight !== scrollHeight) {
				this.refresh();
			}

			if (scrollTop >= maxScroll) {
				var target = this._targets[this._targets.length - 1];

				if (this._activeTarget !== target) {
					this._activate(target);
				}

				return;
			}

			if (this._activeTarget && scrollTop < this._offsets[0] && this._offsets[0] > 0) {
				this._activeTarget = null;

				this._clear();

				return;
			}

			for (var i = this._offsets.length; i--;) {
				var isActiveTarget = this._activeTarget !== this._targets[i] && scrollTop >= this._offsets[i] && (typeof this._offsets[i + 1] === 'undefined' || scrollTop < this._offsets[i + 1]);

				if (isActiveTarget) {
					this._activate(this._targets[i]);
				}
			}
		};

		_proto._activate = function _activate(target) {
			this._activeTarget = target;

			this._clear();

			var queries = this._selector.split(',').map(function (selector) {
				return selector + "[data-target=\"" + target + "\"]," + selector + "[href=\"" + target + "\"]";
			});

			var $link = $__default["default"]([].slice.call(document.querySelectorAll(queries.join(','))));

			if ($link.hasClass(CLASS_NAME_DROPDOWN_ITEM)) {
				$link.closest(SELECTOR_DROPDOWN$1).find(SELECTOR_DROPDOWN_TOGGLE$1).addClass(CLASS_NAME_ACTIVE$1);
				$link.addClass(CLASS_NAME_ACTIVE$1);
			} else {
				// Set triggered link as active
				$link.addClass(CLASS_NAME_ACTIVE$1); // Set triggered links parents as active
				// With both <ul> and <nav> markup a parent is the previous sibling of any nav ancestor

				$link.parents(SELECTOR_NAV_LIST_GROUP$1).prev(SELECTOR_NAV_LINKS + ", " + SELECTOR_LIST_ITEMS).addClass(CLASS_NAME_ACTIVE$1); // Handle special case when .nav-link is inside .nav-item

				$link.parents(SELECTOR_NAV_LIST_GROUP$1).prev(SELECTOR_NAV_ITEMS).children(SELECTOR_NAV_LINKS).addClass(CLASS_NAME_ACTIVE$1);
			}

			$__default["default"](this._scrollElement).trigger(EVENT_ACTIVATE, {
				relatedTarget: target
			});
		};

		_proto._clear = function _clear() {
			[].slice.call(document.querySelectorAll(this._selector)).filter(function (node) {
				return node.classList.contains(CLASS_NAME_ACTIVE$1);
			}).forEach(function (node) {
				return node.classList.remove(CLASS_NAME_ACTIVE$1);
			});
		} // Static
			;

		ScrollSpy._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var data = $__default["default"](this).data(DATA_KEY$2);

				var _config = typeof config === 'object' && config;

				if (!data) {
					data = new ScrollSpy(this, _config);
					$__default["default"](this).data(DATA_KEY$2, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		_createClass(ScrollSpy, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$2;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default$1;
			}
		}]);

		return ScrollSpy;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](window).on(EVENT_LOAD_DATA_API, function () {
		var scrollSpys = [].slice.call(document.querySelectorAll(SELECTOR_DATA_SPY));
		var scrollSpysLength = scrollSpys.length;

		for (var i = scrollSpysLength; i--;) {
			var $spy = $__default["default"](scrollSpys[i]);

			ScrollSpy._jQueryInterface.call($spy, $spy.data());
		}
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$2] = ScrollSpy._jQueryInterface;
	$__default["default"].fn[NAME$2].Constructor = ScrollSpy;

	$__default["default"].fn[NAME$2].noConflict = function () {
		$__default["default"].fn[NAME$2] = JQUERY_NO_CONFLICT$2;
		return ScrollSpy._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME$1 = 'tab';
	var VERSION$1 = '4.6.2';
	var DATA_KEY$1 = 'bs.tab';
	var EVENT_KEY$1 = "." + DATA_KEY$1;
	var DATA_API_KEY = '.data-api';
	var JQUERY_NO_CONFLICT$1 = $__default["default"].fn[NAME$1];
	var CLASS_NAME_DROPDOWN_MENU = 'dropdown-menu';
	var CLASS_NAME_ACTIVE = 'active';
	var CLASS_NAME_DISABLED = 'disabled';
	var CLASS_NAME_FADE$1 = 'fade';
	var CLASS_NAME_SHOW$1 = 'show';
	var EVENT_HIDE$1 = "hide" + EVENT_KEY$1;
	var EVENT_HIDDEN$1 = "hidden" + EVENT_KEY$1;
	var EVENT_SHOW$1 = "show" + EVENT_KEY$1;
	var EVENT_SHOWN$1 = "shown" + EVENT_KEY$1;
	var EVENT_CLICK_DATA_API = "click" + EVENT_KEY$1 + DATA_API_KEY;
	var SELECTOR_DROPDOWN = '.dropdown';
	var SELECTOR_NAV_LIST_GROUP = '.nav, .list-group';
	var SELECTOR_ACTIVE = '.active';
	var SELECTOR_ACTIVE_UL = '> li > .active';
	var SELECTOR_DATA_TOGGLE = '[data-toggle="tab"], [data-toggle="pill"], [data-toggle="list"]';
	var SELECTOR_DROPDOWN_TOGGLE = '.dropdown-toggle';
	var SELECTOR_DROPDOWN_ACTIVE_CHILD = '> .dropdown-menu .active';
	/**
	 * Class definition
	 */

	var Tab = /*#__PURE__*/function () {
		function Tab(element) {
			this._element = element;
		} // Getters


		var _proto = Tab.prototype;

		// Public
		_proto.show = function show() {
			var _this = this;

			if (this._element.parentNode && this._element.parentNode.nodeType === Node.ELEMENT_NODE && $__default["default"](this._element).hasClass(CLASS_NAME_ACTIVE) || $__default["default"](this._element).hasClass(CLASS_NAME_DISABLED) || this._element.hasAttribute('disabled')) {
				return;
			}

			var target;
			var previous;
			var listElement = $__default["default"](this._element).closest(SELECTOR_NAV_LIST_GROUP)[0];
			var selector = Util.getSelectorFromElement(this._element);

			if (listElement) {
				var itemSelector = listElement.nodeName === 'UL' || listElement.nodeName === 'OL' ? SELECTOR_ACTIVE_UL : SELECTOR_ACTIVE;
				previous = $__default["default"].makeArray($__default["default"](listElement).find(itemSelector));
				previous = previous[previous.length - 1];
			}

			var hideEvent = $__default["default"].Event(EVENT_HIDE$1, {
				relatedTarget: this._element
			});
			var showEvent = $__default["default"].Event(EVENT_SHOW$1, {
				relatedTarget: previous
			});

			if (previous) {
				$__default["default"](previous).trigger(hideEvent);
			}

			$__default["default"](this._element).trigger(showEvent);

			if (showEvent.isDefaultPrevented() || hideEvent.isDefaultPrevented()) {
				return;
			}

			if (selector) {
				target = document.querySelector(selector);
			}

			this._activate(this._element, listElement);

			var complete = function complete() {
				var hiddenEvent = $__default["default"].Event(EVENT_HIDDEN$1, {
					relatedTarget: _this._element
				});
				var shownEvent = $__default["default"].Event(EVENT_SHOWN$1, {
					relatedTarget: previous
				});
				$__default["default"](previous).trigger(hiddenEvent);
				$__default["default"](_this._element).trigger(shownEvent);
			};

			if (target) {
				this._activate(target, target.parentNode, complete);
			} else {
				complete();
			}
		};

		_proto.dispose = function dispose() {
			$__default["default"].removeData(this._element, DATA_KEY$1);
			this._element = null;
		} // Private
			;

		_proto._activate = function _activate(element, container, callback) {
			var _this2 = this;

			var activeElements = container && (container.nodeName === 'UL' || container.nodeName === 'OL') ? $__default["default"](container).find(SELECTOR_ACTIVE_UL) : $__default["default"](container).children(SELECTOR_ACTIVE);
			var active = activeElements[0];
			var isTransitioning = callback && active && $__default["default"](active).hasClass(CLASS_NAME_FADE$1);

			var complete = function complete() {
				return _this2._transitionComplete(element, active, callback);
			};

			if (active && isTransitioning) {
				var transitionDuration = Util.getTransitionDurationFromElement(active);
				$__default["default"](active).removeClass(CLASS_NAME_SHOW$1).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
			} else {
				complete();
			}
		};

		_proto._transitionComplete = function _transitionComplete(element, active, callback) {
			if (active) {
				$__default["default"](active).removeClass(CLASS_NAME_ACTIVE);
				var dropdownChild = $__default["default"](active.parentNode).find(SELECTOR_DROPDOWN_ACTIVE_CHILD)[0];

				if (dropdownChild) {
					$__default["default"](dropdownChild).removeClass(CLASS_NAME_ACTIVE);
				}

				if (active.getAttribute('role') === 'tab') {
					active.setAttribute('aria-selected', false);
				}
			}

			$__default["default"](element).addClass(CLASS_NAME_ACTIVE);

			if (element.getAttribute('role') === 'tab') {
				element.setAttribute('aria-selected', true);
			}

			Util.reflow(element);

			if (element.classList.contains(CLASS_NAME_FADE$1)) {
				element.classList.add(CLASS_NAME_SHOW$1);
			}

			var parent = element.parentNode;

			if (parent && parent.nodeName === 'LI') {
				parent = parent.parentNode;
			}

			if (parent && $__default["default"](parent).hasClass(CLASS_NAME_DROPDOWN_MENU)) {
				var dropdownElement = $__default["default"](element).closest(SELECTOR_DROPDOWN)[0];

				if (dropdownElement) {
					var dropdownToggleList = [].slice.call(dropdownElement.querySelectorAll(SELECTOR_DROPDOWN_TOGGLE));
					$__default["default"](dropdownToggleList).addClass(CLASS_NAME_ACTIVE);
				}

				element.setAttribute('aria-expanded', true);
			}

			if (callback) {
				callback();
			}
		} // Static
			;

		Tab._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var $this = $__default["default"](this);
				var data = $this.data(DATA_KEY$1);

				if (!data) {
					data = new Tab(this);
					$this.data(DATA_KEY$1, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config]();
				}
			});
		};

		_createClass(Tab, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION$1;
			}
		}]);

		return Tab;
	}();
	/**
	 * Data API implementation
	 */


	$__default["default"](document).on(EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function (event) {
		event.preventDefault();

		Tab._jQueryInterface.call($__default["default"](this), 'show');
	});
	/**
	 * jQuery
	 */

	$__default["default"].fn[NAME$1] = Tab._jQueryInterface;
	$__default["default"].fn[NAME$1].Constructor = Tab;

	$__default["default"].fn[NAME$1].noConflict = function () {
		$__default["default"].fn[NAME$1] = JQUERY_NO_CONFLICT$1;
		return Tab._jQueryInterface;
	};

	/**
	 * Constants
	 */

	var NAME = 'toast';
	var VERSION = '4.6.2';
	var DATA_KEY = 'bs.toast';
	var EVENT_KEY = "." + DATA_KEY;
	var JQUERY_NO_CONFLICT = $__default["default"].fn[NAME];
	var CLASS_NAME_FADE = 'fade';
	var CLASS_NAME_HIDE = 'hide';
	var CLASS_NAME_SHOW = 'show';
	var CLASS_NAME_SHOWING = 'showing';
	var EVENT_CLICK_DISMISS = "click.dismiss" + EVENT_KEY;
	var EVENT_HIDE = "hide" + EVENT_KEY;
	var EVENT_HIDDEN = "hidden" + EVENT_KEY;
	var EVENT_SHOW = "show" + EVENT_KEY;
	var EVENT_SHOWN = "shown" + EVENT_KEY;
	var SELECTOR_DATA_DISMISS = '[data-dismiss="toast"]';
	var Default = {
		animation: true,
		autohide: true,
		delay: 500
	};
	var DefaultType = {
		animation: 'boolean',
		autohide: 'boolean',
		delay: 'number'
	};
	/**
	 * Class definition
	 */

	var Toast = /*#__PURE__*/function () {
		function Toast(element, config) {
			this._element = element;
			this._config = this._getConfig(config);
			this._timeout = null;

			this._setListeners();
		} // Getters


		var _proto = Toast.prototype;

		// Public
		_proto.show = function show() {
			var _this = this;

			var showEvent = $__default["default"].Event(EVENT_SHOW);
			$__default["default"](this._element).trigger(showEvent);

			if (showEvent.isDefaultPrevented()) {
				return;
			}

			this._clearTimeout();

			if (this._config.animation) {
				this._element.classList.add(CLASS_NAME_FADE);
			}

			var complete = function complete() {
				_this._element.classList.remove(CLASS_NAME_SHOWING);

				_this._element.classList.add(CLASS_NAME_SHOW);

				$__default["default"](_this._element).trigger(EVENT_SHOWN);

				if (_this._config.autohide) {
					_this._timeout = setTimeout(function () {
						_this.hide();
					}, _this._config.delay);
				}
			};

			this._element.classList.remove(CLASS_NAME_HIDE);

			Util.reflow(this._element);

			this._element.classList.add(CLASS_NAME_SHOWING);

			if (this._config.animation) {
				var transitionDuration = Util.getTransitionDurationFromElement(this._element);
				$__default["default"](this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
			} else {
				complete();
			}
		};

		_proto.hide = function hide() {
			if (!this._element.classList.contains(CLASS_NAME_SHOW)) {
				return;
			}

			var hideEvent = $__default["default"].Event(EVENT_HIDE);
			$__default["default"](this._element).trigger(hideEvent);

			if (hideEvent.isDefaultPrevented()) {
				return;
			}

			this._close();
		};

		_proto.dispose = function dispose() {
			this._clearTimeout();

			if (this._element.classList.contains(CLASS_NAME_SHOW)) {
				this._element.classList.remove(CLASS_NAME_SHOW);
			}

			$__default["default"](this._element).off(EVENT_CLICK_DISMISS);
			$__default["default"].removeData(this._element, DATA_KEY);
			this._element = null;
			this._config = null;
		} // Private
			;

		_proto._getConfig = function _getConfig(config) {
			config = _extends({}, Default, $__default["default"](this._element).data(), typeof config === 'object' && config ? config : {});
			Util.typeCheckConfig(NAME, config, this.constructor.DefaultType);
			return config;
		};

		_proto._setListeners = function _setListeners() {
			var _this2 = this;

			$__default["default"](this._element).on(EVENT_CLICK_DISMISS, SELECTOR_DATA_DISMISS, function () {
				return _this2.hide();
			});
		};

		_proto._close = function _close() {
			var _this3 = this;

			var complete = function complete() {
				_this3._element.classList.add(CLASS_NAME_HIDE);

				$__default["default"](_this3._element).trigger(EVENT_HIDDEN);
			};

			this._element.classList.remove(CLASS_NAME_SHOW);

			if (this._config.animation) {
				var transitionDuration = Util.getTransitionDurationFromElement(this._element);
				$__default["default"](this._element).one(Util.TRANSITION_END, complete).emulateTransitionEnd(transitionDuration);
			} else {
				complete();
			}
		};

		_proto._clearTimeout = function _clearTimeout() {
			clearTimeout(this._timeout);
			this._timeout = null;
		} // Static
			;

		Toast._jQueryInterface = function _jQueryInterface(config) {
			return this.each(function () {
				var $element = $__default["default"](this);
				var data = $element.data(DATA_KEY);

				var _config = typeof config === 'object' && config;

				if (!data) {
					data = new Toast(this, _config);
					$element.data(DATA_KEY, data);
				}

				if (typeof config === 'string') {
					if (typeof data[config] === 'undefined') {
						throw new TypeError("No method named \"" + config + "\"");
					}

					data[config](this);
				}
			});
		};

		_createClass(Toast, null, [{
			key: "VERSION",
			get: function get() {
				return VERSION;
			}
		}, {
			key: "DefaultType",
			get: function get() {
				return DefaultType;
			}
		}, {
			key: "Default",
			get: function get() {
				return Default;
			}
		}]);

		return Toast;
	}();
	/**
	 * jQuery
	 */


	$__default["default"].fn[NAME] = Toast._jQueryInterface;
	$__default["default"].fn[NAME].Constructor = Toast;

	$__default["default"].fn[NAME].noConflict = function () {
		$__default["default"].fn[NAME] = JQUERY_NO_CONFLICT;
		return Toast._jQueryInterface;
	};

	exports.Alert = Alert;
	exports.Button = Button;
	exports.Carousel = Carousel;
	exports.Collapse = Collapse;
	exports.Dropdown = Dropdown;
	exports.Modal = Modal;
	exports.Popover = Popover;
	exports.Scrollspy = ScrollSpy;
	exports.Tab = Tab;
	exports.Toast = Toast;
	exports.Tooltip = Tooltip;
	exports.Util = Util;

	Object.defineProperty(exports, '__esModule', { value: true });

}));
//# sourceMappingURL=bootstrap.js.map

/** 
 ********************************
 **        DATEPICKER          **
 ********************************
 */


(function (factory) {
	if (typeof define === 'function' && define.amd) {
		define(['jquery'], factory);
	} else if (typeof exports === 'object') {
		factory(require('jquery'));
	} else {
		factory(jQuery);
	}
}(function ($, undefined) {
	function UTCDate() {
		return new Date(Date.UTC.apply(Date, arguments));
	}
	function UTCToday() {
		var today = new Date();
		return UTCDate(today.getFullYear(), today.getMonth(), today.getDate());
	}
	function isUTCEquals(date1, date2) {
		return (
			date1.getUTCFullYear() === date2.getUTCFullYear() &&
			date1.getUTCMonth() === date2.getUTCMonth() &&
			date1.getUTCDate() === date2.getUTCDate()
		);
	}
	function alias(method, deprecationMsg) {
		return function () {
			if (deprecationMsg !== undefined) {
				$.fn.datepicker.deprecated(deprecationMsg);
			}

			return this[method].apply(this, arguments);
		};
	}
	function isValidDate(d) {
		return d && !isNaN(d.getTime());
	}

	var DateArray = (function () {
		var extras = {
			get: function (i) {
				return this.slice(i)[0];
			},
			contains: function (d) {
				// Array.indexOf is not cross-browser;
				// $.inArray doesn't work with Dates
				var val = d && d.valueOf();
				for (var i = 0, l = this.length; i < l; i++)
					// Use date arithmetic to allow dates with different times to match
					if (0 <= this[i].valueOf() - val && this[i].valueOf() - val < 1000 * 60 * 60 * 24)
						return i;
				return -1;
			},
			remove: function (i) {
				this.splice(i, 1);
			},
			replace: function (new_array) {
				if (!new_array)
					return;
				if (!$.isArray(new_array))
					new_array = [new_array];
				this.clear();
				this.push.apply(this, new_array);
			},
			clear: function () {
				this.length = 0;
			},
			copy: function () {
				var a = new DateArray();
				a.replace(this);
				return a;
			}
		};

		return function () {
			var a = [];
			a.push.apply(a, arguments);
			$.extend(a, extras);
			return a;
		};
	})();


	// Picker object

	var Datepicker = function (element, options) {
		$.data(element, 'datepicker', this);

		this._events = [];
		this._secondaryEvents = [];

		this._process_options(options);

		this.dates = new DateArray();
		this.viewDate = this.o.defaultViewDate;
		this.focusDate = null;

		this.element = $(element);
		this.isInput = this.element.is('input');
		this.inputField = this.isInput ? this.element : this.element.find('input');
		this.component = this.element.hasClass('date') ? this.element.find('.add-on, .input-group-addon, .input-group-append, .input-group-prepend, .btn') : false;
		if (this.component && this.component.length === 0)
			this.component = false;
		this.isInline = !this.component && this.element.is('div');

		this.picker = $(DPGlobal.template);

		// Checking templates and inserting
		if (this._check_template(this.o.templates.leftArrow)) {
			this.picker.find('.prev').html(this.o.templates.leftArrow);
		}

		if (this._check_template(this.o.templates.rightArrow)) {
			this.picker.find('.next').html(this.o.templates.rightArrow);
		}

		this._buildEvents();
		this._attachEvents();

		if (this.isInline) {
			this.picker.addClass('datepicker-inline').appendTo(this.element);
		}
		else {
			this.picker.addClass('datepicker-dropdown dropdown-menu');
		}

		if (this.o.rtl) {
			this.picker.addClass('datepicker-rtl');
		}

		if (this.o.calendarWeeks) {
			this.picker.find('.datepicker-days .datepicker-switch, thead .datepicker-title, tfoot .today, tfoot .clear')
				.attr('colspan', function (i, val) {
					return Number(val) + 1;
				});
		}

		this._process_options({
			startDate: this._o.startDate,
			endDate: this._o.endDate,
			daysOfWeekDisabled: this.o.daysOfWeekDisabled,
			daysOfWeekHighlighted: this.o.daysOfWeekHighlighted,
			datesDisabled: this.o.datesDisabled
		});

		this._allow_update = false;
		this.setViewMode(this.o.startView);
		this._allow_update = true;

		this.fillDow();
		this.fillMonths();

		this.update();

		if (this.isInline) {
			this.show();
		}
	};

	Datepicker.prototype = {
		constructor: Datepicker,

		_resolveViewName: function (view) {
			$.each(DPGlobal.viewModes, function (i, viewMode) {
				if (view === i || $.inArray(view, viewMode.names) !== -1) {
					view = i;
					return false;
				}
			});

			return view;
		},

		_resolveDaysOfWeek: function (daysOfWeek) {
			if (!$.isArray(daysOfWeek))
				daysOfWeek = daysOfWeek.split(/[,\s]*/);
			return $.map(daysOfWeek, Number);
		},

		_check_template: function (tmp) {
			try {
				// If empty
				if (tmp === undefined || tmp === "") {
					return false;
				}
				// If no html, everything ok
				if ((tmp.match(/[<>]/g) || []).length <= 0) {
					return true;
				}
				// Checking if html is fine
				var jDom = $(tmp);
				return jDom.length > 0;
			}
			catch (ex) {
				return false;
			}
		},

		_process_options: function (opts) {
			// Store raw options for reference
			this._o = $.extend({}, this._o, opts);
			// Processed options
			var o = this.o = $.extend({}, this._o);

			// Check if "de-DE" style date is available, if not language should
			// fallback to 2 letter code eg "de"
			var lang = o.language;
			if (!dates[lang]) {
				lang = lang.split('-')[0];
				if (!dates[lang])
					lang = defaults.language;
			}
			o.language = lang;

			// Retrieve view index from any aliases
			o.startView = this._resolveViewName(o.startView);
			o.minViewMode = this._resolveViewName(o.minViewMode);
			o.maxViewMode = this._resolveViewName(o.maxViewMode);

			// Check view is between min and max
			o.startView = Math.max(this.o.minViewMode, Math.min(this.o.maxViewMode, o.startView));

			// true, false, or Number > 0
			if (o.multidate !== true) {
				o.multidate = Number(o.multidate) || false;
				if (o.multidate !== false)
					o.multidate = Math.max(0, o.multidate);
			}
			o.multidateSeparator = String(o.multidateSeparator);

			o.weekStart %= 7;
			o.weekEnd = (o.weekStart + 6) % 7;

			var format = DPGlobal.parseFormat(o.format);
			if (o.startDate !== -Infinity) {
				if (!!o.startDate) {
					if (o.startDate instanceof Date)
						o.startDate = this._local_to_utc(this._zero_time(o.startDate));
					else
						o.startDate = DPGlobal.parseDate(o.startDate, format, o.language, o.assumeNearbyYear);
				}
				else {
					o.startDate = -Infinity;
				}
			}
			if (o.endDate !== Infinity) {
				if (!!o.endDate) {
					if (o.endDate instanceof Date)
						o.endDate = this._local_to_utc(this._zero_time(o.endDate));
					else
						o.endDate = DPGlobal.parseDate(o.endDate, format, o.language, o.assumeNearbyYear);
				}
				else {
					o.endDate = Infinity;
				}
			}

			o.daysOfWeekDisabled = this._resolveDaysOfWeek(o.daysOfWeekDisabled || []);
			o.daysOfWeekHighlighted = this._resolveDaysOfWeek(o.daysOfWeekHighlighted || []);

			o.datesDisabled = o.datesDisabled || [];
			if (!$.isArray(o.datesDisabled)) {
				o.datesDisabled = o.datesDisabled.split(',');
			}
			o.datesDisabled = $.map(o.datesDisabled, function (d) {
				return DPGlobal.parseDate(d, format, o.language, o.assumeNearbyYear);
			});

			var plc = String(o.orientation).toLowerCase().split(/\s+/g),
				_plc = o.orientation.toLowerCase();
			plc = $.grep(plc, function (word) {
				return /^auto|left|right|top|bottom$/.test(word);
			});
			o.orientation = { x: 'auto', y: 'auto' };
			if (!_plc || _plc === 'auto')
				; // no action
			else if (plc.length === 1) {
				switch (plc[0]) {
					case 'top':
					case 'bottom':
						o.orientation.y = plc[0];
						break;
					case 'left':
					case 'right':
						o.orientation.x = plc[0];
						break;
				}
			}
			else {
				_plc = $.grep(plc, function (word) {
					return /^left|right$/.test(word);
				});
				o.orientation.x = _plc[0] || 'auto';

				_plc = $.grep(plc, function (word) {
					return /^top|bottom$/.test(word);
				});
				o.orientation.y = _plc[0] || 'auto';
			}
			if (o.defaultViewDate instanceof Date || typeof o.defaultViewDate === 'string') {
				o.defaultViewDate = DPGlobal.parseDate(o.defaultViewDate, format, o.language, o.assumeNearbyYear);
			} else if (o.defaultViewDate) {
				var year = o.defaultViewDate.year || new Date().getFullYear();
				var month = o.defaultViewDate.month || 0;
				var day = o.defaultViewDate.day || 1;
				o.defaultViewDate = UTCDate(year, month, day);
			} else {
				o.defaultViewDate = UTCToday();
			}
		},
		_applyEvents: function (evs) {
			for (var i = 0, el, ch, ev; i < evs.length; i++) {
				el = evs[i][0];
				if (evs[i].length === 2) {
					ch = undefined;
					ev = evs[i][1];
				} else if (evs[i].length === 3) {
					ch = evs[i][1];
					ev = evs[i][2];
				}
				el.on(ev, ch);
			}
		},
		_unapplyEvents: function (evs) {
			for (var i = 0, el, ev, ch; i < evs.length; i++) {
				el = evs[i][0];
				if (evs[i].length === 2) {
					ch = undefined;
					ev = evs[i][1];
				} else if (evs[i].length === 3) {
					ch = evs[i][1];
					ev = evs[i][2];
				}
				el.off(ev, ch);
			}
		},
		_buildEvents: function () {
			var events = {
				keyup: $.proxy(function (e) {
					if ($.inArray(e.keyCode, [27, 37, 39, 38, 40, 32, 13, 9]) === -1)
						this.update();
				}, this),
				keydown: $.proxy(this.keydown, this),
				paste: $.proxy(this.paste, this)
			};

			if (this.o.showOnFocus === true) {
				events.focus = $.proxy(this.show, this);
			}

			if (this.isInput) { // single input
				this._events = [
					[this.element, events]
				];
			}
			// component: input + button
			else if (this.component && this.inputField.length) {
				this._events = [
					// For components that are not readonly, allow keyboard nav
					[this.inputField, events],
					[this.component, {
						click: $.proxy(this.show, this)
					}]
				];
			}
			else {
				this._events = [
					[this.element, {
						click: $.proxy(this.show, this),
						keydown: $.proxy(this.keydown, this)
					}]
				];
			}
			this._events.push(
				// Component: listen for blur on element descendants
				[this.element, '*', {
					blur: $.proxy(function (e) {
						this._focused_from = e.target;
					}, this)
				}],
				// Input: listen for blur on element
				[this.element, {
					blur: $.proxy(function (e) {
						this._focused_from = e.target;
					}, this)
				}]
			);

			if (this.o.immediateUpdates) {
				// Trigger input updates immediately on changed year/month
				this._events.push([this.element, {
					'changeYear changeMonth': $.proxy(function (e) {
						this.update(e.date);
					}, this)
				}]);
			}

			this._secondaryEvents = [
				[this.picker, {
					click: $.proxy(this.click, this)
				}],
				[this.picker, '.prev, .next', {
					click: $.proxy(this.navArrowsClick, this)
				}],
				[this.picker, '.day:not(.disabled)', {
					click: $.proxy(this.dayCellClick, this)
				}],
				[$(window), {
					resize: $.proxy(this.place, this)
				}],
				[$(document), {
					'mousedown touchstart': $.proxy(function (e) {
						// Clicked outside the datepicker, hide it
						if (!(
							this.element.is(e.target) ||
							this.element.find(e.target).length ||
							this.picker.is(e.target) ||
							this.picker.find(e.target).length ||
							this.isInline
						)) {
							this.hide();
						}
					}, this)
				}]
			];
		},
		_attachEvents: function () {
			this._detachEvents();
			this._applyEvents(this._events);
		},
		_detachEvents: function () {
			this._unapplyEvents(this._events);
		},
		_attachSecondaryEvents: function () {
			this._detachSecondaryEvents();
			this._applyEvents(this._secondaryEvents);
		},
		_detachSecondaryEvents: function () {
			this._unapplyEvents(this._secondaryEvents);
		},
		_trigger: function (event, altdate) {
			var date = altdate || this.dates.get(-1),
				local_date = this._utc_to_local(date);

			this.element.trigger({
				type: event,
				date: local_date,
				viewMode: this.viewMode,
				dates: $.map(this.dates, this._utc_to_local),
				format: $.proxy(function (ix, format) {
					if (arguments.length === 0) {
						ix = this.dates.length - 1;
						format = this.o.format;
					} else if (typeof ix === 'string') {
						format = ix;
						ix = this.dates.length - 1;
					}
					format = format || this.o.format;
					var date = this.dates.get(ix);
					return DPGlobal.formatDate(date, format, this.o.language);
				}, this)
			});
		},

		show: function () {
			if (this.inputField.is(':disabled') || (this.inputField.prop('readonly') && this.o.enableOnReadonly === false))
				return;
			if (!this.isInline)
				this.picker.appendTo(this.o.container);
			this.place();
			this.picker.show();
			this._attachSecondaryEvents();
			this._trigger('show');
			if ((window.navigator.msMaxTouchPoints || 'ontouchstart' in document) && this.o.disableTouchKeyboard) {
				$(this.element).blur();
			}
			return this;
		},

		hide: function () {
			if (this.isInline || !this.picker.is(':visible'))
				return this;
			this.focusDate = null;
			this.picker.hide().detach();
			this._detachSecondaryEvents();
			this.setViewMode(this.o.startView);

			if (this.o.forceParse && this.inputField.val())
				this.setValue();
			this._trigger('hide');
			return this;
		},

		destroy: function () {
			this.hide();
			this._detachEvents();
			this._detachSecondaryEvents();
			this.picker.remove();
			delete this.element.data().datepicker;
			if (!this.isInput) {
				delete this.element.data().date;
			}
			return this;
		},

		paste: function (e) {
			var dateString;
			if (e.originalEvent.clipboardData && e.originalEvent.clipboardData.types
				&& $.inArray('text/plain', e.originalEvent.clipboardData.types) !== -1) {
				dateString = e.originalEvent.clipboardData.getData('text/plain');
			} else if (window.clipboardData) {
				dateString = window.clipboardData.getData('Text');
			} else {
				return;
			}
			this.setDate(dateString);
			this.update();
			e.preventDefault();
		},

		_utc_to_local: function (utc) {
			if (!utc) {
				return utc;
			}

			var local = new Date(utc.getTime() + (utc.getTimezoneOffset() * 60000));

			if (local.getTimezoneOffset() !== utc.getTimezoneOffset()) {
				local = new Date(utc.getTime() + (local.getTimezoneOffset() * 60000));
			}

			return local;
		},
		_local_to_utc: function (local) {
			return local && new Date(local.getTime() - (local.getTimezoneOffset() * 60000));
		},
		_zero_time: function (local) {
			return local && new Date(local.getFullYear(), local.getMonth(), local.getDate());
		},
		_zero_utc_time: function (utc) {
			return utc && UTCDate(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
		},

		getDates: function () {
			return $.map(this.dates, this._utc_to_local);
		},

		getUTCDates: function () {
			return $.map(this.dates, function (d) {
				return new Date(d);
			});
		},

		getDate: function () {
			return this._utc_to_local(this.getUTCDate());
		},

		getUTCDate: function () {
			var selected_date = this.dates.get(-1);
			if (selected_date !== undefined) {
				return new Date(selected_date);
			} else {
				return null;
			}
		},

		clearDates: function () {
			this.inputField.val('');
			this.update();
			this._trigger('changeDate');

			if (this.o.autoclose) {
				this.hide();
			}
		},

		setDates: function () {
			var args = $.isArray(arguments[0]) ? arguments[0] : arguments;
			this.update.apply(this, args);
			this._trigger('changeDate');
			this.setValue();
			return this;
		},

		setUTCDates: function () {
			var args = $.isArray(arguments[0]) ? arguments[0] : arguments;
			this.setDates.apply(this, $.map(args, this._utc_to_local));
			return this;
		},

		setDate: alias('setDates'),
		setUTCDate: alias('setUTCDates'),
		remove: alias('destroy', 'Method `remove` is deprecated and will be removed in version 2.0. Use `destroy` instead'),

		setValue: function () {
			var formatted = this.getFormattedDate();
			this.inputField.val(formatted);
			return this;
		},

		getFormattedDate: function (format) {
			if (format === undefined)
				format = this.o.format;

			var lang = this.o.language;
			return $.map(this.dates, function (d) {
				return DPGlobal.formatDate(d, format, lang);
			}).join(this.o.multidateSeparator);
		},

		getStartDate: function () {
			return this.o.startDate;
		},

		setStartDate: function (startDate) {
			this._process_options({ startDate: startDate });
			this.update();
			this.updateNavArrows();
			return this;
		},

		getEndDate: function () {
			return this.o.endDate;
		},

		setEndDate: function (endDate) {
			this._process_options({ endDate: endDate });
			this.update();
			this.updateNavArrows();
			return this;
		},

		setDaysOfWeekDisabled: function (daysOfWeekDisabled) {
			this._process_options({ daysOfWeekDisabled: daysOfWeekDisabled });
			this.update();
			return this;
		},

		setDaysOfWeekHighlighted: function (daysOfWeekHighlighted) {
			this._process_options({ daysOfWeekHighlighted: daysOfWeekHighlighted });
			this.update();
			return this;
		},

		setDatesDisabled: function (datesDisabled) {
			this._process_options({ datesDisabled: datesDisabled });
			this.update();
			return this;
		},

		place: function () {
			if (this.isInline)
				return this;
			var calendarWidth = this.picker.outerWidth(),
				calendarHeight = this.picker.outerHeight(),
				visualPadding = 10,
				container = $(this.o.container),
				windowWidth = container.width(),
				scrollTop = this.o.container === 'body' ? $(document).scrollTop() : container.scrollTop(),
				appendOffset = container.offset();

			var parentsZindex = [0];
			this.element.parents().each(function () {
				var itemZIndex = $(this).css('z-index');
				if (itemZIndex !== 'auto' && Number(itemZIndex) !== 0) parentsZindex.push(Number(itemZIndex));
			});
			var zIndex = Math.max.apply(Math, parentsZindex) + this.o.zIndexOffset;
			var offset = this.component ? this.component.parent().offset() : this.element.offset();
			var height = this.component ? this.component.outerHeight(true) : this.element.outerHeight(false);
			var width = this.component ? this.component.outerWidth(true) : this.element.outerWidth(false);
			var left = offset.left - appendOffset.left;
			var top = offset.top - appendOffset.top;

			if (this.o.container !== 'body') {
				top += scrollTop;
			}

			this.picker.removeClass(
				'datepicker-orient-top datepicker-orient-bottom ' +
				'datepicker-orient-right datepicker-orient-left'
			);

			if (this.o.orientation.x !== 'auto') {
				this.picker.addClass('datepicker-orient-' + this.o.orientation.x);
				if (this.o.orientation.x === 'right')
					left -= calendarWidth - width;
			}
			// auto x orientation is best-placement: if it crosses a window
			// edge, fudge it sideways
			else {
				if (offset.left < 0) {
					// component is outside the window on the left side. Move it into visible range
					this.picker.addClass('datepicker-orient-left');
					left -= offset.left - visualPadding;
				} else if (left + calendarWidth > windowWidth) {
					// the calendar passes the widow right edge. Align it to component right side
					this.picker.addClass('datepicker-orient-right');
					left += width - calendarWidth;
				} else {
					if (this.o.rtl) {
						// Default to right
						this.picker.addClass('datepicker-orient-right');
					} else {
						// Default to left
						this.picker.addClass('datepicker-orient-left');
					}
				}
			}

			// auto y orientation is best-situation: top or bottom, no fudging,
			// decision based on which shows more of the calendar
			var yorient = this.o.orientation.y,
				top_overflow;
			if (yorient === 'auto') {
				top_overflow = -scrollTop + top - calendarHeight;
				yorient = top_overflow < 0 ? 'bottom' : 'top';
			}

			this.picker.addClass('datepicker-orient-' + yorient);
			if (yorient === 'top')
				top -= calendarHeight + parseInt(this.picker.css('padding-top'));
			else
				top += height;

			if (this.o.rtl) {
				var right = windowWidth - (left + width);
				this.picker.css({
					top: top,
					right: right,
					zIndex: zIndex
				});
			} else {
				this.picker.css({
					top: top,
					left: left,
					zIndex: zIndex
				});
			}
			return this;
		},

		_allow_update: true,
		update: function () {
			if (!this._allow_update)
				return this;

			var oldDates = this.dates.copy(),
				dates = [],
				fromArgs = false;
			if (arguments.length) {
				$.each(arguments, $.proxy(function (i, date) {
					if (date instanceof Date)
						date = this._local_to_utc(date);
					dates.push(date);
				}, this));
				fromArgs = true;
			} else {
				dates = this.isInput
					? this.element.val()
					: this.element.data('date') || this.inputField.val();
				if (dates && this.o.multidate)
					dates = dates.split(this.o.multidateSeparator);
				else
					dates = [dates];
				delete this.element.data().date;
			}

			dates = $.map(dates, $.proxy(function (date) {
				return DPGlobal.parseDate(date, this.o.format, this.o.language, this.o.assumeNearbyYear);
			}, this));
			dates = $.grep(dates, $.proxy(function (date) {
				return (
					!this.dateWithinRange(date) ||
					!date
				);
			}, this), true);
			this.dates.replace(dates);

			if (this.o.updateViewDate) {
				if (this.dates.length)
					this.viewDate = new Date(this.dates.get(-1));
				else if (this.viewDate < this.o.startDate)
					this.viewDate = new Date(this.o.startDate);
				else if (this.viewDate > this.o.endDate)
					this.viewDate = new Date(this.o.endDate);
				else
					this.viewDate = this.o.defaultViewDate;
			}

			if (fromArgs) {
				// setting date by clicking
				this.setValue();
				this.element.change();
			}
			else if (this.dates.length) {
				// setting date by typing
				if (String(oldDates) !== String(this.dates) && fromArgs) {
					this._trigger('changeDate');
					this.element.change();
				}
			}
			if (!this.dates.length && oldDates.length) {
				this._trigger('clearDate');
				this.element.change();
			}

			this.fill();
			return this;
		},

		fillDow: function () {
			if (this.o.showWeekDays) {
				var dowCnt = this.o.weekStart,
					html = '<tr>';
				if (this.o.calendarWeeks) {
					html += '<th class="cw">&#160;</th>';
				}
				while (dowCnt < this.o.weekStart + 7) {
					html += '<th class="dow';
					if ($.inArray(dowCnt, this.o.daysOfWeekDisabled) !== -1)
						html += ' disabled';
					html += '">' + dates[this.o.language].daysMin[(dowCnt++) % 7] + '</th>';
				}
				html += '</tr>';
				this.picker.find('.datepicker-days thead').append(html);
			}
		},

		fillMonths: function () {
			var localDate = this._utc_to_local(this.viewDate);
			var html = '';
			var focused;
			for (var i = 0; i < 12; i++) {
				focused = localDate && localDate.getMonth() === i ? ' focused' : '';
				html += '<span class="month' + focused + '">' + dates[this.o.language].monthsShort[i] + '</span>';
			}
			this.picker.find('.datepicker-months td').html(html);
		},

		setRange: function (range) {
			if (!range || !range.length)
				delete this.range;
			else
				this.range = $.map(range, function (d) {
					return d.valueOf();
				});
			this.fill();
		},

		getClassNames: function (date) {
			var cls = [],
				year = this.viewDate.getUTCFullYear(),
				month = this.viewDate.getUTCMonth(),
				today = UTCToday();
			if (date.getUTCFullYear() < year || (date.getUTCFullYear() === year && date.getUTCMonth() < month)) {
				cls.push('old');
			} else if (date.getUTCFullYear() > year || (date.getUTCFullYear() === year && date.getUTCMonth() > month)) {
				cls.push('new');
			}
			if (this.focusDate && date.valueOf() === this.focusDate.valueOf())
				cls.push('focused');
			// Compare internal UTC date with UTC today, not local today
			if (this.o.todayHighlight && isUTCEquals(date, today)) {
				cls.push('today');
			}
			if (this.dates.contains(date) !== -1)
				cls.push('active');
			if (!this.dateWithinRange(date)) {
				cls.push('disabled');
			}
			if (this.dateIsDisabled(date)) {
				cls.push('disabled', 'disabled-date');
			}
			if ($.inArray(date.getUTCDay(), this.o.daysOfWeekHighlighted) !== -1) {
				cls.push('highlighted');
			}

			if (this.range) {
				if (date > this.range[0] && date < this.range[this.range.length - 1]) {
					cls.push('range');
				}
				if ($.inArray(date.valueOf(), this.range) !== -1) {
					cls.push('selected');
				}
				if (date.valueOf() === this.range[0]) {
					cls.push('range-start');
				}
				if (date.valueOf() === this.range[this.range.length - 1]) {
					cls.push('range-end');
				}
			}
			return cls;
		},

		_fill_yearsView: function (selector, cssClass, factor, year, startYear, endYear, beforeFn) {
			var html = '';
			var step = factor / 10;
			var view = this.picker.find(selector);
			var startVal = Math.floor(year / factor) * factor;
			var endVal = startVal + step * 9;
			var focusedVal = Math.floor(this.viewDate.getFullYear() / step) * step;
			var selected = $.map(this.dates, function (d) {
				return Math.floor(d.getUTCFullYear() / step) * step;
			});

			var classes, tooltip, before;
			for (var currVal = startVal - step; currVal <= endVal + step; currVal += step) {
				classes = [cssClass];
				tooltip = null;

				if (currVal === startVal - step) {
					classes.push('old');
				} else if (currVal === endVal + step) {
					classes.push('new');
				}
				if ($.inArray(currVal, selected) !== -1) {
					classes.push('active');
				}
				if (currVal < startYear || currVal > endYear) {
					classes.push('disabled');
				}
				if (currVal === focusedVal) {
					classes.push('focused');
				}

				if (beforeFn !== $.noop) {
					before = beforeFn(new Date(currVal, 0, 1));
					if (before === undefined) {
						before = {};
					} else if (typeof before === 'boolean') {
						before = { enabled: before };
					} else if (typeof before === 'string') {
						before = { classes: before };
					}
					if (before.enabled === false) {
						classes.push('disabled');
					}
					if (before.classes) {
						classes = classes.concat(before.classes.split(/\s+/));
					}
					if (before.tooltip) {
						tooltip = before.tooltip;
					}
				}

				html += '<span class="' + classes.join(' ') + '"' + (tooltip ? ' title="' + tooltip + '"' : '') + '>' + currVal + '</span>';
			}

			view.find('.datepicker-switch').text(startVal + '-' + endVal);
			view.find('td').html(html);
		},

		fill: function () {
			var d = new Date(this.viewDate),
				year = d.getUTCFullYear(),
				month = d.getUTCMonth(),
				startYear = this.o.startDate !== -Infinity ? this.o.startDate.getUTCFullYear() : -Infinity,
				startMonth = this.o.startDate !== -Infinity ? this.o.startDate.getUTCMonth() : -Infinity,
				endYear = this.o.endDate !== Infinity ? this.o.endDate.getUTCFullYear() : Infinity,
				endMonth = this.o.endDate !== Infinity ? this.o.endDate.getUTCMonth() : Infinity,
				todaytxt = dates[this.o.language].today || dates['en'].today || '',
				cleartxt = dates[this.o.language].clear || dates['en'].clear || '',
				titleFormat = dates[this.o.language].titleFormat || dates['en'].titleFormat,
				todayDate = UTCToday(),
				titleBtnVisible = (this.o.todayBtn === true || this.o.todayBtn === 'linked') && todayDate >= this.o.startDate && todayDate <= this.o.endDate && !this.weekOfDateIsDisabled(todayDate),
				tooltip,
				before;
			if (isNaN(year) || isNaN(month))
				return;
			this.picker.find('.datepicker-days .datepicker-switch')
				.text(DPGlobal.formatDate(d, titleFormat, this.o.language));
			this.picker.find('tfoot .today')
				.text(todaytxt)
				.css('display', titleBtnVisible ? 'table-cell' : 'none');
			this.picker.find('tfoot .clear')
				.text(cleartxt)
				.css('display', this.o.clearBtn === true ? 'table-cell' : 'none');
			this.picker.find('thead .datepicker-title')
				.text(this.o.title)
				.css('display', typeof this.o.title === 'string' && this.o.title !== '' ? 'table-cell' : 'none');
			this.updateNavArrows();
			this.fillMonths();
			var prevMonth = UTCDate(year, month, 0),
				day = prevMonth.getUTCDate();
			prevMonth.setUTCDate(day - (prevMonth.getUTCDay() - this.o.weekStart + 7) % 7);
			var nextMonth = new Date(prevMonth);
			if (prevMonth.getUTCFullYear() < 100) {
				nextMonth.setUTCFullYear(prevMonth.getUTCFullYear());
			}
			nextMonth.setUTCDate(nextMonth.getUTCDate() + 42);
			nextMonth = nextMonth.valueOf();
			var html = [];
			var weekDay, clsName;
			while (prevMonth.valueOf() < nextMonth) {
				weekDay = prevMonth.getUTCDay();
				if (weekDay === this.o.weekStart) {
					html.push('<tr>');
					if (this.o.calendarWeeks) {
						// ISO 8601: First week contains first thursday.
						// ISO also states week starts on Monday, but we can be more abstract here.
						var
							// Start of current week: based on weekstart/current date
							ws = new Date(+prevMonth + (this.o.weekStart - weekDay - 7) % 7 * 864e5),
							// Thursday of this week
							th = new Date(Number(ws) + (7 + 4 - ws.getUTCDay()) % 7 * 864e5),
							// First Thursday of year, year from thursday
							yth = new Date(Number(yth = UTCDate(th.getUTCFullYear(), 0, 1)) + (7 + 4 - yth.getUTCDay()) % 7 * 864e5),
							// Calendar week: ms between thursdays, div ms per day, div 7 days
							calWeek = (th - yth) / 864e5 / 7 + 1;
						html.push('<td class="cw">' + calWeek + '</td>');
					}
				}
				clsName = this.getClassNames(prevMonth);
				clsName.push('day');

				var content = prevMonth.getUTCDate();

				if (this.o.beforeShowDay !== $.noop) {
					before = this.o.beforeShowDay(this._utc_to_local(prevMonth));
					if (before === undefined)
						before = {};
					else if (typeof before === 'boolean')
						before = { enabled: before };
					else if (typeof before === 'string')
						before = { classes: before };
					if (before.enabled === false)
						clsName.push('disabled');
					if (before.classes)
						clsName = clsName.concat(before.classes.split(/\s+/));
					if (before.tooltip)
						tooltip = before.tooltip;
					if (before.content)
						content = before.content;
				}

				//Check if uniqueSort exists (supported by jquery >=1.12 and >=2.2)
				//Fallback to unique function for older jquery versions
				if ($.isFunction($.uniqueSort)) {
					clsName = $.uniqueSort(clsName);
				} else {
					clsName = $.unique(clsName);
				}

				html.push('<td class="' + clsName.join(' ') + '"' + (tooltip ? ' title="' + tooltip + '"' : '') + ' data-date="' + prevMonth.getTime().toString() + '">' + content + '</td>');
				tooltip = null;
				if (weekDay === this.o.weekEnd) {
					html.push('</tr>');
				}
				prevMonth.setUTCDate(prevMonth.getUTCDate() + 1);
			}
			this.picker.find('.datepicker-days tbody').html(html.join(''));

			var monthsTitle = dates[this.o.language].monthsTitle || dates['en'].monthsTitle || 'Months';
			var months = this.picker.find('.datepicker-months')
				.find('.datepicker-switch')
				.text(this.o.maxViewMode < 2 ? monthsTitle : year)
				.end()
				.find('tbody span').removeClass('active');

			$.each(this.dates, function (i, d) {
				if (d.getUTCFullYear() === year)
					months.eq(d.getUTCMonth()).addClass('active');
			});

			if (year < startYear || year > endYear) {
				months.addClass('disabled');
			}
			if (year === startYear) {
				months.slice(0, startMonth).addClass('disabled');
			}
			if (year === endYear) {
				months.slice(endMonth + 1).addClass('disabled');
			}

			if (this.o.beforeShowMonth !== $.noop) {
				var that = this;
				$.each(months, function (i, month) {
					var moDate = new Date(year, i, 1);
					var before = that.o.beforeShowMonth(moDate);
					if (before === undefined)
						before = {};
					else if (typeof before === 'boolean')
						before = { enabled: before };
					else if (typeof before === 'string')
						before = { classes: before };
					if (before.enabled === false && !$(month).hasClass('disabled'))
						$(month).addClass('disabled');
					if (before.classes)
						$(month).addClass(before.classes);
					if (before.tooltip)
						$(month).prop('title', before.tooltip);
				});
			}

			// Generating decade/years picker
			this._fill_yearsView(
				'.datepicker-years',
				'year',
				10,
				year,
				startYear,
				endYear,
				this.o.beforeShowYear
			);

			// Generating century/decades picker
			this._fill_yearsView(
				'.datepicker-decades',
				'decade',
				100,
				year,
				startYear,
				endYear,
				this.o.beforeShowDecade
			);

			// Generating millennium/centuries picker
			this._fill_yearsView(
				'.datepicker-centuries',
				'century',
				1000,
				year,
				startYear,
				endYear,
				this.o.beforeShowCentury
			);
		},

		updateNavArrows: function () {
			if (!this._allow_update)
				return;

			var d = new Date(this.viewDate),
				year = d.getUTCFullYear(),
				month = d.getUTCMonth(),
				startYear = this.o.startDate !== -Infinity ? this.o.startDate.getUTCFullYear() : -Infinity,
				startMonth = this.o.startDate !== -Infinity ? this.o.startDate.getUTCMonth() : -Infinity,
				endYear = this.o.endDate !== Infinity ? this.o.endDate.getUTCFullYear() : Infinity,
				endMonth = this.o.endDate !== Infinity ? this.o.endDate.getUTCMonth() : Infinity,
				prevIsDisabled,
				nextIsDisabled,
				factor = 1;
			switch (this.viewMode) {
				case 4:
					factor *= 10;
				/* falls through */
				case 3:
					factor *= 10;
				/* falls through */
				case 2:
					factor *= 10;
				/* falls through */
				case 1:
					prevIsDisabled = Math.floor(year / factor) * factor <= startYear;
					nextIsDisabled = Math.floor(year / factor) * factor + factor > endYear;
					break;
				case 0:
					prevIsDisabled = year <= startYear && month <= startMonth;
					nextIsDisabled = year >= endYear && month >= endMonth;
					break;
			}

			this.picker.find('.prev').toggleClass('disabled', prevIsDisabled);
			this.picker.find('.next').toggleClass('disabled', nextIsDisabled);
		},

		click: function (e) {
			e.preventDefault();
			e.stopPropagation();

			var target, dir, day, year, month;
			target = $(e.target);

			// Clicked on the switch
			if (target.hasClass('datepicker-switch') && this.viewMode !== this.o.maxViewMode) {
				this.setViewMode(this.viewMode + 1);
			}

			// Clicked on today button
			if (target.hasClass('today') && !target.hasClass('day')) {
				this.setViewMode(0);
				this._setDate(UTCToday(), this.o.todayBtn === 'linked' ? null : 'view');
			}

			// Clicked on clear button
			if (target.hasClass('clear')) {
				this.clearDates();
			}

			if (!target.hasClass('disabled')) {
				// Clicked on a month, year, decade, century
				if (target.hasClass('month')
					|| target.hasClass('year')
					|| target.hasClass('decade')
					|| target.hasClass('century')) {
					this.viewDate.setUTCDate(1);

					day = 1;
					if (this.viewMode === 1) {
						month = target.parent().find('span').index(target);
						year = this.viewDate.getUTCFullYear();
						this.viewDate.setUTCMonth(month);
					} else {
						month = 0;
						year = Number(target.text());
						this.viewDate.setUTCFullYear(year);
					}

					this._trigger(DPGlobal.viewModes[this.viewMode - 1].e, this.viewDate);

					if (this.viewMode === this.o.minViewMode) {
						this._setDate(UTCDate(year, month, day));
					} else {
						this.setViewMode(this.viewMode - 1);
						this.fill();
					}
				}
			}

			if (this.picker.is(':visible') && this._focused_from) {
				this._focused_from.focus();
			}
			delete this._focused_from;
		},

		dayCellClick: function (e) {
			var $target = $(e.currentTarget);
			var timestamp = $target.data('date');
			var date = new Date(timestamp);

			if (this.o.updateViewDate) {
				if (date.getUTCFullYear() !== this.viewDate.getUTCFullYear()) {
					this._trigger('changeYear', this.viewDate);
				}

				if (date.getUTCMonth() !== this.viewDate.getUTCMonth()) {
					this._trigger('changeMonth', this.viewDate);
				}
			}
			this._setDate(date);
		},

		// Clicked on prev or next
		navArrowsClick: function (e) {
			var $target = $(e.currentTarget);
			var dir = $target.hasClass('prev') ? -1 : 1;
			if (this.viewMode !== 0) {
				dir *= DPGlobal.viewModes[this.viewMode].navStep * 12;
			}
			this.viewDate = this.moveMonth(this.viewDate, dir);
			this._trigger(DPGlobal.viewModes[this.viewMode].e, this.viewDate);
			this.fill();
		},

		_toggle_multidate: function (date) {
			var ix = this.dates.contains(date);
			if (!date) {
				this.dates.clear();
			}

			if (ix !== -1) {
				if (this.o.multidate === true || this.o.multidate > 1 || this.o.toggleActive) {
					this.dates.remove(ix);
				}
			} else if (this.o.multidate === false) {
				this.dates.clear();
				this.dates.push(date);
			}
			else {
				this.dates.push(date);
			}

			if (typeof this.o.multidate === 'number')
				while (this.dates.length > this.o.multidate)
					this.dates.remove(0);
		},

		_setDate: function (date, which) {
			if (!which || which === 'date')
				this._toggle_multidate(date && new Date(date));
			if ((!which && this.o.updateViewDate) || which === 'view')
				this.viewDate = date && new Date(date);

			this.fill();
			this.setValue();
			if (!which || which !== 'view') {
				this._trigger('changeDate');
			}
			this.inputField.trigger('change');
			if (this.o.autoclose && (!which || which === 'date')) {
				this.hide();
			}
		},

		moveDay: function (date, dir) {
			var newDate = new Date(date);
			newDate.setUTCDate(date.getUTCDate() + dir);

			return newDate;
		},

		moveWeek: function (date, dir) {
			return this.moveDay(date, dir * 7);
		},

		moveMonth: function (date, dir) {
			if (!isValidDate(date))
				return this.o.defaultViewDate;
			if (!dir)
				return date;
			var new_date = new Date(date.valueOf()),
				day = new_date.getUTCDate(),
				month = new_date.getUTCMonth(),
				mag = Math.abs(dir),
				new_month, test;
			dir = dir > 0 ? 1 : -1;
			if (mag === 1) {
				test = dir === -1
					// If going back one month, make sure month is not current month
					// (eg, Mar 31 -> Feb 31 == Feb 28, not Mar 02)
					? function () {
						return new_date.getUTCMonth() === month;
					}
					// If going forward one month, make sure month is as expected
					// (eg, Jan 31 -> Feb 31 == Feb 28, not Mar 02)
					: function () {
						return new_date.getUTCMonth() !== new_month;
					};
				new_month = month + dir;
				new_date.setUTCMonth(new_month);
				// Dec -> Jan (12) or Jan -> Dec (-1) -- limit expected date to 0-11
				new_month = (new_month + 12) % 12;
			}
			else {
				// For magnitudes >1, move one month at a time...
				for (var i = 0; i < mag; i++)
					// ...which might decrease the day (eg, Jan 31 to Feb 28, etc)...
					new_date = this.moveMonth(new_date, dir);
				// ...then reset the day, keeping it in the new month
				new_month = new_date.getUTCMonth();
				new_date.setUTCDate(day);
				test = function () {
					return new_month !== new_date.getUTCMonth();
				};
			}
			// Common date-resetting loop -- if date is beyond end of month, make it
			// end of month
			while (test()) {
				new_date.setUTCDate(--day);
				new_date.setUTCMonth(new_month);
			}
			return new_date;
		},

		moveYear: function (date, dir) {
			return this.moveMonth(date, dir * 12);
		},

		moveAvailableDate: function (date, dir, fn) {
			do {
				date = this[fn](date, dir);

				if (!this.dateWithinRange(date))
					return false;

				fn = 'moveDay';
			}
			while (this.dateIsDisabled(date));

			return date;
		},

		weekOfDateIsDisabled: function (date) {
			return $.inArray(date.getUTCDay(), this.o.daysOfWeekDisabled) !== -1;
		},

		dateIsDisabled: function (date) {
			return (
				this.weekOfDateIsDisabled(date) ||
				$.grep(this.o.datesDisabled, function (d) {
					return isUTCEquals(date, d);
				}).length > 0
			);
		},

		dateWithinRange: function (date) {
			return date >= this.o.startDate && date <= this.o.endDate;
		},

		keydown: function (e) {
			if (!this.picker.is(':visible')) {
				if (e.keyCode === 40 || e.keyCode === 27) { // allow down to re-show picker
					this.show();
					e.stopPropagation();
				}
				return;
			}
			var dateChanged = false,
				dir, newViewDate,
				focusDate = this.focusDate || this.viewDate;
			switch (e.keyCode) {
				case 27: // escape
					if (this.focusDate) {
						this.focusDate = null;
						this.viewDate = this.dates.get(-1) || this.viewDate;
						this.fill();
					}
					else
						this.hide();
					e.preventDefault();
					e.stopPropagation();
					break;
				case 37: // left
				case 38: // up
				case 39: // right
				case 40: // down
					if (!this.o.keyboardNavigation || this.o.daysOfWeekDisabled.length === 7)
						break;
					dir = e.keyCode === 37 || e.keyCode === 38 ? -1 : 1;
					if (this.viewMode === 0) {
						if (e.ctrlKey) {
							newViewDate = this.moveAvailableDate(focusDate, dir, 'moveYear');

							if (newViewDate)
								this._trigger('changeYear', this.viewDate);
						} else if (e.shiftKey) {
							newViewDate = this.moveAvailableDate(focusDate, dir, 'moveMonth');

							if (newViewDate)
								this._trigger('changeMonth', this.viewDate);
						} else if (e.keyCode === 37 || e.keyCode === 39) {
							newViewDate = this.moveAvailableDate(focusDate, dir, 'moveDay');
						} else if (!this.weekOfDateIsDisabled(focusDate)) {
							newViewDate = this.moveAvailableDate(focusDate, dir, 'moveWeek');
						}
					} else if (this.viewMode === 1) {
						if (e.keyCode === 38 || e.keyCode === 40) {
							dir = dir * 4;
						}
						newViewDate = this.moveAvailableDate(focusDate, dir, 'moveMonth');
					} else if (this.viewMode === 2) {
						if (e.keyCode === 38 || e.keyCode === 40) {
							dir = dir * 4;
						}
						newViewDate = this.moveAvailableDate(focusDate, dir, 'moveYear');
					}
					if (newViewDate) {
						this.focusDate = this.viewDate = newViewDate;
						this.setValue();
						this.fill();
						e.preventDefault();
					}
					break;
				case 13: // enter
					if (!this.o.forceParse)
						break;
					focusDate = this.focusDate || this.dates.get(-1) || this.viewDate;
					if (this.o.keyboardNavigation) {
						this._toggle_multidate(focusDate);
						dateChanged = true;
					}
					this.focusDate = null;
					this.viewDate = this.dates.get(-1) || this.viewDate;
					this.setValue();
					this.fill();
					if (this.picker.is(':visible')) {
						e.preventDefault();
						e.stopPropagation();
						if (this.o.autoclose)
							this.hide();
					}
					break;
				case 9: // tab
					this.focusDate = null;
					this.viewDate = this.dates.get(-1) || this.viewDate;
					this.fill();
					this.hide();
					break;
			}
			if (dateChanged) {
				if (this.dates.length)
					this._trigger('changeDate');
				else
					this._trigger('clearDate');
				this.inputField.trigger('change');
			}
		},

		setViewMode: function (viewMode) {
			this.viewMode = viewMode;
			this.picker
				.children('div')
				.hide()
				.filter('.datepicker-' + DPGlobal.viewModes[this.viewMode].clsName)
				.show();
			this.updateNavArrows();
			this._trigger('changeViewMode', new Date(this.viewDate));
		}
	};

	var DateRangePicker = function (element, options) {
		$.data(element, 'datepicker', this);
		this.element = $(element);
		this.inputs = $.map(options.inputs, function (i) {
			return i.jquery ? i[0] : i;
		});
		delete options.inputs;

		this.keepEmptyValues = options.keepEmptyValues;
		delete options.keepEmptyValues;

		datepickerPlugin.call($(this.inputs), options)
			.on('changeDate', $.proxy(this.dateUpdated, this));

		this.pickers = $.map(this.inputs, function (i) {
			return $.data(i, 'datepicker');
		});
		this.updateDates();
	};
	DateRangePicker.prototype = {
		updateDates: function () {
			this.dates = $.map(this.pickers, function (i) {
				return i.getUTCDate();
			});
			this.updateRanges();
		},
		updateRanges: function () {
			var range = $.map(this.dates, function (d) {
				return d.valueOf();
			});
			$.each(this.pickers, function (i, p) {
				p.setRange(range);
			});
		},
		clearDates: function () {
			$.each(this.pickers, function (i, p) {
				p.clearDates();
			});
		},
		dateUpdated: function (e) {
			// `this.updating` is a workaround for preventing infinite recursion
			// between `changeDate` triggering and `setUTCDate` calling.  Until
			// there is a better mechanism.
			if (this.updating)
				return;
			this.updating = true;

			var dp = $.data(e.target, 'datepicker');

			if (dp === undefined) {
				return;
			}

			var new_date = dp.getUTCDate(),
				keep_empty_values = this.keepEmptyValues,
				i = $.inArray(e.target, this.inputs),
				j = i - 1,
				k = i + 1,
				l = this.inputs.length;
			if (i === -1)
				return;

			$.each(this.pickers, function (i, p) {
				if (!p.getUTCDate() && (p === dp || !keep_empty_values))
					p.setUTCDate(new_date);
			});

			if (new_date < this.dates[j]) {
				// Date being moved earlier/left
				while (j >= 0 && new_date < this.dates[j]) {
					this.pickers[j--].setUTCDate(new_date);
				}
			} else if (new_date > this.dates[k]) {
				// Date being moved later/right
				while (k < l && new_date > this.dates[k]) {
					this.pickers[k++].setUTCDate(new_date);
				}
			}
			this.updateDates();

			delete this.updating;
		},
		destroy: function () {
			$.map(this.pickers, function (p) { p.destroy(); });
			$(this.inputs).off('changeDate', this.dateUpdated);
			delete this.element.data().datepicker;
		},
		remove: alias('destroy', 'Method `remove` is deprecated and will be removed in version 2.0. Use `destroy` instead')
	};

	function opts_from_el(el, prefix) {
		// Derive options from element data-attrs
		var data = $(el).data(),
			out = {}, inkey,
			replace = new RegExp('^' + prefix.toLowerCase() + '([A-Z])');
		prefix = new RegExp('^' + prefix.toLowerCase());
		function re_lower(_, a) {
			return a.toLowerCase();
		}
		for (var key in data)
			if (prefix.test(key)) {
				inkey = key.replace(replace, re_lower);
				out[inkey] = data[key];
			}
		return out;
	}

	function opts_from_locale(lang) {
		// Derive options from locale plugins
		var out = {};
		// Check if "de-DE" style date is available, if not language should
		// fallback to 2 letter code eg "de"
		if (!dates[lang]) {
			lang = lang.split('-')[0];
			if (!dates[lang])
				return;
		}
		var d = dates[lang];
		$.each(locale_opts, function (i, k) {
			if (k in d)
				out[k] = d[k];
		});
		return out;
	}

	var old = $.fn.datepicker;
	var datepickerPlugin = function (option) {
		var args = Array.apply(null, arguments);
		args.shift();
		var internal_return;
		this.each(function () {
			var $this = $(this),
				data = $this.data('datepicker'),
				options = typeof option === 'object' && option;
			if (!data) {
				var elopts = opts_from_el(this, 'date'),
					// Preliminary otions
					xopts = $.extend({}, defaults, elopts, options),
					locopts = opts_from_locale(xopts.language),
					// Options priority: js args, data-attrs, locales, defaults
					opts = $.extend({}, defaults, locopts, elopts, options);
				if ($this.hasClass('input-daterange') || opts.inputs) {
					$.extend(opts, {
						inputs: opts.inputs || $this.find('input').toArray()
					});
					data = new DateRangePicker(this, opts);
				}
				else {
					data = new Datepicker(this, opts);
				}
				$this.data('datepicker', data);
			}
			if (typeof option === 'string' && typeof data[option] === 'function') {
				internal_return = data[option].apply(data, args);
			}
		});

		if (
			internal_return === undefined ||
			internal_return instanceof Datepicker ||
			internal_return instanceof DateRangePicker
		)
			return this;

		if (this.length > 1)
			throw new Error('Using only allowed for the collection of a single element (' + option + ' function)');
		else
			return internal_return;
	};
	$.fn.datepicker = datepickerPlugin;

	var defaults = $.fn.datepicker.defaults = {
		assumeNearbyYear: false,
		autoclose: false,
		beforeShowDay: $.noop,
		beforeShowMonth: $.noop,
		beforeShowYear: $.noop,
		beforeShowDecade: $.noop,
		beforeShowCentury: $.noop,
		calendarWeeks: false,
		clearBtn: false,
		toggleActive: false,
		daysOfWeekDisabled: [],
		daysOfWeekHighlighted: [],
		datesDisabled: [],
		endDate: Infinity,
		forceParse: true,
		format: 'mm/dd/yyyy',
		keepEmptyValues: false,
		keyboardNavigation: true,
		language: 'en',
		minViewMode: 0,
		maxViewMode: 4,
		multidate: false,
		multidateSeparator: ',',
		orientation: "auto",
		rtl: false,
		startDate: -Infinity,
		startView: 0,
		todayBtn: false,
		todayHighlight: false,
		updateViewDate: true,
		weekStart: 0,
		disableTouchKeyboard: false,
		enableOnReadonly: true,
		showOnFocus: true,
		zIndexOffset: 10,
		container: 'body',
		immediateUpdates: false,
		title: '',
		templates: {
			leftArrow: '&#x00AB;',
			rightArrow: '&#x00BB;'
		},
		showWeekDays: true
	};
	var locale_opts = $.fn.datepicker.locale_opts = [
		'format',
		'rtl',
		'weekStart'
	];
	$.fn.datepicker.Constructor = Datepicker;
	var dates = $.fn.datepicker.dates = {
		en: {
			days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
			daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
			daysMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
			months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
			monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
			today: "Today",
			clear: "Clear",
			titleFormat: "MM yyyy"
		}
	};

	var DPGlobal = {
		viewModes: [
			{
				names: ['days', 'month'],
				clsName: 'days',
				e: 'changeMonth'
			},
			{
				names: ['months', 'year'],
				clsName: 'months',
				e: 'changeYear',
				navStep: 1
			},
			{
				names: ['years', 'decade'],
				clsName: 'years',
				e: 'changeDecade',
				navStep: 10
			},
			{
				names: ['decades', 'century'],
				clsName: 'decades',
				e: 'changeCentury',
				navStep: 100
			},
			{
				names: ['centuries', 'millennium'],
				clsName: 'centuries',
				e: 'changeMillennium',
				navStep: 1000
			}
		],
		validParts: /dd?|DD?|mm?|MM?|yy(?:yy)?/g,
		nonpunctuation: /[^ -\/:-@\u5e74\u6708\u65e5\[-`{-~\t\n\r]+/g,
		parseFormat: function (format) {
			if (typeof format.toValue === 'function' && typeof format.toDisplay === 'function')
				return format;
			// IE treats \0 as a string end in inputs (truncating the value),
			// so it's a bad format delimiter, anyway
			var separators = format.replace(this.validParts, '\0').split('\0'),
				parts = format.match(this.validParts);
			if (!separators || !separators.length || !parts || parts.length === 0) {
				throw new Error("Invalid date format.");
			}
			return { separators: separators, parts: parts };
		},
		parseDate: function (date, format, language, assumeNearby) {
			if (!date)
				return undefined;
			if (date instanceof Date)
				return date;
			if (typeof format === 'string')
				format = DPGlobal.parseFormat(format);
			if (format.toValue)
				return format.toValue(date, format, language);
			var fn_map = {
				d: 'moveDay',
				m: 'moveMonth',
				w: 'moveWeek',
				y: 'moveYear'
			},
				dateAliases = {
					yesterday: '-1d',
					today: '+0d',
					tomorrow: '+1d'
				},
				parts, part, dir, i, fn;
			if (date in dateAliases) {
				date = dateAliases[date];
			}
			if (/^[\-+]\d+[dmwy]([\s,]+[\-+]\d+[dmwy])*$/i.test(date)) {
				parts = date.match(/([\-+]\d+)([dmwy])/gi);
				date = new Date();
				for (i = 0; i < parts.length; i++) {
					part = parts[i].match(/([\-+]\d+)([dmwy])/i);
					dir = Number(part[1]);
					fn = fn_map[part[2].toLowerCase()];
					date = Datepicker.prototype[fn](date, dir);
				}
				return Datepicker.prototype._zero_utc_time(date);
			}

			parts = date && date.match(this.nonpunctuation) || [];

			function applyNearbyYear(year, threshold) {
				if (threshold === true)
					threshold = 10;

				// if year is 2 digits or less, than the user most likely is trying to get a recent century
				if (year < 100) {
					year += 2000;
					// if the new year is more than threshold years in advance, use last century
					if (year > ((new Date()).getFullYear() + threshold)) {
						year -= 100;
					}
				}

				return year;
			}

			var parsed = {},
				setters_order = ['yyyy', 'yy', 'M', 'MM', 'm', 'mm', 'd', 'dd'],
				setters_map = {
					yyyy: function (d, v) {
						return d.setUTCFullYear(assumeNearby ? applyNearbyYear(v, assumeNearby) : v);
					},
					m: function (d, v) {
						if (isNaN(d))
							return d;
						v -= 1;
						while (v < 0) v += 12;
						v %= 12;
						d.setUTCMonth(v);
						while (d.getUTCMonth() !== v)
							d.setUTCDate(d.getUTCDate() - 1);
						return d;
					},
					d: function (d, v) {
						return d.setUTCDate(v);
					}
				},
				val, filtered;
			setters_map['yy'] = setters_map['yyyy'];
			setters_map['M'] = setters_map['MM'] = setters_map['mm'] = setters_map['m'];
			setters_map['dd'] = setters_map['d'];
			date = UTCToday();
			var fparts = format.parts.slice();
			// Remove noop parts
			if (parts.length !== fparts.length) {
				fparts = $(fparts).filter(function (i, p) {
					return $.inArray(p, setters_order) !== -1;
				}).toArray();
			}
			// Process remainder
			function match_part() {
				var m = this.slice(0, parts[i].length),
					p = parts[i].slice(0, m.length);
				return m.toLowerCase() === p.toLowerCase();
			}
			if (parts.length === fparts.length) {
				var cnt;
				for (i = 0, cnt = fparts.length; i < cnt; i++) {
					val = parseInt(parts[i], 10);
					part = fparts[i];
					if (isNaN(val)) {
						switch (part) {
							case 'MM':
								filtered = $(dates[language].months).filter(match_part);
								val = $.inArray(filtered[0], dates[language].months) + 1;
								break;
							case 'M':
								filtered = $(dates[language].monthsShort).filter(match_part);
								val = $.inArray(filtered[0], dates[language].monthsShort) + 1;
								break;
						}
					}
					parsed[part] = val;
				}
				var _date, s;
				for (i = 0; i < setters_order.length; i++) {
					s = setters_order[i];
					if (s in parsed && !isNaN(parsed[s])) {
						_date = new Date(date);
						setters_map[s](_date, parsed[s]);
						if (!isNaN(_date))
							date = _date;
					}
				}
			}
			return date;
		},
		formatDate: function (date, format, language) {
			if (!date)
				return '';
			if (typeof format === 'string')
				format = DPGlobal.parseFormat(format);
			if (format.toDisplay)
				return format.toDisplay(date, format, language);
			var val = {
				d: date.getUTCDate(),
				D: dates[language].daysShort[date.getUTCDay()],
				DD: dates[language].days[date.getUTCDay()],
				m: date.getUTCMonth() + 1,
				M: dates[language].monthsShort[date.getUTCMonth()],
				MM: dates[language].months[date.getUTCMonth()],
				yy: date.getUTCFullYear().toString().substring(2),
				yyyy: date.getUTCFullYear()
			};
			val.dd = (val.d < 10 ? '0' : '') + val.d;
			val.mm = (val.m < 10 ? '0' : '') + val.m;
			date = [];
			var seps = $.extend([], format.separators);
			for (var i = 0, cnt = format.parts.length; i <= cnt; i++) {
				if (seps.length)
					date.push(seps.shift());
				date.push(val[format.parts[i]]);
			}
			return date.join('');
		},
		headTemplate: '<thead>' +
			'<tr>' +
			'<th colspan="7" class="datepicker-title"></th>' +
			'</tr>' +
			'<tr>' +
			'<th class="prev">' + defaults.templates.leftArrow + '</th>' +
			'<th colspan="5" class="datepicker-switch"></th>' +
			'<th class="next">' + defaults.templates.rightArrow + '</th>' +
			'</tr>' +
			'</thead>',
		contTemplate: '<tbody><tr><td colspan="7"></td></tr></tbody>',
		footTemplate: '<tfoot>' +
			'<tr>' +
			'<th colspan="7" class="today"></th>' +
			'</tr>' +
			'<tr>' +
			'<th colspan="7" class="clear"></th>' +
			'</tr>' +
			'</tfoot>'
	};
	DPGlobal.template = '<div class="datepicker">' +
		'<div class="datepicker-days">' +
		'<table class="table-condensed">' +
		DPGlobal.headTemplate +
		'<tbody></tbody>' +
		DPGlobal.footTemplate +
		'</table>' +
		'</div>' +
		'<div class="datepicker-months">' +
		'<table class="table-condensed">' +
		DPGlobal.headTemplate +
		DPGlobal.contTemplate +
		DPGlobal.footTemplate +
		'</table>' +
		'</div>' +
		'<div class="datepicker-years">' +
		'<table class="table-condensed">' +
		DPGlobal.headTemplate +
		DPGlobal.contTemplate +
		DPGlobal.footTemplate +
		'</table>' +
		'</div>' +
		'<div class="datepicker-decades">' +
		'<table class="table-condensed">' +
		DPGlobal.headTemplate +
		DPGlobal.contTemplate +
		DPGlobal.footTemplate +
		'</table>' +
		'</div>' +
		'<div class="datepicker-centuries">' +
		'<table class="table-condensed">' +
		DPGlobal.headTemplate +
		DPGlobal.contTemplate +
		DPGlobal.footTemplate +
		'</table>' +
		'</div>' +
		'</div>';

	$.fn.datepicker.DPGlobal = DPGlobal;


	/* DATEPICKER NO CONFLICT
	* =================== */

	$.fn.datepicker.noConflict = function () {
		$.fn.datepicker = old;
		return this;
	};

	/* DATEPICKER VERSION
	 * =================== */
	$.fn.datepicker.version = '1.9.0';

	$.fn.datepicker.deprecated = function (msg) {
		var console = window.console;
		if (console && console.warn) {
			console.warn('DEPRECATED: ' + msg);
		}
	};


	/* DATEPICKER DATA-API
	* ================== */

	$(document).on(
		'focus.datepicker.data-api click.datepicker.data-api',
		'[data-provide="datepicker"]',
		function (e) {
			var $this = $(this);
			if ($this.data('datepicker'))
				return;
			e.preventDefault();
			// component click requires us to explicitly show it
			datepickerPlugin.call($this, 'show');
		}
	);
	$(function () {
		datepickerPlugin.call($('[data-provide="datepicker-inline"]'));
	});

}));


/**
 ********************************
 **        MODERNIZR           **
 ********************************
 */

;
window.Modernizr = function (a, b, c) {
	function x(a) {
		j.cssText = a
	}

	function y(a, b) {
		return x(prefixes.join(a + ";") + (b || ""))
	}

	function z(a, b) {
		return typeof a === b
	}

	function A(a, b) {
		return !!~("" + a).indexOf(b)
	}

	function B(a, b) {
		for (var d in a) {
			var e = a[d];
			if (!A(e, "-") && j[e] !== c) return b == "pfx" ? e : !0
		}
		return !1
	}

	function C(a, b, d) {
		for (var e in a) {
			var f = b[a[e]];
			if (f !== c) return d === !1 ? a[e] : z(f, "function") ? f.bind(d || b) : f
		}
		return !1
	}

	function D(a, b, c) {
		var d = a.charAt(0).toUpperCase() + a.slice(1),
			e = (a + " " + n.join(d + " ") + d).split(" ");
		return z(b, "string") || z(b, "undefined") ? B(e, b) : (e = (a + " " + o.join(d + " ") + d).split(" "), C(e, b, c))
	}
	var d = "2.8.3",
		e = {},
		f = !0,
		g = b.documentElement,
		h = "modernizr",
		i = b.createElement(h),
		j = i.style,
		k, l = {}.toString,
		m = "Webkit Moz O ms",
		n = m.split(" "),
		o = m.toLowerCase().split(" "),
		p = {},
		q = {},
		r = {},
		s = [],
		t = s.slice,
		u, v = {}.hasOwnProperty,
		w;
	!z(v, "undefined") && !z(v.call, "undefined") ? w = function (a, b) {
		return v.call(a, b)
	} : w = function (a, b) {
		return b in a && z(a.constructor.prototype[b], "undefined")
	}, Function.prototype.bind || (Function.prototype.bind = function (b) {
		var c = this;
		if (typeof c != "function") throw new TypeError;
		var d = t.call(arguments, 1),
			e = function () {
				if (this instanceof e) {
					var a = function () { };
					a.prototype = c.prototype;
					var f = new a,
						g = c.apply(f, d.concat(t.call(arguments)));
					return Object(g) === g ? g : f
				}
				return c.apply(b, d.concat(t.call(arguments)))
			};
		return e
	}), p.csstransitions = function () {
		return D("transition")
	};
	for (var E in p) w(p, E) && (u = E.toLowerCase(), e[u] = p[E](), s.push((e[u] ? "" : "no-") + u));
	return e.addTest = function (a, b) {
		if (typeof a == "object")
			for (var d in a) w(a, d) && e.addTest(d, a[d]);
		else {
			a = a.toLowerCase();
			if (e[a] !== c) return e;
			b = typeof b == "function" ? b() : b, typeof f != "undefined" && f && (g.className += " " + (b ? "" : "no-") + a), e[a] = b
		}
		return e
	}, x(""), i = k = null,
		function (a, b) {
			function l(a, b) {
				var c = a.createElement("p"),
					d = a.getElementsByTagName("head")[0] || a.documentElement;
				return c.innerHTML = "x<style>" + b + "</style>", d.insertBefore(c.lastChild, d.firstChild)
			}

			function m() {
				var a = s.elements;
				return typeof a == "string" ? a.split(" ") : a
			}

			function n(a) {
				var b = j[a[h]];
				return b || (b = {}, i++, a[h] = i, j[i] = b), b
			}

			function o(a, c, d) {
				c || (c = b);
				if (k) return c.createElement(a);
				d || (d = n(c));
				var g;
				return d.cache[a] ? g = d.cache[a].cloneNode() : f.test(a) ? g = (d.cache[a] = d.createElem(a)).cloneNode() : g = d.createElem(a), g.canHaveChildren && !e.test(a) && !g.tagUrn ? d.frag.appendChild(g) : g
			}

			function p(a, c) {
				a || (a = b);
				if (k) return a.createDocumentFragment();
				c = c || n(a);
				var d = c.frag.cloneNode(),
					e = 0,
					f = m(),
					g = f.length;
				for (; e < g; e++) d.createElement(f[e]);
				return d
			}

			function q(a, b) {
				b.cache || (b.cache = {}, b.createElem = a.createElement, b.createFrag = a.createDocumentFragment, b.frag = b.createFrag()), a.createElement = function (c) {
					return s.shivMethods ? o(c, a, b) : b.createElem(c)
				}, a.createDocumentFragment = Function("h,f", "return function(){var n=f.cloneNode(),c=n.createElement;h.shivMethods&&(" + m().join().replace(/[\w\-]+/g, function (a) {
					return b.createElem(a), b.frag.createElement(a), 'c("' + a + '")'
				}) + ");return n}")(s, b.frag)
			}

			function r(a) {
				a || (a = b);
				var c = n(a);
				return s.shivCSS && !g && !c.hasCSS && (c.hasCSS = !!l(a, "article,aside,dialog,figcaption,figure,footer,header,hgroup,main,nav,section{display:block}mark{background:#FF0;color:#000}template{display:none}")), k || q(a, c), a
			}
			var c = "3.7.0",
				d = a.html5 || {},
				e = /^<|^(?:button|map|select|textarea|object|iframe|option|optgroup)$/i,
				f = /^(?:a|b|code|div|fieldset|h1|h2|h3|h4|h5|h6|i|label|li|ol|p|q|span|strong|style|table|tbody|td|th|tr|ul)$/i,
				g, h = "_html5shiv",
				i = 0,
				j = {},
				k;
			(function () {
				try {
					var a = b.createElement("a");
					a.innerHTML = "<xyz></xyz>", g = "hidden" in a, k = a.childNodes.length == 1 || function () {
						b.createElement("a");
						var a = b.createDocumentFragment();
						return typeof a.cloneNode == "undefined" || typeof a.createDocumentFragment == "undefined" || typeof a.createElement == "undefined"
					}()
				} catch (c) {
					g = !0, k = !0
				}
			})();
			var s = {
				elements: d.elements || "abbr article aside audio bdi canvas data datalist details dialog figcaption figure footer header hgroup main mark meter nav output progress section summary template time video",
				version: c,
				shivCSS: d.shivCSS !== !1,
				supportsUnknownElements: k,
				shivMethods: d.shivMethods !== !1,
				type: "default",
				shivDocument: r,
				createElement: o,
				createDocumentFragment: p
			};
			a.html5 = s, r(b)
		}(this, b), e._version = d, e._domPrefixes = o, e._cssomPrefixes = n, e.testProp = function (a) {
			return B([a])
		}, e.testAllProps = D, e.prefixed = function (a, b, c) {
			return b ? D(a, b, c) : D(a, "pfx")
		}, g.className = g.className.replace(/(^|\s)no-js(\s|$)/, "$1$2") + (f ? " js " + s.join(" ") : ""), e
}(this, this.document),
	function (a, b, c) {
		function d(a) {
			return "[object Function]" == o.call(a)
		}

		function e(a) {
			return "string" == typeof a
		}

		function f() { }

		function g(a) {
			return !a || "loaded" == a || "complete" == a || "uninitialized" == a
		}

		function h() {
			var a = p.shift();
			q = 1, a ? a.t ? m(function () {
				("c" == a.t ? B.injectCss : B.injectJs)(a.s, 0, a.a, a.x, a.e, 1)
			}, 0) : (a(), h()) : q = 0
		}

		function i(a, c, d, e, f, i, j) {
			function k(b) {
				if (!o && g(l.readyState) && (u.r = o = 1, !q && h(), l.onload = l.onreadystatechange = null, b)) {
					"img" != a && m(function () {
						t.removeChild(l)
					}, 50);
					for (var d in y[c]) y[c].hasOwnProperty(d) && y[c][d].onload()
				}
			}
			var j = j || B.errorTimeout,
				l = b.createElement(a),
				o = 0,
				r = 0,
				u = {
					t: d,
					s: c,
					e: f,
					a: i,
					x: j
				};
			1 === y[c] && (r = 1, y[c] = []), "object" == a ? l.data = c : (l.src = c, l.type = a), l.width = l.height = "0", l.onerror = l.onload = l.onreadystatechange = function () {
				k.call(this, r)
			}, p.splice(e, 0, u), "img" != a && (r || 2 === y[c] ? (t.insertBefore(l, s ? null : n), m(k, j)) : y[c].push(l))
		}

		function j(a, b, c, d, f) {
			return q = 0, b = b || "j", e(a) ? i("c" == b ? v : u, a, b, this.i++, c, d, f) : (p.splice(this.i++, 0, a), 1 == p.length && h()), this
		}

		function k() {
			var a = B;
			return a.loader = {
				load: j,
				i: 0
			}, a
		}
		var l = b.documentElement,
			m = a.setTimeout,
			n = b.getElementsByTagName("script")[0],
			o = {}.toString,
			p = [],
			q = 0,
			r = "MozAppearance" in l.style,
			s = r && !!b.createRange().compareNode,
			t = s ? l : n.parentNode,
			l = a.opera && "[object Opera]" == o.call(a.opera),
			l = !!b.attachEvent && !l,
			u = r ? "object" : l ? "script" : "img",
			v = l ? "script" : u,
			w = Array.isArray || function (a) {
				return "[object Array]" == o.call(a)
			},
			x = [],
			y = {},
			z = {
				timeout: function (a, b) {
					return b.length && (a.timeout = b[0]), a
				}
			},
			A, B;
		B = function (a) {
			function b(a) {
				var a = a.split("!"),
					b = x.length,
					c = a.pop(),
					d = a.length,
					c = {
						url: c,
						origUrl: c,
						prefixes: a
					},
					e, f, g;
				for (f = 0; f < d; f++) g = a[f].split("="), (e = z[g.shift()]) && (c = e(c, g));
				for (f = 0; f < b; f++) c = x[f](c);
				return c
			}

			function g(a, e, f, g, h) {
				var i = b(a),
					j = i.autoCallback;
				i.url.split(".").pop().split("?").shift(), i.bypass || (e && (e = d(e) ? e : e[a] || e[g] || e[a.split("/").pop().split("?")[0]]), i.instead ? i.instead(a, e, f, g, h) : (y[i.url] ? i.noexec = !0 : y[i.url] = 1, f.load(i.url, i.forceCSS || !i.forceJS && "css" == i.url.split(".").pop().split("?").shift() ? "c" : c, i.noexec, i.attrs, i.timeout), (d(e) || d(j)) && f.load(function () {
					k(), e && e(i.origUrl, h, g), j && j(i.origUrl, h, g), y[i.url] = 2
				})))
			}

			function h(a, b) {
				function c(a, c) {
					if (a) {
						if (e(a)) c || (j = function () {
							var a = [].slice.call(arguments);
							k.apply(this, a), l()
						}), g(a, j, b, 0, h);
						else if (Object(a) === a)
							for (n in m = function () {
								var b = 0,
									c;
								for (c in a) a.hasOwnProperty(c) && b++;
								return b
							}(), a) a.hasOwnProperty(n) && (!c && !--m && (d(j) ? j = function () {
								var a = [].slice.call(arguments);
								k.apply(this, a), l()
							} : j[n] = function (a) {
								return function () {
									var b = [].slice.call(arguments);
									a && a.apply(this, b), l()
								}
							}(k[n])), g(a[n], j, b, n, h))
					} else !c && l()
				}
				var h = !!a.test,
					i = a.load || a.both,
					j = a.callback || f,
					k = j,
					l = a.complete || f,
					m, n;
				c(h ? a.yep : a.nope, !!i), i && c(i)
			}
			var i, j, l = this.yepnope.loader;
			if (e(a)) g(a, 0, l, 0);
			else if (w(a))
				for (i = 0; i < a.length; i++) j = a[i], e(j) ? g(j, 0, l, 0) : w(j) ? B(j) : Object(j) === j && h(j, l);
			else Object(a) === a && h(a, l)
		}, B.addPrefix = function (a, b) {
			z[a] = b
		}, B.addFilter = function (a) {
			x.push(a)
		}, B.errorTimeout = 1e4, null == b.readyState && b.addEventListener && (b.readyState = "loading", b.addEventListener("DOMContentLoaded", A = function () {
			b.removeEventListener("DOMContentLoaded", A, 0), b.readyState = "complete"
		}, 0)), a.yepnope = k(), a.yepnope.executeStack = h, a.yepnope.injectJs = function (a, c, d, e, i, j) {
			var k = b.createElement("script"),
				l, o, e = e || B.errorTimeout;
			k.src = a;
			for (o in d) k.setAttribute(o, d[o]);
			c = j ? h : c || f, k.onreadystatechange = k.onload = function () {
				!l && g(k.readyState) && (l = 1, c(), k.onload = k.onreadystatechange = null)
			}, m(function () {
				l || (l = 1, c(1))
			}, e), i ? k.onload() : n.parentNode.insertBefore(k, n)
		}, a.yepnope.injectCss = function (a, c, d, e, g, i) {
			var e = b.createElement("link"),
				j, c = i ? h : c || f;
			e.href = a, e.rel = "stylesheet", e.type = "text/css";
			for (j in d) e.setAttribute(j, d[j]);
			g || (n.parentNode.insertBefore(e, n), m(c, 0))
		}
	}(this, document), Modernizr.load = function () {
		yepnope.apply(window, [].slice.call(arguments, 0))
	};
/*
 AngularJS v1.8.2
 (c) 2010-2020 Google LLC. http://angularjs.org
 License: MIT
*/
(function (n, e) {
	'use strict'; function m(d, k, l) {
		var a = l.baseHref(), h = d[0]; return function (f, b, c) {
			var d, g; c = c || {}; g = c.expires; d = e.isDefined(c.path) ? c.path : a; e.isUndefined(b) && (g = "Thu, 01 Jan 1970 00:00:00 GMT", b = ""); e.isString(g) && (g = new Date(g)); b = encodeURIComponent(f) + "=" + encodeURIComponent(b); b = b + (d ? ";path=" + d : "") + (c.domain ? ";domain=" + c.domain : ""); b += g ? ";expires=" + g.toUTCString() : ""; b += c.secure ? ";secure" : ""; b += c.samesite ? ";samesite=" + c.samesite : ""; c = b.length + 1; 4096 < c && k.warn("Cookie '" + f + "' possibly not set or overflowed because it was too large (" +
				c + " > 4096 bytes)!"); h.cookie = b
		}
	} e.module("ngCookies", ["ng"]).info({ angularVersion: "1.8.2" }).provider("$cookies", [function () { var d = this.defaults = {}; this.$get = ["$$cookieReader", "$$cookieWriter", function (k, l) { return { get: function (a) { return k()[a] }, getObject: function (a) { return (a = this.get(a)) ? e.fromJson(a) : a }, getAll: function () { return k() }, put: function (a, h, f) { l(a, h, f ? e.extend({}, d, f) : d) }, putObject: function (a, d, f) { this.put(a, e.toJson(d), f) }, remove: function (a, h) { l(a, void 0, h ? e.extend({}, d, h) : d) } } }] }]); m.$inject =
		["$document", "$log", "$browser"]; e.module("ngCookies").provider("$$cookieWriter", function () { this.$get = m })
})(window, window.angular);
//# sourceMappingURL=angular-cookies.min.js.map

/*
 angular-file-upload v2.5.0
 https://github.com/nervgh/angular-file-upload
*/

(function webpackUniversalModuleDefinition(root, factory) {
	if (typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if (typeof define === 'function' && define.amd)
		define([], factory);
	else if (typeof exports === 'object')
		exports["angular-file-upload"] = factory();
	else
		root["angular-file-upload"] = factory();
})(this, function () {
	return /******/ (function (modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if (installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;
/******/
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
				/******/
			};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
			/******/
		}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
		/******/
	})
/************************************************************************/
/******/([
/* 0 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			var _options = __webpack_require__(2);

			var _options2 = _interopRequireDefault(_options);

			var _FileUploader = __webpack_require__(3);

			var _FileUploader2 = _interopRequireDefault(_FileUploader);

			var _FileLikeObject = __webpack_require__(4);

			var _FileLikeObject2 = _interopRequireDefault(_FileLikeObject);

			var _FileItem = __webpack_require__(5);

			var _FileItem2 = _interopRequireDefault(_FileItem);

			var _FileDirective = __webpack_require__(6);

			var _FileDirective2 = _interopRequireDefault(_FileDirective);

			var _FileSelect = __webpack_require__(7);

			var _FileSelect2 = _interopRequireDefault(_FileSelect);

			var _Pipeline = __webpack_require__(8);

			var _Pipeline2 = _interopRequireDefault(_Pipeline);

			var _FileDrop = __webpack_require__(9);

			var _FileDrop2 = _interopRequireDefault(_FileDrop);

			var _FileOver = __webpack_require__(10);

			var _FileOver2 = _interopRequireDefault(_FileOver);

			var _FileSelect3 = __webpack_require__(11);

			var _FileSelect4 = _interopRequireDefault(_FileSelect3);

			var _FileDrop3 = __webpack_require__(12);

			var _FileDrop4 = _interopRequireDefault(_FileDrop3);

			var _FileOver3 = __webpack_require__(13);

			var _FileOver4 = _interopRequireDefault(_FileOver3);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			angular.module(_config2.default.name, []).value('fileUploaderOptions', _options2.default).factory('FileUploader', _FileUploader2.default).factory('FileLikeObject', _FileLikeObject2.default).factory('FileItem', _FileItem2.default).factory('FileDirective', _FileDirective2.default).factory('FileSelect', _FileSelect2.default).factory('FileDrop', _FileDrop2.default).factory('FileOver', _FileOver2.default).factory('Pipeline', _Pipeline2.default).directive('nvFileSelect', _FileSelect4.default).directive('nvFileDrop', _FileDrop4.default).directive('nvFileOver', _FileOver4.default).run(['FileUploader', 'FileLikeObject', 'FileItem', 'FileDirective', 'FileSelect', 'FileDrop', 'FileOver', 'Pipeline', function (FileUploader, FileLikeObject, FileItem, FileDirective, FileSelect, FileDrop, FileOver, Pipeline) {
				// only for compatibility
				FileUploader.FileLikeObject = FileLikeObject;
				FileUploader.FileItem = FileItem;
				FileUploader.FileDirective = FileDirective;
				FileUploader.FileSelect = FileSelect;
				FileUploader.FileDrop = FileDrop;
				FileUploader.FileOver = FileOver;
				FileUploader.Pipeline = Pipeline;
			}]);

			/***/
		},
/* 1 */
/***/ function (module, exports) {

			module.exports = {
				"name": "angularFileUpload"
			};

			/***/
		},
/* 2 */
/***/ function (module, exports) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = {
				url: '/',
				alias: 'file',
				headers: {},
				queue: [],
				progress: 0,
				autoUpload: false,
				removeAfterUpload: false,
				method: 'POST',
				filters: [],
				formData: [],
				queueLimit: Number.MAX_VALUE,
				withCredentials: false,
				disableMultipart: false
			};

			/***/
		},
/* 3 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});

			var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			var _angular = angular;
			var bind = _angular.bind;
			var copy = _angular.copy;
			var extend = _angular.extend;
			var forEach = _angular.forEach;
			var isObject = _angular.isObject;
			var isNumber = _angular.isNumber;
			var isDefined = _angular.isDefined;
			var isArray = _angular.isArray;
			var isUndefined = _angular.isUndefined;
			var element = _angular.element;
			function __identity(fileUploaderOptions, $rootScope, $http, $window, $timeout, FileLikeObject, FileItem, Pipeline) {
				var File = $window.File;
				var FormData = $window.FormData;

				var FileUploader = function () {
					/**********************
					 * PUBLIC
					 **********************/
					/**
					 * Creates an instance of FileUploader
					 * @param {Object} [options]
					 * @constructor
					 */

					function FileUploader(options) {
						_classCallCheck(this, FileUploader);

						var settings = copy(fileUploaderOptions);

						extend(this, settings, options, {
							isUploading: false,
							_nextIndex: 0,
							_directives: { select: [], drop: [], over: [] }
						});

						// add default filters
						this.filters.unshift({ name: 'queueLimit', fn: this._queueLimitFilter });
						this.filters.unshift({ name: 'folder', fn: this._folderFilter });
					}
					/**
					 * Adds items to the queue
					 * @param {File|HTMLInputElement|Object|FileList|Array<Object>} files
					 * @param {Object} [options]
					 * @param {Array<Function>|String} filters
					 */


					FileUploader.prototype.addToQueue = function addToQueue(files, options, filters) {
						var _this = this;

						var incomingQueue = this.isArrayLikeObject(files) ? Array.prototype.slice.call(files) : [files];
						var arrayOfFilters = this._getFilters(filters);
						var count = this.queue.length;
						var addedFileItems = [];

						var next = function next() {
							var something = incomingQueue.shift();

							if (isUndefined(something)) {
								return done();
							}

							var fileLikeObject = _this.isFile(something) ? something : new FileLikeObject(something);
							var pipes = _this._convertFiltersToPipes(arrayOfFilters);
							var pipeline = new Pipeline(pipes);
							var onThrown = function onThrown(err) {
								var originalFilter = err.pipe.originalFilter;

								var _err$args = _slicedToArray(err.args, 2);

								var fileLikeObject = _err$args[0];
								var options = _err$args[1];

								_this._onWhenAddingFileFailed(fileLikeObject, originalFilter, options);
								next();
							};
							var onSuccessful = function onSuccessful(fileLikeObject, options) {
								var fileItem = new FileItem(_this, fileLikeObject, options);
								addedFileItems.push(fileItem);
								_this.queue.push(fileItem);
								_this._onAfterAddingFile(fileItem);
								next();
							};
							pipeline.onThrown = onThrown;
							pipeline.onSuccessful = onSuccessful;
							pipeline.exec(fileLikeObject, options);
						};

						var done = function done() {
							if (_this.queue.length !== count) {
								_this._onAfterAddingAll(addedFileItems);
								_this.progress = _this._getTotalProgress();
							}

							_this._render();
							if (_this.autoUpload) _this.uploadAll();
						};

						next();
					};
					/**
					 * Remove items from the queue. Remove last: index = -1
					 * @param {FileItem|Number} value
					 */


					FileUploader.prototype.removeFromQueue = function removeFromQueue(value) {
						var index = this.getIndexOfItem(value);
						var item = this.queue[index];
						if (item.isUploading) item.cancel();
						this.queue.splice(index, 1);
						item._destroy();
						this.progress = this._getTotalProgress();
					};
					/**
					 * Clears the queue
					 */


					FileUploader.prototype.clearQueue = function clearQueue() {
						while (this.queue.length) {
							this.queue[0].remove();
						}
						this.progress = 0;
					};
					/**
					 * Uploads a item from the queue
					 * @param {FileItem|Number} value
					 */


					FileUploader.prototype.uploadItem = function uploadItem(value) {
						var index = this.getIndexOfItem(value);
						var item = this.queue[index];
						var transport = this.isHTML5 ? '_xhrTransport' : '_iframeTransport';

						item._prepareToUploading();
						if (this.isUploading) return;

						this._onBeforeUploadItem(item);
						if (item.isCancel) return;

						item.isUploading = true;
						this.isUploading = true;
						this[transport](item);
						this._render();
					};
					/**
					 * Cancels uploading of item from the queue
					 * @param {FileItem|Number} value
					 */


					FileUploader.prototype.cancelItem = function cancelItem(value) {
						var _this2 = this;

						var index = this.getIndexOfItem(value);
						var item = this.queue[index];
						var prop = this.isHTML5 ? '_xhr' : '_form';
						if (!item) return;
						item.isCancel = true;
						if (item.isUploading) {
							// It will call this._onCancelItem() & this._onCompleteItem() asynchronously
							item[prop].abort();
						} else {
							(function () {
								var dummy = [undefined, 0, {}];
								var onNextTick = function onNextTick() {
									_this2._onCancelItem.apply(_this2, [item].concat(dummy));
									_this2._onCompleteItem.apply(_this2, [item].concat(dummy));
								};
								$timeout(onNextTick); // Trigger callbacks asynchronously (setImmediate emulation)
							})();
						}
					};
					/**
					 * Uploads all not uploaded items of queue
					 */


					FileUploader.prototype.uploadAll = function uploadAll() {
						var items = this.getNotUploadedItems().filter(function (item) {
							return !item.isUploading;
						});
						if (!items.length) return;

						forEach(items, function (item) {
							return item._prepareToUploading();
						});
						items[0].upload();
					};
					/**
					 * Cancels all uploads
					 */


					FileUploader.prototype.cancelAll = function cancelAll() {
						var items = this.getNotUploadedItems();
						forEach(items, function (item) {
							return item.cancel();
						});
					};
					/**
					 * Returns "true" if value an instance of File
					 * @param {*} value
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.prototype.isFile = function isFile(value) {
						return this.constructor.isFile(value);
					};
					/**
					 * Returns "true" if value an instance of FileLikeObject
					 * @param {*} value
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.prototype.isFileLikeObject = function isFileLikeObject(value) {
						return this.constructor.isFileLikeObject(value);
					};
					/**
					 * Returns "true" if value is array like object
					 * @param {*} value
					 * @returns {Boolean}
					 */


					FileUploader.prototype.isArrayLikeObject = function isArrayLikeObject(value) {
						return this.constructor.isArrayLikeObject(value);
					};
					/**
					 * Returns a index of item from the queue
					 * @param {Item|Number} value
					 * @returns {Number}
					 */


					FileUploader.prototype.getIndexOfItem = function getIndexOfItem(value) {
						return isNumber(value) ? value : this.queue.indexOf(value);
					};
					/**
					 * Returns not uploaded items
					 * @returns {Array}
					 */


					FileUploader.prototype.getNotUploadedItems = function getNotUploadedItems() {
						return this.queue.filter(function (item) {
							return !item.isUploaded;
						});
					};
					/**
					 * Returns items ready for upload
					 * @returns {Array}
					 */


					FileUploader.prototype.getReadyItems = function getReadyItems() {
						return this.queue.filter(function (item) {
							return item.isReady && !item.isUploading;
						}).sort(function (item1, item2) {
							return item1.index - item2.index;
						});
					};
					/**
					 * Destroys instance of FileUploader
					 */


					FileUploader.prototype.destroy = function destroy() {
						var _this3 = this;

						forEach(this._directives, function (key) {
							forEach(_this3._directives[key], function (object) {
								object.destroy();
							});
						});
					};
					/**
					 * Callback
					 * @param {Array} fileItems
					 */


					FileUploader.prototype.onAfterAddingAll = function onAfterAddingAll(fileItems) { };
					/**
					 * Callback
					 * @param {FileItem} fileItem
					 */


					FileUploader.prototype.onAfterAddingFile = function onAfterAddingFile(fileItem) { };
					/**
					 * Callback
					 * @param {File|Object} item
					 * @param {Object} filter
					 * @param {Object} options
					 */


					FileUploader.prototype.onWhenAddingFileFailed = function onWhenAddingFileFailed(item, filter, options) { };
					/**
					 * Callback
					 * @param {FileItem} fileItem
					 */


					FileUploader.prototype.onBeforeUploadItem = function onBeforeUploadItem(fileItem) { };
					/**
					 * Callback
					 * @param {FileItem} fileItem
					 * @param {Number} progress
					 */


					FileUploader.prototype.onProgressItem = function onProgressItem(fileItem, progress) { };
					/**
					 * Callback
					 * @param {Number} progress
					 */


					FileUploader.prototype.onProgressAll = function onProgressAll(progress) { };
					/**
					 * Callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileUploader.prototype.onSuccessItem = function onSuccessItem(item, response, status, headers) { };
					/**
					 * Callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileUploader.prototype.onErrorItem = function onErrorItem(item, response, status, headers) { };
					/**
					 * Callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileUploader.prototype.onCancelItem = function onCancelItem(item, response, status, headers) { };
					/**
					 * Callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileUploader.prototype.onCompleteItem = function onCompleteItem(item, response, status, headers) { };
					/**
					 * Callback
					 */


					FileUploader.prototype.onCompleteAll = function onCompleteAll() { };
					/**********************
					 * PRIVATE
					 **********************/
					/**
					 * Returns the total progress
					 * @param {Number} [value]
					 * @returns {Number}
					 * @private
					 */


					FileUploader.prototype._getTotalProgress = function _getTotalProgress(value) {
						if (this.removeAfterUpload) return value || 0;

						var notUploaded = this.getNotUploadedItems().length;
						var uploaded = notUploaded ? this.queue.length - notUploaded : this.queue.length;
						var ratio = 100 / this.queue.length;
						var current = (value || 0) * ratio / 100;

						return Math.round(uploaded * ratio + current);
					};
					/**
					 * Returns array of filters
					 * @param {Array<Function>|String} filters
					 * @returns {Array<Function>}
					 * @private
					 */


					FileUploader.prototype._getFilters = function _getFilters(filters) {
						if (!filters) return this.filters;
						if (isArray(filters)) return filters;
						var names = filters.match(/[^\s,]+/g);
						return this.filters.filter(function (filter) {
							return names.indexOf(filter.name) !== -1;
						});
					};
					/**
					* @param {Array<Function>} filters
					* @returns {Array<Function>}
					* @private
					*/


					FileUploader.prototype._convertFiltersToPipes = function _convertFiltersToPipes(filters) {
						var _this4 = this;

						return filters.map(function (filter) {
							var fn = bind(_this4, filter.fn);
							fn.isAsync = filter.fn.length === 3;
							fn.originalFilter = filter;
							return fn;
						});
					};
					/**
					 * Updates html
					 * @private
					 */


					FileUploader.prototype._render = function _render() {
						if (!$rootScope.$$phase) $rootScope.$apply();
					};
					/**
					 * Returns "true" if item is a file (not folder)
					 * @param {File|FileLikeObject} item
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.prototype._folderFilter = function _folderFilter(item) {
						return !!(item.size || item.type);
					};
					/**
					 * Returns "true" if the limit has not been reached
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.prototype._queueLimitFilter = function _queueLimitFilter() {
						return this.queue.length < this.queueLimit;
					};
					/**
					 * Checks whether upload successful
					 * @param {Number} status
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.prototype._isSuccessCode = function _isSuccessCode(status) {
						return status >= 200 && status < 300 || status === 304;
					};
					/**
					 * Transforms the server response
					 * @param {*} response
					 * @param {Object} headers
					 * @returns {*}
					 * @private
					 */


					FileUploader.prototype._transformResponse = function _transformResponse(response, headers) {
						var headersGetter = this._headersGetter(headers);
						forEach($http.defaults.transformResponse, function (transformFn) {
							response = transformFn(response, headersGetter);
						});
						return response;
					};
					/**
					 * Parsed response headers
					 * @param headers
					 * @returns {Object}
					 * @see https://github.com/angular/angular.js/blob/master/src/ng/http.js
					 * @private
					 */


					FileUploader.prototype._parseHeaders = function _parseHeaders(headers) {
						var parsed = {},
							key,
							val,
							i;

						if (!headers) return parsed;

						forEach(headers.split('\n'), function (line) {
							i = line.indexOf(':');
							key = line.slice(0, i).trim().toLowerCase();
							val = line.slice(i + 1).trim();

							if (key) {
								parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
							}
						});

						return parsed;
					};
					/**
					 * Returns function that returns headers
					 * @param {Object} parsedHeaders
					 * @returns {Function}
					 * @private
					 */


					FileUploader.prototype._headersGetter = function _headersGetter(parsedHeaders) {
						return function (name) {
							if (name) {
								return parsedHeaders[name.toLowerCase()] || null;
							}
							return parsedHeaders;
						};
					};
					/**
					 * The XMLHttpRequest transport
					 * @param {FileItem} item
					 * @private
					 */


					FileUploader.prototype._xhrTransport = function _xhrTransport(item) {
						var _this5 = this;

						var xhr = item._xhr = new XMLHttpRequest();
						var sendable;

						if (!item.disableMultipart) {
							sendable = new FormData();
							forEach(item.formData, function (obj) {
								forEach(obj, function (value, key) {
									sendable.append(key, value);
								});
							});

							sendable.append(item.alias, item._file, item.file.name);
						} else {
							sendable = item._file;
						}

						if (typeof item._file.size != 'number') {
							throw new TypeError('The file specified is no longer valid');
						}

						xhr.upload.onprogress = function (event) {
							var progress = Math.round(event.lengthComputable ? event.loaded * 100 / event.total : 0);
							_this5._onProgressItem(item, progress);
						};

						xhr.onload = function () {
							var headers = _this5._parseHeaders(xhr.getAllResponseHeaders());
							var response = _this5._transformResponse(xhr.response, headers);
							var gist = _this5._isSuccessCode(xhr.status) ? 'Success' : 'Error';
							var method = '_on' + gist + 'Item';
							_this5[method](item, response, xhr.status, headers);
							_this5._onCompleteItem(item, response, xhr.status, headers);
						};

						xhr.onerror = function () {
							var headers = _this5._parseHeaders(xhr.getAllResponseHeaders());
							var response = _this5._transformResponse(xhr.response, headers);
							_this5._onErrorItem(item, response, xhr.status, headers);
							_this5._onCompleteItem(item, response, xhr.status, headers);
						};

						xhr.onabort = function () {
							var headers = _this5._parseHeaders(xhr.getAllResponseHeaders());
							var response = _this5._transformResponse(xhr.response, headers);
							_this5._onCancelItem(item, response, xhr.status, headers);
							_this5._onCompleteItem(item, response, xhr.status, headers);
						};

						xhr.open(item.method, item.url, true);

						xhr.withCredentials = item.withCredentials;

						forEach(item.headers, function (value, name) {
							xhr.setRequestHeader(name, value);
						});

						xhr.send(sendable);
					};
					/**
					 * The IFrame transport
					 * @param {FileItem} item
					 * @private
					 */


					FileUploader.prototype._iframeTransport = function _iframeTransport(item) {
						var _this6 = this;

						var form = element('<form style="display: none;" />');
						var iframe = element('<iframe name="iframeTransport' + Date.now() + '">');
						var input = item._input;

						if (item._form) item._form.replaceWith(input); // remove old form
						item._form = form; // save link to new form

						input.prop('name', item.alias);

						forEach(item.formData, function (obj) {
							forEach(obj, function (value, key) {
								var element_ = element('<input type="hidden" name="' + key + '" />');
								element_.val(value);
								form.append(element_);
							});
						});

						form.prop({
							action: item.url,
							method: 'POST',
							target: iframe.prop('name'),
							enctype: 'multipart/form-data',
							encoding: 'multipart/form-data' // old IE
						});

						iframe.bind('load', function () {
							var html = '';
							var status = 200;

							try {
								// Fix for legacy IE browsers that loads internal error page
								// when failed WS response received. In consequence iframe
								// content access denied error is thrown becouse trying to
								// access cross domain page. When such thing occurs notifying
								// with empty response object. See more info at:
								// http://stackoverflow.com/questions/151362/access-is-denied-error-on-accessing-iframe-document-object
								// Note that if non standard 4xx or 5xx error code returned
								// from WS then response content can be accessed without error
								// but 'XHR' status becomes 200. In order to avoid confusion
								// returning response via same 'success' event handler.

								// fixed angular.contents() for iframes
								html = iframe[0].contentDocument.body.innerHTML;
							} catch (e) {
								// in case we run into the access-is-denied error or we have another error on the server side
								// (intentional 500,40... errors), we at least say 'something went wrong' -> 500
								status = 500;
							}

							var xhr = { response: html, status: status, dummy: true };
							var headers = {};
							var response = _this6._transformResponse(xhr.response, headers);

							_this6._onSuccessItem(item, response, xhr.status, headers);
							_this6._onCompleteItem(item, response, xhr.status, headers);
						});

						form.abort = function () {
							var xhr = { status: 0, dummy: true };
							var headers = {};
							var response;

							iframe.unbind('load').prop('src', 'javascript:false;');
							form.replaceWith(input);

							_this6._onCancelItem(item, response, xhr.status, headers);
							_this6._onCompleteItem(item, response, xhr.status, headers);
						};

						input.after(form);
						form.append(input).append(iframe);

						form[0].submit();
					};
					/**
					 * Inner callback
					 * @param {File|Object} item
					 * @param {Object} filter
					 * @param {Object} options
					 * @private
					 */


					FileUploader.prototype._onWhenAddingFileFailed = function _onWhenAddingFileFailed(item, filter, options) {
						this.onWhenAddingFileFailed(item, filter, options);
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 */


					FileUploader.prototype._onAfterAddingFile = function _onAfterAddingFile(item) {
						this.onAfterAddingFile(item);
					};
					/**
					 * Inner callback
					 * @param {Array<FileItem>} items
					 */


					FileUploader.prototype._onAfterAddingAll = function _onAfterAddingAll(items) {
						this.onAfterAddingAll(items);
					};
					/**
					 *  Inner callback
					 * @param {FileItem} item
					 * @private
					 */


					FileUploader.prototype._onBeforeUploadItem = function _onBeforeUploadItem(item) {
						item._onBeforeUpload();
						this.onBeforeUploadItem(item);
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 * @param {Number} progress
					 * @private
					 */


					FileUploader.prototype._onProgressItem = function _onProgressItem(item, progress) {
						var total = this._getTotalProgress(progress);
						this.progress = total;
						item._onProgress(progress);
						this.onProgressItem(item, progress);
						this.onProgressAll(total);
						this._render();
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileUploader.prototype._onSuccessItem = function _onSuccessItem(item, response, status, headers) {
						item._onSuccess(response, status, headers);
						this.onSuccessItem(item, response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileUploader.prototype._onErrorItem = function _onErrorItem(item, response, status, headers) {
						item._onError(response, status, headers);
						this.onErrorItem(item, response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileUploader.prototype._onCancelItem = function _onCancelItem(item, response, status, headers) {
						item._onCancel(response, status, headers);
						this.onCancelItem(item, response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {FileItem} item
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileUploader.prototype._onCompleteItem = function _onCompleteItem(item, response, status, headers) {
						item._onComplete(response, status, headers);
						this.onCompleteItem(item, response, status, headers);

						var nextItem = this.getReadyItems()[0];
						this.isUploading = false;

						if (isDefined(nextItem)) {
							nextItem.upload();
							return;
						}

						this.onCompleteAll();
						this.progress = this._getTotalProgress();
						this._render();
					};
					/**********************
					 * STATIC
					 **********************/
					/**
					 * Returns "true" if value an instance of File
					 * @param {*} value
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.isFile = function isFile(value) {
						return File && value instanceof File;
					};
					/**
					 * Returns "true" if value an instance of FileLikeObject
					 * @param {*} value
					 * @returns {Boolean}
					 * @private
					 */


					FileUploader.isFileLikeObject = function isFileLikeObject(value) {
						return value instanceof FileLikeObject;
					};
					/**
					 * Returns "true" if value is array like object
					 * @param {*} value
					 * @returns {Boolean}
					 */


					FileUploader.isArrayLikeObject = function isArrayLikeObject(value) {
						return isObject(value) && 'length' in value;
					};
					/**
					 * Inherits a target (Class_1) by a source (Class_2)
					 * @param {Function} target
					 * @param {Function} source
					 */


					FileUploader.inherit = function inherit(target, source) {
						target.prototype = Object.create(source.prototype);
						target.prototype.constructor = target;
						target.super_ = source;
					};

					return FileUploader;
				}();

				/**********************
				 * PUBLIC
				 **********************/
				/**
				 * Checks a support the html5 uploader
				 * @returns {Boolean}
				 * @readonly
				 */


				FileUploader.prototype.isHTML5 = !!(File && FormData);
				/**********************
				 * STATIC
				 **********************/
				/**
				 * @borrows FileUploader.prototype.isHTML5
				 */
				FileUploader.isHTML5 = FileUploader.prototype.isHTML5;

				return FileUploader;
			}

			__identity.$inject = ['fileUploaderOptions', '$rootScope', '$http', '$window', '$timeout', 'FileLikeObject', 'FileItem', 'Pipeline'];

			/***/
		},
/* 4 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			var _angular = angular;
			var copy = _angular.copy;
			var isElement = _angular.isElement;
			var isString = _angular.isString;
			function __identity() {

				return function () {
					/**
					 * Creates an instance of FileLikeObject
					 * @param {File|HTMLInputElement|Object} fileOrInput
					 * @constructor
					 */

					function FileLikeObject(fileOrInput) {
						_classCallCheck(this, FileLikeObject);

						var isInput = isElement(fileOrInput);
						var fakePathOrObject = isInput ? fileOrInput.value : fileOrInput;
						var postfix = isString(fakePathOrObject) ? 'FakePath' : 'Object';
						var method = '_createFrom' + postfix;
						this[method](fakePathOrObject);
					}
					/**
					 * Creates file like object from fake path string
					 * @param {String} path
					 * @private
					 */


					FileLikeObject.prototype._createFromFakePath = function _createFromFakePath(path) {
						this.lastModifiedDate = null;
						this.size = null;
						this.type = 'like/' + path.slice(path.lastIndexOf('.') + 1).toLowerCase();
						this.name = path.slice(path.lastIndexOf('/') + path.lastIndexOf('\\') + 2);
					};
					/**
					 * Creates file like object from object
					 * @param {File|FileLikeObject} object
					 * @private
					 */


					FileLikeObject.prototype._createFromObject = function _createFromObject(object) {
						this.lastModifiedDate = copy(object.lastModifiedDate);
						this.size = object.size;
						this.type = object.type;
						this.name = object.name;
					};

					return FileLikeObject;
				}();
			}

			/***/
		},
/* 5 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			var _angular = angular;
			var copy = _angular.copy;
			var extend = _angular.extend;
			var element = _angular.element;
			var isElement = _angular.isElement;
			function __identity($compile, FileLikeObject) {

				return function () {
					/**
					 * Creates an instance of FileItem
					 * @param {FileUploader} uploader
					 * @param {File|HTMLInputElement|Object} some
					 * @param {Object} options
					 * @constructor
					 */

					function FileItem(uploader, some, options) {
						_classCallCheck(this, FileItem);

						var isInput = isElement(some);
						var input = isInput ? element(some) : null;
						var file = !isInput ? some : null;

						extend(this, {
							url: uploader.url,
							alias: uploader.alias,
							headers: copy(uploader.headers),
							formData: copy(uploader.formData),
							removeAfterUpload: uploader.removeAfterUpload,
							withCredentials: uploader.withCredentials,
							disableMultipart: uploader.disableMultipart,
							method: uploader.method
						}, options, {
							uploader: uploader,
							file: new FileLikeObject(some),
							isReady: false,
							isUploading: false,
							isUploaded: false,
							isSuccess: false,
							isCancel: false,
							isError: false,
							progress: 0,
							index: null,
							_file: file,
							_input: input
						});

						if (input) this._replaceNode(input);
					}
					/**********************
					 * PUBLIC
					 **********************/
					/**
					 * Uploads a FileItem
					 */


					FileItem.prototype.upload = function upload() {
						try {
							this.uploader.uploadItem(this);
						} catch (e) {
							var message = e.name + ':' + e.message;
							this.uploader._onCompleteItem(this, message, e.code, []);
							this.uploader._onErrorItem(this, message, e.code, []);
						}
					};
					/**
					 * Cancels uploading of FileItem
					 */


					FileItem.prototype.cancel = function cancel() {
						this.uploader.cancelItem(this);
					};
					/**
					 * Removes a FileItem
					 */


					FileItem.prototype.remove = function remove() {
						this.uploader.removeFromQueue(this);
					};
					/**
					 * Callback
					 * @private
					 */


					FileItem.prototype.onBeforeUpload = function onBeforeUpload() { };
					/**
					 * Callback
					 * @param {Number} progress
					 * @private
					 */


					FileItem.prototype.onProgress = function onProgress(progress) { };
					/**
					 * Callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileItem.prototype.onSuccess = function onSuccess(response, status, headers) { };
					/**
					 * Callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileItem.prototype.onError = function onError(response, status, headers) { };
					/**
					 * Callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileItem.prototype.onCancel = function onCancel(response, status, headers) { };
					/**
					 * Callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 */


					FileItem.prototype.onComplete = function onComplete(response, status, headers) { };
					/**********************
					 * PRIVATE
					 **********************/
					/**
					 * Inner callback
					 */


					FileItem.prototype._onBeforeUpload = function _onBeforeUpload() {
						this.isReady = true;
						this.isUploading = false;
						this.isUploaded = false;
						this.isSuccess = false;
						this.isCancel = false;
						this.isError = false;
						this.progress = 0;
						this.onBeforeUpload();
					};
					/**
					 * Inner callback
					 * @param {Number} progress
					 * @private
					 */


					FileItem.prototype._onProgress = function _onProgress(progress) {
						this.progress = progress;
						this.onProgress(progress);
					};
					/**
					 * Inner callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileItem.prototype._onSuccess = function _onSuccess(response, status, headers) {
						this.isReady = false;
						this.isUploading = false;
						this.isUploaded = true;
						this.isSuccess = true;
						this.isCancel = false;
						this.isError = false;
						this.progress = 100;
						this.index = null;
						this.onSuccess(response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileItem.prototype._onError = function _onError(response, status, headers) {
						this.isReady = false;
						this.isUploading = false;
						this.isUploaded = true;
						this.isSuccess = false;
						this.isCancel = false;
						this.isError = true;
						this.progress = 0;
						this.index = null;
						this.onError(response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileItem.prototype._onCancel = function _onCancel(response, status, headers) {
						this.isReady = false;
						this.isUploading = false;
						this.isUploaded = false;
						this.isSuccess = false;
						this.isCancel = true;
						this.isError = false;
						this.progress = 0;
						this.index = null;
						this.onCancel(response, status, headers);
					};
					/**
					 * Inner callback
					 * @param {*} response
					 * @param {Number} status
					 * @param {Object} headers
					 * @private
					 */


					FileItem.prototype._onComplete = function _onComplete(response, status, headers) {
						this.onComplete(response, status, headers);
						if (this.removeAfterUpload) this.remove();
					};
					/**
					 * Destroys a FileItem
					 */


					FileItem.prototype._destroy = function _destroy() {
						if (this._input) this._input.remove();
						if (this._form) this._form.remove();
						delete this._form;
						delete this._input;
					};
					/**
					 * Prepares to uploading
					 * @private
					 */


					FileItem.prototype._prepareToUploading = function _prepareToUploading() {
						this.index = this.index || ++this.uploader._nextIndex;
						this.isReady = true;
					};
					/**
					 * Replaces input element on his clone
					 * @param {JQLite|jQuery} input
					 * @private
					 */


					FileItem.prototype._replaceNode = function _replaceNode(input) {
						var clone = $compile(input.clone())(input.scope());
						clone.prop('value', null); // FF fix
						input.css('display', 'none');
						input.after(clone); // remove jquery dependency
					};

					return FileItem;
				}();
			}

			__identity.$inject = ['$compile', 'FileLikeObject'];

			/***/
		},
/* 6 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			var _angular = angular;
			var extend = _angular.extend;
			function __identity() {
				var FileDirective = function () {
					/**
					 * Creates instance of {FileDirective} object
					 * @param {Object} options
					 * @param {Object} options.uploader
					 * @param {HTMLElement} options.element
					 * @param {Object} options.events
					 * @param {String} options.prop
					 * @constructor
					 */

					function FileDirective(options) {
						_classCallCheck(this, FileDirective);

						extend(this, options);
						this.uploader._directives[this.prop].push(this);
						this._saveLinks();
						this.bind();
					}
					/**
					 * Binds events handles
					 */


					FileDirective.prototype.bind = function bind() {
						for (var key in this.events) {
							var prop = this.events[key];
							this.element.bind(key, this[prop]);
						}
					};
					/**
					 * Unbinds events handles
					 */


					FileDirective.prototype.unbind = function unbind() {
						for (var key in this.events) {
							this.element.unbind(key, this.events[key]);
						}
					};
					/**
					 * Destroys directive
					 */


					FileDirective.prototype.destroy = function destroy() {
						var index = this.uploader._directives[this.prop].indexOf(this);
						this.uploader._directives[this.prop].splice(index, 1);
						this.unbind();
						// this.element = null;
					};
					/**
					 * Saves links to functions
					 * @private
					 */


					FileDirective.prototype._saveLinks = function _saveLinks() {
						for (var key in this.events) {
							var prop = this.events[key];
							this[prop] = this[prop].bind(this);
						}
					};

					return FileDirective;
				}();

				/**
				 * Map of events
				 * @type {Object}
				 */


				FileDirective.prototype.events = {};

				return FileDirective;
			}

			/***/
		},
/* 7 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

			function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

			var _angular = angular;
			var extend = _angular.extend;
			function __identity($compile, FileDirective) {

				return function (_FileDirective) {
					_inherits(FileSelect, _FileDirective);

					/**
					 * Creates instance of {FileSelect} object
					 * @param {Object} options
					 * @constructor
					 */

					function FileSelect(options) {
						_classCallCheck(this, FileSelect);

						var extendedOptions = extend(options, {
							// Map of events
							events: {
								$destroy: 'destroy',
								change: 'onChange'
							},
							// Name of property inside uploader._directive object
							prop: 'select'
						});

						var _this = _possibleConstructorReturn(this, _FileDirective.call(this, extendedOptions));

						if (!_this.uploader.isHTML5) {
							_this.element.removeAttr('multiple');
						}
						_this.element.prop('value', null); // FF fix
						return _this;
					}
					/**
					 * Returns options
					 * @return {Object|undefined}
					 */


					FileSelect.prototype.getOptions = function getOptions() { };
					/**
					 * Returns filters
					 * @return {Array<Function>|String|undefined}
					 */


					FileSelect.prototype.getFilters = function getFilters() { };
					/**
					 * If returns "true" then HTMLInputElement will be cleared
					 * @returns {Boolean}
					 */


					FileSelect.prototype.isEmptyAfterSelection = function isEmptyAfterSelection() {
						return !!this.element.attr('multiple');
					};
					/**
					 * Event handler
					 */


					FileSelect.prototype.onChange = function onChange() {
						var files = this.uploader.isHTML5 ? this.element[0].files : this.element[0];
						var options = this.getOptions();
						var filters = this.getFilters();

						if (!this.uploader.isHTML5) this.destroy();
						this.uploader.addToQueue(files, options, filters);
						if (this.isEmptyAfterSelection()) {
							this.element.prop('value', null);
							this.element.replaceWith($compile(this.element.clone())(this.scope)); // IE fix
						}
					};

					return FileSelect;
				}(FileDirective);
			}

			__identity.$inject = ['$compile', 'FileDirective'];

			/***/
		},
/* 8 */
/***/ function (module, exports) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			var _angular = angular;
			var bind = _angular.bind;
			var isUndefined = _angular.isUndefined;
			function __identity($q) {

				return function () {
					/**
					 * @param {Array<Function>} pipes
					 */

					function Pipeline() {
						var pipes = arguments.length <= 0 || arguments[0] === undefined ? [] : arguments[0];

						_classCallCheck(this, Pipeline);

						this.pipes = pipes;
					}

					Pipeline.prototype.next = function next(args) {
						var pipe = this.pipes.shift();
						if (isUndefined(pipe)) {
							this.onSuccessful.apply(this, _toConsumableArray(args));
							return;
						}
						var err = new Error('The filter has not passed');
						err.pipe = pipe;
						err.args = args;
						if (pipe.isAsync) {
							var deferred = $q.defer();
							var onFulfilled = bind(this, this.next, args);
							var onRejected = bind(this, this.onThrown, err);
							deferred.promise.then(onFulfilled, onRejected);
							pipe.apply(undefined, _toConsumableArray(args).concat([deferred]));
						} else {
							var isDone = Boolean(pipe.apply(undefined, _toConsumableArray(args)));
							if (isDone) {
								this.next(args);
							} else {
								this.onThrown(err);
							}
						}
					};

					Pipeline.prototype.exec = function exec() {
						for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
							args[_key] = arguments[_key];
						}

						this.next(args);
					};

					Pipeline.prototype.onThrown = function onThrown(err) { };

					Pipeline.prototype.onSuccessful = function onSuccessful() { };

					return Pipeline;
				}();
			}

			__identity.$inject = ['$q'];

			/***/
		},
/* 9 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

			function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

			var _angular = angular;
			var extend = _angular.extend;
			var forEach = _angular.forEach;
			function __identity(FileDirective) {

				return function (_FileDirective) {
					_inherits(FileDrop, _FileDirective);

					/**
					 * Creates instance of {FileDrop} object
					 * @param {Object} options
					 * @constructor
					 */

					function FileDrop(options) {
						_classCallCheck(this, FileDrop);

						var extendedOptions = extend(options, {
							// Map of events
							events: {
								$destroy: 'destroy',
								drop: 'onDrop',
								dragover: 'onDragOver',
								dragleave: 'onDragLeave'
							},
							// Name of property inside uploader._directive object
							prop: 'drop'
						});

						return _possibleConstructorReturn(this, _FileDirective.call(this, extendedOptions));
					}
					/**
					 * Returns options
					 * @return {Object|undefined}
					 */


					FileDrop.prototype.getOptions = function getOptions() { };
					/**
					 * Returns filters
					 * @return {Array<Function>|String|undefined}
					 */


					FileDrop.prototype.getFilters = function getFilters() { };
					/**
					 * Event handler
					 */


					FileDrop.prototype.onDrop = function onDrop(event) {
						var transfer = this._getTransfer(event);
						if (!transfer) return;
						var options = this.getOptions();
						var filters = this.getFilters();
						this._preventAndStop(event);
						forEach(this.uploader._directives.over, this._removeOverClass, this);
						this.uploader.addToQueue(transfer.files, options, filters);
					};
					/**
					 * Event handler
					 */


					FileDrop.prototype.onDragOver = function onDragOver(event) {
						var transfer = this._getTransfer(event);
						if (!this._haveFiles(transfer.types)) return;
						transfer.dropEffect = 'copy';
						this._preventAndStop(event);
						forEach(this.uploader._directives.over, this._addOverClass, this);
					};
					/**
					 * Event handler
					 */


					FileDrop.prototype.onDragLeave = function onDragLeave(event) {
						if (event.currentTarget === this.element[0]) return;
						this._preventAndStop(event);
						forEach(this.uploader._directives.over, this._removeOverClass, this);
					};
					/**
					 * Helper
					 */


					FileDrop.prototype._getTransfer = function _getTransfer(event) {
						return event.dataTransfer ? event.dataTransfer : event.originalEvent.dataTransfer; // jQuery fix;
					};
					/**
					 * Helper
					 */


					FileDrop.prototype._preventAndStop = function _preventAndStop(event) {
						event.preventDefault();
						event.stopPropagation();
					};
					/**
					 * Returns "true" if types contains files
					 * @param {Object} types
					 */


					FileDrop.prototype._haveFiles = function _haveFiles(types) {
						if (!types) return false;
						if (types.indexOf) {
							return types.indexOf('Files') !== -1;
						} else if (types.contains) {
							return types.contains('Files');
						} else {
							return false;
						}
					};
					/**
					 * Callback
					 */


					FileDrop.prototype._addOverClass = function _addOverClass(item) {
						item.addOverClass();
					};
					/**
					 * Callback
					 */


					FileDrop.prototype._removeOverClass = function _removeOverClass(item) {
						item.removeOverClass();
					};

					return FileDrop;
				}(FileDirective);
			}

			__identity.$inject = ['FileDirective'];

			/***/
		},
/* 10 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

			function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

			function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

			var _angular = angular;
			var extend = _angular.extend;
			function __identity(FileDirective) {

				return function (_FileDirective) {
					_inherits(FileOver, _FileDirective);

					/**
					 * Creates instance of {FileDrop} object
					 * @param {Object} options
					 * @constructor
					 */

					function FileOver(options) {
						_classCallCheck(this, FileOver);

						var extendedOptions = extend(options, {
							// Map of events
							events: {
								$destroy: 'destroy'
							},
							// Name of property inside uploader._directive object
							prop: 'over',
							// Over class
							overClass: 'nv-file-over'
						});

						return _possibleConstructorReturn(this, _FileDirective.call(this, extendedOptions));
					}
					/**
					 * Adds over class
					 */


					FileOver.prototype.addOverClass = function addOverClass() {
						this.element.addClass(this.getOverClass());
					};
					/**
					 * Removes over class
					 */


					FileOver.prototype.removeOverClass = function removeOverClass() {
						this.element.removeClass(this.getOverClass());
					};
					/**
					 * Returns over class
					 * @returns {String}
					 */


					FileOver.prototype.getOverClass = function getOverClass() {
						return this.overClass;
					};

					return FileOver;
				}(FileDirective);
			}

			__identity.$inject = ['FileDirective'];

			/***/
		},
/* 11 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function __identity($parse, FileUploader, FileSelect) {

				return {
					link: function link(scope, element, attributes) {
						var uploader = scope.$eval(attributes.uploader);

						if (!(uploader instanceof FileUploader)) {
							throw new TypeError('"Uploader" must be an instance of FileUploader');
						}

						var object = new FileSelect({
							uploader: uploader,
							element: element,
							scope: scope
						});

						object.getOptions = $parse(attributes.options).bind(object, scope);
						object.getFilters = function () {
							return attributes.filters;
						};
					}
				};
			}

			__identity.$inject = ['$parse', 'FileUploader', 'FileSelect'];

			/***/
		},
/* 12 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function __identity($parse, FileUploader, FileDrop) {

				return {
					link: function link(scope, element, attributes) {
						var uploader = scope.$eval(attributes.uploader);

						if (!(uploader instanceof FileUploader)) {
							throw new TypeError('"Uploader" must be an instance of FileUploader');
						}

						if (!uploader.isHTML5) return;

						var object = new FileDrop({
							uploader: uploader,
							element: element
						});

						object.getOptions = $parse(attributes.options).bind(object, scope);
						object.getFilters = function () {
							return attributes.filters;
						};
					}
				};
			}

			__identity.$inject = ['$parse', 'FileUploader', 'FileDrop'];

			/***/
		},
/* 13 */
/***/ function (module, exports, __webpack_require__) {

			'use strict';

			Object.defineProperty(exports, "__esModule", {
				value: true
			});
			exports.default = __identity;

			var _config = __webpack_require__(1);

			var _config2 = _interopRequireDefault(_config);

			function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

			function __identity(FileUploader, FileOver) {

				return {
					link: function link(scope, element, attributes) {
						var uploader = scope.$eval(attributes.uploader);

						if (!(uploader instanceof FileUploader)) {
							throw new TypeError('"Uploader" must be an instance of FileUploader');
						}

						var object = new FileOver({
							uploader: uploader,
							element: element
						});

						object.getOverClass = function () {
							return attributes.overClass || object.overClass;
						};
					}
				};
			}

			__identity.$inject = ['FileUploader', 'FileOver'];

			/***/
		}
/******/])
});
;
//# sourceMappingURL=angular-file-upload.js.map
'use strict';
angular.module("ngLocale", [], ["$provide", function ($provide) {
	var PLURAL_CATEGORY = { ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other" };
	function getDecimals(n) {
		n = n + '';
		var i = n.indexOf('.');
		return (i == -1) ? 0 : n.length - i - 1;
	}

	function getVF(n, opt_precision) {
		var v = opt_precision;

		if (undefined === v) {
			v = Math.min(getDecimals(n), 3);
		}

		var base = Math.pow(10, v);
		var f = ((n * base) | 0) % base;
		return { v: v, f: f };
	}

	$provide.value("$locale", {
		"DATETIME_FORMATS": {
			"AMPMS": [
				"AM",
				"PM"
			],
			"DAY": [
				"domenica",
				"luned\u00ec",
				"marted\u00ec",
				"mercoled\u00ec",
				"gioved\u00ec",
				"venerd\u00ec",
				"sabato"
			],
			"ERANAMES": [
				"a.C.",
				"d.C."
			],
			"ERAS": [
				"a.C.",
				"d.C."
			],
			"FIRSTDAYOFWEEK": 0,
			"MONTH": [
				"gennaio",
				"febbraio",
				"marzo",
				"aprile",
				"maggio",
				"giugno",
				"luglio",
				"agosto",
				"settembre",
				"ottobre",
				"novembre",
				"dicembre"
			],
			"SHORTDAY": [
				"dom",
				"lun",
				"mar",
				"mer",
				"gio",
				"ven",
				"sab"
			],
			"SHORTMONTH": [
				"gen",
				"feb",
				"mar",
				"apr",
				"mag",
				"giu",
				"lug",
				"ago",
				"set",
				"ott",
				"nov",
				"dic"
			],
			"STANDALONEMONTH": [
				"Gennaio",
				"Febbraio",
				"Marzo",
				"Aprile",
				"Maggio",
				"Giugno",
				"Luglio",
				"Agosto",
				"Settembre",
				"Ottobre",
				"Novembre",
				"Dicembre"
			],
			"WEEKENDRANGE": [
				5,
				6
			],
			"fullDate": "EEEE d MMMM y",
			"longDate": "d MMMM y",
			"medium": "dd MMM y HH:mm:ss",
			"mediumDate": "dd MMM y",
			"mediumTime": "HH:mm:ss",
			"short": "dd/MM/yy HH:mm",
			"shortDate": "dd/MM/yy",
			"shortTime": "HH:mm"
		},
		"NUMBER_FORMATS": {
			"CURRENCY_SYM": "\u20ac",
			"DECIMAL_SEP": ",",
			"GROUP_SEP": ".",
			"PATTERNS": [
				{
					"gSize": 3,
					"lgSize": 3,
					"maxFrac": 3,
					"minFrac": 0,
					"minInt": 1,
					"negPre": "-",
					"negSuf": "",
					"posPre": "",
					"posSuf": ""
				},
				{
					"gSize": 3,
					"lgSize": 3,
					"maxFrac": 2,
					"minFrac": 2,
					"minInt": 1,
					"negPre": "-",
					"negSuf": "\u00a0\u00a4",
					"posPre": "",
					"posSuf": "\u00a0\u00a4"
				}
			]
		},
		"id": "it-it",
		"localeID": "it_IT",
		"pluralCat": function (n, opt_precision) { var i = n | 0; var vf = getVF(n, opt_precision); if (i == 1 && vf.v == 0) { return PLURAL_CATEGORY.ONE; } return PLURAL_CATEGORY.OTHER; }
	});
}]);
/*
 AngularJS v1.8.2
 (c) 2010-2020 Google LLC. http://angularjs.org
 License: MIT
*/
(function (z, l) {
	'use strict'; function q(n) {
		return ["$animate", function (u) {
			return {
				restrict: "AE", transclude: "element", priority: 1, terminal: !0, require: "^^ngMessages", link: function (m, f, a, e, r) {
					var b, h, s; if (!n) { b = f[0]; s = a.ngMessage || a.when; a = a.ngMessageExp || a.whenExp; var k = function (c) { h = c ? v(c) ? c : c.split(/[\s,]+/) : null; e.reRender() }; a ? (k(m.$eval(a)), m.$watchCollection(a, k)) : k(s) } var g, t; e.register(b, t = {
						test: function (c) { var b = h; c = b ? v(b) ? 0 <= b.indexOf(c) : b.hasOwnProperty(c) : void 0; return c }, attach: function () {
							g ||
								r(function (c, a) { u.enter(c, null, f); g = c; var d = g.$$attachId = e.getAttachId(); g.on("$destroy", function () { g && g.$$attachId === d && (e.deregister(b, n), t.detach()); a.$destroy() }) })
						}, detach: function () { if (g) { var c = g; g = null; u.leave(c) } }
					}, n); m.$on("$destroy", function () { e.deregister(b, n) })
				}
			}
		}]
	} var x, v, p, y; l.module("ngMessages", [], function () { x = l.forEach; v = l.isArray; p = l.isString; y = l.element }).info({ angularVersion: "1.8.2" }).directive("ngMessages", ["$animate", function (n) {
		function u(f, a) { return p(a) && 0 === a.length || m(f.$eval(a)) }
		function m(f) { return p(f) ? f.length : !!f } return {
			require: "ngMessages", restrict: "AE", controller: ["$element", "$scope", "$attrs", function (f, a, e) {
				function r(c, b) { for (var d = b, a = []; d && d !== c;) { var e = d.$$ngMessageNode; if (e && e.length) return k[e]; d.childNodes.length && -1 === a.indexOf(d) ? (a.push(d), d = d.childNodes[d.childNodes.length - 1]) : d.previousSibling ? d = d.previousSibling : (d = d.parentNode, a.push(d)) } } var b = this, h = 0, s = 0; this.getAttachId = function () { return s++ }; var k = this.messages = {}, g, t; this.render = function (c) {
					c = c ||
						{}; g = !1; t = c; for (var w = u(a, e.ngMessagesMultiple) || u(a, e.multiple), d = [], r = {}, h = 0, k = b.head, s = !1, l = 0; null != k;) { l++; var p = k.message, q = !1; s || x(c, function (b, c) { m(b) && !q && (h++, p.test(c) && !r[c] && (q = r[c] = !0, p.attach())) }); q ? s = !w : d.push(p); k = k.next } x(d, function (c) { c.detach() }); c = d.length !== l; (w = b.default && !c && 0 < h) ? b.default.attach() : b.default && b.default.detach(); c || w ? n.setClass(f, "ng-active", "ng-inactive") : n.setClass(f, "ng-inactive", "ng-active")
				}; a.$watchCollection(e.ngMessages || e["for"], b.render); this.reRender =
					function () { g || (g = !0, a.$evalAsync(function () { g && t && b.render(t) })) }; this.register = function (c, a, d) { if (d) b.default = a; else { d = h.toString(); k[d] = { message: a }; var e = f[0]; a = k[d]; b.head ? (e = r(e, c)) ? (a.next = e.next, e.next = a) : (a.next = b.head, b.head = a) : b.head = a; c.$$ngMessageNode = d; h++ } b.reRender() }; this.deregister = function (a, e) { if (e) delete b.default; else { var d = a.$$ngMessageNode; delete a.$$ngMessageNode; var g = k[d]; if (g) { var h = r(f[0], a); h ? h.next = g.next : b.head = g.next } delete k[d] } b.reRender() }
			}]
		}
	}]).directive("ngMessagesInclude",
		["$templateRequest", "$document", "$compile", function (n, l, m) { function f(a, e) { var f = m.$$createComment ? m.$$createComment("ngMessagesInclude", e) : l[0].createComment(" ngMessagesInclude: " + e + " "), f = y(f); a.after(f); a.remove() } return { restrict: "AE", require: "^^ngMessages", link: function (a, e, l) { var b = l.ngMessagesInclude || l.src; n(b).then(function (h) { a.$$destroyed || (p(h) && !h.trim() ? f(e, b) : m(h)(a, function (a) { e.after(a); f(e, b) })) }) } } }]).directive("ngMessage", q()).directive("ngMessageExp", q()).directive("ngMessageDefault",
			q(!0))
})(window, window.angular);
//# sourceMappingURL=angular-messages.min.js.map

/*!
 * Angular-PDF: An Angularjs directive <ng-pdf> to display PDF in the browser with PDFJS.
 * @version 2.0.0
 * @link https://github.com/sayanee/angular-pdf#readme
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */
(function webpackUniversalModuleDefinition(root, factory) {
	if (typeof exports === 'object' && typeof module === 'object')
		module.exports = factory(require("angular"));
	else if (typeof define === 'function' && define.amd)
		define("pdf", ["angular"], factory);
	else if (typeof exports === 'object')
		exports["pdf"] = factory(require("angular"));
	else
		root["pdf"] = factory(root["angular"]);
})(this, function (__WEBPACK_EXTERNAL_MODULE_0__) {
	return /******/ (function (modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if (installedModules[moduleId]) {
/******/ 			return installedModules[moduleId].exports;
				/******/
			}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
				/******/
			};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
			/******/
		}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// identity function for calling harmony imports with the correct context
/******/ 	__webpack_require__.i = function (value) { return value; };
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function (exports, name, getter) {
/******/ 		if (!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
			/******/
		});
				/******/
			}
			/******/
		};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function (module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
			/******/
		};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function (object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "/";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = "./src/angular-pdf.module.js");
		/******/
	})
/************************************************************************/
/******/({

/***/ "./src/angular-pdf.directive.js":
/***/ (function (module, exports, __webpack_require__) {

				"use strict";


				Object.defineProperty(exports, "__esModule", {
					value: true
				});
				var NgPdf = exports.NgPdf = ["$window", "$document", "$log", function NgPdf($window, $document, $log) {
					'ngInject';

					var backingScale = function backingScale(canvas) {
						var ctx = canvas.getContext('2d');
						var dpr = $window.devicePixelRatio || 1;
						var bsr = ctx.webkitBackingStorePixelRatio || ctx.mozBackingStorePixelRatio || ctx.msBackingStorePixelRatio || ctx.oBackingStorePixelRatio || ctx.backingStorePixelRatio || 1;

						return dpr / bsr;
					};

					var setCanvasDimensions = function setCanvasDimensions(canvas, w, h) {
						var ratio = backingScale(canvas);
						canvas.width = Math.floor(w * ratio);
						canvas.height = Math.floor(h * ratio);
						canvas.style.width = Math.floor(w) + 'px';
						canvas.style.height = Math.floor(h) + 'px';
						canvas.getContext('2d').setTransform(ratio, 0, 0, ratio, 0, 0);
						return canvas;
					};

					var initCanvas = function initCanvas(element, canvas) {
						angular.element(canvas).addClass('rotate0');
						element.append(canvas);
					};

					return {
						restrict: 'E',
						templateUrl: function templateUrl(element, attr) {
							return attr.templateUrl ? attr.templateUrl : 'partials/viewer.html';
						},
						link: function link(scope, element, attrs) {
							var renderTask = null;
							var pdfLoaderTask = null;
							var debug = false;
							var url = scope.pdfUrl;
							var httpHeaders = scope.httpHeaders;
							var pdfDoc = null;
							var pageToDisplay = isFinite(attrs.page) ? parseInt(attrs.page) : 1;
							var pageFit = attrs.scale === 'page-fit';
							var limitHeight = attrs.limitcanvasheight === '1';
							var scale = attrs.scale > 0 ? attrs.scale : 1;
							var canvas = $document[0].createElement('canvas');
							initCanvas(element, canvas);
							var creds = attrs.usecredentials;
							debug = attrs.hasOwnProperty('debug') ? attrs.debug : false;

							var ctx = canvas.getContext('2d');
							var windowEl = angular.element($window);

							element.css('display', 'block');

							windowEl.on('scroll', function () {
								scope.$apply(function () {
									scope.scroll = windowEl[0].scrollY;
								});
							});

							PDFJS.disableWorker = true;
							scope.pageNum = pageToDisplay;

							scope.renderPage = function (num) {
								if (renderTask) {
									renderTask._internalRenderTask.cancel();
								}

								pdfDoc.getPage(num).then(function (page) {
									var viewport = void 0;
									var pageWidthScale = void 0;
									var renderContext = void 0;

									if (pageFit) {
										viewport = page.getViewport(1);
										var clientRect = element[0].getBoundingClientRect();
										pageWidthScale = clientRect.width / viewport.width;
										if (limitHeight) {
											pageWidthScale = Math.min(pageWidthScale, clientRect.height / viewport.height);
										}
										scale = pageWidthScale;
									}
									viewport = page.getViewport(scale);

									setCanvasDimensions(canvas, viewport.width, viewport.height);

									renderContext = {
										canvasContext: ctx,
										viewport: viewport
									};

									renderTask = page.render(renderContext);
									renderTask.promise.then(function () {
										if (angular.isFunction(scope.onPageRender)) {
											scope.onPageRender();
										}
									}).catch(function (reason) {
										$log.log(reason);
									});
								});
							};

							scope.goPrevious = function () {
								if (scope.pageToDisplay <= 1) {
									return;
								}
								scope.pageToDisplay = parseInt(scope.pageToDisplay) - 1;
								scope.pageNum = scope.pageToDisplay;
							};

							scope.goNext = function () {
								if (scope.pageToDisplay >= pdfDoc.numPages) {
									return;
								}
								scope.pageToDisplay = parseInt(scope.pageToDisplay) + 1;
								scope.pageNum = scope.pageToDisplay;
							};

							scope.zoomIn = function () {
								pageFit = false;
								scale = parseFloat(scale) + 0.2;
								scope.renderPage(scope.pageToDisplay);
								return scale;
							};

							scope.zoomOut = function () {
								pageFit = false;
								scale = parseFloat(scale) - 0.2;
								scope.renderPage(scope.pageToDisplay);
								return scale;
							};

							scope.fit = function () {
								pageFit = true;
								scope.renderPage(scope.pageToDisplay);
							};

							scope.changePage = function () {
								scope.renderPage(scope.pageToDisplay);
							};

							scope.rotate = function () {
								if (canvas.getAttribute('class') === 'rotate0') {
									canvas.setAttribute('class', 'rotate90');
								} else if (canvas.getAttribute('class') === 'rotate90') {
									canvas.setAttribute('class', 'rotate180');
								} else if (canvas.getAttribute('class') === 'rotate180') {
									canvas.setAttribute('class', 'rotate270');
								} else {
									canvas.setAttribute('class', 'rotate0');
								}
							};

							function clearCanvas() {
								if (ctx) {
									ctx.clearRect(0, 0, canvas.width, canvas.height);
								}
							}

							function renderPDF() {
								clearCanvas();

								var params = {
									'url': url,
									'withCredentials': creds
								};

								if (httpHeaders) {
									params.httpHeaders = httpHeaders;
								}

								if (url && url.length) {
									pdfLoaderTask = PDFJS.getDocument(params);
									pdfLoaderTask.onProgress = scope.onProgress;
									pdfLoaderTask.onPassword = scope.onPassword;
									pdfLoaderTask.then(function (_pdfDoc) {
										if (angular.isFunction(scope.onLoad)) {
											scope.onLoad();
										}

										pdfDoc = _pdfDoc;
										scope.renderPage(scope.pageToDisplay);

										scope.$apply(function () {
											scope.pageCount = _pdfDoc.numPages;
										});
									}, function (error) {
										if (error) {
											if (angular.isFunction(scope.onError)) {
												scope.onError(error);
											}
										}
									});
								}
							}

							scope.$watch('pageNum', function (newVal) {
								scope.pageToDisplay = parseInt(newVal);
								if (pdfDoc !== null) {
									scope.renderPage(scope.pageToDisplay);
								}
							});

							scope.$watch('pdfUrl', function (newVal) {
								if (newVal !== '') {
									if (debug) {
										$log.log('pdfUrl value change detected: ', scope.pdfUrl);
									}
									url = newVal;
									scope.pageNum = scope.pageToDisplay = pageToDisplay;
									if (pdfLoaderTask) {
										pdfLoaderTask.destroy().then(function () {
											renderPDF();
										});
									} else {
										renderPDF();
									}
								}
							});
						}
					};
				}];

				/***/
			}),

/***/ "./src/angular-pdf.module.js":
/***/ (function (module, exports, __webpack_require__) {

				"use strict";


				Object.defineProperty(exports, "__esModule", {
					value: true
				});
				exports.Pdf = undefined;

				var _angular = __webpack_require__(0);

				var _angular2 = _interopRequireDefault(_angular);

				var _angularPdf = __webpack_require__("./src/angular-pdf.directive.js");

				function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

				var Pdf = exports.Pdf = _angular2.default.module('pdf', []).directive('ngPdf', _angularPdf.NgPdf).name;

				exports.default = Pdf;

				/***/
			}),

/***/ 0:
/***/ (function (module, exports) {

				module.exports = __WEBPACK_EXTERNAL_MODULE_0__;

				/***/
			})

		/******/
	});
});
//# sourceMappingURL=angular-pdf.js.map
/*
 AngularJS v1.8.2
 (c) 2010-2020 Google LLC. http://angularjs.org
 License: MIT
*/
(function (I, b) {
	'use strict'; function z(b, h) { var d = [], c = b.replace(/([().])/g, "\\$1").replace(/(\/)?:(\w+)(\*\?|[?*])?/g, function (b, c, h, k) { b = "?" === k || "*?" === k; k = "*" === k || "*?" === k; d.push({ name: h, optional: b }); c = c || ""; return (b ? "(?:" + c : c + "(?:") + (k ? "(.+?)" : "([^/]+)") + (b ? "?)?" : ")") }).replace(/([/$*])/g, "\\$1"); h.ignoreTrailingSlashes && (c = c.replace(/\/+$/, "") + "/*"); return { keys: d, regexp: new RegExp("^" + c + "(?:[?#]|$)", h.caseInsensitiveMatch ? "i" : "") } } function A(b) { p && b.get("$route") } function v(u, h, d) {
		return {
			restrict: "ECA",
			terminal: !0, priority: 400, transclude: "element", link: function (c, f, g, l, k) {
				function q() { r && (d.cancel(r), r = null); m && (m.$destroy(), m = null); s && (r = d.leave(s), r.done(function (b) { !1 !== b && (r = null) }), s = null) } function C() { var g = u.current && u.current.locals; if (b.isDefined(g && g.$template)) { var g = c.$new(), l = u.current; s = k(g, function (g) { d.enter(g, null, s || f).done(function (d) { !1 === d || !b.isDefined(w) || w && !c.$eval(w) || h() }); q() }); m = l.scope = g; m.$emit("$viewContentLoaded"); m.$eval(p) } else q() } var m, s, r, w = g.autoscroll, p = g.onload ||
					""; c.$on("$routeChangeSuccess", C); C()
			}
		}
	} function x(b, h, d) { return { restrict: "ECA", priority: -400, link: function (c, f) { var g = d.current, l = g.locals; f.html(l.$template); var k = b(f.contents()); if (g.controller) { l.$scope = c; var q = h(g.controller, l); g.controllerAs && (c[g.controllerAs] = q); f.data("$ngControllerController", q); f.children().data("$ngControllerController", q) } c[g.resolveAs || "$resolve"] = l; k(c) } } } var D, E, F, G, y = b.module("ngRoute", []).info({ angularVersion: "1.8.2" }).provider("$route", function () {
		function u(d,
			c) { return b.extend(Object.create(d), c) } D = b.isArray; E = b.isObject; F = b.isDefined; G = b.noop; var h = {}; this.when = function (d, c) {
				var f; f = void 0; if (D(c)) { f = f || []; for (var g = 0, l = c.length; g < l; g++)f[g] = c[g] } else if (E(c)) for (g in f = f || {}, c) if ("$" !== g.charAt(0) || "$" !== g.charAt(1)) f[g] = c[g]; f = f || c; b.isUndefined(f.reloadOnUrl) && (f.reloadOnUrl = !0); b.isUndefined(f.reloadOnSearch) && (f.reloadOnSearch = !0); b.isUndefined(f.caseInsensitiveMatch) && (f.caseInsensitiveMatch = this.caseInsensitiveMatch); h[d] = b.extend(f, { originalPath: d },
					d && z(d, f)); d && (g = "/" === d[d.length - 1] ? d.substr(0, d.length - 1) : d + "/", h[g] = b.extend({ originalPath: d, redirectTo: d }, z(g, f))); return this
			}; this.caseInsensitiveMatch = !1; this.otherwise = function (b) { "string" === typeof b && (b = { redirectTo: b }); this.when(null, b); return this }; p = !0; this.eagerInstantiationEnabled = function (b) { return F(b) ? (p = b, this) : p }; this.$get = ["$rootScope", "$location", "$routeParams", "$q", "$injector", "$templateRequest", "$sce", "$browser", function (d, c, f, g, l, k, q, p) {
				function m(a) {
					var e = t.current; n = A(); (x =
						!B && n && e && n.$$route === e.$$route && (!n.reloadOnUrl || !n.reloadOnSearch && b.equals(n.pathParams, e.pathParams))) || !e && !n || d.$broadcast("$routeChangeStart", n, e).defaultPrevented && a && a.preventDefault()
				} function s() {
					var a = t.current, e = n; if (x) a.params = e.params, b.copy(a.params, f), d.$broadcast("$routeUpdate", a); else if (e || a) {
						B = !1; t.current = e; var c = g.resolve(e); p.$$incOutstandingRequestCount("$route"); c.then(r).then(w).then(function (g) {
							return g && c.then(y).then(function (c) {
								e === t.current && (e && (e.locals = c, b.copy(e.params,
									f)), d.$broadcast("$routeChangeSuccess", e, a))
							})
						}).catch(function (b) { e === t.current && d.$broadcast("$routeChangeError", e, a, b) }).finally(function () { p.$$completeOutstandingRequest(G, "$route") })
					}
				} function r(a) {
					var e = { route: a, hasRedirection: !1 }; if (a) if (a.redirectTo) if (b.isString(a.redirectTo)) e.path = v(a.redirectTo, a.params), e.search = a.params, e.hasRedirection = !0; else { var d = c.path(), f = c.search(); a = a.redirectTo(a.pathParams, d, f); b.isDefined(a) && (e.url = a, e.hasRedirection = !0) } else if (a.resolveRedirectTo) return g.resolve(l.invoke(a.resolveRedirectTo)).then(function (a) {
						b.isDefined(a) &&
							(e.url = a, e.hasRedirection = !0); return e
					}); return e
				} function w(a) { var b = !0; if (a.route !== t.current) b = !1; else if (a.hasRedirection) { var g = c.url(), d = a.url; d ? c.url(d).replace() : d = c.path(a.path).search(a.search).replace().url(); d !== g && (b = !1) } return b } function y(a) { if (a) { var e = b.extend({}, a.resolve); b.forEach(e, function (a, c) { e[c] = b.isString(a) ? l.get(a) : l.invoke(a, null, null, c) }); a = z(a); b.isDefined(a) && (e.$template = a); return g.all(e) } } function z(a) {
					var e, c; b.isDefined(e = a.template) ? b.isFunction(e) && (e = e(a.params)) :
						b.isDefined(c = a.templateUrl) && (b.isFunction(c) && (c = c(a.params)), b.isDefined(c) && (a.loadedTemplateUrl = q.valueOf(c), e = k(c))); return e
				} function A() { var a, e; b.forEach(h, function (d, g) { var f; if (f = !e) { var h = c.path(); f = d.keys; var l = {}; if (d.regexp) if (h = d.regexp.exec(h)) { for (var k = 1, p = h.length; k < p; ++k) { var m = f[k - 1], n = h[k]; m && n && (l[m.name] = n) } f = l } else f = null; else f = null; f = a = f } f && (e = u(d, { params: b.extend({}, c.search(), a), pathParams: a }), e.$$route = d) }); return e || h[null] && u(h[null], { params: {}, pathParams: {} }) } function v(a,
					c) { var d = []; b.forEach((a || "").split(":"), function (a, b) { if (0 === b) d.push(a); else { var f = a.match(/(\w+)(?:[?*])?(.*)/), g = f[1]; d.push(c[g]); d.push(f[2] || ""); delete c[g] } }); return d.join("") } var B = !1, n, x, t = {
						routes: h, reload: function () { B = !0; var a = { defaultPrevented: !1, preventDefault: function () { this.defaultPrevented = !0; B = !1 } }; d.$evalAsync(function () { m(a); a.defaultPrevented || s() }) }, updateParams: function (a) {
							if (this.current && this.current.$$route) a = b.extend({}, this.current.params, a), c.path(v(this.current.$$route.originalPath,
								a)), c.search(a); else throw H("norout");
						}
					}; d.$on("$locationChangeStart", m); d.$on("$locationChangeSuccess", s); return t
			}]
	}).run(A), H = b.$$minErr("ngRoute"), p; A.$inject = ["$injector"]; y.provider("$routeParams", function () { this.$get = function () { return {} } }); y.directive("ngView", v); y.directive("ngView", x); v.$inject = ["$route", "$anchorScroll", "$animate"]; x.$inject = ["$compile", "$controller", "$route"]
})(window, window.angular);
//# sourceMappingURL=angular-route.min.js.map
/*
 AngularJS v1.8.2
 (c) 2010-2020 Google LLC. http://angularjs.org
 License: MIT
*/
(function (s, e) {
	'use strict'; function O(e) { var g = []; B(g, D).chars(e); return g.join("") } var C = e.$$minErr("$sanitize"), E, g, F, G, H, q, D, I, J, B; e.module("ngSanitize", []).provider("$sanitize", function () {
		function h(a, d) { return A(a.split(","), d) } function A(a, d) { var c = {}, b; for (b = 0; b < a.length; b++)c[d ? q(a[b]) : a[b]] = !0; return c } function t(a, d) { d && d.length && g(a, A(d)) } function P(a) { for (var d = {}, c = 0, b = a.length; c < b; c++) { var k = a[c]; d[k.name] = k.value } return d } function K(a) {
			return a.replace(/&/g, "&amp;").replace(Q, function (a) {
				var c =
					a.charCodeAt(0); a = a.charCodeAt(1); return "&#" + (1024 * (c - 55296) + (a - 56320) + 65536) + ";"
			}).replace(u, function (a) { return "&#" + a.charCodeAt(0) + ";" }).replace(/</g, "&lt;").replace(/>/g, "&gt;")
		} function z(a) { for (; a;) { if (a.nodeType === s.Node.ELEMENT_NODE) for (var d = a.attributes, c = 0, b = d.length; c < b; c++) { var k = d[c], f = k.name.toLowerCase(); if ("xmlns:ns1" === f || 0 === f.lastIndexOf("ns1:", 0)) a.removeAttributeNode(k), c--, b-- } (d = a.firstChild) && z(d); a = v("nextSibling", a) } } function v(a, d) {
			var c = d[a]; if (c && I.call(d, c)) throw C("elclob",
				d.outerHTML || d.outerText); return c
		} var y = !1, f = !1; this.$get = ["$$sanitizeUri", function (a) { y = !0; f && g(m, l); return function (d) { var c = []; J(d, B(c, function (b, c) { return !/^unsafe:/.test(a(b, c)) })); return c.join("") } }]; this.enableSvg = function (a) { return H(a) ? (f = a, this) : f }; this.addValidElements = function (a) { y || (G(a) && (a = { htmlElements: a }), t(l, a.svgElements), t(r, a.htmlVoidElements), t(m, a.htmlVoidElements), t(m, a.htmlElements)); return this }; this.addValidAttrs = function (a) { y || g(L, A(a, !0)); return this }; E = e.bind; g = e.extend;
		F = e.forEach; G = e.isArray; H = e.isDefined; q = e.$$lowercase; D = e.noop; J = function (a, d) {
			null === a || void 0 === a ? a = "" : "string" !== typeof a && (a = "" + a); var c = M(a); if (!c) return ""; var b = 5; do { if (0 === b) throw C("uinput"); b--; a = c.innerHTML; c = M(a) } while (a !== c.innerHTML); for (b = c.firstChild; b;) {
				switch (b.nodeType) { case 1: d.start(b.nodeName.toLowerCase(), P(b.attributes)); break; case 3: d.chars(b.textContent) }var k; if (!(k = b.firstChild) && (1 === b.nodeType && d.end(b.nodeName.toLowerCase()), k = v("nextSibling", b), !k)) for (; null == k;) {
					b =
						v("parentNode", b); if (b === c) break; k = v("nextSibling", b); 1 === b.nodeType && d.end(b.nodeName.toLowerCase())
				} b = k
			} for (; b = c.firstChild;)c.removeChild(b)
		}; B = function (a, d) {
			var c = !1, b = E(a, a.push); return {
				start: function (a, f) { a = q(a); !c && w[a] && (c = a); c || !0 !== m[a] || (b("<"), b(a), F(f, function (c, f) { var e = q(f), h = "img" === a && "src" === e || "background" === e; !0 !== L[e] || !0 === N[e] && !d(c, h) || (b(" "), b(f), b('="'), b(K(c)), b('"')) }), b(">")) }, end: function (a) { a = q(a); c || !0 !== m[a] || !0 === r[a] || (b("</"), b(a), b(">")); a == c && (c = !1) }, chars: function (a) {
					c ||
						b(K(a))
				}
			}
		}; I = s.Node.prototype.contains || function (a) { return !!(this.compareDocumentPosition(a) & 16) }; var Q = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g, u = /([^#-~ |!])/g, r = h("area,br,col,hr,img,wbr"), x = h("colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr"), p = h("rp,rt"), n = g({}, p, x), x = g({}, x, h("address,article,aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,section,table,ul")), p = g({}, p, h("a,abbr,acronym,b,bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s,samp,small,span,strike,strong,sub,sup,time,tt,u,var")),
			l = h("circle,defs,desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,hkern,image,linearGradient,line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,radialGradient,rect,stop,svg,switch,text,title,tspan"), w = h("script,style"), m = g({}, r, x, p, n), N = h("background,cite,href,longdesc,src,xlink:href,xml:base"), n = h("abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,scope,scrolling,shape,size,span,start,summary,tabindex,target,title,type,valign,value,vspace,width"),
			p = h("accent-height,accumulate,additive,alphabetic,arabic-form,ascent,baseProfile,bbox,begin,by,calcMode,cap-height,class,color,color-rendering,content,cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,font-size,font-stretch,font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,gradientUnits,hanging,height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,keySplines,keyTimes,lang,marker-end,marker-mid,marker-start,markerHeight,markerUnits,markerWidth,mathematical,max,min,offset,opacity,orient,origin,overline-position,overline-thickness,panose-1,path,pathLength,points,preserveAspectRatio,r,refX,refY,repeatCount,repeatDur,requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,stemv,stop-color,stop-opacity,strikethrough-position,strikethrough-thickness,stroke,stroke-dasharray,stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,stroke-opacity,stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,underline-position,underline-thickness,unicode,unicode-range,units-per-em,values,version,viewBox,visibility,width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,xlink:show,xlink:title,xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,zoomAndPan",
				!0), L = g({}, N, p, n), M = function (a, d) { function c(b) { b = "<remove></remove>" + b; try { var c = (new a.DOMParser).parseFromString(b, "text/html").body; c.firstChild.remove(); return c } catch (d) { } } var b; try { b = !!c("") } catch (f) { b = !1 } if (b) return c; if (!d || !d.implementation) throw C("noinert"); b = d.implementation.createHTMLDocument("inert"); var e = (b.documentElement || b.getDocumentElement()).querySelector("body"); return function (a) { e.innerHTML = a; d.documentMode && z(e); return e } }(s, s.document)
	}).info({ angularVersion: "1.8.2" });
	e.module("ngSanitize").filter("linky", ["$sanitize", function (h) {
		var g = /((s?ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/i, t = /^mailto:/i, q = e.$$minErr("linky"), s = e.isDefined, z = e.isFunction, v = e.isObject, y = e.isString; return function (f, e, u) {
			function r(e) { e && l.push(O(e)) } function x(f, h) { var g, a = p(f); l.push("<a "); for (g in a) l.push(g + '="' + a[g] + '" '); !s(e) || "target" in a || l.push('target="', e, '" '); l.push('href="', f.replace(/"/g, "&quot;"), '">'); r(h); l.push("</a>") } if (null ==
				f || "" === f) return f; if (!y(f)) throw q("notstring", f); for (var p = z(u) ? u : v(u) ? function () { return u } : function () { return {} }, n = f, l = [], w, m; f = n.match(g);)w = f[0], f[2] || f[4] || (w = (f[3] ? "http://" : "mailto:") + w), m = f.index, r(n.substr(0, m)), x(w, f[0].replace(t, "")), n = n.substring(m + f[0].length); r(n); return h(l.join(""))
		}
	}])
})(window, window.angular);
//# sourceMappingURL=angular-sanitize.min.js.map
/*!
 * angular-translate - v2.19.0 - 2021-09-02
 * 
 * Copyright (c) 2021 The angular-translate team, Pascal Precht; Licensed MIT
 */
!function (t, e) { "function" == typeof define && define.amd ? define([], function () { return e() }) : "object" == typeof module && module.exports ? module.exports = e() : e() }(0, function () { function t(e) { "use strict"; var n = e.storageKey(), a = e.storage(), t = function () { var t = e.preferredLanguage(); angular.isString(t) ? e.use(t) : a.put(n, e.use()) }; t.displayName = "fallbackFromIncorrectStorageValue", a ? a.get(n) ? e.use(a.get(n)).catch(t) : t() : angular.isString(e.preferredLanguage()) && e.use(e.preferredLanguage()) } function e(t, r, e, i) { "use strict"; var T, c, z, x, F, I, _, n, V, R, D, K, U, M, H, G, q = {}, Y = [], B = t, J = [], Q = "translate-cloak", W = !1, X = !1, Z = !0, tt = ".", et = !1, nt = !1, at = 0, rt = !0, a = "default", s = { default: function (t) { return (t || "").split("-").join("_") }, java: function (t) { var e = (t || "").split("-").join("_"), n = e.split("_"); return 1 < n.length ? n[0].toLowerCase() + "_" + n[1].toUpperCase() : e }, bcp47: function (t) { var e = (t || "").split("_").join("-"), n = e.split("-"); switch (n.length) { case 1: n[0] = n[0].toLowerCase(); break; case 2: n[0] = n[0].toLowerCase(), 4 === n[1].length ? n[1] = n[1].charAt(0).toUpperCase() + n[1].slice(1).toLowerCase() : n[1] = n[1].toUpperCase(); break; case 3: n[0] = n[0].toLowerCase(), n[1] = n[1].charAt(0).toUpperCase() + n[1].slice(1).toLowerCase(), n[2] = n[2].toUpperCase(); break; default: return e }return n.join("-") }, "iso639-1": function (t) { return (t || "").split("_").join("-").split("-")[0].toLowerCase() } }, o = function () { if (angular.isFunction(i.getLocale)) return i.getLocale(); var t, e, n = r.$get().navigator, a = ["language", "browserLanguage", "systemLanguage", "userLanguage"]; if (angular.isArray(n.languages)) for (t = 0; t < n.languages.length; t++)if ((e = n.languages[t]) && e.length) return e; for (t = 0; t < a.length; t++)if ((e = n[a[t]]) && e.length) return e; return null }; o.displayName = "angular-translate/service: getFirstBrowserLanguage"; var it = function () { var t = o() || ""; return s[a] && (t = s[a](t)), t }; it.displayName = "angular-translate/service: getLocale"; var st = function (t, e) { for (var n = 0, a = t.length; n < a; n++)if (t[n] === e) return n; return -1 }, ot = function () { return this.toString().replace(/^\s+|\s+$/g, "") }, f = function (t) { return angular.isString(t) ? t.toLowerCase() : t }, lt = function (t) { if (t) { for (var e, n = [], a = f(t), r = 0, i = Y.length; r < i; r++)n.push(f(Y[r])); if (-1 < (r = st(n, a))) return Y[r]; if (c) for (var s in c) if (c.hasOwnProperty(s)) { var o = !1, l = Object.prototype.hasOwnProperty.call(c, s) && f(s) === f(t); if ("*" === s.slice(-1) && (o = f(s.slice(0, -1)) === f(t.slice(0, s.length - 1))), (l || o) && (e = c[s], -1 < st(n, f(e)))) return e } var u = t.split("_"); return 1 < u.length && -1 < st(n, f(u[0])) ? u[0] : void 0 } }, ut = function (t, e) { if (!t && !e) return q; if (t && !e) { if (angular.isString(t)) return q[t] } else angular.isObject(q[t]) || (q[t] = {}), angular.extend(q[t], ct(e)); return this }; this.translations = ut, this.cloakClassName = function (t) { return t ? (Q = t, this) : Q }, this.allowNamespaces = function (t) { return void 0 === t ? Z : (Z = t, this) }, this.nestedObjectDelimeter = function (t) { return t ? (tt = t, this) : tt }; var ct = function (t, e, n, a) { var r, i, s; if (e || (e = []), n || (n = {}), !Z) return t; for (r in t) Object.prototype.hasOwnProperty.call(t, r) && (s = t[r], angular.isObject(s) ? ct(s, e.concat(r), n, r) : (i = e.length ? "" + e.join(tt) + tt + r : r, e.length && r === a && (n["" + e.join(tt)] = "@:" + i), n[i] = s)); return n }; ct.displayName = "flatObject", this.addInterpolation = function (t) { return J.push(t), this }, this.useMessageFormatInterpolation = function () { return this.useInterpolation("$translateMessageFormatInterpolation") }, this.useInterpolation = function (t) { return R = t, this }, this.useSanitizeValueStrategy = function (t) { return e.useStrategy(t), this }, this.preferredLanguage = function (t) { return t ? (ft(t), this) : T }; var ft = function (t) { return t && (T = t), T }; this.translationNotFoundIndicator = function (t) { return this.translationNotFoundIndicatorLeft(t), this.translationNotFoundIndicatorRight(t), this }, this.translationNotFoundIndicatorLeft = function (t) { return t ? (U = t, this) : U }, this.translationNotFoundIndicatorRight = function (t) { return t ? (M = t, this) : M }, this.fallbackLanguage = function (t) { return gt(t), this }; var gt = function (t) { return t ? (angular.isString(t) ? (x = !0, z = [t]) : angular.isArray(t) && (x = !1, z = t), angular.isString(T) && st(z, T) < 0 && z.push(T), this) : x ? z[0] : z }; this.use = function (t) { if (t) { if (!q[t] && !D) throw new Error("$translateProvider couldn't find translationTable for langKey: '" + t + "'"); return F = t, this } return F }, this.resolveClientLocale = function () { return it() }; var pt = function (t) { return t ? (B = t, this) : n ? n + B : B }; this.storageKey = pt, this.useUrlLoader = function (t, e) { return this.useLoader("$translateUrlLoader", angular.extend({ url: t }, e)) }, this.useStaticFilesLoader = function (t) { return this.useLoader("$translateStaticFilesLoader", t) }, this.useLoader = function (t, e) { return D = t, K = e || {}, this }, this.useLocalStorage = function () { return this.useStorage("$translateLocalStorage") }, this.useCookieStorage = function () { return this.useStorage("$translateCookieStorage") }, this.useStorage = function (t) { return _ = t, this }, this.storagePrefix = function (t) { return t ? (n = t, this) : t }, this.useMissingTranslationHandlerLog = function () { return this.useMissingTranslationHandler("$translateMissingTranslationHandlerLog") }, this.useMissingTranslationHandler = function (t) { return V = t, this }, this.usePostCompiling = function (t) { return W = !!t, this }, this.forceAsyncReload = function (t) { return X = !!t, this }, this.uniformLanguageTag = function (t) { return t ? angular.isString(t) && (t = { standard: t }) : t = {}, a = t.standard, this }, this.determinePreferredLanguage = function (t) { var e = t && angular.isFunction(t) ? t() : it(); return T = Y.length && lt(e) || e, this }, this.registerAvailableLanguageKeys = function (t, e) { return t ? (Y = t, e && (c = e), this) : Y }, this.useLoaderCache = function (t) { return !1 === t ? H = void 0 : !0 === t ? H = !0 : void 0 === t ? H = "$translationCache" : t && (H = t), this }, this.directivePriority = function (t) { return void 0 === t ? at : (at = t, this) }, this.statefulFilter = function (t) { return void 0 === t ? rt : (rt = t, this) }, this.postProcess = function (t) { return G = t || void 0, this }, this.keepContent = function (t) { return nt = !!t, this }, this.$get = ["$log", "$injector", "$rootScope", "$q", function (t, o, s, m) { var i, $, y, b = o.get(R || "$translateDefaultInterpolation"), S = !1, L = {}, f = {}, w = function (t, s, o, l, u, c) { !F && T && (F = T); var a = u && u !== F ? lt(u) || u : F; if (u && v(u), angular.isArray(t)) { return function (t) { for (var a = {}, e = [], n = function (e) { var n = m.defer(), t = function (t) { a[e] = t, n.resolve([e, t]) }; return w(e, s, o, l, u, c).then(t, t), n.promise }, r = 0, i = t.length; r < i; r++)e.push(n(t[r])); return m.all(e).then(function () { return a }) }(t) } var e = m.defer(); if (!t) throw new TypeError("translationId must be a not empty string"); t = ot.apply(t); var n = function () { var t = f[a] || f[T]; if ($ = 0, _ && !t) { var e = i.get(B); if (t = f[e], z && z.length) { var n = st(z, e); $ = 0 === n ? 1 : 0, st(z, T) < 0 && z.push(T) } } return t }(); if (n) { var r = function () { u || (a = F), h(t, s, o, l, a, c).then(e.resolve, e.reject) }; r.displayName = "promiseResolved", n.finally(r).catch(angular.noop) } else h(t, s, o, l, a, c).then(e.resolve, e.reject); return e.promise }, j = function (t) { return U && (t = [U, t].join(" ")), M && (t = [t, M].join(" ")), t }, l = function (t) { F = t, _ && i.put(w.storageKey(), F), s.$emit("$translateChangeSuccess", { language: t }), b.setLocale(F); var e = function (t, e) { L[e].setLocale(F) }; e.displayName = "eachInterpolatorLocaleSetter", angular.forEach(L, e), s.$emit("$translateChangeEnd", { language: t }) }, u = function (n) { if (!n) throw "No language key specified for loading."; var a = m.defer(); s.$emit("$translateLoadingStart", { language: n }), S = !0; var t = H; "string" == typeof t && (t = o.get(t)); var e = angular.extend({}, K, { key: n, $http: angular.extend({}, { cache: t }, K.$http) }), r = function (t) { var e = {}; s.$emit("$translateLoadingSuccess", { language: n }), angular.isArray(t) ? angular.forEach(t, function (t) { angular.extend(e, ct(t)) }) : angular.extend(e, ct(t)), S = !1, a.resolve({ key: n, table: e }), s.$emit("$translateLoadingEnd", { language: n }) }; r.displayName = "onLoaderSuccess"; var i = function (t) { s.$emit("$translateLoadingError", { language: t }), a.reject(t), s.$emit("$translateLoadingEnd", { language: t }) }; return i.displayName = "onLoaderError", o.get(D)(e).then(r, i), a.promise }; if (_ && (!(i = o.get(_)).get || !i.put)) throw new Error("Couldn't use storage '" + _ + "', missing get() or put() method!"); if (J.length) { var e = function (t) { var e = o.get(t); e.setLocale(T || F), L[e.getInterpolationIdentifier()] = e }; e.displayName = "interpolationFactoryAdder", angular.forEach(J, e) } var c = function (a, r, i, s, o) { var l = m.defer(), t = function (t) { if (Object.prototype.hasOwnProperty.call(t, r) && null !== t[r]) { s.setLocale(a); var e = t[r]; if ("@:" === e.substr(0, 2)) c(a, e.substr(2), i, s, o).then(l.resolve, l.reject); else { var n = s.interpolate(t[r], i, "service", o, r); n = O(r, t[r], n, i, a), l.resolve(n) } s.setLocale(F) } else l.reject() }; return t.displayName = "fallbackTranslationResolver", function (t) { var e = m.defer(); if (Object.prototype.hasOwnProperty.call(q, t)) e.resolve(q[t]); else if (f[t]) { var n = function (t) { ut(t.key, t.table), e.resolve(t.table) }; n.displayName = "translationTableResolver", f[t].then(n, e.reject) } else e.reject(); return e.promise }(a).then(t, l.reject), l.promise }, g = function (t, e, n, a, r) { var i, s = q[t]; if (s && Object.prototype.hasOwnProperty.call(s, e) && null !== s[e]) { if (a.setLocale(t), i = a.interpolate(s[e], n, "filter", r, e), i = O(e, s[e], i, n, t, r), !angular.isString(i) && angular.isFunction(i.$$unwrapTrustedValue)) { var o = i.$$unwrapTrustedValue(); if ("@:" === o.substr(0, 2)) return g(t, o.substr(2), n, a, r) } else if ("@:" === i.substr(0, 2)) return g(t, i.substr(2), n, a, r); a.setLocale(F) } return i }, C = function (t, e, n, a) { return V ? o.get(V)(t, F, e, n, a) : t }, N = function (t, e, n, a, r, i) { var s = m.defer(); if (t < z.length) { var o = z[t]; c(o, e, n, a, i).then(function (t) { s.resolve(t) }, function () { return N(t + 1, e, n, a, r, i).then(s.resolve, s.reject) }) } else if (r) s.resolve(r); else { var l = C(e, n, r); V && l ? s.resolve(l) : s.reject(j(e)) } return s.promise }, p = function (t, e, n, a, r) { var i; if (t < z.length) { var s = z[t]; (i = g(s, e, n, a, r)) || "" === i || (i = p(t + 1, e, n, a)) } return i }, h = function (t, e, n, a, r, i) { var s, o, l, u, c, f = m.defer(), g = r ? q[r] : q, p = n ? L[n] : b; if (g && Object.prototype.hasOwnProperty.call(g, t) && null !== g[t]) { var h = g[t]; if ("@:" === h.substr(0, 2)) w(h.substr(2), e, n, a, r, i).then(f.resolve, f.reject); else { var d = p.interpolate(h, e, "service", i, t); d = O(t, h, d, e, r), f.resolve(d) } } else { var v; V && !S && (v = C(t, e, a)), r && z && z.length ? (s = t, o = e, l = p, u = a, c = i, N(0 < y ? y : $, s, o, l, u, c)).then(function (t) { f.resolve(t) }, function (t) { f.reject(j(t)) }) : V && !S && v ? a ? f.resolve(a) : f.resolve(v) : a ? f.resolve(a) : f.reject(j(t)) } return f.promise }, d = function (t, e, n, a, r) { var i, s = a ? q[a] : q, o = b; if (L && Object.prototype.hasOwnProperty.call(L, n) && (o = L[n]), s && Object.prototype.hasOwnProperty.call(s, t) && null !== s[t]) { var l = s[t]; i = "@:" === l.substr(0, 2) ? d(l.substr(2), e, n, a, r) : (i = o.interpolate(l, e, "filter", r, t), O(t, l, i, e, a, r)) } else { var u; V && !S && (u = C(t, e, r)), i = a && z && z.length ? p(($ = 0) < y ? y : $, t, e, o, r) : V && !S && u ? u : j(t) } return i }, O = function (t, e, n, a, r, i) { var s = G; return s && ("string" == typeof s && (s = o.get(s)), s) ? s(t, e, n, a, r, i) : n }, v = function (t) { q[t] || !D || f[t] || (f[t] = u(t).then(function (t) { return ut(t.key, t.table), t })) }; w.preferredLanguage = function (t) { return t && ft(t), T }, w.cloakClassName = function () { return Q }, w.allowNamespaces = function () { return Z }, w.nestedObjectDelimeter = function () { return tt }, w.fallbackLanguage = function (t) { if (null != t) { if (gt(t), D && z && z.length) for (var e = 0, n = z.length; e < n; e++)f[z[e]] || (f[z[e]] = u(z[e])); w.use(w.use()) } return x ? z[0] : z }, w.useFallbackLanguage = function (t) { if (null != t) if (t) { var e = st(z, t); -1 < e && (y = e) } else y = 0 }, w.proposedLanguage = function () { return I }, w.storage = function () { return i }, w.negotiateLocale = lt, w.use = function (e) { if (!e) return F; var n = m.defer(); n.promise.then(null, angular.noop), s.$emit("$translateChangeStart", { language: e }); var t = lt(e); return 0 < Y.length && !t ? m.reject(e) : (t && (e = t), I = e, !X && q[e] || !D || f[e] ? f[e] ? f[e].then(function (t) { return I === t.key && l(t.key), n.resolve(t.key), t }, function (t) { return !F && z && 0 < z.length && z[0] !== t ? w.use(z[0]).then(n.resolve, n.reject) : n.reject(t) }) : (n.resolve(e), l(e)) : (f[e] = u(e).then(function (t) { return ut(t.key, t.table), n.resolve(t.key), I === e && l(t.key), t }, function (t) { return s.$emit("$translateChangeError", { language: t }), n.reject(t), s.$emit("$translateChangeEnd", { language: t }), m.reject(t) }), f[e].finally(function () { var t; I === (t = e) && (I = void 0), f[t] = void 0 }).catch(angular.noop)), n.promise) }, w.resolveClientLocale = function () { return it() }, w.storageKey = function () { return pt() }, w.isPostCompilingEnabled = function () { return W }, w.isForceAsyncReloadEnabled = function () { return X }, w.isKeepContent = function () { return nt }, w.refresh = function (t) { if (!D) throw new Error("Couldn't refresh translation table, no loader registered!"); s.$emit("$translateRefreshStart", { language: t }); var e = m.defer(), n = {}; function a(e) { var t = u(e); return (f[e] = t).then(function (t) { q[e] = {}, ut(e, t.table), n[e] = !0 }, angular.noop), t } if (e.promise.then(function () { for (var t in q) q.hasOwnProperty(t) && (t in n || delete q[t]); F && l(F) }, angular.noop).finally(function () { s.$emit("$translateRefreshEnd", { language: t }) }), t) q[t] ? a(t).then(e.resolve, e.reject) : e.reject(); else { var r = z && z.slice() || []; F && -1 === r.indexOf(F) && r.push(F), m.all(r.map(a)).then(e.resolve, e.reject) } return e.promise }, w.instant = function (t, e, n, a, r) { var i = a && a !== F ? lt(a) || a : F; if (null === t || angular.isUndefined(t)) return t; if (a && v(a), angular.isArray(t)) { for (var s = {}, o = 0, l = t.length; o < l; o++)s[t[o]] = w.instant(t[o], e, n, a, r); return s } if (angular.isString(t) && t.length < 1) return t; t && (t = ot.apply(t)); var u, c, f = []; T && f.push(T), i && f.push(i), z && z.length && (f = f.concat(z)); for (var g = 0, p = f.length; g < p; g++) { var h = f[g]; if (q[h] && void 0 !== q[h][t] && (u = d(t, e, n, i, r)), void 0 !== u) break } u || "" === u || (U || M ? u = j(t) : (u = b.interpolate(t, e, "filter", r), V && !S && (c = C(t, e, r)), V && !S && c && (u = c))); return u }, w.versionInfo = function () { return "2.19.0" }, w.loaderCache = function () { return H }, w.directivePriority = function () { return at }, w.statefulFilter = function () { return rt }, w.isReady = function () { return et }; var n = m.defer(); n.promise.then(function () { et = !0 }), w.onReady = function (t) { var e = m.defer(); return angular.isFunction(t) && e.promise.then(t), et ? e.resolve() : n.promise.then(e.resolve), e.promise }, w.getAvailableLanguageKeys = function () { return 0 < Y.length ? Y : null }, w.getTranslationTable = function (t) { return (t = t || w.use()) && q[t] ? angular.copy(q[t]) : null }; var a = s.$on("$translateReady", function () { n.resolve(), a(), a = null }), r = s.$on("$translateChangeEnd", function () { n.resolve(), r(), r = null }); if (D) { if (angular.equals(q, {}) && w.use() && w.use(w.use()), z && z.length) for (var E = function (t) { return ut(t.key, t.table), s.$emit("$translateChangeEnd", { language: t.key }), t }, k = 0, P = z.length; k < P; k++) { var A = z[k]; !X && q[A] || (f[A] = u(A).then(E)) } } else s.$emit("$translateReady", { language: w.use() }); return w }] } function n(s, o) { "use strict"; var t = {}; return t.setLocale = function (t) { t }, t.getInterpolationIdentifier = function () { return "default" }, t.useSanitizeValueStrategy = function (t) { return o.useStrategy(t), this }, t.interpolate = function (t, e, n, a, r) { var i; return e = e || {}, e = o.sanitize(e, "params", a, n), i = angular.isNumber(t) ? "" + t : angular.isString(t) ? (i = s(t)(e), o.sanitize(i, "text", a, n)) : "" }, t } function a(S, L, w, j, C) { "use strict"; var N = function (t) { return angular.isString(t) ? t.toLowerCase() : t }; return { restrict: "AE", scope: !0, priority: S.directivePriority(), compile: function (t, h) { var d = h.translateValues ? h.translateValues : void 0, v = h.translateInterpolation ? h.translateInterpolation : void 0, m = h.translateSanitizeStrategy ? h.translateSanitizeStrategy : void 0, $ = t[0].outerHTML.match(/translate-value-+/i), y = "^(.*)(" + L.startSymbol() + ".*" + L.endSymbol() + ")(.*)", b = "^(.*)" + L.startSymbol() + "(.*)" + L.endSymbol() + "(.*)"; return function (r, l, u) { r.interpolateParams = {}, r.preText = "", r.postText = "", r.translateNamespace = function t(e) { if (e.translateNamespace) return e.translateNamespace; if (e.$parent) return t(e.$parent) }(r); var i = {}, s = function (t) { if (angular.isFunction(s._unwatchOld) && (s._unwatchOld(), s._unwatchOld = void 0), angular.equals(t, "") || !angular.isDefined(t)) { var e = function () { return this.toString().replace(/^\s+|\s+$/g, "") }.apply(l.text()).replace(/\n/g, " "), n = e.match(y); if (angular.isArray(n)) { r.preText = n[1], r.postText = n[3], i.translate = L(n[2])(r.$parent); var a = e.match(b); angular.isArray(a) && a[2] && a[2].length && (s._unwatchOld = r.$watch(a[2], function (t) { i.translate = t, c() })) } else i.translate = e || void 0 } else i.translate = t; c() }, t = function (e) { u.$observe(e, function (t) { i[e] = t, c() }) }; !function (t, e, n) { if (e.translateValues && angular.extend(t, j(e.translateValues)(r.$parent)), $) for (var a in n) Object.prototype.hasOwnProperty.call(e, a) && "translateValue" === a.substr(0, 14) && "translateValues" !== a && (t[N(a.substr(14, 1)) + a.substr(15)] = n[a]) }(r.interpolateParams, u, h); var e = !0; for (var n in u.$observe("translate", function (t) { void 0 === t ? s("") : "" === t && e || (i.translate = t, c()), e = !1 }), u) u.hasOwnProperty(n) && "translateAttr" === n.substr(0, 13) && 13 < n.length && t(n); if (u.$observe("translateDefault", function (t) { r.defaultText = t, c() }), m && u.$observe("translateSanitizeStrategy", function (t) { r.sanitizeStrategy = j(t)(r.$parent), c() }), d && u.$observe("translateValues", function (t) { t && r.$parent.$watch(function () { angular.extend(r.interpolateParams, j(t)(r.$parent)) }) }), $) { var a = function (n) { u.$observe(n, function (t) { var e = N(n.substr(14, 1)) + n.substr(15); r.interpolateParams[e] = t }) }; for (var o in u) Object.prototype.hasOwnProperty.call(u, o) && "translateValue" === o.substr(0, 14) && "translateValues" !== o && a(o) } var c = function () { for (var t in i) i.hasOwnProperty(t) && void 0 !== i[t] && f(t, i[t], r, r.interpolateParams, r.defaultText, r.translateNamespace) }, f = function (e, t, n, a, r, i) { t ? (i && "." === t.charAt(0) && (t = i + t), S(t, a, v, r, n.translateLanguage, n.sanitizeStrategy).then(function (t) { g(t, n, !0, e) }, function (t) { g(t, n, !1, e) })) : g(t, n, !1, e) }, g = function (t, e, n, a) { if (n || void 0 !== e.defaultText && (t = e.defaultText), "translate" === a) { (n || !n && !S.isKeepContent() && void 0 === u.translateKeepContent) && l.empty().append(e.preText + t + e.postText); var r = S.isPostCompilingEnabled(), i = void 0 !== h.translateCompile, s = i && "false" !== h.translateCompile; (r && !i || s) && w(l.contents())(e) } else { var o = u.$attr[a]; "data-" === o.substr(0, 5) && (o = o.substr(5)), o = o.substr(15), l.attr(o, t) } }; (d || $ || u.translateDefault) && r.$watch("interpolateParams", c, !0), r.$on("translateLanguageChanged", c); var p = C.$on("$translateChangeSuccess", c); l.text().length ? u.translate ? s(u.translate) : s("") : u.translate && s(u.translate), c(), r.$on("$destroy", p) } } } } function r(u, c) { "use strict"; return { restrict: "A", priority: u.directivePriority(), link: function (n, a, r) { var i, s, o, l = {}, t = function () { angular.forEach(i, function (t, e) { t && (l[e] = !0, n.translateNamespace && "." === t.charAt(0) && (t = n.translateNamespace + t), u(t, s, r.translateInterpolation, void 0, n.translateLanguage, o).then(function (t) { a.attr(e, t) }, function (t) { a.attr(e, t) })) }), angular.forEach(l, function (t, e) { i[e] || (a.removeAttr(e), delete l[e]) }) }; f(n, r.translateAttr, function (t) { i = t }, t), f(n, r.translateValues, function (t) { s = t }, t), f(n, r.translateSanitizeStrategy, function (t) { o = t }, t), r.translateValues && n.$watch(r.translateValues, t, !0), n.$on("translateLanguageChanged", t); var e = c.$on("$translateChangeSuccess", t); t(), n.$on("$destroy", e) } } } function f(t, e, n, a) { "use strict"; e && ("::" === e.substr(0, 2) ? e = e.substr(2) : t.$watch(e, function (t) { n(t), a() }, !0), n(t.$eval(e))) } function i(s, o) { "use strict"; return { compile: function (t) { var i = function (t) { t.addClass(s.cloakClassName()) }; return i(t), function (t, e, n) { var a = function (t) { t.removeClass(s.cloakClassName()) }.bind(this, e), r = i.bind(this, e); n.translateCloak && n.translateCloak.length ? (n.$observe("translateCloak", function (t) { s(t).then(a, r) }), o.$on("$translateChangeSuccess", function () { s(n.translateCloak).then(a, r) })) : s.onReady(a) } } } } function s() { "use strict"; return { restrict: "A", scope: !0, compile: function () { return { pre: function (t, e, n) { t.translateNamespace = function t(e) { if (e.translateNamespace) return e.translateNamespace; if (e.$parent) return t(e.$parent) }(t), t.translateNamespace && "." === n.translateNamespace.charAt(0) ? t.translateNamespace += n.translateNamespace : t.translateNamespace = n.translateNamespace } } } } } function o() { "use strict"; return { restrict: "A", scope: !0, compile: function () { return function (e, t, n) { n.$observe("translateLanguage", function (t) { e.translateLanguage = t }), e.$watch("translateLanguage", function () { e.$broadcast("translateLanguageChanged") }) } } } } function l(i, s) { "use strict"; var t = function (t, e, n, a) { if (!angular.isObject(e)) { var r = this || { __SCOPE_IS_NOT_AVAILABLE: "More info at https://github.com/angular/angular.js/commit/8863b9d04c722b278fa93c5d66ad1e578ad6eb1f" }; e = i(e)(r) } return s.instant(t, e, n, a) }; return s.statefulFilter() && (t.$stateful = !0), t } function u(t) { "use strict"; return t("translations") } return t.$inject = ["$translate"], e.$inject = ["$STORAGE_KEY", "$windowProvider", "$translateSanitizationProvider", "pascalprechtTranslateOverrider"], n.$inject = ["$interpolate", "$translateSanitization"], a.$inject = ["$translate", "$interpolate", "$compile", "$parse", "$rootScope"], r.$inject = ["$translate", "$rootScope"], i.$inject = ["$translate", "$rootScope"], l.$inject = ["$parse", "$translate"], u.$inject = ["$cacheFactory"], angular.module("pascalprecht.translate", ["ng"]).run(t), t.displayName = "runTranslate", angular.module("pascalprecht.translate").provider("$translateSanitization", function () { "use strict"; var n, a, g, p = null, h = !1, d = !1; (g = { sanitize: function (t, e) { return "text" === e && (t = i(t)), t }, escape: function (t, e) { return "text" === e && (t = r(t)), t }, sanitizeParameters: function (t, e) { return "params" === e && (t = o(t, i)), t }, escapeParameters: function (t, e) { return "params" === e && (t = o(t, r)), t }, sce: function (t, e, n) { return "text" === e ? t = s(t) : "params" === e && "filter" !== n && (t = o(t, r)), t }, sceParameters: function (t, e) { return "params" === e && (t = o(t, s)), t } }).escaped = g.escapeParameters, this.addStrategy = function (t, e) { return g[t] = e, this }, this.removeStrategy = function (t) { return delete g[t], this }, this.useStrategy = function (t) { return h = !0, p = t, this }, this.$get = ["$injector", "$log", function (u, c) { var e, f = {}; return u.has("$sanitize") && (n = u.get("$sanitize")), u.has("$sce") && (a = u.get("$sce")), { useStrategy: (e = this, function (t) { e.useStrategy(t) }), sanitize: function (t, e, n, a) { if (p || h || d || (c.warn("pascalprecht.translate.$translateSanitization: No sanitization strategy has been configured. This can have serious security implications. See http://angular-translate.github.io/docs/#/guide/19_security for details."), d = !0), n || null === n || (n = p), !n) return t; a || (a = "service"); var r, i, s, o, l = angular.isArray(n) ? n : [n]; return r = t, i = e, s = a, o = l, angular.forEach(o, function (e) { if (angular.isFunction(e)) r = e(r, i, s); else if (angular.isFunction(g[e])) r = g[e](r, i, s); else { if (!angular.isString(g[e])) throw new Error("pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: '" + e + "'"); if (!f[g[e]]) try { f[g[e]] = u.get(g[e]) } catch (t) { throw f[g[e]] = function () { }, new Error("pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: '" + e + "'") } r = f[g[e]](r, i, s) } }), r } } }]; var r = function (t) { var e = angular.element("<div></div>"); return e.text(t), e.html() }, i = function (t) { if (!n) throw new Error("pascalprecht.translate.$translateSanitization: Error cannot find $sanitize service. Either include the ngSanitize module (https://docs.angularjs.org/api/ngSanitize) or use a sanitization strategy which does not depend on $sanitize, such as 'escape'."); return n(t) }, s = function (t) { if (!a) throw new Error("pascalprecht.translate.$translateSanitization: Error cannot find $sce service."); return a.trustAsHtml(t) }, o = function (t, n, a) { if (angular.isDate(t)) return t; if (angular.isObject(t)) { var r = angular.isArray(t) ? [] : {}; if (a) { if (-1 < a.indexOf(t)) throw new Error("pascalprecht.translate.$translateSanitization: Error cannot interpolate parameter due recursive object") } else a = []; return a.push(t), angular.forEach(t, function (t, e) { angular.isFunction(t) || (r[e] = o(t, n, a)) }), a.splice(-1, 1), r } return angular.isNumber(t) ? t : !0 === t || !1 === t ? t : angular.isUndefined(t) || null === t ? t : n(t) } }), angular.module("pascalprecht.translate").constant("pascalprechtTranslateOverrider", {}).provider("$translate", e), e.displayName = "displayName", angular.module("pascalprecht.translate").factory("$translateDefaultInterpolation", n), n.displayName = "$translateDefaultInterpolation", angular.module("pascalprecht.translate").constant("$STORAGE_KEY", "NG_TRANSLATE_LANG_KEY"), angular.module("pascalprecht.translate").directive("translate", a), a.displayName = "translateDirective", angular.module("pascalprecht.translate").directive("translateAttr", r), r.displayName = "translateAttrDirective", angular.module("pascalprecht.translate").directive("translateCloak", i), i.displayName = "translateCloakDirective", angular.module("pascalprecht.translate").directive("translateNamespace", s), s.displayName = "translateNamespaceDirective", angular.module("pascalprecht.translate").directive("translateLanguage", o), o.displayName = "translateLanguageDirective", angular.module("pascalprecht.translate").filter("translate", l), l.displayName = "translateFilterFactory", angular.module("pascalprecht.translate").factory("$translationCache", u), u.displayName = "$translationCache", "pascalprecht.translate" });
!function (a) { a.fn.datepicker.dates["en-GB"] = { days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], daysMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"], months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"], today: "Today", monthsTitle: "Months", clear: "Clear", weekStart: 1, format: "dd/mm/yyyy" } }(jQuery);
!function (a) { a.fn.datepicker.dates.fr = { days: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"], daysShort: ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."], daysMin: ["d", "l", "ma", "me", "j", "v", "s"], months: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"], monthsShort: ["Janv.", "Févr.", "Mars", "Avril", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."], today: "Aujourd'hui", monthsTitle: "Mois", clear: "Effacer", weekStart: 1, format: "dd/mm/yyyy" } }(jQuery);
!function (a) { a.fn.datepicker.dates.it = { days: ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"], daysShort: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"], daysMin: ["Do", "Lu", "Ma", "Me", "Gi", "Ve", "Sa"], months: ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"], monthsShort: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"], today: "Oggi", monthsTitle: "Mesi", clear: "Cancella", weekStart: 1, format: "dd/mm/yyyy" } }(jQuery);
/*!
 * Chart.js
 * http://chartjs.org/
 * Version: 2.7.0
 *
 * Copyright 2017 Nick Downie
 * Released under the MIT license
 * https://github.com/chartjs/Chart.js/blob/master/LICENSE.md
 */
!function (t) { if ("object" == typeof exports && "undefined" != typeof module) module.exports = t(); else if ("function" == typeof define && define.amd) define([], t); else { ("undefined" != typeof window ? window : "undefined" != typeof global ? global : "undefined" != typeof self ? self : this).Chart = t() } }(function () { return function t(e, n, i) { function a(r, l) { if (!n[r]) { if (!e[r]) { var s = "function" == typeof require && require; if (!l && s) return s(r, !0); if (o) return o(r, !0); var u = new Error("Cannot find module '" + r + "'"); throw u.code = "MODULE_NOT_FOUND", u } var d = n[r] = { exports: {} }; e[r][0].call(d.exports, function (t) { var n = e[r][1][t]; return a(n || t) }, d, d.exports, t, e, n, i) } return n[r].exports } for (var o = "function" == typeof require && require, r = 0; r < i.length; r++)a(i[r]); return a }({ 1: [function (t, e, n) { }, {}], 2: [function (t, e, n) { function i(t) { if (t) { var e = /^#([a-fA-F0-9]{3})$/i, n = /^#([a-fA-F0-9]{6})$/i, i = /^rgba?\(\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*,\s*([+-]?\d+)\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/i, a = /^rgba?\(\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*,\s*([+-]?[\d\.]+)\%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)$/i, o = /(\w+)/, r = [0, 0, 0], l = 1, s = t.match(e); if (s) { s = s[1]; for (d = 0; d < r.length; d++)r[d] = parseInt(s[d] + s[d], 16) } else if (s = t.match(n)) { s = s[1]; for (d = 0; d < r.length; d++)r[d] = parseInt(s.slice(2 * d, 2 * d + 2), 16) } else if (s = t.match(i)) { for (d = 0; d < r.length; d++)r[d] = parseInt(s[d + 1]); l = parseFloat(s[4]) } else if (s = t.match(a)) { for (d = 0; d < r.length; d++)r[d] = Math.round(2.55 * parseFloat(s[d + 1])); l = parseFloat(s[4]) } else if (s = t.match(o)) { if ("transparent" == s[1]) return [0, 0, 0, 0]; if (!(r = c[s[1]])) return } for (var d = 0; d < r.length; d++)r[d] = u(r[d], 0, 255); return l = l || 0 == l ? u(l, 0, 1) : 1, r[3] = l, r } } function a(t) { if (t) { var e = /^hsla?\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/, n = t.match(e); if (n) { var i = parseFloat(n[4]); return [u(parseInt(n[1]), 0, 360), u(parseFloat(n[2]), 0, 100), u(parseFloat(n[3]), 0, 100), u(isNaN(i) ? 1 : i, 0, 1)] } } } function o(t) { if (t) { var e = /^hwb\(\s*([+-]?\d+)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?[\d\.]+)\s*)?\)/, n = t.match(e); if (n) { var i = parseFloat(n[4]); return [u(parseInt(n[1]), 0, 360), u(parseFloat(n[2]), 0, 100), u(parseFloat(n[3]), 0, 100), u(isNaN(i) ? 1 : i, 0, 1)] } } } function r(t, e) { return void 0 === e && (e = void 0 !== t[3] ? t[3] : 1), "rgba(" + t[0] + ", " + t[1] + ", " + t[2] + ", " + e + ")" } function l(t, e) { return "rgba(" + Math.round(t[0] / 255 * 100) + "%, " + Math.round(t[1] / 255 * 100) + "%, " + Math.round(t[2] / 255 * 100) + "%, " + (e || t[3] || 1) + ")" } function s(t, e) { return void 0 === e && (e = void 0 !== t[3] ? t[3] : 1), "hsla(" + t[0] + ", " + t[1] + "%, " + t[2] + "%, " + e + ")" } function u(t, e, n) { return Math.min(Math.max(e, t), n) } function d(t) { var e = t.toString(16).toUpperCase(); return e.length < 2 ? "0" + e : e } var c = t(6); e.exports = { getRgba: i, getHsla: a, getRgb: function (t) { var e = i(t); return e && e.slice(0, 3) }, getHsl: function (t) { var e = a(t); return e && e.slice(0, 3) }, getHwb: o, getAlpha: function (t) { var e = i(t); return e ? e[3] : (e = a(t)) ? e[3] : (e = o(t)) ? e[3] : void 0 }, hexString: function (t) { return "#" + d(t[0]) + d(t[1]) + d(t[2]) }, rgbString: function (t, e) { return e < 1 || t[3] && t[3] < 1 ? r(t, e) : "rgb(" + t[0] + ", " + t[1] + ", " + t[2] + ")" }, rgbaString: r, percentString: function (t, e) { return e < 1 || t[3] && t[3] < 1 ? l(t, e) : "rgb(" + Math.round(t[0] / 255 * 100) + "%, " + Math.round(t[1] / 255 * 100) + "%, " + Math.round(t[2] / 255 * 100) + "%)" }, percentaString: l, hslString: function (t, e) { return e < 1 || t[3] && t[3] < 1 ? s(t, e) : "hsl(" + t[0] + ", " + t[1] + "%, " + t[2] + "%)" }, hslaString: s, hwbString: function (t, e) { return void 0 === e && (e = void 0 !== t[3] ? t[3] : 1), "hwb(" + t[0] + ", " + t[1] + "%, " + t[2] + "%" + (void 0 !== e && 1 !== e ? ", " + e : "") + ")" }, keyword: function (t) { return h[t.slice(0, 3)] } }; var h = {}; for (var f in c) h[c[f]] = f }, { 6: 6 }], 3: [function (t, e, n) { var i = t(5), a = t(2), o = function (t) { if (t instanceof o) return t; if (!(this instanceof o)) return new o(t); this.valid = !1, this.values = { rgb: [0, 0, 0], hsl: [0, 0, 0], hsv: [0, 0, 0], hwb: [0, 0, 0], cmyk: [0, 0, 0, 0], alpha: 1 }; var e; "string" == typeof t ? (e = a.getRgba(t)) ? this.setValues("rgb", e) : (e = a.getHsla(t)) ? this.setValues("hsl", e) : (e = a.getHwb(t)) && this.setValues("hwb", e) : "object" == typeof t && (void 0 !== (e = t).r || void 0 !== e.red ? this.setValues("rgb", e) : void 0 !== e.l || void 0 !== e.lightness ? this.setValues("hsl", e) : void 0 !== e.v || void 0 !== e.value ? this.setValues("hsv", e) : void 0 !== e.w || void 0 !== e.whiteness ? this.setValues("hwb", e) : void 0 === e.c && void 0 === e.cyan || this.setValues("cmyk", e)) }; o.prototype = { isValid: function () { return this.valid }, rgb: function () { return this.setSpace("rgb", arguments) }, hsl: function () { return this.setSpace("hsl", arguments) }, hsv: function () { return this.setSpace("hsv", arguments) }, hwb: function () { return this.setSpace("hwb", arguments) }, cmyk: function () { return this.setSpace("cmyk", arguments) }, rgbArray: function () { return this.values.rgb }, hslArray: function () { return this.values.hsl }, hsvArray: function () { return this.values.hsv }, hwbArray: function () { var t = this.values; return 1 !== t.alpha ? t.hwb.concat([t.alpha]) : t.hwb }, cmykArray: function () { return this.values.cmyk }, rgbaArray: function () { var t = this.values; return t.rgb.concat([t.alpha]) }, hslaArray: function () { var t = this.values; return t.hsl.concat([t.alpha]) }, alpha: function (t) { return void 0 === t ? this.values.alpha : (this.setValues("alpha", t), this) }, red: function (t) { return this.setChannel("rgb", 0, t) }, green: function (t) { return this.setChannel("rgb", 1, t) }, blue: function (t) { return this.setChannel("rgb", 2, t) }, hue: function (t) { return t && (t = (t %= 360) < 0 ? 360 + t : t), this.setChannel("hsl", 0, t) }, saturation: function (t) { return this.setChannel("hsl", 1, t) }, lightness: function (t) { return this.setChannel("hsl", 2, t) }, saturationv: function (t) { return this.setChannel("hsv", 1, t) }, whiteness: function (t) { return this.setChannel("hwb", 1, t) }, blackness: function (t) { return this.setChannel("hwb", 2, t) }, value: function (t) { return this.setChannel("hsv", 2, t) }, cyan: function (t) { return this.setChannel("cmyk", 0, t) }, magenta: function (t) { return this.setChannel("cmyk", 1, t) }, yellow: function (t) { return this.setChannel("cmyk", 2, t) }, black: function (t) { return this.setChannel("cmyk", 3, t) }, hexString: function () { return a.hexString(this.values.rgb) }, rgbString: function () { return a.rgbString(this.values.rgb, this.values.alpha) }, rgbaString: function () { return a.rgbaString(this.values.rgb, this.values.alpha) }, percentString: function () { return a.percentString(this.values.rgb, this.values.alpha) }, hslString: function () { return a.hslString(this.values.hsl, this.values.alpha) }, hslaString: function () { return a.hslaString(this.values.hsl, this.values.alpha) }, hwbString: function () { return a.hwbString(this.values.hwb, this.values.alpha) }, keyword: function () { return a.keyword(this.values.rgb, this.values.alpha) }, rgbNumber: function () { var t = this.values.rgb; return t[0] << 16 | t[1] << 8 | t[2] }, luminosity: function () { for (var t = this.values.rgb, e = [], n = 0; n < t.length; n++) { var i = t[n] / 255; e[n] = i <= .03928 ? i / 12.92 : Math.pow((i + .055) / 1.055, 2.4) } return .2126 * e[0] + .7152 * e[1] + .0722 * e[2] }, contrast: function (t) { var e = this.luminosity(), n = t.luminosity(); return e > n ? (e + .05) / (n + .05) : (n + .05) / (e + .05) }, level: function (t) { var e = this.contrast(t); return e >= 7.1 ? "AAA" : e >= 4.5 ? "AA" : "" }, dark: function () { var t = this.values.rgb; return (299 * t[0] + 587 * t[1] + 114 * t[2]) / 1e3 < 128 }, light: function () { return !this.dark() }, negate: function () { for (var t = [], e = 0; e < 3; e++)t[e] = 255 - this.values.rgb[e]; return this.setValues("rgb", t), this }, lighten: function (t) { var e = this.values.hsl; return e[2] += e[2] * t, this.setValues("hsl", e), this }, darken: function (t) { var e = this.values.hsl; return e[2] -= e[2] * t, this.setValues("hsl", e), this }, saturate: function (t) { var e = this.values.hsl; return e[1] += e[1] * t, this.setValues("hsl", e), this }, desaturate: function (t) { var e = this.values.hsl; return e[1] -= e[1] * t, this.setValues("hsl", e), this }, whiten: function (t) { var e = this.values.hwb; return e[1] += e[1] * t, this.setValues("hwb", e), this }, blacken: function (t) { var e = this.values.hwb; return e[2] += e[2] * t, this.setValues("hwb", e), this }, greyscale: function () { var t = this.values.rgb, e = .3 * t[0] + .59 * t[1] + .11 * t[2]; return this.setValues("rgb", [e, e, e]), this }, clearer: function (t) { var e = this.values.alpha; return this.setValues("alpha", e - e * t), this }, opaquer: function (t) { var e = this.values.alpha; return this.setValues("alpha", e + e * t), this }, rotate: function (t) { var e = this.values.hsl, n = (e[0] + t) % 360; return e[0] = n < 0 ? 360 + n : n, this.setValues("hsl", e), this }, mix: function (t, e) { var n = this, i = t, a = void 0 === e ? .5 : e, o = 2 * a - 1, r = n.alpha() - i.alpha(), l = ((o * r == -1 ? o : (o + r) / (1 + o * r)) + 1) / 2, s = 1 - l; return this.rgb(l * n.red() + s * i.red(), l * n.green() + s * i.green(), l * n.blue() + s * i.blue()).alpha(n.alpha() * a + i.alpha() * (1 - a)) }, toJSON: function () { return this.rgb() }, clone: function () { var t, e, n = new o, i = this.values, a = n.values; for (var r in i) i.hasOwnProperty(r) && (t = i[r], "[object Array]" === (e = {}.toString.call(t)) ? a[r] = t.slice(0) : "[object Number]" === e ? a[r] = t : console.error("unexpected color value:", t)); return n } }, o.prototype.spaces = { rgb: ["red", "green", "blue"], hsl: ["hue", "saturation", "lightness"], hsv: ["hue", "saturation", "value"], hwb: ["hue", "whiteness", "blackness"], cmyk: ["cyan", "magenta", "yellow", "black"] }, o.prototype.maxes = { rgb: [255, 255, 255], hsl: [360, 100, 100], hsv: [360, 100, 100], hwb: [360, 100, 100], cmyk: [100, 100, 100, 100] }, o.prototype.getValues = function (t) { for (var e = this.values, n = {}, i = 0; i < t.length; i++)n[t.charAt(i)] = e[t][i]; return 1 !== e.alpha && (n.a = e.alpha), n }, o.prototype.setValues = function (t, e) { var n, a = this.values, o = this.spaces, r = this.maxes, l = 1; if (this.valid = !0, "alpha" === t) l = e; else if (e.length) a[t] = e.slice(0, t.length), l = e[t.length]; else if (void 0 !== e[t.charAt(0)]) { for (n = 0; n < t.length; n++)a[t][n] = e[t.charAt(n)]; l = e.a } else if (void 0 !== e[o[t][0]]) { var s = o[t]; for (n = 0; n < t.length; n++)a[t][n] = e[s[n]]; l = e.alpha } if (a.alpha = Math.max(0, Math.min(1, void 0 === l ? a.alpha : l)), "alpha" === t) return !1; var u; for (n = 0; n < t.length; n++)u = Math.max(0, Math.min(r[t][n], a[t][n])), a[t][n] = Math.round(u); for (var d in o) d !== t && (a[d] = i[t][d](a[t])); return !0 }, o.prototype.setSpace = function (t, e) { var n = e[0]; return void 0 === n ? this.getValues(t) : ("number" == typeof n && (n = Array.prototype.slice.call(e)), this.setValues(t, n), this) }, o.prototype.setChannel = function (t, e, n) { var i = this.values[t]; return void 0 === n ? i[e] : n === i[e] ? this : (i[e] = n, this.setValues(t, i), this) }, "undefined" != typeof window && (window.Color = o), e.exports = o }, { 2: 2, 5: 5 }], 4: [function (t, e, n) { function i(t) { var e, n, i, a = t[0] / 255, o = t[1] / 255, r = t[2] / 255, l = Math.min(a, o, r), s = Math.max(a, o, r), u = s - l; return s == l ? e = 0 : a == s ? e = (o - r) / u : o == s ? e = 2 + (r - a) / u : r == s && (e = 4 + (a - o) / u), (e = Math.min(60 * e, 360)) < 0 && (e += 360), i = (l + s) / 2, n = s == l ? 0 : i <= .5 ? u / (s + l) : u / (2 - s - l), [e, 100 * n, 100 * i] } function a(t) { var e, n, i, a = t[0], o = t[1], r = t[2], l = Math.min(a, o, r), s = Math.max(a, o, r), u = s - l; return n = 0 == s ? 0 : u / s * 1e3 / 10, s == l ? e = 0 : a == s ? e = (o - r) / u : o == s ? e = 2 + (r - a) / u : r == s && (e = 4 + (a - o) / u), (e = Math.min(60 * e, 360)) < 0 && (e += 360), i = s / 255 * 1e3 / 10, [e, n, i] } function o(t) { var e = t[0], n = t[1], a = t[2]; return [i(t)[0], 100 * (1 / 255 * Math.min(e, Math.min(n, a))), 100 * (a = 1 - 1 / 255 * Math.max(e, Math.max(n, a)))] } function l(t) { var e, n, i, a, o = t[0] / 255, r = t[1] / 255, l = t[2] / 255; return a = Math.min(1 - o, 1 - r, 1 - l), e = (1 - o - a) / (1 - a) || 0, n = (1 - r - a) / (1 - a) || 0, i = (1 - l - a) / (1 - a) || 0, [100 * e, 100 * n, 100 * i, 100 * a] } function s(t) { return C[JSON.stringify(t)] } function u(t) { var e = t[0] / 255, n = t[1] / 255, i = t[2] / 255; return [100 * (.4124 * (e = e > .04045 ? Math.pow((e + .055) / 1.055, 2.4) : e / 12.92) + .3576 * (n = n > .04045 ? Math.pow((n + .055) / 1.055, 2.4) : n / 12.92) + .1805 * (i = i > .04045 ? Math.pow((i + .055) / 1.055, 2.4) : i / 12.92)), 100 * (.2126 * e + .7152 * n + .0722 * i), 100 * (.0193 * e + .1192 * n + .9505 * i)] } function d(t) { var e, n, i, a = u(t), o = a[0], r = a[1], l = a[2]; return o /= 95.047, r /= 100, l /= 108.883, o = o > .008856 ? Math.pow(o, 1 / 3) : 7.787 * o + 16 / 116, r = r > .008856 ? Math.pow(r, 1 / 3) : 7.787 * r + 16 / 116, l = l > .008856 ? Math.pow(l, 1 / 3) : 7.787 * l + 16 / 116, e = 116 * r - 16, n = 500 * (o - r), i = 200 * (r - l), [e, n, i] } function c(t) { var e, n, i, a, o, r = t[0] / 360, l = t[1] / 100, s = t[2] / 100; if (0 == l) return o = 255 * s, [o, o, o]; e = 2 * s - (n = s < .5 ? s * (1 + l) : s + l - s * l), a = [0, 0, 0]; for (var u = 0; u < 3; u++)(i = r + 1 / 3 * -(u - 1)) < 0 && i++, i > 1 && i--, o = 6 * i < 1 ? e + 6 * (n - e) * i : 2 * i < 1 ? n : 3 * i < 2 ? e + (n - e) * (2 / 3 - i) * 6 : e, a[u] = 255 * o; return a } function h(t) { var e = t[0] / 60, n = t[1] / 100, i = t[2] / 100, a = Math.floor(e) % 6, o = e - Math.floor(e), r = 255 * i * (1 - n), l = 255 * i * (1 - n * o), s = 255 * i * (1 - n * (1 - o)), i = 255 * i; switch (a) { case 0: return [i, s, r]; case 1: return [l, i, r]; case 2: return [r, i, s]; case 3: return [r, l, i]; case 4: return [s, r, i]; case 5: return [i, r, l] } } function f(t) { var e, n, i, a, o = t[0] / 360, l = t[1] / 100, s = t[2] / 100, u = l + s; switch (u > 1 && (l /= u, s /= u), e = Math.floor(6 * o), n = 1 - s, i = 6 * o - e, 0 != (1 & e) && (i = 1 - i), a = l + i * (n - l), e) { default: case 6: case 0: r = n, g = a, b = l; break; case 1: r = a, g = n, b = l; break; case 2: r = l, g = n, b = a; break; case 3: r = l, g = a, b = n; break; case 4: r = a, g = l, b = n; break; case 5: r = n, g = l, b = a }return [255 * r, 255 * g, 255 * b] } function p(t) { var e, n, i, a = t[0] / 100, o = t[1] / 100, r = t[2] / 100, l = t[3] / 100; return e = 1 - Math.min(1, a * (1 - l) + l), n = 1 - Math.min(1, o * (1 - l) + l), i = 1 - Math.min(1, r * (1 - l) + l), [255 * e, 255 * n, 255 * i] } function v(t) { var e, n, i, a = t[0] / 100, o = t[1] / 100, r = t[2] / 100; return e = 3.2406 * a + -1.5372 * o + -.4986 * r, n = -.9689 * a + 1.8758 * o + .0415 * r, i = .0557 * a + -.204 * o + 1.057 * r, e = e > .0031308 ? 1.055 * Math.pow(e, 1 / 2.4) - .055 : e *= 12.92, n = n > .0031308 ? 1.055 * Math.pow(n, 1 / 2.4) - .055 : n *= 12.92, i = i > .0031308 ? 1.055 * Math.pow(i, 1 / 2.4) - .055 : i *= 12.92, e = Math.min(Math.max(0, e), 1), n = Math.min(Math.max(0, n), 1), i = Math.min(Math.max(0, i), 1), [255 * e, 255 * n, 255 * i] } function m(t) { var e, n, i, a = t[0], o = t[1], r = t[2]; return a /= 95.047, o /= 100, r /= 108.883, a = a > .008856 ? Math.pow(a, 1 / 3) : 7.787 * a + 16 / 116, o = o > .008856 ? Math.pow(o, 1 / 3) : 7.787 * o + 16 / 116, r = r > .008856 ? Math.pow(r, 1 / 3) : 7.787 * r + 16 / 116, e = 116 * o - 16, n = 500 * (a - o), i = 200 * (o - r), [e, n, i] } function x(t) { var e, n, i, a, o = t[0], r = t[1], l = t[2]; return o <= 8 ? a = (n = 100 * o / 903.3) / 100 * 7.787 + 16 / 116 : (n = 100 * Math.pow((o + 16) / 116, 3), a = Math.pow(n / 100, 1 / 3)), e = e / 95.047 <= .008856 ? e = 95.047 * (r / 500 + a - 16 / 116) / 7.787 : 95.047 * Math.pow(r / 500 + a, 3), i = i / 108.883 <= .008859 ? i = 108.883 * (a - l / 200 - 16 / 116) / 7.787 : 108.883 * Math.pow(a - l / 200, 3), [e, n, i] } function y(t) { var e, n, i, a = t[0], o = t[1], r = t[2]; return e = Math.atan2(r, o), (n = 360 * e / 2 / Math.PI) < 0 && (n += 360), i = Math.sqrt(o * o + r * r), [a, i, n] } function k(t) { return v(x(t)) } function w(t) { var e, n, i, a = t[0], o = t[1]; return i = t[2] / 360 * 2 * Math.PI, e = o * Math.cos(i), n = o * Math.sin(i), [a, e, n] } function M(t) { return S[t] } e.exports = { rgb2hsl: i, rgb2hsv: a, rgb2hwb: o, rgb2cmyk: l, rgb2keyword: s, rgb2xyz: u, rgb2lab: d, rgb2lch: function (t) { return y(d(t)) }, hsl2rgb: c, hsl2hsv: function (t) { var e, n, i = t[0], a = t[1] / 100, o = t[2] / 100; return 0 === o ? [0, 0, 0] : (o *= 2, a *= o <= 1 ? o : 2 - o, n = (o + a) / 2, e = 2 * a / (o + a), [i, 100 * e, 100 * n]) }, hsl2hwb: function (t) { return o(c(t)) }, hsl2cmyk: function (t) { return l(c(t)) }, hsl2keyword: function (t) { return s(c(t)) }, hsv2rgb: h, hsv2hsl: function (t) { var e, n, i = t[0], a = t[1] / 100, o = t[2] / 100; return n = (2 - a) * o, e = a * o, e /= n <= 1 ? n : 2 - n, e = e || 0, n /= 2, [i, 100 * e, 100 * n] }, hsv2hwb: function (t) { return o(h(t)) }, hsv2cmyk: function (t) { return l(h(t)) }, hsv2keyword: function (t) { return s(h(t)) }, hwb2rgb: f, hwb2hsl: function (t) { return i(f(t)) }, hwb2hsv: function (t) { return a(f(t)) }, hwb2cmyk: function (t) { return l(f(t)) }, hwb2keyword: function (t) { return s(f(t)) }, cmyk2rgb: p, cmyk2hsl: function (t) { return i(p(t)) }, cmyk2hsv: function (t) { return a(p(t)) }, cmyk2hwb: function (t) { return o(p(t)) }, cmyk2keyword: function (t) { return s(p(t)) }, keyword2rgb: M, keyword2hsl: function (t) { return i(M(t)) }, keyword2hsv: function (t) { return a(M(t)) }, keyword2hwb: function (t) { return o(M(t)) }, keyword2cmyk: function (t) { return l(M(t)) }, keyword2lab: function (t) { return d(M(t)) }, keyword2xyz: function (t) { return u(M(t)) }, xyz2rgb: v, xyz2lab: m, xyz2lch: function (t) { return y(m(t)) }, lab2xyz: x, lab2rgb: k, lab2lch: y, lch2lab: w, lch2xyz: function (t) { return x(w(t)) }, lch2rgb: function (t) { return k(w(t)) } }; var S = { aliceblue: [240, 248, 255], antiquewhite: [250, 235, 215], aqua: [0, 255, 255], aquamarine: [127, 255, 212], azure: [240, 255, 255], beige: [245, 245, 220], bisque: [255, 228, 196], black: [0, 0, 0], blanchedalmond: [255, 235, 205], blue: [0, 0, 255], blueviolet: [138, 43, 226], brown: [165, 42, 42], burlywood: [222, 184, 135], cadetblue: [95, 158, 160], chartreuse: [127, 255, 0], chocolate: [210, 105, 30], coral: [255, 127, 80], cornflowerblue: [100, 149, 237], cornsilk: [255, 248, 220], crimson: [220, 20, 60], cyan: [0, 255, 255], darkblue: [0, 0, 139], darkcyan: [0, 139, 139], darkgoldenrod: [184, 134, 11], darkgray: [169, 169, 169], darkgreen: [0, 100, 0], darkgrey: [169, 169, 169], darkkhaki: [189, 183, 107], darkmagenta: [139, 0, 139], darkolivegreen: [85, 107, 47], darkorange: [255, 140, 0], darkorchid: [153, 50, 204], darkred: [139, 0, 0], darksalmon: [233, 150, 122], darkseagreen: [143, 188, 143], darkslateblue: [72, 61, 139], darkslategray: [47, 79, 79], darkslategrey: [47, 79, 79], darkturquoise: [0, 206, 209], darkviolet: [148, 0, 211], deeppink: [255, 20, 147], deepskyblue: [0, 191, 255], dimgray: [105, 105, 105], dimgrey: [105, 105, 105], dodgerblue: [30, 144, 255], firebrick: [178, 34, 34], floralwhite: [255, 250, 240], forestgreen: [34, 139, 34], fuchsia: [255, 0, 255], gainsboro: [220, 220, 220], ghostwhite: [248, 248, 255], gold: [255, 215, 0], goldenrod: [218, 165, 32], gray: [128, 128, 128], green: [0, 128, 0], greenyellow: [173, 255, 47], grey: [128, 128, 128], honeydew: [240, 255, 240], hotpink: [255, 105, 180], indianred: [205, 92, 92], indigo: [75, 0, 130], ivory: [255, 255, 240], khaki: [240, 230, 140], lavender: [230, 230, 250], lavenderblush: [255, 240, 245], lawngreen: [124, 252, 0], lemonchiffon: [255, 250, 205], lightblue: [173, 216, 230], lightcoral: [240, 128, 128], lightcyan: [224, 255, 255], lightgoldenrodyellow: [250, 250, 210], lightgray: [211, 211, 211], lightgreen: [144, 238, 144], lightgrey: [211, 211, 211], lightpink: [255, 182, 193], lightsalmon: [255, 160, 122], lightseagreen: [32, 178, 170], lightskyblue: [135, 206, 250], lightslategray: [119, 136, 153], lightslategrey: [119, 136, 153], lightsteelblue: [176, 196, 222], lightyellow: [255, 255, 224], lime: [0, 255, 0], limegreen: [50, 205, 50], linen: [250, 240, 230], magenta: [255, 0, 255], maroon: [128, 0, 0], mediumaquamarine: [102, 205, 170], mediumblue: [0, 0, 205], mediumorchid: [186, 85, 211], mediumpurple: [147, 112, 219], mediumseagreen: [60, 179, 113], mediumslateblue: [123, 104, 238], mediumspringgreen: [0, 250, 154], mediumturquoise: [72, 209, 204], mediumvioletred: [199, 21, 133], midnightblue: [25, 25, 112], mintcream: [245, 255, 250], mistyrose: [255, 228, 225], moccasin: [255, 228, 181], navajowhite: [255, 222, 173], navy: [0, 0, 128], oldlace: [253, 245, 230], olive: [128, 128, 0], olivedrab: [107, 142, 35], orange: [255, 165, 0], orangered: [255, 69, 0], orchid: [218, 112, 214], palegoldenrod: [238, 232, 170], palegreen: [152, 251, 152], paleturquoise: [175, 238, 238], palevioletred: [219, 112, 147], papayawhip: [255, 239, 213], peachpuff: [255, 218, 185], peru: [205, 133, 63], pink: [255, 192, 203], plum: [221, 160, 221], powderblue: [176, 224, 230], purple: [128, 0, 128], rebeccapurple: [102, 51, 153], red: [255, 0, 0], rosybrown: [188, 143, 143], royalblue: [65, 105, 225], saddlebrown: [139, 69, 19], salmon: [250, 128, 114], sandybrown: [244, 164, 96], seagreen: [46, 139, 87], seashell: [255, 245, 238], sienna: [160, 82, 45], silver: [192, 192, 192], skyblue: [135, 206, 235], slateblue: [106, 90, 205], slategray: [112, 128, 144], slategrey: [112, 128, 144], snow: [255, 250, 250], springgreen: [0, 255, 127], steelblue: [70, 130, 180], tan: [210, 180, 140], teal: [0, 128, 128], thistle: [216, 191, 216], tomato: [255, 99, 71], turquoise: [64, 224, 208], violet: [238, 130, 238], wheat: [245, 222, 179], white: [255, 255, 255], whitesmoke: [245, 245, 245], yellow: [255, 255, 0], yellowgreen: [154, 205, 50] }, C = {}; for (var _ in S) C[JSON.stringify(S[_])] = _ }, {}], 5: [function (t, e, n) { var i = t(4), a = function () { return new u }; for (var o in i) { a[o + "Raw"] = function (t) { return function (e) { return "number" == typeof e && (e = Array.prototype.slice.call(arguments)), i[t](e) } }(o); var r = /(\w+)2(\w+)/.exec(o), l = r[1], s = r[2]; (a[l] = a[l] || {})[s] = a[o] = function (t) { return function (e) { "number" == typeof e && (e = Array.prototype.slice.call(arguments)); var n = i[t](e); if ("string" == typeof n || void 0 === n) return n; for (var a = 0; a < n.length; a++)n[a] = Math.round(n[a]); return n } }(o) } var u = function () { this.convs = {} }; u.prototype.routeSpace = function (t, e) { var n = e[0]; return void 0 === n ? this.getValues(t) : ("number" == typeof n && (n = Array.prototype.slice.call(e)), this.setValues(t, n)) }, u.prototype.setValues = function (t, e) { return this.space = t, this.convs = {}, this.convs[t] = e, this }, u.prototype.getValues = function (t) { var e = this.convs[t]; if (!e) { var n = this.space, i = this.convs[n]; e = a[n][t](i), this.convs[t] = e } return e }, ["rgb", "hsl", "hsv", "cmyk", "keyword"].forEach(function (t) { u.prototype[t] = function (e) { return this.routeSpace(t, arguments) } }), e.exports = a }, { 4: 4 }], 6: [function (t, e, n) { "use strict"; e.exports = { aliceblue: [240, 248, 255], antiquewhite: [250, 235, 215], aqua: [0, 255, 255], aquamarine: [127, 255, 212], azure: [240, 255, 255], beige: [245, 245, 220], bisque: [255, 228, 196], black: [0, 0, 0], blanchedalmond: [255, 235, 205], blue: [0, 0, 255], blueviolet: [138, 43, 226], brown: [165, 42, 42], burlywood: [222, 184, 135], cadetblue: [95, 158, 160], chartreuse: [127, 255, 0], chocolate: [210, 105, 30], coral: [255, 127, 80], cornflowerblue: [100, 149, 237], cornsilk: [255, 248, 220], crimson: [220, 20, 60], cyan: [0, 255, 255], darkblue: [0, 0, 139], darkcyan: [0, 139, 139], darkgoldenrod: [184, 134, 11], darkgray: [169, 169, 169], darkgreen: [0, 100, 0], darkgrey: [169, 169, 169], darkkhaki: [189, 183, 107], darkmagenta: [139, 0, 139], darkolivegreen: [85, 107, 47], darkorange: [255, 140, 0], darkorchid: [153, 50, 204], darkred: [139, 0, 0], darksalmon: [233, 150, 122], darkseagreen: [143, 188, 143], darkslateblue: [72, 61, 139], darkslategray: [47, 79, 79], darkslategrey: [47, 79, 79], darkturquoise: [0, 206, 209], darkviolet: [148, 0, 211], deeppink: [255, 20, 147], deepskyblue: [0, 191, 255], dimgray: [105, 105, 105], dimgrey: [105, 105, 105], dodgerblue: [30, 144, 255], firebrick: [178, 34, 34], floralwhite: [255, 250, 240], forestgreen: [34, 139, 34], fuchsia: [255, 0, 255], gainsboro: [220, 220, 220], ghostwhite: [248, 248, 255], gold: [255, 215, 0], goldenrod: [218, 165, 32], gray: [128, 128, 128], green: [0, 128, 0], greenyellow: [173, 255, 47], grey: [128, 128, 128], honeydew: [240, 255, 240], hotpink: [255, 105, 180], indianred: [205, 92, 92], indigo: [75, 0, 130], ivory: [255, 255, 240], khaki: [240, 230, 140], lavender: [230, 230, 250], lavenderblush: [255, 240, 245], lawngreen: [124, 252, 0], lemonchiffon: [255, 250, 205], lightblue: [173, 216, 230], lightcoral: [240, 128, 128], lightcyan: [224, 255, 255], lightgoldenrodyellow: [250, 250, 210], lightgray: [211, 211, 211], lightgreen: [144, 238, 144], lightgrey: [211, 211, 211], lightpink: [255, 182, 193], lightsalmon: [255, 160, 122], lightseagreen: [32, 178, 170], lightskyblue: [135, 206, 250], lightslategray: [119, 136, 153], lightslategrey: [119, 136, 153], lightsteelblue: [176, 196, 222], lightyellow: [255, 255, 224], lime: [0, 255, 0], limegreen: [50, 205, 50], linen: [250, 240, 230], magenta: [255, 0, 255], maroon: [128, 0, 0], mediumaquamarine: [102, 205, 170], mediumblue: [0, 0, 205], mediumorchid: [186, 85, 211], mediumpurple: [147, 112, 219], mediumseagreen: [60, 179, 113], mediumslateblue: [123, 104, 238], mediumspringgreen: [0, 250, 154], mediumturquoise: [72, 209, 204], mediumvioletred: [199, 21, 133], midnightblue: [25, 25, 112], mintcream: [245, 255, 250], mistyrose: [255, 228, 225], moccasin: [255, 228, 181], navajowhite: [255, 222, 173], navy: [0, 0, 128], oldlace: [253, 245, 230], olive: [128, 128, 0], olivedrab: [107, 142, 35], orange: [255, 165, 0], orangered: [255, 69, 0], orchid: [218, 112, 214], palegoldenrod: [238, 232, 170], palegreen: [152, 251, 152], paleturquoise: [175, 238, 238], palevioletred: [219, 112, 147], papayawhip: [255, 239, 213], peachpuff: [255, 218, 185], peru: [205, 133, 63], pink: [255, 192, 203], plum: [221, 160, 221], powderblue: [176, 224, 230], purple: [128, 0, 128], rebeccapurple: [102, 51, 153], red: [255, 0, 0], rosybrown: [188, 143, 143], royalblue: [65, 105, 225], saddlebrown: [139, 69, 19], salmon: [250, 128, 114], sandybrown: [244, 164, 96], seagreen: [46, 139, 87], seashell: [255, 245, 238], sienna: [160, 82, 45], silver: [192, 192, 192], skyblue: [135, 206, 235], slateblue: [106, 90, 205], slategray: [112, 128, 144], slategrey: [112, 128, 144], snow: [255, 250, 250], springgreen: [0, 255, 127], steelblue: [70, 130, 180], tan: [210, 180, 140], teal: [0, 128, 128], thistle: [216, 191, 216], tomato: [255, 99, 71], turquoise: [64, 224, 208], violet: [238, 130, 238], wheat: [245, 222, 179], white: [255, 255, 255], whitesmoke: [245, 245, 245], yellow: [255, 255, 0], yellowgreen: [154, 205, 50] } }, {}], 7: [function (t, e, n) { var i = t(29)(); i.helpers = t(45), t(27)(i), i.defaults = t(25), i.Element = t(26), i.elements = t(40), i.Interaction = t(28), i.platform = t(48), t(31)(i), t(22)(i), t(23)(i), t(24)(i), t(30)(i), t(33)(i), t(32)(i), t(35)(i), t(54)(i), t(52)(i), t(53)(i), t(55)(i), t(56)(i), t(57)(i), t(15)(i), t(16)(i), t(17)(i), t(18)(i), t(19)(i), t(20)(i), t(21)(i), t(8)(i), t(9)(i), t(10)(i), t(11)(i), t(12)(i), t(13)(i), t(14)(i); var a = []; a.push(t(49)(i), t(50)(i), t(51)(i)), i.plugins.register(a), i.platform.initialize(), e.exports = i, "undefined" != typeof window && (window.Chart = i), i.canvasHelpers = i.helpers.canvas }, { 10: 10, 11: 11, 12: 12, 13: 13, 14: 14, 15: 15, 16: 16, 17: 17, 18: 18, 19: 19, 20: 20, 21: 21, 22: 22, 23: 23, 24: 24, 25: 25, 26: 26, 27: 27, 28: 28, 29: 29, 30: 30, 31: 31, 32: 32, 33: 33, 35: 35, 40: 40, 45: 45, 48: 48, 49: 49, 50: 50, 51: 51, 52: 52, 53: 53, 54: 54, 55: 55, 56: 56, 57: 57, 8: 8, 9: 9 }], 8: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Bar = function (e, n) { return n.type = "bar", new t(e, n) } } }, {}], 9: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Bubble = function (e, n) { return n.type = "bubble", new t(e, n) } } }, {}], 10: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Doughnut = function (e, n) { return n.type = "doughnut", new t(e, n) } } }, {}], 11: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Line = function (e, n) { return n.type = "line", new t(e, n) } } }, {}], 12: [function (t, e, n) { "use strict"; e.exports = function (t) { t.PolarArea = function (e, n) { return n.type = "polarArea", new t(e, n) } } }, {}], 13: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Radar = function (e, n) { return n.type = "radar", new t(e, n) } } }, {}], 14: [function (t, e, n) { "use strict"; e.exports = function (t) { t.Scatter = function (e, n) { return n.type = "scatter", new t(e, n) } } }, {}], 15: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("bar", { hover: { mode: "label" }, scales: { xAxes: [{ type: "category", categoryPercentage: .8, barPercentage: .9, offset: !0, gridLines: { offsetGridLines: !0 } }], yAxes: [{ type: "linear" }] } }), i._set("horizontalBar", { hover: { mode: "index", axis: "y" }, scales: { xAxes: [{ type: "linear", position: "bottom" }], yAxes: [{ position: "left", type: "category", categoryPercentage: .8, barPercentage: .9, offset: !0, gridLines: { offsetGridLines: !0 } }] }, elements: { rectangle: { borderSkipped: "left" } }, tooltips: { callbacks: { title: function (t, e) { var n = ""; return t.length > 0 && (t[0].yLabel ? n = t[0].yLabel : e.labels.length > 0 && t[0].index < e.labels.length && (n = e.labels[t[0].index])), n }, label: function (t, e) { return (e.datasets[t.datasetIndex].label || "") + ": " + t.xLabel } }, mode: "index", axis: "y" } }), e.exports = function (t) { t.controllers.bar = t.DatasetController.extend({ dataElementType: a.Rectangle, initialize: function () { var e, n = this; t.DatasetController.prototype.initialize.apply(n, arguments), (e = n.getMeta()).stack = n.getDataset().stack, e.bar = !0 }, update: function (t) { var e, n, i = this, a = i.getMeta().data; for (i._ruler = i.getRuler(), e = 0, n = a.length; e < n; ++e)i.updateElement(a[e], e, t) }, updateElement: function (t, e, n) { var i = this, a = i.chart, r = i.getMeta(), l = i.getDataset(), s = t.custom || {}, u = a.options.elements.rectangle; t._xScale = i.getScaleForId(r.xAxisID), t._yScale = i.getScaleForId(r.yAxisID), t._datasetIndex = i.index, t._index = e, t._model = { datasetLabel: l.label, label: a.data.labels[e], borderSkipped: s.borderSkipped ? s.borderSkipped : u.borderSkipped, backgroundColor: s.backgroundColor ? s.backgroundColor : o.valueAtIndexOrDefault(l.backgroundColor, e, u.backgroundColor), borderColor: s.borderColor ? s.borderColor : o.valueAtIndexOrDefault(l.borderColor, e, u.borderColor), borderWidth: s.borderWidth ? s.borderWidth : o.valueAtIndexOrDefault(l.borderWidth, e, u.borderWidth) }, i.updateElementGeometry(t, e, n), t.pivot() }, updateElementGeometry: function (t, e, n) { var i = this, a = t._model, o = i.getValueScale(), r = o.getBasePixel(), l = o.isHorizontal(), s = i._ruler || i.getRuler(), u = i.calculateBarValuePixels(i.index, e), d = i.calculateBarIndexPixels(i.index, e, s); a.horizontal = l, a.base = n ? r : u.base, a.x = l ? n ? r : u.head : d.center, a.y = l ? d.center : n ? r : u.head, a.height = l ? d.size : void 0, a.width = l ? void 0 : d.size }, getValueScaleId: function () { return this.getMeta().yAxisID }, getIndexScaleId: function () { return this.getMeta().xAxisID }, getValueScale: function () { return this.getScaleForId(this.getValueScaleId()) }, getIndexScale: function () { return this.getScaleForId(this.getIndexScaleId()) }, getStackCount: function (t) { var e, n, i = this, a = i.chart, o = i.getIndexScale().options.stacked, r = void 0 === t ? a.data.datasets.length : t + 1, l = []; for (e = 0; e < r; ++e)(n = a.getDatasetMeta(e)).bar && a.isDatasetVisible(e) && (!1 === o || !0 === o && -1 === l.indexOf(n.stack) || void 0 === o && (void 0 === n.stack || -1 === l.indexOf(n.stack))) && l.push(n.stack); return l.length }, getStackIndex: function (t) { return this.getStackCount(t) - 1 }, getRuler: function () { var t, e, n = this, i = n.getIndexScale(), a = n.getStackCount(), o = n.index, r = [], l = i.isHorizontal(), s = l ? i.left : i.top, u = s + (l ? i.width : i.height); for (t = 0, e = n.getMeta().data.length; t < e; ++t)r.push(i.getPixelForValue(null, t, o)); return { pixels: r, start: s, end: u, stackCount: a, scale: i } }, calculateBarValuePixels: function (t, e) { var n, i, a, o, r, l, s = this, u = s.chart, d = s.getMeta(), c = s.getValueScale(), h = u.data.datasets, f = c.getRightValue(h[t].data[e]), g = c.options.stacked, p = d.stack, v = 0; if (g || void 0 === g && void 0 !== p) for (n = 0; n < t; ++n)(i = u.getDatasetMeta(n)).bar && i.stack === p && i.controller.getValueScaleId() === c.id && u.isDatasetVisible(n) && (a = c.getRightValue(h[n].data[e]), (f < 0 && a < 0 || f >= 0 && a > 0) && (v += a)); return o = c.getPixelForValue(v), r = c.getPixelForValue(v + f), l = (r - o) / 2, { size: l, base: o, head: r, center: r + l / 2 } }, calculateBarIndexPixels: function (t, e, n) { var i, a, r, l, s, u, d = this, c = n.scale.options, h = d.getStackIndex(t), f = n.pixels, g = f[e], p = f.length, v = n.start, m = n.end; return 1 === p ? (i = g > v ? g - v : m - g, a = g < m ? m - g : g - v) : (e > 0 && (i = (g - f[e - 1]) / 2, e === p - 1 && (a = i)), e < p - 1 && (a = (f[e + 1] - g) / 2, 0 === e && (i = a))), r = i * c.categoryPercentage, l = a * c.categoryPercentage, s = (r + l) / n.stackCount, u = s * c.barPercentage, u = Math.min(o.valueOrDefault(c.barThickness, u), o.valueOrDefault(c.maxBarThickness, 1 / 0)), g -= r, g += s * h, g += (s - u) / 2, { size: u, base: g, head: g + u, center: g + u / 2 } }, draw: function () { var t = this, e = t.chart, n = t.getValueScale(), i = t.getMeta().data, a = t.getDataset(), r = i.length, l = 0; for (o.canvas.clipArea(e.ctx, e.chartArea); l < r; ++l)isNaN(n.getRightValue(a.data[l])) || i[l].draw(); o.canvas.unclipArea(e.ctx) }, setHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t._index, i = t.custom || {}, a = t._model; a.backgroundColor = i.hoverBackgroundColor ? i.hoverBackgroundColor : o.valueAtIndexOrDefault(e.hoverBackgroundColor, n, o.getHoverColor(a.backgroundColor)), a.borderColor = i.hoverBorderColor ? i.hoverBorderColor : o.valueAtIndexOrDefault(e.hoverBorderColor, n, o.getHoverColor(a.borderColor)), a.borderWidth = i.hoverBorderWidth ? i.hoverBorderWidth : o.valueAtIndexOrDefault(e.hoverBorderWidth, n, a.borderWidth) }, removeHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t._index, i = t.custom || {}, a = t._model, r = this.chart.options.elements.rectangle; a.backgroundColor = i.backgroundColor ? i.backgroundColor : o.valueAtIndexOrDefault(e.backgroundColor, n, r.backgroundColor), a.borderColor = i.borderColor ? i.borderColor : o.valueAtIndexOrDefault(e.borderColor, n, r.borderColor), a.borderWidth = i.borderWidth ? i.borderWidth : o.valueAtIndexOrDefault(e.borderWidth, n, r.borderWidth) } }), t.controllers.horizontalBar = t.controllers.bar.extend({ getValueScaleId: function () { return this.getMeta().xAxisID }, getIndexScaleId: function () { return this.getMeta().yAxisID } }) } }, { 25: 25, 40: 40, 45: 45 }], 16: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("bubble", { hover: { mode: "single" }, scales: { xAxes: [{ type: "linear", position: "bottom", id: "x-axis-0" }], yAxes: [{ type: "linear", position: "left", id: "y-axis-0" }] }, tooltips: { callbacks: { title: function () { return "" }, label: function (t, e) { var n = e.datasets[t.datasetIndex].label || "", i = e.datasets[t.datasetIndex].data[t.index]; return n + ": (" + t.xLabel + ", " + t.yLabel + ", " + i.r + ")" } } } }), e.exports = function (t) { t.controllers.bubble = t.DatasetController.extend({ dataElementType: a.Point, update: function (t) { var e = this, n = e.getMeta().data; o.each(n, function (n, i) { e.updateElement(n, i, t) }) }, updateElement: function (t, e, n) { var i = this, a = i.getMeta(), o = t.custom || {}, r = i.getScaleForId(a.xAxisID), l = i.getScaleForId(a.yAxisID), s = i._resolveElementOptions(t, e), u = i.getDataset().data[e], d = i.index, c = n ? r.getPixelForDecimal(.5) : r.getPixelForValue("object" == typeof u ? u : NaN, e, d), h = n ? l.getBasePixel() : l.getPixelForValue(u, e, d); t._xScale = r, t._yScale = l, t._options = s, t._datasetIndex = d, t._index = e, t._model = { backgroundColor: s.backgroundColor, borderColor: s.borderColor, borderWidth: s.borderWidth, hitRadius: s.hitRadius, pointStyle: s.pointStyle, radius: n ? 0 : s.radius, skip: o.skip || isNaN(c) || isNaN(h), x: c, y: h }, t.pivot() }, setHoverStyle: function (t) { var e = t._model, n = t._options; e.backgroundColor = o.valueOrDefault(n.hoverBackgroundColor, o.getHoverColor(n.backgroundColor)), e.borderColor = o.valueOrDefault(n.hoverBorderColor, o.getHoverColor(n.borderColor)), e.borderWidth = o.valueOrDefault(n.hoverBorderWidth, n.borderWidth), e.radius = n.radius + n.hoverRadius }, removeHoverStyle: function (t) { var e = t._model, n = t._options; e.backgroundColor = n.backgroundColor, e.borderColor = n.borderColor, e.borderWidth = n.borderWidth, e.radius = n.radius }, _resolveElementOptions: function (t, e) { var n, i, a, r = this, l = r.chart, s = l.data.datasets[r.index], u = t.custom || {}, d = l.options.elements.point, c = o.options.resolve, h = s.data[e], f = {}, g = { chart: l, dataIndex: e, dataset: s, datasetIndex: r.index }, p = ["backgroundColor", "borderColor", "borderWidth", "hoverBackgroundColor", "hoverBorderColor", "hoverBorderWidth", "hoverRadius", "hitRadius", "pointStyle"]; for (n = 0, i = p.length; n < i; ++n)f[a = p[n]] = c([u[a], s[a], d[a]], g, e); return f.radius = c([u.radius, h ? h.r : void 0, s.radius, d.radius], g, e), f } }) } }, { 25: 25, 40: 40, 45: 45 }], 17: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("doughnut", { animation: { animateRotate: !0, animateScale: !1 }, hover: { mode: "single" }, legendCallback: function (t) { var e = []; e.push('<ul class="' + t.id + '-legend">'); var n = t.data, i = n.datasets, a = n.labels; if (i.length) for (var o = 0; o < i[0].data.length; ++o)e.push('<li><span style="background-color:' + i[0].backgroundColor[o] + '"></span>'), a[o] && e.push(a[o]), e.push("</li>"); return e.push("</ul>"), e.join("") }, legend: { labels: { generateLabels: function (t) { var e = t.data; return e.labels.length && e.datasets.length ? e.labels.map(function (n, i) { var a = t.getDatasetMeta(0), r = e.datasets[0], l = a.data[i], s = l && l.custom || {}, u = o.valueAtIndexOrDefault, d = t.options.elements.arc; return { text: n, fillStyle: s.backgroundColor ? s.backgroundColor : u(r.backgroundColor, i, d.backgroundColor), strokeStyle: s.borderColor ? s.borderColor : u(r.borderColor, i, d.borderColor), lineWidth: s.borderWidth ? s.borderWidth : u(r.borderWidth, i, d.borderWidth), hidden: isNaN(r.data[i]) || a.data[i].hidden, index: i } }) : [] } }, onClick: function (t, e) { var n, i, a, o = e.index, r = this.chart; for (n = 0, i = (r.data.datasets || []).length; n < i; ++n)(a = r.getDatasetMeta(n)).data[o] && (a.data[o].hidden = !a.data[o].hidden); r.update() } }, cutoutPercentage: 50, rotation: -.5 * Math.PI, circumference: 2 * Math.PI, tooltips: { callbacks: { title: function () { return "" }, label: function (t, e) { var n = e.labels[t.index], i = ": " + e.datasets[t.datasetIndex].data[t.index]; return o.isArray(n) ? (n = n.slice())[0] += i : n += i, n } } } }), i._set("pie", o.clone(i.doughnut)), i._set("pie", { cutoutPercentage: 0 }), e.exports = function (t) { t.controllers.doughnut = t.controllers.pie = t.DatasetController.extend({ dataElementType: a.Arc, linkScales: o.noop, getRingIndex: function (t) { for (var e = 0, n = 0; n < t; ++n)this.chart.isDatasetVisible(n) && ++e; return e }, update: function (t) { var e = this, n = e.chart, i = n.chartArea, a = n.options, r = a.elements.arc, l = i.right - i.left - r.borderWidth, s = i.bottom - i.top - r.borderWidth, u = Math.min(l, s), d = { x: 0, y: 0 }, c = e.getMeta(), h = a.cutoutPercentage, f = a.circumference; if (f < 2 * Math.PI) { var g = a.rotation % (2 * Math.PI), p = (g += 2 * Math.PI * (g >= Math.PI ? -1 : g < -Math.PI ? 1 : 0)) + f, v = { x: Math.cos(g), y: Math.sin(g) }, m = { x: Math.cos(p), y: Math.sin(p) }, b = g <= 0 && p >= 0 || g <= 2 * Math.PI && 2 * Math.PI <= p, x = g <= .5 * Math.PI && .5 * Math.PI <= p || g <= 2.5 * Math.PI && 2.5 * Math.PI <= p, y = g <= -Math.PI && -Math.PI <= p || g <= Math.PI && Math.PI <= p, k = g <= .5 * -Math.PI && .5 * -Math.PI <= p || g <= 1.5 * Math.PI && 1.5 * Math.PI <= p, w = h / 100, M = { x: y ? -1 : Math.min(v.x * (v.x < 0 ? 1 : w), m.x * (m.x < 0 ? 1 : w)), y: k ? -1 : Math.min(v.y * (v.y < 0 ? 1 : w), m.y * (m.y < 0 ? 1 : w)) }, S = { x: b ? 1 : Math.max(v.x * (v.x > 0 ? 1 : w), m.x * (m.x > 0 ? 1 : w)), y: x ? 1 : Math.max(v.y * (v.y > 0 ? 1 : w), m.y * (m.y > 0 ? 1 : w)) }, C = { width: .5 * (S.x - M.x), height: .5 * (S.y - M.y) }; u = Math.min(l / C.width, s / C.height), d = { x: -.5 * (S.x + M.x), y: -.5 * (S.y + M.y) } } n.borderWidth = e.getMaxBorderWidth(c.data), n.outerRadius = Math.max((u - n.borderWidth) / 2, 0), n.innerRadius = Math.max(h ? n.outerRadius / 100 * h : 0, 0), n.radiusLength = (n.outerRadius - n.innerRadius) / n.getVisibleDatasetCount(), n.offsetX = d.x * n.outerRadius, n.offsetY = d.y * n.outerRadius, c.total = e.calculateTotal(), e.outerRadius = n.outerRadius - n.radiusLength * e.getRingIndex(e.index), e.innerRadius = Math.max(e.outerRadius - n.radiusLength, 0), o.each(c.data, function (n, i) { e.updateElement(n, i, t) }) }, updateElement: function (t, e, n) { var i = this, a = i.chart, r = a.chartArea, l = a.options, s = l.animation, u = (r.left + r.right) / 2, d = (r.top + r.bottom) / 2, c = l.rotation, h = l.rotation, f = i.getDataset(), g = n && s.animateRotate ? 0 : t.hidden ? 0 : i.calculateCircumference(f.data[e]) * (l.circumference / (2 * Math.PI)), p = n && s.animateScale ? 0 : i.innerRadius, v = n && s.animateScale ? 0 : i.outerRadius, m = o.valueAtIndexOrDefault; o.extend(t, { _datasetIndex: i.index, _index: e, _model: { x: u + a.offsetX, y: d + a.offsetY, startAngle: c, endAngle: h, circumference: g, outerRadius: v, innerRadius: p, label: m(f.label, e, a.data.labels[e]) } }); var b = t._model; this.removeHoverStyle(t), n && s.animateRotate || (b.startAngle = 0 === e ? l.rotation : i.getMeta().data[e - 1]._model.endAngle, b.endAngle = b.startAngle + b.circumference), t.pivot() }, removeHoverStyle: function (e) { t.DatasetController.prototype.removeHoverStyle.call(this, e, this.chart.options.elements.arc) }, calculateTotal: function () { var t, e = this.getDataset(), n = this.getMeta(), i = 0; return o.each(n.data, function (n, a) { t = e.data[a], isNaN(t) || n.hidden || (i += Math.abs(t)) }), i }, calculateCircumference: function (t) { var e = this.getMeta().total; return e > 0 && !isNaN(t) ? 2 * Math.PI * (t / e) : 0 }, getMaxBorderWidth: function (t) { for (var e, n, i = 0, a = this.index, o = t.length, r = 0; r < o; r++)e = t[r]._model ? t[r]._model.borderWidth : 0, i = (n = t[r]._chart ? t[r]._chart.config.data.datasets[a].hoverBorderWidth : 0) > (i = e > i ? e : i) ? n : i; return i } }) } }, { 25: 25, 40: 40, 45: 45 }], 18: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("line", { showLines: !0, spanGaps: !1, hover: { mode: "label" }, scales: { xAxes: [{ type: "category", id: "x-axis-0" }], yAxes: [{ type: "linear", id: "y-axis-0" }] } }), e.exports = function (t) { function e(t, e) { return o.valueOrDefault(t.showLine, e.showLines) } t.controllers.line = t.DatasetController.extend({ datasetElementType: a.Line, dataElementType: a.Point, update: function (t) { var n, i, a, r = this, l = r.getMeta(), s = l.dataset, u = l.data || [], d = r.chart.options, c = d.elements.line, h = r.getScaleForId(l.yAxisID), f = r.getDataset(), g = e(f, d); for (g && (a = s.custom || {}, void 0 !== f.tension && void 0 === f.lineTension && (f.lineTension = f.tension), s._scale = h, s._datasetIndex = r.index, s._children = u, s._model = { spanGaps: f.spanGaps ? f.spanGaps : d.spanGaps, tension: a.tension ? a.tension : o.valueOrDefault(f.lineTension, c.tension), backgroundColor: a.backgroundColor ? a.backgroundColor : f.backgroundColor || c.backgroundColor, borderWidth: a.borderWidth ? a.borderWidth : f.borderWidth || c.borderWidth, borderColor: a.borderColor ? a.borderColor : f.borderColor || c.borderColor, borderCapStyle: a.borderCapStyle ? a.borderCapStyle : f.borderCapStyle || c.borderCapStyle, borderDash: a.borderDash ? a.borderDash : f.borderDash || c.borderDash, borderDashOffset: a.borderDashOffset ? a.borderDashOffset : f.borderDashOffset || c.borderDashOffset, borderJoinStyle: a.borderJoinStyle ? a.borderJoinStyle : f.borderJoinStyle || c.borderJoinStyle, fill: a.fill ? a.fill : void 0 !== f.fill ? f.fill : c.fill, steppedLine: a.steppedLine ? a.steppedLine : o.valueOrDefault(f.steppedLine, c.stepped), cubicInterpolationMode: a.cubicInterpolationMode ? a.cubicInterpolationMode : o.valueOrDefault(f.cubicInterpolationMode, c.cubicInterpolationMode) }, s.pivot()), n = 0, i = u.length; n < i; ++n)r.updateElement(u[n], n, t); for (g && 0 !== s._model.tension && r.updateBezierControlPoints(), n = 0, i = u.length; n < i; ++n)u[n].pivot() }, getPointBackgroundColor: function (t, e) { var n = this.chart.options.elements.point.backgroundColor, i = this.getDataset(), a = t.custom || {}; return a.backgroundColor ? n = a.backgroundColor : i.pointBackgroundColor ? n = o.valueAtIndexOrDefault(i.pointBackgroundColor, e, n) : i.backgroundColor && (n = i.backgroundColor), n }, getPointBorderColor: function (t, e) { var n = this.chart.options.elements.point.borderColor, i = this.getDataset(), a = t.custom || {}; return a.borderColor ? n = a.borderColor : i.pointBorderColor ? n = o.valueAtIndexOrDefault(i.pointBorderColor, e, n) : i.borderColor && (n = i.borderColor), n }, getPointBorderWidth: function (t, e) { var n = this.chart.options.elements.point.borderWidth, i = this.getDataset(), a = t.custom || {}; return isNaN(a.borderWidth) ? !isNaN(i.pointBorderWidth) || o.isArray(i.pointBorderWidth) ? n = o.valueAtIndexOrDefault(i.pointBorderWidth, e, n) : isNaN(i.borderWidth) || (n = i.borderWidth) : n = a.borderWidth, n }, updateElement: function (t, e, n) { var i, a, r = this, l = r.getMeta(), s = t.custom || {}, u = r.getDataset(), d = r.index, c = u.data[e], h = r.getScaleForId(l.yAxisID), f = r.getScaleForId(l.xAxisID), g = r.chart.options.elements.point; void 0 !== u.radius && void 0 === u.pointRadius && (u.pointRadius = u.radius), void 0 !== u.hitRadius && void 0 === u.pointHitRadius && (u.pointHitRadius = u.hitRadius), i = f.getPixelForValue("object" == typeof c ? c : NaN, e, d), a = n ? h.getBasePixel() : r.calculatePointY(c, e, d), t._xScale = f, t._yScale = h, t._datasetIndex = d, t._index = e, t._model = { x: i, y: a, skip: s.skip || isNaN(i) || isNaN(a), radius: s.radius || o.valueAtIndexOrDefault(u.pointRadius, e, g.radius), pointStyle: s.pointStyle || o.valueAtIndexOrDefault(u.pointStyle, e, g.pointStyle), backgroundColor: r.getPointBackgroundColor(t, e), borderColor: r.getPointBorderColor(t, e), borderWidth: r.getPointBorderWidth(t, e), tension: l.dataset._model ? l.dataset._model.tension : 0, steppedLine: !!l.dataset._model && l.dataset._model.steppedLine, hitRadius: s.hitRadius || o.valueAtIndexOrDefault(u.pointHitRadius, e, g.hitRadius) } }, calculatePointY: function (t, e, n) { var i, a, o, r = this, l = r.chart, s = r.getMeta(), u = r.getScaleForId(s.yAxisID), d = 0, c = 0; if (u.options.stacked) { for (i = 0; i < n; i++)if (a = l.data.datasets[i], "line" === (o = l.getDatasetMeta(i)).type && o.yAxisID === u.id && l.isDatasetVisible(i)) { var h = Number(u.getRightValue(a.data[e])); h < 0 ? c += h || 0 : d += h || 0 } var f = Number(u.getRightValue(t)); return f < 0 ? u.getPixelForValue(c + f) : u.getPixelForValue(d + f) } return u.getPixelForValue(t) }, updateBezierControlPoints: function () { function t(t, e, n) { return Math.max(Math.min(t, n), e) } var e, n, i, a, r = this, l = r.getMeta(), s = r.chart.chartArea, u = l.data || []; if (l.dataset._model.spanGaps && (u = u.filter(function (t) { return !t._model.skip })), "monotone" === l.dataset._model.cubicInterpolationMode) o.splineCurveMonotone(u); else for (e = 0, n = u.length; e < n; ++e)i = u[e]._model, a = o.splineCurve(o.previousItem(u, e)._model, i, o.nextItem(u, e)._model, l.dataset._model.tension), i.controlPointPreviousX = a.previous.x, i.controlPointPreviousY = a.previous.y, i.controlPointNextX = a.next.x, i.controlPointNextY = a.next.y; if (r.chart.options.elements.line.capBezierPoints) for (e = 0, n = u.length; e < n; ++e)(i = u[e]._model).controlPointPreviousX = t(i.controlPointPreviousX, s.left, s.right), i.controlPointPreviousY = t(i.controlPointPreviousY, s.top, s.bottom), i.controlPointNextX = t(i.controlPointNextX, s.left, s.right), i.controlPointNextY = t(i.controlPointNextY, s.top, s.bottom) }, draw: function () { var t = this, n = t.chart, i = t.getMeta(), a = i.data || [], r = n.chartArea, l = a.length, s = 0; for (o.canvas.clipArea(n.ctx, r), e(t.getDataset(), n.options) && i.dataset.draw(), o.canvas.unclipArea(n.ctx); s < l; ++s)a[s].draw(r) }, setHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t._index, i = t.custom || {}, a = t._model; a.radius = i.hoverRadius || o.valueAtIndexOrDefault(e.pointHoverRadius, n, this.chart.options.elements.point.hoverRadius), a.backgroundColor = i.hoverBackgroundColor || o.valueAtIndexOrDefault(e.pointHoverBackgroundColor, n, o.getHoverColor(a.backgroundColor)), a.borderColor = i.hoverBorderColor || o.valueAtIndexOrDefault(e.pointHoverBorderColor, n, o.getHoverColor(a.borderColor)), a.borderWidth = i.hoverBorderWidth || o.valueAtIndexOrDefault(e.pointHoverBorderWidth, n, a.borderWidth) }, removeHoverStyle: function (t) { var e = this, n = e.chart.data.datasets[t._datasetIndex], i = t._index, a = t.custom || {}, r = t._model; void 0 !== n.radius && void 0 === n.pointRadius && (n.pointRadius = n.radius), r.radius = a.radius || o.valueAtIndexOrDefault(n.pointRadius, i, e.chart.options.elements.point.radius), r.backgroundColor = e.getPointBackgroundColor(t, i), r.borderColor = e.getPointBorderColor(t, i), r.borderWidth = e.getPointBorderWidth(t, i) } }) } }, { 25: 25, 40: 40, 45: 45 }], 19: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("polarArea", { scale: { type: "radialLinear", angleLines: { display: !1 }, gridLines: { circular: !0 }, pointLabels: { display: !1 }, ticks: { beginAtZero: !0 } }, animation: { animateRotate: !0, animateScale: !0 }, startAngle: -.5 * Math.PI, legendCallback: function (t) { var e = []; e.push('<ul class="' + t.id + '-legend">'); var n = t.data, i = n.datasets, a = n.labels; if (i.length) for (var o = 0; o < i[0].data.length; ++o)e.push('<li><span style="background-color:' + i[0].backgroundColor[o] + '"></span>'), a[o] && e.push(a[o]), e.push("</li>"); return e.push("</ul>"), e.join("") }, legend: { labels: { generateLabels: function (t) { var e = t.data; return e.labels.length && e.datasets.length ? e.labels.map(function (n, i) { var a = t.getDatasetMeta(0), r = e.datasets[0], l = a.data[i].custom || {}, s = o.valueAtIndexOrDefault, u = t.options.elements.arc; return { text: n, fillStyle: l.backgroundColor ? l.backgroundColor : s(r.backgroundColor, i, u.backgroundColor), strokeStyle: l.borderColor ? l.borderColor : s(r.borderColor, i, u.borderColor), lineWidth: l.borderWidth ? l.borderWidth : s(r.borderWidth, i, u.borderWidth), hidden: isNaN(r.data[i]) || a.data[i].hidden, index: i } }) : [] } }, onClick: function (t, e) { var n, i, a, o = e.index, r = this.chart; for (n = 0, i = (r.data.datasets || []).length; n < i; ++n)(a = r.getDatasetMeta(n)).data[o].hidden = !a.data[o].hidden; r.update() } }, tooltips: { callbacks: { title: function () { return "" }, label: function (t, e) { return e.labels[t.index] + ": " + t.yLabel } } } }), e.exports = function (t) { t.controllers.polarArea = t.DatasetController.extend({ dataElementType: a.Arc, linkScales: o.noop, update: function (t) { var e = this, n = e.chart, i = n.chartArea, a = e.getMeta(), r = n.options, l = r.elements.arc, s = Math.min(i.right - i.left, i.bottom - i.top); n.outerRadius = Math.max((s - l.borderWidth / 2) / 2, 0), n.innerRadius = Math.max(r.cutoutPercentage ? n.outerRadius / 100 * r.cutoutPercentage : 1, 0), n.radiusLength = (n.outerRadius - n.innerRadius) / n.getVisibleDatasetCount(), e.outerRadius = n.outerRadius - n.radiusLength * e.index, e.innerRadius = e.outerRadius - n.radiusLength, a.count = e.countVisibleElements(), o.each(a.data, function (n, i) { e.updateElement(n, i, t) }) }, updateElement: function (t, e, n) { for (var i = this, a = i.chart, r = i.getDataset(), l = a.options, s = l.animation, u = a.scale, d = a.data.labels, c = i.calculateCircumference(r.data[e]), h = u.xCenter, f = u.yCenter, g = 0, p = i.getMeta(), v = 0; v < e; ++v)isNaN(r.data[v]) || p.data[v].hidden || ++g; var m = l.startAngle, b = t.hidden ? 0 : u.getDistanceFromCenterForValue(r.data[e]), x = m + c * g, y = x + (t.hidden ? 0 : c), k = s.animateScale ? 0 : u.getDistanceFromCenterForValue(r.data[e]); o.extend(t, { _datasetIndex: i.index, _index: e, _scale: u, _model: { x: h, y: f, innerRadius: 0, outerRadius: n ? k : b, startAngle: n && s.animateRotate ? m : x, endAngle: n && s.animateRotate ? m : y, label: o.valueAtIndexOrDefault(d, e, d[e]) } }), i.removeHoverStyle(t), t.pivot() }, removeHoverStyle: function (e) { t.DatasetController.prototype.removeHoverStyle.call(this, e, this.chart.options.elements.arc) }, countVisibleElements: function () { var t = this.getDataset(), e = this.getMeta(), n = 0; return o.each(e.data, function (e, i) { isNaN(t.data[i]) || e.hidden || n++ }), n }, calculateCircumference: function (t) { var e = this.getMeta().count; return e > 0 && !isNaN(t) ? 2 * Math.PI / e : 0 } }) } }, { 25: 25, 40: 40, 45: 45 }], 20: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("radar", { scale: { type: "radialLinear" }, elements: { line: { tension: 0 } } }), e.exports = function (t) { t.controllers.radar = t.DatasetController.extend({ datasetElementType: a.Line, dataElementType: a.Point, linkScales: o.noop, update: function (t) { var e = this, n = e.getMeta(), i = n.dataset, a = n.data, r = i.custom || {}, l = e.getDataset(), s = e.chart.options.elements.line, u = e.chart.scale; void 0 !== l.tension && void 0 === l.lineTension && (l.lineTension = l.tension), o.extend(n.dataset, { _datasetIndex: e.index, _scale: u, _children: a, _loop: !0, _model: { tension: r.tension ? r.tension : o.valueOrDefault(l.lineTension, s.tension), backgroundColor: r.backgroundColor ? r.backgroundColor : l.backgroundColor || s.backgroundColor, borderWidth: r.borderWidth ? r.borderWidth : l.borderWidth || s.borderWidth, borderColor: r.borderColor ? r.borderColor : l.borderColor || s.borderColor, fill: r.fill ? r.fill : void 0 !== l.fill ? l.fill : s.fill, borderCapStyle: r.borderCapStyle ? r.borderCapStyle : l.borderCapStyle || s.borderCapStyle, borderDash: r.borderDash ? r.borderDash : l.borderDash || s.borderDash, borderDashOffset: r.borderDashOffset ? r.borderDashOffset : l.borderDashOffset || s.borderDashOffset, borderJoinStyle: r.borderJoinStyle ? r.borderJoinStyle : l.borderJoinStyle || s.borderJoinStyle } }), n.dataset.pivot(), o.each(a, function (n, i) { e.updateElement(n, i, t) }, e), e.updateBezierControlPoints() }, updateElement: function (t, e, n) { var i = this, a = t.custom || {}, r = i.getDataset(), l = i.chart.scale, s = i.chart.options.elements.point, u = l.getPointPositionForValue(e, r.data[e]); void 0 !== r.radius && void 0 === r.pointRadius && (r.pointRadius = r.radius), void 0 !== r.hitRadius && void 0 === r.pointHitRadius && (r.pointHitRadius = r.hitRadius), o.extend(t, { _datasetIndex: i.index, _index: e, _scale: l, _model: { x: n ? l.xCenter : u.x, y: n ? l.yCenter : u.y, tension: a.tension ? a.tension : o.valueOrDefault(r.lineTension, i.chart.options.elements.line.tension), radius: a.radius ? a.radius : o.valueAtIndexOrDefault(r.pointRadius, e, s.radius), backgroundColor: a.backgroundColor ? a.backgroundColor : o.valueAtIndexOrDefault(r.pointBackgroundColor, e, s.backgroundColor), borderColor: a.borderColor ? a.borderColor : o.valueAtIndexOrDefault(r.pointBorderColor, e, s.borderColor), borderWidth: a.borderWidth ? a.borderWidth : o.valueAtIndexOrDefault(r.pointBorderWidth, e, s.borderWidth), pointStyle: a.pointStyle ? a.pointStyle : o.valueAtIndexOrDefault(r.pointStyle, e, s.pointStyle), hitRadius: a.hitRadius ? a.hitRadius : o.valueAtIndexOrDefault(r.pointHitRadius, e, s.hitRadius) } }), t._model.skip = a.skip ? a.skip : isNaN(t._model.x) || isNaN(t._model.y) }, updateBezierControlPoints: function () { var t = this.chart.chartArea, e = this.getMeta(); o.each(e.data, function (n, i) { var a = n._model, r = o.splineCurve(o.previousItem(e.data, i, !0)._model, a, o.nextItem(e.data, i, !0)._model, a.tension); a.controlPointPreviousX = Math.max(Math.min(r.previous.x, t.right), t.left), a.controlPointPreviousY = Math.max(Math.min(r.previous.y, t.bottom), t.top), a.controlPointNextX = Math.max(Math.min(r.next.x, t.right), t.left), a.controlPointNextY = Math.max(Math.min(r.next.y, t.bottom), t.top), n.pivot() }) }, setHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t.custom || {}, i = t._index, a = t._model; a.radius = n.hoverRadius ? n.hoverRadius : o.valueAtIndexOrDefault(e.pointHoverRadius, i, this.chart.options.elements.point.hoverRadius), a.backgroundColor = n.hoverBackgroundColor ? n.hoverBackgroundColor : o.valueAtIndexOrDefault(e.pointHoverBackgroundColor, i, o.getHoverColor(a.backgroundColor)), a.borderColor = n.hoverBorderColor ? n.hoverBorderColor : o.valueAtIndexOrDefault(e.pointHoverBorderColor, i, o.getHoverColor(a.borderColor)), a.borderWidth = n.hoverBorderWidth ? n.hoverBorderWidth : o.valueAtIndexOrDefault(e.pointHoverBorderWidth, i, a.borderWidth) }, removeHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t.custom || {}, i = t._index, a = t._model, r = this.chart.options.elements.point; a.radius = n.radius ? n.radius : o.valueAtIndexOrDefault(e.pointRadius, i, r.radius), a.backgroundColor = n.backgroundColor ? n.backgroundColor : o.valueAtIndexOrDefault(e.pointBackgroundColor, i, r.backgroundColor), a.borderColor = n.borderColor ? n.borderColor : o.valueAtIndexOrDefault(e.pointBorderColor, i, r.borderColor), a.borderWidth = n.borderWidth ? n.borderWidth : o.valueAtIndexOrDefault(e.pointBorderWidth, i, r.borderWidth) } }) } }, { 25: 25, 40: 40, 45: 45 }], 21: [function (t, e, n) { "use strict"; t(25)._set("scatter", { hover: { mode: "single" }, scales: { xAxes: [{ id: "x-axis-1", type: "linear", position: "bottom" }], yAxes: [{ id: "y-axis-1", type: "linear", position: "left" }] }, showLines: !1, tooltips: { callbacks: { title: function () { return "" }, label: function (t) { return "(" + t.xLabel + ", " + t.yLabel + ")" } } } }), e.exports = function (t) { t.controllers.scatter = t.controllers.line } }, { 25: 25 }], 22: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { animation: { duration: 1e3, easing: "easeOutQuart", onProgress: o.noop, onComplete: o.noop } }), e.exports = function (t) { t.Animation = a.extend({ chart: null, currentStep: 0, numSteps: 60, easing: "", render: null, onAnimationProgress: null, onAnimationComplete: null }), t.animationService = { frameDuration: 17, animations: [], dropFrames: 0, request: null, addAnimation: function (t, e, n, i) { var a, o, r = this.animations; for (e.chart = t, i || (t.animating = !0), a = 0, o = r.length; a < o; ++a)if (r[a].chart === t) return void (r[a] = e); r.push(e), 1 === r.length && this.requestAnimationFrame() }, cancelAnimation: function (t) { var e = o.findIndex(this.animations, function (e) { return e.chart === t }); -1 !== e && (this.animations.splice(e, 1), t.animating = !1) }, requestAnimationFrame: function () { var t = this; null === t.request && (t.request = o.requestAnimFrame.call(window, function () { t.request = null, t.startDigest() })) }, startDigest: function () { var t = this, e = Date.now(), n = 0; t.dropFrames > 1 && (n = Math.floor(t.dropFrames), t.dropFrames = t.dropFrames % 1), t.advance(1 + n); var i = Date.now(); t.dropFrames += (i - e) / t.frameDuration, t.animations.length > 0 && t.requestAnimationFrame() }, advance: function (t) { for (var e, n, i = this.animations, a = 0; a < i.length;)n = (e = i[a]).chart, e.currentStep = (e.currentStep || 0) + t, e.currentStep = Math.min(e.currentStep, e.numSteps), o.callback(e.render, [n, e], n), o.callback(e.onAnimationProgress, [e], n), e.currentStep >= e.numSteps ? (o.callback(e.onAnimationComplete, [e], n), n.animating = !1, i.splice(a, 1)) : ++a } }, Object.defineProperty(t.Animation.prototype, "animationObject", { get: function () { return this } }), Object.defineProperty(t.Animation.prototype, "chartInstance", { get: function () { return this.chart }, set: function (t) { this.chart = t } }) } }, { 25: 25, 26: 26, 45: 45 }], 23: [function (t, e, n) { "use strict"; var i = t(25), a = t(45), o = t(28), r = t(48); e.exports = function (t) { function e(t) { var e = (t = t || {}).data = t.data || {}; return e.datasets = e.datasets || [], e.labels = e.labels || [], t.options = a.configMerge(i.global, i[t.type], t.options || {}), t } function n(t) { var e = t.options; e.scale ? t.scale.options = e.scale : e.scales && e.scales.xAxes.concat(e.scales.yAxes).forEach(function (e) { t.scales[e.id].options = e }), t.tooltip._options = e.tooltips } function l(t) { return "top" === t || "bottom" === t } var s = t.plugins; t.types = {}, t.instances = {}, t.controllers = {}, a.extend(t.prototype, { construct: function (n, i) { var o = this; i = e(i); var l = r.acquireContext(n, i), s = l && l.canvas, u = s && s.height, d = s && s.width; o.id = a.uid(), o.ctx = l, o.canvas = s, o.config = i, o.width = d, o.height = u, o.aspectRatio = u ? d / u : null, o.options = i.options, o._bufferedRender = !1, o.chart = o, o.controller = o, t.instances[o.id] = o, Object.defineProperty(o, "data", { get: function () { return o.config.data }, set: function (t) { o.config.data = t } }), l && s ? (o.initialize(), o.update()) : console.error("Failed to create chart: can't acquire context from the given item") }, initialize: function () { var t = this; return s.notify(t, "beforeInit"), a.retinaScale(t, t.options.devicePixelRatio), t.bindEvents(), t.options.responsive && t.resize(!0), t.ensureScalesHaveIDs(), t.buildScales(), t.initToolTip(), s.notify(t, "afterInit"), t }, clear: function () { return a.canvas.clear(this), this }, stop: function () { return t.animationService.cancelAnimation(this), this }, resize: function (t) { var e = this, n = e.options, i = e.canvas, o = n.maintainAspectRatio && e.aspectRatio || null, r = Math.max(0, Math.floor(a.getMaximumWidth(i))), l = Math.max(0, Math.floor(o ? r / o : a.getMaximumHeight(i))); if ((e.width !== r || e.height !== l) && (i.width = e.width = r, i.height = e.height = l, i.style.width = r + "px", i.style.height = l + "px", a.retinaScale(e, n.devicePixelRatio), !t)) { var u = { width: r, height: l }; s.notify(e, "resize", [u]), e.options.onResize && e.options.onResize(e, u), e.stop(), e.update(e.options.responsiveAnimationDuration) } }, ensureScalesHaveIDs: function () { var t = this.options, e = t.scales || {}, n = t.scale; a.each(e.xAxes, function (t, e) { t.id = t.id || "x-axis-" + e }), a.each(e.yAxes, function (t, e) { t.id = t.id || "y-axis-" + e }), n && (n.id = n.id || "scale") }, buildScales: function () { var e = this, n = e.options, i = e.scales = {}, o = []; n.scales && (o = o.concat((n.scales.xAxes || []).map(function (t) { return { options: t, dtype: "category", dposition: "bottom" } }), (n.scales.yAxes || []).map(function (t) { return { options: t, dtype: "linear", dposition: "left" } }))), n.scale && o.push({ options: n.scale, dtype: "radialLinear", isDefault: !0, dposition: "chartArea" }), a.each(o, function (n) { var o = n.options, r = a.valueOrDefault(o.type, n.dtype), s = t.scaleService.getScaleConstructor(r); if (s) { l(o.position) !== l(n.dposition) && (o.position = n.dposition); var u = new s({ id: o.id, options: o, ctx: e.ctx, chart: e }); i[u.id] = u, u.mergeTicksOptions(), n.isDefault && (e.scale = u) } }), t.scaleService.addScalesToLayout(this) }, buildOrUpdateControllers: function () { var e = this, n = [], i = []; return a.each(e.data.datasets, function (a, o) { var r = e.getDatasetMeta(o), l = a.type || e.config.type; if (r.type && r.type !== l && (e.destroyDatasetMeta(o), r = e.getDatasetMeta(o)), r.type = l, n.push(r.type), r.controller) r.controller.updateIndex(o); else { var s = t.controllers[r.type]; if (void 0 === s) throw new Error('"' + r.type + '" is not a chart type.'); r.controller = new s(e, o), i.push(r.controller) } }, e), i }, resetElements: function () { var t = this; a.each(t.data.datasets, function (e, n) { t.getDatasetMeta(n).controller.reset() }, t) }, reset: function () { this.resetElements(), this.tooltip.initialize() }, update: function (t) { var e = this; if (t && "object" == typeof t || (t = { duration: t, lazy: arguments[1] }), n(e), !1 !== s.notify(e, "beforeUpdate")) { e.tooltip._data = e.data; var i = e.buildOrUpdateControllers(); a.each(e.data.datasets, function (t, n) { e.getDatasetMeta(n).controller.buildOrUpdateElements() }, e), e.updateLayout(), a.each(i, function (t) { t.reset() }), e.updateDatasets(), s.notify(e, "afterUpdate"), e._bufferedRender ? e._bufferedRequest = { duration: t.duration, easing: t.easing, lazy: t.lazy } : e.render(t) } }, updateLayout: function () { var e = this; !1 !== s.notify(e, "beforeLayout") && (t.layoutService.update(this, this.width, this.height), s.notify(e, "afterScaleUpdate"), s.notify(e, "afterLayout")) }, updateDatasets: function () { var t = this; if (!1 !== s.notify(t, "beforeDatasetsUpdate")) { for (var e = 0, n = t.data.datasets.length; e < n; ++e)t.updateDataset(e); s.notify(t, "afterDatasetsUpdate") } }, updateDataset: function (t) { var e = this, n = e.getDatasetMeta(t), i = { meta: n, index: t }; !1 !== s.notify(e, "beforeDatasetUpdate", [i]) && (n.controller.update(), s.notify(e, "afterDatasetUpdate", [i])) }, render: function (e) { var n = this; e && "object" == typeof e || (e = { duration: e, lazy: arguments[1] }); var i = e.duration, o = e.lazy; if (!1 !== s.notify(n, "beforeRender")) { var r = n.options.animation, l = function (t) { s.notify(n, "afterRender"), a.callback(r && r.onComplete, [t], n) }; if (r && (void 0 !== i && 0 !== i || void 0 === i && 0 !== r.duration)) { var u = new t.Animation({ numSteps: (i || r.duration) / 16.66, easing: e.easing || r.easing, render: function (t, e) { var n = a.easing.effects[e.easing], i = e.currentStep, o = i / e.numSteps; t.draw(n(o), o, i) }, onAnimationProgress: r.onProgress, onAnimationComplete: l }); t.animationService.addAnimation(n, u, i, o) } else n.draw(), l(new t.Animation({ numSteps: 0, chart: n })); return n } }, draw: function (t) { var e = this; e.clear(), a.isNullOrUndef(t) && (t = 1), e.transition(t), !1 !== s.notify(e, "beforeDraw", [t]) && (a.each(e.boxes, function (t) { t.draw(e.chartArea) }, e), e.scale && e.scale.draw(), e.drawDatasets(t), e.tooltip.draw(), s.notify(e, "afterDraw", [t])) }, transition: function (t) { for (var e = this, n = 0, i = (e.data.datasets || []).length; n < i; ++n)e.isDatasetVisible(n) && e.getDatasetMeta(n).controller.transition(t); e.tooltip.transition(t) }, drawDatasets: function (t) { var e = this; if (!1 !== s.notify(e, "beforeDatasetsDraw", [t])) { for (var n = (e.data.datasets || []).length - 1; n >= 0; --n)e.isDatasetVisible(n) && e.drawDataset(n, t); s.notify(e, "afterDatasetsDraw", [t]) } }, drawDataset: function (t, e) { var n = this, i = n.getDatasetMeta(t), a = { meta: i, index: t, easingValue: e }; !1 !== s.notify(n, "beforeDatasetDraw", [a]) && (i.controller.draw(e), s.notify(n, "afterDatasetDraw", [a])) }, getElementAtEvent: function (t) { return o.modes.single(this, t) }, getElementsAtEvent: function (t) { return o.modes.label(this, t, { intersect: !0 }) }, getElementsAtXAxis: function (t) { return o.modes["x-axis"](this, t, { intersect: !0 }) }, getElementsAtEventForMode: function (t, e, n) { var i = o.modes[e]; return "function" == typeof i ? i(this, t, n) : [] }, getDatasetAtEvent: function (t) { return o.modes.dataset(this, t, { intersect: !0 }) }, getDatasetMeta: function (t) { var e = this, n = e.data.datasets[t]; n._meta || (n._meta = {}); var i = n._meta[e.id]; return i || (i = n._meta[e.id] = { type: null, data: [], dataset: null, controller: null, hidden: null, xAxisID: null, yAxisID: null }), i }, getVisibleDatasetCount: function () { for (var t = 0, e = 0, n = this.data.datasets.length; e < n; ++e)this.isDatasetVisible(e) && t++; return t }, isDatasetVisible: function (t) { var e = this.getDatasetMeta(t); return "boolean" == typeof e.hidden ? !e.hidden : !this.data.datasets[t].hidden }, generateLegend: function () { return this.options.legendCallback(this) }, destroyDatasetMeta: function (t) { var e = this.id, n = this.data.datasets[t], i = n._meta && n._meta[e]; i && (i.controller.destroy(), delete n._meta[e]) }, destroy: function () { var e, n, i = this, o = i.canvas; for (i.stop(), e = 0, n = i.data.datasets.length; e < n; ++e)i.destroyDatasetMeta(e); o && (i.unbindEvents(), a.canvas.clear(i), r.releaseContext(i.ctx), i.canvas = null, i.ctx = null), s.notify(i, "destroy"), delete t.instances[i.id] }, toBase64Image: function () { return this.canvas.toDataURL.apply(this.canvas, arguments) }, initToolTip: function () { var e = this; e.tooltip = new t.Tooltip({ _chart: e, _chartInstance: e, _data: e.data, _options: e.options.tooltips }, e) }, bindEvents: function () { var t = this, e = t._listeners = {}, n = function () { t.eventHandler.apply(t, arguments) }; a.each(t.options.events, function (i) { r.addEventListener(t, i, n), e[i] = n }), t.options.responsive && (n = function () { t.resize() }, r.addEventListener(t, "resize", n), e.resize = n) }, unbindEvents: function () { var t = this, e = t._listeners; e && (delete t._listeners, a.each(e, function (e, n) { r.removeEventListener(t, n, e) })) }, updateHoverStyle: function (t, e, n) { var i, a, o, r = n ? "setHoverStyle" : "removeHoverStyle"; for (a = 0, o = t.length; a < o; ++a)(i = t[a]) && this.getDatasetMeta(i._datasetIndex).controller[r](i) }, eventHandler: function (t) { var e = this, n = e.tooltip; if (!1 !== s.notify(e, "beforeEvent", [t])) { e._bufferedRender = !0, e._bufferedRequest = null; var i = e.handleEvent(t); i |= n && n.handleEvent(t), s.notify(e, "afterEvent", [t]); var a = e._bufferedRequest; return a ? e.render(a) : i && !e.animating && (e.stop(), e.render(e.options.hover.animationDuration, !0)), e._bufferedRender = !1, e._bufferedRequest = null, e } }, handleEvent: function (t) { var e = this, n = e.options || {}, i = n.hover, o = !1; return e.lastActive = e.lastActive || [], "mouseout" === t.type ? e.active = [] : e.active = e.getElementsAtEventForMode(t, i.mode, i), a.callback(n.onHover || n.hover.onHover, [t.native, e.active], e), "mouseup" !== t.type && "click" !== t.type || n.onClick && n.onClick.call(e, t.native, e.active), e.lastActive.length && e.updateHoverStyle(e.lastActive, i.mode, !1), e.active.length && i.mode && e.updateHoverStyle(e.active, i.mode, !0), o = !a.arrayEquals(e.active, e.lastActive), e.lastActive = e.active, o } }), t.Controller = t } }, { 25: 25, 28: 28, 45: 45, 48: 48 }], 24: [function (t, e, n) { "use strict"; var i = t(45); e.exports = function (t) { function e(t, e) { t._chartjs ? t._chartjs.listeners.push(e) : (Object.defineProperty(t, "_chartjs", { configurable: !0, enumerable: !1, value: { listeners: [e] } }), a.forEach(function (e) { var n = "onData" + e.charAt(0).toUpperCase() + e.slice(1), a = t[e]; Object.defineProperty(t, e, { configurable: !0, enumerable: !1, value: function () { var e = Array.prototype.slice.call(arguments), o = a.apply(this, e); return i.each(t._chartjs.listeners, function (t) { "function" == typeof t[n] && t[n].apply(t, e) }), o } }) })) } function n(t, e) { var n = t._chartjs; if (n) { var i = n.listeners, o = i.indexOf(e); -1 !== o && i.splice(o, 1), i.length > 0 || (a.forEach(function (e) { delete t[e] }), delete t._chartjs) } } var a = ["push", "pop", "shift", "splice", "unshift"]; t.DatasetController = function (t, e) { this.initialize(t, e) }, i.extend(t.DatasetController.prototype, { datasetElementType: null, dataElementType: null, initialize: function (t, e) { var n = this; n.chart = t, n.index = e, n.linkScales(), n.addElements() }, updateIndex: function (t) { this.index = t }, linkScales: function () { var t = this, e = t.getMeta(), n = t.getDataset(); null === e.xAxisID && (e.xAxisID = n.xAxisID || t.chart.options.scales.xAxes[0].id), null === e.yAxisID && (e.yAxisID = n.yAxisID || t.chart.options.scales.yAxes[0].id) }, getDataset: function () { return this.chart.data.datasets[this.index] }, getMeta: function () { return this.chart.getDatasetMeta(this.index) }, getScaleForId: function (t) { return this.chart.scales[t] }, reset: function () { this.update(!0) }, destroy: function () { this._data && n(this._data, this) }, createMetaDataset: function () { var t = this, e = t.datasetElementType; return e && new e({ _chart: t.chart, _datasetIndex: t.index }) }, createMetaData: function (t) { var e = this, n = e.dataElementType; return n && new n({ _chart: e.chart, _datasetIndex: e.index, _index: t }) }, addElements: function () { var t, e, n = this, i = n.getMeta(), a = n.getDataset().data || [], o = i.data; for (t = 0, e = a.length; t < e; ++t)o[t] = o[t] || n.createMetaData(t); i.dataset = i.dataset || n.createMetaDataset() }, addElementAndReset: function (t) { var e = this.createMetaData(t); this.getMeta().data.splice(t, 0, e), this.updateElement(e, t, !0) }, buildOrUpdateElements: function () { var t = this, i = t.getDataset(), a = i.data || (i.data = []); t._data !== a && (t._data && n(t._data, t), e(a, t), t._data = a), t.resyncElements() }, update: i.noop, transition: function (t) { for (var e = this.getMeta(), n = e.data || [], i = n.length, a = 0; a < i; ++a)n[a].transition(t); e.dataset && e.dataset.transition(t) }, draw: function () { var t = this.getMeta(), e = t.data || [], n = e.length, i = 0; for (t.dataset && t.dataset.draw(); i < n; ++i)e[i].draw() }, removeHoverStyle: function (t, e) { var n = this.chart.data.datasets[t._datasetIndex], a = t._index, o = t.custom || {}, r = i.valueAtIndexOrDefault, l = t._model; l.backgroundColor = o.backgroundColor ? o.backgroundColor : r(n.backgroundColor, a, e.backgroundColor), l.borderColor = o.borderColor ? o.borderColor : r(n.borderColor, a, e.borderColor), l.borderWidth = o.borderWidth ? o.borderWidth : r(n.borderWidth, a, e.borderWidth) }, setHoverStyle: function (t) { var e = this.chart.data.datasets[t._datasetIndex], n = t._index, a = t.custom || {}, o = i.valueAtIndexOrDefault, r = i.getHoverColor, l = t._model; l.backgroundColor = a.hoverBackgroundColor ? a.hoverBackgroundColor : o(e.hoverBackgroundColor, n, r(l.backgroundColor)), l.borderColor = a.hoverBorderColor ? a.hoverBorderColor : o(e.hoverBorderColor, n, r(l.borderColor)), l.borderWidth = a.hoverBorderWidth ? a.hoverBorderWidth : o(e.hoverBorderWidth, n, l.borderWidth) }, resyncElements: function () { var t = this, e = t.getMeta(), n = t.getDataset().data, i = e.data.length, a = n.length; a < i ? e.data.splice(a, i - a) : a > i && t.insertElements(i, a - i) }, insertElements: function (t, e) { for (var n = 0; n < e; ++n)this.addElementAndReset(t + n) }, onDataPush: function () { this.insertElements(this.getDataset().data.length - 1, arguments.length) }, onDataPop: function () { this.getMeta().data.pop() }, onDataShift: function () { this.getMeta().data.shift() }, onDataSplice: function (t, e) { this.getMeta().data.splice(t, e), this.insertElements(t, arguments.length - 2) }, onDataUnshift: function () { this.insertElements(0, arguments.length) } }), t.DatasetController.extend = i.inherits } }, { 45: 45 }], 25: [function (t, e, n) { "use strict"; var i = t(45); e.exports = { _set: function (t, e) { return i.merge(this[t] || (this[t] = {}), e) } } }, { 45: 45 }], 26: [function (t, e, n) { "use strict"; function i(t, e, n, i) { var o, r, l, s, u, d, c, h, f, g = Object.keys(n); for (o = 0, r = g.length; o < r; ++o)if (l = g[o], d = n[l], e.hasOwnProperty(l) || (e[l] = d), (s = e[l]) !== d && "_" !== l[0]) { if (t.hasOwnProperty(l) || (t[l] = s), u = t[l], (c = typeof d) === typeof u) if ("string" === c) { if ((h = a(u)).valid && (f = a(d)).valid) { e[l] = f.mix(h, i).rgbString(); continue } } else if ("number" === c && isFinite(u) && isFinite(d)) { e[l] = u + (d - u) * i; continue } e[l] = d } } var a = t(3), o = t(45), r = function (t) { o.extend(this, t), this.initialize.apply(this, arguments) }; o.extend(r.prototype, { initialize: function () { this.hidden = !1 }, pivot: function () { var t = this; return t._view || (t._view = o.clone(t._model)), t._start = {}, t }, transition: function (t) { var e = this, n = e._model, a = e._start, o = e._view; return n && 1 !== t ? (o || (o = e._view = {}), a || (a = e._start = {}), i(a, o, n, t), e) : (e._view = n, e._start = null, e) }, tooltipPosition: function () { return { x: this._model.x, y: this._model.y } }, hasValue: function () { return o.isNumber(this._model.x) && o.isNumber(this._model.y) } }), r.extend = o.inherits, e.exports = r }, { 3: 3, 45: 45 }], 27: [function (t, e, n) { "use strict"; var i = t(3), a = t(25), o = t(45); e.exports = function (t) { function e(t, e, n) { var i; return "string" == typeof t ? (i = parseInt(t, 10), -1 !== t.indexOf("%") && (i = i / 100 * e.parentNode[n])) : i = t, i } function n(t) { return void 0 !== t && null !== t && "none" !== t } function r(t, i, a) { var o = document.defaultView, r = t.parentNode, l = o.getComputedStyle(t)[i], s = o.getComputedStyle(r)[i], u = n(l), d = n(s), c = Number.POSITIVE_INFINITY; return u || d ? Math.min(u ? e(l, t, a) : c, d ? e(s, r, a) : c) : "none" } o.extend = function (t) { for (var e = 1, n = arguments.length; e < n; e++)o.each(arguments[e], function (e, n) { t[n] = e }); return t }, o.configMerge = function () { return o.merge(o.clone(arguments[0]), [].slice.call(arguments, 1), { merger: function (e, n, i, a) { var r = n[e] || {}, l = i[e]; "scales" === e ? n[e] = o.scaleMerge(r, l) : "scale" === e ? n[e] = o.merge(r, [t.scaleService.getScaleDefaults(l.type), l]) : o._merger(e, n, i, a) } }) }, o.scaleMerge = function () { return o.merge(o.clone(arguments[0]), [].slice.call(arguments, 1), { merger: function (e, n, i, a) { if ("xAxes" === e || "yAxes" === e) { var r, l, s, u = i[e].length; for (n[e] || (n[e] = []), r = 0; r < u; ++r)s = i[e][r], l = o.valueOrDefault(s.type, "xAxes" === e ? "category" : "linear"), r >= n[e].length && n[e].push({}), !n[e][r].type || s.type && s.type !== n[e][r].type ? o.merge(n[e][r], [t.scaleService.getScaleDefaults(l), s]) : o.merge(n[e][r], s) } else o._merger(e, n, i, a) } }) }, o.where = function (t, e) { if (o.isArray(t) && Array.prototype.filter) return t.filter(e); var n = []; return o.each(t, function (t) { e(t) && n.push(t) }), n }, o.findIndex = Array.prototype.findIndex ? function (t, e, n) { return t.findIndex(e, n) } : function (t, e, n) { n = void 0 === n ? t : n; for (var i = 0, a = t.length; i < a; ++i)if (e.call(n, t[i], i, t)) return i; return -1 }, o.findNextWhere = function (t, e, n) { o.isNullOrUndef(n) && (n = -1); for (var i = n + 1; i < t.length; i++) { var a = t[i]; if (e(a)) return a } }, o.findPreviousWhere = function (t, e, n) { o.isNullOrUndef(n) && (n = t.length); for (var i = n - 1; i >= 0; i--) { var a = t[i]; if (e(a)) return a } }, o.inherits = function (t) { var e = this, n = t && t.hasOwnProperty("constructor") ? t.constructor : function () { return e.apply(this, arguments) }, i = function () { this.constructor = n }; return i.prototype = e.prototype, n.prototype = new i, n.extend = o.inherits, t && o.extend(n.prototype, t), n.__super__ = e.prototype, n }, o.isNumber = function (t) { return !isNaN(parseFloat(t)) && isFinite(t) }, o.almostEquals = function (t, e, n) { return Math.abs(t - e) < n }, o.almostWhole = function (t, e) { var n = Math.round(t); return n - e < t && n + e > t }, o.max = function (t) { return t.reduce(function (t, e) { return isNaN(e) ? t : Math.max(t, e) }, Number.NEGATIVE_INFINITY) }, o.min = function (t) { return t.reduce(function (t, e) { return isNaN(e) ? t : Math.min(t, e) }, Number.POSITIVE_INFINITY) }, o.sign = Math.sign ? function (t) { return Math.sign(t) } : function (t) { return 0 == (t = +t) || isNaN(t) ? t : t > 0 ? 1 : -1 }, o.log10 = Math.log10 ? function (t) { return Math.log10(t) } : function (t) { return Math.log(t) / Math.LN10 }, o.toRadians = function (t) { return t * (Math.PI / 180) }, o.toDegrees = function (t) { return t * (180 / Math.PI) }, o.getAngleFromPoint = function (t, e) { var n = e.x - t.x, i = e.y - t.y, a = Math.sqrt(n * n + i * i), o = Math.atan2(i, n); return o < -.5 * Math.PI && (o += 2 * Math.PI), { angle: o, distance: a } }, o.distanceBetweenPoints = function (t, e) { return Math.sqrt(Math.pow(e.x - t.x, 2) + Math.pow(e.y - t.y, 2)) }, o.aliasPixel = function (t) { return t % 2 == 0 ? 0 : .5 }, o.splineCurve = function (t, e, n, i) { var a = t.skip ? e : t, o = e, r = n.skip ? e : n, l = Math.sqrt(Math.pow(o.x - a.x, 2) + Math.pow(o.y - a.y, 2)), s = Math.sqrt(Math.pow(r.x - o.x, 2) + Math.pow(r.y - o.y, 2)), u = l / (l + s), d = s / (l + s), c = i * (u = isNaN(u) ? 0 : u), h = i * (d = isNaN(d) ? 0 : d); return { previous: { x: o.x - c * (r.x - a.x), y: o.y - c * (r.y - a.y) }, next: { x: o.x + h * (r.x - a.x), y: o.y + h * (r.y - a.y) } } }, o.EPSILON = Number.EPSILON || 1e-14, o.splineCurveMonotone = function (t) { var e, n, i, a, r = (t || []).map(function (t) { return { model: t._model, deltaK: 0, mK: 0 } }), l = r.length; for (e = 0; e < l; ++e)if (!(i = r[e]).model.skip) { if (n = e > 0 ? r[e - 1] : null, (a = e < l - 1 ? r[e + 1] : null) && !a.model.skip) { var s = a.model.x - i.model.x; i.deltaK = 0 !== s ? (a.model.y - i.model.y) / s : 0 } !n || n.model.skip ? i.mK = i.deltaK : !a || a.model.skip ? i.mK = n.deltaK : this.sign(n.deltaK) !== this.sign(i.deltaK) ? i.mK = 0 : i.mK = (n.deltaK + i.deltaK) / 2 } var u, d, c, h; for (e = 0; e < l - 1; ++e)i = r[e], a = r[e + 1], i.model.skip || a.model.skip || (o.almostEquals(i.deltaK, 0, this.EPSILON) ? i.mK = a.mK = 0 : (u = i.mK / i.deltaK, d = a.mK / i.deltaK, (h = Math.pow(u, 2) + Math.pow(d, 2)) <= 9 || (c = 3 / Math.sqrt(h), i.mK = u * c * i.deltaK, a.mK = d * c * i.deltaK))); var f; for (e = 0; e < l; ++e)(i = r[e]).model.skip || (n = e > 0 ? r[e - 1] : null, a = e < l - 1 ? r[e + 1] : null, n && !n.model.skip && (f = (i.model.x - n.model.x) / 3, i.model.controlPointPreviousX = i.model.x - f, i.model.controlPointPreviousY = i.model.y - f * i.mK), a && !a.model.skip && (f = (a.model.x - i.model.x) / 3, i.model.controlPointNextX = i.model.x + f, i.model.controlPointNextY = i.model.y + f * i.mK)) }, o.nextItem = function (t, e, n) { return n ? e >= t.length - 1 ? t[0] : t[e + 1] : e >= t.length - 1 ? t[t.length - 1] : t[e + 1] }, o.previousItem = function (t, e, n) { return n ? e <= 0 ? t[t.length - 1] : t[e - 1] : e <= 0 ? t[0] : t[e - 1] }, o.niceNum = function (t, e) { var n = Math.floor(o.log10(t)), i = t / Math.pow(10, n); return (e ? i < 1.5 ? 1 : i < 3 ? 2 : i < 7 ? 5 : 10 : i <= 1 ? 1 : i <= 2 ? 2 : i <= 5 ? 5 : 10) * Math.pow(10, n) }, o.requestAnimFrame = "undefined" == typeof window ? function (t) { t() } : window.requestAnimationFrame || window.webkitRequestAnimationFrame || window.mozRequestAnimationFrame || window.oRequestAnimationFrame || window.msRequestAnimationFrame || function (t) { return window.setTimeout(t, 1e3 / 60) }, o.getRelativePosition = function (t, e) { var n, i, a = t.originalEvent || t, r = t.currentTarget || t.srcElement, l = r.getBoundingClientRect(), s = a.touches; s && s.length > 0 ? (n = s[0].clientX, i = s[0].clientY) : (n = a.clientX, i = a.clientY); var u = parseFloat(o.getStyle(r, "padding-left")), d = parseFloat(o.getStyle(r, "padding-top")), c = parseFloat(o.getStyle(r, "padding-right")), h = parseFloat(o.getStyle(r, "padding-bottom")), f = l.right - l.left - u - c, g = l.bottom - l.top - d - h; return n = Math.round((n - l.left - u) / f * r.width / e.currentDevicePixelRatio), i = Math.round((i - l.top - d) / g * r.height / e.currentDevicePixelRatio), { x: n, y: i } }, o.getConstraintWidth = function (t) { return r(t, "max-width", "clientWidth") }, o.getConstraintHeight = function (t) { return r(t, "max-height", "clientHeight") }, o.getMaximumWidth = function (t) { var e = t.parentNode; if (!e) return t.clientWidth; var n = parseInt(o.getStyle(e, "padding-left"), 10), i = parseInt(o.getStyle(e, "padding-right"), 10), a = e.clientWidth - n - i, r = o.getConstraintWidth(t); return isNaN(r) ? a : Math.min(a, r) }, o.getMaximumHeight = function (t) { var e = t.parentNode; if (!e) return t.clientHeight; var n = parseInt(o.getStyle(e, "padding-top"), 10), i = parseInt(o.getStyle(e, "padding-bottom"), 10), a = e.clientHeight - n - i, r = o.getConstraintHeight(t); return isNaN(r) ? a : Math.min(a, r) }, o.getStyle = function (t, e) { return t.currentStyle ? t.currentStyle[e] : document.defaultView.getComputedStyle(t, null).getPropertyValue(e) }, o.retinaScale = function (t, e) { var n = t.currentDevicePixelRatio = e || window.devicePixelRatio || 1; if (1 !== n) { var i = t.canvas, a = t.height, o = t.width; i.height = a * n, i.width = o * n, t.ctx.scale(n, n), i.style.height = a + "px", i.style.width = o + "px" } }, o.fontString = function (t, e, n) { return e + " " + t + "px " + n }, o.longestText = function (t, e, n, i) { var a = (i = i || {}).data = i.data || {}, r = i.garbageCollect = i.garbageCollect || []; i.font !== e && (a = i.data = {}, r = i.garbageCollect = [], i.font = e), t.font = e; var l = 0; o.each(n, function (e) { void 0 !== e && null !== e && !0 !== o.isArray(e) ? l = o.measureText(t, a, r, l, e) : o.isArray(e) && o.each(e, function (e) { void 0 === e || null === e || o.isArray(e) || (l = o.measureText(t, a, r, l, e)) }) }); var s = r.length / 2; if (s > n.length) { for (var u = 0; u < s; u++)delete a[r[u]]; r.splice(0, s) } return l }, o.measureText = function (t, e, n, i, a) { var o = e[a]; return o || (o = e[a] = t.measureText(a).width, n.push(a)), o > i && (i = o), i }, o.numberOfLabelLines = function (t) { var e = 1; return o.each(t, function (t) { o.isArray(t) && t.length > e && (e = t.length) }), e }, o.color = i ? function (t) { return t instanceof CanvasGradient && (t = a.global.defaultColor), i(t) } : function (t) { return console.error("Color.js not found!"), t }, o.getHoverColor = function (t) { return t instanceof CanvasPattern ? t : o.color(t).saturate(.5).darken(.1).rgbString() } } }, { 25: 25, 3: 3, 45: 45 }], 28: [function (t, e, n) { "use strict"; function i(t, e) { return t.native ? { x: t.x, y: t.y } : u.getRelativePosition(t, e) } function a(t, e) { var n, i, a, o, r; for (i = 0, o = t.data.datasets.length; i < o; ++i)if (t.isDatasetVisible(i)) for (a = 0, r = (n = t.getDatasetMeta(i)).data.length; a < r; ++a) { var l = n.data[a]; l._view.skip || e(l) } } function o(t, e) { var n = []; return a(t, function (t) { t.inRange(e.x, e.y) && n.push(t) }), n } function r(t, e, n, i) { var o = Number.POSITIVE_INFINITY, r = []; return a(t, function (t) { if (!n || t.inRange(e.x, e.y)) { var a = t.getCenterPoint(), l = i(e, a); l < o ? (r = [t], o = l) : l === o && r.push(t) } }), r } function l(t) { var e = -1 !== t.indexOf("x"), n = -1 !== t.indexOf("y"); return function (t, i) { var a = e ? Math.abs(t.x - i.x) : 0, o = n ? Math.abs(t.y - i.y) : 0; return Math.sqrt(Math.pow(a, 2) + Math.pow(o, 2)) } } function s(t, e, n) { var a = i(e, t); n.axis = n.axis || "x"; var s = l(n.axis), u = n.intersect ? o(t, a) : r(t, a, !1, s), d = []; return u.length ? (t.data.datasets.forEach(function (e, n) { if (t.isDatasetVisible(n)) { var i = t.getDatasetMeta(n).data[u[0]._index]; i && !i._view.skip && d.push(i) } }), d) : [] } var u = t(45); e.exports = { modes: { single: function (t, e) { var n = i(e, t), o = []; return a(t, function (t) { if (t.inRange(n.x, n.y)) return o.push(t), o }), o.slice(0, 1) }, label: s, index: s, dataset: function (t, e, n) { var a = i(e, t); n.axis = n.axis || "xy"; var s = l(n.axis), u = n.intersect ? o(t, a) : r(t, a, !1, s); return u.length > 0 && (u = t.getDatasetMeta(u[0]._datasetIndex).data), u }, "x-axis": function (t, e) { return s(t, e, { intersect: !0 }) }, point: function (t, e) { return o(t, i(e, t)) }, nearest: function (t, e, n) { var a = i(e, t); n.axis = n.axis || "xy"; var o = l(n.axis), s = r(t, a, n.intersect, o); return s.length > 1 && s.sort(function (t, e) { var n = t.getArea() - e.getArea(); return 0 === n && (n = t._datasetIndex - e._datasetIndex), n }), s.slice(0, 1) }, x: function (t, e, n) { var o = i(e, t), r = [], l = !1; return a(t, function (t) { t.inXRange(o.x) && r.push(t), t.inRange(o.x, o.y) && (l = !0) }), n.intersect && !l && (r = []), r }, y: function (t, e, n) { var o = i(e, t), r = [], l = !1; return a(t, function (t) { t.inYRange(o.y) && r.push(t), t.inRange(o.x, o.y) && (l = !0) }), n.intersect && !l && (r = []), r } } } }, { 45: 45 }], 29: [function (t, e, n) { "use strict"; t(25)._set("global", { responsive: !0, responsiveAnimationDuration: 0, maintainAspectRatio: !0, events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"], hover: { onHover: null, mode: "nearest", intersect: !0, animationDuration: 400 }, onClick: null, defaultColor: "rgba(0,0,0,0.1)", defaultFontColor: "#666", defaultFontFamily: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif", defaultFontSize: 12, defaultFontStyle: "normal", showLines: !0, elements: {}, layout: { padding: { top: 0, right: 0, bottom: 0, left: 0 } } }), e.exports = function () { var t = function (t, e) { return this.construct(t, e), this }; return t.Chart = t, t } }, { 25: 25 }], 30: [function (t, e, n) { "use strict"; var i = t(45); e.exports = function (t) { function e(t, e) { return i.where(t, function (t) { return t.position === e }) } function n(t, e) { t.forEach(function (t, e) { return t._tmpIndex_ = e, t }), t.sort(function (t, n) { var i = e ? n : t, a = e ? t : n; return i.weight === a.weight ? i._tmpIndex_ - a._tmpIndex_ : i.weight - a.weight }), t.forEach(function (t) { delete t._tmpIndex_ }) } t.layoutService = { defaults: {}, addBox: function (t, e) { t.boxes || (t.boxes = []), e.fullWidth = e.fullWidth || !1, e.position = e.position || "top", e.weight = e.weight || 0, t.boxes.push(e) }, removeBox: function (t, e) { var n = t.boxes ? t.boxes.indexOf(e) : -1; -1 !== n && t.boxes.splice(n, 1) }, configure: function (t, e, n) { for (var i, a = ["fullWidth", "position", "weight"], o = a.length, r = 0; r < o; ++r)i = a[r], n.hasOwnProperty(i) && (e[i] = n[i]) }, update: function (t, a, o) { function r(t) { var e = i.findNextWhere(_, function (e) { return e.box === t }); if (e) if (t.isHorizontal()) { var n = { left: Math.max(T, D), right: Math.max(F, I), top: 0, bottom: 0 }; t.update(t.fullWidth ? x : S, y / 2, n) } else t.update(e.minSize.width, C) } function l(t) { t.isHorizontal() ? (t.left = t.fullWidth ? d : T, t.right = t.fullWidth ? a - c : T + S, t.top = V, t.bottom = V + t.height, V = t.bottom) : (t.left = N, t.right = N + t.width, t.top = O, t.bottom = O + C, N = t.right) } if (t) { var s = t.options.layout || {}, u = i.options.toPadding(s.padding), d = u.left, c = u.right, h = u.top, f = u.bottom, g = e(t.boxes, "left"), p = e(t.boxes, "right"), v = e(t.boxes, "top"), m = e(t.boxes, "bottom"), b = e(t.boxes, "chartArea"); n(g, !0), n(p, !1), n(v, !0), n(m, !1); var x = a - d - c, y = o - h - f, k = y / 2, w = (a - x / 2) / (g.length + p.length), M = (o - k) / (v.length + m.length), S = x, C = y, _ = []; i.each(g.concat(p, v, m), function (t) { var e, n = t.isHorizontal(); n ? (e = t.update(t.fullWidth ? x : S, M), C -= e.height) : (e = t.update(w, k), S -= e.width), _.push({ horizontal: n, minSize: e, box: t }) }); var D = 0, I = 0, P = 0, A = 0; i.each(v.concat(m), function (t) { if (t.getPadding) { var e = t.getPadding(); D = Math.max(D, e.left), I = Math.max(I, e.right) } }), i.each(g.concat(p), function (t) { if (t.getPadding) { var e = t.getPadding(); P = Math.max(P, e.top), A = Math.max(A, e.bottom) } }); var T = d, F = c, O = h, R = f; i.each(g.concat(p), r), i.each(g, function (t) { T += t.width }), i.each(p, function (t) { F += t.width }), i.each(v.concat(m), r), i.each(v, function (t) { O += t.height }), i.each(m, function (t) { R += t.height }), i.each(g.concat(p), function (t) { var e = i.findNextWhere(_, function (e) { return e.box === t }), n = { left: 0, right: 0, top: O, bottom: R }; e && t.update(e.minSize.width, C, n) }), T = d, F = c, O = h, R = f, i.each(g, function (t) { T += t.width }), i.each(p, function (t) { F += t.width }), i.each(v, function (t) { O += t.height }), i.each(m, function (t) { R += t.height }); var L = Math.max(D - T, 0); T += L, F += Math.max(I - F, 0); var z = Math.max(P - O, 0); O += z, R += Math.max(A - R, 0); var B = o - O - R, W = a - T - F; W === S && B === C || (i.each(g, function (t) { t.height = B }), i.each(p, function (t) { t.height = B }), i.each(v, function (t) { t.fullWidth || (t.width = W) }), i.each(m, function (t) { t.fullWidth || (t.width = W) }), C = B, S = W); var N = d + L, V = h + z; i.each(g.concat(v), l), N += S, V += C, i.each(p, l), i.each(m, l), t.chartArea = { left: T, top: O, right: T + S, bottom: O + C }, i.each(b, function (e) { e.left = t.chartArea.left, e.top = t.chartArea.top, e.right = t.chartArea.right, e.bottom = t.chartArea.bottom, e.update(S, C) }) } } } } }, { 45: 45 }], 31: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { plugins: {} }), e.exports = function (t) { t.plugins = { _plugins: [], _cacheId: 0, register: function (t) { var e = this._plugins;[].concat(t).forEach(function (t) { -1 === e.indexOf(t) && e.push(t) }), this._cacheId++ }, unregister: function (t) { var e = this._plugins;[].concat(t).forEach(function (t) { var n = e.indexOf(t); -1 !== n && e.splice(n, 1) }), this._cacheId++ }, clear: function () { this._plugins = [], this._cacheId++ }, count: function () { return this._plugins.length }, getAll: function () { return this._plugins }, notify: function (t, e, n) { var i, a, o, r, l, s = this.descriptors(t), u = s.length; for (i = 0; i < u; ++i)if (a = s[i], o = a.plugin, "function" == typeof (l = o[e]) && ((r = [t].concat(n || [])).push(a.options), !1 === l.apply(o, r))) return !1; return !0 }, descriptors: function (t) { var e = t._plugins || (t._plugins = {}); if (e.id === this._cacheId) return e.descriptors; var n = [], a = [], r = t && t.config || {}, l = r.options && r.options.plugins || {}; return this._plugins.concat(r.plugins || []).forEach(function (t) { if (-1 === n.indexOf(t)) { var e = t.id, r = l[e]; !1 !== r && (!0 === r && (r = o.clone(i.global.plugins[e])), n.push(t), a.push({ plugin: t, options: r || {} })) } }), e.descriptors = a, e.id = this._cacheId, a } }, t.pluginService = t.plugins, t.PluginBase = a.extend({}) } }, { 25: 25, 26: 26, 45: 45 }], 32: [function (t, e, n) { "use strict"; function i(t) { var e, n, i = []; for (e = 0, n = t.length; e < n; ++e)i.push(t[e].label); return i } function a(t, e, n) { var i = t.getPixelForTick(e); return n && (i -= 0 === e ? (t.getPixelForTick(1) - i) / 2 : (i - t.getPixelForTick(e - 1)) / 2), i } var o = t(25), r = t(26), l = t(45), s = t(34); o._set("scale", { display: !0, position: "left", offset: !1, gridLines: { display: !0, color: "rgba(0, 0, 0, 0.1)", lineWidth: 1, drawBorder: !0, drawOnChartArea: !0, drawTicks: !0, tickMarkLength: 10, zeroLineWidth: 1, zeroLineColor: "rgba(0,0,0,0.25)", zeroLineBorderDash: [], zeroLineBorderDashOffset: 0, offsetGridLines: !1, borderDash: [], borderDashOffset: 0 }, scaleLabel: { display: !1, labelString: "", lineHeight: 1.2, padding: { top: 4, bottom: 4 } }, ticks: { beginAtZero: !1, minRotation: 0, maxRotation: 50, mirror: !1, padding: 0, reverse: !1, display: !0, autoSkip: !0, autoSkipPadding: 0, labelOffset: 0, callback: s.formatters.values, minor: {}, major: {} } }), e.exports = function (t) { function e(t, e, n) { return l.isArray(e) ? l.longestText(t, n, e) : t.measureText(e).width } function n(t) { var e = l.valueOrDefault, n = o.global, i = e(t.fontSize, n.defaultFontSize), a = e(t.fontStyle, n.defaultFontStyle), r = e(t.fontFamily, n.defaultFontFamily); return { size: i, style: a, family: r, font: l.fontString(i, a, r) } } function s(t) { return l.options.toLineHeight(l.valueOrDefault(t.lineHeight, 1.2), l.valueOrDefault(t.fontSize, o.global.defaultFontSize)) } t.Scale = r.extend({ getPadding: function () { var t = this; return { left: t.paddingLeft || 0, top: t.paddingTop || 0, right: t.paddingRight || 0, bottom: t.paddingBottom || 0 } }, getTicks: function () { return this._ticks }, mergeTicksOptions: function () { var t = this.options.ticks; !1 === t.minor && (t.minor = { display: !1 }), !1 === t.major && (t.major = { display: !1 }); for (var e in t) "major" !== e && "minor" !== e && (void 0 === t.minor[e] && (t.minor[e] = t[e]), void 0 === t.major[e] && (t.major[e] = t[e])) }, beforeUpdate: function () { l.callback(this.options.beforeUpdate, [this]) }, update: function (t, e, n) { var i, a, o, r, s, u, d = this; for (d.beforeUpdate(), d.maxWidth = t, d.maxHeight = e, d.margins = l.extend({ left: 0, right: 0, top: 0, bottom: 0 }, n), d.longestTextCache = d.longestTextCache || {}, d.beforeSetDimensions(), d.setDimensions(), d.afterSetDimensions(), d.beforeDataLimits(), d.determineDataLimits(), d.afterDataLimits(), d.beforeBuildTicks(), s = d.buildTicks() || [], d.afterBuildTicks(), d.beforeTickToLabelConversion(), o = d.convertTicksToLabels(s) || d.ticks, d.afterTickToLabelConversion(), d.ticks = o, i = 0, a = o.length; i < a; ++i)r = o[i], (u = s[i]) ? u.label = r : s.push(u = { label: r, major: !1 }); return d._ticks = s, d.beforeCalculateTickRotation(), d.calculateTickRotation(), d.afterCalculateTickRotation(), d.beforeFit(), d.fit(), d.afterFit(), d.afterUpdate(), d.minSize }, afterUpdate: function () { l.callback(this.options.afterUpdate, [this]) }, beforeSetDimensions: function () { l.callback(this.options.beforeSetDimensions, [this]) }, setDimensions: function () { var t = this; t.isHorizontal() ? (t.width = t.maxWidth, t.left = 0, t.right = t.width) : (t.height = t.maxHeight, t.top = 0, t.bottom = t.height), t.paddingLeft = 0, t.paddingTop = 0, t.paddingRight = 0, t.paddingBottom = 0 }, afterSetDimensions: function () { l.callback(this.options.afterSetDimensions, [this]) }, beforeDataLimits: function () { l.callback(this.options.beforeDataLimits, [this]) }, determineDataLimits: l.noop, afterDataLimits: function () { l.callback(this.options.afterDataLimits, [this]) }, beforeBuildTicks: function () { l.callback(this.options.beforeBuildTicks, [this]) }, buildTicks: l.noop, afterBuildTicks: function () { l.callback(this.options.afterBuildTicks, [this]) }, beforeTickToLabelConversion: function () { l.callback(this.options.beforeTickToLabelConversion, [this]) }, convertTicksToLabels: function () { var t = this, e = t.options.ticks; t.ticks = t.ticks.map(e.userCallback || e.callback, this) }, afterTickToLabelConversion: function () { l.callback(this.options.afterTickToLabelConversion, [this]) }, beforeCalculateTickRotation: function () { l.callback(this.options.beforeCalculateTickRotation, [this]) }, calculateTickRotation: function () { var t = this, e = t.ctx, a = t.options.ticks, o = i(t._ticks), r = n(a); e.font = r.font; var s = a.minRotation || 0; if (o.length && t.options.display && t.isHorizontal()) for (var u, d = l.longestText(e, r.font, o, t.longestTextCache), c = d, h = t.getPixelForTick(1) - t.getPixelForTick(0) - 6; c > h && s < a.maxRotation;) { var f = l.toRadians(s); if (u = Math.cos(f), Math.sin(f) * d > t.maxHeight) { s--; break } s++, c = u * d } t.labelRotation = s }, afterCalculateTickRotation: function () { l.callback(this.options.afterCalculateTickRotation, [this]) }, beforeFit: function () { l.callback(this.options.beforeFit, [this]) }, fit: function () { var t = this, a = t.minSize = { width: 0, height: 0 }, o = i(t._ticks), r = t.options, u = r.ticks, d = r.scaleLabel, c = r.gridLines, h = r.display, f = t.isHorizontal(), g = n(u), p = r.gridLines.tickMarkLength; if (a.width = f ? t.isFullWidth() ? t.maxWidth - t.margins.left - t.margins.right : t.maxWidth : h && c.drawTicks ? p : 0, a.height = f ? h && c.drawTicks ? p : 0 : t.maxHeight, d.display && h) { var v = s(d) + l.options.toPadding(d.padding).height; f ? a.height += v : a.width += v } if (u.display && h) { var m = l.longestText(t.ctx, g.font, o, t.longestTextCache), b = l.numberOfLabelLines(o), x = .5 * g.size, y = t.options.ticks.padding; if (f) { t.longestLabelWidth = m; var k = l.toRadians(t.labelRotation), w = Math.cos(k), M = Math.sin(k) * m + g.size * b + x * (b - 1) + x; a.height = Math.min(t.maxHeight, a.height + M + y), t.ctx.font = g.font; var S = e(t.ctx, o[0], g.font), C = e(t.ctx, o[o.length - 1], g.font); 0 !== t.labelRotation ? (t.paddingLeft = "bottom" === r.position ? w * S + 3 : w * x + 3, t.paddingRight = "bottom" === r.position ? w * x + 3 : w * C + 3) : (t.paddingLeft = S / 2 + 3, t.paddingRight = C / 2 + 3) } else u.mirror ? m = 0 : m += y + x, a.width = Math.min(t.maxWidth, a.width + m), t.paddingTop = g.size / 2, t.paddingBottom = g.size / 2 } t.handleMargins(), t.width = a.width, t.height = a.height }, handleMargins: function () { var t = this; t.margins && (t.paddingLeft = Math.max(t.paddingLeft - t.margins.left, 0), t.paddingTop = Math.max(t.paddingTop - t.margins.top, 0), t.paddingRight = Math.max(t.paddingRight - t.margins.right, 0), t.paddingBottom = Math.max(t.paddingBottom - t.margins.bottom, 0)) }, afterFit: function () { l.callback(this.options.afterFit, [this]) }, isHorizontal: function () { return "top" === this.options.position || "bottom" === this.options.position }, isFullWidth: function () { return this.options.fullWidth }, getRightValue: function (t) { if (l.isNullOrUndef(t)) return NaN; if ("number" == typeof t && !isFinite(t)) return NaN; if (t) if (this.isHorizontal()) { if (void 0 !== t.x) return this.getRightValue(t.x) } else if (void 0 !== t.y) return this.getRightValue(t.y); return t }, getLabelForIndex: l.noop, getPixelForValue: l.noop, getValueForPixel: l.noop, getPixelForTick: function (t) { var e = this, n = e.options.offset; if (e.isHorizontal()) { var i = (e.width - (e.paddingLeft + e.paddingRight)) / Math.max(e._ticks.length - (n ? 0 : 1), 1), a = i * t + e.paddingLeft; n && (a += i / 2); var o = e.left + Math.round(a); return o += e.isFullWidth() ? e.margins.left : 0 } var r = e.height - (e.paddingTop + e.paddingBottom); return e.top + t * (r / (e._ticks.length - 1)) }, getPixelForDecimal: function (t) { var e = this; if (e.isHorizontal()) { var n = (e.width - (e.paddingLeft + e.paddingRight)) * t + e.paddingLeft, i = e.left + Math.round(n); return i += e.isFullWidth() ? e.margins.left : 0 } return e.top + t * e.height }, getBasePixel: function () { return this.getPixelForValue(this.getBaseValue()) }, getBaseValue: function () { var t = this, e = t.min, n = t.max; return t.beginAtZero ? 0 : e < 0 && n < 0 ? n : e > 0 && n > 0 ? e : 0 }, _autoSkip: function (t) { var e, n, i, a, o = this, r = o.isHorizontal(), s = o.options.ticks.minor, u = t.length, d = l.toRadians(o.labelRotation), c = Math.cos(d), h = o.longestLabelWidth * c, f = []; for (s.maxTicksLimit && (a = s.maxTicksLimit), r && (e = !1, (h + s.autoSkipPadding) * u > o.width - (o.paddingLeft + o.paddingRight) && (e = 1 + Math.floor((h + s.autoSkipPadding) * u / (o.width - (o.paddingLeft + o.paddingRight)))), a && u > a && (e = Math.max(e, Math.floor(u / a)))), n = 0; n < u; n++)i = t[n], ((e > 1 && n % e > 0 || n % e == 0 && n + e >= u) && n !== u - 1 || l.isNullOrUndef(i.label)) && delete i.label, f.push(i); return f }, draw: function (t) { var e = this, i = e.options; if (i.display) { var r = e.ctx, u = o.global, d = i.ticks.minor, c = i.ticks.major || d, h = i.gridLines, f = i.scaleLabel, g = 0 !== e.labelRotation, p = e.isHorizontal(), v = d.autoSkip ? e._autoSkip(e.getTicks()) : e.getTicks(), m = l.valueOrDefault(d.fontColor, u.defaultFontColor), b = n(d), x = l.valueOrDefault(c.fontColor, u.defaultFontColor), y = n(c), k = h.drawTicks ? h.tickMarkLength : 0, w = l.valueOrDefault(f.fontColor, u.defaultFontColor), M = n(f), S = l.options.toPadding(f.padding), C = l.toRadians(e.labelRotation), _ = [], D = "right" === i.position ? e.left : e.right - k, I = "right" === i.position ? e.left + k : e.right, P = "bottom" === i.position ? e.top : e.bottom - k, A = "bottom" === i.position ? e.top + k : e.bottom; if (l.each(v, function (n, o) { if (void 0 !== n.label) { var r, s, c, f, m = n.label; o === e.zeroLineIndex && i.offset === h.offsetGridLines ? (r = h.zeroLineWidth, s = h.zeroLineColor, c = h.zeroLineBorderDash, f = h.zeroLineBorderDashOffset) : (r = l.valueAtIndexOrDefault(h.lineWidth, o), s = l.valueAtIndexOrDefault(h.color, o), c = l.valueOrDefault(h.borderDash, u.borderDash), f = l.valueOrDefault(h.borderDashOffset, u.borderDashOffset)); var b, x, y, w, M, S, T, F, O, R, L = "middle", z = "middle", B = d.padding; if (p) { var W = k + B; "bottom" === i.position ? (z = g ? "middle" : "top", L = g ? "right" : "center", R = e.top + W) : (z = g ? "middle" : "bottom", L = g ? "left" : "center", R = e.bottom - W); var N = a(e, o, h.offsetGridLines && v.length > 1); N < e.left && (s = "rgba(0,0,0,0)"), N += l.aliasPixel(r), O = e.getPixelForTick(o) + d.labelOffset, b = y = M = T = N, x = P, w = A, S = t.top, F = t.bottom } else { var V, E = "left" === i.position; d.mirror ? (L = E ? "left" : "right", V = B) : (L = E ? "right" : "left", V = k + B), O = E ? e.right - V : e.left + V; var H = a(e, o, h.offsetGridLines && v.length > 1); H < e.top && (s = "rgba(0,0,0,0)"), H += l.aliasPixel(r), R = e.getPixelForTick(o) + d.labelOffset, b = D, y = I, M = t.left, T = t.right, x = w = S = F = H } _.push({ tx1: b, ty1: x, tx2: y, ty2: w, x1: M, y1: S, x2: T, y2: F, labelX: O, labelY: R, glWidth: r, glColor: s, glBorderDash: c, glBorderDashOffset: f, rotation: -1 * C, label: m, major: n.major, textBaseline: z, textAlign: L }) } }), l.each(_, function (t) { if (h.display && (r.save(), r.lineWidth = t.glWidth, r.strokeStyle = t.glColor, r.setLineDash && (r.setLineDash(t.glBorderDash), r.lineDashOffset = t.glBorderDashOffset), r.beginPath(), h.drawTicks && (r.moveTo(t.tx1, t.ty1), r.lineTo(t.tx2, t.ty2)), h.drawOnChartArea && (r.moveTo(t.x1, t.y1), r.lineTo(t.x2, t.y2)), r.stroke(), r.restore()), d.display) { r.save(), r.translate(t.labelX, t.labelY), r.rotate(t.rotation), r.font = t.major ? y.font : b.font, r.fillStyle = t.major ? x : m, r.textBaseline = t.textBaseline, r.textAlign = t.textAlign; var e = t.label; if (l.isArray(e)) for (var n = 0, i = 0; n < e.length; ++n)r.fillText("" + e[n], 0, i), i += 1.5 * b.size; else r.fillText(e, 0, 0); r.restore() } }), f.display) { var T, F, O = 0, R = s(f) / 2; if (p) T = e.left + (e.right - e.left) / 2, F = "bottom" === i.position ? e.bottom - R - S.bottom : e.top + R + S.top; else { var L = "left" === i.position; T = L ? e.left + R + S.top : e.right - R - S.top, F = e.top + (e.bottom - e.top) / 2, O = L ? -.5 * Math.PI : .5 * Math.PI } r.save(), r.translate(T, F), r.rotate(O), r.textAlign = "center", r.textBaseline = "middle", r.fillStyle = w, r.font = M.font, r.fillText(f.labelString, 0, 0), r.restore() } if (h.drawBorder) { r.lineWidth = l.valueAtIndexOrDefault(h.lineWidth, 0), r.strokeStyle = l.valueAtIndexOrDefault(h.color, 0); var z = e.left, B = e.right, W = e.top, N = e.bottom, V = l.aliasPixel(r.lineWidth); p ? (W = N = "top" === i.position ? e.bottom : e.top, W += V, N += V) : (z = B = "left" === i.position ? e.right : e.left, z += V, B += V), r.beginPath(), r.moveTo(z, W), r.lineTo(B, N), r.stroke() } } } }) } }, { 25: 25, 26: 26, 34: 34, 45: 45 }], 33: [function (t, e, n) { "use strict"; var i = t(25), a = t(45); e.exports = function (t) { t.scaleService = { constructors: {}, defaults: {}, registerScaleType: function (t, e, n) { this.constructors[t] = e, this.defaults[t] = a.clone(n) }, getScaleConstructor: function (t) { return this.constructors.hasOwnProperty(t) ? this.constructors[t] : void 0 }, getScaleDefaults: function (t) { return this.defaults.hasOwnProperty(t) ? a.merge({}, [i.scale, this.defaults[t]]) : {} }, updateScaleDefaults: function (t, e) { var n = this; n.defaults.hasOwnProperty(t) && (n.defaults[t] = a.extend(n.defaults[t], e)) }, addScalesToLayout: function (e) { a.each(e.scales, function (n) { n.fullWidth = n.options.fullWidth, n.position = n.options.position, n.weight = n.options.weight, t.layoutService.addBox(e, n) }) } } } }, { 25: 25, 45: 45 }], 34: [function (t, e, n) { "use strict"; var i = t(45); e.exports = { generators: { linear: function (t, e) { var n, a = []; if (t.stepSize && t.stepSize > 0) n = t.stepSize; else { var o = i.niceNum(e.max - e.min, !1); n = i.niceNum(o / (t.maxTicks - 1), !0) } var r = Math.floor(e.min / n) * n, l = Math.ceil(e.max / n) * n; t.min && t.max && t.stepSize && i.almostWhole((t.max - t.min) / t.stepSize, n / 1e3) && (r = t.min, l = t.max); var s = (l - r) / n; s = i.almostEquals(s, Math.round(s), n / 1e3) ? Math.round(s) : Math.ceil(s), a.push(void 0 !== t.min ? t.min : r); for (var u = 1; u < s; ++u)a.push(r + u * n); return a.push(void 0 !== t.max ? t.max : l), a }, logarithmic: function (t, e) { var n, a, o = [], r = i.valueOrDefault, l = r(t.min, Math.pow(10, Math.floor(i.log10(e.min)))), s = Math.floor(i.log10(e.max)), u = Math.ceil(e.max / Math.pow(10, s)); 0 === l ? (n = Math.floor(i.log10(e.minNotZero)), a = Math.floor(e.minNotZero / Math.pow(10, n)), o.push(l), l = a * Math.pow(10, n)) : (n = Math.floor(i.log10(l)), a = Math.floor(l / Math.pow(10, n))); do { o.push(l), 10 === ++a && (a = 1, ++n), l = a * Math.pow(10, n) } while (n < s || n === s && a < u); var d = r(t.max, l); return o.push(d), o } }, formatters: { values: function (t) { return i.isArray(t) ? t : "" + t }, linear: function (t, e, n) { var a = n.length > 3 ? n[2] - n[1] : n[1] - n[0]; Math.abs(a) > 1 && t !== Math.floor(t) && (a = t - Math.floor(t)); var o = i.log10(Math.abs(a)), r = ""; if (0 !== t) { var l = -1 * Math.floor(o); l = Math.max(Math.min(l, 20), 0), r = t.toFixed(l) } else r = "0"; return r }, logarithmic: function (t, e, n) { var a = t / Math.pow(10, Math.floor(i.log10(t))); return 0 === t ? "0" : 1 === a || 2 === a || 5 === a || 0 === e || e === n.length - 1 ? t.toExponential() : "" } } } }, { 45: 45 }], 35: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { tooltips: { enabled: !0, custom: null, mode: "nearest", position: "average", intersect: !0, backgroundColor: "rgba(0,0,0,0.8)", titleFontStyle: "bold", titleSpacing: 2, titleMarginBottom: 6, titleFontColor: "#fff", titleAlign: "left", bodySpacing: 2, bodyFontColor: "#fff", bodyAlign: "left", footerFontStyle: "bold", footerSpacing: 2, footerMarginTop: 6, footerFontColor: "#fff", footerAlign: "left", yPadding: 6, xPadding: 6, caretPadding: 2, caretSize: 5, cornerRadius: 6, multiKeyBackground: "#fff", displayColors: !0, borderColor: "rgba(0,0,0,0)", borderWidth: 0, callbacks: { beforeTitle: o.noop, title: function (t, e) { var n = "", i = e.labels, a = i ? i.length : 0; if (t.length > 0) { var o = t[0]; o.xLabel ? n = o.xLabel : a > 0 && o.index < a && (n = i[o.index]) } return n }, afterTitle: o.noop, beforeBody: o.noop, beforeLabel: o.noop, label: function (t, e) { var n = e.datasets[t.datasetIndex].label || ""; return n && (n += ": "), n += t.yLabel }, labelColor: function (t, e) { var n = e.getDatasetMeta(t.datasetIndex).data[t.index]._view; return { borderColor: n.borderColor, backgroundColor: n.backgroundColor } }, labelTextColor: function () { return this._options.bodyFontColor }, afterLabel: o.noop, afterBody: o.noop, beforeFooter: o.noop, footer: o.noop, afterFooter: o.noop } } }), e.exports = function (t) { function e(t, e) { var n = o.color(t); return n.alpha(e * n.alpha()).rgbaString() } function n(t, e) { return e && (o.isArray(e) ? Array.prototype.push.apply(t, e) : t.push(e)), t } function r(t) { var e = t._xScale, n = t._yScale || t._scale, i = t._index, a = t._datasetIndex; return { xLabel: e ? e.getLabelForIndex(i, a) : "", yLabel: n ? n.getLabelForIndex(i, a) : "", index: i, datasetIndex: a, x: t._model.x, y: t._model.y } } function l(t) { var e = i.global, n = o.valueOrDefault; return { xPadding: t.xPadding, yPadding: t.yPadding, xAlign: t.xAlign, yAlign: t.yAlign, bodyFontColor: t.bodyFontColor, _bodyFontFamily: n(t.bodyFontFamily, e.defaultFontFamily), _bodyFontStyle: n(t.bodyFontStyle, e.defaultFontStyle), _bodyAlign: t.bodyAlign, bodyFontSize: n(t.bodyFontSize, e.defaultFontSize), bodySpacing: t.bodySpacing, titleFontColor: t.titleFontColor, _titleFontFamily: n(t.titleFontFamily, e.defaultFontFamily), _titleFontStyle: n(t.titleFontStyle, e.defaultFontStyle), titleFontSize: n(t.titleFontSize, e.defaultFontSize), _titleAlign: t.titleAlign, titleSpacing: t.titleSpacing, titleMarginBottom: t.titleMarginBottom, footerFontColor: t.footerFontColor, _footerFontFamily: n(t.footerFontFamily, e.defaultFontFamily), _footerFontStyle: n(t.footerFontStyle, e.defaultFontStyle), footerFontSize: n(t.footerFontSize, e.defaultFontSize), _footerAlign: t.footerAlign, footerSpacing: t.footerSpacing, footerMarginTop: t.footerMarginTop, caretSize: t.caretSize, cornerRadius: t.cornerRadius, backgroundColor: t.backgroundColor, opacity: 0, legendColorBackground: t.multiKeyBackground, displayColors: t.displayColors, borderColor: t.borderColor, borderWidth: t.borderWidth } } function s(t, e) { var n = t._chart.ctx, i = 2 * e.yPadding, a = 0, r = e.body, l = r.reduce(function (t, e) { return t + e.before.length + e.lines.length + e.after.length }, 0); l += e.beforeBody.length + e.afterBody.length; var s = e.title.length, u = e.footer.length, d = e.titleFontSize, c = e.bodyFontSize, h = e.footerFontSize; i += s * d, i += s ? (s - 1) * e.titleSpacing : 0, i += s ? e.titleMarginBottom : 0, i += l * c, i += l ? (l - 1) * e.bodySpacing : 0, i += u ? e.footerMarginTop : 0, i += u * h, i += u ? (u - 1) * e.footerSpacing : 0; var f = 0, g = function (t) { a = Math.max(a, n.measureText(t).width + f) }; return n.font = o.fontString(d, e._titleFontStyle, e._titleFontFamily), o.each(e.title, g), n.font = o.fontString(c, e._bodyFontStyle, e._bodyFontFamily), o.each(e.beforeBody.concat(e.afterBody), g), f = e.displayColors ? c + 2 : 0, o.each(r, function (t) { o.each(t.before, g), o.each(t.lines, g), o.each(t.after, g) }), f = 0, n.font = o.fontString(h, e._footerFontStyle, e._footerFontFamily), o.each(e.footer, g), a += 2 * e.xPadding, { width: a, height: i } } function u(t, e) { var n = t._model, i = t._chart, a = t._chart.chartArea, o = "center", r = "center"; n.y < e.height ? r = "top" : n.y > i.height - e.height && (r = "bottom"); var l, s, u, d, c, h = (a.left + a.right) / 2, f = (a.top + a.bottom) / 2; "center" === r ? (l = function (t) { return t <= h }, s = function (t) { return t > h }) : (l = function (t) { return t <= e.width / 2 }, s = function (t) { return t >= i.width - e.width / 2 }), u = function (t) { return t + e.width > i.width }, d = function (t) { return t - e.width < 0 }, c = function (t) { return t <= f ? "top" : "bottom" }, l(n.x) ? (o = "left", u(n.x) && (o = "center", r = c(n.y))) : s(n.x) && (o = "right", d(n.x) && (o = "center", r = c(n.y))); var g = t._options; return { xAlign: g.xAlign ? g.xAlign : o, yAlign: g.yAlign ? g.yAlign : r } } function d(t, e, n) { var i = t.x, a = t.y, o = t.caretSize, r = t.caretPadding, l = t.cornerRadius, s = n.xAlign, u = n.yAlign, d = o + r, c = l + r; return "right" === s ? i -= e.width : "center" === s && (i -= e.width / 2), "top" === u ? a += d : a -= "bottom" === u ? e.height + d : e.height / 2, "center" === u ? "left" === s ? i += d : "right" === s && (i -= d) : "left" === s ? i -= c : "right" === s && (i += c), { x: i, y: a } } t.Tooltip = a.extend({ initialize: function () { this._model = l(this._options) }, getTitle: function () { var t = this, e = t._options.callbacks, i = e.beforeTitle.apply(t, arguments), a = e.title.apply(t, arguments), o = e.afterTitle.apply(t, arguments), r = []; return r = n(r, i), r = n(r, a), r = n(r, o) }, getBeforeBody: function () { var t = this._options.callbacks.beforeBody.apply(this, arguments); return o.isArray(t) ? t : void 0 !== t ? [t] : [] }, getBody: function (t, e) { var i = this, a = i._options.callbacks, r = []; return o.each(t, function (t) { var o = { before: [], lines: [], after: [] }; n(o.before, a.beforeLabel.call(i, t, e)), n(o.lines, a.label.call(i, t, e)), n(o.after, a.afterLabel.call(i, t, e)), r.push(o) }), r }, getAfterBody: function () { var t = this._options.callbacks.afterBody.apply(this, arguments); return o.isArray(t) ? t : void 0 !== t ? [t] : [] }, getFooter: function () { var t = this, e = t._options.callbacks, i = e.beforeFooter.apply(t, arguments), a = e.footer.apply(t, arguments), o = e.afterFooter.apply(t, arguments), r = []; return r = n(r, i), r = n(r, a), r = n(r, o) }, update: function (e) { var n, i, a = this, c = a._options, h = a._model, f = a._model = l(c), g = a._active, p = a._data, v = { xAlign: h.xAlign, yAlign: h.yAlign }, m = { x: h.x, y: h.y }, b = { width: h.width, height: h.height }, x = { x: h.caretX, y: h.caretY }; if (g.length) { f.opacity = 1; var y = [], k = []; x = t.Tooltip.positioners[c.position](g, a._eventPosition); var w = []; for (n = 0, i = g.length; n < i; ++n)w.push(r(g[n])); c.filter && (w = w.filter(function (t) { return c.filter(t, p) })), c.itemSort && (w = w.sort(function (t, e) { return c.itemSort(t, e, p) })), o.each(w, function (t) { y.push(c.callbacks.labelColor.call(a, t, a._chart)), k.push(c.callbacks.labelTextColor.call(a, t, a._chart)) }), f.title = a.getTitle(w, p), f.beforeBody = a.getBeforeBody(w, p), f.body = a.getBody(w, p), f.afterBody = a.getAfterBody(w, p), f.footer = a.getFooter(w, p), f.x = Math.round(x.x), f.y = Math.round(x.y), f.caretPadding = c.caretPadding, f.labelColors = y, f.labelTextColors = k, f.dataPoints = w, m = d(f, b = s(this, f), v = u(this, b)) } else f.opacity = 0; return f.xAlign = v.xAlign, f.yAlign = v.yAlign, f.x = m.x, f.y = m.y, f.width = b.width, f.height = b.height, f.caretX = x.x, f.caretY = x.y, a._model = f, e && c.custom && c.custom.call(a, f), a }, drawCaret: function (t, e) { var n = this._chart.ctx, i = this._view, a = this.getCaretPosition(t, e, i); n.lineTo(a.x1, a.y1), n.lineTo(a.x2, a.y2), n.lineTo(a.x3, a.y3) }, getCaretPosition: function (t, e, n) { var i, a, o, r, l, s, u = n.caretSize, d = n.cornerRadius, c = n.xAlign, h = n.yAlign, f = t.x, g = t.y, p = e.width, v = e.height; if ("center" === h) l = g + v / 2, "left" === c ? (a = (i = f) - u, o = i, r = l + u, s = l - u) : (a = (i = f + p) + u, o = i, r = l - u, s = l + u); else if ("left" === c ? (i = (a = f + d + u) - u, o = a + u) : "right" === c ? (i = (a = f + p - d - u) - u, o = a + u) : (i = (a = f + p / 2) - u, o = a + u), "top" === h) l = (r = g) - u, s = r; else { l = (r = g + v) + u, s = r; var m = o; o = i, i = m } return { x1: i, x2: a, x3: o, y1: r, y2: l, y3: s } }, drawTitle: function (t, n, i, a) { var r = n.title; if (r.length) { i.textAlign = n._titleAlign, i.textBaseline = "top"; var l = n.titleFontSize, s = n.titleSpacing; i.fillStyle = e(n.titleFontColor, a), i.font = o.fontString(l, n._titleFontStyle, n._titleFontFamily); var u, d; for (u = 0, d = r.length; u < d; ++u)i.fillText(r[u], t.x, t.y), t.y += l + s, u + 1 === r.length && (t.y += n.titleMarginBottom - s) } }, drawBody: function (t, n, i, a) { var r = n.bodyFontSize, l = n.bodySpacing, s = n.body; i.textAlign = n._bodyAlign, i.textBaseline = "top", i.font = o.fontString(r, n._bodyFontStyle, n._bodyFontFamily); var u = 0, d = function (e) { i.fillText(e, t.x + u, t.y), t.y += r + l }; o.each(n.beforeBody, d); var c = n.displayColors; u = c ? r + 2 : 0, o.each(s, function (l, s) { o.each(l.before, d), o.each(l.lines, function (o) { if (c) { i.fillStyle = e(n.legendColorBackground, a), i.fillRect(t.x, t.y, r, r), i.lineWidth = 1, i.strokeStyle = e(n.labelColors[s].borderColor, a), i.strokeRect(t.x, t.y, r, r), i.fillStyle = e(n.labelColors[s].backgroundColor, a), i.fillRect(t.x + 1, t.y + 1, r - 2, r - 2); var l = e(n.labelTextColors[s], a); i.fillStyle = l } d(o) }), o.each(l.after, d) }), u = 0, o.each(n.afterBody, d), t.y -= l }, drawFooter: function (t, n, i, a) { var r = n.footer; r.length && (t.y += n.footerMarginTop, i.textAlign = n._footerAlign, i.textBaseline = "top", i.fillStyle = e(n.footerFontColor, a), i.font = o.fontString(n.footerFontSize, n._footerFontStyle, n._footerFontFamily), o.each(r, function (e) { i.fillText(e, t.x, t.y), t.y += n.footerFontSize + n.footerSpacing })) }, drawBackground: function (t, n, i, a, o) { i.fillStyle = e(n.backgroundColor, o), i.strokeStyle = e(n.borderColor, o), i.lineWidth = n.borderWidth; var r = n.xAlign, l = n.yAlign, s = t.x, u = t.y, d = a.width, c = a.height, h = n.cornerRadius; i.beginPath(), i.moveTo(s + h, u), "top" === l && this.drawCaret(t, a), i.lineTo(s + d - h, u), i.quadraticCurveTo(s + d, u, s + d, u + h), "center" === l && "right" === r && this.drawCaret(t, a), i.lineTo(s + d, u + c - h), i.quadraticCurveTo(s + d, u + c, s + d - h, u + c), "bottom" === l && this.drawCaret(t, a), i.lineTo(s + h, u + c), i.quadraticCurveTo(s, u + c, s, u + c - h), "center" === l && "left" === r && this.drawCaret(t, a), i.lineTo(s, u + h), i.quadraticCurveTo(s, u, s + h, u), i.closePath(), i.fill(), n.borderWidth > 0 && i.stroke() }, draw: function () { var t = this._chart.ctx, e = this._view; if (0 !== e.opacity) { var n = { width: e.width, height: e.height }, i = { x: e.x, y: e.y }, a = Math.abs(e.opacity < .001) ? 0 : e.opacity, o = e.title.length || e.beforeBody.length || e.body.length || e.afterBody.length || e.footer.length; this._options.enabled && o && (this.drawBackground(i, e, t, n, a), i.x += e.xPadding, i.y += e.yPadding, this.drawTitle(i, e, t, a), this.drawBody(i, e, t, a), this.drawFooter(i, e, t, a)) } }, handleEvent: function (t) { var e = this, n = e._options, i = !1; if (e._lastActive = e._lastActive || [], "mouseout" === t.type ? e._active = [] : e._active = e._chart.getElementsAtEventForMode(t, n.mode, n), !(i = !o.arrayEquals(e._active, e._lastActive))) return !1; if (e._lastActive = e._active, n.enabled || n.custom) { e._eventPosition = { x: t.x, y: t.y }; var a = e._model; e.update(!0), e.pivot(), i |= a.x !== e._model.x || a.y !== e._model.y } return i } }), t.Tooltip.positioners = { average: function (t) { if (!t.length) return !1; var e, n, i = 0, a = 0, o = 0; for (e = 0, n = t.length; e < n; ++e) { var r = t[e]; if (r && r.hasValue()) { var l = r.tooltipPosition(); i += l.x, a += l.y, ++o } } return { x: Math.round(i / o), y: Math.round(a / o) } }, nearest: function (t, e) { var n, i, a, r = e.x, l = e.y, s = Number.POSITIVE_INFINITY; for (n = 0, i = t.length; n < i; ++n) { var u = t[n]; if (u && u.hasValue()) { var d = u.getCenterPoint(), c = o.distanceBetweenPoints(e, d); c < s && (s = c, a = u) } } if (a) { var h = a.tooltipPosition(); r = h.x, l = h.y } return { x: r, y: l } } } } }, { 25: 25, 26: 26, 45: 45 }], 36: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { elements: { arc: { backgroundColor: i.global.defaultColor, borderColor: "#fff", borderWidth: 2 } } }), e.exports = a.extend({ inLabelRange: function (t) { var e = this._view; return !!e && Math.pow(t - e.x, 2) < Math.pow(e.radius + e.hoverRadius, 2) }, inRange: function (t, e) { var n = this._view; if (n) { for (var i = o.getAngleFromPoint(n, { x: t, y: e }), a = i.angle, r = i.distance, l = n.startAngle, s = n.endAngle; s < l;)s += 2 * Math.PI; for (; a > s;)a -= 2 * Math.PI; for (; a < l;)a += 2 * Math.PI; var u = a >= l && a <= s, d = r >= n.innerRadius && r <= n.outerRadius; return u && d } return !1 }, getCenterPoint: function () { var t = this._view, e = (t.startAngle + t.endAngle) / 2, n = (t.innerRadius + t.outerRadius) / 2; return { x: t.x + Math.cos(e) * n, y: t.y + Math.sin(e) * n } }, getArea: function () { var t = this._view; return Math.PI * ((t.endAngle - t.startAngle) / (2 * Math.PI)) * (Math.pow(t.outerRadius, 2) - Math.pow(t.innerRadius, 2)) }, tooltipPosition: function () { var t = this._view, e = t.startAngle + (t.endAngle - t.startAngle) / 2, n = (t.outerRadius - t.innerRadius) / 2 + t.innerRadius; return { x: t.x + Math.cos(e) * n, y: t.y + Math.sin(e) * n } }, draw: function () { var t = this._chart.ctx, e = this._view, n = e.startAngle, i = e.endAngle; t.beginPath(), t.arc(e.x, e.y, e.outerRadius, n, i), t.arc(e.x, e.y, e.innerRadius, i, n, !0), t.closePath(), t.strokeStyle = e.borderColor, t.lineWidth = e.borderWidth, t.fillStyle = e.backgroundColor, t.fill(), t.lineJoin = "bevel", e.borderWidth && t.stroke() } }) }, { 25: 25, 26: 26, 45: 45 }], 37: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45), r = i.global; i._set("global", { elements: { line: { tension: .4, backgroundColor: r.defaultColor, borderWidth: 3, borderColor: r.defaultColor, borderCapStyle: "butt", borderDash: [], borderDashOffset: 0, borderJoinStyle: "miter", capBezierPoints: !0, fill: !0 } } }), e.exports = a.extend({ draw: function () { var t, e, n, i, a = this, l = a._view, s = a._chart.ctx, u = l.spanGaps, d = a._children.slice(), c = r.elements.line, h = -1; for (a._loop && d.length && d.push(d[0]), s.save(), s.lineCap = l.borderCapStyle || c.borderCapStyle, s.setLineDash && s.setLineDash(l.borderDash || c.borderDash), s.lineDashOffset = l.borderDashOffset || c.borderDashOffset, s.lineJoin = l.borderJoinStyle || c.borderJoinStyle, s.lineWidth = l.borderWidth || c.borderWidth, s.strokeStyle = l.borderColor || r.defaultColor, s.beginPath(), h = -1, t = 0; t < d.length; ++t)e = d[t], n = o.previousItem(d, t), i = e._view, 0 === t ? i.skip || (s.moveTo(i.x, i.y), h = t) : (n = -1 === h ? n : d[h], i.skip || (h !== t - 1 && !u || -1 === h ? s.moveTo(i.x, i.y) : o.canvas.lineTo(s, n._view, e._view), h = t)); s.stroke(), s.restore() } }) }, { 25: 25, 26: 26, 45: 45 }], 38: [function (t, e, n) { "use strict"; function i(t) { var e = this._view; return !!e && Math.pow(t - e.x, 2) < Math.pow(e.radius + e.hitRadius, 2) } var a = t(25), o = t(26), r = t(45), l = a.global.defaultColor; a._set("global", { elements: { point: { radius: 3, pointStyle: "circle", backgroundColor: l, borderColor: l, borderWidth: 1, hitRadius: 1, hoverRadius: 4, hoverBorderWidth: 1 } } }), e.exports = o.extend({ inRange: function (t, e) { var n = this._view; return !!n && Math.pow(t - n.x, 2) + Math.pow(e - n.y, 2) < Math.pow(n.hitRadius + n.radius, 2) }, inLabelRange: i, inXRange: i, inYRange: function (t) { var e = this._view; return !!e && Math.pow(t - e.y, 2) < Math.pow(e.radius + e.hitRadius, 2) }, getCenterPoint: function () { var t = this._view; return { x: t.x, y: t.y } }, getArea: function () { return Math.PI * Math.pow(this._view.radius, 2) }, tooltipPosition: function () { var t = this._view; return { x: t.x, y: t.y, padding: t.radius + t.borderWidth } }, draw: function (t) { var e = this._view, n = this._model, i = this._chart.ctx, o = e.pointStyle, s = e.radius, u = e.x, d = e.y, c = r.color, h = 0; e.skip || (i.strokeStyle = e.borderColor || l, i.lineWidth = r.valueOrDefault(e.borderWidth, a.global.elements.point.borderWidth), i.fillStyle = e.backgroundColor || l, void 0 !== t && (n.x < t.left || 1.01 * t.right < n.x || n.y < t.top || 1.01 * t.bottom < n.y) && (n.x < t.left ? h = (u - n.x) / (t.left - n.x) : 1.01 * t.right < n.x ? h = (n.x - u) / (n.x - t.right) : n.y < t.top ? h = (d - n.y) / (t.top - n.y) : 1.01 * t.bottom < n.y && (h = (n.y - d) / (n.y - t.bottom)), h = Math.round(100 * h) / 100, i.strokeStyle = c(i.strokeStyle).alpha(h).rgbString(), i.fillStyle = c(i.fillStyle).alpha(h).rgbString()), r.canvas.drawPoint(i, o, s, u, d)) } }) }, { 25: 25, 26: 26, 45: 45 }], 39: [function (t, e, n) { "use strict"; function i(t) { return void 0 !== t._view.width } function a(t) { var e, n, a, o, r = t._view; if (i(t)) { var l = r.width / 2; e = r.x - l, n = r.x + l, a = Math.min(r.y, r.base), o = Math.max(r.y, r.base) } else { var s = r.height / 2; e = Math.min(r.x, r.base), n = Math.max(r.x, r.base), a = r.y - s, o = r.y + s } return { left: e, top: a, right: n, bottom: o } } var o = t(25), r = t(26); o._set("global", { elements: { rectangle: { backgroundColor: o.global.defaultColor, borderColor: o.global.defaultColor, borderSkipped: "bottom", borderWidth: 0 } } }), e.exports = r.extend({ draw: function () { function t(t) { return m[(b + t) % 4] } var e, n, i, a, o, r, l, s = this._chart.ctx, u = this._view, d = u.borderWidth; if (u.horizontal ? (e = u.base, n = u.x, i = u.y - u.height / 2, a = u.y + u.height / 2, o = n > e ? 1 : -1, r = 1, l = u.borderSkipped || "left") : (e = u.x - u.width / 2, n = u.x + u.width / 2, i = u.y, o = 1, r = (a = u.base) > i ? 1 : -1, l = u.borderSkipped || "bottom"), d) { var c = Math.min(Math.abs(e - n), Math.abs(i - a)), h = (d = d > c ? c : d) / 2, f = e + ("left" !== l ? h * o : 0), g = n + ("right" !== l ? -h * o : 0), p = i + ("top" !== l ? h * r : 0), v = a + ("bottom" !== l ? -h * r : 0); f !== g && (i = p, a = v), p !== v && (e = f, n = g) } s.beginPath(), s.fillStyle = u.backgroundColor, s.strokeStyle = u.borderColor, s.lineWidth = d; var m = [[e, a], [e, i], [n, i], [n, a]], b = ["bottom", "left", "top", "right"].indexOf(l, 0); -1 === b && (b = 0); var x = t(0); s.moveTo(x[0], x[1]); for (var y = 1; y < 4; y++)x = t(y), s.lineTo(x[0], x[1]); s.fill(), d && s.stroke() }, height: function () { var t = this._view; return t.base - t.y }, inRange: function (t, e) { var n = !1; if (this._view) { var i = a(this); n = t >= i.left && t <= i.right && e >= i.top && e <= i.bottom } return n }, inLabelRange: function (t, e) { var n = this; if (!n._view) return !1; var o = a(n); return i(n) ? t >= o.left && t <= o.right : e >= o.top && e <= o.bottom }, inXRange: function (t) { var e = a(this); return t >= e.left && t <= e.right }, inYRange: function (t) { var e = a(this); return t >= e.top && t <= e.bottom }, getCenterPoint: function () { var t, e, n = this._view; return i(this) ? (t = n.x, e = (n.y + n.base) / 2) : (t = (n.x + n.base) / 2, e = n.y), { x: t, y: e } }, getArea: function () { var t = this._view; return t.width * Math.abs(t.y - t.base) }, tooltipPosition: function () { var t = this._view; return { x: t.x, y: t.y } } }) }, { 25: 25, 26: 26 }], 40: [function (t, e, n) { "use strict"; e.exports = {}, e.exports.Arc = t(36), e.exports.Line = t(37), e.exports.Point = t(38), e.exports.Rectangle = t(39) }, { 36: 36, 37: 37, 38: 38, 39: 39 }], 41: [function (t, e, n) { "use strict"; var i = t(42), n = e.exports = { clear: function (t) { t.ctx.clearRect(0, 0, t.width, t.height) }, roundedRect: function (t, e, n, i, a, o) { if (o) { var r = Math.min(o, i / 2), l = Math.min(o, a / 2); t.moveTo(e + r, n), t.lineTo(e + i - r, n), t.quadraticCurveTo(e + i, n, e + i, n + l), t.lineTo(e + i, n + a - l), t.quadraticCurveTo(e + i, n + a, e + i - r, n + a), t.lineTo(e + r, n + a), t.quadraticCurveTo(e, n + a, e, n + a - l), t.lineTo(e, n + l), t.quadraticCurveTo(e, n, e + r, n) } else t.rect(e, n, i, a) }, drawPoint: function (t, e, n, i, a) { var o, r, l, s, u, d; if ("object" != typeof e || "[object HTMLImageElement]" !== (o = e.toString()) && "[object HTMLCanvasElement]" !== o) { if (!(isNaN(n) || n <= 0)) { switch (e) { default: t.beginPath(), t.arc(i, a, n, 0, 2 * Math.PI), t.closePath(), t.fill(); break; case "triangle": t.beginPath(), u = (r = 3 * n / Math.sqrt(3)) * Math.sqrt(3) / 2, t.moveTo(i - r / 2, a + u / 3), t.lineTo(i + r / 2, a + u / 3), t.lineTo(i, a - 2 * u / 3), t.closePath(), t.fill(); break; case "rect": d = 1 / Math.SQRT2 * n, t.beginPath(), t.fillRect(i - d, a - d, 2 * d, 2 * d), t.strokeRect(i - d, a - d, 2 * d, 2 * d); break; case "rectRounded": var c = n / Math.SQRT2, h = i - c, f = a - c, g = Math.SQRT2 * n; t.beginPath(), this.roundedRect(t, h, f, g, g, n / 2), t.closePath(), t.fill(); break; case "rectRot": d = 1 / Math.SQRT2 * n, t.beginPath(), t.moveTo(i - d, a), t.lineTo(i, a + d), t.lineTo(i + d, a), t.lineTo(i, a - d), t.closePath(), t.fill(); break; case "cross": t.beginPath(), t.moveTo(i, a + n), t.lineTo(i, a - n), t.moveTo(i - n, a), t.lineTo(i + n, a), t.closePath(); break; case "crossRot": t.beginPath(), l = Math.cos(Math.PI / 4) * n, s = Math.sin(Math.PI / 4) * n, t.moveTo(i - l, a - s), t.lineTo(i + l, a + s), t.moveTo(i - l, a + s), t.lineTo(i + l, a - s), t.closePath(); break; case "star": t.beginPath(), t.moveTo(i, a + n), t.lineTo(i, a - n), t.moveTo(i - n, a), t.lineTo(i + n, a), l = Math.cos(Math.PI / 4) * n, s = Math.sin(Math.PI / 4) * n, t.moveTo(i - l, a - s), t.lineTo(i + l, a + s), t.moveTo(i - l, a + s), t.lineTo(i + l, a - s), t.closePath(); break; case "line": t.beginPath(), t.moveTo(i - n, a), t.lineTo(i + n, a), t.closePath(); break; case "dash": t.beginPath(), t.moveTo(i, a), t.lineTo(i + n, a), t.closePath() }t.stroke() } } else t.drawImage(e, i - e.width / 2, a - e.height / 2, e.width, e.height) }, clipArea: function (t, e) { t.save(), t.beginPath(), t.rect(e.left, e.top, e.right - e.left, e.bottom - e.top), t.clip() }, unclipArea: function (t) { t.restore() }, lineTo: function (t, e, n, i) { if (n.steppedLine) return "after" === n.steppedLine && !i || "after" !== n.steppedLine && i ? t.lineTo(e.x, n.y) : t.lineTo(n.x, e.y), void t.lineTo(n.x, n.y); n.tension ? t.bezierCurveTo(i ? e.controlPointPreviousX : e.controlPointNextX, i ? e.controlPointPreviousY : e.controlPointNextY, i ? n.controlPointNextX : n.controlPointPreviousX, i ? n.controlPointNextY : n.controlPointPreviousY, n.x, n.y) : t.lineTo(n.x, n.y) } }; i.clear = n.clear, i.drawRoundedRectangle = function (t) { t.beginPath(), n.roundedRect.apply(n, arguments), t.closePath() } }, { 42: 42 }], 42: [function (t, e, n) { "use strict"; var i = { noop: function () { }, uid: function () { var t = 0; return function () { return t++ } }(), isNullOrUndef: function (t) { return null === t || void 0 === t }, isArray: Array.isArray ? Array.isArray : function (t) { return "[object Array]" === Object.prototype.toString.call(t) }, isObject: function (t) { return null !== t && "[object Object]" === Object.prototype.toString.call(t) }, valueOrDefault: function (t, e) { return void 0 === t ? e : t }, valueAtIndexOrDefault: function (t, e, n) { return i.valueOrDefault(i.isArray(t) ? t[e] : t, n) }, callback: function (t, e, n) { if (t && "function" == typeof t.call) return t.apply(n, e) }, each: function (t, e, n, a) { var o, r, l; if (i.isArray(t)) if (r = t.length, a) for (o = r - 1; o >= 0; o--)e.call(n, t[o], o); else for (o = 0; o < r; o++)e.call(n, t[o], o); else if (i.isObject(t)) for (r = (l = Object.keys(t)).length, o = 0; o < r; o++)e.call(n, t[l[o]], l[o]) }, arrayEquals: function (t, e) { var n, a, o, r; if (!t || !e || t.length !== e.length) return !1; for (n = 0, a = t.length; n < a; ++n)if (o = t[n], r = e[n], o instanceof Array && r instanceof Array) { if (!i.arrayEquals(o, r)) return !1 } else if (o !== r) return !1; return !0 }, clone: function (t) { if (i.isArray(t)) return t.map(i.clone); if (i.isObject(t)) { for (var e = {}, n = Object.keys(t), a = n.length, o = 0; o < a; ++o)e[n[o]] = i.clone(t[n[o]]); return e } return t }, _merger: function (t, e, n, a) { var o = e[t], r = n[t]; i.isObject(o) && i.isObject(r) ? i.merge(o, r, a) : e[t] = i.clone(r) }, _mergerIf: function (t, e, n) { var a = e[t], o = n[t]; i.isObject(a) && i.isObject(o) ? i.mergeIf(a, o) : e.hasOwnProperty(t) || (e[t] = i.clone(o)) }, merge: function (t, e, n) { var a, o, r, l, s, u = i.isArray(e) ? e : [e], d = u.length; if (!i.isObject(t)) return t; for (a = (n = n || {}).merger || i._merger, o = 0; o < d; ++o)if (e = u[o], i.isObject(e)) for (s = 0, l = (r = Object.keys(e)).length; s < l; ++s)a(r[s], t, e, n); return t }, mergeIf: function (t, e) { return i.merge(t, e, { merger: i._mergerIf }) } }; e.exports = i, i.callCallback = i.callback, i.indexOf = function (t, e, n) { return Array.prototype.indexOf.call(t, e, n) }, i.getValueOrDefault = i.valueOrDefault, i.getValueAtIndexOrDefault = i.valueAtIndexOrDefault }, {}], 43: [function (t, e, n) { "use strict"; var i = t(42), a = { linear: function (t) { return t }, easeInQuad: function (t) { return t * t }, easeOutQuad: function (t) { return -t * (t - 2) }, easeInOutQuad: function (t) { return (t /= .5) < 1 ? .5 * t * t : -.5 * (--t * (t - 2) - 1) }, easeInCubic: function (t) { return t * t * t }, easeOutCubic: function (t) { return (t -= 1) * t * t + 1 }, easeInOutCubic: function (t) { return (t /= .5) < 1 ? .5 * t * t * t : .5 * ((t -= 2) * t * t + 2) }, easeInQuart: function (t) { return t * t * t * t }, easeOutQuart: function (t) { return -((t -= 1) * t * t * t - 1) }, easeInOutQuart: function (t) { return (t /= .5) < 1 ? .5 * t * t * t * t : -.5 * ((t -= 2) * t * t * t - 2) }, easeInQuint: function (t) { return t * t * t * t * t }, easeOutQuint: function (t) { return (t -= 1) * t * t * t * t + 1 }, easeInOutQuint: function (t) { return (t /= .5) < 1 ? .5 * t * t * t * t * t : .5 * ((t -= 2) * t * t * t * t + 2) }, easeInSine: function (t) { return 1 - Math.cos(t * (Math.PI / 2)) }, easeOutSine: function (t) { return Math.sin(t * (Math.PI / 2)) }, easeInOutSine: function (t) { return -.5 * (Math.cos(Math.PI * t) - 1) }, easeInExpo: function (t) { return 0 === t ? 0 : Math.pow(2, 10 * (t - 1)) }, easeOutExpo: function (t) { return 1 === t ? 1 : 1 - Math.pow(2, -10 * t) }, easeInOutExpo: function (t) { return 0 === t ? 0 : 1 === t ? 1 : (t /= .5) < 1 ? .5 * Math.pow(2, 10 * (t - 1)) : .5 * (2 - Math.pow(2, -10 * --t)) }, easeInCirc: function (t) { return t >= 1 ? t : -(Math.sqrt(1 - t * t) - 1) }, easeOutCirc: function (t) { return Math.sqrt(1 - (t -= 1) * t) }, easeInOutCirc: function (t) { return (t /= .5) < 1 ? -.5 * (Math.sqrt(1 - t * t) - 1) : .5 * (Math.sqrt(1 - (t -= 2) * t) + 1) }, easeInElastic: function (t) { var e = 1.70158, n = 0, i = 1; return 0 === t ? 0 : 1 === t ? 1 : (n || (n = .3), i < 1 ? (i = 1, e = n / 4) : e = n / (2 * Math.PI) * Math.asin(1 / i), -i * Math.pow(2, 10 * (t -= 1)) * Math.sin((t - e) * (2 * Math.PI) / n)) }, easeOutElastic: function (t) { var e = 1.70158, n = 0, i = 1; return 0 === t ? 0 : 1 === t ? 1 : (n || (n = .3), i < 1 ? (i = 1, e = n / 4) : e = n / (2 * Math.PI) * Math.asin(1 / i), i * Math.pow(2, -10 * t) * Math.sin((t - e) * (2 * Math.PI) / n) + 1) }, easeInOutElastic: function (t) { var e = 1.70158, n = 0, i = 1; return 0 === t ? 0 : 2 == (t /= .5) ? 1 : (n || (n = .45), i < 1 ? (i = 1, e = n / 4) : e = n / (2 * Math.PI) * Math.asin(1 / i), t < 1 ? i * Math.pow(2, 10 * (t -= 1)) * Math.sin((t - e) * (2 * Math.PI) / n) * -.5 : i * Math.pow(2, -10 * (t -= 1)) * Math.sin((t - e) * (2 * Math.PI) / n) * .5 + 1) }, easeInBack: function (t) { var e = 1.70158; return t * t * ((e + 1) * t - e) }, easeOutBack: function (t) { var e = 1.70158; return (t -= 1) * t * ((e + 1) * t + e) + 1 }, easeInOutBack: function (t) { var e = 1.70158; return (t /= .5) < 1 ? t * t * ((1 + (e *= 1.525)) * t - e) * .5 : .5 * ((t -= 2) * t * ((1 + (e *= 1.525)) * t + e) + 2) }, easeInBounce: function (t) { return 1 - a.easeOutBounce(1 - t) }, easeOutBounce: function (t) { return t < 1 / 2.75 ? 7.5625 * t * t : t < 2 / 2.75 ? 7.5625 * (t -= 1.5 / 2.75) * t + .75 : t < 2.5 / 2.75 ? 7.5625 * (t -= 2.25 / 2.75) * t + .9375 : 7.5625 * (t -= 2.625 / 2.75) * t + .984375 }, easeInOutBounce: function (t) { return t < .5 ? .5 * a.easeInBounce(2 * t) : .5 * a.easeOutBounce(2 * t - 1) + .5 } }; e.exports = { effects: a }, i.easingEffects = a }, { 42: 42 }], 44: [function (t, e, n) { "use strict"; var i = t(42); e.exports = { toLineHeight: function (t, e) { var n = ("" + t).match(/^(normal|(\d+(?:\.\d+)?)(px|em|%)?)$/); if (!n || "normal" === n[1]) return 1.2 * e; switch (t = +n[2], n[3]) { case "px": return t; case "%": t /= 100 }return e * t }, toPadding: function (t) { var e, n, a, o; return i.isObject(t) ? (e = +t.top || 0, n = +t.right || 0, a = +t.bottom || 0, o = +t.left || 0) : e = n = a = o = +t || 0, { top: e, right: n, bottom: a, left: o, height: e + a, width: o + n } }, resolve: function (t, e, n) { var a, o, r; for (a = 0, o = t.length; a < o; ++a)if (void 0 !== (r = t[a]) && (void 0 !== e && "function" == typeof r && (r = r(e)), void 0 !== n && i.isArray(r) && (r = r[n]), void 0 !== r)) return r } } }, { 42: 42 }], 45: [function (t, e, n) { "use strict"; e.exports = t(42), e.exports.easing = t(43), e.exports.canvas = t(41), e.exports.options = t(44) }, { 41: 41, 42: 42, 43: 43, 44: 44 }], 46: [function (t, e, n) { e.exports = { acquireContext: function (t) { return t && t.canvas && (t = t.canvas), t && t.getContext("2d") || null } } }, {}], 47: [function (t, e, n) { "use strict"; function i(t, e) { var n = v.getStyle(t, e), i = n && n.match(/^(\d+)(\.\d+)?px$/); return i ? Number(i[1]) : void 0 } function a(t, e) { var n = t.style, a = t.getAttribute("height"), o = t.getAttribute("width"); if (t[m] = { initial: { height: a, width: o, style: { display: n.display, height: n.height, width: n.width } } }, n.display = n.display || "block", null === o || "" === o) { var r = i(t, "width"); void 0 !== r && (t.width = r) } if (null === a || "" === a) if ("" === t.style.height) t.height = t.width / (e.options.aspectRatio || 2); else { var l = i(t, "height"); void 0 !== r && (t.height = l) } return t } function o(t, e, n) { t.addEventListener(e, n, M) } function r(t, e, n) { t.removeEventListener(e, n, M) } function l(t, e, n, i, a) { return { type: t, chart: e, native: a || null, x: void 0 !== n ? n : null, y: void 0 !== i ? i : null } } function s(t, e) { var n = w[t.type] || t.type, i = v.getRelativePosition(t, e); return l(n, e, i.x, i.y, t) } function u(t, e) { var n = !1, i = []; return function () { i = Array.prototype.slice.call(arguments), e = e || this, n || (n = !0, v.requestAnimFrame.call(window, function () { n = !1, t.apply(e, i) })) } } function d(t) { var e = document.createElement("div"), n = b + "size-monitor", i = "position:absolute;left:0;top:0;right:0;bottom:0;overflow:hidden;pointer-events:none;visibility:hidden;z-index:-1;"; e.style.cssText = i, e.className = n, e.innerHTML = '<div class="' + n + '-expand" style="' + i + '"><div style="position:absolute;width:1000000px;height:1000000px;left:0;top:0"></div></div><div class="' + n + '-shrink" style="' + i + '"><div style="position:absolute;width:200%;height:200%;left:0; top:0"></div></div>'; var a = e.childNodes[0], r = e.childNodes[1]; e._reset = function () { a.scrollLeft = 1e6, a.scrollTop = 1e6, r.scrollLeft = 1e6, r.scrollTop = 1e6 }; var l = function () { e._reset(), t() }; return o(a, "scroll", l.bind(a, "expand")), o(r, "scroll", l.bind(r, "shrink")), e } function c(t, e) { var n = (t[m] || (t[m] = {})).renderProxy = function (t) { t.animationName === y && e() }; v.each(k, function (e) { o(t, e, n) }), t.classList.add(x) } function h(t) { var e = t[m] || {}, n = e.renderProxy; n && (v.each(k, function (e) { r(t, e, n) }), delete e.renderProxy), t.classList.remove(x) } function f(t, e, n) { var i = t[m] || (t[m] = {}), a = i.resizer = d(u(function () { if (i.resizer) return e(l("resize", n)) })); c(t, function () { if (i.resizer) { var e = t.parentNode; e && e !== a.parentNode && e.insertBefore(a, e.firstChild), a._reset() } }) } function g(t) { var e = t[m] || {}, n = e.resizer; delete e.resizer, h(t), n && n.parentNode && n.parentNode.removeChild(n) } function p(t, e) { var n = t._style || document.createElement("style"); t._style || (t._style = n, e = "/* Chart.js */\n" + e, n.setAttribute("type", "text/css"), document.getElementsByTagName("head")[0].appendChild(n)), n.appendChild(document.createTextNode(e)) } var v = t(45), m = "$chartjs", b = "chartjs-", x = b + "render-monitor", y = b + "render-animation", k = ["animationstart", "webkitAnimationStart"], w = { touchstart: "mousedown", touchmove: "mousemove", touchend: "mouseup", pointerenter: "mouseenter", pointerdown: "mousedown", pointermove: "mousemove", pointerup: "mouseup", pointerleave: "mouseout", pointerout: "mouseout" }, M = !!function () { var t = !1; try { var e = Object.defineProperty({}, "passive", { get: function () { t = !0 } }); window.addEventListener("e", null, e) } catch (t) { } return t }() && { passive: !0 }; e.exports = { _enabled: "undefined" != typeof window && "undefined" != typeof document, initialize: function () { var t = "from{opacity:0.99}to{opacity:1}"; p(this, "@-webkit-keyframes " + y + "{" + t + "}@keyframes " + y + "{" + t + "}." + x + "{-webkit-animation:" + y + " 0.001s;animation:" + y + " 0.001s;}") }, acquireContext: function (t, e) { "string" == typeof t ? t = document.getElementById(t) : t.length && (t = t[0]), t && t.canvas && (t = t.canvas); var n = t && t.getContext && t.getContext("2d"); return n && n.canvas === t ? (a(t, e), n) : null }, releaseContext: function (t) { var e = t.canvas; if (e[m]) { var n = e[m].initial;["height", "width"].forEach(function (t) { var i = n[t]; v.isNullOrUndef(i) ? e.removeAttribute(t) : e.setAttribute(t, i) }), v.each(n.style || {}, function (t, n) { e.style[n] = t }), e.width = e.width, delete e[m] } }, addEventListener: function (t, e, n) { var i = t.canvas; if ("resize" !== e) { var a = n[m] || (n[m] = {}); o(i, e, (a.proxies || (a.proxies = {}))[t.id + "_" + e] = function (e) { n(s(e, t)) }) } else f(i, n, t) }, removeEventListener: function (t, e, n) { var i = t.canvas; if ("resize" !== e) { var a = ((n[m] || {}).proxies || {})[t.id + "_" + e]; a && r(i, e, a) } else g(i) } }, v.addEvent = o, v.removeEvent = r }, { 45: 45 }], 48: [function (t, e, n) { "use strict"; var i = t(45), a = t(46), o = t(47), r = o._enabled ? o : a; e.exports = i.extend({ initialize: function () { }, acquireContext: function () { }, releaseContext: function () { }, addEventListener: function () { }, removeEventListener: function () { } }, r) }, { 45: 45, 46: 46, 47: 47 }], 49: [function (t, e, n) { "use strict"; var i = t(25), a = t(40), o = t(45); i._set("global", { plugins: { filler: { propagate: !0 } } }), e.exports = function () { function t(t, e, n) { var i, a = t._model || {}, o = a.fill; if (void 0 === o && (o = !!a.backgroundColor), !1 === o || null === o) return !1; if (!0 === o) return "origin"; if (i = parseFloat(o, 10), isFinite(i) && Math.floor(i) === i) return "-" !== o[0] && "+" !== o[0] || (i = e + i), !(i === e || i < 0 || i >= n) && i; switch (o) { case "bottom": return "start"; case "top": return "end"; case "zero": return "origin"; case "origin": case "start": case "end": return o; default: return !1 } } function e(t) { var e, n = t.el._model || {}, i = t.el._scale || {}, a = t.fill, o = null; if (isFinite(a)) return null; if ("start" === a ? o = void 0 === n.scaleBottom ? i.bottom : n.scaleBottom : "end" === a ? o = void 0 === n.scaleTop ? i.top : n.scaleTop : void 0 !== n.scaleZero ? o = n.scaleZero : i.getBasePosition ? o = i.getBasePosition() : i.getBasePixel && (o = i.getBasePixel()), void 0 !== o && null !== o) { if (void 0 !== o.x && void 0 !== o.y) return o; if ("number" == typeof o && isFinite(o)) return e = i.isHorizontal(), { x: e ? o : null, y: e ? null : o } } return null } function n(t, e, n) { var i, a = t[e].fill, o = [e]; if (!n) return a; for (; !1 !== a && -1 === o.indexOf(a);) { if (!isFinite(a)) return a; if (!(i = t[a])) return !1; if (i.visible) return a; o.push(a), a = i.fill } return !1 } function r(t) { var e = t.fill, n = "dataset"; return !1 === e ? null : (isFinite(e) || (n = "boundary"), d[n](t)) } function l(t) { return t && !t.skip } function s(t, e, n, i, a) { var r; if (i && a) { for (t.moveTo(e[0].x, e[0].y), r = 1; r < i; ++r)o.canvas.lineTo(t, e[r - 1], e[r]); for (t.lineTo(n[a - 1].x, n[a - 1].y), r = a - 1; r > 0; --r)o.canvas.lineTo(t, n[r], n[r - 1], !0) } } function u(t, e, n, i, a, o) { var r, u, d, c, h, f, g, p = e.length, v = i.spanGaps, m = [], b = [], x = 0, y = 0; for (t.beginPath(), r = 0, u = p + !!o; r < u; ++r)h = n(c = e[d = r % p]._view, d, i), f = l(c), g = l(h), f && g ? (x = m.push(c), y = b.push(h)) : x && y && (v ? (f && m.push(c), g && b.push(h)) : (s(t, m, b, x, y), x = y = 0, m = [], b = [])); s(t, m, b, x, y), t.closePath(), t.fillStyle = a, t.fill() } var d = { dataset: function (t) { var e = t.fill, n = t.chart, i = n.getDatasetMeta(e), a = i && n.isDatasetVisible(e) && i.dataset._children || [], o = a.length || 0; return o ? function (t, e) { return e < o && a[e]._view || null } : null }, boundary: function (t) { var e = t.boundary, n = e ? e.x : null, i = e ? e.y : null; return function (t) { return { x: null === n ? t.x : n, y: null === i ? t.y : i } } } }; return { id: "filler", afterDatasetsUpdate: function (i, o) { var l, s, u, d, c = (i.data.datasets || []).length, h = o.propagate, f = []; for (s = 0; s < c; ++s)d = null, (u = (l = i.getDatasetMeta(s)).dataset) && u._model && u instanceof a.Line && (d = { visible: i.isDatasetVisible(s), fill: t(u, s, c), chart: i, el: u }), l.$filler = d, f.push(d); for (s = 0; s < c; ++s)(d = f[s]) && (d.fill = n(f, s, h), d.boundary = e(d), d.mapper = r(d)) }, beforeDatasetDraw: function (t, e) { var n = e.meta.$filler; if (n) { var a = t.ctx, r = n.el, l = r._view, s = r._children || [], d = n.mapper, c = l.backgroundColor || i.global.defaultColor; d && c && s.length && (o.canvas.clipArea(a, t.chartArea), u(a, s, d, l, c, r._loop), o.canvas.unclipArea(a)) } } } } }, { 25: 25, 40: 40, 45: 45 }], 50: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { legend: { display: !0, position: "top", fullWidth: !0, reverse: !1, weight: 1e3, onClick: function (t, e) { var n = e.datasetIndex, i = this.chart, a = i.getDatasetMeta(n); a.hidden = null === a.hidden ? !i.data.datasets[n].hidden : null, i.update() }, onHover: null, labels: { boxWidth: 40, padding: 10, generateLabels: function (t) { var e = t.data; return o.isArray(e.datasets) ? e.datasets.map(function (e, n) { return { text: e.label, fillStyle: o.isArray(e.backgroundColor) ? e.backgroundColor[0] : e.backgroundColor, hidden: !t.isDatasetVisible(n), lineCap: e.borderCapStyle, lineDash: e.borderDash, lineDashOffset: e.borderDashOffset, lineJoin: e.borderJoinStyle, lineWidth: e.borderWidth, strokeStyle: e.borderColor, pointStyle: e.pointStyle, datasetIndex: n } }, this) : [] } } }, legendCallback: function (t) { var e = []; e.push('<ul class="' + t.id + '-legend">'); for (var n = 0; n < t.data.datasets.length; n++)e.push('<li><span style="background-color:' + t.data.datasets[n].backgroundColor + '"></span>'), t.data.datasets[n].label && e.push(t.data.datasets[n].label), e.push("</li>"); return e.push("</ul>"), e.join("") } }), e.exports = function (t) { function e(t, e) { return t.usePointStyle ? e * Math.SQRT2 : t.boxWidth } function n(e, n) { var i = new t.Legend({ ctx: e.ctx, options: n, chart: e }); r.configure(e, i, n), r.addBox(e, i), e.legend = i } var r = t.layoutService, l = o.noop; return t.Legend = a.extend({ initialize: function (t) { o.extend(this, t), this.legendHitBoxes = [], this.doughnutMode = !1 }, beforeUpdate: l, update: function (t, e, n) { var i = this; return i.beforeUpdate(), i.maxWidth = t, i.maxHeight = e, i.margins = n, i.beforeSetDimensions(), i.setDimensions(), i.afterSetDimensions(), i.beforeBuildLabels(), i.buildLabels(), i.afterBuildLabels(), i.beforeFit(), i.fit(), i.afterFit(), i.afterUpdate(), i.minSize }, afterUpdate: l, beforeSetDimensions: l, setDimensions: function () { var t = this; t.isHorizontal() ? (t.width = t.maxWidth, t.left = 0, t.right = t.width) : (t.height = t.maxHeight, t.top = 0, t.bottom = t.height), t.paddingLeft = 0, t.paddingTop = 0, t.paddingRight = 0, t.paddingBottom = 0, t.minSize = { width: 0, height: 0 } }, afterSetDimensions: l, beforeBuildLabels: l, buildLabels: function () { var t = this, e = t.options.labels || {}, n = o.callback(e.generateLabels, [t.chart], t) || []; e.filter && (n = n.filter(function (n) { return e.filter(n, t.chart.data) })), t.options.reverse && n.reverse(), t.legendItems = n }, afterBuildLabels: l, beforeFit: l, fit: function () { var t = this, n = t.options, a = n.labels, r = n.display, l = t.ctx, s = i.global, u = o.valueOrDefault, d = u(a.fontSize, s.defaultFontSize), c = u(a.fontStyle, s.defaultFontStyle), h = u(a.fontFamily, s.defaultFontFamily), f = o.fontString(d, c, h), g = t.legendHitBoxes = [], p = t.minSize, v = t.isHorizontal(); if (v ? (p.width = t.maxWidth, p.height = r ? 10 : 0) : (p.width = r ? 10 : 0, p.height = t.maxHeight), r) if (l.font = f, v) { var m = t.lineWidths = [0], b = t.legendItems.length ? d + a.padding : 0; l.textAlign = "left", l.textBaseline = "top", o.each(t.legendItems, function (n, i) { var o = e(a, d) + d / 2 + l.measureText(n.text).width; m[m.length - 1] + o + a.padding >= t.width && (b += d + a.padding, m[m.length] = t.left), g[i] = { left: 0, top: 0, width: o, height: d }, m[m.length - 1] += o + a.padding }), p.height += b } else { var x = a.padding, y = t.columnWidths = [], k = a.padding, w = 0, M = 0, S = d + x; o.each(t.legendItems, function (t, n) { var i = e(a, d) + d / 2 + l.measureText(t.text).width; M + S > p.height && (k += w + a.padding, y.push(w), w = 0, M = 0), w = Math.max(w, i), M += S, g[n] = { left: 0, top: 0, width: i, height: d } }), k += w, y.push(w), p.width += k } t.width = p.width, t.height = p.height }, afterFit: l, isHorizontal: function () { return "top" === this.options.position || "bottom" === this.options.position }, draw: function () { var t = this, n = t.options, a = n.labels, r = i.global, l = r.elements.line, s = t.width, u = t.lineWidths; if (n.display) { var d, c = t.ctx, h = o.valueOrDefault, f = h(a.fontColor, r.defaultFontColor), g = h(a.fontSize, r.defaultFontSize), p = h(a.fontStyle, r.defaultFontStyle), v = h(a.fontFamily, r.defaultFontFamily), m = o.fontString(g, p, v); c.textAlign = "left", c.textBaseline = "middle", c.lineWidth = .5, c.strokeStyle = f, c.fillStyle = f, c.font = m; var b = e(a, g), x = t.legendHitBoxes, y = function (t, e, i) { if (!(isNaN(b) || b <= 0)) { c.save(), c.fillStyle = h(i.fillStyle, r.defaultColor), c.lineCap = h(i.lineCap, l.borderCapStyle), c.lineDashOffset = h(i.lineDashOffset, l.borderDashOffset), c.lineJoin = h(i.lineJoin, l.borderJoinStyle), c.lineWidth = h(i.lineWidth, l.borderWidth), c.strokeStyle = h(i.strokeStyle, r.defaultColor); var a = 0 === h(i.lineWidth, l.borderWidth); if (c.setLineDash && c.setLineDash(h(i.lineDash, l.borderDash)), n.labels && n.labels.usePointStyle) { var s = g * Math.SQRT2 / 2, u = s / Math.SQRT2, d = t + u, f = e + u; o.canvas.drawPoint(c, i.pointStyle, s, d, f) } else a || c.strokeRect(t, e, b, g), c.fillRect(t, e, b, g); c.restore() } }, k = function (t, e, n, i) { var a = g / 2, o = b + a + t, r = e + a; c.fillText(n.text, o, r), n.hidden && (c.beginPath(), c.lineWidth = 2, c.moveTo(o, r), c.lineTo(o + i, r), c.stroke()) }, w = t.isHorizontal(); d = w ? { x: t.left + (s - u[0]) / 2, y: t.top + a.padding, line: 0 } : { x: t.left + a.padding, y: t.top + a.padding, line: 0 }; var M = g + a.padding; o.each(t.legendItems, function (e, n) { var i = c.measureText(e.text).width, o = b + g / 2 + i, r = d.x, l = d.y; w ? r + o >= s && (l = d.y += M, d.line++, r = d.x = t.left + (s - u[d.line]) / 2) : l + M > t.bottom && (r = d.x = r + t.columnWidths[d.line] + a.padding, l = d.y = t.top + a.padding, d.line++), y(r, l, e), x[n].left = r, x[n].top = l, k(r, l, e, i), w ? d.x += o + a.padding : d.y += M }) } }, handleEvent: function (t) { var e = this, n = e.options, i = "mouseup" === t.type ? "click" : t.type, a = !1; if ("mousemove" === i) { if (!n.onHover) return } else { if ("click" !== i) return; if (!n.onClick) return } var o = t.x, r = t.y; if (o >= e.left && o <= e.right && r >= e.top && r <= e.bottom) for (var l = e.legendHitBoxes, s = 0; s < l.length; ++s) { var u = l[s]; if (o >= u.left && o <= u.left + u.width && r >= u.top && r <= u.top + u.height) { if ("click" === i) { n.onClick.call(e, t.native, e.legendItems[s]), a = !0; break } if ("mousemove" === i) { n.onHover.call(e, t.native, e.legendItems[s]), a = !0; break } } } return a } }), { id: "legend", beforeInit: function (t) { var e = t.options.legend; e && n(t, e) }, beforeUpdate: function (t) { var e = t.options.legend, a = t.legend; e ? (o.mergeIf(e, i.global.legend), a ? (r.configure(t, a, e), a.options = e) : n(t, e)) : a && (r.removeBox(t, a), delete t.legend) }, afterEvent: function (t, e) { var n = t.legend; n && n.handleEvent(e) } } } }, { 25: 25, 26: 26, 45: 45 }], 51: [function (t, e, n) { "use strict"; var i = t(25), a = t(26), o = t(45); i._set("global", { title: { display: !1, fontStyle: "bold", fullWidth: !0, lineHeight: 1.2, padding: 10, position: "top", text: "", weight: 2e3 } }), e.exports = function (t) { function e(e, i) { var a = new t.Title({ ctx: e.ctx, options: i, chart: e }); n.configure(e, a, i), n.addBox(e, a), e.titleBlock = a } var n = t.layoutService, r = o.noop; return t.Title = a.extend({ initialize: function (t) { var e = this; o.extend(e, t), e.legendHitBoxes = [] }, beforeUpdate: r, update: function (t, e, n) { var i = this; return i.beforeUpdate(), i.maxWidth = t, i.maxHeight = e, i.margins = n, i.beforeSetDimensions(), i.setDimensions(), i.afterSetDimensions(), i.beforeBuildLabels(), i.buildLabels(), i.afterBuildLabels(), i.beforeFit(), i.fit(), i.afterFit(), i.afterUpdate(), i.minSize }, afterUpdate: r, beforeSetDimensions: r, setDimensions: function () { var t = this; t.isHorizontal() ? (t.width = t.maxWidth, t.left = 0, t.right = t.width) : (t.height = t.maxHeight, t.top = 0, t.bottom = t.height), t.paddingLeft = 0, t.paddingTop = 0, t.paddingRight = 0, t.paddingBottom = 0, t.minSize = { width: 0, height: 0 } }, afterSetDimensions: r, beforeBuildLabels: r, buildLabels: r, afterBuildLabels: r, beforeFit: r, fit: function () { var t = this, e = o.valueOrDefault, n = t.options, a = n.display, r = e(n.fontSize, i.global.defaultFontSize), l = t.minSize, s = o.isArray(n.text) ? n.text.length : 1, u = o.options.toLineHeight(n.lineHeight, r), d = a ? s * u + 2 * n.padding : 0; t.isHorizontal() ? (l.width = t.maxWidth, l.height = d) : (l.width = d, l.height = t.maxHeight), t.width = l.width, t.height = l.height }, afterFit: r, isHorizontal: function () { var t = this.options.position; return "top" === t || "bottom" === t }, draw: function () { var t = this, e = t.ctx, n = o.valueOrDefault, a = t.options, r = i.global; if (a.display) { var l, s, u, d = n(a.fontSize, r.defaultFontSize), c = n(a.fontStyle, r.defaultFontStyle), h = n(a.fontFamily, r.defaultFontFamily), f = o.fontString(d, c, h), g = o.options.toLineHeight(a.lineHeight, d), p = g / 2 + a.padding, v = 0, m = t.top, b = t.left, x = t.bottom, y = t.right; e.fillStyle = n(a.fontColor, r.defaultFontColor), e.font = f, t.isHorizontal() ? (s = b + (y - b) / 2, u = m + p, l = y - b) : (s = "left" === a.position ? b + p : y - p, u = m + (x - m) / 2, l = x - m, v = Math.PI * ("left" === a.position ? -.5 : .5)), e.save(), e.translate(s, u), e.rotate(v), e.textAlign = "center", e.textBaseline = "middle"; var k = a.text; if (o.isArray(k)) for (var w = 0, M = 0; M < k.length; ++M)e.fillText(k[M], 0, w, l), w += g; else e.fillText(k, 0, 0, l); e.restore() } } }), { id: "title", beforeInit: function (t) { var n = t.options.title; n && e(t, n) }, beforeUpdate: function (a) { var r = a.options.title, l = a.titleBlock; r ? (o.mergeIf(r, i.global.title), l ? (n.configure(a, l, r), l.options = r) : e(a, r)) : l && (t.layoutService.removeBox(a, l), delete a.titleBlock) } } } }, { 25: 25, 26: 26, 45: 45 }], 52: [function (t, e, n) { "use strict"; e.exports = function (t) { var e = { position: "bottom" }, n = t.Scale.extend({ getLabels: function () { var t = this.chart.data; return this.options.labels || (this.isHorizontal() ? t.xLabels : t.yLabels) || t.labels }, determineDataLimits: function () { var t = this, e = t.getLabels(); t.minIndex = 0, t.maxIndex = e.length - 1; var n; void 0 !== t.options.ticks.min && (n = e.indexOf(t.options.ticks.min), t.minIndex = -1 !== n ? n : t.minIndex), void 0 !== t.options.ticks.max && (n = e.indexOf(t.options.ticks.max), t.maxIndex = -1 !== n ? n : t.maxIndex), t.min = e[t.minIndex], t.max = e[t.maxIndex] }, buildTicks: function () { var t = this, e = t.getLabels(); t.ticks = 0 === t.minIndex && t.maxIndex === e.length - 1 ? e : e.slice(t.minIndex, t.maxIndex + 1) }, getLabelForIndex: function (t, e) { var n = this, i = n.chart.data, a = n.isHorizontal(); return i.yLabels && !a ? n.getRightValue(i.datasets[e].data[t]) : n.ticks[t - n.minIndex] }, getPixelForValue: function (t, e) { var n, i = this, a = i.options.offset, o = Math.max(i.maxIndex + 1 - i.minIndex - (a ? 0 : 1), 1); if (void 0 !== t && null !== t && (n = i.isHorizontal() ? t.x : t.y), void 0 !== n || void 0 !== t && isNaN(e)) { var r = i.getLabels(); t = n || t; var l = r.indexOf(t); e = -1 !== l ? l : e } if (i.isHorizontal()) { var s = i.width / o, u = s * (e - i.minIndex); return a && (u += s / 2), i.left + Math.round(u) } var d = i.height / o, c = d * (e - i.minIndex); return a && (c += d / 2), i.top + Math.round(c) }, getPixelForTick: function (t) { return this.getPixelForValue(this.ticks[t], t + this.minIndex, null) }, getValueForPixel: function (t) { var e = this, n = e.options.offset, i = Math.max(e._ticks.length - (n ? 0 : 1), 1), a = e.isHorizontal(), o = (a ? e.width : e.height) / i; return t -= a ? e.left : e.top, n && (t -= o / 2), (t <= 0 ? 0 : Math.round(t / o)) + e.minIndex }, getBasePixel: function () { return this.bottom } }); t.scaleService.registerScaleType("category", n, e) } }, {}], 53: [function (t, e, n) { "use strict"; var i = t(25), a = t(45), o = t(34); e.exports = function (t) { var e = { position: "left", ticks: { callback: o.formatters.linear } }, n = t.LinearScaleBase.extend({ determineDataLimits: function () { function t(t) { return r ? t.xAxisID === e.id : t.yAxisID === e.id } var e = this, n = e.options, i = e.chart, o = i.data.datasets, r = e.isHorizontal(); e.min = null, e.max = null; var l = n.stacked; if (void 0 === l && a.each(o, function (e, n) { if (!l) { var a = i.getDatasetMeta(n); i.isDatasetVisible(n) && t(a) && void 0 !== a.stack && (l = !0) } }), n.stacked || l) { var s = {}; a.each(o, function (o, r) { var l = i.getDatasetMeta(r), u = [l.type, void 0 === n.stacked && void 0 === l.stack ? r : "", l.stack].join("."); void 0 === s[u] && (s[u] = { positiveValues: [], negativeValues: [] }); var d = s[u].positiveValues, c = s[u].negativeValues; i.isDatasetVisible(r) && t(l) && a.each(o.data, function (t, i) { var a = +e.getRightValue(t); isNaN(a) || l.data[i].hidden || (d[i] = d[i] || 0, c[i] = c[i] || 0, n.relativePoints ? d[i] = 100 : a < 0 ? c[i] += a : d[i] += a) }) }), a.each(s, function (t) { var n = t.positiveValues.concat(t.negativeValues), i = a.min(n), o = a.max(n); e.min = null === e.min ? i : Math.min(e.min, i), e.max = null === e.max ? o : Math.max(e.max, o) }) } else a.each(o, function (n, o) { var r = i.getDatasetMeta(o); i.isDatasetVisible(o) && t(r) && a.each(n.data, function (t, n) { var i = +e.getRightValue(t); isNaN(i) || r.data[n].hidden || (null === e.min ? e.min = i : i < e.min && (e.min = i), null === e.max ? e.max = i : i > e.max && (e.max = i)) }) }); e.min = isFinite(e.min) && !isNaN(e.min) ? e.min : 0, e.max = isFinite(e.max) && !isNaN(e.max) ? e.max : 1, this.handleTickRangeOptions() }, getTickLimit: function () { var t, e = this, n = e.options.ticks; if (e.isHorizontal()) t = Math.min(n.maxTicksLimit ? n.maxTicksLimit : 11, Math.ceil(e.width / 50)); else { var o = a.valueOrDefault(n.fontSize, i.global.defaultFontSize); t = Math.min(n.maxTicksLimit ? n.maxTicksLimit : 11, Math.ceil(e.height / (2 * o))) } return t }, handleDirectionalChanges: function () { this.isHorizontal() || this.ticks.reverse() }, getLabelForIndex: function (t, e) { return +this.getRightValue(this.chart.data.datasets[e].data[t]) }, getPixelForValue: function (t) { var e, n = this, i = n.start, a = +n.getRightValue(t), o = n.end - i; return n.isHorizontal() ? (e = n.left + n.width / o * (a - i), Math.round(e)) : (e = n.bottom - n.height / o * (a - i), Math.round(e)) }, getValueForPixel: function (t) { var e = this, n = e.isHorizontal(), i = n ? e.width : e.height, a = (n ? t - e.left : e.bottom - t) / i; return e.start + (e.end - e.start) * a }, getPixelForTick: function (t) { return this.getPixelForValue(this.ticksAsNumbers[t]) } }); t.scaleService.registerScaleType("linear", n, e) } }, { 25: 25, 34: 34, 45: 45 }], 54: [function (t, e, n) { "use strict"; var i = t(45), a = t(34); e.exports = function (t) { var e = i.noop; t.LinearScaleBase = t.Scale.extend({ getRightValue: function (e) { return "string" == typeof e ? +e : t.Scale.prototype.getRightValue.call(this, e) }, handleTickRangeOptions: function () { var t = this, e = t.options.ticks; if (e.beginAtZero) { var n = i.sign(t.min), a = i.sign(t.max); n < 0 && a < 0 ? t.max = 0 : n > 0 && a > 0 && (t.min = 0) } var o = void 0 !== e.min || void 0 !== e.suggestedMin, r = void 0 !== e.max || void 0 !== e.suggestedMax; void 0 !== e.min ? t.min = e.min : void 0 !== e.suggestedMin && (null === t.min ? t.min = e.suggestedMin : t.min = Math.min(t.min, e.suggestedMin)), void 0 !== e.max ? t.max = e.max : void 0 !== e.suggestedMax && (null === t.max ? t.max = e.suggestedMax : t.max = Math.max(t.max, e.suggestedMax)), o !== r && t.min >= t.max && (o ? t.max = t.min + 1 : t.min = t.max - 1), t.min === t.max && (t.max++, e.beginAtZero || t.min--) }, getTickLimit: e, handleDirectionalChanges: e, buildTicks: function () { var t = this, e = t.options.ticks, n = t.getTickLimit(), o = { maxTicks: n = Math.max(2, n), min: e.min, max: e.max, stepSize: i.valueOrDefault(e.fixedStepSize, e.stepSize) }, r = t.ticks = a.generators.linear(o, t); t.handleDirectionalChanges(), t.max = i.max(r), t.min = i.min(r), e.reverse ? (r.reverse(), t.start = t.max, t.end = t.min) : (t.start = t.min, t.end = t.max) }, convertTicksToLabels: function () { var e = this; e.ticksAsNumbers = e.ticks.slice(), e.zeroLineIndex = e.ticks.indexOf(0), t.Scale.prototype.convertTicksToLabels.call(e) } }) } }, { 34: 34, 45: 45 }], 55: [function (t, e, n) { "use strict"; var i = t(45), a = t(34); e.exports = function (t) { var e = { position: "left", ticks: { callback: a.formatters.logarithmic } }, n = t.Scale.extend({ determineDataLimits: function () { function t(t) { return s ? t.xAxisID === e.id : t.yAxisID === e.id } var e = this, n = e.options, a = n.ticks, o = e.chart, r = o.data.datasets, l = i.valueOrDefault, s = e.isHorizontal(); e.min = null, e.max = null, e.minNotZero = null; var u = n.stacked; if (void 0 === u && i.each(r, function (e, n) { if (!u) { var i = o.getDatasetMeta(n); o.isDatasetVisible(n) && t(i) && void 0 !== i.stack && (u = !0) } }), n.stacked || u) { var d = {}; i.each(r, function (a, r) { var l = o.getDatasetMeta(r), s = [l.type, void 0 === n.stacked && void 0 === l.stack ? r : "", l.stack].join("."); o.isDatasetVisible(r) && t(l) && (void 0 === d[s] && (d[s] = []), i.each(a.data, function (t, i) { var a = d[s], o = +e.getRightValue(t); isNaN(o) || l.data[i].hidden || (a[i] = a[i] || 0, n.relativePoints ? a[i] = 100 : a[i] += o) })) }), i.each(d, function (t) { var n = i.min(t), a = i.max(t); e.min = null === e.min ? n : Math.min(e.min, n), e.max = null === e.max ? a : Math.max(e.max, a) }) } else i.each(r, function (n, a) { var r = o.getDatasetMeta(a); o.isDatasetVisible(a) && t(r) && i.each(n.data, function (t, n) { var i = +e.getRightValue(t); isNaN(i) || r.data[n].hidden || (null === e.min ? e.min = i : i < e.min && (e.min = i), null === e.max ? e.max = i : i > e.max && (e.max = i), 0 !== i && (null === e.minNotZero || i < e.minNotZero) && (e.minNotZero = i)) }) }); e.min = l(a.min, e.min), e.max = l(a.max, e.max), e.min === e.max && (0 !== e.min && null !== e.min ? (e.min = Math.pow(10, Math.floor(i.log10(e.min)) - 1), e.max = Math.pow(10, Math.floor(i.log10(e.max)) + 1)) : (e.min = 1, e.max = 10)) }, buildTicks: function () { var t = this, e = t.options.ticks, n = { min: e.min, max: e.max }, o = t.ticks = a.generators.logarithmic(n, t); t.isHorizontal() || o.reverse(), t.max = i.max(o), t.min = i.min(o), e.reverse ? (o.reverse(), t.start = t.max, t.end = t.min) : (t.start = t.min, t.end = t.max) }, convertTicksToLabels: function () { this.tickValues = this.ticks.slice(), t.Scale.prototype.convertTicksToLabels.call(this) }, getLabelForIndex: function (t, e) { return +this.getRightValue(this.chart.data.datasets[e].data[t]) }, getPixelForTick: function (t) { return this.getPixelForValue(this.tickValues[t]) }, getPixelForValue: function (t) { var e, n, a, o = this, r = o.start, l = +o.getRightValue(t), s = o.options.ticks; return o.isHorizontal() ? (a = i.log10(o.end) - i.log10(r), 0 === l ? n = o.left : (e = o.width, n = o.left + e / a * (i.log10(l) - i.log10(r)))) : (e = o.height, 0 !== r || s.reverse ? 0 === o.end && s.reverse ? (a = i.log10(o.start) - i.log10(o.minNotZero), n = l === o.end ? o.top : l === o.minNotZero ? o.top + .02 * e : o.top + .02 * e + .98 * e / a * (i.log10(l) - i.log10(o.minNotZero))) : 0 === l ? n = s.reverse ? o.top : o.bottom : (a = i.log10(o.end) - i.log10(r), e = o.height, n = o.bottom - e / a * (i.log10(l) - i.log10(r))) : (a = i.log10(o.end) - i.log10(o.minNotZero), n = l === r ? o.bottom : l === o.minNotZero ? o.bottom - .02 * e : o.bottom - .02 * e - .98 * e / a * (i.log10(l) - i.log10(o.minNotZero)))), n }, getValueForPixel: function (t) { var e, n, a = this, o = i.log10(a.end) - i.log10(a.start); return a.isHorizontal() ? (n = a.width, e = a.start * Math.pow(10, (t - a.left) * o / n)) : (n = a.height, e = Math.pow(10, (a.bottom - t) * o / n) / a.start), e } }); t.scaleService.registerScaleType("logarithmic", n, e) } }, { 34: 34, 45: 45 }], 56: [function (t, e, n) { "use strict"; var i = t(25), a = t(45), o = t(34); e.exports = function (t) { function e(t) { var e = t.options; return e.angleLines.display || e.pointLabels.display ? t.chart.data.labels.length : 0 } function n(t) { var e = t.options.pointLabels, n = a.valueOrDefault(e.fontSize, v.defaultFontSize), i = a.valueOrDefault(e.fontStyle, v.defaultFontStyle), o = a.valueOrDefault(e.fontFamily, v.defaultFontFamily); return { size: n, style: i, family: o, font: a.fontString(n, i, o) } } function r(t, e, n) { return a.isArray(n) ? { w: a.longestText(t, t.font, n), h: n.length * e + 1.5 * (n.length - 1) * e } : { w: t.measureText(n).width, h: e } } function l(t, e, n, i, a) { return t === i || t === a ? { start: e - n / 2, end: e + n / 2 } : t < i || t > a ? { start: e - n - 5, end: e } : { start: e, end: e + n + 5 } } function s(t) { var i, o, s, u = n(t), d = Math.min(t.height / 2, t.width / 2), c = { r: t.width, l: 0, t: t.height, b: 0 }, h = {}; t.ctx.font = u.font, t._pointLabelSizes = []; var f = e(t); for (i = 0; i < f; i++) { s = t.getPointPosition(i, d), o = r(t.ctx, u.size, t.pointLabels[i] || ""), t._pointLabelSizes[i] = o; var g = t.getIndexAngle(i), p = a.toDegrees(g) % 360, v = l(p, s.x, o.w, 0, 180), m = l(p, s.y, o.h, 90, 270); v.start < c.l && (c.l = v.start, h.l = g), v.end > c.r && (c.r = v.end, h.r = g), m.start < c.t && (c.t = m.start, h.t = g), m.end > c.b && (c.b = m.end, h.b = g) } t.setReductions(d, c, h) } function u(t) { var e = Math.min(t.height / 2, t.width / 2); t.drawingArea = Math.round(e), t.setCenterPoint(0, 0, 0, 0) } function d(t) { return 0 === t || 180 === t ? "center" : t < 180 ? "left" : "right" } function c(t, e, n, i) { if (a.isArray(e)) for (var o = n.y, r = 1.5 * i, l = 0; l < e.length; ++l)t.fillText(e[l], n.x, o), o += r; else t.fillText(e, n.x, n.y) } function h(t, e, n) { 90 === t || 270 === t ? n.y -= e.h / 2 : (t > 270 || t < 90) && (n.y -= e.h) } function f(t) { var i = t.ctx, o = a.valueOrDefault, r = t.options, l = r.angleLines, s = r.pointLabels; i.lineWidth = l.lineWidth, i.strokeStyle = l.color; var u = t.getDistanceFromCenterForValue(r.ticks.reverse ? t.min : t.max), f = n(t); i.textBaseline = "top"; for (var g = e(t) - 1; g >= 0; g--) { if (l.display) { var p = t.getPointPosition(g, u); i.beginPath(), i.moveTo(t.xCenter, t.yCenter), i.lineTo(p.x, p.y), i.stroke(), i.closePath() } if (s.display) { var m = t.getPointPosition(g, u + 5), b = o(s.fontColor, v.defaultFontColor); i.font = f.font, i.fillStyle = b; var x = t.getIndexAngle(g), y = a.toDegrees(x); i.textAlign = d(y), h(y, t._pointLabelSizes[g], m), c(i, t.pointLabels[g] || "", m, f.size) } } } function g(t, n, i, o) { var r = t.ctx; if (r.strokeStyle = a.valueAtIndexOrDefault(n.color, o - 1), r.lineWidth = a.valueAtIndexOrDefault(n.lineWidth, o - 1), t.options.gridLines.circular) r.beginPath(), r.arc(t.xCenter, t.yCenter, i, 0, 2 * Math.PI), r.closePath(), r.stroke(); else { var l = e(t); if (0 === l) return; r.beginPath(); var s = t.getPointPosition(0, i); r.moveTo(s.x, s.y); for (var u = 1; u < l; u++)s = t.getPointPosition(u, i), r.lineTo(s.x, s.y); r.closePath(), r.stroke() } } function p(t) { return a.isNumber(t) ? t : 0 } var v = i.global, m = { display: !0, animate: !0, position: "chartArea", angleLines: { display: !0, color: "rgba(0, 0, 0, 0.1)", lineWidth: 1 }, gridLines: { circular: !1 }, ticks: { showLabelBackdrop: !0, backdropColor: "rgba(255,255,255,0.75)", backdropPaddingY: 2, backdropPaddingX: 2, callback: o.formatters.linear }, pointLabels: { display: !0, fontSize: 10, callback: function (t) { return t } } }, b = t.LinearScaleBase.extend({ setDimensions: function () { var t = this, e = t.options, n = e.ticks; t.width = t.maxWidth, t.height = t.maxHeight, t.xCenter = Math.round(t.width / 2), t.yCenter = Math.round(t.height / 2); var i = a.min([t.height, t.width]), o = a.valueOrDefault(n.fontSize, v.defaultFontSize); t.drawingArea = e.display ? i / 2 - (o / 2 + n.backdropPaddingY) : i / 2 }, determineDataLimits: function () { var t = this, e = t.chart, n = Number.POSITIVE_INFINITY, i = Number.NEGATIVE_INFINITY; a.each(e.data.datasets, function (o, r) { if (e.isDatasetVisible(r)) { var l = e.getDatasetMeta(r); a.each(o.data, function (e, a) { var o = +t.getRightValue(e); isNaN(o) || l.data[a].hidden || (n = Math.min(o, n), i = Math.max(o, i)) }) } }), t.min = n === Number.POSITIVE_INFINITY ? 0 : n, t.max = i === Number.NEGATIVE_INFINITY ? 0 : i, t.handleTickRangeOptions() }, getTickLimit: function () { var t = this.options.ticks, e = a.valueOrDefault(t.fontSize, v.defaultFontSize); return Math.min(t.maxTicksLimit ? t.maxTicksLimit : 11, Math.ceil(this.drawingArea / (1.5 * e))) }, convertTicksToLabels: function () { var e = this; t.LinearScaleBase.prototype.convertTicksToLabels.call(e), e.pointLabels = e.chart.data.labels.map(e.options.pointLabels.callback, e) }, getLabelForIndex: function (t, e) { return +this.getRightValue(this.chart.data.datasets[e].data[t]) }, fit: function () { this.options.pointLabels.display ? s(this) : u(this) }, setReductions: function (t, e, n) { var i = this, a = e.l / Math.sin(n.l), o = Math.max(e.r - i.width, 0) / Math.sin(n.r), r = -e.t / Math.cos(n.t), l = -Math.max(e.b - i.height, 0) / Math.cos(n.b); a = p(a), o = p(o), r = p(r), l = p(l), i.drawingArea = Math.min(Math.round(t - (a + o) / 2), Math.round(t - (r + l) / 2)), i.setCenterPoint(a, o, r, l) }, setCenterPoint: function (t, e, n, i) { var a = this, o = a.width - e - a.drawingArea, r = t + a.drawingArea, l = n + a.drawingArea, s = a.height - i - a.drawingArea; a.xCenter = Math.round((r + o) / 2 + a.left), a.yCenter = Math.round((l + s) / 2 + a.top) }, getIndexAngle: function (t) { return t * (2 * Math.PI / e(this)) + (this.chart.options && this.chart.options.startAngle ? this.chart.options.startAngle : 0) * Math.PI * 2 / 360 }, getDistanceFromCenterForValue: function (t) { var e = this; if (null === t) return 0; var n = e.drawingArea / (e.max - e.min); return e.options.ticks.reverse ? (e.max - t) * n : (t - e.min) * n }, getPointPosition: function (t, e) { var n = this, i = n.getIndexAngle(t) - Math.PI / 2; return { x: Math.round(Math.cos(i) * e) + n.xCenter, y: Math.round(Math.sin(i) * e) + n.yCenter } }, getPointPositionForValue: function (t, e) { return this.getPointPosition(t, this.getDistanceFromCenterForValue(e)) }, getBasePosition: function () { var t = this, e = t.min, n = t.max; return t.getPointPositionForValue(0, t.beginAtZero ? 0 : e < 0 && n < 0 ? n : e > 0 && n > 0 ? e : 0) }, draw: function () { var t = this, e = t.options, n = e.gridLines, i = e.ticks, o = a.valueOrDefault; if (e.display) { var r = t.ctx, l = this.getIndexAngle(0), s = o(i.fontSize, v.defaultFontSize), u = o(i.fontStyle, v.defaultFontStyle), d = o(i.fontFamily, v.defaultFontFamily), c = a.fontString(s, u, d); a.each(t.ticks, function (e, a) { if (a > 0 || i.reverse) { var u = t.getDistanceFromCenterForValue(t.ticksAsNumbers[a]); if (n.display && 0 !== a && g(t, n, u, a), i.display) { var d = o(i.fontColor, v.defaultFontColor); if (r.font = c, r.save(), r.translate(t.xCenter, t.yCenter), r.rotate(l), i.showLabelBackdrop) { var h = r.measureText(e).width; r.fillStyle = i.backdropColor, r.fillRect(-h / 2 - i.backdropPaddingX, -u - s / 2 - i.backdropPaddingY, h + 2 * i.backdropPaddingX, s + 2 * i.backdropPaddingY) } r.textAlign = "center", r.textBaseline = "middle", r.fillStyle = d, r.fillText(e, 0, -u), r.restore() } } }), (e.angleLines.display || e.pointLabels.display) && f(t) } } }); t.scaleService.registerScaleType("radialLinear", b, m) } }, { 25: 25, 34: 34, 45: 45 }], 57: [function (t, e, n) { "use strict"; function i(t, e) { return t - e } function a(t) { var e, n, i, a = {}, o = []; for (e = 0, n = t.length; e < n; ++e)a[i = t[e]] || (a[i] = !0, o.push(i)); return o } function o(t, e, n, i) { if ("linear" === i || !t.length) return [{ time: e, pos: 0 }, { time: n, pos: 1 }]; var a, o, r, l, s, u = [], d = [e]; for (a = 0, o = t.length; a < o; ++a)(l = t[a]) > e && l < n && d.push(l); for (d.push(n), a = 0, o = d.length; a < o; ++a)s = d[a + 1], r = d[a - 1], l = d[a], void 0 !== r && void 0 !== s && Math.round((s + r) / 2) === l || u.push({ time: l, pos: a / (o - 1) }); return u } function r(t, e, n) { for (var i, a, o, r = 0, l = t.length - 1; r >= 0 && r <= l;) { if (i = r + l >> 1, a = t[i - 1] || null, o = t[i], !a) return { lo: null, hi: o }; if (o[e] < n) r = i + 1; else { if (!(a[e] > n)) return { lo: a, hi: o }; l = i - 1 } } return { lo: o, hi: null } } function l(t, e, n, i) { var a = r(t, e, n), o = a.lo ? a.hi ? a.lo : t[t.length - 2] : t[0], l = a.lo ? a.hi ? a.hi : t[t.length - 1] : t[1], s = l[e] - o[e], u = s ? (n - o[e]) / s : 0, d = (l[i] - o[i]) * u; return o[i] + d } function s(t, e) { var n = e.parser, i = e.parser || e.format; return "function" == typeof n ? n(t) : "string" == typeof t && "string" == typeof i ? v(t, i) : (t instanceof v || (t = v(t)), t.isValid() ? t : "function" == typeof i ? i(t) : t) } function u(t, e) { if (b.isNullOrUndef(t)) return null; var n = e.options.time, i = s(e.getRightValue(t), n); return i.isValid() ? (n.round && i.startOf(n.round), i.valueOf()) : null } function d(t, e, n, i) { var a, o, r, l = e - t, s = k[n], u = s.size, d = s.steps; if (!d) return Math.ceil(l / ((i || 1) * u)); for (a = 0, o = d.length; a < o && (r = d[a], !(Math.ceil(l / (u * r)) <= i)); ++a); return r } function c(t, e, n, i) { var a, o, r, l = w.length; for (a = w.indexOf(t); a < l - 1; ++a)if (o = k[w[a]], r = o.steps ? o.steps[o.steps.length - 1] : y, Math.ceil((n - e) / (r * o.size)) <= i) return w[a]; return w[l - 1] } function h(t) { for (var e = w.indexOf(t) + 1, n = w.length; e < n; ++e)if (k[w[e]].major) return w[e] } function f(t, e, n, i, a, o) { var r, l = o.time, s = b.valueOrDefault(l.stepSize, l.unitStepSize), u = "week" === n && l.isoWeekday, c = o.ticks.major.enabled, h = k[n], f = v(t), g = v(e), p = []; for (s || (s = d(t, e, n, a)), u && (f = f.isoWeekday(u), g = g.isoWeekday(u)), f = f.startOf(u ? "day" : n), (g = g.startOf(u ? "day" : n)) < e && g.add(1, n), r = v(f), c && i && !u && !l.round && (r.startOf(i), r.add(~~((f - r) / (h.size * s)) * s, n)); r < g; r.add(s, n))p.push(+r); return p.push(+r), p } function g(t, e, n, i, a) { var o, r, s = 0, u = 0; return a.offset && e.length && (a.time.min || (o = e.length > 1 ? e[1] : i, r = e[0], s = (l(t, "time", o, "pos") - l(t, "time", r, "pos")) / 2), a.time.max || (o = e[e.length - 1], r = e.length > 1 ? e[e.length - 2] : n, u = (l(t, "time", o, "pos") - l(t, "time", r, "pos")) / 2)), { left: s, right: u } } function p(t, e) { var n, i, a, o, r = []; for (n = 0, i = t.length; n < i; ++n)a = t[n], o = !!e && a === +v(a).startOf(e), r.push({ value: a, major: o }); return r } var v = t(1); v = "function" == typeof v ? v : window.moment; var m = t(25), b = t(45), x = Number.MIN_SAFE_INTEGER || -9007199254740991, y = Number.MAX_SAFE_INTEGER || 9007199254740991, k = { millisecond: { major: !0, size: 1, steps: [1, 2, 5, 10, 20, 50, 100, 250, 500] }, second: { major: !0, size: 1e3, steps: [1, 2, 5, 10, 30] }, minute: { major: !0, size: 6e4, steps: [1, 2, 5, 10, 30] }, hour: { major: !0, size: 36e5, steps: [1, 2, 3, 6, 12] }, day: { major: !0, size: 864e5, steps: [1, 2, 5] }, week: { major: !1, size: 6048e5, steps: [1, 2, 3, 4] }, month: { major: !0, size: 2628e6, steps: [1, 2, 3] }, quarter: { major: !1, size: 7884e6, steps: [1, 2, 3, 4] }, year: { major: !0, size: 3154e7 } }, w = Object.keys(k); e.exports = function (t) { var e = { position: "bottom", distribution: "linear", bounds: "data", time: { parser: !1, format: !1, unit: !1, round: !1, displayFormat: !1, isoWeekday: !1, minUnit: "millisecond", displayFormats: { millisecond: "h:mm:ss.SSS a", second: "h:mm:ss a", minute: "h:mm a", hour: "hA", day: "MMM D", week: "ll", month: "MMM YYYY", quarter: "[Q]Q - YYYY", year: "YYYY" } }, ticks: { autoSkip: !1, source: "auto", major: { enabled: !1 } } }, n = t.Scale.extend({ initialize: function () { if (!v) throw new Error("Chart.js - Moment.js could not be found! You must include it before Chart.js to use the time scale. Download at https://momentjs.com"); this.mergeTicksOptions(), t.Scale.prototype.initialize.call(this) }, update: function () { var e = this, n = e.options; return n.time && n.time.format && console.warn("options.time.format is deprecated and replaced by options.time.parser."), t.Scale.prototype.update.apply(e, arguments) }, getRightValue: function (e) { return e && void 0 !== e.t && (e = e.t), t.Scale.prototype.getRightValue.call(this, e) }, determineDataLimits: function () { var t, e, n, o, r, l, s = this, d = s.chart, c = s.options.time, h = u(c.min, s) || y, f = u(c.max, s) || x, g = [], p = [], m = []; for (t = 0, n = d.data.labels.length; t < n; ++t)m.push(u(d.data.labels[t], s)); for (t = 0, n = (d.data.datasets || []).length; t < n; ++t)if (d.isDatasetVisible(t)) if (r = d.data.datasets[t].data, b.isObject(r[0])) for (p[t] = [], e = 0, o = r.length; e < o; ++e)l = u(r[e], s), g.push(l), p[t][e] = l; else g.push.apply(g, m), p[t] = m.slice(0); else p[t] = []; m.length && (m = a(m).sort(i), h = Math.min(h, m[0]), f = Math.max(f, m[m.length - 1])), g.length && (g = a(g).sort(i), h = Math.min(h, g[0]), f = Math.max(f, g[g.length - 1])), h = h === y ? +v().startOf("day") : h, f = f === x ? +v().endOf("day") + 1 : f, s.min = Math.min(h, f), s.max = Math.max(h + 1, f), s._horizontal = s.isHorizontal(), s._table = [], s._timestamps = { data: g, datasets: p, labels: m } }, buildTicks: function () { var t, e, n, i = this, a = i.min, r = i.max, l = i.options, s = l.time, d = s.displayFormats, v = i.getLabelCapacity(a), m = s.unit || c(s.minUnit, a, r, v), b = h(m), x = [], y = []; switch (l.ticks.source) { case "data": x = i._timestamps.data; break; case "labels": x = i._timestamps.labels; break; case "auto": default: x = f(a, r, m, b, v, l) }for ("ticks" === l.bounds && x.length && (a = x[0], r = x[x.length - 1]), a = u(s.min, i) || a, r = u(s.max, i) || r, t = 0, e = x.length; t < e; ++t)(n = x[t]) >= a && n <= r && y.push(n); return i.min = a, i.max = r, i._unit = m, i._majorUnit = b, i._minorFormat = d[m], i._majorFormat = d[b], i._table = o(i._timestamps.data, a, r, l.distribution), i._offsets = g(i._table, y, a, r, l), p(y, b) }, getLabelForIndex: function (t, e) { var n = this, i = n.chart.data, a = n.options.time, o = i.labels && t < i.labels.length ? i.labels[t] : "", r = i.datasets[e].data[t]; return b.isObject(r) && (o = n.getRightValue(r)), a.tooltipFormat && (o = s(o, a).format(a.tooltipFormat)), o }, tickFormatFunction: function (t, e, n) { var i = this, a = i.options, o = t.valueOf(), r = i._majorUnit, l = i._majorFormat, s = t.clone().startOf(i._majorUnit).valueOf(), u = a.ticks.major, d = u.enabled && r && l && o === s, c = t.format(d ? l : i._minorFormat), h = d ? u : a.ticks.minor, f = b.valueOrDefault(h.callback, h.userCallback); return f ? f(c, e, n) : c }, convertTicksToLabels: function (t) { var e, n, i = []; for (e = 0, n = t.length; e < n; ++e)i.push(this.tickFormatFunction(v(t[e].value), e, t)); return i }, getPixelForOffset: function (t) { var e = this, n = e._horizontal ? e.width : e.height, i = e._horizontal ? e.left : e.top, a = l(e._table, "time", t, "pos"); return i + n * (e._offsets.left + a) / (e._offsets.left + 1 + e._offsets.right) }, getPixelForValue: function (t, e, n) { var i = this, a = null; if (void 0 !== e && void 0 !== n && (a = i._timestamps.datasets[n][e]), null === a && (a = u(t, i)), null !== a) return i.getPixelForOffset(a) }, getPixelForTick: function (t) { var e = this.getTicks(); return t >= 0 && t < e.length ? this.getPixelForOffset(e[t].value) : null }, getValueForPixel: function (t) { var e = this, n = e._horizontal ? e.width : e.height, i = e._horizontal ? e.left : e.top, a = (n ? (t - i) / n : 0) * (e._offsets.left + 1 + e._offsets.left) - e._offsets.right, o = l(e._table, "pos", a, "time"); return v(o) }, getLabelWidth: function (t) { var e = this, n = e.options.ticks, i = e.ctx.measureText(t).width, a = b.toRadians(n.maxRotation), o = Math.cos(a), r = Math.sin(a); return i * o + b.valueOrDefault(n.fontSize, m.global.defaultFontSize) * r }, getLabelCapacity: function (t) { var e = this; e._minorFormat = e.options.time.displayFormats.millisecond; var n = e.tickFormatFunction(v(t), 0, []), i = e.getLabelWidth(n), a = e.isHorizontal() ? e.width : e.height; return Math.floor(a / i) } }); t.scaleService.registerScaleType("time", n, e) } }, { 1: 1, 25: 25, 45: 45 }] }, {}, [7])(7) });
/**
 * dirPagination - AngularJS module for paginating (almost) anything.
 *
 *
 * Credits
 * =======
 *
 * Daniel Tabuenca: https://groups.google.com/d/msg/angular/an9QpzqIYiM/r8v-3W1X5vcJ
 * for the idea on how to dynamically invoke the ng-repeat directive.
 *
 * I borrowed a couple of lines and a few attribute names from the AngularUI Bootstrap project:
 * https://github.com/angular-ui/bootstrap/blob/master/src/pagination/pagination.js
 *
 * Copyright 2014 Michael Bromley <michael@michaelbromley.co.uk>
 */

(function () {

	/**
	 * Config
	 */
	var moduleName = 'angularUtils.directives.dirPagination';
	var DEFAULT_ID = '__default';

	/**
	 * Module
	 */
	angular.module(moduleName, [])
		.directive('dirPaginate', ['$compile', '$parse', 'paginationService', dirPaginateDirective])
		.directive('dirPaginateNoCompile', noCompileDirective)
		.directive('dirPaginationControls', ['paginationService', 'paginationTemplate', dirPaginationControlsDirective])
		.filter('itemsPerPage', ['paginationService', itemsPerPageFilter])
		.service('paginationService', paginationService)
		.provider('paginationTemplate', paginationTemplateProvider)
		.run(['$templateCache', dirPaginationControlsTemplateInstaller]);

	function dirPaginateDirective($compile, $parse, paginationService) {

		return {
			terminal: true,
			multiElement: true,
			priority: 100,
			compile: dirPaginationCompileFn
		};

		function dirPaginationCompileFn(tElement, tAttrs) {

			var expression = tAttrs.dirPaginate;
			// regex taken directly from https://github.com/angular/angular.js/blob/v1.4.x/src/ng/directive/ngRepeat.js#L339
			var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);

			var filterPattern = /\|\s*itemsPerPage\s*:\s*(.*\(\s*\w*\)|([^\)]*?(?=\s+as\s+))|[^\)]*)/;
			if (match[2].match(filterPattern) === null) {
				throw 'pagination directive: the \'itemsPerPage\' filter must be set.';
			}
			var itemsPerPageFilterRemoved = match[2].replace(filterPattern, '');
			var collectionGetter = $parse(itemsPerPageFilterRemoved);

			addNoCompileAttributes(tElement);

			// If any value is specified for paginationId, we register the un-evaluated expression at this stage for the benefit of any
			// dir-pagination-controls directives that may be looking for this ID.
			var rawId = tAttrs.paginationId || DEFAULT_ID;
			paginationService.registerInstance(rawId);

			return function dirPaginationLinkFn(scope, element, attrs) {

				// Now that we have access to the `scope` we can interpolate any expression given in the paginationId attribute and
				// potentially register a new ID if it evaluates to a different value than the rawId.
				var paginationId = $parse(attrs.paginationId)(scope) || attrs.paginationId || DEFAULT_ID;

				// (TODO: this seems sound, but I'm reverting as many bug reports followed it's introduction in 0.11.0.
				// Needs more investigation.)
				// In case rawId != paginationId we deregister using rawId for the sake of general cleanliness
				// before registering using paginationId
				// paginationService.deregisterInstance(rawId);
				paginationService.registerInstance(paginationId);

				var repeatExpression = getRepeatExpression(expression, paginationId);
				addNgRepeatToElement(element, attrs, repeatExpression);

				removeTemporaryAttributes(element);
				var compiled = $compile(element);

				var currentPageGetter = makeCurrentPageGetterFn(scope, attrs, paginationId);
				paginationService.setCurrentPageParser(paginationId, currentPageGetter, scope);

				if (typeof attrs.totalItems !== 'undefined') {
					paginationService.setAsyncModeTrue(paginationId);
					scope.$watch(function () {
						return $parse(attrs.totalItems)(scope);
					}, function (result) {
						if (0 <= result) {
							paginationService.setCollectionLength(paginationId, result);
						}
					});
				} else {
					paginationService.setAsyncModeFalse(paginationId);
					scope.$watchCollection(function () {
						return collectionGetter(scope);
					}, function (collection) {
						if (collection) {
							var collectionLength = (collection instanceof Array) ? collection.length : Object.keys(collection).length;
							paginationService.setCollectionLength(paginationId, collectionLength);
						}
					});
				}

				// Delegate to the link function returned by the new compilation of the ng-repeat
				compiled(scope);

				// (TODO: Reverting this due to many bug reports in v 0.11.0. Needs investigation as the
				// principle is sound)
				// When the scope is destroyed, we make sure to remove the reference to it in paginationService
				// so that it can be properly garbage collected
				// scope.$on('$destroy', function destroyDirPagination() {
				//     paginationService.deregisterInstance(paginationId);
				// });
			};
		}

		/**
		 * If a pagination id has been specified, we need to check that it is present as the second argument passed to
		 * the itemsPerPage filter. If it is not there, we add it and return the modified expression.
		 *
		 * @param expression
		 * @param paginationId
		 * @returns {*}
		 */
		function getRepeatExpression(expression, paginationId) {
			var repeatExpression,
				idDefinedInFilter = !!expression.match(/(\|\s*itemsPerPage\s*:[^|]*:[^|]*)/);

			if (paginationId !== DEFAULT_ID && !idDefinedInFilter) {
				repeatExpression = expression.replace(/(\|\s*itemsPerPage\s*:\s*[^|\s]*)/, "$1 : '" + paginationId + "'");
			} else {
				repeatExpression = expression;
			}

			return repeatExpression;
		}

		/**
		 * Adds the ng-repeat directive to the element. In the case of multi-element (-start, -end) it adds the
		 * appropriate multi-element ng-repeat to the first and last element in the range.
		 * @param element
		 * @param attrs
		 * @param repeatExpression
		 */
		function addNgRepeatToElement(element, attrs, repeatExpression) {
			if (element[0].hasAttribute('dir-paginate-start') || element[0].hasAttribute('data-dir-paginate-start')) {
				// using multiElement mode (dir-paginate-start, dir-paginate-end)
				attrs.$set('ngRepeatStart', repeatExpression);
				element.eq(element.length - 1).attr('ng-repeat-end', true);
			} else {
				attrs.$set('ngRepeat', repeatExpression);
			}
		}

		/**
		 * Adds the dir-paginate-no-compile directive to each element in the tElement range.
		 * @param tElement
		 */
		function addNoCompileAttributes(tElement) {
			angular.forEach(tElement, function (el) {
				if (el.nodeType === 1) {
					angular.element(el).attr('dir-paginate-no-compile', true);
				}
			});
		}

		/**
		 * Removes the variations on dir-paginate (data-, -start, -end) and the dir-paginate-no-compile directives.
		 * @param element
		 */
		function removeTemporaryAttributes(element) {
			angular.forEach(element, function (el) {
				if (el.nodeType === 1) {
					angular.element(el).removeAttr('dir-paginate-no-compile');
				}
			});
			element.eq(0).removeAttr('dir-paginate-start').removeAttr('dir-paginate').removeAttr('data-dir-paginate-start').removeAttr('data-dir-paginate');
			element.eq(element.length - 1).removeAttr('dir-paginate-end').removeAttr('data-dir-paginate-end');
		}

		/**
		 * Creates a getter function for the current-page attribute, using the expression provided or a default value if
		 * no current-page expression was specified.
		 *
		 * @param scope
		 * @param attrs
		 * @param paginationId
		 * @returns {*}
		 */
		function makeCurrentPageGetterFn(scope, attrs, paginationId) {
			var currentPageGetter;
			if (attrs.currentPage) {
				currentPageGetter = $parse(attrs.currentPage);
			} else {
				// If the current-page attribute was not set, we'll make our own.
				// Replace any non-alphanumeric characters which might confuse
				// the $parse service and give unexpected results.
				// See https://github.com/michaelbromley/angularUtils/issues/233
				var defaultCurrentPage = (paginationId + '__currentPage').replace(/\W/g, '_');
				scope[defaultCurrentPage] = 1;
				currentPageGetter = $parse(defaultCurrentPage);
			}
			return currentPageGetter;
		}
	}

	/**
	 * This is a helper directive that allows correct compilation when in multi-element mode (ie dir-paginate-start, dir-paginate-end).
	 * It is dynamically added to all elements in the dir-paginate compile function, and it prevents further compilation of
	 * any inner directives. It is then removed in the link function, and all inner directives are then manually compiled.
	 */
	function noCompileDirective() {
		return {
			priority: 5000,
			terminal: true
		};
	}
	//ng-if="1 < pages.length || !autoHide"
	// di {{pages.length}}
	function dirPaginationControlsTemplateInstaller($templateCache) {
		//$templateCache.put('angularUtils.directives.dirPagination.template', '<ul class="pagination" ><li ng-if="boundaryLinks" ng-class="{ disabled : pagination.current == 1 }"><a href="" ng-click="setCurrent(1)">&laquo;</a></li><li ng-if="directionLinks" ng-class="{ disabled : pagination.current == 1 }"><a href="" ng-click="setCurrent(pagination.current - 1)">&lsaquo;</a></li><li ng-repeat="pageNumber in pages track by tracker(pageNumber, $index)" ng-class="{ active : pagination.current == pageNumber, disabled : pageNumber == \'...\' || ( ! autoHide && pages.length === 1 ) }"><a href="" ng-click="setCurrent(pageNumber)">{{ pageNumber }}</a></li><li ng-if="directionLinks" ng-class="{ disabled : pagination.current == pagination.last }"><a href="" ng-click="setCurrent(pagination.current + 1)">&rsaquo;</a></li><li ng-if="boundaryLinks"  ng-class="{ disabled : pagination.current == pagination.last }"><a href="" ng-click="setCurrent(pagination.last)">&raquo;</a></li></ul>');


		$templateCache.put('angularUtils.directives.dirPagination.template', '<ul class="pagination" ><li ng-if="boundaryLinks" ng-class="{ disabledItem : pagination.current == 1 }"><a href="" ng-click="setCurrent(1)">&laquo;</a></li><li ng-if="directionLinks" ng-class="{ disabledItem : pagination.current == 1 }"><a href="" ng-click="setCurrent(pagination.current - 1)">&lsaquo;</a></li><li class="active">{{ pagination.current }} di {{pagination.last}}</li><li ng-if="directionLinks" ng-class="{ disabledItem : pagination.current == pagination.last }"><a href="" ng-click="setCurrent(pagination.current + 1)">&rsaquo;</a></li><li ng-if="boundaryLinks"  ng-class="{ disabledItem : pagination.current == pagination.last }"><a href="" ng-click="setCurrent(pagination.last)">&raquo;</a></li></ul>');
	}

	function dirPaginationControlsDirective(paginationService, paginationTemplate) {

		var numberRegex = /^\d+$/;

		var DDO = {
			restrict: 'AE',
			scope: {
				maxSize: '=?',
				onPageChange: '&?',
				paginationId: '=?',
				autoHide: '=?'
			},
			link: dirPaginationControlsLinkFn
		};

		// We need to check the paginationTemplate service to see whether a template path or
		// string has been specified, and add the `template` or `templateUrl` property to
		// the DDO as appropriate. The order of priority to decide which template to use is
		// (highest priority first):
		// 1. paginationTemplate.getString()
		// 2. attrs.templateUrl
		// 3. paginationTemplate.getPath()
		var templateString = paginationTemplate.getString();
		if (templateString !== undefined) {
			DDO.template = templateString;
		} else {
			DDO.templateUrl = function (elem, attrs) {
				return attrs.templateUrl || paginationTemplate.getPath();
			};
		}
		return DDO;

		function dirPaginationControlsLinkFn(scope, element, attrs) {

			// rawId is the un-interpolated value of the pagination-id attribute. This is only important when the corresponding dir-paginate directive has
			// not yet been linked (e.g. if it is inside an ng-if block), and in that case it prevents this controls directive from assuming that there is
			// no corresponding dir-paginate directive and wrongly throwing an exception.
			var rawId = attrs.paginationId || DEFAULT_ID;
			var paginationId = scope.paginationId || attrs.paginationId || DEFAULT_ID;

			if (!paginationService.isRegistered(paginationId) && !paginationService.isRegistered(rawId)) {
				var idMessage = (paginationId !== DEFAULT_ID) ? ' (id: ' + paginationId + ') ' : ' ';
				if (window.console) {
					console.warn('Pagination directive: the pagination controls' + idMessage + 'cannot be used without the corresponding pagination directive, which was not found at link time.');
				}
			}

			if (!scope.maxSize) { scope.maxSize = 9; }
			scope.autoHide = scope.autoHide === undefined ? true : scope.autoHide;
			scope.directionLinks = angular.isDefined(attrs.directionLinks) ? scope.$parent.$eval(attrs.directionLinks) : true;
			scope.boundaryLinks = angular.isDefined(attrs.boundaryLinks) ? scope.$parent.$eval(attrs.boundaryLinks) : false;

			var paginationRange = Math.max(scope.maxSize, 5);
			scope.pages = [];
			scope.pagination = {
				last: 1,
				current: 1
			};
			scope.range = {
				lower: 1,
				upper: 1,
				total: 1
			};

			scope.$watch('maxSize', function (val) {
				if (val) {
					paginationRange = Math.max(scope.maxSize, 5);
					generatePagination();
				}
			});

			scope.$watch(function () {
				if (paginationService.isRegistered(paginationId)) {
					return (paginationService.getCollectionLength(paginationId) + 1) * paginationService.getItemsPerPage(paginationId);
				}
			}, function (length) {
				if (0 < length) {
					generatePagination();
				}
			});

			scope.$watch(function () {
				if (paginationService.isRegistered(paginationId)) {
					return (paginationService.getItemsPerPage(paginationId));
				}
			}, function (current, previous) {
				if (current != previous && typeof previous !== 'undefined') {
					goToPage(scope.pagination.current);
				}
			});

			scope.$watch(function () {
				if (paginationService.isRegistered(paginationId)) {
					return paginationService.getCurrentPage(paginationId);
				}
			}, function (currentPage, previousPage) {
				if (currentPage != previousPage) {
					goToPage(currentPage);
				}
			});

			scope.setCurrent = function (num) {
				if (paginationService.isRegistered(paginationId) && isValidPageNumber(num)) {
					num = parseInt(num, 10);
					paginationService.setCurrentPage(paginationId, num);
				}
			};

			/**
			 * Custom "track by" function which allows for duplicate "..." entries on long lists,
			 * yet fixes the problem of wrongly-highlighted links which happens when using
			 * "track by $index" - see https://github.com/michaelbromley/angularUtils/issues/153
			 * @param id
			 * @param index
			 * @returns {string}
			 */
			scope.tracker = function (id, index) {
				return id + '_' + index;
			};

			function goToPage(num) {
				if (paginationService.isRegistered(paginationId) && isValidPageNumber(num)) {
					var oldPageNumber = scope.pagination.current;

					scope.pages = generatePagesArray(num, paginationService.getCollectionLength(paginationId), paginationService.getItemsPerPage(paginationId), paginationRange);
					scope.pagination.current = num;
					updateRangeValues();

					// if a callback has been set, then call it with the page number as the first argument
					// and the previous page number as a second argument
					if (scope.onPageChange) {
						scope.onPageChange({
							newPageNumber: num,
							oldPageNumber: oldPageNumber
						});
					}
				}
			}

			function generatePagination() {
				if (paginationService.isRegistered(paginationId)) {
					var page = parseInt(paginationService.getCurrentPage(paginationId)) || 1;
					scope.pages = generatePagesArray(page, paginationService.getCollectionLength(paginationId), paginationService.getItemsPerPage(paginationId), paginationRange);
					scope.pagination.current = page;
					scope.pagination.last = scope.pages[scope.pages.length - 1];
					if (scope.pagination.last < scope.pagination.current) {
						scope.setCurrent(scope.pagination.last);
					} else {
						updateRangeValues();
					}
				}
			}

			/**
			 * This function updates the values (lower, upper, total) of the `scope.range` object, which can be used in the pagination
			 * template to display the current page range, e.g. "showing 21 - 40 of 144 results";
			 */
			function updateRangeValues() {
				if (paginationService.isRegistered(paginationId)) {
					var currentPage = paginationService.getCurrentPage(paginationId),
						itemsPerPage = paginationService.getItemsPerPage(paginationId),
						totalItems = paginationService.getCollectionLength(paginationId);

					scope.range.lower = (currentPage - 1) * itemsPerPage + 1;
					scope.range.upper = Math.min(currentPage * itemsPerPage, totalItems);
					scope.range.total = totalItems;
				}
			}
			function isValidPageNumber(num) {
				return (numberRegex.test(num) && (0 < num && num <= scope.pagination.last));
			}
		}

		/**
		 * Generate an array of page numbers (or the '...' string) which is used in an ng-repeat to generate the
		 * links used in pagination
		 *
		 * @param currentPage
		 * @param rowsPerPage
		 * @param paginationRange
		 * @param collectionLength
		 * @returns {Array}
		 */
		function generatePagesArray(currentPage, collectionLength, rowsPerPage, paginationRange) {
			var pages = [];
			var totalPages = Math.ceil(collectionLength / rowsPerPage);
			var halfWay = Math.ceil(paginationRange / 2);
			var position;

			if (currentPage <= halfWay) {
				position = 'start';
			} else if (totalPages - halfWay < currentPage) {
				position = 'end';
			} else {
				position = 'middle';
			}

			var ellipsesNeeded = paginationRange < totalPages;
			var i = 1;
			while (i <= totalPages && i <= paginationRange) {
				var pageNumber = calculatePageNumber(i, currentPage, paginationRange, totalPages);

				var openingEllipsesNeeded = (i === 2 && (position === 'middle' || position === 'end'));
				var closingEllipsesNeeded = (i === paginationRange - 1 && (position === 'middle' || position === 'start'));
				if (ellipsesNeeded && (openingEllipsesNeeded || closingEllipsesNeeded)) {
					pages.push('...');
				} else {
					pages.push(pageNumber);
				}
				i++;
			}
			return pages;
		}

		/**
		 * Given the position in the sequence of pagination links [i], figure out what page number corresponds to that position.
		 *
		 * @param i
		 * @param currentPage
		 * @param paginationRange
		 * @param totalPages
		 * @returns {*}
		 */
		function calculatePageNumber(i, currentPage, paginationRange, totalPages) {
			var halfWay = Math.ceil(paginationRange / 2);
			if (i === paginationRange) {
				return totalPages;
			} else if (i === 1) {
				return i;
			} else if (paginationRange < totalPages) {
				if (totalPages - halfWay < currentPage) {
					return totalPages - paginationRange + i;
				} else if (halfWay < currentPage) {
					return currentPage - halfWay + i;
				} else {
					return i;
				}
			} else {
				return i;
			}
		}
	}

	/**
	 * This filter slices the collection into pages based on the current page number and number of items per page.
	 * @param paginationService
	 * @returns {Function}
	 */
	function itemsPerPageFilter(paginationService) {

		return function (collection, itemsPerPage, paginationId) {
			if (typeof (paginationId) === 'undefined') {
				paginationId = DEFAULT_ID;
			}
			if (!paginationService.isRegistered(paginationId)) {
				throw 'pagination directive: the itemsPerPage id argument (id: ' + paginationId + ') does not match a registered pagination-id.';
			}
			var end;
			var start;
			if (angular.isObject(collection)) {
				itemsPerPage = parseInt(itemsPerPage) || 9999999999;
				if (paginationService.isAsyncMode(paginationId)) {
					start = 0;
				} else {
					start = (paginationService.getCurrentPage(paginationId) - 1) * itemsPerPage;
				}
				end = start + itemsPerPage;
				paginationService.setItemsPerPage(paginationId, itemsPerPage);

				if (collection instanceof Array) {
					// the array just needs to be sliced
					return collection.slice(start, end);
				} else {
					// in the case of an object, we need to get an array of keys, slice that, then map back to
					// the original object.
					var slicedObject = {};
					angular.forEach(keys(collection).slice(start, end), function (key) {
						slicedObject[key] = collection[key];
					});
					return slicedObject;
				}
			} else {
				return collection;
			}
		};
	}

	/**
	 * Shim for the Object.keys() method which does not exist in IE < 9
	 * @param obj
	 * @returns {Array}
	 */
	function keys(obj) {
		if (!Object.keys) {
			var objKeys = [];
			for (var i in obj) {
				if (obj.hasOwnProperty(i)) {
					objKeys.push(i);
				}
			}
			return objKeys;
		} else {
			return Object.keys(obj);
		}
	}

	/**
	 * This service allows the various parts of the module to communicate and stay in sync.
	 */
	function paginationService() {

		var instances = {};
		var lastRegisteredInstance;

		this.registerInstance = function (instanceId) {
			if (typeof instances[instanceId] === 'undefined') {
				instances[instanceId] = {
					asyncMode: false
				};
				lastRegisteredInstance = instanceId;
			}
		};

		this.deregisterInstance = function (instanceId) {
			delete instances[instanceId];
		};

		this.isRegistered = function (instanceId) {
			return (typeof instances[instanceId] !== 'undefined');
		};

		this.getLastInstanceId = function () {
			return lastRegisteredInstance;
		};

		this.setCurrentPageParser = function (instanceId, val, scope) {
			instances[instanceId].currentPageParser = val;
			instances[instanceId].context = scope;
		};
		this.setCurrentPage = function (instanceId, val) {
			instances[instanceId].currentPageParser.assign(instances[instanceId].context, val);
		};
		this.getCurrentPage = function (instanceId) {
			var parser = instances[instanceId].currentPageParser;
			return parser ? parser(instances[instanceId].context) : 1;
		};

		this.setItemsPerPage = function (instanceId, val) {
			instances[instanceId].itemsPerPage = val;
		};
		this.getItemsPerPage = function (instanceId) {
			return instances[instanceId].itemsPerPage;
		};

		this.setCollectionLength = function (instanceId, val) {
			instances[instanceId].collectionLength = val;
		};
		this.getCollectionLength = function (instanceId) {
			return instances[instanceId].collectionLength;
		};

		this.setAsyncModeTrue = function (instanceId) {
			instances[instanceId].asyncMode = true;
		};

		this.setAsyncModeFalse = function (instanceId) {
			instances[instanceId].asyncMode = false;
		};

		this.isAsyncMode = function (instanceId) {
			return instances[instanceId].asyncMode;
		};
	}

	/**
	 * This provider allows global configuration of the template path used by the dir-pagination-controls directive.
	 */
	function paginationTemplateProvider() {

		var templatePath = 'angularUtils.directives.dirPagination.template';
		var templateString;

		/**
		 * Set a templateUrl to be used by all instances of <dir-pagination-controls>
		 * @param {String} path
		 */
		this.setPath = function (path) {
			templatePath = path;
		};

		/**
		 * Set a string of HTML to be used as a template by all instances
		 * of <dir-pagination-controls>. If both a path *and* a string have been set,
		 * the string takes precedence.
		 * @param {String} str
		 */
		this.setString = function (str) {
			templateString = str;
		};

		this.$get = function () {
			return {
				getPath: function () {
					return templatePath;
				},
				getString: function () {
					return templateString;
				}
			};
		};
	}
})();

!function (e) { "use strict"; function a() { return "ondrag" in document.createElement("a") } function n(e) { e.originalEvent && (e.dataTransfer = e.originalEvent.dataTransfer), "undefined" != typeof e.dataTransfer && "none" === e.dataTransfer.dropEffect && ("copy" === e.dataTransfer.effectAllowed || "move" === e.dataTransfer.effectAllowed ? e.dataTransfer.dropEffect = e.dataTransfer.effectAllowed : "copyMove" !== e.dataTransfer.effectAllowed && "copymove" !== e.dataTransfer.effectAllowed || (e.dataTransfer.dropEffect = e.ctrlKey ? "copy" : "move")) } if (!a()) return void e.module("ang-drag-drop", []); var r = e.module("ang-drag-drop", []); r.directive("uiDraggable", ["$parse", "$rootScope", "$dragImage", function (a, r, t) { return function (o, d, f) { function i(e) { e.originalEvent && (e.dataTransfer = e.originalEvent.dataTransfer), setTimeout(function () { d.unbind("$destroy", i) }, 0); var t = f.dragChannel || "defaultchannel"; if (r.$broadcast("ANGULAR_DRAG_END", e, t), n(e), e.dataTransfer && "none" !== e.dataTransfer.dropEffect) { if (f.onDropSuccess) { var s = a(f.onDropSuccess); o.$evalAsync(function () { s(o, { $event: e }) }) } } else if (e.dataTransfer && "none" === e.dataTransfer.dropEffect && f.onDropFailure) { var l = a(f.onDropFailure); o.$evalAsync(function () { l(o, { $event: e }) }) } d.removeClass(c) } function s(n, r) { var t; n.originalEvent && (n.dataTransfer = n.originalEvent.dataTransfer), t = a(r), o.$apply(function () { var a, r = t(o, { $event: n }); r && e.isString(r) && (a = document.getElementById(r), a && n.dataTransfer.setDragImage(a, 0, 0)) }) } function l(n) { n.originalEvent && (n.dataTransfer = n.originalEvent.dataTransfer); var l = !v || u.classList.contains(g); if (l) { var p = f.dragChannel || "defaultchannel", $ = ""; f.drag && ($ = o.$eval(f.drag)); var m = f.dragImage || null; d.addClass(c), d.bind("$destroy", i); var T = !(document.uniqueID || window.opera); if (m && T) { var h = a(f.dragImage); o.$apply(function () { var a = h(o, { $event: n }); if (a && (e.isString(a) && (a = t.generate(a)), a.image)) { var r = a.xOffset || 0, d = a.yOffset || 0; n.dataTransfer.setDragImage(a.image, r, d) } }) } else f.dragImageElementId && s(n, f.dragImageElementId); var b = { x: n.offsetX, y: n.offsetY }, D = { data: $, channel: p, offset: b }, y = e.toJson(D); n.dataTransfer.setData("text", y), n.dataTransfer.effectAllowed = "copyMove", r.$broadcast("ANGULAR_DRAG_START", n, p, D) } else n.preventDefault() } var g, u, v = !1, c = f.draggingClass || "on-dragging"; d.attr("draggable", !1), o.$watch(f.uiDraggable, function (e) { e ? (d.attr("draggable", e), d.bind("dragend", i), d.bind("dragstart", l)) : (d.removeAttr("draggable"), d.unbind("dragend", i), d.unbind("dragstart", l)) }), e.isString(f.dragHandleClass) && (v = !0, g = f.dragHandleClass.trim() || "drag-handle", d.bind("mousedown", function (e) { u = e.target })) } }]), r.directive("uiOnDrop", ["$parse", "$rootScope", function (a, r) { return function (t, o, d) { function f(e) { for (var a = { x: e.offsetX, y: e.offsetY }, n = e.target; n !== o[0];)if (a.x = a.x + n.offsetLeft, a.y = a.y + n.offsetTop, n = n.offsetParent, !n) return null; return a } function i(e) { e.preventDefault && e.preventDefault(), e.stopPropagation && e.stopPropagation(); var n = a(d.uiOnDragOver); return t.$evalAsync(function () { n(t, { $event: e, $channel: p }) }), !1 } function s(e) { e.preventDefault && e.preventDefault(), e.stopPropagation && e.stopPropagation(), c--, 0 === c && (t.$evalAsync(function () { b(t, { $event: e, $channel: p }) }), o.addClass(m), o.removeClass(T)); var n = a(d.uiOnDragLeave); t.$evalAsync(function () { n(t, { $event: e, $channel: p }) }) } function l(e) { e.preventDefault && e.preventDefault(), e.stopPropagation && e.stopPropagation(), 0 === c && (t.$evalAsync(function () { h(t, { $event: e, $channel: p }) }), o.removeClass(m), o.addClass(T)), c++; var n = a(d.uiOnDragEnter); t.$evalAsync(function () { n(t, { $event: e, $channel: p }) }), r.$broadcast("ANGULAR_HOVER", $) } function g(r) { r.originalEvent && (r.dataTransfer = r.originalEvent.dataTransfer), r.preventDefault && r.preventDefault(), r.stopPropagation && r.stopPropagation(); var i = r.dataTransfer.getData("text"); i = e.fromJson(i); var s = f(r), l = s ? { x: s.x - i.offset.x, y: s.y - i.offset.y } : null; n(r); var g = a(d.uiOnDrop); t.$evalAsync(function () { g(t, { $data: i.data, $event: r, $channel: i.channel, $position: l }) }), o.removeClass(m), c = 0 } function u(e, a) { if ("*" === a) return !0; var n = new RegExp("(\\s|[,])+(" + e + ")(\\s|[,])+", "i"); return n.test("," + a + ",") } function v(e) { return e.originalEvent && (e.dataTransfer = e.originalEvent.dataTransfer), e.preventDefault && e.preventDefault(), e.stopPropagation && e.stopPropagation(), e.dataTransfer.dropEffect = "none", !1 } var c = 0, p = d.dropChannel || "defaultchannel", $ = "", m = d.dragEnterClass || "on-drag-enter", T = d.dragHoverClass || "on-drag-hover", h = a(d.onDragEnter), b = a(d.onDragLeave), D = r.$on("ANGULAR_DRAG_START", function (e, n, r, f) { $ = r; var c = !0; if (u(r, p) || (c = !1), c && d.dropValidate) { var T = a(d.dropValidate); c = T(t, { $drop: { scope: t, element: o }, $event: n, $data: f.data, $channel: f.channel }) } c ? (o.bind("dragover", i), o.bind("dragenter", l), o.bind("dragleave", s), o.bind("drop", g), o.addClass(m)) : (o.bind("dragover", v), o.bind("dragenter", v), o.bind("dragleave", v), o.bind("drop", v), o.removeClass(m)) }), y = r.$on("ANGULAR_DRAG_END", function () { o.unbind("dragover", i), o.unbind("dragenter", l), o.unbind("dragleave", s), o.unbind("drop", g), o.removeClass(T), o.removeClass(m), o.unbind("dragover", v), o.unbind("dragenter", v), o.unbind("dragleave", v), o.unbind("drop", v) }); t.$on("$destroy", function () { D(), y() }), d.$observe("dropChannel", function (e) { e && (p = e) }) } }]), r.constant("$dragImageConfig", { height: 20, width: 200, padding: 10, font: "bold 11px Arial", fontColor: "#eee8d5", backgroundColor: "#93a1a1", xOffset: 0, yOffset: 0 }), r.service("$dragImage", ["$dragImageConfig", function (a) { function n(e, a, n) { var t = e.measureText(a).width; if (t < n.width) return a; for (; t + n.padding > n.width;)a = a.substring(0, a.length - 1), t = e.measureText(a + r).width; return a + r } var r = "…"; this.generate = function (r, t) { var o = e.extend({}, a, t || {}), d = document.createElement("canvas"); d.height = o.height, d.width = o.width; var f = d.getContext("2d"); f.fillStyle = o.backgroundColor, f.fillRect(0, 0, o.width, o.height), f.font = o.font, f.fillStyle = o.fontColor; var i = n(f, r, o); f.fillText(i, 4, o.padding + 4); var s = new Image; return s.src = d.toDataURL(), { image: s, xOffset: o.xOffset, yOffset: o.yOffset } } }]) }(angular);
/*
 jquery.fullscreen 1.1.5
 https://github.com/code-lts/jquery-fullscreen-plugin
 Copyright (C) 2012-2013 Klaus Reimer <k@ailis.de>
 Licensed under the MIT license
 (See http://www.opensource.org/licenses/mit-license)
*/
!function (e) { function l(e) { var l, r, c; return this.length ? ((l = this[0]).ownerDocument ? c = l.ownerDocument : l = (c = l).documentElement, null == e ? c.exitFullscreen || c.webkitExitFullscreen || c.webkitCancelFullScreen || c.msExitFullscreen || c.mozCancelFullScreen ? (e = n(c)) && (c.fullscreenElement || c.webkitFullscreenElement || c.webkitCurrentFullScreenElement || c.msFullscreenElement || c.mozFullScreenElement) || e : null : e ? ((r = l.requestFullscreen || l.webkitRequestFullscreen || l.webkitRequestFullScreen || l.msRequestFullscreen || l.mozRequestFullScreen) && r.call(l), this) : ((r = c.exitFullscreen || c.webkitExitFullscreen || c.webkitCancelFullScreen || c.msExitFullscreen || c.mozCancelFullScreen) && n(c) && r.call(c), this)) : this } function n(e) { return !!(e.fullscreenElement || e.msFullscreenElement || e.webkitIsFullScreen || e.mozFullScreen) } function r(l) { e(document).trigger(new e.Event("fullscreenchange")) } function c(l) { e(document).trigger(new e.Event("fullscreenerror")) } var u, t, s; e.fn.fullScreen = l, e.fn.toggleFullScreen = function () { return l.call(this, !l.call(this)) }, (u = document).webkitCancelFullScreen ? (t = "webkitfullscreenchange", s = "webkitfullscreenerror") : u.msExitFullscreen ? (t = "MSFullscreenChange", s = "MSFullscreenError") : u.mozCancelFullScreen ? (t = "mozfullscreenchange", s = "mozfullscreenerror") : (t = "fullscreenchange", s = "fullscreenerror"), e(document).bind(t, r), e(document).bind(s, c) }(jQuery);
/* == jquery mousewheel plugin == Version: 3.1.13, License: MIT License (MIT) */
!function (a) { "function" == typeof define && define.amd ? define(["jquery"], a) : "object" == typeof exports ? module.exports = a : a(jQuery) }(function (a) { function b(b) { var g = b || window.event, h = i.call(arguments, 1), j = 0, l = 0, m = 0, n = 0, o = 0, p = 0; if (b = a.event.fix(g), b.type = "mousewheel", "detail" in g && (m = -1 * g.detail), "wheelDelta" in g && (m = g.wheelDelta), "wheelDeltaY" in g && (m = g.wheelDeltaY), "wheelDeltaX" in g && (l = -1 * g.wheelDeltaX), "axis" in g && g.axis === g.HORIZONTAL_AXIS && (l = -1 * m, m = 0), j = 0 === m ? l : m, "deltaY" in g && (m = -1 * g.deltaY, j = m), "deltaX" in g && (l = g.deltaX, 0 === m && (j = -1 * l)), 0 !== m || 0 !== l) { if (1 === g.deltaMode) { var q = a.data(this, "mousewheel-line-height"); j *= q, m *= q, l *= q } else if (2 === g.deltaMode) { var r = a.data(this, "mousewheel-page-height"); j *= r, m *= r, l *= r } if (n = Math.max(Math.abs(m), Math.abs(l)), (!f || f > n) && (f = n, d(g, n) && (f /= 40)), d(g, n) && (j /= 40, l /= 40, m /= 40), j = Math[j >= 1 ? "floor" : "ceil"](j / f), l = Math[l >= 1 ? "floor" : "ceil"](l / f), m = Math[m >= 1 ? "floor" : "ceil"](m / f), k.settings.normalizeOffset && this.getBoundingClientRect) { var s = this.getBoundingClientRect(); o = b.clientX - s.left, p = b.clientY - s.top } return b.deltaX = l, b.deltaY = m, b.deltaFactor = f, b.offsetX = o, b.offsetY = p, b.deltaMode = 0, h.unshift(b, j, l, m), e && clearTimeout(e), e = setTimeout(c, 200), (a.event.dispatch || a.event.handle).apply(this, h) } } function c() { f = null } function d(a, b) { return k.settings.adjustOldDeltas && "mousewheel" === a.type && b % 120 === 0 } var e, f, g = ["wheel", "mousewheel", "DOMMouseScroll", "MozMousePixelScroll"], h = "onwheel" in document || document.documentMode >= 9 ? ["wheel"] : ["mousewheel", "DomMouseScroll", "MozMousePixelScroll"], i = Array.prototype.slice; if (a.event.fixHooks) for (var j = g.length; j;)a.event.fixHooks[g[--j]] = a.event.mouseHooks; var k = a.event.special.mousewheel = { version: "3.1.12", setup: function () { if (this.addEventListener) for (var c = h.length; c;)this.addEventListener(h[--c], b, !1); else this.onmousewheel = b; a.data(this, "mousewheel-line-height", k.getLineHeight(this)), a.data(this, "mousewheel-page-height", k.getPageHeight(this)) }, teardown: function () { if (this.removeEventListener) for (var c = h.length; c;)this.removeEventListener(h[--c], b, !1); else this.onmousewheel = null; a.removeData(this, "mousewheel-line-height"), a.removeData(this, "mousewheel-page-height") }, getLineHeight: function (b) { var c = a(b), d = c["offsetParent" in a.fn ? "offsetParent" : "parent"](); return d.length || (d = a("body")), parseInt(d.css("fontSize"), 10) || parseInt(c.css("fontSize"), 10) || 16 }, getPageHeight: function (b) { return a(b).height() }, settings: { adjustOldDeltas: !0, normalizeOffset: !0 } }; a.fn.extend({ mousewheel: function (a) { return a ? this.bind("mousewheel", a) : this.trigger("mousewheel") }, unmousewheel: function (a) { return this.unbind("mousewheel", a) } }) }); !function (a) { "function" == typeof define && define.amd ? define(["jquery"], a) : "object" == typeof exports ? module.exports = a : a(jQuery) }(function (a) { function b(b) { var g = b || window.event, h = i.call(arguments, 1), j = 0, l = 0, m = 0, n = 0, o = 0, p = 0; if (b = a.event.fix(g), b.type = "mousewheel", "detail" in g && (m = -1 * g.detail), "wheelDelta" in g && (m = g.wheelDelta), "wheelDeltaY" in g && (m = g.wheelDeltaY), "wheelDeltaX" in g && (l = -1 * g.wheelDeltaX), "axis" in g && g.axis === g.HORIZONTAL_AXIS && (l = -1 * m, m = 0), j = 0 === m ? l : m, "deltaY" in g && (m = -1 * g.deltaY, j = m), "deltaX" in g && (l = g.deltaX, 0 === m && (j = -1 * l)), 0 !== m || 0 !== l) { if (1 === g.deltaMode) { var q = a.data(this, "mousewheel-line-height"); j *= q, m *= q, l *= q } else if (2 === g.deltaMode) { var r = a.data(this, "mousewheel-page-height"); j *= r, m *= r, l *= r } if (n = Math.max(Math.abs(m), Math.abs(l)), (!f || f > n) && (f = n, d(g, n) && (f /= 40)), d(g, n) && (j /= 40, l /= 40, m /= 40), j = Math[j >= 1 ? "floor" : "ceil"](j / f), l = Math[l >= 1 ? "floor" : "ceil"](l / f), m = Math[m >= 1 ? "floor" : "ceil"](m / f), k.settings.normalizeOffset && this.getBoundingClientRect) { var s = this.getBoundingClientRect(); o = b.clientX - s.left, p = b.clientY - s.top } return b.deltaX = l, b.deltaY = m, b.deltaFactor = f, b.offsetX = o, b.offsetY = p, b.deltaMode = 0, h.unshift(b, j, l, m), e && clearTimeout(e), e = setTimeout(c, 200), (a.event.dispatch || a.event.handle).apply(this, h) } } function c() { f = null } function d(a, b) { return k.settings.adjustOldDeltas && "mousewheel" === a.type && b % 120 === 0 } var e, f, g = ["wheel", "mousewheel", "DOMMouseScroll", "MozMousePixelScroll"], h = "onwheel" in document || document.documentMode >= 9 ? ["wheel"] : ["mousewheel", "DomMouseScroll", "MozMousePixelScroll"], i = Array.prototype.slice; if (a.event.fixHooks) for (var j = g.length; j;)a.event.fixHooks[g[--j]] = a.event.mouseHooks; var k = a.event.special.mousewheel = { version: "3.1.12", setup: function () { if (this.addEventListener) for (var c = h.length; c;)this.addEventListener(h[--c], b, !1); else this.onmousewheel = b; a.data(this, "mousewheel-line-height", k.getLineHeight(this)), a.data(this, "mousewheel-page-height", k.getPageHeight(this)) }, teardown: function () { if (this.removeEventListener) for (var c = h.length; c;)this.removeEventListener(h[--c], b, !1); else this.onmousewheel = null; a.removeData(this, "mousewheel-line-height"), a.removeData(this, "mousewheel-page-height") }, getLineHeight: function (b) { var c = a(b), d = c["offsetParent" in a.fn ? "offsetParent" : "parent"](); return d.length || (d = a("body")), parseInt(d.css("fontSize"), 10) || parseInt(c.css("fontSize"), 10) || 16 }, getPageHeight: function (b) { return a(b).height() }, settings: { adjustOldDeltas: !0, normalizeOffset: !0 } }; a.fn.extend({ mousewheel: function (a) { return a ? this.bind("mousewheel", a) : this.trigger("mousewheel") }, unmousewheel: function (a) { return this.unbind("mousewheel", a) } }) });
/* == malihu jquery custom scrollbar plugin == Version: 3.1.5, License: MIT License (MIT) */
!function (e) { "function" == typeof define && define.amd ? define(["jquery"], e) : "undefined" != typeof module && module.exports ? module.exports = e : e(jQuery, window, document) }(function (e) {
	!function (t) { var o = "function" == typeof define && define.amd, a = "undefined" != typeof module && module.exports, n = "https:" == document.location.protocol ? "https:" : "http:", i = "cdnjs.cloudflare.com/ajax/libs/jquery-mousewheel/3.1.13/jquery.mousewheel.min.js"; o || (a ? require("jquery-mousewheel")(e) : e.event.special.mousewheel || e("head").append(decodeURI("%3Cscript src=" + n + "//" + i + "%3E%3C/script%3E"))), t() }(function () {
		var t, o = "mCustomScrollbar", a = "mCS", n = ".mCustomScrollbar", i = { setTop: 0, setLeft: 0, axis: "y", scrollbarPosition: "inside", scrollInertia: 950, autoDraggerLength: !0, alwaysShowScrollbar: 0, snapOffset: 0, mouseWheel: { enable: !0, scrollAmount: "auto", axis: "y", deltaFactor: "auto", disableOver: ["select", "option", "keygen", "datalist", "textarea"] }, scrollButtons: { scrollType: "stepless", scrollAmount: "auto" }, keyboard: { enable: !0, scrollType: "stepless", scrollAmount: "auto" }, contentTouchScroll: 25, documentTouchScroll: !0, advanced: { autoScrollOnFocus: "input,textarea,select,button,datalist,keygen,a[tabindex],area,object,[contenteditable='true']", updateOnContentResize: !0, updateOnImageLoad: "auto", autoUpdateTimeout: 60 }, theme: "light", callbacks: { onTotalScrollOffset: 0, onTotalScrollBackOffset: 0, alwaysTriggerOffsets: !0 } }, r = 0, l = {}, s = window.attachEvent && !window.addEventListener ? 1 : 0, c = !1, d = ["mCSB_dragger_onDrag", "mCSB_scrollTools_onDrag", "mCS_img_loaded", "mCS_disabled", "mCS_destroyed", "mCS_no_scrollbar", "mCS-autoHide", "mCS-dir-rtl", "mCS_no_scrollbar_y", "mCS_no_scrollbar_x", "mCS_y_hidden", "mCS_x_hidden", "mCSB_draggerContainer", "mCSB_buttonUp", "mCSB_buttonDown", "mCSB_buttonLeft", "mCSB_buttonRight"], u = { init: function (t) { var t = e.extend(!0, {}, i, t), o = f.call(this); if (t.live) { var s = t.liveSelector || this.selector || n, c = e(s); if ("off" === t.live) return void m(s); l[s] = setTimeout(function () { c.mCustomScrollbar(t), "once" === t.live && c.length && m(s) }, 500) } else m(s); return t.setWidth = t.set_width ? t.set_width : t.setWidth, t.setHeight = t.set_height ? t.set_height : t.setHeight, t.axis = t.horizontalScroll ? "x" : p(t.axis), t.scrollInertia = t.scrollInertia > 0 && t.scrollInertia < 17 ? 17 : t.scrollInertia, "object" != typeof t.mouseWheel && 1 == t.mouseWheel && (t.mouseWheel = { enable: !0, scrollAmount: "auto", axis: "y", preventDefault: !1, deltaFactor: "auto", normalizeDelta: !1, invert: !1 }), t.mouseWheel.scrollAmount = t.mouseWheelPixels ? t.mouseWheelPixels : t.mouseWheel.scrollAmount, t.mouseWheel.normalizeDelta = t.advanced.normalizeMouseWheelDelta ? t.advanced.normalizeMouseWheelDelta : t.mouseWheel.normalizeDelta, t.scrollButtons.scrollType = g(t.scrollButtons.scrollType), h(t), e(o).each(function () { var o = e(this); if (!o.data(a)) { o.data(a, { idx: ++r, opt: t, scrollRatio: { y: null, x: null }, overflowed: null, contentReset: { y: null, x: null }, bindEvents: !1, tweenRunning: !1, sequential: {}, langDir: o.css("direction"), cbOffsets: null, trigger: null, poll: { size: { o: 0, n: 0 }, img: { o: 0, n: 0 }, change: { o: 0, n: 0 } } }); var n = o.data(a), i = n.opt, l = o.data("mcs-axis"), s = o.data("mcs-scrollbar-position"), c = o.data("mcs-theme"); l && (i.axis = l), s && (i.scrollbarPosition = s), c && (i.theme = c, h(i)), v.call(this), n && i.callbacks.onCreate && "function" == typeof i.callbacks.onCreate && i.callbacks.onCreate.call(this), e("#mCSB_" + n.idx + "_container img:not(." + d[2] + ")").addClass(d[2]), u.update.call(null, o) } }) }, update: function (t, o) { var n = t || f.call(this); return e(n).each(function () { var t = e(this); if (t.data(a)) { var n = t.data(a), i = n.opt, r = e("#mCSB_" + n.idx + "_container"), l = e("#mCSB_" + n.idx), s = [e("#mCSB_" + n.idx + "_dragger_vertical"), e("#mCSB_" + n.idx + "_dragger_horizontal")]; if (!r.length) return; n.tweenRunning && Q(t), o && n && i.callbacks.onBeforeUpdate && "function" == typeof i.callbacks.onBeforeUpdate && i.callbacks.onBeforeUpdate.call(this), t.hasClass(d[3]) && t.removeClass(d[3]), t.hasClass(d[4]) && t.removeClass(d[4]), l.css("max-height", "none"), l.height() !== t.height() && l.css("max-height", t.height()), _.call(this), "y" === i.axis || i.advanced.autoExpandHorizontalScroll || r.css("width", x(r)), n.overflowed = y.call(this), M.call(this), i.autoDraggerLength && S.call(this), b.call(this), T.call(this); var c = [Math.abs(r[0].offsetTop), Math.abs(r[0].offsetLeft)]; "x" !== i.axis && (n.overflowed[0] ? s[0].height() > s[0].parent().height() ? B.call(this) : (G(t, c[0].toString(), { dir: "y", dur: 0, overwrite: "none" }), n.contentReset.y = null) : (B.call(this), "y" === i.axis ? k.call(this) : "yx" === i.axis && n.overflowed[1] && G(t, c[1].toString(), { dir: "x", dur: 0, overwrite: "none" }))), "y" !== i.axis && (n.overflowed[1] ? s[1].width() > s[1].parent().width() ? B.call(this) : (G(t, c[1].toString(), { dir: "x", dur: 0, overwrite: "none" }), n.contentReset.x = null) : (B.call(this), "x" === i.axis ? k.call(this) : "yx" === i.axis && n.overflowed[0] && G(t, c[0].toString(), { dir: "y", dur: 0, overwrite: "none" }))), o && n && (2 === o && i.callbacks.onImageLoad && "function" == typeof i.callbacks.onImageLoad ? i.callbacks.onImageLoad.call(this) : 3 === o && i.callbacks.onSelectorChange && "function" == typeof i.callbacks.onSelectorChange ? i.callbacks.onSelectorChange.call(this) : i.callbacks.onUpdate && "function" == typeof i.callbacks.onUpdate && i.callbacks.onUpdate.call(this)), N.call(this) } }) }, scrollTo: function (t, o) { if ("undefined" != typeof t && null != t) { var n = f.call(this); return e(n).each(function () { var n = e(this); if (n.data(a)) { var i = n.data(a), r = i.opt, l = { trigger: "external", scrollInertia: r.scrollInertia, scrollEasing: "mcsEaseInOut", moveDragger: !1, timeout: 60, callbacks: !0, onStart: !0, onUpdate: !0, onComplete: !0 }, s = e.extend(!0, {}, l, o), c = Y.call(this, t), d = s.scrollInertia > 0 && s.scrollInertia < 17 ? 17 : s.scrollInertia; c[0] = X.call(this, c[0], "y"), c[1] = X.call(this, c[1], "x"), s.moveDragger && (c[0] *= i.scrollRatio.y, c[1] *= i.scrollRatio.x), s.dur = ne() ? 0 : d, setTimeout(function () { null !== c[0] && "undefined" != typeof c[0] && "x" !== r.axis && i.overflowed[0] && (s.dir = "y", s.overwrite = "all", G(n, c[0].toString(), s)), null !== c[1] && "undefined" != typeof c[1] && "y" !== r.axis && i.overflowed[1] && (s.dir = "x", s.overwrite = "none", G(n, c[1].toString(), s)) }, s.timeout) } }) } }, stop: function () { var t = f.call(this); return e(t).each(function () { var t = e(this); t.data(a) && Q(t) }) }, disable: function (t) { var o = f.call(this); return e(o).each(function () { var o = e(this); if (o.data(a)) { o.data(a); N.call(this, "remove"), k.call(this), t && B.call(this), M.call(this, !0), o.addClass(d[3]) } }) }, destroy: function () { var t = f.call(this); return e(t).each(function () { var n = e(this); if (n.data(a)) { var i = n.data(a), r = i.opt, l = e("#mCSB_" + i.idx), s = e("#mCSB_" + i.idx + "_container"), c = e(".mCSB_" + i.idx + "_scrollbar"); r.live && m(r.liveSelector || e(t).selector), N.call(this, "remove"), k.call(this), B.call(this), n.removeData(a), $(this, "mcs"), c.remove(), s.find("img." + d[2]).removeClass(d[2]), l.replaceWith(s.contents()), n.removeClass(o + " _" + a + "_" + i.idx + " " + d[6] + " " + d[7] + " " + d[5] + " " + d[3]).addClass(d[4]) } }) } }, f = function () { return "object" != typeof e(this) || e(this).length < 1 ? n : this }, h = function (t) { var o = ["rounded", "rounded-dark", "rounded-dots", "rounded-dots-dark"], a = ["rounded-dots", "rounded-dots-dark", "3d", "3d-dark", "3d-thick", "3d-thick-dark", "inset", "inset-dark", "inset-2", "inset-2-dark", "inset-3", "inset-3-dark"], n = ["minimal", "minimal-dark"], i = ["minimal", "minimal-dark"], r = ["minimal", "minimal-dark"]; t.autoDraggerLength = e.inArray(t.theme, o) > -1 ? !1 : t.autoDraggerLength, t.autoExpandScrollbar = e.inArray(t.theme, a) > -1 ? !1 : t.autoExpandScrollbar, t.scrollButtons.enable = e.inArray(t.theme, n) > -1 ? !1 : t.scrollButtons.enable, t.autoHideScrollbar = e.inArray(t.theme, i) > -1 ? !0 : t.autoHideScrollbar, t.scrollbarPosition = e.inArray(t.theme, r) > -1 ? "outside" : t.scrollbarPosition }, m = function (e) { l[e] && (clearTimeout(l[e]), $(l, e)) }, p = function (e) { return "yx" === e || "xy" === e || "auto" === e ? "yx" : "x" === e || "horizontal" === e ? "x" : "y" }, g = function (e) { return "stepped" === e || "pixels" === e || "step" === e || "click" === e ? "stepped" : "stepless" }, v = function () { var t = e(this), n = t.data(a), i = n.opt, r = i.autoExpandScrollbar ? " " + d[1] + "_expand" : "", l = ["<div id='mCSB_" + n.idx + "_scrollbar_vertical' class='mCSB_scrollTools mCSB_" + n.idx + "_scrollbar mCS-" + i.theme + " mCSB_scrollTools_vertical" + r + "'><div class='" + d[12] + "'><div id='mCSB_" + n.idx + "_dragger_vertical' class='mCSB_dragger' style='position:absolute;'><div class='mCSB_dragger_bar' /></div><div class='mCSB_draggerRail' /></div></div>", "<div id='mCSB_" + n.idx + "_scrollbar_horizontal' class='mCSB_scrollTools mCSB_" + n.idx + "_scrollbar mCS-" + i.theme + " mCSB_scrollTools_horizontal" + r + "'><div class='" + d[12] + "'><div id='mCSB_" + n.idx + "_dragger_horizontal' class='mCSB_dragger' style='position:absolute;'><div class='mCSB_dragger_bar' /></div><div class='mCSB_draggerRail' /></div></div>"], s = "yx" === i.axis ? "mCSB_vertical_horizontal" : "x" === i.axis ? "mCSB_horizontal" : "mCSB_vertical", c = "yx" === i.axis ? l[0] + l[1] : "x" === i.axis ? l[1] : l[0], u = "yx" === i.axis ? "<div id='mCSB_" + n.idx + "_container_wrapper' class='mCSB_container_wrapper' />" : "", f = i.autoHideScrollbar ? " " + d[6] : "", h = "x" !== i.axis && "rtl" === n.langDir ? " " + d[7] : ""; i.setWidth && t.css("width", i.setWidth), i.setHeight && t.css("height", i.setHeight), i.setLeft = "y" !== i.axis && "rtl" === n.langDir ? "989999px" : i.setLeft, t.addClass(o + " _" + a + "_" + n.idx + f + h).wrapInner("<div id='mCSB_" + n.idx + "' class='mCustomScrollBox mCS-" + i.theme + " " + s + "'><div id='mCSB_" + n.idx + "_container' class='mCSB_container' style='position:relative; top:" + i.setTop + "; left:" + i.setLeft + ";' dir='" + n.langDir + "' /></div>"); var m = e("#mCSB_" + n.idx), p = e("#mCSB_" + n.idx + "_container"); "y" === i.axis || i.advanced.autoExpandHorizontalScroll || p.css("width", x(p)), "outside" === i.scrollbarPosition ? ("static" === t.css("position") && t.css("position", "relative"), t.css("overflow", "visible"), m.addClass("mCSB_outside").after(c)) : (m.addClass("mCSB_inside").append(c), p.wrap(u)), w.call(this); var g = [e("#mCSB_" + n.idx + "_dragger_vertical"), e("#mCSB_" + n.idx + "_dragger_horizontal")]; g[0].css("min-height", g[0].height()), g[1].css("min-width", g[1].width()) }, x = function (t) { var o = [t[0].scrollWidth, Math.max.apply(Math, t.children().map(function () { return e(this).outerWidth(!0) }).get())], a = t.parent().width(); return o[0] > a ? o[0] : o[1] > a ? o[1] : "100%" }, _ = function () { var t = e(this), o = t.data(a), n = o.opt, i = e("#mCSB_" + o.idx + "_container"); if (n.advanced.autoExpandHorizontalScroll && "y" !== n.axis) { i.css({ width: "auto", "min-width": 0, "overflow-x": "scroll" }); var r = Math.ceil(i[0].scrollWidth); 3 === n.advanced.autoExpandHorizontalScroll || 2 !== n.advanced.autoExpandHorizontalScroll && r > i.parent().width() ? i.css({ width: r, "min-width": "100%", "overflow-x": "inherit" }) : i.css({ "overflow-x": "inherit", position: "absolute" }).wrap("<div class='mCSB_h_wrapper' style='position:relative; left:0; width:999999px;' />").css({ width: Math.ceil(i[0].getBoundingClientRect().right + .4) - Math.floor(i[0].getBoundingClientRect().left), "min-width": "100%", position: "relative" }).unwrap() } }, w = function () { var t = e(this), o = t.data(a), n = o.opt, i = e(".mCSB_" + o.idx + "_scrollbar:first"), r = oe(n.scrollButtons.tabindex) ? "tabindex='" + n.scrollButtons.tabindex + "'" : "", l = ["<a href='#' class='" + d[13] + "' " + r + " />", "<a href='#' class='" + d[14] + "' " + r + " />", "<a href='#' class='" + d[15] + "' " + r + " />", "<a href='#' class='" + d[16] + "' " + r + " />"], s = ["x" === n.axis ? l[2] : l[0], "x" === n.axis ? l[3] : l[1], l[2], l[3]]; n.scrollButtons.enable && i.prepend(s[0]).append(s[1]).next(".mCSB_scrollTools").prepend(s[2]).append(s[3]) }, S = function () { var t = e(this), o = t.data(a), n = e("#mCSB_" + o.idx), i = e("#mCSB_" + o.idx + "_container"), r = [e("#mCSB_" + o.idx + "_dragger_vertical"), e("#mCSB_" + o.idx + "_dragger_horizontal")], l = [n.height() / i.outerHeight(!1), n.width() / i.outerWidth(!1)], c = [parseInt(r[0].css("min-height")), Math.round(l[0] * r[0].parent().height()), parseInt(r[1].css("min-width")), Math.round(l[1] * r[1].parent().width())], d = s && c[1] < c[0] ? c[0] : c[1], u = s && c[3] < c[2] ? c[2] : c[3]; r[0].css({ height: d, "max-height": r[0].parent().height() - 10 }).find(".mCSB_dragger_bar").css({ "line-height": c[0] + "px" }), r[1].css({ width: u, "max-width": r[1].parent().width() - 10 }) }, b = function () { var t = e(this), o = t.data(a), n = e("#mCSB_" + o.idx), i = e("#mCSB_" + o.idx + "_container"), r = [e("#mCSB_" + o.idx + "_dragger_vertical"), e("#mCSB_" + o.idx + "_dragger_horizontal")], l = [i.outerHeight(!1) - n.height(), i.outerWidth(!1) - n.width()], s = [l[0] / (r[0].parent().height() - r[0].height()), l[1] / (r[1].parent().width() - r[1].width())]; o.scrollRatio = { y: s[0], x: s[1] } }, C = function (e, t, o) { var a = o ? d[0] + "_expanded" : "", n = e.closest(".mCSB_scrollTools"); "active" === t ? (e.toggleClass(d[0] + " " + a), n.toggleClass(d[1]), e[0]._draggable = e[0]._draggable ? 0 : 1) : e[0]._draggable || ("hide" === t ? (e.removeClass(d[0]), n.removeClass(d[1])) : (e.addClass(d[0]), n.addClass(d[1]))) }, y = function () { var t = e(this), o = t.data(a), n = e("#mCSB_" + o.idx), i = e("#mCSB_" + o.idx + "_container"), r = null == o.overflowed ? i.height() : i.outerHeight(!1), l = null == o.overflowed ? i.width() : i.outerWidth(!1), s = i[0].scrollHeight, c = i[0].scrollWidth; return s > r && (r = s), c > l && (l = c), [r > n.height(), l > n.width()] }, B = function () { var t = e(this), o = t.data(a), n = o.opt, i = e("#mCSB_" + o.idx), r = e("#mCSB_" + o.idx + "_container"), l = [e("#mCSB_" + o.idx + "_dragger_vertical"), e("#mCSB_" + o.idx + "_dragger_horizontal")]; if (Q(t), ("x" !== n.axis && !o.overflowed[0] || "y" === n.axis && o.overflowed[0]) && (l[0].add(r).css("top", 0), G(t, "_resetY")), "y" !== n.axis && !o.overflowed[1] || "x" === n.axis && o.overflowed[1]) { var s = dx = 0; "rtl" === o.langDir && (s = i.width() - r.outerWidth(!1), dx = Math.abs(s / o.scrollRatio.x)), r.css("left", s), l[1].css("left", dx), G(t, "_resetX") } }, T = function () { function t() { r = setTimeout(function () { e.event.special.mousewheel ? (clearTimeout(r), W.call(o[0])) : t() }, 100) } var o = e(this), n = o.data(a), i = n.opt; if (!n.bindEvents) { if (I.call(this), i.contentTouchScroll && D.call(this), E.call(this), i.mouseWheel.enable) { var r; t() } P.call(this), U.call(this), i.advanced.autoScrollOnFocus && H.call(this), i.scrollButtons.enable && F.call(this), i.keyboard.enable && q.call(this), n.bindEvents = !0 } }, k = function () { var t = e(this), o = t.data(a), n = o.opt, i = a + "_" + o.idx, r = ".mCSB_" + o.idx + "_scrollbar", l = e("#mCSB_" + o.idx + ",#mCSB_" + o.idx + "_container,#mCSB_" + o.idx + "_container_wrapper," + r + " ." + d[12] + ",#mCSB_" + o.idx + "_dragger_vertical,#mCSB_" + o.idx + "_dragger_horizontal," + r + ">a"), s = e("#mCSB_" + o.idx + "_container"); n.advanced.releaseDraggableSelectors && l.add(e(n.advanced.releaseDraggableSelectors)), n.advanced.extraDraggableSelectors && l.add(e(n.advanced.extraDraggableSelectors)), o.bindEvents && (e(document).add(e(!A() || top.document)).unbind("." + i), l.each(function () { e(this).unbind("." + i) }), clearTimeout(t[0]._focusTimeout), $(t[0], "_focusTimeout"), clearTimeout(o.sequential.step), $(o.sequential, "step"), clearTimeout(s[0].onCompleteTimeout), $(s[0], "onCompleteTimeout"), o.bindEvents = !1) }, M = function (t) { var o = e(this), n = o.data(a), i = n.opt, r = e("#mCSB_" + n.idx + "_container_wrapper"), l = r.length ? r : e("#mCSB_" + n.idx + "_container"), s = [e("#mCSB_" + n.idx + "_scrollbar_vertical"), e("#mCSB_" + n.idx + "_scrollbar_horizontal")], c = [s[0].find(".mCSB_dragger"), s[1].find(".mCSB_dragger")]; "x" !== i.axis && (n.overflowed[0] && !t ? (s[0].add(c[0]).add(s[0].children("a")).css("display", "block"), l.removeClass(d[8] + " " + d[10])) : (i.alwaysShowScrollbar ? (2 !== i.alwaysShowScrollbar && c[0].css("display", "none"), l.removeClass(d[10])) : (s[0].css("display", "none"), l.addClass(d[10])), l.addClass(d[8]))), "y" !== i.axis && (n.overflowed[1] && !t ? (s[1].add(c[1]).add(s[1].children("a")).css("display", "block"), l.removeClass(d[9] + " " + d[11])) : (i.alwaysShowScrollbar ? (2 !== i.alwaysShowScrollbar && c[1].css("display", "none"), l.removeClass(d[11])) : (s[1].css("display", "none"), l.addClass(d[11])), l.addClass(d[9]))), n.overflowed[0] || n.overflowed[1] ? o.removeClass(d[5]) : o.addClass(d[5]) }, O = function (t) { var o = t.type, a = t.target.ownerDocument !== document && null !== frameElement ? [e(frameElement).offset().top, e(frameElement).offset().left] : null, n = A() && t.target.ownerDocument !== top.document && null !== frameElement ? [e(t.view.frameElement).offset().top, e(t.view.frameElement).offset().left] : [0, 0]; switch (o) { case "pointerdown": case "MSPointerDown": case "pointermove": case "MSPointerMove": case "pointerup": case "MSPointerUp": return a ? [t.originalEvent.pageY - a[0] + n[0], t.originalEvent.pageX - a[1] + n[1], !1] : [t.originalEvent.pageY, t.originalEvent.pageX, !1]; case "touchstart": case "touchmove": case "touchend": var i = t.originalEvent.touches[0] || t.originalEvent.changedTouches[0], r = t.originalEvent.touches.length || t.originalEvent.changedTouches.length; return t.target.ownerDocument !== document ? [i.screenY, i.screenX, r > 1] : [i.pageY, i.pageX, r > 1]; default: return a ? [t.pageY - a[0] + n[0], t.pageX - a[1] + n[1], !1] : [t.pageY, t.pageX, !1] } }, I = function () { function t(e, t, a, n) { if (h[0].idleTimer = d.scrollInertia < 233 ? 250 : 0, o.attr("id") === f[1]) var i = "x", s = (o[0].offsetLeft - t + n) * l.scrollRatio.x; else var i = "y", s = (o[0].offsetTop - e + a) * l.scrollRatio.y; G(r, s.toString(), { dir: i, drag: !0 }) } var o, n, i, r = e(this), l = r.data(a), d = l.opt, u = a + "_" + l.idx, f = ["mCSB_" + l.idx + "_dragger_vertical", "mCSB_" + l.idx + "_dragger_horizontal"], h = e("#mCSB_" + l.idx + "_container"), m = e("#" + f[0] + ",#" + f[1]), p = d.advanced.releaseDraggableSelectors ? m.add(e(d.advanced.releaseDraggableSelectors)) : m, g = d.advanced.extraDraggableSelectors ? e(!A() || top.document).add(e(d.advanced.extraDraggableSelectors)) : e(!A() || top.document); m.bind("contextmenu." + u, function (e) { e.preventDefault() }).bind("mousedown." + u + " touchstart." + u + " pointerdown." + u + " MSPointerDown." + u, function (t) { if (t.stopImmediatePropagation(), t.preventDefault(), ee(t)) { c = !0, s && (document.onselectstart = function () { return !1 }), L.call(h, !1), Q(r), o = e(this); var a = o.offset(), l = O(t)[0] - a.top, u = O(t)[1] - a.left, f = o.height() + a.top, m = o.width() + a.left; f > l && l > 0 && m > u && u > 0 && (n = l, i = u), C(o, "active", d.autoExpandScrollbar) } }).bind("touchmove." + u, function (e) { e.stopImmediatePropagation(), e.preventDefault(); var a = o.offset(), r = O(e)[0] - a.top, l = O(e)[1] - a.left; t(n, i, r, l) }), e(document).add(g).bind("mousemove." + u + " pointermove." + u + " MSPointerMove." + u, function (e) { if (o) { var a = o.offset(), r = O(e)[0] - a.top, l = O(e)[1] - a.left; if (n === r && i === l) return; t(n, i, r, l) } }).add(p).bind("mouseup." + u + " touchend." + u + " pointerup." + u + " MSPointerUp." + u, function () { o && (C(o, "active", d.autoExpandScrollbar), o = null), c = !1, s && (document.onselectstart = null), L.call(h, !0) }) }, D = function () { function o(e) { if (!te(e) || c || O(e)[2]) return void (t = 0); t = 1, b = 0, C = 0, d = 1, y.removeClass("mCS_touch_action"); var o = I.offset(); u = O(e)[0] - o.top, f = O(e)[1] - o.left, z = [O(e)[0], O(e)[1]] } function n(e) { if (te(e) && !c && !O(e)[2] && (T.documentTouchScroll || e.preventDefault(), e.stopImmediatePropagation(), (!C || b) && d)) { g = K(); var t = M.offset(), o = O(e)[0] - t.top, a = O(e)[1] - t.left, n = "mcsLinearOut"; if (E.push(o), W.push(a), z[2] = Math.abs(O(e)[0] - z[0]), z[3] = Math.abs(O(e)[1] - z[1]), B.overflowed[0]) var i = D[0].parent().height() - D[0].height(), r = u - o > 0 && o - u > -(i * B.scrollRatio.y) && (2 * z[3] < z[2] || "yx" === T.axis); if (B.overflowed[1]) var l = D[1].parent().width() - D[1].width(), h = f - a > 0 && a - f > -(l * B.scrollRatio.x) && (2 * z[2] < z[3] || "yx" === T.axis); r || h ? (U || e.preventDefault(), b = 1) : (C = 1, y.addClass("mCS_touch_action")), U && e.preventDefault(), w = "yx" === T.axis ? [u - o, f - a] : "x" === T.axis ? [null, f - a] : [u - o, null], I[0].idleTimer = 250, B.overflowed[0] && s(w[0], R, n, "y", "all", !0), B.overflowed[1] && s(w[1], R, n, "x", L, !0) } } function i(e) { if (!te(e) || c || O(e)[2]) return void (t = 0); t = 1, e.stopImmediatePropagation(), Q(y), p = K(); var o = M.offset(); h = O(e)[0] - o.top, m = O(e)[1] - o.left, E = [], W = [] } function r(e) { if (te(e) && !c && !O(e)[2]) { d = 0, e.stopImmediatePropagation(), b = 0, C = 0, v = K(); var t = M.offset(), o = O(e)[0] - t.top, a = O(e)[1] - t.left; if (!(v - g > 30)) { _ = 1e3 / (v - p); var n = "mcsEaseOut", i = 2.5 > _, r = i ? [E[E.length - 2], W[W.length - 2]] : [0, 0]; x = i ? [o - r[0], a - r[1]] : [o - h, a - m]; var u = [Math.abs(x[0]), Math.abs(x[1])]; _ = i ? [Math.abs(x[0] / 4), Math.abs(x[1] / 4)] : [_, _]; var f = [Math.abs(I[0].offsetTop) - x[0] * l(u[0] / _[0], _[0]), Math.abs(I[0].offsetLeft) - x[1] * l(u[1] / _[1], _[1])]; w = "yx" === T.axis ? [f[0], f[1]] : "x" === T.axis ? [null, f[1]] : [f[0], null], S = [4 * u[0] + T.scrollInertia, 4 * u[1] + T.scrollInertia]; var y = parseInt(T.contentTouchScroll) || 0; w[0] = u[0] > y ? w[0] : 0, w[1] = u[1] > y ? w[1] : 0, B.overflowed[0] && s(w[0], S[0], n, "y", L, !1), B.overflowed[1] && s(w[1], S[1], n, "x", L, !1) } } } function l(e, t) { var o = [1.5 * t, 2 * t, t / 1.5, t / 2]; return e > 90 ? t > 4 ? o[0] : o[3] : e > 60 ? t > 3 ? o[3] : o[2] : e > 30 ? t > 8 ? o[1] : t > 6 ? o[0] : t > 4 ? t : o[2] : t > 8 ? t : o[3] } function s(e, t, o, a, n, i) { e && G(y, e.toString(), { dur: t, scrollEasing: o, dir: a, overwrite: n, drag: i }) } var d, u, f, h, m, p, g, v, x, _, w, S, b, C, y = e(this), B = y.data(a), T = B.opt, k = a + "_" + B.idx, M = e("#mCSB_" + B.idx), I = e("#mCSB_" + B.idx + "_container"), D = [e("#mCSB_" + B.idx + "_dragger_vertical"), e("#mCSB_" + B.idx + "_dragger_horizontal")], E = [], W = [], R = 0, L = "yx" === T.axis ? "none" : "all", z = [], P = I.find("iframe"), H = ["touchstart." + k + " pointerdown." + k + " MSPointerDown." + k, "touchmove." + k + " pointermove." + k + " MSPointerMove." + k, "touchend." + k + " pointerup." + k + " MSPointerUp." + k], U = void 0 !== document.body.style.touchAction && "" !== document.body.style.touchAction; I.bind(H[0], function (e) { o(e) }).bind(H[1], function (e) { n(e) }), M.bind(H[0], function (e) { i(e) }).bind(H[2], function (e) { r(e) }), P.length && P.each(function () { e(this).bind("load", function () { A(this) && e(this.contentDocument || this.contentWindow.document).bind(H[0], function (e) { o(e), i(e) }).bind(H[1], function (e) { n(e) }).bind(H[2], function (e) { r(e) }) }) }) }, E = function () { function o() { return window.getSelection ? window.getSelection().toString() : document.selection && "Control" != document.selection.type ? document.selection.createRange().text : 0 } function n(e, t, o) { d.type = o && i ? "stepped" : "stepless", d.scrollAmount = 10, j(r, e, t, "mcsLinearOut", o ? 60 : null) } var i, r = e(this), l = r.data(a), s = l.opt, d = l.sequential, u = a + "_" + l.idx, f = e("#mCSB_" + l.idx + "_container"), h = f.parent(); f.bind("mousedown." + u, function () { t || i || (i = 1, c = !0) }).add(document).bind("mousemove." + u, function (e) { if (!t && i && o()) { var a = f.offset(), r = O(e)[0] - a.top + f[0].offsetTop, c = O(e)[1] - a.left + f[0].offsetLeft; r > 0 && r < h.height() && c > 0 && c < h.width() ? d.step && n("off", null, "stepped") : ("x" !== s.axis && l.overflowed[0] && (0 > r ? n("on", 38) : r > h.height() && n("on", 40)), "y" !== s.axis && l.overflowed[1] && (0 > c ? n("on", 37) : c > h.width() && n("on", 39))) } }).bind("mouseup." + u + " dragend." + u, function () { t || (i && (i = 0, n("off", null)), c = !1) }) }, W = function () { function t(t, a) { if (Q(o), !z(o, t.target)) { var r = "auto" !== i.mouseWheel.deltaFactor ? parseInt(i.mouseWheel.deltaFactor) : s && t.deltaFactor < 100 ? 100 : t.deltaFactor || 100, d = i.scrollInertia; if ("x" === i.axis || "x" === i.mouseWheel.axis) var u = "x", f = [Math.round(r * n.scrollRatio.x), parseInt(i.mouseWheel.scrollAmount)], h = "auto" !== i.mouseWheel.scrollAmount ? f[1] : f[0] >= l.width() ? .9 * l.width() : f[0], m = Math.abs(e("#mCSB_" + n.idx + "_container")[0].offsetLeft), p = c[1][0].offsetLeft, g = c[1].parent().width() - c[1].width(), v = "y" === i.mouseWheel.axis ? t.deltaY || a : t.deltaX; else var u = "y", f = [Math.round(r * n.scrollRatio.y), parseInt(i.mouseWheel.scrollAmount)], h = "auto" !== i.mouseWheel.scrollAmount ? f[1] : f[0] >= l.height() ? .9 * l.height() : f[0], m = Math.abs(e("#mCSB_" + n.idx + "_container")[0].offsetTop), p = c[0][0].offsetTop, g = c[0].parent().height() - c[0].height(), v = t.deltaY || a; "y" === u && !n.overflowed[0] || "x" === u && !n.overflowed[1] || ((i.mouseWheel.invert || t.webkitDirectionInvertedFromDevice) && (v = -v), i.mouseWheel.normalizeDelta && (v = 0 > v ? -1 : 1), (v > 0 && 0 !== p || 0 > v && p !== g || i.mouseWheel.preventDefault) && (t.stopImmediatePropagation(), t.preventDefault()), t.deltaFactor < 5 && !i.mouseWheel.normalizeDelta && (h = t.deltaFactor, d = 17), G(o, (m - v * h).toString(), { dir: u, dur: d })) } } if (e(this).data(a)) { var o = e(this), n = o.data(a), i = n.opt, r = a + "_" + n.idx, l = e("#mCSB_" + n.idx), c = [e("#mCSB_" + n.idx + "_dragger_vertical"), e("#mCSB_" + n.idx + "_dragger_horizontal")], d = e("#mCSB_" + n.idx + "_container").find("iframe"); d.length && d.each(function () { e(this).bind("load", function () { A(this) && e(this.contentDocument || this.contentWindow.document).bind("mousewheel." + r, function (e, o) { t(e, o) }) }) }), l.bind("mousewheel." + r, function (e, o) { t(e, o) }) } }, R = new Object, A = function (t) { var o = !1, a = !1, n = null; if (void 0 === t ? a = "#empty" : void 0 !== e(t).attr("id") && (a = e(t).attr("id")), a !== !1 && void 0 !== R[a]) return R[a]; if (t) { try { var i = t.contentDocument || t.contentWindow.document; n = i.body.innerHTML } catch (r) { } o = null !== n } else { try { var i = top.document; n = i.body.innerHTML } catch (r) { } o = null !== n } return a !== !1 && (R[a] = o), o }, L = function (e) { var t = this.find("iframe"); if (t.length) { var o = e ? "auto" : "none"; t.css("pointer-events", o) } }, z = function (t, o) { var n = o.nodeName.toLowerCase(), i = t.data(a).opt.mouseWheel.disableOver, r = ["select", "textarea"]; return e.inArray(n, i) > -1 && !(e.inArray(n, r) > -1 && !e(o).is(":focus")) }, P = function () { var t, o = e(this), n = o.data(a), i = a + "_" + n.idx, r = e("#mCSB_" + n.idx + "_container"), l = r.parent(), s = e(".mCSB_" + n.idx + "_scrollbar ." + d[12]); s.bind("mousedown." + i + " touchstart." + i + " pointerdown." + i + " MSPointerDown." + i, function (o) { c = !0, e(o.target).hasClass("mCSB_dragger") || (t = 1) }).bind("touchend." + i + " pointerup." + i + " MSPointerUp." + i, function () { c = !1 }).bind("click." + i, function (a) { if (t && (t = 0, e(a.target).hasClass(d[12]) || e(a.target).hasClass("mCSB_draggerRail"))) { Q(o); var i = e(this), s = i.find(".mCSB_dragger"); if (i.parent(".mCSB_scrollTools_horizontal").length > 0) { if (!n.overflowed[1]) return; var c = "x", u = a.pageX > s.offset().left ? -1 : 1, f = Math.abs(r[0].offsetLeft) - u * (.9 * l.width()) } else { if (!n.overflowed[0]) return; var c = "y", u = a.pageY > s.offset().top ? -1 : 1, f = Math.abs(r[0].offsetTop) - u * (.9 * l.height()) } G(o, f.toString(), { dir: c, scrollEasing: "mcsEaseInOut" }) } }) }, H = function () { var t = e(this), o = t.data(a), n = o.opt, i = a + "_" + o.idx, r = e("#mCSB_" + o.idx + "_container"), l = r.parent(); r.bind("focusin." + i, function () { var o = e(document.activeElement), a = r.find(".mCustomScrollBox").length, i = 0; o.is(n.advanced.autoScrollOnFocus) && (Q(t), clearTimeout(t[0]._focusTimeout), t[0]._focusTimer = a ? (i + 17) * a : 0, t[0]._focusTimeout = setTimeout(function () { var e = [ae(o)[0], ae(o)[1]], a = [r[0].offsetTop, r[0].offsetLeft], s = [a[0] + e[0] >= 0 && a[0] + e[0] < l.height() - o.outerHeight(!1), a[1] + e[1] >= 0 && a[0] + e[1] < l.width() - o.outerWidth(!1)], c = "yx" !== n.axis || s[0] || s[1] ? "all" : "none"; "x" === n.axis || s[0] || G(t, e[0].toString(), { dir: "y", scrollEasing: "mcsEaseInOut", overwrite: c, dur: i }), "y" === n.axis || s[1] || G(t, e[1].toString(), { dir: "x", scrollEasing: "mcsEaseInOut", overwrite: c, dur: i }) }, t[0]._focusTimer)) }) }, U = function () { var t = e(this), o = t.data(a), n = a + "_" + o.idx, i = e("#mCSB_" + o.idx + "_container").parent(); i.bind("scroll." + n, function () { 0 === i.scrollTop() && 0 === i.scrollLeft() || e(".mCSB_" + o.idx + "_scrollbar").css("visibility", "hidden") }) }, F = function () { var t = e(this), o = t.data(a), n = o.opt, i = o.sequential, r = a + "_" + o.idx, l = ".mCSB_" + o.idx + "_scrollbar", s = e(l + ">a"); s.bind("contextmenu." + r, function (e) { e.preventDefault() }).bind("mousedown." + r + " touchstart." + r + " pointerdown." + r + " MSPointerDown." + r + " mouseup." + r + " touchend." + r + " pointerup." + r + " MSPointerUp." + r + " mouseout." + r + " pointerout." + r + " MSPointerOut." + r + " click." + r, function (a) { function r(e, o) { i.scrollAmount = n.scrollButtons.scrollAmount, j(t, e, o) } if (a.preventDefault(), ee(a)) { var l = e(this).attr("class"); switch (i.type = n.scrollButtons.scrollType, a.type) { case "mousedown": case "touchstart": case "pointerdown": case "MSPointerDown": if ("stepped" === i.type) return; c = !0, o.tweenRunning = !1, r("on", l); break; case "mouseup": case "touchend": case "pointerup": case "MSPointerUp": case "mouseout": case "pointerout": case "MSPointerOut": if ("stepped" === i.type) return; c = !1, i.dir && r("off", l); break; case "click": if ("stepped" !== i.type || o.tweenRunning) return; r("on", l) } } }) }, q = function () { function t(t) { function a(e, t) { r.type = i.keyboard.scrollType, r.scrollAmount = i.keyboard.scrollAmount, "stepped" === r.type && n.tweenRunning || j(o, e, t) } switch (t.type) { case "blur": n.tweenRunning && r.dir && a("off", null); break; case "keydown": case "keyup": var l = t.keyCode ? t.keyCode : t.which, s = "on"; if ("x" !== i.axis && (38 === l || 40 === l) || "y" !== i.axis && (37 === l || 39 === l)) { if ((38 === l || 40 === l) && !n.overflowed[0] || (37 === l || 39 === l) && !n.overflowed[1]) return; "keyup" === t.type && (s = "off"), e(document.activeElement).is(u) || (t.preventDefault(), t.stopImmediatePropagation(), a(s, l)) } else if (33 === l || 34 === l) { if ((n.overflowed[0] || n.overflowed[1]) && (t.preventDefault(), t.stopImmediatePropagation()), "keyup" === t.type) { Q(o); var f = 34 === l ? -1 : 1; if ("x" === i.axis || "yx" === i.axis && n.overflowed[1] && !n.overflowed[0]) var h = "x", m = Math.abs(c[0].offsetLeft) - f * (.9 * d.width()); else var h = "y", m = Math.abs(c[0].offsetTop) - f * (.9 * d.height()); G(o, m.toString(), { dir: h, scrollEasing: "mcsEaseInOut" }) } } else if ((35 === l || 36 === l) && !e(document.activeElement).is(u) && ((n.overflowed[0] || n.overflowed[1]) && (t.preventDefault(), t.stopImmediatePropagation()), "keyup" === t.type)) { if ("x" === i.axis || "yx" === i.axis && n.overflowed[1] && !n.overflowed[0]) var h = "x", m = 35 === l ? Math.abs(d.width() - c.outerWidth(!1)) : 0; else var h = "y", m = 35 === l ? Math.abs(d.height() - c.outerHeight(!1)) : 0; G(o, m.toString(), { dir: h, scrollEasing: "mcsEaseInOut" }) } } } var o = e(this), n = o.data(a), i = n.opt, r = n.sequential, l = a + "_" + n.idx, s = e("#mCSB_" + n.idx), c = e("#mCSB_" + n.idx + "_container"), d = c.parent(), u = "input,textarea,select,datalist,keygen,[contenteditable='true']", f = c.find("iframe"), h = ["blur." + l + " keydown." + l + " keyup." + l]; f.length && f.each(function () { e(this).bind("load", function () { A(this) && e(this.contentDocument || this.contentWindow.document).bind(h[0], function (e) { t(e) }) }) }), s.attr("tabindex", "0").bind(h[0], function (e) { t(e) }) }, j = function (t, o, n, i, r) { function l(e) { u.snapAmount && (f.scrollAmount = u.snapAmount instanceof Array ? "x" === f.dir[0] ? u.snapAmount[1] : u.snapAmount[0] : u.snapAmount); var o = "stepped" !== f.type, a = r ? r : e ? o ? p / 1.5 : g : 1e3 / 60, n = e ? o ? 7.5 : 40 : 2.5, s = [Math.abs(h[0].offsetTop), Math.abs(h[0].offsetLeft)], d = [c.scrollRatio.y > 10 ? 10 : c.scrollRatio.y, c.scrollRatio.x > 10 ? 10 : c.scrollRatio.x], m = "x" === f.dir[0] ? s[1] + f.dir[1] * (d[1] * n) : s[0] + f.dir[1] * (d[0] * n), v = "x" === f.dir[0] ? s[1] + f.dir[1] * parseInt(f.scrollAmount) : s[0] + f.dir[1] * parseInt(f.scrollAmount), x = "auto" !== f.scrollAmount ? v : m, _ = i ? i : e ? o ? "mcsLinearOut" : "mcsEaseInOut" : "mcsLinear", w = !!e; return e && 17 > a && (x = "x" === f.dir[0] ? s[1] : s[0]), G(t, x.toString(), { dir: f.dir[0], scrollEasing: _, dur: a, onComplete: w }), e ? void (f.dir = !1) : (clearTimeout(f.step), void (f.step = setTimeout(function () { l() }, a))) } function s() { clearTimeout(f.step), $(f, "step"), Q(t) } var c = t.data(a), u = c.opt, f = c.sequential, h = e("#mCSB_" + c.idx + "_container"), m = "stepped" === f.type, p = u.scrollInertia < 26 ? 26 : u.scrollInertia, g = u.scrollInertia < 1 ? 17 : u.scrollInertia; switch (o) { case "on": if (f.dir = [n === d[16] || n === d[15] || 39 === n || 37 === n ? "x" : "y", n === d[13] || n === d[15] || 38 === n || 37 === n ? -1 : 1], Q(t), oe(n) && "stepped" === f.type) return; l(m); break; case "off": s(), (m || c.tweenRunning && f.dir) && l(!0) } }, Y = function (t) { var o = e(this).data(a).opt, n = []; return "function" == typeof t && (t = t()), t instanceof Array ? n = t.length > 1 ? [t[0], t[1]] : "x" === o.axis ? [null, t[0]] : [t[0], null] : (n[0] = t.y ? t.y : t.x || "x" === o.axis ? null : t, n[1] = t.x ? t.x : t.y || "y" === o.axis ? null : t), "function" == typeof n[0] && (n[0] = n[0]()), "function" == typeof n[1] && (n[1] = n[1]()), n }, X = function (t, o) { if (null != t && "undefined" != typeof t) { var n = e(this), i = n.data(a), r = i.opt, l = e("#mCSB_" + i.idx + "_container"), s = l.parent(), c = typeof t; o || (o = "x" === r.axis ? "x" : "y"); var d = "x" === o ? l.outerWidth(!1) - s.width() : l.outerHeight(!1) - s.height(), f = "x" === o ? l[0].offsetLeft : l[0].offsetTop, h = "x" === o ? "left" : "top"; switch (c) { case "function": return t(); case "object": var m = t.jquery ? t : e(t); if (!m.length) return; return "x" === o ? ae(m)[1] : ae(m)[0]; case "string": case "number": if (oe(t)) return Math.abs(t); if (-1 !== t.indexOf("%")) return Math.abs(d * parseInt(t) / 100); if (-1 !== t.indexOf("-=")) return Math.abs(f - parseInt(t.split("-=")[1])); if (-1 !== t.indexOf("+=")) { var p = f + parseInt(t.split("+=")[1]); return p >= 0 ? 0 : Math.abs(p) } if (-1 !== t.indexOf("px") && oe(t.split("px")[0])) return Math.abs(t.split("px")[0]); if ("top" === t || "left" === t) return 0; if ("bottom" === t) return Math.abs(s.height() - l.outerHeight(!1)); if ("right" === t) return Math.abs(s.width() - l.outerWidth(!1)); if ("first" === t || "last" === t) { var m = l.find(":" + t); return "x" === o ? ae(m)[1] : ae(m)[0] } return e(t).length ? "x" === o ? ae(e(t))[1] : ae(e(t))[0] : (l.css(h, t), void u.update.call(null, n[0])) } } }, N = function (t) {
			function o() { return clearTimeout(f[0].autoUpdate), 0 === l.parents("html").length ? void (l = null) : void (f[0].autoUpdate = setTimeout(function () { return c.advanced.updateOnSelectorChange && (s.poll.change.n = i(), s.poll.change.n !== s.poll.change.o) ? (s.poll.change.o = s.poll.change.n, void r(3)) : c.advanced.updateOnContentResize && (s.poll.size.n = l[0].scrollHeight + l[0].scrollWidth + f[0].offsetHeight + l[0].offsetHeight + l[0].offsetWidth, s.poll.size.n !== s.poll.size.o) ? (s.poll.size.o = s.poll.size.n, void r(1)) : !c.advanced.updateOnImageLoad || "auto" === c.advanced.updateOnImageLoad && "y" === c.axis || (s.poll.img.n = f.find("img").length, s.poll.img.n === s.poll.img.o) ? void ((c.advanced.updateOnSelectorChange || c.advanced.updateOnContentResize || c.advanced.updateOnImageLoad) && o()) : (s.poll.img.o = s.poll.img.n, void f.find("img").each(function () { n(this) })) }, c.advanced.autoUpdateTimeout)) } function n(t) {
				function o(e, t) {
					return function () {
						return t.apply(e, arguments)
					}
				} function a() { this.onload = null, e(t).addClass(d[2]), r(2) } if (e(t).hasClass(d[2])) return void r(); var n = new Image; n.onload = o(n, a), n.src = t.src
			} function i() { c.advanced.updateOnSelectorChange === !0 && (c.advanced.updateOnSelectorChange = "*"); var e = 0, t = f.find(c.advanced.updateOnSelectorChange); return c.advanced.updateOnSelectorChange && t.length > 0 && t.each(function () { e += this.offsetHeight + this.offsetWidth }), e } function r(e) { clearTimeout(f[0].autoUpdate), u.update.call(null, l[0], e) } var l = e(this), s = l.data(a), c = s.opt, f = e("#mCSB_" + s.idx + "_container"); return t ? (clearTimeout(f[0].autoUpdate), void $(f[0], "autoUpdate")) : void o()
		}, V = function (e, t, o) { return Math.round(e / t) * t - o }, Q = function (t) { var o = t.data(a), n = e("#mCSB_" + o.idx + "_container,#mCSB_" + o.idx + "_container_wrapper,#mCSB_" + o.idx + "_dragger_vertical,#mCSB_" + o.idx + "_dragger_horizontal"); n.each(function () { Z.call(this) }) }, G = function (t, o, n) { function i(e) { return s && c.callbacks[e] && "function" == typeof c.callbacks[e] } function r() { return [c.callbacks.alwaysTriggerOffsets || w >= S[0] + y, c.callbacks.alwaysTriggerOffsets || -B >= w] } function l() { var e = [h[0].offsetTop, h[0].offsetLeft], o = [x[0].offsetTop, x[0].offsetLeft], a = [h.outerHeight(!1), h.outerWidth(!1)], i = [f.height(), f.width()]; t[0].mcs = { content: h, top: e[0], left: e[1], draggerTop: o[0], draggerLeft: o[1], topPct: Math.round(100 * Math.abs(e[0]) / (Math.abs(a[0]) - i[0])), leftPct: Math.round(100 * Math.abs(e[1]) / (Math.abs(a[1]) - i[1])), direction: n.dir } } var s = t.data(a), c = s.opt, d = { trigger: "internal", dir: "y", scrollEasing: "mcsEaseOut", drag: !1, dur: c.scrollInertia, overwrite: "all", callbacks: !0, onStart: !0, onUpdate: !0, onComplete: !0 }, n = e.extend(d, n), u = [n.dur, n.drag ? 0 : n.dur], f = e("#mCSB_" + s.idx), h = e("#mCSB_" + s.idx + "_container"), m = h.parent(), p = c.callbacks.onTotalScrollOffset ? Y.call(t, c.callbacks.onTotalScrollOffset) : [0, 0], g = c.callbacks.onTotalScrollBackOffset ? Y.call(t, c.callbacks.onTotalScrollBackOffset) : [0, 0]; if (s.trigger = n.trigger, 0 === m.scrollTop() && 0 === m.scrollLeft() || (e(".mCSB_" + s.idx + "_scrollbar").css("visibility", "visible"), m.scrollTop(0).scrollLeft(0)), "_resetY" !== o || s.contentReset.y || (i("onOverflowYNone") && c.callbacks.onOverflowYNone.call(t[0]), s.contentReset.y = 1), "_resetX" !== o || s.contentReset.x || (i("onOverflowXNone") && c.callbacks.onOverflowXNone.call(t[0]), s.contentReset.x = 1), "_resetY" !== o && "_resetX" !== o) { if (!s.contentReset.y && t[0].mcs || !s.overflowed[0] || (i("onOverflowY") && c.callbacks.onOverflowY.call(t[0]), s.contentReset.x = null), !s.contentReset.x && t[0].mcs || !s.overflowed[1] || (i("onOverflowX") && c.callbacks.onOverflowX.call(t[0]), s.contentReset.x = null), c.snapAmount) { var v = c.snapAmount instanceof Array ? "x" === n.dir ? c.snapAmount[1] : c.snapAmount[0] : c.snapAmount; o = V(o, v, c.snapOffset) } switch (n.dir) { case "x": var x = e("#mCSB_" + s.idx + "_dragger_horizontal"), _ = "left", w = h[0].offsetLeft, S = [f.width() - h.outerWidth(!1), x.parent().width() - x.width()], b = [o, 0 === o ? 0 : o / s.scrollRatio.x], y = p[1], B = g[1], T = y > 0 ? y / s.scrollRatio.x : 0, k = B > 0 ? B / s.scrollRatio.x : 0; break; case "y": var x = e("#mCSB_" + s.idx + "_dragger_vertical"), _ = "top", w = h[0].offsetTop, S = [f.height() - h.outerHeight(!1), x.parent().height() - x.height()], b = [o, 0 === o ? 0 : o / s.scrollRatio.y], y = p[0], B = g[0], T = y > 0 ? y / s.scrollRatio.y : 0, k = B > 0 ? B / s.scrollRatio.y : 0 }b[1] < 0 || 0 === b[0] && 0 === b[1] ? b = [0, 0] : b[1] >= S[1] ? b = [S[0], S[1]] : b[0] = -b[0], t[0].mcs || (l(), i("onInit") && c.callbacks.onInit.call(t[0])), clearTimeout(h[0].onCompleteTimeout), J(x[0], _, Math.round(b[1]), u[1], n.scrollEasing), !s.tweenRunning && (0 === w && b[0] >= 0 || w === S[0] && b[0] <= S[0]) || J(h[0], _, Math.round(b[0]), u[0], n.scrollEasing, n.overwrite, { onStart: function () { n.callbacks && n.onStart && !s.tweenRunning && (i("onScrollStart") && (l(), c.callbacks.onScrollStart.call(t[0])), s.tweenRunning = !0, C(x), s.cbOffsets = r()) }, onUpdate: function () { n.callbacks && n.onUpdate && i("whileScrolling") && (l(), c.callbacks.whileScrolling.call(t[0])) }, onComplete: function () { if (n.callbacks && n.onComplete) { "yx" === c.axis && clearTimeout(h[0].onCompleteTimeout); var e = h[0].idleTimer || 0; h[0].onCompleteTimeout = setTimeout(function () { i("onScroll") && (l(), c.callbacks.onScroll.call(t[0])), i("onTotalScroll") && b[1] >= S[1] - T && s.cbOffsets[0] && (l(), c.callbacks.onTotalScroll.call(t[0])), i("onTotalScrollBack") && b[1] <= k && s.cbOffsets[1] && (l(), c.callbacks.onTotalScrollBack.call(t[0])), s.tweenRunning = !1, h[0].idleTimer = 0, C(x, "hide") }, e) } } }) } }, J = function (e, t, o, a, n, i, r) { function l() { S.stop || (x || m.call(), x = K() - v, s(), x >= S.time && (S.time = x > S.time ? x + f - (x - S.time) : x + f - 1, S.time < x + 1 && (S.time = x + 1)), S.time < a ? S.id = h(l) : g.call()) } function s() { a > 0 ? (S.currVal = u(S.time, _, b, a, n), w[t] = Math.round(S.currVal) + "px") : w[t] = o + "px", p.call() } function c() { f = 1e3 / 60, S.time = x + f, h = window.requestAnimationFrame ? window.requestAnimationFrame : function (e) { return s(), setTimeout(e, .01) }, S.id = h(l) } function d() { null != S.id && (window.requestAnimationFrame ? window.cancelAnimationFrame(S.id) : clearTimeout(S.id), S.id = null) } function u(e, t, o, a, n) { switch (n) { case "linear": case "mcsLinear": return o * e / a + t; case "mcsLinearOut": return e /= a, e--, o * Math.sqrt(1 - e * e) + t; case "easeInOutSmooth": return e /= a / 2, 1 > e ? o / 2 * e * e + t : (e--, -o / 2 * (e * (e - 2) - 1) + t); case "easeInOutStrong": return e /= a / 2, 1 > e ? o / 2 * Math.pow(2, 10 * (e - 1)) + t : (e--, o / 2 * (-Math.pow(2, -10 * e) + 2) + t); case "easeInOut": case "mcsEaseInOut": return e /= a / 2, 1 > e ? o / 2 * e * e * e + t : (e -= 2, o / 2 * (e * e * e + 2) + t); case "easeOutSmooth": return e /= a, e--, -o * (e * e * e * e - 1) + t; case "easeOutStrong": return o * (-Math.pow(2, -10 * e / a) + 1) + t; case "easeOut": case "mcsEaseOut": default: var i = (e /= a) * e, r = i * e; return t + o * (.499999999999997 * r * i + -2.5 * i * i + 5.5 * r + -6.5 * i + 4 * e) } } e._mTween || (e._mTween = { top: {}, left: {} }); var f, h, r = r || {}, m = r.onStart || function () { }, p = r.onUpdate || function () { }, g = r.onComplete || function () { }, v = K(), x = 0, _ = e.offsetTop, w = e.style, S = e._mTween[t]; "left" === t && (_ = e.offsetLeft); var b = o - _; S.stop = 0, "none" !== i && d(), c() }, K = function () { return window.performance && window.performance.now ? window.performance.now() : window.performance && window.performance.webkitNow ? window.performance.webkitNow() : Date.now ? Date.now() : (new Date).getTime() }, Z = function () { var e = this; e._mTween || (e._mTween = { top: {}, left: {} }); for (var t = ["top", "left"], o = 0; o < t.length; o++) { var a = t[o]; e._mTween[a].id && (window.requestAnimationFrame ? window.cancelAnimationFrame(e._mTween[a].id) : clearTimeout(e._mTween[a].id), e._mTween[a].id = null, e._mTween[a].stop = 1) } }, $ = function (e, t) { try { delete e[t] } catch (o) { e[t] = null } }, ee = function (e) { return !(e.which && 1 !== e.which) }, te = function (e) { var t = e.originalEvent.pointerType; return !(t && "touch" !== t && 2 !== t) }, oe = function (e) { return !isNaN(parseFloat(e)) && isFinite(e) }, ae = function (e) { var t = e.parents(".mCSB_container"); return [e.offset().top - t.offset().top, e.offset().left - t.offset().left] }, ne = function () { function e() { var e = ["webkit", "moz", "ms", "o"]; if ("hidden" in document) return "hidden"; for (var t = 0; t < e.length; t++)if (e[t] + "Hidden" in document) return e[t] + "Hidden"; return null } var t = e(); return t ? document[t] : !1 }; e.fn[o] = function (t) { return u[t] ? u[t].apply(this, Array.prototype.slice.call(arguments, 1)) : "object" != typeof t && t ? void e.error("Method " + t + " does not exist") : u.init.apply(this, arguments) }, e[o] = function (t) { return u[t] ? u[t].apply(this, Array.prototype.slice.call(arguments, 1)) : "object" != typeof t && t ? void e.error("Method " + t + " does not exist") : u.init.apply(this, arguments) }, e[o].defaults = i, window[o] = !0, e(window).bind("load", function () { e(n)[o](), e.extend(e.expr[":"], { mcsInView: e.expr[":"].mcsInView || function (t) { var o, a, n = e(t), i = n.parents(".mCSB_container"); if (i.length) return o = i.parent(), a = [i[0].offsetTop, i[0].offsetLeft], a[0] + ae(n)[0] >= 0 && a[0] + ae(n)[0] < o.height() - n.outerHeight(!1) && a[1] + ae(n)[1] >= 0 && a[1] + ae(n)[1] < o.width() - n.outerWidth(!1) }, mcsInSight: e.expr[":"].mcsInSight || function (t, o, a) { var n, i, r, l, s = e(t), c = s.parents(".mCSB_container"), d = "exact" === a[3] ? [[1, 0], [1, 0]] : [[.9, .1], [.6, .4]]; if (c.length) return n = [s.outerHeight(!1), s.outerWidth(!1)], r = [c[0].offsetTop + ae(s)[0], c[0].offsetLeft + ae(s)[1]], i = [c.parent()[0].offsetHeight, c.parent()[0].offsetWidth], l = [n[0] < i[0] ? d[0] : d[1], n[1] < i[1] ? d[0] : d[1]], r[0] - i[0] * l[0][0] < 0 && r[0] + n[0] - i[0] * l[0][1] >= 0 && r[1] - i[1] * l[1][0] < 0 && r[1] + n[1] - i[1] * l[1][1] >= 0 }, mcsOverflow: e.expr[":"].mcsOverflow || function (t) { var o = e(t).data(a); if (o) return o.overflowed[0] || o.overflowed[1] } }) })
	})
});
/*
== malihu jquery custom scrollbar plugin == 
Version: 3.1.5 
Plugin URI: http://manos.malihu.gr/jquery-custom-content-scroller 
Author: malihu
Author URI: http://manos.malihu.gr
License: MIT License (MIT)
*/

/*
Copyright Manos Malihutsakis (email: manos@malihu.gr)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/

/*
The code below is fairly long, fully commented and should be normally used in development. 
For production, use either the minified jquery.mCustomScrollbar.min.js script or 
the production-ready jquery.mCustomScrollbar.concat.min.js which contains the plugin 
and dependencies (minified). 
*/

(function (factory) {
	if (typeof define === "function" && define.amd) {
		define(["jquery"], factory);
	} else if (typeof module !== "undefined" && module.exports) {
		module.exports = factory;
	} else {
		factory(jQuery, window, document);
	}
}(function ($) {
	(function (init) {
		var _rjs = typeof define === "function" && define.amd, /* RequireJS */
			_njs = typeof module !== "undefined" && module.exports, /* NodeJS */
			_dlp = ("https:" == document.location.protocol) ? "https:" : "http:", /* location protocol */
			_url = "cdnjs.cloudflare.com/ajax/libs/jquery-mousewheel/3.1.13/jquery.mousewheel.min.js";
		if (!_rjs) {
			if (_njs) {
				require("jquery-mousewheel")($);
			} else {
				/* load jquery-mousewheel plugin (via CDN) if it's not present or not loaded via RequireJS 
				(works when mCustomScrollbar fn is called on window load) */
				$.event.special.mousewheel || $("head").append(decodeURI("%3Cscript src=" + _dlp + "//" + _url + "%3E%3C/script%3E"));
			}
		}
		init();
	}(function () {

		/* 
		----------------------------------------
		PLUGIN NAMESPACE, PREFIX, DEFAULT SELECTOR(S) 
		----------------------------------------
		*/

		var pluginNS = "mCustomScrollbar",
			pluginPfx = "mCS",
			defaultSelector = ".mCustomScrollbar",





			/* 
			----------------------------------------
			DEFAULT OPTIONS 
			----------------------------------------
			*/

			defaults = {
				/*
				set element/content width/height programmatically 
				values: boolean, pixels, percentage 
					option						default
					-------------------------------------
					setWidth					false
					setHeight					false
				*/
				/*
				set the initial css top property of content  
				values: string (e.g. "-100px", "10%" etc.)
				*/
				setTop: 0,
				/*
				set the initial css left property of content  
				values: string (e.g. "-100px", "10%" etc.)
				*/
				setLeft: 0,
				/* 
				scrollbar axis (vertical and/or horizontal scrollbars) 
				values (string): "y", "x", "yx"
				*/
				axis: "y",
				/*
				position of scrollbar relative to content  
				values (string): "inside", "outside" ("outside" requires elements with position:relative)
				*/
				scrollbarPosition: "inside",
				/*
				scrolling inertia
				values: integer (milliseconds)
				*/
				scrollInertia: 950,
				/* 
				auto-adjust scrollbar dragger length
				values: boolean
				*/
				autoDraggerLength: true,
				/*
				auto-hide scrollbar when idle 
				values: boolean
					option						default
					-------------------------------------
					autoHideScrollbar			false
				*/
				/*
				auto-expands scrollbar on mouse-over and dragging
				values: boolean
					option						default
					-------------------------------------
					autoExpandScrollbar			false
				*/
				/*
				always show scrollbar, even when there's nothing to scroll 
				values: integer (0=disable, 1=always show dragger rail and buttons, 2=always show dragger rail, dragger and buttons), boolean
				*/
				alwaysShowScrollbar: 0,
				/*
				scrolling always snaps to a multiple of this number in pixels
				values: integer, array ([y,x])
					option						default
					-------------------------------------
					snapAmount					null
				*/
				/*
				when snapping, snap with this number in pixels as an offset 
				values: integer
				*/
				snapOffset: 0,
				/* 
				mouse-wheel scrolling
				*/
				mouseWheel: {
					/* 
					enable mouse-wheel scrolling
					values: boolean
					*/
					enable: true,
					/* 
					scrolling amount in pixels
					values: "auto", integer 
					*/
					scrollAmount: "auto",
					/* 
					mouse-wheel scrolling axis 
					the default scrolling direction when both vertical and horizontal scrollbars are present 
					values (string): "y", "x" 
					*/
					axis: "y",
					/* 
					prevent the default behaviour which automatically scrolls the parent element(s) when end of scrolling is reached 
					values: boolean
						option						default
						-------------------------------------
						preventDefault				null
					*/
					/*
					the reported mouse-wheel delta value. The number of lines (translated to pixels) one wheel notch scrolls.  
					values: "auto", integer 
					"auto" uses the default OS/browser value 
					*/
					deltaFactor: "auto",
					/*
					normalize mouse-wheel delta to -1 or 1 (disables mouse-wheel acceleration) 
					values: boolean
						option						default
						-------------------------------------
						normalizeDelta				null
					*/
					/*
					invert mouse-wheel scrolling direction 
					values: boolean
						option						default
						-------------------------------------
						invert						null
					*/
					/*
					the tags that disable mouse-wheel when cursor is over them
					*/
					disableOver: ["select", "option", "keygen", "datalist", "textarea"]
				},
				/* 
				scrollbar buttons
				*/
				scrollButtons: {
					/*
					enable scrollbar buttons
					values: boolean
						option						default
						-------------------------------------
						enable						null
					*/
					/*
					scrollbar buttons scrolling type 
					values (string): "stepless", "stepped"
					*/
					scrollType: "stepless",
					/*
					scrolling amount in pixels
					values: "auto", integer 
					*/
					scrollAmount: "auto"
					/*
					tabindex of the scrollbar buttons
					values: false, integer
						option						default
						-------------------------------------
						tabindex					null
					*/
				},
				/* 
				keyboard scrolling
				*/
				keyboard: {
					/*
					enable scrolling via keyboard
					values: boolean
					*/
					enable: true,
					/*
					keyboard scrolling type 
					values (string): "stepless", "stepped"
					*/
					scrollType: "stepless",
					/*
					scrolling amount in pixels
					values: "auto", integer 
					*/
					scrollAmount: "auto"
				},
				/*
				enable content touch-swipe scrolling 
				values: boolean, integer, string (number)
				integer values define the axis-specific minimum amount required for scrolling momentum
				*/
				contentTouchScroll: 25,
				/*
				enable/disable document (default) touch-swipe scrolling 
				*/
				documentTouchScroll: true,
				/*
				advanced option parameters
				*/
				advanced: {
					/*
					auto-expand content horizontally (for "x" or "yx" axis) 
					values: boolean, integer (the value 2 forces the non scrollHeight/scrollWidth method, the value 3 forces the scrollHeight/scrollWidth method)
						option						default
						-------------------------------------
						autoExpandHorizontalScroll	null
					*/
					/*
					auto-scroll to elements with focus
					*/
					autoScrollOnFocus: "input,textarea,select,button,datalist,keygen,a[tabindex],area,object,[contenteditable='true']",
					/*
					auto-update scrollbars on content, element or viewport resize 
					should be true for fluid layouts/elements, adding/removing content dynamically, hiding/showing elements, content with images etc. 
					values: boolean
					*/
					updateOnContentResize: true,
					/*
					auto-update scrollbars each time each image inside the element is fully loaded 
					values: "auto", boolean
					*/
					updateOnImageLoad: "auto",
					/*
					auto-update scrollbars based on the amount and size changes of specific selectors 
					useful when you need to update the scrollbar(s) automatically, each time a type of element is added, removed or changes its size 
					values: boolean, string (e.g. "ul li" will auto-update scrollbars each time list-items inside the element are changed) 
					a value of true (boolean) will auto-update scrollbars each time any element is changed
						option						default
						-------------------------------------
						updateOnSelectorChange		null
					*/
					/*
					extra selectors that'll allow scrollbar dragging upon mousemove/up, pointermove/up, touchend etc. (e.g. "selector-1, selector-2")
						option						default
						-------------------------------------
						extraDraggableSelectors		null
					*/
					/*
					extra selectors that'll release scrollbar dragging upon mouseup, pointerup, touchend etc. (e.g. "selector-1, selector-2")
						option						default
						-------------------------------------
						releaseDraggableSelectors	null
					*/
					/*
					auto-update timeout 
					values: integer (milliseconds)
					*/
					autoUpdateTimeout: 60
				},
				/* 
				scrollbar theme 
				values: string (see CSS/plugin URI for a list of ready-to-use themes)
				*/
				theme: "light",
				/*
				user defined callback functions
				*/
				callbacks: {
					/*
					Available callbacks: 
						callback					default
						-------------------------------------
						onCreate					null
						onInit						null
						onScrollStart				null
						onScroll					null
						onTotalScroll				null
						onTotalScrollBack			null
						whileScrolling				null
						onOverflowY					null
						onOverflowX					null
						onOverflowYNone				null
						onOverflowXNone				null
						onImageLoad					null
						onSelectorChange			null
						onBeforeUpdate				null
						onUpdate					null
					*/
					onTotalScrollOffset: 0,
					onTotalScrollBackOffset: 0,
					alwaysTriggerOffsets: true
				}
				/*
				add scrollbar(s) on all elements matching the current selector, now and in the future 
				values: boolean, string 
				string values: "on" (enable), "once" (disable after first invocation), "off" (disable)
				liveSelector values: string (selector)
					option						default
					-------------------------------------
					live						false
					liveSelector				null
				*/
			},





			/* 
			----------------------------------------
			VARS, CONSTANTS 
			----------------------------------------
			*/

			totalInstances = 0, /* plugin instances amount */
			liveTimers = {}, /* live option timers */
			oldIE = (window.attachEvent && !window.addEventListener) ? 1 : 0, /* detect IE < 9 */
			touchActive = false, touchable, /* global touch vars (for touch and pointer events) */
			/* general plugin classes */
			classes = [
				"mCSB_dragger_onDrag", "mCSB_scrollTools_onDrag", "mCS_img_loaded", "mCS_disabled", "mCS_destroyed", "mCS_no_scrollbar",
				"mCS-autoHide", "mCS-dir-rtl", "mCS_no_scrollbar_y", "mCS_no_scrollbar_x", "mCS_y_hidden", "mCS_x_hidden", "mCSB_draggerContainer",
				"mCSB_buttonUp", "mCSB_buttonDown", "mCSB_buttonLeft", "mCSB_buttonRight"
			],





			/* 
			----------------------------------------
			METHODS 
			----------------------------------------
			*/

			methods = {

				/* 
				plugin initialization method 
				creates the scrollbar(s), plugin data object and options
				----------------------------------------
				*/

				init: function (options) {

					var options = $.extend(true, {}, defaults, options),
						selector = _selector.call(this); /* validate selector */

					/* 
					if live option is enabled, monitor for elements matching the current selector and 
					apply scrollbar(s) when found (now and in the future) 
					*/
					if (options.live) {
						var liveSelector = options.liveSelector || this.selector || defaultSelector, /* live selector(s) */
							$liveSelector = $(liveSelector); /* live selector(s) as jquery object */
						if (options.live === "off") {
							/* 
							disable live if requested 
							usage: $(selector).mCustomScrollbar({live:"off"}); 
							*/
							removeLiveTimers(liveSelector);
							return;
						}
						liveTimers[liveSelector] = setTimeout(function () {
							/* call mCustomScrollbar fn on live selector(s) every half-second */
							$liveSelector.mCustomScrollbar(options);
							if (options.live === "once" && $liveSelector.length) {
								/* disable live after first invocation */
								removeLiveTimers(liveSelector);
							}
						}, 500);
					} else {
						removeLiveTimers(liveSelector);
					}

					/* options backward compatibility (for versions < 3.0.0) and normalization */
					options.setWidth = (options.set_width) ? options.set_width : options.setWidth;
					options.setHeight = (options.set_height) ? options.set_height : options.setHeight;
					options.axis = (options.horizontalScroll) ? "x" : _findAxis(options.axis);
					options.scrollInertia = options.scrollInertia > 0 && options.scrollInertia < 17 ? 17 : options.scrollInertia;
					if (typeof options.mouseWheel !== "object" && options.mouseWheel == true) { /* old school mouseWheel option (non-object) */
						options.mouseWheel = { enable: true, scrollAmount: "auto", axis: "y", preventDefault: false, deltaFactor: "auto", normalizeDelta: false, invert: false }
					}
					options.mouseWheel.scrollAmount = !options.mouseWheelPixels ? options.mouseWheel.scrollAmount : options.mouseWheelPixels;
					options.mouseWheel.normalizeDelta = !options.advanced.normalizeMouseWheelDelta ? options.mouseWheel.normalizeDelta : options.advanced.normalizeMouseWheelDelta;
					options.scrollButtons.scrollType = _findScrollButtonsType(options.scrollButtons.scrollType);

					_theme(options); /* theme-specific options */

					/* plugin constructor */
					return $(selector).each(function () {

						var $this = $(this);

						if (!$this.data(pluginPfx)) { /* prevent multiple instantiations */

							/* store options and create objects in jquery data */
							$this.data(pluginPfx, {
								idx: ++totalInstances, /* instance index */
								opt: options, /* options */
								scrollRatio: { y: null, x: null }, /* scrollbar to content ratio */
								overflowed: null, /* overflowed axis */
								contentReset: { y: null, x: null }, /* object to check when content resets */
								bindEvents: false, /* object to check if events are bound */
								tweenRunning: false, /* object to check if tween is running */
								sequential: {}, /* sequential scrolling object */
								langDir: $this.css("direction"), /* detect/store direction (ltr or rtl) */
								cbOffsets: null, /* object to check whether callback offsets always trigger */
								/* 
								object to check how scrolling events where last triggered 
								"internal" (default - triggered by this script), "external" (triggered by other scripts, e.g. via scrollTo method) 
								usage: object.data("mCS").trigger
								*/
								trigger: null,
								/* 
								object to check for changes in elements in order to call the update method automatically 
								*/
								poll: { size: { o: 0, n: 0 }, img: { o: 0, n: 0 }, change: { o: 0, n: 0 } }
							});

							var d = $this.data(pluginPfx), o = d.opt,
								/* HTML data attributes */
								htmlDataAxis = $this.data("mcs-axis"), htmlDataSbPos = $this.data("mcs-scrollbar-position"), htmlDataTheme = $this.data("mcs-theme");

							if (htmlDataAxis) { o.axis = htmlDataAxis; } /* usage example: data-mcs-axis="y" */
							if (htmlDataSbPos) { o.scrollbarPosition = htmlDataSbPos; } /* usage example: data-mcs-scrollbar-position="outside" */
							if (htmlDataTheme) { /* usage example: data-mcs-theme="minimal" */
								o.theme = htmlDataTheme;
								_theme(o); /* theme-specific options */
							}

							_pluginMarkup.call(this); /* add plugin markup */

							if (d && o.callbacks.onCreate && typeof o.callbacks.onCreate === "function") { o.callbacks.onCreate.call(this); } /* callbacks: onCreate */

							$("#mCSB_" + d.idx + "_container img:not(." + classes[2] + ")").addClass(classes[2]); /* flag loaded images */

							methods.update.call(null, $this); /* call the update method */

						}

					});

				},
				/* ---------------------------------------- */



				/* 
				plugin update method 
				updates content and scrollbar(s) values, events and status 
				----------------------------------------
				usage: $(selector).mCustomScrollbar("update");
				*/

				update: function (el, cb) {

					var selector = el || _selector.call(this); /* validate selector */

					return $(selector).each(function () {

						var $this = $(this);

						if ($this.data(pluginPfx)) { /* check if plugin has initialized */

							var d = $this.data(pluginPfx), o = d.opt,
								mCSB_container = $("#mCSB_" + d.idx + "_container"),
								mCustomScrollBox = $("#mCSB_" + d.idx),
								mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")];

							if (!mCSB_container.length) { return; }

							if (d.tweenRunning) { _stop($this); } /* stop any running tweens while updating */

							if (cb && d && o.callbacks.onBeforeUpdate && typeof o.callbacks.onBeforeUpdate === "function") { o.callbacks.onBeforeUpdate.call(this); } /* callbacks: onBeforeUpdate */

							/* if element was disabled or destroyed, remove class(es) */
							if ($this.hasClass(classes[3])) { $this.removeClass(classes[3]); }
							if ($this.hasClass(classes[4])) { $this.removeClass(classes[4]); }

							/* css flexbox fix, detect/set max-height */
							mCustomScrollBox.css("max-height", "none");
							if (mCustomScrollBox.height() !== $this.height()) { mCustomScrollBox.css("max-height", $this.height()); }

							_expandContentHorizontally.call(this); /* expand content horizontally */

							if (o.axis !== "y" && !o.advanced.autoExpandHorizontalScroll) {
								mCSB_container.css("width", _contentWidth(mCSB_container));
							}

							d.overflowed = _overflowed.call(this); /* determine if scrolling is required */

							_scrollbarVisibility.call(this); /* show/hide scrollbar(s) */

							/* auto-adjust scrollbar dragger length analogous to content */
							if (o.autoDraggerLength) { _setDraggerLength.call(this); }

							_scrollRatio.call(this); /* calculate and store scrollbar to content ratio */

							_bindEvents.call(this); /* bind scrollbar events */

							/* reset scrolling position and/or events */
							var to = [Math.abs(mCSB_container[0].offsetTop), Math.abs(mCSB_container[0].offsetLeft)];
							if (o.axis !== "x") { /* y/yx axis */
								if (!d.overflowed[0]) { /* y scrolling is not required */
									_resetContentPosition.call(this); /* reset content position */
									if (o.axis === "y") {
										_unbindEvents.call(this);
									} else if (o.axis === "yx" && d.overflowed[1]) {
										_scrollTo($this, to[1].toString(), { dir: "x", dur: 0, overwrite: "none" });
									}
								} else if (mCSB_dragger[0].height() > mCSB_dragger[0].parent().height()) {
									_resetContentPosition.call(this); /* reset content position */
								} else { /* y scrolling is required */
									_scrollTo($this, to[0].toString(), { dir: "y", dur: 0, overwrite: "none" });
									d.contentReset.y = null;
								}
							}
							if (o.axis !== "y") { /* x/yx axis */
								if (!d.overflowed[1]) { /* x scrolling is not required */
									_resetContentPosition.call(this); /* reset content position */
									if (o.axis === "x") {
										_unbindEvents.call(this);
									} else if (o.axis === "yx" && d.overflowed[0]) {
										_scrollTo($this, to[0].toString(), { dir: "y", dur: 0, overwrite: "none" });
									}
								} else if (mCSB_dragger[1].width() > mCSB_dragger[1].parent().width()) {
									_resetContentPosition.call(this); /* reset content position */
								} else { /* x scrolling is required */
									_scrollTo($this, to[1].toString(), { dir: "x", dur: 0, overwrite: "none" });
									d.contentReset.x = null;
								}
							}

							/* callbacks: onImageLoad, onSelectorChange, onUpdate */
							if (cb && d) {
								if (cb === 2 && o.callbacks.onImageLoad && typeof o.callbacks.onImageLoad === "function") {
									o.callbacks.onImageLoad.call(this);
								} else if (cb === 3 && o.callbacks.onSelectorChange && typeof o.callbacks.onSelectorChange === "function") {
									o.callbacks.onSelectorChange.call(this);
								} else if (o.callbacks.onUpdate && typeof o.callbacks.onUpdate === "function") {
									o.callbacks.onUpdate.call(this);
								}
							}

							_autoUpdate.call(this); /* initialize automatic updating (for dynamic content, fluid layouts etc.) */

						}

					});

				},
				/* ---------------------------------------- */



				/* 
				plugin scrollTo method 
				triggers a scrolling event to a specific value
				----------------------------------------
				usage: $(selector).mCustomScrollbar("scrollTo",value,options);
				*/

				scrollTo: function (val, options) {

					/* prevent silly things like $(selector).mCustomScrollbar("scrollTo",undefined); */
					if (typeof val == "undefined" || val == null) { return; }

					var selector = _selector.call(this); /* validate selector */

					return $(selector).each(function () {

						var $this = $(this);

						if ($this.data(pluginPfx)) { /* check if plugin has initialized */

							var d = $this.data(pluginPfx), o = d.opt,
								/* method default options */
								methodDefaults = {
									trigger: "external", /* method is by default triggered externally (e.g. from other scripts) */
									scrollInertia: o.scrollInertia, /* scrolling inertia (animation duration) */
									scrollEasing: "mcsEaseInOut", /* animation easing */
									moveDragger: false, /* move dragger instead of content */
									timeout: 60, /* scroll-to delay */
									callbacks: true, /* enable/disable callbacks */
									onStart: true,
									onUpdate: true,
									onComplete: true
								},
								methodOptions = $.extend(true, {}, methodDefaults, options),
								to = _arr.call(this, val), dur = methodOptions.scrollInertia > 0 && methodOptions.scrollInertia < 17 ? 17 : methodOptions.scrollInertia;

							/* translate yx values to actual scroll-to positions */
							to[0] = _to.call(this, to[0], "y");
							to[1] = _to.call(this, to[1], "x");

							/* 
							check if scroll-to value moves the dragger instead of content. 
							Only pixel values apply on dragger (e.g. 100, "100px", "-=100" etc.) 
							*/
							if (methodOptions.moveDragger) {
								to[0] *= d.scrollRatio.y;
								to[1] *= d.scrollRatio.x;
							}

							methodOptions.dur = _isTabHidden() ? 0 : dur; //skip animations if browser tab is hidden

							setTimeout(function () {
								/* do the scrolling */
								if (to[0] !== null && typeof to[0] !== "undefined" && o.axis !== "x" && d.overflowed[0]) { /* scroll y */
									methodOptions.dir = "y";
									methodOptions.overwrite = "all";
									_scrollTo($this, to[0].toString(), methodOptions);
								}
								if (to[1] !== null && typeof to[1] !== "undefined" && o.axis !== "y" && d.overflowed[1]) { /* scroll x */
									methodOptions.dir = "x";
									methodOptions.overwrite = "none";
									_scrollTo($this, to[1].toString(), methodOptions);
								}
							}, methodOptions.timeout);

						}

					});

				},
				/* ---------------------------------------- */



				/*
				plugin stop method 
				stops scrolling animation
				----------------------------------------
				usage: $(selector).mCustomScrollbar("stop");
				*/
				stop: function () {

					var selector = _selector.call(this); /* validate selector */

					return $(selector).each(function () {

						var $this = $(this);

						if ($this.data(pluginPfx)) { /* check if plugin has initialized */

							_stop($this);

						}

					});

				},
				/* ---------------------------------------- */



				/*
				plugin disable method 
				temporarily disables the scrollbar(s) 
				----------------------------------------
				usage: $(selector).mCustomScrollbar("disable",reset); 
				reset (boolean): resets content position to 0 
				*/
				disable: function (r) {

					var selector = _selector.call(this); /* validate selector */

					return $(selector).each(function () {

						var $this = $(this);

						if ($this.data(pluginPfx)) { /* check if plugin has initialized */

							var d = $this.data(pluginPfx);

							_autoUpdate.call(this, "remove"); /* remove automatic updating */

							_unbindEvents.call(this); /* unbind events */

							if (r) { _resetContentPosition.call(this); } /* reset content position */

							_scrollbarVisibility.call(this, true); /* show/hide scrollbar(s) */

							$this.addClass(classes[3]); /* add disable class */

						}

					});

				},
				/* ---------------------------------------- */



				/*
				plugin destroy method 
				completely removes the scrollbar(s) and returns the element to its original state
				----------------------------------------
				usage: $(selector).mCustomScrollbar("destroy"); 
				*/
				destroy: function () {

					var selector = _selector.call(this); /* validate selector */

					return $(selector).each(function () {

						var $this = $(this);

						if ($this.data(pluginPfx)) { /* check if plugin has initialized */

							var d = $this.data(pluginPfx), o = d.opt,
								mCustomScrollBox = $("#mCSB_" + d.idx),
								mCSB_container = $("#mCSB_" + d.idx + "_container"),
								scrollbar = $(".mCSB_" + d.idx + "_scrollbar");

							if (o.live) { removeLiveTimers(o.liveSelector || $(selector).selector); } /* remove live timers */

							_autoUpdate.call(this, "remove"); /* remove automatic updating */

							_unbindEvents.call(this); /* unbind events */

							_resetContentPosition.call(this); /* reset content position */

							$this.removeData(pluginPfx); /* remove plugin data object */

							_delete(this, "mcs"); /* delete callbacks object */

							/* remove plugin markup */
							scrollbar.remove(); /* remove scrollbar(s) first (those can be either inside or outside plugin's inner wrapper) */
							mCSB_container.find("img." + classes[2]).removeClass(classes[2]); /* remove loaded images flag */
							mCustomScrollBox.replaceWith(mCSB_container.contents()); /* replace plugin's inner wrapper with the original content */
							/* remove plugin classes from the element and add destroy class */
							$this.removeClass(pluginNS + " _" + pluginPfx + "_" + d.idx + " " + classes[6] + " " + classes[7] + " " + classes[5] + " " + classes[3]).addClass(classes[4]);

						}

					});

				}
				/* ---------------------------------------- */

			},





			/* 
			----------------------------------------
			FUNCTIONS
			----------------------------------------
			*/

			/* validates selector (if selector is invalid or undefined uses the default one) */
			_selector = function () {
				return (typeof $(this) !== "object" || $(this).length < 1) ? defaultSelector : this;
			},
			/* -------------------- */


			/* changes options according to theme */
			_theme = function (obj) {
				var fixedSizeScrollbarThemes = ["rounded", "rounded-dark", "rounded-dots", "rounded-dots-dark"],
					nonExpandedScrollbarThemes = ["rounded-dots", "rounded-dots-dark", "3d", "3d-dark", "3d-thick", "3d-thick-dark", "inset", "inset-dark", "inset-2", "inset-2-dark", "inset-3", "inset-3-dark"],
					disabledScrollButtonsThemes = ["minimal", "minimal-dark"],
					enabledAutoHideScrollbarThemes = ["minimal", "minimal-dark"],
					scrollbarPositionOutsideThemes = ["minimal", "minimal-dark"];
				obj.autoDraggerLength = $.inArray(obj.theme, fixedSizeScrollbarThemes) > -1 ? false : obj.autoDraggerLength;
				obj.autoExpandScrollbar = $.inArray(obj.theme, nonExpandedScrollbarThemes) > -1 ? false : obj.autoExpandScrollbar;
				obj.scrollButtons.enable = $.inArray(obj.theme, disabledScrollButtonsThemes) > -1 ? false : obj.scrollButtons.enable;
				obj.autoHideScrollbar = $.inArray(obj.theme, enabledAutoHideScrollbarThemes) > -1 ? true : obj.autoHideScrollbar;
				obj.scrollbarPosition = $.inArray(obj.theme, scrollbarPositionOutsideThemes) > -1 ? "outside" : obj.scrollbarPosition;
			},
			/* -------------------- */


			/* live option timers removal */
			removeLiveTimers = function (selector) {
				if (liveTimers[selector]) {
					clearTimeout(liveTimers[selector]);
					_delete(liveTimers, selector);
				}
			},
			/* -------------------- */


			/* normalizes axis option to valid values: "y", "x", "yx" */
			_findAxis = function (val) {
				return (val === "yx" || val === "xy" || val === "auto") ? "yx" : (val === "x" || val === "horizontal") ? "x" : "y";
			},
			/* -------------------- */


			/* normalizes scrollButtons.scrollType option to valid values: "stepless", "stepped" */
			_findScrollButtonsType = function (val) {
				return (val === "stepped" || val === "pixels" || val === "step" || val === "click") ? "stepped" : "stepless";
			},
			/* -------------------- */


			/* generates plugin markup */
			_pluginMarkup = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					expandClass = o.autoExpandScrollbar ? " " + classes[1] + "_expand" : "",
					scrollbar = ["<div id='mCSB_" + d.idx + "_scrollbar_vertical' class='mCSB_scrollTools mCSB_" + d.idx + "_scrollbar mCS-" + o.theme + " mCSB_scrollTools_vertical" + expandClass + "'><div class='" + classes[12] + "'><div id='mCSB_" + d.idx + "_dragger_vertical' class='mCSB_dragger' style='position:absolute;'><div class='mCSB_dragger_bar' /></div><div class='mCSB_draggerRail' /></div></div>", "<div id='mCSB_" + d.idx + "_scrollbar_horizontal' class='mCSB_scrollTools mCSB_" + d.idx + "_scrollbar mCS-" + o.theme + " mCSB_scrollTools_horizontal" + expandClass + "'><div class='" + classes[12] + "'><div id='mCSB_" + d.idx + "_dragger_horizontal' class='mCSB_dragger' style='position:absolute;'><div class='mCSB_dragger_bar' /></div><div class='mCSB_draggerRail' /></div></div>"],
					wrapperClass = o.axis === "yx" ? "mCSB_vertical_horizontal" : o.axis === "x" ? "mCSB_horizontal" : "mCSB_vertical",
					scrollbars = o.axis === "yx" ? scrollbar[0] + scrollbar[1] : o.axis === "x" ? scrollbar[1] : scrollbar[0],
					contentWrapper = o.axis === "yx" ? "<div id='mCSB_" + d.idx + "_container_wrapper' class='mCSB_container_wrapper' />" : "",
					autoHideClass = o.autoHideScrollbar ? " " + classes[6] : "",
					scrollbarDirClass = (o.axis !== "x" && d.langDir === "rtl") ? " " + classes[7] : "";
				if (o.setWidth) { $this.css("width", o.setWidth); } /* set element width */
				if (o.setHeight) { $this.css("height", o.setHeight); } /* set element height */
				o.setLeft = (o.axis !== "y" && d.langDir === "rtl") ? "989999px" : o.setLeft; /* adjust left position for rtl direction */
				$this.addClass(pluginNS + " _" + pluginPfx + "_" + d.idx + autoHideClass + scrollbarDirClass).wrapInner("<div id='mCSB_" + d.idx + "' class='mCustomScrollBox mCS-" + o.theme + " " + wrapperClass + "'><div id='mCSB_" + d.idx + "_container' class='mCSB_container' style='position:relative; top:" + o.setTop + "; left:" + o.setLeft + ";' dir='" + d.langDir + "' /></div>");
				var mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container");
				if (o.axis !== "y" && !o.advanced.autoExpandHorizontalScroll) {
					mCSB_container.css("width", _contentWidth(mCSB_container));
				}
				if (o.scrollbarPosition === "outside") {
					if ($this.css("position") === "static") { /* requires elements with non-static position */
						$this.css("position", "relative");
					}
					$this.css("overflow", "visible");
					mCustomScrollBox.addClass("mCSB_outside").after(scrollbars);
				} else {
					mCustomScrollBox.addClass("mCSB_inside").append(scrollbars);
					mCSB_container.wrap(contentWrapper);
				}
				_scrollButtons.call(this); /* add scrollbar buttons */
				/* minimum dragger length */
				var mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")];
				mCSB_dragger[0].css("min-height", mCSB_dragger[0].height());
				mCSB_dragger[1].css("min-width", mCSB_dragger[1].width());
			},
			/* -------------------- */


			/* calculates content width */
			_contentWidth = function (el) {
				var val = [el[0].scrollWidth, Math.max.apply(Math, el.children().map(function () { return $(this).outerWidth(true); }).get())], w = el.parent().width();
				return val[0] > w ? val[0] : val[1] > w ? val[1] : "100%";
			},
			/* -------------------- */


			/* expands content horizontally */
			_expandContentHorizontally = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					mCSB_container = $("#mCSB_" + d.idx + "_container");
				if (o.advanced.autoExpandHorizontalScroll && o.axis !== "y") {
					/* calculate scrollWidth */
					mCSB_container.css({ "width": "auto", "min-width": 0, "overflow-x": "scroll" });
					var w = Math.ceil(mCSB_container[0].scrollWidth);
					if (o.advanced.autoExpandHorizontalScroll === 3 || (o.advanced.autoExpandHorizontalScroll !== 2 && w > mCSB_container.parent().width())) {
						mCSB_container.css({ "width": w, "min-width": "100%", "overflow-x": "inherit" });
					} else {
						/* 
						wrap content with an infinite width div and set its position to absolute and width to auto. 
						Setting width to auto before calculating the actual width is important! 
						We must let the browser set the width as browser zoom values are impossible to calculate.
						*/
						mCSB_container.css({ "overflow-x": "inherit", "position": "absolute" })
							.wrap("<div class='mCSB_h_wrapper' style='position:relative; left:0; width:999999px;' />")
							.css({ /* set actual width, original position and un-wrap */
								/* 
								get the exact width (with decimals) and then round-up. 
								Using jquery outerWidth() will round the width value which will mess up with inner elements that have non-integer width
								*/
								"width": (Math.ceil(mCSB_container[0].getBoundingClientRect().right + 0.4) - Math.floor(mCSB_container[0].getBoundingClientRect().left)),
								"min-width": "100%",
								"position": "relative"
							}).unwrap();
					}
				}
			},
			/* -------------------- */


			/* adds scrollbar buttons */
			_scrollButtons = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					mCSB_scrollTools = $(".mCSB_" + d.idx + "_scrollbar:first"),
					tabindex = !_isNumeric(o.scrollButtons.tabindex) ? "" : "tabindex='" + o.scrollButtons.tabindex + "'",
					btnHTML = [
						"<a href='#' class='" + classes[13] + "' " + tabindex + " />",
						"<a href='#' class='" + classes[14] + "' " + tabindex + " />",
						"<a href='#' class='" + classes[15] + "' " + tabindex + " />",
						"<a href='#' class='" + classes[16] + "' " + tabindex + " />"
					],
					btn = [(o.axis === "x" ? btnHTML[2] : btnHTML[0]), (o.axis === "x" ? btnHTML[3] : btnHTML[1]), btnHTML[2], btnHTML[3]];
				if (o.scrollButtons.enable) {
					mCSB_scrollTools.prepend(btn[0]).append(btn[1]).next(".mCSB_scrollTools").prepend(btn[2]).append(btn[3]);
				}
			},
			/* -------------------- */


			/* auto-adjusts scrollbar dragger length */
			_setDraggerLength = function () {
				var $this = $(this), d = $this.data(pluginPfx),
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")],
					ratio = [mCustomScrollBox.height() / mCSB_container.outerHeight(false), mCustomScrollBox.width() / mCSB_container.outerWidth(false)],
					l = [
						parseInt(mCSB_dragger[0].css("min-height")), Math.round(ratio[0] * mCSB_dragger[0].parent().height()),
						parseInt(mCSB_dragger[1].css("min-width")), Math.round(ratio[1] * mCSB_dragger[1].parent().width())
					],
					h = oldIE && (l[1] < l[0]) ? l[0] : l[1], w = oldIE && (l[3] < l[2]) ? l[2] : l[3];
				mCSB_dragger[0].css({
					"height": h, "max-height": (mCSB_dragger[0].parent().height() - 10)
				}).find(".mCSB_dragger_bar").css({ "line-height": l[0] + "px" });
				mCSB_dragger[1].css({
					"width": w, "max-width": (mCSB_dragger[1].parent().width() - 10)
				});
			},
			/* -------------------- */


			/* calculates scrollbar to content ratio */
			_scrollRatio = function () {
				var $this = $(this), d = $this.data(pluginPfx),
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")],
					scrollAmount = [mCSB_container.outerHeight(false) - mCustomScrollBox.height(), mCSB_container.outerWidth(false) - mCustomScrollBox.width()],
					ratio = [
						scrollAmount[0] / (mCSB_dragger[0].parent().height() - mCSB_dragger[0].height()),
						scrollAmount[1] / (mCSB_dragger[1].parent().width() - mCSB_dragger[1].width())
					];
				d.scrollRatio = { y: ratio[0], x: ratio[1] };
			},
			/* -------------------- */


			/* toggles scrolling classes */
			_onDragClasses = function (el, action, xpnd) {
				var expandClass = xpnd ? classes[0] + "_expanded" : "",
					scrollbar = el.closest(".mCSB_scrollTools");
				if (action === "active") {
					el.toggleClass(classes[0] + " " + expandClass); scrollbar.toggleClass(classes[1]);
					el[0]._draggable = el[0]._draggable ? 0 : 1;
				} else {
					if (!el[0]._draggable) {
						if (action === "hide") {
							el.removeClass(classes[0]); scrollbar.removeClass(classes[1]);
						} else {
							el.addClass(classes[0]); scrollbar.addClass(classes[1]);
						}
					}
				}
			},
			/* -------------------- */


			/* checks if content overflows its container to determine if scrolling is required */
			_overflowed = function () {
				var $this = $(this), d = $this.data(pluginPfx),
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					contentHeight = d.overflowed == null ? mCSB_container.height() : mCSB_container.outerHeight(false),
					contentWidth = d.overflowed == null ? mCSB_container.width() : mCSB_container.outerWidth(false),
					h = mCSB_container[0].scrollHeight, w = mCSB_container[0].scrollWidth;
				if (h > contentHeight) { contentHeight = h; }
				if (w > contentWidth) { contentWidth = w; }
				return [contentHeight > mCustomScrollBox.height(), contentWidth > mCustomScrollBox.width()];
			},
			/* -------------------- */


			/* resets content position to 0 */
			_resetContentPosition = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")];
				_stop($this); /* stop any current scrolling before resetting */
				if ((o.axis !== "x" && !d.overflowed[0]) || (o.axis === "y" && d.overflowed[0])) { /* reset y */
					mCSB_dragger[0].add(mCSB_container).css("top", 0);
					_scrollTo($this, "_resetY");
				}
				if ((o.axis !== "y" && !d.overflowed[1]) || (o.axis === "x" && d.overflowed[1])) { /* reset x */
					var cx = dx = 0;
					if (d.langDir === "rtl") { /* adjust left position for rtl direction */
						cx = mCustomScrollBox.width() - mCSB_container.outerWidth(false);
						dx = Math.abs(cx / d.scrollRatio.x);
					}
					mCSB_container.css("left", cx);
					mCSB_dragger[1].css("left", dx);
					_scrollTo($this, "_resetX");
				}
			},
			/* -------------------- */


			/* binds scrollbar events */
			_bindEvents = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt;
				if (!d.bindEvents) { /* check if events are already bound */
					_draggable.call(this);
					if (o.contentTouchScroll) { _contentDraggable.call(this); }
					_selectable.call(this);
					if (o.mouseWheel.enable) { /* bind mousewheel fn when plugin is available */
						function _mwt() {
							mousewheelTimeout = setTimeout(function () {
								if (!$.event.special.mousewheel) {
									_mwt();
								} else {
									clearTimeout(mousewheelTimeout);
									_mousewheel.call($this[0]);
								}
							}, 100);
						}
						var mousewheelTimeout;
						_mwt();
					}
					_draggerRail.call(this);
					_wrapperScroll.call(this);
					if (o.advanced.autoScrollOnFocus) { _focus.call(this); }
					if (o.scrollButtons.enable) { _buttons.call(this); }
					if (o.keyboard.enable) { _keyboard.call(this); }
					d.bindEvents = true;
				}
			},
			/* -------------------- */


			/* unbinds scrollbar events */
			_unbindEvents = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					namespace = pluginPfx + "_" + d.idx,
					sb = ".mCSB_" + d.idx + "_scrollbar",
					sel = $("#mCSB_" + d.idx + ",#mCSB_" + d.idx + "_container,#mCSB_" + d.idx + "_container_wrapper," + sb + " ." + classes[12] + ",#mCSB_" + d.idx + "_dragger_vertical,#mCSB_" + d.idx + "_dragger_horizontal," + sb + ">a"),
					mCSB_container = $("#mCSB_" + d.idx + "_container");
				if (o.advanced.releaseDraggableSelectors) { sel.add($(o.advanced.releaseDraggableSelectors)); }
				if (o.advanced.extraDraggableSelectors) { sel.add($(o.advanced.extraDraggableSelectors)); }
				if (d.bindEvents) { /* check if events are bound */
					/* unbind namespaced events from document/selectors */
					$(document).add($(!_canAccessIFrame() || top.document)).unbind("." + namespace);
					sel.each(function () {
						$(this).unbind("." + namespace);
					});
					/* clear and delete timeouts/objects */
					clearTimeout($this[0]._focusTimeout); _delete($this[0], "_focusTimeout");
					clearTimeout(d.sequential.step); _delete(d.sequential, "step");
					clearTimeout(mCSB_container[0].onCompleteTimeout); _delete(mCSB_container[0], "onCompleteTimeout");
					d.bindEvents = false;
				}
			},
			/* -------------------- */


			/* toggles scrollbar visibility */
			_scrollbarVisibility = function (disabled) {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					contentWrapper = $("#mCSB_" + d.idx + "_container_wrapper"),
					content = contentWrapper.length ? contentWrapper : $("#mCSB_" + d.idx + "_container"),
					scrollbar = [$("#mCSB_" + d.idx + "_scrollbar_vertical"), $("#mCSB_" + d.idx + "_scrollbar_horizontal")],
					mCSB_dragger = [scrollbar[0].find(".mCSB_dragger"), scrollbar[1].find(".mCSB_dragger")];
				if (o.axis !== "x") {
					if (d.overflowed[0] && !disabled) {
						scrollbar[0].add(mCSB_dragger[0]).add(scrollbar[0].children("a")).css("display", "block");
						content.removeClass(classes[8] + " " + classes[10]);
					} else {
						if (o.alwaysShowScrollbar) {
							if (o.alwaysShowScrollbar !== 2) { mCSB_dragger[0].css("display", "none"); }
							content.removeClass(classes[10]);
						} else {
							scrollbar[0].css("display", "none");
							content.addClass(classes[10]);
						}
						content.addClass(classes[8]);
					}
				}
				if (o.axis !== "y") {
					if (d.overflowed[1] && !disabled) {
						scrollbar[1].add(mCSB_dragger[1]).add(scrollbar[1].children("a")).css("display", "block");
						content.removeClass(classes[9] + " " + classes[11]);
					} else {
						if (o.alwaysShowScrollbar) {
							if (o.alwaysShowScrollbar !== 2) { mCSB_dragger[1].css("display", "none"); }
							content.removeClass(classes[11]);
						} else {
							scrollbar[1].css("display", "none");
							content.addClass(classes[11]);
						}
						content.addClass(classes[9]);
					}
				}
				if (!d.overflowed[0] && !d.overflowed[1]) {
					$this.addClass(classes[5]);
				} else {
					$this.removeClass(classes[5]);
				}
			},
			/* -------------------- */


			/* returns input coordinates of pointer, touch and mouse events (relative to document) */
			_coordinates = function (e) {
				var t = e.type, o = e.target.ownerDocument !== document && frameElement !== null ? [$(frameElement).offset().top, $(frameElement).offset().left] : null,
					io = _canAccessIFrame() && e.target.ownerDocument !== top.document && frameElement !== null ? [$(e.view.frameElement).offset().top, $(e.view.frameElement).offset().left] : [0, 0];
				switch (t) {
					case "pointerdown": case "MSPointerDown": case "pointermove": case "MSPointerMove": case "pointerup": case "MSPointerUp":
						return o ? [e.originalEvent.pageY - o[0] + io[0], e.originalEvent.pageX - o[1] + io[1], false] : [e.originalEvent.pageY, e.originalEvent.pageX, false];
						break;
					case "touchstart": case "touchmove": case "touchend":
						var touch = e.originalEvent.touches[0] || e.originalEvent.changedTouches[0],
							touches = e.originalEvent.touches.length || e.originalEvent.changedTouches.length;
						return e.target.ownerDocument !== document ? [touch.screenY, touch.screenX, touches > 1] : [touch.pageY, touch.pageX, touches > 1];
						break;
					default:
						return o ? [e.pageY - o[0] + io[0], e.pageX - o[1] + io[1], false] : [e.pageY, e.pageX, false];
				}
			},
			/* -------------------- */


			/* 
			SCROLLBAR DRAG EVENTS
			scrolls content via scrollbar dragging 
			*/
			_draggable = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					namespace = pluginPfx + "_" + d.idx,
					draggerId = ["mCSB_" + d.idx + "_dragger_vertical", "mCSB_" + d.idx + "_dragger_horizontal"],
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					mCSB_dragger = $("#" + draggerId[0] + ",#" + draggerId[1]),
					draggable, dragY, dragX,
					rds = o.advanced.releaseDraggableSelectors ? mCSB_dragger.add($(o.advanced.releaseDraggableSelectors)) : mCSB_dragger,
					eds = o.advanced.extraDraggableSelectors ? $(!_canAccessIFrame() || top.document).add($(o.advanced.extraDraggableSelectors)) : $(!_canAccessIFrame() || top.document);
				mCSB_dragger.bind("contextmenu." + namespace, function (e) {
					e.preventDefault(); //prevent right click
				}).bind("mousedown." + namespace + " touchstart." + namespace + " pointerdown." + namespace + " MSPointerDown." + namespace, function (e) {
					e.stopImmediatePropagation();
					e.preventDefault();
					if (!_mouseBtnLeft(e)) { return; } /* left mouse button only */
					touchActive = true;
					if (oldIE) { document.onselectstart = function () { return false; } } /* disable text selection for IE < 9 */
					_iframe.call(mCSB_container, false); /* enable scrollbar dragging over iframes by disabling their events */
					_stop($this);
					draggable = $(this);
					var offset = draggable.offset(), y = _coordinates(e)[0] - offset.top, x = _coordinates(e)[1] - offset.left,
						h = draggable.height() + offset.top, w = draggable.width() + offset.left;
					if (y < h && y > 0 && x < w && x > 0) {
						dragY = y;
						dragX = x;
					}
					_onDragClasses(draggable, "active", o.autoExpandScrollbar);
				}).bind("touchmove." + namespace, function (e) {
					e.stopImmediatePropagation();
					e.preventDefault();
					var offset = draggable.offset(), y = _coordinates(e)[0] - offset.top, x = _coordinates(e)[1] - offset.left;
					_drag(dragY, dragX, y, x);
				});
				$(document).add(eds).bind("mousemove." + namespace + " pointermove." + namespace + " MSPointerMove." + namespace, function (e) {
					if (draggable) {
						var offset = draggable.offset(), y = _coordinates(e)[0] - offset.top, x = _coordinates(e)[1] - offset.left;
						if (dragY === y && dragX === x) { return; } /* has it really moved? */
						_drag(dragY, dragX, y, x);
					}
				}).add(rds).bind("mouseup." + namespace + " touchend." + namespace + " pointerup." + namespace + " MSPointerUp." + namespace, function (e) {
					if (draggable) {
						_onDragClasses(draggable, "active", o.autoExpandScrollbar);
						draggable = null;
					}
					touchActive = false;
					if (oldIE) { document.onselectstart = null; } /* enable text selection for IE < 9 */
					_iframe.call(mCSB_container, true); /* enable iframes events */
				});
				function _drag(dragY, dragX, y, x) {
					mCSB_container[0].idleTimer = o.scrollInertia < 233 ? 250 : 0;
					if (draggable.attr("id") === draggerId[1]) {
						var dir = "x", to = ((draggable[0].offsetLeft - dragX) + x) * d.scrollRatio.x;
					} else {
						var dir = "y", to = ((draggable[0].offsetTop - dragY) + y) * d.scrollRatio.y;
					}
					_scrollTo($this, to.toString(), { dir: dir, drag: true });
				}
			},
			/* -------------------- */


			/* 
			TOUCH SWIPE EVENTS
			scrolls content via touch swipe 
			Emulates the native touch-swipe scrolling with momentum found in iOS, Android and WP devices 
			*/
			_contentDraggable = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					namespace = pluginPfx + "_" + d.idx,
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")],
					draggable, dragY, dragX, touchStartY, touchStartX, touchMoveY = [], touchMoveX = [], startTime, runningTime, endTime, distance, speed, amount,
					durA = 0, durB, overwrite = o.axis === "yx" ? "none" : "all", touchIntent = [], touchDrag, docDrag,
					iframe = mCSB_container.find("iframe"),
					events = [
						"touchstart." + namespace + " pointerdown." + namespace + " MSPointerDown." + namespace, //start
						"touchmove." + namespace + " pointermove." + namespace + " MSPointerMove." + namespace, //move
						"touchend." + namespace + " pointerup." + namespace + " MSPointerUp." + namespace //end
					],
					touchAction = document.body.style.touchAction !== undefined && document.body.style.touchAction !== "";
				mCSB_container.bind(events[0], function (e) {
					_onTouchstart(e);
				}).bind(events[1], function (e) {
					_onTouchmove(e);
				});
				mCustomScrollBox.bind(events[0], function (e) {
					_onTouchstart2(e);
				}).bind(events[2], function (e) {
					_onTouchend(e);
				});
				if (iframe.length) {
					iframe.each(function () {
						$(this).bind("load", function () {
							/* bind events on accessible iframes */
							if (_canAccessIFrame(this)) {
								$(this.contentDocument || this.contentWindow.document).bind(events[0], function (e) {
									_onTouchstart(e);
									_onTouchstart2(e);
								}).bind(events[1], function (e) {
									_onTouchmove(e);
								}).bind(events[2], function (e) {
									_onTouchend(e);
								});
							}
						});
					});
				}
				function _onTouchstart(e) {
					if (!_pointerTouch(e) || touchActive || _coordinates(e)[2]) { touchable = 0; return; }
					touchable = 1; touchDrag = 0; docDrag = 0; draggable = 1;
					$this.removeClass("mCS_touch_action");
					var offset = mCSB_container.offset();
					dragY = _coordinates(e)[0] - offset.top;
					dragX = _coordinates(e)[1] - offset.left;
					touchIntent = [_coordinates(e)[0], _coordinates(e)[1]];
				}
				function _onTouchmove(e) {
					if (!_pointerTouch(e) || touchActive || _coordinates(e)[2]) { return; }
					if (!o.documentTouchScroll) { e.preventDefault(); }
					e.stopImmediatePropagation();
					if (docDrag && !touchDrag) { return; }
					if (draggable) {
						runningTime = _getTime();
						var offset = mCustomScrollBox.offset(), y = _coordinates(e)[0] - offset.top, x = _coordinates(e)[1] - offset.left,
							easing = "mcsLinearOut";
						touchMoveY.push(y);
						touchMoveX.push(x);
						touchIntent[2] = Math.abs(_coordinates(e)[0] - touchIntent[0]); touchIntent[3] = Math.abs(_coordinates(e)[1] - touchIntent[1]);
						if (d.overflowed[0]) {
							var limit = mCSB_dragger[0].parent().height() - mCSB_dragger[0].height(),
								prevent = ((dragY - y) > 0 && (y - dragY) > -(limit * d.scrollRatio.y) && (touchIntent[3] * 2 < touchIntent[2] || o.axis === "yx"));
						}
						if (d.overflowed[1]) {
							var limitX = mCSB_dragger[1].parent().width() - mCSB_dragger[1].width(),
								preventX = ((dragX - x) > 0 && (x - dragX) > -(limitX * d.scrollRatio.x) && (touchIntent[2] * 2 < touchIntent[3] || o.axis === "yx"));
						}
						if (prevent || preventX) { /* prevent native document scrolling */
							if (!touchAction) { e.preventDefault(); }
							touchDrag = 1;
						} else {
							docDrag = 1;
							$this.addClass("mCS_touch_action");
						}
						if (touchAction) { e.preventDefault(); }
						amount = o.axis === "yx" ? [(dragY - y), (dragX - x)] : o.axis === "x" ? [null, (dragX - x)] : [(dragY - y), null];
						mCSB_container[0].idleTimer = 250;
						if (d.overflowed[0]) { _drag(amount[0], durA, easing, "y", "all", true); }
						if (d.overflowed[1]) { _drag(amount[1], durA, easing, "x", overwrite, true); }
					}
				}
				function _onTouchstart2(e) {
					if (!_pointerTouch(e) || touchActive || _coordinates(e)[2]) { touchable = 0; return; }
					touchable = 1;
					e.stopImmediatePropagation();
					_stop($this);
					startTime = _getTime();
					var offset = mCustomScrollBox.offset();
					touchStartY = _coordinates(e)[0] - offset.top;
					touchStartX = _coordinates(e)[1] - offset.left;
					touchMoveY = []; touchMoveX = [];
				}
				function _onTouchend(e) {
					if (!_pointerTouch(e) || touchActive || _coordinates(e)[2]) { return; }
					draggable = 0;
					e.stopImmediatePropagation();
					touchDrag = 0; docDrag = 0;
					endTime = _getTime();
					var offset = mCustomScrollBox.offset(), y = _coordinates(e)[0] - offset.top, x = _coordinates(e)[1] - offset.left;
					if ((endTime - runningTime) > 30) { return; }
					speed = 1000 / (endTime - startTime);
					var easing = "mcsEaseOut", slow = speed < 2.5,
						diff = slow ? [touchMoveY[touchMoveY.length - 2], touchMoveX[touchMoveX.length - 2]] : [0, 0];
					distance = slow ? [(y - diff[0]), (x - diff[1])] : [y - touchStartY, x - touchStartX];
					var absDistance = [Math.abs(distance[0]), Math.abs(distance[1])];
					speed = slow ? [Math.abs(distance[0] / 4), Math.abs(distance[1] / 4)] : [speed, speed];
					var a = [
						Math.abs(mCSB_container[0].offsetTop) - (distance[0] * _m((absDistance[0] / speed[0]), speed[0])),
						Math.abs(mCSB_container[0].offsetLeft) - (distance[1] * _m((absDistance[1] / speed[1]), speed[1]))
					];
					amount = o.axis === "yx" ? [a[0], a[1]] : o.axis === "x" ? [null, a[1]] : [a[0], null];
					durB = [(absDistance[0] * 4) + o.scrollInertia, (absDistance[1] * 4) + o.scrollInertia];
					var md = parseInt(o.contentTouchScroll) || 0; /* absolute minimum distance required */
					amount[0] = absDistance[0] > md ? amount[0] : 0;
					amount[1] = absDistance[1] > md ? amount[1] : 0;
					if (d.overflowed[0]) { _drag(amount[0], durB[0], easing, "y", overwrite, false); }
					if (d.overflowed[1]) { _drag(amount[1], durB[1], easing, "x", overwrite, false); }
				}
				function _m(ds, s) {
					var r = [s * 1.5, s * 2, s / 1.5, s / 2];
					if (ds > 90) {
						return s > 4 ? r[0] : r[3];
					} else if (ds > 60) {
						return s > 3 ? r[3] : r[2];
					} else if (ds > 30) {
						return s > 8 ? r[1] : s > 6 ? r[0] : s > 4 ? s : r[2];
					} else {
						return s > 8 ? s : r[3];
					}
				}
				function _drag(amount, dur, easing, dir, overwrite, drag) {
					if (!amount) { return; }
					_scrollTo($this, amount.toString(), { dur: dur, scrollEasing: easing, dir: dir, overwrite: overwrite, drag: drag });
				}
			},
			/* -------------------- */


			/* 
			SELECT TEXT EVENTS 
			scrolls content when text is selected 
			*/
			_selectable = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt, seq = d.sequential,
					namespace = pluginPfx + "_" + d.idx,
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent(),
					action;
				mCSB_container.bind("mousedown." + namespace, function (e) {
					if (touchable) { return; }
					if (!action) { action = 1; touchActive = true; }
				}).add(document).bind("mousemove." + namespace, function (e) {
					if (!touchable && action && _sel()) {
						var offset = mCSB_container.offset(),
							y = _coordinates(e)[0] - offset.top + mCSB_container[0].offsetTop, x = _coordinates(e)[1] - offset.left + mCSB_container[0].offsetLeft;
						if (y > 0 && y < wrapper.height() && x > 0 && x < wrapper.width()) {
							if (seq.step) { _seq("off", null, "stepped"); }
						} else {
							if (o.axis !== "x" && d.overflowed[0]) {
								if (y < 0) {
									_seq("on", 38);
								} else if (y > wrapper.height()) {
									_seq("on", 40);
								}
							}
							if (o.axis !== "y" && d.overflowed[1]) {
								if (x < 0) {
									_seq("on", 37);
								} else if (x > wrapper.width()) {
									_seq("on", 39);
								}
							}
						}
					}
				}).bind("mouseup." + namespace + " dragend." + namespace, function (e) {
					if (touchable) { return; }
					if (action) { action = 0; _seq("off", null); }
					touchActive = false;
				});
				function _sel() {
					return window.getSelection ? window.getSelection().toString() :
						document.selection && document.selection.type != "Control" ? document.selection.createRange().text : 0;
				}
				function _seq(a, c, s) {
					seq.type = s && action ? "stepped" : "stepless";
					seq.scrollAmount = 10;
					_sequentialScroll($this, a, c, "mcsLinearOut", s ? 60 : null);
				}
			},
			/* -------------------- */


			/* 
			MOUSE WHEEL EVENT
			scrolls content via mouse-wheel 
			via mouse-wheel plugin (https://github.com/brandonaaron/jquery-mousewheel)
			*/
			_mousewheel = function () {
				if (!$(this).data(pluginPfx)) { return; } /* Check if the scrollbar is ready to use mousewheel events (issue: #185) */
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					namespace = pluginPfx + "_" + d.idx,
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_dragger = [$("#mCSB_" + d.idx + "_dragger_vertical"), $("#mCSB_" + d.idx + "_dragger_horizontal")],
					iframe = $("#mCSB_" + d.idx + "_container").find("iframe");
				if (iframe.length) {
					iframe.each(function () {
						$(this).bind("load", function () {
							/* bind events on accessible iframes */
							if (_canAccessIFrame(this)) {
								$(this.contentDocument || this.contentWindow.document).bind("mousewheel." + namespace, function (e, delta) {
									_onMousewheel(e, delta);
								});
							}
						});
					});
				}
				mCustomScrollBox.bind("mousewheel." + namespace, function (e, delta) {
					_onMousewheel(e, delta);
				});
				function _onMousewheel(e, delta) {
					_stop($this);
					if (_disableMousewheel($this, e.target)) { return; } /* disables mouse-wheel when hovering specific elements */
					var deltaFactor = o.mouseWheel.deltaFactor !== "auto" ? parseInt(o.mouseWheel.deltaFactor) : (oldIE && e.deltaFactor < 100) ? 100 : e.deltaFactor || 100,
						dur = o.scrollInertia;
					if (o.axis === "x" || o.mouseWheel.axis === "x") {
						var dir = "x",
							px = [Math.round(deltaFactor * d.scrollRatio.x), parseInt(o.mouseWheel.scrollAmount)],
							amount = o.mouseWheel.scrollAmount !== "auto" ? px[1] : px[0] >= mCustomScrollBox.width() ? mCustomScrollBox.width() * 0.9 : px[0],
							contentPos = Math.abs($("#mCSB_" + d.idx + "_container")[0].offsetLeft),
							draggerPos = mCSB_dragger[1][0].offsetLeft,
							limit = mCSB_dragger[1].parent().width() - mCSB_dragger[1].width(),
							dlt = o.mouseWheel.axis === "y" ? (e.deltaY || delta) : e.deltaX;
					} else {
						var dir = "y",
							px = [Math.round(deltaFactor * d.scrollRatio.y), parseInt(o.mouseWheel.scrollAmount)],
							amount = o.mouseWheel.scrollAmount !== "auto" ? px[1] : px[0] >= mCustomScrollBox.height() ? mCustomScrollBox.height() * 0.9 : px[0],
							contentPos = Math.abs($("#mCSB_" + d.idx + "_container")[0].offsetTop),
							draggerPos = mCSB_dragger[0][0].offsetTop,
							limit = mCSB_dragger[0].parent().height() - mCSB_dragger[0].height(),
							dlt = e.deltaY || delta;
					}
					if ((dir === "y" && !d.overflowed[0]) || (dir === "x" && !d.overflowed[1])) { return; }
					if (o.mouseWheel.invert || e.webkitDirectionInvertedFromDevice) { dlt = -dlt; }
					if (o.mouseWheel.normalizeDelta) { dlt = dlt < 0 ? -1 : 1; }
					if ((dlt > 0 && draggerPos !== 0) || (dlt < 0 && draggerPos !== limit) || o.mouseWheel.preventDefault) {
						e.stopImmediatePropagation();
						e.preventDefault();
					}
					if (e.deltaFactor < 5 && !o.mouseWheel.normalizeDelta) {
						//very low deltaFactor values mean some kind of delta acceleration (e.g. osx trackpad), so adjusting scrolling accordingly
						amount = e.deltaFactor; dur = 17;
					}
					_scrollTo($this, (contentPos - (dlt * amount)).toString(), { dir: dir, dur: dur });
				}
			},
			/* -------------------- */


			/* checks if iframe can be accessed */
			_canAccessIFrameCache = new Object(),
			_canAccessIFrame = function (iframe) {
				var result = false, cacheKey = false, html = null;
				if (iframe === undefined) {
					cacheKey = "#empty";
				} else if ($(iframe).attr("id") !== undefined) {
					cacheKey = $(iframe).attr("id");
				}
				if (cacheKey !== false && _canAccessIFrameCache[cacheKey] !== undefined) {
					return _canAccessIFrameCache[cacheKey];
				}
				if (!iframe) {
					try {
						var doc = top.document;
						html = doc.body.innerHTML;
					} catch (err) {/* do nothing */ }
					result = (html !== null);
				} else {
					try {
						var doc = iframe.contentDocument || iframe.contentWindow.document;
						html = doc.body.innerHTML;
					} catch (err) {/* do nothing */ }
					result = (html !== null);
				}
				if (cacheKey !== false) { _canAccessIFrameCache[cacheKey] = result; }
				return result;
			},
			/* -------------------- */


			/* switches iframe's pointer-events property (drag, mousewheel etc. over cross-domain iframes) */
			_iframe = function (evt) {
				var el = this.find("iframe");
				if (!el.length) { return; } /* check if content contains iframes */
				var val = !evt ? "none" : "auto";
				el.css("pointer-events", val); /* for IE11, iframe's display property should not be "block" */
			},
			/* -------------------- */


			/* disables mouse-wheel when hovering specific elements like select, datalist etc. */
			_disableMousewheel = function (el, target) {
				var tag = target.nodeName.toLowerCase(),
					tags = el.data(pluginPfx).opt.mouseWheel.disableOver,
					/* elements that require focus */
					focusTags = ["select", "textarea"];
				return $.inArray(tag, tags) > -1 && !($.inArray(tag, focusTags) > -1 && !$(target).is(":focus"));
			},
			/* -------------------- */


			/* 
			DRAGGER RAIL CLICK EVENT
			scrolls content via dragger rail 
			*/
			_draggerRail = function () {
				var $this = $(this), d = $this.data(pluginPfx),
					namespace = pluginPfx + "_" + d.idx,
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent(),
					mCSB_draggerContainer = $(".mCSB_" + d.idx + "_scrollbar ." + classes[12]),
					clickable;
				mCSB_draggerContainer.bind("mousedown." + namespace + " touchstart." + namespace + " pointerdown." + namespace + " MSPointerDown." + namespace, function (e) {
					touchActive = true;
					if (!$(e.target).hasClass("mCSB_dragger")) { clickable = 1; }
				}).bind("touchend." + namespace + " pointerup." + namespace + " MSPointerUp." + namespace, function (e) {
					touchActive = false;
				}).bind("click." + namespace, function (e) {
					if (!clickable) { return; }
					clickable = 0;
					if ($(e.target).hasClass(classes[12]) || $(e.target).hasClass("mCSB_draggerRail")) {
						_stop($this);
						var el = $(this), mCSB_dragger = el.find(".mCSB_dragger");
						if (el.parent(".mCSB_scrollTools_horizontal").length > 0) {
							if (!d.overflowed[1]) { return; }
							var dir = "x",
								clickDir = e.pageX > mCSB_dragger.offset().left ? -1 : 1,
								to = Math.abs(mCSB_container[0].offsetLeft) - (clickDir * (wrapper.width() * 0.9));
						} else {
							if (!d.overflowed[0]) { return; }
							var dir = "y",
								clickDir = e.pageY > mCSB_dragger.offset().top ? -1 : 1,
								to = Math.abs(mCSB_container[0].offsetTop) - (clickDir * (wrapper.height() * 0.9));
						}
						_scrollTo($this, to.toString(), { dir: dir, scrollEasing: "mcsEaseInOut" });
					}
				});
			},
			/* -------------------- */


			/* 
			FOCUS EVENT
			scrolls content via element focus (e.g. clicking an input, pressing TAB key etc.)
			*/
			_focus = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					namespace = pluginPfx + "_" + d.idx,
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent();
				mCSB_container.bind("focusin." + namespace, function (e) {
					var el = $(document.activeElement),
						nested = mCSB_container.find(".mCustomScrollBox").length,
						dur = 0;
					if (!el.is(o.advanced.autoScrollOnFocus)) { return; }
					_stop($this);
					clearTimeout($this[0]._focusTimeout);
					$this[0]._focusTimer = nested ? (dur + 17) * nested : 0;
					$this[0]._focusTimeout = setTimeout(function () {
						var to = [_childPos(el)[0], _childPos(el)[1]],
							contentPos = [mCSB_container[0].offsetTop, mCSB_container[0].offsetLeft],
							isVisible = [
								(contentPos[0] + to[0] >= 0 && contentPos[0] + to[0] < wrapper.height() - el.outerHeight(false)),
								(contentPos[1] + to[1] >= 0 && contentPos[0] + to[1] < wrapper.width() - el.outerWidth(false))
							],
							overwrite = (o.axis === "yx" && !isVisible[0] && !isVisible[1]) ? "none" : "all";
						if (o.axis !== "x" && !isVisible[0]) {
							_scrollTo($this, to[0].toString(), { dir: "y", scrollEasing: "mcsEaseInOut", overwrite: overwrite, dur: dur });
						}
						if (o.axis !== "y" && !isVisible[1]) {
							_scrollTo($this, to[1].toString(), { dir: "x", scrollEasing: "mcsEaseInOut", overwrite: overwrite, dur: dur });
						}
					}, $this[0]._focusTimer);
				});
			},
			/* -------------------- */


			/* sets content wrapper scrollTop/scrollLeft always to 0 */
			_wrapperScroll = function () {
				var $this = $(this), d = $this.data(pluginPfx),
					namespace = pluginPfx + "_" + d.idx,
					wrapper = $("#mCSB_" + d.idx + "_container").parent();
				wrapper.bind("scroll." + namespace, function (e) {
					if (wrapper.scrollTop() !== 0 || wrapper.scrollLeft() !== 0) {
						$(".mCSB_" + d.idx + "_scrollbar").css("visibility", "hidden"); /* hide scrollbar(s) */
					}
				});
			},
			/* -------------------- */


			/* 
			BUTTONS EVENTS
			scrolls content via up, down, left and right buttons 
			*/
			_buttons = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt, seq = d.sequential,
					namespace = pluginPfx + "_" + d.idx,
					sel = ".mCSB_" + d.idx + "_scrollbar",
					btn = $(sel + ">a");
				btn.bind("contextmenu." + namespace, function (e) {
					e.preventDefault(); //prevent right click
				}).bind("mousedown." + namespace + " touchstart." + namespace + " pointerdown." + namespace + " MSPointerDown." + namespace + " mouseup." + namespace + " touchend." + namespace + " pointerup." + namespace + " MSPointerUp." + namespace + " mouseout." + namespace + " pointerout." + namespace + " MSPointerOut." + namespace + " click." + namespace, function (e) {
					e.preventDefault();
					if (!_mouseBtnLeft(e)) { return; } /* left mouse button only */
					var btnClass = $(this).attr("class");
					seq.type = o.scrollButtons.scrollType;
					switch (e.type) {
						case "mousedown": case "touchstart": case "pointerdown": case "MSPointerDown":
							if (seq.type === "stepped") { return; }
							touchActive = true;
							d.tweenRunning = false;
							_seq("on", btnClass);
							break;
						case "mouseup": case "touchend": case "pointerup": case "MSPointerUp":
						case "mouseout": case "pointerout": case "MSPointerOut":
							if (seq.type === "stepped") { return; }
							touchActive = false;
							if (seq.dir) { _seq("off", btnClass); }
							break;
						case "click":
							if (seq.type !== "stepped" || d.tweenRunning) { return; }
							_seq("on", btnClass);
							break;
					}
					function _seq(a, c) {
						seq.scrollAmount = o.scrollButtons.scrollAmount;
						_sequentialScroll($this, a, c);
					}
				});
			},
			/* -------------------- */


			/* 
			KEYBOARD EVENTS
			scrolls content via keyboard 
			Keys: up arrow, down arrow, left arrow, right arrow, PgUp, PgDn, Home, End
			*/
			_keyboard = function () {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt, seq = d.sequential,
					namespace = pluginPfx + "_" + d.idx,
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent(),
					editables = "input,textarea,select,datalist,keygen,[contenteditable='true']",
					iframe = mCSB_container.find("iframe"),
					events = ["blur." + namespace + " keydown." + namespace + " keyup." + namespace];
				if (iframe.length) {
					iframe.each(function () {
						$(this).bind("load", function () {
							/* bind events on accessible iframes */
							if (_canAccessIFrame(this)) {
								$(this.contentDocument || this.contentWindow.document).bind(events[0], function (e) {
									_onKeyboard(e);
								});
							}
						});
					});
				}
				mCustomScrollBox.attr("tabindex", "0").bind(events[0], function (e) {
					_onKeyboard(e);
				});
				function _onKeyboard(e) {
					switch (e.type) {
						case "blur":
							if (d.tweenRunning && seq.dir) { _seq("off", null); }
							break;
						case "keydown": case "keyup":
							var code = e.keyCode ? e.keyCode : e.which, action = "on";
							if ((o.axis !== "x" && (code === 38 || code === 40)) || (o.axis !== "y" && (code === 37 || code === 39))) {
								/* up (38), down (40), left (37), right (39) arrows */
								if (((code === 38 || code === 40) && !d.overflowed[0]) || ((code === 37 || code === 39) && !d.overflowed[1])) { return; }
								if (e.type === "keyup") { action = "off"; }
								if (!$(document.activeElement).is(editables)) {
									e.preventDefault();
									e.stopImmediatePropagation();
									_seq(action, code);
								}
							} else if (code === 33 || code === 34) {
								/* PgUp (33), PgDn (34) */
								if (d.overflowed[0] || d.overflowed[1]) {
									e.preventDefault();
									e.stopImmediatePropagation();
								}
								if (e.type === "keyup") {
									_stop($this);
									var keyboardDir = code === 34 ? -1 : 1;
									if (o.axis === "x" || (o.axis === "yx" && d.overflowed[1] && !d.overflowed[0])) {
										var dir = "x", to = Math.abs(mCSB_container[0].offsetLeft) - (keyboardDir * (wrapper.width() * 0.9));
									} else {
										var dir = "y", to = Math.abs(mCSB_container[0].offsetTop) - (keyboardDir * (wrapper.height() * 0.9));
									}
									_scrollTo($this, to.toString(), { dir: dir, scrollEasing: "mcsEaseInOut" });
								}
							} else if (code === 35 || code === 36) {
								/* End (35), Home (36) */
								if (!$(document.activeElement).is(editables)) {
									if (d.overflowed[0] || d.overflowed[1]) {
										e.preventDefault();
										e.stopImmediatePropagation();
									}
									if (e.type === "keyup") {
										if (o.axis === "x" || (o.axis === "yx" && d.overflowed[1] && !d.overflowed[0])) {
											var dir = "x", to = code === 35 ? Math.abs(wrapper.width() - mCSB_container.outerWidth(false)) : 0;
										} else {
											var dir = "y", to = code === 35 ? Math.abs(wrapper.height() - mCSB_container.outerHeight(false)) : 0;
										}
										_scrollTo($this, to.toString(), { dir: dir, scrollEasing: "mcsEaseInOut" });
									}
								}
							}
							break;
					}
					function _seq(a, c) {
						seq.type = o.keyboard.scrollType;
						seq.scrollAmount = o.keyboard.scrollAmount;
						if (seq.type === "stepped" && d.tweenRunning) { return; }
						_sequentialScroll($this, a, c);
					}
				}
			},
			/* -------------------- */


			/* scrolls content sequentially (used when scrolling via buttons, keyboard arrows etc.) */
			_sequentialScroll = function (el, action, trigger, e, s) {
				var d = el.data(pluginPfx), o = d.opt, seq = d.sequential,
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					once = seq.type === "stepped" ? true : false,
					steplessSpeed = o.scrollInertia < 26 ? 26 : o.scrollInertia, /* 26/1.5=17 */
					steppedSpeed = o.scrollInertia < 1 ? 17 : o.scrollInertia;
				switch (action) {
					case "on":
						seq.dir = [
							(trigger === classes[16] || trigger === classes[15] || trigger === 39 || trigger === 37 ? "x" : "y"),
							(trigger === classes[13] || trigger === classes[15] || trigger === 38 || trigger === 37 ? -1 : 1)
						];
						_stop(el);
						if (_isNumeric(trigger) && seq.type === "stepped") { return; }
						_on(once);
						break;
					case "off":
						_off();
						if (once || (d.tweenRunning && seq.dir)) {
							_on(true);
						}
						break;
				}

				/* starts sequence */
				function _on(once) {
					if (o.snapAmount) { seq.scrollAmount = !(o.snapAmount instanceof Array) ? o.snapAmount : seq.dir[0] === "x" ? o.snapAmount[1] : o.snapAmount[0]; } /* scrolling snapping */
					var c = seq.type !== "stepped", /* continuous scrolling */
						t = s ? s : !once ? 1000 / 60 : c ? steplessSpeed / 1.5 : steppedSpeed, /* timer */
						m = !once ? 2.5 : c ? 7.5 : 40, /* multiplier */
						contentPos = [Math.abs(mCSB_container[0].offsetTop), Math.abs(mCSB_container[0].offsetLeft)],
						ratio = [d.scrollRatio.y > 10 ? 10 : d.scrollRatio.y, d.scrollRatio.x > 10 ? 10 : d.scrollRatio.x],
						amount = seq.dir[0] === "x" ? contentPos[1] + (seq.dir[1] * (ratio[1] * m)) : contentPos[0] + (seq.dir[1] * (ratio[0] * m)),
						px = seq.dir[0] === "x" ? contentPos[1] + (seq.dir[1] * parseInt(seq.scrollAmount)) : contentPos[0] + (seq.dir[1] * parseInt(seq.scrollAmount)),
						to = seq.scrollAmount !== "auto" ? px : amount,
						easing = e ? e : !once ? "mcsLinear" : c ? "mcsLinearOut" : "mcsEaseInOut",
						onComplete = !once ? false : true;
					if (once && t < 17) {
						to = seq.dir[0] === "x" ? contentPos[1] : contentPos[0];
					}
					_scrollTo(el, to.toString(), { dir: seq.dir[0], scrollEasing: easing, dur: t, onComplete: onComplete });
					if (once) {
						seq.dir = false;
						return;
					}
					clearTimeout(seq.step);
					seq.step = setTimeout(function () {
						_on();
					}, t);
				}
				/* stops sequence */
				function _off() {
					clearTimeout(seq.step);
					_delete(seq, "step");
					_stop(el);
				}
			},
			/* -------------------- */


			/* returns a yx array from value */
			_arr = function (val) {
				var o = $(this).data(pluginPfx).opt, vals = [];
				if (typeof val === "function") { val = val(); } /* check if the value is a single anonymous function */
				/* check if value is object or array, its length and create an array with yx values */
				if (!(val instanceof Array)) { /* object value (e.g. {y:"100",x:"100"}, 100 etc.) */
					vals[0] = val.y ? val.y : val.x || o.axis === "x" ? null : val;
					vals[1] = val.x ? val.x : val.y || o.axis === "y" ? null : val;
				} else { /* array value (e.g. [100,100]) */
					vals = val.length > 1 ? [val[0], val[1]] : o.axis === "x" ? [null, val[0]] : [val[0], null];
				}
				/* check if array values are anonymous functions */
				if (typeof vals[0] === "function") { vals[0] = vals[0](); }
				if (typeof vals[1] === "function") { vals[1] = vals[1](); }
				return vals;
			},
			/* -------------------- */


			/* translates values (e.g. "top", 100, "100px", "#id") to actual scroll-to positions */
			_to = function (val, dir) {
				if (val == null || typeof val == "undefined") { return; }
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent(),
					t = typeof val;
				if (!dir) { dir = o.axis === "x" ? "x" : "y"; }
				var contentLength = dir === "x" ? mCSB_container.outerWidth(false) - wrapper.width() : mCSB_container.outerHeight(false) - wrapper.height(),
					contentPos = dir === "x" ? mCSB_container[0].offsetLeft : mCSB_container[0].offsetTop,
					cssProp = dir === "x" ? "left" : "top";
				switch (t) {
					case "function": /* this currently is not used. Consider removing it */
						return val();
						break;
					case "object": /* js/jquery object */
						var obj = val.jquery ? val : $(val);
						if (!obj.length) { return; }
						return dir === "x" ? _childPos(obj)[1] : _childPos(obj)[0];
						break;
					case "string": case "number":
						if (_isNumeric(val)) { /* numeric value */
							return Math.abs(val);
						} else if (val.indexOf("%") !== -1) { /* percentage value */
							return Math.abs(contentLength * parseInt(val) / 100);
						} else if (val.indexOf("-=") !== -1) { /* decrease value */
							return Math.abs(contentPos - parseInt(val.split("-=")[1]));
						} else if (val.indexOf("+=") !== -1) { /* inrease value */
							var p = (contentPos + parseInt(val.split("+=")[1]));
							return p >= 0 ? 0 : Math.abs(p);
						} else if (val.indexOf("px") !== -1 && _isNumeric(val.split("px")[0])) { /* pixels string value (e.g. "100px") */
							return Math.abs(val.split("px")[0]);
						} else {
							if (val === "top" || val === "left") { /* special strings */
								return 0;
							} else if (val === "bottom") {
								return Math.abs(wrapper.height() - mCSB_container.outerHeight(false));
							} else if (val === "right") {
								return Math.abs(wrapper.width() - mCSB_container.outerWidth(false));
							} else if (val === "first" || val === "last") {
								var obj = mCSB_container.find(":" + val);
								return dir === "x" ? _childPos(obj)[1] : _childPos(obj)[0];
							} else {
								if ($(val).length) { /* jquery selector */
									return dir === "x" ? _childPos($(val))[1] : _childPos($(val))[0];
								} else { /* other values (e.g. "100em") */
									mCSB_container.css(cssProp, val);
									methods.update.call(null, $this[0]);
									return;
								}
							}
						}
						break;
				}
			},
			/* -------------------- */


			/* calls the update method automatically */
			_autoUpdate = function (rem) {
				var $this = $(this), d = $this.data(pluginPfx), o = d.opt,
					mCSB_container = $("#mCSB_" + d.idx + "_container");
				if (rem) {
					/* 
					removes autoUpdate timer 
					usage: _autoUpdate.call(this,"remove");
					*/
					clearTimeout(mCSB_container[0].autoUpdate);
					_delete(mCSB_container[0], "autoUpdate");
					return;
				}
				upd();
				function upd() {
					clearTimeout(mCSB_container[0].autoUpdate);
					if ($this.parents("html").length === 0) {
						/* check element in dom tree */
						$this = null;
						return;
					}
					mCSB_container[0].autoUpdate = setTimeout(function () {
						/* update on specific selector(s) length and size change */
						if (o.advanced.updateOnSelectorChange) {
							d.poll.change.n = sizesSum();
							if (d.poll.change.n !== d.poll.change.o) {
								d.poll.change.o = d.poll.change.n;
								doUpd(3);
								return;
							}
						}
						/* update on main element and scrollbar size changes */
						if (o.advanced.updateOnContentResize) {
							d.poll.size.n = $this[0].scrollHeight + $this[0].scrollWidth + mCSB_container[0].offsetHeight + $this[0].offsetHeight + $this[0].offsetWidth;
							if (d.poll.size.n !== d.poll.size.o) {
								d.poll.size.o = d.poll.size.n;
								doUpd(1);
								return;
							}
						}
						/* update on image load */
						if (o.advanced.updateOnImageLoad) {
							if (!(o.advanced.updateOnImageLoad === "auto" && o.axis === "y")) { //by default, it doesn't run on vertical content
								d.poll.img.n = mCSB_container.find("img").length;
								if (d.poll.img.n !== d.poll.img.o) {
									d.poll.img.o = d.poll.img.n;
									mCSB_container.find("img").each(function () {
										imgLoader(this);
									});
									return;
								}
							}
						}
						if (o.advanced.updateOnSelectorChange || o.advanced.updateOnContentResize || o.advanced.updateOnImageLoad) { upd(); }
					}, o.advanced.autoUpdateTimeout);
				}
				/* a tiny image loader */
				function imgLoader(el) {
					if ($(el).hasClass(classes[2])) { doUpd(); return; }
					var img = new Image();
					function createDelegate(contextObject, delegateMethod) {
						return function () { return delegateMethod.apply(contextObject, arguments); }
					}
					function imgOnLoad() {
						this.onload = null;
						$(el).addClass(classes[2]);
						doUpd(2);
					}
					img.onload = createDelegate(img, imgOnLoad);
					img.src = el.src;
				}
				/* returns the total height and width sum of all elements matching the selector */
				function sizesSum() {
					if (o.advanced.updateOnSelectorChange === true) { o.advanced.updateOnSelectorChange = "*"; }
					var total = 0, sel = mCSB_container.find(o.advanced.updateOnSelectorChange);
					if (o.advanced.updateOnSelectorChange && sel.length > 0) { sel.each(function () { total += this.offsetHeight + this.offsetWidth; }); }
					return total;
				}
				/* calls the update method */
				function doUpd(cb) {
					clearTimeout(mCSB_container[0].autoUpdate);
					methods.update.call(null, $this[0], cb);
				}
			},
			/* -------------------- */


			/* snaps scrolling to a multiple of a pixels number */
			_snapAmount = function (to, amount, offset) {
				return (Math.round(to / amount) * amount - offset);
			},
			/* -------------------- */


			/* stops content and scrollbar animations */
			_stop = function (el) {
				var d = el.data(pluginPfx),
					sel = $("#mCSB_" + d.idx + "_container,#mCSB_" + d.idx + "_container_wrapper,#mCSB_" + d.idx + "_dragger_vertical,#mCSB_" + d.idx + "_dragger_horizontal");
				sel.each(function () {
					_stopTween.call(this);
				});
			},
			/* -------------------- */


			/* 
			ANIMATES CONTENT 
			This is where the actual scrolling happens
			*/
			_scrollTo = function (el, to, options) {
				var d = el.data(pluginPfx), o = d.opt,
					defaults = {
						trigger: "internal",
						dir: "y",
						scrollEasing: "mcsEaseOut",
						drag: false,
						dur: o.scrollInertia,
						overwrite: "all",
						callbacks: true,
						onStart: true,
						onUpdate: true,
						onComplete: true
					},
					options = $.extend(defaults, options),
					dur = [options.dur, (options.drag ? 0 : options.dur)],
					mCustomScrollBox = $("#mCSB_" + d.idx),
					mCSB_container = $("#mCSB_" + d.idx + "_container"),
					wrapper = mCSB_container.parent(),
					totalScrollOffsets = o.callbacks.onTotalScrollOffset ? _arr.call(el, o.callbacks.onTotalScrollOffset) : [0, 0],
					totalScrollBackOffsets = o.callbacks.onTotalScrollBackOffset ? _arr.call(el, o.callbacks.onTotalScrollBackOffset) : [0, 0];
				d.trigger = options.trigger;
				if (wrapper.scrollTop() !== 0 || wrapper.scrollLeft() !== 0) { /* always reset scrollTop/Left */
					$(".mCSB_" + d.idx + "_scrollbar").css("visibility", "visible");
					wrapper.scrollTop(0).scrollLeft(0);
				}
				if (to === "_resetY" && !d.contentReset.y) {
					/* callbacks: onOverflowYNone */
					if (_cb("onOverflowYNone")) { o.callbacks.onOverflowYNone.call(el[0]); }
					d.contentReset.y = 1;
				}
				if (to === "_resetX" && !d.contentReset.x) {
					/* callbacks: onOverflowXNone */
					if (_cb("onOverflowXNone")) { o.callbacks.onOverflowXNone.call(el[0]); }
					d.contentReset.x = 1;
				}
				if (to === "_resetY" || to === "_resetX") { return; }
				if ((d.contentReset.y || !el[0].mcs) && d.overflowed[0]) {
					/* callbacks: onOverflowY */
					if (_cb("onOverflowY")) { o.callbacks.onOverflowY.call(el[0]); }
					d.contentReset.x = null;
				}
				if ((d.contentReset.x || !el[0].mcs) && d.overflowed[1]) {
					/* callbacks: onOverflowX */
					if (_cb("onOverflowX")) { o.callbacks.onOverflowX.call(el[0]); }
					d.contentReset.x = null;
				}
				if (o.snapAmount) { /* scrolling snapping */
					var snapAmount = !(o.snapAmount instanceof Array) ? o.snapAmount : options.dir === "x" ? o.snapAmount[1] : o.snapAmount[0];
					to = _snapAmount(to, snapAmount, o.snapOffset);
				}
				switch (options.dir) {
					case "x":
						var mCSB_dragger = $("#mCSB_" + d.idx + "_dragger_horizontal"),
							property = "left",
							contentPos = mCSB_container[0].offsetLeft,
							limit = [
								mCustomScrollBox.width() - mCSB_container.outerWidth(false),
								mCSB_dragger.parent().width() - mCSB_dragger.width()
							],
							scrollTo = [to, to === 0 ? 0 : (to / d.scrollRatio.x)],
							tso = totalScrollOffsets[1],
							tsbo = totalScrollBackOffsets[1],
							totalScrollOffset = tso > 0 ? tso / d.scrollRatio.x : 0,
							totalScrollBackOffset = tsbo > 0 ? tsbo / d.scrollRatio.x : 0;
						break;
					case "y":
						var mCSB_dragger = $("#mCSB_" + d.idx + "_dragger_vertical"),
							property = "top",
							contentPos = mCSB_container[0].offsetTop,
							limit = [
								mCustomScrollBox.height() - mCSB_container.outerHeight(false),
								mCSB_dragger.parent().height() - mCSB_dragger.height()
							],
							scrollTo = [to, to === 0 ? 0 : (to / d.scrollRatio.y)],
							tso = totalScrollOffsets[0],
							tsbo = totalScrollBackOffsets[0],
							totalScrollOffset = tso > 0 ? tso / d.scrollRatio.y : 0,
							totalScrollBackOffset = tsbo > 0 ? tsbo / d.scrollRatio.y : 0;
						break;
				}
				if (scrollTo[1] < 0 || (scrollTo[0] === 0 && scrollTo[1] === 0)) {
					scrollTo = [0, 0];
				} else if (scrollTo[1] >= limit[1]) {
					scrollTo = [limit[0], limit[1]];
				} else {
					scrollTo[0] = -scrollTo[0];
				}
				if (!el[0].mcs) {
					_mcs();  /* init mcs object (once) to make it available before callbacks */
					if (_cb("onInit")) { o.callbacks.onInit.call(el[0]); } /* callbacks: onInit */
				}
				clearTimeout(mCSB_container[0].onCompleteTimeout);
				_tweenTo(mCSB_dragger[0], property, Math.round(scrollTo[1]), dur[1], options.scrollEasing);
				if (!d.tweenRunning && ((contentPos === 0 && scrollTo[0] >= 0) || (contentPos === limit[0] && scrollTo[0] <= limit[0]))) { return; }
				_tweenTo(mCSB_container[0], property, Math.round(scrollTo[0]), dur[0], options.scrollEasing, options.overwrite, {
					onStart: function () {
						if (options.callbacks && options.onStart && !d.tweenRunning) {
							/* callbacks: onScrollStart */
							if (_cb("onScrollStart")) { _mcs(); o.callbacks.onScrollStart.call(el[0]); }
							d.tweenRunning = true;
							_onDragClasses(mCSB_dragger);
							d.cbOffsets = _cbOffsets();
						}
					}, onUpdate: function () {
						if (options.callbacks && options.onUpdate) {
							/* callbacks: whileScrolling */
							if (_cb("whileScrolling")) { _mcs(); o.callbacks.whileScrolling.call(el[0]); }
						}
					}, onComplete: function () {
						if (options.callbacks && options.onComplete) {
							if (o.axis === "yx") { clearTimeout(mCSB_container[0].onCompleteTimeout); }
							var t = mCSB_container[0].idleTimer || 0;
							mCSB_container[0].onCompleteTimeout = setTimeout(function () {
								/* callbacks: onScroll, onTotalScroll, onTotalScrollBack */
								if (_cb("onScroll")) { _mcs(); o.callbacks.onScroll.call(el[0]); }
								if (_cb("onTotalScroll") && scrollTo[1] >= limit[1] - totalScrollOffset && d.cbOffsets[0]) { _mcs(); o.callbacks.onTotalScroll.call(el[0]); }
								if (_cb("onTotalScrollBack") && scrollTo[1] <= totalScrollBackOffset && d.cbOffsets[1]) { _mcs(); o.callbacks.onTotalScrollBack.call(el[0]); }
								d.tweenRunning = false;
								mCSB_container[0].idleTimer = 0;
								_onDragClasses(mCSB_dragger, "hide");
							}, t);
						}
					}
				});
				/* checks if callback function exists */
				function _cb(cb) {
					return d && o.callbacks[cb] && typeof o.callbacks[cb] === "function";
				}
				/* checks whether callback offsets always trigger */
				function _cbOffsets() {
					return [o.callbacks.alwaysTriggerOffsets || contentPos >= limit[0] + tso, o.callbacks.alwaysTriggerOffsets || contentPos <= -tsbo];
				}
				/* 
				populates object with useful values for the user 
				values: 
					content: this.mcs.content
					content top position: this.mcs.top 
					content left position: this.mcs.left 
					dragger top position: this.mcs.draggerTop 
					dragger left position: this.mcs.draggerLeft 
					scrolling y percentage: this.mcs.topPct 
					scrolling x percentage: this.mcs.leftPct 
					scrolling direction: this.mcs.direction
				*/
				function _mcs() {
					var cp = [mCSB_container[0].offsetTop, mCSB_container[0].offsetLeft], /* content position */
						dp = [mCSB_dragger[0].offsetTop, mCSB_dragger[0].offsetLeft], /* dragger position */
						cl = [mCSB_container.outerHeight(false), mCSB_container.outerWidth(false)], /* content length */
						pl = [mCustomScrollBox.height(), mCustomScrollBox.width()]; /* content parent length */
					el[0].mcs = {
						content: mCSB_container, /* original content wrapper as jquery object */
						top: cp[0], left: cp[1], draggerTop: dp[0], draggerLeft: dp[1],
						topPct: Math.round((100 * Math.abs(cp[0])) / (Math.abs(cl[0]) - pl[0])), leftPct: Math.round((100 * Math.abs(cp[1])) / (Math.abs(cl[1]) - pl[1])),
						direction: options.dir
					};
					/* 
					this refers to the original element containing the scrollbar(s)
					usage: this.mcs.top, this.mcs.leftPct etc. 
					*/
				}
			},
			/* -------------------- */


			/* 
			CUSTOM JAVASCRIPT ANIMATION TWEEN 
			Lighter and faster than jquery animate() and css transitions 
			Animates top/left properties and includes easings 
			*/
			_tweenTo = function (el, prop, to, duration, easing, overwrite, callbacks) {
				if (!el._mTween) { el._mTween = { top: {}, left: {} }; }
				var callbacks = callbacks || {},
					onStart = callbacks.onStart || function () { }, onUpdate = callbacks.onUpdate || function () { }, onComplete = callbacks.onComplete || function () { },
					startTime = _getTime(), _delay, progress = 0, from = el.offsetTop, elStyle = el.style, _request, tobj = el._mTween[prop];
				if (prop === "left") { from = el.offsetLeft; }
				var diff = to - from;
				tobj.stop = 0;
				if (overwrite !== "none") { _cancelTween(); }
				_startTween();
				function _step() {
					if (tobj.stop) { return; }
					if (!progress) { onStart.call(); }
					progress = _getTime() - startTime;
					_tween();
					if (progress >= tobj.time) {
						tobj.time = (progress > tobj.time) ? progress + _delay - (progress - tobj.time) : progress + _delay - 1;
						if (tobj.time < progress + 1) { tobj.time = progress + 1; }
					}
					if (tobj.time < duration) { tobj.id = _request(_step); } else { onComplete.call(); }
				}
				function _tween() {
					if (duration > 0) {
						tobj.currVal = _ease(tobj.time, from, diff, duration, easing);
						elStyle[prop] = Math.round(tobj.currVal) + "px";
					} else {
						elStyle[prop] = to + "px";
					}
					onUpdate.call();
				}
				function _startTween() {
					_delay = 1000 / 60;
					tobj.time = progress + _delay;
					_request = (!window.requestAnimationFrame) ? function (f) { _tween(); return setTimeout(f, 0.01); } : window.requestAnimationFrame;
					tobj.id = _request(_step);
				}
				function _cancelTween() {
					if (tobj.id == null) { return; }
					if (!window.requestAnimationFrame) {
						clearTimeout(tobj.id);
					} else { window.cancelAnimationFrame(tobj.id); }
					tobj.id = null;
				}
				function _ease(t, b, c, d, type) {
					switch (type) {
						case "linear": case "mcsLinear":
							return c * t / d + b;
							break;
						case "mcsLinearOut":
							t /= d; t--; return c * Math.sqrt(1 - t * t) + b;
							break;
						case "easeInOutSmooth":
							t /= d / 2;
							if (t < 1) return c / 2 * t * t + b;
							t--;
							return -c / 2 * (t * (t - 2) - 1) + b;
							break;
						case "easeInOutStrong":
							t /= d / 2;
							if (t < 1) return c / 2 * Math.pow(2, 10 * (t - 1)) + b;
							t--;
							return c / 2 * (-Math.pow(2, -10 * t) + 2) + b;
							break;
						case "easeInOut": case "mcsEaseInOut":
							t /= d / 2;
							if (t < 1) return c / 2 * t * t * t + b;
							t -= 2;
							return c / 2 * (t * t * t + 2) + b;
							break;
						case "easeOutSmooth":
							t /= d; t--;
							return -c * (t * t * t * t - 1) + b;
							break;
						case "easeOutStrong":
							return c * (-Math.pow(2, -10 * t / d) + 1) + b;
							break;
						case "easeOut": case "mcsEaseOut": default:
							var ts = (t /= d) * t, tc = ts * t;
							return b + c * (0.499999999999997 * tc * ts + -2.5 * ts * ts + 5.5 * tc + -6.5 * ts + 4 * t);
					}
				}
			},
			/* -------------------- */


			/* returns current time */
			_getTime = function () {
				if (window.performance && window.performance.now) {
					return window.performance.now();
				} else {
					if (window.performance && window.performance.webkitNow) {
						return window.performance.webkitNow();
					} else {
						if (Date.now) { return Date.now(); } else { return new Date().getTime(); }
					}
				}
			},
			/* -------------------- */


			/* stops a tween */
			_stopTween = function () {
				var el = this;
				if (!el._mTween) { el._mTween = { top: {}, left: {} }; }
				var props = ["top", "left"];
				for (var i = 0; i < props.length; i++) {
					var prop = props[i];
					if (el._mTween[prop].id) {
						if (!window.requestAnimationFrame) {
							clearTimeout(el._mTween[prop].id);
						} else { window.cancelAnimationFrame(el._mTween[prop].id); }
						el._mTween[prop].id = null;
						el._mTween[prop].stop = 1;
					}
				}
			},
			/* -------------------- */


			/* deletes a property (avoiding the exception thrown by IE) */
			_delete = function (c, m) {
				try { delete c[m]; } catch (e) { c[m] = null; }
			},
			/* -------------------- */


			/* detects left mouse button */
			_mouseBtnLeft = function (e) {
				return !(e.which && e.which !== 1);
			},
			/* -------------------- */


			/* detects if pointer type event is touch */
			_pointerTouch = function (e) {
				var t = e.originalEvent.pointerType;
				return !(t && t !== "touch" && t !== 2);
			},
			/* -------------------- */


			/* checks if value is numeric */
			_isNumeric = function (val) {
				return !isNaN(parseFloat(val)) && isFinite(val);
			},
			/* -------------------- */


			/* returns element position according to content */
			_childPos = function (el) {
				var p = el.parents(".mCSB_container");
				return [el.offset().top - p.offset().top, el.offset().left - p.offset().left];
			},
			/* -------------------- */


			/* checks if browser tab is hidden/inactive via Page Visibility API */
			_isTabHidden = function () {
				var prop = _getHiddenProp();
				if (!prop) return false;
				return document[prop];
				function _getHiddenProp() {
					var pfx = ["webkit", "moz", "ms", "o"];
					if ("hidden" in document) return "hidden"; //natively supported
					for (var i = 0; i < pfx.length; i++) { //prefixed
						if ((pfx[i] + "Hidden") in document)
							return pfx[i] + "Hidden";
					}
					return null; //not supported
				}
			};
		/* -------------------- */





		/* 
		----------------------------------------
		PLUGIN SETUP 
		----------------------------------------
		*/

		/* plugin constructor functions */
		$.fn[pluginNS] = function (method) { /* usage: $(selector).mCustomScrollbar(); */
			if (methods[method]) {
				return methods[method].apply(this, Array.prototype.slice.call(arguments, 1));
			} else if (typeof method === "object" || !method) {
				return methods.init.apply(this, arguments);
			} else {
				$.error("Method " + method + " does not exist");
			}
		};
		$[pluginNS] = function (method) { /* usage: $.mCustomScrollbar(); */
			if (methods[method]) {
				return methods[method].apply(this, Array.prototype.slice.call(arguments, 1));
			} else if (typeof method === "object" || !method) {
				return methods.init.apply(this, arguments);
			} else {
				$.error("Method " + method + " does not exist");
			}
		};

		/* 
		allow setting plugin default options. 
		usage: $.mCustomScrollbar.defaults.scrollInertia=500; 
		to apply any changed default options on default selectors (below), use inside document ready fn 
		e.g.: $(document).ready(function(){ $.mCustomScrollbar.defaults.scrollInertia=500; });
		*/
		$[pluginNS].defaults = defaults;

		/* 
		add window object (window.mCustomScrollbar) 
		usage: if(window.mCustomScrollbar){console.log("custom scrollbar plugin loaded");}
		*/
		window[pluginNS] = true;

		$(window).bind("load", function () {

			$(defaultSelector)[pluginNS](); /* add scrollbars automatically on default selector */

			/* extend jQuery expressions */
			$.extend($.expr[":"], {
				/* checks if element is within scrollable viewport */
				mcsInView: $.expr[":"].mcsInView || function (el) {
					var $el = $(el), content = $el.parents(".mCSB_container"), wrapper, cPos;
					if (!content.length) { return; }
					wrapper = content.parent();
					cPos = [content[0].offsetTop, content[0].offsetLeft];
					return cPos[0] + _childPos($el)[0] >= 0 && cPos[0] + _childPos($el)[0] < wrapper.height() - $el.outerHeight(false) &&
						cPos[1] + _childPos($el)[1] >= 0 && cPos[1] + _childPos($el)[1] < wrapper.width() - $el.outerWidth(false);
				},
				/* checks if element or part of element is in view of scrollable viewport */
				mcsInSight: $.expr[":"].mcsInSight || function (el, i, m) {
					var $el = $(el), elD, content = $el.parents(".mCSB_container"), wrapperView, pos, wrapperViewPct,
						pctVals = m[3] === "exact" ? [[1, 0], [1, 0]] : [[0.9, 0.1], [0.6, 0.4]];
					if (!content.length) { return; }
					elD = [$el.outerHeight(false), $el.outerWidth(false)];
					pos = [content[0].offsetTop + _childPos($el)[0], content[0].offsetLeft + _childPos($el)[1]];
					wrapperView = [content.parent()[0].offsetHeight, content.parent()[0].offsetWidth];
					wrapperViewPct = [elD[0] < wrapperView[0] ? pctVals[0] : pctVals[1], elD[1] < wrapperView[1] ? pctVals[0] : pctVals[1]];
					return pos[0] - (wrapperView[0] * wrapperViewPct[0][0]) < 0 && pos[0] + elD[0] - (wrapperView[0] * wrapperViewPct[0][1]) >= 0 &&
						pos[1] - (wrapperView[1] * wrapperViewPct[1][0]) < 0 && pos[1] + elD[1] - (wrapperView[1] * wrapperViewPct[1][1]) >= 0;
				},
				/* checks if element is overflowed having visible scrollbar(s) */
				mcsOverflow: $.expr[":"].mcsOverflow || function (el) {
					var d = $(el).data(pluginPfx);
					if (!d) { return; }
					return d.overflowed[0] || d.overflowed[1];
				}
			});

		});

	}))
}));
/*!
 * jQuery Mousewheel 3.1.13
 *
 * Copyright 2015 jQuery Foundation and other contributors
 * Released under the MIT license.
 * http://jquery.org/license
 */
!function (a) { "function" == typeof define && define.amd ? define(["jquery"], a) : "object" == typeof exports ? module.exports = a : a(jQuery) }(function (a) { function b(b) { var g = b || window.event, h = i.call(arguments, 1), j = 0, l = 0, m = 0, n = 0, o = 0, p = 0; if (b = a.event.fix(g), b.type = "mousewheel", "detail" in g && (m = -1 * g.detail), "wheelDelta" in g && (m = g.wheelDelta), "wheelDeltaY" in g && (m = g.wheelDeltaY), "wheelDeltaX" in g && (l = -1 * g.wheelDeltaX), "axis" in g && g.axis === g.HORIZONTAL_AXIS && (l = -1 * m, m = 0), j = 0 === m ? l : m, "deltaY" in g && (m = -1 * g.deltaY, j = m), "deltaX" in g && (l = g.deltaX, 0 === m && (j = -1 * l)), 0 !== m || 0 !== l) { if (1 === g.deltaMode) { var q = a.data(this, "mousewheel-line-height"); j *= q, m *= q, l *= q } else if (2 === g.deltaMode) { var r = a.data(this, "mousewheel-page-height"); j *= r, m *= r, l *= r } if (n = Math.max(Math.abs(m), Math.abs(l)), (!f || f > n) && (f = n, d(g, n) && (f /= 40)), d(g, n) && (j /= 40, l /= 40, m /= 40), j = Math[j >= 1 ? "floor" : "ceil"](j / f), l = Math[l >= 1 ? "floor" : "ceil"](l / f), m = Math[m >= 1 ? "floor" : "ceil"](m / f), k.settings.normalizeOffset && this.getBoundingClientRect) { var s = this.getBoundingClientRect(); o = b.clientX - s.left, p = b.clientY - s.top } return b.deltaX = l, b.deltaY = m, b.deltaFactor = f, b.offsetX = o, b.offsetY = p, b.deltaMode = 0, h.unshift(b, j, l, m), e && clearTimeout(e), e = setTimeout(c, 200), (a.event.dispatch || a.event.handle).apply(this, h) } } function c() { f = null } function d(a, b) { return k.settings.adjustOldDeltas && "mousewheel" === a.type && b % 120 === 0 } var e, f, g = ["wheel", "mousewheel", "DOMMouseScroll", "MozMousePixelScroll"], h = "onwheel" in document || document.documentMode >= 9 ? ["wheel"] : ["mousewheel", "DomMouseScroll", "MozMousePixelScroll"], i = Array.prototype.slice; if (a.event.fixHooks) for (var j = g.length; j;)a.event.fixHooks[g[--j]] = a.event.mouseHooks; var k = a.event.special.mousewheel = { version: "3.1.12", setup: function () { if (this.addEventListener) for (var c = h.length; c;)this.addEventListener(h[--c], b, !1); else this.onmousewheel = b; a.data(this, "mousewheel-line-height", k.getLineHeight(this)), a.data(this, "mousewheel-page-height", k.getPageHeight(this)) }, teardown: function () { if (this.removeEventListener) for (var c = h.length; c;)this.removeEventListener(h[--c], b, !1); else this.onmousewheel = null; a.removeData(this, "mousewheel-line-height"), a.removeData(this, "mousewheel-page-height") }, getLineHeight: function (b) { var c = a(b), d = c["offsetParent" in a.fn ? "offsetParent" : "parent"](); return d.length || (d = a("body")), parseInt(d.css("fontSize"), 10) || parseInt(c.css("fontSize"), 10) || 16 }, getPageHeight: function (b) { return a(b).height() }, settings: { adjustOldDeltas: !0, normalizeOffset: !0 } }; a.fn.extend({ mousewheel: function (a) { return a ? this.bind("mousewheel", a) : this.trigger("mousewheel") }, unmousewheel: function (a) { return this.unbind("mousewheel", a) } }) });
(function () {
	var a = "' of type ", l = "SCRIPT", p = "Uneven number of arguments", q = "array", t = "function", aa = "google.charts.load", ba = "hasOwnProperty", u = "number", v = "object", x = "pre-45", ca = "propertyIsEnumerable", y = "string", da = "text/javascript", ea = "toLocaleString"; function A() { return function (b) { return b } } function B() { return function () { } } function D(b) { return function () { return this[b] } } function E(b) { return function () { return b } } var F, G = G || {}; G.scope = {};
	G.Tk = function (b) { var c = 0; return function () { return c < b.length ? { done: !1, value: b[c++] } : { done: !0 } } }; G.Sk = function (b) { return { next: G.Tk(b) } }; G.Dd = function (b) { var c = "undefined" != typeof Symbol && Symbol.iterator && b[Symbol.iterator]; return c ? c.call(b) : G.Sk(b) }; G.Rk = function (b) { for (var c, d = []; !(c = b.next()).done;)d.push(c.value); return d }; G.ve = function (b) { return b instanceof Array ? b : G.Rk(G.Dd(b)) };
	G.$t = function (b, c, d) { b instanceof String && (b = String(b)); for (var e = b.length, f = 0; f < e; f++) { var g = b[f]; if (c.call(d, g, f, b)) return { Bm: f, Oo: g } } return { Bm: -1, Oo: void 0 } }; G.Aj = !1; G.hp = !1; G.ip = !1; G.Nr = !1; G.defineProperty = G.Aj || typeof Object.defineProperties == t ? Object.defineProperty : function (b, c, d) { b != Array.prototype && b != Object.prototype && (b[c] = d.value) }; G.fm = function (b) { return "undefined" != typeof window && window === b ? b : "undefined" != typeof global && null != global ? global : b }; G.global = G.fm(this);
	G.Pc = function (b, c) { if (c) { var d = G.global; b = b.split("."); for (var e = 0; e < b.length - 1; e++) { var f = b[e]; f in d || (d[f] = {}); d = d[f] } b = b[b.length - 1]; e = d[b]; c = c(e); c != e && null != c && G.defineProperty(d, b, { configurable: !0, writable: !0, value: c }) } }; G.Rg = function (b, c, d) { if (null == b) throw new TypeError("The 'this' value for String.prototype." + d + " must not be null or undefined"); if (c instanceof RegExp) throw new TypeError("First argument to String.prototype." + d + " must not be a regular expression"); return b + "" };
	G.Pc("String.prototype.repeat", function (b) { return b ? b : function (c) { var d = G.Rg(this, null, "repeat"); if (0 > c || 1342177279 < c) throw new RangeError("Invalid count value"); c |= 0; for (var e = ""; c;)if (c & 1 && (e += d), c >>>= 1) d += d; return e } }); G.$j = !1;
	G.Pc("Promise", function (b) {
		function c(k) { this.qa = g.$a; this.Ia = void 0; this.ic = []; var m = this.De(); try { k(m.resolve, m.reject) } catch (n) { m.reject(n) } } function d() { this.pb = null } function e(k) { return k instanceof c ? k : new c(function (m) { m(k) }) } if (b && !G.$j) return b; d.prototype.Ig = function (k) { if (null == this.pb) { this.pb = []; var m = this; this.Jg(function () { m.Ol() }) } this.pb.push(k) }; var f = G.global.setTimeout; d.prototype.Jg = function (k) { f(k, 0) }; d.prototype.Ol = function () {
			for (; this.pb && this.pb.length;) {
				var k = this.pb; this.pb =
					[]; for (var m = 0; m < k.length; ++m) { var n = k[m]; k[m] = null; try { n() } catch (r) { this.el(r) } }
			} this.pb = null
		}; d.prototype.el = function (k) { this.Jg(function () { throw k; }) }; var g = { $a: 0, nb: 1, Ja: 2 }; c.prototype.De = function () { function k(r) { return function (w) { n || (n = !0, r.call(m, w)) } } var m = this, n = !1; return { resolve: k(this.Qn), reject: k(this.sf) } }; c.prototype.Qn = function (k) {
			if (k === this) this.sf(new TypeError("A Promise cannot resolve to itself")); else if (k instanceof c) this.no(k); else {
				a: switch (typeof k) {
					case v: var m = null != k; break a;
					case t: m = !0; break a; default: m = !1
				}m ? this.Pn(k) : this.qh(k)
			}
		}; c.prototype.Pn = function (k) { var m = void 0; try { m = k.then } catch (n) { this.sf(n); return } typeof m == t ? this.oo(m, k) : this.qh(k) }; c.prototype.sf = function (k) { this.aj(g.Ja, k) }; c.prototype.qh = function (k) { this.aj(g.nb, k) }; c.prototype.aj = function (k, m) { if (this.qa != g.$a) throw Error("Cannot settle(" + k + ", " + m + "): Promise already settled in state" + this.qa); this.qa = k; this.Ia = m; this.Ql() }; c.prototype.Ql = function () {
			if (null != this.ic) {
				for (var k = 0; k < this.ic.length; ++k)h.Ig(this.ic[k]);
				this.ic = null
			}
		}; var h = new d; c.prototype.no = function (k) { var m = this.De(); k.dd(m.resolve, m.reject) }; c.prototype.oo = function (k, m) { var n = this.De(); try { k.call(m, n.resolve, n.reject) } catch (r) { n.reject(r) } }; c.prototype.then = function (k, m) { function n(z, C) { return typeof z == t ? function (U) { try { r(z(U)) } catch (V) { w(V) } } : C } var r, w, W = new c(function (z, C) { r = z; w = C }); this.dd(n(k, r), n(m, w)); return W }; c.prototype["catch"] = function (k) { return this.then(void 0, k) }; c.prototype.dd = function (k, m) {
			function n() {
				switch (r.qa) {
					case g.nb: k(r.Ia);
						break; case g.Ja: m(r.Ia); break; default: throw Error("Unexpected state: " + r.qa);
				}
			} var r = this; null == this.ic ? h.Ig(n) : this.ic.push(n)
		}; c.resolve = e; c.reject = function (k) { return new c(function (m, n) { n(k) }) }; c.race = function (k) { return new c(function (m, n) { for (var r = G.Dd(k), w = r.next(); !w.done; w = r.next())e(w.value).dd(m, n) }) }; c.all = function (k) {
			var m = G.Dd(k), n = m.next(); return n.done ? e([]) : new c(function (r, w) {
				function W(U) { return function (V) { z[U] = V; C--; 0 == C && r(z) } } var z = [], C = 0; do z.push(void 0), C++, e(n.value).dd(W(z.length -
					1), w), n = m.next(); while (!n.done)
			})
		}; return c
	}); G.Pc("Object.is", function (b) { return b ? b : function (c, d) { return c === d ? 0 !== c || 1 / c === 1 / d : c !== c && d !== d } }); G.Pc("Array.prototype.includes", function (b) { return b ? b : function (c, d) { var e = this; e instanceof String && (e = String(e)); var f = e.length; d = d || 0; for (0 > d && (d = Math.max(d + f, 0)); d < f; d++) { var g = e[d]; if (g === c || Object.is(g, c)) return !0 } return !1 } }); G.Pc("String.prototype.includes", function (b) { return b ? b : function (c, d) { return -1 !== G.Rg(this, c, "includes").indexOf(c, d || 0) } });
	var H = H || {}; H.global = this || self; H.ca = function (b) { return void 0 !== b }; H.L = function (b) { return typeof b == y }; H.Em = function (b) { return "boolean" == typeof b }; H.Bb = function (b) { return typeof b == u }; H.ih = function (b, c, d) { b = b.split("."); d = d || H.global; b[0] in d || "undefined" == typeof d.execScript || d.execScript("var " + b[0]); for (var e; b.length && (e = b.shift());)!b.length && H.ca(c) ? d[e] = c : d = d[e] && d[e] !== Object.prototype[e] ? d[e] : d[e] = {} }; H.define = function (b, c) { return c }; H.eq = 2012; H.sa = !0; H.S = "en"; H.pe = !0; H.Ck = !1; H.Vj = !H.sa;
	H.Yp = !1; H.kw = function (b) { if (H.mi()) throw Error("goog.provide cannot be used within a module."); H.Wg(b) }; H.Wg = function (b, c) { H.ih(b, c) }; H.Sh = function () { null === H.Ee && (H.Ee = H.km()); return H.Ee }; H.kk = /^[\w+/_-]+[=]{0,2}$/; H.Ee = null; H.km = function () { var b = H.global.document; return (b = b.querySelector && b.querySelector("script[nonce]")) && (b = b.nonce || b.getAttribute("nonce")) && H.kk.test(b) ? b : "" }; H.Jk = /^[a-zA-Z_$][a-zA-Z0-9._$]*$/;
	H.pf = function (b) {
		if (!H.L(b) || !b || -1 == b.search(H.Jk)) throw Error("Invalid module identifier"); if (!H.li()) throw Error("Module " + b + " has been loaded incorrectly. Note, modules cannot be loaded as normal scripts. They require some kind of pre-processing step. You're likely trying to load a module via a script tag or as a part of a concatenated bundle without rewriting the module. For more info see: https://github.com/google/closure-library/wiki/goog.module:-an-ES6-module-like-alternative-to-goog.provide.");
		if (H.Fa.Mc) throw Error("goog.module may only be called once per module."); H.Fa.Mc = b
	}; H.pf.get = E(null); H.pf.Au = E(null); H.rc = { bg: "es6", je: "goog" }; H.Fa = null; H.mi = function () { return H.li() || H.Lm() }; H.li = function () { return !!H.Fa && H.Fa.type == H.rc.je }; H.Lm = function () { if (H.Fa && H.Fa.type == H.rc.bg) return !0; var b = H.global.$jscomp; return b ? typeof b.Ne != t ? !1 : !!b.Ne() : !1 }; H.pf.Fe = function () { H.Fa.Fe = !0 };
	H.Jt = function (b) { if (H.Fa) H.Fa.Mc = b; else { var c = H.global.$jscomp; if (!c || typeof c.Ne != t) throw Error('Module with namespace "' + b + '" has been loaded incorrectly.'); c = c.Nn(c.Ne()); H.Ei[b] = { Sl: c, type: H.rc.bg, un: b } } }; H.dx = function (b) { if (H.Vj) throw b = b || "", Error("Importing test-only code into non-debug environment" + (b ? ": " + b : ".")); }; H.fu = B(); H.wb = function (b) { b = b.split("."); for (var c = H.global, d = 0; d < b.length; d++)if (c = c[b[d]], !H.zb(c)) return null; return c };
	H.Qu = function (b, c) { c = c || H.global; for (var d in b) c[d] = b[d] }; H.As = B(); H.Gx = !1; H.Zp = !0; H.Fi = function (b) { H.global.console && H.global.console.error(b) }; H.Nn = B(); H.vw = function () { return {} }; H.hl = ""; H.Rb = B(); H.ys = function () { throw Error("unimplemented abstract method"); }; H.Bs = B(); H.bv = []; H.Wq = !0; H.yk = H.sa; H.Ei = {}; H.Kp = !1; H.ls = "detect"; H.gp = !1; H.ms = ""; H.Ek = "transpile.js"; H.$e = null;
	H.Mo = function () { if (null == H.$e) { try { var b = !eval('"use strict";let x = 1; function f() { return typeof x; };f() == "number";') } catch (c) { b = !1 } H.$e = b } return H.$e }; H.Ro = function (b) { return "(function(){" + b + "\n;})();\n" };
	H.Pv = function (b) { var c = H.Fa; try { H.Fa = { Mc: "", Fe: !1, type: H.rc.je }; if (H.Wa(b)) var d = b.call(void 0, {}); else if (H.L(b)) H.Mo() && (b = H.Ro(b)), d = H.gn.call(void 0, b); else throw Error("Invalid module definition"); var e = H.Fa.Mc; if (H.L(e) && e) H.Fa.Fe ? H.Wg(e, d) : H.yk && Object.seal && typeof d == v && null != d && Object.seal(d), H.Ei[e] = { Sl: d, type: H.rc.je, un: H.Fa.Mc }; else throw Error('Invalid module name "' + e + '"'); } finally { H.Fa = c } }; H.gn = function (b) { eval(b); return {} };
	H.$v = function (b) { b = b.split("/"); for (var c = 0; c < b.length;)"." == b[c] ? b.splice(c, 1) : c && ".." == b[c] && b[c - 1] && ".." != b[c - 1] ? b.splice(--c, 2) : c++; return b.join("/") }; H.cn = function (b) { if (H.global.Oj) return H.global.Oj(b); try { var c = new H.global.XMLHttpRequest; c.open("get", b, !1); c.send(); return 0 == c.status || 200 == c.status ? c.responseText : null } catch (d) { return null } };
	H.Ax = function (b, c, d) {
		var e = H.global.$jscomp; e || (H.global.$jscomp = e = {}); var f = e.Gf; if (!f) { var g = H.hl + H.Ek, h = H.cn(g); if (h) { (function () { (0, eval)(h + "\n//# sourceURL=" + g) }).call(H.global); if (H.global.$gwtExport && H.global.$gwtExport.$jscomp && !H.global.$gwtExport.$jscomp.transpile) throw Error('The transpiler did not properly export the "transpile" method. $gwtExport: ' + JSON.stringify(H.global.$gwtExport)); H.global.$jscomp.Gf = H.global.$gwtExport.$jscomp.transpile; e = H.global.$jscomp; f = e.Gf } } if (!f) {
			var k = " requires transpilation but no transpiler was found.";
			k += ' Please add "//javascript/closure:transpiler" as a data dependency to ensure it is included.'; f = e.Gf = function (m, n) { H.Fi(n + k); return m }
		} return f(b, c, d)
	};
	H.ra = function (b) {
		var c = typeof b; if (c == v) if (b) { if (b instanceof Array) return q; if (b instanceof Object) return c; var d = Object.prototype.toString.call(b); if ("[object Window]" == d) return v; if ("[object Array]" == d || typeof b.length == u && "undefined" != typeof b.splice && "undefined" != typeof b.propertyIsEnumerable && !b.propertyIsEnumerable("splice")) return q; if ("[object Function]" == d || "undefined" != typeof b.call && "undefined" != typeof b.propertyIsEnumerable && !b.propertyIsEnumerable("call")) return t } else return "null";
		else if (c == t && "undefined" == typeof b.call) return v; return c
	}; H.zv = function (b) { return null === b }; H.zb = function (b) { return null != b }; H.isArray = function (b) { return H.ra(b) == q }; H.ma = function (b) { var c = H.ra(b); return c == q || c == v && typeof b.length == u }; H.kv = function (b) { return H.Da(b) && typeof b.getFullYear == t }; H.Wa = function (b) { return H.ra(b) == t }; H.Da = function (b) { var c = typeof b; return c == v && null != b || c == t }; H.Uh = function (b) { return b[H.Gb] || (b[H.Gb] = ++H.Eo) }; H.Vu = function (b) { return !!b[H.Gb] };
	H.Mn = function (b) { null !== b && "removeAttribute" in b && b.removeAttribute(H.Gb); try { delete b[H.Gb] } catch (c) { } }; H.Gb = "closure_uid_" + (1E9 * Math.random() >>> 0); H.Eo = 0; H.yu = H.Uh; H.qw = H.Mn; H.ul = function (b) { var c = H.ra(b); if (c == v || c == q) { if (typeof b.clone === t) return b.clone(); c = c == q ? [] : {}; for (var d in b) c[d] = H.ul(b[d]); return c } return b }; H.jl = function (b, c, d) { return b.call.apply(b.bind, arguments) };
	H.il = function (b, c, d) { if (!b) throw Error(); if (2 < arguments.length) { var e = Array.prototype.slice.call(arguments, 2); return function () { var f = Array.prototype.slice.call(arguments); Array.prototype.unshift.apply(f, e); return b.apply(c, f) } } return function () { return b.apply(c, arguments) } }; H.bind = function (b, c, d) { H.bind = Function.prototype.bind && -1 != Function.prototype.bind.toString().indexOf("native code") ? H.jl : H.il; return H.bind.apply(null, arguments) };
	H.Sb = function (b, c) { var d = Array.prototype.slice.call(arguments, 1); return function () { var e = d.slice(); e.push.apply(e, arguments); return b.apply(this, e) } }; H.Vv = function (b, c) { for (var d in c) b[d] = c[d] }; H.now = H.pe && Date.now || function () { return +new Date };
	H.Pu = function (b) { if (H.global.execScript) H.global.execScript(b, "JavaScript"); else if (H.global.eval) { if (null == H.nd) { try { H.global.eval("var _evalTest_ = 1;") } catch (e) { } if ("undefined" != typeof H.global._evalTest_) { try { delete H.global._evalTest_ } catch (e) { } H.nd = !0 } else H.nd = !1 } if (H.nd) H.global.eval(b); else { var c = H.global.document, d = c.createElement(l); d.type = da; d.defer = !1; d.appendChild(c.createTextNode(b)); c.head.appendChild(d); c.head.removeChild(d) } } else throw Error("goog.globalEval not available"); };
	H.nd = null; H.vu = function (b, c) { function d(g) { g = g.split("-"); for (var h = [], k = 0; k < g.length; k++)h.push(e(g[k])); return h.join("-") } function e(g) { return H.ah[g] || g } if ("." == String(b).charAt(0)) throw Error('className passed in goog.getCssName must not start with ".". You passed: ' + b); var f = H.ah ? "BY_WHOLE" == H.Bl ? e : d : A(); b = c ? b + "-" + f(c) : f(b); return H.global.Nj ? H.global.Nj(b) : b }; H.Lw = function (b, c) { H.ah = b; H.Bl = c };
	H.Bu = function (b, c, d) { d && d.b && (b = b.replace(/</g, "&lt;")); c && (b = b.replace(/\{\$([^}]+)}/g, function (e, f) { return null != c && f in c ? c[f] : e })); return b }; H.Cu = A(); H.Dc = function (b, c) { H.ih(b, c, void 0) }; H.Wt = function (b, c, d) { b[c] = d }; H.yb = function (b, c) { function d() { } d.prototype = c.prototype; b.gj = c.prototype; b.prototype = new d; b.prototype.constructor = b; b.gl = function (e, f, g) { for (var h = Array(arguments.length - 2), k = 2; k < arguments.length; k++)h[k - 2] = arguments[k]; return c.prototype[f].apply(e, h) } };
	H.gl = function (b, c, d) {
		var e = arguments.callee.caller; if (H.Ck || H.sa && !e) throw Error("arguments.caller not defined.  goog.base() cannot be used with strict mode code. See http://www.ecma-international.org/ecma-262/5.1/#sec-C"); if ("undefined" !== typeof e.gj) { for (var f = Array(arguments.length - 1), g = 1; g < arguments.length; g++)f[g - 1] = arguments[g]; return e.gj.constructor.apply(b, f) } if (typeof c != y && "symbol" != typeof c) throw Error("method names provided to goog.base must be a string or a symbol"); f = Array(arguments.length -
			2); for (g = 2; g < arguments.length; g++)f[g - 2] = arguments[g]; g = !1; for (var h = b.constructor.prototype; h; h = Object.getPrototypeOf(h))if (h[c] === e) g = !0; else if (g) return h[c].apply(b, f); if (b[c] === e) return b.constructor.prototype[c].apply(b, f); throw Error("goog.base called from a method of one name to a method of a different name");
	}; H.scope = function (b) { if (H.mi()) throw Error("goog.scope is not supported within a module."); b.call(H.global) };
	H.La = function (b, c) { var d = c.constructor, e = c.to; d && d != Object.prototype.constructor || (d = function () { throw Error("cannot instantiate an interface (no constructor defined)."); }); d = H.La.xl(d, b); b && H.yb(d, b); delete c.constructor; delete c.to; H.La.Fg(d.prototype, c); null != e && (e instanceof Function ? e(d) : H.La.Fg(d, e)); return d }; H.La.xk = H.sa;
	H.La.xl = function (b, c) { function d() { var f = b.apply(this, arguments) || this; f[H.Gb] = f[H.Gb]; this.constructor === d && e && Object.seal instanceof Function && Object.seal(f); return f } if (!H.La.xk) return b; var e = !H.La.Wm(c); return d }; H.La.Wm = function (b) { return b && b.prototype && b.prototype[H.Gk] }; H.La.ng = ["constructor", ba, "isPrototypeOf", ca, ea, "toString", "valueOf"];
	H.La.Fg = function (b, c) { for (var d in c) Object.prototype.hasOwnProperty.call(c, d) && (b[d] = c[d]); for (var e = 0; e < H.La.ng.length; e++)d = H.La.ng[e], Object.prototype.hasOwnProperty.call(c, d) && (b[d] = c[d]) }; H.tx = B(); H.Gk = "goog_defineClass_legacy_unsealable"; H.ad = ""; H.Ad = A(); H.$g = function (b) { var c = null; if ("undefined" === typeof TrustedTypes || !TrustedTypes.createPolicy) return c; try { c = TrustedTypes.createPolicy(b, { createHTML: H.Ad, createScript: H.Ad, createScriptURL: H.Ad, createURL: H.Ad }) } catch (d) { H.Fi(d.message) } return c };
	H.os = H.ad ? H.$g(H.ad + "#base") : null; H.debug = {}; H.debug.Error = function (b) { if (Error.captureStackTrace) Error.captureStackTrace(this, H.debug.Error); else { var c = Error().stack; c && (this.stack = c) } b && (this.message = String(b)) }; H.yb(H.debug.Error, Error); H.debug.Error.prototype.name = "CustomError"; H.a = {}; H.a.ua = { mb: 1, jp: 2, $c: 3, yp: 4, aq: 5, $p: 6, zr: 7, Fp: 8, Uc: 9, Sp: 10, Wj: 11, lr: 12 }; H.m = {}; H.m.za = H.sa; H.m.oc = function (b, c) { H.debug.Error.call(this, H.m.vo(b, c)) }; H.yb(H.m.oc, H.debug.Error); H.m.oc.prototype.name = "AssertionError"; H.m.Sj = function (b) { throw b; }; H.m.Ge = H.m.Sj; H.m.vo = function (b, c) { b = b.split("%s"); for (var d = "", e = b.length - 1, f = 0; f < e; f++)d += b[f] + (f < c.length ? c[f] : "%s"); return d + b[e] }; H.m.Ta = function (b, c, d, e) { var f = "Assertion failed"; if (d) { f += ": " + d; var g = e } else b && (f += ": " + b, g = c); b = new H.m.oc("" + f, g || []); H.m.Ge(b) }; H.m.Pw = function (b) { H.m.za && (H.m.Ge = b) };
	H.m.assert = function (b, c, d) { H.m.za && !b && H.m.Ta("", null, c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Ms = function (b, c, d) { H.m.za && null == b && H.m.Ta("Expected to exist: %s.", [b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.xa = function (b, c) { H.m.za && H.m.Ge(new H.m.oc("Failure" + (b ? ": " + b : ""), Array.prototype.slice.call(arguments, 1))) }; H.m.Us = function (b, c, d) { H.m.za && !H.Bb(b) && H.m.Ta("Expected number but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b };
	H.m.Xs = function (b, c, d) { H.m.za && !H.L(b) && H.m.Ta("Expected string but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Os = function (b, c, d) { H.m.za && !H.Wa(b) && H.m.Ta("Expected function but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Vs = function (b, c, d) { H.m.za && !H.Da(b) && H.m.Ta("Expected object but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b };
	H.m.Js = function (b, c, d) { H.m.za && !H.isArray(b) && H.m.Ta("Expected array but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Ks = function (b, c, d) { H.m.za && !H.Em(b) && H.m.Ta("Expected boolean but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Ls = function (b, c, d) { !H.m.za || H.Da(b) && b.nodeType == H.a.ua.mb || H.m.Ta("Expected Element but got %s: %s.", [H.ra(b), b], c, Array.prototype.slice.call(arguments, 2)); return b };
	H.m.Ps = function (b, c, d, e) { !H.m.za || b instanceof c || H.m.Ta("Expected instanceof %s but got %s.", [H.m.Th(c), H.m.Th(b)], d, Array.prototype.slice.call(arguments, 3)); return b }; H.m.Ns = function (b, c, d) { !H.m.za || typeof b == u && isFinite(b) || H.m.Ta("Expected %s to be a finite number but it is not.", [b], c, Array.prototype.slice.call(arguments, 2)); return b }; H.m.Ws = function () { for (var b in Object.prototype) H.m.xa(b + " should not be enumerable in Object.prototype.") };
	H.m.Th = function (b) { return b instanceof Function ? b.displayName || b.name || "unknown type name" : b instanceof Object ? b.constructor.displayName || b.constructor.name || Object.prototype.toString.call(b) : null === b ? "null" : typeof b }; H.g = {}; H.fb = H.pe; H.g.cb = !1; H.g.zn = function (b) { return b[b.length - 1] }; H.g.Lv = H.g.zn; H.g.indexOf = H.fb && (H.g.cb || Array.prototype.indexOf) ? function (b, c, d) { return Array.prototype.indexOf.call(b, c, d) } : function (b, c, d) { d = null == d ? 0 : 0 > d ? Math.max(0, b.length + d) : d; if (H.L(b)) return H.L(c) && 1 == c.length ? b.indexOf(c, d) : -1; for (; d < b.length; d++)if (d in b && b[d] === c) return d; return -1 };
	H.g.lastIndexOf = H.fb && (H.g.cb || Array.prototype.lastIndexOf) ? function (b, c, d) { return Array.prototype.lastIndexOf.call(b, c, null == d ? b.length - 1 : d) } : function (b, c, d) { d = null == d ? b.length - 1 : d; 0 > d && (d = Math.max(0, b.length + d)); if (H.L(b)) return H.L(c) && 1 == c.length ? b.lastIndexOf(c, d) : -1; for (; 0 <= d; d--)if (d in b && b[d] === c) return d; return -1 };
	H.g.forEach = H.fb && (H.g.cb || Array.prototype.forEach) ? function (b, c, d) { Array.prototype.forEach.call(b, c, d) } : function (b, c, d) { for (var e = b.length, f = H.L(b) ? b.split("") : b, g = 0; g < e; g++)g in f && c.call(d, f[g], g, b) }; H.g.ph = function (b, c) { var d = b.length, e = H.L(b) ? b.split("") : b; for (--d; 0 <= d; --d)d in e && c.call(void 0, e[d], d, b) };
	H.g.filter = H.fb && (H.g.cb || Array.prototype.filter) ? function (b, c, d) { return Array.prototype.filter.call(b, c, d) } : function (b, c, d) { for (var e = b.length, f = [], g = 0, h = H.L(b) ? b.split("") : b, k = 0; k < e; k++)if (k in h) { var m = h[k]; c.call(d, m, k, b) && (f[g++] = m) } return f }; H.g.map = H.fb && (H.g.cb || Array.prototype.map) ? function (b, c, d) { return Array.prototype.map.call(b, c, d) } : function (b, c, d) { for (var e = b.length, f = Array(e), g = H.L(b) ? b.split("") : b, h = 0; h < e; h++)h in g && (f[h] = c.call(d, g[h], h, b)); return f };
	H.g.reduce = H.fb && (H.g.cb || Array.prototype.reduce) ? function (b, c, d, e) { e && (c = H.bind(c, e)); return Array.prototype.reduce.call(b, c, d) } : function (b, c, d, e) { var f = d; H.g.forEach(b, function (g, h) { f = c.call(e, f, g, h, b) }); return f }; H.g.reduceRight = H.fb && (H.g.cb || Array.prototype.reduceRight) ? function (b, c, d, e) { e && (c = H.bind(c, e)); return Array.prototype.reduceRight.call(b, c, d) } : function (b, c, d, e) { var f = d; H.g.ph(b, function (g, h) { f = c.call(e, f, g, h, b) }); return f };
	H.g.some = H.fb && (H.g.cb || Array.prototype.some) ? function (b, c, d) { return Array.prototype.some.call(b, c, d) } : function (b, c, d) { for (var e = b.length, f = H.L(b) ? b.split("") : b, g = 0; g < e; g++)if (g in f && c.call(d, f[g], g, b)) return !0; return !1 }; H.g.every = H.fb && (H.g.cb || Array.prototype.every) ? function (b, c, d) { return Array.prototype.every.call(b, c, d) } : function (b, c, d) { for (var e = b.length, f = H.L(b) ? b.split("") : b, g = 0; g < e; g++)if (g in f && !c.call(d, f[g], g, b)) return !1; return !0 };
	H.g.count = function (b, c, d) { var e = 0; H.g.forEach(b, function (f, g, h) { c.call(d, f, g, h) && ++e }, d); return e }; H.g.find = function (b, c, d) { c = H.g.findIndex(b, c, d); return 0 > c ? null : H.L(b) ? b.charAt(c) : b[c] }; H.g.findIndex = function (b, c, d) { for (var e = b.length, f = H.L(b) ? b.split("") : b, g = 0; g < e; g++)if (g in f && c.call(d, f[g], g, b)) return g; return -1 }; H.g.au = function (b, c, d) { c = H.g.Tl(b, c, d); return 0 > c ? null : H.L(b) ? b.charAt(c) : b[c] };
	H.g.Tl = function (b, c, d) { var e = b.length, f = H.L(b) ? b.split("") : b; for (--e; 0 <= e; e--)if (e in f && c.call(d, f[e], e, b)) return e; return -1 }; H.g.contains = function (b, c) { return 0 <= H.g.indexOf(b, c) }; H.g.Ca = function (b) { return 0 == b.length }; H.g.clear = function (b) { if (!H.isArray(b)) for (var c = b.length - 1; 0 <= c; c--)delete b[c]; b.length = 0 }; H.g.Zu = function (b, c) { H.g.contains(b, c) || b.push(c) }; H.g.bi = function (b, c, d) { H.g.splice(b, d, 0, c) }; H.g.av = function (b, c, d) { H.Sb(H.g.splice, b, d, 0).apply(null, c) };
	H.g.insertBefore = function (b, c, d) { var e; 2 == arguments.length || 0 > (e = H.g.indexOf(b, d)) ? b.push(c) : H.g.bi(b, c, e) }; H.g.remove = function (b, c) { c = H.g.indexOf(b, c); var d; (d = 0 <= c) && H.g.kc(b, c); return d }; H.g.sw = function (b, c) { c = H.g.lastIndexOf(b, c); return 0 <= c ? (H.g.kc(b, c), !0) : !1 }; H.g.kc = function (b, c) { return 1 == Array.prototype.splice.call(b, c, 1).length }; H.g.rw = function (b, c, d) { c = H.g.findIndex(b, c, d); return 0 <= c ? (H.g.kc(b, c), !0) : !1 };
	H.g.pw = function (b, c, d) { var e = 0; H.g.ph(b, function (f, g) { c.call(d, f, g, b) && H.g.kc(b, g) && e++ }); return e }; H.g.concat = function (b) { return Array.prototype.concat.apply([], arguments) }; H.g.join = function (b) { return Array.prototype.concat.apply([], arguments) }; H.g.jb = function (b) { var c = b.length; if (0 < c) { for (var d = Array(c), e = 0; e < c; e++)d[e] = b[e]; return d } return [] }; H.g.clone = H.g.jb;
	H.g.extend = function (b, c) { for (var d = 1; d < arguments.length; d++) { var e = arguments[d]; if (H.ma(e)) { var f = b.length || 0, g = e.length || 0; b.length = f + g; for (var h = 0; h < g; h++)b[f + h] = e[h] } else b.push(e) } }; H.g.splice = function (b, c, d, e) { return Array.prototype.splice.apply(b, H.g.slice(arguments, 1)) }; H.g.slice = function (b, c, d) { return 2 >= arguments.length ? Array.prototype.slice.call(b, c) : Array.prototype.slice.call(b, c, d) };
	H.g.Jn = function (b, c) { c = c || b; for (var d = {}, e = 0, f = 0; f < b.length;) { var g = b[f++]; var h = g; h = H.Da(h) ? "o" + H.Uh(h) : (typeof h).charAt(0) + h; Object.prototype.hasOwnProperty.call(d, h) || (d[h] = !0, c[e++] = g) } c.length = e }; H.g.Kg = function (b, c, d) { return H.g.Lg(b, d || H.g.sb, !1, c) }; H.g.at = function (b, c, d) { return H.g.Lg(b, c, !0, void 0, d) }; H.g.Lg = function (b, c, d, e, f) { for (var g = 0, h = b.length, k; g < h;) { var m = g + h >> 1; var n = d ? c.call(f, b[m], m, b) : c(e, b[m]); 0 < n ? g = m + 1 : (h = m, k = !n) } return k ? g : ~g }; H.g.sort = function (b, c) { b.sort(c || H.g.sb) };
	H.g.mx = function (b, c) { for (var d = Array(b.length), e = 0; e < b.length; e++)d[e] = { index: e, value: b[e] }; var f = c || H.g.sb; H.g.sort(d, function (g, h) { return f(g.value, h.value) || g.index - h.index }); for (e = 0; e < b.length; e++)b[e] = d[e].value }; H.g.qo = function (b, c, d) { var e = d || H.g.sb; H.g.sort(b, function (f, g) { return e(c(f), c(g)) }) }; H.g.jx = function (b, c, d) { H.g.qo(b, function (e) { return e[c] }, d) }; H.g.wi = function (b) { for (var c = H.g.sb, d = 1; d < b.length; d++)if (0 < c(b[d - 1], b[d])) return !1; return !0 };
	H.g.Ob = function (b, c) { if (!H.ma(b) || !H.ma(c) || b.length != c.length) return !1; for (var d = b.length, e = H.g.eh, f = 0; f < d; f++)if (!e(b[f], c[f])) return !1; return !0 }; H.g.nt = function (b, c, d) { d = d || H.g.sb; for (var e = Math.min(b.length, c.length), f = 0; f < e; f++) { var g = d(b[f], c[f]); if (0 != g) return g } return H.g.sb(b.length, c.length) }; H.g.sb = function (b, c) { return b > c ? 1 : b < c ? -1 : 0 }; H.g.dv = function (b, c) { return -H.g.sb(b, c) }; H.g.eh = function (b, c) { return b === c }; H.g.Zs = function (b, c, d) { d = H.g.Kg(b, c, d); return 0 > d ? (H.g.bi(b, c, -(d + 1)), !0) : !1 };
	H.g.$s = function (b, c, d) { c = H.g.Kg(b, c, d); return 0 <= c ? H.g.kc(b, c) : !1 }; H.g.ct = function (b, c, d) { for (var e = {}, f = 0; f < b.length; f++) { var g = b[f], h = c.call(d, g, f, b); H.ca(h) && (e[h] || (e[h] = [])).push(g) } return e }; H.g.Ao = function (b, c, d) { var e = {}; H.g.forEach(b, function (f, g) { e[c.call(d, f, g, b)] = f }); return e }; H.g.Qd = function (b, c, d) { var e = [], f = 0, g = b; d = d || 1; void 0 !== c && (f = b, g = c); if (0 > d * (g - f)) return []; if (0 < d) for (b = f; b < g; b += d)e.push(b); else for (b = f; b > g; b += d)e.push(b); return e };
	H.g.repeat = function (b, c) { for (var d = [], e = 0; e < c; e++)d[e] = b; return d }; H.g.flatten = function (b) { for (var c = [], d = 0; d < arguments.length; d++) { var e = arguments[d]; if (H.isArray(e)) for (var f = 0; f < e.length; f += 8192) { var g = H.g.slice(e, f, f + 8192); g = H.g.flatten.apply(null, g); for (var h = 0; h < g.length; h++)c.push(g[h]) } else c.push(e) } return c }; H.g.rotate = function (b, c) { b.length && (c %= b.length, 0 < c ? Array.prototype.unshift.apply(b, b.splice(-c, c)) : 0 > c && Array.prototype.push.apply(b, b.splice(0, -c))); return b };
	H.g.Xv = function (b, c, d) { c = Array.prototype.splice.call(b, c, 1); Array.prototype.splice.call(b, d, 0, c[0]) }; H.g.uj = function (b) { if (!arguments.length) return []; for (var c = [], d = arguments[0].length, e = 1; e < arguments.length; e++)arguments[e].length < d && (d = arguments[e].length); for (e = 0; e < d; e++) { for (var f = [], g = 0; g < arguments.length; g++)f.push(arguments[g][e]); c.push(f) } return c }; H.g.ix = function (b, c) { c = c || Math.random; for (var d = b.length - 1; 0 < d; d--) { var e = Math.floor(c() * (d + 1)), f = b[d]; b[d] = b[e]; b[e] = f } };
	H.g.tt = function (b, c) { var d = []; H.g.forEach(c, function (e) { d.push(b[e]) }); return d }; H.g.qt = function (b, c, d) { return H.g.concat.apply([], H.g.map(b, c, d)) }; H.async = {}; H.async.Wc = function (b, c, d) { this.bn = d; this.Al = b; this.On = c; this.Fd = 0; this.zd = null }; H.async.Wc.prototype.get = function () { if (0 < this.Fd) { this.Fd--; var b = this.zd; this.zd = b.next; b.next = null } else b = this.Al(); return b }; H.async.Wc.prototype.put = function (b) { this.On(b); this.Fd < this.bn && (this.Fd++, b.next = this.zd, this.zd = b) }; H.debug.pa = {}; H.debug.bq = B(); H.debug.pa.jc = []; H.debug.pa.qf = []; H.debug.pa.Mi = !1; H.debug.pa.register = function (b) { H.debug.pa.jc[H.debug.pa.jc.length] = b; if (H.debug.pa.Mi) for (var c = H.debug.pa.qf, d = 0; d < c.length; d++)b(H.bind(c[d].So, c[d])) }; H.debug.pa.Wv = function (b) { H.debug.pa.Mi = !0; for (var c = H.bind(b.So, b), d = 0; d < H.debug.pa.jc.length; d++)H.debug.pa.jc[d](c); H.debug.pa.qf.push(b) }; H.debug.pa.Fx = function (b) { var c = H.debug.pa.qf; b = H.bind(b.K, b); for (var d = 0; d < H.debug.pa.jc.length; d++)H.debug.pa.jc[d](b); c.length-- }; H.a.m = {}; H.a.m.we = function (b) { if (H.m.za) { var c = H.a.m.ec(b); c && (!b || !(b instanceof c.Location) && b instanceof c.Element) && H.m.xa("Argument is not a Location (or a non-Element mock); got: %s", H.a.m.dh(b)) } }; H.a.m.Aa = function (b, c) { if (H.m.za) { var d = H.a.m.ec(b); d && "undefined" != typeof d[c] && (b && (b instanceof d[c] || !(b instanceof d.Location || b instanceof d.Element)) || H.m.xa("Argument is not a %s (or a non-Element, non-Location mock); got: %s", c, H.a.m.dh(b))) } return b }; H.a.m.Uk = function (b) { H.a.m.Aa(b, "HTMLAnchorElement") };
	H.a.m.Wk = function (b) { return H.a.m.Aa(b, "HTMLButtonElement") }; H.a.m.bl = function (b) { H.a.m.Aa(b, "HTMLLinkElement") }; H.a.m.$k = function (b) { H.a.m.Aa(b, "HTMLImageElement") }; H.a.m.Vk = function (b) { H.a.m.Aa(b, "HTMLAudioElement") }; H.a.m.dl = function (b) { H.a.m.Aa(b, "HTMLVideoElement") }; H.a.m.al = function (b) { return H.a.m.Aa(b, "HTMLInputElement") }; H.a.m.Rs = function (b) { return H.a.m.Aa(b, "HTMLTextAreaElement") }; H.a.m.Qs = function (b) { return H.a.m.Aa(b, "HTMLCanvasElement") }; H.a.m.Xk = function (b) { H.a.m.Aa(b, "HTMLEmbedElement") };
	H.a.m.Yk = function (b) { return H.a.m.Aa(b, "HTMLFormElement") }; H.a.m.Zk = function (b) { H.a.m.Aa(b, "HTMLFrameElement") }; H.a.m.Gg = function (b) { H.a.m.Aa(b, "HTMLIFrameElement") }; H.a.m.cl = function (b) { H.a.m.Aa(b, "HTMLObjectElement") }; H.a.m.Hg = function (b) { H.a.m.Aa(b, "HTMLScriptElement") }; H.a.m.dh = function (b) { if (H.Da(b)) try { return b.constructor.displayName || b.constructor.name || Object.prototype.toString.call(b) } catch (c) { return "<object could not be stringified>" } else return void 0 === b ? "undefined" : null === b ? "null" : typeof b };
	H.a.m.ec = function (b) { try { var c = b && b.ownerDocument, d = c && (c.defaultView || c.parentWindow); d = d || H.global; if (d.Element && d.Location) return d } catch (e) { } return null }; H.V = {}; H.V.Vg = function (b) { return function () { return b } }; H.V.cq = E(!1); H.V.ns = E(!0); H.V.mr = E(null); H.V.ai = A(); H.V.error = function (b) { return function () { throw Error(b); } }; H.V.xa = function (b) { return function () { throw b; } }; H.V.lock = function (b, c) { c = c || 0; return function () { return b.apply(this, Array.prototype.slice.call(arguments, 0, c)) } }; H.V.cw = function (b) { return function () { return arguments[b] } };
	H.V.iw = function (b, c) { var d = Array.prototype.slice.call(arguments, 1); return function () { var e = Array.prototype.slice.call(arguments); e.push.apply(e, d); return b.apply(this, e) } }; H.V.Jx = function (b, c) { return H.V.fo(b, H.V.Vg(c)) }; H.V.Ut = function (b, c) { return function (d) { return c ? b == d : b === d } }; H.V.ot = function (b, c) { var d = arguments, e = d.length; return function () { var f; e && (f = d[e - 1].apply(this, arguments)); for (var g = e - 2; 0 <= g; g--)f = d[g].call(this, f); return f } };
	H.V.fo = function (b) { var c = arguments, d = c.length; return function () { for (var e, f = 0; f < d; f++)e = c[f].apply(this, arguments); return e } }; H.V.and = function (b) { var c = arguments, d = c.length; return function () { for (var e = 0; e < d; e++)if (!c[e].apply(this, arguments)) return !1; return !0 } }; H.V.or = function (b) { var c = arguments, d = c.length; return function () { for (var e = 0; e < d; e++)if (c[e].apply(this, arguments)) return !0; return !1 } }; H.V.xn = function (b) { return function () { return !b.apply(this, arguments) } };
	H.V.create = function (b, c) { function d() { } d.prototype = b.prototype; var e = new d; b.apply(e, Array.prototype.slice.call(arguments, 1)); return e }; H.V.Jj = !0; H.V.ol = function (b) { var c = !1, d; return function () { if (!H.V.Jj) return b(); c || (d = b(), c = !0); return d } }; H.V.once = function (b) { var c = b; return function () { if (c) { var d = c; c = null; d() } } }; H.V.It = function (b, c, d) { var e = 0; return function (f) { H.global.clearTimeout(e); var g = arguments; e = H.global.setTimeout(function () { b.apply(d, g) }, c) } };
	H.V.vx = function (b, c, d) { function e() { g = H.global.setTimeout(f, c); b.apply(d, k) } function f() { g = 0; h && (h = !1, e()) } var g = 0, h = !1, k = []; return function (m) { k = arguments; g ? h = !0 : e() } }; H.V.mw = function (b, c, d) { function e() { f = 0 } var f = 0; return function (g) { f || (f = H.global.setTimeout(e, c), b.apply(d, arguments)) } }; H.a.zq = B(); H.a.f = function (b) { this.wo = b }; H.a.f.prototype.toString = D("wo"); H.a.f.Uo = new H.a.f("A"); H.a.f.Vo = new H.a.f("ABBR"); H.a.f.Xo = new H.a.f("ACRONYM"); H.a.f.Yo = new H.a.f("ADDRESS"); H.a.f.bp = new H.a.f("APPLET"); H.a.f.cp = new H.a.f("AREA"); H.a.f.ep = new H.a.f("ARTICLE"); H.a.f.fp = new H.a.f("ASIDE"); H.a.f.kp = new H.a.f("AUDIO"); H.a.f.lp = new H.a.f("B"); H.a.f.mp = new H.a.f("BASE"); H.a.f.np = new H.a.f("BASEFONT"); H.a.f.op = new H.a.f("BDI"); H.a.f.pp = new H.a.f("BDO"); H.a.f.sp = new H.a.f("BIG"); H.a.f.tp = new H.a.f("BLOCKQUOTE");
	H.a.f.up = new H.a.f("BODY"); H.a.f.Xf = new H.a.f("BR"); H.a.f.vp = new H.a.f("BUTTON"); H.a.f.wp = new H.a.f("CANVAS"); H.a.f.xp = new H.a.f("CAPTION"); H.a.f.zp = new H.a.f("CENTER"); H.a.f.Ap = new H.a.f("CITE"); H.a.f.Bp = new H.a.f("CODE"); H.a.f.Cp = new H.a.f("COL"); H.a.f.Dp = new H.a.f("COLGROUP"); H.a.f.Ep = new H.a.f("COMMAND"); H.a.f.Gp = new H.a.f("DATA"); H.a.f.Hp = new H.a.f("DATALIST"); H.a.f.Ip = new H.a.f("DD"); H.a.f.Jp = new H.a.f("DEL"); H.a.f.Lp = new H.a.f("DETAILS"); H.a.f.Mp = new H.a.f("DFN"); H.a.f.Np = new H.a.f("DIALOG");
	H.a.f.Op = new H.a.f("DIR"); H.a.f.Pp = new H.a.f("DIV"); H.a.f.Qp = new H.a.f("DL"); H.a.f.Tp = new H.a.f("DT"); H.a.f.Wp = new H.a.f("EM"); H.a.f.Xp = new H.a.f("EMBED"); H.a.f.fq = new H.a.f("FIELDSET"); H.a.f.gq = new H.a.f("FIGCAPTION"); H.a.f.hq = new H.a.f("FIGURE"); H.a.f.iq = new H.a.f("FONT"); H.a.f.jq = new H.a.f("FOOTER"); H.a.f.kq = new H.a.f("FORM"); H.a.f.lq = new H.a.f("FRAME"); H.a.f.mq = new H.a.f("FRAMESET"); H.a.f.oq = new H.a.f("H1"); H.a.f.pq = new H.a.f("H2"); H.a.f.qq = new H.a.f("H3"); H.a.f.rq = new H.a.f("H4"); H.a.f.sq = new H.a.f("H5");
	H.a.f.tq = new H.a.f("H6"); H.a.f.uq = new H.a.f("HEAD"); H.a.f.vq = new H.a.f("HEADER"); H.a.f.wq = new H.a.f("HGROUP"); H.a.f.xq = new H.a.f("HR"); H.a.f.yq = new H.a.f("HTML"); H.a.f.Aq = new H.a.f("I"); H.a.f.Dq = new H.a.f("IFRAME"); H.a.f.Eq = new H.a.f("IMG"); H.a.f.Fq = new H.a.f("INPUT"); H.a.f.Gq = new H.a.f("INS"); H.a.f.Lq = new H.a.f("ISINDEX"); H.a.f.Oq = new H.a.f("KBD"); H.a.f.Pq = new H.a.f("KEYGEN"); H.a.f.Qq = new H.a.f("LABEL"); H.a.f.Sq = new H.a.f("LEGEND"); H.a.f.Tq = new H.a.f("LI"); H.a.f.Uq = new H.a.f("LINK"); H.a.f.Yq = new H.a.f("MAIN");
	H.a.f.Zq = new H.a.f("MAP"); H.a.f.$q = new H.a.f("MARK"); H.a.f.ar = new H.a.f("MATH"); H.a.f.cr = new H.a.f("MENU"); H.a.f.dr = new H.a.f("MENUITEM"); H.a.f.er = new H.a.f("META"); H.a.f.fr = new H.a.f("METER"); H.a.f.ir = new H.a.f("NAV"); H.a.f.jr = new H.a.f("NOFRAMES"); H.a.f.kr = new H.a.f("NOSCRIPT"); H.a.f.nr = new H.a.f("OBJECT"); H.a.f.qr = new H.a.f("OL"); H.a.f.rr = new H.a.f("OPTGROUP"); H.a.f.sr = new H.a.f("OPTION"); H.a.f.tr = new H.a.f("OUTPUT"); H.a.f.ur = new H.a.f("P"); H.a.f.vr = new H.a.f("PARAM"); H.a.f.wr = new H.a.f("PICTURE");
	H.a.f.yr = new H.a.f("PRE"); H.a.f.Ar = new H.a.f("PROGRESS"); H.a.f.Q = new H.a.f("Q"); H.a.f.Br = new H.a.f("RP"); H.a.f.Cr = new H.a.f("RT"); H.a.f.Dr = new H.a.f("RTC"); H.a.f.Er = new H.a.f("RUBY"); H.a.f.Gr = new H.a.f("S"); H.a.f.Jr = new H.a.f("SAMP"); H.a.f.Kr = new H.a.f(l); H.a.f.Lr = new H.a.f("SECTION"); H.a.f.Mr = new H.a.f("SELECT"); H.a.f.Or = new H.a.f("SMALL"); H.a.f.Pr = new H.a.f("SOURCE"); H.a.f.Qr = new H.a.f("SPAN"); H.a.f.Rr = new H.a.f("STRIKE"); H.a.f.Sr = new H.a.f("STRONG"); H.a.f.Tr = new H.a.f("STYLE"); H.a.f.Ur = new H.a.f("SUB");
	H.a.f.Vr = new H.a.f("SUMMARY"); H.a.f.Wr = new H.a.f("SUP"); H.a.f.Xr = new H.a.f("SVG"); H.a.f.Yr = new H.a.f("TABLE"); H.a.f.Zr = new H.a.f("TBODY"); H.a.f.$r = new H.a.f("TD"); H.a.f.bs = new H.a.f("TEMPLATE"); H.a.f.cs = new H.a.f("TEXTAREA"); H.a.f.ds = new H.a.f("TFOOT"); H.a.f.es = new H.a.f("TH"); H.a.f.fs = new H.a.f("THEAD"); H.a.f.gs = new H.a.f("TIME"); H.a.f.hs = new H.a.f("TITLE"); H.a.f.js = new H.a.f("TR"); H.a.f.ks = new H.a.f("TRACK"); H.a.f.ps = new H.a.f("TT"); H.a.f.rs = new H.a.f("U"); H.a.f.ss = new H.a.f("UL"); H.a.f.ts = new H.a.f("VAR");
	H.a.f.us = new H.a.f("VIDEO"); H.a.f.vs = new H.a.f("WBR"); H.object = {}; H.object.is = function (b, c) { return b === c ? 0 !== b || 1 / b === 1 / c : b !== b && c !== c }; H.object.forEach = function (b, c, d) { for (var e in b) c.call(d, b[e], e, b) }; H.object.filter = function (b, c, d) { var e = {}, f; for (f in b) c.call(d, b[f], f, b) && (e[f] = b[f]); return e }; H.object.map = function (b, c, d) { var e = {}, f; for (f in b) e[f] = c.call(d, b[f], f, b); return e }; H.object.some = function (b, c, d) { for (var e in b) if (c.call(d, b[e], e, b)) return !0; return !1 }; H.object.every = function (b, c, d) { for (var e in b) if (!c.call(d, b[e], e, b)) return !1; return !0 };
	H.object.ub = function (b) { var c = 0, d; for (d in b) c++; return c }; H.object.tu = function (b) { for (var c in b) return c }; H.object.uu = function (b) { for (var c in b) return b[c] }; H.object.contains = function (b, c) { return H.object.Mb(b, c) }; H.object.ga = function (b) { var c = [], d = 0, e; for (e in b) c[d++] = b[e]; return c }; H.object.la = function (b) { var c = [], d = 0, e; for (e in b) c[d++] = e; return c }; H.object.Ou = function (b, c) { var d = H.ma(c), e = d ? c : arguments; for (d = d ? 0 : 1; d < e.length; d++) { if (null == b) return; b = b[e[d]] } return b };
	H.object.Lb = function (b, c) { return null !== b && c in b }; H.object.Mb = function (b, c) { for (var d in b) if (b[d] == c) return !0; return !1 }; H.object.Ul = function (b, c, d) { for (var e in b) if (c.call(d, b[e], e, b)) return e }; H.object.bu = function (b, c, d) { return (c = H.object.Ul(b, c, d)) && b[c] }; H.object.Ca = function (b) { for (var c in b) return !1; return !0 }; H.object.clear = function (b) { for (var c in b) delete b[c] }; H.object.remove = function (b, c) { var d; (d = c in b) && delete b[c]; return d };
	H.object.add = function (b, c, d) { if (null !== b && c in b) throw Error('The object already contains the key "' + c + '"'); H.object.set(b, c, d) }; H.object.get = function (b, c, d) { return null !== b && c in b ? b[c] : d }; H.object.set = function (b, c, d) { b[c] = d }; H.object.Tw = function (b, c, d) { return c in b ? b[c] : b[c] = d }; H.object.hx = function (b, c, d) { if (c in b) return b[c]; d = d(); return b[c] = d }; H.object.Ob = function (b, c) { for (var d in b) if (!(d in c) || b[d] !== c[d]) return !1; for (var e in c) if (!(e in b)) return !1; return !0 };
	H.object.clone = function (b) { var c = {}, d; for (d in b) c[d] = b[d]; return c }; H.object.Jo = function (b) { var c = H.ra(b); if (c == v || c == q) { if (H.Wa(b.clone)) return b.clone(); c = c == q ? [] : {}; for (var d in b) c[d] = H.object.Jo(b[d]); return c } return b }; H.object.Co = function (b) { var c = {}, d; for (d in b) c[b[d]] = d; return c }; H.object.qg = ["constructor", ba, "isPrototypeOf", ca, ea, "toString", "valueOf"];
	H.object.extend = function (b, c) { for (var d, e, f = 1; f < arguments.length; f++) { e = arguments[f]; for (d in e) b[d] = e[d]; for (var g = 0; g < H.object.qg.length; g++)d = H.object.qg[g], Object.prototype.hasOwnProperty.call(e, d) && (b[d] = e[d]) } }; H.object.create = function (b) { var c = arguments.length; if (1 == c && H.isArray(arguments[0])) return H.object.create.apply(null, arguments[0]); if (c % 2) throw Error(p); for (var d = {}, e = 0; e < c; e += 2)d[arguments[e]] = arguments[e + 1]; return d };
	H.object.yl = function (b) { var c = arguments.length; if (1 == c && H.isArray(arguments[0])) return H.object.yl.apply(null, arguments[0]); for (var d = {}, e = 0; e < c; e++)d[arguments[e]] = !0; return d }; H.object.zt = function (b) { var c = b; Object.isFrozen && !Object.isFrozen(b) && (c = Object.create(b), Object.freeze(c)); return c }; H.object.pv = function (b) { return !!Object.isFrozen && Object.isFrozen(b) };
	H.object.su = function (b, c, d) { if (!b) return []; if (!Object.getOwnPropertyNames || !Object.getPrototypeOf) return H.object.la(b); for (var e = {}; b && (b !== Object.prototype || c) && (b !== Function.prototype || d);) { for (var f = Object.getOwnPropertyNames(b), g = 0; g < f.length; g++)e[f[g]] = !0; b = Object.getPrototypeOf(b) } return H.object.la(e) }; H.object.Nu = function (b) { return (b = Object.getPrototypeOf(b.prototype)) && b.constructor }; H.a.tags = {}; H.a.tags.Nk = { area: !0, base: !0, br: !0, col: !0, command: !0, embed: !0, hr: !0, img: !0, input: !0, keygen: !0, link: !0, meta: !0, param: !0, source: !0, track: !0, wbr: !0 }; H.a.tags.Ym = function (b) { return !0 === H.a.tags.Nk[b] }; H.b = {}; H.b.Ya = {}; H.b.Ya.ab = H.ad ? H.$g(H.ad + "#html") : null; H.c = {}; H.c.qs = B(); H.c.M = function (b, c) { this.Ef = b === H.c.M.fg && c || ""; this.Dk = H.c.M.wg }; H.c.M.prototype.Va = !0; H.c.M.prototype.Ga = D("Ef"); H.c.M.prototype.toString = function () { return "Const{" + this.Ef + "}" }; H.c.M.K = function (b) { if (b instanceof H.c.M && b.constructor === H.c.M && b.Dk === H.c.M.wg) return b.Ef; H.m.xa("expected object of type Const, got '" + b + "'"); return "type_error:Const" }; H.c.M.from = function (b) { return new H.c.M(H.c.M.fg, b) }; H.c.M.wg = {}; H.c.M.fg = {}; H.c.M.EMPTY = H.c.M.from(""); H.b.W = function () { this.Jd = ""; this.tk = H.b.W.wa }; H.b.W.prototype.Va = !0; H.b.W.wa = {}; H.b.W.Fc = function (b) { b = H.c.M.K(b); return 0 === b.length ? H.b.W.EMPTY : H.b.W.uc(b) }; H.b.W.hu = function (b, c) { for (var d = [], e = 1; e < arguments.length; e++)d.push(H.b.W.ej(arguments[e])); return H.b.W.uc("(" + H.c.M.K(b) + ")(" + d.join(", ") + ");") }; H.b.W.ku = function (b) { return H.b.W.uc(H.b.W.ej(b)) }; H.b.W.prototype.Ga = function () { return this.Jd.toString() }; H.sa && (H.b.W.prototype.toString = function () { return "SafeScript{" + this.Jd + "}" });
	H.b.W.K = function (b) { return H.b.W.pj(b).toString() }; H.b.W.pj = function (b) { if (b instanceof H.b.W && b.constructor === H.b.W && b.tk === H.b.W.wa) return b.Jd; H.m.xa("expected object of type SafeScript, got '" + b + a + H.ra(b)); return "type_error:SafeScript" }; H.b.W.ej = function (b) { return JSON.stringify(b).replace(/</g, "\\x3c") }; H.b.W.uc = function (b) { return (new H.b.W).Qb(b) }; H.b.W.prototype.Qb = function (b) { this.Jd = H.b.Ya.ab ? H.b.Ya.ab.createScript(b) : b; return this }; H.b.W.EMPTY = H.b.W.uc(""); H.Ua = {}; H.Ua.url = {}; H.Ua.url.wl = function (b) { return H.Ua.url.Vh().createObjectURL(b) }; H.Ua.url.yw = function (b) { H.Ua.url.Vh().revokeObjectURL(b) }; H.Ua.url.Vh = function () { var b = H.Ua.url.nh(); if (null != b) return b; throw Error("This browser doesn't seem to support blob URLs"); }; H.Ua.url.nh = function () { return H.ca(H.global.URL) && H.ca(H.global.URL.createObjectURL) ? H.global.URL : H.ca(H.global.webkitURL) && H.ca(H.global.webkitURL.createObjectURL) ? H.global.webkitURL : H.ca(H.global.createObjectURL) ? H.global : null };
	H.Ua.url.bt = function () { return null != H.Ua.url.nh() }; H.i = {}; H.i.j = {}; H.i.j.ak = !1;
	H.i.j.jg = H.i.j.ak || ("ar" == H.S.substring(0, 2).toLowerCase() || "fa" == H.S.substring(0, 2).toLowerCase() || "he" == H.S.substring(0, 2).toLowerCase() || "iw" == H.S.substring(0, 2).toLowerCase() || "ps" == H.S.substring(0, 2).toLowerCase() || "sd" == H.S.substring(0, 2).toLowerCase() || "ug" == H.S.substring(0, 2).toLowerCase() || "ur" == H.S.substring(0, 2).toLowerCase() || "yi" == H.S.substring(0, 2).toLowerCase()) && (2 == H.S.length || "-" == H.S.substring(2, 3) || "_" == H.S.substring(2, 3)) || 3 <= H.S.length && "ckb" == H.S.substring(0, 3).toLowerCase() &&
		(3 == H.S.length || "-" == H.S.substring(3, 4) || "_" == H.S.substring(3, 4)) || 7 <= H.S.length && ("-" == H.S.substring(2, 3) || "_" == H.S.substring(2, 3)) && ("adlm" == H.S.substring(3, 7).toLowerCase() || "arab" == H.S.substring(3, 7).toLowerCase() || "hebr" == H.S.substring(3, 7).toLowerCase() || "nkoo" == H.S.substring(3, 7).toLowerCase() || "rohg" == H.S.substring(3, 7).toLowerCase() || "thaa" == H.S.substring(3, 7).toLowerCase()) || 8 <= H.S.length && ("-" == H.S.substring(3, 4) || "_" == H.S.substring(3, 4)) && ("adlm" == H.S.substring(4, 8).toLowerCase() || "arab" ==
			H.S.substring(4, 8).toLowerCase() || "hebr" == H.S.substring(4, 8).toLowerCase() || "nkoo" == H.S.substring(4, 8).toLowerCase() || "rohg" == H.S.substring(4, 8).toLowerCase() || "thaa" == H.S.substring(4, 8).toLowerCase()); H.i.j.Wb = { gk: "\u202a", pk: "\u202b", pg: "\u202c", hk: "\u200e", qk: "\u200f" }; H.i.j.aa = { Eb: 1, Fb: -1, Za: 0 }; H.i.j.Zc = "right"; H.i.j.Xc = "left"; H.i.j.Cq = H.i.j.jg ? H.i.j.Xc : H.i.j.Zc; H.i.j.Bq = H.i.j.jg ? H.i.j.Zc : H.i.j.Xc;
	H.i.j.zo = function (b) { return typeof b == u ? 0 < b ? H.i.j.aa.Eb : 0 > b ? H.i.j.aa.Fb : H.i.j.aa.Za : null == b ? null : b ? H.i.j.aa.Fb : H.i.j.aa.Eb }; H.i.j.gc = "A-Za-z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u02b8\u0300-\u0590\u0900-\u1fff\u200e\u2c00-\ud801\ud804-\ud839\ud83c-\udbff\uf900-\ufb1c\ufe00-\ufe6f\ufefd-\uffff"; H.i.j.lc = "\u0591-\u06ef\u06fa-\u08ff\u200f\ud802-\ud803\ud83a-\ud83b\ufb1d-\ufdff\ufe70-\ufefc"; H.i.j.Am = /<[^>]*>|&[^;]+;/g; H.i.j.Cb = function (b, c) { return c ? b.replace(H.i.j.Am, "") : b };
	H.i.j.Tn = new RegExp("[" + H.i.j.lc + "]"); H.i.j.kn = new RegExp("[" + H.i.j.gc + "]"); H.i.j.Ze = function (b, c) { return H.i.j.Tn.test(H.i.j.Cb(b, c)) }; H.i.j.Uu = H.i.j.Ze; H.i.j.Zh = function (b) { return H.i.j.kn.test(H.i.j.Cb(b, void 0)) }; H.i.j.nn = new RegExp("^[" + H.i.j.gc + "]"); H.i.j.Yn = new RegExp("^[" + H.i.j.lc + "]"); H.i.j.Tm = function (b) { return H.i.j.Yn.test(b) }; H.i.j.Pm = function (b) { return H.i.j.nn.test(b) }; H.i.j.xv = function (b) { return !H.i.j.Pm(b) && !H.i.j.Tm(b) }; H.i.j.ln = new RegExp("^[^" + H.i.j.lc + "]*[" + H.i.j.gc + "]");
	H.i.j.Vn = new RegExp("^[^" + H.i.j.gc + "]*[" + H.i.j.lc + "]"); H.i.j.cj = function (b, c) { return H.i.j.Vn.test(H.i.j.Cb(b, c)) }; H.i.j.Ev = H.i.j.cj; H.i.j.so = function (b, c) { return H.i.j.ln.test(H.i.j.Cb(b, c)) }; H.i.j.vv = H.i.j.so; H.i.j.ui = /^http:\/\/.*/; H.i.j.yv = function (b, c) { b = H.i.j.Cb(b, c); return H.i.j.ui.test(b) || !H.i.j.Zh(b) && !H.i.j.Ze(b) }; H.i.j.mn = new RegExp("[" + H.i.j.gc + "][^" + H.i.j.lc + "]*$"); H.i.j.Wn = new RegExp("[" + H.i.j.lc + "][^" + H.i.j.gc + "]*$"); H.i.j.Ll = function (b, c) { return H.i.j.mn.test(H.i.j.Cb(b, c)) };
	H.i.j.uv = H.i.j.Ll; H.i.j.Ml = function (b, c) { return H.i.j.Wn.test(H.i.j.Cb(b, c)) }; H.i.j.Cv = H.i.j.Ml; H.i.j.Xn = /^(ar|ckb|dv|he|iw|fa|nqo|ps|sd|ug|ur|yi|.*[-_](Adlm|Arab|Hebr|Nkoo|Rohg|Thaa))(?!.*[-_](Latn|Cyrl)($|-|_))($|-|_)/i; H.i.j.Dv = function (b) { return H.i.j.Xn.test(b) }; H.i.j.ll = /(\(.*?\)+)|(\[.*?\]+)|(\{.*?\}+)|(<.*?>+)/g; H.i.j.Su = function (b, c) { c = (void 0 === c ? H.i.j.Ze(b) : c) ? H.i.j.Wb.qk : H.i.j.Wb.hk; return b.replace(H.i.j.ll, c + "$&" + c) };
	H.i.j.Rt = function (b) { return "<" == b.charAt(0) ? b.replace(/<\w+/, "$& dir=rtl") : "\n<span dir=rtl>" + b + "</span>" }; H.i.j.St = function (b) { return H.i.j.Wb.pk + b + H.i.j.Wb.pg }; H.i.j.Pt = function (b) { return "<" == b.charAt(0) ? b.replace(/<\w+/, "$& dir=ltr") : "\n<span dir=ltr>" + b + "</span>" }; H.i.j.Qt = function (b) { return H.i.j.Wb.gk + b + H.i.j.Wb.pg }; H.i.j.Hl = /:\s*([.\d][.\w]*)\s+([.\d][.\w]*)\s+([.\d][.\w]*)\s+([.\d][.\w]*)/g; H.i.j.$m = /left/gi; H.i.j.Sn = /right/gi; H.i.j.xo = /%%%%/g;
	H.i.j.Uv = function (b) { return b.replace(H.i.j.Hl, ":$1 $4 $3 $2").replace(H.i.j.$m, "%%%%").replace(H.i.j.Sn, H.i.j.Xc).replace(H.i.j.xo, H.i.j.Zc) }; H.i.j.Jl = /([\u0591-\u05f2])"/g; H.i.j.po = /([\u0591-\u05f2])'/g; H.i.j.Zv = function (b) { return b.replace(H.i.j.Jl, "$1\u05f4").replace(H.i.j.po, "$1\u05f3") }; H.i.j.Qo = /\s+/; H.i.j.ym = /[\d\u06f0-\u06f9]/; H.i.j.Un = .4;
	H.i.j.hh = function (b, c) { var d = 0, e = 0, f = !1; b = H.i.j.Cb(b, c).split(H.i.j.Qo); for (c = 0; c < b.length; c++) { var g = b[c]; H.i.j.cj(g) ? (d++, e++) : H.i.j.ui.test(g) ? f = !0 : H.i.j.Zh(g) ? e++ : H.i.j.ym.test(g) && (f = !0) } return 0 == e ? f ? H.i.j.aa.Eb : H.i.j.aa.Za : d / e > H.i.j.Un ? H.i.j.aa.Fb : H.i.j.aa.Eb }; H.i.j.Kt = function (b, c) { return H.i.j.hh(b, c) == H.i.j.aa.Fb }; H.i.j.Mw = function (b, c) { b && (c = H.i.j.zo(c)) && (b.style.textAlign = c == H.i.j.aa.Fb ? H.i.j.Zc : H.i.j.Xc, b.dir = c == H.i.j.aa.Fb ? "rtl" : "ltr") };
	H.i.j.Nw = function (b, c) { switch (H.i.j.hh(c)) { case H.i.j.aa.Eb: b.dir = "ltr"; break; case H.i.j.aa.Fb: b.dir = "rtl"; break; default: b.removeAttribute("dir") } }; H.i.j.Up = B(); H.b.H = function () { this.Nd = ""; this.Hf = null; this.Fk = H.b.H.wa }; H.b.H.prototype.Va = !0; H.b.H.prototype.Ga = function () { return this.Nd.toString() }; H.b.H.prototype.af = !0; H.b.H.prototype.vb = function () { return H.i.j.aa.Eb }; H.sa && (H.b.H.prototype.toString = function () { return "TrustedResourceUrl{" + this.Nd + "}" }); H.b.H.K = function (b) { return H.b.H.Zd(b).toString() };
	H.b.H.Zd = function (b) { if (b instanceof H.b.H && b.constructor === H.b.H && b.Fk === H.b.H.wa) return b.Nd; H.m.xa("expected object of type TrustedResourceUrl, got '" + b + a + H.ra(b)); return "type_error:TrustedResourceUrl" }; H.b.H.na = function (b) { return b.Hf ? b.Hf : H.b.H.K(b) };
	H.b.H.format = function (b, c) { var d = H.c.M.K(b); if (!H.b.H.Hj.test(d)) throw Error("Invalid TrustedResourceUrl format: " + d); b = d.replace(H.b.H.bk, function (e, f) { if (!Object.prototype.hasOwnProperty.call(c, f)) throw Error('Found marker, "' + f + '", in format string, "' + d + '", but no valid label mapping found in args: ' + JSON.stringify(c)); e = c[f]; return e instanceof H.c.M ? H.c.M.K(e) : encodeURIComponent(String(e)) }); return H.b.H.xc(b) }; H.b.H.bk = /%{(\w+)}/g; H.b.H.Hj = /^((https:)?\/\/[0-9a-z.:[\]-]+\/|\/[^/\\]|[^:/\\%]+\/|[^:/\\%]*[?#]|about:blank#)/i;
	H.b.H.Ik = /^([^?#]*)(\?[^#]*)?(#[\s\S]*)?/; H.b.H.eu = function (b, c, d, e) { b = H.b.H.format(b, c); b = H.b.H.K(b); b = H.b.H.Ik.exec(b); c = b[3] || ""; return H.b.H.xc(b[1] + H.b.H.dj("?", b[2] || "", d) + H.b.H.dj("#", c, e)) }; H.b.H.Fc = function (b) { return H.b.H.xc(H.c.M.K(b)) }; H.b.H.iu = function (b) { for (var c = "", d = 0; d < b.length; d++)c += H.c.M.K(b[d]); return H.b.H.xc(c) }; H.b.H.wa = {}; H.b.H.xc = function (b) { var c = new H.b.H; c.Nd = H.b.Ya.ab ? H.b.Ya.ab.createScriptURL(b) : b; H.b.Ya.ab && (c.Hf = H.b.Ya.ab.createURL(b)); return c };
	H.b.H.dj = function (b, c, d) { if (null == d) return c; if (H.L(d)) return d ? b + encodeURIComponent(d) : ""; for (var e in d) { var f = d[e]; f = H.isArray(f) ? f : [f]; for (var g = 0; g < f.length; g++) { var h = f[g]; null != h && (c || (c = b), c += (c.length > b.length ? "&" : "") + encodeURIComponent(e) + "=" + encodeURIComponent(String(h))) } } return c }; H.c.A = {}; H.c.A.startsWith = function (b, c) { return 0 == b.lastIndexOf(c, 0) }; H.c.A.endsWith = function (b, c) { var d = b.length - c.length; return 0 <= d && b.indexOf(c, d) == d }; H.c.A.Ib = function (b, c) { return 0 == H.c.A.ed(c, b.substr(0, c.length)) }; H.c.A.Og = function (b, c) { return 0 == H.c.A.ed(c, b.substr(b.length - c.length, c.length)) }; H.c.A.Pg = function (b, c) { return b.toLowerCase() == c.toLowerCase() }; H.c.A.Lc = function (b) { return /^[\s\xa0]*$/.test(b) }; H.c.A.trim = H.pe && String.prototype.trim ? function (b) { return b.trim() } : function (b) { return /^[\s\xa0]*([\s\S]*?)[\s\xa0]*$/.exec(b)[1] };
	H.c.A.ed = function (b, c) { b = String(b).toLowerCase(); c = String(c).toLowerCase(); return b < c ? -1 : b == c ? 0 : 1 }; H.c.A.Nc = function (b, c) { return b.replace(/(\r\n|\r|\n)/g, c ? "<br />" : "<br>") };
	H.c.A.Ha = function (b, c) {
		if (c) b = b.replace(H.c.A.Lf, "&amp;").replace(H.c.A.kg, "&lt;").replace(H.c.A.gg, "&gt;").replace(H.c.A.rg, "&quot;").replace(H.c.A.tg, "&#39;").replace(H.c.A.mg, "&#0;"); else {
			if (!H.c.A.yj.test(b)) return b; -1 != b.indexOf("&") && (b = b.replace(H.c.A.Lf, "&amp;")); -1 != b.indexOf("<") && (b = b.replace(H.c.A.kg, "&lt;")); -1 != b.indexOf(">") && (b = b.replace(H.c.A.gg, "&gt;")); -1 != b.indexOf('"') && (b = b.replace(H.c.A.rg, "&quot;")); -1 != b.indexOf("'") && (b = b.replace(H.c.A.tg, "&#39;")); -1 != b.indexOf("\x00") &&
				(b = b.replace(H.c.A.mg, "&#0;"))
		} return b
	}; H.c.A.Lf = /&/g; H.c.A.kg = /</g; H.c.A.gg = />/g; H.c.A.rg = /"/g; H.c.A.tg = /'/g; H.c.A.mg = /\x00/g; H.c.A.yj = /[\x00&<>"']/; H.c.A.sj = function (b) { return H.c.A.Nc(b.replace(/  /g, " &#160;"), void 0) }; H.c.A.contains = function (b, c) { return -1 != b.indexOf(c) }; H.c.A.fd = function (b, c) { return H.c.A.contains(b.toLowerCase(), c.toLowerCase()) };
	H.c.A.Kb = function (b, c) { var d = 0; b = H.c.A.trim(String(b)).split("."); c = H.c.A.trim(String(c)).split("."); for (var e = Math.max(b.length, c.length), f = 0; 0 == d && f < e; f++) { var g = b[f] || "", h = c[f] || ""; do { g = /(\d*)(\D*)(.*)/.exec(g) || ["", "", "", ""]; h = /(\d*)(\D*)(.*)/.exec(h) || ["", "", "", ""]; if (0 == g[0].length && 0 == h[0].length) break; d = H.c.A.Ae(0 == g[1].length ? 0 : parseInt(g[1], 10), 0 == h[1].length ? 0 : parseInt(h[1], 10)) || H.c.A.Ae(0 == g[2].length, 0 == h[2].length) || H.c.A.Ae(g[2], h[2]); g = g[3]; h = h[3] } while (0 == d) } return d };
	H.c.A.Ae = function (b, c) { return b < c ? -1 : b > c ? 1 : 0 }; H.b.s = function () { this.Md = ""; this.wk = H.b.s.wa }; H.b.s.ta = "about:invalid#zClosurez"; H.b.s.prototype.Va = !0; H.b.s.prototype.Ga = function () { return this.Md.toString() }; H.b.s.prototype.af = !0; H.b.s.prototype.vb = function () { return H.i.j.aa.Eb }; H.sa && (H.b.s.prototype.toString = function () { return "SafeUrl{" + this.Md + "}" }); H.b.s.K = function (b) { return H.b.s.na(b).toString() };
	H.b.s.na = function (b) { if (b instanceof H.b.s && b.constructor === H.b.s && b.wk === H.b.s.wa) return b.Md; H.m.xa("expected object of type SafeUrl, got '" + b + a + H.ra(b)); return "type_error:SafeUrl" }; H.b.s.Fc = function (b) { return H.b.s.Ba(H.c.M.K(b)) }; H.b.ne = /^(?:audio\/(?:3gpp2|3gpp|aac|L16|midi|mp3|mp4|mpeg|oga|ogg|opus|x-m4a|x-wav|wav|webm)|image\/(?:bmp|gif|jpeg|jpg|png|tiff|webp|x-icon)|text\/csv|video\/(?:mpeg|mp4|ogg|webm|quicktime))(?:;\w+=(?:\w+|"[\w;=]+"))*$/i; H.b.s.Gv = function (b) { return H.b.ne.test(b) };
	H.b.s.gu = function (b) { b = H.b.ne.test(b.type) ? H.Ua.url.wl(b) : H.b.s.ta; return H.b.s.Ba(b) }; H.b.Rj = /^data:([^,]*);base64,[a-z0-9+\/]+=*$/i; H.b.s.Wl = function (b) { b = b.replace(/(%0A|%0D)/g, ""); var c = b.match(H.b.Rj); c = c && H.b.ne.test(c[1]); return H.b.s.Ba(c ? b : H.b.s.ta) }; H.b.s.pu = function (b) { H.c.A.Ib(b, "tel:") || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.Ak = /^sip[s]?:[+a-z0-9_.!$%&'*\/=^`{|}~-]+@([a-z0-9-]+\.)+[a-z0-9]{2,63}$/i; H.b.s.mu = function (b) { H.b.Ak.test(decodeURIComponent(b)) || (b = H.b.s.ta); return H.b.s.Ba(b) };
	H.b.s.ju = function (b) { H.c.A.Ib(b, "fb-messenger://share") || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.ru = function (b) { H.c.A.Ib(b, "whatsapp://send") || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.nu = function (b) { H.c.A.Ib(b, "sms:") && H.b.s.Um(b) || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.Um = function (b) { var c = b.indexOf("#"); 0 < c && (b = b.substring(0, c)); c = b.match(/[?&]body=/gi); if (!c) return !0; if (1 < c.length) return !1; b = b.match(/[?&]body=([^&]*)/)[1]; if (!b) return !0; try { decodeURIComponent(b) } catch (d) { return !1 } return /^(?:[a-z0-9\-_.~]|%[0-9a-f]{2})+$/i.test(b) };
	H.b.s.ou = function (b) { H.c.A.Ib(b, "ssh://") || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.Fw = function (b, c) { return H.b.s.uf(/^chrome-extension:\/\/([^\/]+)\//, b, c) }; H.b.s.Hw = function (b, c) { return H.b.s.uf(/^moz-extension:\/\/([^\/]+)\//, b, c) }; H.b.s.Gw = function (b, c) { return H.b.s.uf(/^ms-browser-extension:\/\/([^\/]+)\//, b, c) }; H.b.s.uf = function (b, c, d) { (b = b.exec(c)) ? (b = b[1], -1 == (d instanceof H.c.M ? [H.c.M.K(d)] : d.map(function (e) { return H.c.M.K(e) })).indexOf(b) && (c = H.b.s.ta)) : c = H.b.s.ta; return H.b.s.Ba(c) };
	H.b.s.qu = function (b) { return H.b.s.Ba(H.b.H.K(b)) }; H.b.oe = /^(?:(?:https?|mailto|ftp):|[^:/?#]*(?:[/?#]|$))/i; H.b.s.Ir = H.b.oe; H.b.s.Td = function (b) { if (b instanceof H.b.s) return b; b = typeof b == v && b.Va ? b.Ga() : String(b); H.b.oe.test(b) || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.Pa = function (b, c) { if (b instanceof H.b.s) return b; b = typeof b == v && b.Va ? b.Ga() : String(b); if (c && /^data:/i.test(b) && (c = H.b.s.Wl(b), c.Ga() == b)) return c; H.b.oe.test(b) || (b = H.b.s.ta); return H.b.s.Ba(b) }; H.b.s.wa = {};
	H.b.s.Ba = function (b) { var c = new H.b.s; c.Md = H.b.Ya.ab ? H.b.Ya.ab.createURL(b) : b; return c }; H.b.s.Wo = H.b.s.Ba("about:blank"); H.b.D = function () { this.Ld = ""; this.vk = H.b.D.wa }; H.b.D.prototype.Va = !0; H.b.D.wa = {}; H.b.D.Fc = function (b) { b = H.c.M.K(b); return 0 === b.length ? H.b.D.EMPTY : H.b.D.vc(b) }; H.b.D.prototype.Ga = D("Ld"); H.sa && (H.b.D.prototype.toString = function () { return "SafeStyle{" + this.Ld + "}" }); H.b.D.K = function (b) { if (b instanceof H.b.D && b.constructor === H.b.D && b.vk === H.b.D.wa) return b.Ld; H.m.xa("expected object of type SafeStyle, got '" + b + a + H.ra(b)); return "type_error:SafeStyle" }; H.b.D.vc = function (b) { return (new H.b.D).Qb(b) };
	H.b.D.prototype.Qb = function (b) { this.Ld = b; return this }; H.b.D.EMPTY = H.b.D.vc(""); H.b.D.ta = "zClosurez"; H.b.D.create = function (b) { var c = "", d; for (d in b) { if (!/^[-_a-zA-Z0-9]+$/.test(d)) throw Error("Name allows only [-_a-zA-Z0-9], got: " + d); var e = b[d]; null != e && (e = H.isArray(e) ? H.g.map(e, H.b.D.Vi).join(" ") : H.b.D.Vi(e), c += d + ":" + e + ";") } return c ? H.b.D.vc(c) : H.b.D.EMPTY };
	H.b.D.Vi = function (b) { if (b instanceof H.b.s) return 'url("' + H.b.s.K(b).replace(/</g, "%3c").replace(/[\\"]/g, "\\$&") + '")'; b = b instanceof H.c.M ? H.c.M.K(b) : H.b.D.bo(String(b)); if (/[{;}]/.test(b)) throw new H.m.oc("Value does not allow [{;}], got: %s.", [b]); return b };
	H.b.D.bo = function (b) {
		var c = b.replace(H.b.D.eg, "$1").replace(H.b.D.eg, "$1").replace(H.b.D.xg, "url"); if (H.b.D.Kk.test(c)) { if (H.b.D.Pj.test(b)) return H.m.xa("String value disallows comments, got: " + b), H.b.D.ta; if (!H.b.D.um(b)) return H.m.xa("String value requires balanced quotes, got: " + b), H.b.D.ta; if (!H.b.D.vm(b)) return H.m.xa("String value requires balanced square brackets and one identifier per pair of brackets, got: " + b), H.b.D.ta } else return H.m.xa("String value allows only " + H.b.D.Ag + " and simple functions, got: " +
			b), H.b.D.ta; return H.b.D.co(b)
	}; H.b.D.um = function (b) { for (var c = !0, d = !0, e = 0; e < b.length; e++) { var f = b.charAt(e); "'" == f && d ? c = !c : '"' == f && c && (d = !d) } return c && d }; H.b.D.vm = function (b) { for (var c = !0, d = /^[-_a-zA-Z0-9]$/, e = 0; e < b.length; e++) { var f = b.charAt(e); if ("]" == f) { if (c) return !1; c = !0 } else if ("[" == f) { if (!c) return !1; c = !1 } else if (!c && !d.test(f)) return !1 } return c }; H.b.D.Ag = "[-,.\"'%_!# a-zA-Z0-9\\[\\]]"; H.b.D.Kk = new RegExp("^" + H.b.D.Ag + "+$"); H.b.D.xg = /\b(url\([ \t\n]*)('[ -&(-\[\]-~]*'|"[ !#-\[\]-~]*"|[!#-&*-\[\]-~]*)([ \t\n]*\))/g;
	H.b.D.eg = /\b(hsl|hsla|rgb|rgba|matrix|calc|minmax|fit-content|repeat|(rotate|scale|translate)(X|Y|Z|3d)?)\([-+*/0-9a-z.%\[\], ]+\)/g; H.b.D.Pj = /\/\*/; H.b.D.co = function (b) { return b.replace(H.b.D.xg, function (c, d, e, f) { var g = ""; e = e.replace(/^(['"])(.*)\1$/, function (h, k, m) { g = k; return m }); c = H.b.s.Td(e).Ga(); return d + g + c + g + f }) }; H.b.D.concat = function (b) { function c(e) { H.isArray(e) ? H.g.forEach(e, c) : d += H.b.D.K(e) } var d = ""; H.g.forEach(arguments, c); return d ? H.b.D.vc(d) : H.b.D.EMPTY }; H.b.Y = function () { this.Kd = ""; this.uk = H.b.Y.wa }; H.b.Y.prototype.Va = !0; H.b.Y.wa = {};
	H.b.Y.Bt = function (b, c) { if (H.c.A.contains(b, "<")) throw Error("Selector does not allow '<', got: " + b); var d = b.replace(/('|")((?!\1)[^\r\n\f\\]|\\[\s\S])*\1/g, ""); if (!/^[-_a-zA-Z0-9#.:* ,>+~[\]()=^$|]+$/.test(d)) throw Error("Selector allows only [-_a-zA-Z0-9#.:* ,>+~[\\]()=^$|] and strings, got: " + b); if (!H.b.Y.tm(d)) throw Error("() and [] in selector must be balanced, got: " + b); c instanceof H.b.D || (c = H.b.D.create(c)); b = b + "{" + H.b.D.K(c).replace(/</g, "\\3C ") + "}"; return H.b.Y.wc(b) };
	H.b.Y.tm = function (b) { for (var c = { "(": ")", "[": "]" }, d = [], e = 0; e < b.length; e++) { var f = b[e]; if (c[f]) d.push(c[f]); else if (H.object.contains(c, f) && d.pop() != f) return !1 } return 0 == d.length }; H.b.Y.concat = function (b) { function c(e) { H.isArray(e) ? H.g.forEach(e, c) : d += H.b.Y.K(e) } var d = ""; H.g.forEach(arguments, c); return H.b.Y.wc(d) }; H.b.Y.Fc = function (b) { b = H.c.M.K(b); return 0 === b.length ? H.b.Y.EMPTY : H.b.Y.wc(b) }; H.b.Y.prototype.Ga = D("Kd"); H.sa && (H.b.Y.prototype.toString = function () { return "SafeStyleSheet{" + this.Kd + "}" });
	H.b.Y.K = function (b) { if (b instanceof H.b.Y && b.constructor === H.b.Y && b.uk === H.b.Y.wa) return b.Kd; H.m.xa("expected object of type SafeStyleSheet, got '" + b + a + H.ra(b)); return "type_error:SafeStyleSheet" }; H.b.Y.wc = function (b) { return (new H.b.Y).Qb(b) }; H.b.Y.prototype.Qb = function (b) { this.Kd = b; return this }; H.b.Y.EMPTY = H.b.Y.wc(""); H.h = {}; H.h.userAgent = {}; H.h.userAgent.F = {}; H.h.userAgent.F.Fh = function () { var b = H.h.userAgent.F.hm(); return b && (b = b.userAgent) ? b : "" }; H.h.userAgent.F.hm = function () { return H.global.navigator }; H.h.userAgent.F.qj = H.h.userAgent.F.Fh(); H.h.userAgent.F.fx = function (b) { H.h.userAgent.F.qj = b || H.h.userAgent.F.Fh() }; H.h.userAgent.F.cc = function () { return H.h.userAgent.F.qj }; H.h.userAgent.F.T = function (b) { return H.c.A.contains(H.h.userAgent.F.cc(), b) };
	H.h.userAgent.F.nf = function (b) { return H.c.A.fd(H.h.userAgent.F.cc(), b) }; H.h.userAgent.F.jh = function (b) { for (var c = /(\w[\w ]+)\/([^\s]+)\s*(?:\((.*?)\))?/g, d = [], e; e = c.exec(b);)d.push([e[1], e[2], e[3] || void 0]); return d }; H.h.userAgent.B = {}; H.h.userAgent.B.Ii = function () { return H.h.userAgent.F.T("Opera") }; H.h.userAgent.B.sn = function () { return H.h.userAgent.F.T("Trident") || H.h.userAgent.F.T("MSIE") }; H.h.userAgent.B.lf = function () { return H.h.userAgent.F.T("Edge") }; H.h.userAgent.B.Gi = function () { return H.h.userAgent.F.T("Edg/") }; H.h.userAgent.B.Hi = function () { return H.h.userAgent.F.T("OPR") }; H.h.userAgent.B.mf = function () { return H.h.userAgent.F.T("Firefox") || H.h.userAgent.F.T("FxiOS") };
	H.h.userAgent.B.Ji = function () { return H.h.userAgent.F.T("Safari") && !(H.h.userAgent.B.jf() || H.h.userAgent.B.kf() || H.h.userAgent.B.Ii() || H.h.userAgent.B.lf() || H.h.userAgent.B.Gi() || H.h.userAgent.B.Hi() || H.h.userAgent.B.mf() || H.h.userAgent.B.vi() || H.h.userAgent.F.T("Android")) }; H.h.userAgent.B.kf = function () { return H.h.userAgent.F.T("Coast") };
	H.h.userAgent.B.tn = function () { return (H.h.userAgent.F.T("iPad") || H.h.userAgent.F.T("iPhone")) && !H.h.userAgent.B.Ji() && !H.h.userAgent.B.jf() && !H.h.userAgent.B.kf() && !H.h.userAgent.B.mf() && H.h.userAgent.F.T("AppleWebKit") }; H.h.userAgent.B.jf = function () { return (H.h.userAgent.F.T("Chrome") || H.h.userAgent.F.T("CriOS")) && !H.h.userAgent.B.lf() }; H.h.userAgent.B.rn = function () { return H.h.userAgent.F.T("Android") && !(H.h.userAgent.B.hi() || H.h.userAgent.B.Im() || H.h.userAgent.B.ff() || H.h.userAgent.B.vi()) };
	H.h.userAgent.B.ff = H.h.userAgent.B.Ii; H.h.userAgent.B.Bd = H.h.userAgent.B.sn; H.h.userAgent.B.Ab = H.h.userAgent.B.lf; H.h.userAgent.B.Gm = H.h.userAgent.B.Gi; H.h.userAgent.B.Bv = H.h.userAgent.B.Hi; H.h.userAgent.B.Im = H.h.userAgent.B.mf; H.h.userAgent.B.Fv = H.h.userAgent.B.Ji; H.h.userAgent.B.jv = H.h.userAgent.B.kf; H.h.userAgent.B.sv = H.h.userAgent.B.tn; H.h.userAgent.B.hi = H.h.userAgent.B.jf; H.h.userAgent.B.gv = H.h.userAgent.B.rn; H.h.userAgent.B.vi = function () { return H.h.userAgent.F.T("Silk") };
	H.h.userAgent.B.Hc = function () { function b(f) { f = H.g.find(f, e); return d[f] || "" } var c = H.h.userAgent.F.cc(); if (H.h.userAgent.B.Bd()) return H.h.userAgent.B.gm(c); c = H.h.userAgent.F.jh(c); var d = {}; H.g.forEach(c, function (f) { d[f[0]] = f[1] }); var e = H.Sb(H.object.Lb, d); return H.h.userAgent.B.ff() ? b(["Version", "Opera"]) : H.h.userAgent.B.Ab() ? b(["Edge"]) : H.h.userAgent.B.Gm() ? b(["Edg"]) : H.h.userAgent.B.hi() ? b(["Chrome", "CriOS"]) : (c = c[2]) && c[1] || "" };
	H.h.userAgent.B.Xa = function (b) { return 0 <= H.c.A.Kb(H.h.userAgent.B.Hc(), b) }; H.h.userAgent.B.gm = function (b) { var c = /rv: *([\d\.]*)/.exec(b); if (c && c[1]) return c[1]; c = ""; var d = /MSIE +([\d\.]+)/.exec(b); if (d && d[1]) if (b = /Trident\/(\d.\d)/.exec(b), "7.0" == d[1]) if (b && b[1]) switch (b[1]) { case "4.0": c = "8.0"; break; case "5.0": c = "9.0"; break; case "6.0": c = "10.0"; break; case "7.0": c = "11.0" } else c = "7.0"; else c = d[1]; return c }; H.b.u = function () { this.Id = ""; this.sk = H.b.u.wa; this.md = null }; H.b.u.prototype.af = !0; H.b.u.prototype.vb = D("md"); H.b.u.prototype.Va = !0; H.b.u.prototype.Ga = function () { return this.Id.toString() }; H.sa && (H.b.u.prototype.toString = function () { return "SafeHtml{" + this.Id + "}" }); H.b.u.K = function (b) { return H.b.u.Db(b).toString() }; H.b.u.Db = function (b) { if (b instanceof H.b.u && b.constructor === H.b.u && b.sk === H.b.u.wa) return b.Id; H.m.xa("expected object of type SafeHtml, got '" + b + a + H.ra(b)); return "type_error:SafeHtml" };
	H.b.u.Ha = function (b) { if (b instanceof H.b.u) return b; var c = typeof b == v, d = null; c && b.af && (d = b.vb()); return H.b.u.Sa(H.c.A.Ha(c && b.Va ? b.Ga() : String(b)), d) }; H.b.u.Xu = function (b) { if (b instanceof H.b.u) return b; b = H.b.u.Ha(b); return H.b.u.Sa(H.c.A.Nc(H.b.u.K(b)), b.vb()) }; H.b.u.Yu = function (b) { if (b instanceof H.b.u) return b; b = H.b.u.Ha(b); return H.b.u.Sa(H.c.A.sj(H.b.u.K(b)), b.vb()) }; H.b.u.from = H.b.u.Ha; H.b.u.zg = /^[a-zA-Z0-9-]+$/;
	H.b.u.Hk = { action: !0, cite: !0, data: !0, formaction: !0, href: !0, manifest: !0, poster: !0, src: !0 }; H.b.u.lk = { APPLET: !0, BASE: !0, EMBED: !0, IFRAME: !0, LINK: !0, MATH: !0, META: !0, OBJECT: !0, SCRIPT: !0, STYLE: !0, SVG: !0, TEMPLATE: !0 }; H.b.u.create = function (b, c, d) { H.b.u.Po(String(b)); return H.b.u.Nb(String(b), c, d) }; H.b.u.Po = function (b) { if (!H.b.u.zg.test(b)) throw Error("Invalid tag name <" + b + ">."); if (b.toUpperCase() in H.b.u.lk) throw Error("Tag name <" + b + "> is not allowed for SafeHtml."); };
	H.b.u.xt = function (b, c, d, e) { b && H.b.H.K(b); var f = {}; f.src = b || null; f.srcdoc = c && H.b.u.K(c); b = H.b.u.hd(f, { sandbox: "" }, d); return H.b.u.Nb("iframe", b, e) }; H.b.u.Ct = function (b, c, d, e) { if (!H.b.u.pl()) throw Error("The browser does not support sandboxed iframes."); var f = {}; f.src = b ? H.b.s.K(H.b.s.Td(b)) : null; f.srcdoc = c || null; f.sandbox = ""; b = H.b.u.hd(f, {}, d); return H.b.u.Nb("iframe", b, e) }; H.b.u.pl = function () { return H.global.HTMLIFrameElement && "sandbox" in H.global.HTMLIFrameElement.prototype };
	H.b.u.Dt = function (b, c) { H.b.H.K(b); b = H.b.u.hd({ src: b }, {}, c); return H.b.u.Nb("script", b) }; H.b.u.createScript = function (b, c) { for (var d in c) { var e = d.toLowerCase(); if ("language" == e || "src" == e || "text" == e || "type" == e) throw Error('Cannot set "' + e + '" attribute'); } d = ""; b = H.g.concat(b); for (e = 0; e < b.length; e++)d += H.b.W.K(b[e]); b = H.b.u.Sa(d, H.i.j.aa.Za); return H.b.u.Nb("script", c, b) };
	H.b.u.Et = function (b, c) { c = H.b.u.hd({ type: "text/css" }, {}, c); var d = ""; b = H.g.concat(b); for (var e = 0; e < b.length; e++)d += H.b.Y.K(b[e]); b = H.b.u.Sa(d, H.i.j.aa.Za); return H.b.u.Nb("style", c, b) }; H.b.u.At = function (b, c) { b = H.b.s.K(H.b.s.Td(b)); (H.h.userAgent.B.Bd() || H.h.userAgent.B.Ab()) && H.c.A.contains(b, ";") && (b = "'" + b.replace(/'/g, "%27") + "'"); return H.b.u.Nb("meta", { "http-equiv": "refresh", content: (c || 0) + "; url=" + b }) };
	H.b.u.Yl = function (b, c, d) {
		if (d instanceof H.c.M) d = H.c.M.K(d); else if ("style" == c.toLowerCase()) d = H.b.u.nm(d); else { if (/^on/i.test(c)) throw Error('Attribute "' + c + '" requires goog.string.Const value, "' + d + '" given.'); if (c.toLowerCase() in H.b.u.Hk) if (d instanceof H.b.H) d = H.b.H.K(d); else if (d instanceof H.b.s) d = H.b.s.K(d); else if (H.L(d)) d = H.b.s.Td(d).Ga(); else throw Error('Attribute "' + c + '" on tag "' + b + '" requires goog.html.SafeUrl, goog.string.Const, or string, value "' + d + '" given.'); } d.Va && (d = d.Ga());
		return c + '="' + H.c.A.Ha(String(d)) + '"'
	}; H.b.u.nm = function (b) { if (!H.Da(b)) throw Error('The "style" attribute requires goog.html.SafeStyle or map of style properties, ' + typeof b + " given: " + b); b instanceof H.b.D || (b = H.b.D.create(b)); return H.b.D.K(b) }; H.b.u.Gt = function (b, c, d, e) { c = H.b.u.create(c, d, e); c.md = b; return c };
	H.b.u.join = function (b, c) { function d(g) { H.isArray(g) ? H.g.forEach(g, d) : (g = H.b.u.Ha(g), f.push(H.b.u.K(g)), g = g.vb(), e == H.i.j.aa.Za ? e = g : g != H.i.j.aa.Za && e != g && (e = null)) } b = H.b.u.Ha(b); var e = b.vb(), f = []; H.g.forEach(c, d); return H.b.u.Sa(f.join(H.b.u.K(b)), e) }; H.b.u.concat = function (b) { return H.b.u.join(H.b.u.EMPTY, Array.prototype.slice.call(arguments)) }; H.b.u.rt = function (b, c) { var d = H.b.u.concat(H.g.slice(arguments, 1)); d.md = b; return d }; H.b.u.wa = {}; H.b.u.Sa = function (b, c) { return (new H.b.u).Qb(b, c) };
	H.b.u.prototype.Qb = function (b, c) { this.Id = H.b.Ya.ab ? H.b.Ya.ab.createHTML(b) : b; this.md = c; return this }; H.b.u.Nb = function (b, c, d) { var e = null; var f = "<" + b + H.b.u.uo(b, c); H.zb(d) ? H.isArray(d) || (d = [d]) : d = []; H.a.tags.Ym(b.toLowerCase()) ? f += ">" : (e = H.b.u.concat(d), f += ">" + H.b.u.K(e) + "</" + b + ">", e = e.vb()); (b = c && c.dir) && (e = /^(ltr|rtl|auto)$/i.test(b) ? H.i.j.aa.Za : null); return H.b.u.Sa(f, e) };
	H.b.u.uo = function (b, c) { var d = ""; if (c) for (var e in c) { if (!H.b.u.zg.test(e)) throw Error('Invalid attribute name "' + e + '".'); var f = c[e]; H.zb(f) && (d += " " + H.b.u.Yl(b, e, f)) } return d }; H.b.u.hd = function (b, c, d) { var e = {}, f; for (f in b) e[f] = b[f]; for (f in c) e[f] = c[f]; for (f in d) { var g = f.toLowerCase(); if (g in b) throw Error('Cannot override "' + g + '" attribute, got "' + f + '" with value "' + d[f] + '"'); g in c && delete e[g]; e[f] = d[f] } return e }; H.b.u.Rp = H.b.u.Sa("<!DOCTYPE html>", H.i.j.aa.Za); H.b.u.EMPTY = H.b.u.Sa("", H.i.j.aa.Za);
	H.b.u.Xf = H.b.u.Sa("<br>", H.i.j.aa.Za); H.b.kb = {}; H.b.kb.Si = function (b, c) { return H.b.u.Sa(c, null) }; H.b.kb.Cw = function (b, c) { return H.b.W.uc(c) }; H.b.kb.Dw = function (b, c) { return H.b.D.vc(c) }; H.b.kb.Ew = function (b, c) { return H.b.Y.wc(c) }; H.b.kb.$n = function (b, c) { return H.b.s.Ba(c) }; H.b.kb.Cx = function (b, c) { return H.b.H.xc(c) }; H.a.O = {}; H.a.O.Mq = { Zo: "afterbegin", $o: "afterend", qp: "beforebegin", rp: "beforeend" }; H.a.O.$u = function (b, c, d) { b.insertAdjacentHTML(c, H.b.u.Db(d)) }; H.a.O.zk = { MATH: !0, SCRIPT: !0, STYLE: !0, SVG: !0, TEMPLATE: !0 }; H.a.O.Mm = H.V.ol(function () { if (H.sa && "undefined" === typeof document) return !1; var b = document.createElement("div"), c = document.createElement("div"); c.appendChild(document.createElement("div")); b.appendChild(c); if (H.sa && !b.firstChild) return !1; c = b.firstChild.firstChild; b.innerHTML = H.b.u.Db(H.b.u.EMPTY); return !c.parentElement });
	H.a.O.Ko = function (b, c) { if (H.a.O.Mm()) for (; b.lastChild;)b.removeChild(b.lastChild); b.innerHTML = H.b.u.Db(c) }; H.a.O.xf = function (b, c) { if (H.m.za && H.a.O.zk[b.tagName.toUpperCase()]) throw Error("goog.dom.safe.setInnerHtml cannot be used to set content of " + b.tagName + "."); H.a.O.Ko(b, c) }; H.a.O.Zw = function (b, c) { b.outerHTML = H.b.u.Db(c) }; H.a.O.Qw = function (b, c) { c = c instanceof H.b.s ? c : H.b.s.Pa(c); H.a.m.Yk(b).action = H.b.s.na(c) }; H.a.O.Kw = function (b, c) { c = c instanceof H.b.s ? c : H.b.s.Pa(c); H.a.m.Wk(b).formAction = H.b.s.na(c) };
	H.a.O.Vw = function (b, c) { c = c instanceof H.b.s ? c : H.b.s.Pa(c); H.a.m.al(b).formAction = H.b.s.na(c) }; H.a.O.cx = function (b, c) { b.style.cssText = H.b.D.K(c) }; H.a.O.Il = function (b) { b.write(H.b.u.Db(H.b.u.EMPTY)) }; H.a.O.Iw = function (b, c) { H.a.m.Uk(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c); b.href = H.b.s.na(c) }; H.a.O.io = function (b, c) { H.a.m.$k(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c, /^data:image\//i.test(c)); b.src = H.b.s.na(c) };
	H.a.O.Jw = function (b, c) { H.a.m.Vk(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c, /^data:audio\//i.test(c)); b.src = H.b.s.na(c) }; H.a.O.gx = function (b, c) { H.a.m.dl(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c, /^data:video\//i.test(c)); b.src = H.b.s.na(c) }; H.a.O.Ow = function (b, c) { H.a.m.Xk(b); b.src = H.b.H.Zd(c) }; H.a.O.Sw = function (b, c) { H.a.m.Zk(b); b.src = H.b.H.na(c) }; H.a.O.ho = function (b) { var c = H.b.H.Fc(H.c.M.EMPTY); H.a.m.Gg(b); b.src = H.b.H.na(c) }; H.a.O.Uw = function (b, c) { H.a.m.Gg(b); b.srcdoc = H.b.u.Db(c) };
	H.a.O.Ww = function (b, c, d) { H.a.m.bl(b); b.rel = d; H.c.A.fd(d, "stylesheet") ? b.href = H.b.H.na(c) : b.href = c instanceof H.b.H ? H.b.H.na(c) : c instanceof H.b.s ? H.b.s.na(c) : H.b.s.na(H.b.s.Pa(c)) }; H.a.O.Yw = function (b, c) { H.a.m.cl(b); b.data = H.b.H.Zd(c) }; H.a.O.mo = function (b, c) { H.a.m.Hg(b); b.src = H.b.H.Zd(c); (c = H.Sh()) && b.setAttribute("nonce", c) }; H.a.O.bx = function (b, c) { H.a.m.Hg(b); b.text = H.b.W.pj(c); (c = H.Sh()) && b.setAttribute("nonce", c) }; H.a.O.Xw = function (b, c) { H.a.m.we(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c); b.href = H.b.s.na(c) };
	H.a.O.Ys = function (b, c) { H.a.m.we(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c); b.assign(H.b.s.na(c)) }; H.a.O.uw = function (b, c) { H.a.m.we(b); c = c instanceof H.b.s ? c : H.b.s.Pa(c); b.replace(H.b.s.na(c)) }; H.a.O.fw = function (b, c, d, e, f) { b = b instanceof H.b.s ? b : H.b.s.Pa(b); return (c || H.global).open(H.b.s.na(b), d ? H.c.M.K(d) : "", e, f) }; H.a.O.hw = function (b, c) { return H.a.O.parseFromString(b, c, "text/html") }; H.a.O.parseFromString = function (b, c, d) { return b.parseFromString(H.b.u.Db(c), d) };
	H.a.O.yt = function (b) { if (!/^image\/.*/g.test(b.type)) throw Error("goog.dom.safe.createImageFromBlob only accepts MIME type image/.*."); var c = H.global.URL.createObjectURL(b); b = new H.global.Image; b.onload = function () { H.global.URL.revokeObjectURL(c) }; H.a.O.io(b, H.b.kb.$n(H.c.M.from("Image blob URL."), c)); return b }; H.c.Uj = !1; H.c.Zj = !1; H.c.yg = { lg: "\u00a0" }; H.c.startsWith = H.c.A.startsWith; H.c.endsWith = H.c.A.endsWith; H.c.Ib = H.c.A.Ib; H.c.Og = H.c.A.Og; H.c.Pg = H.c.A.Pg; H.c.sx = function (b, c) { for (var d = b.split("%s"), e = "", f = Array.prototype.slice.call(arguments, 1); f.length && 1 < d.length;)e += d.shift() + f.shift(); return e + d.join("%s") }; H.c.kt = function (b) { return b.replace(/[\s\xa0]+/g, " ").replace(/^\s+|\s+$/g, "") }; H.c.Lc = H.c.A.Lc; H.c.nv = function (b) { return 0 == b.length }; H.c.Ca = H.c.Lc; H.c.Hm = function (b) { return H.c.Lc(H.c.on(b)) };
	H.c.mv = H.c.Hm; H.c.hv = function (b) { return !/[^\t\n\r ]/.test(b) }; H.c.ev = function (b) { return !/[^a-zA-Z]/.test(b) }; H.c.Av = function (b) { return !/[^0-9]/.test(b) }; H.c.fv = function (b) { return !/[^a-zA-Z0-9]/.test(b) }; H.c.Hv = function (b) { return " " == b }; H.c.Iv = function (b) { return 1 == b.length && " " <= b && "~" >= b || "\u0080" <= b && "\ufffd" >= b }; H.c.qx = function (b) { return b.replace(/(\r\n|\r|\n)+/g, " ") }; H.c.rl = function (b) { return b.replace(/(\r\n|\r|\n)/g, "\n") }; H.c.bw = function (b) { return b.replace(/\xa0|\s/g, " ") };
	H.c.aw = function (b) { return b.replace(/\xa0|[ \t]+/g, " ") }; H.c.jt = function (b) { return b.replace(/[\t\r\n ]+/g, " ").replace(/^[\t\r\n ]+|[\t\r\n ]+$/g, "") }; H.c.trim = H.c.A.trim; H.c.trimLeft = function (b) { return b.replace(/^[\s\xa0]+/, "") }; H.c.trimRight = function (b) { return b.replace(/[\s\xa0]+$/, "") }; H.c.ed = H.c.A.ed;
	H.c.Ni = function (b, c, d) { if (b == c) return 0; if (!b) return -1; if (!c) return 1; for (var e = b.toLowerCase().match(d), f = c.toLowerCase().match(d), g = Math.min(e.length, f.length), h = 0; h < g; h++) { d = e[h]; var k = f[h]; if (d != k) return b = parseInt(d, 10), !isNaN(b) && (c = parseInt(k, 10), !isNaN(c) && b - c) ? b - c : d < k ? -1 : 1 } return e.length != f.length ? e.length - f.length : b < c ? -1 : 1 }; H.c.cv = function (b, c) { return H.c.Ni(b, c, /\d+|\D+/g) }; H.c.Vl = function (b, c) { return H.c.Ni(b, c, /\d+|\.\d+|\D+/g) }; H.c.dw = H.c.Vl; H.c.Rc = function (b) { return encodeURIComponent(String(b)) };
	H.c.$d = function (b) { return decodeURIComponent(b.replace(/\+/g, " ")) }; H.c.Nc = H.c.A.Nc; H.c.Ha = function (b, c) { b = H.c.A.Ha(b, c); H.c.Uj && (b = b.replace(H.c.Yj, "&#101;")); return b }; H.c.Yj = /e/g; H.c.nj = function (b) { return H.c.contains(b, "&") ? !H.c.Zj && "document" in H.global ? H.c.oj(b) : H.c.Ho(b) : b }; H.c.Dx = function (b, c) { return H.c.contains(b, "&") ? H.c.oj(b, c) : b };
	H.c.oj = function (b, c) { var d = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"' }; var e = c ? c.createElement("div") : H.global.document.createElement("div"); return b.replace(H.c.dk, function (f, g) { var h = d[f]; if (h) return h; "#" == g.charAt(0) && (g = Number("0" + g.substr(1)), isNaN(g) || (h = String.fromCharCode(g))); h || (H.a.O.xf(e, H.b.kb.Si(H.c.M.from("Single HTML entity."), f + " ")), h = e.firstChild.nodeValue.slice(0, -1)); return d[f] = h }) };
	H.c.Ho = function (b) { return b.replace(/&([^;]+);/g, function (c, d) { switch (d) { case "amp": return "&"; case "lt": return "<"; case "gt": return ">"; case "quot": return '"'; default: return "#" != d.charAt(0) || (d = Number("0" + d.substr(1)), isNaN(d)) ? c : String.fromCharCode(d) } }) }; H.c.dk = /&([^;\s<&]+);?/g; H.c.sj = function (b) { return H.c.Nc(b.replace(/  /g, " &#160;"), void 0) }; H.c.jw = function (b) { return b.replace(/(^|[\n ]) /g, "$1" + H.c.yg.lg) };
	H.c.rx = function (b, c) { for (var d = c.length, e = 0; e < d; e++) { var f = 1 == d ? c : c.charAt(e); if (b.charAt(0) == f && b.charAt(b.length - 1) == f) return b.substring(1, b.length - 1) } return b }; H.c.truncate = function (b, c, d) { d && (b = H.c.nj(b)); b.length > c && (b = b.substring(0, c - 3) + "..."); d && (b = H.c.Ha(b)); return b }; H.c.Bx = function (b, c, d, e) { d && (b = H.c.nj(b)); e && b.length > c ? (e > c && (e = c), b = b.substring(0, c - e) + "..." + b.substring(b.length - e)) : b.length > c && (e = Math.floor(c / 2), b = b.substring(0, e + c % 2) + "..." + b.substring(b.length - e)); d && (b = H.c.Ha(b)); return b };
	H.c.Bf = { "\x00": "\\0", "\b": "\\b", "\f": "\\f", "\n": "\\n", "\r": "\\r", "\t": "\\t", "\x0B": "\\x0B", '"': '\\"', "\\": "\\\\", "<": "\\u003C" }; H.c.Cd = { "'": "\\'" }; H.c.quote = function (b) { b = String(b); for (var c = ['"'], d = 0; d < b.length; d++) { var e = b.charAt(d), f = e.charCodeAt(0); c[d + 1] = H.c.Bf[e] || (31 < f && 127 > f ? e : H.c.gh(e)) } c.push('"'); return c.join("") }; H.c.Vt = function (b) { for (var c = [], d = 0; d < b.length; d++)c[d] = H.c.gh(b.charAt(d)); return c.join("") };
	H.c.gh = function (b) { if (b in H.c.Cd) return H.c.Cd[b]; if (b in H.c.Bf) return H.c.Cd[b] = H.c.Bf[b]; var c = b.charCodeAt(0); if (31 < c && 127 > c) var d = b; else { if (256 > c) { if (d = "\\x", 16 > c || 256 < c) d += "0" } else d = "\\u", 4096 > c && (d += "0"); d += c.toString(16).toUpperCase() } return H.c.Cd[b] = d }; H.c.contains = H.c.A.contains; H.c.fd = H.c.A.fd; H.c.ut = function (b, c) { return b && c ? b.split(c).length - 1 : 0 }; H.c.kc = A(); H.c.remove = function (b, c) { return b.replace(c, "") }; H.c.ow = function (b, c) { c = new RegExp(H.c.rf(c), "g"); return b.replace(c, "") };
	H.c.tw = function (b, c, d) { c = new RegExp(H.c.rf(c), "g"); return b.replace(c, d.replace(/\$/g, "$$$$")) }; H.c.rf = function (b) { return String(b).replace(/([-()\[\]{}+?*.$\^|,:#<!\\])/g, "\\$1").replace(/\x08/g, "\\x08") }; H.c.repeat = String.prototype.repeat ? function (b, c) { return b.repeat(c) } : function (b, c) { return Array(c + 1).join(b) }; H.c.gw = function (b, c, d) { b = H.ca(d) ? b.toFixed(d) : String(b); d = b.indexOf("."); -1 == d && (d = b.length); return H.c.repeat("0", Math.max(0, c - d)) + b }; H.c.on = function (b) { return null == b ? "" : String(b) };
	H.c.nl = function (b) { return Array.prototype.join.call(arguments, "") }; H.c.Ph = function () { return Math.floor(2147483648 * Math.random()).toString(36) + Math.abs(Math.floor(2147483648 * Math.random()) ^ H.now()).toString(36) }; H.c.Kb = H.c.A.Kb; H.c.Wu = function (b) { for (var c = 0, d = 0; d < b.length; ++d)c = 31 * c + b.charCodeAt(d) >>> 0; return c }; H.c.Io = 2147483648 * Math.random() | 0; H.c.Ft = function () { return "goog_" + H.c.Io++ }; H.c.xx = function (b) { var c = Number(b); return 0 == c && H.c.Lc(b) ? NaN : c }; H.c.tv = function (b) { return /^[a-z]+([A-Z][a-z]*)*$/.test(b) };
	H.c.Jv = function (b) { return /^([A-Z][a-z]*)+$/.test(b) }; H.c.wx = function (b) { return String(b).replace(/\-([a-z])/g, function (c, d) { return d.toUpperCase() }) }; H.c.yx = function (b) { return String(b).replace(/([A-Z])/g, "-$1").toLowerCase() }; H.c.zx = function (b, c) { c = H.L(c) ? H.c.rf(c) : "\\s"; return b.replace(new RegExp("(^" + (c ? "|[" + c + "]+" : "") + ")([a-z])", "g"), function (d, e, f) { return e + f.toUpperCase() }) }; H.c.ft = function (b) { return String(b.charAt(0)).toUpperCase() + String(b.substr(1)).toLowerCase() };
	H.c.parseInt = function (b) { isFinite(b) && (b = String(b)); return H.L(b) ? /^\s*-?0x/i.test(b) ? parseInt(b, 16) : parseInt(b, 10) : NaN }; H.c.kx = function (b, c, d) { b = b.split(c); for (var e = []; 0 < d && b.length;)e.push(b.shift()), d--; b.length && e.push(b.join(c)); return e }; H.c.Mv = function (b, c) { if (c) typeof c == y && (c = [c]); else return b; for (var d = -1, e = 0; e < c.length; e++)if ("" != c[e]) { var f = b.lastIndexOf(c[e]); f > d && (d = f) } return -1 == d ? b : b.slice(d + 1) };
	H.c.Ot = function (b, c) { var d = [], e = []; if (b == c) return 0; if (!b.length || !c.length) return Math.max(b.length, c.length); for (var f = 0; f < c.length + 1; f++)d[f] = f; for (f = 0; f < b.length; f++) { e[0] = f + 1; for (var g = 0; g < c.length; g++)e[g + 1] = Math.min(e[g] + 1, d[g + 1] + 1, d[g] + Number(b[f] != c[g])); for (g = 0; g < d.length; g++)d[g] = e[g] } return e[c.length] }; H.h.userAgent.ea = {}; H.h.userAgent.ea.Rm = function () { return H.h.userAgent.F.T("Presto") }; H.h.userAgent.ea.Vm = function () { return H.h.userAgent.F.T("Trident") || H.h.userAgent.F.T("MSIE") }; H.h.userAgent.ea.Ab = function () { return H.h.userAgent.F.T("Edge") }; H.h.userAgent.ea.yi = function () { return H.h.userAgent.F.nf("WebKit") && !H.h.userAgent.ea.Ab() }; H.h.userAgent.ea.Jm = function () { return H.h.userAgent.F.T("Gecko") && !H.h.userAgent.ea.yi() && !H.h.userAgent.ea.Vm() && !H.h.userAgent.ea.Ab() };
	H.h.userAgent.ea.Hc = function () { var b = H.h.userAgent.F.cc(); if (b) { b = H.h.userAgent.F.jh(b); var c = H.h.userAgent.ea.dm(b); if (c) return "Gecko" == c[0] ? H.h.userAgent.ea.rm(b) : c[1]; b = b[0]; var d; if (b && (d = b[2]) && (d = /Trident\/([^\s;]+)/.exec(d))) return d[1] } return "" }; H.h.userAgent.ea.dm = function (b) { if (!H.h.userAgent.ea.Ab()) return b[1]; for (var c = 0; c < b.length; c++) { var d = b[c]; if ("Edge" == d[0]) return d } }; H.h.userAgent.ea.Xa = function (b) { return 0 <= H.c.Kb(H.h.userAgent.ea.Hc(), b) };
	H.h.userAgent.ea.rm = function (b) { return (b = H.g.find(b, function (c) { return "Firefox" == c[0] })) && b[1] || "" }; H.async.jj = function (b) { H.global.setTimeout(function () { throw b; }, 0) }; H.async.Na = function (b, c, d) { var e = b; c && (e = H.bind(b, c)); e = H.async.Na.tj(e); H.Wa(H.global.setImmediate) && (d || H.async.Na.No()) ? H.global.setImmediate(e) : (H.async.Na.Zi || (H.async.Na.Zi = H.async.Na.mm()), H.async.Na.Zi(e)) }; H.async.Na.No = function () { return H.global.Window && H.global.Window.prototype && !H.h.userAgent.B.Ab() && H.global.Window.prototype.setImmediate == H.global.setImmediate ? !1 : !0 };
	H.async.Na.mm = function () {
		var b = H.global.MessageChannel; "undefined" === typeof b && "undefined" !== typeof window && window.postMessage && window.addEventListener && !H.h.userAgent.ea.Rm() && (b = function () {
			var f = document.createElement("IFRAME"); f.style.display = "none"; H.a.O.ho(f); document.documentElement.appendChild(f); var g = f.contentWindow; f = g.document; f.open(); H.a.O.Il(f); f.close(); var h = "callImmediate" + Math.random(), k = "file:" == g.location.protocol ? "*" : g.location.protocol + "//" + g.location.host; f = H.bind(function (m) {
				if (("*" ==
					k || m.origin == k) && m.data == h) this.port1.onmessage()
			}, this); g.addEventListener("message", f, !1); this.port1 = {}; this.port2 = { postMessage: function () { g.postMessage(h, k) } }
		}); if ("undefined" !== typeof b && !H.h.userAgent.B.Bd()) { var c = new b, d = {}, e = d; c.port1.onmessage = function () { if (H.ca(d.next)) { d = d.next; var f = d.Qg; d.Qg = null; f() } }; return function (f) { e.next = { Qg: f }; e = e.next; c.port2.postMessage(0) } } return "undefined" !== typeof document && "onreadystatechange" in document.createElement(l) ? function (f) {
			var g = document.createElement(l);
			g.onreadystatechange = function () { g.onreadystatechange = null; g.parentNode.removeChild(g); g = null; f(); f = null }; document.documentElement.appendChild(g)
		} : function (f) { H.global.setTimeout(f, 0) }
	}; H.async.Na.tj = H.V.ai; H.debug.pa.register(function (b) { H.async.Na.tj = b }); H.async.gb = function () { this.be = this.nc = null }; H.async.gb.ge = 100; H.async.gb.Ec = new H.async.Wc(function () { return new H.async.re }, function (b) { b.reset() }, H.async.gb.ge); H.async.gb.prototype.add = function (b, c) { var d = H.async.gb.Ec.get(); d.set(b, c); this.be ? this.be.next = d : this.nc = d; this.be = d }; H.async.gb.prototype.remove = function () { var b = null; this.nc && (b = this.nc, this.nc = this.nc.next, this.nc || (this.be = null), b.next = null); return b }; H.async.re = function () { this.next = this.scope = this.Je = null };
	H.async.re.prototype.set = function (b, c) { this.Je = b; this.scope = c; this.next = null }; H.async.re.prototype.reset = function () { this.next = this.scope = this.Je = null }; H.Dj = !1; H.async.X = function (b, c) { H.async.X.Ud || H.async.X.Dm(); H.async.X.ae || (H.async.X.Ud(), H.async.X.ae = !0); H.async.X.Jf.add(b, c) }; H.async.X.Dm = function () { if (H.Dj || H.global.Promise && H.global.Promise.resolve) { var b = H.global.Promise.resolve(void 0); H.async.X.Ud = function () { b.then(H.async.X.Od) } } else H.async.X.Ud = function () { H.async.Na(H.async.X.Od) } }; H.async.X.du = function (b) { H.async.X.Ud = function () { H.async.Na(H.async.X.Od); b && b(H.async.X.Od) } }; H.async.X.ae = !1; H.async.X.Jf = new H.async.gb;
	H.sa && (H.async.X.xw = function () { H.async.X.ae = !1; H.async.X.Jf = new H.async.gb }); H.async.X.Od = function () { for (var b; b = H.async.X.Jf.remove();) { try { b.Je.call(b.scope) } catch (c) { H.async.jj(c) } H.async.gb.Ec.put(b) } H.async.X.ae = !1 }; H.h.userAgent.platform = {}; H.h.userAgent.platform.gi = function () { return H.h.userAgent.F.T("Android") }; H.h.userAgent.platform.ri = function () { return H.h.userAgent.F.T("iPod") }; H.h.userAgent.platform.pi = function () { return H.h.userAgent.F.T("iPhone") && !H.h.userAgent.F.T("iPod") && !H.h.userAgent.F.T("iPad") }; H.h.userAgent.platform.oi = function () { return H.h.userAgent.F.T("iPad") }; H.h.userAgent.platform.ni = function () { return H.h.userAgent.platform.pi() || H.h.userAgent.platform.oi() || H.h.userAgent.platform.ri() };
	H.h.userAgent.platform.ti = function () { return H.h.userAgent.F.T("Macintosh") }; H.h.userAgent.platform.Om = function () { return H.h.userAgent.F.T("Linux") }; H.h.userAgent.platform.Bi = function () { return H.h.userAgent.F.T("Windows") }; H.h.userAgent.platform.ii = function () { return H.h.userAgent.F.T("CrOS") }; H.h.userAgent.platform.iv = function () { return H.h.userAgent.F.T("CrKey") }; H.h.userAgent.platform.si = function () { return H.h.userAgent.F.nf("KaiOS") }; H.h.userAgent.platform.Km = function () { return H.h.userAgent.F.nf("GAFP") };
	H.h.userAgent.platform.Hc = function () {
		var b = H.h.userAgent.F.cc(), c = ""; H.h.userAgent.platform.Bi() ? (c = /Windows (?:NT|Phone) ([0-9.]+)/, c = (b = c.exec(b)) ? b[1] : "0.0") : H.h.userAgent.platform.ni() ? (c = /(?:iPhone|iPod|iPad|CPU)\s+OS\s+(\S+)/, c = (b = c.exec(b)) && b[1].replace(/_/g, ".")) : H.h.userAgent.platform.ti() ? (c = /Mac OS X ([0-9_.]+)/, c = (b = c.exec(b)) ? b[1].replace(/_/g, ".") : "10") : H.h.userAgent.platform.si() ? (c = /(?:KaiOS)\/(\S+)/i, c = (b = c.exec(b)) && b[1]) : H.h.userAgent.platform.gi() ? (c = /Android\s+([^\);]+)(\)|;)/,
			c = (b = c.exec(b)) && b[1]) : H.h.userAgent.platform.ii() && (c = /(?:CrOS\s+(?:i686|x86_64)\s+([0-9.]+))/, c = (b = c.exec(b)) && b[1]); return c || ""
	}; H.h.userAgent.platform.Xa = function (b) { return 0 <= H.c.Kb(H.h.userAgent.platform.Hc(), b) }; H.ib = {}; H.ib.object = function (b, c) { return c }; H.ib.Af = function (b) { H.ib.Af[" "](b); return b }; H.ib.Af[" "] = H.Rb; H.ib.dt = function (b, c) { try { return H.ib.Af(b[c]), !0 } catch (d) { } return !1 }; H.ib.cache = function (b, c, d, e) { e = e ? e(c) : c; return Object.prototype.hasOwnProperty.call(b, e) ? b[e] : b[e] = d(c) }; H.userAgent = {}; H.userAgent.Pf = !1; H.userAgent.Nf = !1; H.userAgent.Of = !1; H.userAgent.Uf = !1; H.userAgent.fe = !1; H.userAgent.Sf = !1; H.userAgent.zj = !1; H.userAgent.pc = H.userAgent.Pf || H.userAgent.Nf || H.userAgent.Of || H.userAgent.fe || H.userAgent.Uf || H.userAgent.Sf; H.userAgent.pm = function () { return H.h.userAgent.F.cc() }; H.userAgent.We = function () { return H.global.navigator || null }; H.userAgent.Du = function () { return H.userAgent.We() }; H.userAgent.og = H.userAgent.pc ? H.userAgent.Sf : H.h.userAgent.B.ff();
	H.userAgent.oa = H.userAgent.pc ? H.userAgent.Pf : H.h.userAgent.B.Bd(); H.userAgent.ag = H.userAgent.pc ? H.userAgent.Nf : H.h.userAgent.ea.Ab(); H.userAgent.Vp = H.userAgent.ag || H.userAgent.oa; H.userAgent.he = H.userAgent.pc ? H.userAgent.Of : H.h.userAgent.ea.Jm(); H.userAgent.sc = H.userAgent.pc ? H.userAgent.Uf || H.userAgent.fe : H.h.userAgent.ea.yi(); H.userAgent.Qm = function () { return H.userAgent.sc && H.h.userAgent.F.T("Mobile") }; H.userAgent.gr = H.userAgent.fe || H.userAgent.Qm(); H.userAgent.Hr = H.userAgent.sc;
	H.userAgent.Fl = function () { var b = H.userAgent.We(); return b && b.platform || "" }; H.userAgent.xr = H.userAgent.Fl(); H.userAgent.Rf = !1; H.userAgent.Vf = !1; H.userAgent.Qf = !1; H.userAgent.Wf = !1; H.userAgent.Mf = !1; H.userAgent.de = !1; H.userAgent.ce = !1; H.userAgent.ee = !1; H.userAgent.Cj = !1; H.userAgent.Bj = !1; H.userAgent.Ra = H.userAgent.Rf || H.userAgent.Vf || H.userAgent.Qf || H.userAgent.Wf || H.userAgent.Mf || H.userAgent.de || H.userAgent.ce || H.userAgent.ee; H.userAgent.Xq = H.userAgent.Ra ? H.userAgent.Rf : H.h.userAgent.platform.ti();
	H.userAgent.ws = H.userAgent.Ra ? H.userAgent.Vf : H.h.userAgent.platform.Bi(); H.userAgent.Nm = function () { return H.h.userAgent.platform.Om() || H.h.userAgent.platform.ii() }; H.userAgent.Vq = H.userAgent.Ra ? H.userAgent.Qf : H.userAgent.Nm(); H.userAgent.Zm = function () { var b = H.userAgent.We(); return !!b && H.c.contains(b.appVersion || "", "X11") }; H.userAgent.xs = H.userAgent.Ra ? H.userAgent.Wf : H.userAgent.Zm(); H.userAgent.ap = H.userAgent.Ra ? H.userAgent.Mf : H.h.userAgent.platform.gi();
	H.userAgent.Jq = H.userAgent.Ra ? H.userAgent.de : H.h.userAgent.platform.pi(); H.userAgent.Iq = H.userAgent.Ra ? H.userAgent.ce : H.h.userAgent.platform.oi(); H.userAgent.Kq = H.userAgent.Ra ? H.userAgent.ee : H.h.userAgent.platform.ri(); H.userAgent.Hq = H.userAgent.Ra ? H.userAgent.de || H.userAgent.ce || H.userAgent.ee : H.h.userAgent.platform.ni(); H.userAgent.Nq = H.userAgent.Ra ? H.userAgent.Cj : H.h.userAgent.platform.si(); H.userAgent.nq = H.userAgent.Ra ? H.userAgent.Bj : H.h.userAgent.platform.Km();
	H.userAgent.Gl = function () { var b = "", c = H.userAgent.sm(); c && (b = c ? c[1] : ""); return H.userAgent.oa && (c = H.userAgent.xh(), null != c && c > parseFloat(b)) ? String(c) : b }; H.userAgent.sm = function () { var b = H.userAgent.pm(); if (H.userAgent.he) return /rv:([^\);]+)(\)|;)/.exec(b); if (H.userAgent.ag) return /Edge\/([\d\.]+)/.exec(b); if (H.userAgent.oa) return /\b(?:MSIE|rv)[: ]([^\);]+)(\)|;)/.exec(b); if (H.userAgent.sc) return /WebKit\/(\S+)/.exec(b); if (H.userAgent.og) return /(?:Version)[ \/]?(\S+)/.exec(b) };
	H.userAgent.xh = function () { var b = H.global.document; return b ? b.documentMode : void 0 }; H.userAgent.VERSION = H.userAgent.Gl(); H.userAgent.compare = function (b, c) { return H.c.Kb(b, c) }; H.userAgent.Xm = {}; H.userAgent.Xa = function (b) { return H.userAgent.zj || H.ib.cache(H.userAgent.Xm, b, function () { return 0 <= H.c.Kb(H.userAgent.VERSION, b) }) }; H.userAgent.Kv = H.userAgent.Xa; H.userAgent.Kc = function (b) { return Number(H.userAgent.Xj) >= b }; H.userAgent.lv = H.userAgent.Kc; var fa;
	fa = H.global.document && H.userAgent.oa ? H.userAgent.xh() : void 0; H.userAgent.Xj = fa; H.a.ha = {}; H.a.ha.Ej = !1; H.a.ha.Fj = !1; H.a.ha.El = function () { try { return !!(new self.OffscreenCanvas(0, 0)).getContext("2d") } catch (b) { } return !1 }; H.a.ha.pr = !H.a.ha.Ej && (H.a.ha.Fj || H.a.ha.El()); H.a.ha.Kj = !H.userAgent.oa || H.userAgent.Kc(9); H.a.ha.Lj = !H.userAgent.he && !H.userAgent.oa || H.userAgent.oa && H.userAgent.Kc(9) || H.userAgent.he && H.userAgent.Xa("1.9.1"); H.a.ha.Yf = H.userAgent.oa && !H.userAgent.Xa("9"); H.a.ha.Mj = H.userAgent.oa || H.userAgent.og || H.userAgent.sc; H.a.ha.ek = H.userAgent.oa;
	H.a.ha.Rq = H.userAgent.oa && !H.userAgent.Kc(9); H.C = {}; H.C.lw = function (b) { return Math.floor(Math.random() * b) }; H.C.Ex = function (b, c) { return b + Math.random() * (c - b) }; H.C.ht = function (b, c, d) { return Math.min(Math.max(b, c), d) }; H.C.Li = function (b, c) { b %= c; return 0 > b * c ? b + c : b }; H.C.Nv = function (b, c, d) { return b + d * (c - b) }; H.C.Yv = function (b, c, d) { return Math.abs(b - c) <= (d || 1E-6) }; H.C.Df = function (b) { return H.C.Li(b, 360) }; H.C.nx = function (b) { return H.C.Li(b, 2 * Math.PI) }; H.C.mj = function (b) { return b * Math.PI / 180 }; H.C.yo = function (b) { return 180 * b / Math.PI };
	H.C.Es = function (b, c) { return c * Math.cos(H.C.mj(b)) }; H.C.Fs = function (b, c) { return c * Math.sin(H.C.mj(b)) }; H.C.angle = function (b, c, d, e) { return H.C.Df(H.C.yo(Math.atan2(e - c, d - b))) }; H.C.Ds = function (b, c) { b = H.C.Df(c) - H.C.Df(b); 180 < b ? b -= 360 : -180 >= b && (b = 360 + b); return b }; H.C.sign = function (b) { return 0 < b ? 1 : 0 > b ? -1 : b };
	H.C.Rv = function (b, c, d, e) { d = d || function (r, w) { return r == w }; e = e || function (r) { return b[r] }; for (var f = b.length, g = c.length, h = [], k = 0; k < f + 1; k++)h[k] = [], h[k][0] = 0; for (var m = 0; m < g + 1; m++)h[0][m] = 0; for (k = 1; k <= f; k++)for (m = 1; m <= g; m++)d(b[k - 1], c[m - 1]) ? h[k][m] = h[k - 1][m - 1] + 1 : h[k][m] = Math.max(h[k - 1][m], h[k][m - 1]); var n = []; k = f; for (m = g; 0 < k && 0 < m;)d(b[k - 1], c[m - 1]) ? (n.unshift(e(k - 1, m - 1)), k--, m--) : h[k - 1][m] > h[k][m - 1] ? k-- : m--; return n }; H.C.Ff = function (b) { return H.g.reduce(arguments, function (c, d) { return c + d }, 0) };
	H.C.fl = function (b) { return H.C.Ff.apply(null, arguments) / arguments.length }; H.C.ao = function (b) { var c = arguments.length; if (2 > c) return 0; var d = H.C.fl.apply(null, arguments); return H.C.Ff.apply(null, H.g.map(arguments, function (e) { return Math.pow(e - d, 2) })) / (c - 1) }; H.C.ox = function (b) { return Math.sqrt(H.C.ao.apply(null, arguments)) }; H.C.rv = function (b) { return isFinite(b) && 0 == b % 1 }; H.C.ov = function (b) { return isFinite(b) }; H.C.wv = function (b) { return 0 == b && 0 > 1 / b };
	H.C.Qv = function (b) { if (0 < b) { var c = Math.round(Math.log(b) * Math.LOG10E); return c - (parseFloat("1e" + c) > b ? 1 : 0) } return 0 == b ? -Infinity : NaN }; H.C.Aw = function (b, c) { return Math.floor(b + (c || 2E-15)) }; H.C.zw = function (b, c) { return Math.ceil(b - (c || 2E-15)) }; H.C.ia = function (b, c) { this.x = H.ca(b) ? b : 0; this.y = H.ca(c) ? c : 0 }; H.C.ia.prototype.clone = function () { return new H.C.ia(this.x, this.y) }; H.sa && (H.C.ia.prototype.toString = function () { return "(" + this.x + ", " + this.y + ")" }); H.C.ia.prototype.Ob = function (b) { return b instanceof H.C.ia && H.C.ia.Ob(this, b) }; H.C.ia.Ob = function (b, c) { return b == c ? !0 : b && c ? b.x == c.x && b.y == c.y : !1 }; H.C.ia.Mt = function (b, c) { var d = b.x - c.x; b = b.y - c.y; return Math.sqrt(d * d + b * b) }; H.C.ia.Sv = function (b) { return Math.sqrt(b.x * b.x + b.y * b.y) };
	H.C.ia.azimuth = function (b) { return H.C.angle(0, 0, b.x, b.y) }; H.C.ia.lx = function (b, c) { var d = b.x - c.x; b = b.y - c.y; return d * d + b * b }; H.C.ia.Lt = function (b, c) { return new H.C.ia(b.x - c.x, b.y - c.y) }; H.C.ia.Ff = function (b, c) { return new H.C.ia(b.x + c.x, b.y + c.y) }; F = H.C.ia.prototype; F.ceil = function () { this.x = Math.ceil(this.x); this.y = Math.ceil(this.y); return this }; F.floor = function () { this.x = Math.floor(this.x); this.y = Math.floor(this.y); return this }; F.round = function () { this.x = Math.round(this.x); this.y = Math.round(this.y); return this };
	F.translate = function (b, c) { b instanceof H.C.ia ? (this.x += b.x, this.y += b.y) : (this.x += Number(b), H.Bb(c) && (this.y += c)); return this }; F.scale = function (b, c) { c = H.Bb(c) ? c : b; this.x *= b; this.y *= c; return this }; H.C.Yb = function (b, c) { this.width = b; this.height = c }; H.C.Yb.Ob = function (b, c) { return b == c ? !0 : b && c ? b.width == c.width && b.height == c.height : !1 }; H.C.Yb.prototype.clone = function () { return new H.C.Yb(this.width, this.height) }; H.sa && (H.C.Yb.prototype.toString = function () { return "(" + this.width + " x " + this.height + ")" }); F = H.C.Yb.prototype; F.aspectRatio = function () { return this.width / this.height }; F.Ca = function () { return !(this.width * this.height) };
	F.ceil = function () { this.width = Math.ceil(this.width); this.height = Math.ceil(this.height); return this }; F.floor = function () { this.width = Math.floor(this.width); this.height = Math.floor(this.height); return this }; F.round = function () { this.width = Math.round(this.width); this.height = Math.round(this.height); return this }; F.scale = function (b, c) { c = H.Bb(c) ? c : b; this.width *= b; this.height *= c; return this }; H.a.Gj = !1; H.a.Tf = !1; H.a.Qj = H.a.Gj || H.a.Tf; H.a.Qe = function (b) { return b ? new H.a.Vb(H.a.xb(b)) : H.a.Cl || (H.a.Cl = new H.a.Vb) }; H.a.Zl = function () { return document }; H.a.Re = function (b) { return H.a.Ue(document, b) }; H.a.Ue = function (b, c) { return H.L(c) ? b.getElementById(c) : c }; H.a.jm = function (b) { return H.a.Rh(document, b) }; H.a.Rh = function (b, c) { return H.a.Ue(b, c) }; H.a.vj = H.a.Re; H.a.getElementsByTagName = function (b, c) { return (c || document).getElementsByTagName(String(b)) };
	H.a.Ve = function (b, c, d) { return H.a.qd(document, b, c, d) }; H.a.cm = function (b, c, d) { return H.a.Te(document, b, c, d) }; H.a.Ah = function (b, c) { var d = c || document; return H.a.ze(d) ? d.querySelectorAll("." + b) : H.a.qd(document, "*", b, c) }; H.a.Se = function (b, c) { var d = c || document; return (d.getElementsByClassName ? d.getElementsByClassName(b)[0] : H.a.Te(document, "*", b, c)) || null }; H.a.Qh = function (b, c) { return H.a.Se(b, c) }; H.a.ze = function (b) { return !(!b.querySelectorAll || !b.querySelector) };
	H.a.qd = function (b, c, d, e) { b = e || b; c = c && "*" != c ? String(c).toUpperCase() : ""; if (H.a.ze(b) && (c || d)) return b.querySelectorAll(c + (d ? "." + d : "")); if (d && b.getElementsByClassName) { b = b.getElementsByClassName(d); if (c) { e = {}; for (var f = 0, g = 0, h; h = b[g]; g++)c == h.nodeName && (e[f++] = h); e.length = f; return e } return b } b = b.getElementsByTagName(c || "*"); if (d) { e = {}; for (g = f = 0; h = b[g]; g++)c = h.className, typeof c.split == t && H.g.contains(c.split(/\s+/), d) && (e[f++] = h); e.length = f; return e } return b };
	H.a.Te = function (b, c, d, e) { var f = e || b, g = c && "*" != c ? String(c).toUpperCase() : ""; return H.a.ze(f) && (g || d) ? f.querySelector(g + (d ? "." + d : "")) : H.a.qd(b, c, d, e)[0] || null }; H.a.wj = H.a.Ve; H.a.Wd = function (b, c) { H.object.forEach(c, function (d, e) { d && typeof d == v && d.Va && (d = d.Ga()); "style" == e ? b.style.cssText = d : "class" == e ? b.className = d : "for" == e ? b.htmlFor = d : H.a.$f.hasOwnProperty(e) ? b.setAttribute(H.a.$f[e], d) : H.c.startsWith(e, "aria-") || H.c.startsWith(e, "data-") ? b.setAttribute(e, d) : b[e] = d }) };
	H.a.$f = { cellpadding: "cellPadding", cellspacing: "cellSpacing", colspan: "colSpan", frameborder: "frameBorder", height: "height", maxlength: "maxLength", nonce: "nonce", role: "role", rowspan: "rowSpan", type: "type", usemap: "useMap", valign: "vAlign", width: "width" }; H.a.Wh = function (b) { return H.a.Xh(b || window) }; H.a.Xh = function (b) { b = b.document; b = H.a.Jc(b) ? b.documentElement : b.body; return new H.C.Yb(b.clientWidth, b.clientHeight) }; H.a.$l = function () { return H.a.Oe(window) }; H.a.wu = function (b) { return H.a.Oe(b) };
	H.a.Oe = function (b) { var c = b.document, d = 0; if (c) { d = c.body; var e = c.documentElement; if (!e || !d) return 0; b = H.a.Xh(b).height; if (H.a.Jc(c) && e.scrollHeight) d = e.scrollHeight != b ? e.scrollHeight : e.offsetHeight; else { c = e.scrollHeight; var f = e.offsetHeight; e.clientHeight != f && (c = d.scrollHeight, f = d.offsetHeight); d = c > b ? c > f ? c : f : c < f ? c : f } } return d }; H.a.Fu = function (b) { return H.a.Qe((b || H.global || window).document).yh() }; H.a.yh = function () { return H.a.zh(document) };
	H.a.zh = function (b) { var c = H.a.Pe(b); b = H.a.ec(b); return H.userAgent.oa && H.userAgent.Xa("10") && b.pageYOffset != c.scrollTop ? new H.C.ia(c.scrollLeft, c.scrollTop) : new H.C.ia(b.pageXOffset || c.scrollLeft, b.pageYOffset || c.scrollTop) }; H.a.am = function () { return H.a.Pe(document) }; H.a.Pe = function (b) { return b.scrollingElement ? b.scrollingElement : !H.userAgent.sc && H.a.Jc(b) ? b.documentElement : b.body || b.documentElement }; H.a.dc = function (b) { return b ? H.a.ec(b) : window }; H.a.ec = function (b) { return b.parentWindow || b.defaultView };
	H.a.Ce = function (b, c, d) { return H.a.Yg(document, arguments) }; H.a.Yg = function (b, c) { var d = String(c[0]), e = c[1]; if (!H.a.ha.Kj && e && (e.name || e.type)) { d = ["<", d]; e.name && d.push(' name="', H.c.Ha(e.name), '"'); if (e.type) { d.push(' type="', H.c.Ha(e.type), '"'); var f = {}; H.object.extend(f, e); delete f.type; e = f } d.push(">"); d = d.join("") } d = b.createElement(d); e && (H.L(e) ? d.className = e : H.isArray(e) ? d.className = e.join(" ") : H.a.Wd(d, e)); 2 < c.length && H.a.Eg(b, d, c, 2); return d };
	H.a.Eg = function (b, c, d, e) { function f(h) { h && c.appendChild(H.L(h) ? b.createTextNode(h) : h) } for (; e < d.length; e++) { var g = d[e]; H.ma(g) && !H.a.df(g) ? H.g.forEach(H.a.ef(g) ? H.g.jb(g) : g, f) : f(g) } }; H.a.xj = H.a.Ce; H.a.createElement = function (b) { return H.a.rb(document, b) }; H.a.rb = function (b, c) { return b.createElement(String(c)) }; H.a.createTextNode = function (b) { return document.createTextNode(String(b)) }; H.a.zl = function (b, c, d) { return H.a.Zg(document, b, c, !!d) };
	H.a.Zg = function (b, c, d, e) { for (var f = H.a.rb(b, "TABLE"), g = f.appendChild(H.a.rb(b, "TBODY")), h = 0; h < c; h++) { for (var k = H.a.rb(b, "TR"), m = 0; m < d; m++) { var n = H.a.rb(b, "TD"); e && H.a.yf(n, H.c.yg.lg); k.appendChild(n) } g.appendChild(k) } return f }; H.a.st = function (b) { var c = H.g.map(arguments, H.c.M.K); c = H.b.kb.Si(H.c.M.from("Constant HTML string, that gets turned into a Node later, so it will be automatically balanced."), c.join("")); return H.a.Ti(c) }; H.a.Ti = function (b) { return H.a.Ui(document, b) };
	H.a.Ui = function (b, c) { var d = H.a.rb(b, "DIV"); H.a.ha.ek ? (H.a.O.xf(d, H.b.u.concat(H.b.u.Xf, c)), d.removeChild(d.firstChild)) : H.a.O.xf(d, c); return H.a.tl(b, d) }; H.a.tl = function (b, c) { if (1 == c.childNodes.length) return c.removeChild(c.firstChild); for (b = b.createDocumentFragment(); c.firstChild;)b.appendChild(c.firstChild); return b }; H.a.Fm = function () { return H.a.Jc(document) }; H.a.Jc = function (b) { return H.a.Qj ? H.a.Tf : "CSS1Compat" == b.compatMode }; H.a.canHaveChildren = function (b) { if (b.nodeType != H.a.ua.mb) return !1; switch (b.tagName) { case "APPLET": case "AREA": case "BASE": case "BR": case "COL": case "COMMAND": case "EMBED": case "FRAME": case "HR": case "IMG": case "INPUT": case "IFRAME": case "ISINDEX": case "KEYGEN": case "LINK": case "NOFRAMES": case "NOSCRIPT": case "META": case "OBJECT": case "PARAM": case l: case "SOURCE": case "STYLE": case "TRACK": case "WBR": return !1 }return !0 };
	H.a.appendChild = function (b, c) { b.appendChild(c) }; H.a.append = function (b, c) { H.a.Eg(H.a.xb(b), b, arguments, 1) }; H.a.tf = function (b) { for (var c; c = b.firstChild;)b.removeChild(c) }; H.a.ei = function (b, c) { c.parentNode && c.parentNode.insertBefore(b, c) }; H.a.di = function (b, c) { c.parentNode && c.parentNode.insertBefore(b, c.nextSibling) }; H.a.ci = function (b, c, d) { b.insertBefore(c, b.childNodes[d] || null) }; H.a.removeNode = function (b) { return b && b.parentNode ? b.parentNode.removeChild(b) : null };
	H.a.Ri = function (b, c) { var d = c.parentNode; d && d.replaceChild(b, c) }; H.a.oh = function (b) { var c, d = b.parentNode; if (d && d.nodeType != H.a.ua.Wj) { if (b.removeNode) return b.removeNode(!1); for (; c = b.firstChild;)d.insertBefore(c, b); return H.a.removeNode(b) } }; H.a.vh = function (b) { return H.a.ha.Lj && void 0 != b.children ? b.children : H.g.filter(b.childNodes, function (c) { return c.nodeType == H.a.ua.mb }) }; H.a.Bh = function (b) { return H.ca(b.firstElementChild) ? b.firstElementChild : H.a.sd(b.firstChild, !0) };
	H.a.Eh = function (b) { return H.ca(b.lastElementChild) ? b.lastElementChild : H.a.sd(b.lastChild, !1) }; H.a.Gh = function (b) { return H.ca(b.nextElementSibling) ? b.nextElementSibling : H.a.sd(b.nextSibling, !0) }; H.a.Nh = function (b) { return H.ca(b.previousElementSibling) ? b.previousElementSibling : H.a.sd(b.previousSibling, !1) }; H.a.sd = function (b, c) { for (; b && b.nodeType != H.a.ua.mb;)b = c ? b.nextSibling : b.previousSibling; return b };
	H.a.Hh = function (b) { if (!b) return null; if (b.firstChild) return b.firstChild; for (; b && !b.nextSibling;)b = b.parentNode; return b ? b.nextSibling : null }; H.a.Oh = function (b) { if (!b) return null; if (!b.previousSibling) return b.parentNode; for (b = b.previousSibling; b && b.lastChild;)b = b.lastChild; return b }; H.a.df = function (b) { return H.Da(b) && 0 < b.nodeType }; H.a.bf = function (b) { return H.Da(b) && b.nodeType == H.a.ua.mb }; H.a.zi = function (b) { return H.Da(b) && b.window == b };
	H.a.Mh = function (b) { var c; if (H.a.ha.Mj && !(H.userAgent.oa && H.userAgent.Xa("9") && !H.userAgent.Xa("10") && H.global.SVGElement && b instanceof H.global.SVGElement) && (c = b.parentElement)) return c; c = b.parentNode; return H.a.bf(c) ? c : null }; H.a.contains = function (b, c) { if (!b || !c) return !1; if (b.contains && c.nodeType == H.a.ua.mb) return b == c || b.contains(c); if ("undefined" != typeof b.compareDocumentPosition) return b == c || !!(b.compareDocumentPosition(c) & 16); for (; c && b != c;)c = c.parentNode; return c == b };
	H.a.Sg = function (b, c) {
		if (b == c) return 0; if (b.compareDocumentPosition) return b.compareDocumentPosition(c) & 2 ? 1 : -1; if (H.userAgent.oa && !H.userAgent.Kc(9)) { if (b.nodeType == H.a.ua.Uc) return -1; if (c.nodeType == H.a.ua.Uc) return 1 } if ("sourceIndex" in b || b.parentNode && "sourceIndex" in b.parentNode) {
			var d = b.nodeType == H.a.ua.mb, e = c.nodeType == H.a.ua.mb; if (d && e) return b.sourceIndex - c.sourceIndex; var f = b.parentNode, g = c.parentNode; return f == g ? H.a.Ug(b, c) : !d && H.a.contains(f, c) ? -1 * H.a.Tg(b, c) : !e && H.a.contains(g, b) ? H.a.Tg(c,
				b) : (d ? b.sourceIndex : f.sourceIndex) - (e ? c.sourceIndex : g.sourceIndex)
		} e = H.a.xb(b); d = e.createRange(); d.selectNode(b); d.collapse(!0); b = e.createRange(); b.selectNode(c); b.collapse(!0); return d.compareBoundaryPoints(H.global.Range.START_TO_END, b)
	}; H.a.Tg = function (b, c) { var d = b.parentNode; if (d == c) return -1; for (; c.parentNode != d;)c = c.parentNode; return H.a.Ug(c, b) }; H.a.Ug = function (b, c) { for (; c = c.previousSibling;)if (c == b) return -1; return 1 };
	H.a.kh = function (b) { var c, d = arguments.length; if (!d) return null; if (1 == d) return arguments[0]; var e = [], f = Infinity; for (c = 0; c < d; c++) { for (var g = [], h = arguments[c]; h;)g.unshift(h), h = h.parentNode; e.push(g); f = Math.min(f, g.length) } g = null; for (c = 0; c < f; c++) { h = e[0][c]; for (var k = 1; k < d; k++)if (h != e[k][c]) return g; g = h } return g }; H.a.qv = function (b) { return 16 == (b.ownerDocument.compareDocumentPosition(b) & 16) }; H.a.xb = function (b) { return b.nodeType == H.a.ua.Uc ? b : b.ownerDocument || b.document };
	H.a.Ch = function (b) { return b.contentDocument || b.contentWindow.document }; H.a.Dh = function (b) { try { return b.contentWindow || (b.contentDocument ? H.a.dc(b.contentDocument) : null) } catch (c) { } return null }; H.a.yf = function (b, c) { if ("textContent" in b) b.textContent = c; else if (b.nodeType == H.a.ua.$c) b.data = String(c); else if (b.firstChild && b.firstChild.nodeType == H.a.ua.$c) { for (; b.lastChild != b.firstChild;)b.removeChild(b.lastChild); b.firstChild.data = String(c) } else H.a.tf(b), b.appendChild(H.a.xb(b).createTextNode(String(c))) };
	H.a.Lh = function (b) { if ("outerHTML" in b) return b.outerHTML; var c = H.a.rb(H.a.xb(b), "DIV"); c.appendChild(b.cloneNode(!0)); return c.innerHTML }; H.a.lh = function (b, c) { var d = []; return H.a.Ie(b, c, d, !0) ? d[0] : void 0 }; H.a.mh = function (b, c) { var d = []; H.a.Ie(b, c, d, !1); return d }; H.a.Ie = function (b, c, d, e) { if (null != b) for (b = b.firstChild; b;) { if (c(b) && (d.push(b), e) || H.a.Ie(b, c, d, e)) return !0; b = b.nextSibling } return !1 };
	H.a.Yt = function (b, c) { for (b = H.a.wh(b); 0 < b.length;) { var d = b.pop(); if (c(d)) return d; for (d = d.lastElementChild; d; d = d.previousElementSibling)b.push(d) } return null }; H.a.Zt = function (b, c) { var d = []; for (b = H.a.wh(b); 0 < b.length;) { var e = b.pop(); c(e) && d.push(e); for (e = e.lastElementChild; e; e = e.previousElementSibling)b.push(e) } return d }; H.a.wh = function (b) { if (b.nodeType == H.a.ua.Uc) return [b.documentElement]; var c = []; for (b = b.lastElementChild; b; b = b.previousElementSibling)c.push(b); return c };
	H.a.vg = { SCRIPT: 1, STYLE: 1, HEAD: 1, IFRAME: 1, OBJECT: 1 }; H.a.Yc = { IMG: " ", BR: "\n" }; H.a.cf = function (b) { return H.a.$h(b) && H.a.xi(b) }; H.a.Yi = function (b, c) { c ? b.tabIndex = 0 : (b.tabIndex = -1, b.removeAttribute("tabIndex")) }; H.a.ji = function (b) { var c; return (c = H.a.vn(b) ? !b.disabled && (!H.a.$h(b) || H.a.xi(b)) : H.a.cf(b)) && H.userAgent.oa ? H.a.xm(b) : c }; H.a.$h = function (b) { return H.userAgent.oa && !H.userAgent.Xa("9") ? (b = b.getAttributeNode("tabindex"), H.zb(b) && b.specified) : b.hasAttribute("tabindex") };
	H.a.xi = function (b) { b = b.tabIndex; return H.Bb(b) && 0 <= b && 32768 > b }; H.a.vn = function (b) { return "A" == b.tagName && b.hasAttribute("href") || "INPUT" == b.tagName || "TEXTAREA" == b.tagName || "SELECT" == b.tagName || "BUTTON" == b.tagName }; H.a.xm = function (b) { b = !H.Wa(b.getBoundingClientRect) || H.userAgent.oa && null == b.parentElement ? { height: b.offsetHeight, width: b.offsetWidth } : b.getBoundingClientRect(); return H.zb(b) && 0 < b.height && 0 < b.width };
	H.a.ud = function (b) { if (H.a.ha.Yf && null !== b && "innerText" in b) b = H.c.rl(b.innerText); else { var c = []; H.a.Ye(b, c, !0); b = c.join("") } b = b.replace(/ \xAD /g, " ").replace(/\xAD/g, ""); b = b.replace(/\u200B/g, ""); H.a.ha.Yf || (b = b.replace(/ +/g, " ")); " " != b && (b = b.replace(/^\s*/, "")); return b }; H.a.Ku = function (b) { var c = []; H.a.Ye(b, c, !1); return c.join("") };
	H.a.Ye = function (b, c, d) { if (!(b.nodeName in H.a.vg)) if (b.nodeType == H.a.ua.$c) d ? c.push(String(b.nodeValue).replace(/(\r\n|\r|\n)/g, "")) : c.push(b.nodeValue); else if (b.nodeName in H.a.Yc) c.push(H.a.Yc[b.nodeName]); else for (b = b.firstChild; b;)H.a.Ye(b, c, d), b = b.nextSibling }; H.a.Jh = function (b) { return H.a.ud(b).length }; H.a.Kh = function (b, c) { c = c || H.a.xb(b).body; for (var d = []; b && b != c;) { for (var e = b; e = e.previousSibling;)d.unshift(H.a.ud(e)); b = b.parentNode } return H.c.trimLeft(d.join("")).replace(/ +/g, " ").length };
	H.a.Ih = function (b, c, d) { b = [b]; for (var e = 0, f = null; 0 < b.length && e < c;)if (f = b.pop(), !(f.nodeName in H.a.vg)) if (f.nodeType == H.a.ua.$c) { var g = f.nodeValue.replace(/(\r\n|\r|\n)/g, "").replace(/ +/g, " "); e += g.length } else if (f.nodeName in H.a.Yc) e += H.a.Yc[f.nodeName].length; else for (g = f.childNodes.length - 1; 0 <= g; g--)b.push(f.childNodes[g]); H.Da(d) && (d.nw = f ? f.nodeValue.length + c - e - 1 : 0, d.node = f); return f };
	H.a.ef = function (b) { if (b && typeof b.length == u) { if (H.Da(b)) return typeof b.item == t || typeof b.item == y; if (H.Wa(b)) return typeof b.item == t } return !1 }; H.a.Me = function (b, c, d, e) { if (!c && !d) return null; var f = c ? String(c).toUpperCase() : null; return H.a.Le(b, function (g) { return (!f || g.nodeName == f) && (!d || H.L(g.className) && H.g.contains(g.className.split(/\s+/), d)) }, !0, e) }; H.a.sh = function (b, c, d) { return H.a.Me(b, null, c, d) };
	H.a.Le = function (b, c, d, e) { b && !d && (b = b.parentNode); for (d = 0; b && (null == e || d <= e);) { if (c(b)) return b; b = b.parentNode; d++ } return null }; H.a.rh = function (b) { try { var c = b && b.activeElement; return c && c.nodeName ? c : null } catch (d) { return null } }; H.a.Ju = function () { var b = H.a.dc(); return H.ca(b.devicePixelRatio) ? b.devicePixelRatio : b.matchMedia ? H.a.Ed(3) || H.a.Ed(2) || H.a.Ed(1.5) || H.a.Ed(1) || .75 : 1 };
	H.a.Ed = function (b) { return H.a.dc().matchMedia("(min-resolution: " + b + "dppx),(min--moz-device-pixel-ratio: " + b + "),(min-resolution: " + 96 * b + "dpi)").matches ? b : 0 }; H.a.uh = function (b) { return b.getContext("2d") }; H.a.Vb = function (b) { this.ka = b || H.global.document || document }; F = H.a.Vb.prototype; F.Qe = H.a.Qe; F.Zl = D("ka"); F.Re = function (b) { return H.a.Ue(this.ka, b) }; F.jm = function (b) { return H.a.Rh(this.ka, b) }; F.vj = H.a.Vb.prototype.Re; F.getElementsByTagName = function (b, c) { return (c || this.ka).getElementsByTagName(String(b)) };
	F.Ve = function (b, c, d) { return H.a.qd(this.ka, b, c, d) }; F.cm = function (b, c, d) { return H.a.Te(this.ka, b, c, d) }; F.Ah = function (b, c) { return H.a.Ah(b, c || this.ka) }; F.Se = function (b, c) { return H.a.Se(b, c || this.ka) }; F.Qh = function (b, c) { return H.a.Qh(b, c || this.ka) }; F.wj = H.a.Vb.prototype.Ve; F.Wd = H.a.Wd; F.Wh = function (b) { return H.a.Wh(b || this.dc()) }; F.$l = function () { return H.a.Oe(this.dc()) }; F.Ce = function (b, c, d) { return H.a.Yg(this.ka, arguments) }; F.xj = H.a.Vb.prototype.Ce;
	F.createElement = function (b) { return H.a.rb(this.ka, b) }; F.createTextNode = function (b) { return this.ka.createTextNode(String(b)) }; F.zl = function (b, c, d) { return H.a.Zg(this.ka, b, c, !!d) }; F.Ti = function (b) { return H.a.Ui(this.ka, b) }; F.Fm = function () { return H.a.Jc(this.ka) }; F.dc = function () { return H.a.ec(this.ka) }; F.am = function () { return H.a.Pe(this.ka) }; F.yh = function () { return H.a.zh(this.ka) }; F.rh = function (b) { return H.a.rh(b || this.ka) }; F.appendChild = H.a.appendChild; F.append = H.a.append; F.canHaveChildren = H.a.canHaveChildren;
	F.tf = H.a.tf; F.ei = H.a.ei; F.di = H.a.di; F.ci = H.a.ci; F.removeNode = H.a.removeNode; F.Ri = H.a.Ri; F.oh = H.a.oh; F.vh = H.a.vh; F.Bh = H.a.Bh; F.Eh = H.a.Eh; F.Gh = H.a.Gh; F.Nh = H.a.Nh; F.Hh = H.a.Hh; F.Oh = H.a.Oh; F.df = H.a.df; F.bf = H.a.bf; F.zi = H.a.zi; F.Mh = H.a.Mh; F.contains = H.a.contains; F.Sg = H.a.Sg; F.kh = H.a.kh; F.xb = H.a.xb; F.Ch = H.a.Ch; F.Dh = H.a.Dh; F.yf = H.a.yf; F.Lh = H.a.Lh; F.lh = H.a.lh; F.mh = H.a.mh; F.cf = H.a.cf; F.Yi = H.a.Yi; F.ji = H.a.ji; F.ud = H.a.ud; F.Jh = H.a.Jh; F.Kh = H.a.Kh; F.Ih = H.a.Ih; F.ef = H.a.ef; F.Me = H.a.Me; F.sh = H.a.sh; F.Le = H.a.Le;
	F.uh = H.a.uh; H.o = {}; H.o.va = "StopIteration" in H.global ? H.global.StopIteration : { message: "StopIteration", stack: "" }; H.o.Iterator = B(); H.o.Iterator.prototype.next = function () { throw H.o.va; }; H.o.Iterator.prototype.se = function () { return this }; H.o.fa = function (b) { if (b instanceof H.o.Iterator) return b; if (typeof b.se == t) return b.se(!1); if (H.ma(b)) { var c = 0, d = new H.o.Iterator; d.next = function () { for (; ;) { if (c >= b.length) throw H.o.va; if (c in b) return b[c++]; c++ } }; return d } throw Error("Not implemented"); };
	H.o.forEach = function (b, c, d) { if (H.ma(b)) try { H.g.forEach(b, c, d) } catch (e) { if (e !== H.o.va) throw e; } else { b = H.o.fa(b); try { for (; ;)c.call(d, b.next(), void 0, b) } catch (e) { if (e !== H.o.va) throw e; } } }; H.o.filter = function (b, c, d) { var e = H.o.fa(b); b = new H.o.Iterator; b.next = function () { for (; ;) { var f = e.next(); if (c.call(d, f, void 0, e)) return f } }; return b }; H.o.Xt = function (b, c, d) { return H.o.filter(b, H.V.xn(c), d) };
	H.o.Qd = function (b, c, d) { var e = 0, f = b, g = d || 1; 1 < arguments.length && (e = b, f = +c); if (0 == g) throw Error("Range step argument must not be zero"); var h = new H.o.Iterator; h.next = function () { if (0 < g && e >= f || 0 > g && e <= f) throw H.o.va; var k = e; e += g; return k }; return h }; H.o.join = function (b, c) { return H.o.jb(b).join(c) }; H.o.map = function (b, c, d) { var e = H.o.fa(b); b = new H.o.Iterator; b.next = function () { var f = e.next(); return c.call(d, f, void 0, e) }; return b };
	H.o.reduce = function (b, c, d, e) { var f = d; H.o.forEach(b, function (g) { f = c.call(e, f, g) }); return f }; H.o.some = function (b, c, d) { b = H.o.fa(b); try { for (; ;)if (c.call(d, b.next(), void 0, b)) return !0 } catch (e) { if (e !== H.o.va) throw e; } return !1 }; H.o.every = function (b, c, d) { b = H.o.fa(b); try { for (; ;)if (!c.call(d, b.next(), void 0, b)) return !1 } catch (e) { if (e !== H.o.va) throw e; } return !0 }; H.o.gt = function (b) { return H.o.sl(arguments) };
	H.o.sl = function (b) { var c = H.o.fa(b); b = new H.o.Iterator; var d = null; b.next = function () { for (; ;) { if (null == d) { var e = c.next(); d = H.o.fa(e) } try { return d.next() } catch (f) { if (f !== H.o.va) throw f; d = null } } }; return b }; H.o.Nt = function (b, c, d) { var e = H.o.fa(b); b = new H.o.Iterator; var f = !0; b.next = function () { for (; ;) { var g = e.next(); if (!f || !c.call(d, g, void 0, e)) return f = !1, g } }; return b };
	H.o.ux = function (b, c, d) { var e = H.o.fa(b); b = new H.o.Iterator; b.next = function () { var f = e.next(); if (c.call(d, f, void 0, e)) return f; throw H.o.va; }; return b }; H.o.jb = function (b) { if (H.ma(b)) return H.g.jb(b); b = H.o.fa(b); var c = []; H.o.forEach(b, function (d) { c.push(d) }); return c }; H.o.Ob = function (b, c) { b = H.o.To({}, b, c); var d = H.g.eh; return H.o.every(b, function (e) { return d(e[0], e[1]) }) }; H.o.wn = function (b) { try { H.o.fa(b).next() } catch (c) { if (c != H.o.va) throw c; } };
	H.o.product = function (b) { if (H.g.some(arguments, function (f) { return !f.length }) || !arguments.length) return new H.o.Iterator; var c = new H.o.Iterator, d = arguments, e = H.g.repeat(0, d.length); c.next = function () { if (e) { for (var f = H.g.map(e, function (h, k) { return d[k][h] }), g = e.length - 1; 0 <= g; g--) { if (e[g] < d[g].length - 1) { e[g]++; break } if (0 == g) { e = null; break } e[g] = 0 } return f } throw H.o.va; }; return c };
	H.o.Ht = function (b) { var c = H.o.fa(b), d = [], e = 0; b = new H.o.Iterator; var f = !1; b.next = function () { var g = null; if (!f) try { return g = c.next(), d.push(g), g } catch (h) { if (h != H.o.va || H.g.Ca(d)) throw h; f = !0 } g = d[e]; e = (e + 1) % d.length; return g }; return b }; H.o.count = function (b, c) { var d = b || 0, e = H.ca(c) ? c : 1; b = new H.o.Iterator; b.next = function () { var f = d; d += e; return f }; return b }; H.o.repeat = function (b) { var c = new H.o.Iterator; c.next = H.V.Vg(b); return c };
	H.o.zs = function (b) { var c = H.o.fa(b), d = 0; b = new H.o.Iterator; b.next = function () { return d += c.next() }; return b }; H.o.uj = function (b) { var c = arguments, d = new H.o.Iterator; if (0 < c.length) { var e = H.g.map(c, H.o.fa); d.next = function () { return H.g.map(e, function (f) { return f.next() }) } } return d };
	H.o.To = function (b, c) { var d = H.g.slice(arguments, 1), e = new H.o.Iterator; if (0 < d.length) { var f = H.g.map(d, H.o.fa); e.next = function () { var g = !1, h = H.g.map(f, function (k) { try { var m = k.next(); g = !0 } catch (n) { if (n !== H.o.va) throw n; m = b } return m }); if (!g) throw H.o.va; return h } } return e }; H.o.pt = function (b, c) { var d = H.o.fa(c); return H.o.filter(b, function () { return !!d.next() }) }; H.o.ke = function (b, c) { this.iterator = H.o.fa(b); this.Di = c || H.V.ai }; H.yb(H.o.ke, H.o.Iterator);
	H.o.ke.prototype.next = function () { for (; this.yc == this.hj;)this.kd = this.iterator.next(), this.yc = this.Di(this.kd); for (var b = this.hj = this.yc, c = this.hj, d = []; this.yc == c;) { d.push(this.kd); try { this.kd = this.iterator.next() } catch (e) { if (e !== H.o.va) throw e; break } this.yc = this.Di(this.kd) } return [b, d] }; H.o.Ru = function (b, c) { return new H.o.ke(b, c) }; H.o.px = function (b, c, d) { var e = H.o.fa(b); b = new H.o.Iterator; b.next = function () { var f = H.o.jb(e.next()); return c.apply(d, H.g.concat(f, void 0, e)) }; return b };
	H.o.tee = function (b, c) { function d() { var g = e.next(); H.g.forEach(f, function (h) { h.push(g) }) } var e = H.o.fa(b), f = H.g.map(H.g.Qd(H.Bb(c) ? c : 2), function () { return [] }); return H.g.map(f, function (g) { var h = new H.o.Iterator; h.next = function () { H.g.Ca(g) && d(); return g.shift() }; return h }) }; H.o.Tt = function (b, c) { return H.o.uj(H.o.count(c), b) }; H.o.an = function (b, c) { var d = H.o.fa(b); b = new H.o.Iterator; var e = c; b.next = function () { if (0 < e--) return d.next(); throw H.o.va; }; return b };
	H.o.vl = function (b, c) { for (b = H.o.fa(b); 0 < c--;)H.o.wn(b); return b }; H.o.slice = function (b, c, d) { b = H.o.vl(b, c); H.Bb(d) && (b = H.o.an(b, d - c)); return b }; H.o.wm = function (b) { var c = []; H.g.Jn(b, c); return b.length != c.length }; H.o.An = function (b, c) { b = H.o.jb(b); c = H.g.repeat(b, H.Bb(c) ? c : b.length); c = H.o.product.apply(void 0, c); return H.o.filter(c, function (d) { return !H.o.wm(d) }) };
	H.o.lt = function (b, c) { function d(g) { return e[g] } var e = H.o.jb(b); b = H.o.Qd(e.length); c = H.o.An(b, c); var f = H.o.filter(c, function (g) { return H.g.wi(g) }); c = new H.o.Iterator; c.next = function () { return H.g.map(f.next(), d) }; return c }; H.o.mt = function (b, c) { function d(g) { return e[g] } var e = H.o.jb(b); b = H.g.Qd(e.length); c = H.g.repeat(b, c); c = H.o.product.apply(void 0, c); var f = H.o.filter(c, function (g) { return H.g.wi(g) }); c = new H.o.Iterator; c.next = function () { return H.g.map(f.next(), d) }; return c }; H.Pd = {}; H.Pd.Fr = B(); H.Thenable = B(); H.Thenable.prototype.then = B(); H.Thenable.hg = "$goog_Thenable"; H.Thenable.Cg = function (b) { b.prototype[H.Thenable.hg] = !0 }; H.Thenable.ki = function (b) { if (!b) return !1; try { return !!b[H.Thenable.hg] } catch (c) { return !1 } }; H.Promise = function (b, c) { this.qa = H.Promise.ba.$a; this.Ia = void 0; this.Zb = this.qb = this.ya = null; this.He = !1; 0 < H.Promise.Hb ? this.Yd = 0 : 0 == H.Promise.Hb && (this.wd = !1); H.Promise.eb && (this.Cf = [], I(this, Error("created")), this.bh = 0); if (b != H.Rb) try { var d = this; b.call(c, function (e) { J(d, H.Promise.ba.nb, e) }, function (e) { if (H.sa && !(e instanceof H.Promise.Ub)) try { if (e instanceof Error) throw e; throw Error("Promise rejected."); } catch (f) { } J(d, H.Promise.ba.Ja, e) }) } catch (e) { J(this, H.Promise.ba.Ja, e) } }; H.Promise.eb = !1;
	H.Promise.Hb = 0; H.Promise.ba = { $a: 0, Ij: 1, nb: 2, Ja: 3 }; H.Promise.Zf = function () { this.next = this.context = this.hc = this.Oc = this.Jb = null; this.bd = !1 }; H.Promise.Zf.prototype.reset = function () { this.context = this.hc = this.Oc = this.Jb = null; this.bd = !1 }; H.Promise.ge = 100; H.Promise.Ec = new H.async.Wc(function () { return new H.Promise.Zf }, function (b) { b.reset() }, H.Promise.ge); H.Promise.th = function (b, c, d) { var e = H.Promise.Ec.get(); e.Oc = b; e.hc = c; e.context = d; return e }; H.Promise.Rn = function (b) { H.Promise.Ec.put(b) };
	H.Promise.resolve = function (b) { if (b instanceof H.Promise) return b; var c = new H.Promise(H.Rb); J(c, H.Promise.ba.nb, b); return c }; H.Promise.reject = function (b) { return new H.Promise(function (c, d) { d(b) }) }; H.Promise.Rd = function (b, c, d) { H.Promise.Ki(b, c, d, null) || H.async.X(H.Sb(c, b)) }; H.Promise.race = function (b) { return new H.Promise(function (c, d) { b.length || c(void 0); for (var e = 0, f; e < b.length; e++)f = b[e], H.Promise.Rd(f, c, d) }) };
	H.Promise.all = function (b) { return new H.Promise(function (c, d) { var e = b.length, f = []; if (e) for (var g = function (n, r) { e--; f[n] = r; 0 == e && c(f) }, h = function (n) { d(n) }, k = 0, m; k < b.length; k++)m = b[k], H.Promise.Rd(m, H.Sb(g, k), h); else c(f) }) }; H.Promise.Cs = function (b) { return new H.Promise(function (c) { var d = b.length, e = []; if (d) for (var f = function (k, m, n) { d--; e[k] = m ? { Xl: !0, value: n } : { Xl: !1, reason: n }; 0 == d && c(e) }, g = 0, h; g < b.length; g++)h = b[g], H.Promise.Rd(h, H.Sb(f, g, !0), H.Sb(f, g, !1)); else c(e) }) };
	H.Promise.cu = function (b) { return new H.Promise(function (c, d) { var e = b.length, f = []; if (e) for (var g = function (n) { c(n) }, h = function (n, r) { e--; f[n] = r; 0 == e && d(f) }, k = 0, m; k < b.length; k++)m = b[k], H.Promise.Rd(m, g, H.Sb(h, k)); else c(void 0) }) }; H.Promise.Ix = function () { var b, c, d = new H.Promise(function (e, f) { b = e; c = f }); return new H.Promise.rk(d, b, c) }; H.Promise.prototype.then = function (b, c, d) { H.Promise.eb && I(this, Error("then")); return ha(this, H.Wa(b) ? b : null, H.Wa(c) ? c : null, d) }; H.Thenable.Cg(H.Promise);
	H.Promise.prototype.cancel = function (b) { this.qa == H.Promise.ba.$a && H.async.X(function () { var c = new H.Promise.Ub(b); ia(this, c) }, this) }; function ia(b, c) { if (b.qa == H.Promise.ba.$a) if (b.ya) { var d = b.ya; if (d.qb) { for (var e = 0, f = null, g = null, h = d.qb; h && (h.bd || (e++, h.Jb == b && (f = h), !(f && 1 < e))); h = h.next)f || (g = h); f && (d.qa == H.Promise.ba.$a && 1 == e ? ia(d, c) : (g ? (e = g, e.next == d.Zb && (d.Zb = e), e.next = e.next.next) : ja(d), ka(d, f, H.Promise.ba.Ja, c))) } b.ya = null } else J(b, H.Promise.ba.Ja, c) }
	function la(b, c) { b.qb || b.qa != H.Promise.ba.nb && b.qa != H.Promise.ba.Ja || ma(b); b.Zb ? b.Zb.next = c : b.qb = c; b.Zb = c } function ha(b, c, d, e) { var f = H.Promise.th(null, null, null); f.Jb = new H.Promise(function (g, h) { f.Oc = c ? function (k) { try { var m = c.call(e, k); g(m) } catch (n) { h(n) } } : g; f.hc = d ? function (k) { try { var m = d.call(e, k); !H.ca(m) && k instanceof H.Promise.Ub ? h(k) : g(m) } catch (n) { h(n) } } : h }); f.Jb.ya = b; la(b, f); return f.Jb } H.Promise.prototype.Fo = function (b) { this.qa = H.Promise.ba.$a; J(this, H.Promise.ba.nb, b) };
	H.Promise.prototype.Go = function (b) { this.qa = H.Promise.ba.$a; J(this, H.Promise.ba.Ja, b) }; function J(b, c, d) { b.qa == H.Promise.ba.$a && (b === d && (c = H.Promise.ba.Ja, d = new TypeError("Promise cannot resolve to itself")), b.qa = H.Promise.ba.Ij, H.Promise.Ki(d, b.Fo, b.Go, b) || (b.Ia = d, b.qa = c, b.ya = null, ma(b), c != H.Promise.ba.Ja || d instanceof H.Promise.Ub || H.Promise.Ok(b, d))) }
	H.Promise.Ki = function (b, c, d, e) { if (b instanceof H.Promise) return H.Promise.eb && I(b, Error("then")), la(b, H.Promise.th(c || H.Rb, d || null, e)), !0; if (H.Thenable.ki(b)) return b.then(c, d, e), !0; if (H.Da(b)) try { var f = b.then; if (H.Wa(f)) return H.Promise.Do(b, f, c, d, e), !0 } catch (g) { return d.call(e, g), !0 } return !1 }; H.Promise.Do = function (b, c, d, e, f) { function g(m) { k || (k = !0, e.call(f, m)) } function h(m) { k || (k = !0, d.call(f, m)) } var k = !1; try { c.call(b, h, g) } catch (m) { g(m) } }; function ma(b) { b.He || (b.He = !0, H.async.X(b.Pl, b)) }
	function ja(b) { var c = null; b.qb && (c = b.qb, b.qb = c.next, c.next = null); b.qb || (b.Zb = null); return c } H.Promise.prototype.Pl = function () { for (var b; b = ja(this);)H.Promise.eb && this.bh++, ka(this, b, this.qa, this.Ia); this.He = !1 };
	function ka(b, c, d, e) { if (d == H.Promise.ba.Ja && c.hc && !c.bd) if (0 < H.Promise.Hb) for (; b && b.Yd; b = b.ya)H.global.clearTimeout(b.Yd), b.Yd = 0; else if (0 == H.Promise.Hb) for (; b && b.wd; b = b.ya)b.wd = !1; if (c.Jb) c.Jb.ya = null, H.Promise.fi(c, d, e); else try { c.bd ? c.Oc.call(c.context) : H.Promise.fi(c, d, e) } catch (f) { H.Promise.xd.call(null, f) } H.Promise.Rn(c) } H.Promise.fi = function (b, c, d) { c == H.Promise.ba.nb ? b.Oc.call(b.context, d) : b.hc && b.hc.call(b.context, d) };
	function I(b, c) { if (H.Promise.eb && H.L(c.stack)) { var d = c.stack.split("\n", 4)[3]; c = c.message; c += Array(11 - c.length).join(" "); b.Cf.push(c + d) } } function na(b, c) { if (H.Promise.eb && c && H.L(c.stack) && b.Cf.length) { for (var d = ["Promise trace:"], e = b; e; e = e.ya) { for (var f = b.bh; 0 <= f; f--)d.push(e.Cf[f]); d.push("Value: [" + (e.qa == H.Promise.ba.Ja ? "REJECTED" : "FULFILLED") + "] <" + String(e.Ia) + ">") } c.stack += "\n\n" + d.join("\n") } }
	H.Promise.Ok = function (b, c) { 0 < H.Promise.Hb ? b.Yd = H.global.setTimeout(function () { na(b, c); H.Promise.xd.call(null, c) }, H.Promise.Hb) : 0 == H.Promise.Hb && (b.wd = !0, H.async.X(function () { b.wd && (na(b, c), H.Promise.xd.call(null, c)) })) }; H.Promise.xd = H.async.jj; H.Promise.ex = function (b) { H.Promise.xd = b }; H.Promise.Ub = function (b) { H.debug.Error.call(this, b) }; H.yb(H.Promise.Ub, H.debug.Error); H.Promise.Ub.prototype.name = "cancel"; H.Promise.rk = function (b, c, d) { this.Pd = b; this.resolve = c; this.reject = d };/*
 Portions of this code are from MochiKit, received by
 The Closure Authors under the MIT license. All other code is Copyright
 2005-2009 The Closure Authors. All Rights Reserved.
*/
	H.async.I = function (b, c) { this.Vd = []; this.Oi = b; this.fh = c || null; this.fc = this.$b = !1; this.Ia = void 0; this.zf = this.kl = this.xe = !1; this.Xd = 0; this.ya = null; this.cd = 0; H.async.I.eb && (this.Be = null, Error.captureStackTrace && (b = { stack: "" }, Error.captureStackTrace(b, H.async.I), typeof b.stack == y && (this.Be = b.stack.replace(/^[^\n]*\n/, "")))) }; H.async.I.Bk = !1; H.async.I.eb = !1; F = H.async.I.prototype;
	F.cancel = function (b) { if (this.$b) this.Ia instanceof H.async.I && this.Ia.cancel(); else { if (this.ya) { var c = this.ya; delete this.ya; b ? c.cancel(b) : (c.cd--, 0 >= c.cd && c.cancel()) } this.Oi ? this.Oi.call(this.fh, this) : this.zf = !0; this.$b || this.tb(new H.async.I.Tb(this)) } }; F.Xg = function (b, c) { this.xe = !1; K(this, b, c) }; function K(b, c, d) { b.$b = !0; b.Ia = d; b.fc = !c; oa(b) } function pa(b) { if (b.$b) { if (!b.zf) throw new H.async.I.Tc(b); b.zf = !1 } } F.tc = function (b) { pa(this); K(this, !0, b) };
	F.tb = function (b) { pa(this); qa(this, b); K(this, !1, b) }; function qa(b, c) { H.async.I.eb && b.Be && H.Da(c) && c.stack && /^[^\n]+(\n   [^\n]+)+/.test(c.stack) && (c.stack = c.stack + "\nDEFERRED OPERATION:\n" + b.Be) } function L(b, c, d) { return M(b, c, null, d) } function ra(b, c) { M(b, null, c, void 0) } function M(b, c, d, e) { b.Vd.push([c, d, e]); b.$b && oa(b); return b } F.then = function (b, c, d) { var e, f, g = new H.Promise(function (h, k) { e = h; f = k }); M(this, e, function (h) { h instanceof H.async.I.Tb ? g.cancel() : f(h) }); return g.then(b, c, d) }; H.Thenable.Cg(H.async.I);
	H.async.I.prototype.ml = function () { var b = new H.async.I; M(this, b.tc, b.tb, b); b.ya = this; this.cd++; return b }; function sa(b) { return H.g.some(b.Vd, function (c) { return H.Wa(c[1]) }) }
	function oa(b) {
		b.Xd && b.$b && sa(b) && (H.async.I.Lo(b.Xd), b.Xd = 0); b.ya && (b.ya.cd--, delete b.ya); for (var c = b.Ia, d = !1, e = !1; b.Vd.length && !b.xe;) { var f = b.Vd.shift(), g = f[0], h = f[1]; f = f[2]; if (g = b.fc ? h : g) try { var k = g.call(f || b.fh, c); H.ca(k) && (b.fc = b.fc && (k == c || k instanceof Error), b.Ia = c = k); if (H.Thenable.ki(c) || typeof H.global.Promise === t && c instanceof H.global.Promise) e = !0, b.xe = !0 } catch (m) { c = m, b.fc = !0, qa(b, c), sa(b) || (d = !0) } } b.Ia = c; e ? (e = H.bind(b.Xg, b, !0), k = H.bind(b.Xg, b, !1), c instanceof H.async.I ? (M(c, e, k), c.kl =
			!0) : c.then(e, k)) : H.async.I.Bk && c instanceof Error && !(c instanceof H.async.I.Tb) && (d = b.fc = !0); d && (b.Xd = H.async.I.eo(c))
	} H.async.I.fj = function (b) { var c = new H.async.I; c.tc(b); return c }; H.async.I.lu = function (b) { var c = new H.async.I; b.then(function (d) { c.tc(d) }, function (d) { c.tb(d) }); return c }; H.async.I.xa = function (b) { var c = new H.async.I; c.tb(b); return c }; H.async.I.et = function () { var b = new H.async.I; b.cancel(); return b };
	H.async.I.Hx = function (b, c, d) { return b instanceof H.async.I ? L(b.ml(), c, d) : L(H.async.I.fj(b), c, d) }; H.async.I.Tc = function () { H.debug.Error.call(this) }; H.yb(H.async.I.Tc, H.debug.Error); H.async.I.Tc.prototype.message = "Deferred has already fired"; H.async.I.Tc.prototype.name = "AlreadyCalledError"; H.async.I.Tb = function () { H.debug.Error.call(this) }; H.yb(H.async.I.Tb, H.debug.Error); H.async.I.Tb.prototype.message = "Deferred was canceled"; H.async.I.Tb.prototype.name = "CanceledError";
	H.async.I.cg = function (b) { this.Ic = H.global.setTimeout(H.bind(this.ij, this), 0); this.Nl = b }; H.async.I.cg.prototype.ij = function () { delete H.async.I.Cc[this.Ic]; throw this.Nl; }; H.async.I.Cc = {}; H.async.I.eo = function (b) { b = new H.async.I.cg(b); H.async.I.Cc[b.Ic] = b; return b.Ic }; H.async.I.Lo = function (b) { var c = H.async.I.Cc[b]; c && (H.global.clearTimeout(c.Ic), delete H.async.I.Cc[b]) }; H.async.I.Ss = function () { var b = H.async.I.Cc, c; for (c in b) { var d = b[c]; H.global.clearTimeout(d.Ic); d.ij() } }; H.N = {}; H.N.P = {}; H.N.P.ie = "closure_verification"; H.N.P.Tj = 5E3; H.N.P.vf = []; H.N.P.Zn = function (b, c) { function d() { var f = b.shift(); f = H.N.P.Sd(f, c); b.length && M(f, d, d, void 0); return f } if (!b.length) return H.async.I.fj(null); var e = H.N.P.vf.length; H.g.extend(H.N.P.vf, b); if (e) return H.N.P.Wi; b = H.N.P.vf; H.N.P.Wi = d(); return H.N.P.Wi };
	H.N.P.Sd = function (b, c) {
		var d = c || {}; c = d.document || document; var e = H.b.H.K(b), f = H.a.createElement(l), g = { Xi: f, lj: void 0 }, h = new H.async.I(H.N.P.ql, g), k = null, m = H.zb(d.timeout) ? d.timeout : H.N.P.Tj; 0 < m && (k = window.setTimeout(function () { H.N.P.gd(f, !0); h.tb(new H.N.P.Error(H.N.P.Vc.TIMEOUT, "Timeout reached for loading script " + e)) }, m), g.lj = k); f.onload = f.onreadystatechange = function () { f.readyState && "loaded" != f.readyState && "complete" != f.readyState || (H.N.P.gd(f, d.it || !1, k), h.tc(null)) }; f.onerror = function () {
			H.N.P.gd(f,
				!0, k); h.tb(new H.N.P.Error(H.N.P.Vc.fk, "Error while loading script " + e))
		}; g = d.attributes || {}; H.object.extend(g, { type: da, charset: "UTF-8" }); H.a.Wd(f, g); H.a.O.mo(f, b); H.N.P.lm(c).appendChild(f); return h
	};
	H.N.P.Bw = function (b, c, d) { H.global[H.N.P.ie] || (H.global[H.N.P.ie] = {}); var e = H.global[H.N.P.ie], f = H.b.H.K(b); if (H.ca(e[c])) return H.async.I.xa(new H.N.P.Error(H.N.P.Vc.Mk, "Verification object " + c + " already defined.")); b = H.N.P.Sd(b, d); var g = new H.async.I(H.bind(b.cancel, b)); L(b, function () { var h = e[c]; H.ca(h) ? (g.tc(h), delete e[c]) : g.tb(new H.N.P.Error(H.N.P.Vc.Lk, "Script " + f + " loaded, but verification object " + c + " was not defined.")) }); ra(b, function (h) { H.ca(e[c]) && delete e[c]; g.tb(h) }); return g };
	H.N.P.lm = function (b) { var c = H.a.getElementsByTagName("HEAD", b); return !c || H.g.Ca(c) ? b.documentElement : c[0] }; H.N.P.ql = function () { if (this && this.Xi) { var b = this.Xi; b && b.tagName == l && H.N.P.gd(b, !0, this.lj) } }; H.N.P.gd = function (b, c, d) { H.zb(d) && H.global.clearTimeout(d); b.onload = H.Rb; b.onerror = H.Rb; b.onreadystatechange = H.Rb; c && window.setTimeout(function () { H.a.removeNode(b) }, 0) }; H.N.P.Vc = { fk: 0, TIMEOUT: 1, Lk: 2, Mk: 3 };
	H.N.P.Error = function (b, c) { var d = "Jsloader error (code #" + b + ")"; c && (d += ": " + c); H.debug.Error.call(this, d); this.code = b }; H.yb(H.N.P.Error, H.debug.Error); H.R = {}; H.R.Map = function (b, c) { this.Ea = {}; this.$ = []; this.Sc = this.Z = 0; var d = arguments.length; if (1 < d) { if (d % 2) throw Error(p); for (var e = 0; e < d; e += 2)this.set(arguments[e], arguments[e + 1]) } else b && this.addAll(b) }; F = H.R.Map.prototype; F.ub = D("Z"); F.ga = function () { N(this); for (var b = [], c = 0; c < this.$.length; c++)b.push(this.Ea[this.$[c]]); return b }; F.la = function () { N(this); return this.$.concat() }; F.Lb = function (b) { return H.R.Map.Pb(this.Ea, b) };
	F.Mb = function (b) { for (var c = 0; c < this.$.length; c++) { var d = this.$[c]; if (H.R.Map.Pb(this.Ea, d) && this.Ea[d] == b) return !0 } return !1 }; F.Ob = function (b, c) { if (this === b) return !0; if (this.Z != b.ub()) return !1; c = c || H.R.Map.Dl; N(this); for (var d, e = 0; d = this.$[e]; e++)if (!c(this.get(d), b.get(d))) return !1; return !0 }; H.R.Map.Dl = function (b, c) { return b === c }; F = H.R.Map.prototype; F.Ca = function () { return 0 == this.Z }; F.clear = function () { this.Ea = {}; this.Sc = this.Z = this.$.length = 0 };
	F.remove = function (b) { return H.R.Map.Pb(this.Ea, b) ? (delete this.Ea[b], this.Z--, this.Sc++, this.$.length > 2 * this.Z && N(this), !0) : !1 }; function N(b) { if (b.Z != b.$.length) { for (var c = 0, d = 0; c < b.$.length;) { var e = b.$[c]; H.R.Map.Pb(b.Ea, e) && (b.$[d++] = e); c++ } b.$.length = d } if (b.Z != b.$.length) { var f = {}; for (d = c = 0; c < b.$.length;)e = b.$[c], H.R.Map.Pb(f, e) || (b.$[d++] = e, f[e] = 1), c++; b.$.length = d } } F.get = function (b, c) { return H.R.Map.Pb(this.Ea, b) ? this.Ea[b] : c };
	F.set = function (b, c) { H.R.Map.Pb(this.Ea, b) || (this.Z++, this.$.push(b), this.Sc++); this.Ea[b] = c }; F.addAll = function (b) { if (b instanceof H.R.Map) for (var c = b.la(), d = 0; d < c.length; d++)this.set(c[d], b.get(c[d])); else for (c in b) this.set(c, b[c]) }; F.forEach = function (b, c) { for (var d = this.la(), e = 0; e < d.length; e++) { var f = d[e], g = this.get(f); b.call(c, g, f, this) } }; F.clone = function () { return new H.R.Map(this) }; F.Co = function () { for (var b = new H.R.Map, c = 0; c < this.$.length; c++) { var d = this.$[c]; b.set(this.Ea[d], d) } return b };
	F.Ao = function () { N(this); for (var b = {}, c = 0; c < this.$.length; c++) { var d = this.$[c]; b[d] = this.Ea[d] } return b }; F.se = function (b) { N(this); var c = 0, d = this.Sc, e = this, f = new H.o.Iterator; f.next = function () { if (d != e.Sc) throw Error("The map has changed since the iterator was created"); if (c >= e.$.length) throw H.o.va; var g = e.$[c++]; return b ? g : e.Ea[g] }; return f }; H.R.Map.Pb = function (b, c) { return Object.prototype.hasOwnProperty.call(b, c) }; H.R.ub = function (b) { return b.ub && typeof b.ub == t ? b.ub() : H.ma(b) || H.L(b) ? b.length : H.object.ub(b) }; H.R.ga = function (b) { if (b.ga && typeof b.ga == t) return b.ga(); if (H.L(b)) return b.split(""); if (H.ma(b)) { for (var c = [], d = b.length, e = 0; e < d; e++)c.push(b[e]); return c } return H.object.ga(b) }; H.R.la = function (b) { if (b.la && typeof b.la == t) return b.la(); if (!b.ga || typeof b.ga != t) { if (H.ma(b) || H.L(b)) { var c = []; b = b.length; for (var d = 0; d < b; d++)c.push(d); return c } return H.object.la(b) } };
	H.R.contains = function (b, c) { return b.contains && typeof b.contains == t ? b.contains(c) : b.Mb && typeof b.Mb == t ? b.Mb(c) : H.ma(b) || H.L(b) ? H.g.contains(b, c) : H.object.Mb(b, c) }; H.R.Ca = function (b) { return b.Ca && typeof b.Ca == t ? b.Ca() : H.ma(b) || H.L(b) ? H.g.Ca(b) : H.object.Ca(b) }; H.R.clear = function (b) { b.clear && typeof b.clear == t ? b.clear() : H.ma(b) ? H.g.clear(b) : H.object.clear(b) };
	H.R.forEach = function (b, c, d) { if (b.forEach && typeof b.forEach == t) b.forEach(c, d); else if (H.ma(b) || H.L(b)) H.g.forEach(b, c, d); else for (var e = H.R.la(b), f = H.R.ga(b), g = f.length, h = 0; h < g; h++)c.call(d, f[h], e && e[h], b) }; H.R.filter = function (b, c, d) { if (typeof b.filter == t) return b.filter(c, d); if (H.ma(b) || H.L(b)) return H.g.filter(b, c, d); var e = H.R.la(b), f = H.R.ga(b), g = f.length; if (e) { var h = {}; for (var k = 0; k < g; k++)c.call(d, f[k], e[k], b) && (h[e[k]] = f[k]) } else for (h = [], k = 0; k < g; k++)c.call(d, f[k], void 0, b) && h.push(f[k]); return h };
	H.R.map = function (b, c, d) { if (typeof b.map == t) return b.map(c, d); if (H.ma(b) || H.L(b)) return H.g.map(b, c, d); var e = H.R.la(b), f = H.R.ga(b), g = f.length; if (e) { var h = {}; for (var k = 0; k < g; k++)h[e[k]] = c.call(d, f[k], e[k], b) } else for (h = [], k = 0; k < g; k++)h[k] = c.call(d, f[k], void 0, b); return h }; H.R.some = function (b, c, d) { if (typeof b.some == t) return b.some(c, d); if (H.ma(b) || H.L(b)) return H.g.some(b, c, d); for (var e = H.R.la(b), f = H.R.ga(b), g = f.length, h = 0; h < g; h++)if (c.call(d, f[h], e && e[h], b)) return !0; return !1 };
	H.R.every = function (b, c, d) { if (typeof b.every == t) return b.every(c, d); if (H.ma(b) || H.L(b)) return H.g.every(b, c, d); for (var e = H.R.la(b), f = H.R.ga(b), g = f.length, h = 0; h < g; h++)if (!c.call(d, f[h], e && e[h], b)) return !1; return !0 }; H.uri = {}; H.uri.l = {}; H.uri.l.qc = { Kf: 38, EQUAL: 61, ck: 35, mk: 63 }; H.uri.l.ye = function (b, c, d, e, f, g, h) { var k = ""; b && (k += b + ":"); d && (k += "//", c && (k += c + "@"), k += d, e && (k += ":" + e)); f && (k += f); g && (k += "?" + g); h && (k += "#" + h); return k }; H.uri.l.ro = /^(?:([^:/?#.]+):)?(?:\/\/(?:([^/?#]*)@)?([^/#?]*?)(?::([0-9]+))?(?=[/#?]|$))?([^?#]+)?(?:\?([^#]*))?(?:#([\s\S]*))?$/; H.uri.l.U = { Xb: 1, qe: 2, lb: 3, ob: 4, le: 5, me: 6, dg: 7 }; H.uri.l.split = function (b) { return b.match(H.uri.l.ro) };
	H.uri.l.ld = function (b, c) { return b ? c ? decodeURI(b) : decodeURIComponent(b) : b }; H.uri.l.ac = function (b, c) { return H.uri.l.split(c)[b] || null }; H.uri.l.Gc = function (b) { return H.uri.l.ac(H.uri.l.U.Xb, b) }; H.uri.l.xu = function (b) { b = H.uri.l.Gc(b); !b && H.global.self && H.global.self.location && (b = H.global.self.location.protocol, b = b.substr(0, b.length - 1)); return b ? b.toLowerCase() : "" }; H.uri.l.qm = function () { return H.uri.l.ac(H.uri.l.U.qe, void 0) }; H.uri.l.vd = function () { return H.uri.l.ld(H.uri.l.qm()) };
	H.uri.l.bm = function () { return H.uri.l.ac(H.uri.l.U.lb, void 0) }; H.uri.l.pd = function () { return H.uri.l.ld(H.uri.l.bm(), !0) }; H.uri.l.td = function () { return Number(H.uri.l.ac(H.uri.l.U.ob, void 0)) || null }; H.uri.l.im = function () { return H.uri.l.ac(H.uri.l.U.le, void 0) }; H.uri.l.bc = function () { return H.uri.l.ld(H.uri.l.im(), !0) }; H.uri.l.Xe = function () { return H.uri.l.ac(H.uri.l.U.me, void 0) }; H.uri.l.em = function () { var b = (void 0).indexOf("#"); return 0 > b ? null : (void 0).substr(b + 1) };
	H.uri.l.Rw = function (b, c) { return H.uri.l.Kn(b) + (c ? "#" + c : "") }; H.uri.l.rd = function () { return H.uri.l.ld(H.uri.l.em()) }; H.uri.l.zu = function (b) { b = H.uri.l.split(b); return H.uri.l.ye(b[H.uri.l.U.Xb], b[H.uri.l.U.qe], b[H.uri.l.U.lb], b[H.uri.l.U.ob]) }; H.uri.l.Eu = function (b) { b = H.uri.l.split(b); return H.uri.l.ye(b[H.uri.l.U.Xb], null, b[H.uri.l.U.lb], b[H.uri.l.U.ob]) }; H.uri.l.Iu = function (b) { b = H.uri.l.split(b); return H.uri.l.ye(null, null, null, null, b[H.uri.l.U.le], b[H.uri.l.U.me], b[H.uri.l.U.dg]) };
	H.uri.l.Kn = function (b) { var c = b.indexOf("#"); return 0 > c ? b : b.substr(0, c) }; H.uri.l.zm = function (b, c) { b = H.uri.l.split(b); c = H.uri.l.split(c); return b[H.uri.l.U.lb] == c[H.uri.l.U.lb] && b[H.uri.l.U.Xb] == c[H.uri.l.U.Xb] && b[H.uri.l.U.ob] == c[H.uri.l.U.ob] }; H.uri.l.Ts = B(); H.uri.l.yn = function (b, c) { if (b) { b = b.split("&"); for (var d = 0; d < b.length; d++) { var e = b[d].indexOf("="), f = null; if (0 <= e) { var g = b[d].substring(0, e); f = b[d].substring(e + 1) } else g = b[d]; c(g, f ? H.c.$d(f) : "") } } };
	H.uri.l.bj = function (b) { var c = b.indexOf("#"); 0 > c && (c = b.length); var d = b.indexOf("?"); if (0 > d || d > c) { d = c; var e = "" } else e = b.substring(d + 1, c); return [b.substr(0, d), e, b.substr(c)] }; H.uri.l.Ci = function (b) { return b[0] + (b[1] ? "?" + b[1] : "") + b[2] }; H.uri.l.Dg = function (b, c) { return c ? b ? b + "&" + c : c : b }; H.uri.l.ue = function (b, c) { if (!c) return b; b = H.uri.l.bj(b); b[1] = H.uri.l.Dg(b[1], c); return H.uri.l.Ci(b) };
	H.uri.l.te = function (b, c, d) { if (H.isArray(c)) for (var e = 0; e < c.length; e++)H.uri.l.te(b, String(c[e]), d); else null != c && d.push(b + ("" === c ? "" : "=" + H.c.Rc(c))) }; H.uri.l.Mg = function (b, c) { var d = []; for (c = c || 0; c < b.length; c += 2)H.uri.l.te(b[c], b[c + 1], d); return d.join("&") }; H.uri.l.Ng = function (b) { var c = [], d; for (d in b) H.uri.l.te(d, b[d], c); return c.join("&") }; H.uri.l.Gs = function (b, c) { var d = 2 == arguments.length ? H.uri.l.Mg(arguments[1], 0) : H.uri.l.Mg(arguments, 1); return H.uri.l.ue(b, d) };
	H.uri.l.Hs = function (b, c) { c = H.uri.l.Ng(c); return H.uri.l.ue(b, c) }; H.uri.l.Pk = function (b, c, d) { d = H.zb(d) ? "=" + H.c.Rc(d) : ""; return H.uri.l.ue(b, c + d) }; H.uri.l.od = function (b, c, d, e) { for (var f = d.length; 0 <= (c = b.indexOf(d, c)) && c < e;) { var g = b.charCodeAt(c - 1); if (g == H.uri.l.qc.Kf || g == H.uri.l.qc.mk) if (g = b.charCodeAt(c + f), !g || g == H.uri.l.qc.EQUAL || g == H.uri.l.qc.Kf || g == H.uri.l.qc.ck) return c; c += f + 1 } return -1 }; H.uri.l.yd = /#|$/; H.uri.l.Tu = function (b, c) { return 0 <= H.uri.l.od(b, 0, c, b.search(H.uri.l.yd)) };
	H.uri.l.Gu = function (b, c) { var d = b.search(H.uri.l.yd), e = H.uri.l.od(b, 0, c, d); if (0 > e) return null; var f = b.indexOf("&", e); if (0 > f || f > d) f = d; e += c.length + 1; return H.c.$d(b.substr(e, f - e)) }; H.uri.l.Hu = function (b, c) { for (var d = b.search(H.uri.l.yd), e = 0, f, g = []; 0 <= (f = H.uri.l.od(b, e, c, d));) { e = b.indexOf("&", f); if (0 > e || e > d) e = d; f += c.length + 1; g.push(H.c.$d(b.substr(f, e - f))) } return g }; H.uri.l.Bo = /[?&]($|#)/;
	H.uri.l.Ln = function (b, c) { for (var d = b.search(H.uri.l.yd), e = 0, f, g = []; 0 <= (f = H.uri.l.od(b, e, c, d));)g.push(b.substring(e, f)), e = Math.min(b.indexOf("&", f) + 1 || d, d); g.push(b.substr(e)); return g.join("").replace(H.uri.l.Bo, "$1") }; H.uri.l.ko = function (b) { var c = H.uri.l.ug.sg, d = H.c.Ph(); return H.uri.l.Pk(H.uri.l.Ln(b, c), c, d) };
	H.uri.l.$w = function (b, c) { b = H.uri.l.bj(b); var d = b[1], e = []; d && H.g.forEach(d.split("&"), function (f) { var g = f.indexOf("="); c.hasOwnProperty(0 <= g ? f.substr(0, g) : f) || e.push(f) }); b[1] = H.uri.l.Dg(e.join("&"), H.uri.l.Ng(c)); return H.uri.l.Ci(b) }; H.uri.l.Is = function (b, c) { H.c.endsWith(b, "/") && (b = b.substr(0, b.length - 1)); H.c.startsWith(c, "/") && (c = c.substr(1)); return H.c.nl(b, "/", c) }; H.uri.l.Qc = function (b) { H.uri.l.split(b) }; H.uri.l.ug = { sg: "zx" }; H.uri.l.pn = function (b) { return H.uri.l.ko(b) }; H.G = function (b, c) {
		this.Ac = this.If = this.mc = ""; this.Hd = null; this.Ke = this.Gd = ""; this.Ma = this.Sm = !1; var d; b instanceof H.G ? (this.Ma = H.ca(c) ? c : b.Ma, O(this, b.Gc()), P(this, b.vd()), Q(this, b.pd()), R(this, b.td()), this.Qc(b.bc()), S(this, b.Xe().clone()), T(this, b.rd())) : b && (d = H.uri.l.split(String(b))) ? (this.Ma = !!c, O(this, d[H.uri.l.U.Xb] || "", !0), P(this, d[H.uri.l.U.qe] || "", !0), Q(this, d[H.uri.l.U.lb] || "", !0), R(this, d[H.uri.l.U.ob]), this.Qc(d[H.uri.l.U.le] || "", !0), S(this, d[H.uri.l.U.me] || "", !0), T(this, d[H.uri.l.U.dg] ||
			"", !0)) : (this.Ma = !!c, this.Oa = new H.G.bb(null, this.Ma))
	}; H.G.nk = H.uri.l.ug.sg; F = H.G.prototype;
	F.toString = function () { var b = [], c = this.Gc(); c && b.push(H.G.Bc(c, H.G.Pi, !0), ":"); var d = this.pd(); if (d || "file" == c) b.push("//"), (c = this.vd()) && b.push(H.G.Bc(c, H.G.Pi, !0), "@"), b.push(H.G.Qi(H.c.Rc(d))), d = this.td(), null != d && b.push(":", String(d)); if (d = this.bc()) this.Ac && "/" != d.charAt(0) && b.push("/"), b.push(H.G.Bc(d, "/" == d.charAt(0) ? H.G.En : H.G.Hn, !0)); (d = this.Oa.toString()) && b.push("?", d); (d = this.rd()) && b.push("#", H.G.Bc(d, H.G.Fn)); return b.join("") };
	F.resolve = function (b) { var c = this.clone(), d = !!b.mc; d ? O(c, b.Gc()) : d = !!b.If; d ? P(c, b.vd()) : d = !!b.Ac; d ? Q(c, b.pd()) : d = null != b.Hd; var e = b.bc(); if (d) R(c, b.td()); else if (d = !!b.Gd) { if ("/" != e.charAt(0)) if (this.Ac && !this.Gd) e = "/" + e; else { var f = c.bc().lastIndexOf("/"); -1 != f && (e = c.bc().substr(0, f + 1) + e) } e = H.G.In(e) } d ? c.Qc(e) : d = "" !== b.Oa.toString(); d ? S(c, b.Xe().clone()) : d = !!b.Ke; d && T(c, b.rd()); return c }; F.clone = function () { return new H.G(this) }; F.Gc = D("mc");
	function O(b, c, d) { X(b); b.mc = d ? H.G.zc(c, !0) : c; b.mc && (b.mc = b.mc.replace(/:$/, "")) } F.vd = D("If"); function P(b, c, d) { X(b); b.If = d ? H.G.zc(c) : c } F.pd = D("Ac"); function Q(b, c, d) { X(b); b.Ac = d ? H.G.zc(c, !0) : c } F.td = D("Hd"); function R(b, c) { X(b); if (c) { c = Number(c); if (isNaN(c) || 0 > c) throw Error("Bad port number " + c); b.Hd = c } else b.Hd = null } F.bc = D("Gd"); F.Qc = function (b, c) { X(this); this.Gd = c ? H.G.zc(b, !0) : b }; function S(b, c, d) { X(b); c instanceof H.G.bb ? (b.Oa = c, b.Oa.wf(b.Ma)) : (d || (c = H.G.Bc(c, H.G.Gn)), b.Oa = new H.G.bb(c, b.Ma)) }
	F.Xe = D("Oa"); F.getQuery = function () { return this.Oa.toString() }; F.rd = D("Ke"); function T(b, c, d) { X(b); b.Ke = d ? H.G.zc(c) : c } F.pn = function () { X(this); var b = H.G.nk, c = H.c.Ph(); X(this); this.Oa.set(b, c); return this }; F.removeParameter = function (b) { X(this); this.Oa.remove(b); return this }; function X(b) { if (b.Sm) throw Error("Tried to modify a read-only Uri"); } F.wf = function (b) { this.Ma = b; this.Oa && this.Oa.wf(b) }; H.G.parse = function (b, c) { return b instanceof H.G ? b.clone() : new H.G(b, c) };
	H.G.create = function (b, c, d, e, f, g, h, k) { k = new H.G(null, k); b && O(k, b); c && P(k, c); d && Q(k, d); e && R(k, e); f && k.Qc(f); g && S(k, g); h && T(k, h); return k }; H.G.resolve = function (b, c) { b instanceof H.G || (b = H.G.parse(b)); c instanceof H.G || (c = H.G.parse(c)); return b.resolve(c) };
	H.G.In = function (b) { if (".." == b || "." == b) return ""; if (H.c.contains(b, "./") || H.c.contains(b, "/.")) { var c = H.c.startsWith(b, "/"); b = b.split("/"); for (var d = [], e = 0; e < b.length;) { var f = b[e++]; "." == f ? c && e == b.length && d.push("") : ".." == f ? ((1 < d.length || 1 == d.length && "" != d[0]) && d.pop(), c && e == b.length && d.push("")) : (d.push(f), c = !0) } return d.join("/") } return b }; H.G.zc = function (b, c) { return b ? c ? decodeURI(b.replace(/%25/g, "%2525")) : decodeURIComponent(b) : "" };
	H.G.Bc = function (b, c, d) { return H.L(b) ? (b = encodeURI(b).replace(c, H.G.Kl), d && (b = H.G.Qi(b)), b) : null }; H.G.Kl = function (b) { b = b.charCodeAt(0); return "%" + (b >> 4 & 15).toString(16) + (b & 15).toString(16) }; H.G.Qi = function (b) { return b.replace(/%25([0-9a-fA-F]{2})/g, "%$1") }; H.G.Pi = /[#\/\?@]/g; H.G.Hn = /[#\?:]/g; H.G.En = /[#\?]/g; H.G.Gn = /[#\?@]/g; H.G.Fn = /#/g; H.G.zm = function (b, c) { b = H.uri.l.split(b); c = H.uri.l.split(c); return b[H.uri.l.U.lb] == c[H.uri.l.U.lb] && b[H.uri.l.U.ob] == c[H.uri.l.U.ob] };
	H.G.bb = function (b, c) { this.Z = this.da = null; this.Ka = b || null; this.Ma = !!c }; function Y(b) { b.da || (b.da = new H.R.Map, b.Z = 0, b.Ka && H.uri.l.yn(b.Ka, function (c, d) { b.add(H.c.$d(c), d) })) } H.G.bb.wt = function (b, c, d) { c = H.R.la(b); if ("undefined" == typeof c) throw Error("Keys are undefined"); d = new H.G.bb(null, d); b = H.R.ga(b); for (var e = 0; e < c.length; e++) { var f = c[e], g = b[e]; H.isArray(g) ? ta(d, f, g) : d.add(f, g) } return d };
	H.G.bb.vt = function (b, c, d, e) { if (b.length != c.length) throw Error("Mismatched lengths for keys/values"); d = new H.G.bb(null, e); for (e = 0; e < b.length; e++)d.add(b[e], c[e]); return d }; F = H.G.bb.prototype; F.ub = function () { Y(this); return this.Z }; F.add = function (b, c) { Y(this); this.Ka = null; b = Z(this, b); var d = this.da.get(b); d || this.da.set(b, d = []); d.push(c); this.Z += 1; return this }; F.remove = function (b) { Y(this); b = Z(this, b); return this.da.Lb(b) ? (this.Ka = null, this.Z -= this.da.get(b).length, this.da.remove(b)) : !1 };
	F.clear = function () { this.da = this.Ka = null; this.Z = 0 }; F.Ca = function () { Y(this); return 0 == this.Z }; F.Lb = function (b) { Y(this); b = Z(this, b); return this.da.Lb(b) }; F.Mb = function (b) { var c = this.ga(); return H.g.contains(c, b) }; F.forEach = function (b, c) { Y(this); this.da.forEach(function (d, e) { H.g.forEach(d, function (f) { b.call(c, f, e, this) }, this) }, this) }; F.la = function () { Y(this); for (var b = this.da.ga(), c = this.da.la(), d = [], e = 0; e < c.length; e++)for (var f = b[e], g = 0; g < f.length; g++)d.push(c[e]); return d };
	F.ga = function (b) { Y(this); var c = []; if (H.L(b)) this.Lb(b) && (c = H.g.concat(c, this.da.get(Z(this, b)))); else { b = this.da.ga(); for (var d = 0; d < b.length; d++)c = H.g.concat(c, b[d]) } return c }; F.set = function (b, c) { Y(this); this.Ka = null; b = Z(this, b); this.Lb(b) && (this.Z -= this.da.get(b).length); this.da.set(b, [c]); this.Z += 1; return this }; F.get = function (b, c) { if (!b) return c; b = this.ga(b); return 0 < b.length ? String(b[0]) : c }; function ta(b, c, d) { b.remove(c); 0 < d.length && (b.Ka = null, b.da.set(Z(b, c), H.g.clone(d)), b.Z += d.length) }
	F.toString = function () { if (this.Ka) return this.Ka; if (!this.da) return ""; for (var b = [], c = this.da.la(), d = 0; d < c.length; d++) { var e = c[d], f = H.c.Rc(e); e = this.ga(e); for (var g = 0; g < e.length; g++) { var h = f; "" !== e[g] && (h += "=" + H.c.Rc(e[g])); b.push(h) } } return this.Ka = b.join("&") }; F.clone = function () { var b = new H.G.bb; b.Ka = this.Ka; this.da && (b.da = this.da.clone(), b.Z = this.Z); return b }; function Z(b, c) { c = String(c); b.Ma && (c = c.toLowerCase()); return c }
	F.wf = function (b) { b && !this.Ma && (Y(this), this.Ka = null, this.da.forEach(function (c, d) { var e = d.toLowerCase(); d != e && (this.remove(d), ta(this, e, c)) }, this)); this.Ma = b }; F.extend = function (b) { for (var c = 0; c < arguments.length; c++)H.R.forEach(arguments[c], function (d, e) { this.add(e, d) }, this) }; var google = { v: {} }; google.v.w = {}; google.v.w.ja = {}; google.v.w.ja.Ai = function () { return new Promise(function (b) { if ("undefined" == typeof window || "complete" === document.readyState) b(); else if (window.addEventListener) document.addEventListener("DOMContentLoaded", b, !0), window.addEventListener("load", b, !0); else if (window.attachEvent) window.attachEvent("onload", b); else { var c = window.onload; H.Wa(c) ? window.onload = function (d) { c(d); b() } : window.onload = b } }) }; H.Dc("google.charts.loader.Utils.isWindowLoaded", google.v.w.ja.Ai);
	google.v.w.ja.hb = {}; google.v.w.ja.ww = function () { google.v.w.ja.hb = {} }; google.v.w.ja.Mu = function (b) { return google.v.w.ja.hb[b] && google.v.w.ja.hb[b].Pd }; google.v.w.ja.Lu = function (b) { return google.v.w.ja.hb[b] && google.v.w.ja.hb[b].loaded }; google.v.w.ja.ax = function (b, c) { google.v.w.ja.hb[b] = { Pd: c, loaded: !1 } }; google.v.w.ja.lo = function (b) { google.v.w.ja.hb[b] || (google.v.w.ja.hb[b] = { loaded: !1 }); google.v.w.ja.hb[b].loaded = !0 }; google.v.w.Qa = {}; google.v.w.Qa.kj = 3E4; google.v.w.Qa.Tv = function (b, c) { return { format: b, Qk: c } }; google.v.w.Qa.om = function (b) { return H.b.H.format(b.format, b.Qk) }; google.v.w.Qa.load = function (b, c) { var d = H.b.H.format(b, c), e = H.N.P.Sd(d, { timeout: google.v.w.Qa.kj, attributes: { async: !1, defer: !1 } }); return new Promise(function (f) { google.v.w.ja.lo(d.toString()); L(e, f) }) };
	google.v.w.Qa.Ov = function (b) { b = H.g.map(b, google.v.w.Qa.om); if (H.g.Ca(b)) return Promise.resolve(); var c = { timeout: google.v.w.Qa.kj, attributes: { async: !1, defer: !1 } }, d = []; !H.userAgent.oa || H.userAgent.Xa(11) ? H.g.forEach(b, function (e) { d.push(H.N.P.Sd(e, c)) }) : d.push(H.N.P.Zn(b, c)); return Promise.all(H.g.map(d, function (e) { return new Promise(function (f) { return L(e, f) }) })) }; google.v.w.Bg = { 1: "1.0", "1.0": "current", "1.1": "upcoming", 41: x, 42: x, 43: x, 44: x, 46: "46.1", "46.1": "46.2", previous: "45.2", current: "46", upcoming: "46.2" }; google.v.w.J = {}; google.v.w.J.Cm = function () { google.v.w.J.gf = null; google.v.w.J.jd = null; google.v.w.J.rj = null }; H.wb(aa) ? console.warn("Google Charts loader.js should only be loaded once.") : google.v.w.J.Cm(); google.v.w.J.qn = function (b) { var c = b, d = b.match(/^testing-/); d && (c = c.replace(/^testing-/, "")); b = c; do { if (c === google.v.w.Bg[c]) throw Error("Infinite loop in version mapping: " + c); var e = google.v.w.Bg[c]; e && (c = e) } while (e); d = (d ? "testing-" : "") + c; return { version: c == x ? b : d, jn: d } };
	google.v.w.J.hn = function (b) {
		var c = google.v.w.J.qn(b), d = H.c.M.from("https://www.gstatic.com/charts/%{version}/loader.js"); return google.v.w.Qa.load(d, { version: c.jn }).then(function () {
			var e = H.wb("google.charts.loader.VersionSpecific.load") || H.wb("google.charts.loader.publicLoad") || H.wb("google.charts.versionSpecific.load"); if (!e) throw Error("Bad version: " + b); google.v.w.J.rj = function (f) {
				f = e(c.version, f); if (null == f || null == f.then) {
					var g = H.wb("google.charts.loader.publicSetOnLoadCallback") || H.wb("google.charts.versionSpecific.setOnLoadCallback");
					f = new Promise(function (h) { g(h) }); f.then = g
				} return f
			}
		})
	}; google.v.w.J.en = function (b, c) { if (!google.v.w.J.gf) { if (c.enableUrlSettings && window.URLSearchParams) try { b = (new URLSearchParams(top.location.search)).get("charts-version") || b } catch (d) { console.info("Failed to get charts-version from top URL", d) } google.v.w.J.gf = google.v.w.J.hn(b) } return google.v.w.J.jd = google.v.w.J.gf.then(function () { return google.v.w.J.rj(c) }) };
	google.v.w.J.jo = function (b) { if (!google.v.w.J.jd) throw Error("Must call google.charts.load before google.charts.setOnLoadCallback"); return b ? google.v.w.J.jd.then(b) : google.v.w.J.jd }; google.v.load = function (b) { for (var c = [], d = 0; d < arguments.length; ++d)c[d - 0] = arguments[d]; d = 0; "visualization" === c[d] && d++; var e = "current"; H.L(c[d]) && (e = c[d], d++); var f = {}; H.Da(c[d]) && (f = c[d]); return google.v.w.J.en(e, f) }; H.Dc(aa, google.v.load); google.v.$i = google.v.w.J.jo; H.Dc("google.charts.setOnLoadCallback", google.v.$i);
	google.v.w.J.ik = H.c.M.from("https://maps.googleapis.com/maps/api/js?jsapiRedirect=true&key=%{key}&v=%{version}&libraries=%{libraries}"); google.v.w.J.jk = H.c.M.from("https://maps-api-ssl.google.com/maps?jsapiRedirect=true&file=googleapi&key=%{key}&v=%{version}&libraries=%{libraries}");
	google.v.w.J.fn = function (b, c, d) { console.warn("Loading Maps API with the jsapi loader is deprecated."); d = d || {}; b = google.v.w.J.hf(d.callback); google.v.w.Qa.load("2" === c ? google.v.w.J.jk : google.v.w.J.ik, { key: d.key || d.client || "", version: c || "", libraries: d.libraries || "" }).then(b) }; google.v.w.J.ig = H.c.M.from("https://www.gstatic.com/inputtools/js/ita/inputtools_3.js");
	google.v.w.J.dn = function (b, c, d) { H.Da(d) && d.packages ? (H.isArray(d.packages) ? d.packages : [d.packages]).includes("inputtools") ? (console.warn("Loading Input Tools with the jsapi loader is deprecated.\nPlease load " + google.v.w.J.ig + " directly."), b = google.v.w.J.hf(d.callback), google.v.w.Qa.load(google.v.w.J.ig, {}).then(b)) : console.error("Loading elements other than inputtools with the jsapi loader is unsupported.") : console.error("google.load of elements was invoked without specifying packages") };
	google.v.w.J.hf = function (b) { return function () { if (H.L(b) && "" !== b) try { H.wb(b)() } catch (c) { throw Error("Callback failed with: " + c); } } }; google.v.w.J.Yh = function (b) { for (var c = [], d = 0; d < arguments.length; ++d)c[d - 0] = arguments[d]; switch (c[0]) { case "maps": google.v.w.J.fn.apply(google.v.w.J, G.ve(c)); return; case "elements": google.v.w.J.dn.apply(google.v.w.J, G.ve(c)); return }if ("visualization" !== c[0]) throw Error('Module "' + c[0] + '" is not supported.'); google.v.load.apply(google.v, G.ve(c)) };
	google.v.w.J.Cn = function (b) { H.L(b) && (b = google.v.w.J.hf(b), google.v.w.ja.Ai().then(b)) }; google.v.w.J.Bn = function (b) { if (H.L(b)) try { if ("" !== b) for (var c = JSON.parse(b).modules, d = G.Dd(c), e = d.next(); !e.done; e = d.next()) { var f = e.value; google.v.w.J.Yh(f.name, f.version, f) } } catch (g) { throw Error("Autoload failed with: " + g); } }; google.v.w.J.Rl = function () { H.wb("google.load") || (H.Dc("google.load", google.v.w.J.Yh), H.Dc("google.setOnLoadCallback", google.v.$i)) };
	google.v.w.J.Dn = function () { google.v.w.J.Rl(); var b = document.getElementsByTagName("script"); b = b[b.length - 1].getAttribute("src"); b = (new H.G(b)).Oa.toString(); b = new H.G.bb(b); google.v.w.J.Cn(b.get("callback")); google.v.w.J.Bn(b.get("autoload")) }; google.v.w.J.Dn();
}).call(this);

!function (a, b) { "object" == typeof exports && "undefined" != typeof module ? module.exports = b() : "function" == typeof define && define.amd ? define(b) : a.moment = b() }(this, function () {
	"use strict"; function a() { return re.apply(null, arguments) } function b(a) { re = a } function c(a) { return a instanceof Array || "[object Array]" === Object.prototype.toString.call(a) } function d(a) { return null != a && "[object Object]" === Object.prototype.toString.call(a) } function e(a) { var b; for (b in a) return !1; return !0 } function f(a) { return void 0 === a } function g(a) { return "number" == typeof a || "[object Number]" === Object.prototype.toString.call(a) } function h(a) { return a instanceof Date || "[object Date]" === Object.prototype.toString.call(a) } function i(a, b) { var c, d = []; for (c = 0; c < a.length; ++c)d.push(b(a[c], c)); return d } function j(a, b) { return Object.prototype.hasOwnProperty.call(a, b) } function k(a, b) { for (var c in b) j(b, c) && (a[c] = b[c]); return j(b, "toString") && (a.toString = b.toString), j(b, "valueOf") && (a.valueOf = b.valueOf), a } function l(a, b, c, d) { return sb(a, b, c, d, !0).utc() } function m() { return { empty: !1, unusedTokens: [], unusedInput: [], overflow: -2, charsLeftOver: 0, nullInput: !1, invalidMonth: null, invalidFormat: !1, userInvalidated: !1, iso: !1, parsedDateParts: [], meridiem: null, rfc2822: !1, weekdayMismatch: !1 } } function n(a) { return null == a._pf && (a._pf = m()), a._pf } function o(a) { if (null == a._isValid) { var b = n(a), c = te.call(b.parsedDateParts, function (a) { return null != a }), d = !isNaN(a._d.getTime()) && b.overflow < 0 && !b.empty && !b.invalidMonth && !b.invalidWeekday && !b.nullInput && !b.invalidFormat && !b.userInvalidated && (!b.meridiem || b.meridiem && c); if (a._strict && (d = d && 0 === b.charsLeftOver && 0 === b.unusedTokens.length && void 0 === b.bigHour), null != Object.isFrozen && Object.isFrozen(a)) return d; a._isValid = d } return a._isValid } function p(a) { var b = l(NaN); return null != a ? k(n(b), a) : n(b).userInvalidated = !0, b } function q(a, b) { var c, d, e; if (f(b._isAMomentObject) || (a._isAMomentObject = b._isAMomentObject), f(b._i) || (a._i = b._i), f(b._f) || (a._f = b._f), f(b._l) || (a._l = b._l), f(b._strict) || (a._strict = b._strict), f(b._tzm) || (a._tzm = b._tzm), f(b._isUTC) || (a._isUTC = b._isUTC), f(b._offset) || (a._offset = b._offset), f(b._pf) || (a._pf = n(b)), f(b._locale) || (a._locale = b._locale), ue.length > 0) for (c = 0; c < ue.length; c++)d = ue[c], e = b[d], f(e) || (a[d] = e); return a } function r(b) { q(this, b), this._d = new Date(null != b._d ? b._d.getTime() : NaN), this.isValid() || (this._d = new Date(NaN)), ve === !1 && (ve = !0, a.updateOffset(this), ve = !1) } function s(a) { return a instanceof r || null != a && null != a._isAMomentObject } function t(a) { return a < 0 ? Math.ceil(a) || 0 : Math.floor(a) } function u(a) { var b = +a, c = 0; return 0 !== b && isFinite(b) && (c = t(b)), c } function v(a, b, c) { var d, e = Math.min(a.length, b.length), f = Math.abs(a.length - b.length), g = 0; for (d = 0; d < e; d++)(c && a[d] !== b[d] || !c && u(a[d]) !== u(b[d])) && g++; return g + f } function w(b) { a.suppressDeprecationWarnings === !1 && "undefined" != typeof console && console.warn && console.warn("Deprecation warning: " + b) } function x(b, c) { var d = !0; return k(function () { if (null != a.deprecationHandler && a.deprecationHandler(null, b), d) { for (var e, f = [], g = 0; g < arguments.length; g++) { if (e = "", "object" == typeof arguments[g]) { e += "\n[" + g + "] "; for (var h in arguments[0]) e += h + ": " + arguments[0][h] + ", "; e = e.slice(0, -2) } else e = arguments[g]; f.push(e) } w(b + "\nArguments: " + Array.prototype.slice.call(f).join("") + "\n" + (new Error).stack), d = !1 } return c.apply(this, arguments) }, c) } function y(b, c) { null != a.deprecationHandler && a.deprecationHandler(b, c), we[b] || (w(c), we[b] = !0) } function z(a) { return a instanceof Function || "[object Function]" === Object.prototype.toString.call(a) } function A(a) { var b, c; for (c in a) b = a[c], z(b) ? this[c] = b : this["_" + c] = b; this._config = a, this._dayOfMonthOrdinalParseLenient = new RegExp((this._dayOfMonthOrdinalParse.source || this._ordinalParse.source) + "|" + /\d{1,2}/.source) } function B(a, b) { var c, e = k({}, a); for (c in b) j(b, c) && (d(a[c]) && d(b[c]) ? (e[c] = {}, k(e[c], a[c]), k(e[c], b[c])) : null != b[c] ? e[c] = b[c] : delete e[c]); for (c in a) j(a, c) && !j(b, c) && d(a[c]) && (e[c] = k({}, e[c])); return e } function C(a) { null != a && this.set(a) } function D(a, b, c) { var d = this._calendar[a] || this._calendar.sameElse; return z(d) ? d.call(b, c) : d } function E(a) { var b = this._longDateFormat[a], c = this._longDateFormat[a.toUpperCase()]; return b || !c ? b : (this._longDateFormat[a] = c.replace(/MMMM|MM|DD|dddd/g, function (a) { return a.slice(1) }), this._longDateFormat[a]) } function F() { return this._invalidDate } function G(a) { return this._ordinal.replace("%d", a) } function H(a, b, c, d) { var e = this._relativeTime[c]; return z(e) ? e(a, b, c, d) : e.replace(/%d/i, a) } function I(a, b) { var c = this._relativeTime[a > 0 ? "future" : "past"]; return z(c) ? c(b) : c.replace(/%s/i, b) } function J(a, b) { var c = a.toLowerCase(); Ge[c] = Ge[c + "s"] = Ge[b] = a } function K(a) { return "string" == typeof a ? Ge[a] || Ge[a.toLowerCase()] : void 0 } function L(a) { var b, c, d = {}; for (c in a) j(a, c) && (b = K(c), b && (d[b] = a[c])); return d } function M(a, b) { He[a] = b } function N(a) { var b = []; for (var c in a) b.push({ unit: c, priority: He[c] }); return b.sort(function (a, b) { return a.priority - b.priority }), b } function O(b, c) { return function (d) { return null != d ? (Q(this, b, d), a.updateOffset(this, c), this) : P(this, b) } } function P(a, b) { return a.isValid() ? a._d["get" + (a._isUTC ? "UTC" : "") + b]() : NaN } function Q(a, b, c) { a.isValid() && a._d["set" + (a._isUTC ? "UTC" : "") + b](c) } function R(a) { return a = K(a), z(this[a]) ? this[a]() : this } function S(a, b) { if ("object" == typeof a) { a = L(a); for (var c = N(a), d = 0; d < c.length; d++)this[c[d].unit](a[c[d].unit]) } else if (a = K(a), z(this[a])) return this[a](b); return this } function T(a, b, c) { var d = "" + Math.abs(a), e = b - d.length, f = a >= 0; return (f ? c ? "+" : "" : "-") + Math.pow(10, Math.max(0, e)).toString().substr(1) + d } function U(a, b, c, d) { var e = d; "string" == typeof d && (e = function () { return this[d]() }), a && (Le[a] = e), b && (Le[b[0]] = function () { return T(e.apply(this, arguments), b[1], b[2]) }), c && (Le[c] = function () { return this.localeData().ordinal(e.apply(this, arguments), a) }) } function V(a) { return a.match(/\[[\s\S]/) ? a.replace(/^\[|\]$/g, "") : a.replace(/\\/g, "") } function W(a) { var b, c, d = a.match(Ie); for (b = 0, c = d.length; b < c; b++)Le[d[b]] ? d[b] = Le[d[b]] : d[b] = V(d[b]); return function (b) { var e, f = ""; for (e = 0; e < c; e++)f += z(d[e]) ? d[e].call(b, a) : d[e]; return f } } function X(a, b) { return a.isValid() ? (b = Y(b, a.localeData()), Ke[b] = Ke[b] || W(b), Ke[b](a)) : a.localeData().invalidDate() } function Y(a, b) { function c(a) { return b.longDateFormat(a) || a } var d = 5; for (Je.lastIndex = 0; d >= 0 && Je.test(a);)a = a.replace(Je, c), Je.lastIndex = 0, d -= 1; return a } function Z(a, b, c) { bf[a] = z(b) ? b : function (a, d) { return a && c ? c : b } } function $(a, b) { return j(bf, a) ? bf[a](b._strict, b._locale) : new RegExp(_(a)) } function _(a) { return aa(a.replace("\\", "").replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (a, b, c, d, e) { return b || c || d || e })) } function aa(a) { return a.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&") } function ba(a, b) { var c, d = b; for ("string" == typeof a && (a = [a]), g(b) && (d = function (a, c) { c[b] = u(a) }), c = 0; c < a.length; c++)cf[a[c]] = d } function ca(a, b) { ba(a, function (a, c, d, e) { d._w = d._w || {}, b(a, d._w, d, e) }) } function da(a, b, c) { null != b && j(cf, a) && cf[a](b, c._a, c, a) } function ea(a, b) { return new Date(Date.UTC(a, b + 1, 0)).getUTCDate() } function fa(a, b) { return a ? c(this._months) ? this._months[a.month()] : this._months[(this._months.isFormat || of).test(b) ? "format" : "standalone"][a.month()] : c(this._months) ? this._months : this._months.standalone } function ga(a, b) { return a ? c(this._monthsShort) ? this._monthsShort[a.month()] : this._monthsShort[of.test(b) ? "format" : "standalone"][a.month()] : c(this._monthsShort) ? this._monthsShort : this._monthsShort.standalone } function ha(a, b, c) { var d, e, f, g = a.toLocaleLowerCase(); if (!this._monthsParse) for (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = [], d = 0; d < 12; ++d)f = l([2e3, d]), this._shortMonthsParse[d] = this.monthsShort(f, "").toLocaleLowerCase(), this._longMonthsParse[d] = this.months(f, "").toLocaleLowerCase(); return c ? "MMM" === b ? (e = nf.call(this._shortMonthsParse, g), e !== -1 ? e : null) : (e = nf.call(this._longMonthsParse, g), e !== -1 ? e : null) : "MMM" === b ? (e = nf.call(this._shortMonthsParse, g), e !== -1 ? e : (e = nf.call(this._longMonthsParse, g), e !== -1 ? e : null)) : (e = nf.call(this._longMonthsParse, g), e !== -1 ? e : (e = nf.call(this._shortMonthsParse, g), e !== -1 ? e : null)) } function ia(a, b, c) { var d, e, f; if (this._monthsParseExact) return ha.call(this, a, b, c); for (this._monthsParse || (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = []), d = 0; d < 12; d++) { if (e = l([2e3, d]), c && !this._longMonthsParse[d] && (this._longMonthsParse[d] = new RegExp("^" + this.months(e, "").replace(".", "") + "$", "i"), this._shortMonthsParse[d] = new RegExp("^" + this.monthsShort(e, "").replace(".", "") + "$", "i")), c || this._monthsParse[d] || (f = "^" + this.months(e, "") + "|^" + this.monthsShort(e, ""), this._monthsParse[d] = new RegExp(f.replace(".", ""), "i")), c && "MMMM" === b && this._longMonthsParse[d].test(a)) return d; if (c && "MMM" === b && this._shortMonthsParse[d].test(a)) return d; if (!c && this._monthsParse[d].test(a)) return d } } function ja(a, b) { var c; if (!a.isValid()) return a; if ("string" == typeof b) if (/^\d+$/.test(b)) b = u(b); else if (b = a.localeData().monthsParse(b), !g(b)) return a; return c = Math.min(a.date(), ea(a.year(), b)), a._d["set" + (a._isUTC ? "UTC" : "") + "Month"](b, c), a } function ka(b) { return null != b ? (ja(this, b), a.updateOffset(this, !0), this) : P(this, "Month") } function la() { return ea(this.year(), this.month()) } function ma(a) { return this._monthsParseExact ? (j(this, "_monthsRegex") || oa.call(this), a ? this._monthsShortStrictRegex : this._monthsShortRegex) : (j(this, "_monthsShortRegex") || (this._monthsShortRegex = rf), this._monthsShortStrictRegex && a ? this._monthsShortStrictRegex : this._monthsShortRegex) } function na(a) { return this._monthsParseExact ? (j(this, "_monthsRegex") || oa.call(this), a ? this._monthsStrictRegex : this._monthsRegex) : (j(this, "_monthsRegex") || (this._monthsRegex = sf), this._monthsStrictRegex && a ? this._monthsStrictRegex : this._monthsRegex) } function oa() { function a(a, b) { return b.length - a.length } var b, c, d = [], e = [], f = []; for (b = 0; b < 12; b++)c = l([2e3, b]), d.push(this.monthsShort(c, "")), e.push(this.months(c, "")), f.push(this.months(c, "")), f.push(this.monthsShort(c, "")); for (d.sort(a), e.sort(a), f.sort(a), b = 0; b < 12; b++)d[b] = aa(d[b]), e[b] = aa(e[b]); for (b = 0; b < 24; b++)f[b] = aa(f[b]); this._monthsRegex = new RegExp("^(" + f.join("|") + ")", "i"), this._monthsShortRegex = this._monthsRegex, this._monthsStrictRegex = new RegExp("^(" + e.join("|") + ")", "i"), this._monthsShortStrictRegex = new RegExp("^(" + d.join("|") + ")", "i") } function pa(a) { return qa(a) ? 366 : 365 } function qa(a) { return a % 4 === 0 && a % 100 !== 0 || a % 400 === 0 } function ra() { return qa(this.year()) } function sa(a, b, c, d, e, f, g) { var h = new Date(a, b, c, d, e, f, g); return a < 100 && a >= 0 && isFinite(h.getFullYear()) && h.setFullYear(a), h } function ta(a) { var b = new Date(Date.UTC.apply(null, arguments)); return a < 100 && a >= 0 && isFinite(b.getUTCFullYear()) && b.setUTCFullYear(a), b } function ua(a, b, c) { var d = 7 + b - c, e = (7 + ta(a, 0, d).getUTCDay() - b) % 7; return -e + d - 1 } function va(a, b, c, d, e) { var f, g, h = (7 + c - d) % 7, i = ua(a, d, e), j = 1 + 7 * (b - 1) + h + i; return j <= 0 ? (f = a - 1, g = pa(f) + j) : j > pa(a) ? (f = a + 1, g = j - pa(a)) : (f = a, g = j), { year: f, dayOfYear: g } } function wa(a, b, c) { var d, e, f = ua(a.year(), b, c), g = Math.floor((a.dayOfYear() - f - 1) / 7) + 1; return g < 1 ? (e = a.year() - 1, d = g + xa(e, b, c)) : g > xa(a.year(), b, c) ? (d = g - xa(a.year(), b, c), e = a.year() + 1) : (e = a.year(), d = g), { week: d, year: e } } function xa(a, b, c) { var d = ua(a, b, c), e = ua(a + 1, b, c); return (pa(a) - d + e) / 7 } function ya(a) { return wa(a, this._week.dow, this._week.doy).week } function za() { return this._week.dow } function Aa() { return this._week.doy } function Ba(a) { var b = this.localeData().week(this); return null == a ? b : this.add(7 * (a - b), "d") } function Ca(a) { var b = wa(this, 1, 4).week; return null == a ? b : this.add(7 * (a - b), "d") } function Da(a, b) { return "string" != typeof a ? a : isNaN(a) ? (a = b.weekdaysParse(a), "number" == typeof a ? a : null) : parseInt(a, 10) } function Ea(a, b) { return "string" == typeof a ? b.weekdaysParse(a) % 7 || 7 : isNaN(a) ? null : a } function Fa(a, b) { return a ? c(this._weekdays) ? this._weekdays[a.day()] : this._weekdays[this._weekdays.isFormat.test(b) ? "format" : "standalone"][a.day()] : c(this._weekdays) ? this._weekdays : this._weekdays.standalone } function Ga(a) { return a ? this._weekdaysShort[a.day()] : this._weekdaysShort } function Ha(a) { return a ? this._weekdaysMin[a.day()] : this._weekdaysMin } function Ia(a, b, c) { var d, e, f, g = a.toLocaleLowerCase(); if (!this._weekdaysParse) for (this._weekdaysParse = [], this._shortWeekdaysParse = [], this._minWeekdaysParse = [], d = 0; d < 7; ++d)f = l([2e3, 1]).day(d), this._minWeekdaysParse[d] = this.weekdaysMin(f, "").toLocaleLowerCase(), this._shortWeekdaysParse[d] = this.weekdaysShort(f, "").toLocaleLowerCase(), this._weekdaysParse[d] = this.weekdays(f, "").toLocaleLowerCase(); return c ? "dddd" === b ? (e = nf.call(this._weekdaysParse, g), e !== -1 ? e : null) : "ddd" === b ? (e = nf.call(this._shortWeekdaysParse, g), e !== -1 ? e : null) : (e = nf.call(this._minWeekdaysParse, g), e !== -1 ? e : null) : "dddd" === b ? (e = nf.call(this._weekdaysParse, g), e !== -1 ? e : (e = nf.call(this._shortWeekdaysParse, g), e !== -1 ? e : (e = nf.call(this._minWeekdaysParse, g), e !== -1 ? e : null))) : "ddd" === b ? (e = nf.call(this._shortWeekdaysParse, g), e !== -1 ? e : (e = nf.call(this._weekdaysParse, g), e !== -1 ? e : (e = nf.call(this._minWeekdaysParse, g), e !== -1 ? e : null))) : (e = nf.call(this._minWeekdaysParse, g), e !== -1 ? e : (e = nf.call(this._weekdaysParse, g), e !== -1 ? e : (e = nf.call(this._shortWeekdaysParse, g), e !== -1 ? e : null))) } function Ja(a, b, c) { var d, e, f; if (this._weekdaysParseExact) return Ia.call(this, a, b, c); for (this._weekdaysParse || (this._weekdaysParse = [], this._minWeekdaysParse = [], this._shortWeekdaysParse = [], this._fullWeekdaysParse = []), d = 0; d < 7; d++) { if (e = l([2e3, 1]).day(d), c && !this._fullWeekdaysParse[d] && (this._fullWeekdaysParse[d] = new RegExp("^" + this.weekdays(e, "").replace(".", ".?") + "$", "i"), this._shortWeekdaysParse[d] = new RegExp("^" + this.weekdaysShort(e, "").replace(".", ".?") + "$", "i"), this._minWeekdaysParse[d] = new RegExp("^" + this.weekdaysMin(e, "").replace(".", ".?") + "$", "i")), this._weekdaysParse[d] || (f = "^" + this.weekdays(e, "") + "|^" + this.weekdaysShort(e, "") + "|^" + this.weekdaysMin(e, ""), this._weekdaysParse[d] = new RegExp(f.replace(".", ""), "i")), c && "dddd" === b && this._fullWeekdaysParse[d].test(a)) return d; if (c && "ddd" === b && this._shortWeekdaysParse[d].test(a)) return d; if (c && "dd" === b && this._minWeekdaysParse[d].test(a)) return d; if (!c && this._weekdaysParse[d].test(a)) return d } } function Ka(a) { if (!this.isValid()) return null != a ? this : NaN; var b = this._isUTC ? this._d.getUTCDay() : this._d.getDay(); return null != a ? (a = Da(a, this.localeData()), this.add(a - b, "d")) : b } function La(a) { if (!this.isValid()) return null != a ? this : NaN; var b = (this.day() + 7 - this.localeData()._week.dow) % 7; return null == a ? b : this.add(a - b, "d") } function Ma(a) { if (!this.isValid()) return null != a ? this : NaN; if (null != a) { var b = Ea(a, this.localeData()); return this.day(this.day() % 7 ? b : b - 7) } return this.day() || 7 } function Na(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysStrictRegex : this._weekdaysRegex) : (j(this, "_weekdaysRegex") || (this._weekdaysRegex = yf), this._weekdaysStrictRegex && a ? this._weekdaysStrictRegex : this._weekdaysRegex) } function Oa(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex) : (j(this, "_weekdaysShortRegex") || (this._weekdaysShortRegex = zf), this._weekdaysShortStrictRegex && a ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex) } function Pa(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex) : (j(this, "_weekdaysMinRegex") || (this._weekdaysMinRegex = Af), this._weekdaysMinStrictRegex && a ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex) } function Qa() { function a(a, b) { return b.length - a.length } var b, c, d, e, f, g = [], h = [], i = [], j = []; for (b = 0; b < 7; b++)c = l([2e3, 1]).day(b), d = this.weekdaysMin(c, ""), e = this.weekdaysShort(c, ""), f = this.weekdays(c, ""), g.push(d), h.push(e), i.push(f), j.push(d), j.push(e), j.push(f); for (g.sort(a), h.sort(a), i.sort(a), j.sort(a), b = 0; b < 7; b++)h[b] = aa(h[b]), i[b] = aa(i[b]), j[b] = aa(j[b]); this._weekdaysRegex = new RegExp("^(" + j.join("|") + ")", "i"), this._weekdaysShortRegex = this._weekdaysRegex, this._weekdaysMinRegex = this._weekdaysRegex, this._weekdaysStrictRegex = new RegExp("^(" + i.join("|") + ")", "i"), this._weekdaysShortStrictRegex = new RegExp("^(" + h.join("|") + ")", "i"), this._weekdaysMinStrictRegex = new RegExp("^(" + g.join("|") + ")", "i") } function Ra() { return this.hours() % 12 || 12 } function Sa() { return this.hours() || 24 } function Ta(a, b) { U(a, 0, 0, function () { return this.localeData().meridiem(this.hours(), this.minutes(), b) }) } function Ua(a, b) { return b._meridiemParse } function Va(a) { return "p" === (a + "").toLowerCase().charAt(0) } function Wa(a, b, c) { return a > 11 ? c ? "pm" : "PM" : c ? "am" : "AM" } function Xa(a) { return a ? a.toLowerCase().replace("_", "-") : a } function Ya(a) { for (var b, c, d, e, f = 0; f < a.length;) { for (e = Xa(a[f]).split("-"), b = e.length, c = Xa(a[f + 1]), c = c ? c.split("-") : null; b > 0;) { if (d = Za(e.slice(0, b).join("-"))) return d; if (c && c.length >= b && v(e, c, !0) >= b - 1) break; b-- } f++ } return null } function Za(a) { var b = null; if (!Ff[a] && "undefined" != typeof module && module && module.exports) try { b = Bf._abbr, require("./locale/" + a), $a(b) } catch (a) { } return Ff[a] } function $a(a, b) { var c; return a && (c = f(b) ? bb(a) : _a(a, b), c && (Bf = c)), Bf._abbr } function _a(a, b) { if (null !== b) { var c = Ef; if (b.abbr = a, null != Ff[a]) y("defineLocaleOverride", "use moment.updateLocale(localeName, config) to change an existing locale. moment.defineLocale(localeName, config) should only be used for creating a new locale See http://momentjs.com/guides/#/warnings/define-locale/ for more info."), c = Ff[a]._config; else if (null != b.parentLocale) { if (null == Ff[b.parentLocale]) return Gf[b.parentLocale] || (Gf[b.parentLocale] = []), Gf[b.parentLocale].push({ name: a, config: b }), null; c = Ff[b.parentLocale]._config } return Ff[a] = new C(B(c, b)), Gf[a] && Gf[a].forEach(function (a) { _a(a.name, a.config) }), $a(a), Ff[a] } return delete Ff[a], null } function ab(a, b) { if (null != b) { var c, d = Ef; null != Ff[a] && (d = Ff[a]._config), b = B(d, b), c = new C(b), c.parentLocale = Ff[a], Ff[a] = c, $a(a) } else null != Ff[a] && (null != Ff[a].parentLocale ? Ff[a] = Ff[a].parentLocale : null != Ff[a] && delete Ff[a]); return Ff[a] } function bb(a) { var b; if (a && a._locale && a._locale._abbr && (a = a._locale._abbr), !a) return Bf; if (!c(a)) { if (b = Za(a)) return b; a = [a] } return Ya(a) } function cb() { return ze(Ff) } function db(a) { var b, c = a._a; return c && n(a).overflow === -2 && (b = c[ef] < 0 || c[ef] > 11 ? ef : c[ff] < 1 || c[ff] > ea(c[df], c[ef]) ? ff : c[gf] < 0 || c[gf] > 24 || 24 === c[gf] && (0 !== c[hf] || 0 !== c[jf] || 0 !== c[kf]) ? gf : c[hf] < 0 || c[hf] > 59 ? hf : c[jf] < 0 || c[jf] > 59 ? jf : c[kf] < 0 || c[kf] > 999 ? kf : -1, n(a)._overflowDayOfYear && (b < df || b > ff) && (b = ff), n(a)._overflowWeeks && b === -1 && (b = lf), n(a)._overflowWeekday && b === -1 && (b = mf), n(a).overflow = b), a } function eb(a) { var b, c, d, e, f, g, h = a._i, i = Hf.exec(h) || If.exec(h); if (i) { for (n(a).iso = !0, b = 0, c = Kf.length; b < c; b++)if (Kf[b][1].exec(i[1])) { e = Kf[b][0], d = Kf[b][2] !== !1; break } if (null == e) return void (a._isValid = !1); if (i[3]) { for (b = 0, c = Lf.length; b < c; b++)if (Lf[b][1].exec(i[3])) { f = (i[2] || " ") + Lf[b][0]; break } if (null == f) return void (a._isValid = !1) } if (!d && null != f) return void (a._isValid = !1); if (i[4]) { if (!Jf.exec(i[4])) return void (a._isValid = !1); g = "Z" } a._f = e + (f || "") + (g || ""), lb(a) } else a._isValid = !1 } function fb(a) { var b, c, d, e, f, g, h, i, j = { " GMT": " +0000", " EDT": " -0400", " EST": " -0500", " CDT": " -0500", " CST": " -0600", " MDT": " -0600", " MST": " -0700", " PDT": " -0700", " PST": " -0800" }, k = "YXWVUTSRQPONZABCDEFGHIKLM"; if (b = a._i.replace(/\([^\)]*\)|[\n\t]/g, " ").replace(/(\s\s+)/g, " ").replace(/^\s|\s$/g, ""), c = Nf.exec(b)) { if (d = c[1] ? "ddd" + (5 === c[1].length ? ", " : " ") : "", e = "D MMM " + (c[2].length > 10 ? "YYYY " : "YY "), f = "HH:mm" + (c[4] ? ":ss" : ""), c[1]) { var l = new Date(c[2]), m = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][l.getDay()]; if (c[1].substr(0, 3) !== m) return n(a).weekdayMismatch = !0, void (a._isValid = !1) } switch (c[5].length) { case 2: 0 === i ? h = " +0000" : (i = k.indexOf(c[5][1].toUpperCase()) - 12, h = (i < 0 ? " -" : " +") + ("" + i).replace(/^-?/, "0").match(/..$/)[0] + "00"); break; case 4: h = j[c[5]]; break; default: h = j[" GMT"] }c[5] = h, a._i = c.splice(1).join(""), g = " ZZ", a._f = d + e + f + g, lb(a), n(a).rfc2822 = !0 } else a._isValid = !1 } function gb(b) { var c = Mf.exec(b._i); return null !== c ? void (b._d = new Date(+c[1])) : (eb(b), void (b._isValid === !1 && (delete b._isValid, fb(b), b._isValid === !1 && (delete b._isValid, a.createFromInputFallback(b))))) } function hb(a, b, c) { return null != a ? a : null != b ? b : c } function ib(b) { var c = new Date(a.now()); return b._useUTC ? [c.getUTCFullYear(), c.getUTCMonth(), c.getUTCDate()] : [c.getFullYear(), c.getMonth(), c.getDate()] } function jb(a) { var b, c, d, e, f = []; if (!a._d) { for (d = ib(a), a._w && null == a._a[ff] && null == a._a[ef] && kb(a), null != a._dayOfYear && (e = hb(a._a[df], d[df]), (a._dayOfYear > pa(e) || 0 === a._dayOfYear) && (n(a)._overflowDayOfYear = !0), c = ta(e, 0, a._dayOfYear), a._a[ef] = c.getUTCMonth(), a._a[ff] = c.getUTCDate()), b = 0; b < 3 && null == a._a[b]; ++b)a._a[b] = f[b] = d[b]; for (; b < 7; b++)a._a[b] = f[b] = null == a._a[b] ? 2 === b ? 1 : 0 : a._a[b]; 24 === a._a[gf] && 0 === a._a[hf] && 0 === a._a[jf] && 0 === a._a[kf] && (a._nextDay = !0, a._a[gf] = 0), a._d = (a._useUTC ? ta : sa).apply(null, f), null != a._tzm && a._d.setUTCMinutes(a._d.getUTCMinutes() - a._tzm), a._nextDay && (a._a[gf] = 24) } } function kb(a) { var b, c, d, e, f, g, h, i; if (b = a._w, null != b.GG || null != b.W || null != b.E) f = 1, g = 4, c = hb(b.GG, a._a[df], wa(tb(), 1, 4).year), d = hb(b.W, 1), e = hb(b.E, 1), (e < 1 || e > 7) && (i = !0); else { f = a._locale._week.dow, g = a._locale._week.doy; var j = wa(tb(), f, g); c = hb(b.gg, a._a[df], j.year), d = hb(b.w, j.week), null != b.d ? (e = b.d, (e < 0 || e > 6) && (i = !0)) : null != b.e ? (e = b.e + f, (b.e < 0 || b.e > 6) && (i = !0)) : e = f } d < 1 || d > xa(c, f, g) ? n(a)._overflowWeeks = !0 : null != i ? n(a)._overflowWeekday = !0 : (h = va(c, d, e, f, g), a._a[df] = h.year, a._dayOfYear = h.dayOfYear) } function lb(b) { if (b._f === a.ISO_8601) return void eb(b); if (b._f === a.RFC_2822) return void fb(b); b._a = [], n(b).empty = !0; var c, d, e, f, g, h = "" + b._i, i = h.length, j = 0; for (e = Y(b._f, b._locale).match(Ie) || [], c = 0; c < e.length; c++)f = e[c], d = (h.match($(f, b)) || [])[0], d && (g = h.substr(0, h.indexOf(d)), g.length > 0 && n(b).unusedInput.push(g), h = h.slice(h.indexOf(d) + d.length), j += d.length), Le[f] ? (d ? n(b).empty = !1 : n(b).unusedTokens.push(f), da(f, d, b)) : b._strict && !d && n(b).unusedTokens.push(f); n(b).charsLeftOver = i - j, h.length > 0 && n(b).unusedInput.push(h), b._a[gf] <= 12 && n(b).bigHour === !0 && b._a[gf] > 0 && (n(b).bigHour = void 0), n(b).parsedDateParts = b._a.slice(0), n(b).meridiem = b._meridiem, b._a[gf] = mb(b._locale, b._a[gf], b._meridiem), jb(b), db(b) } function mb(a, b, c) { var d; return null == c ? b : null != a.meridiemHour ? a.meridiemHour(b, c) : null != a.isPM ? (d = a.isPM(c), d && b < 12 && (b += 12), d || 12 !== b || (b = 0), b) : b } function nb(a) { var b, c, d, e, f; if (0 === a._f.length) return n(a).invalidFormat = !0, void (a._d = new Date(NaN)); for (e = 0; e < a._f.length; e++)f = 0, b = q({}, a), null != a._useUTC && (b._useUTC = a._useUTC), b._f = a._f[e], lb(b), o(b) && (f += n(b).charsLeftOver, f += 10 * n(b).unusedTokens.length, n(b).score = f, (null == d || f < d) && (d = f, c = b)); k(a, c || b) } function ob(a) { if (!a._d) { var b = L(a._i); a._a = i([b.year, b.month, b.day || b.date, b.hour, b.minute, b.second, b.millisecond], function (a) { return a && parseInt(a, 10) }), jb(a) } } function pb(a) { var b = new r(db(qb(a))); return b._nextDay && (b.add(1, "d"), b._nextDay = void 0), b } function qb(a) { var b = a._i, d = a._f; return a._locale = a._locale || bb(a._l), null === b || void 0 === d && "" === b ? p({ nullInput: !0 }) : ("string" == typeof b && (a._i = b = a._locale.preparse(b)), s(b) ? new r(db(b)) : (h(b) ? a._d = b : c(d) ? nb(a) : d ? lb(a) : rb(a), o(a) || (a._d = null), a)) } function rb(b) { var e = b._i; f(e) ? b._d = new Date(a.now()) : h(e) ? b._d = new Date(e.valueOf()) : "string" == typeof e ? gb(b) : c(e) ? (b._a = i(e.slice(0), function (a) { return parseInt(a, 10) }), jb(b)) : d(e) ? ob(b) : g(e) ? b._d = new Date(e) : a.createFromInputFallback(b) } function sb(a, b, f, g, h) { var i = {}; return f !== !0 && f !== !1 || (g = f, f = void 0), (d(a) && e(a) || c(a) && 0 === a.length) && (a = void 0), i._isAMomentObject = !0, i._useUTC = i._isUTC = h, i._l = f, i._i = a, i._f = b, i._strict = g, pb(i) } function tb(a, b, c, d) { return sb(a, b, c, d, !1) } function ub(a, b) { var d, e; if (1 === b.length && c(b[0]) && (b = b[0]), !b.length) return tb(); for (d = b[0], e = 1; e < b.length; ++e)b[e].isValid() && !b[e][a](d) || (d = b[e]); return d } function vb() { var a = [].slice.call(arguments, 0); return ub("isBefore", a) } function wb() { var a = [].slice.call(arguments, 0); return ub("isAfter", a) } function xb(a) { for (var b in a) if (Rf.indexOf(b) === -1 || null != a[b] && isNaN(a[b])) return !1; for (var c = !1, d = 0; d < Rf.length; ++d)if (a[Rf[d]]) { if (c) return !1; parseFloat(a[Rf[d]]) !== u(a[Rf[d]]) && (c = !0) } return !0 } function yb() { return this._isValid } function zb() { return Sb(NaN) } function Ab(a) { var b = L(a), c = b.year || 0, d = b.quarter || 0, e = b.month || 0, f = b.week || 0, g = b.day || 0, h = b.hour || 0, i = b.minute || 0, j = b.second || 0, k = b.millisecond || 0; this._isValid = xb(b), this._milliseconds = +k + 1e3 * j + 6e4 * i + 1e3 * h * 60 * 60, this._days = +g + 7 * f, this._months = +e + 3 * d + 12 * c, this._data = {}, this._locale = bb(), this._bubble() } function Bb(a) { return a instanceof Ab } function Cb(a) { return a < 0 ? Math.round(-1 * a) * -1 : Math.round(a) } function Db(a, b) { U(a, 0, 0, function () { var a = this.utcOffset(), c = "+"; return a < 0 && (a = -a, c = "-"), c + T(~~(a / 60), 2) + b + T(~~a % 60, 2) }) } function Eb(a, b) { var c = (b || "").match(a); if (null === c) return null; var d = c[c.length - 1] || [], e = (d + "").match(Sf) || ["-", 0, 0], f = +(60 * e[1]) + u(e[2]); return 0 === f ? 0 : "+" === e[0] ? f : -f } function Fb(b, c) { var d, e; return c._isUTC ? (d = c.clone(), e = (s(b) || h(b) ? b.valueOf() : tb(b).valueOf()) - d.valueOf(), d._d.setTime(d._d.valueOf() + e), a.updateOffset(d, !1), d) : tb(b).local() } function Gb(a) { return 15 * -Math.round(a._d.getTimezoneOffset() / 15) } function Hb(b, c, d) { var e, f = this._offset || 0; if (!this.isValid()) return null != b ? this : NaN; if (null != b) { if ("string" == typeof b) { if (b = Eb($e, b), null === b) return this } else Math.abs(b) < 16 && !d && (b = 60 * b); return !this._isUTC && c && (e = Gb(this)), this._offset = b, this._isUTC = !0, null != e && this.add(e, "m"), f !== b && (!c || this._changeInProgress ? Xb(this, Sb(b - f, "m"), 1, !1) : this._changeInProgress || (this._changeInProgress = !0, a.updateOffset(this, !0), this._changeInProgress = null)), this } return this._isUTC ? f : Gb(this) } function Ib(a, b) { return null != a ? ("string" != typeof a && (a = -a), this.utcOffset(a, b), this) : -this.utcOffset() } function Jb(a) { return this.utcOffset(0, a) } function Kb(a) { return this._isUTC && (this.utcOffset(0, a), this._isUTC = !1, a && this.subtract(Gb(this), "m")), this } function Lb() { if (null != this._tzm) this.utcOffset(this._tzm, !1, !0); else if ("string" == typeof this._i) { var a = Eb(Ze, this._i); null != a ? this.utcOffset(a) : this.utcOffset(0, !0) } return this } function Mb(a) { return !!this.isValid() && (a = a ? tb(a).utcOffset() : 0, (this.utcOffset() - a) % 60 === 0) } function Nb() { return this.utcOffset() > this.clone().month(0).utcOffset() || this.utcOffset() > this.clone().month(5).utcOffset() } function Ob() { if (!f(this._isDSTShifted)) return this._isDSTShifted; var a = {}; if (q(a, this), a = qb(a), a._a) { var b = a._isUTC ? l(a._a) : tb(a._a); this._isDSTShifted = this.isValid() && v(a._a, b.toArray()) > 0 } else this._isDSTShifted = !1; return this._isDSTShifted } function Pb() { return !!this.isValid() && !this._isUTC } function Qb() { return !!this.isValid() && this._isUTC } function Rb() { return !!this.isValid() && (this._isUTC && 0 === this._offset) } function Sb(a, b) { var c, d, e, f = a, h = null; return Bb(a) ? f = { ms: a._milliseconds, d: a._days, M: a._months } : g(a) ? (f = {}, b ? f[b] = a : f.milliseconds = a) : (h = Tf.exec(a)) ? (c = "-" === h[1] ? -1 : 1, f = { y: 0, d: u(h[ff]) * c, h: u(h[gf]) * c, m: u(h[hf]) * c, s: u(h[jf]) * c, ms: u(Cb(1e3 * h[kf])) * c }) : (h = Uf.exec(a)) ? (c = "-" === h[1] ? -1 : 1, f = { y: Tb(h[2], c), M: Tb(h[3], c), w: Tb(h[4], c), d: Tb(h[5], c), h: Tb(h[6], c), m: Tb(h[7], c), s: Tb(h[8], c) }) : null == f ? f = {} : "object" == typeof f && ("from" in f || "to" in f) && (e = Vb(tb(f.from), tb(f.to)), f = {}, f.ms = e.milliseconds, f.M = e.months), d = new Ab(f), Bb(a) && j(a, "_locale") && (d._locale = a._locale), d } function Tb(a, b) { var c = a && parseFloat(a.replace(",", ".")); return (isNaN(c) ? 0 : c) * b } function Ub(a, b) { var c = { milliseconds: 0, months: 0 }; return c.months = b.month() - a.month() + 12 * (b.year() - a.year()), a.clone().add(c.months, "M").isAfter(b) && --c.months, c.milliseconds = +b - +a.clone().add(c.months, "M"), c } function Vb(a, b) { var c; return a.isValid() && b.isValid() ? (b = Fb(b, a), a.isBefore(b) ? c = Ub(a, b) : (c = Ub(b, a), c.milliseconds = -c.milliseconds, c.months = -c.months), c) : { milliseconds: 0, months: 0 } } function Wb(a, b) { return function (c, d) { var e, f; return null === d || isNaN(+d) || (y(b, "moment()." + b + "(period, number) is deprecated. Please use moment()." + b + "(number, period). See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info."), f = c, c = d, d = f), c = "string" == typeof c ? +c : c, e = Sb(c, d), Xb(this, e, a), this } } function Xb(b, c, d, e) { var f = c._milliseconds, g = Cb(c._days), h = Cb(c._months); b.isValid() && (e = null == e || e, f && b._d.setTime(b._d.valueOf() + f * d), g && Q(b, "Date", P(b, "Date") + g * d), h && ja(b, P(b, "Month") + h * d), e && a.updateOffset(b, g || h)) } function Yb(a, b) { var c = a.diff(b, "days", !0); return c < -6 ? "sameElse" : c < -1 ? "lastWeek" : c < 0 ? "lastDay" : c < 1 ? "sameDay" : c < 2 ? "nextDay" : c < 7 ? "nextWeek" : "sameElse" } function Zb(b, c) { var d = b || tb(), e = Fb(d, this).startOf("day"), f = a.calendarFormat(this, e) || "sameElse", g = c && (z(c[f]) ? c[f].call(this, d) : c[f]); return this.format(g || this.localeData().calendar(f, this, tb(d))) } function $b() { return new r(this) } function _b(a, b) { var c = s(a) ? a : tb(a); return !(!this.isValid() || !c.isValid()) && (b = K(f(b) ? "millisecond" : b), "millisecond" === b ? this.valueOf() > c.valueOf() : c.valueOf() < this.clone().startOf(b).valueOf()) } function ac(a, b) { var c = s(a) ? a : tb(a); return !(!this.isValid() || !c.isValid()) && (b = K(f(b) ? "millisecond" : b), "millisecond" === b ? this.valueOf() < c.valueOf() : this.clone().endOf(b).valueOf() < c.valueOf()) } function bc(a, b, c, d) { return d = d || "()", ("(" === d[0] ? this.isAfter(a, c) : !this.isBefore(a, c)) && (")" === d[1] ? this.isBefore(b, c) : !this.isAfter(b, c)) } function cc(a, b) { var c, d = s(a) ? a : tb(a); return !(!this.isValid() || !d.isValid()) && (b = K(b || "millisecond"), "millisecond" === b ? this.valueOf() === d.valueOf() : (c = d.valueOf(), this.clone().startOf(b).valueOf() <= c && c <= this.clone().endOf(b).valueOf())) } function dc(a, b) { return this.isSame(a, b) || this.isAfter(a, b) } function ec(a, b) { return this.isSame(a, b) || this.isBefore(a, b) } function fc(a, b, c) { var d, e, f, g; return this.isValid() ? (d = Fb(a, this), d.isValid() ? (e = 6e4 * (d.utcOffset() - this.utcOffset()), b = K(b), "year" === b || "month" === b || "quarter" === b ? (g = gc(this, d), "quarter" === b ? g /= 3 : "year" === b && (g /= 12)) : (f = this - d, g = "second" === b ? f / 1e3 : "minute" === b ? f / 6e4 : "hour" === b ? f / 36e5 : "day" === b ? (f - e) / 864e5 : "week" === b ? (f - e) / 6048e5 : f), c ? g : t(g)) : NaN) : NaN } function gc(a, b) { var c, d, e = 12 * (b.year() - a.year()) + (b.month() - a.month()), f = a.clone().add(e, "months"); return b - f < 0 ? (c = a.clone().add(e - 1, "months"), d = (b - f) / (f - c)) : (c = a.clone().add(e + 1, "months"), d = (b - f) / (c - f)), -(e + d) || 0 } function hc() { return this.clone().locale("en").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ") } function ic() { if (!this.isValid()) return null; var a = this.clone().utc(); return a.year() < 0 || a.year() > 9999 ? X(a, "YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]") : z(Date.prototype.toISOString) ? this.toDate().toISOString() : X(a, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]") } function jc() { if (!this.isValid()) return "moment.invalid(/* " + this._i + " */)"; var a = "moment", b = ""; this.isLocal() || (a = 0 === this.utcOffset() ? "moment.utc" : "moment.parseZone", b = "Z"); var c = "[" + a + '("]', d = 0 <= this.year() && this.year() <= 9999 ? "YYYY" : "YYYYYY", e = "-MM-DD[T]HH:mm:ss.SSS", f = b + '[")]'; return this.format(c + d + e + f) } function kc(b) { b || (b = this.isUtc() ? a.defaultFormatUtc : a.defaultFormat); var c = X(this, b); return this.localeData().postformat(c) } function lc(a, b) { return this.isValid() && (s(a) && a.isValid() || tb(a).isValid()) ? Sb({ to: this, from: a }).locale(this.locale()).humanize(!b) : this.localeData().invalidDate() } function mc(a) { return this.from(tb(), a) } function nc(a, b) { return this.isValid() && (s(a) && a.isValid() || tb(a).isValid()) ? Sb({ from: this, to: a }).locale(this.locale()).humanize(!b) : this.localeData().invalidDate() } function oc(a) { return this.to(tb(), a) } function pc(a) { var b; return void 0 === a ? this._locale._abbr : (b = bb(a), null != b && (this._locale = b), this) } function qc() { return this._locale } function rc(a) { switch (a = K(a)) { case "year": this.month(0); case "quarter": case "month": this.date(1); case "week": case "isoWeek": case "day": case "date": this.hours(0); case "hour": this.minutes(0); case "minute": this.seconds(0); case "second": this.milliseconds(0) }return "week" === a && this.weekday(0), "isoWeek" === a && this.isoWeekday(1), "quarter" === a && this.month(3 * Math.floor(this.month() / 3)), this } function sc(a) { return a = K(a), void 0 === a || "millisecond" === a ? this : ("date" === a && (a = "day"), this.startOf(a).add(1, "isoWeek" === a ? "week" : a).subtract(1, "ms")) } function tc() { return this._d.valueOf() - 6e4 * (this._offset || 0) } function uc() { return Math.floor(this.valueOf() / 1e3) } function vc() { return new Date(this.valueOf()) } function wc() { var a = this; return [a.year(), a.month(), a.date(), a.hour(), a.minute(), a.second(), a.millisecond()] } function xc() { var a = this; return { years: a.year(), months: a.month(), date: a.date(), hours: a.hours(), minutes: a.minutes(), seconds: a.seconds(), milliseconds: a.milliseconds() } } function yc() { return this.isValid() ? this.toISOString() : null } function zc() { return o(this) } function Ac() {
		return k({}, n(this))
	} function Bc() { return n(this).overflow } function Cc() { return { input: this._i, format: this._f, locale: this._locale, isUTC: this._isUTC, strict: this._strict } } function Dc(a, b) { U(0, [a, a.length], 0, b) } function Ec(a) { return Ic.call(this, a, this.week(), this.weekday(), this.localeData()._week.dow, this.localeData()._week.doy) } function Fc(a) { return Ic.call(this, a, this.isoWeek(), this.isoWeekday(), 1, 4) } function Gc() { return xa(this.year(), 1, 4) } function Hc() { var a = this.localeData()._week; return xa(this.year(), a.dow, a.doy) } function Ic(a, b, c, d, e) { var f; return null == a ? wa(this, d, e).year : (f = xa(a, d, e), b > f && (b = f), Jc.call(this, a, b, c, d, e)) } function Jc(a, b, c, d, e) { var f = va(a, b, c, d, e), g = ta(f.year, 0, f.dayOfYear); return this.year(g.getUTCFullYear()), this.month(g.getUTCMonth()), this.date(g.getUTCDate()), this } function Kc(a) { return null == a ? Math.ceil((this.month() + 1) / 3) : this.month(3 * (a - 1) + this.month() % 3) } function Lc(a) { var b = Math.round((this.clone().startOf("day") - this.clone().startOf("year")) / 864e5) + 1; return null == a ? b : this.add(a - b, "d") } function Mc(a, b) { b[kf] = u(1e3 * ("0." + a)) } function Nc() { return this._isUTC ? "UTC" : "" } function Oc() { return this._isUTC ? "Coordinated Universal Time" : "" } function Pc(a) { return tb(1e3 * a) } function Qc() { return tb.apply(null, arguments).parseZone() } function Rc(a) { return a } function Sc(a, b, c, d) { var e = bb(), f = l().set(d, b); return e[c](f, a) } function Tc(a, b, c) { if (g(a) && (b = a, a = void 0), a = a || "", null != b) return Sc(a, b, c, "month"); var d, e = []; for (d = 0; d < 12; d++)e[d] = Sc(a, d, c, "month"); return e } function Uc(a, b, c, d) { "boolean" == typeof a ? (g(b) && (c = b, b = void 0), b = b || "") : (b = a, c = b, a = !1, g(b) && (c = b, b = void 0), b = b || ""); var e = bb(), f = a ? e._week.dow : 0; if (null != c) return Sc(b, (c + f) % 7, d, "day"); var h, i = []; for (h = 0; h < 7; h++)i[h] = Sc(b, (h + f) % 7, d, "day"); return i } function Vc(a, b) { return Tc(a, b, "months") } function Wc(a, b) { return Tc(a, b, "monthsShort") } function Xc(a, b, c) { return Uc(a, b, c, "weekdays") } function Yc(a, b, c) { return Uc(a, b, c, "weekdaysShort") } function Zc(a, b, c) { return Uc(a, b, c, "weekdaysMin") } function $c() { var a = this._data; return this._milliseconds = dg(this._milliseconds), this._days = dg(this._days), this._months = dg(this._months), a.milliseconds = dg(a.milliseconds), a.seconds = dg(a.seconds), a.minutes = dg(a.minutes), a.hours = dg(a.hours), a.months = dg(a.months), a.years = dg(a.years), this } function _c(a, b, c, d) { var e = Sb(b, c); return a._milliseconds += d * e._milliseconds, a._days += d * e._days, a._months += d * e._months, a._bubble() } function ad(a, b) { return _c(this, a, b, 1) } function bd(a, b) { return _c(this, a, b, -1) } function cd(a) { return a < 0 ? Math.floor(a) : Math.ceil(a) } function dd() { var a, b, c, d, e, f = this._milliseconds, g = this._days, h = this._months, i = this._data; return f >= 0 && g >= 0 && h >= 0 || f <= 0 && g <= 0 && h <= 0 || (f += 864e5 * cd(fd(h) + g), g = 0, h = 0), i.milliseconds = f % 1e3, a = t(f / 1e3), i.seconds = a % 60, b = t(a / 60), i.minutes = b % 60, c = t(b / 60), i.hours = c % 24, g += t(c / 24), e = t(ed(g)), h += e, g -= cd(fd(e)), d = t(h / 12), h %= 12, i.days = g, i.months = h, i.years = d, this } function ed(a) { return 4800 * a / 146097 } function fd(a) { return 146097 * a / 4800 } function gd(a) { if (!this.isValid()) return NaN; var b, c, d = this._milliseconds; if (a = K(a), "month" === a || "year" === a) return b = this._days + d / 864e5, c = this._months + ed(b), "month" === a ? c : c / 12; switch (b = this._days + Math.round(fd(this._months)), a) { case "week": return b / 7 + d / 6048e5; case "day": return b + d / 864e5; case "hour": return 24 * b + d / 36e5; case "minute": return 1440 * b + d / 6e4; case "second": return 86400 * b + d / 1e3; case "millisecond": return Math.floor(864e5 * b) + d; default: throw new Error("Unknown unit " + a) } } function hd() { return this.isValid() ? this._milliseconds + 864e5 * this._days + this._months % 12 * 2592e6 + 31536e6 * u(this._months / 12) : NaN } function id(a) { return function () { return this.as(a) } } function jd(a) { return a = K(a), this.isValid() ? this[a + "s"]() : NaN } function kd(a) { return function () { return this.isValid() ? this._data[a] : NaN } } function ld() { return t(this.days() / 7) } function md(a, b, c, d, e) { return e.relativeTime(b || 1, !!c, a, d) } function nd(a, b, c) { var d = Sb(a).abs(), e = tg(d.as("s")), f = tg(d.as("m")), g = tg(d.as("h")), h = tg(d.as("d")), i = tg(d.as("M")), j = tg(d.as("y")), k = e <= ug.ss && ["s", e] || e < ug.s && ["ss", e] || f <= 1 && ["m"] || f < ug.m && ["mm", f] || g <= 1 && ["h"] || g < ug.h && ["hh", g] || h <= 1 && ["d"] || h < ug.d && ["dd", h] || i <= 1 && ["M"] || i < ug.M && ["MM", i] || j <= 1 && ["y"] || ["yy", j]; return k[2] = b, k[3] = +a > 0, k[4] = c, md.apply(null, k) } function od(a) { return void 0 === a ? tg : "function" == typeof a && (tg = a, !0) } function pd(a, b) { return void 0 !== ug[a] && (void 0 === b ? ug[a] : (ug[a] = b, "s" === a && (ug.ss = b - 1), !0)) } function qd(a) { if (!this.isValid()) return this.localeData().invalidDate(); var b = this.localeData(), c = nd(this, !a, b); return a && (c = b.pastFuture(+this, c)), b.postformat(c) } function rd() { if (!this.isValid()) return this.localeData().invalidDate(); var a, b, c, d = vg(this._milliseconds) / 1e3, e = vg(this._days), f = vg(this._months); a = t(d / 60), b = t(a / 60), d %= 60, a %= 60, c = t(f / 12), f %= 12; var g = c, h = f, i = e, j = b, k = a, l = d, m = this.asSeconds(); return m ? (m < 0 ? "-" : "") + "P" + (g ? g + "Y" : "") + (h ? h + "M" : "") + (i ? i + "D" : "") + (j || k || l ? "T" : "") + (j ? j + "H" : "") + (k ? k + "M" : "") + (l ? l + "S" : "") : "P0D" }
	//! moment.js locale configuration
	//! locale : Belarusian [be]
	//! author : Dmitry Demidov : https://github.com/demidov91
	//! author: Praleska: http://praleska.pro/
	//! Author : Menelion Elensúle : https://github.com/Oire
	function sd(a, b) { var c = a.split("_"); return b % 10 === 1 && b % 100 !== 11 ? c[0] : b % 10 >= 2 && b % 10 <= 4 && (b % 100 < 10 || b % 100 >= 20) ? c[1] : c[2] } function td(a, b, c) { var d = { mm: b ? "хвіліна_хвіліны_хвілін" : "хвіліну_хвіліны_хвілін", hh: b ? "гадзіна_гадзіны_гадзін" : "гадзіну_гадзіны_гадзін", dd: "дзень_дні_дзён", MM: "месяц_месяцы_месяцаў", yy: "год_гады_гадоў" }; return "m" === c ? b ? "хвіліна" : "хвіліну" : "h" === c ? b ? "гадзіна" : "гадзіну" : a + " " + sd(d[c], +a) }
	//! moment.js locale configuration
	//! locale : Breton [br]
	//! author : Jean-Baptiste Le Duigou : https://github.com/jbleduigou
	function ud(a, b, c) { var d = { mm: "munutenn", MM: "miz", dd: "devezh" }; return a + " " + xd(d[c], a) } function vd(a) { switch (wd(a)) { case 1: case 3: case 4: case 5: case 9: return a + " bloaz"; default: return a + " vloaz" } } function wd(a) { return a > 9 ? wd(a % 10) : a } function xd(a, b) { return 2 === b ? yd(a) : a } function yd(a) { var b = { m: "v", b: "v", d: "z" }; return void 0 === b[a.charAt(0)] ? a : b[a.charAt(0)] + a.substring(1) }
	//! moment.js locale configuration
	//! locale : Bosnian [bs]
	//! author : Nedim Cholich : https://github.com/frontyard
	//! based on (hr) translation by Bojan Marković
	function zd(a, b, c) { var d = a + " "; switch (c) { case "m": return b ? "jedna minuta" : "jedne minute"; case "mm": return d += 1 === a ? "minuta" : 2 === a || 3 === a || 4 === a ? "minute" : "minuta"; case "h": return b ? "jedan sat" : "jednog sata"; case "hh": return d += 1 === a ? "sat" : 2 === a || 3 === a || 4 === a ? "sata" : "sati"; case "dd": return d += 1 === a ? "dan" : "dana"; case "MM": return d += 1 === a ? "mjesec" : 2 === a || 3 === a || 4 === a ? "mjeseca" : "mjeseci"; case "yy": return d += 1 === a ? "godina" : 2 === a || 3 === a || 4 === a ? "godine" : "godina" } } function Ad(a) { return a > 1 && a < 5 && 1 !== ~~(a / 10) } function Bd(a, b, c, d) { var e = a + " "; switch (c) { case "s": return b || d ? "pár sekund" : "pár sekundami"; case "m": return b ? "minuta" : d ? "minutu" : "minutou"; case "mm": return b || d ? e + (Ad(a) ? "minuty" : "minut") : e + "minutami"; break; case "h": return b ? "hodina" : d ? "hodinu" : "hodinou"; case "hh": return b || d ? e + (Ad(a) ? "hodiny" : "hodin") : e + "hodinami"; break; case "d": return b || d ? "den" : "dnem"; case "dd": return b || d ? e + (Ad(a) ? "dny" : "dní") : e + "dny"; break; case "M": return b || d ? "měsíc" : "měsícem"; case "MM": return b || d ? e + (Ad(a) ? "měsíce" : "měsíců") : e + "měsíci"; break; case "y": return b || d ? "rok" : "rokem"; case "yy": return b || d ? e + (Ad(a) ? "roky" : "let") : e + "lety" } }
	//! moment.js locale configuration
	//! locale : German (Austria) [de-at]
	//! author : lluchs : https://github.com/lluchs
	//! author: Menelion Elensúle: https://github.com/Oire
	//! author : Martin Groller : https://github.com/MadMG
	//! author : Mikolaj Dadela : https://github.com/mik01aj
	function Cd(a, b, c, d) { var e = { m: ["eine Minute", "einer Minute"], h: ["eine Stunde", "einer Stunde"], d: ["ein Tag", "einem Tag"], dd: [a + " Tage", a + " Tagen"], M: ["ein Monat", "einem Monat"], MM: [a + " Monate", a + " Monaten"], y: ["ein Jahr", "einem Jahr"], yy: [a + " Jahre", a + " Jahren"] }; return b ? e[c][0] : e[c][1] }
	//! moment.js locale configuration
	//! locale : German (Switzerland) [de-ch]
	//! author : sschueller : https://github.com/sschueller
	function Dd(a, b, c, d) { var e = { m: ["eine Minute", "einer Minute"], h: ["eine Stunde", "einer Stunde"], d: ["ein Tag", "einem Tag"], dd: [a + " Tage", a + " Tagen"], M: ["ein Monat", "einem Monat"], MM: [a + " Monate", a + " Monaten"], y: ["ein Jahr", "einem Jahr"], yy: [a + " Jahre", a + " Jahren"] }; return b ? e[c][0] : e[c][1] }
	//! moment.js locale configuration
	//! locale : German [de]
	//! author : lluchs : https://github.com/lluchs
	//! author: Menelion Elensúle: https://github.com/Oire
	//! author : Mikolaj Dadela : https://github.com/mik01aj
	function Ed(a, b, c, d) { var e = { m: ["eine Minute", "einer Minute"], h: ["eine Stunde", "einer Stunde"], d: ["ein Tag", "einem Tag"], dd: [a + " Tage", a + " Tagen"], M: ["ein Monat", "einem Monat"], MM: [a + " Monate", a + " Monaten"], y: ["ein Jahr", "einem Jahr"], yy: [a + " Jahre", a + " Jahren"] }; return b ? e[c][0] : e[c][1] }
	//! moment.js locale configuration
	//! locale : Estonian [et]
	//! author : Henry Kehlmann : https://github.com/madhenry
	//! improvements : Illimar Tambek : https://github.com/ragulka
	function Fd(a, b, c, d) { var e = { s: ["mõne sekundi", "mõni sekund", "paar sekundit"], m: ["ühe minuti", "üks minut"], mm: [a + " minuti", a + " minutit"], h: ["ühe tunni", "tund aega", "üks tund"], hh: [a + " tunni", a + " tundi"], d: ["ühe päeva", "üks päev"], M: ["kuu aja", "kuu aega", "üks kuu"], MM: [a + " kuu", a + " kuud"], y: ["ühe aasta", "aasta", "üks aasta"], yy: [a + " aasta", a + " aastat"] }; return b ? e[c][2] ? e[c][2] : e[c][1] : d ? e[c][0] : e[c][1] } function Gd(a, b, c, d) { var e = ""; switch (c) { case "s": return d ? "muutaman sekunnin" : "muutama sekunti"; case "m": return d ? "minuutin" : "minuutti"; case "mm": e = d ? "minuutin" : "minuuttia"; break; case "h": return d ? "tunnin" : "tunti"; case "hh": e = d ? "tunnin" : "tuntia"; break; case "d": return d ? "päivän" : "päivä"; case "dd": e = d ? "päivän" : "päivää"; break; case "M": return d ? "kuukauden" : "kuukausi"; case "MM": e = d ? "kuukauden" : "kuukautta"; break; case "y": return d ? "vuoden" : "vuosi"; case "yy": e = d ? "vuoden" : "vuotta" }return e = Hd(a, d) + " " + e } function Hd(a, b) { return a < 10 ? b ? $g[a] : Zg[a] : a }
	//! moment.js locale configuration
	//! locale : Konkani Latin script [gom-latn]
	//! author : The Discoverer : https://github.com/WikiDiscoverer
	function Id(a, b, c, d) { var e = { s: ["thodde secondanim", "thodde second"], m: ["eka mintan", "ek minute"], mm: [a + " mintanim", a + " mintam"], h: ["eka horan", "ek hor"], hh: [a + " horanim", a + " hor"], d: ["eka disan", "ek dis"], dd: [a + " disanim", a + " dis"], M: ["eka mhoinean", "ek mhoino"], MM: [a + " mhoineanim", a + " mhoine"], y: ["eka vorsan", "ek voros"], yy: [a + " vorsanim", a + " vorsam"] }; return b ? e[c][0] : e[c][1] }
	//! moment.js locale configuration
	//! locale : Croatian [hr]
	//! author : Bojan Marković : https://github.com/bmarkovic
	function Jd(a, b, c) { var d = a + " "; switch (c) { case "m": return b ? "jedna minuta" : "jedne minute"; case "mm": return d += 1 === a ? "minuta" : 2 === a || 3 === a || 4 === a ? "minute" : "minuta"; case "h": return b ? "jedan sat" : "jednog sata"; case "hh": return d += 1 === a ? "sat" : 2 === a || 3 === a || 4 === a ? "sata" : "sati"; case "dd": return d += 1 === a ? "dan" : "dana"; case "MM": return d += 1 === a ? "mjesec" : 2 === a || 3 === a || 4 === a ? "mjeseca" : "mjeseci"; case "yy": return d += 1 === a ? "godina" : 2 === a || 3 === a || 4 === a ? "godine" : "godina" } } function Kd(a, b, c, d) { var e = a; switch (c) { case "s": return d || b ? "néhány másodperc" : "néhány másodperce"; case "m": return "egy" + (d || b ? " perc" : " perce"); case "mm": return e + (d || b ? " perc" : " perce"); case "h": return "egy" + (d || b ? " óra" : " órája"); case "hh": return e + (d || b ? " óra" : " órája"); case "d": return "egy" + (d || b ? " nap" : " napja"); case "dd": return e + (d || b ? " nap" : " napja"); case "M": return "egy" + (d || b ? " hónap" : " hónapja"); case "MM": return e + (d || b ? " hónap" : " hónapja"); case "y": return "egy" + (d || b ? " év" : " éve"); case "yy": return e + (d || b ? " év" : " éve") }return "" } function Ld(a) { return (a ? "" : "[múlt] ") + "[" + ih[this.day()] + "] LT[-kor]" }
	//! moment.js locale configuration
	//! locale : Icelandic [is]
	//! author : Hinrik Örn Sigurðsson : https://github.com/hinrik
	function Md(a) { return a % 100 === 11 || a % 10 !== 1 } function Nd(a, b, c, d) { var e = a + " "; switch (c) { case "s": return b || d ? "nokkrar sekúndur" : "nokkrum sekúndum"; case "m": return b ? "mínúta" : "mínútu"; case "mm": return Md(a) ? e + (b || d ? "mínútur" : "mínútum") : b ? e + "mínúta" : e + "mínútu"; case "hh": return Md(a) ? e + (b || d ? "klukkustundir" : "klukkustundum") : e + "klukkustund"; case "d": return b ? "dagur" : d ? "dag" : "degi"; case "dd": return Md(a) ? b ? e + "dagar" : e + (d ? "daga" : "dögum") : b ? e + "dagur" : e + (d ? "dag" : "degi"); case "M": return b ? "mánuður" : d ? "mánuð" : "mánuði"; case "MM": return Md(a) ? b ? e + "mánuðir" : e + (d ? "mánuði" : "mánuðum") : b ? e + "mánuður" : e + (d ? "mánuð" : "mánuði"); case "y": return b || d ? "ár" : "ári"; case "yy": return Md(a) ? e + (b || d ? "ár" : "árum") : e + (b || d ? "ár" : "ári") } }
	//! moment.js locale configuration
	//! locale : Luxembourgish [lb]
	//! author : mweimerskirch : https://github.com/mweimerskirch
	//! author : David Raison : https://github.com/kwisatz
	function Od(a, b, c, d) { var e = { m: ["eng Minutt", "enger Minutt"], h: ["eng Stonn", "enger Stonn"], d: ["een Dag", "engem Dag"], M: ["ee Mount", "engem Mount"], y: ["ee Joer", "engem Joer"] }; return b ? e[c][0] : e[c][1] } function Pd(a) { var b = a.substr(0, a.indexOf(" ")); return Rd(b) ? "a " + a : "an " + a } function Qd(a) { var b = a.substr(0, a.indexOf(" ")); return Rd(b) ? "viru " + a : "virun " + a } function Rd(a) { if (a = parseInt(a, 10), isNaN(a)) return !1; if (a < 0) return !0; if (a < 10) return 4 <= a && a <= 7; if (a < 100) { var b = a % 10, c = a / 10; return Rd(0 === b ? c : b) } if (a < 1e4) { for (; a >= 10;)a /= 10; return Rd(a) } return a /= 1e3, Rd(a) } function Sd(a, b, c, d) { return b ? "kelios sekundės" : d ? "kelių sekundžių" : "kelias sekundes" } function Td(a, b, c, d) { return b ? Vd(c)[0] : d ? Vd(c)[1] : Vd(c)[2] } function Ud(a) { return a % 10 === 0 || a > 10 && a < 20 } function Vd(a) { return nh[a].split("_") } function Wd(a, b, c, d) { var e = a + " "; return 1 === a ? e + Td(a, b, c[0], d) : b ? e + (Ud(a) ? Vd(c)[1] : Vd(c)[0]) : d ? e + Vd(c)[1] : e + (Ud(a) ? Vd(c)[1] : Vd(c)[2]) } function Xd(a, b, c) { return c ? b % 10 === 1 && b % 100 !== 11 ? a[2] : a[3] : b % 10 === 1 && b % 100 !== 11 ? a[0] : a[1] } function Yd(a, b, c) { return a + " " + Xd(oh[c], a, b) } function Zd(a, b, c) { return Xd(oh[c], a, b) } function $d(a, b) { return b ? "dažas sekundes" : "dažām sekundēm" } function _d(a, b, c, d) { var e = ""; if (b) switch (c) { case "s": e = "काही सेकंद"; break; case "m": e = "एक मिनिट"; break; case "mm": e = "%d मिनिटे"; break; case "h": e = "एक तास"; break; case "hh": e = "%d तास"; break; case "d": e = "एक दिवस"; break; case "dd": e = "%d दिवस"; break; case "M": e = "एक महिना"; break; case "MM": e = "%d महिने"; break; case "y": e = "एक वर्ष"; break; case "yy": e = "%d वर्षे" } else switch (c) { case "s": e = "काही सेकंदां"; break; case "m": e = "एका मिनिटा"; break; case "mm": e = "%d मिनिटां"; break; case "h": e = "एका तासा"; break; case "hh": e = "%d तासां"; break; case "d": e = "एका दिवसा"; break; case "dd": e = "%d दिवसां"; break; case "M": e = "एका महिन्या"; break; case "MM": e = "%d महिन्यां"; break; case "y": e = "एका वर्षा"; break; case "yy": e = "%d वर्षां" }return e.replace(/%d/i, a) } function ae(a) { return a % 10 < 5 && a % 10 > 1 && ~~(a / 10) % 10 !== 1 } function be(a, b, c) { var d = a + " "; switch (c) { case "m": return b ? "minuta" : "minutę"; case "mm": return d + (ae(a) ? "minuty" : "minut"); case "h": return b ? "godzina" : "godzinę"; case "hh": return d + (ae(a) ? "godziny" : "godzin"); case "MM": return d + (ae(a) ? "miesiące" : "miesięcy"); case "yy": return d + (ae(a) ? "lata" : "lat") } }
	//! moment.js locale configuration
	//! locale : Romanian [ro]
	//! author : Vlad Gurdiga : https://github.com/gurdiga
	//! author : Valentin Agachi : https://github.com/avaly
	function ce(a, b, c) { var d = { mm: "minute", hh: "ore", dd: "zile", MM: "luni", yy: "ani" }, e = " "; return (a % 100 >= 20 || a >= 100 && a % 100 === 0) && (e = " de "), a + e + d[c] }
	//! moment.js locale configuration
	//! locale : Russian [ru]
	//! author : Viktorminator : https://github.com/Viktorminator
	//! Author : Menelion Elensúle : https://github.com/Oire
	//! author : Коренберг Марк : https://github.com/socketpair
	function de(a, b) { var c = a.split("_"); return b % 10 === 1 && b % 100 !== 11 ? c[0] : b % 10 >= 2 && b % 10 <= 4 && (b % 100 < 10 || b % 100 >= 20) ? c[1] : c[2] } function ee(a, b, c) { var d = { mm: b ? "минута_минуты_минут" : "минуту_минуты_минут", hh: "час_часа_часов", dd: "день_дня_дней", MM: "месяц_месяца_месяцев", yy: "год_года_лет" }; return "m" === c ? b ? "минута" : "минуту" : a + " " + de(d[c], +a) } function fe(a) { return a > 1 && a < 5 } function ge(a, b, c, d) { var e = a + " "; switch (c) { case "s": return b || d ? "pár sekúnd" : "pár sekundami"; case "m": return b ? "minúta" : d ? "minútu" : "minútou"; case "mm": return b || d ? e + (fe(a) ? "minúty" : "minút") : e + "minútami"; break; case "h": return b ? "hodina" : d ? "hodinu" : "hodinou"; case "hh": return b || d ? e + (fe(a) ? "hodiny" : "hodín") : e + "hodinami"; break; case "d": return b || d ? "deň" : "dňom"; case "dd": return b || d ? e + (fe(a) ? "dni" : "dní") : e + "dňami"; break; case "M": return b || d ? "mesiac" : "mesiacom"; case "MM": return b || d ? e + (fe(a) ? "mesiace" : "mesiacov") : e + "mesiacmi"; break; case "y": return b || d ? "rok" : "rokom"; case "yy": return b || d ? e + (fe(a) ? "roky" : "rokov") : e + "rokmi" } }
	//! moment.js locale configuration
	//! locale : Slovenian [sl]
	//! author : Robert Sedovšek : https://github.com/sedovsek
	function he(a, b, c, d) { var e = a + " "; switch (c) { case "s": return b || d ? "nekaj sekund" : "nekaj sekundami"; case "m": return b ? "ena minuta" : "eno minuto"; case "mm": return e += 1 === a ? b ? "minuta" : "minuto" : 2 === a ? b || d ? "minuti" : "minutama" : a < 5 ? b || d ? "minute" : "minutami" : b || d ? "minut" : "minutami"; case "h": return b ? "ena ura" : "eno uro"; case "hh": return e += 1 === a ? b ? "ura" : "uro" : 2 === a ? b || d ? "uri" : "urama" : a < 5 ? b || d ? "ure" : "urami" : b || d ? "ur" : "urami"; case "d": return b || d ? "en dan" : "enim dnem"; case "dd": return e += 1 === a ? b || d ? "dan" : "dnem" : 2 === a ? b || d ? "dni" : "dnevoma" : b || d ? "dni" : "dnevi"; case "M": return b || d ? "en mesec" : "enim mesecem"; case "MM": return e += 1 === a ? b || d ? "mesec" : "mesecem" : 2 === a ? b || d ? "meseca" : "mesecema" : a < 5 ? b || d ? "mesece" : "meseci" : b || d ? "mesecev" : "meseci"; case "y": return b || d ? "eno leto" : "enim letom"; case "yy": return e += 1 === a ? b || d ? "leto" : "letom" : 2 === a ? b || d ? "leti" : "letoma" : a < 5 ? b || d ? "leta" : "leti" : b || d ? "let" : "leti" } } function ie(a) { var b = a; return b = a.indexOf("jaj") !== -1 ? b.slice(0, -3) + "leS" : a.indexOf("jar") !== -1 ? b.slice(0, -3) + "waQ" : a.indexOf("DIS") !== -1 ? b.slice(0, -3) + "nem" : b + " pIq" } function je(a) { var b = a; return b = a.indexOf("jaj") !== -1 ? b.slice(0, -3) + "Hu’" : a.indexOf("jar") !== -1 ? b.slice(0, -3) + "wen" : a.indexOf("DIS") !== -1 ? b.slice(0, -3) + "ben" : b + " ret" } function ke(a, b, c, d) { var e = le(a); switch (c) { case "mm": return e + " tup"; case "hh": return e + " rep"; case "dd": return e + " jaj"; case "MM": return e + " jar"; case "yy": return e + " DIS" } } function le(a) { var b = Math.floor(a % 1e3 / 100), c = Math.floor(a % 100 / 10), d = a % 10, e = ""; return b > 0 && (e += Rh[b] + "vatlh"), c > 0 && (e += ("" !== e ? " " : "") + Rh[c] + "maH"), d > 0 && (e += ("" !== e ? " " : "") + Rh[d]), "" === e ? "pagh" : e } function me(a, b, c, d) { var e = { s: ["viensas secunds", "'iensas secunds"], m: ["'n míut", "'iens míut"], mm: [a + " míuts", "" + a + " míuts"], h: ["'n þora", "'iensa þora"], hh: [a + " þoras", "" + a + " þoras"], d: ["'n ziua", "'iensa ziua"], dd: [a + " ziuas", "" + a + " ziuas"], M: ["'n mes", "'iens mes"], MM: [a + " mesen", "" + a + " mesen"], y: ["'n ar", "'iens ar"], yy: [a + " ars", "" + a + " ars"] }; return d ? e[c][0] : b ? e[c][0] : e[c][1] }
	//! moment.js locale configuration
	//! locale : Ukrainian [uk]
	//! author : zemlanin : https://github.com/zemlanin
	//! Author : Menelion Elensúle : https://github.com/Oire
	function ne(a, b) { var c = a.split("_"); return b % 10 === 1 && b % 100 !== 11 ? c[0] : b % 10 >= 2 && b % 10 <= 4 && (b % 100 < 10 || b % 100 >= 20) ? c[1] : c[2] } function oe(a, b, c) { var d = { mm: b ? "хвилина_хвилини_хвилин" : "хвилину_хвилини_хвилин", hh: b ? "година_години_годин" : "годину_години_годин", dd: "день_дні_днів", MM: "місяць_місяці_місяців", yy: "рік_роки_років" }; return "m" === c ? b ? "хвилина" : "хвилину" : "h" === c ? b ? "година" : "годину" : a + " " + ne(d[c], +a) } function pe(a, b) { var c = { nominative: "неділя_понеділок_вівторок_середа_четвер_п’ятниця_субота".split("_"), accusative: "неділю_понеділок_вівторок_середу_четвер_п’ятницю_суботу".split("_"), genitive: "неділі_понеділка_вівторка_середи_четверга_п’ятниці_суботи".split("_") }; if (!a) return c.nominative; var d = /(\[[ВвУу]\]) ?dddd/.test(b) ? "accusative" : /\[?(?:минулої|наступної)? ?\] ?dddd/.test(b) ? "genitive" : "nominative"; return c[d][a.day()] } function qe(a) { return function () { return a + "о" + (11 === this.hours() ? "б" : "") + "] LT" } } var re, se; se = Array.prototype.some ? Array.prototype.some : function (a) { for (var b = Object(this), c = b.length >>> 0, d = 0; d < c; d++)if (d in b && a.call(this, b[d], d, b)) return !0; return !1 }; var te = se, ue = a.momentProperties = [], ve = !1, we = {}; a.suppressDeprecationWarnings = !1, a.deprecationHandler = null; var xe; xe = Object.keys ? Object.keys : function (a) { var b, c = []; for (b in a) j(a, b) && c.push(b); return c }; var ye, ze = xe, Ae = { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, Be = { LTS: "h:mm:ss A", LT: "h:mm A", L: "MM/DD/YYYY", LL: "MMMM D, YYYY", LLL: "MMMM D, YYYY h:mm A", LLLL: "dddd, MMMM D, YYYY h:mm A" }, Ce = "Invalid date", De = "%d", Ee = /\d{1,2}/, Fe = { future: "in %s", past: "%s ago", s: "a few seconds", ss: "%d seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, Ge = {}, He = {}, Ie = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g, Je = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g, Ke = {}, Le = {}, Me = /\d/, Ne = /\d\d/, Oe = /\d{3}/, Pe = /\d{4}/, Qe = /[+-]?\d{6}/, Re = /\d\d?/, Se = /\d\d\d\d?/, Te = /\d\d\d\d\d\d?/, Ue = /\d{1,3}/, Ve = /\d{1,4}/, We = /[+-]?\d{1,6}/, Xe = /\d+/, Ye = /[+-]?\d+/, Ze = /Z|[+-]\d\d:?\d\d/gi, $e = /Z|[+-]\d\d(?::?\d\d)?/gi, _e = /[+-]?\d+(\.\d{1,3})?/, af = /[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i, bf = {}, cf = {}, df = 0, ef = 1, ff = 2, gf = 3, hf = 4, jf = 5, kf = 6, lf = 7, mf = 8; ye = Array.prototype.indexOf ? Array.prototype.indexOf : function (a) { var b; for (b = 0; b < this.length; ++b)if (this[b] === a) return b; return -1 }; var nf = ye; U("M", ["MM", 2], "Mo", function () { return this.month() + 1 }), U("MMM", 0, 0, function (a) { return this.localeData().monthsShort(this, a) }), U("MMMM", 0, 0, function (a) { return this.localeData().months(this, a) }), J("month", "M"), M("month", 8), Z("M", Re), Z("MM", Re, Ne), Z("MMM", function (a, b) { return b.monthsShortRegex(a) }), Z("MMMM", function (a, b) { return b.monthsRegex(a) }), ba(["M", "MM"], function (a, b) { b[ef] = u(a) - 1 }), ba(["MMM", "MMMM"], function (a, b, c, d) { var e = c._locale.monthsParse(a, d, c._strict); null != e ? b[ef] = e : n(c).invalidMonth = a }); var of = /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/, pf = "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), qf = "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), rf = af, sf = af; U("Y", 0, 0, function () { var a = this.year(); return a <= 9999 ? "" + a : "+" + a }), U(0, ["YY", 2], 0, function () { return this.year() % 100 }), U(0, ["YYYY", 4], 0, "year"), U(0, ["YYYYY", 5], 0, "year"), U(0, ["YYYYYY", 6, !0], 0, "year"), J("year", "y"), M("year", 1), Z("Y", Ye), Z("YY", Re, Ne), Z("YYYY", Ve, Pe), Z("YYYYY", We, Qe), Z("YYYYYY", We, Qe), ba(["YYYYY", "YYYYYY"], df), ba("YYYY", function (b, c) { c[df] = 2 === b.length ? a.parseTwoDigitYear(b) : u(b) }), ba("YY", function (b, c) { c[df] = a.parseTwoDigitYear(b) }), ba("Y", function (a, b) { b[df] = parseInt(a, 10) }), a.parseTwoDigitYear = function (a) { return u(a) + (u(a) > 68 ? 1900 : 2e3) }; var tf = O("FullYear", !0); U("w", ["ww", 2], "wo", "week"), U("W", ["WW", 2], "Wo", "isoWeek"), J("week", "w"), J("isoWeek", "W"), M("week", 5), M("isoWeek", 5), Z("w", Re), Z("ww", Re, Ne), Z("W", Re), Z("WW", Re, Ne), ca(["w", "ww", "W", "WW"], function (a, b, c, d) { b[d.substr(0, 1)] = u(a) }); var uf = { dow: 0, doy: 6 }; U("d", 0, "do", "day"), U("dd", 0, 0, function (a) { return this.localeData().weekdaysMin(this, a) }), U("ddd", 0, 0, function (a) { return this.localeData().weekdaysShort(this, a) }), U("dddd", 0, 0, function (a) { return this.localeData().weekdays(this, a) }), U("e", 0, 0, "weekday"), U("E", 0, 0, "isoWeekday"), J("day", "d"), J("weekday", "e"), J("isoWeekday", "E"), M("day", 11), M("weekday", 11), M("isoWeekday", 11), Z("d", Re), Z("e", Re), Z("E", Re), Z("dd", function (a, b) { return b.weekdaysMinRegex(a) }), Z("ddd", function (a, b) { return b.weekdaysShortRegex(a) }), Z("dddd", function (a, b) { return b.weekdaysRegex(a) }), ca(["dd", "ddd", "dddd"], function (a, b, c, d) { var e = c._locale.weekdaysParse(a, d, c._strict); null != e ? b.d = e : n(c).invalidWeekday = a }), ca(["d", "e", "E"], function (a, b, c, d) { b[d] = u(a) }); var vf = "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), wf = "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), xf = "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), yf = af, zf = af, Af = af; U("H", ["HH", 2], 0, "hour"), U("h", ["hh", 2], 0, Ra), U("k", ["kk", 2], 0, Sa), U("hmm", 0, 0, function () { return "" + Ra.apply(this) + T(this.minutes(), 2) }), U("hmmss", 0, 0, function () { return "" + Ra.apply(this) + T(this.minutes(), 2) + T(this.seconds(), 2) }), U("Hmm", 0, 0, function () { return "" + this.hours() + T(this.minutes(), 2) }), U("Hmmss", 0, 0, function () { return "" + this.hours() + T(this.minutes(), 2) + T(this.seconds(), 2) }), Ta("a", !0), Ta("A", !1), J("hour", "h"), M("hour", 13), Z("a", Ua), Z("A", Ua), Z("H", Re), Z("h", Re), Z("k", Re), Z("HH", Re, Ne), Z("hh", Re, Ne), Z("kk", Re, Ne), Z("hmm", Se), Z("hmmss", Te), Z("Hmm", Se), Z("Hmmss", Te), ba(["H", "HH"], gf), ba(["k", "kk"], function (a, b, c) { var d = u(a); b[gf] = 24 === d ? 0 : d }), ba(["a", "A"], function (a, b, c) { c._isPm = c._locale.isPM(a), c._meridiem = a }), ba(["h", "hh"], function (a, b, c) { b[gf] = u(a), n(c).bigHour = !0 }), ba("hmm", function (a, b, c) { var d = a.length - 2; b[gf] = u(a.substr(0, d)), b[hf] = u(a.substr(d)), n(c).bigHour = !0 }), ba("hmmss", function (a, b, c) { var d = a.length - 4, e = a.length - 2; b[gf] = u(a.substr(0, d)), b[hf] = u(a.substr(d, 2)), b[jf] = u(a.substr(e)), n(c).bigHour = !0 }), ba("Hmm", function (a, b, c) { var d = a.length - 2; b[gf] = u(a.substr(0, d)), b[hf] = u(a.substr(d)) }), ba("Hmmss", function (a, b, c) { var d = a.length - 4, e = a.length - 2; b[gf] = u(a.substr(0, d)), b[hf] = u(a.substr(d, 2)), b[jf] = u(a.substr(e)) }); var Bf, Cf = /[ap]\.?m?\.?/i, Df = O("Hours", !0), Ef = { calendar: Ae, longDateFormat: Be, invalidDate: Ce, ordinal: De, dayOfMonthOrdinalParse: Ee, relativeTime: Fe, months: pf, monthsShort: qf, week: uf, weekdays: vf, weekdaysMin: xf, weekdaysShort: wf, meridiemParse: Cf }, Ff = {}, Gf = {}, Hf = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/, If = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/, Jf = /Z|[+-]\d\d(?::?\d\d)?/, Kf = [["YYYYYY-MM-DD", /[+-]\d{6}-\d\d-\d\d/], ["YYYY-MM-DD", /\d{4}-\d\d-\d\d/], ["GGGG-[W]WW-E", /\d{4}-W\d\d-\d/], ["GGGG-[W]WW", /\d{4}-W\d\d/, !1], ["YYYY-DDD", /\d{4}-\d{3}/], ["YYYY-MM", /\d{4}-\d\d/, !1], ["YYYYYYMMDD", /[+-]\d{10}/], ["YYYYMMDD", /\d{8}/], ["GGGG[W]WWE", /\d{4}W\d{3}/], ["GGGG[W]WW", /\d{4}W\d{2}/, !1], ["YYYYDDD", /\d{7}/]], Lf = [["HH:mm:ss.SSSS", /\d\d:\d\d:\d\d\.\d+/], ["HH:mm:ss,SSSS", /\d\d:\d\d:\d\d,\d+/], ["HH:mm:ss", /\d\d:\d\d:\d\d/], ["HH:mm", /\d\d:\d\d/], ["HHmmss.SSSS", /\d\d\d\d\d\d\.\d+/], ["HHmmss,SSSS", /\d\d\d\d\d\d,\d+/], ["HHmmss", /\d\d\d\d\d\d/], ["HHmm", /\d\d\d\d/], ["HH", /\d\d/]], Mf = /^\/?Date\((\-?\d+)/i, Nf = /^((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d?\d\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(?:\d\d)?\d\d\s)(\d\d:\d\d)(\:\d\d)?(\s(?:UT|GMT|[ECMP][SD]T|[A-IK-Za-ik-z]|[+-]\d{4}))$/; a.createFromInputFallback = x("value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are discouraged and will be removed in an upcoming major release. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.", function (a) { a._d = new Date(a._i + (a._useUTC ? " UTC" : "")) }), a.ISO_8601 = function () { }, a.RFC_2822 = function () { }; var Of = x("moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/", function () { var a = tb.apply(null, arguments); return this.isValid() && a.isValid() ? a < this ? this : a : p() }), Pf = x("moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/", function () { var a = tb.apply(null, arguments); return this.isValid() && a.isValid() ? a > this ? this : a : p() }), Qf = function () { return Date.now ? Date.now() : +new Date }, Rf = ["year", "quarter", "month", "week", "day", "hour", "minute", "second", "millisecond"]; Db("Z", ":"), Db("ZZ", ""), Z("Z", $e), Z("ZZ", $e), ba(["Z", "ZZ"], function (a, b, c) { c._useUTC = !0, c._tzm = Eb($e, a) }); var Sf = /([\+\-]|\d\d)/gi; a.updateOffset = function () { }; var Tf = /^(\-)?(?:(\d*)[. ])?(\d+)\:(\d+)(?:\:(\d+)(\.\d*)?)?$/, Uf = /^(-)?P(?:(-?[0-9,.]*)Y)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)W)?(?:(-?[0-9,.]*)D)?(?:T(?:(-?[0-9,.]*)H)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)S)?)?$/; Sb.fn = Ab.prototype, Sb.invalid = zb; var Vf = Wb(1, "add"), Wf = Wb(-1, "subtract"); a.defaultFormat = "YYYY-MM-DDTHH:mm:ssZ", a.defaultFormatUtc = "YYYY-MM-DDTHH:mm:ss[Z]"; var Xf = x("moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.", function (a) { return void 0 === a ? this.localeData() : this.locale(a) }); U(0, ["gg", 2], 0, function () { return this.weekYear() % 100 }), U(0, ["GG", 2], 0, function () { return this.isoWeekYear() % 100 }), Dc("gggg", "weekYear"), Dc("ggggg", "weekYear"), Dc("GGGG", "isoWeekYear"), Dc("GGGGG", "isoWeekYear"), J("weekYear", "gg"), J("isoWeekYear", "GG"), M("weekYear", 1), M("isoWeekYear", 1), Z("G", Ye), Z("g", Ye), Z("GG", Re, Ne), Z("gg", Re, Ne), Z("GGGG", Ve, Pe), Z("gggg", Ve, Pe), Z("GGGGG", We, Qe), Z("ggggg", We, Qe), ca(["gggg", "ggggg", "GGGG", "GGGGG"], function (a, b, c, d) { b[d.substr(0, 2)] = u(a) }), ca(["gg", "GG"], function (b, c, d, e) { c[e] = a.parseTwoDigitYear(b) }), U("Q", 0, "Qo", "quarter"), J("quarter", "Q"), M("quarter", 7), Z("Q", Me), ba("Q", function (a, b) { b[ef] = 3 * (u(a) - 1) }), U("D", ["DD", 2], "Do", "date"), J("date", "D"), M("date", 9), Z("D", Re), Z("DD", Re, Ne), Z("Do", function (a, b) { return a ? b._dayOfMonthOrdinalParse || b._ordinalParse : b._dayOfMonthOrdinalParseLenient }), ba(["D", "DD"], ff), ba("Do", function (a, b) { b[ff] = u(a.match(Re)[0], 10) }); var Yf = O("Date", !0); U("DDD", ["DDDD", 3], "DDDo", "dayOfYear"), J("dayOfYear", "DDD"), M("dayOfYear", 4), Z("DDD", Ue), Z("DDDD", Oe), ba(["DDD", "DDDD"], function (a, b, c) { c._dayOfYear = u(a) }), U("m", ["mm", 2], 0, "minute"), J("minute", "m"), M("minute", 14), Z("m", Re), Z("mm", Re, Ne), ba(["m", "mm"], hf); var Zf = O("Minutes", !1); U("s", ["ss", 2], 0, "second"), J("second", "s"), M("second", 15), Z("s", Re), Z("ss", Re, Ne), ba(["s", "ss"], jf); var $f = O("Seconds", !1); U("S", 0, 0, function () { return ~~(this.millisecond() / 100) }), U(0, ["SS", 2], 0, function () { return ~~(this.millisecond() / 10) }), U(0, ["SSS", 3], 0, "millisecond"), U(0, ["SSSS", 4], 0, function () { return 10 * this.millisecond() }), U(0, ["SSSSS", 5], 0, function () { return 100 * this.millisecond() }), U(0, ["SSSSSS", 6], 0, function () { return 1e3 * this.millisecond() }), U(0, ["SSSSSSS", 7], 0, function () { return 1e4 * this.millisecond() }), U(0, ["SSSSSSSS", 8], 0, function () { return 1e5 * this.millisecond() }), U(0, ["SSSSSSSSS", 9], 0, function () { return 1e6 * this.millisecond() }), J("millisecond", "ms"), M("millisecond", 16), Z("S", Ue, Me), Z("SS", Ue, Ne), Z("SSS", Ue, Oe); var _f; for (_f = "SSSS"; _f.length <= 9; _f += "S")Z(_f, Xe); for (_f = "S"; _f.length <= 9; _f += "S")ba(_f, Mc); var ag = O("Milliseconds", !1); U("z", 0, 0, "zoneAbbr"), U("zz", 0, 0, "zoneName"); var bg = r.prototype; bg.add = Vf, bg.calendar = Zb, bg.clone = $b, bg.diff = fc, bg.endOf = sc, bg.format = kc, bg.from = lc, bg.fromNow = mc, bg.to = nc, bg.toNow = oc, bg.get = R, bg.invalidAt = Bc, bg.isAfter = _b, bg.isBefore = ac, bg.isBetween = bc, bg.isSame = cc, bg.isSameOrAfter = dc, bg.isSameOrBefore = ec, bg.isValid = zc, bg.lang = Xf, bg.locale = pc, bg.localeData = qc, bg.max = Pf, bg.min = Of, bg.parsingFlags = Ac, bg.set = S, bg.startOf = rc, bg.subtract = Wf, bg.toArray = wc, bg.toObject = xc, bg.toDate = vc, bg.toISOString = ic, bg.inspect = jc, bg.toJSON = yc, bg.toString = hc, bg.unix = uc, bg.valueOf = tc, bg.creationData = Cc, bg.year = tf, bg.isLeapYear = ra, bg.weekYear = Ec, bg.isoWeekYear = Fc, bg.quarter = bg.quarters = Kc, bg.month = ka, bg.daysInMonth = la, bg.week = bg.weeks = Ba, bg.isoWeek = bg.isoWeeks = Ca, bg.weeksInYear = Hc, bg.isoWeeksInYear = Gc, bg.date = Yf, bg.day = bg.days = Ka, bg.weekday = La, bg.isoWeekday = Ma, bg.dayOfYear = Lc, bg.hour = bg.hours = Df, bg.minute = bg.minutes = Zf, bg.second = bg.seconds = $f, bg.millisecond = bg.milliseconds = ag, bg.utcOffset = Hb, bg.utc = Jb, bg.local = Kb, bg.parseZone = Lb, bg.hasAlignedHourOffset = Mb, bg.isDST = Nb, bg.isLocal = Pb, bg.isUtcOffset = Qb, bg.isUtc = Rb, bg.isUTC = Rb, bg.zoneAbbr = Nc, bg.zoneName = Oc, bg.dates = x("dates accessor is deprecated. Use date instead.", Yf), bg.months = x("months accessor is deprecated. Use month instead", ka), bg.years = x("years accessor is deprecated. Use year instead", tf), bg.zone = x("moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/", Ib), bg.isDSTShifted = x("isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information", Ob); var cg = C.prototype; cg.calendar = D, cg.longDateFormat = E, cg.invalidDate = F, cg.ordinal = G, cg.preparse = Rc, cg.postformat = Rc, cg.relativeTime = H, cg.pastFuture = I, cg.set = A, cg.months = fa, cg.monthsShort = ga, cg.monthsParse = ia, cg.monthsRegex = na, cg.monthsShortRegex = ma, cg.week = ya, cg.firstDayOfYear = Aa, cg.firstDayOfWeek = za, cg.weekdays = Fa, cg.weekdaysMin = Ha, cg.weekdaysShort = Ga, cg.weekdaysParse = Ja, cg.weekdaysRegex = Na, cg.weekdaysShortRegex = Oa, cg.weekdaysMinRegex = Pa, cg.isPM = Va, cg.meridiem = Wa, $a("en", { dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/, ordinal: function (a) { var b = a % 10, c = 1 === u(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c } }), a.lang = x("moment.lang is deprecated. Use moment.locale instead.", $a), a.langData = x("moment.langData is deprecated. Use moment.localeData instead.", bb); var dg = Math.abs, eg = id("ms"), fg = id("s"), gg = id("m"), hg = id("h"), ig = id("d"), jg = id("w"), kg = id("M"), lg = id("y"), mg = kd("milliseconds"), ng = kd("seconds"), og = kd("minutes"), pg = kd("hours"), qg = kd("days"), rg = kd("months"), sg = kd("years"), tg = Math.round, ug = { ss: 44, s: 45, m: 45, h: 22, d: 26, M: 11 }, vg = Math.abs, wg = Ab.prototype; wg.isValid = yb, wg.abs = $c, wg.add = ad, wg.subtract = bd, wg.as = gd, wg.asMilliseconds = eg, wg.asSeconds = fg, wg.asMinutes = gg, wg.asHours = hg, wg.asDays = ig, wg.asWeeks = jg, wg.asMonths = kg, wg.asYears = lg, wg.valueOf = hd, wg._bubble = dd, wg.get = jd, wg.milliseconds = mg, wg.seconds = ng, wg.minutes = og, wg.hours = pg, wg.days = qg, wg.weeks = ld, wg.months = rg, wg.years = sg, wg.humanize = qd, wg.toISOString = rd, wg.toString = rd, wg.toJSON = rd, wg.locale = pc, wg.localeData = qc, wg.toIsoString = x("toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)", rd), wg.lang = Xf, U("X", 0, 0, "unix"), U("x", 0, 0, "valueOf"), Z("x", Ye), Z("X", _e), ba("X", function (a, b, c) { c._d = new Date(1e3 * parseFloat(a, 10)) }), ba("x", function (a, b, c) { c._d = new Date(u(a)) }),
		//! moment.js
		//! version : 2.29.4
		//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
		//! license : MIT
		//! momentjs.com
		a.version = "2.29.4", b(tb), a.fn = bg, a.min = vb, a.max = wb, a.now = Qf, a.utc = l, a.unix = Pc, a.months = Vc, a.isDate = h, a.locale = $a, a.invalid = p, a.duration = Sb, a.isMoment = s, a.weekdays = Xc, a.parseZone = Qc, a.localeData = bb, a.isDuration = Bb, a.monthsShort = Wc, a.weekdaysMin = Zc, a.defineLocale = _a, a.updateLocale = ab, a.locales = cb, a.weekdaysShort = Yc, a.normalizeUnits = K, a.relativeTimeRounding = od, a.relativeTimeThreshold = pd, a.calendarFormat = Yb, a.prototype = bg,
		//! moment.js locale configuration
		//! locale : Afrikaans [af]
		//! author : Werner Mollentze : https://github.com/wernerm
		a.defineLocale("af", { months: "Januarie_Februarie_Maart_April_Mei_Junie_Julie_Augustus_September_Oktober_November_Desember".split("_"), monthsShort: "Jan_Feb_Mrt_Apr_Mei_Jun_Jul_Aug_Sep_Okt_Nov_Des".split("_"), weekdays: "Sondag_Maandag_Dinsdag_Woensdag_Donderdag_Vrydag_Saterdag".split("_"), weekdaysShort: "Son_Maa_Din_Woe_Don_Vry_Sat".split("_"), weekdaysMin: "So_Ma_Di_Wo_Do_Vr_Sa".split("_"), meridiemParse: /vm|nm/i, isPM: function (a) { return /^nm$/i.test(a) }, meridiem: function (a, b, c) { return a < 12 ? c ? "vm" : "VM" : c ? "nm" : "NM" }, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Vandag om] LT", nextDay: "[Môre om] LT", nextWeek: "dddd [om] LT", lastDay: "[Gister om] LT", lastWeek: "[Laas] dddd [om] LT", sameElse: "L" }, relativeTime: { future: "oor %s", past: "%s gelede", s: "'n paar sekondes", m: "'n minuut", mm: "%d minute", h: "'n uur", hh: "%d ure", d: "'n dag", dd: "%d dae", M: "'n maand", MM: "%d maande", y: "'n jaar", yy: "%d jaar" }, dayOfMonthOrdinalParse: /\d{1,2}(ste|de)/, ordinal: function (a) { return a + (1 === a || 8 === a || a >= 20 ? "ste" : "de") }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Arabic (Algeria) [ar-dz]
		//! author : Noureddine LOUAHEDJ : https://github.com/noureddineme
		a.defineLocale("ar-dz", { months: "جانفي_فيفري_مارس_أفريل_ماي_جوان_جويلية_أوت_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), monthsShort: "جانفي_فيفري_مارس_أفريل_ماي_جوان_جويلية_أوت_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), weekdays: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "احد_اثنين_ثلاثاء_اربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "أح_إث_ثلا_أر_خم_جم_سب".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[اليوم على الساعة] LT", nextDay: "[غدا على الساعة] LT", nextWeek: "dddd [على الساعة] LT", lastDay: "[أمس على الساعة] LT", lastWeek: "dddd [على الساعة] LT", sameElse: "L" }, relativeTime: { future: "في %s", past: "منذ %s", s: "ثوان", m: "دقيقة", mm: "%d دقائق", h: "ساعة", hh: "%d ساعات", d: "يوم", dd: "%d أيام", M: "شهر", MM: "%d أشهر", y: "سنة", yy: "%d سنوات" }, week: { dow: 0, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Arabic (Kuwait) [ar-kw]
		//! author : Nusret Parlak: https://github.com/nusretparlak
		a.defineLocale("ar-kw", { months: "يناير_فبراير_مارس_أبريل_ماي_يونيو_يوليوز_غشت_شتنبر_أكتوبر_نونبر_دجنبر".split("_"), monthsShort: "يناير_فبراير_مارس_أبريل_ماي_يونيو_يوليوز_غشت_شتنبر_أكتوبر_نونبر_دجنبر".split("_"), weekdays: "الأحد_الإتنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "احد_اتنين_ثلاثاء_اربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[اليوم على الساعة] LT", nextDay: "[غدا على الساعة] LT", nextWeek: "dddd [على الساعة] LT", lastDay: "[أمس على الساعة] LT", lastWeek: "dddd [على الساعة] LT", sameElse: "L" }, relativeTime: { future: "في %s", past: "منذ %s", s: "ثوان", m: "دقيقة", mm: "%d دقائق", h: "ساعة", hh: "%d ساعات", d: "يوم", dd: "%d أيام", M: "شهر", MM: "%d أشهر", y: "سنة", yy: "%d سنوات" }, week: { dow: 0, doy: 12 } });
	//! moment.js locale configuration
	//! locale : Arabic (Lybia) [ar-ly]
	//! author : Ali Hmer: https://github.com/kikoanis
	var xg = { 1: "1", 2: "2", 3: "3", 4: "4", 5: "5", 6: "6", 7: "7", 8: "8", 9: "9", 0: "0" }, yg = function (a) { return 0 === a ? 0 : 1 === a ? 1 : 2 === a ? 2 : a % 100 >= 3 && a % 100 <= 10 ? 3 : a % 100 >= 11 ? 4 : 5 }, zg = { s: ["أقل من ثانية", "ثانية واحدة", ["ثانيتان", "ثانيتين"], "%d ثوان", "%d ثانية", "%d ثانية"], m: ["أقل من دقيقة", "دقيقة واحدة", ["دقيقتان", "دقيقتين"], "%d دقائق", "%d دقيقة", "%d دقيقة"], h: ["أقل من ساعة", "ساعة واحدة", ["ساعتان", "ساعتين"], "%d ساعات", "%d ساعة", "%d ساعة"], d: ["أقل من يوم", "يوم واحد", ["يومان", "يومين"], "%d أيام", "%d يومًا", "%d يوم"], M: ["أقل من شهر", "شهر واحد", ["شهران", "شهرين"], "%d أشهر", "%d شهرا", "%d شهر"], y: ["أقل من عام", "عام واحد", ["عامان", "عامين"], "%d أعوام", "%d عامًا", "%d عام"] }, Ag = function (a) { return function (b, c, d, e) { var f = yg(b), g = zg[a][yg(b)]; return 2 === f && (g = g[c ? 0 : 1]), g.replace(/%d/i, b) } }, Bg = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"]; a.defineLocale("ar-ly", { months: Bg, monthsShort: Bg, weekdays: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "أحد_إثنين_ثلاثاء_أربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "D/‏M/‏YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, meridiemParse: /ص|م/, isPM: function (a) { return "م" === a }, meridiem: function (a, b, c) { return a < 12 ? "ص" : "م" }, calendar: { sameDay: "[اليوم عند الساعة] LT", nextDay: "[غدًا عند الساعة] LT", nextWeek: "dddd [عند الساعة] LT", lastDay: "[أمس عند الساعة] LT", lastWeek: "dddd [عند الساعة] LT", sameElse: "L" }, relativeTime: { future: "بعد %s", past: "منذ %s", s: Ag("s"), m: Ag("m"), mm: Ag("m"), h: Ag("h"), hh: Ag("h"), d: Ag("d"), dd: Ag("d"), M: Ag("M"), MM: Ag("M"), y: Ag("y"), yy: Ag("y") }, preparse: function (a) { return a.replace(/\u200f/g, "").replace(/،/g, ",") }, postformat: function (a) { return a.replace(/\d/g, function (a) { return xg[a] }).replace(/,/g, "،") }, week: { dow: 6, doy: 12 } }),
		//! moment.js locale configuration
		//! locale : Arabic (Morocco) [ar-ma]
		//! author : ElFadili Yassine : https://github.com/ElFadiliY
		//! author : Abdel Said : https://github.com/abdelsaid
		a.defineLocale("ar-ma", { months: "يناير_فبراير_مارس_أبريل_ماي_يونيو_يوليوز_غشت_شتنبر_أكتوبر_نونبر_دجنبر".split("_"), monthsShort: "يناير_فبراير_مارس_أبريل_ماي_يونيو_يوليوز_غشت_شتنبر_أكتوبر_نونبر_دجنبر".split("_"), weekdays: "الأحد_الإتنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "احد_اتنين_ثلاثاء_اربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[اليوم على الساعة] LT", nextDay: "[غدا على الساعة] LT", nextWeek: "dddd [على الساعة] LT", lastDay: "[أمس على الساعة] LT", lastWeek: "dddd [على الساعة] LT", sameElse: "L" }, relativeTime: { future: "في %s", past: "منذ %s", s: "ثوان", m: "دقيقة", mm: "%d دقائق", h: "ساعة", hh: "%d ساعات", d: "يوم", dd: "%d أيام", M: "شهر", MM: "%d أشهر", y: "سنة", yy: "%d سنوات" }, week: { dow: 6, doy: 12 } });
	//! moment.js locale configuration
	//! locale : Arabic (Saudi Arabia) [ar-sa]
	//! author : Suhail Alkowaileet : https://github.com/xsoh
	var Cg = { 1: "١", 2: "٢", 3: "٣", 4: "٤", 5: "٥", 6: "٦", 7: "٧", 8: "٨", 9: "٩", 0: "٠" }, Dg = { "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9", "٠": "0" }; a.defineLocale("ar-sa", { months: "يناير_فبراير_مارس_أبريل_مايو_يونيو_يوليو_أغسطس_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), monthsShort: "يناير_فبراير_مارس_أبريل_مايو_يونيو_يوليو_أغسطس_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), weekdays: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "أحد_إثنين_ثلاثاء_أربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, meridiemParse: /ص|م/, isPM: function (a) { return "م" === a }, meridiem: function (a, b, c) { return a < 12 ? "ص" : "م" }, calendar: { sameDay: "[اليوم على الساعة] LT", nextDay: "[غدا على الساعة] LT", nextWeek: "dddd [على الساعة] LT", lastDay: "[أمس على الساعة] LT", lastWeek: "dddd [على الساعة] LT", sameElse: "L" }, relativeTime: { future: "في %s", past: "منذ %s", s: "ثوان", m: "دقيقة", mm: "%d دقائق", h: "ساعة", hh: "%d ساعات", d: "يوم", dd: "%d أيام", M: "شهر", MM: "%d أشهر", y: "سنة", yy: "%d سنوات" }, preparse: function (a) { return a.replace(/[١٢٣٤٥٦٧٨٩٠]/g, function (a) { return Dg[a] }).replace(/،/g, ",") }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Cg[a] }).replace(/,/g, "،") }, week: { dow: 0, doy: 6 } }),
		//! moment.js locale configuration
		//! locale  :  Arabic (Tunisia) [ar-tn]
		//! author : Nader Toukabri : https://github.com/naderio
		a.defineLocale("ar-tn", { months: "جانفي_فيفري_مارس_أفريل_ماي_جوان_جويلية_أوت_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), monthsShort: "جانفي_فيفري_مارس_أفريل_ماي_جوان_جويلية_أوت_سبتمبر_أكتوبر_نوفمبر_ديسمبر".split("_"), weekdays: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "أحد_إثنين_ثلاثاء_أربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[اليوم على الساعة] LT", nextDay: "[غدا على الساعة] LT", nextWeek: "dddd [على الساعة] LT", lastDay: "[أمس على الساعة] LT", lastWeek: "dddd [على الساعة] LT", sameElse: "L" }, relativeTime: { future: "في %s", past: "منذ %s", s: "ثوان", m: "دقيقة", mm: "%d دقائق", h: "ساعة", hh: "%d ساعات", d: "يوم", dd: "%d أيام", M: "شهر", MM: "%d أشهر", y: "سنة", yy: "%d سنوات" }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Arabic [ar]
	//! author : Abdel Said: https://github.com/abdelsaid
	//! author : Ahmed Elkhatib
	//! author : forabi https://github.com/forabi
	var Eg = { 1: "١", 2: "٢", 3: "٣", 4: "٤", 5: "٥", 6: "٦", 7: "٧", 8: "٨", 9: "٩", 0: "٠" }, Fg = { "١": "1", "٢": "2", "٣": "3", "٤": "4", "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9", "٠": "0" }, Gg = function (a) { return 0 === a ? 0 : 1 === a ? 1 : 2 === a ? 2 : a % 100 >= 3 && a % 100 <= 10 ? 3 : a % 100 >= 11 ? 4 : 5 }, Hg = { s: ["أقل من ثانية", "ثانية واحدة", ["ثانيتان", "ثانيتين"], "%d ثوان", "%d ثانية", "%d ثانية"], m: ["أقل من دقيقة", "دقيقة واحدة", ["دقيقتان", "دقيقتين"], "%d دقائق", "%d دقيقة", "%d دقيقة"], h: ["أقل من ساعة", "ساعة واحدة", ["ساعتان", "ساعتين"], "%d ساعات", "%d ساعة", "%d ساعة"], d: ["أقل من يوم", "يوم واحد", ["يومان", "يومين"], "%d أيام", "%d يومًا", "%d يوم"], M: ["أقل من شهر", "شهر واحد", ["شهران", "شهرين"], "%d أشهر", "%d شهرا", "%d شهر"], y: ["أقل من عام", "عام واحد", ["عامان", "عامين"], "%d أعوام", "%d عامًا", "%d عام"] }, Ig = function (a) { return function (b, c, d, e) { var f = Gg(b), g = Hg[a][Gg(b)]; return 2 === f && (g = g[c ? 0 : 1]), g.replace(/%d/i, b) } }, Jg = ["كانون الثاني يناير", "شباط فبراير", "آذار مارس", "نيسان أبريل", "أيار مايو", "حزيران يونيو", "تموز يوليو", "آب أغسطس", "أيلول سبتمبر", "تشرين الأول أكتوبر", "تشرين الثاني نوفمبر", "كانون الأول ديسمبر"]; a.defineLocale("ar", { months: Jg, monthsShort: Jg, weekdays: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split("_"), weekdaysShort: "أحد_إثنين_ثلاثاء_أربعاء_خميس_جمعة_سبت".split("_"), weekdaysMin: "ح_ن_ث_ر_خ_ج_س".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "D/‏M/‏YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, meridiemParse: /ص|م/, isPM: function (a) { return "م" === a }, meridiem: function (a, b, c) { return a < 12 ? "ص" : "م" }, calendar: { sameDay: "[اليوم عند الساعة] LT", nextDay: "[غدًا عند الساعة] LT", nextWeek: "dddd [عند الساعة] LT", lastDay: "[أمس عند الساعة] LT", lastWeek: "dddd [عند الساعة] LT", sameElse: "L" }, relativeTime: { future: "بعد %s", past: "منذ %s", s: Ig("s"), m: Ig("m"), mm: Ig("m"), h: Ig("h"), hh: Ig("h"), d: Ig("d"), dd: Ig("d"), M: Ig("M"), MM: Ig("M"), y: Ig("y"), yy: Ig("y") }, preparse: function (a) { return a.replace(/\u200f/g, "").replace(/[١٢٣٤٥٦٧٨٩٠]/g, function (a) { return Fg[a] }).replace(/،/g, ",") }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Eg[a] }).replace(/,/g, "،") }, week: { dow: 6, doy: 12 } });
	//! moment.js locale configuration
	//! locale : Azerbaijani [az]
	//! author : topchiyev : https://github.com/topchiyev
	var Kg = { 1: "-inci", 5: "-inci", 8: "-inci", 70: "-inci", 80: "-inci", 2: "-nci", 7: "-nci", 20: "-nci", 50: "-nci", 3: "-üncü", 4: "-üncü", 100: "-üncü", 6: "-ncı", 9: "-uncu", 10: "-uncu", 30: "-uncu", 60: "-ıncı", 90: "-ıncı" }; a.defineLocale("az", { months: "yanvar_fevral_mart_aprel_may_iyun_iyul_avqust_sentyabr_oktyabr_noyabr_dekabr".split("_"), monthsShort: "yan_fev_mar_apr_may_iyn_iyl_avq_sen_okt_noy_dek".split("_"), weekdays: "Bazar_Bazar ertəsi_Çərşənbə axşamı_Çərşənbə_Cümə axşamı_Cümə_Şənbə".split("_"), weekdaysShort: "Baz_BzE_ÇAx_Çər_CAx_Cüm_Şən".split("_"), weekdaysMin: "Bz_BE_ÇA_Çə_CA_Cü_Şə".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[bugün saat] LT", nextDay: "[sabah saat] LT", nextWeek: "[gələn həftə] dddd [saat] LT", lastDay: "[dünən] LT", lastWeek: "[keçən həftə] dddd [saat] LT", sameElse: "L" }, relativeTime: { future: "%s sonra", past: "%s əvvəl", s: "birneçə saniyyə", m: "bir dəqiqə", mm: "%d dəqiqə", h: "bir saat", hh: "%d saat", d: "bir gün", dd: "%d gün", M: "bir ay", MM: "%d ay", y: "bir il", yy: "%d il" }, meridiemParse: /gecə|səhər|gündüz|axşam/, isPM: function (a) { return /^(gündüz|axşam)$/.test(a) }, meridiem: function (a, b, c) { return a < 4 ? "gecə" : a < 12 ? "səhər" : a < 17 ? "gündüz" : "axşam" }, dayOfMonthOrdinalParse: /\d{1,2}-(ıncı|inci|nci|üncü|ncı|uncu)/, ordinal: function (a) { if (0 === a) return a + "-ıncı"; var b = a % 10, c = a % 100 - b, d = a >= 100 ? 100 : null; return a + (Kg[b] || Kg[c] || Kg[d]) }, week: { dow: 1, doy: 7 } }), a.defineLocale("be", { months: { format: "студзеня_лютага_сакавіка_красавіка_траўня_чэрвеня_ліпеня_жніўня_верасня_кастрычніка_лістапада_снежня".split("_"), standalone: "студзень_люты_сакавік_красавік_травень_чэрвень_ліпень_жнівень_верасень_кастрычнік_лістапад_снежань".split("_") }, monthsShort: "студ_лют_сак_крас_трав_чэрв_ліп_жнів_вер_каст_ліст_снеж".split("_"), weekdays: { format: "нядзелю_панядзелак_аўторак_сераду_чацвер_пятніцу_суботу".split("_"), standalone: "нядзеля_панядзелак_аўторак_серада_чацвер_пятніца_субота".split("_"), isFormat: /\[ ?[Вв] ?(?:мінулую|наступную)? ?\] ?dddd/ }, weekdaysShort: "нд_пн_ат_ср_чц_пт_сб".split("_"), weekdaysMin: "нд_пн_ат_ср_чц_пт_сб".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY г.", LLL: "D MMMM YYYY г., HH:mm", LLLL: "dddd, D MMMM YYYY г., HH:mm" }, calendar: { sameDay: "[Сёння ў] LT", nextDay: "[Заўтра ў] LT", lastDay: "[Учора ў] LT", nextWeek: function () { return "[У] dddd [ў] LT" }, lastWeek: function () { switch (this.day()) { case 0: case 3: case 5: case 6: return "[У мінулую] dddd [ў] LT"; case 1: case 2: case 4: return "[У мінулы] dddd [ў] LT" } }, sameElse: "L" }, relativeTime: { future: "праз %s", past: "%s таму", s: "некалькі секунд", m: td, mm: td, h: td, hh: td, d: "дзень", dd: td, M: "месяц", MM: td, y: "год", yy: td }, meridiemParse: /ночы|раніцы|дня|вечара/, isPM: function (a) { return /^(дня|вечара)$/.test(a) }, meridiem: function (a, b, c) { return a < 4 ? "ночы" : a < 12 ? "раніцы" : a < 17 ? "дня" : "вечара" }, dayOfMonthOrdinalParse: /\d{1,2}-(і|ы|га)/, ordinal: function (a, b) { switch (b) { case "M": case "d": case "DDD": case "w": case "W": return a % 10 !== 2 && a % 10 !== 3 || a % 100 === 12 || a % 100 === 13 ? a + "-ы" : a + "-і"; case "D": return a + "-га"; default: return a } }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Bulgarian [bg]
		//! author : Krasen Borisov : https://github.com/kraz
		a.defineLocale("bg", { months: "януари_февруари_март_април_май_юни_юли_август_септември_октомври_ноември_декември".split("_"), monthsShort: "янр_фев_мар_апр_май_юни_юли_авг_сеп_окт_ное_дек".split("_"), weekdays: "неделя_понеделник_вторник_сряда_четвъртък_петък_събота".split("_"), weekdaysShort: "нед_пон_вто_сря_чет_пет_съб".split("_"), weekdaysMin: "нд_пн_вт_ср_чт_пт_сб".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "D.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY H:mm", LLLL: "dddd, D MMMM YYYY H:mm" }, calendar: { sameDay: "[Днес в] LT", nextDay: "[Утре в] LT", nextWeek: "dddd [в] LT", lastDay: "[Вчера в] LT", lastWeek: function () { switch (this.day()) { case 0: case 3: case 6: return "[В изминалата] dddd [в] LT"; case 1: case 2: case 4: case 5: return "[В изминалия] dddd [в] LT" } }, sameElse: "L" }, relativeTime: { future: "след %s", past: "преди %s", s: "няколко секунди", m: "минута", mm: "%d минути", h: "час", hh: "%d часа", d: "ден", dd: "%d дни", M: "месец", MM: "%d месеца", y: "година", yy: "%d години" }, dayOfMonthOrdinalParse: /\d{1,2}-(ев|ен|ти|ви|ри|ми)/, ordinal: function (a) { var b = a % 10, c = a % 100; return 0 === a ? a + "-ев" : 0 === c ? a + "-ен" : c > 10 && c < 20 ? a + "-ти" : 1 === b ? a + "-ви" : 2 === b ? a + "-ри" : 7 === b || 8 === b ? a + "-ми" : a + "-ти" }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Bengali [bn]
	//! author : Kaushik Gandhi : https://github.com/kaushikgandhi
	var Lg = { 1: "১", 2: "২", 3: "৩", 4: "৪", 5: "৫", 6: "৬", 7: "৭", 8: "৮", 9: "৯", 0: "০" }, Mg = { "১": "1", "২": "2", "৩": "3", "৪": "4", "৫": "5", "৬": "6", "৭": "7", "৮": "8", "৯": "9", "০": "0" }; a.defineLocale("bn", { months: "জানুয়ারী_ফেব্রুয়ারি_মার্চ_এপ্রিল_মে_জুন_জুলাই_আগস্ট_সেপ্টেম্বর_অক্টোবর_নভেম্বর_ডিসেম্বর".split("_"), monthsShort: "জানু_ফেব_মার্চ_এপ্র_মে_জুন_জুল_আগ_সেপ্ট_অক্টো_নভে_ডিসে".split("_"), weekdays: "রবিবার_সোমবার_মঙ্গলবার_বুধবার_বৃহস্পতিবার_শুক্রবার_শনিবার".split("_"), weekdaysShort: "রবি_সোম_মঙ্গল_বুধ_বৃহস্পতি_শুক্র_শনি".split("_"), weekdaysMin: "রবি_সোম_মঙ্গ_বুধ_বৃহঃ_শুক্র_শনি".split("_"), longDateFormat: { LT: "A h:mm সময়", LTS: "A h:mm:ss সময়", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm সময়", LLLL: "dddd, D MMMM YYYY, A h:mm সময়" }, calendar: { sameDay: "[আজ] LT", nextDay: "[আগামীকাল] LT", nextWeek: "dddd, LT", lastDay: "[গতকাল] LT", lastWeek: "[গত] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s পরে", past: "%s আগে", s: "কয়েক সেকেন্ড", m: "এক মিনিট", mm: "%d মিনিট", h: "এক ঘন্টা", hh: "%d ঘন্টা", d: "এক দিন", dd: "%d দিন", M: "এক মাস", MM: "%d মাস", y: "এক বছর", yy: "%d বছর" }, preparse: function (a) { return a.replace(/[১২৩৪৫৬৭৮৯০]/g, function (a) { return Mg[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Lg[a] }) }, meridiemParse: /রাত|সকাল|দুপুর|বিকাল|রাত/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "রাত" === b && a >= 4 || "দুপুর" === b && a < 5 || "বিকাল" === b ? a + 12 : a }, meridiem: function (a, b, c) { return a < 4 ? "রাত" : a < 10 ? "সকাল" : a < 17 ? "দুপুর" : a < 20 ? "বিকাল" : "রাত" }, week: { dow: 0, doy: 6 } });
	//! moment.js locale configuration
	//! locale : Tibetan [bo]
	//! author : Thupten N. Chakrishar : https://github.com/vajradog
	var Ng = { 1: "༡", 2: "༢", 3: "༣", 4: "༤", 5: "༥", 6: "༦", 7: "༧", 8: "༨", 9: "༩", 0: "༠" }, Og = { "༡": "1", "༢": "2", "༣": "3", "༤": "4", "༥": "5", "༦": "6", "༧": "7", "༨": "8", "༩": "9", "༠": "0" }; a.defineLocale("bo", { months: "ཟླ་བ་དང་པོ_ཟླ་བ་གཉིས་པ_ཟླ་བ་གསུམ་པ_ཟླ་བ་བཞི་པ_ཟླ་བ་ལྔ་པ_ཟླ་བ་དྲུག་པ_ཟླ་བ་བདུན་པ_ཟླ་བ་བརྒྱད་པ_ཟླ་བ་དགུ་པ_ཟླ་བ་བཅུ་པ_ཟླ་བ་བཅུ་གཅིག་པ_ཟླ་བ་བཅུ་གཉིས་པ".split("_"), monthsShort: "ཟླ་བ་དང་པོ_ཟླ་བ་གཉིས་པ_ཟླ་བ་གསུམ་པ_ཟླ་བ་བཞི་པ_ཟླ་བ་ལྔ་པ_ཟླ་བ་དྲུག་པ_ཟླ་བ་བདུན་པ_ཟླ་བ་བརྒྱད་པ_ཟླ་བ་དགུ་པ_ཟླ་བ་བཅུ་པ_ཟླ་བ་བཅུ་གཅིག་པ_ཟླ་བ་བཅུ་གཉིས་པ".split("_"), weekdays: "གཟའ་ཉི་མ་_གཟའ་ཟླ་བ་_གཟའ་མིག་དམར་_གཟའ་ལྷག་པ་_གཟའ་ཕུར་བུ_གཟའ་པ་སངས་_གཟའ་སྤེན་པ་".split("_"), weekdaysShort: "ཉི་མ་_ཟླ་བ་_མིག་དམར་_ལྷག་པ་_ཕུར་བུ_པ་སངས་_སྤེན་པ་".split("_"), weekdaysMin: "ཉི་མ་_ཟླ་བ་_མིག་དམར་_ལྷག་པ་_ཕུར་བུ_པ་སངས་_སྤེན་པ་".split("_"), longDateFormat: { LT: "A h:mm", LTS: "A h:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm", LLLL: "dddd, D MMMM YYYY, A h:mm" }, calendar: { sameDay: "[དི་རིང] LT", nextDay: "[སང་ཉིན] LT", nextWeek: "[བདུན་ཕྲག་རྗེས་མ], LT", lastDay: "[ཁ་སང] LT", lastWeek: "[བདུན་ཕྲག་མཐའ་མ] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s ལ་", past: "%s སྔན་ལ", s: "ལམ་སང", m: "སྐར་མ་གཅིག", mm: "%d སྐར་མ", h: "ཆུ་ཚོད་གཅིག", hh: "%d ཆུ་ཚོད", d: "ཉིན་གཅིག", dd: "%d ཉིན་", M: "ཟླ་བ་གཅིག", MM: "%d ཟླ་བ", y: "ལོ་གཅིག", yy: "%d ལོ" }, preparse: function (a) { return a.replace(/[༡༢༣༤༥༦༧༨༩༠]/g, function (a) { return Og[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Ng[a] }) }, meridiemParse: /མཚན་མོ|ཞོགས་ཀས|ཉིན་གུང|དགོང་དག|མཚན་མོ/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "མཚན་མོ" === b && a >= 4 || "ཉིན་གུང" === b && a < 5 || "དགོང་དག" === b ? a + 12 : a }, meridiem: function (a, b, c) { return a < 4 ? "མཚན་མོ" : a < 10 ? "ཞོགས་ཀས" : a < 17 ? "ཉིན་གུང" : a < 20 ? "དགོང་དག" : "མཚན་མོ" }, week: { dow: 0, doy: 6 } }), a.defineLocale("br", { months: "Genver_C'hwevrer_Meurzh_Ebrel_Mae_Mezheven_Gouere_Eost_Gwengolo_Here_Du_Kerzu".split("_"), monthsShort: "Gen_C'hwe_Meu_Ebr_Mae_Eve_Gou_Eos_Gwe_Her_Du_Ker".split("_"), weekdays: "Sul_Lun_Meurzh_Merc'her_Yaou_Gwener_Sadorn".split("_"), weekdaysShort: "Sul_Lun_Meu_Mer_Yao_Gwe_Sad".split("_"), weekdaysMin: "Su_Lu_Me_Mer_Ya_Gw_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "h[e]mm A", LTS: "h[e]mm:ss A", L: "DD/MM/YYYY", LL: "D [a viz] MMMM YYYY", LLL: "D [a viz] MMMM YYYY h[e]mm A", LLLL: "dddd, D [a viz] MMMM YYYY h[e]mm A" }, calendar: { sameDay: "[Hiziv da] LT", nextDay: "[Warc'hoazh da] LT", nextWeek: "dddd [da] LT", lastDay: "[Dec'h da] LT", lastWeek: "dddd [paset da] LT", sameElse: "L" }, relativeTime: { future: "a-benn %s", past: "%s 'zo", s: "un nebeud segondennoù", m: "ur vunutenn", mm: ud, h: "un eur", hh: "%d eur", d: "un devezh", dd: ud, M: "ur miz", MM: ud, y: "ur bloaz", yy: vd }, dayOfMonthOrdinalParse: /\d{1,2}(añ|vet)/, ordinal: function (a) { var b = 1 === a ? "añ" : "vet"; return a + b }, week: { dow: 1, doy: 4 } }), a.defineLocale("bs", { months: "januar_februar_mart_april_maj_juni_juli_august_septembar_oktobar_novembar_decembar".split("_"), monthsShort: "jan._feb._mar._apr._maj._jun._jul._aug._sep._okt._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "nedjelja_ponedjeljak_utorak_srijeda_četvrtak_petak_subota".split("_"), weekdaysShort: "ned._pon._uto._sri._čet._pet._sub.".split("_"), weekdaysMin: "ne_po_ut_sr_če_pe_su".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[danas u] LT", nextDay: "[sutra u] LT", nextWeek: function () { switch (this.day()) { case 0: return "[u] [nedjelju] [u] LT"; case 3: return "[u] [srijedu] [u] LT"; case 6: return "[u] [subotu] [u] LT"; case 1: case 2: case 4: case 5: return "[u] dddd [u] LT" } }, lastDay: "[jučer u] LT", lastWeek: function () { switch (this.day()) { case 0: case 3: return "[prošlu] dddd [u] LT"; case 6: return "[prošle] [subote] [u] LT"; case 1: case 2: case 4: case 5: return "[prošli] dddd [u] LT" } }, sameElse: "L" }, relativeTime: { future: "za %s", past: "prije %s", s: "par sekundi", m: zd, mm: zd, h: zd, hh: zd, d: "dan", dd: zd, M: "mjesec", MM: zd, y: "godinu", yy: zd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Catalan [ca]
		//! author : Juan G. Hurtado : https://github.com/juanghurtado
		a.defineLocale("ca", { months: { standalone: "gener_febrer_març_abril_maig_juny_juliol_agost_setembre_octubre_novembre_desembre".split("_"), format: "de gener_de febrer_de març_d'abril_de maig_de juny_de juliol_d'agost_de setembre_d'octubre_de novembre_de desembre".split("_"), isFormat: /D[oD]?(\s)+MMMM/ }, monthsShort: "gen._febr._març_abr._maig_juny_jul._ag._set._oct._nov._des.".split("_"), monthsParseExact: !0, weekdays: "diumenge_dilluns_dimarts_dimecres_dijous_divendres_dissabte".split("_"), weekdaysShort: "dg._dl._dt._dc._dj._dv._ds.".split("_"), weekdaysMin: "Dg_Dl_Dt_Dc_Dj_Dv_Ds".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD/MM/YYYY", LL: "[el] D MMMM [de] YYYY", ll: "D MMM YYYY", LLL: "[el] D MMMM [de] YYYY [a les] H:mm", lll: "D MMM YYYY, H:mm", LLLL: "[el] dddd D MMMM [de] YYYY [a les] H:mm", llll: "ddd D MMM YYYY, H:mm" }, calendar: { sameDay: function () { return "[avui a " + (1 !== this.hours() ? "les" : "la") + "] LT" }, nextDay: function () { return "[demà a " + (1 !== this.hours() ? "les" : "la") + "] LT" }, nextWeek: function () { return "dddd [a " + (1 !== this.hours() ? "les" : "la") + "] LT" }, lastDay: function () { return "[ahir a " + (1 !== this.hours() ? "les" : "la") + "] LT" }, lastWeek: function () { return "[el] dddd [passat a " + (1 !== this.hours() ? "les" : "la") + "] LT" }, sameElse: "L" }, relativeTime: { future: "d'aquí %s", past: "fa %s", s: "uns segons", m: "un minut", mm: "%d minuts", h: "una hora", hh: "%d hores", d: "un dia", dd: "%d dies", M: "un mes", MM: "%d mesos", y: "un any", yy: "%d anys" }, dayOfMonthOrdinalParse: /\d{1,2}(r|n|t|è|a)/, ordinal: function (a, b) { var c = 1 === a ? "r" : 2 === a ? "n" : 3 === a ? "r" : 4 === a ? "t" : "è"; return "w" !== b && "W" !== b || (c = "a"), a + c }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Czech [cs]
	//! author : petrbela : https://github.com/petrbela
	var Pg = "leden_únor_březen_duben_květen_červen_červenec_srpen_září_říjen_listopad_prosinec".split("_"), Qg = "led_úno_bře_dub_kvě_čvn_čvc_srp_zář_říj_lis_pro".split("_"); a.defineLocale("cs", { months: Pg, monthsShort: Qg, monthsParse: function (a, b) { var c, d = []; for (c = 0; c < 12; c++)d[c] = new RegExp("^" + a[c] + "$|^" + b[c] + "$", "i"); return d }(Pg, Qg), shortMonthsParse: function (a) { var b, c = []; for (b = 0; b < 12; b++)c[b] = new RegExp("^" + a[b] + "$", "i"); return c }(Qg), longMonthsParse: function (a) { var b, c = []; for (b = 0; b < 12; b++)c[b] = new RegExp("^" + a[b] + "$", "i"); return c }(Pg), weekdays: "neděle_pondělí_úterý_středa_čtvrtek_pátek_sobota".split("_"), weekdaysShort: "ne_po_út_st_čt_pá_so".split("_"), weekdaysMin: "ne_po_út_st_čt_pá_so".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd D. MMMM YYYY H:mm", l: "D. M. YYYY" }, calendar: { sameDay: "[dnes v] LT", nextDay: "[zítra v] LT", nextWeek: function () { switch (this.day()) { case 0: return "[v neděli v] LT"; case 1: case 2: return "[v] dddd [v] LT"; case 3: return "[ve středu v] LT"; case 4: return "[ve čtvrtek v] LT"; case 5: return "[v pátek v] LT"; case 6: return "[v sobotu v] LT" } }, lastDay: "[včera v] LT", lastWeek: function () { switch (this.day()) { case 0: return "[minulou neděli v] LT"; case 1: case 2: return "[minulé] dddd [v] LT"; case 3: return "[minulou středu v] LT"; case 4: case 5: return "[minulý] dddd [v] LT"; case 6: return "[minulou sobotu v] LT" } }, sameElse: "L" }, relativeTime: { future: "za %s", past: "před %s", s: Bd, m: Bd, mm: Bd, h: Bd, hh: Bd, d: Bd, dd: Bd, M: Bd, MM: Bd, y: Bd, yy: Bd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Chuvash [cv]
		//! author : Anatoly Mironov : https://github.com/mirontoli
		a.defineLocale("cv", { months: "кӑрлач_нарӑс_пуш_ака_май_ҫӗртме_утӑ_ҫурла_авӑн_юпа_чӳк_раштав".split("_"), monthsShort: "кӑр_нар_пуш_ака_май_ҫӗр_утӑ_ҫур_авн_юпа_чӳк_раш".split("_"), weekdays: "вырсарникун_тунтикун_ытларикун_юнкун_кӗҫнерникун_эрнекун_шӑматкун".split("_"), weekdaysShort: "выр_тун_ытл_юн_кӗҫ_эрн_шӑм".split("_"), weekdaysMin: "вр_тн_ыт_юн_кҫ_эр_шм".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD-MM-YYYY", LL: "YYYY [ҫулхи] MMMM [уйӑхӗн] D[-мӗшӗ]", LLL: "YYYY [ҫулхи] MMMM [уйӑхӗн] D[-мӗшӗ], HH:mm", LLLL: "dddd, YYYY [ҫулхи] MMMM [уйӑхӗн] D[-мӗшӗ], HH:mm" }, calendar: { sameDay: "[Паян] LT [сехетре]", nextDay: "[Ыран] LT [сехетре]", lastDay: "[Ӗнер] LT [сехетре]", nextWeek: "[Ҫитес] dddd LT [сехетре]", lastWeek: "[Иртнӗ] dddd LT [сехетре]", sameElse: "L" }, relativeTime: { future: function (a) { var b = /сехет$/i.exec(a) ? "рен" : /ҫул$/i.exec(a) ? "тан" : "ран"; return a + b }, past: "%s каялла", s: "пӗр-ик ҫеккунт", m: "пӗр минут", mm: "%d минут", h: "пӗр сехет", hh: "%d сехет", d: "пӗр кун", dd: "%d кун", M: "пӗр уйӑх", MM: "%d уйӑх", y: "пӗр ҫул", yy: "%d ҫул" }, dayOfMonthOrdinalParse: /\d{1,2}-мӗш/, ordinal: "%d-мӗш", week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Welsh [cy]
		//! author : Robert Allen : https://github.com/robgallen
		//! author : https://github.com/ryangreaves
		a.defineLocale("cy", { months: "Ionawr_Chwefror_Mawrth_Ebrill_Mai_Mehefin_Gorffennaf_Awst_Medi_Hydref_Tachwedd_Rhagfyr".split("_"), monthsShort: "Ion_Chwe_Maw_Ebr_Mai_Meh_Gor_Aws_Med_Hyd_Tach_Rhag".split("_"), weekdays: "Dydd Sul_Dydd Llun_Dydd Mawrth_Dydd Mercher_Dydd Iau_Dydd Gwener_Dydd Sadwrn".split("_"), weekdaysShort: "Sul_Llun_Maw_Mer_Iau_Gwe_Sad".split("_"), weekdaysMin: "Su_Ll_Ma_Me_Ia_Gw_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Heddiw am] LT", nextDay: "[Yfory am] LT", nextWeek: "dddd [am] LT", lastDay: "[Ddoe am] LT", lastWeek: "dddd [diwethaf am] LT", sameElse: "L" }, relativeTime: { future: "mewn %s", past: "%s yn ôl", s: "ychydig eiliadau", m: "munud", mm: "%d munud", h: "awr", hh: "%d awr", d: "diwrnod", dd: "%d diwrnod", M: "mis", MM: "%d mis", y: "blwyddyn", yy: "%d flynedd" }, dayOfMonthOrdinalParse: /\d{1,2}(fed|ain|af|il|ydd|ed|eg)/, ordinal: function (a) { var b = a, c = "", d = ["", "af", "il", "ydd", "ydd", "ed", "ed", "ed", "fed", "fed", "fed", "eg", "fed", "eg", "eg", "fed", "eg", "eg", "fed", "eg", "fed"]; return b > 20 ? c = 40 === b || 50 === b || 60 === b || 80 === b || 100 === b ? "fed" : "ain" : b > 0 && (c = d[b]), a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Danish [da]
		//! author : Ulrik Nielsen : https://github.com/mrbase
		a.defineLocale("da", { months: "januar_februar_marts_april_maj_juni_juli_august_september_oktober_november_december".split("_"), monthsShort: "jan_feb_mar_apr_maj_jun_jul_aug_sep_okt_nov_dec".split("_"), weekdays: "søndag_mandag_tirsdag_onsdag_torsdag_fredag_lørdag".split("_"), weekdaysShort: "søn_man_tir_ons_tor_fre_lør".split("_"), weekdaysMin: "sø_ma_ti_on_to_fr_lø".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY HH:mm", LLLL: "dddd [d.] D. MMMM YYYY [kl.] HH:mm" }, calendar: { sameDay: "[i dag kl.] LT", nextDay: "[i morgen kl.] LT", nextWeek: "på dddd [kl.] LT", lastDay: "[i går kl.] LT", lastWeek: "[i] dddd[s kl.] LT", sameElse: "L" }, relativeTime: { future: "om %s", past: "%s siden", s: "få sekunder", m: "et minut", mm: "%d minutter", h: "en time", hh: "%d timer", d: "en dag", dd: "%d dage", M: "en måned", MM: "%d måneder", y: "et år", yy: "%d år" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }), a.defineLocale("de-at", { months: "Jänner_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"), monthsShort: "Jän._Febr._Mrz._Apr._Mai_Jun._Jul._Aug._Sept._Okt._Nov._Dez.".split("_"), monthsParseExact: !0, weekdays: "Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"), weekdaysShort: "So._Mo._Di._Mi._Do._Fr._Sa.".split("_"), weekdaysMin: "So_Mo_Di_Mi_Do_Fr_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY HH:mm", LLLL: "dddd, D. MMMM YYYY HH:mm" }, calendar: { sameDay: "[heute um] LT [Uhr]", sameElse: "L", nextDay: "[morgen um] LT [Uhr]", nextWeek: "dddd [um] LT [Uhr]", lastDay: "[gestern um] LT [Uhr]", lastWeek: "[letzten] dddd [um] LT [Uhr]" }, relativeTime: { future: "in %s", past: "vor %s", s: "ein paar Sekunden", m: Cd, mm: "%d Minuten", h: Cd, hh: "%d Stunden", d: Cd, dd: Cd, M: Cd, MM: Cd, y: Cd, yy: Cd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }), a.defineLocale("de-ch", { months: "Januar_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"), monthsShort: "Jan._Febr._März_April_Mai_Juni_Juli_Aug._Sept._Okt._Nov._Dez.".split("_"), monthsParseExact: !0, weekdays: "Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"), weekdaysShort: "So_Mo_Di_Mi_Do_Fr_Sa".split("_"), weekdaysMin: "So_Mo_Di_Mi_Do_Fr_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY HH.mm", LLLL: "dddd, D. MMMM YYYY HH.mm" }, calendar: { sameDay: "[heute um] LT [Uhr]", sameElse: "L", nextDay: "[morgen um] LT [Uhr]", nextWeek: "dddd [um] LT [Uhr]", lastDay: "[gestern um] LT [Uhr]", lastWeek: "[letzten] dddd [um] LT [Uhr]" }, relativeTime: { future: "in %s", past: "vor %s", s: "ein paar Sekunden", m: Dd, mm: "%d Minuten", h: Dd, hh: "%d Stunden", d: Dd, dd: Dd, M: Dd, MM: Dd, y: Dd, yy: Dd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }), a.defineLocale("de", { months: "Januar_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"), monthsShort: "Jan._Febr._Mrz._Apr._Mai_Jun._Jul._Aug._Sept._Okt._Nov._Dez.".split("_"), monthsParseExact: !0, weekdays: "Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"), weekdaysShort: "So._Mo._Di._Mi._Do._Fr._Sa.".split("_"), weekdaysMin: "So_Mo_Di_Mi_Do_Fr_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY HH:mm", LLLL: "dddd, D. MMMM YYYY HH:mm" }, calendar: { sameDay: "[heute um] LT [Uhr]", sameElse: "L", nextDay: "[morgen um] LT [Uhr]", nextWeek: "dddd [um] LT [Uhr]", lastDay: "[gestern um] LT [Uhr]", lastWeek: "[letzten] dddd [um] LT [Uhr]" }, relativeTime: { future: "in %s", past: "vor %s", s: "ein paar Sekunden", m: Ed, mm: "%d Minuten", h: Ed, hh: "%d Stunden", d: Ed, dd: Ed, M: Ed, MM: Ed, y: Ed, yy: Ed }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Maldivian [dv]
	//! author : Jawish Hameed : https://github.com/jawish
	var Rg = ["ޖެނުއަރީ", "ފެބްރުއަރީ", "މާރިޗު", "އޭޕްރީލު", "މޭ", "ޖޫން", "ޖުލައި", "އޯގަސްޓު", "ސެޕްޓެމްބަރު", "އޮކްޓޯބަރު", "ނޮވެމްބަރު", "ޑިސެމްބަރު"], Sg = ["އާދިއްތަ", "ހޯމަ", "އަންގާރަ", "ބުދަ", "ބުރާސްފަތި", "ހުކުރު", "ހޮނިހިރު"]; a.defineLocale("dv", { months: Rg, monthsShort: Rg, weekdays: Sg, weekdaysShort: Sg, weekdaysMin: "އާދި_ހޯމަ_އަން_ބުދަ_ބުރާ_ހުކު_ހޮނި".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "D/M/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, meridiemParse: /މކ|މފ/, isPM: function (a) { return "މފ" === a }, meridiem: function (a, b, c) { return a < 12 ? "މކ" : "މފ" }, calendar: { sameDay: "[މިއަދު] LT", nextDay: "[މާދަމާ] LT", nextWeek: "dddd LT", lastDay: "[އިއްޔެ] LT", lastWeek: "[ފާއިތުވި] dddd LT", sameElse: "L" }, relativeTime: { future: "ތެރޭގައި %s", past: "ކުރިން %s", s: "ސިކުންތުކޮޅެއް", m: "މިނިޓެއް", mm: "މިނިޓު %d", h: "ގަޑިއިރެއް", hh: "ގަޑިއިރު %d", d: "ދުވަހެއް", dd: "ދުވަސް %d", M: "މަހެއް", MM: "މަސް %d", y: "އަހަރެއް", yy: "އަހަރު %d" }, preparse: function (a) { return a.replace(/،/g, ",") }, postformat: function (a) { return a.replace(/,/g, "،") }, week: { dow: 7, doy: 12 } }),
		//! moment.js locale configuration
		//! locale : Greek [el]
		//! author : Aggelos Karalias : https://github.com/mehiel
		a.defineLocale("el", { monthsNominativeEl: "Ιανουάριος_Φεβρουάριος_Μάρτιος_Απρίλιος_Μάιος_Ιούνιος_Ιούλιος_Αύγουστος_Σεπτέμβριος_Οκτώβριος_Νοέμβριος_Δεκέμβριος".split("_"), monthsGenitiveEl: "Ιανουαρίου_Φεβρουαρίου_Μαρτίου_Απριλίου_Μαΐου_Ιουνίου_Ιουλίου_Αυγούστου_Σεπτεμβρίου_Οκτωβρίου_Νοεμβρίου_Δεκεμβρίου".split("_"), months: function (a, b) { return a ? /D/.test(b.substring(0, b.indexOf("MMMM"))) ? this._monthsGenitiveEl[a.month()] : this._monthsNominativeEl[a.month()] : this._monthsNominativeEl }, monthsShort: "Ιαν_Φεβ_Μαρ_Απρ_Μαϊ_Ιουν_Ιουλ_Αυγ_Σεπ_Οκτ_Νοε_Δεκ".split("_"), weekdays: "Κυριακή_Δευτέρα_Τρίτη_Τετάρτη_Πέμπτη_Παρασκευή_Σάββατο".split("_"), weekdaysShort: "Κυρ_Δευ_Τρι_Τετ_Πεμ_Παρ_Σαβ".split("_"), weekdaysMin: "Κυ_Δε_Τρ_Τε_Πε_Πα_Σα".split("_"), meridiem: function (a, b, c) { return a > 11 ? c ? "μμ" : "ΜΜ" : c ? "πμ" : "ΠΜ" }, isPM: function (a) { return "μ" === (a + "").toLowerCase()[0] }, meridiemParse: /[ΠΜ]\.?Μ?\.?/i, longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendarEl: { sameDay: "[Σήμερα {}] LT", nextDay: "[Αύριο {}] LT", nextWeek: "dddd [{}] LT", lastDay: "[Χθες {}] LT", lastWeek: function () { switch (this.day()) { case 6: return "[το προηγούμενο] dddd [{}] LT"; default: return "[την προηγούμενη] dddd [{}] LT" } }, sameElse: "L" }, calendar: function (a, b) { var c = this._calendarEl[a], d = b && b.hours(); return z(c) && (c = c.apply(b)), c.replace("{}", d % 12 === 1 ? "στη" : "στις") }, relativeTime: { future: "σε %s", past: "%s πριν", s: "λίγα δευτερόλεπτα", m: "ένα λεπτό", mm: "%d λεπτά", h: "μία ώρα", hh: "%d ώρες", d: "μία μέρα", dd: "%d μέρες", M: "ένας μήνας", MM: "%d μήνες", y: "ένας χρόνος", yy: "%d χρόνια" }, dayOfMonthOrdinalParse: /\d{1,2}η/, ordinal: "%dη", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : English (Australia) [en-au]
		//! author : Jared Morse : https://github.com/jarcoal
		a.defineLocale("en-au", { months: "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), monthsShort: "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), weekdays: "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), weekdaysShort: "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), weekdaysMin: "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendar: { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, relativeTime: { future: "in %s", past: "%s ago", s: "a few seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : English (Canada) [en-ca]
		//! author : Jonathan Abourbih : https://github.com/jonbca
		a.defineLocale("en-ca", { months: "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), monthsShort: "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), weekdays: "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), weekdaysShort: "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), weekdaysMin: "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "YYYY-MM-DD", LL: "MMMM D, YYYY", LLL: "MMMM D, YYYY h:mm A", LLLL: "dddd, MMMM D, YYYY h:mm A" }, calendar: { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, relativeTime: { future: "in %s", past: "%s ago", s: "a few seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c } }),
		//! moment.js locale configuration
		//! locale : English (United Kingdom) [en-gb]
		//! author : Chris Gedrim : https://github.com/chrisgedrim
		a.defineLocale("en-gb", { months: "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), monthsShort: "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), weekdays: "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), weekdaysShort: "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), weekdaysMin: "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, relativeTime: { future: "in %s", past: "%s ago", s: "a few seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : English (Ireland) [en-ie]
		//! author : Chris Cartlidge : https://github.com/chriscartlidge
		a.defineLocale("en-ie", { months: "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), monthsShort: "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), weekdays: "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), weekdaysShort: "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), weekdaysMin: "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD-MM-YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, relativeTime: { future: "in %s", past: "%s ago", s: "a few seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : English (New Zealand) [en-nz]
		//! author : Luke McGregor : https://github.com/lukemcgregor
		a.defineLocale("en-nz", { months: "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), monthsShort: "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), weekdays: "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), weekdaysShort: "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), weekdaysMin: "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendar: { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, relativeTime: { future: "in %s", past: "%s ago", s: "a few seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Esperanto [eo]
		//! author : Colin Dean : https://github.com/colindean
		//! author : Mia Nordentoft Imperatori : https://github.com/miestasmia
		//! comment : miestasmia corrected the translation by colindean
		a.defineLocale("eo", { months: "januaro_februaro_marto_aprilo_majo_junio_julio_aŭgusto_septembro_oktobro_novembro_decembro".split("_"), monthsShort: "jan_feb_mar_apr_maj_jun_jul_aŭg_sep_okt_nov_dec".split("_"), weekdays: "dimanĉo_lundo_mardo_merkredo_ĵaŭdo_vendredo_sabato".split("_"), weekdaysShort: "dim_lun_mard_merk_ĵaŭ_ven_sab".split("_"), weekdaysMin: "di_lu_ma_me_ĵa_ve_sa".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY-MM-DD", LL: "D[-a de] MMMM, YYYY", LLL: "D[-a de] MMMM, YYYY HH:mm", LLLL: "dddd, [la] D[-a de] MMMM, YYYY HH:mm" }, meridiemParse: /[ap]\.t\.m/i, isPM: function (a) { return "p" === a.charAt(0).toLowerCase() }, meridiem: function (a, b, c) { return a > 11 ? c ? "p.t.m." : "P.T.M." : c ? "a.t.m." : "A.T.M." }, calendar: { sameDay: "[Hodiaŭ je] LT", nextDay: "[Morgaŭ je] LT", nextWeek: "dddd [je] LT", lastDay: "[Hieraŭ je] LT", lastWeek: "[pasinta] dddd [je] LT", sameElse: "L" }, relativeTime: { future: "post %s", past: "antaŭ %s", s: "sekundoj", m: "minuto", mm: "%d minutoj", h: "horo", hh: "%d horoj", d: "tago", dd: "%d tagoj", M: "monato", MM: "%d monatoj", y: "jaro", yy: "%d jaroj" }, dayOfMonthOrdinalParse: /\d{1,2}a/, ordinal: "%da", week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Spanish (Dominican Republic) [es-do]
	var Tg = "ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_"), Ug = "ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"); a.defineLocale("es-do", { months: "enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"), monthsShort: function (a, b) { return a ? /-MMM-/.test(b) ? Ug[a.month()] : Tg[a.month()] : Tg }, monthsParseExact: !0, weekdays: "domingo_lunes_martes_miércoles_jueves_viernes_sábado".split("_"), weekdaysShort: "dom._lun._mar._mié._jue._vie._sáb.".split("_"), weekdaysMin: "do_lu_ma_mi_ju_vi_sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D [de] MMMM [de] YYYY", LLL: "D [de] MMMM [de] YYYY h:mm A", LLLL: "dddd, D [de] MMMM [de] YYYY h:mm A" }, calendar: { sameDay: function () { return "[hoy a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, nextDay: function () { return "[mañana a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, nextWeek: function () { return "dddd [a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, lastDay: function () { return "[ayer a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, lastWeek: function () { return "[el] dddd [pasado a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, sameElse: "L" }, relativeTime: { future: "en %s", past: "hace %s", s: "unos segundos", m: "un minuto", mm: "%d minutos", h: "una hora", hh: "%d horas", d: "un día", dd: "%d días", M: "un mes", MM: "%d meses", y: "un año", yy: "%d años" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Spanish [es]
	//! author : Julio Napurí : https://github.com/julionc
	var Vg = "ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_"), Wg = "ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"); a.defineLocale("es", { months: "enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"), monthsShort: function (a, b) { return a ? /-MMM-/.test(b) ? Wg[a.month()] : Vg[a.month()] : Vg }, monthsParseExact: !0, weekdays: "domingo_lunes_martes_miércoles_jueves_viernes_sábado".split("_"), weekdaysShort: "dom._lun._mar._mié._jue._vie._sáb.".split("_"), weekdaysMin: "do_lu_ma_mi_ju_vi_sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD/MM/YYYY", LL: "D [de] MMMM [de] YYYY", LLL: "D [de] MMMM [de] YYYY H:mm", LLLL: "dddd, D [de] MMMM [de] YYYY H:mm" }, calendar: { sameDay: function () { return "[hoy a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, nextDay: function () { return "[mañana a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, nextWeek: function () { return "dddd [a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, lastDay: function () { return "[ayer a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, lastWeek: function () { return "[el] dddd [pasado a la" + (1 !== this.hours() ? "s" : "") + "] LT" }, sameElse: "L" }, relativeTime: { future: "en %s", past: "hace %s", s: "unos segundos", m: "un minuto", mm: "%d minutos", h: "una hora", hh: "%d horas", d: "un día", dd: "%d días", M: "un mes", MM: "%d meses", y: "un año", yy: "%d años" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } }), a.defineLocale("et", { months: "jaanuar_veebruar_märts_aprill_mai_juuni_juuli_august_september_oktoober_november_detsember".split("_"), monthsShort: "jaan_veebr_märts_apr_mai_juuni_juuli_aug_sept_okt_nov_dets".split("_"), weekdays: "pühapäev_esmaspäev_teisipäev_kolmapäev_neljapäev_reede_laupäev".split("_"), weekdaysShort: "P_E_T_K_N_R_L".split("_"), weekdaysMin: "P_E_T_K_N_R_L".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[Täna,] LT", nextDay: "[Homme,] LT", nextWeek: "[Järgmine] dddd LT", lastDay: "[Eile,] LT", lastWeek: "[Eelmine] dddd LT", sameElse: "L" }, relativeTime: { future: "%s pärast", past: "%s tagasi", s: Fd, m: Fd, mm: Fd, h: Fd, hh: Fd, d: Fd, dd: "%d päeva", M: Fd, MM: Fd, y: Fd, yy: Fd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Basque [eu]
		//! author : Eneko Illarramendi : https://github.com/eillarra
		a.defineLocale("eu", { months: "urtarrila_otsaila_martxoa_apirila_maiatza_ekaina_uztaila_abuztua_iraila_urria_azaroa_abendua".split("_"), monthsShort: "urt._ots._mar._api._mai._eka._uzt._abu._ira._urr._aza._abe.".split("_"), monthsParseExact: !0, weekdays: "igandea_astelehena_asteartea_asteazkena_osteguna_ostirala_larunbata".split("_"), weekdaysShort: "ig._al._ar._az._og._ol._lr.".split("_"), weekdaysMin: "ig_al_ar_az_og_ol_lr".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY-MM-DD", LL: "YYYY[ko] MMMM[ren] D[a]", LLL: "YYYY[ko] MMMM[ren] D[a] HH:mm", LLLL: "dddd, YYYY[ko] MMMM[ren] D[a] HH:mm", l: "YYYY-M-D", ll: "YYYY[ko] MMM D[a]", lll: "YYYY[ko] MMM D[a] HH:mm", llll: "ddd, YYYY[ko] MMM D[a] HH:mm" }, calendar: { sameDay: "[gaur] LT[etan]", nextDay: "[bihar] LT[etan]", nextWeek: "dddd LT[etan]", lastDay: "[atzo] LT[etan]", lastWeek: "[aurreko] dddd LT[etan]", sameElse: "L" }, relativeTime: { future: "%s barru", past: "duela %s", s: "segundo batzuk", m: "minutu bat", mm: "%d minutu", h: "ordu bat", hh: "%d ordu", d: "egun bat", dd: "%d egun", M: "hilabete bat", MM: "%d hilabete", y: "urte bat", yy: "%d urte" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Persian [fa]
	//! author : Ebrahim Byagowi : https://github.com/ebraminio
	var Xg = { 1: "۱", 2: "۲", 3: "۳", 4: "۴", 5: "۵", 6: "۶", 7: "۷", 8: "۸", 9: "۹", 0: "۰" }, Yg = { "۱": "1", "۲": "2", "۳": "3", "۴": "4", "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9", "۰": "0" }; a.defineLocale("fa", { months: "ژانویه_فوریه_مارس_آوریل_مه_ژوئن_ژوئیه_اوت_سپتامبر_اکتبر_نوامبر_دسامبر".split("_"), monthsShort: "ژانویه_فوریه_مارس_آوریل_مه_ژوئن_ژوئیه_اوت_سپتامبر_اکتبر_نوامبر_دسامبر".split("_"), weekdays: "یک‌شنبه_دوشنبه_سه‌شنبه_چهارشنبه_پنج‌شنبه_جمعه_شنبه".split("_"), weekdaysShort: "یک‌شنبه_دوشنبه_سه‌شنبه_چهارشنبه_پنج‌شنبه_جمعه_شنبه".split("_"), weekdaysMin: "ی_د_س_چ_پ_ج_ش".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, meridiemParse: /قبل از ظهر|بعد از ظهر/, isPM: function (a) { return /بعد از ظهر/.test(a) }, meridiem: function (a, b, c) { return a < 12 ? "قبل از ظهر" : "بعد از ظهر" }, calendar: { sameDay: "[امروز ساعت] LT", nextDay: "[فردا ساعت] LT", nextWeek: "dddd [ساعت] LT", lastDay: "[دیروز ساعت] LT", lastWeek: "dddd [پیش] [ساعت] LT", sameElse: "L" }, relativeTime: { future: "در %s", past: "%s پیش", s: "چند ثانیه", m: "یک دقیقه", mm: "%d دقیقه", h: "یک ساعت", hh: "%d ساعت", d: "یک روز", dd: "%d روز", M: "یک ماه", MM: "%d ماه", y: "یک سال", yy: "%d سال" }, preparse: function (a) { return a.replace(/[۰-۹]/g, function (a) { return Yg[a] }).replace(/،/g, ",") }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Xg[a] }).replace(/,/g, "،") }, dayOfMonthOrdinalParse: /\d{1,2}م/, ordinal: "%dم", week: { dow: 6, doy: 12 } });
	//! moment.js locale configuration
	//! locale : Finnish [fi]
	//! author : Tarmo Aidantausta : https://github.com/bleadof
	var Zg = "nolla yksi kaksi kolme neljä viisi kuusi seitsemän kahdeksan yhdeksän".split(" "), $g = ["nolla", "yhden", "kahden", "kolmen", "neljän", "viiden", "kuuden", Zg[7], Zg[8], Zg[9]]; a.defineLocale("fi", { months: "tammikuu_helmikuu_maaliskuu_huhtikuu_toukokuu_kesäkuu_heinäkuu_elokuu_syyskuu_lokakuu_marraskuu_joulukuu".split("_"), monthsShort: "tammi_helmi_maalis_huhti_touko_kesä_heinä_elo_syys_loka_marras_joulu".split("_"), weekdays: "sunnuntai_maanantai_tiistai_keskiviikko_torstai_perjantai_lauantai".split("_"), weekdaysShort: "su_ma_ti_ke_to_pe_la".split("_"), weekdaysMin: "su_ma_ti_ke_to_pe_la".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD.MM.YYYY", LL: "Do MMMM[ta] YYYY", LLL: "Do MMMM[ta] YYYY, [klo] HH.mm", LLLL: "dddd, Do MMMM[ta] YYYY, [klo] HH.mm", l: "D.M.YYYY", ll: "Do MMM YYYY", lll: "Do MMM YYYY, [klo] HH.mm", llll: "ddd, Do MMM YYYY, [klo] HH.mm" }, calendar: { sameDay: "[tänään] [klo] LT", nextDay: "[huomenna] [klo] LT", nextWeek: "dddd [klo] LT", lastDay: "[eilen] [klo] LT", lastWeek: "[viime] dddd[na] [klo] LT", sameElse: "L" }, relativeTime: { future: "%s päästä", past: "%s sitten", s: Gd, m: Gd, mm: Gd, h: Gd, hh: Gd, d: Gd, dd: Gd, M: Gd, MM: Gd, y: Gd, yy: Gd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Faroese [fo]
		//! author : Ragnar Johannesen : https://github.com/ragnar123
		a.defineLocale("fo", { months: "januar_februar_mars_apríl_mai_juni_juli_august_september_oktober_november_desember".split("_"), monthsShort: "jan_feb_mar_apr_mai_jun_jul_aug_sep_okt_nov_des".split("_"), weekdays: "sunnudagur_mánadagur_týsdagur_mikudagur_hósdagur_fríggjadagur_leygardagur".split("_"), weekdaysShort: "sun_mán_týs_mik_hós_frí_ley".split("_"), weekdaysMin: "su_má_tý_mi_hó_fr_le".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D. MMMM, YYYY HH:mm" }, calendar: { sameDay: "[Í dag kl.] LT", nextDay: "[Í morgin kl.] LT", nextWeek: "dddd [kl.] LT", lastDay: "[Í gjár kl.] LT", lastWeek: "[síðstu] dddd [kl] LT", sameElse: "L" }, relativeTime: { future: "um %s", past: "%s síðani", s: "fá sekund", m: "ein minutt", mm: "%d minuttir", h: "ein tími", hh: "%d tímar", d: "ein dagur", dd: "%d dagar", M: "ein mánaði", MM: "%d mánaðir", y: "eitt ár", yy: "%d ár" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : French (Canada) [fr-ca]
		//! author : Jonathan Abourbih : https://github.com/jonbca
		a.defineLocale("fr-ca", { months: "janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre".split("_"), monthsShort: "janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.".split("_"), monthsParseExact: !0, weekdays: "dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"), weekdaysShort: "dim._lun._mar._mer._jeu._ven._sam.".split("_"), weekdaysMin: "Di_Lu_Ma_Me_Je_Ve_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY-MM-DD", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Aujourd’hui à] LT", nextDay: "[Demain à] LT", nextWeek: "dddd [à] LT", lastDay: "[Hier à] LT", lastWeek: "dddd [dernier à] LT", sameElse: "L" }, relativeTime: { future: "dans %s", past: "il y a %s", s: "quelques secondes", m: "une minute", mm: "%d minutes", h: "une heure", hh: "%d heures", d: "un jour", dd: "%d jours", M: "un mois", MM: "%d mois", y: "un an", yy: "%d ans" }, dayOfMonthOrdinalParse: /\d{1,2}(er|e)/, ordinal: function (a, b) { switch (b) { default: case "M": case "Q": case "D": case "DDD": case "d": return a + (1 === a ? "er" : "e"); case "w": case "W": return a + (1 === a ? "re" : "e") } } }),
		//! moment.js locale configuration
		//! locale : French (Switzerland) [fr-ch]
		//! author : Gaspard Bucher : https://github.com/gaspard
		a.defineLocale("fr-ch", { months: "janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre".split("_"), monthsShort: "janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.".split("_"), monthsParseExact: !0, weekdays: "dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"), weekdaysShort: "dim._lun._mar._mer._jeu._ven._sam.".split("_"), weekdaysMin: "Di_Lu_Ma_Me_Je_Ve_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Aujourd’hui à] LT", nextDay: "[Demain à] LT", nextWeek: "dddd [à] LT", lastDay: "[Hier à] LT", lastWeek: "dddd [dernier à] LT", sameElse: "L" }, relativeTime: { future: "dans %s", past: "il y a %s", s: "quelques secondes", m: "une minute", mm: "%d minutes", h: "une heure", hh: "%d heures", d: "un jour", dd: "%d jours", M: "un mois", MM: "%d mois", y: "un an", yy: "%d ans" }, dayOfMonthOrdinalParse: /\d{1,2}(er|e)/, ordinal: function (a, b) { switch (b) { default: case "M": case "Q": case "D": case "DDD": case "d": return a + (1 === a ? "er" : "e"); case "w": case "W": return a + (1 === a ? "re" : "e") } }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : French [fr]
		//! author : John Fischer : https://github.com/jfroffice
		a.defineLocale("fr", { months: "janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre".split("_"), monthsShort: "janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.".split("_"), monthsParseExact: !0, weekdays: "dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"), weekdaysShort: "dim._lun._mar._mer._jeu._ven._sam.".split("_"), weekdaysMin: "Di_Lu_Ma_Me_Je_Ve_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Aujourd’hui à] LT", nextDay: "[Demain à] LT", nextWeek: "dddd [à] LT", lastDay: "[Hier à] LT", lastWeek: "dddd [dernier à] LT", sameElse: "L" }, relativeTime: { future: "dans %s", past: "il y a %s", s: "quelques secondes", m: "une minute", mm: "%d minutes", h: "une heure", hh: "%d heures", d: "un jour", dd: "%d jours", M: "un mois", MM: "%d mois", y: "un an", yy: "%d ans" }, dayOfMonthOrdinalParse: /\d{1,2}(er|)/, ordinal: function (a, b) { switch (b) { case "D": return a + (1 === a ? "er" : ""); default: case "M": case "Q": case "DDD": case "d": return a + (1 === a ? "er" : "e"); case "w": case "W": return a + (1 === a ? "re" : "e") } }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Frisian [fy]
	//! author : Robin van der Vliet : https://github.com/robin0van0der0v
	var _g = "jan._feb._mrt._apr._mai_jun._jul._aug._sep._okt._nov._des.".split("_"), ah = "jan_feb_mrt_apr_mai_jun_jul_aug_sep_okt_nov_des".split("_"); a.defineLocale("fy", { months: "jannewaris_febrewaris_maart_april_maaie_juny_july_augustus_septimber_oktober_novimber_desimber".split("_"), monthsShort: function (a, b) { return a ? /-MMM-/.test(b) ? ah[a.month()] : _g[a.month()] : _g }, monthsParseExact: !0, weekdays: "snein_moandei_tiisdei_woansdei_tongersdei_freed_sneon".split("_"), weekdaysShort: "si._mo._ti._wo._to._fr._so.".split("_"), weekdaysMin: "Si_Mo_Ti_Wo_To_Fr_So".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD-MM-YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[hjoed om] LT", nextDay: "[moarn om] LT", nextWeek: "dddd [om] LT", lastDay: "[juster om] LT", lastWeek: "[ôfrûne] dddd [om] LT", sameElse: "L" }, relativeTime: { future: "oer %s", past: "%s lyn", s: "in pear sekonden", m: "ien minút", mm: "%d minuten", h: "ien oere", hh: "%d oeren", d: "ien dei", dd: "%d dagen", M: "ien moanne", MM: "%d moannen", y: "ien jier", yy: "%d jierren" }, dayOfMonthOrdinalParse: /\d{1,2}(ste|de)/, ordinal: function (a) { return a + (1 === a || 8 === a || a >= 20 ? "ste" : "de") }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Scottish Gaelic [gd]
	//! author : Jon Ashdown : https://github.com/jonashdown
	var bh = ["Am Faoilleach", "An Gearran", "Am Màrt", "An Giblean", "An Cèitean", "An t-Ògmhios", "An t-Iuchar", "An Lùnastal", "An t-Sultain", "An Dàmhair", "An t-Samhain", "An Dùbhlachd"], ch = ["Faoi", "Gear", "Màrt", "Gibl", "Cèit", "Ògmh", "Iuch", "Lùn", "Sult", "Dàmh", "Samh", "Dùbh"], dh = ["Didòmhnaich", "Diluain", "Dimàirt", "Diciadain", "Diardaoin", "Dihaoine", "Disathairne"], eh = ["Did", "Dil", "Dim", "Dic", "Dia", "Dih", "Dis"], fh = ["Dò", "Lu", "Mà", "Ci", "Ar", "Ha", "Sa"]; a.defineLocale("gd", { months: bh, monthsShort: ch, monthsParseExact: !0, weekdays: dh, weekdaysShort: eh, weekdaysMin: fh, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[An-diugh aig] LT", nextDay: "[A-màireach aig] LT", nextWeek: "dddd [aig] LT", lastDay: "[An-dè aig] LT", lastWeek: "dddd [seo chaidh] [aig] LT", sameElse: "L" }, relativeTime: { future: "ann an %s", past: "bho chionn %s", s: "beagan diogan", m: "mionaid", mm: "%d mionaidean", h: "uair", hh: "%d uairean", d: "latha", dd: "%d latha", M: "mìos", MM: "%d mìosan", y: "bliadhna", yy: "%d bliadhna" }, dayOfMonthOrdinalParse: /\d{1,2}(d|na|mh)/, ordinal: function (a) { var b = 1 === a ? "d" : a % 10 === 2 ? "na" : "mh"; return a + b }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Galician [gl]
		//! author : Juan G. Hurtado : https://github.com/juanghurtado
		a.defineLocale("gl", { months: "xaneiro_febreiro_marzo_abril_maio_xuño_xullo_agosto_setembro_outubro_novembro_decembro".split("_"), monthsShort: "xan._feb._mar._abr._mai._xuñ._xul._ago._set._out._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "domingo_luns_martes_mércores_xoves_venres_sábado".split("_"), weekdaysShort: "dom._lun._mar._mér._xov._ven._sáb.".split("_"), weekdaysMin: "do_lu_ma_mé_xo_ve_sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD/MM/YYYY", LL: "D [de] MMMM [de] YYYY", LLL: "D [de] MMMM [de] YYYY H:mm", LLLL: "dddd, D [de] MMMM [de] YYYY H:mm" }, calendar: { sameDay: function () { return "[hoxe " + (1 !== this.hours() ? "ás" : "á") + "] LT" }, nextDay: function () { return "[mañá " + (1 !== this.hours() ? "ás" : "á") + "] LT" }, nextWeek: function () { return "dddd [" + (1 !== this.hours() ? "ás" : "a") + "] LT" }, lastDay: function () { return "[onte " + (1 !== this.hours() ? "á" : "a") + "] LT" }, lastWeek: function () { return "[o] dddd [pasado " + (1 !== this.hours() ? "ás" : "a") + "] LT" }, sameElse: "L" }, relativeTime: { future: function (a) { return 0 === a.indexOf("un") ? "n" + a : "en " + a }, past: "hai %s", s: "uns segundos", m: "un minuto", mm: "%d minutos", h: "unha hora", hh: "%d horas", d: "un día", dd: "%d días", M: "un mes", MM: "%d meses", y: "un ano", yy: "%d anos" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } }), a.defineLocale("gom-latn", { months: "Janer_Febrer_Mars_Abril_Mai_Jun_Julai_Agost_Setembr_Otubr_Novembr_Dezembr".split("_"), monthsShort: "Jan._Feb._Mars_Abr._Mai_Jun_Jul._Ago._Set._Otu._Nov._Dez.".split("_"), monthsParseExact: !0, weekdays: "Aitar_Somar_Mongllar_Budvar_Brestar_Sukrar_Son'var".split("_"), weekdaysShort: "Ait._Som._Mon._Bud._Bre._Suk._Son.".split("_"), weekdaysMin: "Ai_Sm_Mo_Bu_Br_Su_Sn".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "A h:mm [vazta]", LTS: "A h:mm:ss [vazta]", L: "DD-MM-YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY A h:mm [vazta]", LLLL: "dddd, MMMM[achea] Do, YYYY, A h:mm [vazta]", llll: "ddd, D MMM YYYY, A h:mm [vazta]" }, calendar: { sameDay: "[Aiz] LT", nextDay: "[Faleam] LT", nextWeek: "[Ieta to] dddd[,] LT", lastDay: "[Kal] LT", lastWeek: "[Fatlo] dddd[,] LT", sameElse: "L" }, relativeTime: { future: "%s", past: "%s adim", s: Id, m: Id, mm: Id, h: Id, hh: Id, d: Id, dd: Id, M: Id, MM: Id, y: Id, yy: Id }, dayOfMonthOrdinalParse: /\d{1,2}(er)/, ordinal: function (a, b) { switch (b) { case "D": return a + "er"; default: case "M": case "Q": case "DDD": case "d": case "w": case "W": return a } }, week: { dow: 1, doy: 4 }, meridiemParse: /rati|sokalli|donparam|sanje/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "rati" === b ? a < 4 ? a : a + 12 : "sokalli" === b ? a : "donparam" === b ? a > 12 ? a : a + 12 : "sanje" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "rati" : a < 12 ? "sokalli" : a < 16 ? "donparam" : a < 20 ? "sanje" : "rati" } }),
		//! moment.js locale configuration
		//! locale : Hebrew [he]
		//! author : Tomer Cohen : https://github.com/tomer
		//! author : Moshe Simantov : https://github.com/DevelopmentIL
		//! author : Tal Ater : https://github.com/TalAter
		a.defineLocale("he", { months: "ינואר_פברואר_מרץ_אפריל_מאי_יוני_יולי_אוגוסט_ספטמבר_אוקטובר_נובמבר_דצמבר".split("_"), monthsShort: "ינו׳_פבר׳_מרץ_אפר׳_מאי_יוני_יולי_אוג׳_ספט׳_אוק׳_נוב׳_דצמ׳".split("_"), weekdays: "ראשון_שני_שלישי_רביעי_חמישי_שישי_שבת".split("_"), weekdaysShort: "א׳_ב׳_ג׳_ד׳_ה׳_ו׳_ש׳".split("_"), weekdaysMin: "א_ב_ג_ד_ה_ו_ש".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D [ב]MMMM YYYY", LLL: "D [ב]MMMM YYYY HH:mm", LLLL: "dddd, D [ב]MMMM YYYY HH:mm", l: "D/M/YYYY", ll: "D MMM YYYY", lll: "D MMM YYYY HH:mm", llll: "ddd, D MMM YYYY HH:mm" }, calendar: { sameDay: "[היום ב־]LT", nextDay: "[מחר ב־]LT", nextWeek: "dddd [בשעה] LT", lastDay: "[אתמול ב־]LT", lastWeek: "[ביום] dddd [האחרון בשעה] LT", sameElse: "L" }, relativeTime: { future: "בעוד %s", past: "לפני %s", s: "מספר שניות", m: "דקה", mm: "%d דקות", h: "שעה", hh: function (a) { return 2 === a ? "שעתיים" : a + " שעות" }, d: "יום", dd: function (a) { return 2 === a ? "יומיים" : a + " ימים" }, M: "חודש", MM: function (a) { return 2 === a ? "חודשיים" : a + " חודשים" }, y: "שנה", yy: function (a) { return 2 === a ? "שנתיים" : a % 10 === 0 && 10 !== a ? a + " שנה" : a + " שנים" } }, meridiemParse: /אחה"צ|לפנה"צ|אחרי הצהריים|לפני הצהריים|לפנות בוקר|בבוקר|בערב/i, isPM: function (a) { return /^(אחה"צ|אחרי הצהריים|בערב)$/.test(a) }, meridiem: function (a, b, c) { return a < 5 ? "לפנות בוקר" : a < 10 ? "בבוקר" : a < 12 ? c ? 'לפנה"צ' : "לפני הצהריים" : a < 18 ? c ? 'אחה"צ' : "אחרי הצהריים" : "בערב" } });
	//! moment.js locale configuration
	//! locale : Hindi [hi]
	//! author : Mayank Singhal : https://github.com/mayanksinghal
	var gh = { 1: "१", 2: "२", 3: "३", 4: "४", 5: "५", 6: "६", 7: "७", 8: "८", 9: "९", 0: "०" }, hh = { "१": "1", "२": "2", "३": "3", "४": "4", "५": "5", "६": "6", "७": "7", "८": "8", "९": "9", "०": "0" }; a.defineLocale("hi", { months: "जनवरी_फ़रवरी_मार्च_अप्रैल_मई_जून_जुलाई_अगस्त_सितम्बर_अक्टूबर_नवम्बर_दिसम्बर".split("_"), monthsShort: "जन._फ़र._मार्च_अप्रै._मई_जून_जुल._अग._सित._अक्टू._नव._दिस.".split("_"), monthsParseExact: !0, weekdays: "रविवार_सोमवार_मंगलवार_बुधवार_गुरूवार_शुक्रवार_शनिवार".split("_"), weekdaysShort: "रवि_सोम_मंगल_बुध_गुरू_शुक्र_शनि".split("_"), weekdaysMin: "र_सो_मं_बु_गु_शु_श".split("_"), longDateFormat: { LT: "A h:mm बजे", LTS: "A h:mm:ss बजे", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm बजे", LLLL: "dddd, D MMMM YYYY, A h:mm बजे" }, calendar: { sameDay: "[आज] LT", nextDay: "[कल] LT", nextWeek: "dddd, LT", lastDay: "[कल] LT", lastWeek: "[पिछले] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s में", past: "%s पहले", s: "कुछ ही क्षण", m: "एक मिनट", mm: "%d मिनट", h: "एक घंटा", hh: "%d घंटे", d: "एक दिन", dd: "%d दिन", M: "एक महीने", MM: "%d महीने", y: "एक वर्ष", yy: "%d वर्ष" }, preparse: function (a) { return a.replace(/[१२३४५६७८९०]/g, function (a) { return hh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return gh[a] }) }, meridiemParse: /रात|सुबह|दोपहर|शाम/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "रात" === b ? a < 4 ? a : a + 12 : "सुबह" === b ? a : "दोपहर" === b ? a >= 10 ? a : a + 12 : "शाम" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "रात" : a < 10 ? "सुबह" : a < 17 ? "दोपहर" : a < 20 ? "शाम" : "रात" }, week: { dow: 0, doy: 6 } }), a.defineLocale("hr", { months: { format: "siječnja_veljače_ožujka_travnja_svibnja_lipnja_srpnja_kolovoza_rujna_listopada_studenoga_prosinca".split("_"), standalone: "siječanj_veljača_ožujak_travanj_svibanj_lipanj_srpanj_kolovoz_rujan_listopad_studeni_prosinac".split("_") }, monthsShort: "sij._velj._ožu._tra._svi._lip._srp._kol._ruj._lis._stu._pro.".split("_"), monthsParseExact: !0, weekdays: "nedjelja_ponedjeljak_utorak_srijeda_četvrtak_petak_subota".split("_"), weekdaysShort: "ned._pon._uto._sri._čet._pet._sub.".split("_"), weekdaysMin: "ne_po_ut_sr_če_pe_su".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[danas u] LT", nextDay: "[sutra u] LT", nextWeek: function () { switch (this.day()) { case 0: return "[u] [nedjelju] [u] LT"; case 3: return "[u] [srijedu] [u] LT"; case 6: return "[u] [subotu] [u] LT"; case 1: case 2: case 4: case 5: return "[u] dddd [u] LT" } }, lastDay: "[jučer u] LT", lastWeek: function () { switch (this.day()) { case 0: case 3: return "[prošlu] dddd [u] LT"; case 6: return "[prošle] [subote] [u] LT"; case 1: case 2: case 4: case 5: return "[prošli] dddd [u] LT" } }, sameElse: "L" }, relativeTime: { future: "za %s", past: "prije %s", s: "par sekundi", m: Jd, mm: Jd, h: Jd, hh: Jd, d: "dan", dd: Jd, M: "mjesec", MM: Jd, y: "godinu", yy: Jd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Hungarian [hu]
	//! author : Adam Brunner : https://github.com/adambrunner
	var ih = "vasárnap hétfőn kedden szerdán csütörtökön pénteken szombaton".split(" "); a.defineLocale("hu", { months: "január_február_március_április_május_június_július_augusztus_szeptember_október_november_december".split("_"), monthsShort: "jan_feb_márc_ápr_máj_jún_júl_aug_szept_okt_nov_dec".split("_"), weekdays: "vasárnap_hétfő_kedd_szerda_csütörtök_péntek_szombat".split("_"), weekdaysShort: "vas_hét_kedd_sze_csüt_pén_szo".split("_"), weekdaysMin: "v_h_k_sze_cs_p_szo".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "YYYY.MM.DD.", LL: "YYYY. MMMM D.", LLL: "YYYY. MMMM D. H:mm", LLLL: "YYYY. MMMM D., dddd H:mm" }, meridiemParse: /de|du/i, isPM: function (a) { return "u" === a.charAt(1).toLowerCase() }, meridiem: function (a, b, c) { return a < 12 ? c === !0 ? "de" : "DE" : c === !0 ? "du" : "DU" }, calendar: { sameDay: "[ma] LT[-kor]", nextDay: "[holnap] LT[-kor]", nextWeek: function () { return Ld.call(this, !0) }, lastDay: "[tegnap] LT[-kor]", lastWeek: function () { return Ld.call(this, !1) }, sameElse: "L" }, relativeTime: { future: "%s múlva", past: "%s", s: Kd, m: Kd, mm: Kd, h: Kd, hh: Kd, d: Kd, dd: Kd, M: Kd, MM: Kd, y: Kd, yy: Kd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Armenian [hy-am]
		//! author : Armendarabyan : https://github.com/armendarabyan
		a.defineLocale("hy-am", { months: { format: "հունվարի_փետրվարի_մարտի_ապրիլի_մայիսի_հունիսի_հուլիսի_օգոստոսի_սեպտեմբերի_հոկտեմբերի_նոյեմբերի_դեկտեմբերի".split("_"), standalone: "հունվար_փետրվար_մարտ_ապրիլ_մայիս_հունիս_հուլիս_օգոստոս_սեպտեմբեր_հոկտեմբեր_նոյեմբեր_դեկտեմբեր".split("_") }, monthsShort: "հնվ_փտր_մրտ_ապր_մյս_հնս_հլս_օգս_սպտ_հկտ_նմբ_դկտ".split("_"), weekdays: "կիրակի_երկուշաբթի_երեքշաբթի_չորեքշաբթի_հինգշաբթի_ուրբաթ_շաբաթ".split("_"), weekdaysShort: "կրկ_երկ_երք_չրք_հնգ_ուրբ_շբթ".split("_"), weekdaysMin: "կրկ_երկ_երք_չրք_հնգ_ուրբ_շբթ".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY թ.", LLL: "D MMMM YYYY թ., HH:mm", LLLL: "dddd, D MMMM YYYY թ., HH:mm" }, calendar: { sameDay: "[այսօր] LT", nextDay: "[վաղը] LT", lastDay: "[երեկ] LT", nextWeek: function () { return "dddd [օրը ժամը] LT" }, lastWeek: function () { return "[անցած] dddd [օրը ժամը] LT" }, sameElse: "L" }, relativeTime: { future: "%s հետո", past: "%s առաջ", s: "մի քանի վայրկյան", m: "րոպե", mm: "%d րոպե", h: "ժամ", hh: "%d ժամ", d: "օր", dd: "%d օր", M: "ամիս", MM: "%d ամիս", y: "տարի", yy: "%d տարի" }, meridiemParse: /գիշերվա|առավոտվա|ցերեկվա|երեկոյան/, isPM: function (a) { return /^(ցերեկվա|երեկոյան)$/.test(a) }, meridiem: function (a) { return a < 4 ? "գիշերվա" : a < 12 ? "առավոտվա" : a < 17 ? "ցերեկվա" : "երեկոյան" }, dayOfMonthOrdinalParse: /\d{1,2}|\d{1,2}-(ին|րդ)/, ordinal: function (a, b) { switch (b) { case "DDD": case "w": case "W": case "DDDo": return 1 === a ? a + "-ին" : a + "-րդ"; default: return a } }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Indonesian [id]
		//! author : Mohammad Satrio Utomo : https://github.com/tyok
		//! reference: http://id.wikisource.org/wiki/Pedoman_Umum_Ejaan_Bahasa_Indonesia_yang_Disempurnakan
		a.defineLocale("id", { months: "Januari_Februari_Maret_April_Mei_Juni_Juli_Agustus_September_Oktober_November_Desember".split("_"), monthsShort: "Jan_Feb_Mar_Apr_Mei_Jun_Jul_Ags_Sep_Okt_Nov_Des".split("_"), weekdays: "Minggu_Senin_Selasa_Rabu_Kamis_Jumat_Sabtu".split("_"), weekdaysShort: "Min_Sen_Sel_Rab_Kam_Jum_Sab".split("_"), weekdaysMin: "Mg_Sn_Sl_Rb_Km_Jm_Sb".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [pukul] HH.mm", LLLL: "dddd, D MMMM YYYY [pukul] HH.mm" }, meridiemParse: /pagi|siang|sore|malam/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "pagi" === b ? a : "siang" === b ? a >= 11 ? a : a + 12 : "sore" === b || "malam" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 11 ? "pagi" : a < 15 ? "siang" : a < 19 ? "sore" : "malam" }, calendar: { sameDay: "[Hari ini pukul] LT", nextDay: "[Besok pukul] LT", nextWeek: "dddd [pukul] LT", lastDay: "[Kemarin pukul] LT", lastWeek: "dddd [lalu pukul] LT", sameElse: "L" }, relativeTime: { future: "dalam %s", past: "%s yang lalu", s: "beberapa detik", m: "semenit", mm: "%d menit", h: "sejam", hh: "%d jam", d: "sehari", dd: "%d hari", M: "sebulan", MM: "%d bulan", y: "setahun", yy: "%d tahun" }, week: { dow: 1, doy: 7 } }), a.defineLocale("is", { months: "janúar_febrúar_mars_apríl_maí_júní_júlí_ágúst_september_október_nóvember_desember".split("_"), monthsShort: "jan_feb_mar_apr_maí_jún_júl_ágú_sep_okt_nóv_des".split("_"), weekdays: "sunnudagur_mánudagur_þriðjudagur_miðvikudagur_fimmtudagur_föstudagur_laugardagur".split("_"), weekdaysShort: "sun_mán_þri_mið_fim_fös_lau".split("_"), weekdaysMin: "Su_Má_Þr_Mi_Fi_Fö_La".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY [kl.] H:mm", LLLL: "dddd, D. MMMM YYYY [kl.] H:mm" }, calendar: { sameDay: "[í dag kl.] LT", nextDay: "[á morgun kl.] LT", nextWeek: "dddd [kl.] LT", lastDay: "[í gær kl.] LT", lastWeek: "[síðasta] dddd [kl.] LT", sameElse: "L" }, relativeTime: { future: "eftir %s", past: "fyrir %s síðan", s: Nd, m: Nd, mm: Nd, h: "klukkustund", hh: Nd, d: Nd, dd: Nd, M: Nd, MM: Nd, y: Nd, yy: Nd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Italian [it]
		//! author : Lorenzo : https://github.com/aliem
		//! author: Mattia Larentis: https://github.com/nostalgiaz
		a.defineLocale("it", { months: "gennaio_febbraio_marzo_aprile_maggio_giugno_luglio_agosto_settembre_ottobre_novembre_dicembre".split("_"), monthsShort: "gen_feb_mar_apr_mag_giu_lug_ago_set_ott_nov_dic".split("_"), weekdays: "domenica_lunedì_martedì_mercoledì_giovedì_venerdì_sabato".split("_"), weekdaysShort: "dom_lun_mar_mer_gio_ven_sab".split("_"), weekdaysMin: "do_lu_ma_me_gi_ve_sa".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Oggi alle] LT", nextDay: "[Domani alle] LT", nextWeek: "dddd [alle] LT", lastDay: "[Ieri alle] LT", lastWeek: function () { switch (this.day()) { case 0: return "[la scorsa] dddd [alle] LT"; default: return "[lo scorso] dddd [alle] LT" } }, sameElse: "L" }, relativeTime: { future: function (a) { return (/^[0-9].+$/.test(a) ? "tra" : "in") + " " + a }, past: "%s fa", s: "alcuni secondi", m: "un minuto", mm: "%d minuti", h: "un'ora", hh: "%d ore", d: "un giorno", dd: "%d giorni", M: "un mese", MM: "%d mesi", y: "un anno", yy: "%d anni" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Japanese [ja]
		//! author : LI Long : https://github.com/baryon
		a.defineLocale("ja", { months: "1月_2月_3月_4月_5月_6月_7月_8月_9月_10月_11月_12月".split("_"), monthsShort: "1月_2月_3月_4月_5月_6月_7月_8月_9月_10月_11月_12月".split("_"), weekdays: "日曜日_月曜日_火曜日_水曜日_木曜日_金曜日_土曜日".split("_"), weekdaysShort: "日_月_火_水_木_金_土".split("_"), weekdaysMin: "日_月_火_水_木_金_土".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY/MM/DD", LL: "YYYY年M月D日", LLL: "YYYY年M月D日 HH:mm", LLLL: "YYYY年M月D日 HH:mm dddd", l: "YYYY/MM/DD", ll: "YYYY年M月D日", lll: "YYYY年M月D日 HH:mm", llll: "YYYY年M月D日 HH:mm dddd" }, meridiemParse: /午前|午後/i, isPM: function (a) { return "午後" === a }, meridiem: function (a, b, c) { return a < 12 ? "午前" : "午後" }, calendar: { sameDay: "[今日] LT", nextDay: "[明日] LT", nextWeek: "[来週]dddd LT", lastDay: "[昨日] LT", lastWeek: "[前週]dddd LT", sameElse: "L" }, dayOfMonthOrdinalParse: /\d{1,2}日/, ordinal: function (a, b) { switch (b) { case "d": case "D": case "DDD": return a + "日"; default: return a } }, relativeTime: { future: "%s後", past: "%s前", s: "数秒", m: "1分", mm: "%d分", h: "1時間", hh: "%d時間", d: "1日", dd: "%d日", M: "1ヶ月", MM: "%dヶ月", y: "1年", yy: "%d年" } }),
		//! moment.js locale configuration
		//! locale : Javanese [jv]
		//! author : Rony Lantip : https://github.com/lantip
		//! reference: http://jv.wikipedia.org/wiki/Basa_Jawa
		a.defineLocale("jv", { months: "Januari_Februari_Maret_April_Mei_Juni_Juli_Agustus_September_Oktober_Nopember_Desember".split("_"), monthsShort: "Jan_Feb_Mar_Apr_Mei_Jun_Jul_Ags_Sep_Okt_Nop_Des".split("_"), weekdays: "Minggu_Senen_Seloso_Rebu_Kemis_Jemuwah_Septu".split("_"), weekdaysShort: "Min_Sen_Sel_Reb_Kem_Jem_Sep".split("_"), weekdaysMin: "Mg_Sn_Sl_Rb_Km_Jm_Sp".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [pukul] HH.mm", LLLL: "dddd, D MMMM YYYY [pukul] HH.mm" }, meridiemParse: /enjing|siyang|sonten|ndalu/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "enjing" === b ? a : "siyang" === b ? a >= 11 ? a : a + 12 : "sonten" === b || "ndalu" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 11 ? "enjing" : a < 15 ? "siyang" : a < 19 ? "sonten" : "ndalu" }, calendar: { sameDay: "[Dinten puniko pukul] LT", nextDay: "[Mbenjang pukul] LT", nextWeek: "dddd [pukul] LT", lastDay: "[Kala wingi pukul] LT", lastWeek: "dddd [kepengker pukul] LT", sameElse: "L" }, relativeTime: { future: "wonten ing %s", past: "%s ingkang kepengker", s: "sawetawis detik", m: "setunggal menit", mm: "%d menit", h: "setunggal jam", hh: "%d jam", d: "sedinten", dd: "%d dinten", M: "sewulan", MM: "%d wulan", y: "setaun", yy: "%d taun" }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Georgian [ka]
		//! author : Irakli Janiashvili : https://github.com/irakli-janiashvili
		a.defineLocale("ka", { months: { standalone: "იანვარი_თებერვალი_მარტი_აპრილი_მაისი_ივნისი_ივლისი_აგვისტო_სექტემბერი_ოქტომბერი_ნოემბერი_დეკემბერი".split("_"), format: "იანვარს_თებერვალს_მარტს_აპრილის_მაისს_ივნისს_ივლისს_აგვისტს_სექტემბერს_ოქტომბერს_ნოემბერს_დეკემბერს".split("_") }, monthsShort: "იან_თებ_მარ_აპრ_მაი_ივნ_ივლ_აგვ_სექ_ოქტ_ნოე_დეკ".split("_"), weekdays: { standalone: "კვირა_ორშაბათი_სამშაბათი_ოთხშაბათი_ხუთშაბათი_პარასკევი_შაბათი".split("_"), format: "კვირას_ორშაბათს_სამშაბათს_ოთხშაბათს_ხუთშაბათს_პარასკევს_შაბათს".split("_"), isFormat: /(წინა|შემდეგ)/ }, weekdaysShort: "კვი_ორშ_სამ_ოთხ_ხუთ_პარ_შაბ".split("_"), weekdaysMin: "კვ_ორ_სა_ოთ_ხუ_პა_შა".split("_"), longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendar: { sameDay: "[დღეს] LT[-ზე]", nextDay: "[ხვალ] LT[-ზე]", lastDay: "[გუშინ] LT[-ზე]", nextWeek: "[შემდეგ] dddd LT[-ზე]", lastWeek: "[წინა] dddd LT-ზე", sameElse: "L" }, relativeTime: { future: function (a) { return /(წამი|წუთი|საათი|წელი)/.test(a) ? a.replace(/ი$/, "ში") : a + "ში" }, past: function (a) { return /(წამი|წუთი|საათი|დღე|თვე)/.test(a) ? a.replace(/(ი|ე)$/, "ის უკან") : /წელი/.test(a) ? a.replace(/წელი$/, "წლის უკან") : void 0 }, s: "რამდენიმე წამი", m: "წუთი", mm: "%d წუთი", h: "საათი", hh: "%d საათი", d: "დღე", dd: "%d დღე", M: "თვე", MM: "%d თვე", y: "წელი", yy: "%d წელი" }, dayOfMonthOrdinalParse: /0|1-ლი|მე-\d{1,2}|\d{1,2}-ე/, ordinal: function (a) { return 0 === a ? a : 1 === a ? a + "-ლი" : a < 20 || a <= 100 && a % 20 === 0 || a % 100 === 0 ? "მე-" + a : a + "-ე" }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Kazakh [kk]
	//! authors : Nurlan Rakhimzhanov : https://github.com/nurlan
	var jh = { 0: "-ші", 1: "-ші", 2: "-ші", 3: "-ші", 4: "-ші", 5: "-ші", 6: "-шы", 7: "-ші", 8: "-ші", 9: "-шы", 10: "-шы", 20: "-шы", 30: "-шы", 40: "-шы", 50: "-ші", 60: "-шы", 70: "-ші", 80: "-ші", 90: "-шы", 100: "-ші" }; a.defineLocale("kk", { months: "қаңтар_ақпан_наурыз_сәуір_мамыр_маусым_шілде_тамыз_қыркүйек_қазан_қараша_желтоқсан".split("_"), monthsShort: "қаң_ақп_нау_сәу_мам_мау_шіл_там_қыр_қаз_қар_жел".split("_"), weekdays: "жексенбі_дүйсенбі_сейсенбі_сәрсенбі_бейсенбі_жұма_сенбі".split("_"), weekdaysShort: "жек_дүй_сей_сәр_бей_жұм_сен".split("_"), weekdaysMin: "жк_дй_сй_ср_бй_жм_сн".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Бүгін сағат] LT", nextDay: "[Ертең сағат] LT", nextWeek: "dddd [сағат] LT", lastDay: "[Кеше сағат] LT", lastWeek: "[Өткен аптаның] dddd [сағат] LT", sameElse: "L" }, relativeTime: { future: "%s ішінде", past: "%s бұрын", s: "бірнеше секунд", m: "бір минут", mm: "%d минут", h: "бір сағат", hh: "%d сағат", d: "бір күн", dd: "%d күн", M: "бір ай", MM: "%d ай", y: "бір жыл", yy: "%d жыл" }, dayOfMonthOrdinalParse: /\d{1,2}-(ші|шы)/, ordinal: function (a) { var b = a % 10, c = a >= 100 ? 100 : null; return a + (jh[a] || jh[b] || jh[c]) }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Cambodian [km]
		//! author : Kruy Vanna : https://github.com/kruyvanna
		a.defineLocale("km", { months: "មករា_កុម្ភៈ_មីនា_មេសា_ឧសភា_មិថុនា_កក្កដា_សីហា_កញ្ញា_តុលា_វិច្ឆិកា_ធ្នូ".split("_"), monthsShort: "មករា_កុម្ភៈ_មីនា_មេសា_ឧសភា_មិថុនា_កក្កដា_សីហា_កញ្ញា_តុលា_វិច្ឆិកា_ធ្នូ".split("_"), weekdays: "អាទិត្យ_ច័ន្ទ_អង្គារ_ពុធ_ព្រហស្បតិ៍_សុក្រ_សៅរ៍".split("_"), weekdaysShort: "អាទិត្យ_ច័ន្ទ_អង្គារ_ពុធ_ព្រហស្បតិ៍_សុក្រ_សៅរ៍".split("_"), weekdaysMin: "អាទិត្យ_ច័ន្ទ_អង្គារ_ពុធ_ព្រហស្បតិ៍_សុក្រ_សៅរ៍".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[ថ្ងៃនេះ ម៉ោង] LT", nextDay: "[ស្អែក ម៉ោង] LT", nextWeek: "dddd [ម៉ោង] LT", lastDay: "[ម្សិលមិញ ម៉ោង] LT", lastWeek: "dddd [សប្តាហ៍មុន] [ម៉ោង] LT", sameElse: "L" }, relativeTime: { future: "%sទៀត", past: "%sមុន", s: "ប៉ុន្មានវិនាទី", m: "មួយនាទី", mm: "%d នាទី", h: "មួយម៉ោង", hh: "%d ម៉ោង", d: "មួយថ្ងៃ", dd: "%d ថ្ងៃ", M: "មួយខែ", MM: "%d ខែ", y: "មួយឆ្នាំ", yy: "%d ឆ្នាំ" }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Kannada [kn]
	//! author : Rajeev Naik : https://github.com/rajeevnaikte
	var kh = { 1: "೧", 2: "೨", 3: "೩", 4: "೪", 5: "೫", 6: "೬", 7: "೭", 8: "೮", 9: "೯", 0: "೦" }, lh = { "೧": "1", "೨": "2", "೩": "3", "೪": "4", "೫": "5", "೬": "6", "೭": "7", "೮": "8", "೯": "9", "೦": "0" }; a.defineLocale("kn", { months: "ಜನವರಿ_ಫೆಬ್ರವರಿ_ಮಾರ್ಚ್_ಏಪ್ರಿಲ್_ಮೇ_ಜೂನ್_ಜುಲೈ_ಆಗಸ್ಟ್_ಸೆಪ್ಟೆಂಬರ್_ಅಕ್ಟೋಬರ್_ನವೆಂಬರ್_ಡಿಸೆಂಬರ್".split("_"), monthsShort: "ಜನ_ಫೆಬ್ರ_ಮಾರ್ಚ್_ಏಪ್ರಿಲ್_ಮೇ_ಜೂನ್_ಜುಲೈ_ಆಗಸ್ಟ್_ಸೆಪ್ಟೆಂಬ_ಅಕ್ಟೋಬ_ನವೆಂಬ_ಡಿಸೆಂಬ".split("_"), monthsParseExact: !0, weekdays: "ಭಾನುವಾರ_ಸೋಮವಾರ_ಮಂಗಳವಾರ_ಬುಧವಾರ_ಗುರುವಾರ_ಶುಕ್ರವಾರ_ಶನಿವಾರ".split("_"), weekdaysShort: "ಭಾನು_ಸೋಮ_ಮಂಗಳ_ಬುಧ_ಗುರು_ಶುಕ್ರ_ಶನಿ".split("_"), weekdaysMin: "ಭಾ_ಸೋ_ಮಂ_ಬು_ಗು_ಶು_ಶ".split("_"), longDateFormat: { LT: "A h:mm", LTS: "A h:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm", LLLL: "dddd, D MMMM YYYY, A h:mm" }, calendar: { sameDay: "[ಇಂದು] LT", nextDay: "[ನಾಳೆ] LT", nextWeek: "dddd, LT", lastDay: "[ನಿನ್ನೆ] LT", lastWeek: "[ಕೊನೆಯ] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s ನಂತರ", past: "%s ಹಿಂದೆ", s: "ಕೆಲವು ಕ್ಷಣಗಳು", m: "ಒಂದು ನಿಮಿಷ", mm: "%d ನಿಮಿಷ", h: "ಒಂದು ಗಂಟೆ", hh: "%d ಗಂಟೆ", d: "ಒಂದು ದಿನ", dd: "%d ದಿನ", M: "ಒಂದು ತಿಂಗಳು", MM: "%d ತಿಂಗಳು", y: "ಒಂದು ವರ್ಷ", yy: "%d ವರ್ಷ" }, preparse: function (a) { return a.replace(/[೧೨೩೪೫೬೭೮೯೦]/g, function (a) { return lh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return kh[a] }) }, meridiemParse: /ರಾತ್ರಿ|ಬೆಳಿಗ್ಗೆ|ಮಧ್ಯಾಹ್ನ|ಸಂಜೆ/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "ರಾತ್ರಿ" === b ? a < 4 ? a : a + 12 : "ಬೆಳಿಗ್ಗೆ" === b ? a : "ಮಧ್ಯಾಹ್ನ" === b ? a >= 10 ? a : a + 12 : "ಸಂಜೆ" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "ರಾತ್ರಿ" : a < 10 ? "ಬೆಳಿಗ್ಗೆ" : a < 17 ? "ಮಧ್ಯಾಹ್ನ" : a < 20 ? "ಸಂಜೆ" : "ರಾತ್ರಿ" }, dayOfMonthOrdinalParse: /\d{1,2}(ನೇ)/, ordinal: function (a) { return a + "ನೇ" }, week: { dow: 0, doy: 6 } }),
		//! moment.js locale configuration
		//! locale : Korean [ko]
		//! author : Kyungwook, Park : https://github.com/kyungw00k
		//! author : Jeeeyul Lee <jeeeyul@gmail.com>
		a.defineLocale("ko", { months: "1월_2월_3월_4월_5월_6월_7월_8월_9월_10월_11월_12월".split("_"), monthsShort: "1월_2월_3월_4월_5월_6월_7월_8월_9월_10월_11월_12월".split("_"), weekdays: "일요일_월요일_화요일_수요일_목요일_금요일_토요일".split("_"), weekdaysShort: "일_월_화_수_목_금_토".split("_"), weekdaysMin: "일_월_화_수_목_금_토".split("_"), longDateFormat: { LT: "A h:mm", LTS: "A h:mm:ss", L: "YYYY.MM.DD", LL: "YYYY년 MMMM D일", LLL: "YYYY년 MMMM D일 A h:mm", LLLL: "YYYY년 MMMM D일 dddd A h:mm", l: "YYYY.MM.DD", ll: "YYYY년 MMMM D일", lll: "YYYY년 MMMM D일 A h:mm", llll: "YYYY년 MMMM D일 dddd A h:mm" }, calendar: { sameDay: "오늘 LT", nextDay: "내일 LT", nextWeek: "dddd LT", lastDay: "어제 LT", lastWeek: "지난주 dddd LT", sameElse: "L" }, relativeTime: { future: "%s 후", past: "%s 전", s: "몇 초", ss: "%d초", m: "1분", mm: "%d분", h: "한 시간", hh: "%d시간", d: "하루", dd: "%d일", M: "한 달", MM: "%d달", y: "일 년", yy: "%d년" }, dayOfMonthOrdinalParse: /\d{1,2}일/, ordinal: "%d일", meridiemParse: /오전|오후/, isPM: function (a) { return "오후" === a }, meridiem: function (a, b, c) { return a < 12 ? "오전" : "오후" } });
	//! moment.js locale configuration
	//! locale : Kyrgyz [ky]
	//! author : Chyngyz Arystan uulu : https://github.com/chyngyz
	var mh = { 0: "-чү", 1: "-чи", 2: "-чи", 3: "-чү", 4: "-чү", 5: "-чи", 6: "-чы", 7: "-чи", 8: "-чи", 9: "-чу", 10: "-чу", 20: "-чы", 30: "-чу", 40: "-чы", 50: "-чү", 60: "-чы", 70: "-чи", 80: "-чи", 90: "-чу", 100: "-чү" }; a.defineLocale("ky", { months: "январь_февраль_март_апрель_май_июнь_июль_август_сентябрь_октябрь_ноябрь_декабрь".split("_"), monthsShort: "янв_фев_март_апр_май_июнь_июль_авг_сен_окт_ноя_дек".split("_"), weekdays: "Жекшемби_Дүйшөмбү_Шейшемби_Шаршемби_Бейшемби_Жума_Ишемби".split("_"), weekdaysShort: "Жек_Дүй_Шей_Шар_Бей_Жум_Ише".split("_"), weekdaysMin: "Жк_Дй_Шй_Шр_Бй_Жм_Иш".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Бүгүн саат] LT", nextDay: "[Эртең саат] LT", nextWeek: "dddd [саат] LT", lastDay: "[Кече саат] LT", lastWeek: "[Өткен аптанын] dddd [күнү] [саат] LT", sameElse: "L" }, relativeTime: { future: "%s ичинде", past: "%s мурун", s: "бирнече секунд", m: "бир мүнөт", mm: "%d мүнөт", h: "бир саат", hh: "%d саат", d: "бир күн", dd: "%d күн", M: "бир ай", MM: "%d ай", y: "бир жыл", yy: "%d жыл" }, dayOfMonthOrdinalParse: /\d{1,2}-(чи|чы|чү|чу)/, ordinal: function (a) { var b = a % 10, c = a >= 100 ? 100 : null; return a + (mh[a] || mh[b] || mh[c]) }, week: { dow: 1, doy: 7 } }), a.defineLocale("lb", { months: "Januar_Februar_Mäerz_Abrëll_Mee_Juni_Juli_August_September_Oktober_November_Dezember".split("_"), monthsShort: "Jan._Febr._Mrz._Abr._Mee_Jun._Jul._Aug._Sept._Okt._Nov._Dez.".split("_"), monthsParseExact: !0, weekdays: "Sonndeg_Méindeg_Dënschdeg_Mëttwoch_Donneschdeg_Freideg_Samschdeg".split("_"), weekdaysShort: "So._Mé._Dë._Më._Do._Fr._Sa.".split("_"), weekdaysMin: "So_Mé_Dë_Më_Do_Fr_Sa".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm [Auer]", LTS: "H:mm:ss [Auer]", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm [Auer]", LLLL: "dddd, D. MMMM YYYY H:mm [Auer]" }, calendar: { sameDay: "[Haut um] LT", sameElse: "L", nextDay: "[Muer um] LT", nextWeek: "dddd [um] LT", lastDay: "[Gëschter um] LT", lastWeek: function () { switch (this.day()) { case 2: case 4: return "[Leschten] dddd [um] LT"; default: return "[Leschte] dddd [um] LT" } } }, relativeTime: { future: Pd, past: Qd, s: "e puer Sekonnen", m: Od, mm: "%d Minutten", h: Od, hh: "%d Stonnen", d: Od, dd: "%d Deeg", M: Od, MM: "%d Méint", y: Od, yy: "%d Joer" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Lao [lo]
		//! author : Ryan Hart : https://github.com/ryanhart2
		a.defineLocale("lo", { months: "ມັງກອນ_ກຸມພາ_ມີນາ_ເມສາ_ພຶດສະພາ_ມິຖຸນາ_ກໍລະກົດ_ສິງຫາ_ກັນຍາ_ຕຸລາ_ພະຈິກ_ທັນວາ".split("_"), monthsShort: "ມັງກອນ_ກຸມພາ_ມີນາ_ເມສາ_ພຶດສະພາ_ມິຖຸນາ_ກໍລະກົດ_ສິງຫາ_ກັນຍາ_ຕຸລາ_ພະຈິກ_ທັນວາ".split("_"), weekdays: "ອາທິດ_ຈັນ_ອັງຄານ_ພຸດ_ພະຫັດ_ສຸກ_ເສົາ".split("_"), weekdaysShort: "ທິດ_ຈັນ_ອັງຄານ_ພຸດ_ພະຫັດ_ສຸກ_ເສົາ".split("_"), weekdaysMin: "ທ_ຈ_ອຄ_ພ_ພຫ_ສກ_ສ".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "ວັນdddd D MMMM YYYY HH:mm" }, meridiemParse: /ຕອນເຊົ້າ|ຕອນແລງ/, isPM: function (a) { return "ຕອນແລງ" === a }, meridiem: function (a, b, c) { return a < 12 ? "ຕອນເຊົ້າ" : "ຕອນແລງ" }, calendar: { sameDay: "[ມື້ນີ້ເວລາ] LT", nextDay: "[ມື້ອື່ນເວລາ] LT", nextWeek: "[ວັນ]dddd[ໜ້າເວລາ] LT", lastDay: "[ມື້ວານນີ້ເວລາ] LT", lastWeek: "[ວັນ]dddd[ແລ້ວນີ້ເວລາ] LT", sameElse: "L" }, relativeTime: { future: "ອີກ %s", past: "%sຜ່ານມາ", s: "ບໍ່ເທົ່າໃດວິນາທີ", m: "1 ນາທີ", mm: "%d ນາທີ", h: "1 ຊົ່ວໂມງ", hh: "%d ຊົ່ວໂມງ", d: "1 ມື້", dd: "%d ມື້", M: "1 ເດືອນ", MM: "%d ເດືອນ", y: "1 ປີ", yy: "%d ປີ" }, dayOfMonthOrdinalParse: /(ທີ່)\d{1,2}/, ordinal: function (a) { return "ທີ່" + a } });
	//! moment.js locale configuration
	//! locale : Lithuanian [lt]
	//! author : Mindaugas Mozūras : https://github.com/mmozuras
	var nh = { m: "minutė_minutės_minutę", mm: "minutės_minučių_minutes", h: "valanda_valandos_valandą", hh: "valandos_valandų_valandas", d: "diena_dienos_dieną", dd: "dienos_dienų_dienas", M: "mėnuo_mėnesio_mėnesį", MM: "mėnesiai_mėnesių_mėnesius", y: "metai_metų_metus", yy: "metai_metų_metus" }; a.defineLocale("lt", { months: { format: "sausio_vasario_kovo_balandžio_gegužės_birželio_liepos_rugpjūčio_rugsėjo_spalio_lapkričio_gruodžio".split("_"), standalone: "sausis_vasaris_kovas_balandis_gegužė_birželis_liepa_rugpjūtis_rugsėjis_spalis_lapkritis_gruodis".split("_"), isFormat: /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?|MMMM?(\[[^\[\]]*\]|\s)+D[oD]?/ }, monthsShort: "sau_vas_kov_bal_geg_bir_lie_rgp_rgs_spa_lap_grd".split("_"), weekdays: { format: "sekmadienį_pirmadienį_antradienį_trečiadienį_ketvirtadienį_penktadienį_šeštadienį".split("_"), standalone: "sekmadienis_pirmadienis_antradienis_trečiadienis_ketvirtadienis_penktadienis_šeštadienis".split("_"), isFormat: /dddd HH:mm/ }, weekdaysShort: "Sek_Pir_Ant_Tre_Ket_Pen_Šeš".split("_"), weekdaysMin: "S_P_A_T_K_Pn_Š".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY-MM-DD", LL: "YYYY [m.] MMMM D [d.]", LLL: "YYYY [m.] MMMM D [d.], HH:mm [val.]", LLLL: "YYYY [m.] MMMM D [d.], dddd, HH:mm [val.]", l: "YYYY-MM-DD", ll: "YYYY [m.] MMMM D [d.]", lll: "YYYY [m.] MMMM D [d.], HH:mm [val.]", llll: "YYYY [m.] MMMM D [d.], ddd, HH:mm [val.]" }, calendar: { sameDay: "[Šiandien] LT", nextDay: "[Rytoj] LT", nextWeek: "dddd LT", lastDay: "[Vakar] LT", lastWeek: "[Praėjusį] dddd LT", sameElse: "L" }, relativeTime: { future: "po %s", past: "prieš %s", s: Sd, m: Td, mm: Wd, h: Td, hh: Wd, d: Td, dd: Wd, M: Td, MM: Wd, y: Td, yy: Wd }, dayOfMonthOrdinalParse: /\d{1,2}-oji/, ordinal: function (a) { return a + "-oji" }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Latvian [lv]
	//! author : Kristaps Karlsons : https://github.com/skakri
	//! author : Jānis Elmeris : https://github.com/JanisE
	var oh = { m: "minūtes_minūtēm_minūte_minūtes".split("_"), mm: "minūtes_minūtēm_minūte_minūtes".split("_"), h: "stundas_stundām_stunda_stundas".split("_"), hh: "stundas_stundām_stunda_stundas".split("_"), d: "dienas_dienām_diena_dienas".split("_"), dd: "dienas_dienām_diena_dienas".split("_"), M: "mēneša_mēnešiem_mēnesis_mēneši".split("_"), MM: "mēneša_mēnešiem_mēnesis_mēneši".split("_"), y: "gada_gadiem_gads_gadi".split("_"), yy: "gada_gadiem_gads_gadi".split("_") }; a.defineLocale("lv", { months: "janvāris_februāris_marts_aprīlis_maijs_jūnijs_jūlijs_augusts_septembris_oktobris_novembris_decembris".split("_"), monthsShort: "jan_feb_mar_apr_mai_jūn_jūl_aug_sep_okt_nov_dec".split("_"), weekdays: "svētdiena_pirmdiena_otrdiena_trešdiena_ceturtdiena_piektdiena_sestdiena".split("_"), weekdaysShort: "Sv_P_O_T_C_Pk_S".split("_"), weekdaysMin: "Sv_P_O_T_C_Pk_S".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY.", LL: "YYYY. [gada] D. MMMM", LLL: "YYYY. [gada] D. MMMM, HH:mm", LLLL: "YYYY. [gada] D. MMMM, dddd, HH:mm" }, calendar: { sameDay: "[Šodien pulksten] LT", nextDay: "[Rīt pulksten] LT", nextWeek: "dddd [pulksten] LT", lastDay: "[Vakar pulksten] LT", lastWeek: "[Pagājušā] dddd [pulksten] LT", sameElse: "L" }, relativeTime: { future: "pēc %s", past: "pirms %s", s: $d, m: Zd, mm: Yd, h: Zd, hh: Yd, d: Zd, dd: Yd, M: Zd, MM: Yd, y: Zd, yy: Yd }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Montenegrin [me]
	//! author : Miodrag Nikač <miodrag@restartit.me> : https://github.com/miodragnikac
	var ph = { words: { m: ["jedan minut", "jednog minuta"], mm: ["minut", "minuta", "minuta"], h: ["jedan sat", "jednog sata"], hh: ["sat", "sata", "sati"], dd: ["dan", "dana", "dana"], MM: ["mjesec", "mjeseca", "mjeseci"], yy: ["godina", "godine", "godina"] }, correctGrammaticalCase: function (a, b) { return 1 === a ? b[0] : a >= 2 && a <= 4 ? b[1] : b[2] }, translate: function (a, b, c) { var d = ph.words[c]; return 1 === c.length ? b ? d[0] : d[1] : a + " " + ph.correctGrammaticalCase(a, d) } }; a.defineLocale("me", { months: "januar_februar_mart_april_maj_jun_jul_avgust_septembar_oktobar_novembar_decembar".split("_"), monthsShort: "jan._feb._mar._apr._maj_jun_jul_avg._sep._okt._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "nedjelja_ponedjeljak_utorak_srijeda_četvrtak_petak_subota".split("_"), weekdaysShort: "ned._pon._uto._sri._čet._pet._sub.".split("_"), weekdaysMin: "ne_po_ut_sr_če_pe_su".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[danas u] LT", nextDay: "[sjutra u] LT", nextWeek: function () { switch (this.day()) { case 0: return "[u] [nedjelju] [u] LT"; case 3: return "[u] [srijedu] [u] LT"; case 6: return "[u] [subotu] [u] LT"; case 1: case 2: case 4: case 5: return "[u] dddd [u] LT" } }, lastDay: "[juče u] LT", lastWeek: function () { var a = ["[prošle] [nedjelje] [u] LT", "[prošlog] [ponedjeljka] [u] LT", "[prošlog] [utorka] [u] LT", "[prošle] [srijede] [u] LT", "[prošlog] [četvrtka] [u] LT", "[prošlog] [petka] [u] LT", "[prošle] [subote] [u] LT"]; return a[this.day()] }, sameElse: "L" }, relativeTime: { future: "za %s", past: "prije %s", s: "nekoliko sekundi", m: ph.translate, mm: ph.translate, h: ph.translate, hh: ph.translate, d: "dan", dd: ph.translate, M: "mjesec", MM: ph.translate, y: "godinu", yy: ph.translate }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Maori [mi]
		//! author : John Corrigan <robbiecloset@gmail.com> : https://github.com/johnideal
		a.defineLocale("mi", { months: "Kohi-tāte_Hui-tanguru_Poutū-te-rangi_Paenga-whāwhā_Haratua_Pipiri_Hōngoingoi_Here-turi-kōkā_Mahuru_Whiringa-ā-nuku_Whiringa-ā-rangi_Hakihea".split("_"), monthsShort: "Kohi_Hui_Pou_Pae_Hara_Pipi_Hōngoi_Here_Mahu_Whi-nu_Whi-ra_Haki".split("_"), monthsRegex: /(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i, monthsStrictRegex: /(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i, monthsShortRegex: /(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i, monthsShortStrictRegex: /(?:['a-z\u0101\u014D\u016B]+\-?){1,2}/i, weekdays: "Rātapu_Mane_Tūrei_Wenerei_Tāite_Paraire_Hātarei".split("_"), weekdaysShort: "Ta_Ma_Tū_We_Tāi_Pa_Hā".split("_"), weekdaysMin: "Ta_Ma_Tū_We_Tāi_Pa_Hā".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [i] HH:mm", LLLL: "dddd, D MMMM YYYY [i] HH:mm" }, calendar: { sameDay: "[i teie mahana, i] LT", nextDay: "[apopo i] LT", nextWeek: "dddd [i] LT", lastDay: "[inanahi i] LT", lastWeek: "dddd [whakamutunga i] LT", sameElse: "L" }, relativeTime: { future: "i roto i %s", past: "%s i mua", s: "te hēkona ruarua", m: "he meneti", mm: "%d meneti", h: "te haora", hh: "%d haora", d: "he ra", dd: "%d ra", M: "he marama", MM: "%d marama", y: "he tau", yy: "%d tau" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Macedonian [mk]
		//! author : Borislav Mickov : https://github.com/B0k0
		a.defineLocale("mk", { months: "јануари_февруари_март_април_мај_јуни_јули_август_септември_октомври_ноември_декември".split("_"), monthsShort: "јан_фев_мар_апр_мај_јун_јул_авг_сеп_окт_ное_дек".split("_"), weekdays: "недела_понеделник_вторник_среда_четврток_петок_сабота".split("_"), weekdaysShort: "нед_пон_вто_сре_чет_пет_саб".split("_"), weekdaysMin: "нe_пo_вт_ср_че_пе_сa".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "D.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY H:mm", LLLL: "dddd, D MMMM YYYY H:mm" }, calendar: { sameDay: "[Денес во] LT", nextDay: "[Утре во] LT", nextWeek: "[Во] dddd [во] LT", lastDay: "[Вчера во] LT", lastWeek: function () { switch (this.day()) { case 0: case 3: case 6: return "[Изминатата] dddd [во] LT"; case 1: case 2: case 4: case 5: return "[Изминатиот] dddd [во] LT" } }, sameElse: "L" }, relativeTime: { future: "после %s", past: "пред %s", s: "неколку секунди", m: "минута", mm: "%d минути", h: "час", hh: "%d часа", d: "ден", dd: "%d дена", M: "месец", MM: "%d месеци", y: "година", yy: "%d години" }, dayOfMonthOrdinalParse: /\d{1,2}-(ев|ен|ти|ви|ри|ми)/, ordinal: function (a) { var b = a % 10, c = a % 100; return 0 === a ? a + "-ев" : 0 === c ? a + "-ен" : c > 10 && c < 20 ? a + "-ти" : 1 === b ? a + "-ви" : 2 === b ? a + "-ри" : 7 === b || 8 === b ? a + "-ми" : a + "-ти" }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Malayalam [ml]
		//! author : Floyd Pink : https://github.com/floydpink
		a.defineLocale("ml", { months: "ജനുവരി_ഫെബ്രുവരി_മാർച്ച്_ഏപ്രിൽ_മേയ്_ജൂൺ_ജൂലൈ_ഓഗസ്റ്റ്_സെപ്റ്റംബർ_ഒക്ടോബർ_നവംബർ_ഡിസംബർ".split("_"), monthsShort: "ജനു._ഫെബ്രു._മാർ._ഏപ്രി._മേയ്_ജൂൺ_ജൂലൈ._ഓഗ._സെപ്റ്റ._ഒക്ടോ._നവം._ഡിസം.".split("_"), monthsParseExact: !0, weekdays: "ഞായറാഴ്ച_തിങ്കളാഴ്ച_ചൊവ്വാഴ്ച_ബുധനാഴ്ച_വ്യാഴാഴ്ച_വെള്ളിയാഴ്ച_ശനിയാഴ്ച".split("_"), weekdaysShort: "ഞായർ_തിങ്കൾ_ചൊവ്വ_ബുധൻ_വ്യാഴം_വെള്ളി_ശനി".split("_"), weekdaysMin: "ഞാ_തി_ചൊ_ബു_വ്യാ_വെ_ശ".split("_"), longDateFormat: { LT: "A h:mm -നു", LTS: "A h:mm:ss -നു", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm -നു", LLLL: "dddd, D MMMM YYYY, A h:mm -നു" }, calendar: { sameDay: "[ഇന്ന്] LT", nextDay: "[നാളെ] LT", nextWeek: "dddd, LT", lastDay: "[ഇന്നലെ] LT", lastWeek: "[കഴിഞ്ഞ] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s കഴിഞ്ഞ്", past: "%s മുൻപ്", s: "അൽപ നിമിഷങ്ങൾ", m: "ഒരു മിനിറ്റ്", mm: "%d മിനിറ്റ്", h: "ഒരു മണിക്കൂർ", hh: "%d മണിക്കൂർ", d: "ഒരു ദിവസം", dd: "%d ദിവസം", M: "ഒരു മാസം", MM: "%d മാസം", y: "ഒരു വർഷം", yy: "%d വർഷം" }, meridiemParse: /രാത്രി|രാവിലെ|ഉച്ച കഴിഞ്ഞ്|വൈകുന്നേരം|രാത്രി/i, meridiemHour: function (a, b) { return 12 === a && (a = 0), "രാത്രി" === b && a >= 4 || "ഉച്ച കഴിഞ്ഞ്" === b || "വൈകുന്നേരം" === b ? a + 12 : a }, meridiem: function (a, b, c) { return a < 4 ? "രാത്രി" : a < 12 ? "രാവിലെ" : a < 17 ? "ഉച്ച കഴിഞ്ഞ്" : a < 20 ? "വൈകുന്നേരം" : "രാത്രി" } });
	//! moment.js locale configuration
	//! locale : Marathi [mr]
	//! author : Harshad Kale : https://github.com/kalehv
	//! author : Vivek Athalye : https://github.com/vnathalye
	var qh = { 1: "१", 2: "२", 3: "३", 4: "४", 5: "५", 6: "६", 7: "७", 8: "८", 9: "९", 0: "०" }, rh = { "१": "1", "२": "2", "३": "3", "४": "4", "५": "5", "६": "6", "७": "7", "८": "8", "९": "9", "०": "0" }; a.defineLocale("mr", { months: "जानेवारी_फेब्रुवारी_मार्च_एप्रिल_मे_जून_जुलै_ऑगस्ट_सप्टेंबर_ऑक्टोबर_नोव्हेंबर_डिसेंबर".split("_"), monthsShort: "जाने._फेब्रु._मार्च._एप्रि._मे._जून._जुलै._ऑग._सप्टें._ऑक्टो._नोव्हें._डिसें.".split("_"), monthsParseExact: !0, weekdays: "रविवार_सोमवार_मंगळवार_बुधवार_गुरूवार_शुक्रवार_शनिवार".split("_"), weekdaysShort: "रवि_सोम_मंगळ_बुध_गुरू_शुक्र_शनि".split("_"), weekdaysMin: "र_सो_मं_बु_गु_शु_श".split("_"), longDateFormat: { LT: "A h:mm वाजता", LTS: "A h:mm:ss वाजता", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm वाजता", LLLL: "dddd, D MMMM YYYY, A h:mm वाजता" }, calendar: { sameDay: "[आज] LT", nextDay: "[उद्या] LT", nextWeek: "dddd, LT", lastDay: "[काल] LT", lastWeek: "[मागील] dddd, LT", sameElse: "L" }, relativeTime: { future: "%sमध्ये", past: "%sपूर्वी", s: _d, m: _d, mm: _d, h: _d, hh: _d, d: _d, dd: _d, M: _d, MM: _d, y: _d, yy: _d }, preparse: function (a) { return a.replace(/[१२३४५६७८९०]/g, function (a) { return rh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return qh[a] }) }, meridiemParse: /रात्री|सकाळी|दुपारी|सायंकाळी/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "रात्री" === b ? a < 4 ? a : a + 12 : "सकाळी" === b ? a : "दुपारी" === b ? a >= 10 ? a : a + 12 : "सायंकाळी" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "रात्री" : a < 10 ? "सकाळी" : a < 17 ? "दुपारी" : a < 20 ? "सायंकाळी" : "रात्री" }, week: { dow: 0, doy: 6 } }),
		//! moment.js locale configuration
		//! locale : Malay [ms-my]
		//! note : DEPRECATED, the correct one is [ms]
		//! author : Weldan Jamili : https://github.com/weldan
		a.defineLocale("ms-my", { months: "Januari_Februari_Mac_April_Mei_Jun_Julai_Ogos_September_Oktober_November_Disember".split("_"), monthsShort: "Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ogs_Sep_Okt_Nov_Dis".split("_"), weekdays: "Ahad_Isnin_Selasa_Rabu_Khamis_Jumaat_Sabtu".split("_"), weekdaysShort: "Ahd_Isn_Sel_Rab_Kha_Jum_Sab".split("_"), weekdaysMin: "Ah_Is_Sl_Rb_Km_Jm_Sb".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [pukul] HH.mm", LLLL: "dddd, D MMMM YYYY [pukul] HH.mm" }, meridiemParse: /pagi|tengahari|petang|malam/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "pagi" === b ? a : "tengahari" === b ? a >= 11 ? a : a + 12 : "petang" === b || "malam" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 11 ? "pagi" : a < 15 ? "tengahari" : a < 19 ? "petang" : "malam" }, calendar: { sameDay: "[Hari ini pukul] LT", nextDay: "[Esok pukul] LT", nextWeek: "dddd [pukul] LT", lastDay: "[Kelmarin pukul] LT", lastWeek: "dddd [lepas pukul] LT", sameElse: "L" }, relativeTime: { future: "dalam %s", past: "%s yang lepas", s: "beberapa saat", m: "seminit", mm: "%d minit", h: "sejam", hh: "%d jam", d: "sehari", dd: "%d hari", M: "sebulan", MM: "%d bulan", y: "setahun", yy: "%d tahun" }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Malay [ms]
		//! author : Weldan Jamili : https://github.com/weldan
		a.defineLocale("ms", { months: "Januari_Februari_Mac_April_Mei_Jun_Julai_Ogos_September_Oktober_November_Disember".split("_"), monthsShort: "Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ogs_Sep_Okt_Nov_Dis".split("_"), weekdays: "Ahad_Isnin_Selasa_Rabu_Khamis_Jumaat_Sabtu".split("_"), weekdaysShort: "Ahd_Isn_Sel_Rab_Kha_Jum_Sab".split("_"), weekdaysMin: "Ah_Is_Sl_Rb_Km_Jm_Sb".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [pukul] HH.mm", LLLL: "dddd, D MMMM YYYY [pukul] HH.mm" }, meridiemParse: /pagi|tengahari|petang|malam/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "pagi" === b ? a : "tengahari" === b ? a >= 11 ? a : a + 12 : "petang" === b || "malam" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 11 ? "pagi" : a < 15 ? "tengahari" : a < 19 ? "petang" : "malam" }, calendar: { sameDay: "[Hari ini pukul] LT", nextDay: "[Esok pukul] LT", nextWeek: "dddd [pukul] LT", lastDay: "[Kelmarin pukul] LT", lastWeek: "dddd [lepas pukul] LT", sameElse: "L" }, relativeTime: { future: "dalam %s", past: "%s yang lepas", s: "beberapa saat", m: "seminit", mm: "%d minit", h: "sejam", hh: "%d jam", d: "sehari", dd: "%d hari", M: "sebulan", MM: "%d bulan", y: "setahun", yy: "%d tahun" }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Burmese [my]
	//! author : Squar team, mysquar.com
	//! author : David Rossellat : https://github.com/gholadr
	//! author : Tin Aung Lin : https://github.com/thanyawzinmin
	var sh = { 1: "၁", 2: "၂", 3: "၃", 4: "၄", 5: "၅", 6: "၆", 7: "၇", 8: "၈", 9: "၉", 0: "၀" }, th = { "၁": "1", "၂": "2", "၃": "3", "၄": "4", "၅": "5", "၆": "6", "၇": "7", "၈": "8", "၉": "9", "၀": "0" }; a.defineLocale("my", { months: "ဇန်နဝါရီ_ဖေဖော်ဝါရီ_မတ်_ဧပြီ_မေ_ဇွန်_ဇူလိုင်_သြဂုတ်_စက်တင်ဘာ_အောက်တိုဘာ_နိုဝင်ဘာ_ဒီဇင်ဘာ".split("_"), monthsShort: "ဇန်_ဖေ_မတ်_ပြီ_မေ_ဇွန်_လိုင်_သြ_စက်_အောက်_နို_ဒီ".split("_"), weekdays: "တနင်္ဂနွေ_တနင်္လာ_အင်္ဂါ_ဗုဒ္ဓဟူး_ကြာသပတေး_သောကြာ_စနေ".split("_"), weekdaysShort: "နွေ_လာ_ဂါ_ဟူး_ကြာ_သော_နေ".split("_"), weekdaysMin: "နွေ_လာ_ဂါ_ဟူး_ကြာ_သော_နေ".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[ယနေ.] LT [မှာ]", nextDay: "[မနက်ဖြန်] LT [မှာ]", nextWeek: "dddd LT [မှာ]", lastDay: "[မနေ.က] LT [မှာ]", lastWeek: "[ပြီးခဲ့သော] dddd LT [မှာ]", sameElse: "L" }, relativeTime: { future: "လာမည့် %s မှာ", past: "လွန်ခဲ့သော %s က", s: "စက္ကန်.အနည်းငယ်", m: "တစ်မိနစ်", mm: "%d မိနစ်", h: "တစ်နာရီ", hh: "%d နာရီ", d: "တစ်ရက်", dd: "%d ရက်", M: "တစ်လ", MM: "%d လ", y: "တစ်နှစ်", yy: "%d နှစ်" }, preparse: function (a) { return a.replace(/[၁၂၃၄၅၆၇၈၉၀]/g, function (a) { return th[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return sh[a] }) }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Norwegian Bokmål [nb]
		//! authors : Espen Hovlandsdal : https://github.com/rexxars
		//!           Sigurd Gartmann : https://github.com/sigurdga
		a.defineLocale("nb", { months: "januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember".split("_"), monthsShort: "jan._feb._mars_april_mai_juni_juli_aug._sep._okt._nov._des.".split("_"), monthsParseExact: !0, weekdays: "søndag_mandag_tirsdag_onsdag_torsdag_fredag_lørdag".split("_"), weekdaysShort: "sø._ma._ti._on._to._fr._lø.".split("_"), weekdaysMin: "sø_ma_ti_on_to_fr_lø".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY [kl.] HH:mm", LLLL: "dddd D. MMMM YYYY [kl.] HH:mm" }, calendar: { sameDay: "[i dag kl.] LT", nextDay: "[i morgen kl.] LT", nextWeek: "dddd [kl.] LT", lastDay: "[i går kl.] LT", lastWeek: "[forrige] dddd [kl.] LT", sameElse: "L" }, relativeTime: { future: "om %s", past: "%s siden", s: "noen sekunder", m: "ett minutt", mm: "%d minutter", h: "en time", hh: "%d timer", d: "en dag", dd: "%d dager", M: "en måned", MM: "%d måneder", y: "ett år", yy: "%d år" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Nepalese [ne]
	//! author : suvash : https://github.com/suvash
	var uh = { 1: "१", 2: "२", 3: "३", 4: "४", 5: "५", 6: "६", 7: "७", 8: "८", 9: "९", 0: "०" }, vh = { "१": "1", "२": "2", "३": "3", "४": "4", "५": "5", "६": "6", "७": "7", "८": "8", "९": "9", "०": "0" }; a.defineLocale("ne", { months: "जनवरी_फेब्रुवरी_मार्च_अप्रिल_मई_जुन_जुलाई_अगष्ट_सेप्टेम्बर_अक्टोबर_नोभेम्बर_डिसेम्बर".split("_"), monthsShort: "जन._फेब्रु._मार्च_अप्रि._मई_जुन_जुलाई._अग._सेप्ट._अक्टो._नोभे._डिसे.".split("_"), monthsParseExact: !0, weekdays: "आइतबार_सोमबार_मङ्गलबार_बुधबार_बिहिबार_शुक्रबार_शनिबार".split("_"), weekdaysShort: "आइत._सोम._मङ्गल._बुध._बिहि._शुक्र._शनि.".split("_"), weekdaysMin: "आ._सो._मं._बु._बि._शु._श.".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "Aको h:mm बजे", LTS: "Aको h:mm:ss बजे", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, Aको h:mm बजे", LLLL: "dddd, D MMMM YYYY, Aको h:mm बजे" }, preparse: function (a) { return a.replace(/[१२३४५६७८९०]/g, function (a) { return vh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return uh[a] }) }, meridiemParse: /राति|बिहान|दिउँसो|साँझ/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "राति" === b ? a < 4 ? a : a + 12 : "बिहान" === b ? a : "दिउँसो" === b ? a >= 10 ? a : a + 12 : "साँझ" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 3 ? "राति" : a < 12 ? "बिहान" : a < 16 ? "दिउँसो" : a < 20 ? "साँझ" : "राति" }, calendar: { sameDay: "[आज] LT", nextDay: "[भोलि] LT", nextWeek: "[आउँदो] dddd[,] LT", lastDay: "[हिजो] LT", lastWeek: "[गएको] dddd[,] LT", sameElse: "L" }, relativeTime: { future: "%sमा", past: "%s अगाडि", s: "केही क्षण", m: "एक मिनेट", mm: "%d मिनेट", h: "एक घण्टा", hh: "%d घण्टा", d: "एक दिन", dd: "%d दिन", M: "एक महिना", MM: "%d महिना", y: "एक बर्ष", yy: "%d बर्ष" }, week: { dow: 0, doy: 6 } });
	//! moment.js locale configuration
	//! locale : Dutch (Belgium) [nl-be]
	//! author : Joris Röling : https://github.com/jorisroling
	//! author : Jacob Middag : https://github.com/middagj
	var wh = "jan._feb._mrt._apr._mei_jun._jul._aug._sep._okt._nov._dec.".split("_"), xh = "jan_feb_mrt_apr_mei_jun_jul_aug_sep_okt_nov_dec".split("_"), yh = [/^jan/i, /^feb/i, /^maart|mrt.?$/i, /^apr/i, /^mei$/i, /^jun[i.]?$/i, /^jul[i.]?$/i, /^aug/i, /^sep/i, /^okt/i, /^nov/i, /^dec/i], zh = /^(januari|februari|maart|april|mei|april|ju[nl]i|augustus|september|oktober|november|december|jan\.?|feb\.?|mrt\.?|apr\.?|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i; a.defineLocale("nl-be", { months: "januari_februari_maart_april_mei_juni_juli_augustus_september_oktober_november_december".split("_"), monthsShort: function (a, b) { return a ? /-MMM-/.test(b) ? xh[a.month()] : wh[a.month()] : wh }, monthsRegex: zh, monthsShortRegex: zh, monthsStrictRegex: /^(januari|februari|maart|mei|ju[nl]i|april|augustus|september|oktober|november|december)/i, monthsShortStrictRegex: /^(jan\.?|feb\.?|mrt\.?|apr\.?|mei|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i, monthsParse: yh, longMonthsParse: yh, shortMonthsParse: yh, weekdays: "zondag_maandag_dinsdag_woensdag_donderdag_vrijdag_zaterdag".split("_"), weekdaysShort: "zo._ma._di._wo._do._vr._za.".split("_"), weekdaysMin: "Zo_Ma_Di_Wo_Do_Vr_Za".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[vandaag om] LT", nextDay: "[morgen om] LT", nextWeek: "dddd [om] LT", lastDay: "[gisteren om] LT", lastWeek: "[afgelopen] dddd [om] LT", sameElse: "L" }, relativeTime: { future: "over %s", past: "%s geleden", s: "een paar seconden", m: "één minuut", mm: "%d minuten", h: "één uur", hh: "%d uur", d: "één dag", dd: "%d dagen", M: "één maand", MM: "%d maanden", y: "één jaar", yy: "%d jaar" }, dayOfMonthOrdinalParse: /\d{1,2}(ste|de)/, ordinal: function (a) { return a + (1 === a || 8 === a || a >= 20 ? "ste" : "de") }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Dutch [nl]
	//! author : Joris Röling : https://github.com/jorisroling
	//! author : Jacob Middag : https://github.com/middagj
	var Ah = "jan._feb._mrt._apr._mei_jun._jul._aug._sep._okt._nov._dec.".split("_"), Bh = "jan_feb_mrt_apr_mei_jun_jul_aug_sep_okt_nov_dec".split("_"), Ch = [/^jan/i, /^feb/i, /^maart|mrt.?$/i, /^apr/i, /^mei$/i, /^jun[i.]?$/i, /^jul[i.]?$/i, /^aug/i, /^sep/i, /^okt/i, /^nov/i, /^dec/i], Dh = /^(januari|februari|maart|april|mei|april|ju[nl]i|augustus|september|oktober|november|december|jan\.?|feb\.?|mrt\.?|apr\.?|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i; a.defineLocale("nl", { months: "januari_februari_maart_april_mei_juni_juli_augustus_september_oktober_november_december".split("_"), monthsShort: function (a, b) { return a ? /-MMM-/.test(b) ? Bh[a.month()] : Ah[a.month()] : Ah }, monthsRegex: Dh, monthsShortRegex: Dh, monthsStrictRegex: /^(januari|februari|maart|mei|ju[nl]i|april|augustus|september|oktober|november|december)/i, monthsShortStrictRegex: /^(jan\.?|feb\.?|mrt\.?|apr\.?|mei|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i, monthsParse: Ch, longMonthsParse: Ch, shortMonthsParse: Ch, weekdays: "zondag_maandag_dinsdag_woensdag_donderdag_vrijdag_zaterdag".split("_"), weekdaysShort: "zo._ma._di._wo._do._vr._za.".split("_"), weekdaysMin: "Zo_Ma_Di_Wo_Do_Vr_Za".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD-MM-YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[vandaag om] LT", nextDay: "[morgen om] LT", nextWeek: "dddd [om] LT", lastDay: "[gisteren om] LT", lastWeek: "[afgelopen] dddd [om] LT", sameElse: "L" }, relativeTime: { future: "over %s", past: "%s geleden", s: "een paar seconden", m: "één minuut", mm: "%d minuten", h: "één uur", hh: "%d uur", d: "één dag", dd: "%d dagen", M: "één maand", MM: "%d maanden", y: "één jaar", yy: "%d jaar" }, dayOfMonthOrdinalParse: /\d{1,2}(ste|de)/, ordinal: function (a) { return a + (1 === a || 8 === a || a >= 20 ? "ste" : "de") }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Nynorsk [nn]
		//! author : https://github.com/mechuwind
		a.defineLocale("nn", { months: "januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember".split("_"), monthsShort: "jan_feb_mar_apr_mai_jun_jul_aug_sep_okt_nov_des".split("_"), weekdays: "sundag_måndag_tysdag_onsdag_torsdag_fredag_laurdag".split("_"), weekdaysShort: "sun_mån_tys_ons_tor_fre_lau".split("_"), weekdaysMin: "su_må_ty_on_to_fr_lø".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY [kl.] H:mm", LLLL: "dddd D. MMMM YYYY [kl.] HH:mm" }, calendar: { sameDay: "[I dag klokka] LT", nextDay: "[I morgon klokka] LT", nextWeek: "dddd [klokka] LT", lastDay: "[I går klokka] LT", lastWeek: "[Føregåande] dddd [klokka] LT", sameElse: "L" }, relativeTime: { future: "om %s", past: "%s sidan", s: "nokre sekund", m: "eit minutt", mm: "%d minutt", h: "ein time", hh: "%d timar", d: "ein dag", dd: "%d dagar", M: "ein månad", MM: "%d månader", y: "eit år", yy: "%d år" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Punjabi (India) [pa-in]
	//! author : Harpreet Singh : https://github.com/harpreetkhalsagtbit
	var Eh = { 1: "੧", 2: "੨", 3: "੩", 4: "੪", 5: "੫", 6: "੬", 7: "੭", 8: "੮", 9: "੯", 0: "੦" }, Fh = { "੧": "1", "੨": "2", "੩": "3", "੪": "4", "੫": "5", "੬": "6", "੭": "7", "੮": "8", "੯": "9", "੦": "0" }; a.defineLocale("pa-in", { months: "ਜਨਵਰੀ_ਫ਼ਰਵਰੀ_ਮਾਰਚ_ਅਪ੍ਰੈਲ_ਮਈ_ਜੂਨ_ਜੁਲਾਈ_ਅਗਸਤ_ਸਤੰਬਰ_ਅਕਤੂਬਰ_ਨਵੰਬਰ_ਦਸੰਬਰ".split("_"), monthsShort: "ਜਨਵਰੀ_ਫ਼ਰਵਰੀ_ਮਾਰਚ_ਅਪ੍ਰੈਲ_ਮਈ_ਜੂਨ_ਜੁਲਾਈ_ਅਗਸਤ_ਸਤੰਬਰ_ਅਕਤੂਬਰ_ਨਵੰਬਰ_ਦਸੰਬਰ".split("_"), weekdays: "ਐਤਵਾਰ_ਸੋਮਵਾਰ_ਮੰਗਲਵਾਰ_ਬੁਧਵਾਰ_ਵੀਰਵਾਰ_ਸ਼ੁੱਕਰਵਾਰ_ਸ਼ਨੀਚਰਵਾਰ".split("_"), weekdaysShort: "ਐਤ_ਸੋਮ_ਮੰਗਲ_ਬੁਧ_ਵੀਰ_ਸ਼ੁਕਰ_ਸ਼ਨੀ".split("_"), weekdaysMin: "ਐਤ_ਸੋਮ_ਮੰਗਲ_ਬੁਧ_ਵੀਰ_ਸ਼ੁਕਰ_ਸ਼ਨੀ".split("_"), longDateFormat: { LT: "A h:mm ਵਜੇ", LTS: "A h:mm:ss ਵਜੇ", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm ਵਜੇ", LLLL: "dddd, D MMMM YYYY, A h:mm ਵਜੇ" }, calendar: { sameDay: "[ਅਜ] LT", nextDay: "[ਕਲ] LT", nextWeek: "dddd, LT", lastDay: "[ਕਲ] LT", lastWeek: "[ਪਿਛਲੇ] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s ਵਿੱਚ", past: "%s ਪਿਛਲੇ", s: "ਕੁਝ ਸਕਿੰਟ", m: "ਇਕ ਮਿੰਟ", mm: "%d ਮਿੰਟ", h: "ਇੱਕ ਘੰਟਾ", hh: "%d ਘੰਟੇ", d: "ਇੱਕ ਦਿਨ", dd: "%d ਦਿਨ", M: "ਇੱਕ ਮਹੀਨਾ", MM: "%d ਮਹੀਨੇ", y: "ਇੱਕ ਸਾਲ", yy: "%d ਸਾਲ" }, preparse: function (a) { return a.replace(/[੧੨੩੪੫੬੭੮੯੦]/g, function (a) { return Fh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Eh[a] }) }, meridiemParse: /ਰਾਤ|ਸਵੇਰ|ਦੁਪਹਿਰ|ਸ਼ਾਮ/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "ਰਾਤ" === b ? a < 4 ? a : a + 12 : "ਸਵੇਰ" === b ? a : "ਦੁਪਹਿਰ" === b ? a >= 10 ? a : a + 12 : "ਸ਼ਾਮ" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "ਰਾਤ" : a < 10 ? "ਸਵੇਰ" : a < 17 ? "ਦੁਪਹਿਰ" : a < 20 ? "ਸ਼ਾਮ" : "ਰਾਤ" }, week: { dow: 0, doy: 6 } });
	//! moment.js locale configuration
	//! locale : Polish [pl]
	//! author : Rafal Hirsz : https://github.com/evoL
	var Gh = "styczeń_luty_marzec_kwiecień_maj_czerwiec_lipiec_sierpień_wrzesień_październik_listopad_grudzień".split("_"), Hh = "stycznia_lutego_marca_kwietnia_maja_czerwca_lipca_sierpnia_września_października_listopada_grudnia".split("_"); a.defineLocale("pl", { months: function (a, b) { return a ? "" === b ? "(" + Hh[a.month()] + "|" + Gh[a.month()] + ")" : /D MMMM/.test(b) ? Hh[a.month()] : Gh[a.month()] : Gh }, monthsShort: "sty_lut_mar_kwi_maj_cze_lip_sie_wrz_paź_lis_gru".split("_"), weekdays: "niedziela_poniedziałek_wtorek_środa_czwartek_piątek_sobota".split("_"), weekdaysShort: "ndz_pon_wt_śr_czw_pt_sob".split("_"), weekdaysMin: "Nd_Pn_Wt_Śr_Cz_Pt_So".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Dziś o] LT", nextDay: "[Jutro o] LT", nextWeek: "[W] dddd [o] LT", lastDay: "[Wczoraj o] LT", lastWeek: function () { switch (this.day()) { case 0: return "[W zeszłą niedzielę o] LT"; case 3: return "[W zeszłą środę o] LT"; case 6: return "[W zeszłą sobotę o] LT"; default: return "[W zeszły] dddd [o] LT" } }, sameElse: "L" }, relativeTime: { future: "za %s", past: "%s temu", s: "kilka sekund", m: be, mm: be, h: be, hh: be, d: "1 dzień", dd: "%d dni", M: "miesiąc", MM: be, y: "rok", yy: be }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Portuguese (Brazil) [pt-br]
		//! author : Caio Ribeiro Pereira : https://github.com/caio-ribeiro-pereira
		a.defineLocale("pt-br", { months: "Janeiro_Fevereiro_Março_Abril_Maio_Junho_Julho_Agosto_Setembro_Outubro_Novembro_Dezembro".split("_"), monthsShort: "Jan_Fev_Mar_Abr_Mai_Jun_Jul_Ago_Set_Out_Nov_Dez".split("_"), weekdays: "Domingo_Segunda-feira_Terça-feira_Quarta-feira_Quinta-feira_Sexta-feira_Sábado".split("_"), weekdaysShort: "Dom_Seg_Ter_Qua_Qui_Sex_Sáb".split("_"), weekdaysMin: "Do_2ª_3ª_4ª_5ª_6ª_Sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D [de] MMMM [de] YYYY", LLL: "D [de] MMMM [de] YYYY [às] HH:mm", LLLL: "dddd, D [de] MMMM [de] YYYY [às] HH:mm" }, calendar: { sameDay: "[Hoje às] LT", nextDay: "[Amanhã às] LT", nextWeek: "dddd [às] LT", lastDay: "[Ontem às] LT", lastWeek: function () { return 0 === this.day() || 6 === this.day() ? "[Último] dddd [às] LT" : "[Última] dddd [às] LT" }, sameElse: "L" }, relativeTime: { future: "em %s", past: "%s atrás", s: "poucos segundos", m: "um minuto", mm: "%d minutos", h: "uma hora", hh: "%d horas", d: "um dia", dd: "%d dias", M: "um mês", MM: "%d meses", y: "um ano", yy: "%d anos" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº" }),
		//! moment.js locale configuration
		//! locale : Portuguese [pt]
		//! author : Jefferson : https://github.com/jalex79
		a.defineLocale("pt", { months: "Janeiro_Fevereiro_Março_Abril_Maio_Junho_Julho_Agosto_Setembro_Outubro_Novembro_Dezembro".split("_"), monthsShort: "Jan_Fev_Mar_Abr_Mai_Jun_Jul_Ago_Set_Out_Nov_Dez".split("_"), weekdays: "Domingo_Segunda-Feira_Terça-Feira_Quarta-Feira_Quinta-Feira_Sexta-Feira_Sábado".split("_"), weekdaysShort: "Dom_Seg_Ter_Qua_Qui_Sex_Sáb".split("_"), weekdaysMin: "Do_2ª_3ª_4ª_5ª_6ª_Sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D [de] MMMM [de] YYYY", LLL: "D [de] MMMM [de] YYYY HH:mm", LLLL: "dddd, D [de] MMMM [de] YYYY HH:mm" }, calendar: { sameDay: "[Hoje às] LT", nextDay: "[Amanhã às] LT", nextWeek: "dddd [às] LT", lastDay: "[Ontem às] LT", lastWeek: function () { return 0 === this.day() || 6 === this.day() ? "[Último] dddd [às] LT" : "[Última] dddd [às] LT" }, sameElse: "L" }, relativeTime: { future: "em %s", past: "há %s", s: "segundos", m: "um minuto", mm: "%d minutos", h: "uma hora", hh: "%d horas", d: "um dia", dd: "%d dias", M: "um mês", MM: "%d meses", y: "um ano", yy: "%d anos" }, dayOfMonthOrdinalParse: /\d{1,2}º/, ordinal: "%dº", week: { dow: 1, doy: 4 } }), a.defineLocale("ro", { months: "ianuarie_februarie_martie_aprilie_mai_iunie_iulie_august_septembrie_octombrie_noiembrie_decembrie".split("_"), monthsShort: "ian._febr._mart._apr._mai_iun._iul._aug._sept._oct._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "duminică_luni_marți_miercuri_joi_vineri_sâmbătă".split("_"), weekdaysShort: "Dum_Lun_Mar_Mie_Joi_Vin_Sâm".split("_"), weekdaysMin: "Du_Lu_Ma_Mi_Jo_Vi_Sâ".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY H:mm", LLLL: "dddd, D MMMM YYYY H:mm" }, calendar: { sameDay: "[azi la] LT", nextDay: "[mâine la] LT", nextWeek: "dddd [la] LT", lastDay: "[ieri la] LT", lastWeek: "[fosta] dddd [la] LT", sameElse: "L" }, relativeTime: { future: "peste %s", past: "%s în urmă", s: "câteva secunde", m: "un minut", mm: ce, h: "o oră", hh: ce, d: "o zi", dd: ce, M: "o lună", MM: ce, y: "un an", yy: ce }, week: { dow: 1, doy: 7 } }); var Ih = [/^янв/i, /^фев/i, /^мар/i, /^апр/i, /^ма[йя]/i, /^июн/i, /^июл/i, /^авг/i, /^сен/i, /^окт/i, /^ноя/i, /^дек/i]; a.defineLocale("ru", { months: { format: "января_февраля_марта_апреля_мая_июня_июля_августа_сентября_октября_ноября_декабря".split("_"), standalone: "январь_февраль_март_апрель_май_июнь_июль_август_сентябрь_октябрь_ноябрь_декабрь".split("_") }, monthsShort: { format: "янв._февр._мар._апр._мая_июня_июля_авг._сент._окт._нояб._дек.".split("_"), standalone: "янв._февр._март_апр._май_июнь_июль_авг._сент._окт._нояб._дек.".split("_") }, weekdays: { standalone: "воскресенье_понедельник_вторник_среда_четверг_пятница_суббота".split("_"), format: "воскресенье_понедельник_вторник_среду_четверг_пятницу_субботу".split("_"), isFormat: /\[ ?[Вв] ?(?:прошлую|следующую|эту)? ?\] ?dddd/ }, weekdaysShort: "вс_пн_вт_ср_чт_пт_сб".split("_"), weekdaysMin: "вс_пн_вт_ср_чт_пт_сб".split("_"), monthsParse: Ih, longMonthsParse: Ih, shortMonthsParse: Ih, monthsRegex: /^(январ[ья]|янв\.?|феврал[ья]|февр?\.?|марта?|мар\.?|апрел[ья]|апр\.?|ма[йя]|июн[ья]|июн\.?|июл[ья]|июл\.?|августа?|авг\.?|сентябр[ья]|сент?\.?|октябр[ья]|окт\.?|ноябр[ья]|нояб?\.?|декабр[ья]|дек\.?)/i, monthsShortRegex: /^(январ[ья]|янв\.?|феврал[ья]|февр?\.?|марта?|мар\.?|апрел[ья]|апр\.?|ма[йя]|июн[ья]|июн\.?|июл[ья]|июл\.?|августа?|авг\.?|сентябр[ья]|сент?\.?|октябр[ья]|окт\.?|ноябр[ья]|нояб?\.?|декабр[ья]|дек\.?)/i, monthsStrictRegex: /^(январ[яь]|феврал[яь]|марта?|апрел[яь]|ма[яй]|июн[яь]|июл[яь]|августа?|сентябр[яь]|октябр[яь]|ноябр[яь]|декабр[яь])/i, monthsShortStrictRegex: /^(янв\.|февр?\.|мар[т.]|апр\.|ма[яй]|июн[ья.]|июл[ья.]|авг\.|сент?\.|окт\.|нояб?\.|дек\.)/i, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY г.", LLL: "D MMMM YYYY г., HH:mm", LLLL: "dddd, D MMMM YYYY г., HH:mm" }, calendar: { sameDay: "[Сегодня в] LT", nextDay: "[Завтра в] LT", lastDay: "[Вчера в] LT", nextWeek: function (a) { if (a.week() === this.week()) return 2 === this.day() ? "[Во] dddd [в] LT" : "[В] dddd [в] LT"; switch (this.day()) { case 0: return "[В следующее] dddd [в] LT"; case 1: case 2: case 4: return "[В следующий] dddd [в] LT"; case 3: case 5: case 6: return "[В следующую] dddd [в] LT" } }, lastWeek: function (a) { if (a.week() === this.week()) return 2 === this.day() ? "[Во] dddd [в] LT" : "[В] dddd [в] LT"; switch (this.day()) { case 0: return "[В прошлое] dddd [в] LT"; case 1: case 2: case 4: return "[В прошлый] dddd [в] LT"; case 3: case 5: case 6: return "[В прошлую] dddd [в] LT" } }, sameElse: "L" }, relativeTime: { future: "через %s", past: "%s назад", s: "несколько секунд", m: ee, mm: ee, h: "час", hh: ee, d: "день", dd: ee, M: "месяц", MM: ee, y: "год", yy: ee }, meridiemParse: /ночи|утра|дня|вечера/i, isPM: function (a) { return /^(дня|вечера)$/.test(a) }, meridiem: function (a, b, c) { return a < 4 ? "ночи" : a < 12 ? "утра" : a < 17 ? "дня" : "вечера" }, dayOfMonthOrdinalParse: /\d{1,2}-(й|го|я)/, ordinal: function (a, b) { switch (b) { case "M": case "d": case "DDD": return a + "-й"; case "D": return a + "-го"; case "w": case "W": return a + "-я"; default: return a } }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Sindhi [sd]
	//! author : Narain Sagar : https://github.com/narainsagar
	var Jh = ["جنوري", "فيبروري", "مارچ", "اپريل", "مئي", "جون", "جولاءِ", "آگسٽ", "سيپٽمبر", "آڪٽوبر", "نومبر", "ڊسمبر"], Kh = ["آچر", "سومر", "اڱارو", "اربع", "خميس", "جمع", "ڇنڇر"]; a.defineLocale("sd", { months: Jh, monthsShort: Jh, weekdays: Kh, weekdaysShort: Kh, weekdaysMin: Kh, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd، D MMMM YYYY HH:mm" }, meridiemParse: /صبح|شام/, isPM: function (a) { return "شام" === a }, meridiem: function (a, b, c) { return a < 12 ? "صبح" : "شام" }, calendar: { sameDay: "[اڄ] LT", nextDay: "[سڀاڻي] LT", nextWeek: "dddd [اڳين هفتي تي] LT", lastDay: "[ڪالهه] LT", lastWeek: "[گزريل هفتي] dddd [تي] LT", sameElse: "L" }, relativeTime: { future: "%s پوء", past: "%s اڳ", s: "چند سيڪنڊ", m: "هڪ منٽ", mm: "%d منٽ", h: "هڪ ڪلاڪ", hh: "%d ڪلاڪ", d: "هڪ ڏينهن", dd: "%d ڏينهن", M: "هڪ مهينو", MM: "%d مهينا", y: "هڪ سال", yy: "%d سال" }, preparse: function (a) { return a.replace(/،/g, ",") }, postformat: function (a) { return a.replace(/,/g, "،") }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Northern Sami [se]
		//! authors : Bård Rolstad Henriksen : https://github.com/karamell
		a.defineLocale("se", { months: "ođđajagemánnu_guovvamánnu_njukčamánnu_cuoŋománnu_miessemánnu_geassemánnu_suoidnemánnu_borgemánnu_čakčamánnu_golggotmánnu_skábmamánnu_juovlamánnu".split("_"), monthsShort: "ođđj_guov_njuk_cuo_mies_geas_suoi_borg_čakč_golg_skáb_juov".split("_"), weekdays: "sotnabeaivi_vuossárga_maŋŋebárga_gaskavahkku_duorastat_bearjadat_lávvardat".split("_"), weekdaysShort: "sotn_vuos_maŋ_gask_duor_bear_láv".split("_"), weekdaysMin: "s_v_m_g_d_b_L".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "MMMM D. [b.] YYYY", LLL: "MMMM D. [b.] YYYY [ti.] HH:mm", LLLL: "dddd, MMMM D. [b.] YYYY [ti.] HH:mm" }, calendar: { sameDay: "[otne ti] LT", nextDay: "[ihttin ti] LT", nextWeek: "dddd [ti] LT", lastDay: "[ikte ti] LT", lastWeek: "[ovddit] dddd [ti] LT", sameElse: "L" }, relativeTime: { future: "%s geažes", past: "maŋit %s", s: "moadde sekunddat", m: "okta minuhta", mm: "%d minuhtat", h: "okta diimmu", hh: "%d diimmut", d: "okta beaivi", dd: "%d beaivvit", M: "okta mánnu", MM: "%d mánut", y: "okta jahki", yy: "%d jagit" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Sinhalese [si]
		//! author : Sampath Sitinamaluwa : https://github.com/sampathsris
		a.defineLocale("si", { months: "ජනවාරි_පෙබරවාරි_මාර්තු_අප්‍රේල්_මැයි_ජූනි_ජූලි_අගෝස්තු_සැප්තැම්බර්_ඔක්තෝබර්_නොවැම්බර්_දෙසැම්බර්".split("_"), monthsShort: "ජන_පෙබ_මාර්_අප්_මැයි_ජූනි_ජූලි_අගෝ_සැප්_ඔක්_නොවැ_දෙසැ".split("_"), weekdays: "ඉරිදා_සඳුදා_අඟහරුවාදා_බදාදා_බ්‍රහස්පතින්දා_සිකුරාදා_සෙනසුරාදා".split("_"), weekdaysShort: "ඉරි_සඳු_අඟ_බදා_බ්‍රහ_සිකු_සෙන".split("_"), weekdaysMin: "ඉ_ස_අ_බ_බ්‍ර_සි_සෙ".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "a h:mm", LTS: "a h:mm:ss", L: "YYYY/MM/DD", LL: "YYYY MMMM D", LLL: "YYYY MMMM D, a h:mm", LLLL: "YYYY MMMM D [වැනි] dddd, a h:mm:ss" }, calendar: { sameDay: "[අද] LT[ට]", nextDay: "[හෙට] LT[ට]", nextWeek: "dddd LT[ට]", lastDay: "[ඊයේ] LT[ට]", lastWeek: "[පසුගිය] dddd LT[ට]", sameElse: "L" }, relativeTime: { future: "%sකින්", past: "%sකට පෙර", s: "තත්පර කිහිපය", m: "මිනිත්තුව", mm: "මිනිත්තු %d", h: "පැය", hh: "පැය %d", d: "දිනය", dd: "දින %d", M: "මාසය", MM: "මාස %d", y: "වසර", yy: "වසර %d" }, dayOfMonthOrdinalParse: /\d{1,2} වැනි/, ordinal: function (a) { return a + " වැනි" }, meridiemParse: /පෙර වරු|පස් වරු|පෙ.ව|ප.ව./, isPM: function (a) { return "ප.ව." === a || "පස් වරු" === a }, meridiem: function (a, b, c) { return a > 11 ? c ? "ප.ව." : "පස් වරු" : c ? "පෙ.ව." : "පෙර වරු" } });
	//! moment.js locale configuration
	//! locale : Slovak [sk]
	//! author : Martin Minka : https://github.com/k2s
	//! based on work of petrbela : https://github.com/petrbela
	var Lh = "január_február_marec_apríl_máj_jún_júl_august_september_október_november_december".split("_"), Mh = "jan_feb_mar_apr_máj_jún_júl_aug_sep_okt_nov_dec".split("_"); a.defineLocale("sk", { months: Lh, monthsShort: Mh, weekdays: "nedeľa_pondelok_utorok_streda_štvrtok_piatok_sobota".split("_"), weekdaysShort: "ne_po_ut_st_št_pi_so".split("_"), weekdaysMin: "ne_po_ut_st_št_pi_so".split("_"), longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd D. MMMM YYYY H:mm" }, calendar: { sameDay: "[dnes o] LT", nextDay: "[zajtra o] LT", nextWeek: function () { switch (this.day()) { case 0: return "[v nedeľu o] LT"; case 1: case 2: return "[v] dddd [o] LT"; case 3: return "[v stredu o] LT"; case 4: return "[vo štvrtok o] LT"; case 5: return "[v piatok o] LT"; case 6: return "[v sobotu o] LT" } }, lastDay: "[včera o] LT", lastWeek: function () { switch (this.day()) { case 0: return "[minulú nedeľu o] LT"; case 1: case 2: return "[minulý] dddd [o] LT"; case 3: return "[minulú stredu o] LT"; case 4: case 5: return "[minulý] dddd [o] LT"; case 6: return "[minulú sobotu o] LT" } }, sameElse: "L" }, relativeTime: { future: "za %s", past: "pred %s", s: ge, m: ge, mm: ge, h: ge, hh: ge, d: ge, dd: ge, M: ge, MM: ge, y: ge, yy: ge }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }), a.defineLocale("sl", { months: "januar_februar_marec_april_maj_junij_julij_avgust_september_oktober_november_december".split("_"), monthsShort: "jan._feb._mar._apr._maj._jun._jul._avg._sep._okt._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "nedelja_ponedeljek_torek_sreda_četrtek_petek_sobota".split("_"), weekdaysShort: "ned._pon._tor._sre._čet._pet._sob.".split("_"), weekdaysMin: "ne_po_to_sr_če_pe_so".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[danes ob] LT", nextDay: "[jutri ob] LT", nextWeek: function () { switch (this.day()) { case 0: return "[v] [nedeljo] [ob] LT"; case 3: return "[v] [sredo] [ob] LT"; case 6: return "[v] [soboto] [ob] LT"; case 1: case 2: case 4: case 5: return "[v] dddd [ob] LT" } }, lastDay: "[včeraj ob] LT", lastWeek: function () { switch (this.day()) { case 0: return "[prejšnjo] [nedeljo] [ob] LT"; case 3: return "[prejšnjo] [sredo] [ob] LT"; case 6: return "[prejšnjo] [soboto] [ob] LT"; case 1: case 2: case 4: case 5: return "[prejšnji] dddd [ob] LT" } }, sameElse: "L" }, relativeTime: { future: "čez %s", past: "pred %s", s: he, m: he, mm: he, h: he, hh: he, d: he, dd: he, M: he, MM: he, y: he, yy: he }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Albanian [sq]
		//! author : Flakërim Ismani : https://github.com/flakerimi
		//! author : Menelion Elensúle : https://github.com/Oire
		//! author : Oerd Cukalla : https://github.com/oerd
		a.defineLocale("sq", { months: "Janar_Shkurt_Mars_Prill_Maj_Qershor_Korrik_Gusht_Shtator_Tetor_Nëntor_Dhjetor".split("_"), monthsShort: "Jan_Shk_Mar_Pri_Maj_Qer_Kor_Gus_Sht_Tet_Nën_Dhj".split("_"), weekdays: "E Diel_E Hënë_E Martë_E Mërkurë_E Enjte_E Premte_E Shtunë".split("_"), weekdaysShort: "Die_Hën_Mar_Mër_Enj_Pre_Sht".split("_"), weekdaysMin: "D_H_Ma_Më_E_P_Sh".split("_"), weekdaysParseExact: !0, meridiemParse: /PD|MD/, isPM: function (a) { return "M" === a.charAt(0) }, meridiem: function (a, b, c) { return a < 12 ? "PD" : "MD" }, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Sot në] LT", nextDay: "[Nesër në] LT", nextWeek: "dddd [në] LT", lastDay: "[Dje në] LT", lastWeek: "dddd [e kaluar në] LT", sameElse: "L" }, relativeTime: { future: "në %s", past: "%s më parë", s: "disa sekonda", m: "një minutë", mm: "%d minuta", h: "një orë", hh: "%d orë", d: "një ditë", dd: "%d ditë", M: "një muaj", MM: "%d muaj", y: "një vit", yy: "%d vite" }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Serbian Cyrillic [sr-cyrl]
	//! author : Milan Janačković<milanjanackovic@gmail.com> : https://github.com/milan-j
	var Nh = { words: { m: ["један минут", "једне минуте"], mm: ["минут", "минуте", "минута"], h: ["један сат", "једног сата"], hh: ["сат", "сата", "сати"], dd: ["дан", "дана", "дана"], MM: ["месец", "месеца", "месеци"], yy: ["година", "године", "година"] }, correctGrammaticalCase: function (a, b) { return 1 === a ? b[0] : a >= 2 && a <= 4 ? b[1] : b[2] }, translate: function (a, b, c) { var d = Nh.words[c]; return 1 === c.length ? b ? d[0] : d[1] : a + " " + Nh.correctGrammaticalCase(a, d) } }; a.defineLocale("sr-cyrl", { months: "јануар_фебруар_март_април_мај_јун_јул_август_септембар_октобар_новембар_децембар".split("_"), monthsShort: "јан._феб._мар._апр._мај_јун_јул_авг._сеп._окт._нов._дец.".split("_"), monthsParseExact: !0, weekdays: "недеља_понедељак_уторак_среда_четвртак_петак_субота".split("_"), weekdaysShort: "нед._пон._уто._сре._чет._пет._суб.".split("_"), weekdaysMin: "не_по_ут_ср_че_пе_су".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[данас у] LT", nextDay: "[сутра у] LT", nextWeek: function () { switch (this.day()) { case 0: return "[у] [недељу] [у] LT"; case 3: return "[у] [среду] [у] LT"; case 6: return "[у] [суботу] [у] LT"; case 1: case 2: case 4: case 5: return "[у] dddd [у] LT" } }, lastDay: "[јуче у] LT", lastWeek: function () { var a = ["[прошле] [недеље] [у] LT", "[прошлог] [понедељка] [у] LT", "[прошлог] [уторка] [у] LT", "[прошле] [среде] [у] LT", "[прошлог] [четвртка] [у] LT", "[прошлог] [петка] [у] LT", "[прошле] [суботе] [у] LT"]; return a[this.day()] }, sameElse: "L" }, relativeTime: { future: "за %s", past: "пре %s", s: "неколико секунди", m: Nh.translate, mm: Nh.translate, h: Nh.translate, hh: Nh.translate, d: "дан", dd: Nh.translate, M: "месец", MM: Nh.translate, y: "годину", yy: Nh.translate }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Serbian [sr]
	//! author : Milan Janačković<milanjanackovic@gmail.com> : https://github.com/milan-j
	var Oh = { words: { m: ["jedan minut", "jedne minute"], mm: ["minut", "minute", "minuta"], h: ["jedan sat", "jednog sata"], hh: ["sat", "sata", "sati"], dd: ["dan", "dana", "dana"], MM: ["mesec", "meseca", "meseci"], yy: ["godina", "godine", "godina"] }, correctGrammaticalCase: function (a, b) { return 1 === a ? b[0] : a >= 2 && a <= 4 ? b[1] : b[2] }, translate: function (a, b, c) { var d = Oh.words[c]; return 1 === c.length ? b ? d[0] : d[1] : a + " " + Oh.correctGrammaticalCase(a, d) } }; a.defineLocale("sr", { months: "januar_februar_mart_april_maj_jun_jul_avgust_septembar_oktobar_novembar_decembar".split("_"), monthsShort: "jan._feb._mar._apr._maj_jun_jul_avg._sep._okt._nov._dec.".split("_"), monthsParseExact: !0, weekdays: "nedelja_ponedeljak_utorak_sreda_četvrtak_petak_subota".split("_"), weekdaysShort: "ned._pon._uto._sre._čet._pet._sub.".split("_"), weekdaysMin: "ne_po_ut_sr_če_pe_su".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD.MM.YYYY", LL: "D. MMMM YYYY", LLL: "D. MMMM YYYY H:mm", LLLL: "dddd, D. MMMM YYYY H:mm" }, calendar: { sameDay: "[danas u] LT", nextDay: "[sutra u] LT", nextWeek: function () { switch (this.day()) { case 0: return "[u] [nedelju] [u] LT"; case 3: return "[u] [sredu] [u] LT"; case 6: return "[u] [subotu] [u] LT"; case 1: case 2: case 4: case 5: return "[u] dddd [u] LT" } }, lastDay: "[juče u] LT", lastWeek: function () { var a = ["[prošle] [nedelje] [u] LT", "[prošlog] [ponedeljka] [u] LT", "[prošlog] [utorka] [u] LT", "[prošle] [srede] [u] LT", "[prošlog] [četvrtka] [u] LT", "[prošlog] [petka] [u] LT", "[prošle] [subote] [u] LT"]; return a[this.day()] }, sameElse: "L" }, relativeTime: { future: "za %s", past: "pre %s", s: "nekoliko sekundi", m: Oh.translate, mm: Oh.translate, h: Oh.translate, hh: Oh.translate, d: "dan", dd: Oh.translate, M: "mesec", MM: Oh.translate, y: "godinu", yy: Oh.translate }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : siSwati [ss]
		//! author : Nicolai Davies<mail@nicolai.io> : https://github.com/nicolaidavies
		a.defineLocale("ss", { months: "Bhimbidvwane_Indlovana_Indlov'lenkhulu_Mabasa_Inkhwekhweti_Inhlaba_Kholwane_Ingci_Inyoni_Imphala_Lweti_Ingongoni".split("_"), monthsShort: "Bhi_Ina_Inu_Mab_Ink_Inh_Kho_Igc_Iny_Imp_Lwe_Igo".split("_"), weekdays: "Lisontfo_Umsombuluko_Lesibili_Lesitsatfu_Lesine_Lesihlanu_Umgcibelo".split("_"), weekdaysShort: "Lis_Umb_Lsb_Les_Lsi_Lsh_Umg".split("_"), weekdaysMin: "Li_Us_Lb_Lt_Ls_Lh_Ug".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendar: { sameDay: "[Namuhla nga] LT", nextDay: "[Kusasa nga] LT", nextWeek: "dddd [nga] LT", lastDay: "[Itolo nga] LT", lastWeek: "dddd [leliphelile] [nga] LT", sameElse: "L" }, relativeTime: { future: "nga %s", past: "wenteka nga %s", s: "emizuzwana lomcane", m: "umzuzu", mm: "%d emizuzu", h: "lihora", hh: "%d emahora", d: "lilanga", dd: "%d emalanga", M: "inyanga", MM: "%d tinyanga", y: "umnyaka", yy: "%d iminyaka" }, meridiemParse: /ekuseni|emini|entsambama|ebusuku/, meridiem: function (a, b, c) { return a < 11 ? "ekuseni" : a < 15 ? "emini" : a < 19 ? "entsambama" : "ebusuku" }, meridiemHour: function (a, b) { return 12 === a && (a = 0), "ekuseni" === b ? a : "emini" === b ? a >= 11 ? a : a + 12 : "entsambama" === b || "ebusuku" === b ? 0 === a ? 0 : a + 12 : void 0 }, dayOfMonthOrdinalParse: /\d{1,2}/, ordinal: "%d", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Swedish [sv]
		//! author : Jens Alm : https://github.com/ulmus
		a.defineLocale("sv", { months: "januari_februari_mars_april_maj_juni_juli_augusti_september_oktober_november_december".split("_"), monthsShort: "jan_feb_mar_apr_maj_jun_jul_aug_sep_okt_nov_dec".split("_"), weekdays: "söndag_måndag_tisdag_onsdag_torsdag_fredag_lördag".split("_"), weekdaysShort: "sön_mån_tis_ons_tor_fre_lör".split("_"), weekdaysMin: "sö_må_ti_on_to_fr_lö".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY-MM-DD", LL: "D MMMM YYYY", LLL: "D MMMM YYYY [kl.] HH:mm", LLLL: "dddd D MMMM YYYY [kl.] HH:mm", lll: "D MMM YYYY HH:mm", llll: "ddd D MMM YYYY HH:mm" }, calendar: { sameDay: "[Idag] LT", nextDay: "[Imorgon] LT", lastDay: "[Igår] LT", nextWeek: "[På] dddd LT", lastWeek: "[I] dddd[s] LT", sameElse: "L" }, relativeTime: { future: "om %s", past: "för %s sedan", s: "några sekunder", m: "en minut", mm: "%d minuter", h: "en timme", hh: "%d timmar", d: "en dag", dd: "%d dagar", M: "en månad", MM: "%d månader", y: "ett år", yy: "%d år" }, dayOfMonthOrdinalParse: /\d{1,2}(e|a)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "e" : 1 === b ? "a" : 2 === b ? "a" : "e"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Swahili [sw]
		//! author : Fahad Kassim : https://github.com/fadsel
		a.defineLocale("sw", { months: "Januari_Februari_Machi_Aprili_Mei_Juni_Julai_Agosti_Septemba_Oktoba_Novemba_Desemba".split("_"), monthsShort: "Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ago_Sep_Okt_Nov_Des".split("_"), weekdays: "Jumapili_Jumatatu_Jumanne_Jumatano_Alhamisi_Ijumaa_Jumamosi".split("_"), weekdaysShort: "Jpl_Jtat_Jnne_Jtan_Alh_Ijm_Jmos".split("_"), weekdaysMin: "J2_J3_J4_J5_Al_Ij_J1".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[leo saa] LT", nextDay: "[kesho saa] LT", nextWeek: "[wiki ijayo] dddd [saat] LT", lastDay: "[jana] LT", lastWeek: "[wiki iliyopita] dddd [saat] LT", sameElse: "L" }, relativeTime: { future: "%s baadaye", past: "tokea %s", s: "hivi punde", m: "dakika moja", mm: "dakika %d", h: "saa limoja", hh: "masaa %d", d: "siku moja", dd: "masiku %d", M: "mwezi mmoja", MM: "miezi %d", y: "mwaka mmoja", yy: "miaka %d" }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Tamil [ta]
	//! author : Arjunkumar Krishnamoorthy : https://github.com/tk120404
	var Ph = { 1: "௧", 2: "௨", 3: "௩", 4: "௪", 5: "௫", 6: "௬", 7: "௭", 8: "௮", 9: "௯", 0: "௦" }, Qh = { "௧": "1", "௨": "2", "௩": "3", "௪": "4", "௫": "5", "௬": "6", "௭": "7", "௮": "8", "௯": "9", "௦": "0" }; a.defineLocale("ta", { months: "ஜனவரி_பிப்ரவரி_மார்ச்_ஏப்ரல்_மே_ஜூன்_ஜூலை_ஆகஸ்ட்_செப்டெம்பர்_அக்டோபர்_நவம்பர்_டிசம்பர்".split("_"), monthsShort: "ஜனவரி_பிப்ரவரி_மார்ச்_ஏப்ரல்_மே_ஜூன்_ஜூலை_ஆகஸ்ட்_செப்டெம்பர்_அக்டோபர்_நவம்பர்_டிசம்பர்".split("_"), weekdays: "ஞாயிற்றுக்கிழமை_திங்கட்கிழமை_செவ்வாய்கிழமை_புதன்கிழமை_வியாழக்கிழமை_வெள்ளிக்கிழமை_சனிக்கிழமை".split("_"), weekdaysShort: "ஞாயிறு_திங்கள்_செவ்வாய்_புதன்_வியாழன்_வெள்ளி_சனி".split("_"), weekdaysMin: "ஞா_தி_செ_பு_வி_வெ_ச".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, HH:mm", LLLL: "dddd, D MMMM YYYY, HH:mm" }, calendar: { sameDay: "[இன்று] LT", nextDay: "[நாளை] LT", nextWeek: "dddd, LT", lastDay: "[நேற்று] LT", lastWeek: "[கடந்த வாரம்] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s இல்", past: "%s முன்", s: "ஒரு சில விநாடிகள்", m: "ஒரு நிமிடம்", mm: "%d நிமிடங்கள்", h: "ஒரு மணி நேரம்", hh: "%d மணி நேரம்", d: "ஒரு நாள்", dd: "%d நாட்கள்", M: "ஒரு மாதம்", MM: "%d மாதங்கள்", y: "ஒரு வருடம்", yy: "%d ஆண்டுகள்" }, dayOfMonthOrdinalParse: /\d{1,2}வது/, ordinal: function (a) { return a + "வது" }, preparse: function (a) { return a.replace(/[௧௨௩௪௫௬௭௮௯௦]/g, function (a) { return Qh[a] }) }, postformat: function (a) { return a.replace(/\d/g, function (a) { return Ph[a] }) }, meridiemParse: /யாமம்|வைகறை|காலை|நண்பகல்|எற்பாடு|மாலை/, meridiem: function (a, b, c) { return a < 2 ? " யாமம்" : a < 6 ? " வைகறை" : a < 10 ? " காலை" : a < 14 ? " நண்பகல்" : a < 18 ? " எற்பாடு" : a < 22 ? " மாலை" : " யாமம்" }, meridiemHour: function (a, b) { return 12 === a && (a = 0), "யாமம்" === b ? a < 2 ? a : a + 12 : "வைகறை" === b || "காலை" === b ? a : "நண்பகல்" === b && a >= 10 ? a : a + 12 }, week: { dow: 0, doy: 6 } }),
		//! moment.js locale configuration
		//! locale : Telugu [te]
		//! author : Krishna Chaitanya Thota : https://github.com/kcthota
		a.defineLocale("te", { months: "జనవరి_ఫిబ్రవరి_మార్చి_ఏప్రిల్_మే_జూన్_జూలై_ఆగస్టు_సెప్టెంబర్_అక్టోబర్_నవంబర్_డిసెంబర్".split("_"), monthsShort: "జన._ఫిబ్ర._మార్చి_ఏప్రి._మే_జూన్_జూలై_ఆగ._సెప్._అక్టో._నవ._డిసె.".split("_"), monthsParseExact: !0, weekdays: "ఆదివారం_సోమవారం_మంగళవారం_బుధవారం_గురువారం_శుక్రవారం_శనివారం".split("_"), weekdaysShort: "ఆది_సోమ_మంగళ_బుధ_గురు_శుక్ర_శని".split("_"), weekdaysMin: "ఆ_సో_మం_బు_గు_శు_శ".split("_"), longDateFormat: { LT: "A h:mm", LTS: "A h:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY, A h:mm", LLLL: "dddd, D MMMM YYYY, A h:mm" }, calendar: { sameDay: "[నేడు] LT", nextDay: "[రేపు] LT", nextWeek: "dddd, LT", lastDay: "[నిన్న] LT", lastWeek: "[గత] dddd, LT", sameElse: "L" }, relativeTime: { future: "%s లో", past: "%s క్రితం", s: "కొన్ని క్షణాలు", m: "ఒక నిమిషం", mm: "%d నిమిషాలు", h: "ఒక గంట", hh: "%d గంటలు", d: "ఒక రోజు", dd: "%d రోజులు", M: "ఒక నెల", MM: "%d నెలలు", y: "ఒక సంవత్సరం", yy: "%d సంవత్సరాలు" }, dayOfMonthOrdinalParse: /\d{1,2}వ/, ordinal: "%dవ", meridiemParse: /రాత్రి|ఉదయం|మధ్యాహ్నం|సాయంత్రం/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "రాత్రి" === b ? a < 4 ? a : a + 12 : "ఉదయం" === b ? a : "మధ్యాహ్నం" === b ? a >= 10 ? a : a + 12 : "సాయంత్రం" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { return a < 4 ? "రాత్రి" : a < 10 ? "ఉదయం" : a < 17 ? "మధ్యాహ్నం" : a < 20 ? "సాయంత్రం" : "రాత్రి" }, week: { dow: 0, doy: 6 } }),
		//! moment.js locale configuration
		//! locale : Tetun Dili (East Timor) [tet]
		//! author : Joshua Brooks : https://github.com/joshbrooks
		//! author : Onorio De J. Afonso : https://github.com/marobo
		a.defineLocale("tet", { months: "Janeiru_Fevereiru_Marsu_Abril_Maiu_Juniu_Juliu_Augustu_Setembru_Outubru_Novembru_Dezembru".split("_"), monthsShort: "Jan_Fev_Mar_Abr_Mai_Jun_Jul_Aug_Set_Out_Nov_Dez".split("_"), weekdays: "Domingu_Segunda_Tersa_Kuarta_Kinta_Sexta_Sabadu".split("_"), weekdaysShort: "Dom_Seg_Ters_Kua_Kint_Sext_Sab".split("_"), weekdaysMin: "Do_Seg_Te_Ku_Ki_Sex_Sa".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[Ohin iha] LT", nextDay: "[Aban iha] LT", nextWeek: "dddd [iha] LT", lastDay: "[Horiseik iha] LT", lastWeek: "dddd [semana kotuk] [iha] LT", sameElse: "L" }, relativeTime: { future: "iha %s", past: "%s liuba", s: "minutu balun", m: "minutu ida", mm: "minutus %d", h: "horas ida", hh: "horas %d", d: "loron ida", dd: "loron %d", M: "fulan ida", MM: "fulan %d", y: "tinan ida", yy: "tinan %d" }, dayOfMonthOrdinalParse: /\d{1,2}(st|nd|rd|th)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Thai [th]
		//! author : Kridsada Thanabulpong : https://github.com/sirn
		a.defineLocale("th", { months: "มกราคม_กุมภาพันธ์_มีนาคม_เมษายน_พฤษภาคม_มิถุนายน_กรกฎาคม_สิงหาคม_กันยายน_ตุลาคม_พฤศจิกายน_ธันวาคม".split("_"), monthsShort: "ม.ค._ก.พ._มี.ค._เม.ย._พ.ค._มิ.ย._ก.ค._ส.ค._ก.ย._ต.ค._พ.ย._ธ.ค.".split("_"), monthsParseExact: !0, weekdays: "อาทิตย์_จันทร์_อังคาร_พุธ_พฤหัสบดี_ศุกร์_เสาร์".split("_"), weekdaysShort: "อาทิตย์_จันทร์_อังคาร_พุธ_พฤหัส_ศุกร์_เสาร์".split("_"), weekdaysMin: "อา._จ._อ._พ._พฤ._ศ._ส.".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "H:mm", LTS: "H:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY เวลา H:mm", LLLL: "วันddddที่ D MMMM YYYY เวลา H:mm" }, meridiemParse: /ก่อนเที่ยง|หลังเที่ยง/, isPM: function (a) { return "หลังเที่ยง" === a }, meridiem: function (a, b, c) { return a < 12 ? "ก่อนเที่ยง" : "หลังเที่ยง" }, calendar: { sameDay: "[วันนี้ เวลา] LT", nextDay: "[พรุ่งนี้ เวลา] LT", nextWeek: "dddd[หน้า เวลา] LT", lastDay: "[เมื่อวานนี้ เวลา] LT", lastWeek: "[วัน]dddd[ที่แล้ว เวลา] LT", sameElse: "L" }, relativeTime: { future: "อีก %s", past: "%sที่แล้ว", s: "ไม่กี่วินาที", m: "1 นาที", mm: "%d นาที", h: "1 ชั่วโมง", hh: "%d ชั่วโมง", d: "1 วัน", dd: "%d วัน", M: "1 เดือน", MM: "%d เดือน", y: "1 ปี", yy: "%d ปี" } }),
		//! moment.js locale configuration
		//! locale : Tagalog (Philippines) [tl-ph]
		//! author : Dan Hagman : https://github.com/hagmandan
		a.defineLocale("tl-ph", { months: "Enero_Pebrero_Marso_Abril_Mayo_Hunyo_Hulyo_Agosto_Setyembre_Oktubre_Nobyembre_Disyembre".split("_"), monthsShort: "Ene_Peb_Mar_Abr_May_Hun_Hul_Ago_Set_Okt_Nob_Dis".split("_"), weekdays: "Linggo_Lunes_Martes_Miyerkules_Huwebes_Biyernes_Sabado".split("_"), weekdaysShort: "Lin_Lun_Mar_Miy_Huw_Biy_Sab".split("_"), weekdaysMin: "Li_Lu_Ma_Mi_Hu_Bi_Sab".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "MM/D/YYYY", LL: "MMMM D, YYYY", LLL: "MMMM D, YYYY HH:mm", LLLL: "dddd, MMMM DD, YYYY HH:mm" }, calendar: { sameDay: "LT [ngayong araw]", nextDay: "[Bukas ng] LT", nextWeek: "LT [sa susunod na] dddd", lastDay: "LT [kahapon]", lastWeek: "LT [noong nakaraang] dddd", sameElse: "L" }, relativeTime: { future: "sa loob ng %s", past: "%s ang nakalipas", s: "ilang segundo", m: "isang minuto", mm: "%d minuto", h: "isang oras", hh: "%d oras", d: "isang araw", dd: "%d araw", M: "isang buwan", MM: "%d buwan", y: "isang taon", yy: "%d taon" }, dayOfMonthOrdinalParse: /\d{1,2}/, ordinal: function (a) { return a }, week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Klingon [tlh]
	//! author : Dominika Kruk : https://github.com/amaranthrose
	var Rh = "pagh_wa’_cha’_wej_loS_vagh_jav_Soch_chorgh_Hut".split("_"); a.defineLocale("tlh", { months: "tera’ jar wa’_tera’ jar cha’_tera’ jar wej_tera’ jar loS_tera’ jar vagh_tera’ jar jav_tera’ jar Soch_tera’ jar chorgh_tera’ jar Hut_tera’ jar wa’maH_tera’ jar wa’maH wa’_tera’ jar wa’maH cha’".split("_"), monthsShort: "jar wa’_jar cha’_jar wej_jar loS_jar vagh_jar jav_jar Soch_jar chorgh_jar Hut_jar wa’maH_jar wa’maH wa’_jar wa’maH cha’".split("_"), monthsParseExact: !0, weekdays: "lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"), weekdaysShort: "lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"), weekdaysMin: "lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[DaHjaj] LT", nextDay: "[wa’leS] LT", nextWeek: "LLL", lastDay: "[wa’Hu’] LT", lastWeek: "LLL", sameElse: "L" }, relativeTime: { future: ie, past: je, s: "puS lup", m: "wa’ tup", mm: ke, h: "wa’ rep", hh: ke, d: "wa’ jaj", dd: ke, M: "wa’ jar", MM: ke, y: "wa’ DIS", yy: ke }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } });
	//! moment.js locale configuration
	//! locale : Turkish [tr]
	//! authors : Erhan Gundogan : https://github.com/erhangundogan,
	//!           Burak Yiğit Kaya: https://github.com/BYK
	var Sh = { 1: "'inci", 5: "'inci", 8: "'inci", 70: "'inci", 80: "'inci", 2: "'nci", 7: "'nci", 20: "'nci", 50: "'nci", 3: "'üncü", 4: "'üncü", 100: "'üncü", 6: "'ncı", 9: "'uncu", 10: "'uncu", 30: "'uncu", 60: "'ıncı", 90: "'ıncı" }; a.defineLocale("tr", { months: "Ocak_Şubat_Mart_Nisan_Mayıs_Haziran_Temmuz_Ağustos_Eylül_Ekim_Kasım_Aralık".split("_"), monthsShort: "Oca_Şub_Mar_Nis_May_Haz_Tem_Ağu_Eyl_Eki_Kas_Ara".split("_"), weekdays: "Pazar_Pazartesi_Salı_Çarşamba_Perşembe_Cuma_Cumartesi".split("_"), weekdaysShort: "Paz_Pts_Sal_Çar_Per_Cum_Cts".split("_"), weekdaysMin: "Pz_Pt_Sa_Ça_Pe_Cu_Ct".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[bugün saat] LT", nextDay: "[yarın saat] LT", nextWeek: "[haftaya] dddd [saat] LT", lastDay: "[dün] LT", lastWeek: "[geçen hafta] dddd [saat] LT", sameElse: "L" }, relativeTime: { future: "%s sonra", past: "%s önce", s: "birkaç saniye", m: "bir dakika", mm: "%d dakika", h: "bir saat", hh: "%d saat", d: "bir gün", dd: "%d gün", M: "bir ay", MM: "%d ay", y: "bir yıl", yy: "%d yıl" }, dayOfMonthOrdinalParse: /\d{1,2}'(inci|nci|üncü|ncı|uncu|ıncı)/, ordinal: function (a) { if (0 === a) return a + "'ıncı"; var b = a % 10, c = a % 100 - b, d = a >= 100 ? 100 : null; return a + (Sh[b] || Sh[c] || Sh[d]) }, week: { dow: 1, doy: 7 } }),
		//! moment.js locale configuration
		//! locale : Talossan [tzl]
		//! author : Robin van der Vliet : https://github.com/robin0van0der0v
		//! author : Iustì Canun
		a.defineLocale("tzl", { months: "Januar_Fevraglh_Març_Avrïu_Mai_Gün_Julia_Guscht_Setemvar_Listopäts_Noemvar_Zecemvar".split("_"), monthsShort: "Jan_Fev_Mar_Avr_Mai_Gün_Jul_Gus_Set_Lis_Noe_Zec".split("_"), weekdays: "Súladi_Lúneçi_Maitzi_Márcuri_Xhúadi_Viénerçi_Sáturi".split("_"), weekdaysShort: "Súl_Lún_Mai_Már_Xhú_Vié_Sát".split("_"), weekdaysMin: "Sú_Lú_Ma_Má_Xh_Vi_Sá".split("_"), longDateFormat: { LT: "HH.mm", LTS: "HH.mm.ss", L: "DD.MM.YYYY", LL: "D. MMMM [dallas] YYYY", LLL: "D. MMMM [dallas] YYYY HH.mm", LLLL: "dddd, [li] D. MMMM [dallas] YYYY HH.mm" }, meridiemParse: /d\'o|d\'a/i, isPM: function (a) { return "d'o" === a.toLowerCase() }, meridiem: function (a, b, c) { return a > 11 ? c ? "d'o" : "D'O" : c ? "d'a" : "D'A" }, calendar: { sameDay: "[oxhi à] LT", nextDay: "[demà à] LT", nextWeek: "dddd [à] LT", lastDay: "[ieiri à] LT", lastWeek: "[sür el] dddd [lasteu à] LT", sameElse: "L" }, relativeTime: { future: "osprei %s", past: "ja%s", s: me, m: me, mm: me, h: me, hh: me, d: me, dd: me, M: me, MM: me, y: me, yy: me }, dayOfMonthOrdinalParse: /\d{1,2}\./, ordinal: "%d.", week: { dow: 1, doy: 4 } }),
		//! moment.js locale configuration
		//! locale : Central Atlas Tamazight Latin [tzm-latn]
		//! author : Abdel Said : https://github.com/abdelsaid
		a.defineLocale("tzm-latn", { months: "innayr_brˤayrˤ_marˤsˤ_ibrir_mayyw_ywnyw_ywlywz_ɣwšt_šwtanbir_ktˤwbrˤ_nwwanbir_dwjnbir".split("_"), monthsShort: "innayr_brˤayrˤ_marˤsˤ_ibrir_mayyw_ywnyw_ywlywz_ɣwšt_šwtanbir_ktˤwbrˤ_nwwanbir_dwjnbir".split("_"), weekdays: "asamas_aynas_asinas_akras_akwas_asimwas_asiḍyas".split("_"), weekdaysShort: "asamas_aynas_asinas_akras_akwas_asimwas_asiḍyas".split("_"), weekdaysMin: "asamas_aynas_asinas_akras_akwas_asimwas_asiḍyas".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[asdkh g] LT", nextDay: "[aska g] LT", nextWeek: "dddd [g] LT", lastDay: "[assant g] LT", lastWeek: "dddd [g] LT", sameElse: "L" }, relativeTime: { future: "dadkh s yan %s", past: "yan %s", s: "imik", m: "minuḍ", mm: "%d minuḍ", h: "saɛa", hh: "%d tassaɛin", d: "ass", dd: "%d ossan", M: "ayowr", MM: "%d iyyirn", y: "asgas", yy: "%d isgasn" }, week: { dow: 6, doy: 12 } }),
		//! moment.js locale configuration
		//! locale : Central Atlas Tamazight [tzm]
		//! author : Abdel Said : https://github.com/abdelsaid
		a.defineLocale("tzm", { months: "ⵉⵏⵏⴰⵢⵔ_ⴱⵕⴰⵢⵕ_ⵎⴰⵕⵚ_ⵉⴱⵔⵉⵔ_ⵎⴰⵢⵢⵓ_ⵢⵓⵏⵢⵓ_ⵢⵓⵍⵢⵓⵣ_ⵖⵓⵛⵜ_ⵛⵓⵜⴰⵏⴱⵉⵔ_ⴽⵟⵓⴱⵕ_ⵏⵓⵡⴰⵏⴱⵉⵔ_ⴷⵓⵊⵏⴱⵉⵔ".split("_"), monthsShort: "ⵉⵏⵏⴰⵢⵔ_ⴱⵕⴰⵢⵕ_ⵎⴰⵕⵚ_ⵉⴱⵔⵉⵔ_ⵎⴰⵢⵢⵓ_ⵢⵓⵏⵢⵓ_ⵢⵓⵍⵢⵓⵣ_ⵖⵓⵛⵜ_ⵛⵓⵜⴰⵏⴱⵉⵔ_ⴽⵟⵓⴱⵕ_ⵏⵓⵡⴰⵏⴱⵉⵔ_ⴷⵓⵊⵏⴱⵉⵔ".split("_"), weekdays: "ⴰⵙⴰⵎⴰⵙ_ⴰⵢⵏⴰⵙ_ⴰⵙⵉⵏⴰⵙ_ⴰⴽⵔⴰⵙ_ⴰⴽⵡⴰⵙ_ⴰⵙⵉⵎⵡⴰⵙ_ⴰⵙⵉⴹⵢⴰⵙ".split("_"), weekdaysShort: "ⴰⵙⴰⵎⴰⵙ_ⴰⵢⵏⴰⵙ_ⴰⵙⵉⵏⴰⵙ_ⴰⴽⵔⴰⵙ_ⴰⴽⵡⴰⵙ_ⴰⵙⵉⵎⵡⴰⵙ_ⴰⵙⵉⴹⵢⴰⵙ".split("_"), weekdaysMin: "ⴰⵙⴰⵎⴰⵙ_ⴰⵢⵏⴰⵙ_ⴰⵙⵉⵏⴰⵙ_ⴰⴽⵔⴰⵙ_ⴰⴽⵡⴰⵙ_ⴰⵙⵉⵎⵡⴰⵙ_ⴰⵙⵉⴹⵢⴰⵙ".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd D MMMM YYYY HH:mm" }, calendar: { sameDay: "[ⴰⵙⴷⵅ ⴴ] LT", nextDay: "[ⴰⵙⴽⴰ ⴴ] LT", nextWeek: "dddd [ⴴ] LT", lastDay: "[ⴰⵚⴰⵏⵜ ⴴ] LT", lastWeek: "dddd [ⴴ] LT", sameElse: "L" }, relativeTime: { future: "ⴷⴰⴷⵅ ⵙ ⵢⴰⵏ %s", past: "ⵢⴰⵏ %s", s: "ⵉⵎⵉⴽ", m: "ⵎⵉⵏⵓⴺ", mm: "%d ⵎⵉⵏⵓⴺ", h: "ⵙⴰⵄⴰ", hh: "%d ⵜⴰⵙⵙⴰⵄⵉⵏ", d: "ⴰⵙⵙ", dd: "%d oⵙⵙⴰⵏ", M: "ⴰⵢoⵓⵔ", MM: "%d ⵉⵢⵢⵉⵔⵏ", y: "ⴰⵙⴳⴰⵙ", yy: "%d ⵉⵙⴳⴰⵙⵏ" }, week: { dow: 6, doy: 12 } }), a.defineLocale("uk", { months: { format: "січня_лютого_березня_квітня_травня_червня_липня_серпня_вересня_жовтня_листопада_грудня".split("_"), standalone: "січень_лютий_березень_квітень_травень_червень_липень_серпень_вересень_жовтень_листопад_грудень".split("_") }, monthsShort: "січ_лют_бер_квіт_трав_черв_лип_серп_вер_жовт_лист_груд".split("_"), weekdays: pe, weekdaysShort: "нд_пн_вт_ср_чт_пт_сб".split("_"), weekdaysMin: "нд_пн_вт_ср_чт_пт_сб".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD.MM.YYYY", LL: "D MMMM YYYY р.", LLL: "D MMMM YYYY р., HH:mm", LLLL: "dddd, D MMMM YYYY р., HH:mm" }, calendar: { sameDay: qe("[Сьогодні "), nextDay: qe("[Завтра "), lastDay: qe("[Вчора "), nextWeek: qe("[У] dddd ["), lastWeek: function () { switch (this.day()) { case 0: case 3: case 5: case 6: return qe("[Минулої] dddd [").call(this); case 1: case 2: case 4: return qe("[Минулого] dddd [").call(this) } }, sameElse: "L" }, relativeTime: { future: "за %s", past: "%s тому", s: "декілька секунд", m: oe, mm: oe, h: "годину", hh: oe, d: "день", dd: oe, M: "місяць", MM: oe, y: "рік", yy: oe }, meridiemParse: /ночі|ранку|дня|вечора/, isPM: function (a) { return /^(дня|вечора)$/.test(a) }, meridiem: function (a, b, c) { return a < 4 ? "ночі" : a < 12 ? "ранку" : a < 17 ? "дня" : "вечора" }, dayOfMonthOrdinalParse: /\d{1,2}-(й|го)/, ordinal: function (a, b) { switch (b) { case "M": case "d": case "DDD": case "w": case "W": return a + "-й"; case "D": return a + "-го"; default: return a } }, week: { dow: 1, doy: 7 } });
	//! moment.js locale configuration
	//! locale : Urdu [ur]
	//! author : Sawood Alam : https://github.com/ibnesayeed
	//! author : Zack : https://github.com/ZackVision
	var Th = ["جنوری", "فروری", "مارچ", "اپریل", "مئی", "جون", "جولائی", "اگست", "ستمبر", "اکتوبر", "نومبر", "دسمبر"], Uh = ["اتوار", "پیر", "منگل", "بدھ", "جمعرات", "جمعہ", "ہفتہ"];
	//! moment.js locale configuration
	//! locale : Uzbek Latin [uz-latn]
	//! author : Rasulbek Mirzayev : github.com/Rasulbeeek
	//! moment.js locale configuration
	//! locale : Uzbek [uz]
	//! author : Sardor Muminov : https://github.com/muminoff
	//! moment.js locale configuration
	//! locale : Vietnamese [vi]
	//! author : Bang Nguyen : https://github.com/bangnk
	//! moment.js locale configuration
	//! locale : Pseudo [x-pseudo]
	//! author : Andrew Hood : https://github.com/andrewhood125
	//! moment.js locale configuration
	//! locale : Yoruba Nigeria [yo]
	//! author : Atolagbe Abisoye : https://github.com/andela-batolagbe
	//! moment.js locale configuration
	//! locale : Chinese (China) [zh-cn]
	//! author : suupic : https://github.com/suupic
	//! author : Zeno Zeng : https://github.com/zenozeng
	//! moment.js locale configuration
	//! locale : Chinese (Hong Kong) [zh-hk]
	//! author : Ben : https://github.com/ben-lin
	//! author : Chris Lam : https://github.com/hehachris
	//! author : Konstantin : https://github.com/skfd
	//! moment.js locale configuration
	//! locale : Chinese (Taiwan) [zh-tw]
	//! author : Ben : https://github.com/ben-lin
	//! author : Chris Lam : https://github.com/hehachris
	return a.defineLocale("ur", { months: Th, monthsShort: Th, weekdays: Uh, weekdaysShort: Uh, weekdaysMin: Uh, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd، D MMMM YYYY HH:mm" }, meridiemParse: /صبح|شام/, isPM: function (a) { return "شام" === a }, meridiem: function (a, b, c) { return a < 12 ? "صبح" : "شام" }, calendar: { sameDay: "[آج بوقت] LT", nextDay: "[کل بوقت] LT", nextWeek: "dddd [بوقت] LT", lastDay: "[گذشتہ روز بوقت] LT", lastWeek: "[گذشتہ] dddd [بوقت] LT", sameElse: "L" }, relativeTime: { future: "%s بعد", past: "%s قبل", s: "چند سیکنڈ", m: "ایک منٹ", mm: "%d منٹ", h: "ایک گھنٹہ", hh: "%d گھنٹے", d: "ایک دن", dd: "%d دن", M: "ایک ماہ", MM: "%d ماہ", y: "ایک سال", yy: "%d سال" }, preparse: function (a) { return a.replace(/،/g, ",") }, postformat: function (a) { return a.replace(/,/g, "،") }, week: { dow: 1, doy: 4 } }), a.defineLocale("uz-latn", { months: "Yanvar_Fevral_Mart_Aprel_May_Iyun_Iyul_Avgust_Sentabr_Oktabr_Noyabr_Dekabr".split("_"), monthsShort: "Yan_Fev_Mar_Apr_May_Iyun_Iyul_Avg_Sen_Okt_Noy_Dek".split("_"), weekdays: "Yakshanba_Dushanba_Seshanba_Chorshanba_Payshanba_Juma_Shanba".split("_"), weekdaysShort: "Yak_Dush_Sesh_Chor_Pay_Jum_Shan".split("_"), weekdaysMin: "Ya_Du_Se_Cho_Pa_Ju_Sha".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "D MMMM YYYY, dddd HH:mm" }, calendar: { sameDay: "[Bugun soat] LT [da]", nextDay: "[Ertaga] LT [da]", nextWeek: "dddd [kuni soat] LT [da]", lastDay: "[Kecha soat] LT [da]", lastWeek: "[O'tgan] dddd [kuni soat] LT [da]", sameElse: "L" }, relativeTime: { future: "Yaqin %s ichida", past: "Bir necha %s oldin", s: "soniya", m: "bir daqiqa", mm: "%d daqiqa", h: "bir soat", hh: "%d soat", d: "bir kun", dd: "%d kun", M: "bir oy", MM: "%d oy", y: "bir yil", yy: "%d yil" }, week: { dow: 1, doy: 7 } }), a.defineLocale("uz", { months: "январ_феврал_март_апрел_май_июн_июл_август_сентябр_октябр_ноябр_декабр".split("_"), monthsShort: "янв_фев_мар_апр_май_июн_июл_авг_сен_окт_ноя_дек".split("_"), weekdays: "Якшанба_Душанба_Сешанба_Чоршанба_Пайшанба_Жума_Шанба".split("_"), weekdaysShort: "Якш_Душ_Сеш_Чор_Пай_Жум_Шан".split("_"), weekdaysMin: "Як_Ду_Се_Чо_Па_Жу_Ша".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "D MMMM YYYY, dddd HH:mm" }, calendar: { sameDay: "[Бугун соат] LT [да]", nextDay: "[Эртага] LT [да]", nextWeek: "dddd [куни соат] LT [да]", lastDay: "[Кеча соат] LT [да]", lastWeek: "[Утган] dddd [куни соат] LT [да]", sameElse: "L" }, relativeTime: { future: "Якин %s ичида", past: "Бир неча %s олдин", s: "фурсат", m: "бир дакика", mm: "%d дакика", h: "бир соат", hh: "%d соат", d: "бир кун", dd: "%d кун", M: "бир ой", MM: "%d ой", y: "бир йил", yy: "%d йил" }, week: { dow: 1, doy: 7 } }), a.defineLocale("vi", { months: "tháng 1_tháng 2_tháng 3_tháng 4_tháng 5_tháng 6_tháng 7_tháng 8_tháng 9_tháng 10_tháng 11_tháng 12".split("_"), monthsShort: "Th01_Th02_Th03_Th04_Th05_Th06_Th07_Th08_Th09_Th10_Th11_Th12".split("_"), monthsParseExact: !0, weekdays: "chủ nhật_thứ hai_thứ ba_thứ tư_thứ năm_thứ sáu_thứ bảy".split("_"), weekdaysShort: "CN_T2_T3_T4_T5_T6_T7".split("_"), weekdaysMin: "CN_T2_T3_T4_T5_T6_T7".split("_"), weekdaysParseExact: !0, meridiemParse: /sa|ch/i, isPM: function (a) { return /^ch$/i.test(a) }, meridiem: function (a, b, c) { return a < 12 ? c ? "sa" : "SA" : c ? "ch" : "CH" }, longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "DD/MM/YYYY", LL: "D MMMM [năm] YYYY", LLL: "D MMMM [năm] YYYY HH:mm", LLLL: "dddd, D MMMM [năm] YYYY HH:mm", l: "DD/M/YYYY", ll: "D MMM YYYY", lll: "D MMM YYYY HH:mm", llll: "ddd, D MMM YYYY HH:mm" }, calendar: { sameDay: "[Hôm nay lúc] LT", nextDay: "[Ngày mai lúc] LT", nextWeek: "dddd [tuần tới lúc] LT", lastDay: "[Hôm qua lúc] LT", lastWeek: "dddd [tuần rồi lúc] LT", sameElse: "L" }, relativeTime: { future: "%s tới", past: "%s trước", s: "vài giây", m: "một phút", mm: "%d phút", h: "một giờ", hh: "%d giờ", d: "một ngày", dd: "%d ngày", M: "một tháng", MM: "%d tháng", y: "một năm", yy: "%d năm" }, dayOfMonthOrdinalParse: /\d{1,2}/, ordinal: function (a) { return a }, week: { dow: 1, doy: 4 } }), a.defineLocale("x-pseudo", { months: "J~áñúá~rý_F~ébrú~árý_~Márc~h_Áp~ríl_~Máý_~Júñé~_Júl~ý_Áú~gúst~_Sép~témb~ér_Ó~ctób~ér_Ñ~óvém~bér_~Décé~mbér".split("_"), monthsShort: "J~áñ_~Féb_~Már_~Ápr_~Máý_~Júñ_~Júl_~Áúg_~Sép_~Óct_~Ñóv_~Déc".split("_"), monthsParseExact: !0, weekdays: "S~úñdá~ý_Mó~ñdáý~_Túé~sdáý~_Wéd~ñésd~áý_T~húrs~dáý_~Fríd~áý_S~átúr~dáý".split("_"), weekdaysShort: "S~úñ_~Móñ_~Túé_~Wéd_~Thú_~Frí_~Sát".split("_"), weekdaysMin: "S~ú_Mó~_Tú_~Wé_T~h_Fr~_Sá".split("_"), weekdaysParseExact: !0, longDateFormat: { LT: "HH:mm", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY HH:mm", LLLL: "dddd, D MMMM YYYY HH:mm" }, calendar: { sameDay: "[T~ódá~ý át] LT", nextDay: "[T~ómó~rró~w át] LT", nextWeek: "dddd [át] LT", lastDay: "[Ý~ést~érdá~ý át] LT", lastWeek: "[L~ást] dddd [át] LT", sameElse: "L" }, relativeTime: { future: "í~ñ %s", past: "%s á~gó", s: "á ~féw ~sécó~ñds", m: "á ~míñ~úté", mm: "%d m~íñú~tés", h: "á~ñ hó~úr", hh: "%d h~óúrs", d: "á ~dáý", dd: "%d d~áýs", M: "á ~móñ~th", MM: "%d m~óñt~hs", y: "á ~ýéár", yy: "%d ý~éárs" }, dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/, ordinal: function (a) { var b = a % 10, c = 1 === ~~(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c }, week: { dow: 1, doy: 4 } }), a.defineLocale("yo", { months: "Sẹ́rẹ́_Èrèlè_Ẹrẹ̀nà_Ìgbé_Èbibi_Òkùdu_Agẹmo_Ògún_Owewe_Ọ̀wàrà_Bélú_Ọ̀pẹ̀̀".split("_"), monthsShort: "Sẹ́r_Èrl_Ẹrn_Ìgb_Èbi_Òkù_Agẹ_Ògú_Owe_Ọ̀wà_Bél_Ọ̀pẹ̀̀".split("_"), weekdays: "Àìkú_Ajé_Ìsẹ́gun_Ọjọ́rú_Ọjọ́bọ_Ẹtì_Àbámẹ́ta".split("_"), weekdaysShort: "Àìk_Ajé_Ìsẹ́_Ọjr_Ọjb_Ẹtì_Àbá".split("_"), weekdaysMin: "Àì_Aj_Ìs_Ọr_Ọb_Ẹt_Àb".split("_"), longDateFormat: { LT: "h:mm A", LTS: "h:mm:ss A", L: "DD/MM/YYYY", LL: "D MMMM YYYY", LLL: "D MMMM YYYY h:mm A", LLLL: "dddd, D MMMM YYYY h:mm A" }, calendar: { sameDay: "[Ònì ni] LT", nextDay: "[Ọ̀la ni] LT", nextWeek: "dddd [Ọsẹ̀ tón'bọ] [ni] LT", lastDay: "[Àna ni] LT", lastWeek: "dddd [Ọsẹ̀ tólọ́] [ni] LT", sameElse: "L" }, relativeTime: { future: "ní %s", past: "%s kọjá", s: "ìsẹjú aayá die", m: "ìsẹjú kan", mm: "ìsẹjú %d", h: "wákati kan", hh: "wákati %d", d: "ọjọ́ kan", dd: "ọjọ́ %d", M: "osù kan", MM: "osù %d", y: "ọdún kan", yy: "ọdún %d" }, dayOfMonthOrdinalParse: /ọjọ́\s\d{1,2}/, ordinal: "ọjọ́ %d", week: { dow: 1, doy: 4 } }), a.defineLocale("zh-cn", { months: "一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月".split("_"), monthsShort: "1月_2月_3月_4月_5月_6月_7月_8月_9月_10月_11月_12月".split("_"), weekdays: "星期日_星期一_星期二_星期三_星期四_星期五_星期六".split("_"), weekdaysShort: "周日_周一_周二_周三_周四_周五_周六".split("_"), weekdaysMin: "日_一_二_三_四_五_六".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY年MMMD日", LL: "YYYY年MMMD日", LLL: "YYYY年MMMD日Ah点mm分", LLLL: "YYYY年MMMD日ddddAh点mm分", l: "YYYY年MMMD日", ll: "YYYY年MMMD日", lll: "YYYY年MMMD日 HH:mm", llll: "YYYY年MMMD日dddd HH:mm" }, meridiemParse: /凌晨|早上|上午|中午|下午|晚上/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "凌晨" === b || "早上" === b || "上午" === b ? a : "下午" === b || "晚上" === b ? a + 12 : a >= 11 ? a : a + 12 }, meridiem: function (a, b, c) { var d = 100 * a + b; return d < 600 ? "凌晨" : d < 900 ? "早上" : d < 1130 ? "上午" : d < 1230 ? "中午" : d < 1800 ? "下午" : "晚上" }, calendar: { sameDay: "[今天]LT", nextDay: "[明天]LT", nextWeek: "[下]ddddLT", lastDay: "[昨天]LT", lastWeek: "[上]ddddLT", sameElse: "L" }, dayOfMonthOrdinalParse: /\d{1,2}(日|月|周)/, ordinal: function (a, b) { switch (b) { case "d": case "D": case "DDD": return a + "日"; case "M": return a + "月"; case "w": case "W": return a + "周"; default: return a } }, relativeTime: { future: "%s内", past: "%s前", s: "几秒", m: "1 分钟", mm: "%d 分钟", h: "1 小时", hh: "%d 小时", d: "1 天", dd: "%d 天", M: "1 个月", MM: "%d 个月", y: "1 年", yy: "%d 年" }, week: { dow: 1, doy: 4 } }), a.defineLocale("zh-hk", { months: "一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月".split("_"), monthsShort: "1月_2月_3月_4月_5月_6月_7月_8月_9月_10月_11月_12月".split("_"), weekdays: "星期日_星期一_星期二_星期三_星期四_星期五_星期六".split("_"), weekdaysShort: "週日_週一_週二_週三_週四_週五_週六".split("_"), weekdaysMin: "日_一_二_三_四_五_六".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY年MMMD日", LL: "YYYY年MMMD日", LLL: "YYYY年MMMD日 HH:mm", LLLL: "YYYY年MMMD日dddd HH:mm", l: "YYYY年MMMD日", ll: "YYYY年MMMD日", lll: "YYYY年MMMD日 HH:mm", llll: "YYYY年MMMD日dddd HH:mm" }, meridiemParse: /凌晨|早上|上午|中午|下午|晚上/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "凌晨" === b || "早上" === b || "上午" === b ? a : "中午" === b ? a >= 11 ? a : a + 12 : "下午" === b || "晚上" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { var d = 100 * a + b; return d < 600 ? "凌晨" : d < 900 ? "早上" : d < 1130 ? "上午" : d < 1230 ? "中午" : d < 1800 ? "下午" : "晚上" }, calendar: { sameDay: "[今天]LT", nextDay: "[明天]LT", nextWeek: "[下]ddddLT", lastDay: "[昨天]LT", lastWeek: "[上]ddddLT", sameElse: "L" }, dayOfMonthOrdinalParse: /\d{1,2}(日|月|週)/, ordinal: function (a, b) { switch (b) { case "d": case "D": case "DDD": return a + "日"; case "M": return a + "月"; case "w": case "W": return a + "週"; default: return a } }, relativeTime: { future: "%s內", past: "%s前", s: "幾秒", m: "1 分鐘", mm: "%d 分鐘", h: "1 小時", hh: "%d 小時", d: "1 天", dd: "%d 天", M: "1 個月", MM: "%d 個月", y: "1 年", yy: "%d 年" } }), a.defineLocale("zh-tw", { months: "一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月".split("_"), monthsShort: "1月_2月_3月_4月_5月_6月_7月_8月_9月_10月_11月_12月".split("_"), weekdays: "星期日_星期一_星期二_星期三_星期四_星期五_星期六".split("_"), weekdaysShort: "週日_週一_週二_週三_週四_週五_週六".split("_"), weekdaysMin: "日_一_二_三_四_五_六".split("_"), longDateFormat: { LT: "HH:mm", LTS: "HH:mm:ss", L: "YYYY年MMMD日", LL: "YYYY年MMMD日", LLL: "YYYY年MMMD日 HH:mm", LLLL: "YYYY年MMMD日dddd HH:mm", l: "YYYY年MMMD日", ll: "YYYY年MMMD日", lll: "YYYY年MMMD日 HH:mm", llll: "YYYY年MMMD日dddd HH:mm" }, meridiemParse: /凌晨|早上|上午|中午|下午|晚上/, meridiemHour: function (a, b) { return 12 === a && (a = 0), "凌晨" === b || "早上" === b || "上午" === b ? a : "中午" === b ? a >= 11 ? a : a + 12 : "下午" === b || "晚上" === b ? a + 12 : void 0 }, meridiem: function (a, b, c) { var d = 100 * a + b; return d < 600 ? "凌晨" : d < 900 ? "早上" : d < 1130 ? "上午" : d < 1230 ? "中午" : d < 1800 ? "下午" : "晚上" }, calendar: { sameDay: "[今天]LT", nextDay: "[明天]LT", nextWeek: "[下]ddddLT", lastDay: "[昨天]LT", lastWeek: "[上]ddddLT", sameElse: "L" }, dayOfMonthOrdinalParse: /\d{1,2}(日|月|週)/, ordinal: function (a, b) { switch (b) { case "d": case "D": case "DDD": return a + "日"; case "M": return a + "月"; case "w": case "W": return a + "週"; default: return a } }, relativeTime: { future: "%s內", past: "%s前", s: "幾秒", m: "1 分鐘", mm: "%d 分鐘", h: "1 小時", hh: "%d 小時", d: "1 天", dd: "%d 天", M: "1 個月", MM: "%d 個月", y: "1 年", yy: "%d 年" } }), a.locale("en"), a
});
//! moment.js
//! version : 2.29.4
//! authors : Tim Wood, Iskren Chernev, Moment.js contributors
//! license : MIT
//! momentjs.com
!function (a, b) { "object" == typeof exports && "undefined" != typeof module ? module.exports = b() : "function" == typeof define && define.amd ? define(b) : a.moment = b() }(this, function () {
	"use strict"; function a() { return sd.apply(null, arguments) } function b(a) { sd = a } function c(a) { return a instanceof Array || "[object Array]" === Object.prototype.toString.call(a) } function d(a) { return null != a && "[object Object]" === Object.prototype.toString.call(a) } function e(a) { var b; for (b in a) return !1; return !0 } function f(a) { return void 0 === a } function g(a) { return "number" == typeof a || "[object Number]" === Object.prototype.toString.call(a) } function h(a) { return a instanceof Date || "[object Date]" === Object.prototype.toString.call(a) } function i(a, b) { var c, d = []; for (c = 0; c < a.length; ++c)d.push(b(a[c], c)); return d } function j(a, b) { return Object.prototype.hasOwnProperty.call(a, b) } function k(a, b) { for (var c in b) j(b, c) && (a[c] = b[c]); return j(b, "toString") && (a.toString = b.toString), j(b, "valueOf") && (a.valueOf = b.valueOf), a } function l(a, b, c, d) { return sb(a, b, c, d, !0).utc() } function m() { return { empty: !1, unusedTokens: [], unusedInput: [], overflow: -2, charsLeftOver: 0, nullInput: !1, invalidMonth: null, invalidFormat: !1, userInvalidated: !1, iso: !1, parsedDateParts: [], meridiem: null, rfc2822: !1, weekdayMismatch: !1 } } function n(a) { return null == a._pf && (a._pf = m()), a._pf } function o(a) { if (null == a._isValid) { var b = n(a), c = ud.call(b.parsedDateParts, function (a) { return null != a }), d = !isNaN(a._d.getTime()) && b.overflow < 0 && !b.empty && !b.invalidMonth && !b.invalidWeekday && !b.nullInput && !b.invalidFormat && !b.userInvalidated && (!b.meridiem || b.meridiem && c); if (a._strict && (d = d && 0 === b.charsLeftOver && 0 === b.unusedTokens.length && void 0 === b.bigHour), null != Object.isFrozen && Object.isFrozen(a)) return d; a._isValid = d } return a._isValid } function p(a) { var b = l(NaN); return null != a ? k(n(b), a) : n(b).userInvalidated = !0, b } function q(a, b) { var c, d, e; if (f(b._isAMomentObject) || (a._isAMomentObject = b._isAMomentObject), f(b._i) || (a._i = b._i), f(b._f) || (a._f = b._f), f(b._l) || (a._l = b._l), f(b._strict) || (a._strict = b._strict), f(b._tzm) || (a._tzm = b._tzm), f(b._isUTC) || (a._isUTC = b._isUTC), f(b._offset) || (a._offset = b._offset), f(b._pf) || (a._pf = n(b)), f(b._locale) || (a._locale = b._locale), vd.length > 0) for (c = 0; c < vd.length; c++)d = vd[c], e = b[d], f(e) || (a[d] = e); return a } function r(b) { q(this, b), this._d = new Date(null != b._d ? b._d.getTime() : NaN), this.isValid() || (this._d = new Date(NaN)), wd === !1 && (wd = !0, a.updateOffset(this), wd = !1) } function s(a) { return a instanceof r || null != a && null != a._isAMomentObject } function t(a) { return a < 0 ? Math.ceil(a) || 0 : Math.floor(a) } function u(a) { var b = +a, c = 0; return 0 !== b && isFinite(b) && (c = t(b)), c } function v(a, b, c) { var d, e = Math.min(a.length, b.length), f = Math.abs(a.length - b.length), g = 0; for (d = 0; d < e; d++)(c && a[d] !== b[d] || !c && u(a[d]) !== u(b[d])) && g++; return g + f } function w(b) { a.suppressDeprecationWarnings === !1 && "undefined" != typeof console && console.warn && console.warn("Deprecation warning: " + b) } function x(b, c) { var d = !0; return k(function () { if (null != a.deprecationHandler && a.deprecationHandler(null, b), d) { for (var e, f = [], g = 0; g < arguments.length; g++) { if (e = "", "object" == typeof arguments[g]) { e += "\n[" + g + "] "; for (var h in arguments[0]) e += h + ": " + arguments[0][h] + ", "; e = e.slice(0, -2) } else e = arguments[g]; f.push(e) } w(b + "\nArguments: " + Array.prototype.slice.call(f).join("") + "\n" + (new Error).stack), d = !1 } return c.apply(this, arguments) }, c) } function y(b, c) { null != a.deprecationHandler && a.deprecationHandler(b, c), xd[b] || (w(c), xd[b] = !0) } function z(a) { return a instanceof Function || "[object Function]" === Object.prototype.toString.call(a) } function A(a) { var b, c; for (c in a) b = a[c], z(b) ? this[c] = b : this["_" + c] = b; this._config = a, this._dayOfMonthOrdinalParseLenient = new RegExp((this._dayOfMonthOrdinalParse.source || this._ordinalParse.source) + "|" + /\d{1,2}/.source) } function B(a, b) { var c, e = k({}, a); for (c in b) j(b, c) && (d(a[c]) && d(b[c]) ? (e[c] = {}, k(e[c], a[c]), k(e[c], b[c])) : null != b[c] ? e[c] = b[c] : delete e[c]); for (c in a) j(a, c) && !j(b, c) && d(a[c]) && (e[c] = k({}, e[c])); return e } function C(a) { null != a && this.set(a) } function D(a, b, c) { var d = this._calendar[a] || this._calendar.sameElse; return z(d) ? d.call(b, c) : d } function E(a) { var b = this._longDateFormat[a], c = this._longDateFormat[a.toUpperCase()]; return b || !c ? b : (this._longDateFormat[a] = c.replace(/MMMM|MM|DD|dddd/g, function (a) { return a.slice(1) }), this._longDateFormat[a]) } function F() { return this._invalidDate } function G(a) { return this._ordinal.replace("%d", a) } function H(a, b, c, d) { var e = this._relativeTime[c]; return z(e) ? e(a, b, c, d) : e.replace(/%d/i, a) } function I(a, b) { var c = this._relativeTime[a > 0 ? "future" : "past"]; return z(c) ? c(b) : c.replace(/%s/i, b) } function J(a, b) { var c = a.toLowerCase(); Hd[c] = Hd[c + "s"] = Hd[b] = a } function K(a) { return "string" == typeof a ? Hd[a] || Hd[a.toLowerCase()] : void 0 } function L(a) { var b, c, d = {}; for (c in a) j(a, c) && (b = K(c), b && (d[b] = a[c])); return d } function M(a, b) { Id[a] = b } function N(a) { var b = []; for (var c in a) b.push({ unit: c, priority: Id[c] }); return b.sort(function (a, b) { return a.priority - b.priority }), b } function O(b, c) { return function (d) { return null != d ? (Q(this, b, d), a.updateOffset(this, c), this) : P(this, b) } } function P(a, b) { return a.isValid() ? a._d["get" + (a._isUTC ? "UTC" : "") + b]() : NaN } function Q(a, b, c) { a.isValid() && a._d["set" + (a._isUTC ? "UTC" : "") + b](c) } function R(a) { return a = K(a), z(this[a]) ? this[a]() : this } function S(a, b) { if ("object" == typeof a) { a = L(a); for (var c = N(a), d = 0; d < c.length; d++)this[c[d].unit](a[c[d].unit]) } else if (a = K(a), z(this[a])) return this[a](b); return this } function T(a, b, c) { var d = "" + Math.abs(a), e = b - d.length, f = a >= 0; return (f ? c ? "+" : "" : "-") + Math.pow(10, Math.max(0, e)).toString().substr(1) + d } function U(a, b, c, d) { var e = d; "string" == typeof d && (e = function () { return this[d]() }), a && (Md[a] = e), b && (Md[b[0]] = function () { return T(e.apply(this, arguments), b[1], b[2]) }), c && (Md[c] = function () { return this.localeData().ordinal(e.apply(this, arguments), a) }) } function V(a) { return a.match(/\[[\s\S]/) ? a.replace(/^\[|\]$/g, "") : a.replace(/\\/g, "") } function W(a) { var b, c, d = a.match(Jd); for (b = 0, c = d.length; b < c; b++)Md[d[b]] ? d[b] = Md[d[b]] : d[b] = V(d[b]); return function (b) { var e, f = ""; for (e = 0; e < c; e++)f += z(d[e]) ? d[e].call(b, a) : d[e]; return f } } function X(a, b) { return a.isValid() ? (b = Y(b, a.localeData()), Ld[b] = Ld[b] || W(b), Ld[b](a)) : a.localeData().invalidDate() } function Y(a, b) { function c(a) { return b.longDateFormat(a) || a } var d = 5; for (Kd.lastIndex = 0; d >= 0 && Kd.test(a);)a = a.replace(Kd, c), Kd.lastIndex = 0, d -= 1; return a } function Z(a, b, c) { ce[a] = z(b) ? b : function (a, d) { return a && c ? c : b } } function $(a, b) { return j(ce, a) ? ce[a](b._strict, b._locale) : new RegExp(_(a)) } function _(a) { return aa(a.replace("\\", "").replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g, function (a, b, c, d, e) { return b || c || d || e })) } function aa(a) { return a.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&") } function ba(a, b) { var c, d = b; for ("string" == typeof a && (a = [a]), g(b) && (d = function (a, c) { c[b] = u(a) }), c = 0; c < a.length; c++)de[a[c]] = d } function ca(a, b) { ba(a, function (a, c, d, e) { d._w = d._w || {}, b(a, d._w, d, e) }) } function da(a, b, c) { null != b && j(de, a) && de[a](b, c._a, c, a) } function ea(a, b) { return new Date(Date.UTC(a, b + 1, 0)).getUTCDate() } function fa(a, b) { return a ? c(this._months) ? this._months[a.month()] : this._months[(this._months.isFormat || oe).test(b) ? "format" : "standalone"][a.month()] : c(this._months) ? this._months : this._months.standalone } function ga(a, b) { return a ? c(this._monthsShort) ? this._monthsShort[a.month()] : this._monthsShort[oe.test(b) ? "format" : "standalone"][a.month()] : c(this._monthsShort) ? this._monthsShort : this._monthsShort.standalone } function ha(a, b, c) { var d, e, f, g = a.toLocaleLowerCase(); if (!this._monthsParse) for (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = [], d = 0; d < 12; ++d)f = l([2e3, d]), this._shortMonthsParse[d] = this.monthsShort(f, "").toLocaleLowerCase(), this._longMonthsParse[d] = this.months(f, "").toLocaleLowerCase(); return c ? "MMM" === b ? (e = ne.call(this._shortMonthsParse, g), e !== -1 ? e : null) : (e = ne.call(this._longMonthsParse, g), e !== -1 ? e : null) : "MMM" === b ? (e = ne.call(this._shortMonthsParse, g), e !== -1 ? e : (e = ne.call(this._longMonthsParse, g), e !== -1 ? e : null)) : (e = ne.call(this._longMonthsParse, g), e !== -1 ? e : (e = ne.call(this._shortMonthsParse, g), e !== -1 ? e : null)) } function ia(a, b, c) { var d, e, f; if (this._monthsParseExact) return ha.call(this, a, b, c); for (this._monthsParse || (this._monthsParse = [], this._longMonthsParse = [], this._shortMonthsParse = []), d = 0; d < 12; d++) { if (e = l([2e3, d]), c && !this._longMonthsParse[d] && (this._longMonthsParse[d] = new RegExp("^" + this.months(e, "").replace(".", "") + "$", "i"), this._shortMonthsParse[d] = new RegExp("^" + this.monthsShort(e, "").replace(".", "") + "$", "i")), c || this._monthsParse[d] || (f = "^" + this.months(e, "") + "|^" + this.monthsShort(e, ""), this._monthsParse[d] = new RegExp(f.replace(".", ""), "i")), c && "MMMM" === b && this._longMonthsParse[d].test(a)) return d; if (c && "MMM" === b && this._shortMonthsParse[d].test(a)) return d; if (!c && this._monthsParse[d].test(a)) return d } } function ja(a, b) { var c; if (!a.isValid()) return a; if ("string" == typeof b) if (/^\d+$/.test(b)) b = u(b); else if (b = a.localeData().monthsParse(b), !g(b)) return a; return c = Math.min(a.date(), ea(a.year(), b)), a._d["set" + (a._isUTC ? "UTC" : "") + "Month"](b, c), a } function ka(b) { return null != b ? (ja(this, b), a.updateOffset(this, !0), this) : P(this, "Month") } function la() { return ea(this.year(), this.month()) } function ma(a) { return this._monthsParseExact ? (j(this, "_monthsRegex") || oa.call(this), a ? this._monthsShortStrictRegex : this._monthsShortRegex) : (j(this, "_monthsShortRegex") || (this._monthsShortRegex = re), this._monthsShortStrictRegex && a ? this._monthsShortStrictRegex : this._monthsShortRegex) } function na(a) { return this._monthsParseExact ? (j(this, "_monthsRegex") || oa.call(this), a ? this._monthsStrictRegex : this._monthsRegex) : (j(this, "_monthsRegex") || (this._monthsRegex = se), this._monthsStrictRegex && a ? this._monthsStrictRegex : this._monthsRegex) } function oa() { function a(a, b) { return b.length - a.length } var b, c, d = [], e = [], f = []; for (b = 0; b < 12; b++)c = l([2e3, b]), d.push(this.monthsShort(c, "")), e.push(this.months(c, "")), f.push(this.months(c, "")), f.push(this.monthsShort(c, "")); for (d.sort(a), e.sort(a), f.sort(a), b = 0; b < 12; b++)d[b] = aa(d[b]), e[b] = aa(e[b]); for (b = 0; b < 24; b++)f[b] = aa(f[b]); this._monthsRegex = new RegExp("^(" + f.join("|") + ")", "i"), this._monthsShortRegex = this._monthsRegex, this._monthsStrictRegex = new RegExp("^(" + e.join("|") + ")", "i"), this._monthsShortStrictRegex = new RegExp("^(" + d.join("|") + ")", "i") } function pa(a) { return qa(a) ? 366 : 365 } function qa(a) { return a % 4 === 0 && a % 100 !== 0 || a % 400 === 0 } function ra() { return qa(this.year()) } function sa(a, b, c, d, e, f, g) { var h = new Date(a, b, c, d, e, f, g); return a < 100 && a >= 0 && isFinite(h.getFullYear()) && h.setFullYear(a), h } function ta(a) { var b = new Date(Date.UTC.apply(null, arguments)); return a < 100 && a >= 0 && isFinite(b.getUTCFullYear()) && b.setUTCFullYear(a), b } function ua(a, b, c) { var d = 7 + b - c, e = (7 + ta(a, 0, d).getUTCDay() - b) % 7; return -e + d - 1 } function va(a, b, c, d, e) { var f, g, h = (7 + c - d) % 7, i = ua(a, d, e), j = 1 + 7 * (b - 1) + h + i; return j <= 0 ? (f = a - 1, g = pa(f) + j) : j > pa(a) ? (f = a + 1, g = j - pa(a)) : (f = a, g = j), { year: f, dayOfYear: g } } function wa(a, b, c) { var d, e, f = ua(a.year(), b, c), g = Math.floor((a.dayOfYear() - f - 1) / 7) + 1; return g < 1 ? (e = a.year() - 1, d = g + xa(e, b, c)) : g > xa(a.year(), b, c) ? (d = g - xa(a.year(), b, c), e = a.year() + 1) : (e = a.year(), d = g), { week: d, year: e } } function xa(a, b, c) { var d = ua(a, b, c), e = ua(a + 1, b, c); return (pa(a) - d + e) / 7 } function ya(a) { return wa(a, this._week.dow, this._week.doy).week } function za() { return this._week.dow } function Aa() { return this._week.doy } function Ba(a) { var b = this.localeData().week(this); return null == a ? b : this.add(7 * (a - b), "d") } function Ca(a) { var b = wa(this, 1, 4).week; return null == a ? b : this.add(7 * (a - b), "d") } function Da(a, b) { return "string" != typeof a ? a : isNaN(a) ? (a = b.weekdaysParse(a), "number" == typeof a ? a : null) : parseInt(a, 10) } function Ea(a, b) { return "string" == typeof a ? b.weekdaysParse(a) % 7 || 7 : isNaN(a) ? null : a } function Fa(a, b) { return a ? c(this._weekdays) ? this._weekdays[a.day()] : this._weekdays[this._weekdays.isFormat.test(b) ? "format" : "standalone"][a.day()] : c(this._weekdays) ? this._weekdays : this._weekdays.standalone } function Ga(a) { return a ? this._weekdaysShort[a.day()] : this._weekdaysShort } function Ha(a) { return a ? this._weekdaysMin[a.day()] : this._weekdaysMin } function Ia(a, b, c) { var d, e, f, g = a.toLocaleLowerCase(); if (!this._weekdaysParse) for (this._weekdaysParse = [], this._shortWeekdaysParse = [], this._minWeekdaysParse = [], d = 0; d < 7; ++d)f = l([2e3, 1]).day(d), this._minWeekdaysParse[d] = this.weekdaysMin(f, "").toLocaleLowerCase(), this._shortWeekdaysParse[d] = this.weekdaysShort(f, "").toLocaleLowerCase(), this._weekdaysParse[d] = this.weekdays(f, "").toLocaleLowerCase(); return c ? "dddd" === b ? (e = ne.call(this._weekdaysParse, g), e !== -1 ? e : null) : "ddd" === b ? (e = ne.call(this._shortWeekdaysParse, g), e !== -1 ? e : null) : (e = ne.call(this._minWeekdaysParse, g), e !== -1 ? e : null) : "dddd" === b ? (e = ne.call(this._weekdaysParse, g), e !== -1 ? e : (e = ne.call(this._shortWeekdaysParse, g), e !== -1 ? e : (e = ne.call(this._minWeekdaysParse, g), e !== -1 ? e : null))) : "ddd" === b ? (e = ne.call(this._shortWeekdaysParse, g), e !== -1 ? e : (e = ne.call(this._weekdaysParse, g), e !== -1 ? e : (e = ne.call(this._minWeekdaysParse, g), e !== -1 ? e : null))) : (e = ne.call(this._minWeekdaysParse, g), e !== -1 ? e : (e = ne.call(this._weekdaysParse, g), e !== -1 ? e : (e = ne.call(this._shortWeekdaysParse, g), e !== -1 ? e : null))) } function Ja(a, b, c) { var d, e, f; if (this._weekdaysParseExact) return Ia.call(this, a, b, c); for (this._weekdaysParse || (this._weekdaysParse = [], this._minWeekdaysParse = [], this._shortWeekdaysParse = [], this._fullWeekdaysParse = []), d = 0; d < 7; d++) { if (e = l([2e3, 1]).day(d), c && !this._fullWeekdaysParse[d] && (this._fullWeekdaysParse[d] = new RegExp("^" + this.weekdays(e, "").replace(".", ".?") + "$", "i"), this._shortWeekdaysParse[d] = new RegExp("^" + this.weekdaysShort(e, "").replace(".", ".?") + "$", "i"), this._minWeekdaysParse[d] = new RegExp("^" + this.weekdaysMin(e, "").replace(".", ".?") + "$", "i")), this._weekdaysParse[d] || (f = "^" + this.weekdays(e, "") + "|^" + this.weekdaysShort(e, "") + "|^" + this.weekdaysMin(e, ""), this._weekdaysParse[d] = new RegExp(f.replace(".", ""), "i")), c && "dddd" === b && this._fullWeekdaysParse[d].test(a)) return d; if (c && "ddd" === b && this._shortWeekdaysParse[d].test(a)) return d; if (c && "dd" === b && this._minWeekdaysParse[d].test(a)) return d; if (!c && this._weekdaysParse[d].test(a)) return d } } function Ka(a) { if (!this.isValid()) return null != a ? this : NaN; var b = this._isUTC ? this._d.getUTCDay() : this._d.getDay(); return null != a ? (a = Da(a, this.localeData()), this.add(a - b, "d")) : b } function La(a) { if (!this.isValid()) return null != a ? this : NaN; var b = (this.day() + 7 - this.localeData()._week.dow) % 7; return null == a ? b : this.add(a - b, "d") } function Ma(a) { if (!this.isValid()) return null != a ? this : NaN; if (null != a) { var b = Ea(a, this.localeData()); return this.day(this.day() % 7 ? b : b - 7) } return this.day() || 7 } function Na(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysStrictRegex : this._weekdaysRegex) : (j(this, "_weekdaysRegex") || (this._weekdaysRegex = ye), this._weekdaysStrictRegex && a ? this._weekdaysStrictRegex : this._weekdaysRegex) } function Oa(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex) : (j(this, "_weekdaysShortRegex") || (this._weekdaysShortRegex = ze), this._weekdaysShortStrictRegex && a ? this._weekdaysShortStrictRegex : this._weekdaysShortRegex) } function Pa(a) { return this._weekdaysParseExact ? (j(this, "_weekdaysRegex") || Qa.call(this), a ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex) : (j(this, "_weekdaysMinRegex") || (this._weekdaysMinRegex = Ae), this._weekdaysMinStrictRegex && a ? this._weekdaysMinStrictRegex : this._weekdaysMinRegex) } function Qa() { function a(a, b) { return b.length - a.length } var b, c, d, e, f, g = [], h = [], i = [], j = []; for (b = 0; b < 7; b++)c = l([2e3, 1]).day(b), d = this.weekdaysMin(c, ""), e = this.weekdaysShort(c, ""), f = this.weekdays(c, ""), g.push(d), h.push(e), i.push(f), j.push(d), j.push(e), j.push(f); for (g.sort(a), h.sort(a), i.sort(a), j.sort(a), b = 0; b < 7; b++)h[b] = aa(h[b]), i[b] = aa(i[b]), j[b] = aa(j[b]); this._weekdaysRegex = new RegExp("^(" + j.join("|") + ")", "i"), this._weekdaysShortRegex = this._weekdaysRegex, this._weekdaysMinRegex = this._weekdaysRegex, this._weekdaysStrictRegex = new RegExp("^(" + i.join("|") + ")", "i"), this._weekdaysShortStrictRegex = new RegExp("^(" + h.join("|") + ")", "i"), this._weekdaysMinStrictRegex = new RegExp("^(" + g.join("|") + ")", "i") } function Ra() { return this.hours() % 12 || 12 } function Sa() { return this.hours() || 24 } function Ta(a, b) { U(a, 0, 0, function () { return this.localeData().meridiem(this.hours(), this.minutes(), b) }) } function Ua(a, b) { return b._meridiemParse } function Va(a) { return "p" === (a + "").toLowerCase().charAt(0) } function Wa(a, b, c) { return a > 11 ? c ? "pm" : "PM" : c ? "am" : "AM" } function Xa(a) { return a ? a.toLowerCase().replace("_", "-") : a } function Ya(a) { for (var b, c, d, e, f = 0; f < a.length;) { for (e = Xa(a[f]).split("-"), b = e.length, c = Xa(a[f + 1]), c = c ? c.split("-") : null; b > 0;) { if (d = Za(e.slice(0, b).join("-"))) return d; if (c && c.length >= b && v(e, c, !0) >= b - 1) break; b-- } f++ } return null } function Za(a) { var b = null; if (!Fe[a] && "undefined" != typeof module && module && module.exports) try { b = Be._abbr, require("./locale/" + a), $a(b) } catch (a) { } return Fe[a] } function $a(a, b) { var c; return a && (c = f(b) ? bb(a) : _a(a, b), c && (Be = c)), Be._abbr } function _a(a, b) { if (null !== b) { var c = Ee; if (b.abbr = a, null != Fe[a]) y("defineLocaleOverride", "use moment.updateLocale(localeName, config) to change an existing locale. moment.defineLocale(localeName, config) should only be used for creating a new locale See http://momentjs.com/guides/#/warnings/define-locale/ for more info."), c = Fe[a]._config; else if (null != b.parentLocale) { if (null == Fe[b.parentLocale]) return Ge[b.parentLocale] || (Ge[b.parentLocale] = []), Ge[b.parentLocale].push({ name: a, config: b }), null; c = Fe[b.parentLocale]._config } return Fe[a] = new C(B(c, b)), Ge[a] && Ge[a].forEach(function (a) { _a(a.name, a.config) }), $a(a), Fe[a] } return delete Fe[a], null } function ab(a, b) { if (null != b) { var c, d = Ee; null != Fe[a] && (d = Fe[a]._config), b = B(d, b), c = new C(b), c.parentLocale = Fe[a], Fe[a] = c, $a(a) } else null != Fe[a] && (null != Fe[a].parentLocale ? Fe[a] = Fe[a].parentLocale : null != Fe[a] && delete Fe[a]); return Fe[a] } function bb(a) { var b; if (a && a._locale && a._locale._abbr && (a = a._locale._abbr), !a) return Be; if (!c(a)) { if (b = Za(a)) return b; a = [a] } return Ya(a) } function cb() { return Ad(Fe) } function db(a) { var b, c = a._a; return c && n(a).overflow === -2 && (b = c[fe] < 0 || c[fe] > 11 ? fe : c[ge] < 1 || c[ge] > ea(c[ee], c[fe]) ? ge : c[he] < 0 || c[he] > 24 || 24 === c[he] && (0 !== c[ie] || 0 !== c[je] || 0 !== c[ke]) ? he : c[ie] < 0 || c[ie] > 59 ? ie : c[je] < 0 || c[je] > 59 ? je : c[ke] < 0 || c[ke] > 999 ? ke : -1, n(a)._overflowDayOfYear && (b < ee || b > ge) && (b = ge), n(a)._overflowWeeks && b === -1 && (b = le), n(a)._overflowWeekday && b === -1 && (b = me), n(a).overflow = b), a } function eb(a) { var b, c, d, e, f, g, h = a._i, i = He.exec(h) || Ie.exec(h); if (i) { for (n(a).iso = !0, b = 0, c = Ke.length; b < c; b++)if (Ke[b][1].exec(i[1])) { e = Ke[b][0], d = Ke[b][2] !== !1; break } if (null == e) return void (a._isValid = !1); if (i[3]) { for (b = 0, c = Le.length; b < c; b++)if (Le[b][1].exec(i[3])) { f = (i[2] || " ") + Le[b][0]; break } if (null == f) return void (a._isValid = !1) } if (!d && null != f) return void (a._isValid = !1); if (i[4]) { if (!Je.exec(i[4])) return void (a._isValid = !1); g = "Z" } a._f = e + (f || "") + (g || ""), lb(a) } else a._isValid = !1 } function fb(a) { var b, c, d, e, f, g, h, i, j = { " GMT": " +0000", " EDT": " -0400", " EST": " -0500", " CDT": " -0500", " CST": " -0600", " MDT": " -0600", " MST": " -0700", " PDT": " -0700", " PST": " -0800" }, k = "YXWVUTSRQPONZABCDEFGHIKLM"; if (b = a._i.replace(/\([^\)]*\)|[\n\t]/g, " ").replace(/(\s\s+)/g, " ").replace(/^\s|\s$/g, ""), c = Ne.exec(b)) { if (d = c[1] ? "ddd" + (5 === c[1].length ? ", " : " ") : "", e = "D MMM " + (c[2].length > 10 ? "YYYY " : "YY "), f = "HH:mm" + (c[4] ? ":ss" : ""), c[1]) { var l = new Date(c[2]), m = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][l.getDay()]; if (c[1].substr(0, 3) !== m) return n(a).weekdayMismatch = !0, void (a._isValid = !1) } switch (c[5].length) { case 2: 0 === i ? h = " +0000" : (i = k.indexOf(c[5][1].toUpperCase()) - 12, h = (i < 0 ? " -" : " +") + ("" + i).replace(/^-?/, "0").match(/..$/)[0] + "00"); break; case 4: h = j[c[5]]; break; default: h = j[" GMT"] }c[5] = h, a._i = c.splice(1).join(""), g = " ZZ", a._f = d + e + f + g, lb(a), n(a).rfc2822 = !0 } else a._isValid = !1 } function gb(b) { var c = Me.exec(b._i); return null !== c ? void (b._d = new Date(+c[1])) : (eb(b), void (b._isValid === !1 && (delete b._isValid, fb(b), b._isValid === !1 && (delete b._isValid, a.createFromInputFallback(b))))) } function hb(a, b, c) { return null != a ? a : null != b ? b : c } function ib(b) { var c = new Date(a.now()); return b._useUTC ? [c.getUTCFullYear(), c.getUTCMonth(), c.getUTCDate()] : [c.getFullYear(), c.getMonth(), c.getDate()] } function jb(a) { var b, c, d, e, f = []; if (!a._d) { for (d = ib(a), a._w && null == a._a[ge] && null == a._a[fe] && kb(a), null != a._dayOfYear && (e = hb(a._a[ee], d[ee]), (a._dayOfYear > pa(e) || 0 === a._dayOfYear) && (n(a)._overflowDayOfYear = !0), c = ta(e, 0, a._dayOfYear), a._a[fe] = c.getUTCMonth(), a._a[ge] = c.getUTCDate()), b = 0; b < 3 && null == a._a[b]; ++b)a._a[b] = f[b] = d[b]; for (; b < 7; b++)a._a[b] = f[b] = null == a._a[b] ? 2 === b ? 1 : 0 : a._a[b]; 24 === a._a[he] && 0 === a._a[ie] && 0 === a._a[je] && 0 === a._a[ke] && (a._nextDay = !0, a._a[he] = 0), a._d = (a._useUTC ? ta : sa).apply(null, f), null != a._tzm && a._d.setUTCMinutes(a._d.getUTCMinutes() - a._tzm), a._nextDay && (a._a[he] = 24) } } function kb(a) { var b, c, d, e, f, g, h, i; if (b = a._w, null != b.GG || null != b.W || null != b.E) f = 1, g = 4, c = hb(b.GG, a._a[ee], wa(tb(), 1, 4).year), d = hb(b.W, 1), e = hb(b.E, 1), (e < 1 || e > 7) && (i = !0); else { f = a._locale._week.dow, g = a._locale._week.doy; var j = wa(tb(), f, g); c = hb(b.gg, a._a[ee], j.year), d = hb(b.w, j.week), null != b.d ? (e = b.d, (e < 0 || e > 6) && (i = !0)) : null != b.e ? (e = b.e + f, (b.e < 0 || b.e > 6) && (i = !0)) : e = f } d < 1 || d > xa(c, f, g) ? n(a)._overflowWeeks = !0 : null != i ? n(a)._overflowWeekday = !0 : (h = va(c, d, e, f, g), a._a[ee] = h.year, a._dayOfYear = h.dayOfYear) } function lb(b) { if (b._f === a.ISO_8601) return void eb(b); if (b._f === a.RFC_2822) return void fb(b); b._a = [], n(b).empty = !0; var c, d, e, f, g, h = "" + b._i, i = h.length, j = 0; for (e = Y(b._f, b._locale).match(Jd) || [], c = 0; c < e.length; c++)f = e[c], d = (h.match($(f, b)) || [])[0], d && (g = h.substr(0, h.indexOf(d)), g.length > 0 && n(b).unusedInput.push(g), h = h.slice(h.indexOf(d) + d.length), j += d.length), Md[f] ? (d ? n(b).empty = !1 : n(b).unusedTokens.push(f), da(f, d, b)) : b._strict && !d && n(b).unusedTokens.push(f); n(b).charsLeftOver = i - j, h.length > 0 && n(b).unusedInput.push(h), b._a[he] <= 12 && n(b).bigHour === !0 && b._a[he] > 0 && (n(b).bigHour = void 0), n(b).parsedDateParts = b._a.slice(0), n(b).meridiem = b._meridiem, b._a[he] = mb(b._locale, b._a[he], b._meridiem), jb(b), db(b) } function mb(a, b, c) { var d; return null == c ? b : null != a.meridiemHour ? a.meridiemHour(b, c) : null != a.isPM ? (d = a.isPM(c), d && b < 12 && (b += 12), d || 12 !== b || (b = 0), b) : b } function nb(a) { var b, c, d, e, f; if (0 === a._f.length) return n(a).invalidFormat = !0, void (a._d = new Date(NaN)); for (e = 0; e < a._f.length; e++)f = 0, b = q({}, a), null != a._useUTC && (b._useUTC = a._useUTC), b._f = a._f[e], lb(b), o(b) && (f += n(b).charsLeftOver, f += 10 * n(b).unusedTokens.length, n(b).score = f, (null == d || f < d) && (d = f, c = b)); k(a, c || b) } function ob(a) { if (!a._d) { var b = L(a._i); a._a = i([b.year, b.month, b.day || b.date, b.hour, b.minute, b.second, b.millisecond], function (a) { return a && parseInt(a, 10) }), jb(a) } } function pb(a) { var b = new r(db(qb(a))); return b._nextDay && (b.add(1, "d"), b._nextDay = void 0), b } function qb(a) { var b = a._i, d = a._f; return a._locale = a._locale || bb(a._l), null === b || void 0 === d && "" === b ? p({ nullInput: !0 }) : ("string" == typeof b && (a._i = b = a._locale.preparse(b)), s(b) ? new r(db(b)) : (h(b) ? a._d = b : c(d) ? nb(a) : d ? lb(a) : rb(a), o(a) || (a._d = null), a)) } function rb(b) { var e = b._i; f(e) ? b._d = new Date(a.now()) : h(e) ? b._d = new Date(e.valueOf()) : "string" == typeof e ? gb(b) : c(e) ? (b._a = i(e.slice(0), function (a) { return parseInt(a, 10) }), jb(b)) : d(e) ? ob(b) : g(e) ? b._d = new Date(e) : a.createFromInputFallback(b) } function sb(a, b, f, g, h) { var i = {}; return f !== !0 && f !== !1 || (g = f, f = void 0), (d(a) && e(a) || c(a) && 0 === a.length) && (a = void 0), i._isAMomentObject = !0, i._useUTC = i._isUTC = h, i._l = f, i._i = a, i._f = b, i._strict = g, pb(i) } function tb(a, b, c, d) { return sb(a, b, c, d, !1) } function ub(a, b) { var d, e; if (1 === b.length && c(b[0]) && (b = b[0]), !b.length) return tb(); for (d = b[0], e = 1; e < b.length; ++e)b[e].isValid() && !b[e][a](d) || (d = b[e]); return d } function vb() { var a = [].slice.call(arguments, 0); return ub("isBefore", a) } function wb() { var a = [].slice.call(arguments, 0); return ub("isAfter", a) } function xb(a) { for (var b in a) if (Re.indexOf(b) === -1 || null != a[b] && isNaN(a[b])) return !1; for (var c = !1, d = 0; d < Re.length; ++d)if (a[Re[d]]) { if (c) return !1; parseFloat(a[Re[d]]) !== u(a[Re[d]]) && (c = !0) } return !0 } function yb() { return this._isValid } function zb() { return Sb(NaN) } function Ab(a) { var b = L(a), c = b.year || 0, d = b.quarter || 0, e = b.month || 0, f = b.week || 0, g = b.day || 0, h = b.hour || 0, i = b.minute || 0, j = b.second || 0, k = b.millisecond || 0; this._isValid = xb(b), this._milliseconds = +k + 1e3 * j + 6e4 * i + 1e3 * h * 60 * 60, this._days = +g + 7 * f, this._months = +e + 3 * d + 12 * c, this._data = {}, this._locale = bb(), this._bubble() } function Bb(a) { return a instanceof Ab } function Cb(a) { return a < 0 ? Math.round(-1 * a) * -1 : Math.round(a) } function Db(a, b) { U(a, 0, 0, function () { var a = this.utcOffset(), c = "+"; return a < 0 && (a = -a, c = "-"), c + T(~~(a / 60), 2) + b + T(~~a % 60, 2) }) } function Eb(a, b) { var c = (b || "").match(a); if (null === c) return null; var d = c[c.length - 1] || [], e = (d + "").match(Se) || ["-", 0, 0], f = +(60 * e[1]) + u(e[2]); return 0 === f ? 0 : "+" === e[0] ? f : -f } function Fb(b, c) { var d, e; return c._isUTC ? (d = c.clone(), e = (s(b) || h(b) ? b.valueOf() : tb(b).valueOf()) - d.valueOf(), d._d.setTime(d._d.valueOf() + e), a.updateOffset(d, !1), d) : tb(b).local() } function Gb(a) { return 15 * -Math.round(a._d.getTimezoneOffset() / 15) } function Hb(b, c, d) { var e, f = this._offset || 0; if (!this.isValid()) return null != b ? this : NaN; if (null != b) { if ("string" == typeof b) { if (b = Eb(_d, b), null === b) return this } else Math.abs(b) < 16 && !d && (b = 60 * b); return !this._isUTC && c && (e = Gb(this)), this._offset = b, this._isUTC = !0, null != e && this.add(e, "m"), f !== b && (!c || this._changeInProgress ? Xb(this, Sb(b - f, "m"), 1, !1) : this._changeInProgress || (this._changeInProgress = !0, a.updateOffset(this, !0), this._changeInProgress = null)), this } return this._isUTC ? f : Gb(this) } function Ib(a, b) { return null != a ? ("string" != typeof a && (a = -a), this.utcOffset(a, b), this) : -this.utcOffset() } function Jb(a) { return this.utcOffset(0, a) } function Kb(a) { return this._isUTC && (this.utcOffset(0, a), this._isUTC = !1, a && this.subtract(Gb(this), "m")), this } function Lb() { if (null != this._tzm) this.utcOffset(this._tzm, !1, !0); else if ("string" == typeof this._i) { var a = Eb($d, this._i); null != a ? this.utcOffset(a) : this.utcOffset(0, !0) } return this } function Mb(a) { return !!this.isValid() && (a = a ? tb(a).utcOffset() : 0, (this.utcOffset() - a) % 60 === 0) } function Nb() { return this.utcOffset() > this.clone().month(0).utcOffset() || this.utcOffset() > this.clone().month(5).utcOffset() } function Ob() { if (!f(this._isDSTShifted)) return this._isDSTShifted; var a = {}; if (q(a, this), a = qb(a), a._a) { var b = a._isUTC ? l(a._a) : tb(a._a); this._isDSTShifted = this.isValid() && v(a._a, b.toArray()) > 0 } else this._isDSTShifted = !1; return this._isDSTShifted } function Pb() { return !!this.isValid() && !this._isUTC } function Qb() { return !!this.isValid() && this._isUTC } function Rb() { return !!this.isValid() && (this._isUTC && 0 === this._offset) } function Sb(a, b) { var c, d, e, f = a, h = null; return Bb(a) ? f = { ms: a._milliseconds, d: a._days, M: a._months } : g(a) ? (f = {}, b ? f[b] = a : f.milliseconds = a) : (h = Te.exec(a)) ? (c = "-" === h[1] ? -1 : 1, f = { y: 0, d: u(h[ge]) * c, h: u(h[he]) * c, m: u(h[ie]) * c, s: u(h[je]) * c, ms: u(Cb(1e3 * h[ke])) * c }) : (h = Ue.exec(a)) ? (c = "-" === h[1] ? -1 : 1, f = { y: Tb(h[2], c), M: Tb(h[3], c), w: Tb(h[4], c), d: Tb(h[5], c), h: Tb(h[6], c), m: Tb(h[7], c), s: Tb(h[8], c) }) : null == f ? f = {} : "object" == typeof f && ("from" in f || "to" in f) && (e = Vb(tb(f.from), tb(f.to)), f = {}, f.ms = e.milliseconds, f.M = e.months), d = new Ab(f), Bb(a) && j(a, "_locale") && (d._locale = a._locale), d } function Tb(a, b) { var c = a && parseFloat(a.replace(",", ".")); return (isNaN(c) ? 0 : c) * b } function Ub(a, b) { var c = { milliseconds: 0, months: 0 }; return c.months = b.month() - a.month() + 12 * (b.year() - a.year()), a.clone().add(c.months, "M").isAfter(b) && --c.months, c.milliseconds = +b - +a.clone().add(c.months, "M"), c } function Vb(a, b) { var c; return a.isValid() && b.isValid() ? (b = Fb(b, a), a.isBefore(b) ? c = Ub(a, b) : (c = Ub(b, a), c.milliseconds = -c.milliseconds, c.months = -c.months), c) : { milliseconds: 0, months: 0 } } function Wb(a, b) { return function (c, d) { var e, f; return null === d || isNaN(+d) || (y(b, "moment()." + b + "(period, number) is deprecated. Please use moment()." + b + "(number, period). See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info."), f = c, c = d, d = f), c = "string" == typeof c ? +c : c, e = Sb(c, d), Xb(this, e, a), this } } function Xb(b, c, d, e) { var f = c._milliseconds, g = Cb(c._days), h = Cb(c._months); b.isValid() && (e = null == e || e, f && b._d.setTime(b._d.valueOf() + f * d), g && Q(b, "Date", P(b, "Date") + g * d), h && ja(b, P(b, "Month") + h * d), e && a.updateOffset(b, g || h)) } function Yb(a, b) { var c = a.diff(b, "days", !0); return c < -6 ? "sameElse" : c < -1 ? "lastWeek" : c < 0 ? "lastDay" : c < 1 ? "sameDay" : c < 2 ? "nextDay" : c < 7 ? "nextWeek" : "sameElse" } function Zb(b, c) { var d = b || tb(), e = Fb(d, this).startOf("day"), f = a.calendarFormat(this, e) || "sameElse", g = c && (z(c[f]) ? c[f].call(this, d) : c[f]); return this.format(g || this.localeData().calendar(f, this, tb(d))) } function $b() { return new r(this) } function _b(a, b) { var c = s(a) ? a : tb(a); return !(!this.isValid() || !c.isValid()) && (b = K(f(b) ? "millisecond" : b), "millisecond" === b ? this.valueOf() > c.valueOf() : c.valueOf() < this.clone().startOf(b).valueOf()) } function ac(a, b) { var c = s(a) ? a : tb(a); return !(!this.isValid() || !c.isValid()) && (b = K(f(b) ? "millisecond" : b), "millisecond" === b ? this.valueOf() < c.valueOf() : this.clone().endOf(b).valueOf() < c.valueOf()) } function bc(a, b, c, d) { return d = d || "()", ("(" === d[0] ? this.isAfter(a, c) : !this.isBefore(a, c)) && (")" === d[1] ? this.isBefore(b, c) : !this.isAfter(b, c)) } function cc(a, b) { var c, d = s(a) ? a : tb(a); return !(!this.isValid() || !d.isValid()) && (b = K(b || "millisecond"), "millisecond" === b ? this.valueOf() === d.valueOf() : (c = d.valueOf(), this.clone().startOf(b).valueOf() <= c && c <= this.clone().endOf(b).valueOf())) } function dc(a, b) { return this.isSame(a, b) || this.isAfter(a, b) } function ec(a, b) { return this.isSame(a, b) || this.isBefore(a, b) } function fc(a, b, c) { var d, e, f, g; return this.isValid() ? (d = Fb(a, this), d.isValid() ? (e = 6e4 * (d.utcOffset() - this.utcOffset()), b = K(b), "year" === b || "month" === b || "quarter" === b ? (g = gc(this, d), "quarter" === b ? g /= 3 : "year" === b && (g /= 12)) : (f = this - d, g = "second" === b ? f / 1e3 : "minute" === b ? f / 6e4 : "hour" === b ? f / 36e5 : "day" === b ? (f - e) / 864e5 : "week" === b ? (f - e) / 6048e5 : f), c ? g : t(g)) : NaN) : NaN } function gc(a, b) { var c, d, e = 12 * (b.year() - a.year()) + (b.month() - a.month()), f = a.clone().add(e, "months"); return b - f < 0 ? (c = a.clone().add(e - 1, "months"), d = (b - f) / (f - c)) : (c = a.clone().add(e + 1, "months"), d = (b - f) / (c - f)), -(e + d) || 0 } function hc() { return this.clone().locale("en").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ") } function ic() { if (!this.isValid()) return null; var a = this.clone().utc(); return a.year() < 0 || a.year() > 9999 ? X(a, "YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]") : z(Date.prototype.toISOString) ? this.toDate().toISOString() : X(a, "YYYY-MM-DD[T]HH:mm:ss.SSS[Z]") } function jc() { if (!this.isValid()) return "moment.invalid(/* " + this._i + " */)"; var a = "moment", b = ""; this.isLocal() || (a = 0 === this.utcOffset() ? "moment.utc" : "moment.parseZone", b = "Z"); var c = "[" + a + '("]', d = 0 <= this.year() && this.year() <= 9999 ? "YYYY" : "YYYYYY", e = "-MM-DD[T]HH:mm:ss.SSS", f = b + '[")]'; return this.format(c + d + e + f) } function kc(b) { b || (b = this.isUtc() ? a.defaultFormatUtc : a.defaultFormat); var c = X(this, b); return this.localeData().postformat(c) } function lc(a, b) { return this.isValid() && (s(a) && a.isValid() || tb(a).isValid()) ? Sb({ to: this, from: a }).locale(this.locale()).humanize(!b) : this.localeData().invalidDate() } function mc(a) { return this.from(tb(), a) } function nc(a, b) { return this.isValid() && (s(a) && a.isValid() || tb(a).isValid()) ? Sb({ from: this, to: a }).locale(this.locale()).humanize(!b) : this.localeData().invalidDate() } function oc(a) { return this.to(tb(), a) } function pc(a) { var b; return void 0 === a ? this._locale._abbr : (b = bb(a), null != b && (this._locale = b), this) } function qc() { return this._locale } function rc(a) { switch (a = K(a)) { case "year": this.month(0); case "quarter": case "month": this.date(1); case "week": case "isoWeek": case "day": case "date": this.hours(0); case "hour": this.minutes(0); case "minute": this.seconds(0); case "second": this.milliseconds(0) }return "week" === a && this.weekday(0), "isoWeek" === a && this.isoWeekday(1), "quarter" === a && this.month(3 * Math.floor(this.month() / 3)), this } function sc(a) { return a = K(a), void 0 === a || "millisecond" === a ? this : ("date" === a && (a = "day"), this.startOf(a).add(1, "isoWeek" === a ? "week" : a).subtract(1, "ms")) } function tc() { return this._d.valueOf() - 6e4 * (this._offset || 0) } function uc() { return Math.floor(this.valueOf() / 1e3) } function vc() { return new Date(this.valueOf()) } function wc() { var a = this; return [a.year(), a.month(), a.date(), a.hour(), a.minute(), a.second(), a.millisecond()] } function xc() { var a = this; return { years: a.year(), months: a.month(), date: a.date(), hours: a.hours(), minutes: a.minutes(), seconds: a.seconds(), milliseconds: a.milliseconds() } } function yc() { return this.isValid() ? this.toISOString() : null } function zc() { return o(this) } function Ac() {
		return k({}, n(this))
	} function Bc() { return n(this).overflow } function Cc() { return { input: this._i, format: this._f, locale: this._locale, isUTC: this._isUTC, strict: this._strict } } function Dc(a, b) { U(0, [a, a.length], 0, b) } function Ec(a) { return Ic.call(this, a, this.week(), this.weekday(), this.localeData()._week.dow, this.localeData()._week.doy) } function Fc(a) { return Ic.call(this, a, this.isoWeek(), this.isoWeekday(), 1, 4) } function Gc() { return xa(this.year(), 1, 4) } function Hc() { var a = this.localeData()._week; return xa(this.year(), a.dow, a.doy) } function Ic(a, b, c, d, e) { var f; return null == a ? wa(this, d, e).year : (f = xa(a, d, e), b > f && (b = f), Jc.call(this, a, b, c, d, e)) } function Jc(a, b, c, d, e) { var f = va(a, b, c, d, e), g = ta(f.year, 0, f.dayOfYear); return this.year(g.getUTCFullYear()), this.month(g.getUTCMonth()), this.date(g.getUTCDate()), this } function Kc(a) { return null == a ? Math.ceil((this.month() + 1) / 3) : this.month(3 * (a - 1) + this.month() % 3) } function Lc(a) { var b = Math.round((this.clone().startOf("day") - this.clone().startOf("year")) / 864e5) + 1; return null == a ? b : this.add(a - b, "d") } function Mc(a, b) { b[ke] = u(1e3 * ("0." + a)) } function Nc() { return this._isUTC ? "UTC" : "" } function Oc() { return this._isUTC ? "Coordinated Universal Time" : "" } function Pc(a) { return tb(1e3 * a) } function Qc() { return tb.apply(null, arguments).parseZone() } function Rc(a) { return a } function Sc(a, b, c, d) { var e = bb(), f = l().set(d, b); return e[c](f, a) } function Tc(a, b, c) { if (g(a) && (b = a, a = void 0), a = a || "", null != b) return Sc(a, b, c, "month"); var d, e = []; for (d = 0; d < 12; d++)e[d] = Sc(a, d, c, "month"); return e } function Uc(a, b, c, d) { "boolean" == typeof a ? (g(b) && (c = b, b = void 0), b = b || "") : (b = a, c = b, a = !1, g(b) && (c = b, b = void 0), b = b || ""); var e = bb(), f = a ? e._week.dow : 0; if (null != c) return Sc(b, (c + f) % 7, d, "day"); var h, i = []; for (h = 0; h < 7; h++)i[h] = Sc(b, (h + f) % 7, d, "day"); return i } function Vc(a, b) { return Tc(a, b, "months") } function Wc(a, b) { return Tc(a, b, "monthsShort") } function Xc(a, b, c) { return Uc(a, b, c, "weekdays") } function Yc(a, b, c) { return Uc(a, b, c, "weekdaysShort") } function Zc(a, b, c) { return Uc(a, b, c, "weekdaysMin") } function $c() { var a = this._data; return this._milliseconds = df(this._milliseconds), this._days = df(this._days), this._months = df(this._months), a.milliseconds = df(a.milliseconds), a.seconds = df(a.seconds), a.minutes = df(a.minutes), a.hours = df(a.hours), a.months = df(a.months), a.years = df(a.years), this } function _c(a, b, c, d) { var e = Sb(b, c); return a._milliseconds += d * e._milliseconds, a._days += d * e._days, a._months += d * e._months, a._bubble() } function ad(a, b) { return _c(this, a, b, 1) } function bd(a, b) { return _c(this, a, b, -1) } function cd(a) { return a < 0 ? Math.floor(a) : Math.ceil(a) } function dd() { var a, b, c, d, e, f = this._milliseconds, g = this._days, h = this._months, i = this._data; return f >= 0 && g >= 0 && h >= 0 || f <= 0 && g <= 0 && h <= 0 || (f += 864e5 * cd(fd(h) + g), g = 0, h = 0), i.milliseconds = f % 1e3, a = t(f / 1e3), i.seconds = a % 60, b = t(a / 60), i.minutes = b % 60, c = t(b / 60), i.hours = c % 24, g += t(c / 24), e = t(ed(g)), h += e, g -= cd(fd(e)), d = t(h / 12), h %= 12, i.days = g, i.months = h, i.years = d, this } function ed(a) { return 4800 * a / 146097 } function fd(a) { return 146097 * a / 4800 } function gd(a) { if (!this.isValid()) return NaN; var b, c, d = this._milliseconds; if (a = K(a), "month" === a || "year" === a) return b = this._days + d / 864e5, c = this._months + ed(b), "month" === a ? c : c / 12; switch (b = this._days + Math.round(fd(this._months)), a) { case "week": return b / 7 + d / 6048e5; case "day": return b + d / 864e5; case "hour": return 24 * b + d / 36e5; case "minute": return 1440 * b + d / 6e4; case "second": return 86400 * b + d / 1e3; case "millisecond": return Math.floor(864e5 * b) + d; default: throw new Error("Unknown unit " + a) } } function hd() { return this.isValid() ? this._milliseconds + 864e5 * this._days + this._months % 12 * 2592e6 + 31536e6 * u(this._months / 12) : NaN } function id(a) { return function () { return this.as(a) } } function jd(a) { return a = K(a), this.isValid() ? this[a + "s"]() : NaN } function kd(a) { return function () { return this.isValid() ? this._data[a] : NaN } } function ld() { return t(this.days() / 7) } function md(a, b, c, d, e) { return e.relativeTime(b || 1, !!c, a, d) } function nd(a, b, c) { var d = Sb(a).abs(), e = uf(d.as("s")), f = uf(d.as("m")), g = uf(d.as("h")), h = uf(d.as("d")), i = uf(d.as("M")), j = uf(d.as("y")), k = e <= vf.ss && ["s", e] || e < vf.s && ["ss", e] || f <= 1 && ["m"] || f < vf.m && ["mm", f] || g <= 1 && ["h"] || g < vf.h && ["hh", g] || h <= 1 && ["d"] || h < vf.d && ["dd", h] || i <= 1 && ["M"] || i < vf.M && ["MM", i] || j <= 1 && ["y"] || ["yy", j]; return k[2] = b, k[3] = +a > 0, k[4] = c, md.apply(null, k) } function od(a) { return void 0 === a ? uf : "function" == typeof a && (uf = a, !0) } function pd(a, b) { return void 0 !== vf[a] && (void 0 === b ? vf[a] : (vf[a] = b, "s" === a && (vf.ss = b - 1), !0)) } function qd(a) { if (!this.isValid()) return this.localeData().invalidDate(); var b = this.localeData(), c = nd(this, !a, b); return a && (c = b.pastFuture(+this, c)), b.postformat(c) } function rd() { if (!this.isValid()) return this.localeData().invalidDate(); var a, b, c, d = wf(this._milliseconds) / 1e3, e = wf(this._days), f = wf(this._months); a = t(d / 60), b = t(a / 60), d %= 60, a %= 60, c = t(f / 12), f %= 12; var g = c, h = f, i = e, j = b, k = a, l = d, m = this.asSeconds(); return m ? (m < 0 ? "-" : "") + "P" + (g ? g + "Y" : "") + (h ? h + "M" : "") + (i ? i + "D" : "") + (j || k || l ? "T" : "") + (j ? j + "H" : "") + (k ? k + "M" : "") + (l ? l + "S" : "") : "P0D" } var sd, td; td = Array.prototype.some ? Array.prototype.some : function (a) { for (var b = Object(this), c = b.length >>> 0, d = 0; d < c; d++)if (d in b && a.call(this, b[d], d, b)) return !0; return !1 }; var ud = td, vd = a.momentProperties = [], wd = !1, xd = {}; a.suppressDeprecationWarnings = !1, a.deprecationHandler = null; var yd; yd = Object.keys ? Object.keys : function (a) { var b, c = []; for (b in a) j(a, b) && c.push(b); return c }; var zd, Ad = yd, Bd = { sameDay: "[Today at] LT", nextDay: "[Tomorrow at] LT", nextWeek: "dddd [at] LT", lastDay: "[Yesterday at] LT", lastWeek: "[Last] dddd [at] LT", sameElse: "L" }, Cd = { LTS: "h:mm:ss A", LT: "h:mm A", L: "MM/DD/YYYY", LL: "MMMM D, YYYY", LLL: "MMMM D, YYYY h:mm A", LLLL: "dddd, MMMM D, YYYY h:mm A" }, Dd = "Invalid date", Ed = "%d", Fd = /\d{1,2}/, Gd = { future: "in %s", past: "%s ago", s: "a few seconds", ss: "%d seconds", m: "a minute", mm: "%d minutes", h: "an hour", hh: "%d hours", d: "a day", dd: "%d days", M: "a month", MM: "%d months", y: "a year", yy: "%d years" }, Hd = {}, Id = {}, Jd = /(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|YYYYYY|YYYYY|YYYY|YY|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g, Kd = /(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g, Ld = {}, Md = {}, Nd = /\d/, Od = /\d\d/, Pd = /\d{3}/, Qd = /\d{4}/, Rd = /[+-]?\d{6}/, Sd = /\d\d?/, Td = /\d\d\d\d?/, Ud = /\d\d\d\d\d\d?/, Vd = /\d{1,3}/, Wd = /\d{1,4}/, Xd = /[+-]?\d{1,6}/, Yd = /\d+/, Zd = /[+-]?\d+/, $d = /Z|[+-]\d\d:?\d\d/gi, _d = /Z|[+-]\d\d(?::?\d\d)?/gi, ae = /[+-]?\d+(\.\d{1,3})?/, be = /[0-9]*['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+|[\u0600-\u06FF\/]+(\s*?[\u0600-\u06FF]+){1,2}/i, ce = {}, de = {}, ee = 0, fe = 1, ge = 2, he = 3, ie = 4, je = 5, ke = 6, le = 7, me = 8; zd = Array.prototype.indexOf ? Array.prototype.indexOf : function (a) { var b; for (b = 0; b < this.length; ++b)if (this[b] === a) return b; return -1 }; var ne = zd; U("M", ["MM", 2], "Mo", function () { return this.month() + 1 }), U("MMM", 0, 0, function (a) { return this.localeData().monthsShort(this, a) }), U("MMMM", 0, 0, function (a) { return this.localeData().months(this, a) }), J("month", "M"), M("month", 8), Z("M", Sd), Z("MM", Sd, Od), Z("MMM", function (a, b) { return b.monthsShortRegex(a) }), Z("MMMM", function (a, b) { return b.monthsRegex(a) }), ba(["M", "MM"], function (a, b) { b[fe] = u(a) - 1 }), ba(["MMM", "MMMM"], function (a, b, c, d) { var e = c._locale.monthsParse(a, d, c._strict); null != e ? b[fe] = e : n(c).invalidMonth = a }); var oe = /D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/, pe = "January_February_March_April_May_June_July_August_September_October_November_December".split("_"), qe = "Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"), re = be, se = be; U("Y", 0, 0, function () { var a = this.year(); return a <= 9999 ? "" + a : "+" + a }), U(0, ["YY", 2], 0, function () { return this.year() % 100 }), U(0, ["YYYY", 4], 0, "year"), U(0, ["YYYYY", 5], 0, "year"), U(0, ["YYYYYY", 6, !0], 0, "year"), J("year", "y"), M("year", 1), Z("Y", Zd), Z("YY", Sd, Od), Z("YYYY", Wd, Qd), Z("YYYYY", Xd, Rd), Z("YYYYYY", Xd, Rd), ba(["YYYYY", "YYYYYY"], ee), ba("YYYY", function (b, c) { c[ee] = 2 === b.length ? a.parseTwoDigitYear(b) : u(b) }), ba("YY", function (b, c) { c[ee] = a.parseTwoDigitYear(b) }), ba("Y", function (a, b) { b[ee] = parseInt(a, 10) }), a.parseTwoDigitYear = function (a) { return u(a) + (u(a) > 68 ? 1900 : 2e3) }; var te = O("FullYear", !0); U("w", ["ww", 2], "wo", "week"), U("W", ["WW", 2], "Wo", "isoWeek"), J("week", "w"), J("isoWeek", "W"), M("week", 5), M("isoWeek", 5), Z("w", Sd), Z("ww", Sd, Od), Z("W", Sd), Z("WW", Sd, Od), ca(["w", "ww", "W", "WW"], function (a, b, c, d) { b[d.substr(0, 1)] = u(a) }); var ue = { dow: 0, doy: 6 }; U("d", 0, "do", "day"), U("dd", 0, 0, function (a) { return this.localeData().weekdaysMin(this, a) }), U("ddd", 0, 0, function (a) { return this.localeData().weekdaysShort(this, a) }), U("dddd", 0, 0, function (a) { return this.localeData().weekdays(this, a) }), U("e", 0, 0, "weekday"), U("E", 0, 0, "isoWeekday"), J("day", "d"), J("weekday", "e"), J("isoWeekday", "E"), M("day", 11), M("weekday", 11), M("isoWeekday", 11), Z("d", Sd), Z("e", Sd), Z("E", Sd), Z("dd", function (a, b) { return b.weekdaysMinRegex(a) }), Z("ddd", function (a, b) { return b.weekdaysShortRegex(a) }), Z("dddd", function (a, b) { return b.weekdaysRegex(a) }), ca(["dd", "ddd", "dddd"], function (a, b, c, d) { var e = c._locale.weekdaysParse(a, d, c._strict); null != e ? b.d = e : n(c).invalidWeekday = a }), ca(["d", "e", "E"], function (a, b, c, d) { b[d] = u(a) }); var ve = "Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"), we = "Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"), xe = "Su_Mo_Tu_We_Th_Fr_Sa".split("_"), ye = be, ze = be, Ae = be; U("H", ["HH", 2], 0, "hour"), U("h", ["hh", 2], 0, Ra), U("k", ["kk", 2], 0, Sa), U("hmm", 0, 0, function () { return "" + Ra.apply(this) + T(this.minutes(), 2) }), U("hmmss", 0, 0, function () { return "" + Ra.apply(this) + T(this.minutes(), 2) + T(this.seconds(), 2) }), U("Hmm", 0, 0, function () { return "" + this.hours() + T(this.minutes(), 2) }), U("Hmmss", 0, 0, function () { return "" + this.hours() + T(this.minutes(), 2) + T(this.seconds(), 2) }), Ta("a", !0), Ta("A", !1), J("hour", "h"), M("hour", 13), Z("a", Ua), Z("A", Ua), Z("H", Sd), Z("h", Sd), Z("k", Sd), Z("HH", Sd, Od), Z("hh", Sd, Od), Z("kk", Sd, Od), Z("hmm", Td), Z("hmmss", Ud), Z("Hmm", Td), Z("Hmmss", Ud), ba(["H", "HH"], he), ba(["k", "kk"], function (a, b, c) { var d = u(a); b[he] = 24 === d ? 0 : d }), ba(["a", "A"], function (a, b, c) { c._isPm = c._locale.isPM(a), c._meridiem = a }), ba(["h", "hh"], function (a, b, c) { b[he] = u(a), n(c).bigHour = !0 }), ba("hmm", function (a, b, c) { var d = a.length - 2; b[he] = u(a.substr(0, d)), b[ie] = u(a.substr(d)), n(c).bigHour = !0 }), ba("hmmss", function (a, b, c) { var d = a.length - 4, e = a.length - 2; b[he] = u(a.substr(0, d)), b[ie] = u(a.substr(d, 2)), b[je] = u(a.substr(e)), n(c).bigHour = !0 }), ba("Hmm", function (a, b, c) { var d = a.length - 2; b[he] = u(a.substr(0, d)), b[ie] = u(a.substr(d)) }), ba("Hmmss", function (a, b, c) { var d = a.length - 4, e = a.length - 2; b[he] = u(a.substr(0, d)), b[ie] = u(a.substr(d, 2)), b[je] = u(a.substr(e)) }); var Be, Ce = /[ap]\.?m?\.?/i, De = O("Hours", !0), Ee = { calendar: Bd, longDateFormat: Cd, invalidDate: Dd, ordinal: Ed, dayOfMonthOrdinalParse: Fd, relativeTime: Gd, months: pe, monthsShort: qe, week: ue, weekdays: ve, weekdaysMin: xe, weekdaysShort: we, meridiemParse: Ce }, Fe = {}, Ge = {}, He = /^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/, Ie = /^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([\+\-]\d\d(?::?\d\d)?|\s*Z)?)?$/, Je = /Z|[+-]\d\d(?::?\d\d)?/, Ke = [["YYYYYY-MM-DD", /[+-]\d{6}-\d\d-\d\d/], ["YYYY-MM-DD", /\d{4}-\d\d-\d\d/], ["GGGG-[W]WW-E", /\d{4}-W\d\d-\d/], ["GGGG-[W]WW", /\d{4}-W\d\d/, !1], ["YYYY-DDD", /\d{4}-\d{3}/], ["YYYY-MM", /\d{4}-\d\d/, !1], ["YYYYYYMMDD", /[+-]\d{10}/], ["YYYYMMDD", /\d{8}/], ["GGGG[W]WWE", /\d{4}W\d{3}/], ["GGGG[W]WW", /\d{4}W\d{2}/, !1], ["YYYYDDD", /\d{7}/]], Le = [["HH:mm:ss.SSSS", /\d\d:\d\d:\d\d\.\d+/], ["HH:mm:ss,SSSS", /\d\d:\d\d:\d\d,\d+/], ["HH:mm:ss", /\d\d:\d\d:\d\d/], ["HH:mm", /\d\d:\d\d/], ["HHmmss.SSSS", /\d\d\d\d\d\d\.\d+/], ["HHmmss,SSSS", /\d\d\d\d\d\d,\d+/], ["HHmmss", /\d\d\d\d\d\d/], ["HHmm", /\d\d\d\d/], ["HH", /\d\d/]], Me = /^\/?Date\((\-?\d+)/i, Ne = /^((?:Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d?\d\s(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(?:\d\d)?\d\d\s)(\d\d:\d\d)(\:\d\d)?(\s(?:UT|GMT|[ECMP][SD]T|[A-IK-Za-ik-z]|[+-]\d{4}))$/; a.createFromInputFallback = x("value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are discouraged and will be removed in an upcoming major release. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.", function (a) { a._d = new Date(a._i + (a._useUTC ? " UTC" : "")) }), a.ISO_8601 = function () { }, a.RFC_2822 = function () { }; var Oe = x("moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/", function () { var a = tb.apply(null, arguments); return this.isValid() && a.isValid() ? a < this ? this : a : p() }), Pe = x("moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/", function () { var a = tb.apply(null, arguments); return this.isValid() && a.isValid() ? a > this ? this : a : p() }), Qe = function () { return Date.now ? Date.now() : +new Date }, Re = ["year", "quarter", "month", "week", "day", "hour", "minute", "second", "millisecond"]; Db("Z", ":"), Db("ZZ", ""), Z("Z", _d), Z("ZZ", _d), ba(["Z", "ZZ"], function (a, b, c) { c._useUTC = !0, c._tzm = Eb(_d, a) }); var Se = /([\+\-]|\d\d)/gi; a.updateOffset = function () { }; var Te = /^(\-)?(?:(\d*)[. ])?(\d+)\:(\d+)(?:\:(\d+)(\.\d*)?)?$/, Ue = /^(-)?P(?:(-?[0-9,.]*)Y)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)W)?(?:(-?[0-9,.]*)D)?(?:T(?:(-?[0-9,.]*)H)?(?:(-?[0-9,.]*)M)?(?:(-?[0-9,.]*)S)?)?$/; Sb.fn = Ab.prototype, Sb.invalid = zb; var Ve = Wb(1, "add"), We = Wb(-1, "subtract"); a.defaultFormat = "YYYY-MM-DDTHH:mm:ssZ", a.defaultFormatUtc = "YYYY-MM-DDTHH:mm:ss[Z]"; var Xe = x("moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.", function (a) { return void 0 === a ? this.localeData() : this.locale(a) }); U(0, ["gg", 2], 0, function () { return this.weekYear() % 100 }), U(0, ["GG", 2], 0, function () { return this.isoWeekYear() % 100 }), Dc("gggg", "weekYear"), Dc("ggggg", "weekYear"), Dc("GGGG", "isoWeekYear"), Dc("GGGGG", "isoWeekYear"), J("weekYear", "gg"), J("isoWeekYear", "GG"), M("weekYear", 1), M("isoWeekYear", 1), Z("G", Zd), Z("g", Zd), Z("GG", Sd, Od), Z("gg", Sd, Od), Z("GGGG", Wd, Qd), Z("gggg", Wd, Qd), Z("GGGGG", Xd, Rd), Z("ggggg", Xd, Rd), ca(["gggg", "ggggg", "GGGG", "GGGGG"], function (a, b, c, d) { b[d.substr(0, 2)] = u(a) }), ca(["gg", "GG"], function (b, c, d, e) { c[e] = a.parseTwoDigitYear(b) }), U("Q", 0, "Qo", "quarter"), J("quarter", "Q"), M("quarter", 7), Z("Q", Nd), ba("Q", function (a, b) { b[fe] = 3 * (u(a) - 1) }), U("D", ["DD", 2], "Do", "date"), J("date", "D"), M("date", 9), Z("D", Sd), Z("DD", Sd, Od), Z("Do", function (a, b) { return a ? b._dayOfMonthOrdinalParse || b._ordinalParse : b._dayOfMonthOrdinalParseLenient }), ba(["D", "DD"], ge), ba("Do", function (a, b) { b[ge] = u(a.match(Sd)[0], 10) }); var Ye = O("Date", !0); U("DDD", ["DDDD", 3], "DDDo", "dayOfYear"), J("dayOfYear", "DDD"), M("dayOfYear", 4), Z("DDD", Vd), Z("DDDD", Pd), ba(["DDD", "DDDD"], function (a, b, c) { c._dayOfYear = u(a) }), U("m", ["mm", 2], 0, "minute"), J("minute", "m"), M("minute", 14), Z("m", Sd), Z("mm", Sd, Od), ba(["m", "mm"], ie); var Ze = O("Minutes", !1); U("s", ["ss", 2], 0, "second"), J("second", "s"), M("second", 15), Z("s", Sd), Z("ss", Sd, Od), ba(["s", "ss"], je); var $e = O("Seconds", !1); U("S", 0, 0, function () { return ~~(this.millisecond() / 100) }), U(0, ["SS", 2], 0, function () { return ~~(this.millisecond() / 10) }), U(0, ["SSS", 3], 0, "millisecond"), U(0, ["SSSS", 4], 0, function () { return 10 * this.millisecond() }), U(0, ["SSSSS", 5], 0, function () { return 100 * this.millisecond() }), U(0, ["SSSSSS", 6], 0, function () { return 1e3 * this.millisecond() }), U(0, ["SSSSSSS", 7], 0, function () { return 1e4 * this.millisecond() }), U(0, ["SSSSSSSS", 8], 0, function () { return 1e5 * this.millisecond() }), U(0, ["SSSSSSSSS", 9], 0, function () { return 1e6 * this.millisecond() }), J("millisecond", "ms"), M("millisecond", 16), Z("S", Vd, Nd), Z("SS", Vd, Od), Z("SSS", Vd, Pd); var _e; for (_e = "SSSS"; _e.length <= 9; _e += "S")Z(_e, Yd); for (_e = "S"; _e.length <= 9; _e += "S")ba(_e, Mc); var af = O("Milliseconds", !1); U("z", 0, 0, "zoneAbbr"), U("zz", 0, 0, "zoneName"); var bf = r.prototype; bf.add = Ve, bf.calendar = Zb, bf.clone = $b, bf.diff = fc, bf.endOf = sc, bf.format = kc, bf.from = lc, bf.fromNow = mc, bf.to = nc, bf.toNow = oc, bf.get = R, bf.invalidAt = Bc, bf.isAfter = _b, bf.isBefore = ac, bf.isBetween = bc, bf.isSame = cc, bf.isSameOrAfter = dc, bf.isSameOrBefore = ec, bf.isValid = zc, bf.lang = Xe, bf.locale = pc, bf.localeData = qc, bf.max = Pe, bf.min = Oe, bf.parsingFlags = Ac, bf.set = S, bf.startOf = rc, bf.subtract = We, bf.toArray = wc, bf.toObject = xc, bf.toDate = vc, bf.toISOString = ic, bf.inspect = jc, bf.toJSON = yc, bf.toString = hc, bf.unix = uc, bf.valueOf = tc, bf.creationData = Cc, bf.year = te, bf.isLeapYear = ra, bf.weekYear = Ec, bf.isoWeekYear = Fc, bf.quarter = bf.quarters = Kc, bf.month = ka, bf.daysInMonth = la, bf.week = bf.weeks = Ba, bf.isoWeek = bf.isoWeeks = Ca, bf.weeksInYear = Hc, bf.isoWeeksInYear = Gc, bf.date = Ye, bf.day = bf.days = Ka, bf.weekday = La, bf.isoWeekday = Ma, bf.dayOfYear = Lc, bf.hour = bf.hours = De, bf.minute = bf.minutes = Ze, bf.second = bf.seconds = $e, bf.millisecond = bf.milliseconds = af, bf.utcOffset = Hb, bf.utc = Jb, bf.local = Kb, bf.parseZone = Lb, bf.hasAlignedHourOffset = Mb, bf.isDST = Nb, bf.isLocal = Pb, bf.isUtcOffset = Qb, bf.isUtc = Rb, bf.isUTC = Rb, bf.zoneAbbr = Nc, bf.zoneName = Oc, bf.dates = x("dates accessor is deprecated. Use date instead.", Ye), bf.months = x("months accessor is deprecated. Use month instead", ka), bf.years = x("years accessor is deprecated. Use year instead", te), bf.zone = x("moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/", Ib), bf.isDSTShifted = x("isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information", Ob); var cf = C.prototype; cf.calendar = D, cf.longDateFormat = E, cf.invalidDate = F, cf.ordinal = G, cf.preparse = Rc, cf.postformat = Rc, cf.relativeTime = H, cf.pastFuture = I, cf.set = A, cf.months = fa, cf.monthsShort = ga, cf.monthsParse = ia, cf.monthsRegex = na, cf.monthsShortRegex = ma, cf.week = ya, cf.firstDayOfYear = Aa, cf.firstDayOfWeek = za, cf.weekdays = Fa, cf.weekdaysMin = Ha, cf.weekdaysShort = Ga, cf.weekdaysParse = Ja, cf.weekdaysRegex = Na, cf.weekdaysShortRegex = Oa, cf.weekdaysMinRegex = Pa, cf.isPM = Va, cf.meridiem = Wa, $a("en", { dayOfMonthOrdinalParse: /\d{1,2}(th|st|nd|rd)/, ordinal: function (a) { var b = a % 10, c = 1 === u(a % 100 / 10) ? "th" : 1 === b ? "st" : 2 === b ? "nd" : 3 === b ? "rd" : "th"; return a + c } }), a.lang = x("moment.lang is deprecated. Use moment.locale instead.", $a), a.langData = x("moment.langData is deprecated. Use moment.localeData instead.", bb); var df = Math.abs, ef = id("ms"), ff = id("s"), gf = id("m"), hf = id("h"), jf = id("d"), kf = id("w"), lf = id("M"), mf = id("y"), nf = kd("milliseconds"), of = kd("seconds"), pf = kd("minutes"), qf = kd("hours"), rf = kd("days"), sf = kd("months"), tf = kd("years"), uf = Math.round, vf = { ss: 44, s: 45, m: 45, h: 22, d: 26, M: 11 }, wf = Math.abs, xf = Ab.prototype; return xf.isValid = yb, xf.abs = $c, xf.add = ad, xf.subtract = bd, xf.as = gd, xf.asMilliseconds = ef, xf.asSeconds = ff, xf.asMinutes = gf, xf.asHours = hf, xf.asDays = jf, xf.asWeeks = kf, xf.asMonths = lf, xf.asYears = mf, xf.valueOf = hd, xf._bubble = dd, xf.get = jd, xf.milliseconds = nf, xf.seconds = of, xf.minutes = pf, xf.hours = qf, xf.days = rf, xf.weeks = ld, xf.months = sf, xf.years = tf, xf.humanize = qd, xf.toISOString = rd, xf.toString = rd, xf.toJSON = rd, xf.locale = pc, xf.localeData = qc, xf.toIsoString = x("toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)", rd), xf.lang = Xe, U("X", 0, 0, "unix"), U("x", 0, 0, "valueOf"), Z("x", Zd), Z("X", ae), ba("X", function (a, b, c) { c._d = new Date(1e3 * parseFloat(a, 10)) }), ba("x", function (a, b, c) { c._d = new Date(u(a)) }), a.version = "2.29.4", b(tb), a.fn = bf, a.min = vb, a.max = wb, a.now = Qe, a.utc = l, a.unix = Pc, a.months = Vc, a.isDate = h, a.locale = $a, a.invalid = p, a.duration = Sb, a.isMoment = s, a.weekdays = Xc, a.parseZone = Qc, a.localeData = bb, a.isDuration = Bb, a.monthsShort = Wc, a.weekdaysMin = Zc, a.defineLocale = _a, a.updateLocale = ab, a.locales = cb, a.weekdaysShort = Yc, a.normalizeUnits = K, a.relativeTimeRounding = od, a.relativeTimeThreshold = pd, a.calendarFormat = Yb, a.prototype = bf, a
});
(function e(t, n, r) { function s(o, u) { if (!n[o]) { if (!t[o]) { var a = typeof require == "function" && require; if (!u && a) return a(o, !0); if (i) return i(o, !0); throw new Error("Cannot find module '" + o + "'") } var f = n[o] = { exports: {} }; t[o][0].call(f.exports, function (e) { var n = t[o][1][e]; return s(n ? n : e) }, f, f.exports, e, t, n, r) } return n[o].exports } var i = typeof require == "function" && require; for (var o = 0; o < r.length; o++)s(r[o]); return s })({
	1: [function (require, module, exports) {
		module.exports = angular.module('ngInputCurrency', []).name;

		angular.module('ngInputCurrency').service('ngInputCurrencyService', ['$locale', function ($locale) {
			this.toFloat = function (str) {
				if (angular.isNumber(str))
					return parseFloat(str, 10);

				if (!angular.isString(str))
					throw new TypeError('ngInputCurrencyService.toFloat expects argument to be a String, but was given ' + str);

				str = str
					.replace(new RegExp(this.stringToRegExp($locale.NUMBER_FORMATS.GROUP_SEP), 'g'), '')
					.replace(new RegExp(this.stringToRegExp($locale.NUMBER_FORMATS.CURRENCY_SYM), 'g'), '')
					.replace(new RegExp(this.stringToRegExp($locale.NUMBER_FORMATS.DECIMAL_SEP), 'g'), '.');

				return parseFloat(str, 10);
			};

			this.stringToRegExp = function (str, opt) {
				return str.replace(/\./g, '\\.')
					.replace(/\[/g, '\\[')
					.replace(/\]/g, '\\]')
					.replace(/\,/g, '\\,')
					.replace(/\|/g, '\\|')
					.replace(/\)/g, '\\)')
					.replace(/\(/g, '\\(')
					.replace(/\^/g, '\\^')
					.replace(/\$/g, '\\$')
					.replace(/\_/g, '\\_')
					.replace(/\?/g, '\\?')
					.replace(/\-/g, '\\-');
			};

			this.isValid = function (val) {
				return angular.isNumber(val) && !isNaN(val);
			};

			this.preformatValue = function (val) {
				if (!angular.isString(val))
					return val;

				val = val.replace($locale.NUMBER_FORMATS.CURRENCY_SYM, '')

				var groupRegex = new RegExp(this.stringToRegExp($locale.NUMBER_FORMATS.GROUP_SEP), 'g'),
					decimalRegex = new RegExp(this.stringToRegExp($locale.NUMBER_FORMATS.DECIMAL_SEP), 'g');

				var groupMatch = val.match(groupRegex), decimalMatch = val.match(decimalRegex);
				if (groupMatch && groupMatch.length == 1 && (!decimalMatch || decimalMatch.length === 0))
					return val.replace(groupRegex, '.');

				if (decimalMatch && decimalMatch.length == 1 && (!groupMatch || groupMatch.length === 0))
					return val.replace(decimalRegex, '.');

				return val;
			};
		}]);

		angular.module('ngInputCurrency').directive('ngInputCurrency', ['$locale', '$filter', 'ngInputCurrencyService', '$timeout', function ($locale, $filter, util, $timeout) {
			var link = function ($scope, $element, $attrs, $ngModel) {

				var opts = {
					updateOn: 'blur enter',
					updateOnDefault: false
				};
				if ($ngModel.$options !== null && $ngModel.$options !== undefined)
					opts = $ngModel.$options.createChild(opts);

				$ngModel.$options = opts;

				var filter = $filter('currency');
				$ngModel.$formatters.push(function fromModelToView(value) {
					return filter(value);
				});

				$ngModel.$parsers.push(function (value) {
					value = util.preformatValue(value);

					var currency = util.toFloat(filter(value));
					if (util.isValid(currency)) {
						$ngModel.$setViewValue(filter(currency));
						$ngModel.$render();
						return currency;
					}
				});

				$element.on('blur', function () {
					if ($ngModel.$viewValue === $ngModel.$modelValue)
						$element.val(filter($ngModel.$modelValue));
				});

				$element.on('focus', function () {
					if (util.isValid($ngModel.$modelValue)) {
						$ngModel.$setViewValue($ngModel.$modelValue);
						$ngModel.$render();
					}
				});

				$ngModel.$validators.currency = function (modelValue) {
					return util.isValid(modelValue);
				};

			};
			return {
				'restrict': 'A',
				'require': 'ngModel',
				'link': link
			};
		}]);

	}, {}]
}, {}, [1])
/*
 *
 * https://github.com/fatlinesofcode/ngDraggable
 */
angular.module("ngDraggable", [])
	.service('ngDraggable', [function () {


		var scope = this;
		scope.inputEvent = function (event) {
			if (angular.isDefined(event.touches)) {
				return event.touches[0];
			}
			//Checking both is not redundent. If only check if touches isDefined, angularjs isDefnied will return error and stop the remaining scripty if event.originalEvent is not defined.
			else if (angular.isDefined(event.originalEvent) && angular.isDefined(event.originalEvent.touches)) {
				return event.originalEvent.touches[0];
			}
			return event;
		};

		scope.touchTimeout = 100;

	}])
	.directive('ngDrag', ['$rootScope', '$parse', '$document', '$window', 'ngDraggable', function ($rootScope, $parse, $document, $window, ngDraggable) {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				scope.value = attrs.ngDrag;
				var offset, _centerAnchor = false, _mx, _my, _tx, _ty, _mrx, _mry;
				var _hasTouch = ('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch;
				var _pressEvents = 'touchstart mousedown';
				var _moveEvents = 'touchmove mousemove';
				var _releaseEvents = 'touchend mouseup';
				var _dragHandle;

				// to identify the element in order to prevent getting superflous events when a single element has both drag and drop directives on it.
				var _myid = scope.$id;
				var _data = null;

				var _dragOffset = null;

				var _dragEnabled = false;

				var _pressTimer = null;

				var onDragStartCallback = $parse(attrs.ngDragStart) || null;
				var onDragStopCallback = $parse(attrs.ngDragStop) || null;
				var onDragSuccessCallback = $parse(attrs.ngDragSuccess) || null;
				var allowTransform = angular.isDefined(attrs.allowTransform) ? scope.$eval(attrs.allowTransform) : true;

				var getDragData = $parse(attrs.ngDragData);

				// deregistration function for mouse move events in $rootScope triggered by jqLite trigger handler
				var _deregisterRootMoveListener = angular.noop;

				var initialize = function () {
					element.attr('draggable', 'false'); // prevent native drag
					// check to see if drag handle(s) was specified
					// if querySelectorAll is available, we use this instead of find
					// as JQLite find is limited to tagnames
					if (element[0].querySelectorAll) {
						var dragHandles = angular.element(element[0].querySelectorAll('[ng-drag-handle]'));
					} else {
						var dragHandles = element.find('[ng-drag-handle]');
					}
					if (dragHandles.length) {
						_dragHandle = dragHandles;
					}
					toggleListeners(true);
				};

				var toggleListeners = function (enable) {
					if (!enable) return;
					// add listeners.

					scope.$on('$destroy', onDestroy);
					scope.$watch(attrs.ngDrag, onEnableChange);
					scope.$watch(attrs.ngCenterAnchor, onCenterAnchor);
					// wire up touch events
					if (_dragHandle) {
						// handle(s) specified, use those to initiate drag
						_dragHandle.on(_pressEvents, onpress);
					} else {
						// no handle(s) specified, use the element as the handle
						element.on(_pressEvents, onpress);
					}
					// if(! _hasTouch && element[0].nodeName.toLowerCase() == "img"){
					if (element[0].nodeName.toLowerCase() == "img") {
						element.on('mousedown', function () { return false; }); // prevent native drag for images
					}
				};
				var onDestroy = function (enable) {
					toggleListeners(false);
				};
				var onEnableChange = function (newVal, oldVal) {
					_dragEnabled = (newVal);
				};
				var onCenterAnchor = function (newVal, oldVal) {
					if (angular.isDefined(newVal))
						_centerAnchor = (newVal || 'true');
				};

				var isClickableElement = function (evt) {
					return (
						angular.isDefined(angular.element(evt.target).attr("ng-cancel-drag"))
					);
				};
				/*
				 * When the element is clicked start the drag behaviour
				 * On touch devices as a small delay so as not to prevent native window scrolling
				 */
				var onpress = function (evt) {
					// console.log("110"+" onpress: "+Math.random()+" "+ evt.type);
					if (!_dragEnabled) return;

					if (isClickableElement(evt)) {
						return;
					}

					if (evt.type == "mousedown" && evt.button != 0) {
						// Do not start dragging on right-click
						return;
					}

					var useTouch = evt.type === 'touchstart' ? true : false;


					if (useTouch) {
						cancelPress();
						_pressTimer = setTimeout(function () {
							cancelPress();
							onlongpress(evt);
							onmove(evt);
						}, ngDraggable.touchTimeout);
						$document.on(_moveEvents, cancelPress);
						$document.on(_releaseEvents, cancelPress);
					} else {
						onlongpress(evt);
					}

				};

				var cancelPress = function () {
					clearTimeout(_pressTimer);
					$document.off(_moveEvents, cancelPress);
					$document.off(_releaseEvents, cancelPress);
				};

				var onlongpress = function (evt) {
					if (!_dragEnabled) return;
					evt.preventDefault();

					offset = element[0].getBoundingClientRect();
					if (allowTransform)
						_dragOffset = offset;
					else {
						_dragOffset = { left: document.body.scrollLeft, top: document.body.scrollTop };
					}


					element.centerX = element[0].offsetWidth / 2;
					element.centerY = element[0].offsetHeight / 2;

					_mx = ngDraggable.inputEvent(evt).pageX;//ngDraggable.getEventProp(evt, 'pageX');
					_my = ngDraggable.inputEvent(evt).pageY;//ngDraggable.getEventProp(evt, 'pageY');
					_mrx = _mx - offset.left;
					_mry = _my - offset.top;
					if (_centerAnchor) {
						_tx = _mx - element.centerX - $window.pageXOffset;
						_ty = _my - element.centerY - $window.pageYOffset;
					} else {
						_tx = _mx - _mrx - $window.pageXOffset;
						_ty = _my - _mry - $window.pageYOffset;
					}

					/* FIX DRAG / DROP ON TOUCH DEVICES */

					//$document.on(_moveEvents, onmove);
					//$document.on(_releaseEvents, onrelease);

					if (_hasTouch) {
						if (_dragHandle) {
							_dragHandle.on(_moveEvents, onmove);
							_dragHandle.on(_releaseEvents, onrelease);
						} else {
							element.on(_moveEvents, onmove);
							element.on(_releaseEvents, onrelease);
						}
					} else {
						$document.on(_moveEvents, onmove);
						$document.on(_releaseEvents, onrelease);
					}
					/* END FIX */

					// This event is used to receive manually triggered mouse move events
					// jqLite unfortunately only supports triggerHandler(...)
					// See http://api.jquery.com/triggerHandler/
					// _deregisterRootMoveListener = $rootScope.$on('draggable:_triggerHandlerMove', onmove);
					_deregisterRootMoveListener = $rootScope.$on('draggable:_triggerHandlerMove', function (event, origEvent) {
						onmove(origEvent);
					});
				};

				var onmove = function (evt) {
					if (!_dragEnabled) return;
					evt.preventDefault();

					if (!element.hasClass('dragging')) {
						_data = getDragData(scope);
						element.addClass('dragging');
						$rootScope.$broadcast('draggable:start', { x: _mx, y: _my, tx: _tx, ty: _ty, event: evt, element: element, data: _data });

						if (onDragStartCallback) {
							scope.$apply(function () {
								onDragStartCallback(scope, { $data: _data, $event: evt });
							});
						}
					}

					_mx = ngDraggable.inputEvent(evt).pageX;//ngDraggable.getEventProp(evt, 'pageX');
					_my = ngDraggable.inputEvent(evt).pageY;//ngDraggable.getEventProp(evt, 'pageY');

					if (_centerAnchor) {
						_tx = _mx - element.centerX - _dragOffset.left;
						_ty = _my - element.centerY - _dragOffset.top;
					} else {
						_tx = _mx - _mrx - _dragOffset.left;
						_ty = _my - _mry - _dragOffset.top;
					}

					moveElement(_tx, _ty);

					$rootScope.$broadcast('draggable:move', { x: _mx, y: _my, tx: _tx, ty: _ty, event: evt, element: element, data: _data, uid: _myid, dragOffset: _dragOffset });
				};

				var onrelease = function (evt) {
					if (!_dragEnabled)
						return;
					evt.preventDefault();
					$rootScope.$broadcast('draggable:end', { x: _mx, y: _my, tx: _tx, ty: _ty, event: evt, element: element, data: _data, callback: onDragComplete, uid: _myid });
					element.removeClass('dragging');
					element.parent().find('.drag-enter').removeClass('drag-enter');
					reset();
					$document.off(_moveEvents, onmove);
					$document.off(_releaseEvents, onrelease);

					if (onDragStopCallback) {
						scope.$apply(function () {
							onDragStopCallback(scope, { $data: _data, $event: evt });
						});
					}

					_deregisterRootMoveListener();
				};

				var onDragComplete = function (evt) {


					if (!onDragSuccessCallback) return;

					scope.$apply(function () {
						onDragSuccessCallback(scope, { $data: _data, $event: evt });
					});
				};

				var reset = function () {
					if (allowTransform)
						element.css({ transform: '', 'z-index': '', '-webkit-transform': '', '-ms-transform': '' });
					else
						element.css({ 'position': '', top: '', left: '' });
				};

				var moveElement = function (x, y) {
					if (allowTransform) {
						element.css({
							transform: 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, ' + x + ', ' + y + ', 0, 1)',
							'z-index': 99999,
							'-webkit-transform': 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, ' + x + ', ' + y + ', 0, 1)',
							'-ms-transform': 'matrix(1, 0, 0, 1, ' + x + ', ' + y + ')'
						});
					} else {
						element.css({ 'left': x + 'px', 'top': y + 'px', 'position': 'fixed' });
					}
				};
				initialize();
			}
		};
	}])

	.directive('ngDrop', ['$parse', '$timeout', '$window', '$document', 'ngDraggable', function ($parse, $timeout, $window, $document, ngDraggable) {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				scope.value = attrs.ngDrop;
				scope.isTouching = false;

				var _lastDropTouch = null;

				var _myid = scope.$id;

				var _dropEnabled = false;

				var onDropCallback = $parse(attrs.ngDropSuccess);// || function(){};

				var onDragStartCallback = $parse(attrs.ngDragStart);
				var onDragStopCallback = $parse(attrs.ngDragStop);
				var onDragMoveCallback = $parse(attrs.ngDragMove);

				var initialize = function () {
					toggleListeners(true);
				};

				var toggleListeners = function (enable) {
					// remove listeners

					if (!enable) return;
					// add listeners.
					scope.$watch(attrs.ngDrop, onEnableChange);
					scope.$on('$destroy', onDestroy);
					scope.$on('draggable:start', onDragStart);
					scope.$on('draggable:move', onDragMove);
					scope.$on('draggable:end', onDragEnd);
				};

				var onDestroy = function (enable) {
					toggleListeners(false);
				};
				var onEnableChange = function (newVal, oldVal) {
					_dropEnabled = newVal;
				};
				var onDragStart = function (evt, obj) {
					if (!_dropEnabled) return;
					isTouching(obj.x, obj.y, obj.element);

					if (attrs.ngDragStart) {
						$timeout(function () {
							onDragStartCallback(scope, { $data: obj.data, $event: obj });
						});
					}
				};
				var onDragMove = function (evt, obj) {
					if (!_dropEnabled) return;
					isTouching(obj.x, obj.y, obj.element);

					if (attrs.ngDragMove) {
						$timeout(function () {
							onDragMoveCallback(scope, { $data: obj.data, $event: obj });
						});
					}
				};

				var onDragEnd = function (evt, obj) {

					// don't listen to drop events if this is the element being dragged
					// only update the styles and return
					if (!_dropEnabled || _myid === obj.uid) {
						updateDragStyles(false, obj.element);
						return;
					}
					if (isTouching(obj.x, obj.y, obj.element)) {
						// call the ngDraggable ngDragSuccess element callback
						if (obj.callback) {
							obj.callback(obj);
						}

						if (attrs.ngDropSuccess) {
							$timeout(function () {
								onDropCallback(scope, { $data: obj.data, $event: obj, $target: scope.$eval(scope.value) });
							});
						}
					}

					if (attrs.ngDragStop) {
						$timeout(function () {
							onDragStopCallback(scope, { $data: obj.data, $event: obj });
						});
					}

					updateDragStyles(false, obj.element);
				};

				var isTouching = function (mouseX, mouseY, dragElement) {
					var touching = hitTest(mouseX, mouseY);
					scope.isTouching = touching;
					if (touching) {
						_lastDropTouch = element;
					}
					updateDragStyles(touching, dragElement);
					return touching;
				};

				var updateDragStyles = function (touching, dragElement) {
					if (touching) {
						element.addClass('drag-enter');
						dragElement.addClass('drag-over');
					} else if (_lastDropTouch == element) {
						_lastDropTouch = null;
						element.removeClass('drag-enter');
						dragElement.removeClass('drag-over');
					}
				};

				var hitTest = function (x, y) {
					var bounds = element[0].getBoundingClientRect();// ngDraggable.getPrivOffset(element);
					x -= $document[0].body.scrollLeft + $document[0].documentElement.scrollLeft;
					y -= $document[0].body.scrollTop + $document[0].documentElement.scrollTop;
					return x >= bounds.left
						&& x <= bounds.right
						&& y <= bounds.bottom
						&& y >= bounds.top;
				};

				initialize();
			}
		};
	}])
	.directive('ngDragClone', ['$parse', '$timeout', 'ngDraggable', function ($parse, $timeout, ngDraggable) {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				var img, _allowClone = true;
				var _dragOffset = null;
				scope.clonedData = {};
				var initialize = function () {

					img = element.find('img');
					element.attr('draggable', 'false');
					img.attr('draggable', 'false');
					reset();
					toggleListeners(true);
				};


				var toggleListeners = function (enable) {
					// remove listeners

					if (!enable) return;
					// add listeners.
					scope.$on('draggable:start', onDragStart);
					scope.$on('draggable:move', onDragMove);
					scope.$on('draggable:end', onDragEnd);
					preventContextMenu();

				};
				var preventContextMenu = function () {
					//  element.off('mousedown touchstart touchmove touchend touchcancel', absorbEvent_);
					img.off('mousedown touchstart touchmove touchend touchcancel', absorbEvent_);
					//  element.on('mousedown touchstart touchmove touchend touchcancel', absorbEvent_);
					img.on('mousedown touchstart touchmove touchend touchcancel', absorbEvent_);
				};
				var onDragStart = function (evt, obj, elm) {
					_allowClone = true;
					if (angular.isDefined(obj.data.allowClone)) {
						_allowClone = obj.data.allowClone;
					}
					if (_allowClone) {
						scope.$apply(function () {
							scope.clonedData = obj.data;
						});
						element.css('width', obj.element[0].offsetWidth);
						element.css('height', obj.element[0].offsetHeight);

						moveElement(obj.tx, obj.ty);
					}

				};
				var onDragMove = function (evt, obj) {
					if (_allowClone) {

						_tx = obj.tx + obj.dragOffset.left;
						_ty = obj.ty + obj.dragOffset.top;

						moveElement(_tx, _ty);
					}
				};
				var onDragEnd = function (evt, obj) {
					//moveElement(obj.tx,obj.ty);
					if (_allowClone) {
						reset();
					}
				};

				var reset = function () {
					element.css({ left: 0, top: 0, position: 'fixed', 'z-index': -1, visibility: 'hidden' });
				};
				var moveElement = function (x, y) {
					element.css({
						transform: 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, ' + x + ', ' + y + ', 0, 1)', 'z-index': 99999, 'visibility': 'visible',
						'-webkit-transform': 'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, ' + x + ', ' + y + ', 0, 1)',
						'-ms-transform': 'matrix(1, 0, 0, 1, ' + x + ', ' + y + ')'
						//,margin: '0'  don't monkey with the margin,
					});
				};

				var absorbEvent_ = function (event) {
					var e = event;//.originalEvent;
					e.preventDefault && e.preventDefault();
					e.stopPropagation && e.stopPropagation();
					e.cancelBubble = true;
					e.returnValue = false;
					return false;
				};

				initialize();
			}
		};
	}])
	.directive('ngPreventDrag', ['$parse', '$timeout', function ($parse, $timeout) {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				var initialize = function () {

					element.attr('draggable', 'false');
					toggleListeners(true);
				};


				var toggleListeners = function (enable) {
					// remove listeners

					if (!enable) return;
					// add listeners.
					element.on('mousedown touchstart touchmove touchend touchcancel', absorbEvent_);
				};


				var absorbEvent_ = function (event) {
					var e = event.originalEvent;
					e.preventDefault && e.preventDefault();
					e.stopPropagation && e.stopPropagation();
					e.cancelBubble = true;
					e.returnValue = false;
					return false;
				};

				initialize();
			}
		};
	}])
	.directive('ngCancelDrag', [function () {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				element.find('*').attr('ng-cancel-drag', 'ng-cancel-drag');
			}
		};
	}])
	.directive('ngDragScroll', ['$window', '$interval', '$timeout', '$document', '$rootScope', function ($window, $interval, $timeout, $document, $rootScope) {
		return {
			restrict: 'A',
			link: function (scope, element, attrs) {
				var intervalPromise = null;
				var lastMouseEvent = null;

				var config = {
					verticalScroll: attrs.verticalScroll || true,
					horizontalScroll: attrs.horizontalScroll || true,
					activationDistance: attrs.activationDistance || 75,
					scrollDistance: attrs.scrollDistance || 15
				};


				var reqAnimFrame = (function () {
					return window.requestAnimationFrame ||
						window.webkitRequestAnimationFrame ||
						window.mozRequestAnimationFrame ||
						window.oRequestAnimationFrame ||
						window.msRequestAnimationFrame ||
						function ( /* function FrameRequestCallback */ callback, /* DOMElement Element */ element) {
							window.setTimeout(callback, 1000 / 60);
						};
				})();

				var animationIsOn = false;
				var createInterval = function () {
					animationIsOn = true;

					function nextFrame(callback) {
						var args = Array.prototype.slice.call(arguments);
						if (animationIsOn) {
							reqAnimFrame(function () {
								$rootScope.$apply(function () {
									callback.apply(null, args);
									nextFrame(callback);
								});
							})
						}
					}

					nextFrame(function () {
						if (!lastMouseEvent) return;

						var viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
						var viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

						var scrollX = 0;
						var scrollY = 0;

						if (config.horizontalScroll) {
							// If horizontal scrolling is active.
							if (lastMouseEvent.clientX < config.activationDistance) {
								// If the mouse is on the left of the viewport within the activation distance.
								scrollX = -config.scrollDistance;
							}
							else if (lastMouseEvent.clientX > viewportWidth - config.activationDistance) {
								// If the mouse is on the right of the viewport within the activation distance.
								scrollX = config.scrollDistance;
							}
						}

						if (config.verticalScroll) {
							// If vertical scrolling is active.
							if (lastMouseEvent.clientY < config.activationDistance) {
								// If the mouse is on the top of the viewport within the activation distance.
								scrollY = -config.scrollDistance;
							}
							else if (lastMouseEvent.clientY > viewportHeight - config.activationDistance) {
								// If the mouse is on the bottom of the viewport within the activation distance.
								scrollY = config.scrollDistance;
							}
						}



						if (scrollX !== 0 || scrollY !== 0) {
							// Record the current scroll position.
							var currentScrollLeft = ($window.pageXOffset || $document[0].documentElement.scrollLeft);
							var currentScrollTop = ($window.pageYOffset || $document[0].documentElement.scrollTop);

							// Remove the transformation from the element, scroll the window by the scroll distance
							// record how far we scrolled, then reapply the element transformation.
							var elementTransform = element.css('transform');
							element.css('transform', 'initial');

							$window.scrollBy(scrollX, scrollY);

							var horizontalScrollAmount = ($window.pageXOffset || $document[0].documentElement.scrollLeft) - currentScrollLeft;
							var verticalScrollAmount = ($window.pageYOffset || $document[0].documentElement.scrollTop) - currentScrollTop;

							element.css('transform', elementTransform);

							lastMouseEvent.pageX += horizontalScrollAmount;
							lastMouseEvent.pageY += verticalScrollAmount;

							$rootScope.$emit('draggable:_triggerHandlerMove', lastMouseEvent);
						}

					});
				};

				var clearInterval = function () {
					animationIsOn = false;
				};

				scope.$on('draggable:start', function (event, obj) {
					// Ignore this event if it's not for this element.
					if (obj.element[0] !== element[0]) return;

					if (!animationIsOn) createInterval();
				});

				scope.$on('draggable:end', function (event, obj) {
					// Ignore this event if it's not for this element.
					if (obj.element[0] !== element[0]) return;

					if (animationIsOn) clearInterval();
				});

				scope.$on('draggable:move', function (event, obj) {
					// Ignore this event if it's not for this element.
					if (obj.element[0] !== element[0]) return;

					lastMouseEvent = obj.event;
				});
			}
		};
	}]);

/*! ngstorage 0.3.10 | Copyright (c) 2015 Gias Kay Lee | MIT License */!function (a, b) { "use strict"; "function" == typeof define && define.amd ? define(["angular"], b) : a.hasOwnProperty("angular") ? b(a.angular) : "object" == typeof exports && (module.exports = b(require("angular"))) }(this, function (a) { "use strict"; function b(b) { return function () { var c = "ngStorage-"; this.setKeyPrefix = function (a) { if ("string" != typeof a) throw new TypeError("[ngStorage] - " + b + "Provider.setKeyPrefix() expects a String."); c = a }; var d = a.toJson, e = a.fromJson; this.setSerializer = function (a) { if ("function" != typeof a) throw new TypeError("[ngStorage] - " + b + "Provider.setSerializer expects a function."); d = a }, this.setDeserializer = function (a) { if ("function" != typeof a) throw new TypeError("[ngStorage] - " + b + "Provider.setDeserializer expects a function."); e = a }, this.get = function (a) { return e(window[b].getItem(c + a)) }, this.set = function (a, e) { return window[b].setItem(c + a, d(e)) }, this.$get = ["$rootScope", "$window", "$log", "$timeout", "$document", function (f, g, h, i, j) { function k(a) { var b; try { b = g[a] } catch (c) { b = !1 } if (b && "localStorage" === a) { var d = "__" + Math.round(1e7 * Math.random()); try { localStorage.setItem(d, d), localStorage.removeItem(d) } catch (c) { b = !1 } } return b } var l, m, n = c.length, o = k(b) || (h.warn("This browser does not support Web Storage!"), { setItem: a.noop, getItem: a.noop, removeItem: a.noop }), p = { $default: function (b) { for (var c in b) a.isDefined(p[c]) || (p[c] = a.copy(b[c])); return p.$sync(), p }, $reset: function (a) { for (var b in p) "$" === b[0] || delete p[b] && o.removeItem(c + b); return p.$default(a) }, $sync: function () { for (var a, b = 0, d = o.length; d > b; b++)(a = o.key(b)) && c === a.slice(0, n) && (p[a.slice(n)] = e(o.getItem(a))) }, $apply: function () { var b; if (m = null, !a.equals(p, l)) { b = a.copy(l), a.forEach(p, function (e, f) { a.isDefined(e) && "$" !== f[0] && (o.setItem(c + f, d(e)), delete b[f]) }); for (var e in b) o.removeItem(c + e); l = a.copy(p) } } }; return p.$sync(), l = a.copy(p), f.$watch(function () { m || (m = i(p.$apply, 100, !1)) }), g.addEventListener && g.addEventListener("storage", function (b) { if (b.key) { var d = j[0]; d.hasFocus && d.hasFocus() || c !== b.key.slice(0, n) || (b.newValue ? p[b.key.slice(n)] = e(b.newValue) : delete p[b.key.slice(n)], l = a.copy(p), f.$apply()) } }), g.addEventListener && g.addEventListener("beforeunload", function () { p.$apply() }), p }] } } return a = a && a.module ? a : window.angular, a.module("ngStorage", []).provider("$localStorage", b("localStorage")).provider("$sessionStorage", b("sessionStorage")) });
/**
 * Owl Carousel v2.2.1
 * Copyright 2013-2017 David Deutsch
 * Licensed under  ()
 */
!function (a, b, c, d) { function e(b, c) { this.settings = null, this.options = a.extend({}, e.Defaults, c), this.$element = a(b), this._handlers = {}, this._plugins = {}, this._supress = {}, this._current = null, this._speed = null, this._coordinates = [], this._breakpoint = null, this._width = null, this._items = [], this._clones = [], this._mergers = [], this._widths = [], this._invalidated = {}, this._pipe = [], this._drag = { time: null, target: null, pointer: null, stage: { start: null, current: null }, direction: null }, this._states = { current: {}, tags: { initializing: ["busy"], animating: ["busy"], dragging: ["interacting"] } }, a.each(["onResize", "onThrottledResize"], a.proxy(function (b, c) { this._handlers[c] = a.proxy(this[c], this) }, this)), a.each(e.Plugins, a.proxy(function (a, b) { this._plugins[a.charAt(0).toLowerCase() + a.slice(1)] = new b(this) }, this)), a.each(e.Workers, a.proxy(function (b, c) { this._pipe.push({ filter: c.filter, run: a.proxy(c.run, this) }) }, this)), this.setup(), this.initialize() } e.Defaults = { items: 3, loop: !1, center: !1, rewind: !1, mouseDrag: !0, touchDrag: !0, pullDrag: !0, freeDrag: !1, margin: 0, stagePadding: 0, merge: !1, mergeFit: !0, autoWidth: !1, startPosition: 0, rtl: !1, smartSpeed: 250, fluidSpeed: !1, dragEndSpeed: !1, responsive: {}, responsiveRefreshRate: 200, responsiveBaseElement: b, fallbackEasing: "swing", info: !1, nestedItemSelector: !1, itemElement: "div", stageElement: "div", refreshClass: "owl-refresh", loadedClass: "owl-loaded", loadingClass: "owl-loading", rtlClass: "owl-rtl", responsiveClass: "owl-responsive", dragClass: "owl-drag", itemClass: "owl-item", stageClass: "owl-stage", stageOuterClass: "owl-stage-outer", grabClass: "owl-grab" }, e.Width = { Default: "default", Inner: "inner", Outer: "outer" }, e.Type = { Event: "event", State: "state" }, e.Plugins = {}, e.Workers = [{ filter: ["width", "settings"], run: function () { this._width = this.$element.width() } }, { filter: ["width", "items", "settings"], run: function (a) { a.current = this._items && this._items[this.relative(this._current)] } }, { filter: ["items", "settings"], run: function () { this.$stage.children(".cloned").remove() } }, { filter: ["width", "items", "settings"], run: function (a) { var b = this.settings.margin || "", c = !this.settings.autoWidth, d = this.settings.rtl, e = { width: "auto", "margin-left": d ? b : "", "margin-right": d ? "" : b }; !c && this.$stage.children().css(e), a.css = e } }, { filter: ["width", "items", "settings"], run: function (a) { var b = (this.width() / this.settings.items).toFixed(3) - this.settings.margin, c = null, d = this._items.length, e = !this.settings.autoWidth, f = []; for (a.items = { merge: !1, width: b }; d--;)c = this._mergers[d], c = this.settings.mergeFit && Math.min(c, this.settings.items) || c, a.items.merge = c > 1 || a.items.merge, f[d] = e ? b * c : this._items[d].width(); this._widths = f } }, { filter: ["items", "settings"], run: function () { var b = [], c = this._items, d = this.settings, e = Math.max(2 * d.items, 4), f = 2 * Math.ceil(c.length / 2), g = d.loop && c.length ? d.rewind ? e : Math.max(e, f) : 0, h = "", i = ""; for (g /= 2; g--;)b.push(this.normalize(b.length / 2, !0)), h += c[b[b.length - 1]][0].outerHTML, b.push(this.normalize(c.length - 1 - (b.length - 1) / 2, !0)), i = c[b[b.length - 1]][0].outerHTML + i; this._clones = b, a(h).addClass("cloned").appendTo(this.$stage), a(i).addClass("cloned").prependTo(this.$stage) } }, { filter: ["width", "items", "settings"], run: function () { for (var a = this.settings.rtl ? 1 : -1, b = this._clones.length + this._items.length, c = -1, d = 0, e = 0, f = []; ++c < b;)d = f[c - 1] || 0, e = this._widths[this.relative(c)] + this.settings.margin, f.push(d + e * a); this._coordinates = f } }, { filter: ["width", "items", "settings"], run: function () { var a = this.settings.stagePadding, b = this._coordinates, c = { width: Math.ceil(Math.abs(b[b.length - 1])) + 2 * a, "padding-left": a || "", "padding-right": a || "" }; this.$stage.css(c) } }, { filter: ["width", "items", "settings"], run: function (a) { var b = this._coordinates.length, c = !this.settings.autoWidth, d = this.$stage.children(); if (c && a.items.merge) for (; b--;)a.css.width = this._widths[this.relative(b)], d.eq(b).css(a.css); else c && (a.css.width = a.items.width, d.css(a.css)) } }, { filter: ["items"], run: function () { this._coordinates.length < 1 && this.$stage.removeAttr("style") } }, { filter: ["width", "items", "settings"], run: function (a) { a.current = a.current ? this.$stage.children().index(a.current) : 0, a.current = Math.max(this.minimum(), Math.min(this.maximum(), a.current)), this.reset(a.current) } }, { filter: ["position"], run: function () { this.animate(this.coordinates(this._current)) } }, { filter: ["width", "position", "items", "settings"], run: function () { var a, b, c, d, e = this.settings.rtl ? 1 : -1, f = 2 * this.settings.stagePadding, g = this.coordinates(this.current()) + f, h = g + this.width() * e, i = []; for (c = 0, d = this._coordinates.length; c < d; c++)a = this._coordinates[c - 1] || 0, b = Math.abs(this._coordinates[c]) + f * e, (this.op(a, "<=", g) && this.op(a, ">", h) || this.op(b, "<", g) && this.op(b, ">", h)) && i.push(c); this.$stage.children(".active").removeClass("active"), this.$stage.children(":eq(" + i.join("), :eq(") + ")").addClass("active"), this.settings.center && (this.$stage.children(".center").removeClass("center"), this.$stage.children().eq(this.current()).addClass("center")) } }], e.prototype.initialize = function () { if (this.enter("initializing"), this.trigger("initialize"), this.$element.toggleClass(this.settings.rtlClass, this.settings.rtl), this.settings.autoWidth && !this.is("pre-loading")) { var b, c, e; b = this.$element.find("img"), c = this.settings.nestedItemSelector ? "." + this.settings.nestedItemSelector : d, e = this.$element.children(c).width(), b.length && e <= 0 && this.preloadAutoWidthImages(b) } this.$element.addClass(this.options.loadingClass), this.$stage = a("<" + this.settings.stageElement + ' class="' + this.settings.stageClass + '"/>').wrap('<div class="' + this.settings.stageOuterClass + '"/>'), this.$element.append(this.$stage.parent()), this.replace(this.$element.children().not(this.$stage.parent())), this.$element.is(":visible") ? this.refresh() : this.invalidate("width"), this.$element.removeClass(this.options.loadingClass).addClass(this.options.loadedClass), this.registerEventHandlers(), this.leave("initializing"), this.trigger("initialized") }, e.prototype.setup = function () { var b = this.viewport(), c = this.options.responsive, d = -1, e = null; c ? (a.each(c, function (a) { a <= b && a > d && (d = Number(a)) }), e = a.extend({}, this.options, c[d]), "function" == typeof e.stagePadding && (e.stagePadding = e.stagePadding()), delete e.responsive, e.responsiveClass && this.$element.attr("class", this.$element.attr("class").replace(new RegExp("(" + this.options.responsiveClass + "-)\\S+\\s", "g"), "$1" + d))) : e = a.extend({}, this.options), this.trigger("change", { property: { name: "settings", value: e } }), this._breakpoint = d, this.settings = e, this.invalidate("settings"), this.trigger("changed", { property: { name: "settings", value: this.settings } }) }, e.prototype.optionsLogic = function () { this.settings.autoWidth && (this.settings.stagePadding = !1, this.settings.merge = !1) }, e.prototype.prepare = function (b) { var c = this.trigger("prepare", { content: b }); return c.data || (c.data = a("<" + this.settings.itemElement + "/>").addClass(this.options.itemClass).append(b)), this.trigger("prepared", { content: c.data }), c.data }, e.prototype.update = function () { for (var b = 0, c = this._pipe.length, d = a.proxy(function (a) { return this[a] }, this._invalidated), e = {}; b < c;)(this._invalidated.all || a.grep(this._pipe[b].filter, d).length > 0) && this._pipe[b].run(e), b++; this._invalidated = {}, !this.is("valid") && this.enter("valid") }, e.prototype.width = function (a) { switch (a = a || e.Width.Default) { case e.Width.Inner: case e.Width.Outer: return this._width; default: return this._width - 2 * this.settings.stagePadding + this.settings.margin } }, e.prototype.refresh = function () { this.enter("refreshing"), this.trigger("refresh"), this.setup(), this.optionsLogic(), this.$element.addClass(this.options.refreshClass), this.update(), this.$element.removeClass(this.options.refreshClass), this.leave("refreshing"), this.trigger("refreshed") }, e.prototype.onThrottledResize = function () { b.clearTimeout(this.resizeTimer), this.resizeTimer = b.setTimeout(this._handlers.onResize, this.settings.responsiveRefreshRate) }, e.prototype.onResize = function () { return !!this._items.length && (this._width !== this.$element.width() && (!!this.$element.is(":visible") && (this.enter("resizing"), this.trigger("resize").isDefaultPrevented() ? (this.leave("resizing"), !1) : (this.invalidate("width"), this.refresh(), this.leave("resizing"), void this.trigger("resized"))))) }, e.prototype.registerEventHandlers = function () { a.support.transition && this.$stage.on(a.support.transition.end + ".owl.core", a.proxy(this.onTransitionEnd, this)), this.settings.responsive !== !1 && this.on(b, "resize", this._handlers.onThrottledResize), this.settings.mouseDrag && (this.$element.addClass(this.options.dragClass), this.$stage.on("mousedown.owl.core", a.proxy(this.onDragStart, this)), this.$stage.on("dragstart.owl.core selectstart.owl.core", function () { return !1 })), this.settings.touchDrag && (this.$stage.on("touchstart.owl.core", a.proxy(this.onDragStart, this)), this.$stage.on("touchcancel.owl.core", a.proxy(this.onDragEnd, this))) }, e.prototype.onDragStart = function (b) { var d = null; 3 !== b.which && (a.support.transform ? (d = this.$stage.css("transform").replace(/.*\(|\)| /g, "").split(","), d = { x: d[16 === d.length ? 12 : 4], y: d[16 === d.length ? 13 : 5] }) : (d = this.$stage.position(), d = { x: this.settings.rtl ? d.left + this.$stage.width() - this.width() + this.settings.margin : d.left, y: d.top }), this.is("animating") && (a.support.transform ? this.animate(d.x) : this.$stage.stop(), this.invalidate("position")), this.$element.toggleClass(this.options.grabClass, "mousedown" === b.type), this.speed(0), this._drag.time = (new Date).getTime(), this._drag.target = a(b.target), this._drag.stage.start = d, this._drag.stage.current = d, this._drag.pointer = this.pointer(b), a(c).on("mouseup.owl.core touchend.owl.core", a.proxy(this.onDragEnd, this)), a(c).one("mousemove.owl.core touchmove.owl.core", a.proxy(function (b) { var d = this.difference(this._drag.pointer, this.pointer(b)); a(c).on("mousemove.owl.core touchmove.owl.core", a.proxy(this.onDragMove, this)), Math.abs(d.x) < Math.abs(d.y) && this.is("valid") || (b.preventDefault(), this.enter("dragging"), this.trigger("drag")) }, this))) }, e.prototype.onDragMove = function (a) { var b = null, c = null, d = null, e = this.difference(this._drag.pointer, this.pointer(a)), f = this.difference(this._drag.stage.start, e); this.is("dragging") && (a.preventDefault(), this.settings.loop ? (b = this.coordinates(this.minimum()), c = this.coordinates(this.maximum() + 1) - b, f.x = ((f.x - b) % c + c) % c + b) : (b = this.settings.rtl ? this.coordinates(this.maximum()) : this.coordinates(this.minimum()), c = this.settings.rtl ? this.coordinates(this.minimum()) : this.coordinates(this.maximum()), d = this.settings.pullDrag ? -1 * e.x / 5 : 0, f.x = Math.max(Math.min(f.x, b + d), c + d)), this._drag.stage.current = f, this.animate(f.x)) }, e.prototype.onDragEnd = function (b) { var d = this.difference(this._drag.pointer, this.pointer(b)), e = this._drag.stage.current, f = d.x > 0 ^ this.settings.rtl ? "left" : "right"; a(c).off(".owl.core"), this.$element.removeClass(this.options.grabClass), (0 !== d.x && this.is("dragging") || !this.is("valid")) && (this.speed(this.settings.dragEndSpeed || this.settings.smartSpeed), this.current(this.closest(e.x, 0 !== d.x ? f : this._drag.direction)), this.invalidate("position"), this.update(), this._drag.direction = f, (Math.abs(d.x) > 3 || (new Date).getTime() - this._drag.time > 300) && this._drag.target.one("click.owl.core", function () { return !1 })), this.is("dragging") && (this.leave("dragging"), this.trigger("dragged")) }, e.prototype.closest = function (b, c) { var d = -1, e = 30, f = this.width(), g = this.coordinates(); return this.settings.freeDrag || a.each(g, a.proxy(function (a, h) { return "left" === c && b > h - e && b < h + e ? d = a : "right" === c && b > h - f - e && b < h - f + e ? d = a + 1 : this.op(b, "<", h) && this.op(b, ">", g[a + 1] || h - f) && (d = "left" === c ? a + 1 : a), d === -1 }, this)), this.settings.loop || (this.op(b, ">", g[this.minimum()]) ? d = b = this.minimum() : this.op(b, "<", g[this.maximum()]) && (d = b = this.maximum())), d }, e.prototype.animate = function (b) { var c = this.speed() > 0; this.is("animating") && this.onTransitionEnd(), c && (this.enter("animating"), this.trigger("translate")), a.support.transform3d && a.support.transition ? this.$stage.css({ transform: "translate3d(" + b + "px,0px,0px)", transition: this.speed() / 1e3 + "s" }) : c ? this.$stage.animate({ left: b + "px" }, this.speed(), this.settings.fallbackEasing, a.proxy(this.onTransitionEnd, this)) : this.$stage.css({ left: b + "px" }) }, e.prototype.is = function (a) { return this._states.current[a] && this._states.current[a] > 0 }, e.prototype.current = function (a) { if (a === d) return this._current; if (0 === this._items.length) return d; if (a = this.normalize(a), this._current !== a) { var b = this.trigger("change", { property: { name: "position", value: a } }); b.data !== d && (a = this.normalize(b.data)), this._current = a, this.invalidate("position"), this.trigger("changed", { property: { name: "position", value: this._current } }) } return this._current }, e.prototype.invalidate = function (b) { return "string" === a.type(b) && (this._invalidated[b] = !0, this.is("valid") && this.leave("valid")), a.map(this._invalidated, function (a, b) { return b }) }, e.prototype.reset = function (a) { a = this.normalize(a), a !== d && (this._speed = 0, this._current = a, this.suppress(["translate", "translated"]), this.animate(this.coordinates(a)), this.release(["translate", "translated"])) }, e.prototype.normalize = function (a, b) { var c = this._items.length, e = b ? 0 : this._clones.length; return !this.isNumeric(a) || c < 1 ? a = d : (a < 0 || a >= c + e) && (a = ((a - e / 2) % c + c) % c + e / 2), a }, e.prototype.relative = function (a) { return a -= this._clones.length / 2, this.normalize(a, !0) }, e.prototype.maximum = function (a) { var b, c, d, e = this.settings, f = this._coordinates.length; if (e.loop) f = this._clones.length / 2 + this._items.length - 1; else if (e.autoWidth || e.merge) { for (b = this._items.length, c = this._items[--b].width(), d = this.$element.width(); b-- && (c += this._items[b].width() + this.settings.margin, !(c > d));); f = b + 1 } else f = e.center ? this._items.length - 1 : this._items.length - e.items; return a && (f -= this._clones.length / 2), Math.max(f, 0) }, e.prototype.minimum = function (a) { return a ? 0 : this._clones.length / 2 }, e.prototype.items = function (a) { return a === d ? this._items.slice() : (a = this.normalize(a, !0), this._items[a]) }, e.prototype.mergers = function (a) { return a === d ? this._mergers.slice() : (a = this.normalize(a, !0), this._mergers[a]) }, e.prototype.clones = function (b) { var c = this._clones.length / 2, e = c + this._items.length, f = function (a) { return a % 2 === 0 ? e + a / 2 : c - (a + 1) / 2 }; return b === d ? a.map(this._clones, function (a, b) { return f(b) }) : a.map(this._clones, function (a, c) { return a === b ? f(c) : null }) }, e.prototype.speed = function (a) { return a !== d && (this._speed = a), this._speed }, e.prototype.coordinates = function (b) { var c, e = 1, f = b - 1; return b === d ? a.map(this._coordinates, a.proxy(function (a, b) { return this.coordinates(b) }, this)) : (this.settings.center ? (this.settings.rtl && (e = -1, f = b + 1), c = this._coordinates[b], c += (this.width() - c + (this._coordinates[f] || 0)) / 2 * e) : c = this._coordinates[f] || 0, c = Math.ceil(c)) }, e.prototype.duration = function (a, b, c) { return 0 === c ? 0 : Math.min(Math.max(Math.abs(b - a), 1), 6) * Math.abs(c || this.settings.smartSpeed) }, e.prototype.to = function (a, b) { var c = this.current(), d = null, e = a - this.relative(c), f = (e > 0) - (e < 0), g = this._items.length, h = this.minimum(), i = this.maximum(); this.settings.loop ? (!this.settings.rewind && Math.abs(e) > g / 2 && (e += f * -1 * g), a = c + e, d = ((a - h) % g + g) % g + h, d !== a && d - e <= i && d - e > 0 && (c = d - e, a = d, this.reset(c))) : this.settings.rewind ? (i += 1, a = (a % i + i) % i) : a = Math.max(h, Math.min(i, a)), this.speed(this.duration(c, a, b)), this.current(a), this.$element.is(":visible") && this.update() }, e.prototype.next = function (a) { a = a || !1, this.to(this.relative(this.current()) + 1, a) }, e.prototype.prev = function (a) { a = a || !1, this.to(this.relative(this.current()) - 1, a) }, e.prototype.onTransitionEnd = function (a) { if (a !== d && (a.stopPropagation(), (a.target || a.srcElement || a.originalTarget) !== this.$stage.get(0))) return !1; this.leave("animating"), this.trigger("translated") }, e.prototype.viewport = function () { var d; return this.options.responsiveBaseElement !== b ? d = a(this.options.responsiveBaseElement).width() : b.innerWidth ? d = b.innerWidth : c.documentElement && c.documentElement.clientWidth ? d = c.documentElement.clientWidth : console.warn("Can not detect viewport width."), d }, e.prototype.replace = function (b) { this.$stage.empty(), this._items = [], b && (b = b instanceof jQuery ? b : a(b)), this.settings.nestedItemSelector && (b = b.find("." + this.settings.nestedItemSelector)), b.filter(function () { return 1 === this.nodeType }).each(a.proxy(function (a, b) { b = this.prepare(b), this.$stage.append(b), this._items.push(b), this._mergers.push(1 * b.find("[data-merge]").addBack("[data-merge]").attr("data-merge") || 1) }, this)), this.reset(this.isNumeric(this.settings.startPosition) ? this.settings.startPosition : 0), this.invalidate("items") }, e.prototype.add = function (b, c) { var e = this.relative(this._current); c = c === d ? this._items.length : this.normalize(c, !0), b = b instanceof jQuery ? b : a(b), this.trigger("add", { content: b, position: c }), b = this.prepare(b), 0 === this._items.length || c === this._items.length ? (0 === this._items.length && this.$stage.append(b), 0 !== this._items.length && this._items[c - 1].after(b), this._items.push(b), this._mergers.push(1 * b.find("[data-merge]").addBack("[data-merge]").attr("data-merge") || 1)) : (this._items[c].before(b), this._items.splice(c, 0, b), this._mergers.splice(c, 0, 1 * b.find("[data-merge]").addBack("[data-merge]").attr("data-merge") || 1)), this._items[e] && this.reset(this._items[e].index()), this.invalidate("items"), this.trigger("added", { content: b, position: c }) }, e.prototype.remove = function (a) { a = this.normalize(a, !0), a !== d && (this.trigger("remove", { content: this._items[a], position: a }), this._items[a].remove(), this._items.splice(a, 1), this._mergers.splice(a, 1), this.invalidate("items"), this.trigger("removed", { content: null, position: a })) }, e.prototype.preloadAutoWidthImages = function (b) { b.each(a.proxy(function (b, c) { this.enter("pre-loading"), c = a(c), a(new Image).one("load", a.proxy(function (a) { c.attr("src", a.target.src), c.css("opacity", 1), this.leave("pre-loading"), !this.is("pre-loading") && !this.is("initializing") && this.refresh() }, this)).attr("src", c.attr("src") || c.attr("data-src") || c.attr("data-src-retina")) }, this)) }, e.prototype.destroy = function () { this.$element.off(".owl.core"), this.$stage.off(".owl.core"), a(c).off(".owl.core"), this.settings.responsive !== !1 && (b.clearTimeout(this.resizeTimer), this.off(b, "resize", this._handlers.onThrottledResize)); for (var d in this._plugins) this._plugins[d].destroy(); this.$stage.children(".cloned").remove(), this.$stage.unwrap(), this.$stage.children().contents().unwrap(), this.$stage.children().unwrap(), this.$element.removeClass(this.options.refreshClass).removeClass(this.options.loadingClass).removeClass(this.options.loadedClass).removeClass(this.options.rtlClass).removeClass(this.options.dragClass).removeClass(this.options.grabClass).attr("class", this.$element.attr("class").replace(new RegExp(this.options.responsiveClass + "-\\S+\\s", "g"), "")).removeData("owl.carousel") }, e.prototype.op = function (a, b, c) { var d = this.settings.rtl; switch (b) { case "<": return d ? a > c : a < c; case ">": return d ? a < c : a > c; case ">=": return d ? a <= c : a >= c; case "<=": return d ? a >= c : a <= c } }, e.prototype.on = function (a, b, c, d) { a.addEventListener ? a.addEventListener(b, c, d) : a.attachEvent && a.attachEvent("on" + b, c) }, e.prototype.off = function (a, b, c, d) { a.removeEventListener ? a.removeEventListener(b, c, d) : a.detachEvent && a.detachEvent("on" + b, c) }, e.prototype.trigger = function (b, c, d, f, g) { var h = { item: { count: this._items.length, index: this.current() } }, i = a.camelCase(a.grep(["on", b, d], function (a) { return a }).join("-").toLowerCase()), j = a.Event([b, "owl", d || "carousel"].join(".").toLowerCase(), a.extend({ relatedTarget: this }, h, c)); return this._supress[b] || (a.each(this._plugins, function (a, b) { b.onTrigger && b.onTrigger(j) }), this.register({ type: e.Type.Event, name: b }), this.$element.trigger(j), this.settings && "function" == typeof this.settings[i] && this.settings[i].call(this, j)), j }, e.prototype.enter = function (b) { a.each([b].concat(this._states.tags[b] || []), a.proxy(function (a, b) { this._states.current[b] === d && (this._states.current[b] = 0), this._states.current[b]++ }, this)) }, e.prototype.leave = function (b) { a.each([b].concat(this._states.tags[b] || []), a.proxy(function (a, b) { this._states.current[b]-- }, this)) }, e.prototype.register = function (b) { if (b.type === e.Type.Event) { if (a.event.special[b.name] || (a.event.special[b.name] = {}), !a.event.special[b.name].owl) { var c = a.event.special[b.name]._default; a.event.special[b.name]._default = function (a) { return !c || !c.apply || a.namespace && a.namespace.indexOf("owl") !== -1 ? a.namespace && a.namespace.indexOf("owl") > -1 : c.apply(this, arguments) }, a.event.special[b.name].owl = !0 } } else b.type === e.Type.State && (this._states.tags[b.name] ? this._states.tags[b.name] = this._states.tags[b.name].concat(b.tags) : this._states.tags[b.name] = b.tags, this._states.tags[b.name] = a.grep(this._states.tags[b.name], a.proxy(function (c, d) { return a.inArray(c, this._states.tags[b.name]) === d }, this))) }, e.prototype.suppress = function (b) { a.each(b, a.proxy(function (a, b) { this._supress[b] = !0 }, this)) }, e.prototype.release = function (b) { a.each(b, a.proxy(function (a, b) { delete this._supress[b] }, this)) }, e.prototype.pointer = function (a) { var c = { x: null, y: null }; return a = a.originalEvent || a || b.event, a = a.touches && a.touches.length ? a.touches[0] : a.changedTouches && a.changedTouches.length ? a.changedTouches[0] : a, a.pageX ? (c.x = a.pageX, c.y = a.pageY) : (c.x = a.clientX, c.y = a.clientY), c }, e.prototype.isNumeric = function (a) { return !isNaN(parseFloat(a)) }, e.prototype.difference = function (a, b) { return { x: a.x - b.x, y: a.y - b.y } }, a.fn.owlCarousel = function (b) { var c = Array.prototype.slice.call(arguments, 1); return this.each(function () { var d = a(this), f = d.data("owl.carousel"); f || (f = new e(this, "object" == typeof b && b), d.data("owl.carousel", f), a.each(["next", "prev", "to", "destroy", "refresh", "replace", "add", "remove"], function (b, c) { f.register({ type: e.Type.Event, name: c }), f.$element.on(c + ".owl.carousel.core", a.proxy(function (a) { a.namespace && a.relatedTarget !== this && (this.suppress([c]), f[c].apply(this, [].slice.call(arguments, 1)), this.release([c])) }, f)) })), "string" == typeof b && "_" !== b.charAt(0) && f[b].apply(f, c) }) }, a.fn.owlCarousel.Constructor = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { var e = function (b) { this._core = b, this._interval = null, this._visible = null, this._handlers = { "initialized.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.autoRefresh && this.watch() }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this._core.$element.on(this._handlers) }; e.Defaults = { autoRefresh: !0, autoRefreshInterval: 500 }, e.prototype.watch = function () { this._interval || (this._visible = this._core.$element.is(":visible"), this._interval = b.setInterval(a.proxy(this.refresh, this), this._core.settings.autoRefreshInterval)) }, e.prototype.refresh = function () { this._core.$element.is(":visible") !== this._visible && (this._visible = !this._visible, this._core.$element.toggleClass("owl-hidden", !this._visible), this._visible && this._core.invalidate("width") && this._core.refresh()) }, e.prototype.destroy = function () { var a, c; b.clearInterval(this._interval); for (a in this._handlers) this._core.$element.off(a, this._handlers[a]); for (c in Object.getOwnPropertyNames(this)) "function" != typeof this[c] && (this[c] = null) }, a.fn.owlCarousel.Constructor.Plugins.AutoRefresh = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { var e = function (b) { this._core = b, this._loaded = [], this._handlers = { "initialized.owl.carousel change.owl.carousel resized.owl.carousel": a.proxy(function (b) { if (b.namespace && this._core.settings && this._core.settings.lazyLoad && (b.property && "position" == b.property.name || "initialized" == b.type)) for (var c = this._core.settings, e = c.center && Math.ceil(c.items / 2) || c.items, f = c.center && e * -1 || 0, g = (b.property && b.property.value !== d ? b.property.value : this._core.current()) + f, h = this._core.clones().length, i = a.proxy(function (a, b) { this.load(b) }, this); f++ < e;)this.load(h / 2 + this._core.relative(g)), h && a.each(this._core.clones(this._core.relative(g)), i), g++ }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this._core.$element.on(this._handlers) }; e.Defaults = { lazyLoad: !1 }, e.prototype.load = function (c) { var d = this._core.$stage.children().eq(c), e = d && d.find(".owl-lazy"); !e || a.inArray(d.get(0), this._loaded) > -1 || (e.each(a.proxy(function (c, d) { var e, f = a(d), g = b.devicePixelRatio > 1 && f.attr("data-src-retina") || f.attr("data-src"); this._core.trigger("load", { element: f, url: g }, "lazy"), f.is("img") ? f.one("load.owl.lazy", a.proxy(function () { f.css("opacity", 1), this._core.trigger("loaded", { element: f, url: g }, "lazy") }, this)).attr("src", g) : (e = new Image, e.onload = a.proxy(function () { f.css({ "background-image": 'url("' + g + '")', opacity: "1" }), this._core.trigger("loaded", { element: f, url: g }, "lazy") }, this), e.src = g) }, this)), this._loaded.push(d.get(0))) }, e.prototype.destroy = function () { var a, b; for (a in this.handlers) this._core.$element.off(a, this.handlers[a]); for (b in Object.getOwnPropertyNames(this)) "function" != typeof this[b] && (this[b] = null) }, a.fn.owlCarousel.Constructor.Plugins.Lazy = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { var e = function (b) { this._core = b, this._handlers = { "initialized.owl.carousel refreshed.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.autoHeight && this.update() }, this), "changed.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.autoHeight && "position" == a.property.name && this.update() }, this), "loaded.owl.lazy": a.proxy(function (a) { a.namespace && this._core.settings.autoHeight && a.element.closest("." + this._core.settings.itemClass).index() === this._core.current() && this.update() }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this._core.$element.on(this._handlers) }; e.Defaults = { autoHeight: !1, autoHeightClass: "owl-height" }, e.prototype.update = function () { var b = this._core._current, c = b + this._core.settings.items, d = this._core.$stage.children().toArray().slice(b, c), e = [], f = 0; a.each(d, function (b, c) { e.push(a(c).height()) }), f = Math.max.apply(null, e), this._core.$stage.parent().height(f).addClass(this._core.settings.autoHeightClass) }, e.prototype.destroy = function () { var a, b; for (a in this._handlers) this._core.$element.off(a, this._handlers[a]); for (b in Object.getOwnPropertyNames(this)) "function" != typeof this[b] && (this[b] = null) }, a.fn.owlCarousel.Constructor.Plugins.AutoHeight = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { var e = function (b) { this._core = b, this._videos = {}, this._playing = null, this._handlers = { "initialized.owl.carousel": a.proxy(function (a) { a.namespace && this._core.register({ type: "state", name: "playing", tags: ["interacting"] }) }, this), "resize.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.video && this.isInFullScreen() && a.preventDefault() }, this), "refreshed.owl.carousel": a.proxy(function (a) { a.namespace && this._core.is("resizing") && this._core.$stage.find(".cloned .owl-video-frame").remove() }, this), "changed.owl.carousel": a.proxy(function (a) { a.namespace && "position" === a.property.name && this._playing && this.stop() }, this), "prepared.owl.carousel": a.proxy(function (b) { if (b.namespace) { var c = a(b.content).find(".owl-video"); c.length && (c.css("display", "none"), this.fetch(c, a(b.content))) } }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this._core.$element.on(this._handlers), this._core.$element.on("click.owl.video", ".owl-video-play-icon", a.proxy(function (a) { this.play(a) }, this)) }; e.Defaults = { video: !1, videoHeight: !1, videoWidth: !1 }, e.prototype.fetch = function (a, b) { var c = function () { return a.attr("data-vimeo-id") ? "vimeo" : a.attr("data-vzaar-id") ? "vzaar" : "youtube" }(), d = a.attr("data-vimeo-id") || a.attr("data-youtube-id") || a.attr("data-vzaar-id"), e = a.attr("data-width") || this._core.settings.videoWidth, f = a.attr("data-height") || this._core.settings.videoHeight, g = a.attr("href"); if (!g) throw new Error("Missing video URL."); if (d = g.match(/(http:|https:|)\/\/(player.|www.|app.)?(vimeo\.com|youtu(be\.com|\.be|be\.googleapis\.com)|vzaar\.com)\/(video\/|videos\/|embed\/|channels\/.+\/|groups\/.+\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/), d[3].indexOf("youtu") > -1) c = "youtube"; else if (d[3].indexOf("vimeo") > -1) c = "vimeo"; else { if (!(d[3].indexOf("vzaar") > -1)) throw new Error("Video URL not supported."); c = "vzaar" } d = d[6], this._videos[g] = { type: c, id: d, width: e, height: f }, b.attr("data-video", g), this.thumbnail(a, this._videos[g]) }, e.prototype.thumbnail = function (b, c) { var d, e, f, g = c.width && c.height ? 'style="width:' + c.width + "px;height:" + c.height + 'px;"' : "", h = b.find("img"), i = "src", j = "", k = this._core.settings, l = function (a) { e = '<div class="owl-video-play-icon"></div>', d = k.lazyLoad ? '<div class="owl-video-tn ' + j + '" ' + i + '="' + a + '"></div>' : '<div class="owl-video-tn" style="opacity:1;background-image:url(' + a + ')"></div>', b.after(d), b.after(e) }; if (b.wrap('<div class="owl-video-wrapper"' + g + "></div>"), this._core.settings.lazyLoad && (i = "data-src", j = "owl-lazy"), h.length) return l(h.attr(i)), h.remove(), !1; "youtube" === c.type ? (f = "//img.youtube.com/vi/" + c.id + "/hqdefault.jpg", l(f)) : "vimeo" === c.type ? a.ajax({ type: "GET", url: "//vimeo.com/api/v2/video/" + c.id + ".json", jsonp: "callback", dataType: "jsonp", success: function (a) { f = a[0].thumbnail_large, l(f) } }) : "vzaar" === c.type && a.ajax({ type: "GET", url: "//vzaar.com/api/videos/" + c.id + ".json", jsonp: "callback", dataType: "jsonp", success: function (a) { f = a.framegrab_url, l(f) } }) }, e.prototype.stop = function () { this._core.trigger("stop", null, "video"), this._playing.find(".owl-video-frame").remove(), this._playing.removeClass("owl-video-playing"), this._playing = null, this._core.leave("playing"), this._core.trigger("stopped", null, "video") }, e.prototype.play = function (b) { var c, d = a(b.target), e = d.closest("." + this._core.settings.itemClass), f = this._videos[e.attr("data-video")], g = f.width || "100%", h = f.height || this._core.$stage.height(); this._playing || (this._core.enter("playing"), this._core.trigger("play", null, "video"), e = this._core.items(this._core.relative(e.index())), this._core.reset(e.index()), "youtube" === f.type ? c = '<iframe width="' + g + '" height="' + h + '" src="//www.youtube.com/embed/' + f.id + "?autoplay=1&rel=0&v=" + f.id + '" frameborder="0" allowfullscreen></iframe>' : "vimeo" === f.type ? c = '<iframe src="//player.vimeo.com/video/' + f.id + '?autoplay=1" width="' + g + '" height="' + h + '" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>' : "vzaar" === f.type && (c = '<iframe frameborder="0"height="' + h + '"width="' + g + '" allowfullscreen mozallowfullscreen webkitAllowFullScreen src="//view.vzaar.com/' + f.id + '/player?autoplay=true"></iframe>'), a('<div class="owl-video-frame">' + c + "</div>").insertAfter(e.find(".owl-video")), this._playing = e.addClass("owl-video-playing")) }, e.prototype.isInFullScreen = function () { var b = c.fullscreenElement || c.mozFullScreenElement || c.webkitFullscreenElement; return b && a(b).parent().hasClass("owl-video-frame") }, e.prototype.destroy = function () { var a, b; this._core.$element.off("click.owl.video"); for (a in this._handlers) this._core.$element.off(a, this._handlers[a]); for (b in Object.getOwnPropertyNames(this)) "function" != typeof this[b] && (this[b] = null) }, a.fn.owlCarousel.Constructor.Plugins.Video = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) {
	var e = function (b) { this.core = b, this.core.options = a.extend({}, e.Defaults, this.core.options), this.swapping = !0, this.previous = d, this.next = d, this.handlers = { "change.owl.carousel": a.proxy(function (a) { a.namespace && "position" == a.property.name && (this.previous = this.core.current(), this.next = a.property.value) }, this), "drag.owl.carousel dragged.owl.carousel translated.owl.carousel": a.proxy(function (a) { a.namespace && (this.swapping = "translated" == a.type) }, this), "translate.owl.carousel": a.proxy(function (a) { a.namespace && this.swapping && (this.core.options.animateOut || this.core.options.animateIn) && this.swap() }, this) }, this.core.$element.on(this.handlers) }; e.Defaults = { animateOut: !1, animateIn: !1 }, e.prototype.swap = function () { if (1 === this.core.settings.items && a.support.animation && a.support.transition) { this.core.speed(0); var b, c = a.proxy(this.clear, this), d = this.core.$stage.children().eq(this.previous), e = this.core.$stage.children().eq(this.next), f = this.core.settings.animateIn, g = this.core.settings.animateOut; this.core.current() !== this.previous && (g && (b = this.core.coordinates(this.previous) - this.core.coordinates(this.next), d.one(a.support.animation.end, c).css({ left: b + "px" }).addClass("animated owl-animated-out").addClass(g)), f && e.one(a.support.animation.end, c).addClass("animated owl-animated-in").addClass(f)) } }, e.prototype.clear = function (b) { a(b.target).css({ left: "" }).removeClass("animated owl-animated-out owl-animated-in").removeClass(this.core.settings.animateIn).removeClass(this.core.settings.animateOut), this.core.onTransitionEnd() }, e.prototype.destroy = function () { var a, b; for (a in this.handlers) this.core.$element.off(a, this.handlers[a]); for (b in Object.getOwnPropertyNames(this)) "function" != typeof this[b] && (this[b] = null) },
		a.fn.owlCarousel.Constructor.Plugins.Animate = e
}(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { var e = function (b) { this._core = b, this._timeout = null, this._paused = !1, this._handlers = { "changed.owl.carousel": a.proxy(function (a) { a.namespace && "settings" === a.property.name ? this._core.settings.autoplay ? this.play() : this.stop() : a.namespace && "position" === a.property.name && this._core.settings.autoplay && this._setAutoPlayInterval() }, this), "initialized.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.autoplay && this.play() }, this), "play.owl.autoplay": a.proxy(function (a, b, c) { a.namespace && this.play(b, c) }, this), "stop.owl.autoplay": a.proxy(function (a) { a.namespace && this.stop() }, this), "mouseover.owl.autoplay": a.proxy(function () { this._core.settings.autoplayHoverPause && this._core.is("rotating") && this.pause() }, this), "mouseleave.owl.autoplay": a.proxy(function () { this._core.settings.autoplayHoverPause && this._core.is("rotating") && this.play() }, this), "touchstart.owl.core": a.proxy(function () { this._core.settings.autoplayHoverPause && this._core.is("rotating") && this.pause() }, this), "touchend.owl.core": a.proxy(function () { this._core.settings.autoplayHoverPause && this.play() }, this) }, this._core.$element.on(this._handlers), this._core.options = a.extend({}, e.Defaults, this._core.options) }; e.Defaults = { autoplay: !1, autoplayTimeout: 5e3, autoplayHoverPause: !1, autoplaySpeed: !1 }, e.prototype.play = function (a, b) { this._paused = !1, this._core.is("rotating") || (this._core.enter("rotating"), this._setAutoPlayInterval()) }, e.prototype._getNextTimeout = function (d, e) { return this._timeout && b.clearTimeout(this._timeout), b.setTimeout(a.proxy(function () { this._paused || this._core.is("busy") || this._core.is("interacting") || c.hidden || this._core.next(e || this._core.settings.autoplaySpeed) }, this), d || this._core.settings.autoplayTimeout) }, e.prototype._setAutoPlayInterval = function () { this._timeout = this._getNextTimeout() }, e.prototype.stop = function () { this._core.is("rotating") && (b.clearTimeout(this._timeout), this._core.leave("rotating")) }, e.prototype.pause = function () { this._core.is("rotating") && (this._paused = !0) }, e.prototype.destroy = function () { var a, b; this.stop(); for (a in this._handlers) this._core.$element.off(a, this._handlers[a]); for (b in Object.getOwnPropertyNames(this)) "function" != typeof this[b] && (this[b] = null) }, a.fn.owlCarousel.Constructor.Plugins.autoplay = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { "use strict"; var e = function (b) { this._core = b, this._initialized = !1, this._pages = [], this._controls = {}, this._templates = [], this.$element = this._core.$element, this._overrides = { next: this._core.next, prev: this._core.prev, to: this._core.to }, this._handlers = { "prepared.owl.carousel": a.proxy(function (b) { b.namespace && this._core.settings.dotsData && this._templates.push('<div class="' + this._core.settings.dotClass + '">' + a(b.content).find("[data-dot]").addBack("[data-dot]").attr("data-dot") + "</div>") }, this), "added.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.dotsData && this._templates.splice(a.position, 0, this._templates.pop()) }, this), "remove.owl.carousel": a.proxy(function (a) { a.namespace && this._core.settings.dotsData && this._templates.splice(a.position, 1) }, this), "changed.owl.carousel": a.proxy(function (a) { a.namespace && "position" == a.property.name && this.draw() }, this), "initialized.owl.carousel": a.proxy(function (a) { a.namespace && !this._initialized && (this._core.trigger("initialize", null, "navigation"), this.initialize(), this.update(), this.draw(), this._initialized = !0, this._core.trigger("initialized", null, "navigation")) }, this), "refreshed.owl.carousel": a.proxy(function (a) { a.namespace && this._initialized && (this._core.trigger("refresh", null, "navigation"), this.update(), this.draw(), this._core.trigger("refreshed", null, "navigation")) }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this.$element.on(this._handlers) }; e.Defaults = { nav: !1, navText: ["prev", "next"], navSpeed: !1, navElement: "div", navContainer: !1, navContainerClass: "owl-nav", navClass: ["owl-prev", "owl-next"], slideBy: 1, dotClass: "owl-dot", dotsClass: "owl-dots", dots: !0, dotsEach: !1, dotsData: !1, dotsSpeed: !1, dotsContainer: !1 }, e.prototype.initialize = function () { var b, c = this._core.settings; this._controls.$relative = (c.navContainer ? a(c.navContainer) : a("<div>").addClass(c.navContainerClass).appendTo(this.$element)).addClass("disabled"), this._controls.$previous = a("<" + c.navElement + ">").addClass(c.navClass[0]).html(c.navText[0]).prependTo(this._controls.$relative).on("click", a.proxy(function (a) { this.prev(c.navSpeed) }, this)), this._controls.$next = a("<" + c.navElement + ">").addClass(c.navClass[1]).html(c.navText[1]).appendTo(this._controls.$relative).on("click", a.proxy(function (a) { this.next(c.navSpeed) }, this)), c.dotsData || (this._templates = [a("<div>").addClass(c.dotClass).append(a("<span>")).prop("outerHTML")]), this._controls.$absolute = (c.dotsContainer ? a(c.dotsContainer) : a("<div>").addClass(c.dotsClass).appendTo(this.$element)).addClass("disabled"), this._controls.$absolute.on("click", "div", a.proxy(function (b) { var d = a(b.target).parent().is(this._controls.$absolute) ? a(b.target).index() : a(b.target).parent().index(); b.preventDefault(), this.to(d, c.dotsSpeed) }, this)); for (b in this._overrides) this._core[b] = a.proxy(this[b], this) }, e.prototype.destroy = function () { var a, b, c, d; for (a in this._handlers) this.$element.off(a, this._handlers[a]); for (b in this._controls) this._controls[b].remove(); for (d in this.overides) this._core[d] = this._overrides[d]; for (c in Object.getOwnPropertyNames(this)) "function" != typeof this[c] && (this[c] = null) }, e.prototype.update = function () { var a, b, c, d = this._core.clones().length / 2, e = d + this._core.items().length, f = this._core.maximum(!0), g = this._core.settings, h = g.center || g.autoWidth || g.dotsData ? 1 : g.dotsEach || g.items; if ("page" !== g.slideBy && (g.slideBy = Math.min(g.slideBy, g.items)), g.dots || "page" == g.slideBy) for (this._pages = [], a = d, b = 0, c = 0; a < e; a++) { if (b >= h || 0 === b) { if (this._pages.push({ start: Math.min(f, a - d), end: a - d + h - 1 }), Math.min(f, a - d) === f) break; b = 0, ++c } b += this._core.mergers(this._core.relative(a)) } }, e.prototype.draw = function () { var b, c = this._core.settings, d = this._core.items().length <= c.items, e = this._core.relative(this._core.current()), f = c.loop || c.rewind; this._controls.$relative.toggleClass("disabled", !c.nav || d), c.nav && (this._controls.$previous.toggleClass("disabled", !f && e <= this._core.minimum(!0)), this._controls.$next.toggleClass("disabled", !f && e >= this._core.maximum(!0))), this._controls.$absolute.toggleClass("disabled", !c.dots || d), c.dots && (b = this._pages.length - this._controls.$absolute.children().length, c.dotsData && 0 !== b ? this._controls.$absolute.html(this._templates.join("")) : b > 0 ? this._controls.$absolute.append(new Array(b + 1).join(this._templates[0])) : b < 0 && this._controls.$absolute.children().slice(b).remove(), this._controls.$absolute.find(".active").removeClass("active"), this._controls.$absolute.children().eq(a.inArray(this.current(), this._pages)).addClass("active")) }, e.prototype.onTrigger = function (b) { var c = this._core.settings; b.page = { index: a.inArray(this.current(), this._pages), count: this._pages.length, size: c && (c.center || c.autoWidth || c.dotsData ? 1 : c.dotsEach || c.items) } }, e.prototype.current = function () { var b = this._core.relative(this._core.current()); return a.grep(this._pages, a.proxy(function (a, c) { return a.start <= b && a.end >= b }, this)).pop() }, e.prototype.getPosition = function (b) { var c, d, e = this._core.settings; return "page" == e.slideBy ? (c = a.inArray(this.current(), this._pages), d = this._pages.length, b ? ++c : --c, c = this._pages[(c % d + d) % d].start) : (c = this._core.relative(this._core.current()), d = this._core.items().length, b ? c += e.slideBy : c -= e.slideBy), c }, e.prototype.next = function (b) { a.proxy(this._overrides.to, this._core)(this.getPosition(!0), b) }, e.prototype.prev = function (b) { a.proxy(this._overrides.to, this._core)(this.getPosition(!1), b) }, e.prototype.to = function (b, c, d) { var e; !d && this._pages.length ? (e = this._pages.length, a.proxy(this._overrides.to, this._core)(this._pages[(b % e + e) % e].start, c)) : a.proxy(this._overrides.to, this._core)(b, c) }, a.fn.owlCarousel.Constructor.Plugins.Navigation = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { "use strict"; var e = function (c) { this._core = c, this._hashes = {}, this.$element = this._core.$element, this._handlers = { "initialized.owl.carousel": a.proxy(function (c) { c.namespace && "URLHash" === this._core.settings.startPosition && a(b).trigger("hashchange.owl.navigation") }, this), "prepared.owl.carousel": a.proxy(function (b) { if (b.namespace) { var c = a(b.content).find("[data-hash]").addBack("[data-hash]").attr("data-hash"); if (!c) return; this._hashes[c] = b.content } }, this), "changed.owl.carousel": a.proxy(function (c) { if (c.namespace && "position" === c.property.name) { var d = this._core.items(this._core.relative(this._core.current())), e = a.map(this._hashes, function (a, b) { return a === d ? b : null }).join(); if (!e || b.location.hash.slice(1) === e) return; b.location.hash = e } }, this) }, this._core.options = a.extend({}, e.Defaults, this._core.options), this.$element.on(this._handlers), a(b).on("hashchange.owl.navigation", a.proxy(function (a) { var c = b.location.hash.substring(1), e = this._core.$stage.children(), f = this._hashes[c] && e.index(this._hashes[c]); f !== d && f !== this._core.current() && this._core.to(this._core.relative(f), !1, !0) }, this)) }; e.Defaults = { URLhashListener: !1 }, e.prototype.destroy = function () { var c, d; a(b).off("hashchange.owl.navigation"); for (c in this._handlers) this._core.$element.off(c, this._handlers[c]); for (d in Object.getOwnPropertyNames(this)) "function" != typeof this[d] && (this[d] = null) }, a.fn.owlCarousel.Constructor.Plugins.Hash = e }(window.Zepto || window.jQuery, window, document), function (a, b, c, d) { function e(b, c) { var e = !1, f = b.charAt(0).toUpperCase() + b.slice(1); return a.each((b + " " + h.join(f + " ") + f).split(" "), function (a, b) { if (g[b] !== d) return e = !c || b, !1 }), e } function f(a) { return e(a, !0) } var g = a("<support>").get(0).style, h = "Webkit Moz O ms".split(" "), i = { transition: { end: { WebkitTransition: "webkitTransitionEnd", MozTransition: "transitionend", OTransition: "oTransitionEnd", transition: "transitionend" } }, animation: { end: { WebkitAnimation: "webkitAnimationEnd", MozAnimation: "animationend", OAnimation: "oAnimationEnd", animation: "animationend" } } }, j = { csstransforms: function () { return !!e("transform") }, csstransforms3d: function () { return !!e("perspective") }, csstransitions: function () { return !!e("transition") }, cssanimations: function () { return !!e("animation") } }; j.csstransitions() && (a.support.transition = new String(f("transition")), a.support.transition.end = i.transition.end[a.support.transition]), j.cssanimations() && (a.support.animation = new String(f("animation")), a.support.animation.end = i.animation.end[a.support.animation]), j.csstransforms() && (a.support.transform = new String(f("transform")), a.support.transform3d = j.csstransforms3d()) }(window.Zepto || window.jQuery, window, document);
/**
 * Copyright (c) 2011-2013 Fabien Cazenave, Mozilla.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to
 * deal in the Software without restriction, including without limitation the
 * rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS
 * IN THE SOFTWARE.
 */
/*
  Additional modifications for PDF.js project:
	- Disables language initialization on page loading;
	- Removes consoleWarn and consoleLog and use console.log/warn directly.
	- Removes window._ assignment.
	- Remove compatibility code for OldIE.
*/

/*jshint browser: true, devel: true, es5: true, globalstrict: true */
'use strict';

document.webL10n = (function (window, document, undefined) {
	var gL10nData = {};
	var gTextData = '';
	var gTextProp = 'textContent';
	var gLanguage = '';
	var gMacros = {};
	var gReadyState = 'loading';


	/**
	 * Synchronously loading l10n resources significantly minimizes flickering
	 * from displaying the app with non-localized strings and then updating the
	 * strings. Although this will block all script execution on this page, we
	 * expect that the l10n resources are available locally on flash-storage.
	 *
	 * As synchronous XHR is generally considered as a bad idea, we're still
	 * loading l10n resources asynchronously -- but we keep this in a setting,
	 * just in case... and applications using this library should hide their
	 * content until the `localized' event happens.
	 */

	var gAsyncResourceLoading = true; // read-only


	/**
	 * DOM helpers for the so-called "HTML API".
	 *
	 * These functions are written for modern browsers. For old versions of IE,
	 * they're overridden in the 'startup' section at the end of this file.
	 */

	function getL10nResourceLinks() {
		return document.querySelectorAll('link[type="application/l10n"]');
	}

	function getL10nDictionary() {
		var script = document.querySelector('script[type="application/l10n"]');
		// TODO: support multiple and external JSON dictionaries
		return script ? JSON.parse(script.innerHTML) : null;
	}

	function getTranslatableChildren(element) {
		return element ? element.querySelectorAll('*[data-l10n-id]') : [];
	}

	function getL10nAttributes(element) {
		if (!element)
			return {};

		var l10nId = element.getAttribute('data-l10n-id');
		var l10nArgs = element.getAttribute('data-l10n-args');
		var args = {};
		if (l10nArgs) {
			try {
				args = JSON.parse(l10nArgs);
			} catch (e) {
				console.warn('could not parse arguments for #' + l10nId);
			}
		}
		return { id: l10nId, args: args };
	}

	function fireL10nReadyEvent(lang) {
		var evtObject = document.createEvent('Event');
		evtObject.initEvent('localized', true, false);
		evtObject.language = lang;
		document.dispatchEvent(evtObject);
	}

	function xhrLoadText(url, onSuccess, onFailure) {
		onSuccess = onSuccess || function _onSuccess(data) { };
		onFailure = onFailure || function _onFailure() { };

		var xhr = new XMLHttpRequest();
		xhr.open('GET', url, gAsyncResourceLoading);
		if (xhr.overrideMimeType) {
			xhr.overrideMimeType('text/plain; charset=utf-8');
		}
		xhr.onreadystatechange = function () {
			if (xhr.readyState == 4) {
				if (xhr.status == 200 || xhr.status === 0) {
					onSuccess(xhr.responseText);
				} else {
					onFailure();
				}
			}
		};
		xhr.onerror = onFailure;
		xhr.ontimeout = onFailure;

		// in Firefox OS with the app:// protocol, trying to XHR a non-existing
		// URL will raise an exception here -- hence this ugly try...catch.
		try {
			xhr.send(null);
		} catch (e) {
			onFailure();
		}
	}


	/**
	 * l10n resource parser:
	 *  - reads (async XHR) the l10n resource matching `lang';
	 *  - imports linked resources (synchronously) when specified;
	 *  - parses the text data (fills `gL10nData' and `gTextData');
	 *  - triggers success/failure callbacks when done.
	 *
	 * @param {string} href
	 *    URL of the l10n resource to parse.
	 *
	 * @param {string} lang
	 *    locale (language) to parse. Must be a lowercase string.
	 *
	 * @param {Function} successCallback
	 *    triggered when the l10n resource has been successully parsed.
	 *
	 * @param {Function} failureCallback
	 *    triggered when the an error has occured.
	 *
	 * @return {void}
	 *    uses the following global variables: gL10nData, gTextData, gTextProp.
	 */

	function parseResource(href, lang, successCallback, failureCallback) {
		var baseURL = href.replace(/[^\/]*$/, '') || './';

		// handle escaped characters (backslashes) in a string
		function evalString(text) {
			if (text.lastIndexOf('\\') < 0)
				return text;
			return text.replace(/\\\\/g, '\\')
				.replace(/\\n/g, '\n')
				.replace(/\\r/g, '\r')
				.replace(/\\t/g, '\t')
				.replace(/\\b/g, '\b')
				.replace(/\\f/g, '\f')
				.replace(/\\{/g, '{')
				.replace(/\\}/g, '}')
				.replace(/\\"/g, '"')
				.replace(/\\'/g, "'");
		}

		// parse *.properties text data into an l10n dictionary
		// If gAsyncResourceLoading is false, then the callback will be called
		// synchronously. Otherwise it is called asynchronously.
		function parseProperties(text, parsedPropertiesCallback) {
			var dictionary = {};

			// token expressions
			var reBlank = /^\s*|\s*$/;
			var reComment = /^\s*#|^\s*$/;
			var reSection = /^\s*\[(.*)\]\s*$/;
			var reImport = /^\s*@import\s+url\((.*)\)\s*$/i;
			var reSplit = /^([^=\s]*)\s*=\s*(.+)$/; // TODO: escape EOLs with '\'

			// parse the *.properties file into an associative array
			function parseRawLines(rawText, extendedSyntax, parsedRawLinesCallback) {
				var entries = rawText.replace(reBlank, '').split(/[\r\n]+/);
				var currentLang = '*';
				var genericLang = lang.split('-', 1)[0];
				var skipLang = false;
				var match = '';

				function nextEntry() {
					// Use infinite loop instead of recursion to avoid reaching the
					// maximum recursion limit for content with many lines.
					while (true) {
						if (!entries.length) {
							parsedRawLinesCallback();
							return;
						}
						var line = entries.shift();

						// comment or blank line?
						if (reComment.test(line))
							continue;

						// the extended syntax supports [lang] sections and @import rules
						if (extendedSyntax) {
							match = reSection.exec(line);
							if (match) { // section start?
								// RFC 4646, section 4.4, "All comparisons MUST be performed
								// in a case-insensitive manner."

								currentLang = match[1].toLowerCase();
								skipLang = (currentLang !== '*') &&
									(currentLang !== lang) && (currentLang !== genericLang);
								continue;
							} else if (skipLang) {
								continue;
							}
							match = reImport.exec(line);
							if (match) { // @import rule?
								loadImport(baseURL + match[1], nextEntry);
								return;
							}
						}

						// key-value pair
						var tmp = line.match(reSplit);
						if (tmp && tmp.length == 3) {
							dictionary[tmp[1]] = evalString(tmp[2]);
						}
					}
				}
				nextEntry();
			}

			// import another *.properties file
			function loadImport(url, callback) {
				xhrLoadText(url, function (content) {
					parseRawLines(content, false, callback); // don't allow recursive imports
				}, function () {
					console.warn(url + ' not found.');
					callback();
				});
			}

			// fill the dictionary
			parseRawLines(text, true, function () {
				parsedPropertiesCallback(dictionary);
			});
		}

		// load and parse l10n data (warning: global variables are used here)
		xhrLoadText(href, function (response) {
			gTextData += response; // mostly for debug

			// parse *.properties text data into an l10n dictionary
			parseProperties(response, function (data) {

				// find attribute descriptions, if any
				for (var key in data) {
					var id, prop, index = key.lastIndexOf('.');
					if (index > 0) { // an attribute has been specified
						id = key.substring(0, index);
						prop = key.substr(index + 1);
					} else { // no attribute: assuming text content by default
						id = key;
						prop = gTextProp;
					}
					if (!gL10nData[id]) {
						gL10nData[id] = {};
					}
					gL10nData[id][prop] = data[key];
				}

				// trigger callback
				if (successCallback) {
					successCallback();
				}
			});
		}, failureCallback);
	}

	// load and parse all resources for the specified locale
	function loadLocale(lang, callback) {
		// RFC 4646, section 2.1 states that language tags have to be treated as
		// case-insensitive. Convert to lowercase for case-insensitive comparisons.
		if (lang) {
			lang = lang.toLowerCase();
		}

		callback = callback || function _callback() { };

		clear();
		gLanguage = lang;

		// check all <link type="application/l10n" href="..." /> nodes
		// and load the resource files
		var langLinks = getL10nResourceLinks();
		var langCount = langLinks.length;
		if (langCount === 0) {
			// we might have a pre-compiled dictionary instead
			var dict = getL10nDictionary();
			if (dict && dict.locales && dict.default_locale) {
				console.log('using the embedded JSON directory, early way out');
				gL10nData = dict.locales[lang];
				if (!gL10nData) {
					var defaultLocale = dict.default_locale.toLowerCase();
					for (var anyCaseLang in dict.locales) {
						anyCaseLang = anyCaseLang.toLowerCase();
						if (anyCaseLang === lang) {
							gL10nData = dict.locales[lang];
							break;
						} else if (anyCaseLang === defaultLocale) {
							gL10nData = dict.locales[defaultLocale];
						}
					}
				}
				callback();
			} else {
				console.log('no resource to load, early way out');
			}
			// early way out
			fireL10nReadyEvent(lang);
			gReadyState = 'complete';
			return;
		}

		// start the callback when all resources are loaded
		var onResourceLoaded = null;
		var gResourceCount = 0;
		onResourceLoaded = function () {
			gResourceCount++;
			if (gResourceCount >= langCount) {
				callback();
				fireL10nReadyEvent(lang);
				gReadyState = 'complete';
			}
		};

		// load all resource files
		function L10nResourceLink(link) {
			var href = link.href;
			// Note: If |gAsyncResourceLoading| is false, then the following callbacks
			// are synchronously called.
			this.load = function (lang, callback) {
				parseResource(href, lang, callback, function () {
					console.warn(href + ' not found.');
					// lang not found, used default resource instead
					console.warn('"' + lang + '" resource not found');
					gLanguage = '';
					// Resource not loaded, but we still need to call the callback.
					callback();
				});
			};
		}

		for (var i = 0; i < langCount; i++) {
			var resource = new L10nResourceLink(langLinks[i]);
			resource.load(lang, onResourceLoaded);
		}
	}

	// clear all l10n data
	function clear() {
		gL10nData = {};
		gTextData = '';
		gLanguage = '';
		// TODO: clear all non predefined macros.
		// There's no such macro /yet/ but we're planning to have some...
	}


	/**
	 * Get rules for plural forms (shared with JetPack), see:
	 * http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html
	 * https://github.com/mozilla/addon-sdk/blob/master/python-lib/plural-rules-generator.p
	 *
	 * @param {string} lang
	 *    locale (language) used.
	 *
	 * @return {Function}
	 *    returns a function that gives the plural form name for a given integer:
	 *       var fun = getPluralRules('en');
	 *       fun(1)    -> 'one'
	 *       fun(0)    -> 'other'
	 *       fun(1000) -> 'other'.
	 */

	function getPluralRules(lang) {
		var locales2rules = {
			'af': 3,
			'ak': 4,
			'am': 4,
			'ar': 1,
			'asa': 3,
			'az': 0,
			'be': 11,
			'bem': 3,
			'bez': 3,
			'bg': 3,
			'bh': 4,
			'bm': 0,
			'bn': 3,
			'bo': 0,
			'br': 20,
			'brx': 3,
			'bs': 11,
			'ca': 3,
			'cgg': 3,
			'chr': 3,
			'cs': 12,
			'cy': 17,
			'da': 3,
			'de': 3,
			'dv': 3,
			'dz': 0,
			'ee': 3,
			'el': 3,
			'en': 3,
			'eo': 3,
			'es': 3,
			'et': 3,
			'eu': 3,
			'fa': 0,
			'ff': 5,
			'fi': 3,
			'fil': 4,
			'fo': 3,
			'fr': 5,
			'fur': 3,
			'fy': 3,
			'ga': 8,
			'gd': 24,
			'gl': 3,
			'gsw': 3,
			'gu': 3,
			'guw': 4,
			'gv': 23,
			'ha': 3,
			'haw': 3,
			'he': 2,
			'hi': 4,
			'hr': 11,
			'hu': 0,
			'id': 0,
			'ig': 0,
			'ii': 0,
			'is': 3,
			'it': 3,
			'iu': 7,
			'ja': 0,
			'jmc': 3,
			'jv': 0,
			'ka': 0,
			'kab': 5,
			'kaj': 3,
			'kcg': 3,
			'kde': 0,
			'kea': 0,
			'kk': 3,
			'kl': 3,
			'km': 0,
			'kn': 0,
			'ko': 0,
			'ksb': 3,
			'ksh': 21,
			'ku': 3,
			'kw': 7,
			'lag': 18,
			'lb': 3,
			'lg': 3,
			'ln': 4,
			'lo': 0,
			'lt': 10,
			'lv': 6,
			'mas': 3,
			'mg': 4,
			'mk': 16,
			'ml': 3,
			'mn': 3,
			'mo': 9,
			'mr': 3,
			'ms': 0,
			'mt': 15,
			'my': 0,
			'nah': 3,
			'naq': 7,
			'nb': 3,
			'nd': 3,
			'ne': 3,
			'nl': 3,
			'nn': 3,
			'no': 3,
			'nr': 3,
			'nso': 4,
			'ny': 3,
			'nyn': 3,
			'om': 3,
			'or': 3,
			'pa': 3,
			'pap': 3,
			'pl': 13,
			'ps': 3,
			'pt': 3,
			'rm': 3,
			'ro': 9,
			'rof': 3,
			'ru': 11,
			'rwk': 3,
			'sah': 0,
			'saq': 3,
			'se': 7,
			'seh': 3,
			'ses': 0,
			'sg': 0,
			'sh': 11,
			'shi': 19,
			'sk': 12,
			'sl': 14,
			'sma': 7,
			'smi': 7,
			'smj': 7,
			'smn': 7,
			'sms': 7,
			'sn': 3,
			'so': 3,
			'sq': 3,
			'sr': 11,
			'ss': 3,
			'ssy': 3,
			'st': 3,
			'sv': 3,
			'sw': 3,
			'syr': 3,
			'ta': 3,
			'te': 3,
			'teo': 3,
			'th': 0,
			'ti': 4,
			'tig': 3,
			'tk': 3,
			'tl': 4,
			'tn': 3,
			'to': 0,
			'tr': 0,
			'ts': 3,
			'tzm': 22,
			'uk': 11,
			'ur': 3,
			've': 3,
			'vi': 0,
			'vun': 3,
			'wa': 4,
			'wae': 3,
			'wo': 0,
			'xh': 3,
			'xog': 3,
			'yo': 0,
			'zh': 0,
			'zu': 3
		};

		// utility functions for plural rules methods
		function isIn(n, list) {
			return list.indexOf(n) !== -1;
		}
		function isBetween(n, start, end) {
			return start <= n && n <= end;
		}

		// list of all plural rules methods:
		// map an integer to the plural form name to use
		var pluralRules = {
			'0': function (n) {
				return 'other';
			},
			'1': function (n) {
				if ((isBetween((n % 100), 3, 10)))
					return 'few';
				if (n === 0)
					return 'zero';
				if ((isBetween((n % 100), 11, 99)))
					return 'many';
				if (n == 2)
					return 'two';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'2': function (n) {
				if (n !== 0 && (n % 10) === 0)
					return 'many';
				if (n == 2)
					return 'two';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'3': function (n) {
				if (n == 1)
					return 'one';
				return 'other';
			},
			'4': function (n) {
				if ((isBetween(n, 0, 1)))
					return 'one';
				return 'other';
			},
			'5': function (n) {
				if ((isBetween(n, 0, 2)) && n != 2)
					return 'one';
				return 'other';
			},
			'6': function (n) {
				if (n === 0)
					return 'zero';
				if ((n % 10) == 1 && (n % 100) != 11)
					return 'one';
				return 'other';
			},
			'7': function (n) {
				if (n == 2)
					return 'two';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'8': function (n) {
				if ((isBetween(n, 3, 6)))
					return 'few';
				if ((isBetween(n, 7, 10)))
					return 'many';
				if (n == 2)
					return 'two';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'9': function (n) {
				if (n === 0 || n != 1 && (isBetween((n % 100), 1, 19)))
					return 'few';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'10': function (n) {
				if ((isBetween((n % 10), 2, 9)) && !(isBetween((n % 100), 11, 19)))
					return 'few';
				if ((n % 10) == 1 && !(isBetween((n % 100), 11, 19)))
					return 'one';
				return 'other';
			},
			'11': function (n) {
				if ((isBetween((n % 10), 2, 4)) && !(isBetween((n % 100), 12, 14)))
					return 'few';
				if ((n % 10) === 0 ||
					(isBetween((n % 10), 5, 9)) ||
					(isBetween((n % 100), 11, 14)))
					return 'many';
				if ((n % 10) == 1 && (n % 100) != 11)
					return 'one';
				return 'other';
			},
			'12': function (n) {
				if ((isBetween(n, 2, 4)))
					return 'few';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'13': function (n) {
				if ((isBetween((n % 10), 2, 4)) && !(isBetween((n % 100), 12, 14)))
					return 'few';
				if (n != 1 && (isBetween((n % 10), 0, 1)) ||
					(isBetween((n % 10), 5, 9)) ||
					(isBetween((n % 100), 12, 14)))
					return 'many';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'14': function (n) {
				if ((isBetween((n % 100), 3, 4)))
					return 'few';
				if ((n % 100) == 2)
					return 'two';
				if ((n % 100) == 1)
					return 'one';
				return 'other';
			},
			'15': function (n) {
				if (n === 0 || (isBetween((n % 100), 2, 10)))
					return 'few';
				if ((isBetween((n % 100), 11, 19)))
					return 'many';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'16': function (n) {
				if ((n % 10) == 1 && n != 11)
					return 'one';
				return 'other';
			},
			'17': function (n) {
				if (n == 3)
					return 'few';
				if (n === 0)
					return 'zero';
				if (n == 6)
					return 'many';
				if (n == 2)
					return 'two';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'18': function (n) {
				if (n === 0)
					return 'zero';
				if ((isBetween(n, 0, 2)) && n !== 0 && n != 2)
					return 'one';
				return 'other';
			},
			'19': function (n) {
				if ((isBetween(n, 2, 10)))
					return 'few';
				if ((isBetween(n, 0, 1)))
					return 'one';
				return 'other';
			},
			'20': function (n) {
				if ((isBetween((n % 10), 3, 4) || ((n % 10) == 9)) && !(
					isBetween((n % 100), 10, 19) ||
					isBetween((n % 100), 70, 79) ||
					isBetween((n % 100), 90, 99)
				))
					return 'few';
				if ((n % 1000000) === 0 && n !== 0)
					return 'many';
				if ((n % 10) == 2 && !isIn((n % 100), [12, 72, 92]))
					return 'two';
				if ((n % 10) == 1 && !isIn((n % 100), [11, 71, 91]))
					return 'one';
				return 'other';
			},
			'21': function (n) {
				if (n === 0)
					return 'zero';
				if (n == 1)
					return 'one';
				return 'other';
			},
			'22': function (n) {
				if ((isBetween(n, 0, 1)) || (isBetween(n, 11, 99)))
					return 'one';
				return 'other';
			},
			'23': function (n) {
				if ((isBetween((n % 10), 1, 2)) || (n % 20) === 0)
					return 'one';
				return 'other';
			},
			'24': function (n) {
				if ((isBetween(n, 3, 10) || isBetween(n, 13, 19)))
					return 'few';
				if (isIn(n, [2, 12]))
					return 'two';
				if (isIn(n, [1, 11]))
					return 'one';
				return 'other';
			}
		};

		// return a function that gives the plural form name for a given integer
		var index = locales2rules[lang.replace(/-.*$/, '')];
		if (!(index in pluralRules)) {
			console.warn('plural form unknown for [' + lang + ']');
			return function () { return 'other'; };
		}
		return pluralRules[index];
	}

	// pre-defined 'plural' macro
	gMacros.plural = function (str, param, key, prop) {
		var n = parseFloat(param);
		if (isNaN(n))
			return str;

		// TODO: support other properties (l20n still doesn't...)
		if (prop != gTextProp)
			return str;

		// initialize _pluralRules
		if (!gMacros._pluralRules) {
			gMacros._pluralRules = getPluralRules(gLanguage);
		}
		var index = '[' + gMacros._pluralRules(n) + ']';

		// try to find a [zero|one|two] key if it's defined
		if (n === 0 && (key + '[zero]') in gL10nData) {
			str = gL10nData[key + '[zero]'][prop];
		} else if (n == 1 && (key + '[one]') in gL10nData) {
			str = gL10nData[key + '[one]'][prop];
		} else if (n == 2 && (key + '[two]') in gL10nData) {
			str = gL10nData[key + '[two]'][prop];
		} else if ((key + index) in gL10nData) {
			str = gL10nData[key + index][prop];
		} else if ((key + '[other]') in gL10nData) {
			str = gL10nData[key + '[other]'][prop];
		}

		return str;
	};


	/**
	 * l10n dictionary functions
	 */

	// fetch an l10n object, warn if not found, apply `args' if possible
	function getL10nData(key, args, fallback) {
		var data = gL10nData[key];
		if (!data) {
			///console.warn('#' + key + ' is undefined.'); /// patched
			// === patch start ===
			if (Object.keys(gL10nData).length > 0) {
				console.warn('#' + key + ' is undefined.');
			}
			// === patch end === 
			if (!fallback) {
				return null;
			}
			data = fallback;
		}

		/** This is where l10n expressions should be processed.
		  * The plan is to support C-style expressions from the l20n project;
		  * until then, only two kinds of simple expressions are supported:
		  *   {[ index ]} and {{ arguments }}.
		  */
		var rv = {};
		for (var prop in data) {
			var str = data[prop];
			str = substIndexes(str, args, key, prop);
			str = substArguments(str, args, key);
			rv[prop] = str;
		}
		return rv;
	}

	// replace {[macros]} with their values
	function substIndexes(str, args, key, prop) {
		var reIndex = /\{\[\s*([a-zA-Z]+)\(([a-zA-Z]+)\)\s*\]\}/;
		var reMatch = reIndex.exec(str);
		if (!reMatch || !reMatch.length)
			return str;

		// an index/macro has been found
		// Note: at the moment, only one parameter is supported
		var macroName = reMatch[1];
		var paramName = reMatch[2];
		var param;
		if (args && paramName in args) {
			param = args[paramName];
		} else if (paramName in gL10nData) {
			param = gL10nData[paramName];
		}

		// there's no macro parser yet: it has to be defined in gMacros
		if (macroName in gMacros) {
			var macro = gMacros[macroName];
			str = macro(str, param, key, prop);
		}
		return str;
	}

	// replace {{arguments}} with their values
	function substArguments(str, args, key) {
		var reArgs = /\{\{\s*(.+?)\s*\}\}/g;
		return str.replace(reArgs, function (matched_text, arg) {
			if (args && arg in args) {
				return args[arg];
			}
			if (arg in gL10nData) {
				return gL10nData[arg];
			}
			console.log('argument {{' + arg + '}} for #' + key + ' is undefined.');
			return matched_text;
		});
	}

	// translate an HTML element
	function translateElement(element) {
		var l10n = getL10nAttributes(element);
		if (!l10n.id)
			return;

		// get the related l10n object
		var data = getL10nData(l10n.id, l10n.args);
		if (!data) {
			console.warn('#' + l10n.id + ' is undefined.');
			return;
		}

		// translate element (TODO: security checks?)
		if (data[gTextProp]) { // XXX
			if (getChildElementCount(element) === 0) {
				element[gTextProp] = data[gTextProp];
			} else {
				// this element has element children: replace the content of the first
				// (non-empty) child textNode and clear other child textNodes
				var children = element.childNodes;
				var found = false;
				for (var i = 0, l = children.length; i < l; i++) {
					if (children[i].nodeType === 3 && /\S/.test(children[i].nodeValue)) {
						if (found) {
							children[i].nodeValue = '';
						} else {
							children[i].nodeValue = data[gTextProp];
							found = true;
						}
					}
				}
				// if no (non-empty) textNode is found, insert a textNode before the
				// first element child.
				if (!found) {
					var textNode = document.createTextNode(data[gTextProp]);
					element.insertBefore(textNode, element.firstChild);
				}
			}
			delete data[gTextProp];
		}

		for (var k in data) {
			element[k] = data[k];
		}
	}

	// webkit browsers don't currently support 'children' on SVG elements...
	function getChildElementCount(element) {
		if (element.children) {
			return element.children.length;
		}
		if (typeof element.childElementCount !== 'undefined') {
			return element.childElementCount;
		}
		var count = 0;
		for (var i = 0; i < element.childNodes.length; i++) {
			count += element.nodeType === 1 ? 1 : 0;
		}
		return count;
	}

	// translate an HTML subtree
	function translateFragment(element) {
		element = element || document.documentElement;

		// check all translatable children (= w/ a `data-l10n-id' attribute)
		var children = getTranslatableChildren(element);
		var elementCount = children.length;
		for (var i = 0; i < elementCount; i++) {
			translateElement(children[i]);
		}

		// translate element itself if necessary
		translateElement(element);
	}

	return {
		// get a localized string
		get: function (key, args, fallbackString) {
			var index = key.lastIndexOf('.');
			var prop = gTextProp;
			if (index > 0) { // An attribute has been specified
				prop = key.substr(index + 1);
				key = key.substring(0, index);
			}
			var fallback;
			if (fallbackString) {
				fallback = {};
				fallback[prop] = fallbackString;
			}
			var data = getL10nData(key, args, fallback);
			if (data && prop in data) {
				return data[prop];
			}
			return '{{' + key + '}}';
		},

		// debug
		getData: function () { return gL10nData; },
		getText: function () { return gTextData; },

		// get|set the document language
		getLanguage: function () { return gLanguage; },
		setLanguage: function (lang, callback) {
			loadLocale(lang, function () {
				if (callback)
					callback();
				translateFragment();
			});
		},

		// get the direction (ltr|rtl) of the current language
		getDirection: function () {
			// http://www.w3.org/International/questions/qa-scripts
			// Arabic, Hebrew, Farsi, Pashto, Urdu
			var rtlList = ['ar', 'he', 'fa', 'ps', 'ur'];
			var shortCode = gLanguage.split('-', 1)[0];
			return (rtlList.indexOf(shortCode) >= 0) ? 'rtl' : 'ltr';
		},

		// translate an element or document fragment
		translate: translateFragment,

		// this can be used to prevent race conditions
		getReadyState: function () { return gReadyState; },
		ready: function (callback) {
			if (!callback) {
				return;
			} else if (gReadyState == 'complete' || gReadyState == 'interactive') {
				window.setTimeout(function () {
					callback();
				});
			} else if (document.addEventListener) {
				document.addEventListener('localized', function once() {
					document.removeEventListener('localized', once);
					callback();
				});
			}
		}
	};
})(window, document);
/* Copyright 2017 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

(function webpackUniversalModuleDefinition(root, factory) {
	if (typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if (typeof define === 'function' && define.amd)
		define("pdfjs-dist/build/pdf", [], factory);
	else if (typeof exports === 'object')
		exports["pdfjs-dist/build/pdf"] = factory();
	else
		root["pdfjs-dist/build/pdf"] = root.pdfjsDistBuildPdf = factory();
})(this, function () {
	return /******/ (function (modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __w_pdfjs_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if (installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;
/******/
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
				/******/
			};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __w_pdfjs_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
			/******/
		}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__w_pdfjs_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__w_pdfjs_require__.c = installedModules;
/******/
/******/ 	// identity function for calling harmony imports with the correct context
/******/ 	__w_pdfjs_require__.i = function (value) { return value; };
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__w_pdfjs_require__.d = function (exports, name, getter) {
/******/ 		if (!__w_pdfjs_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
			/******/
		});
				/******/
			}
			/******/
		};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__w_pdfjs_require__.n = function (module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__w_pdfjs_require__.d(getter, 'a', getter);
/******/ 		return getter;
			/******/
		};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__w_pdfjs_require__.o = function (object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__w_pdfjs_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __w_pdfjs_require__(__w_pdfjs_require__.s = 14);
		/******/
	})
/************************************************************************/
/******/([
/* 0 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";
/* WEBPACK VAR INJECTION */(function (global) {
				var compatibility = __w_pdfjs_require__(13);
				var globalScope = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : this;
				var FONT_IDENTITY_MATRIX = [
					0.001,
					0,
					0,
					0.001,
					0,
					0
				];
				var TextRenderingMode = {
					FILL: 0,
					STROKE: 1,
					FILL_STROKE: 2,
					INVISIBLE: 3,
					FILL_ADD_TO_PATH: 4,
					STROKE_ADD_TO_PATH: 5,
					FILL_STROKE_ADD_TO_PATH: 6,
					ADD_TO_PATH: 7,
					FILL_STROKE_MASK: 3,
					ADD_TO_PATH_FLAG: 4
				};
				var ImageKind = {
					GRAYSCALE_1BPP: 1,
					RGB_24BPP: 2,
					RGBA_32BPP: 3
				};
				var AnnotationType = {
					TEXT: 1,
					LINK: 2,
					FREETEXT: 3,
					LINE: 4,
					SQUARE: 5,
					CIRCLE: 6,
					POLYGON: 7,
					POLYLINE: 8,
					HIGHLIGHT: 9,
					UNDERLINE: 10,
					SQUIGGLY: 11,
					STRIKEOUT: 12,
					STAMP: 13,
					CARET: 14,
					INK: 15,
					POPUP: 16,
					FILEATTACHMENT: 17,
					SOUND: 18,
					MOVIE: 19,
					WIDGET: 20,
					SCREEN: 21,
					PRINTERMARK: 22,
					TRAPNET: 23,
					WATERMARK: 24,
					THREED: 25,
					REDACT: 26
				};
				var AnnotationFlag = {
					INVISIBLE: 0x01,
					HIDDEN: 0x02,
					PRINT: 0x04,
					NOZOOM: 0x08,
					NOROTATE: 0x10,
					NOVIEW: 0x20,
					READONLY: 0x40,
					LOCKED: 0x80,
					TOGGLENOVIEW: 0x100,
					LOCKEDCONTENTS: 0x200
				};
				var AnnotationFieldFlag = {
					READONLY: 0x0000001,
					REQUIRED: 0x0000002,
					NOEXPORT: 0x0000004,
					MULTILINE: 0x0001000,
					PASSWORD: 0x0002000,
					NOTOGGLETOOFF: 0x0004000,
					RADIO: 0x0008000,
					PUSHBUTTON: 0x0010000,
					COMBO: 0x0020000,
					EDIT: 0x0040000,
					SORT: 0x0080000,
					FILESELECT: 0x0100000,
					MULTISELECT: 0x0200000,
					DONOTSPELLCHECK: 0x0400000,
					DONOTSCROLL: 0x0800000,
					COMB: 0x1000000,
					RICHTEXT: 0x2000000,
					RADIOSINUNISON: 0x2000000,
					COMMITONSELCHANGE: 0x4000000
				};
				var AnnotationBorderStyleType = {
					SOLID: 1,
					DASHED: 2,
					BEVELED: 3,
					INSET: 4,
					UNDERLINE: 5
				};
				var StreamType = {
					UNKNOWN: 0,
					FLATE: 1,
					LZW: 2,
					DCT: 3,
					JPX: 4,
					JBIG: 5,
					A85: 6,
					AHX: 7,
					CCF: 8,
					RL: 9
				};
				var FontType = {
					UNKNOWN: 0,
					TYPE1: 1,
					TYPE1C: 2,
					CIDFONTTYPE0: 3,
					CIDFONTTYPE0C: 4,
					TRUETYPE: 5,
					CIDFONTTYPE2: 6,
					TYPE3: 7,
					OPENTYPE: 8,
					TYPE0: 9,
					MMTYPE1: 10
				};
				var VERBOSITY_LEVELS = {
					errors: 0,
					warnings: 1,
					infos: 5
				};
				var CMapCompressionType = {
					NONE: 0,
					BINARY: 1,
					STREAM: 2
				};
				var OPS = {
					dependency: 1,
					setLineWidth: 2,
					setLineCap: 3,
					setLineJoin: 4,
					setMiterLimit: 5,
					setDash: 6,
					setRenderingIntent: 7,
					setFlatness: 8,
					setGState: 9,
					save: 10,
					restore: 11,
					transform: 12,
					moveTo: 13,
					lineTo: 14,
					curveTo: 15,
					curveTo2: 16,
					curveTo3: 17,
					closePath: 18,
					rectangle: 19,
					stroke: 20,
					closeStroke: 21,
					fill: 22,
					eoFill: 23,
					fillStroke: 24,
					eoFillStroke: 25,
					closeFillStroke: 26,
					closeEOFillStroke: 27,
					endPath: 28,
					clip: 29,
					eoClip: 30,
					beginText: 31,
					endText: 32,
					setCharSpacing: 33,
					setWordSpacing: 34,
					setHScale: 35,
					setLeading: 36,
					setFont: 37,
					setTextRenderingMode: 38,
					setTextRise: 39,
					moveText: 40,
					setLeadingMoveText: 41,
					setTextMatrix: 42,
					nextLine: 43,
					showText: 44,
					showSpacedText: 45,
					nextLineShowText: 46,
					nextLineSetSpacingShowText: 47,
					setCharWidth: 48,
					setCharWidthAndBounds: 49,
					setStrokeColorSpace: 50,
					setFillColorSpace: 51,
					setStrokeColor: 52,
					setStrokeColorN: 53,
					setFillColor: 54,
					setFillColorN: 55,
					setStrokeGray: 56,
					setFillGray: 57,
					setStrokeRGBColor: 58,
					setFillRGBColor: 59,
					setStrokeCMYKColor: 60,
					setFillCMYKColor: 61,
					shadingFill: 62,
					beginInlineImage: 63,
					beginImageData: 64,
					endInlineImage: 65,
					paintXObject: 66,
					markPoint: 67,
					markPointProps: 68,
					beginMarkedContent: 69,
					beginMarkedContentProps: 70,
					endMarkedContent: 71,
					beginCompat: 72,
					endCompat: 73,
					paintFormXObjectBegin: 74,
					paintFormXObjectEnd: 75,
					beginGroup: 76,
					endGroup: 77,
					beginAnnotations: 78,
					endAnnotations: 79,
					beginAnnotation: 80,
					endAnnotation: 81,
					paintJpegXObject: 82,
					paintImageMaskXObject: 83,
					paintImageMaskXObjectGroup: 84,
					paintImageXObject: 85,
					paintInlineImageXObject: 86,
					paintInlineImageXObjectGroup: 87,
					paintImageXObjectRepeat: 88,
					paintImageMaskXObjectRepeat: 89,
					paintSolidColorImageMask: 90,
					constructPath: 91
				};
				var verbosity = VERBOSITY_LEVELS.warnings;
				function setVerbosityLevel(level) {
					verbosity = level;
				}
				function getVerbosityLevel() {
					return verbosity;
				}
				function info(msg) {
					if (verbosity >= VERBOSITY_LEVELS.infos) {
						console.log('Info: ' + msg);
					}
				}
				function warn(msg) {
					if (verbosity >= VERBOSITY_LEVELS.warnings) {
						console.log('Warning: ' + msg);
					}
				}
				function deprecated(details) {
					console.log('Deprecated API usage: ' + details);
				}
				function error(msg) {
					if (verbosity >= VERBOSITY_LEVELS.errors) {
						console.log('Error: ' + msg);
						console.log(backtrace());
					}
					throw new Error(msg);
				}
				function backtrace() {
					try {
						throw new Error();
					} catch (e) {
						return e.stack ? e.stack.split('\n').slice(2).join('\n') : '';
					}
				}
				function assert(cond, msg) {
					if (!cond) {
						error(msg);
					}
				}
				var UNSUPPORTED_FEATURES = {
					unknown: 'unknown',
					forms: 'forms',
					javaScript: 'javaScript',
					smask: 'smask',
					shadingPattern: 'shadingPattern',
					font: 'font'
				};
				function isSameOrigin(baseUrl, otherUrl) {
					try {
						var base = new URL(baseUrl);
						if (!base.origin || base.origin === 'null') {
							return false;
						}
					} catch (e) {
						return false;
					}
					var other = new URL(otherUrl, base);
					return base.origin === other.origin;
				}
				function isValidProtocol(url) {
					if (!url) {
						return false;
					}
					switch (url.protocol) {
						case 'http:':
						case 'https:':
						case 'ftp:':
						case 'mailto:':
						case 'tel:':
							return true;
						default:
							return false;
					}
				}
				function createValidAbsoluteUrl(url, baseUrl) {
					if (!url) {
						return null;
					}
					try {
						var absoluteUrl = baseUrl ? new URL(url, baseUrl) : new URL(url);
						if (isValidProtocol(absoluteUrl)) {
							return absoluteUrl;
						}
					} catch (ex) {
					}
					return null;
				}
				function shadow(obj, prop, value) {
					Object.defineProperty(obj, prop, {
						value: value,
						enumerable: true,
						configurable: true,
						writable: false
					});
					return value;
				}
				function getLookupTableFactory(initializer) {
					var lookup;
					return function () {
						if (initializer) {
							lookup = Object.create(null);
							initializer(lookup);
							initializer = null;
						}
						return lookup;
					};
				}
				var PasswordResponses = {
					NEED_PASSWORD: 1,
					INCORRECT_PASSWORD: 2
				};
				var PasswordException = function PasswordExceptionClosure() {
					function PasswordException(msg, code) {
						this.name = 'PasswordException';
						this.message = msg;
						this.code = code;
					}
					PasswordException.prototype = new Error();
					PasswordException.constructor = PasswordException;
					return PasswordException;
				}();
				var UnknownErrorException = function UnknownErrorExceptionClosure() {
					function UnknownErrorException(msg, details) {
						this.name = 'UnknownErrorException';
						this.message = msg;
						this.details = details;
					}
					UnknownErrorException.prototype = new Error();
					UnknownErrorException.constructor = UnknownErrorException;
					return UnknownErrorException;
				}();
				var InvalidPDFException = function InvalidPDFExceptionClosure() {
					function InvalidPDFException(msg) {
						this.name = 'InvalidPDFException';
						this.message = msg;
					}
					InvalidPDFException.prototype = new Error();
					InvalidPDFException.constructor = InvalidPDFException;
					return InvalidPDFException;
				}();
				var MissingPDFException = function MissingPDFExceptionClosure() {
					function MissingPDFException(msg) {
						this.name = 'MissingPDFException';
						this.message = msg;
					}
					MissingPDFException.prototype = new Error();
					MissingPDFException.constructor = MissingPDFException;
					return MissingPDFException;
				}();
				var UnexpectedResponseException = function UnexpectedResponseExceptionClosure() {
					function UnexpectedResponseException(msg, status) {
						this.name = 'UnexpectedResponseException';
						this.message = msg;
						this.status = status;
					}
					UnexpectedResponseException.prototype = new Error();
					UnexpectedResponseException.constructor = UnexpectedResponseException;
					return UnexpectedResponseException;
				}();
				var NotImplementedException = function NotImplementedExceptionClosure() {
					function NotImplementedException(msg) {
						this.message = msg;
					}
					NotImplementedException.prototype = new Error();
					NotImplementedException.prototype.name = 'NotImplementedException';
					NotImplementedException.constructor = NotImplementedException;
					return NotImplementedException;
				}();
				var MissingDataException = function MissingDataExceptionClosure() {
					function MissingDataException(begin, end) {
						this.begin = begin;
						this.end = end;
						this.message = 'Missing data [' + begin + ', ' + end + ')';
					}
					MissingDataException.prototype = new Error();
					MissingDataException.prototype.name = 'MissingDataException';
					MissingDataException.constructor = MissingDataException;
					return MissingDataException;
				}();
				var XRefParseException = function XRefParseExceptionClosure() {
					function XRefParseException(msg) {
						this.message = msg;
					}
					XRefParseException.prototype = new Error();
					XRefParseException.prototype.name = 'XRefParseException';
					XRefParseException.constructor = XRefParseException;
					return XRefParseException;
				}();
				var NullCharactersRegExp = /\x00/g;
				function removeNullCharacters(str) {
					if (typeof str !== 'string') {
						warn('The argument for removeNullCharacters must be a string.');
						return str;
					}
					return str.replace(NullCharactersRegExp, '');
				}
				function bytesToString(bytes) {
					assert(bytes !== null && typeof bytes === 'object' && bytes.length !== undefined, 'Invalid argument for bytesToString');
					var length = bytes.length;
					var MAX_ARGUMENT_COUNT = 8192;
					if (length < MAX_ARGUMENT_COUNT) {
						return String.fromCharCode.apply(null, bytes);
					}
					var strBuf = [];
					for (var i = 0; i < length; i += MAX_ARGUMENT_COUNT) {
						var chunkEnd = Math.min(i + MAX_ARGUMENT_COUNT, length);
						var chunk = bytes.subarray(i, chunkEnd);
						strBuf.push(String.fromCharCode.apply(null, chunk));
					}
					return strBuf.join('');
				}
				function stringToBytes(str) {
					assert(typeof str === 'string', 'Invalid argument for stringToBytes');
					var length = str.length;
					var bytes = new Uint8Array(length);
					for (var i = 0; i < length; ++i) {
						bytes[i] = str.charCodeAt(i) & 0xFF;
					}
					return bytes;
				}
				function arrayByteLength(arr) {
					if (arr.length !== undefined) {
						return arr.length;
					}
					assert(arr.byteLength !== undefined);
					return arr.byteLength;
				}
				function arraysToBytes(arr) {
					if (arr.length === 1 && arr[0] instanceof Uint8Array) {
						return arr[0];
					}
					var resultLength = 0;
					var i, ii = arr.length;
					var item, itemLength;
					for (i = 0; i < ii; i++) {
						item = arr[i];
						itemLength = arrayByteLength(item);
						resultLength += itemLength;
					}
					var pos = 0;
					var data = new Uint8Array(resultLength);
					for (i = 0; i < ii; i++) {
						item = arr[i];
						if (!(item instanceof Uint8Array)) {
							if (typeof item === 'string') {
								item = stringToBytes(item);
							} else {
								item = new Uint8Array(item);
							}
						}
						itemLength = item.byteLength;
						data.set(item, pos);
						pos += itemLength;
					}
					return data;
				}
				function string32(value) {
					return String.fromCharCode(value >> 24 & 0xff, value >> 16 & 0xff, value >> 8 & 0xff, value & 0xff);
				}
				function log2(x) {
					var n = 1, i = 0;
					while (x > n) {
						n <<= 1;
						i++;
					}
					return i;
				}
				function readInt8(data, start) {
					return data[start] << 24 >> 24;
				}
				function readUint16(data, offset) {
					return data[offset] << 8 | data[offset + 1];
				}
				function readUint32(data, offset) {
					return (data[offset] << 24 | data[offset + 1] << 16 | data[offset + 2] << 8 | data[offset + 3]) >>> 0;
				}
				function isLittleEndian() {
					var buffer8 = new Uint8Array(2);
					buffer8[0] = 1;
					var buffer16 = new Uint16Array(buffer8.buffer);
					return buffer16[0] === 1;
				}
				function isEvalSupported() {
					try {
						new Function('');
						return true;
					} catch (e) {
						return false;
					}
				}
				var Uint32ArrayView = function Uint32ArrayViewClosure() {
					function Uint32ArrayView(buffer, length) {
						this.buffer = buffer;
						this.byteLength = buffer.length;
						this.length = length === undefined ? this.byteLength >> 2 : length;
						ensureUint32ArrayViewProps(this.length);
					}
					Uint32ArrayView.prototype = Object.create(null);
					var uint32ArrayViewSetters = 0;
					function createUint32ArrayProp(index) {
						return {
							get: function () {
								var buffer = this.buffer, offset = index << 2;
								return (buffer[offset] | buffer[offset + 1] << 8 | buffer[offset + 2] << 16 | buffer[offset + 3] << 24) >>> 0;
							},
							set: function (value) {
								var buffer = this.buffer, offset = index << 2;
								buffer[offset] = value & 255;
								buffer[offset + 1] = value >> 8 & 255;
								buffer[offset + 2] = value >> 16 & 255;
								buffer[offset + 3] = value >>> 24 & 255;
							}
						};
					}
					function ensureUint32ArrayViewProps(length) {
						while (uint32ArrayViewSetters < length) {
							Object.defineProperty(Uint32ArrayView.prototype, uint32ArrayViewSetters, createUint32ArrayProp(uint32ArrayViewSetters));
							uint32ArrayViewSetters++;
						}
					}
					return Uint32ArrayView;
				}();
				exports.Uint32ArrayView = Uint32ArrayView;
				var IDENTITY_MATRIX = [
					1,
					0,
					0,
					1,
					0,
					0
				];
				var Util = function UtilClosure() {
					function Util() {
					}
					var rgbBuf = [
						'rgb(',
						0,
						',',
						0,
						',',
						0,
						')'
					];
					Util.makeCssRgb = function Util_makeCssRgb(r, g, b) {
						rgbBuf[1] = r;
						rgbBuf[3] = g;
						rgbBuf[5] = b;
						return rgbBuf.join('');
					};
					Util.transform = function Util_transform(m1, m2) {
						return [
							m1[0] * m2[0] + m1[2] * m2[1],
							m1[1] * m2[0] + m1[3] * m2[1],
							m1[0] * m2[2] + m1[2] * m2[3],
							m1[1] * m2[2] + m1[3] * m2[3],
							m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
							m1[1] * m2[4] + m1[3] * m2[5] + m1[5]
						];
					};
					Util.applyTransform = function Util_applyTransform(p, m) {
						var xt = p[0] * m[0] + p[1] * m[2] + m[4];
						var yt = p[0] * m[1] + p[1] * m[3] + m[5];
						return [
							xt,
							yt
						];
					};
					Util.applyInverseTransform = function Util_applyInverseTransform(p, m) {
						var d = m[0] * m[3] - m[1] * m[2];
						var xt = (p[0] * m[3] - p[1] * m[2] + m[2] * m[5] - m[4] * m[3]) / d;
						var yt = (-p[0] * m[1] + p[1] * m[0] + m[4] * m[1] - m[5] * m[0]) / d;
						return [
							xt,
							yt
						];
					};
					Util.getAxialAlignedBoundingBox = function Util_getAxialAlignedBoundingBox(r, m) {
						var p1 = Util.applyTransform(r, m);
						var p2 = Util.applyTransform(r.slice(2, 4), m);
						var p3 = Util.applyTransform([
							r[0],
							r[3]
						], m);
						var p4 = Util.applyTransform([
							r[2],
							r[1]
						], m);
						return [
							Math.min(p1[0], p2[0], p3[0], p4[0]),
							Math.min(p1[1], p2[1], p3[1], p4[1]),
							Math.max(p1[0], p2[0], p3[0], p4[0]),
							Math.max(p1[1], p2[1], p3[1], p4[1])
						];
					};
					Util.inverseTransform = function Util_inverseTransform(m) {
						var d = m[0] * m[3] - m[1] * m[2];
						return [
							m[3] / d,
							-m[1] / d,
							-m[2] / d,
							m[0] / d,
							(m[2] * m[5] - m[4] * m[3]) / d,
							(m[4] * m[1] - m[5] * m[0]) / d
						];
					};
					Util.apply3dTransform = function Util_apply3dTransform(m, v) {
						return [
							m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
							m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
							m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
						];
					};
					Util.singularValueDecompose2dScale = function Util_singularValueDecompose2dScale(m) {
						var transpose = [
							m[0],
							m[2],
							m[1],
							m[3]
						];
						var a = m[0] * transpose[0] + m[1] * transpose[2];
						var b = m[0] * transpose[1] + m[1] * transpose[3];
						var c = m[2] * transpose[0] + m[3] * transpose[2];
						var d = m[2] * transpose[1] + m[3] * transpose[3];
						var first = (a + d) / 2;
						var second = Math.sqrt((a + d) * (a + d) - 4 * (a * d - c * b)) / 2;
						var sx = first + second || 1;
						var sy = first - second || 1;
						return [
							Math.sqrt(sx),
							Math.sqrt(sy)
						];
					};
					Util.normalizeRect = function Util_normalizeRect(rect) {
						var r = rect.slice(0);
						if (rect[0] > rect[2]) {
							r[0] = rect[2];
							r[2] = rect[0];
						}
						if (rect[1] > rect[3]) {
							r[1] = rect[3];
							r[3] = rect[1];
						}
						return r;
					};
					Util.intersect = function Util_intersect(rect1, rect2) {
						function compare(a, b) {
							return a - b;
						}
						var orderedX = [
							rect1[0],
							rect1[2],
							rect2[0],
							rect2[2]
						].sort(compare), orderedY = [
							rect1[1],
							rect1[3],
							rect2[1],
							rect2[3]
						].sort(compare), result = [];
						rect1 = Util.normalizeRect(rect1);
						rect2 = Util.normalizeRect(rect2);
						if (orderedX[0] === rect1[0] && orderedX[1] === rect2[0] || orderedX[0] === rect2[0] && orderedX[1] === rect1[0]) {
							result[0] = orderedX[1];
							result[2] = orderedX[2];
						} else {
							return false;
						}
						if (orderedY[0] === rect1[1] && orderedY[1] === rect2[1] || orderedY[0] === rect2[1] && orderedY[1] === rect1[1]) {
							result[1] = orderedY[1];
							result[3] = orderedY[2];
						} else {
							return false;
						}
						return result;
					};
					Util.sign = function Util_sign(num) {
						return num < 0 ? -1 : 1;
					};
					var ROMAN_NUMBER_MAP = [
						'',
						'C',
						'CC',
						'CCC',
						'CD',
						'D',
						'DC',
						'DCC',
						'DCCC',
						'CM',
						'',
						'X',
						'XX',
						'XXX',
						'XL',
						'L',
						'LX',
						'LXX',
						'LXXX',
						'XC',
						'',
						'I',
						'II',
						'III',
						'IV',
						'V',
						'VI',
						'VII',
						'VIII',
						'IX'
					];
					Util.toRoman = function Util_toRoman(number, lowerCase) {
						assert(isInt(number) && number > 0, 'The number should be a positive integer.');
						var pos, romanBuf = [];
						while (number >= 1000) {
							number -= 1000;
							romanBuf.push('M');
						}
						pos = number / 100 | 0;
						number %= 100;
						romanBuf.push(ROMAN_NUMBER_MAP[pos]);
						pos = number / 10 | 0;
						number %= 10;
						romanBuf.push(ROMAN_NUMBER_MAP[10 + pos]);
						romanBuf.push(ROMAN_NUMBER_MAP[20 + number]);
						var romanStr = romanBuf.join('');
						return lowerCase ? romanStr.toLowerCase() : romanStr;
					};
					Util.appendToArray = function Util_appendToArray(arr1, arr2) {
						Array.prototype.push.apply(arr1, arr2);
					};
					Util.prependToArray = function Util_prependToArray(arr1, arr2) {
						Array.prototype.unshift.apply(arr1, arr2);
					};
					Util.extendObj = function extendObj(obj1, obj2) {
						for (var key in obj2) {
							obj1[key] = obj2[key];
						}
					};
					Util.getInheritableProperty = function Util_getInheritableProperty(dict, name, getArray) {
						while (dict && !dict.has(name)) {
							dict = dict.get('Parent');
						}
						if (!dict) {
							return null;
						}
						return getArray ? dict.getArray(name) : dict.get(name);
					};
					Util.inherit = function Util_inherit(sub, base, prototype) {
						sub.prototype = Object.create(base.prototype);
						sub.prototype.constructor = sub;
						for (var prop in prototype) {
							sub.prototype[prop] = prototype[prop];
						}
					};
					Util.loadScript = function Util_loadScript(src, callback) {
						var script = document.createElement('script');
						var loaded = false;
						script.setAttribute('src', src);
						if (callback) {
							script.onload = function () {
								if (!loaded) {
									callback();
								}
								loaded = true;
							};
						}
						document.getElementsByTagName('head')[0].appendChild(script);
					};
					return Util;
				}();
				var PageViewport = function PageViewportClosure() {
					function PageViewport(viewBox, scale, rotation, offsetX, offsetY, dontFlip) {
						this.viewBox = viewBox;
						this.scale = scale;
						this.rotation = rotation;
						this.offsetX = offsetX;
						this.offsetY = offsetY;
						var centerX = (viewBox[2] + viewBox[0]) / 2;
						var centerY = (viewBox[3] + viewBox[1]) / 2;
						var rotateA, rotateB, rotateC, rotateD;
						rotation = rotation % 360;
						rotation = rotation < 0 ? rotation + 360 : rotation;
						switch (rotation) {
							case 180:
								rotateA = -1;
								rotateB = 0;
								rotateC = 0;
								rotateD = 1;
								break;
							case 90:
								rotateA = 0;
								rotateB = 1;
								rotateC = 1;
								rotateD = 0;
								break;
							case 270:
								rotateA = 0;
								rotateB = -1;
								rotateC = -1;
								rotateD = 0;
								break;
							default:
								rotateA = 1;
								rotateB = 0;
								rotateC = 0;
								rotateD = -1;
								break;
						}
						if (dontFlip) {
							rotateC = -rotateC;
							rotateD = -rotateD;
						}
						var offsetCanvasX, offsetCanvasY;
						var width, height;
						if (rotateA === 0) {
							offsetCanvasX = Math.abs(centerY - viewBox[1]) * scale + offsetX;
							offsetCanvasY = Math.abs(centerX - viewBox[0]) * scale + offsetY;
							width = Math.abs(viewBox[3] - viewBox[1]) * scale;
							height = Math.abs(viewBox[2] - viewBox[0]) * scale;
						} else {
							offsetCanvasX = Math.abs(centerX - viewBox[0]) * scale + offsetX;
							offsetCanvasY = Math.abs(centerY - viewBox[1]) * scale + offsetY;
							width = Math.abs(viewBox[2] - viewBox[0]) * scale;
							height = Math.abs(viewBox[3] - viewBox[1]) * scale;
						}
						this.transform = [
							rotateA * scale,
							rotateB * scale,
							rotateC * scale,
							rotateD * scale,
							offsetCanvasX - rotateA * scale * centerX - rotateC * scale * centerY,
							offsetCanvasY - rotateB * scale * centerX - rotateD * scale * centerY
						];
						this.width = width;
						this.height = height;
						this.fontScale = scale;
					}
					PageViewport.prototype = {
						clone: function PageViewPort_clone(args) {
							args = args || {};
							var scale = 'scale' in args ? args.scale : this.scale;
							var rotation = 'rotation' in args ? args.rotation : this.rotation;
							return new PageViewport(this.viewBox.slice(), scale, rotation, this.offsetX, this.offsetY, args.dontFlip);
						},
						convertToViewportPoint: function PageViewport_convertToViewportPoint(x, y) {
							return Util.applyTransform([
								x,
								y
							], this.transform);
						},
						convertToViewportRectangle: function PageViewport_convertToViewportRectangle(rect) {
							var tl = Util.applyTransform([
								rect[0],
								rect[1]
							], this.transform);
							var br = Util.applyTransform([
								rect[2],
								rect[3]
							], this.transform);
							return [
								tl[0],
								tl[1],
								br[0],
								br[1]
							];
						},
						convertToPdfPoint: function PageViewport_convertToPdfPoint(x, y) {
							return Util.applyInverseTransform([
								x,
								y
							], this.transform);
						}
					};
					return PageViewport;
				}();
				var PDFStringTranslateTable = [
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0x2D8,
					0x2C7,
					0x2C6,
					0x2D9,
					0x2DD,
					0x2DB,
					0x2DA,
					0x2DC,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0x2022,
					0x2020,
					0x2021,
					0x2026,
					0x2014,
					0x2013,
					0x192,
					0x2044,
					0x2039,
					0x203A,
					0x2212,
					0x2030,
					0x201E,
					0x201C,
					0x201D,
					0x2018,
					0x2019,
					0x201A,
					0x2122,
					0xFB01,
					0xFB02,
					0x141,
					0x152,
					0x160,
					0x178,
					0x17D,
					0x131,
					0x142,
					0x153,
					0x161,
					0x17E,
					0,
					0x20AC
				];
				function stringToPDFString(str) {
					var i, n = str.length, strBuf = [];
					if (str[0] === '\xFE' && str[1] === '\xFF') {
						for (i = 2; i < n; i += 2) {
							strBuf.push(String.fromCharCode(str.charCodeAt(i) << 8 | str.charCodeAt(i + 1)));
						}
					} else {
						for (i = 0; i < n; ++i) {
							var code = PDFStringTranslateTable[str.charCodeAt(i)];
							strBuf.push(code ? String.fromCharCode(code) : str.charAt(i));
						}
					}
					return strBuf.join('');
				}
				function stringToUTF8String(str) {
					return decodeURIComponent(escape(str));
				}
				function utf8StringToString(str) {
					return unescape(encodeURIComponent(str));
				}
				function isEmptyObj(obj) {
					for (var key in obj) {
						return false;
					}
					return true;
				}
				function isBool(v) {
					return typeof v === 'boolean';
				}
				function isInt(v) {
					return typeof v === 'number' && (v | 0) === v;
				}
				function isNum(v) {
					return typeof v === 'number';
				}
				function isString(v) {
					return typeof v === 'string';
				}
				function isArray(v) {
					return v instanceof Array;
				}
				function isArrayBuffer(v) {
					return typeof v === 'object' && v !== null && v.byteLength !== undefined;
				}
				function isSpace(ch) {
					return ch === 0x20 || ch === 0x09 || ch === 0x0D || ch === 0x0A;
				}
				function isNodeJS() {
					if (typeof __pdfjsdev_webpack__ === 'undefined') {
						return typeof process === 'object' && process + '' === '[object process]';
					}
					return false;
				}
				function createPromiseCapability() {
					var capability = {};
					capability.promise = new Promise(function (resolve, reject) {
						capability.resolve = resolve;
						capability.reject = reject;
					});
					return capability;
				}
				var StatTimer = function StatTimerClosure() {
					function rpad(str, pad, length) {
						while (str.length < length) {
							str += pad;
						}
						return str;
					}
					function StatTimer() {
						this.started = Object.create(null);
						this.times = [];
						this.enabled = true;
					}
					StatTimer.prototype = {
						time: function StatTimer_time(name) {
							if (!this.enabled) {
								return;
							}
							if (name in this.started) {
								warn('Timer is already running for ' + name);
							}
							this.started[name] = Date.now();
						},
						timeEnd: function StatTimer_timeEnd(name) {
							if (!this.enabled) {
								return;
							}
							if (!(name in this.started)) {
								warn('Timer has not been started for ' + name);
							}
							this.times.push({
								'name': name,
								'start': this.started[name],
								'end': Date.now()
							});
							delete this.started[name];
						},
						toString: function StatTimer_toString() {
							var i, ii;
							var times = this.times;
							var out = '';
							var longest = 0;
							for (i = 0, ii = times.length; i < ii; ++i) {
								var name = times[i]['name'];
								if (name.length > longest) {
									longest = name.length;
								}
							}
							for (i = 0, ii = times.length; i < ii; ++i) {
								var span = times[i];
								var duration = span.end - span.start;
								out += rpad(span['name'], ' ', longest) + ' ' + duration + 'ms\n';
							}
							return out;
						}
					};
					return StatTimer;
				}();
				var createBlob = function createBlob(data, contentType) {
					if (typeof Blob !== 'undefined') {
						return new Blob([data], { type: contentType });
					}
					warn('The "Blob" constructor is not supported.');
				};
				var createObjectURL = function createObjectURLClosure() {
					var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
					return function createObjectURL(data, contentType, forceDataSchema) {
						if (!forceDataSchema && typeof URL !== 'undefined' && URL.createObjectURL) {
							var blob = createBlob(data, contentType);
							return URL.createObjectURL(blob);
						}
						var buffer = 'data:' + contentType + ';base64,';
						for (var i = 0, ii = data.length; i < ii; i += 3) {
							var b1 = data[i] & 0xFF;
							var b2 = data[i + 1] & 0xFF;
							var b3 = data[i + 2] & 0xFF;
							var d1 = b1 >> 2, d2 = (b1 & 3) << 4 | b2 >> 4;
							var d3 = i + 1 < ii ? (b2 & 0xF) << 2 | b3 >> 6 : 64;
							var d4 = i + 2 < ii ? b3 & 0x3F : 64;
							buffer += digits[d1] + digits[d2] + digits[d3] + digits[d4];
						}
						return buffer;
					};
				}();
				function MessageHandler(sourceName, targetName, comObj) {
					this.sourceName = sourceName;
					this.targetName = targetName;
					this.comObj = comObj;
					this.callbackIndex = 1;
					this.postMessageTransfers = true;
					var callbacksCapabilities = this.callbacksCapabilities = Object.create(null);
					var ah = this.actionHandler = Object.create(null);
					this._onComObjOnMessage = function messageHandlerComObjOnMessage(event) {
						var data = event.data;
						if (data.targetName !== this.sourceName) {
							return;
						}
						if (data.isReply) {
							var callbackId = data.callbackId;
							if (data.callbackId in callbacksCapabilities) {
								var callback = callbacksCapabilities[callbackId];
								delete callbacksCapabilities[callbackId];
								if ('error' in data) {
									callback.reject(data.error);
								} else {
									callback.resolve(data.data);
								}
							} else {
								error('Cannot resolve callback ' + callbackId);
							}
						} else if (data.action in ah) {
							var action = ah[data.action];
							if (data.callbackId) {
								var sourceName = this.sourceName;
								var targetName = data.sourceName;
								Promise.resolve().then(function () {
									return action[0].call(action[1], data.data);
								}).then(function (result) {
									comObj.postMessage({
										sourceName: sourceName,
										targetName: targetName,
										isReply: true,
										callbackId: data.callbackId,
										data: result
									});
								}, function (reason) {
									if (reason instanceof Error) {
										reason = reason + '';
									}
									comObj.postMessage({
										sourceName: sourceName,
										targetName: targetName,
										isReply: true,
										callbackId: data.callbackId,
										error: reason
									});
								});
							} else {
								action[0].call(action[1], data.data);
							}
						} else {
							error('Unknown action from worker: ' + data.action);
						}
					}.bind(this);
					comObj.addEventListener('message', this._onComObjOnMessage);
				}
				MessageHandler.prototype = {
					on: function messageHandlerOn(actionName, handler, scope) {
						var ah = this.actionHandler;
						if (ah[actionName]) {
							error('There is already an actionName called "' + actionName + '"');
						}
						ah[actionName] = [
							handler,
							scope
						];
					},
					send: function messageHandlerSend(actionName, data, transfers) {
						var message = {
							sourceName: this.sourceName,
							targetName: this.targetName,
							action: actionName,
							data: data
						};
						this.postMessage(message, transfers);
					},
					sendWithPromise: function messageHandlerSendWithPromise(actionName, data, transfers) {
						var callbackId = this.callbackIndex++;
						var message = {
							sourceName: this.sourceName,
							targetName: this.targetName,
							action: actionName,
							data: data,
							callbackId: callbackId
						};
						var capability = createPromiseCapability();
						this.callbacksCapabilities[callbackId] = capability;
						try {
							this.postMessage(message, transfers);
						} catch (e) {
							capability.reject(e);
						}
						return capability.promise;
					},
					postMessage: function (message, transfers) {
						if (transfers && this.postMessageTransfers) {
							this.comObj.postMessage(message, transfers);
						} else {
							this.comObj.postMessage(message);
						}
					},
					destroy: function () {
						this.comObj.removeEventListener('message', this._onComObjOnMessage);
					}
				};
				function loadJpegStream(id, imageUrl, objs) {
					var img = new Image();
					img.onload = function loadJpegStream_onloadClosure() {
						objs.resolve(id, img);
					};
					img.onerror = function loadJpegStream_onerrorClosure() {
						objs.resolve(id, null);
						warn('Error during JPEG image loading');
					};
					img.src = imageUrl;
				}
				exports.FONT_IDENTITY_MATRIX = FONT_IDENTITY_MATRIX;
				exports.IDENTITY_MATRIX = IDENTITY_MATRIX;
				exports.OPS = OPS;
				exports.VERBOSITY_LEVELS = VERBOSITY_LEVELS;
				exports.UNSUPPORTED_FEATURES = UNSUPPORTED_FEATURES;
				exports.AnnotationBorderStyleType = AnnotationBorderStyleType;
				exports.AnnotationFieldFlag = AnnotationFieldFlag;
				exports.AnnotationFlag = AnnotationFlag;
				exports.AnnotationType = AnnotationType;
				exports.FontType = FontType;
				exports.ImageKind = ImageKind;
				exports.CMapCompressionType = CMapCompressionType;
				exports.InvalidPDFException = InvalidPDFException;
				exports.MessageHandler = MessageHandler;
				exports.MissingDataException = MissingDataException;
				exports.MissingPDFException = MissingPDFException;
				exports.NotImplementedException = NotImplementedException;
				exports.PageViewport = PageViewport;
				exports.PasswordException = PasswordException;
				exports.PasswordResponses = PasswordResponses;
				exports.StatTimer = StatTimer;
				exports.StreamType = StreamType;
				exports.TextRenderingMode = TextRenderingMode;
				exports.UnexpectedResponseException = UnexpectedResponseException;
				exports.UnknownErrorException = UnknownErrorException;
				exports.Util = Util;
				exports.XRefParseException = XRefParseException;
				exports.arrayByteLength = arrayByteLength;
				exports.arraysToBytes = arraysToBytes;
				exports.assert = assert;
				exports.bytesToString = bytesToString;
				exports.createBlob = createBlob;
				exports.createPromiseCapability = createPromiseCapability;
				exports.createObjectURL = createObjectURL;
				exports.deprecated = deprecated;
				exports.error = error;
				exports.getLookupTableFactory = getLookupTableFactory;
				exports.getVerbosityLevel = getVerbosityLevel;
				exports.globalScope = globalScope;
				exports.info = info;
				exports.isArray = isArray;
				exports.isArrayBuffer = isArrayBuffer;
				exports.isBool = isBool;
				exports.isEmptyObj = isEmptyObj;
				exports.isInt = isInt;
				exports.isNum = isNum;
				exports.isString = isString;
				exports.isSpace = isSpace;
				exports.isNodeJS = isNodeJS;
				exports.isSameOrigin = isSameOrigin;
				exports.createValidAbsoluteUrl = createValidAbsoluteUrl;
				exports.isLittleEndian = isLittleEndian;
				exports.isEvalSupported = isEvalSupported;
				exports.loadJpegStream = loadJpegStream;
				exports.log2 = log2;
				exports.readInt8 = readInt8;
				exports.readUint16 = readUint16;
				exports.readUint32 = readUint32;
				exports.removeNullCharacters = removeNullCharacters;
				exports.setVerbosityLevel = setVerbosityLevel;
				exports.shadow = shadow;
				exports.string32 = string32;
				exports.stringToBytes = stringToBytes;
				exports.stringToPDFString = stringToPDFString;
				exports.stringToUTF8String = stringToUTF8String;
				exports.utf8StringToString = utf8StringToString;
				exports.warn = warn;
				/* WEBPACK VAR INJECTION */
			}.call(exports, __w_pdfjs_require__(6)))

			/***/
		}),
/* 1 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var assert = sharedUtil.assert;
			var removeNullCharacters = sharedUtil.removeNullCharacters;
			var warn = sharedUtil.warn;
			var deprecated = sharedUtil.deprecated;
			var createValidAbsoluteUrl = sharedUtil.createValidAbsoluteUrl;
			var stringToBytes = sharedUtil.stringToBytes;
			var CMapCompressionType = sharedUtil.CMapCompressionType;
			var DEFAULT_LINK_REL = 'noopener noreferrer nofollow';
			function DOMCanvasFactory() {
			}
			DOMCanvasFactory.prototype = {
				create: function DOMCanvasFactory_create(width, height) {
					assert(width > 0 && height > 0, 'invalid canvas size');
					var canvas = document.createElement('canvas');
					var context = canvas.getContext('2d');
					canvas.width = width;
					canvas.height = height;
					return {
						canvas: canvas,
						context: context
					};
				},
				reset: function DOMCanvasFactory_reset(canvasAndContextPair, width, height) {
					assert(canvasAndContextPair.canvas, 'canvas is not specified');
					assert(width > 0 && height > 0, 'invalid canvas size');
					canvasAndContextPair.canvas.width = width;
					canvasAndContextPair.canvas.height = height;
				},
				destroy: function DOMCanvasFactory_destroy(canvasAndContextPair) {
					assert(canvasAndContextPair.canvas, 'canvas is not specified');
					canvasAndContextPair.canvas.width = 0;
					canvasAndContextPair.canvas.height = 0;
					canvasAndContextPair.canvas = null;
					canvasAndContextPair.context = null;
				}
			};
			var DOMCMapReaderFactory = function DOMCMapReaderFactoryClosure() {
				function DOMCMapReaderFactory(params) {
					this.baseUrl = params.baseUrl || null;
					this.isCompressed = params.isCompressed || false;
				}
				DOMCMapReaderFactory.prototype = {
					fetch: function (params) {
						if (!params.name) {
							return Promise.reject(new Error('CMap name must be specified.'));
						}
						return new Promise(function (resolve, reject) {
							var url = this.baseUrl + params.name;
							var request = new XMLHttpRequest();
							if (this.isCompressed) {
								url += '.bcmap';
								request.responseType = 'arraybuffer';
							}
							request.onreadystatechange = function () {
								if (request.readyState === XMLHttpRequest.DONE && (request.status === 200 || request.status === 0)) {
									var data;
									if (this.isCompressed && request.response) {
										data = new Uint8Array(request.response);
									} else if (!this.isCompressed && request.responseText) {
										data = stringToBytes(request.responseText);
									}
									if (data) {
										resolve({
											cMapData: data,
											compressionType: this.isCompressed ? CMapCompressionType.BINARY : CMapCompressionType.NONE
										});
										return;
									}
									reject(new Error('Unable to load ' + (this.isCompressed ? 'binary' : '') + ' CMap at: ' + url));
								}
							}.bind(this);
							request.open('GET', url, true);
							request.send(null);
						}.bind(this));
					}
				};
				return DOMCMapReaderFactory;
			}();
			var CustomStyle = function CustomStyleClosure() {
				var prefixes = [
					'ms',
					'Moz',
					'Webkit',
					'O'
				];
				var _cache = Object.create(null);
				function CustomStyle() {
				}
				CustomStyle.getProp = function get(propName, element) {
					if (arguments.length === 1 && typeof _cache[propName] === 'string') {
						return _cache[propName];
					}
					element = element || document.documentElement;
					var style = element.style, prefixed, uPropName;
					if (typeof style[propName] === 'string') {
						return _cache[propName] = propName;
					}
					uPropName = propName.charAt(0).toUpperCase() + propName.slice(1);
					for (var i = 0, l = prefixes.length; i < l; i++) {
						prefixed = prefixes[i] + uPropName;
						if (typeof style[prefixed] === 'string') {
							return _cache[propName] = prefixed;
						}
					}
					return _cache[propName] = 'undefined';
				};
				CustomStyle.setProp = function set(propName, element, str) {
					var prop = this.getProp(propName);
					if (prop !== 'undefined') {
						element.style[prop] = str;
					}
				};
				return CustomStyle;
			}();
			var hasCanvasTypedArrays;
			hasCanvasTypedArrays = function hasCanvasTypedArrays() {
				var canvas = document.createElement('canvas');
				canvas.width = canvas.height = 1;
				var ctx = canvas.getContext('2d');
				var imageData = ctx.createImageData(1, 1);
				return typeof imageData.data.buffer !== 'undefined';
			};
			var LinkTarget = {
				NONE: 0,
				SELF: 1,
				BLANK: 2,
				PARENT: 3,
				TOP: 4
			};
			var LinkTargetStringMap = [
				'',
				'_self',
				'_blank',
				'_parent',
				'_top'
			];
			function addLinkAttributes(link, params) {
				var url = params && params.url;
				link.href = link.title = url ? removeNullCharacters(url) : '';
				if (url) {
					var target = params.target;
					if (typeof target === 'undefined') {
						target = getDefaultSetting('externalLinkTarget');
					}
					link.target = LinkTargetStringMap[target];
					var rel = params.rel;
					if (typeof rel === 'undefined') {
						rel = getDefaultSetting('externalLinkRel');
					}
					link.rel = rel;
				}
			}
			function getFilenameFromUrl(url) {
				var anchor = url.indexOf('#');
				var query = url.indexOf('?');
				var end = Math.min(anchor > 0 ? anchor : url.length, query > 0 ? query : url.length);
				return url.substring(url.lastIndexOf('/', end) + 1, end);
			}
			function getDefaultSetting(id) {
				var globalSettings = sharedUtil.globalScope.PDFJS;
				switch (id) {
					case 'pdfBug':
						return globalSettings ? globalSettings.pdfBug : false;
					case 'disableAutoFetch':
						return globalSettings ? globalSettings.disableAutoFetch : false;
					case 'disableStream':
						return globalSettings ? globalSettings.disableStream : false;
					case 'disableRange':
						return globalSettings ? globalSettings.disableRange : false;
					case 'disableFontFace':
						return globalSettings ? globalSettings.disableFontFace : false;
					case 'disableCreateObjectURL':
						return globalSettings ? globalSettings.disableCreateObjectURL : false;
					case 'disableWebGL':
						return globalSettings ? globalSettings.disableWebGL : true;
					case 'cMapUrl':
						return globalSettings ? globalSettings.cMapUrl : null;
					case 'cMapPacked':
						return globalSettings ? globalSettings.cMapPacked : false;
					case 'postMessageTransfers':
						return globalSettings ? globalSettings.postMessageTransfers : true;
					case 'workerPort':
						return globalSettings ? globalSettings.workerPort : null;
					case 'workerSrc':
						return globalSettings ? globalSettings.workerSrc : null;
					case 'disableWorker':
						return globalSettings ? globalSettings.disableWorker : false;
					case 'maxImageSize':
						return globalSettings ? globalSettings.maxImageSize : -1;
					case 'imageResourcesPath':
						return globalSettings ? globalSettings.imageResourcesPath : '';
					case 'isEvalSupported':
						return globalSettings ? globalSettings.isEvalSupported : true;
					case 'externalLinkTarget':
						if (!globalSettings) {
							return LinkTarget.NONE;
						}
						switch (globalSettings.externalLinkTarget) {
							case LinkTarget.NONE:
							case LinkTarget.SELF:
							case LinkTarget.BLANK:
							case LinkTarget.PARENT:
							case LinkTarget.TOP:
								return globalSettings.externalLinkTarget;
						}
						warn('PDFJS.externalLinkTarget is invalid: ' + globalSettings.externalLinkTarget);
						globalSettings.externalLinkTarget = LinkTarget.NONE;
						return LinkTarget.NONE;
					case 'externalLinkRel':
						return globalSettings ? globalSettings.externalLinkRel : DEFAULT_LINK_REL;
					case 'enableStats':
						return !!(globalSettings && globalSettings.enableStats);
					default:
						throw new Error('Unknown default setting: ' + id);
				}
			}
			function isExternalLinkTargetSet() {
				var externalLinkTarget = getDefaultSetting('externalLinkTarget');
				switch (externalLinkTarget) {
					case LinkTarget.NONE:
						return false;
					case LinkTarget.SELF:
					case LinkTarget.BLANK:
					case LinkTarget.PARENT:
					case LinkTarget.TOP:
						return true;
				}
			}
			function isValidUrl(url, allowRelative) {
				deprecated('isValidUrl(), please use createValidAbsoluteUrl() instead.');
				var baseUrl = allowRelative ? 'http://example.com' : null;
				return createValidAbsoluteUrl(url, baseUrl) !== null;
			}
			exports.CustomStyle = CustomStyle;
			exports.addLinkAttributes = addLinkAttributes;
			exports.isExternalLinkTargetSet = isExternalLinkTargetSet;
			exports.isValidUrl = isValidUrl;
			exports.getFilenameFromUrl = getFilenameFromUrl;
			exports.LinkTarget = LinkTarget;
			exports.hasCanvasTypedArrays = hasCanvasTypedArrays;
			exports.getDefaultSetting = getDefaultSetting;
			exports.DEFAULT_LINK_REL = DEFAULT_LINK_REL;
			exports.DOMCanvasFactory = DOMCanvasFactory;
			exports.DOMCMapReaderFactory = DOMCMapReaderFactory;

			/***/
		}),
/* 2 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var AnnotationBorderStyleType = sharedUtil.AnnotationBorderStyleType;
			var AnnotationType = sharedUtil.AnnotationType;
			var stringToPDFString = sharedUtil.stringToPDFString;
			var Util = sharedUtil.Util;
			var addLinkAttributes = displayDOMUtils.addLinkAttributes;
			var LinkTarget = displayDOMUtils.LinkTarget;
			var getFilenameFromUrl = displayDOMUtils.getFilenameFromUrl;
			var warn = sharedUtil.warn;
			var CustomStyle = displayDOMUtils.CustomStyle;
			var getDefaultSetting = displayDOMUtils.getDefaultSetting;
			function AnnotationElementFactory() {
			}
			AnnotationElementFactory.prototype = {
				create: function AnnotationElementFactory_create(parameters) {
					var subtype = parameters.data.annotationType;
					switch (subtype) {
						case AnnotationType.LINK:
							return new LinkAnnotationElement(parameters);
						case AnnotationType.TEXT:
							return new TextAnnotationElement(parameters);
						case AnnotationType.WIDGET:
							var fieldType = parameters.data.fieldType;
							switch (fieldType) {
								case 'Tx':
									return new TextWidgetAnnotationElement(parameters);
								case 'Btn':
									if (parameters.data.radioButton) {
										return new RadioButtonWidgetAnnotationElement(parameters);
									} else if (parameters.data.checkBox) {
										return new CheckboxWidgetAnnotationElement(parameters);
									}
									warn('Unimplemented button widget annotation: pushbutton');
									break;
								case 'Ch':
									return new ChoiceWidgetAnnotationElement(parameters);
							}
							return new WidgetAnnotationElement(parameters);
						case AnnotationType.POPUP:
							return new PopupAnnotationElement(parameters);
						case AnnotationType.HIGHLIGHT:
							return new HighlightAnnotationElement(parameters);
						case AnnotationType.UNDERLINE:
							return new UnderlineAnnotationElement(parameters);
						case AnnotationType.SQUIGGLY:
							return new SquigglyAnnotationElement(parameters);
						case AnnotationType.STRIKEOUT:
							return new StrikeOutAnnotationElement(parameters);
						case AnnotationType.FILEATTACHMENT:
							return new FileAttachmentAnnotationElement(parameters);
						default:
							return new AnnotationElement(parameters);
					}
				}
			};
			var AnnotationElement = function AnnotationElementClosure() {
				function AnnotationElement(parameters, isRenderable) {
					this.isRenderable = isRenderable || false;
					this.data = parameters.data;
					this.layer = parameters.layer;
					this.page = parameters.page;
					this.viewport = parameters.viewport;
					this.linkService = parameters.linkService;
					this.downloadManager = parameters.downloadManager;
					this.imageResourcesPath = parameters.imageResourcesPath;
					this.renderInteractiveForms = parameters.renderInteractiveForms;
					if (isRenderable) {
						this.container = this._createContainer();
					}
				}
				AnnotationElement.prototype = {
					_createContainer: function AnnotationElement_createContainer() {
						var data = this.data, page = this.page, viewport = this.viewport;
						var container = document.createElement('section');
						var width = data.rect[2] - data.rect[0];
						var height = data.rect[3] - data.rect[1];
						container.setAttribute('data-annotation-id', data.id);
						var rect = Util.normalizeRect([
							data.rect[0],
							page.view[3] - data.rect[1] + page.view[1],
							data.rect[2],
							page.view[3] - data.rect[3] + page.view[1]
						]);
						CustomStyle.setProp('transform', container, 'matrix(' + viewport.transform.join(',') + ')');
						CustomStyle.setProp('transformOrigin', container, -rect[0] + 'px ' + -rect[1] + 'px');
						if (data.borderStyle.width > 0) {
							container.style.borderWidth = data.borderStyle.width + 'px';
							if (data.borderStyle.style !== AnnotationBorderStyleType.UNDERLINE) {
								width = width - 2 * data.borderStyle.width;
								height = height - 2 * data.borderStyle.width;
							}
							var horizontalRadius = data.borderStyle.horizontalCornerRadius;
							var verticalRadius = data.borderStyle.verticalCornerRadius;
							if (horizontalRadius > 0 || verticalRadius > 0) {
								var radius = horizontalRadius + 'px / ' + verticalRadius + 'px';
								CustomStyle.setProp('borderRadius', container, radius);
							}
							switch (data.borderStyle.style) {
								case AnnotationBorderStyleType.SOLID:
									container.style.borderStyle = 'solid';
									break;
								case AnnotationBorderStyleType.DASHED:
									container.style.borderStyle = 'dashed';
									break;
								case AnnotationBorderStyleType.BEVELED:
									warn('Unimplemented border style: beveled');
									break;
								case AnnotationBorderStyleType.INSET:
									warn('Unimplemented border style: inset');
									break;
								case AnnotationBorderStyleType.UNDERLINE:
									container.style.borderBottomStyle = 'solid';
									break;
								default:
									break;
							}
							if (data.color) {
								container.style.borderColor = Util.makeCssRgb(data.color[0] | 0, data.color[1] | 0, data.color[2] | 0);
							} else {
								container.style.borderWidth = 0;
							}
						}
						container.style.left = rect[0] + 'px';
						container.style.top = rect[1] + 'px';
						container.style.width = width + 'px';
						container.style.height = height + 'px';
						return container;
					},
					_createPopup: function AnnotationElement_createPopup(container, trigger, data) {
						if (!trigger) {
							trigger = document.createElement('div');
							trigger.style.height = container.style.height;
							trigger.style.width = container.style.width;
							container.appendChild(trigger);
						}
						var popupElement = new PopupElement({
							container: container,
							trigger: trigger,
							color: data.color,
							title: data.title,
							contents: data.contents,
							hideWrapper: true
						});
						var popup = popupElement.render();
						popup.style.left = container.style.width;
						container.appendChild(popup);
					},
					render: function AnnotationElement_render() {
						throw new Error('Abstract method AnnotationElement.render called');
					}
				};
				return AnnotationElement;
			}();
			var LinkAnnotationElement = function LinkAnnotationElementClosure() {
				function LinkAnnotationElement(parameters) {
					AnnotationElement.call(this, parameters, true);
				}
				Util.inherit(LinkAnnotationElement, AnnotationElement, {
					render: function LinkAnnotationElement_render() {
						this.container.className = 'linkAnnotation';
						var link = document.createElement('a');
						addLinkAttributes(link, {
							url: this.data.url,
							target: this.data.newWindow ? LinkTarget.BLANK : undefined
						});
						if (!this.data.url) {
							if (this.data.action) {
								this._bindNamedAction(link, this.data.action);
							} else {
								this._bindLink(link, this.data.dest);
							}
						}
						this.container.appendChild(link);
						return this.container;
					},
					_bindLink: function LinkAnnotationElement_bindLink(link, destination) {
						var self = this;
						link.href = this.linkService.getDestinationHash(destination);
						link.onclick = function () {
							if (destination) {
								self.linkService.navigateTo(destination);
							}
							return false;
						};
						if (destination) {
							link.className = 'internalLink';
						}
					},
					_bindNamedAction: function LinkAnnotationElement_bindNamedAction(link, action) {
						var self = this;
						link.href = this.linkService.getAnchorUrl('');
						link.onclick = function () {
							self.linkService.executeNamedAction(action);
							return false;
						};
						link.className = 'internalLink';
					}
				});
				return LinkAnnotationElement;
			}();
			var TextAnnotationElement = function TextAnnotationElementClosure() {
				function TextAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.hasPopup || parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(TextAnnotationElement, AnnotationElement, {
					render: function TextAnnotationElement_render() {
						this.container.className = 'textAnnotation';
						var image = document.createElement('img');
						image.style.height = this.container.style.height;
						image.style.width = this.container.style.width;
						image.src = this.imageResourcesPath + 'annotation-' + this.data.name.toLowerCase() + '.svg';
						image.alt = '[{{type}} Annotation]';
						image.dataset.l10nId = 'text_annotation_type';
						image.dataset.l10nArgs = JSON.stringify({ type: this.data.name });
						if (!this.data.hasPopup) {
							this._createPopup(this.container, image, this.data);
						}
						this.container.appendChild(image);
						return this.container;
					}
				});
				return TextAnnotationElement;
			}();
			var WidgetAnnotationElement = function WidgetAnnotationElementClosure() {
				function WidgetAnnotationElement(parameters, isRenderable) {
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(WidgetAnnotationElement, AnnotationElement, {
					render: function WidgetAnnotationElement_render() {
						return this.container;
					}
				});
				return WidgetAnnotationElement;
			}();
			var TextWidgetAnnotationElement = function TextWidgetAnnotationElementClosure() {
				var TEXT_ALIGNMENT = [
					'left',
					'center',
					'right'
				];
				function TextWidgetAnnotationElement(parameters) {
					var isRenderable = parameters.renderInteractiveForms || !parameters.data.hasAppearance && !!parameters.data.fieldValue;
					WidgetAnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(TextWidgetAnnotationElement, WidgetAnnotationElement, {
					render: function TextWidgetAnnotationElement_render() {
						this.container.className = 'textWidgetAnnotation';
						var element = null;
						if (this.renderInteractiveForms) {
							if (this.data.multiLine) {
								element = document.createElement('textarea');
								element.textContent = this.data.fieldValue;
							} else {
								element = document.createElement('input');
								element.type = 'text';
								element.setAttribute('value', this.data.fieldValue);
							}
							element.disabled = this.data.readOnly;
							if (this.data.maxLen !== null) {
								element.maxLength = this.data.maxLen;
							}
							if (this.data.comb) {
								var fieldWidth = this.data.rect[2] - this.data.rect[0];
								var combWidth = fieldWidth / this.data.maxLen;
								element.classList.add('comb');
								element.style.letterSpacing = 'calc(' + combWidth + 'px - 1ch)';
							}
						} else {
							element = document.createElement('div');
							element.textContent = this.data.fieldValue;
							element.style.verticalAlign = 'middle';
							element.style.display = 'table-cell';
							var font = null;
							if (this.data.fontRefName) {
								font = this.page.commonObjs.getData(this.data.fontRefName);
							}
							this._setTextStyle(element, font);
						}
						if (this.data.textAlignment !== null) {
							element.style.textAlign = TEXT_ALIGNMENT[this.data.textAlignment];
						}
						this.container.appendChild(element);
						return this.container;
					},
					_setTextStyle: function TextWidgetAnnotationElement_setTextStyle(element, font) {
						var style = element.style;
						style.fontSize = this.data.fontSize + 'px';
						style.direction = this.data.fontDirection < 0 ? 'rtl' : 'ltr';
						if (!font) {
							return;
						}
						style.fontWeight = font.black ? font.bold ? '900' : 'bold' : font.bold ? 'bold' : 'normal';
						style.fontStyle = font.italic ? 'italic' : 'normal';
						var fontFamily = font.loadedName ? '"' + font.loadedName + '", ' : '';
						var fallbackName = font.fallbackName || 'Helvetica, sans-serif';
						style.fontFamily = fontFamily + fallbackName;
					}
				});
				return TextWidgetAnnotationElement;
			}();
			var CheckboxWidgetAnnotationElement = function CheckboxWidgetAnnotationElementClosure() {
				function CheckboxWidgetAnnotationElement(parameters) {
					WidgetAnnotationElement.call(this, parameters, parameters.renderInteractiveForms);
				}
				Util.inherit(CheckboxWidgetAnnotationElement, WidgetAnnotationElement, {
					render: function CheckboxWidgetAnnotationElement_render() {
						this.container.className = 'buttonWidgetAnnotation checkBox';
						var element = document.createElement('input');
						element.disabled = this.data.readOnly;
						element.type = 'checkbox';
						if (this.data.fieldValue && this.data.fieldValue !== 'Off') {
							element.setAttribute('checked', true);
						}
						this.container.appendChild(element);
						return this.container;
					}
				});
				return CheckboxWidgetAnnotationElement;
			}();
			var RadioButtonWidgetAnnotationElement = function RadioButtonWidgetAnnotationElementClosure() {
				function RadioButtonWidgetAnnotationElement(parameters) {
					WidgetAnnotationElement.call(this, parameters, parameters.renderInteractiveForms);
				}
				Util.inherit(RadioButtonWidgetAnnotationElement, WidgetAnnotationElement, {
					render: function RadioButtonWidgetAnnotationElement_render() {
						this.container.className = 'buttonWidgetAnnotation radioButton';
						var element = document.createElement('input');
						element.disabled = this.data.readOnly;
						element.type = 'radio';
						element.name = this.data.fieldName;
						if (this.data.fieldValue === this.data.buttonValue) {
							element.setAttribute('checked', true);
						}
						this.container.appendChild(element);
						return this.container;
					}
				});
				return RadioButtonWidgetAnnotationElement;
			}();
			var ChoiceWidgetAnnotationElement = function ChoiceWidgetAnnotationElementClosure() {
				function ChoiceWidgetAnnotationElement(parameters) {
					WidgetAnnotationElement.call(this, parameters, parameters.renderInteractiveForms);
				}
				Util.inherit(ChoiceWidgetAnnotationElement, WidgetAnnotationElement, {
					render: function ChoiceWidgetAnnotationElement_render() {
						this.container.className = 'choiceWidgetAnnotation';
						var selectElement = document.createElement('select');
						selectElement.disabled = this.data.readOnly;
						if (!this.data.combo) {
							selectElement.size = this.data.options.length;
							if (this.data.multiSelect) {
								selectElement.multiple = true;
							}
						}
						for (var i = 0, ii = this.data.options.length; i < ii; i++) {
							var option = this.data.options[i];
							var optionElement = document.createElement('option');
							optionElement.textContent = option.displayValue;
							optionElement.value = option.exportValue;
							if (this.data.fieldValue.indexOf(option.displayValue) >= 0) {
								optionElement.setAttribute('selected', true);
							}
							selectElement.appendChild(optionElement);
						}
						this.container.appendChild(selectElement);
						return this.container;
					}
				});
				return ChoiceWidgetAnnotationElement;
			}();
			var PopupAnnotationElement = function PopupAnnotationElementClosure() {
				function PopupAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(PopupAnnotationElement, AnnotationElement, {
					render: function PopupAnnotationElement_render() {
						this.container.className = 'popupAnnotation';
						var selector = '[data-annotation-id="' + this.data.parentId + '"]';
						var parentElement = this.layer.querySelector(selector);
						if (!parentElement) {
							return this.container;
						}
						var popup = new PopupElement({
							container: this.container,
							trigger: parentElement,
							color: this.data.color,
							title: this.data.title,
							contents: this.data.contents
						});
						var parentLeft = parseFloat(parentElement.style.left);
						var parentWidth = parseFloat(parentElement.style.width);
						CustomStyle.setProp('transformOrigin', this.container, -(parentLeft + parentWidth) + 'px -' + parentElement.style.top);
						this.container.style.left = parentLeft + parentWidth + 'px';
						this.container.appendChild(popup.render());
						return this.container;
					}
				});
				return PopupAnnotationElement;
			}();
			var PopupElement = function PopupElementClosure() {
				var BACKGROUND_ENLIGHT = 0.7;
				function PopupElement(parameters) {
					this.container = parameters.container;
					this.trigger = parameters.trigger;
					this.color = parameters.color;
					this.title = parameters.title;
					this.contents = parameters.contents;
					this.hideWrapper = parameters.hideWrapper || false;
					this.pinned = false;
				}
				PopupElement.prototype = {
					render: function PopupElement_render() {
						var wrapper = document.createElement('div');
						wrapper.className = 'popupWrapper';
						this.hideElement = this.hideWrapper ? wrapper : this.container;
						this.hideElement.setAttribute('hidden', true);
						var popup = document.createElement('div');
						popup.className = 'popup';
						var color = this.color;
						if (color) {
							var r = BACKGROUND_ENLIGHT * (255 - color[0]) + color[0];
							var g = BACKGROUND_ENLIGHT * (255 - color[1]) + color[1];
							var b = BACKGROUND_ENLIGHT * (255 - color[2]) + color[2];
							popup.style.backgroundColor = Util.makeCssRgb(r | 0, g | 0, b | 0);
						}
						var contents = this._formatContents(this.contents);
						var title = document.createElement('h1');
						title.textContent = this.title;
						this.trigger.addEventListener('click', this._toggle.bind(this));
						this.trigger.addEventListener('mouseover', this._show.bind(this, false));
						this.trigger.addEventListener('mouseout', this._hide.bind(this, false));
						popup.addEventListener('click', this._hide.bind(this, true));
						popup.appendChild(title);
						popup.appendChild(contents);
						wrapper.appendChild(popup);
						return wrapper;
					},
					_formatContents: function PopupElement_formatContents(contents) {
						var p = document.createElement('p');
						var lines = contents.split(/(?:\r\n?|\n)/);
						for (var i = 0, ii = lines.length; i < ii; ++i) {
							var line = lines[i];
							p.appendChild(document.createTextNode(line));
							if (i < ii - 1) {
								p.appendChild(document.createElement('br'));
							}
						}
						return p;
					},
					_toggle: function PopupElement_toggle() {
						if (this.pinned) {
							this._hide(true);
						} else {
							this._show(true);
						}
					},
					_show: function PopupElement_show(pin) {
						if (pin) {
							this.pinned = true;
						}
						if (this.hideElement.hasAttribute('hidden')) {
							this.hideElement.removeAttribute('hidden');
							this.container.style.zIndex += 1;
						}
					},
					_hide: function PopupElement_hide(unpin) {
						if (unpin) {
							this.pinned = false;
						}
						if (!this.hideElement.hasAttribute('hidden') && !this.pinned) {
							this.hideElement.setAttribute('hidden', true);
							this.container.style.zIndex -= 1;
						}
					}
				};
				return PopupElement;
			}();
			var HighlightAnnotationElement = function HighlightAnnotationElementClosure() {
				function HighlightAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.hasPopup || parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(HighlightAnnotationElement, AnnotationElement, {
					render: function HighlightAnnotationElement_render() {
						this.container.className = 'highlightAnnotation';
						if (!this.data.hasPopup) {
							this._createPopup(this.container, null, this.data);
						}
						return this.container;
					}
				});
				return HighlightAnnotationElement;
			}();
			var UnderlineAnnotationElement = function UnderlineAnnotationElementClosure() {
				function UnderlineAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.hasPopup || parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(UnderlineAnnotationElement, AnnotationElement, {
					render: function UnderlineAnnotationElement_render() {
						this.container.className = 'underlineAnnotation';
						if (!this.data.hasPopup) {
							this._createPopup(this.container, null, this.data);
						}
						return this.container;
					}
				});
				return UnderlineAnnotationElement;
			}();
			var SquigglyAnnotationElement = function SquigglyAnnotationElementClosure() {
				function SquigglyAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.hasPopup || parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(SquigglyAnnotationElement, AnnotationElement, {
					render: function SquigglyAnnotationElement_render() {
						this.container.className = 'squigglyAnnotation';
						if (!this.data.hasPopup) {
							this._createPopup(this.container, null, this.data);
						}
						return this.container;
					}
				});
				return SquigglyAnnotationElement;
			}();
			var StrikeOutAnnotationElement = function StrikeOutAnnotationElementClosure() {
				function StrikeOutAnnotationElement(parameters) {
					var isRenderable = !!(parameters.data.hasPopup || parameters.data.title || parameters.data.contents);
					AnnotationElement.call(this, parameters, isRenderable);
				}
				Util.inherit(StrikeOutAnnotationElement, AnnotationElement, {
					render: function StrikeOutAnnotationElement_render() {
						this.container.className = 'strikeoutAnnotation';
						if (!this.data.hasPopup) {
							this._createPopup(this.container, null, this.data);
						}
						return this.container;
					}
				});
				return StrikeOutAnnotationElement;
			}();
			var FileAttachmentAnnotationElement = function FileAttachmentAnnotationElementClosure() {
				function FileAttachmentAnnotationElement(parameters) {
					AnnotationElement.call(this, parameters, true);
					var file = this.data.file;
					this.filename = getFilenameFromUrl(file.filename);
					this.content = file.content;
					this.linkService.onFileAttachmentAnnotation({
						id: stringToPDFString(file.filename),
						filename: file.filename,
						content: file.content
					});
				}
				Util.inherit(FileAttachmentAnnotationElement, AnnotationElement, {
					render: function FileAttachmentAnnotationElement_render() {
						this.container.className = 'fileAttachmentAnnotation';
						var trigger = document.createElement('div');
						trigger.style.height = this.container.style.height;
						trigger.style.width = this.container.style.width;
						trigger.addEventListener('dblclick', this._download.bind(this));
						if (!this.data.hasPopup && (this.data.title || this.data.contents)) {
							this._createPopup(this.container, trigger, this.data);
						}
						this.container.appendChild(trigger);
						return this.container;
					},
					_download: function FileAttachmentAnnotationElement_download() {
						if (!this.downloadManager) {
							warn('Download cannot be started due to unavailable download manager');
							return;
						}
						this.downloadManager.downloadData(this.content, this.filename, '');
					}
				});
				return FileAttachmentAnnotationElement;
			}();
			var AnnotationLayer = function AnnotationLayerClosure() {
				return {
					render: function AnnotationLayer_render(parameters) {
						var annotationElementFactory = new AnnotationElementFactory();
						for (var i = 0, ii = parameters.annotations.length; i < ii; i++) {
							var data = parameters.annotations[i];
							if (!data) {
								continue;
							}
							var element = annotationElementFactory.create({
								data: data,
								layer: parameters.div,
								page: parameters.page,
								viewport: parameters.viewport,
								linkService: parameters.linkService,
								downloadManager: parameters.downloadManager,
								imageResourcesPath: parameters.imageResourcesPath || getDefaultSetting('imageResourcesPath'),
								renderInteractiveForms: parameters.renderInteractiveForms || false
							});
							if (element.isRenderable) {
								parameters.div.appendChild(element.render());
							}
						}
					},
					update: function AnnotationLayer_update(parameters) {
						for (var i = 0, ii = parameters.annotations.length; i < ii; i++) {
							var data = parameters.annotations[i];
							var element = parameters.div.querySelector('[data-annotation-id="' + data.id + '"]');
							if (element) {
								CustomStyle.setProp('transform', element, 'matrix(' + parameters.viewport.transform.join(',') + ')');
							}
						}
						parameters.div.removeAttribute('hidden');
					}
				};
			}();
			exports.AnnotationLayer = AnnotationLayer;

			/***/
		}),
/* 3 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayFontLoader = __w_pdfjs_require__(11);
			var displayCanvas = __w_pdfjs_require__(10);
			var displayMetadata = __w_pdfjs_require__(7);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var amdRequire;
			var InvalidPDFException = sharedUtil.InvalidPDFException;
			var MessageHandler = sharedUtil.MessageHandler;
			var MissingPDFException = sharedUtil.MissingPDFException;
			var PageViewport = sharedUtil.PageViewport;
			var PasswordException = sharedUtil.PasswordException;
			var StatTimer = sharedUtil.StatTimer;
			var UnexpectedResponseException = sharedUtil.UnexpectedResponseException;
			var UnknownErrorException = sharedUtil.UnknownErrorException;
			var Util = sharedUtil.Util;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var error = sharedUtil.error;
			var deprecated = sharedUtil.deprecated;
			var getVerbosityLevel = sharedUtil.getVerbosityLevel;
			var info = sharedUtil.info;
			var isInt = sharedUtil.isInt;
			var isArray = sharedUtil.isArray;
			var isArrayBuffer = sharedUtil.isArrayBuffer;
			var isSameOrigin = sharedUtil.isSameOrigin;
			var loadJpegStream = sharedUtil.loadJpegStream;
			var stringToBytes = sharedUtil.stringToBytes;
			var globalScope = sharedUtil.globalScope;
			var warn = sharedUtil.warn;
			var FontFaceObject = displayFontLoader.FontFaceObject;
			var FontLoader = displayFontLoader.FontLoader;
			var CanvasGraphics = displayCanvas.CanvasGraphics;
			var Metadata = displayMetadata.Metadata;
			var getDefaultSetting = displayDOMUtils.getDefaultSetting;
			var DOMCanvasFactory = displayDOMUtils.DOMCanvasFactory;
			var DOMCMapReaderFactory = displayDOMUtils.DOMCMapReaderFactory;
			var DEFAULT_RANGE_CHUNK_SIZE = 65536;
			var isWorkerDisabled = false;
			var workerSrc;
			var isPostMessageTransfersDisabled = false;
			var pdfjsFilePath = typeof document !== 'undefined' && document.currentScript ? document.currentScript.src : null;
			var fakeWorkerFilesLoader = null;
			var useRequireEnsure = false;
			if (typeof __pdfjsdev_webpack__ === 'undefined') {
				if (typeof window === 'undefined') {
					isWorkerDisabled = true;
					if (typeof require.ensure === 'undefined') {
						require.ensure = require('node-ensure');
					}
					useRequireEnsure = true;
				} else if (typeof require !== 'undefined' && typeof require.ensure === 'function') {
					useRequireEnsure = true;
				}
				if (typeof requirejs !== 'undefined' && requirejs.toUrl) {
					workerSrc = requirejs.toUrl('pdfjs-dist/build/pdf.worker.js');
				}
				var dynamicLoaderSupported = typeof requirejs !== 'undefined' && requirejs.load;
				fakeWorkerFilesLoader = useRequireEnsure ? function (callback) {
					require.ensure([], function () {
						var worker = require('./pdf.worker.js');
						callback(worker.WorkerMessageHandler);
					});
				} : dynamicLoaderSupported ? function (callback) {
					requirejs(['pdfjs-dist/build/pdf.worker'], function (worker) {
						callback(worker.WorkerMessageHandler);
					});
				} : null;
			}
			function getDocument(src, pdfDataRangeTransport, passwordCallback, progressCallback) {
				var task = new PDFDocumentLoadingTask();
				if (arguments.length > 1) {
					deprecated('getDocument is called with pdfDataRangeTransport, ' + 'passwordCallback or progressCallback argument');
				}
				if (pdfDataRangeTransport) {
					if (!(pdfDataRangeTransport instanceof PDFDataRangeTransport)) {
						pdfDataRangeTransport = Object.create(pdfDataRangeTransport);
						pdfDataRangeTransport.length = src.length;
						pdfDataRangeTransport.initialData = src.initialData;
						if (!pdfDataRangeTransport.abort) {
							pdfDataRangeTransport.abort = function () {
							};
						}
					}
					src = Object.create(src);
					src.range = pdfDataRangeTransport;
				}
				task.onPassword = passwordCallback || null;
				task.onProgress = progressCallback || null;
				var source;
				if (typeof src === 'string') {
					source = { url: src };
				} else if (isArrayBuffer(src)) {
					source = { data: src };
				} else if (src instanceof PDFDataRangeTransport) {
					source = { range: src };
				} else {
					if (typeof src !== 'object') {
						error('Invalid parameter in getDocument, need either Uint8Array, ' + 'string or a parameter object');
					}
					if (!src.url && !src.data && !src.range) {
						error('Invalid parameter object: need either .data, .range or .url');
					}
					source = src;
				}
				var params = {};
				var rangeTransport = null;
				var worker = null;
				for (var key in source) {
					if (key === 'url' && typeof window !== 'undefined') {
						params[key] = new URL(source[key], window.location).href;
						continue;
					} else if (key === 'range') {
						rangeTransport = source[key];
						continue;
					} else if (key === 'worker') {
						worker = source[key];
						continue;
					} else if (key === 'data' && !(source[key] instanceof Uint8Array)) {
						var pdfBytes = source[key];
						if (typeof pdfBytes === 'string') {
							params[key] = stringToBytes(pdfBytes);
						} else if (typeof pdfBytes === 'object' && pdfBytes !== null && !isNaN(pdfBytes.length)) {
							params[key] = new Uint8Array(pdfBytes);
						} else if (isArrayBuffer(pdfBytes)) {
							params[key] = new Uint8Array(pdfBytes);
						} else {
							error('Invalid PDF binary data: either typed array, string or ' + 'array-like object is expected in the data property.');
						}
						continue;
					}
					params[key] = source[key];
				}
				params.rangeChunkSize = params.rangeChunkSize || DEFAULT_RANGE_CHUNK_SIZE;
				params.disableNativeImageDecoder = params.disableNativeImageDecoder === true;
				var CMapReaderFactory = params.CMapReaderFactory || DOMCMapReaderFactory;
				if (!worker) {
					var workerPort = getDefaultSetting('workerPort');
					worker = workerPort ? new PDFWorker(null, workerPort) : new PDFWorker();
					task._worker = worker;
				}
				var docId = task.docId;
				worker.promise.then(function () {
					if (task.destroyed) {
						throw new Error('Loading aborted');
					}
					return _fetchDocument(worker, params, rangeTransport, docId).then(function (workerId) {
						if (task.destroyed) {
							throw new Error('Loading aborted');
						}
						var messageHandler = new MessageHandler(docId, workerId, worker.port);
						var transport = new WorkerTransport(messageHandler, task, rangeTransport, CMapReaderFactory);
						task._transport = transport;
						messageHandler.send('Ready', null);
					});
				}).catch(task._capability.reject);
				return task;
			}
			function _fetchDocument(worker, source, pdfDataRangeTransport, docId) {
				if (worker.destroyed) {
					return Promise.reject(new Error('Worker was destroyed'));
				}
				source.disableAutoFetch = getDefaultSetting('disableAutoFetch');
				source.disableStream = getDefaultSetting('disableStream');
				source.chunkedViewerLoading = !!pdfDataRangeTransport;
				if (pdfDataRangeTransport) {
					source.length = pdfDataRangeTransport.length;
					source.initialData = pdfDataRangeTransport.initialData;
				}
				return worker.messageHandler.sendWithPromise('GetDocRequest', {
					docId: docId,
					source: source,
					disableRange: getDefaultSetting('disableRange'),
					maxImageSize: getDefaultSetting('maxImageSize'),
					disableFontFace: getDefaultSetting('disableFontFace'),
					disableCreateObjectURL: getDefaultSetting('disableCreateObjectURL'),
					postMessageTransfers: getDefaultSetting('postMessageTransfers') && !isPostMessageTransfersDisabled,
					docBaseUrl: source.docBaseUrl,
					disableNativeImageDecoder: source.disableNativeImageDecoder
				}).then(function (workerId) {
					if (worker.destroyed) {
						throw new Error('Worker was destroyed');
					}
					return workerId;
				});
			}
			var PDFDocumentLoadingTask = function PDFDocumentLoadingTaskClosure() {
				var nextDocumentId = 0;
				function PDFDocumentLoadingTask() {
					this._capability = createPromiseCapability();
					this._transport = null;
					this._worker = null;
					this.docId = 'd' + nextDocumentId++;
					this.destroyed = false;
					this.onPassword = null;
					this.onProgress = null;
					this.onUnsupportedFeature = null;
				}
				PDFDocumentLoadingTask.prototype = {
					get promise() {
						return this._capability.promise;
					},
					destroy: function () {
						this.destroyed = true;
						var transportDestroyed = !this._transport ? Promise.resolve() : this._transport.destroy();
						return transportDestroyed.then(function () {
							this._transport = null;
							if (this._worker) {
								this._worker.destroy();
								this._worker = null;
							}
						}.bind(this));
					},
					then: function PDFDocumentLoadingTask_then(onFulfilled, onRejected) {
						return this.promise.then.apply(this.promise, arguments);
					}
				};
				return PDFDocumentLoadingTask;
			}();
			var PDFDataRangeTransport = function pdfDataRangeTransportClosure() {
				function PDFDataRangeTransport(length, initialData) {
					this.length = length;
					this.initialData = initialData;
					this._rangeListeners = [];
					this._progressListeners = [];
					this._progressiveReadListeners = [];
					this._readyCapability = createPromiseCapability();
				}
				PDFDataRangeTransport.prototype = {
					addRangeListener: function PDFDataRangeTransport_addRangeListener(listener) {
						this._rangeListeners.push(listener);
					},
					addProgressListener: function PDFDataRangeTransport_addProgressListener(listener) {
						this._progressListeners.push(listener);
					},
					addProgressiveReadListener: function PDFDataRangeTransport_addProgressiveReadListener(listener) {
						this._progressiveReadListeners.push(listener);
					},
					onDataRange: function PDFDataRangeTransport_onDataRange(begin, chunk) {
						var listeners = this._rangeListeners;
						for (var i = 0, n = listeners.length; i < n; ++i) {
							listeners[i](begin, chunk);
						}
					},
					onDataProgress: function PDFDataRangeTransport_onDataProgress(loaded) {
						this._readyCapability.promise.then(function () {
							var listeners = this._progressListeners;
							for (var i = 0, n = listeners.length; i < n; ++i) {
								listeners[i](loaded);
							}
						}.bind(this));
					},
					onDataProgressiveRead: function PDFDataRangeTransport_onDataProgress(chunk) {
						this._readyCapability.promise.then(function () {
							var listeners = this._progressiveReadListeners;
							for (var i = 0, n = listeners.length; i < n; ++i) {
								listeners[i](chunk);
							}
						}.bind(this));
					},
					transportReady: function PDFDataRangeTransport_transportReady() {
						this._readyCapability.resolve();
					},
					requestDataRange: function PDFDataRangeTransport_requestDataRange(begin, end) {
						throw new Error('Abstract method PDFDataRangeTransport.requestDataRange');
					},
					abort: function PDFDataRangeTransport_abort() {
					}
				};
				return PDFDataRangeTransport;
			}();
			var PDFDocumentProxy = function PDFDocumentProxyClosure() {
				function PDFDocumentProxy(pdfInfo, transport, loadingTask) {
					this.pdfInfo = pdfInfo;
					this.transport = transport;
					this.loadingTask = loadingTask;
				}
				PDFDocumentProxy.prototype = {
					get numPages() {
						return this.pdfInfo.numPages;
					},
					get fingerprint() {
						return this.pdfInfo.fingerprint;
					},
					getPage: function PDFDocumentProxy_getPage(pageNumber) {
						return this.transport.getPage(pageNumber);
					},
					getPageIndex: function PDFDocumentProxy_getPageIndex(ref) {
						return this.transport.getPageIndex(ref);
					},
					getDestinations: function PDFDocumentProxy_getDestinations() {
						return this.transport.getDestinations();
					},
					getDestination: function PDFDocumentProxy_getDestination(id) {
						return this.transport.getDestination(id);
					},
					getPageLabels: function PDFDocumentProxy_getPageLabels() {
						return this.transport.getPageLabels();
					},
					getAttachments: function PDFDocumentProxy_getAttachments() {
						return this.transport.getAttachments();
					},
					getJavaScript: function PDFDocumentProxy_getJavaScript() {
						return this.transport.getJavaScript();
					},
					getOutline: function PDFDocumentProxy_getOutline() {
						return this.transport.getOutline();
					},
					getMetadata: function PDFDocumentProxy_getMetadata() {
						return this.transport.getMetadata();
					},
					getData: function PDFDocumentProxy_getData() {
						return this.transport.getData();
					},
					getDownloadInfo: function PDFDocumentProxy_getDownloadInfo() {
						return this.transport.downloadInfoCapability.promise;
					},
					getStats: function PDFDocumentProxy_getStats() {
						return this.transport.getStats();
					},
					cleanup: function PDFDocumentProxy_cleanup() {
						this.transport.startCleanup();
					},
					destroy: function PDFDocumentProxy_destroy() {
						return this.loadingTask.destroy();
					}
				};
				return PDFDocumentProxy;
			}();
			var PDFPageProxy = function PDFPageProxyClosure() {
				function PDFPageProxy(pageIndex, pageInfo, transport) {
					this.pageIndex = pageIndex;
					this.pageInfo = pageInfo;
					this.transport = transport;
					this.stats = new StatTimer();
					this.stats.enabled = getDefaultSetting('enableStats');
					this.commonObjs = transport.commonObjs;
					this.objs = new PDFObjects();
					this.cleanupAfterRender = false;
					this.pendingCleanup = false;
					this.intentStates = Object.create(null);
					this.destroyed = false;
				}
				PDFPageProxy.prototype = {
					get pageNumber() {
						return this.pageIndex + 1;
					},
					get rotate() {
						return this.pageInfo.rotate;
					},
					get ref() {
						return this.pageInfo.ref;
					},
					get userUnit() {
						return this.pageInfo.userUnit;
					},
					get view() {
						return this.pageInfo.view;
					},
					getViewport: function PDFPageProxy_getViewport(scale, rotate) {
						if (arguments.length < 2) {
							rotate = this.rotate;
						}
						return new PageViewport(this.view, scale, rotate, 0, 0);
					},
					getAnnotations: function PDFPageProxy_getAnnotations(params) {
						var intent = params && params.intent || null;
						if (!this.annotationsPromise || this.annotationsIntent !== intent) {
							this.annotationsPromise = this.transport.getAnnotations(this.pageIndex, intent);
							this.annotationsIntent = intent;
						}
						return this.annotationsPromise;
					},
					render: function PDFPageProxy_render(params) {
						var stats = this.stats;
						stats.time('Overall');
						this.pendingCleanup = false;
						var renderingIntent = params.intent === 'print' ? 'print' : 'display';
						var renderInteractiveForms = params.renderInteractiveForms === true ? true : false;
						var canvasFactory = params.canvasFactory || new DOMCanvasFactory();
						if (!this.intentStates[renderingIntent]) {
							this.intentStates[renderingIntent] = Object.create(null);
						}
						var intentState = this.intentStates[renderingIntent];
						if (!intentState.displayReadyCapability) {
							intentState.receivingOperatorList = true;
							intentState.displayReadyCapability = createPromiseCapability();
							intentState.operatorList = {
								fnArray: [],
								argsArray: [],
								lastChunk: false
							};
							this.stats.time('Page Request');
							this.transport.messageHandler.send('RenderPageRequest', {
								pageIndex: this.pageNumber - 1,
								intent: renderingIntent,
								renderInteractiveForms: renderInteractiveForms
							});
						}
						var internalRenderTask = new InternalRenderTask(complete, params, this.objs, this.commonObjs, intentState.operatorList, this.pageNumber, canvasFactory);
						internalRenderTask.useRequestAnimationFrame = renderingIntent !== 'print';
						if (!intentState.renderTasks) {
							intentState.renderTasks = [];
						}
						intentState.renderTasks.push(internalRenderTask);
						var renderTask = internalRenderTask.task;
						if (params.continueCallback) {
							deprecated('render is used with continueCallback parameter');
							renderTask.onContinue = params.continueCallback;
						}
						var self = this;
						intentState.displayReadyCapability.promise.then(function pageDisplayReadyPromise(transparency) {
							if (self.pendingCleanup) {
								complete();
								return;
							}
							stats.time('Rendering');
							internalRenderTask.initializeGraphics(transparency);
							internalRenderTask.operatorListChanged();
						}, function pageDisplayReadPromiseError(reason) {
							complete(reason);
						});
						function complete(error) {
							var i = intentState.renderTasks.indexOf(internalRenderTask);
							if (i >= 0) {
								intentState.renderTasks.splice(i, 1);
							}
							if (self.cleanupAfterRender) {
								self.pendingCleanup = true;
							}
							self._tryCleanup();
							if (error) {
								internalRenderTask.capability.reject(error);
							} else {
								internalRenderTask.capability.resolve();
							}
							stats.timeEnd('Rendering');
							stats.timeEnd('Overall');
						}
						return renderTask;
					},
					getOperatorList: function PDFPageProxy_getOperatorList() {
						function operatorListChanged() {
							if (intentState.operatorList.lastChunk) {
								intentState.opListReadCapability.resolve(intentState.operatorList);
								var i = intentState.renderTasks.indexOf(opListTask);
								if (i >= 0) {
									intentState.renderTasks.splice(i, 1);
								}
							}
						}
						var renderingIntent = 'oplist';
						if (!this.intentStates[renderingIntent]) {
							this.intentStates[renderingIntent] = Object.create(null);
						}
						var intentState = this.intentStates[renderingIntent];
						var opListTask;
						if (!intentState.opListReadCapability) {
							opListTask = {};
							opListTask.operatorListChanged = operatorListChanged;
							intentState.receivingOperatorList = true;
							intentState.opListReadCapability = createPromiseCapability();
							intentState.renderTasks = [];
							intentState.renderTasks.push(opListTask);
							intentState.operatorList = {
								fnArray: [],
								argsArray: [],
								lastChunk: false
							};
							this.transport.messageHandler.send('RenderPageRequest', {
								pageIndex: this.pageIndex,
								intent: renderingIntent
							});
						}
						return intentState.opListReadCapability.promise;
					},
					getTextContent: function PDFPageProxy_getTextContent(params) {
						return this.transport.messageHandler.sendWithPromise('GetTextContent', {
							pageIndex: this.pageNumber - 1,
							normalizeWhitespace: params && params.normalizeWhitespace === true ? true : false,
							combineTextItems: params && params.disableCombineTextItems === true ? false : true
						});
					},
					_destroy: function PDFPageProxy_destroy() {
						this.destroyed = true;
						this.transport.pageCache[this.pageIndex] = null;
						var waitOn = [];
						Object.keys(this.intentStates).forEach(function (intent) {
							if (intent === 'oplist') {
								return;
							}
							var intentState = this.intentStates[intent];
							intentState.renderTasks.forEach(function (renderTask) {
								var renderCompleted = renderTask.capability.promise.catch(function () {
								});
								waitOn.push(renderCompleted);
								renderTask.cancel();
							});
						}, this);
						this.objs.clear();
						this.annotationsPromise = null;
						this.pendingCleanup = false;
						return Promise.all(waitOn);
					},
					destroy: function () {
						deprecated('page destroy method, use cleanup() instead');
						this.cleanup();
					},
					cleanup: function PDFPageProxy_cleanup() {
						this.pendingCleanup = true;
						this._tryCleanup();
					},
					_tryCleanup: function PDFPageProxy_tryCleanup() {
						if (!this.pendingCleanup || Object.keys(this.intentStates).some(function (intent) {
							var intentState = this.intentStates[intent];
							return intentState.renderTasks.length !== 0 || intentState.receivingOperatorList;
						}, this)) {
							return;
						}
						Object.keys(this.intentStates).forEach(function (intent) {
							delete this.intentStates[intent];
						}, this);
						this.objs.clear();
						this.annotationsPromise = null;
						this.pendingCleanup = false;
					},
					_startRenderPage: function PDFPageProxy_startRenderPage(transparency, intent) {
						var intentState = this.intentStates[intent];
						if (intentState.displayReadyCapability) {
							intentState.displayReadyCapability.resolve(transparency);
						}
					},
					_renderPageChunk: function PDFPageProxy_renderPageChunk(operatorListChunk, intent) {
						var intentState = this.intentStates[intent];
						var i, ii;
						for (i = 0, ii = operatorListChunk.length; i < ii; i++) {
							intentState.operatorList.fnArray.push(operatorListChunk.fnArray[i]);
							intentState.operatorList.argsArray.push(operatorListChunk.argsArray[i]);
						}
						intentState.operatorList.lastChunk = operatorListChunk.lastChunk;
						for (i = 0; i < intentState.renderTasks.length; i++) {
							intentState.renderTasks[i].operatorListChanged();
						}
						if (operatorListChunk.lastChunk) {
							intentState.receivingOperatorList = false;
							this._tryCleanup();
						}
					}
				};
				return PDFPageProxy;
			}();
			var PDFWorker = function PDFWorkerClosure() {
				var nextFakeWorkerId = 0;
				function getWorkerSrc() {
					if (typeof workerSrc !== 'undefined') {
						return workerSrc;
					}
					if (getDefaultSetting('workerSrc')) {
						return getDefaultSetting('workerSrc');
					}
					if (pdfjsFilePath) {
						return pdfjsFilePath.replace(/\.js$/i, '.worker.js');
					}
					error('No PDFJS.workerSrc specified');
				}
				var fakeWorkerFilesLoadedCapability;
				function setupFakeWorkerGlobal() {
					var WorkerMessageHandler;
					if (fakeWorkerFilesLoadedCapability) {
						return fakeWorkerFilesLoadedCapability.promise;
					}
					fakeWorkerFilesLoadedCapability = createPromiseCapability();
					var loader = fakeWorkerFilesLoader || function (callback) {
						Util.loadScript(getWorkerSrc(), function () {
							callback(window.pdfjsDistBuildPdfWorker.WorkerMessageHandler);
						});
					};
					loader(fakeWorkerFilesLoadedCapability.resolve);
					return fakeWorkerFilesLoadedCapability.promise;
				}
				function FakeWorkerPort(defer) {
					this._listeners = [];
					this._defer = defer;
					this._deferred = Promise.resolve(undefined);
				}
				FakeWorkerPort.prototype = {
					postMessage: function (obj, transfers) {
						function cloneValue(value) {
							if (typeof value !== 'object' || value === null) {
								return value;
							}
							if (cloned.has(value)) {
								return cloned.get(value);
							}
							var result;
							var buffer;
							if ((buffer = value.buffer) && isArrayBuffer(buffer)) {
								var transferable = transfers && transfers.indexOf(buffer) >= 0;
								if (value === buffer) {
									result = value;
								} else if (transferable) {
									result = new value.constructor(buffer, value.byteOffset, value.byteLength);
								} else {
									result = new value.constructor(value);
								}
								cloned.set(value, result);
								return result;
							}
							result = isArray(value) ? [] : {};
							cloned.set(value, result);
							for (var i in value) {
								var desc, p = value;
								while (!(desc = Object.getOwnPropertyDescriptor(p, i))) {
									p = Object.getPrototypeOf(p);
								}
								if (typeof desc.value === 'undefined' || typeof desc.value === 'function') {
									continue;
								}
								result[i] = cloneValue(desc.value);
							}
							return result;
						}
						if (!this._defer) {
							this._listeners.forEach(function (listener) {
								listener.call(this, { data: obj });
							}, this);
							return;
						}
						var cloned = new WeakMap();
						var e = { data: cloneValue(obj) };
						this._deferred.then(function () {
							this._listeners.forEach(function (listener) {
								listener.call(this, e);
							}, this);
						}.bind(this));
					},
					addEventListener: function (name, listener) {
						this._listeners.push(listener);
					},
					removeEventListener: function (name, listener) {
						var i = this._listeners.indexOf(listener);
						this._listeners.splice(i, 1);
					},
					terminate: function () {
						this._listeners = [];
					}
				};
				function createCDNWrapper(url) {
					var wrapper = 'importScripts(\'' + url + '\');';
					return URL.createObjectURL(new Blob([wrapper]));
				}
				function PDFWorker(name, port) {
					this.name = name;
					this.destroyed = false;
					this._readyCapability = createPromiseCapability();
					this._port = null;
					this._webWorker = null;
					this._messageHandler = null;
					if (port) {
						this._initializeFromPort(port);
						return;
					}
					this._initialize();
				}
				PDFWorker.prototype = {
					get promise() {
						return this._readyCapability.promise;
					},
					get port() {
						return this._port;
					},
					get messageHandler() {
						return this._messageHandler;
					},
					_initializeFromPort: function PDFWorker_initializeFromPort(port) {
						this._port = port;
						this._messageHandler = new MessageHandler('main', 'worker', port);
						this._messageHandler.on('ready', function () {
						});
						this._readyCapability.resolve();
					},
					_initialize: function PDFWorker_initialize() {
						if (!isWorkerDisabled && !getDefaultSetting('disableWorker') && typeof Worker !== 'undefined') {
							var workerSrc = getWorkerSrc();
							try {
								if (!isSameOrigin(window.location.href, workerSrc)) {
									workerSrc = createCDNWrapper(new URL(workerSrc, window.location).href);
								}
								var worker = new Worker(workerSrc);
								var messageHandler = new MessageHandler('main', 'worker', worker);
								var terminateEarly = function () {
									worker.removeEventListener('error', onWorkerError);
									messageHandler.destroy();
									worker.terminate();
									if (this.destroyed) {
										this._readyCapability.reject(new Error('Worker was destroyed'));
									} else {
										this._setupFakeWorker();
									}
								}.bind(this);
								var onWorkerError = function (event) {
									if (!this._webWorker) {
										terminateEarly();
									}
								}.bind(this);
								worker.addEventListener('error', onWorkerError);
								messageHandler.on('test', function PDFWorker_test(data) {
									worker.removeEventListener('error', onWorkerError);
									if (this.destroyed) {
										terminateEarly();
										return;
									}
									var supportTypedArray = data && data.supportTypedArray;
									if (supportTypedArray) {
										this._messageHandler = messageHandler;
										this._port = worker;
										this._webWorker = worker;
										if (!data.supportTransfers) {
											isPostMessageTransfersDisabled = true;
										}
										this._readyCapability.resolve();
										messageHandler.send('configure', { verbosity: getVerbosityLevel() });
									} else {
										this._setupFakeWorker();
										messageHandler.destroy();
										worker.terminate();
									}
								}.bind(this));
								messageHandler.on('console_log', function (data) {
									console.log.apply(console, data);
								});
								messageHandler.on('console_error', function (data) {
									console.error.apply(console, data);
								});
								messageHandler.on('ready', function (data) {
									worker.removeEventListener('error', onWorkerError);
									if (this.destroyed) {
										terminateEarly();
										return;
									}
									try {
										sendTest();
									} catch (e) {
										this._setupFakeWorker();
									}
								}.bind(this));
								var sendTest = function () {
									var postMessageTransfers = getDefaultSetting('postMessageTransfers') && !isPostMessageTransfersDisabled;
									var testObj = new Uint8Array([postMessageTransfers ? 255 : 0]);
									try {
										messageHandler.send('test', testObj, [testObj.buffer]);
									} catch (ex) {
										info('Cannot use postMessage transfers');
										testObj[0] = 0;
										messageHandler.send('test', testObj);
									}
								};
								sendTest();
								return;
							} catch (e) {
								info('The worker has been disabled.');
							}
						}
						this._setupFakeWorker();
					},
					_setupFakeWorker: function PDFWorker_setupFakeWorker() {
						if (!isWorkerDisabled && !getDefaultSetting('disableWorker')) {
							warn('Setting up fake worker.');
							isWorkerDisabled = true;
						}
						setupFakeWorkerGlobal().then(function (WorkerMessageHandler) {
							if (this.destroyed) {
								this._readyCapability.reject(new Error('Worker was destroyed'));
								return;
							}
							var isTypedArraysPresent = Uint8Array !== Float32Array;
							var port = new FakeWorkerPort(isTypedArraysPresent);
							this._port = port;
							var id = 'fake' + nextFakeWorkerId++;
							var workerHandler = new MessageHandler(id + '_worker', id, port);
							WorkerMessageHandler.setup(workerHandler, port);
							var messageHandler = new MessageHandler(id, id + '_worker', port);
							this._messageHandler = messageHandler;
							this._readyCapability.resolve();
						}.bind(this));
					},
					destroy: function PDFWorker_destroy() {
						this.destroyed = true;
						if (this._webWorker) {
							this._webWorker.terminate();
							this._webWorker = null;
						}
						this._port = null;
						if (this._messageHandler) {
							this._messageHandler.destroy();
							this._messageHandler = null;
						}
					}
				};
				return PDFWorker;
			}();
			var WorkerTransport = function WorkerTransportClosure() {
				function WorkerTransport(messageHandler, loadingTask, pdfDataRangeTransport, CMapReaderFactory) {
					this.messageHandler = messageHandler;
					this.loadingTask = loadingTask;
					this.pdfDataRangeTransport = pdfDataRangeTransport;
					this.commonObjs = new PDFObjects();
					this.fontLoader = new FontLoader(loadingTask.docId);
					this.CMapReaderFactory = new CMapReaderFactory({
						baseUrl: getDefaultSetting('cMapUrl'),
						isCompressed: getDefaultSetting('cMapPacked')
					});
					this.destroyed = false;
					this.destroyCapability = null;
					this._passwordCapability = null;
					this.pageCache = [];
					this.pagePromises = [];
					this.downloadInfoCapability = createPromiseCapability();
					this.setupMessageHandler();
				}
				WorkerTransport.prototype = {
					destroy: function WorkerTransport_destroy() {
						if (this.destroyCapability) {
							return this.destroyCapability.promise;
						}
						this.destroyed = true;
						this.destroyCapability = createPromiseCapability();
						if (this._passwordCapability) {
							this._passwordCapability.reject(new Error('Worker was destroyed during onPassword callback'));
						}
						var waitOn = [];
						this.pageCache.forEach(function (page) {
							if (page) {
								waitOn.push(page._destroy());
							}
						});
						this.pageCache = [];
						this.pagePromises = [];
						var self = this;
						var terminated = this.messageHandler.sendWithPromise('Terminate', null);
						waitOn.push(terminated);
						Promise.all(waitOn).then(function () {
							self.fontLoader.clear();
							if (self.pdfDataRangeTransport) {
								self.pdfDataRangeTransport.abort();
								self.pdfDataRangeTransport = null;
							}
							if (self.messageHandler) {
								self.messageHandler.destroy();
								self.messageHandler = null;
							}
							self.destroyCapability.resolve();
						}, this.destroyCapability.reject);
						return this.destroyCapability.promise;
					},
					setupMessageHandler: function WorkerTransport_setupMessageHandler() {
						var messageHandler = this.messageHandler;
						var loadingTask = this.loadingTask;
						var pdfDataRangeTransport = this.pdfDataRangeTransport;
						if (pdfDataRangeTransport) {
							pdfDataRangeTransport.addRangeListener(function (begin, chunk) {
								messageHandler.send('OnDataRange', {
									begin: begin,
									chunk: chunk
								});
							});
							pdfDataRangeTransport.addProgressListener(function (loaded) {
								messageHandler.send('OnDataProgress', { loaded: loaded });
							});
							pdfDataRangeTransport.addProgressiveReadListener(function (chunk) {
								messageHandler.send('OnDataRange', { chunk: chunk });
							});
							messageHandler.on('RequestDataRange', function transportDataRange(data) {
								pdfDataRangeTransport.requestDataRange(data.begin, data.end);
							}, this);
						}
						messageHandler.on('GetDoc', function transportDoc(data) {
							var pdfInfo = data.pdfInfo;
							this.numPages = data.pdfInfo.numPages;
							var loadingTask = this.loadingTask;
							var pdfDocument = new PDFDocumentProxy(pdfInfo, this, loadingTask);
							this.pdfDocument = pdfDocument;
							loadingTask._capability.resolve(pdfDocument);
						}, this);
						messageHandler.on('PasswordRequest', function transportPasswordRequest(exception) {
							this._passwordCapability = createPromiseCapability();
							if (loadingTask.onPassword) {
								var updatePassword = function (password) {
									this._passwordCapability.resolve({ password: password });
								}.bind(this);
								loadingTask.onPassword(updatePassword, exception.code);
							} else {
								this._passwordCapability.reject(new PasswordException(exception.message, exception.code));
							}
							return this._passwordCapability.promise;
						}, this);
						messageHandler.on('PasswordException', function transportPasswordException(exception) {
							loadingTask._capability.reject(new PasswordException(exception.message, exception.code));
						}, this);
						messageHandler.on('InvalidPDF', function transportInvalidPDF(exception) {
							this.loadingTask._capability.reject(new InvalidPDFException(exception.message));
						}, this);
						messageHandler.on('MissingPDF', function transportMissingPDF(exception) {
							this.loadingTask._capability.reject(new MissingPDFException(exception.message));
						}, this);
						messageHandler.on('UnexpectedResponse', function transportUnexpectedResponse(exception) {
							this.loadingTask._capability.reject(new UnexpectedResponseException(exception.message, exception.status));
						}, this);
						messageHandler.on('UnknownError', function transportUnknownError(exception) {
							this.loadingTask._capability.reject(new UnknownErrorException(exception.message, exception.details));
						}, this);
						messageHandler.on('DataLoaded', function transportPage(data) {
							this.downloadInfoCapability.resolve(data);
						}, this);
						messageHandler.on('PDFManagerReady', function transportPage(data) {
							if (this.pdfDataRangeTransport) {
								this.pdfDataRangeTransport.transportReady();
							}
						}, this);
						messageHandler.on('StartRenderPage', function transportRender(data) {
							if (this.destroyed) {
								return;
							}
							var page = this.pageCache[data.pageIndex];
							page.stats.timeEnd('Page Request');
							page._startRenderPage(data.transparency, data.intent);
						}, this);
						messageHandler.on('RenderPageChunk', function transportRender(data) {
							if (this.destroyed) {
								return;
							}
							var page = this.pageCache[data.pageIndex];
							page._renderPageChunk(data.operatorList, data.intent);
						}, this);
						messageHandler.on('commonobj', function transportObj(data) {
							if (this.destroyed) {
								return;
							}
							var id = data[0];
							var type = data[1];
							if (this.commonObjs.hasData(id)) {
								return;
							}
							switch (type) {
								case 'Font':
									var exportedData = data[2];
									if ('error' in exportedData) {
										var exportedError = exportedData.error;
										warn('Error during font loading: ' + exportedError);
										this.commonObjs.resolve(id, exportedError);
										break;
									}
									var fontRegistry = null;
									if (getDefaultSetting('pdfBug') && globalScope.FontInspector && globalScope['FontInspector'].enabled) {
										fontRegistry = {
											registerFont: function (font, url) {
												globalScope['FontInspector'].fontAdded(font, url);
											}
										};
									}
									var font = new FontFaceObject(exportedData, {
										isEvalSuported: getDefaultSetting('isEvalSupported'),
										disableFontFace: getDefaultSetting('disableFontFace'),
										fontRegistry: fontRegistry
									});
									this.fontLoader.bind([font], function fontReady(fontObjs) {
										this.commonObjs.resolve(id, font);
									}.bind(this));
									break;
								case 'FontPath':
									this.commonObjs.resolve(id, data[2]);
									break;
								default:
									error('Got unknown common object type ' + type);
							}
						}, this);
						messageHandler.on('obj', function transportObj(data) {
							if (this.destroyed) {
								return;
							}
							var id = data[0];
							var pageIndex = data[1];
							var type = data[2];
							var pageProxy = this.pageCache[pageIndex];
							var imageData;
							if (pageProxy.objs.hasData(id)) {
								return;
							}
							switch (type) {
								case 'JpegStream':
									imageData = data[3];
									loadJpegStream(id, imageData, pageProxy.objs);
									break;
								case 'Image':
									imageData = data[3];
									pageProxy.objs.resolve(id, imageData);
									var MAX_IMAGE_SIZE_TO_STORE = 8000000;
									if (imageData && 'data' in imageData && imageData.data.length > MAX_IMAGE_SIZE_TO_STORE) {
										pageProxy.cleanupAfterRender = true;
									}
									break;
								default:
									error('Got unknown object type ' + type);
							}
						}, this);
						messageHandler.on('DocProgress', function transportDocProgress(data) {
							if (this.destroyed) {
								return;
							}
							var loadingTask = this.loadingTask;
							if (loadingTask.onProgress) {
								loadingTask.onProgress({
									loaded: data.loaded,
									total: data.total
								});
							}
						}, this);
						messageHandler.on('PageError', function transportError(data) {
							if (this.destroyed) {
								return;
							}
							var page = this.pageCache[data.pageNum - 1];
							var intentState = page.intentStates[data.intent];
							if (intentState.displayReadyCapability) {
								intentState.displayReadyCapability.reject(data.error);
							} else {
								error(data.error);
							}
							if (intentState.operatorList) {
								intentState.operatorList.lastChunk = true;
								for (var i = 0; i < intentState.renderTasks.length; i++) {
									intentState.renderTasks[i].operatorListChanged();
								}
							}
						}, this);
						messageHandler.on('UnsupportedFeature', function transportUnsupportedFeature(data) {
							if (this.destroyed) {
								return;
							}
							var featureId = data.featureId;
							var loadingTask = this.loadingTask;
							if (loadingTask.onUnsupportedFeature) {
								loadingTask.onUnsupportedFeature(featureId);
							}
							_UnsupportedManager.notify(featureId);
						}, this);
						messageHandler.on('JpegDecode', function (data) {
							if (this.destroyed) {
								return Promise.reject(new Error('Worker was destroyed'));
							}
							if (typeof document === 'undefined') {
								return Promise.reject(new Error('"document" is not defined.'));
							}
							var imageUrl = data[0];
							var components = data[1];
							if (components !== 3 && components !== 1) {
								return Promise.reject(new Error('Only 3 components or 1 component can be returned'));
							}
							return new Promise(function (resolve, reject) {
								var img = new Image();
								img.onload = function () {
									var width = img.width;
									var height = img.height;
									var size = width * height;
									var rgbaLength = size * 4;
									var buf = new Uint8Array(size * components);
									var tmpCanvas = document.createElement('canvas');
									tmpCanvas.width = width;
									tmpCanvas.height = height;
									var tmpCtx = tmpCanvas.getContext('2d');
									tmpCtx.drawImage(img, 0, 0);
									var data = tmpCtx.getImageData(0, 0, width, height).data;
									var i, j;
									if (components === 3) {
										for (i = 0, j = 0; i < rgbaLength; i += 4, j += 3) {
											buf[j] = data[i];
											buf[j + 1] = data[i + 1];
											buf[j + 2] = data[i + 2];
										}
									} else if (components === 1) {
										for (i = 0, j = 0; i < rgbaLength; i += 4, j++) {
											buf[j] = data[i];
										}
									}
									resolve({
										data: buf,
										width: width,
										height: height
									});
								};
								img.onerror = function () {
									reject(new Error('JpegDecode failed to load image'));
								};
								img.src = imageUrl;
							});
						}, this);
						messageHandler.on('FetchBuiltInCMap', function (data) {
							if (this.destroyed) {
								return Promise.reject(new Error('Worker was destroyed'));
							}
							return this.CMapReaderFactory.fetch({ name: data.name });
						}, this);
					},
					getData: function WorkerTransport_getData() {
						return this.messageHandler.sendWithPromise('GetData', null);
					},
					getPage: function WorkerTransport_getPage(pageNumber, capability) {
						if (!isInt(pageNumber) || pageNumber <= 0 || pageNumber > this.numPages) {
							return Promise.reject(new Error('Invalid page request'));
						}
						var pageIndex = pageNumber - 1;
						if (pageIndex in this.pagePromises) {
							return this.pagePromises[pageIndex];
						}
						var promise = this.messageHandler.sendWithPromise('GetPage', { pageIndex: pageIndex }).then(function (pageInfo) {
							if (this.destroyed) {
								throw new Error('Transport destroyed');
							}
							var page = new PDFPageProxy(pageIndex, pageInfo, this);
							this.pageCache[pageIndex] = page;
							return page;
						}.bind(this));
						this.pagePromises[pageIndex] = promise;
						return promise;
					},
					getPageIndex: function WorkerTransport_getPageIndexByRef(ref) {
						return this.messageHandler.sendWithPromise('GetPageIndex', { ref: ref }).catch(function (reason) {
							return Promise.reject(new Error(reason));
						});
					},
					getAnnotations: function WorkerTransport_getAnnotations(pageIndex, intent) {
						return this.messageHandler.sendWithPromise('GetAnnotations', {
							pageIndex: pageIndex,
							intent: intent
						});
					},
					getDestinations: function WorkerTransport_getDestinations() {
						return this.messageHandler.sendWithPromise('GetDestinations', null);
					},
					getDestination: function WorkerTransport_getDestination(id) {
						return this.messageHandler.sendWithPromise('GetDestination', { id: id });
					},
					getPageLabels: function WorkerTransport_getPageLabels() {
						return this.messageHandler.sendWithPromise('GetPageLabels', null);
					},
					getAttachments: function WorkerTransport_getAttachments() {
						return this.messageHandler.sendWithPromise('GetAttachments', null);
					},
					getJavaScript: function WorkerTransport_getJavaScript() {
						return this.messageHandler.sendWithPromise('GetJavaScript', null);
					},
					getOutline: function WorkerTransport_getOutline() {
						return this.messageHandler.sendWithPromise('GetOutline', null);
					},
					getMetadata: function WorkerTransport_getMetadata() {
						return this.messageHandler.sendWithPromise('GetMetadata', null).then(function transportMetadata(results) {
							return {
								info: results[0],
								metadata: results[1] ? new Metadata(results[1]) : null
							};
						});
					},
					getStats: function WorkerTransport_getStats() {
						return this.messageHandler.sendWithPromise('GetStats', null);
					},
					startCleanup: function WorkerTransport_startCleanup() {
						this.messageHandler.sendWithPromise('Cleanup', null).then(function endCleanup() {
							for (var i = 0, ii = this.pageCache.length; i < ii; i++) {
								var page = this.pageCache[i];
								if (page) {
									page.cleanup();
								}
							}
							this.commonObjs.clear();
							this.fontLoader.clear();
						}.bind(this));
					}
				};
				return WorkerTransport;
			}();
			var PDFObjects = function PDFObjectsClosure() {
				function PDFObjects() {
					this.objs = Object.create(null);
				}
				PDFObjects.prototype = {
					ensureObj: function PDFObjects_ensureObj(objId) {
						if (this.objs[objId]) {
							return this.objs[objId];
						}
						var obj = {
							capability: createPromiseCapability(),
							data: null,
							resolved: false
						};
						this.objs[objId] = obj;
						return obj;
					},
					get: function PDFObjects_get(objId, callback) {
						if (callback) {
							this.ensureObj(objId).capability.promise.then(callback);
							return null;
						}
						var obj = this.objs[objId];
						if (!obj || !obj.resolved) {
							error('Requesting object that isn\'t resolved yet ' + objId);
						}
						return obj.data;
					},
					resolve: function PDFObjects_resolve(objId, data) {
						var obj = this.ensureObj(objId);
						obj.resolved = true;
						obj.data = data;
						obj.capability.resolve(data);
					},
					isResolved: function PDFObjects_isResolved(objId) {
						var objs = this.objs;
						if (!objs[objId]) {
							return false;
						}
						return objs[objId].resolved;
					},
					hasData: function PDFObjects_hasData(objId) {
						return this.isResolved(objId);
					},
					getData: function PDFObjects_getData(objId) {
						var objs = this.objs;
						if (!objs[objId] || !objs[objId].resolved) {
							return null;
						}
						return objs[objId].data;
					},
					clear: function PDFObjects_clear() {
						this.objs = Object.create(null);
					}
				};
				return PDFObjects;
			}();
			var RenderTask = function RenderTaskClosure() {
				function RenderTask(internalRenderTask) {
					this._internalRenderTask = internalRenderTask;
					this.onContinue = null;
				}
				RenderTask.prototype = {
					get promise() {
						return this._internalRenderTask.capability.promise;
					},
					cancel: function RenderTask_cancel() {
						this._internalRenderTask.cancel();
					},
					then: function RenderTask_then(onFulfilled, onRejected) {
						return this.promise.then.apply(this.promise, arguments);
					}
				};
				return RenderTask;
			}();
			var InternalRenderTask = function InternalRenderTaskClosure() {
				function InternalRenderTask(callback, params, objs, commonObjs, operatorList, pageNumber, canvasFactory) {
					this.callback = callback;
					this.params = params;
					this.objs = objs;
					this.commonObjs = commonObjs;
					this.operatorListIdx = null;
					this.operatorList = operatorList;
					this.pageNumber = pageNumber;
					this.canvasFactory = canvasFactory;
					this.running = false;
					this.graphicsReadyCallback = null;
					this.graphicsReady = false;
					this.useRequestAnimationFrame = false;
					this.cancelled = false;
					this.capability = createPromiseCapability();
					this.task = new RenderTask(this);
					this._continueBound = this._continue.bind(this);
					this._scheduleNextBound = this._scheduleNext.bind(this);
					this._nextBound = this._next.bind(this);
				}
				InternalRenderTask.prototype = {
					initializeGraphics: function InternalRenderTask_initializeGraphics(transparency) {
						if (this.cancelled) {
							return;
						}
						if (getDefaultSetting('pdfBug') && globalScope.StepperManager && globalScope.StepperManager.enabled) {
							this.stepper = globalScope.StepperManager.create(this.pageNumber - 1);
							this.stepper.init(this.operatorList);
							this.stepper.nextBreakPoint = this.stepper.getNextBreakPoint();
						}
						var params = this.params;
						this.gfx = new CanvasGraphics(params.canvasContext, this.commonObjs, this.objs, this.canvasFactory, params.imageLayer);
						this.gfx.beginDrawing(params.transform, params.viewport, transparency);
						this.operatorListIdx = 0;
						this.graphicsReady = true;
						if (this.graphicsReadyCallback) {
							this.graphicsReadyCallback();
						}
					},
					cancel: function InternalRenderTask_cancel() {
						this.running = false;
						this.cancelled = true;
						this.callback('cancelled');
					},
					operatorListChanged: function InternalRenderTask_operatorListChanged() {
						if (!this.graphicsReady) {
							if (!this.graphicsReadyCallback) {
								this.graphicsReadyCallback = this._continueBound;
							}
							return;
						}
						if (this.stepper) {
							this.stepper.updateOperatorList(this.operatorList);
						}
						if (this.running) {
							return;
						}
						this._continue();
					},
					_continue: function InternalRenderTask__continue() {
						this.running = true;
						if (this.cancelled) {
							return;
						}
						if (this.task.onContinue) {
							this.task.onContinue(this._scheduleNextBound);
						} else {
							this._scheduleNext();
						}
					},
					_scheduleNext: function InternalRenderTask__scheduleNext() {
						if (this.useRequestAnimationFrame && typeof window !== 'undefined') {
							window.requestAnimationFrame(this._nextBound);
						} else {
							Promise.resolve(undefined).then(this._nextBound);
						}
					},
					_next: function InternalRenderTask__next() {
						if (this.cancelled) {
							return;
						}
						this.operatorListIdx = this.gfx.executeOperatorList(this.operatorList, this.operatorListIdx, this._continueBound, this.stepper);
						if (this.operatorListIdx === this.operatorList.argsArray.length) {
							this.running = false;
							if (this.operatorList.lastChunk) {
								this.gfx.endDrawing();
								this.callback();
							}
						}
					}
				};
				return InternalRenderTask;
			}();
			var _UnsupportedManager = function UnsupportedManagerClosure() {
				var listeners = [];
				return {
					listen: function (cb) {
						deprecated('Global UnsupportedManager.listen is used: ' + ' use PDFDocumentLoadingTask.onUnsupportedFeature instead');
						listeners.push(cb);
					},
					notify: function (featureId) {
						for (var i = 0, ii = listeners.length; i < ii; i++) {
							listeners[i](featureId);
						}
					}
				};
			}();
			exports.version = '1.7.354';
			exports.build = '0f7548ba';
			exports.getDocument = getDocument;
			exports.PDFDataRangeTransport = PDFDataRangeTransport;
			exports.PDFWorker = PDFWorker;
			exports.PDFDocumentProxy = PDFDocumentProxy;
			exports.PDFPageProxy = PDFPageProxy;
			exports._UnsupportedManager = _UnsupportedManager;

			/***/
		}),
/* 4 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var FONT_IDENTITY_MATRIX = sharedUtil.FONT_IDENTITY_MATRIX;
			var IDENTITY_MATRIX = sharedUtil.IDENTITY_MATRIX;
			var ImageKind = sharedUtil.ImageKind;
			var OPS = sharedUtil.OPS;
			var Util = sharedUtil.Util;
			var isNum = sharedUtil.isNum;
			var isArray = sharedUtil.isArray;
			var warn = sharedUtil.warn;
			var createObjectURL = sharedUtil.createObjectURL;
			var SVG_DEFAULTS = {
				fontStyle: 'normal',
				fontWeight: 'normal',
				fillColor: '#000000'
			};
			var convertImgDataToPng = function convertImgDataToPngClosure() {
				var PNG_HEADER = new Uint8Array([
					0x89,
					0x50,
					0x4e,
					0x47,
					0x0d,
					0x0a,
					0x1a,
					0x0a
				]);
				var CHUNK_WRAPPER_SIZE = 12;
				var crcTable = new Int32Array(256);
				for (var i = 0; i < 256; i++) {
					var c = i;
					for (var h = 0; h < 8; h++) {
						if (c & 1) {
							c = 0xedB88320 ^ c >> 1 & 0x7fffffff;
						} else {
							c = c >> 1 & 0x7fffffff;
						}
					}
					crcTable[i] = c;
				}
				function crc32(data, start, end) {
					var crc = -1;
					for (var i = start; i < end; i++) {
						var a = (crc ^ data[i]) & 0xff;
						var b = crcTable[a];
						crc = crc >>> 8 ^ b;
					}
					return crc ^ -1;
				}
				function writePngChunk(type, body, data, offset) {
					var p = offset;
					var len = body.length;
					data[p] = len >> 24 & 0xff;
					data[p + 1] = len >> 16 & 0xff;
					data[p + 2] = len >> 8 & 0xff;
					data[p + 3] = len & 0xff;
					p += 4;
					data[p] = type.charCodeAt(0) & 0xff;
					data[p + 1] = type.charCodeAt(1) & 0xff;
					data[p + 2] = type.charCodeAt(2) & 0xff;
					data[p + 3] = type.charCodeAt(3) & 0xff;
					p += 4;
					data.set(body, p);
					p += body.length;
					var crc = crc32(data, offset + 4, p);
					data[p] = crc >> 24 & 0xff;
					data[p + 1] = crc >> 16 & 0xff;
					data[p + 2] = crc >> 8 & 0xff;
					data[p + 3] = crc & 0xff;
				}
				function adler32(data, start, end) {
					var a = 1;
					var b = 0;
					for (var i = start; i < end; ++i) {
						a = (a + (data[i] & 0xff)) % 65521;
						b = (b + a) % 65521;
					}
					return b << 16 | a;
				}
				function encode(imgData, kind, forceDataSchema) {
					var width = imgData.width;
					var height = imgData.height;
					var bitDepth, colorType, lineSize;
					var bytes = imgData.data;
					switch (kind) {
						case ImageKind.GRAYSCALE_1BPP:
							colorType = 0;
							bitDepth = 1;
							lineSize = width + 7 >> 3;
							break;
						case ImageKind.RGB_24BPP:
							colorType = 2;
							bitDepth = 8;
							lineSize = width * 3;
							break;
						case ImageKind.RGBA_32BPP:
							colorType = 6;
							bitDepth = 8;
							lineSize = width * 4;
							break;
						default:
							throw new Error('invalid format');
					}
					var literals = new Uint8Array((1 + lineSize) * height);
					var offsetLiterals = 0, offsetBytes = 0;
					var y, i;
					for (y = 0; y < height; ++y) {
						literals[offsetLiterals++] = 0;
						literals.set(bytes.subarray(offsetBytes, offsetBytes + lineSize), offsetLiterals);
						offsetBytes += lineSize;
						offsetLiterals += lineSize;
					}
					if (kind === ImageKind.GRAYSCALE_1BPP) {
						offsetLiterals = 0;
						for (y = 0; y < height; y++) {
							offsetLiterals++;
							for (i = 0; i < lineSize; i++) {
								literals[offsetLiterals++] ^= 0xFF;
							}
						}
					}
					var ihdr = new Uint8Array([
						width >> 24 & 0xff,
						width >> 16 & 0xff,
						width >> 8 & 0xff,
						width & 0xff,
						height >> 24 & 0xff,
						height >> 16 & 0xff,
						height >> 8 & 0xff,
						height & 0xff,
						bitDepth,
						colorType,
						0x00,
						0x00,
						0x00
					]);
					var len = literals.length;
					var maxBlockLength = 0xFFFF;
					var deflateBlocks = Math.ceil(len / maxBlockLength);
					var idat = new Uint8Array(2 + len + deflateBlocks * 5 + 4);
					var pi = 0;
					idat[pi++] = 0x78;
					idat[pi++] = 0x9c;
					var pos = 0;
					while (len > maxBlockLength) {
						idat[pi++] = 0x00;
						idat[pi++] = 0xff;
						idat[pi++] = 0xff;
						idat[pi++] = 0x00;
						idat[pi++] = 0x00;
						idat.set(literals.subarray(pos, pos + maxBlockLength), pi);
						pi += maxBlockLength;
						pos += maxBlockLength;
						len -= maxBlockLength;
					}
					idat[pi++] = 0x01;
					idat[pi++] = len & 0xff;
					idat[pi++] = len >> 8 & 0xff;
					idat[pi++] = ~len & 0xffff & 0xff;
					idat[pi++] = (~len & 0xffff) >> 8 & 0xff;
					idat.set(literals.subarray(pos), pi);
					pi += literals.length - pos;
					var adler = adler32(literals, 0, literals.length);
					idat[pi++] = adler >> 24 & 0xff;
					idat[pi++] = adler >> 16 & 0xff;
					idat[pi++] = adler >> 8 & 0xff;
					idat[pi++] = adler & 0xff;
					var pngLength = PNG_HEADER.length + CHUNK_WRAPPER_SIZE * 3 + ihdr.length + idat.length;
					var data = new Uint8Array(pngLength);
					var offset = 0;
					data.set(PNG_HEADER, offset);
					offset += PNG_HEADER.length;
					writePngChunk('IHDR', ihdr, data, offset);
					offset += CHUNK_WRAPPER_SIZE + ihdr.length;
					writePngChunk('IDATA', idat, data, offset);
					offset += CHUNK_WRAPPER_SIZE + idat.length;
					writePngChunk('IEND', new Uint8Array(0), data, offset);
					return createObjectURL(data, 'image/png', forceDataSchema);
				}
				return function convertImgDataToPng(imgData, forceDataSchema) {
					var kind = imgData.kind === undefined ? ImageKind.GRAYSCALE_1BPP : imgData.kind;
					return encode(imgData, kind, forceDataSchema);
				};
			}();
			var SVGExtraState = function SVGExtraStateClosure() {
				function SVGExtraState() {
					this.fontSizeScale = 1;
					this.fontWeight = SVG_DEFAULTS.fontWeight;
					this.fontSize = 0;
					this.textMatrix = IDENTITY_MATRIX;
					this.fontMatrix = FONT_IDENTITY_MATRIX;
					this.leading = 0;
					this.x = 0;
					this.y = 0;
					this.lineX = 0;
					this.lineY = 0;
					this.charSpacing = 0;
					this.wordSpacing = 0;
					this.textHScale = 1;
					this.textRise = 0;
					this.fillColor = SVG_DEFAULTS.fillColor;
					this.strokeColor = '#000000';
					this.fillAlpha = 1;
					this.strokeAlpha = 1;
					this.lineWidth = 1;
					this.lineJoin = '';
					this.lineCap = '';
					this.miterLimit = 0;
					this.dashArray = [];
					this.dashPhase = 0;
					this.dependencies = [];
					this.activeClipUrl = null;
					this.clipGroup = null;
					this.maskId = '';
				}
				SVGExtraState.prototype = {
					clone: function SVGExtraState_clone() {
						return Object.create(this);
					},
					setCurrentPoint: function SVGExtraState_setCurrentPoint(x, y) {
						this.x = x;
						this.y = y;
					}
				};
				return SVGExtraState;
			}();
			var SVGGraphics = function SVGGraphicsClosure() {
				function opListToTree(opList) {
					var opTree = [];
					var tmp = [];
					var opListLen = opList.length;
					for (var x = 0; x < opListLen; x++) {
						if (opList[x].fn === 'save') {
							opTree.push({
								'fnId': 92,
								'fn': 'group',
								'items': []
							});
							tmp.push(opTree);
							opTree = opTree[opTree.length - 1].items;
							continue;
						}
						if (opList[x].fn === 'restore') {
							opTree = tmp.pop();
						} else {
							opTree.push(opList[x]);
						}
					}
					return opTree;
				}
				function pf(value) {
					if (value === (value | 0)) {
						return value.toString();
					}
					var s = value.toFixed(10);
					var i = s.length - 1;
					if (s[i] !== '0') {
						return s;
					}
					do {
						i--;
					} while (s[i] === '0');
					return s.substr(0, s[i] === '.' ? i : i + 1);
				}
				function pm(m) {
					if (m[4] === 0 && m[5] === 0) {
						if (m[1] === 0 && m[2] === 0) {
							if (m[0] === 1 && m[3] === 1) {
								return '';
							}
							return 'scale(' + pf(m[0]) + ' ' + pf(m[3]) + ')';
						}
						if (m[0] === m[3] && m[1] === -m[2]) {
							var a = Math.acos(m[0]) * 180 / Math.PI;
							return 'rotate(' + pf(a) + ')';
						}
					} else {
						if (m[0] === 1 && m[1] === 0 && m[2] === 0 && m[3] === 1) {
							return 'translate(' + pf(m[4]) + ' ' + pf(m[5]) + ')';
						}
					}
					return 'matrix(' + pf(m[0]) + ' ' + pf(m[1]) + ' ' + pf(m[2]) + ' ' + pf(m[3]) + ' ' + pf(m[4]) + ' ' + pf(m[5]) + ')';
				}
				function SVGGraphics(commonObjs, objs, forceDataSchema) {
					this.current = new SVGExtraState();
					this.transformMatrix = IDENTITY_MATRIX;
					this.transformStack = [];
					this.extraStack = [];
					this.commonObjs = commonObjs;
					this.objs = objs;
					this.pendingEOFill = false;
					this.embedFonts = false;
					this.embeddedFonts = Object.create(null);
					this.cssStyle = null;
					this.forceDataSchema = !!forceDataSchema;
				}
				var NS = 'http://www.w3.org/2000/svg';
				var XML_NS = 'http://www.w3.org/XML/1998/namespace';
				var XLINK_NS = 'http://www.w3.org/1999/xlink';
				var LINE_CAP_STYLES = [
					'butt',
					'round',
					'square'
				];
				var LINE_JOIN_STYLES = [
					'miter',
					'round',
					'bevel'
				];
				var clipCount = 0;
				var maskCount = 0;
				SVGGraphics.prototype = {
					save: function SVGGraphics_save() {
						this.transformStack.push(this.transformMatrix);
						var old = this.current;
						this.extraStack.push(old);
						this.current = old.clone();
					},
					restore: function SVGGraphics_restore() {
						this.transformMatrix = this.transformStack.pop();
						this.current = this.extraStack.pop();
						this.tgrp = null;
					},
					group: function SVGGraphics_group(items) {
						this.save();
						this.executeOpTree(items);
						this.restore();
					},
					loadDependencies: function SVGGraphics_loadDependencies(operatorList) {
						var fnArray = operatorList.fnArray;
						var fnArrayLen = fnArray.length;
						var argsArray = operatorList.argsArray;
						var self = this;
						for (var i = 0; i < fnArrayLen; i++) {
							if (OPS.dependency === fnArray[i]) {
								var deps = argsArray[i];
								for (var n = 0, nn = deps.length; n < nn; n++) {
									var obj = deps[n];
									var common = obj.substring(0, 2) === 'g_';
									var promise;
									if (common) {
										promise = new Promise(function (resolve) {
											self.commonObjs.get(obj, resolve);
										});
									} else {
										promise = new Promise(function (resolve) {
											self.objs.get(obj, resolve);
										});
									}
									this.current.dependencies.push(promise);
								}
							}
						}
						return Promise.all(this.current.dependencies);
					},
					transform: function SVGGraphics_transform(a, b, c, d, e, f) {
						var transformMatrix = [
							a,
							b,
							c,
							d,
							e,
							f
						];
						this.transformMatrix = Util.transform(this.transformMatrix, transformMatrix);
						this.tgrp = null;
					},
					getSVG: function SVGGraphics_getSVG(operatorList, viewport) {
						this.viewport = viewport;
						var svgElement = this._initialize(viewport);
						return this.loadDependencies(operatorList).then(function () {
							this.transformMatrix = IDENTITY_MATRIX;
							var opTree = this.convertOpList(operatorList);
							this.executeOpTree(opTree);
							return svgElement;
						}.bind(this));
					},
					convertOpList: function SVGGraphics_convertOpList(operatorList) {
						var argsArray = operatorList.argsArray;
						var fnArray = operatorList.fnArray;
						var fnArrayLen = fnArray.length;
						var REVOPS = [];
						var opList = [];
						for (var op in OPS) {
							REVOPS[OPS[op]] = op;
						}
						for (var x = 0; x < fnArrayLen; x++) {
							var fnId = fnArray[x];
							opList.push({
								'fnId': fnId,
								'fn': REVOPS[fnId],
								'args': argsArray[x]
							});
						}
						return opListToTree(opList);
					},
					executeOpTree: function SVGGraphics_executeOpTree(opTree) {
						var opTreeLen = opTree.length;
						for (var x = 0; x < opTreeLen; x++) {
							var fn = opTree[x].fn;
							var fnId = opTree[x].fnId;
							var args = opTree[x].args;
							switch (fnId | 0) {
								case OPS.beginText:
									this.beginText();
									break;
								case OPS.setLeading:
									this.setLeading(args);
									break;
								case OPS.setLeadingMoveText:
									this.setLeadingMoveText(args[0], args[1]);
									break;
								case OPS.setFont:
									this.setFont(args);
									break;
								case OPS.showText:
									this.showText(args[0]);
									break;
								case OPS.showSpacedText:
									this.showText(args[0]);
									break;
								case OPS.endText:
									this.endText();
									break;
								case OPS.moveText:
									this.moveText(args[0], args[1]);
									break;
								case OPS.setCharSpacing:
									this.setCharSpacing(args[0]);
									break;
								case OPS.setWordSpacing:
									this.setWordSpacing(args[0]);
									break;
								case OPS.setHScale:
									this.setHScale(args[0]);
									break;
								case OPS.setTextMatrix:
									this.setTextMatrix(args[0], args[1], args[2], args[3], args[4], args[5]);
									break;
								case OPS.setLineWidth:
									this.setLineWidth(args[0]);
									break;
								case OPS.setLineJoin:
									this.setLineJoin(args[0]);
									break;
								case OPS.setLineCap:
									this.setLineCap(args[0]);
									break;
								case OPS.setMiterLimit:
									this.setMiterLimit(args[0]);
									break;
								case OPS.setFillRGBColor:
									this.setFillRGBColor(args[0], args[1], args[2]);
									break;
								case OPS.setStrokeRGBColor:
									this.setStrokeRGBColor(args[0], args[1], args[2]);
									break;
								case OPS.setDash:
									this.setDash(args[0], args[1]);
									break;
								case OPS.setGState:
									this.setGState(args[0]);
									break;
								case OPS.fill:
									this.fill();
									break;
								case OPS.eoFill:
									this.eoFill();
									break;
								case OPS.stroke:
									this.stroke();
									break;
								case OPS.fillStroke:
									this.fillStroke();
									break;
								case OPS.eoFillStroke:
									this.eoFillStroke();
									break;
								case OPS.clip:
									this.clip('nonzero');
									break;
								case OPS.eoClip:
									this.clip('evenodd');
									break;
								case OPS.paintSolidColorImageMask:
									this.paintSolidColorImageMask();
									break;
								case OPS.paintJpegXObject:
									this.paintJpegXObject(args[0], args[1], args[2]);
									break;
								case OPS.paintImageXObject:
									this.paintImageXObject(args[0]);
									break;
								case OPS.paintInlineImageXObject:
									this.paintInlineImageXObject(args[0]);
									break;
								case OPS.paintImageMaskXObject:
									this.paintImageMaskXObject(args[0]);
									break;
								case OPS.paintFormXObjectBegin:
									this.paintFormXObjectBegin(args[0], args[1]);
									break;
								case OPS.paintFormXObjectEnd:
									this.paintFormXObjectEnd();
									break;
								case OPS.closePath:
									this.closePath();
									break;
								case OPS.closeStroke:
									this.closeStroke();
									break;
								case OPS.closeFillStroke:
									this.closeFillStroke();
									break;
								case OPS.nextLine:
									this.nextLine();
									break;
								case OPS.transform:
									this.transform(args[0], args[1], args[2], args[3], args[4], args[5]);
									break;
								case OPS.constructPath:
									this.constructPath(args[0], args[1]);
									break;
								case OPS.endPath:
									this.endPath();
									break;
								case 92:
									this.group(opTree[x].items);
									break;
								default:
									warn('Unimplemented operator ' + fn);
									break;
							}
						}
					},
					setWordSpacing: function SVGGraphics_setWordSpacing(wordSpacing) {
						this.current.wordSpacing = wordSpacing;
					},
					setCharSpacing: function SVGGraphics_setCharSpacing(charSpacing) {
						this.current.charSpacing = charSpacing;
					},
					nextLine: function SVGGraphics_nextLine() {
						this.moveText(0, this.current.leading);
					},
					setTextMatrix: function SVGGraphics_setTextMatrix(a, b, c, d, e, f) {
						var current = this.current;
						this.current.textMatrix = this.current.lineMatrix = [
							a,
							b,
							c,
							d,
							e,
							f
						];
						this.current.x = this.current.lineX = 0;
						this.current.y = this.current.lineY = 0;
						current.xcoords = [];
						current.tspan = document.createElementNS(NS, 'svg:tspan');
						current.tspan.setAttributeNS(null, 'font-family', current.fontFamily);
						current.tspan.setAttributeNS(null, 'font-size', pf(current.fontSize) + 'px');
						current.tspan.setAttributeNS(null, 'y', pf(-current.y));
						current.txtElement = document.createElementNS(NS, 'svg:text');
						current.txtElement.appendChild(current.tspan);
					},
					beginText: function SVGGraphics_beginText() {
						this.current.x = this.current.lineX = 0;
						this.current.y = this.current.lineY = 0;
						this.current.textMatrix = IDENTITY_MATRIX;
						this.current.lineMatrix = IDENTITY_MATRIX;
						this.current.tspan = document.createElementNS(NS, 'svg:tspan');
						this.current.txtElement = document.createElementNS(NS, 'svg:text');
						this.current.txtgrp = document.createElementNS(NS, 'svg:g');
						this.current.xcoords = [];
					},
					moveText: function SVGGraphics_moveText(x, y) {
						var current = this.current;
						this.current.x = this.current.lineX += x;
						this.current.y = this.current.lineY += y;
						current.xcoords = [];
						current.tspan = document.createElementNS(NS, 'svg:tspan');
						current.tspan.setAttributeNS(null, 'font-family', current.fontFamily);
						current.tspan.setAttributeNS(null, 'font-size', pf(current.fontSize) + 'px');
						current.tspan.setAttributeNS(null, 'y', pf(-current.y));
					},
					showText: function SVGGraphics_showText(glyphs) {
						var current = this.current;
						var font = current.font;
						var fontSize = current.fontSize;
						if (fontSize === 0) {
							return;
						}
						var charSpacing = current.charSpacing;
						var wordSpacing = current.wordSpacing;
						var fontDirection = current.fontDirection;
						var textHScale = current.textHScale * fontDirection;
						var glyphsLength = glyphs.length;
						var vertical = font.vertical;
						var widthAdvanceScale = fontSize * current.fontMatrix[0];
						var x = 0, i;
						for (i = 0; i < glyphsLength; ++i) {
							var glyph = glyphs[i];
							if (glyph === null) {
								x += fontDirection * wordSpacing;
								continue;
							} else if (isNum(glyph)) {
								x += -glyph * fontSize * 0.001;
								continue;
							}
							current.xcoords.push(current.x + x * textHScale);
							var width = glyph.width;
							var character = glyph.fontChar;
							var charWidth = width * widthAdvanceScale + charSpacing * fontDirection;
							x += charWidth;
							current.tspan.textContent += character;
						}
						if (vertical) {
							current.y -= x * textHScale;
						} else {
							current.x += x * textHScale;
						}
						current.tspan.setAttributeNS(null, 'x', current.xcoords.map(pf).join(' '));
						current.tspan.setAttributeNS(null, 'y', pf(-current.y));
						current.tspan.setAttributeNS(null, 'font-family', current.fontFamily);
						current.tspan.setAttributeNS(null, 'font-size', pf(current.fontSize) + 'px');
						if (current.fontStyle !== SVG_DEFAULTS.fontStyle) {
							current.tspan.setAttributeNS(null, 'font-style', current.fontStyle);
						}
						if (current.fontWeight !== SVG_DEFAULTS.fontWeight) {
							current.tspan.setAttributeNS(null, 'font-weight', current.fontWeight);
						}
						if (current.fillColor !== SVG_DEFAULTS.fillColor) {
							current.tspan.setAttributeNS(null, 'fill', current.fillColor);
						}
						current.txtElement.setAttributeNS(null, 'transform', pm(current.textMatrix) + ' scale(1, -1)');
						current.txtElement.setAttributeNS(XML_NS, 'xml:space', 'preserve');
						current.txtElement.appendChild(current.tspan);
						current.txtgrp.appendChild(current.txtElement);
						this._ensureTransformGroup().appendChild(current.txtElement);
					},
					setLeadingMoveText: function SVGGraphics_setLeadingMoveText(x, y) {
						this.setLeading(-y);
						this.moveText(x, y);
					},
					addFontStyle: function SVGGraphics_addFontStyle(fontObj) {
						if (!this.cssStyle) {
							this.cssStyle = document.createElementNS(NS, 'svg:style');
							this.cssStyle.setAttributeNS(null, 'type', 'text/css');
							this.defs.appendChild(this.cssStyle);
						}
						var url = createObjectURL(fontObj.data, fontObj.mimetype, this.forceDataSchema);
						this.cssStyle.textContent += '@font-face { font-family: "' + fontObj.loadedName + '";' + ' src: url(' + url + '); }\n';
					},
					setFont: function SVGGraphics_setFont(details) {
						var current = this.current;
						var fontObj = this.commonObjs.get(details[0]);
						var size = details[1];
						this.current.font = fontObj;
						if (this.embedFonts && fontObj.data && !this.embeddedFonts[fontObj.loadedName]) {
							this.addFontStyle(fontObj);
							this.embeddedFonts[fontObj.loadedName] = fontObj;
						}
						current.fontMatrix = fontObj.fontMatrix ? fontObj.fontMatrix : FONT_IDENTITY_MATRIX;
						var bold = fontObj.black ? fontObj.bold ? 'bolder' : 'bold' : fontObj.bold ? 'bold' : 'normal';
						var italic = fontObj.italic ? 'italic' : 'normal';
						if (size < 0) {
							size = -size;
							current.fontDirection = -1;
						} else {
							current.fontDirection = 1;
						}
						current.fontSize = size;
						current.fontFamily = fontObj.loadedName;
						current.fontWeight = bold;
						current.fontStyle = italic;
						current.tspan = document.createElementNS(NS, 'svg:tspan');
						current.tspan.setAttributeNS(null, 'y', pf(-current.y));
						current.xcoords = [];
					},
					endText: function SVGGraphics_endText() {
					},
					setLineWidth: function SVGGraphics_setLineWidth(width) {
						this.current.lineWidth = width;
					},
					setLineCap: function SVGGraphics_setLineCap(style) {
						this.current.lineCap = LINE_CAP_STYLES[style];
					},
					setLineJoin: function SVGGraphics_setLineJoin(style) {
						this.current.lineJoin = LINE_JOIN_STYLES[style];
					},
					setMiterLimit: function SVGGraphics_setMiterLimit(limit) {
						this.current.miterLimit = limit;
					},
					setStrokeRGBColor: function SVGGraphics_setStrokeRGBColor(r, g, b) {
						var color = Util.makeCssRgb(r, g, b);
						this.current.strokeColor = color;
					},
					setFillRGBColor: function SVGGraphics_setFillRGBColor(r, g, b) {
						var color = Util.makeCssRgb(r, g, b);
						this.current.fillColor = color;
						this.current.tspan = document.createElementNS(NS, 'svg:tspan');
						this.current.xcoords = [];
					},
					setDash: function SVGGraphics_setDash(dashArray, dashPhase) {
						this.current.dashArray = dashArray;
						this.current.dashPhase = dashPhase;
					},
					constructPath: function SVGGraphics_constructPath(ops, args) {
						var current = this.current;
						var x = current.x, y = current.y;
						current.path = document.createElementNS(NS, 'svg:path');
						var d = [];
						var opLength = ops.length;
						for (var i = 0, j = 0; i < opLength; i++) {
							switch (ops[i] | 0) {
								case OPS.rectangle:
									x = args[j++];
									y = args[j++];
									var width = args[j++];
									var height = args[j++];
									var xw = x + width;
									var yh = y + height;
									d.push('M', pf(x), pf(y), 'L', pf(xw), pf(y), 'L', pf(xw), pf(yh), 'L', pf(x), pf(yh), 'Z');
									break;
								case OPS.moveTo:
									x = args[j++];
									y = args[j++];
									d.push('M', pf(x), pf(y));
									break;
								case OPS.lineTo:
									x = args[j++];
									y = args[j++];
									d.push('L', pf(x), pf(y));
									break;
								case OPS.curveTo:
									x = args[j + 4];
									y = args[j + 5];
									d.push('C', pf(args[j]), pf(args[j + 1]), pf(args[j + 2]), pf(args[j + 3]), pf(x), pf(y));
									j += 6;
									break;
								case OPS.curveTo2:
									x = args[j + 2];
									y = args[j + 3];
									d.push('C', pf(x), pf(y), pf(args[j]), pf(args[j + 1]), pf(args[j + 2]), pf(args[j + 3]));
									j += 4;
									break;
								case OPS.curveTo3:
									x = args[j + 2];
									y = args[j + 3];
									d.push('C', pf(args[j]), pf(args[j + 1]), pf(x), pf(y), pf(x), pf(y));
									j += 4;
									break;
								case OPS.closePath:
									d.push('Z');
									break;
							}
						}
						current.path.setAttributeNS(null, 'd', d.join(' '));
						current.path.setAttributeNS(null, 'stroke-miterlimit', pf(current.miterLimit));
						current.path.setAttributeNS(null, 'stroke-linecap', current.lineCap);
						current.path.setAttributeNS(null, 'stroke-linejoin', current.lineJoin);
						current.path.setAttributeNS(null, 'stroke-width', pf(current.lineWidth) + 'px');
						current.path.setAttributeNS(null, 'stroke-dasharray', current.dashArray.map(pf).join(' '));
						current.path.setAttributeNS(null, 'stroke-dashoffset', pf(current.dashPhase) + 'px');
						current.path.setAttributeNS(null, 'fill', 'none');
						this._ensureTransformGroup().appendChild(current.path);
						current.element = current.path;
						current.setCurrentPoint(x, y);
					},
					endPath: function SVGGraphics_endPath() {
					},
					clip: function SVGGraphics_clip(type) {
						var current = this.current;
						var clipId = 'clippath' + clipCount;
						clipCount++;
						var clipPath = document.createElementNS(NS, 'svg:clipPath');
						clipPath.setAttributeNS(null, 'id', clipId);
						clipPath.setAttributeNS(null, 'transform', pm(this.transformMatrix));
						var clipElement = current.element.cloneNode();
						if (type === 'evenodd') {
							clipElement.setAttributeNS(null, 'clip-rule', 'evenodd');
						} else {
							clipElement.setAttributeNS(null, 'clip-rule', 'nonzero');
						}
						clipPath.appendChild(clipElement);
						this.defs.appendChild(clipPath);
						if (current.activeClipUrl) {
							current.clipGroup = null;
							this.extraStack.forEach(function (prev) {
								prev.clipGroup = null;
							});
						}
						current.activeClipUrl = 'url(#' + clipId + ')';
						this.tgrp = null;
					},
					closePath: function SVGGraphics_closePath() {
						var current = this.current;
						var d = current.path.getAttributeNS(null, 'd');
						d += 'Z';
						current.path.setAttributeNS(null, 'd', d);
					},
					setLeading: function SVGGraphics_setLeading(leading) {
						this.current.leading = -leading;
					},
					setTextRise: function SVGGraphics_setTextRise(textRise) {
						this.current.textRise = textRise;
					},
					setHScale: function SVGGraphics_setHScale(scale) {
						this.current.textHScale = scale / 100;
					},
					setGState: function SVGGraphics_setGState(states) {
						for (var i = 0, ii = states.length; i < ii; i++) {
							var state = states[i];
							var key = state[0];
							var value = state[1];
							switch (key) {
								case 'LW':
									this.setLineWidth(value);
									break;
								case 'LC':
									this.setLineCap(value);
									break;
								case 'LJ':
									this.setLineJoin(value);
									break;
								case 'ML':
									this.setMiterLimit(value);
									break;
								case 'D':
									this.setDash(value[0], value[1]);
									break;
								case 'Font':
									this.setFont(value);
									break;
								default:
									warn('Unimplemented graphic state ' + key);
									break;
							}
						}
					},
					fill: function SVGGraphics_fill() {
						var current = this.current;
						current.element.setAttributeNS(null, 'fill', current.fillColor);
					},
					stroke: function SVGGraphics_stroke() {
						var current = this.current;
						current.element.setAttributeNS(null, 'stroke', current.strokeColor);
						current.element.setAttributeNS(null, 'fill', 'none');
					},
					eoFill: function SVGGraphics_eoFill() {
						var current = this.current;
						current.element.setAttributeNS(null, 'fill', current.fillColor);
						current.element.setAttributeNS(null, 'fill-rule', 'evenodd');
					},
					fillStroke: function SVGGraphics_fillStroke() {
						this.stroke();
						this.fill();
					},
					eoFillStroke: function SVGGraphics_eoFillStroke() {
						this.current.element.setAttributeNS(null, 'fill-rule', 'evenodd');
						this.fillStroke();
					},
					closeStroke: function SVGGraphics_closeStroke() {
						this.closePath();
						this.stroke();
					},
					closeFillStroke: function SVGGraphics_closeFillStroke() {
						this.closePath();
						this.fillStroke();
					},
					paintSolidColorImageMask: function SVGGraphics_paintSolidColorImageMask() {
						var current = this.current;
						var rect = document.createElementNS(NS, 'svg:rect');
						rect.setAttributeNS(null, 'x', '0');
						rect.setAttributeNS(null, 'y', '0');
						rect.setAttributeNS(null, 'width', '1px');
						rect.setAttributeNS(null, 'height', '1px');
						rect.setAttributeNS(null, 'fill', current.fillColor);
						this._ensureTransformGroup().appendChild(rect);
					},
					paintJpegXObject: function SVGGraphics_paintJpegXObject(objId, w, h) {
						var imgObj = this.objs.get(objId);
						var imgEl = document.createElementNS(NS, 'svg:image');
						imgEl.setAttributeNS(XLINK_NS, 'xlink:href', imgObj.src);
						imgEl.setAttributeNS(null, 'width', imgObj.width + 'px');
						imgEl.setAttributeNS(null, 'height', imgObj.height + 'px');
						imgEl.setAttributeNS(null, 'x', '0');
						imgEl.setAttributeNS(null, 'y', pf(-h));
						imgEl.setAttributeNS(null, 'transform', 'scale(' + pf(1 / w) + ' ' + pf(-1 / h) + ')');
						this._ensureTransformGroup().appendChild(imgEl);
					},
					paintImageXObject: function SVGGraphics_paintImageXObject(objId) {
						var imgData = this.objs.get(objId);
						if (!imgData) {
							warn('Dependent image isn\'t ready yet');
							return;
						}
						this.paintInlineImageXObject(imgData);
					},
					paintInlineImageXObject: function SVGGraphics_paintInlineImageXObject(imgData, mask) {
						var width = imgData.width;
						var height = imgData.height;
						var imgSrc = convertImgDataToPng(imgData, this.forceDataSchema);
						var cliprect = document.createElementNS(NS, 'svg:rect');
						cliprect.setAttributeNS(null, 'x', '0');
						cliprect.setAttributeNS(null, 'y', '0');
						cliprect.setAttributeNS(null, 'width', pf(width));
						cliprect.setAttributeNS(null, 'height', pf(height));
						this.current.element = cliprect;
						this.clip('nonzero');
						var imgEl = document.createElementNS(NS, 'svg:image');
						imgEl.setAttributeNS(XLINK_NS, 'xlink:href', imgSrc);
						imgEl.setAttributeNS(null, 'x', '0');
						imgEl.setAttributeNS(null, 'y', pf(-height));
						imgEl.setAttributeNS(null, 'width', pf(width) + 'px');
						imgEl.setAttributeNS(null, 'height', pf(height) + 'px');
						imgEl.setAttributeNS(null, 'transform', 'scale(' + pf(1 / width) + ' ' + pf(-1 / height) + ')');
						if (mask) {
							mask.appendChild(imgEl);
						} else {
							this._ensureTransformGroup().appendChild(imgEl);
						}
					},
					paintImageMaskXObject: function SVGGraphics_paintImageMaskXObject(imgData) {
						var current = this.current;
						var width = imgData.width;
						var height = imgData.height;
						var fillColor = current.fillColor;
						current.maskId = 'mask' + maskCount++;
						var mask = document.createElementNS(NS, 'svg:mask');
						mask.setAttributeNS(null, 'id', current.maskId);
						var rect = document.createElementNS(NS, 'svg:rect');
						rect.setAttributeNS(null, 'x', '0');
						rect.setAttributeNS(null, 'y', '0');
						rect.setAttributeNS(null, 'width', pf(width));
						rect.setAttributeNS(null, 'height', pf(height));
						rect.setAttributeNS(null, 'fill', fillColor);
						rect.setAttributeNS(null, 'mask', 'url(#' + current.maskId + ')');
						this.defs.appendChild(mask);
						this._ensureTransformGroup().appendChild(rect);
						this.paintInlineImageXObject(imgData, mask);
					},
					paintFormXObjectBegin: function SVGGraphics_paintFormXObjectBegin(matrix, bbox) {
						if (isArray(matrix) && matrix.length === 6) {
							this.transform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
						}
						if (isArray(bbox) && bbox.length === 4) {
							var width = bbox[2] - bbox[0];
							var height = bbox[3] - bbox[1];
							var cliprect = document.createElementNS(NS, 'svg:rect');
							cliprect.setAttributeNS(null, 'x', bbox[0]);
							cliprect.setAttributeNS(null, 'y', bbox[1]);
							cliprect.setAttributeNS(null, 'width', pf(width));
							cliprect.setAttributeNS(null, 'height', pf(height));
							this.current.element = cliprect;
							this.clip('nonzero');
							this.endPath();
						}
					},
					paintFormXObjectEnd: function SVGGraphics_paintFormXObjectEnd() {
					},
					_initialize: function SVGGraphics_initialize(viewport) {
						var svg = document.createElementNS(NS, 'svg:svg');
						svg.setAttributeNS(null, 'version', '1.1');
						svg.setAttributeNS(null, 'width', viewport.width + 'px');
						svg.setAttributeNS(null, 'height', viewport.height + 'px');
						svg.setAttributeNS(null, 'preserveAspectRatio', 'none');
						svg.setAttributeNS(null, 'viewBox', '0 0 ' + viewport.width + ' ' + viewport.height);
						var definitions = document.createElementNS(NS, 'svg:defs');
						svg.appendChild(definitions);
						this.defs = definitions;
						var rootGroup = document.createElementNS(NS, 'svg:g');
						rootGroup.setAttributeNS(null, 'transform', pm(viewport.transform));
						svg.appendChild(rootGroup);
						this.svg = rootGroup;
						return svg;
					},
					_ensureClipGroup: function SVGGraphics_ensureClipGroup() {
						if (!this.current.clipGroup) {
							var clipGroup = document.createElementNS(NS, 'svg:g');
							clipGroup.setAttributeNS(null, 'clip-path', this.current.activeClipUrl);
							this.svg.appendChild(clipGroup);
							this.current.clipGroup = clipGroup;
						}
						return this.current.clipGroup;
					},
					_ensureTransformGroup: function SVGGraphics_ensureTransformGroup() {
						if (!this.tgrp) {
							this.tgrp = document.createElementNS(NS, 'svg:g');
							this.tgrp.setAttributeNS(null, 'transform', pm(this.transformMatrix));
							if (this.current.activeClipUrl) {
								this._ensureClipGroup().appendChild(this.tgrp);
							} else {
								this.svg.appendChild(this.tgrp);
							}
						}
						return this.tgrp;
					}
				};
				return SVGGraphics;
			}();
			exports.SVGGraphics = SVGGraphics;

			/***/
		}),
/* 5 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var Util = sharedUtil.Util;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var CustomStyle = displayDOMUtils.CustomStyle;
			var getDefaultSetting = displayDOMUtils.getDefaultSetting;
			var renderTextLayer = function renderTextLayerClosure() {
				var MAX_TEXT_DIVS_TO_RENDER = 100000;
				var NonWhitespaceRegexp = /\S/;
				function isAllWhitespace(str) {
					return !NonWhitespaceRegexp.test(str);
				}
				var styleBuf = [
					'left: ',
					0,
					'px; top: ',
					0,
					'px; font-size: ',
					0,
					'px; font-family: ',
					'',
					';'
				];
				function appendText(task, geom, styles) {
					var textDiv = document.createElement('div');
					var textDivProperties = {
						style: null,
						angle: 0,
						canvasWidth: 0,
						isWhitespace: false,
						originalTransform: null,
						paddingBottom: 0,
						paddingLeft: 0,
						paddingRight: 0,
						paddingTop: 0,
						scale: 1
					};
					task._textDivs.push(textDiv);
					if (isAllWhitespace(geom.str)) {
						textDivProperties.isWhitespace = true;
						task._textDivProperties.set(textDiv, textDivProperties);
						return;
					}
					var tx = Util.transform(task._viewport.transform, geom.transform);
					var angle = Math.atan2(tx[1], tx[0]);
					var style = styles[geom.fontName];
					if (style.vertical) {
						angle += Math.PI / 2;
					}
					var fontHeight = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);
					var fontAscent = fontHeight;
					if (style.ascent) {
						fontAscent = style.ascent * fontAscent;
					} else if (style.descent) {
						fontAscent = (1 + style.descent) * fontAscent;
					}
					var left;
					var top;
					if (angle === 0) {
						left = tx[4];
						top = tx[5] - fontAscent;
					} else {
						left = tx[4] + fontAscent * Math.sin(angle);
						top = tx[5] - fontAscent * Math.cos(angle);
					}
					styleBuf[1] = left;
					styleBuf[3] = top;
					styleBuf[5] = fontHeight;
					styleBuf[7] = style.fontFamily;
					textDivProperties.style = styleBuf.join('');
					textDiv.setAttribute('style', textDivProperties.style);
					textDiv.textContent = geom.str;
					if (getDefaultSetting('pdfBug')) {
						textDiv.dataset.fontName = geom.fontName;
					}
					if (angle !== 0) {
						textDivProperties.angle = angle * (180 / Math.PI);
					}
					if (geom.str.length > 1) {
						if (style.vertical) {
							textDivProperties.canvasWidth = geom.height * task._viewport.scale;
						} else {
							textDivProperties.canvasWidth = geom.width * task._viewport.scale;
						}
					}
					task._textDivProperties.set(textDiv, textDivProperties);
					if (task._enhanceTextSelection) {
						var angleCos = 1, angleSin = 0;
						if (angle !== 0) {
							angleCos = Math.cos(angle);
							angleSin = Math.sin(angle);
						}
						var divWidth = (style.vertical ? geom.height : geom.width) * task._viewport.scale;
						var divHeight = fontHeight;
						var m, b;
						if (angle !== 0) {
							m = [
								angleCos,
								angleSin,
								-angleSin,
								angleCos,
								left,
								top
							];
							b = Util.getAxialAlignedBoundingBox([
								0,
								0,
								divWidth,
								divHeight
							], m);
						} else {
							b = [
								left,
								top,
								left + divWidth,
								top + divHeight
							];
						}
						task._bounds.push({
							left: b[0],
							top: b[1],
							right: b[2],
							bottom: b[3],
							div: textDiv,
							size: [
								divWidth,
								divHeight
							],
							m: m
						});
					}
				}
				function render(task) {
					if (task._canceled) {
						return;
					}
					var textLayerFrag = task._container;
					var textDivs = task._textDivs;
					var capability = task._capability;
					var textDivsLength = textDivs.length;
					if (textDivsLength > MAX_TEXT_DIVS_TO_RENDER) {
						task._renderingDone = true;
						capability.resolve();
						return;
					}
					var canvas = document.createElement('canvas');
					canvas.mozOpaque = true;
					var ctx = canvas.getContext('2d', { alpha: false });
					var lastFontSize;
					var lastFontFamily;
					for (var i = 0; i < textDivsLength; i++) {
						var textDiv = textDivs[i];
						var textDivProperties = task._textDivProperties.get(textDiv);
						if (textDivProperties.isWhitespace) {
							continue;
						}
						var fontSize = textDiv.style.fontSize;
						var fontFamily = textDiv.style.fontFamily;
						if (fontSize !== lastFontSize || fontFamily !== lastFontFamily) {
							ctx.font = fontSize + ' ' + fontFamily;
							lastFontSize = fontSize;
							lastFontFamily = fontFamily;
						}
						var width = ctx.measureText(textDiv.textContent).width;
						textLayerFrag.appendChild(textDiv);
						var transform = '';
						if (textDivProperties.canvasWidth !== 0 && width > 0) {
							textDivProperties.scale = textDivProperties.canvasWidth / width;
							transform = 'scaleX(' + textDivProperties.scale + ')';
						}
						if (textDivProperties.angle !== 0) {
							transform = 'rotate(' + textDivProperties.angle + 'deg) ' + transform;
						}
						if (transform !== '') {
							textDivProperties.originalTransform = transform;
							CustomStyle.setProp('transform', textDiv, transform);
						}
						task._textDivProperties.set(textDiv, textDivProperties);
					}
					task._renderingDone = true;
					capability.resolve();
				}
				function expand(task) {
					var bounds = task._bounds;
					var viewport = task._viewport;
					var expanded = expandBounds(viewport.width, viewport.height, bounds);
					for (var i = 0; i < expanded.length; i++) {
						var div = bounds[i].div;
						var divProperties = task._textDivProperties.get(div);
						if (divProperties.angle === 0) {
							divProperties.paddingLeft = bounds[i].left - expanded[i].left;
							divProperties.paddingTop = bounds[i].top - expanded[i].top;
							divProperties.paddingRight = expanded[i].right - bounds[i].right;
							divProperties.paddingBottom = expanded[i].bottom - bounds[i].bottom;
							task._textDivProperties.set(div, divProperties);
							continue;
						}
						var e = expanded[i], b = bounds[i];
						var m = b.m, c = m[0], s = m[1];
						var points = [
							[
								0,
								0
							],
							[
								0,
								b.size[1]
							],
							[
								b.size[0],
								0
							],
							b.size
						];
						var ts = new Float64Array(64);
						points.forEach(function (p, i) {
							var t = Util.applyTransform(p, m);
							ts[i + 0] = c && (e.left - t[0]) / c;
							ts[i + 4] = s && (e.top - t[1]) / s;
							ts[i + 8] = c && (e.right - t[0]) / c;
							ts[i + 12] = s && (e.bottom - t[1]) / s;
							ts[i + 16] = s && (e.left - t[0]) / -s;
							ts[i + 20] = c && (e.top - t[1]) / c;
							ts[i + 24] = s && (e.right - t[0]) / -s;
							ts[i + 28] = c && (e.bottom - t[1]) / c;
							ts[i + 32] = c && (e.left - t[0]) / -c;
							ts[i + 36] = s && (e.top - t[1]) / -s;
							ts[i + 40] = c && (e.right - t[0]) / -c;
							ts[i + 44] = s && (e.bottom - t[1]) / -s;
							ts[i + 48] = s && (e.left - t[0]) / s;
							ts[i + 52] = c && (e.top - t[1]) / -c;
							ts[i + 56] = s && (e.right - t[0]) / s;
							ts[i + 60] = c && (e.bottom - t[1]) / -c;
						});
						var findPositiveMin = function (ts, offset, count) {
							var result = 0;
							for (var i = 0; i < count; i++) {
								var t = ts[offset++];
								if (t > 0) {
									result = result ? Math.min(t, result) : t;
								}
							}
							return result;
						};
						var boxScale = 1 + Math.min(Math.abs(c), Math.abs(s));
						divProperties.paddingLeft = findPositiveMin(ts, 32, 16) / boxScale;
						divProperties.paddingTop = findPositiveMin(ts, 48, 16) / boxScale;
						divProperties.paddingRight = findPositiveMin(ts, 0, 16) / boxScale;
						divProperties.paddingBottom = findPositiveMin(ts, 16, 16) / boxScale;
						task._textDivProperties.set(div, divProperties);
					}
				}
				function expandBounds(width, height, boxes) {
					var bounds = boxes.map(function (box, i) {
						return {
							x1: box.left,
							y1: box.top,
							x2: box.right,
							y2: box.bottom,
							index: i,
							x1New: undefined,
							x2New: undefined
						};
					});
					expandBoundsLTR(width, bounds);
					var expanded = new Array(boxes.length);
					bounds.forEach(function (b) {
						var i = b.index;
						expanded[i] = {
							left: b.x1New,
							top: 0,
							right: b.x2New,
							bottom: 0
						};
					});
					boxes.map(function (box, i) {
						var e = expanded[i], b = bounds[i];
						b.x1 = box.top;
						b.y1 = width - e.right;
						b.x2 = box.bottom;
						b.y2 = width - e.left;
						b.index = i;
						b.x1New = undefined;
						b.x2New = undefined;
					});
					expandBoundsLTR(height, bounds);
					bounds.forEach(function (b) {
						var i = b.index;
						expanded[i].top = b.x1New;
						expanded[i].bottom = b.x2New;
					});
					return expanded;
				}
				function expandBoundsLTR(width, bounds) {
					bounds.sort(function (a, b) {
						return a.x1 - b.x1 || a.index - b.index;
					});
					var fakeBoundary = {
						x1: -Infinity,
						y1: -Infinity,
						x2: 0,
						y2: Infinity,
						index: -1,
						x1New: 0,
						x2New: 0
					};
					var horizon = [{
						start: -Infinity,
						end: Infinity,
						boundary: fakeBoundary
					}];
					bounds.forEach(function (boundary) {
						var i = 0;
						while (i < horizon.length && horizon[i].end <= boundary.y1) {
							i++;
						}
						var j = horizon.length - 1;
						while (j >= 0 && horizon[j].start >= boundary.y2) {
							j--;
						}
						var horizonPart, affectedBoundary;
						var q, k, maxXNew = -Infinity;
						for (q = i; q <= j; q++) {
							horizonPart = horizon[q];
							affectedBoundary = horizonPart.boundary;
							var xNew;
							if (affectedBoundary.x2 > boundary.x1) {
								xNew = affectedBoundary.index > boundary.index ? affectedBoundary.x1New : boundary.x1;
							} else if (affectedBoundary.x2New === undefined) {
								xNew = (affectedBoundary.x2 + boundary.x1) / 2;
							} else {
								xNew = affectedBoundary.x2New;
							}
							if (xNew > maxXNew) {
								maxXNew = xNew;
							}
						}
						boundary.x1New = maxXNew;
						for (q = i; q <= j; q++) {
							horizonPart = horizon[q];
							affectedBoundary = horizonPart.boundary;
							if (affectedBoundary.x2New === undefined) {
								if (affectedBoundary.x2 > boundary.x1) {
									if (affectedBoundary.index > boundary.index) {
										affectedBoundary.x2New = affectedBoundary.x2;
									}
								} else {
									affectedBoundary.x2New = maxXNew;
								}
							} else if (affectedBoundary.x2New > maxXNew) {
								affectedBoundary.x2New = Math.max(maxXNew, affectedBoundary.x2);
							}
						}
						var changedHorizon = [], lastBoundary = null;
						for (q = i; q <= j; q++) {
							horizonPart = horizon[q];
							affectedBoundary = horizonPart.boundary;
							var useBoundary = affectedBoundary.x2 > boundary.x2 ? affectedBoundary : boundary;
							if (lastBoundary === useBoundary) {
								changedHorizon[changedHorizon.length - 1].end = horizonPart.end;
							} else {
								changedHorizon.push({
									start: horizonPart.start,
									end: horizonPart.end,
									boundary: useBoundary
								});
								lastBoundary = useBoundary;
							}
						}
						if (horizon[i].start < boundary.y1) {
							changedHorizon[0].start = boundary.y1;
							changedHorizon.unshift({
								start: horizon[i].start,
								end: boundary.y1,
								boundary: horizon[i].boundary
							});
						}
						if (boundary.y2 < horizon[j].end) {
							changedHorizon[changedHorizon.length - 1].end = boundary.y2;
							changedHorizon.push({
								start: boundary.y2,
								end: horizon[j].end,
								boundary: horizon[j].boundary
							});
						}
						for (q = i; q <= j; q++) {
							horizonPart = horizon[q];
							affectedBoundary = horizonPart.boundary;
							if (affectedBoundary.x2New !== undefined) {
								continue;
							}
							var used = false;
							for (k = i - 1; !used && k >= 0 && horizon[k].start >= affectedBoundary.y1; k--) {
								used = horizon[k].boundary === affectedBoundary;
							}
							for (k = j + 1; !used && k < horizon.length && horizon[k].end <= affectedBoundary.y2; k++) {
								used = horizon[k].boundary === affectedBoundary;
							}
							for (k = 0; !used && k < changedHorizon.length; k++) {
								used = changedHorizon[k].boundary === affectedBoundary;
							}
							if (!used) {
								affectedBoundary.x2New = maxXNew;
							}
						}
						Array.prototype.splice.apply(horizon, [
							i,
							j - i + 1
						].concat(changedHorizon));
					});
					horizon.forEach(function (horizonPart) {
						var affectedBoundary = horizonPart.boundary;
						if (affectedBoundary.x2New === undefined) {
							affectedBoundary.x2New = Math.max(width, affectedBoundary.x2);
						}
					});
				}
				function TextLayerRenderTask(textContent, container, viewport, textDivs, enhanceTextSelection) {
					this._textContent = textContent;
					this._container = container;
					this._viewport = viewport;
					this._textDivs = textDivs || [];
					this._textDivProperties = new WeakMap();
					this._renderingDone = false;
					this._canceled = false;
					this._capability = createPromiseCapability();
					this._renderTimer = null;
					this._bounds = [];
					this._enhanceTextSelection = !!enhanceTextSelection;
				}
				TextLayerRenderTask.prototype = {
					get promise() {
						return this._capability.promise;
					},
					cancel: function TextLayer_cancel() {
						this._canceled = true;
						if (this._renderTimer !== null) {
							clearTimeout(this._renderTimer);
							this._renderTimer = null;
						}
						this._capability.reject('canceled');
					},
					_render: function TextLayer_render(timeout) {
						var textItems = this._textContent.items;
						var textStyles = this._textContent.styles;
						for (var i = 0, len = textItems.length; i < len; i++) {
							appendText(this, textItems[i], textStyles);
						}
						if (!timeout) {
							render(this);
						} else {
							var self = this;
							this._renderTimer = setTimeout(function () {
								render(self);
								self._renderTimer = null;
							}, timeout);
						}
					},
					expandTextDivs: function TextLayer_expandTextDivs(expandDivs) {
						if (!this._enhanceTextSelection || !this._renderingDone) {
							return;
						}
						if (this._bounds !== null) {
							expand(this);
							this._bounds = null;
						}
						for (var i = 0, ii = this._textDivs.length; i < ii; i++) {
							var div = this._textDivs[i];
							var divProperties = this._textDivProperties.get(div);
							if (divProperties.isWhitespace) {
								continue;
							}
							if (expandDivs) {
								var transform = '', padding = '';
								if (divProperties.scale !== 1) {
									transform = 'scaleX(' + divProperties.scale + ')';
								}
								if (divProperties.angle !== 0) {
									transform = 'rotate(' + divProperties.angle + 'deg) ' + transform;
								}
								if (divProperties.paddingLeft !== 0) {
									padding += ' padding-left: ' + divProperties.paddingLeft / divProperties.scale + 'px;';
									transform += ' translateX(' + -divProperties.paddingLeft / divProperties.scale + 'px)';
								}
								if (divProperties.paddingTop !== 0) {
									padding += ' padding-top: ' + divProperties.paddingTop + 'px;';
									transform += ' translateY(' + -divProperties.paddingTop + 'px)';
								}
								if (divProperties.paddingRight !== 0) {
									padding += ' padding-right: ' + divProperties.paddingRight / divProperties.scale + 'px;';
								}
								if (divProperties.paddingBottom !== 0) {
									padding += ' padding-bottom: ' + divProperties.paddingBottom + 'px;';
								}
								if (padding !== '') {
									div.setAttribute('style', divProperties.style + padding);
								}
								if (transform !== '') {
									CustomStyle.setProp('transform', div, transform);
								}
							} else {
								div.style.padding = 0;
								CustomStyle.setProp('transform', div, divProperties.originalTransform || '');
							}
						}
					}
				};
				function renderTextLayer(renderParameters) {
					var task = new TextLayerRenderTask(renderParameters.textContent, renderParameters.container, renderParameters.viewport, renderParameters.textDivs, renderParameters.enhanceTextSelection);
					task._render(renderParameters.timeout);
					return task;
				}
				return renderTextLayer;
			}();
			exports.renderTextLayer = renderTextLayer;

			/***/
		}),
/* 6 */
/***/ (function (module, exports) {

			var g;
			g = function () {
				return this;
			}();
			try {
				g = g || Function("return this")() || (1, eval)("this");
			} catch (e) {
				if (typeof window === "object")
					g = window;
			}
			module.exports = g;

			/***/
		}),
/* 7 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var error = sharedUtil.error;
			function fixMetadata(meta) {
				return meta.replace(/>\\376\\377([^<]+)/g, function (all, codes) {
					var bytes = codes.replace(/\\([0-3])([0-7])([0-7])/g, function (code, d1, d2, d3) {
						return String.fromCharCode(d1 * 64 + d2 * 8 + d3 * 1);
					});
					var chars = '';
					for (var i = 0; i < bytes.length; i += 2) {
						var code = bytes.charCodeAt(i) * 256 + bytes.charCodeAt(i + 1);
						chars += code >= 32 && code < 127 && code !== 60 && code !== 62 && code !== 38 ? String.fromCharCode(code) : '&#x' + (0x10000 + code).toString(16).substring(1) + ';';
					}
					return '>' + chars;
				});
			}
			function Metadata(meta) {
				if (typeof meta === 'string') {
					meta = fixMetadata(meta);
					var parser = new DOMParser();
					meta = parser.parseFromString(meta, 'application/xml');
				} else if (!(meta instanceof Document)) {
					error('Metadata: Invalid metadata object');
				}
				this.metaDocument = meta;
				this.metadata = Object.create(null);
				this.parse();
			}
			Metadata.prototype = {
				parse: function Metadata_parse() {
					var doc = this.metaDocument;
					var rdf = doc.documentElement;
					if (rdf.nodeName.toLowerCase() !== 'rdf:rdf') {
						rdf = rdf.firstChild;
						while (rdf && rdf.nodeName.toLowerCase() !== 'rdf:rdf') {
							rdf = rdf.nextSibling;
						}
					}
					var nodeName = rdf ? rdf.nodeName.toLowerCase() : null;
					if (!rdf || nodeName !== 'rdf:rdf' || !rdf.hasChildNodes()) {
						return;
					}
					var children = rdf.childNodes, desc, entry, name, i, ii, length, iLength;
					for (i = 0, length = children.length; i < length; i++) {
						desc = children[i];
						if (desc.nodeName.toLowerCase() !== 'rdf:description') {
							continue;
						}
						for (ii = 0, iLength = desc.childNodes.length; ii < iLength; ii++) {
							if (desc.childNodes[ii].nodeName.toLowerCase() !== '#text') {
								entry = desc.childNodes[ii];
								name = entry.nodeName.toLowerCase();
								this.metadata[name] = entry.textContent.trim();
							}
						}
					}
				},
				get: function Metadata_get(name) {
					return this.metadata[name] || null;
				},
				has: function Metadata_has(name) {
					return typeof this.metadata[name] !== 'undefined';
				}
			};
			exports.Metadata = Metadata;

			/***/
		}),
/* 8 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var shadow = sharedUtil.shadow;
			var getDefaultSetting = displayDOMUtils.getDefaultSetting;
			var WebGLUtils = function WebGLUtilsClosure() {
				function loadShader(gl, code, shaderType) {
					var shader = gl.createShader(shaderType);
					gl.shaderSource(shader, code);
					gl.compileShader(shader);
					var compiled = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
					if (!compiled) {
						var errorMsg = gl.getShaderInfoLog(shader);
						throw new Error('Error during shader compilation: ' + errorMsg);
					}
					return shader;
				}
				function createVertexShader(gl, code) {
					return loadShader(gl, code, gl.VERTEX_SHADER);
				}
				function createFragmentShader(gl, code) {
					return loadShader(gl, code, gl.FRAGMENT_SHADER);
				}
				function createProgram(gl, shaders) {
					var program = gl.createProgram();
					for (var i = 0, ii = shaders.length; i < ii; ++i) {
						gl.attachShader(program, shaders[i]);
					}
					gl.linkProgram(program);
					var linked = gl.getProgramParameter(program, gl.LINK_STATUS);
					if (!linked) {
						var errorMsg = gl.getProgramInfoLog(program);
						throw new Error('Error during program linking: ' + errorMsg);
					}
					return program;
				}
				function createTexture(gl, image, textureId) {
					gl.activeTexture(textureId);
					var texture = gl.createTexture();
					gl.bindTexture(gl.TEXTURE_2D, texture);
					gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
					gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
					gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
					gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
					gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
					return texture;
				}
				var currentGL, currentCanvas;
				function generateGL() {
					if (currentGL) {
						return;
					}
					currentCanvas = document.createElement('canvas');
					currentGL = currentCanvas.getContext('webgl', { premultipliedalpha: false });
				}
				var smaskVertexShaderCode = '\
  attribute vec2 a_position;                                    \
  attribute vec2 a_texCoord;                                    \
                                                                \
  uniform vec2 u_resolution;                                    \
                                                                \
  varying vec2 v_texCoord;                                      \
                                                                \
  void main() {                                                 \
    vec2 clipSpace = (a_position / u_resolution) * 2.0 - 1.0;   \
    gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);          \
                                                                \
    v_texCoord = a_texCoord;                                    \
  }                                                             ';
				var smaskFragmentShaderCode = '\
  precision mediump float;                                      \
                                                                \
  uniform vec4 u_backdrop;                                      \
  uniform int u_subtype;                                        \
  uniform sampler2D u_image;                                    \
  uniform sampler2D u_mask;                                     \
                                                                \
  varying vec2 v_texCoord;                                      \
                                                                \
  void main() {                                                 \
    vec4 imageColor = texture2D(u_image, v_texCoord);           \
    vec4 maskColor = texture2D(u_mask, v_texCoord);             \
    if (u_backdrop.a > 0.0) {                                   \
      maskColor.rgb = maskColor.rgb * maskColor.a +             \
                      u_backdrop.rgb * (1.0 - maskColor.a);     \
    }                                                           \
    float lum;                                                  \
    if (u_subtype == 0) {                                       \
      lum = maskColor.a;                                        \
    } else {                                                    \
      lum = maskColor.r * 0.3 + maskColor.g * 0.59 +            \
            maskColor.b * 0.11;                                 \
    }                                                           \
    imageColor.a *= lum;                                        \
    imageColor.rgb *= imageColor.a;                             \
    gl_FragColor = imageColor;                                  \
  }                                                             ';
				var smaskCache = null;
				function initSmaskGL() {
					var canvas, gl;
					generateGL();
					canvas = currentCanvas;
					currentCanvas = null;
					gl = currentGL;
					currentGL = null;
					var vertexShader = createVertexShader(gl, smaskVertexShaderCode);
					var fragmentShader = createFragmentShader(gl, smaskFragmentShaderCode);
					var program = createProgram(gl, [
						vertexShader,
						fragmentShader
					]);
					gl.useProgram(program);
					var cache = {};
					cache.gl = gl;
					cache.canvas = canvas;
					cache.resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
					cache.positionLocation = gl.getAttribLocation(program, 'a_position');
					cache.backdropLocation = gl.getUniformLocation(program, 'u_backdrop');
					cache.subtypeLocation = gl.getUniformLocation(program, 'u_subtype');
					var texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');
					var texLayerLocation = gl.getUniformLocation(program, 'u_image');
					var texMaskLocation = gl.getUniformLocation(program, 'u_mask');
					var texCoordBuffer = gl.createBuffer();
					gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
					gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
						0.0,
						0.0,
						1.0,
						0.0,
						0.0,
						1.0,
						0.0,
						1.0,
						1.0,
						0.0,
						1.0,
						1.0
					]), gl.STATIC_DRAW);
					gl.enableVertexAttribArray(texCoordLocation);
					gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 0, 0);
					gl.uniform1i(texLayerLocation, 0);
					gl.uniform1i(texMaskLocation, 1);
					smaskCache = cache;
				}
				function composeSMask(layer, mask, properties) {
					var width = layer.width, height = layer.height;
					if (!smaskCache) {
						initSmaskGL();
					}
					var cache = smaskCache, canvas = cache.canvas, gl = cache.gl;
					canvas.width = width;
					canvas.height = height;
					gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
					gl.uniform2f(cache.resolutionLocation, width, height);
					if (properties.backdrop) {
						gl.uniform4f(cache.resolutionLocation, properties.backdrop[0], properties.backdrop[1], properties.backdrop[2], 1);
					} else {
						gl.uniform4f(cache.resolutionLocation, 0, 0, 0, 0);
					}
					gl.uniform1i(cache.subtypeLocation, properties.subtype === 'Luminosity' ? 1 : 0);
					var texture = createTexture(gl, layer, gl.TEXTURE0);
					var maskTexture = createTexture(gl, mask, gl.TEXTURE1);
					var buffer = gl.createBuffer();
					gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
					gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
						0,
						0,
						width,
						0,
						0,
						height,
						0,
						height,
						width,
						0,
						width,
						height
					]), gl.STATIC_DRAW);
					gl.enableVertexAttribArray(cache.positionLocation);
					gl.vertexAttribPointer(cache.positionLocation, 2, gl.FLOAT, false, 0, 0);
					gl.clearColor(0, 0, 0, 0);
					gl.enable(gl.BLEND);
					gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
					gl.clear(gl.COLOR_BUFFER_BIT);
					gl.drawArrays(gl.TRIANGLES, 0, 6);
					gl.flush();
					gl.deleteTexture(texture);
					gl.deleteTexture(maskTexture);
					gl.deleteBuffer(buffer);
					return canvas;
				}
				var figuresVertexShaderCode = '\
  attribute vec2 a_position;                                    \
  attribute vec3 a_color;                                       \
                                                                \
  uniform vec2 u_resolution;                                    \
  uniform vec2 u_scale;                                         \
  uniform vec2 u_offset;                                        \
                                                                \
  varying vec4 v_color;                                         \
                                                                \
  void main() {                                                 \
    vec2 position = (a_position + u_offset) * u_scale;          \
    vec2 clipSpace = (position / u_resolution) * 2.0 - 1.0;     \
    gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);          \
                                                                \
    v_color = vec4(a_color / 255.0, 1.0);                       \
  }                                                             ';
				var figuresFragmentShaderCode = '\
  precision mediump float;                                      \
                                                                \
  varying vec4 v_color;                                         \
                                                                \
  void main() {                                                 \
    gl_FragColor = v_color;                                     \
  }                                                             ';
				var figuresCache = null;
				function initFiguresGL() {
					var canvas, gl;
					generateGL();
					canvas = currentCanvas;
					currentCanvas = null;
					gl = currentGL;
					currentGL = null;
					var vertexShader = createVertexShader(gl, figuresVertexShaderCode);
					var fragmentShader = createFragmentShader(gl, figuresFragmentShaderCode);
					var program = createProgram(gl, [
						vertexShader,
						fragmentShader
					]);
					gl.useProgram(program);
					var cache = {};
					cache.gl = gl;
					cache.canvas = canvas;
					cache.resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
					cache.scaleLocation = gl.getUniformLocation(program, 'u_scale');
					cache.offsetLocation = gl.getUniformLocation(program, 'u_offset');
					cache.positionLocation = gl.getAttribLocation(program, 'a_position');
					cache.colorLocation = gl.getAttribLocation(program, 'a_color');
					figuresCache = cache;
				}
				function drawFigures(width, height, backgroundColor, figures, context) {
					if (!figuresCache) {
						initFiguresGL();
					}
					var cache = figuresCache, canvas = cache.canvas, gl = cache.gl;
					canvas.width = width;
					canvas.height = height;
					gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
					gl.uniform2f(cache.resolutionLocation, width, height);
					var count = 0;
					var i, ii, rows;
					for (i = 0, ii = figures.length; i < ii; i++) {
						switch (figures[i].type) {
							case 'lattice':
								rows = figures[i].coords.length / figures[i].verticesPerRow | 0;
								count += (rows - 1) * (figures[i].verticesPerRow - 1) * 6;
								break;
							case 'triangles':
								count += figures[i].coords.length;
								break;
						}
					}
					var coords = new Float32Array(count * 2);
					var colors = new Uint8Array(count * 3);
					var coordsMap = context.coords, colorsMap = context.colors;
					var pIndex = 0, cIndex = 0;
					for (i = 0, ii = figures.length; i < ii; i++) {
						var figure = figures[i], ps = figure.coords, cs = figure.colors;
						switch (figure.type) {
							case 'lattice':
								var cols = figure.verticesPerRow;
								rows = ps.length / cols | 0;
								for (var row = 1; row < rows; row++) {
									var offset = row * cols + 1;
									for (var col = 1; col < cols; col++, offset++) {
										coords[pIndex] = coordsMap[ps[offset - cols - 1]];
										coords[pIndex + 1] = coordsMap[ps[offset - cols - 1] + 1];
										coords[pIndex + 2] = coordsMap[ps[offset - cols]];
										coords[pIndex + 3] = coordsMap[ps[offset - cols] + 1];
										coords[pIndex + 4] = coordsMap[ps[offset - 1]];
										coords[pIndex + 5] = coordsMap[ps[offset - 1] + 1];
										colors[cIndex] = colorsMap[cs[offset - cols - 1]];
										colors[cIndex + 1] = colorsMap[cs[offset - cols - 1] + 1];
										colors[cIndex + 2] = colorsMap[cs[offset - cols - 1] + 2];
										colors[cIndex + 3] = colorsMap[cs[offset - cols]];
										colors[cIndex + 4] = colorsMap[cs[offset - cols] + 1];
										colors[cIndex + 5] = colorsMap[cs[offset - cols] + 2];
										colors[cIndex + 6] = colorsMap[cs[offset - 1]];
										colors[cIndex + 7] = colorsMap[cs[offset - 1] + 1];
										colors[cIndex + 8] = colorsMap[cs[offset - 1] + 2];
										coords[pIndex + 6] = coords[pIndex + 2];
										coords[pIndex + 7] = coords[pIndex + 3];
										coords[pIndex + 8] = coords[pIndex + 4];
										coords[pIndex + 9] = coords[pIndex + 5];
										coords[pIndex + 10] = coordsMap[ps[offset]];
										coords[pIndex + 11] = coordsMap[ps[offset] + 1];
										colors[cIndex + 9] = colors[cIndex + 3];
										colors[cIndex + 10] = colors[cIndex + 4];
										colors[cIndex + 11] = colors[cIndex + 5];
										colors[cIndex + 12] = colors[cIndex + 6];
										colors[cIndex + 13] = colors[cIndex + 7];
										colors[cIndex + 14] = colors[cIndex + 8];
										colors[cIndex + 15] = colorsMap[cs[offset]];
										colors[cIndex + 16] = colorsMap[cs[offset] + 1];
										colors[cIndex + 17] = colorsMap[cs[offset] + 2];
										pIndex += 12;
										cIndex += 18;
									}
								}
								break;
							case 'triangles':
								for (var j = 0, jj = ps.length; j < jj; j++) {
									coords[pIndex] = coordsMap[ps[j]];
									coords[pIndex + 1] = coordsMap[ps[j] + 1];
									colors[cIndex] = colorsMap[cs[j]];
									colors[cIndex + 1] = colorsMap[cs[j] + 1];
									colors[cIndex + 2] = colorsMap[cs[j] + 2];
									pIndex += 2;
									cIndex += 3;
								}
								break;
						}
					}
					if (backgroundColor) {
						gl.clearColor(backgroundColor[0] / 255, backgroundColor[1] / 255, backgroundColor[2] / 255, 1.0);
					} else {
						gl.clearColor(0, 0, 0, 0);
					}
					gl.clear(gl.COLOR_BUFFER_BIT);
					var coordsBuffer = gl.createBuffer();
					gl.bindBuffer(gl.ARRAY_BUFFER, coordsBuffer);
					gl.bufferData(gl.ARRAY_BUFFER, coords, gl.STATIC_DRAW);
					gl.enableVertexAttribArray(cache.positionLocation);
					gl.vertexAttribPointer(cache.positionLocation, 2, gl.FLOAT, false, 0, 0);
					var colorsBuffer = gl.createBuffer();
					gl.bindBuffer(gl.ARRAY_BUFFER, colorsBuffer);
					gl.bufferData(gl.ARRAY_BUFFER, colors, gl.STATIC_DRAW);
					gl.enableVertexAttribArray(cache.colorLocation);
					gl.vertexAttribPointer(cache.colorLocation, 3, gl.UNSIGNED_BYTE, false, 0, 0);
					gl.uniform2f(cache.scaleLocation, context.scaleX, context.scaleY);
					gl.uniform2f(cache.offsetLocation, context.offsetX, context.offsetY);
					gl.drawArrays(gl.TRIANGLES, 0, count);
					gl.flush();
					gl.deleteBuffer(coordsBuffer);
					gl.deleteBuffer(colorsBuffer);
					return canvas;
				}
				function cleanup() {
					if (smaskCache && smaskCache.canvas) {
						smaskCache.canvas.width = 0;
						smaskCache.canvas.height = 0;
					}
					if (figuresCache && figuresCache.canvas) {
						figuresCache.canvas.width = 0;
						figuresCache.canvas.height = 0;
					}
					smaskCache = null;
					figuresCache = null;
				}
				return {
					get isEnabled() {
						if (getDefaultSetting('disableWebGL')) {
							return false;
						}
						var enabled = false;
						try {
							generateGL();
							enabled = !!currentGL;
						} catch (e) {
						}
						return shadow(this, 'isEnabled', enabled);
					},
					composeSMask: composeSMask,
					drawFigures: drawFigures,
					clear: cleanup
				};
			}();
			exports.WebGLUtils = WebGLUtils;

			/***/
		}),
/* 9 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var displayAPI = __w_pdfjs_require__(3);
			var displayAnnotationLayer = __w_pdfjs_require__(2);
			var displayTextLayer = __w_pdfjs_require__(5);
			var displayMetadata = __w_pdfjs_require__(7);
			var displaySVG = __w_pdfjs_require__(4);
			var globalScope = sharedUtil.globalScope;
			var deprecated = sharedUtil.deprecated;
			var warn = sharedUtil.warn;
			var LinkTarget = displayDOMUtils.LinkTarget;
			var DEFAULT_LINK_REL = displayDOMUtils.DEFAULT_LINK_REL;
			var isWorker = typeof window === 'undefined';
			if (!globalScope.PDFJS) {
				globalScope.PDFJS = {};
			}
			var PDFJS = globalScope.PDFJS;
			PDFJS.version = '1.7.354';
			PDFJS.build = '0f7548ba';
			PDFJS.pdfBug = false;
			if (PDFJS.verbosity !== undefined) {
				sharedUtil.setVerbosityLevel(PDFJS.verbosity);
			}
			delete PDFJS.verbosity;
			Object.defineProperty(PDFJS, 'verbosity', {
				get: function () {
					return sharedUtil.getVerbosityLevel();
				},
				set: function (level) {
					sharedUtil.setVerbosityLevel(level);
				},
				enumerable: true,
				configurable: true
			});
			PDFJS.VERBOSITY_LEVELS = sharedUtil.VERBOSITY_LEVELS;
			PDFJS.OPS = sharedUtil.OPS;
			PDFJS.UNSUPPORTED_FEATURES = sharedUtil.UNSUPPORTED_FEATURES;
			PDFJS.isValidUrl = displayDOMUtils.isValidUrl;
			PDFJS.shadow = sharedUtil.shadow;
			PDFJS.createBlob = sharedUtil.createBlob;
			PDFJS.createObjectURL = function PDFJS_createObjectURL(data, contentType) {
				return sharedUtil.createObjectURL(data, contentType, PDFJS.disableCreateObjectURL);
			};
			Object.defineProperty(PDFJS, 'isLittleEndian', {
				configurable: true,
				get: function PDFJS_isLittleEndian() {
					var value = sharedUtil.isLittleEndian();
					return sharedUtil.shadow(PDFJS, 'isLittleEndian', value);
				}
			});
			PDFJS.removeNullCharacters = sharedUtil.removeNullCharacters;
			PDFJS.PasswordResponses = sharedUtil.PasswordResponses;
			PDFJS.PasswordException = sharedUtil.PasswordException;
			PDFJS.UnknownErrorException = sharedUtil.UnknownErrorException;
			PDFJS.InvalidPDFException = sharedUtil.InvalidPDFException;
			PDFJS.MissingPDFException = sharedUtil.MissingPDFException;
			PDFJS.UnexpectedResponseException = sharedUtil.UnexpectedResponseException;
			PDFJS.Util = sharedUtil.Util;
			PDFJS.PageViewport = sharedUtil.PageViewport;
			PDFJS.createPromiseCapability = sharedUtil.createPromiseCapability;
			PDFJS.maxImageSize = PDFJS.maxImageSize === undefined ? -1 : PDFJS.maxImageSize;
			PDFJS.cMapUrl = PDFJS.cMapUrl === undefined ? null : PDFJS.cMapUrl;
			PDFJS.cMapPacked = PDFJS.cMapPacked === undefined ? false : PDFJS.cMapPacked;
			PDFJS.disableFontFace = PDFJS.disableFontFace === undefined ? false : PDFJS.disableFontFace;
			PDFJS.imageResourcesPath = PDFJS.imageResourcesPath === undefined ? '' : PDFJS.imageResourcesPath;
			PDFJS.disableWorker = PDFJS.disableWorker === undefined ? false : PDFJS.disableWorker;
			PDFJS.workerSrc = PDFJS.workerSrc === undefined ? null : PDFJS.workerSrc;
			PDFJS.workerPort = PDFJS.workerPort === undefined ? null : PDFJS.workerPort;
			PDFJS.disableRange = PDFJS.disableRange === undefined ? false : PDFJS.disableRange;
			PDFJS.disableStream = PDFJS.disableStream === undefined ? false : PDFJS.disableStream;
			PDFJS.disableAutoFetch = PDFJS.disableAutoFetch === undefined ? false : PDFJS.disableAutoFetch;
			PDFJS.pdfBug = PDFJS.pdfBug === undefined ? false : PDFJS.pdfBug;
			PDFJS.postMessageTransfers = PDFJS.postMessageTransfers === undefined ? true : PDFJS.postMessageTransfers;
			PDFJS.disableCreateObjectURL = PDFJS.disableCreateObjectURL === undefined ? false : PDFJS.disableCreateObjectURL;
			PDFJS.disableWebGL = PDFJS.disableWebGL === undefined ? true : PDFJS.disableWebGL;
			PDFJS.externalLinkTarget = PDFJS.externalLinkTarget === undefined ? LinkTarget.NONE : PDFJS.externalLinkTarget;
			PDFJS.externalLinkRel = PDFJS.externalLinkRel === undefined ? DEFAULT_LINK_REL : PDFJS.externalLinkRel;
			PDFJS.isEvalSupported = PDFJS.isEvalSupported === undefined ? true : PDFJS.isEvalSupported;
			var savedOpenExternalLinksInNewWindow = PDFJS.openExternalLinksInNewWindow;
			delete PDFJS.openExternalLinksInNewWindow;
			Object.defineProperty(PDFJS, 'openExternalLinksInNewWindow', {
				get: function () {
					return PDFJS.externalLinkTarget === LinkTarget.BLANK;
				},
				set: function (value) {
					if (value) {
						deprecated('PDFJS.openExternalLinksInNewWindow, please use ' + '"PDFJS.externalLinkTarget = PDFJS.LinkTarget.BLANK" instead.');
					}
					if (PDFJS.externalLinkTarget !== LinkTarget.NONE) {
						warn('PDFJS.externalLinkTarget is already initialized');
						return;
					}
					PDFJS.externalLinkTarget = value ? LinkTarget.BLANK : LinkTarget.NONE;
				},
				enumerable: true,
				configurable: true
			});
			if (savedOpenExternalLinksInNewWindow) {
				PDFJS.openExternalLinksInNewWindow = savedOpenExternalLinksInNewWindow;
			}
			PDFJS.getDocument = displayAPI.getDocument;
			PDFJS.PDFDataRangeTransport = displayAPI.PDFDataRangeTransport;
			PDFJS.PDFWorker = displayAPI.PDFWorker;
			Object.defineProperty(PDFJS, 'hasCanvasTypedArrays', {
				configurable: true,
				get: function PDFJS_hasCanvasTypedArrays() {
					var value = displayDOMUtils.hasCanvasTypedArrays();
					return sharedUtil.shadow(PDFJS, 'hasCanvasTypedArrays', value);
				}
			});
			PDFJS.CustomStyle = displayDOMUtils.CustomStyle;
			PDFJS.LinkTarget = LinkTarget;
			PDFJS.addLinkAttributes = displayDOMUtils.addLinkAttributes;
			PDFJS.getFilenameFromUrl = displayDOMUtils.getFilenameFromUrl;
			PDFJS.isExternalLinkTargetSet = displayDOMUtils.isExternalLinkTargetSet;
			PDFJS.AnnotationLayer = displayAnnotationLayer.AnnotationLayer;
			PDFJS.renderTextLayer = displayTextLayer.renderTextLayer;
			PDFJS.Metadata = displayMetadata.Metadata;
			PDFJS.SVGGraphics = displaySVG.SVGGraphics;
			PDFJS.UnsupportedManager = displayAPI._UnsupportedManager;
			exports.globalScope = globalScope;
			exports.isWorker = isWorker;
			exports.PDFJS = globalScope.PDFJS;

			/***/
		}),
/* 10 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayDOMUtils = __w_pdfjs_require__(1);
			var displayPatternHelper = __w_pdfjs_require__(12);
			var displayWebGL = __w_pdfjs_require__(8);
			var FONT_IDENTITY_MATRIX = sharedUtil.FONT_IDENTITY_MATRIX;
			var IDENTITY_MATRIX = sharedUtil.IDENTITY_MATRIX;
			var ImageKind = sharedUtil.ImageKind;
			var OPS = sharedUtil.OPS;
			var TextRenderingMode = sharedUtil.TextRenderingMode;
			var Uint32ArrayView = sharedUtil.Uint32ArrayView;
			var Util = sharedUtil.Util;
			var assert = sharedUtil.assert;
			var info = sharedUtil.info;
			var isNum = sharedUtil.isNum;
			var isArray = sharedUtil.isArray;
			var isLittleEndian = sharedUtil.isLittleEndian;
			var error = sharedUtil.error;
			var shadow = sharedUtil.shadow;
			var warn = sharedUtil.warn;
			var TilingPattern = displayPatternHelper.TilingPattern;
			var getShadingPatternFromIR = displayPatternHelper.getShadingPatternFromIR;
			var WebGLUtils = displayWebGL.WebGLUtils;
			var hasCanvasTypedArrays = displayDOMUtils.hasCanvasTypedArrays;
			var MIN_FONT_SIZE = 16;
			var MAX_FONT_SIZE = 100;
			var MAX_GROUP_SIZE = 4096;
			var MIN_WIDTH_FACTOR = 0.65;
			var COMPILE_TYPE3_GLYPHS = true;
			var MAX_SIZE_TO_COMPILE = 1000;
			var FULL_CHUNK_HEIGHT = 16;
			var HasCanvasTypedArraysCached = {
				get value() {
					return shadow(HasCanvasTypedArraysCached, 'value', hasCanvasTypedArrays());
				}
			};
			var IsLittleEndianCached = {
				get value() {
					return shadow(IsLittleEndianCached, 'value', isLittleEndian());
				}
			};
			function addContextCurrentTransform(ctx) {
				if (!ctx.mozCurrentTransform) {
					ctx._originalSave = ctx.save;
					ctx._originalRestore = ctx.restore;
					ctx._originalRotate = ctx.rotate;
					ctx._originalScale = ctx.scale;
					ctx._originalTranslate = ctx.translate;
					ctx._originalTransform = ctx.transform;
					ctx._originalSetTransform = ctx.setTransform;
					ctx._transformMatrix = ctx._transformMatrix || [
						1,
						0,
						0,
						1,
						0,
						0
					];
					ctx._transformStack = [];
					Object.defineProperty(ctx, 'mozCurrentTransform', {
						get: function getCurrentTransform() {
							return this._transformMatrix;
						}
					});
					Object.defineProperty(ctx, 'mozCurrentTransformInverse', {
						get: function getCurrentTransformInverse() {
							var m = this._transformMatrix;
							var a = m[0], b = m[1], c = m[2], d = m[3], e = m[4], f = m[5];
							var ad_bc = a * d - b * c;
							var bc_ad = b * c - a * d;
							return [
								d / ad_bc,
								b / bc_ad,
								c / bc_ad,
								a / ad_bc,
								(d * e - c * f) / bc_ad,
								(b * e - a * f) / ad_bc
							];
						}
					});
					ctx.save = function ctxSave() {
						var old = this._transformMatrix;
						this._transformStack.push(old);
						this._transformMatrix = old.slice(0, 6);
						this._originalSave();
					};
					ctx.restore = function ctxRestore() {
						var prev = this._transformStack.pop();
						if (prev) {
							this._transformMatrix = prev;
							this._originalRestore();
						}
					};
					ctx.translate = function ctxTranslate(x, y) {
						var m = this._transformMatrix;
						m[4] = m[0] * x + m[2] * y + m[4];
						m[5] = m[1] * x + m[3] * y + m[5];
						this._originalTranslate(x, y);
					};
					ctx.scale = function ctxScale(x, y) {
						var m = this._transformMatrix;
						m[0] = m[0] * x;
						m[1] = m[1] * x;
						m[2] = m[2] * y;
						m[3] = m[3] * y;
						this._originalScale(x, y);
					};
					ctx.transform = function ctxTransform(a, b, c, d, e, f) {
						var m = this._transformMatrix;
						this._transformMatrix = [
							m[0] * a + m[2] * b,
							m[1] * a + m[3] * b,
							m[0] * c + m[2] * d,
							m[1] * c + m[3] * d,
							m[0] * e + m[2] * f + m[4],
							m[1] * e + m[3] * f + m[5]
						];
						ctx._originalTransform(a, b, c, d, e, f);
					};
					ctx.setTransform = function ctxSetTransform(a, b, c, d, e, f) {
						this._transformMatrix = [
							a,
							b,
							c,
							d,
							e,
							f
						];
						ctx._originalSetTransform(a, b, c, d, e, f);
					};
					ctx.rotate = function ctxRotate(angle) {
						var cosValue = Math.cos(angle);
						var sinValue = Math.sin(angle);
						var m = this._transformMatrix;
						this._transformMatrix = [
							m[0] * cosValue + m[2] * sinValue,
							m[1] * cosValue + m[3] * sinValue,
							m[0] * -sinValue + m[2] * cosValue,
							m[1] * -sinValue + m[3] * cosValue,
							m[4],
							m[5]
						];
						this._originalRotate(angle);
					};
				}
			}
			var CachedCanvases = function CachedCanvasesClosure() {
				function CachedCanvases(canvasFactory) {
					this.canvasFactory = canvasFactory;
					this.cache = Object.create(null);
				}
				CachedCanvases.prototype = {
					getCanvas: function CachedCanvases_getCanvas(id, width, height, trackTransform) {
						var canvasEntry;
						if (this.cache[id] !== undefined) {
							canvasEntry = this.cache[id];
							this.canvasFactory.reset(canvasEntry, width, height);
							canvasEntry.context.setTransform(1, 0, 0, 1, 0, 0);
						} else {
							canvasEntry = this.canvasFactory.create(width, height);
							this.cache[id] = canvasEntry;
						}
						if (trackTransform) {
							addContextCurrentTransform(canvasEntry.context);
						}
						return canvasEntry;
					},
					clear: function () {
						for (var id in this.cache) {
							var canvasEntry = this.cache[id];
							this.canvasFactory.destroy(canvasEntry);
							delete this.cache[id];
						}
					}
				};
				return CachedCanvases;
			}();
			function compileType3Glyph(imgData) {
				var POINT_TO_PROCESS_LIMIT = 1000;
				var width = imgData.width, height = imgData.height;
				var i, j, j0, width1 = width + 1;
				var points = new Uint8Array(width1 * (height + 1));
				var POINT_TYPES = new Uint8Array([
					0,
					2,
					4,
					0,
					1,
					0,
					5,
					4,
					8,
					10,
					0,
					8,
					0,
					2,
					1,
					0
				]);
				var lineSize = width + 7 & ~7, data0 = imgData.data;
				var data = new Uint8Array(lineSize * height), pos = 0, ii;
				for (i = 0, ii = data0.length; i < ii; i++) {
					var mask = 128, elem = data0[i];
					while (mask > 0) {
						data[pos++] = elem & mask ? 0 : 255;
						mask >>= 1;
					}
				}
				var count = 0;
				pos = 0;
				if (data[pos] !== 0) {
					points[0] = 1;
					++count;
				}
				for (j = 1; j < width; j++) {
					if (data[pos] !== data[pos + 1]) {
						points[j] = data[pos] ? 2 : 1;
						++count;
					}
					pos++;
				}
				if (data[pos] !== 0) {
					points[j] = 2;
					++count;
				}
				for (i = 1; i < height; i++) {
					pos = i * lineSize;
					j0 = i * width1;
					if (data[pos - lineSize] !== data[pos]) {
						points[j0] = data[pos] ? 1 : 8;
						++count;
					}
					var sum = (data[pos] ? 4 : 0) + (data[pos - lineSize] ? 8 : 0);
					for (j = 1; j < width; j++) {
						sum = (sum >> 2) + (data[pos + 1] ? 4 : 0) + (data[pos - lineSize + 1] ? 8 : 0);
						if (POINT_TYPES[sum]) {
							points[j0 + j] = POINT_TYPES[sum];
							++count;
						}
						pos++;
					}
					if (data[pos - lineSize] !== data[pos]) {
						points[j0 + j] = data[pos] ? 2 : 4;
						++count;
					}
					if (count > POINT_TO_PROCESS_LIMIT) {
						return null;
					}
				}
				pos = lineSize * (height - 1);
				j0 = i * width1;
				if (data[pos] !== 0) {
					points[j0] = 8;
					++count;
				}
				for (j = 1; j < width; j++) {
					if (data[pos] !== data[pos + 1]) {
						points[j0 + j] = data[pos] ? 4 : 8;
						++count;
					}
					pos++;
				}
				if (data[pos] !== 0) {
					points[j0 + j] = 4;
					++count;
				}
				if (count > POINT_TO_PROCESS_LIMIT) {
					return null;
				}
				var steps = new Int32Array([
					0,
					width1,
					-1,
					0,
					-width1,
					0,
					0,
					0,
					1
				]);
				var outlines = [];
				for (i = 0; count && i <= height; i++) {
					var p = i * width1;
					var end = p + width;
					while (p < end && !points[p]) {
						p++;
					}
					if (p === end) {
						continue;
					}
					var coords = [
						p % width1,
						i
					];
					var type = points[p], p0 = p, pp;
					do {
						var step = steps[type];
						do {
							p += step;
						} while (!points[p]);
						pp = points[p];
						if (pp !== 5 && pp !== 10) {
							type = pp;
							points[p] = 0;
						} else {
							type = pp & 0x33 * type >> 4;
							points[p] &= type >> 2 | type << 2;
						}
						coords.push(p % width1);
						coords.push(p / width1 | 0);
						--count;
					} while (p0 !== p);
					outlines.push(coords);
					--i;
				}
				var drawOutline = function (c) {
					c.save();
					c.scale(1 / width, -1 / height);
					c.translate(0, -height);
					c.beginPath();
					for (var i = 0, ii = outlines.length; i < ii; i++) {
						var o = outlines[i];
						c.moveTo(o[0], o[1]);
						for (var j = 2, jj = o.length; j < jj; j += 2) {
							c.lineTo(o[j], o[j + 1]);
						}
					}
					c.fill();
					c.beginPath();
					c.restore();
				};
				return drawOutline;
			}
			var CanvasExtraState = function CanvasExtraStateClosure() {
				function CanvasExtraState(old) {
					this.alphaIsShape = false;
					this.fontSize = 0;
					this.fontSizeScale = 1;
					this.textMatrix = IDENTITY_MATRIX;
					this.textMatrixScale = 1;
					this.fontMatrix = FONT_IDENTITY_MATRIX;
					this.leading = 0;
					this.x = 0;
					this.y = 0;
					this.lineX = 0;
					this.lineY = 0;
					this.charSpacing = 0;
					this.wordSpacing = 0;
					this.textHScale = 1;
					this.textRenderingMode = TextRenderingMode.FILL;
					this.textRise = 0;
					this.fillColor = '#000000';
					this.strokeColor = '#000000';
					this.patternFill = false;
					this.fillAlpha = 1;
					this.strokeAlpha = 1;
					this.lineWidth = 1;
					this.activeSMask = null;
					this.resumeSMaskCtx = null;
					this.old = old;
				}
				CanvasExtraState.prototype = {
					clone: function CanvasExtraState_clone() {
						return Object.create(this);
					},
					setCurrentPoint: function CanvasExtraState_setCurrentPoint(x, y) {
						this.x = x;
						this.y = y;
					}
				};
				return CanvasExtraState;
			}();
			var CanvasGraphics = function CanvasGraphicsClosure() {
				var EXECUTION_TIME = 15;
				var EXECUTION_STEPS = 10;
				function CanvasGraphics(canvasCtx, commonObjs, objs, canvasFactory, imageLayer) {
					this.ctx = canvasCtx;
					this.current = new CanvasExtraState();
					this.stateStack = [];
					this.pendingClip = null;
					this.pendingEOFill = false;
					this.res = null;
					this.xobjs = null;
					this.commonObjs = commonObjs;
					this.objs = objs;
					this.canvasFactory = canvasFactory;
					this.imageLayer = imageLayer;
					this.groupStack = [];
					this.processingType3 = null;
					this.baseTransform = null;
					this.baseTransformStack = [];
					this.groupLevel = 0;
					this.smaskStack = [];
					this.smaskCounter = 0;
					this.tempSMask = null;
					this.cachedCanvases = new CachedCanvases(this.canvasFactory);
					if (canvasCtx) {
						addContextCurrentTransform(canvasCtx);
					}
					this.cachedGetSinglePixelWidth = null;
				}
				function putBinaryImageData(ctx, imgData) {
					if (typeof ImageData !== 'undefined' && imgData instanceof ImageData) {
						ctx.putImageData(imgData, 0, 0);
						return;
					}
					var height = imgData.height, width = imgData.width;
					var partialChunkHeight = height % FULL_CHUNK_HEIGHT;
					var fullChunks = (height - partialChunkHeight) / FULL_CHUNK_HEIGHT;
					var totalChunks = partialChunkHeight === 0 ? fullChunks : fullChunks + 1;
					var chunkImgData = ctx.createImageData(width, FULL_CHUNK_HEIGHT);
					var srcPos = 0, destPos;
					var src = imgData.data;
					var dest = chunkImgData.data;
					var i, j, thisChunkHeight, elemsInThisChunk;
					if (imgData.kind === ImageKind.GRAYSCALE_1BPP) {
						var srcLength = src.byteLength;
						var dest32 = HasCanvasTypedArraysCached.value ? new Uint32Array(dest.buffer) : new Uint32ArrayView(dest);
						var dest32DataLength = dest32.length;
						var fullSrcDiff = width + 7 >> 3;
						var white = 0xFFFFFFFF;
						var black = IsLittleEndianCached.value || !HasCanvasTypedArraysCached.value ? 0xFF000000 : 0x000000FF;
						for (i = 0; i < totalChunks; i++) {
							thisChunkHeight = i < fullChunks ? FULL_CHUNK_HEIGHT : partialChunkHeight;
							destPos = 0;
							for (j = 0; j < thisChunkHeight; j++) {
								var srcDiff = srcLength - srcPos;
								var k = 0;
								var kEnd = srcDiff > fullSrcDiff ? width : srcDiff * 8 - 7;
								var kEndUnrolled = kEnd & ~7;
								var mask = 0;
								var srcByte = 0;
								for (; k < kEndUnrolled; k += 8) {
									srcByte = src[srcPos++];
									dest32[destPos++] = srcByte & 128 ? white : black;
									dest32[destPos++] = srcByte & 64 ? white : black;
									dest32[destPos++] = srcByte & 32 ? white : black;
									dest32[destPos++] = srcByte & 16 ? white : black;
									dest32[destPos++] = srcByte & 8 ? white : black;
									dest32[destPos++] = srcByte & 4 ? white : black;
									dest32[destPos++] = srcByte & 2 ? white : black;
									dest32[destPos++] = srcByte & 1 ? white : black;
								}
								for (; k < kEnd; k++) {
									if (mask === 0) {
										srcByte = src[srcPos++];
										mask = 128;
									}
									dest32[destPos++] = srcByte & mask ? white : black;
									mask >>= 1;
								}
							}
							while (destPos < dest32DataLength) {
								dest32[destPos++] = 0;
							}
							ctx.putImageData(chunkImgData, 0, i * FULL_CHUNK_HEIGHT);
						}
					} else if (imgData.kind === ImageKind.RGBA_32BPP) {
						j = 0;
						elemsInThisChunk = width * FULL_CHUNK_HEIGHT * 4;
						for (i = 0; i < fullChunks; i++) {
							dest.set(src.subarray(srcPos, srcPos + elemsInThisChunk));
							srcPos += elemsInThisChunk;
							ctx.putImageData(chunkImgData, 0, j);
							j += FULL_CHUNK_HEIGHT;
						}
						if (i < totalChunks) {
							elemsInThisChunk = width * partialChunkHeight * 4;
							dest.set(src.subarray(srcPos, srcPos + elemsInThisChunk));
							ctx.putImageData(chunkImgData, 0, j);
						}
					} else if (imgData.kind === ImageKind.RGB_24BPP) {
						thisChunkHeight = FULL_CHUNK_HEIGHT;
						elemsInThisChunk = width * thisChunkHeight;
						for (i = 0; i < totalChunks; i++) {
							if (i >= fullChunks) {
								thisChunkHeight = partialChunkHeight;
								elemsInThisChunk = width * thisChunkHeight;
							}
							destPos = 0;
							for (j = elemsInThisChunk; j--;) {
								dest[destPos++] = src[srcPos++];
								dest[destPos++] = src[srcPos++];
								dest[destPos++] = src[srcPos++];
								dest[destPos++] = 255;
							}
							ctx.putImageData(chunkImgData, 0, i * FULL_CHUNK_HEIGHT);
						}
					} else {
						error('bad image kind: ' + imgData.kind);
					}
				}
				function putBinaryImageMask(ctx, imgData) {
					var height = imgData.height, width = imgData.width;
					var partialChunkHeight = height % FULL_CHUNK_HEIGHT;
					var fullChunks = (height - partialChunkHeight) / FULL_CHUNK_HEIGHT;
					var totalChunks = partialChunkHeight === 0 ? fullChunks : fullChunks + 1;
					var chunkImgData = ctx.createImageData(width, FULL_CHUNK_HEIGHT);
					var srcPos = 0;
					var src = imgData.data;
					var dest = chunkImgData.data;
					for (var i = 0; i < totalChunks; i++) {
						var thisChunkHeight = i < fullChunks ? FULL_CHUNK_HEIGHT : partialChunkHeight;
						var destPos = 3;
						for (var j = 0; j < thisChunkHeight; j++) {
							var mask = 0;
							for (var k = 0; k < width; k++) {
								if (!mask) {
									var elem = src[srcPos++];
									mask = 128;
								}
								dest[destPos] = elem & mask ? 0 : 255;
								destPos += 4;
								mask >>= 1;
							}
						}
						ctx.putImageData(chunkImgData, 0, i * FULL_CHUNK_HEIGHT);
					}
				}
				function copyCtxState(sourceCtx, destCtx) {
					var properties = [
						'strokeStyle',
						'fillStyle',
						'fillRule',
						'globalAlpha',
						'lineWidth',
						'lineCap',
						'lineJoin',
						'miterLimit',
						'globalCompositeOperation',
						'font'
					];
					for (var i = 0, ii = properties.length; i < ii; i++) {
						var property = properties[i];
						if (sourceCtx[property] !== undefined) {
							destCtx[property] = sourceCtx[property];
						}
					}
					if (sourceCtx.setLineDash !== undefined) {
						destCtx.setLineDash(sourceCtx.getLineDash());
						destCtx.lineDashOffset = sourceCtx.lineDashOffset;
					}
				}
				function composeSMaskBackdrop(bytes, r0, g0, b0) {
					var length = bytes.length;
					for (var i = 3; i < length; i += 4) {
						var alpha = bytes[i];
						if (alpha === 0) {
							bytes[i - 3] = r0;
							bytes[i - 2] = g0;
							bytes[i - 1] = b0;
						} else if (alpha < 255) {
							var alpha_ = 255 - alpha;
							bytes[i - 3] = bytes[i - 3] * alpha + r0 * alpha_ >> 8;
							bytes[i - 2] = bytes[i - 2] * alpha + g0 * alpha_ >> 8;
							bytes[i - 1] = bytes[i - 1] * alpha + b0 * alpha_ >> 8;
						}
					}
				}
				function composeSMaskAlpha(maskData, layerData, transferMap) {
					var length = maskData.length;
					var scale = 1 / 255;
					for (var i = 3; i < length; i += 4) {
						var alpha = transferMap ? transferMap[maskData[i]] : maskData[i];
						layerData[i] = layerData[i] * alpha * scale | 0;
					}
				}
				function composeSMaskLuminosity(maskData, layerData, transferMap) {
					var length = maskData.length;
					for (var i = 3; i < length; i += 4) {
						var y = maskData[i - 3] * 77 + maskData[i - 2] * 152 + maskData[i - 1] * 28;
						layerData[i] = transferMap ? layerData[i] * transferMap[y >> 8] >> 8 : layerData[i] * y >> 16;
					}
				}
				function genericComposeSMask(maskCtx, layerCtx, width, height, subtype, backdrop, transferMap) {
					var hasBackdrop = !!backdrop;
					var r0 = hasBackdrop ? backdrop[0] : 0;
					var g0 = hasBackdrop ? backdrop[1] : 0;
					var b0 = hasBackdrop ? backdrop[2] : 0;
					var composeFn;
					if (subtype === 'Luminosity') {
						composeFn = composeSMaskLuminosity;
					} else {
						composeFn = composeSMaskAlpha;
					}
					var PIXELS_TO_PROCESS = 1048576;
					var chunkSize = Math.min(height, Math.ceil(PIXELS_TO_PROCESS / width));
					for (var row = 0; row < height; row += chunkSize) {
						var chunkHeight = Math.min(chunkSize, height - row);
						var maskData = maskCtx.getImageData(0, row, width, chunkHeight);
						var layerData = layerCtx.getImageData(0, row, width, chunkHeight);
						if (hasBackdrop) {
							composeSMaskBackdrop(maskData.data, r0, g0, b0);
						}
						composeFn(maskData.data, layerData.data, transferMap);
						maskCtx.putImageData(layerData, 0, row);
					}
				}
				function composeSMask(ctx, smask, layerCtx) {
					var mask = smask.canvas;
					var maskCtx = smask.context;
					ctx.setTransform(smask.scaleX, 0, 0, smask.scaleY, smask.offsetX, smask.offsetY);
					var backdrop = smask.backdrop || null;
					if (!smask.transferMap && WebGLUtils.isEnabled) {
						var composed = WebGLUtils.composeSMask(layerCtx.canvas, mask, {
							subtype: smask.subtype,
							backdrop: backdrop
						});
						ctx.setTransform(1, 0, 0, 1, 0, 0);
						ctx.drawImage(composed, smask.offsetX, smask.offsetY);
						return;
					}
					genericComposeSMask(maskCtx, layerCtx, mask.width, mask.height, smask.subtype, backdrop, smask.transferMap);
					ctx.drawImage(mask, 0, 0);
				}
				var LINE_CAP_STYLES = [
					'butt',
					'round',
					'square'
				];
				var LINE_JOIN_STYLES = [
					'miter',
					'round',
					'bevel'
				];
				var NORMAL_CLIP = {};
				var EO_CLIP = {};
				CanvasGraphics.prototype = {
					beginDrawing: function CanvasGraphics_beginDrawing(transform, viewport, transparency) {
						var width = this.ctx.canvas.width;
						var height = this.ctx.canvas.height;
						this.ctx.save();
						this.ctx.fillStyle = 'rgb(255, 255, 255)';
						this.ctx.fillRect(0, 0, width, height);
						this.ctx.restore();
						if (transparency) {
							var transparentCanvas = this.cachedCanvases.getCanvas('transparent', width, height, true);
							this.compositeCtx = this.ctx;
							this.transparentCanvas = transparentCanvas.canvas;
							this.ctx = transparentCanvas.context;
							this.ctx.save();
							this.ctx.transform.apply(this.ctx, this.compositeCtx.mozCurrentTransform);
						}
						this.ctx.save();
						if (transform) {
							this.ctx.transform.apply(this.ctx, transform);
						}
						this.ctx.transform.apply(this.ctx, viewport.transform);
						this.baseTransform = this.ctx.mozCurrentTransform.slice();
						if (this.imageLayer) {
							this.imageLayer.beginLayout();
						}
					},
					executeOperatorList: function CanvasGraphics_executeOperatorList(operatorList, executionStartIdx, continueCallback, stepper) {
						var argsArray = operatorList.argsArray;
						var fnArray = operatorList.fnArray;
						var i = executionStartIdx || 0;
						var argsArrayLen = argsArray.length;
						if (argsArrayLen === i) {
							return i;
						}
						var chunkOperations = argsArrayLen - i > EXECUTION_STEPS && typeof continueCallback === 'function';
						var endTime = chunkOperations ? Date.now() + EXECUTION_TIME : 0;
						var steps = 0;
						var commonObjs = this.commonObjs;
						var objs = this.objs;
						var fnId;
						while (true) {
							if (stepper !== undefined && i === stepper.nextBreakPoint) {
								stepper.breakIt(i, continueCallback);
								return i;
							}
							fnId = fnArray[i];
							if (fnId !== OPS.dependency) {
								this[fnId].apply(this, argsArray[i]);
							} else {
								var deps = argsArray[i];
								for (var n = 0, nn = deps.length; n < nn; n++) {
									var depObjId = deps[n];
									var common = depObjId[0] === 'g' && depObjId[1] === '_';
									var objsPool = common ? commonObjs : objs;
									if (!objsPool.isResolved(depObjId)) {
										objsPool.get(depObjId, continueCallback);
										return i;
									}
								}
							}
							i++;
							if (i === argsArrayLen) {
								return i;
							}
							if (chunkOperations && ++steps > EXECUTION_STEPS) {
								if (Date.now() > endTime) {
									continueCallback();
									return i;
								}
								steps = 0;
							}
						}
					},
					endDrawing: function CanvasGraphics_endDrawing() {
						if (this.current.activeSMask !== null) {
							this.endSMaskGroup();
						}
						this.ctx.restore();
						if (this.transparentCanvas) {
							this.ctx = this.compositeCtx;
							this.ctx.save();
							this.ctx.setTransform(1, 0, 0, 1, 0, 0);
							this.ctx.drawImage(this.transparentCanvas, 0, 0);
							this.ctx.restore();
							this.transparentCanvas = null;
						}
						this.cachedCanvases.clear();
						WebGLUtils.clear();
						if (this.imageLayer) {
							this.imageLayer.endLayout();
						}
					},
					setLineWidth: function CanvasGraphics_setLineWidth(width) {
						this.current.lineWidth = width;
						this.ctx.lineWidth = width;
					},
					setLineCap: function CanvasGraphics_setLineCap(style) {
						this.ctx.lineCap = LINE_CAP_STYLES[style];
					},
					setLineJoin: function CanvasGraphics_setLineJoin(style) {
						this.ctx.lineJoin = LINE_JOIN_STYLES[style];
					},
					setMiterLimit: function CanvasGraphics_setMiterLimit(limit) {
						this.ctx.miterLimit = limit;
					},
					setDash: function CanvasGraphics_setDash(dashArray, dashPhase) {
						var ctx = this.ctx;
						if (ctx.setLineDash !== undefined) {
							ctx.setLineDash(dashArray);
							ctx.lineDashOffset = dashPhase;
						}
					},
					setRenderingIntent: function CanvasGraphics_setRenderingIntent(intent) {
					},
					setFlatness: function CanvasGraphics_setFlatness(flatness) {
					},
					setGState: function CanvasGraphics_setGState(states) {
						for (var i = 0, ii = states.length; i < ii; i++) {
							var state = states[i];
							var key = state[0];
							var value = state[1];
							switch (key) {
								case 'LW':
									this.setLineWidth(value);
									break;
								case 'LC':
									this.setLineCap(value);
									break;
								case 'LJ':
									this.setLineJoin(value);
									break;
								case 'ML':
									this.setMiterLimit(value);
									break;
								case 'D':
									this.setDash(value[0], value[1]);
									break;
								case 'RI':
									this.setRenderingIntent(value);
									break;
								case 'FL':
									this.setFlatness(value);
									break;
								case 'Font':
									this.setFont(value[0], value[1]);
									break;
								case 'CA':
									this.current.strokeAlpha = state[1];
									break;
								case 'ca':
									this.current.fillAlpha = state[1];
									this.ctx.globalAlpha = state[1];
									break;
								case 'BM':
									if (value && value.name && value.name !== 'Normal') {
										var mode = value.name.replace(/([A-Z])/g, function (c) {
											return '-' + c.toLowerCase();
										}).substring(1);
										this.ctx.globalCompositeOperation = mode;
										if (this.ctx.globalCompositeOperation !== mode) {
											warn('globalCompositeOperation "' + mode + '" is not supported');
										}
									} else {
										this.ctx.globalCompositeOperation = 'source-over';
									}
									break;
								case 'SMask':
									if (this.current.activeSMask) {
										if (this.stateStack.length > 0 && this.stateStack[this.stateStack.length - 1].activeSMask === this.current.activeSMask) {
											this.suspendSMaskGroup();
										} else {
											this.endSMaskGroup();
										}
									}
									this.current.activeSMask = value ? this.tempSMask : null;
									if (this.current.activeSMask) {
										this.beginSMaskGroup();
									}
									this.tempSMask = null;
									break;
							}
						}
					},
					beginSMaskGroup: function CanvasGraphics_beginSMaskGroup() {
						var activeSMask = this.current.activeSMask;
						var drawnWidth = activeSMask.canvas.width;
						var drawnHeight = activeSMask.canvas.height;
						var cacheId = 'smaskGroupAt' + this.groupLevel;
						var scratchCanvas = this.cachedCanvases.getCanvas(cacheId, drawnWidth, drawnHeight, true);
						var currentCtx = this.ctx;
						var currentTransform = currentCtx.mozCurrentTransform;
						this.ctx.save();
						var groupCtx = scratchCanvas.context;
						groupCtx.scale(1 / activeSMask.scaleX, 1 / activeSMask.scaleY);
						groupCtx.translate(-activeSMask.offsetX, -activeSMask.offsetY);
						groupCtx.transform.apply(groupCtx, currentTransform);
						activeSMask.startTransformInverse = groupCtx.mozCurrentTransformInverse;
						copyCtxState(currentCtx, groupCtx);
						this.ctx = groupCtx;
						this.setGState([
							[
								'BM',
								'Normal'
							],
							[
								'ca',
								1
							],
							[
								'CA',
								1
							]
						]);
						this.groupStack.push(currentCtx);
						this.groupLevel++;
					},
					suspendSMaskGroup: function CanvasGraphics_endSMaskGroup() {
						var groupCtx = this.ctx;
						this.groupLevel--;
						this.ctx = this.groupStack.pop();
						composeSMask(this.ctx, this.current.activeSMask, groupCtx);
						this.ctx.restore();
						this.ctx.save();
						copyCtxState(groupCtx, this.ctx);
						this.current.resumeSMaskCtx = groupCtx;
						var deltaTransform = Util.transform(this.current.activeSMask.startTransformInverse, groupCtx.mozCurrentTransform);
						this.ctx.transform.apply(this.ctx, deltaTransform);
						groupCtx.save();
						groupCtx.setTransform(1, 0, 0, 1, 0, 0);
						groupCtx.clearRect(0, 0, groupCtx.canvas.width, groupCtx.canvas.height);
						groupCtx.restore();
					},
					resumeSMaskGroup: function CanvasGraphics_endSMaskGroup() {
						var groupCtx = this.current.resumeSMaskCtx;
						var currentCtx = this.ctx;
						this.ctx = groupCtx;
						this.groupStack.push(currentCtx);
						this.groupLevel++;
					},
					endSMaskGroup: function CanvasGraphics_endSMaskGroup() {
						var groupCtx = this.ctx;
						this.groupLevel--;
						this.ctx = this.groupStack.pop();
						composeSMask(this.ctx, this.current.activeSMask, groupCtx);
						this.ctx.restore();
						copyCtxState(groupCtx, this.ctx);
						var deltaTransform = Util.transform(this.current.activeSMask.startTransformInverse, groupCtx.mozCurrentTransform);
						this.ctx.transform.apply(this.ctx, deltaTransform);
					},
					save: function CanvasGraphics_save() {
						this.ctx.save();
						var old = this.current;
						this.stateStack.push(old);
						this.current = old.clone();
						this.current.resumeSMaskCtx = null;
					},
					restore: function CanvasGraphics_restore() {
						if (this.current.resumeSMaskCtx) {
							this.resumeSMaskGroup();
						}
						if (this.current.activeSMask !== null && (this.stateStack.length === 0 || this.stateStack[this.stateStack.length - 1].activeSMask !== this.current.activeSMask)) {
							this.endSMaskGroup();
						}
						if (this.stateStack.length !== 0) {
							this.current = this.stateStack.pop();
							this.ctx.restore();
							this.pendingClip = null;
							this.cachedGetSinglePixelWidth = null;
						}
					},
					transform: function CanvasGraphics_transform(a, b, c, d, e, f) {
						this.ctx.transform(a, b, c, d, e, f);
						this.cachedGetSinglePixelWidth = null;
					},
					constructPath: function CanvasGraphics_constructPath(ops, args) {
						var ctx = this.ctx;
						var current = this.current;
						var x = current.x, y = current.y;
						for (var i = 0, j = 0, ii = ops.length; i < ii; i++) {
							switch (ops[i] | 0) {
								case OPS.rectangle:
									x = args[j++];
									y = args[j++];
									var width = args[j++];
									var height = args[j++];
									if (width === 0) {
										width = this.getSinglePixelWidth();
									}
									if (height === 0) {
										height = this.getSinglePixelWidth();
									}
									var xw = x + width;
									var yh = y + height;
									this.ctx.moveTo(x, y);
									this.ctx.lineTo(xw, y);
									this.ctx.lineTo(xw, yh);
									this.ctx.lineTo(x, yh);
									this.ctx.lineTo(x, y);
									this.ctx.closePath();
									break;
								case OPS.moveTo:
									x = args[j++];
									y = args[j++];
									ctx.moveTo(x, y);
									break;
								case OPS.lineTo:
									x = args[j++];
									y = args[j++];
									ctx.lineTo(x, y);
									break;
								case OPS.curveTo:
									x = args[j + 4];
									y = args[j + 5];
									ctx.bezierCurveTo(args[j], args[j + 1], args[j + 2], args[j + 3], x, y);
									j += 6;
									break;
								case OPS.curveTo2:
									ctx.bezierCurveTo(x, y, args[j], args[j + 1], args[j + 2], args[j + 3]);
									x = args[j + 2];
									y = args[j + 3];
									j += 4;
									break;
								case OPS.curveTo3:
									x = args[j + 2];
									y = args[j + 3];
									ctx.bezierCurveTo(args[j], args[j + 1], x, y, x, y);
									j += 4;
									break;
								case OPS.closePath:
									ctx.closePath();
									break;
							}
						}
						current.setCurrentPoint(x, y);
					},
					closePath: function CanvasGraphics_closePath() {
						this.ctx.closePath();
					},
					stroke: function CanvasGraphics_stroke(consumePath) {
						consumePath = typeof consumePath !== 'undefined' ? consumePath : true;
						var ctx = this.ctx;
						var strokeColor = this.current.strokeColor;
						ctx.lineWidth = Math.max(this.getSinglePixelWidth() * MIN_WIDTH_FACTOR, this.current.lineWidth);
						ctx.globalAlpha = this.current.strokeAlpha;
						if (strokeColor && strokeColor.hasOwnProperty('type') && strokeColor.type === 'Pattern') {
							ctx.save();
							ctx.strokeStyle = strokeColor.getPattern(ctx, this);
							ctx.stroke();
							ctx.restore();
						} else {
							ctx.stroke();
						}
						if (consumePath) {
							this.consumePath();
						}
						ctx.globalAlpha = this.current.fillAlpha;
					},
					closeStroke: function CanvasGraphics_closeStroke() {
						this.closePath();
						this.stroke();
					},
					fill: function CanvasGraphics_fill(consumePath) {
						consumePath = typeof consumePath !== 'undefined' ? consumePath : true;
						var ctx = this.ctx;
						var fillColor = this.current.fillColor;
						var isPatternFill = this.current.patternFill;
						var needRestore = false;
						if (isPatternFill) {
							ctx.save();
							if (this.baseTransform) {
								ctx.setTransform.apply(ctx, this.baseTransform);
							}
							ctx.fillStyle = fillColor.getPattern(ctx, this);
							needRestore = true;
						}
						if (this.pendingEOFill) {
							ctx.fill('evenodd');
							this.pendingEOFill = false;
						} else {
							ctx.fill();
						}
						if (needRestore) {
							ctx.restore();
						}
						if (consumePath) {
							this.consumePath();
						}
					},
					eoFill: function CanvasGraphics_eoFill() {
						this.pendingEOFill = true;
						this.fill();
					},
					fillStroke: function CanvasGraphics_fillStroke() {
						this.fill(false);
						this.stroke(false);
						this.consumePath();
					},
					eoFillStroke: function CanvasGraphics_eoFillStroke() {
						this.pendingEOFill = true;
						this.fillStroke();
					},
					closeFillStroke: function CanvasGraphics_closeFillStroke() {
						this.closePath();
						this.fillStroke();
					},
					closeEOFillStroke: function CanvasGraphics_closeEOFillStroke() {
						this.pendingEOFill = true;
						this.closePath();
						this.fillStroke();
					},
					endPath: function CanvasGraphics_endPath() {
						this.consumePath();
					},
					clip: function CanvasGraphics_clip() {
						this.pendingClip = NORMAL_CLIP;
					},
					eoClip: function CanvasGraphics_eoClip() {
						this.pendingClip = EO_CLIP;
					},
					beginText: function CanvasGraphics_beginText() {
						this.current.textMatrix = IDENTITY_MATRIX;
						this.current.textMatrixScale = 1;
						this.current.x = this.current.lineX = 0;
						this.current.y = this.current.lineY = 0;
					},
					endText: function CanvasGraphics_endText() {
						var paths = this.pendingTextPaths;
						var ctx = this.ctx;
						if (paths === undefined) {
							ctx.beginPath();
							return;
						}
						ctx.save();
						ctx.beginPath();
						for (var i = 0; i < paths.length; i++) {
							var path = paths[i];
							ctx.setTransform.apply(ctx, path.transform);
							ctx.translate(path.x, path.y);
							path.addToPath(ctx, path.fontSize);
						}
						ctx.restore();
						ctx.clip();
						ctx.beginPath();
						delete this.pendingTextPaths;
					},
					setCharSpacing: function CanvasGraphics_setCharSpacing(spacing) {
						this.current.charSpacing = spacing;
					},
					setWordSpacing: function CanvasGraphics_setWordSpacing(spacing) {
						this.current.wordSpacing = spacing;
					},
					setHScale: function CanvasGraphics_setHScale(scale) {
						this.current.textHScale = scale / 100;
					},
					setLeading: function CanvasGraphics_setLeading(leading) {
						this.current.leading = -leading;
					},
					setFont: function CanvasGraphics_setFont(fontRefName, size) {
						var fontObj = this.commonObjs.get(fontRefName);
						var current = this.current;
						if (!fontObj) {
							error('Can\'t find font for ' + fontRefName);
						}
						current.fontMatrix = fontObj.fontMatrix ? fontObj.fontMatrix : FONT_IDENTITY_MATRIX;
						if (current.fontMatrix[0] === 0 || current.fontMatrix[3] === 0) {
							warn('Invalid font matrix for font ' + fontRefName);
						}
						if (size < 0) {
							size = -size;
							current.fontDirection = -1;
						} else {
							current.fontDirection = 1;
						}
						this.current.font = fontObj;
						this.current.fontSize = size;
						if (fontObj.isType3Font) {
							return;
						}
						var name = fontObj.loadedName || 'sans-serif';
						var bold = fontObj.black ? '900' : fontObj.bold ? 'bold' : 'normal';
						var italic = fontObj.italic ? 'italic' : 'normal';
						var typeface = '"' + name + '", ' + fontObj.fallbackName;
						var browserFontSize = size < MIN_FONT_SIZE ? MIN_FONT_SIZE : size > MAX_FONT_SIZE ? MAX_FONT_SIZE : size;
						this.current.fontSizeScale = size / browserFontSize;
						var rule = italic + ' ' + bold + ' ' + browserFontSize + 'px ' + typeface;
						this.ctx.font = rule;
					},
					setTextRenderingMode: function CanvasGraphics_setTextRenderingMode(mode) {
						this.current.textRenderingMode = mode;
					},
					setTextRise: function CanvasGraphics_setTextRise(rise) {
						this.current.textRise = rise;
					},
					moveText: function CanvasGraphics_moveText(x, y) {
						this.current.x = this.current.lineX += x;
						this.current.y = this.current.lineY += y;
					},
					setLeadingMoveText: function CanvasGraphics_setLeadingMoveText(x, y) {
						this.setLeading(-y);
						this.moveText(x, y);
					},
					setTextMatrix: function CanvasGraphics_setTextMatrix(a, b, c, d, e, f) {
						this.current.textMatrix = [
							a,
							b,
							c,
							d,
							e,
							f
						];
						this.current.textMatrixScale = Math.sqrt(a * a + b * b);
						this.current.x = this.current.lineX = 0;
						this.current.y = this.current.lineY = 0;
					},
					nextLine: function CanvasGraphics_nextLine() {
						this.moveText(0, this.current.leading);
					},
					paintChar: function CanvasGraphics_paintChar(character, x, y) {
						var ctx = this.ctx;
						var current = this.current;
						var font = current.font;
						var textRenderingMode = current.textRenderingMode;
						var fontSize = current.fontSize / current.fontSizeScale;
						var fillStrokeMode = textRenderingMode & TextRenderingMode.FILL_STROKE_MASK;
						var isAddToPathSet = !!(textRenderingMode & TextRenderingMode.ADD_TO_PATH_FLAG);
						var addToPath;
						if (font.disableFontFace || isAddToPathSet) {
							addToPath = font.getPathGenerator(this.commonObjs, character);
						}
						if (font.disableFontFace) {
							ctx.save();
							ctx.translate(x, y);
							ctx.beginPath();
							addToPath(ctx, fontSize);
							if (fillStrokeMode === TextRenderingMode.FILL || fillStrokeMode === TextRenderingMode.FILL_STROKE) {
								ctx.fill();
							}
							if (fillStrokeMode === TextRenderingMode.STROKE || fillStrokeMode === TextRenderingMode.FILL_STROKE) {
								ctx.stroke();
							}
							ctx.restore();
						} else {
							if (fillStrokeMode === TextRenderingMode.FILL || fillStrokeMode === TextRenderingMode.FILL_STROKE) {
								ctx.fillText(character, x, y);
							}
							if (fillStrokeMode === TextRenderingMode.STROKE || fillStrokeMode === TextRenderingMode.FILL_STROKE) {
								ctx.strokeText(character, x, y);
							}
						}
						if (isAddToPathSet) {
							var paths = this.pendingTextPaths || (this.pendingTextPaths = []);
							paths.push({
								transform: ctx.mozCurrentTransform,
								x: x,
								y: y,
								fontSize: fontSize,
								addToPath: addToPath
							});
						}
					},
					get isFontSubpixelAAEnabled() {
						var ctx = this.canvasFactory.create(10, 10).context;
						ctx.scale(1.5, 1);
						ctx.fillText('I', 0, 10);
						var data = ctx.getImageData(0, 0, 10, 10).data;
						var enabled = false;
						for (var i = 3; i < data.length; i += 4) {
							if (data[i] > 0 && data[i] < 255) {
								enabled = true;
								break;
							}
						}
						return shadow(this, 'isFontSubpixelAAEnabled', enabled);
					},
					showText: function CanvasGraphics_showText(glyphs) {
						var current = this.current;
						var font = current.font;
						if (font.isType3Font) {
							return this.showType3Text(glyphs);
						}
						var fontSize = current.fontSize;
						if (fontSize === 0) {
							return;
						}
						var ctx = this.ctx;
						var fontSizeScale = current.fontSizeScale;
						var charSpacing = current.charSpacing;
						var wordSpacing = current.wordSpacing;
						var fontDirection = current.fontDirection;
						var textHScale = current.textHScale * fontDirection;
						var glyphsLength = glyphs.length;
						var vertical = font.vertical;
						var spacingDir = vertical ? 1 : -1;
						var defaultVMetrics = font.defaultVMetrics;
						var widthAdvanceScale = fontSize * current.fontMatrix[0];
						var simpleFillText = current.textRenderingMode === TextRenderingMode.FILL && !font.disableFontFace;
						ctx.save();
						ctx.transform.apply(ctx, current.textMatrix);
						ctx.translate(current.x, current.y + current.textRise);
						if (current.patternFill) {
							ctx.fillStyle = current.fillColor.getPattern(ctx, this);
						}
						if (fontDirection > 0) {
							ctx.scale(textHScale, -1);
						} else {
							ctx.scale(textHScale, 1);
						}
						var lineWidth = current.lineWidth;
						var scale = current.textMatrixScale;
						if (scale === 0 || lineWidth === 0) {
							var fillStrokeMode = current.textRenderingMode & TextRenderingMode.FILL_STROKE_MASK;
							if (fillStrokeMode === TextRenderingMode.STROKE || fillStrokeMode === TextRenderingMode.FILL_STROKE) {
								this.cachedGetSinglePixelWidth = null;
								lineWidth = this.getSinglePixelWidth() * MIN_WIDTH_FACTOR;
							}
						} else {
							lineWidth /= scale;
						}
						if (fontSizeScale !== 1.0) {
							ctx.scale(fontSizeScale, fontSizeScale);
							lineWidth /= fontSizeScale;
						}
						ctx.lineWidth = lineWidth;
						var x = 0, i;
						for (i = 0; i < glyphsLength; ++i) {
							var glyph = glyphs[i];
							if (isNum(glyph)) {
								x += spacingDir * glyph * fontSize / 1000;
								continue;
							}
							var restoreNeeded = false;
							var spacing = (glyph.isSpace ? wordSpacing : 0) + charSpacing;
							var character = glyph.fontChar;
							var accent = glyph.accent;
							var scaledX, scaledY, scaledAccentX, scaledAccentY;
							var width = glyph.width;
							if (vertical) {
								var vmetric, vx, vy;
								vmetric = glyph.vmetric || defaultVMetrics;
								vx = glyph.vmetric ? vmetric[1] : width * 0.5;
								vx = -vx * widthAdvanceScale;
								vy = vmetric[2] * widthAdvanceScale;
								width = vmetric ? -vmetric[0] : width;
								scaledX = vx / fontSizeScale;
								scaledY = (x + vy) / fontSizeScale;
							} else {
								scaledX = x / fontSizeScale;
								scaledY = 0;
							}
							if (font.remeasure && width > 0) {
								var measuredWidth = ctx.measureText(character).width * 1000 / fontSize * fontSizeScale;
								if (width < measuredWidth && this.isFontSubpixelAAEnabled) {
									var characterScaleX = width / measuredWidth;
									restoreNeeded = true;
									ctx.save();
									ctx.scale(characterScaleX, 1);
									scaledX /= characterScaleX;
								} else if (width !== measuredWidth) {
									scaledX += (width - measuredWidth) / 2000 * fontSize / fontSizeScale;
								}
							}
							if (glyph.isInFont || font.missingFile) {
								if (simpleFillText && !accent) {
									ctx.fillText(character, scaledX, scaledY);
								} else {
									this.paintChar(character, scaledX, scaledY);
									if (accent) {
										scaledAccentX = scaledX + accent.offset.x / fontSizeScale;
										scaledAccentY = scaledY - accent.offset.y / fontSizeScale;
										this.paintChar(accent.fontChar, scaledAccentX, scaledAccentY);
									}
								}
							}
							var charWidth = width * widthAdvanceScale + spacing * fontDirection;
							x += charWidth;
							if (restoreNeeded) {
								ctx.restore();
							}
						}
						if (vertical) {
							current.y -= x * textHScale;
						} else {
							current.x += x * textHScale;
						}
						ctx.restore();
					},
					showType3Text: function CanvasGraphics_showType3Text(glyphs) {
						var ctx = this.ctx;
						var current = this.current;
						var font = current.font;
						var fontSize = current.fontSize;
						var fontDirection = current.fontDirection;
						var spacingDir = font.vertical ? 1 : -1;
						var charSpacing = current.charSpacing;
						var wordSpacing = current.wordSpacing;
						var textHScale = current.textHScale * fontDirection;
						var fontMatrix = current.fontMatrix || FONT_IDENTITY_MATRIX;
						var glyphsLength = glyphs.length;
						var isTextInvisible = current.textRenderingMode === TextRenderingMode.INVISIBLE;
						var i, glyph, width, spacingLength;
						if (isTextInvisible || fontSize === 0) {
							return;
						}
						this.cachedGetSinglePixelWidth = null;
						ctx.save();
						ctx.transform.apply(ctx, current.textMatrix);
						ctx.translate(current.x, current.y);
						ctx.scale(textHScale, fontDirection);
						for (i = 0; i < glyphsLength; ++i) {
							glyph = glyphs[i];
							if (isNum(glyph)) {
								spacingLength = spacingDir * glyph * fontSize / 1000;
								this.ctx.translate(spacingLength, 0);
								current.x += spacingLength * textHScale;
								continue;
							}
							var spacing = (glyph.isSpace ? wordSpacing : 0) + charSpacing;
							var operatorList = font.charProcOperatorList[glyph.operatorListId];
							if (!operatorList) {
								warn('Type3 character \"' + glyph.operatorListId + '\" is not available');
								continue;
							}
							this.processingType3 = glyph;
							this.save();
							ctx.scale(fontSize, fontSize);
							ctx.transform.apply(ctx, fontMatrix);
							this.executeOperatorList(operatorList);
							this.restore();
							var transformed = Util.applyTransform([
								glyph.width,
								0
							], fontMatrix);
							width = transformed[0] * fontSize + spacing;
							ctx.translate(width, 0);
							current.x += width * textHScale;
						}
						ctx.restore();
						this.processingType3 = null;
					},
					setCharWidth: function CanvasGraphics_setCharWidth(xWidth, yWidth) {
					},
					setCharWidthAndBounds: function CanvasGraphics_setCharWidthAndBounds(xWidth, yWidth, llx, lly, urx, ury) {
						this.ctx.rect(llx, lly, urx - llx, ury - lly);
						this.clip();
						this.endPath();
					},
					getColorN_Pattern: function CanvasGraphics_getColorN_Pattern(IR) {
						var pattern;
						if (IR[0] === 'TilingPattern') {
							var color = IR[1];
							var baseTransform = this.baseTransform || this.ctx.mozCurrentTransform.slice();
							var self = this;
							var canvasGraphicsFactory = {
								createCanvasGraphics: function (ctx) {
									return new CanvasGraphics(ctx, self.commonObjs, self.objs, self.canvasFactory);
								}
							};
							pattern = new TilingPattern(IR, color, this.ctx, canvasGraphicsFactory, baseTransform);
						} else {
							pattern = getShadingPatternFromIR(IR);
						}
						return pattern;
					},
					setStrokeColorN: function CanvasGraphics_setStrokeColorN() {
						this.current.strokeColor = this.getColorN_Pattern(arguments);
					},
					setFillColorN: function CanvasGraphics_setFillColorN() {
						this.current.fillColor = this.getColorN_Pattern(arguments);
						this.current.patternFill = true;
					},
					setStrokeRGBColor: function CanvasGraphics_setStrokeRGBColor(r, g, b) {
						var color = Util.makeCssRgb(r, g, b);
						this.ctx.strokeStyle = color;
						this.current.strokeColor = color;
					},
					setFillRGBColor: function CanvasGraphics_setFillRGBColor(r, g, b) {
						var color = Util.makeCssRgb(r, g, b);
						this.ctx.fillStyle = color;
						this.current.fillColor = color;
						this.current.patternFill = false;
					},
					shadingFill: function CanvasGraphics_shadingFill(patternIR) {
						var ctx = this.ctx;
						this.save();
						var pattern = getShadingPatternFromIR(patternIR);
						ctx.fillStyle = pattern.getPattern(ctx, this, true);
						var inv = ctx.mozCurrentTransformInverse;
						if (inv) {
							var canvas = ctx.canvas;
							var width = canvas.width;
							var height = canvas.height;
							var bl = Util.applyTransform([
								0,
								0
							], inv);
							var br = Util.applyTransform([
								0,
								height
							], inv);
							var ul = Util.applyTransform([
								width,
								0
							], inv);
							var ur = Util.applyTransform([
								width,
								height
							], inv);
							var x0 = Math.min(bl[0], br[0], ul[0], ur[0]);
							var y0 = Math.min(bl[1], br[1], ul[1], ur[1]);
							var x1 = Math.max(bl[0], br[0], ul[0], ur[0]);
							var y1 = Math.max(bl[1], br[1], ul[1], ur[1]);
							this.ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
						} else {
							this.ctx.fillRect(-1e10, -1e10, 2e10, 2e10);
						}
						this.restore();
					},
					beginInlineImage: function CanvasGraphics_beginInlineImage() {
						error('Should not call beginInlineImage');
					},
					beginImageData: function CanvasGraphics_beginImageData() {
						error('Should not call beginImageData');
					},
					paintFormXObjectBegin: function CanvasGraphics_paintFormXObjectBegin(matrix, bbox) {
						this.save();
						this.baseTransformStack.push(this.baseTransform);
						if (isArray(matrix) && matrix.length === 6) {
							this.transform.apply(this, matrix);
						}
						this.baseTransform = this.ctx.mozCurrentTransform;
						if (isArray(bbox) && bbox.length === 4) {
							var width = bbox[2] - bbox[0];
							var height = bbox[3] - bbox[1];
							this.ctx.rect(bbox[0], bbox[1], width, height);
							this.clip();
							this.endPath();
						}
					},
					paintFormXObjectEnd: function CanvasGraphics_paintFormXObjectEnd() {
						this.restore();
						this.baseTransform = this.baseTransformStack.pop();
					},
					beginGroup: function CanvasGraphics_beginGroup(group) {
						this.save();
						var currentCtx = this.ctx;
						if (!group.isolated) {
							info('TODO: Support non-isolated groups.');
						}
						if (group.knockout) {
							warn('Knockout groups not supported.');
						}
						var currentTransform = currentCtx.mozCurrentTransform;
						if (group.matrix) {
							currentCtx.transform.apply(currentCtx, group.matrix);
						}
						assert(group.bbox, 'Bounding box is required.');
						var bounds = Util.getAxialAlignedBoundingBox(group.bbox, currentCtx.mozCurrentTransform);
						var canvasBounds = [
							0,
							0,
							currentCtx.canvas.width,
							currentCtx.canvas.height
						];
						bounds = Util.intersect(bounds, canvasBounds) || [
							0,
							0,
							0,
							0
						];
						var offsetX = Math.floor(bounds[0]);
						var offsetY = Math.floor(bounds[1]);
						var drawnWidth = Math.max(Math.ceil(bounds[2]) - offsetX, 1);
						var drawnHeight = Math.max(Math.ceil(bounds[3]) - offsetY, 1);
						var scaleX = 1, scaleY = 1;
						if (drawnWidth > MAX_GROUP_SIZE) {
							scaleX = drawnWidth / MAX_GROUP_SIZE;
							drawnWidth = MAX_GROUP_SIZE;
						}
						if (drawnHeight > MAX_GROUP_SIZE) {
							scaleY = drawnHeight / MAX_GROUP_SIZE;
							drawnHeight = MAX_GROUP_SIZE;
						}
						var cacheId = 'groupAt' + this.groupLevel;
						if (group.smask) {
							cacheId += '_smask_' + this.smaskCounter++ % 2;
						}
						var scratchCanvas = this.cachedCanvases.getCanvas(cacheId, drawnWidth, drawnHeight, true);
						var groupCtx = scratchCanvas.context;
						groupCtx.scale(1 / scaleX, 1 / scaleY);
						groupCtx.translate(-offsetX, -offsetY);
						groupCtx.transform.apply(groupCtx, currentTransform);
						if (group.smask) {
							this.smaskStack.push({
								canvas: scratchCanvas.canvas,
								context: groupCtx,
								offsetX: offsetX,
								offsetY: offsetY,
								scaleX: scaleX,
								scaleY: scaleY,
								subtype: group.smask.subtype,
								backdrop: group.smask.backdrop,
								transferMap: group.smask.transferMap || null,
								startTransformInverse: null
							});
						} else {
							currentCtx.setTransform(1, 0, 0, 1, 0, 0);
							currentCtx.translate(offsetX, offsetY);
							currentCtx.scale(scaleX, scaleY);
						}
						copyCtxState(currentCtx, groupCtx);
						this.ctx = groupCtx;
						this.setGState([
							[
								'BM',
								'Normal'
							],
							[
								'ca',
								1
							],
							[
								'CA',
								1
							]
						]);
						this.groupStack.push(currentCtx);
						this.groupLevel++;
						this.current.activeSMask = null;
					},
					endGroup: function CanvasGraphics_endGroup(group) {
						this.groupLevel--;
						var groupCtx = this.ctx;
						this.ctx = this.groupStack.pop();
						if (this.ctx.imageSmoothingEnabled !== undefined) {
							this.ctx.imageSmoothingEnabled = false;
						} else {
							this.ctx.mozImageSmoothingEnabled = false;
						}
						if (group.smask) {
							this.tempSMask = this.smaskStack.pop();
						} else {
							this.ctx.drawImage(groupCtx.canvas, 0, 0);
						}
						this.restore();
					},
					beginAnnotations: function CanvasGraphics_beginAnnotations() {
						this.save();
						this.current = new CanvasExtraState();
						if (this.baseTransform) {
							this.ctx.setTransform.apply(this.ctx, this.baseTransform);
						}
					},
					endAnnotations: function CanvasGraphics_endAnnotations() {
						this.restore();
					},
					beginAnnotation: function CanvasGraphics_beginAnnotation(rect, transform, matrix) {
						this.save();
						if (isArray(rect) && rect.length === 4) {
							var width = rect[2] - rect[0];
							var height = rect[3] - rect[1];
							this.ctx.rect(rect[0], rect[1], width, height);
							this.clip();
							this.endPath();
						}
						this.transform.apply(this, transform);
						this.transform.apply(this, matrix);
					},
					endAnnotation: function CanvasGraphics_endAnnotation() {
						this.restore();
					},
					paintJpegXObject: function CanvasGraphics_paintJpegXObject(objId, w, h) {
						var domImage = this.objs.get(objId);
						if (!domImage) {
							warn('Dependent image isn\'t ready yet');
							return;
						}
						this.save();
						var ctx = this.ctx;
						ctx.scale(1 / w, -1 / h);
						ctx.drawImage(domImage, 0, 0, domImage.width, domImage.height, 0, -h, w, h);
						if (this.imageLayer) {
							var currentTransform = ctx.mozCurrentTransformInverse;
							var position = this.getCanvasPosition(0, 0);
							this.imageLayer.appendImage({
								objId: objId,
								left: position[0],
								top: position[1],
								width: w / currentTransform[0],
								height: h / currentTransform[3]
							});
						}
						this.restore();
					},
					paintImageMaskXObject: function CanvasGraphics_paintImageMaskXObject(img) {
						var ctx = this.ctx;
						var width = img.width, height = img.height;
						var fillColor = this.current.fillColor;
						var isPatternFill = this.current.patternFill;
						var glyph = this.processingType3;
						if (COMPILE_TYPE3_GLYPHS && glyph && glyph.compiled === undefined) {
							if (width <= MAX_SIZE_TO_COMPILE && height <= MAX_SIZE_TO_COMPILE) {
								glyph.compiled = compileType3Glyph({
									data: img.data,
									width: width,
									height: height
								});
							} else {
								glyph.compiled = null;
							}
						}
						if (glyph && glyph.compiled) {
							glyph.compiled(ctx);
							return;
						}
						var maskCanvas = this.cachedCanvases.getCanvas('maskCanvas', width, height);
						var maskCtx = maskCanvas.context;
						maskCtx.save();
						putBinaryImageMask(maskCtx, img);
						maskCtx.globalCompositeOperation = 'source-in';
						maskCtx.fillStyle = isPatternFill ? fillColor.getPattern(maskCtx, this) : fillColor;
						maskCtx.fillRect(0, 0, width, height);
						maskCtx.restore();
						this.paintInlineImageXObject(maskCanvas.canvas);
					},
					paintImageMaskXObjectRepeat: function CanvasGraphics_paintImageMaskXObjectRepeat(imgData, scaleX, scaleY, positions) {
						var width = imgData.width;
						var height = imgData.height;
						var fillColor = this.current.fillColor;
						var isPatternFill = this.current.patternFill;
						var maskCanvas = this.cachedCanvases.getCanvas('maskCanvas', width, height);
						var maskCtx = maskCanvas.context;
						maskCtx.save();
						putBinaryImageMask(maskCtx, imgData);
						maskCtx.globalCompositeOperation = 'source-in';
						maskCtx.fillStyle = isPatternFill ? fillColor.getPattern(maskCtx, this) : fillColor;
						maskCtx.fillRect(0, 0, width, height);
						maskCtx.restore();
						var ctx = this.ctx;
						for (var i = 0, ii = positions.length; i < ii; i += 2) {
							ctx.save();
							ctx.transform(scaleX, 0, 0, scaleY, positions[i], positions[i + 1]);
							ctx.scale(1, -1);
							ctx.drawImage(maskCanvas.canvas, 0, 0, width, height, 0, -1, 1, 1);
							ctx.restore();
						}
					},
					paintImageMaskXObjectGroup: function CanvasGraphics_paintImageMaskXObjectGroup(images) {
						var ctx = this.ctx;
						var fillColor = this.current.fillColor;
						var isPatternFill = this.current.patternFill;
						for (var i = 0, ii = images.length; i < ii; i++) {
							var image = images[i];
							var width = image.width, height = image.height;
							var maskCanvas = this.cachedCanvases.getCanvas('maskCanvas', width, height);
							var maskCtx = maskCanvas.context;
							maskCtx.save();
							putBinaryImageMask(maskCtx, image);
							maskCtx.globalCompositeOperation = 'source-in';
							maskCtx.fillStyle = isPatternFill ? fillColor.getPattern(maskCtx, this) : fillColor;
							maskCtx.fillRect(0, 0, width, height);
							maskCtx.restore();
							ctx.save();
							ctx.transform.apply(ctx, image.transform);
							ctx.scale(1, -1);
							ctx.drawImage(maskCanvas.canvas, 0, 0, width, height, 0, -1, 1, 1);
							ctx.restore();
						}
					},
					paintImageXObject: function CanvasGraphics_paintImageXObject(objId) {
						var imgData = this.objs.get(objId);
						if (!imgData) {
							warn('Dependent image isn\'t ready yet');
							return;
						}
						this.paintInlineImageXObject(imgData);
					},
					paintImageXObjectRepeat: function CanvasGraphics_paintImageXObjectRepeat(objId, scaleX, scaleY, positions) {
						var imgData = this.objs.get(objId);
						if (!imgData) {
							warn('Dependent image isn\'t ready yet');
							return;
						}
						var width = imgData.width;
						var height = imgData.height;
						var map = [];
						for (var i = 0, ii = positions.length; i < ii; i += 2) {
							map.push({
								transform: [
									scaleX,
									0,
									0,
									scaleY,
									positions[i],
									positions[i + 1]
								],
								x: 0,
								y: 0,
								w: width,
								h: height
							});
						}
						this.paintInlineImageXObjectGroup(imgData, map);
					},
					paintInlineImageXObject: function CanvasGraphics_paintInlineImageXObject(imgData) {
						var width = imgData.width;
						var height = imgData.height;
						var ctx = this.ctx;
						this.save();
						ctx.scale(1 / width, -1 / height);
						var currentTransform = ctx.mozCurrentTransformInverse;
						var a = currentTransform[0], b = currentTransform[1];
						var widthScale = Math.max(Math.sqrt(a * a + b * b), 1);
						var c = currentTransform[2], d = currentTransform[3];
						var heightScale = Math.max(Math.sqrt(c * c + d * d), 1);
						var imgToPaint, tmpCanvas;
						if (imgData instanceof HTMLElement || !imgData.data) {
							imgToPaint = imgData;
						} else {
							tmpCanvas = this.cachedCanvases.getCanvas('inlineImage', width, height);
							var tmpCtx = tmpCanvas.context;
							putBinaryImageData(tmpCtx, imgData);
							imgToPaint = tmpCanvas.canvas;
						}
						var paintWidth = width, paintHeight = height;
						var tmpCanvasId = 'prescale1';
						while (widthScale > 2 && paintWidth > 1 || heightScale > 2 && paintHeight > 1) {
							var newWidth = paintWidth, newHeight = paintHeight;
							if (widthScale > 2 && paintWidth > 1) {
								newWidth = Math.ceil(paintWidth / 2);
								widthScale /= paintWidth / newWidth;
							}
							if (heightScale > 2 && paintHeight > 1) {
								newHeight = Math.ceil(paintHeight / 2);
								heightScale /= paintHeight / newHeight;
							}
							tmpCanvas = this.cachedCanvases.getCanvas(tmpCanvasId, newWidth, newHeight);
							tmpCtx = tmpCanvas.context;
							tmpCtx.clearRect(0, 0, newWidth, newHeight);
							tmpCtx.drawImage(imgToPaint, 0, 0, paintWidth, paintHeight, 0, 0, newWidth, newHeight);
							imgToPaint = tmpCanvas.canvas;
							paintWidth = newWidth;
							paintHeight = newHeight;
							tmpCanvasId = tmpCanvasId === 'prescale1' ? 'prescale2' : 'prescale1';
						}
						ctx.drawImage(imgToPaint, 0, 0, paintWidth, paintHeight, 0, -height, width, height);
						if (this.imageLayer) {
							var position = this.getCanvasPosition(0, -height);
							this.imageLayer.appendImage({
								imgData: imgData,
								left: position[0],
								top: position[1],
								width: width / currentTransform[0],
								height: height / currentTransform[3]
							});
						}
						this.restore();
					},
					paintInlineImageXObjectGroup: function CanvasGraphics_paintInlineImageXObjectGroup(imgData, map) {
						var ctx = this.ctx;
						var w = imgData.width;
						var h = imgData.height;
						var tmpCanvas = this.cachedCanvases.getCanvas('inlineImage', w, h);
						var tmpCtx = tmpCanvas.context;
						putBinaryImageData(tmpCtx, imgData);
						for (var i = 0, ii = map.length; i < ii; i++) {
							var entry = map[i];
							ctx.save();
							ctx.transform.apply(ctx, entry.transform);
							ctx.scale(1, -1);
							ctx.drawImage(tmpCanvas.canvas, entry.x, entry.y, entry.w, entry.h, 0, -1, 1, 1);
							if (this.imageLayer) {
								var position = this.getCanvasPosition(entry.x, entry.y);
								this.imageLayer.appendImage({
									imgData: imgData,
									left: position[0],
									top: position[1],
									width: w,
									height: h
								});
							}
							ctx.restore();
						}
					},
					paintSolidColorImageMask: function CanvasGraphics_paintSolidColorImageMask() {
						this.ctx.fillRect(0, 0, 1, 1);
					},
					paintXObject: function CanvasGraphics_paintXObject() {
						warn('Unsupported \'paintXObject\' command.');
					},
					markPoint: function CanvasGraphics_markPoint(tag) {
					},
					markPointProps: function CanvasGraphics_markPointProps(tag, properties) {
					},
					beginMarkedContent: function CanvasGraphics_beginMarkedContent(tag) {
					},
					beginMarkedContentProps: function CanvasGraphics_beginMarkedContentProps(tag, properties) {
					},
					endMarkedContent: function CanvasGraphics_endMarkedContent() {
					},
					beginCompat: function CanvasGraphics_beginCompat() {
					},
					endCompat: function CanvasGraphics_endCompat() {
					},
					consumePath: function CanvasGraphics_consumePath() {
						var ctx = this.ctx;
						if (this.pendingClip) {
							if (this.pendingClip === EO_CLIP) {
								ctx.clip('evenodd');
							} else {
								ctx.clip();
							}
							this.pendingClip = null;
						}
						ctx.beginPath();
					},
					getSinglePixelWidth: function CanvasGraphics_getSinglePixelWidth(scale) {
						if (this.cachedGetSinglePixelWidth === null) {
							this.ctx.save();
							var inverse = this.ctx.mozCurrentTransformInverse;
							this.ctx.restore();
							this.cachedGetSinglePixelWidth = Math.sqrt(Math.max(inverse[0] * inverse[0] + inverse[1] * inverse[1], inverse[2] * inverse[2] + inverse[3] * inverse[3]));
						}
						return this.cachedGetSinglePixelWidth;
					},
					getCanvasPosition: function CanvasGraphics_getCanvasPosition(x, y) {
						var transform = this.ctx.mozCurrentTransform;
						return [
							transform[0] * x + transform[2] * y + transform[4],
							transform[1] * x + transform[3] * y + transform[5]
						];
					}
				};
				for (var op in OPS) {
					CanvasGraphics.prototype[OPS[op]] = CanvasGraphics.prototype[op];
				}
				return CanvasGraphics;
			}();
			exports.CanvasGraphics = CanvasGraphics;

			/***/
		}),
/* 11 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var assert = sharedUtil.assert;
			var bytesToString = sharedUtil.bytesToString;
			var string32 = sharedUtil.string32;
			var shadow = sharedUtil.shadow;
			var warn = sharedUtil.warn;
			function FontLoader(docId) {
				this.docId = docId;
				this.styleElement = null;
				this.nativeFontFaces = [];
				this.loadTestFontId = 0;
				this.loadingContext = {
					requests: [],
					nextRequestId: 0
				};
			}
			FontLoader.prototype = {
				insertRule: function fontLoaderInsertRule(rule) {
					var styleElement = this.styleElement;
					if (!styleElement) {
						styleElement = this.styleElement = document.createElement('style');
						styleElement.id = 'PDFJS_FONT_STYLE_TAG_' + this.docId;
						document.documentElement.getElementsByTagName('head')[0].appendChild(styleElement);
					}
					var styleSheet = styleElement.sheet;
					styleSheet.insertRule(rule, styleSheet.cssRules.length);
				},
				clear: function fontLoaderClear() {
					if (this.styleElement) {
						this.styleElement.remove();
						this.styleElement = null;
					}
					this.nativeFontFaces.forEach(function (nativeFontFace) {
						document.fonts.delete(nativeFontFace);
					});
					this.nativeFontFaces.length = 0;
				}
			};
			var getLoadTestFont = function () {
				return atob('T1RUTwALAIAAAwAwQ0ZGIDHtZg4AAAOYAAAAgUZGVE1lkzZwAAAEHAAAABxHREVGABQAFQ' + 'AABDgAAAAeT1MvMlYNYwkAAAEgAAAAYGNtYXABDQLUAAACNAAAAUJoZWFk/xVFDQAAALwA' + 'AAA2aGhlYQdkA+oAAAD0AAAAJGhtdHgD6AAAAAAEWAAAAAZtYXhwAAJQAAAAARgAAAAGbm' + 'FtZVjmdH4AAAGAAAAAsXBvc3T/hgAzAAADeAAAACAAAQAAAAEAALZRFsRfDzz1AAsD6AAA' + 'AADOBOTLAAAAAM4KHDwAAAAAA+gDIQAAAAgAAgAAAAAAAAABAAADIQAAAFoD6AAAAAAD6A' + 'ABAAAAAAAAAAAAAAAAAAAAAQAAUAAAAgAAAAQD6AH0AAUAAAKKArwAAACMAooCvAAAAeAA' + 'MQECAAACAAYJAAAAAAAAAAAAAQAAAAAAAAAAAAAAAFBmRWQAwAAuAC4DIP84AFoDIQAAAA' + 'AAAQAAAAAAAAAAACAAIAABAAAADgCuAAEAAAAAAAAAAQAAAAEAAAAAAAEAAQAAAAEAAAAA' + 'AAIAAQAAAAEAAAAAAAMAAQAAAAEAAAAAAAQAAQAAAAEAAAAAAAUAAQAAAAEAAAAAAAYAAQ' + 'AAAAMAAQQJAAAAAgABAAMAAQQJAAEAAgABAAMAAQQJAAIAAgABAAMAAQQJAAMAAgABAAMA' + 'AQQJAAQAAgABAAMAAQQJAAUAAgABAAMAAQQJAAYAAgABWABYAAAAAAAAAwAAAAMAAAAcAA' + 'EAAAAAADwAAwABAAAAHAAEACAAAAAEAAQAAQAAAC7//wAAAC7////TAAEAAAAAAAABBgAA' + 'AQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAA' + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' + 'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMAAA' + 'AAAAD/gwAyAAAAAQAAAAAAAAAAAAAAAAAAAAABAAQEAAEBAQJYAAEBASH4DwD4GwHEAvgc' + 'A/gXBIwMAYuL+nz5tQXkD5j3CBLnEQACAQEBIVhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWF' + 'hYWFhYWFhYAAABAQAADwACAQEEE/t3Dov6fAH6fAT+fPp8+nwHDosMCvm1Cvm1DAz6fBQA' + 'AAAAAAABAAAAAMmJbzEAAAAAzgTjFQAAAADOBOQpAAEAAAAAAAAADAAUAAQAAAABAAAAAg' + 'ABAAAAAAAAAAAD6AAAAAAAAA==');
			};
			Object.defineProperty(FontLoader.prototype, 'loadTestFont', {
				get: function () {
					return shadow(this, 'loadTestFont', getLoadTestFont());
				},
				configurable: true
			});
			FontLoader.prototype.addNativeFontFace = function fontLoader_addNativeFontFace(nativeFontFace) {
				this.nativeFontFaces.push(nativeFontFace);
				document.fonts.add(nativeFontFace);
			};
			FontLoader.prototype.bind = function fontLoaderBind(fonts, callback) {
				var rules = [];
				var fontsToLoad = [];
				var fontLoadPromises = [];
				var getNativeFontPromise = function (nativeFontFace) {
					return nativeFontFace.loaded.catch(function (e) {
						warn('Failed to load font "' + nativeFontFace.family + '": ' + e);
					});
				};
				var isFontLoadingAPISupported = FontLoader.isFontLoadingAPISupported && !FontLoader.isSyncFontLoadingSupported;
				for (var i = 0, ii = fonts.length; i < ii; i++) {
					var font = fonts[i];
					if (font.attached || font.loading === false) {
						continue;
					}
					font.attached = true;
					if (isFontLoadingAPISupported) {
						var nativeFontFace = font.createNativeFontFace();
						if (nativeFontFace) {
							this.addNativeFontFace(nativeFontFace);
							fontLoadPromises.push(getNativeFontPromise(nativeFontFace));
						}
					} else {
						var rule = font.createFontFaceRule();
						if (rule) {
							this.insertRule(rule);
							rules.push(rule);
							fontsToLoad.push(font);
						}
					}
				}
				var request = this.queueLoadingCallback(callback);
				if (isFontLoadingAPISupported) {
					Promise.all(fontLoadPromises).then(function () {
						request.complete();
					});
				} else if (rules.length > 0 && !FontLoader.isSyncFontLoadingSupported) {
					this.prepareFontLoadEvent(rules, fontsToLoad, request);
				} else {
					request.complete();
				}
			};
			FontLoader.prototype.queueLoadingCallback = function FontLoader_queueLoadingCallback(callback) {
				function LoadLoader_completeRequest() {
					assert(!request.end, 'completeRequest() cannot be called twice');
					request.end = Date.now();
					while (context.requests.length > 0 && context.requests[0].end) {
						var otherRequest = context.requests.shift();
						setTimeout(otherRequest.callback, 0);
					}
				}
				var context = this.loadingContext;
				var requestId = 'pdfjs-font-loading-' + context.nextRequestId++;
				var request = {
					id: requestId,
					complete: LoadLoader_completeRequest,
					callback: callback,
					started: Date.now()
				};
				context.requests.push(request);
				return request;
			};
			FontLoader.prototype.prepareFontLoadEvent = function fontLoaderPrepareFontLoadEvent(rules, fonts, request) {
				function int32(data, offset) {
					return data.charCodeAt(offset) << 24 | data.charCodeAt(offset + 1) << 16 | data.charCodeAt(offset + 2) << 8 | data.charCodeAt(offset + 3) & 0xff;
				}
				function spliceString(s, offset, remove, insert) {
					var chunk1 = s.substr(0, offset);
					var chunk2 = s.substr(offset + remove);
					return chunk1 + insert + chunk2;
				}
				var i, ii;
				var canvas = document.createElement('canvas');
				canvas.width = 1;
				canvas.height = 1;
				var ctx = canvas.getContext('2d');
				var called = 0;
				function isFontReady(name, callback) {
					called++;
					if (called > 30) {
						warn('Load test font never loaded.');
						callback();
						return;
					}
					ctx.font = '30px ' + name;
					ctx.fillText('.', 0, 20);
					var imageData = ctx.getImageData(0, 0, 1, 1);
					if (imageData.data[3] > 0) {
						callback();
						return;
					}
					setTimeout(isFontReady.bind(null, name, callback));
				}
				var loadTestFontId = 'lt' + Date.now() + this.loadTestFontId++;
				var data = this.loadTestFont;
				var COMMENT_OFFSET = 976;
				data = spliceString(data, COMMENT_OFFSET, loadTestFontId.length, loadTestFontId);
				var CFF_CHECKSUM_OFFSET = 16;
				var XXXX_VALUE = 0x58585858;
				var checksum = int32(data, CFF_CHECKSUM_OFFSET);
				for (i = 0, ii = loadTestFontId.length - 3; i < ii; i += 4) {
					checksum = checksum - XXXX_VALUE + int32(loadTestFontId, i) | 0;
				}
				if (i < loadTestFontId.length) {
					checksum = checksum - XXXX_VALUE + int32(loadTestFontId + 'XXX', i) | 0;
				}
				data = spliceString(data, CFF_CHECKSUM_OFFSET, 4, string32(checksum));
				var url = 'url(data:font/opentype;base64,' + btoa(data) + ');';
				var rule = '@font-face { font-family:"' + loadTestFontId + '";src:' + url + '}';
				this.insertRule(rule);
				var names = [];
				for (i = 0, ii = fonts.length; i < ii; i++) {
					names.push(fonts[i].loadedName);
				}
				names.push(loadTestFontId);
				var div = document.createElement('div');
				div.setAttribute('style', 'visibility: hidden;' + 'width: 10px; height: 10px;' + 'position: absolute; top: 0px; left: 0px;');
				for (i = 0, ii = names.length; i < ii; ++i) {
					var span = document.createElement('span');
					span.textContent = 'Hi';
					span.style.fontFamily = names[i];
					div.appendChild(span);
				}
				document.body.appendChild(div);
				isFontReady(loadTestFontId, function () {
					document.body.removeChild(div);
					request.complete();
				});
			};
			FontLoader.isFontLoadingAPISupported = typeof document !== 'undefined' && !!document.fonts;
			var isSyncFontLoadingSupported = function isSyncFontLoadingSupported() {
				if (typeof navigator === 'undefined') {
					return true;
				}
				var supported = false;
				var m = /Mozilla\/5.0.*?rv:(\d+).*? Gecko/.exec(navigator.userAgent);
				if (m && m[1] >= 14) {
					supported = true;
				}
				return supported;
			};
			Object.defineProperty(FontLoader, 'isSyncFontLoadingSupported', {
				get: function () {
					return shadow(FontLoader, 'isSyncFontLoadingSupported', isSyncFontLoadingSupported());
				},
				enumerable: true,
				configurable: true
			});
			var IsEvalSupportedCached = {
				get value() {
					return shadow(this, 'value', sharedUtil.isEvalSupported());
				}
			};
			var FontFaceObject = function FontFaceObjectClosure() {
				function FontFaceObject(translatedData, options) {
					this.compiledGlyphs = Object.create(null);
					for (var i in translatedData) {
						this[i] = translatedData[i];
					}
					this.options = options;
				}
				FontFaceObject.prototype = {
					createNativeFontFace: function FontFaceObject_createNativeFontFace() {
						if (!this.data) {
							return null;
						}
						if (this.options.disableFontFace) {
							this.disableFontFace = true;
							return null;
						}
						var nativeFontFace = new FontFace(this.loadedName, this.data, {});
						if (this.options.fontRegistry) {
							this.options.fontRegistry.registerFont(this);
						}
						return nativeFontFace;
					},
					createFontFaceRule: function FontFaceObject_createFontFaceRule() {
						if (!this.data) {
							return null;
						}
						if (this.options.disableFontFace) {
							this.disableFontFace = true;
							return null;
						}
						var data = bytesToString(new Uint8Array(this.data));
						var fontName = this.loadedName;
						var url = 'url(data:' + this.mimetype + ';base64,' + btoa(data) + ');';
						var rule = '@font-face { font-family:"' + fontName + '";src:' + url + '}';
						if (this.options.fontRegistry) {
							this.options.fontRegistry.registerFont(this, url);
						}
						return rule;
					},
					getPathGenerator: function FontFaceObject_getPathGenerator(objs, character) {
						if (!(character in this.compiledGlyphs)) {
							var cmds = objs.get(this.loadedName + '_path_' + character);
							var current, i, len;
							if (this.options.isEvalSupported && IsEvalSupportedCached.value) {
								var args, js = '';
								for (i = 0, len = cmds.length; i < len; i++) {
									current = cmds[i];
									if (current.args !== undefined) {
										args = current.args.join(',');
									} else {
										args = '';
									}
									js += 'c.' + current.cmd + '(' + args + ');\n';
								}
								this.compiledGlyphs[character] = new Function('c', 'size', js);
							} else {
								this.compiledGlyphs[character] = function (c, size) {
									for (i = 0, len = cmds.length; i < len; i++) {
										current = cmds[i];
										if (current.cmd === 'scale') {
											current.args = [
												size,
												-size
											];
										}
										c[current.cmd].apply(c, current.args);
									}
								};
							}
						}
						return this.compiledGlyphs[character];
					}
				};
				return FontFaceObject;
			}();
			exports.FontFaceObject = FontFaceObject;
			exports.FontLoader = FontLoader;

			/***/
		}),
/* 12 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var displayWebGL = __w_pdfjs_require__(8);
			var Util = sharedUtil.Util;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var error = sharedUtil.error;
			var WebGLUtils = displayWebGL.WebGLUtils;
			var ShadingIRs = {};
			ShadingIRs.RadialAxial = {
				fromIR: function RadialAxial_fromIR(raw) {
					var type = raw[1];
					var colorStops = raw[2];
					var p0 = raw[3];
					var p1 = raw[4];
					var r0 = raw[5];
					var r1 = raw[6];
					return {
						type: 'Pattern',
						getPattern: function RadialAxial_getPattern(ctx) {
							var grad;
							if (type === 'axial') {
								grad = ctx.createLinearGradient(p0[0], p0[1], p1[0], p1[1]);
							} else if (type === 'radial') {
								grad = ctx.createRadialGradient(p0[0], p0[1], r0, p1[0], p1[1], r1);
							}
							for (var i = 0, ii = colorStops.length; i < ii; ++i) {
								var c = colorStops[i];
								grad.addColorStop(c[0], c[1]);
							}
							return grad;
						}
					};
				}
			};
			var createMeshCanvas = function createMeshCanvasClosure() {
				function drawTriangle(data, context, p1, p2, p3, c1, c2, c3) {
					var coords = context.coords, colors = context.colors;
					var bytes = data.data, rowSize = data.width * 4;
					var tmp;
					if (coords[p1 + 1] > coords[p2 + 1]) {
						tmp = p1;
						p1 = p2;
						p2 = tmp;
						tmp = c1;
						c1 = c2;
						c2 = tmp;
					}
					if (coords[p2 + 1] > coords[p3 + 1]) {
						tmp = p2;
						p2 = p3;
						p3 = tmp;
						tmp = c2;
						c2 = c3;
						c3 = tmp;
					}
					if (coords[p1 + 1] > coords[p2 + 1]) {
						tmp = p1;
						p1 = p2;
						p2 = tmp;
						tmp = c1;
						c1 = c2;
						c2 = tmp;
					}
					var x1 = (coords[p1] + context.offsetX) * context.scaleX;
					var y1 = (coords[p1 + 1] + context.offsetY) * context.scaleY;
					var x2 = (coords[p2] + context.offsetX) * context.scaleX;
					var y2 = (coords[p2 + 1] + context.offsetY) * context.scaleY;
					var x3 = (coords[p3] + context.offsetX) * context.scaleX;
					var y3 = (coords[p3 + 1] + context.offsetY) * context.scaleY;
					if (y1 >= y3) {
						return;
					}
					var c1r = colors[c1], c1g = colors[c1 + 1], c1b = colors[c1 + 2];
					var c2r = colors[c2], c2g = colors[c2 + 1], c2b = colors[c2 + 2];
					var c3r = colors[c3], c3g = colors[c3 + 1], c3b = colors[c3 + 2];
					var minY = Math.round(y1), maxY = Math.round(y3);
					var xa, car, cag, cab;
					var xb, cbr, cbg, cbb;
					var k;
					for (var y = minY; y <= maxY; y++) {
						if (y < y2) {
							k = y < y1 ? 0 : y1 === y2 ? 1 : (y1 - y) / (y1 - y2);
							xa = x1 - (x1 - x2) * k;
							car = c1r - (c1r - c2r) * k;
							cag = c1g - (c1g - c2g) * k;
							cab = c1b - (c1b - c2b) * k;
						} else {
							k = y > y3 ? 1 : y2 === y3 ? 0 : (y2 - y) / (y2 - y3);
							xa = x2 - (x2 - x3) * k;
							car = c2r - (c2r - c3r) * k;
							cag = c2g - (c2g - c3g) * k;
							cab = c2b - (c2b - c3b) * k;
						}
						k = y < y1 ? 0 : y > y3 ? 1 : (y1 - y) / (y1 - y3);
						xb = x1 - (x1 - x3) * k;
						cbr = c1r - (c1r - c3r) * k;
						cbg = c1g - (c1g - c3g) * k;
						cbb = c1b - (c1b - c3b) * k;
						var x1_ = Math.round(Math.min(xa, xb));
						var x2_ = Math.round(Math.max(xa, xb));
						var j = rowSize * y + x1_ * 4;
						for (var x = x1_; x <= x2_; x++) {
							k = (xa - x) / (xa - xb);
							k = k < 0 ? 0 : k > 1 ? 1 : k;
							bytes[j++] = car - (car - cbr) * k | 0;
							bytes[j++] = cag - (cag - cbg) * k | 0;
							bytes[j++] = cab - (cab - cbb) * k | 0;
							bytes[j++] = 255;
						}
					}
				}
				function drawFigure(data, figure, context) {
					var ps = figure.coords;
					var cs = figure.colors;
					var i, ii;
					switch (figure.type) {
						case 'lattice':
							var verticesPerRow = figure.verticesPerRow;
							var rows = Math.floor(ps.length / verticesPerRow) - 1;
							var cols = verticesPerRow - 1;
							for (i = 0; i < rows; i++) {
								var q = i * verticesPerRow;
								for (var j = 0; j < cols; j++, q++) {
									drawTriangle(data, context, ps[q], ps[q + 1], ps[q + verticesPerRow], cs[q], cs[q + 1], cs[q + verticesPerRow]);
									drawTriangle(data, context, ps[q + verticesPerRow + 1], ps[q + 1], ps[q + verticesPerRow], cs[q + verticesPerRow + 1], cs[q + 1], cs[q + verticesPerRow]);
								}
							}
							break;
						case 'triangles':
							for (i = 0, ii = ps.length; i < ii; i += 3) {
								drawTriangle(data, context, ps[i], ps[i + 1], ps[i + 2], cs[i], cs[i + 1], cs[i + 2]);
							}
							break;
						default:
							error('illigal figure');
							break;
					}
				}
				function createMeshCanvas(bounds, combinesScale, coords, colors, figures, backgroundColor, cachedCanvases) {
					var EXPECTED_SCALE = 1.1;
					var MAX_PATTERN_SIZE = 3000;
					var BORDER_SIZE = 2;
					var offsetX = Math.floor(bounds[0]);
					var offsetY = Math.floor(bounds[1]);
					var boundsWidth = Math.ceil(bounds[2]) - offsetX;
					var boundsHeight = Math.ceil(bounds[3]) - offsetY;
					var width = Math.min(Math.ceil(Math.abs(boundsWidth * combinesScale[0] * EXPECTED_SCALE)), MAX_PATTERN_SIZE);
					var height = Math.min(Math.ceil(Math.abs(boundsHeight * combinesScale[1] * EXPECTED_SCALE)), MAX_PATTERN_SIZE);
					var scaleX = boundsWidth / width;
					var scaleY = boundsHeight / height;
					var context = {
						coords: coords,
						colors: colors,
						offsetX: -offsetX,
						offsetY: -offsetY,
						scaleX: 1 / scaleX,
						scaleY: 1 / scaleY
					};
					var paddedWidth = width + BORDER_SIZE * 2;
					var paddedHeight = height + BORDER_SIZE * 2;
					var canvas, tmpCanvas, i, ii;
					if (WebGLUtils.isEnabled) {
						canvas = WebGLUtils.drawFigures(width, height, backgroundColor, figures, context);
						tmpCanvas = cachedCanvases.getCanvas('mesh', paddedWidth, paddedHeight, false);
						tmpCanvas.context.drawImage(canvas, BORDER_SIZE, BORDER_SIZE);
						canvas = tmpCanvas.canvas;
					} else {
						tmpCanvas = cachedCanvases.getCanvas('mesh', paddedWidth, paddedHeight, false);
						var tmpCtx = tmpCanvas.context;
						var data = tmpCtx.createImageData(width, height);
						if (backgroundColor) {
							var bytes = data.data;
							for (i = 0, ii = bytes.length; i < ii; i += 4) {
								bytes[i] = backgroundColor[0];
								bytes[i + 1] = backgroundColor[1];
								bytes[i + 2] = backgroundColor[2];
								bytes[i + 3] = 255;
							}
						}
						for (i = 0; i < figures.length; i++) {
							drawFigure(data, figures[i], context);
						}
						tmpCtx.putImageData(data, BORDER_SIZE, BORDER_SIZE);
						canvas = tmpCanvas.canvas;
					}
					return {
						canvas: canvas,
						offsetX: offsetX - BORDER_SIZE * scaleX,
						offsetY: offsetY - BORDER_SIZE * scaleY,
						scaleX: scaleX,
						scaleY: scaleY
					};
				}
				return createMeshCanvas;
			}();
			ShadingIRs.Mesh = {
				fromIR: function Mesh_fromIR(raw) {
					var coords = raw[2];
					var colors = raw[3];
					var figures = raw[4];
					var bounds = raw[5];
					var matrix = raw[6];
					var background = raw[8];
					return {
						type: 'Pattern',
						getPattern: function Mesh_getPattern(ctx, owner, shadingFill) {
							var scale;
							if (shadingFill) {
								scale = Util.singularValueDecompose2dScale(ctx.mozCurrentTransform);
							} else {
								scale = Util.singularValueDecompose2dScale(owner.baseTransform);
								if (matrix) {
									var matrixScale = Util.singularValueDecompose2dScale(matrix);
									scale = [
										scale[0] * matrixScale[0],
										scale[1] * matrixScale[1]
									];
								}
							}
							var temporaryPatternCanvas = createMeshCanvas(bounds, scale, coords, colors, figures, shadingFill ? null : background, owner.cachedCanvases);
							if (!shadingFill) {
								ctx.setTransform.apply(ctx, owner.baseTransform);
								if (matrix) {
									ctx.transform.apply(ctx, matrix);
								}
							}
							ctx.translate(temporaryPatternCanvas.offsetX, temporaryPatternCanvas.offsetY);
							ctx.scale(temporaryPatternCanvas.scaleX, temporaryPatternCanvas.scaleY);
							return ctx.createPattern(temporaryPatternCanvas.canvas, 'no-repeat');
						}
					};
				}
			};
			ShadingIRs.Dummy = {
				fromIR: function Dummy_fromIR() {
					return {
						type: 'Pattern',
						getPattern: function Dummy_fromIR_getPattern() {
							return 'hotpink';
						}
					};
				}
			};
			function getShadingPatternFromIR(raw) {
				var shadingIR = ShadingIRs[raw[0]];
				if (!shadingIR) {
					error('Unknown IR type: ' + raw[0]);
				}
				return shadingIR.fromIR(raw);
			}
			var TilingPattern = function TilingPatternClosure() {
				var PaintType = {
					COLORED: 1,
					UNCOLORED: 2
				};
				var MAX_PATTERN_SIZE = 3000;
				function TilingPattern(IR, color, ctx, canvasGraphicsFactory, baseTransform) {
					this.operatorList = IR[2];
					this.matrix = IR[3] || [
						1,
						0,
						0,
						1,
						0,
						0
					];
					this.bbox = IR[4];
					this.xstep = IR[5];
					this.ystep = IR[6];
					this.paintType = IR[7];
					this.tilingType = IR[8];
					this.color = color;
					this.canvasGraphicsFactory = canvasGraphicsFactory;
					this.baseTransform = baseTransform;
					this.type = 'Pattern';
					this.ctx = ctx;
				}
				TilingPattern.prototype = {
					createPatternCanvas: function TilinPattern_createPatternCanvas(owner) {
						var operatorList = this.operatorList;
						var bbox = this.bbox;
						var xstep = this.xstep;
						var ystep = this.ystep;
						var paintType = this.paintType;
						var tilingType = this.tilingType;
						var color = this.color;
						var canvasGraphicsFactory = this.canvasGraphicsFactory;
						info('TilingType: ' + tilingType);
						var x0 = bbox[0], y0 = bbox[1], x1 = bbox[2], y1 = bbox[3];
						var topLeft = [
							x0,
							y0
						];
						var botRight = [
							x0 + xstep,
							y0 + ystep
						];
						var width = botRight[0] - topLeft[0];
						var height = botRight[1] - topLeft[1];
						var matrixScale = Util.singularValueDecompose2dScale(this.matrix);
						var curMatrixScale = Util.singularValueDecompose2dScale(this.baseTransform);
						var combinedScale = [
							matrixScale[0] * curMatrixScale[0],
							matrixScale[1] * curMatrixScale[1]
						];
						width = Math.min(Math.ceil(Math.abs(width * combinedScale[0])), MAX_PATTERN_SIZE);
						height = Math.min(Math.ceil(Math.abs(height * combinedScale[1])), MAX_PATTERN_SIZE);
						var tmpCanvas = owner.cachedCanvases.getCanvas('pattern', width, height, true);
						var tmpCtx = tmpCanvas.context;
						var graphics = canvasGraphicsFactory.createCanvasGraphics(tmpCtx);
						graphics.groupLevel = owner.groupLevel;
						this.setFillAndStrokeStyleToContext(tmpCtx, paintType, color);
						this.setScale(width, height, xstep, ystep);
						this.transformToScale(graphics);
						var tmpTranslate = [
							1,
							0,
							0,
							1,
							-topLeft[0],
							-topLeft[1]
						];
						graphics.transform.apply(graphics, tmpTranslate);
						this.clipBbox(graphics, bbox, x0, y0, x1, y1);
						graphics.executeOperatorList(operatorList);
						return tmpCanvas.canvas;
					},
					setScale: function TilingPattern_setScale(width, height, xstep, ystep) {
						this.scale = [
							width / xstep,
							height / ystep
						];
					},
					transformToScale: function TilingPattern_transformToScale(graphics) {
						var scale = this.scale;
						var tmpScale = [
							scale[0],
							0,
							0,
							scale[1],
							0,
							0
						];
						graphics.transform.apply(graphics, tmpScale);
					},
					scaleToContext: function TilingPattern_scaleToContext() {
						var scale = this.scale;
						this.ctx.scale(1 / scale[0], 1 / scale[1]);
					},
					clipBbox: function clipBbox(graphics, bbox, x0, y0, x1, y1) {
						if (bbox && isArray(bbox) && bbox.length === 4) {
							var bboxWidth = x1 - x0;
							var bboxHeight = y1 - y0;
							graphics.ctx.rect(x0, y0, bboxWidth, bboxHeight);
							graphics.clip();
							graphics.endPath();
						}
					},
					setFillAndStrokeStyleToContext: function setFillAndStrokeStyleToContext(context, paintType, color) {
						switch (paintType) {
							case PaintType.COLORED:
								var ctx = this.ctx;
								context.fillStyle = ctx.fillStyle;
								context.strokeStyle = ctx.strokeStyle;
								break;
							case PaintType.UNCOLORED:
								var cssColor = Util.makeCssRgb(color[0], color[1], color[2]);
								context.fillStyle = cssColor;
								context.strokeStyle = cssColor;
								break;
							default:
								error('Unsupported paint type: ' + paintType);
						}
					},
					getPattern: function TilingPattern_getPattern(ctx, owner) {
						var temporaryPatternCanvas = this.createPatternCanvas(owner);
						ctx = this.ctx;
						ctx.setTransform.apply(ctx, this.baseTransform);
						ctx.transform.apply(ctx, this.matrix);
						this.scaleToContext();
						return ctx.createPattern(temporaryPatternCanvas, 'repeat');
					}
				};
				return TilingPattern;
			}();
			exports.getShadingPatternFromIR = getShadingPatternFromIR;
			exports.TilingPattern = TilingPattern;

			/***/
		}),
/* 13 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";
/* WEBPACK VAR INJECTION */(function (global) {
				if (typeof PDFJS === 'undefined' || !PDFJS.compatibilityChecked) {
					var globalScope = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : this;
					var userAgent = typeof navigator !== 'undefined' && navigator.userAgent || '';
					var isAndroid = /Android/.test(userAgent);
					var isAndroidPre3 = /Android\s[0-2][^\d]/.test(userAgent);
					var isAndroidPre5 = /Android\s[0-4][^\d]/.test(userAgent);
					var isChrome = userAgent.indexOf('Chrom') >= 0;
					var isChromeWithRangeBug = /Chrome\/(39|40)\./.test(userAgent);
					var isIOSChrome = userAgent.indexOf('CriOS') >= 0;
					var isIE = userAgent.indexOf('Trident') >= 0;
					var isIOS = /\b(iPad|iPhone|iPod)(?=;)/.test(userAgent);
					var isOpera = userAgent.indexOf('Opera') >= 0;
					var isSafari = /Safari\//.test(userAgent) && !/(Chrome\/|Android\s)/.test(userAgent);
					var hasDOM = typeof window === 'object' && typeof document === 'object';
					if (typeof PDFJS === 'undefined') {
						globalScope.PDFJS = {};
					}
					PDFJS.compatibilityChecked = true;
					(function checkTypedArrayCompatibility() {
						if (typeof Uint8Array !== 'undefined') {
							if (typeof Uint8Array.prototype.subarray === 'undefined') {
								Uint8Array.prototype.subarray = function subarray(start, end) {
									return new Uint8Array(this.slice(start, end));
								};
								Float32Array.prototype.subarray = function subarray(start, end) {
									return new Float32Array(this.slice(start, end));
								};
							}
							if (typeof Float64Array === 'undefined') {
								globalScope.Float64Array = Float32Array;
							}
							return;
						}
						function subarray(start, end) {
							return new TypedArray(this.slice(start, end));
						}
						function setArrayOffset(array, offset) {
							if (arguments.length < 2) {
								offset = 0;
							}
							for (var i = 0, n = array.length; i < n; ++i, ++offset) {
								this[offset] = array[i] & 0xFF;
							}
						}
						function TypedArray(arg1) {
							var result, i, n;
							if (typeof arg1 === 'number') {
								result = [];
								for (i = 0; i < arg1; ++i) {
									result[i] = 0;
								}
							} else if ('slice' in arg1) {
								result = arg1.slice(0);
							} else {
								result = [];
								for (i = 0, n = arg1.length; i < n; ++i) {
									result[i] = arg1[i];
								}
							}
							result.subarray = subarray;
							result.buffer = result;
							result.byteLength = result.length;
							result.set = setArrayOffset;
							if (typeof arg1 === 'object' && arg1.buffer) {
								result.buffer = arg1.buffer;
							}
							return result;
						}
						globalScope.Uint8Array = TypedArray;
						globalScope.Int8Array = TypedArray;
						globalScope.Uint32Array = TypedArray;
						globalScope.Int32Array = TypedArray;
						globalScope.Uint16Array = TypedArray;
						globalScope.Float32Array = TypedArray;
						globalScope.Float64Array = TypedArray;
					}());
					(function normalizeURLObject() {
						if (!globalScope.URL) {
							globalScope.URL = globalScope.webkitURL;
						}
					}());
					(function checkObjectDefinePropertyCompatibility() {
						if (typeof Object.defineProperty !== 'undefined') {
							var definePropertyPossible = true;
							try {
								if (hasDOM) {
									Object.defineProperty(new Image(), 'id', { value: 'test' });
								}
								var Test = function Test() {
								};
								Test.prototype = {
									get id() {
									}
								};
								Object.defineProperty(new Test(), 'id', {
									value: '',
									configurable: true,
									enumerable: true,
									writable: false
								});
							} catch (e) {
								definePropertyPossible = false;
							}
							if (definePropertyPossible) {
								return;
							}
						}
						Object.defineProperty = function objectDefineProperty(obj, name, def) {
							delete obj[name];
							if ('get' in def) {
								obj.__defineGetter__(name, def['get']);
							}
							if ('set' in def) {
								obj.__defineSetter__(name, def['set']);
							}
							if ('value' in def) {
								obj.__defineSetter__(name, function objectDefinePropertySetter(value) {
									this.__defineGetter__(name, function objectDefinePropertyGetter() {
										return value;
									});
									return value;
								});
								obj[name] = def.value;
							}
						};
					}());
					(function checkXMLHttpRequestResponseCompatibility() {
						if (typeof XMLHttpRequest === 'undefined') {
							return;
						}
						var xhrPrototype = XMLHttpRequest.prototype;
						var xhr = new XMLHttpRequest();
						if (!('overrideMimeType' in xhr)) {
							Object.defineProperty(xhrPrototype, 'overrideMimeType', {
								value: function xmlHttpRequestOverrideMimeType(mimeType) {
								}
							});
						}
						if ('responseType' in xhr) {
							return;
						}
						Object.defineProperty(xhrPrototype, 'responseType', {
							get: function xmlHttpRequestGetResponseType() {
								return this._responseType || 'text';
							},
							set: function xmlHttpRequestSetResponseType(value) {
								if (value === 'text' || value === 'arraybuffer') {
									this._responseType = value;
									if (value === 'arraybuffer' && typeof this.overrideMimeType === 'function') {
										this.overrideMimeType('text/plain; charset=x-user-defined');
									}
								}
							}
						});
						if (typeof VBArray !== 'undefined') {
							Object.defineProperty(xhrPrototype, 'response', {
								get: function xmlHttpRequestResponseGet() {
									if (this.responseType === 'arraybuffer') {
										return new Uint8Array(new VBArray(this.responseBody).toArray());
									}
									return this.responseText;
								}
							});
							return;
						}
						Object.defineProperty(xhrPrototype, 'response', {
							get: function xmlHttpRequestResponseGet() {
								if (this.responseType !== 'arraybuffer') {
									return this.responseText;
								}
								var text = this.responseText;
								var i, n = text.length;
								var result = new Uint8Array(n);
								for (i = 0; i < n; ++i) {
									result[i] = text.charCodeAt(i) & 0xFF;
								}
								return result.buffer;
							}
						});
					}());
					(function checkWindowBtoaCompatibility() {
						if ('btoa' in globalScope) {
							return;
						}
						var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
						globalScope.btoa = function (chars) {
							var buffer = '';
							var i, n;
							for (i = 0, n = chars.length; i < n; i += 3) {
								var b1 = chars.charCodeAt(i) & 0xFF;
								var b2 = chars.charCodeAt(i + 1) & 0xFF;
								var b3 = chars.charCodeAt(i + 2) & 0xFF;
								var d1 = b1 >> 2, d2 = (b1 & 3) << 4 | b2 >> 4;
								var d3 = i + 1 < n ? (b2 & 0xF) << 2 | b3 >> 6 : 64;
								var d4 = i + 2 < n ? b3 & 0x3F : 64;
								buffer += digits.charAt(d1) + digits.charAt(d2) + digits.charAt(d3) + digits.charAt(d4);
							}
							return buffer;
						};
					}());
					(function checkWindowAtobCompatibility() {
						if ('atob' in globalScope) {
							return;
						}
						var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
						globalScope.atob = function (input) {
							input = input.replace(/=+$/, '');
							if (input.length % 4 === 1) {
								throw new Error('bad atob input');
							}
							for (var bc = 0, bs, buffer, idx = 0, output = ''; buffer = input.charAt(idx++); ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer, bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0) {
								buffer = digits.indexOf(buffer);
							}
							return output;
						};
					}());
					(function checkFunctionPrototypeBindCompatibility() {
						if (typeof Function.prototype.bind !== 'undefined') {
							return;
						}
						Function.prototype.bind = function functionPrototypeBind(obj) {
							var fn = this, headArgs = Array.prototype.slice.call(arguments, 1);
							var bound = function functionPrototypeBindBound() {
								var args = headArgs.concat(Array.prototype.slice.call(arguments));
								return fn.apply(obj, args);
							};
							return bound;
						};
					}());
					(function checkDatasetProperty() {
						if (!hasDOM) {
							return;
						}
						var div = document.createElement('div');
						if ('dataset' in div) {
							return;
						}
						Object.defineProperty(HTMLElement.prototype, 'dataset', {
							get: function () {
								if (this._dataset) {
									return this._dataset;
								}
								var dataset = {};
								for (var j = 0, jj = this.attributes.length; j < jj; j++) {
									var attribute = this.attributes[j];
									if (attribute.name.substring(0, 5) !== 'data-') {
										continue;
									}
									var key = attribute.name.substring(5).replace(/\-([a-z])/g, function (all, ch) {
										return ch.toUpperCase();
									});
									dataset[key] = attribute.value;
								}
								Object.defineProperty(this, '_dataset', {
									value: dataset,
									writable: false,
									enumerable: false
								});
								return dataset;
							},
							enumerable: true
						});
					}());
					(function checkClassListProperty() {
						function changeList(element, itemName, add, remove) {
							var s = element.className || '';
							var list = s.split(/\s+/g);
							if (list[0] === '') {
								list.shift();
							}
							var index = list.indexOf(itemName);
							if (index < 0 && add) {
								list.push(itemName);
							}
							if (index >= 0 && remove) {
								list.splice(index, 1);
							}
							element.className = list.join(' ');
							return index >= 0;
						}
						if (!hasDOM) {
							return;
						}
						var div = document.createElement('div');
						if ('classList' in div) {
							return;
						}
						var classListPrototype = {
							add: function (name) {
								changeList(this.element, name, true, false);
							},
							contains: function (name) {
								return changeList(this.element, name, false, false);
							},
							remove: function (name) {
								changeList(this.element, name, false, true);
							},
							toggle: function (name) {
								changeList(this.element, name, true, true);
							}
						};
						Object.defineProperty(HTMLElement.prototype, 'classList', {
							get: function () {
								if (this._classList) {
									return this._classList;
								}
								var classList = Object.create(classListPrototype, {
									element: {
										value: this,
										writable: false,
										enumerable: true
									}
								});
								Object.defineProperty(this, '_classList', {
									value: classList,
									writable: false,
									enumerable: false
								});
								return classList;
							},
							enumerable: true
						});
					}());
					(function checkWorkerConsoleCompatibility() {
						if (typeof importScripts === 'undefined' || 'console' in globalScope) {
							return;
						}
						var consoleTimer = {};
						var workerConsole = {
							log: function log() {
								var args = Array.prototype.slice.call(arguments);
								globalScope.postMessage({
									targetName: 'main',
									action: 'console_log',
									data: args
								});
							},
							error: function error() {
								var args = Array.prototype.slice.call(arguments);
								globalScope.postMessage({
									targetName: 'main',
									action: 'console_error',
									data: args
								});
							},
							time: function time(name) {
								consoleTimer[name] = Date.now();
							},
							timeEnd: function timeEnd(name) {
								var time = consoleTimer[name];
								if (!time) {
									throw new Error('Unknown timer name ' + name);
								}
								this.log('Timer:', name, Date.now() - time);
							}
						};
						globalScope.console = workerConsole;
					}());
					(function checkConsoleCompatibility() {
						if (!hasDOM) {
							return;
						}
						if (!('console' in window)) {
							window.console = {
								log: function () {
								},
								error: function () {
								},
								warn: function () {
								}
							};
							return;
						}
						if (!('bind' in console.log)) {
							console.log = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.log);
							console.error = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.error);
							console.warn = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.warn);
							return;
						}
					}());
					(function checkOnClickCompatibility() {
						function ignoreIfTargetDisabled(event) {
							if (isDisabled(event.target)) {
								event.stopPropagation();
							}
						}
						function isDisabled(node) {
							return node.disabled || node.parentNode && isDisabled(node.parentNode);
						}
						if (isOpera) {
							document.addEventListener('click', ignoreIfTargetDisabled, true);
						}
					}());
					(function checkOnBlobSupport() {
						if (isIE || isIOSChrome) {
							PDFJS.disableCreateObjectURL = true;
						}
					}());
					(function checkNavigatorLanguage() {
						if (typeof navigator === 'undefined') {
							return;
						}
						if ('language' in navigator) {
							return;
						}
						PDFJS.locale = navigator.userLanguage || 'en-US';
					}());
					(function checkRangeRequests() {
						if (isSafari || isAndroidPre3 || isChromeWithRangeBug || isIOS) {
							PDFJS.disableRange = true;
							PDFJS.disableStream = true;
						}
					}());
					(function checkHistoryManipulation() {
						if (!hasDOM) {
							return;
						}
						if (!history.pushState || isAndroidPre3) {
							PDFJS.disableHistory = true;
						}
					}());
					(function checkSetPresenceInImageData() {
						if (!hasDOM) {
							return;
						}
						if (window.CanvasPixelArray) {
							if (typeof window.CanvasPixelArray.prototype.set !== 'function') {
								window.CanvasPixelArray.prototype.set = function (arr) {
									for (var i = 0, ii = this.length; i < ii; i++) {
										this[i] = arr[i];
									}
								};
							}
						} else {
							var polyfill = false, versionMatch;
							if (isChrome) {
								versionMatch = userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
								polyfill = versionMatch && parseInt(versionMatch[2]) < 21;
							} else if (isAndroid) {
								polyfill = isAndroidPre5;
							} else if (isSafari) {
								versionMatch = userAgent.match(/Version\/([0-9]+)\.([0-9]+)\.([0-9]+) Safari\//);
								polyfill = versionMatch && parseInt(versionMatch[1]) < 6;
							}
							if (polyfill) {
								var contextPrototype = window.CanvasRenderingContext2D.prototype;
								var createImageData = contextPrototype.createImageData;
								contextPrototype.createImageData = function (w, h) {
									var imageData = createImageData.call(this, w, h);
									imageData.data.set = function (arr) {
										for (var i = 0, ii = this.length; i < ii; i++) {
											this[i] = arr[i];
										}
									};
									return imageData;
								};
								contextPrototype = null;
							}
						}
					}());
					(function checkRequestAnimationFrame() {
						function fakeRequestAnimationFrame(callback) {
							window.setTimeout(callback, 20);
						}
						if (!hasDOM) {
							return;
						}
						if (isIOS) {
							window.requestAnimationFrame = fakeRequestAnimationFrame;
							return;
						}
						if ('requestAnimationFrame' in window) {
							return;
						}
						window.requestAnimationFrame = window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || fakeRequestAnimationFrame;
					}());
					(function checkCanvasSizeLimitation() {
						if (isIOS || isAndroid) {
							PDFJS.maxCanvasPixels = 5242880;
						}
					}());
					(function checkFullscreenSupport() {
						if (!hasDOM) {
							return;
						}
						if (isIE && window.parent !== window) {
							PDFJS.disableFullscreen = true;
						}
					}());
					(function checkCurrentScript() {
						if (!hasDOM) {
							return;
						}
						if ('currentScript' in document) {
							return;
						}
						Object.defineProperty(document, 'currentScript', {
							get: function () {
								var scripts = document.getElementsByTagName('script');
								return scripts[scripts.length - 1];
							},
							enumerable: true,
							configurable: true
						});
					}());
					(function checkInputTypeNumberAssign() {
						if (!hasDOM) {
							return;
						}
						var el = document.createElement('input');
						try {
							el.type = 'number';
						} catch (ex) {
							var inputProto = el.constructor.prototype;
							var typeProperty = Object.getOwnPropertyDescriptor(inputProto, 'type');
							Object.defineProperty(inputProto, 'type', {
								get: function () {
									return typeProperty.get.call(this);
								},
								set: function (value) {
									typeProperty.set.call(this, value === 'number' ? 'text' : value);
								},
								enumerable: true,
								configurable: true
							});
						}
					}());
					(function checkDocumentReadyState() {
						if (!hasDOM) {
							return;
						}
						if (!document.attachEvent) {
							return;
						}
						var documentProto = document.constructor.prototype;
						var readyStateProto = Object.getOwnPropertyDescriptor(documentProto, 'readyState');
						Object.defineProperty(documentProto, 'readyState', {
							get: function () {
								var value = readyStateProto.get.call(this);
								return value === 'interactive' ? 'loading' : value;
							},
							set: function (value) {
								readyStateProto.set.call(this, value);
							},
							enumerable: true,
							configurable: true
						});
					}());
					(function checkChildNodeRemove() {
						if (!hasDOM) {
							return;
						}
						if (typeof Element.prototype.remove !== 'undefined') {
							return;
						}
						Element.prototype.remove = function () {
							if (this.parentNode) {
								this.parentNode.removeChild(this);
							}
						};
					}());
					(function checkPromise() {
						if (globalScope.Promise) {
							if (typeof globalScope.Promise.all !== 'function') {
								globalScope.Promise.all = function (iterable) {
									var count = 0, results = [], resolve, reject;
									var promise = new globalScope.Promise(function (resolve_, reject_) {
										resolve = resolve_;
										reject = reject_;
									});
									iterable.forEach(function (p, i) {
										count++;
										p.then(function (result) {
											results[i] = result;
											count--;
											if (count === 0) {
												resolve(results);
											}
										}, reject);
									});
									if (count === 0) {
										resolve(results);
									}
									return promise;
								};
							}
							if (typeof globalScope.Promise.resolve !== 'function') {
								globalScope.Promise.resolve = function (value) {
									return new globalScope.Promise(function (resolve) {
										resolve(value);
									});
								};
							}
							if (typeof globalScope.Promise.reject !== 'function') {
								globalScope.Promise.reject = function (reason) {
									return new globalScope.Promise(function (resolve, reject) {
										reject(reason);
									});
								};
							}
							if (typeof globalScope.Promise.prototype.catch !== 'function') {
								globalScope.Promise.prototype.catch = function (onReject) {
									return globalScope.Promise.prototype.then(undefined, onReject);
								};
							}
							return;
						}
						var STATUS_PENDING = 0;
						var STATUS_RESOLVED = 1;
						var STATUS_REJECTED = 2;
						var REJECTION_TIMEOUT = 500;
						var HandlerManager = {
							handlers: [],
							running: false,
							unhandledRejections: [],
							pendingRejectionCheck: false,
							scheduleHandlers: function scheduleHandlers(promise) {
								if (promise._status === STATUS_PENDING) {
									return;
								}
								this.handlers = this.handlers.concat(promise._handlers);
								promise._handlers = [];
								if (this.running) {
									return;
								}
								this.running = true;
								setTimeout(this.runHandlers.bind(this), 0);
							},
							runHandlers: function runHandlers() {
								var RUN_TIMEOUT = 1;
								var timeoutAt = Date.now() + RUN_TIMEOUT;
								while (this.handlers.length > 0) {
									var handler = this.handlers.shift();
									var nextStatus = handler.thisPromise._status;
									var nextValue = handler.thisPromise._value;
									try {
										if (nextStatus === STATUS_RESOLVED) {
											if (typeof handler.onResolve === 'function') {
												nextValue = handler.onResolve(nextValue);
											}
										} else if (typeof handler.onReject === 'function') {
											nextValue = handler.onReject(nextValue);
											nextStatus = STATUS_RESOLVED;
											if (handler.thisPromise._unhandledRejection) {
												this.removeUnhandeledRejection(handler.thisPromise);
											}
										}
									} catch (ex) {
										nextStatus = STATUS_REJECTED;
										nextValue = ex;
									}
									handler.nextPromise._updateStatus(nextStatus, nextValue);
									if (Date.now() >= timeoutAt) {
										break;
									}
								}
								if (this.handlers.length > 0) {
									setTimeout(this.runHandlers.bind(this), 0);
									return;
								}
								this.running = false;
							},
							addUnhandledRejection: function addUnhandledRejection(promise) {
								this.unhandledRejections.push({
									promise: promise,
									time: Date.now()
								});
								this.scheduleRejectionCheck();
							},
							removeUnhandeledRejection: function removeUnhandeledRejection(promise) {
								promise._unhandledRejection = false;
								for (var i = 0; i < this.unhandledRejections.length; i++) {
									if (this.unhandledRejections[i].promise === promise) {
										this.unhandledRejections.splice(i);
										i--;
									}
								}
							},
							scheduleRejectionCheck: function scheduleRejectionCheck() {
								if (this.pendingRejectionCheck) {
									return;
								}
								this.pendingRejectionCheck = true;
								setTimeout(function rejectionCheck() {
									this.pendingRejectionCheck = false;
									var now = Date.now();
									for (var i = 0; i < this.unhandledRejections.length; i++) {
										if (now - this.unhandledRejections[i].time > REJECTION_TIMEOUT) {
											var unhandled = this.unhandledRejections[i].promise._value;
											var msg = 'Unhandled rejection: ' + unhandled;
											if (unhandled.stack) {
												msg += '\n' + unhandled.stack;
											}
											try {
												throw new Error(msg);
											} catch (_) {
												console.warn(msg);
											}
											this.unhandledRejections.splice(i);
											i--;
										}
									}
									if (this.unhandledRejections.length) {
										this.scheduleRejectionCheck();
									}
								}.bind(this), REJECTION_TIMEOUT);
							}
						};
						var Promise = function Promise(resolver) {
							this._status = STATUS_PENDING;
							this._handlers = [];
							try {
								resolver.call(this, this._resolve.bind(this), this._reject.bind(this));
							} catch (e) {
								this._reject(e);
							}
						};
						Promise.all = function Promise_all(promises) {
							var resolveAll, rejectAll;
							var deferred = new Promise(function (resolve, reject) {
								resolveAll = resolve;
								rejectAll = reject;
							});
							var unresolved = promises.length;
							var results = [];
							if (unresolved === 0) {
								resolveAll(results);
								return deferred;
							}
							function reject(reason) {
								if (deferred._status === STATUS_REJECTED) {
									return;
								}
								results = [];
								rejectAll(reason);
							}
							for (var i = 0, ii = promises.length; i < ii; ++i) {
								var promise = promises[i];
								var resolve = function (i) {
									return function (value) {
										if (deferred._status === STATUS_REJECTED) {
											return;
										}
										results[i] = value;
										unresolved--;
										if (unresolved === 0) {
											resolveAll(results);
										}
									};
								}(i);
								if (Promise.isPromise(promise)) {
									promise.then(resolve, reject);
								} else {
									resolve(promise);
								}
							}
							return deferred;
						};
						Promise.isPromise = function Promise_isPromise(value) {
							return value && typeof value.then === 'function';
						};
						Promise.resolve = function Promise_resolve(value) {
							return new Promise(function (resolve) {
								resolve(value);
							});
						};
						Promise.reject = function Promise_reject(reason) {
							return new Promise(function (resolve, reject) {
								reject(reason);
							});
						};
						Promise.prototype = {
							_status: null,
							_value: null,
							_handlers: null,
							_unhandledRejection: null,
							_updateStatus: function Promise__updateStatus(status, value) {
								if (this._status === STATUS_RESOLVED || this._status === STATUS_REJECTED) {
									return;
								}
								if (status === STATUS_RESOLVED && Promise.isPromise(value)) {
									value.then(this._updateStatus.bind(this, STATUS_RESOLVED), this._updateStatus.bind(this, STATUS_REJECTED));
									return;
								}
								this._status = status;
								this._value = value;
								if (status === STATUS_REJECTED && this._handlers.length === 0) {
									this._unhandledRejection = true;
									HandlerManager.addUnhandledRejection(this);
								}
								HandlerManager.scheduleHandlers(this);
							},
							_resolve: function Promise_resolve(value) {
								this._updateStatus(STATUS_RESOLVED, value);
							},
							_reject: function Promise_reject(reason) {
								this._updateStatus(STATUS_REJECTED, reason);
							},
							then: function Promise_then(onResolve, onReject) {
								var nextPromise = new Promise(function (resolve, reject) {
									this.resolve = resolve;
									this.reject = reject;
								});
								this._handlers.push({
									thisPromise: this,
									onResolve: onResolve,
									onReject: onReject,
									nextPromise: nextPromise
								});
								HandlerManager.scheduleHandlers(this);
								return nextPromise;
							},
							catch: function Promise_catch(onReject) {
								return this.then(undefined, onReject);
							}
						};
						globalScope.Promise = Promise;
					}());
					(function checkWeakMap() {
						if (globalScope.WeakMap) {
							return;
						}
						var id = 0;
						function WeakMap() {
							this.id = '$weakmap' + id++;
						}
						WeakMap.prototype = {
							has: function (obj) {
								return !!Object.getOwnPropertyDescriptor(obj, this.id);
							},
							get: function (obj, defaultValue) {
								return this.has(obj) ? obj[this.id] : defaultValue;
							},
							set: function (obj, value) {
								Object.defineProperty(obj, this.id, {
									value: value,
									enumerable: false,
									configurable: true
								});
							},
							delete: function (obj) {
								delete obj[this.id];
							}
						};
						globalScope.WeakMap = WeakMap;
					}());
					(function checkURLConstructor() {
						var hasWorkingUrl = false;
						try {
							if (typeof URL === 'function' && typeof URL.prototype === 'object' && 'origin' in URL.prototype) {
								var u = new URL('b', 'http://a');
								u.pathname = 'c%20d';
								hasWorkingUrl = u.href === 'http://a/c%20d';
							}
						} catch (e) {
						}
						if (hasWorkingUrl) {
							return;
						}
						var relative = Object.create(null);
						relative['ftp'] = 21;
						relative['file'] = 0;
						relative['gopher'] = 70;
						relative['http'] = 80;
						relative['https'] = 443;
						relative['ws'] = 80;
						relative['wss'] = 443;
						var relativePathDotMapping = Object.create(null);
						relativePathDotMapping['%2e'] = '.';
						relativePathDotMapping['.%2e'] = '..';
						relativePathDotMapping['%2e.'] = '..';
						relativePathDotMapping['%2e%2e'] = '..';
						function isRelativeScheme(scheme) {
							return relative[scheme] !== undefined;
						}
						function invalid() {
							clear.call(this);
							this._isInvalid = true;
						}
						function IDNAToASCII(h) {
							if (h === '') {
								invalid.call(this);
							}
							return h.toLowerCase();
						}
						function percentEscape(c) {
							var unicode = c.charCodeAt(0);
							if (unicode > 0x20 && unicode < 0x7F && [
								0x22,
								0x23,
								0x3C,
								0x3E,
								0x3F,
								0x60
							].indexOf(unicode) === -1) {
								return c;
							}
							return encodeURIComponent(c);
						}
						function percentEscapeQuery(c) {
							var unicode = c.charCodeAt(0);
							if (unicode > 0x20 && unicode < 0x7F && [
								0x22,
								0x23,
								0x3C,
								0x3E,
								0x60
							].indexOf(unicode) === -1) {
								return c;
							}
							return encodeURIComponent(c);
						}
						var EOF, ALPHA = /[a-zA-Z]/, ALPHANUMERIC = /[a-zA-Z0-9\+\-\.]/;
						function parse(input, stateOverride, base) {
							function err(message) {
								errors.push(message);
							}
							var state = stateOverride || 'scheme start', cursor = 0, buffer = '', seenAt = false, seenBracket = false, errors = [];
							loop:
							while ((input[cursor - 1] !== EOF || cursor === 0) && !this._isInvalid) {
								var c = input[cursor];
								switch (state) {
									case 'scheme start':
										if (c && ALPHA.test(c)) {
											buffer += c.toLowerCase();
											state = 'scheme';
										} else if (!stateOverride) {
											buffer = '';
											state = 'no scheme';
											continue;
										} else {
											err('Invalid scheme.');
											break loop;
										}
										break;
									case 'scheme':
										if (c && ALPHANUMERIC.test(c)) {
											buffer += c.toLowerCase();
										} else if (c === ':') {
											this._scheme = buffer;
											buffer = '';
											if (stateOverride) {
												break loop;
											}
											if (isRelativeScheme(this._scheme)) {
												this._isRelative = true;
											}
											if (this._scheme === 'file') {
												state = 'relative';
											} else if (this._isRelative && base && base._scheme === this._scheme) {
												state = 'relative or authority';
											} else if (this._isRelative) {
												state = 'authority first slash';
											} else {
												state = 'scheme data';
											}
										} else if (!stateOverride) {
											buffer = '';
											cursor = 0;
											state = 'no scheme';
											continue;
										} else if (EOF === c) {
											break loop;
										} else {
											err('Code point not allowed in scheme: ' + c);
											break loop;
										}
										break;
									case 'scheme data':
										if (c === '?') {
											this._query = '?';
											state = 'query';
										} else if (c === '#') {
											this._fragment = '#';
											state = 'fragment';
										} else {
											if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
												this._schemeData += percentEscape(c);
											}
										}
										break;
									case 'no scheme':
										if (!base || !isRelativeScheme(base._scheme)) {
											err('Missing scheme.');
											invalid.call(this);
										} else {
											state = 'relative';
											continue;
										}
										break;
									case 'relative or authority':
										if (c === '/' && input[cursor + 1] === '/') {
											state = 'authority ignore slashes';
										} else {
											err('Expected /, got: ' + c);
											state = 'relative';
											continue;
										}
										break;
									case 'relative':
										this._isRelative = true;
										if ('file' !== this._scheme) {
											this._scheme = base._scheme;
										}
										if (EOF === c) {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = base._query;
											this._username = base._username;
											this._password = base._password;
											break loop;
										} else if (c === '/' || c === '\\') {
											if (c === '\\') {
												err('\\ is an invalid code point.');
											}
											state = 'relative slash';
										} else if (c === '?') {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = '?';
											this._username = base._username;
											this._password = base._password;
											state = 'query';
										} else if (c === '#') {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = base._query;
											this._fragment = '#';
											this._username = base._username;
											this._password = base._password;
											state = 'fragment';
										} else {
											var nextC = input[cursor + 1];
											var nextNextC = input[cursor + 2];
											if ('file' !== this._scheme || !ALPHA.test(c) || nextC !== ':' && nextC !== '|' || EOF !== nextNextC && '/' !== nextNextC && '\\' !== nextNextC && '?' !== nextNextC && '#' !== nextNextC) {
												this._host = base._host;
												this._port = base._port;
												this._username = base._username;
												this._password = base._password;
												this._path = base._path.slice();
												this._path.pop();
											}
											state = 'relative path';
											continue;
										}
										break;
									case 'relative slash':
										if (c === '/' || c === '\\') {
											if (c === '\\') {
												err('\\ is an invalid code point.');
											}
											if (this._scheme === 'file') {
												state = 'file host';
											} else {
												state = 'authority ignore slashes';
											}
										} else {
											if ('file' !== this._scheme) {
												this._host = base._host;
												this._port = base._port;
												this._username = base._username;
												this._password = base._password;
											}
											state = 'relative path';
											continue;
										}
										break;
									case 'authority first slash':
										if (c === '/') {
											state = 'authority second slash';
										} else {
											err('Expected \'/\', got: ' + c);
											state = 'authority ignore slashes';
											continue;
										}
										break;
									case 'authority second slash':
										state = 'authority ignore slashes';
										if ('/' !== c) {
											err('Expected \'/\', got: ' + c);
											continue;
										}
										break;
									case 'authority ignore slashes':
										if ('/' !== c && '\\' !== c) {
											state = 'authority';
											continue;
										} else {
											err('Expected authority, got: ' + c);
										}
										break;
									case 'authority':
										if (c === '@') {
											if (seenAt) {
												err('@ already seen.');
												buffer += '%40';
											}
											seenAt = true;
											for (var i = 0; i < buffer.length; i++) {
												var cp = buffer[i];
												if (cp === '\t' || cp === '\n' || cp === '\r') {
													err('Invalid whitespace in authority.');
													continue;
												}
												if (cp === ':' && this._password === null) {
													this._password = '';
													continue;
												}
												var tempC = percentEscape(cp);
												if (null !== this._password) {
													this._password += tempC;
												} else {
													this._username += tempC;
												}
											}
											buffer = '';
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											cursor -= buffer.length;
											buffer = '';
											state = 'host';
											continue;
										} else {
											buffer += c;
										}
										break;
									case 'file host':
										if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											if (buffer.length === 2 && ALPHA.test(buffer[0]) && (buffer[1] === ':' || buffer[1] === '|')) {
												state = 'relative path';
											} else if (buffer.length === 0) {
												state = 'relative path start';
											} else {
												this._host = IDNAToASCII.call(this, buffer);
												buffer = '';
												state = 'relative path start';
											}
											continue;
										} else if (c === '\t' || c === '\n' || c === '\r') {
											err('Invalid whitespace in file host.');
										} else {
											buffer += c;
										}
										break;
									case 'host':
									case 'hostname':
										if (c === ':' && !seenBracket) {
											this._host = IDNAToASCII.call(this, buffer);
											buffer = '';
											state = 'port';
											if (stateOverride === 'hostname') {
												break loop;
											}
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											this._host = IDNAToASCII.call(this, buffer);
											buffer = '';
											state = 'relative path start';
											if (stateOverride) {
												break loop;
											}
											continue;
										} else if ('\t' !== c && '\n' !== c && '\r' !== c) {
											if (c === '[') {
												seenBracket = true;
											} else if (c === ']') {
												seenBracket = false;
											}
											buffer += c;
										} else {
											err('Invalid code point in host/hostname: ' + c);
										}
										break;
									case 'port':
										if (/[0-9]/.test(c)) {
											buffer += c;
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#' || stateOverride) {
											if ('' !== buffer) {
												var temp = parseInt(buffer, 10);
												if (temp !== relative[this._scheme]) {
													this._port = temp + '';
												}
												buffer = '';
											}
											if (stateOverride) {
												break loop;
											}
											state = 'relative path start';
											continue;
										} else if (c === '\t' || c === '\n' || c === '\r') {
											err('Invalid code point in port: ' + c);
										} else {
											invalid.call(this);
										}
										break;
									case 'relative path start':
										if (c === '\\') {
											err('\'\\\' not allowed in path.');
										}
										state = 'relative path';
										if ('/' !== c && '\\' !== c) {
											continue;
										}
										break;
									case 'relative path':
										if (c === EOF || c === '/' || c === '\\' || !stateOverride && (c === '?' || c === '#')) {
											if (c === '\\') {
												err('\\ not allowed in relative path.');
											}
											var tmp;
											if (tmp = relativePathDotMapping[buffer.toLowerCase()]) {
												buffer = tmp;
											}
											if (buffer === '..') {
												this._path.pop();
												if ('/' !== c && '\\' !== c) {
													this._path.push('');
												}
											} else if (buffer === '.' && '/' !== c && '\\' !== c) {
												this._path.push('');
											} else if ('.' !== buffer) {
												if (this._scheme === 'file' && this._path.length === 0 && buffer.length === 2 && ALPHA.test(buffer[0]) && buffer[1] === '|') {
													buffer = buffer[0] + ':';
												}
												this._path.push(buffer);
											}
											buffer = '';
											if (c === '?') {
												this._query = '?';
												state = 'query';
											} else if (c === '#') {
												this._fragment = '#';
												state = 'fragment';
											}
										} else if ('\t' !== c && '\n' !== c && '\r' !== c) {
											buffer += percentEscape(c);
										}
										break;
									case 'query':
										if (!stateOverride && c === '#') {
											this._fragment = '#';
											state = 'fragment';
										} else if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
											this._query += percentEscapeQuery(c);
										}
										break;
									case 'fragment':
										if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
											this._fragment += c;
										}
										break;
								}
								cursor++;
							}
						}
						function clear() {
							this._scheme = '';
							this._schemeData = '';
							this._username = '';
							this._password = null;
							this._host = '';
							this._port = '';
							this._path = [];
							this._query = '';
							this._fragment = '';
							this._isInvalid = false;
							this._isRelative = false;
						}
						function JURL(url, base) {
							if (base !== undefined && !(base instanceof JURL)) {
								base = new JURL(String(base));
							}
							this._url = url;
							clear.call(this);
							var input = url.replace(/^[ \t\r\n\f]+|[ \t\r\n\f]+$/g, '');
							parse.call(this, input, null, base);
						}
						JURL.prototype = {
							toString: function () {
								return this.href;
							},
							get href() {
								if (this._isInvalid) {
									return this._url;
								}
								var authority = '';
								if ('' !== this._username || null !== this._password) {
									authority = this._username + (null !== this._password ? ':' + this._password : '') + '@';
								}
								return this.protocol + (this._isRelative ? '//' + authority + this.host : '') + this.pathname + this._query + this._fragment;
							},
							set href(href) {
								clear.call(this);
								parse.call(this, href);
							},
							get protocol() {
								return this._scheme + ':';
							},
							set protocol(protocol) {
								if (this._isInvalid) {
									return;
								}
								parse.call(this, protocol + ':', 'scheme start');
							},
							get host() {
								return this._isInvalid ? '' : this._port ? this._host + ':' + this._port : this._host;
							},
							set host(host) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, host, 'host');
							},
							get hostname() {
								return this._host;
							},
							set hostname(hostname) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, hostname, 'hostname');
							},
							get port() {
								return this._port;
							},
							set port(port) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, port, 'port');
							},
							get pathname() {
								return this._isInvalid ? '' : this._isRelative ? '/' + this._path.join('/') : this._schemeData;
							},
							set pathname(pathname) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								this._path = [];
								parse.call(this, pathname, 'relative path start');
							},
							get search() {
								return this._isInvalid || !this._query || this._query === '?' ? '' : this._query;
							},
							set search(search) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								this._query = '?';
								if (search[0] === '?') {
									search = search.slice(1);
								}
								parse.call(this, search, 'query');
							},
							get hash() {
								return this._isInvalid || !this._fragment || this._fragment === '#' ? '' : this._fragment;
							},
							set hash(hash) {
								if (this._isInvalid) {
									return;
								}
								this._fragment = '#';
								if (hash[0] === '#') {
									hash = hash.slice(1);
								}
								parse.call(this, hash, 'fragment');
							},
							get origin() {
								var host;
								if (this._isInvalid || !this._scheme) {
									return '';
								}
								switch (this._scheme) {
									case 'data':
									case 'file':
									case 'javascript':
									case 'mailto':
										return 'null';
								}
								host = this.host;
								if (!host) {
									return '';
								}
								return this._scheme + '://' + host;
							}
						};
						var OriginalURL = globalScope.URL;
						if (OriginalURL) {
							JURL.createObjectURL = function (blob) {
								return OriginalURL.createObjectURL.apply(OriginalURL, arguments);
							};
							JURL.revokeObjectURL = function (url) {
								OriginalURL.revokeObjectURL(url);
							};
						}
						globalScope.URL = JURL;
					}());
				}
				/* WEBPACK VAR INJECTION */
			}.call(exports, __w_pdfjs_require__(6)))

			/***/
		}),
/* 14 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var pdfjsVersion = '1.7.354';
			var pdfjsBuild = '0f7548ba';
			var pdfjsSharedUtil = __w_pdfjs_require__(0);
			var pdfjsDisplayGlobal = __w_pdfjs_require__(9);
			var pdfjsDisplayAPI = __w_pdfjs_require__(3);
			var pdfjsDisplayTextLayer = __w_pdfjs_require__(5);
			var pdfjsDisplayAnnotationLayer = __w_pdfjs_require__(2);
			var pdfjsDisplayDOMUtils = __w_pdfjs_require__(1);
			var pdfjsDisplaySVG = __w_pdfjs_require__(4);
			exports.PDFJS = pdfjsDisplayGlobal.PDFJS;
			exports.build = pdfjsDisplayAPI.build;
			exports.version = pdfjsDisplayAPI.version;
			exports.getDocument = pdfjsDisplayAPI.getDocument;
			exports.PDFDataRangeTransport = pdfjsDisplayAPI.PDFDataRangeTransport;
			exports.PDFWorker = pdfjsDisplayAPI.PDFWorker;
			exports.renderTextLayer = pdfjsDisplayTextLayer.renderTextLayer;
			exports.AnnotationLayer = pdfjsDisplayAnnotationLayer.AnnotationLayer;
			exports.CustomStyle = pdfjsDisplayDOMUtils.CustomStyle;
			exports.createPromiseCapability = pdfjsSharedUtil.createPromiseCapability;
			exports.PasswordResponses = pdfjsSharedUtil.PasswordResponses;
			exports.InvalidPDFException = pdfjsSharedUtil.InvalidPDFException;
			exports.MissingPDFException = pdfjsSharedUtil.MissingPDFException;
			exports.SVGGraphics = pdfjsDisplaySVG.SVGGraphics;
			exports.UnexpectedResponseException = pdfjsSharedUtil.UnexpectedResponseException;
			exports.OPS = pdfjsSharedUtil.OPS;
			exports.UNSUPPORTED_FEATURES = pdfjsSharedUtil.UNSUPPORTED_FEATURES;
			exports.isValidUrl = pdfjsDisplayDOMUtils.isValidUrl;
			exports.createValidAbsoluteUrl = pdfjsSharedUtil.createValidAbsoluteUrl;
			exports.createObjectURL = pdfjsSharedUtil.createObjectURL;
			exports.removeNullCharacters = pdfjsSharedUtil.removeNullCharacters;
			exports.shadow = pdfjsSharedUtil.shadow;
			exports.createBlob = pdfjsSharedUtil.createBlob;
			exports.getFilenameFromUrl = pdfjsDisplayDOMUtils.getFilenameFromUrl;
			exports.addLinkAttributes = pdfjsDisplayDOMUtils.addLinkAttributes;

			/***/
		})
/******/]);
});/* Copyright 2012 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use strict';

var FontInspector = (function FontInspectorClosure() {
	var fonts;
	var active = false;
	var fontAttribute = 'data-font-name';
	function removeSelection() {
		var divs = document.querySelectorAll('div[' + fontAttribute + ']');
		for (var i = 0, ii = divs.length; i < ii; ++i) {
			var div = divs[i];
			div.className = '';
		}
	}
	function resetSelection() {
		var divs = document.querySelectorAll('div[' + fontAttribute + ']');
		for (var i = 0, ii = divs.length; i < ii; ++i) {
			var div = divs[i];
			div.className = 'debuggerHideText';
		}
	}
	function selectFont(fontName, show) {
		var divs = document.querySelectorAll('div[' + fontAttribute + '=' +
			fontName + ']');
		for (var i = 0, ii = divs.length; i < ii; ++i) {
			var div = divs[i];
			div.className = show ? 'debuggerShowText' : 'debuggerHideText';
		}
	}
	function textLayerClick(e) {
		if (!e.target.dataset.fontName ||
			e.target.tagName.toUpperCase() !== 'DIV') {
			return;
		}
		var fontName = e.target.dataset.fontName;
		var selects = document.getElementsByTagName('input');
		for (var i = 0; i < selects.length; ++i) {
			var select = selects[i];
			if (select.dataset.fontName !== fontName) {
				continue;
			}
			select.checked = !select.checked;
			selectFont(fontName, select.checked);
			select.scrollIntoView();
		}
	}
	return {
		// Properties/functions needed by PDFBug.
		id: 'FontInspector',
		name: 'Font Inspector',
		panel: null,
		manager: null,
		init: function init(pdfjsLib) {
			var panel = this.panel;
			panel.setAttribute('style', 'padding: 5px;');
			var tmp = document.createElement('button');
			tmp.addEventListener('click', resetSelection);
			tmp.textContent = 'Refresh';
			panel.appendChild(tmp);

			fonts = document.createElement('div');
			panel.appendChild(fonts);
		},
		cleanup: function cleanup() {
			fonts.textContent = '';
		},
		enabled: false,
		get active() {
			return active;
		},
		set active(value) {
			active = value;
			if (active) {
				document.body.addEventListener('click', textLayerClick, true);
				resetSelection();
			} else {
				document.body.removeEventListener('click', textLayerClick, true);
				removeSelection();
			}
		},
		// FontInspector specific functions.
		fontAdded: function fontAdded(fontObj, url) {
			function properties(obj, list) {
				var moreInfo = document.createElement('table');
				for (var i = 0; i < list.length; i++) {
					var tr = document.createElement('tr');
					var td1 = document.createElement('td');
					td1.textContent = list[i];
					tr.appendChild(td1);
					var td2 = document.createElement('td');
					td2.textContent = obj[list[i]].toString();
					tr.appendChild(td2);
					moreInfo.appendChild(tr);
				}
				return moreInfo;
			}
			var moreInfo = properties(fontObj, ['name', 'type']);
			var fontName = fontObj.loadedName;
			var font = document.createElement('div');
			var name = document.createElement('span');
			name.textContent = fontName;
			var download = document.createElement('a');
			if (url) {
				url = /url\(['"]?([^\)"']+)/.exec(url);
				download.href = url[1];
			} else if (fontObj.data) {
				url = URL.createObjectURL(new Blob([fontObj.data], {
					type: fontObj.mimeType
				}));
				download.href = url;
			}
			download.textContent = 'Download';
			var logIt = document.createElement('a');
			logIt.href = '';
			logIt.textContent = 'Log';
			logIt.addEventListener('click', function (event) {
				event.preventDefault();
				console.log(fontObj);
			});
			var select = document.createElement('input');
			select.setAttribute('type', 'checkbox');
			select.dataset.fontName = fontName;
			select.addEventListener('click', (function (select, fontName) {
				return (function () {
					selectFont(fontName, select.checked);
				});
			})(select, fontName));
			font.appendChild(select);
			font.appendChild(name);
			font.appendChild(document.createTextNode(' '));
			font.appendChild(download);
			font.appendChild(document.createTextNode(' '));
			font.appendChild(logIt);
			font.appendChild(moreInfo);
			fonts.appendChild(font);
			// Somewhat of a hack, should probably add a hook for when the text layer
			// is done rendering.
			setTimeout(function () {
				if (this.active) {
					resetSelection();
				}
			}.bind(this), 2000);
		}
	};
})();

var opMap;

// Manages all the page steppers.
var StepperManager = (function StepperManagerClosure() {
	var steppers = [];
	var stepperDiv = null;
	var stepperControls = null;
	var stepperChooser = null;
	var breakPoints = Object.create(null);
	return {
		// Properties/functions needed by PDFBug.
		id: 'Stepper',
		name: 'Stepper',
		panel: null,
		manager: null,
		init: function init(pdfjsLib) {
			var self = this;
			this.panel.setAttribute('style', 'padding: 5px;');
			stepperControls = document.createElement('div');
			stepperChooser = document.createElement('select');
			stepperChooser.addEventListener('change', function (event) {
				self.selectStepper(this.value);
			});
			stepperControls.appendChild(stepperChooser);
			stepperDiv = document.createElement('div');
			this.panel.appendChild(stepperControls);
			this.panel.appendChild(stepperDiv);
			if (sessionStorage.getItem('pdfjsBreakPoints')) {
				breakPoints = JSON.parse(sessionStorage.getItem('pdfjsBreakPoints'));
			}

			opMap = Object.create(null);
			for (var key in pdfjsLib.OPS) {
				opMap[pdfjsLib.OPS[key]] = key;
			}
		},
		cleanup: function cleanup() {
			stepperChooser.textContent = '';
			stepperDiv.textContent = '';
			steppers = [];
		},
		enabled: false,
		active: false,
		// Stepper specific functions.
		create: function create(pageIndex) {
			var debug = document.createElement('div');
			debug.id = 'stepper' + pageIndex;
			debug.setAttribute('hidden', true);
			debug.className = 'stepper';
			stepperDiv.appendChild(debug);
			var b = document.createElement('option');
			b.textContent = 'Page ' + (pageIndex + 1);
			b.value = pageIndex;
			stepperChooser.appendChild(b);
			var initBreakPoints = breakPoints[pageIndex] || [];
			var stepper = new Stepper(debug, pageIndex, initBreakPoints);
			steppers.push(stepper);
			if (steppers.length === 1) {
				this.selectStepper(pageIndex, false);
			}
			return stepper;
		},
		selectStepper: function selectStepper(pageIndex, selectPanel) {
			var i;
			pageIndex = pageIndex | 0;
			if (selectPanel) {
				this.manager.selectPanel(this);
			}
			for (i = 0; i < steppers.length; ++i) {
				var stepper = steppers[i];
				if (stepper.pageIndex === pageIndex) {
					stepper.panel.removeAttribute('hidden');
				} else {
					stepper.panel.setAttribute('hidden', true);
				}
			}
			var options = stepperChooser.options;
			for (i = 0; i < options.length; ++i) {
				var option = options[i];
				option.selected = (option.value | 0) === pageIndex;
			}
		},
		saveBreakPoints: function saveBreakPoints(pageIndex, bps) {
			breakPoints[pageIndex] = bps;
			sessionStorage.setItem('pdfjsBreakPoints', JSON.stringify(breakPoints));
		}
	};
})();

// The stepper for each page's IRQueue.
var Stepper = (function StepperClosure() {
	// Shorter way to create element and optionally set textContent.
	function c(tag, textContent) {
		var d = document.createElement(tag);
		if (textContent) {
			d.textContent = textContent;
		}
		return d;
	}

	function simplifyArgs(args) {
		if (typeof args === 'string') {
			var MAX_STRING_LENGTH = 75;
			return args.length <= MAX_STRING_LENGTH ? args :
				args.substr(0, MAX_STRING_LENGTH) + '...';
		}
		if (typeof args !== 'object' || args === null) {
			return args;
		}
		if ('length' in args) { // array
			var simpleArgs = [], i, ii;
			var MAX_ITEMS = 10;
			for (i = 0, ii = Math.min(MAX_ITEMS, args.length); i < ii; i++) {
				simpleArgs.push(simplifyArgs(args[i]));
			}
			if (i < args.length) {
				simpleArgs.push('...');
			}
			return simpleArgs;
		}
		var simpleObj = {};
		for (var key in args) {
			simpleObj[key] = simplifyArgs(args[key]);
		}
		return simpleObj;
	}

	function Stepper(panel, pageIndex, initialBreakPoints) {
		this.panel = panel;
		this.breakPoint = 0;
		this.nextBreakPoint = null;
		this.pageIndex = pageIndex;
		this.breakPoints = initialBreakPoints;
		this.currentIdx = -1;
		this.operatorListIdx = 0;
	}
	Stepper.prototype = {
		init: function init(operatorList) {
			var panel = this.panel;
			var content = c('div', 'c=continue, s=step');
			var table = c('table');
			content.appendChild(table);
			table.cellSpacing = 0;
			var headerRow = c('tr');
			table.appendChild(headerRow);
			headerRow.appendChild(c('th', 'Break'));
			headerRow.appendChild(c('th', 'Idx'));
			headerRow.appendChild(c('th', 'fn'));
			headerRow.appendChild(c('th', 'args'));
			panel.appendChild(content);
			this.table = table;
			this.updateOperatorList(operatorList);
		},
		updateOperatorList: function updateOperatorList(operatorList) {
			var self = this;

			function cboxOnClick() {
				var x = +this.dataset.idx;
				if (this.checked) {
					self.breakPoints.push(x);
				} else {
					self.breakPoints.splice(self.breakPoints.indexOf(x), 1);
				}
				StepperManager.saveBreakPoints(self.pageIndex, self.breakPoints);
			}

			var MAX_OPERATORS_COUNT = 15000;
			if (this.operatorListIdx > MAX_OPERATORS_COUNT) {
				return;
			}

			var chunk = document.createDocumentFragment();
			var operatorsToDisplay = Math.min(MAX_OPERATORS_COUNT,
				operatorList.fnArray.length);
			for (var i = this.operatorListIdx; i < operatorsToDisplay; i++) {
				var line = c('tr');
				line.className = 'line';
				line.dataset.idx = i;
				chunk.appendChild(line);
				var checked = this.breakPoints.indexOf(i) !== -1;
				var args = operatorList.argsArray[i] || [];

				var breakCell = c('td');
				var cbox = c('input');
				cbox.type = 'checkbox';
				cbox.className = 'points';
				cbox.checked = checked;
				cbox.dataset.idx = i;
				cbox.onclick = cboxOnClick;

				breakCell.appendChild(cbox);
				line.appendChild(breakCell);
				line.appendChild(c('td', i.toString()));
				var fn = opMap[operatorList.fnArray[i]];
				var decArgs = args;
				if (fn === 'showText') {
					var glyphs = args[0];
					var newArgs = [];
					var str = [];
					for (var j = 0; j < glyphs.length; j++) {
						var glyph = glyphs[j];
						if (typeof glyph === 'object' && glyph !== null) {
							str.push(glyph.fontChar);
						} else {
							if (str.length > 0) {
								newArgs.push(str.join(''));
								str = [];
							}
							newArgs.push(glyph); // null or number
						}
					}
					if (str.length > 0) {
						newArgs.push(str.join(''));
					}
					decArgs = [newArgs];
				}
				line.appendChild(c('td', fn));
				line.appendChild(c('td', JSON.stringify(simplifyArgs(decArgs))));
			}
			if (operatorsToDisplay < operatorList.fnArray.length) {
				line = c('tr');
				var lastCell = c('td', '...');
				lastCell.colspan = 4;
				chunk.appendChild(lastCell);
			}
			this.operatorListIdx = operatorList.fnArray.length;
			this.table.appendChild(chunk);
		},
		getNextBreakPoint: function getNextBreakPoint() {
			this.breakPoints.sort(function (a, b) {
				return a - b;
			});
			for (var i = 0; i < this.breakPoints.length; i++) {
				if (this.breakPoints[i] > this.currentIdx) {
					return this.breakPoints[i];
				}
			}
			return null;
		},
		breakIt: function breakIt(idx, callback) {
			StepperManager.selectStepper(this.pageIndex, true);
			var self = this;
			var dom = document;
			self.currentIdx = idx;
			var listener = function (e) {
				switch (e.keyCode) {
					case 83: // step
						dom.removeEventListener('keydown', listener);
						self.nextBreakPoint = self.currentIdx + 1;
						self.goTo(-1);
						callback();
						break;
					case 67: // continue
						dom.removeEventListener('keydown', listener);
						var breakPoint = self.getNextBreakPoint();
						self.nextBreakPoint = breakPoint;
						self.goTo(-1);
						callback();
						break;
				}
			};
			dom.addEventListener('keydown', listener);
			self.goTo(idx);
		},
		goTo: function goTo(idx) {
			var allRows = this.panel.getElementsByClassName('line');
			for (var x = 0, xx = allRows.length; x < xx; ++x) {
				var row = allRows[x];
				if ((row.dataset.idx | 0) === idx) {
					row.style.backgroundColor = 'rgb(251,250,207)';
					row.scrollIntoView();
				} else {
					row.style.backgroundColor = null;
				}
			}
		}
	};
	return Stepper;
})();

var Stats = (function Stats() {
	var stats = [];
	function clear(node) {
		while (node.hasChildNodes()) {
			node.removeChild(node.lastChild);
		}
	}
	function getStatIndex(pageNumber) {
		for (var i = 0, ii = stats.length; i < ii; ++i) {
			if (stats[i].pageNumber === pageNumber) {
				return i;
			}
		}
		return false;
	}
	return {
		// Properties/functions needed by PDFBug.
		id: 'Stats',
		name: 'Stats',
		panel: null,
		manager: null,
		init: function init(pdfjsLib) {
			this.panel.setAttribute('style', 'padding: 5px;');
			pdfjsLib.PDFJS.enableStats = true;
		},
		enabled: false,
		active: false,
		// Stats specific functions.
		add: function (pageNumber, stat) {
			if (!stat) {
				return;
			}
			var statsIndex = getStatIndex(pageNumber);
			if (statsIndex !== false) {
				var b = stats[statsIndex];
				this.panel.removeChild(b.div);
				stats.splice(statsIndex, 1);
			}
			var wrapper = document.createElement('div');
			wrapper.className = 'stats';
			var title = document.createElement('div');
			title.className = 'title';
			title.textContent = 'Page: ' + pageNumber;
			var statsDiv = document.createElement('div');
			statsDiv.textContent = stat.toString();
			wrapper.appendChild(title);
			wrapper.appendChild(statsDiv);
			stats.push({ pageNumber: pageNumber, div: wrapper });
			stats.sort(function (a, b) {
				return a.pageNumber - b.pageNumber;
			});
			clear(this.panel);
			for (var i = 0, ii = stats.length; i < ii; ++i) {
				this.panel.appendChild(stats[i].div);
			}
		},
		cleanup: function () {
			stats = [];
			clear(this.panel);
		}
	};
})();

// Manages all the debugging tools.
var PDFBug = (function PDFBugClosure() {
	var panelWidth = 300;
	var buttons = [];
	var activePanel = null;

	return {
		tools: [
			FontInspector,
			StepperManager,
			Stats
		],
		enable: function (ids) {
			var all = false, tools = this.tools;
			if (ids.length === 1 && ids[0] === 'all') {
				all = true;
			}
			for (var i = 0; i < tools.length; ++i) {
				var tool = tools[i];
				if (all || ids.indexOf(tool.id) !== -1) {
					tool.enabled = true;
				}
			}
			if (!all) {
				// Sort the tools by the order they are enabled.
				tools.sort(function (a, b) {
					var indexA = ids.indexOf(a.id);
					indexA = indexA < 0 ? tools.length : indexA;
					var indexB = ids.indexOf(b.id);
					indexB = indexB < 0 ? tools.length : indexB;
					return indexA - indexB;
				});
			}
		},
		init: function init(pdfjsLib, container) {
			/*
			 * Basic Layout:
			 * PDFBug
			 *  Controls
			 *  Panels
			 *    Panel
			 *    Panel
			 *    ...
			 */
			var ui = document.createElement('div');
			ui.id = 'PDFBug';

			var controls = document.createElement('div');
			controls.setAttribute('class', 'controls');
			ui.appendChild(controls);

			var panels = document.createElement('div');
			panels.setAttribute('class', 'panels');
			ui.appendChild(panels);

			container.appendChild(ui);
			container.style.right = panelWidth + 'px';

			// Initialize all the debugging tools.
			var tools = this.tools;
			var self = this;
			for (var i = 0; i < tools.length; ++i) {
				var tool = tools[i];
				var panel = document.createElement('div');
				var panelButton = document.createElement('button');
				panelButton.textContent = tool.name;
				panelButton.addEventListener('click', (function (selected) {
					return function (event) {
						event.preventDefault();
						self.selectPanel(selected);
					};
				})(i));
				controls.appendChild(panelButton);
				panels.appendChild(panel);
				tool.panel = panel;
				tool.manager = this;
				if (tool.enabled) {
					tool.init(pdfjsLib);
				} else {
					panel.textContent = tool.name + ' is disabled. To enable add ' +
						' "' + tool.id + '" to the pdfBug parameter ' +
						'and refresh (separate multiple by commas).';
				}
				buttons.push(panelButton);
			}
			this.selectPanel(0);
		},
		cleanup: function cleanup() {
			for (var i = 0, ii = this.tools.length; i < ii; i++) {
				if (this.tools[i].enabled) {
					this.tools[i].cleanup();
				}
			}
		},
		selectPanel: function selectPanel(index) {
			if (typeof index !== 'number') {
				index = this.tools.indexOf(index);
			}
			if (index === activePanel) {
				return;
			}
			activePanel = index;
			var tools = this.tools;
			for (var j = 0; j < tools.length; ++j) {
				if (j === index) {
					buttons[j].setAttribute('class', 'active');
					tools[j].active = true;
					tools[j].panel.removeAttribute('hidden');
				} else {
					buttons[j].setAttribute('class', '');
					tools[j].active = false;
					tools[j].panel.setAttribute('hidden', 'true');
				}
			}
		}
	};
})();
/* Copyright 2017 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/******/ (function (modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if (installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;
/******/
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
			/******/
		};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
		/******/
	}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;
/******/
/******/ 	// identity function for calling harmony imports with the correct context
/******/ 	__webpack_require__.i = function (value) { return value; };
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__webpack_require__.d = function (exports, name, getter) {
/******/ 		if (!__webpack_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
		/******/
	});
			/******/
		}
		/******/
	};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__webpack_require__.n = function (module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__webpack_require__.d(getter, 'a', getter);
/******/ 		return getter;
		/******/
	};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__webpack_require__.o = function (object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(__webpack_require__.s = 30);
	/******/
})
/************************************************************************/
/******/([
/* 0 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var pdfjsLib = __webpack_require__(1);
		var CSS_UNITS = 96.0 / 72.0;
		var DEFAULT_SCALE_VALUE = 'auto';
		var DEFAULT_SCALE = 1.0;
		var MIN_SCALE = 0.25;
		var MAX_SCALE = 10.0;
		var UNKNOWN_SCALE = 0;
		var MAX_AUTO_SCALE = 1.25;
		var SCROLLBAR_PADDING = 40;
		var VERTICAL_PADDING = 5;
		var RendererType = {
			CANVAS: 'canvas',
			SVG: 'svg'
		};
		var mozL10n = document.mozL10n || document.webL10n;
		var PDFJS = pdfjsLib.PDFJS;
		PDFJS.disableFullscreen = PDFJS.disableFullscreen === undefined ? false : PDFJS.disableFullscreen;
		PDFJS.useOnlyCssZoom = PDFJS.useOnlyCssZoom === undefined ? false : PDFJS.useOnlyCssZoom;
		PDFJS.maxCanvasPixels = PDFJS.maxCanvasPixels === undefined ? 16777216 : PDFJS.maxCanvasPixels;
		PDFJS.disableHistory = PDFJS.disableHistory === undefined ? false : PDFJS.disableHistory;
		PDFJS.disableTextLayer = PDFJS.disableTextLayer === undefined ? false : PDFJS.disableTextLayer;
		PDFJS.ignoreCurrentPositionOnZoom = PDFJS.ignoreCurrentPositionOnZoom === undefined ? false : PDFJS.ignoreCurrentPositionOnZoom;
		PDFJS.locale = PDFJS.locale === undefined ? navigator.language : PDFJS.locale;
		function getOutputScale(ctx) {
			var devicePixelRatio = window.devicePixelRatio || 1;
			var backingStoreRatio = ctx.webkitBackingStorePixelRatio || ctx.mozBackingStorePixelRatio || ctx.msBackingStorePixelRatio || ctx.oBackingStorePixelRatio || ctx.backingStorePixelRatio || 1;
			var pixelRatio = devicePixelRatio / backingStoreRatio;
			return {
				sx: pixelRatio,
				sy: pixelRatio,
				scaled: pixelRatio !== 1
			};
		}
		function scrollIntoView(element, spot, skipOverflowHiddenElements) {
			var parent = element.offsetParent;
			if (!parent) {
				console.error('offsetParent is not set -- cannot scroll');
				return;
			}
			var checkOverflow = skipOverflowHiddenElements || false;
			var offsetY = element.offsetTop + element.clientTop;
			var offsetX = element.offsetLeft + element.clientLeft;
			while (parent.clientHeight === parent.scrollHeight || checkOverflow && getComputedStyle(parent).overflow === 'hidden') {
				if (parent.dataset._scaleY) {
					offsetY /= parent.dataset._scaleY;
					offsetX /= parent.dataset._scaleX;
				}
				offsetY += parent.offsetTop;
				offsetX += parent.offsetLeft;
				parent = parent.offsetParent;
				if (!parent) {
					return;
				}
			}
			if (spot) {
				if (spot.top !== undefined) {
					offsetY += spot.top;
				}
				if (spot.left !== undefined) {
					offsetX += spot.left;
					parent.scrollLeft = offsetX;
				}
			}
			parent.scrollTop = offsetY;
		}
		function watchScroll(viewAreaElement, callback) {
			var debounceScroll = function debounceScroll(evt) {
				if (rAF) {
					return;
				}
				rAF = window.requestAnimationFrame(function viewAreaElementScrolled() {
					rAF = null;
					var currentY = viewAreaElement.scrollTop;
					var lastY = state.lastY;
					if (currentY !== lastY) {
						state.down = currentY > lastY;
					}
					state.lastY = currentY;
					callback(state);
				});
			};
			var state = {
				down: true,
				lastY: viewAreaElement.scrollTop,
				_eventHandler: debounceScroll
			};
			var rAF = null;
			viewAreaElement.addEventListener('scroll', debounceScroll, true);
			return state;
		}
		function parseQueryString(query) {
			var parts = query.split('&');
			var params = {};
			for (var i = 0, ii = parts.length; i < ii; ++i) {
				var param = parts[i].split('=');
				var key = param[0].toLowerCase();
				var value = param.length > 1 ? param[1] : null;
				params[decodeURIComponent(key)] = decodeURIComponent(value);
			}
			return params;
		}
		function binarySearchFirstItem(items, condition) {
			var minIndex = 0;
			var maxIndex = items.length - 1;
			if (items.length === 0 || !condition(items[maxIndex])) {
				return items.length;
			}
			if (condition(items[minIndex])) {
				return minIndex;
			}
			while (minIndex < maxIndex) {
				var currentIndex = minIndex + maxIndex >> 1;
				var currentItem = items[currentIndex];
				if (condition(currentItem)) {
					maxIndex = currentIndex;
				} else {
					minIndex = currentIndex + 1;
				}
			}
			return minIndex;
		}
		function approximateFraction(x) {
			if (Math.floor(x) === x) {
				return [
					x,
					1
				];
			}
			var xinv = 1 / x;
			var limit = 8;
			if (xinv > limit) {
				return [
					1,
					limit
				];
			} else if (Math.floor(xinv) === xinv) {
				return [
					1,
					xinv
				];
			}
			var x_ = x > 1 ? xinv : x;
			var a = 0, b = 1, c = 1, d = 1;
			while (true) {
				var p = a + c, q = b + d;
				if (q > limit) {
					break;
				}
				if (x_ <= p / q) {
					c = p;
					d = q;
				} else {
					a = p;
					b = q;
				}
			}
			var result;
			if (x_ - a / b < c / d - x_) {
				result = x_ === x ? [
					a,
					b
				] : [
					b,
					a
				];
			} else {
				result = x_ === x ? [
					c,
					d
				] : [
					d,
					c
				];
			}
			return result;
		}
		function roundToDivide(x, div) {
			var r = x % div;
			return r === 0 ? x : Math.round(x - r + div);
		}
		function getVisibleElements(scrollEl, views, sortByVisibility) {
			var top = scrollEl.scrollTop, bottom = top + scrollEl.clientHeight;
			var left = scrollEl.scrollLeft, right = left + scrollEl.clientWidth;
			function isElementBottomBelowViewTop(view) {
				var element = view.div;
				var elementBottom = element.offsetTop + element.clientTop + element.clientHeight;
				return elementBottom > top;
			}
			var visible = [], view, element;
			var currentHeight, viewHeight, hiddenHeight, percentHeight;
			var currentWidth, viewWidth;
			var firstVisibleElementInd = views.length === 0 ? 0 : binarySearchFirstItem(views, isElementBottomBelowViewTop);
			for (var i = firstVisibleElementInd, ii = views.length; i < ii; i++) {
				view = views[i];
				element = view.div;
				currentHeight = element.offsetTop + element.clientTop;
				viewHeight = element.clientHeight;
				if (currentHeight > bottom) {
					break;
				}
				currentWidth = element.offsetLeft + element.clientLeft;
				viewWidth = element.clientWidth;
				if (currentWidth + viewWidth < left || currentWidth > right) {
					continue;
				}
				hiddenHeight = Math.max(0, top - currentHeight) + Math.max(0, currentHeight + viewHeight - bottom);
				percentHeight = (viewHeight - hiddenHeight) * 100 / viewHeight | 0;
				visible.push({
					id: view.id,
					x: currentWidth,
					y: currentHeight,
					view: view,
					percent: percentHeight
				});
			}
			var first = visible[0];
			var last = visible[visible.length - 1];
			if (sortByVisibility) {
				visible.sort(function (a, b) {
					var pc = a.percent - b.percent;
					if (Math.abs(pc) > 0.001) {
						return -pc;
					}
					return a.id - b.id;
				});
			}
			return {
				first: first,
				last: last,
				views: visible
			};
		}
		function noContextMenuHandler(e) {
			e.preventDefault();
		}
		function getPDFFileNameFromURL(url, defaultFilename) {
			if (typeof defaultFilename === 'undefined' || defaultFilename == "") {
				defaultFilename = 'document.pdf';
			}
			var reURI = /^(?:(?:[^:]+:)?\/\/[^\/]+)?([^?#]*)(\?[^#]*)?(#.*)?$/;
			var reFilename = /[^\/?#=]+\.pdf\b(?!.*\.pdf\b)/i;
			var splitURI = reURI.exec(url);
			var suggestedFilename = reFilename.exec(splitURI[1]) || reFilename.exec(splitURI[2]) || reFilename.exec(splitURI[3]);
			if (suggestedFilename) {
				suggestedFilename = suggestedFilename[0];
				if (suggestedFilename.indexOf('%') !== -1) {
					try {
						suggestedFilename = reFilename.exec(decodeURIComponent(suggestedFilename))[0];
					} catch (e) {
					}
				}
			}
			return suggestedFilename || defaultFilename;
		}
		function normalizeWheelEventDelta(evt) {
			var delta = Math.sqrt(evt.deltaX * evt.deltaX + evt.deltaY * evt.deltaY);
			var angle = Math.atan2(evt.deltaY, evt.deltaX);
			if (-0.25 * Math.PI < angle && angle < 0.75 * Math.PI) {
				delta = -delta;
			}
			var MOUSE_DOM_DELTA_PIXEL_MODE = 0;
			var MOUSE_DOM_DELTA_LINE_MODE = 1;
			var MOUSE_PIXELS_PER_LINE = 30;
			var MOUSE_LINES_PER_PAGE = 30;
			if (evt.deltaMode === MOUSE_DOM_DELTA_PIXEL_MODE) {
				delta /= MOUSE_PIXELS_PER_LINE * MOUSE_LINES_PER_PAGE;
			} else if (evt.deltaMode === MOUSE_DOM_DELTA_LINE_MODE) {
				delta /= MOUSE_LINES_PER_PAGE;
			}
			return delta;
		}
		var animationStarted = new Promise(function (resolve) {
			window.requestAnimationFrame(resolve);
		});
		var localized = new Promise(function (resolve, reject) {
			if (!mozL10n) {
				resolve();
				return;
			}
			if (mozL10n.getReadyState() !== 'loading') {
				resolve();
				return;
			}
			window.addEventListener('localized', function localized(evt) {
				resolve();
			});
		});
		var EventBus = function EventBusClosure() {
			function EventBus() {
				this._listeners = Object.create(null);
			}
			EventBus.prototype = {
				on: function EventBus_on(eventName, listener) {
					var eventListeners = this._listeners[eventName];
					if (!eventListeners) {
						eventListeners = [];
						this._listeners[eventName] = eventListeners;
					}
					eventListeners.push(listener);
				},
				off: function EventBus_on(eventName, listener) {
					var eventListeners = this._listeners[eventName];
					var i;
					if (!eventListeners || (i = eventListeners.indexOf(listener)) < 0) {
						return;
					}
					eventListeners.splice(i, 1);
				},
				dispatch: function EventBus_dispath(eventName) {
					var eventListeners = this._listeners[eventName];
					if (!eventListeners || eventListeners.length === 0) {
						return;
					}
					var args = Array.prototype.slice.call(arguments, 1);
					eventListeners.slice(0).forEach(function (listener) {
						listener.apply(null, args);
					});
				}
			};
			return EventBus;
		}();
		var ProgressBar = function ProgressBarClosure() {
			function clamp(v, min, max) {
				return Math.min(Math.max(v, min), max);
			}
			function ProgressBar(id, opts) {
				this.visible = true;
				this.div = document.querySelector(id + ' .progress');
				this.bar = this.div.parentNode;
				this.height = opts.height || 100;
				this.width = opts.width || 100;
				this.units = opts.units || '%';
				this.div.style.height = this.height + this.units;
				this.percent = 0;
			}
			ProgressBar.prototype = {
				updateBar: function ProgressBar_updateBar() {
					if (this._indeterminate) {
						this.div.classList.add('indeterminate');
						this.div.style.width = this.width + this.units;
						return;
					}
					this.div.classList.remove('indeterminate');
					var progressSize = this.width * this._percent / 100;
					this.div.style.width = progressSize + this.units;
				},
				get percent() {
					return this._percent;
				},
				set percent(val) {
					this._indeterminate = isNaN(val);
					this._percent = clamp(val, 0, 100);
					this.updateBar();
				},
				setWidth: function ProgressBar_setWidth(viewer) {
					if (viewer) {
						var container = viewer.parentNode;
						var scrollbarWidth = container.offsetWidth - viewer.offsetWidth;
						if (scrollbarWidth > 0) {
							this.bar.setAttribute('style', 'width: calc(100% - ' + scrollbarWidth + 'px);');
						}
					}
				},
				hide: function ProgressBar_hide() {
					if (!this.visible) {
						return;
					}
					this.visible = false;
					this.bar.classList.add('hidden');
					document.body.classList.remove('loadingInProgress');
				},
				show: function ProgressBar_show() {
					if (this.visible) {
						return;
					}
					this.visible = true;
					document.body.classList.add('loadingInProgress');
					this.bar.classList.remove('hidden');
				}
			};
			return ProgressBar;
		}();
		exports.CSS_UNITS = CSS_UNITS;
		exports.DEFAULT_SCALE_VALUE = DEFAULT_SCALE_VALUE;
		exports.DEFAULT_SCALE = DEFAULT_SCALE;
		exports.MIN_SCALE = MIN_SCALE;
		exports.MAX_SCALE = MAX_SCALE;
		exports.UNKNOWN_SCALE = UNKNOWN_SCALE;
		exports.MAX_AUTO_SCALE = MAX_AUTO_SCALE;
		exports.SCROLLBAR_PADDING = SCROLLBAR_PADDING;
		exports.VERTICAL_PADDING = VERTICAL_PADDING;
		exports.RendererType = RendererType;
		exports.mozL10n = mozL10n;
		exports.EventBus = EventBus;
		exports.ProgressBar = ProgressBar;
		exports.getPDFFileNameFromURL = getPDFFileNameFromURL;
		exports.noContextMenuHandler = noContextMenuHandler;
		exports.parseQueryString = parseQueryString;
		exports.getVisibleElements = getVisibleElements;
		exports.roundToDivide = roundToDivide;
		exports.approximateFraction = approximateFraction;
		exports.getOutputScale = getOutputScale;
		exports.scrollIntoView = scrollIntoView;
		exports.watchScroll = watchScroll;
		exports.binarySearchFirstItem = binarySearchFirstItem;
		exports.normalizeWheelEventDelta = normalizeWheelEventDelta;
		exports.animationStarted = animationStarted;
		exports.localized = localized;

		/***/
	}),
/* 1 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		{
			var pdfjsLib;
			if (typeof __pdfjsdev_webpack__ === 'undefined') {
				if (typeof require === 'function') {
					pdfjsLib = require('../build/pdf.js');
				} else {
					pdfjsLib = window['pdfjs-dist/build/pdf'];
				}
			}
			module.exports = pdfjsLib;
		}

		/***/
	}),
/* 2 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var EventBus = uiUtils.EventBus;
		function attachDOMEventsToEventBus(eventBus) {
			eventBus.on('documentload', function () {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('documentload', true, true, {});
				window.dispatchEvent(event);
			});
			eventBus.on('pagerendered', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('pagerendered', true, true, {
					pageNumber: e.pageNumber,
					cssTransform: e.cssTransform
				});
				e.source.div.dispatchEvent(event);
			});
			eventBus.on('textlayerrendered', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('textlayerrendered', true, true, { pageNumber: e.pageNumber });
				e.source.textLayerDiv.dispatchEvent(event);
			});
			eventBus.on('pagechange', function (e) {
				var event = document.createEvent('UIEvents');
				event.initUIEvent('pagechange', true, true, window, 0);
				event.pageNumber = e.pageNumber;
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('pagesinit', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('pagesinit', true, true, null);
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('pagesloaded', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('pagesloaded', true, true, { pagesCount: e.pagesCount });
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('scalechange', function (e) {
				var event = document.createEvent('UIEvents');
				event.initUIEvent('scalechange', true, true, window, 0);
				event.scale = e.scale;
				event.presetValue = e.presetValue;
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('updateviewarea', function (e) {
				var event = document.createEvent('UIEvents');
				event.initUIEvent('updateviewarea', true, true, window, 0);
				event.location = e.location;
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('find', function (e) {
				if (e.source === window) {
					return;
				}
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('find' + e.type, true, true, {
					query: e.query,
					phraseSearch: e.phraseSearch,
					caseSensitive: e.caseSensitive,
					highlightAll: e.highlightAll,
					findPrevious: e.findPrevious
				});
				window.dispatchEvent(event);
			});
			eventBus.on('attachmentsloaded', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('attachmentsloaded', true, true, { attachmentsCount: e.attachmentsCount });
				e.source.container.dispatchEvent(event);
			});
			eventBus.on('sidebarviewchanged', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('sidebarviewchanged', true, true, { view: e.view });
				e.source.outerContainer.dispatchEvent(event);
			});
			eventBus.on('pagemode', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('pagemode', true, true, { mode: e.mode });
				e.source.pdfViewer.container.dispatchEvent(event);
			});
			eventBus.on('namedaction', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('namedaction', true, true, { action: e.action });
				e.source.pdfViewer.container.dispatchEvent(event);
			});
			eventBus.on('presentationmodechanged', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('presentationmodechanged', true, true, {
					active: e.active,
					switchInProgress: e.switchInProgress
				});
				window.dispatchEvent(event);
			});
			eventBus.on('outlineloaded', function (e) {
				var event = document.createEvent('CustomEvent');
				event.initCustomEvent('outlineloaded', true, true, { outlineCount: e.outlineCount });
				e.source.container.dispatchEvent(event);
			});
		}
		var globalEventBus = null;
		function getGlobalEventBus() {
			if (globalEventBus) {
				return globalEventBus;
			}
			globalEventBus = new EventBus();
			attachDOMEventsToEventBus(globalEventBus);
			return globalEventBus;
		}
		exports.attachDOMEventsToEventBus = attachDOMEventsToEventBus;
		exports.getGlobalEventBus = getGlobalEventBus;

		/***/
	}),
/* 3 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var CLEANUP_TIMEOUT = 30000;
		var RenderingStates = {
			INITIAL: 0,
			RUNNING: 1,
			PAUSED: 2,
			FINISHED: 3
		};
		var PDFRenderingQueue = function PDFRenderingQueueClosure() {
			function PDFRenderingQueue() {
				this.pdfViewer = null;
				this.pdfThumbnailViewer = null;
				this.onIdle = null;
				this.highestPriorityPage = null;
				this.idleTimeout = null;
				this.printing = false;
				this.isThumbnailViewEnabled = false;
			}
			PDFRenderingQueue.prototype = {
				setViewer: function PDFRenderingQueue_setViewer(pdfViewer) {
					this.pdfViewer = pdfViewer;
				},
				setThumbnailViewer: function PDFRenderingQueue_setThumbnailViewer(pdfThumbnailViewer) {
					this.pdfThumbnailViewer = pdfThumbnailViewer;
				},
				isHighestPriority: function PDFRenderingQueue_isHighestPriority(view) {
					return this.highestPriorityPage === view.renderingId;
				},
				renderHighestPriority: function PDFRenderingQueue_renderHighestPriority(currentlyVisiblePages) {
					if (this.idleTimeout) {
						clearTimeout(this.idleTimeout);
						this.idleTimeout = null;
					}
					if (this.pdfViewer.forceRendering(currentlyVisiblePages)) {
						return;
					}
					if (this.pdfThumbnailViewer && this.isThumbnailViewEnabled) {
						if (this.pdfThumbnailViewer.forceRendering()) {
							return;
						}
					}
					if (this.printing) {
						return;
					}
					if (this.onIdle) {
						this.idleTimeout = setTimeout(this.onIdle.bind(this), CLEANUP_TIMEOUT);
					}
				},
				getHighestPriority: function PDFRenderingQueue_getHighestPriority(visible, views, scrolledDown) {
					var visibleViews = visible.views;
					var numVisible = visibleViews.length;
					if (numVisible === 0) {
						return false;
					}
					for (var i = 0; i < numVisible; ++i) {
						var view = visibleViews[i].view;
						if (!this.isViewFinished(view)) {
							return view;
						}
					}
					if (scrolledDown) {
						var nextPageIndex = visible.last.id;
						if (views[nextPageIndex] && !this.isViewFinished(views[nextPageIndex])) {
							return views[nextPageIndex];
						}
					} else {
						var previousPageIndex = visible.first.id - 2;
						if (views[previousPageIndex] && !this.isViewFinished(views[previousPageIndex])) {
							return views[previousPageIndex];
						}
					}
					return null;
				},
				isViewFinished: function PDFRenderingQueue_isViewFinished(view) {
					return view.renderingState === RenderingStates.FINISHED;
				},
				renderView: function PDFRenderingQueue_renderView(view) {
					var state = view.renderingState;
					switch (state) {
						case RenderingStates.FINISHED:
							return false;
						case RenderingStates.PAUSED:
							this.highestPriorityPage = view.renderingId;
							view.resume();
							break;
						case RenderingStates.RUNNING:
							this.highestPriorityPage = view.renderingId;
							break;
						case RenderingStates.INITIAL:
							this.highestPriorityPage = view.renderingId;
							var continueRendering = function () {
								this.renderHighestPriority();
							}.bind(this);
							view.draw().then(continueRendering, continueRendering);
							break;
					}
					return true;
				}
			};
			return PDFRenderingQueue;
		}();
		exports.RenderingStates = RenderingStates;
		exports.PDFRenderingQueue = PDFRenderingQueue;

		/***/
	}),
/* 4 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var OverlayManager = {
			overlays: {},
			active: null,
			register: function overlayManagerRegister(name, element, callerCloseMethod, canForceClose) {
				return new Promise(function (resolve) {
					var container;
					if (!name || !element || !(container = element.parentNode)) {
						throw new Error('Not enough parameters.');
					} else if (this.overlays[name]) {
						if (this.active !== name) {
							this.unregister(name);
						} else {
							throw new Error('The overlay is already registered and active.');
						}
					}
					this.overlays[name] = {
						element: element,
						container: container,
						callerCloseMethod: callerCloseMethod || null,
						canForceClose: canForceClose || false
					};
					resolve();
				}.bind(this));
			},
			unregister: function overlayManagerUnregister(name) {
				return new Promise(function (resolve) {
					if (!this.overlays[name]) {
						throw new Error('The overlay does not exist.');
					} else if (this.active === name) {
						throw new Error('The overlay cannot be removed while it is active.');
					}
					delete this.overlays[name];
					resolve();
				}.bind(this));
			},
			open: function overlayManagerOpen(name) {
				return new Promise(function (resolve) {
					if (!this.overlays[name]) {
						throw new Error('The overlay does not exist.');
					} else if (this.active) {
						if (this.overlays[name].canForceClose) {
							this._closeThroughCaller();
						} else if (this.active === name) {
							throw new Error('The overlay is already active.');
						} else {
							throw new Error('Another overlay is currently active.');
						}
					}
					this.active = name;
					this.overlays[this.active].element.classList.remove('hidden');
					this.overlays[this.active].container.classList.remove('hidden');
					window.addEventListener('keydown', this._keyDown);
					resolve();
				}.bind(this));
			},
			close: function overlayManagerClose(name) {
				return new Promise(function (resolve) {
					if (!this.overlays[name]) {
						throw new Error('The overlay does not exist.');
					} else if (!this.active) {
						throw new Error('The overlay is currently not active.');
					} else if (this.active !== name) {
						throw new Error('Another overlay is currently active.');
					}
					this.overlays[this.active].container.classList.add('hidden');
					this.overlays[this.active].element.classList.add('hidden');
					this.active = null;
					window.removeEventListener('keydown', this._keyDown);
					resolve();
				}.bind(this));
			},
			_keyDown: function overlayManager_keyDown(evt) {
				var self = OverlayManager;
				if (self.active && evt.keyCode === 27) {
					self._closeThroughCaller();
					evt.preventDefault();
				}
			},
			_closeThroughCaller: function overlayManager_closeThroughCaller() {
				if (this.overlays[this.active].callerCloseMethod) {
					this.overlays[this.active].callerCloseMethod();
				}
				if (this.active) {
					this.close(this.active);
				}
			}
		};
		exports.OverlayManager = OverlayManager;

		/***/
	}),
/* 5 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var domEvents = __webpack_require__(2);
		var parseQueryString = uiUtils.parseQueryString;
		var PageNumberRegExp = /^\d+$/;
		function isPageNumber(str) {
			return PageNumberRegExp.test(str);
		}
		var PDFLinkService = function PDFLinkServiceClosure() {
			function PDFLinkService(options) {
				options = options || {};
				this.eventBus = options.eventBus || domEvents.getGlobalEventBus();
				this.baseUrl = null;
				this.pdfDocument = null;
				this.pdfViewer = null;
				this.pdfHistory = null;
				this._pagesRefCache = null;
			}
			PDFLinkService.prototype = {
				setDocument: function PDFLinkService_setDocument(pdfDocument, baseUrl) {
					this.baseUrl = baseUrl;
					this.pdfDocument = pdfDocument;
					this._pagesRefCache = Object.create(null);
				},
				setViewer: function PDFLinkService_setViewer(pdfViewer) {
					this.pdfViewer = pdfViewer;
				},
				setHistory: function PDFLinkService_setHistory(pdfHistory) {
					this.pdfHistory = pdfHistory;
				},
				get pagesCount() {
					return this.pdfDocument ? this.pdfDocument.numPages : 0;
				},
				get page() {
					return this.pdfViewer.currentPageNumber;
				},
				set page(value) {
					this.pdfViewer.currentPageNumber = value;
				},
				navigateTo: function PDFLinkService_navigateTo(dest) {
					var destString = '';
					var self = this;
					var goToDestination = function (destRef) {
						var pageNumber;
						if (destRef instanceof Object) {
							pageNumber = self._cachedPageNumber(destRef);
						} else if ((destRef | 0) === destRef) {
							pageNumber = destRef + 1;
						} else {
							console.error('PDFLinkService_navigateTo: "' + destRef + '" is not a valid destination reference.');
							return;
						}
						if (pageNumber) {
							if (pageNumber < 1 || pageNumber > self.pagesCount) {
								console.error('PDFLinkService_navigateTo: "' + pageNumber + '" is a non-existent page number.');
								return;
							}
							self.pdfViewer.scrollPageIntoView({
								pageNumber: pageNumber,
								destArray: dest
							});
							if (self.pdfHistory) {
								self.pdfHistory.push({
									dest: dest,
									hash: destString,
									page: pageNumber
								});
							}
						} else {
							self.pdfDocument.getPageIndex(destRef).then(function (pageIndex) {
								self.cachePageRef(pageIndex + 1, destRef);
								goToDestination(destRef);
							}).catch(function () {
								console.error('PDFLinkService_navigateTo: "' + destRef + '" is not a valid page reference.');
							});
						}
					};
					var destinationPromise;
					if (typeof dest === 'string') {
						destString = dest;
						destinationPromise = this.pdfDocument.getDestination(dest);
					} else {
						destinationPromise = Promise.resolve(dest);
					}
					destinationPromise.then(function (destination) {
						dest = destination;
						if (!(destination instanceof Array)) {
							console.error('PDFLinkService_navigateTo: "' + destination + '" is not a valid destination array.');
							return;
						}
						goToDestination(destination[0]);
					});
				},
				getDestinationHash: function PDFLinkService_getDestinationHash(dest) {
					if (typeof dest === 'string') {
						return this.getAnchorUrl('#' + (isPageNumber(dest) ? 'nameddest=' : '') + escape(dest));
					}
					if (dest instanceof Array) {
						var str = JSON.stringify(dest);
						return this.getAnchorUrl('#' + escape(str));
					}
					return this.getAnchorUrl('');
				},
				getAnchorUrl: function PDFLinkService_getAnchorUrl(anchor) {
					return (this.baseUrl || '') + anchor;
				},
				setHash: function PDFLinkService_setHash(hash) {
					var pageNumber, dest;
					if (hash.indexOf('=') >= 0) {
						var params = parseQueryString(hash);
						if ('search' in params) {
							this.eventBus.dispatch('findfromurlhash', {
								source: this,
								query: params['search'].replace(/"/g, ''),
								phraseSearch: params['phrase'] === 'true'
							});
						}
						if ('nameddest' in params) {
							if (this.pdfHistory) {
								this.pdfHistory.updateNextHashParam(params.nameddest);
							}
							this.navigateTo(params.nameddest);
							return;
						}
						if ('page' in params) {
							pageNumber = params.page | 0 || 1;
						}
						if ('zoom' in params) {
							var zoomArgs = params.zoom.split(',');
							var zoomArg = zoomArgs[0];
							var zoomArgNumber = parseFloat(zoomArg);
							if (zoomArg.indexOf('Fit') === -1) {
								dest = [
									null,
									{ name: 'XYZ' },
									zoomArgs.length > 1 ? zoomArgs[1] | 0 : null,
									zoomArgs.length > 2 ? zoomArgs[2] | 0 : null,
									zoomArgNumber ? zoomArgNumber / 100 : zoomArg
								];
							} else {
								if (zoomArg === 'Fit' || zoomArg === 'FitB') {
									dest = [
										null,
										{ name: zoomArg }
									];
								} else if (zoomArg === 'FitH' || zoomArg === 'FitBH' || (zoomArg === 'FitV' || zoomArg === 'FitBV')) {
									dest = [
										null,
										{ name: zoomArg },
										zoomArgs.length > 1 ? zoomArgs[1] | 0 : null
									];
								} else if (zoomArg === 'FitR') {
									if (zoomArgs.length !== 5) {
										console.error('PDFLinkService_setHash: ' + 'Not enough parameters for \'FitR\'.');
									} else {
										dest = [
											null,
											{ name: zoomArg },
											zoomArgs[1] | 0,
											zoomArgs[2] | 0,
											zoomArgs[3] | 0,
											zoomArgs[4] | 0
										];
									}
								} else {
									console.error('PDFLinkService_setHash: \'' + zoomArg + '\' is not a valid zoom value.');
								}
							}
						}
						if (dest) {
							this.pdfViewer.scrollPageIntoView({
								pageNumber: pageNumber || this.page,
								destArray: dest,
								allowNegativeOffset: true
							});
						} else if (pageNumber) {
							this.page = pageNumber;
						}
						if ('pagemode' in params) {
							this.eventBus.dispatch('pagemode', {
								source: this,
								mode: params.pagemode
							});
						}
					} else {
						if (isPageNumber(hash) && hash <= this.pagesCount) {
							console.warn('PDFLinkService_setHash: specifying a page number ' + 'directly after the hash symbol (#) is deprecated, ' + 'please use the "#page=' + hash + '" form instead.');
							this.page = hash | 0;
						}
						dest = unescape(hash);
						try {
							dest = JSON.parse(dest);
							if (!(dest instanceof Array)) {
								dest = dest.toString();
							}
						} catch (ex) {
						}
						if (typeof dest === 'string' || isValidExplicitDestination(dest)) {
							if (this.pdfHistory) {
								this.pdfHistory.updateNextHashParam(dest);
							}
							this.navigateTo(dest);
							return;
						}
						console.error('PDFLinkService_setHash: \'' + unescape(hash) + '\' is not a valid destination.');
					}
				},
				executeNamedAction: function PDFLinkService_executeNamedAction(action) {
					switch (action) {
						case 'GoBack':
							if (this.pdfHistory) {
								this.pdfHistory.back();
							}
							break;
						case 'GoForward':
							if (this.pdfHistory) {
								this.pdfHistory.forward();
							}
							break;
						case 'NextPage':
							if (this.page < this.pagesCount) {
								this.page++;
							}
							break;
						case 'PrevPage':
							if (this.page > 1) {
								this.page--;
							}
							break;
						case 'LastPage':
							this.page = this.pagesCount;
							break;
						case 'FirstPage':
							this.page = 1;
							break;
						default:
							break;
					}
					this.eventBus.dispatch('namedaction', {
						source: this,
						action: action
					});
				},
				onFileAttachmentAnnotation: function (params) {
					this.eventBus.dispatch('fileattachmentannotation', {
						source: this,
						id: params.id,
						filename: params.filename,
						content: params.content
					});
				},
				cachePageRef: function PDFLinkService_cachePageRef(pageNum, pageRef) {
					var refStr = pageRef.num + ' ' + pageRef.gen + ' R';
					this._pagesRefCache[refStr] = pageNum;
				},
				_cachedPageNumber: function PDFLinkService_cachedPageNumber(pageRef) {
					var refStr = pageRef.num + ' ' + pageRef.gen + ' R';
					return this._pagesRefCache && this._pagesRefCache[refStr] || null;
				}
			};
			function isValidExplicitDestination(dest) {
				if (!(dest instanceof Array)) {
					return false;
				}
				var destLength = dest.length, allowNull = true;
				if (destLength < 2) {
					return false;
				}
				var page = dest[0];
				if (!(typeof page === 'object' && typeof page.num === 'number' && (page.num | 0) === page.num && typeof page.gen === 'number' && (page.gen | 0) === page.gen) && !(typeof page === 'number' && (page | 0) === page && page >= 0)) {
					return false;
				}
				var zoom = dest[1];
				if (!(typeof zoom === 'object' && typeof zoom.name === 'string')) {
					return false;
				}
				switch (zoom.name) {
					case 'XYZ':
						if (destLength !== 5) {
							return false;
						}
						break;
					case 'Fit':
					case 'FitB':
						return destLength === 2;
					case 'FitH':
					case 'FitBH':
					case 'FitV':
					case 'FitBV':
						if (destLength !== 3) {
							return false;
						}
						break;
					case 'FitR':
						if (destLength !== 6) {
							return false;
						}
						allowNull = false;
						break;
					default:
						return false;
				}
				for (var i = 2; i < destLength; i++) {
					var param = dest[i];
					if (!(typeof param === 'number' || allowNull && param === null)) {
						return false;
					}
				}
				return true;
			}
			return PDFLinkService;
		}();
		var SimpleLinkService = function SimpleLinkServiceClosure() {
			function SimpleLinkService() {
			}
			SimpleLinkService.prototype = {
				get page() {
					return 0;
				},
				set page(value) {
				},
				navigateTo: function (dest) {
				},
				getDestinationHash: function (dest) {
					return '#';
				},
				getAnchorUrl: function (hash) {
					return '#';
				},
				setHash: function (hash) {
				},
				executeNamedAction: function (action) {
				},
				onFileAttachmentAnnotation: function (params) {
				},
				cachePageRef: function (pageNum, pageRef) {
				}
			};
			return SimpleLinkService;
		}();
		exports.PDFLinkService = PDFLinkService;
		exports.SimpleLinkService = SimpleLinkService;

		/***/
	}),
/* 6 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtilsLib = __webpack_require__(0);
		var downloadManagerLib = __webpack_require__(11);
		var pdfHistoryLib = __webpack_require__(18);
		var preferencesLib = __webpack_require__(8);
		var pdfSidebarLib = __webpack_require__(22);
		var viewHistoryLib = __webpack_require__(29);
		var pdfThumbnailViewerLib = __webpack_require__(24);
		var toolbarLib = __webpack_require__(28);
		var secondaryToolbarLib = __webpack_require__(26);
		var passwordPromptLib = __webpack_require__(14);
		var pdfPresentationModeLib = __webpack_require__(21);
		var pdfDocumentPropertiesLib = __webpack_require__(16);
		var handToolLib = __webpack_require__(13);
		var pdfViewerLib = __webpack_require__(25);
		var pdfRenderingQueueLib = __webpack_require__(3);
		var pdfLinkServiceLib = __webpack_require__(5);
		var pdfOutlineViewerLib = __webpack_require__(19);
		var overlayManagerLib = __webpack_require__(4);
		var pdfAttachmentViewerLib = __webpack_require__(15);
		var pdfFindControllerLib = __webpack_require__(7);
		var pdfFindBarLib = __webpack_require__(17);
		var domEventsLib = __webpack_require__(2);
		var pdfjsLib = __webpack_require__(1);
		var UNKNOWN_SCALE = uiUtilsLib.UNKNOWN_SCALE;
		var DEFAULT_SCALE_VALUE = uiUtilsLib.DEFAULT_SCALE_VALUE;
		var MIN_SCALE = uiUtilsLib.MIN_SCALE;
		var MAX_SCALE = uiUtilsLib.MAX_SCALE;
		var ProgressBar = uiUtilsLib.ProgressBar;
		var getPDFFileNameFromURL = uiUtilsLib.getPDFFileNameFromURL;
		var noContextMenuHandler = uiUtilsLib.noContextMenuHandler;
		var mozL10n = uiUtilsLib.mozL10n;
		var parseQueryString = uiUtilsLib.parseQueryString;
		var PDFHistory = pdfHistoryLib.PDFHistory;
		var Preferences = preferencesLib.Preferences;
		var SidebarView = pdfSidebarLib.SidebarView;
		var PDFSidebar = pdfSidebarLib.PDFSidebar;
		var ViewHistory = viewHistoryLib.ViewHistory;
		var PDFThumbnailViewer = pdfThumbnailViewerLib.PDFThumbnailViewer;
		var Toolbar = toolbarLib.Toolbar;
		var SecondaryToolbar = secondaryToolbarLib.SecondaryToolbar;
		var PasswordPrompt = passwordPromptLib.PasswordPrompt;
		var PDFPresentationMode = pdfPresentationModeLib.PDFPresentationMode;
		var PDFDocumentProperties = pdfDocumentPropertiesLib.PDFDocumentProperties;
		var HandTool = handToolLib.HandTool;
		var PresentationModeState = pdfViewerLib.PresentationModeState;
		var PDFViewer = pdfViewerLib.PDFViewer;
		var RenderingStates = pdfRenderingQueueLib.RenderingStates;
		var PDFRenderingQueue = pdfRenderingQueueLib.PDFRenderingQueue;
		var PDFLinkService = pdfLinkServiceLib.PDFLinkService;
		var PDFOutlineViewer = pdfOutlineViewerLib.PDFOutlineViewer;
		var OverlayManager = overlayManagerLib.OverlayManager;
		var PDFAttachmentViewer = pdfAttachmentViewerLib.PDFAttachmentViewer;
		var PDFFindController = pdfFindControllerLib.PDFFindController;
		var PDFFindBar = pdfFindBarLib.PDFFindBar;
		var getGlobalEventBus = domEventsLib.getGlobalEventBus;
		var normalizeWheelEventDelta = uiUtilsLib.normalizeWheelEventDelta;
		var animationStarted = uiUtilsLib.animationStarted;
		var localized = uiUtilsLib.localized;
		var RendererType = uiUtilsLib.RendererType;
		var DEFAULT_SCALE_DELTA = 1.1;
		var DISABLE_AUTO_FETCH_LOADING_BAR_TIMEOUT = 5000;
		function configure(PDFJS) {
			PDFJS.cMapPacked = true;
			var scriptTagContainer = document.body || document.getElementsByTagName('head')[0];
			var pdfjsSrc = scriptTagContainer.lastChild.src;
			if (pdfjsSrc) {
				PDFJS.imageResourcesPath = pdfjsSrc.replace(/pdf\.js$/i, 'images/');
				PDFJS.workerSrc = pdfjsSrc.replace(/pdf\.js$/i, 'pdf.worker.js');
				PDFJS.cMapUrl = pdfjsSrc.replace(/pdf\.js$/i, 'cmaps/');
			}
		}
		var DefaultExernalServices = {
			updateFindControlState: function (data) {
			},
			initPassiveLoading: function (callbacks) {
			},
			fallback: function (data, callback) {
			},
			reportTelemetry: function (data) {
			},
			createDownloadManager: function () {
				return new downloadManagerLib.DownloadManager();
			},
			supportsIntegratedFind: false,
			supportsDocumentFonts: true,
			supportsDocumentColors: true,
			supportedMouseWheelZoomModifierKeys: {
				ctrlKey: true,
				metaKey: true
			}
		};
		var PDFViewerApplication = {
			initialBookmark: document.location.hash.substring(1),
			initialDestination: null,
			initialized: false,
			fellback: false,
			appConfig: null,
			pdfDocument: null,
			pdfLoadingTask: null,
			printService: null,
			pdfViewer: null,
			pdfThumbnailViewer: null,
			pdfRenderingQueue: null,
			pdfPresentationMode: null,
			pdfDocumentProperties: null,
			pdfLinkService: null,
			pdfHistory: null,
			pdfSidebar: null,
			pdfOutlineViewer: null,
			pdfAttachmentViewer: null,
			store: null,
			downloadManager: null,
			toolbar: null,
			secondaryToolbar: null,
			eventBus: null,
			pageRotation: 0,
			isInitialViewSet: false,
			viewerPrefs: {
				sidebarViewOnLoad: SidebarView.NONE,
				pdfBugEnabled: false,
				showPreviousViewOnLoad: true,
				defaultZoomValue: '',
				disablePageLabels: false,
				renderer: 'canvas',
				enhanceTextSelection: false,
				renderInteractiveForms: false,
				enablePrintAutoRotate: false
			},
			isViewerEmbedded: window.parent !== window,
			url: '',
			baseUrl: '',
			externalServices: DefaultExernalServices,
			initialize: function pdfViewInitialize(appConfig) {
				var self = this;
				var PDFJS = pdfjsLib.PDFJS;
				Preferences.initialize();
				this.preferences = Preferences;
				configure(PDFJS);
				this.appConfig = appConfig;
				return this._readPreferences().then(function () {
					return self._initializeViewerComponents();
				}).then(function () {
					self.bindEvents();
					self.bindWindowEvents();
					localized.then(function () {
						self.eventBus.dispatch('localized');
					});
					if (self.isViewerEmbedded && !PDFJS.isExternalLinkTargetSet()) {
						PDFJS.externalLinkTarget = PDFJS.LinkTarget.TOP;
					}
					self.initialized = true;
				});
			},
			_readPreferences: function () {
				var self = this;
				var PDFJS = pdfjsLib.PDFJS;
				return Promise.all([
					Preferences.get('enableWebGL').then(function resolved(value) {
						PDFJS.disableWebGL = !value;
					}),
					Preferences.get('sidebarViewOnLoad').then(function resolved(value) {
						self.viewerPrefs['sidebarViewOnLoad'] = value;
					}),
					Preferences.get('pdfBugEnabled').then(function resolved(value) {
						self.viewerPrefs['pdfBugEnabled'] = value;
					}),
					Preferences.get('showPreviousViewOnLoad').then(function resolved(value) {
						self.viewerPrefs['showPreviousViewOnLoad'] = value;
					}),
					Preferences.get('defaultZoomValue').then(function resolved(value) {
						self.viewerPrefs['defaultZoomValue'] = value;
					}),
					Preferences.get('enhanceTextSelection').then(function resolved(value) {
						self.viewerPrefs['enhanceTextSelection'] = value;
					}),
					Preferences.get('disableTextLayer').then(function resolved(value) {
						if (PDFJS.disableTextLayer === true) {
							return;
						}
						PDFJS.disableTextLayer = value;
					}),
					Preferences.get('disableRange').then(function resolved(value) {
						if (PDFJS.disableRange === true) {
							return;
						}
						PDFJS.disableRange = value;
					}),
					Preferences.get('disableStream').then(function resolved(value) {
						if (PDFJS.disableStream === true) {
							return;
						}
						PDFJS.disableStream = value;
					}),
					Preferences.get('disableAutoFetch').then(function resolved(value) {
						PDFJS.disableAutoFetch = value;
					}),
					Preferences.get('disableFontFace').then(function resolved(value) {
						if (PDFJS.disableFontFace === true) {
							return;
						}
						PDFJS.disableFontFace = value;
					}),
					Preferences.get('useOnlyCssZoom').then(function resolved(value) {
						PDFJS.useOnlyCssZoom = value;
					}),
					Preferences.get('externalLinkTarget').then(function resolved(value) {
						if (PDFJS.isExternalLinkTargetSet()) {
							return;
						}
						PDFJS.externalLinkTarget = value;
					}),
					Preferences.get('renderer').then(function resolved(value) {
						self.viewerPrefs['renderer'] = value;
					}),
					Preferences.get('renderInteractiveForms').then(function resolved(value) {
						self.viewerPrefs['renderInteractiveForms'] = value;
					}),
					Preferences.get('disablePageLabels').then(function resolved(value) {
						self.viewerPrefs['disablePageLabels'] = value;
					}),
					Preferences.get('enablePrintAutoRotate').then(function resolved(value) {
						self.viewerPrefs['enablePrintAutoRotate'] = value;
					})
				]).catch(function (reason) {
				});
			},
			_initializeViewerComponents: function () {
				var self = this;
				var appConfig = this.appConfig;
				return new Promise(function (resolve, reject) {
					var eventBus = appConfig.eventBus || getGlobalEventBus();
					self.eventBus = eventBus;
					var pdfRenderingQueue = new PDFRenderingQueue();
					pdfRenderingQueue.onIdle = self.cleanup.bind(self);
					self.pdfRenderingQueue = pdfRenderingQueue;
					var pdfLinkService = new PDFLinkService({ eventBus: eventBus });
					self.pdfLinkService = pdfLinkService;
					var downloadManager = self.externalServices.createDownloadManager();
					self.downloadManager = downloadManager;
					var container = appConfig.mainContainer;
					var viewer = appConfig.viewerContainer;
					self.pdfViewer = new PDFViewer({
						container: container,
						viewer: viewer,
						eventBus: eventBus,
						renderingQueue: pdfRenderingQueue,
						linkService: pdfLinkService,
						downloadManager: downloadManager,
						renderer: self.viewerPrefs['renderer'],
						enhanceTextSelection: self.viewerPrefs['enhanceTextSelection'],
						renderInteractiveForms: self.viewerPrefs['renderInteractiveForms'],
						enablePrintAutoRotate: self.viewerPrefs['enablePrintAutoRotate']
					});
					pdfRenderingQueue.setViewer(self.pdfViewer);
					pdfLinkService.setViewer(self.pdfViewer);
					var thumbnailContainer = appConfig.sidebar.thumbnailView;
					self.pdfThumbnailViewer = new PDFThumbnailViewer({
						container: thumbnailContainer,
						renderingQueue: pdfRenderingQueue,
						linkService: pdfLinkService
					});
					pdfRenderingQueue.setThumbnailViewer(self.pdfThumbnailViewer);
					self.pdfHistory = new PDFHistory({
						linkService: pdfLinkService,
						eventBus: eventBus
					});
					pdfLinkService.setHistory(self.pdfHistory);
					self.findController = new PDFFindController({ pdfViewer: self.pdfViewer });
					self.findController.onUpdateResultsCount = function (matchCount) {
						if (self.supportsIntegratedFind) {
							return;
						}
						self.findBar.updateResultsCount(matchCount);
					};
					self.findController.onUpdateState = function (state, previous, matchCount) {
						if (self.supportsIntegratedFind) {
							self.externalServices.updateFindControlState({
								result: state,
								findPrevious: previous
							});
						} else {
							self.findBar.updateUIState(state, previous, matchCount);
						}
					};
					self.pdfViewer.setFindController(self.findController);
					var findBarConfig = Object.create(appConfig.findBar);
					findBarConfig.findController = self.findController;
					findBarConfig.eventBus = eventBus;
					self.findBar = new PDFFindBar(findBarConfig);
					self.overlayManager = OverlayManager;
					self.handTool = new HandTool({
						container: container,
						eventBus: eventBus
					});
					self.pdfDocumentProperties = new PDFDocumentProperties(appConfig.documentProperties);
					self.toolbar = new Toolbar(appConfig.toolbar, container, eventBus);
					self.secondaryToolbar = new SecondaryToolbar(appConfig.secondaryToolbar, container, eventBus);
					if (self.supportsFullscreen) {
						self.pdfPresentationMode = new PDFPresentationMode({
							container: container,
							viewer: viewer,
							pdfViewer: self.pdfViewer,
							eventBus: eventBus,
							contextMenuItems: appConfig.fullscreen
						});
					}
					self.passwordPrompt = new PasswordPrompt(appConfig.passwordOverlay);
					self.pdfOutlineViewer = new PDFOutlineViewer({
						container: appConfig.sidebar.outlineView,
						eventBus: eventBus,
						linkService: pdfLinkService
					});
					self.pdfAttachmentViewer = new PDFAttachmentViewer({
						container: appConfig.sidebar.attachmentsView,
						eventBus: eventBus,
						downloadManager: downloadManager
					});
					var sidebarConfig = Object.create(appConfig.sidebar);
					sidebarConfig.pdfViewer = self.pdfViewer;
					sidebarConfig.pdfThumbnailViewer = self.pdfThumbnailViewer;
					sidebarConfig.pdfOutlineViewer = self.pdfOutlineViewer;
					sidebarConfig.eventBus = eventBus;
					self.pdfSidebar = new PDFSidebar(sidebarConfig);
					self.pdfSidebar.onToggled = self.forceRendering.bind(self);
					resolve(undefined);
				});
			},
			run: function pdfViewRun(config) {
				this.initialize(config).then(webViewerInitialized);
			},
			zoomIn: function pdfViewZoomIn(ticks) {
				var newScale = this.pdfViewer.currentScale;
				do {
					newScale = (newScale * DEFAULT_SCALE_DELTA).toFixed(2);
					newScale = Math.ceil(newScale * 10) / 10;
					newScale = Math.min(MAX_SCALE, newScale);
				} while (--ticks > 0 && newScale < MAX_SCALE);
				this.pdfViewer.currentScaleValue = newScale;
			},
			zoomOut: function pdfViewZoomOut(ticks) {
				var newScale = this.pdfViewer.currentScale;
				do {
					newScale = (newScale / DEFAULT_SCALE_DELTA).toFixed(2);
					newScale = Math.floor(newScale * 10) / 10;
					newScale = Math.max(MIN_SCALE, newScale);
				} while (--ticks > 0 && newScale > MIN_SCALE);
				this.pdfViewer.currentScaleValue = newScale;
			},
			get pagesCount() {
				return this.pdfDocument ? this.pdfDocument.numPages : 0;
			},
			set page(val) {
				this.pdfViewer.currentPageNumber = val;
			},
			get page() {
				return this.pdfViewer.currentPageNumber;
			},
			get printing() {
				return !!this.printService;
			},
			get supportsPrinting() {
				return PDFPrintServiceFactory.instance.supportsPrinting;
			},
			get supportsFullscreen() {
				var support;
				var doc = document.documentElement;
				support = !!(doc.requestFullscreen || doc.mozRequestFullScreen || doc.webkitRequestFullScreen || doc.msRequestFullscreen);
				if (document.fullscreenEnabled === false || document.mozFullScreenEnabled === false || document.webkitFullscreenEnabled === false || document.msFullscreenEnabled === false) {
					support = false;
				}
				if (support && pdfjsLib.PDFJS.disableFullscreen === true) {
					support = false;
				}
				return pdfjsLib.shadow(this, 'supportsFullscreen', support);
			},
			get supportsIntegratedFind() {
				return this.externalServices.supportsIntegratedFind;
			},
			get supportsDocumentFonts() {
				return this.externalServices.supportsDocumentFonts;
			},
			get supportsDocumentColors() {
				return this.externalServices.supportsDocumentColors;
			},
			get loadingBar() {
				var bar = new ProgressBar('#loadingBar', {});
				return pdfjsLib.shadow(this, 'loadingBar', bar);
			},
			get supportedMouseWheelZoomModifierKeys() {
				return this.externalServices.supportedMouseWheelZoomModifierKeys;
			},
			initPassiveLoading: function pdfViewInitPassiveLoading() {
				throw new Error('Not implemented: initPassiveLoading');
			},
			setTitleUsingUrl: function pdfViewSetTitleUsingUrl(url) {
				this.url = url;
				this.baseUrl = url.split('#')[0];
				var title = getPDFFileNameFromURL(url, '');
				if (!title) {
					try {
						title = decodeURIComponent(pdfjsLib.getFilenameFromUrl(url)) || url;
					} catch (e) {
						title = url;
					}
				}
				this.setTitle(title);
			},
			setTitle: function pdfViewSetTitle(title) {
				if (this.isViewerEmbedded) {
					return;
				}
				document.title = title;
			},
			close: function pdfViewClose() {
				var errorWrapper = this.appConfig.errorWrapper.container;
				errorWrapper.setAttribute('hidden', 'true');
				if (!this.pdfLoadingTask) {
					return Promise.resolve();
				}
				var promise = this.pdfLoadingTask.destroy();
				this.pdfLoadingTask = null;
				if (this.pdfDocument) {
					this.pdfDocument = null;
					this.pdfThumbnailViewer.setDocument(null);
					this.pdfViewer.setDocument(null);
					this.pdfLinkService.setDocument(null, null);
				}
				this.store = null;
				this.isInitialViewSet = false;
				this.pdfSidebar.reset();
				this.pdfOutlineViewer.reset();
				this.pdfAttachmentViewer.reset();
				this.findController.reset();
				this.findBar.reset();
				this.toolbar.reset();
				this.secondaryToolbar.reset();
				if (typeof PDFBug !== 'undefined') {
					PDFBug.cleanup();
				}
				return promise;
			},
			open: function pdfViewOpen(file, args) {
				if (arguments.length > 2 || typeof args === 'number') {
					return Promise.reject(new Error('Call of open() with obsolete signature.'));
				}
				if (this.pdfLoadingTask) {
					return this.close().then(function () {
						Preferences.reload();
						return this.open(file, args);
					}.bind(this));
				}
				var parameters = Object.create(null), scale;
				if (typeof file === 'string') {
					this.setTitleUsingUrl(file);
					parameters.url = file;
				} else if (file && 'byteLength' in file) {
					parameters.data = file;
				} else if (file.url && file.originalUrl) {
					this.setTitleUsingUrl(file.originalUrl);
					parameters.url = file.url;
				}
				if (args) {
					for (var prop in args) {
						parameters[prop] = args[prop];
					}
					if (args.scale) {
						scale = args.scale;
					}
					if (args.length) {
						this.pdfDocumentProperties.setFileSize(args.length);
					}
				}
				var self = this;
				self.downloadComplete = false;
				var loadingTask = pdfjsLib.getDocument(parameters);
				this.pdfLoadingTask = loadingTask;
				loadingTask.onPassword = function passwordNeeded(updateCallback, reason) {
					self.passwordPrompt.setUpdateCallback(updateCallback, reason);
					self.passwordPrompt.open();
				};
				loadingTask.onProgress = function getDocumentProgress(progressData) {
					self.progress(progressData.loaded / progressData.total);
				};
				loadingTask.onUnsupportedFeature = this.fallback.bind(this);
				return loadingTask.promise.then(function getDocumentCallback(pdfDocument) {
					self.load(pdfDocument, scale);
				}, function getDocumentError(exception) {
					var message = exception && exception.message;
					var loadingErrorMessage = mozL10n.get('loading_error', null, 'An error occurred while loading the PDF.');
					if (exception instanceof pdfjsLib.InvalidPDFException) {
						loadingErrorMessage = mozL10n.get('invalid_file_error', null, 'Invalid or corrupted PDF file.');
					} else if (exception instanceof pdfjsLib.MissingPDFException) {
						loadingErrorMessage = mozL10n.get('missing_file_error', null, 'Missing PDF file.');
					} else if (exception instanceof pdfjsLib.UnexpectedResponseException) {
						loadingErrorMessage = mozL10n.get('unexpected_response_error', null, 'Unexpected server response.');
					}
					var moreInfo = { message: message };
					self.error(loadingErrorMessage, moreInfo);
					throw new Error(loadingErrorMessage);
				});
			},
			download: function pdfViewDownload() {
				function downloadByUrl() {
					downloadManager.downloadUrl(url, filename);
				}
				var url = this.baseUrl;
				var filename = getPDFFileNameFromURL(this.url, this.documentInfo.Title);
				var downloadManager = this.downloadManager;
				downloadManager.onerror = function (err) {
					PDFViewerApplication.error('PDF failed to download.');
				};
				if (!this.pdfDocument) {
					downloadByUrl();
					return;
				}
				if (!this.downloadComplete) {
					downloadByUrl();
					return;
				}
				this.pdfDocument.getData().then(function getDataSuccess(data) {
					var blob = pdfjsLib.createBlob(data, 'application/pdf');
					downloadManager.download(blob, url, filename);
				}, downloadByUrl).then(null, downloadByUrl);
			},
			fallback: function pdfViewFallback(featureId) {
			},
			error: function pdfViewError(message, moreInfo) {
				var moreInfoText = mozL10n.get('error_version_info', {
					version: pdfjsLib.version || '?',
					build: pdfjsLib.build || '?'
				}, 'PDF.js v{{version}} (build: {{build}})') + '\n';
				if (moreInfo) {
					moreInfoText += mozL10n.get('error_message', { message: moreInfo.message }, 'Message: {{message}}');
					if (moreInfo.stack) {
						moreInfoText += '\n' + mozL10n.get('error_stack', { stack: moreInfo.stack }, 'Stack: {{stack}}');
					} else {
						if (moreInfo.filename) {
							moreInfoText += '\n' + mozL10n.get('error_file', { file: moreInfo.filename }, 'File: {{file}}');
						}
						if (moreInfo.lineNumber) {
							moreInfoText += '\n' + mozL10n.get('error_line', { line: moreInfo.lineNumber }, 'Line: {{line}}');
						}
					}
				}
				var errorWrapperConfig = this.appConfig.errorWrapper;
				var errorWrapper = errorWrapperConfig.container;
				errorWrapper.removeAttribute('hidden');
				var errorMessage = errorWrapperConfig.errorMessage;
				errorMessage.textContent = message;
				var closeButton = errorWrapperConfig.closeButton;
				closeButton.onclick = function () {
					errorWrapper.setAttribute('hidden', 'true');
				};
				var errorMoreInfo = errorWrapperConfig.errorMoreInfo;
				var moreInfoButton = errorWrapperConfig.moreInfoButton;
				var lessInfoButton = errorWrapperConfig.lessInfoButton;
				moreInfoButton.onclick = function () {
					errorMoreInfo.removeAttribute('hidden');
					moreInfoButton.setAttribute('hidden', 'true');
					lessInfoButton.removeAttribute('hidden');
					errorMoreInfo.style.height = errorMoreInfo.scrollHeight + 'px';
				};
				lessInfoButton.onclick = function () {
					errorMoreInfo.setAttribute('hidden', 'true');
					moreInfoButton.removeAttribute('hidden');
					lessInfoButton.setAttribute('hidden', 'true');
				};
				moreInfoButton.oncontextmenu = noContextMenuHandler;
				lessInfoButton.oncontextmenu = noContextMenuHandler;
				closeButton.oncontextmenu = noContextMenuHandler;
				moreInfoButton.removeAttribute('hidden');
				lessInfoButton.setAttribute('hidden', 'true');
				errorMoreInfo.value = moreInfoText;
			},
			progress: function pdfViewProgress(level) {
				var percent = Math.round(level * 100);
				if (percent > this.loadingBar.percent || isNaN(percent)) {
					this.loadingBar.percent = percent;
					if (pdfjsLib.PDFJS.disableAutoFetch && percent) {
						if (this.disableAutoFetchLoadingBarTimeout) {
							clearTimeout(this.disableAutoFetchLoadingBarTimeout);
							this.disableAutoFetchLoadingBarTimeout = null;
						}
						this.loadingBar.show();
						this.disableAutoFetchLoadingBarTimeout = setTimeout(function () {
							this.loadingBar.hide();
							this.disableAutoFetchLoadingBarTimeout = null;
						}.bind(this), DISABLE_AUTO_FETCH_LOADING_BAR_TIMEOUT);
					}
				}
			},
			load: function pdfViewLoad(pdfDocument, scale) {
				var self = this;
				scale = scale || UNKNOWN_SCALE;
				this.pdfDocument = pdfDocument;
				this.pdfDocumentProperties.setDocumentAndUrl(pdfDocument, this.url);
				var downloadedPromise = pdfDocument.getDownloadInfo().then(function () {
					self.downloadComplete = true;
					self.loadingBar.hide();
				});
				this.toolbar.setPagesCount(pdfDocument.numPages, false);
				this.secondaryToolbar.setPagesCount(pdfDocument.numPages);
				var id = this.documentFingerprint = pdfDocument.fingerprint;
				var store = this.store = new ViewHistory(id);
				var baseDocumentUrl;
				baseDocumentUrl = null;
				this.pdfLinkService.setDocument(pdfDocument, baseDocumentUrl);
				var pdfViewer = this.pdfViewer;
				pdfViewer.currentScale = scale;
				pdfViewer.setDocument(pdfDocument);
				var firstPagePromise = pdfViewer.firstPagePromise;
				var pagesPromise = pdfViewer.pagesPromise;
				var onePageRendered = pdfViewer.onePageRendered;
				this.pageRotation = 0;
				var pdfThumbnailViewer = this.pdfThumbnailViewer;
				pdfThumbnailViewer.setDocument(pdfDocument);
				firstPagePromise.then(function (pdfPage) {
					downloadedPromise.then(function () {
						self.eventBus.dispatch('documentload', { source: self });
					});
					self.loadingBar.setWidth(self.appConfig.viewerContainer);
					if (!pdfjsLib.PDFJS.disableHistory && !self.isViewerEmbedded) {
						if (!self.viewerPrefs['showPreviousViewOnLoad']) {
							self.pdfHistory.clearHistoryState();
						}
						self.pdfHistory.initialize(self.documentFingerprint);
						if (self.pdfHistory.initialDestination) {
							self.initialDestination = self.pdfHistory.initialDestination;
						} else if (self.pdfHistory.initialBookmark) {
							self.initialBookmark = self.pdfHistory.initialBookmark;
						}
					}
					var initialParams = {
						destination: self.initialDestination,
						bookmark: self.initialBookmark,
						hash: null
					};
					store.initializedPromise.then(function resolved() {
						var storedHash = null, sidebarView = null;
						if (self.viewerPrefs['showPreviousViewOnLoad'] && store.get('exists', false)) {
							var pageNum = store.get('page', '1');
							var zoom = self.viewerPrefs['defaultZoomValue'] || store.get('zoom', DEFAULT_SCALE_VALUE);
							var left = store.get('scrollLeft', '0');
							var top = store.get('scrollTop', '0');
							storedHash = 'page=' + pageNum + '&zoom=' + zoom + ',' + left + ',' + top;
							sidebarView = store.get('sidebarView', SidebarView.NONE);
						} else if (self.viewerPrefs['defaultZoomValue']) {
							storedHash = 'page=1&zoom=' + self.viewerPrefs['defaultZoomValue'];
						}
						self.setInitialView(storedHash, {
							scale: scale,
							sidebarView: sidebarView
						});
						initialParams.hash = storedHash;
						if (!self.isViewerEmbedded) {
							self.pdfViewer.focus();
						}
					}, function rejected(reason) {
						console.error(reason);
						self.setInitialView(null, { scale: scale });
					});
					pagesPromise.then(function resolved() {
						if (!initialParams.destination && !initialParams.bookmark && !initialParams.hash) {
							return;
						}
						if (self.hasEqualPageSizes) {
							return;
						}
						self.initialDestination = initialParams.destination;
						self.initialBookmark = initialParams.bookmark;
						self.pdfViewer.currentScaleValue = self.pdfViewer.currentScaleValue;
						self.setInitialView(initialParams.hash);
					});
				});
				pdfDocument.getPageLabels().then(function (labels) {
					if (!labels || self.viewerPrefs['disablePageLabels']) {
						return;
					}
					var i = 0, numLabels = labels.length;
					if (numLabels !== self.pagesCount) {
						console.error('The number of Page Labels does not match ' + 'the number of pages in the document.');
						return;
					}
					while (i < numLabels && labels[i] === (i + 1).toString()) {
						i++;
					}
					if (i === numLabels) {
						return;
					}
					pdfViewer.setPageLabels(labels);
					pdfThumbnailViewer.setPageLabels(labels);
					self.toolbar.setPagesCount(pdfDocument.numPages, true);
					self.toolbar.setPageNumber(pdfViewer.currentPageNumber, pdfViewer.currentPageLabel);
				});
				pagesPromise.then(function () {
					if (self.supportsPrinting) {
						pdfDocument.getJavaScript().then(function (javaScript) {
							if (javaScript.length) {
								console.warn('Warning: JavaScript is not supported');
								self.fallback(pdfjsLib.UNSUPPORTED_FEATURES.javaScript);
							}
							var regex = /\bprint\s*\(/;
							for (var i = 0, ii = javaScript.length; i < ii; i++) {
								var js = javaScript[i];
								if (js && regex.test(js)) {
									setTimeout(function () {
										window.print();
									});
									return;
								}
							}
						});
					}
				});
				Promise.all([
					onePageRendered,
					animationStarted
				]).then(function () {
					pdfDocument.getOutline().then(function (outline) {
						self.pdfOutlineViewer.render({ outline: outline });
					});
					pdfDocument.getAttachments().then(function (attachments) {
						self.pdfAttachmentViewer.render({ attachments: attachments });
					});
				});
				pdfDocument.getMetadata().then(function (data) {
					var info = data.info, metadata = data.metadata;
					self.documentInfo = info;
					self.metadata = metadata;
					console.log('PDF ' + pdfDocument.fingerprint + ' [' + info.PDFFormatVersion + ' ' + (info.Producer || '-').trim() + ' / ' + (info.Creator || '-').trim() + ']' + ' (PDF.js: ' + (pdfjsLib.version || '-') + (!pdfjsLib.PDFJS.disableWebGL ? ' [WebGL]' : '') + ')');
					var pdfTitle;
					if (metadata && metadata.has('dc:title')) {
						var title = metadata.get('dc:title');
						if (title !== 'Untitled') {
							pdfTitle = title;
						}
					}
					if (!pdfTitle && info && info['Title']) {
						pdfTitle = info['Title'];
					}
					if (pdfTitle) {
						self.setTitle(pdfTitle + ' - ' + document.title);
					}
					if (info.IsAcroFormPresent) {
						console.warn('Warning: AcroForm/XFA is not supported');
						self.fallback(pdfjsLib.UNSUPPORTED_FEATURES.forms);
					}
				});
			},
			setInitialView: function pdfViewSetInitialView(storedHash, options) {
				var scale = options && options.scale;
				var sidebarView = options && options.sidebarView;
				this.isInitialViewSet = true;
				this.pdfSidebar.setInitialView(this.viewerPrefs['sidebarViewOnLoad'] || sidebarView | 0);
				if (this.initialDestination) {
					this.pdfLinkService.navigateTo(this.initialDestination);
					this.initialDestination = null;
				} else if (this.initialBookmark) {
					this.pdfLinkService.setHash(this.initialBookmark);
					this.pdfHistory.push({ hash: this.initialBookmark }, true);
					this.initialBookmark = null;
				} else if (storedHash) {
					this.pdfLinkService.setHash(storedHash);
				} else if (scale) {
					this.pdfViewer.currentScaleValue = scale;
					this.page = 1;
				}
				this.toolbar.setPageNumber(this.pdfViewer.currentPageNumber, this.pdfViewer.currentPageLabel);
				this.secondaryToolbar.setPageNumber(this.pdfViewer.currentPageNumber);
				if (!this.pdfViewer.currentScaleValue) {
					this.pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE;
				}
			},
			cleanup: function pdfViewCleanup() {
				if (!this.pdfDocument) {
					return;
				}
				this.pdfViewer.cleanup();
				this.pdfThumbnailViewer.cleanup();
				if (this.pdfViewer.renderer !== RendererType.SVG) {
					if (this.pdfDocument) {
						this.pdfDocument.cleanup();
					}
				}
			},
			forceRendering: function pdfViewForceRendering() {
				this.pdfRenderingQueue.printing = this.printing;
				this.pdfRenderingQueue.isThumbnailViewEnabled = this.pdfSidebar.isThumbnailViewVisible;
				this.pdfRenderingQueue.renderHighestPriority();
			},
			beforePrint: function pdfViewSetupBeforePrint() {
				if (this.printService) {
					return;
				}
				if (!this.supportsPrinting) {
					var printMessage = mozL10n.get('printing_not_supported', null, 'Warning: Printing is not fully supported by this browser.');
					this.error(printMessage);
					return;
				}
				if (!this.pdfViewer.pageViewsReady) {
					var notReadyMessage = mozL10n.get('printing_not_ready', null, 'Warning: The PDF is not fully loaded for printing.');
					window.alert(notReadyMessage);
					return;
				}
				var pagesOverview = this.pdfViewer.getPagesOverview();
				var printContainer = this.appConfig.printContainer;
				var printService = PDFPrintServiceFactory.instance.createPrintService(this.pdfDocument, pagesOverview, printContainer);
				this.printService = printService;
				this.forceRendering();
				printService.layout();
			},
			get hasEqualPageSizes() {
				var firstPage = this.pdfViewer.getPageView(0);
				for (var i = 1, ii = this.pagesCount; i < ii; ++i) {
					var pageView = this.pdfViewer.getPageView(i);
					if (pageView.width !== firstPage.width || pageView.height !== firstPage.height) {
						return false;
					}
				}
				return true;
			},
			afterPrint: function pdfViewSetupAfterPrint() {
				if (this.printService) {
					this.printService.destroy();
					this.printService = null;
				}
				this.forceRendering();
			},
			rotatePages: function pdfViewRotatePages(delta) {
				var pageNumber = this.page;
				this.pageRotation = (this.pageRotation + 360 + delta) % 360;
				this.pdfViewer.pagesRotation = this.pageRotation;
				this.pdfThumbnailViewer.pagesRotation = this.pageRotation;
				this.forceRendering();
				this.pdfViewer.currentPageNumber = pageNumber;
			},
			requestPresentationMode: function pdfViewRequestPresentationMode() {
				if (!this.pdfPresentationMode) {
					return;
				}
				this.pdfPresentationMode.request();
			},
			bindEvents: function pdfViewBindEvents() {
				var eventBus = this.eventBus;
				eventBus.on('resize', webViewerResize);
				eventBus.on('hashchange', webViewerHashchange);
				eventBus.on('beforeprint', this.beforePrint.bind(this));
				eventBus.on('afterprint', this.afterPrint.bind(this));
				eventBus.on('pagerendered', webViewerPageRendered);
				eventBus.on('textlayerrendered', webViewerTextLayerRendered);
				eventBus.on('updateviewarea', webViewerUpdateViewarea);
				eventBus.on('pagechanging', webViewerPageChanging);
				eventBus.on('scalechanging', webViewerScaleChanging);
				eventBus.on('sidebarviewchanged', webViewerSidebarViewChanged);
				eventBus.on('pagemode', webViewerPageMode);
				eventBus.on('namedaction', webViewerNamedAction);
				eventBus.on('presentationmodechanged', webViewerPresentationModeChanged);
				eventBus.on('presentationmode', webViewerPresentationMode);
				eventBus.on('openfile', webViewerOpenFile);
				eventBus.on('print', webViewerPrint);
				eventBus.on('download', webViewerDownload);
				eventBus.on('firstpage', webViewerFirstPage);
				eventBus.on('lastpage', webViewerLastPage);
				eventBus.on('nextpage', webViewerNextPage);
				eventBus.on('previouspage', webViewerPreviousPage);
				eventBus.on('zoomin', webViewerZoomIn);
				eventBus.on('zoomout', webViewerZoomOut);
				eventBus.on('pagenumberchanged', webViewerPageNumberChanged);
				eventBus.on('scalechanged', webViewerScaleChanged);
				eventBus.on('rotatecw', webViewerRotateCw);
				eventBus.on('rotateccw', webViewerRotateCcw);
				eventBus.on('documentproperties', webViewerDocumentProperties);
				eventBus.on('find', webViewerFind);
				eventBus.on('findfromurlhash', webViewerFindFromUrlHash);
				eventBus.on('fileinputchange', webViewerFileInputChange);
			},
			bindWindowEvents: function pdfViewBindWindowEvents() {
				var eventBus = this.eventBus;
				window.addEventListener('wheel', webViewerWheel);
				window.addEventListener('click', webViewerClick);
				window.addEventListener('keydown', webViewerKeyDown);
				window.addEventListener('resize', function windowResize() {
					eventBus.dispatch('resize');
				});
				window.addEventListener('hashchange', function windowHashChange() {
					eventBus.dispatch('hashchange', { hash: document.location.hash.substring(1) });
				});
				window.addEventListener('beforeprint', function windowBeforePrint() {
					eventBus.dispatch('beforeprint');
				});
				window.addEventListener('afterprint', function windowAfterPrint() {
					eventBus.dispatch('afterprint');
				});
				window.addEventListener('change', function windowChange(evt) {
					var files = evt.target.files;
					if (!files || files.length === 0) {
						return;
					}
					if (evt.target.id == PDFViewerApplication.appConfig.openFileInputName)
						eventBus.dispatch('fileinputchange', { fileInput: evt.target });
				});
			}
		};
		var validateFileURL;
		var HOSTED_VIEWER_ORIGINS = [
			'null',
			'http://mozilla.github.io',
			'https://mozilla.github.io'
		];
		validateFileURL = function validateFileURL(file) {
			try {
				var viewerOrigin = new URL(window.location.href).origin || 'null';
				if (HOSTED_VIEWER_ORIGINS.indexOf(viewerOrigin) >= 0) {
					return;
				}
				var fileOrigin = new URL(file, window.location.href).origin;
				if (fileOrigin !== viewerOrigin) {
					throw new Error('file origin does not match viewer\'s');
				}
			} catch (e) {
				var message = e && e.message;
				var loadingErrorMessage = mozL10n.get('loading_error', null, 'An error occurred while loading the PDF.');
				var moreInfo = { message: message };
				PDFViewerApplication.error(loadingErrorMessage, moreInfo);
				throw e;
			}
		};
		function loadAndEnablePDFBug(enabledTabs) {
			return new Promise(function (resolve, reject) {
				var appConfig = PDFViewerApplication.appConfig;
				var script = document.createElement('script');
				script.src = appConfig.debuggerScriptPath;
				script.onload = function () {
					PDFBug.enable(enabledTabs);
					PDFBug.init(pdfjsLib, appConfig.mainContainer);
					resolve();
				};
				script.onerror = function () {
					reject(new Error('Cannot load debugger at ' + script.src));
				};
				(document.getElementsByTagName('head')[0] || document.body).appendChild(script);
			});
		}
		function webViewerInitialized() {
			var appConfig = PDFViewerApplication.appConfig;
			var file;
			var queryString = document.location.search.substring(1);
			var params = parseQueryString(queryString);
			file = 'file' in params ? params.file : appConfig.defaultUrl;
			validateFileURL(file);
			var waitForBeforeOpening = [];
			var fileInput = document.createElement('input');
			fileInput.id = appConfig.openFileInputName;
			fileInput.className = 'fileInput';
			fileInput.setAttribute('type', 'file');
			fileInput.oncontextmenu = noContextMenuHandler;
			document.body.appendChild(fileInput);
			if (!window.File || !window.FileReader || !window.FileList || !window.Blob) {
				appConfig.toolbar.openFile.setAttribute('hidden', 'true');
				appConfig.secondaryToolbar.openFileButton.setAttribute('hidden', 'true');
			} else {
				fileInput.value = null;
			}
			var PDFJS = pdfjsLib.PDFJS;
			if (PDFViewerApplication.viewerPrefs['pdfBugEnabled']) {
				var hash = document.location.hash.substring(1);
				var hashParams = parseQueryString(hash);
				if ('disableworker' in hashParams) {
					PDFJS.disableWorker = hashParams['disableworker'] === 'true';
				}
				if ('disablerange' in hashParams) {
					PDFJS.disableRange = hashParams['disablerange'] === 'true';
				}
				if ('disablestream' in hashParams) {
					PDFJS.disableStream = hashParams['disablestream'] === 'true';
				}
				if ('disableautofetch' in hashParams) {
					PDFJS.disableAutoFetch = hashParams['disableautofetch'] === 'true';
				}
				if ('disablefontface' in hashParams) {
					PDFJS.disableFontFace = hashParams['disablefontface'] === 'true';
				}
				if ('disablehistory' in hashParams) {
					PDFJS.disableHistory = hashParams['disablehistory'] === 'true';
				}
				if ('webgl' in hashParams) {
					PDFJS.disableWebGL = hashParams['webgl'] !== 'true';
				}
				if ('useonlycsszoom' in hashParams) {
					PDFJS.useOnlyCssZoom = hashParams['useonlycsszoom'] === 'true';
				}
				if ('verbosity' in hashParams) {
					PDFJS.verbosity = hashParams['verbosity'] | 0;
				}
				if ('ignorecurrentpositiononzoom' in hashParams) {
					PDFJS.ignoreCurrentPositionOnZoom = hashParams['ignorecurrentpositiononzoom'] === 'true';
				}
				if ('locale' in hashParams) {
					PDFJS.locale = hashParams['locale'];
				}
				if ('textlayer' in hashParams) {
					switch (hashParams['textlayer']) {
						case 'off':
							PDFJS.disableTextLayer = true;
							break;
						case 'visible':
						case 'shadow':
						case 'hover':
							var viewer = appConfig.viewerContainer;
							viewer.classList.add('textLayer-' + hashParams['textlayer']);
							break;
					}
				}
				if ('pdfbug' in hashParams) {
					PDFJS.pdfBug = true;
					var pdfBug = hashParams['pdfbug'];
					var enabled = pdfBug.split(',');
					waitForBeforeOpening.push(loadAndEnablePDFBug(enabled));
				}
			}
			mozL10n.setLanguage(PDFJS.locale);
			if (!PDFViewerApplication.supportsPrinting) {
				appConfig.toolbar.print.classList.add('hidden');
				appConfig.secondaryToolbar.printButton.classList.add('hidden');
			}
			if (!PDFViewerApplication.supportsFullscreen) {
				appConfig.toolbar.presentationModeButton.classList.add('hidden');
				appConfig.secondaryToolbar.presentationModeButton.classList.add('hidden');
			}
			if (PDFViewerApplication.supportsIntegratedFind) {
				appConfig.toolbar.viewFind.classList.add('hidden');
			}
			appConfig.sidebar.mainContainer.addEventListener('transitionend', function (e) {
				if (e.target === this) {
					PDFViewerApplication.eventBus.dispatch('resize');
				}
			}, true);
			appConfig.sidebar.toggleButton.addEventListener('click', function () {
				PDFViewerApplication.pdfSidebar.toggle();
			});
			Promise.all(waitForBeforeOpening).then(function () {
				webViewerOpenFileViaURL(file);
			}).catch(function (reason) {
				PDFViewerApplication.error(mozL10n.get('loading_error', null, 'An error occurred while opening.'), reason);
			});
		}
		var webViewerOpenFileViaURL;
		webViewerOpenFileViaURL = function webViewerOpenFileViaURL(file) {
			if (file && file.lastIndexOf('file:', 0) === 0) {
				PDFViewerApplication.setTitleUsingUrl(file);
				var xhr = new XMLHttpRequest();
				xhr.onload = function () {
					PDFViewerApplication.open(new Uint8Array(xhr.response));
				};
				try {
					xhr.open('GET', file);
					xhr.responseType = 'arraybuffer';
					xhr.send();
				} catch (e) {
					PDFViewerApplication.error(mozL10n.get('loading_error', null, 'An error occurred while loading the PDF.'), e);
				}
				return;
			}
			if (file) {
				PDFViewerApplication.open(file);
			}
		};
		function webViewerPageRendered(e) {
			var pageNumber = e.pageNumber;
			var pageIndex = pageNumber - 1;
			var pageView = PDFViewerApplication.pdfViewer.getPageView(pageIndex);
			if (pageNumber === PDFViewerApplication.page) {
				PDFViewerApplication.toolbar.updateLoadingIndicatorState(false);
			}
			if (!pageView) {
				return;
			}
			if (PDFViewerApplication.pdfSidebar.isThumbnailViewVisible) {
				var thumbnailView = PDFViewerApplication.pdfThumbnailViewer.getThumbnail(pageIndex);
				thumbnailView.setImage(pageView);
			}
			if (pdfjsLib.PDFJS.pdfBug && Stats.enabled && pageView.stats) {
				Stats.add(pageNumber, pageView.stats);
			}
			if (pageView.error) {
				PDFViewerApplication.error(mozL10n.get('rendering_error', null, 'An error occurred while rendering the page.'), pageView.error);
			}
		}
		function webViewerTextLayerRendered(e) {
		}
		function webViewerPageMode(e) {
			var mode = e.mode, view;
			switch (mode) {
				case 'thumbs':
					view = SidebarView.THUMBS;
					break;
				case 'bookmarks':
				case 'outline':
					view = SidebarView.OUTLINE;
					break;
				case 'attachments':
					view = SidebarView.ATTACHMENTS;
					break;
				case 'none':
					view = SidebarView.NONE;
					break;
				default:
					console.error('Invalid "pagemode" hash parameter: ' + mode);
					return;
			}
			PDFViewerApplication.pdfSidebar.switchView(view, true);
		}
		function webViewerNamedAction(e) {
			var action = e.action;
			switch (action) {
				case 'GoToPage':
					PDFViewerApplication.appConfig.toolbar.pageNumber.select();
					break;
				case 'Find':
					if (!PDFViewerApplication.supportsIntegratedFind) {
						PDFViewerApplication.findBar.toggle();
					}
					break;
			}
		}
		function webViewerPresentationModeChanged(e) {
			var active = e.active;
			var switchInProgress = e.switchInProgress;
			PDFViewerApplication.pdfViewer.presentationModeState = switchInProgress ? PresentationModeState.CHANGING : active ? PresentationModeState.FULLSCREEN : PresentationModeState.NORMAL;
		}
		function webViewerSidebarViewChanged(e) {
			PDFViewerApplication.pdfRenderingQueue.isThumbnailViewEnabled = PDFViewerApplication.pdfSidebar.isThumbnailViewVisible;
			var store = PDFViewerApplication.store;
			if (!store || !PDFViewerApplication.isInitialViewSet) {
				return;
			}
			store.initializedPromise.then(function () {
				store.set('sidebarView', e.view).catch(function () {
				});
			});
		}
		function webViewerUpdateViewarea(e) {
			var location = e.location, store = PDFViewerApplication.store;
			if (store) {
				store.initializedPromise.then(function () {
					store.setMultiple({
						'exists': true,
						'page': location.pageNumber,
						'zoom': location.scale,
						'scrollLeft': location.left,
						'scrollTop': location.top
					}).catch(function () {
					});
				});
			}
			var href = PDFViewerApplication.pdfLinkService.getAnchorUrl(location.pdfOpenParams);
			PDFViewerApplication.appConfig.toolbar.viewBookmark.href = href;
			PDFViewerApplication.appConfig.secondaryToolbar.viewBookmarkButton.href = href;
			PDFViewerApplication.pdfHistory.updateCurrentBookmark(location.pdfOpenParams, location.pageNumber);
			var currentPage = PDFViewerApplication.pdfViewer.getPageView(PDFViewerApplication.page - 1);
			var loading = currentPage.renderingState !== RenderingStates.FINISHED;
			PDFViewerApplication.toolbar.updateLoadingIndicatorState(loading);
		}
		function webViewerResize() {
			var currentScaleValue = PDFViewerApplication.pdfViewer.currentScaleValue;
			if (currentScaleValue === 'auto' || currentScaleValue === 'page-fit' || currentScaleValue === 'page-width') {
				PDFViewerApplication.pdfViewer.currentScaleValue = currentScaleValue;
			} else if (!currentScaleValue) {
				PDFViewerApplication.pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE;
			}
			PDFViewerApplication.pdfViewer.update();
		}
		function webViewerHashchange(e) {
			if (PDFViewerApplication.pdfHistory.isHashChangeUnlocked) {
				var hash = e.hash;
				if (!hash) {
					return;
				}
				if (!PDFViewerApplication.isInitialViewSet) {
					PDFViewerApplication.initialBookmark = hash;
				} else {
					PDFViewerApplication.pdfLinkService.setHash(hash);
				}
			}
		}
		var webViewerFileInputChange;
		webViewerFileInputChange = function webViewerFileInputChange(e) {
			var file = e.fileInput.files[0];
			if (!pdfjsLib.PDFJS.disableCreateObjectURL && typeof URL !== 'undefined' && URL.createObjectURL) {
				PDFViewerApplication.open(URL.createObjectURL(file));
			} else {
				var fileReader = new FileReader();
				fileReader.onload = function webViewerChangeFileReaderOnload(evt) {
					var buffer = evt.target.result;
					var uint8Array = new Uint8Array(buffer);
					PDFViewerApplication.open(uint8Array);
				};
				fileReader.readAsArrayBuffer(file);
			}
			PDFViewerApplication.setTitleUsingUrl(file.name);
			var appConfig = PDFViewerApplication.appConfig;
			appConfig.toolbar.viewBookmark.setAttribute('hidden', 'true');
			appConfig.secondaryToolbar.viewBookmarkButton.setAttribute('hidden', 'true');
			appConfig.toolbar.download.setAttribute('hidden', 'true');
			appConfig.secondaryToolbar.downloadButton.setAttribute('hidden', 'true');
		};
		function webViewerPresentationMode() {
			PDFViewerApplication.requestPresentationMode();
		}
		function webViewerOpenFile() {
			var openFileInputName = PDFViewerApplication.appConfig.openFileInputName;
			document.getElementById(openFileInputName).click();
		}
		function webViewerPrint() {
			window.print();
		}
		function webViewerDownload() {
			PDFViewerApplication.download();
		}
		function webViewerFirstPage() {
			if (PDFViewerApplication.pdfDocument) {
				PDFViewerApplication.page = 1;
			}
		}
		function webViewerLastPage() {
			if (PDFViewerApplication.pdfDocument) {
				PDFViewerApplication.page = PDFViewerApplication.pagesCount;
			}
		}
		function webViewerNextPage() {
			PDFViewerApplication.page++;
		}
		function webViewerPreviousPage() {
			PDFViewerApplication.page--;
		}
		function webViewerZoomIn() {
			PDFViewerApplication.zoomIn();
		}
		function webViewerZoomOut() {
			PDFViewerApplication.zoomOut();
		}
		function webViewerPageNumberChanged(e) {
			var pdfViewer = PDFViewerApplication.pdfViewer;
			pdfViewer.currentPageLabel = e.value;
			if (e.value !== pdfViewer.currentPageNumber.toString() && e.value !== pdfViewer.currentPageLabel) {
				PDFViewerApplication.toolbar.setPageNumber(pdfViewer.currentPageNumber, pdfViewer.currentPageLabel);
			}
		}
		function webViewerScaleChanged(e) {
			PDFViewerApplication.pdfViewer.currentScaleValue = e.value;
		}
		function webViewerRotateCw() {
			PDFViewerApplication.rotatePages(90);
		}
		function webViewerRotateCcw() {
			PDFViewerApplication.rotatePages(-90);
		}
		function webViewerDocumentProperties() {
			PDFViewerApplication.pdfDocumentProperties.open();
		}
		function webViewerFind(e) {
			PDFViewerApplication.findController.executeCommand('find' + e.type, {
				query: e.query,
				phraseSearch: e.phraseSearch,
				caseSensitive: e.caseSensitive,
				highlightAll: e.highlightAll,
				findPrevious: e.findPrevious
			});
		}
		function webViewerFindFromUrlHash(e) {
			PDFViewerApplication.findController.executeCommand('find', {
				query: e.query,
				phraseSearch: e.phraseSearch,
				caseSensitive: false,
				highlightAll: true,
				findPrevious: false
			});
		}
		function webViewerScaleChanging(e) {
			PDFViewerApplication.toolbar.setPageScale(e.presetValue, e.scale);
			PDFViewerApplication.pdfViewer.update();
		}
		function webViewerPageChanging(e) {
			var page = e.pageNumber;
			PDFViewerApplication.toolbar.setPageNumber(page, e.pageLabel || null);
			PDFViewerApplication.secondaryToolbar.setPageNumber(page);
			if (PDFViewerApplication.pdfSidebar.isThumbnailViewVisible) {
				PDFViewerApplication.pdfThumbnailViewer.scrollThumbnailIntoView(page);
			}
			if (pdfjsLib.PDFJS.pdfBug && Stats.enabled) {
				var pageView = PDFViewerApplication.pdfViewer.getPageView(page - 1);
				if (pageView.stats) {
					Stats.add(page, pageView.stats);
				}
			}
		}
		var zoomDisabled = false, zoomDisabledTimeout;
		function webViewerWheel(evt) {
			var pdfViewer = PDFViewerApplication.pdfViewer;
			if (pdfViewer.isInPresentationMode) {
				return;
			}
			if (evt.ctrlKey || evt.metaKey) {
				var support = PDFViewerApplication.supportedMouseWheelZoomModifierKeys;
				if (evt.ctrlKey && !support.ctrlKey || evt.metaKey && !support.metaKey) {
					return;
				}
				evt.preventDefault();
				if (zoomDisabled) {
					return;
				}
				var previousScale = pdfViewer.currentScale;
				var delta = normalizeWheelEventDelta(evt);
				var MOUSE_WHEEL_DELTA_PER_PAGE_SCALE = 3.0;
				var ticks = delta * MOUSE_WHEEL_DELTA_PER_PAGE_SCALE;
				if (ticks < 0) {
					PDFViewerApplication.zoomOut(-ticks);
				} else {
					PDFViewerApplication.zoomIn(ticks);
				}
				var currentScale = pdfViewer.currentScale;
				if (previousScale !== currentScale) {
					var scaleCorrectionFactor = currentScale / previousScale - 1;
					var rect = pdfViewer.container.getBoundingClientRect();
					var dx = evt.clientX - rect.left;
					var dy = evt.clientY - rect.top;
					pdfViewer.container.scrollLeft += dx * scaleCorrectionFactor;
					pdfViewer.container.scrollTop += dy * scaleCorrectionFactor;
				}
			} else {
				zoomDisabled = true;
				clearTimeout(zoomDisabledTimeout);
				zoomDisabledTimeout = setTimeout(function () {
					zoomDisabled = false;
				}, 1000);
			}
		}
		function webViewerClick(evt) {
			if (!PDFViewerApplication.secondaryToolbar.isOpen) {
				return;
			}
			var appConfig = PDFViewerApplication.appConfig;
			if (PDFViewerApplication.pdfViewer.containsElement(evt.target) || appConfig.toolbar.container.contains(evt.target) && evt.target !== appConfig.secondaryToolbar.toggleButton) {
				PDFViewerApplication.secondaryToolbar.close();
			}
		}
		function webViewerKeyDown(evt) {
			if (OverlayManager.active) {
				return;
			}
			var handled = false, ensureViewerFocused = false;
			var cmd = (evt.ctrlKey ? 1 : 0) | (evt.altKey ? 2 : 0) | (evt.shiftKey ? 4 : 0) | (evt.metaKey ? 8 : 0);
			var pdfViewer = PDFViewerApplication.pdfViewer;
			var isViewerInPresentationMode = pdfViewer && pdfViewer.isInPresentationMode;
			if (cmd === 1 || cmd === 8 || cmd === 5 || cmd === 12) {
				switch (evt.keyCode) {
					case 70:
						if (!PDFViewerApplication.supportsIntegratedFind) {
							PDFViewerApplication.findBar.open();
							handled = true;
						}
						break;
					case 71:
						if (!PDFViewerApplication.supportsIntegratedFind) {
							var findState = PDFViewerApplication.findController.state;
							if (findState) {
								PDFViewerApplication.findController.executeCommand('findagain', {
									query: findState.query,
									phraseSearch: findState.phraseSearch,
									caseSensitive: findState.caseSensitive,
									highlightAll: findState.highlightAll,
									findPrevious: cmd === 5 || cmd === 12
								});
							}
							handled = true;
						}
						break;
					case 61:
					case 107:
					case 187:
					case 171:
						if (!isViewerInPresentationMode) {
							PDFViewerApplication.zoomIn();
						}
						handled = true;
						break;
					case 173:
					case 109:
					case 189:
						if (!isViewerInPresentationMode) {
							PDFViewerApplication.zoomOut();
						}
						handled = true;
						break;
					case 48:
					case 96:
						if (!isViewerInPresentationMode) {
							setTimeout(function () {
								pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE;
							});
							handled = false;
						}
						break;
					case 38:
						if (isViewerInPresentationMode || PDFViewerApplication.page > 1) {
							PDFViewerApplication.page = 1;
							handled = true;
							ensureViewerFocused = true;
						}
						break;
					case 40:
						if (isViewerInPresentationMode || PDFViewerApplication.page < PDFViewerApplication.pagesCount) {
							PDFViewerApplication.page = PDFViewerApplication.pagesCount;
							handled = true;
							ensureViewerFocused = true;
						}
						break;
				}
			}
			if (cmd === 1 || cmd === 8) {
				switch (evt.keyCode) {
					case 83:
						PDFViewerApplication.download();
						handled = true;
						break;
				}
			}
			if (cmd === 3 || cmd === 10) {
				switch (evt.keyCode) {
					case 80:
						PDFViewerApplication.requestPresentationMode();
						handled = true;
						break;
					case 71:
						PDFViewerApplication.appConfig.toolbar.pageNumber.select();
						handled = true;
						break;
				}
			}
			if (handled) {
				if (ensureViewerFocused && !isViewerInPresentationMode) {
					pdfViewer.focus();
				}
				evt.preventDefault();
				return;
			}
			var curElement = document.activeElement || document.querySelector(':focus');
			var curElementTagName = curElement && curElement.tagName.toUpperCase();
			if (curElementTagName === 'INPUT' || curElementTagName === 'TEXTAREA' || curElementTagName === 'SELECT') {
				if (evt.keyCode !== 27) {
					return;
				}
			}
			if (cmd === 0) {
				switch (evt.keyCode) {
					case 38:
					case 33:
					case 8:
						if (!isViewerInPresentationMode && pdfViewer.currentScaleValue !== 'page-fit') {
							break;
						}
					case 37:
						if (pdfViewer.isHorizontalScrollbarEnabled) {
							break;
						}
					case 75:
					case 80:
						if (PDFViewerApplication.page > 1) {
							PDFViewerApplication.page--;
						}
						handled = true;
						break;
					case 27:
						if (PDFViewerApplication.secondaryToolbar.isOpen) {
							PDFViewerApplication.secondaryToolbar.close();
							handled = true;
						}
						if (!PDFViewerApplication.supportsIntegratedFind && PDFViewerApplication.findBar.opened) {
							PDFViewerApplication.findBar.close();
							handled = true;
						}
						break;
					case 40:
					case 34:
					case 32:
						if (!isViewerInPresentationMode && pdfViewer.currentScaleValue !== 'page-fit') {
							break;
						}
					case 39:
						if (pdfViewer.isHorizontalScrollbarEnabled) {
							break;
						}
					case 74:
					case 78:
						if (PDFViewerApplication.page < PDFViewerApplication.pagesCount) {
							PDFViewerApplication.page++;
						}
						handled = true;
						break;
					case 36:
						if (isViewerInPresentationMode || PDFViewerApplication.page > 1) {
							PDFViewerApplication.page = 1;
							handled = true;
							ensureViewerFocused = true;
						}
						break;
					case 35:
						if (isViewerInPresentationMode || PDFViewerApplication.page < PDFViewerApplication.pagesCount) {
							PDFViewerApplication.page = PDFViewerApplication.pagesCount;
							handled = true;
							ensureViewerFocused = true;
						}
						break;
					case 72:
						if (!isViewerInPresentationMode) {
							PDFViewerApplication.handTool.toggle();
						}
						break;
					case 82:
						PDFViewerApplication.rotatePages(90);
						break;
				}
			}
			if (cmd === 4) {
				switch (evt.keyCode) {
					case 32:
						if (!isViewerInPresentationMode && pdfViewer.currentScaleValue !== 'page-fit') {
							break;
						}
						if (PDFViewerApplication.page > 1) {
							PDFViewerApplication.page--;
						}
						handled = true;
						break;
					case 82:
						PDFViewerApplication.rotatePages(-90);
						break;
				}
			}
			if (!handled && !isViewerInPresentationMode) {
				if (evt.keyCode >= 33 && evt.keyCode <= 40 || evt.keyCode === 32 && curElementTagName !== 'BUTTON') {
					ensureViewerFocused = true;
				}
			}
			if (cmd === 2) {
				switch (evt.keyCode) {
					case 37:
						if (isViewerInPresentationMode) {
							PDFViewerApplication.pdfHistory.back();
							handled = true;
						}
						break;
					case 39:
						if (isViewerInPresentationMode) {
							PDFViewerApplication.pdfHistory.forward();
							handled = true;
						}
						break;
				}
			}
			if (ensureViewerFocused && !pdfViewer.containsElement(curElement)) {
				pdfViewer.focus();
			}
			if (handled) {
				evt.preventDefault();
			}
		}
		localized.then(function webViewerLocalized() {
			document.getElementsByTagName('html')[0].dir = mozL10n.getDirection();
		});
		var PDFPrintServiceFactory = {
			instance: {
				supportsPrinting: false,
				createPrintService: function () {
					throw new Error('Not implemented: createPrintService');
				}
			}
		};
		exports.PDFViewerApplication = PDFViewerApplication;
		exports.DefaultExernalServices = DefaultExernalServices;
		exports.PDFPrintServiceFactory = PDFPrintServiceFactory;

		/***/
	}),
/* 7 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var scrollIntoView = uiUtils.scrollIntoView;
		var FindStates = {
			FIND_FOUND: 0,
			FIND_NOTFOUND: 1,
			FIND_WRAPPED: 2,
			FIND_PENDING: 3
		};
		var FIND_SCROLL_OFFSET_TOP = -50;
		var FIND_SCROLL_OFFSET_LEFT = -400;
		var CHARACTERS_TO_NORMALIZE = {
			'\u2018': '\'',
			'\u2019': '\'',
			'\u201A': '\'',
			'\u201B': '\'',
			'\u201C': '"',
			'\u201D': '"',
			'\u201E': '"',
			'\u201F': '"',
			'\u00BC': '1/4',
			'\u00BD': '1/2',
			'\u00BE': '3/4'
		};
		var PDFFindController = function PDFFindControllerClosure() {
			function PDFFindController(options) {
				this.pdfViewer = options.pdfViewer || null;
				this.onUpdateResultsCount = null;
				this.onUpdateState = null;
				this.reset();
				var replace = Object.keys(CHARACTERS_TO_NORMALIZE).join('');
				this.normalizationRegex = new RegExp('[' + replace + ']', 'g');
			}
			PDFFindController.prototype = {
				reset: function PDFFindController_reset() {
					this.startedTextExtraction = false;
					this.extractTextPromises = [];
					this.pendingFindMatches = Object.create(null);
					this.active = false;
					this.pageContents = [];
					this.pageMatches = [];
					this.pageMatchesLength = null;
					this.matchCount = 0;
					this.selected = {
						pageIdx: -1,
						matchIdx: -1
					};
					this.offset = {
						pageIdx: null,
						matchIdx: null
					};
					this.pagesToSearch = null;
					this.resumePageIdx = null;
					this.state = null;
					this.dirtyMatch = false;
					this.findTimeout = null;
					this.firstPagePromise = new Promise(function (resolve) {
						this.resolveFirstPage = resolve;
					}.bind(this));
				},
				normalize: function PDFFindController_normalize(text) {
					return text.replace(this.normalizationRegex, function (ch) {
						return CHARACTERS_TO_NORMALIZE[ch];
					});
				},
				_prepareMatches: function PDFFindController_prepareMatches(matchesWithLength, matches, matchesLength) {
					function isSubTerm(matchesWithLength, currentIndex) {
						var currentElem, prevElem, nextElem;
						currentElem = matchesWithLength[currentIndex];
						nextElem = matchesWithLength[currentIndex + 1];
						if (currentIndex < matchesWithLength.length - 1 && currentElem.match === nextElem.match) {
							currentElem.skipped = true;
							return true;
						}
						for (var i = currentIndex - 1; i >= 0; i--) {
							prevElem = matchesWithLength[i];
							if (prevElem.skipped) {
								continue;
							}
							if (prevElem.match + prevElem.matchLength < currentElem.match) {
								break;
							}
							if (prevElem.match + prevElem.matchLength >= currentElem.match + currentElem.matchLength) {
								currentElem.skipped = true;
								return true;
							}
						}
						return false;
					}
					var i, len;
					matchesWithLength.sort(function (a, b) {
						return a.match === b.match ? a.matchLength - b.matchLength : a.match - b.match;
					});
					for (i = 0, len = matchesWithLength.length; i < len; i++) {
						if (isSubTerm(matchesWithLength, i)) {
							continue;
						}
						matches.push(matchesWithLength[i].match);
						matchesLength.push(matchesWithLength[i].matchLength);
					}
				},
				calcFindPhraseMatch: function PDFFindController_calcFindPhraseMatch(query, pageIndex, pageContent) {
					var matches = [];
					var queryLen = query.length;
					var matchIdx = -queryLen;
					while (true) {
						matchIdx = pageContent.indexOf(query, matchIdx + queryLen);
						if (matchIdx === -1) {
							break;
						}
						matches.push(matchIdx);
					}
					this.pageMatches[pageIndex] = matches;
				},
				calcFindWordMatch: function PDFFindController_calcFindWordMatch(query, pageIndex, pageContent) {
					var matchesWithLength = [];
					var queryArray = query.match(/\S+/g);
					var subquery, subqueryLen, matchIdx;
					for (var i = 0, len = queryArray.length; i < len; i++) {
						subquery = queryArray[i];
						subqueryLen = subquery.length;
						matchIdx = -subqueryLen;
						while (true) {
							matchIdx = pageContent.indexOf(subquery, matchIdx + subqueryLen);
							if (matchIdx === -1) {
								break;
							}
							matchesWithLength.push({
								match: matchIdx,
								matchLength: subqueryLen,
								skipped: false
							});
						}
					}
					if (!this.pageMatchesLength) {
						this.pageMatchesLength = [];
					}
					this.pageMatchesLength[pageIndex] = [];
					this.pageMatches[pageIndex] = [];
					this._prepareMatches(matchesWithLength, this.pageMatches[pageIndex], this.pageMatchesLength[pageIndex]);
				},
				calcFindMatch: function PDFFindController_calcFindMatch(pageIndex) {
					var pageContent = this.normalize(this.pageContents[pageIndex]);
					var query = this.normalize(this.state.query);
					var caseSensitive = this.state.caseSensitive;
					var phraseSearch = this.state.phraseSearch;
					var queryLen = query.length;
					if (queryLen === 0) {
						return;
					}
					if (!caseSensitive) {
						pageContent = pageContent.toLowerCase();
						query = query.toLowerCase();
					}
					if (phraseSearch) {
						this.calcFindPhraseMatch(query, pageIndex, pageContent);
					} else {
						this.calcFindWordMatch(query, pageIndex, pageContent);
					}
					this.updatePage(pageIndex);
					if (this.resumePageIdx === pageIndex) {
						this.resumePageIdx = null;
						this.nextPageMatch();
					}
					if (this.pageMatches[pageIndex].length > 0) {
						this.matchCount += this.pageMatches[pageIndex].length;
						this.updateUIResultsCount();
					}
				},
				extractText: function PDFFindController_extractText() {
					if (this.startedTextExtraction) {
						return;
					}
					this.startedTextExtraction = true;
					this.pageContents = [];
					var extractTextPromisesResolves = [];
					var numPages = this.pdfViewer.pagesCount;
					for (var i = 0; i < numPages; i++) {
						this.extractTextPromises.push(new Promise(function (resolve) {
							extractTextPromisesResolves.push(resolve);
						}));
					}
					var self = this;
					function extractPageText(pageIndex) {
						self.pdfViewer.getPageTextContent(pageIndex).then(function textContentResolved(textContent) {
							var textItems = textContent.items;
							var str = [];
							for (var i = 0, len = textItems.length; i < len; i++) {
								str.push(textItems[i].str);
							}
							self.pageContents.push(str.join(''));
							extractTextPromisesResolves[pageIndex](pageIndex);
							if (pageIndex + 1 < self.pdfViewer.pagesCount) {
								extractPageText(pageIndex + 1);
							}
						});
					}
					extractPageText(0);
				},
				executeCommand: function PDFFindController_executeCommand(cmd, state) {
					if (this.state === null || cmd !== 'findagain') {
						this.dirtyMatch = true;
					}
					this.state = state;
					this.updateUIState(FindStates.FIND_PENDING);
					this.firstPagePromise.then(function () {
						this.extractText();
						clearTimeout(this.findTimeout);
						if (cmd === 'find') {
							this.findTimeout = setTimeout(this.nextMatch.bind(this), 250);
						} else {
							this.nextMatch();
						}
					}.bind(this));
				},
				updatePage: function PDFFindController_updatePage(index) {
					if (this.selected.pageIdx === index) {
						this.pdfViewer.currentPageNumber = index + 1;
					}
					var page = this.pdfViewer.getPageView(index);
					if (page.textLayer) {
						page.textLayer.updateMatches();
					}
				},
				nextMatch: function PDFFindController_nextMatch() {
					var previous = this.state.findPrevious;
					var currentPageIndex = this.pdfViewer.currentPageNumber - 1;
					var numPages = this.pdfViewer.pagesCount;
					this.active = true;
					if (this.dirtyMatch) {
						this.dirtyMatch = false;
						this.selected.pageIdx = this.selected.matchIdx = -1;
						this.offset.pageIdx = currentPageIndex;
						this.offset.matchIdx = null;
						this.hadMatch = false;
						this.resumePageIdx = null;
						this.pageMatches = [];
						this.matchCount = 0;
						this.pageMatchesLength = null;
						var self = this;
						for (var i = 0; i < numPages; i++) {
							this.updatePage(i);
							if (!(i in this.pendingFindMatches)) {
								this.pendingFindMatches[i] = true;
								this.extractTextPromises[i].then(function (pageIdx) {
									delete self.pendingFindMatches[pageIdx];
									self.calcFindMatch(pageIdx);
								});
							}
						}
					}
					if (this.state.query === '') {
						this.updateUIState(FindStates.FIND_FOUND);
						return;
					}
					if (this.resumePageIdx) {
						return;
					}
					var offset = this.offset;
					this.pagesToSearch = numPages;
					if (offset.matchIdx !== null) {
						var numPageMatches = this.pageMatches[offset.pageIdx].length;
						if (!previous && offset.matchIdx + 1 < numPageMatches || previous && offset.matchIdx > 0) {
							this.hadMatch = true;
							offset.matchIdx = previous ? offset.matchIdx - 1 : offset.matchIdx + 1;
							this.updateMatch(true);
							return;
						}
						this.advanceOffsetPage(previous);
					}
					this.nextPageMatch();
				},
				matchesReady: function PDFFindController_matchesReady(matches) {
					var offset = this.offset;
					var numMatches = matches.length;
					var previous = this.state.findPrevious;
					if (numMatches) {
						this.hadMatch = true;
						offset.matchIdx = previous ? numMatches - 1 : 0;
						this.updateMatch(true);
						return true;
					}
					this.advanceOffsetPage(previous);
					if (offset.wrapped) {
						offset.matchIdx = null;
						if (this.pagesToSearch < 0) {
							this.updateMatch(false);
							return true;
						}
					}
					return false;
				},
				updateMatchPosition: function PDFFindController_updateMatchPosition(pageIndex, index, elements, beginIdx) {
					if (this.selected.matchIdx === index && this.selected.pageIdx === pageIndex) {
						var spot = {
							top: FIND_SCROLL_OFFSET_TOP,
							left: FIND_SCROLL_OFFSET_LEFT
						};
						scrollIntoView(elements[beginIdx], spot, true);
					}
				},
				nextPageMatch: function PDFFindController_nextPageMatch() {
					if (this.resumePageIdx !== null) {
						console.error('There can only be one pending page.');
					}
					do {
						var pageIdx = this.offset.pageIdx;
						var matches = this.pageMatches[pageIdx];
						if (!matches) {
							this.resumePageIdx = pageIdx;
							break;
						}
					} while (!this.matchesReady(matches));
				},
				advanceOffsetPage: function PDFFindController_advanceOffsetPage(previous) {
					var offset = this.offset;
					var numPages = this.extractTextPromises.length;
					offset.pageIdx = previous ? offset.pageIdx - 1 : offset.pageIdx + 1;
					offset.matchIdx = null;
					this.pagesToSearch--;
					if (offset.pageIdx >= numPages || offset.pageIdx < 0) {
						offset.pageIdx = previous ? numPages - 1 : 0;
						offset.wrapped = true;
					}
				},
				updateMatch: function PDFFindController_updateMatch(found) {
					var state = FindStates.FIND_NOTFOUND;
					var wrapped = this.offset.wrapped;
					this.offset.wrapped = false;
					if (found) {
						var previousPage = this.selected.pageIdx;
						this.selected.pageIdx = this.offset.pageIdx;
						this.selected.matchIdx = this.offset.matchIdx;
						state = wrapped ? FindStates.FIND_WRAPPED : FindStates.FIND_FOUND;
						if (previousPage !== -1 && previousPage !== this.selected.pageIdx) {
							this.updatePage(previousPage);
						}
					}
					this.updateUIState(state, this.state.findPrevious);
					if (this.selected.pageIdx !== -1) {
						this.updatePage(this.selected.pageIdx);
					}
				},
				updateUIResultsCount: function PDFFindController_updateUIResultsCount() {
					if (this.onUpdateResultsCount) {
						this.onUpdateResultsCount(this.matchCount);
					}
				},
				updateUIState: function PDFFindController_updateUIState(state, previous) {
					if (this.onUpdateState) {
						this.onUpdateState(state, previous, this.matchCount);
					}
				}
			};
			return PDFFindController;
		}();
		exports.FindStates = FindStates;
		exports.PDFFindController = PDFFindController;

		/***/
	}),
/* 8 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var defaultPreferences = null;
		function getDefaultPreferences() {
			if (!defaultPreferences) {
				defaultPreferences = Promise.resolve({
					"showPreviousViewOnLoad": true,
					"defaultZoomValue": "",
					"sidebarViewOnLoad": 0,
					"enableHandToolOnLoad": false,
					"enableWebGL": false,
					"pdfBugEnabled": false,
					"disableRange": false,
					"disableStream": false,
					"disableAutoFetch": false,
					"disableFontFace": false,
					"disableTextLayer": false,
					"useOnlyCssZoom": false,
					"externalLinkTarget": 0,
					"enhanceTextSelection": false,
					"renderer": "canvas",
					"renderInteractiveForms": false,
					"enablePrintAutoRotate": false,
					"disablePageLabels": false
				});
			}
			return defaultPreferences;
		}
		function cloneObj(obj) {
			var result = {};
			for (var i in obj) {
				if (Object.prototype.hasOwnProperty.call(obj, i)) {
					result[i] = obj[i];
				}
			}
			return result;
		}
		var Preferences = {
			prefs: null,
			isInitializedPromiseResolved: false,
			initializedPromise: null,
			initialize: function preferencesInitialize() {
				return this.initializedPromise = getDefaultPreferences().then(function (defaults) {
					Object.defineProperty(this, 'defaults', {
						value: Object.freeze(defaults),
						writable: false,
						enumerable: true,
						configurable: false
					});
					this.prefs = cloneObj(defaults);
					return this._readFromStorage(defaults);
				}.bind(this)).then(function (prefObj) {
					this.isInitializedPromiseResolved = true;
					if (prefObj) {
						this.prefs = prefObj;
					}
				}.bind(this));
			},
			_writeToStorage: function preferences_writeToStorage(prefObj) {
				return Promise.resolve();
			},
			_readFromStorage: function preferences_readFromStorage(prefObj) {
				return Promise.resolve();
			},
			reset: function preferencesReset() {
				return this.initializedPromise.then(function () {
					this.prefs = cloneObj(this.defaults);
					return this._writeToStorage(this.defaults);
				}.bind(this));
			},
			reload: function preferencesReload() {
				return this.initializedPromise.then(function () {
					this._readFromStorage(this.defaults).then(function (prefObj) {
						if (prefObj) {
							this.prefs = prefObj;
						}
					}.bind(this));
				}.bind(this));
			},
			set: function preferencesSet(name, value) {
				return this.initializedPromise.then(function () {
					if (this.defaults[name] === undefined) {
						throw new Error('preferencesSet: \'' + name + '\' is undefined.');
					} else if (value === undefined) {
						throw new Error('preferencesSet: no value is specified.');
					}
					var valueType = typeof value;
					var defaultType = typeof this.defaults[name];
					if (valueType !== defaultType) {
						if (valueType === 'number' && defaultType === 'string') {
							value = value.toString();
						} else {
							throw new Error('Preferences_set: \'' + value + '\' is a \"' + valueType + '\", expected \"' + defaultType + '\".');
						}
					} else {
						if (valueType === 'number' && (value | 0) !== value) {
							throw new Error('Preferences_set: \'' + value + '\' must be an \"integer\".');
						}
					}
					this.prefs[name] = value;
					return this._writeToStorage(this.prefs);
				}.bind(this));
			},
			get: function preferencesGet(name) {
				return this.initializedPromise.then(function () {
					var defaultValue = this.defaults[name];
					if (defaultValue === undefined) {
						throw new Error('preferencesGet: \'' + name + '\' is undefined.');
					} else {
						var prefValue = this.prefs[name];
						if (prefValue !== undefined) {
							return prefValue;
						}
					}
					return defaultValue;
				}.bind(this));
			}
		};
		Preferences._writeToStorage = function (prefObj) {
			return new Promise(function (resolve) {
				localStorage.setItem('pdfjs.preferences', JSON.stringify(prefObj));
				resolve();
			});
		};
		Preferences._readFromStorage = function (prefObj) {
			return new Promise(function (resolve) {
				var readPrefs = JSON.parse(localStorage.getItem('pdfjs.preferences'));
				resolve(readPrefs);
			});
		};
		exports.Preferences = Preferences;

		/***/
	}),
/* 9 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var overlayManager = __webpack_require__(4);
		var app = __webpack_require__(6);
		var pdfjsLib = __webpack_require__(1);
		var mozL10n = uiUtils.mozL10n;
		var CSS_UNITS = uiUtils.CSS_UNITS;
		var PDFPrintServiceFactory = app.PDFPrintServiceFactory;
		var OverlayManager = overlayManager.OverlayManager;
		var activeService = null;
		function renderPage(activeServiceOnEntry, pdfDocument, pageNumber, size) {
			var scratchCanvas = activeService.scratchCanvas;
			var PRINT_RESOLUTION = 150;
			var PRINT_UNITS = PRINT_RESOLUTION / 72.0;
			scratchCanvas.width = Math.floor(size.width * PRINT_UNITS);
			scratchCanvas.height = Math.floor(size.height * PRINT_UNITS);
			var width = Math.floor(size.width * CSS_UNITS) + 'px';
			var height = Math.floor(size.height * CSS_UNITS) + 'px';
			var ctx = scratchCanvas.getContext('2d');
			ctx.save();
			ctx.fillStyle = 'rgb(255, 255, 255)';
			ctx.fillRect(0, 0, scratchCanvas.width, scratchCanvas.height);
			ctx.restore();
			return pdfDocument.getPage(pageNumber).then(function (pdfPage) {
				var renderContext = {
					canvasContext: ctx,
					transform: [
						PRINT_UNITS,
						0,
						0,
						PRINT_UNITS,
						0,
						0
					],
					viewport: pdfPage.getViewport(1, size.rotation),
					intent: 'print'
				};
				return pdfPage.render(renderContext).promise;
			}).then(function () {
				return {
					width: width,
					height: height
				};
			});
		}
		function PDFPrintService(pdfDocument, pagesOverview, printContainer) {
			this.pdfDocument = pdfDocument;
			this.pagesOverview = pagesOverview;
			this.printContainer = printContainer;
			this.currentPage = -1;
			this.scratchCanvas = document.createElement('canvas');
		}
		PDFPrintService.prototype = {
			layout: function () {
				this.throwIfInactive();
				var body = document.querySelector('body');
				body.setAttribute('data-pdfjsprinting', true);
				var hasEqualPageSizes = this.pagesOverview.every(function (size) {
					return size.width === this.pagesOverview[0].width && size.height === this.pagesOverview[0].height;
				}, this);
				if (!hasEqualPageSizes) {
					console.warn('Not all pages have the same size. The printed ' + 'result may be incorrect!');
				}
				this.pageStyleSheet = document.createElement('style');
				var pageSize = this.pagesOverview[0];
				this.pageStyleSheet.textContent = '@supports ((size:A4) and (size:1pt 1pt)) {' + '@page { size: ' + pageSize.width + 'pt ' + pageSize.height + 'pt;}' + '}';
				body.appendChild(this.pageStyleSheet);
			},
			destroy: function () {
				if (activeService !== this) {
					return;
				}
				this.printContainer.textContent = '';
				if (this.pageStyleSheet && this.pageStyleSheet.parentNode) {
					this.pageStyleSheet.parentNode.removeChild(this.pageStyleSheet);
					this.pageStyleSheet = null;
				}
				this.scratchCanvas.width = this.scratchCanvas.height = 0;
				this.scratchCanvas = null;
				activeService = null;
				ensureOverlay().then(function () {
					if (OverlayManager.active !== 'printServiceOverlay') {
						return;
					}
					OverlayManager.close('printServiceOverlay');
				});
			},
			renderPages: function () {
				var pageCount = this.pagesOverview.length;
				var renderNextPage = function (resolve, reject) {
					this.throwIfInactive();
					if (++this.currentPage >= pageCount) {
						renderProgress(pageCount, pageCount);
						resolve();
						return;
					}
					var index = this.currentPage;
					renderProgress(index, pageCount);
					renderPage(this, this.pdfDocument, index + 1, this.pagesOverview[index]).then(this.useRenderedPage.bind(this)).then(function () {
						renderNextPage(resolve, reject);
					}, reject);
				}.bind(this);
				return new Promise(renderNextPage);
			},
			useRenderedPage: function (printItem) {
				this.throwIfInactive();
				var img = document.createElement('img');
				img.style.width = printItem.width;
				img.style.height = printItem.height;
				var scratchCanvas = this.scratchCanvas;
				if ('toBlob' in scratchCanvas && !pdfjsLib.PDFJS.disableCreateObjectURL) {
					scratchCanvas.toBlob(function (blob) {
						img.src = URL.createObjectURL(blob);
					});
				} else {
					img.src = scratchCanvas.toDataURL();
				}
				var wrapper = document.createElement('div');
				wrapper.appendChild(img);
				this.printContainer.appendChild(wrapper);
				return new Promise(function (resolve, reject) {
					img.onload = resolve;
					img.onerror = reject;
				});
			},
			performPrint: function () {
				this.throwIfInactive();
				return new Promise(function (resolve) {
					setTimeout(function () {
						if (!this.active) {
							resolve();
							return;
						}
						print.call(window);
						setTimeout(resolve, 20);
					}.bind(this), 0);
				}.bind(this));
			},
			get active() {
				return this === activeService;
			},
			throwIfInactive: function () {
				if (!this.active) {
					throw new Error('This print request was cancelled or completed.');
				}
			}
		};
		var print = window.print;
		window.print = function print() {
			if (activeService) {
				console.warn('Ignored window.print() because of a pending print job.');
				return;
			}
			ensureOverlay().then(function () {
				if (activeService) {
					OverlayManager.open('printServiceOverlay');
				}
			});
			try {
				dispatchEvent('beforeprint');
			} finally {
				if (!activeService) {
					console.error('Expected print service to be initialized.');
					if (OverlayManager.active === 'printServiceOverlay') {
						OverlayManager.close('printServiceOverlay');
					}
					return;
				}
				var activeServiceOnEntry = activeService;
				activeService.renderPages().then(function () {
					return activeServiceOnEntry.performPrint();
				}).catch(function () {
				}).then(function () {
					if (activeServiceOnEntry.active) {
						abort();
					}
				});
			}
		};
		function dispatchEvent(eventType) {
			var event = document.createEvent('CustomEvent');
			event.initCustomEvent(eventType, false, false, 'custom');
			window.dispatchEvent(event);
		}
		function abort() {
			if (activeService) {
				activeService.destroy();
				dispatchEvent('afterprint');
			}
		}
		function renderProgress(index, total) {
			var progressContainer = document.getElementById('printServiceOverlay');
			var progress = Math.round(100 * index / total);
			var progressBar = progressContainer.querySelector('progress');
			var progressPerc = progressContainer.querySelector('.relative-progress');
			progressBar.value = progress;
			progressPerc.textContent = mozL10n.get('print_progress_percent', { progress: progress }, progress + '%');
		}
		var hasAttachEvent = !!document.attachEvent;
		window.addEventListener('keydown', function (event) {
			if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey && (!event.shiftKey || window.chrome || window.opera)) {
				window.print();
				if (hasAttachEvent) {
					return;
				}
				event.preventDefault();
				if (event.stopImmediatePropagation) {
					event.stopImmediatePropagation();
				} else {
					event.stopPropagation();
				}
				return;
			}
		}, true);
		if (hasAttachEvent) {
			document.attachEvent('onkeydown', function (event) {
				event = event || window.event;
				if (event.keyCode === 80 && event.ctrlKey) {
					event.keyCode = 0;
					return false;
				}
			});
		}
		if ('onbeforeprint' in window) {
			var stopPropagationIfNeeded = function (event) {
				if (event.detail !== 'custom' && event.stopImmediatePropagation) {
					event.stopImmediatePropagation();
				}
			};
			window.addEventListener('beforeprint', stopPropagationIfNeeded);
			window.addEventListener('afterprint', stopPropagationIfNeeded);
		}
		var overlayPromise;
		function ensureOverlay() {
			if (!overlayPromise) {
				overlayPromise = OverlayManager.register('printServiceOverlay', document.getElementById('printServiceOverlay'), abort, true);
				document.getElementById('printCancel').onclick = abort;
			}
			return overlayPromise;
		}
		PDFPrintServiceFactory.instance = {
			supportsPrinting: true,
			createPrintService: function (pdfDocument, pagesOverview, printContainer) {
				if (activeService) {
					throw new Error('The print service is created and active.');
				}
				activeService = new PDFPrintService(pdfDocument, pagesOverview, printContainer);
				return activeService;
			}
		};
		exports.PDFPrintService = PDFPrintService;

		/***/
	}),
/* 10 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfLinkService = __webpack_require__(5);
		var pdfjsLib = __webpack_require__(1);
		var mozL10n = uiUtils.mozL10n;
		var SimpleLinkService = pdfLinkService.SimpleLinkService;
		var AnnotationLayerBuilder = function AnnotationLayerBuilderClosure() {
			function AnnotationLayerBuilder(options) {
				this.pageDiv = options.pageDiv;
				this.pdfPage = options.pdfPage;
				this.renderInteractiveForms = options.renderInteractiveForms;
				this.linkService = options.linkService;
				this.downloadManager = options.downloadManager;
				this.div = null;
			}
			AnnotationLayerBuilder.prototype = {
				render: function AnnotationLayerBuilder_render(viewport, intent) {
					var self = this;
					var parameters = { intent: intent === undefined ? 'display' : intent };
					this.pdfPage.getAnnotations(parameters).then(function (annotations) {
						viewport = viewport.clone({ dontFlip: true });
						parameters = {
							viewport: viewport,
							div: self.div,
							annotations: annotations,
							page: self.pdfPage,
							renderInteractiveForms: self.renderInteractiveForms,
							linkService: self.linkService,
							downloadManager: self.downloadManager
						};
						if (self.div) {
							pdfjsLib.AnnotationLayer.update(parameters);
						} else {
							if (annotations.length === 0) {
								return;
							}
							self.div = document.createElement('div');
							self.div.className = 'annotationLayer';
							self.pageDiv.appendChild(self.div);
							parameters.div = self.div;
							pdfjsLib.AnnotationLayer.render(parameters);
							if (typeof mozL10n !== 'undefined') {
								mozL10n.translate(self.div);
							}
						}
					});
				},
				hide: function AnnotationLayerBuilder_hide() {
					if (!this.div) {
						return;
					}
					this.div.setAttribute('hidden', 'true');
				}
			};
			return AnnotationLayerBuilder;
		}();
		function DefaultAnnotationLayerFactory() {
		}
		DefaultAnnotationLayerFactory.prototype = {
			createAnnotationLayerBuilder: function (pageDiv, pdfPage, renderInteractiveForms) {
				return new AnnotationLayerBuilder({
					pageDiv: pageDiv,
					pdfPage: pdfPage,
					renderInteractiveForms: renderInteractiveForms,
					linkService: new SimpleLinkService()
				});
			}
		};
		exports.AnnotationLayerBuilder = AnnotationLayerBuilder;
		exports.DefaultAnnotationLayerFactory = DefaultAnnotationLayerFactory;

		/***/
	}),
/* 11 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var pdfjsLib = __webpack_require__(1);
		function download(blobUrl, filename) {
			var a = document.createElement('a');
			if (a.click) {
				a.href = blobUrl;
				a.target = '_parent';
				if ('download' in a) {
					a.download = filename;
				}
				(document.body || document.documentElement).appendChild(a);
				a.click();
				a.parentNode.removeChild(a);
			} else {
				if (window.top === window && blobUrl.split('#')[0] === window.location.href.split('#')[0]) {
					var padCharacter = blobUrl.indexOf('?') === -1 ? '?' : '&';
					blobUrl = blobUrl.replace(/#|$/, padCharacter + '$&');
				}
				window.open(blobUrl, '_parent');
			}
		}
		function DownloadManager() {
		}
		DownloadManager.prototype = {
			downloadUrl: function DownloadManager_downloadUrl(url, filename) {
				if (!pdfjsLib.createValidAbsoluteUrl(url, 'http://example.com')) {
					return;
				}
				download(url + '#pdfjs.action=download', filename);
			},
			downloadData: function DownloadManager_downloadData(data, filename, contentType) {
				if (navigator.msSaveBlob) {
					return navigator.msSaveBlob(new Blob([data], { type: contentType }), filename);
				}
				var blobUrl = pdfjsLib.createObjectURL(data, contentType, pdfjsLib.PDFJS.disableCreateObjectURL);
				download(blobUrl, filename);
			},
			download: function DownloadManager_download(blob, url, filename) {
				if (navigator.msSaveBlob) {
					if (!navigator.msSaveBlob(blob, filename)) {
						this.downloadUrl(url, filename);
					}
					return;
				}
				if (pdfjsLib.PDFJS.disableCreateObjectURL) {
					this.downloadUrl(url, filename);
					return;
				}
				var blobUrl = URL.createObjectURL(blob);
				download(blobUrl, filename);
			}
		};
		exports.DownloadManager = DownloadManager;

		/***/
	}),
/* 12 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		function GrabToPan(options) {
			this.element = options.element;
			this.document = options.element.ownerDocument;
			if (typeof options.ignoreTarget === 'function') {
				this.ignoreTarget = options.ignoreTarget;
			}
			this.onActiveChanged = options.onActiveChanged;
			this.activate = this.activate.bind(this);
			this.deactivate = this.deactivate.bind(this);
			this.toggle = this.toggle.bind(this);
			this._onmousedown = this._onmousedown.bind(this);
			this._onmousemove = this._onmousemove.bind(this);
			this._endPan = this._endPan.bind(this);
			var overlay = this.overlay = document.createElement('div');
			overlay.className = 'grab-to-pan-grabbing';
		}
		GrabToPan.prototype = {
			CSS_CLASS_GRAB: 'grab-to-pan-grab',
			activate: function GrabToPan_activate() {
				if (!this.active) {
					this.active = true;
					this.element.addEventListener('mousedown', this._onmousedown, true);
					this.element.classList.add(this.CSS_CLASS_GRAB);
					if (this.onActiveChanged) {
						this.onActiveChanged(true);
					}
				}
			},
			deactivate: function GrabToPan_deactivate() {
				if (this.active) {
					this.active = false;
					this.element.removeEventListener('mousedown', this._onmousedown, true);
					this._endPan();
					this.element.classList.remove(this.CSS_CLASS_GRAB);
					if (this.onActiveChanged) {
						this.onActiveChanged(false);
					}
				}
			},
			toggle: function GrabToPan_toggle() {
				if (this.active) {
					this.deactivate();
				} else {
					this.activate();
				}
			},
			ignoreTarget: function GrabToPan_ignoreTarget(node) {
				return node[matchesSelector]('a[href], a[href] *, input, textarea, button, button *, select, option');
			},
			_onmousedown: function GrabToPan__onmousedown(event) {
				if (event.button !== 0 || this.ignoreTarget(event.target)) {
					return;
				}
				if (event.originalTarget) {
					try {
						event.originalTarget.tagName;
					} catch (e) {
						return;
					}
				}
				this.scrollLeftStart = this.element.scrollLeft;
				this.scrollTopStart = this.element.scrollTop;
				this.clientXStart = event.clientX;
				this.clientYStart = event.clientY;
				this.document.addEventListener('mousemove', this._onmousemove, true);
				this.document.addEventListener('mouseup', this._endPan, true);
				this.element.addEventListener('scroll', this._endPan, true);
				event.preventDefault();
				event.stopPropagation();
				var focusedElement = document.activeElement;
				if (focusedElement && !focusedElement.contains(event.target)) {
					focusedElement.blur();
				}
			},
			_onmousemove: function GrabToPan__onmousemove(event) {
				this.element.removeEventListener('scroll', this._endPan, true);
				if (isLeftMouseReleased(event)) {
					this._endPan();
					return;
				}
				var xDiff = event.clientX - this.clientXStart;
				var yDiff = event.clientY - this.clientYStart;
				var scrollTop = this.scrollTopStart - yDiff;
				var scrollLeft = this.scrollLeftStart - xDiff;
				if (this.element.scrollTo) {
					this.element.scrollTo({
						top: scrollTop,
						left: scrollLeft,
						behavior: 'instant'
					});
				} else {
					this.element.scrollTop = scrollTop;
					this.element.scrollLeft = scrollLeft;
				}
				if (!this.overlay.parentNode) {
					document.body.appendChild(this.overlay);
				}
			},
			_endPan: function GrabToPan__endPan() {
				this.element.removeEventListener('scroll', this._endPan, true);
				this.document.removeEventListener('mousemove', this._onmousemove, true);
				this.document.removeEventListener('mouseup', this._endPan, true);
				this.overlay.remove();
			}
		};
		var matchesSelector;
		[
			'webkitM',
			'mozM',
			'msM',
			'oM',
			'm'
		].some(function (prefix) {
			var name = prefix + 'atches';
			if (name in document.documentElement) {
				matchesSelector = name;
			}
			name += 'Selector';
			if (name in document.documentElement) {
				matchesSelector = name;
			}
			return matchesSelector;
		});
		var isNotIEorIsIE10plus = !document.documentMode || document.documentMode > 9;
		var chrome = window.chrome;
		var isChrome15OrOpera15plus = chrome && (chrome.webstore || chrome.app);
		var isSafari6plus = /Apple/.test(navigator.vendor) && /Version\/([6-9]\d*|[1-5]\d+)/.test(navigator.userAgent);
		function isLeftMouseReleased(event) {
			if ('buttons' in event && isNotIEorIsIE10plus) {
				return !(event.buttons & 1);
			}
			if (isChrome15OrOpera15plus || isSafari6plus) {
				return event.which === 0;
			}
		}
		exports.GrabToPan = GrabToPan;

		/***/
	}),
/* 13 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var grabToPan = __webpack_require__(12);
		var preferences = __webpack_require__(8);
		var uiUtils = __webpack_require__(0);
		var GrabToPan = grabToPan.GrabToPan;
		var Preferences = preferences.Preferences;
		var localized = uiUtils.localized;
		var HandTool = function HandToolClosure() {
			function HandTool(options) {
				this.container = options.container;
				this.eventBus = options.eventBus;
				this.wasActive = false;
				this.handTool = new GrabToPan({
					element: this.container,
					onActiveChanged: function (isActive) {
						this.eventBus.dispatch('handtoolchanged', { isActive: isActive });
					}.bind(this)
				});
				this.eventBus.on('togglehandtool', this.toggle.bind(this));
				Promise.all([
					localized,
					Preferences.get('enableHandToolOnLoad')
				]).then(function resolved(values) {
					if (values[1] === true) {
						this.handTool.activate();
					}
				}.bind(this)).catch(function rejected(reason) {
				});
				this.eventBus.on('presentationmodechanged', function (e) {
					if (e.switchInProgress) {
						return;
					}
					if (e.active) {
						this.enterPresentationMode();
					} else {
						this.exitPresentationMode();
					}
				}.bind(this));
			}
			HandTool.prototype = {
				get isActive() {
					return !!this.handTool.active;
				},
				toggle: function HandTool_toggle() {
					this.handTool.toggle();
				},
				enterPresentationMode: function HandTool_enterPresentationMode() {
					if (this.isActive) {
						this.wasActive = true;
						this.handTool.deactivate();
					}
				},
				exitPresentationMode: function HandTool_exitPresentationMode() {
					if (this.wasActive) {
						this.wasActive = false;
						this.handTool.activate();
					}
				}
			};
			return HandTool;
		}();
		exports.HandTool = HandTool;

		/***/
	}),
/* 14 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var overlayManager = __webpack_require__(4);
		var pdfjsLib = __webpack_require__(1);
		var mozL10n = uiUtils.mozL10n;
		var OverlayManager = overlayManager.OverlayManager;
		var PasswordPrompt = function PasswordPromptClosure() {
			function PasswordPrompt(options) {
				this.overlayName = options.overlayName;
				this.container = options.container;
				this.label = options.label;
				this.input = options.input;
				this.submitButton = options.submitButton;
				this.cancelButton = options.cancelButton;
				this.updateCallback = null;
				this.reason = null;
				this.submitButton.addEventListener('click', this.verify.bind(this));
				this.cancelButton.addEventListener('click', this.close.bind(this));
				this.input.addEventListener('keydown', function (e) {
					if (e.keyCode === 13) {
						this.verify();
					}
				}.bind(this));
				OverlayManager.register(this.overlayName, this.container, this.close.bind(this), true);
			}
			PasswordPrompt.prototype = {
				open: function PasswordPrompt_open() {
					OverlayManager.open(this.overlayName).then(function () {
						this.input.type = 'password';
						this.input.focus();
						var promptString = mozL10n.get('password_label', null, 'Enter the password to open this PDF file.');
						if (this.reason === pdfjsLib.PasswordResponses.INCORRECT_PASSWORD) {
							promptString = mozL10n.get('password_invalid', null, 'Invalid password. Please try again.');
						}
						this.label.textContent = promptString;
					}.bind(this));
				},
				close: function PasswordPrompt_close() {
					OverlayManager.close(this.overlayName).then(function () {
						this.input.value = '';
						this.input.type = '';
					}.bind(this));
				},
				verify: function PasswordPrompt_verify() {
					var password = this.input.value;
					if (password && password.length > 0) {
						this.close();
						return this.updateCallback(password);
					}
				},
				setUpdateCallback: function PasswordPrompt_setUpdateCallback(updateCallback, reason) {
					this.updateCallback = updateCallback;
					this.reason = reason;
				}
			};
			return PasswordPrompt;
		}();
		exports.PasswordPrompt = PasswordPrompt;

		/***/
	}),
/* 15 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var pdfjsLib = __webpack_require__(1);
		var PDFAttachmentViewer = function PDFAttachmentViewerClosure() {
			function PDFAttachmentViewer(options) {
				this.attachments = null;
				this.container = options.container;
				this.eventBus = options.eventBus;
				this.downloadManager = options.downloadManager;
				this._renderedCapability = pdfjsLib.createPromiseCapability();
				this.eventBus.on('fileattachmentannotation', this._appendAttachment.bind(this));
			}
			PDFAttachmentViewer.prototype = {
				reset: function PDFAttachmentViewer_reset(keepRenderedCapability) {
					this.attachments = null;
					this.container.textContent = '';
					if (!keepRenderedCapability) {
						this._renderedCapability = pdfjsLib.createPromiseCapability();
					}
				},
				_dispatchEvent: function PDFAttachmentViewer_dispatchEvent(attachmentsCount) {
					this.eventBus.dispatch('attachmentsloaded', {
						source: this,
						attachmentsCount: attachmentsCount
					});
					this._renderedCapability.resolve();
				},
				_bindPdfLink: function PDFAttachmentViewer_bindPdfLink(button, content, filename) {
					var blobUrl;
					button.onclick = function () {
						if (!blobUrl) {
							blobUrl = pdfjsLib.createObjectURL(content, 'application/pdf', pdfjsLib.PDFJS.disableCreateObjectURL);
						}
						var viewerUrl;
						viewerUrl = '?file=' + encodeURIComponent(blobUrl + '#' + filename);
						window.open(viewerUrl);
						return false;
					};
				},
				_bindLink: function PDFAttachmentViewer_bindLink(button, content, filename) {
					button.onclick = function downloadFile(e) {
						this.downloadManager.downloadData(content, filename, '');
						return false;
					}.bind(this);
				},
				render: function PDFAttachmentViewer_render(params) {
					params = params || {};
					var attachments = params.attachments || null;
					var attachmentsCount = 0;
					if (this.attachments) {
						var keepRenderedCapability = params.keepRenderedCapability === true;
						this.reset(keepRenderedCapability);
					}
					this.attachments = attachments;
					if (!attachments) {
						this._dispatchEvent(attachmentsCount);
						return;
					}
					var names = Object.keys(attachments).sort(function (a, b) {
						return a.toLowerCase().localeCompare(b.toLowerCase());
					});
					attachmentsCount = names.length;
					for (var i = 0; i < attachmentsCount; i++) {
						var item = attachments[names[i]];
						var filename = pdfjsLib.getFilenameFromUrl(item.filename);
						filename = pdfjsLib.removeNullCharacters(filename);
						var div = document.createElement('div');
						div.className = 'attachmentsItem';
						var button = document.createElement('button');
						button.textContent = filename;
						if (/\.pdf$/i.test(filename)) {
							this._bindPdfLink(button, item.content, filename);
						} else {
							this._bindLink(button, item.content, filename);
						}
						div.appendChild(button);
						this.container.appendChild(div);
					}
					this._dispatchEvent(attachmentsCount);
				},
				_appendAttachment: function PDFAttachmentViewer_appendAttachment(item) {
					this._renderedCapability.promise.then(function (id, filename, content) {
						var attachments = this.attachments;
						if (!attachments) {
							attachments = Object.create(null);
						} else {
							for (var name in attachments) {
								if (id === name) {
									return;
								}
							}
						}
						attachments[id] = {
							filename: filename,
							content: content
						};
						this.render({
							attachments: attachments,
							keepRenderedCapability: true
						});
					}.bind(this, item.id, item.filename, item.content));
				}
			};
			return PDFAttachmentViewer;
		}();
		exports.PDFAttachmentViewer = PDFAttachmentViewer;

		/***/
	}),
/* 16 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var overlayManager = __webpack_require__(4);
		var getPDFFileNameFromURL = uiUtils.getPDFFileNameFromURL;
		var mozL10n = uiUtils.mozL10n;
		var OverlayManager = overlayManager.OverlayManager;
		var PDFDocumentProperties = function PDFDocumentPropertiesClosure() {
			function PDFDocumentProperties(options) {
				this.fields = options.fields;
				this.overlayName = options.overlayName;
				this.container = options.container;
				this.rawFileSize = 0;
				this.url = null;
				this.pdfDocument = null;
				if (options.closeButton) {
					options.closeButton.addEventListener('click', this.close.bind(this));
				}
				this.dataAvailablePromise = new Promise(function (resolve) {
					this.resolveDataAvailable = resolve;
				}.bind(this));
				OverlayManager.register(this.overlayName, this.container, this.close.bind(this));
			}
			PDFDocumentProperties.prototype = {
				open: function PDFDocumentProperties_open() {
					Promise.all([
						OverlayManager.open(this.overlayName),
						this.dataAvailablePromise
					]).then(function () {
						this._getProperties();
					}.bind(this));
				},
				close: function PDFDocumentProperties_close() {
					OverlayManager.close(this.overlayName);
				},
				setFileSize: function PDFDocumentProperties_setFileSize(fileSize) {
					if (fileSize > 0) {
						this.rawFileSize = fileSize;
					}
				},
				setDocumentAndUrl: function PDFDocumentProperties_setDocumentAndUrl(pdfDocument, url) {
					this.pdfDocument = pdfDocument;
					this.url = url;
					this.resolveDataAvailable();
				},
				_getProperties: function PDFDocumentProperties_getProperties() {
					if (!OverlayManager.active) {
						return;
					}
					this.pdfDocument.getDownloadInfo().then(function (data) {
						if (data.length === this.rawFileSize) {
							return;
						}
						this.setFileSize(data.length);
						this._updateUI(this.fields['fileSize'], this._parseFileSize());
					}.bind(this));
					this.pdfDocument.getMetadata().then(function (data) {
						var content = {
							'fileName': getPDFFileNameFromURL(this.url),
							'fileSize': this._parseFileSize(),
							'title': data.info.Title,
							'author': data.info.Author,
							'subject': data.info.Subject,
							'keywords': data.info.Keywords,
							'creationDate': this._parseDate(data.info.CreationDate),
							'modificationDate': this._parseDate(data.info.ModDate),
							'creator': data.info.Creator,
							'producer': data.info.Producer,
							'version': data.info.PDFFormatVersion,
							'pageCount': this.pdfDocument.numPages
						};
						for (var identifier in content) {
							this._updateUI(this.fields[identifier], content[identifier]);
						}
					}.bind(this));
				},
				_updateUI: function PDFDocumentProperties_updateUI(field, content) {
					if (field && content !== undefined && content !== '') {
						field.textContent = content;
					}
				},
				_parseFileSize: function PDFDocumentProperties_parseFileSize() {
					var fileSize = this.rawFileSize, kb = fileSize / 1024;
					if (!kb) {
						return;
					} else if (kb < 1024) {
						return mozL10n.get('document_properties_kb', {
							size_kb: (+kb.toPrecision(3)).toLocaleString(),
							size_b: fileSize.toLocaleString()
						}, '{{size_kb}} KB ({{size_b}} bytes)');
					}
					return mozL10n.get('document_properties_mb', {
						size_mb: (+(kb / 1024).toPrecision(3)).toLocaleString(),
						size_b: fileSize.toLocaleString()
					}, '{{size_mb}} MB ({{size_b}} bytes)');
				},
				_parseDate: function PDFDocumentProperties_parseDate(inputDate) {
					var dateToParse = inputDate;
					if (dateToParse === undefined) {
						return '';
					}
					if (dateToParse.substring(0, 2) === 'D:') {
						dateToParse = dateToParse.substring(2);
					}
					var year = parseInt(dateToParse.substring(0, 4), 10);
					var month = parseInt(dateToParse.substring(4, 6), 10) - 1;
					var day = parseInt(dateToParse.substring(6, 8), 10);
					var hours = parseInt(dateToParse.substring(8, 10), 10);
					var minutes = parseInt(dateToParse.substring(10, 12), 10);
					var seconds = parseInt(dateToParse.substring(12, 14), 10);
					var utRel = dateToParse.substring(14, 15);
					var offsetHours = parseInt(dateToParse.substring(15, 17), 10);
					var offsetMinutes = parseInt(dateToParse.substring(18, 20), 10);
					if (utRel === '-') {
						hours += offsetHours;
						minutes += offsetMinutes;
					} else if (utRel === '+') {
						hours -= offsetHours;
						minutes -= offsetMinutes;
					}
					var date = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
					var dateString = date.toLocaleDateString();
					var timeString = date.toLocaleTimeString();
					return mozL10n.get('document_properties_date_string', {
						date: dateString,
						time: timeString
					}, '{{date}}, {{time}}');
				}
			};
			return PDFDocumentProperties;
		}();
		exports.PDFDocumentProperties = PDFDocumentProperties;

		/***/
	}),
/* 17 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfFindController = __webpack_require__(7);
		var mozL10n = uiUtils.mozL10n;
		var FindStates = pdfFindController.FindStates;
		var PDFFindBar = function PDFFindBarClosure() {
			function PDFFindBar(options) {
				this.opened = false;
				this.bar = options.bar || null;
				this.toggleButton = options.toggleButton || null;
				this.findField = options.findField || null;
				this.highlightAll = options.highlightAllCheckbox || null;
				this.caseSensitive = options.caseSensitiveCheckbox || null;
				this.findMsg = options.findMsg || null;
				this.findResultsCount = options.findResultsCount || null;
				this.findStatusIcon = options.findStatusIcon || null;
				this.findPreviousButton = options.findPreviousButton || null;
				this.findNextButton = options.findNextButton || null;
				this.findController = options.findController || null;
				this.eventBus = options.eventBus;
				if (this.findController === null) {
					throw new Error('PDFFindBar cannot be used without a ' + 'PDFFindController instance.');
				}
				var self = this;
				this.toggleButton.addEventListener('click', function () {
					self.toggle();
				});
				this.findField.addEventListener('input', function () {
					self.dispatchEvent('');
				});
				this.bar.addEventListener('keydown', function (evt) {
					switch (evt.keyCode) {
						case 13:
							if (evt.target === self.findField) {
								self.dispatchEvent('again', evt.shiftKey);
							}
							break;
						case 27:
							self.close();
							break;
					}
				});
				this.findPreviousButton.addEventListener('click', function () {
					self.dispatchEvent('again', true);
				});
				this.findNextButton.addEventListener('click', function () {
					self.dispatchEvent('again', false);
				});
				this.highlightAll.addEventListener('click', function () {
					self.dispatchEvent('highlightallchange');
				});
				this.caseSensitive.addEventListener('click', function () {
					self.dispatchEvent('casesensitivitychange');
				});
			}
			PDFFindBar.prototype = {
				reset: function PDFFindBar_reset() {
					this.updateUIState();
				},
				dispatchEvent: function PDFFindBar_dispatchEvent(type, findPrev) {
					this.eventBus.dispatch('find', {
						source: this,
						type: type,
						query: this.findField.value,
						caseSensitive: this.caseSensitive.checked,
						phraseSearch: true,
						highlightAll: this.highlightAll.checked,
						findPrevious: findPrev
					});
				},
				updateUIState: function PDFFindBar_updateUIState(state, previous, matchCount) {
					var notFound = false;
					var findMsg = '';
					var status = '';
					switch (state) {
						case FindStates.FIND_FOUND:
							break;
						case FindStates.FIND_PENDING:
							status = 'pending';
							break;
						case FindStates.FIND_NOTFOUND:
							findMsg = mozL10n.get('find_not_found', null, 'Phrase not found');
							notFound = true;
							break;
						case FindStates.FIND_WRAPPED:
							if (previous) {
								findMsg = mozL10n.get('find_reached_top', null, 'Reached top of document, continued from bottom');
							} else {
								findMsg = mozL10n.get('find_reached_bottom', null, 'Reached end of document, continued from top');
							}
							break;
					}
					if (notFound) {
						this.findField.classList.add('notFound');
					} else {
						this.findField.classList.remove('notFound');
					}
					this.findField.setAttribute('data-status', status);
					this.findMsg.textContent = findMsg;
					this.updateResultsCount(matchCount);
				},
				updateResultsCount: function (matchCount) {
					if (!this.findResultsCount) {
						return;
					}
					if (!matchCount) {
						this.findResultsCount.classList.add('hidden');
						return;
					}
					this.findResultsCount.textContent = matchCount.toLocaleString();
					this.findResultsCount.classList.remove('hidden');
				},
				open: function PDFFindBar_open() {
					if (!this.opened) {
						this.opened = true;
						this.toggleButton.classList.add('toggled');
						this.bar.classList.remove('hidden');
					}
					this.findField.select();
					this.findField.focus();
				},
				close: function PDFFindBar_close() {
					if (!this.opened) {
						return;
					}
					this.opened = false;
					this.toggleButton.classList.remove('toggled');
					this.bar.classList.add('hidden');
					this.findController.active = false;
				},
				toggle: function PDFFindBar_toggle() {
					if (this.opened) {
						this.close();
					} else {
						this.open();
					}
				}
			};
			return PDFFindBar;
		}();
		exports.PDFFindBar = PDFFindBar;

		/***/
	}),
/* 18 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var domEvents = __webpack_require__(2);
		function PDFHistory(options) {
			this.linkService = options.linkService;
			this.eventBus = options.eventBus || domEvents.getGlobalEventBus();
			this.initialized = false;
			this.initialDestination = null;
			this.initialBookmark = null;
		}
		PDFHistory.prototype = {
			initialize: function pdfHistoryInitialize(fingerprint) {
				this.initialized = true;
				this.reInitialized = false;
				this.allowHashChange = true;
				this.historyUnlocked = true;
				this.isViewerInPresentationMode = false;
				this.previousHash = window.location.hash.substring(1);
				this.currentBookmark = '';
				this.currentPage = 0;
				this.updatePreviousBookmark = false;
				this.previousBookmark = '';
				this.previousPage = 0;
				this.nextHashParam = '';
				this.fingerprint = fingerprint;
				this.currentUid = this.uid = 0;
				this.current = {};
				var state = window.history.state;
				if (this._isStateObjectDefined(state)) {
					if (state.target.dest) {
						this.initialDestination = state.target.dest;
					} else {
						this.initialBookmark = state.target.hash;
					}
					this.currentUid = state.uid;
					this.uid = state.uid + 1;
					this.current = state.target;
				} else {
					if (state && state.fingerprint && this.fingerprint !== state.fingerprint) {
						this.reInitialized = true;
					}
					this._pushOrReplaceState({ fingerprint: this.fingerprint }, true);
				}
				var self = this;
				window.addEventListener('popstate', function pdfHistoryPopstate(evt) {
					if (!self.historyUnlocked) {
						return;
					}
					if (evt.state) {
						self._goTo(evt.state);
						return;
					}
					if (self.uid === 0) {
						var previousParams = self.previousHash && self.currentBookmark && self.previousHash !== self.currentBookmark ? {
							hash: self.currentBookmark,
							page: self.currentPage
						} : { page: 1 };
						replacePreviousHistoryState(previousParams, function () {
							updateHistoryWithCurrentHash();
						});
					} else {
						updateHistoryWithCurrentHash();
					}
				});
				function updateHistoryWithCurrentHash() {
					self.previousHash = window.location.hash.slice(1);
					self._pushToHistory({ hash: self.previousHash }, false, true);
					self._updatePreviousBookmark();
				}
				function replacePreviousHistoryState(params, callback) {
					self.historyUnlocked = false;
					self.allowHashChange = false;
					window.addEventListener('popstate', rewriteHistoryAfterBack);
					history.back();
					function rewriteHistoryAfterBack() {
						window.removeEventListener('popstate', rewriteHistoryAfterBack);
						window.addEventListener('popstate', rewriteHistoryAfterForward);
						self._pushToHistory(params, false, true);
						history.forward();
					}
					function rewriteHistoryAfterForward() {
						window.removeEventListener('popstate', rewriteHistoryAfterForward);
						self.allowHashChange = true;
						self.historyUnlocked = true;
						callback();
					}
				}
				function pdfHistoryBeforeUnload() {
					var previousParams = self._getPreviousParams(null, true);
					if (previousParams) {
						var replacePrevious = !self.current.dest && self.current.hash !== self.previousHash;
						self._pushToHistory(previousParams, false, replacePrevious);
						self._updatePreviousBookmark();
					}
					window.removeEventListener('beforeunload', pdfHistoryBeforeUnload);
				}
				window.addEventListener('beforeunload', pdfHistoryBeforeUnload);
				window.addEventListener('pageshow', function pdfHistoryPageShow(evt) {
					window.addEventListener('beforeunload', pdfHistoryBeforeUnload);
				});
				self.eventBus.on('presentationmodechanged', function (e) {
					self.isViewerInPresentationMode = e.active;
				});
			},
			clearHistoryState: function pdfHistory_clearHistoryState() {
				this._pushOrReplaceState(null, true);
			},
			_isStateObjectDefined: function pdfHistory_isStateObjectDefined(state) {
				return state && state.uid >= 0 && state.fingerprint && this.fingerprint === state.fingerprint && state.target && state.target.hash ? true : false;
			},
			_pushOrReplaceState: function pdfHistory_pushOrReplaceState(stateObj, replace) {
				if (replace) {
					window.history.replaceState(stateObj, '', document.URL);
				} else {
					window.history.pushState(stateObj, '', document.URL);
				}
			},
			get isHashChangeUnlocked() {
				if (!this.initialized) {
					return true;
				}
				return this.allowHashChange;
			},
			_updatePreviousBookmark: function pdfHistory_updatePreviousBookmark() {
				if (this.updatePreviousBookmark && this.currentBookmark && this.currentPage) {
					this.previousBookmark = this.currentBookmark;
					this.previousPage = this.currentPage;
					this.updatePreviousBookmark = false;
				}
			},
			updateCurrentBookmark: function pdfHistoryUpdateCurrentBookmark(bookmark, pageNum) {
				if (this.initialized) {
					this.currentBookmark = bookmark.substring(1);
					this.currentPage = pageNum | 0;
					this._updatePreviousBookmark();
				}
			},
			updateNextHashParam: function pdfHistoryUpdateNextHashParam(param) {
				if (this.initialized) {
					this.nextHashParam = param;
				}
			},
			push: function pdfHistoryPush(params, isInitialBookmark) {
				if (!(this.initialized && this.historyUnlocked)) {
					return;
				}
				if (params.dest && !params.hash) {
					params.hash = this.current.hash && this.current.dest && this.current.dest === params.dest ? this.current.hash : this.linkService.getDestinationHash(params.dest).split('#')[1];
				}
				if (params.page) {
					params.page |= 0;
				}
				if (isInitialBookmark) {
					var target = window.history.state.target;
					if (!target) {
						this._pushToHistory(params, false);
						this.previousHash = window.location.hash.substring(1);
					}
					this.updatePreviousBookmark = this.nextHashParam ? false : true;
					if (target) {
						this._updatePreviousBookmark();
					}
					return;
				}
				if (this.nextHashParam) {
					if (this.nextHashParam === params.hash) {
						this.nextHashParam = null;
						this.updatePreviousBookmark = true;
						return;
					}
					this.nextHashParam = null;
				}
				if (params.hash) {
					if (this.current.hash) {
						if (this.current.hash !== params.hash) {
							this._pushToHistory(params, true);
						} else {
							if (!this.current.page && params.page) {
								this._pushToHistory(params, false, true);
							}
							this.updatePreviousBookmark = true;
						}
					} else {
						this._pushToHistory(params, true);
					}
				} else if (this.current.page && params.page && this.current.page !== params.page) {
					this._pushToHistory(params, true);
				}
			},
			_getPreviousParams: function pdfHistory_getPreviousParams(onlyCheckPage, beforeUnload) {
				if (!(this.currentBookmark && this.currentPage)) {
					return null;
				} else if (this.updatePreviousBookmark) {
					this.updatePreviousBookmark = false;
				}
				if (this.uid > 0 && !(this.previousBookmark && this.previousPage)) {
					return null;
				}
				if (!this.current.dest && !onlyCheckPage || beforeUnload) {
					if (this.previousBookmark === this.currentBookmark) {
						return null;
					}
				} else if (this.current.page || onlyCheckPage) {
					if (this.previousPage === this.currentPage) {
						return null;
					}
				} else {
					return null;
				}
				var params = {
					hash: this.currentBookmark,
					page: this.currentPage
				};
				if (this.isViewerInPresentationMode) {
					params.hash = null;
				}
				return params;
			},
			_stateObj: function pdfHistory_stateObj(params) {
				return {
					fingerprint: this.fingerprint,
					uid: this.uid,
					target: params
				};
			},
			_pushToHistory: function pdfHistory_pushToHistory(params, addPrevious, overwrite) {
				if (!this.initialized) {
					return;
				}
				if (!params.hash && params.page) {
					params.hash = 'page=' + params.page;
				}
				if (addPrevious && !overwrite) {
					var previousParams = this._getPreviousParams();
					if (previousParams) {
						var replacePrevious = !this.current.dest && this.current.hash !== this.previousHash;
						this._pushToHistory(previousParams, false, replacePrevious);
					}
				}
				this._pushOrReplaceState(this._stateObj(params), overwrite || this.uid === 0);
				this.currentUid = this.uid++;
				this.current = params;
				this.updatePreviousBookmark = true;
			},
			_goTo: function pdfHistory_goTo(state) {
				if (!(this.initialized && this.historyUnlocked && this._isStateObjectDefined(state))) {
					return;
				}
				if (!this.reInitialized && state.uid < this.currentUid) {
					var previousParams = this._getPreviousParams(true);
					if (previousParams) {
						this._pushToHistory(this.current, false);
						this._pushToHistory(previousParams, false);
						this.currentUid = state.uid;
						window.history.back();
						return;
					}
				}
				this.historyUnlocked = false;
				if (state.target.dest) {
					this.linkService.navigateTo(state.target.dest);
				} else {
					this.linkService.setHash(state.target.hash);
				}
				this.currentUid = state.uid;
				if (state.uid > this.uid) {
					this.uid = state.uid;
				}
				this.current = state.target;
				this.updatePreviousBookmark = true;
				var currentHash = window.location.hash.substring(1);
				if (this.previousHash !== currentHash) {
					this.allowHashChange = false;
				}
				this.previousHash = currentHash;
				this.historyUnlocked = true;
			},
			back: function pdfHistoryBack() {
				this.go(-1);
			},
			forward: function pdfHistoryForward() {
				this.go(1);
			},
			go: function pdfHistoryGo(direction) {
				if (this.initialized && this.historyUnlocked) {
					var state = window.history.state;
					if (direction === -1 && state && state.uid > 0) {
						window.history.back();
					} else if (direction === 1 && state && state.uid < this.uid - 1) {
						window.history.forward();
					}
				}
			}
		};
		exports.PDFHistory = PDFHistory;

		/***/
	}),
/* 19 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var pdfjsLib = __webpack_require__(1);
		var PDFJS = pdfjsLib.PDFJS;
		var DEFAULT_TITLE = '\u2013';
		var PDFOutlineViewer = function PDFOutlineViewerClosure() {
			function PDFOutlineViewer(options) {
				this.outline = null;
				this.lastToggleIsShow = true;
				this.container = options.container;
				this.linkService = options.linkService;
				this.eventBus = options.eventBus;
			}
			PDFOutlineViewer.prototype = {
				reset: function PDFOutlineViewer_reset() {
					this.outline = null;
					this.lastToggleIsShow = true;
					this.container.textContent = '';
					this.container.classList.remove('outlineWithDeepNesting');
				},
				_dispatchEvent: function PDFOutlineViewer_dispatchEvent(outlineCount) {
					this.eventBus.dispatch('outlineloaded', {
						source: this,
						outlineCount: outlineCount
					});
				},
				_bindLink: function PDFOutlineViewer_bindLink(element, item) {
					if (item.url) {
						pdfjsLib.addLinkAttributes(element, {
							url: item.url,
							target: item.newWindow ? PDFJS.LinkTarget.BLANK : undefined
						});
						return;
					}
					var self = this, destination = item.dest;
					element.href = self.linkService.getDestinationHash(destination);
					element.onclick = function () {
						if (destination) {
							self.linkService.navigateTo(destination);
						}
						return false;
					};
				},
				_setStyles: function PDFOutlineViewer_setStyles(element, item) {
					var styleStr = '';
					if (item.bold) {
						styleStr += 'font-weight: bold;';
					}
					if (item.italic) {
						styleStr += 'font-style: italic;';
					}
					if (styleStr) {
						element.setAttribute('style', styleStr);
					}
				},
				_addToggleButton: function PDFOutlineViewer_addToggleButton(div) {
					var toggler = document.createElement('div');
					toggler.className = 'outlineItemToggler';
					toggler.onclick = function (event) {
						event.stopPropagation();
						toggler.classList.toggle('outlineItemsHidden');
						if (event.shiftKey) {
							var shouldShowAll = !toggler.classList.contains('outlineItemsHidden');
							this._toggleOutlineItem(div, shouldShowAll);
						}
					}.bind(this);
					div.insertBefore(toggler, div.firstChild);
				},
				_toggleOutlineItem: function PDFOutlineViewer_toggleOutlineItem(root, show) {
					this.lastToggleIsShow = show;
					var togglers = root.querySelectorAll('.outlineItemToggler');
					for (var i = 0, ii = togglers.length; i < ii; ++i) {
						togglers[i].classList[show ? 'remove' : 'add']('outlineItemsHidden');
					}
				},
				toggleOutlineTree: function PDFOutlineViewer_toggleOutlineTree() {
					if (!this.outline) {
						return;
					}
					this._toggleOutlineItem(this.container, !this.lastToggleIsShow);
				},
				render: function PDFOutlineViewer_render(params) {
					var outline = params && params.outline || null;
					var outlineCount = 0;
					if (this.outline) {
						this.reset();
					}
					this.outline = outline;
					if (!outline) {
						this._dispatchEvent(outlineCount);
						return;
					}
					var fragment = document.createDocumentFragment();
					var queue = [{
						parent: fragment,
						items: this.outline
					}];
					var hasAnyNesting = false;
					while (queue.length > 0) {
						var levelData = queue.shift();
						for (var i = 0, len = levelData.items.length; i < len; i++) {
							var item = levelData.items[i];
							var div = document.createElement('div');
							div.className = 'outlineItem';
							var element = document.createElement('a');
							this._bindLink(element, item);
							this._setStyles(element, item);
							element.textContent = pdfjsLib.removeNullCharacters(item.title) || DEFAULT_TITLE;
							div.appendChild(element);
							if (item.items.length > 0) {
								hasAnyNesting = true;
								this._addToggleButton(div);
								var itemsDiv = document.createElement('div');
								itemsDiv.className = 'outlineItems';
								div.appendChild(itemsDiv);
								queue.push({
									parent: itemsDiv,
									items: item.items
								});
							}
							levelData.parent.appendChild(div);
							outlineCount++;
						}
					}
					if (hasAnyNesting) {
						this.container.classList.add('outlineWithDeepNesting');
					}
					this.container.appendChild(fragment);
					this._dispatchEvent(outlineCount);
				}
			};
			return PDFOutlineViewer;
		}();
		exports.PDFOutlineViewer = PDFOutlineViewer;

		/***/
	}),
/* 20 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfRenderingQueue = __webpack_require__(3);
		var domEvents = __webpack_require__(2);
		var pdfjsLib = __webpack_require__(1);
		var CSS_UNITS = uiUtils.CSS_UNITS;
		var DEFAULT_SCALE = uiUtils.DEFAULT_SCALE;
		var getOutputScale = uiUtils.getOutputScale;
		var approximateFraction = uiUtils.approximateFraction;
		var roundToDivide = uiUtils.roundToDivide;
		var RendererType = uiUtils.RendererType;
		var RenderingStates = pdfRenderingQueue.RenderingStates;
		var TEXT_LAYER_RENDER_DELAY = 200;
		var PDFPageView = function PDFPageViewClosure() {
			function PDFPageView(options) {
				var container = options.container;
				var id = options.id;
				var scale = options.scale;
				var defaultViewport = options.defaultViewport;
				var renderingQueue = options.renderingQueue;
				var textLayerFactory = options.textLayerFactory;
				var annotationLayerFactory = options.annotationLayerFactory;
				var enhanceTextSelection = options.enhanceTextSelection || false;
				var renderInteractiveForms = options.renderInteractiveForms || false;
				this.id = id;
				this.renderingId = 'page' + id;
				this.pageLabel = null;
				this.rotation = 0;
				this.scale = scale || DEFAULT_SCALE;
				this.viewport = defaultViewport;
				this.pdfPageRotate = defaultViewport.rotation;
				this.hasRestrictedScaling = false;
				this.enhanceTextSelection = enhanceTextSelection;
				this.renderInteractiveForms = renderInteractiveForms;
				this.eventBus = options.eventBus || domEvents.getGlobalEventBus();
				this.renderingQueue = renderingQueue;
				this.textLayerFactory = textLayerFactory;
				this.annotationLayerFactory = annotationLayerFactory;
				this.renderer = options.renderer || RendererType.CANVAS;
				this.paintTask = null;
				this.paintedViewportMap = new WeakMap();
				this.renderingState = RenderingStates.INITIAL;
				this.resume = null;
				this.error = null;
				this.onBeforeDraw = null;
				this.onAfterDraw = null;
				this.textLayer = null;
				this.zoomLayer = null;
				this.annotationLayer = null;
				var div = document.createElement('div');
				div.className = 'page';
				div.style.width = Math.floor(this.viewport.width) + 'px';
				div.style.height = Math.floor(this.viewport.height) + 'px';
				div.setAttribute('data-page-number', this.id);
				this.div = div;
				container.appendChild(div);
			}
			PDFPageView.prototype = {
				setPdfPage: function PDFPageView_setPdfPage(pdfPage) {
					this.pdfPage = pdfPage;
					this.pdfPageRotate = pdfPage.rotate;
					var totalRotation = (this.rotation + this.pdfPageRotate) % 360;
					this.viewport = pdfPage.getViewport(this.scale * CSS_UNITS, totalRotation);
					this.stats = pdfPage.stats;
					this.reset();
				},
				destroy: function PDFPageView_destroy() {
					this.zoomLayer = null;
					this.reset();
					if (this.pdfPage) {
						this.pdfPage.cleanup();
					}
				},
				reset: function PDFPageView_reset(keepZoomLayer, keepAnnotations) {
					this.cancelRendering();
					var div = this.div;
					div.style.width = Math.floor(this.viewport.width) + 'px';
					div.style.height = Math.floor(this.viewport.height) + 'px';
					var childNodes = div.childNodes;
					var currentZoomLayerNode = keepZoomLayer && this.zoomLayer || null;
					var currentAnnotationNode = keepAnnotations && this.annotationLayer && this.annotationLayer.div || null;
					for (var i = childNodes.length - 1; i >= 0; i--) {
						var node = childNodes[i];
						if (currentZoomLayerNode === node || currentAnnotationNode === node) {
							continue;
						}
						div.removeChild(node);
					}
					div.removeAttribute('data-loaded');
					if (currentAnnotationNode) {
						this.annotationLayer.hide();
					} else {
						this.annotationLayer = null;
					}
					if (this.canvas && !currentZoomLayerNode) {
						this.paintedViewportMap.delete(this.canvas);
						this.canvas.width = 0;
						this.canvas.height = 0;
						delete this.canvas;
					}
					if (this.svg) {
						this.paintedViewportMap.delete(this.svg);
						delete this.svg;
					}
					this.loadingIconDiv = document.createElement('div');
					this.loadingIconDiv.className = 'loadingIcon';
					div.appendChild(this.loadingIconDiv);
				},
				update: function PDFPageView_update(scale, rotation) {
					this.scale = scale || this.scale;
					if (typeof rotation !== 'undefined') {
						this.rotation = rotation;
					}
					var totalRotation = (this.rotation + this.pdfPageRotate) % 360;
					this.viewport = this.viewport.clone({
						scale: this.scale * CSS_UNITS,
						rotation: totalRotation
					});
					if (this.svg) {
						this.cssTransform(this.svg, true);
						this.eventBus.dispatch('pagerendered', {
							source: this,
							pageNumber: this.id,
							cssTransform: true
						});
						return;
					}
					var isScalingRestricted = false;
					if (this.canvas && pdfjsLib.PDFJS.maxCanvasPixels > 0) {
						var outputScale = this.outputScale;
						if ((Math.floor(this.viewport.width) * outputScale.sx | 0) * (Math.floor(this.viewport.height) * outputScale.sy | 0) > pdfjsLib.PDFJS.maxCanvasPixels) {
							isScalingRestricted = true;
						}
					}
					if (this.canvas) {
						if (pdfjsLib.PDFJS.useOnlyCssZoom || this.hasRestrictedScaling && isScalingRestricted) {
							this.cssTransform(this.canvas, true);
							this.eventBus.dispatch('pagerendered', {
								source: this,
								pageNumber: this.id,
								cssTransform: true
							});
							return;
						}
						if (!this.zoomLayer) {
							this.zoomLayer = this.canvas.parentNode;
							this.zoomLayer.style.position = 'absolute';
						}
					}
					if (this.zoomLayer) {
						this.cssTransform(this.zoomLayer.firstChild);
					}
					this.reset(true, true);
				},
				cancelRendering: function PDFPageView_cancelRendering() {
					if (this.paintTask) {
						this.paintTask.cancel();
						this.paintTask = null;
					}
					this.renderingState = RenderingStates.INITIAL;
					this.resume = null;
					if (this.textLayer) {
						this.textLayer.cancel();
						this.textLayer = null;
					}
				},
				updatePosition: function PDFPageView_updatePosition() {
					if (this.textLayer) {
						this.textLayer.render(TEXT_LAYER_RENDER_DELAY);
					}
				},
				cssTransform: function PDFPageView_transform(target, redrawAnnotations) {
					var CustomStyle = pdfjsLib.CustomStyle;
					var width = this.viewport.width;
					var height = this.viewport.height;
					var div = this.div;
					target.style.width = target.parentNode.style.width = div.style.width = Math.floor(width) + 'px';
					target.style.height = target.parentNode.style.height = div.style.height = Math.floor(height) + 'px';
					var relativeRotation = this.viewport.rotation - this.paintedViewportMap.get(target).rotation;
					var absRotation = Math.abs(relativeRotation);
					var scaleX = 1, scaleY = 1;
					if (absRotation === 90 || absRotation === 270) {
						scaleX = height / width;
						scaleY = width / height;
					}
					var cssTransform = 'rotate(' + relativeRotation + 'deg) ' + 'scale(' + scaleX + ',' + scaleY + ')';
					CustomStyle.setProp('transform', target, cssTransform);
					if (this.textLayer) {
						var textLayerViewport = this.textLayer.viewport;
						var textRelativeRotation = this.viewport.rotation - textLayerViewport.rotation;
						var textAbsRotation = Math.abs(textRelativeRotation);
						var scale = width / textLayerViewport.width;
						if (textAbsRotation === 90 || textAbsRotation === 270) {
							scale = width / textLayerViewport.height;
						}
						var textLayerDiv = this.textLayer.textLayerDiv;
						var transX, transY;
						switch (textAbsRotation) {
							case 0:
								transX = transY = 0;
								break;
							case 90:
								transX = 0;
								transY = '-' + textLayerDiv.style.height;
								break;
							case 180:
								transX = '-' + textLayerDiv.style.width;
								transY = '-' + textLayerDiv.style.height;
								break;
							case 270:
								transX = '-' + textLayerDiv.style.width;
								transY = 0;
								break;
							default:
								console.error('Bad rotation value.');
								break;
						}
						CustomStyle.setProp('transform', textLayerDiv, 'rotate(' + textAbsRotation + 'deg) ' + 'scale(' + scale + ', ' + scale + ') ' + 'translate(' + transX + ', ' + transY + ')');
						CustomStyle.setProp('transformOrigin', textLayerDiv, '0% 0%');
					}
					if (redrawAnnotations && this.annotationLayer) {
						this.annotationLayer.render(this.viewport, 'display');
					}
				},
				get width() {
					return this.viewport.width;
				},
				get height() {
					return this.viewport.height;
				},
				getPagePoint: function PDFPageView_getPagePoint(x, y) {
					return this.viewport.convertToPdfPoint(x, y);
				},
				draw: function PDFPageView_draw() {
					if (this.renderingState !== RenderingStates.INITIAL) {
						console.error('Must be in new state before drawing');
						this.reset();
					}
					this.renderingState = RenderingStates.RUNNING;
					var self = this;
					var pdfPage = this.pdfPage;
					var div = this.div;
					var canvasWrapper = document.createElement('div');
					canvasWrapper.style.width = div.style.width;
					canvasWrapper.style.height = div.style.height;
					canvasWrapper.classList.add('canvasWrapper');
					if (this.annotationLayer && this.annotationLayer.div) {
						div.insertBefore(canvasWrapper, this.annotationLayer.div);
					} else {
						div.appendChild(canvasWrapper);
					}
					var textLayerDiv = null;
					var textLayer = null;
					if (this.textLayerFactory) {
						textLayerDiv = document.createElement('div');
						textLayerDiv.className = 'textLayer';
						textLayerDiv.style.width = canvasWrapper.style.width;
						textLayerDiv.style.height = canvasWrapper.style.height;
						if (this.annotationLayer && this.annotationLayer.div) {
							div.insertBefore(textLayerDiv, this.annotationLayer.div);
						} else {
							div.appendChild(textLayerDiv);
						}
						textLayer = this.textLayerFactory.createTextLayerBuilder(textLayerDiv, this.id - 1, this.viewport, this.enhanceTextSelection);
					}
					this.textLayer = textLayer;
					var renderContinueCallback = null;
					if (this.renderingQueue) {
						renderContinueCallback = function renderContinueCallback(cont) {
							if (!self.renderingQueue.isHighestPriority(self)) {
								self.renderingState = RenderingStates.PAUSED;
								self.resume = function resumeCallback() {
									self.renderingState = RenderingStates.RUNNING;
									cont();
								};
								return;
							}
							cont();
						};
					}
					var finishPaintTask = function finishPaintTask(error) {
						if (paintTask === self.paintTask) {
							self.paintTask = null;
						}
						if (error === 'cancelled') {
							self.error = null;
							return Promise.resolve(undefined);
						}
						self.renderingState = RenderingStates.FINISHED;
						if (self.loadingIconDiv) {
							div.removeChild(self.loadingIconDiv);
							delete self.loadingIconDiv;
						}
						if (self.zoomLayer) {
							var zoomLayerCanvas = self.zoomLayer.firstChild;
							self.paintedViewportMap.delete(zoomLayerCanvas);
							zoomLayerCanvas.width = 0;
							zoomLayerCanvas.height = 0;
							if (div.contains(self.zoomLayer)) {
								div.removeChild(self.zoomLayer);
							}
							self.zoomLayer = null;
						}
						self.error = error;
						self.stats = pdfPage.stats;
						if (self.onAfterDraw) {
							self.onAfterDraw();
						}
						self.eventBus.dispatch('pagerendered', {
							source: self,
							pageNumber: self.id,
							cssTransform: false
						});
						if (error) {
							return Promise.reject(error);
						}
						return Promise.resolve(undefined);
					};
					var paintTask = this.renderer === RendererType.SVG ? this.paintOnSvg(canvasWrapper) : this.paintOnCanvas(canvasWrapper);
					paintTask.onRenderContinue = renderContinueCallback;
					this.paintTask = paintTask;
					var resultPromise = paintTask.promise.then(function () {
						return finishPaintTask(null).then(function () {
							if (textLayer) {
								pdfPage.getTextContent({ normalizeWhitespace: true }).then(function textContentResolved(textContent) {
									textLayer.setTextContent(textContent);
									textLayer.render(TEXT_LAYER_RENDER_DELAY);
								});
							}
						});
					}, function (reason) {
						return finishPaintTask(reason);
					});
					if (this.annotationLayerFactory) {
						if (!this.annotationLayer) {
							this.annotationLayer = this.annotationLayerFactory.createAnnotationLayerBuilder(div, pdfPage, this.renderInteractiveForms);
						}
						this.annotationLayer.render(this.viewport, 'display');
					}
					div.setAttribute('data-loaded', true);
					if (this.onBeforeDraw) {
						this.onBeforeDraw();
					}
					return resultPromise;
				},
				paintOnCanvas: function (canvasWrapper) {
					var resolveRenderPromise, rejectRenderPromise;
					var promise = new Promise(function (resolve, reject) {
						resolveRenderPromise = resolve;
						rejectRenderPromise = reject;
					});
					var result = {
						promise: promise,
						onRenderContinue: function (cont) {
							cont();
						},
						cancel: function () {
							renderTask.cancel();
						}
					};
					var viewport = this.viewport;
					var canvas = document.createElement('canvas');
					canvas.id = 'page' + this.id;
					canvas.setAttribute('hidden', 'hidden');
					var isCanvasHidden = true;
					var showCanvas = function () {
						if (isCanvasHidden) {
							canvas.removeAttribute('hidden');
							isCanvasHidden = false;
						}
					};
					canvasWrapper.appendChild(canvas);
					this.canvas = canvas;
					canvas.mozOpaque = true;
					var ctx = canvas.getContext('2d', { alpha: false });
					var outputScale = getOutputScale(ctx);
					this.outputScale = outputScale;
					if (pdfjsLib.PDFJS.useOnlyCssZoom) {
						var actualSizeViewport = viewport.clone({ scale: CSS_UNITS });
						outputScale.sx *= actualSizeViewport.width / viewport.width;
						outputScale.sy *= actualSizeViewport.height / viewport.height;
						outputScale.scaled = true;
					}
					if (pdfjsLib.PDFJS.maxCanvasPixels > 0) {
						var pixelsInViewport = viewport.width * viewport.height;
						var maxScale = Math.sqrt(pdfjsLib.PDFJS.maxCanvasPixels / pixelsInViewport);
						if (outputScale.sx > maxScale || outputScale.sy > maxScale) {
							outputScale.sx = maxScale;
							outputScale.sy = maxScale;
							outputScale.scaled = true;
							this.hasRestrictedScaling = true;
						} else {
							this.hasRestrictedScaling = false;
						}
					}
					var sfx = approximateFraction(outputScale.sx);
					var sfy = approximateFraction(outputScale.sy);
					canvas.width = roundToDivide(viewport.width * outputScale.sx, sfx[0]);
					canvas.height = roundToDivide(viewport.height * outputScale.sy, sfy[0]);
					canvas.style.width = roundToDivide(viewport.width, sfx[1]) + 'px';
					canvas.style.height = roundToDivide(viewport.height, sfy[1]) + 'px';
					this.paintedViewportMap.set(canvas, viewport);
					var transform = !outputScale.scaled ? null : [
						outputScale.sx,
						0,
						0,
						outputScale.sy,
						0,
						0
					];
					var renderContext = {
						canvasContext: ctx,
						transform: transform,
						viewport: this.viewport,
						renderInteractiveForms: this.renderInteractiveForms
					};
					var renderTask = this.pdfPage.render(renderContext);
					renderTask.onContinue = function (cont) {
						showCanvas();
						if (result.onRenderContinue) {
							result.onRenderContinue(cont);
						} else {
							cont();
						}
					};
					renderTask.promise.then(function pdfPageRenderCallback() {
						showCanvas();
						resolveRenderPromise(undefined);
					}, function pdfPageRenderError(error) {
						showCanvas();
						rejectRenderPromise(error);
					});
					return result;
				},
				paintOnSvg: function PDFPageView_paintOnSvg(wrapper) {
					var cancelled = false;
					var ensureNotCancelled = function () {
						if (cancelled) {
							throw 'cancelled';
						}
					};
					var self = this;
					var pdfPage = this.pdfPage;
					var SVGGraphics = pdfjsLib.SVGGraphics;
					var actualSizeViewport = this.viewport.clone({ scale: CSS_UNITS });
					var promise = pdfPage.getOperatorList().then(function (opList) {
						ensureNotCancelled();
						var svgGfx = new SVGGraphics(pdfPage.commonObjs, pdfPage.objs);
						return svgGfx.getSVG(opList, actualSizeViewport).then(function (svg) {
							ensureNotCancelled();
							self.svg = svg;
							self.paintedViewportMap.set(svg, actualSizeViewport);
							svg.style.width = wrapper.style.width;
							svg.style.height = wrapper.style.height;
							self.renderingState = RenderingStates.FINISHED;
							wrapper.appendChild(svg);
						});
					});
					return {
						promise: promise,
						onRenderContinue: function (cont) {
							cont();
						},
						cancel: function () {
							cancelled = true;
						}
					};
				},
				setPageLabel: function PDFView_setPageLabel(label) {
					this.pageLabel = typeof label === 'string' ? label : null;
					if (this.pageLabel !== null) {
						this.div.setAttribute('data-page-label', this.pageLabel);
					} else {
						this.div.removeAttribute('data-page-label');
					}
				}
			};
			return PDFPageView;
		}();
		exports.PDFPageView = PDFPageView;

		/***/
	}),
/* 21 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var normalizeWheelEventDelta = uiUtils.normalizeWheelEventDelta;
		var DELAY_BEFORE_RESETTING_SWITCH_IN_PROGRESS = 1500;
		var DELAY_BEFORE_HIDING_CONTROLS = 3000;
		var ACTIVE_SELECTOR = 'pdfPresentationMode';
		var CONTROLS_SELECTOR = 'pdfPresentationModeControls';
		var PDFPresentationMode = function PDFPresentationModeClosure() {
			function PDFPresentationMode(options) {
				this.container = options.container;
				this.viewer = options.viewer || options.container.firstElementChild;
				this.pdfViewer = options.pdfViewer;
				this.eventBus = options.eventBus;
				var contextMenuItems = options.contextMenuItems || null;
				this.active = false;
				this.args = null;
				this.contextMenuOpen = false;
				this.mouseScrollTimeStamp = 0;
				this.mouseScrollDelta = 0;
				this.touchSwipeState = null;
				if (contextMenuItems) {
					contextMenuItems.contextFirstPage.addEventListener('click', function PDFPresentationMode_contextFirstPageClick(e) {
						this.contextMenuOpen = false;
						this.eventBus.dispatch('firstpage');
					}.bind(this));
					contextMenuItems.contextLastPage.addEventListener('click', function PDFPresentationMode_contextLastPageClick(e) {
						this.contextMenuOpen = false;
						this.eventBus.dispatch('lastpage');
					}.bind(this));
					contextMenuItems.contextPageRotateCw.addEventListener('click', function PDFPresentationMode_contextPageRotateCwClick(e) {
						this.contextMenuOpen = false;
						this.eventBus.dispatch('rotatecw');
					}.bind(this));
					contextMenuItems.contextPageRotateCcw.addEventListener('click', function PDFPresentationMode_contextPageRotateCcwClick(e) {
						this.contextMenuOpen = false;
						this.eventBus.dispatch('rotateccw');
					}.bind(this));
				}
			}
			PDFPresentationMode.prototype = {
				request: function PDFPresentationMode_request() {
					if (this.switchInProgress || this.active || !this.viewer.hasChildNodes()) {
						return false;
					}
					this._addFullscreenChangeListeners();
					this._setSwitchInProgress();
					this._notifyStateChange();
					if (this.container.requestFullscreen) {
						this.container.requestFullscreen();
					} else if (this.container.mozRequestFullScreen) {
						this.container.mozRequestFullScreen();
					} else if (this.container.webkitRequestFullscreen) {
						this.container.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
					} else if (this.container.msRequestFullscreen) {
						this.container.msRequestFullscreen();
					} else {
						return false;
					}
					this.args = {
						page: this.pdfViewer.currentPageNumber,
						previousScale: this.pdfViewer.currentScaleValue
					};
					return true;
				},
				_mouseWheel: function PDFPresentationMode_mouseWheel(evt) {
					if (!this.active) {
						return;
					}
					evt.preventDefault();
					var delta = normalizeWheelEventDelta(evt);
					var MOUSE_SCROLL_COOLDOWN_TIME = 50;
					var PAGE_SWITCH_THRESHOLD = 0.1;
					var currentTime = new Date().getTime();
					var storedTime = this.mouseScrollTimeStamp;
					if (currentTime > storedTime && currentTime - storedTime < MOUSE_SCROLL_COOLDOWN_TIME) {
						return;
					}
					if (this.mouseScrollDelta > 0 && delta < 0 || this.mouseScrollDelta < 0 && delta > 0) {
						this._resetMouseScrollState();
					}
					this.mouseScrollDelta += delta;
					if (Math.abs(this.mouseScrollDelta) >= PAGE_SWITCH_THRESHOLD) {
						var totalDelta = this.mouseScrollDelta;
						this._resetMouseScrollState();
						var success = totalDelta > 0 ? this._goToPreviousPage() : this._goToNextPage();
						if (success) {
							this.mouseScrollTimeStamp = currentTime;
						}
					}
				},
				get isFullscreen() {
					return !!(document.fullscreenElement || document.mozFullScreen || document.webkitIsFullScreen || document.msFullscreenElement);
				},
				_goToPreviousPage: function PDFPresentationMode_goToPreviousPage() {
					var page = this.pdfViewer.currentPageNumber;
					if (page <= 1) {
						return false;
					}
					this.pdfViewer.currentPageNumber = page - 1;
					return true;
				},
				_goToNextPage: function PDFPresentationMode_goToNextPage() {
					var page = this.pdfViewer.currentPageNumber;
					if (page >= this.pdfViewer.pagesCount) {
						return false;
					}
					this.pdfViewer.currentPageNumber = page + 1;
					return true;
				},
				_notifyStateChange: function PDFPresentationMode_notifyStateChange() {
					this.eventBus.dispatch('presentationmodechanged', {
						source: this,
						active: this.active,
						switchInProgress: !!this.switchInProgress
					});
				},
				_setSwitchInProgress: function PDFPresentationMode_setSwitchInProgress() {
					if (this.switchInProgress) {
						clearTimeout(this.switchInProgress);
					}
					this.switchInProgress = setTimeout(function switchInProgressTimeout() {
						this._removeFullscreenChangeListeners();
						delete this.switchInProgress;
						this._notifyStateChange();
					}.bind(this), DELAY_BEFORE_RESETTING_SWITCH_IN_PROGRESS);
				},
				_resetSwitchInProgress: function PDFPresentationMode_resetSwitchInProgress() {
					if (this.switchInProgress) {
						clearTimeout(this.switchInProgress);
						delete this.switchInProgress;
					}
				},
				_enter: function PDFPresentationMode_enter() {
					this.active = true;
					this._resetSwitchInProgress();
					this._notifyStateChange();
					this.container.classList.add(ACTIVE_SELECTOR);
					setTimeout(function enterPresentationModeTimeout() {
						this.pdfViewer.currentPageNumber = this.args.page;
						this.pdfViewer.currentScaleValue = 'page-fit';
					}.bind(this), 0);
					this._addWindowListeners();
					this._showControls();
					this.contextMenuOpen = false;
					this.container.setAttribute('contextmenu', 'viewerContextMenu');
					window.getSelection().removeAllRanges();
				},
				_exit: function PDFPresentationMode_exit() {
					var page = this.pdfViewer.currentPageNumber;
					this.container.classList.remove(ACTIVE_SELECTOR);
					setTimeout(function exitPresentationModeTimeout() {
						this.active = false;
						this._removeFullscreenChangeListeners();
						this._notifyStateChange();
						this.pdfViewer.currentScaleValue = this.args.previousScale;
						this.pdfViewer.currentPageNumber = page;
						this.args = null;
					}.bind(this), 0);
					this._removeWindowListeners();
					this._hideControls();
					this._resetMouseScrollState();
					this.container.removeAttribute('contextmenu');
					this.contextMenuOpen = false;
				},
				_mouseDown: function PDFPresentationMode_mouseDown(evt) {
					if (this.contextMenuOpen) {
						this.contextMenuOpen = false;
						evt.preventDefault();
						return;
					}
					if (evt.button === 0) {
						var isInternalLink = evt.target.href && evt.target.classList.contains('internalLink');
						if (!isInternalLink) {
							evt.preventDefault();
							this.pdfViewer.currentPageNumber += evt.shiftKey ? -1 : 1;
						}
					}
				},
				_contextMenu: function PDFPresentationMode_contextMenu() {
					this.contextMenuOpen = true;
				},
				_showControls: function PDFPresentationMode_showControls() {
					if (this.controlsTimeout) {
						clearTimeout(this.controlsTimeout);
					} else {
						this.container.classList.add(CONTROLS_SELECTOR);
					}
					this.controlsTimeout = setTimeout(function showControlsTimeout() {
						this.container.classList.remove(CONTROLS_SELECTOR);
						delete this.controlsTimeout;
					}.bind(this), DELAY_BEFORE_HIDING_CONTROLS);
				},
				_hideControls: function PDFPresentationMode_hideControls() {
					if (!this.controlsTimeout) {
						return;
					}
					clearTimeout(this.controlsTimeout);
					this.container.classList.remove(CONTROLS_SELECTOR);
					delete this.controlsTimeout;
				},
				_resetMouseScrollState: function PDFPresentationMode_resetMouseScrollState() {
					this.mouseScrollTimeStamp = 0;
					this.mouseScrollDelta = 0;
				},
				_touchSwipe: function PDFPresentationMode_touchSwipe(evt) {
					if (!this.active) {
						return;
					}
					var SWIPE_MIN_DISTANCE_THRESHOLD = 50;
					var SWIPE_ANGLE_THRESHOLD = Math.PI / 6;
					if (evt.touches.length > 1) {
						this.touchSwipeState = null;
						return;
					}
					switch (evt.type) {
						case 'touchstart':
							this.touchSwipeState = {
								startX: evt.touches[0].pageX,
								startY: evt.touches[0].pageY,
								endX: evt.touches[0].pageX,
								endY: evt.touches[0].pageY
							};
							break;
						case 'touchmove':
							if (this.touchSwipeState === null) {
								return;
							}
							this.touchSwipeState.endX = evt.touches[0].pageX;
							this.touchSwipeState.endY = evt.touches[0].pageY;
							evt.preventDefault();
							break;
						case 'touchend':
							if (this.touchSwipeState === null) {
								return;
							}
							var delta = 0;
							var dx = this.touchSwipeState.endX - this.touchSwipeState.startX;
							var dy = this.touchSwipeState.endY - this.touchSwipeState.startY;
							var absAngle = Math.abs(Math.atan2(dy, dx));
							if (Math.abs(dx) > SWIPE_MIN_DISTANCE_THRESHOLD && (absAngle <= SWIPE_ANGLE_THRESHOLD || absAngle >= Math.PI - SWIPE_ANGLE_THRESHOLD)) {
								delta = dx;
							} else if (Math.abs(dy) > SWIPE_MIN_DISTANCE_THRESHOLD && Math.abs(absAngle - Math.PI / 2) <= SWIPE_ANGLE_THRESHOLD) {
								delta = dy;
							}
							if (delta > 0) {
								this._goToPreviousPage();
							} else if (delta < 0) {
								this._goToNextPage();
							}
							break;
					}
				},
				_addWindowListeners: function PDFPresentationMode_addWindowListeners() {
					this.showControlsBind = this._showControls.bind(this);
					this.mouseDownBind = this._mouseDown.bind(this);
					this.mouseWheelBind = this._mouseWheel.bind(this);
					this.resetMouseScrollStateBind = this._resetMouseScrollState.bind(this);
					this.contextMenuBind = this._contextMenu.bind(this);
					this.touchSwipeBind = this._touchSwipe.bind(this);
					window.addEventListener('mousemove', this.showControlsBind);
					window.addEventListener('mousedown', this.mouseDownBind);
					window.addEventListener('wheel', this.mouseWheelBind);
					window.addEventListener('keydown', this.resetMouseScrollStateBind);
					window.addEventListener('contextmenu', this.contextMenuBind);
					window.addEventListener('touchstart', this.touchSwipeBind);
					window.addEventListener('touchmove', this.touchSwipeBind);
					window.addEventListener('touchend', this.touchSwipeBind);
				},
				_removeWindowListeners: function PDFPresentationMode_removeWindowListeners() {
					window.removeEventListener('mousemove', this.showControlsBind);
					window.removeEventListener('mousedown', this.mouseDownBind);
					window.removeEventListener('wheel', this.mouseWheelBind);
					window.removeEventListener('keydown', this.resetMouseScrollStateBind);
					window.removeEventListener('contextmenu', this.contextMenuBind);
					window.removeEventListener('touchstart', this.touchSwipeBind);
					window.removeEventListener('touchmove', this.touchSwipeBind);
					window.removeEventListener('touchend', this.touchSwipeBind);
					delete this.showControlsBind;
					delete this.mouseDownBind;
					delete this.mouseWheelBind;
					delete this.resetMouseScrollStateBind;
					delete this.contextMenuBind;
					delete this.touchSwipeBind;
				},
				_fullscreenChange: function PDFPresentationMode_fullscreenChange() {
					if (this.isFullscreen) {
						this._enter();
					} else {
						this._exit();
					}
				},
				_addFullscreenChangeListeners: function PDFPresentationMode_addFullscreenChangeListeners() {
					this.fullscreenChangeBind = this._fullscreenChange.bind(this);
					window.addEventListener('fullscreenchange', this.fullscreenChangeBind);
					window.addEventListener('mozfullscreenchange', this.fullscreenChangeBind);
					window.addEventListener('webkitfullscreenchange', this.fullscreenChangeBind);
					window.addEventListener('MSFullscreenChange', this.fullscreenChangeBind);
				},
				_removeFullscreenChangeListeners: function PDFPresentationMode_removeFullscreenChangeListeners() {
					window.removeEventListener('fullscreenchange', this.fullscreenChangeBind);
					window.removeEventListener('mozfullscreenchange', this.fullscreenChangeBind);
					window.removeEventListener('webkitfullscreenchange', this.fullscreenChangeBind);
					window.removeEventListener('MSFullscreenChange', this.fullscreenChangeBind);
					delete this.fullscreenChangeBind;
				}
			};
			return PDFPresentationMode;
		}();
		exports.PDFPresentationMode = PDFPresentationMode;

		/***/
	}),
/* 22 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var pdfRenderingQueue = __webpack_require__(3);
		var uiUtils = __webpack_require__(0);
		var RenderingStates = pdfRenderingQueue.RenderingStates;
		var mozL10n = uiUtils.mozL10n;
		var UI_NOTIFICATION_CLASS = 'pdfSidebarNotification';
		var SidebarView = {
			NONE: 0,
			THUMBS: 1,
			OUTLINE: 2,
			ATTACHMENTS: 3
		};
		var PDFSidebar = function PDFSidebarClosure() {
			function PDFSidebar(options) {
				this.isOpen = false;
				this.active = SidebarView.THUMBS;
				this.isInitialViewSet = false;
				this.onToggled = null;
				this.pdfViewer = options.pdfViewer;
				this.pdfThumbnailViewer = options.pdfThumbnailViewer;
				this.pdfOutlineViewer = options.pdfOutlineViewer;
				this.mainContainer = options.mainContainer;
				this.outerContainer = options.outerContainer;
				this.eventBus = options.eventBus;
				this.toggleButton = options.toggleButton;
				this.thumbnailButton = options.thumbnailButton;
				this.outlineButton = options.outlineButton;
				this.attachmentsButton = options.attachmentsButton;
				this.thumbnailView = options.thumbnailView;
				this.outlineView = options.outlineView;
				this.attachmentsView = options.attachmentsView;
				this.disableNotification = options.disableNotification || false;
				this._addEventListeners();
			}
			PDFSidebar.prototype = {
				reset: function PDFSidebar_reset() {
					this.isInitialViewSet = false;
					this._hideUINotification(null);
					this.switchView(SidebarView.THUMBS);
					this.outlineButton.disabled = false;
					this.attachmentsButton.disabled = false;
				},
				get visibleView() {
					return this.isOpen ? this.active : SidebarView.NONE;
				},
				get isThumbnailViewVisible() {
					return this.isOpen && this.active === SidebarView.THUMBS;
				},
				get isOutlineViewVisible() {
					return this.isOpen && this.active === SidebarView.OUTLINE;
				},
				get isAttachmentsViewVisible() {
					return this.isOpen && this.active === SidebarView.ATTACHMENTS;
				},
				setInitialView: function PDFSidebar_setInitialView(view) {
					if (this.isInitialViewSet) {
						return;
					}
					this.isInitialViewSet = true;
					if (this.isOpen && view === SidebarView.NONE) {
						this._dispatchEvent();
						return;
					}
					var isViewPreserved = view === this.visibleView;
					this.switchView(view, true);
					if (isViewPreserved) {
						this._dispatchEvent();
					}
				},
				switchView: function PDFSidebar_switchView(view, forceOpen) {
					if (view === SidebarView.NONE) {
						this.close();
						return;
					}
					var isViewChanged = view !== this.active;
					var shouldForceRendering = false;
					switch (view) {
						case SidebarView.THUMBS:
							this.thumbnailButton.classList.add('toggled');
							this.outlineButton.classList.remove('toggled');
							this.attachmentsButton.classList.remove('toggled');
							this.thumbnailView.classList.remove('hidden');
							this.outlineView.classList.add('hidden');
							this.attachmentsView.classList.add('hidden');
							if (this.isOpen && isViewChanged) {
								this._updateThumbnailViewer();
								shouldForceRendering = true;
							}
							break;
						case SidebarView.OUTLINE:
							if (this.outlineButton.disabled) {
								return;
							}
							this.thumbnailButton.classList.remove('toggled');
							this.outlineButton.classList.add('toggled');
							this.attachmentsButton.classList.remove('toggled');
							this.thumbnailView.classList.add('hidden');
							this.outlineView.classList.remove('hidden');
							this.attachmentsView.classList.add('hidden');
							break;
						case SidebarView.ATTACHMENTS:
							if (this.attachmentsButton.disabled) {
								return;
							}
							this.thumbnailButton.classList.remove('toggled');
							this.outlineButton.classList.remove('toggled');
							this.attachmentsButton.classList.add('toggled');
							this.thumbnailView.classList.add('hidden');
							this.outlineView.classList.add('hidden');
							this.attachmentsView.classList.remove('hidden');
							break;
						default:
							console.error('PDFSidebar_switchView: "' + view + '" is an unsupported value.');
							return;
					}
					this.active = view | 0;
					if (forceOpen && !this.isOpen) {
						this.open();
						return;
					}
					if (shouldForceRendering) {
						this._forceRendering();
					}
					if (isViewChanged) {
						this._dispatchEvent();
					}
					this._hideUINotification(this.active);
				},
				open: function PDFSidebar_open() {
					if (this.isOpen) {
						return;
					}
					this.isOpen = true;
					this.toggleButton.classList.add('toggled');
					this.outerContainer.classList.add('sidebarMoving');
					this.outerContainer.classList.add('sidebarOpen');
					if (this.active === SidebarView.THUMBS) {
						this._updateThumbnailViewer();
					}
					this._forceRendering();
					this._dispatchEvent();
					this._hideUINotification(this.active);
				},
				close: function PDFSidebar_close() {
					if (!this.isOpen) {
						return;
					}
					this.isOpen = false;
					this.toggleButton.classList.remove('toggled');
					this.outerContainer.classList.add('sidebarMoving');
					this.outerContainer.classList.remove('sidebarOpen');
					this._forceRendering();
					this._dispatchEvent();
				},
				toggle: function PDFSidebar_toggle() {
					if (this.isOpen) {
						this.close();
					} else {
						this.open();
					}
				},
				_dispatchEvent: function PDFSidebar_dispatchEvent() {
					this.eventBus.dispatch('sidebarviewchanged', {
						source: this,
						view: this.visibleView
					});
				},
				_forceRendering: function PDFSidebar_forceRendering() {
					if (this.onToggled) {
						this.onToggled();
					} else {
						this.pdfViewer.forceRendering();
						this.pdfThumbnailViewer.forceRendering();
					}
				},
				_updateThumbnailViewer: function PDFSidebar_updateThumbnailViewer() {
					var pdfViewer = this.pdfViewer;
					var thumbnailViewer = this.pdfThumbnailViewer;
					var pagesCount = pdfViewer.pagesCount;
					for (var pageIndex = 0; pageIndex < pagesCount; pageIndex++) {
						var pageView = pdfViewer.getPageView(pageIndex);
						if (pageView && pageView.renderingState === RenderingStates.FINISHED) {
							var thumbnailView = thumbnailViewer.getThumbnail(pageIndex);
							thumbnailView.setImage(pageView);
						}
					}
					thumbnailViewer.scrollThumbnailIntoView(pdfViewer.currentPageNumber);
				},
				_showUINotification: function (view) {
					if (this.disableNotification) {
						return;
					}
					this.toggleButton.title = mozL10n.get('toggle_sidebar_notification.title', null, 'Toggle Sidebar (document contains outline/attachments)');
					if (!this.isOpen) {
						this.toggleButton.classList.add(UI_NOTIFICATION_CLASS);
					} else if (view === this.active) {
						return;
					}
					switch (view) {
						case SidebarView.OUTLINE:
							this.outlineButton.classList.add(UI_NOTIFICATION_CLASS);
							break;
						case SidebarView.ATTACHMENTS:
							this.attachmentsButton.classList.add(UI_NOTIFICATION_CLASS);
							break;
					}
				},
				_hideUINotification: function (view) {
					if (this.disableNotification) {
						return;
					}
					var removeNotification = function (view) {
						switch (view) {
							case SidebarView.OUTLINE:
								this.outlineButton.classList.remove(UI_NOTIFICATION_CLASS);
								break;
							case SidebarView.ATTACHMENTS:
								this.attachmentsButton.classList.remove(UI_NOTIFICATION_CLASS);
								break;
						}
					}.bind(this);
					if (!this.isOpen && view !== null) {
						return;
					}
					this.toggleButton.classList.remove(UI_NOTIFICATION_CLASS);
					if (view !== null) {
						removeNotification(view);
						return;
					}
					for (view in SidebarView) {
						removeNotification(SidebarView[view]);
					}
					this.toggleButton.title = mozL10n.get('toggle_sidebar.title', null, 'Toggle Sidebar');
				},
				_addEventListeners: function PDFSidebar_addEventListeners() {
					var self = this;
					self.mainContainer.addEventListener('transitionend', function (evt) {
						if (evt.target === this) {
							self.outerContainer.classList.remove('sidebarMoving');
						}
					});
					self.thumbnailButton.addEventListener('click', function () {
						self.switchView(SidebarView.THUMBS);
					});
					self.outlineButton.addEventListener('click', function () {
						self.switchView(SidebarView.OUTLINE);
					});
					self.outlineButton.addEventListener('dblclick', function () {
						self.pdfOutlineViewer.toggleOutlineTree();
					});
					self.attachmentsButton.addEventListener('click', function () {
						self.switchView(SidebarView.ATTACHMENTS);
					});
					self.eventBus.on('outlineloaded', function (e) {
						var outlineCount = e.outlineCount;
						self.outlineButton.disabled = !outlineCount;
						if (outlineCount) {
							self._showUINotification(SidebarView.OUTLINE);
						} else if (self.active === SidebarView.OUTLINE) {
							self.switchView(SidebarView.THUMBS);
						}
					});
					self.eventBus.on('attachmentsloaded', function (e) {
						var attachmentsCount = e.attachmentsCount;
						self.attachmentsButton.disabled = !attachmentsCount;
						if (attachmentsCount) {
							self._showUINotification(SidebarView.ATTACHMENTS);
						} else if (self.active === SidebarView.ATTACHMENTS) {
							self.switchView(SidebarView.THUMBS);
						}
					});
					self.eventBus.on('presentationmodechanged', function (e) {
						if (!e.active && !e.switchInProgress && self.isThumbnailViewVisible) {
							self._updateThumbnailViewer();
						}
					});
				}
			};
			return PDFSidebar;
		}();
		exports.SidebarView = SidebarView;
		exports.PDFSidebar = PDFSidebar;

		/***/
	}),
/* 23 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfRenderingQueue = __webpack_require__(3);
		var mozL10n = uiUtils.mozL10n;
		var getOutputScale = uiUtils.getOutputScale;
		var RenderingStates = pdfRenderingQueue.RenderingStates;
		var THUMBNAIL_WIDTH = 98;
		var THUMBNAIL_CANVAS_BORDER_WIDTH = 1;
		var PDFThumbnailView = function PDFThumbnailViewClosure() {
			function getTempCanvas(width, height) {
				var tempCanvas = PDFThumbnailView.tempImageCache;
				if (!tempCanvas) {
					tempCanvas = document.createElement('canvas');
					PDFThumbnailView.tempImageCache = tempCanvas;
				}
				tempCanvas.width = width;
				tempCanvas.height = height;
				tempCanvas.mozOpaque = true;
				var ctx = tempCanvas.getContext('2d', { alpha: false });
				ctx.save();
				ctx.fillStyle = 'rgb(255, 255, 255)';
				ctx.fillRect(0, 0, width, height);
				ctx.restore();
				return tempCanvas;
			}
			function PDFThumbnailView(options) {
				var container = options.container;
				var id = options.id;
				var defaultViewport = options.defaultViewport;
				var linkService = options.linkService;
				var renderingQueue = options.renderingQueue;
				var disableCanvasToImageConversion = options.disableCanvasToImageConversion || false;
				this.id = id;
				this.renderingId = 'thumbnail' + id;
				this.pageLabel = null;
				this.pdfPage = null;
				this.rotation = 0;
				this.viewport = defaultViewport;
				this.pdfPageRotate = defaultViewport.rotation;
				this.linkService = linkService;
				this.renderingQueue = renderingQueue;
				this.renderTask = null;
				this.renderingState = RenderingStates.INITIAL;
				this.resume = null;
				this.disableCanvasToImageConversion = disableCanvasToImageConversion;
				this.pageWidth = this.viewport.width;
				this.pageHeight = this.viewport.height;
				this.pageRatio = this.pageWidth / this.pageHeight;
				this.canvasWidth = THUMBNAIL_WIDTH;
				this.canvasHeight = this.canvasWidth / this.pageRatio | 0;
				this.scale = this.canvasWidth / this.pageWidth;
				var anchor = document.createElement('a');
				anchor.href = linkService.getAnchorUrl('#page=' + id);
				anchor.title = mozL10n.get('thumb_page_title', { page: id }, 'Page {{page}}');
				anchor.onclick = function stopNavigation() {
					linkService.page = id;
					return false;
				};
				this.anchor = anchor;
				var div = document.createElement('div');
				div.className = 'thumbnail';
				div.setAttribute('data-page-number', this.id);
				this.div = div;
				if (id === 1) {
					div.classList.add('selected');
				}
				var ring = document.createElement('div');
				ring.className = 'thumbnailSelectionRing';
				var borderAdjustment = 2 * THUMBNAIL_CANVAS_BORDER_WIDTH;
				ring.style.width = this.canvasWidth + borderAdjustment + 'px';
				ring.style.height = this.canvasHeight + borderAdjustment + 'px';
				this.ring = ring;
				div.appendChild(ring);
				anchor.appendChild(div);
				container.appendChild(anchor);
			}
			PDFThumbnailView.prototype = {
				setPdfPage: function PDFThumbnailView_setPdfPage(pdfPage) {
					this.pdfPage = pdfPage;
					this.pdfPageRotate = pdfPage.rotate;
					var totalRotation = (this.rotation + this.pdfPageRotate) % 360;
					this.viewport = pdfPage.getViewport(1, totalRotation);
					this.reset();
				},
				reset: function PDFThumbnailView_reset() {
					this.cancelRendering();
					this.pageWidth = this.viewport.width;
					this.pageHeight = this.viewport.height;
					this.pageRatio = this.pageWidth / this.pageHeight;
					this.canvasHeight = this.canvasWidth / this.pageRatio | 0;
					this.scale = this.canvasWidth / this.pageWidth;
					this.div.removeAttribute('data-loaded');
					var ring = this.ring;
					var childNodes = ring.childNodes;
					for (var i = childNodes.length - 1; i >= 0; i--) {
						ring.removeChild(childNodes[i]);
					}
					var borderAdjustment = 2 * THUMBNAIL_CANVAS_BORDER_WIDTH;
					ring.style.width = this.canvasWidth + borderAdjustment + 'px';
					ring.style.height = this.canvasHeight + borderAdjustment + 'px';
					if (this.canvas) {
						this.canvas.width = 0;
						this.canvas.height = 0;
						delete this.canvas;
					}
					if (this.image) {
						this.image.removeAttribute('src');
						delete this.image;
					}
				},
				update: function PDFThumbnailView_update(rotation) {
					if (typeof rotation !== 'undefined') {
						this.rotation = rotation;
					}
					var totalRotation = (this.rotation + this.pdfPageRotate) % 360;
					this.viewport = this.viewport.clone({
						scale: 1,
						rotation: totalRotation
					});
					this.reset();
				},
				cancelRendering: function PDFThumbnailView_cancelRendering() {
					if (this.renderTask) {
						this.renderTask.cancel();
						this.renderTask = null;
					}
					this.renderingState = RenderingStates.INITIAL;
					this.resume = null;
				},
				_getPageDrawContext: function PDFThumbnailView_getPageDrawContext(noCtxScale) {
					var canvas = document.createElement('canvas');
					this.canvas = canvas;
					canvas.mozOpaque = true;
					var ctx = canvas.getContext('2d', { alpha: false });
					var outputScale = getOutputScale(ctx);
					canvas.width = this.canvasWidth * outputScale.sx | 0;
					canvas.height = this.canvasHeight * outputScale.sy | 0;
					canvas.style.width = this.canvasWidth + 'px';
					canvas.style.height = this.canvasHeight + 'px';
					if (!noCtxScale && outputScale.scaled) {
						ctx.scale(outputScale.sx, outputScale.sy);
					}
					return ctx;
				},
				_convertCanvasToImage: function PDFThumbnailView_convertCanvasToImage() {
					if (!this.canvas) {
						return;
					}
					if (this.renderingState !== RenderingStates.FINISHED) {
						return;
					}
					var id = this.renderingId;
					var className = 'thumbnailImage';
					var ariaLabel = mozL10n.get('thumb_page_canvas', { page: this.pageId }, 'Thumbnail of Page {{page}}');
					if (this.disableCanvasToImageConversion) {
						this.canvas.id = id;
						this.canvas.className = className;
						this.canvas.setAttribute('aria-label', ariaLabel);
						this.div.setAttribute('data-loaded', true);
						this.ring.appendChild(this.canvas);
						return;
					}
					var image = document.createElement('img');
					image.id = id;
					image.className = className;
					image.setAttribute('aria-label', ariaLabel);
					image.style.width = this.canvasWidth + 'px';
					image.style.height = this.canvasHeight + 'px';
					image.src = this.canvas.toDataURL();
					this.image = image;
					this.div.setAttribute('data-loaded', true);
					this.ring.appendChild(image);
					this.canvas.width = 0;
					this.canvas.height = 0;
					delete this.canvas;
				},
				draw: function PDFThumbnailView_draw() {
					if (this.renderingState !== RenderingStates.INITIAL) {
						console.error('Must be in new state before drawing');
						return Promise.resolve(undefined);
					}
					this.renderingState = RenderingStates.RUNNING;
					var resolveRenderPromise, rejectRenderPromise;
					var promise = new Promise(function (resolve, reject) {
						resolveRenderPromise = resolve;
						rejectRenderPromise = reject;
					});
					var self = this;
					function thumbnailDrawCallback(error) {
						if (renderTask === self.renderTask) {
							self.renderTask = null;
						}
						if (error === 'cancelled') {
							rejectRenderPromise(error);
							return;
						}
						self.renderingState = RenderingStates.FINISHED;
						self._convertCanvasToImage();
						if (!error) {
							resolveRenderPromise(undefined);
						} else {
							rejectRenderPromise(error);
						}
					}
					var ctx = this._getPageDrawContext();
					var drawViewport = this.viewport.clone({ scale: this.scale });
					var renderContinueCallback = function renderContinueCallback(cont) {
						if (!self.renderingQueue.isHighestPriority(self)) {
							self.renderingState = RenderingStates.PAUSED;
							self.resume = function resumeCallback() {
								self.renderingState = RenderingStates.RUNNING;
								cont();
							};
							return;
						}
						cont();
					};
					var renderContext = {
						canvasContext: ctx,
						viewport: drawViewport
					};
					var renderTask = this.renderTask = this.pdfPage.render(renderContext);
					renderTask.onContinue = renderContinueCallback;
					renderTask.promise.then(function pdfPageRenderCallback() {
						thumbnailDrawCallback(null);
					}, function pdfPageRenderError(error) {
						thumbnailDrawCallback(error);
					});
					return promise;
				},
				setImage: function PDFThumbnailView_setImage(pageView) {
					if (this.renderingState !== RenderingStates.INITIAL) {
						return;
					}
					var img = pageView.canvas;
					if (!img) {
						return;
					}
					if (!this.pdfPage) {
						this.setPdfPage(pageView.pdfPage);
					}
					this.renderingState = RenderingStates.FINISHED;
					var ctx = this._getPageDrawContext(true);
					var canvas = ctx.canvas;
					if (img.width <= 2 * canvas.width) {
						ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
						this._convertCanvasToImage();
						return;
					}
					var MAX_NUM_SCALING_STEPS = 3;
					var reducedWidth = canvas.width << MAX_NUM_SCALING_STEPS;
					var reducedHeight = canvas.height << MAX_NUM_SCALING_STEPS;
					var reducedImage = getTempCanvas(reducedWidth, reducedHeight);
					var reducedImageCtx = reducedImage.getContext('2d');
					while (reducedWidth > img.width || reducedHeight > img.height) {
						reducedWidth >>= 1;
						reducedHeight >>= 1;
					}
					reducedImageCtx.drawImage(img, 0, 0, img.width, img.height, 0, 0, reducedWidth, reducedHeight);
					while (reducedWidth > 2 * canvas.width) {
						reducedImageCtx.drawImage(reducedImage, 0, 0, reducedWidth, reducedHeight, 0, 0, reducedWidth >> 1, reducedHeight >> 1);
						reducedWidth >>= 1;
						reducedHeight >>= 1;
					}
					ctx.drawImage(reducedImage, 0, 0, reducedWidth, reducedHeight, 0, 0, canvas.width, canvas.height);
					this._convertCanvasToImage();
				},
				get pageId() {
					return this.pageLabel !== null ? this.pageLabel : this.id;
				},
				setPageLabel: function PDFThumbnailView_setPageLabel(label) {
					this.pageLabel = typeof label === 'string' ? label : null;
					this.anchor.title = mozL10n.get('thumb_page_title', { page: this.pageId }, 'Page {{page}}');
					if (this.renderingState !== RenderingStates.FINISHED) {
						return;
					}
					var ariaLabel = mozL10n.get('thumb_page_canvas', { page: this.pageId }, 'Thumbnail of Page {{page}}');
					if (this.image) {
						this.image.setAttribute('aria-label', ariaLabel);
					} else if (this.disableCanvasToImageConversion && this.canvas) {
						this.canvas.setAttribute('aria-label', ariaLabel);
					}
				}
			};
			return PDFThumbnailView;
		}();
		PDFThumbnailView.tempImageCache = null;
		exports.PDFThumbnailView = PDFThumbnailView;

		/***/
	}),
/* 24 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfThumbnailView = __webpack_require__(23);
		var watchScroll = uiUtils.watchScroll;
		var getVisibleElements = uiUtils.getVisibleElements;
		var scrollIntoView = uiUtils.scrollIntoView;
		var PDFThumbnailView = pdfThumbnailView.PDFThumbnailView;
		var THUMBNAIL_SCROLL_MARGIN = -19;
		var PDFThumbnailViewer = function PDFThumbnailViewerClosure() {
			function PDFThumbnailViewer(options) {
				this.container = options.container;
				this.renderingQueue = options.renderingQueue;
				this.linkService = options.linkService;
				this.scroll = watchScroll(this.container, this._scrollUpdated.bind(this));
				this._resetView();
			}
			PDFThumbnailViewer.prototype = {
				_scrollUpdated: function PDFThumbnailViewer_scrollUpdated() {
					this.renderingQueue.renderHighestPriority();
				},
				getThumbnail: function PDFThumbnailViewer_getThumbnail(index) {
					return this.thumbnails[index];
				},
				_getVisibleThumbs: function PDFThumbnailViewer_getVisibleThumbs() {
					return getVisibleElements(this.container, this.thumbnails);
				},
				scrollThumbnailIntoView: function PDFThumbnailViewer_scrollThumbnailIntoView(page) {
					var selected = document.querySelector('.thumbnail.selected');
					if (selected) {
						selected.classList.remove('selected');
					}
					var thumbnail = document.querySelector('div.thumbnail[data-page-number="' + page + '"]');
					if (thumbnail) {
						thumbnail.classList.add('selected');
					}
					var visibleThumbs = this._getVisibleThumbs();
					var numVisibleThumbs = visibleThumbs.views.length;
					if (numVisibleThumbs > 0) {
						var first = visibleThumbs.first.id;
						var last = numVisibleThumbs > 1 ? visibleThumbs.last.id : first;
						if (page <= first || page >= last) {
							scrollIntoView(thumbnail, { top: THUMBNAIL_SCROLL_MARGIN });
						}
					}
				},
				get pagesRotation() {
					return this._pagesRotation;
				},
				set pagesRotation(rotation) {
					this._pagesRotation = rotation;
					for (var i = 0, l = this.thumbnails.length; i < l; i++) {
						var thumb = this.thumbnails[i];
						thumb.update(rotation);
					}
				},
				cleanup: function PDFThumbnailViewer_cleanup() {
					var tempCanvas = PDFThumbnailView.tempImageCache;
					if (tempCanvas) {
						tempCanvas.width = 0;
						tempCanvas.height = 0;
					}
					PDFThumbnailView.tempImageCache = null;
				},
				_resetView: function PDFThumbnailViewer_resetView() {
					this.thumbnails = [];
					this._pageLabels = null;
					this._pagesRotation = 0;
					this._pagesRequests = [];
					this.container.textContent = '';
				},
				setDocument: function PDFThumbnailViewer_setDocument(pdfDocument) {
					if (this.pdfDocument) {
						this._cancelRendering();
						this._resetView();
					}
					this.pdfDocument = pdfDocument;
					if (!pdfDocument) {
						return Promise.resolve();
					}
					return pdfDocument.getPage(1).then(function (firstPage) {
						var pagesCount = pdfDocument.numPages;
						var viewport = firstPage.getViewport(1.0);
						for (var pageNum = 1; pageNum <= pagesCount; ++pageNum) {
							var thumbnail = new PDFThumbnailView({
								container: this.container,
								id: pageNum,
								defaultViewport: viewport.clone(),
								linkService: this.linkService,
								renderingQueue: this.renderingQueue,
								disableCanvasToImageConversion: false
							});
							this.thumbnails.push(thumbnail);
						}
					}.bind(this));
				},
				_cancelRendering: function PDFThumbnailViewer_cancelRendering() {
					for (var i = 0, ii = this.thumbnails.length; i < ii; i++) {
						if (this.thumbnails[i]) {
							this.thumbnails[i].cancelRendering();
						}
					}
				},
				setPageLabels: function PDFThumbnailViewer_setPageLabels(labels) {
					if (!this.pdfDocument) {
						return;
					}
					if (!labels) {
						this._pageLabels = null;
					} else if (!(labels instanceof Array && this.pdfDocument.numPages === labels.length)) {
						this._pageLabels = null;
						console.error('PDFThumbnailViewer_setPageLabels: Invalid page labels.');
					} else {
						this._pageLabels = labels;
					}
					for (var i = 0, ii = this.thumbnails.length; i < ii; i++) {
						var thumbnailView = this.thumbnails[i];
						var label = this._pageLabels && this._pageLabels[i];
						thumbnailView.setPageLabel(label);
					}
				},
				_ensurePdfPageLoaded: function PDFThumbnailViewer_ensurePdfPageLoaded(thumbView) {
					if (thumbView.pdfPage) {
						return Promise.resolve(thumbView.pdfPage);
					}
					var pageNumber = thumbView.id;
					if (this._pagesRequests[pageNumber]) {
						return this._pagesRequests[pageNumber];
					}
					var promise = this.pdfDocument.getPage(pageNumber).then(function (pdfPage) {
						thumbView.setPdfPage(pdfPage);
						this._pagesRequests[pageNumber] = null;
						return pdfPage;
					}.bind(this));
					this._pagesRequests[pageNumber] = promise;
					return promise;
				},
				forceRendering: function () {
					var visibleThumbs = this._getVisibleThumbs();
					var thumbView = this.renderingQueue.getHighestPriority(visibleThumbs, this.thumbnails, this.scroll.down);
					if (thumbView) {
						this._ensurePdfPageLoaded(thumbView).then(function () {
							this.renderingQueue.renderView(thumbView);
						}.bind(this));
						return true;
					}
					return false;
				}
			};
			return PDFThumbnailViewer;
		}();
		exports.PDFThumbnailViewer = PDFThumbnailViewer;

		/***/
	}),
/* 25 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var pdfPageView = __webpack_require__(20);
		var pdfRenderingQueue = __webpack_require__(3);
		var textLayerBuilder = __webpack_require__(27);
		var annotationLayerBuilder = __webpack_require__(10);
		var pdfLinkService = __webpack_require__(5);
		var domEvents = __webpack_require__(2);
		var pdfjsLib = __webpack_require__(1);
		var UNKNOWN_SCALE = uiUtils.UNKNOWN_SCALE;
		var SCROLLBAR_PADDING = uiUtils.SCROLLBAR_PADDING;
		var VERTICAL_PADDING = uiUtils.VERTICAL_PADDING;
		var MAX_AUTO_SCALE = uiUtils.MAX_AUTO_SCALE;
		var CSS_UNITS = uiUtils.CSS_UNITS;
		var DEFAULT_SCALE = uiUtils.DEFAULT_SCALE;
		var DEFAULT_SCALE_VALUE = uiUtils.DEFAULT_SCALE_VALUE;
		var RendererType = uiUtils.RendererType;
		var scrollIntoView = uiUtils.scrollIntoView;
		var watchScroll = uiUtils.watchScroll;
		var getVisibleElements = uiUtils.getVisibleElements;
		var PDFPageView = pdfPageView.PDFPageView;
		var RenderingStates = pdfRenderingQueue.RenderingStates;
		var PDFRenderingQueue = pdfRenderingQueue.PDFRenderingQueue;
		var TextLayerBuilder = textLayerBuilder.TextLayerBuilder;
		var AnnotationLayerBuilder = annotationLayerBuilder.AnnotationLayerBuilder;
		var SimpleLinkService = pdfLinkService.SimpleLinkService;
		var PresentationModeState = {
			UNKNOWN: 0,
			NORMAL: 1,
			CHANGING: 2,
			FULLSCREEN: 3
		};
		var DEFAULT_CACHE_SIZE = 10;
		var PDFViewer = function pdfViewer() {
			function PDFPageViewBuffer(size) {
				var data = [];
				this.push = function cachePush(view) {
					var i = data.indexOf(view);
					if (i >= 0) {
						data.splice(i, 1);
					}
					data.push(view);
					if (data.length > size) {
						data.shift().destroy();
					}
				};
				this.resize = function (newSize) {
					size = newSize;
					while (data.length > size) {
						data.shift().destroy();
					}
				};
			}
			function isSameScale(oldScale, newScale) {
				if (newScale === oldScale) {
					return true;
				}
				if (Math.abs(newScale - oldScale) < 1e-15) {
					return true;
				}
				return false;
			}
			function isPortraitOrientation(size) {
				return size.width <= size.height;
			}
			function PDFViewer(options) {
				this.container = options.container;
				this.viewer = options.viewer || options.container.firstElementChild;
				this.eventBus = options.eventBus || domEvents.getGlobalEventBus();
				this.linkService = options.linkService || new SimpleLinkService();
				this.downloadManager = options.downloadManager || null;
				this.removePageBorders = options.removePageBorders || false;
				this.enhanceTextSelection = options.enhanceTextSelection || false;
				this.renderInteractiveForms = options.renderInteractiveForms || false;
				this.enablePrintAutoRotate = options.enablePrintAutoRotate || false;
				this.renderer = options.renderer || RendererType.CANVAS;
				this.defaultRenderingQueue = !options.renderingQueue;
				if (this.defaultRenderingQueue) {
					this.renderingQueue = new PDFRenderingQueue();
					this.renderingQueue.setViewer(this);
				} else {
					this.renderingQueue = options.renderingQueue;
				}
				this.scroll = watchScroll(this.container, this._scrollUpdate.bind(this));
				this.presentationModeState = PresentationModeState.UNKNOWN;
				this._resetView();
				if (this.removePageBorders) {
					this.viewer.classList.add('removePageBorders');
				}
			}
			PDFViewer.prototype = {
				get pagesCount() {
					return this._pages.length;
				},
				getPageView: function (index) {
					return this._pages[index];
				},
				get pageViewsReady() {
					return this._pageViewsReady;
				},
				get currentPageNumber() {
					return this._currentPageNumber;
				},
				set currentPageNumber(val) {
					if ((val | 0) !== val) {
						throw new Error('Invalid page number.');
					}
					if (!this.pdfDocument) {
						this._currentPageNumber = val;
						return;
					}
					this._setCurrentPageNumber(val, true);
				},
				_setCurrentPageNumber: function PDFViewer_setCurrentPageNumber(val, resetCurrentPageView) {
					if (this._currentPageNumber === val) {
						if (resetCurrentPageView) {
							this._resetCurrentPageView();
						}
						return;
					}
					if (!(0 < val && val <= this.pagesCount)) {
						console.error('PDFViewer_setCurrentPageNumber: "' + val + '" is out of bounds.');
						return;
					}
					var arg = {
						source: this,
						pageNumber: val,
						pageLabel: this._pageLabels && this._pageLabels[val - 1]
					};
					this._currentPageNumber = val;
					this.eventBus.dispatch('pagechanging', arg);
					this.eventBus.dispatch('pagechange', arg);
					if (resetCurrentPageView) {
						this._resetCurrentPageView();
					}
				},
				get currentPageLabel() {
					return this._pageLabels && this._pageLabels[this._currentPageNumber - 1];
				},
				set currentPageLabel(val) {
					var pageNumber = val | 0;
					if (this._pageLabels) {
						var i = this._pageLabels.indexOf(val);
						if (i >= 0) {
							pageNumber = i + 1;
						}
					}
					this.currentPageNumber = pageNumber;
				},
				get currentScale() {
					return this._currentScale !== UNKNOWN_SCALE ? this._currentScale : DEFAULT_SCALE;
				},
				set currentScale(val) {
					if (isNaN(val)) {
						throw new Error('Invalid numeric scale');
					}
					if (!this.pdfDocument) {
						this._currentScale = val;
						this._currentScaleValue = val !== UNKNOWN_SCALE ? val.toString() : null;
						return;
					}
					this._setScale(val, false);
				},
				get currentScaleValue() {
					return this._currentScaleValue;
				},
				set currentScaleValue(val) {
					if (!this.pdfDocument) {
						this._currentScale = isNaN(val) ? UNKNOWN_SCALE : val;
						this._currentScaleValue = val.toString();
						return;
					}
					this._setScale(val, false);
				},
				get pagesRotation() {
					return this._pagesRotation;
				},
				set pagesRotation(rotation) {
					if (!(typeof rotation === 'number' && rotation % 90 === 0)) {
						throw new Error('Invalid pages rotation angle.');
					}
					this._pagesRotation = rotation;
					if (!this.pdfDocument) {
						return;
					}
					for (var i = 0, l = this._pages.length; i < l; i++) {
						var pageView = this._pages[i];
						pageView.update(pageView.scale, rotation);
					}
					this._setScale(this._currentScaleValue, true);
					if (this.defaultRenderingQueue) {
						this.update();
					}
				},
				setDocument: function (pdfDocument) {
					if (this.pdfDocument) {
						this._cancelRendering();
						this._resetView();
					}
					this.pdfDocument = pdfDocument;
					if (!pdfDocument) {
						return;
					}
					var pagesCount = pdfDocument.numPages;
					var self = this;
					var resolvePagesPromise;
					var pagesPromise = new Promise(function (resolve) {
						resolvePagesPromise = resolve;
					});
					this.pagesPromise = pagesPromise;
					pagesPromise.then(function () {
						self._pageViewsReady = true;
						self.eventBus.dispatch('pagesloaded', {
							source: self,
							pagesCount: pagesCount
						});
					});
					var isOnePageRenderedResolved = false;
					var resolveOnePageRendered = null;
					var onePageRendered = new Promise(function (resolve) {
						resolveOnePageRendered = resolve;
					});
					this.onePageRendered = onePageRendered;
					var bindOnAfterAndBeforeDraw = function (pageView) {
						pageView.onBeforeDraw = function pdfViewLoadOnBeforeDraw() {
							self._buffer.push(this);
						};
						pageView.onAfterDraw = function pdfViewLoadOnAfterDraw() {
							if (!isOnePageRenderedResolved) {
								isOnePageRenderedResolved = true;
								resolveOnePageRendered();
							}
						};
					};
					var firstPagePromise = pdfDocument.getPage(1);
					this.firstPagePromise = firstPagePromise;
					return firstPagePromise.then(function (pdfPage) {
						var scale = this.currentScale;
						var viewport = pdfPage.getViewport(scale * CSS_UNITS);
						for (var pageNum = 1; pageNum <= pagesCount; ++pageNum) {
							var textLayerFactory = null;
							if (!pdfjsLib.PDFJS.disableTextLayer) {
								textLayerFactory = this;
							}
							var pageView = new PDFPageView({
								container: this.viewer,
								eventBus: this.eventBus,
								id: pageNum,
								scale: scale,
								defaultViewport: viewport.clone(),
								renderingQueue: this.renderingQueue,
								textLayerFactory: textLayerFactory,
								annotationLayerFactory: this,
								enhanceTextSelection: this.enhanceTextSelection,
								renderInteractiveForms: this.renderInteractiveForms,
								renderer: this.renderer
							});
							bindOnAfterAndBeforeDraw(pageView);
							this._pages.push(pageView);
						}
						var linkService = this.linkService;
						onePageRendered.then(function () {
							if (!pdfjsLib.PDFJS.disableAutoFetch) {
								var getPagesLeft = pagesCount;
								for (var pageNum = 1; pageNum <= pagesCount; ++pageNum) {
									pdfDocument.getPage(pageNum).then(function (pageNum, pdfPage) {
										var pageView = self._pages[pageNum - 1];
										if (!pageView.pdfPage) {
											pageView.setPdfPage(pdfPage);
										}
										linkService.cachePageRef(pageNum, pdfPage.ref);
										getPagesLeft--;
										if (!getPagesLeft) {
											resolvePagesPromise();
										}
									}.bind(null, pageNum));
								}
							} else {
								resolvePagesPromise();
							}
						});
						self.eventBus.dispatch('pagesinit', { source: self });
						if (this.defaultRenderingQueue) {
							this.update();
						}
						if (this.findController) {
							this.findController.resolveFirstPage();
						}
					}.bind(this));
				},
				setPageLabels: function PDFViewer_setPageLabels(labels) {
					if (!this.pdfDocument) {
						return;
					}
					if (!labels) {
						this._pageLabels = null;
					} else if (!(labels instanceof Array && this.pdfDocument.numPages === labels.length)) {
						this._pageLabels = null;
						console.error('PDFViewer_setPageLabels: Invalid page labels.');
					} else {
						this._pageLabels = labels;
					}
					for (var i = 0, ii = this._pages.length; i < ii; i++) {
						var pageView = this._pages[i];
						var label = this._pageLabels && this._pageLabels[i];
						pageView.setPageLabel(label);
					}
				},
				_resetView: function () {
					this._pages = [];
					this._currentPageNumber = 1;
					this._currentScale = UNKNOWN_SCALE;
					this._currentScaleValue = null;
					this._pageLabels = null;
					this._buffer = new PDFPageViewBuffer(DEFAULT_CACHE_SIZE);
					this._location = null;
					this._pagesRotation = 0;
					this._pagesRequests = [];
					this._pageViewsReady = false;
					this.viewer.textContent = '';
				},
				_scrollUpdate: function PDFViewer_scrollUpdate() {
					if (this.pagesCount === 0) {
						return;
					}
					this.update();
					for (var i = 0, ii = this._pages.length; i < ii; i++) {
						this._pages[i].updatePosition();
					}
				},
				_setScaleDispatchEvent: function pdfViewer_setScaleDispatchEvent(newScale, newValue, preset) {
					var arg = {
						source: this,
						scale: newScale,
						presetValue: preset ? newValue : undefined
					};
					this.eventBus.dispatch('scalechanging', arg);
					this.eventBus.dispatch('scalechange', arg);
				},
				_setScaleUpdatePages: function pdfViewer_setScaleUpdatePages(newScale, newValue, noScroll, preset) {
					this._currentScaleValue = newValue.toString();
					if (isSameScale(this._currentScale, newScale)) {
						if (preset) {
							this._setScaleDispatchEvent(newScale, newValue, true);
						}
						return;
					}
					for (var i = 0, ii = this._pages.length; i < ii; i++) {
						this._pages[i].update(newScale);
					}
					this._currentScale = newScale;
					if (!noScroll) {
						var page = this._currentPageNumber, dest;
						if (this._location && !pdfjsLib.PDFJS.ignoreCurrentPositionOnZoom && !(this.isInPresentationMode || this.isChangingPresentationMode)) {
							page = this._location.pageNumber;
							dest = [
								null,
								{ name: 'XYZ' },
								this._location.left,
								this._location.top,
								null
							];
						}
						this.scrollPageIntoView({
							pageNumber: page,
							destArray: dest,
							allowNegativeOffset: true
						});
					}
					this._setScaleDispatchEvent(newScale, newValue, preset);
					if (this.defaultRenderingQueue) {
						this.update();
					}
				},
				_setScale: function PDFViewer_setScale(value, noScroll) {
					var scale = parseFloat(value);
					if (scale > 0) {
						this._setScaleUpdatePages(scale, value, noScroll, false);
					} else {
						var currentPage = this._pages[this._currentPageNumber - 1];
						if (!currentPage) {
							return;
						}
						var hPadding = this.isInPresentationMode || this.removePageBorders ? 0 : SCROLLBAR_PADDING;
						var vPadding = this.isInPresentationMode || this.removePageBorders ? 0 : VERTICAL_PADDING;
						var pageWidthScale = (this.container.clientWidth - hPadding) / currentPage.width * currentPage.scale;
						var pageHeightScale = (this.container.clientHeight - vPadding) / currentPage.height * currentPage.scale;
						switch (value) {
							case 'page-actual':
								scale = 1;
								break;
							case 'page-width':
								scale = pageWidthScale;
								break;
							case 'page-height':
								scale = pageHeightScale;
								break;
							case 'page-fit':
								scale = Math.min(pageWidthScale, pageHeightScale);
								break;
							case 'auto':
								var isLandscape = currentPage.width > currentPage.height;
								var horizontalScale = isLandscape ? Math.min(pageHeightScale, pageWidthScale) : pageWidthScale;
								scale = Math.min(MAX_AUTO_SCALE, horizontalScale);
								break;
							default:
								console.error('PDFViewer_setScale: "' + value + '" is an unknown zoom value.');
								return;
						}
						this._setScaleUpdatePages(scale, value, noScroll, true);
					}
				},
				_resetCurrentPageView: function () {
					if (this.isInPresentationMode) {
						this._setScale(this._currentScaleValue, true);
					}
					var pageView = this._pages[this._currentPageNumber - 1];
					scrollIntoView(pageView.div);
				},
				scrollPageIntoView: function PDFViewer_scrollPageIntoView(params) {
					if (!this.pdfDocument) {
						return;
					}
					if (arguments.length > 1 || typeof params === 'number') {
						console.warn('Call of scrollPageIntoView() with obsolete signature.');
						var paramObj = {};
						if (typeof params === 'number') {
							paramObj.pageNumber = params;
						}
						if (arguments[1] instanceof Array) {
							paramObj.destArray = arguments[1];
						}
						params = paramObj;
					}
					var pageNumber = params.pageNumber || 0;
					var dest = params.destArray || null;
					var allowNegativeOffset = params.allowNegativeOffset || false;
					if (this.isInPresentationMode || !dest) {
						this._setCurrentPageNumber(pageNumber, true);
						return;
					}
					var pageView = this._pages[pageNumber - 1];
					if (!pageView) {
						console.error('PDFViewer_scrollPageIntoView: ' + 'Invalid "pageNumber" parameter.');
						return;
					}
					var x = 0, y = 0;
					var width = 0, height = 0, widthScale, heightScale;
					var changeOrientation = pageView.rotation % 180 === 0 ? false : true;
					var pageWidth = (changeOrientation ? pageView.height : pageView.width) / pageView.scale / CSS_UNITS;
					var pageHeight = (changeOrientation ? pageView.width : pageView.height) / pageView.scale / CSS_UNITS;
					var scale = 0;
					switch (dest[1].name) {
						case 'XYZ':
							x = dest[2];
							y = dest[3];
							scale = dest[4];
							x = x !== null ? x : 0;
							y = y !== null ? y : pageHeight;
							break;
						case 'Fit':
						case 'FitB':
							scale = 'page-fit';
							break;
						case 'FitH':
						case 'FitBH':
							y = dest[2];
							scale = 'page-width';
							if (y === null && this._location) {
								x = this._location.left;
								y = this._location.top;
							}
							break;
						case 'FitV':
						case 'FitBV':
							x = dest[2];
							width = pageWidth;
							height = pageHeight;
							scale = 'page-height';
							break;
						case 'FitR':
							x = dest[2];
							y = dest[3];
							width = dest[4] - x;
							height = dest[5] - y;
							var hPadding = this.removePageBorders ? 0 : SCROLLBAR_PADDING;
							var vPadding = this.removePageBorders ? 0 : VERTICAL_PADDING;
							widthScale = (this.container.clientWidth - hPadding) / width / CSS_UNITS;
							heightScale = (this.container.clientHeight - vPadding) / height / CSS_UNITS;
							scale = Math.min(Math.abs(widthScale), Math.abs(heightScale));
							break;
						default:
							console.error('PDFViewer_scrollPageIntoView: \'' + dest[1].name + '\' is not a valid destination type.');
							return;
					}
					if (scale && scale !== this._currentScale) {
						this.currentScaleValue = scale;
					} else if (this._currentScale === UNKNOWN_SCALE) {
						this.currentScaleValue = DEFAULT_SCALE_VALUE;
					}
					if (scale === 'page-fit' && !dest[4]) {
						scrollIntoView(pageView.div);
						return;
					}
					var boundingRect = [
						pageView.viewport.convertToViewportPoint(x, y),
						pageView.viewport.convertToViewportPoint(x + width, y + height)
					];
					var left = Math.min(boundingRect[0][0], boundingRect[1][0]);
					var top = Math.min(boundingRect[0][1], boundingRect[1][1]);
					if (!allowNegativeOffset) {
						left = Math.max(left, 0);
						top = Math.max(top, 0);
					}
					scrollIntoView(pageView.div, {
						left: left,
						top: top
					});
				},
				_updateLocation: function (firstPage) {
					var currentScale = this._currentScale;
					var currentScaleValue = this._currentScaleValue;
					var normalizedScaleValue = parseFloat(currentScaleValue) === currentScale ? Math.round(currentScale * 10000) / 100 : currentScaleValue;
					var pageNumber = firstPage.id;
					var pdfOpenParams = '#page=' + pageNumber;
					pdfOpenParams += '&zoom=' + normalizedScaleValue;
					var currentPageView = this._pages[pageNumber - 1];
					var container = this.container;
					var topLeft = currentPageView.getPagePoint(container.scrollLeft - firstPage.x, container.scrollTop - firstPage.y);
					var intLeft = Math.round(topLeft[0]);
					var intTop = Math.round(topLeft[1]);
					pdfOpenParams += ',' + intLeft + ',' + intTop;
					this._location = {
						pageNumber: pageNumber,
						scale: normalizedScaleValue,
						top: intTop,
						left: intLeft,
						pdfOpenParams: pdfOpenParams
					};
				},
				update: function PDFViewer_update() {
					var visible = this._getVisiblePages();
					var visiblePages = visible.views;
					if (visiblePages.length === 0) {
						return;
					}
					var suggestedCacheSize = Math.max(DEFAULT_CACHE_SIZE, 2 * visiblePages.length + 1);
					this._buffer.resize(suggestedCacheSize);
					this.renderingQueue.renderHighestPriority(visible);
					var currentId = this._currentPageNumber;
					var firstPage = visible.first;
					for (var i = 0, ii = visiblePages.length, stillFullyVisible = false; i < ii; ++i) {
						var page = visiblePages[i];
						if (page.percent < 100) {
							break;
						}
						if (page.id === currentId) {
							stillFullyVisible = true;
							break;
						}
					}
					if (!stillFullyVisible) {
						currentId = visiblePages[0].id;
					}
					if (!this.isInPresentationMode) {
						this._setCurrentPageNumber(currentId);
					}
					this._updateLocation(firstPage);
					this.eventBus.dispatch('updateviewarea', {
						source: this,
						location: this._location
					});
				},
				containsElement: function (element) {
					return this.container.contains(element);
				},
				focus: function () {
					this.container.focus();
				},
				get isInPresentationMode() {
					return this.presentationModeState === PresentationModeState.FULLSCREEN;
				},
				get isChangingPresentationMode() {
					return this.presentationModeState === PresentationModeState.CHANGING;
				},
				get isHorizontalScrollbarEnabled() {
					return this.isInPresentationMode ? false : this.container.scrollWidth > this.container.clientWidth;
				},
				_getVisiblePages: function () {
					if (!this.isInPresentationMode) {
						return getVisibleElements(this.container, this._pages, true);
					}
					var visible = [];
					var currentPage = this._pages[this._currentPageNumber - 1];
					visible.push({
						id: currentPage.id,
						view: currentPage
					});
					return {
						first: currentPage,
						last: currentPage,
						views: visible
					};
				},
				cleanup: function () {
					for (var i = 0, ii = this._pages.length; i < ii; i++) {
						if (this._pages[i] && this._pages[i].renderingState !== RenderingStates.FINISHED) {
							this._pages[i].reset();
						}
					}
				},
				_cancelRendering: function PDFViewer_cancelRendering() {
					for (var i = 0, ii = this._pages.length; i < ii; i++) {
						if (this._pages[i]) {
							this._pages[i].cancelRendering();
						}
					}
				},
				_ensurePdfPageLoaded: function (pageView) {
					if (pageView.pdfPage) {
						return Promise.resolve(pageView.pdfPage);
					}
					var pageNumber = pageView.id;
					if (this._pagesRequests[pageNumber]) {
						return this._pagesRequests[pageNumber];
					}
					var promise = this.pdfDocument.getPage(pageNumber).then(function (pdfPage) {
						pageView.setPdfPage(pdfPage);
						this._pagesRequests[pageNumber] = null;
						return pdfPage;
					}.bind(this));
					this._pagesRequests[pageNumber] = promise;
					return promise;
				},
				forceRendering: function (currentlyVisiblePages) {
					var visiblePages = currentlyVisiblePages || this._getVisiblePages();
					var pageView = this.renderingQueue.getHighestPriority(visiblePages, this._pages, this.scroll.down);
					if (pageView) {
						this._ensurePdfPageLoaded(pageView).then(function () {
							this.renderingQueue.renderView(pageView);
						}.bind(this));
						return true;
					}
					return false;
				},
				getPageTextContent: function (pageIndex) {
					return this.pdfDocument.getPage(pageIndex + 1).then(function (page) {
						return page.getTextContent({ normalizeWhitespace: true });
					});
				},
				createTextLayerBuilder: function (textLayerDiv, pageIndex, viewport, enhanceTextSelection) {
					return new TextLayerBuilder({
						textLayerDiv: textLayerDiv,
						eventBus: this.eventBus,
						pageIndex: pageIndex,
						viewport: viewport,
						findController: this.isInPresentationMode ? null : this.findController,
						enhanceTextSelection: this.isInPresentationMode ? false : enhanceTextSelection
					});
				},
				createAnnotationLayerBuilder: function (pageDiv, pdfPage, renderInteractiveForms) {
					return new AnnotationLayerBuilder({
						pageDiv: pageDiv,
						pdfPage: pdfPage,
						renderInteractiveForms: renderInteractiveForms,
						linkService: this.linkService,
						downloadManager: this.downloadManager
					});
				},
				setFindController: function (findController) {
					this.findController = findController;
				},
				getPagesOverview: function () {
					var pagesOverview = this._pages.map(function (pageView) {
						var viewport = pageView.pdfPage.getViewport(1);
						return {
							width: viewport.width,
							height: viewport.height,
							rotation: viewport.rotation
						};
					});
					if (!this.enablePrintAutoRotate) {
						return pagesOverview;
					}
					var isFirstPagePortrait = isPortraitOrientation(pagesOverview[0]);
					return pagesOverview.map(function (size) {
						if (isFirstPagePortrait === isPortraitOrientation(size)) {
							return size;
						}
						return {
							width: size.height,
							height: size.width,
							rotation: (size.rotation + 90) % 360
						};
					});
				}
			};
			return PDFViewer;
		}();
		exports.PresentationModeState = PresentationModeState;
		exports.PDFViewer = PDFViewer;

		/***/
	}),
/* 26 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var SCROLLBAR_PADDING = uiUtils.SCROLLBAR_PADDING;
		var mozL10n = uiUtils.mozL10n;
		var SecondaryToolbar = function SecondaryToolbarClosure() {
			function SecondaryToolbar(options, mainContainer, eventBus) {
				this.toolbar = options.toolbar;
				this.toggleButton = options.toggleButton;
				this.toolbarButtonContainer = options.toolbarButtonContainer;
				this.buttons = [
					{
						element: options.presentationModeButton,
						eventName: 'presentationmode',
						close: true
					},
					{
						element: options.openFileButton,
						eventName: 'openfile',
						close: true
					},
					{
						element: options.printButton,
						eventName: 'print',
						close: true
					},
					{
						element: options.downloadButton,
						eventName: 'download',
						close: true
					},
					{
						element: options.viewBookmarkButton,
						eventName: null,
						close: true
					},
					{
						element: options.firstPageButton,
						eventName: 'firstpage',
						close: true
					},
					{
						element: options.lastPageButton,
						eventName: 'lastpage',
						close: true
					},
					{
						element: options.pageRotateCwButton,
						eventName: 'rotatecw',
						close: false
					},
					{
						element: options.pageRotateCcwButton,
						eventName: 'rotateccw',
						close: false
					},
					{
						element: options.toggleHandToolButton,
						eventName: 'togglehandtool',
						close: true
					},
					{
						element: options.documentPropertiesButton,
						eventName: 'documentproperties',
						close: true
					}
				];
				this.items = {
					firstPage: options.firstPageButton,
					lastPage: options.lastPageButton,
					pageRotateCw: options.pageRotateCwButton,
					pageRotateCcw: options.pageRotateCcwButton
				};
				this.mainContainer = mainContainer;
				this.eventBus = eventBus;
				this.opened = false;
				this.containerHeight = null;
				this.previousContainerHeight = null;
				this.reset();
				this._bindClickListeners();
				this._bindHandToolListener(options.toggleHandToolButton);
				this.eventBus.on('resize', this._setMaxHeight.bind(this));
			}
			SecondaryToolbar.prototype = {
				get isOpen() {
					return this.opened;
				},
				setPageNumber: function SecondaryToolbar_setPageNumber(pageNumber) {
					this.pageNumber = pageNumber;
					this._updateUIState();
				},
				setPagesCount: function SecondaryToolbar_setPagesCount(pagesCount) {
					this.pagesCount = pagesCount;
					this._updateUIState();
				},
				reset: function SecondaryToolbar_reset() {
					this.pageNumber = 0;
					this.pagesCount = 0;
					this._updateUIState();
				},
				_updateUIState: function SecondaryToolbar_updateUIState() {
					var items = this.items;
					items.firstPage.disabled = this.pageNumber <= 1;
					items.lastPage.disabled = this.pageNumber >= this.pagesCount;
					items.pageRotateCw.disabled = this.pagesCount === 0;
					items.pageRotateCcw.disabled = this.pagesCount === 0;
				},
				_bindClickListeners: function SecondaryToolbar_bindClickListeners() {
					this.toggleButton.addEventListener('click', this.toggle.bind(this));
					for (var button in this.buttons) {
						var element = this.buttons[button].element;
						var eventName = this.buttons[button].eventName;
						var close = this.buttons[button].close;
						element.addEventListener('click', function (eventName, close) {
							if (eventName !== null) {
								this.eventBus.dispatch(eventName, { source: this });
							}
							if (close) {
								this.close();
							}
						}.bind(this, eventName, close));
					}
				},
				_bindHandToolListener: function SecondaryToolbar_bindHandToolListener(toggleHandToolButton) {
					var isHandToolActive = false;
					this.eventBus.on('handtoolchanged', function (e) {
						if (isHandToolActive === e.isActive) {
							return;
						}
						isHandToolActive = e.isActive;
						if (isHandToolActive) {
							toggleHandToolButton.title = mozL10n.get('hand_tool_disable.title', null, 'Disable hand tool');
							toggleHandToolButton.firstElementChild.textContent = mozL10n.get('hand_tool_disable_label', null, 'Disable hand tool');
						} else {
							toggleHandToolButton.title = mozL10n.get('hand_tool_enable.title', null, 'Enable hand tool');
							toggleHandToolButton.firstElementChild.textContent = mozL10n.get('hand_tool_enable_label', null, 'Enable hand tool');
						}
					});
				},
				open: function SecondaryToolbar_open() {
					if (this.opened) {
						return;
					}
					this.opened = true;
					this._setMaxHeight();
					this.toggleButton.classList.add('toggled');
					this.toolbar.classList.remove('hidden');
				},
				close: function SecondaryToolbar_close() {
					if (!this.opened) {
						return;
					}
					this.opened = false;
					this.toolbar.classList.add('hidden');
					this.toggleButton.classList.remove('toggled');
				},
				toggle: function SecondaryToolbar_toggle() {
					if (this.opened) {
						this.close();
					} else {
						this.open();
					}
				},
				_setMaxHeight: function SecondaryToolbar_setMaxHeight() {
					if (!this.opened) {
						return;
					}
					this.containerHeight = this.mainContainer.clientHeight;
					if (this.containerHeight === this.previousContainerHeight) {
						return;
					}
					this.toolbarButtonContainer.setAttribute('style', 'max-height: ' + (this.containerHeight - SCROLLBAR_PADDING) + 'px;');
					this.previousContainerHeight = this.containerHeight;
				}
			};
			return SecondaryToolbar;
		}();
		exports.SecondaryToolbar = SecondaryToolbar;

		/***/
	}),
/* 27 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var domEvents = __webpack_require__(2);
		var pdfjsLib = __webpack_require__(1);
		var EXPAND_DIVS_TIMEOUT = 300;
		var TextLayerBuilder = function TextLayerBuilderClosure() {
			function TextLayerBuilder(options) {
				this.textLayerDiv = options.textLayerDiv;
				this.eventBus = options.eventBus || domEvents.getGlobalEventBus();
				this.textContent = null;
				this.renderingDone = false;
				this.pageIdx = options.pageIndex;
				this.pageNumber = this.pageIdx + 1;
				this.matches = [];
				this.viewport = options.viewport;
				this.textDivs = [];
				this.findController = options.findController || null;
				this.textLayerRenderTask = null;
				this.enhanceTextSelection = options.enhanceTextSelection;
				this._bindMouse();
			}
			TextLayerBuilder.prototype = {
				_finishRendering: function TextLayerBuilder_finishRendering() {
					this.renderingDone = true;
					if (!this.enhanceTextSelection) {
						var endOfContent = document.createElement('div');
						endOfContent.className = 'endOfContent';
						this.textLayerDiv.appendChild(endOfContent);
					}
					this.eventBus.dispatch('textlayerrendered', {
						source: this,
						pageNumber: this.pageNumber,
						numTextDivs: this.textDivs.length
					});
				},
				render: function TextLayerBuilder_render(timeout) {
					if (!this.textContent || this.renderingDone) {
						return;
					}
					this.cancel();
					this.textDivs = [];
					var textLayerFrag = document.createDocumentFragment();
					this.textLayerRenderTask = pdfjsLib.renderTextLayer({
						textContent: this.textContent,
						container: textLayerFrag,
						viewport: this.viewport,
						textDivs: this.textDivs,
						timeout: timeout,
						enhanceTextSelection: this.enhanceTextSelection
					});
					this.textLayerRenderTask.promise.then(function () {
						this.textLayerDiv.appendChild(textLayerFrag);
						this._finishRendering();
						this.updateMatches();
					}.bind(this), function (reason) {
					});
				},
				cancel: function TextLayerBuilder_cancel() {
					if (this.textLayerRenderTask) {
						this.textLayerRenderTask.cancel();
						this.textLayerRenderTask = null;
					}
				},
				setTextContent: function TextLayerBuilder_setTextContent(textContent) {
					this.cancel();
					this.textContent = textContent;
				},
				convertMatches: function TextLayerBuilder_convertMatches(matches, matchesLength) {
					var i = 0;
					var iIndex = 0;
					var bidiTexts = this.textContent.items;
					var end = bidiTexts.length - 1;
					var queryLen = this.findController === null ? 0 : this.findController.state.query.length;
					var ret = [];
					if (!matches) {
						return ret;
					}
					for (var m = 0, len = matches.length; m < len; m++) {
						var matchIdx = matches[m];
						while (i !== end && matchIdx >= iIndex + bidiTexts[i].str.length) {
							iIndex += bidiTexts[i].str.length;
							i++;
						}
						if (i === bidiTexts.length) {
							console.error('Could not find a matching mapping');
						}
						var match = {
							begin: {
								divIdx: i,
								offset: matchIdx - iIndex
							}
						};
						if (matchesLength) {
							matchIdx += matchesLength[m];
						} else {
							matchIdx += queryLen;
						}
						while (i !== end && matchIdx > iIndex + bidiTexts[i].str.length) {
							iIndex += bidiTexts[i].str.length;
							i++;
						}
						match.end = {
							divIdx: i,
							offset: matchIdx - iIndex
						};
						ret.push(match);
					}
					return ret;
				},
				renderMatches: function TextLayerBuilder_renderMatches(matches) {
					if (matches.length === 0) {
						return;
					}
					var bidiTexts = this.textContent.items;
					var textDivs = this.textDivs;
					var prevEnd = null;
					var pageIdx = this.pageIdx;
					var isSelectedPage = this.findController === null ? false : pageIdx === this.findController.selected.pageIdx;
					var selectedMatchIdx = this.findController === null ? -1 : this.findController.selected.matchIdx;
					var highlightAll = this.findController === null ? false : this.findController.state.highlightAll;
					var infinity = {
						divIdx: -1,
						offset: undefined
					};
					function beginText(begin, className) {
						var divIdx = begin.divIdx;
						textDivs[divIdx].textContent = '';
						appendTextToDiv(divIdx, 0, begin.offset, className);
					}
					function appendTextToDiv(divIdx, fromOffset, toOffset, className) {
						var div = textDivs[divIdx];
						var content = bidiTexts[divIdx].str.substring(fromOffset, toOffset);
						var node = document.createTextNode(content);
						if (className) {
							var span = document.createElement('span');
							span.className = className;
							span.appendChild(node);
							div.appendChild(span);
							return;
						}
						div.appendChild(node);
					}
					var i0 = selectedMatchIdx, i1 = i0 + 1;
					if (highlightAll) {
						i0 = 0;
						i1 = matches.length;
					} else if (!isSelectedPage) {
						return;
					}
					for (var i = i0; i < i1; i++) {
						var match = matches[i];
						var begin = match.begin;
						var end = match.end;
						var isSelected = isSelectedPage && i === selectedMatchIdx;
						var highlightSuffix = isSelected ? ' selected' : '';
						if (this.findController) {
							this.findController.updateMatchPosition(pageIdx, i, textDivs, begin.divIdx);
						}
						if (!prevEnd || begin.divIdx !== prevEnd.divIdx) {
							if (prevEnd !== null) {
								appendTextToDiv(prevEnd.divIdx, prevEnd.offset, infinity.offset);
							}
							beginText(begin);
						} else {
							appendTextToDiv(prevEnd.divIdx, prevEnd.offset, begin.offset);
						}
						if (begin.divIdx === end.divIdx) {
							appendTextToDiv(begin.divIdx, begin.offset, end.offset, 'highlight' + highlightSuffix);
						} else {
							appendTextToDiv(begin.divIdx, begin.offset, infinity.offset, 'highlight begin' + highlightSuffix);
							for (var n0 = begin.divIdx + 1, n1 = end.divIdx; n0 < n1; n0++) {
								textDivs[n0].className = 'highlight middle' + highlightSuffix;
							}
							beginText(end, 'highlight end' + highlightSuffix);
						}
						prevEnd = end;
					}
					if (prevEnd) {
						appendTextToDiv(prevEnd.divIdx, prevEnd.offset, infinity.offset);
					}
				},
				updateMatches: function TextLayerBuilder_updateMatches() {
					if (!this.renderingDone) {
						return;
					}
					var matches = this.matches;
					var textDivs = this.textDivs;
					var bidiTexts = this.textContent.items;
					var clearedUntilDivIdx = -1;
					for (var i = 0, len = matches.length; i < len; i++) {
						var match = matches[i];
						var begin = Math.max(clearedUntilDivIdx, match.begin.divIdx);
						for (var n = begin, end = match.end.divIdx; n <= end; n++) {
							var div = textDivs[n];
							div.textContent = bidiTexts[n].str;
							div.className = '';
						}
						clearedUntilDivIdx = match.end.divIdx + 1;
					}
					if (this.findController === null || !this.findController.active) {
						return;
					}
					var pageMatches, pageMatchesLength;
					if (this.findController !== null) {
						pageMatches = this.findController.pageMatches[this.pageIdx] || null;
						pageMatchesLength = this.findController.pageMatchesLength ? this.findController.pageMatchesLength[this.pageIdx] || null : null;
					}
					this.matches = this.convertMatches(pageMatches, pageMatchesLength);
					this.renderMatches(this.matches);
				},
				_bindMouse: function TextLayerBuilder_bindMouse() {
					var div = this.textLayerDiv;
					var self = this;
					var expandDivsTimer = null;
					div.addEventListener('mousedown', function (e) {
						if (self.enhanceTextSelection && self.textLayerRenderTask) {
							self.textLayerRenderTask.expandTextDivs(true);
							if (expandDivsTimer) {
								clearTimeout(expandDivsTimer);
								expandDivsTimer = null;
							}
							return;
						}
						var end = div.querySelector('.endOfContent');
						if (!end) {
							return;
						}
						var adjustTop = e.target !== div;
						adjustTop = adjustTop && window.getComputedStyle(end).getPropertyValue('-moz-user-select') !== 'none';
						if (adjustTop) {
							var divBounds = div.getBoundingClientRect();
							var r = Math.max(0, (e.pageY - divBounds.top) / divBounds.height);
							end.style.top = (r * 100).toFixed(2) + '%';
						}
						end.classList.add('active');
					});
					div.addEventListener('mouseup', function (e) {
						if (self.enhanceTextSelection && self.textLayerRenderTask) {
							expandDivsTimer = setTimeout(function () {
								if (self.textLayerRenderTask) {
									self.textLayerRenderTask.expandTextDivs(false);
								}
								expandDivsTimer = null;
							}, EXPAND_DIVS_TIMEOUT);
							return;
						}
						var end = div.querySelector('.endOfContent');
						if (!end) {
							return;
						}
						end.style.top = '';
						end.classList.remove('active');
					});
				}
			};
			return TextLayerBuilder;
		}();
		function DefaultTextLayerFactory() {
		}
		DefaultTextLayerFactory.prototype = {
			createTextLayerBuilder: function (textLayerDiv, pageIndex, viewport, enhanceTextSelection) {
				return new TextLayerBuilder({
					textLayerDiv: textLayerDiv,
					pageIndex: pageIndex,
					viewport: viewport,
					enhanceTextSelection: enhanceTextSelection
				});
			}
		};
		exports.TextLayerBuilder = TextLayerBuilder;
		exports.DefaultTextLayerFactory = DefaultTextLayerFactory;

		/***/
	}),
/* 28 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var uiUtils = __webpack_require__(0);
		var mozL10n = uiUtils.mozL10n;
		var noContextMenuHandler = uiUtils.noContextMenuHandler;
		var animationStarted = uiUtils.animationStarted;
		var localized = uiUtils.localized;
		var DEFAULT_SCALE_VALUE = uiUtils.DEFAULT_SCALE_VALUE;
		var DEFAULT_SCALE = uiUtils.DEFAULT_SCALE;
		var MIN_SCALE = uiUtils.MIN_SCALE;
		var MAX_SCALE = uiUtils.MAX_SCALE;
		var PAGE_NUMBER_LOADING_INDICATOR = 'visiblePageIsLoading';
		var SCALE_SELECT_CONTAINER_PADDING = 8;
		var SCALE_SELECT_PADDING = 22;
		var Toolbar = function ToolbarClosure() {
			function Toolbar(options, mainContainer, eventBus) {
				this.toolbar = options.container;
				this.mainContainer = mainContainer;
				this.eventBus = eventBus;
				this.items = options;
				this._wasLocalized = false;
				this.reset();
				this._bindListeners();
			}
			Toolbar.prototype = {
				setPageNumber: function (pageNumber, pageLabel) {
					this.pageNumber = pageNumber;
					this.pageLabel = pageLabel;
					this._updateUIState(false);
				},
				setPagesCount: function (pagesCount, hasPageLabels) {
					this.pagesCount = pagesCount;
					this.hasPageLabels = hasPageLabels;
					this._updateUIState(true);
				},
				setPageScale: function (pageScaleValue, pageScale) {
					this.pageScaleValue = pageScaleValue;
					this.pageScale = pageScale;
					this._updateUIState(false);
				},
				reset: function () {
					this.pageNumber = 0;
					this.pageLabel = null;
					this.hasPageLabels = false;
					this.pagesCount = 0;
					this.pageScaleValue = DEFAULT_SCALE_VALUE;
					this.pageScale = DEFAULT_SCALE;
					this._updateUIState(true);
				},
				_bindListeners: function Toolbar_bindClickListeners() {
					var eventBus = this.eventBus;
					var self = this;
					var items = this.items;
					items.previous.addEventListener('click', function () {
						eventBus.dispatch('previouspage');
					});
					items.next.addEventListener('click', function () {
						eventBus.dispatch('nextpage');
					});
					items.zoomIn.addEventListener('click', function () {
						eventBus.dispatch('zoomin');
					});
					items.zoomOut.addEventListener('click', function () {
						eventBus.dispatch('zoomout');
					});
					items.pageNumber.addEventListener('click', function () {
						this.select();
					});
					items.pageNumber.addEventListener('change', function () {
						eventBus.dispatch('pagenumberchanged', {
							source: self,
							value: this.value
						});
					});
					items.scaleSelect.addEventListener('change', function () {
						if (this.value === 'custom') {
							return;
						}
						eventBus.dispatch('scalechanged', {
							source: self,
							value: this.value
						});
					});
					items.presentationModeButton.addEventListener('click', function (e) {
						eventBus.dispatch('presentationmode');
					});
					items.openFile.addEventListener('click', function (e) {
						eventBus.dispatch('openfile');
					});
					items.print.addEventListener('click', function (e) {
						eventBus.dispatch('print');
					});
					items.download.addEventListener('click', function (e) {
						eventBus.dispatch('download');
					});
					items.scaleSelect.oncontextmenu = noContextMenuHandler;
					localized.then(this._localized.bind(this));
				},
				_localized: function Toolbar_localized() {
					this._wasLocalized = true;
					this._adjustScaleWidth();
					this._updateUIState(true);
				},
				_updateUIState: function Toolbar_updateUIState(resetNumPages) {
					function selectScaleOption(value, scale) {
						var options = items.scaleSelect.options;
						var predefinedValueFound = false;
						for (var i = 0, ii = options.length; i < ii; i++) {
							var option = options[i];
							if (option.value !== value) {
								option.selected = false;
								continue;
							}
							option.selected = true;
							predefinedValueFound = true;
						}
						if (!predefinedValueFound) {
							var customScale = Math.round(scale * 10000) / 100;
							items.customScaleOption.textContent = mozL10n.get('page_scale_percent', { scale: customScale }, '{{scale}}%');
							items.customScaleOption.selected = true;
						}
					}
					if (!this._wasLocalized) {
						return;
					}
					var pageNumber = this.pageNumber;
					var scaleValue = (this.pageScaleValue || this.pageScale).toString();
					var scale = this.pageScale;
					var items = this.items;
					var pagesCount = this.pagesCount;
					if (resetNumPages) {
						if (this.hasPageLabels) {
							items.pageNumber.type = 'text';
						} else {
							items.pageNumber.type = 'number';
							items.numPages.textContent = mozL10n.get('of_pages', { pagesCount: pagesCount }, 'of {{pagesCount}}');
						}
						items.pageNumber.max = pagesCount;
					}
					if (this.hasPageLabels) {
						items.pageNumber.value = this.pageLabel;
						items.numPages.textContent = mozL10n.get('page_of_pages', {
							pageNumber: pageNumber,
							pagesCount: pagesCount
						}, '({{pageNumber}} of {{pagesCount}})');
					} else {
						items.pageNumber.value = pageNumber;
					}
					items.previous.disabled = pageNumber <= 1;
					items.next.disabled = pageNumber >= pagesCount;
					items.zoomOut.disabled = scale <= MIN_SCALE;
					items.zoomIn.disabled = scale >= MAX_SCALE;
					selectScaleOption(scaleValue, scale);
				},
				updateLoadingIndicatorState: function Toolbar_updateLoadingIndicatorState(loading) {
					var pageNumberInput = this.items.pageNumber;
					if (loading) {
						pageNumberInput.classList.add(PAGE_NUMBER_LOADING_INDICATOR);
					} else {
						pageNumberInput.classList.remove(PAGE_NUMBER_LOADING_INDICATOR);
					}
				},
				_adjustScaleWidth: function Toolbar_adjustScaleWidth() {
					var container = this.items.scaleSelectContainer;
					var select = this.items.scaleSelect;
					animationStarted.then(function () {
						if (container.clientWidth === 0) {
							container.setAttribute('style', 'display: inherit;');
						}
						if (container.clientWidth > 0) {
							select.setAttribute('style', 'min-width: inherit;');
							var width = select.clientWidth + SCALE_SELECT_CONTAINER_PADDING;
							select.setAttribute('style', 'min-width: ' + (width + SCALE_SELECT_PADDING) + 'px;');
							container.setAttribute('style', 'min-width: ' + width + 'px; ' + 'max-width: ' + width + 'px;');
						}
					});
				}
			};
			return Toolbar;
		}();
		exports.Toolbar = Toolbar;

		/***/
	}),
/* 29 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var DEFAULT_VIEW_HISTORY_CACHE_SIZE = 20;
		var ViewHistory = function ViewHistoryClosure() {
			function ViewHistory(fingerprint, cacheSize) {
				this.fingerprint = fingerprint;
				this.cacheSize = cacheSize || DEFAULT_VIEW_HISTORY_CACHE_SIZE;
				this.isInitializedPromiseResolved = false;
				this.initializedPromise = this._readFromStorage().then(function (databaseStr) {
					this.isInitializedPromiseResolved = true;
					var database = JSON.parse(databaseStr || '{}');
					if (!('files' in database)) {
						database.files = [];
					}
					if (database.files.length >= this.cacheSize) {
						database.files.shift();
					}
					var index;
					for (var i = 0, length = database.files.length; i < length; i++) {
						var branch = database.files[i];
						if (branch.fingerprint === this.fingerprint) {
							index = i;
							break;
						}
					}
					if (typeof index !== 'number') {
						index = database.files.push({ fingerprint: this.fingerprint }) - 1;
					}
					this.file = database.files[index];
					this.database = database;
				}.bind(this));
			}
			ViewHistory.prototype = {
				_writeToStorage: function ViewHistory_writeToStorage() {
					return new Promise(function (resolve) {
						var databaseStr = JSON.stringify(this.database);
						localStorage.setItem('pdfjs.history', databaseStr);
						resolve();
					}.bind(this));
				},
				_readFromStorage: function ViewHistory_readFromStorage() {
					return new Promise(function (resolve) {
						var value = localStorage.getItem('pdfjs.history');
						if (!value) {
							var databaseStr = localStorage.getItem('database');
							if (databaseStr) {
								try {
									var database = JSON.parse(databaseStr);
									if (typeof database.files[0].fingerprint === 'string') {
										localStorage.setItem('pdfjs.history', databaseStr);
										localStorage.removeItem('database');
										value = databaseStr;
									}
								} catch (ex) {
								}
							}
						}
						resolve(value);
					});
				},
				set: function ViewHistory_set(name, val) {
					if (!this.isInitializedPromiseResolved) {
						return;
					}
					this.file[name] = val;
					return this._writeToStorage();
				},
				setMultiple: function ViewHistory_setMultiple(properties) {
					if (!this.isInitializedPromiseResolved) {
						return;
					}
					for (var name in properties) {
						this.file[name] = properties[name];
					}
					return this._writeToStorage();
				},
				get: function ViewHistory_get(name, defaultValue) {
					if (!this.isInitializedPromiseResolved) {
						return defaultValue;
					}
					return this.file[name] || defaultValue;
				}
			};
			return ViewHistory;
		}();
		exports.ViewHistory = ViewHistory;

		/***/
	}),
/* 30 */
/***/ (function (module, exports, __webpack_require__) {

		"use strict";

		var DEFAULT_URL = 'compressed.tracemonkey-pldi-09.pdf';
		;
		var pdfjsWebApp;
		{
			pdfjsWebApp = __webpack_require__(6);
		}
		;
		;
		{
			__webpack_require__(9);
		}
		function getViewerConfiguration() {
			return {
				appContainer: document.body,
				mainContainer: document.getElementById('viewerContainer'),
				viewerContainer: document.getElementById('viewer'),
				eventBus: null,
				toolbar: {
					container: document.getElementById('toolbarViewer'),
					numPages: document.getElementById('numPages'),
					pageNumber: document.getElementById('pageNumber'),
					scaleSelectContainer: document.getElementById('scaleSelectContainer'),
					scaleSelect: document.getElementById('scaleSelect'),
					customScaleOption: document.getElementById('customScaleOption'),
					previous: document.getElementById('previous'),
					next: document.getElementById('next'),
					zoomIn: document.getElementById('zoomIn'),
					zoomOut: document.getElementById('zoomOut'),
					viewFind: document.getElementById('viewFind'),
					openFile: document.getElementById('openFile'),
					print: document.getElementById('print'),
					presentationModeButton: document.getElementById('presentationMode'),
					download: document.getElementById('download'),
					viewBookmark: document.getElementById('viewBookmark')
				},
				secondaryToolbar: {
					toolbar: document.getElementById('secondaryToolbar'),
					toggleButton: document.getElementById('secondaryToolbarToggle'),
					toolbarButtonContainer: document.getElementById('secondaryToolbarButtonContainer'),
					presentationModeButton: document.getElementById('secondaryPresentationMode'),
					openFileButton: document.getElementById('secondaryOpenFile'),
					printButton: document.getElementById('secondaryPrint'),
					downloadButton: document.getElementById('secondaryDownload'),
					viewBookmarkButton: document.getElementById('secondaryViewBookmark'),
					firstPageButton: document.getElementById('firstPage'),
					lastPageButton: document.getElementById('lastPage'),
					pageRotateCwButton: document.getElementById('pageRotateCw'),
					pageRotateCcwButton: document.getElementById('pageRotateCcw'),
					toggleHandToolButton: document.getElementById('toggleHandTool'),
					documentPropertiesButton: document.getElementById('documentProperties')
				},
				fullscreen: {
					contextFirstPage: document.getElementById('contextFirstPage'),
					contextLastPage: document.getElementById('contextLastPage'),
					contextPageRotateCw: document.getElementById('contextPageRotateCw'),
					contextPageRotateCcw: document.getElementById('contextPageRotateCcw')
				},
				sidebar: {
					mainContainer: document.getElementById('mainContainer'),
					outerContainer: document.getElementById('outerContainer'),
					toggleButton: document.getElementById('sidebarToggle'),
					thumbnailButton: document.getElementById('viewThumbnail'),
					outlineButton: document.getElementById('viewOutline'),
					attachmentsButton: document.getElementById('viewAttachments'),
					thumbnailView: document.getElementById('thumbnailView'),
					outlineView: document.getElementById('outlineView'),
					attachmentsView: document.getElementById('attachmentsView')
				},
				findBar: {
					bar: document.getElementById('findbar'),
					toggleButton: document.getElementById('viewFind'),
					findField: document.getElementById('findInput'),
					highlightAllCheckbox: document.getElementById('findHighlightAll'),
					caseSensitiveCheckbox: document.getElementById('findMatchCase'),
					findMsg: document.getElementById('findMsg'),
					findResultsCount: document.getElementById('findResultsCount'),
					findStatusIcon: document.getElementById('findStatusIcon'),
					findPreviousButton: document.getElementById('findPrevious'),
					findNextButton: document.getElementById('findNext')
				},
				passwordOverlay: {
					overlayName: 'passwordOverlay',
					container: document.getElementById('passwordOverlay'),
					label: document.getElementById('passwordText'),
					input: document.getElementById('password'),
					submitButton: document.getElementById('passwordSubmit'),
					cancelButton: document.getElementById('passwordCancel')
				},
				documentProperties: {
					overlayName: 'documentPropertiesOverlay',
					container: document.getElementById('documentPropertiesOverlay'),
					closeButton: document.getElementById('documentPropertiesClose'),
					fields: {
						'fileName': document.getElementById('fileNameField'),
						'fileSize': document.getElementById('fileSizeField'),
						'title': document.getElementById('titleField'),
						'author': document.getElementById('authorField'),
						'subject': document.getElementById('subjectField'),
						'keywords': document.getElementById('keywordsField'),
						'creationDate': document.getElementById('creationDateField'),
						'modificationDate': document.getElementById('modificationDateField'),
						'creator': document.getElementById('creatorField'),
						'producer': document.getElementById('producerField'),
						'version': document.getElementById('versionField'),
						'pageCount': document.getElementById('pageCountField')
					}
				},
				errorWrapper: {
					container: document.getElementById('errorWrapper'),
					errorMessage: document.getElementById('errorMessage'),
					closeButton: document.getElementById('errorClose'),
					errorMoreInfo: document.getElementById('errorMoreInfo'),
					moreInfoButton: document.getElementById('errorShowMore'),
					lessInfoButton: document.getElementById('errorShowLess')
				},
				printContainer: document.getElementById('printContainer'),
				openFileInputName: 'fileInput',
				debuggerScriptPath: './debugger.js',
				defaultUrl: DEFAULT_URL
			};
		}
		function webViewerLoad() {
			var config = getViewerConfiguration();
			window.PDFViewerApplication = pdfjsWebApp.PDFViewerApplication;
			pdfjsWebApp.PDFViewerApplication.run(config);
		}
		if (document.readyState === 'interactive' || document.readyState === 'complete') {
			webViewerLoad();
		} else {
			PDFJS.webViewerLoad = function (src) {
				DEFAULT_URL = src;
				webViewerLoad();
			};
		}

		/***/
	})
/******/]);
/* Copyright 2017 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

(function webpackUniversalModuleDefinition(root, factory) {
	if (typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if (typeof define === 'function' && define.amd)
		define("pdfjs-dist/build/pdf.worker", [], factory);
	else if (typeof exports === 'object')
		exports["pdfjs-dist/build/pdf.worker"] = factory();
	else
		root["pdfjs-dist/build/pdf.worker"] = root.pdfjsDistBuildPdfWorker = factory();
})(this, function () {
	return /******/ (function (modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};
/******/
/******/ 	// The require function
/******/ 	function __w_pdfjs_require__(moduleId) {
/******/
/******/ 		// Check if module is in cache
/******/ 		if (installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;
/******/
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			i: moduleId,
/******/ 			l: false,
/******/ 			exports: {}
				/******/
			};
/******/
/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __w_pdfjs_require__);
/******/
/******/ 		// Flag the module as loaded
/******/ 		module.l = true;
/******/
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
			/******/
		}
/******/
/******/
/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__w_pdfjs_require__.m = modules;
/******/
/******/ 	// expose the module cache
/******/ 	__w_pdfjs_require__.c = installedModules;
/******/
/******/ 	// identity function for calling harmony imports with the correct context
/******/ 	__w_pdfjs_require__.i = function (value) { return value; };
/******/
/******/ 	// define getter function for harmony exports
/******/ 	__w_pdfjs_require__.d = function (exports, name, getter) {
/******/ 		if (!__w_pdfjs_require__.o(exports, name)) {
/******/ 			Object.defineProperty(exports, name, {
/******/ 				configurable: false,
/******/ 				enumerable: true,
/******/ 				get: getter
			/******/
		});
				/******/
			}
			/******/
		};
/******/
/******/ 	// getDefaultExport function for compatibility with non-harmony modules
/******/ 	__w_pdfjs_require__.n = function (module) {
/******/ 		var getter = module && module.__esModule ?
/******/ 			function getDefault() { return module['default']; } :
/******/ 			function getModuleExports() { return module; };
/******/ 		__w_pdfjs_require__.d(getter, 'a', getter);
/******/ 		return getter;
			/******/
		};
/******/
/******/ 	// Object.prototype.hasOwnProperty.call
/******/ 	__w_pdfjs_require__.o = function (object, property) { return Object.prototype.hasOwnProperty.call(object, property); };
/******/
/******/ 	// __webpack_public_path__
/******/ 	__w_pdfjs_require__.p = "";
/******/
/******/ 	// Load entry module and return exports
/******/ 	return __w_pdfjs_require__(__w_pdfjs_require__.s = 37);
		/******/
	})
/************************************************************************/
/******/([
/* 0 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";
/* WEBPACK VAR INJECTION */(function (global) {
				var compatibility = __w_pdfjs_require__(36);
				var globalScope = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : this;
				var FONT_IDENTITY_MATRIX = [
					0.001,
					0,
					0,
					0.001,
					0,
					0
				];
				var TextRenderingMode = {
					FILL: 0,
					STROKE: 1,
					FILL_STROKE: 2,
					INVISIBLE: 3,
					FILL_ADD_TO_PATH: 4,
					STROKE_ADD_TO_PATH: 5,
					FILL_STROKE_ADD_TO_PATH: 6,
					ADD_TO_PATH: 7,
					FILL_STROKE_MASK: 3,
					ADD_TO_PATH_FLAG: 4
				};
				var ImageKind = {
					GRAYSCALE_1BPP: 1,
					RGB_24BPP: 2,
					RGBA_32BPP: 3
				};
				var AnnotationType = {
					TEXT: 1,
					LINK: 2,
					FREETEXT: 3,
					LINE: 4,
					SQUARE: 5,
					CIRCLE: 6,
					POLYGON: 7,
					POLYLINE: 8,
					HIGHLIGHT: 9,
					UNDERLINE: 10,
					SQUIGGLY: 11,
					STRIKEOUT: 12,
					STAMP: 13,
					CARET: 14,
					INK: 15,
					POPUP: 16,
					FILEATTACHMENT: 17,
					SOUND: 18,
					MOVIE: 19,
					WIDGET: 20,
					SCREEN: 21,
					PRINTERMARK: 22,
					TRAPNET: 23,
					WATERMARK: 24,
					THREED: 25,
					REDACT: 26
				};
				var AnnotationFlag = {
					INVISIBLE: 0x01,
					HIDDEN: 0x02,
					PRINT: 0x04,
					NOZOOM: 0x08,
					NOROTATE: 0x10,
					NOVIEW: 0x20,
					READONLY: 0x40,
					LOCKED: 0x80,
					TOGGLENOVIEW: 0x100,
					LOCKEDCONTENTS: 0x200
				};
				var AnnotationFieldFlag = {
					READONLY: 0x0000001,
					REQUIRED: 0x0000002,
					NOEXPORT: 0x0000004,
					MULTILINE: 0x0001000,
					PASSWORD: 0x0002000,
					NOTOGGLETOOFF: 0x0004000,
					RADIO: 0x0008000,
					PUSHBUTTON: 0x0010000,
					COMBO: 0x0020000,
					EDIT: 0x0040000,
					SORT: 0x0080000,
					FILESELECT: 0x0100000,
					MULTISELECT: 0x0200000,
					DONOTSPELLCHECK: 0x0400000,
					DONOTSCROLL: 0x0800000,
					COMB: 0x1000000,
					RICHTEXT: 0x2000000,
					RADIOSINUNISON: 0x2000000,
					COMMITONSELCHANGE: 0x4000000
				};
				var AnnotationBorderStyleType = {
					SOLID: 1,
					DASHED: 2,
					BEVELED: 3,
					INSET: 4,
					UNDERLINE: 5
				};
				var StreamType = {
					UNKNOWN: 0,
					FLATE: 1,
					LZW: 2,
					DCT: 3,
					JPX: 4,
					JBIG: 5,
					A85: 6,
					AHX: 7,
					CCF: 8,
					RL: 9
				};
				var FontType = {
					UNKNOWN: 0,
					TYPE1: 1,
					TYPE1C: 2,
					CIDFONTTYPE0: 3,
					CIDFONTTYPE0C: 4,
					TRUETYPE: 5,
					CIDFONTTYPE2: 6,
					TYPE3: 7,
					OPENTYPE: 8,
					TYPE0: 9,
					MMTYPE1: 10
				};
				var VERBOSITY_LEVELS = {
					errors: 0,
					warnings: 1,
					infos: 5
				};
				var CMapCompressionType = {
					NONE: 0,
					BINARY: 1,
					STREAM: 2
				};
				var OPS = {
					dependency: 1,
					setLineWidth: 2,
					setLineCap: 3,
					setLineJoin: 4,
					setMiterLimit: 5,
					setDash: 6,
					setRenderingIntent: 7,
					setFlatness: 8,
					setGState: 9,
					save: 10,
					restore: 11,
					transform: 12,
					moveTo: 13,
					lineTo: 14,
					curveTo: 15,
					curveTo2: 16,
					curveTo3: 17,
					closePath: 18,
					rectangle: 19,
					stroke: 20,
					closeStroke: 21,
					fill: 22,
					eoFill: 23,
					fillStroke: 24,
					eoFillStroke: 25,
					closeFillStroke: 26,
					closeEOFillStroke: 27,
					endPath: 28,
					clip: 29,
					eoClip: 30,
					beginText: 31,
					endText: 32,
					setCharSpacing: 33,
					setWordSpacing: 34,
					setHScale: 35,
					setLeading: 36,
					setFont: 37,
					setTextRenderingMode: 38,
					setTextRise: 39,
					moveText: 40,
					setLeadingMoveText: 41,
					setTextMatrix: 42,
					nextLine: 43,
					showText: 44,
					showSpacedText: 45,
					nextLineShowText: 46,
					nextLineSetSpacingShowText: 47,
					setCharWidth: 48,
					setCharWidthAndBounds: 49,
					setStrokeColorSpace: 50,
					setFillColorSpace: 51,
					setStrokeColor: 52,
					setStrokeColorN: 53,
					setFillColor: 54,
					setFillColorN: 55,
					setStrokeGray: 56,
					setFillGray: 57,
					setStrokeRGBColor: 58,
					setFillRGBColor: 59,
					setStrokeCMYKColor: 60,
					setFillCMYKColor: 61,
					shadingFill: 62,
					beginInlineImage: 63,
					beginImageData: 64,
					endInlineImage: 65,
					paintXObject: 66,
					markPoint: 67,
					markPointProps: 68,
					beginMarkedContent: 69,
					beginMarkedContentProps: 70,
					endMarkedContent: 71,
					beginCompat: 72,
					endCompat: 73,
					paintFormXObjectBegin: 74,
					paintFormXObjectEnd: 75,
					beginGroup: 76,
					endGroup: 77,
					beginAnnotations: 78,
					endAnnotations: 79,
					beginAnnotation: 80,
					endAnnotation: 81,
					paintJpegXObject: 82,
					paintImageMaskXObject: 83,
					paintImageMaskXObjectGroup: 84,
					paintImageXObject: 85,
					paintInlineImageXObject: 86,
					paintInlineImageXObjectGroup: 87,
					paintImageXObjectRepeat: 88,
					paintImageMaskXObjectRepeat: 89,
					paintSolidColorImageMask: 90,
					constructPath: 91
				};
				var verbosity = VERBOSITY_LEVELS.warnings;
				function setVerbosityLevel(level) {
					verbosity = level;
				}
				function getVerbosityLevel() {
					return verbosity;
				}
				function info(msg) {
					if (verbosity >= VERBOSITY_LEVELS.infos) {
						console.log('Info: ' + msg);
					}
				}
				function warn(msg) {
					if (verbosity >= VERBOSITY_LEVELS.warnings) {
						console.log('Warning: ' + msg);
					}
				}
				function deprecated(details) {
					console.log('Deprecated API usage: ' + details);
				}
				function error(msg) {
					if (verbosity >= VERBOSITY_LEVELS.errors) {
						console.log('Error: ' + msg);
						console.log(backtrace());
					}
					throw new Error(msg);
				}
				function backtrace() {
					try {
						throw new Error();
					} catch (e) {
						return e.stack ? e.stack.split('\n').slice(2).join('\n') : '';
					}
				}
				function assert(cond, msg) {
					if (!cond) {
						error(msg);
					}
				}
				var UNSUPPORTED_FEATURES = {
					unknown: 'unknown',
					forms: 'forms',
					javaScript: 'javaScript',
					smask: 'smask',
					shadingPattern: 'shadingPattern',
					font: 'font'
				};
				function isSameOrigin(baseUrl, otherUrl) {
					try {
						var base = new URL(baseUrl);
						if (!base.origin || base.origin === 'null') {
							return false;
						}
					} catch (e) {
						return false;
					}
					var other = new URL(otherUrl, base);
					return base.origin === other.origin;
				}
				function isValidProtocol(url) {
					if (!url) {
						return false;
					}
					switch (url.protocol) {
						case 'http:':
						case 'https:':
						case 'ftp:':
						case 'mailto:':
						case 'tel:':
							return true;
						default:
							return false;
					}
				}
				function createValidAbsoluteUrl(url, baseUrl) {
					if (!url) {
						return null;
					}
					try {
						var absoluteUrl = baseUrl ? new URL(url, baseUrl) : new URL(url);
						if (isValidProtocol(absoluteUrl)) {
							return absoluteUrl;
						}
					} catch (ex) {
					}
					return null;
				}
				function shadow(obj, prop, value) {
					Object.defineProperty(obj, prop, {
						value: value,
						enumerable: true,
						configurable: true,
						writable: false
					});
					return value;
				}
				function getLookupTableFactory(initializer) {
					var lookup;
					return function () {
						if (initializer) {
							lookup = Object.create(null);
							initializer(lookup);
							initializer = null;
						}
						return lookup;
					};
				}
				var PasswordResponses = {
					NEED_PASSWORD: 1,
					INCORRECT_PASSWORD: 2
				};
				var PasswordException = function PasswordExceptionClosure() {
					function PasswordException(msg, code) {
						this.name = 'PasswordException';
						this.message = msg;
						this.code = code;
					}
					PasswordException.prototype = new Error();
					PasswordException.constructor = PasswordException;
					return PasswordException;
				}();
				var UnknownErrorException = function UnknownErrorExceptionClosure() {
					function UnknownErrorException(msg, details) {
						this.name = 'UnknownErrorException';
						this.message = msg;
						this.details = details;
					}
					UnknownErrorException.prototype = new Error();
					UnknownErrorException.constructor = UnknownErrorException;
					return UnknownErrorException;
				}();
				var InvalidPDFException = function InvalidPDFExceptionClosure() {
					function InvalidPDFException(msg) {
						this.name = 'InvalidPDFException';
						this.message = msg;
					}
					InvalidPDFException.prototype = new Error();
					InvalidPDFException.constructor = InvalidPDFException;
					return InvalidPDFException;
				}();
				var MissingPDFException = function MissingPDFExceptionClosure() {
					function MissingPDFException(msg) {
						this.name = 'MissingPDFException';
						this.message = msg;
					}
					MissingPDFException.prototype = new Error();
					MissingPDFException.constructor = MissingPDFException;
					return MissingPDFException;
				}();
				var UnexpectedResponseException = function UnexpectedResponseExceptionClosure() {
					function UnexpectedResponseException(msg, status) {
						this.name = 'UnexpectedResponseException';
						this.message = msg;
						this.status = status;
					}
					UnexpectedResponseException.prototype = new Error();
					UnexpectedResponseException.constructor = UnexpectedResponseException;
					return UnexpectedResponseException;
				}();
				var NotImplementedException = function NotImplementedExceptionClosure() {
					function NotImplementedException(msg) {
						this.message = msg;
					}
					NotImplementedException.prototype = new Error();
					NotImplementedException.prototype.name = 'NotImplementedException';
					NotImplementedException.constructor = NotImplementedException;
					return NotImplementedException;
				}();
				var MissingDataException = function MissingDataExceptionClosure() {
					function MissingDataException(begin, end) {
						this.begin = begin;
						this.end = end;
						this.message = 'Missing data [' + begin + ', ' + end + ')';
					}
					MissingDataException.prototype = new Error();
					MissingDataException.prototype.name = 'MissingDataException';
					MissingDataException.constructor = MissingDataException;
					return MissingDataException;
				}();
				var XRefParseException = function XRefParseExceptionClosure() {
					function XRefParseException(msg) {
						this.message = msg;
					}
					XRefParseException.prototype = new Error();
					XRefParseException.prototype.name = 'XRefParseException';
					XRefParseException.constructor = XRefParseException;
					return XRefParseException;
				}();
				var NullCharactersRegExp = /\x00/g;
				function removeNullCharacters(str) {
					if (typeof str !== 'string') {
						warn('The argument for removeNullCharacters must be a string.');
						return str;
					}
					return str.replace(NullCharactersRegExp, '');
				}
				function bytesToString(bytes) {
					assert(bytes !== null && typeof bytes === 'object' && bytes.length !== undefined, 'Invalid argument for bytesToString');
					var length = bytes.length;
					var MAX_ARGUMENT_COUNT = 8192;
					if (length < MAX_ARGUMENT_COUNT) {
						return String.fromCharCode.apply(null, bytes);
					}
					var strBuf = [];
					for (var i = 0; i < length; i += MAX_ARGUMENT_COUNT) {
						var chunkEnd = Math.min(i + MAX_ARGUMENT_COUNT, length);
						var chunk = bytes.subarray(i, chunkEnd);
						strBuf.push(String.fromCharCode.apply(null, chunk));
					}
					return strBuf.join('');
				}
				function stringToBytes(str) {
					assert(typeof str === 'string', 'Invalid argument for stringToBytes');
					var length = str.length;
					var bytes = new Uint8Array(length);
					for (var i = 0; i < length; ++i) {
						bytes[i] = str.charCodeAt(i) & 0xFF;
					}
					return bytes;
				}
				function arrayByteLength(arr) {
					if (arr.length !== undefined) {
						return arr.length;
					}
					assert(arr.byteLength !== undefined);
					return arr.byteLength;
				}
				function arraysToBytes(arr) {
					if (arr.length === 1 && arr[0] instanceof Uint8Array) {
						return arr[0];
					}
					var resultLength = 0;
					var i, ii = arr.length;
					var item, itemLength;
					for (i = 0; i < ii; i++) {
						item = arr[i];
						itemLength = arrayByteLength(item);
						resultLength += itemLength;
					}
					var pos = 0;
					var data = new Uint8Array(resultLength);
					for (i = 0; i < ii; i++) {
						item = arr[i];
						if (!(item instanceof Uint8Array)) {
							if (typeof item === 'string') {
								item = stringToBytes(item);
							} else {
								item = new Uint8Array(item);
							}
						}
						itemLength = item.byteLength;
						data.set(item, pos);
						pos += itemLength;
					}
					return data;
				}
				function string32(value) {
					return String.fromCharCode(value >> 24 & 0xff, value >> 16 & 0xff, value >> 8 & 0xff, value & 0xff);
				}
				function log2(x) {
					var n = 1, i = 0;
					while (x > n) {
						n <<= 1;
						i++;
					}
					return i;
				}
				function readInt8(data, start) {
					return data[start] << 24 >> 24;
				}
				function readUint16(data, offset) {
					return data[offset] << 8 | data[offset + 1];
				}
				function readUint32(data, offset) {
					return (data[offset] << 24 | data[offset + 1] << 16 | data[offset + 2] << 8 | data[offset + 3]) >>> 0;
				}
				function isLittleEndian() {
					var buffer8 = new Uint8Array(2);
					buffer8[0] = 1;
					var buffer16 = new Uint16Array(buffer8.buffer);
					return buffer16[0] === 1;
				}
				function isEvalSupported() {
					try {
						new Function('');
						return true;
					} catch (e) {
						return false;
					}
				}
				var Uint32ArrayView = function Uint32ArrayViewClosure() {
					function Uint32ArrayView(buffer, length) {
						this.buffer = buffer;
						this.byteLength = buffer.length;
						this.length = length === undefined ? this.byteLength >> 2 : length;
						ensureUint32ArrayViewProps(this.length);
					}
					Uint32ArrayView.prototype = Object.create(null);
					var uint32ArrayViewSetters = 0;
					function createUint32ArrayProp(index) {
						return {
							get: function () {
								var buffer = this.buffer, offset = index << 2;
								return (buffer[offset] | buffer[offset + 1] << 8 | buffer[offset + 2] << 16 | buffer[offset + 3] << 24) >>> 0;
							},
							set: function (value) {
								var buffer = this.buffer, offset = index << 2;
								buffer[offset] = value & 255;
								buffer[offset + 1] = value >> 8 & 255;
								buffer[offset + 2] = value >> 16 & 255;
								buffer[offset + 3] = value >>> 24 & 255;
							}
						};
					}
					function ensureUint32ArrayViewProps(length) {
						while (uint32ArrayViewSetters < length) {
							Object.defineProperty(Uint32ArrayView.prototype, uint32ArrayViewSetters, createUint32ArrayProp(uint32ArrayViewSetters));
							uint32ArrayViewSetters++;
						}
					}
					return Uint32ArrayView;
				}();
				exports.Uint32ArrayView = Uint32ArrayView;
				var IDENTITY_MATRIX = [
					1,
					0,
					0,
					1,
					0,
					0
				];
				var Util = function UtilClosure() {
					function Util() {
					}
					var rgbBuf = [
						'rgb(',
						0,
						',',
						0,
						',',
						0,
						')'
					];
					Util.makeCssRgb = function Util_makeCssRgb(r, g, b) {
						rgbBuf[1] = r;
						rgbBuf[3] = g;
						rgbBuf[5] = b;
						return rgbBuf.join('');
					};
					Util.transform = function Util_transform(m1, m2) {
						return [
							m1[0] * m2[0] + m1[2] * m2[1],
							m1[1] * m2[0] + m1[3] * m2[1],
							m1[0] * m2[2] + m1[2] * m2[3],
							m1[1] * m2[2] + m1[3] * m2[3],
							m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
							m1[1] * m2[4] + m1[3] * m2[5] + m1[5]
						];
					};
					Util.applyTransform = function Util_applyTransform(p, m) {
						var xt = p[0] * m[0] + p[1] * m[2] + m[4];
						var yt = p[0] * m[1] + p[1] * m[3] + m[5];
						return [
							xt,
							yt
						];
					};
					Util.applyInverseTransform = function Util_applyInverseTransform(p, m) {
						var d = m[0] * m[3] - m[1] * m[2];
						var xt = (p[0] * m[3] - p[1] * m[2] + m[2] * m[5] - m[4] * m[3]) / d;
						var yt = (-p[0] * m[1] + p[1] * m[0] + m[4] * m[1] - m[5] * m[0]) / d;
						return [
							xt,
							yt
						];
					};
					Util.getAxialAlignedBoundingBox = function Util_getAxialAlignedBoundingBox(r, m) {
						var p1 = Util.applyTransform(r, m);
						var p2 = Util.applyTransform(r.slice(2, 4), m);
						var p3 = Util.applyTransform([
							r[0],
							r[3]
						], m);
						var p4 = Util.applyTransform([
							r[2],
							r[1]
						], m);
						return [
							Math.min(p1[0], p2[0], p3[0], p4[0]),
							Math.min(p1[1], p2[1], p3[1], p4[1]),
							Math.max(p1[0], p2[0], p3[0], p4[0]),
							Math.max(p1[1], p2[1], p3[1], p4[1])
						];
					};
					Util.inverseTransform = function Util_inverseTransform(m) {
						var d = m[0] * m[3] - m[1] * m[2];
						return [
							m[3] / d,
							-m[1] / d,
							-m[2] / d,
							m[0] / d,
							(m[2] * m[5] - m[4] * m[3]) / d,
							(m[4] * m[1] - m[5] * m[0]) / d
						];
					};
					Util.apply3dTransform = function Util_apply3dTransform(m, v) {
						return [
							m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
							m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
							m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
						];
					};
					Util.singularValueDecompose2dScale = function Util_singularValueDecompose2dScale(m) {
						var transpose = [
							m[0],
							m[2],
							m[1],
							m[3]
						];
						var a = m[0] * transpose[0] + m[1] * transpose[2];
						var b = m[0] * transpose[1] + m[1] * transpose[3];
						var c = m[2] * transpose[0] + m[3] * transpose[2];
						var d = m[2] * transpose[1] + m[3] * transpose[3];
						var first = (a + d) / 2;
						var second = Math.sqrt((a + d) * (a + d) - 4 * (a * d - c * b)) / 2;
						var sx = first + second || 1;
						var sy = first - second || 1;
						return [
							Math.sqrt(sx),
							Math.sqrt(sy)
						];
					};
					Util.normalizeRect = function Util_normalizeRect(rect) {
						var r = rect.slice(0);
						if (rect[0] > rect[2]) {
							r[0] = rect[2];
							r[2] = rect[0];
						}
						if (rect[1] > rect[3]) {
							r[1] = rect[3];
							r[3] = rect[1];
						}
						return r;
					};
					Util.intersect = function Util_intersect(rect1, rect2) {
						function compare(a, b) {
							return a - b;
						}
						var orderedX = [
							rect1[0],
							rect1[2],
							rect2[0],
							rect2[2]
						].sort(compare), orderedY = [
							rect1[1],
							rect1[3],
							rect2[1],
							rect2[3]
						].sort(compare), result = [];
						rect1 = Util.normalizeRect(rect1);
						rect2 = Util.normalizeRect(rect2);
						if (orderedX[0] === rect1[0] && orderedX[1] === rect2[0] || orderedX[0] === rect2[0] && orderedX[1] === rect1[0]) {
							result[0] = orderedX[1];
							result[2] = orderedX[2];
						} else {
							return false;
						}
						if (orderedY[0] === rect1[1] && orderedY[1] === rect2[1] || orderedY[0] === rect2[1] && orderedY[1] === rect1[1]) {
							result[1] = orderedY[1];
							result[3] = orderedY[2];
						} else {
							return false;
						}
						return result;
					};
					Util.sign = function Util_sign(num) {
						return num < 0 ? -1 : 1;
					};
					var ROMAN_NUMBER_MAP = [
						'',
						'C',
						'CC',
						'CCC',
						'CD',
						'D',
						'DC',
						'DCC',
						'DCCC',
						'CM',
						'',
						'X',
						'XX',
						'XXX',
						'XL',
						'L',
						'LX',
						'LXX',
						'LXXX',
						'XC',
						'',
						'I',
						'II',
						'III',
						'IV',
						'V',
						'VI',
						'VII',
						'VIII',
						'IX'
					];
					Util.toRoman = function Util_toRoman(number, lowerCase) {
						assert(isInt(number) && number > 0, 'The number should be a positive integer.');
						var pos, romanBuf = [];
						while (number >= 1000) {
							number -= 1000;
							romanBuf.push('M');
						}
						pos = number / 100 | 0;
						number %= 100;
						romanBuf.push(ROMAN_NUMBER_MAP[pos]);
						pos = number / 10 | 0;
						number %= 10;
						romanBuf.push(ROMAN_NUMBER_MAP[10 + pos]);
						romanBuf.push(ROMAN_NUMBER_MAP[20 + number]);
						var romanStr = romanBuf.join('');
						return lowerCase ? romanStr.toLowerCase() : romanStr;
					};
					Util.appendToArray = function Util_appendToArray(arr1, arr2) {
						Array.prototype.push.apply(arr1, arr2);
					};
					Util.prependToArray = function Util_prependToArray(arr1, arr2) {
						Array.prototype.unshift.apply(arr1, arr2);
					};
					Util.extendObj = function extendObj(obj1, obj2) {
						for (var key in obj2) {
							obj1[key] = obj2[key];
						}
					};
					Util.getInheritableProperty = function Util_getInheritableProperty(dict, name, getArray) {
						while (dict && !dict.has(name)) {
							dict = dict.get('Parent');
						}
						if (!dict) {
							return null;
						}
						return getArray ? dict.getArray(name) : dict.get(name);
					};
					Util.inherit = function Util_inherit(sub, base, prototype) {
						sub.prototype = Object.create(base.prototype);
						sub.prototype.constructor = sub;
						for (var prop in prototype) {
							sub.prototype[prop] = prototype[prop];
						}
					};
					Util.loadScript = function Util_loadScript(src, callback) {
						var script = document.createElement('script');
						var loaded = false;
						script.setAttribute('src', src);
						if (callback) {
							script.onload = function () {
								if (!loaded) {
									callback();
								}
								loaded = true;
							};
						}
						document.getElementsByTagName('head')[0].appendChild(script);
					};
					return Util;
				}();
				var PageViewport = function PageViewportClosure() {
					function PageViewport(viewBox, scale, rotation, offsetX, offsetY, dontFlip) {
						this.viewBox = viewBox;
						this.scale = scale;
						this.rotation = rotation;
						this.offsetX = offsetX;
						this.offsetY = offsetY;
						var centerX = (viewBox[2] + viewBox[0]) / 2;
						var centerY = (viewBox[3] + viewBox[1]) / 2;
						var rotateA, rotateB, rotateC, rotateD;
						rotation = rotation % 360;
						rotation = rotation < 0 ? rotation + 360 : rotation;
						switch (rotation) {
							case 180:
								rotateA = -1;
								rotateB = 0;
								rotateC = 0;
								rotateD = 1;
								break;
							case 90:
								rotateA = 0;
								rotateB = 1;
								rotateC = 1;
								rotateD = 0;
								break;
							case 270:
								rotateA = 0;
								rotateB = -1;
								rotateC = -1;
								rotateD = 0;
								break;
							default:
								rotateA = 1;
								rotateB = 0;
								rotateC = 0;
								rotateD = -1;
								break;
						}
						if (dontFlip) {
							rotateC = -rotateC;
							rotateD = -rotateD;
						}
						var offsetCanvasX, offsetCanvasY;
						var width, height;
						if (rotateA === 0) {
							offsetCanvasX = Math.abs(centerY - viewBox[1]) * scale + offsetX;
							offsetCanvasY = Math.abs(centerX - viewBox[0]) * scale + offsetY;
							width = Math.abs(viewBox[3] - viewBox[1]) * scale;
							height = Math.abs(viewBox[2] - viewBox[0]) * scale;
						} else {
							offsetCanvasX = Math.abs(centerX - viewBox[0]) * scale + offsetX;
							offsetCanvasY = Math.abs(centerY - viewBox[1]) * scale + offsetY;
							width = Math.abs(viewBox[2] - viewBox[0]) * scale;
							height = Math.abs(viewBox[3] - viewBox[1]) * scale;
						}
						this.transform = [
							rotateA * scale,
							rotateB * scale,
							rotateC * scale,
							rotateD * scale,
							offsetCanvasX - rotateA * scale * centerX - rotateC * scale * centerY,
							offsetCanvasY - rotateB * scale * centerX - rotateD * scale * centerY
						];
						this.width = width;
						this.height = height;
						this.fontScale = scale;
					}
					PageViewport.prototype = {
						clone: function PageViewPort_clone(args) {
							args = args || {};
							var scale = 'scale' in args ? args.scale : this.scale;
							var rotation = 'rotation' in args ? args.rotation : this.rotation;
							return new PageViewport(this.viewBox.slice(), scale, rotation, this.offsetX, this.offsetY, args.dontFlip);
						},
						convertToViewportPoint: function PageViewport_convertToViewportPoint(x, y) {
							return Util.applyTransform([
								x,
								y
							], this.transform);
						},
						convertToViewportRectangle: function PageViewport_convertToViewportRectangle(rect) {
							var tl = Util.applyTransform([
								rect[0],
								rect[1]
							], this.transform);
							var br = Util.applyTransform([
								rect[2],
								rect[3]
							], this.transform);
							return [
								tl[0],
								tl[1],
								br[0],
								br[1]
							];
						},
						convertToPdfPoint: function PageViewport_convertToPdfPoint(x, y) {
							return Util.applyInverseTransform([
								x,
								y
							], this.transform);
						}
					};
					return PageViewport;
				}();
				var PDFStringTranslateTable = [
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0x2D8,
					0x2C7,
					0x2C6,
					0x2D9,
					0x2DD,
					0x2DB,
					0x2DA,
					0x2DC,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0x2022,
					0x2020,
					0x2021,
					0x2026,
					0x2014,
					0x2013,
					0x192,
					0x2044,
					0x2039,
					0x203A,
					0x2212,
					0x2030,
					0x201E,
					0x201C,
					0x201D,
					0x2018,
					0x2019,
					0x201A,
					0x2122,
					0xFB01,
					0xFB02,
					0x141,
					0x152,
					0x160,
					0x178,
					0x17D,
					0x131,
					0x142,
					0x153,
					0x161,
					0x17E,
					0,
					0x20AC
				];
				function stringToPDFString(str) {
					var i, n = str.length, strBuf = [];
					if (str[0] === '\xFE' && str[1] === '\xFF') {
						for (i = 2; i < n; i += 2) {
							strBuf.push(String.fromCharCode(str.charCodeAt(i) << 8 | str.charCodeAt(i + 1)));
						}
					} else {
						for (i = 0; i < n; ++i) {
							var code = PDFStringTranslateTable[str.charCodeAt(i)];
							strBuf.push(code ? String.fromCharCode(code) : str.charAt(i));
						}
					}
					return strBuf.join('');
				}
				function stringToUTF8String(str) {
					return decodeURIComponent(escape(str));
				}
				function utf8StringToString(str) {
					return unescape(encodeURIComponent(str));
				}
				function isEmptyObj(obj) {
					for (var key in obj) {
						return false;
					}
					return true;
				}
				function isBool(v) {
					return typeof v === 'boolean';
				}
				function isInt(v) {
					return typeof v === 'number' && (v | 0) === v;
				}
				function isNum(v) {
					return typeof v === 'number';
				}
				function isString(v) {
					return typeof v === 'string';
				}
				function isArray(v) {
					return v instanceof Array;
				}
				function isArrayBuffer(v) {
					return typeof v === 'object' && v !== null && v.byteLength !== undefined;
				}
				function isSpace(ch) {
					return ch === 0x20 || ch === 0x09 || ch === 0x0D || ch === 0x0A;
				}
				function isNodeJS() {
					if (typeof __pdfjsdev_webpack__ === 'undefined') {
						return typeof process === 'object' && process + '' === '[object process]';
					}
					return false;
				}
				function createPromiseCapability() {
					var capability = {};
					capability.promise = new Promise(function (resolve, reject) {
						capability.resolve = resolve;
						capability.reject = reject;
					});
					return capability;
				}
				var StatTimer = function StatTimerClosure() {
					function rpad(str, pad, length) {
						while (str.length < length) {
							str += pad;
						}
						return str;
					}
					function StatTimer() {
						this.started = Object.create(null);
						this.times = [];
						this.enabled = true;
					}
					StatTimer.prototype = {
						time: function StatTimer_time(name) {
							if (!this.enabled) {
								return;
							}
							if (name in this.started) {
								warn('Timer is already running for ' + name);
							}
							this.started[name] = Date.now();
						},
						timeEnd: function StatTimer_timeEnd(name) {
							if (!this.enabled) {
								return;
							}
							if (!(name in this.started)) {
								warn('Timer has not been started for ' + name);
							}
							this.times.push({
								'name': name,
								'start': this.started[name],
								'end': Date.now()
							});
							delete this.started[name];
						},
						toString: function StatTimer_toString() {
							var i, ii;
							var times = this.times;
							var out = '';
							var longest = 0;
							for (i = 0, ii = times.length; i < ii; ++i) {
								var name = times[i]['name'];
								if (name.length > longest) {
									longest = name.length;
								}
							}
							for (i = 0, ii = times.length; i < ii; ++i) {
								var span = times[i];
								var duration = span.end - span.start;
								out += rpad(span['name'], ' ', longest) + ' ' + duration + 'ms\n';
							}
							return out;
						}
					};
					return StatTimer;
				}();
				var createBlob = function createBlob(data, contentType) {
					if (typeof Blob !== 'undefined') {
						return new Blob([data], { type: contentType });
					}
					warn('The "Blob" constructor is not supported.');
				};
				var createObjectURL = function createObjectURLClosure() {
					var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
					return function createObjectURL(data, contentType, forceDataSchema) {
						if (!forceDataSchema && typeof URL !== 'undefined' && URL.createObjectURL) {
							var blob = createBlob(data, contentType);
							return URL.createObjectURL(blob);
						}
						var buffer = 'data:' + contentType + ';base64,';
						for (var i = 0, ii = data.length; i < ii; i += 3) {
							var b1 = data[i] & 0xFF;
							var b2 = data[i + 1] & 0xFF;
							var b3 = data[i + 2] & 0xFF;
							var d1 = b1 >> 2, d2 = (b1 & 3) << 4 | b2 >> 4;
							var d3 = i + 1 < ii ? (b2 & 0xF) << 2 | b3 >> 6 : 64;
							var d4 = i + 2 < ii ? b3 & 0x3F : 64;
							buffer += digits[d1] + digits[d2] + digits[d3] + digits[d4];
						}
						return buffer;
					};
				}();
				function MessageHandler(sourceName, targetName, comObj) {
					this.sourceName = sourceName;
					this.targetName = targetName;
					this.comObj = comObj;
					this.callbackIndex = 1;
					this.postMessageTransfers = true;
					var callbacksCapabilities = this.callbacksCapabilities = Object.create(null);
					var ah = this.actionHandler = Object.create(null);
					this._onComObjOnMessage = function messageHandlerComObjOnMessage(event) {
						var data = event.data;
						if (data.targetName !== this.sourceName) {
							return;
						}
						if (data.isReply) {
							var callbackId = data.callbackId;
							if (data.callbackId in callbacksCapabilities) {
								var callback = callbacksCapabilities[callbackId];
								delete callbacksCapabilities[callbackId];
								if ('error' in data) {
									callback.reject(data.error);
								} else {
									callback.resolve(data.data);
								}
							} else {
								error('Cannot resolve callback ' + callbackId);
							}
						} else if (data.action in ah) {
							var action = ah[data.action];
							if (data.callbackId) {
								var sourceName = this.sourceName;
								var targetName = data.sourceName;
								Promise.resolve().then(function () {
									return action[0].call(action[1], data.data);
								}).then(function (result) {
									comObj.postMessage({
										sourceName: sourceName,
										targetName: targetName,
										isReply: true,
										callbackId: data.callbackId,
										data: result
									});
								}, function (reason) {
									if (reason instanceof Error) {
										reason = reason + '';
									}
									comObj.postMessage({
										sourceName: sourceName,
										targetName: targetName,
										isReply: true,
										callbackId: data.callbackId,
										error: reason
									});
								});
							} else {
								action[0].call(action[1], data.data);
							}
						} else {
							error('Unknown action from worker: ' + data.action);
						}
					}.bind(this);
					comObj.addEventListener('message', this._onComObjOnMessage);
				}
				MessageHandler.prototype = {
					on: function messageHandlerOn(actionName, handler, scope) {
						var ah = this.actionHandler;
						if (ah[actionName]) {
							error('There is already an actionName called "' + actionName + '"');
						}
						ah[actionName] = [
							handler,
							scope
						];
					},
					send: function messageHandlerSend(actionName, data, transfers) {
						var message = {
							sourceName: this.sourceName,
							targetName: this.targetName,
							action: actionName,
							data: data
						};
						this.postMessage(message, transfers);
					},
					sendWithPromise: function messageHandlerSendWithPromise(actionName, data, transfers) {
						var callbackId = this.callbackIndex++;
						var message = {
							sourceName: this.sourceName,
							targetName: this.targetName,
							action: actionName,
							data: data,
							callbackId: callbackId
						};
						var capability = createPromiseCapability();
						this.callbacksCapabilities[callbackId] = capability;
						try {
							this.postMessage(message, transfers);
						} catch (e) {
							capability.reject(e);
						}
						return capability.promise;
					},
					postMessage: function (message, transfers) {
						if (transfers && this.postMessageTransfers) {
							this.comObj.postMessage(message, transfers);
						} else {
							this.comObj.postMessage(message);
						}
					},
					destroy: function () {
						this.comObj.removeEventListener('message', this._onComObjOnMessage);
					}
				};
				function loadJpegStream(id, imageUrl, objs) {
					var img = new Image();
					img.onload = function loadJpegStream_onloadClosure() {
						objs.resolve(id, img);
					};
					img.onerror = function loadJpegStream_onerrorClosure() {
						objs.resolve(id, null);
						warn('Error during JPEG image loading');
					};
					img.src = imageUrl;
				}
				exports.FONT_IDENTITY_MATRIX = FONT_IDENTITY_MATRIX;
				exports.IDENTITY_MATRIX = IDENTITY_MATRIX;
				exports.OPS = OPS;
				exports.VERBOSITY_LEVELS = VERBOSITY_LEVELS;
				exports.UNSUPPORTED_FEATURES = UNSUPPORTED_FEATURES;
				exports.AnnotationBorderStyleType = AnnotationBorderStyleType;
				exports.AnnotationFieldFlag = AnnotationFieldFlag;
				exports.AnnotationFlag = AnnotationFlag;
				exports.AnnotationType = AnnotationType;
				exports.FontType = FontType;
				exports.ImageKind = ImageKind;
				exports.CMapCompressionType = CMapCompressionType;
				exports.InvalidPDFException = InvalidPDFException;
				exports.MessageHandler = MessageHandler;
				exports.MissingDataException = MissingDataException;
				exports.MissingPDFException = MissingPDFException;
				exports.NotImplementedException = NotImplementedException;
				exports.PageViewport = PageViewport;
				exports.PasswordException = PasswordException;
				exports.PasswordResponses = PasswordResponses;
				exports.StatTimer = StatTimer;
				exports.StreamType = StreamType;
				exports.TextRenderingMode = TextRenderingMode;
				exports.UnexpectedResponseException = UnexpectedResponseException;
				exports.UnknownErrorException = UnknownErrorException;
				exports.Util = Util;
				exports.XRefParseException = XRefParseException;
				exports.arrayByteLength = arrayByteLength;
				exports.arraysToBytes = arraysToBytes;
				exports.assert = assert;
				exports.bytesToString = bytesToString;
				exports.createBlob = createBlob;
				exports.createPromiseCapability = createPromiseCapability;
				exports.createObjectURL = createObjectURL;
				exports.deprecated = deprecated;
				exports.error = error;
				exports.getLookupTableFactory = getLookupTableFactory;
				exports.getVerbosityLevel = getVerbosityLevel;
				exports.globalScope = globalScope;
				exports.info = info;
				exports.isArray = isArray;
				exports.isArrayBuffer = isArrayBuffer;
				exports.isBool = isBool;
				exports.isEmptyObj = isEmptyObj;
				exports.isInt = isInt;
				exports.isNum = isNum;
				exports.isString = isString;
				exports.isSpace = isSpace;
				exports.isNodeJS = isNodeJS;
				exports.isSameOrigin = isSameOrigin;
				exports.createValidAbsoluteUrl = createValidAbsoluteUrl;
				exports.isLittleEndian = isLittleEndian;
				exports.isEvalSupported = isEvalSupported;
				exports.loadJpegStream = loadJpegStream;
				exports.log2 = log2;
				exports.readInt8 = readInt8;
				exports.readUint16 = readUint16;
				exports.readUint32 = readUint32;
				exports.removeNullCharacters = removeNullCharacters;
				exports.setVerbosityLevel = setVerbosityLevel;
				exports.shadow = shadow;
				exports.string32 = string32;
				exports.stringToBytes = stringToBytes;
				exports.stringToPDFString = stringToPDFString;
				exports.stringToUTF8String = stringToUTF8String;
				exports.utf8StringToString = utf8StringToString;
				exports.warn = warn;
				/* WEBPACK VAR INJECTION */
			}.call(exports, __w_pdfjs_require__(9)))

			/***/
		}),
/* 1 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var isArray = sharedUtil.isArray;
			var EOF = {};
			var Name = function NameClosure() {
				function Name(name) {
					this.name = name;
				}
				Name.prototype = {};
				var nameCache = Object.create(null);
				Name.get = function Name_get(name) {
					var nameValue = nameCache[name];
					return nameValue ? nameValue : nameCache[name] = new Name(name);
				};
				return Name;
			}();
			var Cmd = function CmdClosure() {
				function Cmd(cmd) {
					this.cmd = cmd;
				}
				Cmd.prototype = {};
				var cmdCache = Object.create(null);
				Cmd.get = function Cmd_get(cmd) {
					var cmdValue = cmdCache[cmd];
					return cmdValue ? cmdValue : cmdCache[cmd] = new Cmd(cmd);
				};
				return Cmd;
			}();
			var Dict = function DictClosure() {
				var nonSerializable = function nonSerializableClosure() {
					return nonSerializable;
				};
				function Dict(xref) {
					this.map = Object.create(null);
					this.xref = xref;
					this.objId = null;
					this.suppressEncryption = false;
					this.__nonSerializable__ = nonSerializable;
				}
				Dict.prototype = {
					assignXref: function Dict_assignXref(newXref) {
						this.xref = newXref;
					},
					get: function Dict_get(key1, key2, key3) {
						var value;
						var xref = this.xref, suppressEncryption = this.suppressEncryption;
						if (typeof (value = this.map[key1]) !== 'undefined' || key1 in this.map || typeof key2 === 'undefined') {
							return xref ? xref.fetchIfRef(value, suppressEncryption) : value;
						}
						if (typeof (value = this.map[key2]) !== 'undefined' || key2 in this.map || typeof key3 === 'undefined') {
							return xref ? xref.fetchIfRef(value, suppressEncryption) : value;
						}
						value = this.map[key3] || null;
						return xref ? xref.fetchIfRef(value, suppressEncryption) : value;
					},
					getAsync: function Dict_getAsync(key1, key2, key3) {
						var value;
						var xref = this.xref, suppressEncryption = this.suppressEncryption;
						if (typeof (value = this.map[key1]) !== 'undefined' || key1 in this.map || typeof key2 === 'undefined') {
							if (xref) {
								return xref.fetchIfRefAsync(value, suppressEncryption);
							}
							return Promise.resolve(value);
						}
						if (typeof (value = this.map[key2]) !== 'undefined' || key2 in this.map || typeof key3 === 'undefined') {
							if (xref) {
								return xref.fetchIfRefAsync(value, suppressEncryption);
							}
							return Promise.resolve(value);
						}
						value = this.map[key3] || null;
						if (xref) {
							return xref.fetchIfRefAsync(value, suppressEncryption);
						}
						return Promise.resolve(value);
					},
					getArray: function Dict_getArray(key1, key2, key3) {
						var value = this.get(key1, key2, key3);
						var xref = this.xref, suppressEncryption = this.suppressEncryption;
						if (!isArray(value) || !xref) {
							return value;
						}
						value = value.slice();
						for (var i = 0, ii = value.length; i < ii; i++) {
							if (!isRef(value[i])) {
								continue;
							}
							value[i] = xref.fetch(value[i], suppressEncryption);
						}
						return value;
					},
					getRaw: function Dict_getRaw(key) {
						return this.map[key];
					},
					getKeys: function Dict_getKeys() {
						return Object.keys(this.map);
					},
					set: function Dict_set(key, value) {
						this.map[key] = value;
					},
					has: function Dict_has(key) {
						return key in this.map;
					},
					forEach: function Dict_forEach(callback) {
						for (var key in this.map) {
							callback(key, this.get(key));
						}
					}
				};
				Dict.empty = new Dict(null);
				Dict.merge = function Dict_merge(xref, dictArray) {
					var mergedDict = new Dict(xref);
					for (var i = 0, ii = dictArray.length; i < ii; i++) {
						var dict = dictArray[i];
						if (!isDict(dict)) {
							continue;
						}
						for (var keyName in dict.map) {
							if (mergedDict.map[keyName]) {
								continue;
							}
							mergedDict.map[keyName] = dict.map[keyName];
						}
					}
					return mergedDict;
				};
				return Dict;
			}();
			var Ref = function RefClosure() {
				function Ref(num, gen) {
					this.num = num;
					this.gen = gen;
				}
				Ref.prototype = {
					toString: function Ref_toString() {
						var str = this.num + 'R';
						if (this.gen !== 0) {
							str += this.gen;
						}
						return str;
					}
				};
				return Ref;
			}();
			var RefSet = function RefSetClosure() {
				function RefSet() {
					this.dict = Object.create(null);
				}
				RefSet.prototype = {
					has: function RefSet_has(ref) {
						return ref.toString() in this.dict;
					},
					put: function RefSet_put(ref) {
						this.dict[ref.toString()] = true;
					},
					remove: function RefSet_remove(ref) {
						delete this.dict[ref.toString()];
					}
				};
				return RefSet;
			}();
			var RefSetCache = function RefSetCacheClosure() {
				function RefSetCache() {
					this.dict = Object.create(null);
				}
				RefSetCache.prototype = {
					get: function RefSetCache_get(ref) {
						return this.dict[ref.toString()];
					},
					has: function RefSetCache_has(ref) {
						return ref.toString() in this.dict;
					},
					put: function RefSetCache_put(ref, obj) {
						this.dict[ref.toString()] = obj;
					},
					putAlias: function RefSetCache_putAlias(ref, aliasRef) {
						this.dict[ref.toString()] = this.get(aliasRef);
					},
					forEach: function RefSetCache_forEach(fn, thisArg) {
						for (var i in this.dict) {
							fn.call(thisArg, this.dict[i]);
						}
					},
					clear: function RefSetCache_clear() {
						this.dict = Object.create(null);
					}
				};
				return RefSetCache;
			}();
			function isEOF(v) {
				return v === EOF;
			}
			function isName(v, name) {
				return v instanceof Name && (name === undefined || v.name === name);
			}
			function isCmd(v, cmd) {
				return v instanceof Cmd && (cmd === undefined || v.cmd === cmd);
			}
			function isDict(v, type) {
				return v instanceof Dict && (type === undefined || isName(v.get('Type'), type));
			}
			function isRef(v) {
				return v instanceof Ref;
			}
			function isRefsEqual(v1, v2) {
				return v1.num === v2.num && v1.gen === v2.gen;
			}
			function isStream(v) {
				return typeof v === 'object' && v !== null && v.getBytes !== undefined;
			}
			exports.EOF = EOF;
			exports.Cmd = Cmd;
			exports.Dict = Dict;
			exports.Name = Name;
			exports.Ref = Ref;
			exports.RefSet = RefSet;
			exports.RefSetCache = RefSetCache;
			exports.isEOF = isEOF;
			exports.isCmd = isCmd;
			exports.isDict = isDict;
			exports.isName = isName;
			exports.isRef = isRef;
			exports.isRefsEqual = isRefsEqual;
			exports.isStream = isStream;

			/***/
		}),
/* 2 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreJbig2 = __w_pdfjs_require__(28);
			var coreJpg = __w_pdfjs_require__(29);
			var coreJpx = __w_pdfjs_require__(15);
			var Util = sharedUtil.Util;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isInt = sharedUtil.isInt;
			var isArray = sharedUtil.isArray;
			var createObjectURL = sharedUtil.createObjectURL;
			var shadow = sharedUtil.shadow;
			var isSpace = sharedUtil.isSpace;
			var Dict = corePrimitives.Dict;
			var isDict = corePrimitives.isDict;
			var isStream = corePrimitives.isStream;
			var Jbig2Image = coreJbig2.Jbig2Image;
			var JpegImage = coreJpg.JpegImage;
			var JpxImage = coreJpx.JpxImage;
			var Stream = function StreamClosure() {
				function Stream(arrayBuffer, start, length, dict) {
					this.bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
					this.start = start || 0;
					this.pos = this.start;
					this.end = start + length || this.bytes.length;
					this.dict = dict;
				}
				Stream.prototype = {
					get length() {
						return this.end - this.start;
					},
					get isEmpty() {
						return this.length === 0;
					},
					getByte: function Stream_getByte() {
						if (this.pos >= this.end) {
							return -1;
						}
						return this.bytes[this.pos++];
					},
					getUint16: function Stream_getUint16() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						if (b0 === -1 || b1 === -1) {
							return -1;
						}
						return (b0 << 8) + b1;
					},
					getInt32: function Stream_getInt32() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						var b2 = this.getByte();
						var b3 = this.getByte();
						return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
					},
					getBytes: function Stream_getBytes(length) {
						var bytes = this.bytes;
						var pos = this.pos;
						var strEnd = this.end;
						if (!length) {
							return bytes.subarray(pos, strEnd);
						}
						var end = pos + length;
						if (end > strEnd) {
							end = strEnd;
						}
						this.pos = end;
						return bytes.subarray(pos, end);
					},
					peekByte: function Stream_peekByte() {
						var peekedByte = this.getByte();
						this.pos--;
						return peekedByte;
					},
					peekBytes: function Stream_peekBytes(length) {
						var bytes = this.getBytes(length);
						this.pos -= bytes.length;
						return bytes;
					},
					skip: function Stream_skip(n) {
						if (!n) {
							n = 1;
						}
						this.pos += n;
					},
					reset: function Stream_reset() {
						this.pos = this.start;
					},
					moveStart: function Stream_moveStart() {
						this.start = this.pos;
					},
					makeSubStream: function Stream_makeSubStream(start, length, dict) {
						return new Stream(this.bytes.buffer, start, length, dict);
					}
				};
				return Stream;
			}();
			var StringStream = function StringStreamClosure() {
				function StringStream(str) {
					var length = str.length;
					var bytes = new Uint8Array(length);
					for (var n = 0; n < length; ++n) {
						bytes[n] = str.charCodeAt(n);
					}
					Stream.call(this, bytes);
				}
				StringStream.prototype = Stream.prototype;
				return StringStream;
			}();
			var DecodeStream = function DecodeStreamClosure() {
				var emptyBuffer = new Uint8Array(0);
				function DecodeStream(maybeMinBufferLength) {
					this.pos = 0;
					this.bufferLength = 0;
					this.eof = false;
					this.buffer = emptyBuffer;
					this.minBufferLength = 512;
					if (maybeMinBufferLength) {
						while (this.minBufferLength < maybeMinBufferLength) {
							this.minBufferLength *= 2;
						}
					}
				}
				DecodeStream.prototype = {
					get isEmpty() {
						while (!this.eof && this.bufferLength === 0) {
							this.readBlock();
						}
						return this.bufferLength === 0;
					},
					ensureBuffer: function DecodeStream_ensureBuffer(requested) {
						var buffer = this.buffer;
						if (requested <= buffer.byteLength) {
							return buffer;
						}
						var size = this.minBufferLength;
						while (size < requested) {
							size *= 2;
						}
						var buffer2 = new Uint8Array(size);
						buffer2.set(buffer);
						return this.buffer = buffer2;
					},
					getByte: function DecodeStream_getByte() {
						var pos = this.pos;
						while (this.bufferLength <= pos) {
							if (this.eof) {
								return -1;
							}
							this.readBlock();
						}
						return this.buffer[this.pos++];
					},
					getUint16: function DecodeStream_getUint16() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						if (b0 === -1 || b1 === -1) {
							return -1;
						}
						return (b0 << 8) + b1;
					},
					getInt32: function DecodeStream_getInt32() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						var b2 = this.getByte();
						var b3 = this.getByte();
						return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
					},
					getBytes: function DecodeStream_getBytes(length) {
						var end, pos = this.pos;
						if (length) {
							this.ensureBuffer(pos + length);
							end = pos + length;
							while (!this.eof && this.bufferLength < end) {
								this.readBlock();
							}
							var bufEnd = this.bufferLength;
							if (end > bufEnd) {
								end = bufEnd;
							}
						} else {
							while (!this.eof) {
								this.readBlock();
							}
							end = this.bufferLength;
						}
						this.pos = end;
						return this.buffer.subarray(pos, end);
					},
					peekByte: function DecodeStream_peekByte() {
						var peekedByte = this.getByte();
						this.pos--;
						return peekedByte;
					},
					peekBytes: function DecodeStream_peekBytes(length) {
						var bytes = this.getBytes(length);
						this.pos -= bytes.length;
						return bytes;
					},
					makeSubStream: function DecodeStream_makeSubStream(start, length, dict) {
						var end = start + length;
						while (this.bufferLength <= end && !this.eof) {
							this.readBlock();
						}
						return new Stream(this.buffer, start, length, dict);
					},
					skip: function DecodeStream_skip(n) {
						if (!n) {
							n = 1;
						}
						this.pos += n;
					},
					reset: function DecodeStream_reset() {
						this.pos = 0;
					},
					getBaseStreams: function DecodeStream_getBaseStreams() {
						if (this.str && this.str.getBaseStreams) {
							return this.str.getBaseStreams();
						}
						return [];
					}
				};
				return DecodeStream;
			}();
			var StreamsSequenceStream = function StreamsSequenceStreamClosure() {
				function StreamsSequenceStream(streams) {
					this.streams = streams;
					DecodeStream.call(this, null);
				}
				StreamsSequenceStream.prototype = Object.create(DecodeStream.prototype);
				StreamsSequenceStream.prototype.readBlock = function streamSequenceStreamReadBlock() {
					var streams = this.streams;
					if (streams.length === 0) {
						this.eof = true;
						return;
					}
					var stream = streams.shift();
					var chunk = stream.getBytes();
					var bufferLength = this.bufferLength;
					var newLength = bufferLength + chunk.length;
					var buffer = this.ensureBuffer(newLength);
					buffer.set(chunk, bufferLength);
					this.bufferLength = newLength;
				};
				StreamsSequenceStream.prototype.getBaseStreams = function StreamsSequenceStream_getBaseStreams() {
					var baseStreams = [];
					for (var i = 0, ii = this.streams.length; i < ii; i++) {
						var stream = this.streams[i];
						if (stream.getBaseStreams) {
							Util.appendToArray(baseStreams, stream.getBaseStreams());
						}
					}
					return baseStreams;
				};
				return StreamsSequenceStream;
			}();
			var FlateStream = function FlateStreamClosure() {
				var codeLenCodeMap = new Int32Array([
					16,
					17,
					18,
					0,
					8,
					7,
					9,
					6,
					10,
					5,
					11,
					4,
					12,
					3,
					13,
					2,
					14,
					1,
					15
				]);
				var lengthDecode = new Int32Array([
					0x00003,
					0x00004,
					0x00005,
					0x00006,
					0x00007,
					0x00008,
					0x00009,
					0x0000a,
					0x1000b,
					0x1000d,
					0x1000f,
					0x10011,
					0x20013,
					0x20017,
					0x2001b,
					0x2001f,
					0x30023,
					0x3002b,
					0x30033,
					0x3003b,
					0x40043,
					0x40053,
					0x40063,
					0x40073,
					0x50083,
					0x500a3,
					0x500c3,
					0x500e3,
					0x00102,
					0x00102,
					0x00102
				]);
				var distDecode = new Int32Array([
					0x00001,
					0x00002,
					0x00003,
					0x00004,
					0x10005,
					0x10007,
					0x20009,
					0x2000d,
					0x30011,
					0x30019,
					0x40021,
					0x40031,
					0x50041,
					0x50061,
					0x60081,
					0x600c1,
					0x70101,
					0x70181,
					0x80201,
					0x80301,
					0x90401,
					0x90601,
					0xa0801,
					0xa0c01,
					0xb1001,
					0xb1801,
					0xc2001,
					0xc3001,
					0xd4001,
					0xd6001
				]);
				var fixedLitCodeTab = [
					new Int32Array([
						0x70100,
						0x80050,
						0x80010,
						0x80118,
						0x70110,
						0x80070,
						0x80030,
						0x900c0,
						0x70108,
						0x80060,
						0x80020,
						0x900a0,
						0x80000,
						0x80080,
						0x80040,
						0x900e0,
						0x70104,
						0x80058,
						0x80018,
						0x90090,
						0x70114,
						0x80078,
						0x80038,
						0x900d0,
						0x7010c,
						0x80068,
						0x80028,
						0x900b0,
						0x80008,
						0x80088,
						0x80048,
						0x900f0,
						0x70102,
						0x80054,
						0x80014,
						0x8011c,
						0x70112,
						0x80074,
						0x80034,
						0x900c8,
						0x7010a,
						0x80064,
						0x80024,
						0x900a8,
						0x80004,
						0x80084,
						0x80044,
						0x900e8,
						0x70106,
						0x8005c,
						0x8001c,
						0x90098,
						0x70116,
						0x8007c,
						0x8003c,
						0x900d8,
						0x7010e,
						0x8006c,
						0x8002c,
						0x900b8,
						0x8000c,
						0x8008c,
						0x8004c,
						0x900f8,
						0x70101,
						0x80052,
						0x80012,
						0x8011a,
						0x70111,
						0x80072,
						0x80032,
						0x900c4,
						0x70109,
						0x80062,
						0x80022,
						0x900a4,
						0x80002,
						0x80082,
						0x80042,
						0x900e4,
						0x70105,
						0x8005a,
						0x8001a,
						0x90094,
						0x70115,
						0x8007a,
						0x8003a,
						0x900d4,
						0x7010d,
						0x8006a,
						0x8002a,
						0x900b4,
						0x8000a,
						0x8008a,
						0x8004a,
						0x900f4,
						0x70103,
						0x80056,
						0x80016,
						0x8011e,
						0x70113,
						0x80076,
						0x80036,
						0x900cc,
						0x7010b,
						0x80066,
						0x80026,
						0x900ac,
						0x80006,
						0x80086,
						0x80046,
						0x900ec,
						0x70107,
						0x8005e,
						0x8001e,
						0x9009c,
						0x70117,
						0x8007e,
						0x8003e,
						0x900dc,
						0x7010f,
						0x8006e,
						0x8002e,
						0x900bc,
						0x8000e,
						0x8008e,
						0x8004e,
						0x900fc,
						0x70100,
						0x80051,
						0x80011,
						0x80119,
						0x70110,
						0x80071,
						0x80031,
						0x900c2,
						0x70108,
						0x80061,
						0x80021,
						0x900a2,
						0x80001,
						0x80081,
						0x80041,
						0x900e2,
						0x70104,
						0x80059,
						0x80019,
						0x90092,
						0x70114,
						0x80079,
						0x80039,
						0x900d2,
						0x7010c,
						0x80069,
						0x80029,
						0x900b2,
						0x80009,
						0x80089,
						0x80049,
						0x900f2,
						0x70102,
						0x80055,
						0x80015,
						0x8011d,
						0x70112,
						0x80075,
						0x80035,
						0x900ca,
						0x7010a,
						0x80065,
						0x80025,
						0x900aa,
						0x80005,
						0x80085,
						0x80045,
						0x900ea,
						0x70106,
						0x8005d,
						0x8001d,
						0x9009a,
						0x70116,
						0x8007d,
						0x8003d,
						0x900da,
						0x7010e,
						0x8006d,
						0x8002d,
						0x900ba,
						0x8000d,
						0x8008d,
						0x8004d,
						0x900fa,
						0x70101,
						0x80053,
						0x80013,
						0x8011b,
						0x70111,
						0x80073,
						0x80033,
						0x900c6,
						0x70109,
						0x80063,
						0x80023,
						0x900a6,
						0x80003,
						0x80083,
						0x80043,
						0x900e6,
						0x70105,
						0x8005b,
						0x8001b,
						0x90096,
						0x70115,
						0x8007b,
						0x8003b,
						0x900d6,
						0x7010d,
						0x8006b,
						0x8002b,
						0x900b6,
						0x8000b,
						0x8008b,
						0x8004b,
						0x900f6,
						0x70103,
						0x80057,
						0x80017,
						0x8011f,
						0x70113,
						0x80077,
						0x80037,
						0x900ce,
						0x7010b,
						0x80067,
						0x80027,
						0x900ae,
						0x80007,
						0x80087,
						0x80047,
						0x900ee,
						0x70107,
						0x8005f,
						0x8001f,
						0x9009e,
						0x70117,
						0x8007f,
						0x8003f,
						0x900de,
						0x7010f,
						0x8006f,
						0x8002f,
						0x900be,
						0x8000f,
						0x8008f,
						0x8004f,
						0x900fe,
						0x70100,
						0x80050,
						0x80010,
						0x80118,
						0x70110,
						0x80070,
						0x80030,
						0x900c1,
						0x70108,
						0x80060,
						0x80020,
						0x900a1,
						0x80000,
						0x80080,
						0x80040,
						0x900e1,
						0x70104,
						0x80058,
						0x80018,
						0x90091,
						0x70114,
						0x80078,
						0x80038,
						0x900d1,
						0x7010c,
						0x80068,
						0x80028,
						0x900b1,
						0x80008,
						0x80088,
						0x80048,
						0x900f1,
						0x70102,
						0x80054,
						0x80014,
						0x8011c,
						0x70112,
						0x80074,
						0x80034,
						0x900c9,
						0x7010a,
						0x80064,
						0x80024,
						0x900a9,
						0x80004,
						0x80084,
						0x80044,
						0x900e9,
						0x70106,
						0x8005c,
						0x8001c,
						0x90099,
						0x70116,
						0x8007c,
						0x8003c,
						0x900d9,
						0x7010e,
						0x8006c,
						0x8002c,
						0x900b9,
						0x8000c,
						0x8008c,
						0x8004c,
						0x900f9,
						0x70101,
						0x80052,
						0x80012,
						0x8011a,
						0x70111,
						0x80072,
						0x80032,
						0x900c5,
						0x70109,
						0x80062,
						0x80022,
						0x900a5,
						0x80002,
						0x80082,
						0x80042,
						0x900e5,
						0x70105,
						0x8005a,
						0x8001a,
						0x90095,
						0x70115,
						0x8007a,
						0x8003a,
						0x900d5,
						0x7010d,
						0x8006a,
						0x8002a,
						0x900b5,
						0x8000a,
						0x8008a,
						0x8004a,
						0x900f5,
						0x70103,
						0x80056,
						0x80016,
						0x8011e,
						0x70113,
						0x80076,
						0x80036,
						0x900cd,
						0x7010b,
						0x80066,
						0x80026,
						0x900ad,
						0x80006,
						0x80086,
						0x80046,
						0x900ed,
						0x70107,
						0x8005e,
						0x8001e,
						0x9009d,
						0x70117,
						0x8007e,
						0x8003e,
						0x900dd,
						0x7010f,
						0x8006e,
						0x8002e,
						0x900bd,
						0x8000e,
						0x8008e,
						0x8004e,
						0x900fd,
						0x70100,
						0x80051,
						0x80011,
						0x80119,
						0x70110,
						0x80071,
						0x80031,
						0x900c3,
						0x70108,
						0x80061,
						0x80021,
						0x900a3,
						0x80001,
						0x80081,
						0x80041,
						0x900e3,
						0x70104,
						0x80059,
						0x80019,
						0x90093,
						0x70114,
						0x80079,
						0x80039,
						0x900d3,
						0x7010c,
						0x80069,
						0x80029,
						0x900b3,
						0x80009,
						0x80089,
						0x80049,
						0x900f3,
						0x70102,
						0x80055,
						0x80015,
						0x8011d,
						0x70112,
						0x80075,
						0x80035,
						0x900cb,
						0x7010a,
						0x80065,
						0x80025,
						0x900ab,
						0x80005,
						0x80085,
						0x80045,
						0x900eb,
						0x70106,
						0x8005d,
						0x8001d,
						0x9009b,
						0x70116,
						0x8007d,
						0x8003d,
						0x900db,
						0x7010e,
						0x8006d,
						0x8002d,
						0x900bb,
						0x8000d,
						0x8008d,
						0x8004d,
						0x900fb,
						0x70101,
						0x80053,
						0x80013,
						0x8011b,
						0x70111,
						0x80073,
						0x80033,
						0x900c7,
						0x70109,
						0x80063,
						0x80023,
						0x900a7,
						0x80003,
						0x80083,
						0x80043,
						0x900e7,
						0x70105,
						0x8005b,
						0x8001b,
						0x90097,
						0x70115,
						0x8007b,
						0x8003b,
						0x900d7,
						0x7010d,
						0x8006b,
						0x8002b,
						0x900b7,
						0x8000b,
						0x8008b,
						0x8004b,
						0x900f7,
						0x70103,
						0x80057,
						0x80017,
						0x8011f,
						0x70113,
						0x80077,
						0x80037,
						0x900cf,
						0x7010b,
						0x80067,
						0x80027,
						0x900af,
						0x80007,
						0x80087,
						0x80047,
						0x900ef,
						0x70107,
						0x8005f,
						0x8001f,
						0x9009f,
						0x70117,
						0x8007f,
						0x8003f,
						0x900df,
						0x7010f,
						0x8006f,
						0x8002f,
						0x900bf,
						0x8000f,
						0x8008f,
						0x8004f,
						0x900ff
					]),
					9
				];
				var fixedDistCodeTab = [
					new Int32Array([
						0x50000,
						0x50010,
						0x50008,
						0x50018,
						0x50004,
						0x50014,
						0x5000c,
						0x5001c,
						0x50002,
						0x50012,
						0x5000a,
						0x5001a,
						0x50006,
						0x50016,
						0x5000e,
						0x00000,
						0x50001,
						0x50011,
						0x50009,
						0x50019,
						0x50005,
						0x50015,
						0x5000d,
						0x5001d,
						0x50003,
						0x50013,
						0x5000b,
						0x5001b,
						0x50007,
						0x50017,
						0x5000f,
						0x00000
					]),
					5
				];
				function FlateStream(str, maybeLength) {
					this.str = str;
					this.dict = str.dict;
					var cmf = str.getByte();
					var flg = str.getByte();
					if (cmf === -1 || flg === -1) {
						error('Invalid header in flate stream: ' + cmf + ', ' + flg);
					}
					if ((cmf & 0x0f) !== 0x08) {
						error('Unknown compression method in flate stream: ' + cmf + ', ' + flg);
					}
					if (((cmf << 8) + flg) % 31 !== 0) {
						error('Bad FCHECK in flate stream: ' + cmf + ', ' + flg);
					}
					if (flg & 0x20) {
						error('FDICT bit set in flate stream: ' + cmf + ', ' + flg);
					}
					this.codeSize = 0;
					this.codeBuf = 0;
					DecodeStream.call(this, maybeLength);
				}
				FlateStream.prototype = Object.create(DecodeStream.prototype);
				FlateStream.prototype.getBits = function FlateStream_getBits(bits) {
					var str = this.str;
					var codeSize = this.codeSize;
					var codeBuf = this.codeBuf;
					var b;
					while (codeSize < bits) {
						if ((b = str.getByte()) === -1) {
							error('Bad encoding in flate stream');
						}
						codeBuf |= b << codeSize;
						codeSize += 8;
					}
					b = codeBuf & (1 << bits) - 1;
					this.codeBuf = codeBuf >> bits;
					this.codeSize = codeSize -= bits;
					return b;
				};
				FlateStream.prototype.getCode = function FlateStream_getCode(table) {
					var str = this.str;
					var codes = table[0];
					var maxLen = table[1];
					var codeSize = this.codeSize;
					var codeBuf = this.codeBuf;
					var b;
					while (codeSize < maxLen) {
						if ((b = str.getByte()) === -1) {
							break;
						}
						codeBuf |= b << codeSize;
						codeSize += 8;
					}
					var code = codes[codeBuf & (1 << maxLen) - 1];
					var codeLen = code >> 16;
					var codeVal = code & 0xffff;
					if (codeLen < 1 || codeSize < codeLen) {
						error('Bad encoding in flate stream');
					}
					this.codeBuf = codeBuf >> codeLen;
					this.codeSize = codeSize - codeLen;
					return codeVal;
				};
				FlateStream.prototype.generateHuffmanTable = function flateStreamGenerateHuffmanTable(lengths) {
					var n = lengths.length;
					var maxLen = 0;
					var i;
					for (i = 0; i < n; ++i) {
						if (lengths[i] > maxLen) {
							maxLen = lengths[i];
						}
					}
					var size = 1 << maxLen;
					var codes = new Int32Array(size);
					for (var len = 1, code = 0, skip = 2; len <= maxLen; ++len, code <<= 1, skip <<= 1) {
						for (var val = 0; val < n; ++val) {
							if (lengths[val] === len) {
								var code2 = 0;
								var t = code;
								for (i = 0; i < len; ++i) {
									code2 = code2 << 1 | t & 1;
									t >>= 1;
								}
								for (i = code2; i < size; i += skip) {
									codes[i] = len << 16 | val;
								}
								++code;
							}
						}
					}
					return [
						codes,
						maxLen
					];
				};
				FlateStream.prototype.readBlock = function FlateStream_readBlock() {
					var buffer, len;
					var str = this.str;
					var hdr = this.getBits(3);
					if (hdr & 1) {
						this.eof = true;
					}
					hdr >>= 1;
					if (hdr === 0) {
						var b;
						if ((b = str.getByte()) === -1) {
							error('Bad block header in flate stream');
						}
						var blockLen = b;
						if ((b = str.getByte()) === -1) {
							error('Bad block header in flate stream');
						}
						blockLen |= b << 8;
						if ((b = str.getByte()) === -1) {
							error('Bad block header in flate stream');
						}
						var check = b;
						if ((b = str.getByte()) === -1) {
							error('Bad block header in flate stream');
						}
						check |= b << 8;
						if (check !== (~blockLen & 0xffff) && (blockLen !== 0 || check !== 0)) {
							error('Bad uncompressed block length in flate stream');
						}
						this.codeBuf = 0;
						this.codeSize = 0;
						var bufferLength = this.bufferLength;
						buffer = this.ensureBuffer(bufferLength + blockLen);
						var end = bufferLength + blockLen;
						this.bufferLength = end;
						if (blockLen === 0) {
							if (str.peekByte() === -1) {
								this.eof = true;
							}
						} else {
							for (var n = bufferLength; n < end; ++n) {
								if ((b = str.getByte()) === -1) {
									this.eof = true;
									break;
								}
								buffer[n] = b;
							}
						}
						return;
					}
					var litCodeTable;
					var distCodeTable;
					if (hdr === 1) {
						litCodeTable = fixedLitCodeTab;
						distCodeTable = fixedDistCodeTab;
					} else if (hdr === 2) {
						var numLitCodes = this.getBits(5) + 257;
						var numDistCodes = this.getBits(5) + 1;
						var numCodeLenCodes = this.getBits(4) + 4;
						var codeLenCodeLengths = new Uint8Array(codeLenCodeMap.length);
						var i;
						for (i = 0; i < numCodeLenCodes; ++i) {
							codeLenCodeLengths[codeLenCodeMap[i]] = this.getBits(3);
						}
						var codeLenCodeTab = this.generateHuffmanTable(codeLenCodeLengths);
						len = 0;
						i = 0;
						var codes = numLitCodes + numDistCodes;
						var codeLengths = new Uint8Array(codes);
						var bitsLength, bitsOffset, what;
						while (i < codes) {
							var code = this.getCode(codeLenCodeTab);
							if (code === 16) {
								bitsLength = 2;
								bitsOffset = 3;
								what = len;
							} else if (code === 17) {
								bitsLength = 3;
								bitsOffset = 3;
								what = len = 0;
							} else if (code === 18) {
								bitsLength = 7;
								bitsOffset = 11;
								what = len = 0;
							} else {
								codeLengths[i++] = len = code;
								continue;
							}
							var repeatLength = this.getBits(bitsLength) + bitsOffset;
							while (repeatLength-- > 0) {
								codeLengths[i++] = what;
							}
						}
						litCodeTable = this.generateHuffmanTable(codeLengths.subarray(0, numLitCodes));
						distCodeTable = this.generateHuffmanTable(codeLengths.subarray(numLitCodes, codes));
					} else {
						error('Unknown block type in flate stream');
					}
					buffer = this.buffer;
					var limit = buffer ? buffer.length : 0;
					var pos = this.bufferLength;
					while (true) {
						var code1 = this.getCode(litCodeTable);
						if (code1 < 256) {
							if (pos + 1 >= limit) {
								buffer = this.ensureBuffer(pos + 1);
								limit = buffer.length;
							}
							buffer[pos++] = code1;
							continue;
						}
						if (code1 === 256) {
							this.bufferLength = pos;
							return;
						}
						code1 -= 257;
						code1 = lengthDecode[code1];
						var code2 = code1 >> 16;
						if (code2 > 0) {
							code2 = this.getBits(code2);
						}
						len = (code1 & 0xffff) + code2;
						code1 = this.getCode(distCodeTable);
						code1 = distDecode[code1];
						code2 = code1 >> 16;
						if (code2 > 0) {
							code2 = this.getBits(code2);
						}
						var dist = (code1 & 0xffff) + code2;
						if (pos + len >= limit) {
							buffer = this.ensureBuffer(pos + len);
							limit = buffer.length;
						}
						for (var k = 0; k < len; ++k, ++pos) {
							buffer[pos] = buffer[pos - dist];
						}
					}
				};
				return FlateStream;
			}();
			var PredictorStream = function PredictorStreamClosure() {
				function PredictorStream(str, maybeLength, params) {
					if (!isDict(params)) {
						return str;
					}
					var predictor = this.predictor = params.get('Predictor') || 1;
					if (predictor <= 1) {
						return str;
					}
					if (predictor !== 2 && (predictor < 10 || predictor > 15)) {
						error('Unsupported predictor: ' + predictor);
					}
					if (predictor === 2) {
						this.readBlock = this.readBlockTiff;
					} else {
						this.readBlock = this.readBlockPng;
					}
					this.str = str;
					this.dict = str.dict;
					var colors = this.colors = params.get('Colors') || 1;
					var bits = this.bits = params.get('BitsPerComponent') || 8;
					var columns = this.columns = params.get('Columns') || 1;
					this.pixBytes = colors * bits + 7 >> 3;
					this.rowBytes = columns * colors * bits + 7 >> 3;
					DecodeStream.call(this, maybeLength);
					return this;
				}
				PredictorStream.prototype = Object.create(DecodeStream.prototype);
				PredictorStream.prototype.readBlockTiff = function predictorStreamReadBlockTiff() {
					var rowBytes = this.rowBytes;
					var bufferLength = this.bufferLength;
					var buffer = this.ensureBuffer(bufferLength + rowBytes);
					var bits = this.bits;
					var colors = this.colors;
					var rawBytes = this.str.getBytes(rowBytes);
					this.eof = !rawBytes.length;
					if (this.eof) {
						return;
					}
					var inbuf = 0, outbuf = 0;
					var inbits = 0, outbits = 0;
					var pos = bufferLength;
					var i;
					if (bits === 1 && colors === 1) {
						for (i = 0; i < rowBytes; ++i) {
							var c = rawBytes[i] ^ inbuf;
							c ^= c >> 1;
							c ^= c >> 2;
							c ^= c >> 4;
							inbuf = (c & 1) << 7;
							buffer[pos++] = c;
						}
					} else if (bits === 8) {
						for (i = 0; i < colors; ++i) {
							buffer[pos++] = rawBytes[i];
						}
						for (; i < rowBytes; ++i) {
							buffer[pos] = buffer[pos - colors] + rawBytes[i];
							pos++;
						}
					} else {
						var compArray = new Uint8Array(colors + 1);
						var bitMask = (1 << bits) - 1;
						var j = 0, k = bufferLength;
						var columns = this.columns;
						for (i = 0; i < columns; ++i) {
							for (var kk = 0; kk < colors; ++kk) {
								if (inbits < bits) {
									inbuf = inbuf << 8 | rawBytes[j++] & 0xFF;
									inbits += 8;
								}
								compArray[kk] = compArray[kk] + (inbuf >> inbits - bits) & bitMask;
								inbits -= bits;
								outbuf = outbuf << bits | compArray[kk];
								outbits += bits;
								if (outbits >= 8) {
									buffer[k++] = outbuf >> outbits - 8 & 0xFF;
									outbits -= 8;
								}
							}
						}
						if (outbits > 0) {
							buffer[k++] = (outbuf << 8 - outbits) + (inbuf & (1 << 8 - outbits) - 1);
						}
					}
					this.bufferLength += rowBytes;
				};
				PredictorStream.prototype.readBlockPng = function predictorStreamReadBlockPng() {
					var rowBytes = this.rowBytes;
					var pixBytes = this.pixBytes;
					var predictor = this.str.getByte();
					var rawBytes = this.str.getBytes(rowBytes);
					this.eof = !rawBytes.length;
					if (this.eof) {
						return;
					}
					var bufferLength = this.bufferLength;
					var buffer = this.ensureBuffer(bufferLength + rowBytes);
					var prevRow = buffer.subarray(bufferLength - rowBytes, bufferLength);
					if (prevRow.length === 0) {
						prevRow = new Uint8Array(rowBytes);
					}
					var i, j = bufferLength, up, c;
					switch (predictor) {
						case 0:
							for (i = 0; i < rowBytes; ++i) {
								buffer[j++] = rawBytes[i];
							}
							break;
						case 1:
							for (i = 0; i < pixBytes; ++i) {
								buffer[j++] = rawBytes[i];
							}
							for (; i < rowBytes; ++i) {
								buffer[j] = buffer[j - pixBytes] + rawBytes[i] & 0xFF;
								j++;
							}
							break;
						case 2:
							for (i = 0; i < rowBytes; ++i) {
								buffer[j++] = prevRow[i] + rawBytes[i] & 0xFF;
							}
							break;
						case 3:
							for (i = 0; i < pixBytes; ++i) {
								buffer[j++] = (prevRow[i] >> 1) + rawBytes[i];
							}
							for (; i < rowBytes; ++i) {
								buffer[j] = (prevRow[i] + buffer[j - pixBytes] >> 1) + rawBytes[i] & 0xFF;
								j++;
							}
							break;
						case 4:
							for (i = 0; i < pixBytes; ++i) {
								up = prevRow[i];
								c = rawBytes[i];
								buffer[j++] = up + c;
							}
							for (; i < rowBytes; ++i) {
								up = prevRow[i];
								var upLeft = prevRow[i - pixBytes];
								var left = buffer[j - pixBytes];
								var p = left + up - upLeft;
								var pa = p - left;
								if (pa < 0) {
									pa = -pa;
								}
								var pb = p - up;
								if (pb < 0) {
									pb = -pb;
								}
								var pc = p - upLeft;
								if (pc < 0) {
									pc = -pc;
								}
								c = rawBytes[i];
								if (pa <= pb && pa <= pc) {
									buffer[j++] = left + c;
								} else if (pb <= pc) {
									buffer[j++] = up + c;
								} else {
									buffer[j++] = upLeft + c;
								}
							}
							break;
						default:
							error('Unsupported predictor: ' + predictor);
					}
					this.bufferLength += rowBytes;
				};
				return PredictorStream;
			}();
			var JpegStream = function JpegStreamClosure() {
				function JpegStream(stream, maybeLength, dict, params) {
					var ch;
					while ((ch = stream.getByte()) !== -1) {
						if (ch === 0xFF) {
							stream.skip(-1);
							break;
						}
					}
					this.stream = stream;
					this.maybeLength = maybeLength;
					this.dict = dict;
					this.params = params;
					DecodeStream.call(this, maybeLength);
				}
				JpegStream.prototype = Object.create(DecodeStream.prototype);
				Object.defineProperty(JpegStream.prototype, 'bytes', {
					get: function JpegStream_bytes() {
						return shadow(this, 'bytes', this.stream.getBytes(this.maybeLength));
					},
					configurable: true
				});
				JpegStream.prototype.ensureBuffer = function JpegStream_ensureBuffer(req) {
					if (this.bufferLength) {
						return;
					}
					var jpegImage = new JpegImage();
					var decodeArr = this.dict.getArray('Decode', 'D');
					if (this.forceRGB && isArray(decodeArr)) {
						var bitsPerComponent = this.dict.get('BitsPerComponent') || 8;
						var decodeArrLength = decodeArr.length;
						var transform = new Int32Array(decodeArrLength);
						var transformNeeded = false;
						var maxValue = (1 << bitsPerComponent) - 1;
						for (var i = 0; i < decodeArrLength; i += 2) {
							transform[i] = (decodeArr[i + 1] - decodeArr[i]) * 256 | 0;
							transform[i + 1] = decodeArr[i] * maxValue | 0;
							if (transform[i] !== 256 || transform[i + 1] !== 0) {
								transformNeeded = true;
							}
						}
						if (transformNeeded) {
							jpegImage.decodeTransform = transform;
						}
					}
					if (isDict(this.params)) {
						var colorTransform = this.params.get('ColorTransform');
						if (isInt(colorTransform)) {
							jpegImage.colorTransform = colorTransform;
						}
					}
					jpegImage.parse(this.bytes);
					var data = jpegImage.getData(this.drawWidth, this.drawHeight, this.forceRGB);
					this.buffer = data;
					this.bufferLength = data.length;
					this.eof = true;
				};
				JpegStream.prototype.getBytes = function JpegStream_getBytes(length) {
					this.ensureBuffer();
					return this.buffer;
				};
				JpegStream.prototype.getIR = function JpegStream_getIR(forceDataSchema) {
					return createObjectURL(this.bytes, 'image/jpeg', forceDataSchema);
				};
				return JpegStream;
			}();
			var JpxStream = function JpxStreamClosure() {
				function JpxStream(stream, maybeLength, dict, params) {
					this.stream = stream;
					this.maybeLength = maybeLength;
					this.dict = dict;
					this.params = params;
					DecodeStream.call(this, maybeLength);
				}
				JpxStream.prototype = Object.create(DecodeStream.prototype);
				Object.defineProperty(JpxStream.prototype, 'bytes', {
					get: function JpxStream_bytes() {
						return shadow(this, 'bytes', this.stream.getBytes(this.maybeLength));
					},
					configurable: true
				});
				JpxStream.prototype.ensureBuffer = function JpxStream_ensureBuffer(req) {
					if (this.bufferLength) {
						return;
					}
					var jpxImage = new JpxImage();
					jpxImage.parse(this.bytes);
					var width = jpxImage.width;
					var height = jpxImage.height;
					var componentsCount = jpxImage.componentsCount;
					var tileCount = jpxImage.tiles.length;
					if (tileCount === 1) {
						this.buffer = jpxImage.tiles[0].items;
					} else {
						var data = new Uint8Array(width * height * componentsCount);
						for (var k = 0; k < tileCount; k++) {
							var tileComponents = jpxImage.tiles[k];
							var tileWidth = tileComponents.width;
							var tileHeight = tileComponents.height;
							var tileLeft = tileComponents.left;
							var tileTop = tileComponents.top;
							var src = tileComponents.items;
							var srcPosition = 0;
							var dataPosition = (width * tileTop + tileLeft) * componentsCount;
							var imgRowSize = width * componentsCount;
							var tileRowSize = tileWidth * componentsCount;
							for (var j = 0; j < tileHeight; j++) {
								var rowBytes = src.subarray(srcPosition, srcPosition + tileRowSize);
								data.set(rowBytes, dataPosition);
								srcPosition += tileRowSize;
								dataPosition += imgRowSize;
							}
						}
						this.buffer = data;
					}
					this.bufferLength = this.buffer.length;
					this.eof = true;
				};
				return JpxStream;
			}();
			var Jbig2Stream = function Jbig2StreamClosure() {
				function Jbig2Stream(stream, maybeLength, dict, params) {
					this.stream = stream;
					this.maybeLength = maybeLength;
					this.dict = dict;
					this.params = params;
					DecodeStream.call(this, maybeLength);
				}
				Jbig2Stream.prototype = Object.create(DecodeStream.prototype);
				Object.defineProperty(Jbig2Stream.prototype, 'bytes', {
					get: function Jbig2Stream_bytes() {
						return shadow(this, 'bytes', this.stream.getBytes(this.maybeLength));
					},
					configurable: true
				});
				Jbig2Stream.prototype.ensureBuffer = function Jbig2Stream_ensureBuffer(req) {
					if (this.bufferLength) {
						return;
					}
					var jbig2Image = new Jbig2Image();
					var chunks = [];
					if (isDict(this.params)) {
						var globalsStream = this.params.get('JBIG2Globals');
						if (isStream(globalsStream)) {
							var globals = globalsStream.getBytes();
							chunks.push({
								data: globals,
								start: 0,
								end: globals.length
							});
						}
					}
					chunks.push({
						data: this.bytes,
						start: 0,
						end: this.bytes.length
					});
					var data = jbig2Image.parseChunks(chunks);
					var dataLength = data.length;
					for (var i = 0; i < dataLength; i++) {
						data[i] ^= 0xFF;
					}
					this.buffer = data;
					this.bufferLength = dataLength;
					this.eof = true;
				};
				return Jbig2Stream;
			}();
			var DecryptStream = function DecryptStreamClosure() {
				function DecryptStream(str, maybeLength, decrypt) {
					this.str = str;
					this.dict = str.dict;
					this.decrypt = decrypt;
					this.nextChunk = null;
					this.initialized = false;
					DecodeStream.call(this, maybeLength);
				}
				var chunkSize = 512;
				DecryptStream.prototype = Object.create(DecodeStream.prototype);
				DecryptStream.prototype.readBlock = function DecryptStream_readBlock() {
					var chunk;
					if (this.initialized) {
						chunk = this.nextChunk;
					} else {
						chunk = this.str.getBytes(chunkSize);
						this.initialized = true;
					}
					if (!chunk || chunk.length === 0) {
						this.eof = true;
						return;
					}
					this.nextChunk = this.str.getBytes(chunkSize);
					var hasMoreData = this.nextChunk && this.nextChunk.length > 0;
					var decrypt = this.decrypt;
					chunk = decrypt(chunk, !hasMoreData);
					var bufferLength = this.bufferLength;
					var i, n = chunk.length;
					var buffer = this.ensureBuffer(bufferLength + n);
					for (i = 0; i < n; i++) {
						buffer[bufferLength++] = chunk[i];
					}
					this.bufferLength = bufferLength;
				};
				return DecryptStream;
			}();
			var Ascii85Stream = function Ascii85StreamClosure() {
				function Ascii85Stream(str, maybeLength) {
					this.str = str;
					this.dict = str.dict;
					this.input = new Uint8Array(5);
					if (maybeLength) {
						maybeLength = 0.8 * maybeLength;
					}
					DecodeStream.call(this, maybeLength);
				}
				Ascii85Stream.prototype = Object.create(DecodeStream.prototype);
				Ascii85Stream.prototype.readBlock = function Ascii85Stream_readBlock() {
					var TILDA_CHAR = 0x7E;
					var Z_LOWER_CHAR = 0x7A;
					var EOF = -1;
					var str = this.str;
					var c = str.getByte();
					while (isSpace(c)) {
						c = str.getByte();
					}
					if (c === EOF || c === TILDA_CHAR) {
						this.eof = true;
						return;
					}
					var bufferLength = this.bufferLength, buffer;
					var i;
					if (c === Z_LOWER_CHAR) {
						buffer = this.ensureBuffer(bufferLength + 4);
						for (i = 0; i < 4; ++i) {
							buffer[bufferLength + i] = 0;
						}
						this.bufferLength += 4;
					} else {
						var input = this.input;
						input[0] = c;
						for (i = 1; i < 5; ++i) {
							c = str.getByte();
							while (isSpace(c)) {
								c = str.getByte();
							}
							input[i] = c;
							if (c === EOF || c === TILDA_CHAR) {
								break;
							}
						}
						buffer = this.ensureBuffer(bufferLength + i - 1);
						this.bufferLength += i - 1;
						if (i < 5) {
							for (; i < 5; ++i) {
								input[i] = 0x21 + 84;
							}
							this.eof = true;
						}
						var t = 0;
						for (i = 0; i < 5; ++i) {
							t = t * 85 + (input[i] - 0x21);
						}
						for (i = 3; i >= 0; --i) {
							buffer[bufferLength + i] = t & 0xFF;
							t >>= 8;
						}
					}
				};
				return Ascii85Stream;
			}();
			var AsciiHexStream = function AsciiHexStreamClosure() {
				function AsciiHexStream(str, maybeLength) {
					this.str = str;
					this.dict = str.dict;
					this.firstDigit = -1;
					if (maybeLength) {
						maybeLength = 0.5 * maybeLength;
					}
					DecodeStream.call(this, maybeLength);
				}
				AsciiHexStream.prototype = Object.create(DecodeStream.prototype);
				AsciiHexStream.prototype.readBlock = function AsciiHexStream_readBlock() {
					var UPSTREAM_BLOCK_SIZE = 8000;
					var bytes = this.str.getBytes(UPSTREAM_BLOCK_SIZE);
					if (!bytes.length) {
						this.eof = true;
						return;
					}
					var maxDecodeLength = bytes.length + 1 >> 1;
					var buffer = this.ensureBuffer(this.bufferLength + maxDecodeLength);
					var bufferLength = this.bufferLength;
					var firstDigit = this.firstDigit;
					for (var i = 0, ii = bytes.length; i < ii; i++) {
						var ch = bytes[i], digit;
						if (ch >= 0x30 && ch <= 0x39) {
							digit = ch & 0x0F;
						} else if (ch >= 0x41 && ch <= 0x46 || ch >= 0x61 && ch <= 0x66) {
							digit = (ch & 0x0F) + 9;
						} else if (ch === 0x3E) {
							this.eof = true;
							break;
						} else {
							continue;
						}
						if (firstDigit < 0) {
							firstDigit = digit;
						} else {
							buffer[bufferLength++] = firstDigit << 4 | digit;
							firstDigit = -1;
						}
					}
					if (firstDigit >= 0 && this.eof) {
						buffer[bufferLength++] = firstDigit << 4;
						firstDigit = -1;
					}
					this.firstDigit = firstDigit;
					this.bufferLength = bufferLength;
				};
				return AsciiHexStream;
			}();
			var RunLengthStream = function RunLengthStreamClosure() {
				function RunLengthStream(str, maybeLength) {
					this.str = str;
					this.dict = str.dict;
					DecodeStream.call(this, maybeLength);
				}
				RunLengthStream.prototype = Object.create(DecodeStream.prototype);
				RunLengthStream.prototype.readBlock = function RunLengthStream_readBlock() {
					var repeatHeader = this.str.getBytes(2);
					if (!repeatHeader || repeatHeader.length < 2 || repeatHeader[0] === 128) {
						this.eof = true;
						return;
					}
					var buffer;
					var bufferLength = this.bufferLength;
					var n = repeatHeader[0];
					if (n < 128) {
						buffer = this.ensureBuffer(bufferLength + n + 1);
						buffer[bufferLength++] = repeatHeader[1];
						if (n > 0) {
							var source = this.str.getBytes(n);
							buffer.set(source, bufferLength);
							bufferLength += n;
						}
					} else {
						n = 257 - n;
						var b = repeatHeader[1];
						buffer = this.ensureBuffer(bufferLength + n + 1);
						for (var i = 0; i < n; i++) {
							buffer[bufferLength++] = b;
						}
					}
					this.bufferLength = bufferLength;
				};
				return RunLengthStream;
			}();
			var CCITTFaxStream = function CCITTFaxStreamClosure() {
				var ccittEOL = -2;
				var ccittEOF = -1;
				var twoDimPass = 0;
				var twoDimHoriz = 1;
				var twoDimVert0 = 2;
				var twoDimVertR1 = 3;
				var twoDimVertL1 = 4;
				var twoDimVertR2 = 5;
				var twoDimVertL2 = 6;
				var twoDimVertR3 = 7;
				var twoDimVertL3 = 8;
				var twoDimTable = [
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						7,
						twoDimVertL3
					],
					[
						7,
						twoDimVertR3
					],
					[
						6,
						twoDimVertL2
					],
					[
						6,
						twoDimVertL2
					],
					[
						6,
						twoDimVertR2
					],
					[
						6,
						twoDimVertR2
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						4,
						twoDimPass
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimHoriz
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertL1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						3,
						twoDimVertR1
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					],
					[
						1,
						twoDimVert0
					]
				];
				var whiteTable1 = [
					[
						-1,
						-1
					],
					[
						12,
						ccittEOL
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						11,
						1792
					],
					[
						11,
						1792
					],
					[
						12,
						1984
					],
					[
						12,
						2048
					],
					[
						12,
						2112
					],
					[
						12,
						2176
					],
					[
						12,
						2240
					],
					[
						12,
						2304
					],
					[
						11,
						1856
					],
					[
						11,
						1856
					],
					[
						11,
						1920
					],
					[
						11,
						1920
					],
					[
						12,
						2368
					],
					[
						12,
						2432
					],
					[
						12,
						2496
					],
					[
						12,
						2560
					]
				];
				var whiteTable2 = [
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						8,
						29
					],
					[
						8,
						29
					],
					[
						8,
						30
					],
					[
						8,
						30
					],
					[
						8,
						45
					],
					[
						8,
						45
					],
					[
						8,
						46
					],
					[
						8,
						46
					],
					[
						7,
						22
					],
					[
						7,
						22
					],
					[
						7,
						22
					],
					[
						7,
						22
					],
					[
						7,
						23
					],
					[
						7,
						23
					],
					[
						7,
						23
					],
					[
						7,
						23
					],
					[
						8,
						47
					],
					[
						8,
						47
					],
					[
						8,
						48
					],
					[
						8,
						48
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						6,
						13
					],
					[
						7,
						20
					],
					[
						7,
						20
					],
					[
						7,
						20
					],
					[
						7,
						20
					],
					[
						8,
						33
					],
					[
						8,
						33
					],
					[
						8,
						34
					],
					[
						8,
						34
					],
					[
						8,
						35
					],
					[
						8,
						35
					],
					[
						8,
						36
					],
					[
						8,
						36
					],
					[
						8,
						37
					],
					[
						8,
						37
					],
					[
						8,
						38
					],
					[
						8,
						38
					],
					[
						7,
						19
					],
					[
						7,
						19
					],
					[
						7,
						19
					],
					[
						7,
						19
					],
					[
						8,
						31
					],
					[
						8,
						31
					],
					[
						8,
						32
					],
					[
						8,
						32
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						1
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						6,
						12
					],
					[
						8,
						53
					],
					[
						8,
						53
					],
					[
						8,
						54
					],
					[
						8,
						54
					],
					[
						7,
						26
					],
					[
						7,
						26
					],
					[
						7,
						26
					],
					[
						7,
						26
					],
					[
						8,
						39
					],
					[
						8,
						39
					],
					[
						8,
						40
					],
					[
						8,
						40
					],
					[
						8,
						41
					],
					[
						8,
						41
					],
					[
						8,
						42
					],
					[
						8,
						42
					],
					[
						8,
						43
					],
					[
						8,
						43
					],
					[
						8,
						44
					],
					[
						8,
						44
					],
					[
						7,
						21
					],
					[
						7,
						21
					],
					[
						7,
						21
					],
					[
						7,
						21
					],
					[
						7,
						28
					],
					[
						7,
						28
					],
					[
						7,
						28
					],
					[
						7,
						28
					],
					[
						8,
						61
					],
					[
						8,
						61
					],
					[
						8,
						62
					],
					[
						8,
						62
					],
					[
						8,
						63
					],
					[
						8,
						63
					],
					[
						8,
						0
					],
					[
						8,
						0
					],
					[
						8,
						320
					],
					[
						8,
						320
					],
					[
						8,
						384
					],
					[
						8,
						384
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						10
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						5,
						11
					],
					[
						7,
						27
					],
					[
						7,
						27
					],
					[
						7,
						27
					],
					[
						7,
						27
					],
					[
						8,
						59
					],
					[
						8,
						59
					],
					[
						8,
						60
					],
					[
						8,
						60
					],
					[
						9,
						1472
					],
					[
						9,
						1536
					],
					[
						9,
						1600
					],
					[
						9,
						1728
					],
					[
						7,
						18
					],
					[
						7,
						18
					],
					[
						7,
						18
					],
					[
						7,
						18
					],
					[
						7,
						24
					],
					[
						7,
						24
					],
					[
						7,
						24
					],
					[
						7,
						24
					],
					[
						8,
						49
					],
					[
						8,
						49
					],
					[
						8,
						50
					],
					[
						8,
						50
					],
					[
						8,
						51
					],
					[
						8,
						51
					],
					[
						8,
						52
					],
					[
						8,
						52
					],
					[
						7,
						25
					],
					[
						7,
						25
					],
					[
						7,
						25
					],
					[
						7,
						25
					],
					[
						8,
						55
					],
					[
						8,
						55
					],
					[
						8,
						56
					],
					[
						8,
						56
					],
					[
						8,
						57
					],
					[
						8,
						57
					],
					[
						8,
						58
					],
					[
						8,
						58
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						192
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						6,
						1664
					],
					[
						8,
						448
					],
					[
						8,
						448
					],
					[
						8,
						512
					],
					[
						8,
						512
					],
					[
						9,
						704
					],
					[
						9,
						768
					],
					[
						8,
						640
					],
					[
						8,
						640
					],
					[
						8,
						576
					],
					[
						8,
						576
					],
					[
						9,
						832
					],
					[
						9,
						896
					],
					[
						9,
						960
					],
					[
						9,
						1024
					],
					[
						9,
						1088
					],
					[
						9,
						1152
					],
					[
						9,
						1216
					],
					[
						9,
						1280
					],
					[
						9,
						1344
					],
					[
						9,
						1408
					],
					[
						7,
						256
					],
					[
						7,
						256
					],
					[
						7,
						256
					],
					[
						7,
						256
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						2
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						4,
						3
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						128
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						8
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						5,
						9
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						16
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						6,
						17
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						4
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						14
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						6,
						15
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						5,
						64
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					],
					[
						4,
						7
					]
				];
				var blackTable1 = [
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						12,
						ccittEOL
					],
					[
						12,
						ccittEOL
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						11,
						1792
					],
					[
						11,
						1792
					],
					[
						11,
						1792
					],
					[
						11,
						1792
					],
					[
						12,
						1984
					],
					[
						12,
						1984
					],
					[
						12,
						2048
					],
					[
						12,
						2048
					],
					[
						12,
						2112
					],
					[
						12,
						2112
					],
					[
						12,
						2176
					],
					[
						12,
						2176
					],
					[
						12,
						2240
					],
					[
						12,
						2240
					],
					[
						12,
						2304
					],
					[
						12,
						2304
					],
					[
						11,
						1856
					],
					[
						11,
						1856
					],
					[
						11,
						1856
					],
					[
						11,
						1856
					],
					[
						11,
						1920
					],
					[
						11,
						1920
					],
					[
						11,
						1920
					],
					[
						11,
						1920
					],
					[
						12,
						2368
					],
					[
						12,
						2368
					],
					[
						12,
						2432
					],
					[
						12,
						2432
					],
					[
						12,
						2496
					],
					[
						12,
						2496
					],
					[
						12,
						2560
					],
					[
						12,
						2560
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						10,
						18
					],
					[
						12,
						52
					],
					[
						12,
						52
					],
					[
						13,
						640
					],
					[
						13,
						704
					],
					[
						13,
						768
					],
					[
						13,
						832
					],
					[
						12,
						55
					],
					[
						12,
						55
					],
					[
						12,
						56
					],
					[
						12,
						56
					],
					[
						13,
						1280
					],
					[
						13,
						1344
					],
					[
						13,
						1408
					],
					[
						13,
						1472
					],
					[
						12,
						59
					],
					[
						12,
						59
					],
					[
						12,
						60
					],
					[
						12,
						60
					],
					[
						13,
						1536
					],
					[
						13,
						1600
					],
					[
						11,
						24
					],
					[
						11,
						24
					],
					[
						11,
						24
					],
					[
						11,
						24
					],
					[
						11,
						25
					],
					[
						11,
						25
					],
					[
						11,
						25
					],
					[
						11,
						25
					],
					[
						13,
						1664
					],
					[
						13,
						1728
					],
					[
						12,
						320
					],
					[
						12,
						320
					],
					[
						12,
						384
					],
					[
						12,
						384
					],
					[
						12,
						448
					],
					[
						12,
						448
					],
					[
						13,
						512
					],
					[
						13,
						576
					],
					[
						12,
						53
					],
					[
						12,
						53
					],
					[
						12,
						54
					],
					[
						12,
						54
					],
					[
						13,
						896
					],
					[
						13,
						960
					],
					[
						13,
						1024
					],
					[
						13,
						1088
					],
					[
						13,
						1152
					],
					[
						13,
						1216
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					],
					[
						10,
						64
					]
				];
				var blackTable2 = [
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						8,
						13
					],
					[
						11,
						23
					],
					[
						11,
						23
					],
					[
						12,
						50
					],
					[
						12,
						51
					],
					[
						12,
						44
					],
					[
						12,
						45
					],
					[
						12,
						46
					],
					[
						12,
						47
					],
					[
						12,
						57
					],
					[
						12,
						58
					],
					[
						12,
						61
					],
					[
						12,
						256
					],
					[
						10,
						16
					],
					[
						10,
						16
					],
					[
						10,
						16
					],
					[
						10,
						16
					],
					[
						10,
						17
					],
					[
						10,
						17
					],
					[
						10,
						17
					],
					[
						10,
						17
					],
					[
						12,
						48
					],
					[
						12,
						49
					],
					[
						12,
						62
					],
					[
						12,
						63
					],
					[
						12,
						30
					],
					[
						12,
						31
					],
					[
						12,
						32
					],
					[
						12,
						33
					],
					[
						12,
						40
					],
					[
						12,
						41
					],
					[
						11,
						22
					],
					[
						11,
						22
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						8,
						14
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						10
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						7,
						11
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						9,
						15
					],
					[
						12,
						128
					],
					[
						12,
						192
					],
					[
						12,
						26
					],
					[
						12,
						27
					],
					[
						12,
						28
					],
					[
						12,
						29
					],
					[
						11,
						19
					],
					[
						11,
						19
					],
					[
						11,
						20
					],
					[
						11,
						20
					],
					[
						12,
						34
					],
					[
						12,
						35
					],
					[
						12,
						36
					],
					[
						12,
						37
					],
					[
						12,
						38
					],
					[
						12,
						39
					],
					[
						11,
						21
					],
					[
						11,
						21
					],
					[
						12,
						42
					],
					[
						12,
						43
					],
					[
						10,
						0
					],
					[
						10,
						0
					],
					[
						10,
						0
					],
					[
						10,
						0
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					],
					[
						7,
						12
					]
				];
				var blackTable3 = [
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						-1,
						-1
					],
					[
						6,
						9
					],
					[
						6,
						8
					],
					[
						5,
						7
					],
					[
						5,
						7
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						6
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						4,
						5
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						1
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						3,
						4
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						3
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					],
					[
						2,
						2
					]
				];
				function CCITTFaxStream(str, maybeLength, params) {
					this.str = str;
					this.dict = str.dict;
					params = params || Dict.empty;
					this.encoding = params.get('K') || 0;
					this.eoline = params.get('EndOfLine') || false;
					this.byteAlign = params.get('EncodedByteAlign') || false;
					this.columns = params.get('Columns') || 1728;
					this.rows = params.get('Rows') || 0;
					var eoblock = params.get('EndOfBlock');
					if (eoblock === null || eoblock === undefined) {
						eoblock = true;
					}
					this.eoblock = eoblock;
					this.black = params.get('BlackIs1') || false;
					this.codingLine = new Uint32Array(this.columns + 1);
					this.refLine = new Uint32Array(this.columns + 2);
					this.codingLine[0] = this.columns;
					this.codingPos = 0;
					this.row = 0;
					this.nextLine2D = this.encoding < 0;
					this.inputBits = 0;
					this.inputBuf = 0;
					this.outputBits = 0;
					var code1;
					while ((code1 = this.lookBits(12)) === 0) {
						this.eatBits(1);
					}
					if (code1 === 1) {
						this.eatBits(12);
					}
					if (this.encoding > 0) {
						this.nextLine2D = !this.lookBits(1);
						this.eatBits(1);
					}
					DecodeStream.call(this, maybeLength);
				}
				CCITTFaxStream.prototype = Object.create(DecodeStream.prototype);
				CCITTFaxStream.prototype.readBlock = function CCITTFaxStream_readBlock() {
					while (!this.eof) {
						var c = this.lookChar();
						this.ensureBuffer(this.bufferLength + 1);
						this.buffer[this.bufferLength++] = c;
					}
				};
				CCITTFaxStream.prototype.addPixels = function ccittFaxStreamAddPixels(a1, blackPixels) {
					var codingLine = this.codingLine;
					var codingPos = this.codingPos;
					if (a1 > codingLine[codingPos]) {
						if (a1 > this.columns) {
							info('row is wrong length');
							this.err = true;
							a1 = this.columns;
						}
						if (codingPos & 1 ^ blackPixels) {
							++codingPos;
						}
						codingLine[codingPos] = a1;
					}
					this.codingPos = codingPos;
				};
				CCITTFaxStream.prototype.addPixelsNeg = function ccittFaxStreamAddPixelsNeg(a1, blackPixels) {
					var codingLine = this.codingLine;
					var codingPos = this.codingPos;
					if (a1 > codingLine[codingPos]) {
						if (a1 > this.columns) {
							info('row is wrong length');
							this.err = true;
							a1 = this.columns;
						}
						if (codingPos & 1 ^ blackPixels) {
							++codingPos;
						}
						codingLine[codingPos] = a1;
					} else if (a1 < codingLine[codingPos]) {
						if (a1 < 0) {
							info('invalid code');
							this.err = true;
							a1 = 0;
						}
						while (codingPos > 0 && a1 < codingLine[codingPos - 1]) {
							--codingPos;
						}
						codingLine[codingPos] = a1;
					}
					this.codingPos = codingPos;
				};
				CCITTFaxStream.prototype.lookChar = function CCITTFaxStream_lookChar() {
					var refLine = this.refLine;
					var codingLine = this.codingLine;
					var columns = this.columns;
					var refPos, blackPixels, bits, i;
					if (this.outputBits === 0) {
						if (this.eof) {
							return null;
						}
						this.err = false;
						var code1, code2, code3;
						if (this.nextLine2D) {
							for (i = 0; codingLine[i] < columns; ++i) {
								refLine[i] = codingLine[i];
							}
							refLine[i++] = columns;
							refLine[i] = columns;
							codingLine[0] = 0;
							this.codingPos = 0;
							refPos = 0;
							blackPixels = 0;
							while (codingLine[this.codingPos] < columns) {
								code1 = this.getTwoDimCode();
								switch (code1) {
									case twoDimPass:
										this.addPixels(refLine[refPos + 1], blackPixels);
										if (refLine[refPos + 1] < columns) {
											refPos += 2;
										}
										break;
									case twoDimHoriz:
										code1 = code2 = 0;
										if (blackPixels) {
											do {
												code1 += code3 = this.getBlackCode();
											} while (code3 >= 64);
											do {
												code2 += code3 = this.getWhiteCode();
											} while (code3 >= 64);
										} else {
											do {
												code1 += code3 = this.getWhiteCode();
											} while (code3 >= 64);
											do {
												code2 += code3 = this.getBlackCode();
											} while (code3 >= 64);
										}
										this.addPixels(codingLine[this.codingPos] + code1, blackPixels);
										if (codingLine[this.codingPos] < columns) {
											this.addPixels(codingLine[this.codingPos] + code2, blackPixels ^ 1);
										}
										while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
											refPos += 2;
										}
										break;
									case twoDimVertR3:
										this.addPixels(refLine[refPos] + 3, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											++refPos;
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVertR2:
										this.addPixels(refLine[refPos] + 2, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											++refPos;
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVertR1:
										this.addPixels(refLine[refPos] + 1, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											++refPos;
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVert0:
										this.addPixels(refLine[refPos], blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											++refPos;
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVertL3:
										this.addPixelsNeg(refLine[refPos] - 3, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											if (refPos > 0) {
												--refPos;
											} else {
												++refPos;
											}
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVertL2:
										this.addPixelsNeg(refLine[refPos] - 2, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											if (refPos > 0) {
												--refPos;
											} else {
												++refPos;
											}
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case twoDimVertL1:
										this.addPixelsNeg(refLine[refPos] - 1, blackPixels);
										blackPixels ^= 1;
										if (codingLine[this.codingPos] < columns) {
											if (refPos > 0) {
												--refPos;
											} else {
												++refPos;
											}
											while (refLine[refPos] <= codingLine[this.codingPos] && refLine[refPos] < columns) {
												refPos += 2;
											}
										}
										break;
									case ccittEOF:
										this.addPixels(columns, 0);
										this.eof = true;
										break;
									default:
										info('bad 2d code');
										this.addPixels(columns, 0);
										this.err = true;
								}
							}
						} else {
							codingLine[0] = 0;
							this.codingPos = 0;
							blackPixels = 0;
							while (codingLine[this.codingPos] < columns) {
								code1 = 0;
								if (blackPixels) {
									do {
										code1 += code3 = this.getBlackCode();
									} while (code3 >= 64);
								} else {
									do {
										code1 += code3 = this.getWhiteCode();
									} while (code3 >= 64);
								}
								this.addPixels(codingLine[this.codingPos] + code1, blackPixels);
								blackPixels ^= 1;
							}
						}
						var gotEOL = false;
						if (this.byteAlign) {
							this.inputBits &= ~7;
						}
						if (!this.eoblock && this.row === this.rows - 1) {
							this.eof = true;
						} else {
							code1 = this.lookBits(12);
							if (this.eoline) {
								while (code1 !== ccittEOF && code1 !== 1) {
									this.eatBits(1);
									code1 = this.lookBits(12);
								}
							} else {
								while (code1 === 0) {
									this.eatBits(1);
									code1 = this.lookBits(12);
								}
							}
							if (code1 === 1) {
								this.eatBits(12);
								gotEOL = true;
							} else if (code1 === ccittEOF) {
								this.eof = true;
							}
						}
						if (!this.eof && this.encoding > 0) {
							this.nextLine2D = !this.lookBits(1);
							this.eatBits(1);
						}
						if (this.eoblock && gotEOL && this.byteAlign) {
							code1 = this.lookBits(12);
							if (code1 === 1) {
								this.eatBits(12);
								if (this.encoding > 0) {
									this.lookBits(1);
									this.eatBits(1);
								}
								if (this.encoding >= 0) {
									for (i = 0; i < 4; ++i) {
										code1 = this.lookBits(12);
										if (code1 !== 1) {
											info('bad rtc code: ' + code1);
										}
										this.eatBits(12);
										if (this.encoding > 0) {
											this.lookBits(1);
											this.eatBits(1);
										}
									}
								}
								this.eof = true;
							}
						} else if (this.err && this.eoline) {
							while (true) {
								code1 = this.lookBits(13);
								if (code1 === ccittEOF) {
									this.eof = true;
									return null;
								}
								if (code1 >> 1 === 1) {
									break;
								}
								this.eatBits(1);
							}
							this.eatBits(12);
							if (this.encoding > 0) {
								this.eatBits(1);
								this.nextLine2D = !(code1 & 1);
							}
						}
						if (codingLine[0] > 0) {
							this.outputBits = codingLine[this.codingPos = 0];
						} else {
							this.outputBits = codingLine[this.codingPos = 1];
						}
						this.row++;
					}
					var c;
					if (this.outputBits >= 8) {
						c = this.codingPos & 1 ? 0 : 0xFF;
						this.outputBits -= 8;
						if (this.outputBits === 0 && codingLine[this.codingPos] < columns) {
							this.codingPos++;
							this.outputBits = codingLine[this.codingPos] - codingLine[this.codingPos - 1];
						}
					} else {
						bits = 8;
						c = 0;
						do {
							if (this.outputBits > bits) {
								c <<= bits;
								if (!(this.codingPos & 1)) {
									c |= 0xFF >> 8 - bits;
								}
								this.outputBits -= bits;
								bits = 0;
							} else {
								c <<= this.outputBits;
								if (!(this.codingPos & 1)) {
									c |= 0xFF >> 8 - this.outputBits;
								}
								bits -= this.outputBits;
								this.outputBits = 0;
								if (codingLine[this.codingPos] < columns) {
									this.codingPos++;
									this.outputBits = codingLine[this.codingPos] - codingLine[this.codingPos - 1];
								} else if (bits > 0) {
									c <<= bits;
									bits = 0;
								}
							}
						} while (bits);
					}
					if (this.black) {
						c ^= 0xFF;
					}
					return c;
				};
				CCITTFaxStream.prototype.findTableCode = function ccittFaxStreamFindTableCode(start, end, table, limit) {
					var limitValue = limit || 0;
					for (var i = start; i <= end; ++i) {
						var code = this.lookBits(i);
						if (code === ccittEOF) {
							return [
								true,
								1,
								false
							];
						}
						if (i < end) {
							code <<= end - i;
						}
						if (!limitValue || code >= limitValue) {
							var p = table[code - limitValue];
							if (p[0] === i) {
								this.eatBits(i);
								return [
									true,
									p[1],
									true
								];
							}
						}
					}
					return [
						false,
						0,
						false
					];
				};
				CCITTFaxStream.prototype.getTwoDimCode = function ccittFaxStreamGetTwoDimCode() {
					var code = 0;
					var p;
					if (this.eoblock) {
						code = this.lookBits(7);
						p = twoDimTable[code];
						if (p && p[0] > 0) {
							this.eatBits(p[0]);
							return p[1];
						}
					} else {
						var result = this.findTableCode(1, 7, twoDimTable);
						if (result[0] && result[2]) {
							return result[1];
						}
					}
					info('Bad two dim code');
					return ccittEOF;
				};
				CCITTFaxStream.prototype.getWhiteCode = function ccittFaxStreamGetWhiteCode() {
					var code = 0;
					var p;
					if (this.eoblock) {
						code = this.lookBits(12);
						if (code === ccittEOF) {
							return 1;
						}
						if (code >> 5 === 0) {
							p = whiteTable1[code];
						} else {
							p = whiteTable2[code >> 3];
						}
						if (p[0] > 0) {
							this.eatBits(p[0]);
							return p[1];
						}
					} else {
						var result = this.findTableCode(1, 9, whiteTable2);
						if (result[0]) {
							return result[1];
						}
						result = this.findTableCode(11, 12, whiteTable1);
						if (result[0]) {
							return result[1];
						}
					}
					info('bad white code');
					this.eatBits(1);
					return 1;
				};
				CCITTFaxStream.prototype.getBlackCode = function ccittFaxStreamGetBlackCode() {
					var code, p;
					if (this.eoblock) {
						code = this.lookBits(13);
						if (code === ccittEOF) {
							return 1;
						}
						if (code >> 7 === 0) {
							p = blackTable1[code];
						} else if (code >> 9 === 0 && code >> 7 !== 0) {
							p = blackTable2[(code >> 1) - 64];
						} else {
							p = blackTable3[code >> 7];
						}
						if (p[0] > 0) {
							this.eatBits(p[0]);
							return p[1];
						}
					} else {
						var result = this.findTableCode(2, 6, blackTable3);
						if (result[0]) {
							return result[1];
						}
						result = this.findTableCode(7, 12, blackTable2, 64);
						if (result[0]) {
							return result[1];
						}
						result = this.findTableCode(10, 13, blackTable1);
						if (result[0]) {
							return result[1];
						}
					}
					info('bad black code');
					this.eatBits(1);
					return 1;
				};
				CCITTFaxStream.prototype.lookBits = function CCITTFaxStream_lookBits(n) {
					var c;
					while (this.inputBits < n) {
						if ((c = this.str.getByte()) === -1) {
							if (this.inputBits === 0) {
								return ccittEOF;
							}
							return this.inputBuf << n - this.inputBits & 0xFFFF >> 16 - n;
						}
						this.inputBuf = this.inputBuf << 8 | c;
						this.inputBits += 8;
					}
					return this.inputBuf >> this.inputBits - n & 0xFFFF >> 16 - n;
				};
				CCITTFaxStream.prototype.eatBits = function CCITTFaxStream_eatBits(n) {
					if ((this.inputBits -= n) < 0) {
						this.inputBits = 0;
					}
				};
				return CCITTFaxStream;
			}();
			var LZWStream = function LZWStreamClosure() {
				function LZWStream(str, maybeLength, earlyChange) {
					this.str = str;
					this.dict = str.dict;
					this.cachedData = 0;
					this.bitsCached = 0;
					var maxLzwDictionarySize = 4096;
					var lzwState = {
						earlyChange: earlyChange,
						codeLength: 9,
						nextCode: 258,
						dictionaryValues: new Uint8Array(maxLzwDictionarySize),
						dictionaryLengths: new Uint16Array(maxLzwDictionarySize),
						dictionaryPrevCodes: new Uint16Array(maxLzwDictionarySize),
						currentSequence: new Uint8Array(maxLzwDictionarySize),
						currentSequenceLength: 0
					};
					for (var i = 0; i < 256; ++i) {
						lzwState.dictionaryValues[i] = i;
						lzwState.dictionaryLengths[i] = 1;
					}
					this.lzwState = lzwState;
					DecodeStream.call(this, maybeLength);
				}
				LZWStream.prototype = Object.create(DecodeStream.prototype);
				LZWStream.prototype.readBits = function LZWStream_readBits(n) {
					var bitsCached = this.bitsCached;
					var cachedData = this.cachedData;
					while (bitsCached < n) {
						var c = this.str.getByte();
						if (c === -1) {
							this.eof = true;
							return null;
						}
						cachedData = cachedData << 8 | c;
						bitsCached += 8;
					}
					this.bitsCached = bitsCached -= n;
					this.cachedData = cachedData;
					this.lastCode = null;
					return cachedData >>> bitsCached & (1 << n) - 1;
				};
				LZWStream.prototype.readBlock = function LZWStream_readBlock() {
					var blockSize = 512;
					var estimatedDecodedSize = blockSize * 2, decodedSizeDelta = blockSize;
					var i, j, q;
					var lzwState = this.lzwState;
					if (!lzwState) {
						return;
					}
					var earlyChange = lzwState.earlyChange;
					var nextCode = lzwState.nextCode;
					var dictionaryValues = lzwState.dictionaryValues;
					var dictionaryLengths = lzwState.dictionaryLengths;
					var dictionaryPrevCodes = lzwState.dictionaryPrevCodes;
					var codeLength = lzwState.codeLength;
					var prevCode = lzwState.prevCode;
					var currentSequence = lzwState.currentSequence;
					var currentSequenceLength = lzwState.currentSequenceLength;
					var decodedLength = 0;
					var currentBufferLength = this.bufferLength;
					var buffer = this.ensureBuffer(this.bufferLength + estimatedDecodedSize);
					for (i = 0; i < blockSize; i++) {
						var code = this.readBits(codeLength);
						var hasPrev = currentSequenceLength > 0;
						if (code < 256) {
							currentSequence[0] = code;
							currentSequenceLength = 1;
						} else if (code >= 258) {
							if (code < nextCode) {
								currentSequenceLength = dictionaryLengths[code];
								for (j = currentSequenceLength - 1, q = code; j >= 0; j--) {
									currentSequence[j] = dictionaryValues[q];
									q = dictionaryPrevCodes[q];
								}
							} else {
								currentSequence[currentSequenceLength++] = currentSequence[0];
							}
						} else if (code === 256) {
							codeLength = 9;
							nextCode = 258;
							currentSequenceLength = 0;
							continue;
						} else {
							this.eof = true;
							delete this.lzwState;
							break;
						}
						if (hasPrev) {
							dictionaryPrevCodes[nextCode] = prevCode;
							dictionaryLengths[nextCode] = dictionaryLengths[prevCode] + 1;
							dictionaryValues[nextCode] = currentSequence[0];
							nextCode++;
							codeLength = nextCode + earlyChange & nextCode + earlyChange - 1 ? codeLength : Math.min(Math.log(nextCode + earlyChange) / 0.6931471805599453 + 1, 12) | 0;
						}
						prevCode = code;
						decodedLength += currentSequenceLength;
						if (estimatedDecodedSize < decodedLength) {
							do {
								estimatedDecodedSize += decodedSizeDelta;
							} while (estimatedDecodedSize < decodedLength);
							buffer = this.ensureBuffer(this.bufferLength + estimatedDecodedSize);
						}
						for (j = 0; j < currentSequenceLength; j++) {
							buffer[currentBufferLength++] = currentSequence[j];
						}
					}
					lzwState.nextCode = nextCode;
					lzwState.codeLength = codeLength;
					lzwState.prevCode = prevCode;
					lzwState.currentSequenceLength = currentSequenceLength;
					this.bufferLength = currentBufferLength;
				};
				return LZWStream;
			}();
			var NullStream = function NullStreamClosure() {
				function NullStream() {
					Stream.call(this, new Uint8Array(0));
				}
				NullStream.prototype = Stream.prototype;
				return NullStream;
			}();
			exports.Ascii85Stream = Ascii85Stream;
			exports.AsciiHexStream = AsciiHexStream;
			exports.CCITTFaxStream = CCITTFaxStream;
			exports.DecryptStream = DecryptStream;
			exports.DecodeStream = DecodeStream;
			exports.FlateStream = FlateStream;
			exports.Jbig2Stream = Jbig2Stream;
			exports.JpegStream = JpegStream;
			exports.JpxStream = JpxStream;
			exports.NullStream = NullStream;
			exports.PredictorStream = PredictorStream;
			exports.RunLengthStream = RunLengthStream;
			exports.Stream = Stream;
			exports.StreamsSequenceStream = StreamsSequenceStream;
			exports.StringStream = StringStream;
			exports.LZWStream = LZWStream;

			/***/
		}),
/* 3 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreFunction = __w_pdfjs_require__(6);
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isString = sharedUtil.isString;
			var shadow = sharedUtil.shadow;
			var warn = sharedUtil.warn;
			var isDict = corePrimitives.isDict;
			var isName = corePrimitives.isName;
			var isStream = corePrimitives.isStream;
			var PDFFunction = coreFunction.PDFFunction;
			var ColorSpace = function ColorSpaceClosure() {
				function resizeRgbImage(src, bpc, w1, h1, w2, h2, alpha01, dest) {
					var COMPONENTS = 3;
					alpha01 = alpha01 !== 1 ? 0 : alpha01;
					var xRatio = w1 / w2;
					var yRatio = h1 / h2;
					var i, j, py, newIndex = 0, oldIndex;
					var xScaled = new Uint16Array(w2);
					var w1Scanline = w1 * COMPONENTS;
					for (i = 0; i < w2; i++) {
						xScaled[i] = Math.floor(i * xRatio) * COMPONENTS;
					}
					for (i = 0; i < h2; i++) {
						py = Math.floor(i * yRatio) * w1Scanline;
						for (j = 0; j < w2; j++) {
							oldIndex = py + xScaled[j];
							dest[newIndex++] = src[oldIndex++];
							dest[newIndex++] = src[oldIndex++];
							dest[newIndex++] = src[oldIndex++];
							newIndex += alpha01;
						}
					}
				}
				function ColorSpace() {
					error('should not call ColorSpace constructor');
				}
				ColorSpace.prototype = {
					getRgb: function ColorSpace_getRgb(src, srcOffset) {
						var rgb = new Uint8Array(3);
						this.getRgbItem(src, srcOffset, rgb, 0);
						return rgb;
					},
					getRgbItem: function ColorSpace_getRgbItem(src, srcOffset, dest, destOffset) {
						error('Should not call ColorSpace.getRgbItem');
					},
					getRgbBuffer: function ColorSpace_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						error('Should not call ColorSpace.getRgbBuffer');
					},
					getOutputLength: function ColorSpace_getOutputLength(inputLength, alpha01) {
						error('Should not call ColorSpace.getOutputLength');
					},
					isPassthrough: function ColorSpace_isPassthrough(bits) {
						return false;
					},
					fillRgb: function ColorSpace_fillRgb(dest, originalWidth, originalHeight, width, height, actualHeight, bpc, comps, alpha01) {
						var count = originalWidth * originalHeight;
						var rgbBuf = null;
						var numComponentColors = 1 << bpc;
						var needsResizing = originalHeight !== height || originalWidth !== width;
						var i, ii;
						if (this.isPassthrough(bpc)) {
							rgbBuf = comps;
						} else if (this.numComps === 1 && count > numComponentColors && this.name !== 'DeviceGray' && this.name !== 'DeviceRGB') {
							var allColors = bpc <= 8 ? new Uint8Array(numComponentColors) : new Uint16Array(numComponentColors);
							var key;
							for (i = 0; i < numComponentColors; i++) {
								allColors[i] = i;
							}
							var colorMap = new Uint8Array(numComponentColors * 3);
							this.getRgbBuffer(allColors, 0, numComponentColors, colorMap, 0, bpc, 0);
							var destPos, rgbPos;
							if (!needsResizing) {
								destPos = 0;
								for (i = 0; i < count; ++i) {
									key = comps[i] * 3;
									dest[destPos++] = colorMap[key];
									dest[destPos++] = colorMap[key + 1];
									dest[destPos++] = colorMap[key + 2];
									destPos += alpha01;
								}
							} else {
								rgbBuf = new Uint8Array(count * 3);
								rgbPos = 0;
								for (i = 0; i < count; ++i) {
									key = comps[i] * 3;
									rgbBuf[rgbPos++] = colorMap[key];
									rgbBuf[rgbPos++] = colorMap[key + 1];
									rgbBuf[rgbPos++] = colorMap[key + 2];
								}
							}
						} else {
							if (!needsResizing) {
								this.getRgbBuffer(comps, 0, width * actualHeight, dest, 0, bpc, alpha01);
							} else {
								rgbBuf = new Uint8Array(count * 3);
								this.getRgbBuffer(comps, 0, count, rgbBuf, 0, bpc, 0);
							}
						}
						if (rgbBuf) {
							if (needsResizing) {
								resizeRgbImage(rgbBuf, bpc, originalWidth, originalHeight, width, height, alpha01, dest);
							} else {
								rgbPos = 0;
								destPos = 0;
								for (i = 0, ii = width * actualHeight; i < ii; i++) {
									dest[destPos++] = rgbBuf[rgbPos++];
									dest[destPos++] = rgbBuf[rgbPos++];
									dest[destPos++] = rgbBuf[rgbPos++];
									destPos += alpha01;
								}
							}
						}
					},
					usesZeroToOneRange: true
				};
				ColorSpace.parse = function ColorSpace_parse(cs, xref, res) {
					var IR = ColorSpace.parseToIR(cs, xref, res);
					if (IR instanceof AlternateCS) {
						return IR;
					}
					return ColorSpace.fromIR(IR);
				};
				ColorSpace.fromIR = function ColorSpace_fromIR(IR) {
					var name = isArray(IR) ? IR[0] : IR;
					var whitePoint, blackPoint, gamma;
					switch (name) {
						case 'DeviceGrayCS':
							return this.singletons.gray;
						case 'DeviceRgbCS':
							return this.singletons.rgb;
						case 'DeviceCmykCS':
							return this.singletons.cmyk;
						case 'CalGrayCS':
							whitePoint = IR[1];
							blackPoint = IR[2];
							gamma = IR[3];
							return new CalGrayCS(whitePoint, blackPoint, gamma);
						case 'CalRGBCS':
							whitePoint = IR[1];
							blackPoint = IR[2];
							gamma = IR[3];
							var matrix = IR[4];
							return new CalRGBCS(whitePoint, blackPoint, gamma, matrix);
						case 'PatternCS':
							var basePatternCS = IR[1];
							if (basePatternCS) {
								basePatternCS = ColorSpace.fromIR(basePatternCS);
							}
							return new PatternCS(basePatternCS);
						case 'IndexedCS':
							var baseIndexedCS = IR[1];
							var hiVal = IR[2];
							var lookup = IR[3];
							return new IndexedCS(ColorSpace.fromIR(baseIndexedCS), hiVal, lookup);
						case 'AlternateCS':
							var numComps = IR[1];
							var alt = IR[2];
							var tintFnIR = IR[3];
							return new AlternateCS(numComps, ColorSpace.fromIR(alt), PDFFunction.fromIR(tintFnIR));
						case 'LabCS':
							whitePoint = IR[1];
							blackPoint = IR[2];
							var range = IR[3];
							return new LabCS(whitePoint, blackPoint, range);
						default:
							error('Unknown name ' + name);
					}
					return null;
				};
				ColorSpace.parseToIR = function ColorSpace_parseToIR(cs, xref, res) {
					if (isName(cs)) {
						var colorSpaces = res.get('ColorSpace');
						if (isDict(colorSpaces)) {
							var refcs = colorSpaces.get(cs.name);
							if (refcs) {
								cs = refcs;
							}
						}
					}
					cs = xref.fetchIfRef(cs);
					if (isName(cs)) {
						switch (cs.name) {
							case 'DeviceGray':
							case 'G':
								return 'DeviceGrayCS';
							case 'DeviceRGB':
							case 'RGB':
								return 'DeviceRgbCS';
							case 'DeviceCMYK':
							case 'CMYK':
								return 'DeviceCmykCS';
							case 'Pattern':
								return [
									'PatternCS',
									null
								];
							default:
								error('unrecognized colorspace ' + cs.name);
						}
					} else if (isArray(cs)) {
						var mode = xref.fetchIfRef(cs[0]).name;
						var numComps, params, alt, whitePoint, blackPoint, gamma;
						switch (mode) {
							case 'DeviceGray':
							case 'G':
								return 'DeviceGrayCS';
							case 'DeviceRGB':
							case 'RGB':
								return 'DeviceRgbCS';
							case 'DeviceCMYK':
							case 'CMYK':
								return 'DeviceCmykCS';
							case 'CalGray':
								params = xref.fetchIfRef(cs[1]);
								whitePoint = params.getArray('WhitePoint');
								blackPoint = params.getArray('BlackPoint');
								gamma = params.get('Gamma');
								return [
									'CalGrayCS',
									whitePoint,
									blackPoint,
									gamma
								];
							case 'CalRGB':
								params = xref.fetchIfRef(cs[1]);
								whitePoint = params.getArray('WhitePoint');
								blackPoint = params.getArray('BlackPoint');
								gamma = params.getArray('Gamma');
								var matrix = params.getArray('Matrix');
								return [
									'CalRGBCS',
									whitePoint,
									blackPoint,
									gamma,
									matrix
								];
							case 'ICCBased':
								var stream = xref.fetchIfRef(cs[1]);
								var dict = stream.dict;
								numComps = dict.get('N');
								alt = dict.get('Alternate');
								if (alt) {
									var altIR = ColorSpace.parseToIR(alt, xref, res);
									var altCS = ColorSpace.fromIR(altIR);
									if (altCS.numComps === numComps) {
										return altIR;
									}
									warn('ICCBased color space: Ignoring incorrect /Alternate entry.');
								}
								if (numComps === 1) {
									return 'DeviceGrayCS';
								} else if (numComps === 3) {
									return 'DeviceRgbCS';
								} else if (numComps === 4) {
									return 'DeviceCmykCS';
								}
								break;
							case 'Pattern':
								var basePatternCS = cs[1] || null;
								if (basePatternCS) {
									basePatternCS = ColorSpace.parseToIR(basePatternCS, xref, res);
								}
								return [
									'PatternCS',
									basePatternCS
								];
							case 'Indexed':
							case 'I':
								var baseIndexedCS = ColorSpace.parseToIR(cs[1], xref, res);
								var hiVal = xref.fetchIfRef(cs[2]) + 1;
								var lookup = xref.fetchIfRef(cs[3]);
								if (isStream(lookup)) {
									lookup = lookup.getBytes();
								}
								return [
									'IndexedCS',
									baseIndexedCS,
									hiVal,
									lookup
								];
							case 'Separation':
							case 'DeviceN':
								var name = xref.fetchIfRef(cs[1]);
								numComps = isArray(name) ? name.length : 1;
								alt = ColorSpace.parseToIR(cs[2], xref, res);
								var tintFnIR = PDFFunction.getIR(xref, xref.fetchIfRef(cs[3]));
								return [
									'AlternateCS',
									numComps,
									alt,
									tintFnIR
								];
							case 'Lab':
								params = xref.fetchIfRef(cs[1]);
								whitePoint = params.getArray('WhitePoint');
								blackPoint = params.getArray('BlackPoint');
								var range = params.getArray('Range');
								return [
									'LabCS',
									whitePoint,
									blackPoint,
									range
								];
							default:
								error('unimplemented color space object "' + mode + '"');
						}
					} else {
						error('unrecognized color space object: "' + cs + '"');
					}
					return null;
				};
				ColorSpace.isDefaultDecode = function ColorSpace_isDefaultDecode(decode, n) {
					if (!isArray(decode)) {
						return true;
					}
					if (n * 2 !== decode.length) {
						warn('The decode map is not the correct length');
						return true;
					}
					for (var i = 0, ii = decode.length; i < ii; i += 2) {
						if (decode[i] !== 0 || decode[i + 1] !== 1) {
							return false;
						}
					}
					return true;
				};
				ColorSpace.singletons = {
					get gray() {
						return shadow(this, 'gray', new DeviceGrayCS());
					},
					get rgb() {
						return shadow(this, 'rgb', new DeviceRgbCS());
					},
					get cmyk() {
						return shadow(this, 'cmyk', new DeviceCmykCS());
					}
				};
				return ColorSpace;
			}();
			var AlternateCS = function AlternateCSClosure() {
				function AlternateCS(numComps, base, tintFn) {
					this.name = 'Alternate';
					this.numComps = numComps;
					this.defaultColor = new Float32Array(numComps);
					for (var i = 0; i < numComps; ++i) {
						this.defaultColor[i] = 1;
					}
					this.base = base;
					this.tintFn = tintFn;
					this.tmpBuf = new Float32Array(base.numComps);
				}
				AlternateCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function AlternateCS_getRgbItem(src, srcOffset, dest, destOffset) {
						var tmpBuf = this.tmpBuf;
						this.tintFn(src, srcOffset, tmpBuf, 0);
						this.base.getRgbItem(tmpBuf, 0, dest, destOffset);
					},
					getRgbBuffer: function AlternateCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var tintFn = this.tintFn;
						var base = this.base;
						var scale = 1 / ((1 << bits) - 1);
						var baseNumComps = base.numComps;
						var usesZeroToOneRange = base.usesZeroToOneRange;
						var isPassthrough = (base.isPassthrough(8) || !usesZeroToOneRange) && alpha01 === 0;
						var pos = isPassthrough ? destOffset : 0;
						var baseBuf = isPassthrough ? dest : new Uint8Array(baseNumComps * count);
						var numComps = this.numComps;
						var scaled = new Float32Array(numComps);
						var tinted = new Float32Array(baseNumComps);
						var i, j;
						for (i = 0; i < count; i++) {
							for (j = 0; j < numComps; j++) {
								scaled[j] = src[srcOffset++] * scale;
							}
							tintFn(scaled, 0, tinted, 0);
							if (usesZeroToOneRange) {
								for (j = 0; j < baseNumComps; j++) {
									baseBuf[pos++] = tinted[j] * 255;
								}
							} else {
								base.getRgbItem(tinted, 0, baseBuf, pos);
								pos += baseNumComps;
							}
						}
						if (!isPassthrough) {
							base.getRgbBuffer(baseBuf, 0, count, dest, destOffset, 8, alpha01);
						}
					},
					getOutputLength: function AlternateCS_getOutputLength(inputLength, alpha01) {
						return this.base.getOutputLength(inputLength * this.base.numComps / this.numComps, alpha01);
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function AlternateCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return AlternateCS;
			}();
			var PatternCS = function PatternCSClosure() {
				function PatternCS(baseCS) {
					this.name = 'Pattern';
					this.base = baseCS;
				}
				PatternCS.prototype = {};
				return PatternCS;
			}();
			var IndexedCS = function IndexedCSClosure() {
				function IndexedCS(base, highVal, lookup) {
					this.name = 'Indexed';
					this.numComps = 1;
					this.defaultColor = new Uint8Array(this.numComps);
					this.base = base;
					this.highVal = highVal;
					var baseNumComps = base.numComps;
					var length = baseNumComps * highVal;
					if (isStream(lookup)) {
						this.lookup = new Uint8Array(length);
						var bytes = lookup.getBytes(length);
						this.lookup.set(bytes);
					} else if (isString(lookup)) {
						this.lookup = new Uint8Array(length);
						for (var i = 0; i < length; ++i) {
							this.lookup[i] = lookup.charCodeAt(i);
						}
					} else if (lookup instanceof Uint8Array || lookup instanceof Array) {
						this.lookup = lookup;
					} else {
						error('Unrecognized lookup table: ' + lookup);
					}
				}
				IndexedCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function IndexedCS_getRgbItem(src, srcOffset, dest, destOffset) {
						var numComps = this.base.numComps;
						var start = src[srcOffset] * numComps;
						this.base.getRgbItem(this.lookup, start, dest, destOffset);
					},
					getRgbBuffer: function IndexedCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var base = this.base;
						var numComps = base.numComps;
						var outputDelta = base.getOutputLength(numComps, alpha01);
						var lookup = this.lookup;
						for (var i = 0; i < count; ++i) {
							var lookupPos = src[srcOffset++] * numComps;
							base.getRgbBuffer(lookup, lookupPos, 1, dest, destOffset, 8, alpha01);
							destOffset += outputDelta;
						}
					},
					getOutputLength: function IndexedCS_getOutputLength(inputLength, alpha01) {
						return this.base.getOutputLength(inputLength * this.base.numComps, alpha01);
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function IndexedCS_isDefaultDecode(decodeMap) {
						return true;
					},
					usesZeroToOneRange: true
				};
				return IndexedCS;
			}();
			var DeviceGrayCS = function DeviceGrayCSClosure() {
				function DeviceGrayCS() {
					this.name = 'DeviceGray';
					this.numComps = 1;
					this.defaultColor = new Float32Array(this.numComps);
				}
				DeviceGrayCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function DeviceGrayCS_getRgbItem(src, srcOffset, dest, destOffset) {
						var c = src[srcOffset] * 255 | 0;
						c = c < 0 ? 0 : c > 255 ? 255 : c;
						dest[destOffset] = dest[destOffset + 1] = dest[destOffset + 2] = c;
					},
					getRgbBuffer: function DeviceGrayCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var scale = 255 / ((1 << bits) - 1);
						var j = srcOffset, q = destOffset;
						for (var i = 0; i < count; ++i) {
							var c = scale * src[j++] | 0;
							dest[q++] = c;
							dest[q++] = c;
							dest[q++] = c;
							q += alpha01;
						}
					},
					getOutputLength: function DeviceGrayCS_getOutputLength(inputLength, alpha01) {
						return inputLength * (3 + alpha01);
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function DeviceGrayCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return DeviceGrayCS;
			}();
			var DeviceRgbCS = function DeviceRgbCSClosure() {
				function DeviceRgbCS() {
					this.name = 'DeviceRGB';
					this.numComps = 3;
					this.defaultColor = new Float32Array(this.numComps);
				}
				DeviceRgbCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function DeviceRgbCS_getRgbItem(src, srcOffset, dest, destOffset) {
						var r = src[srcOffset] * 255 | 0;
						var g = src[srcOffset + 1] * 255 | 0;
						var b = src[srcOffset + 2] * 255 | 0;
						dest[destOffset] = r < 0 ? 0 : r > 255 ? 255 : r;
						dest[destOffset + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
						dest[destOffset + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
					},
					getRgbBuffer: function DeviceRgbCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						if (bits === 8 && alpha01 === 0) {
							dest.set(src.subarray(srcOffset, srcOffset + count * 3), destOffset);
							return;
						}
						var scale = 255 / ((1 << bits) - 1);
						var j = srcOffset, q = destOffset;
						for (var i = 0; i < count; ++i) {
							dest[q++] = scale * src[j++] | 0;
							dest[q++] = scale * src[j++] | 0;
							dest[q++] = scale * src[j++] | 0;
							q += alpha01;
						}
					},
					getOutputLength: function DeviceRgbCS_getOutputLength(inputLength, alpha01) {
						return inputLength * (3 + alpha01) / 3 | 0;
					},
					isPassthrough: function DeviceRgbCS_isPassthrough(bits) {
						return bits === 8;
					},
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function DeviceRgbCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return DeviceRgbCS;
			}();
			var DeviceCmykCS = function DeviceCmykCSClosure() {
				function convertToRgb(src, srcOffset, srcScale, dest, destOffset) {
					var c = src[srcOffset + 0] * srcScale;
					var m = src[srcOffset + 1] * srcScale;
					var y = src[srcOffset + 2] * srcScale;
					var k = src[srcOffset + 3] * srcScale;
					var r = c * (-4.387332384609988 * c + 54.48615194189176 * m + 18.82290502165302 * y + 212.25662451639585 * k + -285.2331026137004) + m * (1.7149763477362134 * m - 5.6096736904047315 * y + -17.873870861415444 * k - 5.497006427196366) + y * (-2.5217340131683033 * y - 21.248923337353073 * k + 17.5119270841813) + k * (-21.86122147463605 * k - 189.48180835922747) + 255 | 0;
					var g = c * (8.841041422036149 * c + 60.118027045597366 * m + 6.871425592049007 * y + 31.159100130055922 * k + -79.2970844816548) + m * (-15.310361306967817 * m + 17.575251261109482 * y + 131.35250912493976 * k - 190.9453302588951) + y * (4.444339102852739 * y + 9.8632861493405 * k - 24.86741582555878) + k * (-20.737325471181034 * k - 187.80453709719578) + 255 | 0;
					var b = c * (0.8842522430003296 * c + 8.078677503112928 * m + 30.89978309703729 * y - 0.23883238689178934 * k + -14.183576799673286) + m * (10.49593273432072 * m + 63.02378494754052 * y + 50.606957656360734 * k - 112.23884253719248) + y * (0.03296041114873217 * y + 115.60384449646641 * k + -193.58209356861505) + k * (-22.33816807309886 * k - 180.12613974708367) + 255 | 0;
					dest[destOffset] = r > 255 ? 255 : r < 0 ? 0 : r;
					dest[destOffset + 1] = g > 255 ? 255 : g < 0 ? 0 : g;
					dest[destOffset + 2] = b > 255 ? 255 : b < 0 ? 0 : b;
				}
				function DeviceCmykCS() {
					this.name = 'DeviceCMYK';
					this.numComps = 4;
					this.defaultColor = new Float32Array(this.numComps);
					this.defaultColor[3] = 1;
				}
				DeviceCmykCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function DeviceCmykCS_getRgbItem(src, srcOffset, dest, destOffset) {
						convertToRgb(src, srcOffset, 1, dest, destOffset);
					},
					getRgbBuffer: function DeviceCmykCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var scale = 1 / ((1 << bits) - 1);
						for (var i = 0; i < count; i++) {
							convertToRgb(src, srcOffset, scale, dest, destOffset);
							srcOffset += 4;
							destOffset += 3 + alpha01;
						}
					},
					getOutputLength: function DeviceCmykCS_getOutputLength(inputLength, alpha01) {
						return inputLength / 4 * (3 + alpha01) | 0;
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function DeviceCmykCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return DeviceCmykCS;
			}();
			var CalGrayCS = function CalGrayCSClosure() {
				function CalGrayCS(whitePoint, blackPoint, gamma) {
					this.name = 'CalGray';
					this.numComps = 1;
					this.defaultColor = new Float32Array(this.numComps);
					if (!whitePoint) {
						error('WhitePoint missing - required for color space CalGray');
					}
					blackPoint = blackPoint || [
						0,
						0,
						0
					];
					gamma = gamma || 1;
					this.XW = whitePoint[0];
					this.YW = whitePoint[1];
					this.ZW = whitePoint[2];
					this.XB = blackPoint[0];
					this.YB = blackPoint[1];
					this.ZB = blackPoint[2];
					this.G = gamma;
					if (this.XW < 0 || this.ZW < 0 || this.YW !== 1) {
						error('Invalid WhitePoint components for ' + this.name + ', no fallback available');
					}
					if (this.XB < 0 || this.YB < 0 || this.ZB < 0) {
						info('Invalid BlackPoint for ' + this.name + ', falling back to default');
						this.XB = this.YB = this.ZB = 0;
					}
					if (this.XB !== 0 || this.YB !== 0 || this.ZB !== 0) {
						warn(this.name + ', BlackPoint: XB: ' + this.XB + ', YB: ' + this.YB + ', ZB: ' + this.ZB + ', only default values are supported.');
					}
					if (this.G < 1) {
						info('Invalid Gamma: ' + this.G + ' for ' + this.name + ', falling back to default');
						this.G = 1;
					}
				}
				function convertToRgb(cs, src, srcOffset, dest, destOffset, scale) {
					var A = src[srcOffset] * scale;
					var AG = Math.pow(A, cs.G);
					var L = cs.YW * AG;
					var val = Math.max(295.8 * Math.pow(L, 0.333333333333333333) - 40.8, 0) | 0;
					dest[destOffset] = val;
					dest[destOffset + 1] = val;
					dest[destOffset + 2] = val;
				}
				CalGrayCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function CalGrayCS_getRgbItem(src, srcOffset, dest, destOffset) {
						convertToRgb(this, src, srcOffset, dest, destOffset, 1);
					},
					getRgbBuffer: function CalGrayCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var scale = 1 / ((1 << bits) - 1);
						for (var i = 0; i < count; ++i) {
							convertToRgb(this, src, srcOffset, dest, destOffset, scale);
							srcOffset += 1;
							destOffset += 3 + alpha01;
						}
					},
					getOutputLength: function CalGrayCS_getOutputLength(inputLength, alpha01) {
						return inputLength * (3 + alpha01);
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function CalGrayCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return CalGrayCS;
			}();
			var CalRGBCS = function CalRGBCSClosure() {
				var BRADFORD_SCALE_MATRIX = new Float32Array([
					0.8951,
					0.2664,
					-0.1614,
					-0.7502,
					1.7135,
					0.0367,
					0.0389,
					-0.0685,
					1.0296
				]);
				var BRADFORD_SCALE_INVERSE_MATRIX = new Float32Array([
					0.9869929,
					-0.1470543,
					0.1599627,
					0.4323053,
					0.5183603,
					0.0492912,
					-0.0085287,
					0.0400428,
					0.9684867
				]);
				var SRGB_D65_XYZ_TO_RGB_MATRIX = new Float32Array([
					3.2404542,
					-1.5371385,
					-0.4985314,
					-0.9692660,
					1.8760108,
					0.0415560,
					0.0556434,
					-0.2040259,
					1.0572252
				]);
				var FLAT_WHITEPOINT_MATRIX = new Float32Array([
					1,
					1,
					1
				]);
				var tempNormalizeMatrix = new Float32Array(3);
				var tempConvertMatrix1 = new Float32Array(3);
				var tempConvertMatrix2 = new Float32Array(3);
				var DECODE_L_CONSTANT = Math.pow((8 + 16) / 116, 3) / 8.0;
				function CalRGBCS(whitePoint, blackPoint, gamma, matrix) {
					this.name = 'CalRGB';
					this.numComps = 3;
					this.defaultColor = new Float32Array(this.numComps);
					if (!whitePoint) {
						error('WhitePoint missing - required for color space CalRGB');
					}
					blackPoint = blackPoint || new Float32Array(3);
					gamma = gamma || new Float32Array([
						1,
						1,
						1
					]);
					matrix = matrix || new Float32Array([
						1,
						0,
						0,
						0,
						1,
						0,
						0,
						0,
						1
					]);
					var XW = whitePoint[0];
					var YW = whitePoint[1];
					var ZW = whitePoint[2];
					this.whitePoint = whitePoint;
					var XB = blackPoint[0];
					var YB = blackPoint[1];
					var ZB = blackPoint[2];
					this.blackPoint = blackPoint;
					this.GR = gamma[0];
					this.GG = gamma[1];
					this.GB = gamma[2];
					this.MXA = matrix[0];
					this.MYA = matrix[1];
					this.MZA = matrix[2];
					this.MXB = matrix[3];
					this.MYB = matrix[4];
					this.MZB = matrix[5];
					this.MXC = matrix[6];
					this.MYC = matrix[7];
					this.MZC = matrix[8];
					if (XW < 0 || ZW < 0 || YW !== 1) {
						error('Invalid WhitePoint components for ' + this.name + ', no fallback available');
					}
					if (XB < 0 || YB < 0 || ZB < 0) {
						info('Invalid BlackPoint for ' + this.name + ' [' + XB + ', ' + YB + ', ' + ZB + '], falling back to default');
						this.blackPoint = new Float32Array(3);
					}
					if (this.GR < 0 || this.GG < 0 || this.GB < 0) {
						info('Invalid Gamma [' + this.GR + ', ' + this.GG + ', ' + this.GB + '] for ' + this.name + ', falling back to default');
						this.GR = this.GG = this.GB = 1;
					}
					if (this.MXA < 0 || this.MYA < 0 || this.MZA < 0 || this.MXB < 0 || this.MYB < 0 || this.MZB < 0 || this.MXC < 0 || this.MYC < 0 || this.MZC < 0) {
						info('Invalid Matrix for ' + this.name + ' [' + this.MXA + ', ' + this.MYA + ', ' + this.MZA + this.MXB + ', ' + this.MYB + ', ' + this.MZB + this.MXC + ', ' + this.MYC + ', ' + this.MZC + '], falling back to default');
						this.MXA = this.MYB = this.MZC = 1;
						this.MXB = this.MYA = this.MZA = this.MXC = this.MYC = this.MZB = 0;
					}
				}
				function matrixProduct(a, b, result) {
					result[0] = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
					result[1] = a[3] * b[0] + a[4] * b[1] + a[5] * b[2];
					result[2] = a[6] * b[0] + a[7] * b[1] + a[8] * b[2];
				}
				function convertToFlat(sourceWhitePoint, LMS, result) {
					result[0] = LMS[0] * 1 / sourceWhitePoint[0];
					result[1] = LMS[1] * 1 / sourceWhitePoint[1];
					result[2] = LMS[2] * 1 / sourceWhitePoint[2];
				}
				function convertToD65(sourceWhitePoint, LMS, result) {
					var D65X = 0.95047;
					var D65Y = 1;
					var D65Z = 1.08883;
					result[0] = LMS[0] * D65X / sourceWhitePoint[0];
					result[1] = LMS[1] * D65Y / sourceWhitePoint[1];
					result[2] = LMS[2] * D65Z / sourceWhitePoint[2];
				}
				function sRGBTransferFunction(color) {
					if (color <= 0.0031308) {
						return adjustToRange(0, 1, 12.92 * color);
					}
					return adjustToRange(0, 1, (1 + 0.055) * Math.pow(color, 1 / 2.4) - 0.055);
				}
				function adjustToRange(min, max, value) {
					return Math.max(min, Math.min(max, value));
				}
				function decodeL(L) {
					if (L < 0) {
						return -decodeL(-L);
					}
					if (L > 8.0) {
						return Math.pow((L + 16) / 116, 3);
					}
					return L * DECODE_L_CONSTANT;
				}
				function compensateBlackPoint(sourceBlackPoint, XYZ_Flat, result) {
					if (sourceBlackPoint[0] === 0 && sourceBlackPoint[1] === 0 && sourceBlackPoint[2] === 0) {
						result[0] = XYZ_Flat[0];
						result[1] = XYZ_Flat[1];
						result[2] = XYZ_Flat[2];
						return;
					}
					var zeroDecodeL = decodeL(0);
					var X_DST = zeroDecodeL;
					var X_SRC = decodeL(sourceBlackPoint[0]);
					var Y_DST = zeroDecodeL;
					var Y_SRC = decodeL(sourceBlackPoint[1]);
					var Z_DST = zeroDecodeL;
					var Z_SRC = decodeL(sourceBlackPoint[2]);
					var X_Scale = (1 - X_DST) / (1 - X_SRC);
					var X_Offset = 1 - X_Scale;
					var Y_Scale = (1 - Y_DST) / (1 - Y_SRC);
					var Y_Offset = 1 - Y_Scale;
					var Z_Scale = (1 - Z_DST) / (1 - Z_SRC);
					var Z_Offset = 1 - Z_Scale;
					result[0] = XYZ_Flat[0] * X_Scale + X_Offset;
					result[1] = XYZ_Flat[1] * Y_Scale + Y_Offset;
					result[2] = XYZ_Flat[2] * Z_Scale + Z_Offset;
				}
				function normalizeWhitePointToFlat(sourceWhitePoint, XYZ_In, result) {
					if (sourceWhitePoint[0] === 1 && sourceWhitePoint[2] === 1) {
						result[0] = XYZ_In[0];
						result[1] = XYZ_In[1];
						result[2] = XYZ_In[2];
						return;
					}
					var LMS = result;
					matrixProduct(BRADFORD_SCALE_MATRIX, XYZ_In, LMS);
					var LMS_Flat = tempNormalizeMatrix;
					convertToFlat(sourceWhitePoint, LMS, LMS_Flat);
					matrixProduct(BRADFORD_SCALE_INVERSE_MATRIX, LMS_Flat, result);
				}
				function normalizeWhitePointToD65(sourceWhitePoint, XYZ_In, result) {
					var LMS = result;
					matrixProduct(BRADFORD_SCALE_MATRIX, XYZ_In, LMS);
					var LMS_D65 = tempNormalizeMatrix;
					convertToD65(sourceWhitePoint, LMS, LMS_D65);
					matrixProduct(BRADFORD_SCALE_INVERSE_MATRIX, LMS_D65, result);
				}
				function convertToRgb(cs, src, srcOffset, dest, destOffset, scale) {
					var A = adjustToRange(0, 1, src[srcOffset] * scale);
					var B = adjustToRange(0, 1, src[srcOffset + 1] * scale);
					var C = adjustToRange(0, 1, src[srcOffset + 2] * scale);
					var AGR = Math.pow(A, cs.GR);
					var BGG = Math.pow(B, cs.GG);
					var CGB = Math.pow(C, cs.GB);
					var X = cs.MXA * AGR + cs.MXB * BGG + cs.MXC * CGB;
					var Y = cs.MYA * AGR + cs.MYB * BGG + cs.MYC * CGB;
					var Z = cs.MZA * AGR + cs.MZB * BGG + cs.MZC * CGB;
					var XYZ = tempConvertMatrix1;
					XYZ[0] = X;
					XYZ[1] = Y;
					XYZ[2] = Z;
					var XYZ_Flat = tempConvertMatrix2;
					normalizeWhitePointToFlat(cs.whitePoint, XYZ, XYZ_Flat);
					var XYZ_Black = tempConvertMatrix1;
					compensateBlackPoint(cs.blackPoint, XYZ_Flat, XYZ_Black);
					var XYZ_D65 = tempConvertMatrix2;
					normalizeWhitePointToD65(FLAT_WHITEPOINT_MATRIX, XYZ_Black, XYZ_D65);
					var SRGB = tempConvertMatrix1;
					matrixProduct(SRGB_D65_XYZ_TO_RGB_MATRIX, XYZ_D65, SRGB);
					var sR = sRGBTransferFunction(SRGB[0]);
					var sG = sRGBTransferFunction(SRGB[1]);
					var sB = sRGBTransferFunction(SRGB[2]);
					dest[destOffset] = Math.round(sR * 255);
					dest[destOffset + 1] = Math.round(sG * 255);
					dest[destOffset + 2] = Math.round(sB * 255);
				}
				CalRGBCS.prototype = {
					getRgb: function CalRGBCS_getRgb(src, srcOffset) {
						var rgb = new Uint8Array(3);
						this.getRgbItem(src, srcOffset, rgb, 0);
						return rgb;
					},
					getRgbItem: function CalRGBCS_getRgbItem(src, srcOffset, dest, destOffset) {
						convertToRgb(this, src, srcOffset, dest, destOffset, 1);
					},
					getRgbBuffer: function CalRGBCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var scale = 1 / ((1 << bits) - 1);
						for (var i = 0; i < count; ++i) {
							convertToRgb(this, src, srcOffset, dest, destOffset, scale);
							srcOffset += 3;
							destOffset += 3 + alpha01;
						}
					},
					getOutputLength: function CalRGBCS_getOutputLength(inputLength, alpha01) {
						return inputLength * (3 + alpha01) / 3 | 0;
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function CalRGBCS_isDefaultDecode(decodeMap) {
						return ColorSpace.isDefaultDecode(decodeMap, this.numComps);
					},
					usesZeroToOneRange: true
				};
				return CalRGBCS;
			}();
			var LabCS = function LabCSClosure() {
				function LabCS(whitePoint, blackPoint, range) {
					this.name = 'Lab';
					this.numComps = 3;
					this.defaultColor = new Float32Array(this.numComps);
					if (!whitePoint) {
						error('WhitePoint missing - required for color space Lab');
					}
					blackPoint = blackPoint || [
						0,
						0,
						0
					];
					range = range || [
						-100,
						100,
						-100,
						100
					];
					this.XW = whitePoint[0];
					this.YW = whitePoint[1];
					this.ZW = whitePoint[2];
					this.amin = range[0];
					this.amax = range[1];
					this.bmin = range[2];
					this.bmax = range[3];
					this.XB = blackPoint[0];
					this.YB = blackPoint[1];
					this.ZB = blackPoint[2];
					if (this.XW < 0 || this.ZW < 0 || this.YW !== 1) {
						error('Invalid WhitePoint components, no fallback available');
					}
					if (this.XB < 0 || this.YB < 0 || this.ZB < 0) {
						info('Invalid BlackPoint, falling back to default');
						this.XB = this.YB = this.ZB = 0;
					}
					if (this.amin > this.amax || this.bmin > this.bmax) {
						info('Invalid Range, falling back to defaults');
						this.amin = -100;
						this.amax = 100;
						this.bmin = -100;
						this.bmax = 100;
					}
				}
				function fn_g(x) {
					var result;
					if (x >= 6 / 29) {
						result = x * x * x;
					} else {
						result = 108 / 841 * (x - 4 / 29);
					}
					return result;
				}
				function decode(value, high1, low2, high2) {
					return low2 + value * (high2 - low2) / high1;
				}
				function convertToRgb(cs, src, srcOffset, maxVal, dest, destOffset) {
					var Ls = src[srcOffset];
					var as = src[srcOffset + 1];
					var bs = src[srcOffset + 2];
					if (maxVal !== false) {
						Ls = decode(Ls, maxVal, 0, 100);
						as = decode(as, maxVal, cs.amin, cs.amax);
						bs = decode(bs, maxVal, cs.bmin, cs.bmax);
					}
					as = as > cs.amax ? cs.amax : as < cs.amin ? cs.amin : as;
					bs = bs > cs.bmax ? cs.bmax : bs < cs.bmin ? cs.bmin : bs;
					var M = (Ls + 16) / 116;
					var L = M + as / 500;
					var N = M - bs / 200;
					var X = cs.XW * fn_g(L);
					var Y = cs.YW * fn_g(M);
					var Z = cs.ZW * fn_g(N);
					var r, g, b;
					if (cs.ZW < 1) {
						r = X * 3.1339 + Y * -1.6170 + Z * -0.4906;
						g = X * -0.9785 + Y * 1.9160 + Z * 0.0333;
						b = X * 0.0720 + Y * -0.2290 + Z * 1.4057;
					} else {
						r = X * 3.2406 + Y * -1.5372 + Z * -0.4986;
						g = X * -0.9689 + Y * 1.8758 + Z * 0.0415;
						b = X * 0.0557 + Y * -0.2040 + Z * 1.0570;
					}
					dest[destOffset] = r <= 0 ? 0 : r >= 1 ? 255 : Math.sqrt(r) * 255 | 0;
					dest[destOffset + 1] = g <= 0 ? 0 : g >= 1 ? 255 : Math.sqrt(g) * 255 | 0;
					dest[destOffset + 2] = b <= 0 ? 0 : b >= 1 ? 255 : Math.sqrt(b) * 255 | 0;
				}
				LabCS.prototype = {
					getRgb: ColorSpace.prototype.getRgb,
					getRgbItem: function LabCS_getRgbItem(src, srcOffset, dest, destOffset) {
						convertToRgb(this, src, srcOffset, false, dest, destOffset);
					},
					getRgbBuffer: function LabCS_getRgbBuffer(src, srcOffset, count, dest, destOffset, bits, alpha01) {
						var maxVal = (1 << bits) - 1;
						for (var i = 0; i < count; i++) {
							convertToRgb(this, src, srcOffset, maxVal, dest, destOffset);
							srcOffset += 3;
							destOffset += 3 + alpha01;
						}
					},
					getOutputLength: function LabCS_getOutputLength(inputLength, alpha01) {
						return inputLength * (3 + alpha01) / 3 | 0;
					},
					isPassthrough: ColorSpace.prototype.isPassthrough,
					fillRgb: ColorSpace.prototype.fillRgb,
					isDefaultDecode: function LabCS_isDefaultDecode(decodeMap) {
						return true;
					},
					usesZeroToOneRange: false
				};
				return LabCS;
			}();
			exports.ColorSpace = ColorSpace;

			/***/
		}),
/* 4 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var ExpertEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclamsmall',
				'Hungarumlautsmall',
				'',
				'dollaroldstyle',
				'dollarsuperior',
				'ampersandsmall',
				'Acutesmall',
				'parenleftsuperior',
				'parenrightsuperior',
				'twodotenleader',
				'onedotenleader',
				'comma',
				'hyphen',
				'period',
				'fraction',
				'zerooldstyle',
				'oneoldstyle',
				'twooldstyle',
				'threeoldstyle',
				'fouroldstyle',
				'fiveoldstyle',
				'sixoldstyle',
				'sevenoldstyle',
				'eightoldstyle',
				'nineoldstyle',
				'colon',
				'semicolon',
				'commasuperior',
				'threequartersemdash',
				'periodsuperior',
				'questionsmall',
				'',
				'asuperior',
				'bsuperior',
				'centsuperior',
				'dsuperior',
				'esuperior',
				'',
				'',
				'isuperior',
				'',
				'',
				'lsuperior',
				'msuperior',
				'nsuperior',
				'osuperior',
				'',
				'',
				'rsuperior',
				'ssuperior',
				'tsuperior',
				'',
				'ff',
				'fi',
				'fl',
				'ffi',
				'ffl',
				'parenleftinferior',
				'',
				'parenrightinferior',
				'Circumflexsmall',
				'hyphensuperior',
				'Gravesmall',
				'Asmall',
				'Bsmall',
				'Csmall',
				'Dsmall',
				'Esmall',
				'Fsmall',
				'Gsmall',
				'Hsmall',
				'Ismall',
				'Jsmall',
				'Ksmall',
				'Lsmall',
				'Msmall',
				'Nsmall',
				'Osmall',
				'Psmall',
				'Qsmall',
				'Rsmall',
				'Ssmall',
				'Tsmall',
				'Usmall',
				'Vsmall',
				'Wsmall',
				'Xsmall',
				'Ysmall',
				'Zsmall',
				'colonmonetary',
				'onefitted',
				'rupiah',
				'Tildesmall',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'exclamdownsmall',
				'centoldstyle',
				'Lslashsmall',
				'',
				'',
				'Scaronsmall',
				'Zcaronsmall',
				'Dieresissmall',
				'Brevesmall',
				'Caronsmall',
				'',
				'Dotaccentsmall',
				'',
				'',
				'Macronsmall',
				'',
				'',
				'figuredash',
				'hypheninferior',
				'',
				'',
				'Ogoneksmall',
				'Ringsmall',
				'Cedillasmall',
				'',
				'',
				'',
				'onequarter',
				'onehalf',
				'threequarters',
				'questiondownsmall',
				'oneeighth',
				'threeeighths',
				'fiveeighths',
				'seveneighths',
				'onethird',
				'twothirds',
				'',
				'',
				'zerosuperior',
				'onesuperior',
				'twosuperior',
				'threesuperior',
				'foursuperior',
				'fivesuperior',
				'sixsuperior',
				'sevensuperior',
				'eightsuperior',
				'ninesuperior',
				'zeroinferior',
				'oneinferior',
				'twoinferior',
				'threeinferior',
				'fourinferior',
				'fiveinferior',
				'sixinferior',
				'seveninferior',
				'eightinferior',
				'nineinferior',
				'centinferior',
				'dollarinferior',
				'periodinferior',
				'commainferior',
				'Agravesmall',
				'Aacutesmall',
				'Acircumflexsmall',
				'Atildesmall',
				'Adieresissmall',
				'Aringsmall',
				'AEsmall',
				'Ccedillasmall',
				'Egravesmall',
				'Eacutesmall',
				'Ecircumflexsmall',
				'Edieresissmall',
				'Igravesmall',
				'Iacutesmall',
				'Icircumflexsmall',
				'Idieresissmall',
				'Ethsmall',
				'Ntildesmall',
				'Ogravesmall',
				'Oacutesmall',
				'Ocircumflexsmall',
				'Otildesmall',
				'Odieresissmall',
				'OEsmall',
				'Oslashsmall',
				'Ugravesmall',
				'Uacutesmall',
				'Ucircumflexsmall',
				'Udieresissmall',
				'Yacutesmall',
				'Thornsmall',
				'Ydieresissmall'
			];
			var MacExpertEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclamsmall',
				'Hungarumlautsmall',
				'centoldstyle',
				'dollaroldstyle',
				'dollarsuperior',
				'ampersandsmall',
				'Acutesmall',
				'parenleftsuperior',
				'parenrightsuperior',
				'twodotenleader',
				'onedotenleader',
				'comma',
				'hyphen',
				'period',
				'fraction',
				'zerooldstyle',
				'oneoldstyle',
				'twooldstyle',
				'threeoldstyle',
				'fouroldstyle',
				'fiveoldstyle',
				'sixoldstyle',
				'sevenoldstyle',
				'eightoldstyle',
				'nineoldstyle',
				'colon',
				'semicolon',
				'',
				'threequartersemdash',
				'',
				'questionsmall',
				'',
				'',
				'',
				'',
				'Ethsmall',
				'',
				'',
				'onequarter',
				'onehalf',
				'threequarters',
				'oneeighth',
				'threeeighths',
				'fiveeighths',
				'seveneighths',
				'onethird',
				'twothirds',
				'',
				'',
				'',
				'',
				'',
				'',
				'ff',
				'fi',
				'fl',
				'ffi',
				'ffl',
				'parenleftinferior',
				'',
				'parenrightinferior',
				'Circumflexsmall',
				'hypheninferior',
				'Gravesmall',
				'Asmall',
				'Bsmall',
				'Csmall',
				'Dsmall',
				'Esmall',
				'Fsmall',
				'Gsmall',
				'Hsmall',
				'Ismall',
				'Jsmall',
				'Ksmall',
				'Lsmall',
				'Msmall',
				'Nsmall',
				'Osmall',
				'Psmall',
				'Qsmall',
				'Rsmall',
				'Ssmall',
				'Tsmall',
				'Usmall',
				'Vsmall',
				'Wsmall',
				'Xsmall',
				'Ysmall',
				'Zsmall',
				'colonmonetary',
				'onefitted',
				'rupiah',
				'Tildesmall',
				'',
				'',
				'asuperior',
				'centsuperior',
				'',
				'',
				'',
				'',
				'Aacutesmall',
				'Agravesmall',
				'Acircumflexsmall',
				'Adieresissmall',
				'Atildesmall',
				'Aringsmall',
				'Ccedillasmall',
				'Eacutesmall',
				'Egravesmall',
				'Ecircumflexsmall',
				'Edieresissmall',
				'Iacutesmall',
				'Igravesmall',
				'Icircumflexsmall',
				'Idieresissmall',
				'Ntildesmall',
				'Oacutesmall',
				'Ogravesmall',
				'Ocircumflexsmall',
				'Odieresissmall',
				'Otildesmall',
				'Uacutesmall',
				'Ugravesmall',
				'Ucircumflexsmall',
				'Udieresissmall',
				'',
				'eightsuperior',
				'fourinferior',
				'threeinferior',
				'sixinferior',
				'eightinferior',
				'seveninferior',
				'Scaronsmall',
				'',
				'centinferior',
				'twoinferior',
				'',
				'Dieresissmall',
				'',
				'Caronsmall',
				'osuperior',
				'fiveinferior',
				'',
				'commainferior',
				'periodinferior',
				'Yacutesmall',
				'',
				'dollarinferior',
				'',
				'Thornsmall',
				'',
				'nineinferior',
				'zeroinferior',
				'Zcaronsmall',
				'AEsmall',
				'Oslashsmall',
				'questiondownsmall',
				'oneinferior',
				'Lslashsmall',
				'',
				'',
				'',
				'',
				'',
				'',
				'Cedillasmall',
				'',
				'',
				'',
				'',
				'',
				'OEsmall',
				'figuredash',
				'hyphensuperior',
				'',
				'',
				'',
				'',
				'exclamdownsmall',
				'',
				'Ydieresissmall',
				'',
				'onesuperior',
				'twosuperior',
				'threesuperior',
				'foursuperior',
				'fivesuperior',
				'sixsuperior',
				'sevensuperior',
				'ninesuperior',
				'zerosuperior',
				'',
				'esuperior',
				'rsuperior',
				'tsuperior',
				'',
				'',
				'isuperior',
				'ssuperior',
				'dsuperior',
				'',
				'',
				'',
				'',
				'',
				'lsuperior',
				'Ogoneksmall',
				'Brevesmall',
				'Macronsmall',
				'bsuperior',
				'nsuperior',
				'msuperior',
				'commasuperior',
				'periodsuperior',
				'Dotaccentsmall',
				'Ringsmall'
			];
			var MacRomanEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quotesingle',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'grave',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'',
				'Adieresis',
				'Aring',
				'Ccedilla',
				'Eacute',
				'Ntilde',
				'Odieresis',
				'Udieresis',
				'aacute',
				'agrave',
				'acircumflex',
				'adieresis',
				'atilde',
				'aring',
				'ccedilla',
				'eacute',
				'egrave',
				'ecircumflex',
				'edieresis',
				'iacute',
				'igrave',
				'icircumflex',
				'idieresis',
				'ntilde',
				'oacute',
				'ograve',
				'ocircumflex',
				'odieresis',
				'otilde',
				'uacute',
				'ugrave',
				'ucircumflex',
				'udieresis',
				'dagger',
				'degree',
				'cent',
				'sterling',
				'section',
				'bullet',
				'paragraph',
				'germandbls',
				'registered',
				'copyright',
				'trademark',
				'acute',
				'dieresis',
				'notequal',
				'AE',
				'Oslash',
				'infinity',
				'plusminus',
				'lessequal',
				'greaterequal',
				'yen',
				'mu',
				'partialdiff',
				'summation',
				'product',
				'pi',
				'integral',
				'ordfeminine',
				'ordmasculine',
				'Omega',
				'ae',
				'oslash',
				'questiondown',
				'exclamdown',
				'logicalnot',
				'radical',
				'florin',
				'approxequal',
				'Delta',
				'guillemotleft',
				'guillemotright',
				'ellipsis',
				'space',
				'Agrave',
				'Atilde',
				'Otilde',
				'OE',
				'oe',
				'endash',
				'emdash',
				'quotedblleft',
				'quotedblright',
				'quoteleft',
				'quoteright',
				'divide',
				'lozenge',
				'ydieresis',
				'Ydieresis',
				'fraction',
				'currency',
				'guilsinglleft',
				'guilsinglright',
				'fi',
				'fl',
				'daggerdbl',
				'periodcentered',
				'quotesinglbase',
				'quotedblbase',
				'perthousand',
				'Acircumflex',
				'Ecircumflex',
				'Aacute',
				'Edieresis',
				'Egrave',
				'Iacute',
				'Icircumflex',
				'Idieresis',
				'Igrave',
				'Oacute',
				'Ocircumflex',
				'apple',
				'Ograve',
				'Uacute',
				'Ucircumflex',
				'Ugrave',
				'dotlessi',
				'circumflex',
				'tilde',
				'macron',
				'breve',
				'dotaccent',
				'ring',
				'cedilla',
				'hungarumlaut',
				'ogonek',
				'caron'
			];
			var StandardEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quoteright',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'quoteleft',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'exclamdown',
				'cent',
				'sterling',
				'fraction',
				'yen',
				'florin',
				'section',
				'currency',
				'quotesingle',
				'quotedblleft',
				'guillemotleft',
				'guilsinglleft',
				'guilsinglright',
				'fi',
				'fl',
				'',
				'endash',
				'dagger',
				'daggerdbl',
				'periodcentered',
				'',
				'paragraph',
				'bullet',
				'quotesinglbase',
				'quotedblbase',
				'quotedblright',
				'guillemotright',
				'ellipsis',
				'perthousand',
				'',
				'questiondown',
				'',
				'grave',
				'acute',
				'circumflex',
				'tilde',
				'macron',
				'breve',
				'dotaccent',
				'dieresis',
				'',
				'ring',
				'cedilla',
				'',
				'hungarumlaut',
				'ogonek',
				'caron',
				'emdash',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'AE',
				'',
				'ordfeminine',
				'',
				'',
				'',
				'',
				'Lslash',
				'Oslash',
				'OE',
				'ordmasculine',
				'',
				'',
				'',
				'',
				'',
				'ae',
				'',
				'',
				'',
				'dotlessi',
				'',
				'',
				'lslash',
				'oslash',
				'oe',
				'germandbls'
			];
			var WinAnsiEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quotesingle',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'grave',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'bullet',
				'Euro',
				'bullet',
				'quotesinglbase',
				'florin',
				'quotedblbase',
				'ellipsis',
				'dagger',
				'daggerdbl',
				'circumflex',
				'perthousand',
				'Scaron',
				'guilsinglleft',
				'OE',
				'bullet',
				'Zcaron',
				'bullet',
				'bullet',
				'quoteleft',
				'quoteright',
				'quotedblleft',
				'quotedblright',
				'bullet',
				'endash',
				'emdash',
				'tilde',
				'trademark',
				'scaron',
				'guilsinglright',
				'oe',
				'bullet',
				'zcaron',
				'Ydieresis',
				'space',
				'exclamdown',
				'cent',
				'sterling',
				'currency',
				'yen',
				'brokenbar',
				'section',
				'dieresis',
				'copyright',
				'ordfeminine',
				'guillemotleft',
				'logicalnot',
				'hyphen',
				'registered',
				'macron',
				'degree',
				'plusminus',
				'twosuperior',
				'threesuperior',
				'acute',
				'mu',
				'paragraph',
				'periodcentered',
				'cedilla',
				'onesuperior',
				'ordmasculine',
				'guillemotright',
				'onequarter',
				'onehalf',
				'threequarters',
				'questiondown',
				'Agrave',
				'Aacute',
				'Acircumflex',
				'Atilde',
				'Adieresis',
				'Aring',
				'AE',
				'Ccedilla',
				'Egrave',
				'Eacute',
				'Ecircumflex',
				'Edieresis',
				'Igrave',
				'Iacute',
				'Icircumflex',
				'Idieresis',
				'Eth',
				'Ntilde',
				'Ograve',
				'Oacute',
				'Ocircumflex',
				'Otilde',
				'Odieresis',
				'multiply',
				'Oslash',
				'Ugrave',
				'Uacute',
				'Ucircumflex',
				'Udieresis',
				'Yacute',
				'Thorn',
				'germandbls',
				'agrave',
				'aacute',
				'acircumflex',
				'atilde',
				'adieresis',
				'aring',
				'ae',
				'ccedilla',
				'egrave',
				'eacute',
				'ecircumflex',
				'edieresis',
				'igrave',
				'iacute',
				'icircumflex',
				'idieresis',
				'eth',
				'ntilde',
				'ograve',
				'oacute',
				'ocircumflex',
				'otilde',
				'odieresis',
				'divide',
				'oslash',
				'ugrave',
				'uacute',
				'ucircumflex',
				'udieresis',
				'yacute',
				'thorn',
				'ydieresis'
			];
			var SymbolSetEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'exclam',
				'universal',
				'numbersign',
				'existential',
				'percent',
				'ampersand',
				'suchthat',
				'parenleft',
				'parenright',
				'asteriskmath',
				'plus',
				'comma',
				'minus',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'congruent',
				'Alpha',
				'Beta',
				'Chi',
				'Delta',
				'Epsilon',
				'Phi',
				'Gamma',
				'Eta',
				'Iota',
				'theta1',
				'Kappa',
				'Lambda',
				'Mu',
				'Nu',
				'Omicron',
				'Pi',
				'Theta',
				'Rho',
				'Sigma',
				'Tau',
				'Upsilon',
				'sigma1',
				'Omega',
				'Xi',
				'Psi',
				'Zeta',
				'bracketleft',
				'therefore',
				'bracketright',
				'perpendicular',
				'underscore',
				'radicalex',
				'alpha',
				'beta',
				'chi',
				'delta',
				'epsilon',
				'phi',
				'gamma',
				'eta',
				'iota',
				'phi1',
				'kappa',
				'lambda',
				'mu',
				'nu',
				'omicron',
				'pi',
				'theta',
				'rho',
				'sigma',
				'tau',
				'upsilon',
				'omega1',
				'omega',
				'xi',
				'psi',
				'zeta',
				'braceleft',
				'bar',
				'braceright',
				'similar',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'Euro',
				'Upsilon1',
				'minute',
				'lessequal',
				'fraction',
				'infinity',
				'florin',
				'club',
				'diamond',
				'heart',
				'spade',
				'arrowboth',
				'arrowleft',
				'arrowup',
				'arrowright',
				'arrowdown',
				'degree',
				'plusminus',
				'second',
				'greaterequal',
				'multiply',
				'proportional',
				'partialdiff',
				'bullet',
				'divide',
				'notequal',
				'equivalence',
				'approxequal',
				'ellipsis',
				'arrowvertex',
				'arrowhorizex',
				'carriagereturn',
				'aleph',
				'Ifraktur',
				'Rfraktur',
				'weierstrass',
				'circlemultiply',
				'circleplus',
				'emptyset',
				'intersection',
				'union',
				'propersuperset',
				'reflexsuperset',
				'notsubset',
				'propersubset',
				'reflexsubset',
				'element',
				'notelement',
				'angle',
				'gradient',
				'registerserif',
				'copyrightserif',
				'trademarkserif',
				'product',
				'radical',
				'dotmath',
				'logicalnot',
				'logicaland',
				'logicalor',
				'arrowdblboth',
				'arrowdblleft',
				'arrowdblup',
				'arrowdblright',
				'arrowdbldown',
				'lozenge',
				'angleleft',
				'registersans',
				'copyrightsans',
				'trademarksans',
				'summation',
				'parenlefttp',
				'parenleftex',
				'parenleftbt',
				'bracketlefttp',
				'bracketleftex',
				'bracketleftbt',
				'bracelefttp',
				'braceleftmid',
				'braceleftbt',
				'braceex',
				'',
				'angleright',
				'integral',
				'integraltp',
				'integralex',
				'integralbt',
				'parenrighttp',
				'parenrightex',
				'parenrightbt',
				'bracketrighttp',
				'bracketrightex',
				'bracketrightbt',
				'bracerighttp',
				'bracerightmid',
				'bracerightbt'
			];
			var ZapfDingbatsEncoding = [
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'space',
				'a1',
				'a2',
				'a202',
				'a3',
				'a4',
				'a5',
				'a119',
				'a118',
				'a117',
				'a11',
				'a12',
				'a13',
				'a14',
				'a15',
				'a16',
				'a105',
				'a17',
				'a18',
				'a19',
				'a20',
				'a21',
				'a22',
				'a23',
				'a24',
				'a25',
				'a26',
				'a27',
				'a28',
				'a6',
				'a7',
				'a8',
				'a9',
				'a10',
				'a29',
				'a30',
				'a31',
				'a32',
				'a33',
				'a34',
				'a35',
				'a36',
				'a37',
				'a38',
				'a39',
				'a40',
				'a41',
				'a42',
				'a43',
				'a44',
				'a45',
				'a46',
				'a47',
				'a48',
				'a49',
				'a50',
				'a51',
				'a52',
				'a53',
				'a54',
				'a55',
				'a56',
				'a57',
				'a58',
				'a59',
				'a60',
				'a61',
				'a62',
				'a63',
				'a64',
				'a65',
				'a66',
				'a67',
				'a68',
				'a69',
				'a70',
				'a71',
				'a72',
				'a73',
				'a74',
				'a203',
				'a75',
				'a204',
				'a76',
				'a77',
				'a78',
				'a79',
				'a81',
				'a82',
				'a83',
				'a84',
				'a97',
				'a98',
				'a99',
				'a100',
				'',
				'a89',
				'a90',
				'a93',
				'a94',
				'a91',
				'a92',
				'a205',
				'a85',
				'a206',
				'a86',
				'a87',
				'a88',
				'a95',
				'a96',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'',
				'a101',
				'a102',
				'a103',
				'a104',
				'a106',
				'a107',
				'a108',
				'a112',
				'a111',
				'a110',
				'a109',
				'a120',
				'a121',
				'a122',
				'a123',
				'a124',
				'a125',
				'a126',
				'a127',
				'a128',
				'a129',
				'a130',
				'a131',
				'a132',
				'a133',
				'a134',
				'a135',
				'a136',
				'a137',
				'a138',
				'a139',
				'a140',
				'a141',
				'a142',
				'a143',
				'a144',
				'a145',
				'a146',
				'a147',
				'a148',
				'a149',
				'a150',
				'a151',
				'a152',
				'a153',
				'a154',
				'a155',
				'a156',
				'a157',
				'a158',
				'a159',
				'a160',
				'a161',
				'a163',
				'a164',
				'a196',
				'a165',
				'a192',
				'a166',
				'a167',
				'a168',
				'a169',
				'a170',
				'a171',
				'a172',
				'a173',
				'a162',
				'a174',
				'a175',
				'a176',
				'a177',
				'a178',
				'a179',
				'a193',
				'a180',
				'a199',
				'a181',
				'a200',
				'a182',
				'',
				'a201',
				'a183',
				'a184',
				'a197',
				'a185',
				'a194',
				'a198',
				'a186',
				'a195',
				'a187',
				'a188',
				'a189',
				'a190',
				'a191'
			];
			function getEncoding(encodingName) {
				switch (encodingName) {
					case 'WinAnsiEncoding':
						return WinAnsiEncoding;
					case 'StandardEncoding':
						return StandardEncoding;
					case 'MacRomanEncoding':
						return MacRomanEncoding;
					case 'SymbolSetEncoding':
						return SymbolSetEncoding;
					case 'ZapfDingbatsEncoding':
						return ZapfDingbatsEncoding;
					case 'ExpertEncoding':
						return ExpertEncoding;
					case 'MacExpertEncoding':
						return MacExpertEncoding;
					default:
						return null;
				}
			}
			exports.WinAnsiEncoding = WinAnsiEncoding;
			exports.StandardEncoding = StandardEncoding;
			exports.MacRomanEncoding = MacRomanEncoding;
			exports.SymbolSetEncoding = SymbolSetEncoding;
			exports.ZapfDingbatsEncoding = ZapfDingbatsEncoding;
			exports.ExpertEncoding = ExpertEncoding;
			exports.getEncoding = getEncoding;

			/***/
		}),
/* 5 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var MissingDataException = sharedUtil.MissingDataException;
			var StreamType = sharedUtil.StreamType;
			var assert = sharedUtil.assert;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isInt = sharedUtil.isInt;
			var isNum = sharedUtil.isNum;
			var isString = sharedUtil.isString;
			var warn = sharedUtil.warn;
			var EOF = corePrimitives.EOF;
			var Cmd = corePrimitives.Cmd;
			var Dict = corePrimitives.Dict;
			var Name = corePrimitives.Name;
			var Ref = corePrimitives.Ref;
			var isEOF = corePrimitives.isEOF;
			var isCmd = corePrimitives.isCmd;
			var isDict = corePrimitives.isDict;
			var isName = corePrimitives.isName;
			var Ascii85Stream = coreStream.Ascii85Stream;
			var AsciiHexStream = coreStream.AsciiHexStream;
			var CCITTFaxStream = coreStream.CCITTFaxStream;
			var FlateStream = coreStream.FlateStream;
			var Jbig2Stream = coreStream.Jbig2Stream;
			var JpegStream = coreStream.JpegStream;
			var JpxStream = coreStream.JpxStream;
			var LZWStream = coreStream.LZWStream;
			var NullStream = coreStream.NullStream;
			var PredictorStream = coreStream.PredictorStream;
			var RunLengthStream = coreStream.RunLengthStream;
			var MAX_LENGTH_TO_CACHE = 1000;
			var Parser = function ParserClosure() {
				function Parser(lexer, allowStreams, xref, recoveryMode) {
					this.lexer = lexer;
					this.allowStreams = allowStreams;
					this.xref = xref;
					this.recoveryMode = recoveryMode || false;
					this.imageCache = Object.create(null);
					this.refill();
				}
				Parser.prototype = {
					refill: function Parser_refill() {
						this.buf1 = this.lexer.getObj();
						this.buf2 = this.lexer.getObj();
					},
					shift: function Parser_shift() {
						if (isCmd(this.buf2, 'ID')) {
							this.buf1 = this.buf2;
							this.buf2 = null;
						} else {
							this.buf1 = this.buf2;
							this.buf2 = this.lexer.getObj();
						}
					},
					tryShift: function Parser_tryShift() {
						try {
							this.shift();
							return true;
						} catch (e) {
							if (e instanceof MissingDataException) {
								throw e;
							}
							return false;
						}
					},
					getObj: function Parser_getObj(cipherTransform) {
						var buf1 = this.buf1;
						this.shift();
						if (buf1 instanceof Cmd) {
							switch (buf1.cmd) {
								case 'BI':
									return this.makeInlineImage(cipherTransform);
								case '[':
									var array = [];
									while (!isCmd(this.buf1, ']') && !isEOF(this.buf1)) {
										array.push(this.getObj(cipherTransform));
									}
									if (isEOF(this.buf1)) {
										if (!this.recoveryMode) {
											error('End of file inside array');
										}
										return array;
									}
									this.shift();
									return array;
								case '<<':
									var dict = new Dict(this.xref);
									while (!isCmd(this.buf1, '>>') && !isEOF(this.buf1)) {
										if (!isName(this.buf1)) {
											info('Malformed dictionary: key must be a name object');
											this.shift();
											continue;
										}
										var key = this.buf1.name;
										this.shift();
										if (isEOF(this.buf1)) {
											break;
										}
										dict.set(key, this.getObj(cipherTransform));
									}
									if (isEOF(this.buf1)) {
										if (!this.recoveryMode) {
											error('End of file inside dictionary');
										}
										return dict;
									}
									if (isCmd(this.buf2, 'stream')) {
										return this.allowStreams ? this.makeStream(dict, cipherTransform) : dict;
									}
									this.shift();
									return dict;
								default:
									return buf1;
							}
						}
						if (isInt(buf1)) {
							var num = buf1;
							if (isInt(this.buf1) && isCmd(this.buf2, 'R')) {
								var ref = new Ref(num, this.buf1);
								this.shift();
								this.shift();
								return ref;
							}
							return num;
						}
						if (isString(buf1)) {
							var str = buf1;
							if (cipherTransform) {
								str = cipherTransform.decryptString(str);
							}
							return str;
						}
						return buf1;
					},
					findDefaultInlineStreamEnd: function Parser_findDefaultInlineStreamEnd(stream) {
						var E = 0x45, I = 0x49, SPACE = 0x20, LF = 0xA, CR = 0xD;
						var startPos = stream.pos, state = 0, ch, i, n, followingBytes;
						while ((ch = stream.getByte()) !== -1) {
							if (state === 0) {
								state = ch === E ? 1 : 0;
							} else if (state === 1) {
								state = ch === I ? 2 : 0;
							} else {
								assert(state === 2);
								if (ch === SPACE || ch === LF || ch === CR) {
									n = 5;
									followingBytes = stream.peekBytes(n);
									for (i = 0; i < n; i++) {
										ch = followingBytes[i];
										if (ch !== LF && ch !== CR && (ch < SPACE || ch > 0x7F)) {
											state = 0;
											break;
										}
									}
									if (state === 2) {
										break;
									}
								} else {
									state = 0;
								}
							}
						}
						return stream.pos - 4 - startPos;
					},
					findDCTDecodeInlineStreamEnd: function Parser_findDCTDecodeInlineStreamEnd(stream) {
						var startPos = stream.pos, foundEOI = false, b, markerLength, length;
						while ((b = stream.getByte()) !== -1) {
							if (b !== 0xFF) {
								continue;
							}
							switch (stream.getByte()) {
								case 0x00:
									break;
								case 0xFF:
									stream.skip(-1);
									break;
								case 0xD9:
									foundEOI = true;
									break;
								case 0xC0:
								case 0xC1:
								case 0xC2:
								case 0xC3:
								case 0xC5:
								case 0xC6:
								case 0xC7:
								case 0xC9:
								case 0xCA:
								case 0xCB:
								case 0xCD:
								case 0xCE:
								case 0xCF:
								case 0xC4:
								case 0xCC:
								case 0xDA:
								case 0xDB:
								case 0xDC:
								case 0xDD:
								case 0xDE:
								case 0xDF:
								case 0xE0:
								case 0xE1:
								case 0xE2:
								case 0xE3:
								case 0xE4:
								case 0xE5:
								case 0xE6:
								case 0xE7:
								case 0xE8:
								case 0xE9:
								case 0xEA:
								case 0xEB:
								case 0xEC:
								case 0xED:
								case 0xEE:
								case 0xEF:
								case 0xFE:
									markerLength = stream.getUint16();
									if (markerLength > 2) {
										stream.skip(markerLength - 2);
									} else {
										stream.skip(-2);
									}
									break;
							}
							if (foundEOI) {
								break;
							}
						}
						length = stream.pos - startPos;
						if (b === -1) {
							warn('Inline DCTDecode image stream: ' + 'EOI marker not found, searching for /EI/ instead.');
							stream.skip(-length);
							return this.findDefaultInlineStreamEnd(stream);
						}
						this.inlineStreamSkipEI(stream);
						return length;
					},
					findASCII85DecodeInlineStreamEnd: function Parser_findASCII85DecodeInlineStreamEnd(stream) {
						var TILDE = 0x7E, GT = 0x3E;
						var startPos = stream.pos, ch, length;
						while ((ch = stream.getByte()) !== -1) {
							if (ch === TILDE && stream.peekByte() === GT) {
								stream.skip();
								break;
							}
						}
						length = stream.pos - startPos;
						if (ch === -1) {
							warn('Inline ASCII85Decode image stream: ' + 'EOD marker not found, searching for /EI/ instead.');
							stream.skip(-length);
							return this.findDefaultInlineStreamEnd(stream);
						}
						this.inlineStreamSkipEI(stream);
						return length;
					},
					findASCIIHexDecodeInlineStreamEnd: function Parser_findASCIIHexDecodeInlineStreamEnd(stream) {
						var GT = 0x3E;
						var startPos = stream.pos, ch, length;
						while ((ch = stream.getByte()) !== -1) {
							if (ch === GT) {
								break;
							}
						}
						length = stream.pos - startPos;
						if (ch === -1) {
							warn('Inline ASCIIHexDecode image stream: ' + 'EOD marker not found, searching for /EI/ instead.');
							stream.skip(-length);
							return this.findDefaultInlineStreamEnd(stream);
						}
						this.inlineStreamSkipEI(stream);
						return length;
					},
					inlineStreamSkipEI: function Parser_inlineStreamSkipEI(stream) {
						var E = 0x45, I = 0x49;
						var state = 0, ch;
						while ((ch = stream.getByte()) !== -1) {
							if (state === 0) {
								state = ch === E ? 1 : 0;
							} else if (state === 1) {
								state = ch === I ? 2 : 0;
							} else if (state === 2) {
								break;
							}
						}
					},
					makeInlineImage: function Parser_makeInlineImage(cipherTransform) {
						var lexer = this.lexer;
						var stream = lexer.stream;
						var dict = new Dict(this.xref);
						while (!isCmd(this.buf1, 'ID') && !isEOF(this.buf1)) {
							if (!isName(this.buf1)) {
								error('Dictionary key must be a name object');
							}
							var key = this.buf1.name;
							this.shift();
							if (isEOF(this.buf1)) {
								break;
							}
							dict.set(key, this.getObj(cipherTransform));
						}
						var filter = dict.get('Filter', 'F'), filterName;
						if (isName(filter)) {
							filterName = filter.name;
						} else if (isArray(filter)) {
							var filterZero = this.xref.fetchIfRef(filter[0]);
							if (isName(filterZero)) {
								filterName = filterZero.name;
							}
						}
						var startPos = stream.pos, length, i, ii;
						if (filterName === 'DCTDecode' || filterName === 'DCT') {
							length = this.findDCTDecodeInlineStreamEnd(stream);
						} else if (filterName === 'ASCII85Decide' || filterName === 'A85') {
							length = this.findASCII85DecodeInlineStreamEnd(stream);
						} else if (filterName === 'ASCIIHexDecode' || filterName === 'AHx') {
							length = this.findASCIIHexDecodeInlineStreamEnd(stream);
						} else {
							length = this.findDefaultInlineStreamEnd(stream);
						}
						var imageStream = stream.makeSubStream(startPos, length, dict);
						var adler32;
						if (length < MAX_LENGTH_TO_CACHE) {
							var imageBytes = imageStream.getBytes();
							imageStream.reset();
							var a = 1;
							var b = 0;
							for (i = 0, ii = imageBytes.length; i < ii; ++i) {
								a += imageBytes[i] & 0xff;
								b += a;
							}
							adler32 = b % 65521 << 16 | a % 65521;
							if (this.imageCache.adler32 === adler32) {
								this.buf2 = Cmd.get('EI');
								this.shift();
								this.imageCache[adler32].reset();
								return this.imageCache[adler32];
							}
						}
						if (cipherTransform) {
							imageStream = cipherTransform.createStream(imageStream, length);
						}
						imageStream = this.filter(imageStream, dict, length);
						imageStream.dict = dict;
						if (adler32 !== undefined) {
							imageStream.cacheKey = 'inline_' + length + '_' + adler32;
							this.imageCache[adler32] = imageStream;
						}
						this.buf2 = Cmd.get('EI');
						this.shift();
						return imageStream;
					},
					makeStream: function Parser_makeStream(dict, cipherTransform) {
						var lexer = this.lexer;
						var stream = lexer.stream;
						lexer.skipToNextLine();
						var pos = stream.pos - 1;
						var length = dict.get('Length');
						if (!isInt(length)) {
							info('Bad ' + length + ' attribute in stream');
							length = 0;
						}
						stream.pos = pos + length;
						lexer.nextChar();
						if (this.tryShift() && isCmd(this.buf2, 'endstream')) {
							this.shift();
						} else {
							stream.pos = pos;
							var SCAN_BLOCK_SIZE = 2048;
							var ENDSTREAM_SIGNATURE_LENGTH = 9;
							var ENDSTREAM_SIGNATURE = [
								0x65,
								0x6E,
								0x64,
								0x73,
								0x74,
								0x72,
								0x65,
								0x61,
								0x6D
							];
							var skipped = 0, found = false, i, j;
							while (stream.pos < stream.end) {
								var scanBytes = stream.peekBytes(SCAN_BLOCK_SIZE);
								var scanLength = scanBytes.length - ENDSTREAM_SIGNATURE_LENGTH;
								if (scanLength <= 0) {
									break;
								}
								found = false;
								i = 0;
								while (i < scanLength) {
									j = 0;
									while (j < ENDSTREAM_SIGNATURE_LENGTH && scanBytes[i + j] === ENDSTREAM_SIGNATURE[j]) {
										j++;
									}
									if (j >= ENDSTREAM_SIGNATURE_LENGTH) {
										found = true;
										break;
									}
									i++;
								}
								if (found) {
									skipped += i;
									stream.pos += i;
									break;
								}
								skipped += scanLength;
								stream.pos += scanLength;
							}
							if (!found) {
								error('Missing endstream');
							}
							length = skipped;
							lexer.nextChar();
							this.shift();
							this.shift();
						}
						this.shift();
						stream = stream.makeSubStream(pos, length, dict);
						if (cipherTransform) {
							stream = cipherTransform.createStream(stream, length);
						}
						stream = this.filter(stream, dict, length);
						stream.dict = dict;
						return stream;
					},
					filter: function Parser_filter(stream, dict, length) {
						var filter = dict.get('Filter', 'F');
						var params = dict.get('DecodeParms', 'DP');
						if (isName(filter)) {
							if (isArray(params)) {
								params = this.xref.fetchIfRef(params[0]);
							}
							return this.makeFilter(stream, filter.name, length, params);
						}
						var maybeLength = length;
						if (isArray(filter)) {
							var filterArray = filter;
							var paramsArray = params;
							for (var i = 0, ii = filterArray.length; i < ii; ++i) {
								filter = this.xref.fetchIfRef(filterArray[i]);
								if (!isName(filter)) {
									error('Bad filter name: ' + filter);
								}
								params = null;
								if (isArray(paramsArray) && i in paramsArray) {
									params = this.xref.fetchIfRef(paramsArray[i]);
								}
								stream = this.makeFilter(stream, filter.name, maybeLength, params);
								maybeLength = null;
							}
						}
						return stream;
					},
					makeFilter: function Parser_makeFilter(stream, name, maybeLength, params) {
						if (maybeLength === 0) {
							warn('Empty "' + name + '" stream.');
							return new NullStream(stream);
						}
						try {
							var xrefStreamStats = this.xref.stats.streamTypes;
							if (name === 'FlateDecode' || name === 'Fl') {
								xrefStreamStats[StreamType.FLATE] = true;
								if (params) {
									return new PredictorStream(new FlateStream(stream, maybeLength), maybeLength, params);
								}
								return new FlateStream(stream, maybeLength);
							}
							if (name === 'LZWDecode' || name === 'LZW') {
								xrefStreamStats[StreamType.LZW] = true;
								var earlyChange = 1;
								if (params) {
									if (params.has('EarlyChange')) {
										earlyChange = params.get('EarlyChange');
									}
									return new PredictorStream(new LZWStream(stream, maybeLength, earlyChange), maybeLength, params);
								}
								return new LZWStream(stream, maybeLength, earlyChange);
							}
							if (name === 'DCTDecode' || name === 'DCT') {
								xrefStreamStats[StreamType.DCT] = true;
								return new JpegStream(stream, maybeLength, stream.dict, params);
							}
							if (name === 'JPXDecode' || name === 'JPX') {
								xrefStreamStats[StreamType.JPX] = true;
								return new JpxStream(stream, maybeLength, stream.dict, params);
							}
							if (name === 'ASCII85Decode' || name === 'A85') {
								xrefStreamStats[StreamType.A85] = true;
								return new Ascii85Stream(stream, maybeLength);
							}
							if (name === 'ASCIIHexDecode' || name === 'AHx') {
								xrefStreamStats[StreamType.AHX] = true;
								return new AsciiHexStream(stream, maybeLength);
							}
							if (name === 'CCITTFaxDecode' || name === 'CCF') {
								xrefStreamStats[StreamType.CCF] = true;
								return new CCITTFaxStream(stream, maybeLength, params);
							}
							if (name === 'RunLengthDecode' || name === 'RL') {
								xrefStreamStats[StreamType.RL] = true;
								return new RunLengthStream(stream, maybeLength);
							}
							if (name === 'JBIG2Decode') {
								xrefStreamStats[StreamType.JBIG] = true;
								return new Jbig2Stream(stream, maybeLength, stream.dict, params);
							}
							warn('filter "' + name + '" not supported yet');
							return stream;
						} catch (ex) {
							if (ex instanceof MissingDataException) {
								throw ex;
							}
							warn('Invalid stream: \"' + ex + '\"');
							return new NullStream(stream);
						}
					}
				};
				return Parser;
			}();
			var Lexer = function LexerClosure() {
				function Lexer(stream, knownCommands) {
					this.stream = stream;
					this.nextChar();
					this.strBuf = [];
					this.knownCommands = knownCommands;
				}
				var specialChars = [
					1,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					1,
					1,
					0,
					1,
					1,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					1,
					0,
					0,
					0,
					0,
					2,
					0,
					0,
					2,
					2,
					0,
					0,
					0,
					0,
					0,
					2,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					2,
					0,
					2,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					2,
					0,
					2,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					2,
					0,
					2,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0,
					0
				];
				function toHexDigit(ch) {
					if (ch >= 0x30 && ch <= 0x39) {
						return ch & 0x0F;
					}
					if (ch >= 0x41 && ch <= 0x46 || ch >= 0x61 && ch <= 0x66) {
						return (ch & 0x0F) + 9;
					}
					return -1;
				}
				Lexer.prototype = {
					nextChar: function Lexer_nextChar() {
						return this.currentChar = this.stream.getByte();
					},
					peekChar: function Lexer_peekChar() {
						return this.stream.peekByte();
					},
					getNumber: function Lexer_getNumber() {
						var ch = this.currentChar;
						var eNotation = false;
						var divideBy = 0;
						var sign = 1;
						if (ch === 0x2D) {
							sign = -1;
							ch = this.nextChar();
							if (ch === 0x2D) {
								ch = this.nextChar();
							}
						} else if (ch === 0x2B) {
							ch = this.nextChar();
						}
						if (ch === 0x2E) {
							divideBy = 10;
							ch = this.nextChar();
						}
						if (ch < 0x30 || ch > 0x39) {
							error('Invalid number: ' + String.fromCharCode(ch));
							return 0;
						}
						var baseValue = ch - 0x30;
						var powerValue = 0;
						var powerValueSign = 1;
						while ((ch = this.nextChar()) >= 0) {
							if (0x30 <= ch && ch <= 0x39) {
								var currentDigit = ch - 0x30;
								if (eNotation) {
									powerValue = powerValue * 10 + currentDigit;
								} else {
									if (divideBy !== 0) {
										divideBy *= 10;
									}
									baseValue = baseValue * 10 + currentDigit;
								}
							} else if (ch === 0x2E) {
								if (divideBy === 0) {
									divideBy = 1;
								} else {
									break;
								}
							} else if (ch === 0x2D) {
								warn('Badly formatted number');
							} else if (ch === 0x45 || ch === 0x65) {
								ch = this.peekChar();
								if (ch === 0x2B || ch === 0x2D) {
									powerValueSign = ch === 0x2D ? -1 : 1;
									this.nextChar();
								} else if (ch < 0x30 || ch > 0x39) {
									break;
								}
								eNotation = true;
							} else {
								break;
							}
						}
						if (divideBy !== 0) {
							baseValue /= divideBy;
						}
						if (eNotation) {
							baseValue *= Math.pow(10, powerValueSign * powerValue);
						}
						return sign * baseValue;
					},
					getString: function Lexer_getString() {
						var numParen = 1;
						var done = false;
						var strBuf = this.strBuf;
						strBuf.length = 0;
						var ch = this.nextChar();
						while (true) {
							var charBuffered = false;
							switch (ch | 0) {
								case -1:
									warn('Unterminated string');
									done = true;
									break;
								case 0x28:
									++numParen;
									strBuf.push('(');
									break;
								case 0x29:
									if (--numParen === 0) {
										this.nextChar();
										done = true;
									} else {
										strBuf.push(')');
									}
									break;
								case 0x5C:
									ch = this.nextChar();
									switch (ch) {
										case -1:
											warn('Unterminated string');
											done = true;
											break;
										case 0x6E:
											strBuf.push('\n');
											break;
										case 0x72:
											strBuf.push('\r');
											break;
										case 0x74:
											strBuf.push('\t');
											break;
										case 0x62:
											strBuf.push('\b');
											break;
										case 0x66:
											strBuf.push('\f');
											break;
										case 0x5C:
										case 0x28:
										case 0x29:
											strBuf.push(String.fromCharCode(ch));
											break;
										case 0x30:
										case 0x31:
										case 0x32:
										case 0x33:
										case 0x34:
										case 0x35:
										case 0x36:
										case 0x37:
											var x = ch & 0x0F;
											ch = this.nextChar();
											charBuffered = true;
											if (ch >= 0x30 && ch <= 0x37) {
												x = (x << 3) + (ch & 0x0F);
												ch = this.nextChar();
												if (ch >= 0x30 && ch <= 0x37) {
													charBuffered = false;
													x = (x << 3) + (ch & 0x0F);
												}
											}
											strBuf.push(String.fromCharCode(x));
											break;
										case 0x0D:
											if (this.peekChar() === 0x0A) {
												this.nextChar();
											}
											break;
										case 0x0A:
											break;
										default:
											strBuf.push(String.fromCharCode(ch));
											break;
									}
									break;
								default:
									strBuf.push(String.fromCharCode(ch));
									break;
							}
							if (done) {
								break;
							}
							if (!charBuffered) {
								ch = this.nextChar();
							}
						}
						return strBuf.join('');
					},
					getName: function Lexer_getName() {
						var ch, previousCh;
						var strBuf = this.strBuf;
						strBuf.length = 0;
						while ((ch = this.nextChar()) >= 0 && !specialChars[ch]) {
							if (ch === 0x23) {
								ch = this.nextChar();
								if (specialChars[ch]) {
									warn('Lexer_getName: ' + 'NUMBER SIGN (#) should be followed by a hexadecimal number.');
									strBuf.push('#');
									break;
								}
								var x = toHexDigit(ch);
								if (x !== -1) {
									previousCh = ch;
									ch = this.nextChar();
									var x2 = toHexDigit(ch);
									if (x2 === -1) {
										warn('Lexer_getName: Illegal digit (' + String.fromCharCode(ch) + ') in hexadecimal number.');
										strBuf.push('#', String.fromCharCode(previousCh));
										if (specialChars[ch]) {
											break;
										}
										strBuf.push(String.fromCharCode(ch));
										continue;
									}
									strBuf.push(String.fromCharCode(x << 4 | x2));
								} else {
									strBuf.push('#', String.fromCharCode(ch));
								}
							} else {
								strBuf.push(String.fromCharCode(ch));
							}
						}
						if (strBuf.length > 127) {
							warn('name token is longer than allowed by the spec: ' + strBuf.length);
						}
						return Name.get(strBuf.join(''));
					},
					getHexString: function Lexer_getHexString() {
						var strBuf = this.strBuf;
						strBuf.length = 0;
						var ch = this.currentChar;
						var isFirstHex = true;
						var firstDigit;
						var secondDigit;
						while (true) {
							if (ch < 0) {
								warn('Unterminated hex string');
								break;
							} else if (ch === 0x3E) {
								this.nextChar();
								break;
							} else if (specialChars[ch] === 1) {
								ch = this.nextChar();
								continue;
							} else {
								if (isFirstHex) {
									firstDigit = toHexDigit(ch);
									if (firstDigit === -1) {
										warn('Ignoring invalid character "' + ch + '" in hex string');
										ch = this.nextChar();
										continue;
									}
								} else {
									secondDigit = toHexDigit(ch);
									if (secondDigit === -1) {
										warn('Ignoring invalid character "' + ch + '" in hex string');
										ch = this.nextChar();
										continue;
									}
									strBuf.push(String.fromCharCode(firstDigit << 4 | secondDigit));
								}
								isFirstHex = !isFirstHex;
								ch = this.nextChar();
							}
						}
						return strBuf.join('');
					},
					getObj: function Lexer_getObj() {
						var comment = false;
						var ch = this.currentChar;
						while (true) {
							if (ch < 0) {
								return EOF;
							}
							if (comment) {
								if (ch === 0x0A || ch === 0x0D) {
									comment = false;
								}
							} else if (ch === 0x25) {
								comment = true;
							} else if (specialChars[ch] !== 1) {
								break;
							}
							ch = this.nextChar();
						}
						switch (ch | 0) {
							case 0x30:
							case 0x31:
							case 0x32:
							case 0x33:
							case 0x34:
							case 0x35:
							case 0x36:
							case 0x37:
							case 0x38:
							case 0x39:
							case 0x2B:
							case 0x2D:
							case 0x2E:
								return this.getNumber();
							case 0x28:
								return this.getString();
							case 0x2F:
								return this.getName();
							case 0x5B:
								this.nextChar();
								return Cmd.get('[');
							case 0x5D:
								this.nextChar();
								return Cmd.get(']');
							case 0x3C:
								ch = this.nextChar();
								if (ch === 0x3C) {
									this.nextChar();
									return Cmd.get('<<');
								}
								return this.getHexString();
							case 0x3E:
								ch = this.nextChar();
								if (ch === 0x3E) {
									this.nextChar();
									return Cmd.get('>>');
								}
								return Cmd.get('>');
							case 0x7B:
								this.nextChar();
								return Cmd.get('{');
							case 0x7D:
								this.nextChar();
								return Cmd.get('}');
							case 0x29:
								this.nextChar();
								error('Illegal character: ' + ch);
								break;
						}
						var str = String.fromCharCode(ch);
						var knownCommands = this.knownCommands;
						var knownCommandFound = knownCommands && knownCommands[str] !== undefined;
						while ((ch = this.nextChar()) >= 0 && !specialChars[ch]) {
							var possibleCommand = str + String.fromCharCode(ch);
							if (knownCommandFound && knownCommands[possibleCommand] === undefined) {
								break;
							}
							if (str.length === 128) {
								error('Command token too long: ' + str.length);
							}
							str = possibleCommand;
							knownCommandFound = knownCommands && knownCommands[str] !== undefined;
						}
						if (str === 'true') {
							return true;
						}
						if (str === 'false') {
							return false;
						}
						if (str === 'null') {
							return null;
						}
						return Cmd.get(str);
					},
					skipToNextLine: function Lexer_skipToNextLine() {
						var ch = this.currentChar;
						while (ch >= 0) {
							if (ch === 0x0D) {
								ch = this.nextChar();
								if (ch === 0x0A) {
									this.nextChar();
								}
								break;
							} else if (ch === 0x0A) {
								this.nextChar();
								break;
							}
							ch = this.nextChar();
						}
					}
				};
				return Lexer;
			}();
			var Linearization = {
				create: function LinearizationCreate(stream) {
					function getInt(name, allowZeroValue) {
						var obj = linDict.get(name);
						if (isInt(obj) && (allowZeroValue ? obj >= 0 : obj > 0)) {
							return obj;
						}
						throw new Error('The "' + name + '" parameter in the linearization ' + 'dictionary is invalid.');
					}
					function getHints() {
						var hints = linDict.get('H'), hintsLength, item;
						if (isArray(hints) && ((hintsLength = hints.length) === 2 || hintsLength === 4)) {
							for (var index = 0; index < hintsLength; index++) {
								if (!(isInt(item = hints[index]) && item > 0)) {
									throw new Error('Hint (' + index + ') in the linearization dictionary is invalid.');
								}
							}
							return hints;
						}
						throw new Error('Hint array in the linearization dictionary is invalid.');
					}
					var parser = new Parser(new Lexer(stream), false, null);
					var obj1 = parser.getObj();
					var obj2 = parser.getObj();
					var obj3 = parser.getObj();
					var linDict = parser.getObj();
					var obj, length;
					if (!(isInt(obj1) && isInt(obj2) && isCmd(obj3, 'obj') && isDict(linDict) && isNum(obj = linDict.get('Linearized')) && obj > 0)) {
						return null;
					} else if ((length = getInt('L')) !== stream.length) {
						throw new Error('The "L" parameter in the linearization dictionary ' + 'does not equal the stream length.');
					}
					return {
						length: length,
						hints: getHints(),
						objectNumberFirst: getInt('O'),
						endFirst: getInt('E'),
						numPages: getInt('N'),
						mainXRefEntriesOffset: getInt('T'),
						pageFirst: linDict.has('P') ? getInt('P', true) : 0
					};
				}
			};
			exports.Lexer = Lexer;
			exports.Linearization = Linearization;
			exports.Parser = Parser;

			/***/
		}),
/* 6 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var corePsParser = __w_pdfjs_require__(34);
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isBool = sharedUtil.isBool;
			var isDict = corePrimitives.isDict;
			var isStream = corePrimitives.isStream;
			var PostScriptLexer = corePsParser.PostScriptLexer;
			var PostScriptParser = corePsParser.PostScriptParser;
			var PDFFunction = function PDFFunctionClosure() {
				var CONSTRUCT_SAMPLED = 0;
				var CONSTRUCT_INTERPOLATED = 2;
				var CONSTRUCT_STICHED = 3;
				var CONSTRUCT_POSTSCRIPT = 4;
				return {
					getSampleArray: function PDFFunction_getSampleArray(size, outputSize, bps, str) {
						var i, ii;
						var length = 1;
						for (i = 0, ii = size.length; i < ii; i++) {
							length *= size[i];
						}
						length *= outputSize;
						var array = new Array(length);
						var codeSize = 0;
						var codeBuf = 0;
						var sampleMul = 1.0 / (Math.pow(2.0, bps) - 1);
						var strBytes = str.getBytes((length * bps + 7) / 8);
						var strIdx = 0;
						for (i = 0; i < length; i++) {
							while (codeSize < bps) {
								codeBuf <<= 8;
								codeBuf |= strBytes[strIdx++];
								codeSize += 8;
							}
							codeSize -= bps;
							array[i] = (codeBuf >> codeSize) * sampleMul;
							codeBuf &= (1 << codeSize) - 1;
						}
						return array;
					},
					getIR: function PDFFunction_getIR(xref, fn) {
						var dict = fn.dict;
						if (!dict) {
							dict = fn;
						}
						var types = [
							this.constructSampled,
							null,
							this.constructInterpolated,
							this.constructStiched,
							this.constructPostScript
						];
						var typeNum = dict.get('FunctionType');
						var typeFn = types[typeNum];
						if (!typeFn) {
							error('Unknown type of function');
						}
						return typeFn.call(this, fn, dict, xref);
					},
					fromIR: function PDFFunction_fromIR(IR) {
						var type = IR[0];
						switch (type) {
							case CONSTRUCT_SAMPLED:
								return this.constructSampledFromIR(IR);
							case CONSTRUCT_INTERPOLATED:
								return this.constructInterpolatedFromIR(IR);
							case CONSTRUCT_STICHED:
								return this.constructStichedFromIR(IR);
							default:
								return this.constructPostScriptFromIR(IR);
						}
					},
					parse: function PDFFunction_parse(xref, fn) {
						var IR = this.getIR(xref, fn);
						return this.fromIR(IR);
					},
					parseArray: function PDFFunction_parseArray(xref, fnObj) {
						if (!isArray(fnObj)) {
							return this.parse(xref, fnObj);
						}
						var fnArray = [];
						for (var j = 0, jj = fnObj.length; j < jj; j++) {
							var obj = xref.fetchIfRef(fnObj[j]);
							fnArray.push(PDFFunction.parse(xref, obj));
						}
						return function (src, srcOffset, dest, destOffset) {
							for (var i = 0, ii = fnArray.length; i < ii; i++) {
								fnArray[i](src, srcOffset, dest, destOffset + i);
							}
						};
					},
					constructSampled: function PDFFunction_constructSampled(str, dict) {
						function toMultiArray(arr) {
							var inputLength = arr.length;
							var out = [];
							var index = 0;
							for (var i = 0; i < inputLength; i += 2) {
								out[index] = [
									arr[i],
									arr[i + 1]
								];
								++index;
							}
							return out;
						}
						var domain = dict.getArray('Domain');
						var range = dict.getArray('Range');
						if (!domain || !range) {
							error('No domain or range');
						}
						var inputSize = domain.length / 2;
						var outputSize = range.length / 2;
						domain = toMultiArray(domain);
						range = toMultiArray(range);
						var size = dict.get('Size');
						var bps = dict.get('BitsPerSample');
						var order = dict.get('Order') || 1;
						if (order !== 1) {
							info('No support for cubic spline interpolation: ' + order);
						}
						var encode = dict.getArray('Encode');
						if (!encode) {
							encode = [];
							for (var i = 0; i < inputSize; ++i) {
								encode.push(0);
								encode.push(size[i] - 1);
							}
						}
						encode = toMultiArray(encode);
						var decode = dict.getArray('Decode');
						if (!decode) {
							decode = range;
						} else {
							decode = toMultiArray(decode);
						}
						var samples = this.getSampleArray(size, outputSize, bps, str);
						return [
							CONSTRUCT_SAMPLED,
							inputSize,
							domain,
							encode,
							decode,
							samples,
							size,
							outputSize,
							Math.pow(2, bps) - 1,
							range
						];
					},
					constructSampledFromIR: function PDFFunction_constructSampledFromIR(IR) {
						function interpolate(x, xmin, xmax, ymin, ymax) {
							return ymin + (x - xmin) * ((ymax - ymin) / (xmax - xmin));
						}
						return function constructSampledFromIRResult(src, srcOffset, dest, destOffset) {
							var m = IR[1];
							var domain = IR[2];
							var encode = IR[3];
							var decode = IR[4];
							var samples = IR[5];
							var size = IR[6];
							var n = IR[7];
							var range = IR[9];
							var cubeVertices = 1 << m;
							var cubeN = new Float64Array(cubeVertices);
							var cubeVertex = new Uint32Array(cubeVertices);
							var i, j;
							for (j = 0; j < cubeVertices; j++) {
								cubeN[j] = 1;
							}
							var k = n, pos = 1;
							for (i = 0; i < m; ++i) {
								var domain_2i = domain[i][0];
								var domain_2i_1 = domain[i][1];
								var xi = Math.min(Math.max(src[srcOffset + i], domain_2i), domain_2i_1);
								var e = interpolate(xi, domain_2i, domain_2i_1, encode[i][0], encode[i][1]);
								var size_i = size[i];
								e = Math.min(Math.max(e, 0), size_i - 1);
								var e0 = e < size_i - 1 ? Math.floor(e) : e - 1;
								var n0 = e0 + 1 - e;
								var n1 = e - e0;
								var offset0 = e0 * k;
								var offset1 = offset0 + k;
								for (j = 0; j < cubeVertices; j++) {
									if (j & pos) {
										cubeN[j] *= n1;
										cubeVertex[j] += offset1;
									} else {
										cubeN[j] *= n0;
										cubeVertex[j] += offset0;
									}
								}
								k *= size_i;
								pos <<= 1;
							}
							for (j = 0; j < n; ++j) {
								var rj = 0;
								for (i = 0; i < cubeVertices; i++) {
									rj += samples[cubeVertex[i] + j] * cubeN[i];
								}
								rj = interpolate(rj, 0, 1, decode[j][0], decode[j][1]);
								dest[destOffset + j] = Math.min(Math.max(rj, range[j][0]), range[j][1]);
							}
						};
					},
					constructInterpolated: function PDFFunction_constructInterpolated(str, dict) {
						var c0 = dict.getArray('C0') || [0];
						var c1 = dict.getArray('C1') || [1];
						var n = dict.get('N');
						if (!isArray(c0) || !isArray(c1)) {
							error('Illegal dictionary for interpolated function');
						}
						var length = c0.length;
						var diff = [];
						for (var i = 0; i < length; ++i) {
							diff.push(c1[i] - c0[i]);
						}
						return [
							CONSTRUCT_INTERPOLATED,
							c0,
							diff,
							n
						];
					},
					constructInterpolatedFromIR: function PDFFunction_constructInterpolatedFromIR(IR) {
						var c0 = IR[1];
						var diff = IR[2];
						var n = IR[3];
						var length = diff.length;
						return function constructInterpolatedFromIRResult(src, srcOffset, dest, destOffset) {
							var x = n === 1 ? src[srcOffset] : Math.pow(src[srcOffset], n);
							for (var j = 0; j < length; ++j) {
								dest[destOffset + j] = c0[j] + x * diff[j];
							}
						};
					},
					constructStiched: function PDFFunction_constructStiched(fn, dict, xref) {
						var domain = dict.getArray('Domain');
						if (!domain) {
							error('No domain');
						}
						var inputSize = domain.length / 2;
						if (inputSize !== 1) {
							error('Bad domain for stiched function');
						}
						var fnRefs = dict.get('Functions');
						var fns = [];
						for (var i = 0, ii = fnRefs.length; i < ii; ++i) {
							fns.push(PDFFunction.getIR(xref, xref.fetchIfRef(fnRefs[i])));
						}
						var bounds = dict.getArray('Bounds');
						var encode = dict.getArray('Encode');
						return [
							CONSTRUCT_STICHED,
							domain,
							bounds,
							encode,
							fns
						];
					},
					constructStichedFromIR: function PDFFunction_constructStichedFromIR(IR) {
						var domain = IR[1];
						var bounds = IR[2];
						var encode = IR[3];
						var fnsIR = IR[4];
						var fns = [];
						var tmpBuf = new Float32Array(1);
						for (var i = 0, ii = fnsIR.length; i < ii; i++) {
							fns.push(PDFFunction.fromIR(fnsIR[i]));
						}
						return function constructStichedFromIRResult(src, srcOffset, dest, destOffset) {
							var clip = function constructStichedFromIRClip(v, min, max) {
								if (v > max) {
									v = max;
								} else if (v < min) {
									v = min;
								}
								return v;
							};
							var v = clip(src[srcOffset], domain[0], domain[1]);
							for (var i = 0, ii = bounds.length; i < ii; ++i) {
								if (v < bounds[i]) {
									break;
								}
							}
							var dmin = domain[0];
							if (i > 0) {
								dmin = bounds[i - 1];
							}
							var dmax = domain[1];
							if (i < bounds.length) {
								dmax = bounds[i];
							}
							var rmin = encode[2 * i];
							var rmax = encode[2 * i + 1];
							tmpBuf[0] = dmin === dmax ? rmin : rmin + (v - dmin) * (rmax - rmin) / (dmax - dmin);
							fns[i](tmpBuf, 0, dest, destOffset);
						};
					},
					constructPostScript: function PDFFunction_constructPostScript(fn, dict, xref) {
						var domain = dict.getArray('Domain');
						var range = dict.getArray('Range');
						if (!domain) {
							error('No domain.');
						}
						if (!range) {
							error('No range.');
						}
						var lexer = new PostScriptLexer(fn);
						var parser = new PostScriptParser(lexer);
						var code = parser.parse();
						return [
							CONSTRUCT_POSTSCRIPT,
							domain,
							range,
							code
						];
					},
					constructPostScriptFromIR: function PDFFunction_constructPostScriptFromIR(IR) {
						var domain = IR[1];
						var range = IR[2];
						var code = IR[3];
						var compiled = new PostScriptCompiler().compile(code, domain, range);
						if (compiled) {
							return new Function('src', 'srcOffset', 'dest', 'destOffset', compiled);
						}
						info('Unable to compile PS function');
						var numOutputs = range.length >> 1;
						var numInputs = domain.length >> 1;
						var evaluator = new PostScriptEvaluator(code);
						var cache = Object.create(null);
						var MAX_CACHE_SIZE = 2048 * 4;
						var cache_available = MAX_CACHE_SIZE;
						var tmpBuf = new Float32Array(numInputs);
						return function constructPostScriptFromIRResult(src, srcOffset, dest, destOffset) {
							var i, value;
							var key = '';
							var input = tmpBuf;
							for (i = 0; i < numInputs; i++) {
								value = src[srcOffset + i];
								input[i] = value;
								key += value + '_';
							}
							var cachedValue = cache[key];
							if (cachedValue !== undefined) {
								dest.set(cachedValue, destOffset);
								return;
							}
							var output = new Float32Array(numOutputs);
							var stack = evaluator.execute(input);
							var stackIndex = stack.length - numOutputs;
							for (i = 0; i < numOutputs; i++) {
								value = stack[stackIndex + i];
								var bound = range[i * 2];
								if (value < bound) {
									value = bound;
								} else {
									bound = range[i * 2 + 1];
									if (value > bound) {
										value = bound;
									}
								}
								output[i] = value;
							}
							if (cache_available > 0) {
								cache_available--;
								cache[key] = output;
							}
							dest.set(output, destOffset);
						};
					}
				};
			}();
			function isPDFFunction(v) {
				var fnDict;
				if (typeof v !== 'object') {
					return false;
				} else if (isDict(v)) {
					fnDict = v;
				} else if (isStream(v)) {
					fnDict = v.dict;
				} else {
					return false;
				}
				return fnDict.has('FunctionType');
			}
			var PostScriptStack = function PostScriptStackClosure() {
				var MAX_STACK_SIZE = 100;
				function PostScriptStack(initialStack) {
					this.stack = !initialStack ? [] : Array.prototype.slice.call(initialStack, 0);
				}
				PostScriptStack.prototype = {
					push: function PostScriptStack_push(value) {
						if (this.stack.length >= MAX_STACK_SIZE) {
							error('PostScript function stack overflow.');
						}
						this.stack.push(value);
					},
					pop: function PostScriptStack_pop() {
						if (this.stack.length <= 0) {
							error('PostScript function stack underflow.');
						}
						return this.stack.pop();
					},
					copy: function PostScriptStack_copy(n) {
						if (this.stack.length + n >= MAX_STACK_SIZE) {
							error('PostScript function stack overflow.');
						}
						var stack = this.stack;
						for (var i = stack.length - n, j = n - 1; j >= 0; j--, i++) {
							stack.push(stack[i]);
						}
					},
					index: function PostScriptStack_index(n) {
						this.push(this.stack[this.stack.length - n - 1]);
					},
					roll: function PostScriptStack_roll(n, p) {
						var stack = this.stack;
						var l = stack.length - n;
						var r = stack.length - 1, c = l + (p - Math.floor(p / n) * n), i, j, t;
						for (i = l, j = r; i < j; i++, j--) {
							t = stack[i];
							stack[i] = stack[j];
							stack[j] = t;
						}
						for (i = l, j = c - 1; i < j; i++, j--) {
							t = stack[i];
							stack[i] = stack[j];
							stack[j] = t;
						}
						for (i = c, j = r; i < j; i++, j--) {
							t = stack[i];
							stack[i] = stack[j];
							stack[j] = t;
						}
					}
				};
				return PostScriptStack;
			}();
			var PostScriptEvaluator = function PostScriptEvaluatorClosure() {
				function PostScriptEvaluator(operators) {
					this.operators = operators;
				}
				PostScriptEvaluator.prototype = {
					execute: function PostScriptEvaluator_execute(initialStack) {
						var stack = new PostScriptStack(initialStack);
						var counter = 0;
						var operators = this.operators;
						var length = operators.length;
						var operator, a, b;
						while (counter < length) {
							operator = operators[counter++];
							if (typeof operator === 'number') {
								stack.push(operator);
								continue;
							}
							switch (operator) {
								case 'jz':
									b = stack.pop();
									a = stack.pop();
									if (!a) {
										counter = b;
									}
									break;
								case 'j':
									a = stack.pop();
									counter = a;
									break;
								case 'abs':
									a = stack.pop();
									stack.push(Math.abs(a));
									break;
								case 'add':
									b = stack.pop();
									a = stack.pop();
									stack.push(a + b);
									break;
								case 'and':
									b = stack.pop();
									a = stack.pop();
									if (isBool(a) && isBool(b)) {
										stack.push(a && b);
									} else {
										stack.push(a & b);
									}
									break;
								case 'atan':
									a = stack.pop();
									stack.push(Math.atan(a));
									break;
								case 'bitshift':
									b = stack.pop();
									a = stack.pop();
									if (a > 0) {
										stack.push(a << b);
									} else {
										stack.push(a >> b);
									}
									break;
								case 'ceiling':
									a = stack.pop();
									stack.push(Math.ceil(a));
									break;
								case 'copy':
									a = stack.pop();
									stack.copy(a);
									break;
								case 'cos':
									a = stack.pop();
									stack.push(Math.cos(a));
									break;
								case 'cvi':
									a = stack.pop() | 0;
									stack.push(a);
									break;
								case 'cvr':
									break;
								case 'div':
									b = stack.pop();
									a = stack.pop();
									stack.push(a / b);
									break;
								case 'dup':
									stack.copy(1);
									break;
								case 'eq':
									b = stack.pop();
									a = stack.pop();
									stack.push(a === b);
									break;
								case 'exch':
									stack.roll(2, 1);
									break;
								case 'exp':
									b = stack.pop();
									a = stack.pop();
									stack.push(Math.pow(a, b));
									break;
								case 'false':
									stack.push(false);
									break;
								case 'floor':
									a = stack.pop();
									stack.push(Math.floor(a));
									break;
								case 'ge':
									b = stack.pop();
									a = stack.pop();
									stack.push(a >= b);
									break;
								case 'gt':
									b = stack.pop();
									a = stack.pop();
									stack.push(a > b);
									break;
								case 'idiv':
									b = stack.pop();
									a = stack.pop();
									stack.push(a / b | 0);
									break;
								case 'index':
									a = stack.pop();
									stack.index(a);
									break;
								case 'le':
									b = stack.pop();
									a = stack.pop();
									stack.push(a <= b);
									break;
								case 'ln':
									a = stack.pop();
									stack.push(Math.log(a));
									break;
								case 'log':
									a = stack.pop();
									stack.push(Math.log(a) / Math.LN10);
									break;
								case 'lt':
									b = stack.pop();
									a = stack.pop();
									stack.push(a < b);
									break;
								case 'mod':
									b = stack.pop();
									a = stack.pop();
									stack.push(a % b);
									break;
								case 'mul':
									b = stack.pop();
									a = stack.pop();
									stack.push(a * b);
									break;
								case 'ne':
									b = stack.pop();
									a = stack.pop();
									stack.push(a !== b);
									break;
								case 'neg':
									a = stack.pop();
									stack.push(-a);
									break;
								case 'not':
									a = stack.pop();
									if (isBool(a)) {
										stack.push(!a);
									} else {
										stack.push(~a);
									}
									break;
								case 'or':
									b = stack.pop();
									a = stack.pop();
									if (isBool(a) && isBool(b)) {
										stack.push(a || b);
									} else {
										stack.push(a | b);
									}
									break;
								case 'pop':
									stack.pop();
									break;
								case 'roll':
									b = stack.pop();
									a = stack.pop();
									stack.roll(a, b);
									break;
								case 'round':
									a = stack.pop();
									stack.push(Math.round(a));
									break;
								case 'sin':
									a = stack.pop();
									stack.push(Math.sin(a));
									break;
								case 'sqrt':
									a = stack.pop();
									stack.push(Math.sqrt(a));
									break;
								case 'sub':
									b = stack.pop();
									a = stack.pop();
									stack.push(a - b);
									break;
								case 'true':
									stack.push(true);
									break;
								case 'truncate':
									a = stack.pop();
									a = a < 0 ? Math.ceil(a) : Math.floor(a);
									stack.push(a);
									break;
								case 'xor':
									b = stack.pop();
									a = stack.pop();
									if (isBool(a) && isBool(b)) {
										stack.push(a !== b);
									} else {
										stack.push(a ^ b);
									}
									break;
								default:
									error('Unknown operator ' + operator);
									break;
							}
						}
						return stack.stack;
					}
				};
				return PostScriptEvaluator;
			}();
			var PostScriptCompiler = function PostScriptCompilerClosure() {
				function AstNode(type) {
					this.type = type;
				}
				AstNode.prototype.visit = function (visitor) {
					throw new Error('abstract method');
				};
				function AstArgument(index, min, max) {
					AstNode.call(this, 'args');
					this.index = index;
					this.min = min;
					this.max = max;
				}
				AstArgument.prototype = Object.create(AstNode.prototype);
				AstArgument.prototype.visit = function (visitor) {
					visitor.visitArgument(this);
				};
				function AstLiteral(number) {
					AstNode.call(this, 'literal');
					this.number = number;
					this.min = number;
					this.max = number;
				}
				AstLiteral.prototype = Object.create(AstNode.prototype);
				AstLiteral.prototype.visit = function (visitor) {
					visitor.visitLiteral(this);
				};
				function AstBinaryOperation(op, arg1, arg2, min, max) {
					AstNode.call(this, 'binary');
					this.op = op;
					this.arg1 = arg1;
					this.arg2 = arg2;
					this.min = min;
					this.max = max;
				}
				AstBinaryOperation.prototype = Object.create(AstNode.prototype);
				AstBinaryOperation.prototype.visit = function (visitor) {
					visitor.visitBinaryOperation(this);
				};
				function AstMin(arg, max) {
					AstNode.call(this, 'max');
					this.arg = arg;
					this.min = arg.min;
					this.max = max;
				}
				AstMin.prototype = Object.create(AstNode.prototype);
				AstMin.prototype.visit = function (visitor) {
					visitor.visitMin(this);
				};
				function AstVariable(index, min, max) {
					AstNode.call(this, 'var');
					this.index = index;
					this.min = min;
					this.max = max;
				}
				AstVariable.prototype = Object.create(AstNode.prototype);
				AstVariable.prototype.visit = function (visitor) {
					visitor.visitVariable(this);
				};
				function AstVariableDefinition(variable, arg) {
					AstNode.call(this, 'definition');
					this.variable = variable;
					this.arg = arg;
				}
				AstVariableDefinition.prototype = Object.create(AstNode.prototype);
				AstVariableDefinition.prototype.visit = function (visitor) {
					visitor.visitVariableDefinition(this);
				};
				function ExpressionBuilderVisitor() {
					this.parts = [];
				}
				ExpressionBuilderVisitor.prototype = {
					visitArgument: function (arg) {
						this.parts.push('Math.max(', arg.min, ', Math.min(', arg.max, ', src[srcOffset + ', arg.index, ']))');
					},
					visitVariable: function (variable) {
						this.parts.push('v', variable.index);
					},
					visitLiteral: function (literal) {
						this.parts.push(literal.number);
					},
					visitBinaryOperation: function (operation) {
						this.parts.push('(');
						operation.arg1.visit(this);
						this.parts.push(' ', operation.op, ' ');
						operation.arg2.visit(this);
						this.parts.push(')');
					},
					visitVariableDefinition: function (definition) {
						this.parts.push('var ');
						definition.variable.visit(this);
						this.parts.push(' = ');
						definition.arg.visit(this);
						this.parts.push(';');
					},
					visitMin: function (max) {
						this.parts.push('Math.min(');
						max.arg.visit(this);
						this.parts.push(', ', max.max, ')');
					},
					toString: function () {
						return this.parts.join('');
					}
				};
				function buildAddOperation(num1, num2) {
					if (num2.type === 'literal' && num2.number === 0) {
						return num1;
					}
					if (num1.type === 'literal' && num1.number === 0) {
						return num2;
					}
					if (num2.type === 'literal' && num1.type === 'literal') {
						return new AstLiteral(num1.number + num2.number);
					}
					return new AstBinaryOperation('+', num1, num2, num1.min + num2.min, num1.max + num2.max);
				}
				function buildMulOperation(num1, num2) {
					if (num2.type === 'literal') {
						if (num2.number === 0) {
							return new AstLiteral(0);
						} else if (num2.number === 1) {
							return num1;
						} else if (num1.type === 'literal') {
							return new AstLiteral(num1.number * num2.number);
						}
					}
					if (num1.type === 'literal') {
						if (num1.number === 0) {
							return new AstLiteral(0);
						} else if (num1.number === 1) {
							return num2;
						}
					}
					var min = Math.min(num1.min * num2.min, num1.min * num2.max, num1.max * num2.min, num1.max * num2.max);
					var max = Math.max(num1.min * num2.min, num1.min * num2.max, num1.max * num2.min, num1.max * num2.max);
					return new AstBinaryOperation('*', num1, num2, min, max);
				}
				function buildSubOperation(num1, num2) {
					if (num2.type === 'literal') {
						if (num2.number === 0) {
							return num1;
						} else if (num1.type === 'literal') {
							return new AstLiteral(num1.number - num2.number);
						}
					}
					if (num2.type === 'binary' && num2.op === '-' && num1.type === 'literal' && num1.number === 1 && num2.arg1.type === 'literal' && num2.arg1.number === 1) {
						return num2.arg2;
					}
					return new AstBinaryOperation('-', num1, num2, num1.min - num2.max, num1.max - num2.min);
				}
				function buildMinOperation(num1, max) {
					if (num1.min >= max) {
						return new AstLiteral(max);
					} else if (num1.max <= max) {
						return num1;
					}
					return new AstMin(num1, max);
				}
				function PostScriptCompiler() {
				}
				PostScriptCompiler.prototype = {
					compile: function PostScriptCompiler_compile(code, domain, range) {
						var stack = [];
						var i, ii;
						var instructions = [];
						var inputSize = domain.length >> 1, outputSize = range.length >> 1;
						var lastRegister = 0;
						var n, j;
						var num1, num2, ast1, ast2, tmpVar, item;
						for (i = 0; i < inputSize; i++) {
							stack.push(new AstArgument(i, domain[i * 2], domain[i * 2 + 1]));
						}
						for (i = 0, ii = code.length; i < ii; i++) {
							item = code[i];
							if (typeof item === 'number') {
								stack.push(new AstLiteral(item));
								continue;
							}
							switch (item) {
								case 'add':
									if (stack.length < 2) {
										return null;
									}
									num2 = stack.pop();
									num1 = stack.pop();
									stack.push(buildAddOperation(num1, num2));
									break;
								case 'cvr':
									if (stack.length < 1) {
										return null;
									}
									break;
								case 'mul':
									if (stack.length < 2) {
										return null;
									}
									num2 = stack.pop();
									num1 = stack.pop();
									stack.push(buildMulOperation(num1, num2));
									break;
								case 'sub':
									if (stack.length < 2) {
										return null;
									}
									num2 = stack.pop();
									num1 = stack.pop();
									stack.push(buildSubOperation(num1, num2));
									break;
								case 'exch':
									if (stack.length < 2) {
										return null;
									}
									ast1 = stack.pop();
									ast2 = stack.pop();
									stack.push(ast1, ast2);
									break;
								case 'pop':
									if (stack.length < 1) {
										return null;
									}
									stack.pop();
									break;
								case 'index':
									if (stack.length < 1) {
										return null;
									}
									num1 = stack.pop();
									if (num1.type !== 'literal') {
										return null;
									}
									n = num1.number;
									if (n < 0 || (n | 0) !== n || stack.length < n) {
										return null;
									}
									ast1 = stack[stack.length - n - 1];
									if (ast1.type === 'literal' || ast1.type === 'var') {
										stack.push(ast1);
										break;
									}
									tmpVar = new AstVariable(lastRegister++, ast1.min, ast1.max);
									stack[stack.length - n - 1] = tmpVar;
									stack.push(tmpVar);
									instructions.push(new AstVariableDefinition(tmpVar, ast1));
									break;
								case 'dup':
									if (stack.length < 1) {
										return null;
									}
									if (typeof code[i + 1] === 'number' && code[i + 2] === 'gt' && code[i + 3] === i + 7 && code[i + 4] === 'jz' && code[i + 5] === 'pop' && code[i + 6] === code[i + 1]) {
										num1 = stack.pop();
										stack.push(buildMinOperation(num1, code[i + 1]));
										i += 6;
										break;
									}
									ast1 = stack[stack.length - 1];
									if (ast1.type === 'literal' || ast1.type === 'var') {
										stack.push(ast1);
										break;
									}
									tmpVar = new AstVariable(lastRegister++, ast1.min, ast1.max);
									stack[stack.length - 1] = tmpVar;
									stack.push(tmpVar);
									instructions.push(new AstVariableDefinition(tmpVar, ast1));
									break;
								case 'roll':
									if (stack.length < 2) {
										return null;
									}
									num2 = stack.pop();
									num1 = stack.pop();
									if (num2.type !== 'literal' || num1.type !== 'literal') {
										return null;
									}
									j = num2.number;
									n = num1.number;
									if (n <= 0 || (n | 0) !== n || (j | 0) !== j || stack.length < n) {
										return null;
									}
									j = (j % n + n) % n;
									if (j === 0) {
										break;
									}
									Array.prototype.push.apply(stack, stack.splice(stack.length - n, n - j));
									break;
								default:
									return null;
							}
						}
						if (stack.length !== outputSize) {
							return null;
						}
						var result = [];
						instructions.forEach(function (instruction) {
							var statementBuilder = new ExpressionBuilderVisitor();
							instruction.visit(statementBuilder);
							result.push(statementBuilder.toString());
						});
						stack.forEach(function (expr, i) {
							var statementBuilder = new ExpressionBuilderVisitor();
							expr.visit(statementBuilder);
							var min = range[i * 2], max = range[i * 2 + 1];
							var out = [statementBuilder.toString()];
							if (min > expr.min) {
								out.unshift('Math.max(', min, ', ');
								out.push(')');
							}
							if (max < expr.max) {
								out.unshift('Math.min(', max, ', ');
								out.push(')');
							}
							out.unshift('dest[destOffset + ', i, '] = ');
							out.push(';');
							result.push(out.join(''));
						});
						return result.join('\n');
					}
				};
				return PostScriptCompiler;
			}();
			exports.isPDFFunction = isPDFFunction;
			exports.PDFFunction = PDFFunction;
			exports.PostScriptEvaluator = PostScriptEvaluator;
			exports.PostScriptCompiler = PostScriptCompiler;

			/***/
		}),
/* 7 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var getLookupTableFactory = sharedUtil.getLookupTableFactory;
			var getGlyphsUnicode = getLookupTableFactory(function (t) {
				t['A'] = 0x0041;
				t['AE'] = 0x00C6;
				t['AEacute'] = 0x01FC;
				t['AEmacron'] = 0x01E2;
				t['AEsmall'] = 0xF7E6;
				t['Aacute'] = 0x00C1;
				t['Aacutesmall'] = 0xF7E1;
				t['Abreve'] = 0x0102;
				t['Abreveacute'] = 0x1EAE;
				t['Abrevecyrillic'] = 0x04D0;
				t['Abrevedotbelow'] = 0x1EB6;
				t['Abrevegrave'] = 0x1EB0;
				t['Abrevehookabove'] = 0x1EB2;
				t['Abrevetilde'] = 0x1EB4;
				t['Acaron'] = 0x01CD;
				t['Acircle'] = 0x24B6;
				t['Acircumflex'] = 0x00C2;
				t['Acircumflexacute'] = 0x1EA4;
				t['Acircumflexdotbelow'] = 0x1EAC;
				t['Acircumflexgrave'] = 0x1EA6;
				t['Acircumflexhookabove'] = 0x1EA8;
				t['Acircumflexsmall'] = 0xF7E2;
				t['Acircumflextilde'] = 0x1EAA;
				t['Acute'] = 0xF6C9;
				t['Acutesmall'] = 0xF7B4;
				t['Acyrillic'] = 0x0410;
				t['Adblgrave'] = 0x0200;
				t['Adieresis'] = 0x00C4;
				t['Adieresiscyrillic'] = 0x04D2;
				t['Adieresismacron'] = 0x01DE;
				t['Adieresissmall'] = 0xF7E4;
				t['Adotbelow'] = 0x1EA0;
				t['Adotmacron'] = 0x01E0;
				t['Agrave'] = 0x00C0;
				t['Agravesmall'] = 0xF7E0;
				t['Ahookabove'] = 0x1EA2;
				t['Aiecyrillic'] = 0x04D4;
				t['Ainvertedbreve'] = 0x0202;
				t['Alpha'] = 0x0391;
				t['Alphatonos'] = 0x0386;
				t['Amacron'] = 0x0100;
				t['Amonospace'] = 0xFF21;
				t['Aogonek'] = 0x0104;
				t['Aring'] = 0x00C5;
				t['Aringacute'] = 0x01FA;
				t['Aringbelow'] = 0x1E00;
				t['Aringsmall'] = 0xF7E5;
				t['Asmall'] = 0xF761;
				t['Atilde'] = 0x00C3;
				t['Atildesmall'] = 0xF7E3;
				t['Aybarmenian'] = 0x0531;
				t['B'] = 0x0042;
				t['Bcircle'] = 0x24B7;
				t['Bdotaccent'] = 0x1E02;
				t['Bdotbelow'] = 0x1E04;
				t['Becyrillic'] = 0x0411;
				t['Benarmenian'] = 0x0532;
				t['Beta'] = 0x0392;
				t['Bhook'] = 0x0181;
				t['Blinebelow'] = 0x1E06;
				t['Bmonospace'] = 0xFF22;
				t['Brevesmall'] = 0xF6F4;
				t['Bsmall'] = 0xF762;
				t['Btopbar'] = 0x0182;
				t['C'] = 0x0043;
				t['Caarmenian'] = 0x053E;
				t['Cacute'] = 0x0106;
				t['Caron'] = 0xF6CA;
				t['Caronsmall'] = 0xF6F5;
				t['Ccaron'] = 0x010C;
				t['Ccedilla'] = 0x00C7;
				t['Ccedillaacute'] = 0x1E08;
				t['Ccedillasmall'] = 0xF7E7;
				t['Ccircle'] = 0x24B8;
				t['Ccircumflex'] = 0x0108;
				t['Cdot'] = 0x010A;
				t['Cdotaccent'] = 0x010A;
				t['Cedillasmall'] = 0xF7B8;
				t['Chaarmenian'] = 0x0549;
				t['Cheabkhasiancyrillic'] = 0x04BC;
				t['Checyrillic'] = 0x0427;
				t['Chedescenderabkhasiancyrillic'] = 0x04BE;
				t['Chedescendercyrillic'] = 0x04B6;
				t['Chedieresiscyrillic'] = 0x04F4;
				t['Cheharmenian'] = 0x0543;
				t['Chekhakassiancyrillic'] = 0x04CB;
				t['Cheverticalstrokecyrillic'] = 0x04B8;
				t['Chi'] = 0x03A7;
				t['Chook'] = 0x0187;
				t['Circumflexsmall'] = 0xF6F6;
				t['Cmonospace'] = 0xFF23;
				t['Coarmenian'] = 0x0551;
				t['Csmall'] = 0xF763;
				t['D'] = 0x0044;
				t['DZ'] = 0x01F1;
				t['DZcaron'] = 0x01C4;
				t['Daarmenian'] = 0x0534;
				t['Dafrican'] = 0x0189;
				t['Dcaron'] = 0x010E;
				t['Dcedilla'] = 0x1E10;
				t['Dcircle'] = 0x24B9;
				t['Dcircumflexbelow'] = 0x1E12;
				t['Dcroat'] = 0x0110;
				t['Ddotaccent'] = 0x1E0A;
				t['Ddotbelow'] = 0x1E0C;
				t['Decyrillic'] = 0x0414;
				t['Deicoptic'] = 0x03EE;
				t['Delta'] = 0x2206;
				t['Deltagreek'] = 0x0394;
				t['Dhook'] = 0x018A;
				t['Dieresis'] = 0xF6CB;
				t['DieresisAcute'] = 0xF6CC;
				t['DieresisGrave'] = 0xF6CD;
				t['Dieresissmall'] = 0xF7A8;
				t['Digammagreek'] = 0x03DC;
				t['Djecyrillic'] = 0x0402;
				t['Dlinebelow'] = 0x1E0E;
				t['Dmonospace'] = 0xFF24;
				t['Dotaccentsmall'] = 0xF6F7;
				t['Dslash'] = 0x0110;
				t['Dsmall'] = 0xF764;
				t['Dtopbar'] = 0x018B;
				t['Dz'] = 0x01F2;
				t['Dzcaron'] = 0x01C5;
				t['Dzeabkhasiancyrillic'] = 0x04E0;
				t['Dzecyrillic'] = 0x0405;
				t['Dzhecyrillic'] = 0x040F;
				t['E'] = 0x0045;
				t['Eacute'] = 0x00C9;
				t['Eacutesmall'] = 0xF7E9;
				t['Ebreve'] = 0x0114;
				t['Ecaron'] = 0x011A;
				t['Ecedillabreve'] = 0x1E1C;
				t['Echarmenian'] = 0x0535;
				t['Ecircle'] = 0x24BA;
				t['Ecircumflex'] = 0x00CA;
				t['Ecircumflexacute'] = 0x1EBE;
				t['Ecircumflexbelow'] = 0x1E18;
				t['Ecircumflexdotbelow'] = 0x1EC6;
				t['Ecircumflexgrave'] = 0x1EC0;
				t['Ecircumflexhookabove'] = 0x1EC2;
				t['Ecircumflexsmall'] = 0xF7EA;
				t['Ecircumflextilde'] = 0x1EC4;
				t['Ecyrillic'] = 0x0404;
				t['Edblgrave'] = 0x0204;
				t['Edieresis'] = 0x00CB;
				t['Edieresissmall'] = 0xF7EB;
				t['Edot'] = 0x0116;
				t['Edotaccent'] = 0x0116;
				t['Edotbelow'] = 0x1EB8;
				t['Efcyrillic'] = 0x0424;
				t['Egrave'] = 0x00C8;
				t['Egravesmall'] = 0xF7E8;
				t['Eharmenian'] = 0x0537;
				t['Ehookabove'] = 0x1EBA;
				t['Eightroman'] = 0x2167;
				t['Einvertedbreve'] = 0x0206;
				t['Eiotifiedcyrillic'] = 0x0464;
				t['Elcyrillic'] = 0x041B;
				t['Elevenroman'] = 0x216A;
				t['Emacron'] = 0x0112;
				t['Emacronacute'] = 0x1E16;
				t['Emacrongrave'] = 0x1E14;
				t['Emcyrillic'] = 0x041C;
				t['Emonospace'] = 0xFF25;
				t['Encyrillic'] = 0x041D;
				t['Endescendercyrillic'] = 0x04A2;
				t['Eng'] = 0x014A;
				t['Enghecyrillic'] = 0x04A4;
				t['Enhookcyrillic'] = 0x04C7;
				t['Eogonek'] = 0x0118;
				t['Eopen'] = 0x0190;
				t['Epsilon'] = 0x0395;
				t['Epsilontonos'] = 0x0388;
				t['Ercyrillic'] = 0x0420;
				t['Ereversed'] = 0x018E;
				t['Ereversedcyrillic'] = 0x042D;
				t['Escyrillic'] = 0x0421;
				t['Esdescendercyrillic'] = 0x04AA;
				t['Esh'] = 0x01A9;
				t['Esmall'] = 0xF765;
				t['Eta'] = 0x0397;
				t['Etarmenian'] = 0x0538;
				t['Etatonos'] = 0x0389;
				t['Eth'] = 0x00D0;
				t['Ethsmall'] = 0xF7F0;
				t['Etilde'] = 0x1EBC;
				t['Etildebelow'] = 0x1E1A;
				t['Euro'] = 0x20AC;
				t['Ezh'] = 0x01B7;
				t['Ezhcaron'] = 0x01EE;
				t['Ezhreversed'] = 0x01B8;
				t['F'] = 0x0046;
				t['Fcircle'] = 0x24BB;
				t['Fdotaccent'] = 0x1E1E;
				t['Feharmenian'] = 0x0556;
				t['Feicoptic'] = 0x03E4;
				t['Fhook'] = 0x0191;
				t['Fitacyrillic'] = 0x0472;
				t['Fiveroman'] = 0x2164;
				t['Fmonospace'] = 0xFF26;
				t['Fourroman'] = 0x2163;
				t['Fsmall'] = 0xF766;
				t['G'] = 0x0047;
				t['GBsquare'] = 0x3387;
				t['Gacute'] = 0x01F4;
				t['Gamma'] = 0x0393;
				t['Gammaafrican'] = 0x0194;
				t['Gangiacoptic'] = 0x03EA;
				t['Gbreve'] = 0x011E;
				t['Gcaron'] = 0x01E6;
				t['Gcedilla'] = 0x0122;
				t['Gcircle'] = 0x24BC;
				t['Gcircumflex'] = 0x011C;
				t['Gcommaaccent'] = 0x0122;
				t['Gdot'] = 0x0120;
				t['Gdotaccent'] = 0x0120;
				t['Gecyrillic'] = 0x0413;
				t['Ghadarmenian'] = 0x0542;
				t['Ghemiddlehookcyrillic'] = 0x0494;
				t['Ghestrokecyrillic'] = 0x0492;
				t['Gheupturncyrillic'] = 0x0490;
				t['Ghook'] = 0x0193;
				t['Gimarmenian'] = 0x0533;
				t['Gjecyrillic'] = 0x0403;
				t['Gmacron'] = 0x1E20;
				t['Gmonospace'] = 0xFF27;
				t['Grave'] = 0xF6CE;
				t['Gravesmall'] = 0xF760;
				t['Gsmall'] = 0xF767;
				t['Gsmallhook'] = 0x029B;
				t['Gstroke'] = 0x01E4;
				t['H'] = 0x0048;
				t['H18533'] = 0x25CF;
				t['H18543'] = 0x25AA;
				t['H18551'] = 0x25AB;
				t['H22073'] = 0x25A1;
				t['HPsquare'] = 0x33CB;
				t['Haabkhasiancyrillic'] = 0x04A8;
				t['Hadescendercyrillic'] = 0x04B2;
				t['Hardsigncyrillic'] = 0x042A;
				t['Hbar'] = 0x0126;
				t['Hbrevebelow'] = 0x1E2A;
				t['Hcedilla'] = 0x1E28;
				t['Hcircle'] = 0x24BD;
				t['Hcircumflex'] = 0x0124;
				t['Hdieresis'] = 0x1E26;
				t['Hdotaccent'] = 0x1E22;
				t['Hdotbelow'] = 0x1E24;
				t['Hmonospace'] = 0xFF28;
				t['Hoarmenian'] = 0x0540;
				t['Horicoptic'] = 0x03E8;
				t['Hsmall'] = 0xF768;
				t['Hungarumlaut'] = 0xF6CF;
				t['Hungarumlautsmall'] = 0xF6F8;
				t['Hzsquare'] = 0x3390;
				t['I'] = 0x0049;
				t['IAcyrillic'] = 0x042F;
				t['IJ'] = 0x0132;
				t['IUcyrillic'] = 0x042E;
				t['Iacute'] = 0x00CD;
				t['Iacutesmall'] = 0xF7ED;
				t['Ibreve'] = 0x012C;
				t['Icaron'] = 0x01CF;
				t['Icircle'] = 0x24BE;
				t['Icircumflex'] = 0x00CE;
				t['Icircumflexsmall'] = 0xF7EE;
				t['Icyrillic'] = 0x0406;
				t['Idblgrave'] = 0x0208;
				t['Idieresis'] = 0x00CF;
				t['Idieresisacute'] = 0x1E2E;
				t['Idieresiscyrillic'] = 0x04E4;
				t['Idieresissmall'] = 0xF7EF;
				t['Idot'] = 0x0130;
				t['Idotaccent'] = 0x0130;
				t['Idotbelow'] = 0x1ECA;
				t['Iebrevecyrillic'] = 0x04D6;
				t['Iecyrillic'] = 0x0415;
				t['Ifraktur'] = 0x2111;
				t['Igrave'] = 0x00CC;
				t['Igravesmall'] = 0xF7EC;
				t['Ihookabove'] = 0x1EC8;
				t['Iicyrillic'] = 0x0418;
				t['Iinvertedbreve'] = 0x020A;
				t['Iishortcyrillic'] = 0x0419;
				t['Imacron'] = 0x012A;
				t['Imacroncyrillic'] = 0x04E2;
				t['Imonospace'] = 0xFF29;
				t['Iniarmenian'] = 0x053B;
				t['Iocyrillic'] = 0x0401;
				t['Iogonek'] = 0x012E;
				t['Iota'] = 0x0399;
				t['Iotaafrican'] = 0x0196;
				t['Iotadieresis'] = 0x03AA;
				t['Iotatonos'] = 0x038A;
				t['Ismall'] = 0xF769;
				t['Istroke'] = 0x0197;
				t['Itilde'] = 0x0128;
				t['Itildebelow'] = 0x1E2C;
				t['Izhitsacyrillic'] = 0x0474;
				t['Izhitsadblgravecyrillic'] = 0x0476;
				t['J'] = 0x004A;
				t['Jaarmenian'] = 0x0541;
				t['Jcircle'] = 0x24BF;
				t['Jcircumflex'] = 0x0134;
				t['Jecyrillic'] = 0x0408;
				t['Jheharmenian'] = 0x054B;
				t['Jmonospace'] = 0xFF2A;
				t['Jsmall'] = 0xF76A;
				t['K'] = 0x004B;
				t['KBsquare'] = 0x3385;
				t['KKsquare'] = 0x33CD;
				t['Kabashkircyrillic'] = 0x04A0;
				t['Kacute'] = 0x1E30;
				t['Kacyrillic'] = 0x041A;
				t['Kadescendercyrillic'] = 0x049A;
				t['Kahookcyrillic'] = 0x04C3;
				t['Kappa'] = 0x039A;
				t['Kastrokecyrillic'] = 0x049E;
				t['Kaverticalstrokecyrillic'] = 0x049C;
				t['Kcaron'] = 0x01E8;
				t['Kcedilla'] = 0x0136;
				t['Kcircle'] = 0x24C0;
				t['Kcommaaccent'] = 0x0136;
				t['Kdotbelow'] = 0x1E32;
				t['Keharmenian'] = 0x0554;
				t['Kenarmenian'] = 0x053F;
				t['Khacyrillic'] = 0x0425;
				t['Kheicoptic'] = 0x03E6;
				t['Khook'] = 0x0198;
				t['Kjecyrillic'] = 0x040C;
				t['Klinebelow'] = 0x1E34;
				t['Kmonospace'] = 0xFF2B;
				t['Koppacyrillic'] = 0x0480;
				t['Koppagreek'] = 0x03DE;
				t['Ksicyrillic'] = 0x046E;
				t['Ksmall'] = 0xF76B;
				t['L'] = 0x004C;
				t['LJ'] = 0x01C7;
				t['LL'] = 0xF6BF;
				t['Lacute'] = 0x0139;
				t['Lambda'] = 0x039B;
				t['Lcaron'] = 0x013D;
				t['Lcedilla'] = 0x013B;
				t['Lcircle'] = 0x24C1;
				t['Lcircumflexbelow'] = 0x1E3C;
				t['Lcommaaccent'] = 0x013B;
				t['Ldot'] = 0x013F;
				t['Ldotaccent'] = 0x013F;
				t['Ldotbelow'] = 0x1E36;
				t['Ldotbelowmacron'] = 0x1E38;
				t['Liwnarmenian'] = 0x053C;
				t['Lj'] = 0x01C8;
				t['Ljecyrillic'] = 0x0409;
				t['Llinebelow'] = 0x1E3A;
				t['Lmonospace'] = 0xFF2C;
				t['Lslash'] = 0x0141;
				t['Lslashsmall'] = 0xF6F9;
				t['Lsmall'] = 0xF76C;
				t['M'] = 0x004D;
				t['MBsquare'] = 0x3386;
				t['Macron'] = 0xF6D0;
				t['Macronsmall'] = 0xF7AF;
				t['Macute'] = 0x1E3E;
				t['Mcircle'] = 0x24C2;
				t['Mdotaccent'] = 0x1E40;
				t['Mdotbelow'] = 0x1E42;
				t['Menarmenian'] = 0x0544;
				t['Mmonospace'] = 0xFF2D;
				t['Msmall'] = 0xF76D;
				t['Mturned'] = 0x019C;
				t['Mu'] = 0x039C;
				t['N'] = 0x004E;
				t['NJ'] = 0x01CA;
				t['Nacute'] = 0x0143;
				t['Ncaron'] = 0x0147;
				t['Ncedilla'] = 0x0145;
				t['Ncircle'] = 0x24C3;
				t['Ncircumflexbelow'] = 0x1E4A;
				t['Ncommaaccent'] = 0x0145;
				t['Ndotaccent'] = 0x1E44;
				t['Ndotbelow'] = 0x1E46;
				t['Nhookleft'] = 0x019D;
				t['Nineroman'] = 0x2168;
				t['Nj'] = 0x01CB;
				t['Njecyrillic'] = 0x040A;
				t['Nlinebelow'] = 0x1E48;
				t['Nmonospace'] = 0xFF2E;
				t['Nowarmenian'] = 0x0546;
				t['Nsmall'] = 0xF76E;
				t['Ntilde'] = 0x00D1;
				t['Ntildesmall'] = 0xF7F1;
				t['Nu'] = 0x039D;
				t['O'] = 0x004F;
				t['OE'] = 0x0152;
				t['OEsmall'] = 0xF6FA;
				t['Oacute'] = 0x00D3;
				t['Oacutesmall'] = 0xF7F3;
				t['Obarredcyrillic'] = 0x04E8;
				t['Obarreddieresiscyrillic'] = 0x04EA;
				t['Obreve'] = 0x014E;
				t['Ocaron'] = 0x01D1;
				t['Ocenteredtilde'] = 0x019F;
				t['Ocircle'] = 0x24C4;
				t['Ocircumflex'] = 0x00D4;
				t['Ocircumflexacute'] = 0x1ED0;
				t['Ocircumflexdotbelow'] = 0x1ED8;
				t['Ocircumflexgrave'] = 0x1ED2;
				t['Ocircumflexhookabove'] = 0x1ED4;
				t['Ocircumflexsmall'] = 0xF7F4;
				t['Ocircumflextilde'] = 0x1ED6;
				t['Ocyrillic'] = 0x041E;
				t['Odblacute'] = 0x0150;
				t['Odblgrave'] = 0x020C;
				t['Odieresis'] = 0x00D6;
				t['Odieresiscyrillic'] = 0x04E6;
				t['Odieresissmall'] = 0xF7F6;
				t['Odotbelow'] = 0x1ECC;
				t['Ogoneksmall'] = 0xF6FB;
				t['Ograve'] = 0x00D2;
				t['Ogravesmall'] = 0xF7F2;
				t['Oharmenian'] = 0x0555;
				t['Ohm'] = 0x2126;
				t['Ohookabove'] = 0x1ECE;
				t['Ohorn'] = 0x01A0;
				t['Ohornacute'] = 0x1EDA;
				t['Ohorndotbelow'] = 0x1EE2;
				t['Ohorngrave'] = 0x1EDC;
				t['Ohornhookabove'] = 0x1EDE;
				t['Ohorntilde'] = 0x1EE0;
				t['Ohungarumlaut'] = 0x0150;
				t['Oi'] = 0x01A2;
				t['Oinvertedbreve'] = 0x020E;
				t['Omacron'] = 0x014C;
				t['Omacronacute'] = 0x1E52;
				t['Omacrongrave'] = 0x1E50;
				t['Omega'] = 0x2126;
				t['Omegacyrillic'] = 0x0460;
				t['Omegagreek'] = 0x03A9;
				t['Omegaroundcyrillic'] = 0x047A;
				t['Omegatitlocyrillic'] = 0x047C;
				t['Omegatonos'] = 0x038F;
				t['Omicron'] = 0x039F;
				t['Omicrontonos'] = 0x038C;
				t['Omonospace'] = 0xFF2F;
				t['Oneroman'] = 0x2160;
				t['Oogonek'] = 0x01EA;
				t['Oogonekmacron'] = 0x01EC;
				t['Oopen'] = 0x0186;
				t['Oslash'] = 0x00D8;
				t['Oslashacute'] = 0x01FE;
				t['Oslashsmall'] = 0xF7F8;
				t['Osmall'] = 0xF76F;
				t['Ostrokeacute'] = 0x01FE;
				t['Otcyrillic'] = 0x047E;
				t['Otilde'] = 0x00D5;
				t['Otildeacute'] = 0x1E4C;
				t['Otildedieresis'] = 0x1E4E;
				t['Otildesmall'] = 0xF7F5;
				t['P'] = 0x0050;
				t['Pacute'] = 0x1E54;
				t['Pcircle'] = 0x24C5;
				t['Pdotaccent'] = 0x1E56;
				t['Pecyrillic'] = 0x041F;
				t['Peharmenian'] = 0x054A;
				t['Pemiddlehookcyrillic'] = 0x04A6;
				t['Phi'] = 0x03A6;
				t['Phook'] = 0x01A4;
				t['Pi'] = 0x03A0;
				t['Piwrarmenian'] = 0x0553;
				t['Pmonospace'] = 0xFF30;
				t['Psi'] = 0x03A8;
				t['Psicyrillic'] = 0x0470;
				t['Psmall'] = 0xF770;
				t['Q'] = 0x0051;
				t['Qcircle'] = 0x24C6;
				t['Qmonospace'] = 0xFF31;
				t['Qsmall'] = 0xF771;
				t['R'] = 0x0052;
				t['Raarmenian'] = 0x054C;
				t['Racute'] = 0x0154;
				t['Rcaron'] = 0x0158;
				t['Rcedilla'] = 0x0156;
				t['Rcircle'] = 0x24C7;
				t['Rcommaaccent'] = 0x0156;
				t['Rdblgrave'] = 0x0210;
				t['Rdotaccent'] = 0x1E58;
				t['Rdotbelow'] = 0x1E5A;
				t['Rdotbelowmacron'] = 0x1E5C;
				t['Reharmenian'] = 0x0550;
				t['Rfraktur'] = 0x211C;
				t['Rho'] = 0x03A1;
				t['Ringsmall'] = 0xF6FC;
				t['Rinvertedbreve'] = 0x0212;
				t['Rlinebelow'] = 0x1E5E;
				t['Rmonospace'] = 0xFF32;
				t['Rsmall'] = 0xF772;
				t['Rsmallinverted'] = 0x0281;
				t['Rsmallinvertedsuperior'] = 0x02B6;
				t['S'] = 0x0053;
				t['SF010000'] = 0x250C;
				t['SF020000'] = 0x2514;
				t['SF030000'] = 0x2510;
				t['SF040000'] = 0x2518;
				t['SF050000'] = 0x253C;
				t['SF060000'] = 0x252C;
				t['SF070000'] = 0x2534;
				t['SF080000'] = 0x251C;
				t['SF090000'] = 0x2524;
				t['SF100000'] = 0x2500;
				t['SF110000'] = 0x2502;
				t['SF190000'] = 0x2561;
				t['SF200000'] = 0x2562;
				t['SF210000'] = 0x2556;
				t['SF220000'] = 0x2555;
				t['SF230000'] = 0x2563;
				t['SF240000'] = 0x2551;
				t['SF250000'] = 0x2557;
				t['SF260000'] = 0x255D;
				t['SF270000'] = 0x255C;
				t['SF280000'] = 0x255B;
				t['SF360000'] = 0x255E;
				t['SF370000'] = 0x255F;
				t['SF380000'] = 0x255A;
				t['SF390000'] = 0x2554;
				t['SF400000'] = 0x2569;
				t['SF410000'] = 0x2566;
				t['SF420000'] = 0x2560;
				t['SF430000'] = 0x2550;
				t['SF440000'] = 0x256C;
				t['SF450000'] = 0x2567;
				t['SF460000'] = 0x2568;
				t['SF470000'] = 0x2564;
				t['SF480000'] = 0x2565;
				t['SF490000'] = 0x2559;
				t['SF500000'] = 0x2558;
				t['SF510000'] = 0x2552;
				t['SF520000'] = 0x2553;
				t['SF530000'] = 0x256B;
				t['SF540000'] = 0x256A;
				t['Sacute'] = 0x015A;
				t['Sacutedotaccent'] = 0x1E64;
				t['Sampigreek'] = 0x03E0;
				t['Scaron'] = 0x0160;
				t['Scarondotaccent'] = 0x1E66;
				t['Scaronsmall'] = 0xF6FD;
				t['Scedilla'] = 0x015E;
				t['Schwa'] = 0x018F;
				t['Schwacyrillic'] = 0x04D8;
				t['Schwadieresiscyrillic'] = 0x04DA;
				t['Scircle'] = 0x24C8;
				t['Scircumflex'] = 0x015C;
				t['Scommaaccent'] = 0x0218;
				t['Sdotaccent'] = 0x1E60;
				t['Sdotbelow'] = 0x1E62;
				t['Sdotbelowdotaccent'] = 0x1E68;
				t['Seharmenian'] = 0x054D;
				t['Sevenroman'] = 0x2166;
				t['Shaarmenian'] = 0x0547;
				t['Shacyrillic'] = 0x0428;
				t['Shchacyrillic'] = 0x0429;
				t['Sheicoptic'] = 0x03E2;
				t['Shhacyrillic'] = 0x04BA;
				t['Shimacoptic'] = 0x03EC;
				t['Sigma'] = 0x03A3;
				t['Sixroman'] = 0x2165;
				t['Smonospace'] = 0xFF33;
				t['Softsigncyrillic'] = 0x042C;
				t['Ssmall'] = 0xF773;
				t['Stigmagreek'] = 0x03DA;
				t['T'] = 0x0054;
				t['Tau'] = 0x03A4;
				t['Tbar'] = 0x0166;
				t['Tcaron'] = 0x0164;
				t['Tcedilla'] = 0x0162;
				t['Tcircle'] = 0x24C9;
				t['Tcircumflexbelow'] = 0x1E70;
				t['Tcommaaccent'] = 0x0162;
				t['Tdotaccent'] = 0x1E6A;
				t['Tdotbelow'] = 0x1E6C;
				t['Tecyrillic'] = 0x0422;
				t['Tedescendercyrillic'] = 0x04AC;
				t['Tenroman'] = 0x2169;
				t['Tetsecyrillic'] = 0x04B4;
				t['Theta'] = 0x0398;
				t['Thook'] = 0x01AC;
				t['Thorn'] = 0x00DE;
				t['Thornsmall'] = 0xF7FE;
				t['Threeroman'] = 0x2162;
				t['Tildesmall'] = 0xF6FE;
				t['Tiwnarmenian'] = 0x054F;
				t['Tlinebelow'] = 0x1E6E;
				t['Tmonospace'] = 0xFF34;
				t['Toarmenian'] = 0x0539;
				t['Tonefive'] = 0x01BC;
				t['Tonesix'] = 0x0184;
				t['Tonetwo'] = 0x01A7;
				t['Tretroflexhook'] = 0x01AE;
				t['Tsecyrillic'] = 0x0426;
				t['Tshecyrillic'] = 0x040B;
				t['Tsmall'] = 0xF774;
				t['Twelveroman'] = 0x216B;
				t['Tworoman'] = 0x2161;
				t['U'] = 0x0055;
				t['Uacute'] = 0x00DA;
				t['Uacutesmall'] = 0xF7FA;
				t['Ubreve'] = 0x016C;
				t['Ucaron'] = 0x01D3;
				t['Ucircle'] = 0x24CA;
				t['Ucircumflex'] = 0x00DB;
				t['Ucircumflexbelow'] = 0x1E76;
				t['Ucircumflexsmall'] = 0xF7FB;
				t['Ucyrillic'] = 0x0423;
				t['Udblacute'] = 0x0170;
				t['Udblgrave'] = 0x0214;
				t['Udieresis'] = 0x00DC;
				t['Udieresisacute'] = 0x01D7;
				t['Udieresisbelow'] = 0x1E72;
				t['Udieresiscaron'] = 0x01D9;
				t['Udieresiscyrillic'] = 0x04F0;
				t['Udieresisgrave'] = 0x01DB;
				t['Udieresismacron'] = 0x01D5;
				t['Udieresissmall'] = 0xF7FC;
				t['Udotbelow'] = 0x1EE4;
				t['Ugrave'] = 0x00D9;
				t['Ugravesmall'] = 0xF7F9;
				t['Uhookabove'] = 0x1EE6;
				t['Uhorn'] = 0x01AF;
				t['Uhornacute'] = 0x1EE8;
				t['Uhorndotbelow'] = 0x1EF0;
				t['Uhorngrave'] = 0x1EEA;
				t['Uhornhookabove'] = 0x1EEC;
				t['Uhorntilde'] = 0x1EEE;
				t['Uhungarumlaut'] = 0x0170;
				t['Uhungarumlautcyrillic'] = 0x04F2;
				t['Uinvertedbreve'] = 0x0216;
				t['Ukcyrillic'] = 0x0478;
				t['Umacron'] = 0x016A;
				t['Umacroncyrillic'] = 0x04EE;
				t['Umacrondieresis'] = 0x1E7A;
				t['Umonospace'] = 0xFF35;
				t['Uogonek'] = 0x0172;
				t['Upsilon'] = 0x03A5;
				t['Upsilon1'] = 0x03D2;
				t['Upsilonacutehooksymbolgreek'] = 0x03D3;
				t['Upsilonafrican'] = 0x01B1;
				t['Upsilondieresis'] = 0x03AB;
				t['Upsilondieresishooksymbolgreek'] = 0x03D4;
				t['Upsilonhooksymbol'] = 0x03D2;
				t['Upsilontonos'] = 0x038E;
				t['Uring'] = 0x016E;
				t['Ushortcyrillic'] = 0x040E;
				t['Usmall'] = 0xF775;
				t['Ustraightcyrillic'] = 0x04AE;
				t['Ustraightstrokecyrillic'] = 0x04B0;
				t['Utilde'] = 0x0168;
				t['Utildeacute'] = 0x1E78;
				t['Utildebelow'] = 0x1E74;
				t['V'] = 0x0056;
				t['Vcircle'] = 0x24CB;
				t['Vdotbelow'] = 0x1E7E;
				t['Vecyrillic'] = 0x0412;
				t['Vewarmenian'] = 0x054E;
				t['Vhook'] = 0x01B2;
				t['Vmonospace'] = 0xFF36;
				t['Voarmenian'] = 0x0548;
				t['Vsmall'] = 0xF776;
				t['Vtilde'] = 0x1E7C;
				t['W'] = 0x0057;
				t['Wacute'] = 0x1E82;
				t['Wcircle'] = 0x24CC;
				t['Wcircumflex'] = 0x0174;
				t['Wdieresis'] = 0x1E84;
				t['Wdotaccent'] = 0x1E86;
				t['Wdotbelow'] = 0x1E88;
				t['Wgrave'] = 0x1E80;
				t['Wmonospace'] = 0xFF37;
				t['Wsmall'] = 0xF777;
				t['X'] = 0x0058;
				t['Xcircle'] = 0x24CD;
				t['Xdieresis'] = 0x1E8C;
				t['Xdotaccent'] = 0x1E8A;
				t['Xeharmenian'] = 0x053D;
				t['Xi'] = 0x039E;
				t['Xmonospace'] = 0xFF38;
				t['Xsmall'] = 0xF778;
				t['Y'] = 0x0059;
				t['Yacute'] = 0x00DD;
				t['Yacutesmall'] = 0xF7FD;
				t['Yatcyrillic'] = 0x0462;
				t['Ycircle'] = 0x24CE;
				t['Ycircumflex'] = 0x0176;
				t['Ydieresis'] = 0x0178;
				t['Ydieresissmall'] = 0xF7FF;
				t['Ydotaccent'] = 0x1E8E;
				t['Ydotbelow'] = 0x1EF4;
				t['Yericyrillic'] = 0x042B;
				t['Yerudieresiscyrillic'] = 0x04F8;
				t['Ygrave'] = 0x1EF2;
				t['Yhook'] = 0x01B3;
				t['Yhookabove'] = 0x1EF6;
				t['Yiarmenian'] = 0x0545;
				t['Yicyrillic'] = 0x0407;
				t['Yiwnarmenian'] = 0x0552;
				t['Ymonospace'] = 0xFF39;
				t['Ysmall'] = 0xF779;
				t['Ytilde'] = 0x1EF8;
				t['Yusbigcyrillic'] = 0x046A;
				t['Yusbigiotifiedcyrillic'] = 0x046C;
				t['Yuslittlecyrillic'] = 0x0466;
				t['Yuslittleiotifiedcyrillic'] = 0x0468;
				t['Z'] = 0x005A;
				t['Zaarmenian'] = 0x0536;
				t['Zacute'] = 0x0179;
				t['Zcaron'] = 0x017D;
				t['Zcaronsmall'] = 0xF6FF;
				t['Zcircle'] = 0x24CF;
				t['Zcircumflex'] = 0x1E90;
				t['Zdot'] = 0x017B;
				t['Zdotaccent'] = 0x017B;
				t['Zdotbelow'] = 0x1E92;
				t['Zecyrillic'] = 0x0417;
				t['Zedescendercyrillic'] = 0x0498;
				t['Zedieresiscyrillic'] = 0x04DE;
				t['Zeta'] = 0x0396;
				t['Zhearmenian'] = 0x053A;
				t['Zhebrevecyrillic'] = 0x04C1;
				t['Zhecyrillic'] = 0x0416;
				t['Zhedescendercyrillic'] = 0x0496;
				t['Zhedieresiscyrillic'] = 0x04DC;
				t['Zlinebelow'] = 0x1E94;
				t['Zmonospace'] = 0xFF3A;
				t['Zsmall'] = 0xF77A;
				t['Zstroke'] = 0x01B5;
				t['a'] = 0x0061;
				t['aabengali'] = 0x0986;
				t['aacute'] = 0x00E1;
				t['aadeva'] = 0x0906;
				t['aagujarati'] = 0x0A86;
				t['aagurmukhi'] = 0x0A06;
				t['aamatragurmukhi'] = 0x0A3E;
				t['aarusquare'] = 0x3303;
				t['aavowelsignbengali'] = 0x09BE;
				t['aavowelsigndeva'] = 0x093E;
				t['aavowelsigngujarati'] = 0x0ABE;
				t['abbreviationmarkarmenian'] = 0x055F;
				t['abbreviationsigndeva'] = 0x0970;
				t['abengali'] = 0x0985;
				t['abopomofo'] = 0x311A;
				t['abreve'] = 0x0103;
				t['abreveacute'] = 0x1EAF;
				t['abrevecyrillic'] = 0x04D1;
				t['abrevedotbelow'] = 0x1EB7;
				t['abrevegrave'] = 0x1EB1;
				t['abrevehookabove'] = 0x1EB3;
				t['abrevetilde'] = 0x1EB5;
				t['acaron'] = 0x01CE;
				t['acircle'] = 0x24D0;
				t['acircumflex'] = 0x00E2;
				t['acircumflexacute'] = 0x1EA5;
				t['acircumflexdotbelow'] = 0x1EAD;
				t['acircumflexgrave'] = 0x1EA7;
				t['acircumflexhookabove'] = 0x1EA9;
				t['acircumflextilde'] = 0x1EAB;
				t['acute'] = 0x00B4;
				t['acutebelowcmb'] = 0x0317;
				t['acutecmb'] = 0x0301;
				t['acutecomb'] = 0x0301;
				t['acutedeva'] = 0x0954;
				t['acutelowmod'] = 0x02CF;
				t['acutetonecmb'] = 0x0341;
				t['acyrillic'] = 0x0430;
				t['adblgrave'] = 0x0201;
				t['addakgurmukhi'] = 0x0A71;
				t['adeva'] = 0x0905;
				t['adieresis'] = 0x00E4;
				t['adieresiscyrillic'] = 0x04D3;
				t['adieresismacron'] = 0x01DF;
				t['adotbelow'] = 0x1EA1;
				t['adotmacron'] = 0x01E1;
				t['ae'] = 0x00E6;
				t['aeacute'] = 0x01FD;
				t['aekorean'] = 0x3150;
				t['aemacron'] = 0x01E3;
				t['afii00208'] = 0x2015;
				t['afii08941'] = 0x20A4;
				t['afii10017'] = 0x0410;
				t['afii10018'] = 0x0411;
				t['afii10019'] = 0x0412;
				t['afii10020'] = 0x0413;
				t['afii10021'] = 0x0414;
				t['afii10022'] = 0x0415;
				t['afii10023'] = 0x0401;
				t['afii10024'] = 0x0416;
				t['afii10025'] = 0x0417;
				t['afii10026'] = 0x0418;
				t['afii10027'] = 0x0419;
				t['afii10028'] = 0x041A;
				t['afii10029'] = 0x041B;
				t['afii10030'] = 0x041C;
				t['afii10031'] = 0x041D;
				t['afii10032'] = 0x041E;
				t['afii10033'] = 0x041F;
				t['afii10034'] = 0x0420;
				t['afii10035'] = 0x0421;
				t['afii10036'] = 0x0422;
				t['afii10037'] = 0x0423;
				t['afii10038'] = 0x0424;
				t['afii10039'] = 0x0425;
				t['afii10040'] = 0x0426;
				t['afii10041'] = 0x0427;
				t['afii10042'] = 0x0428;
				t['afii10043'] = 0x0429;
				t['afii10044'] = 0x042A;
				t['afii10045'] = 0x042B;
				t['afii10046'] = 0x042C;
				t['afii10047'] = 0x042D;
				t['afii10048'] = 0x042E;
				t['afii10049'] = 0x042F;
				t['afii10050'] = 0x0490;
				t['afii10051'] = 0x0402;
				t['afii10052'] = 0x0403;
				t['afii10053'] = 0x0404;
				t['afii10054'] = 0x0405;
				t['afii10055'] = 0x0406;
				t['afii10056'] = 0x0407;
				t['afii10057'] = 0x0408;
				t['afii10058'] = 0x0409;
				t['afii10059'] = 0x040A;
				t['afii10060'] = 0x040B;
				t['afii10061'] = 0x040C;
				t['afii10062'] = 0x040E;
				t['afii10063'] = 0xF6C4;
				t['afii10064'] = 0xF6C5;
				t['afii10065'] = 0x0430;
				t['afii10066'] = 0x0431;
				t['afii10067'] = 0x0432;
				t['afii10068'] = 0x0433;
				t['afii10069'] = 0x0434;
				t['afii10070'] = 0x0435;
				t['afii10071'] = 0x0451;
				t['afii10072'] = 0x0436;
				t['afii10073'] = 0x0437;
				t['afii10074'] = 0x0438;
				t['afii10075'] = 0x0439;
				t['afii10076'] = 0x043A;
				t['afii10077'] = 0x043B;
				t['afii10078'] = 0x043C;
				t['afii10079'] = 0x043D;
				t['afii10080'] = 0x043E;
				t['afii10081'] = 0x043F;
				t['afii10082'] = 0x0440;
				t['afii10083'] = 0x0441;
				t['afii10084'] = 0x0442;
				t['afii10085'] = 0x0443;
				t['afii10086'] = 0x0444;
				t['afii10087'] = 0x0445;
				t['afii10088'] = 0x0446;
				t['afii10089'] = 0x0447;
				t['afii10090'] = 0x0448;
				t['afii10091'] = 0x0449;
				t['afii10092'] = 0x044A;
				t['afii10093'] = 0x044B;
				t['afii10094'] = 0x044C;
				t['afii10095'] = 0x044D;
				t['afii10096'] = 0x044E;
				t['afii10097'] = 0x044F;
				t['afii10098'] = 0x0491;
				t['afii10099'] = 0x0452;
				t['afii10100'] = 0x0453;
				t['afii10101'] = 0x0454;
				t['afii10102'] = 0x0455;
				t['afii10103'] = 0x0456;
				t['afii10104'] = 0x0457;
				t['afii10105'] = 0x0458;
				t['afii10106'] = 0x0459;
				t['afii10107'] = 0x045A;
				t['afii10108'] = 0x045B;
				t['afii10109'] = 0x045C;
				t['afii10110'] = 0x045E;
				t['afii10145'] = 0x040F;
				t['afii10146'] = 0x0462;
				t['afii10147'] = 0x0472;
				t['afii10148'] = 0x0474;
				t['afii10192'] = 0xF6C6;
				t['afii10193'] = 0x045F;
				t['afii10194'] = 0x0463;
				t['afii10195'] = 0x0473;
				t['afii10196'] = 0x0475;
				t['afii10831'] = 0xF6C7;
				t['afii10832'] = 0xF6C8;
				t['afii10846'] = 0x04D9;
				t['afii299'] = 0x200E;
				t['afii300'] = 0x200F;
				t['afii301'] = 0x200D;
				t['afii57381'] = 0x066A;
				t['afii57388'] = 0x060C;
				t['afii57392'] = 0x0660;
				t['afii57393'] = 0x0661;
				t['afii57394'] = 0x0662;
				t['afii57395'] = 0x0663;
				t['afii57396'] = 0x0664;
				t['afii57397'] = 0x0665;
				t['afii57398'] = 0x0666;
				t['afii57399'] = 0x0667;
				t['afii57400'] = 0x0668;
				t['afii57401'] = 0x0669;
				t['afii57403'] = 0x061B;
				t['afii57407'] = 0x061F;
				t['afii57409'] = 0x0621;
				t['afii57410'] = 0x0622;
				t['afii57411'] = 0x0623;
				t['afii57412'] = 0x0624;
				t['afii57413'] = 0x0625;
				t['afii57414'] = 0x0626;
				t['afii57415'] = 0x0627;
				t['afii57416'] = 0x0628;
				t['afii57417'] = 0x0629;
				t['afii57418'] = 0x062A;
				t['afii57419'] = 0x062B;
				t['afii57420'] = 0x062C;
				t['afii57421'] = 0x062D;
				t['afii57422'] = 0x062E;
				t['afii57423'] = 0x062F;
				t['afii57424'] = 0x0630;
				t['afii57425'] = 0x0631;
				t['afii57426'] = 0x0632;
				t['afii57427'] = 0x0633;
				t['afii57428'] = 0x0634;
				t['afii57429'] = 0x0635;
				t['afii57430'] = 0x0636;
				t['afii57431'] = 0x0637;
				t['afii57432'] = 0x0638;
				t['afii57433'] = 0x0639;
				t['afii57434'] = 0x063A;
				t['afii57440'] = 0x0640;
				t['afii57441'] = 0x0641;
				t['afii57442'] = 0x0642;
				t['afii57443'] = 0x0643;
				t['afii57444'] = 0x0644;
				t['afii57445'] = 0x0645;
				t['afii57446'] = 0x0646;
				t['afii57448'] = 0x0648;
				t['afii57449'] = 0x0649;
				t['afii57450'] = 0x064A;
				t['afii57451'] = 0x064B;
				t['afii57452'] = 0x064C;
				t['afii57453'] = 0x064D;
				t['afii57454'] = 0x064E;
				t['afii57455'] = 0x064F;
				t['afii57456'] = 0x0650;
				t['afii57457'] = 0x0651;
				t['afii57458'] = 0x0652;
				t['afii57470'] = 0x0647;
				t['afii57505'] = 0x06A4;
				t['afii57506'] = 0x067E;
				t['afii57507'] = 0x0686;
				t['afii57508'] = 0x0698;
				t['afii57509'] = 0x06AF;
				t['afii57511'] = 0x0679;
				t['afii57512'] = 0x0688;
				t['afii57513'] = 0x0691;
				t['afii57514'] = 0x06BA;
				t['afii57519'] = 0x06D2;
				t['afii57534'] = 0x06D5;
				t['afii57636'] = 0x20AA;
				t['afii57645'] = 0x05BE;
				t['afii57658'] = 0x05C3;
				t['afii57664'] = 0x05D0;
				t['afii57665'] = 0x05D1;
				t['afii57666'] = 0x05D2;
				t['afii57667'] = 0x05D3;
				t['afii57668'] = 0x05D4;
				t['afii57669'] = 0x05D5;
				t['afii57670'] = 0x05D6;
				t['afii57671'] = 0x05D7;
				t['afii57672'] = 0x05D8;
				t['afii57673'] = 0x05D9;
				t['afii57674'] = 0x05DA;
				t['afii57675'] = 0x05DB;
				t['afii57676'] = 0x05DC;
				t['afii57677'] = 0x05DD;
				t['afii57678'] = 0x05DE;
				t['afii57679'] = 0x05DF;
				t['afii57680'] = 0x05E0;
				t['afii57681'] = 0x05E1;
				t['afii57682'] = 0x05E2;
				t['afii57683'] = 0x05E3;
				t['afii57684'] = 0x05E4;
				t['afii57685'] = 0x05E5;
				t['afii57686'] = 0x05E6;
				t['afii57687'] = 0x05E7;
				t['afii57688'] = 0x05E8;
				t['afii57689'] = 0x05E9;
				t['afii57690'] = 0x05EA;
				t['afii57694'] = 0xFB2A;
				t['afii57695'] = 0xFB2B;
				t['afii57700'] = 0xFB4B;
				t['afii57705'] = 0xFB1F;
				t['afii57716'] = 0x05F0;
				t['afii57717'] = 0x05F1;
				t['afii57718'] = 0x05F2;
				t['afii57723'] = 0xFB35;
				t['afii57793'] = 0x05B4;
				t['afii57794'] = 0x05B5;
				t['afii57795'] = 0x05B6;
				t['afii57796'] = 0x05BB;
				t['afii57797'] = 0x05B8;
				t['afii57798'] = 0x05B7;
				t['afii57799'] = 0x05B0;
				t['afii57800'] = 0x05B2;
				t['afii57801'] = 0x05B1;
				t['afii57802'] = 0x05B3;
				t['afii57803'] = 0x05C2;
				t['afii57804'] = 0x05C1;
				t['afii57806'] = 0x05B9;
				t['afii57807'] = 0x05BC;
				t['afii57839'] = 0x05BD;
				t['afii57841'] = 0x05BF;
				t['afii57842'] = 0x05C0;
				t['afii57929'] = 0x02BC;
				t['afii61248'] = 0x2105;
				t['afii61289'] = 0x2113;
				t['afii61352'] = 0x2116;
				t['afii61573'] = 0x202C;
				t['afii61574'] = 0x202D;
				t['afii61575'] = 0x202E;
				t['afii61664'] = 0x200C;
				t['afii63167'] = 0x066D;
				t['afii64937'] = 0x02BD;
				t['agrave'] = 0x00E0;
				t['agujarati'] = 0x0A85;
				t['agurmukhi'] = 0x0A05;
				t['ahiragana'] = 0x3042;
				t['ahookabove'] = 0x1EA3;
				t['aibengali'] = 0x0990;
				t['aibopomofo'] = 0x311E;
				t['aideva'] = 0x0910;
				t['aiecyrillic'] = 0x04D5;
				t['aigujarati'] = 0x0A90;
				t['aigurmukhi'] = 0x0A10;
				t['aimatragurmukhi'] = 0x0A48;
				t['ainarabic'] = 0x0639;
				t['ainfinalarabic'] = 0xFECA;
				t['aininitialarabic'] = 0xFECB;
				t['ainmedialarabic'] = 0xFECC;
				t['ainvertedbreve'] = 0x0203;
				t['aivowelsignbengali'] = 0x09C8;
				t['aivowelsigndeva'] = 0x0948;
				t['aivowelsigngujarati'] = 0x0AC8;
				t['akatakana'] = 0x30A2;
				t['akatakanahalfwidth'] = 0xFF71;
				t['akorean'] = 0x314F;
				t['alef'] = 0x05D0;
				t['alefarabic'] = 0x0627;
				t['alefdageshhebrew'] = 0xFB30;
				t['aleffinalarabic'] = 0xFE8E;
				t['alefhamzaabovearabic'] = 0x0623;
				t['alefhamzaabovefinalarabic'] = 0xFE84;
				t['alefhamzabelowarabic'] = 0x0625;
				t['alefhamzabelowfinalarabic'] = 0xFE88;
				t['alefhebrew'] = 0x05D0;
				t['aleflamedhebrew'] = 0xFB4F;
				t['alefmaddaabovearabic'] = 0x0622;
				t['alefmaddaabovefinalarabic'] = 0xFE82;
				t['alefmaksuraarabic'] = 0x0649;
				t['alefmaksurafinalarabic'] = 0xFEF0;
				t['alefmaksurainitialarabic'] = 0xFEF3;
				t['alefmaksuramedialarabic'] = 0xFEF4;
				t['alefpatahhebrew'] = 0xFB2E;
				t['alefqamatshebrew'] = 0xFB2F;
				t['aleph'] = 0x2135;
				t['allequal'] = 0x224C;
				t['alpha'] = 0x03B1;
				t['alphatonos'] = 0x03AC;
				t['amacron'] = 0x0101;
				t['amonospace'] = 0xFF41;
				t['ampersand'] = 0x0026;
				t['ampersandmonospace'] = 0xFF06;
				t['ampersandsmall'] = 0xF726;
				t['amsquare'] = 0x33C2;
				t['anbopomofo'] = 0x3122;
				t['angbopomofo'] = 0x3124;
				t['angbracketleft'] = 0x3008;
				t['angbracketright'] = 0x3009;
				t['angkhankhuthai'] = 0x0E5A;
				t['angle'] = 0x2220;
				t['anglebracketleft'] = 0x3008;
				t['anglebracketleftvertical'] = 0xFE3F;
				t['anglebracketright'] = 0x3009;
				t['anglebracketrightvertical'] = 0xFE40;
				t['angleleft'] = 0x2329;
				t['angleright'] = 0x232A;
				t['angstrom'] = 0x212B;
				t['anoteleia'] = 0x0387;
				t['anudattadeva'] = 0x0952;
				t['anusvarabengali'] = 0x0982;
				t['anusvaradeva'] = 0x0902;
				t['anusvaragujarati'] = 0x0A82;
				t['aogonek'] = 0x0105;
				t['apaatosquare'] = 0x3300;
				t['aparen'] = 0x249C;
				t['apostrophearmenian'] = 0x055A;
				t['apostrophemod'] = 0x02BC;
				t['apple'] = 0xF8FF;
				t['approaches'] = 0x2250;
				t['approxequal'] = 0x2248;
				t['approxequalorimage'] = 0x2252;
				t['approximatelyequal'] = 0x2245;
				t['araeaekorean'] = 0x318E;
				t['araeakorean'] = 0x318D;
				t['arc'] = 0x2312;
				t['arighthalfring'] = 0x1E9A;
				t['aring'] = 0x00E5;
				t['aringacute'] = 0x01FB;
				t['aringbelow'] = 0x1E01;
				t['arrowboth'] = 0x2194;
				t['arrowdashdown'] = 0x21E3;
				t['arrowdashleft'] = 0x21E0;
				t['arrowdashright'] = 0x21E2;
				t['arrowdashup'] = 0x21E1;
				t['arrowdblboth'] = 0x21D4;
				t['arrowdbldown'] = 0x21D3;
				t['arrowdblleft'] = 0x21D0;
				t['arrowdblright'] = 0x21D2;
				t['arrowdblup'] = 0x21D1;
				t['arrowdown'] = 0x2193;
				t['arrowdownleft'] = 0x2199;
				t['arrowdownright'] = 0x2198;
				t['arrowdownwhite'] = 0x21E9;
				t['arrowheaddownmod'] = 0x02C5;
				t['arrowheadleftmod'] = 0x02C2;
				t['arrowheadrightmod'] = 0x02C3;
				t['arrowheadupmod'] = 0x02C4;
				t['arrowhorizex'] = 0xF8E7;
				t['arrowleft'] = 0x2190;
				t['arrowleftdbl'] = 0x21D0;
				t['arrowleftdblstroke'] = 0x21CD;
				t['arrowleftoverright'] = 0x21C6;
				t['arrowleftwhite'] = 0x21E6;
				t['arrowright'] = 0x2192;
				t['arrowrightdblstroke'] = 0x21CF;
				t['arrowrightheavy'] = 0x279E;
				t['arrowrightoverleft'] = 0x21C4;
				t['arrowrightwhite'] = 0x21E8;
				t['arrowtableft'] = 0x21E4;
				t['arrowtabright'] = 0x21E5;
				t['arrowup'] = 0x2191;
				t['arrowupdn'] = 0x2195;
				t['arrowupdnbse'] = 0x21A8;
				t['arrowupdownbase'] = 0x21A8;
				t['arrowupleft'] = 0x2196;
				t['arrowupleftofdown'] = 0x21C5;
				t['arrowupright'] = 0x2197;
				t['arrowupwhite'] = 0x21E7;
				t['arrowvertex'] = 0xF8E6;
				t['asciicircum'] = 0x005E;
				t['asciicircummonospace'] = 0xFF3E;
				t['asciitilde'] = 0x007E;
				t['asciitildemonospace'] = 0xFF5E;
				t['ascript'] = 0x0251;
				t['ascriptturned'] = 0x0252;
				t['asmallhiragana'] = 0x3041;
				t['asmallkatakana'] = 0x30A1;
				t['asmallkatakanahalfwidth'] = 0xFF67;
				t['asterisk'] = 0x002A;
				t['asteriskaltonearabic'] = 0x066D;
				t['asteriskarabic'] = 0x066D;
				t['asteriskmath'] = 0x2217;
				t['asteriskmonospace'] = 0xFF0A;
				t['asterisksmall'] = 0xFE61;
				t['asterism'] = 0x2042;
				t['asuperior'] = 0xF6E9;
				t['asymptoticallyequal'] = 0x2243;
				t['at'] = 0x0040;
				t['atilde'] = 0x00E3;
				t['atmonospace'] = 0xFF20;
				t['atsmall'] = 0xFE6B;
				t['aturned'] = 0x0250;
				t['aubengali'] = 0x0994;
				t['aubopomofo'] = 0x3120;
				t['audeva'] = 0x0914;
				t['augujarati'] = 0x0A94;
				t['augurmukhi'] = 0x0A14;
				t['aulengthmarkbengali'] = 0x09D7;
				t['aumatragurmukhi'] = 0x0A4C;
				t['auvowelsignbengali'] = 0x09CC;
				t['auvowelsigndeva'] = 0x094C;
				t['auvowelsigngujarati'] = 0x0ACC;
				t['avagrahadeva'] = 0x093D;
				t['aybarmenian'] = 0x0561;
				t['ayin'] = 0x05E2;
				t['ayinaltonehebrew'] = 0xFB20;
				t['ayinhebrew'] = 0x05E2;
				t['b'] = 0x0062;
				t['babengali'] = 0x09AC;
				t['backslash'] = 0x005C;
				t['backslashmonospace'] = 0xFF3C;
				t['badeva'] = 0x092C;
				t['bagujarati'] = 0x0AAC;
				t['bagurmukhi'] = 0x0A2C;
				t['bahiragana'] = 0x3070;
				t['bahtthai'] = 0x0E3F;
				t['bakatakana'] = 0x30D0;
				t['bar'] = 0x007C;
				t['barmonospace'] = 0xFF5C;
				t['bbopomofo'] = 0x3105;
				t['bcircle'] = 0x24D1;
				t['bdotaccent'] = 0x1E03;
				t['bdotbelow'] = 0x1E05;
				t['beamedsixteenthnotes'] = 0x266C;
				t['because'] = 0x2235;
				t['becyrillic'] = 0x0431;
				t['beharabic'] = 0x0628;
				t['behfinalarabic'] = 0xFE90;
				t['behinitialarabic'] = 0xFE91;
				t['behiragana'] = 0x3079;
				t['behmedialarabic'] = 0xFE92;
				t['behmeeminitialarabic'] = 0xFC9F;
				t['behmeemisolatedarabic'] = 0xFC08;
				t['behnoonfinalarabic'] = 0xFC6D;
				t['bekatakana'] = 0x30D9;
				t['benarmenian'] = 0x0562;
				t['bet'] = 0x05D1;
				t['beta'] = 0x03B2;
				t['betasymbolgreek'] = 0x03D0;
				t['betdagesh'] = 0xFB31;
				t['betdageshhebrew'] = 0xFB31;
				t['bethebrew'] = 0x05D1;
				t['betrafehebrew'] = 0xFB4C;
				t['bhabengali'] = 0x09AD;
				t['bhadeva'] = 0x092D;
				t['bhagujarati'] = 0x0AAD;
				t['bhagurmukhi'] = 0x0A2D;
				t['bhook'] = 0x0253;
				t['bihiragana'] = 0x3073;
				t['bikatakana'] = 0x30D3;
				t['bilabialclick'] = 0x0298;
				t['bindigurmukhi'] = 0x0A02;
				t['birusquare'] = 0x3331;
				t['blackcircle'] = 0x25CF;
				t['blackdiamond'] = 0x25C6;
				t['blackdownpointingtriangle'] = 0x25BC;
				t['blackleftpointingpointer'] = 0x25C4;
				t['blackleftpointingtriangle'] = 0x25C0;
				t['blacklenticularbracketleft'] = 0x3010;
				t['blacklenticularbracketleftvertical'] = 0xFE3B;
				t['blacklenticularbracketright'] = 0x3011;
				t['blacklenticularbracketrightvertical'] = 0xFE3C;
				t['blacklowerlefttriangle'] = 0x25E3;
				t['blacklowerrighttriangle'] = 0x25E2;
				t['blackrectangle'] = 0x25AC;
				t['blackrightpointingpointer'] = 0x25BA;
				t['blackrightpointingtriangle'] = 0x25B6;
				t['blacksmallsquare'] = 0x25AA;
				t['blacksmilingface'] = 0x263B;
				t['blacksquare'] = 0x25A0;
				t['blackstar'] = 0x2605;
				t['blackupperlefttriangle'] = 0x25E4;
				t['blackupperrighttriangle'] = 0x25E5;
				t['blackuppointingsmalltriangle'] = 0x25B4;
				t['blackuppointingtriangle'] = 0x25B2;
				t['blank'] = 0x2423;
				t['blinebelow'] = 0x1E07;
				t['block'] = 0x2588;
				t['bmonospace'] = 0xFF42;
				t['bobaimaithai'] = 0x0E1A;
				t['bohiragana'] = 0x307C;
				t['bokatakana'] = 0x30DC;
				t['bparen'] = 0x249D;
				t['bqsquare'] = 0x33C3;
				t['braceex'] = 0xF8F4;
				t['braceleft'] = 0x007B;
				t['braceleftbt'] = 0xF8F3;
				t['braceleftmid'] = 0xF8F2;
				t['braceleftmonospace'] = 0xFF5B;
				t['braceleftsmall'] = 0xFE5B;
				t['bracelefttp'] = 0xF8F1;
				t['braceleftvertical'] = 0xFE37;
				t['braceright'] = 0x007D;
				t['bracerightbt'] = 0xF8FE;
				t['bracerightmid'] = 0xF8FD;
				t['bracerightmonospace'] = 0xFF5D;
				t['bracerightsmall'] = 0xFE5C;
				t['bracerighttp'] = 0xF8FC;
				t['bracerightvertical'] = 0xFE38;
				t['bracketleft'] = 0x005B;
				t['bracketleftbt'] = 0xF8F0;
				t['bracketleftex'] = 0xF8EF;
				t['bracketleftmonospace'] = 0xFF3B;
				t['bracketlefttp'] = 0xF8EE;
				t['bracketright'] = 0x005D;
				t['bracketrightbt'] = 0xF8FB;
				t['bracketrightex'] = 0xF8FA;
				t['bracketrightmonospace'] = 0xFF3D;
				t['bracketrighttp'] = 0xF8F9;
				t['breve'] = 0x02D8;
				t['brevebelowcmb'] = 0x032E;
				t['brevecmb'] = 0x0306;
				t['breveinvertedbelowcmb'] = 0x032F;
				t['breveinvertedcmb'] = 0x0311;
				t['breveinverteddoublecmb'] = 0x0361;
				t['bridgebelowcmb'] = 0x032A;
				t['bridgeinvertedbelowcmb'] = 0x033A;
				t['brokenbar'] = 0x00A6;
				t['bstroke'] = 0x0180;
				t['bsuperior'] = 0xF6EA;
				t['btopbar'] = 0x0183;
				t['buhiragana'] = 0x3076;
				t['bukatakana'] = 0x30D6;
				t['bullet'] = 0x2022;
				t['bulletinverse'] = 0x25D8;
				t['bulletoperator'] = 0x2219;
				t['bullseye'] = 0x25CE;
				t['c'] = 0x0063;
				t['caarmenian'] = 0x056E;
				t['cabengali'] = 0x099A;
				t['cacute'] = 0x0107;
				t['cadeva'] = 0x091A;
				t['cagujarati'] = 0x0A9A;
				t['cagurmukhi'] = 0x0A1A;
				t['calsquare'] = 0x3388;
				t['candrabindubengali'] = 0x0981;
				t['candrabinducmb'] = 0x0310;
				t['candrabindudeva'] = 0x0901;
				t['candrabindugujarati'] = 0x0A81;
				t['capslock'] = 0x21EA;
				t['careof'] = 0x2105;
				t['caron'] = 0x02C7;
				t['caronbelowcmb'] = 0x032C;
				t['caroncmb'] = 0x030C;
				t['carriagereturn'] = 0x21B5;
				t['cbopomofo'] = 0x3118;
				t['ccaron'] = 0x010D;
				t['ccedilla'] = 0x00E7;
				t['ccedillaacute'] = 0x1E09;
				t['ccircle'] = 0x24D2;
				t['ccircumflex'] = 0x0109;
				t['ccurl'] = 0x0255;
				t['cdot'] = 0x010B;
				t['cdotaccent'] = 0x010B;
				t['cdsquare'] = 0x33C5;
				t['cedilla'] = 0x00B8;
				t['cedillacmb'] = 0x0327;
				t['cent'] = 0x00A2;
				t['centigrade'] = 0x2103;
				t['centinferior'] = 0xF6DF;
				t['centmonospace'] = 0xFFE0;
				t['centoldstyle'] = 0xF7A2;
				t['centsuperior'] = 0xF6E0;
				t['chaarmenian'] = 0x0579;
				t['chabengali'] = 0x099B;
				t['chadeva'] = 0x091B;
				t['chagujarati'] = 0x0A9B;
				t['chagurmukhi'] = 0x0A1B;
				t['chbopomofo'] = 0x3114;
				t['cheabkhasiancyrillic'] = 0x04BD;
				t['checkmark'] = 0x2713;
				t['checyrillic'] = 0x0447;
				t['chedescenderabkhasiancyrillic'] = 0x04BF;
				t['chedescendercyrillic'] = 0x04B7;
				t['chedieresiscyrillic'] = 0x04F5;
				t['cheharmenian'] = 0x0573;
				t['chekhakassiancyrillic'] = 0x04CC;
				t['cheverticalstrokecyrillic'] = 0x04B9;
				t['chi'] = 0x03C7;
				t['chieuchacirclekorean'] = 0x3277;
				t['chieuchaparenkorean'] = 0x3217;
				t['chieuchcirclekorean'] = 0x3269;
				t['chieuchkorean'] = 0x314A;
				t['chieuchparenkorean'] = 0x3209;
				t['chochangthai'] = 0x0E0A;
				t['chochanthai'] = 0x0E08;
				t['chochingthai'] = 0x0E09;
				t['chochoethai'] = 0x0E0C;
				t['chook'] = 0x0188;
				t['cieucacirclekorean'] = 0x3276;
				t['cieucaparenkorean'] = 0x3216;
				t['cieuccirclekorean'] = 0x3268;
				t['cieuckorean'] = 0x3148;
				t['cieucparenkorean'] = 0x3208;
				t['cieucuparenkorean'] = 0x321C;
				t['circle'] = 0x25CB;
				t['circlecopyrt'] = 0x00A9;
				t['circlemultiply'] = 0x2297;
				t['circleot'] = 0x2299;
				t['circleplus'] = 0x2295;
				t['circlepostalmark'] = 0x3036;
				t['circlewithlefthalfblack'] = 0x25D0;
				t['circlewithrighthalfblack'] = 0x25D1;
				t['circumflex'] = 0x02C6;
				t['circumflexbelowcmb'] = 0x032D;
				t['circumflexcmb'] = 0x0302;
				t['clear'] = 0x2327;
				t['clickalveolar'] = 0x01C2;
				t['clickdental'] = 0x01C0;
				t['clicklateral'] = 0x01C1;
				t['clickretroflex'] = 0x01C3;
				t['club'] = 0x2663;
				t['clubsuitblack'] = 0x2663;
				t['clubsuitwhite'] = 0x2667;
				t['cmcubedsquare'] = 0x33A4;
				t['cmonospace'] = 0xFF43;
				t['cmsquaredsquare'] = 0x33A0;
				t['coarmenian'] = 0x0581;
				t['colon'] = 0x003A;
				t['colonmonetary'] = 0x20A1;
				t['colonmonospace'] = 0xFF1A;
				t['colonsign'] = 0x20A1;
				t['colonsmall'] = 0xFE55;
				t['colontriangularhalfmod'] = 0x02D1;
				t['colontriangularmod'] = 0x02D0;
				t['comma'] = 0x002C;
				t['commaabovecmb'] = 0x0313;
				t['commaaboverightcmb'] = 0x0315;
				t['commaaccent'] = 0xF6C3;
				t['commaarabic'] = 0x060C;
				t['commaarmenian'] = 0x055D;
				t['commainferior'] = 0xF6E1;
				t['commamonospace'] = 0xFF0C;
				t['commareversedabovecmb'] = 0x0314;
				t['commareversedmod'] = 0x02BD;
				t['commasmall'] = 0xFE50;
				t['commasuperior'] = 0xF6E2;
				t['commaturnedabovecmb'] = 0x0312;
				t['commaturnedmod'] = 0x02BB;
				t['compass'] = 0x263C;
				t['congruent'] = 0x2245;
				t['contourintegral'] = 0x222E;
				t['control'] = 0x2303;
				t['controlACK'] = 0x0006;
				t['controlBEL'] = 0x0007;
				t['controlBS'] = 0x0008;
				t['controlCAN'] = 0x0018;
				t['controlCR'] = 0x000D;
				t['controlDC1'] = 0x0011;
				t['controlDC2'] = 0x0012;
				t['controlDC3'] = 0x0013;
				t['controlDC4'] = 0x0014;
				t['controlDEL'] = 0x007F;
				t['controlDLE'] = 0x0010;
				t['controlEM'] = 0x0019;
				t['controlENQ'] = 0x0005;
				t['controlEOT'] = 0x0004;
				t['controlESC'] = 0x001B;
				t['controlETB'] = 0x0017;
				t['controlETX'] = 0x0003;
				t['controlFF'] = 0x000C;
				t['controlFS'] = 0x001C;
				t['controlGS'] = 0x001D;
				t['controlHT'] = 0x0009;
				t['controlLF'] = 0x000A;
				t['controlNAK'] = 0x0015;
				t['controlNULL'] = 0x0000;
				t['controlRS'] = 0x001E;
				t['controlSI'] = 0x000F;
				t['controlSO'] = 0x000E;
				t['controlSOT'] = 0x0002;
				t['controlSTX'] = 0x0001;
				t['controlSUB'] = 0x001A;
				t['controlSYN'] = 0x0016;
				t['controlUS'] = 0x001F;
				t['controlVT'] = 0x000B;
				t['copyright'] = 0x00A9;
				t['copyrightsans'] = 0xF8E9;
				t['copyrightserif'] = 0xF6D9;
				t['cornerbracketleft'] = 0x300C;
				t['cornerbracketlefthalfwidth'] = 0xFF62;
				t['cornerbracketleftvertical'] = 0xFE41;
				t['cornerbracketright'] = 0x300D;
				t['cornerbracketrighthalfwidth'] = 0xFF63;
				t['cornerbracketrightvertical'] = 0xFE42;
				t['corporationsquare'] = 0x337F;
				t['cosquare'] = 0x33C7;
				t['coverkgsquare'] = 0x33C6;
				t['cparen'] = 0x249E;
				t['cruzeiro'] = 0x20A2;
				t['cstretched'] = 0x0297;
				t['curlyand'] = 0x22CF;
				t['curlyor'] = 0x22CE;
				t['currency'] = 0x00A4;
				t['cyrBreve'] = 0xF6D1;
				t['cyrFlex'] = 0xF6D2;
				t['cyrbreve'] = 0xF6D4;
				t['cyrflex'] = 0xF6D5;
				t['d'] = 0x0064;
				t['daarmenian'] = 0x0564;
				t['dabengali'] = 0x09A6;
				t['dadarabic'] = 0x0636;
				t['dadeva'] = 0x0926;
				t['dadfinalarabic'] = 0xFEBE;
				t['dadinitialarabic'] = 0xFEBF;
				t['dadmedialarabic'] = 0xFEC0;
				t['dagesh'] = 0x05BC;
				t['dageshhebrew'] = 0x05BC;
				t['dagger'] = 0x2020;
				t['daggerdbl'] = 0x2021;
				t['dagujarati'] = 0x0AA6;
				t['dagurmukhi'] = 0x0A26;
				t['dahiragana'] = 0x3060;
				t['dakatakana'] = 0x30C0;
				t['dalarabic'] = 0x062F;
				t['dalet'] = 0x05D3;
				t['daletdagesh'] = 0xFB33;
				t['daletdageshhebrew'] = 0xFB33;
				t['dalethebrew'] = 0x05D3;
				t['dalfinalarabic'] = 0xFEAA;
				t['dammaarabic'] = 0x064F;
				t['dammalowarabic'] = 0x064F;
				t['dammatanaltonearabic'] = 0x064C;
				t['dammatanarabic'] = 0x064C;
				t['danda'] = 0x0964;
				t['dargahebrew'] = 0x05A7;
				t['dargalefthebrew'] = 0x05A7;
				t['dasiapneumatacyrilliccmb'] = 0x0485;
				t['dblGrave'] = 0xF6D3;
				t['dblanglebracketleft'] = 0x300A;
				t['dblanglebracketleftvertical'] = 0xFE3D;
				t['dblanglebracketright'] = 0x300B;
				t['dblanglebracketrightvertical'] = 0xFE3E;
				t['dblarchinvertedbelowcmb'] = 0x032B;
				t['dblarrowleft'] = 0x21D4;
				t['dblarrowright'] = 0x21D2;
				t['dbldanda'] = 0x0965;
				t['dblgrave'] = 0xF6D6;
				t['dblgravecmb'] = 0x030F;
				t['dblintegral'] = 0x222C;
				t['dbllowline'] = 0x2017;
				t['dbllowlinecmb'] = 0x0333;
				t['dbloverlinecmb'] = 0x033F;
				t['dblprimemod'] = 0x02BA;
				t['dblverticalbar'] = 0x2016;
				t['dblverticallineabovecmb'] = 0x030E;
				t['dbopomofo'] = 0x3109;
				t['dbsquare'] = 0x33C8;
				t['dcaron'] = 0x010F;
				t['dcedilla'] = 0x1E11;
				t['dcircle'] = 0x24D3;
				t['dcircumflexbelow'] = 0x1E13;
				t['dcroat'] = 0x0111;
				t['ddabengali'] = 0x09A1;
				t['ddadeva'] = 0x0921;
				t['ddagujarati'] = 0x0AA1;
				t['ddagurmukhi'] = 0x0A21;
				t['ddalarabic'] = 0x0688;
				t['ddalfinalarabic'] = 0xFB89;
				t['dddhadeva'] = 0x095C;
				t['ddhabengali'] = 0x09A2;
				t['ddhadeva'] = 0x0922;
				t['ddhagujarati'] = 0x0AA2;
				t['ddhagurmukhi'] = 0x0A22;
				t['ddotaccent'] = 0x1E0B;
				t['ddotbelow'] = 0x1E0D;
				t['decimalseparatorarabic'] = 0x066B;
				t['decimalseparatorpersian'] = 0x066B;
				t['decyrillic'] = 0x0434;
				t['degree'] = 0x00B0;
				t['dehihebrew'] = 0x05AD;
				t['dehiragana'] = 0x3067;
				t['deicoptic'] = 0x03EF;
				t['dekatakana'] = 0x30C7;
				t['deleteleft'] = 0x232B;
				t['deleteright'] = 0x2326;
				t['delta'] = 0x03B4;
				t['deltaturned'] = 0x018D;
				t['denominatorminusonenumeratorbengali'] = 0x09F8;
				t['dezh'] = 0x02A4;
				t['dhabengali'] = 0x09A7;
				t['dhadeva'] = 0x0927;
				t['dhagujarati'] = 0x0AA7;
				t['dhagurmukhi'] = 0x0A27;
				t['dhook'] = 0x0257;
				t['dialytikatonos'] = 0x0385;
				t['dialytikatonoscmb'] = 0x0344;
				t['diamond'] = 0x2666;
				t['diamondsuitwhite'] = 0x2662;
				t['dieresis'] = 0x00A8;
				t['dieresisacute'] = 0xF6D7;
				t['dieresisbelowcmb'] = 0x0324;
				t['dieresiscmb'] = 0x0308;
				t['dieresisgrave'] = 0xF6D8;
				t['dieresistonos'] = 0x0385;
				t['dihiragana'] = 0x3062;
				t['dikatakana'] = 0x30C2;
				t['dittomark'] = 0x3003;
				t['divide'] = 0x00F7;
				t['divides'] = 0x2223;
				t['divisionslash'] = 0x2215;
				t['djecyrillic'] = 0x0452;
				t['dkshade'] = 0x2593;
				t['dlinebelow'] = 0x1E0F;
				t['dlsquare'] = 0x3397;
				t['dmacron'] = 0x0111;
				t['dmonospace'] = 0xFF44;
				t['dnblock'] = 0x2584;
				t['dochadathai'] = 0x0E0E;
				t['dodekthai'] = 0x0E14;
				t['dohiragana'] = 0x3069;
				t['dokatakana'] = 0x30C9;
				t['dollar'] = 0x0024;
				t['dollarinferior'] = 0xF6E3;
				t['dollarmonospace'] = 0xFF04;
				t['dollaroldstyle'] = 0xF724;
				t['dollarsmall'] = 0xFE69;
				t['dollarsuperior'] = 0xF6E4;
				t['dong'] = 0x20AB;
				t['dorusquare'] = 0x3326;
				t['dotaccent'] = 0x02D9;
				t['dotaccentcmb'] = 0x0307;
				t['dotbelowcmb'] = 0x0323;
				t['dotbelowcomb'] = 0x0323;
				t['dotkatakana'] = 0x30FB;
				t['dotlessi'] = 0x0131;
				t['dotlessj'] = 0xF6BE;
				t['dotlessjstrokehook'] = 0x0284;
				t['dotmath'] = 0x22C5;
				t['dottedcircle'] = 0x25CC;
				t['doubleyodpatah'] = 0xFB1F;
				t['doubleyodpatahhebrew'] = 0xFB1F;
				t['downtackbelowcmb'] = 0x031E;
				t['downtackmod'] = 0x02D5;
				t['dparen'] = 0x249F;
				t['dsuperior'] = 0xF6EB;
				t['dtail'] = 0x0256;
				t['dtopbar'] = 0x018C;
				t['duhiragana'] = 0x3065;
				t['dukatakana'] = 0x30C5;
				t['dz'] = 0x01F3;
				t['dzaltone'] = 0x02A3;
				t['dzcaron'] = 0x01C6;
				t['dzcurl'] = 0x02A5;
				t['dzeabkhasiancyrillic'] = 0x04E1;
				t['dzecyrillic'] = 0x0455;
				t['dzhecyrillic'] = 0x045F;
				t['e'] = 0x0065;
				t['eacute'] = 0x00E9;
				t['earth'] = 0x2641;
				t['ebengali'] = 0x098F;
				t['ebopomofo'] = 0x311C;
				t['ebreve'] = 0x0115;
				t['ecandradeva'] = 0x090D;
				t['ecandragujarati'] = 0x0A8D;
				t['ecandravowelsigndeva'] = 0x0945;
				t['ecandravowelsigngujarati'] = 0x0AC5;
				t['ecaron'] = 0x011B;
				t['ecedillabreve'] = 0x1E1D;
				t['echarmenian'] = 0x0565;
				t['echyiwnarmenian'] = 0x0587;
				t['ecircle'] = 0x24D4;
				t['ecircumflex'] = 0x00EA;
				t['ecircumflexacute'] = 0x1EBF;
				t['ecircumflexbelow'] = 0x1E19;
				t['ecircumflexdotbelow'] = 0x1EC7;
				t['ecircumflexgrave'] = 0x1EC1;
				t['ecircumflexhookabove'] = 0x1EC3;
				t['ecircumflextilde'] = 0x1EC5;
				t['ecyrillic'] = 0x0454;
				t['edblgrave'] = 0x0205;
				t['edeva'] = 0x090F;
				t['edieresis'] = 0x00EB;
				t['edot'] = 0x0117;
				t['edotaccent'] = 0x0117;
				t['edotbelow'] = 0x1EB9;
				t['eegurmukhi'] = 0x0A0F;
				t['eematragurmukhi'] = 0x0A47;
				t['efcyrillic'] = 0x0444;
				t['egrave'] = 0x00E8;
				t['egujarati'] = 0x0A8F;
				t['eharmenian'] = 0x0567;
				t['ehbopomofo'] = 0x311D;
				t['ehiragana'] = 0x3048;
				t['ehookabove'] = 0x1EBB;
				t['eibopomofo'] = 0x311F;
				t['eight'] = 0x0038;
				t['eightarabic'] = 0x0668;
				t['eightbengali'] = 0x09EE;
				t['eightcircle'] = 0x2467;
				t['eightcircleinversesansserif'] = 0x2791;
				t['eightdeva'] = 0x096E;
				t['eighteencircle'] = 0x2471;
				t['eighteenparen'] = 0x2485;
				t['eighteenperiod'] = 0x2499;
				t['eightgujarati'] = 0x0AEE;
				t['eightgurmukhi'] = 0x0A6E;
				t['eighthackarabic'] = 0x0668;
				t['eighthangzhou'] = 0x3028;
				t['eighthnotebeamed'] = 0x266B;
				t['eightideographicparen'] = 0x3227;
				t['eightinferior'] = 0x2088;
				t['eightmonospace'] = 0xFF18;
				t['eightoldstyle'] = 0xF738;
				t['eightparen'] = 0x247B;
				t['eightperiod'] = 0x248F;
				t['eightpersian'] = 0x06F8;
				t['eightroman'] = 0x2177;
				t['eightsuperior'] = 0x2078;
				t['eightthai'] = 0x0E58;
				t['einvertedbreve'] = 0x0207;
				t['eiotifiedcyrillic'] = 0x0465;
				t['ekatakana'] = 0x30A8;
				t['ekatakanahalfwidth'] = 0xFF74;
				t['ekonkargurmukhi'] = 0x0A74;
				t['ekorean'] = 0x3154;
				t['elcyrillic'] = 0x043B;
				t['element'] = 0x2208;
				t['elevencircle'] = 0x246A;
				t['elevenparen'] = 0x247E;
				t['elevenperiod'] = 0x2492;
				t['elevenroman'] = 0x217A;
				t['ellipsis'] = 0x2026;
				t['ellipsisvertical'] = 0x22EE;
				t['emacron'] = 0x0113;
				t['emacronacute'] = 0x1E17;
				t['emacrongrave'] = 0x1E15;
				t['emcyrillic'] = 0x043C;
				t['emdash'] = 0x2014;
				t['emdashvertical'] = 0xFE31;
				t['emonospace'] = 0xFF45;
				t['emphasismarkarmenian'] = 0x055B;
				t['emptyset'] = 0x2205;
				t['enbopomofo'] = 0x3123;
				t['encyrillic'] = 0x043D;
				t['endash'] = 0x2013;
				t['endashvertical'] = 0xFE32;
				t['endescendercyrillic'] = 0x04A3;
				t['eng'] = 0x014B;
				t['engbopomofo'] = 0x3125;
				t['enghecyrillic'] = 0x04A5;
				t['enhookcyrillic'] = 0x04C8;
				t['enspace'] = 0x2002;
				t['eogonek'] = 0x0119;
				t['eokorean'] = 0x3153;
				t['eopen'] = 0x025B;
				t['eopenclosed'] = 0x029A;
				t['eopenreversed'] = 0x025C;
				t['eopenreversedclosed'] = 0x025E;
				t['eopenreversedhook'] = 0x025D;
				t['eparen'] = 0x24A0;
				t['epsilon'] = 0x03B5;
				t['epsilontonos'] = 0x03AD;
				t['equal'] = 0x003D;
				t['equalmonospace'] = 0xFF1D;
				t['equalsmall'] = 0xFE66;
				t['equalsuperior'] = 0x207C;
				t['equivalence'] = 0x2261;
				t['erbopomofo'] = 0x3126;
				t['ercyrillic'] = 0x0440;
				t['ereversed'] = 0x0258;
				t['ereversedcyrillic'] = 0x044D;
				t['escyrillic'] = 0x0441;
				t['esdescendercyrillic'] = 0x04AB;
				t['esh'] = 0x0283;
				t['eshcurl'] = 0x0286;
				t['eshortdeva'] = 0x090E;
				t['eshortvowelsigndeva'] = 0x0946;
				t['eshreversedloop'] = 0x01AA;
				t['eshsquatreversed'] = 0x0285;
				t['esmallhiragana'] = 0x3047;
				t['esmallkatakana'] = 0x30A7;
				t['esmallkatakanahalfwidth'] = 0xFF6A;
				t['estimated'] = 0x212E;
				t['esuperior'] = 0xF6EC;
				t['eta'] = 0x03B7;
				t['etarmenian'] = 0x0568;
				t['etatonos'] = 0x03AE;
				t['eth'] = 0x00F0;
				t['etilde'] = 0x1EBD;
				t['etildebelow'] = 0x1E1B;
				t['etnahtafoukhhebrew'] = 0x0591;
				t['etnahtafoukhlefthebrew'] = 0x0591;
				t['etnahtahebrew'] = 0x0591;
				t['etnahtalefthebrew'] = 0x0591;
				t['eturned'] = 0x01DD;
				t['eukorean'] = 0x3161;
				t['euro'] = 0x20AC;
				t['evowelsignbengali'] = 0x09C7;
				t['evowelsigndeva'] = 0x0947;
				t['evowelsigngujarati'] = 0x0AC7;
				t['exclam'] = 0x0021;
				t['exclamarmenian'] = 0x055C;
				t['exclamdbl'] = 0x203C;
				t['exclamdown'] = 0x00A1;
				t['exclamdownsmall'] = 0xF7A1;
				t['exclammonospace'] = 0xFF01;
				t['exclamsmall'] = 0xF721;
				t['existential'] = 0x2203;
				t['ezh'] = 0x0292;
				t['ezhcaron'] = 0x01EF;
				t['ezhcurl'] = 0x0293;
				t['ezhreversed'] = 0x01B9;
				t['ezhtail'] = 0x01BA;
				t['f'] = 0x0066;
				t['fadeva'] = 0x095E;
				t['fagurmukhi'] = 0x0A5E;
				t['fahrenheit'] = 0x2109;
				t['fathaarabic'] = 0x064E;
				t['fathalowarabic'] = 0x064E;
				t['fathatanarabic'] = 0x064B;
				t['fbopomofo'] = 0x3108;
				t['fcircle'] = 0x24D5;
				t['fdotaccent'] = 0x1E1F;
				t['feharabic'] = 0x0641;
				t['feharmenian'] = 0x0586;
				t['fehfinalarabic'] = 0xFED2;
				t['fehinitialarabic'] = 0xFED3;
				t['fehmedialarabic'] = 0xFED4;
				t['feicoptic'] = 0x03E5;
				t['female'] = 0x2640;
				t['ff'] = 0xFB00;
				t['ffi'] = 0xFB03;
				t['ffl'] = 0xFB04;
				t['fi'] = 0xFB01;
				t['fifteencircle'] = 0x246E;
				t['fifteenparen'] = 0x2482;
				t['fifteenperiod'] = 0x2496;
				t['figuredash'] = 0x2012;
				t['filledbox'] = 0x25A0;
				t['filledrect'] = 0x25AC;
				t['finalkaf'] = 0x05DA;
				t['finalkafdagesh'] = 0xFB3A;
				t['finalkafdageshhebrew'] = 0xFB3A;
				t['finalkafhebrew'] = 0x05DA;
				t['finalmem'] = 0x05DD;
				t['finalmemhebrew'] = 0x05DD;
				t['finalnun'] = 0x05DF;
				t['finalnunhebrew'] = 0x05DF;
				t['finalpe'] = 0x05E3;
				t['finalpehebrew'] = 0x05E3;
				t['finaltsadi'] = 0x05E5;
				t['finaltsadihebrew'] = 0x05E5;
				t['firsttonechinese'] = 0x02C9;
				t['fisheye'] = 0x25C9;
				t['fitacyrillic'] = 0x0473;
				t['five'] = 0x0035;
				t['fivearabic'] = 0x0665;
				t['fivebengali'] = 0x09EB;
				t['fivecircle'] = 0x2464;
				t['fivecircleinversesansserif'] = 0x278E;
				t['fivedeva'] = 0x096B;
				t['fiveeighths'] = 0x215D;
				t['fivegujarati'] = 0x0AEB;
				t['fivegurmukhi'] = 0x0A6B;
				t['fivehackarabic'] = 0x0665;
				t['fivehangzhou'] = 0x3025;
				t['fiveideographicparen'] = 0x3224;
				t['fiveinferior'] = 0x2085;
				t['fivemonospace'] = 0xFF15;
				t['fiveoldstyle'] = 0xF735;
				t['fiveparen'] = 0x2478;
				t['fiveperiod'] = 0x248C;
				t['fivepersian'] = 0x06F5;
				t['fiveroman'] = 0x2174;
				t['fivesuperior'] = 0x2075;
				t['fivethai'] = 0x0E55;
				t['fl'] = 0xFB02;
				t['florin'] = 0x0192;
				t['fmonospace'] = 0xFF46;
				t['fmsquare'] = 0x3399;
				t['fofanthai'] = 0x0E1F;
				t['fofathai'] = 0x0E1D;
				t['fongmanthai'] = 0x0E4F;
				t['forall'] = 0x2200;
				t['four'] = 0x0034;
				t['fourarabic'] = 0x0664;
				t['fourbengali'] = 0x09EA;
				t['fourcircle'] = 0x2463;
				t['fourcircleinversesansserif'] = 0x278D;
				t['fourdeva'] = 0x096A;
				t['fourgujarati'] = 0x0AEA;
				t['fourgurmukhi'] = 0x0A6A;
				t['fourhackarabic'] = 0x0664;
				t['fourhangzhou'] = 0x3024;
				t['fourideographicparen'] = 0x3223;
				t['fourinferior'] = 0x2084;
				t['fourmonospace'] = 0xFF14;
				t['fournumeratorbengali'] = 0x09F7;
				t['fouroldstyle'] = 0xF734;
				t['fourparen'] = 0x2477;
				t['fourperiod'] = 0x248B;
				t['fourpersian'] = 0x06F4;
				t['fourroman'] = 0x2173;
				t['foursuperior'] = 0x2074;
				t['fourteencircle'] = 0x246D;
				t['fourteenparen'] = 0x2481;
				t['fourteenperiod'] = 0x2495;
				t['fourthai'] = 0x0E54;
				t['fourthtonechinese'] = 0x02CB;
				t['fparen'] = 0x24A1;
				t['fraction'] = 0x2044;
				t['franc'] = 0x20A3;
				t['g'] = 0x0067;
				t['gabengali'] = 0x0997;
				t['gacute'] = 0x01F5;
				t['gadeva'] = 0x0917;
				t['gafarabic'] = 0x06AF;
				t['gaffinalarabic'] = 0xFB93;
				t['gafinitialarabic'] = 0xFB94;
				t['gafmedialarabic'] = 0xFB95;
				t['gagujarati'] = 0x0A97;
				t['gagurmukhi'] = 0x0A17;
				t['gahiragana'] = 0x304C;
				t['gakatakana'] = 0x30AC;
				t['gamma'] = 0x03B3;
				t['gammalatinsmall'] = 0x0263;
				t['gammasuperior'] = 0x02E0;
				t['gangiacoptic'] = 0x03EB;
				t['gbopomofo'] = 0x310D;
				t['gbreve'] = 0x011F;
				t['gcaron'] = 0x01E7;
				t['gcedilla'] = 0x0123;
				t['gcircle'] = 0x24D6;
				t['gcircumflex'] = 0x011D;
				t['gcommaaccent'] = 0x0123;
				t['gdot'] = 0x0121;
				t['gdotaccent'] = 0x0121;
				t['gecyrillic'] = 0x0433;
				t['gehiragana'] = 0x3052;
				t['gekatakana'] = 0x30B2;
				t['geometricallyequal'] = 0x2251;
				t['gereshaccenthebrew'] = 0x059C;
				t['gereshhebrew'] = 0x05F3;
				t['gereshmuqdamhebrew'] = 0x059D;
				t['germandbls'] = 0x00DF;
				t['gershayimaccenthebrew'] = 0x059E;
				t['gershayimhebrew'] = 0x05F4;
				t['getamark'] = 0x3013;
				t['ghabengali'] = 0x0998;
				t['ghadarmenian'] = 0x0572;
				t['ghadeva'] = 0x0918;
				t['ghagujarati'] = 0x0A98;
				t['ghagurmukhi'] = 0x0A18;
				t['ghainarabic'] = 0x063A;
				t['ghainfinalarabic'] = 0xFECE;
				t['ghaininitialarabic'] = 0xFECF;
				t['ghainmedialarabic'] = 0xFED0;
				t['ghemiddlehookcyrillic'] = 0x0495;
				t['ghestrokecyrillic'] = 0x0493;
				t['gheupturncyrillic'] = 0x0491;
				t['ghhadeva'] = 0x095A;
				t['ghhagurmukhi'] = 0x0A5A;
				t['ghook'] = 0x0260;
				t['ghzsquare'] = 0x3393;
				t['gihiragana'] = 0x304E;
				t['gikatakana'] = 0x30AE;
				t['gimarmenian'] = 0x0563;
				t['gimel'] = 0x05D2;
				t['gimeldagesh'] = 0xFB32;
				t['gimeldageshhebrew'] = 0xFB32;
				t['gimelhebrew'] = 0x05D2;
				t['gjecyrillic'] = 0x0453;
				t['glottalinvertedstroke'] = 0x01BE;
				t['glottalstop'] = 0x0294;
				t['glottalstopinverted'] = 0x0296;
				t['glottalstopmod'] = 0x02C0;
				t['glottalstopreversed'] = 0x0295;
				t['glottalstopreversedmod'] = 0x02C1;
				t['glottalstopreversedsuperior'] = 0x02E4;
				t['glottalstopstroke'] = 0x02A1;
				t['glottalstopstrokereversed'] = 0x02A2;
				t['gmacron'] = 0x1E21;
				t['gmonospace'] = 0xFF47;
				t['gohiragana'] = 0x3054;
				t['gokatakana'] = 0x30B4;
				t['gparen'] = 0x24A2;
				t['gpasquare'] = 0x33AC;
				t['gradient'] = 0x2207;
				t['grave'] = 0x0060;
				t['gravebelowcmb'] = 0x0316;
				t['gravecmb'] = 0x0300;
				t['gravecomb'] = 0x0300;
				t['gravedeva'] = 0x0953;
				t['gravelowmod'] = 0x02CE;
				t['gravemonospace'] = 0xFF40;
				t['gravetonecmb'] = 0x0340;
				t['greater'] = 0x003E;
				t['greaterequal'] = 0x2265;
				t['greaterequalorless'] = 0x22DB;
				t['greatermonospace'] = 0xFF1E;
				t['greaterorequivalent'] = 0x2273;
				t['greaterorless'] = 0x2277;
				t['greateroverequal'] = 0x2267;
				t['greatersmall'] = 0xFE65;
				t['gscript'] = 0x0261;
				t['gstroke'] = 0x01E5;
				t['guhiragana'] = 0x3050;
				t['guillemotleft'] = 0x00AB;
				t['guillemotright'] = 0x00BB;
				t['guilsinglleft'] = 0x2039;
				t['guilsinglright'] = 0x203A;
				t['gukatakana'] = 0x30B0;
				t['guramusquare'] = 0x3318;
				t['gysquare'] = 0x33C9;
				t['h'] = 0x0068;
				t['haabkhasiancyrillic'] = 0x04A9;
				t['haaltonearabic'] = 0x06C1;
				t['habengali'] = 0x09B9;
				t['hadescendercyrillic'] = 0x04B3;
				t['hadeva'] = 0x0939;
				t['hagujarati'] = 0x0AB9;
				t['hagurmukhi'] = 0x0A39;
				t['haharabic'] = 0x062D;
				t['hahfinalarabic'] = 0xFEA2;
				t['hahinitialarabic'] = 0xFEA3;
				t['hahiragana'] = 0x306F;
				t['hahmedialarabic'] = 0xFEA4;
				t['haitusquare'] = 0x332A;
				t['hakatakana'] = 0x30CF;
				t['hakatakanahalfwidth'] = 0xFF8A;
				t['halantgurmukhi'] = 0x0A4D;
				t['hamzaarabic'] = 0x0621;
				t['hamzalowarabic'] = 0x0621;
				t['hangulfiller'] = 0x3164;
				t['hardsigncyrillic'] = 0x044A;
				t['harpoonleftbarbup'] = 0x21BC;
				t['harpoonrightbarbup'] = 0x21C0;
				t['hasquare'] = 0x33CA;
				t['hatafpatah'] = 0x05B2;
				t['hatafpatah16'] = 0x05B2;
				t['hatafpatah23'] = 0x05B2;
				t['hatafpatah2f'] = 0x05B2;
				t['hatafpatahhebrew'] = 0x05B2;
				t['hatafpatahnarrowhebrew'] = 0x05B2;
				t['hatafpatahquarterhebrew'] = 0x05B2;
				t['hatafpatahwidehebrew'] = 0x05B2;
				t['hatafqamats'] = 0x05B3;
				t['hatafqamats1b'] = 0x05B3;
				t['hatafqamats28'] = 0x05B3;
				t['hatafqamats34'] = 0x05B3;
				t['hatafqamatshebrew'] = 0x05B3;
				t['hatafqamatsnarrowhebrew'] = 0x05B3;
				t['hatafqamatsquarterhebrew'] = 0x05B3;
				t['hatafqamatswidehebrew'] = 0x05B3;
				t['hatafsegol'] = 0x05B1;
				t['hatafsegol17'] = 0x05B1;
				t['hatafsegol24'] = 0x05B1;
				t['hatafsegol30'] = 0x05B1;
				t['hatafsegolhebrew'] = 0x05B1;
				t['hatafsegolnarrowhebrew'] = 0x05B1;
				t['hatafsegolquarterhebrew'] = 0x05B1;
				t['hatafsegolwidehebrew'] = 0x05B1;
				t['hbar'] = 0x0127;
				t['hbopomofo'] = 0x310F;
				t['hbrevebelow'] = 0x1E2B;
				t['hcedilla'] = 0x1E29;
				t['hcircle'] = 0x24D7;
				t['hcircumflex'] = 0x0125;
				t['hdieresis'] = 0x1E27;
				t['hdotaccent'] = 0x1E23;
				t['hdotbelow'] = 0x1E25;
				t['he'] = 0x05D4;
				t['heart'] = 0x2665;
				t['heartsuitblack'] = 0x2665;
				t['heartsuitwhite'] = 0x2661;
				t['hedagesh'] = 0xFB34;
				t['hedageshhebrew'] = 0xFB34;
				t['hehaltonearabic'] = 0x06C1;
				t['heharabic'] = 0x0647;
				t['hehebrew'] = 0x05D4;
				t['hehfinalaltonearabic'] = 0xFBA7;
				t['hehfinalalttwoarabic'] = 0xFEEA;
				t['hehfinalarabic'] = 0xFEEA;
				t['hehhamzaabovefinalarabic'] = 0xFBA5;
				t['hehhamzaaboveisolatedarabic'] = 0xFBA4;
				t['hehinitialaltonearabic'] = 0xFBA8;
				t['hehinitialarabic'] = 0xFEEB;
				t['hehiragana'] = 0x3078;
				t['hehmedialaltonearabic'] = 0xFBA9;
				t['hehmedialarabic'] = 0xFEEC;
				t['heiseierasquare'] = 0x337B;
				t['hekatakana'] = 0x30D8;
				t['hekatakanahalfwidth'] = 0xFF8D;
				t['hekutaarusquare'] = 0x3336;
				t['henghook'] = 0x0267;
				t['herutusquare'] = 0x3339;
				t['het'] = 0x05D7;
				t['hethebrew'] = 0x05D7;
				t['hhook'] = 0x0266;
				t['hhooksuperior'] = 0x02B1;
				t['hieuhacirclekorean'] = 0x327B;
				t['hieuhaparenkorean'] = 0x321B;
				t['hieuhcirclekorean'] = 0x326D;
				t['hieuhkorean'] = 0x314E;
				t['hieuhparenkorean'] = 0x320D;
				t['hihiragana'] = 0x3072;
				t['hikatakana'] = 0x30D2;
				t['hikatakanahalfwidth'] = 0xFF8B;
				t['hiriq'] = 0x05B4;
				t['hiriq14'] = 0x05B4;
				t['hiriq21'] = 0x05B4;
				t['hiriq2d'] = 0x05B4;
				t['hiriqhebrew'] = 0x05B4;
				t['hiriqnarrowhebrew'] = 0x05B4;
				t['hiriqquarterhebrew'] = 0x05B4;
				t['hiriqwidehebrew'] = 0x05B4;
				t['hlinebelow'] = 0x1E96;
				t['hmonospace'] = 0xFF48;
				t['hoarmenian'] = 0x0570;
				t['hohipthai'] = 0x0E2B;
				t['hohiragana'] = 0x307B;
				t['hokatakana'] = 0x30DB;
				t['hokatakanahalfwidth'] = 0xFF8E;
				t['holam'] = 0x05B9;
				t['holam19'] = 0x05B9;
				t['holam26'] = 0x05B9;
				t['holam32'] = 0x05B9;
				t['holamhebrew'] = 0x05B9;
				t['holamnarrowhebrew'] = 0x05B9;
				t['holamquarterhebrew'] = 0x05B9;
				t['holamwidehebrew'] = 0x05B9;
				t['honokhukthai'] = 0x0E2E;
				t['hookabovecomb'] = 0x0309;
				t['hookcmb'] = 0x0309;
				t['hookpalatalizedbelowcmb'] = 0x0321;
				t['hookretroflexbelowcmb'] = 0x0322;
				t['hoonsquare'] = 0x3342;
				t['horicoptic'] = 0x03E9;
				t['horizontalbar'] = 0x2015;
				t['horncmb'] = 0x031B;
				t['hotsprings'] = 0x2668;
				t['house'] = 0x2302;
				t['hparen'] = 0x24A3;
				t['hsuperior'] = 0x02B0;
				t['hturned'] = 0x0265;
				t['huhiragana'] = 0x3075;
				t['huiitosquare'] = 0x3333;
				t['hukatakana'] = 0x30D5;
				t['hukatakanahalfwidth'] = 0xFF8C;
				t['hungarumlaut'] = 0x02DD;
				t['hungarumlautcmb'] = 0x030B;
				t['hv'] = 0x0195;
				t['hyphen'] = 0x002D;
				t['hypheninferior'] = 0xF6E5;
				t['hyphenmonospace'] = 0xFF0D;
				t['hyphensmall'] = 0xFE63;
				t['hyphensuperior'] = 0xF6E6;
				t['hyphentwo'] = 0x2010;
				t['i'] = 0x0069;
				t['iacute'] = 0x00ED;
				t['iacyrillic'] = 0x044F;
				t['ibengali'] = 0x0987;
				t['ibopomofo'] = 0x3127;
				t['ibreve'] = 0x012D;
				t['icaron'] = 0x01D0;
				t['icircle'] = 0x24D8;
				t['icircumflex'] = 0x00EE;
				t['icyrillic'] = 0x0456;
				t['idblgrave'] = 0x0209;
				t['ideographearthcircle'] = 0x328F;
				t['ideographfirecircle'] = 0x328B;
				t['ideographicallianceparen'] = 0x323F;
				t['ideographiccallparen'] = 0x323A;
				t['ideographiccentrecircle'] = 0x32A5;
				t['ideographicclose'] = 0x3006;
				t['ideographiccomma'] = 0x3001;
				t['ideographiccommaleft'] = 0xFF64;
				t['ideographiccongratulationparen'] = 0x3237;
				t['ideographiccorrectcircle'] = 0x32A3;
				t['ideographicearthparen'] = 0x322F;
				t['ideographicenterpriseparen'] = 0x323D;
				t['ideographicexcellentcircle'] = 0x329D;
				t['ideographicfestivalparen'] = 0x3240;
				t['ideographicfinancialcircle'] = 0x3296;
				t['ideographicfinancialparen'] = 0x3236;
				t['ideographicfireparen'] = 0x322B;
				t['ideographichaveparen'] = 0x3232;
				t['ideographichighcircle'] = 0x32A4;
				t['ideographiciterationmark'] = 0x3005;
				t['ideographiclaborcircle'] = 0x3298;
				t['ideographiclaborparen'] = 0x3238;
				t['ideographicleftcircle'] = 0x32A7;
				t['ideographiclowcircle'] = 0x32A6;
				t['ideographicmedicinecircle'] = 0x32A9;
				t['ideographicmetalparen'] = 0x322E;
				t['ideographicmoonparen'] = 0x322A;
				t['ideographicnameparen'] = 0x3234;
				t['ideographicperiod'] = 0x3002;
				t['ideographicprintcircle'] = 0x329E;
				t['ideographicreachparen'] = 0x3243;
				t['ideographicrepresentparen'] = 0x3239;
				t['ideographicresourceparen'] = 0x323E;
				t['ideographicrightcircle'] = 0x32A8;
				t['ideographicsecretcircle'] = 0x3299;
				t['ideographicselfparen'] = 0x3242;
				t['ideographicsocietyparen'] = 0x3233;
				t['ideographicspace'] = 0x3000;
				t['ideographicspecialparen'] = 0x3235;
				t['ideographicstockparen'] = 0x3231;
				t['ideographicstudyparen'] = 0x323B;
				t['ideographicsunparen'] = 0x3230;
				t['ideographicsuperviseparen'] = 0x323C;
				t['ideographicwaterparen'] = 0x322C;
				t['ideographicwoodparen'] = 0x322D;
				t['ideographiczero'] = 0x3007;
				t['ideographmetalcircle'] = 0x328E;
				t['ideographmooncircle'] = 0x328A;
				t['ideographnamecircle'] = 0x3294;
				t['ideographsuncircle'] = 0x3290;
				t['ideographwatercircle'] = 0x328C;
				t['ideographwoodcircle'] = 0x328D;
				t['ideva'] = 0x0907;
				t['idieresis'] = 0x00EF;
				t['idieresisacute'] = 0x1E2F;
				t['idieresiscyrillic'] = 0x04E5;
				t['idotbelow'] = 0x1ECB;
				t['iebrevecyrillic'] = 0x04D7;
				t['iecyrillic'] = 0x0435;
				t['ieungacirclekorean'] = 0x3275;
				t['ieungaparenkorean'] = 0x3215;
				t['ieungcirclekorean'] = 0x3267;
				t['ieungkorean'] = 0x3147;
				t['ieungparenkorean'] = 0x3207;
				t['igrave'] = 0x00EC;
				t['igujarati'] = 0x0A87;
				t['igurmukhi'] = 0x0A07;
				t['ihiragana'] = 0x3044;
				t['ihookabove'] = 0x1EC9;
				t['iibengali'] = 0x0988;
				t['iicyrillic'] = 0x0438;
				t['iideva'] = 0x0908;
				t['iigujarati'] = 0x0A88;
				t['iigurmukhi'] = 0x0A08;
				t['iimatragurmukhi'] = 0x0A40;
				t['iinvertedbreve'] = 0x020B;
				t['iishortcyrillic'] = 0x0439;
				t['iivowelsignbengali'] = 0x09C0;
				t['iivowelsigndeva'] = 0x0940;
				t['iivowelsigngujarati'] = 0x0AC0;
				t['ij'] = 0x0133;
				t['ikatakana'] = 0x30A4;
				t['ikatakanahalfwidth'] = 0xFF72;
				t['ikorean'] = 0x3163;
				t['ilde'] = 0x02DC;
				t['iluyhebrew'] = 0x05AC;
				t['imacron'] = 0x012B;
				t['imacroncyrillic'] = 0x04E3;
				t['imageorapproximatelyequal'] = 0x2253;
				t['imatragurmukhi'] = 0x0A3F;
				t['imonospace'] = 0xFF49;
				t['increment'] = 0x2206;
				t['infinity'] = 0x221E;
				t['iniarmenian'] = 0x056B;
				t['integral'] = 0x222B;
				t['integralbottom'] = 0x2321;
				t['integralbt'] = 0x2321;
				t['integralex'] = 0xF8F5;
				t['integraltop'] = 0x2320;
				t['integraltp'] = 0x2320;
				t['intersection'] = 0x2229;
				t['intisquare'] = 0x3305;
				t['invbullet'] = 0x25D8;
				t['invcircle'] = 0x25D9;
				t['invsmileface'] = 0x263B;
				t['iocyrillic'] = 0x0451;
				t['iogonek'] = 0x012F;
				t['iota'] = 0x03B9;
				t['iotadieresis'] = 0x03CA;
				t['iotadieresistonos'] = 0x0390;
				t['iotalatin'] = 0x0269;
				t['iotatonos'] = 0x03AF;
				t['iparen'] = 0x24A4;
				t['irigurmukhi'] = 0x0A72;
				t['ismallhiragana'] = 0x3043;
				t['ismallkatakana'] = 0x30A3;
				t['ismallkatakanahalfwidth'] = 0xFF68;
				t['issharbengali'] = 0x09FA;
				t['istroke'] = 0x0268;
				t['isuperior'] = 0xF6ED;
				t['iterationhiragana'] = 0x309D;
				t['iterationkatakana'] = 0x30FD;
				t['itilde'] = 0x0129;
				t['itildebelow'] = 0x1E2D;
				t['iubopomofo'] = 0x3129;
				t['iucyrillic'] = 0x044E;
				t['ivowelsignbengali'] = 0x09BF;
				t['ivowelsigndeva'] = 0x093F;
				t['ivowelsigngujarati'] = 0x0ABF;
				t['izhitsacyrillic'] = 0x0475;
				t['izhitsadblgravecyrillic'] = 0x0477;
				t['j'] = 0x006A;
				t['jaarmenian'] = 0x0571;
				t['jabengali'] = 0x099C;
				t['jadeva'] = 0x091C;
				t['jagujarati'] = 0x0A9C;
				t['jagurmukhi'] = 0x0A1C;
				t['jbopomofo'] = 0x3110;
				t['jcaron'] = 0x01F0;
				t['jcircle'] = 0x24D9;
				t['jcircumflex'] = 0x0135;
				t['jcrossedtail'] = 0x029D;
				t['jdotlessstroke'] = 0x025F;
				t['jecyrillic'] = 0x0458;
				t['jeemarabic'] = 0x062C;
				t['jeemfinalarabic'] = 0xFE9E;
				t['jeeminitialarabic'] = 0xFE9F;
				t['jeemmedialarabic'] = 0xFEA0;
				t['jeharabic'] = 0x0698;
				t['jehfinalarabic'] = 0xFB8B;
				t['jhabengali'] = 0x099D;
				t['jhadeva'] = 0x091D;
				t['jhagujarati'] = 0x0A9D;
				t['jhagurmukhi'] = 0x0A1D;
				t['jheharmenian'] = 0x057B;
				t['jis'] = 0x3004;
				t['jmonospace'] = 0xFF4A;
				t['jparen'] = 0x24A5;
				t['jsuperior'] = 0x02B2;
				t['k'] = 0x006B;
				t['kabashkircyrillic'] = 0x04A1;
				t['kabengali'] = 0x0995;
				t['kacute'] = 0x1E31;
				t['kacyrillic'] = 0x043A;
				t['kadescendercyrillic'] = 0x049B;
				t['kadeva'] = 0x0915;
				t['kaf'] = 0x05DB;
				t['kafarabic'] = 0x0643;
				t['kafdagesh'] = 0xFB3B;
				t['kafdageshhebrew'] = 0xFB3B;
				t['kaffinalarabic'] = 0xFEDA;
				t['kafhebrew'] = 0x05DB;
				t['kafinitialarabic'] = 0xFEDB;
				t['kafmedialarabic'] = 0xFEDC;
				t['kafrafehebrew'] = 0xFB4D;
				t['kagujarati'] = 0x0A95;
				t['kagurmukhi'] = 0x0A15;
				t['kahiragana'] = 0x304B;
				t['kahookcyrillic'] = 0x04C4;
				t['kakatakana'] = 0x30AB;
				t['kakatakanahalfwidth'] = 0xFF76;
				t['kappa'] = 0x03BA;
				t['kappasymbolgreek'] = 0x03F0;
				t['kapyeounmieumkorean'] = 0x3171;
				t['kapyeounphieuphkorean'] = 0x3184;
				t['kapyeounpieupkorean'] = 0x3178;
				t['kapyeounssangpieupkorean'] = 0x3179;
				t['karoriisquare'] = 0x330D;
				t['kashidaautoarabic'] = 0x0640;
				t['kashidaautonosidebearingarabic'] = 0x0640;
				t['kasmallkatakana'] = 0x30F5;
				t['kasquare'] = 0x3384;
				t['kasraarabic'] = 0x0650;
				t['kasratanarabic'] = 0x064D;
				t['kastrokecyrillic'] = 0x049F;
				t['katahiraprolongmarkhalfwidth'] = 0xFF70;
				t['kaverticalstrokecyrillic'] = 0x049D;
				t['kbopomofo'] = 0x310E;
				t['kcalsquare'] = 0x3389;
				t['kcaron'] = 0x01E9;
				t['kcedilla'] = 0x0137;
				t['kcircle'] = 0x24DA;
				t['kcommaaccent'] = 0x0137;
				t['kdotbelow'] = 0x1E33;
				t['keharmenian'] = 0x0584;
				t['kehiragana'] = 0x3051;
				t['kekatakana'] = 0x30B1;
				t['kekatakanahalfwidth'] = 0xFF79;
				t['kenarmenian'] = 0x056F;
				t['kesmallkatakana'] = 0x30F6;
				t['kgreenlandic'] = 0x0138;
				t['khabengali'] = 0x0996;
				t['khacyrillic'] = 0x0445;
				t['khadeva'] = 0x0916;
				t['khagujarati'] = 0x0A96;
				t['khagurmukhi'] = 0x0A16;
				t['khaharabic'] = 0x062E;
				t['khahfinalarabic'] = 0xFEA6;
				t['khahinitialarabic'] = 0xFEA7;
				t['khahmedialarabic'] = 0xFEA8;
				t['kheicoptic'] = 0x03E7;
				t['khhadeva'] = 0x0959;
				t['khhagurmukhi'] = 0x0A59;
				t['khieukhacirclekorean'] = 0x3278;
				t['khieukhaparenkorean'] = 0x3218;
				t['khieukhcirclekorean'] = 0x326A;
				t['khieukhkorean'] = 0x314B;
				t['khieukhparenkorean'] = 0x320A;
				t['khokhaithai'] = 0x0E02;
				t['khokhonthai'] = 0x0E05;
				t['khokhuatthai'] = 0x0E03;
				t['khokhwaithai'] = 0x0E04;
				t['khomutthai'] = 0x0E5B;
				t['khook'] = 0x0199;
				t['khorakhangthai'] = 0x0E06;
				t['khzsquare'] = 0x3391;
				t['kihiragana'] = 0x304D;
				t['kikatakana'] = 0x30AD;
				t['kikatakanahalfwidth'] = 0xFF77;
				t['kiroguramusquare'] = 0x3315;
				t['kiromeetorusquare'] = 0x3316;
				t['kirosquare'] = 0x3314;
				t['kiyeokacirclekorean'] = 0x326E;
				t['kiyeokaparenkorean'] = 0x320E;
				t['kiyeokcirclekorean'] = 0x3260;
				t['kiyeokkorean'] = 0x3131;
				t['kiyeokparenkorean'] = 0x3200;
				t['kiyeoksioskorean'] = 0x3133;
				t['kjecyrillic'] = 0x045C;
				t['klinebelow'] = 0x1E35;
				t['klsquare'] = 0x3398;
				t['kmcubedsquare'] = 0x33A6;
				t['kmonospace'] = 0xFF4B;
				t['kmsquaredsquare'] = 0x33A2;
				t['kohiragana'] = 0x3053;
				t['kohmsquare'] = 0x33C0;
				t['kokaithai'] = 0x0E01;
				t['kokatakana'] = 0x30B3;
				t['kokatakanahalfwidth'] = 0xFF7A;
				t['kooposquare'] = 0x331E;
				t['koppacyrillic'] = 0x0481;
				t['koreanstandardsymbol'] = 0x327F;
				t['koroniscmb'] = 0x0343;
				t['kparen'] = 0x24A6;
				t['kpasquare'] = 0x33AA;
				t['ksicyrillic'] = 0x046F;
				t['ktsquare'] = 0x33CF;
				t['kturned'] = 0x029E;
				t['kuhiragana'] = 0x304F;
				t['kukatakana'] = 0x30AF;
				t['kukatakanahalfwidth'] = 0xFF78;
				t['kvsquare'] = 0x33B8;
				t['kwsquare'] = 0x33BE;
				t['l'] = 0x006C;
				t['labengali'] = 0x09B2;
				t['lacute'] = 0x013A;
				t['ladeva'] = 0x0932;
				t['lagujarati'] = 0x0AB2;
				t['lagurmukhi'] = 0x0A32;
				t['lakkhangyaothai'] = 0x0E45;
				t['lamaleffinalarabic'] = 0xFEFC;
				t['lamalefhamzaabovefinalarabic'] = 0xFEF8;
				t['lamalefhamzaaboveisolatedarabic'] = 0xFEF7;
				t['lamalefhamzabelowfinalarabic'] = 0xFEFA;
				t['lamalefhamzabelowisolatedarabic'] = 0xFEF9;
				t['lamalefisolatedarabic'] = 0xFEFB;
				t['lamalefmaddaabovefinalarabic'] = 0xFEF6;
				t['lamalefmaddaaboveisolatedarabic'] = 0xFEF5;
				t['lamarabic'] = 0x0644;
				t['lambda'] = 0x03BB;
				t['lambdastroke'] = 0x019B;
				t['lamed'] = 0x05DC;
				t['lameddagesh'] = 0xFB3C;
				t['lameddageshhebrew'] = 0xFB3C;
				t['lamedhebrew'] = 0x05DC;
				t['lamfinalarabic'] = 0xFEDE;
				t['lamhahinitialarabic'] = 0xFCCA;
				t['laminitialarabic'] = 0xFEDF;
				t['lamjeeminitialarabic'] = 0xFCC9;
				t['lamkhahinitialarabic'] = 0xFCCB;
				t['lamlamhehisolatedarabic'] = 0xFDF2;
				t['lammedialarabic'] = 0xFEE0;
				t['lammeemhahinitialarabic'] = 0xFD88;
				t['lammeeminitialarabic'] = 0xFCCC;
				t['largecircle'] = 0x25EF;
				t['lbar'] = 0x019A;
				t['lbelt'] = 0x026C;
				t['lbopomofo'] = 0x310C;
				t['lcaron'] = 0x013E;
				t['lcedilla'] = 0x013C;
				t['lcircle'] = 0x24DB;
				t['lcircumflexbelow'] = 0x1E3D;
				t['lcommaaccent'] = 0x013C;
				t['ldot'] = 0x0140;
				t['ldotaccent'] = 0x0140;
				t['ldotbelow'] = 0x1E37;
				t['ldotbelowmacron'] = 0x1E39;
				t['leftangleabovecmb'] = 0x031A;
				t['lefttackbelowcmb'] = 0x0318;
				t['less'] = 0x003C;
				t['lessequal'] = 0x2264;
				t['lessequalorgreater'] = 0x22DA;
				t['lessmonospace'] = 0xFF1C;
				t['lessorequivalent'] = 0x2272;
				t['lessorgreater'] = 0x2276;
				t['lessoverequal'] = 0x2266;
				t['lesssmall'] = 0xFE64;
				t['lezh'] = 0x026E;
				t['lfblock'] = 0x258C;
				t['lhookretroflex'] = 0x026D;
				t['lira'] = 0x20A4;
				t['liwnarmenian'] = 0x056C;
				t['lj'] = 0x01C9;
				t['ljecyrillic'] = 0x0459;
				t['ll'] = 0xF6C0;
				t['lladeva'] = 0x0933;
				t['llagujarati'] = 0x0AB3;
				t['llinebelow'] = 0x1E3B;
				t['llladeva'] = 0x0934;
				t['llvocalicbengali'] = 0x09E1;
				t['llvocalicdeva'] = 0x0961;
				t['llvocalicvowelsignbengali'] = 0x09E3;
				t['llvocalicvowelsigndeva'] = 0x0963;
				t['lmiddletilde'] = 0x026B;
				t['lmonospace'] = 0xFF4C;
				t['lmsquare'] = 0x33D0;
				t['lochulathai'] = 0x0E2C;
				t['logicaland'] = 0x2227;
				t['logicalnot'] = 0x00AC;
				t['logicalnotreversed'] = 0x2310;
				t['logicalor'] = 0x2228;
				t['lolingthai'] = 0x0E25;
				t['longs'] = 0x017F;
				t['lowlinecenterline'] = 0xFE4E;
				t['lowlinecmb'] = 0x0332;
				t['lowlinedashed'] = 0xFE4D;
				t['lozenge'] = 0x25CA;
				t['lparen'] = 0x24A7;
				t['lslash'] = 0x0142;
				t['lsquare'] = 0x2113;
				t['lsuperior'] = 0xF6EE;
				t['ltshade'] = 0x2591;
				t['luthai'] = 0x0E26;
				t['lvocalicbengali'] = 0x098C;
				t['lvocalicdeva'] = 0x090C;
				t['lvocalicvowelsignbengali'] = 0x09E2;
				t['lvocalicvowelsigndeva'] = 0x0962;
				t['lxsquare'] = 0x33D3;
				t['m'] = 0x006D;
				t['mabengali'] = 0x09AE;
				t['macron'] = 0x00AF;
				t['macronbelowcmb'] = 0x0331;
				t['macroncmb'] = 0x0304;
				t['macronlowmod'] = 0x02CD;
				t['macronmonospace'] = 0xFFE3;
				t['macute'] = 0x1E3F;
				t['madeva'] = 0x092E;
				t['magujarati'] = 0x0AAE;
				t['magurmukhi'] = 0x0A2E;
				t['mahapakhhebrew'] = 0x05A4;
				t['mahapakhlefthebrew'] = 0x05A4;
				t['mahiragana'] = 0x307E;
				t['maichattawalowleftthai'] = 0xF895;
				t['maichattawalowrightthai'] = 0xF894;
				t['maichattawathai'] = 0x0E4B;
				t['maichattawaupperleftthai'] = 0xF893;
				t['maieklowleftthai'] = 0xF88C;
				t['maieklowrightthai'] = 0xF88B;
				t['maiekthai'] = 0x0E48;
				t['maiekupperleftthai'] = 0xF88A;
				t['maihanakatleftthai'] = 0xF884;
				t['maihanakatthai'] = 0x0E31;
				t['maitaikhuleftthai'] = 0xF889;
				t['maitaikhuthai'] = 0x0E47;
				t['maitholowleftthai'] = 0xF88F;
				t['maitholowrightthai'] = 0xF88E;
				t['maithothai'] = 0x0E49;
				t['maithoupperleftthai'] = 0xF88D;
				t['maitrilowleftthai'] = 0xF892;
				t['maitrilowrightthai'] = 0xF891;
				t['maitrithai'] = 0x0E4A;
				t['maitriupperleftthai'] = 0xF890;
				t['maiyamokthai'] = 0x0E46;
				t['makatakana'] = 0x30DE;
				t['makatakanahalfwidth'] = 0xFF8F;
				t['male'] = 0x2642;
				t['mansyonsquare'] = 0x3347;
				t['maqafhebrew'] = 0x05BE;
				t['mars'] = 0x2642;
				t['masoracirclehebrew'] = 0x05AF;
				t['masquare'] = 0x3383;
				t['mbopomofo'] = 0x3107;
				t['mbsquare'] = 0x33D4;
				t['mcircle'] = 0x24DC;
				t['mcubedsquare'] = 0x33A5;
				t['mdotaccent'] = 0x1E41;
				t['mdotbelow'] = 0x1E43;
				t['meemarabic'] = 0x0645;
				t['meemfinalarabic'] = 0xFEE2;
				t['meeminitialarabic'] = 0xFEE3;
				t['meemmedialarabic'] = 0xFEE4;
				t['meemmeeminitialarabic'] = 0xFCD1;
				t['meemmeemisolatedarabic'] = 0xFC48;
				t['meetorusquare'] = 0x334D;
				t['mehiragana'] = 0x3081;
				t['meizierasquare'] = 0x337E;
				t['mekatakana'] = 0x30E1;
				t['mekatakanahalfwidth'] = 0xFF92;
				t['mem'] = 0x05DE;
				t['memdagesh'] = 0xFB3E;
				t['memdageshhebrew'] = 0xFB3E;
				t['memhebrew'] = 0x05DE;
				t['menarmenian'] = 0x0574;
				t['merkhahebrew'] = 0x05A5;
				t['merkhakefulahebrew'] = 0x05A6;
				t['merkhakefulalefthebrew'] = 0x05A6;
				t['merkhalefthebrew'] = 0x05A5;
				t['mhook'] = 0x0271;
				t['mhzsquare'] = 0x3392;
				t['middledotkatakanahalfwidth'] = 0xFF65;
				t['middot'] = 0x00B7;
				t['mieumacirclekorean'] = 0x3272;
				t['mieumaparenkorean'] = 0x3212;
				t['mieumcirclekorean'] = 0x3264;
				t['mieumkorean'] = 0x3141;
				t['mieumpansioskorean'] = 0x3170;
				t['mieumparenkorean'] = 0x3204;
				t['mieumpieupkorean'] = 0x316E;
				t['mieumsioskorean'] = 0x316F;
				t['mihiragana'] = 0x307F;
				t['mikatakana'] = 0x30DF;
				t['mikatakanahalfwidth'] = 0xFF90;
				t['minus'] = 0x2212;
				t['minusbelowcmb'] = 0x0320;
				t['minuscircle'] = 0x2296;
				t['minusmod'] = 0x02D7;
				t['minusplus'] = 0x2213;
				t['minute'] = 0x2032;
				t['miribaarusquare'] = 0x334A;
				t['mirisquare'] = 0x3349;
				t['mlonglegturned'] = 0x0270;
				t['mlsquare'] = 0x3396;
				t['mmcubedsquare'] = 0x33A3;
				t['mmonospace'] = 0xFF4D;
				t['mmsquaredsquare'] = 0x339F;
				t['mohiragana'] = 0x3082;
				t['mohmsquare'] = 0x33C1;
				t['mokatakana'] = 0x30E2;
				t['mokatakanahalfwidth'] = 0xFF93;
				t['molsquare'] = 0x33D6;
				t['momathai'] = 0x0E21;
				t['moverssquare'] = 0x33A7;
				t['moverssquaredsquare'] = 0x33A8;
				t['mparen'] = 0x24A8;
				t['mpasquare'] = 0x33AB;
				t['mssquare'] = 0x33B3;
				t['msuperior'] = 0xF6EF;
				t['mturned'] = 0x026F;
				t['mu'] = 0x00B5;
				t['mu1'] = 0x00B5;
				t['muasquare'] = 0x3382;
				t['muchgreater'] = 0x226B;
				t['muchless'] = 0x226A;
				t['mufsquare'] = 0x338C;
				t['mugreek'] = 0x03BC;
				t['mugsquare'] = 0x338D;
				t['muhiragana'] = 0x3080;
				t['mukatakana'] = 0x30E0;
				t['mukatakanahalfwidth'] = 0xFF91;
				t['mulsquare'] = 0x3395;
				t['multiply'] = 0x00D7;
				t['mumsquare'] = 0x339B;
				t['munahhebrew'] = 0x05A3;
				t['munahlefthebrew'] = 0x05A3;
				t['musicalnote'] = 0x266A;
				t['musicalnotedbl'] = 0x266B;
				t['musicflatsign'] = 0x266D;
				t['musicsharpsign'] = 0x266F;
				t['mussquare'] = 0x33B2;
				t['muvsquare'] = 0x33B6;
				t['muwsquare'] = 0x33BC;
				t['mvmegasquare'] = 0x33B9;
				t['mvsquare'] = 0x33B7;
				t['mwmegasquare'] = 0x33BF;
				t['mwsquare'] = 0x33BD;
				t['n'] = 0x006E;
				t['nabengali'] = 0x09A8;
				t['nabla'] = 0x2207;
				t['nacute'] = 0x0144;
				t['nadeva'] = 0x0928;
				t['nagujarati'] = 0x0AA8;
				t['nagurmukhi'] = 0x0A28;
				t['nahiragana'] = 0x306A;
				t['nakatakana'] = 0x30CA;
				t['nakatakanahalfwidth'] = 0xFF85;
				t['napostrophe'] = 0x0149;
				t['nasquare'] = 0x3381;
				t['nbopomofo'] = 0x310B;
				t['nbspace'] = 0x00A0;
				t['ncaron'] = 0x0148;
				t['ncedilla'] = 0x0146;
				t['ncircle'] = 0x24DD;
				t['ncircumflexbelow'] = 0x1E4B;
				t['ncommaaccent'] = 0x0146;
				t['ndotaccent'] = 0x1E45;
				t['ndotbelow'] = 0x1E47;
				t['nehiragana'] = 0x306D;
				t['nekatakana'] = 0x30CD;
				t['nekatakanahalfwidth'] = 0xFF88;
				t['newsheqelsign'] = 0x20AA;
				t['nfsquare'] = 0x338B;
				t['ngabengali'] = 0x0999;
				t['ngadeva'] = 0x0919;
				t['ngagujarati'] = 0x0A99;
				t['ngagurmukhi'] = 0x0A19;
				t['ngonguthai'] = 0x0E07;
				t['nhiragana'] = 0x3093;
				t['nhookleft'] = 0x0272;
				t['nhookretroflex'] = 0x0273;
				t['nieunacirclekorean'] = 0x326F;
				t['nieunaparenkorean'] = 0x320F;
				t['nieuncieuckorean'] = 0x3135;
				t['nieuncirclekorean'] = 0x3261;
				t['nieunhieuhkorean'] = 0x3136;
				t['nieunkorean'] = 0x3134;
				t['nieunpansioskorean'] = 0x3168;
				t['nieunparenkorean'] = 0x3201;
				t['nieunsioskorean'] = 0x3167;
				t['nieuntikeutkorean'] = 0x3166;
				t['nihiragana'] = 0x306B;
				t['nikatakana'] = 0x30CB;
				t['nikatakanahalfwidth'] = 0xFF86;
				t['nikhahitleftthai'] = 0xF899;
				t['nikhahitthai'] = 0x0E4D;
				t['nine'] = 0x0039;
				t['ninearabic'] = 0x0669;
				t['ninebengali'] = 0x09EF;
				t['ninecircle'] = 0x2468;
				t['ninecircleinversesansserif'] = 0x2792;
				t['ninedeva'] = 0x096F;
				t['ninegujarati'] = 0x0AEF;
				t['ninegurmukhi'] = 0x0A6F;
				t['ninehackarabic'] = 0x0669;
				t['ninehangzhou'] = 0x3029;
				t['nineideographicparen'] = 0x3228;
				t['nineinferior'] = 0x2089;
				t['ninemonospace'] = 0xFF19;
				t['nineoldstyle'] = 0xF739;
				t['nineparen'] = 0x247C;
				t['nineperiod'] = 0x2490;
				t['ninepersian'] = 0x06F9;
				t['nineroman'] = 0x2178;
				t['ninesuperior'] = 0x2079;
				t['nineteencircle'] = 0x2472;
				t['nineteenparen'] = 0x2486;
				t['nineteenperiod'] = 0x249A;
				t['ninethai'] = 0x0E59;
				t['nj'] = 0x01CC;
				t['njecyrillic'] = 0x045A;
				t['nkatakana'] = 0x30F3;
				t['nkatakanahalfwidth'] = 0xFF9D;
				t['nlegrightlong'] = 0x019E;
				t['nlinebelow'] = 0x1E49;
				t['nmonospace'] = 0xFF4E;
				t['nmsquare'] = 0x339A;
				t['nnabengali'] = 0x09A3;
				t['nnadeva'] = 0x0923;
				t['nnagujarati'] = 0x0AA3;
				t['nnagurmukhi'] = 0x0A23;
				t['nnnadeva'] = 0x0929;
				t['nohiragana'] = 0x306E;
				t['nokatakana'] = 0x30CE;
				t['nokatakanahalfwidth'] = 0xFF89;
				t['nonbreakingspace'] = 0x00A0;
				t['nonenthai'] = 0x0E13;
				t['nonuthai'] = 0x0E19;
				t['noonarabic'] = 0x0646;
				t['noonfinalarabic'] = 0xFEE6;
				t['noonghunnaarabic'] = 0x06BA;
				t['noonghunnafinalarabic'] = 0xFB9F;
				t['nooninitialarabic'] = 0xFEE7;
				t['noonjeeminitialarabic'] = 0xFCD2;
				t['noonjeemisolatedarabic'] = 0xFC4B;
				t['noonmedialarabic'] = 0xFEE8;
				t['noonmeeminitialarabic'] = 0xFCD5;
				t['noonmeemisolatedarabic'] = 0xFC4E;
				t['noonnoonfinalarabic'] = 0xFC8D;
				t['notcontains'] = 0x220C;
				t['notelement'] = 0x2209;
				t['notelementof'] = 0x2209;
				t['notequal'] = 0x2260;
				t['notgreater'] = 0x226F;
				t['notgreaternorequal'] = 0x2271;
				t['notgreaternorless'] = 0x2279;
				t['notidentical'] = 0x2262;
				t['notless'] = 0x226E;
				t['notlessnorequal'] = 0x2270;
				t['notparallel'] = 0x2226;
				t['notprecedes'] = 0x2280;
				t['notsubset'] = 0x2284;
				t['notsucceeds'] = 0x2281;
				t['notsuperset'] = 0x2285;
				t['nowarmenian'] = 0x0576;
				t['nparen'] = 0x24A9;
				t['nssquare'] = 0x33B1;
				t['nsuperior'] = 0x207F;
				t['ntilde'] = 0x00F1;
				t['nu'] = 0x03BD;
				t['nuhiragana'] = 0x306C;
				t['nukatakana'] = 0x30CC;
				t['nukatakanahalfwidth'] = 0xFF87;
				t['nuktabengali'] = 0x09BC;
				t['nuktadeva'] = 0x093C;
				t['nuktagujarati'] = 0x0ABC;
				t['nuktagurmukhi'] = 0x0A3C;
				t['numbersign'] = 0x0023;
				t['numbersignmonospace'] = 0xFF03;
				t['numbersignsmall'] = 0xFE5F;
				t['numeralsigngreek'] = 0x0374;
				t['numeralsignlowergreek'] = 0x0375;
				t['numero'] = 0x2116;
				t['nun'] = 0x05E0;
				t['nundagesh'] = 0xFB40;
				t['nundageshhebrew'] = 0xFB40;
				t['nunhebrew'] = 0x05E0;
				t['nvsquare'] = 0x33B5;
				t['nwsquare'] = 0x33BB;
				t['nyabengali'] = 0x099E;
				t['nyadeva'] = 0x091E;
				t['nyagujarati'] = 0x0A9E;
				t['nyagurmukhi'] = 0x0A1E;
				t['o'] = 0x006F;
				t['oacute'] = 0x00F3;
				t['oangthai'] = 0x0E2D;
				t['obarred'] = 0x0275;
				t['obarredcyrillic'] = 0x04E9;
				t['obarreddieresiscyrillic'] = 0x04EB;
				t['obengali'] = 0x0993;
				t['obopomofo'] = 0x311B;
				t['obreve'] = 0x014F;
				t['ocandradeva'] = 0x0911;
				t['ocandragujarati'] = 0x0A91;
				t['ocandravowelsigndeva'] = 0x0949;
				t['ocandravowelsigngujarati'] = 0x0AC9;
				t['ocaron'] = 0x01D2;
				t['ocircle'] = 0x24DE;
				t['ocircumflex'] = 0x00F4;
				t['ocircumflexacute'] = 0x1ED1;
				t['ocircumflexdotbelow'] = 0x1ED9;
				t['ocircumflexgrave'] = 0x1ED3;
				t['ocircumflexhookabove'] = 0x1ED5;
				t['ocircumflextilde'] = 0x1ED7;
				t['ocyrillic'] = 0x043E;
				t['odblacute'] = 0x0151;
				t['odblgrave'] = 0x020D;
				t['odeva'] = 0x0913;
				t['odieresis'] = 0x00F6;
				t['odieresiscyrillic'] = 0x04E7;
				t['odotbelow'] = 0x1ECD;
				t['oe'] = 0x0153;
				t['oekorean'] = 0x315A;
				t['ogonek'] = 0x02DB;
				t['ogonekcmb'] = 0x0328;
				t['ograve'] = 0x00F2;
				t['ogujarati'] = 0x0A93;
				t['oharmenian'] = 0x0585;
				t['ohiragana'] = 0x304A;
				t['ohookabove'] = 0x1ECF;
				t['ohorn'] = 0x01A1;
				t['ohornacute'] = 0x1EDB;
				t['ohorndotbelow'] = 0x1EE3;
				t['ohorngrave'] = 0x1EDD;
				t['ohornhookabove'] = 0x1EDF;
				t['ohorntilde'] = 0x1EE1;
				t['ohungarumlaut'] = 0x0151;
				t['oi'] = 0x01A3;
				t['oinvertedbreve'] = 0x020F;
				t['okatakana'] = 0x30AA;
				t['okatakanahalfwidth'] = 0xFF75;
				t['okorean'] = 0x3157;
				t['olehebrew'] = 0x05AB;
				t['omacron'] = 0x014D;
				t['omacronacute'] = 0x1E53;
				t['omacrongrave'] = 0x1E51;
				t['omdeva'] = 0x0950;
				t['omega'] = 0x03C9;
				t['omega1'] = 0x03D6;
				t['omegacyrillic'] = 0x0461;
				t['omegalatinclosed'] = 0x0277;
				t['omegaroundcyrillic'] = 0x047B;
				t['omegatitlocyrillic'] = 0x047D;
				t['omegatonos'] = 0x03CE;
				t['omgujarati'] = 0x0AD0;
				t['omicron'] = 0x03BF;
				t['omicrontonos'] = 0x03CC;
				t['omonospace'] = 0xFF4F;
				t['one'] = 0x0031;
				t['onearabic'] = 0x0661;
				t['onebengali'] = 0x09E7;
				t['onecircle'] = 0x2460;
				t['onecircleinversesansserif'] = 0x278A;
				t['onedeva'] = 0x0967;
				t['onedotenleader'] = 0x2024;
				t['oneeighth'] = 0x215B;
				t['onefitted'] = 0xF6DC;
				t['onegujarati'] = 0x0AE7;
				t['onegurmukhi'] = 0x0A67;
				t['onehackarabic'] = 0x0661;
				t['onehalf'] = 0x00BD;
				t['onehangzhou'] = 0x3021;
				t['oneideographicparen'] = 0x3220;
				t['oneinferior'] = 0x2081;
				t['onemonospace'] = 0xFF11;
				t['onenumeratorbengali'] = 0x09F4;
				t['oneoldstyle'] = 0xF731;
				t['oneparen'] = 0x2474;
				t['oneperiod'] = 0x2488;
				t['onepersian'] = 0x06F1;
				t['onequarter'] = 0x00BC;
				t['oneroman'] = 0x2170;
				t['onesuperior'] = 0x00B9;
				t['onethai'] = 0x0E51;
				t['onethird'] = 0x2153;
				t['oogonek'] = 0x01EB;
				t['oogonekmacron'] = 0x01ED;
				t['oogurmukhi'] = 0x0A13;
				t['oomatragurmukhi'] = 0x0A4B;
				t['oopen'] = 0x0254;
				t['oparen'] = 0x24AA;
				t['openbullet'] = 0x25E6;
				t['option'] = 0x2325;
				t['ordfeminine'] = 0x00AA;
				t['ordmasculine'] = 0x00BA;
				t['orthogonal'] = 0x221F;
				t['oshortdeva'] = 0x0912;
				t['oshortvowelsigndeva'] = 0x094A;
				t['oslash'] = 0x00F8;
				t['oslashacute'] = 0x01FF;
				t['osmallhiragana'] = 0x3049;
				t['osmallkatakana'] = 0x30A9;
				t['osmallkatakanahalfwidth'] = 0xFF6B;
				t['ostrokeacute'] = 0x01FF;
				t['osuperior'] = 0xF6F0;
				t['otcyrillic'] = 0x047F;
				t['otilde'] = 0x00F5;
				t['otildeacute'] = 0x1E4D;
				t['otildedieresis'] = 0x1E4F;
				t['oubopomofo'] = 0x3121;
				t['overline'] = 0x203E;
				t['overlinecenterline'] = 0xFE4A;
				t['overlinecmb'] = 0x0305;
				t['overlinedashed'] = 0xFE49;
				t['overlinedblwavy'] = 0xFE4C;
				t['overlinewavy'] = 0xFE4B;
				t['overscore'] = 0x00AF;
				t['ovowelsignbengali'] = 0x09CB;
				t['ovowelsigndeva'] = 0x094B;
				t['ovowelsigngujarati'] = 0x0ACB;
				t['p'] = 0x0070;
				t['paampssquare'] = 0x3380;
				t['paasentosquare'] = 0x332B;
				t['pabengali'] = 0x09AA;
				t['pacute'] = 0x1E55;
				t['padeva'] = 0x092A;
				t['pagedown'] = 0x21DF;
				t['pageup'] = 0x21DE;
				t['pagujarati'] = 0x0AAA;
				t['pagurmukhi'] = 0x0A2A;
				t['pahiragana'] = 0x3071;
				t['paiyannoithai'] = 0x0E2F;
				t['pakatakana'] = 0x30D1;
				t['palatalizationcyrilliccmb'] = 0x0484;
				t['palochkacyrillic'] = 0x04C0;
				t['pansioskorean'] = 0x317F;
				t['paragraph'] = 0x00B6;
				t['parallel'] = 0x2225;
				t['parenleft'] = 0x0028;
				t['parenleftaltonearabic'] = 0xFD3E;
				t['parenleftbt'] = 0xF8ED;
				t['parenleftex'] = 0xF8EC;
				t['parenleftinferior'] = 0x208D;
				t['parenleftmonospace'] = 0xFF08;
				t['parenleftsmall'] = 0xFE59;
				t['parenleftsuperior'] = 0x207D;
				t['parenlefttp'] = 0xF8EB;
				t['parenleftvertical'] = 0xFE35;
				t['parenright'] = 0x0029;
				t['parenrightaltonearabic'] = 0xFD3F;
				t['parenrightbt'] = 0xF8F8;
				t['parenrightex'] = 0xF8F7;
				t['parenrightinferior'] = 0x208E;
				t['parenrightmonospace'] = 0xFF09;
				t['parenrightsmall'] = 0xFE5A;
				t['parenrightsuperior'] = 0x207E;
				t['parenrighttp'] = 0xF8F6;
				t['parenrightvertical'] = 0xFE36;
				t['partialdiff'] = 0x2202;
				t['paseqhebrew'] = 0x05C0;
				t['pashtahebrew'] = 0x0599;
				t['pasquare'] = 0x33A9;
				t['patah'] = 0x05B7;
				t['patah11'] = 0x05B7;
				t['patah1d'] = 0x05B7;
				t['patah2a'] = 0x05B7;
				t['patahhebrew'] = 0x05B7;
				t['patahnarrowhebrew'] = 0x05B7;
				t['patahquarterhebrew'] = 0x05B7;
				t['patahwidehebrew'] = 0x05B7;
				t['pazerhebrew'] = 0x05A1;
				t['pbopomofo'] = 0x3106;
				t['pcircle'] = 0x24DF;
				t['pdotaccent'] = 0x1E57;
				t['pe'] = 0x05E4;
				t['pecyrillic'] = 0x043F;
				t['pedagesh'] = 0xFB44;
				t['pedageshhebrew'] = 0xFB44;
				t['peezisquare'] = 0x333B;
				t['pefinaldageshhebrew'] = 0xFB43;
				t['peharabic'] = 0x067E;
				t['peharmenian'] = 0x057A;
				t['pehebrew'] = 0x05E4;
				t['pehfinalarabic'] = 0xFB57;
				t['pehinitialarabic'] = 0xFB58;
				t['pehiragana'] = 0x307A;
				t['pehmedialarabic'] = 0xFB59;
				t['pekatakana'] = 0x30DA;
				t['pemiddlehookcyrillic'] = 0x04A7;
				t['perafehebrew'] = 0xFB4E;
				t['percent'] = 0x0025;
				t['percentarabic'] = 0x066A;
				t['percentmonospace'] = 0xFF05;
				t['percentsmall'] = 0xFE6A;
				t['period'] = 0x002E;
				t['periodarmenian'] = 0x0589;
				t['periodcentered'] = 0x00B7;
				t['periodhalfwidth'] = 0xFF61;
				t['periodinferior'] = 0xF6E7;
				t['periodmonospace'] = 0xFF0E;
				t['periodsmall'] = 0xFE52;
				t['periodsuperior'] = 0xF6E8;
				t['perispomenigreekcmb'] = 0x0342;
				t['perpendicular'] = 0x22A5;
				t['perthousand'] = 0x2030;
				t['peseta'] = 0x20A7;
				t['pfsquare'] = 0x338A;
				t['phabengali'] = 0x09AB;
				t['phadeva'] = 0x092B;
				t['phagujarati'] = 0x0AAB;
				t['phagurmukhi'] = 0x0A2B;
				t['phi'] = 0x03C6;
				t['phi1'] = 0x03D5;
				t['phieuphacirclekorean'] = 0x327A;
				t['phieuphaparenkorean'] = 0x321A;
				t['phieuphcirclekorean'] = 0x326C;
				t['phieuphkorean'] = 0x314D;
				t['phieuphparenkorean'] = 0x320C;
				t['philatin'] = 0x0278;
				t['phinthuthai'] = 0x0E3A;
				t['phisymbolgreek'] = 0x03D5;
				t['phook'] = 0x01A5;
				t['phophanthai'] = 0x0E1E;
				t['phophungthai'] = 0x0E1C;
				t['phosamphaothai'] = 0x0E20;
				t['pi'] = 0x03C0;
				t['pieupacirclekorean'] = 0x3273;
				t['pieupaparenkorean'] = 0x3213;
				t['pieupcieuckorean'] = 0x3176;
				t['pieupcirclekorean'] = 0x3265;
				t['pieupkiyeokkorean'] = 0x3172;
				t['pieupkorean'] = 0x3142;
				t['pieupparenkorean'] = 0x3205;
				t['pieupsioskiyeokkorean'] = 0x3174;
				t['pieupsioskorean'] = 0x3144;
				t['pieupsiostikeutkorean'] = 0x3175;
				t['pieupthieuthkorean'] = 0x3177;
				t['pieuptikeutkorean'] = 0x3173;
				t['pihiragana'] = 0x3074;
				t['pikatakana'] = 0x30D4;
				t['pisymbolgreek'] = 0x03D6;
				t['piwrarmenian'] = 0x0583;
				t['plus'] = 0x002B;
				t['plusbelowcmb'] = 0x031F;
				t['pluscircle'] = 0x2295;
				t['plusminus'] = 0x00B1;
				t['plusmod'] = 0x02D6;
				t['plusmonospace'] = 0xFF0B;
				t['plussmall'] = 0xFE62;
				t['plussuperior'] = 0x207A;
				t['pmonospace'] = 0xFF50;
				t['pmsquare'] = 0x33D8;
				t['pohiragana'] = 0x307D;
				t['pointingindexdownwhite'] = 0x261F;
				t['pointingindexleftwhite'] = 0x261C;
				t['pointingindexrightwhite'] = 0x261E;
				t['pointingindexupwhite'] = 0x261D;
				t['pokatakana'] = 0x30DD;
				t['poplathai'] = 0x0E1B;
				t['postalmark'] = 0x3012;
				t['postalmarkface'] = 0x3020;
				t['pparen'] = 0x24AB;
				t['precedes'] = 0x227A;
				t['prescription'] = 0x211E;
				t['primemod'] = 0x02B9;
				t['primereversed'] = 0x2035;
				t['product'] = 0x220F;
				t['projective'] = 0x2305;
				t['prolongedkana'] = 0x30FC;
				t['propellor'] = 0x2318;
				t['propersubset'] = 0x2282;
				t['propersuperset'] = 0x2283;
				t['proportion'] = 0x2237;
				t['proportional'] = 0x221D;
				t['psi'] = 0x03C8;
				t['psicyrillic'] = 0x0471;
				t['psilipneumatacyrilliccmb'] = 0x0486;
				t['pssquare'] = 0x33B0;
				t['puhiragana'] = 0x3077;
				t['pukatakana'] = 0x30D7;
				t['pvsquare'] = 0x33B4;
				t['pwsquare'] = 0x33BA;
				t['q'] = 0x0071;
				t['qadeva'] = 0x0958;
				t['qadmahebrew'] = 0x05A8;
				t['qafarabic'] = 0x0642;
				t['qaffinalarabic'] = 0xFED6;
				t['qafinitialarabic'] = 0xFED7;
				t['qafmedialarabic'] = 0xFED8;
				t['qamats'] = 0x05B8;
				t['qamats10'] = 0x05B8;
				t['qamats1a'] = 0x05B8;
				t['qamats1c'] = 0x05B8;
				t['qamats27'] = 0x05B8;
				t['qamats29'] = 0x05B8;
				t['qamats33'] = 0x05B8;
				t['qamatsde'] = 0x05B8;
				t['qamatshebrew'] = 0x05B8;
				t['qamatsnarrowhebrew'] = 0x05B8;
				t['qamatsqatanhebrew'] = 0x05B8;
				t['qamatsqatannarrowhebrew'] = 0x05B8;
				t['qamatsqatanquarterhebrew'] = 0x05B8;
				t['qamatsqatanwidehebrew'] = 0x05B8;
				t['qamatsquarterhebrew'] = 0x05B8;
				t['qamatswidehebrew'] = 0x05B8;
				t['qarneyparahebrew'] = 0x059F;
				t['qbopomofo'] = 0x3111;
				t['qcircle'] = 0x24E0;
				t['qhook'] = 0x02A0;
				t['qmonospace'] = 0xFF51;
				t['qof'] = 0x05E7;
				t['qofdagesh'] = 0xFB47;
				t['qofdageshhebrew'] = 0xFB47;
				t['qofhebrew'] = 0x05E7;
				t['qparen'] = 0x24AC;
				t['quarternote'] = 0x2669;
				t['qubuts'] = 0x05BB;
				t['qubuts18'] = 0x05BB;
				t['qubuts25'] = 0x05BB;
				t['qubuts31'] = 0x05BB;
				t['qubutshebrew'] = 0x05BB;
				t['qubutsnarrowhebrew'] = 0x05BB;
				t['qubutsquarterhebrew'] = 0x05BB;
				t['qubutswidehebrew'] = 0x05BB;
				t['question'] = 0x003F;
				t['questionarabic'] = 0x061F;
				t['questionarmenian'] = 0x055E;
				t['questiondown'] = 0x00BF;
				t['questiondownsmall'] = 0xF7BF;
				t['questiongreek'] = 0x037E;
				t['questionmonospace'] = 0xFF1F;
				t['questionsmall'] = 0xF73F;
				t['quotedbl'] = 0x0022;
				t['quotedblbase'] = 0x201E;
				t['quotedblleft'] = 0x201C;
				t['quotedblmonospace'] = 0xFF02;
				t['quotedblprime'] = 0x301E;
				t['quotedblprimereversed'] = 0x301D;
				t['quotedblright'] = 0x201D;
				t['quoteleft'] = 0x2018;
				t['quoteleftreversed'] = 0x201B;
				t['quotereversed'] = 0x201B;
				t['quoteright'] = 0x2019;
				t['quoterightn'] = 0x0149;
				t['quotesinglbase'] = 0x201A;
				t['quotesingle'] = 0x0027;
				t['quotesinglemonospace'] = 0xFF07;
				t['r'] = 0x0072;
				t['raarmenian'] = 0x057C;
				t['rabengali'] = 0x09B0;
				t['racute'] = 0x0155;
				t['radeva'] = 0x0930;
				t['radical'] = 0x221A;
				t['radicalex'] = 0xF8E5;
				t['radoverssquare'] = 0x33AE;
				t['radoverssquaredsquare'] = 0x33AF;
				t['radsquare'] = 0x33AD;
				t['rafe'] = 0x05BF;
				t['rafehebrew'] = 0x05BF;
				t['ragujarati'] = 0x0AB0;
				t['ragurmukhi'] = 0x0A30;
				t['rahiragana'] = 0x3089;
				t['rakatakana'] = 0x30E9;
				t['rakatakanahalfwidth'] = 0xFF97;
				t['ralowerdiagonalbengali'] = 0x09F1;
				t['ramiddlediagonalbengali'] = 0x09F0;
				t['ramshorn'] = 0x0264;
				t['ratio'] = 0x2236;
				t['rbopomofo'] = 0x3116;
				t['rcaron'] = 0x0159;
				t['rcedilla'] = 0x0157;
				t['rcircle'] = 0x24E1;
				t['rcommaaccent'] = 0x0157;
				t['rdblgrave'] = 0x0211;
				t['rdotaccent'] = 0x1E59;
				t['rdotbelow'] = 0x1E5B;
				t['rdotbelowmacron'] = 0x1E5D;
				t['referencemark'] = 0x203B;
				t['reflexsubset'] = 0x2286;
				t['reflexsuperset'] = 0x2287;
				t['registered'] = 0x00AE;
				t['registersans'] = 0xF8E8;
				t['registerserif'] = 0xF6DA;
				t['reharabic'] = 0x0631;
				t['reharmenian'] = 0x0580;
				t['rehfinalarabic'] = 0xFEAE;
				t['rehiragana'] = 0x308C;
				t['rekatakana'] = 0x30EC;
				t['rekatakanahalfwidth'] = 0xFF9A;
				t['resh'] = 0x05E8;
				t['reshdageshhebrew'] = 0xFB48;
				t['reshhebrew'] = 0x05E8;
				t['reversedtilde'] = 0x223D;
				t['reviahebrew'] = 0x0597;
				t['reviamugrashhebrew'] = 0x0597;
				t['revlogicalnot'] = 0x2310;
				t['rfishhook'] = 0x027E;
				t['rfishhookreversed'] = 0x027F;
				t['rhabengali'] = 0x09DD;
				t['rhadeva'] = 0x095D;
				t['rho'] = 0x03C1;
				t['rhook'] = 0x027D;
				t['rhookturned'] = 0x027B;
				t['rhookturnedsuperior'] = 0x02B5;
				t['rhosymbolgreek'] = 0x03F1;
				t['rhotichookmod'] = 0x02DE;
				t['rieulacirclekorean'] = 0x3271;
				t['rieulaparenkorean'] = 0x3211;
				t['rieulcirclekorean'] = 0x3263;
				t['rieulhieuhkorean'] = 0x3140;
				t['rieulkiyeokkorean'] = 0x313A;
				t['rieulkiyeoksioskorean'] = 0x3169;
				t['rieulkorean'] = 0x3139;
				t['rieulmieumkorean'] = 0x313B;
				t['rieulpansioskorean'] = 0x316C;
				t['rieulparenkorean'] = 0x3203;
				t['rieulphieuphkorean'] = 0x313F;
				t['rieulpieupkorean'] = 0x313C;
				t['rieulpieupsioskorean'] = 0x316B;
				t['rieulsioskorean'] = 0x313D;
				t['rieulthieuthkorean'] = 0x313E;
				t['rieultikeutkorean'] = 0x316A;
				t['rieulyeorinhieuhkorean'] = 0x316D;
				t['rightangle'] = 0x221F;
				t['righttackbelowcmb'] = 0x0319;
				t['righttriangle'] = 0x22BF;
				t['rihiragana'] = 0x308A;
				t['rikatakana'] = 0x30EA;
				t['rikatakanahalfwidth'] = 0xFF98;
				t['ring'] = 0x02DA;
				t['ringbelowcmb'] = 0x0325;
				t['ringcmb'] = 0x030A;
				t['ringhalfleft'] = 0x02BF;
				t['ringhalfleftarmenian'] = 0x0559;
				t['ringhalfleftbelowcmb'] = 0x031C;
				t['ringhalfleftcentered'] = 0x02D3;
				t['ringhalfright'] = 0x02BE;
				t['ringhalfrightbelowcmb'] = 0x0339;
				t['ringhalfrightcentered'] = 0x02D2;
				t['rinvertedbreve'] = 0x0213;
				t['rittorusquare'] = 0x3351;
				t['rlinebelow'] = 0x1E5F;
				t['rlongleg'] = 0x027C;
				t['rlonglegturned'] = 0x027A;
				t['rmonospace'] = 0xFF52;
				t['rohiragana'] = 0x308D;
				t['rokatakana'] = 0x30ED;
				t['rokatakanahalfwidth'] = 0xFF9B;
				t['roruathai'] = 0x0E23;
				t['rparen'] = 0x24AD;
				t['rrabengali'] = 0x09DC;
				t['rradeva'] = 0x0931;
				t['rragurmukhi'] = 0x0A5C;
				t['rreharabic'] = 0x0691;
				t['rrehfinalarabic'] = 0xFB8D;
				t['rrvocalicbengali'] = 0x09E0;
				t['rrvocalicdeva'] = 0x0960;
				t['rrvocalicgujarati'] = 0x0AE0;
				t['rrvocalicvowelsignbengali'] = 0x09C4;
				t['rrvocalicvowelsigndeva'] = 0x0944;
				t['rrvocalicvowelsigngujarati'] = 0x0AC4;
				t['rsuperior'] = 0xF6F1;
				t['rtblock'] = 0x2590;
				t['rturned'] = 0x0279;
				t['rturnedsuperior'] = 0x02B4;
				t['ruhiragana'] = 0x308B;
				t['rukatakana'] = 0x30EB;
				t['rukatakanahalfwidth'] = 0xFF99;
				t['rupeemarkbengali'] = 0x09F2;
				t['rupeesignbengali'] = 0x09F3;
				t['rupiah'] = 0xF6DD;
				t['ruthai'] = 0x0E24;
				t['rvocalicbengali'] = 0x098B;
				t['rvocalicdeva'] = 0x090B;
				t['rvocalicgujarati'] = 0x0A8B;
				t['rvocalicvowelsignbengali'] = 0x09C3;
				t['rvocalicvowelsigndeva'] = 0x0943;
				t['rvocalicvowelsigngujarati'] = 0x0AC3;
				t['s'] = 0x0073;
				t['sabengali'] = 0x09B8;
				t['sacute'] = 0x015B;
				t['sacutedotaccent'] = 0x1E65;
				t['sadarabic'] = 0x0635;
				t['sadeva'] = 0x0938;
				t['sadfinalarabic'] = 0xFEBA;
				t['sadinitialarabic'] = 0xFEBB;
				t['sadmedialarabic'] = 0xFEBC;
				t['sagujarati'] = 0x0AB8;
				t['sagurmukhi'] = 0x0A38;
				t['sahiragana'] = 0x3055;
				t['sakatakana'] = 0x30B5;
				t['sakatakanahalfwidth'] = 0xFF7B;
				t['sallallahoualayhewasallamarabic'] = 0xFDFA;
				t['samekh'] = 0x05E1;
				t['samekhdagesh'] = 0xFB41;
				t['samekhdageshhebrew'] = 0xFB41;
				t['samekhhebrew'] = 0x05E1;
				t['saraaathai'] = 0x0E32;
				t['saraaethai'] = 0x0E41;
				t['saraaimaimalaithai'] = 0x0E44;
				t['saraaimaimuanthai'] = 0x0E43;
				t['saraamthai'] = 0x0E33;
				t['saraathai'] = 0x0E30;
				t['saraethai'] = 0x0E40;
				t['saraiileftthai'] = 0xF886;
				t['saraiithai'] = 0x0E35;
				t['saraileftthai'] = 0xF885;
				t['saraithai'] = 0x0E34;
				t['saraothai'] = 0x0E42;
				t['saraueeleftthai'] = 0xF888;
				t['saraueethai'] = 0x0E37;
				t['saraueleftthai'] = 0xF887;
				t['sarauethai'] = 0x0E36;
				t['sarauthai'] = 0x0E38;
				t['sarauuthai'] = 0x0E39;
				t['sbopomofo'] = 0x3119;
				t['scaron'] = 0x0161;
				t['scarondotaccent'] = 0x1E67;
				t['scedilla'] = 0x015F;
				t['schwa'] = 0x0259;
				t['schwacyrillic'] = 0x04D9;
				t['schwadieresiscyrillic'] = 0x04DB;
				t['schwahook'] = 0x025A;
				t['scircle'] = 0x24E2;
				t['scircumflex'] = 0x015D;
				t['scommaaccent'] = 0x0219;
				t['sdotaccent'] = 0x1E61;
				t['sdotbelow'] = 0x1E63;
				t['sdotbelowdotaccent'] = 0x1E69;
				t['seagullbelowcmb'] = 0x033C;
				t['second'] = 0x2033;
				t['secondtonechinese'] = 0x02CA;
				t['section'] = 0x00A7;
				t['seenarabic'] = 0x0633;
				t['seenfinalarabic'] = 0xFEB2;
				t['seeninitialarabic'] = 0xFEB3;
				t['seenmedialarabic'] = 0xFEB4;
				t['segol'] = 0x05B6;
				t['segol13'] = 0x05B6;
				t['segol1f'] = 0x05B6;
				t['segol2c'] = 0x05B6;
				t['segolhebrew'] = 0x05B6;
				t['segolnarrowhebrew'] = 0x05B6;
				t['segolquarterhebrew'] = 0x05B6;
				t['segoltahebrew'] = 0x0592;
				t['segolwidehebrew'] = 0x05B6;
				t['seharmenian'] = 0x057D;
				t['sehiragana'] = 0x305B;
				t['sekatakana'] = 0x30BB;
				t['sekatakanahalfwidth'] = 0xFF7E;
				t['semicolon'] = 0x003B;
				t['semicolonarabic'] = 0x061B;
				t['semicolonmonospace'] = 0xFF1B;
				t['semicolonsmall'] = 0xFE54;
				t['semivoicedmarkkana'] = 0x309C;
				t['semivoicedmarkkanahalfwidth'] = 0xFF9F;
				t['sentisquare'] = 0x3322;
				t['sentosquare'] = 0x3323;
				t['seven'] = 0x0037;
				t['sevenarabic'] = 0x0667;
				t['sevenbengali'] = 0x09ED;
				t['sevencircle'] = 0x2466;
				t['sevencircleinversesansserif'] = 0x2790;
				t['sevendeva'] = 0x096D;
				t['seveneighths'] = 0x215E;
				t['sevengujarati'] = 0x0AED;
				t['sevengurmukhi'] = 0x0A6D;
				t['sevenhackarabic'] = 0x0667;
				t['sevenhangzhou'] = 0x3027;
				t['sevenideographicparen'] = 0x3226;
				t['seveninferior'] = 0x2087;
				t['sevenmonospace'] = 0xFF17;
				t['sevenoldstyle'] = 0xF737;
				t['sevenparen'] = 0x247A;
				t['sevenperiod'] = 0x248E;
				t['sevenpersian'] = 0x06F7;
				t['sevenroman'] = 0x2176;
				t['sevensuperior'] = 0x2077;
				t['seventeencircle'] = 0x2470;
				t['seventeenparen'] = 0x2484;
				t['seventeenperiod'] = 0x2498;
				t['seventhai'] = 0x0E57;
				t['sfthyphen'] = 0x00AD;
				t['shaarmenian'] = 0x0577;
				t['shabengali'] = 0x09B6;
				t['shacyrillic'] = 0x0448;
				t['shaddaarabic'] = 0x0651;
				t['shaddadammaarabic'] = 0xFC61;
				t['shaddadammatanarabic'] = 0xFC5E;
				t['shaddafathaarabic'] = 0xFC60;
				t['shaddakasraarabic'] = 0xFC62;
				t['shaddakasratanarabic'] = 0xFC5F;
				t['shade'] = 0x2592;
				t['shadedark'] = 0x2593;
				t['shadelight'] = 0x2591;
				t['shademedium'] = 0x2592;
				t['shadeva'] = 0x0936;
				t['shagujarati'] = 0x0AB6;
				t['shagurmukhi'] = 0x0A36;
				t['shalshelethebrew'] = 0x0593;
				t['shbopomofo'] = 0x3115;
				t['shchacyrillic'] = 0x0449;
				t['sheenarabic'] = 0x0634;
				t['sheenfinalarabic'] = 0xFEB6;
				t['sheeninitialarabic'] = 0xFEB7;
				t['sheenmedialarabic'] = 0xFEB8;
				t['sheicoptic'] = 0x03E3;
				t['sheqel'] = 0x20AA;
				t['sheqelhebrew'] = 0x20AA;
				t['sheva'] = 0x05B0;
				t['sheva115'] = 0x05B0;
				t['sheva15'] = 0x05B0;
				t['sheva22'] = 0x05B0;
				t['sheva2e'] = 0x05B0;
				t['shevahebrew'] = 0x05B0;
				t['shevanarrowhebrew'] = 0x05B0;
				t['shevaquarterhebrew'] = 0x05B0;
				t['shevawidehebrew'] = 0x05B0;
				t['shhacyrillic'] = 0x04BB;
				t['shimacoptic'] = 0x03ED;
				t['shin'] = 0x05E9;
				t['shindagesh'] = 0xFB49;
				t['shindageshhebrew'] = 0xFB49;
				t['shindageshshindot'] = 0xFB2C;
				t['shindageshshindothebrew'] = 0xFB2C;
				t['shindageshsindot'] = 0xFB2D;
				t['shindageshsindothebrew'] = 0xFB2D;
				t['shindothebrew'] = 0x05C1;
				t['shinhebrew'] = 0x05E9;
				t['shinshindot'] = 0xFB2A;
				t['shinshindothebrew'] = 0xFB2A;
				t['shinsindot'] = 0xFB2B;
				t['shinsindothebrew'] = 0xFB2B;
				t['shook'] = 0x0282;
				t['sigma'] = 0x03C3;
				t['sigma1'] = 0x03C2;
				t['sigmafinal'] = 0x03C2;
				t['sigmalunatesymbolgreek'] = 0x03F2;
				t['sihiragana'] = 0x3057;
				t['sikatakana'] = 0x30B7;
				t['sikatakanahalfwidth'] = 0xFF7C;
				t['siluqhebrew'] = 0x05BD;
				t['siluqlefthebrew'] = 0x05BD;
				t['similar'] = 0x223C;
				t['sindothebrew'] = 0x05C2;
				t['siosacirclekorean'] = 0x3274;
				t['siosaparenkorean'] = 0x3214;
				t['sioscieuckorean'] = 0x317E;
				t['sioscirclekorean'] = 0x3266;
				t['sioskiyeokkorean'] = 0x317A;
				t['sioskorean'] = 0x3145;
				t['siosnieunkorean'] = 0x317B;
				t['siosparenkorean'] = 0x3206;
				t['siospieupkorean'] = 0x317D;
				t['siostikeutkorean'] = 0x317C;
				t['six'] = 0x0036;
				t['sixarabic'] = 0x0666;
				t['sixbengali'] = 0x09EC;
				t['sixcircle'] = 0x2465;
				t['sixcircleinversesansserif'] = 0x278F;
				t['sixdeva'] = 0x096C;
				t['sixgujarati'] = 0x0AEC;
				t['sixgurmukhi'] = 0x0A6C;
				t['sixhackarabic'] = 0x0666;
				t['sixhangzhou'] = 0x3026;
				t['sixideographicparen'] = 0x3225;
				t['sixinferior'] = 0x2086;
				t['sixmonospace'] = 0xFF16;
				t['sixoldstyle'] = 0xF736;
				t['sixparen'] = 0x2479;
				t['sixperiod'] = 0x248D;
				t['sixpersian'] = 0x06F6;
				t['sixroman'] = 0x2175;
				t['sixsuperior'] = 0x2076;
				t['sixteencircle'] = 0x246F;
				t['sixteencurrencydenominatorbengali'] = 0x09F9;
				t['sixteenparen'] = 0x2483;
				t['sixteenperiod'] = 0x2497;
				t['sixthai'] = 0x0E56;
				t['slash'] = 0x002F;
				t['slashmonospace'] = 0xFF0F;
				t['slong'] = 0x017F;
				t['slongdotaccent'] = 0x1E9B;
				t['smileface'] = 0x263A;
				t['smonospace'] = 0xFF53;
				t['sofpasuqhebrew'] = 0x05C3;
				t['softhyphen'] = 0x00AD;
				t['softsigncyrillic'] = 0x044C;
				t['sohiragana'] = 0x305D;
				t['sokatakana'] = 0x30BD;
				t['sokatakanahalfwidth'] = 0xFF7F;
				t['soliduslongoverlaycmb'] = 0x0338;
				t['solidusshortoverlaycmb'] = 0x0337;
				t['sorusithai'] = 0x0E29;
				t['sosalathai'] = 0x0E28;
				t['sosothai'] = 0x0E0B;
				t['sosuathai'] = 0x0E2A;
				t['space'] = 0x0020;
				t['spacehackarabic'] = 0x0020;
				t['spade'] = 0x2660;
				t['spadesuitblack'] = 0x2660;
				t['spadesuitwhite'] = 0x2664;
				t['sparen'] = 0x24AE;
				t['squarebelowcmb'] = 0x033B;
				t['squarecc'] = 0x33C4;
				t['squarecm'] = 0x339D;
				t['squarediagonalcrosshatchfill'] = 0x25A9;
				t['squarehorizontalfill'] = 0x25A4;
				t['squarekg'] = 0x338F;
				t['squarekm'] = 0x339E;
				t['squarekmcapital'] = 0x33CE;
				t['squareln'] = 0x33D1;
				t['squarelog'] = 0x33D2;
				t['squaremg'] = 0x338E;
				t['squaremil'] = 0x33D5;
				t['squaremm'] = 0x339C;
				t['squaremsquared'] = 0x33A1;
				t['squareorthogonalcrosshatchfill'] = 0x25A6;
				t['squareupperlefttolowerrightfill'] = 0x25A7;
				t['squareupperrighttolowerleftfill'] = 0x25A8;
				t['squareverticalfill'] = 0x25A5;
				t['squarewhitewithsmallblack'] = 0x25A3;
				t['srsquare'] = 0x33DB;
				t['ssabengali'] = 0x09B7;
				t['ssadeva'] = 0x0937;
				t['ssagujarati'] = 0x0AB7;
				t['ssangcieuckorean'] = 0x3149;
				t['ssanghieuhkorean'] = 0x3185;
				t['ssangieungkorean'] = 0x3180;
				t['ssangkiyeokkorean'] = 0x3132;
				t['ssangnieunkorean'] = 0x3165;
				t['ssangpieupkorean'] = 0x3143;
				t['ssangsioskorean'] = 0x3146;
				t['ssangtikeutkorean'] = 0x3138;
				t['ssuperior'] = 0xF6F2;
				t['sterling'] = 0x00A3;
				t['sterlingmonospace'] = 0xFFE1;
				t['strokelongoverlaycmb'] = 0x0336;
				t['strokeshortoverlaycmb'] = 0x0335;
				t['subset'] = 0x2282;
				t['subsetnotequal'] = 0x228A;
				t['subsetorequal'] = 0x2286;
				t['succeeds'] = 0x227B;
				t['suchthat'] = 0x220B;
				t['suhiragana'] = 0x3059;
				t['sukatakana'] = 0x30B9;
				t['sukatakanahalfwidth'] = 0xFF7D;
				t['sukunarabic'] = 0x0652;
				t['summation'] = 0x2211;
				t['sun'] = 0x263C;
				t['superset'] = 0x2283;
				t['supersetnotequal'] = 0x228B;
				t['supersetorequal'] = 0x2287;
				t['svsquare'] = 0x33DC;
				t['syouwaerasquare'] = 0x337C;
				t['t'] = 0x0074;
				t['tabengali'] = 0x09A4;
				t['tackdown'] = 0x22A4;
				t['tackleft'] = 0x22A3;
				t['tadeva'] = 0x0924;
				t['tagujarati'] = 0x0AA4;
				t['tagurmukhi'] = 0x0A24;
				t['taharabic'] = 0x0637;
				t['tahfinalarabic'] = 0xFEC2;
				t['tahinitialarabic'] = 0xFEC3;
				t['tahiragana'] = 0x305F;
				t['tahmedialarabic'] = 0xFEC4;
				t['taisyouerasquare'] = 0x337D;
				t['takatakana'] = 0x30BF;
				t['takatakanahalfwidth'] = 0xFF80;
				t['tatweelarabic'] = 0x0640;
				t['tau'] = 0x03C4;
				t['tav'] = 0x05EA;
				t['tavdages'] = 0xFB4A;
				t['tavdagesh'] = 0xFB4A;
				t['tavdageshhebrew'] = 0xFB4A;
				t['tavhebrew'] = 0x05EA;
				t['tbar'] = 0x0167;
				t['tbopomofo'] = 0x310A;
				t['tcaron'] = 0x0165;
				t['tccurl'] = 0x02A8;
				t['tcedilla'] = 0x0163;
				t['tcheharabic'] = 0x0686;
				t['tchehfinalarabic'] = 0xFB7B;
				t['tchehinitialarabic'] = 0xFB7C;
				t['tchehmedialarabic'] = 0xFB7D;
				t['tcircle'] = 0x24E3;
				t['tcircumflexbelow'] = 0x1E71;
				t['tcommaaccent'] = 0x0163;
				t['tdieresis'] = 0x1E97;
				t['tdotaccent'] = 0x1E6B;
				t['tdotbelow'] = 0x1E6D;
				t['tecyrillic'] = 0x0442;
				t['tedescendercyrillic'] = 0x04AD;
				t['teharabic'] = 0x062A;
				t['tehfinalarabic'] = 0xFE96;
				t['tehhahinitialarabic'] = 0xFCA2;
				t['tehhahisolatedarabic'] = 0xFC0C;
				t['tehinitialarabic'] = 0xFE97;
				t['tehiragana'] = 0x3066;
				t['tehjeeminitialarabic'] = 0xFCA1;
				t['tehjeemisolatedarabic'] = 0xFC0B;
				t['tehmarbutaarabic'] = 0x0629;
				t['tehmarbutafinalarabic'] = 0xFE94;
				t['tehmedialarabic'] = 0xFE98;
				t['tehmeeminitialarabic'] = 0xFCA4;
				t['tehmeemisolatedarabic'] = 0xFC0E;
				t['tehnoonfinalarabic'] = 0xFC73;
				t['tekatakana'] = 0x30C6;
				t['tekatakanahalfwidth'] = 0xFF83;
				t['telephone'] = 0x2121;
				t['telephoneblack'] = 0x260E;
				t['telishagedolahebrew'] = 0x05A0;
				t['telishaqetanahebrew'] = 0x05A9;
				t['tencircle'] = 0x2469;
				t['tenideographicparen'] = 0x3229;
				t['tenparen'] = 0x247D;
				t['tenperiod'] = 0x2491;
				t['tenroman'] = 0x2179;
				t['tesh'] = 0x02A7;
				t['tet'] = 0x05D8;
				t['tetdagesh'] = 0xFB38;
				t['tetdageshhebrew'] = 0xFB38;
				t['tethebrew'] = 0x05D8;
				t['tetsecyrillic'] = 0x04B5;
				t['tevirhebrew'] = 0x059B;
				t['tevirlefthebrew'] = 0x059B;
				t['thabengali'] = 0x09A5;
				t['thadeva'] = 0x0925;
				t['thagujarati'] = 0x0AA5;
				t['thagurmukhi'] = 0x0A25;
				t['thalarabic'] = 0x0630;
				t['thalfinalarabic'] = 0xFEAC;
				t['thanthakhatlowleftthai'] = 0xF898;
				t['thanthakhatlowrightthai'] = 0xF897;
				t['thanthakhatthai'] = 0x0E4C;
				t['thanthakhatupperleftthai'] = 0xF896;
				t['theharabic'] = 0x062B;
				t['thehfinalarabic'] = 0xFE9A;
				t['thehinitialarabic'] = 0xFE9B;
				t['thehmedialarabic'] = 0xFE9C;
				t['thereexists'] = 0x2203;
				t['therefore'] = 0x2234;
				t['theta'] = 0x03B8;
				t['theta1'] = 0x03D1;
				t['thetasymbolgreek'] = 0x03D1;
				t['thieuthacirclekorean'] = 0x3279;
				t['thieuthaparenkorean'] = 0x3219;
				t['thieuthcirclekorean'] = 0x326B;
				t['thieuthkorean'] = 0x314C;
				t['thieuthparenkorean'] = 0x320B;
				t['thirteencircle'] = 0x246C;
				t['thirteenparen'] = 0x2480;
				t['thirteenperiod'] = 0x2494;
				t['thonangmonthothai'] = 0x0E11;
				t['thook'] = 0x01AD;
				t['thophuthaothai'] = 0x0E12;
				t['thorn'] = 0x00FE;
				t['thothahanthai'] = 0x0E17;
				t['thothanthai'] = 0x0E10;
				t['thothongthai'] = 0x0E18;
				t['thothungthai'] = 0x0E16;
				t['thousandcyrillic'] = 0x0482;
				t['thousandsseparatorarabic'] = 0x066C;
				t['thousandsseparatorpersian'] = 0x066C;
				t['three'] = 0x0033;
				t['threearabic'] = 0x0663;
				t['threebengali'] = 0x09E9;
				t['threecircle'] = 0x2462;
				t['threecircleinversesansserif'] = 0x278C;
				t['threedeva'] = 0x0969;
				t['threeeighths'] = 0x215C;
				t['threegujarati'] = 0x0AE9;
				t['threegurmukhi'] = 0x0A69;
				t['threehackarabic'] = 0x0663;
				t['threehangzhou'] = 0x3023;
				t['threeideographicparen'] = 0x3222;
				t['threeinferior'] = 0x2083;
				t['threemonospace'] = 0xFF13;
				t['threenumeratorbengali'] = 0x09F6;
				t['threeoldstyle'] = 0xF733;
				t['threeparen'] = 0x2476;
				t['threeperiod'] = 0x248A;
				t['threepersian'] = 0x06F3;
				t['threequarters'] = 0x00BE;
				t['threequartersemdash'] = 0xF6DE;
				t['threeroman'] = 0x2172;
				t['threesuperior'] = 0x00B3;
				t['threethai'] = 0x0E53;
				t['thzsquare'] = 0x3394;
				t['tihiragana'] = 0x3061;
				t['tikatakana'] = 0x30C1;
				t['tikatakanahalfwidth'] = 0xFF81;
				t['tikeutacirclekorean'] = 0x3270;
				t['tikeutaparenkorean'] = 0x3210;
				t['tikeutcirclekorean'] = 0x3262;
				t['tikeutkorean'] = 0x3137;
				t['tikeutparenkorean'] = 0x3202;
				t['tilde'] = 0x02DC;
				t['tildebelowcmb'] = 0x0330;
				t['tildecmb'] = 0x0303;
				t['tildecomb'] = 0x0303;
				t['tildedoublecmb'] = 0x0360;
				t['tildeoperator'] = 0x223C;
				t['tildeoverlaycmb'] = 0x0334;
				t['tildeverticalcmb'] = 0x033E;
				t['timescircle'] = 0x2297;
				t['tipehahebrew'] = 0x0596;
				t['tipehalefthebrew'] = 0x0596;
				t['tippigurmukhi'] = 0x0A70;
				t['titlocyrilliccmb'] = 0x0483;
				t['tiwnarmenian'] = 0x057F;
				t['tlinebelow'] = 0x1E6F;
				t['tmonospace'] = 0xFF54;
				t['toarmenian'] = 0x0569;
				t['tohiragana'] = 0x3068;
				t['tokatakana'] = 0x30C8;
				t['tokatakanahalfwidth'] = 0xFF84;
				t['tonebarextrahighmod'] = 0x02E5;
				t['tonebarextralowmod'] = 0x02E9;
				t['tonebarhighmod'] = 0x02E6;
				t['tonebarlowmod'] = 0x02E8;
				t['tonebarmidmod'] = 0x02E7;
				t['tonefive'] = 0x01BD;
				t['tonesix'] = 0x0185;
				t['tonetwo'] = 0x01A8;
				t['tonos'] = 0x0384;
				t['tonsquare'] = 0x3327;
				t['topatakthai'] = 0x0E0F;
				t['tortoiseshellbracketleft'] = 0x3014;
				t['tortoiseshellbracketleftsmall'] = 0xFE5D;
				t['tortoiseshellbracketleftvertical'] = 0xFE39;
				t['tortoiseshellbracketright'] = 0x3015;
				t['tortoiseshellbracketrightsmall'] = 0xFE5E;
				t['tortoiseshellbracketrightvertical'] = 0xFE3A;
				t['totaothai'] = 0x0E15;
				t['tpalatalhook'] = 0x01AB;
				t['tparen'] = 0x24AF;
				t['trademark'] = 0x2122;
				t['trademarksans'] = 0xF8EA;
				t['trademarkserif'] = 0xF6DB;
				t['tretroflexhook'] = 0x0288;
				t['triagdn'] = 0x25BC;
				t['triaglf'] = 0x25C4;
				t['triagrt'] = 0x25BA;
				t['triagup'] = 0x25B2;
				t['ts'] = 0x02A6;
				t['tsadi'] = 0x05E6;
				t['tsadidagesh'] = 0xFB46;
				t['tsadidageshhebrew'] = 0xFB46;
				t['tsadihebrew'] = 0x05E6;
				t['tsecyrillic'] = 0x0446;
				t['tsere'] = 0x05B5;
				t['tsere12'] = 0x05B5;
				t['tsere1e'] = 0x05B5;
				t['tsere2b'] = 0x05B5;
				t['tserehebrew'] = 0x05B5;
				t['tserenarrowhebrew'] = 0x05B5;
				t['tserequarterhebrew'] = 0x05B5;
				t['tserewidehebrew'] = 0x05B5;
				t['tshecyrillic'] = 0x045B;
				t['tsuperior'] = 0xF6F3;
				t['ttabengali'] = 0x099F;
				t['ttadeva'] = 0x091F;
				t['ttagujarati'] = 0x0A9F;
				t['ttagurmukhi'] = 0x0A1F;
				t['tteharabic'] = 0x0679;
				t['ttehfinalarabic'] = 0xFB67;
				t['ttehinitialarabic'] = 0xFB68;
				t['ttehmedialarabic'] = 0xFB69;
				t['tthabengali'] = 0x09A0;
				t['tthadeva'] = 0x0920;
				t['tthagujarati'] = 0x0AA0;
				t['tthagurmukhi'] = 0x0A20;
				t['tturned'] = 0x0287;
				t['tuhiragana'] = 0x3064;
				t['tukatakana'] = 0x30C4;
				t['tukatakanahalfwidth'] = 0xFF82;
				t['tusmallhiragana'] = 0x3063;
				t['tusmallkatakana'] = 0x30C3;
				t['tusmallkatakanahalfwidth'] = 0xFF6F;
				t['twelvecircle'] = 0x246B;
				t['twelveparen'] = 0x247F;
				t['twelveperiod'] = 0x2493;
				t['twelveroman'] = 0x217B;
				t['twentycircle'] = 0x2473;
				t['twentyhangzhou'] = 0x5344;
				t['twentyparen'] = 0x2487;
				t['twentyperiod'] = 0x249B;
				t['two'] = 0x0032;
				t['twoarabic'] = 0x0662;
				t['twobengali'] = 0x09E8;
				t['twocircle'] = 0x2461;
				t['twocircleinversesansserif'] = 0x278B;
				t['twodeva'] = 0x0968;
				t['twodotenleader'] = 0x2025;
				t['twodotleader'] = 0x2025;
				t['twodotleadervertical'] = 0xFE30;
				t['twogujarati'] = 0x0AE8;
				t['twogurmukhi'] = 0x0A68;
				t['twohackarabic'] = 0x0662;
				t['twohangzhou'] = 0x3022;
				t['twoideographicparen'] = 0x3221;
				t['twoinferior'] = 0x2082;
				t['twomonospace'] = 0xFF12;
				t['twonumeratorbengali'] = 0x09F5;
				t['twooldstyle'] = 0xF732;
				t['twoparen'] = 0x2475;
				t['twoperiod'] = 0x2489;
				t['twopersian'] = 0x06F2;
				t['tworoman'] = 0x2171;
				t['twostroke'] = 0x01BB;
				t['twosuperior'] = 0x00B2;
				t['twothai'] = 0x0E52;
				t['twothirds'] = 0x2154;
				t['u'] = 0x0075;
				t['uacute'] = 0x00FA;
				t['ubar'] = 0x0289;
				t['ubengali'] = 0x0989;
				t['ubopomofo'] = 0x3128;
				t['ubreve'] = 0x016D;
				t['ucaron'] = 0x01D4;
				t['ucircle'] = 0x24E4;
				t['ucircumflex'] = 0x00FB;
				t['ucircumflexbelow'] = 0x1E77;
				t['ucyrillic'] = 0x0443;
				t['udattadeva'] = 0x0951;
				t['udblacute'] = 0x0171;
				t['udblgrave'] = 0x0215;
				t['udeva'] = 0x0909;
				t['udieresis'] = 0x00FC;
				t['udieresisacute'] = 0x01D8;
				t['udieresisbelow'] = 0x1E73;
				t['udieresiscaron'] = 0x01DA;
				t['udieresiscyrillic'] = 0x04F1;
				t['udieresisgrave'] = 0x01DC;
				t['udieresismacron'] = 0x01D6;
				t['udotbelow'] = 0x1EE5;
				t['ugrave'] = 0x00F9;
				t['ugujarati'] = 0x0A89;
				t['ugurmukhi'] = 0x0A09;
				t['uhiragana'] = 0x3046;
				t['uhookabove'] = 0x1EE7;
				t['uhorn'] = 0x01B0;
				t['uhornacute'] = 0x1EE9;
				t['uhorndotbelow'] = 0x1EF1;
				t['uhorngrave'] = 0x1EEB;
				t['uhornhookabove'] = 0x1EED;
				t['uhorntilde'] = 0x1EEF;
				t['uhungarumlaut'] = 0x0171;
				t['uhungarumlautcyrillic'] = 0x04F3;
				t['uinvertedbreve'] = 0x0217;
				t['ukatakana'] = 0x30A6;
				t['ukatakanahalfwidth'] = 0xFF73;
				t['ukcyrillic'] = 0x0479;
				t['ukorean'] = 0x315C;
				t['umacron'] = 0x016B;
				t['umacroncyrillic'] = 0x04EF;
				t['umacrondieresis'] = 0x1E7B;
				t['umatragurmukhi'] = 0x0A41;
				t['umonospace'] = 0xFF55;
				t['underscore'] = 0x005F;
				t['underscoredbl'] = 0x2017;
				t['underscoremonospace'] = 0xFF3F;
				t['underscorevertical'] = 0xFE33;
				t['underscorewavy'] = 0xFE4F;
				t['union'] = 0x222A;
				t['universal'] = 0x2200;
				t['uogonek'] = 0x0173;
				t['uparen'] = 0x24B0;
				t['upblock'] = 0x2580;
				t['upperdothebrew'] = 0x05C4;
				t['upsilon'] = 0x03C5;
				t['upsilondieresis'] = 0x03CB;
				t['upsilondieresistonos'] = 0x03B0;
				t['upsilonlatin'] = 0x028A;
				t['upsilontonos'] = 0x03CD;
				t['uptackbelowcmb'] = 0x031D;
				t['uptackmod'] = 0x02D4;
				t['uragurmukhi'] = 0x0A73;
				t['uring'] = 0x016F;
				t['ushortcyrillic'] = 0x045E;
				t['usmallhiragana'] = 0x3045;
				t['usmallkatakana'] = 0x30A5;
				t['usmallkatakanahalfwidth'] = 0xFF69;
				t['ustraightcyrillic'] = 0x04AF;
				t['ustraightstrokecyrillic'] = 0x04B1;
				t['utilde'] = 0x0169;
				t['utildeacute'] = 0x1E79;
				t['utildebelow'] = 0x1E75;
				t['uubengali'] = 0x098A;
				t['uudeva'] = 0x090A;
				t['uugujarati'] = 0x0A8A;
				t['uugurmukhi'] = 0x0A0A;
				t['uumatragurmukhi'] = 0x0A42;
				t['uuvowelsignbengali'] = 0x09C2;
				t['uuvowelsigndeva'] = 0x0942;
				t['uuvowelsigngujarati'] = 0x0AC2;
				t['uvowelsignbengali'] = 0x09C1;
				t['uvowelsigndeva'] = 0x0941;
				t['uvowelsigngujarati'] = 0x0AC1;
				t['v'] = 0x0076;
				t['vadeva'] = 0x0935;
				t['vagujarati'] = 0x0AB5;
				t['vagurmukhi'] = 0x0A35;
				t['vakatakana'] = 0x30F7;
				t['vav'] = 0x05D5;
				t['vavdagesh'] = 0xFB35;
				t['vavdagesh65'] = 0xFB35;
				t['vavdageshhebrew'] = 0xFB35;
				t['vavhebrew'] = 0x05D5;
				t['vavholam'] = 0xFB4B;
				t['vavholamhebrew'] = 0xFB4B;
				t['vavvavhebrew'] = 0x05F0;
				t['vavyodhebrew'] = 0x05F1;
				t['vcircle'] = 0x24E5;
				t['vdotbelow'] = 0x1E7F;
				t['vecyrillic'] = 0x0432;
				t['veharabic'] = 0x06A4;
				t['vehfinalarabic'] = 0xFB6B;
				t['vehinitialarabic'] = 0xFB6C;
				t['vehmedialarabic'] = 0xFB6D;
				t['vekatakana'] = 0x30F9;
				t['venus'] = 0x2640;
				t['verticalbar'] = 0x007C;
				t['verticallineabovecmb'] = 0x030D;
				t['verticallinebelowcmb'] = 0x0329;
				t['verticallinelowmod'] = 0x02CC;
				t['verticallinemod'] = 0x02C8;
				t['vewarmenian'] = 0x057E;
				t['vhook'] = 0x028B;
				t['vikatakana'] = 0x30F8;
				t['viramabengali'] = 0x09CD;
				t['viramadeva'] = 0x094D;
				t['viramagujarati'] = 0x0ACD;
				t['visargabengali'] = 0x0983;
				t['visargadeva'] = 0x0903;
				t['visargagujarati'] = 0x0A83;
				t['vmonospace'] = 0xFF56;
				t['voarmenian'] = 0x0578;
				t['voicediterationhiragana'] = 0x309E;
				t['voicediterationkatakana'] = 0x30FE;
				t['voicedmarkkana'] = 0x309B;
				t['voicedmarkkanahalfwidth'] = 0xFF9E;
				t['vokatakana'] = 0x30FA;
				t['vparen'] = 0x24B1;
				t['vtilde'] = 0x1E7D;
				t['vturned'] = 0x028C;
				t['vuhiragana'] = 0x3094;
				t['vukatakana'] = 0x30F4;
				t['w'] = 0x0077;
				t['wacute'] = 0x1E83;
				t['waekorean'] = 0x3159;
				t['wahiragana'] = 0x308F;
				t['wakatakana'] = 0x30EF;
				t['wakatakanahalfwidth'] = 0xFF9C;
				t['wakorean'] = 0x3158;
				t['wasmallhiragana'] = 0x308E;
				t['wasmallkatakana'] = 0x30EE;
				t['wattosquare'] = 0x3357;
				t['wavedash'] = 0x301C;
				t['wavyunderscorevertical'] = 0xFE34;
				t['wawarabic'] = 0x0648;
				t['wawfinalarabic'] = 0xFEEE;
				t['wawhamzaabovearabic'] = 0x0624;
				t['wawhamzaabovefinalarabic'] = 0xFE86;
				t['wbsquare'] = 0x33DD;
				t['wcircle'] = 0x24E6;
				t['wcircumflex'] = 0x0175;
				t['wdieresis'] = 0x1E85;
				t['wdotaccent'] = 0x1E87;
				t['wdotbelow'] = 0x1E89;
				t['wehiragana'] = 0x3091;
				t['weierstrass'] = 0x2118;
				t['wekatakana'] = 0x30F1;
				t['wekorean'] = 0x315E;
				t['weokorean'] = 0x315D;
				t['wgrave'] = 0x1E81;
				t['whitebullet'] = 0x25E6;
				t['whitecircle'] = 0x25CB;
				t['whitecircleinverse'] = 0x25D9;
				t['whitecornerbracketleft'] = 0x300E;
				t['whitecornerbracketleftvertical'] = 0xFE43;
				t['whitecornerbracketright'] = 0x300F;
				t['whitecornerbracketrightvertical'] = 0xFE44;
				t['whitediamond'] = 0x25C7;
				t['whitediamondcontainingblacksmalldiamond'] = 0x25C8;
				t['whitedownpointingsmalltriangle'] = 0x25BF;
				t['whitedownpointingtriangle'] = 0x25BD;
				t['whiteleftpointingsmalltriangle'] = 0x25C3;
				t['whiteleftpointingtriangle'] = 0x25C1;
				t['whitelenticularbracketleft'] = 0x3016;
				t['whitelenticularbracketright'] = 0x3017;
				t['whiterightpointingsmalltriangle'] = 0x25B9;
				t['whiterightpointingtriangle'] = 0x25B7;
				t['whitesmallsquare'] = 0x25AB;
				t['whitesmilingface'] = 0x263A;
				t['whitesquare'] = 0x25A1;
				t['whitestar'] = 0x2606;
				t['whitetelephone'] = 0x260F;
				t['whitetortoiseshellbracketleft'] = 0x3018;
				t['whitetortoiseshellbracketright'] = 0x3019;
				t['whiteuppointingsmalltriangle'] = 0x25B5;
				t['whiteuppointingtriangle'] = 0x25B3;
				t['wihiragana'] = 0x3090;
				t['wikatakana'] = 0x30F0;
				t['wikorean'] = 0x315F;
				t['wmonospace'] = 0xFF57;
				t['wohiragana'] = 0x3092;
				t['wokatakana'] = 0x30F2;
				t['wokatakanahalfwidth'] = 0xFF66;
				t['won'] = 0x20A9;
				t['wonmonospace'] = 0xFFE6;
				t['wowaenthai'] = 0x0E27;
				t['wparen'] = 0x24B2;
				t['wring'] = 0x1E98;
				t['wsuperior'] = 0x02B7;
				t['wturned'] = 0x028D;
				t['wynn'] = 0x01BF;
				t['x'] = 0x0078;
				t['xabovecmb'] = 0x033D;
				t['xbopomofo'] = 0x3112;
				t['xcircle'] = 0x24E7;
				t['xdieresis'] = 0x1E8D;
				t['xdotaccent'] = 0x1E8B;
				t['xeharmenian'] = 0x056D;
				t['xi'] = 0x03BE;
				t['xmonospace'] = 0xFF58;
				t['xparen'] = 0x24B3;
				t['xsuperior'] = 0x02E3;
				t['y'] = 0x0079;
				t['yaadosquare'] = 0x334E;
				t['yabengali'] = 0x09AF;
				t['yacute'] = 0x00FD;
				t['yadeva'] = 0x092F;
				t['yaekorean'] = 0x3152;
				t['yagujarati'] = 0x0AAF;
				t['yagurmukhi'] = 0x0A2F;
				t['yahiragana'] = 0x3084;
				t['yakatakana'] = 0x30E4;
				t['yakatakanahalfwidth'] = 0xFF94;
				t['yakorean'] = 0x3151;
				t['yamakkanthai'] = 0x0E4E;
				t['yasmallhiragana'] = 0x3083;
				t['yasmallkatakana'] = 0x30E3;
				t['yasmallkatakanahalfwidth'] = 0xFF6C;
				t['yatcyrillic'] = 0x0463;
				t['ycircle'] = 0x24E8;
				t['ycircumflex'] = 0x0177;
				t['ydieresis'] = 0x00FF;
				t['ydotaccent'] = 0x1E8F;
				t['ydotbelow'] = 0x1EF5;
				t['yeharabic'] = 0x064A;
				t['yehbarreearabic'] = 0x06D2;
				t['yehbarreefinalarabic'] = 0xFBAF;
				t['yehfinalarabic'] = 0xFEF2;
				t['yehhamzaabovearabic'] = 0x0626;
				t['yehhamzaabovefinalarabic'] = 0xFE8A;
				t['yehhamzaaboveinitialarabic'] = 0xFE8B;
				t['yehhamzaabovemedialarabic'] = 0xFE8C;
				t['yehinitialarabic'] = 0xFEF3;
				t['yehmedialarabic'] = 0xFEF4;
				t['yehmeeminitialarabic'] = 0xFCDD;
				t['yehmeemisolatedarabic'] = 0xFC58;
				t['yehnoonfinalarabic'] = 0xFC94;
				t['yehthreedotsbelowarabic'] = 0x06D1;
				t['yekorean'] = 0x3156;
				t['yen'] = 0x00A5;
				t['yenmonospace'] = 0xFFE5;
				t['yeokorean'] = 0x3155;
				t['yeorinhieuhkorean'] = 0x3186;
				t['yerahbenyomohebrew'] = 0x05AA;
				t['yerahbenyomolefthebrew'] = 0x05AA;
				t['yericyrillic'] = 0x044B;
				t['yerudieresiscyrillic'] = 0x04F9;
				t['yesieungkorean'] = 0x3181;
				t['yesieungpansioskorean'] = 0x3183;
				t['yesieungsioskorean'] = 0x3182;
				t['yetivhebrew'] = 0x059A;
				t['ygrave'] = 0x1EF3;
				t['yhook'] = 0x01B4;
				t['yhookabove'] = 0x1EF7;
				t['yiarmenian'] = 0x0575;
				t['yicyrillic'] = 0x0457;
				t['yikorean'] = 0x3162;
				t['yinyang'] = 0x262F;
				t['yiwnarmenian'] = 0x0582;
				t['ymonospace'] = 0xFF59;
				t['yod'] = 0x05D9;
				t['yoddagesh'] = 0xFB39;
				t['yoddageshhebrew'] = 0xFB39;
				t['yodhebrew'] = 0x05D9;
				t['yodyodhebrew'] = 0x05F2;
				t['yodyodpatahhebrew'] = 0xFB1F;
				t['yohiragana'] = 0x3088;
				t['yoikorean'] = 0x3189;
				t['yokatakana'] = 0x30E8;
				t['yokatakanahalfwidth'] = 0xFF96;
				t['yokorean'] = 0x315B;
				t['yosmallhiragana'] = 0x3087;
				t['yosmallkatakana'] = 0x30E7;
				t['yosmallkatakanahalfwidth'] = 0xFF6E;
				t['yotgreek'] = 0x03F3;
				t['yoyaekorean'] = 0x3188;
				t['yoyakorean'] = 0x3187;
				t['yoyakthai'] = 0x0E22;
				t['yoyingthai'] = 0x0E0D;
				t['yparen'] = 0x24B4;
				t['ypogegrammeni'] = 0x037A;
				t['ypogegrammenigreekcmb'] = 0x0345;
				t['yr'] = 0x01A6;
				t['yring'] = 0x1E99;
				t['ysuperior'] = 0x02B8;
				t['ytilde'] = 0x1EF9;
				t['yturned'] = 0x028E;
				t['yuhiragana'] = 0x3086;
				t['yuikorean'] = 0x318C;
				t['yukatakana'] = 0x30E6;
				t['yukatakanahalfwidth'] = 0xFF95;
				t['yukorean'] = 0x3160;
				t['yusbigcyrillic'] = 0x046B;
				t['yusbigiotifiedcyrillic'] = 0x046D;
				t['yuslittlecyrillic'] = 0x0467;
				t['yuslittleiotifiedcyrillic'] = 0x0469;
				t['yusmallhiragana'] = 0x3085;
				t['yusmallkatakana'] = 0x30E5;
				t['yusmallkatakanahalfwidth'] = 0xFF6D;
				t['yuyekorean'] = 0x318B;
				t['yuyeokorean'] = 0x318A;
				t['yyabengali'] = 0x09DF;
				t['yyadeva'] = 0x095F;
				t['z'] = 0x007A;
				t['zaarmenian'] = 0x0566;
				t['zacute'] = 0x017A;
				t['zadeva'] = 0x095B;
				t['zagurmukhi'] = 0x0A5B;
				t['zaharabic'] = 0x0638;
				t['zahfinalarabic'] = 0xFEC6;
				t['zahinitialarabic'] = 0xFEC7;
				t['zahiragana'] = 0x3056;
				t['zahmedialarabic'] = 0xFEC8;
				t['zainarabic'] = 0x0632;
				t['zainfinalarabic'] = 0xFEB0;
				t['zakatakana'] = 0x30B6;
				t['zaqefgadolhebrew'] = 0x0595;
				t['zaqefqatanhebrew'] = 0x0594;
				t['zarqahebrew'] = 0x0598;
				t['zayin'] = 0x05D6;
				t['zayindagesh'] = 0xFB36;
				t['zayindageshhebrew'] = 0xFB36;
				t['zayinhebrew'] = 0x05D6;
				t['zbopomofo'] = 0x3117;
				t['zcaron'] = 0x017E;
				t['zcircle'] = 0x24E9;
				t['zcircumflex'] = 0x1E91;
				t['zcurl'] = 0x0291;
				t['zdot'] = 0x017C;
				t['zdotaccent'] = 0x017C;
				t['zdotbelow'] = 0x1E93;
				t['zecyrillic'] = 0x0437;
				t['zedescendercyrillic'] = 0x0499;
				t['zedieresiscyrillic'] = 0x04DF;
				t['zehiragana'] = 0x305C;
				t['zekatakana'] = 0x30BC;
				t['zero'] = 0x0030;
				t['zeroarabic'] = 0x0660;
				t['zerobengali'] = 0x09E6;
				t['zerodeva'] = 0x0966;
				t['zerogujarati'] = 0x0AE6;
				t['zerogurmukhi'] = 0x0A66;
				t['zerohackarabic'] = 0x0660;
				t['zeroinferior'] = 0x2080;
				t['zeromonospace'] = 0xFF10;
				t['zerooldstyle'] = 0xF730;
				t['zeropersian'] = 0x06F0;
				t['zerosuperior'] = 0x2070;
				t['zerothai'] = 0x0E50;
				t['zerowidthjoiner'] = 0xFEFF;
				t['zerowidthnonjoiner'] = 0x200C;
				t['zerowidthspace'] = 0x200B;
				t['zeta'] = 0x03B6;
				t['zhbopomofo'] = 0x3113;
				t['zhearmenian'] = 0x056A;
				t['zhebrevecyrillic'] = 0x04C2;
				t['zhecyrillic'] = 0x0436;
				t['zhedescendercyrillic'] = 0x0497;
				t['zhedieresiscyrillic'] = 0x04DD;
				t['zihiragana'] = 0x3058;
				t['zikatakana'] = 0x30B8;
				t['zinorhebrew'] = 0x05AE;
				t['zlinebelow'] = 0x1E95;
				t['zmonospace'] = 0xFF5A;
				t['zohiragana'] = 0x305E;
				t['zokatakana'] = 0x30BE;
				t['zparen'] = 0x24B5;
				t['zretroflexhook'] = 0x0290;
				t['zstroke'] = 0x01B6;
				t['zuhiragana'] = 0x305A;
				t['zukatakana'] = 0x30BA;
				t['.notdef'] = 0x0000;
				t['angbracketleftbig'] = 0x2329;
				t['angbracketleftBig'] = 0x2329;
				t['angbracketleftbigg'] = 0x2329;
				t['angbracketleftBigg'] = 0x2329;
				t['angbracketrightBig'] = 0x232A;
				t['angbracketrightbig'] = 0x232A;
				t['angbracketrightBigg'] = 0x232A;
				t['angbracketrightbigg'] = 0x232A;
				t['arrowhookleft'] = 0x21AA;
				t['arrowhookright'] = 0x21A9;
				t['arrowlefttophalf'] = 0x21BC;
				t['arrowleftbothalf'] = 0x21BD;
				t['arrownortheast'] = 0x2197;
				t['arrownorthwest'] = 0x2196;
				t['arrowrighttophalf'] = 0x21C0;
				t['arrowrightbothalf'] = 0x21C1;
				t['arrowsoutheast'] = 0x2198;
				t['arrowsouthwest'] = 0x2199;
				t['backslashbig'] = 0x2216;
				t['backslashBig'] = 0x2216;
				t['backslashBigg'] = 0x2216;
				t['backslashbigg'] = 0x2216;
				t['bardbl'] = 0x2016;
				t['bracehtipdownleft'] = 0xFE37;
				t['bracehtipdownright'] = 0xFE37;
				t['bracehtipupleft'] = 0xFE38;
				t['bracehtipupright'] = 0xFE38;
				t['braceleftBig'] = 0x007B;
				t['braceleftbig'] = 0x007B;
				t['braceleftbigg'] = 0x007B;
				t['braceleftBigg'] = 0x007B;
				t['bracerightBig'] = 0x007D;
				t['bracerightbig'] = 0x007D;
				t['bracerightbigg'] = 0x007D;
				t['bracerightBigg'] = 0x007D;
				t['bracketleftbig'] = 0x005B;
				t['bracketleftBig'] = 0x005B;
				t['bracketleftbigg'] = 0x005B;
				t['bracketleftBigg'] = 0x005B;
				t['bracketrightBig'] = 0x005D;
				t['bracketrightbig'] = 0x005D;
				t['bracketrightbigg'] = 0x005D;
				t['bracketrightBigg'] = 0x005D;
				t['ceilingleftbig'] = 0x2308;
				t['ceilingleftBig'] = 0x2308;
				t['ceilingleftBigg'] = 0x2308;
				t['ceilingleftbigg'] = 0x2308;
				t['ceilingrightbig'] = 0x2309;
				t['ceilingrightBig'] = 0x2309;
				t['ceilingrightbigg'] = 0x2309;
				t['ceilingrightBigg'] = 0x2309;
				t['circledotdisplay'] = 0x2299;
				t['circledottext'] = 0x2299;
				t['circlemultiplydisplay'] = 0x2297;
				t['circlemultiplytext'] = 0x2297;
				t['circleplusdisplay'] = 0x2295;
				t['circleplustext'] = 0x2295;
				t['contintegraldisplay'] = 0x222E;
				t['contintegraltext'] = 0x222E;
				t['coproductdisplay'] = 0x2210;
				t['coproducttext'] = 0x2210;
				t['floorleftBig'] = 0x230A;
				t['floorleftbig'] = 0x230A;
				t['floorleftbigg'] = 0x230A;
				t['floorleftBigg'] = 0x230A;
				t['floorrightbig'] = 0x230B;
				t['floorrightBig'] = 0x230B;
				t['floorrightBigg'] = 0x230B;
				t['floorrightbigg'] = 0x230B;
				t['hatwide'] = 0x0302;
				t['hatwider'] = 0x0302;
				t['hatwidest'] = 0x0302;
				t['intercal'] = 0x1D40;
				t['integraldisplay'] = 0x222B;
				t['integraltext'] = 0x222B;
				t['intersectiondisplay'] = 0x22C2;
				t['intersectiontext'] = 0x22C2;
				t['logicalanddisplay'] = 0x2227;
				t['logicalandtext'] = 0x2227;
				t['logicalordisplay'] = 0x2228;
				t['logicalortext'] = 0x2228;
				t['parenleftBig'] = 0x0028;
				t['parenleftbig'] = 0x0028;
				t['parenleftBigg'] = 0x0028;
				t['parenleftbigg'] = 0x0028;
				t['parenrightBig'] = 0x0029;
				t['parenrightbig'] = 0x0029;
				t['parenrightBigg'] = 0x0029;
				t['parenrightbigg'] = 0x0029;
				t['prime'] = 0x2032;
				t['productdisplay'] = 0x220F;
				t['producttext'] = 0x220F;
				t['radicalbig'] = 0x221A;
				t['radicalBig'] = 0x221A;
				t['radicalBigg'] = 0x221A;
				t['radicalbigg'] = 0x221A;
				t['radicalbt'] = 0x221A;
				t['radicaltp'] = 0x221A;
				t['radicalvertex'] = 0x221A;
				t['slashbig'] = 0x002F;
				t['slashBig'] = 0x002F;
				t['slashBigg'] = 0x002F;
				t['slashbigg'] = 0x002F;
				t['summationdisplay'] = 0x2211;
				t['summationtext'] = 0x2211;
				t['tildewide'] = 0x02DC;
				t['tildewider'] = 0x02DC;
				t['tildewidest'] = 0x02DC;
				t['uniondisplay'] = 0x22C3;
				t['unionmultidisplay'] = 0x228E;
				t['unionmultitext'] = 0x228E;
				t['unionsqdisplay'] = 0x2294;
				t['unionsqtext'] = 0x2294;
				t['uniontext'] = 0x22C3;
				t['vextenddouble'] = 0x2225;
				t['vextendsingle'] = 0x2223;
			});
			var getDingbatsGlyphsUnicode = getLookupTableFactory(function (t) {
				t['space'] = 0x0020;
				t['a1'] = 0x2701;
				t['a2'] = 0x2702;
				t['a202'] = 0x2703;
				t['a3'] = 0x2704;
				t['a4'] = 0x260E;
				t['a5'] = 0x2706;
				t['a119'] = 0x2707;
				t['a118'] = 0x2708;
				t['a117'] = 0x2709;
				t['a11'] = 0x261B;
				t['a12'] = 0x261E;
				t['a13'] = 0x270C;
				t['a14'] = 0x270D;
				t['a15'] = 0x270E;
				t['a16'] = 0x270F;
				t['a105'] = 0x2710;
				t['a17'] = 0x2711;
				t['a18'] = 0x2712;
				t['a19'] = 0x2713;
				t['a20'] = 0x2714;
				t['a21'] = 0x2715;
				t['a22'] = 0x2716;
				t['a23'] = 0x2717;
				t['a24'] = 0x2718;
				t['a25'] = 0x2719;
				t['a26'] = 0x271A;
				t['a27'] = 0x271B;
				t['a28'] = 0x271C;
				t['a6'] = 0x271D;
				t['a7'] = 0x271E;
				t['a8'] = 0x271F;
				t['a9'] = 0x2720;
				t['a10'] = 0x2721;
				t['a29'] = 0x2722;
				t['a30'] = 0x2723;
				t['a31'] = 0x2724;
				t['a32'] = 0x2725;
				t['a33'] = 0x2726;
				t['a34'] = 0x2727;
				t['a35'] = 0x2605;
				t['a36'] = 0x2729;
				t['a37'] = 0x272A;
				t['a38'] = 0x272B;
				t['a39'] = 0x272C;
				t['a40'] = 0x272D;
				t['a41'] = 0x272E;
				t['a42'] = 0x272F;
				t['a43'] = 0x2730;
				t['a44'] = 0x2731;
				t['a45'] = 0x2732;
				t['a46'] = 0x2733;
				t['a47'] = 0x2734;
				t['a48'] = 0x2735;
				t['a49'] = 0x2736;
				t['a50'] = 0x2737;
				t['a51'] = 0x2738;
				t['a52'] = 0x2739;
				t['a53'] = 0x273A;
				t['a54'] = 0x273B;
				t['a55'] = 0x273C;
				t['a56'] = 0x273D;
				t['a57'] = 0x273E;
				t['a58'] = 0x273F;
				t['a59'] = 0x2740;
				t['a60'] = 0x2741;
				t['a61'] = 0x2742;
				t['a62'] = 0x2743;
				t['a63'] = 0x2744;
				t['a64'] = 0x2745;
				t['a65'] = 0x2746;
				t['a66'] = 0x2747;
				t['a67'] = 0x2748;
				t['a68'] = 0x2749;
				t['a69'] = 0x274A;
				t['a70'] = 0x274B;
				t['a71'] = 0x25CF;
				t['a72'] = 0x274D;
				t['a73'] = 0x25A0;
				t['a74'] = 0x274F;
				t['a203'] = 0x2750;
				t['a75'] = 0x2751;
				t['a204'] = 0x2752;
				t['a76'] = 0x25B2;
				t['a77'] = 0x25BC;
				t['a78'] = 0x25C6;
				t['a79'] = 0x2756;
				t['a81'] = 0x25D7;
				t['a82'] = 0x2758;
				t['a83'] = 0x2759;
				t['a84'] = 0x275A;
				t['a97'] = 0x275B;
				t['a98'] = 0x275C;
				t['a99'] = 0x275D;
				t['a100'] = 0x275E;
				t['a101'] = 0x2761;
				t['a102'] = 0x2762;
				t['a103'] = 0x2763;
				t['a104'] = 0x2764;
				t['a106'] = 0x2765;
				t['a107'] = 0x2766;
				t['a108'] = 0x2767;
				t['a112'] = 0x2663;
				t['a111'] = 0x2666;
				t['a110'] = 0x2665;
				t['a109'] = 0x2660;
				t['a120'] = 0x2460;
				t['a121'] = 0x2461;
				t['a122'] = 0x2462;
				t['a123'] = 0x2463;
				t['a124'] = 0x2464;
				t['a125'] = 0x2465;
				t['a126'] = 0x2466;
				t['a127'] = 0x2467;
				t['a128'] = 0x2468;
				t['a129'] = 0x2469;
				t['a130'] = 0x2776;
				t['a131'] = 0x2777;
				t['a132'] = 0x2778;
				t['a133'] = 0x2779;
				t['a134'] = 0x277A;
				t['a135'] = 0x277B;
				t['a136'] = 0x277C;
				t['a137'] = 0x277D;
				t['a138'] = 0x277E;
				t['a139'] = 0x277F;
				t['a140'] = 0x2780;
				t['a141'] = 0x2781;
				t['a142'] = 0x2782;
				t['a143'] = 0x2783;
				t['a144'] = 0x2784;
				t['a145'] = 0x2785;
				t['a146'] = 0x2786;
				t['a147'] = 0x2787;
				t['a148'] = 0x2788;
				t['a149'] = 0x2789;
				t['a150'] = 0x278A;
				t['a151'] = 0x278B;
				t['a152'] = 0x278C;
				t['a153'] = 0x278D;
				t['a154'] = 0x278E;
				t['a155'] = 0x278F;
				t['a156'] = 0x2790;
				t['a157'] = 0x2791;
				t['a158'] = 0x2792;
				t['a159'] = 0x2793;
				t['a160'] = 0x2794;
				t['a161'] = 0x2192;
				t['a163'] = 0x2194;
				t['a164'] = 0x2195;
				t['a196'] = 0x2798;
				t['a165'] = 0x2799;
				t['a192'] = 0x279A;
				t['a166'] = 0x279B;
				t['a167'] = 0x279C;
				t['a168'] = 0x279D;
				t['a169'] = 0x279E;
				t['a170'] = 0x279F;
				t['a171'] = 0x27A0;
				t['a172'] = 0x27A1;
				t['a173'] = 0x27A2;
				t['a162'] = 0x27A3;
				t['a174'] = 0x27A4;
				t['a175'] = 0x27A5;
				t['a176'] = 0x27A6;
				t['a177'] = 0x27A7;
				t['a178'] = 0x27A8;
				t['a179'] = 0x27A9;
				t['a193'] = 0x27AA;
				t['a180'] = 0x27AB;
				t['a199'] = 0x27AC;
				t['a181'] = 0x27AD;
				t['a200'] = 0x27AE;
				t['a182'] = 0x27AF;
				t['a201'] = 0x27B1;
				t['a183'] = 0x27B2;
				t['a184'] = 0x27B3;
				t['a197'] = 0x27B4;
				t['a185'] = 0x27B5;
				t['a194'] = 0x27B6;
				t['a198'] = 0x27B7;
				t['a186'] = 0x27B8;
				t['a195'] = 0x27B9;
				t['a187'] = 0x27BA;
				t['a188'] = 0x27BB;
				t['a189'] = 0x27BC;
				t['a190'] = 0x27BD;
				t['a191'] = 0x27BE;
				t['a89'] = 0x2768;
				t['a90'] = 0x2769;
				t['a93'] = 0x276A;
				t['a94'] = 0x276B;
				t['a91'] = 0x276C;
				t['a92'] = 0x276D;
				t['a205'] = 0x276E;
				t['a85'] = 0x276F;
				t['a206'] = 0x2770;
				t['a86'] = 0x2771;
				t['a87'] = 0x2772;
				t['a88'] = 0x2773;
				t['a95'] = 0x2774;
				t['a96'] = 0x2775;
				t['.notdef'] = 0x0000;
			});
			exports.getGlyphsUnicode = getGlyphsUnicode;
			exports.getDingbatsGlyphsUnicode = getDingbatsGlyphsUnicode;

			/***/
		}),
/* 8 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var corePdfManager = __w_pdfjs_require__(33);
			var UNSUPPORTED_FEATURES = sharedUtil.UNSUPPORTED_FEATURES;
			var InvalidPDFException = sharedUtil.InvalidPDFException;
			var MessageHandler = sharedUtil.MessageHandler;
			var MissingPDFException = sharedUtil.MissingPDFException;
			var UnexpectedResponseException = sharedUtil.UnexpectedResponseException;
			var PasswordException = sharedUtil.PasswordException;
			var UnknownErrorException = sharedUtil.UnknownErrorException;
			var XRefParseException = sharedUtil.XRefParseException;
			var arrayByteLength = sharedUtil.arrayByteLength;
			var arraysToBytes = sharedUtil.arraysToBytes;
			var assert = sharedUtil.assert;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var info = sharedUtil.info;
			var warn = sharedUtil.warn;
			var setVerbosityLevel = sharedUtil.setVerbosityLevel;
			var isNodeJS = sharedUtil.isNodeJS;
			var Ref = corePrimitives.Ref;
			var LocalPdfManager = corePdfManager.LocalPdfManager;
			var NetworkPdfManager = corePdfManager.NetworkPdfManager;
			var WorkerTask = function WorkerTaskClosure() {
				function WorkerTask(name) {
					this.name = name;
					this.terminated = false;
					this._capability = createPromiseCapability();
				}
				WorkerTask.prototype = {
					get finished() {
						return this._capability.promise;
					},
					finish: function () {
						this._capability.resolve();
					},
					terminate: function () {
						this.terminated = true;
					},
					ensureNotTerminated: function () {
						if (this.terminated) {
							throw new Error('Worker task was terminated');
						}
					}
				};
				return WorkerTask;
			}();
			var PDFWorkerStream = function PDFWorkerStreamClosure() {
				function PDFWorkerStream(params, msgHandler) {
					this._queuedChunks = [];
					var initialData = params.initialData;
					if (initialData && initialData.length > 0) {
						this._queuedChunks.push(initialData);
					}
					this._msgHandler = msgHandler;
					this._isRangeSupported = !params.disableRange;
					this._isStreamingSupported = !params.disableStream;
					this._contentLength = params.length;
					this._fullRequestReader = null;
					this._rangeReaders = [];
					msgHandler.on('OnDataRange', this._onReceiveData.bind(this));
					msgHandler.on('OnDataProgress', this._onProgress.bind(this));
				}
				PDFWorkerStream.prototype = {
					_onReceiveData: function PDFWorkerStream_onReceiveData(args) {
						if (args.begin === undefined) {
							if (this._fullRequestReader) {
								this._fullRequestReader._enqueue(args.chunk);
							} else {
								this._queuedChunks.push(args.chunk);
							}
						} else {
							var found = this._rangeReaders.some(function (rangeReader) {
								if (rangeReader._begin !== args.begin) {
									return false;
								}
								rangeReader._enqueue(args.chunk);
								return true;
							});
							assert(found);
						}
					},
					_onProgress: function PDFWorkerStream_onProgress(evt) {
						if (this._rangeReaders.length > 0) {
							var firstReader = this._rangeReaders[0];
							if (firstReader.onProgress) {
								firstReader.onProgress({ loaded: evt.loaded });
							}
						}
					},
					_removeRangeReader: function PDFWorkerStream_removeRangeReader(reader) {
						var i = this._rangeReaders.indexOf(reader);
						if (i >= 0) {
							this._rangeReaders.splice(i, 1);
						}
					},
					getFullReader: function PDFWorkerStream_getFullReader() {
						assert(!this._fullRequestReader);
						var queuedChunks = this._queuedChunks;
						this._queuedChunks = null;
						return new PDFWorkerStreamReader(this, queuedChunks);
					},
					getRangeReader: function PDFWorkerStream_getRangeReader(begin, end) {
						var reader = new PDFWorkerStreamRangeReader(this, begin, end);
						this._msgHandler.send('RequestDataRange', {
							begin: begin,
							end: end
						});
						this._rangeReaders.push(reader);
						return reader;
					},
					cancelAllRequests: function PDFWorkerStream_cancelAllRequests(reason) {
						if (this._fullRequestReader) {
							this._fullRequestReader.cancel(reason);
						}
						var readers = this._rangeReaders.slice(0);
						readers.forEach(function (rangeReader) {
							rangeReader.cancel(reason);
						});
					}
				};
				function PDFWorkerStreamReader(stream, queuedChunks) {
					this._stream = stream;
					this._done = false;
					this._queuedChunks = queuedChunks || [];
					this._requests = [];
					this._headersReady = Promise.resolve();
					stream._fullRequestReader = this;
					this.onProgress = null;
				}
				PDFWorkerStreamReader.prototype = {
					_enqueue: function PDFWorkerStreamReader_enqueue(chunk) {
						if (this._done) {
							return;
						}
						if (this._requests.length > 0) {
							var requestCapability = this._requests.shift();
							requestCapability.resolve({
								value: chunk,
								done: false
							});
							return;
						}
						this._queuedChunks.push(chunk);
					},
					get headersReady() {
						return this._headersReady;
					},
					get isRangeSupported() {
						return this._stream._isRangeSupported;
					},
					get isStreamingSupported() {
						return this._stream._isStreamingSupported;
					},
					get contentLength() {
						return this._stream._contentLength;
					},
					read: function PDFWorkerStreamReader_read() {
						if (this._queuedChunks.length > 0) {
							var chunk = this._queuedChunks.shift();
							return Promise.resolve({
								value: chunk,
								done: false
							});
						}
						if (this._done) {
							return Promise.resolve({
								value: undefined,
								done: true
							});
						}
						var requestCapability = createPromiseCapability();
						this._requests.push(requestCapability);
						return requestCapability.promise;
					},
					cancel: function PDFWorkerStreamReader_cancel(reason) {
						this._done = true;
						this._requests.forEach(function (requestCapability) {
							requestCapability.resolve({
								value: undefined,
								done: true
							});
						});
						this._requests = [];
					}
				};
				function PDFWorkerStreamRangeReader(stream, begin, end) {
					this._stream = stream;
					this._begin = begin;
					this._end = end;
					this._queuedChunk = null;
					this._requests = [];
					this._done = false;
					this.onProgress = null;
				}
				PDFWorkerStreamRangeReader.prototype = {
					_enqueue: function PDFWorkerStreamRangeReader_enqueue(chunk) {
						if (this._done) {
							return;
						}
						if (this._requests.length === 0) {
							this._queuedChunk = chunk;
						} else {
							var requestsCapability = this._requests.shift();
							requestsCapability.resolve({
								value: chunk,
								done: false
							});
							this._requests.forEach(function (requestCapability) {
								requestCapability.resolve({
									value: undefined,
									done: true
								});
							});
							this._requests = [];
						}
						this._done = true;
						this._stream._removeRangeReader(this);
					},
					get isStreamingSupported() {
						return false;
					},
					read: function PDFWorkerStreamRangeReader_read() {
						if (this._queuedChunk) {
							return Promise.resolve({
								value: this._queuedChunk,
								done: false
							});
						}
						if (this._done) {
							return Promise.resolve({
								value: undefined,
								done: true
							});
						}
						var requestCapability = createPromiseCapability();
						this._requests.push(requestCapability);
						return requestCapability.promise;
					},
					cancel: function PDFWorkerStreamRangeReader_cancel(reason) {
						this._done = true;
						this._requests.forEach(function (requestCapability) {
							requestCapability.resolve({
								value: undefined,
								done: true
							});
						});
						this._requests = [];
						this._stream._removeRangeReader(this);
					}
				};
				return PDFWorkerStream;
			}();
			var PDFNetworkStream;
			function setPDFNetworkStreamClass(cls) {
				PDFNetworkStream = cls;
			}
			var WorkerMessageHandler = {
				setup: function wphSetup(handler, port) {
					var testMessageProcessed = false;
					handler.on('test', function wphSetupTest(data) {
						if (testMessageProcessed) {
							return;
						}
						testMessageProcessed = true;
						if (!(data instanceof Uint8Array)) {
							handler.send('test', 'main', false);
							return;
						}
						var supportTransfers = data[0] === 255;
						handler.postMessageTransfers = supportTransfers;
						var xhr = new XMLHttpRequest();
						var responseExists = 'response' in xhr;
						try {
							xhr.responseType;
						} catch (e) {
							responseExists = false;
						}
						if (!responseExists) {
							handler.send('test', false);
							return;
						}
						handler.send('test', {
							supportTypedArray: true,
							supportTransfers: supportTransfers
						});
					});
					handler.on('configure', function wphConfigure(data) {
						setVerbosityLevel(data.verbosity);
					});
					handler.on('GetDocRequest', function wphSetupDoc(data) {
						return WorkerMessageHandler.createDocumentHandler(data, port);
					});
				},
				createDocumentHandler: function wphCreateDocumentHandler(docParams, port) {
					var pdfManager;
					var terminated = false;
					var cancelXHRs = null;
					var WorkerTasks = [];
					var docId = docParams.docId;
					var docBaseUrl = docParams.docBaseUrl;
					var workerHandlerName = docParams.docId + '_worker';
					var handler = new MessageHandler(workerHandlerName, docId, port);
					handler.postMessageTransfers = docParams.postMessageTransfers;
					function ensureNotTerminated() {
						if (terminated) {
							throw new Error('Worker was terminated');
						}
					}
					function startWorkerTask(task) {
						WorkerTasks.push(task);
					}
					function finishWorkerTask(task) {
						task.finish();
						var i = WorkerTasks.indexOf(task);
						WorkerTasks.splice(i, 1);
					}
					function loadDocument(recoveryMode) {
						var loadDocumentCapability = createPromiseCapability();
						var parseSuccess = function parseSuccess() {
							var numPagesPromise = pdfManager.ensureDoc('numPages');
							var fingerprintPromise = pdfManager.ensureDoc('fingerprint');
							var encryptedPromise = pdfManager.ensureXRef('encrypt');
							Promise.all([
								numPagesPromise,
								fingerprintPromise,
								encryptedPromise
							]).then(function onDocReady(results) {
								var doc = {
									numPages: results[0],
									fingerprint: results[1],
									encrypted: !!results[2]
								};
								loadDocumentCapability.resolve(doc);
							}, parseFailure);
						};
						var parseFailure = function parseFailure(e) {
							loadDocumentCapability.reject(e);
						};
						pdfManager.ensureDoc('checkHeader', []).then(function () {
							pdfManager.ensureDoc('parseStartXRef', []).then(function () {
								pdfManager.ensureDoc('parse', [recoveryMode]).then(parseSuccess, parseFailure);
							}, parseFailure);
						}, parseFailure);
						return loadDocumentCapability.promise;
					}
					function getPdfManager(data, evaluatorOptions) {
						var pdfManagerCapability = createPromiseCapability();
						var pdfManager;
						var source = data.source;
						if (source.data) {
							try {
								pdfManager = new LocalPdfManager(docId, source.data, source.password, evaluatorOptions, docBaseUrl);
								pdfManagerCapability.resolve(pdfManager);
							} catch (ex) {
								pdfManagerCapability.reject(ex);
							}
							return pdfManagerCapability.promise;
						}
						var pdfStream;
						try {
							if (source.chunkedViewerLoading) {
								pdfStream = new PDFWorkerStream(source, handler);
							} else {
								assert(PDFNetworkStream, 'pdfjs/core/network module is not loaded');
								pdfStream = new PDFNetworkStream(data);
							}
						} catch (ex) {
							pdfManagerCapability.reject(ex);
							return pdfManagerCapability.promise;
						}
						var fullRequest = pdfStream.getFullReader();
						fullRequest.headersReady.then(function () {
							if (!fullRequest.isStreamingSupported || !fullRequest.isRangeSupported) {
								fullRequest.onProgress = function (evt) {
									handler.send('DocProgress', {
										loaded: evt.loaded,
										total: evt.total
									});
								};
							}
							if (!fullRequest.isRangeSupported) {
								return;
							}
							var disableAutoFetch = source.disableAutoFetch || fullRequest.isStreamingSupported;
							pdfManager = new NetworkPdfManager(docId, pdfStream, {
								msgHandler: handler,
								url: source.url,
								password: source.password,
								length: fullRequest.contentLength,
								disableAutoFetch: disableAutoFetch,
								rangeChunkSize: source.rangeChunkSize
							}, evaluatorOptions, docBaseUrl);
							pdfManagerCapability.resolve(pdfManager);
							cancelXHRs = null;
						}).catch(function (reason) {
							pdfManagerCapability.reject(reason);
							cancelXHRs = null;
						});
						var cachedChunks = [], loaded = 0;
						var flushChunks = function () {
							var pdfFile = arraysToBytes(cachedChunks);
							if (source.length && pdfFile.length !== source.length) {
								warn('reported HTTP length is different from actual');
							}
							try {
								pdfManager = new LocalPdfManager(docId, pdfFile, source.password, evaluatorOptions, docBaseUrl);
								pdfManagerCapability.resolve(pdfManager);
							} catch (ex) {
								pdfManagerCapability.reject(ex);
							}
							cachedChunks = [];
						};
						var readPromise = new Promise(function (resolve, reject) {
							var readChunk = function (chunk) {
								try {
									ensureNotTerminated();
									if (chunk.done) {
										if (!pdfManager) {
											flushChunks();
										}
										cancelXHRs = null;
										return;
									}
									var data = chunk.value;
									loaded += arrayByteLength(data);
									if (!fullRequest.isStreamingSupported) {
										handler.send('DocProgress', {
											loaded: loaded,
											total: Math.max(loaded, fullRequest.contentLength || 0)
										});
									}
									if (pdfManager) {
										pdfManager.sendProgressiveData(data);
									} else {
										cachedChunks.push(data);
									}
									fullRequest.read().then(readChunk, reject);
								} catch (e) {
									reject(e);
								}
							};
							fullRequest.read().then(readChunk, reject);
						});
						readPromise.catch(function (e) {
							pdfManagerCapability.reject(e);
							cancelXHRs = null;
						});
						cancelXHRs = function () {
							pdfStream.cancelAllRequests('abort');
						};
						return pdfManagerCapability.promise;
					}
					function setupDoc(data) {
						function onSuccess(doc) {
							ensureNotTerminated();
							handler.send('GetDoc', { pdfInfo: doc });
						}
						function onFailure(e) {
							if (e instanceof PasswordException) {
								var task = new WorkerTask('PasswordException: response ' + e.code);
								startWorkerTask(task);
								handler.sendWithPromise('PasswordRequest', e).then(function (data) {
									finishWorkerTask(task);
									pdfManager.updatePassword(data.password);
									pdfManagerReady();
								}).catch(function (ex) {
									finishWorkerTask(task);
									handler.send('PasswordException', ex);
								}.bind(null, e));
							} else if (e instanceof InvalidPDFException) {
								handler.send('InvalidPDF', e);
							} else if (e instanceof MissingPDFException) {
								handler.send('MissingPDF', e);
							} else if (e instanceof UnexpectedResponseException) {
								handler.send('UnexpectedResponse', e);
							} else {
								handler.send('UnknownError', new UnknownErrorException(e.message, e.toString()));
							}
						}
						function pdfManagerReady() {
							ensureNotTerminated();
							loadDocument(false).then(onSuccess, function loadFailure(ex) {
								ensureNotTerminated();
								if (!(ex instanceof XRefParseException)) {
									onFailure(ex);
									return;
								}
								pdfManager.requestLoadedStream();
								pdfManager.onLoadedStream().then(function () {
									ensureNotTerminated();
									loadDocument(true).then(onSuccess, onFailure);
								});
							}, onFailure);
						}
						ensureNotTerminated();
						var evaluatorOptions = {
							forceDataSchema: data.disableCreateObjectURL,
							maxImageSize: data.maxImageSize === undefined ? -1 : data.maxImageSize,
							disableFontFace: data.disableFontFace,
							disableNativeImageDecoder: data.disableNativeImageDecoder
						};
						getPdfManager(data, evaluatorOptions).then(function (newPdfManager) {
							if (terminated) {
								newPdfManager.terminate();
								throw new Error('Worker was terminated');
							}
							pdfManager = newPdfManager;
							handler.send('PDFManagerReady', null);
							pdfManager.onLoadedStream().then(function (stream) {
								handler.send('DataLoaded', { length: stream.bytes.byteLength });
							});
						}).then(pdfManagerReady, onFailure);
					}
					handler.on('GetPage', function wphSetupGetPage(data) {
						return pdfManager.getPage(data.pageIndex).then(function (page) {
							var rotatePromise = pdfManager.ensure(page, 'rotate');
							var refPromise = pdfManager.ensure(page, 'ref');
							var userUnitPromise = pdfManager.ensure(page, 'userUnit');
							var viewPromise = pdfManager.ensure(page, 'view');
							return Promise.all([
								rotatePromise,
								refPromise,
								userUnitPromise,
								viewPromise
							]).then(function (results) {
								return {
									rotate: results[0],
									ref: results[1],
									userUnit: results[2],
									view: results[3]
								};
							});
						});
					});
					handler.on('GetPageIndex', function wphSetupGetPageIndex(data) {
						var ref = new Ref(data.ref.num, data.ref.gen);
						var catalog = pdfManager.pdfDocument.catalog;
						return catalog.getPageIndex(ref);
					});
					handler.on('GetDestinations', function wphSetupGetDestinations(data) {
						return pdfManager.ensureCatalog('destinations');
					});
					handler.on('GetDestination', function wphSetupGetDestination(data) {
						return pdfManager.ensureCatalog('getDestination', [data.id]);
					});
					handler.on('GetPageLabels', function wphSetupGetPageLabels(data) {
						return pdfManager.ensureCatalog('pageLabels');
					});
					handler.on('GetAttachments', function wphSetupGetAttachments(data) {
						return pdfManager.ensureCatalog('attachments');
					});
					handler.on('GetJavaScript', function wphSetupGetJavaScript(data) {
						return pdfManager.ensureCatalog('javaScript');
					});
					handler.on('GetOutline', function wphSetupGetOutline(data) {
						return pdfManager.ensureCatalog('documentOutline');
					});
					handler.on('GetMetadata', function wphSetupGetMetadata(data) {
						return Promise.all([
							pdfManager.ensureDoc('documentInfo'),
							pdfManager.ensureCatalog('metadata')
						]);
					});
					handler.on('GetData', function wphSetupGetData(data) {
						pdfManager.requestLoadedStream();
						return pdfManager.onLoadedStream().then(function (stream) {
							return stream.bytes;
						});
					});
					handler.on('GetStats', function wphSetupGetStats(data) {
						return pdfManager.pdfDocument.xref.stats;
					});
					handler.on('GetAnnotations', function wphSetupGetAnnotations(data) {
						return pdfManager.getPage(data.pageIndex).then(function (page) {
							return pdfManager.ensure(page, 'getAnnotationsData', [data.intent]);
						});
					});
					handler.on('RenderPageRequest', function wphSetupRenderPage(data) {
						var pageIndex = data.pageIndex;
						pdfManager.getPage(pageIndex).then(function (page) {
							var task = new WorkerTask('RenderPageRequest: page ' + pageIndex);
							startWorkerTask(task);
							var pageNum = pageIndex + 1;
							var start = Date.now();
							page.getOperatorList(handler, task, data.intent, data.renderInteractiveForms).then(function (operatorList) {
								finishWorkerTask(task);
								info('page=' + pageNum + ' - getOperatorList: time=' + (Date.now() - start) + 'ms, len=' + operatorList.totalLength);
							}, function (e) {
								finishWorkerTask(task);
								if (task.terminated) {
									return;
								}
								handler.send('UnsupportedFeature', { featureId: UNSUPPORTED_FEATURES.unknown });
								var minimumStackMessage = 'worker.js: while trying to getPage() and getOperatorList()';
								var wrappedException;
								if (typeof e === 'string') {
									wrappedException = {
										message: e,
										stack: minimumStackMessage
									};
								} else if (typeof e === 'object') {
									wrappedException = {
										message: e.message || e.toString(),
										stack: e.stack || minimumStackMessage
									};
								} else {
									wrappedException = {
										message: 'Unknown exception type: ' + typeof e,
										stack: minimumStackMessage
									};
								}
								handler.send('PageError', {
									pageNum: pageNum,
									error: wrappedException,
									intent: data.intent
								});
							});
						});
					}, this);
					handler.on('GetTextContent', function wphExtractText(data) {
						var pageIndex = data.pageIndex;
						var normalizeWhitespace = data.normalizeWhitespace;
						var combineTextItems = data.combineTextItems;
						return pdfManager.getPage(pageIndex).then(function (page) {
							var task = new WorkerTask('GetTextContent: page ' + pageIndex);
							startWorkerTask(task);
							var pageNum = pageIndex + 1;
							var start = Date.now();
							return page.extractTextContent(task, normalizeWhitespace, combineTextItems).then(function (textContent) {
								finishWorkerTask(task);
								info('text indexing: page=' + pageNum + ' - time=' + (Date.now() - start) + 'ms');
								return textContent;
							}, function (reason) {
								finishWorkerTask(task);
								if (task.terminated) {
									return;
								}
								throw reason;
							});
						});
					});
					handler.on('Cleanup', function wphCleanup(data) {
						return pdfManager.cleanup();
					});
					handler.on('Terminate', function wphTerminate(data) {
						terminated = true;
						if (pdfManager) {
							pdfManager.terminate();
							pdfManager = null;
						}
						if (cancelXHRs) {
							cancelXHRs();
						}
						var waitOn = [];
						WorkerTasks.forEach(function (task) {
							waitOn.push(task.finished);
							task.terminate();
						});
						return Promise.all(waitOn).then(function () {
							handler.destroy();
							handler = null;
						});
					});
					handler.on('Ready', function wphReady(data) {
						setupDoc(docParams);
						docParams = null;
					});
					return workerHandlerName;
				}
			};
			function initializeWorker() {
				var handler = new MessageHandler('worker', 'main', self);
				WorkerMessageHandler.setup(handler, self);
				handler.send('ready', null);
			}
			if (typeof window === 'undefined' && !isNodeJS()) {
				initializeWorker();
			}
			exports.setPDFNetworkStreamClass = setPDFNetworkStreamClass;
			exports.WorkerTask = WorkerTask;
			exports.WorkerMessageHandler = WorkerMessageHandler;

			/***/
		}),
/* 9 */
/***/ (function (module, exports) {

			var g;
			g = function () {
				return this;
			}();
			try {
				g = g || Function("return this")() || (1, eval)("this");
			} catch (e) {
				if (typeof window === "object")
					g = window;
			}
			module.exports = g;

			/***/
		}),
/* 10 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var ArithmeticDecoder = function ArithmeticDecoderClosure() {
				var QeTable = [
					{
						qe: 0x5601,
						nmps: 1,
						nlps: 1,
						switchFlag: 1
					},
					{
						qe: 0x3401,
						nmps: 2,
						nlps: 6,
						switchFlag: 0
					},
					{
						qe: 0x1801,
						nmps: 3,
						nlps: 9,
						switchFlag: 0
					},
					{
						qe: 0x0AC1,
						nmps: 4,
						nlps: 12,
						switchFlag: 0
					},
					{
						qe: 0x0521,
						nmps: 5,
						nlps: 29,
						switchFlag: 0
					},
					{
						qe: 0x0221,
						nmps: 38,
						nlps: 33,
						switchFlag: 0
					},
					{
						qe: 0x5601,
						nmps: 7,
						nlps: 6,
						switchFlag: 1
					},
					{
						qe: 0x5401,
						nmps: 8,
						nlps: 14,
						switchFlag: 0
					},
					{
						qe: 0x4801,
						nmps: 9,
						nlps: 14,
						switchFlag: 0
					},
					{
						qe: 0x3801,
						nmps: 10,
						nlps: 14,
						switchFlag: 0
					},
					{
						qe: 0x3001,
						nmps: 11,
						nlps: 17,
						switchFlag: 0
					},
					{
						qe: 0x2401,
						nmps: 12,
						nlps: 18,
						switchFlag: 0
					},
					{
						qe: 0x1C01,
						nmps: 13,
						nlps: 20,
						switchFlag: 0
					},
					{
						qe: 0x1601,
						nmps: 29,
						nlps: 21,
						switchFlag: 0
					},
					{
						qe: 0x5601,
						nmps: 15,
						nlps: 14,
						switchFlag: 1
					},
					{
						qe: 0x5401,
						nmps: 16,
						nlps: 14,
						switchFlag: 0
					},
					{
						qe: 0x5101,
						nmps: 17,
						nlps: 15,
						switchFlag: 0
					},
					{
						qe: 0x4801,
						nmps: 18,
						nlps: 16,
						switchFlag: 0
					},
					{
						qe: 0x3801,
						nmps: 19,
						nlps: 17,
						switchFlag: 0
					},
					{
						qe: 0x3401,
						nmps: 20,
						nlps: 18,
						switchFlag: 0
					},
					{
						qe: 0x3001,
						nmps: 21,
						nlps: 19,
						switchFlag: 0
					},
					{
						qe: 0x2801,
						nmps: 22,
						nlps: 19,
						switchFlag: 0
					},
					{
						qe: 0x2401,
						nmps: 23,
						nlps: 20,
						switchFlag: 0
					},
					{
						qe: 0x2201,
						nmps: 24,
						nlps: 21,
						switchFlag: 0
					},
					{
						qe: 0x1C01,
						nmps: 25,
						nlps: 22,
						switchFlag: 0
					},
					{
						qe: 0x1801,
						nmps: 26,
						nlps: 23,
						switchFlag: 0
					},
					{
						qe: 0x1601,
						nmps: 27,
						nlps: 24,
						switchFlag: 0
					},
					{
						qe: 0x1401,
						nmps: 28,
						nlps: 25,
						switchFlag: 0
					},
					{
						qe: 0x1201,
						nmps: 29,
						nlps: 26,
						switchFlag: 0
					},
					{
						qe: 0x1101,
						nmps: 30,
						nlps: 27,
						switchFlag: 0
					},
					{
						qe: 0x0AC1,
						nmps: 31,
						nlps: 28,
						switchFlag: 0
					},
					{
						qe: 0x09C1,
						nmps: 32,
						nlps: 29,
						switchFlag: 0
					},
					{
						qe: 0x08A1,
						nmps: 33,
						nlps: 30,
						switchFlag: 0
					},
					{
						qe: 0x0521,
						nmps: 34,
						nlps: 31,
						switchFlag: 0
					},
					{
						qe: 0x0441,
						nmps: 35,
						nlps: 32,
						switchFlag: 0
					},
					{
						qe: 0x02A1,
						nmps: 36,
						nlps: 33,
						switchFlag: 0
					},
					{
						qe: 0x0221,
						nmps: 37,
						nlps: 34,
						switchFlag: 0
					},
					{
						qe: 0x0141,
						nmps: 38,
						nlps: 35,
						switchFlag: 0
					},
					{
						qe: 0x0111,
						nmps: 39,
						nlps: 36,
						switchFlag: 0
					},
					{
						qe: 0x0085,
						nmps: 40,
						nlps: 37,
						switchFlag: 0
					},
					{
						qe: 0x0049,
						nmps: 41,
						nlps: 38,
						switchFlag: 0
					},
					{
						qe: 0x0025,
						nmps: 42,
						nlps: 39,
						switchFlag: 0
					},
					{
						qe: 0x0015,
						nmps: 43,
						nlps: 40,
						switchFlag: 0
					},
					{
						qe: 0x0009,
						nmps: 44,
						nlps: 41,
						switchFlag: 0
					},
					{
						qe: 0x0005,
						nmps: 45,
						nlps: 42,
						switchFlag: 0
					},
					{
						qe: 0x0001,
						nmps: 45,
						nlps: 43,
						switchFlag: 0
					},
					{
						qe: 0x5601,
						nmps: 46,
						nlps: 46,
						switchFlag: 0
					}
				];
				function ArithmeticDecoder(data, start, end) {
					this.data = data;
					this.bp = start;
					this.dataEnd = end;
					this.chigh = data[start];
					this.clow = 0;
					this.byteIn();
					this.chigh = this.chigh << 7 & 0xFFFF | this.clow >> 9 & 0x7F;
					this.clow = this.clow << 7 & 0xFFFF;
					this.ct -= 7;
					this.a = 0x8000;
				}
				ArithmeticDecoder.prototype = {
					byteIn: function ArithmeticDecoder_byteIn() {
						var data = this.data;
						var bp = this.bp;
						if (data[bp] === 0xFF) {
							var b1 = data[bp + 1];
							if (b1 > 0x8F) {
								this.clow += 0xFF00;
								this.ct = 8;
							} else {
								bp++;
								this.clow += data[bp] << 9;
								this.ct = 7;
								this.bp = bp;
							}
						} else {
							bp++;
							this.clow += bp < this.dataEnd ? data[bp] << 8 : 0xFF00;
							this.ct = 8;
							this.bp = bp;
						}
						if (this.clow > 0xFFFF) {
							this.chigh += this.clow >> 16;
							this.clow &= 0xFFFF;
						}
					},
					readBit: function ArithmeticDecoder_readBit(contexts, pos) {
						var cx_index = contexts[pos] >> 1, cx_mps = contexts[pos] & 1;
						var qeTableIcx = QeTable[cx_index];
						var qeIcx = qeTableIcx.qe;
						var d;
						var a = this.a - qeIcx;
						if (this.chigh < qeIcx) {
							if (a < qeIcx) {
								a = qeIcx;
								d = cx_mps;
								cx_index = qeTableIcx.nmps;
							} else {
								a = qeIcx;
								d = 1 ^ cx_mps;
								if (qeTableIcx.switchFlag === 1) {
									cx_mps = d;
								}
								cx_index = qeTableIcx.nlps;
							}
						} else {
							this.chigh -= qeIcx;
							if ((a & 0x8000) !== 0) {
								this.a = a;
								return cx_mps;
							}
							if (a < qeIcx) {
								d = 1 ^ cx_mps;
								if (qeTableIcx.switchFlag === 1) {
									cx_mps = d;
								}
								cx_index = qeTableIcx.nlps;
							} else {
								d = cx_mps;
								cx_index = qeTableIcx.nmps;
							}
						}
						do {
							if (this.ct === 0) {
								this.byteIn();
							}
							a <<= 1;
							this.chigh = this.chigh << 1 & 0xFFFF | this.clow >> 15 & 1;
							this.clow = this.clow << 1 & 0xFFFF;
							this.ct--;
						} while ((a & 0x8000) === 0);
						this.a = a;
						contexts[pos] = cx_index << 1 | cx_mps;
						return d;
					}
				};
				return ArithmeticDecoder;
			}();
			exports.ArithmeticDecoder = ArithmeticDecoder;

			/***/
		}),
/* 11 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreCharsets = __w_pdfjs_require__(22);
			var coreEncodings = __w_pdfjs_require__(4);
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var bytesToString = sharedUtil.bytesToString;
			var warn = sharedUtil.warn;
			var isArray = sharedUtil.isArray;
			var Util = sharedUtil.Util;
			var stringToBytes = sharedUtil.stringToBytes;
			var assert = sharedUtil.assert;
			var ISOAdobeCharset = coreCharsets.ISOAdobeCharset;
			var ExpertCharset = coreCharsets.ExpertCharset;
			var ExpertSubsetCharset = coreCharsets.ExpertSubsetCharset;
			var StandardEncoding = coreEncodings.StandardEncoding;
			var ExpertEncoding = coreEncodings.ExpertEncoding;
			var MAX_SUBR_NESTING = 10;
			var CFFStandardStrings = [
				'.notdef',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quoteright',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'quoteleft',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'exclamdown',
				'cent',
				'sterling',
				'fraction',
				'yen',
				'florin',
				'section',
				'currency',
				'quotesingle',
				'quotedblleft',
				'guillemotleft',
				'guilsinglleft',
				'guilsinglright',
				'fi',
				'fl',
				'endash',
				'dagger',
				'daggerdbl',
				'periodcentered',
				'paragraph',
				'bullet',
				'quotesinglbase',
				'quotedblbase',
				'quotedblright',
				'guillemotright',
				'ellipsis',
				'perthousand',
				'questiondown',
				'grave',
				'acute',
				'circumflex',
				'tilde',
				'macron',
				'breve',
				'dotaccent',
				'dieresis',
				'ring',
				'cedilla',
				'hungarumlaut',
				'ogonek',
				'caron',
				'emdash',
				'AE',
				'ordfeminine',
				'Lslash',
				'Oslash',
				'OE',
				'ordmasculine',
				'ae',
				'dotlessi',
				'lslash',
				'oslash',
				'oe',
				'germandbls',
				'onesuperior',
				'logicalnot',
				'mu',
				'trademark',
				'Eth',
				'onehalf',
				'plusminus',
				'Thorn',
				'onequarter',
				'divide',
				'brokenbar',
				'degree',
				'thorn',
				'threequarters',
				'twosuperior',
				'registered',
				'minus',
				'eth',
				'multiply',
				'threesuperior',
				'copyright',
				'Aacute',
				'Acircumflex',
				'Adieresis',
				'Agrave',
				'Aring',
				'Atilde',
				'Ccedilla',
				'Eacute',
				'Ecircumflex',
				'Edieresis',
				'Egrave',
				'Iacute',
				'Icircumflex',
				'Idieresis',
				'Igrave',
				'Ntilde',
				'Oacute',
				'Ocircumflex',
				'Odieresis',
				'Ograve',
				'Otilde',
				'Scaron',
				'Uacute',
				'Ucircumflex',
				'Udieresis',
				'Ugrave',
				'Yacute',
				'Ydieresis',
				'Zcaron',
				'aacute',
				'acircumflex',
				'adieresis',
				'agrave',
				'aring',
				'atilde',
				'ccedilla',
				'eacute',
				'ecircumflex',
				'edieresis',
				'egrave',
				'iacute',
				'icircumflex',
				'idieresis',
				'igrave',
				'ntilde',
				'oacute',
				'ocircumflex',
				'odieresis',
				'ograve',
				'otilde',
				'scaron',
				'uacute',
				'ucircumflex',
				'udieresis',
				'ugrave',
				'yacute',
				'ydieresis',
				'zcaron',
				'exclamsmall',
				'Hungarumlautsmall',
				'dollaroldstyle',
				'dollarsuperior',
				'ampersandsmall',
				'Acutesmall',
				'parenleftsuperior',
				'parenrightsuperior',
				'twodotenleader',
				'onedotenleader',
				'zerooldstyle',
				'oneoldstyle',
				'twooldstyle',
				'threeoldstyle',
				'fouroldstyle',
				'fiveoldstyle',
				'sixoldstyle',
				'sevenoldstyle',
				'eightoldstyle',
				'nineoldstyle',
				'commasuperior',
				'threequartersemdash',
				'periodsuperior',
				'questionsmall',
				'asuperior',
				'bsuperior',
				'centsuperior',
				'dsuperior',
				'esuperior',
				'isuperior',
				'lsuperior',
				'msuperior',
				'nsuperior',
				'osuperior',
				'rsuperior',
				'ssuperior',
				'tsuperior',
				'ff',
				'ffi',
				'ffl',
				'parenleftinferior',
				'parenrightinferior',
				'Circumflexsmall',
				'hyphensuperior',
				'Gravesmall',
				'Asmall',
				'Bsmall',
				'Csmall',
				'Dsmall',
				'Esmall',
				'Fsmall',
				'Gsmall',
				'Hsmall',
				'Ismall',
				'Jsmall',
				'Ksmall',
				'Lsmall',
				'Msmall',
				'Nsmall',
				'Osmall',
				'Psmall',
				'Qsmall',
				'Rsmall',
				'Ssmall',
				'Tsmall',
				'Usmall',
				'Vsmall',
				'Wsmall',
				'Xsmall',
				'Ysmall',
				'Zsmall',
				'colonmonetary',
				'onefitted',
				'rupiah',
				'Tildesmall',
				'exclamdownsmall',
				'centoldstyle',
				'Lslashsmall',
				'Scaronsmall',
				'Zcaronsmall',
				'Dieresissmall',
				'Brevesmall',
				'Caronsmall',
				'Dotaccentsmall',
				'Macronsmall',
				'figuredash',
				'hypheninferior',
				'Ogoneksmall',
				'Ringsmall',
				'Cedillasmall',
				'questiondownsmall',
				'oneeighth',
				'threeeighths',
				'fiveeighths',
				'seveneighths',
				'onethird',
				'twothirds',
				'zerosuperior',
				'foursuperior',
				'fivesuperior',
				'sixsuperior',
				'sevensuperior',
				'eightsuperior',
				'ninesuperior',
				'zeroinferior',
				'oneinferior',
				'twoinferior',
				'threeinferior',
				'fourinferior',
				'fiveinferior',
				'sixinferior',
				'seveninferior',
				'eightinferior',
				'nineinferior',
				'centinferior',
				'dollarinferior',
				'periodinferior',
				'commainferior',
				'Agravesmall',
				'Aacutesmall',
				'Acircumflexsmall',
				'Atildesmall',
				'Adieresissmall',
				'Aringsmall',
				'AEsmall',
				'Ccedillasmall',
				'Egravesmall',
				'Eacutesmall',
				'Ecircumflexsmall',
				'Edieresissmall',
				'Igravesmall',
				'Iacutesmall',
				'Icircumflexsmall',
				'Idieresissmall',
				'Ethsmall',
				'Ntildesmall',
				'Ogravesmall',
				'Oacutesmall',
				'Ocircumflexsmall',
				'Otildesmall',
				'Odieresissmall',
				'OEsmall',
				'Oslashsmall',
				'Ugravesmall',
				'Uacutesmall',
				'Ucircumflexsmall',
				'Udieresissmall',
				'Yacutesmall',
				'Thornsmall',
				'Ydieresissmall',
				'001.000',
				'001.001',
				'001.002',
				'001.003',
				'Black',
				'Bold',
				'Book',
				'Light',
				'Medium',
				'Regular',
				'Roman',
				'Semibold'
			];
			var CFFParser = function CFFParserClosure() {
				var CharstringValidationData = [
					null,
					{
						id: 'hstem',
						min: 2,
						stackClearing: true,
						stem: true
					},
					null,
					{
						id: 'vstem',
						min: 2,
						stackClearing: true,
						stem: true
					},
					{
						id: 'vmoveto',
						min: 1,
						stackClearing: true
					},
					{
						id: 'rlineto',
						min: 2,
						resetStack: true
					},
					{
						id: 'hlineto',
						min: 1,
						resetStack: true
					},
					{
						id: 'vlineto',
						min: 1,
						resetStack: true
					},
					{
						id: 'rrcurveto',
						min: 6,
						resetStack: true
					},
					null,
					{
						id: 'callsubr',
						min: 1,
						undefStack: true
					},
					{
						id: 'return',
						min: 0,
						undefStack: true
					},
					null,
					null,
					{
						id: 'endchar',
						min: 0,
						stackClearing: true
					},
					null,
					null,
					null,
					{
						id: 'hstemhm',
						min: 2,
						stackClearing: true,
						stem: true
					},
					{
						id: 'hintmask',
						min: 0,
						stackClearing: true
					},
					{
						id: 'cntrmask',
						min: 0,
						stackClearing: true
					},
					{
						id: 'rmoveto',
						min: 2,
						stackClearing: true
					},
					{
						id: 'hmoveto',
						min: 1,
						stackClearing: true
					},
					{
						id: 'vstemhm',
						min: 2,
						stackClearing: true,
						stem: true
					},
					{
						id: 'rcurveline',
						min: 8,
						resetStack: true
					},
					{
						id: 'rlinecurve',
						min: 8,
						resetStack: true
					},
					{
						id: 'vvcurveto',
						min: 4,
						resetStack: true
					},
					{
						id: 'hhcurveto',
						min: 4,
						resetStack: true
					},
					null,
					{
						id: 'callgsubr',
						min: 1,
						undefStack: true
					},
					{
						id: 'vhcurveto',
						min: 4,
						resetStack: true
					},
					{
						id: 'hvcurveto',
						min: 4,
						resetStack: true
					}
				];
				var CharstringValidationData12 = [
					null,
					null,
					null,
					{
						id: 'and',
						min: 2,
						stackDelta: -1
					},
					{
						id: 'or',
						min: 2,
						stackDelta: -1
					},
					{
						id: 'not',
						min: 1,
						stackDelta: 0
					},
					null,
					null,
					null,
					{
						id: 'abs',
						min: 1,
						stackDelta: 0
					},
					{
						id: 'add',
						min: 2,
						stackDelta: -1,
						stackFn: function stack_div(stack, index) {
							stack[index - 2] = stack[index - 2] + stack[index - 1];
						}
					},
					{
						id: 'sub',
						min: 2,
						stackDelta: -1,
						stackFn: function stack_div(stack, index) {
							stack[index - 2] = stack[index - 2] - stack[index - 1];
						}
					},
					{
						id: 'div',
						min: 2,
						stackDelta: -1,
						stackFn: function stack_div(stack, index) {
							stack[index - 2] = stack[index - 2] / stack[index - 1];
						}
					},
					null,
					{
						id: 'neg',
						min: 1,
						stackDelta: 0,
						stackFn: function stack_div(stack, index) {
							stack[index - 1] = -stack[index - 1];
						}
					},
					{
						id: 'eq',
						min: 2,
						stackDelta: -1
					},
					null,
					null,
					{
						id: 'drop',
						min: 1,
						stackDelta: -1
					},
					null,
					{
						id: 'put',
						min: 2,
						stackDelta: -2
					},
					{
						id: 'get',
						min: 1,
						stackDelta: 0
					},
					{
						id: 'ifelse',
						min: 4,
						stackDelta: -3
					},
					{
						id: 'random',
						min: 0,
						stackDelta: 1
					},
					{
						id: 'mul',
						min: 2,
						stackDelta: -1,
						stackFn: function stack_div(stack, index) {
							stack[index - 2] = stack[index - 2] * stack[index - 1];
						}
					},
					null,
					{
						id: 'sqrt',
						min: 1,
						stackDelta: 0
					},
					{
						id: 'dup',
						min: 1,
						stackDelta: 1
					},
					{
						id: 'exch',
						min: 2,
						stackDelta: 0
					},
					{
						id: 'index',
						min: 2,
						stackDelta: 0
					},
					{
						id: 'roll',
						min: 3,
						stackDelta: -2
					},
					null,
					null,
					null,
					{
						id: 'hflex',
						min: 7,
						resetStack: true
					},
					{
						id: 'flex',
						min: 13,
						resetStack: true
					},
					{
						id: 'hflex1',
						min: 9,
						resetStack: true
					},
					{
						id: 'flex1',
						min: 11,
						resetStack: true
					}
				];
				function CFFParser(file, properties, seacAnalysisEnabled) {
					this.bytes = file.getBytes();
					this.properties = properties;
					this.seacAnalysisEnabled = !!seacAnalysisEnabled;
				}
				CFFParser.prototype = {
					parse: function CFFParser_parse() {
						var properties = this.properties;
						var cff = new CFF();
						this.cff = cff;
						var header = this.parseHeader();
						var nameIndex = this.parseIndex(header.endPos);
						var topDictIndex = this.parseIndex(nameIndex.endPos);
						var stringIndex = this.parseIndex(topDictIndex.endPos);
						var globalSubrIndex = this.parseIndex(stringIndex.endPos);
						var topDictParsed = this.parseDict(topDictIndex.obj.get(0));
						var topDict = this.createDict(CFFTopDict, topDictParsed, cff.strings);
						cff.header = header.obj;
						cff.names = this.parseNameIndex(nameIndex.obj);
						cff.strings = this.parseStringIndex(stringIndex.obj);
						cff.topDict = topDict;
						cff.globalSubrIndex = globalSubrIndex.obj;
						this.parsePrivateDict(cff.topDict);
						cff.isCIDFont = topDict.hasName('ROS');
						var charStringOffset = topDict.getByName('CharStrings');
						var charStringIndex = this.parseIndex(charStringOffset).obj;
						var fontMatrix = topDict.getByName('FontMatrix');
						if (fontMatrix) {
							properties.fontMatrix = fontMatrix;
						}
						var fontBBox = topDict.getByName('FontBBox');
						if (fontBBox) {
							properties.ascent = Math.max(fontBBox[3], fontBBox[1]);
							properties.descent = Math.min(fontBBox[1], fontBBox[3]);
							properties.ascentScaled = true;
						}
						var charset, encoding;
						if (cff.isCIDFont) {
							var fdArrayIndex = this.parseIndex(topDict.getByName('FDArray')).obj;
							for (var i = 0, ii = fdArrayIndex.count; i < ii; ++i) {
								var dictRaw = fdArrayIndex.get(i);
								var fontDict = this.createDict(CFFTopDict, this.parseDict(dictRaw), cff.strings);
								this.parsePrivateDict(fontDict);
								cff.fdArray.push(fontDict);
							}
							encoding = null;
							charset = this.parseCharsets(topDict.getByName('charset'), charStringIndex.count, cff.strings, true);
							cff.fdSelect = this.parseFDSelect(topDict.getByName('FDSelect'), charStringIndex.count);
						} else {
							charset = this.parseCharsets(topDict.getByName('charset'), charStringIndex.count, cff.strings, false);
							encoding = this.parseEncoding(topDict.getByName('Encoding'), properties, cff.strings, charset.charset);
						}
						cff.charset = charset;
						cff.encoding = encoding;
						var charStringsAndSeacs = this.parseCharStrings(charStringIndex, topDict.privateDict.subrsIndex, globalSubrIndex.obj, cff.fdSelect, cff.fdArray);
						cff.charStrings = charStringsAndSeacs.charStrings;
						cff.seacs = charStringsAndSeacs.seacs;
						cff.widths = charStringsAndSeacs.widths;
						return cff;
					},
					parseHeader: function CFFParser_parseHeader() {
						var bytes = this.bytes;
						var bytesLength = bytes.length;
						var offset = 0;
						while (offset < bytesLength && bytes[offset] !== 1) {
							++offset;
						}
						if (offset >= bytesLength) {
							error('Invalid CFF header');
						} else if (offset !== 0) {
							info('cff data is shifted');
							bytes = bytes.subarray(offset);
							this.bytes = bytes;
						}
						var major = bytes[0];
						var minor = bytes[1];
						var hdrSize = bytes[2];
						var offSize = bytes[3];
						var header = new CFFHeader(major, minor, hdrSize, offSize);
						return {
							obj: header,
							endPos: hdrSize
						};
					},
					parseDict: function CFFParser_parseDict(dict) {
						var pos = 0;
						function parseOperand() {
							var value = dict[pos++];
							if (value === 30) {
								return parseFloatOperand();
							} else if (value === 28) {
								value = dict[pos++];
								value = (value << 24 | dict[pos++] << 16) >> 16;
								return value;
							} else if (value === 29) {
								value = dict[pos++];
								value = value << 8 | dict[pos++];
								value = value << 8 | dict[pos++];
								value = value << 8 | dict[pos++];
								return value;
							} else if (value >= 32 && value <= 246) {
								return value - 139;
							} else if (value >= 247 && value <= 250) {
								return (value - 247) * 256 + dict[pos++] + 108;
							} else if (value >= 251 && value <= 254) {
								return -((value - 251) * 256) - dict[pos++] - 108;
							}
							warn('CFFParser_parseDict: "' + value + '" is a reserved command.');
							return NaN;
						}
						function parseFloatOperand() {
							var str = '';
							var eof = 15;
							var lookup = [
								'0',
								'1',
								'2',
								'3',
								'4',
								'5',
								'6',
								'7',
								'8',
								'9',
								'.',
								'E',
								'E-',
								null,
								'-'
							];
							var length = dict.length;
							while (pos < length) {
								var b = dict[pos++];
								var b1 = b >> 4;
								var b2 = b & 15;
								if (b1 === eof) {
									break;
								}
								str += lookup[b1];
								if (b2 === eof) {
									break;
								}
								str += lookup[b2];
							}
							return parseFloat(str);
						}
						var operands = [];
						var entries = [];
						pos = 0;
						var end = dict.length;
						while (pos < end) {
							var b = dict[pos];
							if (b <= 21) {
								if (b === 12) {
									b = b << 8 | dict[++pos];
								}
								entries.push([
									b,
									operands
								]);
								operands = [];
								++pos;
							} else {
								operands.push(parseOperand());
							}
						}
						return entries;
					},
					parseIndex: function CFFParser_parseIndex(pos) {
						var cffIndex = new CFFIndex();
						var bytes = this.bytes;
						var count = bytes[pos++] << 8 | bytes[pos++];
						var offsets = [];
						var end = pos;
						var i, ii;
						if (count !== 0) {
							var offsetSize = bytes[pos++];
							var startPos = pos + (count + 1) * offsetSize - 1;
							for (i = 0, ii = count + 1; i < ii; ++i) {
								var offset = 0;
								for (var j = 0; j < offsetSize; ++j) {
									offset <<= 8;
									offset += bytes[pos++];
								}
								offsets.push(startPos + offset);
							}
							end = offsets[count];
						}
						for (i = 0, ii = offsets.length - 1; i < ii; ++i) {
							var offsetStart = offsets[i];
							var offsetEnd = offsets[i + 1];
							cffIndex.add(bytes.subarray(offsetStart, offsetEnd));
						}
						return {
							obj: cffIndex,
							endPos: end
						};
					},
					parseNameIndex: function CFFParser_parseNameIndex(index) {
						var names = [];
						for (var i = 0, ii = index.count; i < ii; ++i) {
							var name = index.get(i);
							var length = Math.min(name.length, 127);
							var data = [];
							for (var j = 0; j < length; ++j) {
								var c = name[j];
								if (j === 0 && c === 0) {
									data[j] = c;
									continue;
								}
								if (c < 33 || c > 126 || c === 91 || c === 93 || c === 40 || c === 41 || c === 123 || c === 125 || c === 60 || c === 62 || c === 47 || c === 37 || c === 35) {
									data[j] = 95;
									continue;
								}
								data[j] = c;
							}
							names.push(bytesToString(data));
						}
						return names;
					},
					parseStringIndex: function CFFParser_parseStringIndex(index) {
						var strings = new CFFStrings();
						for (var i = 0, ii = index.count; i < ii; ++i) {
							var data = index.get(i);
							strings.add(bytesToString(data));
						}
						return strings;
					},
					createDict: function CFFParser_createDict(Type, dict, strings) {
						var cffDict = new Type(strings);
						for (var i = 0, ii = dict.length; i < ii; ++i) {
							var pair = dict[i];
							var key = pair[0];
							var value = pair[1];
							cffDict.setByKey(key, value);
						}
						return cffDict;
					},
					parseCharString: function CFFParser_parseCharString(state, data, localSubrIndex, globalSubrIndex) {
						if (state.callDepth > MAX_SUBR_NESTING) {
							return false;
						}
						var stackSize = state.stackSize;
						var stack = state.stack;
						var length = data.length;
						for (var j = 0; j < length;) {
							var value = data[j++];
							var validationCommand = null;
							if (value === 12) {
								var q = data[j++];
								if (q === 0) {
									data[j - 2] = 139;
									data[j - 1] = 22;
									stackSize = 0;
								} else {
									validationCommand = CharstringValidationData12[q];
								}
							} else if (value === 28) {
								stack[stackSize] = (data[j] << 24 | data[j + 1] << 16) >> 16;
								j += 2;
								stackSize++;
							} else if (value === 14) {
								if (stackSize >= 4) {
									stackSize -= 4;
									if (this.seacAnalysisEnabled) {
										state.seac = stack.slice(stackSize, stackSize + 4);
										return false;
									}
								}
								validationCommand = CharstringValidationData[value];
							} else if (value >= 32 && value <= 246) {
								stack[stackSize] = value - 139;
								stackSize++;
							} else if (value >= 247 && value <= 254) {
								stack[stackSize] = value < 251 ? (value - 247 << 8) + data[j] + 108 : -(value - 251 << 8) - data[j] - 108;
								j++;
								stackSize++;
							} else if (value === 255) {
								stack[stackSize] = (data[j] << 24 | data[j + 1] << 16 | data[j + 2] << 8 | data[j + 3]) / 65536;
								j += 4;
								stackSize++;
							} else if (value === 19 || value === 20) {
								state.hints += stackSize >> 1;
								j += state.hints + 7 >> 3;
								stackSize %= 2;
								validationCommand = CharstringValidationData[value];
							} else if (value === 10 || value === 29) {
								var subrsIndex;
								if (value === 10) {
									subrsIndex = localSubrIndex;
								} else {
									subrsIndex = globalSubrIndex;
								}
								if (!subrsIndex) {
									validationCommand = CharstringValidationData[value];
									warn('Missing subrsIndex for ' + validationCommand.id);
									return false;
								}
								var bias = 32768;
								if (subrsIndex.count < 1240) {
									bias = 107;
								} else if (subrsIndex.count < 33900) {
									bias = 1131;
								}
								var subrNumber = stack[--stackSize] + bias;
								if (subrNumber < 0 || subrNumber >= subrsIndex.count) {
									validationCommand = CharstringValidationData[value];
									warn('Out of bounds subrIndex for ' + validationCommand.id);
									return false;
								}
								state.stackSize = stackSize;
								state.callDepth++;
								var valid = this.parseCharString(state, subrsIndex.get(subrNumber), localSubrIndex, globalSubrIndex);
								if (!valid) {
									return false;
								}
								state.callDepth--;
								stackSize = state.stackSize;
								continue;
							} else if (value === 11) {
								state.stackSize = stackSize;
								return true;
							} else {
								validationCommand = CharstringValidationData[value];
							}
							if (validationCommand) {
								if (validationCommand.stem) {
									state.hints += stackSize >> 1;
								}
								if ('min' in validationCommand) {
									if (!state.undefStack && stackSize < validationCommand.min) {
										warn('Not enough parameters for ' + validationCommand.id + '; actual: ' + stackSize + ', expected: ' + validationCommand.min);
										return false;
									}
								}
								if (state.firstStackClearing && validationCommand.stackClearing) {
									state.firstStackClearing = false;
									stackSize -= validationCommand.min;
									if (stackSize >= 2 && validationCommand.stem) {
										stackSize %= 2;
									} else if (stackSize > 1) {
										warn('Found too many parameters for stack-clearing command');
									}
									if (stackSize > 0 && stack[stackSize - 1] >= 0) {
										state.width = stack[stackSize - 1];
									}
								}
								if ('stackDelta' in validationCommand) {
									if ('stackFn' in validationCommand) {
										validationCommand.stackFn(stack, stackSize);
									}
									stackSize += validationCommand.stackDelta;
								} else if (validationCommand.stackClearing) {
									stackSize = 0;
								} else if (validationCommand.resetStack) {
									stackSize = 0;
									state.undefStack = false;
								} else if (validationCommand.undefStack) {
									stackSize = 0;
									state.undefStack = true;
									state.firstStackClearing = false;
								}
							}
						}
						state.stackSize = stackSize;
						return true;
					},
					parseCharStrings: function CFFParser_parseCharStrings(charStrings, localSubrIndex, globalSubrIndex, fdSelect, fdArray) {
						var seacs = [];
						var widths = [];
						var count = charStrings.count;
						for (var i = 0; i < count; i++) {
							var charstring = charStrings.get(i);
							var state = {
								callDepth: 0,
								stackSize: 0,
								stack: [],
								undefStack: true,
								hints: 0,
								firstStackClearing: true,
								seac: null,
								width: null
							};
							var valid = true;
							var localSubrToUse = null;
							if (fdSelect && fdArray.length) {
								var fdIndex = fdSelect.getFDIndex(i);
								if (fdIndex === -1) {
									warn('Glyph index is not in fd select.');
									valid = false;
								}
								if (fdIndex >= fdArray.length) {
									warn('Invalid fd index for glyph index.');
									valid = false;
								}
								if (valid) {
									localSubrToUse = fdArray[fdIndex].privateDict.subrsIndex;
								}
							} else if (localSubrIndex) {
								localSubrToUse = localSubrIndex;
							}
							if (valid) {
								valid = this.parseCharString(state, charstring, localSubrToUse, globalSubrIndex);
							}
							if (state.width !== null) {
								widths[i] = state.width;
							}
							if (state.seac !== null) {
								seacs[i] = state.seac;
							}
							if (!valid) {
								charStrings.set(i, new Uint8Array([14]));
							}
						}
						return {
							charStrings: charStrings,
							seacs: seacs,
							widths: widths
						};
					},
					emptyPrivateDictionary: function CFFParser_emptyPrivateDictionary(parentDict) {
						var privateDict = this.createDict(CFFPrivateDict, [], parentDict.strings);
						parentDict.setByKey(18, [
							0,
							0
						]);
						parentDict.privateDict = privateDict;
					},
					parsePrivateDict: function CFFParser_parsePrivateDict(parentDict) {
						if (!parentDict.hasName('Private')) {
							this.emptyPrivateDictionary(parentDict);
							return;
						}
						var privateOffset = parentDict.getByName('Private');
						if (!isArray(privateOffset) || privateOffset.length !== 2) {
							parentDict.removeByName('Private');
							return;
						}
						var size = privateOffset[0];
						var offset = privateOffset[1];
						if (size === 0 || offset >= this.bytes.length) {
							this.emptyPrivateDictionary(parentDict);
							return;
						}
						var privateDictEnd = offset + size;
						var dictData = this.bytes.subarray(offset, privateDictEnd);
						var dict = this.parseDict(dictData);
						var privateDict = this.createDict(CFFPrivateDict, dict, parentDict.strings);
						parentDict.privateDict = privateDict;
						if (!privateDict.getByName('Subrs')) {
							return;
						}
						var subrsOffset = privateDict.getByName('Subrs');
						var relativeOffset = offset + subrsOffset;
						if (subrsOffset === 0 || relativeOffset >= this.bytes.length) {
							this.emptyPrivateDictionary(parentDict);
							return;
						}
						var subrsIndex = this.parseIndex(relativeOffset);
						privateDict.subrsIndex = subrsIndex.obj;
					},
					parseCharsets: function CFFParser_parseCharsets(pos, length, strings, cid) {
						if (pos === 0) {
							return new CFFCharset(true, CFFCharsetPredefinedTypes.ISO_ADOBE, ISOAdobeCharset);
						} else if (pos === 1) {
							return new CFFCharset(true, CFFCharsetPredefinedTypes.EXPERT, ExpertCharset);
						} else if (pos === 2) {
							return new CFFCharset(true, CFFCharsetPredefinedTypes.EXPERT_SUBSET, ExpertSubsetCharset);
						}
						var bytes = this.bytes;
						var start = pos;
						var format = bytes[pos++];
						var charset = ['.notdef'];
						var id, count, i;
						length -= 1;
						switch (format) {
							case 0:
								for (i = 0; i < length; i++) {
									id = bytes[pos++] << 8 | bytes[pos++];
									charset.push(cid ? id : strings.get(id));
								}
								break;
							case 1:
								while (charset.length <= length) {
									id = bytes[pos++] << 8 | bytes[pos++];
									count = bytes[pos++];
									for (i = 0; i <= count; i++) {
										charset.push(cid ? id++ : strings.get(id++));
									}
								}
								break;
							case 2:
								while (charset.length <= length) {
									id = bytes[pos++] << 8 | bytes[pos++];
									count = bytes[pos++] << 8 | bytes[pos++];
									for (i = 0; i <= count; i++) {
										charset.push(cid ? id++ : strings.get(id++));
									}
								}
								break;
							default:
								error('Unknown charset format');
						}
						var end = pos;
						var raw = bytes.subarray(start, end);
						return new CFFCharset(false, format, charset, raw);
					},
					parseEncoding: function CFFParser_parseEncoding(pos, properties, strings, charset) {
						var encoding = Object.create(null);
						var bytes = this.bytes;
						var predefined = false;
						var format, i, ii;
						var raw = null;
						function readSupplement() {
							var supplementsCount = bytes[pos++];
							for (i = 0; i < supplementsCount; i++) {
								var code = bytes[pos++];
								var sid = (bytes[pos++] << 8) + (bytes[pos++] & 0xff);
								encoding[code] = charset.indexOf(strings.get(sid));
							}
						}
						if (pos === 0 || pos === 1) {
							predefined = true;
							format = pos;
							var baseEncoding = pos ? ExpertEncoding : StandardEncoding;
							for (i = 0, ii = charset.length; i < ii; i++) {
								var index = baseEncoding.indexOf(charset[i]);
								if (index !== -1) {
									encoding[index] = i;
								}
							}
						} else {
							var dataStart = pos;
							format = bytes[pos++];
							switch (format & 0x7f) {
								case 0:
									var glyphsCount = bytes[pos++];
									for (i = 1; i <= glyphsCount; i++) {
										encoding[bytes[pos++]] = i;
									}
									break;
								case 1:
									var rangesCount = bytes[pos++];
									var gid = 1;
									for (i = 0; i < rangesCount; i++) {
										var start = bytes[pos++];
										var left = bytes[pos++];
										for (var j = start; j <= start + left; j++) {
											encoding[j] = gid++;
										}
									}
									break;
								default:
									error('Unknown encoding format: ' + format + ' in CFF');
									break;
							}
							var dataEnd = pos;
							if (format & 0x80) {
								bytes[dataStart] &= 0x7f;
								readSupplement();
							}
							raw = bytes.subarray(dataStart, dataEnd);
						}
						format = format & 0x7f;
						return new CFFEncoding(predefined, format, encoding, raw);
					},
					parseFDSelect: function CFFParser_parseFDSelect(pos, length) {
						var start = pos;
						var bytes = this.bytes;
						var format = bytes[pos++];
						var fdSelect = [], rawBytes;
						var i, invalidFirstGID = false;
						switch (format) {
							case 0:
								for (i = 0; i < length; ++i) {
									var id = bytes[pos++];
									fdSelect.push(id);
								}
								rawBytes = bytes.subarray(start, pos);
								break;
							case 3:
								var rangesCount = bytes[pos++] << 8 | bytes[pos++];
								for (i = 0; i < rangesCount; ++i) {
									var first = bytes[pos++] << 8 | bytes[pos++];
									if (i === 0 && first !== 0) {
										warn('parseFDSelect: The first range must have a first GID of 0' + ' -- trying to recover.');
										invalidFirstGID = true;
										first = 0;
									}
									var fdIndex = bytes[pos++];
									var next = bytes[pos] << 8 | bytes[pos + 1];
									for (var j = first; j < next; ++j) {
										fdSelect.push(fdIndex);
									}
								}
								pos += 2;
								rawBytes = bytes.subarray(start, pos);
								if (invalidFirstGID) {
									rawBytes[3] = rawBytes[4] = 0;
								}
								break;
							default:
								error('parseFDSelect: Unknown format "' + format + '".');
								break;
						}
						assert(fdSelect.length === length, 'parseFDSelect: Invalid font data.');
						return new CFFFDSelect(fdSelect, rawBytes);
					}
				};
				return CFFParser;
			}();
			var CFF = function CFFClosure() {
				function CFF() {
					this.header = null;
					this.names = [];
					this.topDict = null;
					this.strings = new CFFStrings();
					this.globalSubrIndex = null;
					this.encoding = null;
					this.charset = null;
					this.charStrings = null;
					this.fdArray = [];
					this.fdSelect = null;
					this.isCIDFont = false;
				}
				return CFF;
			}();
			var CFFHeader = function CFFHeaderClosure() {
				function CFFHeader(major, minor, hdrSize, offSize) {
					this.major = major;
					this.minor = minor;
					this.hdrSize = hdrSize;
					this.offSize = offSize;
				}
				return CFFHeader;
			}();
			var CFFStrings = function CFFStringsClosure() {
				function CFFStrings() {
					this.strings = [];
				}
				CFFStrings.prototype = {
					get: function CFFStrings_get(index) {
						if (index >= 0 && index <= 390) {
							return CFFStandardStrings[index];
						}
						if (index - 391 <= this.strings.length) {
							return this.strings[index - 391];
						}
						return CFFStandardStrings[0];
					},
					add: function CFFStrings_add(value) {
						this.strings.push(value);
					},
					get count() {
						return this.strings.length;
					}
				};
				return CFFStrings;
			}();
			var CFFIndex = function CFFIndexClosure() {
				function CFFIndex() {
					this.objects = [];
					this.length = 0;
				}
				CFFIndex.prototype = {
					add: function CFFIndex_add(data) {
						this.length += data.length;
						this.objects.push(data);
					},
					set: function CFFIndex_set(index, data) {
						this.length += data.length - this.objects[index].length;
						this.objects[index] = data;
					},
					get: function CFFIndex_get(index) {
						return this.objects[index];
					},
					get count() {
						return this.objects.length;
					}
				};
				return CFFIndex;
			}();
			var CFFDict = function CFFDictClosure() {
				function CFFDict(tables, strings) {
					this.keyToNameMap = tables.keyToNameMap;
					this.nameToKeyMap = tables.nameToKeyMap;
					this.defaults = tables.defaults;
					this.types = tables.types;
					this.opcodes = tables.opcodes;
					this.order = tables.order;
					this.strings = strings;
					this.values = Object.create(null);
				}
				CFFDict.prototype = {
					setByKey: function CFFDict_setByKey(key, value) {
						if (!(key in this.keyToNameMap)) {
							return false;
						}
						var valueLength = value.length;
						if (valueLength === 0) {
							return true;
						}
						for (var i = 0; i < valueLength; i++) {
							if (isNaN(value[i])) {
								warn('Invalid CFFDict value: "' + value + '" for key "' + key + '".');
								return true;
							}
						}
						var type = this.types[key];
						if (type === 'num' || type === 'sid' || type === 'offset') {
							value = value[0];
						}
						this.values[key] = value;
						return true;
					},
					setByName: function CFFDict_setByName(name, value) {
						if (!(name in this.nameToKeyMap)) {
							error('Invalid dictionary name "' + name + '"');
						}
						this.values[this.nameToKeyMap[name]] = value;
					},
					hasName: function CFFDict_hasName(name) {
						return this.nameToKeyMap[name] in this.values;
					},
					getByName: function CFFDict_getByName(name) {
						if (!(name in this.nameToKeyMap)) {
							error('Invalid dictionary name "' + name + '"');
						}
						var key = this.nameToKeyMap[name];
						if (!(key in this.values)) {
							return this.defaults[key];
						}
						return this.values[key];
					},
					removeByName: function CFFDict_removeByName(name) {
						delete this.values[this.nameToKeyMap[name]];
					}
				};
				CFFDict.createTables = function CFFDict_createTables(layout) {
					var tables = {
						keyToNameMap: {},
						nameToKeyMap: {},
						defaults: {},
						types: {},
						opcodes: {},
						order: []
					};
					for (var i = 0, ii = layout.length; i < ii; ++i) {
						var entry = layout[i];
						var key = isArray(entry[0]) ? (entry[0][0] << 8) + entry[0][1] : entry[0];
						tables.keyToNameMap[key] = entry[1];
						tables.nameToKeyMap[entry[1]] = key;
						tables.types[key] = entry[2];
						tables.defaults[key] = entry[3];
						tables.opcodes[key] = isArray(entry[0]) ? entry[0] : [entry[0]];
						tables.order.push(key);
					}
					return tables;
				};
				return CFFDict;
			}();
			var CFFTopDict = function CFFTopDictClosure() {
				var layout = [
					[
						[
							12,
							30
						],
						'ROS',
						[
							'sid',
							'sid',
							'num'
						],
						null
					],
					[
						[
							12,
							20
						],
						'SyntheticBase',
						'num',
						null
					],
					[
						0,
						'version',
						'sid',
						null
					],
					[
						1,
						'Notice',
						'sid',
						null
					],
					[
						[
							12,
							0
						],
						'Copyright',
						'sid',
						null
					],
					[
						2,
						'FullName',
						'sid',
						null
					],
					[
						3,
						'FamilyName',
						'sid',
						null
					],
					[
						4,
						'Weight',
						'sid',
						null
					],
					[
						[
							12,
							1
						],
						'isFixedPitch',
						'num',
						0
					],
					[
						[
							12,
							2
						],
						'ItalicAngle',
						'num',
						0
					],
					[
						[
							12,
							3
						],
						'UnderlinePosition',
						'num',
						-100
					],
					[
						[
							12,
							4
						],
						'UnderlineThickness',
						'num',
						50
					],
					[
						[
							12,
							5
						],
						'PaintType',
						'num',
						0
					],
					[
						[
							12,
							6
						],
						'CharstringType',
						'num',
						2
					],
					[
						[
							12,
							7
						],
						'FontMatrix',
						[
							'num',
							'num',
							'num',
							'num',
							'num',
							'num'
						],
						[
							0.001,
							0,
							0,
							0.001,
							0,
							0
						]
					],
					[
						13,
						'UniqueID',
						'num',
						null
					],
					[
						5,
						'FontBBox',
						[
							'num',
							'num',
							'num',
							'num'
						],
						[
							0,
							0,
							0,
							0
						]
					],
					[
						[
							12,
							8
						],
						'StrokeWidth',
						'num',
						0
					],
					[
						14,
						'XUID',
						'array',
						null
					],
					[
						15,
						'charset',
						'offset',
						0
					],
					[
						16,
						'Encoding',
						'offset',
						0
					],
					[
						17,
						'CharStrings',
						'offset',
						0
					],
					[
						18,
						'Private',
						[
							'offset',
							'offset'
						],
						null
					],
					[
						[
							12,
							21
						],
						'PostScript',
						'sid',
						null
					],
					[
						[
							12,
							22
						],
						'BaseFontName',
						'sid',
						null
					],
					[
						[
							12,
							23
						],
						'BaseFontBlend',
						'delta',
						null
					],
					[
						[
							12,
							31
						],
						'CIDFontVersion',
						'num',
						0
					],
					[
						[
							12,
							32
						],
						'CIDFontRevision',
						'num',
						0
					],
					[
						[
							12,
							33
						],
						'CIDFontType',
						'num',
						0
					],
					[
						[
							12,
							34
						],
						'CIDCount',
						'num',
						8720
					],
					[
						[
							12,
							35
						],
						'UIDBase',
						'num',
						null
					],
					[
						[
							12,
							37
						],
						'FDSelect',
						'offset',
						null
					],
					[
						[
							12,
							36
						],
						'FDArray',
						'offset',
						null
					],
					[
						[
							12,
							38
						],
						'FontName',
						'sid',
						null
					]
				];
				var tables = null;
				function CFFTopDict(strings) {
					if (tables === null) {
						tables = CFFDict.createTables(layout);
					}
					CFFDict.call(this, tables, strings);
					this.privateDict = null;
				}
				CFFTopDict.prototype = Object.create(CFFDict.prototype);
				return CFFTopDict;
			}();
			var CFFPrivateDict = function CFFPrivateDictClosure() {
				var layout = [
					[
						6,
						'BlueValues',
						'delta',
						null
					],
					[
						7,
						'OtherBlues',
						'delta',
						null
					],
					[
						8,
						'FamilyBlues',
						'delta',
						null
					],
					[
						9,
						'FamilyOtherBlues',
						'delta',
						null
					],
					[
						[
							12,
							9
						],
						'BlueScale',
						'num',
						0.039625
					],
					[
						[
							12,
							10
						],
						'BlueShift',
						'num',
						7
					],
					[
						[
							12,
							11
						],
						'BlueFuzz',
						'num',
						1
					],
					[
						10,
						'StdHW',
						'num',
						null
					],
					[
						11,
						'StdVW',
						'num',
						null
					],
					[
						[
							12,
							12
						],
						'StemSnapH',
						'delta',
						null
					],
					[
						[
							12,
							13
						],
						'StemSnapV',
						'delta',
						null
					],
					[
						[
							12,
							14
						],
						'ForceBold',
						'num',
						0
					],
					[
						[
							12,
							17
						],
						'LanguageGroup',
						'num',
						0
					],
					[
						[
							12,
							18
						],
						'ExpansionFactor',
						'num',
						0.06
					],
					[
						[
							12,
							19
						],
						'initialRandomSeed',
						'num',
						0
					],
					[
						20,
						'defaultWidthX',
						'num',
						0
					],
					[
						21,
						'nominalWidthX',
						'num',
						0
					],
					[
						19,
						'Subrs',
						'offset',
						null
					]
				];
				var tables = null;
				function CFFPrivateDict(strings) {
					if (tables === null) {
						tables = CFFDict.createTables(layout);
					}
					CFFDict.call(this, tables, strings);
					this.subrsIndex = null;
				}
				CFFPrivateDict.prototype = Object.create(CFFDict.prototype);
				return CFFPrivateDict;
			}();
			var CFFCharsetPredefinedTypes = {
				ISO_ADOBE: 0,
				EXPERT: 1,
				EXPERT_SUBSET: 2
			};
			var CFFCharset = function CFFCharsetClosure() {
				function CFFCharset(predefined, format, charset, raw) {
					this.predefined = predefined;
					this.format = format;
					this.charset = charset;
					this.raw = raw;
				}
				return CFFCharset;
			}();
			var CFFEncoding = function CFFEncodingClosure() {
				function CFFEncoding(predefined, format, encoding, raw) {
					this.predefined = predefined;
					this.format = format;
					this.encoding = encoding;
					this.raw = raw;
				}
				return CFFEncoding;
			}();
			var CFFFDSelect = function CFFFDSelectClosure() {
				function CFFFDSelect(fdSelect, raw) {
					this.fdSelect = fdSelect;
					this.raw = raw;
				}
				CFFFDSelect.prototype = {
					getFDIndex: function CFFFDSelect_get(glyphIndex) {
						if (glyphIndex < 0 || glyphIndex >= this.fdSelect.length) {
							return -1;
						}
						return this.fdSelect[glyphIndex];
					}
				};
				return CFFFDSelect;
			}();
			var CFFOffsetTracker = function CFFOffsetTrackerClosure() {
				function CFFOffsetTracker() {
					this.offsets = Object.create(null);
				}
				CFFOffsetTracker.prototype = {
					isTracking: function CFFOffsetTracker_isTracking(key) {
						return key in this.offsets;
					},
					track: function CFFOffsetTracker_track(key, location) {
						if (key in this.offsets) {
							error('Already tracking location of ' + key);
						}
						this.offsets[key] = location;
					},
					offset: function CFFOffsetTracker_offset(value) {
						for (var key in this.offsets) {
							this.offsets[key] += value;
						}
					},
					setEntryLocation: function CFFOffsetTracker_setEntryLocation(key, values, output) {
						if (!(key in this.offsets)) {
							error('Not tracking location of ' + key);
						}
						var data = output.data;
						var dataOffset = this.offsets[key];
						var size = 5;
						for (var i = 0, ii = values.length; i < ii; ++i) {
							var offset0 = i * size + dataOffset;
							var offset1 = offset0 + 1;
							var offset2 = offset0 + 2;
							var offset3 = offset0 + 3;
							var offset4 = offset0 + 4;
							if (data[offset0] !== 0x1d || data[offset1] !== 0 || data[offset2] !== 0 || data[offset3] !== 0 || data[offset4] !== 0) {
								error('writing to an offset that is not empty');
							}
							var value = values[i];
							data[offset0] = 0x1d;
							data[offset1] = value >> 24 & 0xFF;
							data[offset2] = value >> 16 & 0xFF;
							data[offset3] = value >> 8 & 0xFF;
							data[offset4] = value & 0xFF;
						}
					}
				};
				return CFFOffsetTracker;
			}();
			var CFFCompiler = function CFFCompilerClosure() {
				function CFFCompiler(cff) {
					this.cff = cff;
				}
				CFFCompiler.prototype = {
					compile: function CFFCompiler_compile() {
						var cff = this.cff;
						var output = {
							data: [],
							length: 0,
							add: function CFFCompiler_add(data) {
								this.data = this.data.concat(data);
								this.length = this.data.length;
							}
						};
						var header = this.compileHeader(cff.header);
						output.add(header);
						var nameIndex = this.compileNameIndex(cff.names);
						output.add(nameIndex);
						if (cff.isCIDFont) {
							if (cff.topDict.hasName('FontMatrix')) {
								var base = cff.topDict.getByName('FontMatrix');
								cff.topDict.removeByName('FontMatrix');
								for (var i = 0, ii = cff.fdArray.length; i < ii; i++) {
									var subDict = cff.fdArray[i];
									var matrix = base.slice(0);
									if (subDict.hasName('FontMatrix')) {
										matrix = Util.transform(matrix, subDict.getByName('FontMatrix'));
									}
									subDict.setByName('FontMatrix', matrix);
								}
							}
						}
						var compiled = this.compileTopDicts([cff.topDict], output.length, cff.isCIDFont);
						output.add(compiled.output);
						var topDictTracker = compiled.trackers[0];
						var stringIndex = this.compileStringIndex(cff.strings.strings);
						output.add(stringIndex);
						var globalSubrIndex = this.compileIndex(cff.globalSubrIndex);
						output.add(globalSubrIndex);
						if (cff.encoding && cff.topDict.hasName('Encoding')) {
							if (cff.encoding.predefined) {
								topDictTracker.setEntryLocation('Encoding', [cff.encoding.format], output);
							} else {
								var encoding = this.compileEncoding(cff.encoding);
								topDictTracker.setEntryLocation('Encoding', [output.length], output);
								output.add(encoding);
							}
						}
						if (cff.charset && cff.topDict.hasName('charset')) {
							if (cff.charset.predefined) {
								topDictTracker.setEntryLocation('charset', [cff.charset.format], output);
							} else {
								var charset = this.compileCharset(cff.charset);
								topDictTracker.setEntryLocation('charset', [output.length], output);
								output.add(charset);
							}
						}
						var charStrings = this.compileCharStrings(cff.charStrings);
						topDictTracker.setEntryLocation('CharStrings', [output.length], output);
						output.add(charStrings);
						if (cff.isCIDFont) {
							topDictTracker.setEntryLocation('FDSelect', [output.length], output);
							var fdSelect = this.compileFDSelect(cff.fdSelect.raw);
							output.add(fdSelect);
							compiled = this.compileTopDicts(cff.fdArray, output.length, true);
							topDictTracker.setEntryLocation('FDArray', [output.length], output);
							output.add(compiled.output);
							var fontDictTrackers = compiled.trackers;
							this.compilePrivateDicts(cff.fdArray, fontDictTrackers, output);
						}
						this.compilePrivateDicts([cff.topDict], [topDictTracker], output);
						output.add([0]);
						return output.data;
					},
					encodeNumber: function CFFCompiler_encodeNumber(value) {
						if (parseFloat(value) === parseInt(value, 10) && !isNaN(value)) {
							return this.encodeInteger(value);
						}
						return this.encodeFloat(value);
					},
					encodeFloat: function CFFCompiler_encodeFloat(num) {
						var value = num.toString();
						var m = /\.(\d*?)(?:9{5,20}|0{5,20})\d{0,2}(?:e(.+)|$)/.exec(value);
						if (m) {
							var epsilon = parseFloat('1e' + ((m[2] ? +m[2] : 0) + m[1].length));
							value = (Math.round(num * epsilon) / epsilon).toString();
						}
						var nibbles = '';
						var i, ii;
						for (i = 0, ii = value.length; i < ii; ++i) {
							var a = value[i];
							if (a === 'e') {
								nibbles += value[++i] === '-' ? 'c' : 'b';
							} else if (a === '.') {
								nibbles += 'a';
							} else if (a === '-') {
								nibbles += 'e';
							} else {
								nibbles += a;
							}
						}
						nibbles += nibbles.length & 1 ? 'f' : 'ff';
						var out = [30];
						for (i = 0, ii = nibbles.length; i < ii; i += 2) {
							out.push(parseInt(nibbles.substr(i, 2), 16));
						}
						return out;
					},
					encodeInteger: function CFFCompiler_encodeInteger(value) {
						var code;
						if (value >= -107 && value <= 107) {
							code = [value + 139];
						} else if (value >= 108 && value <= 1131) {
							value = value - 108;
							code = [
								(value >> 8) + 247,
								value & 0xFF
							];
						} else if (value >= -1131 && value <= -108) {
							value = -value - 108;
							code = [
								(value >> 8) + 251,
								value & 0xFF
							];
						} else if (value >= -32768 && value <= 32767) {
							code = [
								0x1c,
								value >> 8 & 0xFF,
								value & 0xFF
							];
						} else {
							code = [
								0x1d,
								value >> 24 & 0xFF,
								value >> 16 & 0xFF,
								value >> 8 & 0xFF,
								value & 0xFF
							];
						}
						return code;
					},
					compileHeader: function CFFCompiler_compileHeader(header) {
						return [
							header.major,
							header.minor,
							header.hdrSize,
							header.offSize
						];
					},
					compileNameIndex: function CFFCompiler_compileNameIndex(names) {
						var nameIndex = new CFFIndex();
						for (var i = 0, ii = names.length; i < ii; ++i) {
							nameIndex.add(stringToBytes(names[i]));
						}
						return this.compileIndex(nameIndex);
					},
					compileTopDicts: function CFFCompiler_compileTopDicts(dicts, length, removeCidKeys) {
						var fontDictTrackers = [];
						var fdArrayIndex = new CFFIndex();
						for (var i = 0, ii = dicts.length; i < ii; ++i) {
							var fontDict = dicts[i];
							if (removeCidKeys) {
								fontDict.removeByName('CIDFontVersion');
								fontDict.removeByName('CIDFontRevision');
								fontDict.removeByName('CIDFontType');
								fontDict.removeByName('CIDCount');
								fontDict.removeByName('UIDBase');
							}
							var fontDictTracker = new CFFOffsetTracker();
							var fontDictData = this.compileDict(fontDict, fontDictTracker);
							fontDictTrackers.push(fontDictTracker);
							fdArrayIndex.add(fontDictData);
							fontDictTracker.offset(length);
						}
						fdArrayIndex = this.compileIndex(fdArrayIndex, fontDictTrackers);
						return {
							trackers: fontDictTrackers,
							output: fdArrayIndex
						};
					},
					compilePrivateDicts: function CFFCompiler_compilePrivateDicts(dicts, trackers, output) {
						for (var i = 0, ii = dicts.length; i < ii; ++i) {
							var fontDict = dicts[i];
							assert(fontDict.privateDict && fontDict.hasName('Private'), 'There must be an private dictionary.');
							var privateDict = fontDict.privateDict;
							var privateDictTracker = new CFFOffsetTracker();
							var privateDictData = this.compileDict(privateDict, privateDictTracker);
							var outputLength = output.length;
							privateDictTracker.offset(outputLength);
							if (!privateDictData.length) {
								outputLength = 0;
							}
							trackers[i].setEntryLocation('Private', [
								privateDictData.length,
								outputLength
							], output);
							output.add(privateDictData);
							if (privateDict.subrsIndex && privateDict.hasName('Subrs')) {
								var subrs = this.compileIndex(privateDict.subrsIndex);
								privateDictTracker.setEntryLocation('Subrs', [privateDictData.length], output);
								output.add(subrs);
							}
						}
					},
					compileDict: function CFFCompiler_compileDict(dict, offsetTracker) {
						var out = [];
						var order = dict.order;
						for (var i = 0; i < order.length; ++i) {
							var key = order[i];
							if (!(key in dict.values)) {
								continue;
							}
							var values = dict.values[key];
							var types = dict.types[key];
							if (!isArray(types)) {
								types = [types];
							}
							if (!isArray(values)) {
								values = [values];
							}
							if (values.length === 0) {
								continue;
							}
							for (var j = 0, jj = types.length; j < jj; ++j) {
								var type = types[j];
								var value = values[j];
								switch (type) {
									case 'num':
									case 'sid':
										out = out.concat(this.encodeNumber(value));
										break;
									case 'offset':
										var name = dict.keyToNameMap[key];
										if (!offsetTracker.isTracking(name)) {
											offsetTracker.track(name, out.length);
										}
										out = out.concat([
											0x1d,
											0,
											0,
											0,
											0
										]);
										break;
									case 'array':
									case 'delta':
										out = out.concat(this.encodeNumber(value));
										for (var k = 1, kk = values.length; k < kk; ++k) {
											out = out.concat(this.encodeNumber(values[k]));
										}
										break;
									default:
										error('Unknown data type of ' + type);
										break;
								}
							}
							out = out.concat(dict.opcodes[key]);
						}
						return out;
					},
					compileStringIndex: function CFFCompiler_compileStringIndex(strings) {
						var stringIndex = new CFFIndex();
						for (var i = 0, ii = strings.length; i < ii; ++i) {
							stringIndex.add(stringToBytes(strings[i]));
						}
						return this.compileIndex(stringIndex);
					},
					compileGlobalSubrIndex: function CFFCompiler_compileGlobalSubrIndex() {
						var globalSubrIndex = this.cff.globalSubrIndex;
						this.out.writeByteArray(this.compileIndex(globalSubrIndex));
					},
					compileCharStrings: function CFFCompiler_compileCharStrings(charStrings) {
						return this.compileIndex(charStrings);
					},
					compileCharset: function CFFCompiler_compileCharset(charset) {
						return this.compileTypedArray(charset.raw);
					},
					compileEncoding: function CFFCompiler_compileEncoding(encoding) {
						return this.compileTypedArray(encoding.raw);
					},
					compileFDSelect: function CFFCompiler_compileFDSelect(fdSelect) {
						return this.compileTypedArray(fdSelect);
					},
					compileTypedArray: function CFFCompiler_compileTypedArray(data) {
						var out = [];
						for (var i = 0, ii = data.length; i < ii; ++i) {
							out[i] = data[i];
						}
						return out;
					},
					compileIndex: function CFFCompiler_compileIndex(index, trackers) {
						trackers = trackers || [];
						var objects = index.objects;
						var count = objects.length;
						if (count === 0) {
							return [
								0,
								0,
								0
							];
						}
						var data = [
							count >> 8 & 0xFF,
							count & 0xff
						];
						var lastOffset = 1, i;
						for (i = 0; i < count; ++i) {
							lastOffset += objects[i].length;
						}
						var offsetSize;
						if (lastOffset < 0x100) {
							offsetSize = 1;
						} else if (lastOffset < 0x10000) {
							offsetSize = 2;
						} else if (lastOffset < 0x1000000) {
							offsetSize = 3;
						} else {
							offsetSize = 4;
						}
						data.push(offsetSize);
						var relativeOffset = 1;
						for (i = 0; i < count + 1; i++) {
							if (offsetSize === 1) {
								data.push(relativeOffset & 0xFF);
							} else if (offsetSize === 2) {
								data.push(relativeOffset >> 8 & 0xFF, relativeOffset & 0xFF);
							} else if (offsetSize === 3) {
								data.push(relativeOffset >> 16 & 0xFF, relativeOffset >> 8 & 0xFF, relativeOffset & 0xFF);
							} else {
								data.push(relativeOffset >>> 24 & 0xFF, relativeOffset >> 16 & 0xFF, relativeOffset >> 8 & 0xFF, relativeOffset & 0xFF);
							}
							if (objects[i]) {
								relativeOffset += objects[i].length;
							}
						}
						for (i = 0; i < count; i++) {
							if (trackers[i]) {
								trackers[i].offset(data.length);
							}
							for (var j = 0, jj = objects[i].length; j < jj; j++) {
								data.push(objects[i][j]);
							}
						}
						return data;
					}
				};
				return CFFCompiler;
			}();
			exports.CFFStandardStrings = CFFStandardStrings;
			exports.CFFParser = CFFParser;
			exports.CFF = CFF;
			exports.CFFHeader = CFFHeader;
			exports.CFFStrings = CFFStrings;
			exports.CFFIndex = CFFIndex;
			exports.CFFCharset = CFFCharset;
			exports.CFFTopDict = CFFTopDict;
			exports.CFFPrivateDict = CFFPrivateDict;
			exports.CFFCompiler = CFFCompiler;

			/***/
		}),
/* 12 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var MissingDataException = sharedUtil.MissingDataException;
			var arrayByteLength = sharedUtil.arrayByteLength;
			var arraysToBytes = sharedUtil.arraysToBytes;
			var assert = sharedUtil.assert;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var isInt = sharedUtil.isInt;
			var isEmptyObj = sharedUtil.isEmptyObj;
			var ChunkedStream = function ChunkedStreamClosure() {
				function ChunkedStream(length, chunkSize, manager) {
					this.bytes = new Uint8Array(length);
					this.start = 0;
					this.pos = 0;
					this.end = length;
					this.chunkSize = chunkSize;
					this.loadedChunks = [];
					this.numChunksLoaded = 0;
					this.numChunks = Math.ceil(length / chunkSize);
					this.manager = manager;
					this.progressiveDataLength = 0;
					this.lastSuccessfulEnsureByteChunk = -1;
				}
				ChunkedStream.prototype = {
					getMissingChunks: function ChunkedStream_getMissingChunks() {
						var chunks = [];
						for (var chunk = 0, n = this.numChunks; chunk < n; ++chunk) {
							if (!this.loadedChunks[chunk]) {
								chunks.push(chunk);
							}
						}
						return chunks;
					},
					getBaseStreams: function ChunkedStream_getBaseStreams() {
						return [this];
					},
					allChunksLoaded: function ChunkedStream_allChunksLoaded() {
						return this.numChunksLoaded === this.numChunks;
					},
					onReceiveData: function ChunkedStream_onReceiveData(begin, chunk) {
						var end = begin + chunk.byteLength;
						assert(begin % this.chunkSize === 0, 'Bad begin offset: ' + begin);
						var length = this.bytes.length;
						assert(end % this.chunkSize === 0 || end === length, 'Bad end offset: ' + end);
						this.bytes.set(new Uint8Array(chunk), begin);
						var chunkSize = this.chunkSize;
						var beginChunk = Math.floor(begin / chunkSize);
						var endChunk = Math.floor((end - 1) / chunkSize) + 1;
						var curChunk;
						for (curChunk = beginChunk; curChunk < endChunk; ++curChunk) {
							if (!this.loadedChunks[curChunk]) {
								this.loadedChunks[curChunk] = true;
								++this.numChunksLoaded;
							}
						}
					},
					onReceiveProgressiveData: function ChunkedStream_onReceiveProgressiveData(data) {
						var position = this.progressiveDataLength;
						var beginChunk = Math.floor(position / this.chunkSize);
						this.bytes.set(new Uint8Array(data), position);
						position += data.byteLength;
						this.progressiveDataLength = position;
						var endChunk = position >= this.end ? this.numChunks : Math.floor(position / this.chunkSize);
						var curChunk;
						for (curChunk = beginChunk; curChunk < endChunk; ++curChunk) {
							if (!this.loadedChunks[curChunk]) {
								this.loadedChunks[curChunk] = true;
								++this.numChunksLoaded;
							}
						}
					},
					ensureByte: function ChunkedStream_ensureByte(pos) {
						var chunk = Math.floor(pos / this.chunkSize);
						if (chunk === this.lastSuccessfulEnsureByteChunk) {
							return;
						}
						if (!this.loadedChunks[chunk]) {
							throw new MissingDataException(pos, pos + 1);
						}
						this.lastSuccessfulEnsureByteChunk = chunk;
					},
					ensureRange: function ChunkedStream_ensureRange(begin, end) {
						if (begin >= end) {
							return;
						}
						if (end <= this.progressiveDataLength) {
							return;
						}
						var chunkSize = this.chunkSize;
						var beginChunk = Math.floor(begin / chunkSize);
						var endChunk = Math.floor((end - 1) / chunkSize) + 1;
						for (var chunk = beginChunk; chunk < endChunk; ++chunk) {
							if (!this.loadedChunks[chunk]) {
								throw new MissingDataException(begin, end);
							}
						}
					},
					nextEmptyChunk: function ChunkedStream_nextEmptyChunk(beginChunk) {
						var chunk, numChunks = this.numChunks;
						for (var i = 0; i < numChunks; ++i) {
							chunk = (beginChunk + i) % numChunks;
							if (!this.loadedChunks[chunk]) {
								return chunk;
							}
						}
						return null;
					},
					hasChunk: function ChunkedStream_hasChunk(chunk) {
						return !!this.loadedChunks[chunk];
					},
					get length() {
						return this.end - this.start;
					},
					get isEmpty() {
						return this.length === 0;
					},
					getByte: function ChunkedStream_getByte() {
						var pos = this.pos;
						if (pos >= this.end) {
							return -1;
						}
						this.ensureByte(pos);
						return this.bytes[this.pos++];
					},
					getUint16: function ChunkedStream_getUint16() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						if (b0 === -1 || b1 === -1) {
							return -1;
						}
						return (b0 << 8) + b1;
					},
					getInt32: function ChunkedStream_getInt32() {
						var b0 = this.getByte();
						var b1 = this.getByte();
						var b2 = this.getByte();
						var b3 = this.getByte();
						return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
					},
					getBytes: function ChunkedStream_getBytes(length) {
						var bytes = this.bytes;
						var pos = this.pos;
						var strEnd = this.end;
						if (!length) {
							this.ensureRange(pos, strEnd);
							return bytes.subarray(pos, strEnd);
						}
						var end = pos + length;
						if (end > strEnd) {
							end = strEnd;
						}
						this.ensureRange(pos, end);
						this.pos = end;
						return bytes.subarray(pos, end);
					},
					peekByte: function ChunkedStream_peekByte() {
						var peekedByte = this.getByte();
						this.pos--;
						return peekedByte;
					},
					peekBytes: function ChunkedStream_peekBytes(length) {
						var bytes = this.getBytes(length);
						this.pos -= bytes.length;
						return bytes;
					},
					getByteRange: function ChunkedStream_getBytes(begin, end) {
						this.ensureRange(begin, end);
						return this.bytes.subarray(begin, end);
					},
					skip: function ChunkedStream_skip(n) {
						if (!n) {
							n = 1;
						}
						this.pos += n;
					},
					reset: function ChunkedStream_reset() {
						this.pos = this.start;
					},
					moveStart: function ChunkedStream_moveStart() {
						this.start = this.pos;
					},
					makeSubStream: function ChunkedStream_makeSubStream(start, length, dict) {
						this.ensureRange(start, start + length);
						function ChunkedStreamSubstream() {
						}
						ChunkedStreamSubstream.prototype = Object.create(this);
						ChunkedStreamSubstream.prototype.getMissingChunks = function () {
							var chunkSize = this.chunkSize;
							var beginChunk = Math.floor(this.start / chunkSize);
							var endChunk = Math.floor((this.end - 1) / chunkSize) + 1;
							var missingChunks = [];
							for (var chunk = beginChunk; chunk < endChunk; ++chunk) {
								if (!this.loadedChunks[chunk]) {
									missingChunks.push(chunk);
								}
							}
							return missingChunks;
						};
						var subStream = new ChunkedStreamSubstream();
						subStream.pos = subStream.start = start;
						subStream.end = start + length || this.end;
						subStream.dict = dict;
						return subStream;
					}
				};
				return ChunkedStream;
			}();
			var ChunkedStreamManager = function ChunkedStreamManagerClosure() {
				function ChunkedStreamManager(pdfNetworkStream, args) {
					var chunkSize = args.rangeChunkSize;
					var length = args.length;
					this.stream = new ChunkedStream(length, chunkSize, this);
					this.length = length;
					this.chunkSize = chunkSize;
					this.pdfNetworkStream = pdfNetworkStream;
					this.url = args.url;
					this.disableAutoFetch = args.disableAutoFetch;
					this.msgHandler = args.msgHandler;
					this.currRequestId = 0;
					this.chunksNeededByRequest = Object.create(null);
					this.requestsByChunk = Object.create(null);
					this.promisesByRequest = Object.create(null);
					this.progressiveDataLength = 0;
					this.aborted = false;
					this._loadedStreamCapability = createPromiseCapability();
				}
				ChunkedStreamManager.prototype = {
					onLoadedStream: function ChunkedStreamManager_getLoadedStream() {
						return this._loadedStreamCapability.promise;
					},
					sendRequest: function ChunkedStreamManager_sendRequest(begin, end) {
						var rangeReader = this.pdfNetworkStream.getRangeReader(begin, end);
						if (!rangeReader.isStreamingSupported) {
							rangeReader.onProgress = this.onProgress.bind(this);
						}
						var chunks = [], loaded = 0;
						var manager = this;
						var promise = new Promise(function (resolve, reject) {
							var readChunk = function (chunk) {
								try {
									if (!chunk.done) {
										var data = chunk.value;
										chunks.push(data);
										loaded += arrayByteLength(data);
										if (rangeReader.isStreamingSupported) {
											manager.onProgress({ loaded: loaded });
										}
										rangeReader.read().then(readChunk, reject);
										return;
									}
									var chunkData = arraysToBytes(chunks);
									chunks = null;
									resolve(chunkData);
								} catch (e) {
									reject(e);
								}
							};
							rangeReader.read().then(readChunk, reject);
						});
						promise.then(function (data) {
							if (this.aborted) {
								return;
							}
							this.onReceiveData({
								chunk: data,
								begin: begin
							});
						}.bind(this));
					},
					requestAllChunks: function ChunkedStreamManager_requestAllChunks() {
						var missingChunks = this.stream.getMissingChunks();
						this._requestChunks(missingChunks);
						return this._loadedStreamCapability.promise;
					},
					_requestChunks: function ChunkedStreamManager_requestChunks(chunks) {
						var requestId = this.currRequestId++;
						var i, ii;
						var chunksNeeded = Object.create(null);
						this.chunksNeededByRequest[requestId] = chunksNeeded;
						for (i = 0, ii = chunks.length; i < ii; i++) {
							if (!this.stream.hasChunk(chunks[i])) {
								chunksNeeded[chunks[i]] = true;
							}
						}
						if (isEmptyObj(chunksNeeded)) {
							return Promise.resolve();
						}
						var capability = createPromiseCapability();
						this.promisesByRequest[requestId] = capability;
						var chunksToRequest = [];
						for (var chunk in chunksNeeded) {
							chunk = chunk | 0;
							if (!(chunk in this.requestsByChunk)) {
								this.requestsByChunk[chunk] = [];
								chunksToRequest.push(chunk);
							}
							this.requestsByChunk[chunk].push(requestId);
						}
						if (!chunksToRequest.length) {
							return capability.promise;
						}
						var groupedChunksToRequest = this.groupChunks(chunksToRequest);
						for (i = 0; i < groupedChunksToRequest.length; ++i) {
							var groupedChunk = groupedChunksToRequest[i];
							var begin = groupedChunk.beginChunk * this.chunkSize;
							var end = Math.min(groupedChunk.endChunk * this.chunkSize, this.length);
							this.sendRequest(begin, end);
						}
						return capability.promise;
					},
					getStream: function ChunkedStreamManager_getStream() {
						return this.stream;
					},
					requestRange: function ChunkedStreamManager_requestRange(begin, end) {
						end = Math.min(end, this.length);
						var beginChunk = this.getBeginChunk(begin);
						var endChunk = this.getEndChunk(end);
						var chunks = [];
						for (var chunk = beginChunk; chunk < endChunk; ++chunk) {
							chunks.push(chunk);
						}
						return this._requestChunks(chunks);
					},
					requestRanges: function ChunkedStreamManager_requestRanges(ranges) {
						ranges = ranges || [];
						var chunksToRequest = [];
						for (var i = 0; i < ranges.length; i++) {
							var beginChunk = this.getBeginChunk(ranges[i].begin);
							var endChunk = this.getEndChunk(ranges[i].end);
							for (var chunk = beginChunk; chunk < endChunk; ++chunk) {
								if (chunksToRequest.indexOf(chunk) < 0) {
									chunksToRequest.push(chunk);
								}
							}
						}
						chunksToRequest.sort(function (a, b) {
							return a - b;
						});
						return this._requestChunks(chunksToRequest);
					},
					groupChunks: function ChunkedStreamManager_groupChunks(chunks) {
						var groupedChunks = [];
						var beginChunk = -1;
						var prevChunk = -1;
						for (var i = 0; i < chunks.length; ++i) {
							var chunk = chunks[i];
							if (beginChunk < 0) {
								beginChunk = chunk;
							}
							if (prevChunk >= 0 && prevChunk + 1 !== chunk) {
								groupedChunks.push({
									beginChunk: beginChunk,
									endChunk: prevChunk + 1
								});
								beginChunk = chunk;
							}
							if (i + 1 === chunks.length) {
								groupedChunks.push({
									beginChunk: beginChunk,
									endChunk: chunk + 1
								});
							}
							prevChunk = chunk;
						}
						return groupedChunks;
					},
					onProgress: function ChunkedStreamManager_onProgress(args) {
						var bytesLoaded = this.stream.numChunksLoaded * this.chunkSize + args.loaded;
						this.msgHandler.send('DocProgress', {
							loaded: bytesLoaded,
							total: this.length
						});
					},
					onReceiveData: function ChunkedStreamManager_onReceiveData(args) {
						var chunk = args.chunk;
						var isProgressive = args.begin === undefined;
						var begin = isProgressive ? this.progressiveDataLength : args.begin;
						var end = begin + chunk.byteLength;
						var beginChunk = Math.floor(begin / this.chunkSize);
						var endChunk = end < this.length ? Math.floor(end / this.chunkSize) : Math.ceil(end / this.chunkSize);
						if (isProgressive) {
							this.stream.onReceiveProgressiveData(chunk);
							this.progressiveDataLength = end;
						} else {
							this.stream.onReceiveData(begin, chunk);
						}
						if (this.stream.allChunksLoaded()) {
							this._loadedStreamCapability.resolve(this.stream);
						}
						var loadedRequests = [];
						var i, requestId;
						for (chunk = beginChunk; chunk < endChunk; ++chunk) {
							var requestIds = this.requestsByChunk[chunk] || [];
							delete this.requestsByChunk[chunk];
							for (i = 0; i < requestIds.length; ++i) {
								requestId = requestIds[i];
								var chunksNeeded = this.chunksNeededByRequest[requestId];
								if (chunk in chunksNeeded) {
									delete chunksNeeded[chunk];
								}
								if (!isEmptyObj(chunksNeeded)) {
									continue;
								}
								loadedRequests.push(requestId);
							}
						}
						if (!this.disableAutoFetch && isEmptyObj(this.requestsByChunk)) {
							var nextEmptyChunk;
							if (this.stream.numChunksLoaded === 1) {
								var lastChunk = this.stream.numChunks - 1;
								if (!this.stream.hasChunk(lastChunk)) {
									nextEmptyChunk = lastChunk;
								}
							} else {
								nextEmptyChunk = this.stream.nextEmptyChunk(endChunk);
							}
							if (isInt(nextEmptyChunk)) {
								this._requestChunks([nextEmptyChunk]);
							}
						}
						for (i = 0; i < loadedRequests.length; ++i) {
							requestId = loadedRequests[i];
							var capability = this.promisesByRequest[requestId];
							delete this.promisesByRequest[requestId];
							capability.resolve();
						}
						this.msgHandler.send('DocProgress', {
							loaded: this.stream.numChunksLoaded * this.chunkSize,
							total: this.length
						});
					},
					onError: function ChunkedStreamManager_onError(err) {
						this._loadedStreamCapability.reject(err);
					},
					getBeginChunk: function ChunkedStreamManager_getBeginChunk(begin) {
						var chunk = Math.floor(begin / this.chunkSize);
						return chunk;
					},
					getEndChunk: function ChunkedStreamManager_getEndChunk(end) {
						var chunk = Math.floor((end - 1) / this.chunkSize) + 1;
						return chunk;
					},
					abort: function ChunkedStreamManager_abort() {
						this.aborted = true;
						if (this.pdfNetworkStream) {
							this.pdfNetworkStream.cancelAllRequests('abort');
						}
						for (var requestId in this.promisesByRequest) {
							var capability = this.promisesByRequest[requestId];
							capability.reject(new Error('Request was aborted'));
						}
					}
				};
				return ChunkedStreamManager;
			}();
			exports.ChunkedStream = ChunkedStream;
			exports.ChunkedStreamManager = ChunkedStreamManager;

			/***/
		}),
/* 13 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var PasswordException = sharedUtil.PasswordException;
			var PasswordResponses = sharedUtil.PasswordResponses;
			var bytesToString = sharedUtil.bytesToString;
			var warn = sharedUtil.warn;
			var error = sharedUtil.error;
			var assert = sharedUtil.assert;
			var isInt = sharedUtil.isInt;
			var stringToBytes = sharedUtil.stringToBytes;
			var utf8StringToString = sharedUtil.utf8StringToString;
			var Name = corePrimitives.Name;
			var isName = corePrimitives.isName;
			var isDict = corePrimitives.isDict;
			var DecryptStream = coreStream.DecryptStream;
			var ARCFourCipher = function ARCFourCipherClosure() {
				function ARCFourCipher(key) {
					this.a = 0;
					this.b = 0;
					var s = new Uint8Array(256);
					var i, j = 0, tmp, keyLength = key.length;
					for (i = 0; i < 256; ++i) {
						s[i] = i;
					}
					for (i = 0; i < 256; ++i) {
						tmp = s[i];
						j = j + tmp + key[i % keyLength] & 0xFF;
						s[i] = s[j];
						s[j] = tmp;
					}
					this.s = s;
				}
				ARCFourCipher.prototype = {
					encryptBlock: function ARCFourCipher_encryptBlock(data) {
						var i, n = data.length, tmp, tmp2;
						var a = this.a, b = this.b, s = this.s;
						var output = new Uint8Array(n);
						for (i = 0; i < n; ++i) {
							a = a + 1 & 0xFF;
							tmp = s[a];
							b = b + tmp & 0xFF;
							tmp2 = s[b];
							s[a] = tmp2;
							s[b] = tmp;
							output[i] = data[i] ^ s[tmp + tmp2 & 0xFF];
						}
						this.a = a;
						this.b = b;
						return output;
					}
				};
				ARCFourCipher.prototype.decryptBlock = ARCFourCipher.prototype.encryptBlock;
				return ARCFourCipher;
			}();
			var calculateMD5 = function calculateMD5Closure() {
				var r = new Uint8Array([
					7,
					12,
					17,
					22,
					7,
					12,
					17,
					22,
					7,
					12,
					17,
					22,
					7,
					12,
					17,
					22,
					5,
					9,
					14,
					20,
					5,
					9,
					14,
					20,
					5,
					9,
					14,
					20,
					5,
					9,
					14,
					20,
					4,
					11,
					16,
					23,
					4,
					11,
					16,
					23,
					4,
					11,
					16,
					23,
					4,
					11,
					16,
					23,
					6,
					10,
					15,
					21,
					6,
					10,
					15,
					21,
					6,
					10,
					15,
					21,
					6,
					10,
					15,
					21
				]);
				var k = new Int32Array([
					-680876936,
					-389564586,
					606105819,
					-1044525330,
					-176418897,
					1200080426,
					-1473231341,
					-45705983,
					1770035416,
					-1958414417,
					-42063,
					-1990404162,
					1804603682,
					-40341101,
					-1502002290,
					1236535329,
					-165796510,
					-1069501632,
					643717713,
					-373897302,
					-701558691,
					38016083,
					-660478335,
					-405537848,
					568446438,
					-1019803690,
					-187363961,
					1163531501,
					-1444681467,
					-51403784,
					1735328473,
					-1926607734,
					-378558,
					-2022574463,
					1839030562,
					-35309556,
					-1530992060,
					1272893353,
					-155497632,
					-1094730640,
					681279174,
					-358537222,
					-722521979,
					76029189,
					-640364487,
					-421815835,
					530742520,
					-995338651,
					-198630844,
					1126891415,
					-1416354905,
					-57434055,
					1700485571,
					-1894986606,
					-1051523,
					-2054922799,
					1873313359,
					-30611744,
					-1560198380,
					1309151649,
					-145523070,
					-1120210379,
					718787259,
					-343485551
				]);
				function hash(data, offset, length) {
					var h0 = 1732584193, h1 = -271733879, h2 = -1732584194, h3 = 271733878;
					var paddedLength = length + 72 & ~63;
					var padded = new Uint8Array(paddedLength);
					var i, j, n;
					for (i = 0; i < length; ++i) {
						padded[i] = data[offset++];
					}
					padded[i++] = 0x80;
					n = paddedLength - 8;
					while (i < n) {
						padded[i++] = 0;
					}
					padded[i++] = length << 3 & 0xFF;
					padded[i++] = length >> 5 & 0xFF;
					padded[i++] = length >> 13 & 0xFF;
					padded[i++] = length >> 21 & 0xFF;
					padded[i++] = length >>> 29 & 0xFF;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					var w = new Int32Array(16);
					for (i = 0; i < paddedLength;) {
						for (j = 0; j < 16; ++j, i += 4) {
							w[j] = padded[i] | padded[i + 1] << 8 | padded[i + 2] << 16 | padded[i + 3] << 24;
						}
						var a = h0, b = h1, c = h2, d = h3, f, g;
						for (j = 0; j < 64; ++j) {
							if (j < 16) {
								f = b & c | ~b & d;
								g = j;
							} else if (j < 32) {
								f = d & b | ~d & c;
								g = 5 * j + 1 & 15;
							} else if (j < 48) {
								f = b ^ c ^ d;
								g = 3 * j + 5 & 15;
							} else {
								f = c ^ (b | ~d);
								g = 7 * j & 15;
							}
							var tmp = d, rotateArg = a + f + k[j] + w[g] | 0, rotate = r[j];
							d = c;
							c = b;
							b = b + (rotateArg << rotate | rotateArg >>> 32 - rotate) | 0;
							a = tmp;
						}
						h0 = h0 + a | 0;
						h1 = h1 + b | 0;
						h2 = h2 + c | 0;
						h3 = h3 + d | 0;
					}
					return new Uint8Array([
						h0 & 0xFF,
						h0 >> 8 & 0xFF,
						h0 >> 16 & 0xFF,
						h0 >>> 24 & 0xFF,
						h1 & 0xFF,
						h1 >> 8 & 0xFF,
						h1 >> 16 & 0xFF,
						h1 >>> 24 & 0xFF,
						h2 & 0xFF,
						h2 >> 8 & 0xFF,
						h2 >> 16 & 0xFF,
						h2 >>> 24 & 0xFF,
						h3 & 0xFF,
						h3 >> 8 & 0xFF,
						h3 >> 16 & 0xFF,
						h3 >>> 24 & 0xFF
					]);
				}
				return hash;
			}();
			var Word64 = function Word64Closure() {
				function Word64(highInteger, lowInteger) {
					this.high = highInteger | 0;
					this.low = lowInteger | 0;
				}
				Word64.prototype = {
					and: function Word64_and(word) {
						this.high &= word.high;
						this.low &= word.low;
					},
					xor: function Word64_xor(word) {
						this.high ^= word.high;
						this.low ^= word.low;
					},
					or: function Word64_or(word) {
						this.high |= word.high;
						this.low |= word.low;
					},
					shiftRight: function Word64_shiftRight(places) {
						if (places >= 32) {
							this.low = this.high >>> places - 32 | 0;
							this.high = 0;
						} else {
							this.low = this.low >>> places | this.high << 32 - places;
							this.high = this.high >>> places | 0;
						}
					},
					shiftLeft: function Word64_shiftLeft(places) {
						if (places >= 32) {
							this.high = this.low << places - 32;
							this.low = 0;
						} else {
							this.high = this.high << places | this.low >>> 32 - places;
							this.low = this.low << places;
						}
					},
					rotateRight: function Word64_rotateRight(places) {
						var low, high;
						if (places & 32) {
							high = this.low;
							low = this.high;
						} else {
							low = this.low;
							high = this.high;
						}
						places &= 31;
						this.low = low >>> places | high << 32 - places;
						this.high = high >>> places | low << 32 - places;
					},
					not: function Word64_not() {
						this.high = ~this.high;
						this.low = ~this.low;
					},
					add: function Word64_add(word) {
						var lowAdd = (this.low >>> 0) + (word.low >>> 0);
						var highAdd = (this.high >>> 0) + (word.high >>> 0);
						if (lowAdd > 0xFFFFFFFF) {
							highAdd += 1;
						}
						this.low = lowAdd | 0;
						this.high = highAdd | 0;
					},
					copyTo: function Word64_copyTo(bytes, offset) {
						bytes[offset] = this.high >>> 24 & 0xFF;
						bytes[offset + 1] = this.high >> 16 & 0xFF;
						bytes[offset + 2] = this.high >> 8 & 0xFF;
						bytes[offset + 3] = this.high & 0xFF;
						bytes[offset + 4] = this.low >>> 24 & 0xFF;
						bytes[offset + 5] = this.low >> 16 & 0xFF;
						bytes[offset + 6] = this.low >> 8 & 0xFF;
						bytes[offset + 7] = this.low & 0xFF;
					},
					assign: function Word64_assign(word) {
						this.high = word.high;
						this.low = word.low;
					}
				};
				return Word64;
			}();
			var calculateSHA256 = function calculateSHA256Closure() {
				function rotr(x, n) {
					return x >>> n | x << 32 - n;
				}
				function ch(x, y, z) {
					return x & y ^ ~x & z;
				}
				function maj(x, y, z) {
					return x & y ^ x & z ^ y & z;
				}
				function sigma(x) {
					return rotr(x, 2) ^ rotr(x, 13) ^ rotr(x, 22);
				}
				function sigmaPrime(x) {
					return rotr(x, 6) ^ rotr(x, 11) ^ rotr(x, 25);
				}
				function littleSigma(x) {
					return rotr(x, 7) ^ rotr(x, 18) ^ x >>> 3;
				}
				function littleSigmaPrime(x) {
					return rotr(x, 17) ^ rotr(x, 19) ^ x >>> 10;
				}
				var k = [
					0x428a2f98,
					0x71374491,
					0xb5c0fbcf,
					0xe9b5dba5,
					0x3956c25b,
					0x59f111f1,
					0x923f82a4,
					0xab1c5ed5,
					0xd807aa98,
					0x12835b01,
					0x243185be,
					0x550c7dc3,
					0x72be5d74,
					0x80deb1fe,
					0x9bdc06a7,
					0xc19bf174,
					0xe49b69c1,
					0xefbe4786,
					0x0fc19dc6,
					0x240ca1cc,
					0x2de92c6f,
					0x4a7484aa,
					0x5cb0a9dc,
					0x76f988da,
					0x983e5152,
					0xa831c66d,
					0xb00327c8,
					0xbf597fc7,
					0xc6e00bf3,
					0xd5a79147,
					0x06ca6351,
					0x14292967,
					0x27b70a85,
					0x2e1b2138,
					0x4d2c6dfc,
					0x53380d13,
					0x650a7354,
					0x766a0abb,
					0x81c2c92e,
					0x92722c85,
					0xa2bfe8a1,
					0xa81a664b,
					0xc24b8b70,
					0xc76c51a3,
					0xd192e819,
					0xd6990624,
					0xf40e3585,
					0x106aa070,
					0x19a4c116,
					0x1e376c08,
					0x2748774c,
					0x34b0bcb5,
					0x391c0cb3,
					0x4ed8aa4a,
					0x5b9cca4f,
					0x682e6ff3,
					0x748f82ee,
					0x78a5636f,
					0x84c87814,
					0x8cc70208,
					0x90befffa,
					0xa4506ceb,
					0xbef9a3f7,
					0xc67178f2
				];
				function hash(data, offset, length) {
					var h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a, h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;
					var paddedLength = Math.ceil((length + 9) / 64) * 64;
					var padded = new Uint8Array(paddedLength);
					var i, j, n;
					for (i = 0; i < length; ++i) {
						padded[i] = data[offset++];
					}
					padded[i++] = 0x80;
					n = paddedLength - 8;
					while (i < n) {
						padded[i++] = 0;
					}
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = length >>> 29 & 0xFF;
					padded[i++] = length >> 21 & 0xFF;
					padded[i++] = length >> 13 & 0xFF;
					padded[i++] = length >> 5 & 0xFF;
					padded[i++] = length << 3 & 0xFF;
					var w = new Uint32Array(64);
					for (i = 0; i < paddedLength;) {
						for (j = 0; j < 16; ++j) {
							w[j] = padded[i] << 24 | padded[i + 1] << 16 | padded[i + 2] << 8 | padded[i + 3];
							i += 4;
						}
						for (j = 16; j < 64; ++j) {
							w[j] = littleSigmaPrime(w[j - 2]) + w[j - 7] + littleSigma(w[j - 15]) + w[j - 16] | 0;
						}
						var a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7, t1, t2;
						for (j = 0; j < 64; ++j) {
							t1 = h + sigmaPrime(e) + ch(e, f, g) + k[j] + w[j];
							t2 = sigma(a) + maj(a, b, c);
							h = g;
							g = f;
							f = e;
							e = d + t1 | 0;
							d = c;
							c = b;
							b = a;
							a = t1 + t2 | 0;
						}
						h0 = h0 + a | 0;
						h1 = h1 + b | 0;
						h2 = h2 + c | 0;
						h3 = h3 + d | 0;
						h4 = h4 + e | 0;
						h5 = h5 + f | 0;
						h6 = h6 + g | 0;
						h7 = h7 + h | 0;
					}
					return new Uint8Array([
						h0 >> 24 & 0xFF,
						h0 >> 16 & 0xFF,
						h0 >> 8 & 0xFF,
						h0 & 0xFF,
						h1 >> 24 & 0xFF,
						h1 >> 16 & 0xFF,
						h1 >> 8 & 0xFF,
						h1 & 0xFF,
						h2 >> 24 & 0xFF,
						h2 >> 16 & 0xFF,
						h2 >> 8 & 0xFF,
						h2 & 0xFF,
						h3 >> 24 & 0xFF,
						h3 >> 16 & 0xFF,
						h3 >> 8 & 0xFF,
						h3 & 0xFF,
						h4 >> 24 & 0xFF,
						h4 >> 16 & 0xFF,
						h4 >> 8 & 0xFF,
						h4 & 0xFF,
						h5 >> 24 & 0xFF,
						h5 >> 16 & 0xFF,
						h5 >> 8 & 0xFF,
						h5 & 0xFF,
						h6 >> 24 & 0xFF,
						h6 >> 16 & 0xFF,
						h6 >> 8 & 0xFF,
						h6 & 0xFF,
						h7 >> 24 & 0xFF,
						h7 >> 16 & 0xFF,
						h7 >> 8 & 0xFF,
						h7 & 0xFF
					]);
				}
				return hash;
			}();
			var calculateSHA512 = function calculateSHA512Closure() {
				function ch(result, x, y, z, tmp) {
					result.assign(x);
					result.and(y);
					tmp.assign(x);
					tmp.not();
					tmp.and(z);
					result.xor(tmp);
				}
				function maj(result, x, y, z, tmp) {
					result.assign(x);
					result.and(y);
					tmp.assign(x);
					tmp.and(z);
					result.xor(tmp);
					tmp.assign(y);
					tmp.and(z);
					result.xor(tmp);
				}
				function sigma(result, x, tmp) {
					result.assign(x);
					result.rotateRight(28);
					tmp.assign(x);
					tmp.rotateRight(34);
					result.xor(tmp);
					tmp.assign(x);
					tmp.rotateRight(39);
					result.xor(tmp);
				}
				function sigmaPrime(result, x, tmp) {
					result.assign(x);
					result.rotateRight(14);
					tmp.assign(x);
					tmp.rotateRight(18);
					result.xor(tmp);
					tmp.assign(x);
					tmp.rotateRight(41);
					result.xor(tmp);
				}
				function littleSigma(result, x, tmp) {
					result.assign(x);
					result.rotateRight(1);
					tmp.assign(x);
					tmp.rotateRight(8);
					result.xor(tmp);
					tmp.assign(x);
					tmp.shiftRight(7);
					result.xor(tmp);
				}
				function littleSigmaPrime(result, x, tmp) {
					result.assign(x);
					result.rotateRight(19);
					tmp.assign(x);
					tmp.rotateRight(61);
					result.xor(tmp);
					tmp.assign(x);
					tmp.shiftRight(6);
					result.xor(tmp);
				}
				var k = [
					new Word64(0x428a2f98, 0xd728ae22),
					new Word64(0x71374491, 0x23ef65cd),
					new Word64(0xb5c0fbcf, 0xec4d3b2f),
					new Word64(0xe9b5dba5, 0x8189dbbc),
					new Word64(0x3956c25b, 0xf348b538),
					new Word64(0x59f111f1, 0xb605d019),
					new Word64(0x923f82a4, 0xaf194f9b),
					new Word64(0xab1c5ed5, 0xda6d8118),
					new Word64(0xd807aa98, 0xa3030242),
					new Word64(0x12835b01, 0x45706fbe),
					new Word64(0x243185be, 0x4ee4b28c),
					new Word64(0x550c7dc3, 0xd5ffb4e2),
					new Word64(0x72be5d74, 0xf27b896f),
					new Word64(0x80deb1fe, 0x3b1696b1),
					new Word64(0x9bdc06a7, 0x25c71235),
					new Word64(0xc19bf174, 0xcf692694),
					new Word64(0xe49b69c1, 0x9ef14ad2),
					new Word64(0xefbe4786, 0x384f25e3),
					new Word64(0x0fc19dc6, 0x8b8cd5b5),
					new Word64(0x240ca1cc, 0x77ac9c65),
					new Word64(0x2de92c6f, 0x592b0275),
					new Word64(0x4a7484aa, 0x6ea6e483),
					new Word64(0x5cb0a9dc, 0xbd41fbd4),
					new Word64(0x76f988da, 0x831153b5),
					new Word64(0x983e5152, 0xee66dfab),
					new Word64(0xa831c66d, 0x2db43210),
					new Word64(0xb00327c8, 0x98fb213f),
					new Word64(0xbf597fc7, 0xbeef0ee4),
					new Word64(0xc6e00bf3, 0x3da88fc2),
					new Word64(0xd5a79147, 0x930aa725),
					new Word64(0x06ca6351, 0xe003826f),
					new Word64(0x14292967, 0x0a0e6e70),
					new Word64(0x27b70a85, 0x46d22ffc),
					new Word64(0x2e1b2138, 0x5c26c926),
					new Word64(0x4d2c6dfc, 0x5ac42aed),
					new Word64(0x53380d13, 0x9d95b3df),
					new Word64(0x650a7354, 0x8baf63de),
					new Word64(0x766a0abb, 0x3c77b2a8),
					new Word64(0x81c2c92e, 0x47edaee6),
					new Word64(0x92722c85, 0x1482353b),
					new Word64(0xa2bfe8a1, 0x4cf10364),
					new Word64(0xa81a664b, 0xbc423001),
					new Word64(0xc24b8b70, 0xd0f89791),
					new Word64(0xc76c51a3, 0x0654be30),
					new Word64(0xd192e819, 0xd6ef5218),
					new Word64(0xd6990624, 0x5565a910),
					new Word64(0xf40e3585, 0x5771202a),
					new Word64(0x106aa070, 0x32bbd1b8),
					new Word64(0x19a4c116, 0xb8d2d0c8),
					new Word64(0x1e376c08, 0x5141ab53),
					new Word64(0x2748774c, 0xdf8eeb99),
					new Word64(0x34b0bcb5, 0xe19b48a8),
					new Word64(0x391c0cb3, 0xc5c95a63),
					new Word64(0x4ed8aa4a, 0xe3418acb),
					new Word64(0x5b9cca4f, 0x7763e373),
					new Word64(0x682e6ff3, 0xd6b2b8a3),
					new Word64(0x748f82ee, 0x5defb2fc),
					new Word64(0x78a5636f, 0x43172f60),
					new Word64(0x84c87814, 0xa1f0ab72),
					new Word64(0x8cc70208, 0x1a6439ec),
					new Word64(0x90befffa, 0x23631e28),
					new Word64(0xa4506ceb, 0xde82bde9),
					new Word64(0xbef9a3f7, 0xb2c67915),
					new Word64(0xc67178f2, 0xe372532b),
					new Word64(0xca273ece, 0xea26619c),
					new Word64(0xd186b8c7, 0x21c0c207),
					new Word64(0xeada7dd6, 0xcde0eb1e),
					new Word64(0xf57d4f7f, 0xee6ed178),
					new Word64(0x06f067aa, 0x72176fba),
					new Word64(0x0a637dc5, 0xa2c898a6),
					new Word64(0x113f9804, 0xbef90dae),
					new Word64(0x1b710b35, 0x131c471b),
					new Word64(0x28db77f5, 0x23047d84),
					new Word64(0x32caab7b, 0x40c72493),
					new Word64(0x3c9ebe0a, 0x15c9bebc),
					new Word64(0x431d67c4, 0x9c100d4c),
					new Word64(0x4cc5d4be, 0xcb3e42b6),
					new Word64(0x597f299c, 0xfc657e2a),
					new Word64(0x5fcb6fab, 0x3ad6faec),
					new Word64(0x6c44198c, 0x4a475817)
				];
				function hash(data, offset, length, mode384) {
					mode384 = !!mode384;
					var h0, h1, h2, h3, h4, h5, h6, h7;
					if (!mode384) {
						h0 = new Word64(0x6a09e667, 0xf3bcc908);
						h1 = new Word64(0xbb67ae85, 0x84caa73b);
						h2 = new Word64(0x3c6ef372, 0xfe94f82b);
						h3 = new Word64(0xa54ff53a, 0x5f1d36f1);
						h4 = new Word64(0x510e527f, 0xade682d1);
						h5 = new Word64(0x9b05688c, 0x2b3e6c1f);
						h6 = new Word64(0x1f83d9ab, 0xfb41bd6b);
						h7 = new Word64(0x5be0cd19, 0x137e2179);
					} else {
						h0 = new Word64(0xcbbb9d5d, 0xc1059ed8);
						h1 = new Word64(0x629a292a, 0x367cd507);
						h2 = new Word64(0x9159015a, 0x3070dd17);
						h3 = new Word64(0x152fecd8, 0xf70e5939);
						h4 = new Word64(0x67332667, 0xffc00b31);
						h5 = new Word64(0x8eb44a87, 0x68581511);
						h6 = new Word64(0xdb0c2e0d, 0x64f98fa7);
						h7 = new Word64(0x47b5481d, 0xbefa4fa4);
					}
					var paddedLength = Math.ceil((length + 17) / 128) * 128;
					var padded = new Uint8Array(paddedLength);
					var i, j, n;
					for (i = 0; i < length; ++i) {
						padded[i] = data[offset++];
					}
					padded[i++] = 0x80;
					n = paddedLength - 16;
					while (i < n) {
						padded[i++] = 0;
					}
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = 0;
					padded[i++] = length >>> 29 & 0xFF;
					padded[i++] = length >> 21 & 0xFF;
					padded[i++] = length >> 13 & 0xFF;
					padded[i++] = length >> 5 & 0xFF;
					padded[i++] = length << 3 & 0xFF;
					var w = new Array(80);
					for (i = 0; i < 80; i++) {
						w[i] = new Word64(0, 0);
					}
					var a = new Word64(0, 0), b = new Word64(0, 0), c = new Word64(0, 0);
					var d = new Word64(0, 0), e = new Word64(0, 0), f = new Word64(0, 0);
					var g = new Word64(0, 0), h = new Word64(0, 0);
					var t1 = new Word64(0, 0), t2 = new Word64(0, 0);
					var tmp1 = new Word64(0, 0), tmp2 = new Word64(0, 0), tmp3;
					for (i = 0; i < paddedLength;) {
						for (j = 0; j < 16; ++j) {
							w[j].high = padded[i] << 24 | padded[i + 1] << 16 | padded[i + 2] << 8 | padded[i + 3];
							w[j].low = padded[i + 4] << 24 | padded[i + 5] << 16 | padded[i + 6] << 8 | padded[i + 7];
							i += 8;
						}
						for (j = 16; j < 80; ++j) {
							tmp3 = w[j];
							littleSigmaPrime(tmp3, w[j - 2], tmp2);
							tmp3.add(w[j - 7]);
							littleSigma(tmp1, w[j - 15], tmp2);
							tmp3.add(tmp1);
							tmp3.add(w[j - 16]);
						}
						a.assign(h0);
						b.assign(h1);
						c.assign(h2);
						d.assign(h3);
						e.assign(h4);
						f.assign(h5);
						g.assign(h6);
						h.assign(h7);
						for (j = 0; j < 80; ++j) {
							t1.assign(h);
							sigmaPrime(tmp1, e, tmp2);
							t1.add(tmp1);
							ch(tmp1, e, f, g, tmp2);
							t1.add(tmp1);
							t1.add(k[j]);
							t1.add(w[j]);
							sigma(t2, a, tmp2);
							maj(tmp1, a, b, c, tmp2);
							t2.add(tmp1);
							tmp3 = h;
							h = g;
							g = f;
							f = e;
							d.add(t1);
							e = d;
							d = c;
							c = b;
							b = a;
							tmp3.assign(t1);
							tmp3.add(t2);
							a = tmp3;
						}
						h0.add(a);
						h1.add(b);
						h2.add(c);
						h3.add(d);
						h4.add(e);
						h5.add(f);
						h6.add(g);
						h7.add(h);
					}
					var result;
					if (!mode384) {
						result = new Uint8Array(64);
						h0.copyTo(result, 0);
						h1.copyTo(result, 8);
						h2.copyTo(result, 16);
						h3.copyTo(result, 24);
						h4.copyTo(result, 32);
						h5.copyTo(result, 40);
						h6.copyTo(result, 48);
						h7.copyTo(result, 56);
					} else {
						result = new Uint8Array(48);
						h0.copyTo(result, 0);
						h1.copyTo(result, 8);
						h2.copyTo(result, 16);
						h3.copyTo(result, 24);
						h4.copyTo(result, 32);
						h5.copyTo(result, 40);
					}
					return result;
				}
				return hash;
			}();
			var calculateSHA384 = function calculateSHA384Closure() {
				function hash(data, offset, length) {
					return calculateSHA512(data, offset, length, true);
				}
				return hash;
			}();
			var NullCipher = function NullCipherClosure() {
				function NullCipher() {
				}
				NullCipher.prototype = {
					decryptBlock: function NullCipher_decryptBlock(data) {
						return data;
					}
				};
				return NullCipher;
			}();
			var AES128Cipher = function AES128CipherClosure() {
				var rcon = new Uint8Array([
					0x8d,
					0x01,
					0x02,
					0x04,
					0x08,
					0x10,
					0x20,
					0x40,
					0x80,
					0x1b,
					0x36,
					0x6c,
					0xd8,
					0xab,
					0x4d,
					0x9a,
					0x2f,
					0x5e,
					0xbc,
					0x63,
					0xc6,
					0x97,
					0x35,
					0x6a,
					0xd4,
					0xb3,
					0x7d,
					0xfa,
					0xef,
					0xc5,
					0x91,
					0x39,
					0x72,
					0xe4,
					0xd3,
					0xbd,
					0x61,
					0xc2,
					0x9f,
					0x25,
					0x4a,
					0x94,
					0x33,
					0x66,
					0xcc,
					0x83,
					0x1d,
					0x3a,
					0x74,
					0xe8,
					0xcb,
					0x8d,
					0x01,
					0x02,
					0x04,
					0x08,
					0x10,
					0x20,
					0x40,
					0x80,
					0x1b,
					0x36,
					0x6c,
					0xd8,
					0xab,
					0x4d,
					0x9a,
					0x2f,
					0x5e,
					0xbc,
					0x63,
					0xc6,
					0x97,
					0x35,
					0x6a,
					0xd4,
					0xb3,
					0x7d,
					0xfa,
					0xef,
					0xc5,
					0x91,
					0x39,
					0x72,
					0xe4,
					0xd3,
					0xbd,
					0x61,
					0xc2,
					0x9f,
					0x25,
					0x4a,
					0x94,
					0x33,
					0x66,
					0xcc,
					0x83,
					0x1d,
					0x3a,
					0x74,
					0xe8,
					0xcb,
					0x8d,
					0x01,
					0x02,
					0x04,
					0x08,
					0x10,
					0x20,
					0x40,
					0x80,
					0x1b,
					0x36,
					0x6c,
					0xd8,
					0xab,
					0x4d,
					0x9a,
					0x2f,
					0x5e,
					0xbc,
					0x63,
					0xc6,
					0x97,
					0x35,
					0x6a,
					0xd4,
					0xb3,
					0x7d,
					0xfa,
					0xef,
					0xc5,
					0x91,
					0x39,
					0x72,
					0xe4,
					0xd3,
					0xbd,
					0x61,
					0xc2,
					0x9f,
					0x25,
					0x4a,
					0x94,
					0x33,
					0x66,
					0xcc,
					0x83,
					0x1d,
					0x3a,
					0x74,
					0xe8,
					0xcb,
					0x8d,
					0x01,
					0x02,
					0x04,
					0x08,
					0x10,
					0x20,
					0x40,
					0x80,
					0x1b,
					0x36,
					0x6c,
					0xd8,
					0xab,
					0x4d,
					0x9a,
					0x2f,
					0x5e,
					0xbc,
					0x63,
					0xc6,
					0x97,
					0x35,
					0x6a,
					0xd4,
					0xb3,
					0x7d,
					0xfa,
					0xef,
					0xc5,
					0x91,
					0x39,
					0x72,
					0xe4,
					0xd3,
					0xbd,
					0x61,
					0xc2,
					0x9f,
					0x25,
					0x4a,
					0x94,
					0x33,
					0x66,
					0xcc,
					0x83,
					0x1d,
					0x3a,
					0x74,
					0xe8,
					0xcb,
					0x8d,
					0x01,
					0x02,
					0x04,
					0x08,
					0x10,
					0x20,
					0x40,
					0x80,
					0x1b,
					0x36,
					0x6c,
					0xd8,
					0xab,
					0x4d,
					0x9a,
					0x2f,
					0x5e,
					0xbc,
					0x63,
					0xc6,
					0x97,
					0x35,
					0x6a,
					0xd4,
					0xb3,
					0x7d,
					0xfa,
					0xef,
					0xc5,
					0x91,
					0x39,
					0x72,
					0xe4,
					0xd3,
					0xbd,
					0x61,
					0xc2,
					0x9f,
					0x25,
					0x4a,
					0x94,
					0x33,
					0x66,
					0xcc,
					0x83,
					0x1d,
					0x3a,
					0x74,
					0xe8,
					0xcb,
					0x8d
				]);
				var s = new Uint8Array([
					0x63,
					0x7c,
					0x77,
					0x7b,
					0xf2,
					0x6b,
					0x6f,
					0xc5,
					0x30,
					0x01,
					0x67,
					0x2b,
					0xfe,
					0xd7,
					0xab,
					0x76,
					0xca,
					0x82,
					0xc9,
					0x7d,
					0xfa,
					0x59,
					0x47,
					0xf0,
					0xad,
					0xd4,
					0xa2,
					0xaf,
					0x9c,
					0xa4,
					0x72,
					0xc0,
					0xb7,
					0xfd,
					0x93,
					0x26,
					0x36,
					0x3f,
					0xf7,
					0xcc,
					0x34,
					0xa5,
					0xe5,
					0xf1,
					0x71,
					0xd8,
					0x31,
					0x15,
					0x04,
					0xc7,
					0x23,
					0xc3,
					0x18,
					0x96,
					0x05,
					0x9a,
					0x07,
					0x12,
					0x80,
					0xe2,
					0xeb,
					0x27,
					0xb2,
					0x75,
					0x09,
					0x83,
					0x2c,
					0x1a,
					0x1b,
					0x6e,
					0x5a,
					0xa0,
					0x52,
					0x3b,
					0xd6,
					0xb3,
					0x29,
					0xe3,
					0x2f,
					0x84,
					0x53,
					0xd1,
					0x00,
					0xed,
					0x20,
					0xfc,
					0xb1,
					0x5b,
					0x6a,
					0xcb,
					0xbe,
					0x39,
					0x4a,
					0x4c,
					0x58,
					0xcf,
					0xd0,
					0xef,
					0xaa,
					0xfb,
					0x43,
					0x4d,
					0x33,
					0x85,
					0x45,
					0xf9,
					0x02,
					0x7f,
					0x50,
					0x3c,
					0x9f,
					0xa8,
					0x51,
					0xa3,
					0x40,
					0x8f,
					0x92,
					0x9d,
					0x38,
					0xf5,
					0xbc,
					0xb6,
					0xda,
					0x21,
					0x10,
					0xff,
					0xf3,
					0xd2,
					0xcd,
					0x0c,
					0x13,
					0xec,
					0x5f,
					0x97,
					0x44,
					0x17,
					0xc4,
					0xa7,
					0x7e,
					0x3d,
					0x64,
					0x5d,
					0x19,
					0x73,
					0x60,
					0x81,
					0x4f,
					0xdc,
					0x22,
					0x2a,
					0x90,
					0x88,
					0x46,
					0xee,
					0xb8,
					0x14,
					0xde,
					0x5e,
					0x0b,
					0xdb,
					0xe0,
					0x32,
					0x3a,
					0x0a,
					0x49,
					0x06,
					0x24,
					0x5c,
					0xc2,
					0xd3,
					0xac,
					0x62,
					0x91,
					0x95,
					0xe4,
					0x79,
					0xe7,
					0xc8,
					0x37,
					0x6d,
					0x8d,
					0xd5,
					0x4e,
					0xa9,
					0x6c,
					0x56,
					0xf4,
					0xea,
					0x65,
					0x7a,
					0xae,
					0x08,
					0xba,
					0x78,
					0x25,
					0x2e,
					0x1c,
					0xa6,
					0xb4,
					0xc6,
					0xe8,
					0xdd,
					0x74,
					0x1f,
					0x4b,
					0xbd,
					0x8b,
					0x8a,
					0x70,
					0x3e,
					0xb5,
					0x66,
					0x48,
					0x03,
					0xf6,
					0x0e,
					0x61,
					0x35,
					0x57,
					0xb9,
					0x86,
					0xc1,
					0x1d,
					0x9e,
					0xe1,
					0xf8,
					0x98,
					0x11,
					0x69,
					0xd9,
					0x8e,
					0x94,
					0x9b,
					0x1e,
					0x87,
					0xe9,
					0xce,
					0x55,
					0x28,
					0xdf,
					0x8c,
					0xa1,
					0x89,
					0x0d,
					0xbf,
					0xe6,
					0x42,
					0x68,
					0x41,
					0x99,
					0x2d,
					0x0f,
					0xb0,
					0x54,
					0xbb,
					0x16
				]);
				var inv_s = new Uint8Array([
					0x52,
					0x09,
					0x6a,
					0xd5,
					0x30,
					0x36,
					0xa5,
					0x38,
					0xbf,
					0x40,
					0xa3,
					0x9e,
					0x81,
					0xf3,
					0xd7,
					0xfb,
					0x7c,
					0xe3,
					0x39,
					0x82,
					0x9b,
					0x2f,
					0xff,
					0x87,
					0x34,
					0x8e,
					0x43,
					0x44,
					0xc4,
					0xde,
					0xe9,
					0xcb,
					0x54,
					0x7b,
					0x94,
					0x32,
					0xa6,
					0xc2,
					0x23,
					0x3d,
					0xee,
					0x4c,
					0x95,
					0x0b,
					0x42,
					0xfa,
					0xc3,
					0x4e,
					0x08,
					0x2e,
					0xa1,
					0x66,
					0x28,
					0xd9,
					0x24,
					0xb2,
					0x76,
					0x5b,
					0xa2,
					0x49,
					0x6d,
					0x8b,
					0xd1,
					0x25,
					0x72,
					0xf8,
					0xf6,
					0x64,
					0x86,
					0x68,
					0x98,
					0x16,
					0xd4,
					0xa4,
					0x5c,
					0xcc,
					0x5d,
					0x65,
					0xb6,
					0x92,
					0x6c,
					0x70,
					0x48,
					0x50,
					0xfd,
					0xed,
					0xb9,
					0xda,
					0x5e,
					0x15,
					0x46,
					0x57,
					0xa7,
					0x8d,
					0x9d,
					0x84,
					0x90,
					0xd8,
					0xab,
					0x00,
					0x8c,
					0xbc,
					0xd3,
					0x0a,
					0xf7,
					0xe4,
					0x58,
					0x05,
					0xb8,
					0xb3,
					0x45,
					0x06,
					0xd0,
					0x2c,
					0x1e,
					0x8f,
					0xca,
					0x3f,
					0x0f,
					0x02,
					0xc1,
					0xaf,
					0xbd,
					0x03,
					0x01,
					0x13,
					0x8a,
					0x6b,
					0x3a,
					0x91,
					0x11,
					0x41,
					0x4f,
					0x67,
					0xdc,
					0xea,
					0x97,
					0xf2,
					0xcf,
					0xce,
					0xf0,
					0xb4,
					0xe6,
					0x73,
					0x96,
					0xac,
					0x74,
					0x22,
					0xe7,
					0xad,
					0x35,
					0x85,
					0xe2,
					0xf9,
					0x37,
					0xe8,
					0x1c,
					0x75,
					0xdf,
					0x6e,
					0x47,
					0xf1,
					0x1a,
					0x71,
					0x1d,
					0x29,
					0xc5,
					0x89,
					0x6f,
					0xb7,
					0x62,
					0x0e,
					0xaa,
					0x18,
					0xbe,
					0x1b,
					0xfc,
					0x56,
					0x3e,
					0x4b,
					0xc6,
					0xd2,
					0x79,
					0x20,
					0x9a,
					0xdb,
					0xc0,
					0xfe,
					0x78,
					0xcd,
					0x5a,
					0xf4,
					0x1f,
					0xdd,
					0xa8,
					0x33,
					0x88,
					0x07,
					0xc7,
					0x31,
					0xb1,
					0x12,
					0x10,
					0x59,
					0x27,
					0x80,
					0xec,
					0x5f,
					0x60,
					0x51,
					0x7f,
					0xa9,
					0x19,
					0xb5,
					0x4a,
					0x0d,
					0x2d,
					0xe5,
					0x7a,
					0x9f,
					0x93,
					0xc9,
					0x9c,
					0xef,
					0xa0,
					0xe0,
					0x3b,
					0x4d,
					0xae,
					0x2a,
					0xf5,
					0xb0,
					0xc8,
					0xeb,
					0xbb,
					0x3c,
					0x83,
					0x53,
					0x99,
					0x61,
					0x17,
					0x2b,
					0x04,
					0x7e,
					0xba,
					0x77,
					0xd6,
					0x26,
					0xe1,
					0x69,
					0x14,
					0x63,
					0x55,
					0x21,
					0x0c,
					0x7d
				]);
				var mixCol = new Uint8Array(256);
				for (var i = 0; i < 256; i++) {
					if (i < 128) {
						mixCol[i] = i << 1;
					} else {
						mixCol[i] = i << 1 ^ 0x1b;
					}
				}
				var mix = new Uint32Array([
					0x00000000,
					0x0e090d0b,
					0x1c121a16,
					0x121b171d,
					0x3824342c,
					0x362d3927,
					0x24362e3a,
					0x2a3f2331,
					0x70486858,
					0x7e416553,
					0x6c5a724e,
					0x62537f45,
					0x486c5c74,
					0x4665517f,
					0x547e4662,
					0x5a774b69,
					0xe090d0b0,
					0xee99ddbb,
					0xfc82caa6,
					0xf28bc7ad,
					0xd8b4e49c,
					0xd6bde997,
					0xc4a6fe8a,
					0xcaaff381,
					0x90d8b8e8,
					0x9ed1b5e3,
					0x8ccaa2fe,
					0x82c3aff5,
					0xa8fc8cc4,
					0xa6f581cf,
					0xb4ee96d2,
					0xbae79bd9,
					0xdb3bbb7b,
					0xd532b670,
					0xc729a16d,
					0xc920ac66,
					0xe31f8f57,
					0xed16825c,
					0xff0d9541,
					0xf104984a,
					0xab73d323,
					0xa57ade28,
					0xb761c935,
					0xb968c43e,
					0x9357e70f,
					0x9d5eea04,
					0x8f45fd19,
					0x814cf012,
					0x3bab6bcb,
					0x35a266c0,
					0x27b971dd,
					0x29b07cd6,
					0x038f5fe7,
					0x0d8652ec,
					0x1f9d45f1,
					0x119448fa,
					0x4be30393,
					0x45ea0e98,
					0x57f11985,
					0x59f8148e,
					0x73c737bf,
					0x7dce3ab4,
					0x6fd52da9,
					0x61dc20a2,
					0xad766df6,
					0xa37f60fd,
					0xb16477e0,
					0xbf6d7aeb,
					0x955259da,
					0x9b5b54d1,
					0x894043cc,
					0x87494ec7,
					0xdd3e05ae,
					0xd33708a5,
					0xc12c1fb8,
					0xcf2512b3,
					0xe51a3182,
					0xeb133c89,
					0xf9082b94,
					0xf701269f,
					0x4de6bd46,
					0x43efb04d,
					0x51f4a750,
					0x5ffdaa5b,
					0x75c2896a,
					0x7bcb8461,
					0x69d0937c,
					0x67d99e77,
					0x3daed51e,
					0x33a7d815,
					0x21bccf08,
					0x2fb5c203,
					0x058ae132,
					0x0b83ec39,
					0x1998fb24,
					0x1791f62f,
					0x764dd68d,
					0x7844db86,
					0x6a5fcc9b,
					0x6456c190,
					0x4e69e2a1,
					0x4060efaa,
					0x527bf8b7,
					0x5c72f5bc,
					0x0605bed5,
					0x080cb3de,
					0x1a17a4c3,
					0x141ea9c8,
					0x3e218af9,
					0x302887f2,
					0x223390ef,
					0x2c3a9de4,
					0x96dd063d,
					0x98d40b36,
					0x8acf1c2b,
					0x84c61120,
					0xaef93211,
					0xa0f03f1a,
					0xb2eb2807,
					0xbce2250c,
					0xe6956e65,
					0xe89c636e,
					0xfa877473,
					0xf48e7978,
					0xdeb15a49,
					0xd0b85742,
					0xc2a3405f,
					0xccaa4d54,
					0x41ecdaf7,
					0x4fe5d7fc,
					0x5dfec0e1,
					0x53f7cdea,
					0x79c8eedb,
					0x77c1e3d0,
					0x65daf4cd,
					0x6bd3f9c6,
					0x31a4b2af,
					0x3fadbfa4,
					0x2db6a8b9,
					0x23bfa5b2,
					0x09808683,
					0x07898b88,
					0x15929c95,
					0x1b9b919e,
					0xa17c0a47,
					0xaf75074c,
					0xbd6e1051,
					0xb3671d5a,
					0x99583e6b,
					0x97513360,
					0x854a247d,
					0x8b432976,
					0xd134621f,
					0xdf3d6f14,
					0xcd267809,
					0xc32f7502,
					0xe9105633,
					0xe7195b38,
					0xf5024c25,
					0xfb0b412e,
					0x9ad7618c,
					0x94de6c87,
					0x86c57b9a,
					0x88cc7691,
					0xa2f355a0,
					0xacfa58ab,
					0xbee14fb6,
					0xb0e842bd,
					0xea9f09d4,
					0xe49604df,
					0xf68d13c2,
					0xf8841ec9,
					0xd2bb3df8,
					0xdcb230f3,
					0xcea927ee,
					0xc0a02ae5,
					0x7a47b13c,
					0x744ebc37,
					0x6655ab2a,
					0x685ca621,
					0x42638510,
					0x4c6a881b,
					0x5e719f06,
					0x5078920d,
					0x0a0fd964,
					0x0406d46f,
					0x161dc372,
					0x1814ce79,
					0x322bed48,
					0x3c22e043,
					0x2e39f75e,
					0x2030fa55,
					0xec9ab701,
					0xe293ba0a,
					0xf088ad17,
					0xfe81a01c,
					0xd4be832d,
					0xdab78e26,
					0xc8ac993b,
					0xc6a59430,
					0x9cd2df59,
					0x92dbd252,
					0x80c0c54f,
					0x8ec9c844,
					0xa4f6eb75,
					0xaaffe67e,
					0xb8e4f163,
					0xb6edfc68,
					0x0c0a67b1,
					0x02036aba,
					0x10187da7,
					0x1e1170ac,
					0x342e539d,
					0x3a275e96,
					0x283c498b,
					0x26354480,
					0x7c420fe9,
					0x724b02e2,
					0x605015ff,
					0x6e5918f4,
					0x44663bc5,
					0x4a6f36ce,
					0x587421d3,
					0x567d2cd8,
					0x37a10c7a,
					0x39a80171,
					0x2bb3166c,
					0x25ba1b67,
					0x0f853856,
					0x018c355d,
					0x13972240,
					0x1d9e2f4b,
					0x47e96422,
					0x49e06929,
					0x5bfb7e34,
					0x55f2733f,
					0x7fcd500e,
					0x71c45d05,
					0x63df4a18,
					0x6dd64713,
					0xd731dcca,
					0xd938d1c1,
					0xcb23c6dc,
					0xc52acbd7,
					0xef15e8e6,
					0xe11ce5ed,
					0xf307f2f0,
					0xfd0efffb,
					0xa779b492,
					0xa970b999,
					0xbb6bae84,
					0xb562a38f,
					0x9f5d80be,
					0x91548db5,
					0x834f9aa8,
					0x8d4697a3
				]);
				function expandKey128(cipherKey) {
					var b = 176, result = new Uint8Array(b);
					result.set(cipherKey);
					for (var j = 16, i = 1; j < b; ++i) {
						var t1 = result[j - 3], t2 = result[j - 2], t3 = result[j - 1], t4 = result[j - 4];
						t1 = s[t1];
						t2 = s[t2];
						t3 = s[t3];
						t4 = s[t4];
						t1 = t1 ^ rcon[i];
						for (var n = 0; n < 4; ++n) {
							result[j] = t1 ^= result[j - 16];
							j++;
							result[j] = t2 ^= result[j - 16];
							j++;
							result[j] = t3 ^= result[j - 16];
							j++;
							result[j] = t4 ^= result[j - 16];
							j++;
						}
					}
					return result;
				}
				function decrypt128(input, key) {
					var state = new Uint8Array(16);
					state.set(input);
					var i, j, k;
					var t, u, v;
					for (j = 0, k = 160; j < 16; ++j, ++k) {
						state[j] ^= key[k];
					}
					for (i = 9; i >= 1; --i) {
						t = state[13];
						state[13] = state[9];
						state[9] = state[5];
						state[5] = state[1];
						state[1] = t;
						t = state[14];
						u = state[10];
						state[14] = state[6];
						state[10] = state[2];
						state[6] = t;
						state[2] = u;
						t = state[15];
						u = state[11];
						v = state[7];
						state[15] = state[3];
						state[11] = t;
						state[7] = u;
						state[3] = v;
						for (j = 0; j < 16; ++j) {
							state[j] = inv_s[state[j]];
						}
						for (j = 0, k = i * 16; j < 16; ++j, ++k) {
							state[j] ^= key[k];
						}
						for (j = 0; j < 16; j += 4) {
							var s0 = mix[state[j]], s1 = mix[state[j + 1]], s2 = mix[state[j + 2]], s3 = mix[state[j + 3]];
							t = s0 ^ s1 >>> 8 ^ s1 << 24 ^ s2 >>> 16 ^ s2 << 16 ^ s3 >>> 24 ^ s3 << 8;
							state[j] = t >>> 24 & 0xFF;
							state[j + 1] = t >> 16 & 0xFF;
							state[j + 2] = t >> 8 & 0xFF;
							state[j + 3] = t & 0xFF;
						}
					}
					t = state[13];
					state[13] = state[9];
					state[9] = state[5];
					state[5] = state[1];
					state[1] = t;
					t = state[14];
					u = state[10];
					state[14] = state[6];
					state[10] = state[2];
					state[6] = t;
					state[2] = u;
					t = state[15];
					u = state[11];
					v = state[7];
					state[15] = state[3];
					state[11] = t;
					state[7] = u;
					state[3] = v;
					for (j = 0; j < 16; ++j) {
						state[j] = inv_s[state[j]];
						state[j] ^= key[j];
					}
					return state;
				}
				function encrypt128(input, key) {
					var t, u, v, k;
					var state = new Uint8Array(16);
					state.set(input);
					for (j = 0; j < 16; ++j) {
						state[j] ^= key[j];
					}
					for (i = 1; i < 10; i++) {
						for (j = 0; j < 16; ++j) {
							state[j] = s[state[j]];
						}
						v = state[1];
						state[1] = state[5];
						state[5] = state[9];
						state[9] = state[13];
						state[13] = v;
						v = state[2];
						u = state[6];
						state[2] = state[10];
						state[6] = state[14];
						state[10] = v;
						state[14] = u;
						v = state[3];
						u = state[7];
						t = state[11];
						state[3] = state[15];
						state[7] = v;
						state[11] = u;
						state[15] = t;
						for (var j = 0; j < 16; j += 4) {
							var s0 = state[j + 0], s1 = state[j + 1];
							var s2 = state[j + 2], s3 = state[j + 3];
							t = s0 ^ s1 ^ s2 ^ s3;
							state[j + 0] ^= t ^ mixCol[s0 ^ s1];
							state[j + 1] ^= t ^ mixCol[s1 ^ s2];
							state[j + 2] ^= t ^ mixCol[s2 ^ s3];
							state[j + 3] ^= t ^ mixCol[s3 ^ s0];
						}
						for (j = 0, k = i * 16; j < 16; ++j, ++k) {
							state[j] ^= key[k];
						}
					}
					for (j = 0; j < 16; ++j) {
						state[j] = s[state[j]];
					}
					v = state[1];
					state[1] = state[5];
					state[5] = state[9];
					state[9] = state[13];
					state[13] = v;
					v = state[2];
					u = state[6];
					state[2] = state[10];
					state[6] = state[14];
					state[10] = v;
					state[14] = u;
					v = state[3];
					u = state[7];
					t = state[11];
					state[3] = state[15];
					state[7] = v;
					state[11] = u;
					state[15] = t;
					for (j = 0, k = 160; j < 16; ++j, ++k) {
						state[j] ^= key[k];
					}
					return state;
				}
				function AES128Cipher(key) {
					this.key = expandKey128(key);
					this.buffer = new Uint8Array(16);
					this.bufferPosition = 0;
				}
				function decryptBlock2(data, finalize) {
					var i, j, ii, sourceLength = data.length, buffer = this.buffer, bufferLength = this.bufferPosition, result = [], iv = this.iv;
					for (i = 0; i < sourceLength; ++i) {
						buffer[bufferLength] = data[i];
						++bufferLength;
						if (bufferLength < 16) {
							continue;
						}
						var plain = decrypt128(buffer, this.key);
						for (j = 0; j < 16; ++j) {
							plain[j] ^= iv[j];
						}
						iv = buffer;
						result.push(plain);
						buffer = new Uint8Array(16);
						bufferLength = 0;
					}
					this.buffer = buffer;
					this.bufferLength = bufferLength;
					this.iv = iv;
					if (result.length === 0) {
						return new Uint8Array([]);
					}
					var outputLength = 16 * result.length;
					if (finalize) {
						var lastBlock = result[result.length - 1];
						var psLen = lastBlock[15];
						if (psLen <= 16) {
							for (i = 15, ii = 16 - psLen; i >= ii; --i) {
								if (lastBlock[i] !== psLen) {
									psLen = 0;
									break;
								}
							}
							outputLength -= psLen;
							result[result.length - 1] = lastBlock.subarray(0, 16 - psLen);
						}
					}
					var output = new Uint8Array(outputLength);
					for (i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) {
						output.set(result[i], j);
					}
					return output;
				}
				AES128Cipher.prototype = {
					decryptBlock: function AES128Cipher_decryptBlock(data, finalize) {
						var i, sourceLength = data.length;
						var buffer = this.buffer, bufferLength = this.bufferPosition;
						for (i = 0; bufferLength < 16 && i < sourceLength; ++i, ++bufferLength) {
							buffer[bufferLength] = data[i];
						}
						if (bufferLength < 16) {
							this.bufferLength = bufferLength;
							return new Uint8Array([]);
						}
						this.iv = buffer;
						this.buffer = new Uint8Array(16);
						this.bufferLength = 0;
						this.decryptBlock = decryptBlock2;
						return this.decryptBlock(data.subarray(16), finalize);
					},
					encrypt: function AES128Cipher_encrypt(data, iv) {
						var i, j, ii, sourceLength = data.length, buffer = this.buffer, bufferLength = this.bufferPosition, result = [];
						if (!iv) {
							iv = new Uint8Array(16);
						}
						for (i = 0; i < sourceLength; ++i) {
							buffer[bufferLength] = data[i];
							++bufferLength;
							if (bufferLength < 16) {
								continue;
							}
							for (j = 0; j < 16; ++j) {
								buffer[j] ^= iv[j];
							}
							var cipher = encrypt128(buffer, this.key);
							iv = cipher;
							result.push(cipher);
							buffer = new Uint8Array(16);
							bufferLength = 0;
						}
						this.buffer = buffer;
						this.bufferLength = bufferLength;
						this.iv = iv;
						if (result.length === 0) {
							return new Uint8Array([]);
						}
						var outputLength = 16 * result.length;
						var output = new Uint8Array(outputLength);
						for (i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) {
							output.set(result[i], j);
						}
						return output;
					}
				};
				return AES128Cipher;
			}();
			var AES256Cipher = function AES256CipherClosure() {
				var s = new Uint8Array([
					0x63,
					0x7c,
					0x77,
					0x7b,
					0xf2,
					0x6b,
					0x6f,
					0xc5,
					0x30,
					0x01,
					0x67,
					0x2b,
					0xfe,
					0xd7,
					0xab,
					0x76,
					0xca,
					0x82,
					0xc9,
					0x7d,
					0xfa,
					0x59,
					0x47,
					0xf0,
					0xad,
					0xd4,
					0xa2,
					0xaf,
					0x9c,
					0xa4,
					0x72,
					0xc0,
					0xb7,
					0xfd,
					0x93,
					0x26,
					0x36,
					0x3f,
					0xf7,
					0xcc,
					0x34,
					0xa5,
					0xe5,
					0xf1,
					0x71,
					0xd8,
					0x31,
					0x15,
					0x04,
					0xc7,
					0x23,
					0xc3,
					0x18,
					0x96,
					0x05,
					0x9a,
					0x07,
					0x12,
					0x80,
					0xe2,
					0xeb,
					0x27,
					0xb2,
					0x75,
					0x09,
					0x83,
					0x2c,
					0x1a,
					0x1b,
					0x6e,
					0x5a,
					0xa0,
					0x52,
					0x3b,
					0xd6,
					0xb3,
					0x29,
					0xe3,
					0x2f,
					0x84,
					0x53,
					0xd1,
					0x00,
					0xed,
					0x20,
					0xfc,
					0xb1,
					0x5b,
					0x6a,
					0xcb,
					0xbe,
					0x39,
					0x4a,
					0x4c,
					0x58,
					0xcf,
					0xd0,
					0xef,
					0xaa,
					0xfb,
					0x43,
					0x4d,
					0x33,
					0x85,
					0x45,
					0xf9,
					0x02,
					0x7f,
					0x50,
					0x3c,
					0x9f,
					0xa8,
					0x51,
					0xa3,
					0x40,
					0x8f,
					0x92,
					0x9d,
					0x38,
					0xf5,
					0xbc,
					0xb6,
					0xda,
					0x21,
					0x10,
					0xff,
					0xf3,
					0xd2,
					0xcd,
					0x0c,
					0x13,
					0xec,
					0x5f,
					0x97,
					0x44,
					0x17,
					0xc4,
					0xa7,
					0x7e,
					0x3d,
					0x64,
					0x5d,
					0x19,
					0x73,
					0x60,
					0x81,
					0x4f,
					0xdc,
					0x22,
					0x2a,
					0x90,
					0x88,
					0x46,
					0xee,
					0xb8,
					0x14,
					0xde,
					0x5e,
					0x0b,
					0xdb,
					0xe0,
					0x32,
					0x3a,
					0x0a,
					0x49,
					0x06,
					0x24,
					0x5c,
					0xc2,
					0xd3,
					0xac,
					0x62,
					0x91,
					0x95,
					0xe4,
					0x79,
					0xe7,
					0xc8,
					0x37,
					0x6d,
					0x8d,
					0xd5,
					0x4e,
					0xa9,
					0x6c,
					0x56,
					0xf4,
					0xea,
					0x65,
					0x7a,
					0xae,
					0x08,
					0xba,
					0x78,
					0x25,
					0x2e,
					0x1c,
					0xa6,
					0xb4,
					0xc6,
					0xe8,
					0xdd,
					0x74,
					0x1f,
					0x4b,
					0xbd,
					0x8b,
					0x8a,
					0x70,
					0x3e,
					0xb5,
					0x66,
					0x48,
					0x03,
					0xf6,
					0x0e,
					0x61,
					0x35,
					0x57,
					0xb9,
					0x86,
					0xc1,
					0x1d,
					0x9e,
					0xe1,
					0xf8,
					0x98,
					0x11,
					0x69,
					0xd9,
					0x8e,
					0x94,
					0x9b,
					0x1e,
					0x87,
					0xe9,
					0xce,
					0x55,
					0x28,
					0xdf,
					0x8c,
					0xa1,
					0x89,
					0x0d,
					0xbf,
					0xe6,
					0x42,
					0x68,
					0x41,
					0x99,
					0x2d,
					0x0f,
					0xb0,
					0x54,
					0xbb,
					0x16
				]);
				var inv_s = new Uint8Array([
					0x52,
					0x09,
					0x6a,
					0xd5,
					0x30,
					0x36,
					0xa5,
					0x38,
					0xbf,
					0x40,
					0xa3,
					0x9e,
					0x81,
					0xf3,
					0xd7,
					0xfb,
					0x7c,
					0xe3,
					0x39,
					0x82,
					0x9b,
					0x2f,
					0xff,
					0x87,
					0x34,
					0x8e,
					0x43,
					0x44,
					0xc4,
					0xde,
					0xe9,
					0xcb,
					0x54,
					0x7b,
					0x94,
					0x32,
					0xa6,
					0xc2,
					0x23,
					0x3d,
					0xee,
					0x4c,
					0x95,
					0x0b,
					0x42,
					0xfa,
					0xc3,
					0x4e,
					0x08,
					0x2e,
					0xa1,
					0x66,
					0x28,
					0xd9,
					0x24,
					0xb2,
					0x76,
					0x5b,
					0xa2,
					0x49,
					0x6d,
					0x8b,
					0xd1,
					0x25,
					0x72,
					0xf8,
					0xf6,
					0x64,
					0x86,
					0x68,
					0x98,
					0x16,
					0xd4,
					0xa4,
					0x5c,
					0xcc,
					0x5d,
					0x65,
					0xb6,
					0x92,
					0x6c,
					0x70,
					0x48,
					0x50,
					0xfd,
					0xed,
					0xb9,
					0xda,
					0x5e,
					0x15,
					0x46,
					0x57,
					0xa7,
					0x8d,
					0x9d,
					0x84,
					0x90,
					0xd8,
					0xab,
					0x00,
					0x8c,
					0xbc,
					0xd3,
					0x0a,
					0xf7,
					0xe4,
					0x58,
					0x05,
					0xb8,
					0xb3,
					0x45,
					0x06,
					0xd0,
					0x2c,
					0x1e,
					0x8f,
					0xca,
					0x3f,
					0x0f,
					0x02,
					0xc1,
					0xaf,
					0xbd,
					0x03,
					0x01,
					0x13,
					0x8a,
					0x6b,
					0x3a,
					0x91,
					0x11,
					0x41,
					0x4f,
					0x67,
					0xdc,
					0xea,
					0x97,
					0xf2,
					0xcf,
					0xce,
					0xf0,
					0xb4,
					0xe6,
					0x73,
					0x96,
					0xac,
					0x74,
					0x22,
					0xe7,
					0xad,
					0x35,
					0x85,
					0xe2,
					0xf9,
					0x37,
					0xe8,
					0x1c,
					0x75,
					0xdf,
					0x6e,
					0x47,
					0xf1,
					0x1a,
					0x71,
					0x1d,
					0x29,
					0xc5,
					0x89,
					0x6f,
					0xb7,
					0x62,
					0x0e,
					0xaa,
					0x18,
					0xbe,
					0x1b,
					0xfc,
					0x56,
					0x3e,
					0x4b,
					0xc6,
					0xd2,
					0x79,
					0x20,
					0x9a,
					0xdb,
					0xc0,
					0xfe,
					0x78,
					0xcd,
					0x5a,
					0xf4,
					0x1f,
					0xdd,
					0xa8,
					0x33,
					0x88,
					0x07,
					0xc7,
					0x31,
					0xb1,
					0x12,
					0x10,
					0x59,
					0x27,
					0x80,
					0xec,
					0x5f,
					0x60,
					0x51,
					0x7f,
					0xa9,
					0x19,
					0xb5,
					0x4a,
					0x0d,
					0x2d,
					0xe5,
					0x7a,
					0x9f,
					0x93,
					0xc9,
					0x9c,
					0xef,
					0xa0,
					0xe0,
					0x3b,
					0x4d,
					0xae,
					0x2a,
					0xf5,
					0xb0,
					0xc8,
					0xeb,
					0xbb,
					0x3c,
					0x83,
					0x53,
					0x99,
					0x61,
					0x17,
					0x2b,
					0x04,
					0x7e,
					0xba,
					0x77,
					0xd6,
					0x26,
					0xe1,
					0x69,
					0x14,
					0x63,
					0x55,
					0x21,
					0x0c,
					0x7d
				]);
				var mixCol = new Uint8Array(256);
				for (var i = 0; i < 256; i++) {
					if (i < 128) {
						mixCol[i] = i << 1;
					} else {
						mixCol[i] = i << 1 ^ 0x1b;
					}
				}
				var mix = new Uint32Array([
					0x00000000,
					0x0e090d0b,
					0x1c121a16,
					0x121b171d,
					0x3824342c,
					0x362d3927,
					0x24362e3a,
					0x2a3f2331,
					0x70486858,
					0x7e416553,
					0x6c5a724e,
					0x62537f45,
					0x486c5c74,
					0x4665517f,
					0x547e4662,
					0x5a774b69,
					0xe090d0b0,
					0xee99ddbb,
					0xfc82caa6,
					0xf28bc7ad,
					0xd8b4e49c,
					0xd6bde997,
					0xc4a6fe8a,
					0xcaaff381,
					0x90d8b8e8,
					0x9ed1b5e3,
					0x8ccaa2fe,
					0x82c3aff5,
					0xa8fc8cc4,
					0xa6f581cf,
					0xb4ee96d2,
					0xbae79bd9,
					0xdb3bbb7b,
					0xd532b670,
					0xc729a16d,
					0xc920ac66,
					0xe31f8f57,
					0xed16825c,
					0xff0d9541,
					0xf104984a,
					0xab73d323,
					0xa57ade28,
					0xb761c935,
					0xb968c43e,
					0x9357e70f,
					0x9d5eea04,
					0x8f45fd19,
					0x814cf012,
					0x3bab6bcb,
					0x35a266c0,
					0x27b971dd,
					0x29b07cd6,
					0x038f5fe7,
					0x0d8652ec,
					0x1f9d45f1,
					0x119448fa,
					0x4be30393,
					0x45ea0e98,
					0x57f11985,
					0x59f8148e,
					0x73c737bf,
					0x7dce3ab4,
					0x6fd52da9,
					0x61dc20a2,
					0xad766df6,
					0xa37f60fd,
					0xb16477e0,
					0xbf6d7aeb,
					0x955259da,
					0x9b5b54d1,
					0x894043cc,
					0x87494ec7,
					0xdd3e05ae,
					0xd33708a5,
					0xc12c1fb8,
					0xcf2512b3,
					0xe51a3182,
					0xeb133c89,
					0xf9082b94,
					0xf701269f,
					0x4de6bd46,
					0x43efb04d,
					0x51f4a750,
					0x5ffdaa5b,
					0x75c2896a,
					0x7bcb8461,
					0x69d0937c,
					0x67d99e77,
					0x3daed51e,
					0x33a7d815,
					0x21bccf08,
					0x2fb5c203,
					0x058ae132,
					0x0b83ec39,
					0x1998fb24,
					0x1791f62f,
					0x764dd68d,
					0x7844db86,
					0x6a5fcc9b,
					0x6456c190,
					0x4e69e2a1,
					0x4060efaa,
					0x527bf8b7,
					0x5c72f5bc,
					0x0605bed5,
					0x080cb3de,
					0x1a17a4c3,
					0x141ea9c8,
					0x3e218af9,
					0x302887f2,
					0x223390ef,
					0x2c3a9de4,
					0x96dd063d,
					0x98d40b36,
					0x8acf1c2b,
					0x84c61120,
					0xaef93211,
					0xa0f03f1a,
					0xb2eb2807,
					0xbce2250c,
					0xe6956e65,
					0xe89c636e,
					0xfa877473,
					0xf48e7978,
					0xdeb15a49,
					0xd0b85742,
					0xc2a3405f,
					0xccaa4d54,
					0x41ecdaf7,
					0x4fe5d7fc,
					0x5dfec0e1,
					0x53f7cdea,
					0x79c8eedb,
					0x77c1e3d0,
					0x65daf4cd,
					0x6bd3f9c6,
					0x31a4b2af,
					0x3fadbfa4,
					0x2db6a8b9,
					0x23bfa5b2,
					0x09808683,
					0x07898b88,
					0x15929c95,
					0x1b9b919e,
					0xa17c0a47,
					0xaf75074c,
					0xbd6e1051,
					0xb3671d5a,
					0x99583e6b,
					0x97513360,
					0x854a247d,
					0x8b432976,
					0xd134621f,
					0xdf3d6f14,
					0xcd267809,
					0xc32f7502,
					0xe9105633,
					0xe7195b38,
					0xf5024c25,
					0xfb0b412e,
					0x9ad7618c,
					0x94de6c87,
					0x86c57b9a,
					0x88cc7691,
					0xa2f355a0,
					0xacfa58ab,
					0xbee14fb6,
					0xb0e842bd,
					0xea9f09d4,
					0xe49604df,
					0xf68d13c2,
					0xf8841ec9,
					0xd2bb3df8,
					0xdcb230f3,
					0xcea927ee,
					0xc0a02ae5,
					0x7a47b13c,
					0x744ebc37,
					0x6655ab2a,
					0x685ca621,
					0x42638510,
					0x4c6a881b,
					0x5e719f06,
					0x5078920d,
					0x0a0fd964,
					0x0406d46f,
					0x161dc372,
					0x1814ce79,
					0x322bed48,
					0x3c22e043,
					0x2e39f75e,
					0x2030fa55,
					0xec9ab701,
					0xe293ba0a,
					0xf088ad17,
					0xfe81a01c,
					0xd4be832d,
					0xdab78e26,
					0xc8ac993b,
					0xc6a59430,
					0x9cd2df59,
					0x92dbd252,
					0x80c0c54f,
					0x8ec9c844,
					0xa4f6eb75,
					0xaaffe67e,
					0xb8e4f163,
					0xb6edfc68,
					0x0c0a67b1,
					0x02036aba,
					0x10187da7,
					0x1e1170ac,
					0x342e539d,
					0x3a275e96,
					0x283c498b,
					0x26354480,
					0x7c420fe9,
					0x724b02e2,
					0x605015ff,
					0x6e5918f4,
					0x44663bc5,
					0x4a6f36ce,
					0x587421d3,
					0x567d2cd8,
					0x37a10c7a,
					0x39a80171,
					0x2bb3166c,
					0x25ba1b67,
					0x0f853856,
					0x018c355d,
					0x13972240,
					0x1d9e2f4b,
					0x47e96422,
					0x49e06929,
					0x5bfb7e34,
					0x55f2733f,
					0x7fcd500e,
					0x71c45d05,
					0x63df4a18,
					0x6dd64713,
					0xd731dcca,
					0xd938d1c1,
					0xcb23c6dc,
					0xc52acbd7,
					0xef15e8e6,
					0xe11ce5ed,
					0xf307f2f0,
					0xfd0efffb,
					0xa779b492,
					0xa970b999,
					0xbb6bae84,
					0xb562a38f,
					0x9f5d80be,
					0x91548db5,
					0x834f9aa8,
					0x8d4697a3
				]);
				function expandKey256(cipherKey) {
					var b = 240, result = new Uint8Array(b);
					var r = 1;
					result.set(cipherKey);
					for (var j = 32, i = 1; j < b; ++i) {
						if (j % 32 === 16) {
							t1 = s[t1];
							t2 = s[t2];
							t3 = s[t3];
							t4 = s[t4];
						} else if (j % 32 === 0) {
							var t1 = result[j - 3], t2 = result[j - 2], t3 = result[j - 1], t4 = result[j - 4];
							t1 = s[t1];
							t2 = s[t2];
							t3 = s[t3];
							t4 = s[t4];
							t1 = t1 ^ r;
							if ((r <<= 1) >= 256) {
								r = (r ^ 0x1b) & 0xFF;
							}
						}
						for (var n = 0; n < 4; ++n) {
							result[j] = t1 ^= result[j - 32];
							j++;
							result[j] = t2 ^= result[j - 32];
							j++;
							result[j] = t3 ^= result[j - 32];
							j++;
							result[j] = t4 ^= result[j - 32];
							j++;
						}
					}
					return result;
				}
				function decrypt256(input, key) {
					var state = new Uint8Array(16);
					state.set(input);
					var i, j, k;
					var t, u, v;
					for (j = 0, k = 224; j < 16; ++j, ++k) {
						state[j] ^= key[k];
					}
					for (i = 13; i >= 1; --i) {
						t = state[13];
						state[13] = state[9];
						state[9] = state[5];
						state[5] = state[1];
						state[1] = t;
						t = state[14];
						u = state[10];
						state[14] = state[6];
						state[10] = state[2];
						state[6] = t;
						state[2] = u;
						t = state[15];
						u = state[11];
						v = state[7];
						state[15] = state[3];
						state[11] = t;
						state[7] = u;
						state[3] = v;
						for (j = 0; j < 16; ++j) {
							state[j] = inv_s[state[j]];
						}
						for (j = 0, k = i * 16; j < 16; ++j, ++k) {
							state[j] ^= key[k];
						}
						for (j = 0; j < 16; j += 4) {
							var s0 = mix[state[j]], s1 = mix[state[j + 1]], s2 = mix[state[j + 2]], s3 = mix[state[j + 3]];
							t = s0 ^ s1 >>> 8 ^ s1 << 24 ^ s2 >>> 16 ^ s2 << 16 ^ s3 >>> 24 ^ s3 << 8;
							state[j] = t >>> 24 & 0xFF;
							state[j + 1] = t >> 16 & 0xFF;
							state[j + 2] = t >> 8 & 0xFF;
							state[j + 3] = t & 0xFF;
						}
					}
					t = state[13];
					state[13] = state[9];
					state[9] = state[5];
					state[5] = state[1];
					state[1] = t;
					t = state[14];
					u = state[10];
					state[14] = state[6];
					state[10] = state[2];
					state[6] = t;
					state[2] = u;
					t = state[15];
					u = state[11];
					v = state[7];
					state[15] = state[3];
					state[11] = t;
					state[7] = u;
					state[3] = v;
					for (j = 0; j < 16; ++j) {
						state[j] = inv_s[state[j]];
						state[j] ^= key[j];
					}
					return state;
				}
				function encrypt256(input, key) {
					var t, u, v, k;
					var state = new Uint8Array(16);
					state.set(input);
					for (j = 0; j < 16; ++j) {
						state[j] ^= key[j];
					}
					for (i = 1; i < 14; i++) {
						for (j = 0; j < 16; ++j) {
							state[j] = s[state[j]];
						}
						v = state[1];
						state[1] = state[5];
						state[5] = state[9];
						state[9] = state[13];
						state[13] = v;
						v = state[2];
						u = state[6];
						state[2] = state[10];
						state[6] = state[14];
						state[10] = v;
						state[14] = u;
						v = state[3];
						u = state[7];
						t = state[11];
						state[3] = state[15];
						state[7] = v;
						state[11] = u;
						state[15] = t;
						for (var j = 0; j < 16; j += 4) {
							var s0 = state[j + 0], s1 = state[j + 1];
							var s2 = state[j + 2], s3 = state[j + 3];
							t = s0 ^ s1 ^ s2 ^ s3;
							state[j + 0] ^= t ^ mixCol[s0 ^ s1];
							state[j + 1] ^= t ^ mixCol[s1 ^ s2];
							state[j + 2] ^= t ^ mixCol[s2 ^ s3];
							state[j + 3] ^= t ^ mixCol[s3 ^ s0];
						}
						for (j = 0, k = i * 16; j < 16; ++j, ++k) {
							state[j] ^= key[k];
						}
					}
					for (j = 0; j < 16; ++j) {
						state[j] = s[state[j]];
					}
					v = state[1];
					state[1] = state[5];
					state[5] = state[9];
					state[9] = state[13];
					state[13] = v;
					v = state[2];
					u = state[6];
					state[2] = state[10];
					state[6] = state[14];
					state[10] = v;
					state[14] = u;
					v = state[3];
					u = state[7];
					t = state[11];
					state[3] = state[15];
					state[7] = v;
					state[11] = u;
					state[15] = t;
					for (j = 0, k = 224; j < 16; ++j, ++k) {
						state[j] ^= key[k];
					}
					return state;
				}
				function AES256Cipher(key) {
					this.key = expandKey256(key);
					this.buffer = new Uint8Array(16);
					this.bufferPosition = 0;
				}
				function decryptBlock2(data, finalize) {
					var i, j, ii, sourceLength = data.length, buffer = this.buffer, bufferLength = this.bufferPosition, result = [], iv = this.iv;
					for (i = 0; i < sourceLength; ++i) {
						buffer[bufferLength] = data[i];
						++bufferLength;
						if (bufferLength < 16) {
							continue;
						}
						var plain = decrypt256(buffer, this.key);
						for (j = 0; j < 16; ++j) {
							plain[j] ^= iv[j];
						}
						iv = buffer;
						result.push(plain);
						buffer = new Uint8Array(16);
						bufferLength = 0;
					}
					this.buffer = buffer;
					this.bufferLength = bufferLength;
					this.iv = iv;
					if (result.length === 0) {
						return new Uint8Array([]);
					}
					var outputLength = 16 * result.length;
					if (finalize) {
						var lastBlock = result[result.length - 1];
						var psLen = lastBlock[15];
						if (psLen <= 16) {
							for (i = 15, ii = 16 - psLen; i >= ii; --i) {
								if (lastBlock[i] !== psLen) {
									psLen = 0;
									break;
								}
							}
							outputLength -= psLen;
							result[result.length - 1] = lastBlock.subarray(0, 16 - psLen);
						}
					}
					var output = new Uint8Array(outputLength);
					for (i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) {
						output.set(result[i], j);
					}
					return output;
				}
				AES256Cipher.prototype = {
					decryptBlock: function AES256Cipher_decryptBlock(data, finalize, iv) {
						var i, sourceLength = data.length;
						var buffer = this.buffer, bufferLength = this.bufferPosition;
						if (iv) {
							this.iv = iv;
						} else {
							for (i = 0; bufferLength < 16 && i < sourceLength; ++i, ++bufferLength) {
								buffer[bufferLength] = data[i];
							}
							if (bufferLength < 16) {
								this.bufferLength = bufferLength;
								return new Uint8Array([]);
							}
							this.iv = buffer;
							data = data.subarray(16);
						}
						this.buffer = new Uint8Array(16);
						this.bufferLength = 0;
						this.decryptBlock = decryptBlock2;
						return this.decryptBlock(data, finalize);
					},
					encrypt: function AES256Cipher_encrypt(data, iv) {
						var i, j, ii, sourceLength = data.length, buffer = this.buffer, bufferLength = this.bufferPosition, result = [];
						if (!iv) {
							iv = new Uint8Array(16);
						}
						for (i = 0; i < sourceLength; ++i) {
							buffer[bufferLength] = data[i];
							++bufferLength;
							if (bufferLength < 16) {
								continue;
							}
							for (j = 0; j < 16; ++j) {
								buffer[j] ^= iv[j];
							}
							var cipher = encrypt256(buffer, this.key);
							this.iv = cipher;
							result.push(cipher);
							buffer = new Uint8Array(16);
							bufferLength = 0;
						}
						this.buffer = buffer;
						this.bufferLength = bufferLength;
						this.iv = iv;
						if (result.length === 0) {
							return new Uint8Array([]);
						}
						var outputLength = 16 * result.length;
						var output = new Uint8Array(outputLength);
						for (i = 0, j = 0, ii = result.length; i < ii; ++i, j += 16) {
							output.set(result[i], j);
						}
						return output;
					}
				};
				return AES256Cipher;
			}();
			var PDF17 = function PDF17Closure() {
				function compareByteArrays(array1, array2) {
					if (array1.length !== array2.length) {
						return false;
					}
					for (var i = 0; i < array1.length; i++) {
						if (array1[i] !== array2[i]) {
							return false;
						}
					}
					return true;
				}
				function PDF17() {
				}
				PDF17.prototype = {
					checkOwnerPassword: function PDF17_checkOwnerPassword(password, ownerValidationSalt, userBytes, ownerPassword) {
						var hashData = new Uint8Array(password.length + 56);
						hashData.set(password, 0);
						hashData.set(ownerValidationSalt, password.length);
						hashData.set(userBytes, password.length + ownerValidationSalt.length);
						var result = calculateSHA256(hashData, 0, hashData.length);
						return compareByteArrays(result, ownerPassword);
					},
					checkUserPassword: function PDF17_checkUserPassword(password, userValidationSalt, userPassword) {
						var hashData = new Uint8Array(password.length + 8);
						hashData.set(password, 0);
						hashData.set(userValidationSalt, password.length);
						var result = calculateSHA256(hashData, 0, hashData.length);
						return compareByteArrays(result, userPassword);
					},
					getOwnerKey: function PDF17_getOwnerKey(password, ownerKeySalt, userBytes, ownerEncryption) {
						var hashData = new Uint8Array(password.length + 56);
						hashData.set(password, 0);
						hashData.set(ownerKeySalt, password.length);
						hashData.set(userBytes, password.length + ownerKeySalt.length);
						var key = calculateSHA256(hashData, 0, hashData.length);
						var cipher = new AES256Cipher(key);
						return cipher.decryptBlock(ownerEncryption, false, new Uint8Array(16));
					},
					getUserKey: function PDF17_getUserKey(password, userKeySalt, userEncryption) {
						var hashData = new Uint8Array(password.length + 8);
						hashData.set(password, 0);
						hashData.set(userKeySalt, password.length);
						var key = calculateSHA256(hashData, 0, hashData.length);
						var cipher = new AES256Cipher(key);
						return cipher.decryptBlock(userEncryption, false, new Uint8Array(16));
					}
				};
				return PDF17;
			}();
			var PDF20 = function PDF20Closure() {
				function concatArrays(array1, array2) {
					var t = new Uint8Array(array1.length + array2.length);
					t.set(array1, 0);
					t.set(array2, array1.length);
					return t;
				}
				function calculatePDF20Hash(password, input, userBytes) {
					var k = calculateSHA256(input, 0, input.length).subarray(0, 32);
					var e = [0];
					var i = 0;
					while (i < 64 || e[e.length - 1] > i - 32) {
						var arrayLength = password.length + k.length + userBytes.length;
						var k1 = new Uint8Array(arrayLength * 64);
						var array = concatArrays(password, k);
						array = concatArrays(array, userBytes);
						for (var j = 0, pos = 0; j < 64; j++, pos += arrayLength) {
							k1.set(array, pos);
						}
						var cipher = new AES128Cipher(k.subarray(0, 16));
						e = cipher.encrypt(k1, k.subarray(16, 32));
						var remainder = 0;
						for (var z = 0; z < 16; z++) {
							remainder *= 256 % 3;
							remainder %= 3;
							remainder += (e[z] >>> 0) % 3;
							remainder %= 3;
						}
						if (remainder === 0) {
							k = calculateSHA256(e, 0, e.length);
						} else if (remainder === 1) {
							k = calculateSHA384(e, 0, e.length);
						} else if (remainder === 2) {
							k = calculateSHA512(e, 0, e.length);
						}
						i++;
					}
					return k.subarray(0, 32);
				}
				function PDF20() {
				}
				function compareByteArrays(array1, array2) {
					if (array1.length !== array2.length) {
						return false;
					}
					for (var i = 0; i < array1.length; i++) {
						if (array1[i] !== array2[i]) {
							return false;
						}
					}
					return true;
				}
				PDF20.prototype = {
					hash: function PDF20_hash(password, concatBytes, userBytes) {
						return calculatePDF20Hash(password, concatBytes, userBytes);
					},
					checkOwnerPassword: function PDF20_checkOwnerPassword(password, ownerValidationSalt, userBytes, ownerPassword) {
						var hashData = new Uint8Array(password.length + 56);
						hashData.set(password, 0);
						hashData.set(ownerValidationSalt, password.length);
						hashData.set(userBytes, password.length + ownerValidationSalt.length);
						var result = calculatePDF20Hash(password, hashData, userBytes);
						return compareByteArrays(result, ownerPassword);
					},
					checkUserPassword: function PDF20_checkUserPassword(password, userValidationSalt, userPassword) {
						var hashData = new Uint8Array(password.length + 8);
						hashData.set(password, 0);
						hashData.set(userValidationSalt, password.length);
						var result = calculatePDF20Hash(password, hashData, []);
						return compareByteArrays(result, userPassword);
					},
					getOwnerKey: function PDF20_getOwnerKey(password, ownerKeySalt, userBytes, ownerEncryption) {
						var hashData = new Uint8Array(password.length + 56);
						hashData.set(password, 0);
						hashData.set(ownerKeySalt, password.length);
						hashData.set(userBytes, password.length + ownerKeySalt.length);
						var key = calculatePDF20Hash(password, hashData, userBytes);
						var cipher = new AES256Cipher(key);
						return cipher.decryptBlock(ownerEncryption, false, new Uint8Array(16));
					},
					getUserKey: function PDF20_getUserKey(password, userKeySalt, userEncryption) {
						var hashData = new Uint8Array(password.length + 8);
						hashData.set(password, 0);
						hashData.set(userKeySalt, password.length);
						var key = calculatePDF20Hash(password, hashData, []);
						var cipher = new AES256Cipher(key);
						return cipher.decryptBlock(userEncryption, false, new Uint8Array(16));
					}
				};
				return PDF20;
			}();
			var CipherTransform = function CipherTransformClosure() {
				function CipherTransform(stringCipherConstructor, streamCipherConstructor) {
					this.StringCipherConstructor = stringCipherConstructor;
					this.StreamCipherConstructor = streamCipherConstructor;
				}
				CipherTransform.prototype = {
					createStream: function CipherTransform_createStream(stream, length) {
						var cipher = new this.StreamCipherConstructor();
						return new DecryptStream(stream, length, function cipherTransformDecryptStream(data, finalize) {
							return cipher.decryptBlock(data, finalize);
						});
					},
					decryptString: function CipherTransform_decryptString(s) {
						var cipher = new this.StringCipherConstructor();
						var data = stringToBytes(s);
						data = cipher.decryptBlock(data, true);
						return bytesToString(data);
					}
				};
				return CipherTransform;
			}();
			var CipherTransformFactory = function CipherTransformFactoryClosure() {
				var defaultPasswordBytes = new Uint8Array([
					0x28,
					0xBF,
					0x4E,
					0x5E,
					0x4E,
					0x75,
					0x8A,
					0x41,
					0x64,
					0x00,
					0x4E,
					0x56,
					0xFF,
					0xFA,
					0x01,
					0x08,
					0x2E,
					0x2E,
					0x00,
					0xB6,
					0xD0,
					0x68,
					0x3E,
					0x80,
					0x2F,
					0x0C,
					0xA9,
					0xFE,
					0x64,
					0x53,
					0x69,
					0x7A
				]);
				function createEncryptionKey20(revision, password, ownerPassword, ownerValidationSalt, ownerKeySalt, uBytes, userPassword, userValidationSalt, userKeySalt, ownerEncryption, userEncryption, perms) {
					if (password) {
						var passwordLength = Math.min(127, password.length);
						password = password.subarray(0, passwordLength);
					} else {
						password = [];
					}
					var pdfAlgorithm;
					if (revision === 6) {
						pdfAlgorithm = new PDF20();
					} else {
						pdfAlgorithm = new PDF17();
					}
					if (pdfAlgorithm.checkUserPassword(password, userValidationSalt, userPassword)) {
						return pdfAlgorithm.getUserKey(password, userKeySalt, userEncryption);
					} else if (password.length && pdfAlgorithm.checkOwnerPassword(password, ownerValidationSalt, uBytes, ownerPassword)) {
						return pdfAlgorithm.getOwnerKey(password, ownerKeySalt, uBytes, ownerEncryption);
					}
					return null;
				}
				function prepareKeyData(fileId, password, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata) {
					var hashDataSize = 40 + ownerPassword.length + fileId.length;
					var hashData = new Uint8Array(hashDataSize), i = 0, j, n;
					if (password) {
						n = Math.min(32, password.length);
						for (; i < n; ++i) {
							hashData[i] = password[i];
						}
					}
					j = 0;
					while (i < 32) {
						hashData[i++] = defaultPasswordBytes[j++];
					}
					for (j = 0, n = ownerPassword.length; j < n; ++j) {
						hashData[i++] = ownerPassword[j];
					}
					hashData[i++] = flags & 0xFF;
					hashData[i++] = flags >> 8 & 0xFF;
					hashData[i++] = flags >> 16 & 0xFF;
					hashData[i++] = flags >>> 24 & 0xFF;
					for (j = 0, n = fileId.length; j < n; ++j) {
						hashData[i++] = fileId[j];
					}
					if (revision >= 4 && !encryptMetadata) {
						hashData[i++] = 0xFF;
						hashData[i++] = 0xFF;
						hashData[i++] = 0xFF;
						hashData[i++] = 0xFF;
					}
					var hash = calculateMD5(hashData, 0, i);
					var keyLengthInBytes = keyLength >> 3;
					if (revision >= 3) {
						for (j = 0; j < 50; ++j) {
							hash = calculateMD5(hash, 0, keyLengthInBytes);
						}
					}
					var encryptionKey = hash.subarray(0, keyLengthInBytes);
					var cipher, checkData;
					if (revision >= 3) {
						for (i = 0; i < 32; ++i) {
							hashData[i] = defaultPasswordBytes[i];
						}
						for (j = 0, n = fileId.length; j < n; ++j) {
							hashData[i++] = fileId[j];
						}
						cipher = new ARCFourCipher(encryptionKey);
						checkData = cipher.encryptBlock(calculateMD5(hashData, 0, i));
						n = encryptionKey.length;
						var derivedKey = new Uint8Array(n), k;
						for (j = 1; j <= 19; ++j) {
							for (k = 0; k < n; ++k) {
								derivedKey[k] = encryptionKey[k] ^ j;
							}
							cipher = new ARCFourCipher(derivedKey);
							checkData = cipher.encryptBlock(checkData);
						}
						for (j = 0, n = checkData.length; j < n; ++j) {
							if (userPassword[j] !== checkData[j]) {
								return null;
							}
						}
					} else {
						cipher = new ARCFourCipher(encryptionKey);
						checkData = cipher.encryptBlock(defaultPasswordBytes);
						for (j = 0, n = checkData.length; j < n; ++j) {
							if (userPassword[j] !== checkData[j]) {
								return null;
							}
						}
					}
					return encryptionKey;
				}
				function decodeUserPassword(password, ownerPassword, revision, keyLength) {
					var hashData = new Uint8Array(32), i = 0, j, n;
					n = Math.min(32, password.length);
					for (; i < n; ++i) {
						hashData[i] = password[i];
					}
					j = 0;
					while (i < 32) {
						hashData[i++] = defaultPasswordBytes[j++];
					}
					var hash = calculateMD5(hashData, 0, i);
					var keyLengthInBytes = keyLength >> 3;
					if (revision >= 3) {
						for (j = 0; j < 50; ++j) {
							hash = calculateMD5(hash, 0, hash.length);
						}
					}
					var cipher, userPassword;
					if (revision >= 3) {
						userPassword = ownerPassword;
						var derivedKey = new Uint8Array(keyLengthInBytes), k;
						for (j = 19; j >= 0; j--) {
							for (k = 0; k < keyLengthInBytes; ++k) {
								derivedKey[k] = hash[k] ^ j;
							}
							cipher = new ARCFourCipher(derivedKey);
							userPassword = cipher.encryptBlock(userPassword);
						}
					} else {
						cipher = new ARCFourCipher(hash.subarray(0, keyLengthInBytes));
						userPassword = cipher.encryptBlock(ownerPassword);
					}
					return userPassword;
				}
				var identityName = Name.get('Identity');
				function CipherTransformFactory(dict, fileId, password) {
					var filter = dict.get('Filter');
					if (!isName(filter, 'Standard')) {
						error('unknown encryption method');
					}
					this.dict = dict;
					var algorithm = dict.get('V');
					if (!isInt(algorithm) || algorithm !== 1 && algorithm !== 2 && algorithm !== 4 && algorithm !== 5) {
						error('unsupported encryption algorithm');
					}
					this.algorithm = algorithm;
					var keyLength = dict.get('Length');
					if (!keyLength) {
						if (algorithm <= 3) {
							keyLength = 40;
						} else {
							var cfDict = dict.get('CF');
							var streamCryptoName = dict.get('StmF');
							if (isDict(cfDict) && isName(streamCryptoName)) {
								cfDict.suppressEncryption = true;
								var handlerDict = cfDict.get(streamCryptoName.name);
								keyLength = handlerDict && handlerDict.get('Length') || 128;
								if (keyLength < 40) {
									keyLength <<= 3;
								}
							}
						}
					}
					if (!isInt(keyLength) || keyLength < 40 || keyLength % 8 !== 0) {
						error('invalid key length');
					}
					var ownerPassword = stringToBytes(dict.get('O')).subarray(0, 32);
					var userPassword = stringToBytes(dict.get('U')).subarray(0, 32);
					var flags = dict.get('P');
					var revision = dict.get('R');
					var encryptMetadata = (algorithm === 4 || algorithm === 5) && dict.get('EncryptMetadata') !== false;
					this.encryptMetadata = encryptMetadata;
					var fileIdBytes = stringToBytes(fileId);
					var passwordBytes;
					if (password) {
						if (revision === 6) {
							try {
								password = utf8StringToString(password);
							} catch (ex) {
								warn('CipherTransformFactory: ' + 'Unable to convert UTF8 encoded password.');
							}
						}
						passwordBytes = stringToBytes(password);
					}
					var encryptionKey;
					if (algorithm !== 5) {
						encryptionKey = prepareKeyData(fileIdBytes, passwordBytes, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata);
					} else {
						var ownerValidationSalt = stringToBytes(dict.get('O')).subarray(32, 40);
						var ownerKeySalt = stringToBytes(dict.get('O')).subarray(40, 48);
						var uBytes = stringToBytes(dict.get('U')).subarray(0, 48);
						var userValidationSalt = stringToBytes(dict.get('U')).subarray(32, 40);
						var userKeySalt = stringToBytes(dict.get('U')).subarray(40, 48);
						var ownerEncryption = stringToBytes(dict.get('OE'));
						var userEncryption = stringToBytes(dict.get('UE'));
						var perms = stringToBytes(dict.get('Perms'));
						encryptionKey = createEncryptionKey20(revision, passwordBytes, ownerPassword, ownerValidationSalt, ownerKeySalt, uBytes, userPassword, userValidationSalt, userKeySalt, ownerEncryption, userEncryption, perms);
					}
					if (!encryptionKey && !password) {
						throw new PasswordException('No password given', PasswordResponses.NEED_PASSWORD);
					} else if (!encryptionKey && password) {
						var decodedPassword = decodeUserPassword(passwordBytes, ownerPassword, revision, keyLength);
						encryptionKey = prepareKeyData(fileIdBytes, decodedPassword, ownerPassword, userPassword, flags, revision, keyLength, encryptMetadata);
					}
					if (!encryptionKey) {
						throw new PasswordException('Incorrect Password', PasswordResponses.INCORRECT_PASSWORD);
					}
					this.encryptionKey = encryptionKey;
					if (algorithm >= 4) {
						var cf = dict.get('CF');
						if (isDict(cf)) {
							cf.suppressEncryption = true;
						}
						this.cf = cf;
						this.stmf = dict.get('StmF') || identityName;
						this.strf = dict.get('StrF') || identityName;
						this.eff = dict.get('EFF') || this.stmf;
					}
				}
				function buildObjectKey(num, gen, encryptionKey, isAes) {
					var key = new Uint8Array(encryptionKey.length + 9), i, n;
					for (i = 0, n = encryptionKey.length; i < n; ++i) {
						key[i] = encryptionKey[i];
					}
					key[i++] = num & 0xFF;
					key[i++] = num >> 8 & 0xFF;
					key[i++] = num >> 16 & 0xFF;
					key[i++] = gen & 0xFF;
					key[i++] = gen >> 8 & 0xFF;
					if (isAes) {
						key[i++] = 0x73;
						key[i++] = 0x41;
						key[i++] = 0x6C;
						key[i++] = 0x54;
					}
					var hash = calculateMD5(key, 0, i);
					return hash.subarray(0, Math.min(encryptionKey.length + 5, 16));
				}
				function buildCipherConstructor(cf, name, num, gen, key) {
					assert(isName(name), 'Invalid crypt filter name.');
					var cryptFilter = cf.get(name.name);
					var cfm;
					if (cryptFilter !== null && cryptFilter !== undefined) {
						cfm = cryptFilter.get('CFM');
					}
					if (!cfm || cfm.name === 'None') {
						return function cipherTransformFactoryBuildCipherConstructorNone() {
							return new NullCipher();
						};
					}
					if (cfm.name === 'V2') {
						return function cipherTransformFactoryBuildCipherConstructorV2() {
							return new ARCFourCipher(buildObjectKey(num, gen, key, false));
						};
					}
					if (cfm.name === 'AESV2') {
						return function cipherTransformFactoryBuildCipherConstructorAESV2() {
							return new AES128Cipher(buildObjectKey(num, gen, key, true));
						};
					}
					if (cfm.name === 'AESV3') {
						return function cipherTransformFactoryBuildCipherConstructorAESV3() {
							return new AES256Cipher(key);
						};
					}
					error('Unknown crypto method');
				}
				CipherTransformFactory.prototype = {
					createCipherTransform: function CipherTransformFactory_createCipherTransform(num, gen) {
						if (this.algorithm === 4 || this.algorithm === 5) {
							return new CipherTransform(buildCipherConstructor(this.cf, this.stmf, num, gen, this.encryptionKey), buildCipherConstructor(this.cf, this.strf, num, gen, this.encryptionKey));
						}
						var key = buildObjectKey(num, gen, this.encryptionKey, false);
						var cipherConstructor = function buildCipherCipherConstructor() {
							return new ARCFourCipher(key);
						};
						return new CipherTransform(cipherConstructor, cipherConstructor);
					}
				};
				return CipherTransformFactory;
			}();
			exports.AES128Cipher = AES128Cipher;
			exports.AES256Cipher = AES256Cipher;
			exports.ARCFourCipher = ARCFourCipher;
			exports.CipherTransformFactory = CipherTransformFactory;
			exports.PDF17 = PDF17;
			exports.PDF20 = PDF20;
			exports.calculateMD5 = calculateMD5;
			exports.calculateSHA256 = calculateSHA256;
			exports.calculateSHA384 = calculateSHA384;
			exports.calculateSHA512 = calculateSHA512;

			/***/
		}),
/* 14 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var coreParser = __w_pdfjs_require__(5);
			var coreImage = __w_pdfjs_require__(27);
			var coreColorSpace = __w_pdfjs_require__(3);
			var coreMurmurHash3 = __w_pdfjs_require__(31);
			var coreFonts = __w_pdfjs_require__(26);
			var coreFunction = __w_pdfjs_require__(6);
			var corePattern = __w_pdfjs_require__(32);
			var coreCMap = __w_pdfjs_require__(23);
			var coreMetrics = __w_pdfjs_require__(30);
			var coreBidi = __w_pdfjs_require__(21);
			var coreEncodings = __w_pdfjs_require__(4);
			var coreStandardFonts = __w_pdfjs_require__(17);
			var coreUnicode = __w_pdfjs_require__(18);
			var coreGlyphList = __w_pdfjs_require__(7);
			var FONT_IDENTITY_MATRIX = sharedUtil.FONT_IDENTITY_MATRIX;
			var IDENTITY_MATRIX = sharedUtil.IDENTITY_MATRIX;
			var UNSUPPORTED_FEATURES = sharedUtil.UNSUPPORTED_FEATURES;
			var ImageKind = sharedUtil.ImageKind;
			var OPS = sharedUtil.OPS;
			var TextRenderingMode = sharedUtil.TextRenderingMode;
			var CMapCompressionType = sharedUtil.CMapCompressionType;
			var Util = sharedUtil.Util;
			var assert = sharedUtil.assert;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isNum = sharedUtil.isNum;
			var isString = sharedUtil.isString;
			var getLookupTableFactory = sharedUtil.getLookupTableFactory;
			var warn = sharedUtil.warn;
			var Dict = corePrimitives.Dict;
			var Name = corePrimitives.Name;
			var isEOF = corePrimitives.isEOF;
			var isCmd = corePrimitives.isCmd;
			var isDict = corePrimitives.isDict;
			var isName = corePrimitives.isName;
			var isRef = corePrimitives.isRef;
			var isStream = corePrimitives.isStream;
			var DecodeStream = coreStream.DecodeStream;
			var JpegStream = coreStream.JpegStream;
			var Stream = coreStream.Stream;
			var Lexer = coreParser.Lexer;
			var Parser = coreParser.Parser;
			var PDFImage = coreImage.PDFImage;
			var ColorSpace = coreColorSpace.ColorSpace;
			var MurmurHash3_64 = coreMurmurHash3.MurmurHash3_64;
			var ErrorFont = coreFonts.ErrorFont;
			var FontFlags = coreFonts.FontFlags;
			var Font = coreFonts.Font;
			var IdentityToUnicodeMap = coreFonts.IdentityToUnicodeMap;
			var ToUnicodeMap = coreFonts.ToUnicodeMap;
			var getFontType = coreFonts.getFontType;
			var isPDFFunction = coreFunction.isPDFFunction;
			var PDFFunction = coreFunction.PDFFunction;
			var Pattern = corePattern.Pattern;
			var getTilingPatternIR = corePattern.getTilingPatternIR;
			var CMapFactory = coreCMap.CMapFactory;
			var IdentityCMap = coreCMap.IdentityCMap;
			var getMetrics = coreMetrics.getMetrics;
			var bidi = coreBidi.bidi;
			var WinAnsiEncoding = coreEncodings.WinAnsiEncoding;
			var StandardEncoding = coreEncodings.StandardEncoding;
			var MacRomanEncoding = coreEncodings.MacRomanEncoding;
			var SymbolSetEncoding = coreEncodings.SymbolSetEncoding;
			var ZapfDingbatsEncoding = coreEncodings.ZapfDingbatsEncoding;
			var getEncoding = coreEncodings.getEncoding;
			var getStdFontMap = coreStandardFonts.getStdFontMap;
			var getSerifFonts = coreStandardFonts.getSerifFonts;
			var getSymbolsFonts = coreStandardFonts.getSymbolsFonts;
			var getNormalizedUnicodes = coreUnicode.getNormalizedUnicodes;
			var reverseIfRtl = coreUnicode.reverseIfRtl;
			var getUnicodeForGlyph = coreUnicode.getUnicodeForGlyph;
			var getGlyphsUnicode = coreGlyphList.getGlyphsUnicode;
			var PartialEvaluator = function PartialEvaluatorClosure() {
				var DefaultPartialEvaluatorOptions = {
					forceDataSchema: false,
					maxImageSize: -1,
					disableFontFace: false,
					disableNativeImageDecoder: false
				};
				function NativeImageDecoder(xref, resources, handler, forceDataSchema) {
					this.xref = xref;
					this.resources = resources;
					this.handler = handler;
					this.forceDataSchema = forceDataSchema;
				}
				NativeImageDecoder.prototype = {
					canDecode: function (image) {
						return image instanceof JpegStream && NativeImageDecoder.isDecodable(image, this.xref, this.resources);
					},
					decode: function (image) {
						var dict = image.dict;
						var colorSpace = dict.get('ColorSpace', 'CS');
						colorSpace = ColorSpace.parse(colorSpace, this.xref, this.resources);
						var numComps = colorSpace.numComps;
						var decodePromise = this.handler.sendWithPromise('JpegDecode', [
							image.getIR(this.forceDataSchema),
							numComps
						]);
						return decodePromise.then(function (message) {
							var data = message.data;
							return new Stream(data, 0, data.length, image.dict);
						});
					}
				};
				NativeImageDecoder.isSupported = function NativeImageDecoder_isSupported(image, xref, res) {
					var dict = image.dict;
					if (dict.has('DecodeParms') || dict.has('DP')) {
						return false;
					}
					var cs = ColorSpace.parse(dict.get('ColorSpace', 'CS'), xref, res);
					return (cs.name === 'DeviceGray' || cs.name === 'DeviceRGB') && cs.isDefaultDecode(dict.getArray('Decode', 'D'));
				};
				NativeImageDecoder.isDecodable = function NativeImageDecoder_isDecodable(image, xref, res) {
					var dict = image.dict;
					if (dict.has('DecodeParms') || dict.has('DP')) {
						return false;
					}
					var cs = ColorSpace.parse(dict.get('ColorSpace', 'CS'), xref, res);
					return (cs.numComps === 1 || cs.numComps === 3) && cs.isDefaultDecode(dict.getArray('Decode', 'D'));
				};
				function PartialEvaluator(pdfManager, xref, handler, pageIndex, idFactory, fontCache, builtInCMapCache, options) {
					this.pdfManager = pdfManager;
					this.xref = xref;
					this.handler = handler;
					this.pageIndex = pageIndex;
					this.idFactory = idFactory;
					this.fontCache = fontCache;
					this.builtInCMapCache = builtInCMapCache;
					this.options = options || DefaultPartialEvaluatorOptions;
					this.fetchBuiltInCMap = function (name) {
						var cachedCMap = builtInCMapCache[name];
						if (cachedCMap) {
							return Promise.resolve(cachedCMap);
						}
						return handler.sendWithPromise('FetchBuiltInCMap', { name: name }).then(function (data) {
							if (data.compressionType !== CMapCompressionType.NONE) {
								builtInCMapCache[name] = data;
							}
							return data;
						});
					};
				}
				var TIME_SLOT_DURATION_MS = 20;
				var CHECK_TIME_EVERY = 100;
				function TimeSlotManager() {
					this.reset();
				}
				TimeSlotManager.prototype = {
					check: function TimeSlotManager_check() {
						if (++this.checked < CHECK_TIME_EVERY) {
							return false;
						}
						this.checked = 0;
						return this.endTime <= Date.now();
					},
					reset: function TimeSlotManager_reset() {
						this.endTime = Date.now() + TIME_SLOT_DURATION_MS;
						this.checked = 0;
					}
				};
				var deferred = Promise.resolve();
				var TILING_PATTERN = 1, SHADING_PATTERN = 2;
				PartialEvaluator.prototype = {
					hasBlendModes: function PartialEvaluator_hasBlendModes(resources) {
						if (!isDict(resources)) {
							return false;
						}
						var processed = Object.create(null);
						if (resources.objId) {
							processed[resources.objId] = true;
						}
						var nodes = [resources], xref = this.xref;
						while (nodes.length) {
							var key, i, ii;
							var node = nodes.shift();
							var graphicStates = node.get('ExtGState');
							if (isDict(graphicStates)) {
								var graphicStatesKeys = graphicStates.getKeys();
								for (i = 0, ii = graphicStatesKeys.length; i < ii; i++) {
									key = graphicStatesKeys[i];
									var graphicState = graphicStates.get(key);
									var bm = graphicState.get('BM');
									if (isName(bm) && bm.name !== 'Normal') {
										return true;
									}
								}
							}
							var xObjects = node.get('XObject');
							if (!isDict(xObjects)) {
								continue;
							}
							var xObjectsKeys = xObjects.getKeys();
							for (i = 0, ii = xObjectsKeys.length; i < ii; i++) {
								key = xObjectsKeys[i];
								var xObject = xObjects.getRaw(key);
								if (isRef(xObject)) {
									if (processed[xObject.toString()]) {
										continue;
									}
									xObject = xref.fetch(xObject);
								}
								if (!isStream(xObject)) {
									continue;
								}
								if (xObject.dict.objId) {
									if (processed[xObject.dict.objId]) {
										continue;
									}
									processed[xObject.dict.objId] = true;
								}
								var xResources = xObject.dict.get('Resources');
								if (isDict(xResources) && (!xResources.objId || !processed[xResources.objId])) {
									nodes.push(xResources);
									if (xResources.objId) {
										processed[xResources.objId] = true;
									}
								}
							}
						}
						return false;
					},
					buildFormXObject: function PartialEvaluator_buildFormXObject(resources, xobj, smask, operatorList, task, initialState) {
						var matrix = xobj.dict.getArray('Matrix');
						var bbox = xobj.dict.getArray('BBox');
						var group = xobj.dict.get('Group');
						if (group) {
							var groupOptions = {
								matrix: matrix,
								bbox: bbox,
								smask: smask,
								isolated: false,
								knockout: false
							};
							var groupSubtype = group.get('S');
							var colorSpace;
							if (isName(groupSubtype, 'Transparency')) {
								groupOptions.isolated = group.get('I') || false;
								groupOptions.knockout = group.get('K') || false;
								colorSpace = group.has('CS') ? ColorSpace.parse(group.get('CS'), this.xref, resources) : null;
							}
							if (smask && smask.backdrop) {
								colorSpace = colorSpace || ColorSpace.singletons.rgb;
								smask.backdrop = colorSpace.getRgb(smask.backdrop, 0);
							}
							operatorList.addOp(OPS.beginGroup, [groupOptions]);
						}
						operatorList.addOp(OPS.paintFormXObjectBegin, [
							matrix,
							bbox
						]);
						return this.getOperatorList(xobj, task, xobj.dict.get('Resources') || resources, operatorList, initialState).then(function () {
							operatorList.addOp(OPS.paintFormXObjectEnd, []);
							if (group) {
								operatorList.addOp(OPS.endGroup, [groupOptions]);
							}
						});
					},
					buildPaintImageXObject: function PartialEvaluator_buildPaintImageXObject(resources, image, inline, operatorList, cacheKey, imageCache) {
						var self = this;
						var dict = image.dict;
						var w = dict.get('Width', 'W');
						var h = dict.get('Height', 'H');
						if (!(w && isNum(w)) || !(h && isNum(h))) {
							warn('Image dimensions are missing, or not numbers.');
							return;
						}
						var maxImageSize = this.options.maxImageSize;
						if (maxImageSize !== -1 && w * h > maxImageSize) {
							warn('Image exceeded maximum allowed size and was removed.');
							return;
						}
						var imageMask = dict.get('ImageMask', 'IM') || false;
						var imgData, args;
						if (imageMask) {
							var width = dict.get('Width', 'W');
							var height = dict.get('Height', 'H');
							var bitStrideLength = width + 7 >> 3;
							var imgArray = image.getBytes(bitStrideLength * height);
							var decode = dict.getArray('Decode', 'D');
							var inverseDecode = !!decode && decode[0] > 0;
							imgData = PDFImage.createMask(imgArray, width, height, image instanceof DecodeStream, inverseDecode);
							imgData.cached = true;
							args = [imgData];
							operatorList.addOp(OPS.paintImageMaskXObject, args);
							if (cacheKey) {
								imageCache[cacheKey] = {
									fn: OPS.paintImageMaskXObject,
									args: args
								};
							}
							return;
						}
						var softMask = dict.get('SMask', 'SM') || false;
						var mask = dict.get('Mask') || false;
						var SMALL_IMAGE_DIMENSIONS = 200;
						if (inline && !softMask && !mask && !(image instanceof JpegStream) && w + h < SMALL_IMAGE_DIMENSIONS) {
							var imageObj = new PDFImage(this.xref, resources, image, inline, null, null);
							imgData = imageObj.createImageData(true);
							operatorList.addOp(OPS.paintInlineImageXObject, [imgData]);
							return;
						}
						var useNativeImageDecoder = !this.options.disableNativeImageDecoder;
						var objId = 'img_' + this.idFactory.createObjId();
						operatorList.addDependency(objId);
						args = [
							objId,
							w,
							h
						];
						if (useNativeImageDecoder && !softMask && !mask && image instanceof JpegStream && NativeImageDecoder.isSupported(image, this.xref, resources)) {
							operatorList.addOp(OPS.paintJpegXObject, args);
							this.handler.send('obj', [
								objId,
								this.pageIndex,
								'JpegStream',
								image.getIR(this.options.forceDataSchema)
							]);
							return;
						}
						var nativeImageDecoder = null;
						if (useNativeImageDecoder && (image instanceof JpegStream || mask instanceof JpegStream || softMask instanceof JpegStream)) {
							nativeImageDecoder = new NativeImageDecoder(self.xref, resources, self.handler, self.options.forceDataSchema);
						}
						PDFImage.buildImage(self.handler, self.xref, resources, image, inline, nativeImageDecoder).then(function (imageObj) {
							var imgData = imageObj.createImageData(false);
							self.handler.send('obj', [
								objId,
								self.pageIndex,
								'Image',
								imgData
							], [imgData.data.buffer]);
						}).then(undefined, function (reason) {
							warn('Unable to decode image: ' + reason);
							self.handler.send('obj', [
								objId,
								self.pageIndex,
								'Image',
								null
							]);
						});
						operatorList.addOp(OPS.paintImageXObject, args);
						if (cacheKey) {
							imageCache[cacheKey] = {
								fn: OPS.paintImageXObject,
								args: args
							};
						}
					},
					handleSMask: function PartialEvaluator_handleSmask(smask, resources, operatorList, task, stateManager) {
						var smaskContent = smask.get('G');
						var smaskOptions = {
							subtype: smask.get('S').name,
							backdrop: smask.get('BC')
						};
						var transferObj = smask.get('TR');
						if (isPDFFunction(transferObj)) {
							var transferFn = PDFFunction.parse(this.xref, transferObj);
							var transferMap = new Uint8Array(256);
							var tmp = new Float32Array(1);
							for (var i = 0; i < 256; i++) {
								tmp[0] = i / 255;
								transferFn(tmp, 0, tmp, 0);
								transferMap[i] = tmp[0] * 255 | 0;
							}
							smaskOptions.transferMap = transferMap;
						}
						return this.buildFormXObject(resources, smaskContent, smaskOptions, operatorList, task, stateManager.state.clone());
					},
					handleTilingType: function PartialEvaluator_handleTilingType(fn, args, resources, pattern, patternDict, operatorList, task) {
						var tilingOpList = new OperatorList();
						var resourcesArray = [
							patternDict.get('Resources'),
							resources
						];
						var patternResources = Dict.merge(this.xref, resourcesArray);
						return this.getOperatorList(pattern, task, patternResources, tilingOpList).then(function () {
							operatorList.addDependencies(tilingOpList.dependencies);
							operatorList.addOp(fn, getTilingPatternIR({
								fnArray: tilingOpList.fnArray,
								argsArray: tilingOpList.argsArray
							}, patternDict, args));
						});
					},
					handleSetFont: function PartialEvaluator_handleSetFont(resources, fontArgs, fontRef, operatorList, task, state) {
						var fontName;
						if (fontArgs) {
							fontArgs = fontArgs.slice();
							fontName = fontArgs[0].name;
						}
						var self = this;
						return this.loadFont(fontName, fontRef, this.xref, resources).then(function (translated) {
							if (!translated.font.isType3Font) {
								return translated;
							}
							return translated.loadType3Data(self, resources, operatorList, task).then(function () {
								return translated;
							}, function (reason) {
								self.handler.send('UnsupportedFeature', { featureId: UNSUPPORTED_FEATURES.font });
								return new TranslatedFont('g_font_error', new ErrorFont('Type3 font load error: ' + reason), translated.font);
							});
						}).then(function (translated) {
							state.font = translated.font;
							translated.send(self.handler);
							return translated.loadedName;
						});
					},
					handleText: function PartialEvaluator_handleText(chars, state) {
						var font = state.font;
						var glyphs = font.charsToGlyphs(chars);
						var isAddToPathSet = !!(state.textRenderingMode & TextRenderingMode.ADD_TO_PATH_FLAG);
						if (font.data && (isAddToPathSet || this.options.disableFontFace)) {
							var buildPath = function (fontChar) {
								if (!font.renderer.hasBuiltPath(fontChar)) {
									var path = font.renderer.getPathJs(fontChar);
									this.handler.send('commonobj', [
										font.loadedName + '_path_' + fontChar,
										'FontPath',
										path
									]);
								}
							}.bind(this);
							for (var i = 0, ii = glyphs.length; i < ii; i++) {
								var glyph = glyphs[i];
								buildPath(glyph.fontChar);
								var accent = glyph.accent;
								if (accent && accent.fontChar) {
									buildPath(accent.fontChar);
								}
							}
						}
						return glyphs;
					},
					setGState: function PartialEvaluator_setGState(resources, gState, operatorList, task, xref, stateManager) {
						var gStateObj = [];
						var gStateKeys = gState.getKeys();
						var self = this;
						var promise = Promise.resolve();
						for (var i = 0, ii = gStateKeys.length; i < ii; i++) {
							var key = gStateKeys[i];
							var value = gState.get(key);
							switch (key) {
								case 'Type':
									break;
								case 'LW':
								case 'LC':
								case 'LJ':
								case 'ML':
								case 'D':
								case 'RI':
								case 'FL':
								case 'CA':
								case 'ca':
									gStateObj.push([
										key,
										value
									]);
									break;
								case 'Font':
									promise = promise.then(function () {
										return self.handleSetFont(resources, null, value[0], operatorList, task, stateManager.state).then(function (loadedName) {
											operatorList.addDependency(loadedName);
											gStateObj.push([
												key,
												[
													loadedName,
													value[1]
												]
											]);
										});
									});
									break;
								case 'BM':
									gStateObj.push([
										key,
										value
									]);
									break;
								case 'SMask':
									if (isName(value, 'None')) {
										gStateObj.push([
											key,
											false
										]);
										break;
									}
									if (isDict(value)) {
										promise = promise.then(function (dict) {
											return self.handleSMask(dict, resources, operatorList, task, stateManager);
										}.bind(this, value));
										gStateObj.push([
											key,
											true
										]);
									} else {
										warn('Unsupported SMask type');
									}
									break;
								case 'OP':
								case 'op':
								case 'OPM':
								case 'BG':
								case 'BG2':
								case 'UCR':
								case 'UCR2':
								case 'TR':
								case 'TR2':
								case 'HT':
								case 'SM':
								case 'SA':
								case 'AIS':
								case 'TK':
									info('graphic state operator ' + key);
									break;
								default:
									info('Unknown graphic state operator ' + key);
									break;
							}
						}
						return promise.then(function () {
							if (gStateObj.length > 0) {
								operatorList.addOp(OPS.setGState, [gStateObj]);
							}
						});
					},
					loadFont: function PartialEvaluator_loadFont(fontName, font, xref, resources) {
						function errorFont() {
							return Promise.resolve(new TranslatedFont('g_font_error', new ErrorFont('Font ' + fontName + ' is not available'), font));
						}
						var fontRef;
						if (font) {
							assert(isRef(font));
							fontRef = font;
						} else {
							var fontRes = resources.get('Font');
							if (fontRes) {
								fontRef = fontRes.getRaw(fontName);
							} else {
								warn('fontRes not available');
								return errorFont();
							}
						}
						if (!fontRef) {
							warn('fontRef not available');
							return errorFont();
						}
						if (this.fontCache.has(fontRef)) {
							return this.fontCache.get(fontRef);
						}
						font = xref.fetchIfRef(fontRef);
						if (!isDict(font)) {
							return errorFont();
						}
						if (font.translated) {
							return font.translated;
						}
						var fontCapability = createPromiseCapability();
						var preEvaluatedFont = this.preEvaluateFont(font, xref);
						var descriptor = preEvaluatedFont.descriptor;
						var fontRefIsRef = isRef(fontRef), fontID;
						if (fontRefIsRef) {
							fontID = fontRef.toString();
						}
						if (isDict(descriptor)) {
							if (!descriptor.fontAliases) {
								descriptor.fontAliases = Object.create(null);
							}
							var fontAliases = descriptor.fontAliases;
							var hash = preEvaluatedFont.hash;
							if (fontAliases[hash]) {
								var aliasFontRef = fontAliases[hash].aliasRef;
								if (fontRefIsRef && aliasFontRef && this.fontCache.has(aliasFontRef)) {
									this.fontCache.putAlias(fontRef, aliasFontRef);
									return this.fontCache.get(fontRef);
								}
							} else {
								fontAliases[hash] = { fontID: Font.getFontID() };
							}
							if (fontRefIsRef) {
								fontAliases[hash].aliasRef = fontRef;
							}
							fontID = fontAliases[hash].fontID;
						}
						if (fontRefIsRef) {
							this.fontCache.put(fontRef, fontCapability.promise);
						} else {
							if (!fontID) {
								fontID = this.idFactory.createObjId();
							}
							this.fontCache.put('id_' + fontID, fontCapability.promise);
						}
						assert(fontID, 'The "fontID" must be defined.');
						font.loadedName = 'g_' + this.pdfManager.docId + '_f' + fontID;
						font.translated = fontCapability.promise;
						var translatedPromise;
						try {
							translatedPromise = this.translateFont(preEvaluatedFont, xref);
						} catch (e) {
							translatedPromise = Promise.reject(e);
						}
						var self = this;
						translatedPromise.then(function (translatedFont) {
							if (translatedFont.fontType !== undefined) {
								var xrefFontStats = xref.stats.fontTypes;
								xrefFontStats[translatedFont.fontType] = true;
							}
							fontCapability.resolve(new TranslatedFont(font.loadedName, translatedFont, font));
						}, function (reason) {
							self.handler.send('UnsupportedFeature', { featureId: UNSUPPORTED_FEATURES.font });
							try {
								var descriptor = preEvaluatedFont.descriptor;
								var fontFile3 = descriptor && descriptor.get('FontFile3');
								var subtype = fontFile3 && fontFile3.get('Subtype');
								var fontType = getFontType(preEvaluatedFont.type, subtype && subtype.name);
								var xrefFontStats = xref.stats.fontTypes;
								xrefFontStats[fontType] = true;
							} catch (ex) {
							}
							fontCapability.resolve(new TranslatedFont(font.loadedName, new ErrorFont(reason instanceof Error ? reason.message : reason), font));
						});
						return fontCapability.promise;
					},
					buildPath: function PartialEvaluator_buildPath(operatorList, fn, args) {
						var lastIndex = operatorList.length - 1;
						if (!args) {
							args = [];
						}
						if (lastIndex < 0 || operatorList.fnArray[lastIndex] !== OPS.constructPath) {
							operatorList.addOp(OPS.constructPath, [
								[fn],
								args
							]);
						} else {
							var opArgs = operatorList.argsArray[lastIndex];
							opArgs[0].push(fn);
							Array.prototype.push.apply(opArgs[1], args);
						}
					},
					handleColorN: function PartialEvaluator_handleColorN(operatorList, fn, args, cs, patterns, resources, task, xref) {
						var patternName = args[args.length - 1];
						var pattern;
						if (isName(patternName) && (pattern = patterns.get(patternName.name))) {
							var dict = isStream(pattern) ? pattern.dict : pattern;
							var typeNum = dict.get('PatternType');
							if (typeNum === TILING_PATTERN) {
								var color = cs.base ? cs.base.getRgb(args, 0) : null;
								return this.handleTilingType(fn, color, resources, pattern, dict, operatorList, task);
							} else if (typeNum === SHADING_PATTERN) {
								var shading = dict.get('Shading');
								var matrix = dict.getArray('Matrix');
								pattern = Pattern.parseShading(shading, matrix, xref, resources, this.handler);
								operatorList.addOp(fn, pattern.getIR());
								return Promise.resolve();
							}
							return Promise.reject('Unknown PatternType: ' + typeNum);
						}
						operatorList.addOp(fn, args);
						return Promise.resolve();
					},
					getOperatorList: function PartialEvaluator_getOperatorList(stream, task, resources, operatorList, initialState) {
						var self = this;
						var xref = this.xref;
						var imageCache = Object.create(null);
						assert(operatorList);
						resources = resources || Dict.empty;
						var xobjs = resources.get('XObject') || Dict.empty;
						var patterns = resources.get('Pattern') || Dict.empty;
						var stateManager = new StateManager(initialState || new EvalState());
						var preprocessor = new EvaluatorPreprocessor(stream, xref, stateManager);
						var timeSlotManager = new TimeSlotManager();
						return new Promise(function promiseBody(resolve, reject) {
							var next = function (promise) {
								promise.then(function () {
									try {
										promiseBody(resolve, reject);
									} catch (ex) {
										reject(ex);
									}
								}, reject);
							};
							task.ensureNotTerminated();
							timeSlotManager.reset();
							var stop, operation = {}, i, ii, cs;
							while (!(stop = timeSlotManager.check())) {
								operation.args = null;
								if (!preprocessor.read(operation)) {
									break;
								}
								var args = operation.args;
								var fn = operation.fn;
								switch (fn | 0) {
									case OPS.paintXObject:
										if (args[0].code) {
											break;
										}
										var name = args[0].name;
										if (!name) {
											warn('XObject must be referred to by name.');
											continue;
										}
										if (imageCache[name] !== undefined) {
											operatorList.addOp(imageCache[name].fn, imageCache[name].args);
											args = null;
											continue;
										}
										var xobj = xobjs.get(name);
										if (xobj) {
											assert(isStream(xobj), 'XObject should be a stream');
											var type = xobj.dict.get('Subtype');
											assert(isName(type), 'XObject should have a Name subtype');
											if (type.name === 'Form') {
												stateManager.save();
												next(self.buildFormXObject(resources, xobj, null, operatorList, task, stateManager.state.clone()).then(function () {
													stateManager.restore();
												}));
												return;
											} else if (type.name === 'Image') {
												self.buildPaintImageXObject(resources, xobj, false, operatorList, name, imageCache);
												args = null;
												continue;
											} else if (type.name === 'PS') {
												info('Ignored XObject subtype PS');
												continue;
											} else {
												error('Unhandled XObject subtype ' + type.name);
											}
										}
										break;
									case OPS.setFont:
										var fontSize = args[1];
										next(self.handleSetFont(resources, args, null, operatorList, task, stateManager.state).then(function (loadedName) {
											operatorList.addDependency(loadedName);
											operatorList.addOp(OPS.setFont, [
												loadedName,
												fontSize
											]);
										}));
										return;
									case OPS.endInlineImage:
										var cacheKey = args[0].cacheKey;
										if (cacheKey) {
											var cacheEntry = imageCache[cacheKey];
											if (cacheEntry !== undefined) {
												operatorList.addOp(cacheEntry.fn, cacheEntry.args);
												args = null;
												continue;
											}
										}
										self.buildPaintImageXObject(resources, args[0], true, operatorList, cacheKey, imageCache);
										args = null;
										continue;
									case OPS.showText:
										args[0] = self.handleText(args[0], stateManager.state);
										break;
									case OPS.showSpacedText:
										var arr = args[0];
										var combinedGlyphs = [];
										var arrLength = arr.length;
										var state = stateManager.state;
										for (i = 0; i < arrLength; ++i) {
											var arrItem = arr[i];
											if (isString(arrItem)) {
												Array.prototype.push.apply(combinedGlyphs, self.handleText(arrItem, state));
											} else if (isNum(arrItem)) {
												combinedGlyphs.push(arrItem);
											}
										}
										args[0] = combinedGlyphs;
										fn = OPS.showText;
										break;
									case OPS.nextLineShowText:
										operatorList.addOp(OPS.nextLine);
										args[0] = self.handleText(args[0], stateManager.state);
										fn = OPS.showText;
										break;
									case OPS.nextLineSetSpacingShowText:
										operatorList.addOp(OPS.nextLine);
										operatorList.addOp(OPS.setWordSpacing, [args.shift()]);
										operatorList.addOp(OPS.setCharSpacing, [args.shift()]);
										args[0] = self.handleText(args[0], stateManager.state);
										fn = OPS.showText;
										break;
									case OPS.setTextRenderingMode:
										stateManager.state.textRenderingMode = args[0];
										break;
									case OPS.setFillColorSpace:
										stateManager.state.fillColorSpace = ColorSpace.parse(args[0], xref, resources);
										continue;
									case OPS.setStrokeColorSpace:
										stateManager.state.strokeColorSpace = ColorSpace.parse(args[0], xref, resources);
										continue;
									case OPS.setFillColor:
										cs = stateManager.state.fillColorSpace;
										args = cs.getRgb(args, 0);
										fn = OPS.setFillRGBColor;
										break;
									case OPS.setStrokeColor:
										cs = stateManager.state.strokeColorSpace;
										args = cs.getRgb(args, 0);
										fn = OPS.setStrokeRGBColor;
										break;
									case OPS.setFillGray:
										stateManager.state.fillColorSpace = ColorSpace.singletons.gray;
										args = ColorSpace.singletons.gray.getRgb(args, 0);
										fn = OPS.setFillRGBColor;
										break;
									case OPS.setStrokeGray:
										stateManager.state.strokeColorSpace = ColorSpace.singletons.gray;
										args = ColorSpace.singletons.gray.getRgb(args, 0);
										fn = OPS.setStrokeRGBColor;
										break;
									case OPS.setFillCMYKColor:
										stateManager.state.fillColorSpace = ColorSpace.singletons.cmyk;
										args = ColorSpace.singletons.cmyk.getRgb(args, 0);
										fn = OPS.setFillRGBColor;
										break;
									case OPS.setStrokeCMYKColor:
										stateManager.state.strokeColorSpace = ColorSpace.singletons.cmyk;
										args = ColorSpace.singletons.cmyk.getRgb(args, 0);
										fn = OPS.setStrokeRGBColor;
										break;
									case OPS.setFillRGBColor:
										stateManager.state.fillColorSpace = ColorSpace.singletons.rgb;
										args = ColorSpace.singletons.rgb.getRgb(args, 0);
										break;
									case OPS.setStrokeRGBColor:
										stateManager.state.strokeColorSpace = ColorSpace.singletons.rgb;
										args = ColorSpace.singletons.rgb.getRgb(args, 0);
										break;
									case OPS.setFillColorN:
										cs = stateManager.state.fillColorSpace;
										if (cs.name === 'Pattern') {
											next(self.handleColorN(operatorList, OPS.setFillColorN, args, cs, patterns, resources, task, xref));
											return;
										}
										args = cs.getRgb(args, 0);
										fn = OPS.setFillRGBColor;
										break;
									case OPS.setStrokeColorN:
										cs = stateManager.state.strokeColorSpace;
										if (cs.name === 'Pattern') {
											next(self.handleColorN(operatorList, OPS.setStrokeColorN, args, cs, patterns, resources, task, xref));
											return;
										}
										args = cs.getRgb(args, 0);
										fn = OPS.setStrokeRGBColor;
										break;
									case OPS.shadingFill:
										var shadingRes = resources.get('Shading');
										if (!shadingRes) {
											error('No shading resource found');
										}
										var shading = shadingRes.get(args[0].name);
										if (!shading) {
											error('No shading object found');
										}
										var shadingFill = Pattern.parseShading(shading, null, xref, resources, self.handler);
										var patternIR = shadingFill.getIR();
										args = [patternIR];
										fn = OPS.shadingFill;
										break;
									case OPS.setGState:
										var dictName = args[0];
										var extGState = resources.get('ExtGState');
										if (!isDict(extGState) || !extGState.has(dictName.name)) {
											break;
										}
										var gState = extGState.get(dictName.name);
										next(self.setGState(resources, gState, operatorList, task, xref, stateManager));
										return;
									case OPS.moveTo:
									case OPS.lineTo:
									case OPS.curveTo:
									case OPS.curveTo2:
									case OPS.curveTo3:
									case OPS.closePath:
										self.buildPath(operatorList, fn, args);
										continue;
									case OPS.rectangle:
										self.buildPath(operatorList, fn, args);
										continue;
									case OPS.markPoint:
									case OPS.markPointProps:
									case OPS.beginMarkedContent:
									case OPS.beginMarkedContentProps:
									case OPS.endMarkedContent:
									case OPS.beginCompat:
									case OPS.endCompat:
										continue;
									default:
										if (args !== null) {
											for (i = 0, ii = args.length; i < ii; i++) {
												if (args[i] instanceof Dict) {
													break;
												}
											}
											if (i < ii) {
												warn('getOperatorList - ignoring operator: ' + fn);
												continue;
											}
										}
								}
								operatorList.addOp(fn, args);
							}
							if (stop) {
								next(deferred);
								return;
							}
							for (i = 0, ii = preprocessor.savedStatesDepth; i < ii; i++) {
								operatorList.addOp(OPS.restore, []);
							}
							resolve();
						});
					},
					getTextContent: function PartialEvaluator_getTextContent(stream, task, resources, stateManager, normalizeWhitespace, combineTextItems) {
						stateManager = stateManager || new StateManager(new TextState());
						var WhitespaceRegexp = /\s/g;
						var textContent = {
							items: [],
							styles: Object.create(null)
						};
						var textContentItem = {
							initialized: false,
							str: [],
							width: 0,
							height: 0,
							vertical: false,
							lastAdvanceWidth: 0,
							lastAdvanceHeight: 0,
							textAdvanceScale: 0,
							spaceWidth: 0,
							fakeSpaceMin: Infinity,
							fakeMultiSpaceMin: Infinity,
							fakeMultiSpaceMax: -0,
							textRunBreakAllowed: false,
							transform: null,
							fontName: null
						};
						var SPACE_FACTOR = 0.3;
						var MULTI_SPACE_FACTOR = 1.5;
						var MULTI_SPACE_FACTOR_MAX = 4;
						var self = this;
						var xref = this.xref;
						resources = xref.fetchIfRef(resources) || Dict.empty;
						var xobjs = null;
						var xobjsCache = Object.create(null);
						var preprocessor = new EvaluatorPreprocessor(stream, xref, stateManager);
						var textState;
						function ensureTextContentItem() {
							if (textContentItem.initialized) {
								return textContentItem;
							}
							var font = textState.font;
							if (!(font.loadedName in textContent.styles)) {
								textContent.styles[font.loadedName] = {
									fontFamily: font.fallbackName,
									ascent: font.ascent,
									descent: font.descent,
									vertical: font.vertical
								};
							}
							textContentItem.fontName = font.loadedName;
							var tsm = [
								textState.fontSize * textState.textHScale,
								0,
								0,
								textState.fontSize,
								0,
								textState.textRise
							];
							if (font.isType3Font && textState.fontMatrix !== FONT_IDENTITY_MATRIX && textState.fontSize === 1) {
								var glyphHeight = font.bbox[3] - font.bbox[1];
								if (glyphHeight > 0) {
									glyphHeight = glyphHeight * textState.fontMatrix[3];
									tsm[3] *= glyphHeight;
								}
							}
							var trm = Util.transform(textState.ctm, Util.transform(textState.textMatrix, tsm));
							textContentItem.transform = trm;
							if (!font.vertical) {
								textContentItem.width = 0;
								textContentItem.height = Math.sqrt(trm[2] * trm[2] + trm[3] * trm[3]);
								textContentItem.vertical = false;
							} else {
								textContentItem.width = Math.sqrt(trm[0] * trm[0] + trm[1] * trm[1]);
								textContentItem.height = 0;
								textContentItem.vertical = true;
							}
							var a = textState.textLineMatrix[0];
							var b = textState.textLineMatrix[1];
							var scaleLineX = Math.sqrt(a * a + b * b);
							a = textState.ctm[0];
							b = textState.ctm[1];
							var scaleCtmX = Math.sqrt(a * a + b * b);
							textContentItem.textAdvanceScale = scaleCtmX * scaleLineX;
							textContentItem.lastAdvanceWidth = 0;
							textContentItem.lastAdvanceHeight = 0;
							var spaceWidth = font.spaceWidth / 1000 * textState.fontSize;
							if (spaceWidth) {
								textContentItem.spaceWidth = spaceWidth;
								textContentItem.fakeSpaceMin = spaceWidth * SPACE_FACTOR;
								textContentItem.fakeMultiSpaceMin = spaceWidth * MULTI_SPACE_FACTOR;
								textContentItem.fakeMultiSpaceMax = spaceWidth * MULTI_SPACE_FACTOR_MAX;
								textContentItem.textRunBreakAllowed = !font.isMonospace;
							} else {
								textContentItem.spaceWidth = 0;
								textContentItem.fakeSpaceMin = Infinity;
								textContentItem.fakeMultiSpaceMin = Infinity;
								textContentItem.fakeMultiSpaceMax = 0;
								textContentItem.textRunBreakAllowed = false;
							}
							textContentItem.initialized = true;
							return textContentItem;
						}
						function replaceWhitespace(str) {
							var i = 0, ii = str.length, code;
							while (i < ii && (code = str.charCodeAt(i)) >= 0x20 && code <= 0x7F) {
								i++;
							}
							return i < ii ? str.replace(WhitespaceRegexp, ' ') : str;
						}
						function runBidiTransform(textChunk) {
							var str = textChunk.str.join('');
							var bidiResult = bidi(str, -1, textChunk.vertical);
							return {
								str: normalizeWhitespace ? replaceWhitespace(bidiResult.str) : bidiResult.str,
								dir: bidiResult.dir,
								width: textChunk.width,
								height: textChunk.height,
								transform: textChunk.transform,
								fontName: textChunk.fontName
							};
						}
						function handleSetFont(fontName, fontRef) {
							return self.loadFont(fontName, fontRef, xref, resources).then(function (translated) {
								textState.font = translated.font;
								textState.fontMatrix = translated.font.fontMatrix || FONT_IDENTITY_MATRIX;
							});
						}
						function buildTextContentItem(chars) {
							var font = textState.font;
							var textChunk = ensureTextContentItem();
							var width = 0;
							var height = 0;
							var glyphs = font.charsToGlyphs(chars);
							for (var i = 0; i < glyphs.length; i++) {
								var glyph = glyphs[i];
								var glyphWidth = null;
								if (font.vertical && glyph.vmetric) {
									glyphWidth = glyph.vmetric[0];
								} else {
									glyphWidth = glyph.width;
								}
								var glyphUnicode = glyph.unicode;
								var NormalizedUnicodes = getNormalizedUnicodes();
								if (NormalizedUnicodes[glyphUnicode] !== undefined) {
									glyphUnicode = NormalizedUnicodes[glyphUnicode];
								}
								glyphUnicode = reverseIfRtl(glyphUnicode);
								var charSpacing = textState.charSpacing;
								if (glyph.isSpace) {
									var wordSpacing = textState.wordSpacing;
									charSpacing += wordSpacing;
									if (wordSpacing > 0) {
										addFakeSpaces(wordSpacing, textChunk.str);
									}
								}
								var tx = 0;
								var ty = 0;
								if (!font.vertical) {
									var w0 = glyphWidth * textState.fontMatrix[0];
									tx = (w0 * textState.fontSize + charSpacing) * textState.textHScale;
									width += tx;
								} else {
									var w1 = glyphWidth * textState.fontMatrix[0];
									ty = w1 * textState.fontSize + charSpacing;
									height += ty;
								}
								textState.translateTextMatrix(tx, ty);
								textChunk.str.push(glyphUnicode);
							}
							if (!font.vertical) {
								textChunk.lastAdvanceWidth = width;
								textChunk.width += width;
							} else {
								textChunk.lastAdvanceHeight = height;
								textChunk.height += Math.abs(height);
							}
							return textChunk;
						}
						function addFakeSpaces(width, strBuf) {
							if (width < textContentItem.fakeSpaceMin) {
								return;
							}
							if (width < textContentItem.fakeMultiSpaceMin) {
								strBuf.push(' ');
								return;
							}
							var fakeSpaces = Math.round(width / textContentItem.spaceWidth);
							while (fakeSpaces-- > 0) {
								strBuf.push(' ');
							}
						}
						function flushTextContentItem() {
							if (!textContentItem.initialized) {
								return;
							}
							textContentItem.width *= textContentItem.textAdvanceScale;
							textContentItem.height *= textContentItem.textAdvanceScale;
							textContent.items.push(runBidiTransform(textContentItem));
							textContentItem.initialized = false;
							textContentItem.str.length = 0;
						}
						var timeSlotManager = new TimeSlotManager();
						return new Promise(function promiseBody(resolve, reject) {
							var next = function (promise) {
								promise.then(function () {
									try {
										promiseBody(resolve, reject);
									} catch (ex) {
										reject(ex);
									}
								}, reject);
							};
							task.ensureNotTerminated();
							timeSlotManager.reset();
							var stop, operation = {}, args = [];
							while (!(stop = timeSlotManager.check())) {
								args.length = 0;
								operation.args = args;
								if (!preprocessor.read(operation)) {
									break;
								}
								textState = stateManager.state;
								var fn = operation.fn;
								args = operation.args;
								var advance, diff;
								switch (fn | 0) {
									case OPS.setFont:
										var fontNameArg = args[0].name, fontSizeArg = args[1];
										if (textState.font && fontNameArg === textState.fontName && fontSizeArg === textState.fontSize) {
											break;
										}
										flushTextContentItem();
										textState.fontName = fontNameArg;
										textState.fontSize = fontSizeArg;
										next(handleSetFont(fontNameArg, null));
										return;
									case OPS.setTextRise:
										flushTextContentItem();
										textState.textRise = args[0];
										break;
									case OPS.setHScale:
										flushTextContentItem();
										textState.textHScale = args[0] / 100;
										break;
									case OPS.setLeading:
										flushTextContentItem();
										textState.leading = args[0];
										break;
									case OPS.moveText:
										var isSameTextLine = !textState.font ? false : (textState.font.vertical ? args[0] : args[1]) === 0;
										advance = args[0] - args[1];
										if (combineTextItems && isSameTextLine && textContentItem.initialized && advance > 0 && advance <= textContentItem.fakeMultiSpaceMax) {
											textState.translateTextLineMatrix(args[0], args[1]);
											textContentItem.width += args[0] - textContentItem.lastAdvanceWidth;
											textContentItem.height += args[1] - textContentItem.lastAdvanceHeight;
											diff = args[0] - textContentItem.lastAdvanceWidth - (args[1] - textContentItem.lastAdvanceHeight);
											addFakeSpaces(diff, textContentItem.str);
											break;
										}
										flushTextContentItem();
										textState.translateTextLineMatrix(args[0], args[1]);
										textState.textMatrix = textState.textLineMatrix.slice();
										break;
									case OPS.setLeadingMoveText:
										flushTextContentItem();
										textState.leading = -args[1];
										textState.translateTextLineMatrix(args[0], args[1]);
										textState.textMatrix = textState.textLineMatrix.slice();
										break;
									case OPS.nextLine:
										flushTextContentItem();
										textState.carriageReturn();
										break;
									case OPS.setTextMatrix:
										advance = textState.calcTextLineMatrixAdvance(args[0], args[1], args[2], args[3], args[4], args[5]);
										if (combineTextItems && advance !== null && textContentItem.initialized && advance.value > 0 && advance.value <= textContentItem.fakeMultiSpaceMax) {
											textState.translateTextLineMatrix(advance.width, advance.height);
											textContentItem.width += advance.width - textContentItem.lastAdvanceWidth;
											textContentItem.height += advance.height - textContentItem.lastAdvanceHeight;
											diff = advance.width - textContentItem.lastAdvanceWidth - (advance.height - textContentItem.lastAdvanceHeight);
											addFakeSpaces(diff, textContentItem.str);
											break;
										}
										flushTextContentItem();
										textState.setTextMatrix(args[0], args[1], args[2], args[3], args[4], args[5]);
										textState.setTextLineMatrix(args[0], args[1], args[2], args[3], args[4], args[5]);
										break;
									case OPS.setCharSpacing:
										textState.charSpacing = args[0];
										break;
									case OPS.setWordSpacing:
										textState.wordSpacing = args[0];
										break;
									case OPS.beginText:
										flushTextContentItem();
										textState.textMatrix = IDENTITY_MATRIX.slice();
										textState.textLineMatrix = IDENTITY_MATRIX.slice();
										break;
									case OPS.showSpacedText:
										var items = args[0];
										var offset;
										for (var j = 0, jj = items.length; j < jj; j++) {
											if (typeof items[j] === 'string') {
												buildTextContentItem(items[j]);
											} else if (isNum(items[j])) {
												ensureTextContentItem();
												advance = items[j] * textState.fontSize / 1000;
												var breakTextRun = false;
												if (textState.font.vertical) {
													offset = advance;
													textState.translateTextMatrix(0, offset);
													breakTextRun = textContentItem.textRunBreakAllowed && advance > textContentItem.fakeMultiSpaceMax;
													if (!breakTextRun) {
														textContentItem.height += offset;
													}
												} else {
													advance = -advance;
													offset = advance * textState.textHScale;
													textState.translateTextMatrix(offset, 0);
													breakTextRun = textContentItem.textRunBreakAllowed && advance > textContentItem.fakeMultiSpaceMax;
													if (!breakTextRun) {
														textContentItem.width += offset;
													}
												}
												if (breakTextRun) {
													flushTextContentItem();
												} else if (advance > 0) {
													addFakeSpaces(advance, textContentItem.str);
												}
											}
										}
										break;
									case OPS.showText:
										buildTextContentItem(args[0]);
										break;
									case OPS.nextLineShowText:
										flushTextContentItem();
										textState.carriageReturn();
										buildTextContentItem(args[0]);
										break;
									case OPS.nextLineSetSpacingShowText:
										flushTextContentItem();
										textState.wordSpacing = args[0];
										textState.charSpacing = args[1];
										textState.carriageReturn();
										buildTextContentItem(args[2]);
										break;
									case OPS.paintXObject:
										flushTextContentItem();
										if (args[0].code) {
											break;
										}
										if (!xobjs) {
											xobjs = resources.get('XObject') || Dict.empty;
										}
										var name = args[0].name;
										if (xobjsCache.key === name) {
											if (xobjsCache.texts) {
												Util.appendToArray(textContent.items, xobjsCache.texts.items);
												Util.extendObj(textContent.styles, xobjsCache.texts.styles);
											}
											break;
										}
										var xobj = xobjs.get(name);
										if (!xobj) {
											break;
										}
										assert(isStream(xobj), 'XObject should be a stream');
										var type = xobj.dict.get('Subtype');
										assert(isName(type), 'XObject should have a Name subtype');
										if ('Form' !== type.name) {
											xobjsCache.key = name;
											xobjsCache.texts = null;
											break;
										}
										stateManager.save();
										var matrix = xobj.dict.getArray('Matrix');
										if (isArray(matrix) && matrix.length === 6) {
											stateManager.transform(matrix);
										}
										next(self.getTextContent(xobj, task, xobj.dict.get('Resources') || resources, stateManager, normalizeWhitespace, combineTextItems).then(function (formTextContent) {
											Util.appendToArray(textContent.items, formTextContent.items);
											Util.extendObj(textContent.styles, formTextContent.styles);
											stateManager.restore();
											xobjsCache.key = name;
											xobjsCache.texts = formTextContent;
										}));
										return;
									case OPS.setGState:
										flushTextContentItem();
										var dictName = args[0];
										var extGState = resources.get('ExtGState');
										if (!isDict(extGState) || !isName(dictName)) {
											break;
										}
										var gState = extGState.get(dictName.name);
										if (!isDict(gState)) {
											break;
										}
										var gStateFont = gState.get('Font');
										if (gStateFont) {
											textState.fontName = null;
											textState.fontSize = gStateFont[1];
											next(handleSetFont(null, gStateFont[0]));
											return;
										}
										break;
								}
							}
							if (stop) {
								next(deferred);
								return;
							}
							flushTextContentItem();
							resolve(textContent);
						});
					},
					extractDataStructures: function PartialEvaluator_extractDataStructures(dict, baseDict, xref, properties) {
						var toUnicode = dict.get('ToUnicode') || baseDict.get('ToUnicode');
						var toUnicodePromise = toUnicode ? this.readToUnicode(toUnicode) : Promise.resolve(undefined);
						if (properties.composite) {
							var cidSystemInfo = dict.get('CIDSystemInfo');
							if (isDict(cidSystemInfo)) {
								properties.cidSystemInfo = {
									registry: cidSystemInfo.get('Registry'),
									ordering: cidSystemInfo.get('Ordering'),
									supplement: cidSystemInfo.get('Supplement')
								};
							}
							var cidToGidMap = dict.get('CIDToGIDMap');
							if (isStream(cidToGidMap)) {
								properties.cidToGidMap = this.readCidToGidMap(cidToGidMap);
							}
						}
						var differences = [];
						var baseEncodingName = null;
						var encoding;
						if (dict.has('Encoding')) {
							encoding = dict.get('Encoding');
							if (isDict(encoding)) {
								baseEncodingName = encoding.get('BaseEncoding');
								baseEncodingName = isName(baseEncodingName) ? baseEncodingName.name : null;
								if (encoding.has('Differences')) {
									var diffEncoding = encoding.get('Differences');
									var index = 0;
									for (var j = 0, jj = diffEncoding.length; j < jj; j++) {
										var data = xref.fetchIfRef(diffEncoding[j]);
										if (isNum(data)) {
											index = data;
										} else if (isName(data)) {
											differences[index++] = data.name;
										} else {
											error('Invalid entry in \'Differences\' array: ' + data);
										}
									}
								}
							} else if (isName(encoding)) {
								baseEncodingName = encoding.name;
							} else {
								error('Encoding is not a Name nor a Dict');
							}
							if (baseEncodingName !== 'MacRomanEncoding' && baseEncodingName !== 'MacExpertEncoding' && baseEncodingName !== 'WinAnsiEncoding') {
								baseEncodingName = null;
							}
						}
						if (baseEncodingName) {
							properties.defaultEncoding = getEncoding(baseEncodingName).slice();
						} else {
							var isSymbolicFont = !!(properties.flags & FontFlags.Symbolic);
							var isNonsymbolicFont = !!(properties.flags & FontFlags.Nonsymbolic);
							encoding = StandardEncoding;
							if (properties.type === 'TrueType' && !isNonsymbolicFont) {
								encoding = WinAnsiEncoding;
							}
							if (isSymbolicFont) {
								encoding = MacRomanEncoding;
								if (!properties.file) {
									if (/Symbol/i.test(properties.name)) {
										encoding = SymbolSetEncoding;
									} else if (/Dingbats/i.test(properties.name)) {
										encoding = ZapfDingbatsEncoding;
									}
								}
							}
							properties.defaultEncoding = encoding;
						}
						properties.differences = differences;
						properties.baseEncodingName = baseEncodingName;
						properties.hasEncoding = !!baseEncodingName || differences.length > 0;
						properties.dict = dict;
						return toUnicodePromise.then(function (toUnicode) {
							properties.toUnicode = toUnicode;
							return this.buildToUnicode(properties);
						}.bind(this)).then(function (toUnicode) {
							properties.toUnicode = toUnicode;
							return properties;
						});
					},
					buildToUnicode: function PartialEvaluator_buildToUnicode(properties) {
						properties.hasIncludedToUnicodeMap = !!properties.toUnicode && properties.toUnicode.length > 0;
						if (properties.hasIncludedToUnicodeMap) {
							return Promise.resolve(properties.toUnicode);
						}
						var toUnicode, charcode, glyphName;
						if (!properties.composite) {
							toUnicode = [];
							var encoding = properties.defaultEncoding.slice();
							var baseEncodingName = properties.baseEncodingName;
							var differences = properties.differences;
							for (charcode in differences) {
								glyphName = differences[charcode];
								if (glyphName === '.notdef') {
									continue;
								}
								encoding[charcode] = glyphName;
							}
							var glyphsUnicodeMap = getGlyphsUnicode();
							for (charcode in encoding) {
								glyphName = encoding[charcode];
								if (glyphName === '') {
									continue;
								} else if (glyphsUnicodeMap[glyphName] === undefined) {
									var code = 0;
									switch (glyphName[0]) {
										case 'G':
											if (glyphName.length === 3) {
												code = parseInt(glyphName.substr(1), 16);
											}
											break;
										case 'g':
											if (glyphName.length === 5) {
												code = parseInt(glyphName.substr(1), 16);
											}
											break;
										case 'C':
										case 'c':
											if (glyphName.length >= 3) {
												code = +glyphName.substr(1);
											}
											break;
										default:
											var unicode = getUnicodeForGlyph(glyphName, glyphsUnicodeMap);
											if (unicode !== -1) {
												code = unicode;
											}
									}
									if (code) {
										if (baseEncodingName && code === +charcode) {
											var baseEncoding = getEncoding(baseEncodingName);
											if (baseEncoding && (glyphName = baseEncoding[charcode])) {
												toUnicode[charcode] = String.fromCharCode(glyphsUnicodeMap[glyphName]);
												continue;
											}
										}
										toUnicode[charcode] = String.fromCharCode(code);
									}
									continue;
								}
								toUnicode[charcode] = String.fromCharCode(glyphsUnicodeMap[glyphName]);
							}
							return Promise.resolve(new ToUnicodeMap(toUnicode));
						}
						if (properties.composite && (properties.cMap.builtInCMap && !(properties.cMap instanceof IdentityCMap) || properties.cidSystemInfo.registry === 'Adobe' && (properties.cidSystemInfo.ordering === 'GB1' || properties.cidSystemInfo.ordering === 'CNS1' || properties.cidSystemInfo.ordering === 'Japan1' || properties.cidSystemInfo.ordering === 'Korea1'))) {
							var registry = properties.cidSystemInfo.registry;
							var ordering = properties.cidSystemInfo.ordering;
							var ucs2CMapName = Name.get(registry + '-' + ordering + '-UCS2');
							return CMapFactory.create({
								encoding: ucs2CMapName,
								fetchBuiltInCMap: this.fetchBuiltInCMap,
								useCMap: null
							}).then(function (ucs2CMap) {
								var cMap = properties.cMap;
								toUnicode = [];
								cMap.forEach(function (charcode, cid) {
									assert(cid <= 0xffff, 'Max size of CID is 65,535');
									var ucs2 = ucs2CMap.lookup(cid);
									if (ucs2) {
										toUnicode[charcode] = String.fromCharCode((ucs2.charCodeAt(0) << 8) + ucs2.charCodeAt(1));
									}
								});
								return new ToUnicodeMap(toUnicode);
							});
						}
						return Promise.resolve(new IdentityToUnicodeMap(properties.firstChar, properties.lastChar));
					},
					readToUnicode: function PartialEvaluator_readToUnicode(toUnicode) {
						var cmapObj = toUnicode;
						if (isName(cmapObj)) {
							return CMapFactory.create({
								encoding: cmapObj,
								fetchBuiltInCMap: this.fetchBuiltInCMap,
								useCMap: null
							}).then(function (cmap) {
								if (cmap instanceof IdentityCMap) {
									return new IdentityToUnicodeMap(0, 0xFFFF);
								}
								return new ToUnicodeMap(cmap.getMap());
							});
						} else if (isStream(cmapObj)) {
							return CMapFactory.create({
								encoding: cmapObj,
								fetchBuiltInCMap: this.fetchBuiltInCMap,
								useCMap: null
							}).then(function (cmap) {
								if (cmap instanceof IdentityCMap) {
									return new IdentityToUnicodeMap(0, 0xFFFF);
								}
								var map = new Array(cmap.length);
								cmap.forEach(function (charCode, token) {
									var str = [];
									for (var k = 0; k < token.length; k += 2) {
										var w1 = token.charCodeAt(k) << 8 | token.charCodeAt(k + 1);
										if ((w1 & 0xF800) !== 0xD800) {
											str.push(w1);
											continue;
										}
										k += 2;
										var w2 = token.charCodeAt(k) << 8 | token.charCodeAt(k + 1);
										str.push(((w1 & 0x3ff) << 10) + (w2 & 0x3ff) + 0x10000);
									}
									map[charCode] = String.fromCharCode.apply(String, str);
								});
								return new ToUnicodeMap(map);
							});
						}
						return Promise.resolve(null);
					},
					readCidToGidMap: function PartialEvaluator_readCidToGidMap(cidToGidStream) {
						var glyphsData = cidToGidStream.getBytes();
						var result = [];
						for (var j = 0, jj = glyphsData.length; j < jj; j++) {
							var glyphID = glyphsData[j++] << 8 | glyphsData[j];
							if (glyphID === 0) {
								continue;
							}
							var code = j >> 1;
							result[code] = glyphID;
						}
						return result;
					},
					extractWidths: function PartialEvaluator_extractWidths(dict, xref, descriptor, properties) {
						var glyphsWidths = [];
						var defaultWidth = 0;
						var glyphsVMetrics = [];
						var defaultVMetrics;
						var i, ii, j, jj, start, code, widths;
						if (properties.composite) {
							defaultWidth = dict.get('DW') || 1000;
							widths = dict.get('W');
							if (widths) {
								for (i = 0, ii = widths.length; i < ii; i++) {
									start = xref.fetchIfRef(widths[i++]);
									code = xref.fetchIfRef(widths[i]);
									if (isArray(code)) {
										for (j = 0, jj = code.length; j < jj; j++) {
											glyphsWidths[start++] = xref.fetchIfRef(code[j]);
										}
									} else {
										var width = xref.fetchIfRef(widths[++i]);
										for (j = start; j <= code; j++) {
											glyphsWidths[j] = width;
										}
									}
								}
							}
							if (properties.vertical) {
								var vmetrics = dict.getArray('DW2') || [
									880,
									-1000
								];
								defaultVMetrics = [
									vmetrics[1],
									defaultWidth * 0.5,
									vmetrics[0]
								];
								vmetrics = dict.get('W2');
								if (vmetrics) {
									for (i = 0, ii = vmetrics.length; i < ii; i++) {
										start = xref.fetchIfRef(vmetrics[i++]);
										code = xref.fetchIfRef(vmetrics[i]);
										if (isArray(code)) {
											for (j = 0, jj = code.length; j < jj; j++) {
												glyphsVMetrics[start++] = [
													xref.fetchIfRef(code[j++]),
													xref.fetchIfRef(code[j++]),
													xref.fetchIfRef(code[j])
												];
											}
										} else {
											var vmetric = [
												xref.fetchIfRef(vmetrics[++i]),
												xref.fetchIfRef(vmetrics[++i]),
												xref.fetchIfRef(vmetrics[++i])
											];
											for (j = start; j <= code; j++) {
												glyphsVMetrics[j] = vmetric;
											}
										}
									}
								}
							}
						} else {
							var firstChar = properties.firstChar;
							widths = dict.get('Widths');
							if (widths) {
								j = firstChar;
								for (i = 0, ii = widths.length; i < ii; i++) {
									glyphsWidths[j++] = xref.fetchIfRef(widths[i]);
								}
								defaultWidth = parseFloat(descriptor.get('MissingWidth')) || 0;
							} else {
								var baseFontName = dict.get('BaseFont');
								if (isName(baseFontName)) {
									var metrics = this.getBaseFontMetrics(baseFontName.name);
									glyphsWidths = this.buildCharCodeToWidth(metrics.widths, properties);
									defaultWidth = metrics.defaultWidth;
								}
							}
						}
						var isMonospace = true;
						var firstWidth = defaultWidth;
						for (var glyph in glyphsWidths) {
							var glyphWidth = glyphsWidths[glyph];
							if (!glyphWidth) {
								continue;
							}
							if (!firstWidth) {
								firstWidth = glyphWidth;
								continue;
							}
							if (firstWidth !== glyphWidth) {
								isMonospace = false;
								break;
							}
						}
						if (isMonospace) {
							properties.flags |= FontFlags.FixedPitch;
						}
						properties.defaultWidth = defaultWidth;
						properties.widths = glyphsWidths;
						properties.defaultVMetrics = defaultVMetrics;
						properties.vmetrics = glyphsVMetrics;
					},
					isSerifFont: function PartialEvaluator_isSerifFont(baseFontName) {
						var fontNameWoStyle = baseFontName.split('-')[0];
						return fontNameWoStyle in getSerifFonts() || fontNameWoStyle.search(/serif/gi) !== -1;
					},
					getBaseFontMetrics: function PartialEvaluator_getBaseFontMetrics(name) {
						var defaultWidth = 0;
						var widths = [];
						var monospace = false;
						var stdFontMap = getStdFontMap();
						var lookupName = stdFontMap[name] || name;
						var Metrics = getMetrics();
						if (!(lookupName in Metrics)) {
							if (this.isSerifFont(name)) {
								lookupName = 'Times-Roman';
							} else {
								lookupName = 'Helvetica';
							}
						}
						var glyphWidths = Metrics[lookupName];
						if (isNum(glyphWidths)) {
							defaultWidth = glyphWidths;
							monospace = true;
						} else {
							widths = glyphWidths();
						}
						return {
							defaultWidth: defaultWidth,
							monospace: monospace,
							widths: widths
						};
					},
					buildCharCodeToWidth: function PartialEvaluator_bulildCharCodeToWidth(widthsByGlyphName, properties) {
						var widths = Object.create(null);
						var differences = properties.differences;
						var encoding = properties.defaultEncoding;
						for (var charCode = 0; charCode < 256; charCode++) {
							if (charCode in differences && widthsByGlyphName[differences[charCode]]) {
								widths[charCode] = widthsByGlyphName[differences[charCode]];
								continue;
							}
							if (charCode in encoding && widthsByGlyphName[encoding[charCode]]) {
								widths[charCode] = widthsByGlyphName[encoding[charCode]];
								continue;
							}
						}
						return widths;
					},
					preEvaluateFont: function PartialEvaluator_preEvaluateFont(dict, xref) {
						var baseDict = dict;
						var type = dict.get('Subtype');
						assert(isName(type), 'invalid font Subtype');
						var composite = false;
						var uint8array;
						if (type.name === 'Type0') {
							var df = dict.get('DescendantFonts');
							if (!df) {
								error('Descendant fonts are not specified');
							}
							dict = isArray(df) ? xref.fetchIfRef(df[0]) : df;
							type = dict.get('Subtype');
							assert(isName(type), 'invalid font Subtype');
							composite = true;
						}
						var descriptor = dict.get('FontDescriptor');
						if (descriptor) {
							var hash = new MurmurHash3_64();
							var encoding = baseDict.getRaw('Encoding');
							if (isName(encoding)) {
								hash.update(encoding.name);
							} else if (isRef(encoding)) {
								hash.update(encoding.toString());
							} else if (isDict(encoding)) {
								var keys = encoding.getKeys();
								for (var i = 0, ii = keys.length; i < ii; i++) {
									var entry = encoding.getRaw(keys[i]);
									if (isName(entry)) {
										hash.update(entry.name);
									} else if (isRef(entry)) {
										hash.update(entry.toString());
									} else if (isArray(entry)) {
										var diffLength = entry.length, diffBuf = new Array(diffLength);
										for (var j = 0; j < diffLength; j++) {
											var diffEntry = entry[j];
											if (isName(diffEntry)) {
												diffBuf[j] = diffEntry.name;
											} else if (isNum(diffEntry) || isRef(diffEntry)) {
												diffBuf[j] = diffEntry.toString();
											}
										}
										hash.update(diffBuf.join());
									}
								}
							}
							var toUnicode = dict.get('ToUnicode') || baseDict.get('ToUnicode');
							if (isStream(toUnicode)) {
								var stream = toUnicode.str || toUnicode;
								uint8array = stream.buffer ? new Uint8Array(stream.buffer.buffer, 0, stream.bufferLength) : new Uint8Array(stream.bytes.buffer, stream.start, stream.end - stream.start);
								hash.update(uint8array);
							} else if (isName(toUnicode)) {
								hash.update(toUnicode.name);
							}
							var widths = dict.get('Widths') || baseDict.get('Widths');
							if (widths) {
								uint8array = new Uint8Array(new Uint32Array(widths).buffer);
								hash.update(uint8array);
							}
						}
						return {
							descriptor: descriptor,
							dict: dict,
							baseDict: baseDict,
							composite: composite,
							type: type.name,
							hash: hash ? hash.hexdigest() : ''
						};
					},
					translateFont: function PartialEvaluator_translateFont(preEvaluatedFont, xref) {
						var baseDict = preEvaluatedFont.baseDict;
						var dict = preEvaluatedFont.dict;
						var composite = preEvaluatedFont.composite;
						var descriptor = preEvaluatedFont.descriptor;
						var type = preEvaluatedFont.type;
						var maxCharIndex = composite ? 0xFFFF : 0xFF;
						var properties;
						if (!descriptor) {
							if (type === 'Type3') {
								descriptor = new Dict(null);
								descriptor.set('FontName', Name.get(type));
								descriptor.set('FontBBox', dict.getArray('FontBBox'));
							} else {
								var baseFontName = dict.get('BaseFont');
								if (!isName(baseFontName)) {
									error('Base font is not specified');
								}
								baseFontName = baseFontName.name.replace(/[,_]/g, '-');
								var metrics = this.getBaseFontMetrics(baseFontName);
								var fontNameWoStyle = baseFontName.split('-')[0];
								var flags = (this.isSerifFont(fontNameWoStyle) ? FontFlags.Serif : 0) | (metrics.monospace ? FontFlags.FixedPitch : 0) | (getSymbolsFonts()[fontNameWoStyle] ? FontFlags.Symbolic : FontFlags.Nonsymbolic);
								properties = {
									type: type,
									name: baseFontName,
									widths: metrics.widths,
									defaultWidth: metrics.defaultWidth,
									flags: flags,
									firstChar: 0,
									lastChar: maxCharIndex
								};
								return this.extractDataStructures(dict, dict, xref, properties).then(function (properties) {
									properties.widths = this.buildCharCodeToWidth(metrics.widths, properties);
									return new Font(baseFontName, null, properties);
								}.bind(this));
							}
						}
						var firstChar = dict.get('FirstChar') || 0;
						var lastChar = dict.get('LastChar') || maxCharIndex;
						var fontName = descriptor.get('FontName');
						var baseFont = dict.get('BaseFont');
						if (isString(fontName)) {
							fontName = Name.get(fontName);
						}
						if (isString(baseFont)) {
							baseFont = Name.get(baseFont);
						}
						if (type !== 'Type3') {
							var fontNameStr = fontName && fontName.name;
							var baseFontStr = baseFont && baseFont.name;
							if (fontNameStr !== baseFontStr) {
								info('The FontDescriptor\'s FontName is "' + fontNameStr + '" but should be the same as the Font\'s BaseFont "' + baseFontStr + '"');
								if (fontNameStr && baseFontStr && baseFontStr.indexOf(fontNameStr) === 0) {
									fontName = baseFont;
								}
							}
						}
						fontName = fontName || baseFont;
						assert(isName(fontName), 'invalid font name');
						var fontFile = descriptor.get('FontFile', 'FontFile2', 'FontFile3');
						if (fontFile) {
							if (fontFile.dict) {
								var subtype = fontFile.dict.get('Subtype');
								if (subtype) {
									subtype = subtype.name;
								}
								var length1 = fontFile.dict.get('Length1');
								var length2 = fontFile.dict.get('Length2');
								var length3 = fontFile.dict.get('Length3');
							}
						}
						properties = {
							type: type,
							name: fontName.name,
							subtype: subtype,
							file: fontFile,
							length1: length1,
							length2: length2,
							length3: length3,
							loadedName: baseDict.loadedName,
							composite: composite,
							wideChars: composite,
							fixedPitch: false,
							fontMatrix: dict.getArray('FontMatrix') || FONT_IDENTITY_MATRIX,
							firstChar: firstChar || 0,
							lastChar: lastChar || maxCharIndex,
							bbox: descriptor.getArray('FontBBox'),
							ascent: descriptor.get('Ascent'),
							descent: descriptor.get('Descent'),
							xHeight: descriptor.get('XHeight'),
							capHeight: descriptor.get('CapHeight'),
							flags: descriptor.get('Flags'),
							italicAngle: descriptor.get('ItalicAngle'),
							coded: false
						};
						var cMapPromise;
						if (composite) {
							var cidEncoding = baseDict.get('Encoding');
							if (isName(cidEncoding)) {
								properties.cidEncoding = cidEncoding.name;
							}
							cMapPromise = CMapFactory.create({
								encoding: cidEncoding,
								fetchBuiltInCMap: this.fetchBuiltInCMap,
								useCMap: null
							}).then(function (cMap) {
								properties.cMap = cMap;
								properties.vertical = properties.cMap.vertical;
							});
						} else {
							cMapPromise = Promise.resolve(undefined);
						}
						return cMapPromise.then(function () {
							return this.extractDataStructures(dict, baseDict, xref, properties);
						}.bind(this)).then(function (properties) {
							this.extractWidths(dict, xref, descriptor, properties);
							if (type === 'Type3') {
								properties.isType3Font = true;
							}
							return new Font(fontName.name, fontFile, properties);
						}.bind(this));
					}
				};
				return PartialEvaluator;
			}();
			var TranslatedFont = function TranslatedFontClosure() {
				function TranslatedFont(loadedName, font, dict) {
					this.loadedName = loadedName;
					this.font = font;
					this.dict = dict;
					this.type3Loaded = null;
					this.sent = false;
				}
				TranslatedFont.prototype = {
					send: function (handler) {
						if (this.sent) {
							return;
						}
						var fontData = this.font.exportData();
						handler.send('commonobj', [
							this.loadedName,
							'Font',
							fontData
						]);
						this.sent = true;
					},
					loadType3Data: function (evaluator, resources, parentOperatorList, task) {
						assert(this.font.isType3Font);
						if (this.type3Loaded) {
							return this.type3Loaded;
						}
						var translatedFont = this.font;
						var loadCharProcsPromise = Promise.resolve();
						var charProcs = this.dict.get('CharProcs');
						var fontResources = this.dict.get('Resources') || resources;
						var charProcKeys = charProcs.getKeys();
						var charProcOperatorList = Object.create(null);
						for (var i = 0, n = charProcKeys.length; i < n; ++i) {
							loadCharProcsPromise = loadCharProcsPromise.then(function (key) {
								var glyphStream = charProcs.get(key);
								var operatorList = new OperatorList();
								return evaluator.getOperatorList(glyphStream, task, fontResources, operatorList).then(function () {
									charProcOperatorList[key] = operatorList.getIR();
									parentOperatorList.addDependencies(operatorList.dependencies);
								}, function (reason) {
									warn('Type3 font resource \"' + key + '\" is not available');
									var operatorList = new OperatorList();
									charProcOperatorList[key] = operatorList.getIR();
								});
							}.bind(this, charProcKeys[i]));
						}
						this.type3Loaded = loadCharProcsPromise.then(function () {
							translatedFont.charProcOperatorList = charProcOperatorList;
						});
						return this.type3Loaded;
					}
				};
				return TranslatedFont;
			}();
			var OperatorList = function OperatorListClosure() {
				var CHUNK_SIZE = 1000;
				var CHUNK_SIZE_ABOUT = CHUNK_SIZE - 5;
				function getTransfers(queue) {
					var transfers = [];
					var fnArray = queue.fnArray, argsArray = queue.argsArray;
					for (var i = 0, ii = queue.length; i < ii; i++) {
						switch (fnArray[i]) {
							case OPS.paintInlineImageXObject:
							case OPS.paintInlineImageXObjectGroup:
							case OPS.paintImageMaskXObject:
								var arg = argsArray[i][0];
								if (!arg.cached) {
									transfers.push(arg.data.buffer);
								}
								break;
						}
					}
					return transfers;
				}
				function OperatorList(intent, messageHandler, pageIndex) {
					this.messageHandler = messageHandler;
					this.fnArray = [];
					this.argsArray = [];
					this.dependencies = Object.create(null);
					this._totalLength = 0;
					this.pageIndex = pageIndex;
					this.intent = intent;
				}
				OperatorList.prototype = {
					get length() {
						return this.argsArray.length;
					},
					get totalLength() {
						return this._totalLength + this.length;
					},
					addOp: function (fn, args) {
						this.fnArray.push(fn);
						this.argsArray.push(args);
						if (this.messageHandler) {
							if (this.fnArray.length >= CHUNK_SIZE) {
								this.flush();
							} else if (this.fnArray.length >= CHUNK_SIZE_ABOUT && (fn === OPS.restore || fn === OPS.endText)) {
								this.flush();
							}
						}
					},
					addDependency: function (dependency) {
						if (dependency in this.dependencies) {
							return;
						}
						this.dependencies[dependency] = true;
						this.addOp(OPS.dependency, [dependency]);
					},
					addDependencies: function (dependencies) {
						for (var key in dependencies) {
							this.addDependency(key);
						}
					},
					addOpList: function (opList) {
						Util.extendObj(this.dependencies, opList.dependencies);
						for (var i = 0, ii = opList.length; i < ii; i++) {
							this.addOp(opList.fnArray[i], opList.argsArray[i]);
						}
					},
					getIR: function () {
						return {
							fnArray: this.fnArray,
							argsArray: this.argsArray,
							length: this.length
						};
					},
					flush: function (lastChunk) {
						if (this.intent !== 'oplist') {
							new QueueOptimizer().optimize(this);
						}
						var transfers = getTransfers(this);
						var length = this.length;
						this._totalLength += length;
						this.messageHandler.send('RenderPageChunk', {
							operatorList: {
								fnArray: this.fnArray,
								argsArray: this.argsArray,
								lastChunk: lastChunk,
								length: length
							},
							pageIndex: this.pageIndex,
							intent: this.intent
						}, transfers);
						this.dependencies = Object.create(null);
						this.fnArray.length = 0;
						this.argsArray.length = 0;
					}
				};
				return OperatorList;
			}();
			var StateManager = function StateManagerClosure() {
				function StateManager(initialState) {
					this.state = initialState;
					this.stateStack = [];
				}
				StateManager.prototype = {
					save: function () {
						var old = this.state;
						this.stateStack.push(this.state);
						this.state = old.clone();
					},
					restore: function () {
						var prev = this.stateStack.pop();
						if (prev) {
							this.state = prev;
						}
					},
					transform: function (args) {
						this.state.ctm = Util.transform(this.state.ctm, args);
					}
				};
				return StateManager;
			}();
			var TextState = function TextStateClosure() {
				function TextState() {
					this.ctm = new Float32Array(IDENTITY_MATRIX);
					this.fontName = null;
					this.fontSize = 0;
					this.font = null;
					this.fontMatrix = FONT_IDENTITY_MATRIX;
					this.textMatrix = IDENTITY_MATRIX.slice();
					this.textLineMatrix = IDENTITY_MATRIX.slice();
					this.charSpacing = 0;
					this.wordSpacing = 0;
					this.leading = 0;
					this.textHScale = 1;
					this.textRise = 0;
				}
				TextState.prototype = {
					setTextMatrix: function TextState_setTextMatrix(a, b, c, d, e, f) {
						var m = this.textMatrix;
						m[0] = a;
						m[1] = b;
						m[2] = c;
						m[3] = d;
						m[4] = e;
						m[5] = f;
					},
					setTextLineMatrix: function TextState_setTextMatrix(a, b, c, d, e, f) {
						var m = this.textLineMatrix;
						m[0] = a;
						m[1] = b;
						m[2] = c;
						m[3] = d;
						m[4] = e;
						m[5] = f;
					},
					translateTextMatrix: function TextState_translateTextMatrix(x, y) {
						var m = this.textMatrix;
						m[4] = m[0] * x + m[2] * y + m[4];
						m[5] = m[1] * x + m[3] * y + m[5];
					},
					translateTextLineMatrix: function TextState_translateTextMatrix(x, y) {
						var m = this.textLineMatrix;
						m[4] = m[0] * x + m[2] * y + m[4];
						m[5] = m[1] * x + m[3] * y + m[5];
					},
					calcTextLineMatrixAdvance: function TextState_calcTextLineMatrixAdvance(a, b, c, d, e, f) {
						var font = this.font;
						if (!font) {
							return null;
						}
						var m = this.textLineMatrix;
						if (!(a === m[0] && b === m[1] && c === m[2] && d === m[3])) {
							return null;
						}
						var txDiff = e - m[4], tyDiff = f - m[5];
						if (font.vertical && txDiff !== 0 || !font.vertical && tyDiff !== 0) {
							return null;
						}
						var tx, ty, denominator = a * d - b * c;
						if (font.vertical) {
							tx = -tyDiff * c / denominator;
							ty = tyDiff * a / denominator;
						} else {
							tx = txDiff * d / denominator;
							ty = -txDiff * b / denominator;
						}
						return {
							width: tx,
							height: ty,
							value: font.vertical ? ty : tx
						};
					},
					calcRenderMatrix: function TextState_calcRendeMatrix(ctm) {
						var tsm = [
							this.fontSize * this.textHScale,
							0,
							0,
							this.fontSize,
							0,
							this.textRise
						];
						return Util.transform(ctm, Util.transform(this.textMatrix, tsm));
					},
					carriageReturn: function TextState_carriageReturn() {
						this.translateTextLineMatrix(0, -this.leading);
						this.textMatrix = this.textLineMatrix.slice();
					},
					clone: function TextState_clone() {
						var clone = Object.create(this);
						clone.textMatrix = this.textMatrix.slice();
						clone.textLineMatrix = this.textLineMatrix.slice();
						clone.fontMatrix = this.fontMatrix.slice();
						return clone;
					}
				};
				return TextState;
			}();
			var EvalState = function EvalStateClosure() {
				function EvalState() {
					this.ctm = new Float32Array(IDENTITY_MATRIX);
					this.font = null;
					this.textRenderingMode = TextRenderingMode.FILL;
					this.fillColorSpace = ColorSpace.singletons.gray;
					this.strokeColorSpace = ColorSpace.singletons.gray;
				}
				EvalState.prototype = {
					clone: function CanvasExtraState_clone() {
						return Object.create(this);
					}
				};
				return EvalState;
			}();
			var EvaluatorPreprocessor = function EvaluatorPreprocessorClosure() {
				var getOPMap = getLookupTableFactory(function (t) {
					t['w'] = {
						id: OPS.setLineWidth,
						numArgs: 1,
						variableArgs: false
					};
					t['J'] = {
						id: OPS.setLineCap,
						numArgs: 1,
						variableArgs: false
					};
					t['j'] = {
						id: OPS.setLineJoin,
						numArgs: 1,
						variableArgs: false
					};
					t['M'] = {
						id: OPS.setMiterLimit,
						numArgs: 1,
						variableArgs: false
					};
					t['d'] = {
						id: OPS.setDash,
						numArgs: 2,
						variableArgs: false
					};
					t['ri'] = {
						id: OPS.setRenderingIntent,
						numArgs: 1,
						variableArgs: false
					};
					t['i'] = {
						id: OPS.setFlatness,
						numArgs: 1,
						variableArgs: false
					};
					t['gs'] = {
						id: OPS.setGState,
						numArgs: 1,
						variableArgs: false
					};
					t['q'] = {
						id: OPS.save,
						numArgs: 0,
						variableArgs: false
					};
					t['Q'] = {
						id: OPS.restore,
						numArgs: 0,
						variableArgs: false
					};
					t['cm'] = {
						id: OPS.transform,
						numArgs: 6,
						variableArgs: false
					};
					t['m'] = {
						id: OPS.moveTo,
						numArgs: 2,
						variableArgs: false
					};
					t['l'] = {
						id: OPS.lineTo,
						numArgs: 2,
						variableArgs: false
					};
					t['c'] = {
						id: OPS.curveTo,
						numArgs: 6,
						variableArgs: false
					};
					t['v'] = {
						id: OPS.curveTo2,
						numArgs: 4,
						variableArgs: false
					};
					t['y'] = {
						id: OPS.curveTo3,
						numArgs: 4,
						variableArgs: false
					};
					t['h'] = {
						id: OPS.closePath,
						numArgs: 0,
						variableArgs: false
					};
					t['re'] = {
						id: OPS.rectangle,
						numArgs: 4,
						variableArgs: false
					};
					t['S'] = {
						id: OPS.stroke,
						numArgs: 0,
						variableArgs: false
					};
					t['s'] = {
						id: OPS.closeStroke,
						numArgs: 0,
						variableArgs: false
					};
					t['f'] = {
						id: OPS.fill,
						numArgs: 0,
						variableArgs: false
					};
					t['F'] = {
						id: OPS.fill,
						numArgs: 0,
						variableArgs: false
					};
					t['f*'] = {
						id: OPS.eoFill,
						numArgs: 0,
						variableArgs: false
					};
					t['B'] = {
						id: OPS.fillStroke,
						numArgs: 0,
						variableArgs: false
					};
					t['B*'] = {
						id: OPS.eoFillStroke,
						numArgs: 0,
						variableArgs: false
					};
					t['b'] = {
						id: OPS.closeFillStroke,
						numArgs: 0,
						variableArgs: false
					};
					t['b*'] = {
						id: OPS.closeEOFillStroke,
						numArgs: 0,
						variableArgs: false
					};
					t['n'] = {
						id: OPS.endPath,
						numArgs: 0,
						variableArgs: false
					};
					t['W'] = {
						id: OPS.clip,
						numArgs: 0,
						variableArgs: false
					};
					t['W*'] = {
						id: OPS.eoClip,
						numArgs: 0,
						variableArgs: false
					};
					t['BT'] = {
						id: OPS.beginText,
						numArgs: 0,
						variableArgs: false
					};
					t['ET'] = {
						id: OPS.endText,
						numArgs: 0,
						variableArgs: false
					};
					t['Tc'] = {
						id: OPS.setCharSpacing,
						numArgs: 1,
						variableArgs: false
					};
					t['Tw'] = {
						id: OPS.setWordSpacing,
						numArgs: 1,
						variableArgs: false
					};
					t['Tz'] = {
						id: OPS.setHScale,
						numArgs: 1,
						variableArgs: false
					};
					t['TL'] = {
						id: OPS.setLeading,
						numArgs: 1,
						variableArgs: false
					};
					t['Tf'] = {
						id: OPS.setFont,
						numArgs: 2,
						variableArgs: false
					};
					t['Tr'] = {
						id: OPS.setTextRenderingMode,
						numArgs: 1,
						variableArgs: false
					};
					t['Ts'] = {
						id: OPS.setTextRise,
						numArgs: 1,
						variableArgs: false
					};
					t['Td'] = {
						id: OPS.moveText,
						numArgs: 2,
						variableArgs: false
					};
					t['TD'] = {
						id: OPS.setLeadingMoveText,
						numArgs: 2,
						variableArgs: false
					};
					t['Tm'] = {
						id: OPS.setTextMatrix,
						numArgs: 6,
						variableArgs: false
					};
					t['T*'] = {
						id: OPS.nextLine,
						numArgs: 0,
						variableArgs: false
					};
					t['Tj'] = {
						id: OPS.showText,
						numArgs: 1,
						variableArgs: false
					};
					t['TJ'] = {
						id: OPS.showSpacedText,
						numArgs: 1,
						variableArgs: false
					};
					t['\''] = {
						id: OPS.nextLineShowText,
						numArgs: 1,
						variableArgs: false
					};
					t['"'] = {
						id: OPS.nextLineSetSpacingShowText,
						numArgs: 3,
						variableArgs: false
					};
					t['d0'] = {
						id: OPS.setCharWidth,
						numArgs: 2,
						variableArgs: false
					};
					t['d1'] = {
						id: OPS.setCharWidthAndBounds,
						numArgs: 6,
						variableArgs: false
					};
					t['CS'] = {
						id: OPS.setStrokeColorSpace,
						numArgs: 1,
						variableArgs: false
					};
					t['cs'] = {
						id: OPS.setFillColorSpace,
						numArgs: 1,
						variableArgs: false
					};
					t['SC'] = {
						id: OPS.setStrokeColor,
						numArgs: 4,
						variableArgs: true
					};
					t['SCN'] = {
						id: OPS.setStrokeColorN,
						numArgs: 33,
						variableArgs: true
					};
					t['sc'] = {
						id: OPS.setFillColor,
						numArgs: 4,
						variableArgs: true
					};
					t['scn'] = {
						id: OPS.setFillColorN,
						numArgs: 33,
						variableArgs: true
					};
					t['G'] = {
						id: OPS.setStrokeGray,
						numArgs: 1,
						variableArgs: false
					};
					t['g'] = {
						id: OPS.setFillGray,
						numArgs: 1,
						variableArgs: false
					};
					t['RG'] = {
						id: OPS.setStrokeRGBColor,
						numArgs: 3,
						variableArgs: false
					};
					t['rg'] = {
						id: OPS.setFillRGBColor,
						numArgs: 3,
						variableArgs: false
					};
					t['K'] = {
						id: OPS.setStrokeCMYKColor,
						numArgs: 4,
						variableArgs: false
					};
					t['k'] = {
						id: OPS.setFillCMYKColor,
						numArgs: 4,
						variableArgs: false
					};
					t['sh'] = {
						id: OPS.shadingFill,
						numArgs: 1,
						variableArgs: false
					};
					t['BI'] = {
						id: OPS.beginInlineImage,
						numArgs: 0,
						variableArgs: false
					};
					t['ID'] = {
						id: OPS.beginImageData,
						numArgs: 0,
						variableArgs: false
					};
					t['EI'] = {
						id: OPS.endInlineImage,
						numArgs: 1,
						variableArgs: false
					};
					t['Do'] = {
						id: OPS.paintXObject,
						numArgs: 1,
						variableArgs: false
					};
					t['MP'] = {
						id: OPS.markPoint,
						numArgs: 1,
						variableArgs: false
					};
					t['DP'] = {
						id: OPS.markPointProps,
						numArgs: 2,
						variableArgs: false
					};
					t['BMC'] = {
						id: OPS.beginMarkedContent,
						numArgs: 1,
						variableArgs: false
					};
					t['BDC'] = {
						id: OPS.beginMarkedContentProps,
						numArgs: 2,
						variableArgs: false
					};
					t['EMC'] = {
						id: OPS.endMarkedContent,
						numArgs: 0,
						variableArgs: false
					};
					t['BX'] = {
						id: OPS.beginCompat,
						numArgs: 0,
						variableArgs: false
					};
					t['EX'] = {
						id: OPS.endCompat,
						numArgs: 0,
						variableArgs: false
					};
					t['BM'] = null;
					t['BD'] = null;
					t['true'] = null;
					t['fa'] = null;
					t['fal'] = null;
					t['fals'] = null;
					t['false'] = null;
					t['nu'] = null;
					t['nul'] = null;
					t['null'] = null;
				});
				function EvaluatorPreprocessor(stream, xref, stateManager) {
					this.opMap = getOPMap();
					this.parser = new Parser(new Lexer(stream, this.opMap), false, xref);
					this.stateManager = stateManager;
					this.nonProcessedArgs = [];
				}
				EvaluatorPreprocessor.prototype = {
					get savedStatesDepth() {
						return this.stateManager.stateStack.length;
					},
					read: function EvaluatorPreprocessor_read(operation) {
						var args = operation.args;
						while (true) {
							var obj = this.parser.getObj();
							if (isCmd(obj)) {
								var cmd = obj.cmd;
								var opSpec = this.opMap[cmd];
								if (!opSpec) {
									warn('Unknown command "' + cmd + '"');
									continue;
								}
								var fn = opSpec.id;
								var numArgs = opSpec.numArgs;
								var argsLength = args !== null ? args.length : 0;
								if (!opSpec.variableArgs) {
									if (argsLength !== numArgs) {
										var nonProcessedArgs = this.nonProcessedArgs;
										while (argsLength > numArgs) {
											nonProcessedArgs.push(args.shift());
											argsLength--;
										}
										while (argsLength < numArgs && nonProcessedArgs.length !== 0) {
											if (args === null) {
												args = [];
											}
											args.unshift(nonProcessedArgs.pop());
											argsLength++;
										}
									}
									if (argsLength < numArgs) {
										warn('Skipping command ' + fn + ': expected ' + numArgs + ' args, but received ' + argsLength + ' args.');
										if (args !== null) {
											args.length = 0;
										}
										continue;
									}
								} else if (argsLength > numArgs) {
									info('Command ' + fn + ': expected [0,' + numArgs + '] args, but received ' + argsLength + ' args.');
								}
								this.preprocessCommand(fn, args);
								operation.fn = fn;
								operation.args = args;
								return true;
							}
							if (isEOF(obj)) {
								return false;
							}
							if (obj !== null) {
								if (args === null) {
									args = [];
								}
								args.push(obj);
								assert(args.length <= 33, 'Too many arguments');
							}
						}
					},
					preprocessCommand: function EvaluatorPreprocessor_preprocessCommand(fn, args) {
						switch (fn | 0) {
							case OPS.save:
								this.stateManager.save();
								break;
							case OPS.restore:
								this.stateManager.restore();
								break;
							case OPS.transform:
								this.stateManager.transform(args);
								break;
						}
					}
				};
				return EvaluatorPreprocessor;
			}();
			var QueueOptimizer = function QueueOptimizerClosure() {
				function addState(parentState, pattern, fn) {
					var state = parentState;
					for (var i = 0, ii = pattern.length - 1; i < ii; i++) {
						var item = pattern[i];
						state = state[item] || (state[item] = []);
					}
					state[pattern[pattern.length - 1]] = fn;
				}
				function handlePaintSolidColorImageMask(iFirstSave, count, fnArray, argsArray) {
					var iFirstPIMXO = iFirstSave + 2;
					for (var i = 0; i < count; i++) {
						var arg = argsArray[iFirstPIMXO + 4 * i];
						var imageMask = arg.length === 1 && arg[0];
						if (imageMask && imageMask.width === 1 && imageMask.height === 1 && (!imageMask.data.length || imageMask.data.length === 1 && imageMask.data[0] === 0)) {
							fnArray[iFirstPIMXO + 4 * i] = OPS.paintSolidColorImageMask;
							continue;
						}
						break;
					}
					return count - i;
				}
				var InitialState = [];
				addState(InitialState, [
					OPS.save,
					OPS.transform,
					OPS.paintInlineImageXObject,
					OPS.restore
				], function foundInlineImageGroup(context) {
					var MIN_IMAGES_IN_INLINE_IMAGES_BLOCK = 10;
					var MAX_IMAGES_IN_INLINE_IMAGES_BLOCK = 200;
					var MAX_WIDTH = 1000;
					var IMAGE_PADDING = 1;
					var fnArray = context.fnArray, argsArray = context.argsArray;
					var curr = context.iCurr;
					var iFirstSave = curr - 3;
					var iFirstTransform = curr - 2;
					var iFirstPIIXO = curr - 1;
					var i = iFirstSave + 4;
					var ii = fnArray.length;
					while (i + 3 < ii) {
						if (fnArray[i] !== OPS.save || fnArray[i + 1] !== OPS.transform || fnArray[i + 2] !== OPS.paintInlineImageXObject || fnArray[i + 3] !== OPS.restore) {
							break;
						}
						i += 4;
					}
					var count = Math.min((i - iFirstSave) / 4, MAX_IMAGES_IN_INLINE_IMAGES_BLOCK);
					if (count < MIN_IMAGES_IN_INLINE_IMAGES_BLOCK) {
						return i;
					}
					var maxX = 0;
					var map = [], maxLineHeight = 0;
					var currentX = IMAGE_PADDING, currentY = IMAGE_PADDING;
					var q;
					for (q = 0; q < count; q++) {
						var transform = argsArray[iFirstTransform + (q << 2)];
						var img = argsArray[iFirstPIIXO + (q << 2)][0];
						if (currentX + img.width > MAX_WIDTH) {
							maxX = Math.max(maxX, currentX);
							currentY += maxLineHeight + 2 * IMAGE_PADDING;
							currentX = 0;
							maxLineHeight = 0;
						}
						map.push({
							transform: transform,
							x: currentX,
							y: currentY,
							w: img.width,
							h: img.height
						});
						currentX += img.width + 2 * IMAGE_PADDING;
						maxLineHeight = Math.max(maxLineHeight, img.height);
					}
					var imgWidth = Math.max(maxX, currentX) + IMAGE_PADDING;
					var imgHeight = currentY + maxLineHeight + IMAGE_PADDING;
					var imgData = new Uint8Array(imgWidth * imgHeight * 4);
					var imgRowSize = imgWidth << 2;
					for (q = 0; q < count; q++) {
						var data = argsArray[iFirstPIIXO + (q << 2)][0].data;
						var rowSize = map[q].w << 2;
						var dataOffset = 0;
						var offset = map[q].x + map[q].y * imgWidth << 2;
						imgData.set(data.subarray(0, rowSize), offset - imgRowSize);
						for (var k = 0, kk = map[q].h; k < kk; k++) {
							imgData.set(data.subarray(dataOffset, dataOffset + rowSize), offset);
							dataOffset += rowSize;
							offset += imgRowSize;
						}
						imgData.set(data.subarray(dataOffset - rowSize, dataOffset), offset);
						while (offset >= 0) {
							data[offset - 4] = data[offset];
							data[offset - 3] = data[offset + 1];
							data[offset - 2] = data[offset + 2];
							data[offset - 1] = data[offset + 3];
							data[offset + rowSize] = data[offset + rowSize - 4];
							data[offset + rowSize + 1] = data[offset + rowSize - 3];
							data[offset + rowSize + 2] = data[offset + rowSize - 2];
							data[offset + rowSize + 3] = data[offset + rowSize - 1];
							offset -= imgRowSize;
						}
					}
					fnArray.splice(iFirstSave, count * 4, OPS.paintInlineImageXObjectGroup);
					argsArray.splice(iFirstSave, count * 4, [
						{
							width: imgWidth,
							height: imgHeight,
							kind: ImageKind.RGBA_32BPP,
							data: imgData
						},
						map
					]);
					return iFirstSave + 1;
				});
				addState(InitialState, [
					OPS.save,
					OPS.transform,
					OPS.paintImageMaskXObject,
					OPS.restore
				], function foundImageMaskGroup(context) {
					var MIN_IMAGES_IN_MASKS_BLOCK = 10;
					var MAX_IMAGES_IN_MASKS_BLOCK = 100;
					var MAX_SAME_IMAGES_IN_MASKS_BLOCK = 1000;
					var fnArray = context.fnArray, argsArray = context.argsArray;
					var curr = context.iCurr;
					var iFirstSave = curr - 3;
					var iFirstTransform = curr - 2;
					var iFirstPIMXO = curr - 1;
					var i = iFirstSave + 4;
					var ii = fnArray.length;
					while (i + 3 < ii) {
						if (fnArray[i] !== OPS.save || fnArray[i + 1] !== OPS.transform || fnArray[i + 2] !== OPS.paintImageMaskXObject || fnArray[i + 3] !== OPS.restore) {
							break;
						}
						i += 4;
					}
					var count = (i - iFirstSave) / 4;
					count = handlePaintSolidColorImageMask(iFirstSave, count, fnArray, argsArray);
					if (count < MIN_IMAGES_IN_MASKS_BLOCK) {
						return i;
					}
					var q;
					var isSameImage = false;
					var iTransform, transformArgs;
					var firstPIMXOArg0 = argsArray[iFirstPIMXO][0];
					if (argsArray[iFirstTransform][1] === 0 && argsArray[iFirstTransform][2] === 0) {
						isSameImage = true;
						var firstTransformArg0 = argsArray[iFirstTransform][0];
						var firstTransformArg3 = argsArray[iFirstTransform][3];
						iTransform = iFirstTransform + 4;
						var iPIMXO = iFirstPIMXO + 4;
						for (q = 1; q < count; q++, iTransform += 4, iPIMXO += 4) {
							transformArgs = argsArray[iTransform];
							if (argsArray[iPIMXO][0] !== firstPIMXOArg0 || transformArgs[0] !== firstTransformArg0 || transformArgs[1] !== 0 || transformArgs[2] !== 0 || transformArgs[3] !== firstTransformArg3) {
								if (q < MIN_IMAGES_IN_MASKS_BLOCK) {
									isSameImage = false;
								} else {
									count = q;
								}
								break;
							}
						}
					}
					if (isSameImage) {
						count = Math.min(count, MAX_SAME_IMAGES_IN_MASKS_BLOCK);
						var positions = new Float32Array(count * 2);
						iTransform = iFirstTransform;
						for (q = 0; q < count; q++, iTransform += 4) {
							transformArgs = argsArray[iTransform];
							positions[q << 1] = transformArgs[4];
							positions[(q << 1) + 1] = transformArgs[5];
						}
						fnArray.splice(iFirstSave, count * 4, OPS.paintImageMaskXObjectRepeat);
						argsArray.splice(iFirstSave, count * 4, [
							firstPIMXOArg0,
							firstTransformArg0,
							firstTransformArg3,
							positions
						]);
					} else {
						count = Math.min(count, MAX_IMAGES_IN_MASKS_BLOCK);
						var images = [];
						for (q = 0; q < count; q++) {
							transformArgs = argsArray[iFirstTransform + (q << 2)];
							var maskParams = argsArray[iFirstPIMXO + (q << 2)][0];
							images.push({
								data: maskParams.data,
								width: maskParams.width,
								height: maskParams.height,
								transform: transformArgs
							});
						}
						fnArray.splice(iFirstSave, count * 4, OPS.paintImageMaskXObjectGroup);
						argsArray.splice(iFirstSave, count * 4, [images]);
					}
					return iFirstSave + 1;
				});
				addState(InitialState, [
					OPS.save,
					OPS.transform,
					OPS.paintImageXObject,
					OPS.restore
				], function (context) {
					var MIN_IMAGES_IN_BLOCK = 3;
					var MAX_IMAGES_IN_BLOCK = 1000;
					var fnArray = context.fnArray, argsArray = context.argsArray;
					var curr = context.iCurr;
					var iFirstSave = curr - 3;
					var iFirstTransform = curr - 2;
					var iFirstPIXO = curr - 1;
					var iFirstRestore = curr;
					if (argsArray[iFirstTransform][1] !== 0 || argsArray[iFirstTransform][2] !== 0) {
						return iFirstRestore + 1;
					}
					var firstPIXOArg0 = argsArray[iFirstPIXO][0];
					var firstTransformArg0 = argsArray[iFirstTransform][0];
					var firstTransformArg3 = argsArray[iFirstTransform][3];
					var i = iFirstSave + 4;
					var ii = fnArray.length;
					while (i + 3 < ii) {
						if (fnArray[i] !== OPS.save || fnArray[i + 1] !== OPS.transform || fnArray[i + 2] !== OPS.paintImageXObject || fnArray[i + 3] !== OPS.restore) {
							break;
						}
						if (argsArray[i + 1][0] !== firstTransformArg0 || argsArray[i + 1][1] !== 0 || argsArray[i + 1][2] !== 0 || argsArray[i + 1][3] !== firstTransformArg3) {
							break;
						}
						if (argsArray[i + 2][0] !== firstPIXOArg0) {
							break;
						}
						i += 4;
					}
					var count = Math.min((i - iFirstSave) / 4, MAX_IMAGES_IN_BLOCK);
					if (count < MIN_IMAGES_IN_BLOCK) {
						return i;
					}
					var positions = new Float32Array(count * 2);
					var iTransform = iFirstTransform;
					for (var q = 0; q < count; q++, iTransform += 4) {
						var transformArgs = argsArray[iTransform];
						positions[q << 1] = transformArgs[4];
						positions[(q << 1) + 1] = transformArgs[5];
					}
					var args = [
						firstPIXOArg0,
						firstTransformArg0,
						firstTransformArg3,
						positions
					];
					fnArray.splice(iFirstSave, count * 4, OPS.paintImageXObjectRepeat);
					argsArray.splice(iFirstSave, count * 4, args);
					return iFirstSave + 1;
				});
				addState(InitialState, [
					OPS.beginText,
					OPS.setFont,
					OPS.setTextMatrix,
					OPS.showText,
					OPS.endText
				], function (context) {
					var MIN_CHARS_IN_BLOCK = 3;
					var MAX_CHARS_IN_BLOCK = 1000;
					var fnArray = context.fnArray, argsArray = context.argsArray;
					var curr = context.iCurr;
					var iFirstBeginText = curr - 4;
					var iFirstSetFont = curr - 3;
					var iFirstSetTextMatrix = curr - 2;
					var iFirstShowText = curr - 1;
					var iFirstEndText = curr;
					var firstSetFontArg0 = argsArray[iFirstSetFont][0];
					var firstSetFontArg1 = argsArray[iFirstSetFont][1];
					var i = iFirstBeginText + 5;
					var ii = fnArray.length;
					while (i + 4 < ii) {
						if (fnArray[i] !== OPS.beginText || fnArray[i + 1] !== OPS.setFont || fnArray[i + 2] !== OPS.setTextMatrix || fnArray[i + 3] !== OPS.showText || fnArray[i + 4] !== OPS.endText) {
							break;
						}
						if (argsArray[i + 1][0] !== firstSetFontArg0 || argsArray[i + 1][1] !== firstSetFontArg1) {
							break;
						}
						i += 5;
					}
					var count = Math.min((i - iFirstBeginText) / 5, MAX_CHARS_IN_BLOCK);
					if (count < MIN_CHARS_IN_BLOCK) {
						return i;
					}
					var iFirst = iFirstBeginText;
					if (iFirstBeginText >= 4 && fnArray[iFirstBeginText - 4] === fnArray[iFirstSetFont] && fnArray[iFirstBeginText - 3] === fnArray[iFirstSetTextMatrix] && fnArray[iFirstBeginText - 2] === fnArray[iFirstShowText] && fnArray[iFirstBeginText - 1] === fnArray[iFirstEndText] && argsArray[iFirstBeginText - 4][0] === firstSetFontArg0 && argsArray[iFirstBeginText - 4][1] === firstSetFontArg1) {
						count++;
						iFirst -= 5;
					}
					var iEndText = iFirst + 4;
					for (var q = 1; q < count; q++) {
						fnArray.splice(iEndText, 3);
						argsArray.splice(iEndText, 3);
						iEndText += 2;
					}
					return iEndText + 1;
				});
				function QueueOptimizer() {
				}
				QueueOptimizer.prototype = {
					optimize: function QueueOptimizer_optimize(queue) {
						var fnArray = queue.fnArray, argsArray = queue.argsArray;
						var context = {
							iCurr: 0,
							fnArray: fnArray,
							argsArray: argsArray
						};
						var state;
						var i = 0, ii = fnArray.length;
						while (i < ii) {
							state = (state || InitialState)[fnArray[i]];
							if (typeof state === 'function') {
								context.iCurr = i;
								i = state(context);
								state = undefined;
								ii = context.fnArray.length;
							} else {
								i++;
							}
						}
					}
				};
				return QueueOptimizer;
			}();
			exports.OperatorList = OperatorList;
			exports.PartialEvaluator = PartialEvaluator;

			/***/
		}),
/* 15 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreArithmeticDecoder = __w_pdfjs_require__(10);
			var info = sharedUtil.info;
			var warn = sharedUtil.warn;
			var error = sharedUtil.error;
			var log2 = sharedUtil.log2;
			var readUint16 = sharedUtil.readUint16;
			var readUint32 = sharedUtil.readUint32;
			var ArithmeticDecoder = coreArithmeticDecoder.ArithmeticDecoder;
			var JpxImage = function JpxImageClosure() {
				var SubbandsGainLog2 = {
					'LL': 0,
					'LH': 1,
					'HL': 1,
					'HH': 2
				};
				function JpxImage() {
					this.failOnCorruptedImage = false;
				}
				JpxImage.prototype = {
					parse: function JpxImage_parse(data) {
						var head = readUint16(data, 0);
						if (head === 0xFF4F) {
							this.parseCodestream(data, 0, data.length);
							return;
						}
						var position = 0, length = data.length;
						while (position < length) {
							var headerSize = 8;
							var lbox = readUint32(data, position);
							var tbox = readUint32(data, position + 4);
							position += headerSize;
							if (lbox === 1) {
								lbox = readUint32(data, position) * 4294967296 + readUint32(data, position + 4);
								position += 8;
								headerSize += 8;
							}
							if (lbox === 0) {
								lbox = length - position + headerSize;
							}
							if (lbox < headerSize) {
								error('JPX Error: Invalid box field size');
							}
							var dataLength = lbox - headerSize;
							var jumpDataLength = true;
							switch (tbox) {
								case 0x6A703268:
									jumpDataLength = false;
									break;
								case 0x636F6C72:
									var method = data[position];
									if (method === 1) {
										var colorspace = readUint32(data, position + 3);
										switch (colorspace) {
											case 16:
											case 17:
											case 18:
												break;
											default:
												warn('Unknown colorspace ' + colorspace);
												break;
										}
									} else if (method === 2) {
										info('ICC profile not supported');
									}
									break;
								case 0x6A703263:
									this.parseCodestream(data, position, position + dataLength);
									break;
								case 0x6A502020:
									if (0x0d0a870a !== readUint32(data, position)) {
										warn('Invalid JP2 signature');
									}
									break;
								case 0x6A501A1A:
								case 0x66747970:
								case 0x72726571:
								case 0x72657320:
								case 0x69686472:
									break;
								default:
									var headerType = String.fromCharCode(tbox >> 24 & 0xFF, tbox >> 16 & 0xFF, tbox >> 8 & 0xFF, tbox & 0xFF);
									warn('Unsupported header type ' + tbox + ' (' + headerType + ')');
									break;
							}
							if (jumpDataLength) {
								position += dataLength;
							}
						}
					},
					parseImageProperties: function JpxImage_parseImageProperties(stream) {
						var newByte = stream.getByte();
						while (newByte >= 0) {
							var oldByte = newByte;
							newByte = stream.getByte();
							var code = oldByte << 8 | newByte;
							if (code === 0xFF51) {
								stream.skip(4);
								var Xsiz = stream.getInt32() >>> 0;
								var Ysiz = stream.getInt32() >>> 0;
								var XOsiz = stream.getInt32() >>> 0;
								var YOsiz = stream.getInt32() >>> 0;
								stream.skip(16);
								var Csiz = stream.getUint16();
								this.width = Xsiz - XOsiz;
								this.height = Ysiz - YOsiz;
								this.componentsCount = Csiz;
								this.bitsPerComponent = 8;
								return;
							}
						}
						error('JPX Error: No size marker found in JPX stream');
					},
					parseCodestream: function JpxImage_parseCodestream(data, start, end) {
						var context = {};
						var doNotRecover = false;
						try {
							var position = start;
							while (position + 1 < end) {
								var code = readUint16(data, position);
								position += 2;
								var length = 0, j, sqcd, spqcds, spqcdSize, scalarExpounded, tile;
								switch (code) {
									case 0xFF4F:
										context.mainHeader = true;
										break;
									case 0xFFD9:
										break;
									case 0xFF51:
										length = readUint16(data, position);
										var siz = {};
										siz.Xsiz = readUint32(data, position + 4);
										siz.Ysiz = readUint32(data, position + 8);
										siz.XOsiz = readUint32(data, position + 12);
										siz.YOsiz = readUint32(data, position + 16);
										siz.XTsiz = readUint32(data, position + 20);
										siz.YTsiz = readUint32(data, position + 24);
										siz.XTOsiz = readUint32(data, position + 28);
										siz.YTOsiz = readUint32(data, position + 32);
										var componentsCount = readUint16(data, position + 36);
										siz.Csiz = componentsCount;
										var components = [];
										j = position + 38;
										for (var i = 0; i < componentsCount; i++) {
											var component = {
												precision: (data[j] & 0x7F) + 1,
												isSigned: !!(data[j] & 0x80),
												XRsiz: data[j + 1],
												YRsiz: data[j + 1]
											};
											calculateComponentDimensions(component, siz);
											components.push(component);
										}
										context.SIZ = siz;
										context.components = components;
										calculateTileGrids(context, components);
										context.QCC = [];
										context.COC = [];
										break;
									case 0xFF5C:
										length = readUint16(data, position);
										var qcd = {};
										j = position + 2;
										sqcd = data[j++];
										switch (sqcd & 0x1F) {
											case 0:
												spqcdSize = 8;
												scalarExpounded = true;
												break;
											case 1:
												spqcdSize = 16;
												scalarExpounded = false;
												break;
											case 2:
												spqcdSize = 16;
												scalarExpounded = true;
												break;
											default:
												throw new Error('Invalid SQcd value ' + sqcd);
										}
										qcd.noQuantization = spqcdSize === 8;
										qcd.scalarExpounded = scalarExpounded;
										qcd.guardBits = sqcd >> 5;
										spqcds = [];
										while (j < length + position) {
											var spqcd = {};
											if (spqcdSize === 8) {
												spqcd.epsilon = data[j++] >> 3;
												spqcd.mu = 0;
											} else {
												spqcd.epsilon = data[j] >> 3;
												spqcd.mu = (data[j] & 0x7) << 8 | data[j + 1];
												j += 2;
											}
											spqcds.push(spqcd);
										}
										qcd.SPqcds = spqcds;
										if (context.mainHeader) {
											context.QCD = qcd;
										} else {
											context.currentTile.QCD = qcd;
											context.currentTile.QCC = [];
										}
										break;
									case 0xFF5D:
										length = readUint16(data, position);
										var qcc = {};
										j = position + 2;
										var cqcc;
										if (context.SIZ.Csiz < 257) {
											cqcc = data[j++];
										} else {
											cqcc = readUint16(data, j);
											j += 2;
										}
										sqcd = data[j++];
										switch (sqcd & 0x1F) {
											case 0:
												spqcdSize = 8;
												scalarExpounded = true;
												break;
											case 1:
												spqcdSize = 16;
												scalarExpounded = false;
												break;
											case 2:
												spqcdSize = 16;
												scalarExpounded = true;
												break;
											default:
												throw new Error('Invalid SQcd value ' + sqcd);
										}
										qcc.noQuantization = spqcdSize === 8;
										qcc.scalarExpounded = scalarExpounded;
										qcc.guardBits = sqcd >> 5;
										spqcds = [];
										while (j < length + position) {
											spqcd = {};
											if (spqcdSize === 8) {
												spqcd.epsilon = data[j++] >> 3;
												spqcd.mu = 0;
											} else {
												spqcd.epsilon = data[j] >> 3;
												spqcd.mu = (data[j] & 0x7) << 8 | data[j + 1];
												j += 2;
											}
											spqcds.push(spqcd);
										}
										qcc.SPqcds = spqcds;
										if (context.mainHeader) {
											context.QCC[cqcc] = qcc;
										} else {
											context.currentTile.QCC[cqcc] = qcc;
										}
										break;
									case 0xFF52:
										length = readUint16(data, position);
										var cod = {};
										j = position + 2;
										var scod = data[j++];
										cod.entropyCoderWithCustomPrecincts = !!(scod & 1);
										cod.sopMarkerUsed = !!(scod & 2);
										cod.ephMarkerUsed = !!(scod & 4);
										cod.progressionOrder = data[j++];
										cod.layersCount = readUint16(data, j);
										j += 2;
										cod.multipleComponentTransform = data[j++];
										cod.decompositionLevelsCount = data[j++];
										cod.xcb = (data[j++] & 0xF) + 2;
										cod.ycb = (data[j++] & 0xF) + 2;
										var blockStyle = data[j++];
										cod.selectiveArithmeticCodingBypass = !!(blockStyle & 1);
										cod.resetContextProbabilities = !!(blockStyle & 2);
										cod.terminationOnEachCodingPass = !!(blockStyle & 4);
										cod.verticalyStripe = !!(blockStyle & 8);
										cod.predictableTermination = !!(blockStyle & 16);
										cod.segmentationSymbolUsed = !!(blockStyle & 32);
										cod.reversibleTransformation = data[j++];
										if (cod.entropyCoderWithCustomPrecincts) {
											var precinctsSizes = [];
											while (j < length + position) {
												var precinctsSize = data[j++];
												precinctsSizes.push({
													PPx: precinctsSize & 0xF,
													PPy: precinctsSize >> 4
												});
											}
											cod.precinctsSizes = precinctsSizes;
										}
										var unsupported = [];
										if (cod.selectiveArithmeticCodingBypass) {
											unsupported.push('selectiveArithmeticCodingBypass');
										}
										if (cod.resetContextProbabilities) {
											unsupported.push('resetContextProbabilities');
										}
										if (cod.terminationOnEachCodingPass) {
											unsupported.push('terminationOnEachCodingPass');
										}
										if (cod.verticalyStripe) {
											unsupported.push('verticalyStripe');
										}
										if (cod.predictableTermination) {
											unsupported.push('predictableTermination');
										}
										if (unsupported.length > 0) {
											doNotRecover = true;
											throw new Error('Unsupported COD options (' + unsupported.join(', ') + ')');
										}
										if (context.mainHeader) {
											context.COD = cod;
										} else {
											context.currentTile.COD = cod;
											context.currentTile.COC = [];
										}
										break;
									case 0xFF90:
										length = readUint16(data, position);
										tile = {};
										tile.index = readUint16(data, position + 2);
										tile.length = readUint32(data, position + 4);
										tile.dataEnd = tile.length + position - 2;
										tile.partIndex = data[position + 8];
										tile.partsCount = data[position + 9];
										context.mainHeader = false;
										if (tile.partIndex === 0) {
											tile.COD = context.COD;
											tile.COC = context.COC.slice(0);
											tile.QCD = context.QCD;
											tile.QCC = context.QCC.slice(0);
										}
										context.currentTile = tile;
										break;
									case 0xFF93:
										tile = context.currentTile;
										if (tile.partIndex === 0) {
											initializeTile(context, tile.index);
											buildPackets(context);
										}
										length = tile.dataEnd - position;
										parseTilePackets(context, data, position, length);
										break;
									case 0xFF55:
									case 0xFF57:
									case 0xFF58:
									case 0xFF64:
										length = readUint16(data, position);
										break;
									case 0xFF53:
										throw new Error('Codestream code 0xFF53 (COC) is ' + 'not implemented');
									default:
										throw new Error('Unknown codestream code: ' + code.toString(16));
								}
								position += length;
							}
						} catch (e) {
							if (doNotRecover || this.failOnCorruptedImage) {
								error('JPX Error: ' + e.message);
							} else {
								warn('JPX: Trying to recover from: ' + e.message);
							}
						}
						this.tiles = transformComponents(context);
						this.width = context.SIZ.Xsiz - context.SIZ.XOsiz;
						this.height = context.SIZ.Ysiz - context.SIZ.YOsiz;
						this.componentsCount = context.SIZ.Csiz;
					}
				};
				function calculateComponentDimensions(component, siz) {
					component.x0 = Math.ceil(siz.XOsiz / component.XRsiz);
					component.x1 = Math.ceil(siz.Xsiz / component.XRsiz);
					component.y0 = Math.ceil(siz.YOsiz / component.YRsiz);
					component.y1 = Math.ceil(siz.Ysiz / component.YRsiz);
					component.width = component.x1 - component.x0;
					component.height = component.y1 - component.y0;
				}
				function calculateTileGrids(context, components) {
					var siz = context.SIZ;
					var tile, tiles = [];
					var numXtiles = Math.ceil((siz.Xsiz - siz.XTOsiz) / siz.XTsiz);
					var numYtiles = Math.ceil((siz.Ysiz - siz.YTOsiz) / siz.YTsiz);
					for (var q = 0; q < numYtiles; q++) {
						for (var p = 0; p < numXtiles; p++) {
							tile = {};
							tile.tx0 = Math.max(siz.XTOsiz + p * siz.XTsiz, siz.XOsiz);
							tile.ty0 = Math.max(siz.YTOsiz + q * siz.YTsiz, siz.YOsiz);
							tile.tx1 = Math.min(siz.XTOsiz + (p + 1) * siz.XTsiz, siz.Xsiz);
							tile.ty1 = Math.min(siz.YTOsiz + (q + 1) * siz.YTsiz, siz.Ysiz);
							tile.width = tile.tx1 - tile.tx0;
							tile.height = tile.ty1 - tile.ty0;
							tile.components = [];
							tiles.push(tile);
						}
					}
					context.tiles = tiles;
					var componentsCount = siz.Csiz;
					for (var i = 0, ii = componentsCount; i < ii; i++) {
						var component = components[i];
						for (var j = 0, jj = tiles.length; j < jj; j++) {
							var tileComponent = {};
							tile = tiles[j];
							tileComponent.tcx0 = Math.ceil(tile.tx0 / component.XRsiz);
							tileComponent.tcy0 = Math.ceil(tile.ty0 / component.YRsiz);
							tileComponent.tcx1 = Math.ceil(tile.tx1 / component.XRsiz);
							tileComponent.tcy1 = Math.ceil(tile.ty1 / component.YRsiz);
							tileComponent.width = tileComponent.tcx1 - tileComponent.tcx0;
							tileComponent.height = tileComponent.tcy1 - tileComponent.tcy0;
							tile.components[i] = tileComponent;
						}
					}
				}
				function getBlocksDimensions(context, component, r) {
					var codOrCoc = component.codingStyleParameters;
					var result = {};
					if (!codOrCoc.entropyCoderWithCustomPrecincts) {
						result.PPx = 15;
						result.PPy = 15;
					} else {
						result.PPx = codOrCoc.precinctsSizes[r].PPx;
						result.PPy = codOrCoc.precinctsSizes[r].PPy;
					}
					result.xcb_ = r > 0 ? Math.min(codOrCoc.xcb, result.PPx - 1) : Math.min(codOrCoc.xcb, result.PPx);
					result.ycb_ = r > 0 ? Math.min(codOrCoc.ycb, result.PPy - 1) : Math.min(codOrCoc.ycb, result.PPy);
					return result;
				}
				function buildPrecincts(context, resolution, dimensions) {
					var precinctWidth = 1 << dimensions.PPx;
					var precinctHeight = 1 << dimensions.PPy;
					var isZeroRes = resolution.resLevel === 0;
					var precinctWidthInSubband = 1 << dimensions.PPx + (isZeroRes ? 0 : -1);
					var precinctHeightInSubband = 1 << dimensions.PPy + (isZeroRes ? 0 : -1);
					var numprecinctswide = resolution.trx1 > resolution.trx0 ? Math.ceil(resolution.trx1 / precinctWidth) - Math.floor(resolution.trx0 / precinctWidth) : 0;
					var numprecinctshigh = resolution.try1 > resolution.try0 ? Math.ceil(resolution.try1 / precinctHeight) - Math.floor(resolution.try0 / precinctHeight) : 0;
					var numprecincts = numprecinctswide * numprecinctshigh;
					resolution.precinctParameters = {
						precinctWidth: precinctWidth,
						precinctHeight: precinctHeight,
						numprecinctswide: numprecinctswide,
						numprecinctshigh: numprecinctshigh,
						numprecincts: numprecincts,
						precinctWidthInSubband: precinctWidthInSubband,
						precinctHeightInSubband: precinctHeightInSubband
					};
				}
				function buildCodeblocks(context, subband, dimensions) {
					var xcb_ = dimensions.xcb_;
					var ycb_ = dimensions.ycb_;
					var codeblockWidth = 1 << xcb_;
					var codeblockHeight = 1 << ycb_;
					var cbx0 = subband.tbx0 >> xcb_;
					var cby0 = subband.tby0 >> ycb_;
					var cbx1 = subband.tbx1 + codeblockWidth - 1 >> xcb_;
					var cby1 = subband.tby1 + codeblockHeight - 1 >> ycb_;
					var precinctParameters = subband.resolution.precinctParameters;
					var codeblocks = [];
					var precincts = [];
					var i, j, codeblock, precinctNumber;
					for (j = cby0; j < cby1; j++) {
						for (i = cbx0; i < cbx1; i++) {
							codeblock = {
								cbx: i,
								cby: j,
								tbx0: codeblockWidth * i,
								tby0: codeblockHeight * j,
								tbx1: codeblockWidth * (i + 1),
								tby1: codeblockHeight * (j + 1)
							};
							codeblock.tbx0_ = Math.max(subband.tbx0, codeblock.tbx0);
							codeblock.tby0_ = Math.max(subband.tby0, codeblock.tby0);
							codeblock.tbx1_ = Math.min(subband.tbx1, codeblock.tbx1);
							codeblock.tby1_ = Math.min(subband.tby1, codeblock.tby1);
							var pi = Math.floor((codeblock.tbx0_ - subband.tbx0) / precinctParameters.precinctWidthInSubband);
							var pj = Math.floor((codeblock.tby0_ - subband.tby0) / precinctParameters.precinctHeightInSubband);
							precinctNumber = pi + pj * precinctParameters.numprecinctswide;
							codeblock.precinctNumber = precinctNumber;
							codeblock.subbandType = subband.type;
							codeblock.Lblock = 3;
							if (codeblock.tbx1_ <= codeblock.tbx0_ || codeblock.tby1_ <= codeblock.tby0_) {
								continue;
							}
							codeblocks.push(codeblock);
							var precinct = precincts[precinctNumber];
							if (precinct !== undefined) {
								if (i < precinct.cbxMin) {
									precinct.cbxMin = i;
								} else if (i > precinct.cbxMax) {
									precinct.cbxMax = i;
								}
								if (j < precinct.cbyMin) {
									precinct.cbxMin = j;
								} else if (j > precinct.cbyMax) {
									precinct.cbyMax = j;
								}
							} else {
								precincts[precinctNumber] = precinct = {
									cbxMin: i,
									cbyMin: j,
									cbxMax: i,
									cbyMax: j
								};
							}
							codeblock.precinct = precinct;
						}
					}
					subband.codeblockParameters = {
						codeblockWidth: xcb_,
						codeblockHeight: ycb_,
						numcodeblockwide: cbx1 - cbx0 + 1,
						numcodeblockhigh: cby1 - cby0 + 1
					};
					subband.codeblocks = codeblocks;
					subband.precincts = precincts;
				}
				function createPacket(resolution, precinctNumber, layerNumber) {
					var precinctCodeblocks = [];
					var subbands = resolution.subbands;
					for (var i = 0, ii = subbands.length; i < ii; i++) {
						var subband = subbands[i];
						var codeblocks = subband.codeblocks;
						for (var j = 0, jj = codeblocks.length; j < jj; j++) {
							var codeblock = codeblocks[j];
							if (codeblock.precinctNumber !== precinctNumber) {
								continue;
							}
							precinctCodeblocks.push(codeblock);
						}
					}
					return {
						layerNumber: layerNumber,
						codeblocks: precinctCodeblocks
					};
				}
				function LayerResolutionComponentPositionIterator(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var layersCount = tile.codingStyleDefaultParameters.layersCount;
					var componentsCount = siz.Csiz;
					var maxDecompositionLevelsCount = 0;
					for (var q = 0; q < componentsCount; q++) {
						maxDecompositionLevelsCount = Math.max(maxDecompositionLevelsCount, tile.components[q].codingStyleParameters.decompositionLevelsCount);
					}
					var l = 0, r = 0, i = 0, k = 0;
					this.nextPacket = function JpxImage_nextPacket() {
						for (; l < layersCount; l++) {
							for (; r <= maxDecompositionLevelsCount; r++) {
								for (; i < componentsCount; i++) {
									var component = tile.components[i];
									if (r > component.codingStyleParameters.decompositionLevelsCount) {
										continue;
									}
									var resolution = component.resolutions[r];
									var numprecincts = resolution.precinctParameters.numprecincts;
									for (; k < numprecincts;) {
										var packet = createPacket(resolution, k, l);
										k++;
										return packet;
									}
									k = 0;
								}
								i = 0;
							}
							r = 0;
						}
						error('JPX Error: Out of packets');
					};
				}
				function ResolutionLayerComponentPositionIterator(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var layersCount = tile.codingStyleDefaultParameters.layersCount;
					var componentsCount = siz.Csiz;
					var maxDecompositionLevelsCount = 0;
					for (var q = 0; q < componentsCount; q++) {
						maxDecompositionLevelsCount = Math.max(maxDecompositionLevelsCount, tile.components[q].codingStyleParameters.decompositionLevelsCount);
					}
					var r = 0, l = 0, i = 0, k = 0;
					this.nextPacket = function JpxImage_nextPacket() {
						for (; r <= maxDecompositionLevelsCount; r++) {
							for (; l < layersCount; l++) {
								for (; i < componentsCount; i++) {
									var component = tile.components[i];
									if (r > component.codingStyleParameters.decompositionLevelsCount) {
										continue;
									}
									var resolution = component.resolutions[r];
									var numprecincts = resolution.precinctParameters.numprecincts;
									for (; k < numprecincts;) {
										var packet = createPacket(resolution, k, l);
										k++;
										return packet;
									}
									k = 0;
								}
								i = 0;
							}
							l = 0;
						}
						error('JPX Error: Out of packets');
					};
				}
				function ResolutionPositionComponentLayerIterator(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var layersCount = tile.codingStyleDefaultParameters.layersCount;
					var componentsCount = siz.Csiz;
					var l, r, c, p;
					var maxDecompositionLevelsCount = 0;
					for (c = 0; c < componentsCount; c++) {
						var component = tile.components[c];
						maxDecompositionLevelsCount = Math.max(maxDecompositionLevelsCount, component.codingStyleParameters.decompositionLevelsCount);
					}
					var maxNumPrecinctsInLevel = new Int32Array(maxDecompositionLevelsCount + 1);
					for (r = 0; r <= maxDecompositionLevelsCount; ++r) {
						var maxNumPrecincts = 0;
						for (c = 0; c < componentsCount; ++c) {
							var resolutions = tile.components[c].resolutions;
							if (r < resolutions.length) {
								maxNumPrecincts = Math.max(maxNumPrecincts, resolutions[r].precinctParameters.numprecincts);
							}
						}
						maxNumPrecinctsInLevel[r] = maxNumPrecincts;
					}
					l = 0;
					r = 0;
					c = 0;
					p = 0;
					this.nextPacket = function JpxImage_nextPacket() {
						for (; r <= maxDecompositionLevelsCount; r++) {
							for (; p < maxNumPrecinctsInLevel[r]; p++) {
								for (; c < componentsCount; c++) {
									var component = tile.components[c];
									if (r > component.codingStyleParameters.decompositionLevelsCount) {
										continue;
									}
									var resolution = component.resolutions[r];
									var numprecincts = resolution.precinctParameters.numprecincts;
									if (p >= numprecincts) {
										continue;
									}
									for (; l < layersCount;) {
										var packet = createPacket(resolution, p, l);
										l++;
										return packet;
									}
									l = 0;
								}
								c = 0;
							}
							p = 0;
						}
						error('JPX Error: Out of packets');
					};
				}
				function PositionComponentResolutionLayerIterator(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var layersCount = tile.codingStyleDefaultParameters.layersCount;
					var componentsCount = siz.Csiz;
					var precinctsSizes = getPrecinctSizesInImageScale(tile);
					var precinctsIterationSizes = precinctsSizes;
					var l = 0, r = 0, c = 0, px = 0, py = 0;
					this.nextPacket = function JpxImage_nextPacket() {
						for (; py < precinctsIterationSizes.maxNumHigh; py++) {
							for (; px < precinctsIterationSizes.maxNumWide; px++) {
								for (; c < componentsCount; c++) {
									var component = tile.components[c];
									var decompositionLevelsCount = component.codingStyleParameters.decompositionLevelsCount;
									for (; r <= decompositionLevelsCount; r++) {
										var resolution = component.resolutions[r];
										var sizeInImageScale = precinctsSizes.components[c].resolutions[r];
										var k = getPrecinctIndexIfExist(px, py, sizeInImageScale, precinctsIterationSizes, resolution);
										if (k === null) {
											continue;
										}
										for (; l < layersCount;) {
											var packet = createPacket(resolution, k, l);
											l++;
											return packet;
										}
										l = 0;
									}
									r = 0;
								}
								c = 0;
							}
							px = 0;
						}
						error('JPX Error: Out of packets');
					};
				}
				function ComponentPositionResolutionLayerIterator(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var layersCount = tile.codingStyleDefaultParameters.layersCount;
					var componentsCount = siz.Csiz;
					var precinctsSizes = getPrecinctSizesInImageScale(tile);
					var l = 0, r = 0, c = 0, px = 0, py = 0;
					this.nextPacket = function JpxImage_nextPacket() {
						for (; c < componentsCount; ++c) {
							var component = tile.components[c];
							var precinctsIterationSizes = precinctsSizes.components[c];
							var decompositionLevelsCount = component.codingStyleParameters.decompositionLevelsCount;
							for (; py < precinctsIterationSizes.maxNumHigh; py++) {
								for (; px < precinctsIterationSizes.maxNumWide; px++) {
									for (; r <= decompositionLevelsCount; r++) {
										var resolution = component.resolutions[r];
										var sizeInImageScale = precinctsIterationSizes.resolutions[r];
										var k = getPrecinctIndexIfExist(px, py, sizeInImageScale, precinctsIterationSizes, resolution);
										if (k === null) {
											continue;
										}
										for (; l < layersCount;) {
											var packet = createPacket(resolution, k, l);
											l++;
											return packet;
										}
										l = 0;
									}
									r = 0;
								}
								px = 0;
							}
							py = 0;
						}
						error('JPX Error: Out of packets');
					};
				}
				function getPrecinctIndexIfExist(pxIndex, pyIndex, sizeInImageScale, precinctIterationSizes, resolution) {
					var posX = pxIndex * precinctIterationSizes.minWidth;
					var posY = pyIndex * precinctIterationSizes.minHeight;
					if (posX % sizeInImageScale.width !== 0 || posY % sizeInImageScale.height !== 0) {
						return null;
					}
					var startPrecinctRowIndex = posY / sizeInImageScale.width * resolution.precinctParameters.numprecinctswide;
					return posX / sizeInImageScale.height + startPrecinctRowIndex;
				}
				function getPrecinctSizesInImageScale(tile) {
					var componentsCount = tile.components.length;
					var minWidth = Number.MAX_VALUE;
					var minHeight = Number.MAX_VALUE;
					var maxNumWide = 0;
					var maxNumHigh = 0;
					var sizePerComponent = new Array(componentsCount);
					for (var c = 0; c < componentsCount; c++) {
						var component = tile.components[c];
						var decompositionLevelsCount = component.codingStyleParameters.decompositionLevelsCount;
						var sizePerResolution = new Array(decompositionLevelsCount + 1);
						var minWidthCurrentComponent = Number.MAX_VALUE;
						var minHeightCurrentComponent = Number.MAX_VALUE;
						var maxNumWideCurrentComponent = 0;
						var maxNumHighCurrentComponent = 0;
						var scale = 1;
						for (var r = decompositionLevelsCount; r >= 0; --r) {
							var resolution = component.resolutions[r];
							var widthCurrentResolution = scale * resolution.precinctParameters.precinctWidth;
							var heightCurrentResolution = scale * resolution.precinctParameters.precinctHeight;
							minWidthCurrentComponent = Math.min(minWidthCurrentComponent, widthCurrentResolution);
							minHeightCurrentComponent = Math.min(minHeightCurrentComponent, heightCurrentResolution);
							maxNumWideCurrentComponent = Math.max(maxNumWideCurrentComponent, resolution.precinctParameters.numprecinctswide);
							maxNumHighCurrentComponent = Math.max(maxNumHighCurrentComponent, resolution.precinctParameters.numprecinctshigh);
							sizePerResolution[r] = {
								width: widthCurrentResolution,
								height: heightCurrentResolution
							};
							scale <<= 1;
						}
						minWidth = Math.min(minWidth, minWidthCurrentComponent);
						minHeight = Math.min(minHeight, minHeightCurrentComponent);
						maxNumWide = Math.max(maxNumWide, maxNumWideCurrentComponent);
						maxNumHigh = Math.max(maxNumHigh, maxNumHighCurrentComponent);
						sizePerComponent[c] = {
							resolutions: sizePerResolution,
							minWidth: minWidthCurrentComponent,
							minHeight: minHeightCurrentComponent,
							maxNumWide: maxNumWideCurrentComponent,
							maxNumHigh: maxNumHighCurrentComponent
						};
					}
					return {
						components: sizePerComponent,
						minWidth: minWidth,
						minHeight: minHeight,
						maxNumWide: maxNumWide,
						maxNumHigh: maxNumHigh
					};
				}
				function buildPackets(context) {
					var siz = context.SIZ;
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var componentsCount = siz.Csiz;
					for (var c = 0; c < componentsCount; c++) {
						var component = tile.components[c];
						var decompositionLevelsCount = component.codingStyleParameters.decompositionLevelsCount;
						var resolutions = [];
						var subbands = [];
						for (var r = 0; r <= decompositionLevelsCount; r++) {
							var blocksDimensions = getBlocksDimensions(context, component, r);
							var resolution = {};
							var scale = 1 << decompositionLevelsCount - r;
							resolution.trx0 = Math.ceil(component.tcx0 / scale);
							resolution.try0 = Math.ceil(component.tcy0 / scale);
							resolution.trx1 = Math.ceil(component.tcx1 / scale);
							resolution.try1 = Math.ceil(component.tcy1 / scale);
							resolution.resLevel = r;
							buildPrecincts(context, resolution, blocksDimensions);
							resolutions.push(resolution);
							var subband;
							if (r === 0) {
								subband = {};
								subband.type = 'LL';
								subband.tbx0 = Math.ceil(component.tcx0 / scale);
								subband.tby0 = Math.ceil(component.tcy0 / scale);
								subband.tbx1 = Math.ceil(component.tcx1 / scale);
								subband.tby1 = Math.ceil(component.tcy1 / scale);
								subband.resolution = resolution;
								buildCodeblocks(context, subband, blocksDimensions);
								subbands.push(subband);
								resolution.subbands = [subband];
							} else {
								var bscale = 1 << decompositionLevelsCount - r + 1;
								var resolutionSubbands = [];
								subband = {};
								subband.type = 'HL';
								subband.tbx0 = Math.ceil(component.tcx0 / bscale - 0.5);
								subband.tby0 = Math.ceil(component.tcy0 / bscale);
								subband.tbx1 = Math.ceil(component.tcx1 / bscale - 0.5);
								subband.tby1 = Math.ceil(component.tcy1 / bscale);
								subband.resolution = resolution;
								buildCodeblocks(context, subband, blocksDimensions);
								subbands.push(subband);
								resolutionSubbands.push(subband);
								subband = {};
								subband.type = 'LH';
								subband.tbx0 = Math.ceil(component.tcx0 / bscale);
								subband.tby0 = Math.ceil(component.tcy0 / bscale - 0.5);
								subband.tbx1 = Math.ceil(component.tcx1 / bscale);
								subband.tby1 = Math.ceil(component.tcy1 / bscale - 0.5);
								subband.resolution = resolution;
								buildCodeblocks(context, subband, blocksDimensions);
								subbands.push(subband);
								resolutionSubbands.push(subband);
								subband = {};
								subband.type = 'HH';
								subband.tbx0 = Math.ceil(component.tcx0 / bscale - 0.5);
								subband.tby0 = Math.ceil(component.tcy0 / bscale - 0.5);
								subband.tbx1 = Math.ceil(component.tcx1 / bscale - 0.5);
								subband.tby1 = Math.ceil(component.tcy1 / bscale - 0.5);
								subband.resolution = resolution;
								buildCodeblocks(context, subband, blocksDimensions);
								subbands.push(subband);
								resolutionSubbands.push(subband);
								resolution.subbands = resolutionSubbands;
							}
						}
						component.resolutions = resolutions;
						component.subbands = subbands;
					}
					var progressionOrder = tile.codingStyleDefaultParameters.progressionOrder;
					switch (progressionOrder) {
						case 0:
							tile.packetsIterator = new LayerResolutionComponentPositionIterator(context);
							break;
						case 1:
							tile.packetsIterator = new ResolutionLayerComponentPositionIterator(context);
							break;
						case 2:
							tile.packetsIterator = new ResolutionPositionComponentLayerIterator(context);
							break;
						case 3:
							tile.packetsIterator = new PositionComponentResolutionLayerIterator(context);
							break;
						case 4:
							tile.packetsIterator = new ComponentPositionResolutionLayerIterator(context);
							break;
						default:
							error('JPX Error: Unsupported progression order ' + progressionOrder);
					}
				}
				function parseTilePackets(context, data, offset, dataLength) {
					var position = 0;
					var buffer, bufferSize = 0, skipNextBit = false;
					function readBits(count) {
						while (bufferSize < count) {
							var b = data[offset + position];
							position++;
							if (skipNextBit) {
								buffer = buffer << 7 | b;
								bufferSize += 7;
								skipNextBit = false;
							} else {
								buffer = buffer << 8 | b;
								bufferSize += 8;
							}
							if (b === 0xFF) {
								skipNextBit = true;
							}
						}
						bufferSize -= count;
						return buffer >>> bufferSize & (1 << count) - 1;
					}
					function skipMarkerIfEqual(value) {
						if (data[offset + position - 1] === 0xFF && data[offset + position] === value) {
							skipBytes(1);
							return true;
						} else if (data[offset + position] === 0xFF && data[offset + position + 1] === value) {
							skipBytes(2);
							return true;
						}
						return false;
					}
					function skipBytes(count) {
						position += count;
					}
					function alignToByte() {
						bufferSize = 0;
						if (skipNextBit) {
							position++;
							skipNextBit = false;
						}
					}
					function readCodingpasses() {
						if (readBits(1) === 0) {
							return 1;
						}
						if (readBits(1) === 0) {
							return 2;
						}
						var value = readBits(2);
						if (value < 3) {
							return value + 3;
						}
						value = readBits(5);
						if (value < 31) {
							return value + 6;
						}
						value = readBits(7);
						return value + 37;
					}
					var tileIndex = context.currentTile.index;
					var tile = context.tiles[tileIndex];
					var sopMarkerUsed = context.COD.sopMarkerUsed;
					var ephMarkerUsed = context.COD.ephMarkerUsed;
					var packetsIterator = tile.packetsIterator;
					while (position < dataLength) {
						alignToByte();
						if (sopMarkerUsed && skipMarkerIfEqual(0x91)) {
							skipBytes(4);
						}
						var packet = packetsIterator.nextPacket();
						if (!readBits(1)) {
							continue;
						}
						var layerNumber = packet.layerNumber;
						var queue = [], codeblock;
						for (var i = 0, ii = packet.codeblocks.length; i < ii; i++) {
							codeblock = packet.codeblocks[i];
							var precinct = codeblock.precinct;
							var codeblockColumn = codeblock.cbx - precinct.cbxMin;
							var codeblockRow = codeblock.cby - precinct.cbyMin;
							var codeblockIncluded = false;
							var firstTimeInclusion = false;
							var valueReady;
							if (codeblock['included'] !== undefined) {
								codeblockIncluded = !!readBits(1);
							} else {
								precinct = codeblock.precinct;
								var inclusionTree, zeroBitPlanesTree;
								if (precinct['inclusionTree'] !== undefined) {
									inclusionTree = precinct.inclusionTree;
								} else {
									var width = precinct.cbxMax - precinct.cbxMin + 1;
									var height = precinct.cbyMax - precinct.cbyMin + 1;
									inclusionTree = new InclusionTree(width, height, layerNumber);
									zeroBitPlanesTree = new TagTree(width, height);
									precinct.inclusionTree = inclusionTree;
									precinct.zeroBitPlanesTree = zeroBitPlanesTree;
								}
								if (inclusionTree.reset(codeblockColumn, codeblockRow, layerNumber)) {
									while (true) {
										if (readBits(1)) {
											valueReady = !inclusionTree.nextLevel();
											if (valueReady) {
												codeblock.included = true;
												codeblockIncluded = firstTimeInclusion = true;
												break;
											}
										} else {
											inclusionTree.incrementValue(layerNumber);
											break;
										}
									}
								}
							}
							if (!codeblockIncluded) {
								continue;
							}
							if (firstTimeInclusion) {
								zeroBitPlanesTree = precinct.zeroBitPlanesTree;
								zeroBitPlanesTree.reset(codeblockColumn, codeblockRow);
								while (true) {
									if (readBits(1)) {
										valueReady = !zeroBitPlanesTree.nextLevel();
										if (valueReady) {
											break;
										}
									} else {
										zeroBitPlanesTree.incrementValue();
									}
								}
								codeblock.zeroBitPlanes = zeroBitPlanesTree.value;
							}
							var codingpasses = readCodingpasses();
							while (readBits(1)) {
								codeblock.Lblock++;
							}
							var codingpassesLog2 = log2(codingpasses);
							var bits = (codingpasses < 1 << codingpassesLog2 ? codingpassesLog2 - 1 : codingpassesLog2) + codeblock.Lblock;
							var codedDataLength = readBits(bits);
							queue.push({
								codeblock: codeblock,
								codingpasses: codingpasses,
								dataLength: codedDataLength
							});
						}
						alignToByte();
						if (ephMarkerUsed) {
							skipMarkerIfEqual(0x92);
						}
						while (queue.length > 0) {
							var packetItem = queue.shift();
							codeblock = packetItem.codeblock;
							if (codeblock['data'] === undefined) {
								codeblock.data = [];
							}
							codeblock.data.push({
								data: data,
								start: offset + position,
								end: offset + position + packetItem.dataLength,
								codingpasses: packetItem.codingpasses
							});
							position += packetItem.dataLength;
						}
					}
					return position;
				}
				function copyCoefficients(coefficients, levelWidth, levelHeight, subband, delta, mb, reversible, segmentationSymbolUsed) {
					var x0 = subband.tbx0;
					var y0 = subband.tby0;
					var width = subband.tbx1 - subband.tbx0;
					var codeblocks = subband.codeblocks;
					var right = subband.type.charAt(0) === 'H' ? 1 : 0;
					var bottom = subband.type.charAt(1) === 'H' ? levelWidth : 0;
					for (var i = 0, ii = codeblocks.length; i < ii; ++i) {
						var codeblock = codeblocks[i];
						var blockWidth = codeblock.tbx1_ - codeblock.tbx0_;
						var blockHeight = codeblock.tby1_ - codeblock.tby0_;
						if (blockWidth === 0 || blockHeight === 0) {
							continue;
						}
						if (codeblock['data'] === undefined) {
							continue;
						}
						var bitModel, currentCodingpassType;
						bitModel = new BitModel(blockWidth, blockHeight, codeblock.subbandType, codeblock.zeroBitPlanes, mb);
						currentCodingpassType = 2;
						var data = codeblock.data, totalLength = 0, codingpasses = 0;
						var j, jj, dataItem;
						for (j = 0, jj = data.length; j < jj; j++) {
							dataItem = data[j];
							totalLength += dataItem.end - dataItem.start;
							codingpasses += dataItem.codingpasses;
						}
						var encodedData = new Uint8Array(totalLength);
						var position = 0;
						for (j = 0, jj = data.length; j < jj; j++) {
							dataItem = data[j];
							var chunk = dataItem.data.subarray(dataItem.start, dataItem.end);
							encodedData.set(chunk, position);
							position += chunk.length;
						}
						var decoder = new ArithmeticDecoder(encodedData, 0, totalLength);
						bitModel.setDecoder(decoder);
						for (j = 0; j < codingpasses; j++) {
							switch (currentCodingpassType) {
								case 0:
									bitModel.runSignificancePropagationPass();
									break;
								case 1:
									bitModel.runMagnitudeRefinementPass();
									break;
								case 2:
									bitModel.runCleanupPass();
									if (segmentationSymbolUsed) {
										bitModel.checkSegmentationSymbol();
									}
									break;
							}
							currentCodingpassType = (currentCodingpassType + 1) % 3;
						}
						var offset = codeblock.tbx0_ - x0 + (codeblock.tby0_ - y0) * width;
						var sign = bitModel.coefficentsSign;
						var magnitude = bitModel.coefficentsMagnitude;
						var bitsDecoded = bitModel.bitsDecoded;
						var magnitudeCorrection = reversible ? 0 : 0.5;
						var k, n, nb;
						position = 0;
						var interleave = subband.type !== 'LL';
						for (j = 0; j < blockHeight; j++) {
							var row = offset / width | 0;
							var levelOffset = 2 * row * (levelWidth - width) + right + bottom;
							for (k = 0; k < blockWidth; k++) {
								n = magnitude[position];
								if (n !== 0) {
									n = (n + magnitudeCorrection) * delta;
									if (sign[position] !== 0) {
										n = -n;
									}
									nb = bitsDecoded[position];
									var pos = interleave ? levelOffset + (offset << 1) : offset;
									if (reversible && nb >= mb) {
										coefficients[pos] = n;
									} else {
										coefficients[pos] = n * (1 << mb - nb);
									}
								}
								offset++;
								position++;
							}
							offset += width - blockWidth;
						}
					}
				}
				function transformTile(context, tile, c) {
					var component = tile.components[c];
					var codingStyleParameters = component.codingStyleParameters;
					var quantizationParameters = component.quantizationParameters;
					var decompositionLevelsCount = codingStyleParameters.decompositionLevelsCount;
					var spqcds = quantizationParameters.SPqcds;
					var scalarExpounded = quantizationParameters.scalarExpounded;
					var guardBits = quantizationParameters.guardBits;
					var segmentationSymbolUsed = codingStyleParameters.segmentationSymbolUsed;
					var precision = context.components[c].precision;
					var reversible = codingStyleParameters.reversibleTransformation;
					var transform = reversible ? new ReversibleTransform() : new IrreversibleTransform();
					var subbandCoefficients = [];
					var b = 0;
					for (var i = 0; i <= decompositionLevelsCount; i++) {
						var resolution = component.resolutions[i];
						var width = resolution.trx1 - resolution.trx0;
						var height = resolution.try1 - resolution.try0;
						var coefficients = new Float32Array(width * height);
						for (var j = 0, jj = resolution.subbands.length; j < jj; j++) {
							var mu, epsilon;
							if (!scalarExpounded) {
								mu = spqcds[0].mu;
								epsilon = spqcds[0].epsilon + (i > 0 ? 1 - i : 0);
							} else {
								mu = spqcds[b].mu;
								epsilon = spqcds[b].epsilon;
								b++;
							}
							var subband = resolution.subbands[j];
							var gainLog2 = SubbandsGainLog2[subband.type];
							var delta = reversible ? 1 : Math.pow(2, precision + gainLog2 - epsilon) * (1 + mu / 2048);
							var mb = guardBits + epsilon - 1;
							copyCoefficients(coefficients, width, height, subband, delta, mb, reversible, segmentationSymbolUsed);
						}
						subbandCoefficients.push({
							width: width,
							height: height,
							items: coefficients
						});
					}
					var result = transform.calculate(subbandCoefficients, component.tcx0, component.tcy0);
					return {
						left: component.tcx0,
						top: component.tcy0,
						width: result.width,
						height: result.height,
						items: result.items
					};
				}
				function transformComponents(context) {
					var siz = context.SIZ;
					var components = context.components;
					var componentsCount = siz.Csiz;
					var resultImages = [];
					for (var i = 0, ii = context.tiles.length; i < ii; i++) {
						var tile = context.tiles[i];
						var transformedTiles = [];
						var c;
						for (c = 0; c < componentsCount; c++) {
							transformedTiles[c] = transformTile(context, tile, c);
						}
						var tile0 = transformedTiles[0];
						var out = new Uint8Array(tile0.items.length * componentsCount);
						var result = {
							left: tile0.left,
							top: tile0.top,
							width: tile0.width,
							height: tile0.height,
							items: out
						};
						var shift, offset, max, min, maxK;
						var pos = 0, j, jj, y0, y1, y2, r, g, b, k, val;
						if (tile.codingStyleDefaultParameters.multipleComponentTransform) {
							var fourComponents = componentsCount === 4;
							var y0items = transformedTiles[0].items;
							var y1items = transformedTiles[1].items;
							var y2items = transformedTiles[2].items;
							var y3items = fourComponents ? transformedTiles[3].items : null;
							shift = components[0].precision - 8;
							offset = (128 << shift) + 0.5;
							max = 255 * (1 << shift);
							maxK = max * 0.5;
							min = -maxK;
							var component0 = tile.components[0];
							var alpha01 = componentsCount - 3;
							jj = y0items.length;
							if (!component0.codingStyleParameters.reversibleTransformation) {
								for (j = 0; j < jj; j++, pos += alpha01) {
									y0 = y0items[j] + offset;
									y1 = y1items[j];
									y2 = y2items[j];
									r = y0 + 1.402 * y2;
									g = y0 - 0.34413 * y1 - 0.71414 * y2;
									b = y0 + 1.772 * y1;
									out[pos++] = r <= 0 ? 0 : r >= max ? 255 : r >> shift;
									out[pos++] = g <= 0 ? 0 : g >= max ? 255 : g >> shift;
									out[pos++] = b <= 0 ? 0 : b >= max ? 255 : b >> shift;
								}
							} else {
								for (j = 0; j < jj; j++, pos += alpha01) {
									y0 = y0items[j] + offset;
									y1 = y1items[j];
									y2 = y2items[j];
									g = y0 - (y2 + y1 >> 2);
									r = g + y2;
									b = g + y1;
									out[pos++] = r <= 0 ? 0 : r >= max ? 255 : r >> shift;
									out[pos++] = g <= 0 ? 0 : g >= max ? 255 : g >> shift;
									out[pos++] = b <= 0 ? 0 : b >= max ? 255 : b >> shift;
								}
							}
							if (fourComponents) {
								for (j = 0, pos = 3; j < jj; j++, pos += 4) {
									k = y3items[j];
									out[pos] = k <= min ? 0 : k >= maxK ? 255 : k + offset >> shift;
								}
							}
						} else {
							for (c = 0; c < componentsCount; c++) {
								var items = transformedTiles[c].items;
								shift = components[c].precision - 8;
								offset = (128 << shift) + 0.5;
								max = 127.5 * (1 << shift);
								min = -max;
								for (pos = c, j = 0, jj = items.length; j < jj; j++) {
									val = items[j];
									out[pos] = val <= min ? 0 : val >= max ? 255 : val + offset >> shift;
									pos += componentsCount;
								}
							}
						}
						resultImages.push(result);
					}
					return resultImages;
				}
				function initializeTile(context, tileIndex) {
					var siz = context.SIZ;
					var componentsCount = siz.Csiz;
					var tile = context.tiles[tileIndex];
					for (var c = 0; c < componentsCount; c++) {
						var component = tile.components[c];
						var qcdOrQcc = context.currentTile.QCC[c] !== undefined ? context.currentTile.QCC[c] : context.currentTile.QCD;
						component.quantizationParameters = qcdOrQcc;
						var codOrCoc = context.currentTile.COC[c] !== undefined ? context.currentTile.COC[c] : context.currentTile.COD;
						component.codingStyleParameters = codOrCoc;
					}
					tile.codingStyleDefaultParameters = context.currentTile.COD;
				}
				var TagTree = function TagTreeClosure() {
					function TagTree(width, height) {
						var levelsLength = log2(Math.max(width, height)) + 1;
						this.levels = [];
						for (var i = 0; i < levelsLength; i++) {
							var level = {
								width: width,
								height: height,
								items: []
							};
							this.levels.push(level);
							width = Math.ceil(width / 2);
							height = Math.ceil(height / 2);
						}
					}
					TagTree.prototype = {
						reset: function TagTree_reset(i, j) {
							var currentLevel = 0, value = 0, level;
							while (currentLevel < this.levels.length) {
								level = this.levels[currentLevel];
								var index = i + j * level.width;
								if (level.items[index] !== undefined) {
									value = level.items[index];
									break;
								}
								level.index = index;
								i >>= 1;
								j >>= 1;
								currentLevel++;
							}
							currentLevel--;
							level = this.levels[currentLevel];
							level.items[level.index] = value;
							this.currentLevel = currentLevel;
							delete this.value;
						},
						incrementValue: function TagTree_incrementValue() {
							var level = this.levels[this.currentLevel];
							level.items[level.index]++;
						},
						nextLevel: function TagTree_nextLevel() {
							var currentLevel = this.currentLevel;
							var level = this.levels[currentLevel];
							var value = level.items[level.index];
							currentLevel--;
							if (currentLevel < 0) {
								this.value = value;
								return false;
							}
							this.currentLevel = currentLevel;
							level = this.levels[currentLevel];
							level.items[level.index] = value;
							return true;
						}
					};
					return TagTree;
				}();
				var InclusionTree = function InclusionTreeClosure() {
					function InclusionTree(width, height, defaultValue) {
						var levelsLength = log2(Math.max(width, height)) + 1;
						this.levels = [];
						for (var i = 0; i < levelsLength; i++) {
							var items = new Uint8Array(width * height);
							for (var j = 0, jj = items.length; j < jj; j++) {
								items[j] = defaultValue;
							}
							var level = {
								width: width,
								height: height,
								items: items
							};
							this.levels.push(level);
							width = Math.ceil(width / 2);
							height = Math.ceil(height / 2);
						}
					}
					InclusionTree.prototype = {
						reset: function InclusionTree_reset(i, j, stopValue) {
							var currentLevel = 0;
							while (currentLevel < this.levels.length) {
								var level = this.levels[currentLevel];
								var index = i + j * level.width;
								level.index = index;
								var value = level.items[index];
								if (value === 0xFF) {
									break;
								}
								if (value > stopValue) {
									this.currentLevel = currentLevel;
									this.propagateValues();
									return false;
								}
								i >>= 1;
								j >>= 1;
								currentLevel++;
							}
							this.currentLevel = currentLevel - 1;
							return true;
						},
						incrementValue: function InclusionTree_incrementValue(stopValue) {
							var level = this.levels[this.currentLevel];
							level.items[level.index] = stopValue + 1;
							this.propagateValues();
						},
						propagateValues: function InclusionTree_propagateValues() {
							var levelIndex = this.currentLevel;
							var level = this.levels[levelIndex];
							var currentValue = level.items[level.index];
							while (--levelIndex >= 0) {
								level = this.levels[levelIndex];
								level.items[level.index] = currentValue;
							}
						},
						nextLevel: function InclusionTree_nextLevel() {
							var currentLevel = this.currentLevel;
							var level = this.levels[currentLevel];
							var value = level.items[level.index];
							level.items[level.index] = 0xFF;
							currentLevel--;
							if (currentLevel < 0) {
								return false;
							}
							this.currentLevel = currentLevel;
							level = this.levels[currentLevel];
							level.items[level.index] = value;
							return true;
						}
					};
					return InclusionTree;
				}();
				var BitModel = function BitModelClosure() {
					var UNIFORM_CONTEXT = 17;
					var RUNLENGTH_CONTEXT = 18;
					var LLAndLHContextsLabel = new Uint8Array([
						0,
						5,
						8,
						0,
						3,
						7,
						8,
						0,
						4,
						7,
						8,
						0,
						0,
						0,
						0,
						0,
						1,
						6,
						8,
						0,
						3,
						7,
						8,
						0,
						4,
						7,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						6,
						8,
						0,
						3,
						7,
						8,
						0,
						4,
						7,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						6,
						8,
						0,
						3,
						7,
						8,
						0,
						4,
						7,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						6,
						8,
						0,
						3,
						7,
						8,
						0,
						4,
						7,
						8
					]);
					var HLContextLabel = new Uint8Array([
						0,
						3,
						4,
						0,
						5,
						7,
						7,
						0,
						8,
						8,
						8,
						0,
						0,
						0,
						0,
						0,
						1,
						3,
						4,
						0,
						6,
						7,
						7,
						0,
						8,
						8,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						3,
						4,
						0,
						6,
						7,
						7,
						0,
						8,
						8,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						3,
						4,
						0,
						6,
						7,
						7,
						0,
						8,
						8,
						8,
						0,
						0,
						0,
						0,
						0,
						2,
						3,
						4,
						0,
						6,
						7,
						7,
						0,
						8,
						8,
						8
					]);
					var HHContextLabel = new Uint8Array([
						0,
						1,
						2,
						0,
						1,
						2,
						2,
						0,
						2,
						2,
						2,
						0,
						0,
						0,
						0,
						0,
						3,
						4,
						5,
						0,
						4,
						5,
						5,
						0,
						5,
						5,
						5,
						0,
						0,
						0,
						0,
						0,
						6,
						7,
						7,
						0,
						7,
						7,
						7,
						0,
						7,
						7,
						7,
						0,
						0,
						0,
						0,
						0,
						8,
						8,
						8,
						0,
						8,
						8,
						8,
						0,
						8,
						8,
						8,
						0,
						0,
						0,
						0,
						0,
						8,
						8,
						8,
						0,
						8,
						8,
						8,
						0,
						8,
						8,
						8
					]);
					function BitModel(width, height, subband, zeroBitPlanes, mb) {
						this.width = width;
						this.height = height;
						this.contextLabelTable = subband === 'HH' ? HHContextLabel : subband === 'HL' ? HLContextLabel : LLAndLHContextsLabel;
						var coefficientCount = width * height;
						this.neighborsSignificance = new Uint8Array(coefficientCount);
						this.coefficentsSign = new Uint8Array(coefficientCount);
						this.coefficentsMagnitude = mb > 14 ? new Uint32Array(coefficientCount) : mb > 6 ? new Uint16Array(coefficientCount) : new Uint8Array(coefficientCount);
						this.processingFlags = new Uint8Array(coefficientCount);
						var bitsDecoded = new Uint8Array(coefficientCount);
						if (zeroBitPlanes !== 0) {
							for (var i = 0; i < coefficientCount; i++) {
								bitsDecoded[i] = zeroBitPlanes;
							}
						}
						this.bitsDecoded = bitsDecoded;
						this.reset();
					}
					BitModel.prototype = {
						setDecoder: function BitModel_setDecoder(decoder) {
							this.decoder = decoder;
						},
						reset: function BitModel_reset() {
							this.contexts = new Int8Array(19);
							this.contexts[0] = 4 << 1 | 0;
							this.contexts[UNIFORM_CONTEXT] = 46 << 1 | 0;
							this.contexts[RUNLENGTH_CONTEXT] = 3 << 1 | 0;
						},
						setNeighborsSignificance: function BitModel_setNeighborsSignificance(row, column, index) {
							var neighborsSignificance = this.neighborsSignificance;
							var width = this.width, height = this.height;
							var left = column > 0;
							var right = column + 1 < width;
							var i;
							if (row > 0) {
								i = index - width;
								if (left) {
									neighborsSignificance[i - 1] += 0x10;
								}
								if (right) {
									neighborsSignificance[i + 1] += 0x10;
								}
								neighborsSignificance[i] += 0x04;
							}
							if (row + 1 < height) {
								i = index + width;
								if (left) {
									neighborsSignificance[i - 1] += 0x10;
								}
								if (right) {
									neighborsSignificance[i + 1] += 0x10;
								}
								neighborsSignificance[i] += 0x04;
							}
							if (left) {
								neighborsSignificance[index - 1] += 0x01;
							}
							if (right) {
								neighborsSignificance[index + 1] += 0x01;
							}
							neighborsSignificance[index] |= 0x80;
						},
						runSignificancePropagationPass: function BitModel_runSignificancePropagationPass() {
							var decoder = this.decoder;
							var width = this.width, height = this.height;
							var coefficentsMagnitude = this.coefficentsMagnitude;
							var coefficentsSign = this.coefficentsSign;
							var neighborsSignificance = this.neighborsSignificance;
							var processingFlags = this.processingFlags;
							var contexts = this.contexts;
							var labels = this.contextLabelTable;
							var bitsDecoded = this.bitsDecoded;
							var processedInverseMask = ~1;
							var processedMask = 1;
							var firstMagnitudeBitMask = 2;
							for (var i0 = 0; i0 < height; i0 += 4) {
								for (var j = 0; j < width; j++) {
									var index = i0 * width + j;
									for (var i1 = 0; i1 < 4; i1++, index += width) {
										var i = i0 + i1;
										if (i >= height) {
											break;
										}
										processingFlags[index] &= processedInverseMask;
										if (coefficentsMagnitude[index] || !neighborsSignificance[index]) {
											continue;
										}
										var contextLabel = labels[neighborsSignificance[index]];
										var decision = decoder.readBit(contexts, contextLabel);
										if (decision) {
											var sign = this.decodeSignBit(i, j, index);
											coefficentsSign[index] = sign;
											coefficentsMagnitude[index] = 1;
											this.setNeighborsSignificance(i, j, index);
											processingFlags[index] |= firstMagnitudeBitMask;
										}
										bitsDecoded[index]++;
										processingFlags[index] |= processedMask;
									}
								}
							}
						},
						decodeSignBit: function BitModel_decodeSignBit(row, column, index) {
							var width = this.width, height = this.height;
							var coefficentsMagnitude = this.coefficentsMagnitude;
							var coefficentsSign = this.coefficentsSign;
							var contribution, sign0, sign1, significance1;
							var contextLabel, decoded;
							significance1 = column > 0 && coefficentsMagnitude[index - 1] !== 0;
							if (column + 1 < width && coefficentsMagnitude[index + 1] !== 0) {
								sign1 = coefficentsSign[index + 1];
								if (significance1) {
									sign0 = coefficentsSign[index - 1];
									contribution = 1 - sign1 - sign0;
								} else {
									contribution = 1 - sign1 - sign1;
								}
							} else if (significance1) {
								sign0 = coefficentsSign[index - 1];
								contribution = 1 - sign0 - sign0;
							} else {
								contribution = 0;
							}
							var horizontalContribution = 3 * contribution;
							significance1 = row > 0 && coefficentsMagnitude[index - width] !== 0;
							if (row + 1 < height && coefficentsMagnitude[index + width] !== 0) {
								sign1 = coefficentsSign[index + width];
								if (significance1) {
									sign0 = coefficentsSign[index - width];
									contribution = 1 - sign1 - sign0 + horizontalContribution;
								} else {
									contribution = 1 - sign1 - sign1 + horizontalContribution;
								}
							} else if (significance1) {
								sign0 = coefficentsSign[index - width];
								contribution = 1 - sign0 - sign0 + horizontalContribution;
							} else {
								contribution = horizontalContribution;
							}
							if (contribution >= 0) {
								contextLabel = 9 + contribution;
								decoded = this.decoder.readBit(this.contexts, contextLabel);
							} else {
								contextLabel = 9 - contribution;
								decoded = this.decoder.readBit(this.contexts, contextLabel) ^ 1;
							}
							return decoded;
						},
						runMagnitudeRefinementPass: function BitModel_runMagnitudeRefinementPass() {
							var decoder = this.decoder;
							var width = this.width, height = this.height;
							var coefficentsMagnitude = this.coefficentsMagnitude;
							var neighborsSignificance = this.neighborsSignificance;
							var contexts = this.contexts;
							var bitsDecoded = this.bitsDecoded;
							var processingFlags = this.processingFlags;
							var processedMask = 1;
							var firstMagnitudeBitMask = 2;
							var length = width * height;
							var width4 = width * 4;
							for (var index0 = 0, indexNext; index0 < length; index0 = indexNext) {
								indexNext = Math.min(length, index0 + width4);
								for (var j = 0; j < width; j++) {
									for (var index = index0 + j; index < indexNext; index += width) {
										if (!coefficentsMagnitude[index] || (processingFlags[index] & processedMask) !== 0) {
											continue;
										}
										var contextLabel = 16;
										if ((processingFlags[index] & firstMagnitudeBitMask) !== 0) {
											processingFlags[index] ^= firstMagnitudeBitMask;
											var significance = neighborsSignificance[index] & 127;
											contextLabel = significance === 0 ? 15 : 14;
										}
										var bit = decoder.readBit(contexts, contextLabel);
										coefficentsMagnitude[index] = coefficentsMagnitude[index] << 1 | bit;
										bitsDecoded[index]++;
										processingFlags[index] |= processedMask;
									}
								}
							}
						},
						runCleanupPass: function BitModel_runCleanupPass() {
							var decoder = this.decoder;
							var width = this.width, height = this.height;
							var neighborsSignificance = this.neighborsSignificance;
							var coefficentsMagnitude = this.coefficentsMagnitude;
							var coefficentsSign = this.coefficentsSign;
							var contexts = this.contexts;
							var labels = this.contextLabelTable;
							var bitsDecoded = this.bitsDecoded;
							var processingFlags = this.processingFlags;
							var processedMask = 1;
							var firstMagnitudeBitMask = 2;
							var oneRowDown = width;
							var twoRowsDown = width * 2;
							var threeRowsDown = width * 3;
							var iNext;
							for (var i0 = 0; i0 < height; i0 = iNext) {
								iNext = Math.min(i0 + 4, height);
								var indexBase = i0 * width;
								var checkAllEmpty = i0 + 3 < height;
								for (var j = 0; j < width; j++) {
									var index0 = indexBase + j;
									var allEmpty = checkAllEmpty && processingFlags[index0] === 0 && processingFlags[index0 + oneRowDown] === 0 && processingFlags[index0 + twoRowsDown] === 0 && processingFlags[index0 + threeRowsDown] === 0 && neighborsSignificance[index0] === 0 && neighborsSignificance[index0 + oneRowDown] === 0 && neighborsSignificance[index0 + twoRowsDown] === 0 && neighborsSignificance[index0 + threeRowsDown] === 0;
									var i1 = 0, index = index0;
									var i = i0, sign;
									if (allEmpty) {
										var hasSignificantCoefficent = decoder.readBit(contexts, RUNLENGTH_CONTEXT);
										if (!hasSignificantCoefficent) {
											bitsDecoded[index0]++;
											bitsDecoded[index0 + oneRowDown]++;
											bitsDecoded[index0 + twoRowsDown]++;
											bitsDecoded[index0 + threeRowsDown]++;
											continue;
										}
										i1 = decoder.readBit(contexts, UNIFORM_CONTEXT) << 1 | decoder.readBit(contexts, UNIFORM_CONTEXT);
										if (i1 !== 0) {
											i = i0 + i1;
											index += i1 * width;
										}
										sign = this.decodeSignBit(i, j, index);
										coefficentsSign[index] = sign;
										coefficentsMagnitude[index] = 1;
										this.setNeighborsSignificance(i, j, index);
										processingFlags[index] |= firstMagnitudeBitMask;
										index = index0;
										for (var i2 = i0; i2 <= i; i2++, index += width) {
											bitsDecoded[index]++;
										}
										i1++;
									}
									for (i = i0 + i1; i < iNext; i++, index += width) {
										if (coefficentsMagnitude[index] || (processingFlags[index] & processedMask) !== 0) {
											continue;
										}
										var contextLabel = labels[neighborsSignificance[index]];
										var decision = decoder.readBit(contexts, contextLabel);
										if (decision === 1) {
											sign = this.decodeSignBit(i, j, index);
											coefficentsSign[index] = sign;
											coefficentsMagnitude[index] = 1;
											this.setNeighborsSignificance(i, j, index);
											processingFlags[index] |= firstMagnitudeBitMask;
										}
										bitsDecoded[index]++;
									}
								}
							}
						},
						checkSegmentationSymbol: function BitModel_checkSegmentationSymbol() {
							var decoder = this.decoder;
							var contexts = this.contexts;
							var symbol = decoder.readBit(contexts, UNIFORM_CONTEXT) << 3 | decoder.readBit(contexts, UNIFORM_CONTEXT) << 2 | decoder.readBit(contexts, UNIFORM_CONTEXT) << 1 | decoder.readBit(contexts, UNIFORM_CONTEXT);
							if (symbol !== 0xA) {
								error('JPX Error: Invalid segmentation symbol');
							}
						}
					};
					return BitModel;
				}();
				var Transform = function TransformClosure() {
					function Transform() {
					}
					Transform.prototype.calculate = function transformCalculate(subbands, u0, v0) {
						var ll = subbands[0];
						for (var i = 1, ii = subbands.length; i < ii; i++) {
							ll = this.iterate(ll, subbands[i], u0, v0);
						}
						return ll;
					};
					Transform.prototype.extend = function extend(buffer, offset, size) {
						var i1 = offset - 1, j1 = offset + 1;
						var i2 = offset + size - 2, j2 = offset + size;
						buffer[i1--] = buffer[j1++];
						buffer[j2++] = buffer[i2--];
						buffer[i1--] = buffer[j1++];
						buffer[j2++] = buffer[i2--];
						buffer[i1--] = buffer[j1++];
						buffer[j2++] = buffer[i2--];
						buffer[i1] = buffer[j1];
						buffer[j2] = buffer[i2];
					};
					Transform.prototype.iterate = function Transform_iterate(ll, hl_lh_hh, u0, v0) {
						var llWidth = ll.width, llHeight = ll.height, llItems = ll.items;
						var width = hl_lh_hh.width;
						var height = hl_lh_hh.height;
						var items = hl_lh_hh.items;
						var i, j, k, l, u, v;
						for (k = 0, i = 0; i < llHeight; i++) {
							l = i * 2 * width;
							for (j = 0; j < llWidth; j++, k++, l += 2) {
								items[l] = llItems[k];
							}
						}
						llItems = ll.items = null;
						var bufferPadding = 4;
						var rowBuffer = new Float32Array(width + 2 * bufferPadding);
						if (width === 1) {
							if ((u0 & 1) !== 0) {
								for (v = 0, k = 0; v < height; v++, k += width) {
									items[k] *= 0.5;
								}
							}
						} else {
							for (v = 0, k = 0; v < height; v++, k += width) {
								rowBuffer.set(items.subarray(k, k + width), bufferPadding);
								this.extend(rowBuffer, bufferPadding, width);
								this.filter(rowBuffer, bufferPadding, width);
								items.set(rowBuffer.subarray(bufferPadding, bufferPadding + width), k);
							}
						}
						var numBuffers = 16;
						var colBuffers = [];
						for (i = 0; i < numBuffers; i++) {
							colBuffers.push(new Float32Array(height + 2 * bufferPadding));
						}
						var b, currentBuffer = 0;
						ll = bufferPadding + height;
						if (height === 1) {
							if ((v0 & 1) !== 0) {
								for (u = 0; u < width; u++) {
									items[u] *= 0.5;
								}
							}
						} else {
							for (u = 0; u < width; u++) {
								if (currentBuffer === 0) {
									numBuffers = Math.min(width - u, numBuffers);
									for (k = u, l = bufferPadding; l < ll; k += width, l++) {
										for (b = 0; b < numBuffers; b++) {
											colBuffers[b][l] = items[k + b];
										}
									}
									currentBuffer = numBuffers;
								}
								currentBuffer--;
								var buffer = colBuffers[currentBuffer];
								this.extend(buffer, bufferPadding, height);
								this.filter(buffer, bufferPadding, height);
								if (currentBuffer === 0) {
									k = u - numBuffers + 1;
									for (l = bufferPadding; l < ll; k += width, l++) {
										for (b = 0; b < numBuffers; b++) {
											items[k + b] = colBuffers[b][l];
										}
									}
								}
							}
						}
						return {
							width: width,
							height: height,
							items: items
						};
					};
					return Transform;
				}();
				var IrreversibleTransform = function IrreversibleTransformClosure() {
					function IrreversibleTransform() {
						Transform.call(this);
					}
					IrreversibleTransform.prototype = Object.create(Transform.prototype);
					IrreversibleTransform.prototype.filter = function irreversibleTransformFilter(x, offset, length) {
						var len = length >> 1;
						offset = offset | 0;
						var j, n, current, next;
						var alpha = -1.586134342059924;
						var beta = -0.052980118572961;
						var gamma = 0.882911075530934;
						var delta = 0.443506852043971;
						var K = 1.230174104914001;
						var K_ = 1 / K;
						j = offset - 3;
						for (n = len + 4; n--; j += 2) {
							x[j] *= K_;
						}
						j = offset - 2;
						current = delta * x[j - 1];
						for (n = len + 3; n--; j += 2) {
							next = delta * x[j + 1];
							x[j] = K * x[j] - current - next;
							if (n--) {
								j += 2;
								current = delta * x[j + 1];
								x[j] = K * x[j] - current - next;
							} else {
								break;
							}
						}
						j = offset - 1;
						current = gamma * x[j - 1];
						for (n = len + 2; n--; j += 2) {
							next = gamma * x[j + 1];
							x[j] -= current + next;
							if (n--) {
								j += 2;
								current = gamma * x[j + 1];
								x[j] -= current + next;
							} else {
								break;
							}
						}
						j = offset;
						current = beta * x[j - 1];
						for (n = len + 1; n--; j += 2) {
							next = beta * x[j + 1];
							x[j] -= current + next;
							if (n--) {
								j += 2;
								current = beta * x[j + 1];
								x[j] -= current + next;
							} else {
								break;
							}
						}
						if (len !== 0) {
							j = offset + 1;
							current = alpha * x[j - 1];
							for (n = len; n--; j += 2) {
								next = alpha * x[j + 1];
								x[j] -= current + next;
								if (n--) {
									j += 2;
									current = alpha * x[j + 1];
									x[j] -= current + next;
								} else {
									break;
								}
							}
						}
					};
					return IrreversibleTransform;
				}();
				var ReversibleTransform = function ReversibleTransformClosure() {
					function ReversibleTransform() {
						Transform.call(this);
					}
					ReversibleTransform.prototype = Object.create(Transform.prototype);
					ReversibleTransform.prototype.filter = function reversibleTransformFilter(x, offset, length) {
						var len = length >> 1;
						offset = offset | 0;
						var j, n;
						for (j = offset, n = len + 1; n--; j += 2) {
							x[j] -= x[j - 1] + x[j + 1] + 2 >> 2;
						}
						for (j = offset + 1, n = len; n--; j += 2) {
							x[j] += x[j - 1] + x[j + 1] >> 1;
						}
					};
					return ReversibleTransform;
				}();
				return JpxImage;
			}();
			exports.JpxImage = JpxImage;

			/***/
		}),
/* 16 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreCrypto = __w_pdfjs_require__(13);
			var coreParser = __w_pdfjs_require__(5);
			var coreChunkedStream = __w_pdfjs_require__(12);
			var coreColorSpace = __w_pdfjs_require__(3);
			var InvalidPDFException = sharedUtil.InvalidPDFException;
			var MissingDataException = sharedUtil.MissingDataException;
			var XRefParseException = sharedUtil.XRefParseException;
			var assert = sharedUtil.assert;
			var bytesToString = sharedUtil.bytesToString;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isBool = sharedUtil.isBool;
			var isInt = sharedUtil.isInt;
			var isString = sharedUtil.isString;
			var shadow = sharedUtil.shadow;
			var stringToPDFString = sharedUtil.stringToPDFString;
			var stringToUTF8String = sharedUtil.stringToUTF8String;
			var warn = sharedUtil.warn;
			var createValidAbsoluteUrl = sharedUtil.createValidAbsoluteUrl;
			var Util = sharedUtil.Util;
			var Ref = corePrimitives.Ref;
			var RefSet = corePrimitives.RefSet;
			var RefSetCache = corePrimitives.RefSetCache;
			var isName = corePrimitives.isName;
			var isCmd = corePrimitives.isCmd;
			var isDict = corePrimitives.isDict;
			var isRef = corePrimitives.isRef;
			var isRefsEqual = corePrimitives.isRefsEqual;
			var isStream = corePrimitives.isStream;
			var CipherTransformFactory = coreCrypto.CipherTransformFactory;
			var Lexer = coreParser.Lexer;
			var Parser = coreParser.Parser;
			var ChunkedStream = coreChunkedStream.ChunkedStream;
			var ColorSpace = coreColorSpace.ColorSpace;
			var Catalog = function CatalogClosure() {
				function Catalog(pdfManager, xref, pageFactory) {
					this.pdfManager = pdfManager;
					this.xref = xref;
					this.catDict = xref.getCatalogObj();
					this.fontCache = new RefSetCache();
					this.builtInCMapCache = Object.create(null);
					assert(isDict(this.catDict), 'catalog object is not a dictionary');
					this.pageFactory = pageFactory;
					this.pagePromises = [];
				}
				Catalog.prototype = {
					get metadata() {
						var streamRef = this.catDict.getRaw('Metadata');
						if (!isRef(streamRef)) {
							return shadow(this, 'metadata', null);
						}
						var encryptMetadata = !this.xref.encrypt ? false : this.xref.encrypt.encryptMetadata;
						var stream = this.xref.fetch(streamRef, !encryptMetadata);
						var metadata;
						if (stream && isDict(stream.dict)) {
							var type = stream.dict.get('Type');
							var subtype = stream.dict.get('Subtype');
							if (isName(type, 'Metadata') && isName(subtype, 'XML')) {
								try {
									metadata = stringToUTF8String(bytesToString(stream.getBytes()));
								} catch (e) {
									info('Skipping invalid metadata.');
								}
							}
						}
						return shadow(this, 'metadata', metadata);
					},
					get toplevelPagesDict() {
						var pagesObj = this.catDict.get('Pages');
						assert(isDict(pagesObj), 'invalid top-level pages dictionary');
						return shadow(this, 'toplevelPagesDict', pagesObj);
					},
					get documentOutline() {
						var obj = null;
						try {
							obj = this.readDocumentOutline();
						} catch (ex) {
							if (ex instanceof MissingDataException) {
								throw ex;
							}
							warn('Unable to read document outline');
						}
						return shadow(this, 'documentOutline', obj);
					},
					readDocumentOutline: function Catalog_readDocumentOutline() {
						var obj = this.catDict.get('Outlines');
						if (!isDict(obj)) {
							return null;
						}
						obj = obj.getRaw('First');
						if (!isRef(obj)) {
							return null;
						}
						var root = { items: [] };
						var queue = [{
							obj: obj,
							parent: root
						}];
						var processed = new RefSet();
						processed.put(obj);
						var xref = this.xref, blackColor = new Uint8Array(3);
						while (queue.length > 0) {
							var i = queue.shift();
							var outlineDict = xref.fetchIfRef(i.obj);
							if (outlineDict === null) {
								continue;
							}
							assert(outlineDict.has('Title'), 'Invalid outline item');
							var data = {
								url: null,
								dest: null
							};
							Catalog.parseDestDictionary({
								destDict: outlineDict,
								resultObj: data,
								docBaseUrl: this.pdfManager.docBaseUrl
							});
							var title = outlineDict.get('Title');
							var flags = outlineDict.get('F') || 0;
							var color = outlineDict.getArray('C'), rgbColor = blackColor;
							if (isArray(color) && color.length === 3 && (color[0] !== 0 || color[1] !== 0 || color[2] !== 0)) {
								rgbColor = ColorSpace.singletons.rgb.getRgb(color, 0);
							}
							var outlineItem = {
								dest: data.dest,
								url: data.url,
								unsafeUrl: data.unsafeUrl,
								newWindow: data.newWindow,
								title: stringToPDFString(title),
								color: rgbColor,
								count: outlineDict.get('Count'),
								bold: !!(flags & 2),
								italic: !!(flags & 1),
								items: []
							};
							i.parent.items.push(outlineItem);
							obj = outlineDict.getRaw('First');
							if (isRef(obj) && !processed.has(obj)) {
								queue.push({
									obj: obj,
									parent: outlineItem
								});
								processed.put(obj);
							}
							obj = outlineDict.getRaw('Next');
							if (isRef(obj) && !processed.has(obj)) {
								queue.push({
									obj: obj,
									parent: i.parent
								});
								processed.put(obj);
							}
						}
						return root.items.length > 0 ? root.items : null;
					},
					get numPages() {
						var obj = this.toplevelPagesDict.get('Count');
						assert(isInt(obj), 'page count in top level pages object is not an integer');
						return shadow(this, 'num', obj);
					},
					get destinations() {
						function fetchDestination(dest) {
							return isDict(dest) ? dest.get('D') : dest;
						}
						var xref = this.xref;
						var dests = {}, nameTreeRef, nameDictionaryRef;
						var obj = this.catDict.get('Names');
						if (obj && obj.has('Dests')) {
							nameTreeRef = obj.getRaw('Dests');
						} else if (this.catDict.has('Dests')) {
							nameDictionaryRef = this.catDict.get('Dests');
						}
						if (nameDictionaryRef) {
							obj = nameDictionaryRef;
							obj.forEach(function catalogForEach(key, value) {
								if (!value) {
									return;
								}
								dests[key] = fetchDestination(value);
							});
						}
						if (nameTreeRef) {
							var nameTree = new NameTree(nameTreeRef, xref);
							var names = nameTree.getAll();
							for (var name in names) {
								dests[name] = fetchDestination(names[name]);
							}
						}
						return shadow(this, 'destinations', dests);
					},
					getDestination: function Catalog_getDestination(destinationId) {
						function fetchDestination(dest) {
							return isDict(dest) ? dest.get('D') : dest;
						}
						var xref = this.xref;
						var dest = null, nameTreeRef, nameDictionaryRef;
						var obj = this.catDict.get('Names');
						if (obj && obj.has('Dests')) {
							nameTreeRef = obj.getRaw('Dests');
						} else if (this.catDict.has('Dests')) {
							nameDictionaryRef = this.catDict.get('Dests');
						}
						if (nameDictionaryRef) {
							var value = nameDictionaryRef.get(destinationId);
							if (value) {
								dest = fetchDestination(value);
							}
						}
						if (nameTreeRef) {
							var nameTree = new NameTree(nameTreeRef, xref);
							dest = fetchDestination(nameTree.get(destinationId));
						}
						return dest;
					},
					get pageLabels() {
						var obj = null;
						try {
							obj = this.readPageLabels();
						} catch (ex) {
							if (ex instanceof MissingDataException) {
								throw ex;
							}
							warn('Unable to read page labels.');
						}
						return shadow(this, 'pageLabels', obj);
					},
					readPageLabels: function Catalog_readPageLabels() {
						var obj = this.catDict.getRaw('PageLabels');
						if (!obj) {
							return null;
						}
						var pageLabels = new Array(this.numPages);
						var style = null;
						var prefix = '';
						var numberTree = new NumberTree(obj, this.xref);
						var nums = numberTree.getAll();
						var currentLabel = '', currentIndex = 1;
						for (var i = 0, ii = this.numPages; i < ii; i++) {
							if (i in nums) {
								var labelDict = nums[i];
								assert(isDict(labelDict), 'The PageLabel is not a dictionary.');
								var type = labelDict.get('Type');
								assert(!type || isName(type, 'PageLabel'), 'Invalid type in PageLabel dictionary.');
								var s = labelDict.get('S');
								assert(!s || isName(s), 'Invalid style in PageLabel dictionary.');
								style = s ? s.name : null;
								var p = labelDict.get('P');
								assert(!p || isString(p), 'Invalid prefix in PageLabel dictionary.');
								prefix = p ? stringToPDFString(p) : '';
								var st = labelDict.get('St');
								assert(!st || isInt(st) && st >= 1, 'Invalid start in PageLabel dictionary.');
								currentIndex = st || 1;
							}
							switch (style) {
								case 'D':
									currentLabel = currentIndex;
									break;
								case 'R':
								case 'r':
									currentLabel = Util.toRoman(currentIndex, style === 'r');
									break;
								case 'A':
								case 'a':
									var LIMIT = 26;
									var A_UPPER_CASE = 0x41, A_LOWER_CASE = 0x61;
									var baseCharCode = style === 'a' ? A_LOWER_CASE : A_UPPER_CASE;
									var letterIndex = currentIndex - 1;
									var character = String.fromCharCode(baseCharCode + letterIndex % LIMIT);
									var charBuf = [];
									for (var j = 0, jj = letterIndex / LIMIT | 0; j <= jj; j++) {
										charBuf.push(character);
									}
									currentLabel = charBuf.join('');
									break;
								default:
									assert(!style, 'Invalid style "' + style + '" in PageLabel dictionary.');
							}
							pageLabels[i] = prefix + currentLabel;
							currentLabel = '';
							currentIndex++;
						}
						return pageLabels;
					},
					get attachments() {
						var xref = this.xref;
						var attachments = null, nameTreeRef;
						var obj = this.catDict.get('Names');
						if (obj) {
							nameTreeRef = obj.getRaw('EmbeddedFiles');
						}
						if (nameTreeRef) {
							var nameTree = new NameTree(nameTreeRef, xref);
							var names = nameTree.getAll();
							for (var name in names) {
								var fs = new FileSpec(names[name], xref);
								if (!attachments) {
									attachments = Object.create(null);
								}
								attachments[stringToPDFString(name)] = fs.serializable;
							}
						}
						return shadow(this, 'attachments', attachments);
					},
					get javaScript() {
						var xref = this.xref;
						var obj = this.catDict.get('Names');
						var javaScript = [];
						function appendIfJavaScriptDict(jsDict) {
							var type = jsDict.get('S');
							if (!isName(type, 'JavaScript')) {
								return;
							}
							var js = jsDict.get('JS');
							if (isStream(js)) {
								js = bytesToString(js.getBytes());
							} else if (!isString(js)) {
								return;
							}
							javaScript.push(stringToPDFString(js));
						}
						if (obj && obj.has('JavaScript')) {
							var nameTree = new NameTree(obj.getRaw('JavaScript'), xref);
							var names = nameTree.getAll();
							for (var name in names) {
								var jsDict = names[name];
								if (isDict(jsDict)) {
									appendIfJavaScriptDict(jsDict);
								}
							}
						}
						var openactionDict = this.catDict.get('OpenAction');
						if (isDict(openactionDict, 'Action')) {
							var actionType = openactionDict.get('S');
							if (isName(actionType, 'Named')) {
								var action = openactionDict.get('N');
								if (isName(action, 'Print')) {
									javaScript.push('print({});');
								}
							} else {
								appendIfJavaScriptDict(openactionDict);
							}
						}
						return shadow(this, 'javaScript', javaScript);
					},
					cleanup: function Catalog_cleanup() {
						var promises = [];
						this.fontCache.forEach(function (promise) {
							promises.push(promise);
						});
						return Promise.all(promises).then(function (translatedFonts) {
							for (var i = 0, ii = translatedFonts.length; i < ii; i++) {
								var font = translatedFonts[i].dict;
								delete font.translated;
							}
							this.fontCache.clear();
							this.builtInCMapCache = Object.create(null);
						}.bind(this));
					},
					getPage: function Catalog_getPage(pageIndex) {
						if (!(pageIndex in this.pagePromises)) {
							this.pagePromises[pageIndex] = this.getPageDict(pageIndex).then(function (a) {
								var dict = a[0];
								var ref = a[1];
								return this.pageFactory.createPage(pageIndex, dict, ref, this.fontCache, this.builtInCMapCache);
							}.bind(this));
						}
						return this.pagePromises[pageIndex];
					},
					getPageDict: function Catalog_getPageDict(pageIndex) {
						var capability = createPromiseCapability();
						var nodesToVisit = [this.catDict.getRaw('Pages')];
						var currentPageIndex = 0;
						var xref = this.xref;
						function next() {
							while (nodesToVisit.length) {
								var currentNode = nodesToVisit.pop();
								if (isRef(currentNode)) {
									xref.fetchAsync(currentNode).then(function (obj) {
										if (isDict(obj, 'Page') || isDict(obj) && !obj.has('Kids')) {
											if (pageIndex === currentPageIndex) {
												capability.resolve([
													obj,
													currentNode
												]);
											} else {
												currentPageIndex++;
												next();
											}
											return;
										}
										nodesToVisit.push(obj);
										next();
									}, capability.reject);
									return;
								}
								assert(isDict(currentNode), 'page dictionary kid reference points to wrong type of object');
								var count = currentNode.get('Count');
								if (currentPageIndex + count <= pageIndex) {
									currentPageIndex += count;
									continue;
								}
								var kids = currentNode.get('Kids');
								assert(isArray(kids), 'page dictionary kids object is not an array');
								for (var last = kids.length - 1; last >= 0; last--) {
									nodesToVisit.push(kids[last]);
								}
							}
							capability.reject('Page index ' + pageIndex + ' not found.');
						}
						next();
						return capability.promise;
					},
					getPageIndex: function Catalog_getPageIndex(pageRef) {
						var xref = this.xref;
						function pagesBeforeRef(kidRef) {
							var total = 0;
							var parentRef;
							return xref.fetchAsync(kidRef).then(function (node) {
								if (isRefsEqual(kidRef, pageRef) && !isDict(node, 'Page') && !(isDict(node) && !node.has('Type') && node.has('Contents'))) {
									throw new Error('The reference does not point to a /Page Dict.');
								}
								if (!node) {
									return null;
								}
								assert(isDict(node), 'node must be a Dict.');
								parentRef = node.getRaw('Parent');
								return node.getAsync('Parent');
							}).then(function (parent) {
								if (!parent) {
									return null;
								}
								assert(isDict(parent), 'parent must be a Dict.');
								return parent.getAsync('Kids');
							}).then(function (kids) {
								if (!kids) {
									return null;
								}
								var kidPromises = [];
								var found = false;
								for (var i = 0; i < kids.length; i++) {
									var kid = kids[i];
									assert(isRef(kid), 'kid must be a Ref.');
									if (kid.num === kidRef.num) {
										found = true;
										break;
									}
									kidPromises.push(xref.fetchAsync(kid).then(function (kid) {
										if (kid.has('Count')) {
											var count = kid.get('Count');
											total += count;
										} else {
											total++;
										}
									}));
								}
								if (!found) {
									error('kid ref not found in parents kids');
								}
								return Promise.all(kidPromises).then(function () {
									return [
										total,
										parentRef
									];
								});
							});
						}
						var total = 0;
						function next(ref) {
							return pagesBeforeRef(ref).then(function (args) {
								if (!args) {
									return total;
								}
								var count = args[0];
								var parentRef = args[1];
								total += count;
								return next(parentRef);
							});
						}
						return next(pageRef);
					}
				};
				Catalog.parseDestDictionary = function Catalog_parseDestDictionary(params) {
					function addDefaultProtocolToUrl(url) {
						if (url.indexOf('www.') === 0) {
							return 'http://' + url;
						}
						return url;
					}
					function tryConvertUrlEncoding(url) {
						try {
							return stringToUTF8String(url);
						} catch (e) {
							return url;
						}
					}
					var destDict = params.destDict;
					if (!isDict(destDict)) {
						warn('Catalog_parseDestDictionary: "destDict" must be a dictionary.');
						return;
					}
					var resultObj = params.resultObj;
					if (typeof resultObj !== 'object') {
						warn('Catalog_parseDestDictionary: "resultObj" must be an object.');
						return;
					}
					var docBaseUrl = params.docBaseUrl || null;
					var action = destDict.get('A'), url, dest;
					if (isDict(action)) {
						var linkType = action.get('S').name;
						switch (linkType) {
							case 'URI':
								url = action.get('URI');
								if (isName(url)) {
									url = '/' + url.name;
								} else if (isString(url)) {
									url = addDefaultProtocolToUrl(url);
								}
								break;
							case 'GoTo':
								dest = action.get('D');
								break;
							case 'Launch':
							case 'GoToR':
								var urlDict = action.get('F');
								if (isDict(urlDict)) {
									url = urlDict.get('F') || null;
								} else if (isString(urlDict)) {
									url = urlDict;
								}
								var remoteDest = action.get('D');
								if (remoteDest) {
									if (isName(remoteDest)) {
										remoteDest = remoteDest.name;
									}
									if (isString(url)) {
										var baseUrl = url.split('#')[0];
										if (isString(remoteDest)) {
											url = baseUrl + '#' + (/^\d+$/.test(remoteDest) ? 'nameddest=' : '') + remoteDest;
										} else if (isArray(remoteDest)) {
											url = baseUrl + '#' + JSON.stringify(remoteDest);
										}
									}
								}
								var newWindow = action.get('NewWindow');
								if (isBool(newWindow)) {
									resultObj.newWindow = newWindow;
								}
								break;
							case 'Named':
								var namedAction = action.get('N');
								if (isName(namedAction)) {
									resultObj.action = namedAction.name;
								}
								break;
							case 'JavaScript':
								var jsAction = action.get('JS'), js;
								if (isStream(jsAction)) {
									js = bytesToString(jsAction.getBytes());
								} else if (isString(jsAction)) {
									js = jsAction;
								}
								if (js) {
									var URL_OPEN_METHODS = [
										'app.launchURL',
										'window.open'
									];
									var regex = new RegExp('^\\s*(' + URL_OPEN_METHODS.join('|').split('.').join('\\.') + ')\\((?:\'|\")([^\'\"]*)(?:\'|\")(?:,\\s*(\\w+)\\)|\\))', 'i');
									var jsUrl = regex.exec(stringToPDFString(js));
									if (jsUrl && jsUrl[2]) {
										url = jsUrl[2];
										if (jsUrl[3] === 'true' && jsUrl[1] === 'app.launchURL') {
											resultObj.newWindow = true;
										}
										break;
									}
								}
							default:
								warn('Catalog_parseDestDictionary: Unrecognized link type "' + linkType + '".');
								break;
						}
					} else if (destDict.has('Dest')) {
						dest = destDict.get('Dest');
					}
					if (isString(url)) {
						url = tryConvertUrlEncoding(url);
						var absoluteUrl = createValidAbsoluteUrl(url, docBaseUrl);
						if (absoluteUrl) {
							resultObj.url = absoluteUrl.href;
						}
						resultObj.unsafeUrl = url;
					}
					if (dest) {
						if (isName(dest)) {
							dest = dest.name;
						}
						if (isString(dest) || isArray(dest)) {
							resultObj.dest = dest;
						}
					}
				};
				return Catalog;
			}();
			var XRef = function XRefClosure() {
				function XRef(stream, pdfManager) {
					this.stream = stream;
					this.pdfManager = pdfManager;
					this.entries = [];
					this.xrefstms = Object.create(null);
					this.cache = [];
					this.stats = {
						streamTypes: [],
						fontTypes: []
					};
				}
				XRef.prototype = {
					setStartXRef: function XRef_setStartXRef(startXRef) {
						this.startXRefQueue = [startXRef];
					},
					parse: function XRef_parse(recoveryMode) {
						var trailerDict;
						if (!recoveryMode) {
							trailerDict = this.readXRef();
						} else {
							warn('Indexing all PDF objects');
							trailerDict = this.indexObjects();
						}
						trailerDict.assignXref(this);
						this.trailer = trailerDict;
						var encrypt = trailerDict.get('Encrypt');
						if (isDict(encrypt)) {
							var ids = trailerDict.get('ID');
							var fileId = ids && ids.length ? ids[0] : '';
							encrypt.suppressEncryption = true;
							this.encrypt = new CipherTransformFactory(encrypt, fileId, this.pdfManager.password);
						}
						if (!(this.root = trailerDict.get('Root'))) {
							error('Invalid root reference');
						}
					},
					processXRefTable: function XRef_processXRefTable(parser) {
						if (!('tableState' in this)) {
							this.tableState = {
								entryNum: 0,
								streamPos: parser.lexer.stream.pos,
								parserBuf1: parser.buf1,
								parserBuf2: parser.buf2
							};
						}
						var obj = this.readXRefTable(parser);
						if (!isCmd(obj, 'trailer')) {
							error('Invalid XRef table: could not find trailer dictionary');
						}
						var dict = parser.getObj();
						if (!isDict(dict) && dict.dict) {
							dict = dict.dict;
						}
						if (!isDict(dict)) {
							error('Invalid XRef table: could not parse trailer dictionary');
						}
						delete this.tableState;
						return dict;
					},
					readXRefTable: function XRef_readXRefTable(parser) {
						var stream = parser.lexer.stream;
						var tableState = this.tableState;
						stream.pos = tableState.streamPos;
						parser.buf1 = tableState.parserBuf1;
						parser.buf2 = tableState.parserBuf2;
						var obj;
						while (true) {
							if (!('firstEntryNum' in tableState) || !('entryCount' in tableState)) {
								if (isCmd(obj = parser.getObj(), 'trailer')) {
									break;
								}
								tableState.firstEntryNum = obj;
								tableState.entryCount = parser.getObj();
							}
							var first = tableState.firstEntryNum;
							var count = tableState.entryCount;
							if (!isInt(first) || !isInt(count)) {
								error('Invalid XRef table: wrong types in subsection header');
							}
							for (var i = tableState.entryNum; i < count; i++) {
								tableState.streamPos = stream.pos;
								tableState.entryNum = i;
								tableState.parserBuf1 = parser.buf1;
								tableState.parserBuf2 = parser.buf2;
								var entry = {};
								entry.offset = parser.getObj();
								entry.gen = parser.getObj();
								var type = parser.getObj();
								if (isCmd(type, 'f')) {
									entry.free = true;
								} else if (isCmd(type, 'n')) {
									entry.uncompressed = true;
								}
								if (!isInt(entry.offset) || !isInt(entry.gen) || !(entry.free || entry.uncompressed)) {
									error('Invalid entry in XRef subsection: ' + first + ', ' + count);
								}
								if (i === 0 && entry.free && first === 1) {
									first = 0;
								}
								if (!this.entries[i + first]) {
									this.entries[i + first] = entry;
								}
							}
							tableState.entryNum = 0;
							tableState.streamPos = stream.pos;
							tableState.parserBuf1 = parser.buf1;
							tableState.parserBuf2 = parser.buf2;
							delete tableState.firstEntryNum;
							delete tableState.entryCount;
						}
						if (this.entries[0] && !this.entries[0].free) {
							error('Invalid XRef table: unexpected first object');
						}
						return obj;
					},
					processXRefStream: function XRef_processXRefStream(stream) {
						if (!('streamState' in this)) {
							var streamParameters = stream.dict;
							var byteWidths = streamParameters.get('W');
							var range = streamParameters.get('Index');
							if (!range) {
								range = [
									0,
									streamParameters.get('Size')
								];
							}
							this.streamState = {
								entryRanges: range,
								byteWidths: byteWidths,
								entryNum: 0,
								streamPos: stream.pos
							};
						}
						this.readXRefStream(stream);
						delete this.streamState;
						return stream.dict;
					},
					readXRefStream: function XRef_readXRefStream(stream) {
						var i, j;
						var streamState = this.streamState;
						stream.pos = streamState.streamPos;
						var byteWidths = streamState.byteWidths;
						var typeFieldWidth = byteWidths[0];
						var offsetFieldWidth = byteWidths[1];
						var generationFieldWidth = byteWidths[2];
						var entryRanges = streamState.entryRanges;
						while (entryRanges.length > 0) {
							var first = entryRanges[0];
							var n = entryRanges[1];
							if (!isInt(first) || !isInt(n)) {
								error('Invalid XRef range fields: ' + first + ', ' + n);
							}
							if (!isInt(typeFieldWidth) || !isInt(offsetFieldWidth) || !isInt(generationFieldWidth)) {
								error('Invalid XRef entry fields length: ' + first + ', ' + n);
							}
							for (i = streamState.entryNum; i < n; ++i) {
								streamState.entryNum = i;
								streamState.streamPos = stream.pos;
								var type = 0, offset = 0, generation = 0;
								for (j = 0; j < typeFieldWidth; ++j) {
									type = type << 8 | stream.getByte();
								}
								if (typeFieldWidth === 0) {
									type = 1;
								}
								for (j = 0; j < offsetFieldWidth; ++j) {
									offset = offset << 8 | stream.getByte();
								}
								for (j = 0; j < generationFieldWidth; ++j) {
									generation = generation << 8 | stream.getByte();
								}
								var entry = {};
								entry.offset = offset;
								entry.gen = generation;
								switch (type) {
									case 0:
										entry.free = true;
										break;
									case 1:
										entry.uncompressed = true;
										break;
									case 2:
										break;
									default:
										error('Invalid XRef entry type: ' + type);
								}
								if (!this.entries[first + i]) {
									this.entries[first + i] = entry;
								}
							}
							streamState.entryNum = 0;
							streamState.streamPos = stream.pos;
							entryRanges.splice(0, 2);
						}
					},
					indexObjects: function XRef_indexObjects() {
						var TAB = 0x9, LF = 0xA, CR = 0xD, SPACE = 0x20;
						var PERCENT = 0x25, LT = 0x3C;
						function readToken(data, offset) {
							var token = '', ch = data[offset];
							while (ch !== LF && ch !== CR && ch !== LT) {
								if (++offset >= data.length) {
									break;
								}
								token += String.fromCharCode(ch);
								ch = data[offset];
							}
							return token;
						}
						function skipUntil(data, offset, what) {
							var length = what.length, dataLength = data.length;
							var skipped = 0;
							while (offset < dataLength) {
								var i = 0;
								while (i < length && data[offset + i] === what[i]) {
									++i;
								}
								if (i >= length) {
									break;
								}
								offset++;
								skipped++;
							}
							return skipped;
						}
						var objRegExp = /^(\d+)\s+(\d+)\s+obj\b/;
						var trailerBytes = new Uint8Array([
							116,
							114,
							97,
							105,
							108,
							101,
							114
						]);
						var startxrefBytes = new Uint8Array([
							115,
							116,
							97,
							114,
							116,
							120,
							114,
							101,
							102
						]);
						var endobjBytes = new Uint8Array([
							101,
							110,
							100,
							111,
							98,
							106
						]);
						var xrefBytes = new Uint8Array([
							47,
							88,
							82,
							101,
							102
						]);
						this.entries.length = 0;
						var stream = this.stream;
						stream.pos = 0;
						var buffer = stream.getBytes();
						var position = stream.start, length = buffer.length;
						var trailers = [], xrefStms = [];
						while (position < length) {
							var ch = buffer[position];
							if (ch === TAB || ch === LF || ch === CR || ch === SPACE) {
								++position;
								continue;
							}
							if (ch === PERCENT) {
								do {
									++position;
									if (position >= length) {
										break;
									}
									ch = buffer[position];
								} while (ch !== LF && ch !== CR);
								continue;
							}
							var token = readToken(buffer, position);
							var m;
							if (token.indexOf('xref') === 0 && (token.length === 4 || /\s/.test(token[4]))) {
								position += skipUntil(buffer, position, trailerBytes);
								trailers.push(position);
								position += skipUntil(buffer, position, startxrefBytes);
							} else if (m = objRegExp.exec(token)) {
								if (typeof this.entries[m[1]] === 'undefined') {
									this.entries[m[1]] = {
										offset: position - stream.start,
										gen: m[2] | 0,
										uncompressed: true
									};
								}
								var contentLength = skipUntil(buffer, position, endobjBytes) + 7;
								var content = buffer.subarray(position, position + contentLength);
								var xrefTagOffset = skipUntil(content, 0, xrefBytes);
								if (xrefTagOffset < contentLength && content[xrefTagOffset + 5] < 64) {
									xrefStms.push(position - stream.start);
									this.xrefstms[position - stream.start] = 1;
								}
								position += contentLength;
							} else if (token.indexOf('trailer') === 0 && (token.length === 7 || /\s/.test(token[7]))) {
								trailers.push(position);
								position += skipUntil(buffer, position, startxrefBytes);
							} else {
								position += token.length + 1;
							}
						}
						var i, ii;
						for (i = 0, ii = xrefStms.length; i < ii; ++i) {
							this.startXRefQueue.push(xrefStms[i]);
							this.readXRef(true);
						}
						var dict;
						for (i = 0, ii = trailers.length; i < ii; ++i) {
							stream.pos = trailers[i];
							var parser = new Parser(new Lexer(stream), true, this, true);
							var obj = parser.getObj();
							if (!isCmd(obj, 'trailer')) {
								continue;
							}
							dict = parser.getObj();
							if (!isDict(dict)) {
								continue;
							}
							if (dict.has('ID')) {
								return dict;
							}
						}
						if (dict) {
							return dict;
						}
						throw new InvalidPDFException('Invalid PDF structure');
					},
					readXRef: function XRef_readXRef(recoveryMode) {
						var stream = this.stream;
						try {
							while (this.startXRefQueue.length) {
								var startXRef = this.startXRefQueue[0];
								stream.pos = startXRef + stream.start;
								var parser = new Parser(new Lexer(stream), true, this);
								var obj = parser.getObj();
								var dict;
								if (isCmd(obj, 'xref')) {
									dict = this.processXRefTable(parser);
									if (!this.topDict) {
										this.topDict = dict;
									}
									obj = dict.get('XRefStm');
									if (isInt(obj)) {
										var pos = obj;
										if (!(pos in this.xrefstms)) {
											this.xrefstms[pos] = 1;
											this.startXRefQueue.push(pos);
										}
									}
								} else if (isInt(obj)) {
									if (!isInt(parser.getObj()) || !isCmd(parser.getObj(), 'obj') || !isStream(obj = parser.getObj())) {
										error('Invalid XRef stream');
									}
									dict = this.processXRefStream(obj);
									if (!this.topDict) {
										this.topDict = dict;
									}
									if (!dict) {
										error('Failed to read XRef stream');
									}
								} else {
									error('Invalid XRef stream header');
								}
								obj = dict.get('Prev');
								if (isInt(obj)) {
									this.startXRefQueue.push(obj);
								} else if (isRef(obj)) {
									this.startXRefQueue.push(obj.num);
								}
								this.startXRefQueue.shift();
							}
							return this.topDict;
						} catch (e) {
							if (e instanceof MissingDataException) {
								throw e;
							}
							info('(while reading XRef): ' + e);
						}
						if (recoveryMode) {
							return;
						}
						throw new XRefParseException();
					},
					getEntry: function XRef_getEntry(i) {
						var xrefEntry = this.entries[i];
						if (xrefEntry && !xrefEntry.free && xrefEntry.offset) {
							return xrefEntry;
						}
						return null;
					},
					fetchIfRef: function XRef_fetchIfRef(obj, suppressEncryption) {
						if (!isRef(obj)) {
							return obj;
						}
						return this.fetch(obj, suppressEncryption);
					},
					fetch: function XRef_fetch(ref, suppressEncryption) {
						assert(isRef(ref), 'ref object is not a reference');
						var num = ref.num;
						if (num in this.cache) {
							var cacheEntry = this.cache[num];
							if (isDict(cacheEntry) && !cacheEntry.objId) {
								cacheEntry.objId = ref.toString();
							}
							return cacheEntry;
						}
						var xrefEntry = this.getEntry(num);
						if (xrefEntry === null) {
							return this.cache[num] = null;
						}
						if (xrefEntry.uncompressed) {
							xrefEntry = this.fetchUncompressed(ref, xrefEntry, suppressEncryption);
						} else {
							xrefEntry = this.fetchCompressed(xrefEntry, suppressEncryption);
						}
						if (isDict(xrefEntry)) {
							xrefEntry.objId = ref.toString();
						} else if (isStream(xrefEntry)) {
							xrefEntry.dict.objId = ref.toString();
						}
						return xrefEntry;
					},
					fetchUncompressed: function XRef_fetchUncompressed(ref, xrefEntry, suppressEncryption) {
						var gen = ref.gen;
						var num = ref.num;
						if (xrefEntry.gen !== gen) {
							error('inconsistent generation in XRef');
						}
						var stream = this.stream.makeSubStream(xrefEntry.offset + this.stream.start);
						var parser = new Parser(new Lexer(stream), true, this);
						var obj1 = parser.getObj();
						var obj2 = parser.getObj();
						var obj3 = parser.getObj();
						if (!isInt(obj1) || parseInt(obj1, 10) !== num || !isInt(obj2) || parseInt(obj2, 10) !== gen || !isCmd(obj3)) {
							error('bad XRef entry');
						}
						if (!isCmd(obj3, 'obj')) {
							if (obj3.cmd.indexOf('obj') === 0) {
								num = parseInt(obj3.cmd.substring(3), 10);
								if (!isNaN(num)) {
									return num;
								}
							}
							error('bad XRef entry');
						}
						if (this.encrypt && !suppressEncryption) {
							xrefEntry = parser.getObj(this.encrypt.createCipherTransform(num, gen));
						} else {
							xrefEntry = parser.getObj();
						}
						if (!isStream(xrefEntry)) {
							this.cache[num] = xrefEntry;
						}
						return xrefEntry;
					},
					fetchCompressed: function XRef_fetchCompressed(xrefEntry, suppressEncryption) {
						var tableOffset = xrefEntry.offset;
						var stream = this.fetch(new Ref(tableOffset, 0));
						if (!isStream(stream)) {
							error('bad ObjStm stream');
						}
						var first = stream.dict.get('First');
						var n = stream.dict.get('N');
						if (!isInt(first) || !isInt(n)) {
							error('invalid first and n parameters for ObjStm stream');
						}
						var parser = new Parser(new Lexer(stream), false, this);
						parser.allowStreams = true;
						var i, entries = [], num, nums = [];
						for (i = 0; i < n; ++i) {
							num = parser.getObj();
							if (!isInt(num)) {
								error('invalid object number in the ObjStm stream: ' + num);
							}
							nums.push(num);
							var offset = parser.getObj();
							if (!isInt(offset)) {
								error('invalid object offset in the ObjStm stream: ' + offset);
							}
						}
						for (i = 0; i < n; ++i) {
							entries.push(parser.getObj());
							if (isCmd(parser.buf1, 'endobj')) {
								parser.shift();
							}
							num = nums[i];
							var entry = this.entries[num];
							if (entry && entry.offset === tableOffset && entry.gen === i) {
								this.cache[num] = entries[i];
							}
						}
						xrefEntry = entries[xrefEntry.gen];
						if (xrefEntry === undefined) {
							error('bad XRef entry for compressed object');
						}
						return xrefEntry;
					},
					fetchIfRefAsync: function XRef_fetchIfRefAsync(obj, suppressEncryption) {
						if (!isRef(obj)) {
							return Promise.resolve(obj);
						}
						return this.fetchAsync(obj, suppressEncryption);
					},
					fetchAsync: function XRef_fetchAsync(ref, suppressEncryption) {
						var streamManager = this.stream.manager;
						var xref = this;
						return new Promise(function tryFetch(resolve, reject) {
							try {
								resolve(xref.fetch(ref, suppressEncryption));
							} catch (e) {
								if (e instanceof MissingDataException) {
									streamManager.requestRange(e.begin, e.end).then(function () {
										tryFetch(resolve, reject);
									}, reject);
									return;
								}
								reject(e);
							}
						});
					},
					getCatalogObj: function XRef_getCatalogObj() {
						return this.root;
					}
				};
				return XRef;
			}();
			var NameOrNumberTree = function NameOrNumberTreeClosure() {
				function NameOrNumberTree(root, xref) {
					throw new Error('Cannot initialize NameOrNumberTree.');
				}
				NameOrNumberTree.prototype = {
					getAll: function NameOrNumberTree_getAll() {
						var dict = Object.create(null);
						if (!this.root) {
							return dict;
						}
						var xref = this.xref;
						var processed = new RefSet();
						processed.put(this.root);
						var queue = [this.root];
						while (queue.length > 0) {
							var i, n;
							var obj = xref.fetchIfRef(queue.shift());
							if (!isDict(obj)) {
								continue;
							}
							if (obj.has('Kids')) {
								var kids = obj.get('Kids');
								for (i = 0, n = kids.length; i < n; i++) {
									var kid = kids[i];
									assert(!processed.has(kid), 'Duplicate entry in "' + this._type + '" tree.');
									queue.push(kid);
									processed.put(kid);
								}
								continue;
							}
							var entries = obj.get(this._type);
							if (isArray(entries)) {
								for (i = 0, n = entries.length; i < n; i += 2) {
									dict[xref.fetchIfRef(entries[i])] = xref.fetchIfRef(entries[i + 1]);
								}
							}
						}
						return dict;
					},
					get: function NameOrNumberTree_get(key) {
						if (!this.root) {
							return null;
						}
						var xref = this.xref;
						var kidsOrEntries = xref.fetchIfRef(this.root);
						var loopCount = 0;
						var MAX_LEVELS = 10;
						var l, r, m;
						while (kidsOrEntries.has('Kids')) {
							if (++loopCount > MAX_LEVELS) {
								warn('Search depth limit reached for "' + this._type + '" tree.');
								return null;
							}
							var kids = kidsOrEntries.get('Kids');
							if (!isArray(kids)) {
								return null;
							}
							l = 0;
							r = kids.length - 1;
							while (l <= r) {
								m = l + r >> 1;
								var kid = xref.fetchIfRef(kids[m]);
								var limits = kid.get('Limits');
								if (key < xref.fetchIfRef(limits[0])) {
									r = m - 1;
								} else if (key > xref.fetchIfRef(limits[1])) {
									l = m + 1;
								} else {
									kidsOrEntries = xref.fetchIfRef(kids[m]);
									break;
								}
							}
							if (l > r) {
								return null;
							}
						}
						var entries = kidsOrEntries.get(this._type);
						if (isArray(entries)) {
							l = 0;
							r = entries.length - 2;
							while (l <= r) {
								m = l + r & ~1;
								var currentKey = xref.fetchIfRef(entries[m]);
								if (key < currentKey) {
									r = m - 2;
								} else if (key > currentKey) {
									l = m + 2;
								} else {
									return xref.fetchIfRef(entries[m + 1]);
								}
							}
						}
						return null;
					}
				};
				return NameOrNumberTree;
			}();
			var NameTree = function NameTreeClosure() {
				function NameTree(root, xref) {
					this.root = root;
					this.xref = xref;
					this._type = 'Names';
				}
				Util.inherit(NameTree, NameOrNumberTree, {});
				return NameTree;
			}();
			var NumberTree = function NumberTreeClosure() {
				function NumberTree(root, xref) {
					this.root = root;
					this.xref = xref;
					this._type = 'Nums';
				}
				Util.inherit(NumberTree, NameOrNumberTree, {});
				return NumberTree;
			}();
			var FileSpec = function FileSpecClosure() {
				function FileSpec(root, xref) {
					if (!root || !isDict(root)) {
						return;
					}
					this.xref = xref;
					this.root = root;
					if (root.has('FS')) {
						this.fs = root.get('FS');
					}
					this.description = root.has('Desc') ? stringToPDFString(root.get('Desc')) : '';
					if (root.has('RF')) {
						warn('Related file specifications are not supported');
					}
					this.contentAvailable = true;
					if (!root.has('EF')) {
						this.contentAvailable = false;
						warn('Non-embedded file specifications are not supported');
					}
				}
				function pickPlatformItem(dict) {
					if (dict.has('UF')) {
						return dict.get('UF');
					} else if (dict.has('F')) {
						return dict.get('F');
					} else if (dict.has('Unix')) {
						return dict.get('Unix');
					} else if (dict.has('Mac')) {
						return dict.get('Mac');
					} else if (dict.has('DOS')) {
						return dict.get('DOS');
					}
					return null;
				}
				FileSpec.prototype = {
					get filename() {
						if (!this._filename && this.root) {
							var filename = pickPlatformItem(this.root) || 'unnamed';
							this._filename = stringToPDFString(filename).replace(/\\\\/g, '\\').replace(/\\\//g, '/').replace(/\\/g, '/');
						}
						return this._filename;
					},
					get content() {
						if (!this.contentAvailable) {
							return null;
						}
						if (!this.contentRef && this.root) {
							this.contentRef = pickPlatformItem(this.root.get('EF'));
						}
						var content = null;
						if (this.contentRef) {
							var xref = this.xref;
							var fileObj = xref.fetchIfRef(this.contentRef);
							if (fileObj && isStream(fileObj)) {
								content = fileObj.getBytes();
							} else {
								warn('Embedded file specification points to non-existing/invalid ' + 'content');
							}
						} else {
							warn('Embedded file specification does not have a content');
						}
						return content;
					},
					get serializable() {
						return {
							filename: this.filename,
							content: this.content
						};
					}
				};
				return FileSpec;
			}();
			var ObjectLoader = function () {
				function mayHaveChildren(value) {
					return isRef(value) || isDict(value) || isArray(value) || isStream(value);
				}
				function addChildren(node, nodesToVisit) {
					var value;
					if (isDict(node) || isStream(node)) {
						var map;
						if (isDict(node)) {
							map = node.map;
						} else {
							map = node.dict.map;
						}
						for (var key in map) {
							value = map[key];
							if (mayHaveChildren(value)) {
								nodesToVisit.push(value);
							}
						}
					} else if (isArray(node)) {
						for (var i = 0, ii = node.length; i < ii; i++) {
							value = node[i];
							if (mayHaveChildren(value)) {
								nodesToVisit.push(value);
							}
						}
					}
				}
				function ObjectLoader(obj, keys, xref) {
					this.obj = obj;
					this.keys = keys;
					this.xref = xref;
					this.refSet = null;
					this.capability = null;
				}
				ObjectLoader.prototype = {
					load: function ObjectLoader_load() {
						var keys = this.keys;
						this.capability = createPromiseCapability();
						if (!(this.xref.stream instanceof ChunkedStream) || this.xref.stream.getMissingChunks().length === 0) {
							this.capability.resolve();
							return this.capability.promise;
						}
						this.refSet = new RefSet();
						var nodesToVisit = [];
						for (var i = 0; i < keys.length; i++) {
							nodesToVisit.push(this.obj[keys[i]]);
						}
						this._walk(nodesToVisit);
						return this.capability.promise;
					},
					_walk: function ObjectLoader_walk(nodesToVisit) {
						var nodesToRevisit = [];
						var pendingRequests = [];
						while (nodesToVisit.length) {
							var currentNode = nodesToVisit.pop();
							if (isRef(currentNode)) {
								if (this.refSet.has(currentNode)) {
									continue;
								}
								try {
									var ref = currentNode;
									this.refSet.put(ref);
									currentNode = this.xref.fetch(currentNode);
								} catch (e) {
									if (!(e instanceof MissingDataException)) {
										throw e;
									}
									nodesToRevisit.push(currentNode);
									pendingRequests.push({
										begin: e.begin,
										end: e.end
									});
								}
							}
							if (currentNode && currentNode.getBaseStreams) {
								var baseStreams = currentNode.getBaseStreams();
								var foundMissingData = false;
								for (var i = 0; i < baseStreams.length; i++) {
									var stream = baseStreams[i];
									if (stream.getMissingChunks && stream.getMissingChunks().length) {
										foundMissingData = true;
										pendingRequests.push({
											begin: stream.start,
											end: stream.end
										});
									}
								}
								if (foundMissingData) {
									nodesToRevisit.push(currentNode);
								}
							}
							addChildren(currentNode, nodesToVisit);
						}
						if (pendingRequests.length) {
							this.xref.stream.manager.requestRanges(pendingRequests).then(function pendingRequestCallback() {
								nodesToVisit = nodesToRevisit;
								for (var i = 0; i < nodesToRevisit.length; i++) {
									var node = nodesToRevisit[i];
									if (isRef(node)) {
										this.refSet.remove(node);
									}
								}
								this._walk(nodesToVisit);
							}.bind(this), this.capability.reject);
							return;
						}
						this.refSet = null;
						this.capability.resolve();
					}
				};
				return ObjectLoader;
			}();
			exports.Catalog = Catalog;
			exports.ObjectLoader = ObjectLoader;
			exports.XRef = XRef;
			exports.FileSpec = FileSpec;

			/***/
		}),
/* 17 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var getLookupTableFactory = sharedUtil.getLookupTableFactory;
			var getStdFontMap = getLookupTableFactory(function (t) {
				t['ArialNarrow'] = 'Helvetica';
				t['ArialNarrow-Bold'] = 'Helvetica-Bold';
				t['ArialNarrow-BoldItalic'] = 'Helvetica-BoldOblique';
				t['ArialNarrow-Italic'] = 'Helvetica-Oblique';
				t['ArialBlack'] = 'Helvetica';
				t['ArialBlack-Bold'] = 'Helvetica-Bold';
				t['ArialBlack-BoldItalic'] = 'Helvetica-BoldOblique';
				t['ArialBlack-Italic'] = 'Helvetica-Oblique';
				t['Arial-Black'] = 'Helvetica';
				t['Arial-Black-Bold'] = 'Helvetica-Bold';
				t['Arial-Black-BoldItalic'] = 'Helvetica-BoldOblique';
				t['Arial-Black-Italic'] = 'Helvetica-Oblique';
				t['Arial'] = 'Helvetica';
				t['Arial-Bold'] = 'Helvetica-Bold';
				t['Arial-BoldItalic'] = 'Helvetica-BoldOblique';
				t['Arial-Italic'] = 'Helvetica-Oblique';
				t['Arial-BoldItalicMT'] = 'Helvetica-BoldOblique';
				t['Arial-BoldMT'] = 'Helvetica-Bold';
				t['Arial-ItalicMT'] = 'Helvetica-Oblique';
				t['ArialMT'] = 'Helvetica';
				t['Courier-Bold'] = 'Courier-Bold';
				t['Courier-BoldItalic'] = 'Courier-BoldOblique';
				t['Courier-Italic'] = 'Courier-Oblique';
				t['CourierNew'] = 'Courier';
				t['CourierNew-Bold'] = 'Courier-Bold';
				t['CourierNew-BoldItalic'] = 'Courier-BoldOblique';
				t['CourierNew-Italic'] = 'Courier-Oblique';
				t['CourierNewPS-BoldItalicMT'] = 'Courier-BoldOblique';
				t['CourierNewPS-BoldMT'] = 'Courier-Bold';
				t['CourierNewPS-ItalicMT'] = 'Courier-Oblique';
				t['CourierNewPSMT'] = 'Courier';
				t['Helvetica'] = 'Helvetica';
				t['Helvetica-Bold'] = 'Helvetica-Bold';
				t['Helvetica-BoldItalic'] = 'Helvetica-BoldOblique';
				t['Helvetica-BoldOblique'] = 'Helvetica-BoldOblique';
				t['Helvetica-Italic'] = 'Helvetica-Oblique';
				t['Helvetica-Oblique'] = 'Helvetica-Oblique';
				t['Symbol-Bold'] = 'Symbol';
				t['Symbol-BoldItalic'] = 'Symbol';
				t['Symbol-Italic'] = 'Symbol';
				t['TimesNewRoman'] = 'Times-Roman';
				t['TimesNewRoman-Bold'] = 'Times-Bold';
				t['TimesNewRoman-BoldItalic'] = 'Times-BoldItalic';
				t['TimesNewRoman-Italic'] = 'Times-Italic';
				t['TimesNewRomanPS'] = 'Times-Roman';
				t['TimesNewRomanPS-Bold'] = 'Times-Bold';
				t['TimesNewRomanPS-BoldItalic'] = 'Times-BoldItalic';
				t['TimesNewRomanPS-BoldItalicMT'] = 'Times-BoldItalic';
				t['TimesNewRomanPS-BoldMT'] = 'Times-Bold';
				t['TimesNewRomanPS-Italic'] = 'Times-Italic';
				t['TimesNewRomanPS-ItalicMT'] = 'Times-Italic';
				t['TimesNewRomanPSMT'] = 'Times-Roman';
				t['TimesNewRomanPSMT-Bold'] = 'Times-Bold';
				t['TimesNewRomanPSMT-BoldItalic'] = 'Times-BoldItalic';
				t['TimesNewRomanPSMT-Italic'] = 'Times-Italic';
			});
			var getNonStdFontMap = getLookupTableFactory(function (t) {
				t['CenturyGothic'] = 'Helvetica';
				t['CenturyGothic-Bold'] = 'Helvetica-Bold';
				t['CenturyGothic-BoldItalic'] = 'Helvetica-BoldOblique';
				t['CenturyGothic-Italic'] = 'Helvetica-Oblique';
				t['ComicSansMS'] = 'Comic Sans MS';
				t['ComicSansMS-Bold'] = 'Comic Sans MS-Bold';
				t['ComicSansMS-BoldItalic'] = 'Comic Sans MS-BoldItalic';
				t['ComicSansMS-Italic'] = 'Comic Sans MS-Italic';
				t['LucidaConsole'] = 'Courier';
				t['LucidaConsole-Bold'] = 'Courier-Bold';
				t['LucidaConsole-BoldItalic'] = 'Courier-BoldOblique';
				t['LucidaConsole-Italic'] = 'Courier-Oblique';
				t['MS-Gothic'] = 'MS Gothic';
				t['MS-Gothic-Bold'] = 'MS Gothic-Bold';
				t['MS-Gothic-BoldItalic'] = 'MS Gothic-BoldItalic';
				t['MS-Gothic-Italic'] = 'MS Gothic-Italic';
				t['MS-Mincho'] = 'MS Mincho';
				t['MS-Mincho-Bold'] = 'MS Mincho-Bold';
				t['MS-Mincho-BoldItalic'] = 'MS Mincho-BoldItalic';
				t['MS-Mincho-Italic'] = 'MS Mincho-Italic';
				t['MS-PGothic'] = 'MS PGothic';
				t['MS-PGothic-Bold'] = 'MS PGothic-Bold';
				t['MS-PGothic-BoldItalic'] = 'MS PGothic-BoldItalic';
				t['MS-PGothic-Italic'] = 'MS PGothic-Italic';
				t['MS-PMincho'] = 'MS PMincho';
				t['MS-PMincho-Bold'] = 'MS PMincho-Bold';
				t['MS-PMincho-BoldItalic'] = 'MS PMincho-BoldItalic';
				t['MS-PMincho-Italic'] = 'MS PMincho-Italic';
				t['NuptialScript'] = 'Times-Italic';
				t['Wingdings'] = 'ZapfDingbats';
			});
			var getSerifFonts = getLookupTableFactory(function (t) {
				t['Adobe Jenson'] = true;
				t['Adobe Text'] = true;
				t['Albertus'] = true;
				t['Aldus'] = true;
				t['Alexandria'] = true;
				t['Algerian'] = true;
				t['American Typewriter'] = true;
				t['Antiqua'] = true;
				t['Apex'] = true;
				t['Arno'] = true;
				t['Aster'] = true;
				t['Aurora'] = true;
				t['Baskerville'] = true;
				t['Bell'] = true;
				t['Bembo'] = true;
				t['Bembo Schoolbook'] = true;
				t['Benguiat'] = true;
				t['Berkeley Old Style'] = true;
				t['Bernhard Modern'] = true;
				t['Berthold City'] = true;
				t['Bodoni'] = true;
				t['Bauer Bodoni'] = true;
				t['Book Antiqua'] = true;
				t['Bookman'] = true;
				t['Bordeaux Roman'] = true;
				t['Californian FB'] = true;
				t['Calisto'] = true;
				t['Calvert'] = true;
				t['Capitals'] = true;
				t['Cambria'] = true;
				t['Cartier'] = true;
				t['Caslon'] = true;
				t['Catull'] = true;
				t['Centaur'] = true;
				t['Century Old Style'] = true;
				t['Century Schoolbook'] = true;
				t['Chaparral'] = true;
				t['Charis SIL'] = true;
				t['Cheltenham'] = true;
				t['Cholla Slab'] = true;
				t['Clarendon'] = true;
				t['Clearface'] = true;
				t['Cochin'] = true;
				t['Colonna'] = true;
				t['Computer Modern'] = true;
				t['Concrete Roman'] = true;
				t['Constantia'] = true;
				t['Cooper Black'] = true;
				t['Corona'] = true;
				t['Ecotype'] = true;
				t['Egyptienne'] = true;
				t['Elephant'] = true;
				t['Excelsior'] = true;
				t['Fairfield'] = true;
				t['FF Scala'] = true;
				t['Folkard'] = true;
				t['Footlight'] = true;
				t['FreeSerif'] = true;
				t['Friz Quadrata'] = true;
				t['Garamond'] = true;
				t['Gentium'] = true;
				t['Georgia'] = true;
				t['Gloucester'] = true;
				t['Goudy Old Style'] = true;
				t['Goudy Schoolbook'] = true;
				t['Goudy Pro Font'] = true;
				t['Granjon'] = true;
				t['Guardian Egyptian'] = true;
				t['Heather'] = true;
				t['Hercules'] = true;
				t['High Tower Text'] = true;
				t['Hiroshige'] = true;
				t['Hoefler Text'] = true;
				t['Humana Serif'] = true;
				t['Imprint'] = true;
				t['Ionic No. 5'] = true;
				t['Janson'] = true;
				t['Joanna'] = true;
				t['Korinna'] = true;
				t['Lexicon'] = true;
				t['Liberation Serif'] = true;
				t['Linux Libertine'] = true;
				t['Literaturnaya'] = true;
				t['Lucida'] = true;
				t['Lucida Bright'] = true;
				t['Melior'] = true;
				t['Memphis'] = true;
				t['Miller'] = true;
				t['Minion'] = true;
				t['Modern'] = true;
				t['Mona Lisa'] = true;
				t['Mrs Eaves'] = true;
				t['MS Serif'] = true;
				t['Museo Slab'] = true;
				t['New York'] = true;
				t['Nimbus Roman'] = true;
				t['NPS Rawlinson Roadway'] = true;
				t['NuptialScript'] = true;
				t['Palatino'] = true;
				t['Perpetua'] = true;
				t['Plantin'] = true;
				t['Plantin Schoolbook'] = true;
				t['Playbill'] = true;
				t['Poor Richard'] = true;
				t['Rawlinson Roadway'] = true;
				t['Renault'] = true;
				t['Requiem'] = true;
				t['Rockwell'] = true;
				t['Roman'] = true;
				t['Rotis Serif'] = true;
				t['Sabon'] = true;
				t['Scala'] = true;
				t['Seagull'] = true;
				t['Sistina'] = true;
				t['Souvenir'] = true;
				t['STIX'] = true;
				t['Stone Informal'] = true;
				t['Stone Serif'] = true;
				t['Sylfaen'] = true;
				t['Times'] = true;
				t['Trajan'] = true;
				t['Trinité'] = true;
				t['Trump Mediaeval'] = true;
				t['Utopia'] = true;
				t['Vale Type'] = true;
				t['Bitstream Vera'] = true;
				t['Vera Serif'] = true;
				t['Versailles'] = true;
				t['Wanted'] = true;
				t['Weiss'] = true;
				t['Wide Latin'] = true;
				t['Windsor'] = true;
				t['XITS'] = true;
			});
			var getSymbolsFonts = getLookupTableFactory(function (t) {
				t['Dingbats'] = true;
				t['Symbol'] = true;
				t['ZapfDingbats'] = true;
			});
			var getGlyphMapForStandardFonts = getLookupTableFactory(function (t) {
				t[2] = 10;
				t[3] = 32;
				t[4] = 33;
				t[5] = 34;
				t[6] = 35;
				t[7] = 36;
				t[8] = 37;
				t[9] = 38;
				t[10] = 39;
				t[11] = 40;
				t[12] = 41;
				t[13] = 42;
				t[14] = 43;
				t[15] = 44;
				t[16] = 45;
				t[17] = 46;
				t[18] = 47;
				t[19] = 48;
				t[20] = 49;
				t[21] = 50;
				t[22] = 51;
				t[23] = 52;
				t[24] = 53;
				t[25] = 54;
				t[26] = 55;
				t[27] = 56;
				t[28] = 57;
				t[29] = 58;
				t[30] = 894;
				t[31] = 60;
				t[32] = 61;
				t[33] = 62;
				t[34] = 63;
				t[35] = 64;
				t[36] = 65;
				t[37] = 66;
				t[38] = 67;
				t[39] = 68;
				t[40] = 69;
				t[41] = 70;
				t[42] = 71;
				t[43] = 72;
				t[44] = 73;
				t[45] = 74;
				t[46] = 75;
				t[47] = 76;
				t[48] = 77;
				t[49] = 78;
				t[50] = 79;
				t[51] = 80;
				t[52] = 81;
				t[53] = 82;
				t[54] = 83;
				t[55] = 84;
				t[56] = 85;
				t[57] = 86;
				t[58] = 87;
				t[59] = 88;
				t[60] = 89;
				t[61] = 90;
				t[62] = 91;
				t[63] = 92;
				t[64] = 93;
				t[65] = 94;
				t[66] = 95;
				t[67] = 96;
				t[68] = 97;
				t[69] = 98;
				t[70] = 99;
				t[71] = 100;
				t[72] = 101;
				t[73] = 102;
				t[74] = 103;
				t[75] = 104;
				t[76] = 105;
				t[77] = 106;
				t[78] = 107;
				t[79] = 108;
				t[80] = 109;
				t[81] = 110;
				t[82] = 111;
				t[83] = 112;
				t[84] = 113;
				t[85] = 114;
				t[86] = 115;
				t[87] = 116;
				t[88] = 117;
				t[89] = 118;
				t[90] = 119;
				t[91] = 120;
				t[92] = 121;
				t[93] = 122;
				t[94] = 123;
				t[95] = 124;
				t[96] = 125;
				t[97] = 126;
				t[98] = 196;
				t[99] = 197;
				t[100] = 199;
				t[101] = 201;
				t[102] = 209;
				t[103] = 214;
				t[104] = 220;
				t[105] = 225;
				t[106] = 224;
				t[107] = 226;
				t[108] = 228;
				t[109] = 227;
				t[110] = 229;
				t[111] = 231;
				t[112] = 233;
				t[113] = 232;
				t[114] = 234;
				t[115] = 235;
				t[116] = 237;
				t[117] = 236;
				t[118] = 238;
				t[119] = 239;
				t[120] = 241;
				t[121] = 243;
				t[122] = 242;
				t[123] = 244;
				t[124] = 246;
				t[125] = 245;
				t[126] = 250;
				t[127] = 249;
				t[128] = 251;
				t[129] = 252;
				t[130] = 8224;
				t[131] = 176;
				t[132] = 162;
				t[133] = 163;
				t[134] = 167;
				t[135] = 8226;
				t[136] = 182;
				t[137] = 223;
				t[138] = 174;
				t[139] = 169;
				t[140] = 8482;
				t[141] = 180;
				t[142] = 168;
				t[143] = 8800;
				t[144] = 198;
				t[145] = 216;
				t[146] = 8734;
				t[147] = 177;
				t[148] = 8804;
				t[149] = 8805;
				t[150] = 165;
				t[151] = 181;
				t[152] = 8706;
				t[153] = 8721;
				t[154] = 8719;
				t[156] = 8747;
				t[157] = 170;
				t[158] = 186;
				t[159] = 8486;
				t[160] = 230;
				t[161] = 248;
				t[162] = 191;
				t[163] = 161;
				t[164] = 172;
				t[165] = 8730;
				t[166] = 402;
				t[167] = 8776;
				t[168] = 8710;
				t[169] = 171;
				t[170] = 187;
				t[171] = 8230;
				t[210] = 218;
				t[223] = 711;
				t[224] = 321;
				t[225] = 322;
				t[227] = 353;
				t[229] = 382;
				t[234] = 253;
				t[252] = 263;
				t[253] = 268;
				t[254] = 269;
				t[258] = 258;
				t[260] = 260;
				t[261] = 261;
				t[265] = 280;
				t[266] = 281;
				t[268] = 283;
				t[269] = 313;
				t[275] = 323;
				t[276] = 324;
				t[278] = 328;
				t[284] = 345;
				t[285] = 346;
				t[286] = 347;
				t[292] = 367;
				t[295] = 377;
				t[296] = 378;
				t[298] = 380;
				t[305] = 963;
				t[306] = 964;
				t[307] = 966;
				t[308] = 8215;
				t[309] = 8252;
				t[310] = 8319;
				t[311] = 8359;
				t[312] = 8592;
				t[313] = 8593;
				t[337] = 9552;
				t[493] = 1039;
				t[494] = 1040;
				t[705] = 1524;
				t[706] = 8362;
				t[710] = 64288;
				t[711] = 64298;
				t[759] = 1617;
				t[761] = 1776;
				t[763] = 1778;
				t[775] = 1652;
				t[777] = 1764;
				t[778] = 1780;
				t[779] = 1781;
				t[780] = 1782;
				t[782] = 771;
				t[783] = 64726;
				t[786] = 8363;
				t[788] = 8532;
				t[790] = 768;
				t[791] = 769;
				t[792] = 768;
				t[795] = 803;
				t[797] = 64336;
				t[798] = 64337;
				t[799] = 64342;
				t[800] = 64343;
				t[801] = 64344;
				t[802] = 64345;
				t[803] = 64362;
				t[804] = 64363;
				t[805] = 64364;
				t[2424] = 7821;
				t[2425] = 7822;
				t[2426] = 7823;
				t[2427] = 7824;
				t[2428] = 7825;
				t[2429] = 7826;
				t[2430] = 7827;
				t[2433] = 7682;
				t[2678] = 8045;
				t[2679] = 8046;
				t[2830] = 1552;
				t[2838] = 686;
				t[2840] = 751;
				t[2842] = 753;
				t[2843] = 754;
				t[2844] = 755;
				t[2846] = 757;
				t[2856] = 767;
				t[2857] = 848;
				t[2858] = 849;
				t[2862] = 853;
				t[2863] = 854;
				t[2864] = 855;
				t[2865] = 861;
				t[2866] = 862;
				t[2906] = 7460;
				t[2908] = 7462;
				t[2909] = 7463;
				t[2910] = 7464;
				t[2912] = 7466;
				t[2913] = 7467;
				t[2914] = 7468;
				t[2916] = 7470;
				t[2917] = 7471;
				t[2918] = 7472;
				t[2920] = 7474;
				t[2921] = 7475;
				t[2922] = 7476;
				t[2924] = 7478;
				t[2925] = 7479;
				t[2926] = 7480;
				t[2928] = 7482;
				t[2929] = 7483;
				t[2930] = 7484;
				t[2932] = 7486;
				t[2933] = 7487;
				t[2934] = 7488;
				t[2936] = 7490;
				t[2937] = 7491;
				t[2938] = 7492;
				t[2940] = 7494;
				t[2941] = 7495;
				t[2942] = 7496;
				t[2944] = 7498;
				t[2946] = 7500;
				t[2948] = 7502;
				t[2950] = 7504;
				t[2951] = 7505;
				t[2952] = 7506;
				t[2954] = 7508;
				t[2955] = 7509;
				t[2956] = 7510;
				t[2958] = 7512;
				t[2959] = 7513;
				t[2960] = 7514;
				t[2962] = 7516;
				t[2963] = 7517;
				t[2964] = 7518;
				t[2966] = 7520;
				t[2967] = 7521;
				t[2968] = 7522;
				t[2970] = 7524;
				t[2971] = 7525;
				t[2972] = 7526;
				t[2974] = 7528;
				t[2975] = 7529;
				t[2976] = 7530;
				t[2978] = 1537;
				t[2979] = 1538;
				t[2980] = 1539;
				t[2982] = 1549;
				t[2983] = 1551;
				t[2984] = 1552;
				t[2986] = 1554;
				t[2987] = 1555;
				t[2988] = 1556;
				t[2990] = 1623;
				t[2991] = 1624;
				t[2995] = 1775;
				t[2999] = 1791;
				t[3002] = 64290;
				t[3003] = 64291;
				t[3004] = 64292;
				t[3006] = 64294;
				t[3007] = 64295;
				t[3008] = 64296;
				t[3011] = 1900;
				t[3014] = 8223;
				t[3015] = 8244;
				t[3017] = 7532;
				t[3018] = 7533;
				t[3019] = 7534;
				t[3075] = 7590;
				t[3076] = 7591;
				t[3079] = 7594;
				t[3080] = 7595;
				t[3083] = 7598;
				t[3084] = 7599;
				t[3087] = 7602;
				t[3088] = 7603;
				t[3091] = 7606;
				t[3092] = 7607;
				t[3095] = 7610;
				t[3096] = 7611;
				t[3099] = 7614;
				t[3100] = 7615;
				t[3103] = 7618;
				t[3104] = 7619;
				t[3107] = 8337;
				t[3108] = 8338;
				t[3116] = 1884;
				t[3119] = 1885;
				t[3120] = 1885;
				t[3123] = 1886;
				t[3124] = 1886;
				t[3127] = 1887;
				t[3128] = 1887;
				t[3131] = 1888;
				t[3132] = 1888;
				t[3135] = 1889;
				t[3136] = 1889;
				t[3139] = 1890;
				t[3140] = 1890;
				t[3143] = 1891;
				t[3144] = 1891;
				t[3147] = 1892;
				t[3148] = 1892;
				t[3153] = 580;
				t[3154] = 581;
				t[3157] = 584;
				t[3158] = 585;
				t[3161] = 588;
				t[3162] = 589;
				t[3165] = 891;
				t[3166] = 892;
				t[3169] = 1274;
				t[3170] = 1275;
				t[3173] = 1278;
				t[3174] = 1279;
				t[3181] = 7622;
				t[3182] = 7623;
				t[3282] = 11799;
				t[3316] = 578;
				t[3379] = 42785;
				t[3393] = 1159;
				t[3416] = 8377;
			});
			var getSupplementalGlyphMapForArialBlack = getLookupTableFactory(function (t) {
				t[227] = 322;
				t[264] = 261;
				t[291] = 346;
			});
			exports.getStdFontMap = getStdFontMap;
			exports.getNonStdFontMap = getNonStdFontMap;
			exports.getSerifFonts = getSerifFonts;
			exports.getSymbolsFonts = getSymbolsFonts;
			exports.getGlyphMapForStandardFonts = getGlyphMapForStandardFonts;
			exports.getSupplementalGlyphMapForArialBlack = getSupplementalGlyphMapForArialBlack;

			/***/
		}),
/* 18 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var getLookupTableFactory = sharedUtil.getLookupTableFactory;
			var getSpecialPUASymbols = getLookupTableFactory(function (t) {
				t[63721] = 0x00A9;
				t[63193] = 0x00A9;
				t[63720] = 0x00AE;
				t[63194] = 0x00AE;
				t[63722] = 0x2122;
				t[63195] = 0x2122;
				t[63729] = 0x23A7;
				t[63730] = 0x23A8;
				t[63731] = 0x23A9;
				t[63740] = 0x23AB;
				t[63741] = 0x23AC;
				t[63742] = 0x23AD;
				t[63726] = 0x23A1;
				t[63727] = 0x23A2;
				t[63728] = 0x23A3;
				t[63737] = 0x23A4;
				t[63738] = 0x23A5;
				t[63739] = 0x23A6;
				t[63723] = 0x239B;
				t[63724] = 0x239C;
				t[63725] = 0x239D;
				t[63734] = 0x239E;
				t[63735] = 0x239F;
				t[63736] = 0x23A0;
			});
			function mapSpecialUnicodeValues(code) {
				if (code >= 0xFFF0 && code <= 0xFFFF) {
					return 0;
				} else if (code >= 0xF600 && code <= 0xF8FF) {
					return getSpecialPUASymbols()[code] || code;
				}
				return code;
			}
			function getUnicodeForGlyph(name, glyphsUnicodeMap) {
				var unicode = glyphsUnicodeMap[name];
				if (unicode !== undefined) {
					return unicode;
				}
				if (!name) {
					return -1;
				}
				if (name[0] === 'u') {
					var nameLen = name.length, hexStr;
					if (nameLen === 7 && name[1] === 'n' && name[2] === 'i') {
						hexStr = name.substr(3);
					} else if (nameLen >= 5 && nameLen <= 7) {
						hexStr = name.substr(1);
					} else {
						return -1;
					}
					if (hexStr === hexStr.toUpperCase()) {
						unicode = parseInt(hexStr, 16);
						if (unicode >= 0) {
							return unicode;
						}
					}
				}
				return -1;
			}
			var UnicodeRanges = [
				{
					'begin': 0x0000,
					'end': 0x007F
				},
				{
					'begin': 0x0080,
					'end': 0x00FF
				},
				{
					'begin': 0x0100,
					'end': 0x017F
				},
				{
					'begin': 0x0180,
					'end': 0x024F
				},
				{
					'begin': 0x0250,
					'end': 0x02AF
				},
				{
					'begin': 0x02B0,
					'end': 0x02FF
				},
				{
					'begin': 0x0300,
					'end': 0x036F
				},
				{
					'begin': 0x0370,
					'end': 0x03FF
				},
				{
					'begin': 0x2C80,
					'end': 0x2CFF
				},
				{
					'begin': 0x0400,
					'end': 0x04FF
				},
				{
					'begin': 0x0530,
					'end': 0x058F
				},
				{
					'begin': 0x0590,
					'end': 0x05FF
				},
				{
					'begin': 0xA500,
					'end': 0xA63F
				},
				{
					'begin': 0x0600,
					'end': 0x06FF
				},
				{
					'begin': 0x07C0,
					'end': 0x07FF
				},
				{
					'begin': 0x0900,
					'end': 0x097F
				},
				{
					'begin': 0x0980,
					'end': 0x09FF
				},
				{
					'begin': 0x0A00,
					'end': 0x0A7F
				},
				{
					'begin': 0x0A80,
					'end': 0x0AFF
				},
				{
					'begin': 0x0B00,
					'end': 0x0B7F
				},
				{
					'begin': 0x0B80,
					'end': 0x0BFF
				},
				{
					'begin': 0x0C00,
					'end': 0x0C7F
				},
				{
					'begin': 0x0C80,
					'end': 0x0CFF
				},
				{
					'begin': 0x0D00,
					'end': 0x0D7F
				},
				{
					'begin': 0x0E00,
					'end': 0x0E7F
				},
				{
					'begin': 0x0E80,
					'end': 0x0EFF
				},
				{
					'begin': 0x10A0,
					'end': 0x10FF
				},
				{
					'begin': 0x1B00,
					'end': 0x1B7F
				},
				{
					'begin': 0x1100,
					'end': 0x11FF
				},
				{
					'begin': 0x1E00,
					'end': 0x1EFF
				},
				{
					'begin': 0x1F00,
					'end': 0x1FFF
				},
				{
					'begin': 0x2000,
					'end': 0x206F
				},
				{
					'begin': 0x2070,
					'end': 0x209F
				},
				{
					'begin': 0x20A0,
					'end': 0x20CF
				},
				{
					'begin': 0x20D0,
					'end': 0x20FF
				},
				{
					'begin': 0x2100,
					'end': 0x214F
				},
				{
					'begin': 0x2150,
					'end': 0x218F
				},
				{
					'begin': 0x2190,
					'end': 0x21FF
				},
				{
					'begin': 0x2200,
					'end': 0x22FF
				},
				{
					'begin': 0x2300,
					'end': 0x23FF
				},
				{
					'begin': 0x2400,
					'end': 0x243F
				},
				{
					'begin': 0x2440,
					'end': 0x245F
				},
				{
					'begin': 0x2460,
					'end': 0x24FF
				},
				{
					'begin': 0x2500,
					'end': 0x257F
				},
				{
					'begin': 0x2580,
					'end': 0x259F
				},
				{
					'begin': 0x25A0,
					'end': 0x25FF
				},
				{
					'begin': 0x2600,
					'end': 0x26FF
				},
				{
					'begin': 0x2700,
					'end': 0x27BF
				},
				{
					'begin': 0x3000,
					'end': 0x303F
				},
				{
					'begin': 0x3040,
					'end': 0x309F
				},
				{
					'begin': 0x30A0,
					'end': 0x30FF
				},
				{
					'begin': 0x3100,
					'end': 0x312F
				},
				{
					'begin': 0x3130,
					'end': 0x318F
				},
				{
					'begin': 0xA840,
					'end': 0xA87F
				},
				{
					'begin': 0x3200,
					'end': 0x32FF
				},
				{
					'begin': 0x3300,
					'end': 0x33FF
				},
				{
					'begin': 0xAC00,
					'end': 0xD7AF
				},
				{
					'begin': 0xD800,
					'end': 0xDFFF
				},
				{
					'begin': 0x10900,
					'end': 0x1091F
				},
				{
					'begin': 0x4E00,
					'end': 0x9FFF
				},
				{
					'begin': 0xE000,
					'end': 0xF8FF
				},
				{
					'begin': 0x31C0,
					'end': 0x31EF
				},
				{
					'begin': 0xFB00,
					'end': 0xFB4F
				},
				{
					'begin': 0xFB50,
					'end': 0xFDFF
				},
				{
					'begin': 0xFE20,
					'end': 0xFE2F
				},
				{
					'begin': 0xFE10,
					'end': 0xFE1F
				},
				{
					'begin': 0xFE50,
					'end': 0xFE6F
				},
				{
					'begin': 0xFE70,
					'end': 0xFEFF
				},
				{
					'begin': 0xFF00,
					'end': 0xFFEF
				},
				{
					'begin': 0xFFF0,
					'end': 0xFFFF
				},
				{
					'begin': 0x0F00,
					'end': 0x0FFF
				},
				{
					'begin': 0x0700,
					'end': 0x074F
				},
				{
					'begin': 0x0780,
					'end': 0x07BF
				},
				{
					'begin': 0x0D80,
					'end': 0x0DFF
				},
				{
					'begin': 0x1000,
					'end': 0x109F
				},
				{
					'begin': 0x1200,
					'end': 0x137F
				},
				{
					'begin': 0x13A0,
					'end': 0x13FF
				},
				{
					'begin': 0x1400,
					'end': 0x167F
				},
				{
					'begin': 0x1680,
					'end': 0x169F
				},
				{
					'begin': 0x16A0,
					'end': 0x16FF
				},
				{
					'begin': 0x1780,
					'end': 0x17FF
				},
				{
					'begin': 0x1800,
					'end': 0x18AF
				},
				{
					'begin': 0x2800,
					'end': 0x28FF
				},
				{
					'begin': 0xA000,
					'end': 0xA48F
				},
				{
					'begin': 0x1700,
					'end': 0x171F
				},
				{
					'begin': 0x10300,
					'end': 0x1032F
				},
				{
					'begin': 0x10330,
					'end': 0x1034F
				},
				{
					'begin': 0x10400,
					'end': 0x1044F
				},
				{
					'begin': 0x1D000,
					'end': 0x1D0FF
				},
				{
					'begin': 0x1D400,
					'end': 0x1D7FF
				},
				{
					'begin': 0xFF000,
					'end': 0xFFFFD
				},
				{
					'begin': 0xFE00,
					'end': 0xFE0F
				},
				{
					'begin': 0xE0000,
					'end': 0xE007F
				},
				{
					'begin': 0x1900,
					'end': 0x194F
				},
				{
					'begin': 0x1950,
					'end': 0x197F
				},
				{
					'begin': 0x1980,
					'end': 0x19DF
				},
				{
					'begin': 0x1A00,
					'end': 0x1A1F
				},
				{
					'begin': 0x2C00,
					'end': 0x2C5F
				},
				{
					'begin': 0x2D30,
					'end': 0x2D7F
				},
				{
					'begin': 0x4DC0,
					'end': 0x4DFF
				},
				{
					'begin': 0xA800,
					'end': 0xA82F
				},
				{
					'begin': 0x10000,
					'end': 0x1007F
				},
				{
					'begin': 0x10140,
					'end': 0x1018F
				},
				{
					'begin': 0x10380,
					'end': 0x1039F
				},
				{
					'begin': 0x103A0,
					'end': 0x103DF
				},
				{
					'begin': 0x10450,
					'end': 0x1047F
				},
				{
					'begin': 0x10480,
					'end': 0x104AF
				},
				{
					'begin': 0x10800,
					'end': 0x1083F
				},
				{
					'begin': 0x10A00,
					'end': 0x10A5F
				},
				{
					'begin': 0x1D300,
					'end': 0x1D35F
				},
				{
					'begin': 0x12000,
					'end': 0x123FF
				},
				{
					'begin': 0x1D360,
					'end': 0x1D37F
				},
				{
					'begin': 0x1B80,
					'end': 0x1BBF
				},
				{
					'begin': 0x1C00,
					'end': 0x1C4F
				},
				{
					'begin': 0x1C50,
					'end': 0x1C7F
				},
				{
					'begin': 0xA880,
					'end': 0xA8DF
				},
				{
					'begin': 0xA900,
					'end': 0xA92F
				},
				{
					'begin': 0xA930,
					'end': 0xA95F
				},
				{
					'begin': 0xAA00,
					'end': 0xAA5F
				},
				{
					'begin': 0x10190,
					'end': 0x101CF
				},
				{
					'begin': 0x101D0,
					'end': 0x101FF
				},
				{
					'begin': 0x102A0,
					'end': 0x102DF
				},
				{
					'begin': 0x1F030,
					'end': 0x1F09F
				}
			];
			function getUnicodeRangeFor(value) {
				for (var i = 0, ii = UnicodeRanges.length; i < ii; i++) {
					var range = UnicodeRanges[i];
					if (value >= range.begin && value < range.end) {
						return i;
					}
				}
				return -1;
			}
			function isRTLRangeFor(value) {
				var range = UnicodeRanges[13];
				if (value >= range.begin && value < range.end) {
					return true;
				}
				range = UnicodeRanges[11];
				if (value >= range.begin && value < range.end) {
					return true;
				}
				return false;
			}
			var getNormalizedUnicodes = getLookupTableFactory(function (t) {
				t['\u00A8'] = '\u0020\u0308';
				t['\u00AF'] = '\u0020\u0304';
				t['\u00B4'] = '\u0020\u0301';
				t['\u00B5'] = '\u03BC';
				t['\u00B8'] = '\u0020\u0327';
				t['\u0132'] = '\u0049\u004A';
				t['\u0133'] = '\u0069\u006A';
				t['\u013F'] = '\u004C\u00B7';
				t['\u0140'] = '\u006C\u00B7';
				t['\u0149'] = '\u02BC\u006E';
				t['\u017F'] = '\u0073';
				t['\u01C4'] = '\u0044\u017D';
				t['\u01C5'] = '\u0044\u017E';
				t['\u01C6'] = '\u0064\u017E';
				t['\u01C7'] = '\u004C\u004A';
				t['\u01C8'] = '\u004C\u006A';
				t['\u01C9'] = '\u006C\u006A';
				t['\u01CA'] = '\u004E\u004A';
				t['\u01CB'] = '\u004E\u006A';
				t['\u01CC'] = '\u006E\u006A';
				t['\u01F1'] = '\u0044\u005A';
				t['\u01F2'] = '\u0044\u007A';
				t['\u01F3'] = '\u0064\u007A';
				t['\u02D8'] = '\u0020\u0306';
				t['\u02D9'] = '\u0020\u0307';
				t['\u02DA'] = '\u0020\u030A';
				t['\u02DB'] = '\u0020\u0328';
				t['\u02DC'] = '\u0020\u0303';
				t['\u02DD'] = '\u0020\u030B';
				t['\u037A'] = '\u0020\u0345';
				t['\u0384'] = '\u0020\u0301';
				t['\u03D0'] = '\u03B2';
				t['\u03D1'] = '\u03B8';
				t['\u03D2'] = '\u03A5';
				t['\u03D5'] = '\u03C6';
				t['\u03D6'] = '\u03C0';
				t['\u03F0'] = '\u03BA';
				t['\u03F1'] = '\u03C1';
				t['\u03F2'] = '\u03C2';
				t['\u03F4'] = '\u0398';
				t['\u03F5'] = '\u03B5';
				t['\u03F9'] = '\u03A3';
				t['\u0587'] = '\u0565\u0582';
				t['\u0675'] = '\u0627\u0674';
				t['\u0676'] = '\u0648\u0674';
				t['\u0677'] = '\u06C7\u0674';
				t['\u0678'] = '\u064A\u0674';
				t['\u0E33'] = '\u0E4D\u0E32';
				t['\u0EB3'] = '\u0ECD\u0EB2';
				t['\u0EDC'] = '\u0EAB\u0E99';
				t['\u0EDD'] = '\u0EAB\u0EA1';
				t['\u0F77'] = '\u0FB2\u0F81';
				t['\u0F79'] = '\u0FB3\u0F81';
				t['\u1E9A'] = '\u0061\u02BE';
				t['\u1FBD'] = '\u0020\u0313';
				t['\u1FBF'] = '\u0020\u0313';
				t['\u1FC0'] = '\u0020\u0342';
				t['\u1FFE'] = '\u0020\u0314';
				t['\u2002'] = '\u0020';
				t['\u2003'] = '\u0020';
				t['\u2004'] = '\u0020';
				t['\u2005'] = '\u0020';
				t['\u2006'] = '\u0020';
				t['\u2008'] = '\u0020';
				t['\u2009'] = '\u0020';
				t['\u200A'] = '\u0020';
				t['\u2017'] = '\u0020\u0333';
				t['\u2024'] = '\u002E';
				t['\u2025'] = '\u002E\u002E';
				t['\u2026'] = '\u002E\u002E\u002E';
				t['\u2033'] = '\u2032\u2032';
				t['\u2034'] = '\u2032\u2032\u2032';
				t['\u2036'] = '\u2035\u2035';
				t['\u2037'] = '\u2035\u2035\u2035';
				t['\u203C'] = '\u0021\u0021';
				t['\u203E'] = '\u0020\u0305';
				t['\u2047'] = '\u003F\u003F';
				t['\u2048'] = '\u003F\u0021';
				t['\u2049'] = '\u0021\u003F';
				t['\u2057'] = '\u2032\u2032\u2032\u2032';
				t['\u205F'] = '\u0020';
				t['\u20A8'] = '\u0052\u0073';
				t['\u2100'] = '\u0061\u002F\u0063';
				t['\u2101'] = '\u0061\u002F\u0073';
				t['\u2103'] = '\u00B0\u0043';
				t['\u2105'] = '\u0063\u002F\u006F';
				t['\u2106'] = '\u0063\u002F\u0075';
				t['\u2107'] = '\u0190';
				t['\u2109'] = '\u00B0\u0046';
				t['\u2116'] = '\u004E\u006F';
				t['\u2121'] = '\u0054\u0045\u004C';
				t['\u2135'] = '\u05D0';
				t['\u2136'] = '\u05D1';
				t['\u2137'] = '\u05D2';
				t['\u2138'] = '\u05D3';
				t['\u213B'] = '\u0046\u0041\u0058';
				t['\u2160'] = '\u0049';
				t['\u2161'] = '\u0049\u0049';
				t['\u2162'] = '\u0049\u0049\u0049';
				t['\u2163'] = '\u0049\u0056';
				t['\u2164'] = '\u0056';
				t['\u2165'] = '\u0056\u0049';
				t['\u2166'] = '\u0056\u0049\u0049';
				t['\u2167'] = '\u0056\u0049\u0049\u0049';
				t['\u2168'] = '\u0049\u0058';
				t['\u2169'] = '\u0058';
				t['\u216A'] = '\u0058\u0049';
				t['\u216B'] = '\u0058\u0049\u0049';
				t['\u216C'] = '\u004C';
				t['\u216D'] = '\u0043';
				t['\u216E'] = '\u0044';
				t['\u216F'] = '\u004D';
				t['\u2170'] = '\u0069';
				t['\u2171'] = '\u0069\u0069';
				t['\u2172'] = '\u0069\u0069\u0069';
				t['\u2173'] = '\u0069\u0076';
				t['\u2174'] = '\u0076';
				t['\u2175'] = '\u0076\u0069';
				t['\u2176'] = '\u0076\u0069\u0069';
				t['\u2177'] = '\u0076\u0069\u0069\u0069';
				t['\u2178'] = '\u0069\u0078';
				t['\u2179'] = '\u0078';
				t['\u217A'] = '\u0078\u0069';
				t['\u217B'] = '\u0078\u0069\u0069';
				t['\u217C'] = '\u006C';
				t['\u217D'] = '\u0063';
				t['\u217E'] = '\u0064';
				t['\u217F'] = '\u006D';
				t['\u222C'] = '\u222B\u222B';
				t['\u222D'] = '\u222B\u222B\u222B';
				t['\u222F'] = '\u222E\u222E';
				t['\u2230'] = '\u222E\u222E\u222E';
				t['\u2474'] = '\u0028\u0031\u0029';
				t['\u2475'] = '\u0028\u0032\u0029';
				t['\u2476'] = '\u0028\u0033\u0029';
				t['\u2477'] = '\u0028\u0034\u0029';
				t['\u2478'] = '\u0028\u0035\u0029';
				t['\u2479'] = '\u0028\u0036\u0029';
				t['\u247A'] = '\u0028\u0037\u0029';
				t['\u247B'] = '\u0028\u0038\u0029';
				t['\u247C'] = '\u0028\u0039\u0029';
				t['\u247D'] = '\u0028\u0031\u0030\u0029';
				t['\u247E'] = '\u0028\u0031\u0031\u0029';
				t['\u247F'] = '\u0028\u0031\u0032\u0029';
				t['\u2480'] = '\u0028\u0031\u0033\u0029';
				t['\u2481'] = '\u0028\u0031\u0034\u0029';
				t['\u2482'] = '\u0028\u0031\u0035\u0029';
				t['\u2483'] = '\u0028\u0031\u0036\u0029';
				t['\u2484'] = '\u0028\u0031\u0037\u0029';
				t['\u2485'] = '\u0028\u0031\u0038\u0029';
				t['\u2486'] = '\u0028\u0031\u0039\u0029';
				t['\u2487'] = '\u0028\u0032\u0030\u0029';
				t['\u2488'] = '\u0031\u002E';
				t['\u2489'] = '\u0032\u002E';
				t['\u248A'] = '\u0033\u002E';
				t['\u248B'] = '\u0034\u002E';
				t['\u248C'] = '\u0035\u002E';
				t['\u248D'] = '\u0036\u002E';
				t['\u248E'] = '\u0037\u002E';
				t['\u248F'] = '\u0038\u002E';
				t['\u2490'] = '\u0039\u002E';
				t['\u2491'] = '\u0031\u0030\u002E';
				t['\u2492'] = '\u0031\u0031\u002E';
				t['\u2493'] = '\u0031\u0032\u002E';
				t['\u2494'] = '\u0031\u0033\u002E';
				t['\u2495'] = '\u0031\u0034\u002E';
				t['\u2496'] = '\u0031\u0035\u002E';
				t['\u2497'] = '\u0031\u0036\u002E';
				t['\u2498'] = '\u0031\u0037\u002E';
				t['\u2499'] = '\u0031\u0038\u002E';
				t['\u249A'] = '\u0031\u0039\u002E';
				t['\u249B'] = '\u0032\u0030\u002E';
				t['\u249C'] = '\u0028\u0061\u0029';
				t['\u249D'] = '\u0028\u0062\u0029';
				t['\u249E'] = '\u0028\u0063\u0029';
				t['\u249F'] = '\u0028\u0064\u0029';
				t['\u24A0'] = '\u0028\u0065\u0029';
				t['\u24A1'] = '\u0028\u0066\u0029';
				t['\u24A2'] = '\u0028\u0067\u0029';
				t['\u24A3'] = '\u0028\u0068\u0029';
				t['\u24A4'] = '\u0028\u0069\u0029';
				t['\u24A5'] = '\u0028\u006A\u0029';
				t['\u24A6'] = '\u0028\u006B\u0029';
				t['\u24A7'] = '\u0028\u006C\u0029';
				t['\u24A8'] = '\u0028\u006D\u0029';
				t['\u24A9'] = '\u0028\u006E\u0029';
				t['\u24AA'] = '\u0028\u006F\u0029';
				t['\u24AB'] = '\u0028\u0070\u0029';
				t['\u24AC'] = '\u0028\u0071\u0029';
				t['\u24AD'] = '\u0028\u0072\u0029';
				t['\u24AE'] = '\u0028\u0073\u0029';
				t['\u24AF'] = '\u0028\u0074\u0029';
				t['\u24B0'] = '\u0028\u0075\u0029';
				t['\u24B1'] = '\u0028\u0076\u0029';
				t['\u24B2'] = '\u0028\u0077\u0029';
				t['\u24B3'] = '\u0028\u0078\u0029';
				t['\u24B4'] = '\u0028\u0079\u0029';
				t['\u24B5'] = '\u0028\u007A\u0029';
				t['\u2A0C'] = '\u222B\u222B\u222B\u222B';
				t['\u2A74'] = '\u003A\u003A\u003D';
				t['\u2A75'] = '\u003D\u003D';
				t['\u2A76'] = '\u003D\u003D\u003D';
				t['\u2E9F'] = '\u6BCD';
				t['\u2EF3'] = '\u9F9F';
				t['\u2F00'] = '\u4E00';
				t['\u2F01'] = '\u4E28';
				t['\u2F02'] = '\u4E36';
				t['\u2F03'] = '\u4E3F';
				t['\u2F04'] = '\u4E59';
				t['\u2F05'] = '\u4E85';
				t['\u2F06'] = '\u4E8C';
				t['\u2F07'] = '\u4EA0';
				t['\u2F08'] = '\u4EBA';
				t['\u2F09'] = '\u513F';
				t['\u2F0A'] = '\u5165';
				t['\u2F0B'] = '\u516B';
				t['\u2F0C'] = '\u5182';
				t['\u2F0D'] = '\u5196';
				t['\u2F0E'] = '\u51AB';
				t['\u2F0F'] = '\u51E0';
				t['\u2F10'] = '\u51F5';
				t['\u2F11'] = '\u5200';
				t['\u2F12'] = '\u529B';
				t['\u2F13'] = '\u52F9';
				t['\u2F14'] = '\u5315';
				t['\u2F15'] = '\u531A';
				t['\u2F16'] = '\u5338';
				t['\u2F17'] = '\u5341';
				t['\u2F18'] = '\u535C';
				t['\u2F19'] = '\u5369';
				t['\u2F1A'] = '\u5382';
				t['\u2F1B'] = '\u53B6';
				t['\u2F1C'] = '\u53C8';
				t['\u2F1D'] = '\u53E3';
				t['\u2F1E'] = '\u56D7';
				t['\u2F1F'] = '\u571F';
				t['\u2F20'] = '\u58EB';
				t['\u2F21'] = '\u5902';
				t['\u2F22'] = '\u590A';
				t['\u2F23'] = '\u5915';
				t['\u2F24'] = '\u5927';
				t['\u2F25'] = '\u5973';
				t['\u2F26'] = '\u5B50';
				t['\u2F27'] = '\u5B80';
				t['\u2F28'] = '\u5BF8';
				t['\u2F29'] = '\u5C0F';
				t['\u2F2A'] = '\u5C22';
				t['\u2F2B'] = '\u5C38';
				t['\u2F2C'] = '\u5C6E';
				t['\u2F2D'] = '\u5C71';
				t['\u2F2E'] = '\u5DDB';
				t['\u2F2F'] = '\u5DE5';
				t['\u2F30'] = '\u5DF1';
				t['\u2F31'] = '\u5DFE';
				t['\u2F32'] = '\u5E72';
				t['\u2F33'] = '\u5E7A';
				t['\u2F34'] = '\u5E7F';
				t['\u2F35'] = '\u5EF4';
				t['\u2F36'] = '\u5EFE';
				t['\u2F37'] = '\u5F0B';
				t['\u2F38'] = '\u5F13';
				t['\u2F39'] = '\u5F50';
				t['\u2F3A'] = '\u5F61';
				t['\u2F3B'] = '\u5F73';
				t['\u2F3C'] = '\u5FC3';
				t['\u2F3D'] = '\u6208';
				t['\u2F3E'] = '\u6236';
				t['\u2F3F'] = '\u624B';
				t['\u2F40'] = '\u652F';
				t['\u2F41'] = '\u6534';
				t['\u2F42'] = '\u6587';
				t['\u2F43'] = '\u6597';
				t['\u2F44'] = '\u65A4';
				t['\u2F45'] = '\u65B9';
				t['\u2F46'] = '\u65E0';
				t['\u2F47'] = '\u65E5';
				t['\u2F48'] = '\u66F0';
				t['\u2F49'] = '\u6708';
				t['\u2F4A'] = '\u6728';
				t['\u2F4B'] = '\u6B20';
				t['\u2F4C'] = '\u6B62';
				t['\u2F4D'] = '\u6B79';
				t['\u2F4E'] = '\u6BB3';
				t['\u2F4F'] = '\u6BCB';
				t['\u2F50'] = '\u6BD4';
				t['\u2F51'] = '\u6BDB';
				t['\u2F52'] = '\u6C0F';
				t['\u2F53'] = '\u6C14';
				t['\u2F54'] = '\u6C34';
				t['\u2F55'] = '\u706B';
				t['\u2F56'] = '\u722A';
				t['\u2F57'] = '\u7236';
				t['\u2F58'] = '\u723B';
				t['\u2F59'] = '\u723F';
				t['\u2F5A'] = '\u7247';
				t['\u2F5B'] = '\u7259';
				t['\u2F5C'] = '\u725B';
				t['\u2F5D'] = '\u72AC';
				t['\u2F5E'] = '\u7384';
				t['\u2F5F'] = '\u7389';
				t['\u2F60'] = '\u74DC';
				t['\u2F61'] = '\u74E6';
				t['\u2F62'] = '\u7518';
				t['\u2F63'] = '\u751F';
				t['\u2F64'] = '\u7528';
				t['\u2F65'] = '\u7530';
				t['\u2F66'] = '\u758B';
				t['\u2F67'] = '\u7592';
				t['\u2F68'] = '\u7676';
				t['\u2F69'] = '\u767D';
				t['\u2F6A'] = '\u76AE';
				t['\u2F6B'] = '\u76BF';
				t['\u2F6C'] = '\u76EE';
				t['\u2F6D'] = '\u77DB';
				t['\u2F6E'] = '\u77E2';
				t['\u2F6F'] = '\u77F3';
				t['\u2F70'] = '\u793A';
				t['\u2F71'] = '\u79B8';
				t['\u2F72'] = '\u79BE';
				t['\u2F73'] = '\u7A74';
				t['\u2F74'] = '\u7ACB';
				t['\u2F75'] = '\u7AF9';
				t['\u2F76'] = '\u7C73';
				t['\u2F77'] = '\u7CF8';
				t['\u2F78'] = '\u7F36';
				t['\u2F79'] = '\u7F51';
				t['\u2F7A'] = '\u7F8A';
				t['\u2F7B'] = '\u7FBD';
				t['\u2F7C'] = '\u8001';
				t['\u2F7D'] = '\u800C';
				t['\u2F7E'] = '\u8012';
				t['\u2F7F'] = '\u8033';
				t['\u2F80'] = '\u807F';
				t['\u2F81'] = '\u8089';
				t['\u2F82'] = '\u81E3';
				t['\u2F83'] = '\u81EA';
				t['\u2F84'] = '\u81F3';
				t['\u2F85'] = '\u81FC';
				t['\u2F86'] = '\u820C';
				t['\u2F87'] = '\u821B';
				t['\u2F88'] = '\u821F';
				t['\u2F89'] = '\u826E';
				t['\u2F8A'] = '\u8272';
				t['\u2F8B'] = '\u8278';
				t['\u2F8C'] = '\u864D';
				t['\u2F8D'] = '\u866B';
				t['\u2F8E'] = '\u8840';
				t['\u2F8F'] = '\u884C';
				t['\u2F90'] = '\u8863';
				t['\u2F91'] = '\u897E';
				t['\u2F92'] = '\u898B';
				t['\u2F93'] = '\u89D2';
				t['\u2F94'] = '\u8A00';
				t['\u2F95'] = '\u8C37';
				t['\u2F96'] = '\u8C46';
				t['\u2F97'] = '\u8C55';
				t['\u2F98'] = '\u8C78';
				t['\u2F99'] = '\u8C9D';
				t['\u2F9A'] = '\u8D64';
				t['\u2F9B'] = '\u8D70';
				t['\u2F9C'] = '\u8DB3';
				t['\u2F9D'] = '\u8EAB';
				t['\u2F9E'] = '\u8ECA';
				t['\u2F9F'] = '\u8F9B';
				t['\u2FA0'] = '\u8FB0';
				t['\u2FA1'] = '\u8FB5';
				t['\u2FA2'] = '\u9091';
				t['\u2FA3'] = '\u9149';
				t['\u2FA4'] = '\u91C6';
				t['\u2FA5'] = '\u91CC';
				t['\u2FA6'] = '\u91D1';
				t['\u2FA7'] = '\u9577';
				t['\u2FA8'] = '\u9580';
				t['\u2FA9'] = '\u961C';
				t['\u2FAA'] = '\u96B6';
				t['\u2FAB'] = '\u96B9';
				t['\u2FAC'] = '\u96E8';
				t['\u2FAD'] = '\u9751';
				t['\u2FAE'] = '\u975E';
				t['\u2FAF'] = '\u9762';
				t['\u2FB0'] = '\u9769';
				t['\u2FB1'] = '\u97CB';
				t['\u2FB2'] = '\u97ED';
				t['\u2FB3'] = '\u97F3';
				t['\u2FB4'] = '\u9801';
				t['\u2FB5'] = '\u98A8';
				t['\u2FB6'] = '\u98DB';
				t['\u2FB7'] = '\u98DF';
				t['\u2FB8'] = '\u9996';
				t['\u2FB9'] = '\u9999';
				t['\u2FBA'] = '\u99AC';
				t['\u2FBB'] = '\u9AA8';
				t['\u2FBC'] = '\u9AD8';
				t['\u2FBD'] = '\u9ADF';
				t['\u2FBE'] = '\u9B25';
				t['\u2FBF'] = '\u9B2F';
				t['\u2FC0'] = '\u9B32';
				t['\u2FC1'] = '\u9B3C';
				t['\u2FC2'] = '\u9B5A';
				t['\u2FC3'] = '\u9CE5';
				t['\u2FC4'] = '\u9E75';
				t['\u2FC5'] = '\u9E7F';
				t['\u2FC6'] = '\u9EA5';
				t['\u2FC7'] = '\u9EBB';
				t['\u2FC8'] = '\u9EC3';
				t['\u2FC9'] = '\u9ECD';
				t['\u2FCA'] = '\u9ED1';
				t['\u2FCB'] = '\u9EF9';
				t['\u2FCC'] = '\u9EFD';
				t['\u2FCD'] = '\u9F0E';
				t['\u2FCE'] = '\u9F13';
				t['\u2FCF'] = '\u9F20';
				t['\u2FD0'] = '\u9F3B';
				t['\u2FD1'] = '\u9F4A';
				t['\u2FD2'] = '\u9F52';
				t['\u2FD3'] = '\u9F8D';
				t['\u2FD4'] = '\u9F9C';
				t['\u2FD5'] = '\u9FA0';
				t['\u3036'] = '\u3012';
				t['\u3038'] = '\u5341';
				t['\u3039'] = '\u5344';
				t['\u303A'] = '\u5345';
				t['\u309B'] = '\u0020\u3099';
				t['\u309C'] = '\u0020\u309A';
				t['\u3131'] = '\u1100';
				t['\u3132'] = '\u1101';
				t['\u3133'] = '\u11AA';
				t['\u3134'] = '\u1102';
				t['\u3135'] = '\u11AC';
				t['\u3136'] = '\u11AD';
				t['\u3137'] = '\u1103';
				t['\u3138'] = '\u1104';
				t['\u3139'] = '\u1105';
				t['\u313A'] = '\u11B0';
				t['\u313B'] = '\u11B1';
				t['\u313C'] = '\u11B2';
				t['\u313D'] = '\u11B3';
				t['\u313E'] = '\u11B4';
				t['\u313F'] = '\u11B5';
				t['\u3140'] = '\u111A';
				t['\u3141'] = '\u1106';
				t['\u3142'] = '\u1107';
				t['\u3143'] = '\u1108';
				t['\u3144'] = '\u1121';
				t['\u3145'] = '\u1109';
				t['\u3146'] = '\u110A';
				t['\u3147'] = '\u110B';
				t['\u3148'] = '\u110C';
				t['\u3149'] = '\u110D';
				t['\u314A'] = '\u110E';
				t['\u314B'] = '\u110F';
				t['\u314C'] = '\u1110';
				t['\u314D'] = '\u1111';
				t['\u314E'] = '\u1112';
				t['\u314F'] = '\u1161';
				t['\u3150'] = '\u1162';
				t['\u3151'] = '\u1163';
				t['\u3152'] = '\u1164';
				t['\u3153'] = '\u1165';
				t['\u3154'] = '\u1166';
				t['\u3155'] = '\u1167';
				t['\u3156'] = '\u1168';
				t['\u3157'] = '\u1169';
				t['\u3158'] = '\u116A';
				t['\u3159'] = '\u116B';
				t['\u315A'] = '\u116C';
				t['\u315B'] = '\u116D';
				t['\u315C'] = '\u116E';
				t['\u315D'] = '\u116F';
				t['\u315E'] = '\u1170';
				t['\u315F'] = '\u1171';
				t['\u3160'] = '\u1172';
				t['\u3161'] = '\u1173';
				t['\u3162'] = '\u1174';
				t['\u3163'] = '\u1175';
				t['\u3164'] = '\u1160';
				t['\u3165'] = '\u1114';
				t['\u3166'] = '\u1115';
				t['\u3167'] = '\u11C7';
				t['\u3168'] = '\u11C8';
				t['\u3169'] = '\u11CC';
				t['\u316A'] = '\u11CE';
				t['\u316B'] = '\u11D3';
				t['\u316C'] = '\u11D7';
				t['\u316D'] = '\u11D9';
				t['\u316E'] = '\u111C';
				t['\u316F'] = '\u11DD';
				t['\u3170'] = '\u11DF';
				t['\u3171'] = '\u111D';
				t['\u3172'] = '\u111E';
				t['\u3173'] = '\u1120';
				t['\u3174'] = '\u1122';
				t['\u3175'] = '\u1123';
				t['\u3176'] = '\u1127';
				t['\u3177'] = '\u1129';
				t['\u3178'] = '\u112B';
				t['\u3179'] = '\u112C';
				t['\u317A'] = '\u112D';
				t['\u317B'] = '\u112E';
				t['\u317C'] = '\u112F';
				t['\u317D'] = '\u1132';
				t['\u317E'] = '\u1136';
				t['\u317F'] = '\u1140';
				t['\u3180'] = '\u1147';
				t['\u3181'] = '\u114C';
				t['\u3182'] = '\u11F1';
				t['\u3183'] = '\u11F2';
				t['\u3184'] = '\u1157';
				t['\u3185'] = '\u1158';
				t['\u3186'] = '\u1159';
				t['\u3187'] = '\u1184';
				t['\u3188'] = '\u1185';
				t['\u3189'] = '\u1188';
				t['\u318A'] = '\u1191';
				t['\u318B'] = '\u1192';
				t['\u318C'] = '\u1194';
				t['\u318D'] = '\u119E';
				t['\u318E'] = '\u11A1';
				t['\u3200'] = '\u0028\u1100\u0029';
				t['\u3201'] = '\u0028\u1102\u0029';
				t['\u3202'] = '\u0028\u1103\u0029';
				t['\u3203'] = '\u0028\u1105\u0029';
				t['\u3204'] = '\u0028\u1106\u0029';
				t['\u3205'] = '\u0028\u1107\u0029';
				t['\u3206'] = '\u0028\u1109\u0029';
				t['\u3207'] = '\u0028\u110B\u0029';
				t['\u3208'] = '\u0028\u110C\u0029';
				t['\u3209'] = '\u0028\u110E\u0029';
				t['\u320A'] = '\u0028\u110F\u0029';
				t['\u320B'] = '\u0028\u1110\u0029';
				t['\u320C'] = '\u0028\u1111\u0029';
				t['\u320D'] = '\u0028\u1112\u0029';
				t['\u320E'] = '\u0028\u1100\u1161\u0029';
				t['\u320F'] = '\u0028\u1102\u1161\u0029';
				t['\u3210'] = '\u0028\u1103\u1161\u0029';
				t['\u3211'] = '\u0028\u1105\u1161\u0029';
				t['\u3212'] = '\u0028\u1106\u1161\u0029';
				t['\u3213'] = '\u0028\u1107\u1161\u0029';
				t['\u3214'] = '\u0028\u1109\u1161\u0029';
				t['\u3215'] = '\u0028\u110B\u1161\u0029';
				t['\u3216'] = '\u0028\u110C\u1161\u0029';
				t['\u3217'] = '\u0028\u110E\u1161\u0029';
				t['\u3218'] = '\u0028\u110F\u1161\u0029';
				t['\u3219'] = '\u0028\u1110\u1161\u0029';
				t['\u321A'] = '\u0028\u1111\u1161\u0029';
				t['\u321B'] = '\u0028\u1112\u1161\u0029';
				t['\u321C'] = '\u0028\u110C\u116E\u0029';
				t['\u321D'] = '\u0028\u110B\u1169\u110C\u1165\u11AB\u0029';
				t['\u321E'] = '\u0028\u110B\u1169\u1112\u116E\u0029';
				t['\u3220'] = '\u0028\u4E00\u0029';
				t['\u3221'] = '\u0028\u4E8C\u0029';
				t['\u3222'] = '\u0028\u4E09\u0029';
				t['\u3223'] = '\u0028\u56DB\u0029';
				t['\u3224'] = '\u0028\u4E94\u0029';
				t['\u3225'] = '\u0028\u516D\u0029';
				t['\u3226'] = '\u0028\u4E03\u0029';
				t['\u3227'] = '\u0028\u516B\u0029';
				t['\u3228'] = '\u0028\u4E5D\u0029';
				t['\u3229'] = '\u0028\u5341\u0029';
				t['\u322A'] = '\u0028\u6708\u0029';
				t['\u322B'] = '\u0028\u706B\u0029';
				t['\u322C'] = '\u0028\u6C34\u0029';
				t['\u322D'] = '\u0028\u6728\u0029';
				t['\u322E'] = '\u0028\u91D1\u0029';
				t['\u322F'] = '\u0028\u571F\u0029';
				t['\u3230'] = '\u0028\u65E5\u0029';
				t['\u3231'] = '\u0028\u682A\u0029';
				t['\u3232'] = '\u0028\u6709\u0029';
				t['\u3233'] = '\u0028\u793E\u0029';
				t['\u3234'] = '\u0028\u540D\u0029';
				t['\u3235'] = '\u0028\u7279\u0029';
				t['\u3236'] = '\u0028\u8CA1\u0029';
				t['\u3237'] = '\u0028\u795D\u0029';
				t['\u3238'] = '\u0028\u52B4\u0029';
				t['\u3239'] = '\u0028\u4EE3\u0029';
				t['\u323A'] = '\u0028\u547C\u0029';
				t['\u323B'] = '\u0028\u5B66\u0029';
				t['\u323C'] = '\u0028\u76E3\u0029';
				t['\u323D'] = '\u0028\u4F01\u0029';
				t['\u323E'] = '\u0028\u8CC7\u0029';
				t['\u323F'] = '\u0028\u5354\u0029';
				t['\u3240'] = '\u0028\u796D\u0029';
				t['\u3241'] = '\u0028\u4F11\u0029';
				t['\u3242'] = '\u0028\u81EA\u0029';
				t['\u3243'] = '\u0028\u81F3\u0029';
				t['\u32C0'] = '\u0031\u6708';
				t['\u32C1'] = '\u0032\u6708';
				t['\u32C2'] = '\u0033\u6708';
				t['\u32C3'] = '\u0034\u6708';
				t['\u32C4'] = '\u0035\u6708';
				t['\u32C5'] = '\u0036\u6708';
				t['\u32C6'] = '\u0037\u6708';
				t['\u32C7'] = '\u0038\u6708';
				t['\u32C8'] = '\u0039\u6708';
				t['\u32C9'] = '\u0031\u0030\u6708';
				t['\u32CA'] = '\u0031\u0031\u6708';
				t['\u32CB'] = '\u0031\u0032\u6708';
				t['\u3358'] = '\u0030\u70B9';
				t['\u3359'] = '\u0031\u70B9';
				t['\u335A'] = '\u0032\u70B9';
				t['\u335B'] = '\u0033\u70B9';
				t['\u335C'] = '\u0034\u70B9';
				t['\u335D'] = '\u0035\u70B9';
				t['\u335E'] = '\u0036\u70B9';
				t['\u335F'] = '\u0037\u70B9';
				t['\u3360'] = '\u0038\u70B9';
				t['\u3361'] = '\u0039\u70B9';
				t['\u3362'] = '\u0031\u0030\u70B9';
				t['\u3363'] = '\u0031\u0031\u70B9';
				t['\u3364'] = '\u0031\u0032\u70B9';
				t['\u3365'] = '\u0031\u0033\u70B9';
				t['\u3366'] = '\u0031\u0034\u70B9';
				t['\u3367'] = '\u0031\u0035\u70B9';
				t['\u3368'] = '\u0031\u0036\u70B9';
				t['\u3369'] = '\u0031\u0037\u70B9';
				t['\u336A'] = '\u0031\u0038\u70B9';
				t['\u336B'] = '\u0031\u0039\u70B9';
				t['\u336C'] = '\u0032\u0030\u70B9';
				t['\u336D'] = '\u0032\u0031\u70B9';
				t['\u336E'] = '\u0032\u0032\u70B9';
				t['\u336F'] = '\u0032\u0033\u70B9';
				t['\u3370'] = '\u0032\u0034\u70B9';
				t['\u33E0'] = '\u0031\u65E5';
				t['\u33E1'] = '\u0032\u65E5';
				t['\u33E2'] = '\u0033\u65E5';
				t['\u33E3'] = '\u0034\u65E5';
				t['\u33E4'] = '\u0035\u65E5';
				t['\u33E5'] = '\u0036\u65E5';
				t['\u33E6'] = '\u0037\u65E5';
				t['\u33E7'] = '\u0038\u65E5';
				t['\u33E8'] = '\u0039\u65E5';
				t['\u33E9'] = '\u0031\u0030\u65E5';
				t['\u33EA'] = '\u0031\u0031\u65E5';
				t['\u33EB'] = '\u0031\u0032\u65E5';
				t['\u33EC'] = '\u0031\u0033\u65E5';
				t['\u33ED'] = '\u0031\u0034\u65E5';
				t['\u33EE'] = '\u0031\u0035\u65E5';
				t['\u33EF'] = '\u0031\u0036\u65E5';
				t['\u33F0'] = '\u0031\u0037\u65E5';
				t['\u33F1'] = '\u0031\u0038\u65E5';
				t['\u33F2'] = '\u0031\u0039\u65E5';
				t['\u33F3'] = '\u0032\u0030\u65E5';
				t['\u33F4'] = '\u0032\u0031\u65E5';
				t['\u33F5'] = '\u0032\u0032\u65E5';
				t['\u33F6'] = '\u0032\u0033\u65E5';
				t['\u33F7'] = '\u0032\u0034\u65E5';
				t['\u33F8'] = '\u0032\u0035\u65E5';
				t['\u33F9'] = '\u0032\u0036\u65E5';
				t['\u33FA'] = '\u0032\u0037\u65E5';
				t['\u33FB'] = '\u0032\u0038\u65E5';
				t['\u33FC'] = '\u0032\u0039\u65E5';
				t['\u33FD'] = '\u0033\u0030\u65E5';
				t['\u33FE'] = '\u0033\u0031\u65E5';
				t['\uFB00'] = '\u0066\u0066';
				t['\uFB01'] = '\u0066\u0069';
				t['\uFB02'] = '\u0066\u006C';
				t['\uFB03'] = '\u0066\u0066\u0069';
				t['\uFB04'] = '\u0066\u0066\u006C';
				t['\uFB05'] = '\u017F\u0074';
				t['\uFB06'] = '\u0073\u0074';
				t['\uFB13'] = '\u0574\u0576';
				t['\uFB14'] = '\u0574\u0565';
				t['\uFB15'] = '\u0574\u056B';
				t['\uFB16'] = '\u057E\u0576';
				t['\uFB17'] = '\u0574\u056D';
				t['\uFB4F'] = '\u05D0\u05DC';
				t['\uFB50'] = '\u0671';
				t['\uFB51'] = '\u0671';
				t['\uFB52'] = '\u067B';
				t['\uFB53'] = '\u067B';
				t['\uFB54'] = '\u067B';
				t['\uFB55'] = '\u067B';
				t['\uFB56'] = '\u067E';
				t['\uFB57'] = '\u067E';
				t['\uFB58'] = '\u067E';
				t['\uFB59'] = '\u067E';
				t['\uFB5A'] = '\u0680';
				t['\uFB5B'] = '\u0680';
				t['\uFB5C'] = '\u0680';
				t['\uFB5D'] = '\u0680';
				t['\uFB5E'] = '\u067A';
				t['\uFB5F'] = '\u067A';
				t['\uFB60'] = '\u067A';
				t['\uFB61'] = '\u067A';
				t['\uFB62'] = '\u067F';
				t['\uFB63'] = '\u067F';
				t['\uFB64'] = '\u067F';
				t['\uFB65'] = '\u067F';
				t['\uFB66'] = '\u0679';
				t['\uFB67'] = '\u0679';
				t['\uFB68'] = '\u0679';
				t['\uFB69'] = '\u0679';
				t['\uFB6A'] = '\u06A4';
				t['\uFB6B'] = '\u06A4';
				t['\uFB6C'] = '\u06A4';
				t['\uFB6D'] = '\u06A4';
				t['\uFB6E'] = '\u06A6';
				t['\uFB6F'] = '\u06A6';
				t['\uFB70'] = '\u06A6';
				t['\uFB71'] = '\u06A6';
				t['\uFB72'] = '\u0684';
				t['\uFB73'] = '\u0684';
				t['\uFB74'] = '\u0684';
				t['\uFB75'] = '\u0684';
				t['\uFB76'] = '\u0683';
				t['\uFB77'] = '\u0683';
				t['\uFB78'] = '\u0683';
				t['\uFB79'] = '\u0683';
				t['\uFB7A'] = '\u0686';
				t['\uFB7B'] = '\u0686';
				t['\uFB7C'] = '\u0686';
				t['\uFB7D'] = '\u0686';
				t['\uFB7E'] = '\u0687';
				t['\uFB7F'] = '\u0687';
				t['\uFB80'] = '\u0687';
				t['\uFB81'] = '\u0687';
				t['\uFB82'] = '\u068D';
				t['\uFB83'] = '\u068D';
				t['\uFB84'] = '\u068C';
				t['\uFB85'] = '\u068C';
				t['\uFB86'] = '\u068E';
				t['\uFB87'] = '\u068E';
				t['\uFB88'] = '\u0688';
				t['\uFB89'] = '\u0688';
				t['\uFB8A'] = '\u0698';
				t['\uFB8B'] = '\u0698';
				t['\uFB8C'] = '\u0691';
				t['\uFB8D'] = '\u0691';
				t['\uFB8E'] = '\u06A9';
				t['\uFB8F'] = '\u06A9';
				t['\uFB90'] = '\u06A9';
				t['\uFB91'] = '\u06A9';
				t['\uFB92'] = '\u06AF';
				t['\uFB93'] = '\u06AF';
				t['\uFB94'] = '\u06AF';
				t['\uFB95'] = '\u06AF';
				t['\uFB96'] = '\u06B3';
				t['\uFB97'] = '\u06B3';
				t['\uFB98'] = '\u06B3';
				t['\uFB99'] = '\u06B3';
				t['\uFB9A'] = '\u06B1';
				t['\uFB9B'] = '\u06B1';
				t['\uFB9C'] = '\u06B1';
				t['\uFB9D'] = '\u06B1';
				t['\uFB9E'] = '\u06BA';
				t['\uFB9F'] = '\u06BA';
				t['\uFBA0'] = '\u06BB';
				t['\uFBA1'] = '\u06BB';
				t['\uFBA2'] = '\u06BB';
				t['\uFBA3'] = '\u06BB';
				t['\uFBA4'] = '\u06C0';
				t['\uFBA5'] = '\u06C0';
				t['\uFBA6'] = '\u06C1';
				t['\uFBA7'] = '\u06C1';
				t['\uFBA8'] = '\u06C1';
				t['\uFBA9'] = '\u06C1';
				t['\uFBAA'] = '\u06BE';
				t['\uFBAB'] = '\u06BE';
				t['\uFBAC'] = '\u06BE';
				t['\uFBAD'] = '\u06BE';
				t['\uFBAE'] = '\u06D2';
				t['\uFBAF'] = '\u06D2';
				t['\uFBB0'] = '\u06D3';
				t['\uFBB1'] = '\u06D3';
				t['\uFBD3'] = '\u06AD';
				t['\uFBD4'] = '\u06AD';
				t['\uFBD5'] = '\u06AD';
				t['\uFBD6'] = '\u06AD';
				t['\uFBD7'] = '\u06C7';
				t['\uFBD8'] = '\u06C7';
				t['\uFBD9'] = '\u06C6';
				t['\uFBDA'] = '\u06C6';
				t['\uFBDB'] = '\u06C8';
				t['\uFBDC'] = '\u06C8';
				t['\uFBDD'] = '\u0677';
				t['\uFBDE'] = '\u06CB';
				t['\uFBDF'] = '\u06CB';
				t['\uFBE0'] = '\u06C5';
				t['\uFBE1'] = '\u06C5';
				t['\uFBE2'] = '\u06C9';
				t['\uFBE3'] = '\u06C9';
				t['\uFBE4'] = '\u06D0';
				t['\uFBE5'] = '\u06D0';
				t['\uFBE6'] = '\u06D0';
				t['\uFBE7'] = '\u06D0';
				t['\uFBE8'] = '\u0649';
				t['\uFBE9'] = '\u0649';
				t['\uFBEA'] = '\u0626\u0627';
				t['\uFBEB'] = '\u0626\u0627';
				t['\uFBEC'] = '\u0626\u06D5';
				t['\uFBED'] = '\u0626\u06D5';
				t['\uFBEE'] = '\u0626\u0648';
				t['\uFBEF'] = '\u0626\u0648';
				t['\uFBF0'] = '\u0626\u06C7';
				t['\uFBF1'] = '\u0626\u06C7';
				t['\uFBF2'] = '\u0626\u06C6';
				t['\uFBF3'] = '\u0626\u06C6';
				t['\uFBF4'] = '\u0626\u06C8';
				t['\uFBF5'] = '\u0626\u06C8';
				t['\uFBF6'] = '\u0626\u06D0';
				t['\uFBF7'] = '\u0626\u06D0';
				t['\uFBF8'] = '\u0626\u06D0';
				t['\uFBF9'] = '\u0626\u0649';
				t['\uFBFA'] = '\u0626\u0649';
				t['\uFBFB'] = '\u0626\u0649';
				t['\uFBFC'] = '\u06CC';
				t['\uFBFD'] = '\u06CC';
				t['\uFBFE'] = '\u06CC';
				t['\uFBFF'] = '\u06CC';
				t['\uFC00'] = '\u0626\u062C';
				t['\uFC01'] = '\u0626\u062D';
				t['\uFC02'] = '\u0626\u0645';
				t['\uFC03'] = '\u0626\u0649';
				t['\uFC04'] = '\u0626\u064A';
				t['\uFC05'] = '\u0628\u062C';
				t['\uFC06'] = '\u0628\u062D';
				t['\uFC07'] = '\u0628\u062E';
				t['\uFC08'] = '\u0628\u0645';
				t['\uFC09'] = '\u0628\u0649';
				t['\uFC0A'] = '\u0628\u064A';
				t['\uFC0B'] = '\u062A\u062C';
				t['\uFC0C'] = '\u062A\u062D';
				t['\uFC0D'] = '\u062A\u062E';
				t['\uFC0E'] = '\u062A\u0645';
				t['\uFC0F'] = '\u062A\u0649';
				t['\uFC10'] = '\u062A\u064A';
				t['\uFC11'] = '\u062B\u062C';
				t['\uFC12'] = '\u062B\u0645';
				t['\uFC13'] = '\u062B\u0649';
				t['\uFC14'] = '\u062B\u064A';
				t['\uFC15'] = '\u062C\u062D';
				t['\uFC16'] = '\u062C\u0645';
				t['\uFC17'] = '\u062D\u062C';
				t['\uFC18'] = '\u062D\u0645';
				t['\uFC19'] = '\u062E\u062C';
				t['\uFC1A'] = '\u062E\u062D';
				t['\uFC1B'] = '\u062E\u0645';
				t['\uFC1C'] = '\u0633\u062C';
				t['\uFC1D'] = '\u0633\u062D';
				t['\uFC1E'] = '\u0633\u062E';
				t['\uFC1F'] = '\u0633\u0645';
				t['\uFC20'] = '\u0635\u062D';
				t['\uFC21'] = '\u0635\u0645';
				t['\uFC22'] = '\u0636\u062C';
				t['\uFC23'] = '\u0636\u062D';
				t['\uFC24'] = '\u0636\u062E';
				t['\uFC25'] = '\u0636\u0645';
				t['\uFC26'] = '\u0637\u062D';
				t['\uFC27'] = '\u0637\u0645';
				t['\uFC28'] = '\u0638\u0645';
				t['\uFC29'] = '\u0639\u062C';
				t['\uFC2A'] = '\u0639\u0645';
				t['\uFC2B'] = '\u063A\u062C';
				t['\uFC2C'] = '\u063A\u0645';
				t['\uFC2D'] = '\u0641\u062C';
				t['\uFC2E'] = '\u0641\u062D';
				t['\uFC2F'] = '\u0641\u062E';
				t['\uFC30'] = '\u0641\u0645';
				t['\uFC31'] = '\u0641\u0649';
				t['\uFC32'] = '\u0641\u064A';
				t['\uFC33'] = '\u0642\u062D';
				t['\uFC34'] = '\u0642\u0645';
				t['\uFC35'] = '\u0642\u0649';
				t['\uFC36'] = '\u0642\u064A';
				t['\uFC37'] = '\u0643\u0627';
				t['\uFC38'] = '\u0643\u062C';
				t['\uFC39'] = '\u0643\u062D';
				t['\uFC3A'] = '\u0643\u062E';
				t['\uFC3B'] = '\u0643\u0644';
				t['\uFC3C'] = '\u0643\u0645';
				t['\uFC3D'] = '\u0643\u0649';
				t['\uFC3E'] = '\u0643\u064A';
				t['\uFC3F'] = '\u0644\u062C';
				t['\uFC40'] = '\u0644\u062D';
				t['\uFC41'] = '\u0644\u062E';
				t['\uFC42'] = '\u0644\u0645';
				t['\uFC43'] = '\u0644\u0649';
				t['\uFC44'] = '\u0644\u064A';
				t['\uFC45'] = '\u0645\u062C';
				t['\uFC46'] = '\u0645\u062D';
				t['\uFC47'] = '\u0645\u062E';
				t['\uFC48'] = '\u0645\u0645';
				t['\uFC49'] = '\u0645\u0649';
				t['\uFC4A'] = '\u0645\u064A';
				t['\uFC4B'] = '\u0646\u062C';
				t['\uFC4C'] = '\u0646\u062D';
				t['\uFC4D'] = '\u0646\u062E';
				t['\uFC4E'] = '\u0646\u0645';
				t['\uFC4F'] = '\u0646\u0649';
				t['\uFC50'] = '\u0646\u064A';
				t['\uFC51'] = '\u0647\u062C';
				t['\uFC52'] = '\u0647\u0645';
				t['\uFC53'] = '\u0647\u0649';
				t['\uFC54'] = '\u0647\u064A';
				t['\uFC55'] = '\u064A\u062C';
				t['\uFC56'] = '\u064A\u062D';
				t['\uFC57'] = '\u064A\u062E';
				t['\uFC58'] = '\u064A\u0645';
				t['\uFC59'] = '\u064A\u0649';
				t['\uFC5A'] = '\u064A\u064A';
				t['\uFC5B'] = '\u0630\u0670';
				t['\uFC5C'] = '\u0631\u0670';
				t['\uFC5D'] = '\u0649\u0670';
				t['\uFC5E'] = '\u0020\u064C\u0651';
				t['\uFC5F'] = '\u0020\u064D\u0651';
				t['\uFC60'] = '\u0020\u064E\u0651';
				t['\uFC61'] = '\u0020\u064F\u0651';
				t['\uFC62'] = '\u0020\u0650\u0651';
				t['\uFC63'] = '\u0020\u0651\u0670';
				t['\uFC64'] = '\u0626\u0631';
				t['\uFC65'] = '\u0626\u0632';
				t['\uFC66'] = '\u0626\u0645';
				t['\uFC67'] = '\u0626\u0646';
				t['\uFC68'] = '\u0626\u0649';
				t['\uFC69'] = '\u0626\u064A';
				t['\uFC6A'] = '\u0628\u0631';
				t['\uFC6B'] = '\u0628\u0632';
				t['\uFC6C'] = '\u0628\u0645';
				t['\uFC6D'] = '\u0628\u0646';
				t['\uFC6E'] = '\u0628\u0649';
				t['\uFC6F'] = '\u0628\u064A';
				t['\uFC70'] = '\u062A\u0631';
				t['\uFC71'] = '\u062A\u0632';
				t['\uFC72'] = '\u062A\u0645';
				t['\uFC73'] = '\u062A\u0646';
				t['\uFC74'] = '\u062A\u0649';
				t['\uFC75'] = '\u062A\u064A';
				t['\uFC76'] = '\u062B\u0631';
				t['\uFC77'] = '\u062B\u0632';
				t['\uFC78'] = '\u062B\u0645';
				t['\uFC79'] = '\u062B\u0646';
				t['\uFC7A'] = '\u062B\u0649';
				t['\uFC7B'] = '\u062B\u064A';
				t['\uFC7C'] = '\u0641\u0649';
				t['\uFC7D'] = '\u0641\u064A';
				t['\uFC7E'] = '\u0642\u0649';
				t['\uFC7F'] = '\u0642\u064A';
				t['\uFC80'] = '\u0643\u0627';
				t['\uFC81'] = '\u0643\u0644';
				t['\uFC82'] = '\u0643\u0645';
				t['\uFC83'] = '\u0643\u0649';
				t['\uFC84'] = '\u0643\u064A';
				t['\uFC85'] = '\u0644\u0645';
				t['\uFC86'] = '\u0644\u0649';
				t['\uFC87'] = '\u0644\u064A';
				t['\uFC88'] = '\u0645\u0627';
				t['\uFC89'] = '\u0645\u0645';
				t['\uFC8A'] = '\u0646\u0631';
				t['\uFC8B'] = '\u0646\u0632';
				t['\uFC8C'] = '\u0646\u0645';
				t['\uFC8D'] = '\u0646\u0646';
				t['\uFC8E'] = '\u0646\u0649';
				t['\uFC8F'] = '\u0646\u064A';
				t['\uFC90'] = '\u0649\u0670';
				t['\uFC91'] = '\u064A\u0631';
				t['\uFC92'] = '\u064A\u0632';
				t['\uFC93'] = '\u064A\u0645';
				t['\uFC94'] = '\u064A\u0646';
				t['\uFC95'] = '\u064A\u0649';
				t['\uFC96'] = '\u064A\u064A';
				t['\uFC97'] = '\u0626\u062C';
				t['\uFC98'] = '\u0626\u062D';
				t['\uFC99'] = '\u0626\u062E';
				t['\uFC9A'] = '\u0626\u0645';
				t['\uFC9B'] = '\u0626\u0647';
				t['\uFC9C'] = '\u0628\u062C';
				t['\uFC9D'] = '\u0628\u062D';
				t['\uFC9E'] = '\u0628\u062E';
				t['\uFC9F'] = '\u0628\u0645';
				t['\uFCA0'] = '\u0628\u0647';
				t['\uFCA1'] = '\u062A\u062C';
				t['\uFCA2'] = '\u062A\u062D';
				t['\uFCA3'] = '\u062A\u062E';
				t['\uFCA4'] = '\u062A\u0645';
				t['\uFCA5'] = '\u062A\u0647';
				t['\uFCA6'] = '\u062B\u0645';
				t['\uFCA7'] = '\u062C\u062D';
				t['\uFCA8'] = '\u062C\u0645';
				t['\uFCA9'] = '\u062D\u062C';
				t['\uFCAA'] = '\u062D\u0645';
				t['\uFCAB'] = '\u062E\u062C';
				t['\uFCAC'] = '\u062E\u0645';
				t['\uFCAD'] = '\u0633\u062C';
				t['\uFCAE'] = '\u0633\u062D';
				t['\uFCAF'] = '\u0633\u062E';
				t['\uFCB0'] = '\u0633\u0645';
				t['\uFCB1'] = '\u0635\u062D';
				t['\uFCB2'] = '\u0635\u062E';
				t['\uFCB3'] = '\u0635\u0645';
				t['\uFCB4'] = '\u0636\u062C';
				t['\uFCB5'] = '\u0636\u062D';
				t['\uFCB6'] = '\u0636\u062E';
				t['\uFCB7'] = '\u0636\u0645';
				t['\uFCB8'] = '\u0637\u062D';
				t['\uFCB9'] = '\u0638\u0645';
				t['\uFCBA'] = '\u0639\u062C';
				t['\uFCBB'] = '\u0639\u0645';
				t['\uFCBC'] = '\u063A\u062C';
				t['\uFCBD'] = '\u063A\u0645';
				t['\uFCBE'] = '\u0641\u062C';
				t['\uFCBF'] = '\u0641\u062D';
				t['\uFCC0'] = '\u0641\u062E';
				t['\uFCC1'] = '\u0641\u0645';
				t['\uFCC2'] = '\u0642\u062D';
				t['\uFCC3'] = '\u0642\u0645';
				t['\uFCC4'] = '\u0643\u062C';
				t['\uFCC5'] = '\u0643\u062D';
				t['\uFCC6'] = '\u0643\u062E';
				t['\uFCC7'] = '\u0643\u0644';
				t['\uFCC8'] = '\u0643\u0645';
				t['\uFCC9'] = '\u0644\u062C';
				t['\uFCCA'] = '\u0644\u062D';
				t['\uFCCB'] = '\u0644\u062E';
				t['\uFCCC'] = '\u0644\u0645';
				t['\uFCCD'] = '\u0644\u0647';
				t['\uFCCE'] = '\u0645\u062C';
				t['\uFCCF'] = '\u0645\u062D';
				t['\uFCD0'] = '\u0645\u062E';
				t['\uFCD1'] = '\u0645\u0645';
				t['\uFCD2'] = '\u0646\u062C';
				t['\uFCD3'] = '\u0646\u062D';
				t['\uFCD4'] = '\u0646\u062E';
				t['\uFCD5'] = '\u0646\u0645';
				t['\uFCD6'] = '\u0646\u0647';
				t['\uFCD7'] = '\u0647\u062C';
				t['\uFCD8'] = '\u0647\u0645';
				t['\uFCD9'] = '\u0647\u0670';
				t['\uFCDA'] = '\u064A\u062C';
				t['\uFCDB'] = '\u064A\u062D';
				t['\uFCDC'] = '\u064A\u062E';
				t['\uFCDD'] = '\u064A\u0645';
				t['\uFCDE'] = '\u064A\u0647';
				t['\uFCDF'] = '\u0626\u0645';
				t['\uFCE0'] = '\u0626\u0647';
				t['\uFCE1'] = '\u0628\u0645';
				t['\uFCE2'] = '\u0628\u0647';
				t['\uFCE3'] = '\u062A\u0645';
				t['\uFCE4'] = '\u062A\u0647';
				t['\uFCE5'] = '\u062B\u0645';
				t['\uFCE6'] = '\u062B\u0647';
				t['\uFCE7'] = '\u0633\u0645';
				t['\uFCE8'] = '\u0633\u0647';
				t['\uFCE9'] = '\u0634\u0645';
				t['\uFCEA'] = '\u0634\u0647';
				t['\uFCEB'] = '\u0643\u0644';
				t['\uFCEC'] = '\u0643\u0645';
				t['\uFCED'] = '\u0644\u0645';
				t['\uFCEE'] = '\u0646\u0645';
				t['\uFCEF'] = '\u0646\u0647';
				t['\uFCF0'] = '\u064A\u0645';
				t['\uFCF1'] = '\u064A\u0647';
				t['\uFCF2'] = '\u0640\u064E\u0651';
				t['\uFCF3'] = '\u0640\u064F\u0651';
				t['\uFCF4'] = '\u0640\u0650\u0651';
				t['\uFCF5'] = '\u0637\u0649';
				t['\uFCF6'] = '\u0637\u064A';
				t['\uFCF7'] = '\u0639\u0649';
				t['\uFCF8'] = '\u0639\u064A';
				t['\uFCF9'] = '\u063A\u0649';
				t['\uFCFA'] = '\u063A\u064A';
				t['\uFCFB'] = '\u0633\u0649';
				t['\uFCFC'] = '\u0633\u064A';
				t['\uFCFD'] = '\u0634\u0649';
				t['\uFCFE'] = '\u0634\u064A';
				t['\uFCFF'] = '\u062D\u0649';
				t['\uFD00'] = '\u062D\u064A';
				t['\uFD01'] = '\u062C\u0649';
				t['\uFD02'] = '\u062C\u064A';
				t['\uFD03'] = '\u062E\u0649';
				t['\uFD04'] = '\u062E\u064A';
				t['\uFD05'] = '\u0635\u0649';
				t['\uFD06'] = '\u0635\u064A';
				t['\uFD07'] = '\u0636\u0649';
				t['\uFD08'] = '\u0636\u064A';
				t['\uFD09'] = '\u0634\u062C';
				t['\uFD0A'] = '\u0634\u062D';
				t['\uFD0B'] = '\u0634\u062E';
				t['\uFD0C'] = '\u0634\u0645';
				t['\uFD0D'] = '\u0634\u0631';
				t['\uFD0E'] = '\u0633\u0631';
				t['\uFD0F'] = '\u0635\u0631';
				t['\uFD10'] = '\u0636\u0631';
				t['\uFD11'] = '\u0637\u0649';
				t['\uFD12'] = '\u0637\u064A';
				t['\uFD13'] = '\u0639\u0649';
				t['\uFD14'] = '\u0639\u064A';
				t['\uFD15'] = '\u063A\u0649';
				t['\uFD16'] = '\u063A\u064A';
				t['\uFD17'] = '\u0633\u0649';
				t['\uFD18'] = '\u0633\u064A';
				t['\uFD19'] = '\u0634\u0649';
				t['\uFD1A'] = '\u0634\u064A';
				t['\uFD1B'] = '\u062D\u0649';
				t['\uFD1C'] = '\u062D\u064A';
				t['\uFD1D'] = '\u062C\u0649';
				t['\uFD1E'] = '\u062C\u064A';
				t['\uFD1F'] = '\u062E\u0649';
				t['\uFD20'] = '\u062E\u064A';
				t['\uFD21'] = '\u0635\u0649';
				t['\uFD22'] = '\u0635\u064A';
				t['\uFD23'] = '\u0636\u0649';
				t['\uFD24'] = '\u0636\u064A';
				t['\uFD25'] = '\u0634\u062C';
				t['\uFD26'] = '\u0634\u062D';
				t['\uFD27'] = '\u0634\u062E';
				t['\uFD28'] = '\u0634\u0645';
				t['\uFD29'] = '\u0634\u0631';
				t['\uFD2A'] = '\u0633\u0631';
				t['\uFD2B'] = '\u0635\u0631';
				t['\uFD2C'] = '\u0636\u0631';
				t['\uFD2D'] = '\u0634\u062C';
				t['\uFD2E'] = '\u0634\u062D';
				t['\uFD2F'] = '\u0634\u062E';
				t['\uFD30'] = '\u0634\u0645';
				t['\uFD31'] = '\u0633\u0647';
				t['\uFD32'] = '\u0634\u0647';
				t['\uFD33'] = '\u0637\u0645';
				t['\uFD34'] = '\u0633\u062C';
				t['\uFD35'] = '\u0633\u062D';
				t['\uFD36'] = '\u0633\u062E';
				t['\uFD37'] = '\u0634\u062C';
				t['\uFD38'] = '\u0634\u062D';
				t['\uFD39'] = '\u0634\u062E';
				t['\uFD3A'] = '\u0637\u0645';
				t['\uFD3B'] = '\u0638\u0645';
				t['\uFD3C'] = '\u0627\u064B';
				t['\uFD3D'] = '\u0627\u064B';
				t['\uFD50'] = '\u062A\u062C\u0645';
				t['\uFD51'] = '\u062A\u062D\u062C';
				t['\uFD52'] = '\u062A\u062D\u062C';
				t['\uFD53'] = '\u062A\u062D\u0645';
				t['\uFD54'] = '\u062A\u062E\u0645';
				t['\uFD55'] = '\u062A\u0645\u062C';
				t['\uFD56'] = '\u062A\u0645\u062D';
				t['\uFD57'] = '\u062A\u0645\u062E';
				t['\uFD58'] = '\u062C\u0645\u062D';
				t['\uFD59'] = '\u062C\u0645\u062D';
				t['\uFD5A'] = '\u062D\u0645\u064A';
				t['\uFD5B'] = '\u062D\u0645\u0649';
				t['\uFD5C'] = '\u0633\u062D\u062C';
				t['\uFD5D'] = '\u0633\u062C\u062D';
				t['\uFD5E'] = '\u0633\u062C\u0649';
				t['\uFD5F'] = '\u0633\u0645\u062D';
				t['\uFD60'] = '\u0633\u0645\u062D';
				t['\uFD61'] = '\u0633\u0645\u062C';
				t['\uFD62'] = '\u0633\u0645\u0645';
				t['\uFD63'] = '\u0633\u0645\u0645';
				t['\uFD64'] = '\u0635\u062D\u062D';
				t['\uFD65'] = '\u0635\u062D\u062D';
				t['\uFD66'] = '\u0635\u0645\u0645';
				t['\uFD67'] = '\u0634\u062D\u0645';
				t['\uFD68'] = '\u0634\u062D\u0645';
				t['\uFD69'] = '\u0634\u062C\u064A';
				t['\uFD6A'] = '\u0634\u0645\u062E';
				t['\uFD6B'] = '\u0634\u0645\u062E';
				t['\uFD6C'] = '\u0634\u0645\u0645';
				t['\uFD6D'] = '\u0634\u0645\u0645';
				t['\uFD6E'] = '\u0636\u062D\u0649';
				t['\uFD6F'] = '\u0636\u062E\u0645';
				t['\uFD70'] = '\u0636\u062E\u0645';
				t['\uFD71'] = '\u0637\u0645\u062D';
				t['\uFD72'] = '\u0637\u0645\u062D';
				t['\uFD73'] = '\u0637\u0645\u0645';
				t['\uFD74'] = '\u0637\u0645\u064A';
				t['\uFD75'] = '\u0639\u062C\u0645';
				t['\uFD76'] = '\u0639\u0645\u0645';
				t['\uFD77'] = '\u0639\u0645\u0645';
				t['\uFD78'] = '\u0639\u0645\u0649';
				t['\uFD79'] = '\u063A\u0645\u0645';
				t['\uFD7A'] = '\u063A\u0645\u064A';
				t['\uFD7B'] = '\u063A\u0645\u0649';
				t['\uFD7C'] = '\u0641\u062E\u0645';
				t['\uFD7D'] = '\u0641\u062E\u0645';
				t['\uFD7E'] = '\u0642\u0645\u062D';
				t['\uFD7F'] = '\u0642\u0645\u0645';
				t['\uFD80'] = '\u0644\u062D\u0645';
				t['\uFD81'] = '\u0644\u062D\u064A';
				t['\uFD82'] = '\u0644\u062D\u0649';
				t['\uFD83'] = '\u0644\u062C\u062C';
				t['\uFD84'] = '\u0644\u062C\u062C';
				t['\uFD85'] = '\u0644\u062E\u0645';
				t['\uFD86'] = '\u0644\u062E\u0645';
				t['\uFD87'] = '\u0644\u0645\u062D';
				t['\uFD88'] = '\u0644\u0645\u062D';
				t['\uFD89'] = '\u0645\u062D\u062C';
				t['\uFD8A'] = '\u0645\u062D\u0645';
				t['\uFD8B'] = '\u0645\u062D\u064A';
				t['\uFD8C'] = '\u0645\u062C\u062D';
				t['\uFD8D'] = '\u0645\u062C\u0645';
				t['\uFD8E'] = '\u0645\u062E\u062C';
				t['\uFD8F'] = '\u0645\u062E\u0645';
				t['\uFD92'] = '\u0645\u062C\u062E';
				t['\uFD93'] = '\u0647\u0645\u062C';
				t['\uFD94'] = '\u0647\u0645\u0645';
				t['\uFD95'] = '\u0646\u062D\u0645';
				t['\uFD96'] = '\u0646\u062D\u0649';
				t['\uFD97'] = '\u0646\u062C\u0645';
				t['\uFD98'] = '\u0646\u062C\u0645';
				t['\uFD99'] = '\u0646\u062C\u0649';
				t['\uFD9A'] = '\u0646\u0645\u064A';
				t['\uFD9B'] = '\u0646\u0645\u0649';
				t['\uFD9C'] = '\u064A\u0645\u0645';
				t['\uFD9D'] = '\u064A\u0645\u0645';
				t['\uFD9E'] = '\u0628\u062E\u064A';
				t['\uFD9F'] = '\u062A\u062C\u064A';
				t['\uFDA0'] = '\u062A\u062C\u0649';
				t['\uFDA1'] = '\u062A\u062E\u064A';
				t['\uFDA2'] = '\u062A\u062E\u0649';
				t['\uFDA3'] = '\u062A\u0645\u064A';
				t['\uFDA4'] = '\u062A\u0645\u0649';
				t['\uFDA5'] = '\u062C\u0645\u064A';
				t['\uFDA6'] = '\u062C\u062D\u0649';
				t['\uFDA7'] = '\u062C\u0645\u0649';
				t['\uFDA8'] = '\u0633\u062E\u0649';
				t['\uFDA9'] = '\u0635\u062D\u064A';
				t['\uFDAA'] = '\u0634\u062D\u064A';
				t['\uFDAB'] = '\u0636\u062D\u064A';
				t['\uFDAC'] = '\u0644\u062C\u064A';
				t['\uFDAD'] = '\u0644\u0645\u064A';
				t['\uFDAE'] = '\u064A\u062D\u064A';
				t['\uFDAF'] = '\u064A\u062C\u064A';
				t['\uFDB0'] = '\u064A\u0645\u064A';
				t['\uFDB1'] = '\u0645\u0645\u064A';
				t['\uFDB2'] = '\u0642\u0645\u064A';
				t['\uFDB3'] = '\u0646\u062D\u064A';
				t['\uFDB4'] = '\u0642\u0645\u062D';
				t['\uFDB5'] = '\u0644\u062D\u0645';
				t['\uFDB6'] = '\u0639\u0645\u064A';
				t['\uFDB7'] = '\u0643\u0645\u064A';
				t['\uFDB8'] = '\u0646\u062C\u062D';
				t['\uFDB9'] = '\u0645\u062E\u064A';
				t['\uFDBA'] = '\u0644\u062C\u0645';
				t['\uFDBB'] = '\u0643\u0645\u0645';
				t['\uFDBC'] = '\u0644\u062C\u0645';
				t['\uFDBD'] = '\u0646\u062C\u062D';
				t['\uFDBE'] = '\u062C\u062D\u064A';
				t['\uFDBF'] = '\u062D\u062C\u064A';
				t['\uFDC0'] = '\u0645\u062C\u064A';
				t['\uFDC1'] = '\u0641\u0645\u064A';
				t['\uFDC2'] = '\u0628\u062D\u064A';
				t['\uFDC3'] = '\u0643\u0645\u0645';
				t['\uFDC4'] = '\u0639\u062C\u0645';
				t['\uFDC5'] = '\u0635\u0645\u0645';
				t['\uFDC6'] = '\u0633\u062E\u064A';
				t['\uFDC7'] = '\u0646\u062C\u064A';
				t['\uFE49'] = '\u203E';
				t['\uFE4A'] = '\u203E';
				t['\uFE4B'] = '\u203E';
				t['\uFE4C'] = '\u203E';
				t['\uFE4D'] = '\u005F';
				t['\uFE4E'] = '\u005F';
				t['\uFE4F'] = '\u005F';
				t['\uFE80'] = '\u0621';
				t['\uFE81'] = '\u0622';
				t['\uFE82'] = '\u0622';
				t['\uFE83'] = '\u0623';
				t['\uFE84'] = '\u0623';
				t['\uFE85'] = '\u0624';
				t['\uFE86'] = '\u0624';
				t['\uFE87'] = '\u0625';
				t['\uFE88'] = '\u0625';
				t['\uFE89'] = '\u0626';
				t['\uFE8A'] = '\u0626';
				t['\uFE8B'] = '\u0626';
				t['\uFE8C'] = '\u0626';
				t['\uFE8D'] = '\u0627';
				t['\uFE8E'] = '\u0627';
				t['\uFE8F'] = '\u0628';
				t['\uFE90'] = '\u0628';
				t['\uFE91'] = '\u0628';
				t['\uFE92'] = '\u0628';
				t['\uFE93'] = '\u0629';
				t['\uFE94'] = '\u0629';
				t['\uFE95'] = '\u062A';
				t['\uFE96'] = '\u062A';
				t['\uFE97'] = '\u062A';
				t['\uFE98'] = '\u062A';
				t['\uFE99'] = '\u062B';
				t['\uFE9A'] = '\u062B';
				t['\uFE9B'] = '\u062B';
				t['\uFE9C'] = '\u062B';
				t['\uFE9D'] = '\u062C';
				t['\uFE9E'] = '\u062C';
				t['\uFE9F'] = '\u062C';
				t['\uFEA0'] = '\u062C';
				t['\uFEA1'] = '\u062D';
				t['\uFEA2'] = '\u062D';
				t['\uFEA3'] = '\u062D';
				t['\uFEA4'] = '\u062D';
				t['\uFEA5'] = '\u062E';
				t['\uFEA6'] = '\u062E';
				t['\uFEA7'] = '\u062E';
				t['\uFEA8'] = '\u062E';
				t['\uFEA9'] = '\u062F';
				t['\uFEAA'] = '\u062F';
				t['\uFEAB'] = '\u0630';
				t['\uFEAC'] = '\u0630';
				t['\uFEAD'] = '\u0631';
				t['\uFEAE'] = '\u0631';
				t['\uFEAF'] = '\u0632';
				t['\uFEB0'] = '\u0632';
				t['\uFEB1'] = '\u0633';
				t['\uFEB2'] = '\u0633';
				t['\uFEB3'] = '\u0633';
				t['\uFEB4'] = '\u0633';
				t['\uFEB5'] = '\u0634';
				t['\uFEB6'] = '\u0634';
				t['\uFEB7'] = '\u0634';
				t['\uFEB8'] = '\u0634';
				t['\uFEB9'] = '\u0635';
				t['\uFEBA'] = '\u0635';
				t['\uFEBB'] = '\u0635';
				t['\uFEBC'] = '\u0635';
				t['\uFEBD'] = '\u0636';
				t['\uFEBE'] = '\u0636';
				t['\uFEBF'] = '\u0636';
				t['\uFEC0'] = '\u0636';
				t['\uFEC1'] = '\u0637';
				t['\uFEC2'] = '\u0637';
				t['\uFEC3'] = '\u0637';
				t['\uFEC4'] = '\u0637';
				t['\uFEC5'] = '\u0638';
				t['\uFEC6'] = '\u0638';
				t['\uFEC7'] = '\u0638';
				t['\uFEC8'] = '\u0638';
				t['\uFEC9'] = '\u0639';
				t['\uFECA'] = '\u0639';
				t['\uFECB'] = '\u0639';
				t['\uFECC'] = '\u0639';
				t['\uFECD'] = '\u063A';
				t['\uFECE'] = '\u063A';
				t['\uFECF'] = '\u063A';
				t['\uFED0'] = '\u063A';
				t['\uFED1'] = '\u0641';
				t['\uFED2'] = '\u0641';
				t['\uFED3'] = '\u0641';
				t['\uFED4'] = '\u0641';
				t['\uFED5'] = '\u0642';
				t['\uFED6'] = '\u0642';
				t['\uFED7'] = '\u0642';
				t['\uFED8'] = '\u0642';
				t['\uFED9'] = '\u0643';
				t['\uFEDA'] = '\u0643';
				t['\uFEDB'] = '\u0643';
				t['\uFEDC'] = '\u0643';
				t['\uFEDD'] = '\u0644';
				t['\uFEDE'] = '\u0644';
				t['\uFEDF'] = '\u0644';
				t['\uFEE0'] = '\u0644';
				t['\uFEE1'] = '\u0645';
				t['\uFEE2'] = '\u0645';
				t['\uFEE3'] = '\u0645';
				t['\uFEE4'] = '\u0645';
				t['\uFEE5'] = '\u0646';
				t['\uFEE6'] = '\u0646';
				t['\uFEE7'] = '\u0646';
				t['\uFEE8'] = '\u0646';
				t['\uFEE9'] = '\u0647';
				t['\uFEEA'] = '\u0647';
				t['\uFEEB'] = '\u0647';
				t['\uFEEC'] = '\u0647';
				t['\uFEED'] = '\u0648';
				t['\uFEEE'] = '\u0648';
				t['\uFEEF'] = '\u0649';
				t['\uFEF0'] = '\u0649';
				t['\uFEF1'] = '\u064A';
				t['\uFEF2'] = '\u064A';
				t['\uFEF3'] = '\u064A';
				t['\uFEF4'] = '\u064A';
				t['\uFEF5'] = '\u0644\u0622';
				t['\uFEF6'] = '\u0644\u0622';
				t['\uFEF7'] = '\u0644\u0623';
				t['\uFEF8'] = '\u0644\u0623';
				t['\uFEF9'] = '\u0644\u0625';
				t['\uFEFA'] = '\u0644\u0625';
				t['\uFEFB'] = '\u0644\u0627';
				t['\uFEFC'] = '\u0644\u0627';
			});
			function reverseIfRtl(chars) {
				var charsLength = chars.length;
				if (charsLength <= 1 || !isRTLRangeFor(chars.charCodeAt(0))) {
					return chars;
				}
				var s = '';
				for (var ii = charsLength - 1; ii >= 0; ii--) {
					s += chars[ii];
				}
				return s;
			}
			exports.mapSpecialUnicodeValues = mapSpecialUnicodeValues;
			exports.reverseIfRtl = reverseIfRtl;
			exports.getUnicodeRangeFor = getUnicodeRangeFor;
			exports.getNormalizedUnicodes = getNormalizedUnicodes;
			exports.getUnicodeForGlyph = getUnicodeForGlyph;

			/***/
		}),
/* 19 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreWorker = __w_pdfjs_require__(8);
			var globalScope = sharedUtil.globalScope;
			var OK_RESPONSE = 200;
			var PARTIAL_CONTENT_RESPONSE = 206;
			function NetworkManager(url, args) {
				this.url = url;
				args = args || {};
				this.isHttp = /^https?:/i.test(url);
				this.httpHeaders = this.isHttp && args.httpHeaders || {};
				this.withCredentials = args.withCredentials || false;
				this.getXhr = args.getXhr || function NetworkManager_getXhr() {
					return new XMLHttpRequest();
				};
				this.currXhrId = 0;
				this.pendingRequests = Object.create(null);
				this.loadedRequests = Object.create(null);
			}
			function getArrayBuffer(xhr) {
				var data = xhr.response;
				if (typeof data !== 'string') {
					return data;
				}
				var length = data.length;
				var array = new Uint8Array(length);
				for (var i = 0; i < length; i++) {
					array[i] = data.charCodeAt(i) & 0xFF;
				}
				return array.buffer;
			}
			var supportsMozChunked = function supportsMozChunkedClosure() {
				try {
					var x = new XMLHttpRequest();
					x.open('GET', globalScope.location.href);
					x.responseType = 'moz-chunked-arraybuffer';
					return x.responseType === 'moz-chunked-arraybuffer';
				} catch (e) {
					return false;
				}
			}();
			NetworkManager.prototype = {
				requestRange: function NetworkManager_requestRange(begin, end, listeners) {
					var args = {
						begin: begin,
						end: end
					};
					for (var prop in listeners) {
						args[prop] = listeners[prop];
					}
					return this.request(args);
				},
				requestFull: function NetworkManager_requestFull(listeners) {
					return this.request(listeners);
				},
				request: function NetworkManager_request(args) {
					var xhr = this.getXhr();
					var xhrId = this.currXhrId++;
					var pendingRequest = this.pendingRequests[xhrId] = { xhr: xhr };
					xhr.open('GET', this.url);
					xhr.withCredentials = this.withCredentials;
					for (var property in this.httpHeaders) {
						var value = this.httpHeaders[property];
						if (typeof value === 'undefined') {
							continue;
						}
						xhr.setRequestHeader(property, value);
					}
					if (this.isHttp && 'begin' in args && 'end' in args) {
						var rangeStr = args.begin + '-' + (args.end - 1);
						xhr.setRequestHeader('Range', 'bytes=' + rangeStr);
						pendingRequest.expectedStatus = 206;
					} else {
						pendingRequest.expectedStatus = 200;
					}
					var useMozChunkedLoading = supportsMozChunked && !!args.onProgressiveData;
					if (useMozChunkedLoading) {
						xhr.responseType = 'moz-chunked-arraybuffer';
						pendingRequest.onProgressiveData = args.onProgressiveData;
						pendingRequest.mozChunked = true;
					} else {
						xhr.responseType = 'arraybuffer';
					}
					if (args.onError) {
						xhr.onerror = function (evt) {
							args.onError(xhr.status);
						};
					}
					xhr.onreadystatechange = this.onStateChange.bind(this, xhrId);
					xhr.onprogress = this.onProgress.bind(this, xhrId);
					pendingRequest.onHeadersReceived = args.onHeadersReceived;
					pendingRequest.onDone = args.onDone;
					pendingRequest.onError = args.onError;
					pendingRequest.onProgress = args.onProgress;
					xhr.send(null);
					return xhrId;
				},
				onProgress: function NetworkManager_onProgress(xhrId, evt) {
					var pendingRequest = this.pendingRequests[xhrId];
					if (!pendingRequest) {
						return;
					}
					if (pendingRequest.mozChunked) {
						var chunk = getArrayBuffer(pendingRequest.xhr);
						pendingRequest.onProgressiveData(chunk);
					}
					var onProgress = pendingRequest.onProgress;
					if (onProgress) {
						onProgress(evt);
					}
				},
				onStateChange: function NetworkManager_onStateChange(xhrId, evt) {
					var pendingRequest = this.pendingRequests[xhrId];
					if (!pendingRequest) {
						return;
					}
					var xhr = pendingRequest.xhr;
					if (xhr.readyState >= 2 && pendingRequest.onHeadersReceived) {
						pendingRequest.onHeadersReceived();
						delete pendingRequest.onHeadersReceived;
					}
					if (xhr.readyState !== 4) {
						return;
					}
					if (!(xhrId in this.pendingRequests)) {
						return;
					}
					delete this.pendingRequests[xhrId];
					if (xhr.status === 0 && this.isHttp) {
						if (pendingRequest.onError) {
							pendingRequest.onError(xhr.status);
						}
						return;
					}
					var xhrStatus = xhr.status || OK_RESPONSE;
					var ok_response_on_range_request = xhrStatus === OK_RESPONSE && pendingRequest.expectedStatus === PARTIAL_CONTENT_RESPONSE;
					if (!ok_response_on_range_request && xhrStatus !== pendingRequest.expectedStatus) {
						if (pendingRequest.onError) {
							pendingRequest.onError(xhr.status);
						}
						return;
					}
					this.loadedRequests[xhrId] = true;
					var chunk = getArrayBuffer(xhr);
					if (xhrStatus === PARTIAL_CONTENT_RESPONSE) {
						var rangeHeader = xhr.getResponseHeader('Content-Range');
						var matches = /bytes (\d+)-(\d+)\/(\d+)/.exec(rangeHeader);
						var begin = parseInt(matches[1], 10);
						pendingRequest.onDone({
							begin: begin,
							chunk: chunk
						});
					} else if (pendingRequest.onProgressiveData) {
						pendingRequest.onDone(null);
					} else if (chunk) {
						pendingRequest.onDone({
							begin: 0,
							chunk: chunk
						});
					} else if (pendingRequest.onError) {
						pendingRequest.onError(xhr.status);
					}
				},
				hasPendingRequests: function NetworkManager_hasPendingRequests() {
					for (var xhrId in this.pendingRequests) {
						return true;
					}
					return false;
				},
				getRequestXhr: function NetworkManager_getXhr(xhrId) {
					return this.pendingRequests[xhrId].xhr;
				},
				isStreamingRequest: function NetworkManager_isStreamingRequest(xhrId) {
					return !!this.pendingRequests[xhrId].onProgressiveData;
				},
				isPendingRequest: function NetworkManager_isPendingRequest(xhrId) {
					return xhrId in this.pendingRequests;
				},
				isLoadedRequest: function NetworkManager_isLoadedRequest(xhrId) {
					return xhrId in this.loadedRequests;
				},
				abortAllRequests: function NetworkManager_abortAllRequests() {
					for (var xhrId in this.pendingRequests) {
						this.abortRequest(xhrId | 0);
					}
				},
				abortRequest: function NetworkManager_abortRequest(xhrId) {
					var xhr = this.pendingRequests[xhrId].xhr;
					delete this.pendingRequests[xhrId];
					xhr.abort();
				}
			};
			var assert = sharedUtil.assert;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var isInt = sharedUtil.isInt;
			var MissingPDFException = sharedUtil.MissingPDFException;
			var UnexpectedResponseException = sharedUtil.UnexpectedResponseException;
			function PDFNetworkStream(options) {
				this._options = options;
				var source = options.source;
				this._manager = new NetworkManager(source.url, {
					httpHeaders: source.httpHeaders,
					withCredentials: source.withCredentials
				});
				this._rangeChunkSize = source.rangeChunkSize;
				this._fullRequestReader = null;
				this._rangeRequestReaders = [];
			}
			PDFNetworkStream.prototype = {
				_onRangeRequestReaderClosed: function PDFNetworkStream_onRangeRequestReaderClosed(reader) {
					var i = this._rangeRequestReaders.indexOf(reader);
					if (i >= 0) {
						this._rangeRequestReaders.splice(i, 1);
					}
				},
				getFullReader: function PDFNetworkStream_getFullReader() {
					assert(!this._fullRequestReader);
					this._fullRequestReader = new PDFNetworkStreamFullRequestReader(this._manager, this._options);
					return this._fullRequestReader;
				},
				getRangeReader: function PDFNetworkStream_getRangeReader(begin, end) {
					var reader = new PDFNetworkStreamRangeRequestReader(this._manager, begin, end);
					reader.onClosed = this._onRangeRequestReaderClosed.bind(this);
					this._rangeRequestReaders.push(reader);
					return reader;
				},
				cancelAllRequests: function PDFNetworkStream_cancelAllRequests(reason) {
					if (this._fullRequestReader) {
						this._fullRequestReader.cancel(reason);
					}
					var readers = this._rangeRequestReaders.slice(0);
					readers.forEach(function (reader) {
						reader.cancel(reason);
					});
				}
			};
			function PDFNetworkStreamFullRequestReader(manager, options) {
				this._manager = manager;
				var source = options.source;
				var args = {
					onHeadersReceived: this._onHeadersReceived.bind(this),
					onProgressiveData: source.disableStream ? null : this._onProgressiveData.bind(this),
					onDone: this._onDone.bind(this),
					onError: this._onError.bind(this),
					onProgress: this._onProgress.bind(this)
				};
				this._url = source.url;
				this._fullRequestId = manager.requestFull(args);
				this._headersReceivedCapability = createPromiseCapability();
				this._disableRange = options.disableRange || false;
				this._contentLength = source.length;
				this._rangeChunkSize = source.rangeChunkSize;
				if (!this._rangeChunkSize && !this._disableRange) {
					this._disableRange = true;
				}
				this._isStreamingSupported = false;
				this._isRangeSupported = false;
				this._cachedChunks = [];
				this._requests = [];
				this._done = false;
				this._storedError = undefined;
				this.onProgress = null;
			}
			PDFNetworkStreamFullRequestReader.prototype = {
				_validateRangeRequestCapabilities: function PDFNetworkStreamFullRequestReader_validateRangeRequestCapabilities() {
					if (this._disableRange) {
						return false;
					}
					var networkManager = this._manager;
					if (!networkManager.isHttp) {
						return false;
					}
					var fullRequestXhrId = this._fullRequestId;
					var fullRequestXhr = networkManager.getRequestXhr(fullRequestXhrId);
					if (fullRequestXhr.getAllResponseHeaders().includes('Accept-Ranges') && fullRequestXhr.getResponseHeader('Accept-Ranges') !== 'bytes') {
						return false;
					}
					var contentEncoding = fullRequestXhr.getAllResponseHeaders().includes('Content-Encoding') && fullRequestXhr.getResponseHeader('Content-Encoding') || 'identity';
					if (contentEncoding !== 'identity') {
						return false;
					}
					var length = fullRequestXhr.getResponseHeader('Content-Length');
					length = parseInt(length, 10);
					if (!isInt(length)) {
						return false;
					}
					this._contentLength = length;
					if (length <= 2 * this._rangeChunkSize) {
						return false;
					}
					return true;
				},
				_onHeadersReceived: function PDFNetworkStreamFullRequestReader_onHeadersReceived() {
					if (this._validateRangeRequestCapabilities()) {
						this._isRangeSupported = true;
					}
					var networkManager = this._manager;
					var fullRequestXhrId = this._fullRequestId;
					if (networkManager.isStreamingRequest(fullRequestXhrId)) {
						this._isStreamingSupported = true;
					} else if (this._isRangeSupported) {
						networkManager.abortRequest(fullRequestXhrId);
					}
					this._headersReceivedCapability.resolve();
				},
				_onProgressiveData: function PDFNetworkStreamFullRequestReader_onProgressiveData(chunk) {
					if (this._requests.length > 0) {
						var requestCapability = this._requests.shift();
						requestCapability.resolve({
							value: chunk,
							done: false
						});
					} else {
						this._cachedChunks.push(chunk);
					}
				},
				_onDone: function PDFNetworkStreamFullRequestReader_onDone(args) {
					if (args) {
						this._onProgressiveData(args.chunk);
					}
					this._done = true;
					if (this._cachedChunks.length > 0) {
						return;
					}
					this._requests.forEach(function (requestCapability) {
						requestCapability.resolve({
							value: undefined,
							done: true
						});
					});
					this._requests = [];
				},
				_onError: function PDFNetworkStreamFullRequestReader_onError(status) {
					var url = this._url;
					var exception;
					if (status === 404 || status === 0 && /^file:/.test(url)) {
						exception = new MissingPDFException('Missing PDF "' + url + '".');
					} else {
						exception = new UnexpectedResponseException('Unexpected server response (' + status + ') while retrieving PDF "' + url + '".', status);
					}
					this._storedError = exception;
					this._headersReceivedCapability.reject(exception);
					this._requests.forEach(function (requestCapability) {
						requestCapability.reject(exception);
					});
					this._requests = [];
					this._cachedChunks = [];
				},
				_onProgress: function PDFNetworkStreamFullRequestReader_onProgress(data) {
					if (this.onProgress) {
						this.onProgress({
							loaded: data.loaded,
							total: data.lengthComputable ? data.total : this._contentLength
						});
					}
				},
				get isRangeSupported() {
					return this._isRangeSupported;
				},
				get isStreamingSupported() {
					return this._isStreamingSupported;
				},
				get contentLength() {
					return this._contentLength;
				},
				get headersReady() {
					return this._headersReceivedCapability.promise;
				},
				read: function PDFNetworkStreamFullRequestReader_read() {
					if (this._storedError) {
						return Promise.reject(this._storedError);
					}
					if (this._cachedChunks.length > 0) {
						var chunk = this._cachedChunks.shift();
						return Promise.resolve(chunk);
					}
					if (this._done) {
						return Promise.resolve({
							value: undefined,
							done: true
						});
					}
					var requestCapability = createPromiseCapability();
					this._requests.push(requestCapability);
					return requestCapability.promise;
				},
				cancel: function PDFNetworkStreamFullRequestReader_cancel(reason) {
					this._done = true;
					this._headersReceivedCapability.reject(reason);
					this._requests.forEach(function (requestCapability) {
						requestCapability.resolve({
							value: undefined,
							done: true
						});
					});
					this._requests = [];
					if (this._manager.isPendingRequest(this._fullRequestId)) {
						this._manager.abortRequest(this._fullRequestId);
					}
					this._fullRequestReader = null;
				}
			};
			function PDFNetworkStreamRangeRequestReader(manager, begin, end) {
				this._manager = manager;
				var args = {
					onDone: this._onDone.bind(this),
					onProgress: this._onProgress.bind(this)
				};
				this._requestId = manager.requestRange(begin, end, args);
				this._requests = [];
				this._queuedChunk = null;
				this._done = false;
				this.onProgress = null;
				this.onClosed = null;
			}
			PDFNetworkStreamRangeRequestReader.prototype = {
				_close: function PDFNetworkStreamRangeRequestReader_close() {
					if (this.onClosed) {
						this.onClosed(this);
					}
				},
				_onDone: function PDFNetworkStreamRangeRequestReader_onDone(data) {
					var chunk = data.chunk;
					if (this._requests.length > 0) {
						var requestCapability = this._requests.shift();
						requestCapability.resolve({
							value: chunk,
							done: false
						});
					} else {
						this._queuedChunk = chunk;
					}
					this._done = true;
					this._requests.forEach(function (requestCapability) {
						requestCapability.resolve({
							value: undefined,
							done: true
						});
					});
					this._requests = [];
					this._close();
				},
				_onProgress: function PDFNetworkStreamRangeRequestReader_onProgress(evt) {
					if (!this.isStreamingSupported && this.onProgress) {
						this.onProgress({ loaded: evt.loaded });
					}
				},
				get isStreamingSupported() {
					return false;
				},
				read: function PDFNetworkStreamRangeRequestReader_read() {
					if (this._queuedChunk !== null) {
						var chunk = this._queuedChunk;
						this._queuedChunk = null;
						return Promise.resolve({
							value: chunk,
							done: false
						});
					}
					if (this._done) {
						return Promise.resolve({
							value: undefined,
							done: true
						});
					}
					var requestCapability = createPromiseCapability();
					this._requests.push(requestCapability);
					return requestCapability.promise;
				},
				cancel: function PDFNetworkStreamRangeRequestReader_cancel(reason) {
					this._done = true;
					this._requests.forEach(function (requestCapability) {
						requestCapability.resolve({
							value: undefined,
							done: true
						});
					});
					this._requests = [];
					if (this._manager.isPendingRequest(this._requestId)) {
						this._manager.abortRequest(this._requestId);
					}
					this._close();
				}
			};
			coreWorker.setPDFNetworkStreamClass(PDFNetworkStream);
			exports.PDFNetworkStream = PDFNetworkStream;
			exports.NetworkManager = NetworkManager;

			/***/
		}),
/* 20 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var coreColorSpace = __w_pdfjs_require__(3);
			var coreObj = __w_pdfjs_require__(16);
			var coreEvaluator = __w_pdfjs_require__(14);
			var AnnotationBorderStyleType = sharedUtil.AnnotationBorderStyleType;
			var AnnotationFieldFlag = sharedUtil.AnnotationFieldFlag;
			var AnnotationFlag = sharedUtil.AnnotationFlag;
			var AnnotationType = sharedUtil.AnnotationType;
			var OPS = sharedUtil.OPS;
			var Util = sharedUtil.Util;
			var isArray = sharedUtil.isArray;
			var isInt = sharedUtil.isInt;
			var stringToBytes = sharedUtil.stringToBytes;
			var stringToPDFString = sharedUtil.stringToPDFString;
			var warn = sharedUtil.warn;
			var Dict = corePrimitives.Dict;
			var isDict = corePrimitives.isDict;
			var isName = corePrimitives.isName;
			var isRef = corePrimitives.isRef;
			var isStream = corePrimitives.isStream;
			var Stream = coreStream.Stream;
			var ColorSpace = coreColorSpace.ColorSpace;
			var Catalog = coreObj.Catalog;
			var ObjectLoader = coreObj.ObjectLoader;
			var FileSpec = coreObj.FileSpec;
			var OperatorList = coreEvaluator.OperatorList;
			function AnnotationFactory() {
			}
			AnnotationFactory.prototype = {
				create: function AnnotationFactory_create(xref, ref, pdfManager, idFactory) {
					var dict = xref.fetchIfRef(ref);
					if (!isDict(dict)) {
						return;
					}
					var id = isRef(ref) ? ref.toString() : 'annot_' + idFactory.createObjId();
					var subtype = dict.get('Subtype');
					subtype = isName(subtype) ? subtype.name : null;
					var parameters = {
						xref: xref,
						dict: dict,
						ref: isRef(ref) ? ref : null,
						subtype: subtype,
						id: id,
						pdfManager: pdfManager
					};
					switch (subtype) {
						case 'Link':
							return new LinkAnnotation(parameters);
						case 'Text':
							return new TextAnnotation(parameters);
						case 'Widget':
							var fieldType = Util.getInheritableProperty(dict, 'FT');
							fieldType = isName(fieldType) ? fieldType.name : null;
							switch (fieldType) {
								case 'Tx':
									return new TextWidgetAnnotation(parameters);
								case 'Btn':
									return new ButtonWidgetAnnotation(parameters);
								case 'Ch':
									return new ChoiceWidgetAnnotation(parameters);
							}
							warn('Unimplemented widget field type "' + fieldType + '", ' + 'falling back to base field type.');
							return new WidgetAnnotation(parameters);
						case 'Popup':
							return new PopupAnnotation(parameters);
						case 'Highlight':
							return new HighlightAnnotation(parameters);
						case 'Underline':
							return new UnderlineAnnotation(parameters);
						case 'Squiggly':
							return new SquigglyAnnotation(parameters);
						case 'StrikeOut':
							return new StrikeOutAnnotation(parameters);
						case 'FileAttachment':
							return new FileAttachmentAnnotation(parameters);
						default:
							if (!subtype) {
								warn('Annotation is missing the required /Subtype.');
							} else {
								warn('Unimplemented annotation type "' + subtype + '", ' + 'falling back to base annotation.');
							}
							return new Annotation(parameters);
					}
				}
			};
			var Annotation = function AnnotationClosure() {
				function getTransformMatrix(rect, bbox, matrix) {
					var bounds = Util.getAxialAlignedBoundingBox(bbox, matrix);
					var minX = bounds[0];
					var minY = bounds[1];
					var maxX = bounds[2];
					var maxY = bounds[3];
					if (minX === maxX || minY === maxY) {
						return [
							1,
							0,
							0,
							1,
							rect[0],
							rect[1]
						];
					}
					var xRatio = (rect[2] - rect[0]) / (maxX - minX);
					var yRatio = (rect[3] - rect[1]) / (maxY - minY);
					return [
						xRatio,
						0,
						0,
						yRatio,
						rect[0] - minX * xRatio,
						rect[1] - minY * yRatio
					];
				}
				function Annotation(params) {
					var dict = params.dict;
					this.setFlags(dict.get('F'));
					this.setRectangle(dict.getArray('Rect'));
					this.setColor(dict.getArray('C'));
					this.setBorderStyle(dict);
					this.setAppearance(dict);
					this.data = {};
					this.data.id = params.id;
					this.data.subtype = params.subtype;
					this.data.annotationFlags = this.flags;
					this.data.rect = this.rectangle;
					this.data.color = this.color;
					this.data.borderStyle = this.borderStyle;
					this.data.hasAppearance = !!this.appearance;
				}
				Annotation.prototype = {
					_hasFlag: function Annotation_hasFlag(flags, flag) {
						return !!(flags & flag);
					},
					_isViewable: function Annotation_isViewable(flags) {
						return !this._hasFlag(flags, AnnotationFlag.INVISIBLE) && !this._hasFlag(flags, AnnotationFlag.HIDDEN) && !this._hasFlag(flags, AnnotationFlag.NOVIEW);
					},
					_isPrintable: function AnnotationFlag_isPrintable(flags) {
						return this._hasFlag(flags, AnnotationFlag.PRINT) && !this._hasFlag(flags, AnnotationFlag.INVISIBLE) && !this._hasFlag(flags, AnnotationFlag.HIDDEN);
					},
					get viewable() {
						if (this.flags === 0) {
							return true;
						}
						return this._isViewable(this.flags);
					},
					get printable() {
						if (this.flags === 0) {
							return false;
						}
						return this._isPrintable(this.flags);
					},
					setFlags: function Annotation_setFlags(flags) {
						this.flags = isInt(flags) && flags > 0 ? flags : 0;
					},
					hasFlag: function Annotation_hasFlag(flag) {
						return this._hasFlag(this.flags, flag);
					},
					setRectangle: function Annotation_setRectangle(rectangle) {
						if (isArray(rectangle) && rectangle.length === 4) {
							this.rectangle = Util.normalizeRect(rectangle);
						} else {
							this.rectangle = [
								0,
								0,
								0,
								0
							];
						}
					},
					setColor: function Annotation_setColor(color) {
						var rgbColor = new Uint8Array(3);
						if (!isArray(color)) {
							this.color = rgbColor;
							return;
						}
						switch (color.length) {
							case 0:
								this.color = null;
								break;
							case 1:
								ColorSpace.singletons.gray.getRgbItem(color, 0, rgbColor, 0);
								this.color = rgbColor;
								break;
							case 3:
								ColorSpace.singletons.rgb.getRgbItem(color, 0, rgbColor, 0);
								this.color = rgbColor;
								break;
							case 4:
								ColorSpace.singletons.cmyk.getRgbItem(color, 0, rgbColor, 0);
								this.color = rgbColor;
								break;
							default:
								this.color = rgbColor;
								break;
						}
					},
					setBorderStyle: function Annotation_setBorderStyle(borderStyle) {
						this.borderStyle = new AnnotationBorderStyle();
						if (!isDict(borderStyle)) {
							return;
						}
						if (borderStyle.has('BS')) {
							var dict = borderStyle.get('BS');
							var dictType = dict.get('Type');
							if (!dictType || isName(dictType, 'Border')) {
								this.borderStyle.setWidth(dict.get('W'));
								this.borderStyle.setStyle(dict.get('S'));
								this.borderStyle.setDashArray(dict.getArray('D'));
							}
						} else if (borderStyle.has('Border')) {
							var array = borderStyle.getArray('Border');
							if (isArray(array) && array.length >= 3) {
								this.borderStyle.setHorizontalCornerRadius(array[0]);
								this.borderStyle.setVerticalCornerRadius(array[1]);
								this.borderStyle.setWidth(array[2]);
								if (array.length === 4) {
									this.borderStyle.setDashArray(array[3]);
								}
							}
						} else {
							this.borderStyle.setWidth(0);
						}
					},
					setAppearance: function Annotation_setAppearance(dict) {
						this.appearance = null;
						var appearanceStates = dict.get('AP');
						if (!isDict(appearanceStates)) {
							return;
						}
						var normalAppearanceState = appearanceStates.get('N');
						if (isStream(normalAppearanceState)) {
							this.appearance = normalAppearanceState;
							return;
						}
						if (!isDict(normalAppearanceState)) {
							return;
						}
						var as = dict.get('AS');
						if (!isName(as) || !normalAppearanceState.has(as.name)) {
							return;
						}
						this.appearance = normalAppearanceState.get(as.name);
					},
					_preparePopup: function Annotation_preparePopup(dict) {
						if (!dict.has('C')) {
							this.data.color = null;
						}
						this.data.hasPopup = dict.has('Popup');
						this.data.title = stringToPDFString(dict.get('T') || '');
						this.data.contents = stringToPDFString(dict.get('Contents') || '');
					},
					loadResources: function Annotation_loadResources(keys) {
						return new Promise(function (resolve, reject) {
							this.appearance.dict.getAsync('Resources').then(function (resources) {
								if (!resources) {
									resolve();
									return;
								}
								var objectLoader = new ObjectLoader(resources.map, keys, resources.xref);
								objectLoader.load().then(function () {
									resolve(resources);
								}, reject);
							}, reject);
						}.bind(this));
					},
					getOperatorList: function Annotation_getOperatorList(evaluator, task, renderForms) {
						if (!this.appearance) {
							return Promise.resolve(new OperatorList());
						}
						var data = this.data;
						var appearanceDict = this.appearance.dict;
						var resourcesPromise = this.loadResources([
							'ExtGState',
							'ColorSpace',
							'Pattern',
							'Shading',
							'XObject',
							'Font'
						]);
						var bbox = appearanceDict.getArray('BBox') || [
							0,
							0,
							1,
							1
						];
						var matrix = appearanceDict.getArray('Matrix') || [
							1,
							0,
							0,
							1,
							0,
							0
						];
						var transform = getTransformMatrix(data.rect, bbox, matrix);
						var self = this;
						return resourcesPromise.then(function (resources) {
							var opList = new OperatorList();
							opList.addOp(OPS.beginAnnotation, [
								data.rect,
								transform,
								matrix
							]);
							return evaluator.getOperatorList(self.appearance, task, resources, opList).then(function () {
								opList.addOp(OPS.endAnnotation, []);
								self.appearance.reset();
								return opList;
							});
						});
					}
				};
				return Annotation;
			}();
			var AnnotationBorderStyle = function AnnotationBorderStyleClosure() {
				function AnnotationBorderStyle() {
					this.width = 1;
					this.style = AnnotationBorderStyleType.SOLID;
					this.dashArray = [3];
					this.horizontalCornerRadius = 0;
					this.verticalCornerRadius = 0;
				}
				AnnotationBorderStyle.prototype = {
					setWidth: function AnnotationBorderStyle_setWidth(width) {
						if (width === (width | 0)) {
							this.width = width;
						}
					},
					setStyle: function AnnotationBorderStyle_setStyle(style) {
						if (!style) {
							return;
						}
						switch (style.name) {
							case 'S':
								this.style = AnnotationBorderStyleType.SOLID;
								break;
							case 'D':
								this.style = AnnotationBorderStyleType.DASHED;
								break;
							case 'B':
								this.style = AnnotationBorderStyleType.BEVELED;
								break;
							case 'I':
								this.style = AnnotationBorderStyleType.INSET;
								break;
							case 'U':
								this.style = AnnotationBorderStyleType.UNDERLINE;
								break;
							default:
								break;
						}
					},
					setDashArray: function AnnotationBorderStyle_setDashArray(dashArray) {
						if (isArray(dashArray) && dashArray.length > 0) {
							var isValid = true;
							var allZeros = true;
							for (var i = 0, len = dashArray.length; i < len; i++) {
								var element = dashArray[i];
								var validNumber = +element >= 0;
								if (!validNumber) {
									isValid = false;
									break;
								} else if (element > 0) {
									allZeros = false;
								}
							}
							if (isValid && !allZeros) {
								this.dashArray = dashArray;
							} else {
								this.width = 0;
							}
						} else if (dashArray) {
							this.width = 0;
						}
					},
					setHorizontalCornerRadius: function AnnotationBorderStyle_setHorizontalCornerRadius(radius) {
						if (radius === (radius | 0)) {
							this.horizontalCornerRadius = radius;
						}
					},
					setVerticalCornerRadius: function AnnotationBorderStyle_setVerticalCornerRadius(radius) {
						if (radius === (radius | 0)) {
							this.verticalCornerRadius = radius;
						}
					}
				};
				return AnnotationBorderStyle;
			}();
			var WidgetAnnotation = function WidgetAnnotationClosure() {
				function WidgetAnnotation(params) {
					Annotation.call(this, params);
					var dict = params.dict;
					var data = this.data;
					data.annotationType = AnnotationType.WIDGET;
					data.fieldName = this._constructFieldName(dict);
					data.fieldValue = Util.getInheritableProperty(dict, 'V', true);
					data.alternativeText = stringToPDFString(dict.get('TU') || '');
					data.defaultAppearance = Util.getInheritableProperty(dict, 'DA') || '';
					var fieldType = Util.getInheritableProperty(dict, 'FT');
					data.fieldType = isName(fieldType) ? fieldType.name : null;
					this.fieldResources = Util.getInheritableProperty(dict, 'DR') || Dict.empty;
					data.fieldFlags = Util.getInheritableProperty(dict, 'Ff');
					if (!isInt(data.fieldFlags) || data.fieldFlags < 0) {
						data.fieldFlags = 0;
					}
					data.readOnly = this.hasFieldFlag(AnnotationFieldFlag.READONLY);
					if (data.fieldType === 'Sig') {
						this.setFlags(AnnotationFlag.HIDDEN);
					}
				}
				Util.inherit(WidgetAnnotation, Annotation, {
					_constructFieldName: function WidgetAnnotation_constructFieldName(dict) {
						if (!dict.has('T') && !dict.has('Parent')) {
							warn('Unknown field name, falling back to empty field name.');
							return '';
						}
						if (!dict.has('Parent')) {
							return stringToPDFString(dict.get('T'));
						}
						var fieldName = [];
						if (dict.has('T')) {
							fieldName.unshift(stringToPDFString(dict.get('T')));
						}
						var loopDict = dict;
						while (loopDict.has('Parent')) {
							loopDict = loopDict.get('Parent');
							if (loopDict.has('T')) {
								fieldName.unshift(stringToPDFString(loopDict.get('T')));
							}
						}
						return fieldName.join('.');
					},
					hasFieldFlag: function WidgetAnnotation_hasFieldFlag(flag) {
						return !!(this.data.fieldFlags & flag);
					}
				});
				return WidgetAnnotation;
			}();
			var TextWidgetAnnotation = function TextWidgetAnnotationClosure() {
				function TextWidgetAnnotation(params) {
					WidgetAnnotation.call(this, params);
					this.data.fieldValue = stringToPDFString(this.data.fieldValue || '');
					var alignment = Util.getInheritableProperty(params.dict, 'Q');
					if (!isInt(alignment) || alignment < 0 || alignment > 2) {
						alignment = null;
					}
					this.data.textAlignment = alignment;
					var maximumLength = Util.getInheritableProperty(params.dict, 'MaxLen');
					if (!isInt(maximumLength) || maximumLength < 0) {
						maximumLength = null;
					}
					this.data.maxLen = maximumLength;
					this.data.multiLine = this.hasFieldFlag(AnnotationFieldFlag.MULTILINE);
					this.data.comb = this.hasFieldFlag(AnnotationFieldFlag.COMB) && !this.hasFieldFlag(AnnotationFieldFlag.MULTILINE) && !this.hasFieldFlag(AnnotationFieldFlag.PASSWORD) && !this.hasFieldFlag(AnnotationFieldFlag.FILESELECT) && this.data.maxLen !== null;
				}
				Util.inherit(TextWidgetAnnotation, WidgetAnnotation, {
					getOperatorList: function TextWidgetAnnotation_getOperatorList(evaluator, task, renderForms) {
						var operatorList = new OperatorList();
						if (renderForms) {
							return Promise.resolve(operatorList);
						}
						if (this.appearance) {
							return Annotation.prototype.getOperatorList.call(this, evaluator, task, renderForms);
						}
						if (!this.data.defaultAppearance) {
							return Promise.resolve(operatorList);
						}
						var stream = new Stream(stringToBytes(this.data.defaultAppearance));
						return evaluator.getOperatorList(stream, task, this.fieldResources, operatorList).then(function () {
							return operatorList;
						});
					}
				});
				return TextWidgetAnnotation;
			}();
			var ButtonWidgetAnnotation = function ButtonWidgetAnnotationClosure() {
				function ButtonWidgetAnnotation(params) {
					WidgetAnnotation.call(this, params);
					this.data.checkBox = !this.hasFieldFlag(AnnotationFieldFlag.RADIO) && !this.hasFieldFlag(AnnotationFieldFlag.PUSHBUTTON);
					if (this.data.checkBox) {
						if (!isName(this.data.fieldValue)) {
							return;
						}
						this.data.fieldValue = this.data.fieldValue.name;
					}
					this.data.radioButton = this.hasFieldFlag(AnnotationFieldFlag.RADIO) && !this.hasFieldFlag(AnnotationFieldFlag.PUSHBUTTON);
					if (this.data.radioButton) {
						this.data.fieldValue = this.data.buttonValue = null;
						var fieldParent = params.dict.get('Parent');
						if (isDict(fieldParent) && fieldParent.has('V')) {
							var fieldParentValue = fieldParent.get('V');
							if (isName(fieldParentValue)) {
								this.data.fieldValue = fieldParentValue.name;
							}
						}
						var appearanceStates = params.dict.get('AP');
						if (!isDict(appearanceStates)) {
							return;
						}
						var normalAppearanceState = appearanceStates.get('N');
						if (!isDict(normalAppearanceState)) {
							return;
						}
						var keys = normalAppearanceState.getKeys();
						for (var i = 0, ii = keys.length; i < ii; i++) {
							if (keys[i] !== 'Off') {
								this.data.buttonValue = keys[i];
								break;
							}
						}
					}
				}
				Util.inherit(ButtonWidgetAnnotation, WidgetAnnotation, {
					getOperatorList: function ButtonWidgetAnnotation_getOperatorList(evaluator, task, renderForms) {
						var operatorList = new OperatorList();
						if (renderForms) {
							return Promise.resolve(operatorList);
						}
						if (this.appearance) {
							return Annotation.prototype.getOperatorList.call(this, evaluator, task, renderForms);
						}
						return Promise.resolve(operatorList);
					}
				});
				return ButtonWidgetAnnotation;
			}();
			var ChoiceWidgetAnnotation = function ChoiceWidgetAnnotationClosure() {
				function ChoiceWidgetAnnotation(params) {
					WidgetAnnotation.call(this, params);
					this.data.options = [];
					var options = Util.getInheritableProperty(params.dict, 'Opt');
					if (isArray(options)) {
						var xref = params.xref;
						for (var i = 0, ii = options.length; i < ii; i++) {
							var option = xref.fetchIfRef(options[i]);
							var isOptionArray = isArray(option);
							this.data.options[i] = {
								exportValue: isOptionArray ? xref.fetchIfRef(option[0]) : option,
								displayValue: isOptionArray ? xref.fetchIfRef(option[1]) : option
							};
						}
					}
					if (!isArray(this.data.fieldValue)) {
						this.data.fieldValue = [this.data.fieldValue];
					}
					this.data.combo = this.hasFieldFlag(AnnotationFieldFlag.COMBO);
					this.data.multiSelect = this.hasFieldFlag(AnnotationFieldFlag.MULTISELECT);
				}
				Util.inherit(ChoiceWidgetAnnotation, WidgetAnnotation, {
					getOperatorList: function ChoiceWidgetAnnotation_getOperatorList(evaluator, task, renderForms) {
						var operatorList = new OperatorList();
						if (renderForms) {
							return Promise.resolve(operatorList);
						}
						return Annotation.prototype.getOperatorList.call(this, evaluator, task, renderForms);
					}
				});
				return ChoiceWidgetAnnotation;
			}();
			var TextAnnotation = function TextAnnotationClosure() {
				var DEFAULT_ICON_SIZE = 22;
				function TextAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.TEXT;
					if (this.data.hasAppearance) {
						this.data.name = 'NoIcon';
					} else {
						this.data.rect[1] = this.data.rect[3] - DEFAULT_ICON_SIZE;
						this.data.rect[2] = this.data.rect[0] + DEFAULT_ICON_SIZE;
						this.data.name = parameters.dict.has('Name') ? parameters.dict.get('Name').name : 'Note';
					}
					this._preparePopup(parameters.dict);
				}
				Util.inherit(TextAnnotation, Annotation, {});
				return TextAnnotation;
			}();
			var LinkAnnotation = function LinkAnnotationClosure() {
				function LinkAnnotation(params) {
					Annotation.call(this, params);
					var data = this.data;
					data.annotationType = AnnotationType.LINK;
					Catalog.parseDestDictionary({
						destDict: params.dict,
						resultObj: data,
						docBaseUrl: params.pdfManager.docBaseUrl
					});
				}
				Util.inherit(LinkAnnotation, Annotation, {});
				return LinkAnnotation;
			}();
			var PopupAnnotation = function PopupAnnotationClosure() {
				function PopupAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.POPUP;
					var dict = parameters.dict;
					var parentItem = dict.get('Parent');
					if (!parentItem) {
						warn('Popup annotation has a missing or invalid parent annotation.');
						return;
					}
					this.data.parentId = dict.getRaw('Parent').toString();
					this.data.title = stringToPDFString(parentItem.get('T') || '');
					this.data.contents = stringToPDFString(parentItem.get('Contents') || '');
					if (!parentItem.has('C')) {
						this.data.color = null;
					} else {
						this.setColor(parentItem.getArray('C'));
						this.data.color = this.color;
					}
					if (!this.viewable) {
						var parentFlags = parentItem.get('F');
						if (this._isViewable(parentFlags)) {
							this.setFlags(parentFlags);
						}
					}
				}
				Util.inherit(PopupAnnotation, Annotation, {});
				return PopupAnnotation;
			}();
			var HighlightAnnotation = function HighlightAnnotationClosure() {
				function HighlightAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.HIGHLIGHT;
					this._preparePopup(parameters.dict);
					this.data.borderStyle.setWidth(0);
				}
				Util.inherit(HighlightAnnotation, Annotation, {});
				return HighlightAnnotation;
			}();
			var UnderlineAnnotation = function UnderlineAnnotationClosure() {
				function UnderlineAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.UNDERLINE;
					this._preparePopup(parameters.dict);
					this.data.borderStyle.setWidth(0);
				}
				Util.inherit(UnderlineAnnotation, Annotation, {});
				return UnderlineAnnotation;
			}();
			var SquigglyAnnotation = function SquigglyAnnotationClosure() {
				function SquigglyAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.SQUIGGLY;
					this._preparePopup(parameters.dict);
					this.data.borderStyle.setWidth(0);
				}
				Util.inherit(SquigglyAnnotation, Annotation, {});
				return SquigglyAnnotation;
			}();
			var StrikeOutAnnotation = function StrikeOutAnnotationClosure() {
				function StrikeOutAnnotation(parameters) {
					Annotation.call(this, parameters);
					this.data.annotationType = AnnotationType.STRIKEOUT;
					this._preparePopup(parameters.dict);
					this.data.borderStyle.setWidth(0);
				}
				Util.inherit(StrikeOutAnnotation, Annotation, {});
				return StrikeOutAnnotation;
			}();
			var FileAttachmentAnnotation = function FileAttachmentAnnotationClosure() {
				function FileAttachmentAnnotation(parameters) {
					Annotation.call(this, parameters);
					var file = new FileSpec(parameters.dict.get('FS'), parameters.xref);
					this.data.annotationType = AnnotationType.FILEATTACHMENT;
					this.data.file = file.serializable;
					this._preparePopup(parameters.dict);
				}
				Util.inherit(FileAttachmentAnnotation, Annotation, {});
				return FileAttachmentAnnotation;
			}();
			exports.Annotation = Annotation;
			exports.AnnotationBorderStyle = AnnotationBorderStyle;
			exports.AnnotationFactory = AnnotationFactory;

			/***/
		}),
/* 21 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var warn = sharedUtil.warn;
			var baseTypes = [
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'S',
				'B',
				'S',
				'WS',
				'B',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'B',
				'B',
				'B',
				'S',
				'WS',
				'ON',
				'ON',
				'ET',
				'ET',
				'ET',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'ES',
				'CS',
				'ES',
				'CS',
				'CS',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'CS',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'ON',
				'ON',
				'ON',
				'ON',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'B',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'BN',
				'CS',
				'ON',
				'ET',
				'ET',
				'ET',
				'ET',
				'ON',
				'ON',
				'ON',
				'ON',
				'L',
				'ON',
				'ON',
				'BN',
				'ON',
				'ON',
				'ET',
				'ET',
				'EN',
				'EN',
				'ON',
				'L',
				'ON',
				'ON',
				'ON',
				'EN',
				'L',
				'ON',
				'ON',
				'ON',
				'ON',
				'ON',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'ON',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'ON',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L',
				'L'
			];
			var arabicTypes = [
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'ON',
				'ON',
				'AL',
				'ET',
				'ET',
				'AL',
				'CS',
				'AL',
				'ON',
				'ON',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'AL',
				'AL',
				'',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'AN',
				'ET',
				'AN',
				'AN',
				'AL',
				'AL',
				'AL',
				'NSM',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'AN',
				'ON',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'AL',
				'AL',
				'NSM',
				'NSM',
				'ON',
				'NSM',
				'NSM',
				'NSM',
				'NSM',
				'AL',
				'AL',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'EN',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL',
				'AL'
			];
			function isOdd(i) {
				return (i & 1) !== 0;
			}
			function isEven(i) {
				return (i & 1) === 0;
			}
			function findUnequal(arr, start, value) {
				for (var j = start, jj = arr.length; j < jj; ++j) {
					if (arr[j] !== value) {
						return j;
					}
				}
				return j;
			}
			function setValues(arr, start, end, value) {
				for (var j = start; j < end; ++j) {
					arr[j] = value;
				}
			}
			function reverseValues(arr, start, end) {
				for (var i = start, j = end - 1; i < j; ++i, --j) {
					var temp = arr[i];
					arr[i] = arr[j];
					arr[j] = temp;
				}
			}
			function createBidiText(str, isLTR, vertical) {
				return {
					str: str,
					dir: vertical ? 'ttb' : isLTR ? 'ltr' : 'rtl'
				};
			}
			var chars = [];
			var types = [];
			function bidi(str, startLevel, vertical) {
				var isLTR = true;
				var strLength = str.length;
				if (strLength === 0 || vertical) {
					return createBidiText(str, isLTR, vertical);
				}
				chars.length = strLength;
				types.length = strLength;
				var numBidi = 0;
				var i, ii;
				for (i = 0; i < strLength; ++i) {
					chars[i] = str.charAt(i);
					var charCode = str.charCodeAt(i);
					var charType = 'L';
					if (charCode <= 0x00ff) {
						charType = baseTypes[charCode];
					} else if (0x0590 <= charCode && charCode <= 0x05f4) {
						charType = 'R';
					} else if (0x0600 <= charCode && charCode <= 0x06ff) {
						charType = arabicTypes[charCode & 0xff];
						if (!charType) {
							warn('Bidi: invalid Unicode character ' + charCode.toString(16));
						}
					} else if (0x0700 <= charCode && charCode <= 0x08AC) {
						charType = 'AL';
					}
					if (charType === 'R' || charType === 'AL' || charType === 'AN') {
						numBidi++;
					}
					types[i] = charType;
				}
				if (numBidi === 0) {
					isLTR = true;
					return createBidiText(str, isLTR);
				}
				if (startLevel === -1) {
					if (numBidi / strLength < 0.3) {
						isLTR = true;
						startLevel = 0;
					} else {
						isLTR = false;
						startLevel = 1;
					}
				}
				var levels = [];
				for (i = 0; i < strLength; ++i) {
					levels[i] = startLevel;
				}
				var e = isOdd(startLevel) ? 'R' : 'L';
				var sor = e;
				var eor = sor;
				var lastType = sor;
				for (i = 0; i < strLength; ++i) {
					if (types[i] === 'NSM') {
						types[i] = lastType;
					} else {
						lastType = types[i];
					}
				}
				lastType = sor;
				var t;
				for (i = 0; i < strLength; ++i) {
					t = types[i];
					if (t === 'EN') {
						types[i] = lastType === 'AL' ? 'AN' : 'EN';
					} else if (t === 'R' || t === 'L' || t === 'AL') {
						lastType = t;
					}
				}
				for (i = 0; i < strLength; ++i) {
					t = types[i];
					if (t === 'AL') {
						types[i] = 'R';
					}
				}
				for (i = 1; i < strLength - 1; ++i) {
					if (types[i] === 'ES' && types[i - 1] === 'EN' && types[i + 1] === 'EN') {
						types[i] = 'EN';
					}
					if (types[i] === 'CS' && (types[i - 1] === 'EN' || types[i - 1] === 'AN') && types[i + 1] === types[i - 1]) {
						types[i] = types[i - 1];
					}
				}
				for (i = 0; i < strLength; ++i) {
					if (types[i] === 'EN') {
						var j;
						for (j = i - 1; j >= 0; --j) {
							if (types[j] !== 'ET') {
								break;
							}
							types[j] = 'EN';
						}
						for (j = i + 1; j < strLength; ++j) {
							if (types[j] !== 'ET') {
								break;
							}
							types[j] = 'EN';
						}
					}
				}
				for (i = 0; i < strLength; ++i) {
					t = types[i];
					if (t === 'WS' || t === 'ES' || t === 'ET' || t === 'CS') {
						types[i] = 'ON';
					}
				}
				lastType = sor;
				for (i = 0; i < strLength; ++i) {
					t = types[i];
					if (t === 'EN') {
						types[i] = lastType === 'L' ? 'L' : 'EN';
					} else if (t === 'R' || t === 'L') {
						lastType = t;
					}
				}
				for (i = 0; i < strLength; ++i) {
					if (types[i] === 'ON') {
						var end = findUnequal(types, i + 1, 'ON');
						var before = sor;
						if (i > 0) {
							before = types[i - 1];
						}
						var after = eor;
						if (end + 1 < strLength) {
							after = types[end + 1];
						}
						if (before !== 'L') {
							before = 'R';
						}
						if (after !== 'L') {
							after = 'R';
						}
						if (before === after) {
							setValues(types, i, end, before);
						}
						i = end - 1;
					}
				}
				for (i = 0; i < strLength; ++i) {
					if (types[i] === 'ON') {
						types[i] = e;
					}
				}
				for (i = 0; i < strLength; ++i) {
					t = types[i];
					if (isEven(levels[i])) {
						if (t === 'R') {
							levels[i] += 1;
						} else if (t === 'AN' || t === 'EN') {
							levels[i] += 2;
						}
					} else {
						if (t === 'L' || t === 'AN' || t === 'EN') {
							levels[i] += 1;
						}
					}
				}
				var highestLevel = -1;
				var lowestOddLevel = 99;
				var level;
				for (i = 0, ii = levels.length; i < ii; ++i) {
					level = levels[i];
					if (highestLevel < level) {
						highestLevel = level;
					}
					if (lowestOddLevel > level && isOdd(level)) {
						lowestOddLevel = level;
					}
				}
				for (level = highestLevel; level >= lowestOddLevel; --level) {
					var start = -1;
					for (i = 0, ii = levels.length; i < ii; ++i) {
						if (levels[i] < level) {
							if (start >= 0) {
								reverseValues(chars, start, i);
								start = -1;
							}
						} else if (start < 0) {
							start = i;
						}
					}
					if (start >= 0) {
						reverseValues(chars, start, levels.length);
					}
				}
				for (i = 0, ii = chars.length; i < ii; ++i) {
					var ch = chars[i];
					if (ch === '<' || ch === '>') {
						chars[i] = '';
					}
				}
				return createBidiText(chars.join(''), isLTR);
			}
			exports.bidi = bidi;

			/***/
		}),
/* 22 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var ISOAdobeCharset = [
				'.notdef',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quoteright',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'quoteleft',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'exclamdown',
				'cent',
				'sterling',
				'fraction',
				'yen',
				'florin',
				'section',
				'currency',
				'quotesingle',
				'quotedblleft',
				'guillemotleft',
				'guilsinglleft',
				'guilsinglright',
				'fi',
				'fl',
				'endash',
				'dagger',
				'daggerdbl',
				'periodcentered',
				'paragraph',
				'bullet',
				'quotesinglbase',
				'quotedblbase',
				'quotedblright',
				'guillemotright',
				'ellipsis',
				'perthousand',
				'questiondown',
				'grave',
				'acute',
				'circumflex',
				'tilde',
				'macron',
				'breve',
				'dotaccent',
				'dieresis',
				'ring',
				'cedilla',
				'hungarumlaut',
				'ogonek',
				'caron',
				'emdash',
				'AE',
				'ordfeminine',
				'Lslash',
				'Oslash',
				'OE',
				'ordmasculine',
				'ae',
				'dotlessi',
				'lslash',
				'oslash',
				'oe',
				'germandbls',
				'onesuperior',
				'logicalnot',
				'mu',
				'trademark',
				'Eth',
				'onehalf',
				'plusminus',
				'Thorn',
				'onequarter',
				'divide',
				'brokenbar',
				'degree',
				'thorn',
				'threequarters',
				'twosuperior',
				'registered',
				'minus',
				'eth',
				'multiply',
				'threesuperior',
				'copyright',
				'Aacute',
				'Acircumflex',
				'Adieresis',
				'Agrave',
				'Aring',
				'Atilde',
				'Ccedilla',
				'Eacute',
				'Ecircumflex',
				'Edieresis',
				'Egrave',
				'Iacute',
				'Icircumflex',
				'Idieresis',
				'Igrave',
				'Ntilde',
				'Oacute',
				'Ocircumflex',
				'Odieresis',
				'Ograve',
				'Otilde',
				'Scaron',
				'Uacute',
				'Ucircumflex',
				'Udieresis',
				'Ugrave',
				'Yacute',
				'Ydieresis',
				'Zcaron',
				'aacute',
				'acircumflex',
				'adieresis',
				'agrave',
				'aring',
				'atilde',
				'ccedilla',
				'eacute',
				'ecircumflex',
				'edieresis',
				'egrave',
				'iacute',
				'icircumflex',
				'idieresis',
				'igrave',
				'ntilde',
				'oacute',
				'ocircumflex',
				'odieresis',
				'ograve',
				'otilde',
				'scaron',
				'uacute',
				'ucircumflex',
				'udieresis',
				'ugrave',
				'yacute',
				'ydieresis',
				'zcaron'
			];
			var ExpertCharset = [
				'.notdef',
				'space',
				'exclamsmall',
				'Hungarumlautsmall',
				'dollaroldstyle',
				'dollarsuperior',
				'ampersandsmall',
				'Acutesmall',
				'parenleftsuperior',
				'parenrightsuperior',
				'twodotenleader',
				'onedotenleader',
				'comma',
				'hyphen',
				'period',
				'fraction',
				'zerooldstyle',
				'oneoldstyle',
				'twooldstyle',
				'threeoldstyle',
				'fouroldstyle',
				'fiveoldstyle',
				'sixoldstyle',
				'sevenoldstyle',
				'eightoldstyle',
				'nineoldstyle',
				'colon',
				'semicolon',
				'commasuperior',
				'threequartersemdash',
				'periodsuperior',
				'questionsmall',
				'asuperior',
				'bsuperior',
				'centsuperior',
				'dsuperior',
				'esuperior',
				'isuperior',
				'lsuperior',
				'msuperior',
				'nsuperior',
				'osuperior',
				'rsuperior',
				'ssuperior',
				'tsuperior',
				'ff',
				'fi',
				'fl',
				'ffi',
				'ffl',
				'parenleftinferior',
				'parenrightinferior',
				'Circumflexsmall',
				'hyphensuperior',
				'Gravesmall',
				'Asmall',
				'Bsmall',
				'Csmall',
				'Dsmall',
				'Esmall',
				'Fsmall',
				'Gsmall',
				'Hsmall',
				'Ismall',
				'Jsmall',
				'Ksmall',
				'Lsmall',
				'Msmall',
				'Nsmall',
				'Osmall',
				'Psmall',
				'Qsmall',
				'Rsmall',
				'Ssmall',
				'Tsmall',
				'Usmall',
				'Vsmall',
				'Wsmall',
				'Xsmall',
				'Ysmall',
				'Zsmall',
				'colonmonetary',
				'onefitted',
				'rupiah',
				'Tildesmall',
				'exclamdownsmall',
				'centoldstyle',
				'Lslashsmall',
				'Scaronsmall',
				'Zcaronsmall',
				'Dieresissmall',
				'Brevesmall',
				'Caronsmall',
				'Dotaccentsmall',
				'Macronsmall',
				'figuredash',
				'hypheninferior',
				'Ogoneksmall',
				'Ringsmall',
				'Cedillasmall',
				'onequarter',
				'onehalf',
				'threequarters',
				'questiondownsmall',
				'oneeighth',
				'threeeighths',
				'fiveeighths',
				'seveneighths',
				'onethird',
				'twothirds',
				'zerosuperior',
				'onesuperior',
				'twosuperior',
				'threesuperior',
				'foursuperior',
				'fivesuperior',
				'sixsuperior',
				'sevensuperior',
				'eightsuperior',
				'ninesuperior',
				'zeroinferior',
				'oneinferior',
				'twoinferior',
				'threeinferior',
				'fourinferior',
				'fiveinferior',
				'sixinferior',
				'seveninferior',
				'eightinferior',
				'nineinferior',
				'centinferior',
				'dollarinferior',
				'periodinferior',
				'commainferior',
				'Agravesmall',
				'Aacutesmall',
				'Acircumflexsmall',
				'Atildesmall',
				'Adieresissmall',
				'Aringsmall',
				'AEsmall',
				'Ccedillasmall',
				'Egravesmall',
				'Eacutesmall',
				'Ecircumflexsmall',
				'Edieresissmall',
				'Igravesmall',
				'Iacutesmall',
				'Icircumflexsmall',
				'Idieresissmall',
				'Ethsmall',
				'Ntildesmall',
				'Ogravesmall',
				'Oacutesmall',
				'Ocircumflexsmall',
				'Otildesmall',
				'Odieresissmall',
				'OEsmall',
				'Oslashsmall',
				'Ugravesmall',
				'Uacutesmall',
				'Ucircumflexsmall',
				'Udieresissmall',
				'Yacutesmall',
				'Thornsmall',
				'Ydieresissmall'
			];
			var ExpertSubsetCharset = [
				'.notdef',
				'space',
				'dollaroldstyle',
				'dollarsuperior',
				'parenleftsuperior',
				'parenrightsuperior',
				'twodotenleader',
				'onedotenleader',
				'comma',
				'hyphen',
				'period',
				'fraction',
				'zerooldstyle',
				'oneoldstyle',
				'twooldstyle',
				'threeoldstyle',
				'fouroldstyle',
				'fiveoldstyle',
				'sixoldstyle',
				'sevenoldstyle',
				'eightoldstyle',
				'nineoldstyle',
				'colon',
				'semicolon',
				'commasuperior',
				'threequartersemdash',
				'periodsuperior',
				'asuperior',
				'bsuperior',
				'centsuperior',
				'dsuperior',
				'esuperior',
				'isuperior',
				'lsuperior',
				'msuperior',
				'nsuperior',
				'osuperior',
				'rsuperior',
				'ssuperior',
				'tsuperior',
				'ff',
				'fi',
				'fl',
				'ffi',
				'ffl',
				'parenleftinferior',
				'parenrightinferior',
				'hyphensuperior',
				'colonmonetary',
				'onefitted',
				'rupiah',
				'centoldstyle',
				'figuredash',
				'hypheninferior',
				'onequarter',
				'onehalf',
				'threequarters',
				'oneeighth',
				'threeeighths',
				'fiveeighths',
				'seveneighths',
				'onethird',
				'twothirds',
				'zerosuperior',
				'onesuperior',
				'twosuperior',
				'threesuperior',
				'foursuperior',
				'fivesuperior',
				'sixsuperior',
				'sevensuperior',
				'eightsuperior',
				'ninesuperior',
				'zeroinferior',
				'oneinferior',
				'twoinferior',
				'threeinferior',
				'fourinferior',
				'fiveinferior',
				'sixinferior',
				'seveninferior',
				'eightinferior',
				'nineinferior',
				'centinferior',
				'dollarinferior',
				'periodinferior',
				'commainferior'
			];
			exports.ISOAdobeCharset = ISOAdobeCharset;
			exports.ExpertCharset = ExpertCharset;
			exports.ExpertSubsetCharset = ExpertSubsetCharset;

			/***/
		}),
/* 23 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var coreParser = __w_pdfjs_require__(5);
			var Util = sharedUtil.Util;
			var assert = sharedUtil.assert;
			var warn = sharedUtil.warn;
			var error = sharedUtil.error;
			var isInt = sharedUtil.isInt;
			var isString = sharedUtil.isString;
			var MissingDataException = sharedUtil.MissingDataException;
			var CMapCompressionType = sharedUtil.CMapCompressionType;
			var isEOF = corePrimitives.isEOF;
			var isName = corePrimitives.isName;
			var isCmd = corePrimitives.isCmd;
			var isStream = corePrimitives.isStream;
			var Stream = coreStream.Stream;
			var Lexer = coreParser.Lexer;
			var BUILT_IN_CMAPS = [
				'Adobe-GB1-UCS2',
				'Adobe-CNS1-UCS2',
				'Adobe-Japan1-UCS2',
				'Adobe-Korea1-UCS2',
				'78-EUC-H',
				'78-EUC-V',
				'78-H',
				'78-RKSJ-H',
				'78-RKSJ-V',
				'78-V',
				'78ms-RKSJ-H',
				'78ms-RKSJ-V',
				'83pv-RKSJ-H',
				'90ms-RKSJ-H',
				'90ms-RKSJ-V',
				'90msp-RKSJ-H',
				'90msp-RKSJ-V',
				'90pv-RKSJ-H',
				'90pv-RKSJ-V',
				'Add-H',
				'Add-RKSJ-H',
				'Add-RKSJ-V',
				'Add-V',
				'Adobe-CNS1-0',
				'Adobe-CNS1-1',
				'Adobe-CNS1-2',
				'Adobe-CNS1-3',
				'Adobe-CNS1-4',
				'Adobe-CNS1-5',
				'Adobe-CNS1-6',
				'Adobe-GB1-0',
				'Adobe-GB1-1',
				'Adobe-GB1-2',
				'Adobe-GB1-3',
				'Adobe-GB1-4',
				'Adobe-GB1-5',
				'Adobe-Japan1-0',
				'Adobe-Japan1-1',
				'Adobe-Japan1-2',
				'Adobe-Japan1-3',
				'Adobe-Japan1-4',
				'Adobe-Japan1-5',
				'Adobe-Japan1-6',
				'Adobe-Korea1-0',
				'Adobe-Korea1-1',
				'Adobe-Korea1-2',
				'B5-H',
				'B5-V',
				'B5pc-H',
				'B5pc-V',
				'CNS-EUC-H',
				'CNS-EUC-V',
				'CNS1-H',
				'CNS1-V',
				'CNS2-H',
				'CNS2-V',
				'ETHK-B5-H',
				'ETHK-B5-V',
				'ETen-B5-H',
				'ETen-B5-V',
				'ETenms-B5-H',
				'ETenms-B5-V',
				'EUC-H',
				'EUC-V',
				'Ext-H',
				'Ext-RKSJ-H',
				'Ext-RKSJ-V',
				'Ext-V',
				'GB-EUC-H',
				'GB-EUC-V',
				'GB-H',
				'GB-V',
				'GBK-EUC-H',
				'GBK-EUC-V',
				'GBK2K-H',
				'GBK2K-V',
				'GBKp-EUC-H',
				'GBKp-EUC-V',
				'GBT-EUC-H',
				'GBT-EUC-V',
				'GBT-H',
				'GBT-V',
				'GBTpc-EUC-H',
				'GBTpc-EUC-V',
				'GBpc-EUC-H',
				'GBpc-EUC-V',
				'H',
				'HKdla-B5-H',
				'HKdla-B5-V',
				'HKdlb-B5-H',
				'HKdlb-B5-V',
				'HKgccs-B5-H',
				'HKgccs-B5-V',
				'HKm314-B5-H',
				'HKm314-B5-V',
				'HKm471-B5-H',
				'HKm471-B5-V',
				'HKscs-B5-H',
				'HKscs-B5-V',
				'Hankaku',
				'Hiragana',
				'KSC-EUC-H',
				'KSC-EUC-V',
				'KSC-H',
				'KSC-Johab-H',
				'KSC-Johab-V',
				'KSC-V',
				'KSCms-UHC-H',
				'KSCms-UHC-HW-H',
				'KSCms-UHC-HW-V',
				'KSCms-UHC-V',
				'KSCpc-EUC-H',
				'KSCpc-EUC-V',
				'Katakana',
				'NWP-H',
				'NWP-V',
				'RKSJ-H',
				'RKSJ-V',
				'Roman',
				'UniCNS-UCS2-H',
				'UniCNS-UCS2-V',
				'UniCNS-UTF16-H',
				'UniCNS-UTF16-V',
				'UniCNS-UTF32-H',
				'UniCNS-UTF32-V',
				'UniCNS-UTF8-H',
				'UniCNS-UTF8-V',
				'UniGB-UCS2-H',
				'UniGB-UCS2-V',
				'UniGB-UTF16-H',
				'UniGB-UTF16-V',
				'UniGB-UTF32-H',
				'UniGB-UTF32-V',
				'UniGB-UTF8-H',
				'UniGB-UTF8-V',
				'UniJIS-UCS2-H',
				'UniJIS-UCS2-HW-H',
				'UniJIS-UCS2-HW-V',
				'UniJIS-UCS2-V',
				'UniJIS-UTF16-H',
				'UniJIS-UTF16-V',
				'UniJIS-UTF32-H',
				'UniJIS-UTF32-V',
				'UniJIS-UTF8-H',
				'UniJIS-UTF8-V',
				'UniJIS2004-UTF16-H',
				'UniJIS2004-UTF16-V',
				'UniJIS2004-UTF32-H',
				'UniJIS2004-UTF32-V',
				'UniJIS2004-UTF8-H',
				'UniJIS2004-UTF8-V',
				'UniJISPro-UCS2-HW-V',
				'UniJISPro-UCS2-V',
				'UniJISPro-UTF8-V',
				'UniJISX0213-UTF32-H',
				'UniJISX0213-UTF32-V',
				'UniJISX02132004-UTF32-H',
				'UniJISX02132004-UTF32-V',
				'UniKS-UCS2-H',
				'UniKS-UCS2-V',
				'UniKS-UTF16-H',
				'UniKS-UTF16-V',
				'UniKS-UTF32-H',
				'UniKS-UTF32-V',
				'UniKS-UTF8-H',
				'UniKS-UTF8-V',
				'V',
				'WP-Symbol'
			];
			var CMap = function CMapClosure() {
				function CMap(builtInCMap) {
					this.codespaceRanges = [
						[],
						[],
						[],
						[]
					];
					this.numCodespaceRanges = 0;
					this._map = [];
					this.name = '';
					this.vertical = false;
					this.useCMap = null;
					this.builtInCMap = builtInCMap;
				}
				CMap.prototype = {
					addCodespaceRange: function (n, low, high) {
						this.codespaceRanges[n - 1].push(low, high);
						this.numCodespaceRanges++;
					},
					mapCidRange: function (low, high, dstLow) {
						while (low <= high) {
							this._map[low++] = dstLow++;
						}
					},
					mapBfRange: function (low, high, dstLow) {
						var lastByte = dstLow.length - 1;
						while (low <= high) {
							this._map[low++] = dstLow;
							dstLow = dstLow.substr(0, lastByte) + String.fromCharCode(dstLow.charCodeAt(lastByte) + 1);
						}
					},
					mapBfRangeToArray: function (low, high, array) {
						var i = 0, ii = array.length;
						while (low <= high && i < ii) {
							this._map[low] = array[i++];
							++low;
						}
					},
					mapOne: function (src, dst) {
						this._map[src] = dst;
					},
					lookup: function (code) {
						return this._map[code];
					},
					contains: function (code) {
						return this._map[code] !== undefined;
					},
					forEach: function (callback) {
						var map = this._map;
						var length = map.length;
						var i;
						if (length <= 0x10000) {
							for (i = 0; i < length; i++) {
								if (map[i] !== undefined) {
									callback(i, map[i]);
								}
							}
						} else {
							for (i in this._map) {
								callback(i, map[i]);
							}
						}
					},
					charCodeOf: function (value) {
						return this._map.indexOf(value);
					},
					getMap: function () {
						return this._map;
					},
					readCharCode: function (str, offset, out) {
						var c = 0;
						var codespaceRanges = this.codespaceRanges;
						var codespaceRangesLen = this.codespaceRanges.length;
						for (var n = 0; n < codespaceRangesLen; n++) {
							c = (c << 8 | str.charCodeAt(offset + n)) >>> 0;
							var codespaceRange = codespaceRanges[n];
							for (var k = 0, kk = codespaceRange.length; k < kk;) {
								var low = codespaceRange[k++];
								var high = codespaceRange[k++];
								if (c >= low && c <= high) {
									out.charcode = c;
									out.length = n + 1;
									return;
								}
							}
						}
						out.charcode = 0;
						out.length = 1;
					},
					get length() {
						return this._map.length;
					},
					get isIdentityCMap() {
						if (!(this.name === 'Identity-H' || this.name === 'Identity-V')) {
							return false;
						}
						if (this._map.length !== 0x10000) {
							return false;
						}
						for (var i = 0; i < 0x10000; i++) {
							if (this._map[i] !== i) {
								return false;
							}
						}
						return true;
					}
				};
				return CMap;
			}();
			var IdentityCMap = function IdentityCMapClosure() {
				function IdentityCMap(vertical, n) {
					CMap.call(this);
					this.vertical = vertical;
					this.addCodespaceRange(n, 0, 0xffff);
				}
				Util.inherit(IdentityCMap, CMap, {});
				IdentityCMap.prototype = {
					addCodespaceRange: CMap.prototype.addCodespaceRange,
					mapCidRange: function (low, high, dstLow) {
						error('should not call mapCidRange');
					},
					mapBfRange: function (low, high, dstLow) {
						error('should not call mapBfRange');
					},
					mapBfRangeToArray: function (low, high, array) {
						error('should not call mapBfRangeToArray');
					},
					mapOne: function (src, dst) {
						error('should not call mapCidOne');
					},
					lookup: function (code) {
						return isInt(code) && code <= 0xffff ? code : undefined;
					},
					contains: function (code) {
						return isInt(code) && code <= 0xffff;
					},
					forEach: function (callback) {
						for (var i = 0; i <= 0xffff; i++) {
							callback(i, i);
						}
					},
					charCodeOf: function (value) {
						return isInt(value) && value <= 0xffff ? value : -1;
					},
					getMap: function () {
						var map = new Array(0x10000);
						for (var i = 0; i <= 0xffff; i++) {
							map[i] = i;
						}
						return map;
					},
					readCharCode: CMap.prototype.readCharCode,
					get length() {
						return 0x10000;
					},
					get isIdentityCMap() {
						error('should not access .isIdentityCMap');
					}
				};
				return IdentityCMap;
			}();
			var BinaryCMapReader = function BinaryCMapReaderClosure() {
				function hexToInt(a, size) {
					var n = 0;
					for (var i = 0; i <= size; i++) {
						n = n << 8 | a[i];
					}
					return n >>> 0;
				}
				function hexToStr(a, size) {
					if (size === 1) {
						return String.fromCharCode(a[0], a[1]);
					}
					if (size === 3) {
						return String.fromCharCode(a[0], a[1], a[2], a[3]);
					}
					return String.fromCharCode.apply(null, a.subarray(0, size + 1));
				}
				function addHex(a, b, size) {
					var c = 0;
					for (var i = size; i >= 0; i--) {
						c += a[i] + b[i];
						a[i] = c & 255;
						c >>= 8;
					}
				}
				function incHex(a, size) {
					var c = 1;
					for (var i = size; i >= 0 && c > 0; i--) {
						c += a[i];
						a[i] = c & 255;
						c >>= 8;
					}
				}
				var MAX_NUM_SIZE = 16;
				var MAX_ENCODED_NUM_SIZE = 19;
				function BinaryCMapStream(data) {
					this.buffer = data;
					this.pos = 0;
					this.end = data.length;
					this.tmpBuf = new Uint8Array(MAX_ENCODED_NUM_SIZE);
				}
				BinaryCMapStream.prototype = {
					readByte: function () {
						if (this.pos >= this.end) {
							return -1;
						}
						return this.buffer[this.pos++];
					},
					readNumber: function () {
						var n = 0;
						var last;
						do {
							var b = this.readByte();
							if (b < 0) {
								error('unexpected EOF in bcmap');
							}
							last = !(b & 0x80);
							n = n << 7 | b & 0x7F;
						} while (!last);
						return n;
					},
					readSigned: function () {
						var n = this.readNumber();
						return n & 1 ? ~(n >>> 1) : n >>> 1;
					},
					readHex: function (num, size) {
						num.set(this.buffer.subarray(this.pos, this.pos + size + 1));
						this.pos += size + 1;
					},
					readHexNumber: function (num, size) {
						var last;
						var stack = this.tmpBuf, sp = 0;
						do {
							var b = this.readByte();
							if (b < 0) {
								error('unexpected EOF in bcmap');
							}
							last = !(b & 0x80);
							stack[sp++] = b & 0x7F;
						} while (!last);
						var i = size, buffer = 0, bufferSize = 0;
						while (i >= 0) {
							while (bufferSize < 8 && stack.length > 0) {
								buffer = stack[--sp] << bufferSize | buffer;
								bufferSize += 7;
							}
							num[i] = buffer & 255;
							i--;
							buffer >>= 8;
							bufferSize -= 8;
						}
					},
					readHexSigned: function (num, size) {
						this.readHexNumber(num, size);
						var sign = num[size] & 1 ? 255 : 0;
						var c = 0;
						for (var i = 0; i <= size; i++) {
							c = (c & 1) << 8 | num[i];
							num[i] = c >> 1 ^ sign;
						}
					},
					readString: function () {
						var len = this.readNumber();
						var s = '';
						for (var i = 0; i < len; i++) {
							s += String.fromCharCode(this.readNumber());
						}
						return s;
					}
				};
				function processBinaryCMap(data, cMap, extend) {
					return new Promise(function (resolve, reject) {
						var stream = new BinaryCMapStream(data);
						var header = stream.readByte();
						cMap.vertical = !!(header & 1);
						var useCMap = null;
						var start = new Uint8Array(MAX_NUM_SIZE);
						var end = new Uint8Array(MAX_NUM_SIZE);
						var char = new Uint8Array(MAX_NUM_SIZE);
						var charCode = new Uint8Array(MAX_NUM_SIZE);
						var tmp = new Uint8Array(MAX_NUM_SIZE);
						var code;
						var b;
						while ((b = stream.readByte()) >= 0) {
							var type = b >> 5;
							if (type === 7) {
								switch (b & 0x1F) {
									case 0:
										stream.readString();
										break;
									case 1:
										useCMap = stream.readString();
										break;
								}
								continue;
							}
							var sequence = !!(b & 0x10);
							var dataSize = b & 15;
							assert(dataSize + 1 <= MAX_NUM_SIZE);
							var ucs2DataSize = 1;
							var subitemsCount = stream.readNumber();
							var i;
							switch (type) {
								case 0:
									stream.readHex(start, dataSize);
									stream.readHexNumber(end, dataSize);
									addHex(end, start, dataSize);
									cMap.addCodespaceRange(dataSize + 1, hexToInt(start, dataSize), hexToInt(end, dataSize));
									for (i = 1; i < subitemsCount; i++) {
										incHex(end, dataSize);
										stream.readHexNumber(start, dataSize);
										addHex(start, end, dataSize);
										stream.readHexNumber(end, dataSize);
										addHex(end, start, dataSize);
										cMap.addCodespaceRange(dataSize + 1, hexToInt(start, dataSize), hexToInt(end, dataSize));
									}
									break;
								case 1:
									stream.readHex(start, dataSize);
									stream.readHexNumber(end, dataSize);
									addHex(end, start, dataSize);
									code = stream.readNumber();
									for (i = 1; i < subitemsCount; i++) {
										incHex(end, dataSize);
										stream.readHexNumber(start, dataSize);
										addHex(start, end, dataSize);
										stream.readHexNumber(end, dataSize);
										addHex(end, start, dataSize);
										code = stream.readNumber();
									}
									break;
								case 2:
									stream.readHex(char, dataSize);
									code = stream.readNumber();
									cMap.mapOne(hexToInt(char, dataSize), code);
									for (i = 1; i < subitemsCount; i++) {
										incHex(char, dataSize);
										if (!sequence) {
											stream.readHexNumber(tmp, dataSize);
											addHex(char, tmp, dataSize);
										}
										code = stream.readSigned() + (code + 1);
										cMap.mapOne(hexToInt(char, dataSize), code);
									}
									break;
								case 3:
									stream.readHex(start, dataSize);
									stream.readHexNumber(end, dataSize);
									addHex(end, start, dataSize);
									code = stream.readNumber();
									cMap.mapCidRange(hexToInt(start, dataSize), hexToInt(end, dataSize), code);
									for (i = 1; i < subitemsCount; i++) {
										incHex(end, dataSize);
										if (!sequence) {
											stream.readHexNumber(start, dataSize);
											addHex(start, end, dataSize);
										} else {
											start.set(end);
										}
										stream.readHexNumber(end, dataSize);
										addHex(end, start, dataSize);
										code = stream.readNumber();
										cMap.mapCidRange(hexToInt(start, dataSize), hexToInt(end, dataSize), code);
									}
									break;
								case 4:
									stream.readHex(char, ucs2DataSize);
									stream.readHex(charCode, dataSize);
									cMap.mapOne(hexToInt(char, ucs2DataSize), hexToStr(charCode, dataSize));
									for (i = 1; i < subitemsCount; i++) {
										incHex(char, ucs2DataSize);
										if (!sequence) {
											stream.readHexNumber(tmp, ucs2DataSize);
											addHex(char, tmp, ucs2DataSize);
										}
										incHex(charCode, dataSize);
										stream.readHexSigned(tmp, dataSize);
										addHex(charCode, tmp, dataSize);
										cMap.mapOne(hexToInt(char, ucs2DataSize), hexToStr(charCode, dataSize));
									}
									break;
								case 5:
									stream.readHex(start, ucs2DataSize);
									stream.readHexNumber(end, ucs2DataSize);
									addHex(end, start, ucs2DataSize);
									stream.readHex(charCode, dataSize);
									cMap.mapBfRange(hexToInt(start, ucs2DataSize), hexToInt(end, ucs2DataSize), hexToStr(charCode, dataSize));
									for (i = 1; i < subitemsCount; i++) {
										incHex(end, ucs2DataSize);
										if (!sequence) {
											stream.readHexNumber(start, ucs2DataSize);
											addHex(start, end, ucs2DataSize);
										} else {
											start.set(end);
										}
										stream.readHexNumber(end, ucs2DataSize);
										addHex(end, start, ucs2DataSize);
										stream.readHex(charCode, dataSize);
										cMap.mapBfRange(hexToInt(start, ucs2DataSize), hexToInt(end, ucs2DataSize), hexToStr(charCode, dataSize));
									}
									break;
								default:
									reject(new Error('processBinaryCMap: Unknown type: ' + type));
									return;
							}
						}
						if (useCMap) {
							resolve(extend(useCMap));
							return;
						}
						resolve(cMap);
					});
				}
				function BinaryCMapReader() {
				}
				BinaryCMapReader.prototype = { process: processBinaryCMap };
				return BinaryCMapReader;
			}();
			var CMapFactory = function CMapFactoryClosure() {
				function strToInt(str) {
					var a = 0;
					for (var i = 0; i < str.length; i++) {
						a = a << 8 | str.charCodeAt(i);
					}
					return a >>> 0;
				}
				function expectString(obj) {
					if (!isString(obj)) {
						error('Malformed CMap: expected string.');
					}
				}
				function expectInt(obj) {
					if (!isInt(obj)) {
						error('Malformed CMap: expected int.');
					}
				}
				function parseBfChar(cMap, lexer) {
					while (true) {
						var obj = lexer.getObj();
						if (isEOF(obj)) {
							break;
						}
						if (isCmd(obj, 'endbfchar')) {
							return;
						}
						expectString(obj);
						var src = strToInt(obj);
						obj = lexer.getObj();
						expectString(obj);
						var dst = obj;
						cMap.mapOne(src, dst);
					}
				}
				function parseBfRange(cMap, lexer) {
					while (true) {
						var obj = lexer.getObj();
						if (isEOF(obj)) {
							break;
						}
						if (isCmd(obj, 'endbfrange')) {
							return;
						}
						expectString(obj);
						var low = strToInt(obj);
						obj = lexer.getObj();
						expectString(obj);
						var high = strToInt(obj);
						obj = lexer.getObj();
						if (isInt(obj) || isString(obj)) {
							var dstLow = isInt(obj) ? String.fromCharCode(obj) : obj;
							cMap.mapBfRange(low, high, dstLow);
						} else if (isCmd(obj, '[')) {
							obj = lexer.getObj();
							var array = [];
							while (!isCmd(obj, ']') && !isEOF(obj)) {
								array.push(obj);
								obj = lexer.getObj();
							}
							cMap.mapBfRangeToArray(low, high, array);
						} else {
							break;
						}
					}
					error('Invalid bf range.');
				}
				function parseCidChar(cMap, lexer) {
					while (true) {
						var obj = lexer.getObj();
						if (isEOF(obj)) {
							break;
						}
						if (isCmd(obj, 'endcidchar')) {
							return;
						}
						expectString(obj);
						var src = strToInt(obj);
						obj = lexer.getObj();
						expectInt(obj);
						var dst = obj;
						cMap.mapOne(src, dst);
					}
				}
				function parseCidRange(cMap, lexer) {
					while (true) {
						var obj = lexer.getObj();
						if (isEOF(obj)) {
							break;
						}
						if (isCmd(obj, 'endcidrange')) {
							return;
						}
						expectString(obj);
						var low = strToInt(obj);
						obj = lexer.getObj();
						expectString(obj);
						var high = strToInt(obj);
						obj = lexer.getObj();
						expectInt(obj);
						var dstLow = obj;
						cMap.mapCidRange(low, high, dstLow);
					}
				}
				function parseCodespaceRange(cMap, lexer) {
					while (true) {
						var obj = lexer.getObj();
						if (isEOF(obj)) {
							break;
						}
						if (isCmd(obj, 'endcodespacerange')) {
							return;
						}
						if (!isString(obj)) {
							break;
						}
						var low = strToInt(obj);
						obj = lexer.getObj();
						if (!isString(obj)) {
							break;
						}
						var high = strToInt(obj);
						cMap.addCodespaceRange(obj.length, low, high);
					}
					error('Invalid codespace range.');
				}
				function parseWMode(cMap, lexer) {
					var obj = lexer.getObj();
					if (isInt(obj)) {
						cMap.vertical = !!obj;
					}
				}
				function parseCMapName(cMap, lexer) {
					var obj = lexer.getObj();
					if (isName(obj) && isString(obj.name)) {
						cMap.name = obj.name;
					}
				}
				function parseCMap(cMap, lexer, fetchBuiltInCMap, useCMap) {
					var previous;
					var embededUseCMap;
					objLoop:
					while (true) {
						try {
							var obj = lexer.getObj();
							if (isEOF(obj)) {
								break;
							} else if (isName(obj)) {
								if (obj.name === 'WMode') {
									parseWMode(cMap, lexer);
								} else if (obj.name === 'CMapName') {
									parseCMapName(cMap, lexer);
								}
								previous = obj;
							} else if (isCmd(obj)) {
								switch (obj.cmd) {
									case 'endcmap':
										break objLoop;
									case 'usecmap':
										if (isName(previous)) {
											embededUseCMap = previous.name;
										}
										break;
									case 'begincodespacerange':
										parseCodespaceRange(cMap, lexer);
										break;
									case 'beginbfchar':
										parseBfChar(cMap, lexer);
										break;
									case 'begincidchar':
										parseCidChar(cMap, lexer);
										break;
									case 'beginbfrange':
										parseBfRange(cMap, lexer);
										break;
									case 'begincidrange':
										parseCidRange(cMap, lexer);
										break;
								}
							}
						} catch (ex) {
							if (ex instanceof MissingDataException) {
								throw ex;
							}
							warn('Invalid cMap data: ' + ex);
							continue;
						}
					}
					if (!useCMap && embededUseCMap) {
						useCMap = embededUseCMap;
					}
					if (useCMap) {
						return extendCMap(cMap, fetchBuiltInCMap, useCMap);
					}
					return Promise.resolve(cMap);
				}
				function extendCMap(cMap, fetchBuiltInCMap, useCMap) {
					return createBuiltInCMap(useCMap, fetchBuiltInCMap).then(function (newCMap) {
						cMap.useCMap = newCMap;
						if (cMap.numCodespaceRanges === 0) {
							var useCodespaceRanges = cMap.useCMap.codespaceRanges;
							for (var i = 0; i < useCodespaceRanges.length; i++) {
								cMap.codespaceRanges[i] = useCodespaceRanges[i].slice();
							}
							cMap.numCodespaceRanges = cMap.useCMap.numCodespaceRanges;
						}
						cMap.useCMap.forEach(function (key, value) {
							if (!cMap.contains(key)) {
								cMap.mapOne(key, cMap.useCMap.lookup(key));
							}
						});
						return cMap;
					});
				}
				function createBuiltInCMap(name, fetchBuiltInCMap) {
					if (name === 'Identity-H') {
						return Promise.resolve(new IdentityCMap(false, 2));
					} else if (name === 'Identity-V') {
						return Promise.resolve(new IdentityCMap(true, 2));
					}
					if (BUILT_IN_CMAPS.indexOf(name) === -1) {
						return Promise.reject(new Error('Unknown cMap name: ' + name));
					}
					assert(fetchBuiltInCMap, 'Built-in CMap parameters are not provided.');
					return fetchBuiltInCMap(name).then(function (data) {
						var cMapData = data.cMapData, compressionType = data.compressionType;
						var cMap = new CMap(true);
						if (compressionType === CMapCompressionType.BINARY) {
							return new BinaryCMapReader().process(cMapData, cMap, function (useCMap) {
								return extendCMap(cMap, fetchBuiltInCMap, useCMap);
							});
						}
						assert(compressionType === CMapCompressionType.NONE, 'TODO: Only BINARY/NONE CMap compression is currently supported.');
						var lexer = new Lexer(new Stream(cMapData));
						return parseCMap(cMap, lexer, fetchBuiltInCMap, null);
					});
				}
				return {
					create: function (params) {
						var encoding = params.encoding;
						var fetchBuiltInCMap = params.fetchBuiltInCMap;
						var useCMap = params.useCMap;
						if (isName(encoding)) {
							return createBuiltInCMap(encoding.name, fetchBuiltInCMap);
						} else if (isStream(encoding)) {
							var cMap = new CMap();
							var lexer = new Lexer(encoding);
							return parseCMap(cMap, lexer, fetchBuiltInCMap, useCMap).then(function (parsedCMap) {
								if (parsedCMap.isIdentityCMap) {
									return createBuiltInCMap(parsedCMap.name, fetchBuiltInCMap);
								}
								return parsedCMap;
							});
						}
						return Promise.reject(new Error('Encoding required.'));
					}
				};
			}();
			exports.CMap = CMap;
			exports.CMapFactory = CMapFactory;
			exports.IdentityCMap = IdentityCMap;

			/***/
		}),
/* 24 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var coreObj = __w_pdfjs_require__(16);
			var coreParser = __w_pdfjs_require__(5);
			var coreCrypto = __w_pdfjs_require__(13);
			var coreEvaluator = __w_pdfjs_require__(14);
			var coreAnnotation = __w_pdfjs_require__(20);
			var OPS = sharedUtil.OPS;
			var MissingDataException = sharedUtil.MissingDataException;
			var Util = sharedUtil.Util;
			var assert = sharedUtil.assert;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isArrayBuffer = sharedUtil.isArrayBuffer;
			var isNum = sharedUtil.isNum;
			var isString = sharedUtil.isString;
			var shadow = sharedUtil.shadow;
			var stringToBytes = sharedUtil.stringToBytes;
			var stringToPDFString = sharedUtil.stringToPDFString;
			var warn = sharedUtil.warn;
			var isSpace = sharedUtil.isSpace;
			var Dict = corePrimitives.Dict;
			var isDict = corePrimitives.isDict;
			var isName = corePrimitives.isName;
			var isStream = corePrimitives.isStream;
			var NullStream = coreStream.NullStream;
			var Stream = coreStream.Stream;
			var StreamsSequenceStream = coreStream.StreamsSequenceStream;
			var Catalog = coreObj.Catalog;
			var ObjectLoader = coreObj.ObjectLoader;
			var XRef = coreObj.XRef;
			var Linearization = coreParser.Linearization;
			var calculateMD5 = coreCrypto.calculateMD5;
			var OperatorList = coreEvaluator.OperatorList;
			var PartialEvaluator = coreEvaluator.PartialEvaluator;
			var AnnotationFactory = coreAnnotation.AnnotationFactory;
			var Page = function PageClosure() {
				var DEFAULT_USER_UNIT = 1.0;
				var LETTER_SIZE_MEDIABOX = [
					0,
					0,
					612,
					792
				];
				function isAnnotationRenderable(annotation, intent) {
					return intent === 'display' && annotation.viewable || intent === 'print' && annotation.printable;
				}
				function Page(pdfManager, xref, pageIndex, pageDict, ref, fontCache, builtInCMapCache) {
					this.pdfManager = pdfManager;
					this.pageIndex = pageIndex;
					this.pageDict = pageDict;
					this.xref = xref;
					this.ref = ref;
					this.fontCache = fontCache;
					this.builtInCMapCache = builtInCMapCache;
					this.evaluatorOptions = pdfManager.evaluatorOptions;
					this.resourcesPromise = null;
					var uniquePrefix = 'p' + this.pageIndex + '_';
					var idCounters = { obj: 0 };
					this.idFactory = {
						createObjId: function () {
							return uniquePrefix + ++idCounters.obj;
						}
					};
				}
				Page.prototype = {
					getPageProp: function Page_getPageProp(key) {
						return this.pageDict.get(key);
					},
					getInheritedPageProp: function Page_getInheritedPageProp(key, getArray) {
						var dict = this.pageDict, valueArray = null, loopCount = 0;
						var MAX_LOOP_COUNT = 100;
						getArray = getArray || false;
						while (dict) {
							var value = getArray ? dict.getArray(key) : dict.get(key);
							if (value !== undefined) {
								if (!valueArray) {
									valueArray = [];
								}
								valueArray.push(value);
							}
							if (++loopCount > MAX_LOOP_COUNT) {
								warn('getInheritedPageProp: maximum loop count exceeded for ' + key);
								return valueArray ? valueArray[0] : undefined;
							}
							dict = dict.get('Parent');
						}
						if (!valueArray) {
							return undefined;
						}
						if (valueArray.length === 1 || !isDict(valueArray[0])) {
							return valueArray[0];
						}
						return Dict.merge(this.xref, valueArray);
					},
					get content() {
						return this.getPageProp('Contents');
					},
					get resources() {
						return shadow(this, 'resources', this.getInheritedPageProp('Resources') || Dict.empty);
					},
					get mediaBox() {
						var mediaBox = this.getInheritedPageProp('MediaBox', true);
						if (!isArray(mediaBox) || mediaBox.length !== 4) {
							return shadow(this, 'mediaBox', LETTER_SIZE_MEDIABOX);
						}
						return shadow(this, 'mediaBox', mediaBox);
					},
					get cropBox() {
						var cropBox = this.getInheritedPageProp('CropBox', true);
						if (!isArray(cropBox) || cropBox.length !== 4) {
							return shadow(this, 'cropBox', this.mediaBox);
						}
						return shadow(this, 'cropBox', cropBox);
					},
					get userUnit() {
						var obj = this.getPageProp('UserUnit');
						if (!isNum(obj) || obj <= 0) {
							obj = DEFAULT_USER_UNIT;
						}
						return shadow(this, 'userUnit', obj);
					},
					get view() {
						var mediaBox = this.mediaBox, cropBox = this.cropBox;
						if (mediaBox === cropBox) {
							return shadow(this, 'view', mediaBox);
						}
						var intersection = Util.intersect(cropBox, mediaBox);
						return shadow(this, 'view', intersection || mediaBox);
					},
					get rotate() {
						var rotate = this.getInheritedPageProp('Rotate') || 0;
						if (rotate % 90 !== 0) {
							rotate = 0;
						} else if (rotate >= 360) {
							rotate = rotate % 360;
						} else if (rotate < 0) {
							rotate = (rotate % 360 + 360) % 360;
						}
						return shadow(this, 'rotate', rotate);
					},
					getContentStream: function Page_getContentStream() {
						var content = this.content;
						var stream;
						if (isArray(content)) {
							var xref = this.xref;
							var i, n = content.length;
							var streams = [];
							for (i = 0; i < n; ++i) {
								streams.push(xref.fetchIfRef(content[i]));
							}
							stream = new StreamsSequenceStream(streams);
						} else if (isStream(content)) {
							stream = content;
						} else {
							stream = new NullStream();
						}
						return stream;
					},
					loadResources: function Page_loadResources(keys) {
						if (!this.resourcesPromise) {
							this.resourcesPromise = this.pdfManager.ensure(this, 'resources');
						}
						return this.resourcesPromise.then(function resourceSuccess() {
							var objectLoader = new ObjectLoader(this.resources.map, keys, this.xref);
							return objectLoader.load();
						}.bind(this));
					},
					getOperatorList: function Page_getOperatorList(handler, task, intent, renderInteractiveForms) {
						var self = this;
						var pdfManager = this.pdfManager;
						var contentStreamPromise = pdfManager.ensure(this, 'getContentStream', []);
						var resourcesPromise = this.loadResources([
							'ExtGState',
							'ColorSpace',
							'Pattern',
							'Shading',
							'XObject',
							'Font'
						]);
						var partialEvaluator = new PartialEvaluator(pdfManager, this.xref, handler, this.pageIndex, this.idFactory, this.fontCache, this.builtInCMapCache, this.evaluatorOptions);
						var dataPromises = Promise.all([
							contentStreamPromise,
							resourcesPromise
						]);
						var pageListPromise = dataPromises.then(function (data) {
							var contentStream = data[0];
							var opList = new OperatorList(intent, handler, self.pageIndex);
							handler.send('StartRenderPage', {
								transparency: partialEvaluator.hasBlendModes(self.resources),
								pageIndex: self.pageIndex,
								intent: intent
							});
							return partialEvaluator.getOperatorList(contentStream, task, self.resources, opList).then(function () {
								return opList;
							});
						});
						var annotationsPromise = pdfManager.ensure(this, 'annotations');
						return Promise.all([
							pageListPromise,
							annotationsPromise
						]).then(function (datas) {
							var pageOpList = datas[0];
							var annotations = datas[1];
							if (annotations.length === 0) {
								pageOpList.flush(true);
								return pageOpList;
							}
							var i, ii, opListPromises = [];
							for (i = 0, ii = annotations.length; i < ii; i++) {
								if (isAnnotationRenderable(annotations[i], intent)) {
									opListPromises.push(annotations[i].getOperatorList(partialEvaluator, task, renderInteractiveForms));
								}
							}
							return Promise.all(opListPromises).then(function (opLists) {
								pageOpList.addOp(OPS.beginAnnotations, []);
								for (i = 0, ii = opLists.length; i < ii; i++) {
									pageOpList.addOpList(opLists[i]);
								}
								pageOpList.addOp(OPS.endAnnotations, []);
								pageOpList.flush(true);
								return pageOpList;
							});
						});
					},
					extractTextContent: function Page_extractTextContent(task, normalizeWhitespace, combineTextItems) {
						var handler = {
							on: function nullHandlerOn() {
							},
							send: function nullHandlerSend() {
							}
						};
						var self = this;
						var pdfManager = this.pdfManager;
						var contentStreamPromise = pdfManager.ensure(this, 'getContentStream', []);
						var resourcesPromise = this.loadResources([
							'ExtGState',
							'XObject',
							'Font'
						]);
						var dataPromises = Promise.all([
							contentStreamPromise,
							resourcesPromise
						]);
						return dataPromises.then(function (data) {
							var contentStream = data[0];
							var partialEvaluator = new PartialEvaluator(pdfManager, self.xref, handler, self.pageIndex, self.idFactory, self.fontCache, self.builtInCMapCache, self.evaluatorOptions);
							return partialEvaluator.getTextContent(contentStream, task, self.resources, null, normalizeWhitespace, combineTextItems);
						});
					},
					getAnnotationsData: function Page_getAnnotationsData(intent) {
						var annotations = this.annotations;
						var annotationsData = [];
						for (var i = 0, n = annotations.length; i < n; ++i) {
							if (!intent || isAnnotationRenderable(annotations[i], intent)) {
								annotationsData.push(annotations[i].data);
							}
						}
						return annotationsData;
					},
					get annotations() {
						var annotations = [];
						var annotationRefs = this.getInheritedPageProp('Annots') || [];
						var annotationFactory = new AnnotationFactory();
						for (var i = 0, n = annotationRefs.length; i < n; ++i) {
							var annotationRef = annotationRefs[i];
							var annotation = annotationFactory.create(this.xref, annotationRef, this.pdfManager, this.idFactory);
							if (annotation) {
								annotations.push(annotation);
							}
						}
						return shadow(this, 'annotations', annotations);
					}
				};
				return Page;
			}();
			var PDFDocument = function PDFDocumentClosure() {
				var FINGERPRINT_FIRST_BYTES = 1024;
				var EMPTY_FINGERPRINT = '\x00\x00\x00\x00\x00\x00\x00' + '\x00\x00\x00\x00\x00\x00\x00\x00\x00';
				function PDFDocument(pdfManager, arg) {
					var stream;
					if (isStream(arg)) {
						stream = arg;
					} else if (isArrayBuffer(arg)) {
						stream = new Stream(arg);
					} else {
						error('PDFDocument: Unknown argument type');
					}
					assert(stream.length > 0, 'stream must have data');
					this.pdfManager = pdfManager;
					this.stream = stream;
					this.xref = new XRef(stream, pdfManager);
				}
				function find(stream, needle, limit, backwards) {
					var pos = stream.pos;
					var end = stream.end;
					var strBuf = [];
					if (pos + limit > end) {
						limit = end - pos;
					}
					for (var n = 0; n < limit; ++n) {
						strBuf.push(String.fromCharCode(stream.getByte()));
					}
					var str = strBuf.join('');
					stream.pos = pos;
					var index = backwards ? str.lastIndexOf(needle) : str.indexOf(needle);
					if (index === -1) {
						return false;
					}
					stream.pos += index;
					return true;
				}
				var DocumentInfoValidators = {
					get entries() {
						return shadow(this, 'entries', {
							Title: isString,
							Author: isString,
							Subject: isString,
							Keywords: isString,
							Creator: isString,
							Producer: isString,
							CreationDate: isString,
							ModDate: isString,
							Trapped: isName
						});
					}
				};
				PDFDocument.prototype = {
					parse: function PDFDocument_parse(recoveryMode) {
						this.setup(recoveryMode);
						var version = this.catalog.catDict.get('Version');
						if (isName(version)) {
							this.pdfFormatVersion = version.name;
						}
						try {
							this.acroForm = this.catalog.catDict.get('AcroForm');
							if (this.acroForm) {
								this.xfa = this.acroForm.get('XFA');
								var fields = this.acroForm.get('Fields');
								if ((!fields || !isArray(fields) || fields.length === 0) && !this.xfa) {
									this.acroForm = null;
								}
							}
						} catch (ex) {
							info('Something wrong with AcroForm entry');
							this.acroForm = null;
						}
					},
					get linearization() {
						var linearization = null;
						if (this.stream.length) {
							try {
								linearization = Linearization.create(this.stream);
							} catch (err) {
								if (err instanceof MissingDataException) {
									throw err;
								}
								info(err);
							}
						}
						return shadow(this, 'linearization', linearization);
					},
					get startXRef() {
						var stream = this.stream;
						var startXRef = 0;
						var linearization = this.linearization;
						if (linearization) {
							stream.reset();
							if (find(stream, 'endobj', 1024)) {
								startXRef = stream.pos + 6;
							}
						} else {
							var step = 1024;
							var found = false, pos = stream.end;
							while (!found && pos > 0) {
								pos -= step - 'startxref'.length;
								if (pos < 0) {
									pos = 0;
								}
								stream.pos = pos;
								found = find(stream, 'startxref', step, true);
							}
							if (found) {
								stream.skip(9);
								var ch;
								do {
									ch = stream.getByte();
								} while (isSpace(ch));
								var str = '';
								while (ch >= 0x20 && ch <= 0x39) {
									str += String.fromCharCode(ch);
									ch = stream.getByte();
								}
								startXRef = parseInt(str, 10);
								if (isNaN(startXRef)) {
									startXRef = 0;
								}
							}
						}
						return shadow(this, 'startXRef', startXRef);
					},
					get mainXRefEntriesOffset() {
						var mainXRefEntriesOffset = 0;
						var linearization = this.linearization;
						if (linearization) {
							mainXRefEntriesOffset = linearization.mainXRefEntriesOffset;
						}
						return shadow(this, 'mainXRefEntriesOffset', mainXRefEntriesOffset);
					},
					checkHeader: function PDFDocument_checkHeader() {
						var stream = this.stream;
						stream.reset();
						if (find(stream, '%PDF-', 1024)) {
							stream.moveStart();
							var MAX_VERSION_LENGTH = 12;
							var version = '', ch;
							while ((ch = stream.getByte()) > 0x20) {
								if (version.length >= MAX_VERSION_LENGTH) {
									break;
								}
								version += String.fromCharCode(ch);
							}
							if (!this.pdfFormatVersion) {
								this.pdfFormatVersion = version.substring(5);
							}
							return;
						}
					},
					parseStartXRef: function PDFDocument_parseStartXRef() {
						var startXRef = this.startXRef;
						this.xref.setStartXRef(startXRef);
					},
					setup: function PDFDocument_setup(recoveryMode) {
						this.xref.parse(recoveryMode);
						var self = this;
						var pageFactory = {
							createPage: function (pageIndex, dict, ref, fontCache, builtInCMapCache) {
								return new Page(self.pdfManager, self.xref, pageIndex, dict, ref, fontCache, builtInCMapCache);
							}
						};
						this.catalog = new Catalog(this.pdfManager, this.xref, pageFactory);
					},
					get numPages() {
						var linearization = this.linearization;
						var num = linearization ? linearization.numPages : this.catalog.numPages;
						return shadow(this, 'numPages', num);
					},
					get documentInfo() {
						var docInfo = {
							PDFFormatVersion: this.pdfFormatVersion,
							IsAcroFormPresent: !!this.acroForm,
							IsXFAPresent: !!this.xfa
						};
						var infoDict;
						try {
							infoDict = this.xref.trailer.get('Info');
						} catch (err) {
							info('The document information dictionary is invalid.');
						}
						if (infoDict) {
							var validEntries = DocumentInfoValidators.entries;
							for (var key in validEntries) {
								if (infoDict.has(key)) {
									var value = infoDict.get(key);
									if (validEntries[key](value)) {
										docInfo[key] = typeof value !== 'string' ? value : stringToPDFString(value);
									} else {
										info('Bad value in document info for "' + key + '"');
									}
								}
							}
						}
						return shadow(this, 'documentInfo', docInfo);
					},
					get fingerprint() {
						var xref = this.xref, hash, fileID = '';
						var idArray = xref.trailer.get('ID');
						if (idArray && isArray(idArray) && idArray[0] && isString(idArray[0]) && idArray[0] !== EMPTY_FINGERPRINT) {
							hash = stringToBytes(idArray[0]);
						} else {
							if (this.stream.ensureRange) {
								this.stream.ensureRange(0, Math.min(FINGERPRINT_FIRST_BYTES, this.stream.end));
							}
							hash = calculateMD5(this.stream.bytes.subarray(0, FINGERPRINT_FIRST_BYTES), 0, FINGERPRINT_FIRST_BYTES);
						}
						for (var i = 0, n = hash.length; i < n; i++) {
							var hex = hash[i].toString(16);
							fileID += hex.length === 1 ? '0' + hex : hex;
						}
						return shadow(this, 'fingerprint', fileID);
					},
					getPage: function PDFDocument_getPage(pageIndex) {
						return this.catalog.getPage(pageIndex);
					},
					cleanup: function PDFDocument_cleanup() {
						return this.catalog.cleanup();
					}
				};
				return PDFDocument;
			}();
			exports.Page = Page;
			exports.PDFDocument = PDFDocument;

			/***/
		}),
/* 25 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreStream = __w_pdfjs_require__(2);
			var coreGlyphList = __w_pdfjs_require__(7);
			var coreEncodings = __w_pdfjs_require__(4);
			var coreCFFParser = __w_pdfjs_require__(11);
			var Util = sharedUtil.Util;
			var bytesToString = sharedUtil.bytesToString;
			var error = sharedUtil.error;
			var Stream = coreStream.Stream;
			var getGlyphsUnicode = coreGlyphList.getGlyphsUnicode;
			var StandardEncoding = coreEncodings.StandardEncoding;
			var CFFParser = coreCFFParser.CFFParser;
			var FontRendererFactory = function FontRendererFactoryClosure() {
				function getLong(data, offset) {
					return data[offset] << 24 | data[offset + 1] << 16 | data[offset + 2] << 8 | data[offset + 3];
				}
				function getUshort(data, offset) {
					return data[offset] << 8 | data[offset + 1];
				}
				function parseCmap(data, start, end) {
					var offset = getUshort(data, start + 2) === 1 ? getLong(data, start + 8) : getLong(data, start + 16);
					var format = getUshort(data, start + offset);
					var ranges, p, i;
					if (format === 4) {
						getUshort(data, start + offset + 2);
						var segCount = getUshort(data, start + offset + 6) >> 1;
						p = start + offset + 14;
						ranges = [];
						for (i = 0; i < segCount; i++, p += 2) {
							ranges[i] = { end: getUshort(data, p) };
						}
						p += 2;
						for (i = 0; i < segCount; i++, p += 2) {
							ranges[i].start = getUshort(data, p);
						}
						for (i = 0; i < segCount; i++, p += 2) {
							ranges[i].idDelta = getUshort(data, p);
						}
						for (i = 0; i < segCount; i++, p += 2) {
							var idOffset = getUshort(data, p);
							if (idOffset === 0) {
								continue;
							}
							ranges[i].ids = [];
							for (var j = 0, jj = ranges[i].end - ranges[i].start + 1; j < jj; j++) {
								ranges[i].ids[j] = getUshort(data, p + idOffset);
								idOffset += 2;
							}
						}
						return ranges;
					} else if (format === 12) {
						getLong(data, start + offset + 4);
						var groups = getLong(data, start + offset + 12);
						p = start + offset + 16;
						ranges = [];
						for (i = 0; i < groups; i++) {
							ranges.push({
								start: getLong(data, p),
								end: getLong(data, p + 4),
								idDelta: getLong(data, p + 8) - getLong(data, p)
							});
							p += 12;
						}
						return ranges;
					}
					error('not supported cmap: ' + format);
				}
				function parseCff(data, start, end, seacAnalysisEnabled) {
					var properties = {};
					var parser = new CFFParser(new Stream(data, start, end - start), properties, seacAnalysisEnabled);
					var cff = parser.parse();
					return {
						glyphs: cff.charStrings.objects,
						subrs: cff.topDict.privateDict && cff.topDict.privateDict.subrsIndex && cff.topDict.privateDict.subrsIndex.objects,
						gsubrs: cff.globalSubrIndex && cff.globalSubrIndex.objects
					};
				}
				function parseGlyfTable(glyf, loca, isGlyphLocationsLong) {
					var itemSize, itemDecode;
					if (isGlyphLocationsLong) {
						itemSize = 4;
						itemDecode = function fontItemDecodeLong(data, offset) {
							return data[offset] << 24 | data[offset + 1] << 16 | data[offset + 2] << 8 | data[offset + 3];
						};
					} else {
						itemSize = 2;
						itemDecode = function fontItemDecode(data, offset) {
							return data[offset] << 9 | data[offset + 1] << 1;
						};
					}
					var glyphs = [];
					var startOffset = itemDecode(loca, 0);
					for (var j = itemSize; j < loca.length; j += itemSize) {
						var endOffset = itemDecode(loca, j);
						glyphs.push(glyf.subarray(startOffset, endOffset));
						startOffset = endOffset;
					}
					return glyphs;
				}
				function lookupCmap(ranges, unicode) {
					var code = unicode.charCodeAt(0), gid = 0;
					var l = 0, r = ranges.length - 1;
					while (l < r) {
						var c = l + r + 1 >> 1;
						if (code < ranges[c].start) {
							r = c - 1;
						} else {
							l = c;
						}
					}
					if (ranges[l].start <= code && code <= ranges[l].end) {
						gid = ranges[l].idDelta + (ranges[l].ids ? ranges[l].ids[code - ranges[l].start] : code) & 0xFFFF;
					}
					return {
						charCode: code,
						glyphId: gid
					};
				}
				function compileGlyf(code, cmds, font) {
					function moveTo(x, y) {
						cmds.push({
							cmd: 'moveTo',
							args: [
								x,
								y
							]
						});
					}
					function lineTo(x, y) {
						cmds.push({
							cmd: 'lineTo',
							args: [
								x,
								y
							]
						});
					}
					function quadraticCurveTo(xa, ya, x, y) {
						cmds.push({
							cmd: 'quadraticCurveTo',
							args: [
								xa,
								ya,
								x,
								y
							]
						});
					}
					var i = 0;
					var numberOfContours = (code[i] << 24 | code[i + 1] << 16) >> 16;
					var flags;
					var x = 0, y = 0;
					i += 10;
					if (numberOfContours < 0) {
						do {
							flags = code[i] << 8 | code[i + 1];
							var glyphIndex = code[i + 2] << 8 | code[i + 3];
							i += 4;
							var arg1, arg2;
							if (flags & 0x01) {
								arg1 = (code[i] << 24 | code[i + 1] << 16) >> 16;
								arg2 = (code[i + 2] << 24 | code[i + 3] << 16) >> 16;
								i += 4;
							} else {
								arg1 = code[i++];
								arg2 = code[i++];
							}
							if (flags & 0x02) {
								x = arg1;
								y = arg2;
							} else {
								x = 0;
								y = 0;
							}
							var scaleX = 1, scaleY = 1, scale01 = 0, scale10 = 0;
							if (flags & 0x08) {
								scaleX = scaleY = (code[i] << 24 | code[i + 1] << 16) / 1073741824;
								i += 2;
							} else if (flags & 0x40) {
								scaleX = (code[i] << 24 | code[i + 1] << 16) / 1073741824;
								scaleY = (code[i + 2] << 24 | code[i + 3] << 16) / 1073741824;
								i += 4;
							} else if (flags & 0x80) {
								scaleX = (code[i] << 24 | code[i + 1] << 16) / 1073741824;
								scale01 = (code[i + 2] << 24 | code[i + 3] << 16) / 1073741824;
								scale10 = (code[i + 4] << 24 | code[i + 5] << 16) / 1073741824;
								scaleY = (code[i + 6] << 24 | code[i + 7] << 16) / 1073741824;
								i += 8;
							}
							var subglyph = font.glyphs[glyphIndex];
							if (subglyph) {
								cmds.push({ cmd: 'save' });
								cmds.push({
									cmd: 'transform',
									args: [
										scaleX,
										scale01,
										scale10,
										scaleY,
										x,
										y
									]
								});
								compileGlyf(subglyph, cmds, font);
								cmds.push({ cmd: 'restore' });
							}
						} while (flags & 0x20);
					} else {
						var endPtsOfContours = [];
						var j, jj;
						for (j = 0; j < numberOfContours; j++) {
							endPtsOfContours.push(code[i] << 8 | code[i + 1]);
							i += 2;
						}
						var instructionLength = code[i] << 8 | code[i + 1];
						i += 2 + instructionLength;
						var numberOfPoints = endPtsOfContours[endPtsOfContours.length - 1] + 1;
						var points = [];
						while (points.length < numberOfPoints) {
							flags = code[i++];
							var repeat = 1;
							if (flags & 0x08) {
								repeat += code[i++];
							}
							while (repeat-- > 0) {
								points.push({ flags: flags });
							}
						}
						for (j = 0; j < numberOfPoints; j++) {
							switch (points[j].flags & 0x12) {
								case 0x00:
									x += (code[i] << 24 | code[i + 1] << 16) >> 16;
									i += 2;
									break;
								case 0x02:
									x -= code[i++];
									break;
								case 0x12:
									x += code[i++];
									break;
							}
							points[j].x = x;
						}
						for (j = 0; j < numberOfPoints; j++) {
							switch (points[j].flags & 0x24) {
								case 0x00:
									y += (code[i] << 24 | code[i + 1] << 16) >> 16;
									i += 2;
									break;
								case 0x04:
									y -= code[i++];
									break;
								case 0x24:
									y += code[i++];
									break;
							}
							points[j].y = y;
						}
						var startPoint = 0;
						for (i = 0; i < numberOfContours; i++) {
							var endPoint = endPtsOfContours[i];
							var contour = points.slice(startPoint, endPoint + 1);
							if (contour[0].flags & 1) {
								contour.push(contour[0]);
							} else if (contour[contour.length - 1].flags & 1) {
								contour.unshift(contour[contour.length - 1]);
							} else {
								var p = {
									flags: 1,
									x: (contour[0].x + contour[contour.length - 1].x) / 2,
									y: (contour[0].y + contour[contour.length - 1].y) / 2
								};
								contour.unshift(p);
								contour.push(p);
							}
							moveTo(contour[0].x, contour[0].y);
							for (j = 1, jj = contour.length; j < jj; j++) {
								if (contour[j].flags & 1) {
									lineTo(contour[j].x, contour[j].y);
								} else if (contour[j + 1].flags & 1) {
									quadraticCurveTo(contour[j].x, contour[j].y, contour[j + 1].x, contour[j + 1].y);
									j++;
								} else {
									quadraticCurveTo(contour[j].x, contour[j].y, (contour[j].x + contour[j + 1].x) / 2, (contour[j].y + contour[j + 1].y) / 2);
								}
							}
							startPoint = endPoint + 1;
						}
					}
				}
				function compileCharString(code, cmds, font) {
					var stack = [];
					var x = 0, y = 0;
					var stems = 0;
					function moveTo(x, y) {
						cmds.push({
							cmd: 'moveTo',
							args: [
								x,
								y
							]
						});
					}
					function lineTo(x, y) {
						cmds.push({
							cmd: 'lineTo',
							args: [
								x,
								y
							]
						});
					}
					function bezierCurveTo(x1, y1, x2, y2, x, y) {
						cmds.push({
							cmd: 'bezierCurveTo',
							args: [
								x1,
								y1,
								x2,
								y2,
								x,
								y
							]
						});
					}
					function parse(code) {
						var i = 0;
						while (i < code.length) {
							var stackClean = false;
							var v = code[i++];
							var xa, xb, ya, yb, y1, y2, y3, n, subrCode;
							switch (v) {
								case 1:
									stems += stack.length >> 1;
									stackClean = true;
									break;
								case 3:
									stems += stack.length >> 1;
									stackClean = true;
									break;
								case 4:
									y += stack.pop();
									moveTo(x, y);
									stackClean = true;
									break;
								case 5:
									while (stack.length > 0) {
										x += stack.shift();
										y += stack.shift();
										lineTo(x, y);
									}
									break;
								case 6:
									while (stack.length > 0) {
										x += stack.shift();
										lineTo(x, y);
										if (stack.length === 0) {
											break;
										}
										y += stack.shift();
										lineTo(x, y);
									}
									break;
								case 7:
									while (stack.length > 0) {
										y += stack.shift();
										lineTo(x, y);
										if (stack.length === 0) {
											break;
										}
										x += stack.shift();
										lineTo(x, y);
									}
									break;
								case 8:
									while (stack.length > 0) {
										xa = x + stack.shift();
										ya = y + stack.shift();
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb + stack.shift();
										y = yb + stack.shift();
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									break;
								case 10:
									n = stack.pop() + font.subrsBias;
									subrCode = font.subrs[n];
									if (subrCode) {
										parse(subrCode);
									}
									break;
								case 11:
									return;
								case 12:
									v = code[i++];
									switch (v) {
										case 34:
											xa = x + stack.shift();
											xb = xa + stack.shift();
											y1 = y + stack.shift();
											x = xb + stack.shift();
											bezierCurveTo(xa, y, xb, y1, x, y1);
											xa = x + stack.shift();
											xb = xa + stack.shift();
											x = xb + stack.shift();
											bezierCurveTo(xa, y1, xb, y, x, y);
											break;
										case 35:
											xa = x + stack.shift();
											ya = y + stack.shift();
											xb = xa + stack.shift();
											yb = ya + stack.shift();
											x = xb + stack.shift();
											y = yb + stack.shift();
											bezierCurveTo(xa, ya, xb, yb, x, y);
											xa = x + stack.shift();
											ya = y + stack.shift();
											xb = xa + stack.shift();
											yb = ya + stack.shift();
											x = xb + stack.shift();
											y = yb + stack.shift();
											bezierCurveTo(xa, ya, xb, yb, x, y);
											stack.pop();
											break;
										case 36:
											xa = x + stack.shift();
											y1 = y + stack.shift();
											xb = xa + stack.shift();
											y2 = y1 + stack.shift();
											x = xb + stack.shift();
											bezierCurveTo(xa, y1, xb, y2, x, y2);
											xa = x + stack.shift();
											xb = xa + stack.shift();
											y3 = y2 + stack.shift();
											x = xb + stack.shift();
											bezierCurveTo(xa, y2, xb, y3, x, y);
											break;
										case 37:
											var x0 = x, y0 = y;
											xa = x + stack.shift();
											ya = y + stack.shift();
											xb = xa + stack.shift();
											yb = ya + stack.shift();
											x = xb + stack.shift();
											y = yb + stack.shift();
											bezierCurveTo(xa, ya, xb, yb, x, y);
											xa = x + stack.shift();
											ya = y + stack.shift();
											xb = xa + stack.shift();
											yb = ya + stack.shift();
											x = xb;
											y = yb;
											if (Math.abs(x - x0) > Math.abs(y - y0)) {
												x += stack.shift();
											} else {
												y += stack.shift();
											}
											bezierCurveTo(xa, ya, xb, yb, x, y);
											break;
										default:
											error('unknown operator: 12 ' + v);
									}
									break;
								case 14:
									if (stack.length >= 4) {
										var achar = stack.pop();
										var bchar = stack.pop();
										y = stack.pop();
										x = stack.pop();
										cmds.push({ cmd: 'save' });
										cmds.push({
											cmd: 'translate',
											args: [
												x,
												y
											]
										});
										var cmap = lookupCmap(font.cmap, String.fromCharCode(font.glyphNameMap[StandardEncoding[achar]]));
										compileCharString(font.glyphs[cmap.glyphId], cmds, font);
										cmds.push({ cmd: 'restore' });
										cmap = lookupCmap(font.cmap, String.fromCharCode(font.glyphNameMap[StandardEncoding[bchar]]));
										compileCharString(font.glyphs[cmap.glyphId], cmds, font);
									}
									return;
								case 18:
									stems += stack.length >> 1;
									stackClean = true;
									break;
								case 19:
									stems += stack.length >> 1;
									i += stems + 7 >> 3;
									stackClean = true;
									break;
								case 20:
									stems += stack.length >> 1;
									i += stems + 7 >> 3;
									stackClean = true;
									break;
								case 21:
									y += stack.pop();
									x += stack.pop();
									moveTo(x, y);
									stackClean = true;
									break;
								case 22:
									x += stack.pop();
									moveTo(x, y);
									stackClean = true;
									break;
								case 23:
									stems += stack.length >> 1;
									stackClean = true;
									break;
								case 24:
									while (stack.length > 2) {
										xa = x + stack.shift();
										ya = y + stack.shift();
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb + stack.shift();
										y = yb + stack.shift();
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									x += stack.shift();
									y += stack.shift();
									lineTo(x, y);
									break;
								case 25:
									while (stack.length > 6) {
										x += stack.shift();
										y += stack.shift();
										lineTo(x, y);
									}
									xa = x + stack.shift();
									ya = y + stack.shift();
									xb = xa + stack.shift();
									yb = ya + stack.shift();
									x = xb + stack.shift();
									y = yb + stack.shift();
									bezierCurveTo(xa, ya, xb, yb, x, y);
									break;
								case 26:
									if (stack.length % 2) {
										x += stack.shift();
									}
									while (stack.length > 0) {
										xa = x;
										ya = y + stack.shift();
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb;
										y = yb + stack.shift();
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									break;
								case 27:
									if (stack.length % 2) {
										y += stack.shift();
									}
									while (stack.length > 0) {
										xa = x + stack.shift();
										ya = y;
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb + stack.shift();
										y = yb;
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									break;
								case 28:
									stack.push((code[i] << 24 | code[i + 1] << 16) >> 16);
									i += 2;
									break;
								case 29:
									n = stack.pop() + font.gsubrsBias;
									subrCode = font.gsubrs[n];
									if (subrCode) {
										parse(subrCode);
									}
									break;
								case 30:
									while (stack.length > 0) {
										xa = x;
										ya = y + stack.shift();
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb + stack.shift();
										y = yb + (stack.length === 1 ? stack.shift() : 0);
										bezierCurveTo(xa, ya, xb, yb, x, y);
										if (stack.length === 0) {
											break;
										}
										xa = x + stack.shift();
										ya = y;
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										y = yb + stack.shift();
										x = xb + (stack.length === 1 ? stack.shift() : 0);
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									break;
								case 31:
									while (stack.length > 0) {
										xa = x + stack.shift();
										ya = y;
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										y = yb + stack.shift();
										x = xb + (stack.length === 1 ? stack.shift() : 0);
										bezierCurveTo(xa, ya, xb, yb, x, y);
										if (stack.length === 0) {
											break;
										}
										xa = x;
										ya = y + stack.shift();
										xb = xa + stack.shift();
										yb = ya + stack.shift();
										x = xb + stack.shift();
										y = yb + (stack.length === 1 ? stack.shift() : 0);
										bezierCurveTo(xa, ya, xb, yb, x, y);
									}
									break;
								default:
									if (v < 32) {
										error('unknown operator: ' + v);
									}
									if (v < 247) {
										stack.push(v - 139);
									} else if (v < 251) {
										stack.push((v - 247) * 256 + code[i++] + 108);
									} else if (v < 255) {
										stack.push(-(v - 251) * 256 - code[i++] - 108);
									} else {
										stack.push((code[i] << 24 | code[i + 1] << 16 | code[i + 2] << 8 | code[i + 3]) / 65536);
										i += 4;
									}
									break;
							}
							if (stackClean) {
								stack.length = 0;
							}
						}
					}
					parse(code);
				}
				var noop = '';
				function CompiledFont(fontMatrix) {
					this.compiledGlyphs = Object.create(null);
					this.compiledCharCodeToGlyphId = Object.create(null);
					this.fontMatrix = fontMatrix;
				}
				CompiledFont.prototype = {
					getPathJs: function (unicode) {
						var cmap = lookupCmap(this.cmap, unicode);
						var fn = this.compiledGlyphs[cmap.glyphId];
						if (!fn) {
							fn = this.compileGlyph(this.glyphs[cmap.glyphId]);
							this.compiledGlyphs[cmap.glyphId] = fn;
						}
						if (this.compiledCharCodeToGlyphId[cmap.charCode] === undefined) {
							this.compiledCharCodeToGlyphId[cmap.charCode] = cmap.glyphId;
						}
						return fn;
					},
					compileGlyph: function (code) {
						if (!code || code.length === 0 || code[0] === 14) {
							return noop;
						}
						var cmds = [];
						cmds.push({ cmd: 'save' });
						cmds.push({
							cmd: 'transform',
							args: this.fontMatrix.slice()
						});
						cmds.push({
							cmd: 'scale',
							args: [
								'size',
								'-size'
							]
						});
						this.compileGlyphImpl(code, cmds);
						cmds.push({ cmd: 'restore' });
						return cmds;
					},
					compileGlyphImpl: function () {
						error('Children classes should implement this.');
					},
					hasBuiltPath: function (unicode) {
						var cmap = lookupCmap(this.cmap, unicode);
						return this.compiledGlyphs[cmap.glyphId] !== undefined && this.compiledCharCodeToGlyphId[cmap.charCode] !== undefined;
					}
				};
				function TrueTypeCompiled(glyphs, cmap, fontMatrix) {
					fontMatrix = fontMatrix || [
						0.000488,
						0,
						0,
						0.000488,
						0,
						0
					];
					CompiledFont.call(this, fontMatrix);
					this.glyphs = glyphs;
					this.cmap = cmap;
				}
				Util.inherit(TrueTypeCompiled, CompiledFont, {
					compileGlyphImpl: function (code, cmds) {
						compileGlyf(code, cmds, this);
					}
				});
				function Type2Compiled(cffInfo, cmap, fontMatrix, glyphNameMap) {
					fontMatrix = fontMatrix || [
						0.001,
						0,
						0,
						0.001,
						0,
						0
					];
					CompiledFont.call(this, fontMatrix);
					this.glyphs = cffInfo.glyphs;
					this.gsubrs = cffInfo.gsubrs || [];
					this.subrs = cffInfo.subrs || [];
					this.cmap = cmap;
					this.glyphNameMap = glyphNameMap || getGlyphsUnicode();
					this.gsubrsBias = this.gsubrs.length < 1240 ? 107 : this.gsubrs.length < 33900 ? 1131 : 32768;
					this.subrsBias = this.subrs.length < 1240 ? 107 : this.subrs.length < 33900 ? 1131 : 32768;
				}
				Util.inherit(Type2Compiled, CompiledFont, {
					compileGlyphImpl: function (code, cmds) {
						compileCharString(code, cmds, this);
					}
				});
				return {
					create: function FontRendererFactory_create(font, seacAnalysisEnabled) {
						var data = new Uint8Array(font.data);
						var cmap, glyf, loca, cff, indexToLocFormat, unitsPerEm;
						var numTables = getUshort(data, 4);
						for (var i = 0, p = 12; i < numTables; i++, p += 16) {
							var tag = bytesToString(data.subarray(p, p + 4));
							var offset = getLong(data, p + 8);
							var length = getLong(data, p + 12);
							switch (tag) {
								case 'cmap':
									cmap = parseCmap(data, offset, offset + length);
									break;
								case 'glyf':
									glyf = data.subarray(offset, offset + length);
									break;
								case 'loca':
									loca = data.subarray(offset, offset + length);
									break;
								case 'head':
									unitsPerEm = getUshort(data, offset + 18);
									indexToLocFormat = getUshort(data, offset + 50);
									break;
								case 'CFF ':
									cff = parseCff(data, offset, offset + length, seacAnalysisEnabled);
									break;
							}
						}
						if (glyf) {
							var fontMatrix = !unitsPerEm ? font.fontMatrix : [
								1 / unitsPerEm,
								0,
								0,
								1 / unitsPerEm,
								0,
								0
							];
							return new TrueTypeCompiled(parseGlyfTable(glyf, loca, indexToLocFormat), cmap, fontMatrix);
						}
						return new Type2Compiled(cff, cmap, font.fontMatrix, font.glyphNameMap);
					}
				};
			}();
			exports.FontRendererFactory = FontRendererFactory;

			/***/
		}),
/* 26 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreStream = __w_pdfjs_require__(2);
			var coreGlyphList = __w_pdfjs_require__(7);
			var coreFontRenderer = __w_pdfjs_require__(25);
			var coreEncodings = __w_pdfjs_require__(4);
			var coreStandardFonts = __w_pdfjs_require__(17);
			var coreUnicode = __w_pdfjs_require__(18);
			var coreType1Parser = __w_pdfjs_require__(35);
			var coreCFFParser = __w_pdfjs_require__(11);
			var FONT_IDENTITY_MATRIX = sharedUtil.FONT_IDENTITY_MATRIX;
			var FontType = sharedUtil.FontType;
			var assert = sharedUtil.assert;
			var bytesToString = sharedUtil.bytesToString;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var isInt = sharedUtil.isInt;
			var isNum = sharedUtil.isNum;
			var readUint32 = sharedUtil.readUint32;
			var shadow = sharedUtil.shadow;
			var string32 = sharedUtil.string32;
			var warn = sharedUtil.warn;
			var MissingDataException = sharedUtil.MissingDataException;
			var isSpace = sharedUtil.isSpace;
			var Stream = coreStream.Stream;
			var getGlyphsUnicode = coreGlyphList.getGlyphsUnicode;
			var getDingbatsGlyphsUnicode = coreGlyphList.getDingbatsGlyphsUnicode;
			var FontRendererFactory = coreFontRenderer.FontRendererFactory;
			var StandardEncoding = coreEncodings.StandardEncoding;
			var MacRomanEncoding = coreEncodings.MacRomanEncoding;
			var SymbolSetEncoding = coreEncodings.SymbolSetEncoding;
			var ZapfDingbatsEncoding = coreEncodings.ZapfDingbatsEncoding;
			var getEncoding = coreEncodings.getEncoding;
			var getStdFontMap = coreStandardFonts.getStdFontMap;
			var getNonStdFontMap = coreStandardFonts.getNonStdFontMap;
			var getGlyphMapForStandardFonts = coreStandardFonts.getGlyphMapForStandardFonts;
			var getSupplementalGlyphMapForArialBlack = coreStandardFonts.getSupplementalGlyphMapForArialBlack;
			var getUnicodeRangeFor = coreUnicode.getUnicodeRangeFor;
			var mapSpecialUnicodeValues = coreUnicode.mapSpecialUnicodeValues;
			var getUnicodeForGlyph = coreUnicode.getUnicodeForGlyph;
			var Type1Parser = coreType1Parser.Type1Parser;
			var CFFStandardStrings = coreCFFParser.CFFStandardStrings;
			var CFFParser = coreCFFParser.CFFParser;
			var CFFCompiler = coreCFFParser.CFFCompiler;
			var CFF = coreCFFParser.CFF;
			var CFFHeader = coreCFFParser.CFFHeader;
			var CFFTopDict = coreCFFParser.CFFTopDict;
			var CFFPrivateDict = coreCFFParser.CFFPrivateDict;
			var CFFStrings = coreCFFParser.CFFStrings;
			var CFFIndex = coreCFFParser.CFFIndex;
			var CFFCharset = coreCFFParser.CFFCharset;
			var PRIVATE_USE_OFFSET_START = 0xE000;
			var PRIVATE_USE_OFFSET_END = 0xF8FF;
			var SKIP_PRIVATE_USE_RANGE_F000_TO_F01F = false;
			var PDF_GLYPH_SPACE_UNITS = 1000;
			var SEAC_ANALYSIS_ENABLED = false;
			var FontFlags = {
				FixedPitch: 1,
				Serif: 2,
				Symbolic: 4,
				Script: 8,
				Nonsymbolic: 32,
				Italic: 64,
				AllCap: 65536,
				SmallCap: 131072,
				ForceBold: 262144
			};
			var MacStandardGlyphOrdering = [
				'.notdef',
				'.null',
				'nonmarkingreturn',
				'space',
				'exclam',
				'quotedbl',
				'numbersign',
				'dollar',
				'percent',
				'ampersand',
				'quotesingle',
				'parenleft',
				'parenright',
				'asterisk',
				'plus',
				'comma',
				'hyphen',
				'period',
				'slash',
				'zero',
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'colon',
				'semicolon',
				'less',
				'equal',
				'greater',
				'question',
				'at',
				'A',
				'B',
				'C',
				'D',
				'E',
				'F',
				'G',
				'H',
				'I',
				'J',
				'K',
				'L',
				'M',
				'N',
				'O',
				'P',
				'Q',
				'R',
				'S',
				'T',
				'U',
				'V',
				'W',
				'X',
				'Y',
				'Z',
				'bracketleft',
				'backslash',
				'bracketright',
				'asciicircum',
				'underscore',
				'grave',
				'a',
				'b',
				'c',
				'd',
				'e',
				'f',
				'g',
				'h',
				'i',
				'j',
				'k',
				'l',
				'm',
				'n',
				'o',
				'p',
				'q',
				'r',
				's',
				't',
				'u',
				'v',
				'w',
				'x',
				'y',
				'z',
				'braceleft',
				'bar',
				'braceright',
				'asciitilde',
				'Adieresis',
				'Aring',
				'Ccedilla',
				'Eacute',
				'Ntilde',
				'Odieresis',
				'Udieresis',
				'aacute',
				'agrave',
				'acircumflex',
				'adieresis',
				'atilde',
				'aring',
				'ccedilla',
				'eacute',
				'egrave',
				'ecircumflex',
				'edieresis',
				'iacute',
				'igrave',
				'icircumflex',
				'idieresis',
				'ntilde',
				'oacute',
				'ograve',
				'ocircumflex',
				'odieresis',
				'otilde',
				'uacute',
				'ugrave',
				'ucircumflex',
				'udieresis',
				'dagger',
				'degree',
				'cent',
				'sterling',
				'section',
				'bullet',
				'paragraph',
				'germandbls',
				'registered',
				'copyright',
				'trademark',
				'acute',
				'dieresis',
				'notequal',
				'AE',
				'Oslash',
				'infinity',
				'plusminus',
				'lessequal',
				'greaterequal',
				'yen',
				'mu',
				'partialdiff',
				'summation',
				'product',
				'pi',
				'integral',
				'ordfeminine',
				'ordmasculine',
				'Omega',
				'ae',
				'oslash',
				'questiondown',
				'exclamdown',
				'logicalnot',
				'radical',
				'florin',
				'approxequal',
				'Delta',
				'guillemotleft',
				'guillemotright',
				'ellipsis',
				'nonbreakingspace',
				'Agrave',
				'Atilde',
				'Otilde',
				'OE',
				'oe',
				'endash',
				'emdash',
				'quotedblleft',
				'quotedblright',
				'quoteleft',
				'quoteright',
				'divide',
				'lozenge',
				'ydieresis',
				'Ydieresis',
				'fraction',
				'currency',
				'guilsinglleft',
				'guilsinglright',
				'fi',
				'fl',
				'daggerdbl',
				'periodcentered',
				'quotesinglbase',
				'quotedblbase',
				'perthousand',
				'Acircumflex',
				'Ecircumflex',
				'Aacute',
				'Edieresis',
				'Egrave',
				'Iacute',
				'Icircumflex',
				'Idieresis',
				'Igrave',
				'Oacute',
				'Ocircumflex',
				'apple',
				'Ograve',
				'Uacute',
				'Ucircumflex',
				'Ugrave',
				'dotlessi',
				'circumflex',
				'tilde',
				'macron',
				'breve',
				'dotaccent',
				'ring',
				'cedilla',
				'hungarumlaut',
				'ogonek',
				'caron',
				'Lslash',
				'lslash',
				'Scaron',
				'scaron',
				'Zcaron',
				'zcaron',
				'brokenbar',
				'Eth',
				'eth',
				'Yacute',
				'yacute',
				'Thorn',
				'thorn',
				'minus',
				'multiply',
				'onesuperior',
				'twosuperior',
				'threesuperior',
				'onehalf',
				'onequarter',
				'threequarters',
				'franc',
				'Gbreve',
				'gbreve',
				'Idotaccent',
				'Scedilla',
				'scedilla',
				'Cacute',
				'cacute',
				'Ccaron',
				'ccaron',
				'dcroat'
			];
			function adjustWidths(properties) {
				if (!properties.fontMatrix) {
					return;
				}
				if (properties.fontMatrix[0] === FONT_IDENTITY_MATRIX[0]) {
					return;
				}
				var scale = 0.001 / properties.fontMatrix[0];
				var glyphsWidths = properties.widths;
				for (var glyph in glyphsWidths) {
					glyphsWidths[glyph] *= scale;
				}
				properties.defaultWidth *= scale;
			}
			function adjustToUnicode(properties, builtInEncoding) {
				if (properties.hasIncludedToUnicodeMap) {
					return;
				}
				if (properties.hasEncoding) {
					return;
				}
				if (builtInEncoding === properties.defaultEncoding) {
					return;
				}
				if (properties.toUnicode instanceof IdentityToUnicodeMap) {
					return;
				}
				var toUnicode = [], glyphsUnicodeMap = getGlyphsUnicode();
				for (var charCode in builtInEncoding) {
					var glyphName = builtInEncoding[charCode];
					var unicode = getUnicodeForGlyph(glyphName, glyphsUnicodeMap);
					if (unicode !== -1) {
						toUnicode[charCode] = String.fromCharCode(unicode);
					}
				}
				properties.toUnicode.amend(toUnicode);
			}
			function getFontType(type, subtype) {
				switch (type) {
					case 'Type1':
						return subtype === 'Type1C' ? FontType.TYPE1C : FontType.TYPE1;
					case 'CIDFontType0':
						return subtype === 'CIDFontType0C' ? FontType.CIDFONTTYPE0C : FontType.CIDFONTTYPE0;
					case 'OpenType':
						return FontType.OPENTYPE;
					case 'TrueType':
						return FontType.TRUETYPE;
					case 'CIDFontType2':
						return FontType.CIDFONTTYPE2;
					case 'MMType1':
						return FontType.MMTYPE1;
					case 'Type0':
						return FontType.TYPE0;
					default:
						return FontType.UNKNOWN;
				}
			}
			function recoverGlyphName(name, glyphsUnicodeMap) {
				if (glyphsUnicodeMap[name] !== undefined) {
					return name;
				}
				var unicode = getUnicodeForGlyph(name, glyphsUnicodeMap);
				if (unicode !== -1) {
					for (var key in glyphsUnicodeMap) {
						if (glyphsUnicodeMap[key] === unicode) {
							return key;
						}
					}
				}
				info('Unable to recover a standard glyph name for: ' + name);
				return name;
			}
			var Glyph = function GlyphClosure() {
				function Glyph(fontChar, unicode, accent, width, vmetric, operatorListId, isSpace, isInFont) {
					this.fontChar = fontChar;
					this.unicode = unicode;
					this.accent = accent;
					this.width = width;
					this.vmetric = vmetric;
					this.operatorListId = operatorListId;
					this.isSpace = isSpace;
					this.isInFont = isInFont;
				}
				Glyph.prototype.matchesForCache = function (fontChar, unicode, accent, width, vmetric, operatorListId, isSpace, isInFont) {
					return this.fontChar === fontChar && this.unicode === unicode && this.accent === accent && this.width === width && this.vmetric === vmetric && this.operatorListId === operatorListId && this.isSpace === isSpace && this.isInFont === isInFont;
				};
				return Glyph;
			}();
			var ToUnicodeMap = function ToUnicodeMapClosure() {
				function ToUnicodeMap(cmap) {
					this._map = cmap;
				}
				ToUnicodeMap.prototype = {
					get length() {
						return this._map.length;
					},
					forEach: function (callback) {
						for (var charCode in this._map) {
							callback(charCode, this._map[charCode].charCodeAt(0));
						}
					},
					has: function (i) {
						return this._map[i] !== undefined;
					},
					get: function (i) {
						return this._map[i];
					},
					charCodeOf: function (v) {
						return this._map.indexOf(v);
					},
					amend: function (map) {
						for (var charCode in map) {
							this._map[charCode] = map[charCode];
						}
					}
				};
				return ToUnicodeMap;
			}();
			var IdentityToUnicodeMap = function IdentityToUnicodeMapClosure() {
				function IdentityToUnicodeMap(firstChar, lastChar) {
					this.firstChar = firstChar;
					this.lastChar = lastChar;
				}
				IdentityToUnicodeMap.prototype = {
					get length() {
						return this.lastChar + 1 - this.firstChar;
					},
					forEach: function (callback) {
						for (var i = this.firstChar, ii = this.lastChar; i <= ii; i++) {
							callback(i, i);
						}
					},
					has: function (i) {
						return this.firstChar <= i && i <= this.lastChar;
					},
					get: function (i) {
						if (this.firstChar <= i && i <= this.lastChar) {
							return String.fromCharCode(i);
						}
						return undefined;
					},
					charCodeOf: function (v) {
						return isInt(v) && v >= this.firstChar && v <= this.lastChar ? v : -1;
					},
					amend: function (map) {
						error('Should not call amend()');
					}
				};
				return IdentityToUnicodeMap;
			}();
			var OpenTypeFileBuilder = function OpenTypeFileBuilderClosure() {
				function writeInt16(dest, offset, num) {
					dest[offset] = num >> 8 & 0xFF;
					dest[offset + 1] = num & 0xFF;
				}
				function writeInt32(dest, offset, num) {
					dest[offset] = num >> 24 & 0xFF;
					dest[offset + 1] = num >> 16 & 0xFF;
					dest[offset + 2] = num >> 8 & 0xFF;
					dest[offset + 3] = num & 0xFF;
				}
				function writeData(dest, offset, data) {
					var i, ii;
					if (data instanceof Uint8Array) {
						dest.set(data, offset);
					} else if (typeof data === 'string') {
						for (i = 0, ii = data.length; i < ii; i++) {
							dest[offset++] = data.charCodeAt(i) & 0xFF;
						}
					} else {
						for (i = 0, ii = data.length; i < ii; i++) {
							dest[offset++] = data[i] & 0xFF;
						}
					}
				}
				function OpenTypeFileBuilder(sfnt) {
					this.sfnt = sfnt;
					this.tables = Object.create(null);
				}
				OpenTypeFileBuilder.getSearchParams = function OpenTypeFileBuilder_getSearchParams(entriesCount, entrySize) {
					var maxPower2 = 1, log2 = 0;
					while ((maxPower2 ^ entriesCount) > maxPower2) {
						maxPower2 <<= 1;
						log2++;
					}
					var searchRange = maxPower2 * entrySize;
					return {
						range: searchRange,
						entry: log2,
						rangeShift: entrySize * entriesCount - searchRange
					};
				};
				var OTF_HEADER_SIZE = 12;
				var OTF_TABLE_ENTRY_SIZE = 16;
				OpenTypeFileBuilder.prototype = {
					toArray: function OpenTypeFileBuilder_toArray() {
						var sfnt = this.sfnt;
						var tables = this.tables;
						var tablesNames = Object.keys(tables);
						tablesNames.sort();
						var numTables = tablesNames.length;
						var i, j, jj, table, tableName;
						var offset = OTF_HEADER_SIZE + numTables * OTF_TABLE_ENTRY_SIZE;
						var tableOffsets = [offset];
						for (i = 0; i < numTables; i++) {
							table = tables[tablesNames[i]];
							var paddedLength = (table.length + 3 & ~3) >>> 0;
							offset += paddedLength;
							tableOffsets.push(offset);
						}
						var file = new Uint8Array(offset);
						for (i = 0; i < numTables; i++) {
							table = tables[tablesNames[i]];
							writeData(file, tableOffsets[i], table);
						}
						if (sfnt === 'true') {
							sfnt = string32(0x00010000);
						}
						file[0] = sfnt.charCodeAt(0) & 0xFF;
						file[1] = sfnt.charCodeAt(1) & 0xFF;
						file[2] = sfnt.charCodeAt(2) & 0xFF;
						file[3] = sfnt.charCodeAt(3) & 0xFF;
						writeInt16(file, 4, numTables);
						var searchParams = OpenTypeFileBuilder.getSearchParams(numTables, 16);
						writeInt16(file, 6, searchParams.range);
						writeInt16(file, 8, searchParams.entry);
						writeInt16(file, 10, searchParams.rangeShift);
						offset = OTF_HEADER_SIZE;
						for (i = 0; i < numTables; i++) {
							tableName = tablesNames[i];
							file[offset] = tableName.charCodeAt(0) & 0xFF;
							file[offset + 1] = tableName.charCodeAt(1) & 0xFF;
							file[offset + 2] = tableName.charCodeAt(2) & 0xFF;
							file[offset + 3] = tableName.charCodeAt(3) & 0xFF;
							var checksum = 0;
							for (j = tableOffsets[i], jj = tableOffsets[i + 1]; j < jj; j += 4) {
								var quad = readUint32(file, j);
								checksum = checksum + quad >>> 0;
							}
							writeInt32(file, offset + 4, checksum);
							writeInt32(file, offset + 8, tableOffsets[i]);
							writeInt32(file, offset + 12, tables[tableName].length);
							offset += OTF_TABLE_ENTRY_SIZE;
						}
						return file;
					},
					addTable: function OpenTypeFileBuilder_addTable(tag, data) {
						if (tag in this.tables) {
							throw new Error('Table ' + tag + ' already exists');
						}
						this.tables[tag] = data;
					}
				};
				return OpenTypeFileBuilder;
			}();
			var ProblematicCharRanges = new Int32Array([
				0x0000,
				0x0020,
				0x007F,
				0x00A1,
				0x00AD,
				0x00AE,
				0x0600,
				0x0780,
				0x08A0,
				0x10A0,
				0x1780,
				0x1800,
				0x1C00,
				0x1C50,
				0x2000,
				0x2010,
				0x2011,
				0x2012,
				0x2028,
				0x2030,
				0x205F,
				0x2070,
				0x25CC,
				0x25CD,
				0x3000,
				0x3001,
				0xAA60,
				0xAA80,
				0xFFF0,
				0x10000
			]);
			var Font = function FontClosure() {
				function Font(name, file, properties) {
					var charCode, glyphName, unicode;
					this.name = name;
					this.loadedName = properties.loadedName;
					this.isType3Font = properties.isType3Font;
					this.sizes = [];
					this.missingFile = false;
					this.glyphCache = Object.create(null);
					this.isSerifFont = !!(properties.flags & FontFlags.Serif);
					this.isSymbolicFont = !!(properties.flags & FontFlags.Symbolic);
					this.isMonospace = !!(properties.flags & FontFlags.FixedPitch);
					var type = properties.type;
					var subtype = properties.subtype;
					this.type = type;
					this.fallbackName = this.isMonospace ? 'monospace' : this.isSerifFont ? 'serif' : 'sans-serif';
					this.differences = properties.differences;
					this.widths = properties.widths;
					this.defaultWidth = properties.defaultWidth;
					this.composite = properties.composite;
					this.wideChars = properties.wideChars;
					this.cMap = properties.cMap;
					this.ascent = properties.ascent / PDF_GLYPH_SPACE_UNITS;
					this.descent = properties.descent / PDF_GLYPH_SPACE_UNITS;
					this.fontMatrix = properties.fontMatrix;
					this.bbox = properties.bbox;
					this.toUnicode = properties.toUnicode;
					this.toFontChar = [];
					if (properties.type === 'Type3') {
						for (charCode = 0; charCode < 256; charCode++) {
							this.toFontChar[charCode] = this.differences[charCode] || properties.defaultEncoding[charCode];
						}
						this.fontType = FontType.TYPE3;
						return;
					}
					this.cidEncoding = properties.cidEncoding;
					this.vertical = properties.vertical;
					if (this.vertical) {
						this.vmetrics = properties.vmetrics;
						this.defaultVMetrics = properties.defaultVMetrics;
					}
					var glyphsUnicodeMap;
					if (!file || file.isEmpty) {
						if (file) {
							warn('Font file is empty in "' + name + '" (' + this.loadedName + ')');
						}
						this.missingFile = true;
						var fontName = name.replace(/[,_]/g, '-');
						var stdFontMap = getStdFontMap(), nonStdFontMap = getNonStdFontMap();
						var isStandardFont = !!stdFontMap[fontName] || !!(nonStdFontMap[fontName] && stdFontMap[nonStdFontMap[fontName]]);
						fontName = stdFontMap[fontName] || nonStdFontMap[fontName] || fontName;
						this.bold = fontName.search(/bold/gi) !== -1;
						this.italic = fontName.search(/oblique/gi) !== -1 || fontName.search(/italic/gi) !== -1;
						this.black = name.search(/Black/g) !== -1;
						this.remeasure = Object.keys(this.widths).length > 0;
						if (isStandardFont && type === 'CIDFontType2' && properties.cidEncoding.indexOf('Identity-') === 0) {
							var GlyphMapForStandardFonts = getGlyphMapForStandardFonts();
							var map = [];
							for (charCode in GlyphMapForStandardFonts) {
								map[+charCode] = GlyphMapForStandardFonts[charCode];
							}
							if (/Arial-?Black/i.test(name)) {
								var SupplementalGlyphMapForArialBlack = getSupplementalGlyphMapForArialBlack();
								for (charCode in SupplementalGlyphMapForArialBlack) {
									map[+charCode] = SupplementalGlyphMapForArialBlack[charCode];
								}
							}
							var isIdentityUnicode = this.toUnicode instanceof IdentityToUnicodeMap;
							if (!isIdentityUnicode) {
								this.toUnicode.forEach(function (charCode, unicodeCharCode) {
									map[+charCode] = unicodeCharCode;
								});
							}
							this.toFontChar = map;
							this.toUnicode = new ToUnicodeMap(map);
						} else if (/Symbol/i.test(fontName)) {
							this.toFontChar = buildToFontChar(SymbolSetEncoding, getGlyphsUnicode(), properties.differences);
						} else if (/Dingbats/i.test(fontName)) {
							if (/Wingdings/i.test(name)) {
								warn('Non-embedded Wingdings font, falling back to ZapfDingbats.');
							}
							this.toFontChar = buildToFontChar(ZapfDingbatsEncoding, getDingbatsGlyphsUnicode(), properties.differences);
						} else if (isStandardFont) {
							this.toFontChar = buildToFontChar(properties.defaultEncoding, getGlyphsUnicode(), properties.differences);
						} else {
							glyphsUnicodeMap = getGlyphsUnicode();
							this.toUnicode.forEach(function (charCode, unicodeCharCode) {
								if (!this.composite) {
									glyphName = properties.differences[charCode] || properties.defaultEncoding[charCode];
									unicode = getUnicodeForGlyph(glyphName, glyphsUnicodeMap);
									if (unicode !== -1) {
										unicodeCharCode = unicode;
									}
								}
								this.toFontChar[charCode] = unicodeCharCode;
							}.bind(this));
						}
						this.loadedName = fontName.split('-')[0];
						this.loading = false;
						this.fontType = getFontType(type, subtype);
						return;
					}
					if (subtype === 'Type1C') {
						if (type !== 'Type1' && type !== 'MMType1') {
							if (isTrueTypeFile(file)) {
								subtype = 'TrueType';
							} else {
								type = 'Type1';
							}
						} else if (isOpenTypeFile(file)) {
							type = subtype = 'OpenType';
						}
					}
					if (subtype === 'CIDFontType0C' && type !== 'CIDFontType0') {
						type = 'CIDFontType0';
					}
					if (subtype === 'OpenType') {
						type = 'OpenType';
					}
					if (type === 'CIDFontType0') {
						if (isType1File(file)) {
							subtype = 'CIDFontType0';
						} else if (isOpenTypeFile(file)) {
							type = subtype = 'OpenType';
						} else {
							subtype = 'CIDFontType0C';
						}
					}
					var data;
					switch (type) {
						case 'MMType1':
							info('MMType1 font (' + name + '), falling back to Type1.');
						case 'Type1':
						case 'CIDFontType0':
							this.mimetype = 'font/opentype';
							var cff = subtype === 'Type1C' || subtype === 'CIDFontType0C' ? new CFFFont(file, properties) : new Type1Font(name, file, properties);
							adjustWidths(properties);
							data = this.convert(name, cff, properties);
							break;
						case 'OpenType':
						case 'TrueType':
						case 'CIDFontType2':
							this.mimetype = 'font/opentype';
							data = this.checkAndRepair(name, file, properties);
							if (this.isOpenType) {
								adjustWidths(properties);
								type = 'OpenType';
							}
							break;
						default:
							error('Font ' + type + ' is not supported');
							break;
					}
					this.data = data;
					this.fontType = getFontType(type, subtype);
					this.fontMatrix = properties.fontMatrix;
					this.widths = properties.widths;
					this.defaultWidth = properties.defaultWidth;
					this.toUnicode = properties.toUnicode;
					this.encoding = properties.baseEncoding;
					this.seacMap = properties.seacMap;
					this.loading = true;
				}
				Font.getFontID = function () {
					var ID = 1;
					return function Font_getFontID() {
						return String(ID++);
					};
				}();
				function int16(b0, b1) {
					return (b0 << 8) + b1;
				}
				function signedInt16(b0, b1) {
					var value = (b0 << 8) + b1;
					return value & 1 << 15 ? value - 0x10000 : value;
				}
				function int32(b0, b1, b2, b3) {
					return (b0 << 24) + (b1 << 16) + (b2 << 8) + b3;
				}
				function string16(value) {
					return String.fromCharCode(value >> 8 & 0xff, value & 0xff);
				}
				function safeString16(value) {
					value = value > 0x7FFF ? 0x7FFF : value < -0x8000 ? -0x8000 : value;
					return String.fromCharCode(value >> 8 & 0xff, value & 0xff);
				}
				function isTrueTypeFile(file) {
					var header = file.peekBytes(4);
					return readUint32(header, 0) === 0x00010000;
				}
				function isOpenTypeFile(file) {
					var header = file.peekBytes(4);
					return bytesToString(header) === 'OTTO';
				}
				function isType1File(file) {
					var header = file.peekBytes(2);
					if (header[0] === 0x25 && header[1] === 0x21) {
						return true;
					}
					if (header[0] === 0x80 && header[1] === 0x01) {
						return true;
					}
					return false;
				}
				function buildToFontChar(encoding, glyphsUnicodeMap, differences) {
					var toFontChar = [], unicode;
					for (var i = 0, ii = encoding.length; i < ii; i++) {
						unicode = getUnicodeForGlyph(encoding[i], glyphsUnicodeMap);
						if (unicode !== -1) {
							toFontChar[i] = unicode;
						}
					}
					for (var charCode in differences) {
						unicode = getUnicodeForGlyph(differences[charCode], glyphsUnicodeMap);
						if (unicode !== -1) {
							toFontChar[+charCode] = unicode;
						}
					}
					return toFontChar;
				}
				function isProblematicUnicodeLocation(code) {
					var i = 0, j = ProblematicCharRanges.length - 1;
					while (i < j) {
						var c = i + j + 1 >> 1;
						if (code < ProblematicCharRanges[c]) {
							j = c - 1;
						} else {
							i = c;
						}
					}
					return !(i & 1);
				}
				function adjustMapping(charCodeToGlyphId, properties) {
					var toUnicode = properties.toUnicode;
					var isSymbolic = !!(properties.flags & FontFlags.Symbolic);
					var isIdentityUnicode = properties.toUnicode instanceof IdentityToUnicodeMap;
					var newMap = Object.create(null);
					var toFontChar = [];
					var usedFontCharCodes = [];
					var nextAvailableFontCharCode = PRIVATE_USE_OFFSET_START;
					for (var originalCharCode in charCodeToGlyphId) {
						originalCharCode |= 0;
						var glyphId = charCodeToGlyphId[originalCharCode];
						var fontCharCode = originalCharCode;
						var hasUnicodeValue = false;
						if (!isIdentityUnicode && toUnicode.has(originalCharCode)) {
							hasUnicodeValue = true;
							var unicode = toUnicode.get(fontCharCode);
							if (unicode.length === 1) {
								fontCharCode = unicode.charCodeAt(0);
							}
						}
						if ((usedFontCharCodes[fontCharCode] !== undefined || isProblematicUnicodeLocation(fontCharCode) || isSymbolic && !hasUnicodeValue) && nextAvailableFontCharCode <= PRIVATE_USE_OFFSET_END) {
							do {
								fontCharCode = nextAvailableFontCharCode++;
								if (SKIP_PRIVATE_USE_RANGE_F000_TO_F01F && fontCharCode === 0xF000) {
									fontCharCode = 0xF020;
									nextAvailableFontCharCode = fontCharCode + 1;
								}
							} while (usedFontCharCodes[fontCharCode] !== undefined && nextAvailableFontCharCode <= PRIVATE_USE_OFFSET_END);
						}
						newMap[fontCharCode] = glyphId;
						toFontChar[originalCharCode] = fontCharCode;
						usedFontCharCodes[fontCharCode] = true;
					}
					return {
						toFontChar: toFontChar,
						charCodeToGlyphId: newMap,
						nextAvailableFontCharCode: nextAvailableFontCharCode
					};
				}
				function getRanges(glyphs, numGlyphs) {
					var codes = [];
					for (var charCode in glyphs) {
						if (glyphs[charCode] >= numGlyphs) {
							continue;
						}
						codes.push({
							fontCharCode: charCode | 0,
							glyphId: glyphs[charCode]
						});
					}
					codes.sort(function fontGetRangesSort(a, b) {
						return a.fontCharCode - b.fontCharCode;
					});
					var ranges = [];
					var length = codes.length;
					for (var n = 0; n < length;) {
						var start = codes[n].fontCharCode;
						var codeIndices = [codes[n].glyphId];
						++n;
						var end = start;
						while (n < length && end + 1 === codes[n].fontCharCode) {
							codeIndices.push(codes[n].glyphId);
							++end;
							++n;
							if (end === 0xFFFF) {
								break;
							}
						}
						ranges.push([
							start,
							end,
							codeIndices
						]);
					}
					return ranges;
				}
				function createCmapTable(glyphs, numGlyphs) {
					var ranges = getRanges(glyphs, numGlyphs);
					var numTables = ranges[ranges.length - 1][1] > 0xFFFF ? 2 : 1;
					var cmap = '\x00\x00' + string16(numTables) + '\x00\x03' + '\x00\x01' + string32(4 + numTables * 8);
					var i, ii, j, jj;
					for (i = ranges.length - 1; i >= 0; --i) {
						if (ranges[i][0] <= 0xFFFF) {
							break;
						}
					}
					var bmpLength = i + 1;
					if (ranges[i][0] < 0xFFFF && ranges[i][1] === 0xFFFF) {
						ranges[i][1] = 0xFFFE;
					}
					var trailingRangesCount = ranges[i][1] < 0xFFFF ? 1 : 0;
					var segCount = bmpLength + trailingRangesCount;
					var searchParams = OpenTypeFileBuilder.getSearchParams(segCount, 2);
					var startCount = '';
					var endCount = '';
					var idDeltas = '';
					var idRangeOffsets = '';
					var glyphsIds = '';
					var bias = 0;
					var range, start, end, codes;
					for (i = 0, ii = bmpLength; i < ii; i++) {
						range = ranges[i];
						start = range[0];
						end = range[1];
						startCount += string16(start);
						endCount += string16(end);
						codes = range[2];
						var contiguous = true;
						for (j = 1, jj = codes.length; j < jj; ++j) {
							if (codes[j] !== codes[j - 1] + 1) {
								contiguous = false;
								break;
							}
						}
						if (!contiguous) {
							var offset = (segCount - i) * 2 + bias * 2;
							bias += end - start + 1;
							idDeltas += string16(0);
							idRangeOffsets += string16(offset);
							for (j = 0, jj = codes.length; j < jj; ++j) {
								glyphsIds += string16(codes[j]);
							}
						} else {
							var startCode = codes[0];
							idDeltas += string16(startCode - start & 0xFFFF);
							idRangeOffsets += string16(0);
						}
					}
					if (trailingRangesCount > 0) {
						endCount += '\xFF\xFF';
						startCount += '\xFF\xFF';
						idDeltas += '\x00\x01';
						idRangeOffsets += '\x00\x00';
					}
					var format314 = '\x00\x00' + string16(2 * segCount) + string16(searchParams.range) + string16(searchParams.entry) + string16(searchParams.rangeShift) + endCount + '\x00\x00' + startCount + idDeltas + idRangeOffsets + glyphsIds;
					var format31012 = '';
					var header31012 = '';
					if (numTables > 1) {
						cmap += '\x00\x03' + '\x00\x0A' + string32(4 + numTables * 8 + 4 + format314.length);
						format31012 = '';
						for (i = 0, ii = ranges.length; i < ii; i++) {
							range = ranges[i];
							start = range[0];
							codes = range[2];
							var code = codes[0];
							for (j = 1, jj = codes.length; j < jj; ++j) {
								if (codes[j] !== codes[j - 1] + 1) {
									end = range[0] + j - 1;
									format31012 += string32(start) + string32(end) + string32(code);
									start = end + 1;
									code = codes[j];
								}
							}
							format31012 += string32(start) + string32(range[1]) + string32(code);
						}
						header31012 = '\x00\x0C' + '\x00\x00' + string32(format31012.length + 16) + '\x00\x00\x00\x00' + string32(format31012.length / 12);
					}
					return cmap + '\x00\x04' + string16(format314.length + 4) + format314 + header31012 + format31012;
				}
				function validateOS2Table(os2) {
					var stream = new Stream(os2.data);
					var version = stream.getUint16();
					stream.getBytes(60);
					var selection = stream.getUint16();
					if (version < 4 && selection & 0x0300) {
						return false;
					}
					var firstChar = stream.getUint16();
					var lastChar = stream.getUint16();
					if (firstChar > lastChar) {
						return false;
					}
					stream.getBytes(6);
					var usWinAscent = stream.getUint16();
					if (usWinAscent === 0) {
						return false;
					}
					os2.data[8] = os2.data[9] = 0;
					return true;
				}
				function createOS2Table(properties, charstrings, override) {
					override = override || {
						unitsPerEm: 0,
						yMax: 0,
						yMin: 0,
						ascent: 0,
						descent: 0
					};
					var ulUnicodeRange1 = 0;
					var ulUnicodeRange2 = 0;
					var ulUnicodeRange3 = 0;
					var ulUnicodeRange4 = 0;
					var firstCharIndex = null;
					var lastCharIndex = 0;
					if (charstrings) {
						for (var code in charstrings) {
							code |= 0;
							if (firstCharIndex > code || !firstCharIndex) {
								firstCharIndex = code;
							}
							if (lastCharIndex < code) {
								lastCharIndex = code;
							}
							var position = getUnicodeRangeFor(code);
							if (position < 32) {
								ulUnicodeRange1 |= 1 << position;
							} else if (position < 64) {
								ulUnicodeRange2 |= 1 << position - 32;
							} else if (position < 96) {
								ulUnicodeRange3 |= 1 << position - 64;
							} else if (position < 123) {
								ulUnicodeRange4 |= 1 << position - 96;
							} else {
								error('Unicode ranges Bits > 123 are reserved for internal usage');
							}
						}
					} else {
						firstCharIndex = 0;
						lastCharIndex = 255;
					}
					var bbox = properties.bbox || [
						0,
						0,
						0,
						0
					];
					var unitsPerEm = override.unitsPerEm || 1 / (properties.fontMatrix || FONT_IDENTITY_MATRIX)[0];
					var scale = properties.ascentScaled ? 1.0 : unitsPerEm / PDF_GLYPH_SPACE_UNITS;
					var typoAscent = override.ascent || Math.round(scale * (properties.ascent || bbox[3]));
					var typoDescent = override.descent || Math.round(scale * (properties.descent || bbox[1]));
					if (typoDescent > 0 && properties.descent > 0 && bbox[1] < 0) {
						typoDescent = -typoDescent;
					}
					var winAscent = override.yMax || typoAscent;
					var winDescent = -override.yMin || -typoDescent;
					return '\x00\x03' + '\x02\x24' + '\x01\xF4' + '\x00\x05' + '\x00\x00' + '\x02\x8A' + '\x02\xBB' + '\x00\x00' + '\x00\x8C' + '\x02\x8A' + '\x02\xBB' + '\x00\x00' + '\x01\xDF' + '\x00\x31' + '\x01\x02' + '\x00\x00' + '\x00\x00\x06' + String.fromCharCode(properties.fixedPitch ? 0x09 : 0x00) + '\x00\x00\x00\x00\x00\x00' + string32(ulUnicodeRange1) + string32(ulUnicodeRange2) + string32(ulUnicodeRange3) + string32(ulUnicodeRange4) + '\x2A\x32\x31\x2A' + string16(properties.italicAngle ? 1 : 0) + string16(firstCharIndex || properties.firstChar) + string16(lastCharIndex || properties.lastChar) + string16(typoAscent) + string16(typoDescent) + '\x00\x64' + string16(winAscent) + string16(winDescent) + '\x00\x00\x00\x00' + '\x00\x00\x00\x00' + string16(properties.xHeight) + string16(properties.capHeight) + string16(0) + string16(firstCharIndex || properties.firstChar) + '\x00\x03';
				}
				function createPostTable(properties) {
					var angle = Math.floor(properties.italicAngle * Math.pow(2, 16));
					return '\x00\x03\x00\x00' + string32(angle) + '\x00\x00' + '\x00\x00' + string32(properties.fixedPitch) + '\x00\x00\x00\x00' + '\x00\x00\x00\x00' + '\x00\x00\x00\x00' + '\x00\x00\x00\x00';
				}
				function createNameTable(name, proto) {
					if (!proto) {
						proto = [
							[],
							[]
						];
					}
					var strings = [
						proto[0][0] || 'Original licence',
						proto[0][1] || name,
						proto[0][2] || 'Unknown',
						proto[0][3] || 'uniqueID',
						proto[0][4] || name,
						proto[0][5] || 'Version 0.11',
						proto[0][6] || '',
						proto[0][7] || 'Unknown',
						proto[0][8] || 'Unknown',
						proto[0][9] || 'Unknown'
					];
					var stringsUnicode = [];
					var i, ii, j, jj, str;
					for (i = 0, ii = strings.length; i < ii; i++) {
						str = proto[1][i] || strings[i];
						var strBufUnicode = [];
						for (j = 0, jj = str.length; j < jj; j++) {
							strBufUnicode.push(string16(str.charCodeAt(j)));
						}
						stringsUnicode.push(strBufUnicode.join(''));
					}
					var names = [
						strings,
						stringsUnicode
					];
					var platforms = [
						'\x00\x01',
						'\x00\x03'
					];
					var encodings = [
						'\x00\x00',
						'\x00\x01'
					];
					var languages = [
						'\x00\x00',
						'\x04\x09'
					];
					var namesRecordCount = strings.length * platforms.length;
					var nameTable = '\x00\x00' + string16(namesRecordCount) + string16(namesRecordCount * 12 + 6);
					var strOffset = 0;
					for (i = 0, ii = platforms.length; i < ii; i++) {
						var strs = names[i];
						for (j = 0, jj = strs.length; j < jj; j++) {
							str = strs[j];
							var nameRecord = platforms[i] + encodings[i] + languages[i] + string16(j) + string16(str.length) + string16(strOffset);
							nameTable += nameRecord;
							strOffset += str.length;
						}
					}
					nameTable += strings.join('') + stringsUnicode.join('');
					return nameTable;
				}
				Font.prototype = {
					name: null,
					font: null,
					mimetype: null,
					encoding: null,
					get renderer() {
						var renderer = FontRendererFactory.create(this, SEAC_ANALYSIS_ENABLED);
						return shadow(this, 'renderer', renderer);
					},
					exportData: function Font_exportData() {
						var data = {};
						for (var i in this) {
							if (this.hasOwnProperty(i)) {
								data[i] = this[i];
							}
						}
						return data;
					},
					checkAndRepair: function Font_checkAndRepair(name, font, properties) {
						function readTableEntry(file) {
							var tag = bytesToString(file.getBytes(4));
							var checksum = file.getInt32() >>> 0;
							var offset = file.getInt32() >>> 0;
							var length = file.getInt32() >>> 0;
							var previousPosition = file.pos;
							file.pos = file.start ? file.start : 0;
							file.skip(offset);
							var data = file.getBytes(length);
							file.pos = previousPosition;
							if (tag === 'head') {
								data[8] = data[9] = data[10] = data[11] = 0;
								data[17] |= 0x20;
							}
							return {
								tag: tag,
								checksum: checksum,
								length: length,
								offset: offset,
								data: data
							};
						}
						function readOpenTypeHeader(ttf) {
							return {
								version: bytesToString(ttf.getBytes(4)),
								numTables: ttf.getUint16(),
								searchRange: ttf.getUint16(),
								entrySelector: ttf.getUint16(),
								rangeShift: ttf.getUint16()
							};
						}
						function readCmapTable(cmap, font, isSymbolicFont, hasEncoding) {
							if (!cmap) {
								warn('No cmap table available.');
								return {
									platformId: -1,
									encodingId: -1,
									mappings: [],
									hasShortCmap: false
								};
							}
							var segment;
							var start = (font.start ? font.start : 0) + cmap.offset;
							font.pos = start;
							font.getUint16();
							var numTables = font.getUint16();
							var potentialTable;
							var canBreak = false;
							for (var i = 0; i < numTables; i++) {
								var platformId = font.getUint16();
								var encodingId = font.getUint16();
								var offset = font.getInt32() >>> 0;
								var useTable = false;
								if (platformId === 0 && encodingId === 0) {
									useTable = true;
								} else if (platformId === 1 && encodingId === 0) {
									useTable = true;
								} else if (platformId === 3 && encodingId === 1 && (hasEncoding || !potentialTable)) {
									useTable = true;
									if (!isSymbolicFont) {
										canBreak = true;
									}
								} else if (isSymbolicFont && platformId === 3 && encodingId === 0) {
									useTable = true;
									canBreak = true;
								}
								if (useTable) {
									potentialTable = {
										platformId: platformId,
										encodingId: encodingId,
										offset: offset
									};
								}
								if (canBreak) {
									break;
								}
							}
							if (potentialTable) {
								font.pos = start + potentialTable.offset;
							}
							if (!potentialTable || font.peekByte() === -1) {
								warn('Could not find a preferred cmap table.');
								return {
									platformId: -1,
									encodingId: -1,
									mappings: [],
									hasShortCmap: false
								};
							}
							var format = font.getUint16();
							font.getUint16();
							font.getUint16();
							var hasShortCmap = false;
							var mappings = [];
							var j, glyphId;
							if (format === 0) {
								for (j = 0; j < 256; j++) {
									var index = font.getByte();
									if (!index) {
										continue;
									}
									mappings.push({
										charCode: j,
										glyphId: index
									});
								}
								hasShortCmap = true;
							} else if (format === 4) {
								var segCount = font.getUint16() >> 1;
								font.getBytes(6);
								var segIndex, segments = [];
								for (segIndex = 0; segIndex < segCount; segIndex++) {
									segments.push({ end: font.getUint16() });
								}
								font.getUint16();
								for (segIndex = 0; segIndex < segCount; segIndex++) {
									segments[segIndex].start = font.getUint16();
								}
								for (segIndex = 0; segIndex < segCount; segIndex++) {
									segments[segIndex].delta = font.getUint16();
								}
								var offsetsCount = 0;
								for (segIndex = 0; segIndex < segCount; segIndex++) {
									segment = segments[segIndex];
									var rangeOffset = font.getUint16();
									if (!rangeOffset) {
										segment.offsetIndex = -1;
										continue;
									}
									var offsetIndex = (rangeOffset >> 1) - (segCount - segIndex);
									segment.offsetIndex = offsetIndex;
									offsetsCount = Math.max(offsetsCount, offsetIndex + segment.end - segment.start + 1);
								}
								var offsets = [];
								for (j = 0; j < offsetsCount; j++) {
									offsets.push(font.getUint16());
								}
								for (segIndex = 0; segIndex < segCount; segIndex++) {
									segment = segments[segIndex];
									start = segment.start;
									var end = segment.end;
									var delta = segment.delta;
									offsetIndex = segment.offsetIndex;
									for (j = start; j <= end; j++) {
										if (j === 0xFFFF) {
											continue;
										}
										glyphId = offsetIndex < 0 ? j : offsets[offsetIndex + j - start];
										glyphId = glyphId + delta & 0xFFFF;
										if (glyphId === 0) {
											continue;
										}
										mappings.push({
											charCode: j,
											glyphId: glyphId
										});
									}
								}
							} else if (format === 6) {
								var firstCode = font.getUint16();
								var entryCount = font.getUint16();
								for (j = 0; j < entryCount; j++) {
									glyphId = font.getUint16();
									var charCode = firstCode + j;
									mappings.push({
										charCode: charCode,
										glyphId: glyphId
									});
								}
							} else {
								warn('cmap table has unsupported format: ' + format);
								return {
									platformId: -1,
									encodingId: -1,
									mappings: [],
									hasShortCmap: false
								};
							}
							mappings.sort(function (a, b) {
								return a.charCode - b.charCode;
							});
							for (i = 1; i < mappings.length; i++) {
								if (mappings[i - 1].charCode === mappings[i].charCode) {
									mappings.splice(i, 1);
									i--;
								}
							}
							return {
								platformId: potentialTable.platformId,
								encodingId: potentialTable.encodingId,
								mappings: mappings,
								hasShortCmap: hasShortCmap
							};
						}
						function sanitizeMetrics(font, header, metrics, numGlyphs) {
							if (!header) {
								if (metrics) {
									metrics.data = null;
								}
								return;
							}
							font.pos = (font.start ? font.start : 0) + header.offset;
							font.pos += header.length - 2;
							var numOfMetrics = font.getUint16();
							if (numOfMetrics > numGlyphs) {
								info('The numOfMetrics (' + numOfMetrics + ') should not be ' + 'greater than the numGlyphs (' + numGlyphs + ')');
								numOfMetrics = numGlyphs;
								header.data[34] = (numOfMetrics & 0xff00) >> 8;
								header.data[35] = numOfMetrics & 0x00ff;
							}
							var numOfSidebearings = numGlyphs - numOfMetrics;
							var numMissing = numOfSidebearings - (metrics.length - numOfMetrics * 4 >> 1);
							if (numMissing > 0) {
								var entries = new Uint8Array(metrics.length + numMissing * 2);
								entries.set(metrics.data);
								metrics.data = entries;
							}
						}
						function sanitizeGlyph(source, sourceStart, sourceEnd, dest, destStart, hintsValid) {
							if (sourceEnd - sourceStart <= 12) {
								return 0;
							}
							var glyf = source.subarray(sourceStart, sourceEnd);
							var contoursCount = glyf[0] << 8 | glyf[1];
							if (contoursCount & 0x8000) {
								dest.set(glyf, destStart);
								return glyf.length;
							}
							var i, j = 10, flagsCount = 0;
							for (i = 0; i < contoursCount; i++) {
								var endPoint = glyf[j] << 8 | glyf[j + 1];
								flagsCount = endPoint + 1;
								j += 2;
							}
							var instructionsStart = j;
							var instructionsLength = glyf[j] << 8 | glyf[j + 1];
							j += 2 + instructionsLength;
							var instructionsEnd = j;
							var coordinatesLength = 0;
							for (i = 0; i < flagsCount; i++) {
								var flag = glyf[j++];
								if (flag & 0xC0) {
									glyf[j - 1] = flag & 0x3F;
								}
								var xyLength = (flag & 2 ? 1 : flag & 16 ? 0 : 2) + (flag & 4 ? 1 : flag & 32 ? 0 : 2);
								coordinatesLength += xyLength;
								if (flag & 8) {
									var repeat = glyf[j++];
									i += repeat;
									coordinatesLength += repeat * xyLength;
								}
							}
							if (coordinatesLength === 0) {
								return 0;
							}
							var glyphDataLength = j + coordinatesLength;
							if (glyphDataLength > glyf.length) {
								return 0;
							}
							if (!hintsValid && instructionsLength > 0) {
								dest.set(glyf.subarray(0, instructionsStart), destStart);
								dest.set([
									0,
									0
								], destStart + instructionsStart);
								dest.set(glyf.subarray(instructionsEnd, glyphDataLength), destStart + instructionsStart + 2);
								glyphDataLength -= instructionsLength;
								if (glyf.length - glyphDataLength > 3) {
									glyphDataLength = glyphDataLength + 3 & ~3;
								}
								return glyphDataLength;
							}
							if (glyf.length - glyphDataLength > 3) {
								glyphDataLength = glyphDataLength + 3 & ~3;
								dest.set(glyf.subarray(0, glyphDataLength), destStart);
								return glyphDataLength;
							}
							dest.set(glyf, destStart);
							return glyf.length;
						}
						function sanitizeHead(head, numGlyphs, locaLength) {
							var data = head.data;
							var version = int32(data[0], data[1], data[2], data[3]);
							if (version >> 16 !== 1) {
								info('Attempting to fix invalid version in head table: ' + version);
								data[0] = 0;
								data[1] = 1;
								data[2] = 0;
								data[3] = 0;
							}
							var indexToLocFormat = int16(data[50], data[51]);
							if (indexToLocFormat < 0 || indexToLocFormat > 1) {
								info('Attempting to fix invalid indexToLocFormat in head table: ' + indexToLocFormat);
								var numGlyphsPlusOne = numGlyphs + 1;
								if (locaLength === numGlyphsPlusOne << 1) {
									data[50] = 0;
									data[51] = 0;
								} else if (locaLength === numGlyphsPlusOne << 2) {
									data[50] = 0;
									data[51] = 1;
								} else {
									warn('Could not fix indexToLocFormat: ' + indexToLocFormat);
								}
							}
						}
						function sanitizeGlyphLocations(loca, glyf, numGlyphs, isGlyphLocationsLong, hintsValid, dupFirstEntry) {
							var itemSize, itemDecode, itemEncode;
							if (isGlyphLocationsLong) {
								itemSize = 4;
								itemDecode = function fontItemDecodeLong(data, offset) {
									return data[offset] << 24 | data[offset + 1] << 16 | data[offset + 2] << 8 | data[offset + 3];
								};
								itemEncode = function fontItemEncodeLong(data, offset, value) {
									data[offset] = value >>> 24 & 0xFF;
									data[offset + 1] = value >> 16 & 0xFF;
									data[offset + 2] = value >> 8 & 0xFF;
									data[offset + 3] = value & 0xFF;
								};
							} else {
								itemSize = 2;
								itemDecode = function fontItemDecode(data, offset) {
									return data[offset] << 9 | data[offset + 1] << 1;
								};
								itemEncode = function fontItemEncode(data, offset, value) {
									data[offset] = value >> 9 & 0xFF;
									data[offset + 1] = value >> 1 & 0xFF;
								};
							}
							var locaData = loca.data;
							var locaDataSize = itemSize * (1 + numGlyphs);
							if (locaData.length !== locaDataSize) {
								locaData = new Uint8Array(locaDataSize);
								locaData.set(loca.data.subarray(0, locaDataSize));
								loca.data = locaData;
							}
							var oldGlyfData = glyf.data;
							var oldGlyfDataLength = oldGlyfData.length;
							var newGlyfData = new Uint8Array(oldGlyfDataLength);
							var startOffset = itemDecode(locaData, 0);
							var writeOffset = 0;
							var missingGlyphData = Object.create(null);
							itemEncode(locaData, 0, writeOffset);
							var i, j;
							for (i = 0, j = itemSize; i < numGlyphs; i++, j += itemSize) {
								var endOffset = itemDecode(locaData, j);
								if (endOffset > oldGlyfDataLength && (oldGlyfDataLength + 3 & ~3) === endOffset) {
									endOffset = oldGlyfDataLength;
								}
								if (endOffset > oldGlyfDataLength) {
									itemEncode(locaData, j, writeOffset);
									startOffset = endOffset;
									continue;
								}
								if (startOffset === endOffset) {
									missingGlyphData[i] = true;
								}
								var newLength = sanitizeGlyph(oldGlyfData, startOffset, endOffset, newGlyfData, writeOffset, hintsValid);
								writeOffset += newLength;
								itemEncode(locaData, j, writeOffset);
								startOffset = endOffset;
							}
							if (writeOffset === 0) {
								var simpleGlyph = new Uint8Array([
									0,
									1,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									0,
									49,
									0
								]);
								for (i = 0, j = itemSize; i < numGlyphs; i++, j += itemSize) {
									itemEncode(locaData, j, simpleGlyph.length);
								}
								glyf.data = simpleGlyph;
								return missingGlyphData;
							}
							if (dupFirstEntry) {
								var firstEntryLength = itemDecode(locaData, itemSize);
								if (newGlyfData.length > firstEntryLength + writeOffset) {
									glyf.data = newGlyfData.subarray(0, firstEntryLength + writeOffset);
								} else {
									glyf.data = new Uint8Array(firstEntryLength + writeOffset);
									glyf.data.set(newGlyfData.subarray(0, writeOffset));
								}
								glyf.data.set(newGlyfData.subarray(0, firstEntryLength), writeOffset);
								itemEncode(loca.data, locaData.length - itemSize, writeOffset + firstEntryLength);
							} else {
								glyf.data = newGlyfData.subarray(0, writeOffset);
							}
							return missingGlyphData;
						}
						function readPostScriptTable(post, properties, maxpNumGlyphs) {
							var start = (font.start ? font.start : 0) + post.offset;
							font.pos = start;
							var length = post.length, end = start + length;
							var version = font.getInt32();
							font.getBytes(28);
							var glyphNames;
							var valid = true;
							var i;
							switch (version) {
								case 0x00010000:
									glyphNames = MacStandardGlyphOrdering;
									break;
								case 0x00020000:
									var numGlyphs = font.getUint16();
									if (numGlyphs !== maxpNumGlyphs) {
										valid = false;
										break;
									}
									var glyphNameIndexes = [];
									for (i = 0; i < numGlyphs; ++i) {
										var index = font.getUint16();
										if (index >= 32768) {
											valid = false;
											break;
										}
										glyphNameIndexes.push(index);
									}
									if (!valid) {
										break;
									}
									var customNames = [];
									var strBuf = [];
									while (font.pos < end) {
										var stringLength = font.getByte();
										strBuf.length = stringLength;
										for (i = 0; i < stringLength; ++i) {
											strBuf[i] = String.fromCharCode(font.getByte());
										}
										customNames.push(strBuf.join(''));
									}
									glyphNames = [];
									for (i = 0; i < numGlyphs; ++i) {
										var j = glyphNameIndexes[i];
										if (j < 258) {
											glyphNames.push(MacStandardGlyphOrdering[j]);
											continue;
										}
										glyphNames.push(customNames[j - 258]);
									}
									break;
								case 0x00030000:
									break;
								default:
									warn('Unknown/unsupported post table version ' + version);
									valid = false;
									if (properties.defaultEncoding) {
										glyphNames = properties.defaultEncoding;
									}
									break;
							}
							properties.glyphNames = glyphNames;
							return valid;
						}
						function readNameTable(nameTable) {
							var start = (font.start ? font.start : 0) + nameTable.offset;
							font.pos = start;
							var names = [
								[],
								[]
							];
							var length = nameTable.length, end = start + length;
							var format = font.getUint16();
							var FORMAT_0_HEADER_LENGTH = 6;
							if (format !== 0 || length < FORMAT_0_HEADER_LENGTH) {
								return names;
							}
							var numRecords = font.getUint16();
							var stringsStart = font.getUint16();
							var records = [];
							var NAME_RECORD_LENGTH = 12;
							var i, ii;
							for (i = 0; i < numRecords && font.pos + NAME_RECORD_LENGTH <= end; i++) {
								var r = {
									platform: font.getUint16(),
									encoding: font.getUint16(),
									language: font.getUint16(),
									name: font.getUint16(),
									length: font.getUint16(),
									offset: font.getUint16()
								};
								if (r.platform === 1 && r.encoding === 0 && r.language === 0 || r.platform === 3 && r.encoding === 1 && r.language === 0x409) {
									records.push(r);
								}
							}
							for (i = 0, ii = records.length; i < ii; i++) {
								var record = records[i];
								if (record.length <= 0) {
									continue;
								}
								var pos = start + stringsStart + record.offset;
								if (pos + record.length > end) {
									continue;
								}
								font.pos = pos;
								var nameIndex = record.name;
								if (record.encoding) {
									var str = '';
									for (var j = 0, jj = record.length; j < jj; j += 2) {
										str += String.fromCharCode(font.getUint16());
									}
									names[1][nameIndex] = str;
								} else {
									names[0][nameIndex] = bytesToString(font.getBytes(record.length));
								}
							}
							return names;
						}
						var TTOpsStackDeltas = [
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							-2,
							-2,
							-2,
							-2,
							0,
							0,
							-2,
							-5,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							0,
							0,
							-1,
							0,
							-1,
							-1,
							-1,
							-1,
							1,
							-1,
							-999,
							0,
							1,
							0,
							-1,
							-2,
							0,
							-1,
							-2,
							-1,
							-1,
							0,
							-1,
							-1,
							0,
							0,
							-999,
							-999,
							-1,
							-1,
							-1,
							-1,
							-2,
							-999,
							-2,
							-2,
							-999,
							0,
							-2,
							-2,
							0,
							0,
							-2,
							0,
							-2,
							0,
							0,
							0,
							-2,
							-1,
							-1,
							1,
							1,
							0,
							0,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							0,
							0,
							-1,
							0,
							-1,
							-1,
							0,
							-999,
							-1,
							-1,
							-1,
							-1,
							-1,
							-1,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							0,
							-2,
							-999,
							-999,
							-999,
							-999,
							-999,
							-1,
							-1,
							-2,
							-2,
							0,
							0,
							0,
							0,
							-1,
							-1,
							-999,
							-2,
							-2,
							0,
							0,
							-1,
							-2,
							-2,
							0,
							0,
							0,
							-1,
							-1,
							-1,
							-2
						];
						function sanitizeTTProgram(table, ttContext) {
							var data = table.data;
							var i = 0, j, n, b, funcId, pc, lastEndf = 0, lastDeff = 0;
							var stack = [];
							var callstack = [];
							var functionsCalled = [];
							var tooComplexToFollowFunctions = ttContext.tooComplexToFollowFunctions;
							var inFDEF = false, ifLevel = 0, inELSE = 0;
							for (var ii = data.length; i < ii;) {
								var op = data[i++];
								if (op === 0x40) {
									n = data[i++];
									if (inFDEF || inELSE) {
										i += n;
									} else {
										for (j = 0; j < n; j++) {
											stack.push(data[i++]);
										}
									}
								} else if (op === 0x41) {
									n = data[i++];
									if (inFDEF || inELSE) {
										i += n * 2;
									} else {
										for (j = 0; j < n; j++) {
											b = data[i++];
											stack.push(b << 8 | data[i++]);
										}
									}
								} else if ((op & 0xF8) === 0xB0) {
									n = op - 0xB0 + 1;
									if (inFDEF || inELSE) {
										i += n;
									} else {
										for (j = 0; j < n; j++) {
											stack.push(data[i++]);
										}
									}
								} else if ((op & 0xF8) === 0xB8) {
									n = op - 0xB8 + 1;
									if (inFDEF || inELSE) {
										i += n * 2;
									} else {
										for (j = 0; j < n; j++) {
											b = data[i++];
											stack.push(b << 8 | data[i++]);
										}
									}
								} else if (op === 0x2B && !tooComplexToFollowFunctions) {
									if (!inFDEF && !inELSE) {
										funcId = stack[stack.length - 1];
										ttContext.functionsUsed[funcId] = true;
										if (funcId in ttContext.functionsStackDeltas) {
											stack.length += ttContext.functionsStackDeltas[funcId];
										} else if (funcId in ttContext.functionsDefined && functionsCalled.indexOf(funcId) < 0) {
											callstack.push({
												data: data,
												i: i,
												stackTop: stack.length - 1
											});
											functionsCalled.push(funcId);
											pc = ttContext.functionsDefined[funcId];
											if (!pc) {
												warn('TT: CALL non-existent function');
												ttContext.hintsValid = false;
												return;
											}
											data = pc.data;
											i = pc.i;
										}
									}
								} else if (op === 0x2C && !tooComplexToFollowFunctions) {
									if (inFDEF || inELSE) {
										warn('TT: nested FDEFs not allowed');
										tooComplexToFollowFunctions = true;
									}
									inFDEF = true;
									lastDeff = i;
									funcId = stack.pop();
									ttContext.functionsDefined[funcId] = {
										data: data,
										i: i
									};
								} else if (op === 0x2D) {
									if (inFDEF) {
										inFDEF = false;
										lastEndf = i;
									} else {
										pc = callstack.pop();
										if (!pc) {
											warn('TT: ENDF bad stack');
											ttContext.hintsValid = false;
											return;
										}
										funcId = functionsCalled.pop();
										data = pc.data;
										i = pc.i;
										ttContext.functionsStackDeltas[funcId] = stack.length - pc.stackTop;
									}
								} else if (op === 0x89) {
									if (inFDEF || inELSE) {
										warn('TT: nested IDEFs not allowed');
										tooComplexToFollowFunctions = true;
									}
									inFDEF = true;
									lastDeff = i;
								} else if (op === 0x58) {
									++ifLevel;
								} else if (op === 0x1B) {
									inELSE = ifLevel;
								} else if (op === 0x59) {
									if (inELSE === ifLevel) {
										inELSE = 0;
									}
									--ifLevel;
								} else if (op === 0x1C) {
									if (!inFDEF && !inELSE) {
										var offset = stack[stack.length - 1];
										if (offset > 0) {
											i += offset - 1;
										}
									}
								}
								if (!inFDEF && !inELSE) {
									var stackDelta = op <= 0x8E ? TTOpsStackDeltas[op] : op >= 0xC0 && op <= 0xDF ? -1 : op >= 0xE0 ? -2 : 0;
									if (op >= 0x71 && op <= 0x75) {
										n = stack.pop();
										if (!isNaN(n)) {
											stackDelta = -n * 2;
										}
									}
									while (stackDelta < 0 && stack.length > 0) {
										stack.pop();
										stackDelta++;
									}
									while (stackDelta > 0) {
										stack.push(NaN);
										stackDelta--;
									}
								}
							}
							ttContext.tooComplexToFollowFunctions = tooComplexToFollowFunctions;
							var content = [data];
							if (i > data.length) {
								content.push(new Uint8Array(i - data.length));
							}
							if (lastDeff > lastEndf) {
								warn('TT: complementing a missing function tail');
								content.push(new Uint8Array([
									0x22,
									0x2D
								]));
							}
							foldTTTable(table, content);
						}
						function checkInvalidFunctions(ttContext, maxFunctionDefs) {
							if (ttContext.tooComplexToFollowFunctions) {
								return;
							}
							if (ttContext.functionsDefined.length > maxFunctionDefs) {
								warn('TT: more functions defined than expected');
								ttContext.hintsValid = false;
								return;
							}
							for (var j = 0, jj = ttContext.functionsUsed.length; j < jj; j++) {
								if (j > maxFunctionDefs) {
									warn('TT: invalid function id: ' + j);
									ttContext.hintsValid = false;
									return;
								}
								if (ttContext.functionsUsed[j] && !ttContext.functionsDefined[j]) {
									warn('TT: undefined function: ' + j);
									ttContext.hintsValid = false;
									return;
								}
							}
						}
						function foldTTTable(table, content) {
							if (content.length > 1) {
								var newLength = 0;
								var j, jj;
								for (j = 0, jj = content.length; j < jj; j++) {
									newLength += content[j].length;
								}
								newLength = newLength + 3 & ~3;
								var result = new Uint8Array(newLength);
								var pos = 0;
								for (j = 0, jj = content.length; j < jj; j++) {
									result.set(content[j], pos);
									pos += content[j].length;
								}
								table.data = result;
								table.length = newLength;
							}
						}
						function sanitizeTTPrograms(fpgm, prep, cvt, maxFunctionDefs) {
							var ttContext = {
								functionsDefined: [],
								functionsUsed: [],
								functionsStackDeltas: [],
								tooComplexToFollowFunctions: false,
								hintsValid: true
							};
							if (fpgm) {
								sanitizeTTProgram(fpgm, ttContext);
							}
							if (prep) {
								sanitizeTTProgram(prep, ttContext);
							}
							if (fpgm) {
								checkInvalidFunctions(ttContext, maxFunctionDefs);
							}
							if (cvt && cvt.length & 1) {
								var cvtData = new Uint8Array(cvt.length + 1);
								cvtData.set(cvt.data);
								cvt.data = cvtData;
							}
							return ttContext.hintsValid;
						}
						font = new Stream(new Uint8Array(font.getBytes()));
						var VALID_TABLES = [
							'OS/2',
							'cmap',
							'head',
							'hhea',
							'hmtx',
							'maxp',
							'name',
							'post',
							'loca',
							'glyf',
							'fpgm',
							'prep',
							'cvt ',
							'CFF '
						];
						var header = readOpenTypeHeader(font);
						var numTables = header.numTables;
						var cff, cffFile;
						var tables = Object.create(null);
						tables['OS/2'] = null;
						tables['cmap'] = null;
						tables['head'] = null;
						tables['hhea'] = null;
						tables['hmtx'] = null;
						tables['maxp'] = null;
						tables['name'] = null;
						tables['post'] = null;
						var table;
						for (var i = 0; i < numTables; i++) {
							table = readTableEntry(font);
							if (VALID_TABLES.indexOf(table.tag) < 0) {
								continue;
							}
							if (table.length === 0) {
								continue;
							}
							tables[table.tag] = table;
						}
						var isTrueType = !tables['CFF '];
						if (!isTrueType) {
							if (header.version === 'OTTO' && !properties.composite || !tables['head'] || !tables['hhea'] || !tables['maxp'] || !tables['post']) {
								cffFile = new Stream(tables['CFF '].data);
								cff = new CFFFont(cffFile, properties);
								adjustWidths(properties);
								return this.convert(name, cff, properties);
							}
							delete tables['glyf'];
							delete tables['loca'];
							delete tables['fpgm'];
							delete tables['prep'];
							delete tables['cvt '];
							this.isOpenType = true;
						} else {
							if (!tables['loca']) {
								error('Required "loca" table is not found');
							}
							if (!tables['glyf']) {
								warn('Required "glyf" table is not found -- trying to recover.');
								tables['glyf'] = {
									tag: 'glyf',
									data: new Uint8Array(0)
								};
							}
							this.isOpenType = false;
						}
						if (!tables['maxp']) {
							error('Required "maxp" table is not found');
						}
						font.pos = (font.start || 0) + tables['maxp'].offset;
						var version = font.getInt32();
						var numGlyphs = font.getUint16();
						var maxFunctionDefs = 0;
						if (version >= 0x00010000 && tables['maxp'].length >= 22) {
							font.pos += 8;
							var maxZones = font.getUint16();
							if (maxZones > 2) {
								tables['maxp'].data[14] = 0;
								tables['maxp'].data[15] = 2;
							}
							font.pos += 4;
							maxFunctionDefs = font.getUint16();
						}
						var dupFirstEntry = false;
						if (properties.type === 'CIDFontType2' && properties.toUnicode && properties.toUnicode.get(0) > '\u0000') {
							dupFirstEntry = true;
							numGlyphs++;
							tables['maxp'].data[4] = numGlyphs >> 8;
							tables['maxp'].data[5] = numGlyphs & 255;
						}
						var hintsValid = sanitizeTTPrograms(tables['fpgm'], tables['prep'], tables['cvt '], maxFunctionDefs);
						if (!hintsValid) {
							delete tables['fpgm'];
							delete tables['prep'];
							delete tables['cvt '];
						}
						sanitizeMetrics(font, tables['hhea'], tables['hmtx'], numGlyphs);
						if (!tables['head']) {
							error('Required "head" table is not found');
						}
						sanitizeHead(tables['head'], numGlyphs, isTrueType ? tables['loca'].length : 0);
						var missingGlyphs = Object.create(null);
						if (isTrueType) {
							var isGlyphLocationsLong = int16(tables['head'].data[50], tables['head'].data[51]);
							missingGlyphs = sanitizeGlyphLocations(tables['loca'], tables['glyf'], numGlyphs, isGlyphLocationsLong, hintsValid, dupFirstEntry);
						}
						if (!tables['hhea']) {
							error('Required "hhea" table is not found');
						}
						if (tables['hhea'].data[10] === 0 && tables['hhea'].data[11] === 0) {
							tables['hhea'].data[10] = 0xFF;
							tables['hhea'].data[11] = 0xFF;
						}
						var metricsOverride = {
							unitsPerEm: int16(tables['head'].data[18], tables['head'].data[19]),
							yMax: int16(tables['head'].data[42], tables['head'].data[43]),
							yMin: signedInt16(tables['head'].data[38], tables['head'].data[39]),
							ascent: int16(tables['hhea'].data[4], tables['hhea'].data[5]),
							descent: signedInt16(tables['hhea'].data[6], tables['hhea'].data[7])
						};
						this.ascent = metricsOverride.ascent / metricsOverride.unitsPerEm;
						this.descent = metricsOverride.descent / metricsOverride.unitsPerEm;
						if (tables['post']) {
							var valid = readPostScriptTable(tables['post'], properties, numGlyphs);
							if (!valid) {
								tables['post'] = null;
							}
						}
						var charCodeToGlyphId = [], charCode;
						var toUnicode = properties.toUnicode, widths = properties.widths;
						var skipToUnicode = toUnicode instanceof IdentityToUnicodeMap || toUnicode.length === 0x10000;
						function hasGlyph(glyphId, charCode, widthCode) {
							if (!missingGlyphs[glyphId]) {
								return true;
							}
							if (!skipToUnicode && charCode >= 0 && toUnicode.has(charCode)) {
								return true;
							}
							if (widths && widthCode >= 0 && isNum(widths[widthCode])) {
								return true;
							}
							return false;
						}
						if (properties.composite) {
							var cidToGidMap = properties.cidToGidMap || [];
							var isCidToGidMapEmpty = cidToGidMap.length === 0;
							properties.cMap.forEach(function (charCode, cid) {
								assert(cid <= 0xffff, 'Max size of CID is 65,535');
								var glyphId = -1;
								if (isCidToGidMapEmpty) {
									glyphId = cid;
								} else if (cidToGidMap[cid] !== undefined) {
									glyphId = cidToGidMap[cid];
								}
								if (glyphId >= 0 && glyphId < numGlyphs && hasGlyph(glyphId, charCode, cid)) {
									charCodeToGlyphId[charCode] = glyphId;
								}
							});
							if (dupFirstEntry && (isCidToGidMapEmpty || !charCodeToGlyphId[0])) {
								charCodeToGlyphId[0] = numGlyphs - 1;
							}
						} else {
							var cmapTable = readCmapTable(tables['cmap'], font, this.isSymbolicFont, properties.hasEncoding);
							var cmapPlatformId = cmapTable.platformId;
							var cmapEncodingId = cmapTable.encodingId;
							var cmapMappings = cmapTable.mappings;
							var cmapMappingsLength = cmapMappings.length;
							if (properties.hasEncoding && (cmapPlatformId === 3 && cmapEncodingId === 1 || cmapPlatformId === 1 && cmapEncodingId === 0) || cmapPlatformId === -1 && cmapEncodingId === -1 && !!getEncoding(properties.baseEncodingName)) {
								var baseEncoding = [];
								if (properties.baseEncodingName === 'MacRomanEncoding' || properties.baseEncodingName === 'WinAnsiEncoding') {
									baseEncoding = getEncoding(properties.baseEncodingName);
								}
								var glyphsUnicodeMap = getGlyphsUnicode();
								for (charCode = 0; charCode < 256; charCode++) {
									var glyphName, standardGlyphName;
									if (this.differences && charCode in this.differences) {
										glyphName = this.differences[charCode];
									} else if (charCode in baseEncoding && baseEncoding[charCode] !== '') {
										glyphName = baseEncoding[charCode];
									} else {
										glyphName = StandardEncoding[charCode];
									}
									if (!glyphName) {
										continue;
									}
									standardGlyphName = recoverGlyphName(glyphName, glyphsUnicodeMap);
									var unicodeOrCharCode, isUnicode = false;
									if (cmapPlatformId === 3 && cmapEncodingId === 1) {
										unicodeOrCharCode = glyphsUnicodeMap[standardGlyphName];
										isUnicode = true;
									} else if (cmapPlatformId === 1 && cmapEncodingId === 0) {
										unicodeOrCharCode = MacRomanEncoding.indexOf(standardGlyphName);
									}
									var found = false;
									for (i = 0; i < cmapMappingsLength; ++i) {
										if (cmapMappings[i].charCode !== unicodeOrCharCode) {
											continue;
										}
										var code = isUnicode ? charCode : unicodeOrCharCode;
										if (hasGlyph(cmapMappings[i].glyphId, code, -1)) {
											charCodeToGlyphId[charCode] = cmapMappings[i].glyphId;
											found = true;
											break;
										}
									}
									if (!found && properties.glyphNames) {
										var glyphId = properties.glyphNames.indexOf(glyphName);
										if (glyphId === -1 && standardGlyphName !== glyphName) {
											glyphId = properties.glyphNames.indexOf(standardGlyphName);
										}
										if (glyphId > 0 && hasGlyph(glyphId, -1, -1)) {
											charCodeToGlyphId[charCode] = glyphId;
											found = true;
										}
									}
									if (!found) {
										charCodeToGlyphId[charCode] = 0;
									}
								}
							} else if (cmapPlatformId === 0 && cmapEncodingId === 0) {
								for (i = 0; i < cmapMappingsLength; ++i) {
									charCodeToGlyphId[cmapMappings[i].charCode] = cmapMappings[i].glyphId;
								}
							} else {
								for (i = 0; i < cmapMappingsLength; ++i) {
									charCode = cmapMappings[i].charCode & 0xFF;
									charCodeToGlyphId[charCode] = cmapMappings[i].glyphId;
								}
							}
						}
						if (charCodeToGlyphId.length === 0) {
							charCodeToGlyphId[0] = 0;
						}
						var newMapping = adjustMapping(charCodeToGlyphId, properties);
						this.toFontChar = newMapping.toFontChar;
						tables['cmap'] = {
							tag: 'cmap',
							data: createCmapTable(newMapping.charCodeToGlyphId, numGlyphs)
						};
						if (!tables['OS/2'] || !validateOS2Table(tables['OS/2'])) {
							tables['OS/2'] = {
								tag: 'OS/2',
								data: createOS2Table(properties, newMapping.charCodeToGlyphId, metricsOverride)
							};
						}
						if (!tables['post']) {
							tables['post'] = {
								tag: 'post',
								data: createPostTable(properties)
							};
						}
						if (!isTrueType) {
							try {
								cffFile = new Stream(tables['CFF '].data);
								var parser = new CFFParser(cffFile, properties, SEAC_ANALYSIS_ENABLED);
								cff = parser.parse();
								var compiler = new CFFCompiler(cff);
								tables['CFF '].data = compiler.compile();
							} catch (e) {
								warn('Failed to compile font ' + properties.loadedName);
							}
						}
						if (!tables['name']) {
							tables['name'] = {
								tag: 'name',
								data: createNameTable(this.name)
							};
						} else {
							var namePrototype = readNameTable(tables['name']);
							tables['name'].data = createNameTable(name, namePrototype);
						}
						var builder = new OpenTypeFileBuilder(header.version);
						for (var tableTag in tables) {
							builder.addTable(tableTag, tables[tableTag].data);
						}
						return builder.toArray();
					},
					convert: function Font_convert(fontName, font, properties) {
						properties.fixedPitch = false;
						if (properties.builtInEncoding) {
							adjustToUnicode(properties, properties.builtInEncoding);
						}
						var mapping = font.getGlyphMapping(properties);
						var newMapping = adjustMapping(mapping, properties);
						this.toFontChar = newMapping.toFontChar;
						var numGlyphs = font.numGlyphs;
						function getCharCodes(charCodeToGlyphId, glyphId) {
							var charCodes = null;
							for (var charCode in charCodeToGlyphId) {
								if (glyphId === charCodeToGlyphId[charCode]) {
									if (!charCodes) {
										charCodes = [];
									}
									charCodes.push(charCode | 0);
								}
							}
							return charCodes;
						}
						function createCharCode(charCodeToGlyphId, glyphId) {
							for (var charCode in charCodeToGlyphId) {
								if (glyphId === charCodeToGlyphId[charCode]) {
									return charCode | 0;
								}
							}
							newMapping.charCodeToGlyphId[newMapping.nextAvailableFontCharCode] = glyphId;
							return newMapping.nextAvailableFontCharCode++;
						}
						var seacs = font.seacs;
						if (SEAC_ANALYSIS_ENABLED && seacs && seacs.length) {
							var matrix = properties.fontMatrix || FONT_IDENTITY_MATRIX;
							var charset = font.getCharset();
							var seacMap = Object.create(null);
							for (var glyphId in seacs) {
								glyphId |= 0;
								var seac = seacs[glyphId];
								var baseGlyphName = StandardEncoding[seac[2]];
								var accentGlyphName = StandardEncoding[seac[3]];
								var baseGlyphId = charset.indexOf(baseGlyphName);
								var accentGlyphId = charset.indexOf(accentGlyphName);
								if (baseGlyphId < 0 || accentGlyphId < 0) {
									continue;
								}
								var accentOffset = {
									x: seac[0] * matrix[0] + seac[1] * matrix[2] + matrix[4],
									y: seac[0] * matrix[1] + seac[1] * matrix[3] + matrix[5]
								};
								var charCodes = getCharCodes(mapping, glyphId);
								if (!charCodes) {
									continue;
								}
								for (var i = 0, ii = charCodes.length; i < ii; i++) {
									var charCode = charCodes[i];
									var charCodeToGlyphId = newMapping.charCodeToGlyphId;
									var baseFontCharCode = createCharCode(charCodeToGlyphId, baseGlyphId);
									var accentFontCharCode = createCharCode(charCodeToGlyphId, accentGlyphId);
									seacMap[charCode] = {
										baseFontCharCode: baseFontCharCode,
										accentFontCharCode: accentFontCharCode,
										accentOffset: accentOffset
									};
								}
							}
							properties.seacMap = seacMap;
						}
						var unitsPerEm = 1 / (properties.fontMatrix || FONT_IDENTITY_MATRIX)[0];
						var builder = new OpenTypeFileBuilder('\x4F\x54\x54\x4F');
						builder.addTable('CFF ', font.data);
						builder.addTable('OS/2', createOS2Table(properties, newMapping.charCodeToGlyphId));
						builder.addTable('cmap', createCmapTable(newMapping.charCodeToGlyphId, numGlyphs));
						builder.addTable('head', '\x00\x01\x00\x00' + '\x00\x00\x10\x00' + '\x00\x00\x00\x00' + '\x5F\x0F\x3C\xF5' + '\x00\x00' + safeString16(unitsPerEm) + '\x00\x00\x00\x00\x9e\x0b\x7e\x27' + '\x00\x00\x00\x00\x9e\x0b\x7e\x27' + '\x00\x00' + safeString16(properties.descent) + '\x0F\xFF' + safeString16(properties.ascent) + string16(properties.italicAngle ? 2 : 0) + '\x00\x11' + '\x00\x00' + '\x00\x00' + '\x00\x00');
						builder.addTable('hhea', '\x00\x01\x00\x00' + safeString16(properties.ascent) + safeString16(properties.descent) + '\x00\x00' + '\xFF\xFF' + '\x00\x00' + '\x00\x00' + '\x00\x00' + safeString16(properties.capHeight) + safeString16(Math.tan(properties.italicAngle) * properties.xHeight) + '\x00\x00' + '\x00\x00' + '\x00\x00' + '\x00\x00' + '\x00\x00' + '\x00\x00' + string16(numGlyphs));
						builder.addTable('hmtx', function fontFieldsHmtx() {
							var charstrings = font.charstrings;
							var cffWidths = font.cff ? font.cff.widths : null;
							var hmtx = '\x00\x00\x00\x00';
							for (var i = 1, ii = numGlyphs; i < ii; i++) {
								var width = 0;
								if (charstrings) {
									var charstring = charstrings[i - 1];
									width = 'width' in charstring ? charstring.width : 0;
								} else if (cffWidths) {
									width = Math.ceil(cffWidths[i] || 0);
								}
								hmtx += string16(width) + string16(0);
							}
							return hmtx;
						}());
						builder.addTable('maxp', '\x00\x00\x50\x00' + string16(numGlyphs));
						builder.addTable('name', createNameTable(fontName));
						builder.addTable('post', createPostTable(properties));
						return builder.toArray();
					},
					get spaceWidth() {
						if ('_shadowWidth' in this) {
							return this._shadowWidth;
						}
						var possibleSpaceReplacements = [
							'space',
							'minus',
							'one',
							'i',
							'I'
						];
						var width;
						for (var i = 0, ii = possibleSpaceReplacements.length; i < ii; i++) {
							var glyphName = possibleSpaceReplacements[i];
							if (glyphName in this.widths) {
								width = this.widths[glyphName];
								break;
							}
							var glyphsUnicodeMap = getGlyphsUnicode();
							var glyphUnicode = glyphsUnicodeMap[glyphName];
							var charcode = 0;
							if (this.composite) {
								if (this.cMap.contains(glyphUnicode)) {
									charcode = this.cMap.lookup(glyphUnicode);
								}
							}
							if (!charcode && this.toUnicode) {
								charcode = this.toUnicode.charCodeOf(glyphUnicode);
							}
							if (charcode <= 0) {
								charcode = glyphUnicode;
							}
							width = this.widths[charcode];
							if (width) {
								break;
							}
						}
						width = width || this.defaultWidth;
						this._shadowWidth = width;
						return width;
					},
					charToGlyph: function Font_charToGlyph(charcode, isSpace) {
						var fontCharCode, width, operatorListId;
						var widthCode = charcode;
						if (this.cMap && this.cMap.contains(charcode)) {
							widthCode = this.cMap.lookup(charcode);
						}
						width = this.widths[widthCode];
						width = isNum(width) ? width : this.defaultWidth;
						var vmetric = this.vmetrics && this.vmetrics[widthCode];
						var unicode = this.toUnicode.get(charcode) || charcode;
						if (typeof unicode === 'number') {
							unicode = String.fromCharCode(unicode);
						}
						var isInFont = charcode in this.toFontChar;
						fontCharCode = this.toFontChar[charcode] || charcode;
						if (this.missingFile) {
							fontCharCode = mapSpecialUnicodeValues(fontCharCode);
						}
						if (this.isType3Font) {
							operatorListId = fontCharCode;
						}
						var accent = null;
						if (this.seacMap && this.seacMap[charcode]) {
							isInFont = true;
							var seac = this.seacMap[charcode];
							fontCharCode = seac.baseFontCharCode;
							accent = {
								fontChar: String.fromCharCode(seac.accentFontCharCode),
								offset: seac.accentOffset
							};
						}
						var fontChar = String.fromCharCode(fontCharCode);
						var glyph = this.glyphCache[charcode];
						if (!glyph || !glyph.matchesForCache(fontChar, unicode, accent, width, vmetric, operatorListId, isSpace, isInFont)) {
							glyph = new Glyph(fontChar, unicode, accent, width, vmetric, operatorListId, isSpace, isInFont);
							this.glyphCache[charcode] = glyph;
						}
						return glyph;
					},
					charsToGlyphs: function Font_charsToGlyphs(chars) {
						var charsCache = this.charsCache;
						var glyphs, glyph, charcode;
						if (charsCache) {
							glyphs = charsCache[chars];
							if (glyphs) {
								return glyphs;
							}
						}
						if (!charsCache) {
							charsCache = this.charsCache = Object.create(null);
						}
						glyphs = [];
						var charsCacheKey = chars;
						var i = 0, ii;
						if (this.cMap) {
							var c = Object.create(null);
							while (i < chars.length) {
								this.cMap.readCharCode(chars, i, c);
								charcode = c.charcode;
								var length = c.length;
								i += length;
								var isSpace = length === 1 && chars.charCodeAt(i - 1) === 0x20;
								glyph = this.charToGlyph(charcode, isSpace);
								glyphs.push(glyph);
							}
						} else {
							for (i = 0, ii = chars.length; i < ii; ++i) {
								charcode = chars.charCodeAt(i);
								glyph = this.charToGlyph(charcode, charcode === 0x20);
								glyphs.push(glyph);
							}
						}
						return charsCache[charsCacheKey] = glyphs;
					}
				};
				return Font;
			}();
			var ErrorFont = function ErrorFontClosure() {
				function ErrorFont(error) {
					this.error = error;
					this.loadedName = 'g_font_error';
					this.loading = false;
				}
				ErrorFont.prototype = {
					charsToGlyphs: function ErrorFont_charsToGlyphs() {
						return [];
					},
					exportData: function ErrorFont_exportData() {
						return { error: this.error };
					}
				};
				return ErrorFont;
			}();
			function type1FontGlyphMapping(properties, builtInEncoding, glyphNames) {
				var charCodeToGlyphId = Object.create(null);
				var glyphId, charCode, baseEncoding;
				var isSymbolicFont = !!(properties.flags & FontFlags.Symbolic);
				if (properties.baseEncodingName) {
					baseEncoding = getEncoding(properties.baseEncodingName);
					for (charCode = 0; charCode < baseEncoding.length; charCode++) {
						glyphId = glyphNames.indexOf(baseEncoding[charCode]);
						if (glyphId >= 0) {
							charCodeToGlyphId[charCode] = glyphId;
						} else {
							charCodeToGlyphId[charCode] = 0;
						}
					}
				} else if (isSymbolicFont) {
					for (charCode in builtInEncoding) {
						charCodeToGlyphId[charCode] = builtInEncoding[charCode];
					}
				} else {
					baseEncoding = StandardEncoding;
					for (charCode = 0; charCode < baseEncoding.length; charCode++) {
						glyphId = glyphNames.indexOf(baseEncoding[charCode]);
						if (glyphId >= 0) {
							charCodeToGlyphId[charCode] = glyphId;
						} else {
							charCodeToGlyphId[charCode] = 0;
						}
					}
				}
				var differences = properties.differences, glyphsUnicodeMap;
				if (differences) {
					for (charCode in differences) {
						var glyphName = differences[charCode];
						glyphId = glyphNames.indexOf(glyphName);
						if (glyphId === -1) {
							if (!glyphsUnicodeMap) {
								glyphsUnicodeMap = getGlyphsUnicode();
							}
							var standardGlyphName = recoverGlyphName(glyphName, glyphsUnicodeMap);
							if (standardGlyphName !== glyphName) {
								glyphId = glyphNames.indexOf(standardGlyphName);
							}
						}
						if (glyphId >= 0) {
							charCodeToGlyphId[charCode] = glyphId;
						} else {
							charCodeToGlyphId[charCode] = 0;
						}
					}
				}
				return charCodeToGlyphId;
			}
			var Type1Font = function Type1FontClosure() {
				function findBlock(streamBytes, signature, startIndex) {
					var streamBytesLength = streamBytes.length;
					var signatureLength = signature.length;
					var scanLength = streamBytesLength - signatureLength;
					var i = startIndex, j, found = false;
					while (i < scanLength) {
						j = 0;
						while (j < signatureLength && streamBytes[i + j] === signature[j]) {
							j++;
						}
						if (j >= signatureLength) {
							i += j;
							while (i < streamBytesLength && isSpace(streamBytes[i])) {
								i++;
							}
							found = true;
							break;
						}
						i++;
					}
					return {
						found: found,
						length: i
					};
				}
				function getHeaderBlock(stream, suggestedLength) {
					var EEXEC_SIGNATURE = [
						0x65,
						0x65,
						0x78,
						0x65,
						0x63
					];
					var streamStartPos = stream.pos;
					var headerBytes, headerBytesLength, block;
					try {
						headerBytes = stream.getBytes(suggestedLength);
						headerBytesLength = headerBytes.length;
					} catch (ex) {
						if (ex instanceof MissingDataException) {
							throw ex;
						}
					}
					if (headerBytesLength === suggestedLength) {
						block = findBlock(headerBytes, EEXEC_SIGNATURE, suggestedLength - 2 * EEXEC_SIGNATURE.length);
						if (block.found && block.length === suggestedLength) {
							return {
								stream: new Stream(headerBytes),
								length: suggestedLength
							};
						}
					}
					warn('Invalid "Length1" property in Type1 font -- trying to recover.');
					stream.pos = streamStartPos;
					var SCAN_BLOCK_LENGTH = 2048;
					var actualLength;
					while (true) {
						var scanBytes = stream.peekBytes(SCAN_BLOCK_LENGTH);
						block = findBlock(scanBytes, EEXEC_SIGNATURE, 0);
						if (block.length === 0) {
							break;
						}
						stream.pos += block.length;
						if (block.found) {
							actualLength = stream.pos - streamStartPos;
							break;
						}
					}
					stream.pos = streamStartPos;
					if (actualLength) {
						return {
							stream: new Stream(stream.getBytes(actualLength)),
							length: actualLength
						};
					}
					warn('Unable to recover "Length1" property in Type1 font -- using as is.');
					return {
						stream: new Stream(stream.getBytes(suggestedLength)),
						length: suggestedLength
					};
				}
				function getEexecBlock(stream, suggestedLength) {
					var eexecBytes = stream.getBytes();
					return {
						stream: new Stream(eexecBytes),
						length: eexecBytes.length
					};
				}
				function Type1Font(name, file, properties) {
					var PFB_HEADER_SIZE = 6;
					var headerBlockLength = properties.length1;
					var eexecBlockLength = properties.length2;
					var pfbHeader = file.peekBytes(PFB_HEADER_SIZE);
					var pfbHeaderPresent = pfbHeader[0] === 0x80 && pfbHeader[1] === 0x01;
					if (pfbHeaderPresent) {
						file.skip(PFB_HEADER_SIZE);
						headerBlockLength = pfbHeader[5] << 24 | pfbHeader[4] << 16 | pfbHeader[3] << 8 | pfbHeader[2];
					}
					var headerBlock = getHeaderBlock(file, headerBlockLength);
					headerBlockLength = headerBlock.length;
					var headerBlockParser = new Type1Parser(headerBlock.stream, false, SEAC_ANALYSIS_ENABLED);
					headerBlockParser.extractFontHeader(properties);
					if (pfbHeaderPresent) {
						pfbHeader = file.getBytes(PFB_HEADER_SIZE);
						eexecBlockLength = pfbHeader[5] << 24 | pfbHeader[4] << 16 | pfbHeader[3] << 8 | pfbHeader[2];
					}
					var eexecBlock = getEexecBlock(file, eexecBlockLength);
					eexecBlockLength = eexecBlock.length;
					var eexecBlockParser = new Type1Parser(eexecBlock.stream, true, SEAC_ANALYSIS_ENABLED);
					var data = eexecBlockParser.extractFontProgram();
					for (var info in data.properties) {
						properties[info] = data.properties[info];
					}
					var charstrings = data.charstrings;
					var type2Charstrings = this.getType2Charstrings(charstrings);
					var subrs = this.getType2Subrs(data.subrs);
					this.charstrings = charstrings;
					this.data = this.wrap(name, type2Charstrings, this.charstrings, subrs, properties);
					this.seacs = this.getSeacs(data.charstrings);
				}
				Type1Font.prototype = {
					get numGlyphs() {
						return this.charstrings.length + 1;
					},
					getCharset: function Type1Font_getCharset() {
						var charset = ['.notdef'];
						var charstrings = this.charstrings;
						for (var glyphId = 0; glyphId < charstrings.length; glyphId++) {
							charset.push(charstrings[glyphId].glyphName);
						}
						return charset;
					},
					getGlyphMapping: function Type1Font_getGlyphMapping(properties) {
						var charstrings = this.charstrings;
						var glyphNames = ['.notdef'], glyphId;
						for (glyphId = 0; glyphId < charstrings.length; glyphId++) {
							glyphNames.push(charstrings[glyphId].glyphName);
						}
						var encoding = properties.builtInEncoding;
						if (encoding) {
							var builtInEncoding = Object.create(null);
							for (var charCode in encoding) {
								glyphId = glyphNames.indexOf(encoding[charCode]);
								if (glyphId >= 0) {
									builtInEncoding[charCode] = glyphId;
								}
							}
						}
						return type1FontGlyphMapping(properties, builtInEncoding, glyphNames);
					},
					getSeacs: function Type1Font_getSeacs(charstrings) {
						var i, ii;
						var seacMap = [];
						for (i = 0, ii = charstrings.length; i < ii; i++) {
							var charstring = charstrings[i];
							if (charstring.seac) {
								seacMap[i + 1] = charstring.seac;
							}
						}
						return seacMap;
					},
					getType2Charstrings: function Type1Font_getType2Charstrings(type1Charstrings) {
						var type2Charstrings = [];
						for (var i = 0, ii = type1Charstrings.length; i < ii; i++) {
							type2Charstrings.push(type1Charstrings[i].charstring);
						}
						return type2Charstrings;
					},
					getType2Subrs: function Type1Font_getType2Subrs(type1Subrs) {
						var bias = 0;
						var count = type1Subrs.length;
						if (count < 1133) {
							bias = 107;
						} else if (count < 33769) {
							bias = 1131;
						} else {
							bias = 32768;
						}
						var type2Subrs = [];
						var i;
						for (i = 0; i < bias; i++) {
							type2Subrs.push([0x0B]);
						}
						for (i = 0; i < count; i++) {
							type2Subrs.push(type1Subrs[i]);
						}
						return type2Subrs;
					},
					wrap: function Type1Font_wrap(name, glyphs, charstrings, subrs, properties) {
						var cff = new CFF();
						cff.header = new CFFHeader(1, 0, 4, 4);
						cff.names = [name];
						var topDict = new CFFTopDict();
						topDict.setByName('version', 391);
						topDict.setByName('Notice', 392);
						topDict.setByName('FullName', 393);
						topDict.setByName('FamilyName', 394);
						topDict.setByName('Weight', 395);
						topDict.setByName('Encoding', null);
						topDict.setByName('FontMatrix', properties.fontMatrix);
						topDict.setByName('FontBBox', properties.bbox);
						topDict.setByName('charset', null);
						topDict.setByName('CharStrings', null);
						topDict.setByName('Private', null);
						cff.topDict = topDict;
						var strings = new CFFStrings();
						strings.add('Version 0.11');
						strings.add('See original notice');
						strings.add(name);
						strings.add(name);
						strings.add('Medium');
						cff.strings = strings;
						cff.globalSubrIndex = new CFFIndex();
						var count = glyphs.length;
						var charsetArray = [0];
						var i, ii;
						for (i = 0; i < count; i++) {
							var index = CFFStandardStrings.indexOf(charstrings[i].glyphName);
							if (index === -1) {
								index = 0;
							}
							charsetArray.push(index >> 8 & 0xff, index & 0xff);
						}
						cff.charset = new CFFCharset(false, 0, [], charsetArray);
						var charStringsIndex = new CFFIndex();
						charStringsIndex.add([
							0x8B,
							0x0E
						]);
						for (i = 0; i < count; i++) {
							var glyph = glyphs[i];
							if (glyph.length === 0) {
								charStringsIndex.add([
									0x8B,
									0x0E
								]);
								continue;
							}
							charStringsIndex.add(glyph);
						}
						cff.charStrings = charStringsIndex;
						var privateDict = new CFFPrivateDict();
						privateDict.setByName('Subrs', null);
						var fields = [
							'BlueValues',
							'OtherBlues',
							'FamilyBlues',
							'FamilyOtherBlues',
							'StemSnapH',
							'StemSnapV',
							'BlueShift',
							'BlueFuzz',
							'BlueScale',
							'LanguageGroup',
							'ExpansionFactor',
							'ForceBold',
							'StdHW',
							'StdVW'
						];
						for (i = 0, ii = fields.length; i < ii; i++) {
							var field = fields[i];
							if (!(field in properties.privateData)) {
								continue;
							}
							var value = properties.privateData[field];
							if (isArray(value)) {
								for (var j = value.length - 1; j > 0; j--) {
									value[j] -= value[j - 1];
								}
							}
							privateDict.setByName(field, value);
						}
						cff.topDict.privateDict = privateDict;
						var subrIndex = new CFFIndex();
						for (i = 0, ii = subrs.length; i < ii; i++) {
							subrIndex.add(subrs[i]);
						}
						privateDict.subrsIndex = subrIndex;
						var compiler = new CFFCompiler(cff);
						return compiler.compile();
					}
				};
				return Type1Font;
			}();
			var CFFFont = function CFFFontClosure() {
				function CFFFont(file, properties) {
					this.properties = properties;
					var parser = new CFFParser(file, properties, SEAC_ANALYSIS_ENABLED);
					this.cff = parser.parse();
					var compiler = new CFFCompiler(this.cff);
					this.seacs = this.cff.seacs;
					try {
						this.data = compiler.compile();
					} catch (e) {
						warn('Failed to compile font ' + properties.loadedName);
						this.data = file;
					}
				}
				CFFFont.prototype = {
					get numGlyphs() {
						return this.cff.charStrings.count;
					},
					getCharset: function CFFFont_getCharset() {
						return this.cff.charset.charset;
					},
					getGlyphMapping: function CFFFont_getGlyphMapping() {
						var cff = this.cff;
						var properties = this.properties;
						var charsets = cff.charset.charset;
						var charCodeToGlyphId;
						var glyphId;
						if (properties.composite) {
							charCodeToGlyphId = Object.create(null);
							if (cff.isCIDFont) {
								for (glyphId = 0; glyphId < charsets.length; glyphId++) {
									var cid = charsets[glyphId];
									var charCode = properties.cMap.charCodeOf(cid);
									charCodeToGlyphId[charCode] = glyphId;
								}
							} else {
								for (glyphId = 0; glyphId < cff.charStrings.count; glyphId++) {
									charCodeToGlyphId[glyphId] = glyphId;
								}
							}
							return charCodeToGlyphId;
						}
						var encoding = cff.encoding ? cff.encoding.encoding : null;
						charCodeToGlyphId = type1FontGlyphMapping(properties, encoding, charsets);
						return charCodeToGlyphId;
					}
				};
				return CFFFont;
			}();
			(function checkSeacSupport() {
				if (typeof navigator !== 'undefined' && /Windows/.test(navigator.userAgent)) {
					SEAC_ANALYSIS_ENABLED = true;
				}
			}());
			(function checkChromeWindows() {
				if (typeof navigator !== 'undefined' && /Windows.*Chrome/.test(navigator.userAgent)) {
					SKIP_PRIVATE_USE_RANGE_F000_TO_F01F = true;
				}
			}());
			exports.SEAC_ANALYSIS_ENABLED = SEAC_ANALYSIS_ENABLED;
			exports.PRIVATE_USE_OFFSET_START = PRIVATE_USE_OFFSET_START;
			exports.PRIVATE_USE_OFFSET_END = PRIVATE_USE_OFFSET_END;
			exports.ErrorFont = ErrorFont;
			exports.Font = Font;
			exports.FontFlags = FontFlags;
			exports.IdentityToUnicodeMap = IdentityToUnicodeMap;
			exports.ProblematicCharRanges = ProblematicCharRanges;
			exports.ToUnicodeMap = ToUnicodeMap;
			exports.getFontType = getFontType;

			/***/
		}),
/* 27 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreColorSpace = __w_pdfjs_require__(3);
			var coreStream = __w_pdfjs_require__(2);
			var coreJpx = __w_pdfjs_require__(15);
			var ImageKind = sharedUtil.ImageKind;
			var assert = sharedUtil.assert;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var isArray = sharedUtil.isArray;
			var warn = sharedUtil.warn;
			var Name = corePrimitives.Name;
			var isStream = corePrimitives.isStream;
			var ColorSpace = coreColorSpace.ColorSpace;
			var DecodeStream = coreStream.DecodeStream;
			var JpegStream = coreStream.JpegStream;
			var JpxImage = coreJpx.JpxImage;
			var PDFImage = function PDFImageClosure() {
				function handleImageData(image, nativeDecoder) {
					if (nativeDecoder && nativeDecoder.canDecode(image)) {
						return nativeDecoder.decode(image);
					}
					return Promise.resolve(image);
				}
				function decodeAndClamp(value, addend, coefficient, max) {
					value = addend + value * coefficient;
					return value < 0 ? 0 : value > max ? max : value;
				}
				function resizeImageMask(src, bpc, w1, h1, w2, h2) {
					var length = w2 * h2;
					var dest = bpc <= 8 ? new Uint8Array(length) : bpc <= 16 ? new Uint16Array(length) : new Uint32Array(length);
					var xRatio = w1 / w2;
					var yRatio = h1 / h2;
					var i, j, py, newIndex = 0, oldIndex;
					var xScaled = new Uint16Array(w2);
					var w1Scanline = w1;
					for (i = 0; i < w2; i++) {
						xScaled[i] = Math.floor(i * xRatio);
					}
					for (i = 0; i < h2; i++) {
						py = Math.floor(i * yRatio) * w1Scanline;
						for (j = 0; j < w2; j++) {
							oldIndex = py + xScaled[j];
							dest[newIndex++] = src[oldIndex];
						}
					}
					return dest;
				}
				function PDFImage(xref, res, image, inline, smask, mask, isMask) {
					this.image = image;
					var dict = image.dict;
					if (dict.has('Filter')) {
						var filter = dict.get('Filter').name;
						if (filter === 'JPXDecode') {
							var jpxImage = new JpxImage();
							jpxImage.parseImageProperties(image.stream);
							image.stream.reset();
							image.bitsPerComponent = jpxImage.bitsPerComponent;
							image.numComps = jpxImage.componentsCount;
						} else if (filter === 'JBIG2Decode') {
							image.bitsPerComponent = 1;
							image.numComps = 1;
						}
					}
					this.width = dict.get('Width', 'W');
					this.height = dict.get('Height', 'H');
					if (this.width < 1 || this.height < 1) {
						error('Invalid image width: ' + this.width + ' or height: ' + this.height);
					}
					this.interpolate = dict.get('Interpolate', 'I') || false;
					this.imageMask = dict.get('ImageMask', 'IM') || false;
					this.matte = dict.get('Matte') || false;
					var bitsPerComponent = image.bitsPerComponent;
					if (!bitsPerComponent) {
						bitsPerComponent = dict.get('BitsPerComponent', 'BPC');
						if (!bitsPerComponent) {
							if (this.imageMask) {
								bitsPerComponent = 1;
							} else {
								error('Bits per component missing in image: ' + this.imageMask);
							}
						}
					}
					this.bpc = bitsPerComponent;
					if (!this.imageMask) {
						var colorSpace = dict.get('ColorSpace', 'CS');
						if (!colorSpace) {
							info('JPX images (which do not require color spaces)');
							switch (image.numComps) {
								case 1:
									colorSpace = Name.get('DeviceGray');
									break;
								case 3:
									colorSpace = Name.get('DeviceRGB');
									break;
								case 4:
									colorSpace = Name.get('DeviceCMYK');
									break;
								default:
									error('JPX images with ' + this.numComps + ' color components not supported.');
							}
						}
						this.colorSpace = ColorSpace.parse(colorSpace, xref, res);
						this.numComps = this.colorSpace.numComps;
					}
					this.decode = dict.getArray('Decode', 'D');
					this.needsDecode = false;
					if (this.decode && (this.colorSpace && !this.colorSpace.isDefaultDecode(this.decode) || isMask && !ColorSpace.isDefaultDecode(this.decode, 1))) {
						this.needsDecode = true;
						var max = (1 << bitsPerComponent) - 1;
						this.decodeCoefficients = [];
						this.decodeAddends = [];
						for (var i = 0, j = 0; i < this.decode.length; i += 2, ++j) {
							var dmin = this.decode[i];
							var dmax = this.decode[i + 1];
							this.decodeCoefficients[j] = dmax - dmin;
							this.decodeAddends[j] = max * dmin;
						}
					}
					if (smask) {
						this.smask = new PDFImage(xref, res, smask, false);
					} else if (mask) {
						if (isStream(mask)) {
							var maskDict = mask.dict, imageMask = maskDict.get('ImageMask', 'IM');
							if (!imageMask) {
								warn('Ignoring /Mask in image without /ImageMask.');
							} else {
								this.mask = new PDFImage(xref, res, mask, false, null, null, true);
							}
						} else {
							this.mask = mask;
						}
					}
				}
				PDFImage.buildImage = function PDFImage_buildImage(handler, xref, res, image, inline, nativeDecoder) {
					var imagePromise = handleImageData(image, nativeDecoder);
					var smaskPromise;
					var maskPromise;
					var smask = image.dict.get('SMask');
					var mask = image.dict.get('Mask');
					if (smask) {
						smaskPromise = handleImageData(smask, nativeDecoder);
						maskPromise = Promise.resolve(null);
					} else {
						smaskPromise = Promise.resolve(null);
						if (mask) {
							if (isStream(mask)) {
								maskPromise = handleImageData(mask, nativeDecoder);
							} else if (isArray(mask)) {
								maskPromise = Promise.resolve(mask);
							} else {
								warn('Unsupported mask format.');
								maskPromise = Promise.resolve(null);
							}
						} else {
							maskPromise = Promise.resolve(null);
						}
					}
					return Promise.all([
						imagePromise,
						smaskPromise,
						maskPromise
					]).then(function (results) {
						var imageData = results[0];
						var smaskData = results[1];
						var maskData = results[2];
						return new PDFImage(xref, res, imageData, inline, smaskData, maskData);
					});
				};
				PDFImage.createMask = function PDFImage_createMask(imgArray, width, height, imageIsFromDecodeStream, inverseDecode) {
					var computedLength = (width + 7 >> 3) * height;
					var actualLength = imgArray.byteLength;
					var haveFullData = computedLength === actualLength;
					var data, i;
					if (imageIsFromDecodeStream && (!inverseDecode || haveFullData)) {
						data = imgArray;
					} else if (!inverseDecode) {
						data = new Uint8Array(actualLength);
						data.set(imgArray);
					} else {
						data = new Uint8Array(computedLength);
						data.set(imgArray);
						for (i = actualLength; i < computedLength; i++) {
							data[i] = 0xff;
						}
					}
					if (inverseDecode) {
						for (i = 0; i < actualLength; i++) {
							data[i] = ~data[i];
						}
					}
					return {
						data: data,
						width: width,
						height: height
					};
				};
				PDFImage.prototype = {
					get drawWidth() {
						return Math.max(this.width, this.smask && this.smask.width || 0, this.mask && this.mask.width || 0);
					},
					get drawHeight() {
						return Math.max(this.height, this.smask && this.smask.height || 0, this.mask && this.mask.height || 0);
					},
					decodeBuffer: function PDFImage_decodeBuffer(buffer) {
						var bpc = this.bpc;
						var numComps = this.numComps;
						var decodeAddends = this.decodeAddends;
						var decodeCoefficients = this.decodeCoefficients;
						var max = (1 << bpc) - 1;
						var i, ii;
						if (bpc === 1) {
							for (i = 0, ii = buffer.length; i < ii; i++) {
								buffer[i] = +!buffer[i];
							}
							return;
						}
						var index = 0;
						for (i = 0, ii = this.width * this.height; i < ii; i++) {
							for (var j = 0; j < numComps; j++) {
								buffer[index] = decodeAndClamp(buffer[index], decodeAddends[j], decodeCoefficients[j], max);
								index++;
							}
						}
					},
					getComponents: function PDFImage_getComponents(buffer) {
						var bpc = this.bpc;
						if (bpc === 8) {
							return buffer;
						}
						var width = this.width;
						var height = this.height;
						var numComps = this.numComps;
						var length = width * height * numComps;
						var bufferPos = 0;
						var output = bpc <= 8 ? new Uint8Array(length) : bpc <= 16 ? new Uint16Array(length) : new Uint32Array(length);
						var rowComps = width * numComps;
						var max = (1 << bpc) - 1;
						var i = 0, ii, buf;
						if (bpc === 1) {
							var mask, loop1End, loop2End;
							for (var j = 0; j < height; j++) {
								loop1End = i + (rowComps & ~7);
								loop2End = i + rowComps;
								while (i < loop1End) {
									buf = buffer[bufferPos++];
									output[i] = buf >> 7 & 1;
									output[i + 1] = buf >> 6 & 1;
									output[i + 2] = buf >> 5 & 1;
									output[i + 3] = buf >> 4 & 1;
									output[i + 4] = buf >> 3 & 1;
									output[i + 5] = buf >> 2 & 1;
									output[i + 6] = buf >> 1 & 1;
									output[i + 7] = buf & 1;
									i += 8;
								}
								if (i < loop2End) {
									buf = buffer[bufferPos++];
									mask = 128;
									while (i < loop2End) {
										output[i++] = +!!(buf & mask);
										mask >>= 1;
									}
								}
							}
						} else {
							var bits = 0;
							buf = 0;
							for (i = 0, ii = length; i < ii; ++i) {
								if (i % rowComps === 0) {
									buf = 0;
									bits = 0;
								}
								while (bits < bpc) {
									buf = buf << 8 | buffer[bufferPos++];
									bits += 8;
								}
								var remainingBits = bits - bpc;
								var value = buf >> remainingBits;
								output[i] = value < 0 ? 0 : value > max ? max : value;
								buf = buf & (1 << remainingBits) - 1;
								bits = remainingBits;
							}
						}
						return output;
					},
					fillOpacity: function PDFImage_fillOpacity(rgbaBuf, width, height, actualHeight, image) {
						var smask = this.smask;
						var mask = this.mask;
						var alphaBuf, sw, sh, i, ii, j;
						if (smask) {
							sw = smask.width;
							sh = smask.height;
							alphaBuf = new Uint8Array(sw * sh);
							smask.fillGrayBuffer(alphaBuf);
							if (sw !== width || sh !== height) {
								alphaBuf = resizeImageMask(alphaBuf, smask.bpc, sw, sh, width, height);
							}
						} else if (mask) {
							if (mask instanceof PDFImage) {
								sw = mask.width;
								sh = mask.height;
								alphaBuf = new Uint8Array(sw * sh);
								mask.numComps = 1;
								mask.fillGrayBuffer(alphaBuf);
								for (i = 0, ii = sw * sh; i < ii; ++i) {
									alphaBuf[i] = 255 - alphaBuf[i];
								}
								if (sw !== width || sh !== height) {
									alphaBuf = resizeImageMask(alphaBuf, mask.bpc, sw, sh, width, height);
								}
							} else if (isArray(mask)) {
								alphaBuf = new Uint8Array(width * height);
								var numComps = this.numComps;
								for (i = 0, ii = width * height; i < ii; ++i) {
									var opacity = 0;
									var imageOffset = i * numComps;
									for (j = 0; j < numComps; ++j) {
										var color = image[imageOffset + j];
										var maskOffset = j * 2;
										if (color < mask[maskOffset] || color > mask[maskOffset + 1]) {
											opacity = 255;
											break;
										}
									}
									alphaBuf[i] = opacity;
								}
							} else {
								error('Unknown mask format.');
							}
						}
						if (alphaBuf) {
							for (i = 0, j = 3, ii = width * actualHeight; i < ii; ++i, j += 4) {
								rgbaBuf[j] = alphaBuf[i];
							}
						} else {
							for (i = 0, j = 3, ii = width * actualHeight; i < ii; ++i, j += 4) {
								rgbaBuf[j] = 255;
							}
						}
					},
					undoPreblend: function PDFImage_undoPreblend(buffer, width, height) {
						var matte = this.smask && this.smask.matte;
						if (!matte) {
							return;
						}
						var matteRgb = this.colorSpace.getRgb(matte, 0);
						var matteR = matteRgb[0];
						var matteG = matteRgb[1];
						var matteB = matteRgb[2];
						var length = width * height * 4;
						var r, g, b;
						for (var i = 0; i < length; i += 4) {
							var alpha = buffer[i + 3];
							if (alpha === 0) {
								buffer[i] = 255;
								buffer[i + 1] = 255;
								buffer[i + 2] = 255;
								continue;
							}
							var k = 255 / alpha;
							r = (buffer[i] - matteR) * k + matteR;
							g = (buffer[i + 1] - matteG) * k + matteG;
							b = (buffer[i + 2] - matteB) * k + matteB;
							buffer[i] = r <= 0 ? 0 : r >= 255 ? 255 : r | 0;
							buffer[i + 1] = g <= 0 ? 0 : g >= 255 ? 255 : g | 0;
							buffer[i + 2] = b <= 0 ? 0 : b >= 255 ? 255 : b | 0;
						}
					},
					createImageData: function PDFImage_createImageData(forceRGBA) {
						var drawWidth = this.drawWidth;
						var drawHeight = this.drawHeight;
						var imgData = {
							width: drawWidth,
							height: drawHeight
						};
						var numComps = this.numComps;
						var originalWidth = this.width;
						var originalHeight = this.height;
						var bpc = this.bpc;
						var rowBytes = originalWidth * numComps * bpc + 7 >> 3;
						var imgArray;
						if (!forceRGBA) {
							var kind;
							if (this.colorSpace.name === 'DeviceGray' && bpc === 1) {
								kind = ImageKind.GRAYSCALE_1BPP;
							} else if (this.colorSpace.name === 'DeviceRGB' && bpc === 8 && !this.needsDecode) {
								kind = ImageKind.RGB_24BPP;
							}
							if (kind && !this.smask && !this.mask && drawWidth === originalWidth && drawHeight === originalHeight) {
								imgData.kind = kind;
								imgArray = this.getImageBytes(originalHeight * rowBytes);
								if (this.image instanceof DecodeStream) {
									imgData.data = imgArray;
								} else {
									var newArray = new Uint8Array(imgArray.length);
									newArray.set(imgArray);
									imgData.data = newArray;
								}
								if (this.needsDecode) {
									assert(kind === ImageKind.GRAYSCALE_1BPP);
									var buffer = imgData.data;
									for (var i = 0, ii = buffer.length; i < ii; i++) {
										buffer[i] ^= 0xff;
									}
								}
								return imgData;
							}
							if (this.image instanceof JpegStream && !this.smask && !this.mask && (this.colorSpace.name === 'DeviceGray' || this.colorSpace.name === 'DeviceRGB' || this.colorSpace.name === 'DeviceCMYK')) {
								imgData.kind = ImageKind.RGB_24BPP;
								imgData.data = this.getImageBytes(originalHeight * rowBytes, drawWidth, drawHeight, true);
								return imgData;
							}
						}
						imgArray = this.getImageBytes(originalHeight * rowBytes);
						var actualHeight = 0 | imgArray.length / rowBytes * drawHeight / originalHeight;
						var comps = this.getComponents(imgArray);
						var alpha01, maybeUndoPreblend;
						if (!forceRGBA && !this.smask && !this.mask) {
							imgData.kind = ImageKind.RGB_24BPP;
							imgData.data = new Uint8Array(drawWidth * drawHeight * 3);
							alpha01 = 0;
							maybeUndoPreblend = false;
						} else {
							imgData.kind = ImageKind.RGBA_32BPP;
							imgData.data = new Uint8Array(drawWidth * drawHeight * 4);
							alpha01 = 1;
							maybeUndoPreblend = true;
							this.fillOpacity(imgData.data, drawWidth, drawHeight, actualHeight, comps);
						}
						if (this.needsDecode) {
							this.decodeBuffer(comps);
						}
						this.colorSpace.fillRgb(imgData.data, originalWidth, originalHeight, drawWidth, drawHeight, actualHeight, bpc, comps, alpha01);
						if (maybeUndoPreblend) {
							this.undoPreblend(imgData.data, drawWidth, actualHeight);
						}
						return imgData;
					},
					fillGrayBuffer: function PDFImage_fillGrayBuffer(buffer) {
						var numComps = this.numComps;
						if (numComps !== 1) {
							error('Reading gray scale from a color image: ' + numComps);
						}
						var width = this.width;
						var height = this.height;
						var bpc = this.bpc;
						var rowBytes = width * numComps * bpc + 7 >> 3;
						var imgArray = this.getImageBytes(height * rowBytes);
						var comps = this.getComponents(imgArray);
						var i, length;
						if (bpc === 1) {
							length = width * height;
							if (this.needsDecode) {
								for (i = 0; i < length; ++i) {
									buffer[i] = comps[i] - 1 & 255;
								}
							} else {
								for (i = 0; i < length; ++i) {
									buffer[i] = -comps[i] & 255;
								}
							}
							return;
						}
						if (this.needsDecode) {
							this.decodeBuffer(comps);
						}
						length = width * height;
						var scale = 255 / ((1 << bpc) - 1);
						for (i = 0; i < length; ++i) {
							buffer[i] = scale * comps[i] | 0;
						}
					},
					getImageBytes: function PDFImage_getImageBytes(length, drawWidth, drawHeight, forceRGB) {
						this.image.reset();
						this.image.drawWidth = drawWidth || this.width;
						this.image.drawHeight = drawHeight || this.height;
						this.image.forceRGB = !!forceRGB;
						return this.image.getBytes(length);
					}
				};
				return PDFImage;
			}();
			exports.PDFImage = PDFImage;

			/***/
		}),
/* 28 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreArithmeticDecoder = __w_pdfjs_require__(10);
			var error = sharedUtil.error;
			var log2 = sharedUtil.log2;
			var readInt8 = sharedUtil.readInt8;
			var readUint16 = sharedUtil.readUint16;
			var readUint32 = sharedUtil.readUint32;
			var shadow = sharedUtil.shadow;
			var ArithmeticDecoder = coreArithmeticDecoder.ArithmeticDecoder;
			var Jbig2Image = function Jbig2ImageClosure() {
				function ContextCache() {
				}
				ContextCache.prototype = {
					getContexts: function (id) {
						if (id in this) {
							return this[id];
						}
						return this[id] = new Int8Array(1 << 16);
					}
				};
				function DecodingContext(data, start, end) {
					this.data = data;
					this.start = start;
					this.end = end;
				}
				DecodingContext.prototype = {
					get decoder() {
						var decoder = new ArithmeticDecoder(this.data, this.start, this.end);
						return shadow(this, 'decoder', decoder);
					},
					get contextCache() {
						var cache = new ContextCache();
						return shadow(this, 'contextCache', cache);
					}
				};
				function decodeInteger(contextCache, procedure, decoder) {
					var contexts = contextCache.getContexts(procedure);
					var prev = 1;
					function readBits(length) {
						var v = 0;
						for (var i = 0; i < length; i++) {
							var bit = decoder.readBit(contexts, prev);
							prev = prev < 256 ? prev << 1 | bit : (prev << 1 | bit) & 511 | 256;
							v = v << 1 | bit;
						}
						return v >>> 0;
					}
					var sign = readBits(1);
					var value = readBits(1) ? readBits(1) ? readBits(1) ? readBits(1) ? readBits(1) ? readBits(32) + 4436 : readBits(12) + 340 : readBits(8) + 84 : readBits(6) + 20 : readBits(4) + 4 : readBits(2);
					return sign === 0 ? value : value > 0 ? -value : null;
				}
				function decodeIAID(contextCache, decoder, codeLength) {
					var contexts = contextCache.getContexts('IAID');
					var prev = 1;
					for (var i = 0; i < codeLength; i++) {
						var bit = decoder.readBit(contexts, prev);
						prev = prev << 1 | bit;
					}
					if (codeLength < 31) {
						return prev & (1 << codeLength) - 1;
					}
					return prev & 0x7FFFFFFF;
				}
				var SegmentTypes = [
					'SymbolDictionary',
					null,
					null,
					null,
					'IntermediateTextRegion',
					null,
					'ImmediateTextRegion',
					'ImmediateLosslessTextRegion',
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					'patternDictionary',
					null,
					null,
					null,
					'IntermediateHalftoneRegion',
					null,
					'ImmediateHalftoneRegion',
					'ImmediateLosslessHalftoneRegion',
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					'IntermediateGenericRegion',
					null,
					'ImmediateGenericRegion',
					'ImmediateLosslessGenericRegion',
					'IntermediateGenericRefinementRegion',
					null,
					'ImmediateGenericRefinementRegion',
					'ImmediateLosslessGenericRefinementRegion',
					null,
					null,
					null,
					null,
					'PageInformation',
					'EndOfPage',
					'EndOfStripe',
					'EndOfFile',
					'Profiles',
					'Tables',
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					null,
					'Extension'
				];
				var CodingTemplates = [
					[
						{
							x: -1,
							y: -2
						},
						{
							x: 0,
							y: -2
						},
						{
							x: 1,
							y: -2
						},
						{
							x: -2,
							y: -1
						},
						{
							x: -1,
							y: -1
						},
						{
							x: 0,
							y: -1
						},
						{
							x: 1,
							y: -1
						},
						{
							x: 2,
							y: -1
						},
						{
							x: -4,
							y: 0
						},
						{
							x: -3,
							y: 0
						},
						{
							x: -2,
							y: 0
						},
						{
							x: -1,
							y: 0
						}
					],
					[
						{
							x: -1,
							y: -2
						},
						{
							x: 0,
							y: -2
						},
						{
							x: 1,
							y: -2
						},
						{
							x: 2,
							y: -2
						},
						{
							x: -2,
							y: -1
						},
						{
							x: -1,
							y: -1
						},
						{
							x: 0,
							y: -1
						},
						{
							x: 1,
							y: -1
						},
						{
							x: 2,
							y: -1
						},
						{
							x: -3,
							y: 0
						},
						{
							x: -2,
							y: 0
						},
						{
							x: -1,
							y: 0
						}
					],
					[
						{
							x: -1,
							y: -2
						},
						{
							x: 0,
							y: -2
						},
						{
							x: 1,
							y: -2
						},
						{
							x: -2,
							y: -1
						},
						{
							x: -1,
							y: -1
						},
						{
							x: 0,
							y: -1
						},
						{
							x: 1,
							y: -1
						},
						{
							x: -2,
							y: 0
						},
						{
							x: -1,
							y: 0
						}
					],
					[
						{
							x: -3,
							y: -1
						},
						{
							x: -2,
							y: -1
						},
						{
							x: -1,
							y: -1
						},
						{
							x: 0,
							y: -1
						},
						{
							x: 1,
							y: -1
						},
						{
							x: -4,
							y: 0
						},
						{
							x: -3,
							y: 0
						},
						{
							x: -2,
							y: 0
						},
						{
							x: -1,
							y: 0
						}
					]
				];
				var RefinementTemplates = [
					{
						coding: [
							{
								x: 0,
								y: -1
							},
							{
								x: 1,
								y: -1
							},
							{
								x: -1,
								y: 0
							}
						],
						reference: [
							{
								x: 0,
								y: -1
							},
							{
								x: 1,
								y: -1
							},
							{
								x: -1,
								y: 0
							},
							{
								x: 0,
								y: 0
							},
							{
								x: 1,
								y: 0
							},
							{
								x: -1,
								y: 1
							},
							{
								x: 0,
								y: 1
							},
							{
								x: 1,
								y: 1
							}
						]
					},
					{
						coding: [
							{
								x: -1,
								y: -1
							},
							{
								x: 0,
								y: -1
							},
							{
								x: 1,
								y: -1
							},
							{
								x: -1,
								y: 0
							}
						],
						reference: [
							{
								x: 0,
								y: -1
							},
							{
								x: -1,
								y: 0
							},
							{
								x: 0,
								y: 0
							},
							{
								x: 1,
								y: 0
							},
							{
								x: 0,
								y: 1
							},
							{
								x: 1,
								y: 1
							}
						]
					}
				];
				var ReusedContexts = [
					0x9B25,
					0x0795,
					0x00E5,
					0x0195
				];
				var RefinementReusedContexts = [
					0x0020,
					0x0008
				];
				function decodeBitmapTemplate0(width, height, decodingContext) {
					var decoder = decodingContext.decoder;
					var contexts = decodingContext.contextCache.getContexts('GB');
					var contextLabel, i, j, pixel, row, row1, row2, bitmap = [];
					var OLD_PIXEL_MASK = 0x7BF7;
					for (i = 0; i < height; i++) {
						row = bitmap[i] = new Uint8Array(width);
						row1 = i < 1 ? row : bitmap[i - 1];
						row2 = i < 2 ? row : bitmap[i - 2];
						contextLabel = row2[0] << 13 | row2[1] << 12 | row2[2] << 11 | row1[0] << 7 | row1[1] << 6 | row1[2] << 5 | row1[3] << 4;
						for (j = 0; j < width; j++) {
							row[j] = pixel = decoder.readBit(contexts, contextLabel);
							contextLabel = (contextLabel & OLD_PIXEL_MASK) << 1 | (j + 3 < width ? row2[j + 3] << 11 : 0) | (j + 4 < width ? row1[j + 4] << 4 : 0) | pixel;
						}
					}
					return bitmap;
				}
				function decodeBitmap(mmr, width, height, templateIndex, prediction, skip, at, decodingContext) {
					if (mmr) {
						error('JBIG2 error: MMR encoding is not supported');
					}
					if (templateIndex === 0 && !skip && !prediction && at.length === 4 && at[0].x === 3 && at[0].y === -1 && at[1].x === -3 && at[1].y === -1 && at[2].x === 2 && at[2].y === -2 && at[3].x === -2 && at[3].y === -2) {
						return decodeBitmapTemplate0(width, height, decodingContext);
					}
					var useskip = !!skip;
					var template = CodingTemplates[templateIndex].concat(at);
					template.sort(function (a, b) {
						return a.y - b.y || a.x - b.x;
					});
					var templateLength = template.length;
					var templateX = new Int8Array(templateLength);
					var templateY = new Int8Array(templateLength);
					var changingTemplateEntries = [];
					var reuseMask = 0, minX = 0, maxX = 0, minY = 0;
					var c, k;
					for (k = 0; k < templateLength; k++) {
						templateX[k] = template[k].x;
						templateY[k] = template[k].y;
						minX = Math.min(minX, template[k].x);
						maxX = Math.max(maxX, template[k].x);
						minY = Math.min(minY, template[k].y);
						if (k < templateLength - 1 && template[k].y === template[k + 1].y && template[k].x === template[k + 1].x - 1) {
							reuseMask |= 1 << templateLength - 1 - k;
						} else {
							changingTemplateEntries.push(k);
						}
					}
					var changingEntriesLength = changingTemplateEntries.length;
					var changingTemplateX = new Int8Array(changingEntriesLength);
					var changingTemplateY = new Int8Array(changingEntriesLength);
					var changingTemplateBit = new Uint16Array(changingEntriesLength);
					for (c = 0; c < changingEntriesLength; c++) {
						k = changingTemplateEntries[c];
						changingTemplateX[c] = template[k].x;
						changingTemplateY[c] = template[k].y;
						changingTemplateBit[c] = 1 << templateLength - 1 - k;
					}
					var sbb_left = -minX;
					var sbb_top = -minY;
					var sbb_right = width - maxX;
					var pseudoPixelContext = ReusedContexts[templateIndex];
					var row = new Uint8Array(width);
					var bitmap = [];
					var decoder = decodingContext.decoder;
					var contexts = decodingContext.contextCache.getContexts('GB');
					var ltp = 0, j, i0, j0, contextLabel = 0, bit, shift;
					for (var i = 0; i < height; i++) {
						if (prediction) {
							var sltp = decoder.readBit(contexts, pseudoPixelContext);
							ltp ^= sltp;
							if (ltp) {
								bitmap.push(row);
								continue;
							}
						}
						row = new Uint8Array(row);
						bitmap.push(row);
						for (j = 0; j < width; j++) {
							if (useskip && skip[i][j]) {
								row[j] = 0;
								continue;
							}
							if (j >= sbb_left && j < sbb_right && i >= sbb_top) {
								contextLabel = contextLabel << 1 & reuseMask;
								for (k = 0; k < changingEntriesLength; k++) {
									i0 = i + changingTemplateY[k];
									j0 = j + changingTemplateX[k];
									bit = bitmap[i0][j0];
									if (bit) {
										bit = changingTemplateBit[k];
										contextLabel |= bit;
									}
								}
							} else {
								contextLabel = 0;
								shift = templateLength - 1;
								for (k = 0; k < templateLength; k++, shift--) {
									j0 = j + templateX[k];
									if (j0 >= 0 && j0 < width) {
										i0 = i + templateY[k];
										if (i0 >= 0) {
											bit = bitmap[i0][j0];
											if (bit) {
												contextLabel |= bit << shift;
											}
										}
									}
								}
							}
							var pixel = decoder.readBit(contexts, contextLabel);
							row[j] = pixel;
						}
					}
					return bitmap;
				}
				function decodeRefinement(width, height, templateIndex, referenceBitmap, offsetX, offsetY, prediction, at, decodingContext) {
					var codingTemplate = RefinementTemplates[templateIndex].coding;
					if (templateIndex === 0) {
						codingTemplate = codingTemplate.concat([at[0]]);
					}
					var codingTemplateLength = codingTemplate.length;
					var codingTemplateX = new Int32Array(codingTemplateLength);
					var codingTemplateY = new Int32Array(codingTemplateLength);
					var k;
					for (k = 0; k < codingTemplateLength; k++) {
						codingTemplateX[k] = codingTemplate[k].x;
						codingTemplateY[k] = codingTemplate[k].y;
					}
					var referenceTemplate = RefinementTemplates[templateIndex].reference;
					if (templateIndex === 0) {
						referenceTemplate = referenceTemplate.concat([at[1]]);
					}
					var referenceTemplateLength = referenceTemplate.length;
					var referenceTemplateX = new Int32Array(referenceTemplateLength);
					var referenceTemplateY = new Int32Array(referenceTemplateLength);
					for (k = 0; k < referenceTemplateLength; k++) {
						referenceTemplateX[k] = referenceTemplate[k].x;
						referenceTemplateY[k] = referenceTemplate[k].y;
					}
					var referenceWidth = referenceBitmap[0].length;
					var referenceHeight = referenceBitmap.length;
					var pseudoPixelContext = RefinementReusedContexts[templateIndex];
					var bitmap = [];
					var decoder = decodingContext.decoder;
					var contexts = decodingContext.contextCache.getContexts('GR');
					var ltp = 0;
					for (var i = 0; i < height; i++) {
						if (prediction) {
							var sltp = decoder.readBit(contexts, pseudoPixelContext);
							ltp ^= sltp;
							if (ltp) {
								error('JBIG2 error: prediction is not supported');
							}
						}
						var row = new Uint8Array(width);
						bitmap.push(row);
						for (var j = 0; j < width; j++) {
							var i0, j0;
							var contextLabel = 0;
							for (k = 0; k < codingTemplateLength; k++) {
								i0 = i + codingTemplateY[k];
								j0 = j + codingTemplateX[k];
								if (i0 < 0 || j0 < 0 || j0 >= width) {
									contextLabel <<= 1;
								} else {
									contextLabel = contextLabel << 1 | bitmap[i0][j0];
								}
							}
							for (k = 0; k < referenceTemplateLength; k++) {
								i0 = i + referenceTemplateY[k] + offsetY;
								j0 = j + referenceTemplateX[k] + offsetX;
								if (i0 < 0 || i0 >= referenceHeight || j0 < 0 || j0 >= referenceWidth) {
									contextLabel <<= 1;
								} else {
									contextLabel = contextLabel << 1 | referenceBitmap[i0][j0];
								}
							}
							var pixel = decoder.readBit(contexts, contextLabel);
							row[j] = pixel;
						}
					}
					return bitmap;
				}
				function decodeSymbolDictionary(huffman, refinement, symbols, numberOfNewSymbols, numberOfExportedSymbols, huffmanTables, templateIndex, at, refinementTemplateIndex, refinementAt, decodingContext) {
					if (huffman) {
						error('JBIG2 error: huffman is not supported');
					}
					var newSymbols = [];
					var currentHeight = 0;
					var symbolCodeLength = log2(symbols.length + numberOfNewSymbols);
					var decoder = decodingContext.decoder;
					var contextCache = decodingContext.contextCache;
					while (newSymbols.length < numberOfNewSymbols) {
						var deltaHeight = decodeInteger(contextCache, 'IADH', decoder);
						currentHeight += deltaHeight;
						var currentWidth = 0;
						while (true) {
							var deltaWidth = decodeInteger(contextCache, 'IADW', decoder);
							if (deltaWidth === null) {
								break;
							}
							currentWidth += deltaWidth;
							var bitmap;
							if (refinement) {
								var numberOfInstances = decodeInteger(contextCache, 'IAAI', decoder);
								if (numberOfInstances > 1) {
									bitmap = decodeTextRegion(huffman, refinement, currentWidth, currentHeight, 0, numberOfInstances, 1, symbols.concat(newSymbols), symbolCodeLength, 0, 0, 1, 0, huffmanTables, refinementTemplateIndex, refinementAt, decodingContext);
								} else {
									var symbolId = decodeIAID(contextCache, decoder, symbolCodeLength);
									var rdx = decodeInteger(contextCache, 'IARDX', decoder);
									var rdy = decodeInteger(contextCache, 'IARDY', decoder);
									var symbol = symbolId < symbols.length ? symbols[symbolId] : newSymbols[symbolId - symbols.length];
									bitmap = decodeRefinement(currentWidth, currentHeight, refinementTemplateIndex, symbol, rdx, rdy, false, refinementAt, decodingContext);
								}
							} else {
								bitmap = decodeBitmap(false, currentWidth, currentHeight, templateIndex, false, null, at, decodingContext);
							}
							newSymbols.push(bitmap);
						}
					}
					var exportedSymbols = [];
					var flags = [], currentFlag = false;
					var totalSymbolsLength = symbols.length + numberOfNewSymbols;
					while (flags.length < totalSymbolsLength) {
						var runLength = decodeInteger(contextCache, 'IAEX', decoder);
						while (runLength--) {
							flags.push(currentFlag);
						}
						currentFlag = !currentFlag;
					}
					for (var i = 0, ii = symbols.length; i < ii; i++) {
						if (flags[i]) {
							exportedSymbols.push(symbols[i]);
						}
					}
					for (var j = 0; j < numberOfNewSymbols; i++, j++) {
						if (flags[i]) {
							exportedSymbols.push(newSymbols[j]);
						}
					}
					return exportedSymbols;
				}
				function decodeTextRegion(huffman, refinement, width, height, defaultPixelValue, numberOfSymbolInstances, stripSize, inputSymbols, symbolCodeLength, transposed, dsOffset, referenceCorner, combinationOperator, huffmanTables, refinementTemplateIndex, refinementAt, decodingContext) {
					if (huffman) {
						error('JBIG2 error: huffman is not supported');
					}
					var bitmap = [];
					var i, row;
					for (i = 0; i < height; i++) {
						row = new Uint8Array(width);
						if (defaultPixelValue) {
							for (var j = 0; j < width; j++) {
								row[j] = defaultPixelValue;
							}
						}
						bitmap.push(row);
					}
					var decoder = decodingContext.decoder;
					var contextCache = decodingContext.contextCache;
					var stripT = -decodeInteger(contextCache, 'IADT', decoder);
					var firstS = 0;
					i = 0;
					while (i < numberOfSymbolInstances) {
						var deltaT = decodeInteger(contextCache, 'IADT', decoder);
						stripT += deltaT;
						var deltaFirstS = decodeInteger(contextCache, 'IAFS', decoder);
						firstS += deltaFirstS;
						var currentS = firstS;
						do {
							var currentT = stripSize === 1 ? 0 : decodeInteger(contextCache, 'IAIT', decoder);
							var t = stripSize * stripT + currentT;
							var symbolId = decodeIAID(contextCache, decoder, symbolCodeLength);
							var applyRefinement = refinement && decodeInteger(contextCache, 'IARI', decoder);
							var symbolBitmap = inputSymbols[symbolId];
							var symbolWidth = symbolBitmap[0].length;
							var symbolHeight = symbolBitmap.length;
							if (applyRefinement) {
								var rdw = decodeInteger(contextCache, 'IARDW', decoder);
								var rdh = decodeInteger(contextCache, 'IARDH', decoder);
								var rdx = decodeInteger(contextCache, 'IARDX', decoder);
								var rdy = decodeInteger(contextCache, 'IARDY', decoder);
								symbolWidth += rdw;
								symbolHeight += rdh;
								symbolBitmap = decodeRefinement(symbolWidth, symbolHeight, refinementTemplateIndex, symbolBitmap, (rdw >> 1) + rdx, (rdh >> 1) + rdy, false, refinementAt, decodingContext);
							}
							var offsetT = t - (referenceCorner & 1 ? 0 : symbolHeight);
							var offsetS = currentS - (referenceCorner & 2 ? symbolWidth : 0);
							var s2, t2, symbolRow;
							if (transposed) {
								for (s2 = 0; s2 < symbolHeight; s2++) {
									row = bitmap[offsetS + s2];
									if (!row) {
										continue;
									}
									symbolRow = symbolBitmap[s2];
									var maxWidth = Math.min(width - offsetT, symbolWidth);
									switch (combinationOperator) {
										case 0:
											for (t2 = 0; t2 < maxWidth; t2++) {
												row[offsetT + t2] |= symbolRow[t2];
											}
											break;
										case 2:
											for (t2 = 0; t2 < maxWidth; t2++) {
												row[offsetT + t2] ^= symbolRow[t2];
											}
											break;
										default:
											error('JBIG2 error: operator ' + combinationOperator + ' is not supported');
									}
								}
								currentS += symbolHeight - 1;
							} else {
								for (t2 = 0; t2 < symbolHeight; t2++) {
									row = bitmap[offsetT + t2];
									if (!row) {
										continue;
									}
									symbolRow = symbolBitmap[t2];
									switch (combinationOperator) {
										case 0:
											for (s2 = 0; s2 < symbolWidth; s2++) {
												row[offsetS + s2] |= symbolRow[s2];
											}
											break;
										case 2:
											for (s2 = 0; s2 < symbolWidth; s2++) {
												row[offsetS + s2] ^= symbolRow[s2];
											}
											break;
										default:
											error('JBIG2 error: operator ' + combinationOperator + ' is not supported');
									}
								}
								currentS += symbolWidth - 1;
							}
							i++;
							var deltaS = decodeInteger(contextCache, 'IADS', decoder);
							if (deltaS === null) {
								break;
							}
							currentS += deltaS + dsOffset;
						} while (true);
					}
					return bitmap;
				}
				function readSegmentHeader(data, start) {
					var segmentHeader = {};
					segmentHeader.number = readUint32(data, start);
					var flags = data[start + 4];
					var segmentType = flags & 0x3F;
					if (!SegmentTypes[segmentType]) {
						error('JBIG2 error: invalid segment type: ' + segmentType);
					}
					segmentHeader.type = segmentType;
					segmentHeader.typeName = SegmentTypes[segmentType];
					segmentHeader.deferredNonRetain = !!(flags & 0x80);
					var pageAssociationFieldSize = !!(flags & 0x40);
					var referredFlags = data[start + 5];
					var referredToCount = referredFlags >> 5 & 7;
					var retainBits = [referredFlags & 31];
					var position = start + 6;
					if (referredFlags === 7) {
						referredToCount = readUint32(data, position - 1) & 0x1FFFFFFF;
						position += 3;
						var bytes = referredToCount + 7 >> 3;
						retainBits[0] = data[position++];
						while (--bytes > 0) {
							retainBits.push(data[position++]);
						}
					} else if (referredFlags === 5 || referredFlags === 6) {
						error('JBIG2 error: invalid referred-to flags');
					}
					segmentHeader.retainBits = retainBits;
					var referredToSegmentNumberSize = segmentHeader.number <= 256 ? 1 : segmentHeader.number <= 65536 ? 2 : 4;
					var referredTo = [];
					var i, ii;
					for (i = 0; i < referredToCount; i++) {
						var number = referredToSegmentNumberSize === 1 ? data[position] : referredToSegmentNumberSize === 2 ? readUint16(data, position) : readUint32(data, position);
						referredTo.push(number);
						position += referredToSegmentNumberSize;
					}
					segmentHeader.referredTo = referredTo;
					if (!pageAssociationFieldSize) {
						segmentHeader.pageAssociation = data[position++];
					} else {
						segmentHeader.pageAssociation = readUint32(data, position);
						position += 4;
					}
					segmentHeader.length = readUint32(data, position);
					position += 4;
					if (segmentHeader.length === 0xFFFFFFFF) {
						if (segmentType === 38) {
							var genericRegionInfo = readRegionSegmentInformation(data, position);
							var genericRegionSegmentFlags = data[position + RegionSegmentInformationFieldLength];
							var genericRegionMmr = !!(genericRegionSegmentFlags & 1);
							var searchPatternLength = 6;
							var searchPattern = new Uint8Array(searchPatternLength);
							if (!genericRegionMmr) {
								searchPattern[0] = 0xFF;
								searchPattern[1] = 0xAC;
							}
							searchPattern[2] = genericRegionInfo.height >>> 24 & 0xFF;
							searchPattern[3] = genericRegionInfo.height >> 16 & 0xFF;
							searchPattern[4] = genericRegionInfo.height >> 8 & 0xFF;
							searchPattern[5] = genericRegionInfo.height & 0xFF;
							for (i = position, ii = data.length; i < ii; i++) {
								var j = 0;
								while (j < searchPatternLength && searchPattern[j] === data[i + j]) {
									j++;
								}
								if (j === searchPatternLength) {
									segmentHeader.length = i + searchPatternLength;
									break;
								}
							}
							if (segmentHeader.length === 0xFFFFFFFF) {
								error('JBIG2 error: segment end was not found');
							}
						} else {
							error('JBIG2 error: invalid unknown segment length');
						}
					}
					segmentHeader.headerEnd = position;
					return segmentHeader;
				}
				function readSegments(header, data, start, end) {
					var segments = [];
					var position = start;
					while (position < end) {
						var segmentHeader = readSegmentHeader(data, position);
						position = segmentHeader.headerEnd;
						var segment = {
							header: segmentHeader,
							data: data
						};
						if (!header.randomAccess) {
							segment.start = position;
							position += segmentHeader.length;
							segment.end = position;
						}
						segments.push(segment);
						if (segmentHeader.type === 51) {
							break;
						}
					}
					if (header.randomAccess) {
						for (var i = 0, ii = segments.length; i < ii; i++) {
							segments[i].start = position;
							position += segments[i].header.length;
							segments[i].end = position;
						}
					}
					return segments;
				}
				function readRegionSegmentInformation(data, start) {
					return {
						width: readUint32(data, start),
						height: readUint32(data, start + 4),
						x: readUint32(data, start + 8),
						y: readUint32(data, start + 12),
						combinationOperator: data[start + 16] & 7
					};
				}
				var RegionSegmentInformationFieldLength = 17;
				function processSegment(segment, visitor) {
					var header = segment.header;
					var data = segment.data, position = segment.start, end = segment.end;
					var args, at, i, atLength;
					switch (header.type) {
						case 0:
							var dictionary = {};
							var dictionaryFlags = readUint16(data, position);
							dictionary.huffman = !!(dictionaryFlags & 1);
							dictionary.refinement = !!(dictionaryFlags & 2);
							dictionary.huffmanDHSelector = dictionaryFlags >> 2 & 3;
							dictionary.huffmanDWSelector = dictionaryFlags >> 4 & 3;
							dictionary.bitmapSizeSelector = dictionaryFlags >> 6 & 1;
							dictionary.aggregationInstancesSelector = dictionaryFlags >> 7 & 1;
							dictionary.bitmapCodingContextUsed = !!(dictionaryFlags & 256);
							dictionary.bitmapCodingContextRetained = !!(dictionaryFlags & 512);
							dictionary.template = dictionaryFlags >> 10 & 3;
							dictionary.refinementTemplate = dictionaryFlags >> 12 & 1;
							position += 2;
							if (!dictionary.huffman) {
								atLength = dictionary.template === 0 ? 4 : 1;
								at = [];
								for (i = 0; i < atLength; i++) {
									at.push({
										x: readInt8(data, position),
										y: readInt8(data, position + 1)
									});
									position += 2;
								}
								dictionary.at = at;
							}
							if (dictionary.refinement && !dictionary.refinementTemplate) {
								at = [];
								for (i = 0; i < 2; i++) {
									at.push({
										x: readInt8(data, position),
										y: readInt8(data, position + 1)
									});
									position += 2;
								}
								dictionary.refinementAt = at;
							}
							dictionary.numberOfExportedSymbols = readUint32(data, position);
							position += 4;
							dictionary.numberOfNewSymbols = readUint32(data, position);
							position += 4;
							args = [
								dictionary,
								header.number,
								header.referredTo,
								data,
								position,
								end
							];
							break;
						case 6:
						case 7:
							var textRegion = {};
							textRegion.info = readRegionSegmentInformation(data, position);
							position += RegionSegmentInformationFieldLength;
							var textRegionSegmentFlags = readUint16(data, position);
							position += 2;
							textRegion.huffman = !!(textRegionSegmentFlags & 1);
							textRegion.refinement = !!(textRegionSegmentFlags & 2);
							textRegion.stripSize = 1 << (textRegionSegmentFlags >> 2 & 3);
							textRegion.referenceCorner = textRegionSegmentFlags >> 4 & 3;
							textRegion.transposed = !!(textRegionSegmentFlags & 64);
							textRegion.combinationOperator = textRegionSegmentFlags >> 7 & 3;
							textRegion.defaultPixelValue = textRegionSegmentFlags >> 9 & 1;
							textRegion.dsOffset = textRegionSegmentFlags << 17 >> 27;
							textRegion.refinementTemplate = textRegionSegmentFlags >> 15 & 1;
							if (textRegion.huffman) {
								var textRegionHuffmanFlags = readUint16(data, position);
								position += 2;
								textRegion.huffmanFS = textRegionHuffmanFlags & 3;
								textRegion.huffmanDS = textRegionHuffmanFlags >> 2 & 3;
								textRegion.huffmanDT = textRegionHuffmanFlags >> 4 & 3;
								textRegion.huffmanRefinementDW = textRegionHuffmanFlags >> 6 & 3;
								textRegion.huffmanRefinementDH = textRegionHuffmanFlags >> 8 & 3;
								textRegion.huffmanRefinementDX = textRegionHuffmanFlags >> 10 & 3;
								textRegion.huffmanRefinementDY = textRegionHuffmanFlags >> 12 & 3;
								textRegion.huffmanRefinementSizeSelector = !!(textRegionHuffmanFlags & 14);
							}
							if (textRegion.refinement && !textRegion.refinementTemplate) {
								at = [];
								for (i = 0; i < 2; i++) {
									at.push({
										x: readInt8(data, position),
										y: readInt8(data, position + 1)
									});
									position += 2;
								}
								textRegion.refinementAt = at;
							}
							textRegion.numberOfSymbolInstances = readUint32(data, position);
							position += 4;
							if (textRegion.huffman) {
								error('JBIG2 error: huffman is not supported');
							}
							args = [
								textRegion,
								header.referredTo,
								data,
								position,
								end
							];
							break;
						case 38:
						case 39:
							var genericRegion = {};
							genericRegion.info = readRegionSegmentInformation(data, position);
							position += RegionSegmentInformationFieldLength;
							var genericRegionSegmentFlags = data[position++];
							genericRegion.mmr = !!(genericRegionSegmentFlags & 1);
							genericRegion.template = genericRegionSegmentFlags >> 1 & 3;
							genericRegion.prediction = !!(genericRegionSegmentFlags & 8);
							if (!genericRegion.mmr) {
								atLength = genericRegion.template === 0 ? 4 : 1;
								at = [];
								for (i = 0; i < atLength; i++) {
									at.push({
										x: readInt8(data, position),
										y: readInt8(data, position + 1)
									});
									position += 2;
								}
								genericRegion.at = at;
							}
							args = [
								genericRegion,
								data,
								position,
								end
							];
							break;
						case 48:
							var pageInfo = {
								width: readUint32(data, position),
								height: readUint32(data, position + 4),
								resolutionX: readUint32(data, position + 8),
								resolutionY: readUint32(data, position + 12)
							};
							if (pageInfo.height === 0xFFFFFFFF) {
								delete pageInfo.height;
							}
							var pageSegmentFlags = data[position + 16];
							readUint16(data, position + 17);
							pageInfo.lossless = !!(pageSegmentFlags & 1);
							pageInfo.refinement = !!(pageSegmentFlags & 2);
							pageInfo.defaultPixelValue = pageSegmentFlags >> 2 & 1;
							pageInfo.combinationOperator = pageSegmentFlags >> 3 & 3;
							pageInfo.requiresBuffer = !!(pageSegmentFlags & 32);
							pageInfo.combinationOperatorOverride = !!(pageSegmentFlags & 64);
							args = [pageInfo];
							break;
						case 49:
							break;
						case 50:
							break;
						case 51:
							break;
						case 62:
							break;
						default:
							error('JBIG2 error: segment type ' + header.typeName + '(' + header.type + ') is not implemented');
					}
					var callbackName = 'on' + header.typeName;
					if (callbackName in visitor) {
						visitor[callbackName].apply(visitor, args);
					}
				}
				function processSegments(segments, visitor) {
					for (var i = 0, ii = segments.length; i < ii; i++) {
						processSegment(segments[i], visitor);
					}
				}
				function parseJbig2(data, start, end) {
					var position = start;
					if (data[position] !== 0x97 || data[position + 1] !== 0x4A || data[position + 2] !== 0x42 || data[position + 3] !== 0x32 || data[position + 4] !== 0x0D || data[position + 5] !== 0x0A || data[position + 6] !== 0x1A || data[position + 7] !== 0x0A) {
						error('JBIG2 error: invalid header');
					}
					var header = {};
					position += 8;
					var flags = data[position++];
					header.randomAccess = !(flags & 1);
					if (!(flags & 2)) {
						header.numberOfPages = readUint32(data, position);
						position += 4;
					}
					readSegments(header, data, position, end);
					error('Not implemented');
				}
				function parseJbig2Chunks(chunks) {
					var visitor = new SimpleSegmentVisitor();
					for (var i = 0, ii = chunks.length; i < ii; i++) {
						var chunk = chunks[i];
						var segments = readSegments({}, chunk.data, chunk.start, chunk.end);
						processSegments(segments, visitor);
					}
					return visitor.buffer;
				}
				function SimpleSegmentVisitor() {
				}
				SimpleSegmentVisitor.prototype = {
					onPageInformation: function SimpleSegmentVisitor_onPageInformation(info) {
						this.currentPageInfo = info;
						var rowSize = info.width + 7 >> 3;
						var buffer = new Uint8Array(rowSize * info.height);
						if (info.defaultPixelValue) {
							for (var i = 0, ii = buffer.length; i < ii; i++) {
								buffer[i] = 0xFF;
							}
						}
						this.buffer = buffer;
					},
					drawBitmap: function SimpleSegmentVisitor_drawBitmap(regionInfo, bitmap) {
						var pageInfo = this.currentPageInfo;
						var width = regionInfo.width, height = regionInfo.height;
						var rowSize = pageInfo.width + 7 >> 3;
						var combinationOperator = pageInfo.combinationOperatorOverride ? regionInfo.combinationOperator : pageInfo.combinationOperator;
						var buffer = this.buffer;
						var mask0 = 128 >> (regionInfo.x & 7);
						var offset0 = regionInfo.y * rowSize + (regionInfo.x >> 3);
						var i, j, mask, offset;
						switch (combinationOperator) {
							case 0:
								for (i = 0; i < height; i++) {
									mask = mask0;
									offset = offset0;
									for (j = 0; j < width; j++) {
										if (bitmap[i][j]) {
											buffer[offset] |= mask;
										}
										mask >>= 1;
										if (!mask) {
											mask = 128;
											offset++;
										}
									}
									offset0 += rowSize;
								}
								break;
							case 2:
								for (i = 0; i < height; i++) {
									mask = mask0;
									offset = offset0;
									for (j = 0; j < width; j++) {
										if (bitmap[i][j]) {
											buffer[offset] ^= mask;
										}
										mask >>= 1;
										if (!mask) {
											mask = 128;
											offset++;
										}
									}
									offset0 += rowSize;
								}
								break;
							default:
								error('JBIG2 error: operator ' + combinationOperator + ' is not supported');
						}
					},
					onImmediateGenericRegion: function SimpleSegmentVisitor_onImmediateGenericRegion(region, data, start, end) {
						var regionInfo = region.info;
						var decodingContext = new DecodingContext(data, start, end);
						var bitmap = decodeBitmap(region.mmr, regionInfo.width, regionInfo.height, region.template, region.prediction, null, region.at, decodingContext);
						this.drawBitmap(regionInfo, bitmap);
					},
					onImmediateLosslessGenericRegion: function SimpleSegmentVisitor_onImmediateLosslessGenericRegion() {
						this.onImmediateGenericRegion.apply(this, arguments);
					},
					onSymbolDictionary: function SimpleSegmentVisitor_onSymbolDictionary(dictionary, currentSegment, referredSegments, data, start, end) {
						var huffmanTables;
						if (dictionary.huffman) {
							error('JBIG2 error: huffman is not supported');
						}
						var symbols = this.symbols;
						if (!symbols) {
							this.symbols = symbols = {};
						}
						var inputSymbols = [];
						for (var i = 0, ii = referredSegments.length; i < ii; i++) {
							inputSymbols = inputSymbols.concat(symbols[referredSegments[i]]);
						}
						var decodingContext = new DecodingContext(data, start, end);
						symbols[currentSegment] = decodeSymbolDictionary(dictionary.huffman, dictionary.refinement, inputSymbols, dictionary.numberOfNewSymbols, dictionary.numberOfExportedSymbols, huffmanTables, dictionary.template, dictionary.at, dictionary.refinementTemplate, dictionary.refinementAt, decodingContext);
					},
					onImmediateTextRegion: function SimpleSegmentVisitor_onImmediateTextRegion(region, referredSegments, data, start, end) {
						var regionInfo = region.info;
						var huffmanTables;
						var symbols = this.symbols;
						var inputSymbols = [];
						for (var i = 0, ii = referredSegments.length; i < ii; i++) {
							inputSymbols = inputSymbols.concat(symbols[referredSegments[i]]);
						}
						var symbolCodeLength = log2(inputSymbols.length);
						var decodingContext = new DecodingContext(data, start, end);
						var bitmap = decodeTextRegion(region.huffman, region.refinement, regionInfo.width, regionInfo.height, region.defaultPixelValue, region.numberOfSymbolInstances, region.stripSize, inputSymbols, symbolCodeLength, region.transposed, region.dsOffset, region.referenceCorner, region.combinationOperator, huffmanTables, region.refinementTemplate, region.refinementAt, decodingContext);
						this.drawBitmap(regionInfo, bitmap);
					},
					onImmediateLosslessTextRegion: function SimpleSegmentVisitor_onImmediateLosslessTextRegion() {
						this.onImmediateTextRegion.apply(this, arguments);
					}
				};
				function Jbig2Image() {
				}
				Jbig2Image.prototype = {
					parseChunks: function Jbig2Image_parseChunks(chunks) {
						return parseJbig2Chunks(chunks);
					}
				};
				return Jbig2Image;
			}();
			exports.Jbig2Image = Jbig2Image;

			/***/
		}),
/* 29 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var error = sharedUtil.error;
			var JpegImage = function JpegImageClosure() {
				var dctZigZag = new Uint8Array([
					0,
					1,
					8,
					16,
					9,
					2,
					3,
					10,
					17,
					24,
					32,
					25,
					18,
					11,
					4,
					5,
					12,
					19,
					26,
					33,
					40,
					48,
					41,
					34,
					27,
					20,
					13,
					6,
					7,
					14,
					21,
					28,
					35,
					42,
					49,
					56,
					57,
					50,
					43,
					36,
					29,
					22,
					15,
					23,
					30,
					37,
					44,
					51,
					58,
					59,
					52,
					45,
					38,
					31,
					39,
					46,
					53,
					60,
					61,
					54,
					47,
					55,
					62,
					63
				]);
				var dctCos1 = 4017;
				var dctSin1 = 799;
				var dctCos3 = 3406;
				var dctSin3 = 2276;
				var dctCos6 = 1567;
				var dctSin6 = 3784;
				var dctSqrt2 = 5793;
				var dctSqrt1d2 = 2896;
				function JpegImage() {
					this.decodeTransform = null;
					this.colorTransform = -1;
				}
				function buildHuffmanTable(codeLengths, values) {
					var k = 0, code = [], i, j, length = 16;
					while (length > 0 && !codeLengths[length - 1]) {
						length--;
					}
					code.push({
						children: [],
						index: 0
					});
					var p = code[0], q;
					for (i = 0; i < length; i++) {
						for (j = 0; j < codeLengths[i]; j++) {
							p = code.pop();
							p.children[p.index] = values[k];
							while (p.index > 0) {
								p = code.pop();
							}
							p.index++;
							code.push(p);
							while (code.length <= i) {
								code.push(q = {
									children: [],
									index: 0
								});
								p.children[p.index] = q.children;
								p = q;
							}
							k++;
						}
						if (i + 1 < length) {
							code.push(q = {
								children: [],
								index: 0
							});
							p.children[p.index] = q.children;
							p = q;
						}
					}
					return code[0].children;
				}
				function getBlockBufferOffset(component, row, col) {
					return 64 * ((component.blocksPerLine + 1) * row + col);
				}
				function decodeScan(data, offset, frame, components, resetInterval, spectralStart, spectralEnd, successivePrev, successive) {
					var mcusPerLine = frame.mcusPerLine;
					var progressive = frame.progressive;
					var startOffset = offset, bitsData = 0, bitsCount = 0;
					function readBit() {
						if (bitsCount > 0) {
							bitsCount--;
							return bitsData >> bitsCount & 1;
						}
						bitsData = data[offset++];
						if (bitsData === 0xFF) {
							var nextByte = data[offset++];
							if (nextByte) {
								error('JPEG error: unexpected marker ' + (bitsData << 8 | nextByte).toString(16));
							}
						}
						bitsCount = 7;
						return bitsData >>> 7;
					}
					function decodeHuffman(tree) {
						var node = tree;
						while (true) {
							node = node[readBit()];
							if (typeof node === 'number') {
								return node;
							}
							if (typeof node !== 'object') {
								error('JPEG error: invalid huffman sequence');
							}
						}
					}
					function receive(length) {
						var n = 0;
						while (length > 0) {
							n = n << 1 | readBit();
							length--;
						}
						return n;
					}
					function receiveAndExtend(length) {
						if (length === 1) {
							return readBit() === 1 ? 1 : -1;
						}
						var n = receive(length);
						if (n >= 1 << length - 1) {
							return n;
						}
						return n + (-1 << length) + 1;
					}
					function decodeBaseline(component, offset) {
						var t = decodeHuffman(component.huffmanTableDC);
						var diff = t === 0 ? 0 : receiveAndExtend(t);
						component.blockData[offset] = component.pred += diff;
						var k = 1;
						while (k < 64) {
							var rs = decodeHuffman(component.huffmanTableAC);
							var s = rs & 15, r = rs >> 4;
							if (s === 0) {
								if (r < 15) {
									break;
								}
								k += 16;
								continue;
							}
							k += r;
							var z = dctZigZag[k];
							component.blockData[offset + z] = receiveAndExtend(s);
							k++;
						}
					}
					function decodeDCFirst(component, offset) {
						var t = decodeHuffman(component.huffmanTableDC);
						var diff = t === 0 ? 0 : receiveAndExtend(t) << successive;
						component.blockData[offset] = component.pred += diff;
					}
					function decodeDCSuccessive(component, offset) {
						component.blockData[offset] |= readBit() << successive;
					}
					var eobrun = 0;
					function decodeACFirst(component, offset) {
						if (eobrun > 0) {
							eobrun--;
							return;
						}
						var k = spectralStart, e = spectralEnd;
						while (k <= e) {
							var rs = decodeHuffman(component.huffmanTableAC);
							var s = rs & 15, r = rs >> 4;
							if (s === 0) {
								if (r < 15) {
									eobrun = receive(r) + (1 << r) - 1;
									break;
								}
								k += 16;
								continue;
							}
							k += r;
							var z = dctZigZag[k];
							component.blockData[offset + z] = receiveAndExtend(s) * (1 << successive);
							k++;
						}
					}
					var successiveACState = 0, successiveACNextValue;
					function decodeACSuccessive(component, offset) {
						var k = spectralStart;
						var e = spectralEnd;
						var r = 0;
						var s;
						var rs;
						while (k <= e) {
							var z = dctZigZag[k];
							switch (successiveACState) {
								case 0:
									rs = decodeHuffman(component.huffmanTableAC);
									s = rs & 15;
									r = rs >> 4;
									if (s === 0) {
										if (r < 15) {
											eobrun = receive(r) + (1 << r);
											successiveACState = 4;
										} else {
											r = 16;
											successiveACState = 1;
										}
									} else {
										if (s !== 1) {
											error('JPEG error: invalid ACn encoding');
										}
										successiveACNextValue = receiveAndExtend(s);
										successiveACState = r ? 2 : 3;
									}
									continue;
								case 1:
								case 2:
									if (component.blockData[offset + z]) {
										component.blockData[offset + z] += readBit() << successive;
									} else {
										r--;
										if (r === 0) {
											successiveACState = successiveACState === 2 ? 3 : 0;
										}
									}
									break;
								case 3:
									if (component.blockData[offset + z]) {
										component.blockData[offset + z] += readBit() << successive;
									} else {
										component.blockData[offset + z] = successiveACNextValue << successive;
										successiveACState = 0;
									}
									break;
								case 4:
									if (component.blockData[offset + z]) {
										component.blockData[offset + z] += readBit() << successive;
									}
									break;
							}
							k++;
						}
						if (successiveACState === 4) {
							eobrun--;
							if (eobrun === 0) {
								successiveACState = 0;
							}
						}
					}
					function decodeMcu(component, decode, mcu, row, col) {
						var mcuRow = mcu / mcusPerLine | 0;
						var mcuCol = mcu % mcusPerLine;
						var blockRow = mcuRow * component.v + row;
						var blockCol = mcuCol * component.h + col;
						var offset = getBlockBufferOffset(component, blockRow, blockCol);
						decode(component, offset);
					}
					function decodeBlock(component, decode, mcu) {
						var blockRow = mcu / component.blocksPerLine | 0;
						var blockCol = mcu % component.blocksPerLine;
						var offset = getBlockBufferOffset(component, blockRow, blockCol);
						decode(component, offset);
					}
					var componentsLength = components.length;
					var component, i, j, k, n;
					var decodeFn;
					if (progressive) {
						if (spectralStart === 0) {
							decodeFn = successivePrev === 0 ? decodeDCFirst : decodeDCSuccessive;
						} else {
							decodeFn = successivePrev === 0 ? decodeACFirst : decodeACSuccessive;
						}
					} else {
						decodeFn = decodeBaseline;
					}
					var mcu = 0, marker;
					var mcuExpected;
					if (componentsLength === 1) {
						mcuExpected = components[0].blocksPerLine * components[0].blocksPerColumn;
					} else {
						mcuExpected = mcusPerLine * frame.mcusPerColumn;
					}
					if (!resetInterval) {
						resetInterval = mcuExpected;
					}
					var h, v;
					while (mcu < mcuExpected) {
						for (i = 0; i < componentsLength; i++) {
							components[i].pred = 0;
						}
						eobrun = 0;
						if (componentsLength === 1) {
							component = components[0];
							for (n = 0; n < resetInterval; n++) {
								decodeBlock(component, decodeFn, mcu);
								mcu++;
							}
						} else {
							for (n = 0; n < resetInterval; n++) {
								for (i = 0; i < componentsLength; i++) {
									component = components[i];
									h = component.h;
									v = component.v;
									for (j = 0; j < v; j++) {
										for (k = 0; k < h; k++) {
											decodeMcu(component, decodeFn, mcu, j, k);
										}
									}
								}
								mcu++;
							}
						}
						bitsCount = 0;
						marker = data[offset] << 8 | data[offset + 1];
						while (data[offset] === 0x00 && offset < data.length - 1) {
							offset++;
							marker = data[offset] << 8 | data[offset + 1];
						}
						if (marker <= 0xFF00) {
							error('JPEG error: marker was not found');
						}
						if (marker >= 0xFFD0 && marker <= 0xFFD7) {
							offset += 2;
						} else {
							break;
						}
					}
					return offset - startOffset;
				}
				function quantizeAndInverse(component, blockBufferOffset, p) {
					var qt = component.quantizationTable, blockData = component.blockData;
					var v0, v1, v2, v3, v4, v5, v6, v7;
					var p0, p1, p2, p3, p4, p5, p6, p7;
					var t;
					if (!qt) {
						error('JPEG error: missing required Quantization Table.');
					}
					for (var row = 0; row < 64; row += 8) {
						p0 = blockData[blockBufferOffset + row];
						p1 = blockData[blockBufferOffset + row + 1];
						p2 = blockData[blockBufferOffset + row + 2];
						p3 = blockData[blockBufferOffset + row + 3];
						p4 = blockData[blockBufferOffset + row + 4];
						p5 = blockData[blockBufferOffset + row + 5];
						p6 = blockData[blockBufferOffset + row + 6];
						p7 = blockData[blockBufferOffset + row + 7];
						p0 *= qt[row];
						if ((p1 | p2 | p3 | p4 | p5 | p6 | p7) === 0) {
							t = dctSqrt2 * p0 + 512 >> 10;
							p[row] = t;
							p[row + 1] = t;
							p[row + 2] = t;
							p[row + 3] = t;
							p[row + 4] = t;
							p[row + 5] = t;
							p[row + 6] = t;
							p[row + 7] = t;
							continue;
						}
						p1 *= qt[row + 1];
						p2 *= qt[row + 2];
						p3 *= qt[row + 3];
						p4 *= qt[row + 4];
						p5 *= qt[row + 5];
						p6 *= qt[row + 6];
						p7 *= qt[row + 7];
						v0 = dctSqrt2 * p0 + 128 >> 8;
						v1 = dctSqrt2 * p4 + 128 >> 8;
						v2 = p2;
						v3 = p6;
						v4 = dctSqrt1d2 * (p1 - p7) + 128 >> 8;
						v7 = dctSqrt1d2 * (p1 + p7) + 128 >> 8;
						v5 = p3 << 4;
						v6 = p5 << 4;
						v0 = v0 + v1 + 1 >> 1;
						v1 = v0 - v1;
						t = v2 * dctSin6 + v3 * dctCos6 + 128 >> 8;
						v2 = v2 * dctCos6 - v3 * dctSin6 + 128 >> 8;
						v3 = t;
						v4 = v4 + v6 + 1 >> 1;
						v6 = v4 - v6;
						v7 = v7 + v5 + 1 >> 1;
						v5 = v7 - v5;
						v0 = v0 + v3 + 1 >> 1;
						v3 = v0 - v3;
						v1 = v1 + v2 + 1 >> 1;
						v2 = v1 - v2;
						t = v4 * dctSin3 + v7 * dctCos3 + 2048 >> 12;
						v4 = v4 * dctCos3 - v7 * dctSin3 + 2048 >> 12;
						v7 = t;
						t = v5 * dctSin1 + v6 * dctCos1 + 2048 >> 12;
						v5 = v5 * dctCos1 - v6 * dctSin1 + 2048 >> 12;
						v6 = t;
						p[row] = v0 + v7;
						p[row + 7] = v0 - v7;
						p[row + 1] = v1 + v6;
						p[row + 6] = v1 - v6;
						p[row + 2] = v2 + v5;
						p[row + 5] = v2 - v5;
						p[row + 3] = v3 + v4;
						p[row + 4] = v3 - v4;
					}
					for (var col = 0; col < 8; ++col) {
						p0 = p[col];
						p1 = p[col + 8];
						p2 = p[col + 16];
						p3 = p[col + 24];
						p4 = p[col + 32];
						p5 = p[col + 40];
						p6 = p[col + 48];
						p7 = p[col + 56];
						if ((p1 | p2 | p3 | p4 | p5 | p6 | p7) === 0) {
							t = dctSqrt2 * p0 + 8192 >> 14;
							t = t < -2040 ? 0 : t >= 2024 ? 255 : t + 2056 >> 4;
							blockData[blockBufferOffset + col] = t;
							blockData[blockBufferOffset + col + 8] = t;
							blockData[blockBufferOffset + col + 16] = t;
							blockData[blockBufferOffset + col + 24] = t;
							blockData[blockBufferOffset + col + 32] = t;
							blockData[blockBufferOffset + col + 40] = t;
							blockData[blockBufferOffset + col + 48] = t;
							blockData[blockBufferOffset + col + 56] = t;
							continue;
						}
						v0 = dctSqrt2 * p0 + 2048 >> 12;
						v1 = dctSqrt2 * p4 + 2048 >> 12;
						v2 = p2;
						v3 = p6;
						v4 = dctSqrt1d2 * (p1 - p7) + 2048 >> 12;
						v7 = dctSqrt1d2 * (p1 + p7) + 2048 >> 12;
						v5 = p3;
						v6 = p5;
						v0 = (v0 + v1 + 1 >> 1) + 4112;
						v1 = v0 - v1;
						t = v2 * dctSin6 + v3 * dctCos6 + 2048 >> 12;
						v2 = v2 * dctCos6 - v3 * dctSin6 + 2048 >> 12;
						v3 = t;
						v4 = v4 + v6 + 1 >> 1;
						v6 = v4 - v6;
						v7 = v7 + v5 + 1 >> 1;
						v5 = v7 - v5;
						v0 = v0 + v3 + 1 >> 1;
						v3 = v0 - v3;
						v1 = v1 + v2 + 1 >> 1;
						v2 = v1 - v2;
						t = v4 * dctSin3 + v7 * dctCos3 + 2048 >> 12;
						v4 = v4 * dctCos3 - v7 * dctSin3 + 2048 >> 12;
						v7 = t;
						t = v5 * dctSin1 + v6 * dctCos1 + 2048 >> 12;
						v5 = v5 * dctCos1 - v6 * dctSin1 + 2048 >> 12;
						v6 = t;
						p0 = v0 + v7;
						p7 = v0 - v7;
						p1 = v1 + v6;
						p6 = v1 - v6;
						p2 = v2 + v5;
						p5 = v2 - v5;
						p3 = v3 + v4;
						p4 = v3 - v4;
						p0 = p0 < 16 ? 0 : p0 >= 4080 ? 255 : p0 >> 4;
						p1 = p1 < 16 ? 0 : p1 >= 4080 ? 255 : p1 >> 4;
						p2 = p2 < 16 ? 0 : p2 >= 4080 ? 255 : p2 >> 4;
						p3 = p3 < 16 ? 0 : p3 >= 4080 ? 255 : p3 >> 4;
						p4 = p4 < 16 ? 0 : p4 >= 4080 ? 255 : p4 >> 4;
						p5 = p5 < 16 ? 0 : p5 >= 4080 ? 255 : p5 >> 4;
						p6 = p6 < 16 ? 0 : p6 >= 4080 ? 255 : p6 >> 4;
						p7 = p7 < 16 ? 0 : p7 >= 4080 ? 255 : p7 >> 4;
						blockData[blockBufferOffset + col] = p0;
						blockData[blockBufferOffset + col + 8] = p1;
						blockData[blockBufferOffset + col + 16] = p2;
						blockData[blockBufferOffset + col + 24] = p3;
						blockData[blockBufferOffset + col + 32] = p4;
						blockData[blockBufferOffset + col + 40] = p5;
						blockData[blockBufferOffset + col + 48] = p6;
						blockData[blockBufferOffset + col + 56] = p7;
					}
				}
				function buildComponentData(frame, component) {
					var blocksPerLine = component.blocksPerLine;
					var blocksPerColumn = component.blocksPerColumn;
					var computationBuffer = new Int16Array(64);
					for (var blockRow = 0; blockRow < blocksPerColumn; blockRow++) {
						for (var blockCol = 0; blockCol < blocksPerLine; blockCol++) {
							var offset = getBlockBufferOffset(component, blockRow, blockCol);
							quantizeAndInverse(component, offset, computationBuffer);
						}
					}
					return component.blockData;
				}
				function clamp0to255(a) {
					return a <= 0 ? 0 : a >= 255 ? 255 : a;
				}
				JpegImage.prototype = {
					parse: function parse(data) {
						function readUint16() {
							var value = data[offset] << 8 | data[offset + 1];
							offset += 2;
							return value;
						}
						function readDataBlock() {
							var length = readUint16();
							var array = data.subarray(offset, offset + length - 2);
							offset += array.length;
							return array;
						}
						function prepareComponents(frame) {
							var mcusPerLine = Math.ceil(frame.samplesPerLine / 8 / frame.maxH);
							var mcusPerColumn = Math.ceil(frame.scanLines / 8 / frame.maxV);
							for (var i = 0; i < frame.components.length; i++) {
								component = frame.components[i];
								var blocksPerLine = Math.ceil(Math.ceil(frame.samplesPerLine / 8) * component.h / frame.maxH);
								var blocksPerColumn = Math.ceil(Math.ceil(frame.scanLines / 8) * component.v / frame.maxV);
								var blocksPerLineForMcu = mcusPerLine * component.h;
								var blocksPerColumnForMcu = mcusPerColumn * component.v;
								var blocksBufferSize = 64 * blocksPerColumnForMcu * (blocksPerLineForMcu + 1);
								component.blockData = new Int16Array(blocksBufferSize);
								component.blocksPerLine = blocksPerLine;
								component.blocksPerColumn = blocksPerColumn;
							}
							frame.mcusPerLine = mcusPerLine;
							frame.mcusPerColumn = mcusPerColumn;
						}
						var offset = 0;
						var jfif = null;
						var adobe = null;
						var frame, resetInterval;
						var quantizationTables = [];
						var huffmanTablesAC = [], huffmanTablesDC = [];
						var fileMarker = readUint16();
						if (fileMarker !== 0xFFD8) {
							error('JPEG error: SOI not found');
						}
						fileMarker = readUint16();
						while (fileMarker !== 0xFFD9) {
							var i, j, l;
							switch (fileMarker) {
								case 0xFFE0:
								case 0xFFE1:
								case 0xFFE2:
								case 0xFFE3:
								case 0xFFE4:
								case 0xFFE5:
								case 0xFFE6:
								case 0xFFE7:
								case 0xFFE8:
								case 0xFFE9:
								case 0xFFEA:
								case 0xFFEB:
								case 0xFFEC:
								case 0xFFED:
								case 0xFFEE:
								case 0xFFEF:
								case 0xFFFE:
									var appData = readDataBlock();
									if (fileMarker === 0xFFE0) {
										if (appData[0] === 0x4A && appData[1] === 0x46 && appData[2] === 0x49 && appData[3] === 0x46 && appData[4] === 0) {
											jfif = {
												version: {
													major: appData[5],
													minor: appData[6]
												},
												densityUnits: appData[7],
												xDensity: appData[8] << 8 | appData[9],
												yDensity: appData[10] << 8 | appData[11],
												thumbWidth: appData[12],
												thumbHeight: appData[13],
												thumbData: appData.subarray(14, 14 + 3 * appData[12] * appData[13])
											};
										}
									}
									if (fileMarker === 0xFFEE) {
										if (appData[0] === 0x41 && appData[1] === 0x64 && appData[2] === 0x6F && appData[3] === 0x62 && appData[4] === 0x65) {
											adobe = {
												version: appData[5] << 8 | appData[6],
												flags0: appData[7] << 8 | appData[8],
												flags1: appData[9] << 8 | appData[10],
												transformCode: appData[11]
											};
										}
									}
									break;
								case 0xFFDB:
									var quantizationTablesLength = readUint16();
									var quantizationTablesEnd = quantizationTablesLength + offset - 2;
									var z;
									while (offset < quantizationTablesEnd) {
										var quantizationTableSpec = data[offset++];
										var tableData = new Uint16Array(64);
										if (quantizationTableSpec >> 4 === 0) {
											for (j = 0; j < 64; j++) {
												z = dctZigZag[j];
												tableData[z] = data[offset++];
											}
										} else if (quantizationTableSpec >> 4 === 1) {
											for (j = 0; j < 64; j++) {
												z = dctZigZag[j];
												tableData[z] = readUint16();
											}
										} else {
											error('JPEG error: DQT - invalid table spec');
										}
										quantizationTables[quantizationTableSpec & 15] = tableData;
									}
									break;
								case 0xFFC0:
								case 0xFFC1:
								case 0xFFC2:
									if (frame) {
										error('JPEG error: Only single frame JPEGs supported');
									}
									readUint16();
									frame = {};
									frame.extended = fileMarker === 0xFFC1;
									frame.progressive = fileMarker === 0xFFC2;
									frame.precision = data[offset++];
									frame.scanLines = readUint16();
									frame.samplesPerLine = readUint16();
									frame.components = [];
									frame.componentIds = {};
									var componentsCount = data[offset++], componentId;
									var maxH = 0, maxV = 0;
									for (i = 0; i < componentsCount; i++) {
										componentId = data[offset];
										var h = data[offset + 1] >> 4;
										var v = data[offset + 1] & 15;
										if (maxH < h) {
											maxH = h;
										}
										if (maxV < v) {
											maxV = v;
										}
										var qId = data[offset + 2];
										l = frame.components.push({
											h: h,
											v: v,
											quantizationId: qId,
											quantizationTable: null
										});
										frame.componentIds[componentId] = l - 1;
										offset += 3;
									}
									frame.maxH = maxH;
									frame.maxV = maxV;
									prepareComponents(frame);
									break;
								case 0xFFC4:
									var huffmanLength = readUint16();
									for (i = 2; i < huffmanLength;) {
										var huffmanTableSpec = data[offset++];
										var codeLengths = new Uint8Array(16);
										var codeLengthSum = 0;
										for (j = 0; j < 16; j++, offset++) {
											codeLengthSum += codeLengths[j] = data[offset];
										}
										var huffmanValues = new Uint8Array(codeLengthSum);
										for (j = 0; j < codeLengthSum; j++, offset++) {
											huffmanValues[j] = data[offset];
										}
										i += 17 + codeLengthSum;
										(huffmanTableSpec >> 4 === 0 ? huffmanTablesDC : huffmanTablesAC)[huffmanTableSpec & 15] = buildHuffmanTable(codeLengths, huffmanValues);
									}
									break;
								case 0xFFDD:
									readUint16();
									resetInterval = readUint16();
									break;
								case 0xFFDA:
									readUint16();
									var selectorsCount = data[offset++];
									var components = [], component;
									for (i = 0; i < selectorsCount; i++) {
										var componentIndex = frame.componentIds[data[offset++]];
										component = frame.components[componentIndex];
										var tableSpec = data[offset++];
										component.huffmanTableDC = huffmanTablesDC[tableSpec >> 4];
										component.huffmanTableAC = huffmanTablesAC[tableSpec & 15];
										components.push(component);
									}
									var spectralStart = data[offset++];
									var spectralEnd = data[offset++];
									var successiveApproximation = data[offset++];
									var processed = decodeScan(data, offset, frame, components, resetInterval, spectralStart, spectralEnd, successiveApproximation >> 4, successiveApproximation & 15);
									offset += processed;
									break;
								case 0xFFFF:
									if (data[offset] !== 0xFF) {
										offset--;
									}
									break;
								default:
									if (data[offset - 3] === 0xFF && data[offset - 2] >= 0xC0 && data[offset - 2] <= 0xFE) {
										offset -= 3;
										break;
									}
									error('JPEG error: unknown marker ' + fileMarker.toString(16));
							}
							fileMarker = readUint16();
						}
						this.width = frame.samplesPerLine;
						this.height = frame.scanLines;
						this.jfif = jfif;
						this.adobe = adobe;
						this.components = [];
						for (i = 0; i < frame.components.length; i++) {
							component = frame.components[i];
							var quantizationTable = quantizationTables[component.quantizationId];
							if (quantizationTable) {
								component.quantizationTable = quantizationTable;
							}
							this.components.push({
								output: buildComponentData(frame, component),
								scaleX: component.h / frame.maxH,
								scaleY: component.v / frame.maxV,
								blocksPerLine: component.blocksPerLine,
								blocksPerColumn: component.blocksPerColumn
							});
						}
						this.numComponents = this.components.length;
					},
					_getLinearizedBlockData: function getLinearizedBlockData(width, height) {
						var scaleX = this.width / width, scaleY = this.height / height;
						var component, componentScaleX, componentScaleY, blocksPerScanline;
						var x, y, i, j, k;
						var index;
						var offset = 0;
						var output;
						var numComponents = this.components.length;
						var dataLength = width * height * numComponents;
						var data = new Uint8Array(dataLength);
						var xScaleBlockOffset = new Uint32Array(width);
						var mask3LSB = 0xfffffff8;
						for (i = 0; i < numComponents; i++) {
							component = this.components[i];
							componentScaleX = component.scaleX * scaleX;
							componentScaleY = component.scaleY * scaleY;
							offset = i;
							output = component.output;
							blocksPerScanline = component.blocksPerLine + 1 << 3;
							for (x = 0; x < width; x++) {
								j = 0 | x * componentScaleX;
								xScaleBlockOffset[x] = (j & mask3LSB) << 3 | j & 7;
							}
							for (y = 0; y < height; y++) {
								j = 0 | y * componentScaleY;
								index = blocksPerScanline * (j & mask3LSB) | (j & 7) << 3;
								for (x = 0; x < width; x++) {
									data[offset] = output[index + xScaleBlockOffset[x]];
									offset += numComponents;
								}
							}
						}
						var transform = this.decodeTransform;
						if (transform) {
							for (i = 0; i < dataLength;) {
								for (j = 0, k = 0; j < numComponents; j++, i++, k += 2) {
									data[i] = (data[i] * transform[k] >> 8) + transform[k + 1];
								}
							}
						}
						return data;
					},
					_isColorConversionNeeded: function isColorConversionNeeded() {
						if (this.adobe && this.adobe.transformCode) {
							return true;
						} else if (this.numComponents === 3) {
							if (!this.adobe && this.colorTransform === 0) {
								return false;
							}
							return true;
						}
						if (!this.adobe && this.colorTransform === 1) {
							return true;
						}
						return false;
					},
					_convertYccToRgb: function convertYccToRgb(data) {
						var Y, Cb, Cr;
						for (var i = 0, length = data.length; i < length; i += 3) {
							Y = data[i];
							Cb = data[i + 1];
							Cr = data[i + 2];
							data[i] = clamp0to255(Y - 179.456 + 1.402 * Cr);
							data[i + 1] = clamp0to255(Y + 135.459 - 0.344 * Cb - 0.714 * Cr);
							data[i + 2] = clamp0to255(Y - 226.816 + 1.772 * Cb);
						}
						return data;
					},
					_convertYcckToRgb: function convertYcckToRgb(data) {
						var Y, Cb, Cr, k;
						var offset = 0;
						for (var i = 0, length = data.length; i < length; i += 4) {
							Y = data[i];
							Cb = data[i + 1];
							Cr = data[i + 2];
							k = data[i + 3];
							var r = -122.67195406894 + Cb * (-6.60635669420364e-5 * Cb + 0.000437130475926232 * Cr - 5.4080610064599e-5 * Y + 0.00048449797120281 * k - 0.154362151871126) + Cr * (-0.000957964378445773 * Cr + 0.000817076911346625 * Y - 0.00477271405408747 * k + 1.53380253221734) + Y * (0.000961250184130688 * Y - 0.00266257332283933 * k + 0.48357088451265) + k * (-0.000336197177618394 * k + 0.484791561490776);
							var g = 107.268039397724 + Cb * (2.19927104525741e-5 * Cb - 0.000640992018297945 * Cr + 0.000659397001245577 * Y + 0.000426105652938837 * k - 0.176491792462875) + Cr * (-0.000778269941513683 * Cr + 0.00130872261408275 * Y + 0.000770482631801132 * k - 0.151051492775562) + Y * (0.00126935368114843 * Y - 0.00265090189010898 * k + 0.25802910206845) + k * (-0.000318913117588328 * k - 0.213742400323665);
							var b = -20.810012546947 + Cb * (-0.000570115196973677 * Cb - 2.63409051004589e-5 * Cr + 0.0020741088115012 * Y - 0.00288260236853442 * k + 0.814272968359295) + Cr * (-1.53496057440975e-5 * Cr - 0.000132689043961446 * Y + 0.000560833691242812 * k - 0.195152027534049) + Y * (0.00174418132927582 * Y - 0.00255243321439347 * k + 0.116935020465145) + k * (-0.000343531996510555 * k + 0.24165260232407);
							data[offset++] = clamp0to255(r);
							data[offset++] = clamp0to255(g);
							data[offset++] = clamp0to255(b);
						}
						return data;
					},
					_convertYcckToCmyk: function convertYcckToCmyk(data) {
						var Y, Cb, Cr;
						for (var i = 0, length = data.length; i < length; i += 4) {
							Y = data[i];
							Cb = data[i + 1];
							Cr = data[i + 2];
							data[i] = clamp0to255(434.456 - Y - 1.402 * Cr);
							data[i + 1] = clamp0to255(119.541 - Y + 0.344 * Cb + 0.714 * Cr);
							data[i + 2] = clamp0to255(481.816 - Y - 1.772 * Cb);
						}
						return data;
					},
					_convertCmykToRgb: function convertCmykToRgb(data) {
						var c, m, y, k;
						var offset = 0;
						var min = -255 * 255 * 255;
						var scale = 1 / 255 / 255;
						for (var i = 0, length = data.length; i < length; i += 4) {
							c = data[i];
							m = data[i + 1];
							y = data[i + 2];
							k = data[i + 3];
							var r = c * (-4.387332384609988 * c + 54.48615194189176 * m + 18.82290502165302 * y + 212.25662451639585 * k - 72734.4411664936) + m * (1.7149763477362134 * m - 5.6096736904047315 * y - 17.873870861415444 * k - 1401.7366389350734) + y * (-2.5217340131683033 * y - 21.248923337353073 * k + 4465.541406466231) - k * (21.86122147463605 * k + 48317.86113160301);
							var g = c * (8.841041422036149 * c + 60.118027045597366 * m + 6.871425592049007 * y + 31.159100130055922 * k - 20220.756542821975) + m * (-15.310361306967817 * m + 17.575251261109482 * y + 131.35250912493976 * k - 48691.05921601825) + y * (4.444339102852739 * y + 9.8632861493405 * k - 6341.191035517494) - k * (20.737325471181034 * k + 47890.15695978492);
							var b = c * (0.8842522430003296 * c + 8.078677503112928 * m + 30.89978309703729 * y - 0.23883238689178934 * k - 3616.812083916688) + m * (10.49593273432072 * m + 63.02378494754052 * y + 50.606957656360734 * k - 28620.90484698408) + y * (0.03296041114873217 * y + 115.60384449646641 * k - 49363.43385999684) - k * (22.33816807309886 * k + 45932.16563550634);
							data[offset++] = r >= 0 ? 255 : r <= min ? 0 : 255 + r * scale | 0;
							data[offset++] = g >= 0 ? 255 : g <= min ? 0 : 255 + g * scale | 0;
							data[offset++] = b >= 0 ? 255 : b <= min ? 0 : 255 + b * scale | 0;
						}
						return data;
					},
					getData: function getData(width, height, forceRGBoutput) {
						if (this.numComponents > 4) {
							error('JPEG error: Unsupported color mode');
						}
						var data = this._getLinearizedBlockData(width, height);
						if (this.numComponents === 1 && forceRGBoutput) {
							var dataLength = data.length;
							var rgbData = new Uint8Array(dataLength * 3);
							var offset = 0;
							for (var i = 0; i < dataLength; i++) {
								var grayColor = data[i];
								rgbData[offset++] = grayColor;
								rgbData[offset++] = grayColor;
								rgbData[offset++] = grayColor;
							}
							return rgbData;
						} else if (this.numComponents === 3 && this._isColorConversionNeeded()) {
							return this._convertYccToRgb(data);
						} else if (this.numComponents === 4) {
							if (this._isColorConversionNeeded()) {
								if (forceRGBoutput) {
									return this._convertYcckToRgb(data);
								}
								return this._convertYcckToCmyk(data);
							} else if (forceRGBoutput) {
								return this._convertCmykToRgb(data);
							}
						}
						return data;
					}
				};
				return JpegImage;
			}();
			exports.JpegImage = JpegImage;

			/***/
		}),
/* 30 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var getLookupTableFactory = sharedUtil.getLookupTableFactory;
			var getMetrics = getLookupTableFactory(function (t) {
				t['Courier'] = 600;
				t['Courier-Bold'] = 600;
				t['Courier-BoldOblique'] = 600;
				t['Courier-Oblique'] = 600;
				t['Helvetica'] = getLookupTableFactory(function (t) {
					t['space'] = 278;
					t['exclam'] = 278;
					t['quotedbl'] = 355;
					t['numbersign'] = 556;
					t['dollar'] = 556;
					t['percent'] = 889;
					t['ampersand'] = 667;
					t['quoteright'] = 222;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 389;
					t['plus'] = 584;
					t['comma'] = 278;
					t['hyphen'] = 333;
					t['period'] = 278;
					t['slash'] = 278;
					t['zero'] = 556;
					t['one'] = 556;
					t['two'] = 556;
					t['three'] = 556;
					t['four'] = 556;
					t['five'] = 556;
					t['six'] = 556;
					t['seven'] = 556;
					t['eight'] = 556;
					t['nine'] = 556;
					t['colon'] = 278;
					t['semicolon'] = 278;
					t['less'] = 584;
					t['equal'] = 584;
					t['greater'] = 584;
					t['question'] = 556;
					t['at'] = 1015;
					t['A'] = 667;
					t['B'] = 667;
					t['C'] = 722;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 611;
					t['G'] = 778;
					t['H'] = 722;
					t['I'] = 278;
					t['J'] = 500;
					t['K'] = 667;
					t['L'] = 556;
					t['M'] = 833;
					t['N'] = 722;
					t['O'] = 778;
					t['P'] = 667;
					t['Q'] = 778;
					t['R'] = 722;
					t['S'] = 667;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 667;
					t['W'] = 944;
					t['X'] = 667;
					t['Y'] = 667;
					t['Z'] = 611;
					t['bracketleft'] = 278;
					t['backslash'] = 278;
					t['bracketright'] = 278;
					t['asciicircum'] = 469;
					t['underscore'] = 556;
					t['quoteleft'] = 222;
					t['a'] = 556;
					t['b'] = 556;
					t['c'] = 500;
					t['d'] = 556;
					t['e'] = 556;
					t['f'] = 278;
					t['g'] = 556;
					t['h'] = 556;
					t['i'] = 222;
					t['j'] = 222;
					t['k'] = 500;
					t['l'] = 222;
					t['m'] = 833;
					t['n'] = 556;
					t['o'] = 556;
					t['p'] = 556;
					t['q'] = 556;
					t['r'] = 333;
					t['s'] = 500;
					t['t'] = 278;
					t['u'] = 556;
					t['v'] = 500;
					t['w'] = 722;
					t['x'] = 500;
					t['y'] = 500;
					t['z'] = 500;
					t['braceleft'] = 334;
					t['bar'] = 260;
					t['braceright'] = 334;
					t['asciitilde'] = 584;
					t['exclamdown'] = 333;
					t['cent'] = 556;
					t['sterling'] = 556;
					t['fraction'] = 167;
					t['yen'] = 556;
					t['florin'] = 556;
					t['section'] = 556;
					t['currency'] = 556;
					t['quotesingle'] = 191;
					t['quotedblleft'] = 333;
					t['guillemotleft'] = 556;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 500;
					t['fl'] = 500;
					t['endash'] = 556;
					t['dagger'] = 556;
					t['daggerdbl'] = 556;
					t['periodcentered'] = 278;
					t['paragraph'] = 537;
					t['bullet'] = 350;
					t['quotesinglbase'] = 222;
					t['quotedblbase'] = 333;
					t['quotedblright'] = 333;
					t['guillemotright'] = 556;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 611;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 1000;
					t['ordfeminine'] = 370;
					t['Lslash'] = 556;
					t['Oslash'] = 778;
					t['OE'] = 1000;
					t['ordmasculine'] = 365;
					t['ae'] = 889;
					t['dotlessi'] = 278;
					t['lslash'] = 222;
					t['oslash'] = 611;
					t['oe'] = 944;
					t['germandbls'] = 611;
					t['Idieresis'] = 278;
					t['eacute'] = 556;
					t['abreve'] = 556;
					t['uhungarumlaut'] = 556;
					t['ecaron'] = 556;
					t['Ydieresis'] = 667;
					t['divide'] = 584;
					t['Yacute'] = 667;
					t['Acircumflex'] = 667;
					t['aacute'] = 556;
					t['Ucircumflex'] = 722;
					t['yacute'] = 500;
					t['scommaaccent'] = 500;
					t['ecircumflex'] = 556;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 556;
					t['Uacute'] = 722;
					t['uogonek'] = 556;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 737;
					t['Emacron'] = 667;
					t['ccaron'] = 500;
					t['aring'] = 556;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 222;
					t['agrave'] = 556;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 722;
					t['atilde'] = 556;
					t['Edotaccent'] = 667;
					t['scaron'] = 500;
					t['scedilla'] = 500;
					t['iacute'] = 278;
					t['lozenge'] = 471;
					t['Rcaron'] = 722;
					t['Gcommaaccent'] = 778;
					t['ucircumflex'] = 556;
					t['acircumflex'] = 556;
					t['Amacron'] = 667;
					t['rcaron'] = 333;
					t['ccedilla'] = 500;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 667;
					t['Omacron'] = 778;
					t['Racute'] = 722;
					t['Sacute'] = 667;
					t['dcaron'] = 643;
					t['Umacron'] = 722;
					t['uring'] = 556;
					t['threesuperior'] = 333;
					t['Ograve'] = 778;
					t['Agrave'] = 667;
					t['Abreve'] = 667;
					t['multiply'] = 584;
					t['uacute'] = 556;
					t['Tcaron'] = 611;
					t['partialdiff'] = 476;
					t['ydieresis'] = 500;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 556;
					t['edieresis'] = 556;
					t['cacute'] = 500;
					t['nacute'] = 556;
					t['umacron'] = 556;
					t['Ncaron'] = 722;
					t['Iacute'] = 278;
					t['plusminus'] = 584;
					t['brokenbar'] = 260;
					t['registered'] = 737;
					t['Gbreve'] = 778;
					t['Idotaccent'] = 278;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 333;
					t['omacron'] = 556;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 722;
					t['lcommaaccent'] = 222;
					t['tcaron'] = 317;
					t['eogonek'] = 556;
					t['Uogonek'] = 722;
					t['Aacute'] = 667;
					t['Adieresis'] = 667;
					t['egrave'] = 556;
					t['zacute'] = 500;
					t['iogonek'] = 222;
					t['Oacute'] = 778;
					t['oacute'] = 556;
					t['amacron'] = 556;
					t['sacute'] = 500;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 778;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 556;
					t['twosuperior'] = 333;
					t['Odieresis'] = 778;
					t['mu'] = 556;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 556;
					t['Eogonek'] = 667;
					t['dcroat'] = 556;
					t['threequarters'] = 834;
					t['Scedilla'] = 667;
					t['lcaron'] = 299;
					t['Kcommaaccent'] = 667;
					t['Lacute'] = 556;
					t['trademark'] = 1000;
					t['edotaccent'] = 556;
					t['Igrave'] = 278;
					t['Imacron'] = 278;
					t['Lcaron'] = 556;
					t['onehalf'] = 834;
					t['lessequal'] = 549;
					t['ocircumflex'] = 556;
					t['ntilde'] = 556;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 556;
					t['gbreve'] = 556;
					t['onequarter'] = 834;
					t['Scaron'] = 667;
					t['Scommaaccent'] = 667;
					t['Ohungarumlaut'] = 778;
					t['degree'] = 400;
					t['ograve'] = 556;
					t['Ccaron'] = 722;
					t['ugrave'] = 556;
					t['radical'] = 453;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 333;
					t['Ntilde'] = 722;
					t['otilde'] = 556;
					t['Rcommaaccent'] = 722;
					t['Lcommaaccent'] = 556;
					t['Atilde'] = 667;
					t['Aogonek'] = 667;
					t['Aring'] = 667;
					t['Otilde'] = 778;
					t['zdotaccent'] = 500;
					t['Ecaron'] = 667;
					t['Iogonek'] = 278;
					t['kcommaaccent'] = 500;
					t['minus'] = 584;
					t['Icircumflex'] = 278;
					t['ncaron'] = 556;
					t['tcommaaccent'] = 278;
					t['logicalnot'] = 584;
					t['odieresis'] = 556;
					t['udieresis'] = 556;
					t['notequal'] = 549;
					t['gcommaaccent'] = 556;
					t['eth'] = 556;
					t['zcaron'] = 500;
					t['ncommaaccent'] = 556;
					t['onesuperior'] = 333;
					t['imacron'] = 278;
					t['Euro'] = 556;
				});
				t['Helvetica-Bold'] = getLookupTableFactory(function (t) {
					t['space'] = 278;
					t['exclam'] = 333;
					t['quotedbl'] = 474;
					t['numbersign'] = 556;
					t['dollar'] = 556;
					t['percent'] = 889;
					t['ampersand'] = 722;
					t['quoteright'] = 278;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 389;
					t['plus'] = 584;
					t['comma'] = 278;
					t['hyphen'] = 333;
					t['period'] = 278;
					t['slash'] = 278;
					t['zero'] = 556;
					t['one'] = 556;
					t['two'] = 556;
					t['three'] = 556;
					t['four'] = 556;
					t['five'] = 556;
					t['six'] = 556;
					t['seven'] = 556;
					t['eight'] = 556;
					t['nine'] = 556;
					t['colon'] = 333;
					t['semicolon'] = 333;
					t['less'] = 584;
					t['equal'] = 584;
					t['greater'] = 584;
					t['question'] = 611;
					t['at'] = 975;
					t['A'] = 722;
					t['B'] = 722;
					t['C'] = 722;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 611;
					t['G'] = 778;
					t['H'] = 722;
					t['I'] = 278;
					t['J'] = 556;
					t['K'] = 722;
					t['L'] = 611;
					t['M'] = 833;
					t['N'] = 722;
					t['O'] = 778;
					t['P'] = 667;
					t['Q'] = 778;
					t['R'] = 722;
					t['S'] = 667;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 667;
					t['W'] = 944;
					t['X'] = 667;
					t['Y'] = 667;
					t['Z'] = 611;
					t['bracketleft'] = 333;
					t['backslash'] = 278;
					t['bracketright'] = 333;
					t['asciicircum'] = 584;
					t['underscore'] = 556;
					t['quoteleft'] = 278;
					t['a'] = 556;
					t['b'] = 611;
					t['c'] = 556;
					t['d'] = 611;
					t['e'] = 556;
					t['f'] = 333;
					t['g'] = 611;
					t['h'] = 611;
					t['i'] = 278;
					t['j'] = 278;
					t['k'] = 556;
					t['l'] = 278;
					t['m'] = 889;
					t['n'] = 611;
					t['o'] = 611;
					t['p'] = 611;
					t['q'] = 611;
					t['r'] = 389;
					t['s'] = 556;
					t['t'] = 333;
					t['u'] = 611;
					t['v'] = 556;
					t['w'] = 778;
					t['x'] = 556;
					t['y'] = 556;
					t['z'] = 500;
					t['braceleft'] = 389;
					t['bar'] = 280;
					t['braceright'] = 389;
					t['asciitilde'] = 584;
					t['exclamdown'] = 333;
					t['cent'] = 556;
					t['sterling'] = 556;
					t['fraction'] = 167;
					t['yen'] = 556;
					t['florin'] = 556;
					t['section'] = 556;
					t['currency'] = 556;
					t['quotesingle'] = 238;
					t['quotedblleft'] = 500;
					t['guillemotleft'] = 556;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 611;
					t['fl'] = 611;
					t['endash'] = 556;
					t['dagger'] = 556;
					t['daggerdbl'] = 556;
					t['periodcentered'] = 278;
					t['paragraph'] = 556;
					t['bullet'] = 350;
					t['quotesinglbase'] = 278;
					t['quotedblbase'] = 500;
					t['quotedblright'] = 500;
					t['guillemotright'] = 556;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 611;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 1000;
					t['ordfeminine'] = 370;
					t['Lslash'] = 611;
					t['Oslash'] = 778;
					t['OE'] = 1000;
					t['ordmasculine'] = 365;
					t['ae'] = 889;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 611;
					t['oe'] = 944;
					t['germandbls'] = 611;
					t['Idieresis'] = 278;
					t['eacute'] = 556;
					t['abreve'] = 556;
					t['uhungarumlaut'] = 611;
					t['ecaron'] = 556;
					t['Ydieresis'] = 667;
					t['divide'] = 584;
					t['Yacute'] = 667;
					t['Acircumflex'] = 722;
					t['aacute'] = 556;
					t['Ucircumflex'] = 722;
					t['yacute'] = 556;
					t['scommaaccent'] = 556;
					t['ecircumflex'] = 556;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 556;
					t['Uacute'] = 722;
					t['uogonek'] = 611;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 737;
					t['Emacron'] = 667;
					t['ccaron'] = 556;
					t['aring'] = 556;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 278;
					t['agrave'] = 556;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 722;
					t['atilde'] = 556;
					t['Edotaccent'] = 667;
					t['scaron'] = 556;
					t['scedilla'] = 556;
					t['iacute'] = 278;
					t['lozenge'] = 494;
					t['Rcaron'] = 722;
					t['Gcommaaccent'] = 778;
					t['ucircumflex'] = 611;
					t['acircumflex'] = 556;
					t['Amacron'] = 722;
					t['rcaron'] = 389;
					t['ccedilla'] = 556;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 667;
					t['Omacron'] = 778;
					t['Racute'] = 722;
					t['Sacute'] = 667;
					t['dcaron'] = 743;
					t['Umacron'] = 722;
					t['uring'] = 611;
					t['threesuperior'] = 333;
					t['Ograve'] = 778;
					t['Agrave'] = 722;
					t['Abreve'] = 722;
					t['multiply'] = 584;
					t['uacute'] = 611;
					t['Tcaron'] = 611;
					t['partialdiff'] = 494;
					t['ydieresis'] = 556;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 556;
					t['edieresis'] = 556;
					t['cacute'] = 556;
					t['nacute'] = 611;
					t['umacron'] = 611;
					t['Ncaron'] = 722;
					t['Iacute'] = 278;
					t['plusminus'] = 584;
					t['brokenbar'] = 280;
					t['registered'] = 737;
					t['Gbreve'] = 778;
					t['Idotaccent'] = 278;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 389;
					t['omacron'] = 611;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 722;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 389;
					t['eogonek'] = 556;
					t['Uogonek'] = 722;
					t['Aacute'] = 722;
					t['Adieresis'] = 722;
					t['egrave'] = 556;
					t['zacute'] = 500;
					t['iogonek'] = 278;
					t['Oacute'] = 778;
					t['oacute'] = 611;
					t['amacron'] = 556;
					t['sacute'] = 556;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 778;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 611;
					t['twosuperior'] = 333;
					t['Odieresis'] = 778;
					t['mu'] = 611;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 611;
					t['Eogonek'] = 667;
					t['dcroat'] = 611;
					t['threequarters'] = 834;
					t['Scedilla'] = 667;
					t['lcaron'] = 400;
					t['Kcommaaccent'] = 722;
					t['Lacute'] = 611;
					t['trademark'] = 1000;
					t['edotaccent'] = 556;
					t['Igrave'] = 278;
					t['Imacron'] = 278;
					t['Lcaron'] = 611;
					t['onehalf'] = 834;
					t['lessequal'] = 549;
					t['ocircumflex'] = 611;
					t['ntilde'] = 611;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 556;
					t['gbreve'] = 611;
					t['onequarter'] = 834;
					t['Scaron'] = 667;
					t['Scommaaccent'] = 667;
					t['Ohungarumlaut'] = 778;
					t['degree'] = 400;
					t['ograve'] = 611;
					t['Ccaron'] = 722;
					t['ugrave'] = 611;
					t['radical'] = 549;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 389;
					t['Ntilde'] = 722;
					t['otilde'] = 611;
					t['Rcommaaccent'] = 722;
					t['Lcommaaccent'] = 611;
					t['Atilde'] = 722;
					t['Aogonek'] = 722;
					t['Aring'] = 722;
					t['Otilde'] = 778;
					t['zdotaccent'] = 500;
					t['Ecaron'] = 667;
					t['Iogonek'] = 278;
					t['kcommaaccent'] = 556;
					t['minus'] = 584;
					t['Icircumflex'] = 278;
					t['ncaron'] = 611;
					t['tcommaaccent'] = 333;
					t['logicalnot'] = 584;
					t['odieresis'] = 611;
					t['udieresis'] = 611;
					t['notequal'] = 549;
					t['gcommaaccent'] = 611;
					t['eth'] = 611;
					t['zcaron'] = 500;
					t['ncommaaccent'] = 611;
					t['onesuperior'] = 333;
					t['imacron'] = 278;
					t['Euro'] = 556;
				});
				t['Helvetica-BoldOblique'] = getLookupTableFactory(function (t) {
					t['space'] = 278;
					t['exclam'] = 333;
					t['quotedbl'] = 474;
					t['numbersign'] = 556;
					t['dollar'] = 556;
					t['percent'] = 889;
					t['ampersand'] = 722;
					t['quoteright'] = 278;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 389;
					t['plus'] = 584;
					t['comma'] = 278;
					t['hyphen'] = 333;
					t['period'] = 278;
					t['slash'] = 278;
					t['zero'] = 556;
					t['one'] = 556;
					t['two'] = 556;
					t['three'] = 556;
					t['four'] = 556;
					t['five'] = 556;
					t['six'] = 556;
					t['seven'] = 556;
					t['eight'] = 556;
					t['nine'] = 556;
					t['colon'] = 333;
					t['semicolon'] = 333;
					t['less'] = 584;
					t['equal'] = 584;
					t['greater'] = 584;
					t['question'] = 611;
					t['at'] = 975;
					t['A'] = 722;
					t['B'] = 722;
					t['C'] = 722;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 611;
					t['G'] = 778;
					t['H'] = 722;
					t['I'] = 278;
					t['J'] = 556;
					t['K'] = 722;
					t['L'] = 611;
					t['M'] = 833;
					t['N'] = 722;
					t['O'] = 778;
					t['P'] = 667;
					t['Q'] = 778;
					t['R'] = 722;
					t['S'] = 667;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 667;
					t['W'] = 944;
					t['X'] = 667;
					t['Y'] = 667;
					t['Z'] = 611;
					t['bracketleft'] = 333;
					t['backslash'] = 278;
					t['bracketright'] = 333;
					t['asciicircum'] = 584;
					t['underscore'] = 556;
					t['quoteleft'] = 278;
					t['a'] = 556;
					t['b'] = 611;
					t['c'] = 556;
					t['d'] = 611;
					t['e'] = 556;
					t['f'] = 333;
					t['g'] = 611;
					t['h'] = 611;
					t['i'] = 278;
					t['j'] = 278;
					t['k'] = 556;
					t['l'] = 278;
					t['m'] = 889;
					t['n'] = 611;
					t['o'] = 611;
					t['p'] = 611;
					t['q'] = 611;
					t['r'] = 389;
					t['s'] = 556;
					t['t'] = 333;
					t['u'] = 611;
					t['v'] = 556;
					t['w'] = 778;
					t['x'] = 556;
					t['y'] = 556;
					t['z'] = 500;
					t['braceleft'] = 389;
					t['bar'] = 280;
					t['braceright'] = 389;
					t['asciitilde'] = 584;
					t['exclamdown'] = 333;
					t['cent'] = 556;
					t['sterling'] = 556;
					t['fraction'] = 167;
					t['yen'] = 556;
					t['florin'] = 556;
					t['section'] = 556;
					t['currency'] = 556;
					t['quotesingle'] = 238;
					t['quotedblleft'] = 500;
					t['guillemotleft'] = 556;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 611;
					t['fl'] = 611;
					t['endash'] = 556;
					t['dagger'] = 556;
					t['daggerdbl'] = 556;
					t['periodcentered'] = 278;
					t['paragraph'] = 556;
					t['bullet'] = 350;
					t['quotesinglbase'] = 278;
					t['quotedblbase'] = 500;
					t['quotedblright'] = 500;
					t['guillemotright'] = 556;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 611;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 1000;
					t['ordfeminine'] = 370;
					t['Lslash'] = 611;
					t['Oslash'] = 778;
					t['OE'] = 1000;
					t['ordmasculine'] = 365;
					t['ae'] = 889;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 611;
					t['oe'] = 944;
					t['germandbls'] = 611;
					t['Idieresis'] = 278;
					t['eacute'] = 556;
					t['abreve'] = 556;
					t['uhungarumlaut'] = 611;
					t['ecaron'] = 556;
					t['Ydieresis'] = 667;
					t['divide'] = 584;
					t['Yacute'] = 667;
					t['Acircumflex'] = 722;
					t['aacute'] = 556;
					t['Ucircumflex'] = 722;
					t['yacute'] = 556;
					t['scommaaccent'] = 556;
					t['ecircumflex'] = 556;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 556;
					t['Uacute'] = 722;
					t['uogonek'] = 611;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 737;
					t['Emacron'] = 667;
					t['ccaron'] = 556;
					t['aring'] = 556;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 278;
					t['agrave'] = 556;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 722;
					t['atilde'] = 556;
					t['Edotaccent'] = 667;
					t['scaron'] = 556;
					t['scedilla'] = 556;
					t['iacute'] = 278;
					t['lozenge'] = 494;
					t['Rcaron'] = 722;
					t['Gcommaaccent'] = 778;
					t['ucircumflex'] = 611;
					t['acircumflex'] = 556;
					t['Amacron'] = 722;
					t['rcaron'] = 389;
					t['ccedilla'] = 556;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 667;
					t['Omacron'] = 778;
					t['Racute'] = 722;
					t['Sacute'] = 667;
					t['dcaron'] = 743;
					t['Umacron'] = 722;
					t['uring'] = 611;
					t['threesuperior'] = 333;
					t['Ograve'] = 778;
					t['Agrave'] = 722;
					t['Abreve'] = 722;
					t['multiply'] = 584;
					t['uacute'] = 611;
					t['Tcaron'] = 611;
					t['partialdiff'] = 494;
					t['ydieresis'] = 556;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 556;
					t['edieresis'] = 556;
					t['cacute'] = 556;
					t['nacute'] = 611;
					t['umacron'] = 611;
					t['Ncaron'] = 722;
					t['Iacute'] = 278;
					t['plusminus'] = 584;
					t['brokenbar'] = 280;
					t['registered'] = 737;
					t['Gbreve'] = 778;
					t['Idotaccent'] = 278;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 389;
					t['omacron'] = 611;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 722;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 389;
					t['eogonek'] = 556;
					t['Uogonek'] = 722;
					t['Aacute'] = 722;
					t['Adieresis'] = 722;
					t['egrave'] = 556;
					t['zacute'] = 500;
					t['iogonek'] = 278;
					t['Oacute'] = 778;
					t['oacute'] = 611;
					t['amacron'] = 556;
					t['sacute'] = 556;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 778;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 611;
					t['twosuperior'] = 333;
					t['Odieresis'] = 778;
					t['mu'] = 611;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 611;
					t['Eogonek'] = 667;
					t['dcroat'] = 611;
					t['threequarters'] = 834;
					t['Scedilla'] = 667;
					t['lcaron'] = 400;
					t['Kcommaaccent'] = 722;
					t['Lacute'] = 611;
					t['trademark'] = 1000;
					t['edotaccent'] = 556;
					t['Igrave'] = 278;
					t['Imacron'] = 278;
					t['Lcaron'] = 611;
					t['onehalf'] = 834;
					t['lessequal'] = 549;
					t['ocircumflex'] = 611;
					t['ntilde'] = 611;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 556;
					t['gbreve'] = 611;
					t['onequarter'] = 834;
					t['Scaron'] = 667;
					t['Scommaaccent'] = 667;
					t['Ohungarumlaut'] = 778;
					t['degree'] = 400;
					t['ograve'] = 611;
					t['Ccaron'] = 722;
					t['ugrave'] = 611;
					t['radical'] = 549;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 389;
					t['Ntilde'] = 722;
					t['otilde'] = 611;
					t['Rcommaaccent'] = 722;
					t['Lcommaaccent'] = 611;
					t['Atilde'] = 722;
					t['Aogonek'] = 722;
					t['Aring'] = 722;
					t['Otilde'] = 778;
					t['zdotaccent'] = 500;
					t['Ecaron'] = 667;
					t['Iogonek'] = 278;
					t['kcommaaccent'] = 556;
					t['minus'] = 584;
					t['Icircumflex'] = 278;
					t['ncaron'] = 611;
					t['tcommaaccent'] = 333;
					t['logicalnot'] = 584;
					t['odieresis'] = 611;
					t['udieresis'] = 611;
					t['notequal'] = 549;
					t['gcommaaccent'] = 611;
					t['eth'] = 611;
					t['zcaron'] = 500;
					t['ncommaaccent'] = 611;
					t['onesuperior'] = 333;
					t['imacron'] = 278;
					t['Euro'] = 556;
				});
				t['Helvetica-Oblique'] = getLookupTableFactory(function (t) {
					t['space'] = 278;
					t['exclam'] = 278;
					t['quotedbl'] = 355;
					t['numbersign'] = 556;
					t['dollar'] = 556;
					t['percent'] = 889;
					t['ampersand'] = 667;
					t['quoteright'] = 222;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 389;
					t['plus'] = 584;
					t['comma'] = 278;
					t['hyphen'] = 333;
					t['period'] = 278;
					t['slash'] = 278;
					t['zero'] = 556;
					t['one'] = 556;
					t['two'] = 556;
					t['three'] = 556;
					t['four'] = 556;
					t['five'] = 556;
					t['six'] = 556;
					t['seven'] = 556;
					t['eight'] = 556;
					t['nine'] = 556;
					t['colon'] = 278;
					t['semicolon'] = 278;
					t['less'] = 584;
					t['equal'] = 584;
					t['greater'] = 584;
					t['question'] = 556;
					t['at'] = 1015;
					t['A'] = 667;
					t['B'] = 667;
					t['C'] = 722;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 611;
					t['G'] = 778;
					t['H'] = 722;
					t['I'] = 278;
					t['J'] = 500;
					t['K'] = 667;
					t['L'] = 556;
					t['M'] = 833;
					t['N'] = 722;
					t['O'] = 778;
					t['P'] = 667;
					t['Q'] = 778;
					t['R'] = 722;
					t['S'] = 667;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 667;
					t['W'] = 944;
					t['X'] = 667;
					t['Y'] = 667;
					t['Z'] = 611;
					t['bracketleft'] = 278;
					t['backslash'] = 278;
					t['bracketright'] = 278;
					t['asciicircum'] = 469;
					t['underscore'] = 556;
					t['quoteleft'] = 222;
					t['a'] = 556;
					t['b'] = 556;
					t['c'] = 500;
					t['d'] = 556;
					t['e'] = 556;
					t['f'] = 278;
					t['g'] = 556;
					t['h'] = 556;
					t['i'] = 222;
					t['j'] = 222;
					t['k'] = 500;
					t['l'] = 222;
					t['m'] = 833;
					t['n'] = 556;
					t['o'] = 556;
					t['p'] = 556;
					t['q'] = 556;
					t['r'] = 333;
					t['s'] = 500;
					t['t'] = 278;
					t['u'] = 556;
					t['v'] = 500;
					t['w'] = 722;
					t['x'] = 500;
					t['y'] = 500;
					t['z'] = 500;
					t['braceleft'] = 334;
					t['bar'] = 260;
					t['braceright'] = 334;
					t['asciitilde'] = 584;
					t['exclamdown'] = 333;
					t['cent'] = 556;
					t['sterling'] = 556;
					t['fraction'] = 167;
					t['yen'] = 556;
					t['florin'] = 556;
					t['section'] = 556;
					t['currency'] = 556;
					t['quotesingle'] = 191;
					t['quotedblleft'] = 333;
					t['guillemotleft'] = 556;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 500;
					t['fl'] = 500;
					t['endash'] = 556;
					t['dagger'] = 556;
					t['daggerdbl'] = 556;
					t['periodcentered'] = 278;
					t['paragraph'] = 537;
					t['bullet'] = 350;
					t['quotesinglbase'] = 222;
					t['quotedblbase'] = 333;
					t['quotedblright'] = 333;
					t['guillemotright'] = 556;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 611;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 1000;
					t['ordfeminine'] = 370;
					t['Lslash'] = 556;
					t['Oslash'] = 778;
					t['OE'] = 1000;
					t['ordmasculine'] = 365;
					t['ae'] = 889;
					t['dotlessi'] = 278;
					t['lslash'] = 222;
					t['oslash'] = 611;
					t['oe'] = 944;
					t['germandbls'] = 611;
					t['Idieresis'] = 278;
					t['eacute'] = 556;
					t['abreve'] = 556;
					t['uhungarumlaut'] = 556;
					t['ecaron'] = 556;
					t['Ydieresis'] = 667;
					t['divide'] = 584;
					t['Yacute'] = 667;
					t['Acircumflex'] = 667;
					t['aacute'] = 556;
					t['Ucircumflex'] = 722;
					t['yacute'] = 500;
					t['scommaaccent'] = 500;
					t['ecircumflex'] = 556;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 556;
					t['Uacute'] = 722;
					t['uogonek'] = 556;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 737;
					t['Emacron'] = 667;
					t['ccaron'] = 500;
					t['aring'] = 556;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 222;
					t['agrave'] = 556;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 722;
					t['atilde'] = 556;
					t['Edotaccent'] = 667;
					t['scaron'] = 500;
					t['scedilla'] = 500;
					t['iacute'] = 278;
					t['lozenge'] = 471;
					t['Rcaron'] = 722;
					t['Gcommaaccent'] = 778;
					t['ucircumflex'] = 556;
					t['acircumflex'] = 556;
					t['Amacron'] = 667;
					t['rcaron'] = 333;
					t['ccedilla'] = 500;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 667;
					t['Omacron'] = 778;
					t['Racute'] = 722;
					t['Sacute'] = 667;
					t['dcaron'] = 643;
					t['Umacron'] = 722;
					t['uring'] = 556;
					t['threesuperior'] = 333;
					t['Ograve'] = 778;
					t['Agrave'] = 667;
					t['Abreve'] = 667;
					t['multiply'] = 584;
					t['uacute'] = 556;
					t['Tcaron'] = 611;
					t['partialdiff'] = 476;
					t['ydieresis'] = 500;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 556;
					t['edieresis'] = 556;
					t['cacute'] = 500;
					t['nacute'] = 556;
					t['umacron'] = 556;
					t['Ncaron'] = 722;
					t['Iacute'] = 278;
					t['plusminus'] = 584;
					t['brokenbar'] = 260;
					t['registered'] = 737;
					t['Gbreve'] = 778;
					t['Idotaccent'] = 278;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 333;
					t['omacron'] = 556;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 722;
					t['lcommaaccent'] = 222;
					t['tcaron'] = 317;
					t['eogonek'] = 556;
					t['Uogonek'] = 722;
					t['Aacute'] = 667;
					t['Adieresis'] = 667;
					t['egrave'] = 556;
					t['zacute'] = 500;
					t['iogonek'] = 222;
					t['Oacute'] = 778;
					t['oacute'] = 556;
					t['amacron'] = 556;
					t['sacute'] = 500;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 778;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 556;
					t['twosuperior'] = 333;
					t['Odieresis'] = 778;
					t['mu'] = 556;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 556;
					t['Eogonek'] = 667;
					t['dcroat'] = 556;
					t['threequarters'] = 834;
					t['Scedilla'] = 667;
					t['lcaron'] = 299;
					t['Kcommaaccent'] = 667;
					t['Lacute'] = 556;
					t['trademark'] = 1000;
					t['edotaccent'] = 556;
					t['Igrave'] = 278;
					t['Imacron'] = 278;
					t['Lcaron'] = 556;
					t['onehalf'] = 834;
					t['lessequal'] = 549;
					t['ocircumflex'] = 556;
					t['ntilde'] = 556;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 556;
					t['gbreve'] = 556;
					t['onequarter'] = 834;
					t['Scaron'] = 667;
					t['Scommaaccent'] = 667;
					t['Ohungarumlaut'] = 778;
					t['degree'] = 400;
					t['ograve'] = 556;
					t['Ccaron'] = 722;
					t['ugrave'] = 556;
					t['radical'] = 453;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 333;
					t['Ntilde'] = 722;
					t['otilde'] = 556;
					t['Rcommaaccent'] = 722;
					t['Lcommaaccent'] = 556;
					t['Atilde'] = 667;
					t['Aogonek'] = 667;
					t['Aring'] = 667;
					t['Otilde'] = 778;
					t['zdotaccent'] = 500;
					t['Ecaron'] = 667;
					t['Iogonek'] = 278;
					t['kcommaaccent'] = 500;
					t['minus'] = 584;
					t['Icircumflex'] = 278;
					t['ncaron'] = 556;
					t['tcommaaccent'] = 278;
					t['logicalnot'] = 584;
					t['odieresis'] = 556;
					t['udieresis'] = 556;
					t['notequal'] = 549;
					t['gcommaaccent'] = 556;
					t['eth'] = 556;
					t['zcaron'] = 500;
					t['ncommaaccent'] = 556;
					t['onesuperior'] = 333;
					t['imacron'] = 278;
					t['Euro'] = 556;
				});
				t['Symbol'] = getLookupTableFactory(function (t) {
					t['space'] = 250;
					t['exclam'] = 333;
					t['universal'] = 713;
					t['numbersign'] = 500;
					t['existential'] = 549;
					t['percent'] = 833;
					t['ampersand'] = 778;
					t['suchthat'] = 439;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asteriskmath'] = 500;
					t['plus'] = 549;
					t['comma'] = 250;
					t['minus'] = 549;
					t['period'] = 250;
					t['slash'] = 278;
					t['zero'] = 500;
					t['one'] = 500;
					t['two'] = 500;
					t['three'] = 500;
					t['four'] = 500;
					t['five'] = 500;
					t['six'] = 500;
					t['seven'] = 500;
					t['eight'] = 500;
					t['nine'] = 500;
					t['colon'] = 278;
					t['semicolon'] = 278;
					t['less'] = 549;
					t['equal'] = 549;
					t['greater'] = 549;
					t['question'] = 444;
					t['congruent'] = 549;
					t['Alpha'] = 722;
					t['Beta'] = 667;
					t['Chi'] = 722;
					t['Delta'] = 612;
					t['Epsilon'] = 611;
					t['Phi'] = 763;
					t['Gamma'] = 603;
					t['Eta'] = 722;
					t['Iota'] = 333;
					t['theta1'] = 631;
					t['Kappa'] = 722;
					t['Lambda'] = 686;
					t['Mu'] = 889;
					t['Nu'] = 722;
					t['Omicron'] = 722;
					t['Pi'] = 768;
					t['Theta'] = 741;
					t['Rho'] = 556;
					t['Sigma'] = 592;
					t['Tau'] = 611;
					t['Upsilon'] = 690;
					t['sigma1'] = 439;
					t['Omega'] = 768;
					t['Xi'] = 645;
					t['Psi'] = 795;
					t['Zeta'] = 611;
					t['bracketleft'] = 333;
					t['therefore'] = 863;
					t['bracketright'] = 333;
					t['perpendicular'] = 658;
					t['underscore'] = 500;
					t['radicalex'] = 500;
					t['alpha'] = 631;
					t['beta'] = 549;
					t['chi'] = 549;
					t['delta'] = 494;
					t['epsilon'] = 439;
					t['phi'] = 521;
					t['gamma'] = 411;
					t['eta'] = 603;
					t['iota'] = 329;
					t['phi1'] = 603;
					t['kappa'] = 549;
					t['lambda'] = 549;
					t['mu'] = 576;
					t['nu'] = 521;
					t['omicron'] = 549;
					t['pi'] = 549;
					t['theta'] = 521;
					t['rho'] = 549;
					t['sigma'] = 603;
					t['tau'] = 439;
					t['upsilon'] = 576;
					t['omega1'] = 713;
					t['omega'] = 686;
					t['xi'] = 493;
					t['psi'] = 686;
					t['zeta'] = 494;
					t['braceleft'] = 480;
					t['bar'] = 200;
					t['braceright'] = 480;
					t['similar'] = 549;
					t['Euro'] = 750;
					t['Upsilon1'] = 620;
					t['minute'] = 247;
					t['lessequal'] = 549;
					t['fraction'] = 167;
					t['infinity'] = 713;
					t['florin'] = 500;
					t['club'] = 753;
					t['diamond'] = 753;
					t['heart'] = 753;
					t['spade'] = 753;
					t['arrowboth'] = 1042;
					t['arrowleft'] = 987;
					t['arrowup'] = 603;
					t['arrowright'] = 987;
					t['arrowdown'] = 603;
					t['degree'] = 400;
					t['plusminus'] = 549;
					t['second'] = 411;
					t['greaterequal'] = 549;
					t['multiply'] = 549;
					t['proportional'] = 713;
					t['partialdiff'] = 494;
					t['bullet'] = 460;
					t['divide'] = 549;
					t['notequal'] = 549;
					t['equivalence'] = 549;
					t['approxequal'] = 549;
					t['ellipsis'] = 1000;
					t['arrowvertex'] = 603;
					t['arrowhorizex'] = 1000;
					t['carriagereturn'] = 658;
					t['aleph'] = 823;
					t['Ifraktur'] = 686;
					t['Rfraktur'] = 795;
					t['weierstrass'] = 987;
					t['circlemultiply'] = 768;
					t['circleplus'] = 768;
					t['emptyset'] = 823;
					t['intersection'] = 768;
					t['union'] = 768;
					t['propersuperset'] = 713;
					t['reflexsuperset'] = 713;
					t['notsubset'] = 713;
					t['propersubset'] = 713;
					t['reflexsubset'] = 713;
					t['element'] = 713;
					t['notelement'] = 713;
					t['angle'] = 768;
					t['gradient'] = 713;
					t['registerserif'] = 790;
					t['copyrightserif'] = 790;
					t['trademarkserif'] = 890;
					t['product'] = 823;
					t['radical'] = 549;
					t['dotmath'] = 250;
					t['logicalnot'] = 713;
					t['logicaland'] = 603;
					t['logicalor'] = 603;
					t['arrowdblboth'] = 1042;
					t['arrowdblleft'] = 987;
					t['arrowdblup'] = 603;
					t['arrowdblright'] = 987;
					t['arrowdbldown'] = 603;
					t['lozenge'] = 494;
					t['angleleft'] = 329;
					t['registersans'] = 790;
					t['copyrightsans'] = 790;
					t['trademarksans'] = 786;
					t['summation'] = 713;
					t['parenlefttp'] = 384;
					t['parenleftex'] = 384;
					t['parenleftbt'] = 384;
					t['bracketlefttp'] = 384;
					t['bracketleftex'] = 384;
					t['bracketleftbt'] = 384;
					t['bracelefttp'] = 494;
					t['braceleftmid'] = 494;
					t['braceleftbt'] = 494;
					t['braceex'] = 494;
					t['angleright'] = 329;
					t['integral'] = 274;
					t['integraltp'] = 686;
					t['integralex'] = 686;
					t['integralbt'] = 686;
					t['parenrighttp'] = 384;
					t['parenrightex'] = 384;
					t['parenrightbt'] = 384;
					t['bracketrighttp'] = 384;
					t['bracketrightex'] = 384;
					t['bracketrightbt'] = 384;
					t['bracerighttp'] = 494;
					t['bracerightmid'] = 494;
					t['bracerightbt'] = 494;
					t['apple'] = 790;
				});
				t['Times-Roman'] = getLookupTableFactory(function (t) {
					t['space'] = 250;
					t['exclam'] = 333;
					t['quotedbl'] = 408;
					t['numbersign'] = 500;
					t['dollar'] = 500;
					t['percent'] = 833;
					t['ampersand'] = 778;
					t['quoteright'] = 333;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 500;
					t['plus'] = 564;
					t['comma'] = 250;
					t['hyphen'] = 333;
					t['period'] = 250;
					t['slash'] = 278;
					t['zero'] = 500;
					t['one'] = 500;
					t['two'] = 500;
					t['three'] = 500;
					t['four'] = 500;
					t['five'] = 500;
					t['six'] = 500;
					t['seven'] = 500;
					t['eight'] = 500;
					t['nine'] = 500;
					t['colon'] = 278;
					t['semicolon'] = 278;
					t['less'] = 564;
					t['equal'] = 564;
					t['greater'] = 564;
					t['question'] = 444;
					t['at'] = 921;
					t['A'] = 722;
					t['B'] = 667;
					t['C'] = 667;
					t['D'] = 722;
					t['E'] = 611;
					t['F'] = 556;
					t['G'] = 722;
					t['H'] = 722;
					t['I'] = 333;
					t['J'] = 389;
					t['K'] = 722;
					t['L'] = 611;
					t['M'] = 889;
					t['N'] = 722;
					t['O'] = 722;
					t['P'] = 556;
					t['Q'] = 722;
					t['R'] = 667;
					t['S'] = 556;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 722;
					t['W'] = 944;
					t['X'] = 722;
					t['Y'] = 722;
					t['Z'] = 611;
					t['bracketleft'] = 333;
					t['backslash'] = 278;
					t['bracketright'] = 333;
					t['asciicircum'] = 469;
					t['underscore'] = 500;
					t['quoteleft'] = 333;
					t['a'] = 444;
					t['b'] = 500;
					t['c'] = 444;
					t['d'] = 500;
					t['e'] = 444;
					t['f'] = 333;
					t['g'] = 500;
					t['h'] = 500;
					t['i'] = 278;
					t['j'] = 278;
					t['k'] = 500;
					t['l'] = 278;
					t['m'] = 778;
					t['n'] = 500;
					t['o'] = 500;
					t['p'] = 500;
					t['q'] = 500;
					t['r'] = 333;
					t['s'] = 389;
					t['t'] = 278;
					t['u'] = 500;
					t['v'] = 500;
					t['w'] = 722;
					t['x'] = 500;
					t['y'] = 500;
					t['z'] = 444;
					t['braceleft'] = 480;
					t['bar'] = 200;
					t['braceright'] = 480;
					t['asciitilde'] = 541;
					t['exclamdown'] = 333;
					t['cent'] = 500;
					t['sterling'] = 500;
					t['fraction'] = 167;
					t['yen'] = 500;
					t['florin'] = 500;
					t['section'] = 500;
					t['currency'] = 500;
					t['quotesingle'] = 180;
					t['quotedblleft'] = 444;
					t['guillemotleft'] = 500;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 556;
					t['fl'] = 556;
					t['endash'] = 500;
					t['dagger'] = 500;
					t['daggerdbl'] = 500;
					t['periodcentered'] = 250;
					t['paragraph'] = 453;
					t['bullet'] = 350;
					t['quotesinglbase'] = 333;
					t['quotedblbase'] = 444;
					t['quotedblright'] = 444;
					t['guillemotright'] = 500;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 444;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 889;
					t['ordfeminine'] = 276;
					t['Lslash'] = 611;
					t['Oslash'] = 722;
					t['OE'] = 889;
					t['ordmasculine'] = 310;
					t['ae'] = 667;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 500;
					t['oe'] = 722;
					t['germandbls'] = 500;
					t['Idieresis'] = 333;
					t['eacute'] = 444;
					t['abreve'] = 444;
					t['uhungarumlaut'] = 500;
					t['ecaron'] = 444;
					t['Ydieresis'] = 722;
					t['divide'] = 564;
					t['Yacute'] = 722;
					t['Acircumflex'] = 722;
					t['aacute'] = 444;
					t['Ucircumflex'] = 722;
					t['yacute'] = 500;
					t['scommaaccent'] = 389;
					t['ecircumflex'] = 444;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 444;
					t['Uacute'] = 722;
					t['uogonek'] = 500;
					t['Edieresis'] = 611;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 760;
					t['Emacron'] = 611;
					t['ccaron'] = 444;
					t['aring'] = 444;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 278;
					t['agrave'] = 444;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 667;
					t['atilde'] = 444;
					t['Edotaccent'] = 611;
					t['scaron'] = 389;
					t['scedilla'] = 389;
					t['iacute'] = 278;
					t['lozenge'] = 471;
					t['Rcaron'] = 667;
					t['Gcommaaccent'] = 722;
					t['ucircumflex'] = 500;
					t['acircumflex'] = 444;
					t['Amacron'] = 722;
					t['rcaron'] = 333;
					t['ccedilla'] = 444;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 556;
					t['Omacron'] = 722;
					t['Racute'] = 667;
					t['Sacute'] = 556;
					t['dcaron'] = 588;
					t['Umacron'] = 722;
					t['uring'] = 500;
					t['threesuperior'] = 300;
					t['Ograve'] = 722;
					t['Agrave'] = 722;
					t['Abreve'] = 722;
					t['multiply'] = 564;
					t['uacute'] = 500;
					t['Tcaron'] = 611;
					t['partialdiff'] = 476;
					t['ydieresis'] = 500;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 611;
					t['adieresis'] = 444;
					t['edieresis'] = 444;
					t['cacute'] = 444;
					t['nacute'] = 500;
					t['umacron'] = 500;
					t['Ncaron'] = 722;
					t['Iacute'] = 333;
					t['plusminus'] = 564;
					t['brokenbar'] = 200;
					t['registered'] = 760;
					t['Gbreve'] = 722;
					t['Idotaccent'] = 333;
					t['summation'] = 600;
					t['Egrave'] = 611;
					t['racute'] = 333;
					t['omacron'] = 500;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 667;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 326;
					t['eogonek'] = 444;
					t['Uogonek'] = 722;
					t['Aacute'] = 722;
					t['Adieresis'] = 722;
					t['egrave'] = 444;
					t['zacute'] = 444;
					t['iogonek'] = 278;
					t['Oacute'] = 722;
					t['oacute'] = 500;
					t['amacron'] = 444;
					t['sacute'] = 389;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 722;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 500;
					t['twosuperior'] = 300;
					t['Odieresis'] = 722;
					t['mu'] = 500;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 500;
					t['Eogonek'] = 611;
					t['dcroat'] = 500;
					t['threequarters'] = 750;
					t['Scedilla'] = 556;
					t['lcaron'] = 344;
					t['Kcommaaccent'] = 722;
					t['Lacute'] = 611;
					t['trademark'] = 980;
					t['edotaccent'] = 444;
					t['Igrave'] = 333;
					t['Imacron'] = 333;
					t['Lcaron'] = 611;
					t['onehalf'] = 750;
					t['lessequal'] = 549;
					t['ocircumflex'] = 500;
					t['ntilde'] = 500;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 611;
					t['emacron'] = 444;
					t['gbreve'] = 500;
					t['onequarter'] = 750;
					t['Scaron'] = 556;
					t['Scommaaccent'] = 556;
					t['Ohungarumlaut'] = 722;
					t['degree'] = 400;
					t['ograve'] = 500;
					t['Ccaron'] = 667;
					t['ugrave'] = 500;
					t['radical'] = 453;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 333;
					t['Ntilde'] = 722;
					t['otilde'] = 500;
					t['Rcommaaccent'] = 667;
					t['Lcommaaccent'] = 611;
					t['Atilde'] = 722;
					t['Aogonek'] = 722;
					t['Aring'] = 722;
					t['Otilde'] = 722;
					t['zdotaccent'] = 444;
					t['Ecaron'] = 611;
					t['Iogonek'] = 333;
					t['kcommaaccent'] = 500;
					t['minus'] = 564;
					t['Icircumflex'] = 333;
					t['ncaron'] = 500;
					t['tcommaaccent'] = 278;
					t['logicalnot'] = 564;
					t['odieresis'] = 500;
					t['udieresis'] = 500;
					t['notequal'] = 549;
					t['gcommaaccent'] = 500;
					t['eth'] = 500;
					t['zcaron'] = 444;
					t['ncommaaccent'] = 500;
					t['onesuperior'] = 300;
					t['imacron'] = 278;
					t['Euro'] = 500;
				});
				t['Times-Bold'] = getLookupTableFactory(function (t) {
					t['space'] = 250;
					t['exclam'] = 333;
					t['quotedbl'] = 555;
					t['numbersign'] = 500;
					t['dollar'] = 500;
					t['percent'] = 1000;
					t['ampersand'] = 833;
					t['quoteright'] = 333;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 500;
					t['plus'] = 570;
					t['comma'] = 250;
					t['hyphen'] = 333;
					t['period'] = 250;
					t['slash'] = 278;
					t['zero'] = 500;
					t['one'] = 500;
					t['two'] = 500;
					t['three'] = 500;
					t['four'] = 500;
					t['five'] = 500;
					t['six'] = 500;
					t['seven'] = 500;
					t['eight'] = 500;
					t['nine'] = 500;
					t['colon'] = 333;
					t['semicolon'] = 333;
					t['less'] = 570;
					t['equal'] = 570;
					t['greater'] = 570;
					t['question'] = 500;
					t['at'] = 930;
					t['A'] = 722;
					t['B'] = 667;
					t['C'] = 722;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 611;
					t['G'] = 778;
					t['H'] = 778;
					t['I'] = 389;
					t['J'] = 500;
					t['K'] = 778;
					t['L'] = 667;
					t['M'] = 944;
					t['N'] = 722;
					t['O'] = 778;
					t['P'] = 611;
					t['Q'] = 778;
					t['R'] = 722;
					t['S'] = 556;
					t['T'] = 667;
					t['U'] = 722;
					t['V'] = 722;
					t['W'] = 1000;
					t['X'] = 722;
					t['Y'] = 722;
					t['Z'] = 667;
					t['bracketleft'] = 333;
					t['backslash'] = 278;
					t['bracketright'] = 333;
					t['asciicircum'] = 581;
					t['underscore'] = 500;
					t['quoteleft'] = 333;
					t['a'] = 500;
					t['b'] = 556;
					t['c'] = 444;
					t['d'] = 556;
					t['e'] = 444;
					t['f'] = 333;
					t['g'] = 500;
					t['h'] = 556;
					t['i'] = 278;
					t['j'] = 333;
					t['k'] = 556;
					t['l'] = 278;
					t['m'] = 833;
					t['n'] = 556;
					t['o'] = 500;
					t['p'] = 556;
					t['q'] = 556;
					t['r'] = 444;
					t['s'] = 389;
					t['t'] = 333;
					t['u'] = 556;
					t['v'] = 500;
					t['w'] = 722;
					t['x'] = 500;
					t['y'] = 500;
					t['z'] = 444;
					t['braceleft'] = 394;
					t['bar'] = 220;
					t['braceright'] = 394;
					t['asciitilde'] = 520;
					t['exclamdown'] = 333;
					t['cent'] = 500;
					t['sterling'] = 500;
					t['fraction'] = 167;
					t['yen'] = 500;
					t['florin'] = 500;
					t['section'] = 500;
					t['currency'] = 500;
					t['quotesingle'] = 278;
					t['quotedblleft'] = 500;
					t['guillemotleft'] = 500;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 556;
					t['fl'] = 556;
					t['endash'] = 500;
					t['dagger'] = 500;
					t['daggerdbl'] = 500;
					t['periodcentered'] = 250;
					t['paragraph'] = 540;
					t['bullet'] = 350;
					t['quotesinglbase'] = 333;
					t['quotedblbase'] = 500;
					t['quotedblright'] = 500;
					t['guillemotright'] = 500;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 500;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 1000;
					t['ordfeminine'] = 300;
					t['Lslash'] = 667;
					t['Oslash'] = 778;
					t['OE'] = 1000;
					t['ordmasculine'] = 330;
					t['ae'] = 722;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 500;
					t['oe'] = 722;
					t['germandbls'] = 556;
					t['Idieresis'] = 389;
					t['eacute'] = 444;
					t['abreve'] = 500;
					t['uhungarumlaut'] = 556;
					t['ecaron'] = 444;
					t['Ydieresis'] = 722;
					t['divide'] = 570;
					t['Yacute'] = 722;
					t['Acircumflex'] = 722;
					t['aacute'] = 500;
					t['Ucircumflex'] = 722;
					t['yacute'] = 500;
					t['scommaaccent'] = 389;
					t['ecircumflex'] = 444;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 500;
					t['Uacute'] = 722;
					t['uogonek'] = 556;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 747;
					t['Emacron'] = 667;
					t['ccaron'] = 444;
					t['aring'] = 500;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 278;
					t['agrave'] = 500;
					t['Tcommaaccent'] = 667;
					t['Cacute'] = 722;
					t['atilde'] = 500;
					t['Edotaccent'] = 667;
					t['scaron'] = 389;
					t['scedilla'] = 389;
					t['iacute'] = 278;
					t['lozenge'] = 494;
					t['Rcaron'] = 722;
					t['Gcommaaccent'] = 778;
					t['ucircumflex'] = 556;
					t['acircumflex'] = 500;
					t['Amacron'] = 722;
					t['rcaron'] = 444;
					t['ccedilla'] = 444;
					t['Zdotaccent'] = 667;
					t['Thorn'] = 611;
					t['Omacron'] = 778;
					t['Racute'] = 722;
					t['Sacute'] = 556;
					t['dcaron'] = 672;
					t['Umacron'] = 722;
					t['uring'] = 556;
					t['threesuperior'] = 300;
					t['Ograve'] = 778;
					t['Agrave'] = 722;
					t['Abreve'] = 722;
					t['multiply'] = 570;
					t['uacute'] = 556;
					t['Tcaron'] = 667;
					t['partialdiff'] = 494;
					t['ydieresis'] = 500;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 500;
					t['edieresis'] = 444;
					t['cacute'] = 444;
					t['nacute'] = 556;
					t['umacron'] = 556;
					t['Ncaron'] = 722;
					t['Iacute'] = 389;
					t['plusminus'] = 570;
					t['brokenbar'] = 220;
					t['registered'] = 747;
					t['Gbreve'] = 778;
					t['Idotaccent'] = 389;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 444;
					t['omacron'] = 500;
					t['Zacute'] = 667;
					t['Zcaron'] = 667;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 722;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 416;
					t['eogonek'] = 444;
					t['Uogonek'] = 722;
					t['Aacute'] = 722;
					t['Adieresis'] = 722;
					t['egrave'] = 444;
					t['zacute'] = 444;
					t['iogonek'] = 278;
					t['Oacute'] = 778;
					t['oacute'] = 500;
					t['amacron'] = 500;
					t['sacute'] = 389;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 778;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 556;
					t['twosuperior'] = 300;
					t['Odieresis'] = 778;
					t['mu'] = 556;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 500;
					t['Eogonek'] = 667;
					t['dcroat'] = 556;
					t['threequarters'] = 750;
					t['Scedilla'] = 556;
					t['lcaron'] = 394;
					t['Kcommaaccent'] = 778;
					t['Lacute'] = 667;
					t['trademark'] = 1000;
					t['edotaccent'] = 444;
					t['Igrave'] = 389;
					t['Imacron'] = 389;
					t['Lcaron'] = 667;
					t['onehalf'] = 750;
					t['lessequal'] = 549;
					t['ocircumflex'] = 500;
					t['ntilde'] = 556;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 444;
					t['gbreve'] = 500;
					t['onequarter'] = 750;
					t['Scaron'] = 556;
					t['Scommaaccent'] = 556;
					t['Ohungarumlaut'] = 778;
					t['degree'] = 400;
					t['ograve'] = 500;
					t['Ccaron'] = 722;
					t['ugrave'] = 556;
					t['radical'] = 549;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 444;
					t['Ntilde'] = 722;
					t['otilde'] = 500;
					t['Rcommaaccent'] = 722;
					t['Lcommaaccent'] = 667;
					t['Atilde'] = 722;
					t['Aogonek'] = 722;
					t['Aring'] = 722;
					t['Otilde'] = 778;
					t['zdotaccent'] = 444;
					t['Ecaron'] = 667;
					t['Iogonek'] = 389;
					t['kcommaaccent'] = 556;
					t['minus'] = 570;
					t['Icircumflex'] = 389;
					t['ncaron'] = 556;
					t['tcommaaccent'] = 333;
					t['logicalnot'] = 570;
					t['odieresis'] = 500;
					t['udieresis'] = 556;
					t['notequal'] = 549;
					t['gcommaaccent'] = 500;
					t['eth'] = 500;
					t['zcaron'] = 444;
					t['ncommaaccent'] = 556;
					t['onesuperior'] = 300;
					t['imacron'] = 278;
					t['Euro'] = 500;
				});
				t['Times-BoldItalic'] = getLookupTableFactory(function (t) {
					t['space'] = 250;
					t['exclam'] = 389;
					t['quotedbl'] = 555;
					t['numbersign'] = 500;
					t['dollar'] = 500;
					t['percent'] = 833;
					t['ampersand'] = 778;
					t['quoteright'] = 333;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 500;
					t['plus'] = 570;
					t['comma'] = 250;
					t['hyphen'] = 333;
					t['period'] = 250;
					t['slash'] = 278;
					t['zero'] = 500;
					t['one'] = 500;
					t['two'] = 500;
					t['three'] = 500;
					t['four'] = 500;
					t['five'] = 500;
					t['six'] = 500;
					t['seven'] = 500;
					t['eight'] = 500;
					t['nine'] = 500;
					t['colon'] = 333;
					t['semicolon'] = 333;
					t['less'] = 570;
					t['equal'] = 570;
					t['greater'] = 570;
					t['question'] = 500;
					t['at'] = 832;
					t['A'] = 667;
					t['B'] = 667;
					t['C'] = 667;
					t['D'] = 722;
					t['E'] = 667;
					t['F'] = 667;
					t['G'] = 722;
					t['H'] = 778;
					t['I'] = 389;
					t['J'] = 500;
					t['K'] = 667;
					t['L'] = 611;
					t['M'] = 889;
					t['N'] = 722;
					t['O'] = 722;
					t['P'] = 611;
					t['Q'] = 722;
					t['R'] = 667;
					t['S'] = 556;
					t['T'] = 611;
					t['U'] = 722;
					t['V'] = 667;
					t['W'] = 889;
					t['X'] = 667;
					t['Y'] = 611;
					t['Z'] = 611;
					t['bracketleft'] = 333;
					t['backslash'] = 278;
					t['bracketright'] = 333;
					t['asciicircum'] = 570;
					t['underscore'] = 500;
					t['quoteleft'] = 333;
					t['a'] = 500;
					t['b'] = 500;
					t['c'] = 444;
					t['d'] = 500;
					t['e'] = 444;
					t['f'] = 333;
					t['g'] = 500;
					t['h'] = 556;
					t['i'] = 278;
					t['j'] = 278;
					t['k'] = 500;
					t['l'] = 278;
					t['m'] = 778;
					t['n'] = 556;
					t['o'] = 500;
					t['p'] = 500;
					t['q'] = 500;
					t['r'] = 389;
					t['s'] = 389;
					t['t'] = 278;
					t['u'] = 556;
					t['v'] = 444;
					t['w'] = 667;
					t['x'] = 500;
					t['y'] = 444;
					t['z'] = 389;
					t['braceleft'] = 348;
					t['bar'] = 220;
					t['braceright'] = 348;
					t['asciitilde'] = 570;
					t['exclamdown'] = 389;
					t['cent'] = 500;
					t['sterling'] = 500;
					t['fraction'] = 167;
					t['yen'] = 500;
					t['florin'] = 500;
					t['section'] = 500;
					t['currency'] = 500;
					t['quotesingle'] = 278;
					t['quotedblleft'] = 500;
					t['guillemotleft'] = 500;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 556;
					t['fl'] = 556;
					t['endash'] = 500;
					t['dagger'] = 500;
					t['daggerdbl'] = 500;
					t['periodcentered'] = 250;
					t['paragraph'] = 500;
					t['bullet'] = 350;
					t['quotesinglbase'] = 333;
					t['quotedblbase'] = 500;
					t['quotedblright'] = 500;
					t['guillemotright'] = 500;
					t['ellipsis'] = 1000;
					t['perthousand'] = 1000;
					t['questiondown'] = 500;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 1000;
					t['AE'] = 944;
					t['ordfeminine'] = 266;
					t['Lslash'] = 611;
					t['Oslash'] = 722;
					t['OE'] = 944;
					t['ordmasculine'] = 300;
					t['ae'] = 722;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 500;
					t['oe'] = 722;
					t['germandbls'] = 500;
					t['Idieresis'] = 389;
					t['eacute'] = 444;
					t['abreve'] = 500;
					t['uhungarumlaut'] = 556;
					t['ecaron'] = 444;
					t['Ydieresis'] = 611;
					t['divide'] = 570;
					t['Yacute'] = 611;
					t['Acircumflex'] = 667;
					t['aacute'] = 500;
					t['Ucircumflex'] = 722;
					t['yacute'] = 444;
					t['scommaaccent'] = 389;
					t['ecircumflex'] = 444;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 500;
					t['Uacute'] = 722;
					t['uogonek'] = 556;
					t['Edieresis'] = 667;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 747;
					t['Emacron'] = 667;
					t['ccaron'] = 444;
					t['aring'] = 500;
					t['Ncommaaccent'] = 722;
					t['lacute'] = 278;
					t['agrave'] = 500;
					t['Tcommaaccent'] = 611;
					t['Cacute'] = 667;
					t['atilde'] = 500;
					t['Edotaccent'] = 667;
					t['scaron'] = 389;
					t['scedilla'] = 389;
					t['iacute'] = 278;
					t['lozenge'] = 494;
					t['Rcaron'] = 667;
					t['Gcommaaccent'] = 722;
					t['ucircumflex'] = 556;
					t['acircumflex'] = 500;
					t['Amacron'] = 667;
					t['rcaron'] = 389;
					t['ccedilla'] = 444;
					t['Zdotaccent'] = 611;
					t['Thorn'] = 611;
					t['Omacron'] = 722;
					t['Racute'] = 667;
					t['Sacute'] = 556;
					t['dcaron'] = 608;
					t['Umacron'] = 722;
					t['uring'] = 556;
					t['threesuperior'] = 300;
					t['Ograve'] = 722;
					t['Agrave'] = 667;
					t['Abreve'] = 667;
					t['multiply'] = 570;
					t['uacute'] = 556;
					t['Tcaron'] = 611;
					t['partialdiff'] = 494;
					t['ydieresis'] = 444;
					t['Nacute'] = 722;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 667;
					t['adieresis'] = 500;
					t['edieresis'] = 444;
					t['cacute'] = 444;
					t['nacute'] = 556;
					t['umacron'] = 556;
					t['Ncaron'] = 722;
					t['Iacute'] = 389;
					t['plusminus'] = 570;
					t['brokenbar'] = 220;
					t['registered'] = 747;
					t['Gbreve'] = 722;
					t['Idotaccent'] = 389;
					t['summation'] = 600;
					t['Egrave'] = 667;
					t['racute'] = 389;
					t['omacron'] = 500;
					t['Zacute'] = 611;
					t['Zcaron'] = 611;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 667;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 366;
					t['eogonek'] = 444;
					t['Uogonek'] = 722;
					t['Aacute'] = 667;
					t['Adieresis'] = 667;
					t['egrave'] = 444;
					t['zacute'] = 389;
					t['iogonek'] = 278;
					t['Oacute'] = 722;
					t['oacute'] = 500;
					t['amacron'] = 500;
					t['sacute'] = 389;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 722;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 500;
					t['twosuperior'] = 300;
					t['Odieresis'] = 722;
					t['mu'] = 576;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 500;
					t['Eogonek'] = 667;
					t['dcroat'] = 500;
					t['threequarters'] = 750;
					t['Scedilla'] = 556;
					t['lcaron'] = 382;
					t['Kcommaaccent'] = 667;
					t['Lacute'] = 611;
					t['trademark'] = 1000;
					t['edotaccent'] = 444;
					t['Igrave'] = 389;
					t['Imacron'] = 389;
					t['Lcaron'] = 611;
					t['onehalf'] = 750;
					t['lessequal'] = 549;
					t['ocircumflex'] = 500;
					t['ntilde'] = 556;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 667;
					t['emacron'] = 444;
					t['gbreve'] = 500;
					t['onequarter'] = 750;
					t['Scaron'] = 556;
					t['Scommaaccent'] = 556;
					t['Ohungarumlaut'] = 722;
					t['degree'] = 400;
					t['ograve'] = 500;
					t['Ccaron'] = 667;
					t['ugrave'] = 556;
					t['radical'] = 549;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 389;
					t['Ntilde'] = 722;
					t['otilde'] = 500;
					t['Rcommaaccent'] = 667;
					t['Lcommaaccent'] = 611;
					t['Atilde'] = 667;
					t['Aogonek'] = 667;
					t['Aring'] = 667;
					t['Otilde'] = 722;
					t['zdotaccent'] = 389;
					t['Ecaron'] = 667;
					t['Iogonek'] = 389;
					t['kcommaaccent'] = 500;
					t['minus'] = 606;
					t['Icircumflex'] = 389;
					t['ncaron'] = 556;
					t['tcommaaccent'] = 278;
					t['logicalnot'] = 606;
					t['odieresis'] = 500;
					t['udieresis'] = 556;
					t['notequal'] = 549;
					t['gcommaaccent'] = 500;
					t['eth'] = 500;
					t['zcaron'] = 389;
					t['ncommaaccent'] = 556;
					t['onesuperior'] = 300;
					t['imacron'] = 278;
					t['Euro'] = 500;
				});
				t['Times-Italic'] = getLookupTableFactory(function (t) {
					t['space'] = 250;
					t['exclam'] = 333;
					t['quotedbl'] = 420;
					t['numbersign'] = 500;
					t['dollar'] = 500;
					t['percent'] = 833;
					t['ampersand'] = 778;
					t['quoteright'] = 333;
					t['parenleft'] = 333;
					t['parenright'] = 333;
					t['asterisk'] = 500;
					t['plus'] = 675;
					t['comma'] = 250;
					t['hyphen'] = 333;
					t['period'] = 250;
					t['slash'] = 278;
					t['zero'] = 500;
					t['one'] = 500;
					t['two'] = 500;
					t['three'] = 500;
					t['four'] = 500;
					t['five'] = 500;
					t['six'] = 500;
					t['seven'] = 500;
					t['eight'] = 500;
					t['nine'] = 500;
					t['colon'] = 333;
					t['semicolon'] = 333;
					t['less'] = 675;
					t['equal'] = 675;
					t['greater'] = 675;
					t['question'] = 500;
					t['at'] = 920;
					t['A'] = 611;
					t['B'] = 611;
					t['C'] = 667;
					t['D'] = 722;
					t['E'] = 611;
					t['F'] = 611;
					t['G'] = 722;
					t['H'] = 722;
					t['I'] = 333;
					t['J'] = 444;
					t['K'] = 667;
					t['L'] = 556;
					t['M'] = 833;
					t['N'] = 667;
					t['O'] = 722;
					t['P'] = 611;
					t['Q'] = 722;
					t['R'] = 611;
					t['S'] = 500;
					t['T'] = 556;
					t['U'] = 722;
					t['V'] = 611;
					t['W'] = 833;
					t['X'] = 611;
					t['Y'] = 556;
					t['Z'] = 556;
					t['bracketleft'] = 389;
					t['backslash'] = 278;
					t['bracketright'] = 389;
					t['asciicircum'] = 422;
					t['underscore'] = 500;
					t['quoteleft'] = 333;
					t['a'] = 500;
					t['b'] = 500;
					t['c'] = 444;
					t['d'] = 500;
					t['e'] = 444;
					t['f'] = 278;
					t['g'] = 500;
					t['h'] = 500;
					t['i'] = 278;
					t['j'] = 278;
					t['k'] = 444;
					t['l'] = 278;
					t['m'] = 722;
					t['n'] = 500;
					t['o'] = 500;
					t['p'] = 500;
					t['q'] = 500;
					t['r'] = 389;
					t['s'] = 389;
					t['t'] = 278;
					t['u'] = 500;
					t['v'] = 444;
					t['w'] = 667;
					t['x'] = 444;
					t['y'] = 444;
					t['z'] = 389;
					t['braceleft'] = 400;
					t['bar'] = 275;
					t['braceright'] = 400;
					t['asciitilde'] = 541;
					t['exclamdown'] = 389;
					t['cent'] = 500;
					t['sterling'] = 500;
					t['fraction'] = 167;
					t['yen'] = 500;
					t['florin'] = 500;
					t['section'] = 500;
					t['currency'] = 500;
					t['quotesingle'] = 214;
					t['quotedblleft'] = 556;
					t['guillemotleft'] = 500;
					t['guilsinglleft'] = 333;
					t['guilsinglright'] = 333;
					t['fi'] = 500;
					t['fl'] = 500;
					t['endash'] = 500;
					t['dagger'] = 500;
					t['daggerdbl'] = 500;
					t['periodcentered'] = 250;
					t['paragraph'] = 523;
					t['bullet'] = 350;
					t['quotesinglbase'] = 333;
					t['quotedblbase'] = 556;
					t['quotedblright'] = 556;
					t['guillemotright'] = 500;
					t['ellipsis'] = 889;
					t['perthousand'] = 1000;
					t['questiondown'] = 500;
					t['grave'] = 333;
					t['acute'] = 333;
					t['circumflex'] = 333;
					t['tilde'] = 333;
					t['macron'] = 333;
					t['breve'] = 333;
					t['dotaccent'] = 333;
					t['dieresis'] = 333;
					t['ring'] = 333;
					t['cedilla'] = 333;
					t['hungarumlaut'] = 333;
					t['ogonek'] = 333;
					t['caron'] = 333;
					t['emdash'] = 889;
					t['AE'] = 889;
					t['ordfeminine'] = 276;
					t['Lslash'] = 556;
					t['Oslash'] = 722;
					t['OE'] = 944;
					t['ordmasculine'] = 310;
					t['ae'] = 667;
					t['dotlessi'] = 278;
					t['lslash'] = 278;
					t['oslash'] = 500;
					t['oe'] = 667;
					t['germandbls'] = 500;
					t['Idieresis'] = 333;
					t['eacute'] = 444;
					t['abreve'] = 500;
					t['uhungarumlaut'] = 500;
					t['ecaron'] = 444;
					t['Ydieresis'] = 556;
					t['divide'] = 675;
					t['Yacute'] = 556;
					t['Acircumflex'] = 611;
					t['aacute'] = 500;
					t['Ucircumflex'] = 722;
					t['yacute'] = 444;
					t['scommaaccent'] = 389;
					t['ecircumflex'] = 444;
					t['Uring'] = 722;
					t['Udieresis'] = 722;
					t['aogonek'] = 500;
					t['Uacute'] = 722;
					t['uogonek'] = 500;
					t['Edieresis'] = 611;
					t['Dcroat'] = 722;
					t['commaaccent'] = 250;
					t['copyright'] = 760;
					t['Emacron'] = 611;
					t['ccaron'] = 444;
					t['aring'] = 500;
					t['Ncommaaccent'] = 667;
					t['lacute'] = 278;
					t['agrave'] = 500;
					t['Tcommaaccent'] = 556;
					t['Cacute'] = 667;
					t['atilde'] = 500;
					t['Edotaccent'] = 611;
					t['scaron'] = 389;
					t['scedilla'] = 389;
					t['iacute'] = 278;
					t['lozenge'] = 471;
					t['Rcaron'] = 611;
					t['Gcommaaccent'] = 722;
					t['ucircumflex'] = 500;
					t['acircumflex'] = 500;
					t['Amacron'] = 611;
					t['rcaron'] = 389;
					t['ccedilla'] = 444;
					t['Zdotaccent'] = 556;
					t['Thorn'] = 611;
					t['Omacron'] = 722;
					t['Racute'] = 611;
					t['Sacute'] = 500;
					t['dcaron'] = 544;
					t['Umacron'] = 722;
					t['uring'] = 500;
					t['threesuperior'] = 300;
					t['Ograve'] = 722;
					t['Agrave'] = 611;
					t['Abreve'] = 611;
					t['multiply'] = 675;
					t['uacute'] = 500;
					t['Tcaron'] = 556;
					t['partialdiff'] = 476;
					t['ydieresis'] = 444;
					t['Nacute'] = 667;
					t['icircumflex'] = 278;
					t['Ecircumflex'] = 611;
					t['adieresis'] = 500;
					t['edieresis'] = 444;
					t['cacute'] = 444;
					t['nacute'] = 500;
					t['umacron'] = 500;
					t['Ncaron'] = 667;
					t['Iacute'] = 333;
					t['plusminus'] = 675;
					t['brokenbar'] = 275;
					t['registered'] = 760;
					t['Gbreve'] = 722;
					t['Idotaccent'] = 333;
					t['summation'] = 600;
					t['Egrave'] = 611;
					t['racute'] = 389;
					t['omacron'] = 500;
					t['Zacute'] = 556;
					t['Zcaron'] = 556;
					t['greaterequal'] = 549;
					t['Eth'] = 722;
					t['Ccedilla'] = 667;
					t['lcommaaccent'] = 278;
					t['tcaron'] = 300;
					t['eogonek'] = 444;
					t['Uogonek'] = 722;
					t['Aacute'] = 611;
					t['Adieresis'] = 611;
					t['egrave'] = 444;
					t['zacute'] = 389;
					t['iogonek'] = 278;
					t['Oacute'] = 722;
					t['oacute'] = 500;
					t['amacron'] = 500;
					t['sacute'] = 389;
					t['idieresis'] = 278;
					t['Ocircumflex'] = 722;
					t['Ugrave'] = 722;
					t['Delta'] = 612;
					t['thorn'] = 500;
					t['twosuperior'] = 300;
					t['Odieresis'] = 722;
					t['mu'] = 500;
					t['igrave'] = 278;
					t['ohungarumlaut'] = 500;
					t['Eogonek'] = 611;
					t['dcroat'] = 500;
					t['threequarters'] = 750;
					t['Scedilla'] = 500;
					t['lcaron'] = 300;
					t['Kcommaaccent'] = 667;
					t['Lacute'] = 556;
					t['trademark'] = 980;
					t['edotaccent'] = 444;
					t['Igrave'] = 333;
					t['Imacron'] = 333;
					t['Lcaron'] = 611;
					t['onehalf'] = 750;
					t['lessequal'] = 549;
					t['ocircumflex'] = 500;
					t['ntilde'] = 500;
					t['Uhungarumlaut'] = 722;
					t['Eacute'] = 611;
					t['emacron'] = 444;
					t['gbreve'] = 500;
					t['onequarter'] = 750;
					t['Scaron'] = 500;
					t['Scommaaccent'] = 500;
					t['Ohungarumlaut'] = 722;
					t['degree'] = 400;
					t['ograve'] = 500;
					t['Ccaron'] = 667;
					t['ugrave'] = 500;
					t['radical'] = 453;
					t['Dcaron'] = 722;
					t['rcommaaccent'] = 389;
					t['Ntilde'] = 667;
					t['otilde'] = 500;
					t['Rcommaaccent'] = 611;
					t['Lcommaaccent'] = 556;
					t['Atilde'] = 611;
					t['Aogonek'] = 611;
					t['Aring'] = 611;
					t['Otilde'] = 722;
					t['zdotaccent'] = 389;
					t['Ecaron'] = 611;
					t['Iogonek'] = 333;
					t['kcommaaccent'] = 444;
					t['minus'] = 675;
					t['Icircumflex'] = 333;
					t['ncaron'] = 500;
					t['tcommaaccent'] = 278;
					t['logicalnot'] = 675;
					t['odieresis'] = 500;
					t['udieresis'] = 500;
					t['notequal'] = 549;
					t['gcommaaccent'] = 500;
					t['eth'] = 500;
					t['zcaron'] = 389;
					t['ncommaaccent'] = 500;
					t['onesuperior'] = 300;
					t['imacron'] = 278;
					t['Euro'] = 500;
				});
				t['ZapfDingbats'] = getLookupTableFactory(function (t) {
					t['space'] = 278;
					t['a1'] = 974;
					t['a2'] = 961;
					t['a202'] = 974;
					t['a3'] = 980;
					t['a4'] = 719;
					t['a5'] = 789;
					t['a119'] = 790;
					t['a118'] = 791;
					t['a117'] = 690;
					t['a11'] = 960;
					t['a12'] = 939;
					t['a13'] = 549;
					t['a14'] = 855;
					t['a15'] = 911;
					t['a16'] = 933;
					t['a105'] = 911;
					t['a17'] = 945;
					t['a18'] = 974;
					t['a19'] = 755;
					t['a20'] = 846;
					t['a21'] = 762;
					t['a22'] = 761;
					t['a23'] = 571;
					t['a24'] = 677;
					t['a25'] = 763;
					t['a26'] = 760;
					t['a27'] = 759;
					t['a28'] = 754;
					t['a6'] = 494;
					t['a7'] = 552;
					t['a8'] = 537;
					t['a9'] = 577;
					t['a10'] = 692;
					t['a29'] = 786;
					t['a30'] = 788;
					t['a31'] = 788;
					t['a32'] = 790;
					t['a33'] = 793;
					t['a34'] = 794;
					t['a35'] = 816;
					t['a36'] = 823;
					t['a37'] = 789;
					t['a38'] = 841;
					t['a39'] = 823;
					t['a40'] = 833;
					t['a41'] = 816;
					t['a42'] = 831;
					t['a43'] = 923;
					t['a44'] = 744;
					t['a45'] = 723;
					t['a46'] = 749;
					t['a47'] = 790;
					t['a48'] = 792;
					t['a49'] = 695;
					t['a50'] = 776;
					t['a51'] = 768;
					t['a52'] = 792;
					t['a53'] = 759;
					t['a54'] = 707;
					t['a55'] = 708;
					t['a56'] = 682;
					t['a57'] = 701;
					t['a58'] = 826;
					t['a59'] = 815;
					t['a60'] = 789;
					t['a61'] = 789;
					t['a62'] = 707;
					t['a63'] = 687;
					t['a64'] = 696;
					t['a65'] = 689;
					t['a66'] = 786;
					t['a67'] = 787;
					t['a68'] = 713;
					t['a69'] = 791;
					t['a70'] = 785;
					t['a71'] = 791;
					t['a72'] = 873;
					t['a73'] = 761;
					t['a74'] = 762;
					t['a203'] = 762;
					t['a75'] = 759;
					t['a204'] = 759;
					t['a76'] = 892;
					t['a77'] = 892;
					t['a78'] = 788;
					t['a79'] = 784;
					t['a81'] = 438;
					t['a82'] = 138;
					t['a83'] = 277;
					t['a84'] = 415;
					t['a97'] = 392;
					t['a98'] = 392;
					t['a99'] = 668;
					t['a100'] = 668;
					t['a89'] = 390;
					t['a90'] = 390;
					t['a93'] = 317;
					t['a94'] = 317;
					t['a91'] = 276;
					t['a92'] = 276;
					t['a205'] = 509;
					t['a85'] = 509;
					t['a206'] = 410;
					t['a86'] = 410;
					t['a87'] = 234;
					t['a88'] = 234;
					t['a95'] = 334;
					t['a96'] = 334;
					t['a101'] = 732;
					t['a102'] = 544;
					t['a103'] = 544;
					t['a104'] = 910;
					t['a106'] = 667;
					t['a107'] = 760;
					t['a108'] = 760;
					t['a112'] = 776;
					t['a111'] = 595;
					t['a110'] = 694;
					t['a109'] = 626;
					t['a120'] = 788;
					t['a121'] = 788;
					t['a122'] = 788;
					t['a123'] = 788;
					t['a124'] = 788;
					t['a125'] = 788;
					t['a126'] = 788;
					t['a127'] = 788;
					t['a128'] = 788;
					t['a129'] = 788;
					t['a130'] = 788;
					t['a131'] = 788;
					t['a132'] = 788;
					t['a133'] = 788;
					t['a134'] = 788;
					t['a135'] = 788;
					t['a136'] = 788;
					t['a137'] = 788;
					t['a138'] = 788;
					t['a139'] = 788;
					t['a140'] = 788;
					t['a141'] = 788;
					t['a142'] = 788;
					t['a143'] = 788;
					t['a144'] = 788;
					t['a145'] = 788;
					t['a146'] = 788;
					t['a147'] = 788;
					t['a148'] = 788;
					t['a149'] = 788;
					t['a150'] = 788;
					t['a151'] = 788;
					t['a152'] = 788;
					t['a153'] = 788;
					t['a154'] = 788;
					t['a155'] = 788;
					t['a156'] = 788;
					t['a157'] = 788;
					t['a158'] = 788;
					t['a159'] = 788;
					t['a160'] = 894;
					t['a161'] = 838;
					t['a163'] = 1016;
					t['a164'] = 458;
					t['a196'] = 748;
					t['a165'] = 924;
					t['a192'] = 748;
					t['a166'] = 918;
					t['a167'] = 927;
					t['a168'] = 928;
					t['a169'] = 928;
					t['a170'] = 834;
					t['a171'] = 873;
					t['a172'] = 828;
					t['a173'] = 924;
					t['a162'] = 924;
					t['a174'] = 917;
					t['a175'] = 930;
					t['a176'] = 931;
					t['a177'] = 463;
					t['a178'] = 883;
					t['a179'] = 836;
					t['a193'] = 836;
					t['a180'] = 867;
					t['a199'] = 867;
					t['a181'] = 696;
					t['a200'] = 696;
					t['a182'] = 874;
					t['a201'] = 874;
					t['a183'] = 760;
					t['a184'] = 946;
					t['a197'] = 771;
					t['a185'] = 865;
					t['a194'] = 771;
					t['a198'] = 888;
					t['a186'] = 967;
					t['a195'] = 888;
					t['a187'] = 831;
					t['a188'] = 873;
					t['a189'] = 927;
					t['a190'] = 970;
					t['a191'] = 918;
				});
			});
			exports.getMetrics = getMetrics;

			/***/
		}),
/* 31 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var Uint32ArrayView = sharedUtil.Uint32ArrayView;
			var MurmurHash3_64 = function MurmurHash3_64Closure(seed) {
				var MASK_HIGH = 0xffff0000;
				var MASK_LOW = 0xffff;
				function MurmurHash3_64(seed) {
					var SEED = 0xc3d2e1f0;
					this.h1 = seed ? seed & 0xffffffff : SEED;
					this.h2 = seed ? seed & 0xffffffff : SEED;
				}
				var alwaysUseUint32ArrayView = false;
				try {
					new Uint32Array(new Uint8Array(5).buffer, 0, 1);
				} catch (e) {
					alwaysUseUint32ArrayView = true;
				}
				MurmurHash3_64.prototype = {
					update: function MurmurHash3_64_update(input) {
						var useUint32ArrayView = alwaysUseUint32ArrayView;
						var i;
						if (typeof input === 'string') {
							var data = new Uint8Array(input.length * 2);
							var length = 0;
							for (i = 0; i < input.length; i++) {
								var code = input.charCodeAt(i);
								if (code <= 0xff) {
									data[length++] = code;
								} else {
									data[length++] = code >>> 8;
									data[length++] = code & 0xff;
								}
							}
						} else if (input instanceof Uint8Array) {
							data = input;
							length = data.length;
						} else if (typeof input === 'object' && 'length' in input) {
							data = input;
							length = data.length;
							useUint32ArrayView = true;
						} else {
							throw new Error('Wrong data format in MurmurHash3_64_update. ' + 'Input must be a string or array.');
						}
						var blockCounts = length >> 2;
						var tailLength = length - blockCounts * 4;
						var dataUint32 = useUint32ArrayView ? new Uint32ArrayView(data, blockCounts) : new Uint32Array(data.buffer, 0, blockCounts);
						var k1 = 0;
						var k2 = 0;
						var h1 = this.h1;
						var h2 = this.h2;
						var C1 = 0xcc9e2d51;
						var C2 = 0x1b873593;
						var C1_LOW = C1 & MASK_LOW;
						var C2_LOW = C2 & MASK_LOW;
						for (i = 0; i < blockCounts; i++) {
							if (i & 1) {
								k1 = dataUint32[i];
								k1 = k1 * C1 & MASK_HIGH | k1 * C1_LOW & MASK_LOW;
								k1 = k1 << 15 | k1 >>> 17;
								k1 = k1 * C2 & MASK_HIGH | k1 * C2_LOW & MASK_LOW;
								h1 ^= k1;
								h1 = h1 << 13 | h1 >>> 19;
								h1 = h1 * 5 + 0xe6546b64;
							} else {
								k2 = dataUint32[i];
								k2 = k2 * C1 & MASK_HIGH | k2 * C1_LOW & MASK_LOW;
								k2 = k2 << 15 | k2 >>> 17;
								k2 = k2 * C2 & MASK_HIGH | k2 * C2_LOW & MASK_LOW;
								h2 ^= k2;
								h2 = h2 << 13 | h2 >>> 19;
								h2 = h2 * 5 + 0xe6546b64;
							}
						}
						k1 = 0;
						switch (tailLength) {
							case 3:
								k1 ^= data[blockCounts * 4 + 2] << 16;
							case 2:
								k1 ^= data[blockCounts * 4 + 1] << 8;
							case 1:
								k1 ^= data[blockCounts * 4];
								k1 = k1 * C1 & MASK_HIGH | k1 * C1_LOW & MASK_LOW;
								k1 = k1 << 15 | k1 >>> 17;
								k1 = k1 * C2 & MASK_HIGH | k1 * C2_LOW & MASK_LOW;
								if (blockCounts & 1) {
									h1 ^= k1;
								} else {
									h2 ^= k1;
								}
						}
						this.h1 = h1;
						this.h2 = h2;
						return this;
					},
					hexdigest: function MurmurHash3_64_hexdigest() {
						var h1 = this.h1;
						var h2 = this.h2;
						h1 ^= h2 >>> 1;
						h1 = h1 * 0xed558ccd & MASK_HIGH | h1 * 0x8ccd & MASK_LOW;
						h2 = h2 * 0xff51afd7 & MASK_HIGH | ((h2 << 16 | h1 >>> 16) * 0xafd7ed55 & MASK_HIGH) >>> 16;
						h1 ^= h2 >>> 1;
						h1 = h1 * 0x1a85ec53 & MASK_HIGH | h1 * 0xec53 & MASK_LOW;
						h2 = h2 * 0xc4ceb9fe & MASK_HIGH | ((h2 << 16 | h1 >>> 16) * 0xb9fe1a85 & MASK_HIGH) >>> 16;
						h1 ^= h2 >>> 1;
						for (var i = 0, arr = [
							h1,
							h2
						], str = ''; i < arr.length; i++) {
							var hex = (arr[i] >>> 0).toString(16);
							while (hex.length < 8) {
								hex = '0' + hex;
							}
							str += hex;
						}
						return str;
					}
				};
				return MurmurHash3_64;
			}();
			exports.MurmurHash3_64 = MurmurHash3_64;

			/***/
		}),
/* 32 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var coreFunction = __w_pdfjs_require__(6);
			var coreColorSpace = __w_pdfjs_require__(3);
			var UNSUPPORTED_FEATURES = sharedUtil.UNSUPPORTED_FEATURES;
			var MissingDataException = sharedUtil.MissingDataException;
			var Util = sharedUtil.Util;
			var assert = sharedUtil.assert;
			var error = sharedUtil.error;
			var info = sharedUtil.info;
			var warn = sharedUtil.warn;
			var isStream = corePrimitives.isStream;
			var PDFFunction = coreFunction.PDFFunction;
			var ColorSpace = coreColorSpace.ColorSpace;
			var ShadingType = {
				FUNCTION_BASED: 1,
				AXIAL: 2,
				RADIAL: 3,
				FREE_FORM_MESH: 4,
				LATTICE_FORM_MESH: 5,
				COONS_PATCH_MESH: 6,
				TENSOR_PATCH_MESH: 7
			};
			var Pattern = function PatternClosure() {
				function Pattern() {
					error('should not call Pattern constructor');
				}
				Pattern.prototype = {
					getPattern: function Pattern_getPattern(ctx) {
						error('Should not call Pattern.getStyle: ' + ctx);
					}
				};
				Pattern.parseShading = function Pattern_parseShading(shading, matrix, xref, res, handler) {
					var dict = isStream(shading) ? shading.dict : shading;
					var type = dict.get('ShadingType');
					try {
						switch (type) {
							case ShadingType.AXIAL:
							case ShadingType.RADIAL:
								return new Shadings.RadialAxial(dict, matrix, xref, res);
							case ShadingType.FREE_FORM_MESH:
							case ShadingType.LATTICE_FORM_MESH:
							case ShadingType.COONS_PATCH_MESH:
							case ShadingType.TENSOR_PATCH_MESH:
								return new Shadings.Mesh(shading, matrix, xref, res);
							default:
								throw new Error('Unsupported ShadingType: ' + type);
						}
					} catch (ex) {
						if (ex instanceof MissingDataException) {
							throw ex;
						}
						handler.send('UnsupportedFeature', { featureId: UNSUPPORTED_FEATURES.shadingPattern });
						warn(ex);
						return new Shadings.Dummy();
					}
				};
				return Pattern;
			}();
			var Shadings = {};
			Shadings.SMALL_NUMBER = 1e-6;
			Shadings.RadialAxial = function RadialAxialClosure() {
				function RadialAxial(dict, matrix, xref, res) {
					this.matrix = matrix;
					this.coordsArr = dict.getArray('Coords');
					this.shadingType = dict.get('ShadingType');
					this.type = 'Pattern';
					var cs = dict.get('ColorSpace', 'CS');
					cs = ColorSpace.parse(cs, xref, res);
					this.cs = cs;
					var t0 = 0.0, t1 = 1.0;
					if (dict.has('Domain')) {
						var domainArr = dict.getArray('Domain');
						t0 = domainArr[0];
						t1 = domainArr[1];
					}
					var extendStart = false, extendEnd = false;
					if (dict.has('Extend')) {
						var extendArr = dict.getArray('Extend');
						extendStart = extendArr[0];
						extendEnd = extendArr[1];
					}
					if (this.shadingType === ShadingType.RADIAL && (!extendStart || !extendEnd)) {
						var x1 = this.coordsArr[0];
						var y1 = this.coordsArr[1];
						var r1 = this.coordsArr[2];
						var x2 = this.coordsArr[3];
						var y2 = this.coordsArr[4];
						var r2 = this.coordsArr[5];
						var distance = Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
						if (r1 <= r2 + distance && r2 <= r1 + distance) {
							warn('Unsupported radial gradient.');
						}
					}
					this.extendStart = extendStart;
					this.extendEnd = extendEnd;
					var fnObj = dict.get('Function');
					var fn = PDFFunction.parseArray(xref, fnObj);
					var diff = t1 - t0;
					var step = diff / 10;
					var colorStops = this.colorStops = [];
					if (t0 >= t1 || step <= 0) {
						info('Bad shading domain.');
						return;
					}
					var color = new Float32Array(cs.numComps), ratio = new Float32Array(1);
					var rgbColor;
					for (var i = t0; i <= t1; i += step) {
						ratio[0] = i;
						fn(ratio, 0, color, 0);
						rgbColor = cs.getRgb(color, 0);
						var cssColor = Util.makeCssRgb(rgbColor[0], rgbColor[1], rgbColor[2]);
						colorStops.push([
							(i - t0) / diff,
							cssColor
						]);
					}
					var background = 'transparent';
					if (dict.has('Background')) {
						rgbColor = cs.getRgb(dict.get('Background'), 0);
						background = Util.makeCssRgb(rgbColor[0], rgbColor[1], rgbColor[2]);
					}
					if (!extendStart) {
						colorStops.unshift([
							0,
							background
						]);
						colorStops[1][0] += Shadings.SMALL_NUMBER;
					}
					if (!extendEnd) {
						colorStops[colorStops.length - 1][0] -= Shadings.SMALL_NUMBER;
						colorStops.push([
							1,
							background
						]);
					}
					this.colorStops = colorStops;
				}
				RadialAxial.prototype = {
					getIR: function RadialAxial_getIR() {
						var coordsArr = this.coordsArr;
						var shadingType = this.shadingType;
						var type, p0, p1, r0, r1;
						if (shadingType === ShadingType.AXIAL) {
							p0 = [
								coordsArr[0],
								coordsArr[1]
							];
							p1 = [
								coordsArr[2],
								coordsArr[3]
							];
							r0 = null;
							r1 = null;
							type = 'axial';
						} else if (shadingType === ShadingType.RADIAL) {
							p0 = [
								coordsArr[0],
								coordsArr[1]
							];
							p1 = [
								coordsArr[3],
								coordsArr[4]
							];
							r0 = coordsArr[2];
							r1 = coordsArr[5];
							type = 'radial';
						} else {
							error('getPattern type unknown: ' + shadingType);
						}
						var matrix = this.matrix;
						if (matrix) {
							p0 = Util.applyTransform(p0, matrix);
							p1 = Util.applyTransform(p1, matrix);
							if (shadingType === ShadingType.RADIAL) {
								var scale = Util.singularValueDecompose2dScale(matrix);
								r0 *= scale[0];
								r1 *= scale[1];
							}
						}
						return [
							'RadialAxial',
							type,
							this.colorStops,
							p0,
							p1,
							r0,
							r1
						];
					}
				};
				return RadialAxial;
			}();
			Shadings.Mesh = function MeshClosure() {
				function MeshStreamReader(stream, context) {
					this.stream = stream;
					this.context = context;
					this.buffer = 0;
					this.bufferLength = 0;
					var numComps = context.numComps;
					this.tmpCompsBuf = new Float32Array(numComps);
					var csNumComps = context.colorSpace.numComps;
					this.tmpCsCompsBuf = context.colorFn ? new Float32Array(csNumComps) : this.tmpCompsBuf;
				}
				MeshStreamReader.prototype = {
					get hasData() {
						if (this.stream.end) {
							return this.stream.pos < this.stream.end;
						}
						if (this.bufferLength > 0) {
							return true;
						}
						var nextByte = this.stream.getByte();
						if (nextByte < 0) {
							return false;
						}
						this.buffer = nextByte;
						this.bufferLength = 8;
						return true;
					},
					readBits: function MeshStreamReader_readBits(n) {
						var buffer = this.buffer;
						var bufferLength = this.bufferLength;
						if (n === 32) {
							if (bufferLength === 0) {
								return (this.stream.getByte() << 24 | this.stream.getByte() << 16 | this.stream.getByte() << 8 | this.stream.getByte()) >>> 0;
							}
							buffer = buffer << 24 | this.stream.getByte() << 16 | this.stream.getByte() << 8 | this.stream.getByte();
							var nextByte = this.stream.getByte();
							this.buffer = nextByte & (1 << bufferLength) - 1;
							return (buffer << 8 - bufferLength | (nextByte & 0xFF) >> bufferLength) >>> 0;
						}
						if (n === 8 && bufferLength === 0) {
							return this.stream.getByte();
						}
						while (bufferLength < n) {
							buffer = buffer << 8 | this.stream.getByte();
							bufferLength += 8;
						}
						bufferLength -= n;
						this.bufferLength = bufferLength;
						this.buffer = buffer & (1 << bufferLength) - 1;
						return buffer >> bufferLength;
					},
					align: function MeshStreamReader_align() {
						this.buffer = 0;
						this.bufferLength = 0;
					},
					readFlag: function MeshStreamReader_readFlag() {
						return this.readBits(this.context.bitsPerFlag);
					},
					readCoordinate: function MeshStreamReader_readCoordinate() {
						var bitsPerCoordinate = this.context.bitsPerCoordinate;
						var xi = this.readBits(bitsPerCoordinate);
						var yi = this.readBits(bitsPerCoordinate);
						var decode = this.context.decode;
						var scale = bitsPerCoordinate < 32 ? 1 / ((1 << bitsPerCoordinate) - 1) : 2.3283064365386963e-10;
						return [
							xi * scale * (decode[1] - decode[0]) + decode[0],
							yi * scale * (decode[3] - decode[2]) + decode[2]
						];
					},
					readComponents: function MeshStreamReader_readComponents() {
						var numComps = this.context.numComps;
						var bitsPerComponent = this.context.bitsPerComponent;
						var scale = bitsPerComponent < 32 ? 1 / ((1 << bitsPerComponent) - 1) : 2.3283064365386963e-10;
						var decode = this.context.decode;
						var components = this.tmpCompsBuf;
						for (var i = 0, j = 4; i < numComps; i++, j += 2) {
							var ci = this.readBits(bitsPerComponent);
							components[i] = ci * scale * (decode[j + 1] - decode[j]) + decode[j];
						}
						var color = this.tmpCsCompsBuf;
						if (this.context.colorFn) {
							this.context.colorFn(components, 0, color, 0);
						}
						return this.context.colorSpace.getRgb(color, 0);
					}
				};
				function decodeType4Shading(mesh, reader) {
					var coords = mesh.coords;
					var colors = mesh.colors;
					var operators = [];
					var ps = [];
					var verticesLeft = 0;
					while (reader.hasData) {
						var f = reader.readFlag();
						var coord = reader.readCoordinate();
						var color = reader.readComponents();
						if (verticesLeft === 0) {
							assert(0 <= f && f <= 2, 'Unknown type4 flag');
							switch (f) {
								case 0:
									verticesLeft = 3;
									break;
								case 1:
									ps.push(ps[ps.length - 2], ps[ps.length - 1]);
									verticesLeft = 1;
									break;
								case 2:
									ps.push(ps[ps.length - 3], ps[ps.length - 1]);
									verticesLeft = 1;
									break;
							}
							operators.push(f);
						}
						ps.push(coords.length);
						coords.push(coord);
						colors.push(color);
						verticesLeft--;
						reader.align();
					}
					mesh.figures.push({
						type: 'triangles',
						coords: new Int32Array(ps),
						colors: new Int32Array(ps)
					});
				}
				function decodeType5Shading(mesh, reader, verticesPerRow) {
					var coords = mesh.coords;
					var colors = mesh.colors;
					var ps = [];
					while (reader.hasData) {
						var coord = reader.readCoordinate();
						var color = reader.readComponents();
						ps.push(coords.length);
						coords.push(coord);
						colors.push(color);
					}
					mesh.figures.push({
						type: 'lattice',
						coords: new Int32Array(ps),
						colors: new Int32Array(ps),
						verticesPerRow: verticesPerRow
					});
				}
				var MIN_SPLIT_PATCH_CHUNKS_AMOUNT = 3;
				var MAX_SPLIT_PATCH_CHUNKS_AMOUNT = 20;
				var TRIANGLE_DENSITY = 20;
				var getB = function getBClosure() {
					function buildB(count) {
						var lut = [];
						for (var i = 0; i <= count; i++) {
							var t = i / count, t_ = 1 - t;
							lut.push(new Float32Array([
								t_ * t_ * t_,
								3 * t * t_ * t_,
								3 * t * t * t_,
								t * t * t
							]));
						}
						return lut;
					}
					var cache = [];
					return function getB(count) {
						if (!cache[count]) {
							cache[count] = buildB(count);
						}
						return cache[count];
					};
				}();
				function buildFigureFromPatch(mesh, index) {
					var figure = mesh.figures[index];
					assert(figure.type === 'patch', 'Unexpected patch mesh figure');
					var coords = mesh.coords, colors = mesh.colors;
					var pi = figure.coords;
					var ci = figure.colors;
					var figureMinX = Math.min(coords[pi[0]][0], coords[pi[3]][0], coords[pi[12]][0], coords[pi[15]][0]);
					var figureMinY = Math.min(coords[pi[0]][1], coords[pi[3]][1], coords[pi[12]][1], coords[pi[15]][1]);
					var figureMaxX = Math.max(coords[pi[0]][0], coords[pi[3]][0], coords[pi[12]][0], coords[pi[15]][0]);
					var figureMaxY = Math.max(coords[pi[0]][1], coords[pi[3]][1], coords[pi[12]][1], coords[pi[15]][1]);
					var splitXBy = Math.ceil((figureMaxX - figureMinX) * TRIANGLE_DENSITY / (mesh.bounds[2] - mesh.bounds[0]));
					splitXBy = Math.max(MIN_SPLIT_PATCH_CHUNKS_AMOUNT, Math.min(MAX_SPLIT_PATCH_CHUNKS_AMOUNT, splitXBy));
					var splitYBy = Math.ceil((figureMaxY - figureMinY) * TRIANGLE_DENSITY / (mesh.bounds[3] - mesh.bounds[1]));
					splitYBy = Math.max(MIN_SPLIT_PATCH_CHUNKS_AMOUNT, Math.min(MAX_SPLIT_PATCH_CHUNKS_AMOUNT, splitYBy));
					var verticesPerRow = splitXBy + 1;
					var figureCoords = new Int32Array((splitYBy + 1) * verticesPerRow);
					var figureColors = new Int32Array((splitYBy + 1) * verticesPerRow);
					var k = 0;
					var cl = new Uint8Array(3), cr = new Uint8Array(3);
					var c0 = colors[ci[0]], c1 = colors[ci[1]], c2 = colors[ci[2]], c3 = colors[ci[3]];
					var bRow = getB(splitYBy), bCol = getB(splitXBy);
					for (var row = 0; row <= splitYBy; row++) {
						cl[0] = (c0[0] * (splitYBy - row) + c2[0] * row) / splitYBy | 0;
						cl[1] = (c0[1] * (splitYBy - row) + c2[1] * row) / splitYBy | 0;
						cl[2] = (c0[2] * (splitYBy - row) + c2[2] * row) / splitYBy | 0;
						cr[0] = (c1[0] * (splitYBy - row) + c3[0] * row) / splitYBy | 0;
						cr[1] = (c1[1] * (splitYBy - row) + c3[1] * row) / splitYBy | 0;
						cr[2] = (c1[2] * (splitYBy - row) + c3[2] * row) / splitYBy | 0;
						for (var col = 0; col <= splitXBy; col++, k++) {
							if ((row === 0 || row === splitYBy) && (col === 0 || col === splitXBy)) {
								continue;
							}
							var x = 0, y = 0;
							var q = 0;
							for (var i = 0; i <= 3; i++) {
								for (var j = 0; j <= 3; j++, q++) {
									var m = bRow[row][i] * bCol[col][j];
									x += coords[pi[q]][0] * m;
									y += coords[pi[q]][1] * m;
								}
							}
							figureCoords[k] = coords.length;
							coords.push([
								x,
								y
							]);
							figureColors[k] = colors.length;
							var newColor = new Uint8Array(3);
							newColor[0] = (cl[0] * (splitXBy - col) + cr[0] * col) / splitXBy | 0;
							newColor[1] = (cl[1] * (splitXBy - col) + cr[1] * col) / splitXBy | 0;
							newColor[2] = (cl[2] * (splitXBy - col) + cr[2] * col) / splitXBy | 0;
							colors.push(newColor);
						}
					}
					figureCoords[0] = pi[0];
					figureColors[0] = ci[0];
					figureCoords[splitXBy] = pi[3];
					figureColors[splitXBy] = ci[1];
					figureCoords[verticesPerRow * splitYBy] = pi[12];
					figureColors[verticesPerRow * splitYBy] = ci[2];
					figureCoords[verticesPerRow * splitYBy + splitXBy] = pi[15];
					figureColors[verticesPerRow * splitYBy + splitXBy] = ci[3];
					mesh.figures[index] = {
						type: 'lattice',
						coords: figureCoords,
						colors: figureColors,
						verticesPerRow: verticesPerRow
					};
				}
				function decodeType6Shading(mesh, reader) {
					var coords = mesh.coords;
					var colors = mesh.colors;
					var ps = new Int32Array(16);
					var cs = new Int32Array(4);
					while (reader.hasData) {
						var f = reader.readFlag();
						assert(0 <= f && f <= 3, 'Unknown type6 flag');
						var i, ii;
						var pi = coords.length;
						for (i = 0, ii = f !== 0 ? 8 : 12; i < ii; i++) {
							coords.push(reader.readCoordinate());
						}
						var ci = colors.length;
						for (i = 0, ii = f !== 0 ? 2 : 4; i < ii; i++) {
							colors.push(reader.readComponents());
						}
						var tmp1, tmp2, tmp3, tmp4;
						switch (f) {
							case 0:
								ps[12] = pi + 3;
								ps[13] = pi + 4;
								ps[14] = pi + 5;
								ps[15] = pi + 6;
								ps[8] = pi + 2;
								ps[11] = pi + 7;
								ps[4] = pi + 1;
								ps[7] = pi + 8;
								ps[0] = pi;
								ps[1] = pi + 11;
								ps[2] = pi + 10;
								ps[3] = pi + 9;
								cs[2] = ci + 1;
								cs[3] = ci + 2;
								cs[0] = ci;
								cs[1] = ci + 3;
								break;
							case 1:
								tmp1 = ps[12];
								tmp2 = ps[13];
								tmp3 = ps[14];
								tmp4 = ps[15];
								ps[12] = tmp4;
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = tmp3;
								ps[11] = pi + 3;
								ps[4] = tmp2;
								ps[7] = pi + 4;
								ps[0] = tmp1;
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								tmp1 = cs[2];
								tmp2 = cs[3];
								cs[2] = tmp2;
								cs[3] = ci;
								cs[0] = tmp1;
								cs[1] = ci + 1;
								break;
							case 2:
								tmp1 = ps[15];
								tmp2 = ps[11];
								ps[12] = ps[3];
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = ps[7];
								ps[11] = pi + 3;
								ps[4] = tmp2;
								ps[7] = pi + 4;
								ps[0] = tmp1;
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								tmp1 = cs[3];
								cs[2] = cs[1];
								cs[3] = ci;
								cs[0] = tmp1;
								cs[1] = ci + 1;
								break;
							case 3:
								ps[12] = ps[0];
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = ps[1];
								ps[11] = pi + 3;
								ps[4] = ps[2];
								ps[7] = pi + 4;
								ps[0] = ps[3];
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								cs[2] = cs[0];
								cs[3] = ci;
								cs[0] = cs[1];
								cs[1] = ci + 1;
								break;
						}
						ps[5] = coords.length;
						coords.push([
							(-4 * coords[ps[0]][0] - coords[ps[15]][0] + 6 * (coords[ps[4]][0] + coords[ps[1]][0]) - 2 * (coords[ps[12]][0] + coords[ps[3]][0]) + 3 * (coords[ps[13]][0] + coords[ps[7]][0])) / 9,
							(-4 * coords[ps[0]][1] - coords[ps[15]][1] + 6 * (coords[ps[4]][1] + coords[ps[1]][1]) - 2 * (coords[ps[12]][1] + coords[ps[3]][1]) + 3 * (coords[ps[13]][1] + coords[ps[7]][1])) / 9
						]);
						ps[6] = coords.length;
						coords.push([
							(-4 * coords[ps[3]][0] - coords[ps[12]][0] + 6 * (coords[ps[2]][0] + coords[ps[7]][0]) - 2 * (coords[ps[0]][0] + coords[ps[15]][0]) + 3 * (coords[ps[4]][0] + coords[ps[14]][0])) / 9,
							(-4 * coords[ps[3]][1] - coords[ps[12]][1] + 6 * (coords[ps[2]][1] + coords[ps[7]][1]) - 2 * (coords[ps[0]][1] + coords[ps[15]][1]) + 3 * (coords[ps[4]][1] + coords[ps[14]][1])) / 9
						]);
						ps[9] = coords.length;
						coords.push([
							(-4 * coords[ps[12]][0] - coords[ps[3]][0] + 6 * (coords[ps[8]][0] + coords[ps[13]][0]) - 2 * (coords[ps[0]][0] + coords[ps[15]][0]) + 3 * (coords[ps[11]][0] + coords[ps[1]][0])) / 9,
							(-4 * coords[ps[12]][1] - coords[ps[3]][1] + 6 * (coords[ps[8]][1] + coords[ps[13]][1]) - 2 * (coords[ps[0]][1] + coords[ps[15]][1]) + 3 * (coords[ps[11]][1] + coords[ps[1]][1])) / 9
						]);
						ps[10] = coords.length;
						coords.push([
							(-4 * coords[ps[15]][0] - coords[ps[0]][0] + 6 * (coords[ps[11]][0] + coords[ps[14]][0]) - 2 * (coords[ps[12]][0] + coords[ps[3]][0]) + 3 * (coords[ps[2]][0] + coords[ps[8]][0])) / 9,
							(-4 * coords[ps[15]][1] - coords[ps[0]][1] + 6 * (coords[ps[11]][1] + coords[ps[14]][1]) - 2 * (coords[ps[12]][1] + coords[ps[3]][1]) + 3 * (coords[ps[2]][1] + coords[ps[8]][1])) / 9
						]);
						mesh.figures.push({
							type: 'patch',
							coords: new Int32Array(ps),
							colors: new Int32Array(cs)
						});
					}
				}
				function decodeType7Shading(mesh, reader) {
					var coords = mesh.coords;
					var colors = mesh.colors;
					var ps = new Int32Array(16);
					var cs = new Int32Array(4);
					while (reader.hasData) {
						var f = reader.readFlag();
						assert(0 <= f && f <= 3, 'Unknown type7 flag');
						var i, ii;
						var pi = coords.length;
						for (i = 0, ii = f !== 0 ? 12 : 16; i < ii; i++) {
							coords.push(reader.readCoordinate());
						}
						var ci = colors.length;
						for (i = 0, ii = f !== 0 ? 2 : 4; i < ii; i++) {
							colors.push(reader.readComponents());
						}
						var tmp1, tmp2, tmp3, tmp4;
						switch (f) {
							case 0:
								ps[12] = pi + 3;
								ps[13] = pi + 4;
								ps[14] = pi + 5;
								ps[15] = pi + 6;
								ps[8] = pi + 2;
								ps[9] = pi + 13;
								ps[10] = pi + 14;
								ps[11] = pi + 7;
								ps[4] = pi + 1;
								ps[5] = pi + 12;
								ps[6] = pi + 15;
								ps[7] = pi + 8;
								ps[0] = pi;
								ps[1] = pi + 11;
								ps[2] = pi + 10;
								ps[3] = pi + 9;
								cs[2] = ci + 1;
								cs[3] = ci + 2;
								cs[0] = ci;
								cs[1] = ci + 3;
								break;
							case 1:
								tmp1 = ps[12];
								tmp2 = ps[13];
								tmp3 = ps[14];
								tmp4 = ps[15];
								ps[12] = tmp4;
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = tmp3;
								ps[9] = pi + 9;
								ps[10] = pi + 10;
								ps[11] = pi + 3;
								ps[4] = tmp2;
								ps[5] = pi + 8;
								ps[6] = pi + 11;
								ps[7] = pi + 4;
								ps[0] = tmp1;
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								tmp1 = cs[2];
								tmp2 = cs[3];
								cs[2] = tmp2;
								cs[3] = ci;
								cs[0] = tmp1;
								cs[1] = ci + 1;
								break;
							case 2:
								tmp1 = ps[15];
								tmp2 = ps[11];
								ps[12] = ps[3];
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = ps[7];
								ps[9] = pi + 9;
								ps[10] = pi + 10;
								ps[11] = pi + 3;
								ps[4] = tmp2;
								ps[5] = pi + 8;
								ps[6] = pi + 11;
								ps[7] = pi + 4;
								ps[0] = tmp1;
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								tmp1 = cs[3];
								cs[2] = cs[1];
								cs[3] = ci;
								cs[0] = tmp1;
								cs[1] = ci + 1;
								break;
							case 3:
								ps[12] = ps[0];
								ps[13] = pi + 0;
								ps[14] = pi + 1;
								ps[15] = pi + 2;
								ps[8] = ps[1];
								ps[9] = pi + 9;
								ps[10] = pi + 10;
								ps[11] = pi + 3;
								ps[4] = ps[2];
								ps[5] = pi + 8;
								ps[6] = pi + 11;
								ps[7] = pi + 4;
								ps[0] = ps[3];
								ps[1] = pi + 7;
								ps[2] = pi + 6;
								ps[3] = pi + 5;
								cs[2] = cs[0];
								cs[3] = ci;
								cs[0] = cs[1];
								cs[1] = ci + 1;
								break;
						}
						mesh.figures.push({
							type: 'patch',
							coords: new Int32Array(ps),
							colors: new Int32Array(cs)
						});
					}
				}
				function updateBounds(mesh) {
					var minX = mesh.coords[0][0], minY = mesh.coords[0][1], maxX = minX, maxY = minY;
					for (var i = 1, ii = mesh.coords.length; i < ii; i++) {
						var x = mesh.coords[i][0], y = mesh.coords[i][1];
						minX = minX > x ? x : minX;
						minY = minY > y ? y : minY;
						maxX = maxX < x ? x : maxX;
						maxY = maxY < y ? y : maxY;
					}
					mesh.bounds = [
						minX,
						minY,
						maxX,
						maxY
					];
				}
				function packData(mesh) {
					var i, ii, j, jj;
					var coords = mesh.coords;
					var coordsPacked = new Float32Array(coords.length * 2);
					for (i = 0, j = 0, ii = coords.length; i < ii; i++) {
						var xy = coords[i];
						coordsPacked[j++] = xy[0];
						coordsPacked[j++] = xy[1];
					}
					mesh.coords = coordsPacked;
					var colors = mesh.colors;
					var colorsPacked = new Uint8Array(colors.length * 3);
					for (i = 0, j = 0, ii = colors.length; i < ii; i++) {
						var c = colors[i];
						colorsPacked[j++] = c[0];
						colorsPacked[j++] = c[1];
						colorsPacked[j++] = c[2];
					}
					mesh.colors = colorsPacked;
					var figures = mesh.figures;
					for (i = 0, ii = figures.length; i < ii; i++) {
						var figure = figures[i], ps = figure.coords, cs = figure.colors;
						for (j = 0, jj = ps.length; j < jj; j++) {
							ps[j] *= 2;
							cs[j] *= 3;
						}
					}
				}
				function Mesh(stream, matrix, xref, res) {
					assert(isStream(stream), 'Mesh data is not a stream');
					var dict = stream.dict;
					this.matrix = matrix;
					this.shadingType = dict.get('ShadingType');
					this.type = 'Pattern';
					this.bbox = dict.getArray('BBox');
					var cs = dict.get('ColorSpace', 'CS');
					cs = ColorSpace.parse(cs, xref, res);
					this.cs = cs;
					this.background = dict.has('Background') ? cs.getRgb(dict.get('Background'), 0) : null;
					var fnObj = dict.get('Function');
					var fn = fnObj ? PDFFunction.parseArray(xref, fnObj) : null;
					this.coords = [];
					this.colors = [];
					this.figures = [];
					var decodeContext = {
						bitsPerCoordinate: dict.get('BitsPerCoordinate'),
						bitsPerComponent: dict.get('BitsPerComponent'),
						bitsPerFlag: dict.get('BitsPerFlag'),
						decode: dict.getArray('Decode'),
						colorFn: fn,
						colorSpace: cs,
						numComps: fn ? 1 : cs.numComps
					};
					var reader = new MeshStreamReader(stream, decodeContext);
					var patchMesh = false;
					switch (this.shadingType) {
						case ShadingType.FREE_FORM_MESH:
							decodeType4Shading(this, reader);
							break;
						case ShadingType.LATTICE_FORM_MESH:
							var verticesPerRow = dict.get('VerticesPerRow') | 0;
							assert(verticesPerRow >= 2, 'Invalid VerticesPerRow');
							decodeType5Shading(this, reader, verticesPerRow);
							break;
						case ShadingType.COONS_PATCH_MESH:
							decodeType6Shading(this, reader);
							patchMesh = true;
							break;
						case ShadingType.TENSOR_PATCH_MESH:
							decodeType7Shading(this, reader);
							patchMesh = true;
							break;
						default:
							error('Unsupported mesh type.');
							break;
					}
					if (patchMesh) {
						updateBounds(this);
						for (var i = 0, ii = this.figures.length; i < ii; i++) {
							buildFigureFromPatch(this, i);
						}
					}
					updateBounds(this);
					packData(this);
				}
				Mesh.prototype = {
					getIR: function Mesh_getIR() {
						return [
							'Mesh',
							this.shadingType,
							this.coords,
							this.colors,
							this.figures,
							this.bounds,
							this.matrix,
							this.bbox,
							this.background
						];
					}
				};
				return Mesh;
			}();
			Shadings.Dummy = function DummyClosure() {
				function Dummy() {
					this.type = 'Pattern';
				}
				Dummy.prototype = {
					getIR: function Dummy_getIR() {
						return ['Dummy'];
					}
				};
				return Dummy;
			}();
			function getTilingPatternIR(operatorList, dict, args) {
				var matrix = dict.getArray('Matrix');
				var bbox = dict.getArray('BBox');
				var xstep = dict.get('XStep');
				var ystep = dict.get('YStep');
				var paintType = dict.get('PaintType');
				var tilingType = dict.get('TilingType');
				return [
					'TilingPattern',
					args,
					operatorList,
					matrix,
					bbox,
					xstep,
					ystep,
					paintType,
					tilingType
				];
			}
			exports.Pattern = Pattern;
			exports.getTilingPatternIR = getTilingPatternIR;

			/***/
		}),
/* 33 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreStream = __w_pdfjs_require__(2);
			var coreChunkedStream = __w_pdfjs_require__(12);
			var coreDocument = __w_pdfjs_require__(24);
			var warn = sharedUtil.warn;
			var createValidAbsoluteUrl = sharedUtil.createValidAbsoluteUrl;
			var shadow = sharedUtil.shadow;
			var NotImplementedException = sharedUtil.NotImplementedException;
			var MissingDataException = sharedUtil.MissingDataException;
			var createPromiseCapability = sharedUtil.createPromiseCapability;
			var Util = sharedUtil.Util;
			var Stream = coreStream.Stream;
			var ChunkedStreamManager = coreChunkedStream.ChunkedStreamManager;
			var PDFDocument = coreDocument.PDFDocument;
			var BasePdfManager = function BasePdfManagerClosure() {
				function BasePdfManager() {
					throw new Error('Cannot initialize BaseManagerManager');
				}
				BasePdfManager.prototype = {
					get docId() {
						return this._docId;
					},
					get password() {
						return this._password;
					},
					get docBaseUrl() {
						var docBaseUrl = null;
						if (this._docBaseUrl) {
							var absoluteUrl = createValidAbsoluteUrl(this._docBaseUrl);
							if (absoluteUrl) {
								docBaseUrl = absoluteUrl.href;
							} else {
								warn('Invalid absolute docBaseUrl: "' + this._docBaseUrl + '".');
							}
						}
						return shadow(this, 'docBaseUrl', docBaseUrl);
					},
					onLoadedStream: function BasePdfManager_onLoadedStream() {
						throw new NotImplementedException();
					},
					ensureDoc: function BasePdfManager_ensureDoc(prop, args) {
						return this.ensure(this.pdfDocument, prop, args);
					},
					ensureXRef: function BasePdfManager_ensureXRef(prop, args) {
						return this.ensure(this.pdfDocument.xref, prop, args);
					},
					ensureCatalog: function BasePdfManager_ensureCatalog(prop, args) {
						return this.ensure(this.pdfDocument.catalog, prop, args);
					},
					getPage: function BasePdfManager_getPage(pageIndex) {
						return this.pdfDocument.getPage(pageIndex);
					},
					cleanup: function BasePdfManager_cleanup() {
						return this.pdfDocument.cleanup();
					},
					ensure: function BasePdfManager_ensure(obj, prop, args) {
						return new NotImplementedException();
					},
					requestRange: function BasePdfManager_requestRange(begin, end) {
						return new NotImplementedException();
					},
					requestLoadedStream: function BasePdfManager_requestLoadedStream() {
						return new NotImplementedException();
					},
					sendProgressiveData: function BasePdfManager_sendProgressiveData(chunk) {
						return new NotImplementedException();
					},
					updatePassword: function BasePdfManager_updatePassword(password) {
						this._password = password;
					},
					terminate: function BasePdfManager_terminate() {
						return new NotImplementedException();
					}
				};
				return BasePdfManager;
			}();
			var LocalPdfManager = function LocalPdfManagerClosure() {
				function LocalPdfManager(docId, data, password, evaluatorOptions, docBaseUrl) {
					this._docId = docId;
					this._password = password;
					this._docBaseUrl = docBaseUrl;
					this.evaluatorOptions = evaluatorOptions;
					var stream = new Stream(data);
					this.pdfDocument = new PDFDocument(this, stream);
					this._loadedStreamCapability = createPromiseCapability();
					this._loadedStreamCapability.resolve(stream);
				}
				Util.inherit(LocalPdfManager, BasePdfManager, {
					ensure: function LocalPdfManager_ensure(obj, prop, args) {
						return new Promise(function (resolve, reject) {
							try {
								var value = obj[prop];
								var result;
								if (typeof value === 'function') {
									result = value.apply(obj, args);
								} else {
									result = value;
								}
								resolve(result);
							} catch (e) {
								reject(e);
							}
						});
					},
					requestRange: function LocalPdfManager_requestRange(begin, end) {
						return Promise.resolve();
					},
					requestLoadedStream: function LocalPdfManager_requestLoadedStream() {
					},
					onLoadedStream: function LocalPdfManager_onLoadedStream() {
						return this._loadedStreamCapability.promise;
					},
					terminate: function LocalPdfManager_terminate() {
					}
				});
				return LocalPdfManager;
			}();
			var NetworkPdfManager = function NetworkPdfManagerClosure() {
				function NetworkPdfManager(docId, pdfNetworkStream, args, evaluatorOptions, docBaseUrl) {
					this._docId = docId;
					this._password = args.password;
					this._docBaseUrl = docBaseUrl;
					this.msgHandler = args.msgHandler;
					this.evaluatorOptions = evaluatorOptions;
					var params = {
						msgHandler: args.msgHandler,
						url: args.url,
						length: args.length,
						disableAutoFetch: args.disableAutoFetch,
						rangeChunkSize: args.rangeChunkSize
					};
					this.streamManager = new ChunkedStreamManager(pdfNetworkStream, params);
					this.pdfDocument = new PDFDocument(this, this.streamManager.getStream());
				}
				Util.inherit(NetworkPdfManager, BasePdfManager, {
					ensure: function NetworkPdfManager_ensure(obj, prop, args) {
						var pdfManager = this;
						return new Promise(function (resolve, reject) {
							function ensureHelper() {
								try {
									var result;
									var value = obj[prop];
									if (typeof value === 'function') {
										result = value.apply(obj, args);
									} else {
										result = value;
									}
									resolve(result);
								} catch (e) {
									if (!(e instanceof MissingDataException)) {
										reject(e);
										return;
									}
									pdfManager.streamManager.requestRange(e.begin, e.end).then(ensureHelper, reject);
								}
							}
							ensureHelper();
						});
					},
					requestRange: function NetworkPdfManager_requestRange(begin, end) {
						return this.streamManager.requestRange(begin, end);
					},
					requestLoadedStream: function NetworkPdfManager_requestLoadedStream() {
						this.streamManager.requestAllChunks();
					},
					sendProgressiveData: function NetworkPdfManager_sendProgressiveData(chunk) {
						this.streamManager.onReceiveData({ chunk: chunk });
					},
					onLoadedStream: function NetworkPdfManager_onLoadedStream() {
						return this.streamManager.onLoadedStream();
					},
					terminate: function NetworkPdfManager_terminate() {
						this.streamManager.abort();
					}
				});
				return NetworkPdfManager;
			}();
			exports.LocalPdfManager = LocalPdfManager;
			exports.NetworkPdfManager = NetworkPdfManager;

			/***/
		}),
/* 34 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var corePrimitives = __w_pdfjs_require__(1);
			var error = sharedUtil.error;
			var isSpace = sharedUtil.isSpace;
			var EOF = corePrimitives.EOF;
			var PostScriptParser = function PostScriptParserClosure() {
				function PostScriptParser(lexer) {
					this.lexer = lexer;
					this.operators = [];
					this.token = null;
					this.prev = null;
				}
				PostScriptParser.prototype = {
					nextToken: function PostScriptParser_nextToken() {
						this.prev = this.token;
						this.token = this.lexer.getToken();
					},
					accept: function PostScriptParser_accept(type) {
						if (this.token.type === type) {
							this.nextToken();
							return true;
						}
						return false;
					},
					expect: function PostScriptParser_expect(type) {
						if (this.accept(type)) {
							return true;
						}
						error('Unexpected symbol: found ' + this.token.type + ' expected ' + type + '.');
					},
					parse: function PostScriptParser_parse() {
						this.nextToken();
						this.expect(PostScriptTokenTypes.LBRACE);
						this.parseBlock();
						this.expect(PostScriptTokenTypes.RBRACE);
						return this.operators;
					},
					parseBlock: function PostScriptParser_parseBlock() {
						while (true) {
							if (this.accept(PostScriptTokenTypes.NUMBER)) {
								this.operators.push(this.prev.value);
							} else if (this.accept(PostScriptTokenTypes.OPERATOR)) {
								this.operators.push(this.prev.value);
							} else if (this.accept(PostScriptTokenTypes.LBRACE)) {
								this.parseCondition();
							} else {
								return;
							}
						}
					},
					parseCondition: function PostScriptParser_parseCondition() {
						var conditionLocation = this.operators.length;
						this.operators.push(null, null);
						this.parseBlock();
						this.expect(PostScriptTokenTypes.RBRACE);
						if (this.accept(PostScriptTokenTypes.IF)) {
							this.operators[conditionLocation] = this.operators.length;
							this.operators[conditionLocation + 1] = 'jz';
						} else if (this.accept(PostScriptTokenTypes.LBRACE)) {
							var jumpLocation = this.operators.length;
							this.operators.push(null, null);
							var endOfTrue = this.operators.length;
							this.parseBlock();
							this.expect(PostScriptTokenTypes.RBRACE);
							this.expect(PostScriptTokenTypes.IFELSE);
							this.operators[jumpLocation] = this.operators.length;
							this.operators[jumpLocation + 1] = 'j';
							this.operators[conditionLocation] = endOfTrue;
							this.operators[conditionLocation + 1] = 'jz';
						} else {
							error('PS Function: error parsing conditional.');
						}
					}
				};
				return PostScriptParser;
			}();
			var PostScriptTokenTypes = {
				LBRACE: 0,
				RBRACE: 1,
				NUMBER: 2,
				OPERATOR: 3,
				IF: 4,
				IFELSE: 5
			};
			var PostScriptToken = function PostScriptTokenClosure() {
				function PostScriptToken(type, value) {
					this.type = type;
					this.value = value;
				}
				var opCache = Object.create(null);
				PostScriptToken.getOperator = function PostScriptToken_getOperator(op) {
					var opValue = opCache[op];
					if (opValue) {
						return opValue;
					}
					return opCache[op] = new PostScriptToken(PostScriptTokenTypes.OPERATOR, op);
				};
				PostScriptToken.LBRACE = new PostScriptToken(PostScriptTokenTypes.LBRACE, '{');
				PostScriptToken.RBRACE = new PostScriptToken(PostScriptTokenTypes.RBRACE, '}');
				PostScriptToken.IF = new PostScriptToken(PostScriptTokenTypes.IF, 'IF');
				PostScriptToken.IFELSE = new PostScriptToken(PostScriptTokenTypes.IFELSE, 'IFELSE');
				return PostScriptToken;
			}();
			var PostScriptLexer = function PostScriptLexerClosure() {
				function PostScriptLexer(stream) {
					this.stream = stream;
					this.nextChar();
					this.strBuf = [];
				}
				PostScriptLexer.prototype = {
					nextChar: function PostScriptLexer_nextChar() {
						return this.currentChar = this.stream.getByte();
					},
					getToken: function PostScriptLexer_getToken() {
						var comment = false;
						var ch = this.currentChar;
						while (true) {
							if (ch < 0) {
								return EOF;
							}
							if (comment) {
								if (ch === 0x0A || ch === 0x0D) {
									comment = false;
								}
							} else if (ch === 0x25) {
								comment = true;
							} else if (!isSpace(ch)) {
								break;
							}
							ch = this.nextChar();
						}
						switch (ch | 0) {
							case 0x30:
							case 0x31:
							case 0x32:
							case 0x33:
							case 0x34:
							case 0x35:
							case 0x36:
							case 0x37:
							case 0x38:
							case 0x39:
							case 0x2B:
							case 0x2D:
							case 0x2E:
								return new PostScriptToken(PostScriptTokenTypes.NUMBER, this.getNumber());
							case 0x7B:
								this.nextChar();
								return PostScriptToken.LBRACE;
							case 0x7D:
								this.nextChar();
								return PostScriptToken.RBRACE;
						}
						var strBuf = this.strBuf;
						strBuf.length = 0;
						strBuf[0] = String.fromCharCode(ch);
						while ((ch = this.nextChar()) >= 0 && (ch >= 0x41 && ch <= 0x5A || ch >= 0x61 && ch <= 0x7A)) {
							strBuf.push(String.fromCharCode(ch));
						}
						var str = strBuf.join('');
						switch (str.toLowerCase()) {
							case 'if':
								return PostScriptToken.IF;
							case 'ifelse':
								return PostScriptToken.IFELSE;
							default:
								return PostScriptToken.getOperator(str);
						}
					},
					getNumber: function PostScriptLexer_getNumber() {
						var ch = this.currentChar;
						var strBuf = this.strBuf;
						strBuf.length = 0;
						strBuf[0] = String.fromCharCode(ch);
						while ((ch = this.nextChar()) >= 0) {
							if (ch >= 0x30 && ch <= 0x39 || ch === 0x2D || ch === 0x2E) {
								strBuf.push(String.fromCharCode(ch));
							} else {
								break;
							}
						}
						var value = parseFloat(strBuf.join(''));
						if (isNaN(value)) {
							error('Invalid floating point number: ' + value);
						}
						return value;
					}
				};
				return PostScriptLexer;
			}();
			exports.PostScriptLexer = PostScriptLexer;
			exports.PostScriptParser = PostScriptParser;

			/***/
		}),
/* 35 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var sharedUtil = __w_pdfjs_require__(0);
			var coreStream = __w_pdfjs_require__(2);
			var coreEncodings = __w_pdfjs_require__(4);
			var warn = sharedUtil.warn;
			var isSpace = sharedUtil.isSpace;
			var Stream = coreStream.Stream;
			var getEncoding = coreEncodings.getEncoding;
			var HINTING_ENABLED = false;
			var Type1CharString = function Type1CharStringClosure() {
				var COMMAND_MAP = {
					'hstem': [1],
					'vstem': [3],
					'vmoveto': [4],
					'rlineto': [5],
					'hlineto': [6],
					'vlineto': [7],
					'rrcurveto': [8],
					'callsubr': [10],
					'flex': [
						12,
						35
					],
					'drop': [
						12,
						18
					],
					'endchar': [14],
					'rmoveto': [21],
					'hmoveto': [22],
					'vhcurveto': [30],
					'hvcurveto': [31]
				};
				function Type1CharString() {
					this.width = 0;
					this.lsb = 0;
					this.flexing = false;
					this.output = [];
					this.stack = [];
				}
				Type1CharString.prototype = {
					convert: function Type1CharString_convert(encoded, subrs, seacAnalysisEnabled) {
						var count = encoded.length;
						var error = false;
						var wx, sbx, subrNumber;
						for (var i = 0; i < count; i++) {
							var value = encoded[i];
							if (value < 32) {
								if (value === 12) {
									value = (value << 8) + encoded[++i];
								}
								switch (value) {
									case 1:
										if (!HINTING_ENABLED) {
											this.stack = [];
											break;
										}
										error = this.executeCommand(2, COMMAND_MAP.hstem);
										break;
									case 3:
										if (!HINTING_ENABLED) {
											this.stack = [];
											break;
										}
										error = this.executeCommand(2, COMMAND_MAP.vstem);
										break;
									case 4:
										if (this.flexing) {
											if (this.stack.length < 1) {
												error = true;
												break;
											}
											var dy = this.stack.pop();
											this.stack.push(0, dy);
											break;
										}
										error = this.executeCommand(1, COMMAND_MAP.vmoveto);
										break;
									case 5:
										error = this.executeCommand(2, COMMAND_MAP.rlineto);
										break;
									case 6:
										error = this.executeCommand(1, COMMAND_MAP.hlineto);
										break;
									case 7:
										error = this.executeCommand(1, COMMAND_MAP.vlineto);
										break;
									case 8:
										error = this.executeCommand(6, COMMAND_MAP.rrcurveto);
										break;
									case 9:
										this.stack = [];
										break;
									case 10:
										if (this.stack.length < 1) {
											error = true;
											break;
										}
										subrNumber = this.stack.pop();
										error = this.convert(subrs[subrNumber], subrs, seacAnalysisEnabled);
										break;
									case 11:
										return error;
									case 13:
										if (this.stack.length < 2) {
											error = true;
											break;
										}
										wx = this.stack.pop();
										sbx = this.stack.pop();
										this.lsb = sbx;
										this.width = wx;
										this.stack.push(wx, sbx);
										error = this.executeCommand(2, COMMAND_MAP.hmoveto);
										break;
									case 14:
										this.output.push(COMMAND_MAP.endchar[0]);
										break;
									case 21:
										if (this.flexing) {
											break;
										}
										error = this.executeCommand(2, COMMAND_MAP.rmoveto);
										break;
									case 22:
										if (this.flexing) {
											this.stack.push(0);
											break;
										}
										error = this.executeCommand(1, COMMAND_MAP.hmoveto);
										break;
									case 30:
										error = this.executeCommand(4, COMMAND_MAP.vhcurveto);
										break;
									case 31:
										error = this.executeCommand(4, COMMAND_MAP.hvcurveto);
										break;
									case (12 << 8) + 0:
										this.stack = [];
										break;
									case (12 << 8) + 1:
										if (!HINTING_ENABLED) {
											this.stack = [];
											break;
										}
										error = this.executeCommand(2, COMMAND_MAP.vstem);
										break;
									case (12 << 8) + 2:
										if (!HINTING_ENABLED) {
											this.stack = [];
											break;
										}
										error = this.executeCommand(2, COMMAND_MAP.hstem);
										break;
									case (12 << 8) + 6:
										if (seacAnalysisEnabled) {
											this.seac = this.stack.splice(-4, 4);
											error = this.executeCommand(0, COMMAND_MAP.endchar);
										} else {
											error = this.executeCommand(4, COMMAND_MAP.endchar);
										}
										break;
									case (12 << 8) + 7:
										if (this.stack.length < 4) {
											error = true;
											break;
										}
										this.stack.pop();
										wx = this.stack.pop();
										var sby = this.stack.pop();
										sbx = this.stack.pop();
										this.lsb = sbx;
										this.width = wx;
										this.stack.push(wx, sbx, sby);
										error = this.executeCommand(3, COMMAND_MAP.rmoveto);
										break;
									case (12 << 8) + 12:
										if (this.stack.length < 2) {
											error = true;
											break;
										}
										var num2 = this.stack.pop();
										var num1 = this.stack.pop();
										this.stack.push(num1 / num2);
										break;
									case (12 << 8) + 16:
										if (this.stack.length < 2) {
											error = true;
											break;
										}
										subrNumber = this.stack.pop();
										var numArgs = this.stack.pop();
										if (subrNumber === 0 && numArgs === 3) {
											var flexArgs = this.stack.splice(this.stack.length - 17, 17);
											this.stack.push(flexArgs[2] + flexArgs[0], flexArgs[3] + flexArgs[1], flexArgs[4], flexArgs[5], flexArgs[6], flexArgs[7], flexArgs[8], flexArgs[9], flexArgs[10], flexArgs[11], flexArgs[12], flexArgs[13], flexArgs[14]);
											error = this.executeCommand(13, COMMAND_MAP.flex, true);
											this.flexing = false;
											this.stack.push(flexArgs[15], flexArgs[16]);
										} else if (subrNumber === 1 && numArgs === 0) {
											this.flexing = true;
										}
										break;
									case (12 << 8) + 17:
										break;
									case (12 << 8) + 33:
										this.stack = [];
										break;
									default:
										warn('Unknown type 1 charstring command of "' + value + '"');
										break;
								}
								if (error) {
									break;
								}
								continue;
							} else if (value <= 246) {
								value = value - 139;
							} else if (value <= 250) {
								value = (value - 247) * 256 + encoded[++i] + 108;
							} else if (value <= 254) {
								value = -((value - 251) * 256) - encoded[++i] - 108;
							} else {
								value = (encoded[++i] & 0xff) << 24 | (encoded[++i] & 0xff) << 16 | (encoded[++i] & 0xff) << 8 | (encoded[++i] & 0xff) << 0;
							}
							this.stack.push(value);
						}
						return error;
					},
					executeCommand: function (howManyArgs, command, keepStack) {
						var stackLength = this.stack.length;
						if (howManyArgs > stackLength) {
							return true;
						}
						var start = stackLength - howManyArgs;
						for (var i = start; i < stackLength; i++) {
							var value = this.stack[i];
							if (value === (value | 0)) {
								this.output.push(28, value >> 8 & 0xff, value & 0xff);
							} else {
								value = 65536 * value | 0;
								this.output.push(255, value >> 24 & 0xFF, value >> 16 & 0xFF, value >> 8 & 0xFF, value & 0xFF);
							}
						}
						this.output.push.apply(this.output, command);
						if (keepStack) {
							this.stack.splice(start, howManyArgs);
						} else {
							this.stack.length = 0;
						}
						return false;
					}
				};
				return Type1CharString;
			}();
			var Type1Parser = function Type1ParserClosure() {
				var EEXEC_ENCRYPT_KEY = 55665;
				var CHAR_STRS_ENCRYPT_KEY = 4330;
				function isHexDigit(code) {
					return code >= 48 && code <= 57 || code >= 65 && code <= 70 || code >= 97 && code <= 102;
				}
				function decrypt(data, key, discardNumber) {
					if (discardNumber >= data.length) {
						return new Uint8Array(0);
					}
					var r = key | 0, c1 = 52845, c2 = 22719, i, j;
					for (i = 0; i < discardNumber; i++) {
						r = (data[i] + r) * c1 + c2 & (1 << 16) - 1;
					}
					var count = data.length - discardNumber;
					var decrypted = new Uint8Array(count);
					for (i = discardNumber, j = 0; j < count; i++, j++) {
						var value = data[i];
						decrypted[j] = value ^ r >> 8;
						r = (value + r) * c1 + c2 & (1 << 16) - 1;
					}
					return decrypted;
				}
				function decryptAscii(data, key, discardNumber) {
					var r = key | 0, c1 = 52845, c2 = 22719;
					var count = data.length, maybeLength = count >>> 1;
					var decrypted = new Uint8Array(maybeLength);
					var i, j;
					for (i = 0, j = 0; i < count; i++) {
						var digit1 = data[i];
						if (!isHexDigit(digit1)) {
							continue;
						}
						i++;
						var digit2;
						while (i < count && !isHexDigit(digit2 = data[i])) {
							i++;
						}
						if (i < count) {
							var value = parseInt(String.fromCharCode(digit1, digit2), 16);
							decrypted[j++] = value ^ r >> 8;
							r = (value + r) * c1 + c2 & (1 << 16) - 1;
						}
					}
					return Array.prototype.slice.call(decrypted, discardNumber, j);
				}
				function isSpecial(c) {
					return c === 0x2F || c === 0x5B || c === 0x5D || c === 0x7B || c === 0x7D || c === 0x28 || c === 0x29;
				}
				function Type1Parser(stream, encrypted, seacAnalysisEnabled) {
					if (encrypted) {
						var data = stream.getBytes();
						var isBinary = !(isHexDigit(data[0]) && isHexDigit(data[1]) && isHexDigit(data[2]) && isHexDigit(data[3]));
						stream = new Stream(isBinary ? decrypt(data, EEXEC_ENCRYPT_KEY, 4) : decryptAscii(data, EEXEC_ENCRYPT_KEY, 4));
					}
					this.seacAnalysisEnabled = !!seacAnalysisEnabled;
					this.stream = stream;
					this.nextChar();
				}
				Type1Parser.prototype = {
					readNumberArray: function Type1Parser_readNumberArray() {
						this.getToken();
						var array = [];
						while (true) {
							var token = this.getToken();
							if (token === null || token === ']' || token === '}') {
								break;
							}
							array.push(parseFloat(token || 0));
						}
						return array;
					},
					readNumber: function Type1Parser_readNumber() {
						var token = this.getToken();
						return parseFloat(token || 0);
					},
					readInt: function Type1Parser_readInt() {
						var token = this.getToken();
						return parseInt(token || 0, 10) | 0;
					},
					readBoolean: function Type1Parser_readBoolean() {
						var token = this.getToken();
						return token === 'true' ? 1 : 0;
					},
					nextChar: function Type1_nextChar() {
						return this.currentChar = this.stream.getByte();
					},
					getToken: function Type1Parser_getToken() {
						var comment = false;
						var ch = this.currentChar;
						while (true) {
							if (ch === -1) {
								return null;
							}
							if (comment) {
								if (ch === 0x0A || ch === 0x0D) {
									comment = false;
								}
							} else if (ch === 0x25) {
								comment = true;
							} else if (!isSpace(ch)) {
								break;
							}
							ch = this.nextChar();
						}
						if (isSpecial(ch)) {
							this.nextChar();
							return String.fromCharCode(ch);
						}
						var token = '';
						do {
							token += String.fromCharCode(ch);
							ch = this.nextChar();
						} while (ch >= 0 && !isSpace(ch) && !isSpecial(ch));
						return token;
					},
					extractFontProgram: function Type1Parser_extractFontProgram() {
						var stream = this.stream;
						var subrs = [], charstrings = [];
						var privateData = Object.create(null);
						privateData['lenIV'] = 4;
						var program = {
							subrs: [],
							charstrings: [],
							properties: { 'privateData': privateData }
						};
						var token, length, data, lenIV, encoded;
						while ((token = this.getToken()) !== null) {
							if (token !== '/') {
								continue;
							}
							token = this.getToken();
							switch (token) {
								case 'CharStrings':
									this.getToken();
									this.getToken();
									this.getToken();
									this.getToken();
									while (true) {
										token = this.getToken();
										if (token === null || token === 'end') {
											break;
										}
										if (token !== '/') {
											continue;
										}
										var glyph = this.getToken();
										length = this.readInt();
										this.getToken();
										data = stream.makeSubStream(stream.pos, length);
										lenIV = program.properties.privateData['lenIV'];
										encoded = decrypt(data.getBytes(), CHAR_STRS_ENCRYPT_KEY, lenIV);
										stream.skip(length);
										this.nextChar();
										token = this.getToken();
										if (token === 'noaccess') {
											this.getToken();
										}
										charstrings.push({
											glyph: glyph,
											encoded: encoded
										});
									}
									break;
								case 'Subrs':
									this.readInt();
									this.getToken();
									while ((token = this.getToken()) === 'dup') {
										var index = this.readInt();
										length = this.readInt();
										this.getToken();
										data = stream.makeSubStream(stream.pos, length);
										lenIV = program.properties.privateData['lenIV'];
										encoded = decrypt(data.getBytes(), CHAR_STRS_ENCRYPT_KEY, lenIV);
										stream.skip(length);
										this.nextChar();
										token = this.getToken();
										if (token === 'noaccess') {
											this.getToken();
										}
										subrs[index] = encoded;
									}
									break;
								case 'BlueValues':
								case 'OtherBlues':
								case 'FamilyBlues':
								case 'FamilyOtherBlues':
									var blueArray = this.readNumberArray();
									if (blueArray.length > 0 && blueArray.length % 2 === 0 && HINTING_ENABLED) {
										program.properties.privateData[token] = blueArray;
									}
									break;
								case 'StemSnapH':
								case 'StemSnapV':
									program.properties.privateData[token] = this.readNumberArray();
									break;
								case 'StdHW':
								case 'StdVW':
									program.properties.privateData[token] = this.readNumberArray()[0];
									break;
								case 'BlueShift':
								case 'lenIV':
								case 'BlueFuzz':
								case 'BlueScale':
								case 'LanguageGroup':
								case 'ExpansionFactor':
									program.properties.privateData[token] = this.readNumber();
									break;
								case 'ForceBold':
									program.properties.privateData[token] = this.readBoolean();
									break;
							}
						}
						for (var i = 0; i < charstrings.length; i++) {
							glyph = charstrings[i].glyph;
							encoded = charstrings[i].encoded;
							var charString = new Type1CharString();
							var error = charString.convert(encoded, subrs, this.seacAnalysisEnabled);
							var output = charString.output;
							if (error) {
								output = [14];
							}
							program.charstrings.push({
								glyphName: glyph,
								charstring: output,
								width: charString.width,
								lsb: charString.lsb,
								seac: charString.seac
							});
						}
						return program;
					},
					extractFontHeader: function Type1Parser_extractFontHeader(properties) {
						var token;
						while ((token = this.getToken()) !== null) {
							if (token !== '/') {
								continue;
							}
							token = this.getToken();
							switch (token) {
								case 'FontMatrix':
									var matrix = this.readNumberArray();
									properties.fontMatrix = matrix;
									break;
								case 'Encoding':
									var encodingArg = this.getToken();
									var encoding;
									if (!/^\d+$/.test(encodingArg)) {
										encoding = getEncoding(encodingArg);
									} else {
										encoding = [];
										var size = parseInt(encodingArg, 10) | 0;
										this.getToken();
										for (var j = 0; j < size; j++) {
											token = this.getToken();
											while (token !== 'dup' && token !== 'def') {
												token = this.getToken();
												if (token === null) {
													return;
												}
											}
											if (token === 'def') {
												break;
											}
											var index = this.readInt();
											this.getToken();
											var glyph = this.getToken();
											encoding[index] = glyph;
											this.getToken();
										}
									}
									properties.builtInEncoding = encoding;
									break;
								case 'FontBBox':
									var fontBBox = this.readNumberArray();
									properties.ascent = Math.max(fontBBox[3], fontBBox[1]);
									properties.descent = Math.min(fontBBox[1], fontBBox[3]);
									properties.ascentScaled = true;
									break;
							}
						}
					}
				};
				return Type1Parser;
			}();
			exports.Type1Parser = Type1Parser;

			/***/
		}),
/* 36 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";
/* WEBPACK VAR INJECTION */(function (global) {
				if (typeof PDFJS === 'undefined' || !PDFJS.compatibilityChecked) {
					var globalScope = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : this;
					var userAgent = typeof navigator !== 'undefined' && navigator.userAgent || '';
					var isAndroid = /Android/.test(userAgent);
					var isAndroidPre3 = /Android\s[0-2][^\d]/.test(userAgent);
					var isAndroidPre5 = /Android\s[0-4][^\d]/.test(userAgent);
					var isChrome = userAgent.indexOf('Chrom') >= 0;
					var isChromeWithRangeBug = /Chrome\/(39|40)\./.test(userAgent);
					var isIOSChrome = userAgent.indexOf('CriOS') >= 0;
					var isIE = userAgent.indexOf('Trident') >= 0;
					var isIOS = /\b(iPad|iPhone|iPod)(?=;)/.test(userAgent);
					var isOpera = userAgent.indexOf('Opera') >= 0;
					var isSafari = /Safari\//.test(userAgent) && !/(Chrome\/|Android\s)/.test(userAgent);
					var hasDOM = typeof window === 'object' && typeof document === 'object';
					if (typeof PDFJS === 'undefined') {
						globalScope.PDFJS = {};
					}
					PDFJS.compatibilityChecked = true;
					(function checkTypedArrayCompatibility() {
						if (typeof Uint8Array !== 'undefined') {
							if (typeof Uint8Array.prototype.subarray === 'undefined') {
								Uint8Array.prototype.subarray = function subarray(start, end) {
									return new Uint8Array(this.slice(start, end));
								};
								Float32Array.prototype.subarray = function subarray(start, end) {
									return new Float32Array(this.slice(start, end));
								};
							}
							if (typeof Float64Array === 'undefined') {
								globalScope.Float64Array = Float32Array;
							}
							return;
						}
						function subarray(start, end) {
							return new TypedArray(this.slice(start, end));
						}
						function setArrayOffset(array, offset) {
							if (arguments.length < 2) {
								offset = 0;
							}
							for (var i = 0, n = array.length; i < n; ++i, ++offset) {
								this[offset] = array[i] & 0xFF;
							}
						}
						function TypedArray(arg1) {
							var result, i, n;
							if (typeof arg1 === 'number') {
								result = [];
								for (i = 0; i < arg1; ++i) {
									result[i] = 0;
								}
							} else if ('slice' in arg1) {
								result = arg1.slice(0);
							} else {
								result = [];
								for (i = 0, n = arg1.length; i < n; ++i) {
									result[i] = arg1[i];
								}
							}
							result.subarray = subarray;
							result.buffer = result;
							result.byteLength = result.length;
							result.set = setArrayOffset;
							if (typeof arg1 === 'object' && arg1.buffer) {
								result.buffer = arg1.buffer;
							}
							return result;
						}
						globalScope.Uint8Array = TypedArray;
						globalScope.Int8Array = TypedArray;
						globalScope.Uint32Array = TypedArray;
						globalScope.Int32Array = TypedArray;
						globalScope.Uint16Array = TypedArray;
						globalScope.Float32Array = TypedArray;
						globalScope.Float64Array = TypedArray;
					}());
					(function normalizeURLObject() {
						if (!globalScope.URL) {
							globalScope.URL = globalScope.webkitURL;
						}
					}());
					(function checkObjectDefinePropertyCompatibility() {
						if (typeof Object.defineProperty !== 'undefined') {
							var definePropertyPossible = true;
							try {
								if (hasDOM) {
									Object.defineProperty(new Image(), 'id', { value: 'test' });
								}
								var Test = function Test() {
								};
								Test.prototype = {
									get id() {
									}
								};
								Object.defineProperty(new Test(), 'id', {
									value: '',
									configurable: true,
									enumerable: true,
									writable: false
								});
							} catch (e) {
								definePropertyPossible = false;
							}
							if (definePropertyPossible) {
								return;
							}
						}
						Object.defineProperty = function objectDefineProperty(obj, name, def) {
							delete obj[name];
							if ('get' in def) {
								obj.__defineGetter__(name, def['get']);
							}
							if ('set' in def) {
								obj.__defineSetter__(name, def['set']);
							}
							if ('value' in def) {
								obj.__defineSetter__(name, function objectDefinePropertySetter(value) {
									this.__defineGetter__(name, function objectDefinePropertyGetter() {
										return value;
									});
									return value;
								});
								obj[name] = def.value;
							}
						};
					}());
					(function checkXMLHttpRequestResponseCompatibility() {
						if (typeof XMLHttpRequest === 'undefined') {
							return;
						}
						var xhrPrototype = XMLHttpRequest.prototype;
						var xhr = new XMLHttpRequest();
						if (!('overrideMimeType' in xhr)) {
							Object.defineProperty(xhrPrototype, 'overrideMimeType', {
								value: function xmlHttpRequestOverrideMimeType(mimeType) {
								}
							});
						}
						if ('responseType' in xhr) {
							return;
						}
						Object.defineProperty(xhrPrototype, 'responseType', {
							get: function xmlHttpRequestGetResponseType() {
								return this._responseType || 'text';
							},
							set: function xmlHttpRequestSetResponseType(value) {
								if (value === 'text' || value === 'arraybuffer') {
									this._responseType = value;
									if (value === 'arraybuffer' && typeof this.overrideMimeType === 'function') {
										this.overrideMimeType('text/plain; charset=x-user-defined');
									}
								}
							}
						});
						if (typeof VBArray !== 'undefined') {
							Object.defineProperty(xhrPrototype, 'response', {
								get: function xmlHttpRequestResponseGet() {
									if (this.responseType === 'arraybuffer') {
										return new Uint8Array(new VBArray(this.responseBody).toArray());
									}
									return this.responseText;
								}
							});
							return;
						}
						Object.defineProperty(xhrPrototype, 'response', {
							get: function xmlHttpRequestResponseGet() {
								if (this.responseType !== 'arraybuffer') {
									return this.responseText;
								}
								var text = this.responseText;
								var i, n = text.length;
								var result = new Uint8Array(n);
								for (i = 0; i < n; ++i) {
									result[i] = text.charCodeAt(i) & 0xFF;
								}
								return result.buffer;
							}
						});
					}());
					(function checkWindowBtoaCompatibility() {
						if ('btoa' in globalScope) {
							return;
						}
						var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
						globalScope.btoa = function (chars) {
							var buffer = '';
							var i, n;
							for (i = 0, n = chars.length; i < n; i += 3) {
								var b1 = chars.charCodeAt(i) & 0xFF;
								var b2 = chars.charCodeAt(i + 1) & 0xFF;
								var b3 = chars.charCodeAt(i + 2) & 0xFF;
								var d1 = b1 >> 2, d2 = (b1 & 3) << 4 | b2 >> 4;
								var d3 = i + 1 < n ? (b2 & 0xF) << 2 | b3 >> 6 : 64;
								var d4 = i + 2 < n ? b3 & 0x3F : 64;
								buffer += digits.charAt(d1) + digits.charAt(d2) + digits.charAt(d3) + digits.charAt(d4);
							}
							return buffer;
						};
					}());
					(function checkWindowAtobCompatibility() {
						if ('atob' in globalScope) {
							return;
						}
						var digits = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
						globalScope.atob = function (input) {
							input = input.replace(/=+$/, '');
							if (input.length % 4 === 1) {
								throw new Error('bad atob input');
							}
							for (var bc = 0, bs, buffer, idx = 0, output = ''; buffer = input.charAt(idx++); ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer, bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0) {
								buffer = digits.indexOf(buffer);
							}
							return output;
						};
					}());
					(function checkFunctionPrototypeBindCompatibility() {
						if (typeof Function.prototype.bind !== 'undefined') {
							return;
						}
						Function.prototype.bind = function functionPrototypeBind(obj) {
							var fn = this, headArgs = Array.prototype.slice.call(arguments, 1);
							var bound = function functionPrototypeBindBound() {
								var args = headArgs.concat(Array.prototype.slice.call(arguments));
								return fn.apply(obj, args);
							};
							return bound;
						};
					}());
					(function checkDatasetProperty() {
						if (!hasDOM) {
							return;
						}
						var div = document.createElement('div');
						if ('dataset' in div) {
							return;
						}
						Object.defineProperty(HTMLElement.prototype, 'dataset', {
							get: function () {
								if (this._dataset) {
									return this._dataset;
								}
								var dataset = {};
								for (var j = 0, jj = this.attributes.length; j < jj; j++) {
									var attribute = this.attributes[j];
									if (attribute.name.substring(0, 5) !== 'data-') {
										continue;
									}
									var key = attribute.name.substring(5).replace(/\-([a-z])/g, function (all, ch) {
										return ch.toUpperCase();
									});
									dataset[key] = attribute.value;
								}
								Object.defineProperty(this, '_dataset', {
									value: dataset,
									writable: false,
									enumerable: false
								});
								return dataset;
							},
							enumerable: true
						});
					}());
					(function checkClassListProperty() {
						function changeList(element, itemName, add, remove) {
							var s = element.className || '';
							var list = s.split(/\s+/g);
							if (list[0] === '') {
								list.shift();
							}
							var index = list.indexOf(itemName);
							if (index < 0 && add) {
								list.push(itemName);
							}
							if (index >= 0 && remove) {
								list.splice(index, 1);
							}
							element.className = list.join(' ');
							return index >= 0;
						}
						if (!hasDOM) {
							return;
						}
						var div = document.createElement('div');
						if ('classList' in div) {
							return;
						}
						var classListPrototype = {
							add: function (name) {
								changeList(this.element, name, true, false);
							},
							contains: function (name) {
								return changeList(this.element, name, false, false);
							},
							remove: function (name) {
								changeList(this.element, name, false, true);
							},
							toggle: function (name) {
								changeList(this.element, name, true, true);
							}
						};
						Object.defineProperty(HTMLElement.prototype, 'classList', {
							get: function () {
								if (this._classList) {
									return this._classList;
								}
								var classList = Object.create(classListPrototype, {
									element: {
										value: this,
										writable: false,
										enumerable: true
									}
								});
								Object.defineProperty(this, '_classList', {
									value: classList,
									writable: false,
									enumerable: false
								});
								return classList;
							},
							enumerable: true
						});
					}());
					(function checkWorkerConsoleCompatibility() {
						if (typeof importScripts === 'undefined' || 'console' in globalScope) {
							return;
						}
						var consoleTimer = {};
						var workerConsole = {
							log: function log() {
								var args = Array.prototype.slice.call(arguments);
								globalScope.postMessage({
									targetName: 'main',
									action: 'console_log',
									data: args
								});
							},
							error: function error() {
								var args = Array.prototype.slice.call(arguments);
								globalScope.postMessage({
									targetName: 'main',
									action: 'console_error',
									data: args
								});
							},
							time: function time(name) {
								consoleTimer[name] = Date.now();
							},
							timeEnd: function timeEnd(name) {
								var time = consoleTimer[name];
								if (!time) {
									throw new Error('Unknown timer name ' + name);
								}
								this.log('Timer:', name, Date.now() - time);
							}
						};
						globalScope.console = workerConsole;
					}());
					(function checkConsoleCompatibility() {
						if (!hasDOM) {
							return;
						}
						if (!('console' in window)) {
							window.console = {
								log: function () {
								},
								error: function () {
								},
								warn: function () {
								}
							};
							return;
						}
						if (!('bind' in console.log)) {
							console.log = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.log);
							console.error = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.error);
							console.warn = function (fn) {
								return function (msg) {
									return fn(msg);
								};
							}(console.warn);
							return;
						}
					}());
					(function checkOnClickCompatibility() {
						function ignoreIfTargetDisabled(event) {
							if (isDisabled(event.target)) {
								event.stopPropagation();
							}
						}
						function isDisabled(node) {
							return node.disabled || node.parentNode && isDisabled(node.parentNode);
						}
						if (isOpera) {
							document.addEventListener('click', ignoreIfTargetDisabled, true);
						}
					}());
					(function checkOnBlobSupport() {
						if (isIE || isIOSChrome) {
							PDFJS.disableCreateObjectURL = true;
						}
					}());
					(function checkNavigatorLanguage() {
						if (typeof navigator === 'undefined') {
							return;
						}
						if ('language' in navigator) {
							return;
						}
						PDFJS.locale = navigator.userLanguage || 'en-US';
					}());
					(function checkRangeRequests() {
						if (isSafari || isAndroidPre3 || isChromeWithRangeBug || isIOS) {
							PDFJS.disableRange = true;
							PDFJS.disableStream = true;
						}
					}());
					(function checkHistoryManipulation() {
						if (!hasDOM) {
							return;
						}
						if (!history.pushState || isAndroidPre3) {
							PDFJS.disableHistory = true;
						}
					}());
					(function checkSetPresenceInImageData() {
						if (!hasDOM) {
							return;
						}
						if (window.CanvasPixelArray) {
							if (typeof window.CanvasPixelArray.prototype.set !== 'function') {
								window.CanvasPixelArray.prototype.set = function (arr) {
									for (var i = 0, ii = this.length; i < ii; i++) {
										this[i] = arr[i];
									}
								};
							}
						} else {
							var polyfill = false, versionMatch;
							if (isChrome) {
								versionMatch = userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
								polyfill = versionMatch && parseInt(versionMatch[2]) < 21;
							} else if (isAndroid) {
								polyfill = isAndroidPre5;
							} else if (isSafari) {
								versionMatch = userAgent.match(/Version\/([0-9]+)\.([0-9]+)\.([0-9]+) Safari\//);
								polyfill = versionMatch && parseInt(versionMatch[1]) < 6;
							}
							if (polyfill) {
								var contextPrototype = window.CanvasRenderingContext2D.prototype;
								var createImageData = contextPrototype.createImageData;
								contextPrototype.createImageData = function (w, h) {
									var imageData = createImageData.call(this, w, h);
									imageData.data.set = function (arr) {
										for (var i = 0, ii = this.length; i < ii; i++) {
											this[i] = arr[i];
										}
									};
									return imageData;
								};
								contextPrototype = null;
							}
						}
					}());
					(function checkRequestAnimationFrame() {
						function fakeRequestAnimationFrame(callback) {
							window.setTimeout(callback, 20);
						}
						if (!hasDOM) {
							return;
						}
						if (isIOS) {
							window.requestAnimationFrame = fakeRequestAnimationFrame;
							return;
						}
						if ('requestAnimationFrame' in window) {
							return;
						}
						window.requestAnimationFrame = window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || fakeRequestAnimationFrame;
					}());
					(function checkCanvasSizeLimitation() {
						if (isIOS || isAndroid) {
							PDFJS.maxCanvasPixels = 5242880;
						}
					}());
					(function checkFullscreenSupport() {
						if (!hasDOM) {
							return;
						}
						if (isIE && window.parent !== window) {
							PDFJS.disableFullscreen = true;
						}
					}());
					(function checkCurrentScript() {
						if (!hasDOM) {
							return;
						}
						if ('currentScript' in document) {
							return;
						}
						Object.defineProperty(document, 'currentScript', {
							get: function () {
								var scripts = document.getElementsByTagName('script');
								return scripts[scripts.length - 1];
							},
							enumerable: true,
							configurable: true
						});
					}());
					(function checkInputTypeNumberAssign() {
						if (!hasDOM) {
							return;
						}
						var el = document.createElement('input');
						try {
							el.type = 'number';
						} catch (ex) {
							var inputProto = el.constructor.prototype;
							var typeProperty = Object.getOwnPropertyDescriptor(inputProto, 'type');
							Object.defineProperty(inputProto, 'type', {
								get: function () {
									return typeProperty.get.call(this);
								},
								set: function (value) {
									typeProperty.set.call(this, value === 'number' ? 'text' : value);
								},
								enumerable: true,
								configurable: true
							});
						}
					}());
					(function checkDocumentReadyState() {
						if (!hasDOM) {
							return;
						}
						if (!document.attachEvent) {
							return;
						}
						var documentProto = document.constructor.prototype;
						var readyStateProto = Object.getOwnPropertyDescriptor(documentProto, 'readyState');
						Object.defineProperty(documentProto, 'readyState', {
							get: function () {
								var value = readyStateProto.get.call(this);
								return value === 'interactive' ? 'loading' : value;
							},
							set: function (value) {
								readyStateProto.set.call(this, value);
							},
							enumerable: true,
							configurable: true
						});
					}());
					(function checkChildNodeRemove() {
						if (!hasDOM) {
							return;
						}
						if (typeof Element.prototype.remove !== 'undefined') {
							return;
						}
						Element.prototype.remove = function () {
							if (this.parentNode) {
								this.parentNode.removeChild(this);
							}
						};
					}());
					(function checkPromise() {
						if (globalScope.Promise) {
							if (typeof globalScope.Promise.all !== 'function') {
								globalScope.Promise.all = function (iterable) {
									var count = 0, results = [], resolve, reject;
									var promise = new globalScope.Promise(function (resolve_, reject_) {
										resolve = resolve_;
										reject = reject_;
									});
									iterable.forEach(function (p, i) {
										count++;
										p.then(function (result) {
											results[i] = result;
											count--;
											if (count === 0) {
												resolve(results);
											}
										}, reject);
									});
									if (count === 0) {
										resolve(results);
									}
									return promise;
								};
							}
							if (typeof globalScope.Promise.resolve !== 'function') {
								globalScope.Promise.resolve = function (value) {
									return new globalScope.Promise(function (resolve) {
										resolve(value);
									});
								};
							}
							if (typeof globalScope.Promise.reject !== 'function') {
								globalScope.Promise.reject = function (reason) {
									return new globalScope.Promise(function (resolve, reject) {
										reject(reason);
									});
								};
							}
							if (typeof globalScope.Promise.prototype.catch !== 'function') {
								globalScope.Promise.prototype.catch = function (onReject) {
									return globalScope.Promise.prototype.then(undefined, onReject);
								};
							}
							return;
						}
						var STATUS_PENDING = 0;
						var STATUS_RESOLVED = 1;
						var STATUS_REJECTED = 2;
						var REJECTION_TIMEOUT = 500;
						var HandlerManager = {
							handlers: [],
							running: false,
							unhandledRejections: [],
							pendingRejectionCheck: false,
							scheduleHandlers: function scheduleHandlers(promise) {
								if (promise._status === STATUS_PENDING) {
									return;
								}
								this.handlers = this.handlers.concat(promise._handlers);
								promise._handlers = [];
								if (this.running) {
									return;
								}
								this.running = true;
								setTimeout(this.runHandlers.bind(this), 0);
							},
							runHandlers: function runHandlers() {
								var RUN_TIMEOUT = 1;
								var timeoutAt = Date.now() + RUN_TIMEOUT;
								while (this.handlers.length > 0) {
									var handler = this.handlers.shift();
									var nextStatus = handler.thisPromise._status;
									var nextValue = handler.thisPromise._value;
									try {
										if (nextStatus === STATUS_RESOLVED) {
											if (typeof handler.onResolve === 'function') {
												nextValue = handler.onResolve(nextValue);
											}
										} else if (typeof handler.onReject === 'function') {
											nextValue = handler.onReject(nextValue);
											nextStatus = STATUS_RESOLVED;
											if (handler.thisPromise._unhandledRejection) {
												this.removeUnhandeledRejection(handler.thisPromise);
											}
										}
									} catch (ex) {
										nextStatus = STATUS_REJECTED;
										nextValue = ex;
									}
									handler.nextPromise._updateStatus(nextStatus, nextValue);
									if (Date.now() >= timeoutAt) {
										break;
									}
								}
								if (this.handlers.length > 0) {
									setTimeout(this.runHandlers.bind(this), 0);
									return;
								}
								this.running = false;
							},
							addUnhandledRejection: function addUnhandledRejection(promise) {
								this.unhandledRejections.push({
									promise: promise,
									time: Date.now()
								});
								this.scheduleRejectionCheck();
							},
							removeUnhandeledRejection: function removeUnhandeledRejection(promise) {
								promise._unhandledRejection = false;
								for (var i = 0; i < this.unhandledRejections.length; i++) {
									if (this.unhandledRejections[i].promise === promise) {
										this.unhandledRejections.splice(i);
										i--;
									}
								}
							},
							scheduleRejectionCheck: function scheduleRejectionCheck() {
								if (this.pendingRejectionCheck) {
									return;
								}
								this.pendingRejectionCheck = true;
								setTimeout(function rejectionCheck() {
									this.pendingRejectionCheck = false;
									var now = Date.now();
									for (var i = 0; i < this.unhandledRejections.length; i++) {
										if (now - this.unhandledRejections[i].time > REJECTION_TIMEOUT) {
											var unhandled = this.unhandledRejections[i].promise._value;
											var msg = 'Unhandled rejection: ' + unhandled;
											if (unhandled.stack) {
												msg += '\n' + unhandled.stack;
											}
											try {
												throw new Error(msg);
											} catch (_) {
												console.warn(msg);
											}
											this.unhandledRejections.splice(i);
											i--;
										}
									}
									if (this.unhandledRejections.length) {
										this.scheduleRejectionCheck();
									}
								}.bind(this), REJECTION_TIMEOUT);
							}
						};
						var Promise = function Promise(resolver) {
							this._status = STATUS_PENDING;
							this._handlers = [];
							try {
								resolver.call(this, this._resolve.bind(this), this._reject.bind(this));
							} catch (e) {
								this._reject(e);
							}
						};
						Promise.all = function Promise_all(promises) {
							var resolveAll, rejectAll;
							var deferred = new Promise(function (resolve, reject) {
								resolveAll = resolve;
								rejectAll = reject;
							});
							var unresolved = promises.length;
							var results = [];
							if (unresolved === 0) {
								resolveAll(results);
								return deferred;
							}
							function reject(reason) {
								if (deferred._status === STATUS_REJECTED) {
									return;
								}
								results = [];
								rejectAll(reason);
							}
							for (var i = 0, ii = promises.length; i < ii; ++i) {
								var promise = promises[i];
								var resolve = function (i) {
									return function (value) {
										if (deferred._status === STATUS_REJECTED) {
											return;
										}
										results[i] = value;
										unresolved--;
										if (unresolved === 0) {
											resolveAll(results);
										}
									};
								}(i);
								if (Promise.isPromise(promise)) {
									promise.then(resolve, reject);
								} else {
									resolve(promise);
								}
							}
							return deferred;
						};
						Promise.isPromise = function Promise_isPromise(value) {
							return value && typeof value.then === 'function';
						};
						Promise.resolve = function Promise_resolve(value) {
							return new Promise(function (resolve) {
								resolve(value);
							});
						};
						Promise.reject = function Promise_reject(reason) {
							return new Promise(function (resolve, reject) {
								reject(reason);
							});
						};
						Promise.prototype = {
							_status: null,
							_value: null,
							_handlers: null,
							_unhandledRejection: null,
							_updateStatus: function Promise__updateStatus(status, value) {
								if (this._status === STATUS_RESOLVED || this._status === STATUS_REJECTED) {
									return;
								}
								if (status === STATUS_RESOLVED && Promise.isPromise(value)) {
									value.then(this._updateStatus.bind(this, STATUS_RESOLVED), this._updateStatus.bind(this, STATUS_REJECTED));
									return;
								}
								this._status = status;
								this._value = value;
								if (status === STATUS_REJECTED && this._handlers.length === 0) {
									this._unhandledRejection = true;
									HandlerManager.addUnhandledRejection(this);
								}
								HandlerManager.scheduleHandlers(this);
							},
							_resolve: function Promise_resolve(value) {
								this._updateStatus(STATUS_RESOLVED, value);
							},
							_reject: function Promise_reject(reason) {
								this._updateStatus(STATUS_REJECTED, reason);
							},
							then: function Promise_then(onResolve, onReject) {
								var nextPromise = new Promise(function (resolve, reject) {
									this.resolve = resolve;
									this.reject = reject;
								});
								this._handlers.push({
									thisPromise: this,
									onResolve: onResolve,
									onReject: onReject,
									nextPromise: nextPromise
								});
								HandlerManager.scheduleHandlers(this);
								return nextPromise;
							},
							catch: function Promise_catch(onReject) {
								return this.then(undefined, onReject);
							}
						};
						globalScope.Promise = Promise;
					}());
					(function checkWeakMap() {
						if (globalScope.WeakMap) {
							return;
						}
						var id = 0;
						function WeakMap() {
							this.id = '$weakmap' + id++;
						}
						WeakMap.prototype = {
							has: function (obj) {
								return !!Object.getOwnPropertyDescriptor(obj, this.id);
							},
							get: function (obj, defaultValue) {
								return this.has(obj) ? obj[this.id] : defaultValue;
							},
							set: function (obj, value) {
								Object.defineProperty(obj, this.id, {
									value: value,
									enumerable: false,
									configurable: true
								});
							},
							delete: function (obj) {
								delete obj[this.id];
							}
						};
						globalScope.WeakMap = WeakMap;
					}());
					(function checkURLConstructor() {
						var hasWorkingUrl = false;
						try {
							if (typeof URL === 'function' && typeof URL.prototype === 'object' && 'origin' in URL.prototype) {
								var u = new URL('b', 'http://a');
								u.pathname = 'c%20d';
								hasWorkingUrl = u.href === 'http://a/c%20d';
							}
						} catch (e) {
						}
						if (hasWorkingUrl) {
							return;
						}
						var relative = Object.create(null);
						relative['ftp'] = 21;
						relative['file'] = 0;
						relative['gopher'] = 70;
						relative['http'] = 80;
						relative['https'] = 443;
						relative['ws'] = 80;
						relative['wss'] = 443;
						var relativePathDotMapping = Object.create(null);
						relativePathDotMapping['%2e'] = '.';
						relativePathDotMapping['.%2e'] = '..';
						relativePathDotMapping['%2e.'] = '..';
						relativePathDotMapping['%2e%2e'] = '..';
						function isRelativeScheme(scheme) {
							return relative[scheme] !== undefined;
						}
						function invalid() {
							clear.call(this);
							this._isInvalid = true;
						}
						function IDNAToASCII(h) {
							if (h === '') {
								invalid.call(this);
							}
							return h.toLowerCase();
						}
						function percentEscape(c) {
							var unicode = c.charCodeAt(0);
							if (unicode > 0x20 && unicode < 0x7F && [
								0x22,
								0x23,
								0x3C,
								0x3E,
								0x3F,
								0x60
							].indexOf(unicode) === -1) {
								return c;
							}
							return encodeURIComponent(c);
						}
						function percentEscapeQuery(c) {
							var unicode = c.charCodeAt(0);
							if (unicode > 0x20 && unicode < 0x7F && [
								0x22,
								0x23,
								0x3C,
								0x3E,
								0x60
							].indexOf(unicode) === -1) {
								return c;
							}
							return encodeURIComponent(c);
						}
						var EOF, ALPHA = /[a-zA-Z]/, ALPHANUMERIC = /[a-zA-Z0-9\+\-\.]/;
						function parse(input, stateOverride, base) {
							function err(message) {
								errors.push(message);
							}
							var state = stateOverride || 'scheme start', cursor = 0, buffer = '', seenAt = false, seenBracket = false, errors = [];
							loop:
							while ((input[cursor - 1] !== EOF || cursor === 0) && !this._isInvalid) {
								var c = input[cursor];
								switch (state) {
									case 'scheme start':
										if (c && ALPHA.test(c)) {
											buffer += c.toLowerCase();
											state = 'scheme';
										} else if (!stateOverride) {
											buffer = '';
											state = 'no scheme';
											continue;
										} else {
											err('Invalid scheme.');
											break loop;
										}
										break;
									case 'scheme':
										if (c && ALPHANUMERIC.test(c)) {
											buffer += c.toLowerCase();
										} else if (c === ':') {
											this._scheme = buffer;
											buffer = '';
											if (stateOverride) {
												break loop;
											}
											if (isRelativeScheme(this._scheme)) {
												this._isRelative = true;
											}
											if (this._scheme === 'file') {
												state = 'relative';
											} else if (this._isRelative && base && base._scheme === this._scheme) {
												state = 'relative or authority';
											} else if (this._isRelative) {
												state = 'authority first slash';
											} else {
												state = 'scheme data';
											}
										} else if (!stateOverride) {
											buffer = '';
											cursor = 0;
											state = 'no scheme';
											continue;
										} else if (EOF === c) {
											break loop;
										} else {
											err('Code point not allowed in scheme: ' + c);
											break loop;
										}
										break;
									case 'scheme data':
										if (c === '?') {
											this._query = '?';
											state = 'query';
										} else if (c === '#') {
											this._fragment = '#';
											state = 'fragment';
										} else {
											if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
												this._schemeData += percentEscape(c);
											}
										}
										break;
									case 'no scheme':
										if (!base || !isRelativeScheme(base._scheme)) {
											err('Missing scheme.');
											invalid.call(this);
										} else {
											state = 'relative';
											continue;
										}
										break;
									case 'relative or authority':
										if (c === '/' && input[cursor + 1] === '/') {
											state = 'authority ignore slashes';
										} else {
											err('Expected /, got: ' + c);
											state = 'relative';
											continue;
										}
										break;
									case 'relative':
										this._isRelative = true;
										if ('file' !== this._scheme) {
											this._scheme = base._scheme;
										}
										if (EOF === c) {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = base._query;
											this._username = base._username;
											this._password = base._password;
											break loop;
										} else if (c === '/' || c === '\\') {
											if (c === '\\') {
												err('\\ is an invalid code point.');
											}
											state = 'relative slash';
										} else if (c === '?') {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = '?';
											this._username = base._username;
											this._password = base._password;
											state = 'query';
										} else if (c === '#') {
											this._host = base._host;
											this._port = base._port;
											this._path = base._path.slice();
											this._query = base._query;
											this._fragment = '#';
											this._username = base._username;
											this._password = base._password;
											state = 'fragment';
										} else {
											var nextC = input[cursor + 1];
											var nextNextC = input[cursor + 2];
											if ('file' !== this._scheme || !ALPHA.test(c) || nextC !== ':' && nextC !== '|' || EOF !== nextNextC && '/' !== nextNextC && '\\' !== nextNextC && '?' !== nextNextC && '#' !== nextNextC) {
												this._host = base._host;
												this._port = base._port;
												this._username = base._username;
												this._password = base._password;
												this._path = base._path.slice();
												this._path.pop();
											}
											state = 'relative path';
											continue;
										}
										break;
									case 'relative slash':
										if (c === '/' || c === '\\') {
											if (c === '\\') {
												err('\\ is an invalid code point.');
											}
											if (this._scheme === 'file') {
												state = 'file host';
											} else {
												state = 'authority ignore slashes';
											}
										} else {
											if ('file' !== this._scheme) {
												this._host = base._host;
												this._port = base._port;
												this._username = base._username;
												this._password = base._password;
											}
											state = 'relative path';
											continue;
										}
										break;
									case 'authority first slash':
										if (c === '/') {
											state = 'authority second slash';
										} else {
											err('Expected \'/\', got: ' + c);
											state = 'authority ignore slashes';
											continue;
										}
										break;
									case 'authority second slash':
										state = 'authority ignore slashes';
										if ('/' !== c) {
											err('Expected \'/\', got: ' + c);
											continue;
										}
										break;
									case 'authority ignore slashes':
										if ('/' !== c && '\\' !== c) {
											state = 'authority';
											continue;
										} else {
											err('Expected authority, got: ' + c);
										}
										break;
									case 'authority':
										if (c === '@') {
											if (seenAt) {
												err('@ already seen.');
												buffer += '%40';
											}
											seenAt = true;
											for (var i = 0; i < buffer.length; i++) {
												var cp = buffer[i];
												if (cp === '\t' || cp === '\n' || cp === '\r') {
													err('Invalid whitespace in authority.');
													continue;
												}
												if (cp === ':' && this._password === null) {
													this._password = '';
													continue;
												}
												var tempC = percentEscape(cp);
												if (null !== this._password) {
													this._password += tempC;
												} else {
													this._username += tempC;
												}
											}
											buffer = '';
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											cursor -= buffer.length;
											buffer = '';
											state = 'host';
											continue;
										} else {
											buffer += c;
										}
										break;
									case 'file host':
										if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											if (buffer.length === 2 && ALPHA.test(buffer[0]) && (buffer[1] === ':' || buffer[1] === '|')) {
												state = 'relative path';
											} else if (buffer.length === 0) {
												state = 'relative path start';
											} else {
												this._host = IDNAToASCII.call(this, buffer);
												buffer = '';
												state = 'relative path start';
											}
											continue;
										} else if (c === '\t' || c === '\n' || c === '\r') {
											err('Invalid whitespace in file host.');
										} else {
											buffer += c;
										}
										break;
									case 'host':
									case 'hostname':
										if (c === ':' && !seenBracket) {
											this._host = IDNAToASCII.call(this, buffer);
											buffer = '';
											state = 'port';
											if (stateOverride === 'hostname') {
												break loop;
											}
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#') {
											this._host = IDNAToASCII.call(this, buffer);
											buffer = '';
											state = 'relative path start';
											if (stateOverride) {
												break loop;
											}
											continue;
										} else if ('\t' !== c && '\n' !== c && '\r' !== c) {
											if (c === '[') {
												seenBracket = true;
											} else if (c === ']') {
												seenBracket = false;
											}
											buffer += c;
										} else {
											err('Invalid code point in host/hostname: ' + c);
										}
										break;
									case 'port':
										if (/[0-9]/.test(c)) {
											buffer += c;
										} else if (c === EOF || c === '/' || c === '\\' || c === '?' || c === '#' || stateOverride) {
											if ('' !== buffer) {
												var temp = parseInt(buffer, 10);
												if (temp !== relative[this._scheme]) {
													this._port = temp + '';
												}
												buffer = '';
											}
											if (stateOverride) {
												break loop;
											}
											state = 'relative path start';
											continue;
										} else if (c === '\t' || c === '\n' || c === '\r') {
											err('Invalid code point in port: ' + c);
										} else {
											invalid.call(this);
										}
										break;
									case 'relative path start':
										if (c === '\\') {
											err('\'\\\' not allowed in path.');
										}
										state = 'relative path';
										if ('/' !== c && '\\' !== c) {
											continue;
										}
										break;
									case 'relative path':
										if (c === EOF || c === '/' || c === '\\' || !stateOverride && (c === '?' || c === '#')) {
											if (c === '\\') {
												err('\\ not allowed in relative path.');
											}
											var tmp;
											if (tmp = relativePathDotMapping[buffer.toLowerCase()]) {
												buffer = tmp;
											}
											if (buffer === '..') {
												this._path.pop();
												if ('/' !== c && '\\' !== c) {
													this._path.push('');
												}
											} else if (buffer === '.' && '/' !== c && '\\' !== c) {
												this._path.push('');
											} else if ('.' !== buffer) {
												if (this._scheme === 'file' && this._path.length === 0 && buffer.length === 2 && ALPHA.test(buffer[0]) && buffer[1] === '|') {
													buffer = buffer[0] + ':';
												}
												this._path.push(buffer);
											}
											buffer = '';
											if (c === '?') {
												this._query = '?';
												state = 'query';
											} else if (c === '#') {
												this._fragment = '#';
												state = 'fragment';
											}
										} else if ('\t' !== c && '\n' !== c && '\r' !== c) {
											buffer += percentEscape(c);
										}
										break;
									case 'query':
										if (!stateOverride && c === '#') {
											this._fragment = '#';
											state = 'fragment';
										} else if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
											this._query += percentEscapeQuery(c);
										}
										break;
									case 'fragment':
										if (EOF !== c && '\t' !== c && '\n' !== c && '\r' !== c) {
											this._fragment += c;
										}
										break;
								}
								cursor++;
							}
						}
						function clear() {
							this._scheme = '';
							this._schemeData = '';
							this._username = '';
							this._password = null;
							this._host = '';
							this._port = '';
							this._path = [];
							this._query = '';
							this._fragment = '';
							this._isInvalid = false;
							this._isRelative = false;
						}
						function JURL(url, base) {
							if (base !== undefined && !(base instanceof JURL)) {
								base = new JURL(String(base));
							}
							this._url = url;
							clear.call(this);
							var input = url.replace(/^[ \t\r\n\f]+|[ \t\r\n\f]+$/g, '');
							parse.call(this, input, null, base);
						}
						JURL.prototype = {
							toString: function () {
								return this.href;
							},
							get href() {
								if (this._isInvalid) {
									return this._url;
								}
								var authority = '';
								if ('' !== this._username || null !== this._password) {
									authority = this._username + (null !== this._password ? ':' + this._password : '') + '@';
								}
								return this.protocol + (this._isRelative ? '//' + authority + this.host : '') + this.pathname + this._query + this._fragment;
							},
							set href(href) {
								clear.call(this);
								parse.call(this, href);
							},
							get protocol() {
								return this._scheme + ':';
							},
							set protocol(protocol) {
								if (this._isInvalid) {
									return;
								}
								parse.call(this, protocol + ':', 'scheme start');
							},
							get host() {
								return this._isInvalid ? '' : this._port ? this._host + ':' + this._port : this._host;
							},
							set host(host) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, host, 'host');
							},
							get hostname() {
								return this._host;
							},
							set hostname(hostname) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, hostname, 'hostname');
							},
							get port() {
								return this._port;
							},
							set port(port) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								parse.call(this, port, 'port');
							},
							get pathname() {
								return this._isInvalid ? '' : this._isRelative ? '/' + this._path.join('/') : this._schemeData;
							},
							set pathname(pathname) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								this._path = [];
								parse.call(this, pathname, 'relative path start');
							},
							get search() {
								return this._isInvalid || !this._query || this._query === '?' ? '' : this._query;
							},
							set search(search) {
								if (this._isInvalid || !this._isRelative) {
									return;
								}
								this._query = '?';
								if (search[0] === '?') {
									search = search.slice(1);
								}
								parse.call(this, search, 'query');
							},
							get hash() {
								return this._isInvalid || !this._fragment || this._fragment === '#' ? '' : this._fragment;
							},
							set hash(hash) {
								if (this._isInvalid) {
									return;
								}
								this._fragment = '#';
								if (hash[0] === '#') {
									hash = hash.slice(1);
								}
								parse.call(this, hash, 'fragment');
							},
							get origin() {
								var host;
								if (this._isInvalid || !this._scheme) {
									return '';
								}
								switch (this._scheme) {
									case 'data':
									case 'file':
									case 'javascript':
									case 'mailto':
										return 'null';
								}
								host = this.host;
								if (!host) {
									return '';
								}
								return this._scheme + '://' + host;
							}
						};
						var OriginalURL = globalScope.URL;
						if (OriginalURL) {
							JURL.createObjectURL = function (blob) {
								return OriginalURL.createObjectURL.apply(OriginalURL, arguments);
							};
							JURL.revokeObjectURL = function (url) {
								OriginalURL.revokeObjectURL(url);
							};
						}
						globalScope.URL = JURL;
					}());
				}
				/* WEBPACK VAR INJECTION */
			}.call(exports, __w_pdfjs_require__(9)))

			/***/
		}),
/* 37 */
/***/ (function (module, exports, __w_pdfjs_require__) {

			"use strict";

			var pdfjsVersion = '1.7.354';
			var pdfjsBuild = '0f7548ba';
			var pdfjsCoreWorker = __w_pdfjs_require__(8);
			{
				__w_pdfjs_require__(19);
			}
			exports.WorkerMessageHandler = pdfjsCoreWorker.WorkerMessageHandler;

			/***/
		})
/******/]);
});
/*!
 * ui-select
 * http://github.com/angular-ui/ui-select
 * Version: 0.19.7 - 2017-04-15T14:28:36.649Z
 * License: MIT
 */
!function () {
	"use strict"; function e(e) { return angular.isUndefined(e) || null === e } var t = { TAB: 9, ENTER: 13, ESC: 27, SPACE: 32, LEFT: 37, UP: 38, RIGHT: 39, DOWN: 40, SHIFT: 16, CTRL: 17, ALT: 18, PAGE_UP: 33, PAGE_DOWN: 34, HOME: 36, END: 35, BACKSPACE: 8, DELETE: 46, COMMAND: 91, MAP: { 91: "COMMAND", 8: "BACKSPACE", 9: "TAB", 13: "ENTER", 16: "SHIFT", 17: "CTRL", 18: "ALT", 19: "PAUSEBREAK", 20: "CAPSLOCK", 27: "ESC", 32: "SPACE", 33: "PAGE_UP", 34: "PAGE_DOWN", 35: "END", 36: "HOME", 37: "LEFT", 38: "UP", 39: "RIGHT", 40: "DOWN", 43: "+", 44: "PRINTSCREEN", 45: "INSERT", 46: "DELETE", 48: "0", 49: "1", 50: "2", 51: "3", 52: "4", 53: "5", 54: "6", 55: "7", 56: "8", 57: "9", 59: ";", 61: "=", 65: "A", 66: "B", 67: "C", 68: "D", 69: "E", 70: "F", 71: "G", 72: "H", 73: "I", 74: "J", 75: "K", 76: "L", 77: "M", 78: "N", 79: "O", 80: "P", 81: "Q", 82: "R", 83: "S", 84: "T", 85: "U", 86: "V", 87: "W", 88: "X", 89: "Y", 90: "Z", 96: "0", 97: "1", 98: "2", 99: "3", 100: "4", 101: "5", 102: "6", 103: "7", 104: "8", 105: "9", 106: "*", 107: "+", 109: "-", 110: ".", 111: "/", 112: "F1", 113: "F2", 114: "F3", 115: "F4", 116: "F5", 117: "F6", 118: "F7", 119: "F8", 120: "F9", 121: "F10", 122: "F11", 123: "F12", 144: "NUMLOCK", 145: "SCROLLLOCK", 186: ";", 187: "=", 188: ",", 189: "-", 190: ".", 191: "/", 192: "`", 219: "[", 220: "\\", 221: "]", 222: "'" }, isControl: function (e) { var s = e.which; switch (s) { case t.COMMAND: case t.SHIFT: case t.CTRL: case t.ALT: return !0 }return !!(e.metaKey || e.ctrlKey || e.altKey) }, isFunctionKey: function (e) { return e = e.which ? e.which : e, e >= 112 && e <= 123 }, isVerticalMovement: function (e) { return ~[t.UP, t.DOWN].indexOf(e) }, isHorizontalMovement: function (e) { return ~[t.LEFT, t.RIGHT, t.BACKSPACE, t.DELETE].indexOf(e) }, toSeparator: function (e) { var s = { ENTER: "\n", TAB: "\t", SPACE: " " }[e]; return s ? s : t[e] ? void 0 : e } }; void 0 === angular.element.prototype.querySelectorAll && (angular.element.prototype.querySelectorAll = function (e) { return angular.element(this[0].querySelectorAll(e)) }), void 0 === angular.element.prototype.closest && (angular.element.prototype.closest = function (e) { for (var t = this[0], s = t.matches || t.webkitMatchesSelector || t.mozMatchesSelector || t.msMatchesSelector; t;) { if (s.bind(t)(e)) return t; t = t.parentElement } return !1 }); var s = 0, i = angular.module("ui.select", []).constant("uiSelectConfig", { theme: "bootstrap", searchEnabled: !0, sortable: !1, placeholder: "", refreshDelay: 1e3, closeOnSelect: !0, skipFocusser: !1, dropdownPosition: "auto", removeSelected: !0, resetSearchInput: !0, generateId: function () { return s++ }, appendToBody: !1, spinnerEnabled: !1, spinnerClass: "glyphicon glyphicon-refresh ui-select-spin", backspaceReset: !0 }).service("uiSelectMinErr", function () { var e = angular.$$minErr("ui.select"); return function () { var t = e.apply(this, arguments), s = t.message.replace(new RegExp("\nhttp://errors.angularjs.org/.*"), ""); return new Error(s) } }).directive("uisTranscludeAppend", function () { return { link: function (e, t, s, i, c) { c(e, function (e) { t.append(e) }) } } }).filter("highlight", function () { function e(e) { return ("" + e).replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1") } return function (t, s) { return s && t ? ("" + t).replace(new RegExp(e(s), "gi"), '<span class="ui-select-highlight">$&</span>') : t } }).factory("uisOffset", ["$document", "$window", function (e, t) { return function (s) { var i = s[0].getBoundingClientRect(); return { width: i.width || s.prop("offsetWidth"), height: i.height || s.prop("offsetHeight"), top: i.top + (t.pageYOffset || e[0].documentElement.scrollTop), left: i.left + (t.pageXOffset || e[0].documentElement.scrollLeft) } } }]); i.factory("$$uisDebounce", ["$timeout", function (e) { return function (t, s) { var i; return function () { var c = this, n = Array.prototype.slice.call(arguments); i && e.cancel(i), i = e(function () { t.apply(c, n) }, s) } } }]), i.directive("uiSelectChoices", ["uiSelectConfig", "uisRepeatParser", "uiSelectMinErr", "$compile", "$window", function (e, t, s, i, c) { return { restrict: "EA", require: "^uiSelect", replace: !0, transclude: !0, templateUrl: function (t) { t.addClass("ui-select-choices"); var s = t.parent().attr("theme") || e.theme; return s + "/choices.tpl.html" }, compile: function (i, n) { if (!n.repeat) throw s("repeat", "Expected 'repeat' expression."); var l = n.groupBy, a = n.groupFilter; if (l) { var r = i.querySelectorAll(".ui-select-choices-group"); if (1 !== r.length) throw s("rows", "Expected 1 .ui-select-choices-group but got '{0}'.", r.length); r.attr("ng-repeat", t.getGroupNgRepeatExpression()) } var o = t.parse(n.repeat), u = i.querySelectorAll(".ui-select-choices-row"); if (1 !== u.length) throw s("rows", "Expected 1 .ui-select-choices-row but got '{0}'.", u.length); u.attr("ng-repeat", o.repeatExpression(l)).attr("ng-if", "$select.open"); var d = i.querySelectorAll(".ui-select-choices-row-inner"); if (1 !== d.length) throw s("rows", "Expected 1 .ui-select-choices-row-inner but got '{0}'.", d.length); d.attr("uis-transclude-append", ""); var p = c.document.addEventListener ? u : d; return p.attr("ng-click", "$select.select(" + o.itemName + ",$select.skipFocusser,$event)"), function (t, s, c, n) { n.parseRepeatAttr(c.repeat, l, a), n.disableChoiceExpression = c.uiDisableChoice, n.onHighlightCallback = c.onHighlight, n.minimumInputLength = parseInt(c.minimumInputLength) || 0, n.dropdownPosition = c.position ? c.position.toLowerCase() : e.dropdownPosition, t.$watch("$select.search", function (e) { e && !n.open && n.multiple && n.activate(!1, !0), n.activeIndex = n.tagging.isActivated ? -1 : 0, !c.minimumInputLength || n.search.length >= c.minimumInputLength ? n.refresh(c.refresh) : n.items = [] }), c.$observe("refreshDelay", function () { var s = t.$eval(c.refreshDelay); n.refreshDelay = void 0 !== s ? s : e.refreshDelay }), t.$watch("$select.open", function (e) { e ? (i.attr("role", "listbox"), n.refresh(c.refresh)) : s.removeAttr("role") }) } } } }]), i.controller("uiSelectCtrl", ["$scope", "$element", "$timeout", "$filter", "$$uisDebounce", "uisRepeatParser", "uiSelectMinErr", "uiSelectConfig", "$parse", "$injector", "$window", function (s, i, c, n, l, a, r, o, u, d, p) { function h(e, t, s) { if (e.findIndex) return e.findIndex(t, s); for (var i, c = Object(e), n = c.length >>> 0, l = 0; l < n; l++)if (i = c[l], t.call(s, i, l, c)) return l; return -1 } function g() { y.resetSearchInput && (y.search = x, y.selected && y.items.length && !y.multiple && (y.activeIndex = h(y.items, function (e) { return angular.equals(this, e) }, y.selected))) } function f(e, t) { var s, i, c = []; for (s = 0; s < t.length; s++)for (i = 0; i < e.length; i++)e[i].name == [t[s]] && c.push(e[i]); return c } function v(e, t) { var s = I.indexOf(e); t && s === -1 && I.push(e), !t && s > -1 && I.splice(s, 1) } function m(e) { return I.indexOf(e) > -1 } function $(e) { function t(e, t) { var s = i.indexOf(e); t && s === -1 && i.push(e), !t && s > -1 && i.splice(s, 1) } function s(e) { return i.indexOf(e) > -1 } if (e) { var i = []; y.isLocked = function (e, i) { var c = !1, n = y.selected[i]; return n && (e ? (c = !!e.$eval(y.lockChoiceExpression), t(n, c)) : c = s(n)), c } } } function b(e) { var s = !0; switch (e) { case t.DOWN: if (!y.open && y.multiple) y.activate(!1, !0); else if (y.activeIndex < y.items.length - 1) for (var i = ++y.activeIndex; m(y.items[i]) && i < y.items.length;)y.activeIndex = ++i; break; case t.UP: var c = 0 === y.search.length && y.tagging.isActivated ? -1 : 0; if (!y.open && y.multiple) y.activate(!1, !0); else if (y.activeIndex > c) for (var n = --y.activeIndex; m(y.items[n]) && n > c;)y.activeIndex = --n; break; case t.TAB: y.multiple && !y.open || y.select(y.items[y.activeIndex], !0); break; case t.ENTER: y.open && (y.tagging.isActivated || y.activeIndex >= 0) ? y.select(y.items[y.activeIndex], y.skipFocusser) : y.activate(!1, !0); break; case t.ESC: y.close(); break; default: s = !1 }return s } function w() { var e = i.querySelectorAll(".ui-select-choices-content"), t = e.querySelectorAll(".ui-select-choices-row"); if (t.length < 1) throw r("choices", "Expected multiple .ui-select-choices-row but got '{0}'.", t.length); if (!(y.activeIndex < 0)) { var s = t[y.activeIndex], c = s.offsetTop + s.clientHeight - e[0].scrollTop, n = e[0].offsetHeight; c > n ? e[0].scrollTop += c - n : c < s.clientHeight && (y.isGrouped && 0 === y.activeIndex ? e[0].scrollTop = 0 : e[0].scrollTop -= s.clientHeight - c) } } var y = this, x = ""; if (y.placeholder = o.placeholder, y.searchEnabled = o.searchEnabled, y.sortable = o.sortable, y.refreshDelay = o.refreshDelay, y.paste = o.paste, y.resetSearchInput = o.resetSearchInput, y.refreshing = !1, y.spinnerEnabled = o.spinnerEnabled, y.spinnerClass = o.spinnerClass, y.removeSelected = o.removeSelected, y.closeOnSelect = !0, y.skipFocusser = !1, y.search = x, y.activeIndex = 0, y.items = [], y.open = !1, y.focus = !1, y.disabled = !1, y.selected = void 0, y.dropdownPosition = "auto", y.focusser = void 0, y.multiple = void 0, y.disableChoiceExpression = void 0, y.tagging = { isActivated: !1, fct: void 0 }, y.taggingTokens = { isActivated: !1, tokens: void 0 }, y.lockChoiceExpression = void 0, y.clickTriggeredSelect = !1, y.$filter = n, y.$element = i, y.$animate = function () { try { return d.get("$animate") } catch (e) { return null } }(), y.searchInput = i.querySelectorAll("input.ui-select-search"), 1 !== y.searchInput.length) throw r("searchInput", "Expected 1 input.ui-select-search but got '{0}'.", y.searchInput.length); y.isEmpty = function () { return e(y.selected) || "" === y.selected || y.multiple && 0 === y.selected.length }, y.activate = function (e, t) { if (y.disabled || y.open) y.open && !y.searchEnabled && y.close(); else { t || g(), s.$broadcast("uis:activate"), y.open = !0, y.activeIndex = y.activeIndex >= y.items.length ? 0 : y.activeIndex, y.activeIndex === -1 && y.taggingLabel !== !1 && (y.activeIndex = 0); var n = i.querySelectorAll(".ui-select-choices-content"), l = i.querySelectorAll(".ui-select-search"); if (y.$animate && y.$animate.on && y.$animate.enabled(n[0])) { var a = function (t, s) { "start" === s && 0 === y.items.length ? (y.$animate.off("removeClass", l[0], a), c(function () { y.focusSearchInput(e) })) : "close" === s && (y.$animate.off("enter", n[0], a), c(function () { y.focusSearchInput(e) })) }; y.items.length > 0 ? y.$animate.on("enter", n[0], a) : y.$animate.on("removeClass", l[0], a) } else c(function () { y.focusSearchInput(e), !y.tagging.isActivated && y.items.length > 1 && w() }) } }, y.focusSearchInput = function (e) { y.search = e || y.search, y.searchInput[0].focus() }, y.findGroupByName = function (e) { return y.groups && y.groups.filter(function (t) { return t.name === e })[0] }, y.parseRepeatAttr = function (e, t, i) { function c(e) { var c = s.$eval(t); if (y.groups = [], angular.forEach(e, function (e) { var t = angular.isFunction(c) ? c(e) : e[c], s = y.findGroupByName(t); s ? s.items.push(e) : y.groups.push({ name: t, items: [e] }) }), i) { var n = s.$eval(i); angular.isFunction(n) ? y.groups = n(y.groups) : angular.isArray(n) && (y.groups = f(y.groups, n)) } y.items = [], y.groups.forEach(function (e) { y.items = y.items.concat(e.items) }) } function n(e) { y.items = e || [] } y.setItemsFn = t ? c : n, y.parserResult = a.parse(e), y.isGrouped = !!t, y.itemProperty = y.parserResult.itemName; var l = y.parserResult.source, o = function () { var e = l(s); s.$uisSource = Object.keys(e).map(function (t) { var s = {}; return s[y.parserResult.keyName] = t, s.value = e[t], s }) }; y.parserResult.keyName && (o(), y.parserResult.source = u("$uisSource" + y.parserResult.filters), s.$watch(l, function (e, t) { e !== t && o() }, !0)), y.refreshItems = function (e) { e = e || y.parserResult.source(s); var t = y.selected; if (y.isEmpty() || angular.isArray(t) && !t.length || !y.multiple || !y.removeSelected) y.setItemsFn(e); else if (void 0 !== e && null !== e) { var i = e.filter(function (e) { return angular.isArray(t) ? t.every(function (t) { return !angular.equals(e, t) }) : !angular.equals(e, t) }); y.setItemsFn(i) } "auto" !== y.dropdownPosition && "up" !== y.dropdownPosition || s.calculateDropdownPos(), s.$broadcast("uis:refresh") }, s.$watchCollection(y.parserResult.source, function (e) { if (void 0 === e || null === e) y.items = []; else { if (!angular.isArray(e)) throw r("items", "Expected an array but got '{0}'.", e); y.refreshItems(e), angular.isDefined(y.ngModel.$modelValue) && (y.ngModel.$modelValue = null) } }) }; var E; y.refresh = function (e) { void 0 !== e && (E && c.cancel(E), E = c(function () { if (s.$select.search.length >= s.$select.minimumInputLength) { var t = s.$eval(e); t && angular.isFunction(t.then) && !y.refreshing && (y.refreshing = !0, t["finally"](function () { y.refreshing = !1 })) } }, y.refreshDelay)) }, y.isActive = function (e) { if (!y.open) return !1; var t = y.items.indexOf(e[y.itemProperty]), s = t == y.activeIndex; return !(!s || t < 0) && (s && !angular.isUndefined(y.onHighlightCallback) && e.$eval(y.onHighlightCallback), s) }; var S = function (e) { return y.selected && angular.isArray(y.selected) && y.selected.filter(function (t) { return angular.equals(t, e) }).length > 0 }, I = []; y.isDisabled = function (e) { if (y.open) { var t = e[y.itemProperty], s = y.items.indexOf(t), i = !1; if (s >= 0 && (angular.isDefined(y.disableChoiceExpression) || y.multiple)) { if (t.isTag) return !1; y.multiple && (i = S(t)), !i && angular.isDefined(y.disableChoiceExpression) && (i = !!e.$eval(y.disableChoiceExpression)), v(t, i) } return i } }, y.select = function (t, i, c) { if (e(t) || !m(t)) { if (!y.items && !y.search && !y.tagging.isActivated) return; if (!t || !m(t)) { if (y.clickTriggeredSelect = !1, c && ("click" === c.type || "touchend" === c.type) && t && (y.clickTriggeredSelect = !0), y.tagging.isActivated && y.clickTriggeredSelect === !1) { if (y.taggingLabel === !1) if (y.activeIndex < 0) { if (void 0 === t && (t = void 0 !== y.tagging.fct ? y.tagging.fct(y.search) : y.search), !t || angular.equals(y.items[0], t)) return } else t = y.items[y.activeIndex]; else if (0 === y.activeIndex) { if (void 0 === t) return; if (void 0 !== y.tagging.fct && "string" == typeof t) { if (t = y.tagging.fct(t), !t) return } else "string" == typeof t && (t = t.replace(y.taggingLabel, "").trim()) } if (S(t)) return void y.close(i) } g(), s.$broadcast("uis:select", t), y.closeOnSelect && y.close(i) } } }, y.close = function (e) { y.open && (y.ngModel && y.ngModel.$setTouched && y.ngModel.$setTouched(), y.open = !1, g(), s.$broadcast("uis:close", e)) }, y.setFocus = function () { y.focus || y.focusInput[0].focus() }, y.clear = function (e) { y.select(null), e.stopPropagation(), c(function () { y.focusser[0].focus() }, 0, !1) }, y.toggle = function (e) { y.open ? (y.close(), e.preventDefault(), e.stopPropagation()) : y.activate() }, y.isLocked = function () { return !1 }, s.$watch(function () { return angular.isDefined(y.lockChoiceExpression) && "" !== y.lockChoiceExpression }, $); var C = null, k = !1; y.sizeSearchInput = function () { var e = y.searchInput[0], t = y.$element[0], i = function () { return t.clientWidth * !!e.offsetParent }, n = function (t) { if (0 === t) return !1; var s = t - e.offsetLeft; return s < 50 && (s = t), y.searchInput.css("width", s + "px"), !0 }; y.searchInput.css("width", "10px"), c(function () { null !== C || n(i()) || (C = s.$watch(function () { k || (k = !0, s.$$postDigest(function () { k = !1, n(i()) && (C(), C = null) })) }, angular.noop)) }) }, y.searchInput.on("keydown", function (e) { var i = e.which; ~[t.ENTER, t.ESC].indexOf(i) && (e.preventDefault(), e.stopPropagation()), s.$apply(function () { var s = !1; if ((y.items.length > 0 || y.tagging.isActivated) && (b(i) || y.searchEnabled || (e.preventDefault(), e.stopPropagation()), y.taggingTokens.isActivated)) { for (var n = 0; n < y.taggingTokens.tokens.length; n++)y.taggingTokens.tokens[n] === t.MAP[e.keyCode] && y.search.length > 0 && (s = !0); s && c(function () { y.searchInput.triggerHandler("tagged"); var s = y.search.replace(t.MAP[e.keyCode], "").trim(); y.tagging.fct && (s = y.tagging.fct(s)), s && y.select(s, !0) }) } }), t.isVerticalMovement(i) && y.items.length > 0 && w(), i !== t.ENTER && i !== t.ESC || (e.preventDefault(), e.stopPropagation()) }), y.searchInput.on("paste", function (e) { var s; if (s = window.clipboardData && window.clipboardData.getData ? window.clipboardData.getData("Text") : (e.originalEvent || e).clipboardData.getData("text/plain"), s = y.search + s, s && s.length > 0) if (y.taggingTokens.isActivated) { for (var i = [], c = 0; c < y.taggingTokens.tokens.length; c++) { var n = t.toSeparator(y.taggingTokens.tokens[c]) || y.taggingTokens.tokens[c]; if (s.indexOf(n) > -1) { i = s.split(n); break } } 0 === i.length && (i = [s]); var l = y.search; angular.forEach(i, function (e) { var t = y.tagging.fct ? y.tagging.fct(e) : e; t && y.select(t, !0) }), y.search = l || x, e.preventDefault(), e.stopPropagation() } else y.paste && (y.paste(s), y.search = x, e.preventDefault(), e.stopPropagation()) }), y.searchInput.on("tagged", function () { c(function () { g() }) }); var A = l(function () { y.sizeSearchInput() }, 50); angular.element(p).bind("resize", A), s.$on("$destroy", function () { y.searchInput.off("keyup keydown tagged blur paste"), angular.element(p).off("resize", A) }), s.$watch("$select.activeIndex", function (e) { e && i.find("input").attr("aria-activedescendant", "ui-select-choices-row-" + y.generatedId + "-" + e) }), s.$watch("$select.open", function (e) { e || i.find("input").removeAttr("aria-activedescendant") }) }]), i.directive("uiSelect", ["$document", "uiSelectConfig", "uiSelectMinErr", "uisOffset", "$compile", "$parse", "$timeout", function (e, t, s, i, c, n, l) { return { restrict: "EA", templateUrl: function (e, s) { var i = s.theme || t.theme; return i + (angular.isDefined(s.multiple) ? "/select-multiple.tpl.html" : "/select.tpl.html") }, replace: !0, transclude: !0, require: ["uiSelect", "^ngModel"], scope: !0, controller: "uiSelectCtrl", controllerAs: "$select", compile: function (c, a) { var r = /{(.*)}\s*{(.*)}/.exec(a.ngClass); if (r) { var o = "{" + r[1] + ", " + r[2] + "}"; a.ngClass = o, c.attr("ng-class", o) } return angular.isDefined(a.multiple) ? c.append("<ui-select-multiple/>").removeAttr("multiple") : c.append("<ui-select-single/>"), a.inputId && (c.querySelectorAll("input.ui-select-search")[0].id = a.inputId), function (c, a, r, o, u) { function d(e) { if (g.open) { var t = !1; if (t = window.jQuery ? window.jQuery.contains(a[0], e.target) : a[0].contains(e.target), !t && !g.clickTriggeredSelect) { var s; if (g.skipFocusser) s = !0; else { var i = ["input", "button", "textarea", "select"], n = angular.element(e.target).controller("uiSelect"); s = n && n !== g, s || (s = ~i.indexOf(e.target.tagName.toLowerCase())) } g.close(s), c.$digest() } g.clickTriggeredSelect = !1 } } function p() { var t = i(a); m = angular.element('<div class="ui-select-placeholder"></div>'), m[0].style.width = t.width + "px", m[0].style.height = t.height + "px", a.after(m), $ = a[0].style.width, e.find("body").append(a), a[0].style.position = "absolute", a[0].style.left = t.left + "px", a[0].style.top = t.top + "px", a[0].style.width = t.width + "px" } function h() { null !== m && (m.replaceWith(a), m = null, a[0].style.position = "", a[0].style.left = "", a[0].style.top = "", a[0].style.width = $, g.setFocus()) } var g = o[0], f = o[1]; g.generatedId = t.generateId(), g.baseTitle = r.title || "Select box", g.focusserTitle = g.baseTitle + " focus", g.focusserId = "focusser-" + g.generatedId, g.closeOnSelect = function () { return angular.isDefined(r.closeOnSelect) ? n(r.closeOnSelect)() : t.closeOnSelect }(), c.$watch("skipFocusser", function () { var e = c.$eval(r.skipFocusser); g.skipFocusser = void 0 !== e ? e : t.skipFocusser }), g.onSelectCallback = n(r.onSelect), g.onRemoveCallback = n(r.onRemove), g.ngModel = f, g.choiceGrouped = function (e) { return g.isGrouped && e && e.name }, r.tabindex && r.$observe("tabindex", function (e) { g.focusInput.attr("tabindex", e), a.removeAttr("tabindex") }), c.$watch(function () { return c.$eval(r.searchEnabled) }, function (e) { g.searchEnabled = void 0 !== e ? e : t.searchEnabled }), c.$watch("sortable", function () { var e = c.$eval(r.sortable); g.sortable = void 0 !== e ? e : t.sortable }), r.$observe("backspaceReset", function () { var e = c.$eval(r.backspaceReset); g.backspaceReset = void 0 === e || e }), r.$observe("limit", function () { g.limit = angular.isDefined(r.limit) ? parseInt(r.limit, 10) : void 0 }), c.$watch("removeSelected", function () { var e = c.$eval(r.removeSelected); g.removeSelected = void 0 !== e ? e : t.removeSelected }), r.$observe("disabled", function () { g.disabled = void 0 !== r.disabled && r.disabled }), r.$observe("resetSearchInput", function () { var e = c.$eval(r.resetSearchInput); g.resetSearchInput = void 0 === e || e }), r.$observe("paste", function () { g.paste = c.$eval(r.paste) }), r.$observe("tagging", function () { if (void 0 !== r.tagging) { var e = c.$eval(r.tagging); g.tagging = { isActivated: !0, fct: e !== !0 ? e : void 0 } } else g.tagging = { isActivated: !1, fct: void 0 } }), r.$observe("taggingLabel", function () { void 0 !== r.tagging && ("false" === r.taggingLabel ? g.taggingLabel = !1 : g.taggingLabel = void 0 !== r.taggingLabel ? r.taggingLabel : "(new)") }), r.$observe("taggingTokens", function () { if (void 0 !== r.tagging) { var e = void 0 !== r.taggingTokens ? r.taggingTokens.split("|") : [",", "ENTER"]; g.taggingTokens = { isActivated: !0, tokens: e } } }), r.$observe("spinnerEnabled", function () { var e = c.$eval(r.spinnerEnabled); g.spinnerEnabled = void 0 !== e ? e : t.spinnerEnabled }), r.$observe("spinnerClass", function () { var e = r.spinnerClass; g.spinnerClass = void 0 !== e ? r.spinnerClass : t.spinnerClass }), angular.isDefined(r.autofocus) && l(function () { g.setFocus() }), angular.isDefined(r.focusOn) && c.$on(r.focusOn, function () { l(function () { g.setFocus() }) }), e.on("click", d), c.$on("$destroy", function () { e.off("click", d) }), u(c, function (e) { var t = angular.element("<div>").append(e), i = t.querySelectorAll(".ui-select-match"); if (i.removeAttr("ui-select-match"), i.removeAttr("data-ui-select-match"), 1 !== i.length) throw s("transcluded", "Expected 1 .ui-select-match but got '{0}'.", i.length); a.querySelectorAll(".ui-select-match").replaceWith(i); var c = t.querySelectorAll(".ui-select-choices"); if (c.removeAttr("ui-select-choices"), c.removeAttr("data-ui-select-choices"), 1 !== c.length) throw s("transcluded", "Expected 1 .ui-select-choices but got '{0}'.", c.length); a.querySelectorAll(".ui-select-choices").replaceWith(c); var n = t.querySelectorAll(".ui-select-no-choice"); n.removeAttr("ui-select-no-choice"), n.removeAttr("data-ui-select-no-choice"), 1 == n.length && a.querySelectorAll(".ui-select-no-choice").replaceWith(n) }); var v = c.$eval(r.appendToBody); (void 0 !== v ? v : t.appendToBody) && (c.$watch("$select.open", function (e) { e ? p() : h() }), c.$on("$destroy", function () { h() })); var m = null, $ = "", b = null, w = "direction-up"; c.$watch("$select.open", function () { "auto" !== g.dropdownPosition && "up" !== g.dropdownPosition || c.calculateDropdownPos() }); var y = function (e, t) { e = e || i(a), t = t || i(b), b[0].style.position = "absolute", b[0].style.top = t.height * -1 + "px", a.addClass(w) }, x = function (e, t) { a.removeClass(w), e = e || i(a), t = t || i(b), b[0].style.position = "", b[0].style.top = "" }, E = function () { l(function () { if ("up" === g.dropdownPosition) y(); else { a.removeClass(w); var t = i(a), s = i(b), c = e[0].documentElement.scrollTop || e[0].body.scrollTop; t.top + t.height + s.height > c + e[0].documentElement.clientHeight ? y(t, s) : x(t, s) } b[0].style.opacity = 1 }) }, S = !1; c.calculateDropdownPos = function () { if (g.open) { if (b = angular.element(a).querySelectorAll(".ui-select-dropdown"), 0 === b.length) return; if ("" !== g.search || S || (b[0].style.opacity = 0, S = !0), !i(b).height && g.$animate && g.$animate.on && g.$animate.enabled(b)) { var e = !0; g.$animate.on("enter", b, function (t, s) { "close" === s && e && (E(), e = !1) }) } else E() } else { if (null === b || 0 === b.length) return; b[0].style.opacity = 0, b[0].style.position = "", b[0].style.top = "", a.removeClass(w) } } } } } }]), i.directive("uiSelectMatch", ["uiSelectConfig", function (e) { function t(e, t) { return e[0].hasAttribute(t) ? e.attr(t) : e[0].hasAttribute("data-" + t) ? e.attr("data-" + t) : e[0].hasAttribute("x-" + t) ? e.attr("x-" + t) : void 0 } return { restrict: "EA", require: "^uiSelect", replace: !0, transclude: !0, templateUrl: function (s) { s.addClass("ui-select-match"); var i = s.parent(), c = t(i, "theme") || e.theme, n = angular.isDefined(t(i, "multiple")); return c + (n ? "/match-multiple.tpl.html" : "/match.tpl.html") }, link: function (t, s, i, c) { function n(e) { c.allowClear = !!angular.isDefined(e) && ("" === e || "true" === e.toLowerCase()) } c.lockChoiceExpression = i.uiLockChoice, i.$observe("placeholder", function (t) { c.placeholder = void 0 !== t ? t : e.placeholder }), i.$observe("allowClear", n), n(i.allowClear), c.multiple && c.sizeSearchInput() } } }]), i.directive("uiSelectMultiple", ["uiSelectMinErr", "$timeout", function (s, i) { return { restrict: "EA", require: ["^uiSelect", "^ngModel"], controller: ["$scope", "$timeout", function (e, t) { var s, i = this, c = e.$select; angular.isUndefined(c.selected) && (c.selected = []), e.$evalAsync(function () { s = e.ngModel }), i.activeMatchIndex = -1, i.updateModel = function () { s.$setViewValue(Date.now()), i.refreshComponent() }, i.refreshComponent = function () { c.refreshItems && c.refreshItems(), c.sizeSearchInput && c.sizeSearchInput() }, i.removeChoice = function (s) { if (c.isLocked(null, s)) return !1; var n = c.selected[s], l = {}; return l[c.parserResult.itemName] = n, c.selected.splice(s, 1), i.activeMatchIndex = -1, c.sizeSearchInput(), t(function () { c.onRemoveCallback(e, { $item: n, $model: c.parserResult.modelMapper(e, l) }) }), i.updateModel(), !0 }, i.getPlaceholder = function () { if (!c.selected || !c.selected.length) return c.placeholder } }], controllerAs: "$selectMultiple", link: function (c, n, l, a) { function r(e) { return angular.isNumber(e.selectionStart) ? e.selectionStart : e.value.length } function o(e) { function s() { switch (e) { case t.LEFT: return ~g.activeMatchIndex ? u : l; case t.RIGHT: return ~g.activeMatchIndex && a !== l ? o : (p.activate(), !1); case t.BACKSPACE: return ~g.activeMatchIndex ? g.removeChoice(a) ? u : a : l; case t.DELETE: return !!~g.activeMatchIndex && (g.removeChoice(g.activeMatchIndex), a) } } var i = r(p.searchInput[0]), c = p.selected.length, n = 0, l = c - 1, a = g.activeMatchIndex, o = g.activeMatchIndex + 1, u = g.activeMatchIndex - 1, d = a; return !(i > 0 || p.search.length && e == t.RIGHT) && (p.close(), d = s(), p.selected.length && d !== !1 ? g.activeMatchIndex = Math.min(l, Math.max(n, d)) : g.activeMatchIndex = -1, !0) } function u(e) { if (void 0 === e || void 0 === p.search) return !1; var t = e.filter(function (e) { return void 0 !== p.search.toUpperCase() && void 0 !== e && e.toUpperCase() === p.search.toUpperCase() }).length > 0; return t } function d(e, t) { var s = -1; if (angular.isArray(e)) for (var i = angular.copy(e), c = 0; c < i.length; c++)if (void 0 === p.tagging.fct) i[c] + " " + p.taggingLabel === t && (s = c); else { var n = i[c]; angular.isObject(n) && (n.isTag = !0), angular.equals(n, t) && (s = c) } return s } var p = a[0], h = c.ngModel = a[1], g = c.$selectMultiple; p.multiple = !0, p.focusInput = p.searchInput, h.$isEmpty = function (e) { return !e || 0 === e.length }, h.$parsers.unshift(function () { for (var e, t = {}, s = [], i = p.selected.length - 1; i >= 0; i--)t = {}, t[p.parserResult.itemName] = p.selected[i], e = p.parserResult.modelMapper(c, t), s.unshift(e); return s }), h.$formatters.unshift(function (e) { var t, s = p.parserResult && p.parserResult.source(c, { $select: { search: "" } }), i = {}; if (!s) return e; var n = [], l = function (e, s) { if (e && e.length) { for (var l = e.length - 1; l >= 0; l--) { if (i[p.parserResult.itemName] = e[l], t = p.parserResult.modelMapper(c, i), p.parserResult.trackByExp) { var a = /(\w*)\./.exec(p.parserResult.trackByExp), r = /\.([^\s]+)/.exec(p.parserResult.trackByExp); if (a && a.length > 0 && a[1] == p.parserResult.itemName && r && r.length > 0 && t[r[1]] == s[r[1]]) return n.unshift(e[l]), !0 } if (angular.equals(t, s)) return n.unshift(e[l]), !0 } return !1 } }; if (!e) return n; for (var a = e.length - 1; a >= 0; a--)l(p.selected, e[a]) || l(s, e[a]) || n.unshift(e[a]); return n }), c.$watchCollection(function () { return h.$modelValue }, function (e, t) { t != e && (angular.isDefined(h.$modelValue) && (h.$modelValue = null), g.refreshComponent()) }), h.$render = function () { if (!angular.isArray(h.$viewValue)) { if (!e(h.$viewValue)) throw s("multiarr", "Expected model value to be array but got '{0}'", h.$viewValue); h.$viewValue = [] } p.selected = h.$viewValue, g.refreshComponent(), c.$evalAsync() }, c.$on("uis:select", function (e, t) { if (!(p.selected.length >= p.limit)) { p.selected.push(t); var s = {}; s[p.parserResult.itemName] = t, i(function () { p.onSelectCallback(c, { $item: t, $model: p.parserResult.modelMapper(c, s) }) }), g.updateModel() } }), c.$on("uis:activate", function () { g.activeMatchIndex = -1 }), c.$watch("$select.disabled", function (e, t) { t && !e && p.sizeSearchInput() }), p.searchInput.on("keydown", function (e) { var s = e.which; c.$apply(function () { var i = !1; t.isHorizontalMovement(s) && (i = o(s)), i && s != t.TAB && (e.preventDefault(), e.stopPropagation()) }) }), p.searchInput.on("keyup", function (e) { if (t.isVerticalMovement(e.which) || c.$evalAsync(function () { p.activeIndex = p.taggingLabel === !1 ? -1 : 0 }), p.tagging.isActivated && p.search.length > 0) { if (e.which === t.TAB || t.isControl(e) || t.isFunctionKey(e) || e.which === t.ESC || t.isVerticalMovement(e.which)) return; if (p.activeIndex = p.taggingLabel === !1 ? -1 : 0, p.taggingLabel === !1) return; var s, i, n, l, a = angular.copy(p.items), r = angular.copy(p.items), o = !1, h = -1; if (void 0 !== p.tagging.fct) { if (n = p.$filter("filter")(a, { isTag: !0 }), n.length > 0 && (l = n[0]), a.length > 0 && l && (o = !0, a = a.slice(1, a.length), r = r.slice(1, r.length)), s = p.tagging.fct(p.search), r.some(function (e) { return angular.equals(e, s) }) || p.selected.some(function (e) { return angular.equals(e, s) })) return void c.$evalAsync(function () { p.activeIndex = 0, p.items = a }); s && (s.isTag = !0) } else { if (n = p.$filter("filter")(a, function (e) { return e.match(p.taggingLabel) }), n.length > 0 && (l = n[0]), i = a[0], void 0 !== i && a.length > 0 && l && (o = !0, a = a.slice(1, a.length), r = r.slice(1, r.length)), s = p.search + " " + p.taggingLabel, d(p.selected, p.search) > -1) return; if (u(r.concat(p.selected))) return void (o && (a = r, c.$evalAsync(function () { p.activeIndex = 0, p.items = a }))); if (u(r)) return void (o && (p.items = r.slice(1, r.length))) } o && (h = d(p.selected, s)), h > -1 ? a = a.slice(h + 1, a.length - 1) : (a = [], s && a.push(s), a = a.concat(r)), c.$evalAsync(function () { if (p.activeIndex = 0, p.items = a, p.isGrouped) { var e = s ? a.slice(1) : a; p.setItemsFn(e), s && (p.items.unshift(s), p.groups.unshift({ name: "", items: [s], tagging: !0 })) } }) } }), p.searchInput.on("blur", function () { i(function () { g.activeMatchIndex = -1 }) }) } } }]), i.directive("uiSelectNoChoice", ["uiSelectConfig", function (e) { return { restrict: "EA", require: "^uiSelect", replace: !0, transclude: !0, templateUrl: function (t) { t.addClass("ui-select-no-choice"); var s = t.parent().attr("theme") || e.theme; return s + "/no-choice.tpl.html" } } }]), i.directive("uiSelectSingle", ["$timeout", "$compile", function (s, i) { return { restrict: "EA", require: ["^uiSelect", "^ngModel"], link: function (c, n, l, a) { var r = a[0], o = a[1]; o.$parsers.unshift(function (t) { if (e(t)) return t; var s, i = {}; return i[r.parserResult.itemName] = t, s = r.parserResult.modelMapper(c, i) }), o.$formatters.unshift(function (t) { if (e(t)) return t; var s, i = r.parserResult && r.parserResult.source(c, { $select: { search: "" } }), n = {}; if (i) { var l = function (e) { return n[r.parserResult.itemName] = e, s = r.parserResult.modelMapper(c, n), s === t }; if (r.selected && l(r.selected)) return r.selected; for (var a = i.length - 1; a >= 0; a--)if (l(i[a])) return i[a] } return t }), c.$watch("$select.selected", function (e) { o.$viewValue !== e && o.$setViewValue(e) }), o.$render = function () { r.selected = o.$viewValue }, c.$on("uis:select", function (t, i) { r.selected = i; var n = {}; n[r.parserResult.itemName] = i, s(function () { r.onSelectCallback(c, { $item: i, $model: e(i) ? i : r.parserResult.modelMapper(c, n) }) }) }), c.$on("uis:close", function (e, t) { s(function () { r.focusser.prop("disabled", !1), t || r.focusser[0].focus() }, 0, !1) }), c.$on("uis:activate", function () { u.prop("disabled", !0) }); var u = angular.element("<input ng-disabled='$select.disabled' class='ui-select-focusser ui-select-offscreen' type='text' id='{{ $select.focusserId }}' aria-label='{{ $select.focusserTitle }}' aria-haspopup='true' role='button' />"); i(u)(c), r.focusser = u, r.focusInput = u, n.parent().append(u), u.bind("focus", function () { c.$evalAsync(function () { r.focus = !0 }) }), u.bind("blur", function () { c.$evalAsync(function () { r.focus = !1 }) }), u.bind("keydown", function (e) { return e.which === t.BACKSPACE && r.backspaceReset !== !1 ? (e.preventDefault(), e.stopPropagation(), r.select(void 0), void c.$apply()) : void (e.which === t.TAB || t.isControl(e) || t.isFunctionKey(e) || e.which === t.ESC || (e.which != t.DOWN && e.which != t.UP && e.which != t.ENTER && e.which != t.SPACE || (e.preventDefault(), e.stopPropagation(), r.activate()), c.$digest())) }), u.bind("keyup input", function (e) { e.which === t.TAB || t.isControl(e) || t.isFunctionKey(e) || e.which === t.ESC || e.which == t.ENTER || e.which === t.BACKSPACE || (r.activate(u.val()), u.val(""), c.$digest()) }) } } }]), i.directive("uiSelectSort", ["$timeout", "uiSelectConfig", "uiSelectMinErr", function (e, t, s) { return { require: ["^^uiSelect", "^ngModel"], link: function (t, i, c, n) { if (null === t[c.uiSelectSort]) throw s("sort", "Expected a list to sort"); var l = n[0], a = n[1], r = angular.extend({ axis: "horizontal" }, t.$eval(c.uiSelectSortOptions)), o = r.axis, u = "dragging", d = "dropping", p = "dropping-before", h = "dropping-after"; t.$watch(function () { return l.sortable }, function (e) { e ? i.attr("draggable", !0) : i.removeAttr("draggable") }), i.on("dragstart", function (e) { i.addClass(u), (e.dataTransfer || e.originalEvent.dataTransfer).setData("text", t.$index.toString()) }), i.on("dragend", function () { v(u) }); var g, f = function (e, t) { this.splice(t, 0, this.splice(e, 1)[0]) }, v = function (e) { angular.forEach(l.$element.querySelectorAll("." + e), function (t) { angular.element(t).removeClass(e) }) }, m = function (e) { e.preventDefault(); var t = "vertical" === o ? e.offsetY || e.layerY || (e.originalEvent ? e.originalEvent.offsetY : 0) : e.offsetX || e.layerX || (e.originalEvent ? e.originalEvent.offsetX : 0); t < this["vertical" === o ? "offsetHeight" : "offsetWidth"] / 2 ? (v(h), i.addClass(p)) : (v(p), i.addClass(h)) }, $ = function (t) { t.preventDefault(); var s = parseInt((t.dataTransfer || t.originalEvent.dataTransfer).getData("text"), 10); e.cancel(g), g = e(function () { b(s) }, 20) }, b = function (e) { var s = t.$eval(c.uiSelectSort), n = s[e], l = null; l = i.hasClass(p) ? e < t.$index ? t.$index - 1 : t.$index : e < t.$index ? t.$index : t.$index + 1, f.apply(s, [e, l]), a.$setViewValue(Date.now()), t.$apply(function () { t.$emit("uiSelectSort:change", { array: s, item: n, from: e, to: l }) }), v(d), v(p), v(h), i.off("drop", $) }; i.on("dragenter", function () { i.hasClass(u) || (i.addClass(d), i.on("dragover", m), i.on("drop", $)) }), i.on("dragleave", function (e) { e.target == i && (v(d), v(p), v(h), i.off("dragover", m), i.off("drop", $)) }) } } }]), i.directive("uisOpenClose", ["$parse", "$timeout", function (e, t) {
		return {
			restrict: "A", require: "uiSelect", link: function (s, i, c, n) {
				n.onOpenCloseCallback = e(c.uisOpenClose), s.$watch("$select.open", function (e, i) {
					e !== i && t(function () {
						n.onOpenCloseCallback(s, { isOpen: e });
					})
				})
			}
		}
	}]), i.service("uisRepeatParser", ["uiSelectMinErr", "$parse", function (e, t) { var s = this; s.parse = function (s) { var i; if (i = s.match(/^\s*(?:([\s\S]+?)\s+as\s+)?(?:([\$\w][\$\w]*)|(?:\(\s*([\$\w][\$\w]*)\s*,\s*([\$\w][\$\w]*)\s*\)))\s+in\s+(\s*[\s\S]+?)?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/), !i) throw e("iexp", "Expected expression in form of '_item_ in _collection_[ track by _id_]' but got '{0}'.", s); var c = i[5], n = ""; if (i[3]) { c = i[5].replace(/(^\()|(\)$)/g, ""); var l = i[5].match(/^\s*(?:[\s\S]+?)(?:[^\|]|\|\|)+([\s\S]*)\s*$/); l && l[1].trim() && (n = l[1], c = c.replace(n, "")) } return { itemName: i[4] || i[2], keyName: i[3], source: t(c), filters: n, trackByExp: i[6], modelMapper: t(i[1] || i[4] || i[2]), repeatExpression: function (e) { var t = this.itemName + " in " + (e ? "$group.items" : "$select.items"); return this.trackByExp && (t += " track by " + this.trackByExp), t } } }, s.getGroupNgRepeatExpression = function () { return "$group in $select.groups track by $group.name" } }])
}(), angular.module("ui.select").run(["$templateCache", function (e) { e.put("bootstrap/choices.tpl.html", '<ul class="ui-select-choices ui-select-choices-content ui-select-dropdown dropdown-menu" ng-show="$select.open && $select.items.length > 0"><li class="ui-select-choices-group" id="ui-select-choices-{{ $select.generatedId }}"><div class="divider" ng-show="$select.isGrouped && $index > 0"></div><div ng-show="$select.isGrouped" class="ui-select-choices-group-label dropdown-header" ng-bind="$group.name"></div><div ng-attr-id="ui-select-choices-row-{{ $select.generatedId }}-{{$index}}" class="ui-select-choices-row" ng-class="{active: $select.isActive(this), disabled: $select.isDisabled(this)}" role="option"><span class="ui-select-choices-row-inner"></span></div></li></ul>'), e.put("bootstrap/match-multiple.tpl.html", '<span class="ui-select-match"><span ng-repeat="$item in $select.selected track by $index"><span class="ui-select-match-item btn btn-default btn-xs" tabindex="-1" type="button" ng-disabled="$select.disabled" ng-click="$selectMultiple.activeMatchIndex = $index;" ng-class="{\'btn-primary\':$selectMultiple.activeMatchIndex === $index, \'select-locked\':$select.isLocked(this, $index)}" ui-select-sort="$select.selected"><span class="close ui-select-match-close" ng-hide="$select.disabled" ng-click="$selectMultiple.removeChoice($index)">&nbsp;&times;</span> <span uis-transclude-append=""></span></span></span></span>'), e.put("bootstrap/match.tpl.html", '<div class="ui-select-match" ng-hide="$select.open && $select.searchEnabled" ng-disabled="$select.disabled" ng-class="{\'btn-default-focus\':$select.focus}"><span tabindex="-1" class="btn btn-default form-control ui-select-toggle" aria-label="{{ $select.baseTitle }} activate" ng-disabled="$select.disabled" ng-click="$select.activate()" style="outline: 0;"><span ng-show="$select.isEmpty()" class="ui-select-placeholder text-muted">{{$select.placeholder}}</span> <span ng-hide="$select.isEmpty()" class="ui-select-match-text pull-left" ng-class="{\'ui-select-allow-clear\': $select.allowClear && !$select.isEmpty()}" ng-transclude=""></span> <i class="caret pull-right" ng-click="$select.toggle($event)"></i> <a ng-show="$select.allowClear && !$select.isEmpty() && ($select.disabled !== true)" aria-label="{{ $select.baseTitle }} clear" style="margin-right: 10px" ng-click="$select.clear($event)" class="btn btn-xs btn-link pull-right"><i class="glyphicon glyphicon-remove" aria-hidden="true"></i></a></span></div>'), e.put("bootstrap/no-choice.tpl.html", '<ul class="ui-select-no-choice dropdown-menu" ng-show="$select.items.length == 0"><li ng-transclude=""></li></ul>'), e.put("bootstrap/select-multiple.tpl.html", '<div class="ui-select-container ui-select-multiple ui-select-bootstrap dropdown form-control" ng-class="{open: $select.open}"><div><div class="ui-select-match"></div><input type="search" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" class="ui-select-search input-xs" placeholder="{{$selectMultiple.getPlaceholder()}}" ng-disabled="$select.disabled" ng-click="$select.activate()" ng-model="$select.search" role="combobox" aria-expanded="{{$select.open}}" aria-label="{{$select.baseTitle}}" ng-class="{\'spinner\': $select.refreshing}" ondrop="return false;"></div><div class="ui-select-choices"></div><div class="ui-select-no-choice"></div></div>'), e.put("bootstrap/select.tpl.html", '<div class="ui-select-container ui-select-bootstrap dropdown" ng-class="{open: $select.open}"><div class="ui-select-match"></div><span ng-show="$select.open && $select.refreshing && $select.spinnerEnabled" class="ui-select-refreshing {{$select.spinnerClass}}"></span> <input type="search" autocomplete="off" tabindex="-1" aria-expanded="true" aria-label="{{ $select.baseTitle }}" aria-owns="ui-select-choices-{{ $select.generatedId }}" class="form-control ui-select-search" ng-class="{ \'ui-select-search-hidden\' : !$select.searchEnabled }" placeholder="{{$select.placeholder}}" ng-model="$select.search" ng-show="$select.open"><div class="ui-select-choices"></div><div class="ui-select-no-choice"></div></div>'), e.put("select2/choices.tpl.html", '<ul tabindex="-1" class="ui-select-choices ui-select-choices-content select2-results"><li class="ui-select-choices-group" ng-class="{\'select2-result-with-children\': $select.choiceGrouped($group) }"><div ng-show="$select.choiceGrouped($group)" class="ui-select-choices-group-label select2-result-label" ng-bind="$group.name"></div><ul id="ui-select-choices-{{ $select.generatedId }}" ng-class="{\'select2-result-sub\': $select.choiceGrouped($group), \'select2-result-single\': !$select.choiceGrouped($group) }"><li role="option" ng-attr-id="ui-select-choices-row-{{ $select.generatedId }}-{{$index}}" class="ui-select-choices-row" ng-class="{\'select2-highlighted\': $select.isActive(this), \'select2-disabled\': $select.isDisabled(this)}"><div class="select2-result-label ui-select-choices-row-inner"></div></li></ul></li></ul>'), e.put("select2/match-multiple.tpl.html", '<span class="ui-select-match"><li class="ui-select-match-item select2-search-choice" ng-repeat="$item in $select.selected track by $index" ng-class="{\'select2-search-choice-focus\':$selectMultiple.activeMatchIndex === $index, \'select2-locked\':$select.isLocked(this, $index)}" ui-select-sort="$select.selected"><span uis-transclude-append=""></span> <a href="javascript:;" class="ui-select-match-close select2-search-choice-close" ng-click="$selectMultiple.removeChoice($index)" tabindex="-1"></a></li></span>'), e.put("select2/match.tpl.html", '<a class="select2-choice ui-select-match" ng-class="{\'select2-default\': $select.isEmpty()}" ng-click="$select.toggle($event)" aria-label="{{ $select.baseTitle }} select"><span ng-show="$select.isEmpty()" class="select2-chosen">{{$select.placeholder}}</span> <span ng-hide="$select.isEmpty()" class="select2-chosen" ng-transclude=""></span> <abbr ng-if="$select.allowClear && !$select.isEmpty()" class="select2-search-choice-close" ng-click="$select.clear($event)"></abbr> <span class="select2-arrow ui-select-toggle"><b></b></span></a>'), e.put("select2/no-choice.tpl.html", '<div class="ui-select-no-choice dropdown" ng-show="$select.items.length == 0"><div class="dropdown-content"><div data-selectable="" ng-transclude=""></div></div></div>'), e.put("select2/select-multiple.tpl.html", '<div class="ui-select-container ui-select-multiple select2 select2-container select2-container-multi" ng-class="{\'select2-container-active select2-dropdown-open open\': $select.open, \'select2-container-disabled\': $select.disabled}"><ul class="select2-choices"><span class="ui-select-match"></span><li class="select2-search-field"><input type="search" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" role="combobox" aria-expanded="true" aria-owns="ui-select-choices-{{ $select.generatedId }}" aria-label="{{ $select.baseTitle }}" aria-activedescendant="ui-select-choices-row-{{ $select.generatedId }}-{{ $select.activeIndex }}" class="select2-input ui-select-search" placeholder="{{$selectMultiple.getPlaceholder()}}" ng-disabled="$select.disabled" ng-hide="$select.disabled" ng-model="$select.search" ng-click="$select.activate()" style="width: 34px;" ondrop="return false;"></li></ul><div class="ui-select-dropdown select2-drop select2-with-searchbox select2-drop-active" ng-class="{\'select2-display-none\': !$select.open || $select.items.length === 0}"><div class="ui-select-choices"></div></div></div>'), e.put("select2/select.tpl.html", '<div class="ui-select-container select2 select2-container" ng-class="{\'select2-container-active select2-dropdown-open open\': $select.open, \'select2-container-disabled\': $select.disabled, \'select2-container-active\': $select.focus, \'select2-allowclear\': $select.allowClear && !$select.isEmpty()}"><div class="ui-select-match"></div><div class="ui-select-dropdown select2-drop select2-with-searchbox select2-drop-active" ng-class="{\'select2-display-none\': !$select.open}"><div class="search-container" ng-class="{\'ui-select-search-hidden\':!$select.searchEnabled, \'select2-search\':$select.searchEnabled}"><input type="search" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" ng-class="{\'select2-active\': $select.refreshing}" role="combobox" aria-expanded="true" aria-owns="ui-select-choices-{{ $select.generatedId }}" aria-label="{{ $select.baseTitle }}" class="ui-select-search select2-input" ng-model="$select.search"></div><div class="ui-select-choices"></div><div class="ui-select-no-choice"></div></div></div>'), e.put("selectize/choices.tpl.html", '<div ng-show="$select.open" class="ui-select-choices ui-select-dropdown selectize-dropdown" ng-class="{\'single\': !$select.multiple, \'multi\': $select.multiple}"><div class="ui-select-choices-content selectize-dropdown-content"><div class="ui-select-choices-group optgroup"><div ng-show="$select.isGrouped" class="ui-select-choices-group-label optgroup-header" ng-bind="$group.name"></div><div role="option" class="ui-select-choices-row" ng-class="{active: $select.isActive(this), disabled: $select.isDisabled(this)}"><div class="option ui-select-choices-row-inner" data-selectable=""></div></div></div></div></div>'), e.put("selectize/match-multiple.tpl.html", '<div class="ui-select-match" data-value="" ng-repeat="$item in $select.selected track by $index" ng-click="$selectMultiple.activeMatchIndex = $index;" ng-class="{\'active\':$selectMultiple.activeMatchIndex === $index}" ui-select-sort="$select.selected"><span class="ui-select-match-item" ng-class="{\'select-locked\':$select.isLocked(this, $index)}"><span uis-transclude-append=""></span> <span class="remove ui-select-match-close" ng-hide="$select.disabled" ng-click="$selectMultiple.removeChoice($index)">&times;</span></span></div>'), e.put("selectize/match.tpl.html", '<div ng-hide="$select.searchEnabled && ($select.open || $select.isEmpty())" class="ui-select-match"><span ng-show="!$select.searchEnabled && ($select.isEmpty() || $select.open)" class="ui-select-placeholder text-muted">{{$select.placeholder}}</span> <span ng-hide="$select.isEmpty() || $select.open" ng-transclude=""></span></div>'), e.put("selectize/no-choice.tpl.html", '<div class="ui-select-no-choice selectize-dropdown" ng-show="$select.items.length == 0"><div class="selectize-dropdown-content"><div data-selectable="" ng-transclude=""></div></div></div>'), e.put("selectize/select-multiple.tpl.html", '<div class="ui-select-container selectize-control multi plugin-remove_button" ng-class="{\'open\': $select.open}"><div class="selectize-input" ng-class="{\'focus\': $select.open, \'disabled\': $select.disabled, \'selectize-focus\' : $select.focus}" ng-click="$select.open && !$select.searchEnabled ? $select.toggle($event) : $select.activate()"><div class="ui-select-match"></div><input type="search" autocomplete="off" tabindex="-1" class="ui-select-search" ng-class="{\'ui-select-search-hidden\':!$select.searchEnabled}" placeholder="{{$selectMultiple.getPlaceholder()}}" ng-model="$select.search" ng-disabled="$select.disabled" aria-expanded="{{$select.open}}" aria-label="{{ $select.baseTitle }}" ondrop="return false;"></div><div class="ui-select-choices"></div><div class="ui-select-no-choice"></div></div>'), e.put("selectize/select.tpl.html", '<div class="ui-select-container selectize-control single" ng-class="{\'open\': $select.open}"><div class="selectize-input" ng-class="{\'focus\': $select.open, \'disabled\': $select.disabled, \'selectize-focus\' : $select.focus}" ng-click="$select.open && !$select.searchEnabled ? $select.toggle($event) : $select.activate()"><div class="ui-select-match"></div><input type="search" autocomplete="off" tabindex="-1" class="ui-select-search ui-select-toggle" ng-class="{\'ui-select-search-hidden\':!$select.searchEnabled}" ng-click="$select.toggle($event)" placeholder="{{$select.placeholder}}" ng-model="$select.search" ng-hide="!$select.isEmpty() && !$select.open" ng-disabled="$select.disabled" aria-label="{{ $select.baseTitle }}"></div><div class="ui-select-choices"></div><div class="ui-select-no-choice"></div></div>') }]);
//# sourceMappingURL=select.min.js.map
/*! Select2 4.0.13 | https://github.com/select2/select2/blob/master/LICENSE.md */
!function (n) { "function" == typeof define && define.amd ? define(["jquery"], n) : "object" == typeof module && module.exports ? module.exports = function (e, t) { return void 0 === t && (t = "undefined" != typeof window ? require("jquery") : require("jquery")(e)), n(t), t } : n(jQuery) }(function (u) { var e = function () { if (u && u.fn && u.fn.select2 && u.fn.select2.amd) var e = u.fn.select2.amd; var t, n, r, h, o, s, f, g, m, v, y, _, i, a, b; function w(e, t) { return i.call(e, t) } function l(e, t) { var n, r, i, o, s, a, l, c, u, d, p, h = t && t.split("/"), f = y.map, g = f && f["*"] || {}; if (e) { for (s = (e = e.split("/")).length - 1, y.nodeIdCompat && b.test(e[s]) && (e[s] = e[s].replace(b, "")), "." === e[0].charAt(0) && h && (e = h.slice(0, h.length - 1).concat(e)), u = 0; u < e.length; u++)if ("." === (p = e[u])) e.splice(u, 1), --u; else if (".." === p) { if (0 === u || 1 === u && ".." === e[2] || ".." === e[u - 1]) continue; 0 < u && (e.splice(u - 1, 2), u -= 2) } e = e.join("/") } if ((h || g) && f) { for (u = (n = e.split("/")).length; 0 < u; --u) { if (r = n.slice(0, u).join("/"), h) for (d = h.length; 0 < d; --d)if (i = (i = f[h.slice(0, d).join("/")]) && i[r]) { o = i, a = u; break } if (o) break; !l && g && g[r] && (l = g[r], c = u) } !o && l && (o = l, a = c), o && (n.splice(0, a, o), e = n.join("/")) } return e } function A(t, n) { return function () { var e = a.call(arguments, 0); return "string" != typeof e[0] && 1 === e.length && e.push(null), s.apply(h, e.concat([t, n])) } } function x(t) { return function (e) { m[t] = e } } function D(e) { if (w(v, e)) { var t = v[e]; delete v[e], _[e] = !0, o.apply(h, t) } if (!w(m, e) && !w(_, e)) throw new Error("No " + e); return m[e] } function c(e) { var t, n = e ? e.indexOf("!") : -1; return -1 < n && (t = e.substring(0, n), e = e.substring(n + 1, e.length)), [t, e] } function S(e) { return e ? c(e) : [] } return e && e.requirejs || (e ? n = e : e = {}, m = {}, v = {}, y = {}, _ = {}, i = Object.prototype.hasOwnProperty, a = [].slice, b = /\.js$/, f = function (e, t) { var n, r, i = c(e), o = i[0], s = t[1]; return e = i[1], o && (n = D(o = l(o, s))), o ? e = n && n.normalize ? n.normalize(e, (r = s, function (e) { return l(e, r) })) : l(e, s) : (o = (i = c(e = l(e, s)))[0], e = i[1], o && (n = D(o))), { f: o ? o + "!" + e : e, n: e, pr: o, p: n } }, g = { require: function (e) { return A(e) }, exports: function (e) { var t = m[e]; return void 0 !== t ? t : m[e] = {} }, module: function (e) { return { id: e, uri: "", exports: m[e], config: (t = e, function () { return y && y.config && y.config[t] || {} }) }; var t } }, o = function (e, t, n, r) { var i, o, s, a, l, c, u, d = [], p = typeof n; if (c = S(r = r || e), "undefined" == p || "function" == p) { for (t = !t.length && n.length ? ["require", "exports", "module"] : t, l = 0; l < t.length; l += 1)if ("require" === (o = (a = f(t[l], c)).f)) d[l] = g.require(e); else if ("exports" === o) d[l] = g.exports(e), u = !0; else if ("module" === o) i = d[l] = g.module(e); else if (w(m, o) || w(v, o) || w(_, o)) d[l] = D(o); else { if (!a.p) throw new Error(e + " missing " + o); a.p.load(a.n, A(r, !0), x(o), {}), d[l] = m[o] } s = n ? n.apply(m[e], d) : void 0, e && (i && i.exports !== h && i.exports !== m[e] ? m[e] = i.exports : s === h && u || (m[e] = s)) } else e && (m[e] = n) }, t = n = s = function (e, t, n, r, i) { if ("string" == typeof e) return g[e] ? g[e](t) : D(f(e, S(t)).f); if (!e.splice) { if ((y = e).deps && s(y.deps, y.callback), !t) return; t.splice ? (e = t, t = n, n = null) : e = h } return t = t || function () { }, "function" == typeof n && (n = r, r = i), r ? o(h, e, t, n) : setTimeout(function () { o(h, e, t, n) }, 4), s }, s.config = function (e) { return s(e) }, t._defined = m, (r = function (e, t, n) { if ("string" != typeof e) throw new Error("See almond README: incorrect module build, no module name"); t.splice || (n = t, t = []), w(m, e) || w(v, e) || (v[e] = [e, t, n]) }).amd = { jQuery: !0 }, e.requirejs = t, e.require = n, e.define = r), e.define("almond", function () { }), e.define("jquery", [], function () { var e = u || $; return null == e && console && console.error && console.error("Select2: An instance of jQuery or a jQuery-compatible library was not found. Make sure that you are including jQuery before Select2 on your web page."), e }), e.define("select2/utils", ["jquery"], function (o) { var i = {}; function u(e) { var t = e.prototype, n = []; for (var r in t) { "function" == typeof t[r] && "constructor" !== r && n.push(r) } return n } i.Extend = function (e, t) { var n = {}.hasOwnProperty; function r() { this.constructor = e } for (var i in t) n.call(t, i) && (e[i] = t[i]); return r.prototype = t.prototype, e.prototype = new r, e.__super__ = t.prototype, e }, i.Decorate = function (r, i) { var e = u(i), t = u(r); function o() { var e = Array.prototype.unshift, t = i.prototype.constructor.length, n = r.prototype.constructor; 0 < t && (e.call(arguments, r.prototype.constructor), n = i.prototype.constructor), n.apply(this, arguments) } i.displayName = r.displayName, o.prototype = new function () { this.constructor = o }; for (var n = 0; n < t.length; n++) { var s = t[n]; o.prototype[s] = r.prototype[s] } function a(e) { var t = function () { }; e in o.prototype && (t = o.prototype[e]); var n = i.prototype[e]; return function () { return Array.prototype.unshift.call(arguments, t), n.apply(this, arguments) } } for (var l = 0; l < e.length; l++) { var c = e[l]; o.prototype[c] = a(c) } return o }; function e() { this.listeners = {} } e.prototype.on = function (e, t) { this.listeners = this.listeners || {}, e in this.listeners ? this.listeners[e].push(t) : this.listeners[e] = [t] }, e.prototype.trigger = function (e) { var t = Array.prototype.slice, n = t.call(arguments, 1); this.listeners = this.listeners || {}, null == n && (n = []), 0 === n.length && n.push({}), (n[0]._type = e) in this.listeners && this.invoke(this.listeners[e], t.call(arguments, 1)), "*" in this.listeners && this.invoke(this.listeners["*"], arguments) }, e.prototype.invoke = function (e, t) { for (var n = 0, r = e.length; n < r; n++)e[n].apply(this, t) }, i.Observable = e, i.generateChars = function (e) { for (var t = "", n = 0; n < e; n++) { t += Math.floor(36 * Math.random()).toString(36) } return t }, i.bind = function (e, t) { return function () { e.apply(t, arguments) } }, i._convertData = function (e) { for (var t in e) { var n = t.split("-"), r = e; if (1 !== n.length) { for (var i = 0; i < n.length; i++) { var o = n[i]; (o = o.substring(0, 1).toLowerCase() + o.substring(1)) in r || (r[o] = {}), i == n.length - 1 && (r[o] = e[t]), r = r[o] } delete e[t] } } return e }, i.hasScroll = function (e, t) { var n = o(t), r = t.style.overflowX, i = t.style.overflowY; return (r !== i || "hidden" !== i && "visible" !== i) && ("scroll" === r || "scroll" === i || (n.innerHeight() < t.scrollHeight || n.innerWidth() < t.scrollWidth)) }, i.escapeMarkup = function (e) { var t = { "\\": "&#92;", "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;", "/": "&#47;" }; return "string" != typeof e ? e : String(e).replace(/[&<>"'\/\\]/g, function (e) { return t[e] }) }, i.appendMany = function (e, t) { if ("1.7" === o.fn.jquery.substr(0, 3)) { var n = o(); o.map(t, function (e) { n = n.add(e) }), t = n } e.append(t) }, i.__cache = {}; var n = 0; return i.GetUniqueElementId = function (e) { var t = e.getAttribute("data-select2-id"); return null == t && (e.id ? (t = e.id, e.setAttribute("data-select2-id", t)) : (e.setAttribute("data-select2-id", ++n), t = n.toString())), t }, i.StoreData = function (e, t, n) { var r = i.GetUniqueElementId(e); i.__cache[r] || (i.__cache[r] = {}), i.__cache[r][t] = n }, i.GetData = function (e, t) { var n = i.GetUniqueElementId(e); return t ? i.__cache[n] && null != i.__cache[n][t] ? i.__cache[n][t] : o(e).data(t) : i.__cache[n] }, i.RemoveData = function (e) { var t = i.GetUniqueElementId(e); null != i.__cache[t] && delete i.__cache[t], e.removeAttribute("data-select2-id") }, i }), e.define("select2/results", ["jquery", "./utils"], function (h, f) { function r(e, t, n) { this.$element = e, this.data = n, this.options = t, r.__super__.constructor.call(this) } return f.Extend(r, f.Observable), r.prototype.render = function () { var e = h('<ul class="select2-results__options" role="listbox"></ul>'); return this.options.get("multiple") && e.attr("aria-multiselectable", "true"), this.$results = e }, r.prototype.clear = function () { this.$results.empty() }, r.prototype.displayMessage = function (e) { var t = this.options.get("escapeMarkup"); this.clear(), this.hideLoading(); var n = h('<li role="alert" aria-live="assertive" class="select2-results__option"></li>'), r = this.options.get("translations").get(e.message); n.append(t(r(e.args))), n[0].className += " select2-results__message", this.$results.append(n) }, r.prototype.hideMessages = function () { this.$results.find(".select2-results__message").remove() }, r.prototype.append = function (e) { this.hideLoading(); var t = []; if (null != e.results && 0 !== e.results.length) { e.results = this.sort(e.results); for (var n = 0; n < e.results.length; n++) { var r = e.results[n], i = this.option(r); t.push(i) } this.$results.append(t) } else 0 === this.$results.children().length && this.trigger("results:message", { message: "noResults" }) }, r.prototype.position = function (e, t) { t.find(".select2-results").append(e) }, r.prototype.sort = function (e) { return this.options.get("sorter")(e) }, r.prototype.highlightFirstItem = function () { var e = this.$results.find(".select2-results__option[aria-selected]"), t = e.filter("[aria-selected=true]"); 0 < t.length ? t.first().trigger("mouseenter") : e.first().trigger("mouseenter"), this.ensureHighlightVisible() }, r.prototype.setClasses = function () { var t = this; this.data.current(function (e) { var r = h.map(e, function (e) { return e.id.toString() }); t.$results.find(".select2-results__option[aria-selected]").each(function () { var e = h(this), t = f.GetData(this, "data"), n = "" + t.id; null != t.element && t.element.selected || null == t.element && -1 < h.inArray(n, r) ? e.attr("aria-selected", "true") : e.attr("aria-selected", "false") }) }) }, r.prototype.showLoading = function (e) { this.hideLoading(); var t = { disabled: !0, loading: !0, text: this.options.get("translations").get("searching")(e) }, n = this.option(t); n.className += " loading-results", this.$results.prepend(n) }, r.prototype.hideLoading = function () { this.$results.find(".loading-results").remove() }, r.prototype.option = function (e) { var t = document.createElement("li"); t.className = "select2-results__option"; var n = { role: "option", "aria-selected": "false" }, r = window.Element.prototype.matches || window.Element.prototype.msMatchesSelector || window.Element.prototype.webkitMatchesSelector; for (var i in (null != e.element && r.call(e.element, ":disabled") || null == e.element && e.disabled) && (delete n["aria-selected"], n["aria-disabled"] = "true"), null == e.id && delete n["aria-selected"], null != e._resultId && (t.id = e._resultId), e.title && (t.title = e.title), e.children && (n.role = "group", n["aria-label"] = e.text, delete n["aria-selected"]), n) { var o = n[i]; t.setAttribute(i, o) } if (e.children) { var s = h(t), a = document.createElement("strong"); a.className = "select2-results__group"; h(a); this.template(e, a); for (var l = [], c = 0; c < e.children.length; c++) { var u = e.children[c], d = this.option(u); l.push(d) } var p = h("<ul></ul>", { class: "select2-results__options select2-results__options--nested" }); p.append(l), s.append(a), s.append(p) } else this.template(e, t); return f.StoreData(t, "data", e), t }, r.prototype.bind = function (t, e) { var l = this, n = t.id + "-results"; this.$results.attr("id", n), t.on("results:all", function (e) { l.clear(), l.append(e.data), t.isOpen() && (l.setClasses(), l.highlightFirstItem()) }), t.on("results:append", function (e) { l.append(e.data), t.isOpen() && l.setClasses() }), t.on("query", function (e) { l.hideMessages(), l.showLoading(e) }), t.on("select", function () { t.isOpen() && (l.setClasses(), l.options.get("scrollAfterSelect") && l.highlightFirstItem()) }), t.on("unselect", function () { t.isOpen() && (l.setClasses(), l.options.get("scrollAfterSelect") && l.highlightFirstItem()) }), t.on("open", function () { l.$results.attr("aria-expanded", "true"), l.$results.attr("aria-hidden", "false"), l.setClasses(), l.ensureHighlightVisible() }), t.on("close", function () { l.$results.attr("aria-expanded", "false"), l.$results.attr("aria-hidden", "true"), l.$results.removeAttr("aria-activedescendant") }), t.on("results:toggle", function () { var e = l.getHighlightedResults(); 0 !== e.length && e.trigger("mouseup") }), t.on("results:select", function () { var e = l.getHighlightedResults(); if (0 !== e.length) { var t = f.GetData(e[0], "data"); "true" == e.attr("aria-selected") ? l.trigger("close", {}) : l.trigger("select", { data: t }) } }), t.on("results:previous", function () { var e = l.getHighlightedResults(), t = l.$results.find("[aria-selected]"), n = t.index(e); if (!(n <= 0)) { var r = n - 1; 0 === e.length && (r = 0); var i = t.eq(r); i.trigger("mouseenter"); var o = l.$results.offset().top, s = i.offset().top, a = l.$results.scrollTop() + (s - o); 0 === r ? l.$results.scrollTop(0) : s - o < 0 && l.$results.scrollTop(a) } }), t.on("results:next", function () { var e = l.getHighlightedResults(), t = l.$results.find("[aria-selected]"), n = t.index(e) + 1; if (!(n >= t.length)) { var r = t.eq(n); r.trigger("mouseenter"); var i = l.$results.offset().top + l.$results.outerHeight(!1), o = r.offset().top + r.outerHeight(!1), s = l.$results.scrollTop() + o - i; 0 === n ? l.$results.scrollTop(0) : i < o && l.$results.scrollTop(s) } }), t.on("results:focus", function (e) { e.element.addClass("select2-results__option--highlighted") }), t.on("results:message", function (e) { l.displayMessage(e) }), h.fn.mousewheel && this.$results.on("mousewheel", function (e) { var t = l.$results.scrollTop(), n = l.$results.get(0).scrollHeight - t + e.deltaY, r = 0 < e.deltaY && t - e.deltaY <= 0, i = e.deltaY < 0 && n <= l.$results.height(); r ? (l.$results.scrollTop(0), e.preventDefault(), e.stopPropagation()) : i && (l.$results.scrollTop(l.$results.get(0).scrollHeight - l.$results.height()), e.preventDefault(), e.stopPropagation()) }), this.$results.on("mouseup", ".select2-results__option[aria-selected]", function (e) { var t = h(this), n = f.GetData(this, "data"); "true" !== t.attr("aria-selected") ? l.trigger("select", { originalEvent: e, data: n }) : l.options.get("multiple") ? l.trigger("unselect", { originalEvent: e, data: n }) : l.trigger("close", {}) }), this.$results.on("mouseenter", ".select2-results__option[aria-selected]", function (e) { var t = f.GetData(this, "data"); l.getHighlightedResults().removeClass("select2-results__option--highlighted"), l.trigger("results:focus", { data: t, element: h(this) }) }) }, r.prototype.getHighlightedResults = function () { return this.$results.find(".select2-results__option--highlighted") }, r.prototype.destroy = function () { this.$results.remove() }, r.prototype.ensureHighlightVisible = function () { var e = this.getHighlightedResults(); if (0 !== e.length) { var t = this.$results.find("[aria-selected]").index(e), n = this.$results.offset().top, r = e.offset().top, i = this.$results.scrollTop() + (r - n), o = r - n; i -= 2 * e.outerHeight(!1), t <= 2 ? this.$results.scrollTop(0) : (o > this.$results.outerHeight() || o < 0) && this.$results.scrollTop(i) } }, r.prototype.template = function (e, t) { var n = this.options.get("templateResult"), r = this.options.get("escapeMarkup"), i = n(e, t); null == i ? t.style.display = "none" : "string" == typeof i ? t.innerHTML = r(i) : h(t).append(i) }, r }), e.define("select2/keys", [], function () { return { BACKSPACE: 8, TAB: 9, ENTER: 13, SHIFT: 16, CTRL: 17, ALT: 18, ESC: 27, SPACE: 32, PAGE_UP: 33, PAGE_DOWN: 34, END: 35, HOME: 36, LEFT: 37, UP: 38, RIGHT: 39, DOWN: 40, DELETE: 46 } }), e.define("select2/selection/base", ["jquery", "../utils", "../keys"], function (n, r, i) { function o(e, t) { this.$element = e, this.options = t, o.__super__.constructor.call(this) } return r.Extend(o, r.Observable), o.prototype.render = function () { var e = n('<span class="select2-selection" role="combobox"  aria-haspopup="true" aria-expanded="false"></span>'); return this._tabindex = 0, null != r.GetData(this.$element[0], "old-tabindex") ? this._tabindex = r.GetData(this.$element[0], "old-tabindex") : null != this.$element.attr("tabindex") && (this._tabindex = this.$element.attr("tabindex")), e.attr("title", this.$element.attr("title")), e.attr("tabindex", this._tabindex), e.attr("aria-disabled", "false"), this.$selection = e }, o.prototype.bind = function (e, t) { var n = this, r = e.id + "-results"; this.container = e, this.$selection.on("focus", function (e) { n.trigger("focus", e) }), this.$selection.on("blur", function (e) { n._handleBlur(e) }), this.$selection.on("keydown", function (e) { n.trigger("keypress", e), e.which === i.SPACE && e.preventDefault() }), e.on("results:focus", function (e) { n.$selection.attr("aria-activedescendant", e.data._resultId) }), e.on("selection:update", function (e) { n.update(e.data) }), e.on("open", function () { n.$selection.attr("aria-expanded", "true"), n.$selection.attr("aria-owns", r), n._attachCloseHandler(e) }), e.on("close", function () { n.$selection.attr("aria-expanded", "false"), n.$selection.removeAttr("aria-activedescendant"), n.$selection.removeAttr("aria-owns"), n.$selection.trigger("focus"), n._detachCloseHandler(e) }), e.on("enable", function () { n.$selection.attr("tabindex", n._tabindex), n.$selection.attr("aria-disabled", "false") }), e.on("disable", function () { n.$selection.attr("tabindex", "-1"), n.$selection.attr("aria-disabled", "true") }) }, o.prototype._handleBlur = function (e) { var t = this; window.setTimeout(function () { document.activeElement == t.$selection[0] || n.contains(t.$selection[0], document.activeElement) || t.trigger("blur", e) }, 1) }, o.prototype._attachCloseHandler = function (e) { n(document.body).on("mousedown.select2." + e.id, function (e) { var t = n(e.target).closest(".select2"); n(".select2.select2-container--open").each(function () { this != t[0] && r.GetData(this, "element").select2("close") }) }) }, o.prototype._detachCloseHandler = function (e) { n(document.body).off("mousedown.select2." + e.id) }, o.prototype.position = function (e, t) { t.find(".selection").append(e) }, o.prototype.destroy = function () { this._detachCloseHandler(this.container) }, o.prototype.update = function (e) { throw new Error("The `update` method must be defined in child classes.") }, o.prototype.isEnabled = function () { return !this.isDisabled() }, o.prototype.isDisabled = function () { return this.options.get("disabled") }, o }), e.define("select2/selection/single", ["jquery", "./base", "../utils", "../keys"], function (e, t, n, r) { function i() { i.__super__.constructor.apply(this, arguments) } return n.Extend(i, t), i.prototype.render = function () { var e = i.__super__.render.call(this); return e.addClass("select2-selection--single"), e.html('<span class="select2-selection__rendered"></span><span class="select2-selection__arrow" role="presentation"><b role="presentation"></b></span>'), e }, i.prototype.bind = function (t, e) { var n = this; i.__super__.bind.apply(this, arguments); var r = t.id + "-container"; this.$selection.find(".select2-selection__rendered").attr("id", r).attr("role", "textbox").attr("aria-readonly", "true"), this.$selection.attr("aria-labelledby", r), this.$selection.on("mousedown", function (e) { 1 === e.which && n.trigger("toggle", { originalEvent: e }) }), this.$selection.on("focus", function (e) { }), this.$selection.on("blur", function (e) { }), t.on("focus", function (e) { t.isOpen() || n.$selection.trigger("focus") }) }, i.prototype.clear = function () { var e = this.$selection.find(".select2-selection__rendered"); e.empty(), e.removeAttr("title") }, i.prototype.display = function (e, t) { var n = this.options.get("templateSelection"); return this.options.get("escapeMarkup")(n(e, t)) }, i.prototype.selectionContainer = function () { return e("<span></span>") }, i.prototype.update = function (e) { if (0 !== e.length) { var t = e[0], n = this.$selection.find(".select2-selection__rendered"), r = this.display(t, n); n.empty().append(r); var i = t.title || t.text; i ? n.attr("title", i) : n.removeAttr("title") } else this.clear() }, i }), e.define("select2/selection/multiple", ["jquery", "./base", "../utils"], function (i, e, l) { function n(e, t) { n.__super__.constructor.apply(this, arguments) } return l.Extend(n, e), n.prototype.render = function () { var e = n.__super__.render.call(this); return e.addClass("select2-selection--multiple"), e.html('<ul class="select2-selection__rendered"></ul>'), e }, n.prototype.bind = function (e, t) { var r = this; n.__super__.bind.apply(this, arguments), this.$selection.on("click", function (e) { r.trigger("toggle", { originalEvent: e }) }), this.$selection.on("click", ".select2-selection__choice__remove", function (e) { if (!r.isDisabled()) { var t = i(this).parent(), n = l.GetData(t[0], "data"); r.trigger("unselect", { originalEvent: e, data: n }) } }) }, n.prototype.clear = function () { var e = this.$selection.find(".select2-selection__rendered"); e.empty(), e.removeAttr("title") }, n.prototype.display = function (e, t) { var n = this.options.get("templateSelection"); return this.options.get("escapeMarkup")(n(e, t)) }, n.prototype.selectionContainer = function () { return i('<li class="select2-selection__choice"><span class="select2-selection__choice__remove" role="presentation">&times;</span></li>') }, n.prototype.update = function (e) { if (this.clear(), 0 !== e.length) { for (var t = [], n = 0; n < e.length; n++) { var r = e[n], i = this.selectionContainer(), o = this.display(r, i); i.append(o); var s = r.title || r.text; s && i.attr("title", s), l.StoreData(i[0], "data", r), t.push(i) } var a = this.$selection.find(".select2-selection__rendered"); l.appendMany(a, t) } }, n }), e.define("select2/selection/placeholder", ["../utils"], function (e) { function t(e, t, n) { this.placeholder = this.normalizePlaceholder(n.get("placeholder")), e.call(this, t, n) } return t.prototype.normalizePlaceholder = function (e, t) { return "string" == typeof t && (t = { id: "", text: t }), t }, t.prototype.createPlaceholder = function (e, t) { var n = this.selectionContainer(); return n.html(this.display(t)), n.addClass("select2-selection__placeholder").removeClass("select2-selection__choice"), n }, t.prototype.update = function (e, t) { var n = 1 == t.length && t[0].id != this.placeholder.id; if (1 < t.length || n) return e.call(this, t); this.clear(); var r = this.createPlaceholder(this.placeholder); this.$selection.find(".select2-selection__rendered").append(r) }, t }), e.define("select2/selection/allowClear", ["jquery", "../keys", "../utils"], function (i, r, a) { function e() { } return e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), null == this.placeholder && this.options.get("debug") && window.console && console.error && console.error("Select2: The `allowClear` option should be used in combination with the `placeholder` option."), this.$selection.on("mousedown", ".select2-selection__clear", function (e) { r._handleClear(e) }), t.on("keypress", function (e) { r._handleKeyboardClear(e, t) }) }, e.prototype._handleClear = function (e, t) { if (!this.isDisabled()) { var n = this.$selection.find(".select2-selection__clear"); if (0 !== n.length) { t.stopPropagation(); var r = a.GetData(n[0], "data"), i = this.$element.val(); this.$element.val(this.placeholder.id); var o = { data: r }; if (this.trigger("clear", o), o.prevented) this.$element.val(i); else { for (var s = 0; s < r.length; s++)if (o = { data: r[s] }, this.trigger("unselect", o), o.prevented) return void this.$element.val(i); this.$element.trigger("input").trigger("change"), this.trigger("toggle", {}) } } } }, e.prototype._handleKeyboardClear = function (e, t, n) { n.isOpen() || t.which != r.DELETE && t.which != r.BACKSPACE || this._handleClear(t) }, e.prototype.update = function (e, t) { if (e.call(this, t), !(0 < this.$selection.find(".select2-selection__placeholder").length || 0 === t.length)) { var n = this.options.get("translations").get("removeAllItems"), r = i('<span class="select2-selection__clear" title="' + n() + '">&times;</span>'); a.StoreData(r[0], "data", t), this.$selection.find(".select2-selection__rendered").prepend(r) } }, e }), e.define("select2/selection/search", ["jquery", "../utils", "../keys"], function (r, a, l) { function e(e, t, n) { e.call(this, t, n) } return e.prototype.render = function (e) { var t = r('<li class="select2-search select2-search--inline"><input class="select2-search__field" type="search" tabindex="-1" autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false" role="searchbox" aria-autocomplete="list" /></li>'); this.$searchContainer = t, this.$search = t.find("input"); var n = e.call(this); return this._transferTabIndex(), n }, e.prototype.bind = function (e, t, n) { var r = this, i = t.id + "-results"; e.call(this, t, n), t.on("open", function () { r.$search.attr("aria-controls", i), r.$search.trigger("focus") }), t.on("close", function () { r.$search.val(""), r.$search.removeAttr("aria-controls"), r.$search.removeAttr("aria-activedescendant"), r.$search.trigger("focus") }), t.on("enable", function () { r.$search.prop("disabled", !1), r._transferTabIndex() }), t.on("disable", function () { r.$search.prop("disabled", !0) }), t.on("focus", function (e) { r.$search.trigger("focus") }), t.on("results:focus", function (e) { e.data._resultId ? r.$search.attr("aria-activedescendant", e.data._resultId) : r.$search.removeAttr("aria-activedescendant") }), this.$selection.on("focusin", ".select2-search--inline", function (e) { r.trigger("focus", e) }), this.$selection.on("focusout", ".select2-search--inline", function (e) { r._handleBlur(e) }), this.$selection.on("keydown", ".select2-search--inline", function (e) { if (e.stopPropagation(), r.trigger("keypress", e), r._keyUpPrevented = e.isDefaultPrevented(), e.which === l.BACKSPACE && "" === r.$search.val()) { var t = r.$searchContainer.prev(".select2-selection__choice"); if (0 < t.length) { var n = a.GetData(t[0], "data"); r.searchRemoveChoice(n), e.preventDefault() } } }), this.$selection.on("click", ".select2-search--inline", function (e) { r.$search.val() && e.stopPropagation() }); var o = document.documentMode, s = o && o <= 11; this.$selection.on("input.searchcheck", ".select2-search--inline", function (e) { s ? r.$selection.off("input.search input.searchcheck") : r.$selection.off("keyup.search") }), this.$selection.on("keyup.search input.search", ".select2-search--inline", function (e) { if (s && "input" === e.type) r.$selection.off("input.search input.searchcheck"); else { var t = e.which; t != l.SHIFT && t != l.CTRL && t != l.ALT && t != l.TAB && r.handleSearch(e) } }) }, e.prototype._transferTabIndex = function (e) { this.$search.attr("tabindex", this.$selection.attr("tabindex")), this.$selection.attr("tabindex", "-1") }, e.prototype.createPlaceholder = function (e, t) { this.$search.attr("placeholder", t.text) }, e.prototype.update = function (e, t) { var n = this.$search[0] == document.activeElement; this.$search.attr("placeholder", ""), e.call(this, t), this.$selection.find(".select2-selection__rendered").append(this.$searchContainer), this.resizeSearch(), n && this.$search.trigger("focus") }, e.prototype.handleSearch = function () { if (this.resizeSearch(), !this._keyUpPrevented) { var e = this.$search.val(); this.trigger("query", { term: e }) } this._keyUpPrevented = !1 }, e.prototype.searchRemoveChoice = function (e, t) { this.trigger("unselect", { data: t }), this.$search.val(t.text), this.handleSearch() }, e.prototype.resizeSearch = function () { this.$search.css("width", "25px"); var e = ""; "" !== this.$search.attr("placeholder") ? e = this.$selection.find(".select2-selection__rendered").width() : e = .75 * (this.$search.val().length + 1) + "em"; this.$search.css("width", e) }, e }), e.define("select2/selection/eventRelay", ["jquery"], function (s) { function e() { } return e.prototype.bind = function (e, t, n) { var r = this, i = ["open", "opening", "close", "closing", "select", "selecting", "unselect", "unselecting", "clear", "clearing"], o = ["opening", "closing", "selecting", "unselecting", "clearing"]; e.call(this, t, n), t.on("*", function (e, t) { if (-1 !== s.inArray(e, i)) { t = t || {}; var n = s.Event("select2:" + e, { params: t }); r.$element.trigger(n), -1 !== s.inArray(e, o) && (t.prevented = n.isDefaultPrevented()) } }) }, e }), e.define("select2/translation", ["jquery", "require"], function (t, n) { function r(e) { this.dict = e || {} } return r.prototype.all = function () { return this.dict }, r.prototype.get = function (e) { return this.dict[e] }, r.prototype.extend = function (e) { this.dict = t.extend({}, e.all(), this.dict) }, r._cache = {}, r.loadPath = function (e) { if (!(e in r._cache)) { var t = n(e); r._cache[e] = t } return new r(r._cache[e]) }, r }), e.define("select2/diacritics", [], function () { return { "Ⓐ": "A", "Ａ": "A", "À": "A", "Á": "A", "Â": "A", "Ầ": "A", "Ấ": "A", "Ẫ": "A", "Ẩ": "A", "Ã": "A", "Ā": "A", "Ă": "A", "Ằ": "A", "Ắ": "A", "Ẵ": "A", "Ẳ": "A", "Ȧ": "A", "Ǡ": "A", "Ä": "A", "Ǟ": "A", "Ả": "A", "Å": "A", "Ǻ": "A", "Ǎ": "A", "Ȁ": "A", "Ȃ": "A", "Ạ": "A", "Ậ": "A", "Ặ": "A", "Ḁ": "A", "Ą": "A", "Ⱥ": "A", "Ɐ": "A", "Ꜳ": "AA", "Æ": "AE", "Ǽ": "AE", "Ǣ": "AE", "Ꜵ": "AO", "Ꜷ": "AU", "Ꜹ": "AV", "Ꜻ": "AV", "Ꜽ": "AY", "Ⓑ": "B", "Ｂ": "B", "Ḃ": "B", "Ḅ": "B", "Ḇ": "B", "Ƀ": "B", "Ƃ": "B", "Ɓ": "B", "Ⓒ": "C", "Ｃ": "C", "Ć": "C", "Ĉ": "C", "Ċ": "C", "Č": "C", "Ç": "C", "Ḉ": "C", "Ƈ": "C", "Ȼ": "C", "Ꜿ": "C", "Ⓓ": "D", "Ｄ": "D", "Ḋ": "D", "Ď": "D", "Ḍ": "D", "Ḑ": "D", "Ḓ": "D", "Ḏ": "D", "Đ": "D", "Ƌ": "D", "Ɗ": "D", "Ɖ": "D", "Ꝺ": "D", "Ǳ": "DZ", "Ǆ": "DZ", "ǲ": "Dz", "ǅ": "Dz", "Ⓔ": "E", "Ｅ": "E", "È": "E", "É": "E", "Ê": "E", "Ề": "E", "Ế": "E", "Ễ": "E", "Ể": "E", "Ẽ": "E", "Ē": "E", "Ḕ": "E", "Ḗ": "E", "Ĕ": "E", "Ė": "E", "Ë": "E", "Ẻ": "E", "Ě": "E", "Ȅ": "E", "Ȇ": "E", "Ẹ": "E", "Ệ": "E", "Ȩ": "E", "Ḝ": "E", "Ę": "E", "Ḙ": "E", "Ḛ": "E", "Ɛ": "E", "Ǝ": "E", "Ⓕ": "F", "Ｆ": "F", "Ḟ": "F", "Ƒ": "F", "Ꝼ": "F", "Ⓖ": "G", "Ｇ": "G", "Ǵ": "G", "Ĝ": "G", "Ḡ": "G", "Ğ": "G", "Ġ": "G", "Ǧ": "G", "Ģ": "G", "Ǥ": "G", "Ɠ": "G", "Ꞡ": "G", "Ᵹ": "G", "Ꝿ": "G", "Ⓗ": "H", "Ｈ": "H", "Ĥ": "H", "Ḣ": "H", "Ḧ": "H", "Ȟ": "H", "Ḥ": "H", "Ḩ": "H", "Ḫ": "H", "Ħ": "H", "Ⱨ": "H", "Ⱶ": "H", "Ɥ": "H", "Ⓘ": "I", "Ｉ": "I", "Ì": "I", "Í": "I", "Î": "I", "Ĩ": "I", "Ī": "I", "Ĭ": "I", "İ": "I", "Ï": "I", "Ḯ": "I", "Ỉ": "I", "Ǐ": "I", "Ȉ": "I", "Ȋ": "I", "Ị": "I", "Į": "I", "Ḭ": "I", "Ɨ": "I", "Ⓙ": "J", "Ｊ": "J", "Ĵ": "J", "Ɉ": "J", "Ⓚ": "K", "Ｋ": "K", "Ḱ": "K", "Ǩ": "K", "Ḳ": "K", "Ķ": "K", "Ḵ": "K", "Ƙ": "K", "Ⱪ": "K", "Ꝁ": "K", "Ꝃ": "K", "Ꝅ": "K", "Ꞣ": "K", "Ⓛ": "L", "Ｌ": "L", "Ŀ": "L", "Ĺ": "L", "Ľ": "L", "Ḷ": "L", "Ḹ": "L", "Ļ": "L", "Ḽ": "L", "Ḻ": "L", "Ł": "L", "Ƚ": "L", "Ɫ": "L", "Ⱡ": "L", "Ꝉ": "L", "Ꝇ": "L", "Ꞁ": "L", "Ǉ": "LJ", "ǈ": "Lj", "Ⓜ": "M", "Ｍ": "M", "Ḿ": "M", "Ṁ": "M", "Ṃ": "M", "Ɱ": "M", "Ɯ": "M", "Ⓝ": "N", "Ｎ": "N", "Ǹ": "N", "Ń": "N", "Ñ": "N", "Ṅ": "N", "Ň": "N", "Ṇ": "N", "Ņ": "N", "Ṋ": "N", "Ṉ": "N", "Ƞ": "N", "Ɲ": "N", "Ꞑ": "N", "Ꞥ": "N", "Ǌ": "NJ", "ǋ": "Nj", "Ⓞ": "O", "Ｏ": "O", "Ò": "O", "Ó": "O", "Ô": "O", "Ồ": "O", "Ố": "O", "Ỗ": "O", "Ổ": "O", "Õ": "O", "Ṍ": "O", "Ȭ": "O", "Ṏ": "O", "Ō": "O", "Ṑ": "O", "Ṓ": "O", "Ŏ": "O", "Ȯ": "O", "Ȱ": "O", "Ö": "O", "Ȫ": "O", "Ỏ": "O", "Ő": "O", "Ǒ": "O", "Ȍ": "O", "Ȏ": "O", "Ơ": "O", "Ờ": "O", "Ớ": "O", "Ỡ": "O", "Ở": "O", "Ợ": "O", "Ọ": "O", "Ộ": "O", "Ǫ": "O", "Ǭ": "O", "Ø": "O", "Ǿ": "O", "Ɔ": "O", "Ɵ": "O", "Ꝋ": "O", "Ꝍ": "O", "Œ": "OE", "Ƣ": "OI", "Ꝏ": "OO", "Ȣ": "OU", "Ⓟ": "P", "Ｐ": "P", "Ṕ": "P", "Ṗ": "P", "Ƥ": "P", "Ᵽ": "P", "Ꝑ": "P", "Ꝓ": "P", "Ꝕ": "P", "Ⓠ": "Q", "Ｑ": "Q", "Ꝗ": "Q", "Ꝙ": "Q", "Ɋ": "Q", "Ⓡ": "R", "Ｒ": "R", "Ŕ": "R", "Ṙ": "R", "Ř": "R", "Ȑ": "R", "Ȓ": "R", "Ṛ": "R", "Ṝ": "R", "Ŗ": "R", "Ṟ": "R", "Ɍ": "R", "Ɽ": "R", "Ꝛ": "R", "Ꞧ": "R", "Ꞃ": "R", "Ⓢ": "S", "Ｓ": "S", "ẞ": "S", "Ś": "S", "Ṥ": "S", "Ŝ": "S", "Ṡ": "S", "Š": "S", "Ṧ": "S", "Ṣ": "S", "Ṩ": "S", "Ș": "S", "Ş": "S", "Ȿ": "S", "Ꞩ": "S", "Ꞅ": "S", "Ⓣ": "T", "Ｔ": "T", "Ṫ": "T", "Ť": "T", "Ṭ": "T", "Ț": "T", "Ţ": "T", "Ṱ": "T", "Ṯ": "T", "Ŧ": "T", "Ƭ": "T", "Ʈ": "T", "Ⱦ": "T", "Ꞇ": "T", "Ꜩ": "TZ", "Ⓤ": "U", "Ｕ": "U", "Ù": "U", "Ú": "U", "Û": "U", "Ũ": "U", "Ṹ": "U", "Ū": "U", "Ṻ": "U", "Ŭ": "U", "Ü": "U", "Ǜ": "U", "Ǘ": "U", "Ǖ": "U", "Ǚ": "U", "Ủ": "U", "Ů": "U", "Ű": "U", "Ǔ": "U", "Ȕ": "U", "Ȗ": "U", "Ư": "U", "Ừ": "U", "Ứ": "U", "Ữ": "U", "Ử": "U", "Ự": "U", "Ụ": "U", "Ṳ": "U", "Ų": "U", "Ṷ": "U", "Ṵ": "U", "Ʉ": "U", "Ⓥ": "V", "Ｖ": "V", "Ṽ": "V", "Ṿ": "V", "Ʋ": "V", "Ꝟ": "V", "Ʌ": "V", "Ꝡ": "VY", "Ⓦ": "W", "Ｗ": "W", "Ẁ": "W", "Ẃ": "W", "Ŵ": "W", "Ẇ": "W", "Ẅ": "W", "Ẉ": "W", "Ⱳ": "W", "Ⓧ": "X", "Ｘ": "X", "Ẋ": "X", "Ẍ": "X", "Ⓨ": "Y", "Ｙ": "Y", "Ỳ": "Y", "Ý": "Y", "Ŷ": "Y", "Ỹ": "Y", "Ȳ": "Y", "Ẏ": "Y", "Ÿ": "Y", "Ỷ": "Y", "Ỵ": "Y", "Ƴ": "Y", "Ɏ": "Y", "Ỿ": "Y", "Ⓩ": "Z", "Ｚ": "Z", "Ź": "Z", "Ẑ": "Z", "Ż": "Z", "Ž": "Z", "Ẓ": "Z", "Ẕ": "Z", "Ƶ": "Z", "Ȥ": "Z", "Ɀ": "Z", "Ⱬ": "Z", "Ꝣ": "Z", "ⓐ": "a", "ａ": "a", "ẚ": "a", "à": "a", "á": "a", "â": "a", "ầ": "a", "ấ": "a", "ẫ": "a", "ẩ": "a", "ã": "a", "ā": "a", "ă": "a", "ằ": "a", "ắ": "a", "ẵ": "a", "ẳ": "a", "ȧ": "a", "ǡ": "a", "ä": "a", "ǟ": "a", "ả": "a", "å": "a", "ǻ": "a", "ǎ": "a", "ȁ": "a", "ȃ": "a", "ạ": "a", "ậ": "a", "ặ": "a", "ḁ": "a", "ą": "a", "ⱥ": "a", "ɐ": "a", "ꜳ": "aa", "æ": "ae", "ǽ": "ae", "ǣ": "ae", "ꜵ": "ao", "ꜷ": "au", "ꜹ": "av", "ꜻ": "av", "ꜽ": "ay", "ⓑ": "b", "ｂ": "b", "ḃ": "b", "ḅ": "b", "ḇ": "b", "ƀ": "b", "ƃ": "b", "ɓ": "b", "ⓒ": "c", "ｃ": "c", "ć": "c", "ĉ": "c", "ċ": "c", "č": "c", "ç": "c", "ḉ": "c", "ƈ": "c", "ȼ": "c", "ꜿ": "c", "ↄ": "c", "ⓓ": "d", "ｄ": "d", "ḋ": "d", "ď": "d", "ḍ": "d", "ḑ": "d", "ḓ": "d", "ḏ": "d", "đ": "d", "ƌ": "d", "ɖ": "d", "ɗ": "d", "ꝺ": "d", "ǳ": "dz", "ǆ": "dz", "ⓔ": "e", "ｅ": "e", "è": "e", "é": "e", "ê": "e", "ề": "e", "ế": "e", "ễ": "e", "ể": "e", "ẽ": "e", "ē": "e", "ḕ": "e", "ḗ": "e", "ĕ": "e", "ė": "e", "ë": "e", "ẻ": "e", "ě": "e", "ȅ": "e", "ȇ": "e", "ẹ": "e", "ệ": "e", "ȩ": "e", "ḝ": "e", "ę": "e", "ḙ": "e", "ḛ": "e", "ɇ": "e", "ɛ": "e", "ǝ": "e", "ⓕ": "f", "ｆ": "f", "ḟ": "f", "ƒ": "f", "ꝼ": "f", "ⓖ": "g", "ｇ": "g", "ǵ": "g", "ĝ": "g", "ḡ": "g", "ğ": "g", "ġ": "g", "ǧ": "g", "ģ": "g", "ǥ": "g", "ɠ": "g", "ꞡ": "g", "ᵹ": "g", "ꝿ": "g", "ⓗ": "h", "ｈ": "h", "ĥ": "h", "ḣ": "h", "ḧ": "h", "ȟ": "h", "ḥ": "h", "ḩ": "h", "ḫ": "h", "ẖ": "h", "ħ": "h", "ⱨ": "h", "ⱶ": "h", "ɥ": "h", "ƕ": "hv", "ⓘ": "i", "ｉ": "i", "ì": "i", "í": "i", "î": "i", "ĩ": "i", "ī": "i", "ĭ": "i", "ï": "i", "ḯ": "i", "ỉ": "i", "ǐ": "i", "ȉ": "i", "ȋ": "i", "ị": "i", "į": "i", "ḭ": "i", "ɨ": "i", "ı": "i", "ⓙ": "j", "ｊ": "j", "ĵ": "j", "ǰ": "j", "ɉ": "j", "ⓚ": "k", "ｋ": "k", "ḱ": "k", "ǩ": "k", "ḳ": "k", "ķ": "k", "ḵ": "k", "ƙ": "k", "ⱪ": "k", "ꝁ": "k", "ꝃ": "k", "ꝅ": "k", "ꞣ": "k", "ⓛ": "l", "ｌ": "l", "ŀ": "l", "ĺ": "l", "ľ": "l", "ḷ": "l", "ḹ": "l", "ļ": "l", "ḽ": "l", "ḻ": "l", "ſ": "l", "ł": "l", "ƚ": "l", "ɫ": "l", "ⱡ": "l", "ꝉ": "l", "ꞁ": "l", "ꝇ": "l", "ǉ": "lj", "ⓜ": "m", "ｍ": "m", "ḿ": "m", "ṁ": "m", "ṃ": "m", "ɱ": "m", "ɯ": "m", "ⓝ": "n", "ｎ": "n", "ǹ": "n", "ń": "n", "ñ": "n", "ṅ": "n", "ň": "n", "ṇ": "n", "ņ": "n", "ṋ": "n", "ṉ": "n", "ƞ": "n", "ɲ": "n", "ŉ": "n", "ꞑ": "n", "ꞥ": "n", "ǌ": "nj", "ⓞ": "o", "ｏ": "o", "ò": "o", "ó": "o", "ô": "o", "ồ": "o", "ố": "o", "ỗ": "o", "ổ": "o", "õ": "o", "ṍ": "o", "ȭ": "o", "ṏ": "o", "ō": "o", "ṑ": "o", "ṓ": "o", "ŏ": "o", "ȯ": "o", "ȱ": "o", "ö": "o", "ȫ": "o", "ỏ": "o", "ő": "o", "ǒ": "o", "ȍ": "o", "ȏ": "o", "ơ": "o", "ờ": "o", "ớ": "o", "ỡ": "o", "ở": "o", "ợ": "o", "ọ": "o", "ộ": "o", "ǫ": "o", "ǭ": "o", "ø": "o", "ǿ": "o", "ɔ": "o", "ꝋ": "o", "ꝍ": "o", "ɵ": "o", "œ": "oe", "ƣ": "oi", "ȣ": "ou", "ꝏ": "oo", "ⓟ": "p", "ｐ": "p", "ṕ": "p", "ṗ": "p", "ƥ": "p", "ᵽ": "p", "ꝑ": "p", "ꝓ": "p", "ꝕ": "p", "ⓠ": "q", "ｑ": "q", "ɋ": "q", "ꝗ": "q", "ꝙ": "q", "ⓡ": "r", "ｒ": "r", "ŕ": "r", "ṙ": "r", "ř": "r", "ȑ": "r", "ȓ": "r", "ṛ": "r", "ṝ": "r", "ŗ": "r", "ṟ": "r", "ɍ": "r", "ɽ": "r", "ꝛ": "r", "ꞧ": "r", "ꞃ": "r", "ⓢ": "s", "ｓ": "s", "ß": "s", "ś": "s", "ṥ": "s", "ŝ": "s", "ṡ": "s", "š": "s", "ṧ": "s", "ṣ": "s", "ṩ": "s", "ș": "s", "ş": "s", "ȿ": "s", "ꞩ": "s", "ꞅ": "s", "ẛ": "s", "ⓣ": "t", "ｔ": "t", "ṫ": "t", "ẗ": "t", "ť": "t", "ṭ": "t", "ț": "t", "ţ": "t", "ṱ": "t", "ṯ": "t", "ŧ": "t", "ƭ": "t", "ʈ": "t", "ⱦ": "t", "ꞇ": "t", "ꜩ": "tz", "ⓤ": "u", "ｕ": "u", "ù": "u", "ú": "u", "û": "u", "ũ": "u", "ṹ": "u", "ū": "u", "ṻ": "u", "ŭ": "u", "ü": "u", "ǜ": "u", "ǘ": "u", "ǖ": "u", "ǚ": "u", "ủ": "u", "ů": "u", "ű": "u", "ǔ": "u", "ȕ": "u", "ȗ": "u", "ư": "u", "ừ": "u", "ứ": "u", "ữ": "u", "ử": "u", "ự": "u", "ụ": "u", "ṳ": "u", "ų": "u", "ṷ": "u", "ṵ": "u", "ʉ": "u", "ⓥ": "v", "ｖ": "v", "ṽ": "v", "ṿ": "v", "ʋ": "v", "ꝟ": "v", "ʌ": "v", "ꝡ": "vy", "ⓦ": "w", "ｗ": "w", "ẁ": "w", "ẃ": "w", "ŵ": "w", "ẇ": "w", "ẅ": "w", "ẘ": "w", "ẉ": "w", "ⱳ": "w", "ⓧ": "x", "ｘ": "x", "ẋ": "x", "ẍ": "x", "ⓨ": "y", "ｙ": "y", "ỳ": "y", "ý": "y", "ŷ": "y", "ỹ": "y", "ȳ": "y", "ẏ": "y", "ÿ": "y", "ỷ": "y", "ẙ": "y", "ỵ": "y", "ƴ": "y", "ɏ": "y", "ỿ": "y", "ⓩ": "z", "ｚ": "z", "ź": "z", "ẑ": "z", "ż": "z", "ž": "z", "ẓ": "z", "ẕ": "z", "ƶ": "z", "ȥ": "z", "ɀ": "z", "ⱬ": "z", "ꝣ": "z", "Ά": "Α", "Έ": "Ε", "Ή": "Η", "Ί": "Ι", "Ϊ": "Ι", "Ό": "Ο", "Ύ": "Υ", "Ϋ": "Υ", "Ώ": "Ω", "ά": "α", "έ": "ε", "ή": "η", "ί": "ι", "ϊ": "ι", "ΐ": "ι", "ό": "ο", "ύ": "υ", "ϋ": "υ", "ΰ": "υ", "ώ": "ω", "ς": "σ", "’": "'" } }), e.define("select2/data/base", ["../utils"], function (r) { function n(e, t) { n.__super__.constructor.call(this) } return r.Extend(n, r.Observable), n.prototype.current = function (e) { throw new Error("The `current` method must be defined in child classes.") }, n.prototype.query = function (e, t) { throw new Error("The `query` method must be defined in child classes.") }, n.prototype.bind = function (e, t) { }, n.prototype.destroy = function () { }, n.prototype.generateResultId = function (e, t) { var n = e.id + "-result-"; return n += r.generateChars(4), null != t.id ? n += "-" + t.id.toString() : n += "-" + r.generateChars(4), n }, n }), e.define("select2/data/select", ["./base", "../utils", "jquery"], function (e, a, l) { function n(e, t) { this.$element = e, this.options = t, n.__super__.constructor.call(this) } return a.Extend(n, e), n.prototype.current = function (e) { var n = [], r = this; this.$element.find(":selected").each(function () { var e = l(this), t = r.item(e); n.push(t) }), e(n) }, n.prototype.select = function (i) { var o = this; if (i.selected = !0, l(i.element).is("option")) return i.element.selected = !0, void this.$element.trigger("input").trigger("change"); if (this.$element.prop("multiple")) this.current(function (e) { var t = []; (i = [i]).push.apply(i, e); for (var n = 0; n < i.length; n++) { var r = i[n].id; -1 === l.inArray(r, t) && t.push(r) } o.$element.val(t), o.$element.trigger("input").trigger("change") }); else { var e = i.id; this.$element.val(e), this.$element.trigger("input").trigger("change") } }, n.prototype.unselect = function (i) { var o = this; if (this.$element.prop("multiple")) { if (i.selected = !1, l(i.element).is("option")) return i.element.selected = !1, void this.$element.trigger("input").trigger("change"); this.current(function (e) { for (var t = [], n = 0; n < e.length; n++) { var r = e[n].id; r !== i.id && -1 === l.inArray(r, t) && t.push(r) } o.$element.val(t), o.$element.trigger("input").trigger("change") }) } }, n.prototype.bind = function (e, t) { var n = this; (this.container = e).on("select", function (e) { n.select(e.data) }), e.on("unselect", function (e) { n.unselect(e.data) }) }, n.prototype.destroy = function () { this.$element.find("*").each(function () { a.RemoveData(this) }) }, n.prototype.query = function (r, e) { var i = [], o = this; this.$element.children().each(function () { var e = l(this); if (e.is("option") || e.is("optgroup")) { var t = o.item(e), n = o.matches(r, t); null !== n && i.push(n) } }), e({ results: i }) }, n.prototype.addOptions = function (e) { a.appendMany(this.$element, e) }, n.prototype.option = function (e) { var t; e.children ? (t = document.createElement("optgroup")).label = e.text : void 0 !== (t = document.createElement("option")).textContent ? t.textContent = e.text : t.innerText = e.text, void 0 !== e.id && (t.value = e.id), e.disabled && (t.disabled = !0), e.selected && (t.selected = !0), e.title && (t.title = e.title); var n = l(t), r = this._normalizeItem(e); return r.element = t, a.StoreData(t, "data", r), n }, n.prototype.item = function (e) { var t = {}; if (null != (t = a.GetData(e[0], "data"))) return t; if (e.is("option")) t = { id: e.val(), text: e.text(), disabled: e.prop("disabled"), selected: e.prop("selected"), title: e.prop("title") }; else if (e.is("optgroup")) { t = { text: e.prop("label"), children: [], title: e.prop("title") }; for (var n = e.children("option"), r = [], i = 0; i < n.length; i++) { var o = l(n[i]), s = this.item(o); r.push(s) } t.children = r } return (t = this._normalizeItem(t)).element = e[0], a.StoreData(e[0], "data", t), t }, n.prototype._normalizeItem = function (e) { e !== Object(e) && (e = { id: e, text: e }); return null != (e = l.extend({}, { text: "" }, e)).id && (e.id = e.id.toString()), null != e.text && (e.text = e.text.toString()), null == e._resultId && e.id && null != this.container && (e._resultId = this.generateResultId(this.container, e)), l.extend({}, { selected: !1, disabled: !1 }, e) }, n.prototype.matches = function (e, t) { return this.options.get("matcher")(e, t) }, n }), e.define("select2/data/array", ["./select", "../utils", "jquery"], function (e, f, g) { function r(e, t) { this._dataToConvert = t.get("data") || [], r.__super__.constructor.call(this, e, t) } return f.Extend(r, e), r.prototype.bind = function (e, t) { r.__super__.bind.call(this, e, t), this.addOptions(this.convertToOptions(this._dataToConvert)) }, r.prototype.select = function (n) { var e = this.$element.find("option").filter(function (e, t) { return t.value == n.id.toString() }); 0 === e.length && (e = this.option(n), this.addOptions(e)), r.__super__.select.call(this, n) }, r.prototype.convertToOptions = function (e) { var t = this, n = this.$element.find("option"), r = n.map(function () { return t.item(g(this)).id }).get(), i = []; function o(e) { return function () { return g(this).val() == e.id } } for (var s = 0; s < e.length; s++) { var a = this._normalizeItem(e[s]); if (0 <= g.inArray(a.id, r)) { var l = n.filter(o(a)), c = this.item(l), u = g.extend(!0, {}, a, c), d = this.option(u); l.replaceWith(d) } else { var p = this.option(a); if (a.children) { var h = this.convertToOptions(a.children); f.appendMany(p, h) } i.push(p) } } return i }, r }), e.define("select2/data/ajax", ["./array", "../utils", "jquery"], function (e, t, o) { function n(e, t) { this.ajaxOptions = this._applyDefaults(t.get("ajax")), null != this.ajaxOptions.processResults && (this.processResults = this.ajaxOptions.processResults), n.__super__.constructor.call(this, e, t) } return t.Extend(n, e), n.prototype._applyDefaults = function (e) { var t = { data: function (e) { return o.extend({}, e, { q: e.term }) }, transport: function (e, t, n) { var r = o.ajax(e); return r.then(t), r.fail(n), r } }; return o.extend({}, t, e, !0) }, n.prototype.processResults = function (e) { return e }, n.prototype.query = function (n, r) { var i = this; null != this._request && (o.isFunction(this._request.abort) && this._request.abort(), this._request = null); var t = o.extend({ type: "GET" }, this.ajaxOptions); function e() { var e = t.transport(t, function (e) { var t = i.processResults(e, n); i.options.get("debug") && window.console && console.error && (t && t.results && o.isArray(t.results) || console.error("Select2: The AJAX results did not return an array in the `results` key of the response.")), r(t) }, function () { "status" in e && (0 === e.status || "0" === e.status) || i.trigger("results:message", { message: "errorLoading" }) }); i._request = e } "function" == typeof t.url && (t.url = t.url.call(this.$element, n)), "function" == typeof t.data && (t.data = t.data.call(this.$element, n)), this.ajaxOptions.delay && null != n.term ? (this._queryTimeout && window.clearTimeout(this._queryTimeout), this._queryTimeout = window.setTimeout(e, this.ajaxOptions.delay)) : e() }, n }), e.define("select2/data/tags", ["jquery"], function (u) { function e(e, t, n) { var r = n.get("tags"), i = n.get("createTag"); void 0 !== i && (this.createTag = i); var o = n.get("insertTag"); if (void 0 !== o && (this.insertTag = o), e.call(this, t, n), u.isArray(r)) for (var s = 0; s < r.length; s++) { var a = r[s], l = this._normalizeItem(a), c = this.option(l); this.$element.append(c) } } return e.prototype.query = function (e, c, u) { var d = this; this._removeOldTags(), null != c.term && null == c.page ? e.call(this, c, function e(t, n) { for (var r = t.results, i = 0; i < r.length; i++) { var o = r[i], s = null != o.children && !e({ results: o.children }, !0); if ((o.text || "").toUpperCase() === (c.term || "").toUpperCase() || s) return !n && (t.data = r, void u(t)) } if (n) return !0; var a = d.createTag(c); if (null != a) { var l = d.option(a); l.attr("data-select2-tag", !0), d.addOptions([l]), d.insertTag(r, a) } t.results = r, u(t) }) : e.call(this, c, u) }, e.prototype.createTag = function (e, t) { var n = u.trim(t.term); return "" === n ? null : { id: n, text: n } }, e.prototype.insertTag = function (e, t, n) { t.unshift(n) }, e.prototype._removeOldTags = function (e) { this.$element.find("option[data-select2-tag]").each(function () { this.selected || u(this).remove() }) }, e }), e.define("select2/data/tokenizer", ["jquery"], function (d) { function e(e, t, n) { var r = n.get("tokenizer"); void 0 !== r && (this.tokenizer = r), e.call(this, t, n) } return e.prototype.bind = function (e, t, n) { e.call(this, t, n), this.$search = t.dropdown.$search || t.selection.$search || n.find(".select2-search__field") }, e.prototype.query = function (e, t, n) { var i = this; t.term = t.term || ""; var r = this.tokenizer(t, this.options, function (e) { var t, n = i._normalizeItem(e); if (!i.$element.find("option").filter(function () { return d(this).val() === n.id }).length) { var r = i.option(n); r.attr("data-select2-tag", !0), i._removeOldTags(), i.addOptions([r]) } t = n, i.trigger("select", { data: t }) }); r.term !== t.term && (this.$search.length && (this.$search.val(r.term), this.$search.trigger("focus")), t.term = r.term), e.call(this, t, n) }, e.prototype.tokenizer = function (e, t, n, r) { for (var i = n.get("tokenSeparators") || [], o = t.term, s = 0, a = this.createTag || function (e) { return { id: e.term, text: e.term } }; s < o.length;) { var l = o[s]; if (-1 !== d.inArray(l, i)) { var c = o.substr(0, s), u = a(d.extend({}, t, { term: c })); null != u ? (r(u), o = o.substr(s + 1) || "", s = 0) : s++ } else s++ } return { term: o } }, e }), e.define("select2/data/minimumInputLength", [], function () { function e(e, t, n) { this.minimumInputLength = n.get("minimumInputLength"), e.call(this, t, n) } return e.prototype.query = function (e, t, n) { t.term = t.term || "", t.term.length < this.minimumInputLength ? this.trigger("results:message", { message: "inputTooShort", args: { minimum: this.minimumInputLength, input: t.term, params: t } }) : e.call(this, t, n) }, e }), e.define("select2/data/maximumInputLength", [], function () { function e(e, t, n) { this.maximumInputLength = n.get("maximumInputLength"), e.call(this, t, n) } return e.prototype.query = function (e, t, n) { t.term = t.term || "", 0 < this.maximumInputLength && t.term.length > this.maximumInputLength ? this.trigger("results:message", { message: "inputTooLong", args: { maximum: this.maximumInputLength, input: t.term, params: t } }) : e.call(this, t, n) }, e }), e.define("select2/data/maximumSelectionLength", [], function () { function e(e, t, n) { this.maximumSelectionLength = n.get("maximumSelectionLength"), e.call(this, t, n) } return e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), t.on("select", function () { r._checkIfMaximumSelected() }) }, e.prototype.query = function (e, t, n) { var r = this; this._checkIfMaximumSelected(function () { e.call(r, t, n) }) }, e.prototype._checkIfMaximumSelected = function (e, n) { var r = this; this.current(function (e) { var t = null != e ? e.length : 0; 0 < r.maximumSelectionLength && t >= r.maximumSelectionLength ? r.trigger("results:message", { message: "maximumSelected", args: { maximum: r.maximumSelectionLength } }) : n && n() }) }, e }), e.define("select2/dropdown", ["jquery", "./utils"], function (t, e) { function n(e, t) { this.$element = e, this.options = t, n.__super__.constructor.call(this) } return e.Extend(n, e.Observable), n.prototype.render = function () { var e = t('<span class="select2-dropdown"><span class="select2-results"></span></span>'); return e.attr("dir", this.options.get("dir")), this.$dropdown = e }, n.prototype.bind = function () { }, n.prototype.position = function (e, t) { }, n.prototype.destroy = function () { this.$dropdown.remove() }, n }), e.define("select2/dropdown/search", ["jquery", "../utils"], function (o, e) { function t() { } return t.prototype.render = function (e) { var t = e.call(this), n = o('<span class="select2-search select2-search--dropdown"><input class="select2-search__field" type="search" tabindex="-1" autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false" role="searchbox" aria-autocomplete="list" /></span>'); return this.$searchContainer = n, this.$search = n.find("input"), t.prepend(n), t }, t.prototype.bind = function (e, t, n) { var r = this, i = t.id + "-results"; e.call(this, t, n), this.$search.on("keydown", function (e) { r.trigger("keypress", e), r._keyUpPrevented = e.isDefaultPrevented() }), this.$search.on("input", function (e) { o(this).off("keyup") }), this.$search.on("keyup input", function (e) { r.handleSearch(e) }), t.on("open", function () { r.$search.attr("tabindex", 0), r.$search.attr("aria-controls", i), r.$search.trigger("focus"), window.setTimeout(function () { r.$search.trigger("focus") }, 0) }), t.on("close", function () { r.$search.attr("tabindex", -1), r.$search.removeAttr("aria-controls"), r.$search.removeAttr("aria-activedescendant"), r.$search.val(""), r.$search.trigger("blur") }), t.on("focus", function () { t.isOpen() || r.$search.trigger("focus") }), t.on("results:all", function (e) { null != e.query.term && "" !== e.query.term || (r.showSearch(e) ? r.$searchContainer.removeClass("select2-search--hide") : r.$searchContainer.addClass("select2-search--hide")) }), t.on("results:focus", function (e) { e.data._resultId ? r.$search.attr("aria-activedescendant", e.data._resultId) : r.$search.removeAttr("aria-activedescendant") }) }, t.prototype.handleSearch = function (e) { if (!this._keyUpPrevented) { var t = this.$search.val(); this.trigger("query", { term: t }) } this._keyUpPrevented = !1 }, t.prototype.showSearch = function (e, t) { return !0 }, t }), e.define("select2/dropdown/hidePlaceholder", [], function () { function e(e, t, n, r) { this.placeholder = this.normalizePlaceholder(n.get("placeholder")), e.call(this, t, n, r) } return e.prototype.append = function (e, t) { t.results = this.removePlaceholder(t.results), e.call(this, t) }, e.prototype.normalizePlaceholder = function (e, t) { return "string" == typeof t && (t = { id: "", text: t }), t }, e.prototype.removePlaceholder = function (e, t) { for (var n = t.slice(0), r = t.length - 1; 0 <= r; r--) { var i = t[r]; this.placeholder.id === i.id && n.splice(r, 1) } return n }, e }), e.define("select2/dropdown/infiniteScroll", ["jquery"], function (n) { function e(e, t, n, r) { this.lastParams = {}, e.call(this, t, n, r), this.$loadingMore = this.createLoadingMore(), this.loading = !1 } return e.prototype.append = function (e, t) { this.$loadingMore.remove(), this.loading = !1, e.call(this, t), this.showLoadingMore(t) && (this.$results.append(this.$loadingMore), this.loadMoreIfNeeded()) }, e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), t.on("query", function (e) { r.lastParams = e, r.loading = !0 }), t.on("query:append", function (e) { r.lastParams = e, r.loading = !0 }), this.$results.on("scroll", this.loadMoreIfNeeded.bind(this)) }, e.prototype.loadMoreIfNeeded = function () { var e = n.contains(document.documentElement, this.$loadingMore[0]); if (!this.loading && e) { var t = this.$results.offset().top + this.$results.outerHeight(!1); this.$loadingMore.offset().top + this.$loadingMore.outerHeight(!1) <= t + 50 && this.loadMore() } }, e.prototype.loadMore = function () { this.loading = !0; var e = n.extend({}, { page: 1 }, this.lastParams); e.page++, this.trigger("query:append", e) }, e.prototype.showLoadingMore = function (e, t) { return t.pagination && t.pagination.more }, e.prototype.createLoadingMore = function () { var e = n('<li class="select2-results__option select2-results__option--load-more"role="option" aria-disabled="true"></li>'), t = this.options.get("translations").get("loadingMore"); return e.html(t(this.lastParams)), e }, e }), e.define("select2/dropdown/attachBody", ["jquery", "../utils"], function (f, a) { function e(e, t, n) { this.$dropdownParent = f(n.get("dropdownParent") || document.body), e.call(this, t, n) } return e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), t.on("open", function () { r._showDropdown(), r._attachPositioningHandler(t), r._bindContainerResultHandlers(t) }), t.on("close", function () { r._hideDropdown(), r._detachPositioningHandler(t) }), this.$dropdownContainer.on("mousedown", function (e) { e.stopPropagation() }) }, e.prototype.destroy = function (e) { e.call(this), this.$dropdownContainer.remove() }, e.prototype.position = function (e, t, n) { t.attr("class", n.attr("class")), t.removeClass("select2"), t.addClass("select2-container--open"), t.css({ position: "absolute", top: -999999 }), this.$container = n }, e.prototype.render = function (e) { var t = f("<span></span>"), n = e.call(this); return t.append(n), this.$dropdownContainer = t }, e.prototype._hideDropdown = function (e) { this.$dropdownContainer.detach() }, e.prototype._bindContainerResultHandlers = function (e, t) { if (!this._containerResultsHandlersBound) { var n = this; t.on("results:all", function () { n._positionDropdown(), n._resizeDropdown() }), t.on("results:append", function () { n._positionDropdown(), n._resizeDropdown() }), t.on("results:message", function () { n._positionDropdown(), n._resizeDropdown() }), t.on("select", function () { n._positionDropdown(), n._resizeDropdown() }), t.on("unselect", function () { n._positionDropdown(), n._resizeDropdown() }), this._containerResultsHandlersBound = !0 } }, e.prototype._attachPositioningHandler = function (e, t) { var n = this, r = "scroll.select2." + t.id, i = "resize.select2." + t.id, o = "orientationchange.select2." + t.id, s = this.$container.parents().filter(a.hasScroll); s.each(function () { a.StoreData(this, "select2-scroll-position", { x: f(this).scrollLeft(), y: f(this).scrollTop() }) }), s.on(r, function (e) { var t = a.GetData(this, "select2-scroll-position"); f(this).scrollTop(t.y) }), f(window).on(r + " " + i + " " + o, function (e) { n._positionDropdown(), n._resizeDropdown() }) }, e.prototype._detachPositioningHandler = function (e, t) { var n = "scroll.select2." + t.id, r = "resize.select2." + t.id, i = "orientationchange.select2." + t.id; this.$container.parents().filter(a.hasScroll).off(n), f(window).off(n + " " + r + " " + i) }, e.prototype._positionDropdown = function () { var e = f(window), t = this.$dropdown.hasClass("select2-dropdown--above"), n = this.$dropdown.hasClass("select2-dropdown--below"), r = null, i = this.$container.offset(); i.bottom = i.top + this.$container.outerHeight(!1); var o = { height: this.$container.outerHeight(!1) }; o.top = i.top, o.bottom = i.top + o.height; var s = this.$dropdown.outerHeight(!1), a = e.scrollTop(), l = e.scrollTop() + e.height(), c = a < i.top - s, u = l > i.bottom + s, d = { left: i.left, top: o.bottom }, p = this.$dropdownParent; "static" === p.css("position") && (p = p.offsetParent()); var h = { top: 0, left: 0 }; (f.contains(document.body, p[0]) || p[0].isConnected) && (h = p.offset()), d.top -= h.top, d.left -= h.left, t || n || (r = "below"), u || !c || t ? !c && u && t && (r = "below") : r = "above", ("above" == r || t && "below" !== r) && (d.top = o.top - h.top - s), null != r && (this.$dropdown.removeClass("select2-dropdown--below select2-dropdown--above").addClass("select2-dropdown--" + r), this.$container.removeClass("select2-container--below select2-container--above").addClass("select2-container--" + r)), this.$dropdownContainer.css(d) }, e.prototype._resizeDropdown = function () { var e = { width: this.$container.outerWidth(!1) + "px" }; this.options.get("dropdownAutoWidth") && (e.minWidth = e.width, e.position = "relative", e.width = "auto"), this.$dropdown.css(e) }, e.prototype._showDropdown = function (e) { this.$dropdownContainer.appendTo(this.$dropdownParent), this._positionDropdown(), this._resizeDropdown() }, e }), e.define("select2/dropdown/minimumResultsForSearch", [], function () { function e(e, t, n, r) { this.minimumResultsForSearch = n.get("minimumResultsForSearch"), this.minimumResultsForSearch < 0 && (this.minimumResultsForSearch = 1 / 0), e.call(this, t, n, r) } return e.prototype.showSearch = function (e, t) { return !(function e(t) { for (var n = 0, r = 0; r < t.length; r++) { var i = t[r]; i.children ? n += e(i.children) : n++ } return n }(t.data.results) < this.minimumResultsForSearch) && e.call(this, t) }, e }), e.define("select2/dropdown/selectOnClose", ["../utils"], function (o) { function e() { } return e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), t.on("close", function (e) { r._handleSelectOnClose(e) }) }, e.prototype._handleSelectOnClose = function (e, t) { if (t && null != t.originalSelect2Event) { var n = t.originalSelect2Event; if ("select" === n._type || "unselect" === n._type) return } var r = this.getHighlightedResults(); if (!(r.length < 1)) { var i = o.GetData(r[0], "data"); null != i.element && i.element.selected || null == i.element && i.selected || this.trigger("select", { data: i }) } }, e }), e.define("select2/dropdown/closeOnSelect", [], function () { function e() { } return e.prototype.bind = function (e, t, n) { var r = this; e.call(this, t, n), t.on("select", function (e) { r._selectTriggered(e) }), t.on("unselect", function (e) { r._selectTriggered(e) }) }, e.prototype._selectTriggered = function (e, t) { var n = t.originalEvent; n && (n.ctrlKey || n.metaKey) || this.trigger("close", { originalEvent: n, originalSelect2Event: t }) }, e }), e.define("select2/i18n/en", [], function () { return { errorLoading: function () { return "The results could not be loaded." }, inputTooLong: function (e) { var t = e.input.length - e.maximum, n = "Please delete " + t + " character"; return 1 != t && (n += "s"), n }, inputTooShort: function (e) { return "Please enter " + (e.minimum - e.input.length) + " or more characters" }, loadingMore: function () { return "Loading more results…" }, maximumSelected: function (e) { var t = "You can only select " + e.maximum + " item"; return 1 != e.maximum && (t += "s"), t }, noResults: function () { return "No results found" }, searching: function () { return "Searching…" }, removeAllItems: function () { return "Remove all items" } } }), e.define("select2/defaults", ["jquery", "require", "./results", "./selection/single", "./selection/multiple", "./selection/placeholder", "./selection/allowClear", "./selection/search", "./selection/eventRelay", "./utils", "./translation", "./diacritics", "./data/select", "./data/array", "./data/ajax", "./data/tags", "./data/tokenizer", "./data/minimumInputLength", "./data/maximumInputLength", "./data/maximumSelectionLength", "./dropdown", "./dropdown/search", "./dropdown/hidePlaceholder", "./dropdown/infiniteScroll", "./dropdown/attachBody", "./dropdown/minimumResultsForSearch", "./dropdown/selectOnClose", "./dropdown/closeOnSelect", "./i18n/en"], function (c, u, d, p, h, f, g, m, v, y, s, t, _, $, b, w, A, x, D, S, E, C, O, T, q, L, I, j, e) { function n() { this.reset() } return n.prototype.apply = function (e) { if (null == (e = c.extend(!0, {}, this.defaults, e)).dataAdapter) { if (null != e.ajax ? e.dataAdapter = b : null != e.data ? e.dataAdapter = $ : e.dataAdapter = _, 0 < e.minimumInputLength && (e.dataAdapter = y.Decorate(e.dataAdapter, x)), 0 < e.maximumInputLength && (e.dataAdapter = y.Decorate(e.dataAdapter, D)), 0 < e.maximumSelectionLength && (e.dataAdapter = y.Decorate(e.dataAdapter, S)), e.tags && (e.dataAdapter = y.Decorate(e.dataAdapter, w)), null == e.tokenSeparators && null == e.tokenizer || (e.dataAdapter = y.Decorate(e.dataAdapter, A)), null != e.query) { var t = u(e.amdBase + "compat/query"); e.dataAdapter = y.Decorate(e.dataAdapter, t) } if (null != e.initSelection) { var n = u(e.amdBase + "compat/initSelection"); e.dataAdapter = y.Decorate(e.dataAdapter, n) } } if (null == e.resultsAdapter && (e.resultsAdapter = d, null != e.ajax && (e.resultsAdapter = y.Decorate(e.resultsAdapter, T)), null != e.placeholder && (e.resultsAdapter = y.Decorate(e.resultsAdapter, O)), e.selectOnClose && (e.resultsAdapter = y.Decorate(e.resultsAdapter, I))), null == e.dropdownAdapter) { if (e.multiple) e.dropdownAdapter = E; else { var r = y.Decorate(E, C); e.dropdownAdapter = r } if (0 !== e.minimumResultsForSearch && (e.dropdownAdapter = y.Decorate(e.dropdownAdapter, L)), e.closeOnSelect && (e.dropdownAdapter = y.Decorate(e.dropdownAdapter, j)), null != e.dropdownCssClass || null != e.dropdownCss || null != e.adaptDropdownCssClass) { var i = u(e.amdBase + "compat/dropdownCss"); e.dropdownAdapter = y.Decorate(e.dropdownAdapter, i) } e.dropdownAdapter = y.Decorate(e.dropdownAdapter, q) } if (null == e.selectionAdapter) { if (e.multiple ? e.selectionAdapter = h : e.selectionAdapter = p, null != e.placeholder && (e.selectionAdapter = y.Decorate(e.selectionAdapter, f)), e.allowClear && (e.selectionAdapter = y.Decorate(e.selectionAdapter, g)), e.multiple && (e.selectionAdapter = y.Decorate(e.selectionAdapter, m)), null != e.containerCssClass || null != e.containerCss || null != e.adaptContainerCssClass) { var o = u(e.amdBase + "compat/containerCss"); e.selectionAdapter = y.Decorate(e.selectionAdapter, o) } e.selectionAdapter = y.Decorate(e.selectionAdapter, v) } e.language = this._resolveLanguage(e.language), e.language.push("en"); for (var s = [], a = 0; a < e.language.length; a++) { var l = e.language[a]; -1 === s.indexOf(l) && s.push(l) } return e.language = s, e.translations = this._processTranslations(e.language, e.debug), e }, n.prototype.reset = function () { function a(e) { return e.replace(/[^\u0000-\u007E]/g, function (e) { return t[e] || e }) } this.defaults = { amdBase: "./", amdLanguageBase: "./i18n/", closeOnSelect: !0, debug: !1, dropdownAutoWidth: !1, escapeMarkup: y.escapeMarkup, language: {}, matcher: function e(t, n) { if ("" === c.trim(t.term)) return n; if (n.children && 0 < n.children.length) { for (var r = c.extend(!0, {}, n), i = n.children.length - 1; 0 <= i; i--)null == e(t, n.children[i]) && r.children.splice(i, 1); return 0 < r.children.length ? r : e(t, r) } var o = a(n.text).toUpperCase(), s = a(t.term).toUpperCase(); return -1 < o.indexOf(s) ? n : null }, minimumInputLength: 0, maximumInputLength: 0, maximumSelectionLength: 0, minimumResultsForSearch: 0, selectOnClose: !1, scrollAfterSelect: !1, sorter: function (e) { return e }, templateResult: function (e) { return e.text }, templateSelection: function (e) { return e.text }, theme: "default", width: "resolve" } }, n.prototype.applyFromElement = function (e, t) { var n = e.language, r = this.defaults.language, i = t.prop("lang"), o = t.closest("[lang]").prop("lang"), s = Array.prototype.concat.call(this._resolveLanguage(i), this._resolveLanguage(n), this._resolveLanguage(r), this._resolveLanguage(o)); return e.language = s, e }, n.prototype._resolveLanguage = function (e) { if (!e) return []; if (c.isEmptyObject(e)) return []; if (c.isPlainObject(e)) return [e]; var t; t = c.isArray(e) ? e : [e]; for (var n = [], r = 0; r < t.length; r++)if (n.push(t[r]), "string" == typeof t[r] && 0 < t[r].indexOf("-")) { var i = t[r].split("-")[0]; n.push(i) } return n }, n.prototype._processTranslations = function (e, t) { for (var n = new s, r = 0; r < e.length; r++) { var i = new s, o = e[r]; if ("string" == typeof o) try { i = s.loadPath(o) } catch (e) { try { o = this.defaults.amdLanguageBase + o, i = s.loadPath(o) } catch (e) { t && window.console && console.warn && console.warn('Select2: The language file for "' + o + '" could not be automatically loaded. A fallback will be used instead.') } } else i = c.isPlainObject(o) ? new s(o) : o; n.extend(i) } return n }, n.prototype.set = function (e, t) { var n = {}; n[c.camelCase(e)] = t; var r = y._convertData(n); c.extend(!0, this.defaults, r) }, new n }), e.define("select2/options", ["require", "jquery", "./defaults", "./utils"], function (r, d, i, p) { function e(e, t) { if (this.options = e, null != t && this.fromElement(t), null != t && (this.options = i.applyFromElement(this.options, t)), this.options = i.apply(this.options), t && t.is("input")) { var n = r(this.get("amdBase") + "compat/inputData"); this.options.dataAdapter = p.Decorate(this.options.dataAdapter, n) } } return e.prototype.fromElement = function (e) { var t = ["select2"]; null == this.options.multiple && (this.options.multiple = e.prop("multiple")), null == this.options.disabled && (this.options.disabled = e.prop("disabled")), null == this.options.dir && (e.prop("dir") ? this.options.dir = e.prop("dir") : e.closest("[dir]").prop("dir") ? this.options.dir = e.closest("[dir]").prop("dir") : this.options.dir = "ltr"), e.prop("disabled", this.options.disabled), e.prop("multiple", this.options.multiple), p.GetData(e[0], "select2Tags") && (this.options.debug && window.console && console.warn && console.warn('Select2: The `data-select2-tags` attribute has been changed to use the `data-data` and `data-tags="true"` attributes and will be removed in future versions of Select2.'), p.StoreData(e[0], "data", p.GetData(e[0], "select2Tags")), p.StoreData(e[0], "tags", !0)), p.GetData(e[0], "ajaxUrl") && (this.options.debug && window.console && console.warn && console.warn("Select2: The `data-ajax-url` attribute has been changed to `data-ajax--url` and support for the old attribute will be removed in future versions of Select2."), e.attr("ajax--url", p.GetData(e[0], "ajaxUrl")), p.StoreData(e[0], "ajax-Url", p.GetData(e[0], "ajaxUrl"))); var n = {}; function r(e, t) { return t.toUpperCase() } for (var i = 0; i < e[0].attributes.length; i++) { var o = e[0].attributes[i].name, s = "data-"; if (o.substr(0, s.length) == s) { var a = o.substring(s.length), l = p.GetData(e[0], a); n[a.replace(/-([a-z])/g, r)] = l } } d.fn.jquery && "1." == d.fn.jquery.substr(0, 2) && e[0].dataset && (n = d.extend(!0, {}, e[0].dataset, n)); var c = d.extend(!0, {}, p.GetData(e[0]), n); for (var u in c = p._convertData(c)) -1 < d.inArray(u, t) || (d.isPlainObject(this.options[u]) ? d.extend(this.options[u], c[u]) : this.options[u] = c[u]); return this }, e.prototype.get = function (e) { return this.options[e] }, e.prototype.set = function (e, t) { this.options[e] = t }, e }), e.define("select2/core", ["jquery", "./options", "./utils", "./keys"], function (o, c, u, r) { var d = function (e, t) { null != u.GetData(e[0], "select2") && u.GetData(e[0], "select2").destroy(), this.$element = e, this.id = this._generateId(e), t = t || {}, this.options = new c(t, e), d.__super__.constructor.call(this); var n = e.attr("tabindex") || 0; u.StoreData(e[0], "old-tabindex", n), e.attr("tabindex", "-1"); var r = this.options.get("dataAdapter"); this.dataAdapter = new r(e, this.options); var i = this.render(); this._placeContainer(i); var o = this.options.get("selectionAdapter"); this.selection = new o(e, this.options), this.$selection = this.selection.render(), this.selection.position(this.$selection, i); var s = this.options.get("dropdownAdapter"); this.dropdown = new s(e, this.options), this.$dropdown = this.dropdown.render(), this.dropdown.position(this.$dropdown, i); var a = this.options.get("resultsAdapter"); this.results = new a(e, this.options, this.dataAdapter), this.$results = this.results.render(), this.results.position(this.$results, this.$dropdown); var l = this; this._bindAdapters(), this._registerDomEvents(), this._registerDataEvents(), this._registerSelectionEvents(), this._registerDropdownEvents(), this._registerResultsEvents(), this._registerEvents(), this.dataAdapter.current(function (e) { l.trigger("selection:update", { data: e }) }), e.addClass("select2-hidden-accessible"), e.attr("aria-hidden", "true"), this._syncAttributes(), u.StoreData(e[0], "select2", this), e.data("select2", this) }; return u.Extend(d, u.Observable), d.prototype._generateId = function (e) { return "select2-" + (null != e.attr("id") ? e.attr("id") : null != e.attr("name") ? e.attr("name") + "-" + u.generateChars(2) : u.generateChars(4)).replace(/(:|\.|\[|\]|,)/g, "") }, d.prototype._placeContainer = function (e) { e.insertAfter(this.$element); var t = this._resolveWidth(this.$element, this.options.get("width")); null != t && e.css("width", t) }, d.prototype._resolveWidth = function (e, t) { var n = /^width:(([-+]?([0-9]*\.)?[0-9]+)(px|em|ex|%|in|cm|mm|pt|pc))/i; if ("resolve" == t) { var r = this._resolveWidth(e, "style"); return null != r ? r : this._resolveWidth(e, "element") } if ("element" == t) { var i = e.outerWidth(!1); return i <= 0 ? "auto" : i + "px" } if ("style" != t) return "computedstyle" != t ? t : window.getComputedStyle(e[0]).width; var o = e.attr("style"); if ("string" != typeof o) return null; for (var s = o.split(";"), a = 0, l = s.length; a < l; a += 1) { var c = s[a].replace(/\s/g, "").match(n); if (null !== c && 1 <= c.length) return c[1] } return null }, d.prototype._bindAdapters = function () { this.dataAdapter.bind(this, this.$container), this.selection.bind(this, this.$container), this.dropdown.bind(this, this.$container), this.results.bind(this, this.$container) }, d.prototype._registerDomEvents = function () { var t = this; this.$element.on("change.select2", function () { t.dataAdapter.current(function (e) { t.trigger("selection:update", { data: e }) }) }), this.$element.on("focus.select2", function (e) { t.trigger("focus", e) }), this._syncA = u.bind(this._syncAttributes, this), this._syncS = u.bind(this._syncSubtree, this), this.$element[0].attachEvent && this.$element[0].attachEvent("onpropertychange", this._syncA); var e = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver; null != e ? (this._observer = new e(function (e) { t._syncA(), t._syncS(null, e) }), this._observer.observe(this.$element[0], { attributes: !0, childList: !0, subtree: !1 })) : this.$element[0].addEventListener && (this.$element[0].addEventListener("DOMAttrModified", t._syncA, !1), this.$element[0].addEventListener("DOMNodeInserted", t._syncS, !1), this.$element[0].addEventListener("DOMNodeRemoved", t._syncS, !1)) }, d.prototype._registerDataEvents = function () { var n = this; this.dataAdapter.on("*", function (e, t) { n.trigger(e, t) }) }, d.prototype._registerSelectionEvents = function () { var n = this, r = ["toggle", "focus"]; this.selection.on("toggle", function () { n.toggleDropdown() }), this.selection.on("focus", function (e) { n.focus(e) }), this.selection.on("*", function (e, t) { -1 === o.inArray(e, r) && n.trigger(e, t) }) }, d.prototype._registerDropdownEvents = function () { var n = this; this.dropdown.on("*", function (e, t) { n.trigger(e, t) }) }, d.prototype._registerResultsEvents = function () { var n = this; this.results.on("*", function (e, t) { n.trigger(e, t) }) }, d.prototype._registerEvents = function () { var n = this; this.on("open", function () { n.$container.addClass("select2-container--open") }), this.on("close", function () { n.$container.removeClass("select2-container--open") }), this.on("enable", function () { n.$container.removeClass("select2-container--disabled") }), this.on("disable", function () { n.$container.addClass("select2-container--disabled") }), this.on("blur", function () { n.$container.removeClass("select2-container--focus") }), this.on("query", function (t) { n.isOpen() || n.trigger("open", {}), this.dataAdapter.query(t, function (e) { n.trigger("results:all", { data: e, query: t }) }) }), this.on("query:append", function (t) { this.dataAdapter.query(t, function (e) { n.trigger("results:append", { data: e, query: t }) }) }), this.on("keypress", function (e) { var t = e.which; n.isOpen() ? t === r.ESC || t === r.TAB || t === r.UP && e.altKey ? (n.close(e), e.preventDefault()) : t === r.ENTER ? (n.trigger("results:select", {}), e.preventDefault()) : t === r.SPACE && e.ctrlKey ? (n.trigger("results:toggle", {}), e.preventDefault()) : t === r.UP ? (n.trigger("results:previous", {}), e.preventDefault()) : t === r.DOWN && (n.trigger("results:next", {}), e.preventDefault()) : (t === r.ENTER || t === r.SPACE || t === r.DOWN && e.altKey) && (n.open(), e.preventDefault()) }) }, d.prototype._syncAttributes = function () { this.options.set("disabled", this.$element.prop("disabled")), this.isDisabled() ? (this.isOpen() && this.close(), this.trigger("disable", {})) : this.trigger("enable", {}) }, d.prototype._isChangeMutation = function (e, t) { var n = !1, r = this; if (!e || !e.target || "OPTION" === e.target.nodeName || "OPTGROUP" === e.target.nodeName) { if (t) if (t.addedNodes && 0 < t.addedNodes.length) for (var i = 0; i < t.addedNodes.length; i++) { t.addedNodes[i].selected && (n = !0) } else t.removedNodes && 0 < t.removedNodes.length ? n = !0 : o.isArray(t) && o.each(t, function (e, t) { if (r._isChangeMutation(e, t)) return !(n = !0) }); else n = !0; return n } }, d.prototype._syncSubtree = function (e, t) { var n = this._isChangeMutation(e, t), r = this; n && this.dataAdapter.current(function (e) { r.trigger("selection:update", { data: e }) }) }, d.prototype.trigger = function (e, t) { var n = d.__super__.trigger, r = { open: "opening", close: "closing", select: "selecting", unselect: "unselecting", clear: "clearing" }; if (void 0 === t && (t = {}), e in r) { var i = r[e], o = { prevented: !1, name: e, args: t }; if (n.call(this, i, o), o.prevented) return void (t.prevented = !0) } n.call(this, e, t) }, d.prototype.toggleDropdown = function () { this.isDisabled() || (this.isOpen() ? this.close() : this.open()) }, d.prototype.open = function () { this.isOpen() || this.isDisabled() || this.trigger("query", {}) }, d.prototype.close = function (e) { this.isOpen() && this.trigger("close", { originalEvent: e }) }, d.prototype.isEnabled = function () { return !this.isDisabled() }, d.prototype.isDisabled = function () { return this.options.get("disabled") }, d.prototype.isOpen = function () { return this.$container.hasClass("select2-container--open") }, d.prototype.hasFocus = function () { return this.$container.hasClass("select2-container--focus") }, d.prototype.focus = function (e) { this.hasFocus() || (this.$container.addClass("select2-container--focus"), this.trigger("focus", {})) }, d.prototype.enable = function (e) { this.options.get("debug") && window.console && console.warn && console.warn('Select2: The `select2("enable")` method has been deprecated and will be removed in later Select2 versions. Use $element.prop("disabled") instead.'), null != e && 0 !== e.length || (e = [!0]); var t = !e[0]; this.$element.prop("disabled", t) }, d.prototype.data = function () { this.options.get("debug") && 0 < arguments.length && window.console && console.warn && console.warn('Select2: Data can no longer be set using `select2("data")`. You should consider setting the value instead using `$element.val()`.'); var t = []; return this.dataAdapter.current(function (e) { t = e }), t }, d.prototype.val = function (e) { if (this.options.get("debug") && window.console && console.warn && console.warn('Select2: The `select2("val")` method has been deprecated and will be removed in later Select2 versions. Use $element.val() instead.'), null == e || 0 === e.length) return this.$element.val(); var t = e[0]; o.isArray(t) && (t = o.map(t, function (e) { return e.toString() })), this.$element.val(t).trigger("input").trigger("change") }, d.prototype.destroy = function () { this.$container.remove(), this.$element[0].detachEvent && this.$element[0].detachEvent("onpropertychange", this._syncA), null != this._observer ? (this._observer.disconnect(), this._observer = null) : this.$element[0].removeEventListener && (this.$element[0].removeEventListener("DOMAttrModified", this._syncA, !1), this.$element[0].removeEventListener("DOMNodeInserted", this._syncS, !1), this.$element[0].removeEventListener("DOMNodeRemoved", this._syncS, !1)), this._syncA = null, this._syncS = null, this.$element.off(".select2"), this.$element.attr("tabindex", u.GetData(this.$element[0], "old-tabindex")), this.$element.removeClass("select2-hidden-accessible"), this.$element.attr("aria-hidden", "false"), u.RemoveData(this.$element[0]), this.$element.removeData("select2"), this.dataAdapter.destroy(), this.selection.destroy(), this.dropdown.destroy(), this.results.destroy(), this.dataAdapter = null, this.selection = null, this.dropdown = null, this.results = null }, d.prototype.render = function () { var e = o('<span class="select2 select2-container"><span class="selection"></span><span class="dropdown-wrapper" aria-hidden="true"></span></span>'); return e.attr("dir", this.options.get("dir")), this.$container = e, this.$container.addClass("select2-container--" + this.options.get("theme")), u.StoreData(e[0], "element", this.$element), e }, d }), e.define("jquery-mousewheel", ["jquery"], function (e) { return e }), e.define("jquery.select2", ["jquery", "jquery-mousewheel", "./select2/core", "./select2/defaults", "./select2/utils"], function (i, e, o, t, s) { if (null == i.fn.select2) { var a = ["open", "close", "destroy"]; i.fn.select2 = function (t) { if ("object" == typeof (t = t || {})) return this.each(function () { var e = i.extend(!0, {}, t); new o(i(this), e) }), this; if ("string" != typeof t) throw new Error("Invalid arguments for Select2: " + t); var n, r = Array.prototype.slice.call(arguments, 1); return this.each(function () { var e = s.GetData(this, "select2"); null == e && window.console && console.error && console.error("The select2('" + t + "') method was called on an element that is not using Select2."), n = e[t].apply(e, r) }), -1 < i.inArray(t, a) ? this : n } } return null == i.fn.select2.defaults && (i.fn.select2.defaults = t), o }), { define: e.define, require: e.require } }(), t = e.require("jquery.select2"); return u.fn.select2.amd = e, t });
!function (i) { "use strict"; "function" == typeof define && define.amd ? define(["jquery"], i) : "undefined" != typeof exports ? module.exports = i(require("jquery")) : i(jQuery) }(function (i) { "use strict"; var e = window.Slick || {}; (e = function () { var e = 0; return function (t, o) { var s, n = this; n.defaults = { accessibility: !0, adaptiveHeight: !1, appendArrows: i(t), appendDots: i(t), arrows: !0, asNavFor: null, prevArrow: '<button class="slick-prev" aria-label="Previous" type="button">Previous</button>', nextArrow: '<button class="slick-next" aria-label="Next" type="button">Next</button>', autoplay: !1, autoplaySpeed: 3e3, centerMode: !1, centerPadding: "50px", cssEase: "ease", customPaging: function (e, t) { return i('<button type="button" />').text(t + 1) }, dots: !1, dotsClass: "slick-dots", draggable: !0, easing: "linear", edgeFriction: .35, fade: !1, focusOnSelect: !1, focusOnChange: !1, infinite: !0, initialSlide: 0, lazyLoad: "ondemand", mobileFirst: !1, pauseOnHover: !0, pauseOnFocus: !0, pauseOnDotsHover: !1, respondTo: "window", responsive: null, rows: 1, rtl: !1, slide: "", slidesPerRow: 1, slidesToShow: 1, slidesToScroll: 1, speed: 500, swipe: !0, swipeToSlide: !1, touchMove: !0, touchThreshold: 5, useCSS: !0, useTransform: !0, variableWidth: !1, vertical: !1, verticalSwiping: !1, waitForAnimate: !0, zIndex: 1e3 }, n.initials = { animating: !1, dragging: !1, autoPlayTimer: null, currentDirection: 0, currentLeft: null, currentSlide: 0, direction: 1, $dots: null, listWidth: null, listHeight: null, loadIndex: 0, $nextArrow: null, $prevArrow: null, scrolling: !1, slideCount: null, slideWidth: null, $slideTrack: null, $slides: null, sliding: !1, slideOffset: 0, swipeLeft: null, swiping: !1, $list: null, touchObject: {}, transformsEnabled: !1, unslicked: !1 }, i.extend(n, n.initials), n.activeBreakpoint = null, n.animType = null, n.animProp = null, n.breakpoints = [], n.breakpointSettings = [], n.cssTransitions = !1, n.focussed = !1, n.interrupted = !1, n.hidden = "hidden", n.paused = !0, n.positionProp = null, n.respondTo = null, n.rowCount = 1, n.shouldClick = !0, n.$slider = i(t), n.$slidesCache = null, n.transformType = null, n.transitionType = null, n.visibilityChange = "visibilitychange", n.windowWidth = 0, n.windowTimer = null, s = i(t).data("slick") || {}, n.options = i.extend({}, n.defaults, o, s), n.currentSlide = n.options.initialSlide, n.originalSettings = n.options, void 0 !== document.mozHidden ? (n.hidden = "mozHidden", n.visibilityChange = "mozvisibilitychange") : void 0 !== document.webkitHidden && (n.hidden = "webkitHidden", n.visibilityChange = "webkitvisibilitychange"), n.autoPlay = i.proxy(n.autoPlay, n), n.autoPlayClear = i.proxy(n.autoPlayClear, n), n.autoPlayIterator = i.proxy(n.autoPlayIterator, n), n.changeSlide = i.proxy(n.changeSlide, n), n.clickHandler = i.proxy(n.clickHandler, n), n.selectHandler = i.proxy(n.selectHandler, n), n.setPosition = i.proxy(n.setPosition, n), n.swipeHandler = i.proxy(n.swipeHandler, n), n.dragHandler = i.proxy(n.dragHandler, n), n.keyHandler = i.proxy(n.keyHandler, n), n.instanceUid = e++, n.htmlExpr = /^(?:\s*(<[\w\W]+>)[^>]*)$/, n.registerBreakpoints(), n.init(!0) } }()).prototype.activateADA = function () { this.$slideTrack.find(".slick-active").attr({ "aria-hidden": "false" }).find("a, input, button, select").attr({ tabindex: "0" }) }, e.prototype.addSlide = e.prototype.slickAdd = function (e, t, o) { var s = this; if ("boolean" == typeof t) o = t, t = null; else if (t < 0 || t >= s.slideCount) return !1; s.unload(), "number" == typeof t ? 0 === t && 0 === s.$slides.length ? i(e).appendTo(s.$slideTrack) : o ? i(e).insertBefore(s.$slides.eq(t)) : i(e).insertAfter(s.$slides.eq(t)) : !0 === o ? i(e).prependTo(s.$slideTrack) : i(e).appendTo(s.$slideTrack), s.$slides = s.$slideTrack.children(this.options.slide), s.$slideTrack.children(this.options.slide).detach(), s.$slideTrack.append(s.$slides), s.$slides.each(function (e, t) { i(t).attr("data-slick-index", e) }), s.$slidesCache = s.$slides, s.reinit() }, e.prototype.animateHeight = function () { var i = this; if (1 === i.options.slidesToShow && !0 === i.options.adaptiveHeight && !1 === i.options.vertical) { var e = i.$slides.eq(i.currentSlide).outerHeight(!0); i.$list.animate({ height: e }, i.options.speed) } }, e.prototype.animateSlide = function (e, t) { var o = {}, s = this; s.animateHeight(), !0 === s.options.rtl && !1 === s.options.vertical && (e = -e), !1 === s.transformsEnabled ? !1 === s.options.vertical ? s.$slideTrack.animate({ left: e }, s.options.speed, s.options.easing, t) : s.$slideTrack.animate({ top: e }, s.options.speed, s.options.easing, t) : !1 === s.cssTransitions ? (!0 === s.options.rtl && (s.currentLeft = -s.currentLeft), i({ animStart: s.currentLeft }).animate({ animStart: e }, { duration: s.options.speed, easing: s.options.easing, step: function (i) { i = Math.ceil(i), !1 === s.options.vertical ? (o[s.animType] = "translate(" + i + "px, 0px)", s.$slideTrack.css(o)) : (o[s.animType] = "translate(0px," + i + "px)", s.$slideTrack.css(o)) }, complete: function () { t && t.call() } })) : (s.applyTransition(), e = Math.ceil(e), !1 === s.options.vertical ? o[s.animType] = "translate3d(" + e + "px, 0px, 0px)" : o[s.animType] = "translate3d(0px," + e + "px, 0px)", s.$slideTrack.css(o), t && setTimeout(function () { s.disableTransition(), t.call() }, s.options.speed)) }, e.prototype.getNavTarget = function () { var e = this, t = e.options.asNavFor; return t && null !== t && (t = i(t).not(e.$slider)), t }, e.prototype.asNavFor = function (e) { var t = this.getNavTarget(); null !== t && "object" == typeof t && t.each(function () { var t = i(this).slick("getSlick"); t.unslicked || t.slideHandler(e, !0) }) }, e.prototype.applyTransition = function (i) { var e = this, t = {}; !1 === e.options.fade ? t[e.transitionType] = e.transformType + " " + e.options.speed + "ms " + e.options.cssEase : t[e.transitionType] = "opacity " + e.options.speed + "ms " + e.options.cssEase, !1 === e.options.fade ? e.$slideTrack.css(t) : e.$slides.eq(i).css(t) }, e.prototype.autoPlay = function () { var i = this; i.autoPlayClear(), i.slideCount > i.options.slidesToShow && (i.autoPlayTimer = setInterval(i.autoPlayIterator, i.options.autoplaySpeed)) }, e.prototype.autoPlayClear = function () { var i = this; i.autoPlayTimer && clearInterval(i.autoPlayTimer) }, e.prototype.autoPlayIterator = function () { var i = this, e = i.currentSlide + i.options.slidesToScroll; i.paused || i.interrupted || i.focussed || (!1 === i.options.infinite && (1 === i.direction && i.currentSlide + 1 === i.slideCount - 1 ? i.direction = 0 : 0 === i.direction && (e = i.currentSlide - i.options.slidesToScroll, i.currentSlide - 1 == 0 && (i.direction = 1))), i.slideHandler(e)) }, e.prototype.buildArrows = function () { var e = this; !0 === e.options.arrows && (e.$prevArrow = i(e.options.prevArrow).addClass("slick-arrow"), e.$nextArrow = i(e.options.nextArrow).addClass("slick-arrow"), e.slideCount > e.options.slidesToShow ? (e.$prevArrow.removeClass("slick-hidden").removeAttr("aria-hidden tabindex"), e.$nextArrow.removeClass("slick-hidden").removeAttr("aria-hidden tabindex"), e.htmlExpr.test(e.options.prevArrow) && e.$prevArrow.prependTo(e.options.appendArrows), e.htmlExpr.test(e.options.nextArrow) && e.$nextArrow.appendTo(e.options.appendArrows), !0 !== e.options.infinite && e.$prevArrow.addClass("slick-disabled").attr("aria-disabled", "true")) : e.$prevArrow.add(e.$nextArrow).addClass("slick-hidden").attr({ "aria-disabled": "true", tabindex: "-1" })) }, e.prototype.buildDots = function () { var e, t, o = this; if (!0 === o.options.dots) { for (o.$slider.addClass("slick-dotted"), t = i("<ul />").addClass(o.options.dotsClass), e = 0; e <= o.getDotCount(); e += 1)t.append(i("<li />").append(o.options.customPaging.call(this, o, e))); o.$dots = t.appendTo(o.options.appendDots), o.$dots.find("li").first().addClass("slick-active") } }, e.prototype.buildOut = function () { var e = this; e.$slides = e.$slider.children(e.options.slide + ":not(.slick-cloned)").addClass("slick-slide"), e.slideCount = e.$slides.length, e.$slides.each(function (e, t) { i(t).attr("data-slick-index", e).data("originalStyling", i(t).attr("style") || "") }), e.$slider.addClass("slick-slider"), e.$slideTrack = 0 === e.slideCount ? i('<div class="slick-track"/>').appendTo(e.$slider) : e.$slides.wrapAll('<div class="slick-track"/>').parent(), e.$list = e.$slideTrack.wrap('<div class="slick-list"/>').parent(), e.$slideTrack.css("opacity", 0), !0 !== e.options.centerMode && !0 !== e.options.swipeToSlide || (e.options.slidesToScroll = 1), i("img[data-lazy]", e.$slider).not("[src]").addClass("slick-loading"), e.setupInfinite(), e.buildArrows(), e.buildDots(), e.updateDots(), e.setSlideClasses("number" == typeof e.currentSlide ? e.currentSlide : 0), !0 === e.options.draggable && e.$list.addClass("draggable") }, e.prototype.buildRows = function () { var i, e, t, o, s, n, r, l = this; if (o = document.createDocumentFragment(), n = l.$slider.children(), l.options.rows > 1) { for (r = l.options.slidesPerRow * l.options.rows, s = Math.ceil(n.length / r), i = 0; i < s; i++) { var d = document.createElement("div"); for (e = 0; e < l.options.rows; e++) { var a = document.createElement("div"); for (t = 0; t < l.options.slidesPerRow; t++) { var c = i * r + (e * l.options.slidesPerRow + t); n.get(c) && a.appendChild(n.get(c)) } d.appendChild(a) } o.appendChild(d) } l.$slider.empty().append(o), l.$slider.children().children().children().css({ width: 100 / l.options.slidesPerRow + "%", display: "inline-block" }) } }, e.prototype.checkResponsive = function (e, t) { var o, s, n, r = this, l = !1, d = r.$slider.width(), a = window.innerWidth || i(window).width(); if ("window" === r.respondTo ? n = a : "slider" === r.respondTo ? n = d : "min" === r.respondTo && (n = Math.min(a, d)), r.options.responsive && r.options.responsive.length && null !== r.options.responsive) { s = null; for (o in r.breakpoints) r.breakpoints.hasOwnProperty(o) && (!1 === r.originalSettings.mobileFirst ? n < r.breakpoints[o] && (s = r.breakpoints[o]) : n > r.breakpoints[o] && (s = r.breakpoints[o])); null !== s ? null !== r.activeBreakpoint ? (s !== r.activeBreakpoint || t) && (r.activeBreakpoint = s, "unslick" === r.breakpointSettings[s] ? r.unslick(s) : (r.options = i.extend({}, r.originalSettings, r.breakpointSettings[s]), !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e)), l = s) : (r.activeBreakpoint = s, "unslick" === r.breakpointSettings[s] ? r.unslick(s) : (r.options = i.extend({}, r.originalSettings, r.breakpointSettings[s]), !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e)), l = s) : null !== r.activeBreakpoint && (r.activeBreakpoint = null, r.options = r.originalSettings, !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e), l = s), e || !1 === l || r.$slider.trigger("breakpoint", [r, l]) } }, e.prototype.changeSlide = function (e, t) { var o, s, n, r = this, l = i(e.currentTarget); switch (l.is("a") && e.preventDefault(), l.is("li") || (l = l.closest("li")), n = r.slideCount % r.options.slidesToScroll != 0, o = n ? 0 : (r.slideCount - r.currentSlide) % r.options.slidesToScroll, e.data.message) { case "previous": s = 0 === o ? r.options.slidesToScroll : r.options.slidesToShow - o, r.slideCount > r.options.slidesToShow && r.slideHandler(r.currentSlide - s, !1, t); break; case "next": s = 0 === o ? r.options.slidesToScroll : o, r.slideCount > r.options.slidesToShow && r.slideHandler(r.currentSlide + s, !1, t); break; case "index": var d = 0 === e.data.index ? 0 : e.data.index || l.index() * r.options.slidesToScroll; r.slideHandler(r.checkNavigable(d), !1, t), l.children().trigger("focus"); break; default: return } }, e.prototype.checkNavigable = function (i) { var e, t; if (e = this.getNavigableIndexes(), t = 0, i > e[e.length - 1]) i = e[e.length - 1]; else for (var o in e) { if (i < e[o]) { i = t; break } t = e[o] } return i }, e.prototype.cleanUpEvents = function () { var e = this; e.options.dots && null !== e.$dots && (i("li", e.$dots).off("click.slick", e.changeSlide).off("mouseenter.slick", i.proxy(e.interrupt, e, !0)).off("mouseleave.slick", i.proxy(e.interrupt, e, !1)), !0 === e.options.accessibility && e.$dots.off("keydown.slick", e.keyHandler)), e.$slider.off("focus.slick blur.slick"), !0 === e.options.arrows && e.slideCount > e.options.slidesToShow && (e.$prevArrow && e.$prevArrow.off("click.slick", e.changeSlide), e.$nextArrow && e.$nextArrow.off("click.slick", e.changeSlide), !0 === e.options.accessibility && (e.$prevArrow && e.$prevArrow.off("keydown.slick", e.keyHandler), e.$nextArrow && e.$nextArrow.off("keydown.slick", e.keyHandler))), e.$list.off("touchstart.slick mousedown.slick", e.swipeHandler), e.$list.off("touchmove.slick mousemove.slick", e.swipeHandler), e.$list.off("touchend.slick mouseup.slick", e.swipeHandler), e.$list.off("touchcancel.slick mouseleave.slick", e.swipeHandler), e.$list.off("click.slick", e.clickHandler), i(document).off(e.visibilityChange, e.visibility), e.cleanUpSlideEvents(), !0 === e.options.accessibility && e.$list.off("keydown.slick", e.keyHandler), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().off("click.slick", e.selectHandler), i(window).off("orientationchange.slick.slick-" + e.instanceUid, e.orientationChange), i(window).off("resize.slick.slick-" + e.instanceUid, e.resize), i("[draggable!=true]", e.$slideTrack).off("dragstart", e.preventDefault), i(window).off("load.slick.slick-" + e.instanceUid, e.setPosition) }, e.prototype.cleanUpSlideEvents = function () { var e = this; e.$list.off("mouseenter.slick", i.proxy(e.interrupt, e, !0)), e.$list.off("mouseleave.slick", i.proxy(e.interrupt, e, !1)) }, e.prototype.cleanUpRows = function () { var i, e = this; e.options.rows > 1 && ((i = e.$slides.children().children()).removeAttr("style"), e.$slider.empty().append(i)) }, e.prototype.clickHandler = function (i) { !1 === this.shouldClick && (i.stopImmediatePropagation(), i.stopPropagation(), i.preventDefault()) }, e.prototype.destroy = function (e) { var t = this; t.autoPlayClear(), t.touchObject = {}, t.cleanUpEvents(), i(".slick-cloned", t.$slider).detach(), t.$dots && t.$dots.remove(), t.$prevArrow && t.$prevArrow.length && (t.$prevArrow.removeClass("slick-disabled slick-arrow slick-hidden").removeAttr("aria-hidden aria-disabled tabindex").css("display", ""), t.htmlExpr.test(t.options.prevArrow) && t.$prevArrow.remove()), t.$nextArrow && t.$nextArrow.length && (t.$nextArrow.removeClass("slick-disabled slick-arrow slick-hidden").removeAttr("aria-hidden aria-disabled tabindex").css("display", ""), t.htmlExpr.test(t.options.nextArrow) && t.$nextArrow.remove()), t.$slides && (t.$slides.removeClass("slick-slide slick-active slick-center slick-visible slick-current").removeAttr("aria-hidden").removeAttr("data-slick-index").each(function () { i(this).attr("style", i(this).data("originalStyling")) }), t.$slideTrack.children(this.options.slide).detach(), t.$slideTrack.detach(), t.$list.detach(), t.$slider.append(t.$slides)), t.cleanUpRows(), t.$slider.removeClass("slick-slider"), t.$slider.removeClass("slick-initialized"), t.$slider.removeClass("slick-dotted"), t.unslicked = !0, e || t.$slider.trigger("destroy", [t]) }, e.prototype.disableTransition = function (i) { var e = this, t = {}; t[e.transitionType] = "", !1 === e.options.fade ? e.$slideTrack.css(t) : e.$slides.eq(i).css(t) }, e.prototype.fadeSlide = function (i, e) { var t = this; !1 === t.cssTransitions ? (t.$slides.eq(i).css({ zIndex: t.options.zIndex }), t.$slides.eq(i).animate({ opacity: 1 }, t.options.speed, t.options.easing, e)) : (t.applyTransition(i), t.$slides.eq(i).css({ opacity: 1, zIndex: t.options.zIndex }), e && setTimeout(function () { t.disableTransition(i), e.call() }, t.options.speed)) }, e.prototype.fadeSlideOut = function (i) { var e = this; !1 === e.cssTransitions ? e.$slides.eq(i).animate({ opacity: 0, zIndex: e.options.zIndex - 2 }, e.options.speed, e.options.easing) : (e.applyTransition(i), e.$slides.eq(i).css({ opacity: 0, zIndex: e.options.zIndex - 2 })) }, e.prototype.filterSlides = e.prototype.slickFilter = function (i) { var e = this; null !== i && (e.$slidesCache = e.$slides, e.unload(), e.$slideTrack.children(this.options.slide).detach(), e.$slidesCache.filter(i).appendTo(e.$slideTrack), e.reinit()) }, e.prototype.focusHandler = function () { var e = this; e.$slider.off("focus.slick blur.slick").on("focus.slick blur.slick", "*", function (t) { t.stopImmediatePropagation(); var o = i(this); setTimeout(function () { e.options.pauseOnFocus && (e.focussed = o.is(":focus"), e.autoPlay()) }, 0) }) }, e.prototype.getCurrent = e.prototype.slickCurrentSlide = function () { return this.currentSlide }, e.prototype.getDotCount = function () { var i = this, e = 0, t = 0, o = 0; if (!0 === i.options.infinite) if (i.slideCount <= i.options.slidesToShow) ++o; else for (; e < i.slideCount;)++o, e = t + i.options.slidesToScroll, t += i.options.slidesToScroll <= i.options.slidesToShow ? i.options.slidesToScroll : i.options.slidesToShow; else if (!0 === i.options.centerMode) o = i.slideCount; else if (i.options.asNavFor) for (; e < i.slideCount;)++o, e = t + i.options.slidesToScroll, t += i.options.slidesToScroll <= i.options.slidesToShow ? i.options.slidesToScroll : i.options.slidesToShow; else o = 1 + Math.ceil((i.slideCount - i.options.slidesToShow) / i.options.slidesToScroll); return o - 1 }, e.prototype.getLeft = function (i) { var e, t, o, s, n = this, r = 0; return n.slideOffset = 0, t = n.$slides.first().outerHeight(!0), !0 === n.options.infinite ? (n.slideCount > n.options.slidesToShow && (n.slideOffset = n.slideWidth * n.options.slidesToShow * -1, s = -1, !0 === n.options.vertical && !0 === n.options.centerMode && (2 === n.options.slidesToShow ? s = -1.5 : 1 === n.options.slidesToShow && (s = -2)), r = t * n.options.slidesToShow * s), n.slideCount % n.options.slidesToScroll != 0 && i + n.options.slidesToScroll > n.slideCount && n.slideCount > n.options.slidesToShow && (i > n.slideCount ? (n.slideOffset = (n.options.slidesToShow - (i - n.slideCount)) * n.slideWidth * -1, r = (n.options.slidesToShow - (i - n.slideCount)) * t * -1) : (n.slideOffset = n.slideCount % n.options.slidesToScroll * n.slideWidth * -1, r = n.slideCount % n.options.slidesToScroll * t * -1))) : i + n.options.slidesToShow > n.slideCount && (n.slideOffset = (i + n.options.slidesToShow - n.slideCount) * n.slideWidth, r = (i + n.options.slidesToShow - n.slideCount) * t), n.slideCount <= n.options.slidesToShow && (n.slideOffset = 0, r = 0), !0 === n.options.centerMode && n.slideCount <= n.options.slidesToShow ? n.slideOffset = n.slideWidth * Math.floor(n.options.slidesToShow) / 2 - n.slideWidth * n.slideCount / 2 : !0 === n.options.centerMode && !0 === n.options.infinite ? n.slideOffset += n.slideWidth * Math.floor(n.options.slidesToShow / 2) - n.slideWidth : !0 === n.options.centerMode && (n.slideOffset = 0, n.slideOffset += n.slideWidth * Math.floor(n.options.slidesToShow / 2)), e = !1 === n.options.vertical ? i * n.slideWidth * -1 + n.slideOffset : i * t * -1 + r, !0 === n.options.variableWidth && (o = n.slideCount <= n.options.slidesToShow || !1 === n.options.infinite ? n.$slideTrack.children(".slick-slide").eq(i) : n.$slideTrack.children(".slick-slide").eq(i + n.options.slidesToShow), e = !0 === n.options.rtl ? o[0] ? -1 * (n.$slideTrack.width() - o[0].offsetLeft - o.width()) : 0 : o[0] ? -1 * o[0].offsetLeft : 0, !0 === n.options.centerMode && (o = n.slideCount <= n.options.slidesToShow || !1 === n.options.infinite ? n.$slideTrack.children(".slick-slide").eq(i) : n.$slideTrack.children(".slick-slide").eq(i + n.options.slidesToShow + 1), e = !0 === n.options.rtl ? o[0] ? -1 * (n.$slideTrack.width() - o[0].offsetLeft - o.width()) : 0 : o[0] ? -1 * o[0].offsetLeft : 0, e += (n.$list.width() - o.outerWidth()) / 2)), e }, e.prototype.getOption = e.prototype.slickGetOption = function (i) { return this.options[i] }, e.prototype.getNavigableIndexes = function () { var i, e = this, t = 0, o = 0, s = []; for (!1 === e.options.infinite ? i = e.slideCount : (t = -1 * e.options.slidesToScroll, o = -1 * e.options.slidesToScroll, i = 2 * e.slideCount); t < i;)s.push(t), t = o + e.options.slidesToScroll, o += e.options.slidesToScroll <= e.options.slidesToShow ? e.options.slidesToScroll : e.options.slidesToShow; return s }, e.prototype.getSlick = function () { return this }, e.prototype.getSlideCount = function () { var e, t, o = this; return t = !0 === o.options.centerMode ? o.slideWidth * Math.floor(o.options.slidesToShow / 2) : 0, !0 === o.options.swipeToSlide ? (o.$slideTrack.find(".slick-slide").each(function (s, n) { if (n.offsetLeft - t + i(n).outerWidth() / 2 > -1 * o.swipeLeft) return e = n, !1 }), Math.abs(i(e).attr("data-slick-index") - o.currentSlide) || 1) : o.options.slidesToScroll }, e.prototype.goTo = e.prototype.slickGoTo = function (i, e) { this.changeSlide({ data: { message: "index", index: parseInt(i) } }, e) }, e.prototype.init = function (e) { var t = this; i(t.$slider).hasClass("slick-initialized") || (i(t.$slider).addClass("slick-initialized"), t.buildRows(), t.buildOut(), t.setProps(), t.startLoad(), t.loadSlider(), t.initializeEvents(), t.updateArrows(), t.updateDots(), t.checkResponsive(!0), t.focusHandler()), e && t.$slider.trigger("init", [t]), !0 === t.options.accessibility && t.initADA(), t.options.autoplay && (t.paused = !1, t.autoPlay()) }, e.prototype.initADA = function () { var e = this, t = Math.ceil(e.slideCount / e.options.slidesToShow), o = e.getNavigableIndexes().filter(function (i) { return i >= 0 && i < e.slideCount }); e.$slides.add(e.$slideTrack.find(".slick-cloned")).attr({ "aria-hidden": "true", tabindex: "-1" }).find("a, input, button, select").attr({ tabindex: "-1" }), null !== e.$dots && (e.$slides.not(e.$slideTrack.find(".slick-cloned")).each(function (t) { var s = o.indexOf(t); i(this).attr({ role: "tabpanel", id: "slick-slide" + e.instanceUid + t, tabindex: -1 }), -1 !== s && i(this).attr({ "aria-describedby": "slick-slide-control" + e.instanceUid + s }) }), e.$dots.attr("role", "tablist").find("li").each(function (s) { var n = o[s]; i(this).attr({ role: "presentation" }), i(this).find("button").first().attr({ role: "tab", id: "slick-slide-control" + e.instanceUid + s, "aria-controls": "slick-slide" + e.instanceUid + n, "aria-label": s + 1 + " of " + t, "aria-selected": null, tabindex: "-1" }) }).eq(e.currentSlide).find("button").attr({ "aria-selected": "true", tabindex: "0" }).end()); for (var s = e.currentSlide, n = s + e.options.slidesToShow; s < n; s++)e.$slides.eq(s).attr("tabindex", 0); e.activateADA() }, e.prototype.initArrowEvents = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.off("click.slick").on("click.slick", { message: "previous" }, i.changeSlide), i.$nextArrow.off("click.slick").on("click.slick", { message: "next" }, i.changeSlide), !0 === i.options.accessibility && (i.$prevArrow.on("keydown.slick", i.keyHandler), i.$nextArrow.on("keydown.slick", i.keyHandler))) }, e.prototype.initDotEvents = function () { var e = this; !0 === e.options.dots && (i("li", e.$dots).on("click.slick", { message: "index" }, e.changeSlide), !0 === e.options.accessibility && e.$dots.on("keydown.slick", e.keyHandler)), !0 === e.options.dots && !0 === e.options.pauseOnDotsHover && i("li", e.$dots).on("mouseenter.slick", i.proxy(e.interrupt, e, !0)).on("mouseleave.slick", i.proxy(e.interrupt, e, !1)) }, e.prototype.initSlideEvents = function () { var e = this; e.options.pauseOnHover && (e.$list.on("mouseenter.slick", i.proxy(e.interrupt, e, !0)), e.$list.on("mouseleave.slick", i.proxy(e.interrupt, e, !1))) }, e.prototype.initializeEvents = function () { var e = this; e.initArrowEvents(), e.initDotEvents(), e.initSlideEvents(), e.$list.on("touchstart.slick mousedown.slick", { action: "start" }, e.swipeHandler), e.$list.on("touchmove.slick mousemove.slick", { action: "move" }, e.swipeHandler), e.$list.on("touchend.slick mouseup.slick", { action: "end" }, e.swipeHandler), e.$list.on("touchcancel.slick mouseleave.slick", { action: "end" }, e.swipeHandler), e.$list.on("click.slick", e.clickHandler), i(document).on(e.visibilityChange, i.proxy(e.visibility, e)), !0 === e.options.accessibility && e.$list.on("keydown.slick", e.keyHandler), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().on("click.slick", e.selectHandler), i(window).on("orientationchange.slick.slick-" + e.instanceUid, i.proxy(e.orientationChange, e)), i(window).on("resize.slick.slick-" + e.instanceUid, i.proxy(e.resize, e)), i("[draggable!=true]", e.$slideTrack).on("dragstart", e.preventDefault), i(window).on("load.slick.slick-" + e.instanceUid, e.setPosition), i(e.setPosition) }, e.prototype.initUI = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.show(), i.$nextArrow.show()), !0 === i.options.dots && i.slideCount > i.options.slidesToShow && i.$dots.show() }, e.prototype.keyHandler = function (i) { var e = this; i.target.tagName.match("TEXTAREA|INPUT|SELECT") || (37 === i.keyCode && !0 === e.options.accessibility ? e.changeSlide({ data: { message: !0 === e.options.rtl ? "next" : "previous" } }) : 39 === i.keyCode && !0 === e.options.accessibility && e.changeSlide({ data: { message: !0 === e.options.rtl ? "previous" : "next" } })) }, e.prototype.lazyLoad = function () { function e(e) { i("img[data-lazy]", e).each(function () { var e = i(this), t = i(this).attr("data-lazy"), o = i(this).attr("data-srcset"), s = i(this).attr("data-sizes") || n.$slider.attr("data-sizes"), r = document.createElement("img"); r.onload = function () { e.animate({ opacity: 0 }, 100, function () { o && (e.attr("srcset", o), s && e.attr("sizes", s)), e.attr("src", t).animate({ opacity: 1 }, 200, function () { e.removeAttr("data-lazy data-srcset data-sizes").removeClass("slick-loading") }), n.$slider.trigger("lazyLoaded", [n, e, t]) }) }, r.onerror = function () { e.removeAttr("data-lazy").removeClass("slick-loading").addClass("slick-lazyload-error"), n.$slider.trigger("lazyLoadError", [n, e, t]) }, r.src = t }) } var t, o, s, n = this; if (!0 === n.options.centerMode ? !0 === n.options.infinite ? s = (o = n.currentSlide + (n.options.slidesToShow / 2 + 1)) + n.options.slidesToShow + 2 : (o = Math.max(0, n.currentSlide - (n.options.slidesToShow / 2 + 1)), s = n.options.slidesToShow / 2 + 1 + 2 + n.currentSlide) : (o = n.options.infinite ? n.options.slidesToShow + n.currentSlide : n.currentSlide, s = Math.ceil(o + n.options.slidesToShow), !0 === n.options.fade && (o > 0 && o--, s <= n.slideCount && s++)), t = n.$slider.find(".slick-slide").slice(o, s), "anticipated" === n.options.lazyLoad) for (var r = o - 1, l = s, d = n.$slider.find(".slick-slide"), a = 0; a < n.options.slidesToScroll; a++)r < 0 && (r = n.slideCount - 1), t = (t = t.add(d.eq(r))).add(d.eq(l)), r--, l++; e(t), n.slideCount <= n.options.slidesToShow ? e(n.$slider.find(".slick-slide")) : n.currentSlide >= n.slideCount - n.options.slidesToShow ? e(n.$slider.find(".slick-cloned").slice(0, n.options.slidesToShow)) : 0 === n.currentSlide && e(n.$slider.find(".slick-cloned").slice(-1 * n.options.slidesToShow)) }, e.prototype.loadSlider = function () { var i = this; i.setPosition(), i.$slideTrack.css({ opacity: 1 }), i.$slider.removeClass("slick-loading"), i.initUI(), "progressive" === i.options.lazyLoad && i.progressiveLazyLoad() }, e.prototype.next = e.prototype.slickNext = function () { this.changeSlide({ data: { message: "next" } }) }, e.prototype.orientationChange = function () { var i = this; i.checkResponsive(), i.setPosition() }, e.prototype.pause = e.prototype.slickPause = function () { var i = this; i.autoPlayClear(), i.paused = !0 }, e.prototype.play = e.prototype.slickPlay = function () { var i = this; i.autoPlay(), i.options.autoplay = !0, i.paused = !1, i.focussed = !1, i.interrupted = !1 }, e.prototype.postSlide = function (e) { var t = this; t.unslicked || (t.$slider.trigger("afterChange", [t, e]), t.animating = !1, t.slideCount > t.options.slidesToShow && t.setPosition(), t.swipeLeft = null, t.options.autoplay && t.autoPlay(), !0 === t.options.accessibility && (t.initADA(), t.options.focusOnChange && i(t.$slides.get(t.currentSlide)).attr("tabindex", 0).focus())) }, e.prototype.prev = e.prototype.slickPrev = function () { this.changeSlide({ data: { message: "previous" } }) }, e.prototype.preventDefault = function (i) { i.preventDefault() }, e.prototype.progressiveLazyLoad = function (e) { e = e || 1; var t, o, s, n, r, l = this, d = i("img[data-lazy]", l.$slider); d.length ? (t = d.first(), o = t.attr("data-lazy"), s = t.attr("data-srcset"), n = t.attr("data-sizes") || l.$slider.attr("data-sizes"), (r = document.createElement("img")).onload = function () { s && (t.attr("srcset", s), n && t.attr("sizes", n)), t.attr("src", o).removeAttr("data-lazy data-srcset data-sizes").removeClass("slick-loading"), !0 === l.options.adaptiveHeight && l.setPosition(), l.$slider.trigger("lazyLoaded", [l, t, o]), l.progressiveLazyLoad() }, r.onerror = function () { e < 3 ? setTimeout(function () { l.progressiveLazyLoad(e + 1) }, 500) : (t.removeAttr("data-lazy").removeClass("slick-loading").addClass("slick-lazyload-error"), l.$slider.trigger("lazyLoadError", [l, t, o]), l.progressiveLazyLoad()) }, r.src = o) : l.$slider.trigger("allImagesLoaded", [l]) }, e.prototype.refresh = function (e) { var t, o, s = this; o = s.slideCount - s.options.slidesToShow, !s.options.infinite && s.currentSlide > o && (s.currentSlide = o), s.slideCount <= s.options.slidesToShow && (s.currentSlide = 0), t = s.currentSlide, s.destroy(!0), i.extend(s, s.initials, { currentSlide: t }), s.init(), e || s.changeSlide({ data: { message: "index", index: t } }, !1) }, e.prototype.registerBreakpoints = function () { var e, t, o, s = this, n = s.options.responsive || null; if ("array" === i.type(n) && n.length) { s.respondTo = s.options.respondTo || "window"; for (e in n) if (o = s.breakpoints.length - 1, n.hasOwnProperty(e)) { for (t = n[e].breakpoint; o >= 0;)s.breakpoints[o] && s.breakpoints[o] === t && s.breakpoints.splice(o, 1), o--; s.breakpoints.push(t), s.breakpointSettings[t] = n[e].settings } s.breakpoints.sort(function (i, e) { return s.options.mobileFirst ? i - e : e - i }) } }, e.prototype.reinit = function () { var e = this; e.$slides = e.$slideTrack.children(e.options.slide).addClass("slick-slide"), e.slideCount = e.$slides.length, e.currentSlide >= e.slideCount && 0 !== e.currentSlide && (e.currentSlide = e.currentSlide - e.options.slidesToScroll), e.slideCount <= e.options.slidesToShow && (e.currentSlide = 0), e.registerBreakpoints(), e.setProps(), e.setupInfinite(), e.buildArrows(), e.updateArrows(), e.initArrowEvents(), e.buildDots(), e.updateDots(), e.initDotEvents(), e.cleanUpSlideEvents(), e.initSlideEvents(), e.checkResponsive(!1, !0), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().on("click.slick", e.selectHandler), e.setSlideClasses("number" == typeof e.currentSlide ? e.currentSlide : 0), e.setPosition(), e.focusHandler(), e.paused = !e.options.autoplay, e.autoPlay(), e.$slider.trigger("reInit", [e]) }, e.prototype.resize = function () { var e = this; i(window).width() !== e.windowWidth && (clearTimeout(e.windowDelay), e.windowDelay = window.setTimeout(function () { e.windowWidth = i(window).width(), e.checkResponsive(), e.unslicked || e.setPosition() }, 50)) }, e.prototype.removeSlide = e.prototype.slickRemove = function (i, e, t) { var o = this; if (i = "boolean" == typeof i ? !0 === (e = i) ? 0 : o.slideCount - 1 : !0 === e ? --i : i, o.slideCount < 1 || i < 0 || i > o.slideCount - 1) return !1; o.unload(), !0 === t ? o.$slideTrack.children().remove() : o.$slideTrack.children(this.options.slide).eq(i).remove(), o.$slides = o.$slideTrack.children(this.options.slide), o.$slideTrack.children(this.options.slide).detach(), o.$slideTrack.append(o.$slides), o.$slidesCache = o.$slides, o.reinit() }, e.prototype.setCSS = function (i) { var e, t, o = this, s = {}; !0 === o.options.rtl && (i = -i), e = "left" == o.positionProp ? Math.ceil(i) + "px" : "0px", t = "top" == o.positionProp ? Math.ceil(i) + "px" : "0px", s[o.positionProp] = i, !1 === o.transformsEnabled ? o.$slideTrack.css(s) : (s = {}, !1 === o.cssTransitions ? (s[o.animType] = "translate(" + e + ", " + t + ")", o.$slideTrack.css(s)) : (s[o.animType] = "translate3d(" + e + ", " + t + ", 0px)", o.$slideTrack.css(s))) }, e.prototype.setDimensions = function () { var i = this; !1 === i.options.vertical ? !0 === i.options.centerMode && i.$list.css({ padding: "0px " + i.options.centerPadding }) : (i.$list.height(i.$slides.first().outerHeight(!0) * i.options.slidesToShow), !0 === i.options.centerMode && i.$list.css({ padding: i.options.centerPadding + " 0px" })), i.listWidth = i.$list.width(), i.listHeight = i.$list.height(), !1 === i.options.vertical && !1 === i.options.variableWidth ? (i.slideWidth = Math.ceil(i.listWidth / i.options.slidesToShow), i.$slideTrack.width(Math.ceil(i.slideWidth * i.$slideTrack.children(".slick-slide").length))) : !0 === i.options.variableWidth ? i.$slideTrack.width(5e3 * i.slideCount) : (i.slideWidth = Math.ceil(i.listWidth), i.$slideTrack.height(Math.ceil(i.$slides.first().outerHeight(!0) * i.$slideTrack.children(".slick-slide").length))); var e = i.$slides.first().outerWidth(!0) - i.$slides.first().width(); !1 === i.options.variableWidth && i.$slideTrack.children(".slick-slide").width(i.slideWidth - e) }, e.prototype.setFade = function () { var e, t = this; t.$slides.each(function (o, s) { e = t.slideWidth * o * -1, !0 === t.options.rtl ? i(s).css({ position: "relative", right: e, top: 0, zIndex: t.options.zIndex - 2, opacity: 0 }) : i(s).css({ position: "relative", left: e, top: 0, zIndex: t.options.zIndex - 2, opacity: 0 }) }), t.$slides.eq(t.currentSlide).css({ zIndex: t.options.zIndex - 1, opacity: 1 }) }, e.prototype.setHeight = function () { var i = this; if (1 === i.options.slidesToShow && !0 === i.options.adaptiveHeight && !1 === i.options.vertical) { var e = i.$slides.eq(i.currentSlide).outerHeight(!0); i.$list.css("height", e) } }, e.prototype.setOption = e.prototype.slickSetOption = function () { var e, t, o, s, n, r = this, l = !1; if ("object" === i.type(arguments[0]) ? (o = arguments[0], l = arguments[1], n = "multiple") : "string" === i.type(arguments[0]) && (o = arguments[0], s = arguments[1], l = arguments[2], "responsive" === arguments[0] && "array" === i.type(arguments[1]) ? n = "responsive" : void 0 !== arguments[1] && (n = "single")), "single" === n) r.options[o] = s; else if ("multiple" === n) i.each(o, function (i, e) { r.options[i] = e }); else if ("responsive" === n) for (t in s) if ("array" !== i.type(r.options.responsive)) r.options.responsive = [s[t]]; else { for (e = r.options.responsive.length - 1; e >= 0;)r.options.responsive[e].breakpoint === s[t].breakpoint && r.options.responsive.splice(e, 1), e--; r.options.responsive.push(s[t]) } l && (r.unload(), r.reinit()) }, e.prototype.setPosition = function () { var i = this; i.setDimensions(), i.setHeight(), !1 === i.options.fade ? i.setCSS(i.getLeft(i.currentSlide)) : i.setFade(), i.$slider.trigger("setPosition", [i]) }, e.prototype.setProps = function () { var i = this, e = document.body.style; i.positionProp = !0 === i.options.vertical ? "top" : "left", "top" === i.positionProp ? i.$slider.addClass("slick-vertical") : i.$slider.removeClass("slick-vertical"), void 0 === e.WebkitTransition && void 0 === e.MozTransition && void 0 === e.msTransition || !0 === i.options.useCSS && (i.cssTransitions = !0), i.options.fade && ("number" == typeof i.options.zIndex ? i.options.zIndex < 3 && (i.options.zIndex = 3) : i.options.zIndex = i.defaults.zIndex), void 0 !== e.OTransform && (i.animType = "OTransform", i.transformType = "-o-transform", i.transitionType = "OTransition", void 0 === e.perspectiveProperty && void 0 === e.webkitPerspective && (i.animType = !1)), void 0 !== e.MozTransform && (i.animType = "MozTransform", i.transformType = "-moz-transform", i.transitionType = "MozTransition", void 0 === e.perspectiveProperty && void 0 === e.MozPerspective && (i.animType = !1)), void 0 !== e.webkitTransform && (i.animType = "webkitTransform", i.transformType = "-webkit-transform", i.transitionType = "webkitTransition", void 0 === e.perspectiveProperty && void 0 === e.webkitPerspective && (i.animType = !1)), void 0 !== e.msTransform && (i.animType = "msTransform", i.transformType = "-ms-transform", i.transitionType = "msTransition", void 0 === e.msTransform && (i.animType = !1)), void 0 !== e.transform && !1 !== i.animType && (i.animType = "transform", i.transformType = "transform", i.transitionType = "transition"), i.transformsEnabled = i.options.useTransform && null !== i.animType && !1 !== i.animType }, e.prototype.setSlideClasses = function (i) { var e, t, o, s, n = this; if (t = n.$slider.find(".slick-slide").removeClass("slick-active slick-center slick-current").attr("aria-hidden", "true"), n.$slides.eq(i).addClass("slick-current"), !0 === n.options.centerMode) { var r = n.options.slidesToShow % 2 == 0 ? 1 : 0; e = Math.floor(n.options.slidesToShow / 2), !0 === n.options.infinite && (i >= e && i <= n.slideCount - 1 - e ? n.$slides.slice(i - e + r, i + e + 1).addClass("slick-active").attr("aria-hidden", "false") : (o = n.options.slidesToShow + i, t.slice(o - e + 1 + r, o + e + 2).addClass("slick-active").attr("aria-hidden", "false")), 0 === i ? t.eq(t.length - 1 - n.options.slidesToShow).addClass("slick-center") : i === n.slideCount - 1 && t.eq(n.options.slidesToShow).addClass("slick-center")), n.$slides.eq(i).addClass("slick-center") } else i >= 0 && i <= n.slideCount - n.options.slidesToShow ? n.$slides.slice(i, i + n.options.slidesToShow).addClass("slick-active").attr("aria-hidden", "false") : t.length <= n.options.slidesToShow ? t.addClass("slick-active").attr("aria-hidden", "false") : (s = n.slideCount % n.options.slidesToShow, o = !0 === n.options.infinite ? n.options.slidesToShow + i : i, n.options.slidesToShow == n.options.slidesToScroll && n.slideCount - i < n.options.slidesToShow ? t.slice(o - (n.options.slidesToShow - s), o + s).addClass("slick-active").attr("aria-hidden", "false") : t.slice(o, o + n.options.slidesToShow).addClass("slick-active").attr("aria-hidden", "false")); "ondemand" !== n.options.lazyLoad && "anticipated" !== n.options.lazyLoad || n.lazyLoad() }, e.prototype.setupInfinite = function () { var e, t, o, s = this; if (!0 === s.options.fade && (s.options.centerMode = !1), !0 === s.options.infinite && !1 === s.options.fade && (t = null, s.slideCount > s.options.slidesToShow)) { for (o = !0 === s.options.centerMode ? s.options.slidesToShow + 1 : s.options.slidesToShow, e = s.slideCount; e > s.slideCount - o; e -= 1)t = e - 1, i(s.$slides[t]).clone(!0).attr("id", "").attr("data-slick-index", t - s.slideCount).prependTo(s.$slideTrack).addClass("slick-cloned"); for (e = 0; e < o + s.slideCount; e += 1)t = e, i(s.$slides[t]).clone(!0).attr("id", "").attr("data-slick-index", t + s.slideCount).appendTo(s.$slideTrack).addClass("slick-cloned"); s.$slideTrack.find(".slick-cloned").find("[id]").each(function () { i(this).attr("id", "") }) } }, e.prototype.interrupt = function (i) { var e = this; i || e.autoPlay(), e.interrupted = i }, e.prototype.selectHandler = function (e) { var t = this, o = i(e.target).is(".slick-slide") ? i(e.target) : i(e.target).parents(".slick-slide"), s = parseInt(o.attr("data-slick-index")); s || (s = 0), t.slideCount <= t.options.slidesToShow ? t.slideHandler(s, !1, !0) : t.slideHandler(s) }, e.prototype.slideHandler = function (i, e, t) { var o, s, n, r, l, d = null, a = this; if (e = e || !1, !(!0 === a.animating && !0 === a.options.waitForAnimate || !0 === a.options.fade && a.currentSlide === i)) if (!1 === e && a.asNavFor(i), o = i, d = a.getLeft(o), r = a.getLeft(a.currentSlide), a.currentLeft = null === a.swipeLeft ? r : a.swipeLeft, !1 === a.options.infinite && !1 === a.options.centerMode && (i < 0 || i > a.getDotCount() * a.options.slidesToScroll)) !1 === a.options.fade && (o = a.currentSlide, !0 !== t ? a.animateSlide(r, function () { a.postSlide(o) }) : a.postSlide(o)); else if (!1 === a.options.infinite && !0 === a.options.centerMode && (i < 0 || i > a.slideCount - a.options.slidesToScroll)) !1 === a.options.fade && (o = a.currentSlide, !0 !== t ? a.animateSlide(r, function () { a.postSlide(o) }) : a.postSlide(o)); else { if (a.options.autoplay && clearInterval(a.autoPlayTimer), s = o < 0 ? a.slideCount % a.options.slidesToScroll != 0 ? a.slideCount - a.slideCount % a.options.slidesToScroll : a.slideCount + o : o >= a.slideCount ? a.slideCount % a.options.slidesToScroll != 0 ? 0 : o - a.slideCount : o, a.animating = !0, a.$slider.trigger("beforeChange", [a, a.currentSlide, s]), n = a.currentSlide, a.currentSlide = s, a.setSlideClasses(a.currentSlide), a.options.asNavFor && (l = (l = a.getNavTarget()).slick("getSlick")).slideCount <= l.options.slidesToShow && l.setSlideClasses(a.currentSlide), a.updateDots(), a.updateArrows(), !0 === a.options.fade) return !0 !== t ? (a.fadeSlideOut(n), a.fadeSlide(s, function () { a.postSlide(s) })) : a.postSlide(s), void a.animateHeight(); !0 !== t ? a.animateSlide(d, function () { a.postSlide(s) }) : a.postSlide(s) } }, e.prototype.startLoad = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.hide(), i.$nextArrow.hide()), !0 === i.options.dots && i.slideCount > i.options.slidesToShow && i.$dots.hide(), i.$slider.addClass("slick-loading") }, e.prototype.swipeDirection = function () { var i, e, t, o, s = this; return i = s.touchObject.startX - s.touchObject.curX, e = s.touchObject.startY - s.touchObject.curY, t = Math.atan2(e, i), (o = Math.round(180 * t / Math.PI)) < 0 && (o = 360 - Math.abs(o)), o <= 45 && o >= 0 ? !1 === s.options.rtl ? "left" : "right" : o <= 360 && o >= 315 ? !1 === s.options.rtl ? "left" : "right" : o >= 135 && o <= 225 ? !1 === s.options.rtl ? "right" : "left" : !0 === s.options.verticalSwiping ? o >= 35 && o <= 135 ? "down" : "up" : "vertical" }, e.prototype.swipeEnd = function (i) { var e, t, o = this; if (o.dragging = !1, o.swiping = !1, o.scrolling) return o.scrolling = !1, !1; if (o.interrupted = !1, o.shouldClick = !(o.touchObject.swipeLength > 10), void 0 === o.touchObject.curX) return !1; if (!0 === o.touchObject.edgeHit && o.$slider.trigger("edge", [o, o.swipeDirection()]), o.touchObject.swipeLength >= o.touchObject.minSwipe) { switch (t = o.swipeDirection()) { case "left": case "down": e = o.options.swipeToSlide ? o.checkNavigable(o.currentSlide + o.getSlideCount()) : o.currentSlide + o.getSlideCount(), o.currentDirection = 0; break; case "right": case "up": e = o.options.swipeToSlide ? o.checkNavigable(o.currentSlide - o.getSlideCount()) : o.currentSlide - o.getSlideCount(), o.currentDirection = 1 }"vertical" != t && (o.slideHandler(e), o.touchObject = {}, o.$slider.trigger("swipe", [o, t])) } else o.touchObject.startX !== o.touchObject.curX && (o.slideHandler(o.currentSlide), o.touchObject = {}) }, e.prototype.swipeHandler = function (i) { var e = this; if (!(!1 === e.options.swipe || "ontouchend" in document && !1 === e.options.swipe || !1 === e.options.draggable && -1 !== i.type.indexOf("mouse"))) switch (e.touchObject.fingerCount = i.originalEvent && void 0 !== i.originalEvent.touches ? i.originalEvent.touches.length : 1, e.touchObject.minSwipe = e.listWidth / e.options.touchThreshold, !0 === e.options.verticalSwiping && (e.touchObject.minSwipe = e.listHeight / e.options.touchThreshold), i.data.action) { case "start": e.swipeStart(i); break; case "move": e.swipeMove(i); break; case "end": e.swipeEnd(i) } }, e.prototype.swipeMove = function (i) { var e, t, o, s, n, r, l = this; return n = void 0 !== i.originalEvent ? i.originalEvent.touches : null, !(!l.dragging || l.scrolling || n && 1 !== n.length) && (e = l.getLeft(l.currentSlide), l.touchObject.curX = void 0 !== n ? n[0].pageX : i.clientX, l.touchObject.curY = void 0 !== n ? n[0].pageY : i.clientY, l.touchObject.swipeLength = Math.round(Math.sqrt(Math.pow(l.touchObject.curX - l.touchObject.startX, 2))), r = Math.round(Math.sqrt(Math.pow(l.touchObject.curY - l.touchObject.startY, 2))), !l.options.verticalSwiping && !l.swiping && r > 4 ? (l.scrolling = !0, !1) : (!0 === l.options.verticalSwiping && (l.touchObject.swipeLength = r), t = l.swipeDirection(), void 0 !== i.originalEvent && l.touchObject.swipeLength > 4 && (l.swiping = !0, i.preventDefault()), s = (!1 === l.options.rtl ? 1 : -1) * (l.touchObject.curX > l.touchObject.startX ? 1 : -1), !0 === l.options.verticalSwiping && (s = l.touchObject.curY > l.touchObject.startY ? 1 : -1), o = l.touchObject.swipeLength, l.touchObject.edgeHit = !1, !1 === l.options.infinite && (0 === l.currentSlide && "right" === t || l.currentSlide >= l.getDotCount() && "left" === t) && (o = l.touchObject.swipeLength * l.options.edgeFriction, l.touchObject.edgeHit = !0), !1 === l.options.vertical ? l.swipeLeft = e + o * s : l.swipeLeft = e + o * (l.$list.height() / l.listWidth) * s, !0 === l.options.verticalSwiping && (l.swipeLeft = e + o * s), !0 !== l.options.fade && !1 !== l.options.touchMove && (!0 === l.animating ? (l.swipeLeft = null, !1) : void l.setCSS(l.swipeLeft)))) }, e.prototype.swipeStart = function (i) { var e, t = this; if (t.interrupted = !0, 1 !== t.touchObject.fingerCount || t.slideCount <= t.options.slidesToShow) return t.touchObject = {}, !1; void 0 !== i.originalEvent && void 0 !== i.originalEvent.touches && (e = i.originalEvent.touches[0]), t.touchObject.startX = t.touchObject.curX = void 0 !== e ? e.pageX : i.clientX, t.touchObject.startY = t.touchObject.curY = void 0 !== e ? e.pageY : i.clientY, t.dragging = !0 }, e.prototype.unfilterSlides = e.prototype.slickUnfilter = function () { var i = this; null !== i.$slidesCache && (i.unload(), i.$slideTrack.children(this.options.slide).detach(), i.$slidesCache.appendTo(i.$slideTrack), i.reinit()) }, e.prototype.unload = function () { var e = this; i(".slick-cloned", e.$slider).remove(), e.$dots && e.$dots.remove(), e.$prevArrow && e.htmlExpr.test(e.options.prevArrow) && e.$prevArrow.remove(), e.$nextArrow && e.htmlExpr.test(e.options.nextArrow) && e.$nextArrow.remove(), e.$slides.removeClass("slick-slide slick-active slick-visible slick-current").attr("aria-hidden", "true").css("width", "") }, e.prototype.unslick = function (i) { var e = this; e.$slider.trigger("unslick", [e, i]), e.destroy() }, e.prototype.updateArrows = function () { var i = this; Math.floor(i.options.slidesToShow / 2), !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && !i.options.infinite && (i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false"), i.$nextArrow.removeClass("slick-disabled").attr("aria-disabled", "false"), 0 === i.currentSlide ? (i.$prevArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$nextArrow.removeClass("slick-disabled").attr("aria-disabled", "false")) : i.currentSlide >= i.slideCount - i.options.slidesToShow && !1 === i.options.centerMode ? (i.$nextArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false")) : i.currentSlide >= i.slideCount - 1 && !0 === i.options.centerMode && (i.$nextArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false"))) }, e.prototype.updateDots = function () { var i = this; null !== i.$dots && (i.$dots.find("li").removeClass("slick-active").end(), i.$dots.find("li").eq(Math.floor(i.currentSlide / i.options.slidesToScroll)).addClass("slick-active")) }, e.prototype.visibility = function () { var i = this; i.options.autoplay && (document[i.hidden] ? i.interrupted = !0 : i.interrupted = !1) }, i.fn.slick = function () { var i, t, o = this, s = arguments[0], n = Array.prototype.slice.call(arguments, 1), r = o.length; for (i = 0; i < r; i++)if ("object" == typeof s || void 0 === s ? o[i].slick = new e(o[i], s) : t = o[i].slick[s].apply(o[i].slick, n), void 0 !== t) return t; return o } });

/*!
 * typeahead.js 0.11.1
 * https://github.com/twitter/typeahead.js
 * Copyright 2013-2015 Twitter, Inc. and other contributors; Licensed MIT
 */

!function (a, b) { "function" == typeof define && define.amd ? define("bloodhound", ["jquery"], function (c) { return a.Bloodhound = b(c) }) : "object" == typeof exports ? module.exports = b(require("jquery")) : a.Bloodhound = b(jQuery) }(this, function (a) { var b = function () { "use strict"; return { isMsie: function () { return /(msie|trident)/i.test(navigator.userAgent) ? navigator.userAgent.match(/(msie |rv:)(\d+(.\d+)?)/i)[2] : !1 }, isBlankString: function (a) { return !a || /^\s*$/.test(a) }, escapeRegExChars: function (a) { return a.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&") }, isString: function (a) { return "string" == typeof a }, isNumber: function (a) { return "number" == typeof a }, isArray: a.isArray, isFunction: a.isFunction, isObject: a.isPlainObject, isUndefined: function (a) { return "undefined" == typeof a }, isElement: function (a) { return !(!a || 1 !== a.nodeType) }, isJQuery: function (b) { return b instanceof a }, toStr: function (a) { return b.isUndefined(a) || null === a ? "" : a + "" }, bind: a.proxy, each: function (b, c) { function d(a, b) { return c(b, a) } a.each(b, d) }, map: a.map, filter: a.grep, every: function (b, c) { var d = !0; return b ? (a.each(b, function (a, e) { return (d = c.call(null, e, a, b)) ? void 0 : !1 }), !!d) : d }, some: function (b, c) { var d = !1; return b ? (a.each(b, function (a, e) { return (d = c.call(null, e, a, b)) ? !1 : void 0 }), !!d) : d }, mixin: a.extend, identity: function (a) { return a }, clone: function (b) { return a.extend(!0, {}, b) }, getIdGenerator: function () { var a = 0; return function () { return a++ } }, templatify: function (b) { function c() { return String(b) } return a.isFunction(b) ? b : c }, defer: function (a) { setTimeout(a, 0) }, debounce: function (a, b, c) { var d, e; return function () { var f, g, h = this, i = arguments; return f = function () { d = null, c || (e = a.apply(h, i)) }, g = c && !d, clearTimeout(d), d = setTimeout(f, b), g && (e = a.apply(h, i)), e } }, throttle: function (a, b) { var c, d, e, f, g, h; return g = 0, h = function () { g = new Date, e = null, f = a.apply(c, d) }, function () { var i = new Date, j = b - (i - g); return c = this, d = arguments, 0 >= j ? (clearTimeout(e), e = null, g = i, f = a.apply(c, d)) : e || (e = setTimeout(h, j)), f } }, stringify: function (a) { return b.isString(a) ? a : JSON.stringify(a) }, noop: function () { } } }(), c = "0.11.1", d = function () { "use strict"; function a(a) { return a = b.toStr(a), a ? a.split(/\s+/) : [] } function c(a) { return a = b.toStr(a), a ? a.split(/\W+/) : [] } function d(a) { return function (c) { return c = b.isArray(c) ? c : [].slice.call(arguments, 0), function (d) { var e = []; return b.each(c, function (c) { e = e.concat(a(b.toStr(d[c]))) }), e } } } return { nonword: c, whitespace: a, obj: { nonword: d(c), whitespace: d(a) } } }(), e = function () { "use strict"; function c(c) { this.maxSize = b.isNumber(c) ? c : 100, this.reset(), this.maxSize <= 0 && (this.set = this.get = a.noop) } function d() { this.head = this.tail = null } function e(a, b) { this.key = a, this.val = b, this.prev = this.next = null } return b.mixin(c.prototype, { set: function (a, b) { var c, d = this.list.tail; this.size >= this.maxSize && (this.list.remove(d), delete this.hash[d.key], this.size--), (c = this.hash[a]) ? (c.val = b, this.list.moveToFront(c)) : (c = new e(a, b), this.list.add(c), this.hash[a] = c, this.size++) }, get: function (a) { var b = this.hash[a]; return b ? (this.list.moveToFront(b), b.val) : void 0 }, reset: function () { this.size = 0, this.hash = {}, this.list = new d } }), b.mixin(d.prototype, { add: function (a) { this.head && (a.next = this.head, this.head.prev = a), this.head = a, this.tail = this.tail || a }, remove: function (a) { a.prev ? a.prev.next = a.next : this.head = a.next, a.next ? a.next.prev = a.prev : this.tail = a.prev }, moveToFront: function (a) { this.remove(a), this.add(a) } }), c }(), f = function () { "use strict"; function c(a, c) { this.prefix = ["__", a, "__"].join(""), this.ttlKey = "__ttl__", this.keyMatcher = new RegExp("^" + b.escapeRegExChars(this.prefix)), this.ls = c || h, !this.ls && this._noop() } function d() { return (new Date).getTime() } function e(a) { return JSON.stringify(b.isUndefined(a) ? null : a) } function f(b) { return a.parseJSON(b) } function g(a) { var b, c, d = [], e = h.length; for (b = 0; e > b; b++)(c = h.key(b)).match(a) && d.push(c.replace(a, "")); return d } var h; try { h = window.localStorage, h.setItem("~~~", "!"), h.removeItem("~~~") } catch (i) { h = null } return b.mixin(c.prototype, { _prefix: function (a) { return this.prefix + a }, _ttlKey: function (a) { return this._prefix(a) + this.ttlKey }, _noop: function () { this.get = this.set = this.remove = this.clear = this.isExpired = b.noop }, _safeSet: function (a, b) { try { this.ls.setItem(a, b) } catch (c) { "QuotaExceededError" === c.name && (this.clear(), this._noop()) } }, get: function (a) { return this.isExpired(a) && this.remove(a), f(this.ls.getItem(this._prefix(a))) }, set: function (a, c, f) { return b.isNumber(f) ? this._safeSet(this._ttlKey(a), e(d() + f)) : this.ls.removeItem(this._ttlKey(a)), this._safeSet(this._prefix(a), e(c)) }, remove: function (a) { return this.ls.removeItem(this._ttlKey(a)), this.ls.removeItem(this._prefix(a)), this }, clear: function () { var a, b = g(this.keyMatcher); for (a = b.length; a--;)this.remove(b[a]); return this }, isExpired: function (a) { var c = f(this.ls.getItem(this._ttlKey(a))); return b.isNumber(c) && d() > c ? !0 : !1 } }), c }(), g = function () { "use strict"; function c(a) { a = a || {}, this.cancelled = !1, this.lastReq = null, this._send = a.transport, this._get = a.limiter ? a.limiter(this._get) : this._get, this._cache = a.cache === !1 ? new e(0) : h } var d = 0, f = {}, g = 6, h = new e(10); return c.setMaxPendingRequests = function (a) { g = a }, c.resetCache = function () { h.reset() }, b.mixin(c.prototype, { _fingerprint: function (b) { return b = b || {}, b.url + b.type + a.param(b.data || {}) }, _get: function (a, b) { function c(a) { b(null, a), k._cache.set(i, a) } function e() { b(!0) } function h() { d--, delete f[i], k.onDeckRequestArgs && (k._get.apply(k, k.onDeckRequestArgs), k.onDeckRequestArgs = null) } var i, j, k = this; i = this._fingerprint(a), this.cancelled || i !== this.lastReq || ((j = f[i]) ? j.done(c).fail(e) : g > d ? (d++, f[i] = this._send(a).done(c).fail(e).always(h)) : this.onDeckRequestArgs = [].slice.call(arguments, 0)) }, get: function (c, d) { var e, f; d = d || a.noop, c = b.isString(c) ? { url: c } : c || {}, f = this._fingerprint(c), this.cancelled = !1, this.lastReq = f, (e = this._cache.get(f)) ? d(null, e) : this._get(c, d) }, cancel: function () { this.cancelled = !0 } }), c }(), h = window.SearchIndex = function () { "use strict"; function c(c) { c = c || {}, c.datumTokenizer && c.queryTokenizer || a.error("datumTokenizer and queryTokenizer are both required"), this.identify = c.identify || b.stringify, this.datumTokenizer = c.datumTokenizer, this.queryTokenizer = c.queryTokenizer, this.reset() } function d(a) { return a = b.filter(a, function (a) { return !!a }), a = b.map(a, function (a) { return a.toLowerCase() }) } function e() { var a = {}; return a[i] = [], a[h] = {}, a } function f(a) { for (var b = {}, c = [], d = 0, e = a.length; e > d; d++)b[a[d]] || (b[a[d]] = !0, c.push(a[d])); return c } function g(a, b) { var c = 0, d = 0, e = []; a = a.sort(), b = b.sort(); for (var f = a.length, g = b.length; f > c && g > d;)a[c] < b[d] ? c++ : a[c] > b[d] ? d++ : (e.push(a[c]), c++, d++); return e } var h = "c", i = "i"; return b.mixin(c.prototype, { bootstrap: function (a) { this.datums = a.datums, this.trie = a.trie }, add: function (a) { var c = this; a = b.isArray(a) ? a : [a], b.each(a, function (a) { var f, g; c.datums[f = c.identify(a)] = a, g = d(c.datumTokenizer(a)), b.each(g, function (a) { var b, d, g; for (b = c.trie, d = a.split(""); g = d.shift();)b = b[h][g] || (b[h][g] = e()), b[i].push(f) }) }) }, get: function (a) { var c = this; return b.map(a, function (a) { return c.datums[a] }) }, search: function (a) { var c, e, j = this; return c = d(this.queryTokenizer(a)), b.each(c, function (a) { var b, c, d, f; if (e && 0 === e.length) return !1; for (b = j.trie, c = a.split(""); b && (d = c.shift());)b = b[h][d]; return b && 0 === c.length ? (f = b[i].slice(0), void (e = e ? g(e, f) : f)) : (e = [], !1) }), e ? b.map(f(e), function (a) { return j.datums[a] }) : [] }, all: function () { var a = []; for (var b in this.datums) a.push(this.datums[b]); return a }, reset: function () { this.datums = {}, this.trie = e() }, serialize: function () { return { datums: this.datums, trie: this.trie } } }), c }(), i = function () { "use strict"; function a(a) { this.url = a.url, this.ttl = a.ttl, this.cache = a.cache, this.prepare = a.prepare, this.transform = a.transform, this.transport = a.transport, this.thumbprint = a.thumbprint, this.storage = new f(a.cacheKey) } var c; return c = { data: "data", protocol: "protocol", thumbprint: "thumbprint" }, b.mixin(a.prototype, { _settings: function () { return { url: this.url, type: "GET", dataType: "json" } }, store: function (a) { this.cache && (this.storage.set(c.data, a, this.ttl), this.storage.set(c.protocol, location.protocol, this.ttl), this.storage.set(c.thumbprint, this.thumbprint, this.ttl)) }, fromCache: function () { var a, b = {}; return this.cache ? (b.data = this.storage.get(c.data), b.protocol = this.storage.get(c.protocol), b.thumbprint = this.storage.get(c.thumbprint), a = b.thumbprint !== this.thumbprint || b.protocol !== location.protocol, b.data && !a ? b.data : null) : null }, fromNetwork: function (a) { function b() { a(!0) } function c(b) { a(null, e.transform(b)) } var d, e = this; a && (d = this.prepare(this._settings()), this.transport(d).fail(b).done(c)) }, clear: function () { return this.storage.clear(), this } }), a }(), j = function () { "use strict"; function a(a) { this.url = a.url, this.prepare = a.prepare, this.transform = a.transform, this.transport = new g({ cache: a.cache, limiter: a.limiter, transport: a.transport }) } return b.mixin(a.prototype, { _settings: function () { return { url: this.url, type: "GET", dataType: "json" } }, get: function (a, b) { function c(a, c) { b(a ? [] : e.transform(c)) } var d, e = this; if (b) return a = a || "", d = this.prepare(a, this._settings()), this.transport.get(d, c) }, cancelLastRequest: function () { this.transport.cancel() } }), a }(), k = function () { "use strict"; function d(d) { var e; return d ? (e = { url: null, ttl: 864e5, cache: !0, cacheKey: null, thumbprint: "", prepare: b.identity, transform: b.identity, transport: null }, d = b.isString(d) ? { url: d } : d, d = b.mixin(e, d), !d.url && a.error("prefetch requires url to be set"), d.transform = d.filter || d.transform, d.cacheKey = d.cacheKey || d.url, d.thumbprint = c + d.thumbprint, d.transport = d.transport ? h(d.transport) : a.ajax, d) : null } function e(c) { var d; if (c) return d = { url: null, cache: !0, prepare: null, replace: null, wildcard: null, limiter: null, rateLimitBy: "debounce", rateLimitWait: 300, transform: b.identity, transport: null }, c = b.isString(c) ? { url: c } : c, c = b.mixin(d, c), !c.url && a.error("remote requires url to be set"), c.transform = c.filter || c.transform, c.prepare = f(c), c.limiter = g(c), c.transport = c.transport ? h(c.transport) : a.ajax, delete c.replace, delete c.wildcard, delete c.rateLimitBy, delete c.rateLimitWait, c } function f(a) { function b(a, b) { return b.url = f(b.url, a), b } function c(a, b) { return b.url = b.url.replace(g, encodeURIComponent(a)), b } function d(a, b) { return b } var e, f, g; return e = a.prepare, f = a.replace, g = a.wildcard, e ? e : e = f ? b : a.wildcard ? c : d } function g(a) { function c(a) { return function (c) { return b.debounce(c, a) } } function d(a) { return function (c) { return b.throttle(c, a) } } var e, f, g; return e = a.limiter, f = a.rateLimitBy, g = a.rateLimitWait, e || (e = /^throttle$/i.test(f) ? d(g) : c(g)), e } function h(c) { return function (d) { function e(a) { b.defer(function () { g.resolve(a) }) } function f(a) { b.defer(function () { g.reject(a) }) } var g = a.Deferred(); return c(d, e, f), g } } return function (c) { var f, g; return f = { initialize: !0, identify: b.stringify, datumTokenizer: null, queryTokenizer: null, sufficient: 5, sorter: null, local: [], prefetch: null, remote: null }, c = b.mixin(f, c || {}), !c.datumTokenizer && a.error("datumTokenizer is required"), !c.queryTokenizer && a.error("queryTokenizer is required"), g = c.sorter, c.sorter = g ? function (a) { return a.sort(g) } : b.identity, c.local = b.isFunction(c.local) ? c.local() : c.local, c.prefetch = d(c.prefetch), c.remote = e(c.remote), c } }(), l = function () { "use strict"; function c(a) { a = k(a), this.sorter = a.sorter, this.identify = a.identify, this.sufficient = a.sufficient, this.local = a.local, this.remote = a.remote ? new j(a.remote) : null, this.prefetch = a.prefetch ? new i(a.prefetch) : null, this.index = new h({ identify: this.identify, datumTokenizer: a.datumTokenizer, queryTokenizer: a.queryTokenizer }), a.initialize !== !1 && this.initialize() } var e; return e = window && window.Bloodhound, c.noConflict = function () { return window && (window.Bloodhound = e), c }, c.tokenizers = d, b.mixin(c.prototype, { __ttAdapter: function () { function a(a, b, d) { return c.search(a, b, d) } function b(a, b) { return c.search(a, b) } var c = this; return this.remote ? a : b }, _loadPrefetch: function () { function b(a, b) { return a ? c.reject() : (e.add(b), e.prefetch.store(e.index.serialize()), void c.resolve()) } var c, d, e = this; return c = a.Deferred(), this.prefetch ? (d = this.prefetch.fromCache()) ? (this.index.bootstrap(d), c.resolve()) : this.prefetch.fromNetwork(b) : c.resolve(), c.promise() }, _initialize: function () { function a() { b.add(b.local) } var b = this; return this.clear(), (this.initPromise = this._loadPrefetch()).done(a), this.initPromise }, initialize: function (a) { return !this.initPromise || a ? this._initialize() : this.initPromise }, add: function (a) { return this.index.add(a), this }, get: function (a) { return a = b.isArray(a) ? a : [].slice.call(arguments), this.index.get(a) }, search: function (a, c, d) { function e(a) { var c = []; b.each(a, function (a) { !b.some(f, function (b) { return g.identify(a) === g.identify(b) }) && c.push(a) }), d && d(c) } var f, g = this; return f = this.sorter(this.index.search(a)), c(this.remote ? f.slice() : f), this.remote && f.length < this.sufficient ? this.remote.get(a, e) : this.remote && this.remote.cancelLastRequest(), this }, all: function () { return this.index.all() }, clear: function () { return this.index.reset(), this }, clearPrefetchCache: function () { return this.prefetch && this.prefetch.clear(), this }, clearRemoteCache: function () { return g.resetCache(), this }, ttAdapter: function () { return this.__ttAdapter() } }), c }(); return l }), function (a, b) { "function" == typeof define && define.amd ? define("typeahead.js", ["jquery"], function (a) { return b(a) }) : "object" == typeof exports ? module.exports = b(require("jquery")) : b(jQuery) }(this, function (a) {
	var b = function () { "use strict"; return { isMsie: function () { return /(msie|trident)/i.test(navigator.userAgent) ? navigator.userAgent.match(/(msie |rv:)(\d+(.\d+)?)/i)[2] : !1 }, isBlankString: function (a) { return !a || /^\s*$/.test(a) }, escapeRegExChars: function (a) { return a.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&") }, isString: function (a) { return "string" == typeof a }, isNumber: function (a) { return "number" == typeof a }, isArray: a.isArray, isFunction: a.isFunction, isObject: a.isPlainObject, isUndefined: function (a) { return "undefined" == typeof a }, isElement: function (a) { return !(!a || 1 !== a.nodeType) }, isJQuery: function (b) { return b instanceof a }, toStr: function (a) { return b.isUndefined(a) || null === a ? "" : a + "" }, bind: a.proxy, each: function (b, c) { function d(a, b) { return c(b, a) } a.each(b, d) }, map: a.map, filter: a.grep, every: function (b, c) { var d = !0; return b ? (a.each(b, function (a, e) { return (d = c.call(null, e, a, b)) ? void 0 : !1 }), !!d) : d }, some: function (b, c) { var d = !1; return b ? (a.each(b, function (a, e) { return (d = c.call(null, e, a, b)) ? !1 : void 0 }), !!d) : d }, mixin: a.extend, identity: function (a) { return a }, clone: function (b) { return a.extend(!0, {}, b) }, getIdGenerator: function () { var a = 0; return function () { return a++ } }, templatify: function (b) { function c() { return String(b) } return a.isFunction(b) ? b : c }, defer: function (a) { setTimeout(a, 0) }, debounce: function (a, b, c) { var d, e; return function () { var f, g, h = this, i = arguments; return f = function () { d = null, c || (e = a.apply(h, i)) }, g = c && !d, clearTimeout(d), d = setTimeout(f, b), g && (e = a.apply(h, i)), e } }, throttle: function (a, b) { var c, d, e, f, g, h; return g = 0, h = function () { g = new Date, e = null, f = a.apply(c, d) }, function () { var i = new Date, j = b - (i - g); return c = this, d = arguments, 0 >= j ? (clearTimeout(e), e = null, g = i, f = a.apply(c, d)) : e || (e = setTimeout(h, j)), f } }, stringify: function (a) { return b.isString(a) ? a : JSON.stringify(a) }, noop: function () { } } }(), c = function () { "use strict"; function a(a) { var g, h; return h = b.mixin({}, f, a), g = { css: e(), classes: h, html: c(h), selectors: d(h) }, { css: g.css, html: g.html, classes: g.classes, selectors: g.selectors, mixin: function (a) { b.mixin(a, g) } } } function c(a) { return { wrapper: '<span class="' + a.wrapper + '"></span>', menu: '<div class="' + a.menu + '"></div>' } } function d(a) { var c = {}; return b.each(a, function (a, b) { c[b] = "." + a }), c } function e() { var a = { wrapper: { position: "relative", display: "inline-block" }, hint: { position: "absolute", top: "0", left: "0", borderColor: "transparent", boxShadow: "none", opacity: "1" }, input: { position: "relative", verticalAlign: "top", backgroundColor: "transparent" }, inputWithNoHint: { position: "relative", verticalAlign: "top" }, menu: { position: "absolute", top: "100%", left: "0", zIndex: "100", display: "none" }, ltr: { left: "0", right: "auto" }, rtl: { left: "auto", right: " 0" } }; return b.isMsie() && b.mixin(a.input, { backgroundImage: "url(data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7)" }), a } var f = { wrapper: "twitter-typeahead", input: "tt-input", hint: "tt-hint", menu: "tt-menu", dataset: "tt-dataset", suggestion: "tt-suggestion", selectable: "tt-selectable", empty: "tt-empty", open: "tt-open", cursor: "tt-cursor", highlight: "tt-highlight" }; return a }(), d = function () { "use strict"; function c(b) { b && b.el || a.error("EventBus initialized without el"), this.$el = a(b.el) } var d, e; return d = "typeahead:", e = { render: "rendered", cursorchange: "cursorchanged", select: "selected", autocomplete: "autocompleted" }, b.mixin(c.prototype, { _trigger: function (b, c) { var e; return e = a.Event(d + b), (c = c || []).unshift(e), this.$el.trigger.apply(this.$el, c), e }, before: function (a) { var b, c; return b = [].slice.call(arguments, 1), c = this._trigger("before" + a, b), c.isDefaultPrevented() }, trigger: function (a) { var b; this._trigger(a, [].slice.call(arguments, 1)), (b = e[a]) && this._trigger(b, [].slice.call(arguments, 1)) } }), c }(), e = function () { "use strict"; function a(a, b, c, d) { var e; if (!c) return this; for (b = b.split(i), c = d ? h(c, d) : c, this._callbacks = this._callbacks || {}; e = b.shift();)this._callbacks[e] = this._callbacks[e] || { sync: [], async: [] }, this._callbacks[e][a].push(c); return this } function b(b, c, d) { return a.call(this, "async", b, c, d) } function c(b, c, d) { return a.call(this, "sync", b, c, d) } function d(a) { var b; if (!this._callbacks) return this; for (a = a.split(i); b = a.shift();)delete this._callbacks[b]; return this } function e(a) { var b, c, d, e, g; if (!this._callbacks) return this; for (a = a.split(i), d = [].slice.call(arguments, 1); (b = a.shift()) && (c = this._callbacks[b]);)e = f(c.sync, this, [b].concat(d)), g = f(c.async, this, [b].concat(d)), e() && j(g); return this } function f(a, b, c) { function d() { for (var d, e = 0, f = a.length; !d && f > e; e += 1)d = a[e].apply(b, c) === !1; return !d } return d } function g() { var a; return a = window.setImmediate ? function (a) { setImmediate(function () { a() }) } : function (a) { setTimeout(function () { a() }, 0) } } function h(a, b) { return a.bind ? a.bind(b) : function () { a.apply(b, [].slice.call(arguments, 0)) } } var i = /\s+/, j = g(); return { onSync: c, onAsync: b, off: d, trigger: e } }(), f = function (a) { "use strict"; function c(a, c, d) { for (var e, f = [], g = 0, h = a.length; h > g; g++)f.push(b.escapeRegExChars(a[g])); return e = d ? "\\b(" + f.join("|") + ")\\b" : "(" + f.join("|") + ")", c ? new RegExp(e) : new RegExp(e, "i") } var d = { node: null, pattern: null, tagName: "strong", className: null, wordsOnly: !1, caseSensitive: !1 }; return function (e) { function f(b) { var c, d, f; return (c = h.exec(b.data)) && (f = a.createElement(e.tagName), e.className && (f.className = e.className), d = b.splitText(c.index), d.splitText(c[0].length), f.appendChild(d.cloneNode(!0)), b.parentNode.replaceChild(f, d)), !!c } function g(a, b) { for (var c, d = 3, e = 0; e < a.childNodes.length; e++)c = a.childNodes[e], c.nodeType === d ? e += b(c) ? 1 : 0 : g(c, b) } var h; e = b.mixin({}, d, e), e.node && e.pattern && (e.pattern = b.isArray(e.pattern) ? e.pattern : [e.pattern], h = c(e.pattern, e.caseSensitive, e.wordsOnly), g(e.node, f)) } }(window.document), g = function () { "use strict"; function c(c, e) { c = c || {}, c.input || a.error("input is missing"), e.mixin(this), this.$hint = a(c.hint), this.$input = a(c.input), this.query = this.$input.val(), this.queryWhenFocused = this.hasFocus() ? this.query : null, this.$overflowHelper = d(this.$input), this._checkLanguageDirection(), 0 === this.$hint.length && (this.setHint = this.getHint = this.clearHint = this.clearHintIfInvalid = b.noop) } function d(b) { return a('<pre aria-hidden="true"></pre>').css({ position: "absolute", visibility: "hidden", whiteSpace: "pre", fontFamily: b.css("font-family"), fontSize: b.css("font-size"), fontStyle: b.css("font-style"), fontVariant: b.css("font-variant"), fontWeight: b.css("font-weight"), wordSpacing: b.css("word-spacing"), letterSpacing: b.css("letter-spacing"), textIndent: b.css("text-indent"), textRendering: b.css("text-rendering"), textTransform: b.css("text-transform") }).insertAfter(b) } function f(a, b) { return c.normalizeQuery(a) === c.normalizeQuery(b) } function g(a) { return a.altKey || a.ctrlKey || a.metaKey || a.shiftKey } var h; return h = { 9: "tab", 27: "esc", 37: "left", 39: "right", 13: "enter", 38: "up", 40: "down" }, c.normalizeQuery = function (a) { return b.toStr(a).replace(/^\s*/g, "").replace(/\s{2,}/g, " ") }, b.mixin(c.prototype, e, { _onBlur: function () { this.resetInputValue(), this.trigger("blurred") }, _onFocus: function () { this.queryWhenFocused = this.query, this.trigger("focused") }, _onKeydown: function (a) { var b = h[a.which || a.keyCode]; this._managePreventDefault(b, a), b && this._shouldTrigger(b, a) && this.trigger(b + "Keyed", a) }, _onInput: function () { this._setQuery(this.getInputValue()), this.clearHintIfInvalid(), this._checkLanguageDirection() }, _managePreventDefault: function (a, b) { var c; switch (a) { case "up": case "down": c = !g(b); break; default: c = !1 }c && b.preventDefault() }, _shouldTrigger: function (a, b) { var c; switch (a) { case "tab": c = !g(b); break; default: c = !0 }return c }, _checkLanguageDirection: function () { var a = (this.$input.css("direction") || "ltr").toLowerCase(); this.dir !== a && (this.dir = a, this.$hint.attr("dir", a), this.trigger("langDirChanged", a)) }, _setQuery: function (a, b) { var c, d; c = f(a, this.query), d = c ? this.query.length !== a.length : !1, this.query = a, b || c ? !b && d && this.trigger("whitespaceChanged", this.query) : this.trigger("queryChanged", this.query) }, bind: function () { var a, c, d, e, f = this; return a = b.bind(this._onBlur, this), c = b.bind(this._onFocus, this), d = b.bind(this._onKeydown, this), e = b.bind(this._onInput, this), this.$input.on("blur.tt", a).on("focus.tt", c).on("keydown.tt", d), !b.isMsie() || b.isMsie() > 9 ? this.$input.on("input.tt", e) : this.$input.on("keydown.tt keypress.tt cut.tt paste.tt", function (a) { h[a.which || a.keyCode] || b.defer(b.bind(f._onInput, f, a)) }), this }, focus: function () { this.$input.focus() }, blur: function () { this.$input.blur() }, getLangDir: function () { return this.dir }, getQuery: function () { return this.query || "" }, setQuery: function (a, b) { this.setInputValue(a), this._setQuery(a, b) }, hasQueryChangedSinceLastFocus: function () { return this.query !== this.queryWhenFocused }, getInputValue: function () { return this.$input.val() }, setInputValue: function (a) { this.$input.val(a), this.clearHintIfInvalid(), this._checkLanguageDirection() }, resetInputValue: function () { this.setInputValue(this.query) }, getHint: function () { return this.$hint.val() }, setHint: function (a) { this.$hint.val(a) }, clearHint: function () { this.setHint("") }, clearHintIfInvalid: function () { var a, b, c, d; a = this.getInputValue(), b = this.getHint(), c = a !== b && 0 === b.indexOf(a), d = "" !== a && c && !this.hasOverflow(), !d && this.clearHint() }, hasFocus: function () { return this.$input.is(":focus") }, hasOverflow: function () { var a = this.$input.width() - 2; return this.$overflowHelper.text(this.getInputValue()), this.$overflowHelper.width() >= a }, isCursorAtEnd: function () { var a, c, d; return a = this.$input.val().length, c = this.$input[0].selectionStart, b.isNumber(c) ? c === a : document.selection ? (d = document.selection.createRange(), d.moveStart("character", -a), a === d.text.length) : !0 }, destroy: function () { this.$hint.off(".tt"), this.$input.off(".tt"), this.$overflowHelper.remove(), this.$hint = this.$input = this.$overflowHelper = a("<div>") } }), c }(), h = function () { "use strict"; function c(c, e) { c = c || {}, c.templates = c.templates || {}, c.templates.notFound = c.templates.notFound || c.templates.empty, c.source || a.error("missing source"), c.node || a.error("missing node"), c.name && !h(c.name) && a.error("invalid dataset name: " + c.name), e.mixin(this), this.highlight = !!c.highlight, this.name = c.name || j(), this.limit = c.limit || 5, this.displayFn = d(c.display || c.displayKey), this.templates = g(c.templates, this.displayFn), this.source = c.source.__ttAdapter ? c.source.__ttAdapter() : c.source, this.async = b.isUndefined(c.async) ? this.source.length > 2 : !!c.async, this._resetLastSuggestion(), this.$el = a(c.node).addClass(this.classes.dataset).addClass(this.classes.dataset + "-" + this.name) } function d(a) { function c(b) { return b[a] } return a = a || b.stringify, b.isFunction(a) ? a : c } function g(c, d) { function e(b) { return a("<div>").text(d(b)) } return { notFound: c.notFound && b.templatify(c.notFound), pending: c.pending && b.templatify(c.pending), header: c.header && b.templatify(c.header), footer: c.footer && b.templatify(c.footer), suggestion: c.suggestion || e } } function h(a) { return /^[_a-zA-Z0-9-]+$/.test(a) } var i, j; return i = { val: "tt-selectable-display", obj: "tt-selectable-object" }, j = b.getIdGenerator(), c.extractData = function (b) { var c = a(b); return c.data(i.obj) ? { val: c.data(i.val) || "", obj: c.data(i.obj) || null } : null }, b.mixin(c.prototype, e, { _overwrite: function (a, b) { b = b || [], b.length ? this._renderSuggestions(a, b) : this.async && this.templates.pending ? this._renderPending(a) : !this.async && this.templates.notFound ? this._renderNotFound(a) : this._empty(), this.trigger("rendered", this.name, b, !1) }, _append: function (a, b) { b = b || [], b.length && this.$lastSuggestion.length ? this._appendSuggestions(a, b) : b.length ? this._renderSuggestions(a, b) : !this.$lastSuggestion.length && this.templates.notFound && this._renderNotFound(a), this.trigger("rendered", this.name, b, !0) }, _renderSuggestions: function (a, b) { var c; c = this._getSuggestionsFragment(a, b), this.$lastSuggestion = c.children().last(), this.$el.html(c).prepend(this._getHeader(a, b)).append(this._getFooter(a, b)) }, _appendSuggestions: function (a, b) { var c, d; c = this._getSuggestionsFragment(a, b), d = c.children().last(), this.$lastSuggestion.after(c), this.$lastSuggestion = d }, _renderPending: function (a) { var b = this.templates.pending; this._resetLastSuggestion(), b && this.$el.html(b({ query: a, dataset: this.name })) }, _renderNotFound: function (a) { var b = this.templates.notFound; this._resetLastSuggestion(), b && this.$el.html(b({ query: a, dataset: this.name })) }, _empty: function () { this.$el.empty(), this._resetLastSuggestion() }, _getSuggestionsFragment: function (c, d) { var e, g = this; return e = document.createDocumentFragment(), b.each(d, function (b) { var d, f; f = g._injectQuery(c, b), d = a(g.templates.suggestion(f)).data(i.obj, b).data(i.val, g.displayFn(b)).addClass(g.classes.suggestion + " " + g.classes.selectable), e.appendChild(d[0]) }), this.highlight && f({ className: this.classes.highlight, node: e, pattern: c }), a(e) }, _getFooter: function (a, b) { return this.templates.footer ? this.templates.footer({ query: a, suggestions: b, dataset: this.name }) : null }, _getHeader: function (a, b) { return this.templates.header ? this.templates.header({ query: a, suggestions: b, dataset: this.name }) : null }, _resetLastSuggestion: function () { this.$lastSuggestion = a() }, _injectQuery: function (a, c) { return b.isObject(c) ? b.mixin({ _query: a }, c) : c }, update: function (b) { function c(a) { g || (g = !0, a = (a || []).slice(0, e.limit), h = a.length, e._overwrite(b, a), h < e.limit && e.async && e.trigger("asyncRequested", b)) } function d(c) { c = c || [], !f && h < e.limit && (e.cancel = a.noop, h += c.length, e._append(b, c.slice(0, e.limit - h)), e.async && e.trigger("asyncReceived", b)) } var e = this, f = !1, g = !1, h = 0; this.cancel(), this.cancel = function () { f = !0, e.cancel = a.noop, e.async && e.trigger("asyncCanceled", b) }, this.source(b, c, d), !g && c([]) }, cancel: a.noop, clear: function () { this._empty(), this.cancel(), this.trigger("cleared") }, isEmpty: function () { return this.$el.is(":empty") }, destroy: function () { this.$el = a("<div>") } }), c }(), i = function () { "use strict"; function c(c, d) { function e(b) { var c = f.$node.find(b.node).first(); return b.node = c.length ? c : a("<div>").appendTo(f.$node), new h(b, d) } var f = this; c = c || {}, c.node || a.error("node is required"), d.mixin(this), this.$node = a(c.node), this.query = null, this.datasets = b.map(c.datasets, e) } return b.mixin(c.prototype, e, { _onSelectableClick: function (b) { this.trigger("selectableClicked", a(b.currentTarget)) }, _onRendered: function (a, b, c, d) { this.$node.toggleClass(this.classes.empty, this._allDatasetsEmpty()), this.trigger("datasetRendered", b, c, d) }, _onCleared: function () { this.$node.toggleClass(this.classes.empty, this._allDatasetsEmpty()), this.trigger("datasetCleared") }, _propagate: function () { this.trigger.apply(this, arguments) }, _allDatasetsEmpty: function () { function a(a) { return a.isEmpty() } return b.every(this.datasets, a) }, _getSelectables: function () { return this.$node.find(this.selectors.selectable) }, _removeCursor: function () { var a = this.getActiveSelectable(); a && a.removeClass(this.classes.cursor) }, _ensureVisible: function (a) { var b, c, d, e; b = a.position().top, c = b + a.outerHeight(!0), d = this.$node.scrollTop(), e = this.$node.height() + parseInt(this.$node.css("paddingTop"), 10) + parseInt(this.$node.css("paddingBottom"), 10), 0 > b ? this.$node.scrollTop(d + b) : c > e && this.$node.scrollTop(d + (c - e)) }, bind: function () { var a, c = this; return a = b.bind(this._onSelectableClick, this), this.$node.on("click.tt", this.selectors.selectable, a), b.each(this.datasets, function (a) { a.onSync("asyncRequested", c._propagate, c).onSync("asyncCanceled", c._propagate, c).onSync("asyncReceived", c._propagate, c).onSync("rendered", c._onRendered, c).onSync("cleared", c._onCleared, c) }), this }, isOpen: function () { return this.$node.hasClass(this.classes.open) }, open: function () { this.$node.addClass(this.classes.open) }, close: function () { this.$node.removeClass(this.classes.open), this._removeCursor() }, setLanguageDirection: function (a) { this.$node.attr("dir", a) }, selectableRelativeToCursor: function (a) { var b, c, d, e; return c = this.getActiveSelectable(), b = this._getSelectables(), d = c ? b.index(c) : -1, e = d + a, e = (e + 1) % (b.length + 1) - 1, e = -1 > e ? b.length - 1 : e, -1 === e ? null : b.eq(e) }, setCursor: function (a) { this._removeCursor(), (a = a && a.first()) && (a.addClass(this.classes.cursor), this._ensureVisible(a)) }, getSelectableData: function (a) { return a && a.length ? h.extractData(a) : null }, getActiveSelectable: function () { var a = this._getSelectables().filter(this.selectors.cursor).first(); return a.length ? a : null }, getTopSelectable: function () { var a = this._getSelectables().first(); return a.length ? a : null }, update: function (a) { function c(b) { b.update(a) } var d = a !== this.query; return d && (this.query = a, b.each(this.datasets, c)), d }, empty: function () { function a(a) { a.clear() } b.each(this.datasets, a), this.query = null, this.$node.addClass(this.classes.empty) }, destroy: function () { function c(a) { a.destroy() } this.$node.off(".tt"), this.$node = a("<div>"), b.each(this.datasets, c) } }), c }(), j = function () { "use strict"; function a() { i.apply(this, [].slice.call(arguments, 0)) } var c = i.prototype; return b.mixin(a.prototype, i.prototype, { open: function () { return !this._allDatasetsEmpty() && this._show(), c.open.apply(this, [].slice.call(arguments, 0)) }, close: function () { return this._hide(), c.close.apply(this, [].slice.call(arguments, 0)) }, _onRendered: function () { return this._allDatasetsEmpty() ? this._hide() : this.isOpen() && this._show(), c._onRendered.apply(this, [].slice.call(arguments, 0)) }, _onCleared: function () { return this._allDatasetsEmpty() ? this._hide() : this.isOpen() && this._show(), c._onCleared.apply(this, [].slice.call(arguments, 0)) }, setLanguageDirection: function (a) { return this.$node.css("ltr" === a ? this.css.ltr : this.css.rtl), c.setLanguageDirection.apply(this, [].slice.call(arguments, 0)) }, _hide: function () { this.$node.hide() }, _show: function () { this.$node.css("display", "block") } }), a }(), k = function () {
		"use strict"; function c(c, e) { var f, g, h, i, j, k, l, m, n, o, p; c = c || {}, c.input || a.error("missing input"), c.menu || a.error("missing menu"), c.eventBus || a.error("missing event bus"), e.mixin(this), this.eventBus = c.eventBus, this.minLength = b.isNumber(c.minLength) ? c.minLength : 1, this.input = c.input, this.menu = c.menu, this.enabled = !0, this.active = !1, this.input.hasFocus() && this.activate(), this.dir = this.input.getLangDir(), this._hacks(), this.menu.bind().onSync("selectableClicked", this._onSelectableClicked, this).onSync("asyncRequested", this._onAsyncRequested, this).onSync("asyncCanceled", this._onAsyncCanceled, this).onSync("asyncReceived", this._onAsyncReceived, this).onSync("datasetRendered", this._onDatasetRendered, this).onSync("datasetCleared", this._onDatasetCleared, this), f = d(this, "activate", "open", "_onFocused"), g = d(this, "deactivate", "_onBlurred"), h = d(this, "isActive", "isOpen", "_onEnterKeyed"), i = d(this, "isActive", "isOpen", "_onTabKeyed"), j = d(this, "isActive", "_onEscKeyed"), k = d(this, "isActive", "open", "_onUpKeyed"), l = d(this, "isActive", "open", "_onDownKeyed"), m = d(this, "isActive", "isOpen", "_onLeftKeyed"), n = d(this, "isActive", "isOpen", "_onRightKeyed"), o = d(this, "_openIfActive", "_onQueryChanged"), p = d(this, "_openIfActive", "_onWhitespaceChanged"), this.input.bind().onSync("focused", f, this).onSync("blurred", g, this).onSync("enterKeyed", h, this).onSync("tabKeyed", i, this).onSync("escKeyed", j, this).onSync("upKeyed", k, this).onSync("downKeyed", l, this).onSync("leftKeyed", m, this).onSync("rightKeyed", n, this).onSync("queryChanged", o, this).onSync("whitespaceChanged", p, this).onSync("langDirChanged", this._onLangDirChanged, this) } function d(a) { var c = [].slice.call(arguments, 1); return function () { var d = [].slice.call(arguments); b.each(c, function (b) { return a[b].apply(a, d) }) } } return b.mixin(c.prototype, {
			_hacks: function () {
				var c, d; c = this.input.$input || a("<div>"), d = this.menu.$node || a("<div>"), c.on("blur.tt", function (a) {
					var e, f, g;
					e = document.activeElement, f = d.is(e), g = d.has(e).length > 0, b.isMsie() && (f || g) && (a.preventDefault(), a.stopImmediatePropagation(), b.defer(function () { c.focus() }))
				}), d.on("mousedown.tt", function (a) { a.preventDefault() })
			}, _onSelectableClicked: function (a, b) { this.select(b) }, _onDatasetCleared: function () { this._updateHint() }, _onDatasetRendered: function (a, b, c, d) { this._updateHint(), this.eventBus.trigger("render", c, d, b) }, _onAsyncRequested: function (a, b, c) { this.eventBus.trigger("asyncrequest", c, b) }, _onAsyncCanceled: function (a, b, c) { this.eventBus.trigger("asynccancel", c, b) }, _onAsyncReceived: function (a, b, c) { this.eventBus.trigger("asyncreceive", c, b) }, _onFocused: function () { this._minLengthMet() && this.menu.update(this.input.getQuery()) }, _onBlurred: function () { this.input.hasQueryChangedSinceLastFocus() && this.eventBus.trigger("change", this.input.getQuery()) }, _onEnterKeyed: function (a, b) { var c; (c = this.menu.getActiveSelectable()) && this.select(c) && b.preventDefault() }, _onTabKeyed: function (a, b) { var c; (c = this.menu.getActiveSelectable()) ? this.select(c) && b.preventDefault() : (c = this.menu.getTopSelectable()) && this.autocomplete(c) && b.preventDefault() }, _onEscKeyed: function () { this.close() }, _onUpKeyed: function () { this.moveCursor(-1) }, _onDownKeyed: function () { this.moveCursor(1) }, _onLeftKeyed: function () { "rtl" === this.dir && this.input.isCursorAtEnd() && this.autocomplete(this.menu.getTopSelectable()) }, _onRightKeyed: function () { "ltr" === this.dir && this.input.isCursorAtEnd() && this.autocomplete(this.menu.getTopSelectable()) }, _onQueryChanged: function (a, b) { this._minLengthMet(b) ? this.menu.update(b) : this.menu.empty() }, _onWhitespaceChanged: function () { this._updateHint() }, _onLangDirChanged: function (a, b) { this.dir !== b && (this.dir = b, this.menu.setLanguageDirection(b)) }, _openIfActive: function () { this.isActive() && this.open() }, _minLengthMet: function (a) { return a = b.isString(a) ? a : this.input.getQuery() || "", a.length >= this.minLength }, _updateHint: function () { var a, c, d, e, f, h, i; a = this.menu.getTopSelectable(), c = this.menu.getSelectableData(a), d = this.input.getInputValue(), !c || b.isBlankString(d) || this.input.hasOverflow() ? this.input.clearHint() : (e = g.normalizeQuery(d), f = b.escapeRegExChars(e), h = new RegExp("^(?:" + f + ")(.+$)", "i"), i = h.exec(c.val), i && this.input.setHint(d + i[1])) }, isEnabled: function () { return this.enabled }, enable: function () { this.enabled = !0 }, disable: function () { this.enabled = !1 }, isActive: function () { return this.active }, activate: function () { return this.isActive() ? !0 : !this.isEnabled() || this.eventBus.before("active") ? !1 : (this.active = !0, this.eventBus.trigger("active"), !0) }, deactivate: function () { return this.isActive() ? this.eventBus.before("idle") ? !1 : (this.active = !1, this.close(), this.eventBus.trigger("idle"), !0) : !0 }, isOpen: function () { return this.menu.isOpen() }, open: function () { return this.isOpen() || this.eventBus.before("open") || (this.menu.open(), this._updateHint(), this.eventBus.trigger("open")), this.isOpen() }, close: function () { return this.isOpen() && !this.eventBus.before("close") && (this.menu.close(), this.input.clearHint(), this.input.resetInputValue(), this.eventBus.trigger("close")), !this.isOpen() }, setVal: function (a) { this.input.setQuery(b.toStr(a)) }, getVal: function () { return this.input.getQuery() }, select: function (a) { var b = this.menu.getSelectableData(a); return b && !this.eventBus.before("select", b.obj) ? (this.input.setQuery(b.val, !0), this.eventBus.trigger("select", b.obj), this.close(), !0) : !1 }, autocomplete: function (a) { var b, c, d; return b = this.input.getQuery(), c = this.menu.getSelectableData(a), d = c && b !== c.val, d && !this.eventBus.before("autocomplete", c.obj) ? (this.input.setQuery(c.val), this.eventBus.trigger("autocomplete", c.obj), !0) : !1 }, moveCursor: function (a) { var b, c, d, e, f; return b = this.input.getQuery(), c = this.menu.selectableRelativeToCursor(a), d = this.menu.getSelectableData(c), e = d ? d.obj : null, f = this._minLengthMet() && this.menu.update(b), f || this.eventBus.before("cursorchange", e) ? !1 : (this.menu.setCursor(c), d ? this.input.setInputValue(d.val) : (this.input.resetInputValue(), this._updateHint()), this.eventBus.trigger("cursorchange", e), !0) }, destroy: function () { this.input.destroy(), this.menu.destroy() }
		}), c
	}(); !function () { "use strict"; function e(b, c) { b.each(function () { var b, d = a(this); (b = d.data(p.typeahead)) && c(b, d) }) } function f(a, b) { return a.clone().addClass(b.classes.hint).removeData().css(b.css.hint).css(l(a)).prop("readonly", !0).removeAttr("id name placeholder required").attr({ autocomplete: "off", spellcheck: "false", tabindex: -1 }) } function h(a, b) { a.data(p.attrs, { dir: a.attr("dir"), autocomplete: a.attr("autocomplete"), spellcheck: a.attr("spellcheck"), style: a.attr("style") }), a.addClass(b.classes.input).attr({ autocomplete: "off", spellcheck: !1 }); try { !a.attr("dir") && a.attr("dir", "auto") } catch (c) { } return a } function l(a) { return { backgroundAttachment: a.css("background-attachment"), backgroundClip: a.css("background-clip"), backgroundColor: a.css("background-color"), backgroundImage: a.css("background-image"), backgroundOrigin: a.css("background-origin"), backgroundPosition: a.css("background-position"), backgroundRepeat: a.css("background-repeat"), backgroundSize: a.css("background-size") } } function m(a) { var c, d; c = a.data(p.www), d = a.parent().filter(c.selectors.wrapper), b.each(a.data(p.attrs), function (c, d) { b.isUndefined(c) ? a.removeAttr(d) : a.attr(d, c) }), a.removeData(p.typeahead).removeData(p.www).removeData(p.attr).removeClass(c.classes.input), d.length && (a.detach().insertAfter(d), d.remove()) } function n(c) { var d, e; return d = b.isJQuery(c) || b.isElement(c), e = d ? a(c).first() : [], e.length ? e : null } var o, p, q; o = a.fn.typeahead, p = { www: "tt-www", attrs: "tt-attrs", typeahead: "tt-typeahead" }, q = { initialize: function (e, l) { function m() { var c, m, q, r, s, t, u, v, w, x, y; b.each(l, function (a) { a.highlight = !!e.highlight }), c = a(this), m = a(o.html.wrapper), q = n(e.hint), r = n(e.menu), s = e.hint !== !1 && !q, t = e.menu !== !1 && !r, s && (q = f(c, o)), t && (r = a(o.html.menu).css(o.css.menu)), q && q.val(""), c = h(c, o), (s || t) && (m.css(o.css.wrapper), c.css(s ? o.css.input : o.css.inputWithNoHint), c.wrap(m).parent().prepend(s ? q : null).append(t ? r : null)), y = t ? j : i, u = new d({ el: c }), v = new g({ hint: q, input: c }, o), w = new y({ node: r, datasets: l }, o), x = new k({ input: v, menu: w, eventBus: u, minLength: e.minLength }, o), c.data(p.www, o), c.data(p.typeahead, x) } var o; return l = b.isArray(l) ? l : [].slice.call(arguments, 1), e = e || {}, o = c(e.classNames), this.each(m) }, isEnabled: function () { var a; return e(this.first(), function (b) { a = b.isEnabled() }), a }, enable: function () { return e(this, function (a) { a.enable() }), this }, disable: function () { return e(this, function (a) { a.disable() }), this }, isActive: function () { var a; return e(this.first(), function (b) { a = b.isActive() }), a }, activate: function () { return e(this, function (a) { a.activate() }), this }, deactivate: function () { return e(this, function (a) { a.deactivate() }), this }, isOpen: function () { var a; return e(this.first(), function (b) { a = b.isOpen() }), a }, open: function () { return e(this, function (a) { a.open() }), this }, close: function () { return e(this, function (a) { a.close() }), this }, select: function (b) { var c = !1, d = a(b); return e(this.first(), function (a) { c = a.select(d) }), c }, autocomplete: function (b) { var c = !1, d = a(b); return e(this.first(), function (a) { c = a.autocomplete(d) }), c }, moveCursor: function (a) { var b = !1; return e(this.first(), function (c) { b = c.moveCursor(a) }), b }, val: function (a) { var b; return arguments.length ? (e(this, function (b) { b.setVal(a) }), this) : (e(this.first(), function (a) { b = a.getVal() }), b) }, destroy: function () { return e(this, function (a, b) { m(b), a.destroy() }), this } }, a.fn.typeahead = function (a) { return q[a] ? q[a].apply(this, [].slice.call(arguments, 1)) : q.initialize.apply(this, arguments) }, a.fn.typeahead.noConflict = function () { return a.fn.typeahead = o, this } }()
});
'use strict';

(function (angular) {
	// Create all modules and define dependencies to make sure they exist
	// and are loaded in the correct order to satisfy dependency injection
	// before all nested files are concatenated by Gulp

	// Config
	angular.module('ui.carousel.config', []).value('ui.carousel.config', {
		debug: true
	});

	// Modules
	angular.module('ui.carousel.providers', []);
	angular.module('ui.carousel.controllers', []);
	angular.module('ui.carousel.directives', []);
	angular.module('ui.carousel', ['ui.carousel.config', 'ui.carousel.directives', 'ui.carousel.controllers', 'ui.carousel.providers']);
})(angular);
'use strict';

/**
 * angular-ui-carousel
 * for example:
 * length = 8, show = 4, scroll = 3, current = 0
 *          ---------
 *          |       |
 *  |4|5|6|7|0|1|2|3|4|5|6|7|1|2|3|4
 *          |       |
 *          ---------
 * rectangle is visible for users
 */
angular.module('ui.carousel.controllers').controller('CarouselController', ['$scope', '$element', '$timeout', '$q', 'Carousel', '$window', function ($scope, $element, $timeout, $q, Carousel, $window) {
	var _this = this;

	/**
	 * Initial carousel
	 *
	 * Mirgate to angularjs 1.6
	 * @see https://docs.angularjs.org/guide/migration#commit-bcd0d4
	 */
	this.$onInit = function () {
		_this.initOptions();
		_this.initRanges();
		_this.setProps();
		_this.setupInfinite();
	};

	/**
	 * Init option based on directive config
	 */
	this.initOptions = function () {
		_this.options = angular.extend({}, Carousel.getOptions());

		// TODO customize attribute from directive
		if (_this.initialSlide !== undefined) {
			_this.options.initialSlide = _this.initialSlide;
		}
		if (_this.fade !== undefined) {
			_this.options.fade = _this.fade;
		}
		if (_this.autoplay !== undefined) {
			_this.options.autoplay = _this.autoplay;
		}
		if (_this.autoplaySpeed !== undefined) {
			_this.options.autoplaySpeed = _this.autoplaySpeed;
		}
		if (_this.cssEase !== undefined) {
			_this.options.cssEase = _this.cssEase;
		}
		if (_this.speed !== undefined) {
			_this.options.speed = _this.speed;
		}
		if (_this.infinite !== undefined) {
			_this.options.infinite = _this.infinite;
		}
		if (_this.arrows !== undefined) {
			_this.options.arrows = _this.arrows;
		}
		if (_this.dots !== undefined) {
			_this.options.dots = _this.dots;
		}
		if (_this.visiblePrev !== undefined) {
			_this.options.visiblePrev = _this.visiblePrev;
		}
		if (_this.visibleNext !== undefined) {
			_this.options.visibleNext = _this.visibleNext;
		}

		// TODO write more options for fade mode
		// In fade mode we have to setting slides-to-show and slides-to-scroll
		// to 1 slide
		if (_this.options.fade) {
			_this.options.slidesToShow = 1;
			_this.options.slidesToScroll = 1;
		} else {
			if (_this.show) {
				_this.options.slidesToShow = _this.show;
			}
			if (_this.scroll) {
				_this.options.slidesToScroll = _this.scroll;
			}
		}
	};

	/**
	 * init variables, slides, ..
	 */
	this.initRanges = function () {
		if (!_this.slides) {
			_this.slides = [];
		}

		_this.isCarouselReady = false;
		_this.isTrackMoving = false;
		_this.track = $element.find('.track');
		_this.width = 1; // Fake width
		_this.currentSlide = _this.options.initialSlide;
		_this.trackStyle = {};
		_this.slideStyle = {};

		_this.isVisibleDots = false;
		_this.isVisiblePrev = _this.options.visiblePrev;
		_this.isVisibleNext = _this.options.visibleNext;

		_this.isClickablePrev = false;
		_this.isClickableNext = false;

		_this.animType = null;
		_this.transformType = null;
		_this.transitionType = null;
	};

	/**
	 * Init UI and carousel track
	 */
	this.initUI = function () {
		_this.width = $element[0].clientWidth;

		// Update track width first
		_this.initTrack();

		// Then item style
		$timeout(function () {
			_this.updateItemStyle();
		}, 200);
	};

	/**
	 * update common style for each carousel item
	 */
	this.updateItemStyle = function () {
		_this.itemWidth = _this.width / _this.options.slidesToShow;
		_this.slideStyle = {
			'width': _this.itemWidth + 'px'
		};
	};

	/**
	 * init carousel track
	 * also make Carousel is Ready
	 */
	this.initTrack = function () {
		var itemWidth = _this.width / _this.options.slidesToShow;
		var trackWidth = itemWidth * _this.slidesInTrack.length;

		_this.trackStyle.width = trackWidth + 'px';

		_this.slideHandler(_this.currentSlide).finally(function () {
			_this.isCarouselReady = true;

			if (!_this.options.fade) {
				_this.refreshTrackStyle();
			}

			// onInit callback
			if (_this.onInit) {
				_this.onInit();
			}
		}).catch(function () {
			// Catch err
		});
	};

	/**
	 * @see https://github.com/kenwheeler/slick/blob/master/slick/slick.js#L680
	 *
	 * Sync slide to place it should be
	 * for example:
	 * - 9 total, 3 show, 3 scroll, current 1
	 *   => next index = 3 (previous index counted = 0)
	 *
	 * and scroll to next page:
	 * - 6 total, 1 show, 1 scroll, current 0 => next index = 1
	 * - 9 total, 3 show, 3 scroll, current 1 => next index = 3
	 * - 9 total, 3 show, 3 scroll, current 3 => next index = 6
	 * - 9 total, 3 show, 3 scroll, current 8 => next index = 3
	 * - 8 total, 4 show, 3 scroll, current 1 => next index = 4
	 */
	this.next = function () {
		if (!_this.isClickableNext) {
			return false;
		}

		var indexOffset = _this.getIndexOffset();
		var slideOffset = indexOffset === 0 ? _this.options.slidesToScroll : indexOffset;

		_this.slideHandler(_this.currentSlide + slideOffset).catch(function () {
			// Catch err
		});
	};

	/**
	 * move to previous slide
	 * same calculate with next
	 * @see next function
	 */
	this.prev = function () {
		if (!_this.isClickablePrev) {
			return false;
		}

		var indexOffset = _this.getIndexOffset();
		var slideOffset = indexOffset === 0 ? _this.options.slidesToScroll : _this.options.slidesToShow - indexOffset;

		_this.slideHandler(_this.currentSlide - slideOffset).catch(function () {
			// Catch err
		});
	};

	/**
	 * Get index offset
	 */
	this.getIndexOffset = function () {
		var scrollOffset = _this.slides.length % _this.options.slidesToScroll !== 0;
		var indexOffset = scrollOffset ? 0 : (_this.slides.length - _this.currentSlide) % _this.options.slidesToScroll;

		return indexOffset;
	};

	/**
	 * move to page
	 * @params int page
	 * Page counter from 0 (start = 0)
	 */
	this.movePage = function (page) {
		var target = _this.options.slidesToScroll * page;
		_this.slideHandler(target).catch(function () {
			// Catch err
		});
	};

	/**
	 * hanlder carousel
	 * @description move carousel to correct page
	 *
	 * @params int index
	 */
	this.slideHandler = function (index) {
		// TODO prevent when slides not exists
		if (!_this.slides) {
			return $q.reject('Carousel not fully setup');
		}

		// TODO Prevent when track is moving
		if (_this.isTrackMoving) {
			return $q.reject('Track is moving');
		}

		var len = _this.slides.length;
		var show = _this.options.slidesToShow;

		if (len <= show) {
			_this.correctTrack();
			return $q.reject('Length of slides smaller than slides to show');
		}

		// We need target to destination
		// and a anim slide to translate track
		//
		// anim = animSlide (which we use to move)
		// target = targetSlide
		var anim = index;
		var target = null;

		if (anim < 0) {
			if (len % _this.options.slidesToScroll !== 0) {
				target = len - len % _this.options.slidesToScroll;
			} else {
				target = len + anim;
			}
		} else if (anim >= len) {
			if (len % _this.options.slidesToScroll !== 0) {
				target = 0;
			} else {
				target = anim - len;
			}
		} else {
			target = anim;
		}

		if (_this.onBeforeChange) {
			// @see https://docs.angularjs.org/guide/directive
			_this.onBeforeChange({ currentSlide: _this.currentSlide, target: target });
		}

		// Fade handler
		if (_this.options.fade) {
			_this.currentSlide = target;

			// XXX
			// afterChange method
			// fire after faded
			// Should be revised
			$timeout(function () {
				_this.autoplayTrack();

				if (_this.onAfterChange) {
					_this.onAfterChange({ currentSlide: _this.currentSlide });
				}
			}, _this.options.speed);
			return $q.when('Handler fade');
		}

		// No-fade handler
		var itemWidth = _this.width / _this.options.slidesToShow;
		var left = -1 * target * itemWidth;
		if (_this.options.infinite) {
			left = -1 * (anim + show) * itemWidth;
		}

		_this.isTrackMoving = true;
		return _this.moveTrack(left).then(function () {
			_this.isTrackMoving = false;
			_this.currentSlide = target;
			_this.autoplayTrack();

			if (target !== anim) {
				_this.correctTrack();
			}

			if (!_this.options.infinite) {
				if (_this.currentSlide === 0) {
					_this.isClickablePrev = false;
					_this.isClickableNext = true;
				} else if (_this.currentSlide === _this.slidesInTrack.length - _this.options.slidesToShow) {
					_this.isClickableNext = false;
					_this.isClickablePrev = true;
				} else {
					_this.isClickablePrev = true;
					_this.isClickableNext = true;
				}
			}

			// XXX
			// afterChange method
			// fire after 200ms wakeup and correct track
			// Should be revised
			$timeout(function () {
				if (_this.onAfterChange) {
					_this.onAfterChange({ currentSlide: _this.currentSlide });
				}
			}, 200);
		});
	};

	/**
	 * moveTrack
	 * move track to left position using css3 translate
	 * for example left: -1000px
	 */
	this.moveTrack = function (left) {
		var deferred = $q.defer();
		if (_this.options.vertical === false) {
			_this.trackStyle[_this.animType] = 'translate3d(' + left + 'px, 0px, 0px)';
		} else {
			_this.trackStyle[_this.animType] = 'translate3d(0px, ' + left + 'px, 0px)';
		}

		$timeout(function () {
			deferred.resolve('Track moved');
		}, _this.options.speed);

		return deferred.promise;
	};

	/**
	 * correctTrack
	 * @description correct track after move to animSlide we have to move track
	 * to exactly its position
	 */
	this.correctTrack = function () {
		if (_this.options.infinite) {
			(function () {
				var left = 0;
				if (_this.slides.length > _this.options.slidesToShow) {
					left = -1 * (_this.currentSlide + _this.options.slidesToShow) * _this.itemWidth;
				}

				// Move without anim
				_this.trackStyle[_this.transitionType] = _this.transformType + ' ' + 0 + 'ms ' + _this.options.cssEase;

				_this.isTrackMoving = true;
				$timeout(function () {
					_this.trackStyle[_this.animType] = 'translate3d(' + left + 'px, 0, 0px)';

					// Revert animation
					$timeout(function () {
						_this.refreshTrackStyle();
						_this.isTrackMoving = false;
					}, 200);
				});
			})();
		}
	};

	/**
	 * Refresh track style
	 */
	this.refreshTrackStyle = function () {
		_this.trackStyle[_this.transitionType] = _this.transformType + ' ' + _this.options.speed + 'ms ' + _this.options.cssEase;
	};

	/**
	 * autoplay track
	 * @description autoplay = true
	 */
	this.autoplayTrack = function () {
		if (_this.options.autoplay) {
			if (_this.timeout) {
				$timeout.cancel(_this.timeout);
			}

			_this.timeout = $timeout(function () {
				_this.next();

				$timeout.cancel(_this.timeout);
				_this.timeout = null;
			}, _this.options.autoplaySpeed);
		}
	};

	this.getSlideStyle = function (index) {
		var style = _this.slideStyle;
		if (_this.options.fade) {
			var left = -1 * index * _this.itemWidth;
			var uniqueStyle = {
				position: 'relative',
				top: '0px',
				left: left + 'px',
				'z-index': index === _this.currentSlide ? 10 : 9,
				opacity: index === _this.currentSlide ? 1 : 0
			};

			if (index >= _this.currentSlide - 1 && index <= _this.currentSlide + 1) {
				uniqueStyle.transition = 'opacity 250ms linear';
			}

			style = angular.extend(style, uniqueStyle);
		}

		return style;
	};

	/**
	 * setupInfinite
	 * To make carouse infinite we need close number of slidesToShow elements to
	 * previous elements and to after elements
	 *
	 * length = 8, show = 4, scroll = 3, current = 0
	 *          ---------
	 *          |       |
	 *  |4|5|6|7|0|1|2|3|4|5|6|7|1|2|3|4
	 *          |       |
	 *          ---------
	 */
	this.setupInfinite = function () {
		// Clone
		var len = _this.slides.length;
		var show = _this.options.slidesToShow;

		var tmpTrack = angular.copy(_this.slides);

		if (_this.options.infinite && _this.options.fade === false) {
			if (len > show) {
				var number = show;
				for (var i = 0; i < number; i++) {
					tmpTrack.push(angular.copy(_this.slides[i]));
				}
				for (var _i = len - 1; _i >= len - show; _i--) {
					tmpTrack.unshift(angular.copy(_this.slides[_i]));
				}
			}
		}

		_this.slidesInTrack = tmpTrack;
	};

	/**
	 * get number of dosts
	 *
	 * @return Array
	 */
	this.getDots = function () {
		if (!_this.slides) {
			return [];
		}

		var dots = Math.ceil(_this.slides.length / _this.options.slidesToScroll);

		var res = [];
		for (var i = 0; i < dots; i++) {
			res.push(i);
		}
		return res;
	};

	/**
	 * set carousel property
	 *
	 * - animType
	 * - transformType
	 * - transitionType
	 */
	this.setProps = function () {
		var bodyStyle = document.body.style;

		/* eslint-disable */
		if (bodyStyle.OTransform !== undefined) {
			_this.animType = 'OTransform';
			_this.transformType = '-o-transform';
			_this.transitionType = 'OTransition';
		}
		if (bodyStyle.MozTransform !== undefined) {
			_this.animType = 'MozTransform';
			_this.transformType = '-moz-transform';
			_this.transitionType = 'MozTransition';
		}
		if (bodyStyle.webkitTransform !== undefined) {
			_this.animType = 'webkitTransform';
			_this.transformType = '-webkit-transform';
			_this.transitionType = 'webkitTransition';
		}
		if (bodyStyle.msTransform !== undefined) {
			_this.animType = 'msTransform';
			_this.transformType = '-ms-transform';
			_this.transitionType = 'msTransition';
		}
		if (bodyStyle.transform !== undefined && _this.animType !== false) {
			_this.animType = 'transform';
			_this.transformType = 'transform';
			_this.transitionType = 'transition';
		}
		/* eslint-enable */

		_this.transformsEnabled = true;
	};

	/**
	 * Refresh carousel
	 */
	this.refreshCarousel = function () {
		if (_this.slides && _this.slides.length && _this.slides.length > _this.options.slidesToShow) {
			_this.isVisibleDots = true;
			_this.isVisiblePrev = true;
			_this.isVisibleNext = true;
			_this.isClickablePrev = true;
			_this.isClickableNext = true;
		} else {
			_this.isVisibleDots = false;
			_this.isVisiblePrev = _this.options.visiblePrev || false;
			_this.isVisibleNext = _this.options.visibleNext || false;
			_this.isClickablePrev = false;
			_this.isClickableNext = false;
		}

		// Re-init UI
		_this.initUI();
	};

	/**
	 * refresh model
	 */
	$scope.$watchCollection('ctrl.slides', function (slides) {
		if (!slides) {
			return;
		}

		// Init carousel
		if (_this.currentSlide > slides.length - 1) {
			_this.currentSlide = slides.length - 1;
		}

		_this.setupInfinite();
		_this.refreshCarousel();
	});

	/**
	 * update when resize
	 *
	 * @see https://github.com/mihnsen/ui-carousel/issues/10
	 * @author tarkant
	 */
	angular.element($window).on('resize', this.refreshCarousel);

	/**
	 * cleanup when done
	 *
	 * @see https://github.com/mihnsen/ui-carousel/issues/10
	 * @author tarkant
	 */
	$scope.$on('$destroy', function () {
		angular.element($window).off('resize');
	});

	// Prior to v1.5, we need to call `$onInit()` manually.
	// (Bindings will always be pre-assigned in these versions.)
	if (angular.version.major === 1 && angular.version.minor < 5) {
		this.$onInit();
	}
}]);
'use strict';

angular.module('ui.carousel.directives').directive('uiCarousel', ['$compile', '$templateCache', '$sce', function ($compile, $templateCache, $sce) {

	return {
		restrict: 'AE',
		bindToController: true,
		scope: {
			name: '=?',
			slides: '=',
			show: '=?slidesToShow',
			scroll: '=?slidesToScroll',
			classes: '@',
			fade: '=?',
			onChange: '=?',
			disableArrow: '=?',
			autoplay: '=?',
			autoplaySpeed: '=?',
			cssEase: '=?',
			speed: '=?',
			infinite: '=?',
			arrows: '=?',
			dots: '=?',
			initialSlide: '=?',
			visibleNext: '=?',
			visiblePrev: '=?',

			// Method
			onBeforeChange: '&',
			onAfterChange: '&',
			onInit: '&'
		},
		link: function link($scope, el) {
			var template = angular.element($templateCache.get('ui-carousel/carousel.template.html'));

			// dynamic injections to override the inner layers' components
			var injectComponentMap = {
				'carousel-item': '.carousel-item',
				'carousel-prev': '.carousel-prev',
				'carousel-next': '.carousel-next'
			};

			var templateInstance = template.clone();
			angular.forEach(injectComponentMap, function (innerSelector, outerSelector) {
				var outerElement = el[0].querySelector(outerSelector);
				if (outerElement) {
					angular.element(templateInstance[0].querySelector(innerSelector)).html(outerElement.innerHTML);
				}
			});

			var compiledElement = $compile(templateInstance)($scope);
			el.addClass('ui-carousel').html('').append(compiledElement);
		},


		controller: 'CarouselController',
		controllerAs: 'ctrl'
	};
}]);
'use strict';

angular.module('ui.carousel.providers').provider('Carousel', function () {
	var _this = this;

	this.options = {
		// Init like Slick carousel
		// XXX Should be revised
		arrows: true,
		autoplay: false,
		autoplaySpeed: 3000,
		cssEase: 'ease',
		dots: false,

		easing: 'linear',
		fade: false,
		infinite: true,
		initialSlide: 0,

		slidesToShow: 1,
		slidesToScroll: 1,
		speed: 500,

		visiblePrev: false,
		visibleNext: false,

		// Not available right now
		draggable: true,

		lazyLoad: 'ondemand',

		swipe: true,
		swipeToSlide: false,
		touchMove: true,

		vertical: false,
		verticalSwiping: false
	};
	this.$get = [function () {
		return {
			setOptions: function setOptions(options) {
				_this.options = angular.extend(_this.options, options);
			},
			getOptions: function getOptions() {
				return _this.options;
			}
		};
	}];
});
'use strict';

(function (module) {
	try {
		module = angular.module('ui.carousel');
	} catch (e) {
		module = angular.module('ui.carousel', []);
	}
	module.run(['$templateCache', function ($templateCache) {
		$templateCache.put('ui-carousel/carousel.template.html', '<div class="carousel-wrapper" ng-show="ctrl.isCarouselReady"><div class="track-wrapper"><div class="track" ng-style="ctrl.trackStyle"><div class="slide" ng-repeat="item in ctrl.slidesInTrack track by $index" ng-style="ctrl.getSlideStyle($index)"><div class="carousel-item"></div></div></div></div><div class="carousel-prev" ng-if="!ctrl.disableArrow" ng-show="ctrl.isVisiblePrev &amp;&amp; ctrl.options.arrows" ng-class="{\'carousel-disable\': !ctrl.isClickablePrev}" ng-click="ctrl.prev()"><button class="carousel-btn"><i class="ui-icon-prev"></i></button></div><div class="carousel-next" ng-if="!ctrl.disableArrow" ng-show="ctrl.isVisibleNext &amp;&amp; ctrl.options.arrows" ng-class="{\'carousel-disable\': !ctrl.isClickableNext}" ng-click="ctrl.next()"><button class="carousel-btn"><i class="ui-icon-next"></i></button></div><ul class="carousel-dots" ng-show="ctrl.isVisibleDots &amp;&amp; ctrl.options.dots"><li ng-repeat="dot in ctrl.getDots()" ng-class="{ \'carousel-active\': dot == ctrl.currentSlide/ctrl.options.slidesToScroll }" ng-click="ctrl.movePage(dot)"><button>{{ dot }}</button></li></ul></div>');
	}]);
})();
! function (X, Y, G, J) {
	"use strict";
	Y = void 0 !== Y && Y.Math == Math ? Y : "undefined" != typeof self && self.Math == Math ? self : Function("return this")(), X.fn.dropdown = function (z) {
		var P, H = X(this),
			j = X(G),
			N = H.selector || "",
			U = "ontouchstart" in G.documentElement,
			K = (new Date).getTime(),
			W = [],
			B = z,
			$ = "string" == typeof B,
			Q = [].slice.call(arguments, 1);
		return H.each(function (n) {
			var e, t, i, a, o, s, r, m, h = X.isPlainObject(z) ? X.extend(!0, {}, X.fn.dropdown.settings, z) : X.extend({}, X.fn.dropdown.settings),
				g = h.className,
				c = h.message,
				l = h.fields,
				p = h.keys,
				b = h.metadata,
				u = h.namespace,
				d = h.regExp,
				w = h.selector,
				v = h.error,
				f = h.templates,
				x = "." + u,
				C = "module-" + u,
				S = X(this),
				y = X(h.context),
				A = S.find(w.text),
				T = S.find(w.search),
				k = S.find(w.sizer),
				L = S.find(w.input),
				I = S.find(w.icon),
				D = 0 < S.prev().find(w.text).length ? S.prev().find(w.text) : S.prev(),
				q = S.children(w.menu),
				R = q.find(w.item),
				O = !1,
				V = !1,
				E = !1,
				M = this,
				F = S.data(C);
			m = {
				initialize: function () {
					m.debug("Initializing dropdown", h), m.is.alreadySetup() ? m.setup.reference() : (m.setup.layout(), h.values && m.change.values(h.values), m.refreshData(), m.save.defaults(), m.restore.selected(), m.create.id(), m.bind.events(), m.observeChanges(), m.instantiate())
				},
				instantiate: function () {
					m.verbose("Storing instance of dropdown", m), F = m, S.data(C, m)
				},
				destroy: function () {
					m.verbose("Destroying previous dropdown", S), m.remove.tabbable(), S.off(x).removeData(C), q.off(x), j.off(a), m.disconnect.menuObserver(), m.disconnect.selectObserver()
				},
				observeChanges: function () {
					"MutationObserver" in Y && (s = new MutationObserver(m.event.select.mutation), r = new MutationObserver(m.event.menu.mutation), m.debug("Setting up mutation observer", s, r), m.observe.select(), m.observe.menu())
				},
				disconnect: {
					menuObserver: function () {
						r && r.disconnect()
					},
					selectObserver: function () {
						s && s.disconnect()
					}
				},
				observe: {
					select: function () {
						m.has.input() && s.observe(S[0], {
							childList: !0,
							subtree: !0
						})
					},
					menu: function () {
						m.has.menu() && r.observe(q[0], {
							childList: !0,
							subtree: !0
						})
					}
				},
				create: {
					id: function () {
						o = (Math.random().toString(16) + "000000000").substr(2, 8), a = "." + o, m.verbose("Creating unique id for element", o)
					},
					userChoice: function (e) {
						var n, i, a;
						return !!(e = e || m.get.userValues()) && (e = X.isArray(e) ? e : [e], X.each(e, function (e, t) {
							!1 === m.get.item(t) && (a = h.templates.addition(m.add.variables(c.addResult, t)), i = X("<div />").html(a).attr("data-" + b.value, t).attr("data-" + b.text, t).addClass(g.addition).addClass(g.item), h.hideAdditions && i.addClass(g.hidden), n = n === J ? i : n.add(i), m.verbose("Creating user choices for value", t, i))
						}), n)
					},
					userLabels: function (e) {
						var t = m.get.userValues();
						t && (m.debug("Adding user labels", t), X.each(t, function (e, t) {
							m.verbose("Adding custom user value"), m.add.label(t, t)
						}))
					},
					menu: function () {
						q = X("<div />").addClass(g.menu).appendTo(S)
					},
					sizer: function () {
						k = X("<span />").addClass(g.sizer).insertAfter(T)
					}
				},
				search: function (e) {
					e = e !== J ? e : m.get.query(), m.verbose("Searching for query", e), m.has.minCharacters(e) ? m.filter(e) : m.hide()
				},
				select: {
					firstUnfiltered: function () {
						m.verbose("Selecting first non-filtered element"), m.remove.selectedItem(), R.not(w.unselectable).not(w.addition + w.hidden).eq(0).addClass(g.selected)
					},
					nextAvailable: function (e) {
						var t = (e = e.eq(0)).nextAll(w.item).not(w.unselectable).eq(0),
							n = e.prevAll(w.item).not(w.unselectable).eq(0);
						0 < t.length ? (m.verbose("Moving selection to", t), t.addClass(g.selected)) : (m.verbose("Moving selection to", n), n.addClass(g.selected))
					}
				},
				setup: {
					api: function () {
						var e = {
							debug: h.debug,
							urlData: {
								value: m.get.value(),
								query: m.get.query()
							},
							on: !1
						};
						m.verbose("First request, initializing API"), S.api(e)
					},
					layout: function () {
						S.is("select") && (m.setup.select(), m.setup.returnedObject()), m.has.menu() || m.create.menu(), m.is.search() && !m.has.search() && (m.verbose("Adding search input"), T = X("<input />").addClass(g.search).prop("autocomplete", "off").insertBefore(A)), m.is.multiple() && m.is.searchSelection() && !m.has.sizer() && m.create.sizer(), h.allowTab && m.set.tabbable()
					},
					select: function () {
						var e = m.get.selectValues();
						m.debug("Dropdown initialized on a select", e), S.is("select") && (L = S), 0 < L.parent(w.dropdown).length ? (m.debug("UI dropdown already exists. Creating dropdown menu only"), S = L.closest(w.dropdown), m.has.menu() || m.create.menu(), q = S.children(w.menu), m.setup.menu(e)) : (m.debug("Creating entire dropdown from select"), S = X("<div />").attr("class", L.attr("class")).addClass(g.selection).addClass(g.dropdown).html(f.dropdown(e)).insertBefore(L), L.hasClass(g.multiple) && !1 === L.prop("multiple") && (m.error(v.missingMultiple), L.prop("multiple", !0)), L.is("[multiple]") && m.set.multiple(), L.prop("disabled") && (m.debug("Disabling dropdown"), S.addClass(g.disabled)), L.removeAttr("class").detach().prependTo(S)), m.refresh()
					},
					menu: function (e) {
						q.html(f.menu(e, l)), R = q.find(w.item)
					},
					reference: function () {
						m.debug("Dropdown behavior was called on select, replacing with closest dropdown"), S = S.parent(w.dropdown), F = S.data(C), M = S.get(0), m.refresh(), m.setup.returnedObject()
					},
					returnedObject: function () {
						var e = H.slice(0, n),
							t = H.slice(n + 1);
						H = e.add(S).add(t)
					}
				},
				refresh: function () {
					m.refreshSelectors(), m.refreshData()
				},
				refreshItems: function () {
					R = q.find(w.item)
				},
				refreshSelectors: function () {
					m.verbose("Refreshing selector cache"), A = S.find(w.text), T = S.find(w.search), L = S.find(w.input), I = S.find(w.icon), D = 0 < S.prev().find(w.text).length ? S.prev().find(w.text) : S.prev(), q = S.children(w.menu), R = q.find(w.item)
				},
				refreshData: function () {
					m.verbose("Refreshing cached metadata"), R.removeData(b.text).removeData(b.value)
				},
				clearData: function () {
					m.verbose("Clearing metadata"), R.removeData(b.text).removeData(b.value), S.removeData(b.defaultText).removeData(b.defaultValue).removeData(b.placeholderText)
				},
				toggle: function () {
					m.verbose("Toggling menu visibility"), m.is.active() ? m.hide() : m.show()
				},
				show: function (e) {
					if (e = X.isFunction(e) ? e : function () { }, !m.can.show() && m.is.remote() && (m.debug("No API results retrieved, searching before show"), m.queryRemote(m.get.query(), m.show)), m.can.show() && !m.is.active()) {
						if (m.debug("Showing dropdown"), !m.has.message() || m.has.maxSelections() || m.has.allResultsFiltered() || m.remove.message(), m.is.allFiltered()) return !0;
						!1 !== h.onShow.call(M) && m.animate.show(function () {
							m.can.click() && m.bind.intent(), m.has.menuSearch() && m.focusSearch(), m.set.visible(), e.call(M)
						})
					}
				},
				hide: function (e) {
					e = X.isFunction(e) ? e : function () { }, m.is.active() && !m.is.animatingOutward() && (m.debug("Hiding dropdown"), !1 !== h.onHide.call(M) && m.animate.hide(function () {
						m.remove.visible(), e.call(M)
					}))
				},
				hideOthers: function () {
					m.verbose("Finding other dropdowns to hide"), H.not(S).has(w.menu + "." + g.visible).dropdown("hide")
				},
				hideMenu: function () {
					m.verbose("Hiding menu  instantaneously"), m.remove.active(), m.remove.visible(), q.transition("hide")
				},
				hideSubMenus: function () {
					var e = q.children(w.item).find(w.menu);
					m.verbose("Hiding sub menus", e), e.transition("hide")
				},
				bind: {
					events: function () {
						U && m.bind.touchEvents(), m.bind.keyboardEvents(), m.bind.inputEvents(), m.bind.mouseEvents()
					},
					touchEvents: function () {
						m.debug("Touch device detected binding additional touch events"), m.is.searchSelection() || m.is.single() && S.on("touchstart" + x, m.event.test.toggle), q.on("touchstart" + x, w.item, m.event.item.mouseenter)
					},
					keyboardEvents: function () {
						m.verbose("Binding keyboard events"), S.on("keydown" + x, m.event.keydown), m.has.search() && S.on(m.get.inputEvent() + x, w.search, m.event.input), m.is.multiple() && j.on("keydown" + a, m.event.document.keydown)
					},
					inputEvents: function () {
						m.verbose("Binding input change events"), S.on("change" + x, w.input, m.event.change)
					},
					mouseEvents: function () {
						m.verbose("Binding mouse events"), m.is.multiple() && S.on("click" + x, w.label, m.event.label.click).on("click" + x, w.remove, m.event.remove.click), m.is.searchSelection() ? (S.on("mousedown" + x, m.event.mousedown).on("mouseup" + x, m.event.mouseup).on("mousedown" + x, w.menu, m.event.menu.mousedown).on("mouseup" + x, w.menu, m.event.menu.mouseup).on("click" + x, w.icon, m.event.icon.click).on("focus" + x, w.search, m.event.search.focus).on("click" + x, w.search, m.event.search.focus).on("blur" + x, w.search, m.event.search.blur).on("click" + x, w.text, m.event.text.focus), m.is.multiple() && S.on("click" + x, m.event.click)) : ("click" == h.on ? S.on("click" + x, m.event.test.toggle) : "hover" == h.on ? S.on("mouseenter" + x, m.delay.show).on("mouseleave" + x, m.delay.hide) : S.on(h.on + x, m.toggle), S.on("click" + x, w.icon, m.event.icon.click).on("mousedown" + x, m.event.mousedown).on("mouseup" + x, m.event.mouseup).on("focus" + x, m.event.focus), m.has.menuSearch() ? S.on("blur" + x, w.search, m.event.search.blur) : S.on("blur" + x, m.event.blur)), q.on("mouseenter" + x, w.item, m.event.item.mouseenter).on("mouseleave" + x, w.item, m.event.item.mouseleave).on("click" + x, w.item, m.event.item.click)
					},
					intent: function () {
						m.verbose("Binding hide intent event to document"), U && j.on("touchstart" + a, m.event.test.touch).on("touchmove" + a, m.event.test.touch), j.on("click" + a, m.event.test.hide)
					}
				},
				unbind: {
					intent: function () {
						m.verbose("Removing hide intent event from document"), U && j.off("touchstart" + a).off("touchmove" + a), j.off("click" + a)
					}
				},
				filter: function (e) {
					var t = e !== J ? e : m.get.query(),
						n = function () {
							m.is.multiple() && m.filterActive(), (e || !e && 0 == m.get.activeItem().length) && m.select.firstUnfiltered(), m.has.allResultsFiltered() ? h.onNoResults.call(M, t) ? h.allowAdditions ? h.hideAdditions && (m.verbose("User addition with no menu, setting empty style"), m.set.empty(), m.hideMenu()) : (m.verbose("All items filtered, showing message", t), m.add.message(c.noResults)) : (m.verbose("All items filtered, hiding dropdown", t), m.hideMenu()) : (m.remove.empty(), m.remove.message()), h.allowAdditions && m.add.userSuggestion(e), m.is.searchSelection() && m.can.show() && m.is.focusedOnSearch() && m.show()
						};
					h.useLabels && m.has.maxSelections() || (h.apiSettings ? m.can.useAPI() ? m.queryRemote(t, function () {
						h.filterRemoteData && m.filterItems(t), n()
					}) : m.error(v.noAPI) : (m.filterItems(t), n()))
				},
				queryRemote: function (e, n) {
					var t = {
						errorDuration: !1,
						cache: "local",
						throttle: h.throttle,
						urlData: {
							query: e
						},
						onError: function () {
							m.add.message(c.serverError), n()
						},
						onFailure: function () {
							m.add.message(c.serverError), n()
						},
						onSuccess: function (e) {
							var t = e[l.remoteValues];
							X.isArray(t) && 0 < t.length ? (m.remove.message(), m.setup.menu({
								values: e[l.remoteValues]
							})) : m.add.message(c.noResults), n()
						}
					};
					S.api("get request") || m.setup.api(), t = X.extend(!0, {}, t, h.apiSettings), S.api("setting", t).api("query")
				},
				filterItems: function (e) {
					var i = e !== J ? e : m.get.query(),
						a = null,
						t = m.escape.string(i),
						o = new RegExp("^" + t, "igm");
					m.has.query() && (a = [], m.verbose("Searching for matching values", i), R.each(function () {
						var e, t, n = X(this);
						if ("both" == h.match || "text" == h.match) {
							if (-1 !== (e = String(m.get.choiceText(n, !1))).search(o)) return a.push(this), !0;
							if ("exact" === h.fullTextSearch && m.exactSearch(i, e)) return a.push(this), !0;
							if (!0 === h.fullTextSearch && m.fuzzySearch(i, e)) return a.push(this), !0
						}
						if ("both" == h.match || "value" == h.match) {
							if (-1 !== (t = String(m.get.choiceValue(n, e))).search(o)) return a.push(this), !0;
							if ("exact" === h.fullTextSearch && m.exactSearch(i, t)) return a.push(this), !0;
							if (!0 === h.fullTextSearch && m.fuzzySearch(i, t)) return a.push(this), !0
						}
					})), m.debug("Showing only matched items", i), m.remove.filteredItem(), a && R.not(a).addClass(g.filtered)
				},
				fuzzySearch: function (e, t) {
					var n = t.length,
						i = e.length;
					if (e = e.toLowerCase(), t = t.toLowerCase(), n < i) return !1;
					if (i === n) return e === t;
					e: for (var a = 0, o = 0; a < i; a++) {
						for (var s = e.charCodeAt(a); o < n;)
							if (t.charCodeAt(o++) === s) continue e;
						return !1
					}
					return !0
				},
				exactSearch: function (e, t) {
					return e = e.toLowerCase(), -1 < (t = t.toLowerCase()).indexOf(e)
				},
				filterActive: function () {
					h.useLabels && R.filter("." + g.active).addClass(g.filtered)
				},
				focusSearch: function (e) {
					m.has.search() && !m.is.focusedOnSearch() && (e ? (S.off("focus" + x, w.search), T.focus(), S.on("focus" + x, w.search, m.event.search.focus)) : T.focus())
				},
				forceSelection: function () {
					var e = R.not(g.filtered).filter("." + g.selected).eq(0),
						t = R.not(g.filtered).filter("." + g.active).eq(0),
						n = 0 < e.length ? e : t;
					if (0 < n.length && !m.is.multiple()) return m.debug("Forcing partial selection to selected item", n), void m.event.item.click.call(n, {}, !0);
					h.allowAdditions && m.set.selected(m.get.query()), m.remove.searchTerm()
				},
				change: {
					values: function (e) {
						h.allowAdditions || m.clear(), m.debug("Creating dropdown with specified values", e), m.setup.menu({
							values: e
						}), X.each(e, function (e, t) {
							if (1 == t.selected) return m.debug("Setting initial selection to", t.value), m.set.selected(t.value), !0
						})
					}
				},
				event: {
					change: function () {
						E || (m.debug("Input changed, updating selection"), m.set.selected())
					},
					focus: function () {
						h.showOnFocus && !O && m.is.hidden() && !t && m.show()
					},
					blur: function (e) {
						t = G.activeElement === this, O || t || (m.remove.activeLabel(), m.hide())
					},
					mousedown: function () {
						m.is.searchSelection() ? i = !0 : O = !0
					},
					mouseup: function () {
						m.is.searchSelection() ? i = !1 : O = !1
					},
					click: function (e) {
						X(e.target).is(S) && (m.is.focusedOnSearch() ? m.show() : m.focusSearch())
					},
					search: {
						focus: function () {
							O = !0, m.is.multiple() && m.remove.activeLabel(), h.showOnFocus && m.search()
						},
						blur: function (e) {
							t = G.activeElement === this, m.is.searchSelection() && !i && (V || t || (h.forceSelection && m.forceSelection(), m.hide())), i = !1
						}
					},
					icon: {
						click: function (e) {
							I.hasClass(g.clear) ? m.clear() : m.can.click() && m.toggle()
						}
					},
					text: {
						focus: function (e) {
							O = !0, m.focusSearch()
						}
					},
					input: function (e) {
						(m.is.multiple() || m.is.searchSelection()) && m.set.filtered(), clearTimeout(m.timer), m.timer = setTimeout(m.search, h.delay.search)
					},
					label: {
						click: function (e) {
							var t = X(this),
								n = S.find(w.label),
								i = n.filter("." + g.active),
								a = t.nextAll("." + g.active),
								o = t.prevAll("." + g.active),
								s = 0 < a.length ? t.nextUntil(a).add(i).add(t) : t.prevUntil(o).add(i).add(t);
							e.shiftKey ? (i.removeClass(g.active), s.addClass(g.active)) : e.ctrlKey ? t.toggleClass(g.active) : (i.removeClass(g.active), t.addClass(g.active)), h.onLabelSelect.apply(this, n.filter("." + g.active))
						}
					},
					remove: {
						click: function () {
							var e = X(this).parent();
							e.hasClass(g.active) ? m.remove.activeLabels() : m.remove.activeLabels(e)
						}
					},
					test: {
						toggle: function (e) {
							var t = m.is.multiple() ? m.show : m.toggle;
							m.is.bubbledLabelClick(e) || m.is.bubbledIconClick(e) || m.determine.eventOnElement(e, t) && e.preventDefault()
						},
						touch: function (e) {
							m.determine.eventOnElement(e, function () {
								"touchstart" == e.type ? m.timer = setTimeout(function () {
									m.hide()
								}, h.delay.touch) : "touchmove" == e.type && clearTimeout(m.timer)
							}), e.stopPropagation()
						},
						hide: function (e) {
							m.determine.eventInModule(e, m.hide)
						}
					},
					select: {
						mutation: function (e) {
							m.debug("<select> modified, recreating menu");
							var n = !1;
							X.each(e, function (e, t) {
								if (X(t.target).is("select") || X(t.addedNodes).is("select")) return n = !0
							}), n && (m.disconnect.selectObserver(), m.refresh(), m.setup.select(), m.set.selected(), m.observe.select())
						}
					},
					menu: {
						mutation: function (e) {
							var t = e[0],
								n = t.addedNodes ? X(t.addedNodes[0]) : X(!1),
								i = t.removedNodes ? X(t.removedNodes[0]) : X(!1),
								a = n.add(i),
								o = a.is(w.addition) || 0 < a.closest(w.addition).length,
								s = a.is(w.message) || 0 < a.closest(w.message).length;
							o || s ? (m.debug("Updating item selector cache"), m.refreshItems()) : (m.debug("Menu modified, updating selector cache"), m.refresh())
						},
						mousedown: function () {
							V = !0
						},
						mouseup: function () {
							V = !1
						}
					},
					item: {
						mouseenter: function (e) {
							var t = X(e.target),
								n = X(this),
								i = n.children(w.menu),
								a = n.siblings(w.item).children(w.menu),
								o = 0 < i.length;
							!(0 < i.find(t).length) && o && (clearTimeout(m.itemTimer), m.itemTimer = setTimeout(function () {
								m.verbose("Showing sub-menu", i), X.each(a, function () {
									m.animate.hide(!1, X(this))
								}), m.animate.show(!1, i)
							}, h.delay.show), e.preventDefault())
						},
						mouseleave: function (e) {
							var t = X(this).children(w.menu);
							0 < t.length && (clearTimeout(m.itemTimer), m.itemTimer = setTimeout(function () {
								m.verbose("Hiding sub-menu", t), m.animate.hide(!1, t)
							}, h.delay.hide))
						},
						click: function (e, t) {
							var n = X(this),
								i = X(e ? e.target : ""),
								a = n.find(w.menu),
								o = m.get.choiceText(n),
								s = m.get.choiceValue(n, o),
								r = 0 < a.length,
								l = 0 < a.find(i).length;
							m.has.menuSearch() && X(G.activeElement).blur(), l || r && !h.allowCategorySelection || (m.is.searchSelection() && (h.allowAdditions && m.remove.userAddition(), m.remove.searchTerm(), m.is.focusedOnSearch() || 1 == t || m.focusSearch(!0)), h.useLabels || (m.remove.filteredItem(), m.set.scrollPosition(n)), m.determine.selectAction.call(this, o, s))
						}
					},
					document: {
						keydown: function (e) {
							var t = e.which;
							if (m.is.inObject(t, p)) {
								var n = S.find(w.label),
									i = n.filter("." + g.active),
									a = (i.data(b.value), n.index(i)),
									o = n.length,
									s = 0 < i.length,
									r = 1 < i.length,
									l = 0 === a,
									c = a + 1 == o,
									u = m.is.searchSelection(),
									d = m.is.focusedOnSearch(),
									v = m.is.focused(),
									f = d && 0 === m.get.caretPosition();
								if (u && !s && !d) return;
								t == p.leftArrow ? !v && !f || s ? s && (e.shiftKey ? m.verbose("Adding previous label to selection") : (m.verbose("Selecting previous label"), n.removeClass(g.active)), l && !r ? i.addClass(g.active) : i.prev(w.siblingLabel).addClass(g.active).end(), e.preventDefault()) : (m.verbose("Selecting previous label"), n.last().addClass(g.active)) : t == p.rightArrow ? (v && !s && n.first().addClass(g.active), s && (e.shiftKey ? m.verbose("Adding next label to selection") : (m.verbose("Selecting next label"), n.removeClass(g.active)), c ? u ? d ? n.removeClass(g.active) : m.focusSearch() : r ? i.next(w.siblingLabel).addClass(g.active) : i.addClass(g.active) : i.next(w.siblingLabel).addClass(g.active), e.preventDefault())) : t == p.deleteKey || t == p.backspace ? s ? (m.verbose("Removing active labels"), c && u && !d && m.focusSearch(), i.last().next(w.siblingLabel).addClass(g.active), m.remove.activeLabels(i), e.preventDefault()) : f && !s && t == p.backspace && (m.verbose("Removing last label on input backspace"), i = n.last().addClass(g.active), m.remove.activeLabels(i)) : i.removeClass(g.active)
							}
						}
					},
					keydown: function (e) {
						var t = e.which;
						if (m.is.inObject(t, p)) {
							var n, i = R.not(w.unselectable).filter("." + g.selected).eq(0),
								a = q.children("." + g.active).eq(0),
								o = 0 < i.length ? i : a,
								s = 0 < o.length ? o.siblings(":not(." + g.filtered + ")").addBack() : q.children(":not(." + g.filtered + ")"),
								r = o.children(w.menu),
								l = o.closest(w.menu),
								c = l.hasClass(g.visible) || l.hasClass(g.animating) || 0 < l.parent(w.menu).length,
								u = 0 < r.length,
								d = 0 < o.length,
								v = 0 < o.not(w.unselectable).length,
								f = t == p.delimiter && h.allowAdditions && m.is.multiple();
							if (h.allowAdditions && h.hideAdditions && (t == p.enter || f) && v && (m.verbose("Selecting item from keyboard shortcut", o), m.event.item.click.call(o, e), m.is.searchSelection() && m.remove.searchTerm()), m.is.visible()) {
								if ((t == p.enter || f) && (t == p.enter && d && u && !h.allowCategorySelection ? (m.verbose("Pressed enter on unselectable category, opening sub menu"), t = p.rightArrow) : v && (m.verbose("Selecting item from keyboard shortcut", o), m.event.item.click.call(o, e), m.is.searchSelection() && m.remove.searchTerm()), e.preventDefault()), d && (t == p.leftArrow && l[0] !== q[0] && (m.verbose("Left key pressed, closing sub-menu"), m.animate.hide(!1, l), o.removeClass(g.selected), l.closest(w.item).addClass(g.selected), e.preventDefault()), t == p.rightArrow && u && (m.verbose("Right key pressed, opening sub-menu"), m.animate.show(!1, r), o.removeClass(g.selected), r.find(w.item).eq(0).addClass(g.selected), e.preventDefault())), t == p.upArrow) {
									if (n = d && c ? o.prevAll(w.item + ":not(" + w.unselectable + ")").eq(0) : R.eq(0), s.index(n) < 0) return m.verbose("Up key pressed but reached top of current menu"), void e.preventDefault();
									m.verbose("Up key pressed, changing active item"), o.removeClass(g.selected), n.addClass(g.selected), m.set.scrollPosition(n), h.selectOnKeydown && m.is.single() && m.set.selectedItem(n), e.preventDefault()
								}
								if (t == p.downArrow) {
									if (0 === (n = d && c ? n = o.nextAll(w.item + ":not(" + w.unselectable + ")").eq(0) : R.eq(0)).length) return m.verbose("Down key pressed but reached bottom of current menu"), void e.preventDefault();
									m.verbose("Down key pressed, changing active item"), R.removeClass(g.selected), n.addClass(g.selected), m.set.scrollPosition(n), h.selectOnKeydown && m.is.single() && m.set.selectedItem(n), e.preventDefault()
								}
								t == p.pageUp && (m.scrollPage("up"), e.preventDefault()), t == p.pageDown && (m.scrollPage("down"), e.preventDefault()), t == p.escape && (m.verbose("Escape key pressed, closing dropdown"), m.hide())
							} else f && e.preventDefault(), t != p.downArrow || m.is.visible() || (m.verbose("Down key pressed, showing dropdown"), m.show(), e.preventDefault())
						} else m.has.search() || m.set.selectedLetter(String.fromCharCode(t))
					}
				},
				trigger: {
					change: function () {
						var e = G.createEvent("HTMLEvents"),
							t = L[0];
						t && (m.verbose("Triggering native change event"), e.initEvent("change", !0, !1), t.dispatchEvent(e))
					}
				},
				determine: {
					selectAction: function (e, t) {
						m.verbose("Determining action", h.action), X.isFunction(m.action[h.action]) ? (m.verbose("Triggering preset action", h.action, e, t), m.action[h.action].call(M, e, t, this)) : X.isFunction(h.action) ? (m.verbose("Triggering user action", h.action, e, t), h.action.call(M, e, t, this)) : m.error(v.action, h.action)
					},
					eventInModule: function (e, t) {
						var n = X(e.target),
							i = 0 < n.closest(G.documentElement).length,
							a = 0 < n.closest(S).length;
						return t = X.isFunction(t) ? t : function () { }, i && !a ? (m.verbose("Triggering event", t), t(), !0) : (m.verbose("Event occurred in dropdown, canceling callback"), !1)
					},
					eventOnElement: function (e, t) {
						var n = X(e.target),
							i = n.closest(w.siblingLabel),
							a = G.body.contains(e.target),
							o = 0 === S.find(i).length,
							s = 0 === n.closest(q).length;
						return t = X.isFunction(t) ? t : function () { }, a && o && s ? (m.verbose("Triggering event", t), t(), !0) : (m.verbose("Event occurred in dropdown menu, canceling callback"), !1)
					}
				},
				action: {
					nothing: function () { },
					activate: function (e, t, n) {
						if (t = t !== J ? t : e, m.can.activate(X(n))) {
							if (m.set.selected(t, X(n)), m.is.multiple() && !m.is.allFiltered()) return;
							m.hideAndClear()
						}
					},
					select: function (e, t, n) {
						if (t = t !== J ? t : e, m.can.activate(X(n))) {
							if (m.set.value(t, e, X(n)), m.is.multiple() && !m.is.allFiltered()) return;
							m.hideAndClear()
						}
					},
					combo: function (e, t, n) {
						t = t !== J ? t : e, m.set.selected(t, X(n)), m.hideAndClear()
					},
					hide: function (e, t, n) {
						m.set.value(t, e, X(n)), m.hideAndClear()
					}
				},
				get: {
					id: function () {
						return o
					},
					defaultText: function () {
						return S.data(b.defaultText)
					},
					defaultValue: function () {
						return S.data(b.defaultValue)
					},
					placeholderText: function () {
						return "auto" != h.placeholder && "string" == typeof h.placeholder ? h.placeholder : S.data(b.placeholderText) || ""
					},
					text: function () {
						return A.text()
					},
					query: function () {
						return X.trim(T.val())
					},
					searchWidth: function (e) {
						return e = e !== J ? e : T.val(), k.text(e), Math.ceil(k.width() + 1)
					},
					selectionCount: function () {
						var e = m.get.values();
						return m.is.multiple() ? X.isArray(e) ? e.length : 0 : "" !== m.get.value() ? 1 : 0
					},
					transition: function (e) {
						return "auto" == h.transition ? m.is.upward(e) ? "slide up" : "slide down" : h.transition
					},
					userValues: function () {
						var e = m.get.values();
						return !!e && (e = X.isArray(e) ? e : [e], X.grep(e, function (e) {
							return !1 === m.get.item(e)
						}))
					},
					uniqueArray: function (n) {
						return X.grep(n, function (e, t) {
							return X.inArray(e, n) === t
						})
					},
					caretPosition: function () {
						var e, t, n = T.get(0);
						return "selectionStart" in n ? n.selectionStart : G.selection ? (n.focus(), t = (e = G.selection.createRange()).text.length, e.moveStart("character", -n.value.length), e.text.length - t) : void 0
					},
					value: function () {
						var e = 0 < L.length ? L.val() : S.data(b.value),
							t = X.isArray(e) && 1 === e.length && "" === e[0];
						return e === J || t ? "" : e
					},
					values: function () {
						var e = m.get.value();
						return "" === e ? "" : !m.has.selectInput() && m.is.multiple() ? "string" == typeof e ? e.split(h.delimiter) : "" : e
					},
					remoteValues: function () {
						var e = m.get.values(),
							i = !1;
						return e && ("string" == typeof e && (e = [e]), X.each(e, function (e, t) {
							var n = m.read.remoteData(t);
							m.verbose("Restoring value from session data", n, t), n && (i || (i = {}), i[t] = n)
						})), i
					},
					choiceText: function (e, t) {
						if (t = t !== J ? t : h.preserveHTML, e) return 0 < e.find(w.menu).length && (m.verbose("Retrieving text of element with sub-menu"), (e = e.clone()).find(w.menu).remove(), e.find(w.menuIcon).remove()), e.data(b.text) !== J ? e.data(b.text) : t ? X.trim(e.html()) : X.trim(e.text())
					},
					choiceValue: function (e, t) {
						return t = t || m.get.choiceText(e), !!e && (e.data(b.value) !== J ? String(e.data(b.value)) : "string" == typeof t ? X.trim(t.toLowerCase()) : String(t))
					},
					inputEvent: function () {
						var e = T[0];
						return !!e && (e.oninput !== J ? "input" : e.onpropertychange !== J ? "propertychange" : "keyup")
					},
					selectValues: function () {
						var a = {
							values: []
						};
						return S.find("option").each(function () {
							var e = X(this),
								t = e.html(),
								n = e.attr("disabled"),
								i = e.attr("value") !== J ? e.attr("value") : t;
							"auto" === h.placeholder && "" === i ? a.placeholder = t : a.values.push({
								name: t,
								value: i,
								disabled: n
							})
						}), h.placeholder && "auto" !== h.placeholder && (m.debug("Setting placeholder value to", h.placeholder), a.placeholder = h.placeholder), h.sortSelect ? (a.values.sort(function (e, t) {
							return e.name > t.name ? 1 : -1
						}), m.debug("Retrieved and sorted values from select", a)) : m.debug("Retrieved values from select", a), a
					},
					activeItem: function () {
						return R.filter("." + g.active)
					},
					selectedItem: function () {
						var e = R.not(w.unselectable).filter("." + g.selected);
						return 0 < e.length ? e : R.eq(0)
					},
					itemWithAdditions: function (e) {
						var t = m.get.item(e),
							n = m.create.userChoice(e);
						return n && 0 < n.length && (t = 0 < t.length ? t.add(n) : n), t
					},
					item: function (i, a) {
						var e, o, s = !1;
						return i = i !== J ? i : m.get.values() !== J ? m.get.values() : m.get.text(), e = o ? 0 < i.length : i !== J && null !== i, o = m.is.multiple() && X.isArray(i), a = "" === i || 0 === i || (a || !1), e && R.each(function () {
							var e = X(this),
								t = m.get.choiceText(e),
								n = m.get.choiceValue(e, t);
							if (null !== n && n !== J)
								if (o) - 1 === X.inArray(String(n), i) && -1 === X.inArray(t, i) || (s = s ? s.add(e) : e);
								else if (a) {
									if (m.verbose("Ambiguous dropdown value using strict type check", e, i), n === i || t === i) return s = e, !0
								} else if (String(n) == String(i) || t == i) return m.verbose("Found select item by value", n, i), s = e, !0
						}), s
					}
				},
				check: {
					maxSelections: function (e) {
						return !h.maxSelections || ((e = e !== J ? e : m.get.selectionCount()) >= h.maxSelections ? (m.debug("Maximum selection count reached"), h.useLabels && (R.addClass(g.filtered), m.add.message(c.maxSelections)), !0) : (m.verbose("No longer at maximum selection count"), m.remove.message(), m.remove.filteredItem(), m.is.searchSelection() && m.filterItems(), !1))
					}
				},
				restore: {
					defaults: function () {
						m.clear(), m.restore.defaultText(), m.restore.defaultValue()
					},
					defaultText: function () {
						var e = m.get.defaultText();
						e === m.get.placeholderText ? (m.debug("Restoring default placeholder text", e), m.set.placeholderText(e)) : (m.debug("Restoring default text", e), m.set.text(e))
					},
					placeholderText: function () {
						m.set.placeholderText()
					},
					defaultValue: function () {
						var e = m.get.defaultValue();
						e !== J && (m.debug("Restoring default value", e), "" !== e ? (m.set.value(e), m.set.selected()) : (m.remove.activeItem(), m.remove.selectedItem()))
					},
					labels: function () {
						h.allowAdditions && (h.useLabels || (m.error(v.labels), h.useLabels = !0), m.debug("Restoring selected values"), m.create.userLabels()), m.check.maxSelections()
					},
					selected: function () {
						m.restore.values(), m.is.multiple() ? (m.debug("Restoring previously selected values and labels"), m.restore.labels()) : m.debug("Restoring previously selected values")
					},
					values: function () {
						m.set.initialLoad(), h.apiSettings && h.saveRemoteData && m.get.remoteValues() ? m.restore.remoteValues() : m.set.selected(), m.remove.initialLoad()
					},
					remoteValues: function () {
						var e = m.get.remoteValues();
						m.debug("Recreating selected from session data", e), e && (m.is.single() ? X.each(e, function (e, t) {
							m.set.text(t)
						}) : X.each(e, function (e, t) {
							m.add.label(e, t)
						}))
					}
				},
				read: {
					remoteData: function (e) {
						var t;
						if (Y.Storage !== J) return (t = sessionStorage.getItem(e)) !== J && t;
						m.error(v.noStorage)
					}
				},
				save: {
					defaults: function () {
						m.save.defaultText(), m.save.placeholderText(), m.save.defaultValue()
					},
					defaultValue: function () {
						var e = m.get.value();
						m.verbose("Saving default value as", e), S.data(b.defaultValue, e)
					},
					defaultText: function () {
						var e = m.get.text();
						m.verbose("Saving default text as", e), S.data(b.defaultText, e)
					},
					placeholderText: function () {
						var e;
						!1 !== h.placeholder && A.hasClass(g.placeholder) && (e = m.get.text(), m.verbose("Saving placeholder text as", e), S.data(b.placeholderText, e))
					},
					remoteData: function (e, t) {
						Y.Storage !== J ? (m.verbose("Saving remote data to session storage", t, e), sessionStorage.setItem(t, e)) : m.error(v.noStorage)
					}
				},
				clear: function () {
					m.is.multiple() && h.useLabels ? m.remove.labels() : (m.remove.activeItem(), m.remove.selectedItem()), m.set.placeholderText(), m.clearValue()
				},
				clearValue: function () {
					m.set.value("")
				},
				scrollPage: function (e, t) {
					var n, i, a = t || m.get.selectedItem(),
						o = a.closest(w.menu),
						s = o.outerHeight(),
						r = o.scrollTop(),
						l = R.eq(0).outerHeight(),
						c = Math.floor(s / l),
						u = (o.prop("scrollHeight"), "up" == e ? r - l * c : r + l * c),
						d = R.not(w.unselectable);
					i = "up" == e ? d.index(a) - c : d.index(a) + c, 0 < (n = ("up" == e ? 0 <= i : i < d.length) ? d.eq(i) : "up" == e ? d.first() : d.last()).length && (m.debug("Scrolling page", e, n), a.removeClass(g.selected), n.addClass(g.selected), h.selectOnKeydown && m.is.single() && m.set.selectedItem(n), o.scrollTop(u))
				},
				set: {
					filtered: function () {
						var e = m.is.multiple(),
							t = m.is.searchSelection(),
							n = e && t,
							i = t ? m.get.query() : "",
							a = "string" == typeof i && 0 < i.length,
							o = m.get.searchWidth(),
							s = "" !== i;
						e && a && (m.verbose("Adjusting input width", o, h.glyphWidth), T.css("width", o)), a || n && s ? (m.verbose("Hiding placeholder text"), A.addClass(g.filtered)) : (!e || n && !s) && (m.verbose("Showing placeholder text"), A.removeClass(g.filtered))
					},
					empty: function () {
						S.addClass(g.empty)
					},
					loading: function () {
						S.addClass(g.loading)
					},
					placeholderText: function (e) {
						e = e || m.get.placeholderText(), m.debug("Setting placeholder text", e), m.set.text(e), A.addClass(g.placeholder)
					},
					tabbable: function () {
						m.is.searchSelection() ? (m.debug("Added tabindex to searchable dropdown"), T.val("").attr("tabindex", 0), q.attr("tabindex", -1)) : (m.debug("Added tabindex to dropdown"), S.attr("tabindex") === J && (S.attr("tabindex", 0), q.attr("tabindex", -1)))
					},
					initialLoad: function () {
						m.verbose("Setting initial load"), e = !0
					},
					activeItem: function (e) {
						h.allowAdditions && 0 < e.filter(w.addition).length ? e.addClass(g.filtered) : e.addClass(g.active)
					},
					partialSearch: function (e) {
						var t = m.get.query().length;
						T.val(e.substr(0, t))
					},
					scrollPosition: function (e, t) {
						var n, i, a, o, s, r;
						n = (e = e || m.get.selectedItem()).closest(w.menu), i = e && 0 < e.length, t = t !== J && t, e && 0 < n.length && i && (e.position().top, n.addClass(g.loading), a = (o = n.scrollTop()) - n.offset().top + e.offset().top, t || (r = o + n.height() < a + 5, s = a - 5 < o), m.debug("Scrolling to active item", a), (t || s || r) && n.scrollTop(a), n.removeClass(g.loading))
					},
					text: function (e) {
						"select" !== h.action && ("combo" == h.action ? (m.debug("Changing combo button text", e, D), h.preserveHTML ? D.html(e) : D.text(e)) : (e !== m.get.placeholderText() && A.removeClass(g.placeholder), m.debug("Changing text", e, A), A.removeClass(g.filtered), h.preserveHTML ? A.html(e) : A.text(e)))
					},
					selectedItem: function (e) {
						var t = m.get.choiceValue(e),
							n = m.get.choiceText(e, !1),
							i = m.get.choiceText(e, !0);
						m.debug("Setting user selection to item", e), m.remove.activeItem(), m.set.partialSearch(n), m.set.activeItem(e), m.set.selected(t, e), m.set.text(i)
					},
					selectedLetter: function (e) {
						var t, n = R.filter("." + g.selected),
							i = 0 < n.length && m.has.firstLetter(n, e),
							a = !1;
						i && (t = n.nextAll(R).eq(0), m.has.firstLetter(t, e) && (a = t)), a || R.each(function () {
							if (m.has.firstLetter(X(this), e)) return a = X(this), !1
						}), a && (m.verbose("Scrolling to next value with letter", e), m.set.scrollPosition(a), n.removeClass(g.selected), a.addClass(g.selected), h.selectOnKeydown && m.is.single() && m.set.selectedItem(a))
					},
					direction: function (e) {
						"auto" == h.direction ? (m.remove.upward(), m.can.openDownward(e) ? m.remove.upward(e) : m.set.upward(e), m.is.leftward(e) || m.can.openRightward(e) || m.set.leftward(e)) : "upward" == h.direction && m.set.upward(e)
					},
					upward: function (e) {
						(e || S).addClass(g.upward)
					},
					leftward: function (e) {
						(e || q).addClass(g.leftward)
					},
					value: function (e, t, n) {
						var i = m.escape.value(e),
							a = 0 < L.length,
							o = m.get.values(),
							s = e !== J ? String(e) : e;
						if (a) {
							if (!h.allowReselection && s == o && (m.verbose("Skipping value update already same value", e, o), !m.is.initialLoad())) return;
							m.is.single() && m.has.selectInput() && m.can.extendSelect() && (m.debug("Adding user option", e), m.add.optionValue(e)), m.debug("Updating input value", i, o), E = !0, L.val(i), !1 === h.fireOnInit && m.is.initialLoad() ? m.debug("Input native change event ignored on initial load") : m.trigger.change(), E = !1
						} else m.verbose("Storing value in metadata", i, L), i !== o && S.data(b.value, s);
						m.is.single() && h.clearable && (i ? m.set.clearable() : m.remove.clearable()), !1 === h.fireOnInit && m.is.initialLoad() ? m.verbose("No callback on initial load", h.onChange) : h.onChange.call(M, e, t, n)
					},
					active: function () {
						S.addClass(g.active)
					},
					multiple: function () {
						S.addClass(g.multiple)
					},
					visible: function () {
						S.addClass(g.visible)
					},
					exactly: function (e, t) {
						m.debug("Setting selected to exact values"), m.clear(), m.set.selected(e, t)
					},
					selected: function (e, r) {
						var l = m.is.multiple();
						(r = h.allowAdditions ? r || m.get.itemWithAdditions(e) : r || m.get.item(e)) && (m.debug("Setting selected menu item to", r), m.is.multiple() && m.remove.searchWidth(), m.is.single() ? (m.remove.activeItem(), m.remove.selectedItem()) : h.useLabels && m.remove.selectedItem(), r.each(function () {
							var e = X(this),
								t = m.get.choiceText(e),
								n = m.get.choiceValue(e, t),
								i = e.hasClass(g.filtered),
								a = e.hasClass(g.active),
								o = e.hasClass(g.addition),
								s = l && 1 == r.length;
							l ? !a || o ? (h.apiSettings && h.saveRemoteData && m.save.remoteData(t, n), h.useLabels ? (m.add.label(n, t, s), m.add.value(n, t, e), m.set.activeItem(e), m.filterActive(), m.select.nextAvailable(r)) : (m.add.value(n, t, e), m.set.text(m.add.variables(c.count)), m.set.activeItem(e))) : i || (m.debug("Selected active value, removing label"), m.remove.selected(n)) : (h.apiSettings && h.saveRemoteData && m.save.remoteData(t, n), m.set.text(t), m.set.value(n, t, e), e.addClass(g.active).addClass(g.selected))
						}))
					},
					clearable: function () {
						I.addClass(g.clear)
					}
				},
				add: {
					label: function (e, t, n) {
						var i, a = m.is.searchSelection() ? T : A,
							o = m.escape.value(e);
						h.ignoreCase && (o = o.toLowerCase()), i = X("<a />").addClass(g.label).attr("data-" + b.value, o).html(f.label(o, t)), i = h.onLabelCreate.call(i, o, t), m.has.label(e) ? m.debug("User selection already exists, skipping", o) : (h.label.variation && i.addClass(h.label.variation), !0 === n ? (m.debug("Animating in label", i), i.addClass(g.hidden).insertBefore(a).transition(h.label.transition, h.label.duration)) : (m.debug("Adding selection label", i), i.insertBefore(a)))
					},
					message: function (e) {
						var t = q.children(w.message),
							n = h.templates.message(m.add.variables(e));
						0 < t.length ? t.html(n) : t = X("<div/>").html(n).addClass(g.message).appendTo(q)
					},
					optionValue: function (e) {
						var t = m.escape.value(e);
						0 < L.find('option[value="' + m.escape.string(t) + '"]').length || (m.disconnect.selectObserver(), m.is.single() && (m.verbose("Removing previous user addition"), L.find("option." + g.addition).remove()), X("<option/>").prop("value", t).addClass(g.addition).html(e).appendTo(L), m.verbose("Adding user addition as an <option>", e), m.observe.select())
					},
					userSuggestion: function (e) {
						var t, n = q.children(w.addition),
							i = m.get.item(e),
							a = i && i.not(w.addition).length,
							o = 0 < n.length;
						h.useLabels && m.has.maxSelections() || ("" === e || a ? n.remove() : (o ? (n.data(b.value, e).data(b.text, e).attr("data-" + b.value, e).attr("data-" + b.text, e).removeClass(g.filtered), h.hideAdditions || (t = h.templates.addition(m.add.variables(c.addResult, e)), n.html(t)), m.verbose("Replacing user suggestion with new value", n)) : ((n = m.create.userChoice(e)).prependTo(q), m.verbose("Adding item choice to menu corresponding with user choice addition", n)), h.hideAdditions && !m.is.allFiltered() || n.addClass(g.selected).siblings().removeClass(g.selected), m.refreshItems()))
					},
					variables: function (e, t) {
						var n, i, a = -1 !== e.search("{count}"),
							o = -1 !== e.search("{maxCount}"),
							s = -1 !== e.search("{term}");
						return m.verbose("Adding templated variables to message", e), a && (n = m.get.selectionCount(), e = e.replace("{count}", n)), o && (n = m.get.selectionCount(), e = e.replace("{maxCount}", h.maxSelections)), s && (i = t || m.get.query(), e = e.replace("{term}", i)), e
					},
					value: function (e, t, n) {
						var i, a = m.get.values();
						m.has.value(e) ? m.debug("Value already selected") : "" !== e ? (i = X.isArray(a) ? (i = a.concat([e]), m.get.uniqueArray(i)) : [e], m.has.selectInput() ? m.can.extendSelect() && (m.debug("Adding value to select", e, i, L), m.add.optionValue(e)) : (i = i.join(h.delimiter), m.debug("Setting hidden input to delimited value", i, L)), !1 === h.fireOnInit && m.is.initialLoad() ? m.verbose("Skipping onadd callback on initial load", h.onAdd) : h.onAdd.call(M, e, t, n), m.set.value(i, e, t, n), m.check.maxSelections()) : m.debug("Cannot select blank values from multiselect")
					}
				},
				remove: {
					active: function () {
						S.removeClass(g.active)
					},
					activeLabel: function () {
						S.find(w.label).removeClass(g.active)
					},
					empty: function () {
						S.removeClass(g.empty)
					},
					loading: function () {
						S.removeClass(g.loading)
					},
					initialLoad: function () {
						e = !1
					},
					upward: function (e) {
						(e || S).removeClass(g.upward)
					},
					leftward: function (e) {
						(e || q).removeClass(g.leftward)
					},
					visible: function () {
						S.removeClass(g.visible)
					},
					activeItem: function () {
						R.removeClass(g.active)
					},
					filteredItem: function () {
						h.useLabels && m.has.maxSelections() || (h.useLabels && m.is.multiple() ? R.not("." + g.active).removeClass(g.filtered) : R.removeClass(g.filtered), m.remove.empty())
					},
					optionValue: function (e) {
						var t = m.escape.value(e),
							n = L.find('option[value="' + m.escape.string(t) + '"]');
						0 < n.length && n.hasClass(g.addition) && (s && (s.disconnect(), m.verbose("Temporarily disconnecting mutation observer")), n.remove(), m.verbose("Removing user addition as an <option>", t), s && s.observe(L[0], {
							childList: !0,
							subtree: !0
						}))
					},
					message: function () {
						q.children(w.message).remove()
					},
					searchWidth: function () {
						T.css("width", "")
					},
					searchTerm: function () {
						m.verbose("Cleared search term"), T.val(""), m.set.filtered()
					},
					userAddition: function () {
						R.filter(w.addition).remove()
					},
					selected: function (e, t) {
						if (!(t = h.allowAdditions ? t || m.get.itemWithAdditions(e) : t || m.get.item(e))) return !1;
						t.each(function () {
							var e = X(this),
								t = m.get.choiceText(e),
								n = m.get.choiceValue(e, t);
							m.is.multiple() ? h.useLabels ? (m.remove.value(n, t, e), m.remove.label(n)) : (m.remove.value(n, t, e), 0 === m.get.selectionCount() ? m.set.placeholderText() : m.set.text(m.add.variables(c.count))) : m.remove.value(n, t, e), e.removeClass(g.filtered).removeClass(g.active), h.useLabels && e.removeClass(g.selected)
						})
					},
					selectedItem: function () {
						R.removeClass(g.selected)
					},
					value: function (e, t, n) {
						var i, a = m.get.values();
						m.has.selectInput() ? (m.verbose("Input is <select> removing selected option", e), i = m.remove.arrayValue(e, a), m.remove.optionValue(e)) : (m.verbose("Removing from delimited values", e), i = (i = m.remove.arrayValue(e, a)).join(h.delimiter)), !1 === h.fireOnInit && m.is.initialLoad() ? m.verbose("No callback on initial load", h.onRemove) : h.onRemove.call(M, e, t, n), m.set.value(i, t, n), m.check.maxSelections()
					},
					arrayValue: function (t, e) {
						return X.isArray(e) || (e = [e]), e = X.grep(e, function (e) {
							return t != e
						}), m.verbose("Removed value from delimited string", t, e), e
					},
					label: function (e, t) {
						var n = S.find(w.label).filter("[data-" + b.value + '="' + m.escape.string(e) + '"]');
						m.verbose("Removing label", n), n.remove()
					},
					activeLabels: function (e) {
						e = e || S.find(w.label).filter("." + g.active), m.verbose("Removing active label selections", e), m.remove.labels(e)
					},
					labels: function (e) {
						e = e || S.find(w.label), m.verbose("Removing labels", e), e.each(function () {
							var e = X(this),
								t = e.data(b.value),
								n = t !== J ? String(t) : t,
								i = m.is.userValue(n);
							!1 !== h.onLabelRemove.call(e, t) ? (m.remove.message(), i ? (m.remove.value(n), m.remove.label(n)) : m.remove.selected(n)) : m.debug("Label remove callback cancelled removal")
						})
					},
					tabbable: function () {
						m.is.searchSelection() ? (m.debug("Searchable dropdown initialized"), T.removeAttr("tabindex")) : (m.debug("Simple selection dropdown initialized"), S.removeAttr("tabindex")), q.removeAttr("tabindex")
					},
					clearable: function () {
						I.removeClass(g.clear)
					}
				},
				has: {
					menuSearch: function () {
						return m.has.search() && 0 < T.closest(q).length
					},
					search: function () {
						return 0 < T.length
					},
					sizer: function () {
						return 0 < k.length
					},
					selectInput: function () {
						return L.is("select")
					},
					minCharacters: function (e) {
						return !h.minCharacters || (e = e !== J ? String(e) : String(m.get.query())).length >= h.minCharacters
					},
					firstLetter: function (e, t) {
						var n;
						return !(!e || 0 === e.length || "string" != typeof t) && (n = m.get.choiceText(e, !1), (t = t.toLowerCase()) == String(n).charAt(0).toLowerCase())
					},
					input: function () {
						return 0 < L.length
					},
					items: function () {
						return 0 < R.length
					},
					menu: function () {
						return 0 < q.length
					},
					message: function () {
						return 0 !== q.children(w.message).length
					},
					label: function (e) {
						var t = m.escape.value(e),
							n = S.find(w.label);
						return h.ignoreCase && (t = t.toLowerCase()), 0 < n.filter("[data-" + b.value + '="' + m.escape.string(t) + '"]').length
					},
					maxSelections: function () {
						return h.maxSelections && m.get.selectionCount() >= h.maxSelections
					},
					allResultsFiltered: function () {
						var e = R.not(w.addition);
						return e.filter(w.unselectable).length === e.length
					},
					userSuggestion: function () {
						return 0 < q.children(w.addition).length
					},
					query: function () {
						return "" !== m.get.query()
					},
					value: function (e) {
						return h.ignoreCase ? m.has.valueIgnoringCase(e) : m.has.valueMatchingCase(e)
					},
					valueMatchingCase: function (e) {
						var t = m.get.values();
						return !!(X.isArray(t) ? t && -1 !== X.inArray(e, t) : t == e)
					},
					valueIgnoringCase: function (n) {
						var e = m.get.values(),
							i = !1;
						return X.isArray(e) || (e = [e]), X.each(e, function (e, t) {
							if (String(n).toLowerCase() == String(t).toLowerCase()) return !(i = !0)
						}), i
					}
				},
				is: {
					active: function () {
						return S.hasClass(g.active)
					},
					animatingInward: function () {
						return q.transition("is inward")
					},
					animatingOutward: function () {
						return q.transition("is outward")
					},
					bubbledLabelClick: function (e) {
						return X(e.target).is("select, input") && 0 < S.closest("label").length
					},
					bubbledIconClick: function (e) {
						return 0 < X(e.target).closest(I).length
					},
					alreadySetup: function () {
						return S.is("select") && S.parent(w.dropdown).data(C) !== J && 0 === S.prev().length
					},
					animating: function (e) {
						return e ? e.transition && e.transition("is animating") : q.transition && q.transition("is animating")
					},
					leftward: function (e) {
						return (e || q).hasClass(g.leftward)
					},
					disabled: function () {
						return S.hasClass(g.disabled)
					},
					focused: function () {
						return G.activeElement === S[0]
					},
					focusedOnSearch: function () {
						return G.activeElement === T[0]
					},
					allFiltered: function () {
						return (m.is.multiple() || m.has.search()) && !(0 == h.hideAdditions && m.has.userSuggestion()) && !m.has.message() && m.has.allResultsFiltered()
					},
					hidden: function (e) {
						return !m.is.visible(e)
					},
					initialLoad: function () {
						return e
					},
					inObject: function (n, e) {
						var i = !1;
						return X.each(e, function (e, t) {
							if (t == n) return i = !0
						}), i
					},
					multiple: function () {
						return S.hasClass(g.multiple)
					},
					remote: function () {
						return h.apiSettings && m.can.useAPI()
					},
					single: function () {
						return !m.is.multiple()
					},
					selectMutation: function (e) {
						var n = !1;
						return X.each(e, function (e, t) {
							if (t.target && X(t.target).is("select")) return n = !0
						}), n
					},
					search: function () {
						return S.hasClass(g.search)
					},
					searchSelection: function () {
						return m.has.search() && 1 === T.parent(w.dropdown).length
					},
					selection: function () {
						return S.hasClass(g.selection)
					},
					userValue: function (e) {
						return -1 !== X.inArray(e, m.get.userValues())
					},
					upward: function (e) {
						return (e || S).hasClass(g.upward)
					},
					visible: function (e) {
						return e ? e.hasClass(g.visible) : q.hasClass(g.visible)
					},
					verticallyScrollableContext: function () {
						var e = y.get(0) !== Y && y.css("overflow-y");
						return "auto" == e || "scroll" == e
					},
					horizontallyScrollableContext: function () {
						var e = y.get(0) !== Y && y.css("overflow-X");
						return "auto" == e || "scroll" == e
					}
				},
				can: {
					activate: function (e) {
						return !!h.useLabels || (!m.has.maxSelections() || !(!m.has.maxSelections() || !e.hasClass(g.active)))
					},
					openDownward: function (e) {
						var t, n, i = e || q,
							a = !0;
						return i.addClass(g.loading), n = {
							context: {
								offset: y.get(0) === Y ? {
									top: 0,
									left: 0
								} : y.offset(),
								scrollTop: y.scrollTop(),
								height: y.outerHeight()
							},
							menu: {
								offset: i.offset(),
								height: i.outerHeight()
							}
						}, m.is.verticallyScrollableContext() && (n.menu.offset.top += n.context.scrollTop), a = (t = {
							above: n.context.scrollTop <= n.menu.offset.top - n.context.offset.top - n.menu.height,
							below: n.context.scrollTop + n.context.height >= n.menu.offset.top - n.context.offset.top + n.menu.height
						}).below ? (m.verbose("Dropdown can fit in context downward", t), !0) : t.below || t.above ? (m.verbose("Dropdown cannot fit below, opening upward", t), !1) : (m.verbose("Dropdown cannot fit in either direction, favoring downward", t), !0), i.removeClass(g.loading), a
					},
					openRightward: function (e) {
						var t, n, i = e || q,
							a = !0;
						return i.addClass(g.loading), n = {
							context: {
								offset: y.get(0) === Y ? {
									top: 0,
									left: 0
								} : y.offset(),
								scrollLeft: y.scrollLeft(),
								width: y.outerWidth()
							},
							menu: {
								offset: i.offset(),
								width: i.outerWidth()
							}
						}, m.is.horizontallyScrollableContext() && (n.menu.offset.left += n.context.scrollLeft), (t = n.menu.offset.left - n.context.offset.left + n.menu.width >= n.context.scrollLeft + n.context.width) && (m.verbose("Dropdown cannot fit in context rightward", t), a = !1), i.removeClass(g.loading), a
					},
					click: function () {
						return U || "click" == h.on
					},
					extendSelect: function () {
						return h.allowAdditions || h.apiSettings
					},
					show: function () {
						return !m.is.disabled() && (m.has.items() || m.has.message())
					},
					useAPI: function () {
						return X.fn.api !== J
					}
				},
				animate: {
					show: function (e, t) {
						var n, i = t || q,
							a = t ? function () { } : function () {
								m.hideSubMenus(), m.hideOthers(), m.set.active()
							};
						e = X.isFunction(e) ? e : function () { }, m.verbose("Doing menu show animation", i), m.set.direction(t), n = m.get.transition(t), m.is.selection() && m.set.scrollPosition(m.get.selectedItem(), !0), (m.is.hidden(i) || m.is.animating(i)) && ("none" == n ? (a(), i.transition("show"), e.call(M)) : X.fn.transition !== J && S.transition("is supported") ? i.transition({
							animation: n + " in",
							debug: h.debug,
							verbose: h.verbose,
							duration: h.duration,
							queue: !0,
							onStart: a,
							onComplete: function () {
								e.call(M)
							}
						}) : m.error(v.noTransition, n))
					},
					hide: function (e, t) {
						var n = t || q,
							i = (t ? h.duration : h.duration, t ? function () { } : function () {
								m.can.click() && m.unbind.intent(), m.remove.active()
							}),
							a = m.get.transition(t);
						e = X.isFunction(e) ? e : function () { }, (m.is.visible(n) || m.is.animating(n)) && (m.verbose("Doing menu hide animation", n), "none" == a ? (i(), n.transition("hide"), e.call(M)) : X.fn.transition !== J && S.transition("is supported") ? n.transition({
							animation: a + " out",
							duration: h.duration,
							debug: h.debug,
							verbose: h.verbose,
							queue: !1,
							onStart: i,
							onComplete: function () {
								e.call(M)
							}
						}) : m.error(v.transition))
					}
				},
				hideAndClear: function () {
					m.remove.searchTerm(), m.has.maxSelections() || (m.has.search() ? m.hide(function () {
						m.remove.filteredItem()
					}) : m.hide())
				},
				delay: {
					show: function () {
						m.verbose("Delaying show event to ensure user intent"), clearTimeout(m.timer), m.timer = setTimeout(m.show, h.delay.show)
					},
					hide: function () {
						m.verbose("Delaying hide event to ensure user intent"), clearTimeout(m.timer), m.timer = setTimeout(m.hide, h.delay.hide)
					}
				},
				escape: {
					value: function (e) {
						var t = X.isArray(e),
							n = "string" == typeof e,
							i = !n && !t,
							a = n && -1 !== e.search(d.quote),
							o = [];
						return i || !a ? e : (m.debug("Encoding quote values for use in select", e), t ? (X.each(e, function (e, t) {
							o.push(t.replace(d.quote, "&quot;"))
						}), o) : e.replace(d.quote, "&quot;"))
					},
					string: function (e) {
						return (e = String(e)).replace(d.escape, "\\$&")
					}
				},
				setting: function (e, t) {
					if (m.debug("Changing setting", e, t), X.isPlainObject(e)) X.extend(!0, h, e);
					else {
						if (t === J) return h[e];
						X.isPlainObject(h[e]) ? X.extend(!0, h[e], t) : h[e] = t
					}
				},
				internal: function (e, t) {
					if (X.isPlainObject(e)) X.extend(!0, m, e);
					else {
						if (t === J) return m[e];
						m[e] = t
					}
				},
				debug: function () {
					!h.silent && h.debug && (h.performance ? m.performance.log(arguments) : (m.debug = Function.prototype.bind.call(console.info, console, h.name + ":"), m.debug.apply(console, arguments)))
				},
				verbose: function () {
					!h.silent && h.verbose && h.debug && (h.performance ? m.performance.log(arguments) : (m.verbose = Function.prototype.bind.call(console.info, console, h.name + ":"), m.verbose.apply(console, arguments)))
				},
				error: function () {
					h.silent || (m.error = Function.prototype.bind.call(console.error, console, h.name + ":"), m.error.apply(console, arguments))
				},
				performance: {
					log: function (e) {
						var t, n;
						h.performance && (n = (t = (new Date).getTime()) - (K || t), K = t, W.push({
							Name: e[0],
							Arguments: [].slice.call(e, 1) || "",
							Element: M,
							"Execution Time": n
						})), clearTimeout(m.performance.timer), m.performance.timer = setTimeout(m.performance.display, 500)
					},
					display: function () {
						var e = h.name + ":",
							n = 0;
						K = !1, clearTimeout(m.performance.timer), X.each(W, function (e, t) {
							n += t["Execution Time"]
						}), e += " " + n + "ms", N && (e += " '" + N + "'"), (console.group !== J || console.table !== J) && 0 < W.length && (console.groupCollapsed(e), console.table ? console.table(W) : X.each(W, function (e, t) {
							console.log(t.Name + ": " + t["Execution Time"] + "ms")
						}), console.groupEnd()), W = []
					}
				},
				invoke: function (i, e, t) {
					var a, o, n, s = F;
					return e = e || Q, t = M || t, "string" == typeof i && s !== J && (i = i.split(/[\. ]/), a = i.length - 1, X.each(i, function (e, t) {
						var n = e != a ? t + i[e + 1].charAt(0).toUpperCase() + i[e + 1].slice(1) : i;
						if (X.isPlainObject(s[n]) && e != a) s = s[n];
						else {
							if (s[n] !== J) return o = s[n], !1;
							if (!X.isPlainObject(s[t]) || e == a) return s[t] !== J ? o = s[t] : m.error(v.method, i), !1;
							s = s[t]
						}
					})), X.isFunction(o) ? n = o.apply(t, e) : o !== J && (n = o), X.isArray(P) ? P.push(n) : P !== J ? P = [P, n] : n !== J && (P = n), o
				}
			}, $ ? (F === J && m.initialize(), m.invoke(B)) : (F !== J && F.invoke("destroy"), m.initialize())
		}), P !== J ? P : H
	}, X.fn.dropdown.settings = {
		silent: !1,
		debug: !1,
		verbose: !1,
		performance: !0,
		on: "click",
		action: "activate",
		values: !1,
		clearable: !1,
		apiSettings: !1,
		selectOnKeydown: !0,
		minCharacters: 0,
		filterRemoteData: !1,
		saveRemoteData: !0,
		throttle: 200,
		context: Y,
		direction: "auto",
		keepOnScreen: !0,
		match: "both",
		fullTextSearch: !1,
		placeholder: "auto",
		preserveHTML: !0,
		sortSelect: !1,
		forceSelection: !0,
		allowAdditions: !1,
		ignoreCase: !1,
		hideAdditions: !0,
		maxSelections: !1,
		useLabels: !0,
		delimiter: ",",
		showOnFocus: !0,
		allowReselection: !1,
		allowTab: !0,
		allowCategorySelection: !1,
		fireOnInit: !1,
		transition: "auto",
		duration: 200,
		glyphWidth: 1.037,
		label: {
			transition: "scale",
			duration: 200,
			variation: !1
		},
		delay: {
			hide: 300,
			show: 200,
			search: 20,
			touch: 50
		},
		onChange: function (e, t, n) { },
		onAdd: function (e, t, n) { },
		onRemove: function (e, t, n) { },
		onLabelSelect: function (e) { },
		onLabelCreate: function (e, t) {
			return X(this)
		},
		onLabelRemove: function (e) {
			return !0
		},
		onNoResults: function (e) {
			return !0
		},
		onShow: function () { },
		onHide: function () { },
		name: "Dropdown",
		namespace: "dropdown",
		message: {
			addResult: "Add <b>{term}</b>",
			count: "{count} selected",
			maxSelections: "Max {maxCount} selections",
			noResults: "No results found.",
			serverError: "There was an error contacting the server"
		},
		error: {
			action: "You called a dropdown action that was not defined",
			alreadySetup: "Once a select has been initialized behaviors must be called on the created ui dropdown",
			labels: "Allowing user additions currently requires the use of labels.",
			missingMultiple: "<select> requires multiple property to be set to correctly preserve multiple values",
			method: "The method you called is not defined.",
			noAPI: "The API module is required to load resources remotely",
			noStorage: "Saving remote data requires session storage",
			noTransition: "This module requires ui transitions <https://github.com/Semantic-Org/UI-Transition>"
		},
		regExp: {
			escape: /[-[\]{}()*+?.,\\^$|#\s]/g,
			quote: /"/g
		},
		metadata: {
			defaultText: "defaultText",
			defaultValue: "defaultValue",
			placeholderText: "placeholder",
			text: "text",
			value: "value"
		},
		fields: {
			remoteValues: "results",
			values: "values",
			disabled: "disabled",
			name: "name",
			value: "value",
			text: "text"
		},
		keys: {
			backspace: 8,
			delimiter: 188,
			deleteKey: 46,
			enter: 13,
			escape: 27,
			pageUp: 33,
			pageDown: 34,
			leftArrow: 37,
			upArrow: 38,
			rightArrow: 39,
			downArrow: 40
		},
		selector: {
			addition: ".addition",
			dropdown: ".ui.dropdown",
			hidden: ".hidden",
			icon: "> .dropdown.icon",
			input: '> input[type="hidden"], > select',
			item: ".item",
			label: "> .label",
			remove: "> .label > .delete.icon",
			siblingLabel: ".label",
			menu: ".menu",
			message: ".message",
			menuIcon: ".dropdown.icon",
			search: "input.search, .menu > .search > input, .menu input.search",
			sizer: "> input.sizer",
			text: "> .text:not(.icon)",
			unselectable: ".disabled, .filtered"
		},
		className: {
			active: "active",
			addition: "addition",
			animating: "animating",
			clear: "clear",
			disabled: "disabled",
			empty: "empty",
			dropdown: "ui dropdown",
			filtered: "filtered",
			hidden: "hidden transition",
			item: "item",
			label: "ui label",
			loading: "loading",
			menu: "menu",
			message: "message",
			multiple: "multiple",
			placeholder: "default",
			sizer: "sizer",
			search: "search",
			selected: "selected",
			selection: "selection",
			upward: "upward",
			leftward: "left",
			visible: "visible"
		}
	}, X.fn.dropdown.settings.templates = {
		dropdown: function (e) {
			var t = e.placeholder || !1,
				n = (e.values, "");
			return n += '<i class="dropdown icon icon-freccia-basso"></i>', e.placeholder ? n += '<div class="default text">' + t + "</div>" : n += '<div class="text"></div>', n += '<div class="menu">', X.each(e.values, function (e, t) {
				n += t.disabled ? '<div class="disabled item" data-value="' + t.value + '">' + t.name + "</div>" : '<div class="item" data-value="' + t.value + '">' + t.name + "</div>"
			}), n += "</div>"
		},
		menu: function (e, a) {
			var t = e[a.values] || {},
				o = "";
			return X.each(t, function (e, t) {
				var n = t[a.text] ? 'data-text="' + t[a.text] + '"' : "",
					i = t[a.disabled] ? "disabled " : "";
				o += '<div class="' + i + 'item" data-value="' + t[a.value] + '"' + n + ">", o += t[a.name], o += "</div>"
			}), o
		},
		label: function (e, t) {
			return t + '<i class="delete icon icon-close1"></i>'
		},
		message: function (e) {
			return e
		},
		addition: function (e) {
			return e
		}
	}
}(jQuery, window, document);